/* eslint-disable */
import React, { useState, useEffect, useCallback, useRef } from "react";
import ReactDOM from "react-dom";
import * as Sentry from "@sentry/react";
// Drum sequencer kit definitions + loader. Provides BF_KITS metadata, a
// loadKitBuffers(ctx, kitId) async helper that returns decoded AudioBuffers,
// and getKitMeta / listKits for UI rendering. The kit module ships its WAV
// samples base64-embedded so the whole frontend remains a single-file deploy.
import { loadKitBuffers, getKitMeta, listKits, registerAdminKits } from "./BeatFinderKits.js";
// SoundTouchJS — real pitch shift (preserves duration). Port of the C++
// SoundTouch library by Olli Parviainen, MIT-licensed. We use it OFFLINE
// only (process whole buffer, cache the result), not realtime. The
// PitchShifter class is a ScriptProcessorNode-compatible wrapper; we
// drive it manually via the lower-level SoundTouch class so we can run
// inside an OfflineAudioContext without nominal real-time constraints.
// ~80KB minified; resolved by the bundler from package.json.
import { SoundTouch, SimpleFilter } from "soundtouchjs";

// =============================================================================
// SENTRY ERROR MONITORING
// =============================================================================
// Captures uncaught JS errors and unhandled promise rejections and ships them
// to Sentry with stack traces + breadcrumbs (network requests, console logs,
// click events) so we can debug real user issues without needing them to
// describe what happened.
//
// Free Developer-tier limit: 5,000 errors/month. We dial down the noise:
//   - sampleRate: 1.0  → capture 100% of errors (we want them all)
//   - tracesSampleRate: 0.05 → only 5% of transactions for perf monitoring
//     (otherwise we'd burn the quota on routine page loads)
//   - replaysSessionSampleRate: 0 → no session replays (PII concerns + cost)
//   - environment: prod vs dev so we can filter in the dashboard
//
// We deliberately don't ship sourcemaps to Sentry yet — that's a separate
// build-time step. Stack traces will reference the minified bundle for now,
// which is still useful (line + col numbers map back to source via the
// browser devtools' "Show original" feature if you have local source).
try {
  Sentry.init({
    dsn: "https://4ff40f11676086583dad8edbcae24408@o4511395146825728.ingest.us.sentry.io/4511395165110272",
    environment: (typeof window !== "undefined" && window.location && /beatfinder\.co\.uk/.test(window.location.hostname)) ? "production" : "development",
    integrations: [
      Sentry.browserTracingIntegration(),
    ],
    // Performance monitoring sample rate — keep low to stay under the free
    // tier's transaction limit. Errors are sampled at 100% (default).
    tracesSampleRate: 0.05,
    // Don't send default PII like IP addresses — we have a privacy policy
    // to honour. Stack traces alone are enough.
    sendDefaultPii: false,
    // Ignore noisy errors that come from browser extensions / ad-blockers
    // and aren't actionable. Add to this list as patterns emerge.
    ignoreErrors: [
      "ResizeObserver loop limit exceeded",
      "ResizeObserver loop completed with undelivered notifications",
      "Non-Error promise rejection captured",
      "Network request failed",
      // Safari iOS specific noise
      "Load failed",
      // Ad-blocker / extension noise
      /chrome-extension:\/\//,
      /safari-extension:\/\//,
      /^moz-extension:\/\//,
    ],
    // Strip noisy breadcrumbs that don't help debug user-facing issues.
    beforeBreadcrumb: function(breadcrumb) {
      // Skip "info" level console messages — only keep warnings + errors
      if (breadcrumb.category === "console" && breadcrumb.level === "log") {
        return null;
      }
      return breadcrumb;
    },
  });
} catch (e) {
  // If Sentry itself fails to init, swallow it — we never want monitoring
  // to break the actual app.
  if (typeof console !== "undefined" && console.warn) {
    console.warn("[Sentry] init failed:", e && e.message);
  }
}

// =============================================================================
// PREMIUM LOADER COMPONENT
// =============================================================================
const LOADER_STYLE = `
  @media screen and (orientation: landscape) {
    html, body { overflow: hidden; }
    #bf-portrait-lock {
      position: fixed;
      top: 0; left: 0;
      width: 100vh;
      height: 100vw;
      transform-origin: top left;
      transform: rotate(90deg) translateY(-100%);
      overflow-y: auto;
      overflow-x: hidden;
      z-index: 999999;
    }
  }
  @media screen and (orientation: portrait) {
    #bf-portrait-lock {
      position: static;
      width: 100%;
      height: 100%;
      transform: none;
    }
  }
  /* ── Tablets / iPads (>=768px): use the screen natively in BOTH
     orientations. Cancel the phone-only forced-portrait rotation and let
     the app fill a comfortable, centered width instead of a 430px column. */
  @media screen and (min-width: 768px) {
    html, body { overflow: auto !important; }
    #bf-portrait-lock {
      position: static !important;
      width: 100% !important;
      height: auto !important;
      transform: none !important;
      max-width: 820px !important;
      margin: 0 auto !important;
    }
  }
  /* Larger tablets / iPad Pro landscape: a touch wider still. */
  @media screen and (min-width: 1100px) {
    #bf-portrait-lock {
      max-width: 920px !important;
    }
  }
  /* Universal box-sizing: paddings count INSIDE element widths instead
     of adding to them. This is conventional and won't break any layout
     that wasn't already broken. */
  *, *::before, *::after { box-sizing: border-box; }
  @keyframes bf-spin  { to { transform: rotate(360deg); } }
  @keyframes bf-pulse { 0%,100% { opacity:0.4; transform:scaleY(0.5); } 50% { opacity:1; transform:scaleY(1); } }
  @keyframes bf-fade  { from { opacity:0; } to { opacity:1; } }
  @keyframes bf-fadein-up { from { opacity:0; transform:translateY(12px); } to { opacity:1; transform:translateY(0); } }
  @keyframes bf-scale-in  { from { opacity:0; transform:scale(0.96); } to { opacity:1; transform:scale(1); } }
  @keyframes bf-play-pulse { 0%,100% { box-shadow:0 0 0 0 rgba(192,38,211,0.4); } 70% { box-shadow:0 0 0 10px rgba(192,38,211,0); } }
  @keyframes bf-tab-in     { from { opacity:0; transform:translateX(8px); } to { opacity:1; transform:translateX(0); } }
  @keyframes bf-pulse-dot  { 0%,100% { box-shadow:0 0 0 2px rgba(34,197,94,0.18), 0 0 6px rgba(34,197,94,0.6); } 50% { box-shadow:0 0 0 4px rgba(34,197,94,0.08), 0 0 10px rgba(34,197,94,0.8); } }
  @keyframes bf-sf-pulse   { 0%,100% { opacity:0.35; transform:scale(0.8); } 50% { opacity:1; transform:scale(1); } }
  @keyframes bf-pop { 0% { opacity:0; transform: translate(-50%,-50%) scale(0.65); } 60% { opacity:1; transform: translate(-50%,-50%) scale(1.08); } 100% { opacity:1; transform: translate(-50%,-50%) scale(1); } }
  /* ── Members-area "command console" FX ── */
  @keyframes bf-aurora { 0% { transform: translate(-12%, -8%) rotate(0deg); } 50% { transform: translate(10%, 6%) rotate(8deg); } 100% { transform: translate(-12%, -8%) rotate(0deg); } }
  @keyframes bf-scan   { 0% { transform: translateY(-120%); } 100% { transform: translateY(320%); } }
  @keyframes bf-holo   { 0% { background-position: 0% 50%; } 50% { background-position: 100% 50%; } 100% { background-position: 0% 50%; } }
  @keyframes bf-stars  { from { background-position: 0 0, 0 0; } to { background-position: -340px 680px, 200px 400px; } }
  @keyframes bf-edge   { 0%,100% { opacity:0.55; } 50% { opacity:1; } }
  @keyframes bf-float-bob { 0%,100% { transform: translateY(0); } 50% { transform: translateY(-3px); } }
  @keyframes spin { 0% { transform: rotate(0deg); } 100% { transform: rotate(360deg); } }
  /* Indeterminate progress bar — a sliding stripe that loops left→right.
     Used by the soundfont loading toast. We animate 'left' rather than
     'transform: translateX(...)' because the parent toast already uses
     'transform: translateX(-50%)' for centering, and iOS Safari sometimes
     fails to compose a transform animation on a child when the parent
     has its own transform — the stripe would render static. Animating
     left (a layout property, not transform) sidesteps that entirely. */
  @keyframes bf-loader-slide { 0% { left: -40%; } 100% { left: 100%; } }
  @keyframes bf-toast-in     { from { opacity:0; transform: translate(-50%, -8px); } to { opacity:1; transform: translate(-50%, 0); } }
  .bf-loader-stripe {
    position: absolute;
    top: 0; height: 100%;
    width: 35%;
    border-radius: 3px;
    background: linear-gradient(90deg,
      rgba(167,139,250,0) 0%,
      rgba(167,139,250,0.55) 30%,
      rgba(196,181,253,0.95) 50%,
      rgba(167,139,250,0.55) 70%,
      rgba(167,139,250,0) 100%);
    box-shadow: 0 0 12px rgba(167,139,250,0.6);
    animation: bf-loader-slide 1.4s cubic-bezier(0.4, 0, 0.2, 1) infinite;
    will-change: left;
  }
  .bf-page    { animation: bf-fadein-up 0.28s cubic-bezier(0.22,1,0.36,1) both; }
  .bf-card    { animation: bf-scale-in  0.22s cubic-bezier(0.22,1,0.36,1) both; }
  .bf-tab-in  { animation: bf-tab-in   0.22s cubic-bezier(0.22,1,0.36,1) both; }
  .bf-btn     { transition: transform 0.12s ease, opacity 0.12s ease; }
  .bf-btn:active { transform: scale(0.93); opacity: 0.8; }
  .bf-nav-btn { transition: transform 0.15s cubic-bezier(0.34,1.56,0.64,1), color 0.15s ease; }
  .bf-nav-btn:active { transform: scale(0.85); }
  .bf-play    { animation: bf-play-pulse 2s ease infinite; }
  /* ── Premium card microinteractions ── */
  @keyframes bf-shimmer-sweep { 0% { transform: translateX(-160%) rotate(8deg); } 100% { transform: translateX(160%) rotate(8deg); } }
  @keyframes bf-breathe { 0%,100% { box-shadow: 0 12px 30px rgba(0,0,0,0.55), 0 0 16px rgba(176,38,255,0.16), inset 0 1px 0 rgba(255,255,255,0.05); } 50% { box-shadow: 0 14px 34px rgba(0,0,0,0.6), 0 0 26px rgba(176,38,255,0.34), inset 0 1px 0 rgba(255,255,255,0.06); } }
  /* Press lift: a quick spring on touch — card dips slightly then can lift.
     transform-only so it composites on the GPU. */
  .bf-press { transition: transform 0.16s cubic-bezier(0.34,1.56,0.64,1), box-shadow 0.16s ease; will-change: transform; }
  .bf-press:active { transform: scale(0.96) translateY(1px); }
  /* iOS Safari zooms in (and yanks the page to the middle) when focusing a
     text field under 16px — worst inside fixed overlays. Force 16px to stop it. */
  input, textarea, select { font-size: 16px !important; }
  /* Shimmer: a moving light band swept across the card. The element with
     this class should be position:absolute inside an overflow:hidden card. */
  .bf-shimmer { position: absolute; top: 0; bottom: 0; left: 0; width: 55%;
    background: linear-gradient(100deg, transparent, rgba(255,255,255,0.10) 45%, rgba(0,213,255,0.10) 55%, transparent);
    transform: translateX(-160%) rotate(8deg);
    animation: bf-shimmer-sweep 4.5s ease-in-out infinite; pointer-events: none; }
  .bf-breathe { animation: bf-breathe 4s ease-in-out infinite; }
  .bf-carousel { scroll-behavior: smooth; -webkit-overflow-scrolling: touch; }
  * { scrollbar-width: none; -ms-overflow-style: none; }
  *::-webkit-scrollbar { display: none; }
  /* Opt-in visible scrollbar — apply this class to specific containers
     where the user genuinely needs to see scroll position (e.g. preset
     browser grid with 30+ items). */
  .bf-show-scroll { scrollbar-width: thin; -ms-overflow-style: auto; }
  .bf-show-scroll::-webkit-scrollbar { display: block; width: 8px; }
  .bf-show-scroll::-webkit-scrollbar-track { background: rgba(20,15,30,0.4); border-radius: 4px; }
  .bf-show-scroll::-webkit-scrollbar-thumb { background: rgba(167,139,250,0.35); border-radius: 4px; }
  .bf-show-scroll::-webkit-scrollbar-thumb:hover { background: rgba(167,139,250,0.55); }
  .bf-save    { transition: transform 0.18s cubic-bezier(0.34,1.56,0.64,1), color 0.15s ease; }
  .bf-save:active { transform: scale(1.3); }
  input[type=range]::-webkit-slider-thumb { -webkit-appearance:none; width:14px; height:14px; border-radius:50%; background:#9333ea; box-shadow:0 0 6px rgba(147,51,234,0.6); cursor:pointer; }
  input[type=range]::-moz-range-thumb { width:14px; height:14px; border-radius:50%; background:#9333ea; border:none; cursor:pointer; }
  .nr-slider::-webkit-slider-thumb { background:#10B981 !important; box-shadow:0 0 6px rgba(16,185,129,0.6) !important; }
  .nr-slider::-moz-range-thumb { background:#10B981 !important; }
  .bf-spinner {
    width: 44px; height: 44px; border-radius: 50%;
    border: 3px solid rgba(99,102,241,0.12);
    border-top-color: #A855F7;
    border-right-color: #6366F1;
    animation: bf-spin 0.8s linear infinite;
    filter: drop-shadow(0 0 10px rgba(168,85,247,0.5)) drop-shadow(0 0 6px rgba(99,102,241,0.4));
  }
  .bf-bars { display:flex; align-items:flex-end; gap:4px; height:28px; }
  .bf-bar {
    width: 5px; border-radius: 3px;
    background: linear-gradient(180deg,#A855F7 0%,#7C3AED 50%,#3B82F6 100%);
    animation: bf-pulse 1s ease-in-out infinite;
    filter: drop-shadow(0 0 4px rgba(168,85,247,0.7)) drop-shadow(0 0 2px rgba(59,130,246,0.5));
  }
  .bf-bar:nth-child(1) { height:14px; animation-delay:0s; }
  .bf-bar:nth-child(2) { height:22px; animation-delay:0.15s; }
  .bf-bar:nth-child(3) { height:28px; animation-delay:0.3s; }
  .bf-bar:nth-child(4) { height:22px; animation-delay:0.45s; }
  .bf-bar:nth-child(5) { height:14px; animation-delay:0.6s; }
  .bf-loader { animation: bf-fade 0.2s ease; }

  /* Neon progress bar — used for uploads/downloads */
  @keyframes bf-progress-shimmer {
    0% { background-position: 0% 50%; }
    100% { background-position: 200% 50%; }
  }
  @keyframes bf-progress-indet {
    0%   { left: -40%; width: 40%; }
    50%  { left: 30%;  width: 50%; }
    100% { left: 100%; width: 40%; }
  }
  @keyframes bfNudgePulse {
    0%   { transform: scale(1.18); filter: brightness(1.4); }
    100% { transform: scale(1);    filter: brightness(1);   }
  }
  .bf-progress-track {
    position: relative;
    width: 100%;
    height: 6px;
    background: #0a0a0a;
    border: 1px solid #1a1a1a;
    border-radius: 6px;
    overflow: hidden;
    box-shadow: inset 0 1px 2px rgba(0,0,0,0.6);
  }
  .bf-progress-fill {
    position: absolute;
    top: 0; bottom: 0;
    background: linear-gradient(90deg, #A855F7 0%, #7C3AED 35%, #6366F1 65%, #3B82F6 100%);
    background-size: 200% 100%;
    animation: bf-progress-shimmer 2s linear infinite;
    border-radius: 6px;
    box-shadow: 0 0 12px rgba(168,85,247,0.6), 0 0 6px rgba(59,130,246,0.5);
    transition: width 0.25s ease;
  }
  .bf-progress-indet {
    position: absolute;
    top: 0; bottom: 0;
    background: linear-gradient(90deg, transparent 0%, #A855F7 30%, #6366F1 70%, transparent 100%);
    box-shadow: 0 0 12px rgba(168,85,247,0.6);
    animation: bf-progress-indet 1.4s cubic-bezier(0.4,0,0.2,1) infinite;
    border-radius: 6px;
  }

`;

function BFLoader({ text, type }) {
  return (
    <div className="bf-loader" style={{
      display: "flex", flexDirection: "column", alignItems: "center",
      justifyContent: "center", padding: "60px 20px", gap: 20,
    }}>
      <style>{LOADER_STYLE}</style>
      {type === "bars" ? (
        <div className="bf-bars">
          <div className="bf-bar" />
          <div className="bf-bar" />
          <div className="bf-bar" />
          <div className="bf-bar" />
          <div className="bf-bar" />
        </div>
      ) : (
        <div className="bf-spinner" />
      )}
      {text && (
        <div style={{
          color: "#6b6b6b", fontSize: 11, fontWeight: 600,
          letterSpacing: 2, opacity: 0.8,
        }}>
          {text}
        </div>
      )}
    </div>
  );
}

// Sleek neon progress bar — black track with purple→blue gradient fill.
// Pass `progress` as 0-1 for determinate, omit/null for indeterminate animation.
function BFProgressBar({ progress, label, sublabel }) {
  var pct = (typeof progress === "number" && progress >= 0 && progress <= 1)
    ? Math.round(progress * 100) : null;
  return (
    <div style={{ width: "100%" }}>
      <style>{LOADER_STYLE}</style>
      {(label || pct !== null) && (
        <div style={{
          display: "flex", justifyContent: "space-between", alignItems: "center",
          marginBottom: 6,
        }}>
          {label && (
            <span style={{ color: "#aaa", fontSize: 11, fontWeight: 700, letterSpacing: 0.4 }}>
              {label}
            </span>
          )}
          {pct !== null && (
            <span style={{
              color: "#A855F7", fontSize: 11, fontWeight: 800, letterSpacing: 0.4,
              textShadow: "0 0 8px rgba(168,85,247,0.5)",
            }}>
              {pct}%
            </span>
          )}
        </div>
      )}
      <div className="bf-progress-track">
        {pct !== null ? (
          <div className="bf-progress-fill" style={{ width: pct + "%" }} />
        ) : (
          <div className="bf-progress-indet" />
        )}
      </div>
      {sublabel && (
        <div style={{
          color: "#555", fontSize: 10, marginTop: 6, letterSpacing: 0.3,
        }}>
          {sublabel}
        </div>
      )}
    </div>
  );
}



// =============================================================================
// VU METER COMPONENTS
// =============================================================================

// Module-scope flag for "Performance Mode" — set by StudioScreen via the
// settings toggle. When true, visualization hooks short-circuit and avoid
// running their rAF loops. Keeps audio engine 100% untouched. Used to free
// up CPU on iOS during screen recording or on slower devices.
let _performanceMode = false;
function _setPerformanceMode(v) { _performanceMode = !!v; }
function isPerformanceMode() { return _performanceMode; }

// ── Module-scope live-FX-node registry ───────────────────────────
// StudioScreen owns the live FX nodes for each track (the actual Web Audio
// graph). UI components like _TQPluginInner need to read live values from
// these nodes (e.g. det.reduction for the gain reduction meters) but they
// live outside StudioScreen's scope. This registry is the bridge: StudioScreen
// publishes its live-nodes lookup once at mount, and any module-scope component
// can call _getLiveFxNodes(trackId) to retrieve them.
//
// The lookup function returns the same object as fxNodesRef.current[trackId]
// — so {tq: {bandNodes: [...]}, rcomp: {...}, etc.}. Components are responsible
// for null-checking — the lookup may return undefined if the track isn't
// currently playing (no live graph built yet).
let _liveFxNodesLookup = null;
function _registerLiveFxNodesLookup(fn) { _liveFxNodesLookup = fn; }
function _getLiveFxNodes(trackId) {
  if (!_liveFxNodesLookup) return null;
  try { return _liveFxNodesLookup(trackId); } catch(e) { return null; }
}

// Shared hook: drives a real AnalyserNode and returns {level, peak, clipping}
function useAnalyser(analyserNode, isActive) {
  const [level,    setLevel]    = React.useState(0);
  const [peak,     setPeak]     = React.useState(0);
  const [clipping, setClipping] = React.useState(false);
  const peakHold   = React.useRef(0);
  const peakTimer  = React.useRef(null);
  const rafRef     = React.useRef(null);
  // Throttle VU updates to ~30fps. The eye can't distinguish VU bars
  // changing faster than ~33ms, but at 60fps × N tracks × FX plugins
  // active, React reconciliation of VU state was eating enough main
  // thread time during playback that iOS Safari couldn't reliably
  // deliver tap events to buttons (pointerdown/pointerup spaced too
  // far apart by the busy frames). Halving the reconcile rate gives
  // back ~5ms per frame — enough for iOS to register taps on the
  // first try instead of needing 3-4 retries.
  const lastUpdateRef = React.useRef(0);
  const VU_INTERVAL_MS = 33; // ~30fps

  React.useEffect(() => {
    if (!analyserNode || !isActive) {
      setLevel(0); setPeak(0); setClipping(false);
      return;
    }
    const bufLen = analyserNode.frequencyBinCount;
    const data   = new Float32Array(bufLen);
    const tick   = (now) => {
      // Performance Mode short-circuit — if user has turned off visuals,
      // skip the FFT read + state update and reschedule lazily. We still
      // run the rAF so when the user toggles back on, we resume cleanly,
      // but we do NO work per tick. ~5ms saved per frame × N tracks.
      if (_performanceMode) {
        rafRef.current = requestAnimationFrame(tick);
        return;
      }
      // Skip work entirely if not enough time has passed since last update
      if (now - lastUpdateRef.current < VU_INTERVAL_MS) {
        rafRef.current = requestAnimationFrame(tick);
        return;
      }
      lastUpdateRef.current = now;

      analyserNode.getFloatTimeDomainData(data);
      let rms = 0;
      let pk  = 0;
      for (let i = 0; i < bufLen; i++) {
        const v = Math.abs(data[i]);
        rms += v * v;
        if (v > pk) pk = v;
      }
      rms = Math.sqrt(rms / bufLen);
      const rmsDb = rms > 0 ? 20 * Math.log10(rms) : -100;
      const pkDb  = pk  > 0 ? 20 * Math.log10(pk)  : -100;
      // Normalise -60..0 dB → 0..1
      const normRms = Math.max(0, Math.min(1, (rmsDb + 60) / 60));
      const normPk  = Math.max(0, Math.min(1, (pkDb  + 60) / 60));
      setLevel(normRms);
      setClipping(pk >= 0.999);
      // Peak hold: 2 s decay
      if (normPk > peakHold.current) {
        peakHold.current = normPk;
        clearTimeout(peakTimer.current);
        peakTimer.current = setTimeout(() => { peakHold.current = 0; setPeak(0); }, 2000);
      }
      setPeak(peakHold.current);
      rafRef.current = requestAnimationFrame(tick);
    };
    rafRef.current = requestAnimationFrame(tick);
    return () => {
      cancelAnimationFrame(rafRef.current);
      clearTimeout(peakTimer.current);
    };
  }, [analyserNode, isActive]);

  return { level, peak, clipping };
}

// Real VU meter driven by a Web Audio AnalyserNode – used in Studio
function VUMeter({ analyserNode, isActive, compact, label, showLabel }) {
  const { level, peak, clipping } = useAnalyser(analyserNode, isActive);
  const BAR_COUNT = compact ? 8 : 14;
  const HEIGHT    = compact ? 28 : 36;

  return (
    <div style={{ display: "flex", flexDirection: "column", gap: 2 }}>
      {showLabel && (
        <div style={{ color: clipping ? "#EF4444" : "#444", fontSize: 8, fontWeight: 700, letterSpacing: 1, textAlign: "center", fontFamily: "monospace" }}>
          {label || "LEVEL"}{clipping ? " ◆ CLIP" : ""}
        </div>
      )}
      <div style={{
        display: "flex", alignItems: "flex-end", gap: compact ? 1.5 : 2,
        height: HEIGHT,
        background: "rgba(0,0,0,0.6)",
        borderRadius: 6,
        padding: compact ? "4px 6px" : "5px 8px",
        border: "1px solid " + (clipping ? "rgba(239,68,68,0.6)" : "rgba(255,255,255,0.06)"),
        boxShadow: clipping ? "0 0 8px rgba(239,68,68,0.3)" : "none",
        transition: "box-shadow 0.1s, border-color 0.1s",
        position: "relative",
      }}>
        {Array.from({ length: BAR_COUNT }, (_, i) => {
          const thresh   = i / BAR_COUNT;
          const active   = level >= thresh;
          const isPeak   = peak  >= thresh && peak < thresh + (1 / BAR_COUNT) * 1.5;
          const isClipZone = i >= BAR_COUNT - 2;
          const color    = isClipZone ? "#EF4444" : i >= BAR_COUNT * 0.72 ? "#F59E0B" : "#22C55E";
          return (
            <div key={i} style={{
              flex: 1, borderRadius: 1.5,
              background: active ? color : isPeak ? color : "#1a1a1a",
              height: active ? "100%" : isPeak ? "100%" : "30%",
              opacity: active ? 1 : isPeak ? 0.9 : 0.2,
              boxShadow: active && isClipZone ? "0 0 6px rgba(239,68,68,0.8)" : active ? "0 0 3px " + color + "55" : "none",
              transition: active ? "none" : "opacity 0.15s, height 0.15s",
            }} />
          );
        })}
      </div>
    </div>
  );
}

// ─────────────────────────────────────────────────────────────────────────────
// LOGIC PRO-STYLE STEREO VU METER  (horizontal, L above R, green/yellow/red)
// ─────────────────────────────────────────────────────────────────────────────
// ─────────────────────────────────────────────────────────────────────────────
// LOGIC PRO-STYLE STEREO VU METER  (horizontal L/R bars, embedded in fader row)
// ─────────────────────────────────────────────────────────────────────────────
function StereoVUMeter({ analyserNode, isActive }) {
  const { level, peak, clipping } = useAnalyser(analyserNode, isActive);
  const rOff   = React.useRef(0.90 + Math.random() * 0.10);
  const rLevel = Math.min(1, level * rOff.current);
  const rPeak  = Math.min(1, peak  * rOff.current);
  const SEGS   = 16;

  const renderBar = (v, pk) => (
    <div style={{ display:"flex", gap:1, height:3, alignItems:"stretch", width:"100%" }}>
      {Array.from({ length: SEGS }, (_, i) => {
        const frac      = i / SEGS;
        const lit       = v >= frac;
        const isPeak    = pk > 0 && Math.abs(pk - frac) < (1.2 / SEGS);
        const baseColor = frac >= 0.875 ? "#FF3B30" : frac >= 0.688 ? "#FFD60A" : "#30D158";
        const dimColor  = frac >= 0.875 ? "#2a0a08" : frac >= 0.688 ? "#2a2500" : "#082014";
        return (
          <div key={i} style={{
            flex:1, borderRadius:1,
            background: isPeak ? "#fff" : lit ? baseColor : dimColor,
            boxShadow: lit && frac >= 0.875 ? "0 0 2px rgba(255,59,48,0.6)" : "none",
          }} />
        );
      })}
    </div>
  );

  return (
    <div style={{ display:"flex", flexDirection:"column", gap:1.5, width:"100%" }}>
      <div style={{ display:"flex", alignItems:"center", gap:2 }}>
        <span style={{ color:"#404040", fontSize:6, fontWeight:700, width:5, flexShrink:0 }}>L</span>
        <div style={{ flex:1 }}>{renderBar(level, peak)}</div>
        <div style={{ width:4, height:4, borderRadius:1, background: clipping ? "#FF3B30" : "#1a1a1a", flexShrink:0 }} />
      </div>
      <div style={{ display:"flex", alignItems:"center", gap:2 }}>
        <span style={{ color:"#404040", fontSize:6, fontWeight:700, width:5, flexShrink:0 }}>R</span>
        <div style={{ flex:1 }}>{renderBar(rLevel, rPeak)}</div>
        <div style={{ width:4, height:4, borderRadius:1, background:"transparent", flexShrink:0 }} />
      </div>
    </div>
  );
}

// ─────────────────────────────────────────────────────────────────────────────
// LOGIC PRO VOLUME FADER  — horizontal slider with thumb, 0→1.5 range
// Sits below the VU meters exactly like Logic's channel strip fader
// ─────────────────────────────────────────────────────────────────────────────
function LogicVolumeFader({ value = 1, onChange, onBeginGesture, onEndGesture }) {
  const trackRef  = React.useRef(null);

  // value 0..1.5 → pct 0..100 along fader
  const pct = Math.max(0, Math.min(100, (value / 1.5) * 100));
  // Unity (1.0) mark position
  const unityPct = (1 / 1.5) * 100;

  // Use imperative listeners (like GBFader) so we don't compete with the
  // synthesized click event that iOS Safari fires after pointer sequences.
  // The previous React onClick-based pattern had a bug where, after a drag,
  // iOS sometimes fired a click event with clientX = the pointerdown
  // position (not the final pointerup position), causing onChange to snap
  // the value back to where the drag STARTED.
  const onPointerDown = (e) => {
    e.preventDefault();
    e.stopPropagation();
    const el = trackRef.current;
    if (!el) return;
    el.setPointerCapture(e.pointerId);
    const rect = el.getBoundingClientRect();
    const pid = e.pointerId;
    // Compute value from any X within the track
    const calc = function(clientX) {
      const raw = (clientX - rect.left) / rect.width;
      return Math.max(0, Math.min(1.5, raw * 1.5));
    };
    // Open a history gesture so all pointermove updates coalesce into
    // a single undo step instead of dozens of micro-positions.
    if (onBeginGesture) try { onBeginGesture(); } catch(_) {}
    // Apply value at the touch-down point too (tap = jump to value)
    onChange(+calc(e.clientX).toFixed(3));
    const onMove = function(me) {
      if (me.pointerId !== pid) return;
      onChange(+calc(me.clientX).toFixed(3));
    };
    const onUp = function(ue) {
      if (ue.pointerId !== pid) return;
      el.releasePointerCapture(pid);
      el.removeEventListener("pointermove", onMove);
      el.removeEventListener("pointerup",   onUp);
      el.removeEventListener("pointercancel", onUp);
      if (onEndGesture) try { onEndGesture(); } catch(_) {}
    };
    el.addEventListener("pointermove", onMove);
    el.addEventListener("pointerup",   onUp);
    el.addEventListener("pointercancel", onUp);
  };
  const onDoubleClick = (e) => { e.stopPropagation(); onChange(1); };

  const dbLabel = value <= 0.001 ? "-∞" : `${value >= 1 ? "+" : ""}${(20*Math.log10(value)).toFixed(1)}`;

  return (
    <div style={{ display:"flex", alignItems:"center", gap:3, width:"100%" }}>
      {/* Fader track */}
      <div
        ref={trackRef}
        onPointerDown={onPointerDown}
        onDoubleClick={onDoubleClick}
        title={`Volume: ${dbLabel} dB  (double-tap = unity)`}
        style={{
          flex:1, height:14, position:"relative",
          cursor:"pointer", userSelect:"none", touchAction:"none",
        }}
      >
        {/* Track groove */}
        <div style={{
          position:"absolute", top:"50%", left:0, right:0,
          height:3, borderRadius:2, transform:"translateY(-50%)",
          background:"#1a1a1a",
          border:"1px solid #111",
        }}>
          {/* Filled portion (left of thumb) */}
          <div style={{
            position:"absolute", left:0, top:0, bottom:0,
            width:`${pct}%`,
            borderRadius:2,
            background: value > 1.4 ? "linear-gradient(90deg,#30D158,#FF3B30)" : "linear-gradient(90deg,#2a2a2a,#30D158)",
          }} />
          {/* Unity marker */}
          <div style={{
            position:"absolute", left:`${unityPct}%`,
            top:-2, bottom:-2, width:1,
            background:"#555", borderRadius:1,
          }} />
        </div>
        {/* Thumb */}
        <div style={{
          position:"absolute", top:"50%", left:`${pct}%`,
          transform:"translate(-50%,-50%)",
          width:10, height:14, borderRadius:2,
          background:"linear-gradient(180deg,#6a6a6a,#3a3a3a)",
          border:"1px solid #222",
          boxShadow:"0 1px 3px rgba(0,0,0,0.7), inset 0 1px 0 rgba(255,255,255,0.15)",
          cursor:"ew-resize",
          // Center grip line
          display:"flex", alignItems:"center", justifyContent:"center",
        }}>
          <div style={{ width:1, height:7, background:"rgba(255,255,255,0.25)", borderRadius:1 }} />
        </div>
      </div>
      {/* dB readout */}
      <span style={{ color:"#555", fontSize:7, fontWeight:700, width:22, textAlign:"right", flexShrink:0, fontFamily:"monospace" }}>{dbLabel}</span>
    </div>
  );
}

// ─────────────────────────────────────────────────────────────────────────────
// LOGIC PRO PAN KNOB  — rotary knob -1..+1, center-notched
// ─────────────────────────────────────────────────────────────────────────────
function LogicPanSlider({ value = 0, onChange, onBeginGesture, onEndGesture }) {
  const knobRef  = React.useRef(null);
  const startY   = React.useRef(null);
  const startVal = React.useRef(null);

  const panLabel = Math.abs(value) < 0.03 ? "C" : value < 0 ? `L${Math.round(Math.abs(value)*100)}` : `R${Math.round(value*100)}`;
  const angle = value * 135;
  const SIZE = 28, R = 11, cx = 14, cy = 14;

  const toXY = (deg) => {
    const rad = (deg - 90) * Math.PI / 180;
    return [cx + R * Math.cos(rad), cy + R * Math.sin(rad)];
  };

  const onPointerDown = (e) => {
    e.stopPropagation(); e.preventDefault();
    startY.current = e.clientY; startVal.current = value;
    knobRef.current.setPointerCapture(e.pointerId);
    if (onBeginGesture) try { onBeginGesture(); } catch(_) {}
  };
  const onPointerMove = (e) => {
    if (startY.current === null) return;
    const delta = (startY.current - e.clientY) / 80;
    let v = Math.max(-1, Math.min(1, startVal.current + delta));
    if (Math.abs(v) < 0.05) v = 0;
    onChange(+v.toFixed(3));
  };
  const onPointerUp   = () => {
    if (startY.current === null) return;
    startY.current = null;
    if (onEndGesture) try { onEndGesture(); } catch(_) {}
  };
  const onDoubleClick = (e) => { e.stopPropagation(); onChange(0); };

  const arcStart = toXY(0);
  const arcEnd   = toXY(angle);
  const largeArc = Math.abs(angle) > 180 ? 1 : 0;
  const sweep    = angle >= 0 ? 1 : 0;
  const tickEnd  = toXY(0);
  const tickIn   = [cx + 6 * Math.cos(-Math.PI/2), cy + 6 * Math.sin(-Math.PI/2)];
  const thumbOut = [cx + 9 * Math.cos((angle-90)*Math.PI/180), cy + 9 * Math.sin((angle-90)*Math.PI/180)];
  const thumbIn  = [cx + 3 * Math.cos((angle-90)*Math.PI/180), cy + 3 * Math.sin((angle-90)*Math.PI/180)];

  return (
    <div style={{ display:"flex", alignItems:"center", gap:4, width:"100%" }}>
      <span style={{ color:"#404040", fontSize:7, fontWeight:700, width:14, flexShrink:0 }}>PAN</span>
      <div style={{ display:"flex", alignItems:"center", justifyContent:"center", flex:1 }}>
        <svg ref={knobRef} width={SIZE} height={SIZE}
          onPointerDown={onPointerDown} onPointerMove={onPointerMove}
          onPointerUp={onPointerUp} onDoubleClick={onDoubleClick}
          title={`Pan: ${panLabel}  (double-tap = center)`}
          style={{ cursor:"ns-resize", touchAction:"none", userSelect:"none", overflow:"visible" }}>
          <defs>
            <radialGradient id="panKnobGrad" cx="40%" cy="35%" r="60%">
              <stop offset="0%" stopColor="#5a5a5a" />
              <stop offset="100%" stopColor="#222" />
            </radialGradient>
          </defs>
          <circle cx={cx} cy={cy} r={12} fill="url(#panKnobGrad)" stroke="#111" strokeWidth={1} />
          <path d={`M ${toXY(-135)[0]} ${toXY(-135)[1]} A ${R} ${R} 0 1 1 ${toXY(135)[0]} ${toXY(135)[1]}`}
            fill="none" stroke="#1a1a1a" strokeWidth={2.5} strokeLinecap="round" />
          {Math.abs(value) > 0.03 && (
            <path d={`M ${arcStart[0]} ${arcStart[1]} A ${R} ${R} 0 ${largeArc} ${sweep} ${arcEnd[0]} ${arcEnd[1]}`}
              fill="none" stroke="#4a9eff" strokeWidth={2.5} strokeLinecap="round" />
          )}
          <line x1={tickEnd[0]} y1={tickEnd[1]} x2={tickIn[0]} y2={tickIn[1]}
            stroke="#444" strokeWidth={1} strokeLinecap="round" />
          <line x1={thumbIn[0]} y1={thumbIn[1]} x2={thumbOut[0]} y2={thumbOut[1]}
            stroke="rgba(255,255,255,0.85)" strokeWidth={1.5} strokeLinecap="round" />
        </svg>
      </div>
      <span style={{ color:"#4a9eff", fontSize:7, fontWeight:700, width:18, textAlign:"right", flexShrink:0, fontFamily:"monospace" }}>{panLabel}</span>
    </div>
  );
}

// ─────────────────────────────────────────────────────────────────────────────
// LOGIC PRO TRACK HEADER  — solid black, no R button, fader+pan sliders
// ─────────────────────────────────────────────────────────────────────────────
function LogicTrackHeader({
  track, isRec, isSelected, fxOpen, showTakes,
  onSelect, onMute, onSolo, onFx, onTakes, onRemove, onDuplicate, onDrumRack, onOpenSound,
  updateTrack, analyserNode, isPlaying,
  isDragging, isDropTarget,
  onLongPressStart, onLongPressCancel, onDragMove, onDragEnd,
  beginGesture, endGesture,
}) {
  const [renaming, setRenaming] = React.useState(false);
  const [nameVal,  setNameVal]  = React.useState(track.name);
  const [holdProgress, setHoldProgress] = React.useState(0); // 0–1 fill while holding
  const [menuOpen, setMenuOpen] = React.useState(false); // ⋮ overflow menu
  const menuButtonRef = React.useRef(null);
  const [menuPos, setMenuPos] = React.useState({ top: 0, left: 0 });
  const inputRef    = React.useRef(null);
  const holdRafRef  = React.useRef(null);
  const holdStartRef = React.useRef(null);
  const HOLD_MS = 500;

  React.useEffect(() => { setNameVal(track.name); }, [track.name]);
  React.useEffect(() => { if (renaming && inputRef.current) inputRef.current.focus(); }, [renaming]);

  // Cancel hold animation when drag starts from outside
  React.useEffect(() => {
    if (!isDragging) { setHoldProgress(0); }
  }, [isDragging]);

  const commitRename = () => {
    const n = nameVal.trim() || track.name;
    updateTrack(track.id, { name: n });
    setRenaming(false);
  };

  const startHoldAnim = () => {
    holdStartRef.current = performance.now();
    const tick = () => {
      const elapsed = performance.now() - holdStartRef.current;
      const p = Math.min(1, elapsed / HOLD_MS);
      setHoldProgress(p);
      if (p < 1) holdRafRef.current = requestAnimationFrame(tick);
    };
    holdRafRef.current = requestAnimationFrame(tick);
  };

  const cancelHoldAnim = () => {
    cancelAnimationFrame(holdRafRef.current);
    holdStartRef.current = null;
    setHoldProgress(0);
  };

  const accentColor = isRec ? "#FF3B30" : track.color || "#30D158";
  const trackIcon   = track.type === "beat" ? "♩" : "●";

  return (
    <div
      onClick={onSelect}
      onPointerDown={function(e) {
        if (renaming) return;
        // Don't start long-press when the touch lands on an interactive control
        // (volume fader, pan slider, M/S/FX buttons). Those are nested inside
        // this wrapper so their own stopPropagation hasn't run yet — we check
        // the event target directly to bail out early.
        const isInteractive = e.target.closest('button, input, [data-nolongpress]');
        const hasTitleAttr  = e.target.closest('[title]'); // fader & pan tracks carry title attrs
        if (isInteractive || hasTitleAttr) return;
        startHoldAnim();
        onLongPressStart && onLongPressStart(e);
      }}
      onPointerUp={function(e) {
        cancelHoldAnim();
        onLongPressCancel && onLongPressCancel();
        onDragEnd && onDragEnd();
      }}
      onPointerCancel={function() {
        cancelHoldAnim();
        onLongPressCancel && onLongPressCancel();
      }}
      onPointerMove={function(e) {
        if (isDragging) onDragMove && onDragMove(e);
      }}
      onTouchMove={function(e){ e.stopPropagation(); }}
      style={{
        width:"100%", height:"100%",
        display:"flex", flexDirection:"column",
        background: isDragging ? "#1a0a1a" : isDropTarget ? "#0d1a0d" : isSelected ? "#111111" : "#000000",
        borderRight: isSelected ? "1px solid #303030" : "1px solid #1a1a1a",
        borderLeft: `3px solid ${isDragging ? "#C026D3" : isDropTarget ? "#30D158" : accentColor}`,
        cursor: isDragging ? "grabbing" : "pointer",
        overflow:"hidden",
        padding:"4px 5px 4px 4px",
        boxSizing:"border-box",
        gap:3,
        touchAction:"none",
        userSelect:"none",
        opacity: isDragging ? 0.75 : 1,
        boxShadow: isDragging ? "0 0 0 2px #C026D3, 0 4px 24px rgba(192,38,211,0.35)" : isDropTarget ? "0 0 0 2px #30D158 inset" : "none",
        transition: "background 0.1s, box-shadow 0.1s, opacity 0.1s",
        position: "relative",
      }}
    >
      {/* Hold-to-select progress ring */}
      {holdProgress > 0 && holdProgress < 1 && !isDragging && (
        <div style={{
          position:"absolute", inset:0, pointerEvents:"none", zIndex:20,
          background:`linear-gradient(90deg, rgba(192,38,211,${holdProgress * 0.18}) 0%, transparent 100%)`,
          borderRadius:0,
        }}>
          <div style={{
            position:"absolute", bottom:0, left:0,
            height:2, borderRadius:1,
            width:`${holdProgress * 100}%`,
            background:"linear-gradient(90deg,#C026D3,#7C3AED)",
            boxShadow:"0 0 6px rgba(192,38,211,0.8)",
            transition:"none",
          }} />
        </div>
      )}
      {/* Drag grab handle — appears when dragging */}
      {isDragging && (
        <div style={{
          position:"absolute", top:"50%", right:4,
          transform:"translateY(-50%)",
          display:"flex", flexDirection:"column", gap:2,
          pointerEvents:"none", opacity:0.6,
        }}>
          {[0,1,2].map(function(i){ return <div key={i} style={{ width:14, height:1.5, borderRadius:1, background:"#C026D3" }} />; })}
        </div>
      )}
      {/* ── Row 1: Icon badge + name + M + S + FX ── */}
      <div style={{ display:"flex", alignItems:"center", gap:3, minWidth:0 }}>
        {/* Icon badge */}
        <div style={{
          width:13, height:13, borderRadius:3, flexShrink:0,
          background: isSelected ? accentColor : "#1e1e1e",
          border:`1px solid ${isSelected ? accentColor : "#2a2a2a"}`,
          display:"flex", alignItems:"center", justifyContent:"center",
        }}>
          <span style={{ color: isSelected ? "#fff" : "#555", fontSize:7, fontWeight:900, lineHeight:1 }}>
            {trackIcon}
          </span>
        </div>

        {/* Track name — double-tap to rename */}
        {renaming ? (
          <input
            ref={inputRef}
            value={nameVal}
            onChange={e => setNameVal(e.target.value)}
            onBlur={commitRename}
            onKeyDown={e => {
              if (e.key === "Enter")   commitRename();
              if (e.key === "Escape") { setRenaming(false); setNameVal(track.name); }
            }}
            onClick={e => e.stopPropagation()}
            style={{
              flex:1, minWidth:0,
              background:"#0d0d0d", border:`1px solid ${accentColor}`,
              borderRadius:3, color:"#fff", fontSize:9, fontWeight:600,
              padding:"1px 3px", outline:"none",
            }}
          />
        ) : (
          <span
            onDoubleClick={e => { e.stopPropagation(); setRenaming(true); }}
            style={{
              flex:1, minWidth:0,
              color:"#d0d0d0", fontSize:9, fontWeight:700,
              overflow:"hidden", textOverflow:"ellipsis", whiteSpace:"nowrap",
              lineHeight:1,
            }}
          >{track.name}</span>
        )}

        {/* M */}
        <button onClick={e => { e.stopPropagation(); onMute(); }}
          style={{
            width:16, height:13, borderRadius:3, flexShrink:0, padding:0,
            background: track.isMuted ? "#FFD60A" : "#1e1e1e",
            border:`1px solid ${track.isMuted ? "#FFD60A" : "#2a2a2a"}`,
            color: track.isMuted ? "#000" : "#666",
            fontSize:8, fontWeight:900, cursor:"pointer",
            display:"flex", alignItems:"center", justifyContent:"center",
            boxShadow: track.isMuted ? "0 0 4px rgba(255,214,10,0.5)" : "none",
          }}>M</button>

        {/* S */}
        <button onClick={e => { e.stopPropagation(); onSolo(); }}
          style={{
            width:16, height:13, borderRadius:3, flexShrink:0, padding:0,
            background: track.isSoloed ? "#30D158" : "#1e1e1e",
            border:`1px solid ${track.isSoloed ? "#30D158" : "#2a2a2a"}`,
            color: track.isSoloed ? "#000" : "#666",
            fontSize:8, fontWeight:900, cursor:"pointer",
            display:"flex", alignItems:"center", justifyContent:"center",
            boxShadow: track.isSoloed ? "0 0 4px rgba(48,209,88,0.5)" : "none",
          }}>S</button>

        {/* FX */}
        <button onClick={e => { e.stopPropagation(); onFx(); }}
          style={{
            width:20, height:13, borderRadius:3, flexShrink:0, padding:0,
            background: fxOpen ? "rgba(139,92,246,0.22)" : "#1e1e1e",
            border:`1px solid ${fxOpen ? "#8B5CF6" : "#2a2a2a"}`,
            color: fxOpen ? "#a78bfa" : "#555",
            fontSize:7, fontWeight:900, cursor:"pointer",
            display:"flex", alignItems:"center", justifyContent:"center",
          }}>FX</button>

        {/* RACK — only on drum sequencer tracks. Opens the channel rack
            overlay where the user edits step grids and manages patterns. */}
        {track.type === "drumSequencer" && onDrumRack && (
          <button onClick={e => { e.stopPropagation(); onDrumRack(); }}
            style={{
              width:30, height:13, borderRadius:3, flexShrink:0, padding:0,
              background: "rgba(192,38,211,0.22)",
              border: "1px solid #C026D3",
              color: "#E6A8F0",
              fontSize:7, fontWeight:900, cursor:"pointer",
              display:"flex", alignItems:"center", justifyContent:"center",
              letterSpacing: 0.5,
            }}>RACK</button>
        )}

        {/* SOUND — MIDI tracks only. Opens the preset browser bottom sheet.
            Mirrors the RACK button's visual treatment so the entry point
            feels consistent across track types. */}
        {track.type === "midi" && onOpenSound && (
          <button onClick={e => { e.stopPropagation(); onOpenSound(); }}
            style={{
              width:36, height:13, borderRadius:3, flexShrink:0, padding:0,
              background: "rgba(167,139,250,0.20)",
              border: "1px solid #A78BFA",
              color: "#D8C9FF",
              fontSize:7, fontWeight:900, cursor:"pointer",
              display:"flex", alignItems:"center", justifyContent:"center",
              letterSpacing: 0.5,
            }}>SOUND</button>
        )}

        {/* Takes (only if >1 clips) */}
        {track.clips && track.clips.length > 1 && (
          <button onClick={e => { e.stopPropagation(); onTakes(); }}
            style={{
              width:20, height:13, borderRadius:3, flexShrink:0, padding:0,
              background: showTakes ? "rgba(59,130,246,0.22)" : "#1e1e1e",
              border:`1px solid ${showTakes ? "#3B82F6" : "#2a2a2a"}`,
              color: showTakes ? "#60a5fa" : "#555",
              fontSize:7, fontWeight:900, cursor:"pointer",
              display:"flex", alignItems:"center", justifyContent:"center",
            }}>{track.clips.length}▾</button>
        )}

        {/* ⋮ Overflow menu — contains Duplicate + Delete */}
        <div style={{ position:"relative", flexShrink:0 }}>
          <button ref={menuButtonRef} onClick={e => {
            e.stopPropagation();
            if (!menuOpen && menuButtonRef.current) {
              const rect = menuButtonRef.current.getBoundingClientRect();
              const menuW = 140;
              const vw = window.innerWidth;
              const left = Math.min(Math.max(8, rect.right - menuW), vw - menuW - 8);
              setMenuPos({ top: rect.bottom + 4, left: left });
            }
            setMenuOpen(function(v){ return !v; });
          }}
            style={{
              background: menuOpen ? "rgba(167,139,250,0.12)" : "transparent",
              border: "1px solid " + (menuOpen ? "#3a2a52" : "transparent"),
              borderRadius: 6,
              color: menuOpen ? "#a78bfa" : "#777",
              fontSize:16, cursor:"pointer",
              padding:"2px 7px", lineHeight:1,
              fontWeight:900,
              transition:"all 0.12s",
            }}
            onPointerEnter={e => { if (!menuOpen) e.currentTarget.style.color="#bbb"; }}
            onPointerLeave={e => { if (!menuOpen) e.currentTarget.style.color="#777"; }}
          >⋮</button>
          {menuOpen && (function(){
            const menuContent = (
              <>
                {/* Backdrop to close on outside tap */}
                <div onClick={e => { e.stopPropagation(); setMenuOpen(false); }}
                  style={{ position:"fixed", inset:0, zIndex:9990, background:"rgba(0,0,0,0.25)" }} />
                <div style={{
                  position:"fixed", top:menuPos.top, left:menuPos.left, zIndex:9991,
                  background:"linear-gradient(180deg, #1a1530 0%, #14102a 100%)",
                  border:"1px solid #2a2050",
                  borderRadius:12,
                  boxShadow:"0 12px 32px rgba(0,0,0,0.7), 0 0 0 1px rgba(167,139,250,0.06)",
                  minWidth:200, overflow:"hidden",
                }} onClick={e => e.stopPropagation()}>
                {/* Header — track name */}
                <div style={{
                  padding:"9px 14px 8px",
                  background:"rgba(167,139,250,0.05)",
                  borderBottom:"1px solid #261c44",
                  fontSize:9, fontWeight:800,
                  color:"#a78bfa",
                  letterSpacing:1.5,
                  textTransform:"uppercase",
                  whiteSpace:"nowrap",
                  overflow:"hidden",
                  textOverflow:"ellipsis",
                }}>{track.name || "Track"}</div>

                {/* Rename */}
                <button onClick={function(e){
                  e.stopPropagation();
                  setMenuOpen(false);
                  setRenaming(true);
                }}
                  style={{
                    width:"100%", background:"none", border:"none",
                    color:"#e8e6f0",
                    padding:"11px 14px", textAlign:"left", cursor:"pointer",
                    display:"flex", alignItems:"center", gap:12,
                  }}
                  onPointerEnter={e => { e.currentTarget.style.background="rgba(167,139,250,0.08)"; }}
                  onPointerLeave={e => { e.currentTarget.style.background="transparent"; }}
                >
                  <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="#a78bfa" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" style={{flexShrink:0}}>
                    <path d="M12 20h9"/>
                    <path d="M16.5 3.5a2.121 2.121 0 0 1 3 3L7 19l-4 1 1-4L16.5 3.5z"/>
                  </svg>
                  <div style={{flex:1, minWidth:0}}>
                    <div style={{ fontSize:13, fontWeight:600, lineHeight:1.2 }}>Rename</div>
                    <div style={{ fontSize:10, color:"#7a6ea0", marginTop:2, lineHeight:1.2 }}>Edit the track name</div>
                  </div>
                </button>

                <div style={{ height:1, background:"linear-gradient(90deg, transparent, #261c44 20%, #261c44 80%, transparent)" }} />

                {/* Duplicate */}
                <button onClick={function(e){
                  e.stopPropagation();
                  setMenuOpen(false);
                  if (onDuplicate) onDuplicate();
                }}
                  style={{
                    width:"100%", background:"none", border:"none",
                    color:"#e8e6f0",
                    padding:"11px 14px", textAlign:"left", cursor:"pointer",
                    display:"flex", alignItems:"center", gap:12,
                  }}
                  onPointerEnter={e => { e.currentTarget.style.background="rgba(167,139,250,0.08)"; }}
                  onPointerLeave={e => { e.currentTarget.style.background="transparent"; }}
                >
                  <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="#a78bfa" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" style={{flexShrink:0}}>
                    <rect x="9" y="9" width="13" height="13" rx="2"/>
                    <path d="M5 15H4a2 2 0 0 1-2-2V4a2 2 0 0 1 2-2h9a2 2 0 0 1 2 2v1"/>
                  </svg>
                  <div style={{flex:1, minWidth:0}}>
                    <div style={{ fontSize:13, fontWeight:600, lineHeight:1.2 }}>Duplicate</div>
                    <div style={{ fontSize:10, color:"#7a6ea0", marginTop:2, lineHeight:1.2 }}>Make a copy with all settings</div>
                  </div>
                </button>

                <div style={{ height:1, background:"linear-gradient(90deg, transparent, #261c44 20%, #261c44 80%, transparent)" }} />

                {/* Delete */}
                <button onClick={function(e){
                  e.stopPropagation();
                  setMenuOpen(false);
                  if (onRemove) onRemove();
                }}
                  style={{
                    width:"100%", background:"none", border:"none",
                    color:"#ff6b6b",
                    padding:"11px 14px", textAlign:"left", cursor:"pointer",
                    display:"flex", alignItems:"center", gap:12,
                  }}
                  onPointerEnter={e => { e.currentTarget.style.background="rgba(255,59,48,0.10)"; }}
                  onPointerLeave={e => { e.currentTarget.style.background="transparent"; }}
                >
                  <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="#ff6b6b" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" style={{flexShrink:0}}>
                    <polyline points="3 6 5 6 21 6"/>
                    <path d="M19 6l-1 14a2 2 0 0 1-2 2H8a2 2 0 0 1-2-2L5 6"/>
                    <path d="M10 11v6M14 11v6"/>
                  </svg>
                  <div style={{flex:1, minWidth:0}}>
                    <div style={{ fontSize:13, fontWeight:600, lineHeight:1.2 }}>Delete</div>
                    <div style={{ fontSize:10, color:"#9a4a4a", marginTop:2, lineHeight:1.2 }}>Remove track permanently</div>
                  </div>
                </button>
              </div>
            </>
            );
            // Render via portal so the menu escapes any ancestor stacking
            // context, overflow:hidden, or transform-induced containing
            // block on the track row (which would otherwise clip / hide
            // the menu when zoomed in). It always overlays against the
            // real viewport at top zIndex.
            if (typeof document !== "undefined" && document.body && ReactDOM && ReactDOM.createPortal) {
              return ReactDOM.createPortal(menuContent, document.body);
            }
            return menuContent;
          })()}
        </div>
      </div>

      {/* ── Row 2: VU meters ── */}
      <StereoVUMeter analyserNode={analyserNode} isActive={isPlaying && !track.isMuted} />

      {/* ── Row 3: Volume fader (with dB label) ── */}
      <LogicVolumeFader
        value={track.volume ?? 1}
        onChange={v => updateTrack(track.id, { volume: v })}
        onBeginGesture={beginGesture}
        onEndGesture={endGesture}
      />

      {/* ── Row 4: Pan slider ── */}
      <LogicPanSlider
        value={track.pan || 0}
        onChange={v => updateTrack(track.id, { pan: v })}
        onBeginGesture={beginGesture}
        onEndGesture={endGesture}
      />
    </div>
  );
}

// =============================================================================
// CONFIG
// =============================================================================

// -- Backend URL --------------------------------------------------------------
// When developing locally:   http://localhost:8000
// When deployed on Render:   https://your-app.onrender.com
// Set this to your Render/Railway URL after deployment.
const BF_OVERLAY_IMG = "data:image/webp;base64,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";

const API_BASE = "https://beatfinder-backend.onrender.com";

// =============================================================================
// TERMS & CONDITIONS
// Versioned: bumping TERMS_VERSION re-prompts every user to re-accept.
// Acceptance is recorded server-side for logged-in users (so they only
// see this once unless the version changes) and in sessionStorage for
// guests (so it shows every fresh session).
// =============================================================================
const TERMS_VERSION = "1.0";
const TERMS_EFFECTIVE_DATE = "14 May 2026";
const TERMS_CONTENT = [
  {
    heading: "1. Acceptance of Terms",
    body: "By accessing or using BeatFinder (the \"Platform\"), whether as a registered account holder or as a guest, you agree to be bound by these Terms & Conditions (the \"Terms\"). If you do not accept these Terms in full, you must immediately stop using the Platform. Continued use after any update to these Terms constitutes acceptance of the revised version. These Terms form a legally binding contract between you and BeatFinder, governed by the laws of England and Wales."
  },
  {
    heading: "2. Eligibility & Account Responsibility",
    body: "You must be at least 16 years of age (or the age of digital consent in your jurisdiction) to create an account. By registering, you confirm that all information you provide is accurate, current, and complete, and that you will keep it updated. You are solely responsible for safeguarding your account credentials. Any activity occurring under your account is your responsibility, whether or not authorised by you. BeatFinder will not be liable for any loss or damage arising from your failure to protect your account."
  },
  {
    heading: "3. Licences, Beats & Intellectual Property",
    body: "All beats, instrumentals, samples, sound recordings, artwork, written content, and other media displayed on the Platform (\"Content\") are protected by copyright and other intellectual property laws. Producers who upload Content retain ownership of their original works and grant BeatFinder a limited, non-exclusive licence to host, display, and distribute that Content through the Platform. Buyers who purchase a Basic Lease or Premium Exclusive Lease receive only the specific rights described in the lease agreement issued at the point of sale — no other rights of any kind are granted. Free downloads are governed by the Free Licence agreement displayed at download time."
  },
  {
    heading: "4. Strictly Prohibited Conduct — Piracy, Theft & Misuse",
    body: "You agree NOT to: (a) copy, redistribute, re-upload, resell, sub-license, or otherwise exploit any Content beyond the explicit scope of the licence granted to you; (b) attempt to circumvent any technical protection measures, watermarks, or access controls; (c) use scraping, automation, bots, or any unauthorised means to access or extract Content; (d) remove, alter, or obscure producer credits, watermarks, or copyright notices; (e) upload Content you do not own or have written authorisation to upload, including AI-generated content trained on third-party works without consent; (f) claim authorship of any Content you did not create. Any breach of this section constitutes copyright infringement and is grounds for immediate account termination, removal of all uploaded Content, forfeiture of any pending earnings, and referral for civil and/or criminal legal action. BeatFinder reserves the right to pursue full damages, legal costs, and statutory remedies under the UK Copyright, Designs and Patents Act 1988 and equivalent international laws. Pirates and infringers will be held personally liable in their own name."
  },
  {
    heading: "5. Payments, Subscriptions & Refunds",
    body: "Paid subscriptions (Artist Pro, Producer Pro) and one-off lease purchases are processed by Stripe. By making a payment you authorise the charge and agree to Stripe's own terms. Subscriptions auto-renew at the end of each billing period at the then-current price unless cancelled at least 24 hours before renewal. Lease purchases are non-refundable once the contract is countersigned and the MP3 has been made available for download, save where required by mandatory consumer protection law. Subscription cancellations take effect at the end of the current billing period — no pro-rated refunds are issued. BeatFinder is not responsible for foreign exchange differences, bank fees, or chargebacks attempted in bad faith, which may result in account suspension."
  },
  {
    heading: "6. User-Uploaded Content & Producer Warranties",
    body: "If you upload beats, tracks, videos, images, or any other content, you warrant that you are the sole author, that the Content does not infringe any third-party rights (including copyright, moral rights, trademark, publicity, or privacy rights), and that you have full authority to grant the licences described in these Terms. You indemnify BeatFinder, its affiliates, employees, and contractors against any claim, loss, damage, or legal cost arising from a breach of this warranty. BeatFinder may remove any Content at any time without notice if we suspect a breach, receive a takedown request, or in our reasonable discretion."
  },
  {
    heading: "7. Data, Privacy & Security",
    body: "We collect and process personal data (account details, payment metadata via Stripe, usage analytics, uploaded files) only to operate the Platform, fulfil purchases, and improve the service. We do not sell your personal data to third parties. Data is stored on secure servers and protected by industry-standard measures, but no online service is completely immune to risk; you acknowledge this residual risk. By using the Platform you consent to our collection and processing of data as described, and you confirm you have lawful authority to share any information you provide about others (e.g. collaborators)."
  },
  {
    heading: "8. Platform Availability, Modifications & Termination",
    body: "We aim to keep the Platform available 24/7 but make no warranty of uninterrupted access. We may modify, suspend, or discontinue any feature at any time without liability. We may suspend or permanently terminate any account that, in our reasonable judgement, has breached these Terms, abused the Platform, or harmed other users. On termination, your access ceases immediately, but any licences validly granted to third parties before termination survive."
  },
  {
    heading: "9. Disclaimer of Warranties & Limitation of Liability",
    body: "The Platform and all Content are provided \"as is\" and \"as available\" without warranties of any kind, express or implied, including fitness for purpose, merchantability, or non-infringement, save for warranties that cannot be excluded under English law. To the maximum extent permitted by law, BeatFinder's total aggregate liability to you in any 12-month period is limited to the greater of (a) the fees you paid to BeatFinder during that period or (b) £100. BeatFinder is not liable for indirect, consequential, special, or punitive damages, lost profits, lost data, or loss of goodwill. Nothing in these Terms limits liability for death or personal injury caused by negligence, fraud, or any other liability that cannot be excluded under English law."
  },
  {
    heading: "10. Governing Law & Jurisdiction",
    body: "These Terms are governed by the laws of England and Wales. Any dispute arising under or in connection with these Terms is subject to the exclusive jurisdiction of the courts of England and Wales. If you are based outside the UK, you accept English jurisdiction and waive any objection to venue or forum non conveniens."
  },
  {
    heading: "11. Acknowledgement & Consent",
    body: "By tapping \"I Agree\" you confirm that you have read, understood, and accept these Terms in their entirety; that you are entering into a legally binding contract with BeatFinder; that you waive any defence based on not reading the Terms; and that this electronic acceptance has the same legal force as a handwritten signature under the UK Electronic Communications Act 2000."
  }
];

// =============================================================================
// PRIVACY POLICY
// Required by UK GDPR Article 13. Tells users what we collect, why, who we
// share it with, how long we keep it, and what rights they have. Unlike the
// T&C, the Privacy Policy doesn't require explicit acceptance — it just
// needs to be readily available before/at the point of data collection.
// =============================================================================
const PRIVACY_VERSION = "1.0";
const PRIVACY_EFFECTIVE_DATE = "14 May 2026";
const PRIVACY_CONTENT = [
  {
    heading: "1. Who We Are",
    body: "BeatFinder is a music marketplace and creative platform operated from the United Kingdom. We are the \"data controller\" for the personal data described in this policy, which means we decide what data is collected and how it is used. For any data-protection question or to exercise your rights under UK GDPR, you can email us at support@beatfinder.co.uk and we will respond within one calendar month (extendable to three months in complex cases, as permitted by Article 12)."
  },
  {
    heading: "2. What Data We Collect",
    body: "Account data: name, username, email address, hashed password, profile bio, location (if you choose to provide it), social media handles, avatar/header images. Payment metadata: Stripe customer ID, subscription plan, billing period, last 4 digits of card and brand (Stripe sends us this — we never see full card numbers or CVVs). Uploaded content: beats, instrumentals, tracks, videos, artwork, audio files, contract signatures, written lyrics. Usage data: pages visited, beats played, leases purchased, search queries, device type, approximate location derived from IP, timestamps of activity. Communications: messages between users, support requests sent to us. Technical data: IP address, browser type, operating system, time zone, cookies (see Section 8)."
  },
  {
    heading: "3. Why We Process Your Data (Lawful Bases)",
    body: "We rely on the following lawful bases under Article 6 UK GDPR: (a) PERFORMANCE OF A CONTRACT — operating your account, processing your purchases, delivering downloads and leases, fulfilling subscriptions; (b) LEGITIMATE INTERESTS — keeping the Platform secure, preventing fraud and abuse, improving features, analysing aggregate usage; (c) LEGAL OBLIGATION — retaining records of transactions for HMRC tax law (six years), responding to lawful information requests, anti-money-laundering compliance; (d) CONSENT — non-essential cookies, marketing emails (you can withdraw consent at any time without affecting processing already carried out)."
  },
  {
    heading: "4. Who We Share Data With (Sub-processors)",
    body: "We share the minimum necessary data with the following service providers, each bound by a written Data Processing Agreement: STRIPE (payments — receives email, name, billing data; based in Ireland/USA with UK-adequate safeguards); RENDER (backend hosting — stores all account and content data; USA-hosted with Standard Contractual Clauses); VERCEL (frontend hosting — receives no personal data directly, only serves static assets); MONGODB ATLAS (database — UK/EU regions with encryption at rest); CLOUDINARY (media storage — stores beat audio files and images; EU region available); RESEND (transactional emails — receives email address and message content); GOOGLE (YouTube API for trending data — no user data shared, only public search queries). We do NOT sell personal data, share it for cross-context behavioural advertising, or pass it to data brokers under any circumstances."
  },
  {
    heading: "5. International Transfers",
    body: "Some sub-processors store or process data outside the UK (primarily the USA). For each such transfer we rely on UK-approved transfer mechanisms: the UK International Data Transfer Agreement, the EU Standard Contractual Clauses with UK Addendum, or adequacy decisions where they apply. You can request a copy of the relevant transfer safeguard documents by emailing support@beatfinder.co.uk."
  },
  {
    heading: "6. How Long We Keep Your Data",
    body: "Account data: for as long as your account is active, plus 30 days after deletion to allow for accidental-deletion recovery. Transaction records (invoices, lease contracts): six years from the date of transaction, as required by UK tax law. Uploaded content (beats, leases sold): retained while you have a Producer Pro account; on deletion, your beats are removed from public display within 24 hours, though any leases already sold to buyers remain valid in their possession (we cannot revoke existing licensees' rights). Support communications: 24 months from the last message. Logs and analytics: 12 months for security; aggregated and anonymised after that. Backups: rolling 30 days, after which deleted data is fully purged."
  },
  {
    heading: "7. Your Rights Under UK GDPR",
    body: "You have the right to: (a) ACCESS — request a copy of all personal data we hold about you (available via Settings → Download My Data, or by email); (b) RECTIFICATION — correct inaccurate data (editable from your profile); (c) ERASURE — delete your account and personal data (available via Settings → Delete Account); (d) RESTRICTION — temporarily limit how we process your data; (e) PORTABILITY — receive your data in a structured, machine-readable format (JSON); (f) OBJECTION — object to processing based on legitimate interests; (g) WITHDRAW CONSENT — for any processing based on consent; (h) NOT BE SUBJECT TO AUTOMATED DECISIONS — we do not use any fully-automated decision-making that produces legal effects on you. You can also LODGE A COMPLAINT with the Information Commissioner's Office (ICO) at ico.org.uk or by calling 0303 123 1113. We would prefer the chance to resolve any concerns directly first."
  },
  {
    heading: "8. Cookies and Similar Technologies",
    body: "We use cookies and similar storage (localStorage, sessionStorage) for: STRICTLY NECESSARY — keeping you logged in, remembering your preferences, processing payments, fraud prevention (these do not require consent); FUNCTIONAL — saved lyrics, agreed licences, install prompt dismissal (set when you choose to use those features); ANALYTICS — aggregated usage statistics to improve the Platform (we ask for consent before enabling these). You can refuse non-essential cookies via our cookie banner or by clearing storage in your browser. Refusing strictly-necessary cookies will break core functionality."
  },
  {
    heading: "9. Security Measures",
    body: "We implement appropriate technical and organisational measures including: encryption in transit (TLS 1.2+); encryption at rest for database storage; bcrypt password hashing (passwords are never stored or transmitted in plaintext, even to us); JWT-based session tokens with expiry; rate limiting on authentication endpoints; restricted database access on a need-to-know basis; regular security updates of dependencies; periodic backups. No system is 100% secure. If a personal-data breach occurs that is likely to result in a risk to your rights, we will notify the ICO within 72 hours and, where required, notify you directly without undue delay, as required by Articles 33 and 34 UK GDPR."
  },
  {
    heading: "10. Children",
    body: "BeatFinder is not directed at children under 16, and we do not knowingly collect data from anyone under 16 without verifiable parental consent. If you believe a child under 16 has provided personal data to us, please email support@beatfinder.co.uk and we will delete it promptly."
  },
  {
    heading: "11. Changes to This Policy",
    body: "We may update this Privacy Policy from time to time. The version number and effective date are shown at the top. Material changes will be notified to logged-in users by in-app notice or email at least 14 days before they take effect. Continued use after the effective date constitutes acceptance of the revised policy. Previous versions are available on request."
  },
  {
    heading: "12. Contact",
    body: "For any privacy question, to exercise your rights, or to make a complaint: email support@beatfinder.co.uk. For ICO complaints: ico.org.uk · 0303 123 1113 · Wycliffe House, Water Lane, Wilmslow, Cheshire SK9 5AF."
  }
];

// =============================================================================
// GLOBAL PLAY COUNT STORE
// Single source of truth for beat play counts across ALL cards everywhere.
// When any card records a play, all other cards showing the same beat update.
// Uses an event-based pub/sub so no prop drilling is needed.
// =============================================================================
var _playCountStore = {};        // { [beatId]: number }
var _playedThisSession = {};     // { [beatId]: timestamp } — client-side cooldown
var _playTimers = {};            // { [beatId]: timeoutId } — 3s minimum play timer

// =============================================================================
// IN-APP DEBUG LOG BUFFER
// Captures all console.log/warn/error calls whose first argument starts with
// "[BF " so we can view them from a settings screen on iPhone without
// needing Safari DevTools. Keeps last 200 entries with timestamps. Used to
// diagnose cross-context sync issues (free licences, paid leases, etc).
// =============================================================================
var _bfDebugLogs = [];
var _bfDebugListeners = [];

function _bfPushLog(level, args) {
  try {
    var first = args && args[0];
    if (typeof first !== "string" || first.indexOf("[BF") !== 0) return;
    var msg = Array.prototype.slice.call(args).map(function(a) {
      try {
        return typeof a === "string" ? a : JSON.stringify(a, null, 2);
      } catch(e) { return String(a); }
    }).join(" ");
    _bfDebugLogs.push({ level: level, msg: msg, ts: Date.now() });
    if (_bfDebugLogs.length > 200) _bfDebugLogs.shift();
    _bfDebugListeners.forEach(function(fn) { try { fn(); } catch(e) {} });
  } catch(e) {}
}

(function patchConsole() {
  if (typeof console === "undefined") return;
  if (console.__bf_patched) return;
  console.__bf_patched = true;
  ["log", "warn", "error", "info"].forEach(function(lvl) {
    var orig = console[lvl];
    console[lvl] = function() {
      _bfPushLog(lvl, arguments);
      try { return orig.apply(console, arguments); } catch(e) {}
    };
  });
})();

function getBfDebugLogs() { return _bfDebugLogs.slice(); }
function clearBfDebugLogs() { _bfDebugLogs.length = 0; _bfDebugListeners.forEach(function(fn) { try { fn(); } catch(e) {} }); }
function subscribeBfDebugLogs(fn) {
  _bfDebugListeners.push(fn);
  return function() { _bfDebugListeners = _bfDebugListeners.filter(function(x) { return x !== fn; }); };
}

// Module-level debug log writer — writes to the SAME sessionStorage key
// the in-app Debug Log panel reads ("bf_debug_log"). CRITICAL: top-level
// engine functions (BFSpessaLiveEngine, scheduleMidiNote,
// applyInstrumentOverrides, the FluidSynth engine) call bfDebugLog(), but
// the component also defines a local bfDebugLog. Without this module-level
// definition, those top-level calls referenced an undefined name (guarded
// by `typeof bfDebugLog === "function"`) and silently did nothing — which
// is why engine/routing diagnostics never appeared. Defining it here puts
// it in scope for every top-level function. The component's version
// delegates to this so there's a single shared store.
var _BF_DEBUG_LOG_KEY = "bf_debug_log";
var _BF_DEBUG_VERBOSE_KEY = "bf_debug_verbose";
var _BF_TOUCH_TRACE_KEY = "bf_touch_trace";

// Touch tracer — toggleable diagnostic that logs every touch event at the
// document capture phase. Use for debugging "why doesn't this drag work?"
// type issues across the app (piano roll, sliders, knobs, any touch UI).
// Toggle via the debug log panel button or by running this in console:
//   sessionStorage.setItem("bf_touch_trace", "1"); location.reload();
function bfTouchTraceOn() {
  try { return sessionStorage.getItem(_BF_TOUCH_TRACE_KEY) === "1"; } catch(_) { return false; }
}
function bfDescribeTouchTarget(el) {
  // Build a short descriptor of where the touch landed so we can identify
  // which element is intercepting (or failing to intercept) the gesture.
  try {
    if (!el || !el.tagName) return "?";
    var tag = el.tagName.toLowerCase();
    var id = el.id ? "#" + el.id : "";
    var cls = "";
    if (el.className && typeof el.className === "string" && el.className.trim()) {
      // First two class names only — full list would bloat the log.
      var parts = el.className.trim().split(/\s+/).slice(0, 2);
      cls = "." + parts.join(".");
    }
    var role = el.getAttribute ? (el.getAttribute("role") || el.getAttribute("aria-label") || "") : "";
    var ta = "";
    try {
      var cs = window.getComputedStyle(el);
      ta = " ta=" + (cs.touchAction || "?");
    } catch(_){}
    return tag + id + cls + (role ? "[" + role + "]" : "") + ta;
  } catch(_) { return "?"; }
}
// Walk up from the touched element until we find an ancestor with
// overflow:auto/scroll. Report its dimensions so we can tell if scroll
// is mathematically possible (scrollHeight > clientHeight etc.) and its
// computed touch-action.
function bfFindScrollableAncestor(el) {
  try {
    var node = el;
    var depth = 0;
    while (node && node !== document.body && depth < 12) {
      var cs = window.getComputedStyle(node);
      var ox = cs.overflowX, oy = cs.overflowY;
      if (ox === "auto" || ox === "scroll" || oy === "auto" || oy === "scroll") {
        return {
          tag: node.tagName.toLowerCase(),
          ta: cs.touchAction,
          ovX: ox, ovY: oy,
          cw: node.clientWidth, ch: node.clientHeight,
          sw: node.scrollWidth, sh: node.scrollHeight,
          sl: node.scrollLeft, st: node.scrollTop,
          canScrollX: node.scrollWidth > node.clientWidth,
          canScrollY: node.scrollHeight > node.clientHeight,
          depth: depth,
        };
      }
      node = node.parentNode;
      depth++;
    }
  } catch(_){}
  return null;
}
function bfInstallTouchTracer() {
  if (typeof window === "undefined" || typeof document === "undefined") return;
  if (window._bf_touch_tracer_installed) return;
  window._bf_touch_tracer_installed = true;
  var lastMoveLog = 0;
  var moveCount = 0;
  function trace(ev) {
    if (!bfTouchTraceOn()) return;
    try {
      var now = Date.now();
      var touches = ev.touches ? ev.touches.length : 0;
      var tgt = bfDescribeTouchTarget(ev.target);
      var pos = "";
      if (ev.touches && ev.touches[0]) {
        pos = " @(" + Math.round(ev.touches[0].clientX) + "," + Math.round(ev.touches[0].clientY) + ")";
      }
      if (ev.type === "touchmove") {
        // Throttle move logs — 100/sec is too noisy. Sample every 50ms
        // and count how many we skipped.
        moveCount++;
        if (now - lastMoveLog < 50) return;
        var msg = "[TOUCH] move x" + moveCount + " touches=" + touches + pos + " target=" + tgt + " cancelable=" + ev.cancelable + " defaultPrev=" + ev.defaultPrevented;
        bfDebugLog(msg);
        lastMoveLog = now;
        moveCount = 0;
        return;
      }
      moveCount = 0; lastMoveLog = 0;
      var msg2 = "[TOUCH] " + ev.type + " touches=" + touches + pos + " target=" + tgt + " cancelable=" + ev.cancelable + " defaultPrev=" + ev.defaultPrevented;
      bfDebugLog(msg2);
      // On touchstart, log the closest scrollable ancestor so we can see
      // if scroll is possible at all (container dimensions, overflow,
      // touch-action). Only for touchstart to avoid spam.
      if (ev.type === "touchstart") {
        var sa = bfFindScrollableAncestor(ev.target);
        if (sa) {
          bfDebugLog("[TOUCH]   scrollable_parent=" + sa.tag + " d=" + sa.depth +
            " ta=" + sa.ta + " ovX=" + sa.ovX + " ovY=" + sa.ovY +
            " size=" + sa.cw + "x" + sa.ch + " content=" + sa.sw + "x" + sa.sh +
            " scrollPos=(" + sa.sl + "," + sa.st + ")" +
            " canX=" + sa.canScrollX + " canY=" + sa.canScrollY);
        } else {
          bfDebugLog("[TOUCH]   no scrollable_parent found");
        }
      }
    } catch(_){}
  }
  // Capture phase so we see events BEFORE any handler can stopPropagation.
  // Passive: true so we never block anything just by observing.
  document.addEventListener("touchstart",  trace, { capture: true, passive: true });
  document.addEventListener("touchmove",   trace, { capture: true, passive: true });
  document.addEventListener("touchend",    trace, { capture: true, passive: true });
  document.addEventListener("touchcancel", trace, { capture: true, passive: true });
  // Also listen for scroll events on any element so we can confirm
  // whether the OS actually started native scrolling.
  document.addEventListener("scroll", function(ev){
    if (!bfTouchTraceOn()) return;
    try {
      var tgt = bfDescribeTouchTarget(ev.target);
      bfDebugLog("[SCROLL] target=" + tgt + " scrollTop=" + (ev.target.scrollTop|0) + " scrollLeft=" + (ev.target.scrollLeft|0));
    } catch(_){}
  }, { capture: true, passive: true });
}
// Install immediately on first script load so listeners are in place before
// any user touch. Idempotent — guarded by _bf_touch_tracer_installed.
try { bfInstallTouchTracer(); } catch(_){}
// Build marker so we know which bundle is actually running.
try { if (typeof window !== "undefined" && bfDebugLog) bfDebugLog("[BUILD] eq-debug125 loaded " + new Date().toISOString()); } catch(_){}
// Global CSS: range sliders claim their touch gesture exclusively so
// parent scrollable containers can't steal a horizontal slider drag
// when a tiny vertical wobble happens during the slide. Applied
// app-wide so every <input type="range"> behaves consistently.
try {
  if (typeof document !== "undefined" && !document.getElementById("bf-range-touch-css")) {
    var bfRangeStyle = document.createElement("style");
    bfRangeStyle.id = "bf-range-touch-css";
    bfRangeStyle.textContent = 'input[type="range"]{touch-action:none;}';
    (document.head || document.documentElement).appendChild(bfRangeStyle);
  }
} catch(_){}

// Module-level clipboards for cross-editor copy/paste.
// _BF_NOTES_CLIPBOARD: { notes: [{pitch,start,duration,velocity}...], minStart: number } | null
// _BF_DRUM_PATTERN_CLIPBOARD: { channels: [...full channel snapshots], lengthSteps, stepsPerBeat } | null
// Plain JS objects on the window (not React state), so they survive editor
// unmounts. Any piano-roll editor reads/writes _BF_NOTES_CLIPBOARD; any
// drum rack reads/writes _BF_DRUM_PATTERN_CLIPBOARD.
if (typeof window !== "undefined") {
  if (window._BF_NOTES_CLIPBOARD === undefined) window._BF_NOTES_CLIPBOARD = null;
  if (window._BF_DRUM_PATTERN_CLIPBOARD === undefined) window._BF_DRUM_PATTERN_CLIPBOARD = null;
}
function bfGetNotesClipboard() {
  try { return (typeof window !== "undefined") ? window._BF_NOTES_CLIPBOARD : null; }
  catch(_) { return null; }
}
function bfSetNotesClipboard(payload) {
  try { if (typeof window !== "undefined") window._BF_NOTES_CLIPBOARD = payload; }
  catch(_){}
}
function bfGetDrumPatternClipboard() {
  try { return (typeof window !== "undefined") ? window._BF_DRUM_PATTERN_CLIPBOARD : null; }
  catch(_) { return null; }
}
function bfSetDrumPatternClipboard(payload) {
  try { if (typeof window !== "undefined") window._BF_DRUM_PATTERN_CLIPBOARD = payload; }
  catch(_){}
}
function bfDebugLog(line) {
  try {
    var raw = null;
    try { raw = sessionStorage.getItem(_BF_DEBUG_LOG_KEY); } catch(_) {}
    var log = raw ? JSON.parse(raw) : [];
    var ts = new Date().toLocaleTimeString();
    log.push(ts + "  " + line);
    var capped = log.length > 300 ? log.slice(-300) : log;
    try { sessionStorage.setItem(_BF_DEBUG_LOG_KEY, JSON.stringify(capped)); } catch(_) {}
  } catch(e) {}
}
// Verbose engine logging — OFF by default so normal use isn't spammed with
// per-note/per-route lines. Toggle from the Debug Log panel when needed.
// The flag is read fresh from sessionStorage each call so toggling takes
// effect immediately without a reload.
function bfDebugVerboseOn() {
  try { return sessionStorage.getItem(_BF_DEBUG_VERBOSE_KEY) === "1"; } catch(_) { return false; }
}
function bfDebugLogVerbose(line) {
  if (!bfDebugVerboseOn()) return;
  bfDebugLog(line);
}
try {
  if (typeof window !== "undefined") {
    window.__bfDebugLog = bfDebugLog;
    window.__bfDebugVerboseGet = bfDebugVerboseOn;
    window.__bfDebugVerboseSet = function(on){ try { sessionStorage.setItem(_BF_DEBUG_VERBOSE_KEY, on ? "1" : "0"); } catch(_){} };
    // Touch tracer toggles — used by the debug log panel button.
    window.__bfTouchTraceGet = bfTouchTraceOn;
    window.__bfTouchTraceSet = function(on){ try { sessionStorage.setItem(_BF_TOUCH_TRACE_KEY, on ? "1" : "0"); } catch(_){} };
  }
} catch(_) {}


function getStoredPlayCount(beatId) {
  return _playCountStore[beatId] || 0;
}

function setStoredPlayCount(beatId, count) {
  _playCountStore[beatId] = count;
  window.dispatchEvent(new CustomEvent("bf:playCountUpdate", { detail: { beatId, count } }));
}

function usePlayCount(beatId, initialCount) {
  var [count, setCount] = React.useState(
    _playCountStore[beatId] !== undefined ? _playCountStore[beatId] : (initialCount || 0)
  );
  React.useEffect(function() {
    // Seed the store with the initial value from the API if not already set
    if (_playCountStore[beatId] === undefined && initialCount !== undefined) {
      _playCountStore[beatId] = initialCount || 0;
    }
    function handler(e) {
      if (e.detail.beatId === beatId) setCount(e.detail.count);
    }
    window.addEventListener("bf:playCountUpdate", handler);
    return function() { window.removeEventListener("bf:playCountUpdate", handler); };
  }, [beatId, initialCount]);
  return count;
}

// Called when audio actually starts playing. Enforces:
// 1. 3-second minimum before counting (prevents accidental taps)
// 2. 30-minute client-side cooldown (backup to server cooldown)
// 3. Optimistic UI update, then reconcile with server response
function recordPlay(beatId) {
  if (!beatId) return;

  // Client-side cooldown check — 30 min
  var lastPlayed = _playedThisSession[beatId];
  if (lastPlayed && (Date.now() - lastPlayed) < 30 * 60 * 1000) return;

  // Cancel any existing timer for this beat (e.g. user stopped before 3s)
  if (_playTimers[beatId]) {
    clearTimeout(_playTimers[beatId]);
    delete _playTimers[beatId];
  }

  // Start 3-second timer — only record if user actually listens
  _playTimers[beatId] = setTimeout(function() {
    delete _playTimers[beatId];
    _playedThisSession[beatId] = Date.now();

    // Optimistic update
    var current = _playCountStore[beatId] || 0;
    setStoredPlayCount(beatId, current + 1);

    // Server-side record with anti-spam
    apiFetch("/api/auth/beat-play/" + beatId, { method: "POST" })
      .then(function(res) {
        if (res && res.playCount !== undefined) {
          // Reconcile with server's authoritative count
          setStoredPlayCount(beatId, res.playCount);
        }
      })
      .catch(function() {
        // Server failed — roll back optimistic update
        setStoredPlayCount(beatId, Math.max(0, (_playCountStore[beatId] || 1) - 1));
      });
  }, 3000);
}

// Cancel the 3s timer if the user stops before minimum play time
function cancelPlayTimer(beatId) {
  if (_playTimers[beatId]) {
    clearTimeout(_playTimers[beatId]);
    delete _playTimers[beatId];
  }
}

// =============================================================================
// GLOBAL PREVIEW COORDINATOR
// Ensures only one beat/track preview plays at a time across all components.
// Any component that starts a preview calls startGlobalPreview(id).
// Any component that wants to stop another's preview listens with useGlobalPreviewStop.
// =============================================================================
var _activePreviewId = null;
function startGlobalPreview(id) {
  if (_activePreviewId && _activePreviewId !== id) {
    window.dispatchEvent(new CustomEvent("bf:stopPreview", { detail: { except: id } }));
  }
  _activePreviewId = id;
}
function clearGlobalPreview(id) {
  if (_activePreviewId === id) _activePreviewId = null;
}
function useGlobalPreviewStop(myId, stopFn) {
  React.useEffect(function() {
    function handler(e) {
      if (e.detail.except !== myId) stopFn();
    }
    window.addEventListener("bf:stopPreview", handler);
    return function() { window.removeEventListener("bf:stopPreview", handler); };
  }, [myId, stopFn]);
}

// Spotify embed coordinator — only one embed loads at a time.
// ── Open an external URL without leaving a blank tab behind ──────────
// iOS Safari treats a programmatic <a target="_blank"> click DIFFERENTLY
// from window.open(): it doesn't fire the "attempting to open pop-up"
// confirmation, AND it keeps the original tab alive in the background
// rather than blanking it. This is the standard pattern for opening
// external links from a SPA on iOS Safari.
//
// For iOS PWA the same anchor approach works — iOS opens the URL
// externally and the PWA stays running.
//
// Desktop/Android: same anchor approach for consistency and security
// (noopener prevents the new tab from accessing window.opener).
function openExternalLink(url) {
  if (!url) return;
  var normalised = url.indexOf("http") === 0 ? url : "https://" + url;
  // Create a one-shot anchor, click it, then remove it. iOS recognises
  // this as a user-initiated navigation (because we're still inside the
  // tap handler's call stack) and opens the link in a new tab without
  // showing the pop-up confirmation prompt. The original BeatFinder tab
  // stays mounted in the background — no bfcache trickery needed.
  var a = document.createElement("a");
  a.href = normalised;
  a.target = "_blank";
  a.rel = "noopener noreferrer";
  a.style.display = "none";
  document.body.appendChild(a);
  a.click();
  setTimeout(function() {
    try { document.body.removeChild(a); } catch (e) {}
  }, 100);
}

// Audio player for uploaded songs (R2-hosted). Artwork + play/pause + scrub
// bar + time. Pure <audio> under the hood so it streams and seeks natively.
// Persistent audio objects keyed by src, living OUTSIDE React. The profile's
// ContentCard is redefined on every render, so its subtree (incl any JSX
// <audio>) remounts constantly and would restart playback. Backing playback
// with a cached Audio() object means remounts just re-attach to the same
// still-playing audio instead of recreating it.
var __bfAudioCache = {};
// Global "now playing" state so a mini player bar can control the active song
// from any page. Holds the current track's src + metadata + playing flag.
var __bfNowPlaying = { src: null, title: "", artist: "", artwork: "", playing: false, closed: false };
var __bfNPSubs = [];
function __bfNotifyNP() { __bfNPSubs.forEach(function(fn){ try { fn(); } catch(e){} }); }
function __bfSetNowPlaying(info) {
  __bfNowPlaying = Object.assign({}, __bfNowPlaying, info, { closed: false });
  __bfNotifyNP();
}
function __bfGetAudio(src) {
  if (!__bfAudioCache[src]) {
    var a = new Audio(src);
    a.preload = "metadata";
    __bfAudioCache[src] = a;
  }
  return __bfAudioCache[src];
}
// Stop & clear everything (used by the mini player's close/stop).
function __bfStopAll() {
  Object.keys(__bfAudioCache).forEach(function(k){ try { __bfAudioCache[k].pause(); } catch(e){} });
  __bfNowPlaying = { src: null, title: "", artist: "", artwork: "", playing: false, closed: true };
  __bfNotifyNP();
}

var SongPlayer = React.memo(function SongPlayer({ src, title, artist, artwork }) {
  const audio = React.useMemo(function(){ return __bfGetAudio(src); }, [src]);
  const [playing, setPlaying] = React.useState(!audio.paused);
  const [cur, setCur] = React.useState(audio.currentTime || 0);
  const [dur, setDur] = React.useState(isFinite(audio.duration) ? audio.duration : 0);

  React.useEffect(function(){
    function onTime(){ setCur(audio.currentTime || 0); }
    function onMeta(){ setDur(isFinite(audio.duration) ? audio.duration : 0); }
    function onPlay(){ setPlaying(true); __bfSetNowPlaying({ src: src, title: title, artist: artist, artwork: artwork, playing: true }); }
    function onPause(){ setPlaying(false); if (__bfNowPlaying.src === src) __bfSetNowPlaying({ playing: false }); }
    function onEnd(){ setPlaying(false); setCur(0); if (__bfNowPlaying.src === src) __bfSetNowPlaying({ playing: false }); }
    audio.addEventListener("timeupdate", onTime);
    audio.addEventListener("loadedmetadata", onMeta);
    audio.addEventListener("durationchange", onMeta);
    audio.addEventListener("play", onPlay);
    audio.addEventListener("pause", onPause);
    audio.addEventListener("ended", onEnd);
    // sync current state on (re)mount
    setPlaying(!audio.paused); setCur(audio.currentTime || 0);
    if (isFinite(audio.duration)) setDur(audio.duration);
    return function(){
      audio.removeEventListener("timeupdate", onTime);
      audio.removeEventListener("loadedmetadata", onMeta);
      audio.removeEventListener("durationchange", onMeta);
      audio.removeEventListener("play", onPlay);
      audio.removeEventListener("pause", onPause);
      audio.removeEventListener("ended", onEnd);
      // NOTE: deliberately do NOT pause on unmount — that's what let remounts
      // stop the music. The audio keeps playing; we just detach listeners.
    };
  }, [audio]);

  function fmt(t) {
    if (!t || !isFinite(t)) return "0:00";
    var m = Math.floor(t / 60), s = Math.floor(t % 60);
    return m + ":" + (s < 10 ? "0" : "") + s;
  }
  function toggle() {
    if (audio.paused) {
      // Pause any other cached audio so only one plays at a time.
      Object.keys(__bfAudioCache).forEach(function(k){ if (k !== src) { try { __bfAudioCache[k].pause(); } catch(e){} } });
      __bfSetNowPlaying({ src: src, title: title, artist: artist, artwork: artwork, playing: true });
      audio.play();
    } else {
      audio.pause();
    }
  }
  function seek(e) {
    if (!dur) return;
    audio.currentTime = parseFloat(e.target.value);
    setCur(audio.currentTime);
  }

  return (
    <div style={{ display: "flex", alignItems: "center", gap: 12, padding: 12,
      background: "linear-gradient(135deg,rgba(176,38,255,0.10),rgba(0,213,255,0.05))",
      borderBottom: "1px solid rgba(176,38,255,0.18)" }}>
      <div style={{ width: 58, height: 58, borderRadius: 12, flexShrink: 0, overflow: "hidden",
        background: artwork ? "#000" : "linear-gradient(135deg,#6B21A8,#C026D3)",
        display: "flex", alignItems: "center", justifyContent: "center",
        boxShadow: "0 0 16px rgba(176,38,255,0.3)" }}>
        {artwork
          ? <img src={artwork} alt="" style={{ width: "100%", height: "100%", objectFit: "cover" }} />
          : <AppIcon id="music" size={26} />}
      </div>
      <button onClick={toggle} style={{ width: 44, height: 44, borderRadius: "50%", flexShrink: 0,
        background: "linear-gradient(135deg,#B026FF,#7D3CFF)", border: "none", cursor: "pointer",
        display: "flex", alignItems: "center", justifyContent: "center", color: "white",
        boxShadow: "0 0 14px rgba(176,38,255,0.5)" }}>
        <AppIcon id={playing ? "pause" : "play"} size={20} />
      </button>
      <div style={{ flex: 1, minWidth: 0 }}>
        <div style={{ color: "#fff", fontWeight: 800, fontSize: 13.5, whiteSpace: "nowrap", overflow: "hidden", textOverflow: "ellipsis" }}>{title || "Untitled"}</div>
        {artist && <div style={{ color: "#A78BFA", fontSize: 11.5, fontWeight: 600, whiteSpace: "nowrap", overflow: "hidden", textOverflow: "ellipsis", marginBottom: 4 }}>{artist}</div>}
        <input type="range" min={0} max={dur || 0} step="0.1" value={cur} onChange={seek}
          style={{ width: "100%", accentColor: "#B026FF", height: 4, cursor: "pointer" }} />
        <div style={{ display: "flex", justifyContent: "space-between", color: "#8b80a8", fontSize: 10, fontWeight: 600 }}>
          <span>{fmt(cur)}</span><span>{fmt(dur)}</span>
        </div>
      </div>
    </div>
  );
});


// Global mini player bar — appears at the bottom on any page while a track is
// playing (or paused but not closed), so users can pause/resume/stop the audio
// after navigating away from the profile they started it on.
function GlobalMiniPlayer() {
  var [, force] = React.useState(0);
  var [cur, setCur] = React.useState(0);
  var [dur, setDur] = React.useState(0);

  React.useEffect(function(){
    function rerender(){ force(function(n){ return n + 1; }); }
    __bfNPSubs.push(rerender);
    var iv = setInterval(function(){
      var src = __bfNowPlaying.src;
      if (src && __bfAudioCache[src]) {
        var a = __bfAudioCache[src];
        setCur(a.currentTime || 0);
        setDur(isFinite(a.duration) ? a.duration : 0);
      }
    }, 500);
    return function(){
      __bfNPSubs = __bfNPSubs.filter(function(fn){ return fn !== rerender; });
      clearInterval(iv);
    };
  }, []);

  var np = __bfNowPlaying;
  if (!np.src || np.closed) return null;

  var audio = __bfAudioCache[np.src];
  function fmt(t){ if (!t || !isFinite(t)) return "0:00"; var m = Math.floor(t/60), s = Math.floor(t%60); return m + ":" + (s<10?"0":"") + s; }
  function toggle(){
    if (!audio) return;
    if (audio.paused) {
      Object.keys(__bfAudioCache).forEach(function(k){ if (k !== np.src) { try { __bfAudioCache[k].pause(); } catch(e){} } });
      audio.play(); __bfSetNowPlaying({ playing: true });
    } else {
      audio.pause(); __bfSetNowPlaying({ playing: false });
    }
  }
  function seek(e){ if (audio && dur) { audio.currentTime = parseFloat(e.target.value); setCur(audio.currentTime); } }

  return (
    <div style={{ position: "fixed", left: "50%", transform: "translateX(-50%)", width: "calc(100% - 20px)", maxWidth: 800, bottom: "calc(64px + env(safe-area-inset-bottom))", zIndex: 9000,
      borderRadius: 14, padding: "10px 12px",
      background: "rgba(15,12,24,0.97)", backdropFilter: "blur(12px)",
      border: "1px solid rgba(176,38,255,0.4)", boxShadow: "0 6px 24px rgba(0,0,0,0.5), 0 0 18px rgba(176,38,255,0.25)",
      display: "flex", alignItems: "center", gap: 10 }}>
      <div style={{ width: 40, height: 40, borderRadius: 9, flexShrink: 0, overflow: "hidden",
        background: np.artwork ? "#000" : "linear-gradient(135deg,#6B21A8,#C026D3)",
        display: "flex", alignItems: "center", justifyContent: "center" }}>
        {np.artwork ? <img src={np.artwork} alt="" style={{ width: "100%", height: "100%", objectFit: "cover" }} /> : <AppIcon id="music" size={18} />}
      </div>
      <button onClick={toggle} style={{ width: 38, height: 38, borderRadius: "50%", flexShrink: 0,
        background: "linear-gradient(135deg,#B026FF,#7D3CFF)", border: "none", cursor: "pointer",
        display: "flex", alignItems: "center", justifyContent: "center", color: "white" }}>
        <AppIcon id={np.playing ? "pause" : "play"} size={17} />
      </button>
      <div style={{ flex: 1, minWidth: 0 }}>
        <div style={{ color: "#fff", fontWeight: 800, fontSize: 12.5, whiteSpace: "nowrap", overflow: "hidden", textOverflow: "ellipsis" }}>{np.title || "Now playing"}</div>
        <div style={{ display: "flex", alignItems: "center", gap: 6 }}>
          <input type="range" min={0} max={dur || 0} step="0.1" value={cur} onChange={seek}
            style={{ flex: 1, accentColor: "#B026FF", height: 3, cursor: "pointer" }} />
          <span style={{ color: "#8b80a8", fontSize: 9, fontWeight: 600, flexShrink: 0 }}>{fmt(cur)}</span>
        </div>
      </div>
      <button onClick={__bfStopAll} title="Stop & close" style={{ width: 32, height: 32, borderRadius: "50%", flexShrink: 0,
        background: "rgba(255,255,255,0.06)", border: "1px solid rgba(255,255,255,0.15)", cursor: "pointer",
        display: "flex", alignItems: "center", justifyContent: "center", color: "#bbb" }}>
        <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="#ccc" strokeWidth="2.4" strokeLinecap="round"><line x1="18" y1="6" x2="6" y2="18"/><line x1="6" y1="6" x2="18" y2="18"/></svg>
      </button>
    </div>
  );
}


function SpotifyEmbed({ embedUrl, height, style, itemId }) {
  var [active, setActive] = React.useState(false);
  var idRef = React.useRef("spotify_" + itemId);
  var h  = height || 152;
  var br = (style && style.borderRadius) ? style.borderRadius : 0;

  // Normalise Spotify URL to the correct embed format
  // Handles: open.spotify.com/track/ID → open.spotify.com/embed/track/ID
  // Also handles old embed.spotify.com format
  function normaliseEmbedUrl(url) {
    if (!url) return url;
    // Already correct embed format
    if (url.includes("open.spotify.com/embed/")) return url;
    // Old embed domain
    if (url.includes("embed.spotify.com/")) {
      return url.replace("embed.spotify.com/", "open.spotify.com/embed/");
    }
    // open.spotify.com/track/ID or /album/ID or /playlist/ID
    var m = url.match(/open\.spotify\.com\/(track|album|playlist|episode|show)\/([A-Za-z0-9]+)/);
    if (m) return "https://open.spotify.com/embed/" + m[1] + "/" + m[2];
    return url;
  }

  var finalEmbedUrl = normaliseEmbedUrl(embedUrl);

  // Listen for another preview activating — deactivate self (unmounts iframe)
  React.useEffect(function() {
    function handler(e) {
      if (e.detail.except !== idRef.current) setActive(false);
    }
    window.addEventListener("bf:stopPreview", handler);
    return function() { window.removeEventListener("bf:stopPreview", handler); };
  }, []);

  function activate() {
    startGlobalPreview(idRef.current);
    setActive(true);
  }

  var [broken, setBroken] = React.useState(false);
  var iframeRef = React.useRef(null);

  // Detect broken embed via load timeout — if Spotify errors, page is tiny/blank
  React.useEffect(function() {
    if (!active || broken) return;
    var timer = setTimeout(function() {
      try {
        var iframe = iframeRef.current;
        if (!iframe) return;
        // If iframe loaded but content height is very small, it's likely an error page
        // We can't read cross-origin content, so just leave it — user sees Spotify error
      } catch(e) {}
    }, 3000);
    return function() { clearTimeout(timer); };
  }, [active]);

  return (
    <div style={Object.assign({}, style, {
      position: "relative", height: h, overflow: "hidden", borderRadius: br,
    })}>
      <iframe
        ref={iframeRef}
        key={active ? "active" : "inactive"}
        src={finalEmbedUrl + (finalEmbedUrl && finalEmbedUrl.includes("?") ? "&" : "?") + "utm_source=generator&theme=0"}
        width="100%"
        height={h}
        frameBorder="0"
        allow="autoplay; clipboard-write; encrypted-media; fullscreen; picture-in-picture"
        loading={active ? "eager" : "lazy"}
        style={{ display: "block", border: "none" }}
      />
      {!active && (
        <div onClick={activate} style={{
          position: "absolute", inset: 0,
          background: "rgba(0,0,0,0.45)",
          display: "flex", alignItems: "center", justifyContent: "center",
          cursor: "pointer", borderRadius: br,
        }}>
          <div style={{
            background: "rgba(0,0,0,0.6)", borderRadius: 20,
            padding: "8px 18px", border: "1px solid rgba(255,255,255,0.15)",
          }}>
            <span style={{ color: "white", fontSize: 13, fontWeight: 700,
              letterSpacing: 0.3, textShadow: "0 1px 4px rgba(0,0,0,0.8)" }}>
              Tap here to listen
            </span>
          </div>
        </div>
      )}
    </div>
  );
}

// Global content-deletion event bus
// Any component that deletes content dispatches this; any that displays it listens.
function dispatchContentDeleted(id, kind) {
  window.dispatchEvent(new CustomEvent("bf:contentDeleted", { detail: { id: id, kind: kind } }));
}
function useContentDeletedListener(callback) {
  React.useEffect(function() {
    function handler(e) { callback(e.detail); }
    window.addEventListener("bf:contentDeleted", handler);
    return function() { window.removeEventListener("bf:contentDeleted", handler); };
  }, [callback]);
}

// Fired when a beat is edited — all card lists re-fetch to show latest data
function dispatchBeatUpdated(beatId, patch) {
  window.dispatchEvent(new CustomEvent("bf:beatUpdated", { detail: { beatId: beatId, patch: patch } }));
}
function useBeatUpdatedListener(callback) {
  React.useEffect(function() {
    function handler(e) { callback(e.detail); }
    window.addEventListener("bf:beatUpdated", handler);
    return function() { window.removeEventListener("bf:beatUpdated", handler); };
  }, [callback]);
}

// Dispatched anywhere in the tree when a Free user tries a Pro action.
// The top-level BeatFinder component listens and shows the upgrade modal.
// reason ∈ "save" | "buy" | "download" | "rhymefinder" | "studio" | "producer"
function requireUpgrade(reason) {
  window.dispatchEvent(new CustomEvent("bf:requireUpgrade", { detail: { reason: reason } }));
}

// -- In-memory cache (10 minutes TTL) -----------------------------------------
const cache = {};
const CACHE_TTL = 10 * 60 * 1000;
const CACHE_VER = "v2";

// =============================================================================
// API HELPERS
// These call your FastAPI backend - which in turn calls YouTube server-side.
// This fixes the iOS Safari CORS block permanently.
// =============================================================================

// Read JWT token saved after login
function getToken() {
  try { return localStorage.getItem("bf_token") || null; }
  catch { return null; }
}

// Save token after login/register
function saveToken(token) {
  try { localStorage.setItem("bf_token", token); } catch {}
}

// Remove token on logout
function clearToken() {
  try { localStorage.removeItem("bf_token"); } catch {}
  try { localStorage.removeItem("bf_is_admin"); } catch {}
}

// Custom error class for API failures. Carries the HTTP status so callers
// can branch on it (e.g. `if (e.status === 401) ...`). The .message is
// always a user-friendly string ready to show in the UI.
class ApiError extends Error {
  constructor(message, status, raw) {
    super(message);
    this.name   = "ApiError";
    this.status = status;
    this.raw    = raw;  // original server response, useful for debugging
  }
}

// Map common HTTP status codes to friendly default messages. The server's
// own `detail` is preferred when it looks user-friendly (sentence-case
// English, not a code path). Backend devs sometimes return raw tracebacks
// or jargon like "Could not validate credentials" — we replace those.
function bfFriendlyErrorMessage(status, serverDetail) {
  // If server detail is short, looks like a real sentence, and isn't a
  // known unfriendly pattern, prefer it — backend devs know context.
  var unfriendlyPatterns = [
    /could not validate credentials/i,
    /token.*expired/i,
    /jwt/i,
    /traceback/i,
    /500.*internal/i,
    /unauthoriz/i,
    /forbidden/i,
    /^http \d+/i,
    /^api error/i,
    /^\{/, // raw JSON
  ];
  // serverDetail comes from response.json().detail. FastAPI returns this
  // as a string for HTTPException, but as an ARRAY of validation objects
  // for Pydantic 422 errors. We coerce to string defensively so .trim()
  // can never throw "is not a function" — which was crashing the login
  // screen when the backend returned a structured validation response.
  var detail;
  if (serverDetail == null) {
    detail = "";
  } else if (typeof serverDetail === "string") {
    detail = serverDetail.trim();
  } else if (Array.isArray(serverDetail)) {
    // Pydantic validation array — pull the first msg if present
    var first = serverDetail[0];
    detail = (first && typeof first === "object" && first.msg)
      ? String(first.msg).trim()
      : "";
  } else if (typeof serverDetail === "object") {
    detail = (serverDetail.msg ? String(serverDetail.msg) : JSON.stringify(serverDetail)).trim();
  } else {
    detail = String(serverDetail).trim();
  }
  var detailIsFriendly =
    detail &&
    detail.length > 0 &&
    detail.length < 250 &&
    !unfriendlyPatterns.some(function(p) { return p.test(detail); });

  if (detailIsFriendly) return detail;

  // Fall back to a friendly default keyed by status.
  switch (status) {
    case 0:   return "Connection problem — check your internet and try again.";
    case 400: return "Something wasn't quite right with that request. Try again.";
    case 401: return "You've been signed out. Please sign back in to continue.";
    case 403: return "You don't have access to that.";
    case 404: return "We couldn't find that — it may have been deleted.";
    case 409: return "That action couldn't complete — the item changed before your request finished. Refresh and try again.";
    case 413: return "That file is too large to upload.";
    case 422: return "Some of those details aren't valid. Please double-check and try again.";
    case 429: return "Slow down — too many requests at once. Try again in a moment.";
    case 500:
    case 502:
    case 503:
    case 504: return "Something went wrong on our end. We've been notified — please try again in a moment.";
    default:  return "Couldn't complete that — please try again.";
  }
}

// ── Auth-expiry verification (debounced) ─────────────────────────────────
// When apiFetch sees a 401 it does NOT immediately sign the user out.
// Background polls (DMs every 4s, feed every 30s, notifications) can get
// a transient 401 from a Render cold-start or a brief network hiccup even
// when the token is completely valid. Immediately clearing the token on any
// poll 401 is what was causing users to be signed out randomly.
//
// Instead we schedule a single verification ping to /api/auth/me (debounced
// so rapid consecutive 401s from multiple polls don't fire multiple pings).
// If /me ALSO returns 401, the token is genuinely expired → sign out.
// If /me succeeds, the original 401 was transient → do nothing, user stays in.
//
// Uses raw fetch (not apiFetch) so we can't recurse.
var _authVerifyTimer   = null;
var _authVerifyPending = false;

function scheduleAuthVerification() {
  // Already got a verification in flight or scheduled — don't pile on.
  if (_authVerifyPending) return;
  _authVerifyPending = true;
  clearTimeout(_authVerifyTimer);
  // Small delay: let any burst of concurrent 401s (e.g. three polls
  // all failing at once on a cold start) settle before we ping /me.
  _authVerifyTimer = setTimeout(function() {
    const token = getToken();
    if (!token) {
      _authVerifyPending = false;
      return; // already signed out by explicit logout
    }
    fetch(API_BASE + "/api/auth/me", {
      headers: { Authorization: "Bearer " + token }
    }).then(function(r) {
      _authVerifyPending = false;
      if (r.status === 401) {
        // /me also rejected the token — it's genuinely expired.
        try { clearToken(); } catch(_){}
        try {
          window.dispatchEvent(new CustomEvent("bf:auth-expired", {
            detail: { path: "/api/auth/me", verified: true }
          }));
        } catch(_){}
      }
      // If /me succeeded: the earlier 401 was transient. Do nothing —
      // the user remains logged in and their next action will work fine.
    }).catch(function() {
      // Network error on the verification ping itself — don't sign out.
      // We can't tell if the token is valid or not, so err on the side
      // of keeping the user logged in.
      _authVerifyPending = false;
    });
  }, 1500); // 1.5s debounce covers any burst of simultaneous poll 401s
}

// Generic authenticated fetch helper
async function apiFetch(path, options = {}) {
  const token = getToken();
  const headers = {
    "Content-Type": "application/json",
    ...(token ? { Authorization: `Bearer ${token}` } : {}),
    ...(options.headers || {}),
  };

  let res;
  try {
    res = await fetch(`${API_BASE}${path}`, { ...options, headers });
  } catch (networkErr) {
    // fetch() throws on actual network failures (no connection, DNS
    // problem, request aborted, CORS preflight failure). Translate to
    // a friendly ApiError with status 0 so callers can handle it the
    // same way as a regular HTTP failure.
    throw new ApiError(
      bfFriendlyErrorMessage(0, ""),
      0,
      networkErr && networkErr.message ? networkErr.message : "network error"
    );
  }

  if (!res.ok) {
    const raw = await res.json().catch(() => ({ detail: res.statusText }));
    const serverDetail = raw && (raw.detail || raw.message || raw.error) || "";

    // Special-case 401: token may be invalid/expired. Rather than
    // immediately clearing the token (which signs the user out), we
    // schedule a verification ping to /api/auth/me. If that ALSO returns
    // 401 then the token is genuinely dead and we sign out. If /me
    // succeeds, this 401 was a transient error (Render cold-start, DM/
    // notification poll during a brief network hiccup) and we do nothing.
    // Background-poll callers can pass { silent: true } to suppress even
    // the verification attempt — if the user's token is fine they'll learn
    // that from the next user-triggered request instead.
    if (res.status === 401 && token && !options.silent) {
      scheduleAuthVerification();
    }

    throw new ApiError(
      bfFriendlyErrorMessage(res.status, serverDetail),
      res.status,
      serverDetail
    );
  }
  return res.json();
}

// =============================================================================
// AUTOLINK + LINK PREVIEW HELPERS
// Shared by FollowingFeed and PublicProfileScreen so URLs inside post text
// render as underlined tappable links, and the post's link preview card
// renders consistently in both places.
// =============================================================================

// Match http(s) URLs greedily, stopping at whitespace and angle brackets.
// We trim trailing punctuation ourselves below — that's nicer than baking
// it into the regex because it preserves URLs that legitimately end in
// punctuation (e.g. trailing slash).
var BF_URL_REGEX = /(https?:\/\/[^\s<>]+)/gi;

// Open a URL in the user-appropriate way. iOS PWAs misbehave with
// target="_blank" (the blank "Done screen" issue), so when running in a
// home-screen-installed PWA we let the anchor's default navigate the
// current tab — iOS hands off to the YouTube app, Spotify app, etc via
// universal links. Outside PWA we keep target="_blank" so users don't
// lose their place. The render code below sets target accordingly.
function bfIsPWAiOS() {
  try {
    var ua = navigator.userAgent || "";
    var standalone =
      (window.matchMedia && window.matchMedia("(display-mode: standalone)").matches) ||
      window.navigator.standalone === true;
    return standalone && /iPad|iPhone|iPod/.test(ua);
  } catch(_) { return false; }
}

// Live "Xs / Xm / Xh / Xd ago" formatter — used by both Feed and
// public-profile post headers so timestamps tick in real time on every
// surface that shows them. Pure function; the live update comes from
// the consuming component re-rendering on a setInterval tick (see
// useLiveTick below).
function bfTimeAgo(iso) {
  if (!iso) return "";
  var diff = Date.now() - new Date(iso).getTime();
  if (diff < 0) diff = 0;
  var secs = Math.floor(diff / 1000);
  if (secs < 60) return secs + "s ago";
  var mins = Math.floor(secs / 60);
  if (mins < 60) return mins + "m ago";
  var hrs = Math.floor(mins / 60);
  if (hrs < 24) return hrs + "h ago";
  var days = Math.floor(hrs / 24);
  if (days < 7) return days + "d ago";
  var weeks = Math.floor(days / 7);
  if (weeks < 4) return weeks + "w ago";
  return Math.floor(days / 30) + "mo ago";
}

// Force a re-render of the calling component every `intervalMs` ms so
// any bfTimeAgo() call inside it updates live. Hook is opt-in per
// component — keeps the timer scoped and cheap.
function useLiveTick(intervalMs) {
  var setTick = React.useState(0)[1];
  React.useEffect(function() {
    var id = setInterval(function() { setTick(function(n) { return n + 1; }); }, intervalMs || 10000);
    return function() { clearInterval(id); };
  }, [intervalMs]);
}

// Split `text` into an array of strings and React anchor elements so URLs
// inside post bodies render as underlined tappable links. The whole array
// can be rendered in place of {post.text}. We trim trailing punctuation
// off each matched URL (period at end of sentence, etc) but show the
// trimmed punctuation back in the surrounding text node so nothing is
// lost visually.
function renderTextWithLinks(text) {
  if (!text) return null;
  var pwa = bfIsPWAiOS();
  var parts = [];
  var lastIdx = 0;
  var m;
  // Reset the regex's lastIndex each call (it's a /g regex, so state
  // would leak between calls otherwise).
  BF_URL_REGEX.lastIndex = 0;
  var keyN = 0;
  while ((m = BF_URL_REGEX.exec(text)) !== null) {
    var fullMatch = m[0];
    var matchStart = m.index;
    var matchEnd   = matchStart + fullMatch.length;
    // Strip common trailing punctuation back into the surrounding text
    var trimmed = fullMatch;
    var tail    = "";
    var tailMatch = /[.,;!?)\]]+$/.exec(trimmed);
    if (tailMatch) {
      tail    = tailMatch[0];
      trimmed = trimmed.slice(0, trimmed.length - tail.length);
    }
    // Append the text BEFORE the URL match (if any)
    if (matchStart > lastIdx) {
      parts.push(text.slice(lastIdx, matchStart));
    }
    parts.push(
      React.createElement("a", {
        key:    "u" + (keyN++),
        href:   trimmed,
        target: pwa ? undefined : "_blank",
        rel:    "noopener noreferrer",
        onClick: function(e) { e.stopPropagation(); },
        style:  {
          color:          "#A78BFA",
          textDecoration: "underline",
          textUnderlineOffset: 2,
          wordBreak:      "break-all",
        },
      }, trimmed)
    );
    if (tail) parts.push(tail);
    lastIdx = matchEnd;
  }
  if (lastIdx < text.length) parts.push(text.slice(lastIdx));
  return parts.length ? parts : text;
}

// Renders a post's link preview card. Uses post.linkImage when set,
// otherwise a hash-derived gradient placeholder so every link ALWAYS
// has a visual. Tapping opens the URL using the same iOS-aware logic
// as the inline autolinks. Returns null when the post has no linkUrl.
function LinkPreviewCard({ post, margin }) {
  if (!post || !post.linkUrl) return null;
  var host = "";
  try { host = (new URL(post.linkUrl)).hostname.replace(/^www\./, ""); }
  catch(_) { host = post.linkUrl; }
  var displayLabel = (post.linkSiteName || host || "LINK").toUpperCase();
  var palettes = [
    { a: "#C026D3", b: "#7C3AED", c: "#3B82F6" },
    { a: "#3B82F6", b: "#06B6D4", c: "#0EA5E9" },
    { a: "#7C3AED", b: "#C026D3", c: "#EC4899" },
    { a: "#06B6D4", b: "#3B82F6", c: "#7C3AED" },
    { a: "#A855F7", b: "#6366F1", c: "#3B82F6" },
    { a: "#EC4899", b: "#A855F7", c: "#6366F1" },
  ];
  var h = 0;
  for (var i = 0; i < host.length; i++) { h = ((h << 5) - h + host.charCodeAt(i)) | 0; }
  var pal = palettes[Math.abs(h) % palettes.length];
  var initial = (host.replace(/[^a-z0-9]/gi, "")[0] || "?").toUpperCase();
  var hasRealImage = !!post.linkImage;
  var pwa = bfIsPWAiOS();
  return (
    <a href={post.linkUrl}
      target={pwa ? undefined : "_blank"}
      rel="noopener noreferrer"
      onClick={function(e) { e.stopPropagation(); }}
      style={{
        display: "block", textDecoration: "none",
        margin: margin || "0 14px 10px",
        borderRadius: 12, overflow: "hidden",
        background: "linear-gradient(165deg,#120b22 0%,#0a0a14 60%,#080812 100%)",
        border: "1px solid rgba(124,58,237,0.25)",
        cursor: "pointer", color: "inherit",
        WebkitTapHighlightColor: "transparent",
      }}>
      {hasRealImage ? (
        <img src={post.linkImage} alt=""
          style={{ width: "100%", maxHeight: 200, objectFit: "cover", display: "block", background: "#000" }}
          onError={function(e) {
            var img = e.target;
            var ph = img.parentNode && img.parentNode.querySelector(".bf-linkph");
            if (ph) { img.style.display = "none"; ph.style.display = "flex"; }
          }} />
      ) : null}
      <div className="bf-linkph"
        style={{
          display: hasRealImage ? "none" : "flex",
          width: "100%", aspectRatio: "16/9", maxHeight: 200,
          alignItems: "center", justifyContent: "center",
          position: "relative", overflow: "hidden",
          background: "linear-gradient(135deg," + pal.a + " 0%," + pal.b + " 55%," + pal.c + " 100%)",
        }}>
        <div style={{
          position: "absolute", inset: 0,
          background: "radial-gradient(circle at 20% 30%, rgba(255,255,255,0.18), transparent 50%), radial-gradient(circle at 80% 70%, rgba(0,0,0,0.25), transparent 60%)",
        }} />
        <div style={{
          position: "relative", zIndex: 1,
          fontSize: 56, fontWeight: 900, color: "white",
          fontFamily: "'Bebas Neue',sans-serif",
          textShadow: "0 4px 14px rgba(0,0,0,0.4)",
          letterSpacing: 1,
        }}>{initial}</div>
        <div style={{
          position: "absolute", bottom: 0, left: 0, right: 0,
          padding: "6px 12px",
          background: "linear-gradient(180deg,transparent,rgba(0,0,0,0.55))",
          color: "white", fontSize: 11, fontWeight: 800,
          letterSpacing: 0.6, textTransform: "uppercase",
          textAlign: "center",
          textShadow: "0 1px 4px rgba(0,0,0,0.6)",
        }}>{displayLabel}</div>
      </div>
      <div style={{ padding: "10px 12px" }}>
        {post.linkSiteName && (
          <div style={{ color: "#A78BFA", fontSize: 10, fontWeight: 700, letterSpacing: 0.5, textTransform: "uppercase", marginBottom: 3 }}>
            {post.linkSiteName}
          </div>
        )}
        <div style={{ color: "white", fontWeight: 800, fontSize: 13, lineHeight: 1.3, marginBottom: 3,
          display: "-webkit-box", WebkitLineClamp: 2, WebkitBoxOrient: "vertical", overflow: "hidden" }}>
          {post.linkTitle || post.linkUrl}
        </div>
        {post.linkDescription && (
          <div style={{ color: "#8b8b9a", fontSize: 12, lineHeight: 1.4,
            display: "-webkit-box", WebkitLineClamp: 2, WebkitBoxOrient: "vertical", overflow: "hidden" }}>
            {post.linkDescription}
          </div>
        )}
      </div>
    </a>
  );
}

// =============================================================================
// YOUTUBE - calls /api/youtube/search on your FastAPI backend
// Backend makes the real googleapis.com call server-side (no CORS issues)
// =============================================================================
// Words that indicate a track is a song/music video NOT an instrumental beat
// All signals are matched case-insensitively via .toLowerCase() in isLikelyInstrumental
const VOCAL_SIGNALS = [
  // English video signals
  "official video", "official music video", "music video", "official mv",
  "official clip", "official audio", "official single", "official hd",
  "lyrics video", "lyric video", "with lyrics", "letra",
  "visualizer", "visual video", "audio visual",
  // Song/artist signals
  "feat.", "ft.", " ft ", " feat ", "featuring",
  "(clean)", "(explicit)", "(dirty)", "(radio edit)",
  "sing along", "karaoke", "cover", "remix ft",
  "out now", "new song", "new single", "new music",
  // Spanish/Portuguese
  "oficial video", "video oficial", "videoclip oficial",
  "vid oficial", "clip oficial", "musica oficial",
  "clipe oficial", "video clipe", "videoclipe",
  // French/other
  "clip officiel", "video officielle",
  // Short forms
  "(mv)", "[mv]", "m/v", "music vid", "musicvideo",
  // Streaming/release signals
  "vevo", "out now", "available now", "stream now",
  "listen now", "spotify", "apple music",
  // Other giveaways
  "dance video", "dance performance", "live performance",
  "behind the scenes", "bts video", "making of",
];

// Words that are GOOD signals (real type beats)
const BEAT_SIGNALS = [
  "type beat", "type beat)", "type beat]", "instrumental",
  "free beat", "beat free", "no copyright", "(free)", "[free]",
  "prod.", "beat prod", "rap beat", "trap beat", "drill beat",
  "r&b beat", "afrobeat beat", "melodic beat",
];

function isLikelyInstrumental(title) {
  if (!title) return true;
  const t = title.toLowerCase();
  // Reject vocal/video signals FIRST - these override even "type beat" in the title
  if (VOCAL_SIGNALS.some(s => t.includes(s))) return false;
  // Then allow strong beat signals
  if (BEAT_SIGNALS.some(s => t.includes(s))) return true;
  // Reject "Artist - Song" pattern with no beat keywords
  if (t.includes(" - ") && !t.includes("beat") && !t.includes("instrumental") && !t.includes("free")) {
    if (t.includes("official") || t.includes("audio") || t.includes("video") || t.includes("vevo")) return false;
  }
  // Reject if title has no beat-related word at all AND is very short (likely a song title)
  const hasBeatWord = ["beat", "instrumental", "free", "prod", "type", "drill", "trap", "rnb", "afro"].some(w => t.includes(w));
  if (!hasBeatWord && t.length < 30) return false;
  return true;
}

async function fetchBeats(artistName, page, filterTitle, maxResults, extraQueries, depth) {
  const pageNum     = page || 1;
  const maxNum      = maxResults || 10;
  const depthNum    = depth || 1;
  const doFilter    = filterTitle !== false;
  const query       = CACHE_VER + "|" + artistName + "|page" + pageNum + "|filter" + doFilter + "|max" + maxNum + "|depth" + depthNum;
  if (cache[query] && Date.now() - cache[query].ts < CACHE_TTL) {
    return { beats: cache[query].data, error: null, total: cache[query].total || 0 };
  }

  let url = "/api/youtube/search?artist=" + encodeURIComponent(artistName) +
              "&max=" + maxNum + "&page=" + pageNum +
              "&filter_title=" + (doFilter ? "true" : "false");
  if (depthNum > 1) {
    url += "&depth=" + depthNum;
  }
  if (extraQueries) {
    url += "&extra_queries=" + encodeURIComponent(extraQueries);
  }

  // Try up to 2 times - first page of an artist builds the master cache
  // which can take a few seconds on Render free tier
  for (let attempt = 1; attempt <= 2; attempt++) {
    try {
      const data  = await apiFetch(url);
      const raw   = data.beats || [];
      const beats = raw.filter(b => isLikelyInstrumental(b.title));
      cache[query] = { data: beats, ts: Date.now(), total: data.total || 0 };
      return { beats, error: null, total: data.total || 0 };
    } catch (err) {
      if (attempt < 2) {
        await new Promise(r => setTimeout(r, 3000)); // wait 3s then retry
      } else {
        console.error("[BeatFinder] fetchBeats error:", err);
        return { beats: [], error: err.message, total: 0 };
      }
    }
  }
}

// =============================================================================
// AUTH API - register / login / me / upgrade plan
// =============================================================================
const AuthAPI = {
  async register(name, email, password, marketingOptIn) {
    const data = await apiFetch("/api/auth/register", {
      method: "POST",
      // marketing_opt_in defaults to false (GDPR-safe). The signup form
      // surfaces an unticked checkbox; backend records the value + a
      // timestamp for audit purposes.
      body: JSON.stringify({ name, email, password, marketing_opt_in: !!marketingOptIn }),
    });
    saveToken(data.access_token);
    const u = data.user;
    // Pro features require subscriptionActive=true. A fresh signup hasn't paid
    // yet, so subscriptionActive will be false until the Stripe webhook fires
    // after successful checkout. Without this gate, users who close the Stripe
    // tab mid-checkout would have Pro access for the rest of their session.
    const active = !!u.subscriptionActive;
    return { ...u, isPro: active && u.plan === "producer", isArtistPro: active && (u.plan === "artist" || u.plan === "producer") };
  },

  async login(email, password) {
    const data = await apiFetch("/api/auth/login", {
      method: "POST",
      body: JSON.stringify({ email, password }),
    });
    saveToken(data.access_token);
    const u = data.user;
    // Same gate as register — Pro features require an active subscription.
    const active = !!u.subscriptionActive;
    return { ...u, isPro: active && u.plan === "producer", isArtistPro: active && (u.plan === "artist" || u.plan === "producer") };
  },

  async me() {
    return apiFetch("/api/auth/me");
  },

  async upgrade(plan) {
    return apiFetch("/api/auth/upgrade", {
      method: "POST",
      body: JSON.stringify({ plan }),
    });
  },

  logout() {
    clearToken();
  },
};

// =============================================================================
// SAVED BEATS API - syncs with MongoDB via backend
// =============================================================================
const BeatsAPI = {
  async list() {
    return apiFetch("/api/beats/");
  },

  async save(beat) {
    return apiFetch("/api/beats/", {
      method: "POST",
      body: JSON.stringify({ beat }),
    });
  },

  async remove(videoId) {
    return apiFetch(`/api/beats/${videoId}`, { method: "DELETE" });
  },
};

// =============================================================================
// HELPERS
// =============================================================================
const GRAD = [
  ["#6B21A8","#9333EA"],["#0F4C75","#1B6CA8"],["#7C2D12","#C2410C"],
  ["#1E3A5F","#2563EB"],["#4A044E","#86198F"],["#065F46","#059669"],
  ["#7C3AED","#A855F7"],["#9D174D","#EC4899"],["#134E4A","#0D9488"],
  ["#1E1B4B","#4338CA"],["#1a1a2e","#C026D3"],["#0f3460","#F59E0B"],
];
const initials = n => n.split(" ").map(w => w[0]).join("").slice(0,2).toUpperCase();
const watchUrl  = id => `https://www.youtube.com/watch?v=${id}`;
const embedUrl  = id => `https://www.youtube.com/embed/${id}?autoplay=1&playsinline=1&rel=0&modestbranding=1&disablekb=1&iv_load_policy=3&fs=0&color=white&showinfo=0&controls=1&playlist=${id}`;

// =============================================================================
// ARTIST DATABASE
// =============================================================================
const ARTISTS_USA = [
  {id:"21savage",     name:"21 Savage",       cat:"Rap",    flag:"🇺🇸", img:"https://i.ibb.co/fYpYhVVd/IMG-8822.jpg"},
  {id:"drake",        name:"Drake",            cat:"Rap",    flag:"🇺🇸", img:"https://i.ibb.co/2YWV4nrW/IMG-8823.jpg"},
  {id:"future",       name:"Future",           cat:"Melodic Trap",    flag:"🇺🇸", img:"https://i.ibb.co/60Qd6jMC/IMG-8824.jpg"},
  {id:"gunna",        name:"Gunna",            cat:"Melodic Trap",    flag:"🇺🇸", img:"https://i.ibb.co/VcJgbWCs/IMG-8825.jpg"},
  {id:"juicewrld",    name:"Juice WRLD",       cat:"Rap",    flag:"🇺🇸", img:"https://i.ibb.co/jcHpx7y/IMG-8826.jpg"},
  {id:"kanye",        name:"Kanye West",       cat:"Rap",    flag:"🇺🇸", img:"https://i.ibb.co/dsK2fm4n/IMG-8827.webp"},
  {id:"kendrick",     name:"Kendrick Lamar",   cat:"Rap",    flag:"🇺🇸", img:"https://i.ibb.co/bgSH1S6Q/IMG-8830.jpg"},
  {id:"lilbaby",      name:"Lil Baby",         cat:"Rap",    flag:"🇺🇸", img:"https://i.ibb.co/d0D2Y42W/IMG-8829.webp"},
  {id:"lildurk",      name:"Lil Durk",         cat:"Rap",    flag:"🇺🇸", img:"https://i.ibb.co/7J7dP8BR/IMG-8831.webp"},
  {id:"liluzivert",   name:"Lil Uzi Vert",     cat:"Rap",    flag:"🇺🇸", img:"https://i.ibb.co/n8Q57Jss/IMG-8832.webp"},
  {id:"metroboomin",  name:"Metro Boomin",     cat:"Rap",    flag:"🇺🇸", img:"https://i.ibb.co/9mm1S7sM/IMG-8833.jpg"},
  {id:"playboicarti", name:"Playboi Carti",    cat:"Rap",    flag:"🇺🇸", img:"https://i.ibb.co/tM7ZQzSB/IMG-8834.webp"},
  {id:"travis",       name:"Travis Scott",     cat:"Melodic Trap",    flag:"🇺🇸", img:"https://i.ibb.co/Cp0FHyBg/IMG-8836.jpg"},
  {id:"youngthug",    name:"Young Thug",       cat:"Melodic Trap",    flag:"🇺🇸", img:"https://i.ibb.co/zV1sYHXn/IMG-8837.jpg"},
  {id:"nba",          name:"NBA YoungBoy",     cat:"Rap",    flag:"🇺🇸", img:"https://i.ibb.co/mFbR32cY/IMG-8838.webp"},
  {id:"cardib",       name:"Cardi B",          cat:"Rap",    flag:"🇺🇸", img:"https://i.ibb.co/7JMkDL10/IMG-6284.webp"},
  {id:"nickiminaj",   name:"Nicki Minaj",      cat:"Rap",    flag:"🇺🇸", img:"https://i.ibb.co/m5t5f1HL/IMG-4065.webp"},
  {id:"eminem",       name:"Eminem",           cat:"Rap",    flag:"🇺🇸", img:"https://i.ibb.co/6cW6jMHX/IMG-8841.webp"},
  {id:"jcole",        name:"J. Cole",          cat:"Rap",    flag:"🇺🇸", img:"https://i.ibb.co/mVJhFfRt/IMG-8842.webp"},
  {id:"meekmill",     name:"Meek Mill",        cat:"Rap",    flag:"🇺🇸", img:"https://i.ibb.co/mC37B3Lw/IMG-8843.webp"},
  {id:"postmalone",   name:"Post Malone",      cat:"Rap",    flag:"🇺🇸", img:"https://i.ibb.co/kgSFqDRb/IMG-8844.webp"},
  {id:"rodwave",      name:"Rod Wave",         cat:"Melodic Trap",    flag:"🇺🇸", img:"https://i.ibb.co/DPHp7cPh/IMG-8845.jpg"},
  {id:"polo",         name:"Polo G",           cat:"Melodic Trap",    flag:"🇺🇸", img:"https://i.ibb.co/jvfchwXm/IMG-8846.png"},
  {id:"theweeknd",    name:"The Weeknd",       cat:"R&B M",  flag:"🇺🇸", img:"https://i.ibb.co/Z6rN9BrN/IMG-8847.webp"},
  {id:"brysonti",     name:"Bryson Tiller",    cat:"R&B M",  flag:"🇺🇸", img:"https://i.ibb.co/QjTk82kR/IMG-8848.webp"},
  {id:"chrisb",       name:"Chris Brown",      cat:"R&B M",  flag:"🇺🇸", img:"https://i.ibb.co/9H372SLV/IMG-8849.jpg"},
  {id:"usher",        name:"Usher",            cat:"R&B M",  flag:"🇺🇸", img:"https://i.ibb.co/LdydQV0X/IMG-8850.jpg"},
  {id:"frankocean",   name:"Frank Ocean",      cat:"R&B M",  flag:"🇺🇸", img:"https://i.ibb.co/sdVkLKRB/IMG-8851.webp"},
  {id:"andersonpaak", name:"Anderson .Paak",   cat:"R&B M",  flag:"🇺🇸", img:"https://i.ibb.co/Kp7yvqrB/IMG-8852.jpg"},
  {id:"giveon",       name:"Giveon",           cat:"R&B M",  flag:"🇺🇸", img:"https://i.ibb.co/GBV1wLq/IMG-8853.webp"},
  {id:"brentfaiyaz",  name:"Brent Faiyaz",     cat:"R&B M",  flag:"🇺🇸", img:"https://i.ibb.co/Vdxx2GW/IMG-8854.webp"},
  {id:"dvsn",         name:"dvsn",             cat:"R&B M",  flag:"🇺🇸", img:"https://i.ibb.co/Pz9LzfL6/IMG-8855.webp"},
  {id:"beyonce",      name:"Beyonce",          cat:"R&B F",  flag:"🇺🇸", img:"https://i.ibb.co/gL6kCMjJ/IMG-8856.jpg"},
  {id:"sza",          name:"SZA",              cat:"R&B F",  flag:"🇺🇸", img:"https://i.ibb.co/bR7C82ZF/IMG-8857.webp"},
  {id:"her",          name:"H.E.R.",           cat:"R&B F",  flag:"🇺🇸", img:"https://i.ibb.co/Gfc3bSZG/IMG-8858.jpg"},
  {id:"summerwalker", name:"Summer Walker",    cat:"R&B F",  flag:"🇺🇸", img:"https://i.ibb.co/xqQWTsX2/IMG-8859.webp"},
  {id:"keyshia",      name:"Keyshia Cole",     cat:"R&B F",  flag:"🇺🇸", img:"https://i.ibb.co/99GS9GDq/IMG-8860.webp"},
  {id:"jhene",        name:"Jhene Aiko",       cat:"R&B F",  flag:"🇺🇸", img:"https://i.ibb.co/S7066VKt/IMG-8861.webp"},
  {id:"arianag",      name:"Ariana Grande",    cat:"R&B F",  flag:"🇺🇸", img:"https://i.ibb.co/gLjRyWdF/IMG-8862.webp"},
  {id:"aliciakeys",   name:"Alicia Keys",      cat:"R&B F",  flag:"🇺🇸", img:"https://i.ibb.co/4ZCbsmFX/IMG-8863.jpg"},
  {id:"dojacat",      name:"Doja Cat",         cat:"R&B F",  flag:"🇺🇸", img:"https://i.ibb.co/rfL5rhYq/IMG-8865.webp"},
  {id:"bigsean",      name:"Big Sean",         cat:"Detroit",flag:"🇺🇸", img:"https://i.ibb.co/0yJFMLNj/IMG-8866.webp"},
  {id:"teegriz",      name:"Tee Grizzley",     cat:"Detroit",flag:"🇺🇸", img:"https://i.ibb.co/s4KN0fM/IMG-8867.jpg"},
  {id:"babyfaceray",  name:"Babyface Ray",     cat:"Detroit",flag:"🇺🇸", img:"https://i.ibb.co/7xNtvmMr/IMG-8868.webp"},
  {id:"42dugg",       name:"42 Dugg",          cat:"Detroit",flag:"🇺🇸", img:"https://i.ibb.co/hJb1QF8c/IMG-8869.webp"},
  {id:"dannybrown",   name:"Danny Brown",      cat:"Detroit",flag:"🇺🇸", img:"https://i.ibb.co/1YWcSv08/IMG-8870.webp"},
  {id:"sadababy",     name:"Sada Baby",        cat:"Detroit",       flag:"🇺🇸", img:"https://i.ibb.co/N2TGpp2F/IMG-8871.webp"},
  {id:"icewear",      name:"Icewear Vezzo",    cat:"Detroit",       flag:"🇺🇸", img:"https://i.ibb.co/VGBxyCT/IMG-8872.jpg"},
  {id:"riodayungo",   name:"Rio Da Yung OG",   cat:"Detroit",       flag:"🇺🇸", img:"https://i.ibb.co/N6DwJT93/IMG-8873.jpg"},
  {id:"babytron",     name:"BabyTron",         cat:"Detroit",       flag:"🇺🇸", img:"https://i.ibb.co/Wpc8GgpR/IMG-8874.webp"},
  {id:"veeze",        name:"Veeze",            cat:"Detroit",       flag:"🇺🇸", img:"https://i.ibb.co/MxfsY9vm/IMG-8875.webp"},
  {id:"dejloaf",      name:"Dej Loaf",         cat:"Detroit",       flag:"🇺🇸", img:"https://i.ibb.co/h1dXZ33r/IMG-8876.webp"},
  {id:"kashdoll",     name:"Kash Doll",        cat:"Detroit",       flag:"🇺🇸", img:"https://i.ibb.co/k6KfLyc1/IMG-8877.jpg"},
  {id:"skillababy",   name:"Skilla Baby",      cat:"Detroit",       flag:"🇺🇸", img:"https://i.ibb.co/4n9Hd8tx/IMG-8878.webp"},
  {id:"jiprince",     name:"J.I The Prince Of N.Y", cat:"Melodic Trap", flag:"🇺🇸", img:"https://i.ibb.co/tww7qV0F/IMG-9133.jpg",
   searchOverride:"J.I type beat", filterTitle: false,
   extraQueries:"J.I type beat,J.I Type Beats,J.I Instrumental,J.I TYPE BEATS!"},
  {id:"liltjay",      name:"Lil TJay",         cat:"Melodic Trap",  flag:"🇺🇸", img:"https://i.ibb.co/N23pQpRT/IMG-9134.jpg"},
  {id:"aboogie",      name:"A Boogie Wit Da Hoodie", cat:"Melodic Trap", flag:"🇺🇸", img:"https://i.ibb.co/chpLfGPc/IMG-9136.jpg"},
  {id:"sleepyhallow",  name:"Sleepy Hallow",   cat:"Melodic Trap",  flag:"🇺🇸", img:"https://i.ibb.co/zHmXPCgp/IMG-9137.webp"},
  {id:"stunnagambino", name:"Stunna Gambino",  cat:"Melodic Trap",  flag:"🇺🇸", img:"https://i.ibb.co/pjZYNBM0/IMG-9138.jpg"},
];

const ARTISTS_UK = [
  {id:"ajtracey",      name:"AJ Tracey",       cat:"Grime", flag:"🇬🇧", img:"https://i.ibb.co/KjMjGVhN/IMG-0480.jpg"},
  {id:"aitch",         name:"Aitch",           cat:"UK Rap", flag:"🇬🇧", img:"https://i.ibb.co/jdvJ4Zc/IMG-0481.jpg"},
  {id:"centralcee",    name:"Central Cee",     cat:"UK Drill", flag:"🇬🇧", img:"https://i.ibb.co/wZvG4SdH/IMG-0482.jpg"},
  {id:"dblockeurope",  name:"D Block Europe",  cat:"UK Melodic Trap", flag:"🇬🇧", img:"https://i.ibb.co/Zp7BHx0G/IMG-0483.webp"},
  {id:"dave",          name:"Dave",            cat:"UK Rap", flag:"🇬🇧", img:"https://i.ibb.co/qLyG67SS/IMG-0484.jpg"},
  {id:"diggad",        name:"Digga D",         cat:"UK Drill", flag:"🇬🇧", img:"https://i.ibb.co/Y4KX0hXn/IMG-0485.jpg"},
  {id:"headieone",     name:"Headie One",      cat:"UK Drill", flag:"🇬🇧", img:"https://i.ibb.co/mCD7Nx50/IMG-0486.png"},
  {id:"jhus",          name:"J Hus",           cat:"UK Rap", flag:"🇬🇧", img:"https://i.ibb.co/b5jyBxCP/IMG-0487.jpg"},
  {id:"ktrap",         name:"K-Trap",          cat:"UK Drill", flag:"🇬🇧", img:"https://i.ibb.co/svhjVGHB/IMG-0488.jpg"},
  {id:"potterpayper",  name:"Potter Payper",   cat:"UK Rap", flag:"🇬🇧", img:"https://i.ibb.co/ymV2XKF2/IMG-0489.jpg"},
  {id:"skepta",        name:"Skepta",          cat:"Grime", flag:"🇬🇧", img:"https://i.ibb.co/G3sL0H8k/IMG-0490.jpg"},
  {id:"stormzy",       name:"Stormzy",         cat:"Grime", flag:"🇬🇧", img:"https://i.ibb.co/wh8fMHWz/IMG-0491.jpg"},
  {id:"slowthai",      name:"Slowthai",        cat:"UK Rap", flag:"🇬🇧", img:"https://i.ibb.co/60QrHxQT/IMG-0492.jpg"},
  {id:"ghetts",        name:"Ghetts",          cat:"Grime", flag:"🇬🇧", img:"https://i.ibb.co/4RjsrCpc/IMG-0493.jpg"},
  {id:"giggs",         name:"Giggs",           cat:"UK Rap", flag:"🇬🇧", img:"https://i.ibb.co/39VkxCr5/IMG-0494.jpg"},
  {id:"dizzee",        name:"Dizzee Rascal",   cat:"Grime", flag:"🇬🇧", img:"https://i.ibb.co/bgyCJp2M/IMG-0495.jpg"},
  {id:"wiley",         name:"Wiley",           cat:"Grime", flag:"🇬🇧", img:"https://i.ibb.co/pvQsZPdh/IMG-0496.jpg"},
  {id:"jme",           name:"JME",             cat:"Grime", flag:"🇬🇧", img:"https://i.ibb.co/LDWqsQ86/IMG-0497.jpg"},
  {id:"kano",          name:"Kano",            cat:"Grime", flag:"🇬🇧", img:"https://i.ibb.co/HDMKt6Xq/IMG-0498.jpg"},
  {id:"ninesuk",       name:"Nines",           cat:"UK Rap", flag:"🇬🇧", img:"https://i.ibb.co/hxsGJh3k/IMG-0499.jpg"},
  {id:"mostack",       name:"MoStack",         cat:"UK Rap", flag:"🇬🇧", img:"https://i.ibb.co/q3KNrDwY/IMG-8879.webp"},
  {id:"fredo",         name:"Fredo",           cat:"UK Rap", flag:"🇬🇧", img:"https://i.ibb.co/jk5h5zMS/IMG-8880.jpg"},
  {id:"arrdee",        name:"ArrDee",          cat:"UK Rap", flag:"🇬🇧", img:"https://i.ibb.co/hFnJzqzF/IMG-8881.webp"},
  {id:"tion",          name:"Tion Wayne",      cat:"UK Drill", flag:"🇬🇧", img:"https://i.ibb.co/r2sjzVnQ/IMG-8882.webp"},
  {id:"stefflon",      name:"Stefflon Don",    cat:"UK Rap", flag:"🇬🇧", img:"https://i.ibb.co/VpksVFhH/IMG-8883.webp"},
  {id:"ladyleshurr",   name:"Lady Leshurr",    cat:"UK Rap", flag:"🇬🇧", img:"https://i.ibb.co/p6z8yYJN/IMG-8884.jpg"},
  {id:"missdynamite",  name:"Ms Dynamite",     cat:"UK Rap", flag:"🇬🇧", img:"https://i.ibb.co/0yg4SZQn/IMG-8885.webp"},
  {id:"craigdavid",    name:"Craig David",     cat:"UK R&B", flag:"🇬🇧", img:"https://i.ibb.co/nMd0VdzF/IMG-8887.jpg"},
  {id:"jorja",         name:"Jorja Smith",     cat:"UK R&B", flag:"🇬🇧", img:"https://i.ibb.co/KjQr64LN/IMG-8888.jpg"},
  {id:"rayblk",        name:"Ray BLK",         cat:"UK R&B", flag:"🇬🇧", img:"https://i.ibb.co/9H0P51ZW/IMG-8889.webp"},
  {id:"mahalia",       name:"Mahalia",         cat:"UK R&B", flag:"🇬🇧", img:"https://i.ibb.co/mFzQQyfQ/IMG-8890.webp"},
  {id:"pinkpantheress",name:"PinkPantheress",  cat:"UK R&B", flag:"🇬🇧", img:"https://i.ibb.co/WNDS1kY1/IMG-8891.webp"},
  {id:"raye",          name:"RAYE",            cat:"UK R&B", flag:"🇬🇧", img:"https://i.ibb.co/RT1QhM5C/IMG-8893.png"},
  {id:"dotrotten",     name:"Dot Rotten / Zeph Ellis", cat:"Grime", flag:"🇬🇧", img:"https://i.ibb.co/tTtkNPnb/IMG-8894.webp",
   searchOverride:"Dot Rotten Zeph Ellis instrumental",
   filterTitle: false,
   instrumentalOnly: true,
   musicSearch:"Dot Rotten",
   musicAliases:"Zeph Ellis"},
  {id:"mhuncho",       name:"M Huncho",        cat:"UK Melodic Trap", flag:"🇬🇧", img:"https://i.ibb.co/HLqRYYFv/IMG-9140.webp"},
  {id:"nafesmallz",    name:"Nafe Smallz",     cat:"UK Melodic Trap", flag:"🇬🇧", img:"https://i.ibb.co/s9JR82ym/IMG-9141.jpg"},
  {id:"yxngbane",      name:"Yxng Bane",       cat:"UK Melodic Trap", flag:"🇬🇧", img:"https://i.ibb.co/WNj0TwHK/IMG-9142.webp"},
  {id:"byoung",        name:"B Young",         cat:"UK Melodic Trap", flag:"🇬🇧", img:"https://i.ibb.co/rf0PdBBq/IMG-9143.jpg",
   blockedChannels:["Prod.B.Young","BYoungBeats","B Young Beats","prod b young"]},
  {id:"yungfume",      name:"Yung Fume",       cat:"UK Melodic Trap", flag:"🇬🇧", img:"https://i.ibb.co/nN52pN5P/IMG-9144.webp"},
  {id:"wewantwraiths", name:"wewantwraiths",   cat:"UK Melodic Trap", flag:"🇬🇧", img:"https://i.ibb.co/6R0qMMG0/IMG-9145.jpg"},
  {id:"nemzzz",        name:"Nemzzz",          cat:"UK Drill", flag:"🇬🇧", img:"https://i.ibb.co/tpjL1JtW/IMG-1096.jpg"},
  {id:"pozer",         name:"Pozer",           cat:"UK Drill", flag:"🇬🇧", img:"https://i.ibb.co/mryBNMQp/IMG-1097.webp"},
  {id:"marnzmalone",   name:"Marnz Malone",    cat:"UK Rap", flag:"🇬🇧", img:"https://i.ibb.co/VpcP9sFQ/IMG-1098.webp"},
  {id:"johnnygunz",    name:"John Wayne/Johnny Gunz", cat:"UK Rap", flag:"🇬🇧", img:"https://i.ibb.co/Hpz5sRrD/IMG-1154.jpg",
   searchOverride:"Johnny Gunz instrumental",
   extraQueries:"Johnny Gunz type beat",
   instrumentalOnly:true,
   beatTitleMustInclude:"Johnny Gunz",
   musicSearch:"Johnny Gunz",
   musicMustInclude:"Johnny Gunz"},
  {id:"trelli",        name:"Trelli",          cat:"UK Rap", flag:"🇬🇧", img:"https://i.ibb.co/W4gvqFMr/IMG-6398.jpg",
   channelHandle:"trelliofficial"},
];

const ARTISTS_JAMAICA = [
  {id:"bobmarley",     name:"Bob Marley",        cat:"Reggae",    flag:"🇯🇲", img:"https://i.ibb.co/bj20Yf1B/IMG-8938.webp"},
  {id:"shaggy",        name:"Shaggy",            cat:"Dancehall", flag:"🇯🇲", img:"https://i.ibb.co/kV0TpHDB/IMG-8957.webp"},
  {id:"seanpaul",      name:"Sean Paul",          cat:"Dancehall", flag:"🇯🇲", img:"https://i.ibb.co/KcDyfPW5/IMG-8945.webp"},
  {id:"popcaan",       name:"Popcaan",            cat:"Dancehall", flag:"🇯🇲", img:"https://i.ibb.co/wFXD4tkW/IMG-8946.jpg"},
  {id:"alkaline",      name:"Alkaline",           cat:"Dancehall", flag:"🇯🇲", img:"https://i.ibb.co/vvmF4ktR/IMG-8947.webp"},
  {id:"vybzkartel",    name:"Vybz Kartel",        cat:"Dancehall", flag:"🇯🇲", img:"https://i.ibb.co/JjqgMmzC/IMG-8948.webp"},
  {id:"mavadogee",     name:"Mavado",             cat:"Dancehall", flag:"🇯🇲", img:"https://i.ibb.co/qMys8fNq/IMG-8949.webp"},
  {id:"burnaboys",     name:"Busy Signal",        cat:"Reggae",    flag:"🇯🇲", img:"https://i.ibb.co/JFbknRg7/IMG-8944.webp"},
  {id:"chronixx",      name:"Chronixx",           cat:"Reggae",    flag:"🇯🇲", img:"https://i.ibb.co/kgBwMtfw/IMG-8939.webp"},
  {id:"protoje",       name:"Protoje",            cat:"Reggae",    flag:"🇯🇲", img:"https://i.ibb.co/VcHv91Pn/IMG-8940.jpg"},
  {id:"koffee",        name:"Koffee",             cat:"Reggae",    flag:"🇯🇲", img:"https://i.ibb.co/FFnPTyS/IMG-8941.webp"},
  {id:"aidonia",       name:"Aidonia",            cat:"Bashment",  flag:"🇯🇲", img:"https://i.ibb.co/4Rgx7WJv/IMG-8956.webp"},
  {id:"spicejamaica",  name:"Spice",              cat:"Bashment",  flag:"🇯🇲", img:"https://i.ibb.co/CpQybJm2/IMG-8955.jpg"},
  {id:"demarcojamaica",name:"Demarco",            cat:"Bashment",  flag:"🇯🇲", img:"https://i.ibb.co/WWBjbJQw/IMG-8954.jpg"},
  {id:"masicka",       name:"Masicka",            cat:"Dancehall", flag:"🇯🇲", img:"https://i.ibb.co/8LYx9Z8D/IMG-8950.webp"},
  {id:"skillibeng",    name:"Skillibeng",         cat:"Dancehall", flag:"🇯🇲", img:"https://i.ibb.co/Z64DBgz1/IMG-8951.webp"},
  {id:"jahcure",       name:"Jah Cure",           cat:"Reggae",    flag:"🇯🇲", img:"https://i.ibb.co/hxF3wXSz/IMG-8942.webp"},
  {id:"tarrus",        name:"Tarrus Riley",       cat:"Reggae",    flag:"🇯🇲", img:"https://i.ibb.co/LXcMdNzY/IMG-8943.webp"},
  {id:"munga",         name:"Munga Honorable",    cat:"Dancehall", flag:"🇯🇲", img:"https://i.ibb.co/3m70P0v9/IMG-8953.webp"},
  {id:"konshens",      name:"Konshens",           cat:"Dancehall", flag:"🇯🇲", img:"https://i.ibb.co/39LG5x19/IMG-8952.webp"},
];

const ARTISTS_AFRICA = [
  {id:"burnaboy",      name:"Burna Boy",          cat:"Afrobeats", flag:"🇳🇬", img:"https://i.ibb.co/4nVk9JbV/IMG-8906.webp"},
  {id:"wizkid",        name:"WizKid",             cat:"Afrobeats", flag:"🇳🇬", img:"https://i.ibb.co/08BSCx1/IMG-8907.webp"},
  {id:"davido",        name:"Davido",             cat:"Afrobeats", flag:"🇳🇬", img:"https://i.ibb.co/5X0D7Qvx/IMG-8908.webp"},
  {id:"asake",         name:"Asake",              cat:"Afrobeats", flag:"🇳🇬", img:"https://i.ibb.co/qM7nKW4S/IMG-8909.png"},
  {id:"ruema",         name:"Rema",               cat:"Afrobeats", flag:"🇳🇬", img:"https://i.ibb.co/j9rMjRBY/IMG-8910.png"},
  {id:"ckay",          name:"CKay",               cat:"Afrobeats", flag:"🇳🇬", img:"https://i.ibb.co/5XLSCcbZ/IMG-8911.webp"},
  {id:"omah",          name:"Omah Lay",           cat:"Afrobeats", flag:"🇳🇬", img:"https://i.ibb.co/pjjCH4tf/IMG-8912.webp"},
  {id:"ayra",          name:"Ayra Starr",         cat:"Afrobeats", flag:"🇳🇬", img:"https://i.ibb.co/FbC9fmqv/IMG-8913.webp"},
  {id:"tiwasavage",    name:"Tiwa Savage",        cat:"Afrobeats", flag:"🇳🇬", img:"https://i.ibb.co/SD09jqCy/IMG-8914.webp"},
  {id:"olamide",       name:"Olamide",            cat:"Afrobeats", flag:"🇳🇬", img:"https://i.ibb.co/CsnM8t38/IMG-8915.webp"},
  {id:"afrobeatz",     name:"Fireboy DML",        cat:"Afrobeats", flag:"🇳🇬", img:"https://i.ibb.co/rKF6XD24/IMG-8916.webp"},
  {id:"joeboy",        name:"Joeboy",             cat:"Afrobeats", flag:"🇳🇬", img:"https://i.ibb.co/Ps5zG4b4/IMG-8917.webp"},
  {id:"kizzdaniel",    name:"Kizz Daniel",        cat:"Afrobeats", flag:"🇳🇬", img:"https://i.ibb.co/FPkV9tC/IMG-8918.webp"},
  {id:"tems",          name:"Tems",               cat:"Afrobeats", flag:"🇳🇬", img:"https://i.ibb.co/DD1TRV16/IMG-8919.webp"},
  {id:"amaarae",       name:"Amaarae",            cat:"Afrobeats", flag:"🇬🇭", img:"https://i.ibb.co/DHsWm7pj/IMG-8920.webp"},
  {id:"blacksherif",   name:"Black Sherif",       cat:"Afrobeats", flag:"🇬🇭", img:"https://i.ibb.co/xtycqz11/IMG-8921.webp"},
  {id:"sarkodie",      name:"Sarkodie",           cat:"Afrobeats", flag:"🇬🇭", img:"https://i.ibb.co/V09ZjRNk/IMG-8922.webp"},
  {id:"stonebwoy",     name:"Stonebwoy",          cat:"Afrobeats", flag:"🇬🇭", img:"https://i.ibb.co/Vcms8Rrc/IMG-8923.webp"},
  {id:"masterkg",      name:"Master KG",          cat:"Tribal House", flag:"🇿🇦", img:"https://i.ibb.co/4bGNbww/IMG-8926.webp"},
  {id:"nomcebo",       name:"Nomcebo Zikode",     cat:"Tribal House", flag:"🇿🇦", img:"https://i.ibb.co/7JK6qYV9/IMG-8927.webp"},
  {id:"kabza",         name:"Kabza De Small",     cat:"Tribal House", flag:"🇿🇦", img:"https://i.ibb.co/WvqF2PpG/IMG-8928.webp"},
  {id:"djmaphorisa",   name:"DJ Maphorisa",       cat:"Tribal House", flag:"🇿🇦", img:"https://i.ibb.co/Kj4b9SGt/IMG-8929.webp"},
  {id:"sho",           name:"Sho Madjozi",        cat:"Afrobeats", flag:"🇿🇦", img:"https://i.ibb.co/jk1wmtnn/IMG-8924.jpg"},
  {id:"nasty",         name:"Nasty C",            cat:"Afrobeats", flag:"🇿🇦", img:"https://i.ibb.co/Y7w048Kd/IMG-8925.webp"},
  {id:"ladydu",        name:"Lady Du",            cat:"Tribal House", flag:"🇿🇦", img:"https://i.ibb.co/whmMj9M5/IMG-8930.webp"},
];

const USA_CATS     = ["All","Rap","R&B M","R&B F","Detroit","Melodic Trap"];
const UK_CATS      = ["All","UK Rap","UK Drill","UK R&B","Grime","UK Melodic Trap"];
const JAMAICA_CATS = ["All","Reggae","Dancehall","Bashment"];
const AFRICA_CATS  = ["All","Afrobeats","Tribal House"];

// =============================================================================
// LOCAL STORAGE
// =============================================================================
function loadSaved() {
  try { return JSON.parse(localStorage.getItem("bf_saved") || "{}"); }
  catch { return {}; }
}
function persistSaved(s) {
  try { localStorage.setItem("bf_saved", JSON.stringify(s)); }
  catch (e) { console.warn("[BeatFinder] Could not save:", e); }
}

// =============================================================================
// AVATAR
// =============================================================================
function Av({ name, size = 88, idx = 0, img }) {
  const [g1, g2] = GRAD[idx % GRAD.length];
  const [imgErr, setImgErr] = useState(false);

  return (
    <div style={{
      width: size, height: size, borderRadius: "50%", flexShrink: 0,
      overflow: "hidden", position: "relative",
      border: "2.5px solid rgba(255,255,255,0.15)",
      background: img && !imgErr ? "#111" : "linear-gradient(135deg," + g1 + "," + g2 + ")",
      display: "flex", alignItems: "center", justifyContent: "center",
    }}>
      {img && !imgErr ? (
        <img
          src={img}
          alt={name}
          onError={() => setImgErr(true)}
          style={{ width: "100%", height: "100%", objectFit: "cover", display: "block" }}
        />
      ) : (
        <span style={{
          fontSize: size * 0.28, fontWeight: 800, color: "white",
          fontFamily: "'Bebas Neue',sans-serif",
        }}>
          {initials(name)}
        </span>
      )}
    </div>
  );
}



// =============================================================================
// AI LYRIC ASSISTANT - local rhyme engine, no API needed, 100% free
// =============================================================================

// =============================================================================
// LYRIC ASSISTANT ENGINE - local rhyme generator
// =============================================================================


// =============================================================================
// AI LYRIC ASSISTANT ENGINE
// =============================================================================

function getLastLine(lyrics) {
  var lines = lyrics.split(String.fromCharCode(10)).filter(function(l){ return l.trim().length > 0; });
  return lines.length > 0 ? lines[lines.length - 1].trim() : "";
}

function cleanWord(w) { return w.replace(/[^a-zA-Z]/g, "").toLowerCase(); }

function extractTarget(line) {
  var words = line.trim().split(" ").filter(function(w){ return w.trim().length > 0; });
  var cleaned = words.map(cleanWord).filter(function(w){ return w.length > 0; });
  var w1 = cleaned.length > 0 ? cleaned[cleaned.length - 1] : "";
  var w2 = cleaned.length > 1 ? cleaned[cleaned.length - 2] : "";
  var w3 = cleaned.length > 2 ? cleaned[cleaned.length - 3] : "";
  return { w1: w1, w2: w2, w3: w3 };
}

function detectScheme(lines) {
  if (lines.length < 2) return "AA";
  var ends = lines.slice(-4).map(function(l){
    var ws = l.trim().split(" ");
    return cleanWord(ws[ws.length - 1]);
  });
  if (ends.length >= 4 && ends[0] === ends[2] && ends[1] === ends[3]) return "ABAB";
  if (ends.length >= 2 && ends[ends.length-1] === ends[ends.length-2]) return "AA";
  return "ABAB";
}

// Filter out obscure/unusable rhyme words
var REJECT_WORDS = {
  "a":1,"an":1,"the":1,"and":1,"or":1,"but":1,"if":1,"in":1,"on":1,"at":1,
  "to":1,"for":1,"of":1,"with":1,"by":1,"as":1,"is":1,"it":1,"be":1,"we":1,
  "he":1,"she":1,"they":1,"i":1,"you":1,"my":1,"your":1,"our":1,"this":1,
  "that":1,"was":1,"were":1,"are":1,"am":1,"had":1,"has":1,"have":1,"do":1,
  "did":1,"not":1,"no":1,"so":1,"up":1,"oh":1,"ah":1,"uh":1
};

function isUsable(word) {
  if (!word) return false;
  var w = word.toLowerCase().trim();
  // Must be 2-12 letters only
  if (w.length < 2 || w.length > 12) return false;
  if (/[^a-z]/.test(w)) return false;
  // Reject stop words
  if (REJECT_WORDS[w]) return false;
  // Reject if it looks like an obscure word (very low scrabble-style score for common letters)
  // Simple heuristic: if it contains q,x,z heavily it's obscure
  var rare = (w.match(/[qxz]/g) || []).length;
  if (rare > 1) return false;
  return true;
}


// Fetch rhymes from Datamuse for BOTH the last word AND the 2-word phrase
function fetchRhymes(target) {
  // Query 1: sounds-like the full 2-word phrase (e.g. "been through")
  var phraseQuery = target.w2 ? (target.w2 + " " + target.w1) : target.w1;
  // Query 2: perfect rhymes of last word
  // Query 3: near rhymes of last word
  var urls = [
    "https://api.datamuse.com/words?sl=" + encodeURIComponent(phraseQuery) + "&max=100",
    "https://api.datamuse.com/words?rel_rhy=" + encodeURIComponent(target.w1) + "&max=100",
    "https://api.datamuse.com/words?rel_nry=" + encodeURIComponent(target.w1) + "&max=50",
  ];

  return Promise.all(urls.map(function(url){
    return fetch(url).then(function(r){ return r.json(); }).catch(function(){ return []; });
  })).then(function(results) {
    // Phrase sounds-like: these are words that sound like the whole phrase
    var phraseWords = results[0].map(function(w){ return w.word; }).filter(isUsable);
    // Perfect rhymes of last word
    var perfWords   = results[1].map(function(w){ return w.word; }).filter(isUsable);
    // Near rhymes of last word
    var nearWords   = results[2].map(function(w){ return w.word; }).filter(function(w){
      return isUsable(w) && perfWords.indexOf(w) === -1;
    });

    return {
      phrase:  phraseWords,
      perfect: perfWords,
      near:    nearWords,
      query:   phraseQuery,
    };
  }).catch(function(){
    return { phrase: [], perfect: [], near: [], query: phraseQuery };
  });
}

// Reconstruct rhyme phrase matching the target structure
// "been through" with rhyme word "true" -> "stayed true"
// "been through" with rhyme word "knew" -> "always knew"
// We keep the SAME NUMBER of words and same trailing structure
function buildRhymePhrase(rhymeWord, target) {
  if (!target.w2) return rhymeWord;
  // Replace w1 with rhymeWord, keep w2
  // But if rhymeWord sounds like the whole phrase, just use it directly
  // For 2-word targets: verb + rhymeWord
  var connectors = ["been","go","stay","came","felt","kept","done","run","held","stood","ride","live","move","seen","know","feel","through","for","still","always","never"];
  // Pick a connector that sounds natural
  var idx = connectors.indexOf(target.w2);
  var connector = idx > -1 ? connectors[idx] : "stay";
  return connector + " " + rhymeWord;
}

// Bar templates - {R} is replaced with the full rhyme phrase
// Every bar is a complete meaningful sentence
var BARS = [
  "You know I held it down through everything just {R}",
  "They never saw the tears but I was always {R}",
  "Came from nothing, still I found a way to {R}",
  "Put my trust in God and vowed to always {R}",
  "I ain't gone cap, I'm telling you I had to {R}",
  "All the nights I cried alone I chose to {R}",
  "Pressure builds the diamond, I was born to {R}",
  "Real ones never fold, we always gonna {R}",
  "Through the darkest nights I told myself to {R}",
  "God kept pushing, so I had no choice but {R}",
  "Late nights, cold winters, still I chose to {R}",
  "Everything they took from me I had to {R}",
  "Momma always told me never quit so I {R}",
  "They doubted every move I made but I {R}",
  "All the sacrifice was worth it now I {R}",
];

function buildBar(rhymePhrase, index) {
  var template = BARS[index % BARS.length];
  return template.replace("{R}", rhymePhrase);
}

function barQuality(bar) {
  if (!bar || typeof bar !== "string") return false;
  var words = bar.trim().split(" ");
  return words.length >= 7 && words.length <= 18;
}


// =============================================================================
// RHYME FINDER
// =============================================================================
function RhymeFinder({ onClose, onInsert }) {
  // ── All hooks first ──────────────────────────────────────────────────────
  const [word,     setWord]     = useState("");
  const [results,  setResults]  = useState(null);
  const [loading,  setLoading]  = useState(false);
  const [searched, setSearched] = useState("");
  var resultsRef = React.useRef(null);
  var [atTop,    setAtTop]    = React.useState(true);
  var [atBottom, setAtBottom] = React.useState(false);

  function onResultsScroll() {
    var el = resultsRef.current;
    if (!el) return;
    setAtTop(el.scrollTop <= 4);
    setAtBottom(el.scrollTop + el.clientHeight >= el.scrollHeight - 4);
  }

  React.useEffect(function() {
    var el = resultsRef.current;
    if (!el) return;
    setAtTop(true);
    setAtBottom(el.scrollHeight <= el.clientHeight + 4);
    el.scrollTop = 0;
  }, [results]);

  React.useEffect(function() {
    var el = resultsRef.current;
    if (!el) return;
    function onTouchMove(e) {
      var top    = el.scrollTop === 0;
      var bot    = el.scrollTop + el.clientHeight >= el.scrollHeight;
      var goUp   = e.touches[0] && e._touchStartY > e.touches[0].clientY;
      var goDown = e.touches[0] && e._touchStartY < e.touches[0].clientY;
      if ((top && goDown) || (bot && goUp)) e.preventDefault();
      e.stopPropagation();
    }
    function onTouchStart(e) {
      e._touchStartY = e.touches[0] ? e.touches[0].clientY : 0;
    }
    el.addEventListener("touchstart", onTouchStart, { passive: true });
    el.addEventListener("touchmove",  onTouchMove,  { passive: false });
    return function() {
      el.removeEventListener("touchstart", onTouchStart);
      el.removeEventListener("touchmove",  onTouchMove);
    };
  }, []);
  // ── End hooks ─────────────────────────────────────────────────────────────

  const findRhymes = function() {
    var w = word.trim().toLowerCase();
    if (!w) return;
    setLoading(true);
    setResults(null);
    setSearched(w);

    var words = w.trim().split(/\s+/);
    var isMultiWord = words.length > 1;

    // ── Phonetic helpers ────────────────────────────────────────────────────
    // Simplified English phoneme map — vowel sounds keyed by spelling patterns
    var VOWEL_PATTERNS = [
      [/igh|igh|[^aeiou]y$|[^aeiou]ye$|^i[^aeiou]e|[^aeiou]i[^aeiou]e/, "AY"],
      [/ay$|ai|ey$|[^aeiou]a[^aeiou]e/, "EY"],
      [/ow$|oa|o[^aeiou]e|[^aeiou]o[^aeiou]$/, "OW"],
      [/oo|ue$|ew$|ui|[^aeiou]u[^aeiou]e/, "UW"],
      [/aw|au|all|all|[^aeiou]o[^aeiou][^aeiou]/, "AO"],
      [/ee|ea|[^aeiou]e[^aeiou]e|ie$/, "IY"],
      [/[^aeiou]a[^aeiou][^aeiou]|an$|am$|at$|ad$|ag$|ap$|ab$|ax$/, "AE"],
      [/[^aeiou]e[^aeiou]|en$|em$|et$|ed$|eg$|ep$|eb$/, "EH"],
      [/[^aeiou]i[^aeiou]|in$|im$|it$|id$|ig$|ip$|ib$/, "IH"],
      [/[^aeiou]o[^aeiou]|on$|om$|ot$|od$|og$|op$|ob$/, "AA"],
      [/[^aeiou]u[^aeiou]|un$|um$|ut$|ud$|ug$|up$|ub$/, "AH"],
      [/ound|own|out|ow[^aeiou]|ou/, "AW"],
      [/oi|oy/, "OY"],
      [/er$|or$|ar$|ure$|ire$|[aeiou]r/, "R"],
    ];

    // Get the stressed vowel chain for a word (last 2 syllables matter most in rap)
    function getVowelChain(word) {
      var w = word.toLowerCase().replace(/[^a-z]/g, "");
      var chain = [];
      var remaining = w;
      for (var p = 0; p < VOWEL_PATTERNS.length; p++) {
        if (VOWEL_PATTERNS[p][0].test(remaining)) {
          chain.push(VOWEL_PATTERNS[p][1]);
        }
      }
      // Also extract raw ending for fallback matching
      return { chain: chain, ending: w.slice(-3), ending2: w.slice(-2) };
    }

    // Score how well a candidate phrase matches the input phrase phonetically
    function phoneticScore(inputWords, candidateStr) {
      var candWords = candidateStr.toLowerCase().split(/\s+/);

      // Must have at least 1 word
      if (!candWords.length) return 0;

      var inputPhones  = inputWords.map(getVowelChain);
      var candPhones   = candWords.map(getVowelChain);

      var score = 0;

      // 1. Ending match — most important in rap (last word ending)
      var inputLast = inputPhones[inputPhones.length - 1];
      var candLast  = candPhones[candPhones.length - 1];

      if (inputLast.ending === candLast.ending) score += 40;
      else if (inputLast.ending2 === candLast.ending2) score += 25;

      // 2. Vowel chain overlap across the whole phrase
      var inputAllChains = inputPhones.reduce(function(a, p) { return a.concat(p.chain); }, []);
      var candAllChains  = candPhones.reduce(function(a, p) { return a.concat(p.chain); }, []);
      var matches = 0;
      inputAllChains.forEach(function(v) {
        if (candAllChains.indexOf(v) !== -1) matches++;
      });
      var chainScore = inputAllChains.length > 0 ? (matches / inputAllChains.length) * 30 : 0;
      score += chainScore;

      // 3. Syllable count similarity
      function countSyllables(str) {
        return (str.match(/[aeiou]+/g) || []).length;
      }
      var inputSyls = inputWords.join(" ");
      var inputSylCount = countSyllables(inputSyls);
      var candSylCount  = countSyllables(candidateStr);
      var sylDiff = Math.abs(inputSylCount - candSylCount);
      score += Math.max(0, 15 - sylDiff * 5);

      // 4. Penalise if candidate is identical to input
      if (candidateStr === inputWords.join(" ")) score = 0;

      // 5. Penalise overly rare/academic words
      var rarePatterns = /ify$|ology|ification|esque|aceous|ulent|ulent|tude$/;
      if (rarePatterns.test(candidateStr)) score -= 20;

      return score;
    }

    // Filter out obviously bad candidates
    function isUsable(word) {
      if (word.length < 2) return false;
      // Remove pure Latin/academic
      if (/ification$|ological$|escence$|aceous$/.test(word)) return false;
      return true;
    }

    if (isMultiWord) {
      // ── Phonetic multi-word engine ─────────────────────────────────────────
      // Strategy: fetch real candidates for each word separately,
      // validate they are common real words, then pair them phonetically.
      var lastWord  = words[words.length - 1];
      var firstWord = words[0];

      // Common word validator — uses Datamuse frequency tag
      // Words with freq tag f:X where X >= 4 are common enough
      // We'll use a simple known-bad-pattern filter as first pass
      function looksReal(w) {
        if (!w || w.length < 2) return false;
        // Reject if contains uncommon consonant clusters unlikely in English
        if (/[^aeiou]{4,}/.test(w)) return false;
        // Reject if ends in rare patterns
        if (/[^aeiou](ff|rr|bb|dd|gg|pp|tt|kk|ll|mm|nn|ss|zz)$/.test(w) && w.length <= 4) return false;
        // Reject single-syllable words that are clearly not English
        if (w.length <= 5 && !/[aeiou]/.test(w)) return false;
        return true;
      }

      // Fetch candidates with frequency scores — use &md=f to get frequency data
      var fetches = [
        // Rhymes for last word with frequency metadata
        fetch("https://api.datamuse.com/words?rel_rhy=" + encodeURIComponent(lastWord) + "&md=f&max=150").then(function(r){ return r.json(); }),
        // Near rhymes for last word
        fetch("https://api.datamuse.com/words?rel_nry=" + encodeURIComponent(lastWord) + "&md=f&max=100").then(function(r){ return r.json(); }),
        // Rhymes for first word with frequency metadata
        fetch("https://api.datamuse.com/words?rel_rhy=" + encodeURIComponent(firstWord) + "&md=f&max=150").then(function(r){ return r.json(); }),
        // Near rhymes for first word
        fetch("https://api.datamuse.com/words?rel_nry=" + encodeURIComponent(firstWord) + "&md=f&max=100").then(function(r){ return r.json(); }),
      ];

      Promise.all(fetches).then(function(data) {

        // Extract word + frequency from Datamuse response
        // Datamuse returns tags like ["f:12.34"] for frequency
        function extractFreq(entry) {
          var freq = 0;
          if (entry.tags) {
            entry.tags.forEach(function(t) {
              if (t.startsWith("f:")) freq = parseFloat(t.slice(2)) || 0;
            });
          }
          return freq;
        }

        // Filter to only real, common words
        // Datamuse frequency: >1 = fairly common, >3 = common, >10 = very common
        function filterCommon(entries, minFreq) {
          return (entries || []).filter(function(e) {
            if (!looksReal(e.word)) return false;
            if (e.word.indexOf(" ") !== -1) return false; // no multi-word entries
            var freq = extractFreq(e);
            // If no freq data, use Datamuse score as proxy — score > 2000 = common
            if (freq === 0) return (e.score || 0) > 2000;
            return freq >= minFreq;
          }).map(function(e){ return e.word; });
        }

        var lastRhy  = filterCommon(data[0], 1.5);
        var lastNry  = filterCommon(data[1], 2);
        var firstRhy = filterCommon(data[2], 1.5);
        var firstNry = filterCommon(data[3], 2);

        // Deduplicate and cap lists
        function dedupe(arr) {
          var seen = new Set();
          return arr.filter(function(w) {
            if (seen.has(w)) return false;
            seen.add(w);
            return true;
          });
        }

        var fCands = dedupe(firstRhy.concat(firstNry)).slice(0, 25);
        var lCands = dedupe(lastRhy.concat(lastNry)).slice(0, 25);

        // Build pairs — but ONLY real word + real word combinations
        var pairs = [];
        var seenPairs = new Set();

        fCands.forEach(function(f) {
          lCands.forEach(function(l) {
            if (f === l) return;
            var key = f + " " + l;
            if (!seenPairs.has(key)) {
              seenPairs.add(key);
              pairs.push(key);
            }
          });
        });

        // Score every candidate pair phonetically
        var scored = pairs.map(function(p) {
          return { word: p, score: phoneticScore(words, p) };
        }).filter(function(r) { return r.score > 15; });

        scored.sort(function(a, b) { return b.score - a.score; });

        var perfectMultis = scored.filter(function(r){ return r.score >= 50; }).slice(0, 20);
        var nearMultis    = scored.filter(function(r){ return r.score >= 20 && r.score < 50; }).slice(0, 20);

        // Slant rhymes = just the top real single-word rhymes of the last word
        var slant = lastRhy.slice(0, 15).map(function(w){ return { word: w }; });

        setResults({
          perfect:       perfectMultis,
          near:          nearMultis,
          multiSyllable: slant,
          isMultiWord:   true,
          phonetic:      true,
        });
        setLoading(false);

      }).catch(function() {
        setResults({ perfect: [], near: [], multiSyllable: [], isMultiWord: true });
        setLoading(false);
      });

    } else {
      // ── Original single-word engine — UNCHANGED ───────────────────────────
      Promise.all([
        fetch("https://api.datamuse.com/words?rel_rhy=" + encodeURIComponent(w) + "&max=100").then(function(r){ return r.json(); }),
        fetch("https://api.datamuse.com/words?rel_nry=" + encodeURIComponent(w) + "&max=50").then(function(r){ return r.json(); }),
      ]).then(function(data) {
        var allRhymes = (data[0] || []).filter(function(r){ return r.word.indexOf(" ") === -1; });
        var allNear   = (data[1] || []).filter(function(r){ return r.word.indexOf(" ") === -1; });

        var perfect = allRhymes.filter(function(r){ return (r.score || 0) >= 300; });
        if (perfect.length < 5) perfect = allRhymes.filter(function(r){ return (r.score || 0) >= 100; });
        if (perfect.length === 0) perfect = allRhymes.slice(0, 20);

        var perfectWords = new Set(perfect.map(function(r){ return r.word; }));
        var near = allNear.filter(function(r){
          return !perfectWords.has(r.word) && (r.score || 0) > 800;
        });

        setResults({ perfect, near, multiSyllable: null, isMultiWord: false });
        setLoading(false);
      }).catch(function() {
        setResults({ perfect: [], near: [], multiSyllable: null, isMultiWord: false });
        setLoading(false);
      });
    }
  };

  var RhymeChip = function(props) {
    var touchMoved = React.useRef(false);
    return (
      <button
        onTouchStart={function(){ touchMoved.current = false; }}
        onTouchMove={function(){ touchMoved.current = true; }}
        onTouchEnd={function(e){
          if (touchMoved.current) { e.preventDefault(); return; }
        }}
        onClick={function(e) {
          if (touchMoved.current) { e.preventDefault(); return; }
          onInsert(props.word);
        }}
        style={{
          background: "rgba(255,255,255,0.05)", border: "1px solid #2a2a2a",
          borderRadius: 20, padding: "5px 12px", color: "white",
          fontSize: 13, fontWeight: 500, cursor: "pointer", flexShrink: 0,
          fontFamily: "'DM Sans',sans-serif",
          WebkitTapHighlightColor: "transparent",
          userSelect: "none",
        }}>
        {props.word}
      </button>
    );
  };

  var Section = function(props) {
    if (!props.words || props.words.length === 0) return null;
    return (
      <div style={{ marginBottom: 16 }}>
        <div style={{ color: props.color || "#888", fontSize: 10, fontWeight: 800, letterSpacing: 1.5, marginBottom: 8 }}>
          {props.title} ({props.words.length})
        </div>
        <div style={{ display: "flex", flexWrap: "wrap", gap: 6 }}>
          {props.words.map(function(r) {
            return <RhymeChip key={r.word} word={r.word} />;
          })}
        </div>
      </div>
    );
  };

  return (
    <div style={{
      position: "fixed", left: 0, right: 0, bottom: 0,
      background: "#0a0f1a",
      borderTop: "2px solid rgba(6,182,212,0.4)",
      zIndex: 1000,
      display: "flex", flexDirection: "column",
      height: "55vh",
      paddingBottom: "env(safe-area-inset-bottom)",
      boxShadow: "0 -8px 32px rgba(0,0,0,0.8)",
    }}>
      {/* Header + search — always visible, never scrolls */}
      <div style={{ padding: "12px 16px 10px", flexShrink: 0, background: "#0a0f1a", zIndex: 1 }}>
        <div style={{ display: "flex", alignItems: "center", justifyContent: "space-between", marginBottom: 10 }}>
          <div style={{ color: "#06B6D4", fontWeight: 800, fontSize: 13 }}><AppIcon id="target" size={20}/> Rhyme Finder</div>
          <button onClick={function(e){ e.stopPropagation(); onClose(); }} onTouchEnd={function(e){ e.stopPropagation(); }} style={{ background: "none", border: "none", color: "#555", fontSize: 18, cursor: "pointer", padding: "4px 8px" }}>✕</button>
        </div>
        <div style={{ display: "flex", gap: 8 }}>
          <input
            value={word}
            onChange={function(e) { setWord(e.target.value); }}
            onKeyDown={function(e) { if (e.key === "Enter") findRhymes(); }}
            placeholder="Type a word e.g. love, night, fire..."
            style={{
              flex: 1, background: "#1a1a2a", border: "1px solid #2a2a3a",
              borderRadius: 10, padding: "10px 12px", color: "white",
              fontSize: 13, outline: "none",
            }}
          />
          <button onClick={findRhymes} disabled={loading || !word.trim()} style={{
            background: loading || !word.trim() ? "#1a1a2a" : "linear-gradient(135deg,#06B6D4,#0891B2)",
            border: "none", borderRadius: 10, color: "white",
            fontWeight: 800, fontSize: 13, padding: "10px 16px",
            cursor: loading || !word.trim() ? "not-allowed" : "pointer",
            opacity: loading || !word.trim() ? 0.5 : 1, flexShrink: 0,
          }}>
            {loading ? "..." : "Find"}
          </button>
        </div>
      </div>

      {/* Thin divider */}
      <div style={{ height: 1, background: "rgba(6,182,212,0.15)", flexShrink: 0 }} />

      {/* Results — independently scrollable */}
      {results && (
        <div style={{ flex: 1, minHeight: 0, position: "relative", display: "flex", flexDirection: "column" }}>
          {/* Up arrow — hidden at top */}
          {!atTop && (
            <button
              onTouchStart={function(e){ e.stopPropagation(); }}
              onClick={function(){ if (resultsRef.current) resultsRef.current.scrollBy({ top: -120, behavior: "smooth" }); }}
              style={{
                position: "absolute", top: 4, right: 8, zIndex: 20,
                background: "rgba(6,182,212,0.15)", border: "1px solid rgba(6,182,212,0.3)",
                borderRadius: "50%", width: 30, height: 30,
                display: "flex", alignItems: "center", justifyContent: "center",
                cursor: "pointer",
              }}>
              <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="#06B6D4" strokeWidth="2.5" strokeLinecap="round"><path d="M18 15l-6-6-6 6"/></svg>
            </button>
          )}
          {/* Down arrow — hidden at bottom */}
          {!atBottom && (
            <button
              onTouchStart={function(e){ e.stopPropagation(); }}
              onClick={function(){ if (resultsRef.current) resultsRef.current.scrollBy({ top: 120, behavior: "smooth" }); }}
              style={{
                position: "absolute", bottom: 8, right: 8, zIndex: 20,
                background: "rgba(6,182,212,0.15)", border: "1px solid rgba(6,182,212,0.3)",
                borderRadius: "50%", width: 30, height: 30,
                display: "flex", alignItems: "center", justifyContent: "center",
                cursor: "pointer",
              }}>
              <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="#06B6D4" strokeWidth="2.5" strokeLinecap="round"><path d="M6 9l6 6 6-6"/></svg>
            </button>
          )}
          <div
            ref={resultsRef}
            onScroll={onResultsScroll}
            style={{
              overflowY: "scroll",
              WebkitOverflowScrolling: "touch",
              overscrollBehavior: "contain",
              padding: "12px 16px 24px",
              flex: 1,
              minHeight: 0,
              scrollbarWidth: "thin",
              scrollbarColor: "rgba(6,182,212,0.3) transparent",
            }}
          >
            {results.perfect.length === 0 && results.near.length === 0 && (!results.multiSyllable || results.multiSyllable.length === 0) ? (
              <div style={{ color: "#444", fontSize: 13, textAlign: "center", padding: "20px 0" }}>
                No rhymes found for "{searched}"
              </div>
            ) : (
              <div>
                {results.phonetic ? (
                  // ── Phonetic multi-word mode ──────────────────────────────
                  <>
                    {results.perfect.length > 0 && (
                      <Section title="PERFECT MULTIS" words={results.perfect} color="#C026D3" />
                    )}
                    {results.near.length > 0 && (
                      <Section title="NEAR MULTIS" words={results.near} color="#06B6D4" />
                    )}
                    {results.multiSyllable && results.multiSyllable.length > 0 && (
                      <Section title="SLANT RHYMES" words={results.multiSyllable} color="#F59E0B" />
                    )}
                  </>
                ) : (
                  // ── Original single-word mode ─────────────────────────────
                  <>
                    {results.isMultiWord && results.multiSyllable && results.multiSyllable.length > 0 && (
                      <Section title="MULTI-SYLLABLE RHYMES" words={results.multiSyllable} color="#C026D3" />
                    )}
                    <Section title="PERFECT RHYMES" words={results.perfect} color="#06B6D4" />
                    <Section title="NEAR RHYMES" words={results.near} color="#F59E0B" />
                  </>
                )}
              </div>
            )}
          </div>
        </div>
      )}

      {!results && !loading && (
        <div style={{ color: "#333", fontSize: 12, textAlign: "center", padding: "16px 0", flexShrink: 0 }}>
          Tap a rhyme to insert it into your lyrics
        </div>
      )}
    </div>
  );
}

function LyricsNotepad({ beat, onClose, onSaveLyric, initialLyric, lyricIndex, user, studioControls }) {
  const [text,       setText]       = useState(initialLyric ? initialLyric.text  : "");
  const [title,      setTitle]      = useState(initialLyric ? initialLyric.title : "");
  const [saved,      setSaved]      = useState(false);
  const [aiOpen,     setAiOpen]     = useState(null);
  const scrollRef = useCallback(node => {
    if (node) node.scrollTop = 0;
  }, []);
  const isEditing = initialLyric !== undefined && initialLyric !== null;

  // ── Unsaved-changes tracking ──────────────────────────────────────
  // We KILLED the previous autosave (every 3s + on-close) because it
  // was generating new IDs each fire, producing duplicate lyric entries
  // every time the user typed. Now: explicit Save button only.
  //
  // To protect users from losing work, we track whether the editor has
  // unsaved changes since the last save (or since open). When they try
  // to close with unsaved text, we show a 3-button modal:
  //   • Save & Close
  //   • Discard
  //   • Cancel (stay in editor)
  // Lyrics are local-only until Save is pressed.
  var [showCloseConfirm, setShowCloseConfirm] = React.useState(false);
  var initialTextRef     = React.useRef(initialLyric ? (initialLyric.text || "") : "");
  var initialTitleRef    = React.useRef(initialLyric ? (initialLyric.title || "") : "");
  var hasUnsavedChanges = (text.trim() !== initialTextRef.current.trim())
                       || (title.trim() !== initialTitleRef.current.trim());

  // Build the lyric object from current state. We assign an ID ONLY
  // ONCE per editor instance (when saving for the first time on a new
  // lyric), and reuse it on subsequent saves. This prevents the
  // duplicate-on-every-save bug we had with autosave.
  var lyricIdRef = React.useRef(isEditing ? initialLyric.id : null);
  function buildLyric() {
    if (!lyricIdRef.current) {
      lyricIdRef.current = "lyric_" + Date.now() + "_" + Math.random().toString(36).slice(2, 8);
    }
    return {
      id:          lyricIdRef.current,
      title:       title.trim() || (beat ? beat.title : "Untitled"),
      text:        text.trim(),
      beatTitle:   beat ? beat.title : (initialLyric ? initialLyric.beatTitle : ""),
      beatId:      beat ? beat.videoId : (initialLyric ? initialLyric.beatId : ""),
      beatChannel: beat ? (beat.channel || beat.channelTitle || "") : (initialLyric ? (initialLyric.beatChannel || "") : ""),
      beat:        beat || (initialLyric ? initialLyric.beat : null),
      savedAt:     isEditing ? initialLyric.savedAt : new Date().toISOString(),
      updatedAt:   new Date().toISOString(),
    };
  }

  // Close attempt — shows confirm modal if there are unsaved changes,
  // otherwise closes cleanly. Empty editor (no text typed) always
  // closes without prompt.
  var handleClose = function() {
    if (text.trim() && hasUnsavedChanges) {
      setShowCloseConfirm(true);
      return;
    }
    onClose();
  };

  // From the close-confirm modal: "Save & Close"
  function confirmSaveAndClose() {
    if (!text.trim()) { onClose(); return; }
    onSaveLyric(buildLyric(), isEditing ? lyricIndex : null);
    setShowCloseConfirm(false);
    onClose();
  }
  // From the close-confirm modal: "Discard"
  function confirmDiscardAndClose() {
    setShowCloseConfirm(false);
    onClose();
  }

  const handleSave = () => {
    if (!text.trim()) return;
    onSaveLyric(buildLyric(), isEditing ? lyricIndex : null);
    // Mark current state as "clean" so the close prompt doesn't fire
    // again unless they type something new after saving.
    initialTextRef.current  = text;
    initialTitleRef.current = title;
    setSaved(true);
    setTimeout(() => setSaved(false), 2000);
  };

  return (
    <div ref={scrollRef} style={{
      position: "fixed", inset: 0, zIndex: 10001,
      background: "#0a0a0a", display: "flex",
      flexDirection: "column", fontFamily: "'DM Sans',sans-serif",
      paddingTop: "var(--bf-safe-top, env(safe-area-inset-top))",
    }}>
      {/* Unsaved-changes confirm modal — shown when closing with dirty
          text. Three options: Save & Close, Discard, Cancel. Rendered
          inside this fixed container so it overlays everything. */}
      {showCloseConfirm && (
        <div style={{
          position: "fixed", inset: 0, zIndex: 10100,
          background: "rgba(0,0,0,0.82)", backdropFilter: "blur(6px)",
          display: "flex", alignItems: "center", justifyContent: "center",
          padding: 20,
        }}>
          <div style={{
            background: "linear-gradient(165deg,#1a0f1f 0%,#0f0a1f 50%,#0a0a14 100%)",
            border: "1px solid rgba(192,38,211,0.35)",
            borderRadius: 16, padding: "22px 20px 18px",
            maxWidth: 360, width: "100%",
            boxShadow: "0 24px 48px rgba(0,0,0,0.7), 0 0 32px rgba(192,38,211,0.18)",
          }}>
            <div style={{
              fontFamily: "'Bebas Neue',sans-serif", fontSize: 20, letterSpacing: 1.5,
              color: "white", textAlign: "center", marginBottom: 6,
            }}>UNSAVED CHANGES</div>
            <div style={{
              color: "#ccc", fontSize: 13, lineHeight: 1.5,
              textAlign: "center", marginBottom: 16,
            }}>
              You have unsaved lyrics. What would you like to do?
            </div>
            <button
              onClick={confirmSaveAndClose}
              style={{
                width: "100%", marginBottom: 8,
                background: "linear-gradient(135deg,#C026D3,#7C3AED)",
                color: "white", border: "none",
                borderRadius: 10, padding: "12px 14px",
                fontSize: 13, fontWeight: 800, letterSpacing: 0.3,
                cursor: "pointer",
                boxShadow: "0 4px 16px rgba(192,38,211,0.4)",
              }}>
              Save &amp; Close
            </button>
            <button
              onClick={confirmDiscardAndClose}
              style={{
                width: "100%", marginBottom: 8,
                background: "transparent",
                color: "#F87171",
                border: "1px solid rgba(248,113,113,0.4)",
                borderRadius: 10, padding: "11px 14px",
                fontSize: 13, fontWeight: 700,
                cursor: "pointer",
              }}>
              Discard Changes
            </button>
            <button
              onClick={function() { setShowCloseConfirm(false); }}
              style={{
                width: "100%",
                background: "transparent",
                color: "#888",
                border: "1px solid #2a2a2a",
                borderRadius: 10, padding: "11px 14px",
                fontSize: 13, fontWeight: 700,
                cursor: "pointer",
              }}>
              Cancel
            </button>
          </div>
        </div>
      )}
      <div style={{
        background: "linear-gradient(180deg,#0f0a1f 0%,#0a0a14 60%,#080812 100%)",
        flexShrink: 0,
        position: "relative",
      }}>
        {/* LED top accent edge */}
        <div style={{
          position: "absolute", top: 0, left: 0, right: 0, height: 1,
          background: "linear-gradient(90deg,transparent,rgba(192,38,211,0.5),rgba(124,58,237,0.5),rgba(59,130,246,0.5),transparent)",
        }} />
        <div style={{
          display: "flex", alignItems: "center", gap: 12,
          padding: "14px 16px",
          borderBottom: "1px solid rgba(124,58,237,0.2)",
        }}>
          <button onClick={handleClose} style={{
            background: "linear-gradient(135deg,rgba(124,58,237,0.15),rgba(192,38,211,0.12))",
            border: "1px solid rgba(124,58,237,0.4)",
            borderRadius: "50%",
            color: "#E9D5FF", width: 36, height: 36, fontSize: 18,
            cursor: "pointer",
            display: "flex", alignItems: "center", justifyContent: "center",
            boxShadow: "0 0 10px rgba(124,58,237,0.15)",
          }}>
            ←
          </button>
          <div style={{ flex: 1, minWidth: 0 }}>
            <div style={{
              fontFamily: "'Bebas Neue',sans-serif",
              fontSize: 18, letterSpacing: 2.5, fontWeight: 700,
              background: "linear-gradient(90deg,#C026D3,#7C3AED,#3B82F6)",
              WebkitBackgroundClip: "text", backgroundClip: "text",
              color: "transparent",
              filter: "drop-shadow(0 0 8px rgba(124,58,237,0.3))",
              lineHeight: 1,
            }}>LYRICS NOTEPAD</div>
          </div>
          <button onClick={handleSave} style={{
            background: saved
              ? "linear-gradient(135deg,#22C55E,#16A34A)"
              : "linear-gradient(135deg,#C026D3,#7C3AED,#3B82F6)",
            border: "none", borderRadius: 20,
            color: "white",
            fontWeight: 800, fontSize: 12, letterSpacing: 1,
            padding: "8px 18px", cursor: "pointer",
            boxShadow: saved
              ? "0 0 14px rgba(34,197,94,0.35)"
              : "0 0 14px rgba(124,58,237,0.35)",
          }}>
            {saved ? "SAVED ✓" : "SAVE"}
          </button>
        </div>

        {/* Studio transport row — only renders when this notepad was opened
            from inside the Studio. The main fixed transport bar is hidden
            behind this fullscreen overlay, so we surface the same controls
            here so the user can still record/play while writing lyrics. */}
        {studioControls && (
          <div style={{
            display: "flex", alignItems: "center", justifyContent: "center",
            gap: 8, padding: "10px 12px",
            borderBottom: "1px solid #1a1a1a",
            background: "linear-gradient(180deg,rgba(15,10,31,0.7) 0%,rgba(10,10,20,0.7) 100%)",
            position: "relative",
          }}>
            {/* LED accent line */}
            <div style={{
              position: "absolute", bottom: -1, left: 0, right: 0, height: 1,
              background: "linear-gradient(90deg,transparent,rgba(192,38,211,0.4),rgba(124,58,237,0.4),transparent)",
            }} />
            {/* Skip to Start */}
            <button onClick={studioControls.rewind} title="Skip to start"
              style={{ width:32,height:32,borderRadius:8,background:"#141414",border:"1px solid #222",cursor:"pointer",display:"flex",alignItems:"center",justifyContent:"center",flexShrink:0 }}>
              <svg width="14" height="14" viewBox="0 0 24 24" fill="white"><path d="M6 6h2v12H6zm3.5 6 8.5 6V6z"/></svg>
            </button>
            {/* Rewind 5s */}
            <button onClick={studioControls.seekBack} title="Rewind 5s"
              style={{ width:32,height:32,borderRadius:8,background:"#141414",border:"1px solid #222",cursor:"pointer",display:"flex",alignItems:"center",justifyContent:"center",flexShrink:0 }}>
              <svg width="14" height="14" viewBox="0 0 24 24" fill="white"><path d="M12.5 8.5 7 12l5.5 3.5V8.5z"/><path d="M19.5 8.5 14 12l5.5 3.5V8.5z"/></svg>
            </button>
            {/* Record */}
            <button onClick={studioControls.onRecord} disabled={studioControls.countIn > 0}
              style={{
                width:44,height:44,borderRadius:"50%",
                background: studioControls.isRecording ? "#EF4444" : "linear-gradient(135deg,#EF4444,#DC2626)",
                border: studioControls.isRecording ? "3px solid rgba(239,68,68,0.5)" : "3px solid rgba(239,68,68,0.2)",
                cursor: studioControls.countIn > 0 ? "not-allowed" : "pointer",
                opacity: studioControls.countIn > 0 ? 0.4 : 1,
                display:"flex",alignItems:"center",justifyContent:"center",
                boxShadow: studioControls.isRecording ? "0 0 18px rgba(239,68,68,0.5)" : "none",
                flexShrink: 0,
              }}>
              {studioControls.isRecording
                ? <div style={{ width:12,height:12,background:"white",borderRadius:3 }} />
                : <div style={{ width:16,height:16,background:"white",borderRadius:"50%" }} />}
            </button>
            {/* Play / Pause */}
            <button onClick={studioControls.togglePlay}
              style={{ width:34,height:34,borderRadius:"50%",background:"linear-gradient(135deg,#C026D3,#7C3AED)",border:"none",cursor:"pointer",display:"flex",alignItems:"center",justifyContent:"center",flexShrink:0 }}>
              {studioControls.isPlaying
                ? <svg width="14" height="14" viewBox="0 0 24 24" fill="white"><path d="M6 19h4V5H6v14zm8-14v14h4V5h-4z"/></svg>
                : <svg width="14" height="14" viewBox="0 0 24 24" fill="white"><path d="M8 5v14l11-7z"/></svg>}
            </button>
            {/* Fast Forward 5s */}
            <button onClick={studioControls.seekForward} title="Fast forward 5s"
              style={{ width:32,height:32,borderRadius:8,background:"#141414",border:"1px solid #222",cursor:"pointer",display:"flex",alignItems:"center",justifyContent:"center",flexShrink:0 }}>
              <svg width="14" height="14" viewBox="0 0 24 24" fill="white"><path d="M11.5 8.5 17 12l-5.5 3.5V8.5z"/><path d="M4.5 8.5 10 12 4.5 15.5V8.5z"/></svg>
            </button>
            {/* Skip to End */}
            <button onClick={studioControls.skipToEnd} title="Skip to end"
              style={{ width:32,height:32,borderRadius:8,background:"#141414",border:"1px solid #222",cursor:"pointer",display:"flex",alignItems:"center",justifyContent:"center",flexShrink:0 }}>
              <svg width="14" height="14" viewBox="0 0 24 24" fill="white"><path d="M16 6h2v12h-2zm-2.5 6L5 6v12z"/></svg>
            </button>
          </div>
        )}

        {beat && (
          <div style={{ borderBottom: "1px solid #1a1a1a", background: "#000" }}>
            <iframe
              key={beat.videoId}
              src={"https://www.youtube.com/embed/" + beat.videoId + "?autoplay=0&rel=0&modestbranding=1&disablekb=1&iv_load_policy=3&fs=0&controls=1&playlist=" + beat.videoId}
              width="100%"
              height="160"
              style={{ display: "block", border: "none" }}
              allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture"
              sandbox="allow-scripts allow-same-origin allow-presentation"
              title={beat.title}
            />
            <div style={{ padding: "8px 16px", background: "#0d0d0d" }}>
              <div style={{ color: "white", fontSize: 12, fontWeight: 600, whiteSpace: "nowrap", overflow: "hidden", textOverflow: "ellipsis" }}>
                {beat.title}
              </div>
              <div style={{ color: "#888", fontSize: 11, marginTop: 2 }}>{beat.channel}</div>
            </div>
          </div>
        )}
      </div>

      <div style={{ padding: "12px 16px 8px", borderBottom: "1px solid #1a1a1a", background: "#0a0a0a" }}>
        <input
          value={title}
          onChange={e => setTitle(e.target.value)}
          placeholder="Song title (optional)"
          style={{
            width: "100%", background: "#1a1a1a", border: "1px solid #333",
            borderRadius: 10, padding: "10px 14px", color: "white",
            fontSize: 14, outline: "none", boxSizing: "border-box",
          }}
        />
      </div>

      <textarea
        value={text}
        onChange={e => setText(e.target.value)}
        placeholder="Start writing your lyrics here... Stuck for rhymes? Tap the RhymeFinder button below! (Pro members only)"
        style={{
          // ── Flex sizing fix ──
          // Without `minHeight: 0`, a flex item defaults to `min-height: auto`
          // which means "never shrink below your intrinsic content size." A
          // tall body of lyrics then forces the textarea to grow past the
          // viewport, the outer column doesn't scroll (it's position:fixed),
          // and the user is stuck — can't scroll the text body, can't drag
          // the selection handles past the visible region because the
          // textarea isn't scrolling internally.
          //
          // Setting minHeight:0 lets the textarea shrink to its flex slot,
          // and explicit overflowY:auto + WebkitOverflowScrolling:touch
          // turn on iOS's native momentum scrolling inside the textarea.
          // With internal scrolling working, the selection drag-handles can
          // also scroll the content as the user drags them past the edge.
          flex: 1, minHeight: 0,
          overflowY: "auto",
          WebkitOverflowScrolling: "touch",
          background: "#0d0d0d", border: "none", outline: "none",
          color: "white", fontSize: 15, lineHeight: 1.8, padding: "16px",
          resize: "none", fontFamily: "'DM Sans',sans-serif",
          WebkitUserSelect: "text", userSelect: "text",
        }}
      />

      {/* Rhyme Finder Panel */}
      {aiOpen === "rhymes" && (
        <RhymeFinder onClose={() => setAiOpen(null)} onInsert={word => {
          setText(prev => prev ? prev + " " + word : word);
        }} />
      )}

      <div style={{
        padding: "10px 16px",
        background: "#0a0a0a",
        borderTop: aiOpen ? "none" : "1px solid #1a1a1a",
        paddingBottom: aiOpen ? 0 : "calc(10px + env(safe-area-inset-bottom))",
        display: "flex", alignItems: "center", justifyContent: "space-between", gap: 8,
        // Don't let the footer shrink — keep its natural height so the
        // textarea reliably gets the remaining flex space and can scroll
        // its overflow content. Without this iOS occasionally collapses
        // the footer in favor of the textarea, leaving a degenerate flex
        // layout where neither child has proper bounds.
        flexShrink: 0,
      }}>
        <div style={{ color: "#444", fontSize: 11, flexShrink: 0 }}>
          {text.length} chars • {text.split(" ").filter(function(w){return w.length > 0;}).length} words
        </div>
        {user ? (
          <button onClick={function(e){
            e.stopPropagation();
            // RhymeFinder is a paid feature — block free users
            if (!user.isArtistPro && !user.isPro) { requireUpgrade("rhymefinder"); return; }
            setAiOpen(aiOpen === "rhymes" ? null : "rhymes");
          }} style={{
            background: aiOpen === "rhymes" ? "rgba(6,182,212,0.2)" : "rgba(6,182,212,0.15)",
            border: "1px solid " + (aiOpen === "rhymes" ? "#06B6D4" : "rgba(6,182,212,0.4)"),
            borderRadius: 20, color: "#06B6D4", fontWeight: 800,
            fontSize: 12, padding: "8px 14px", cursor: "pointer",
          }}>
            <AppIcon id="target" size={20}/> Rhyme Finder
          </button>
        ) : (
          <div style={{
            background: "rgba(255,255,255,0.04)", border: "1px solid #222",
            borderRadius: 20, color: "#333", fontWeight: 800,
            fontSize: 12, padding: "8px 14px", display: "flex", alignItems: "center", gap: 6,
          }}>
            <AppIcon id="lock" size={14}/> Rhyme Finder
          </div>
        )}
      </div>
    </div>
  );
}

// =============================================================================
// FULL-SCREEN PLAYER
// =============================================================================
function Player({ beat, onClose, savedIds, onSave, isGuest, isArtistPro, onOpenLyrics, savedLyrics, onEditLyric, onGoMembers }) {
  const isAdmin = (function(){ try { return localStorage.getItem("bf_is_admin") === "1"; } catch(e) { return false; } })();
  const [removed, setRemoved] = React.useState(false);
  function removeVideo() {
    if (!window.confirm("Remove this video from the whole app? You can unhide it later in Settings > YouTube Cache.")) return;
    apiFetch("/api/youtube/admin/block-video", {
      method: "POST",
      body: JSON.stringify({ videoId: beat.videoId, title: beat.title || "" }),
    }).then(function(){ setRemoved(true); if (onClose) onClose(); }).catch(function(err){ alert("Couldn't remove: " + (err.message || "failed")); });
  }
  return (
    <div style={{
      position: "fixed", top: 0, left: 0, right: 0, bottom: 0, zIndex: 9999, background: "#000",
      display: "flex", flexDirection: "column", fontFamily: "'DM Sans',sans-serif",
    }}>
      <div style={{
        display: "flex", alignItems: "center", gap: 12,
        padding: "14px 16px", borderBottom: "1px solid #1a1a1a",
        background: "#0a0a0a", flexShrink: 0,
        paddingTop: "calc(14px + var(--bf-safe-top, env(safe-area-inset-top)))",
      }}>
        <button onClick={onClose} style={{
          background: "#1a1a1a", border: "1px solid #333", borderRadius: "50%",
          color: "white", width: 36, height: 36, fontSize: 20, cursor: "pointer",
          display: "flex", alignItems: "center", justifyContent: "center",
        }}>
          ←
        </button>
        <div style={{ flex: 1, overflow: "hidden" }}>
          <div style={{
            color: "white", fontWeight: 700, fontSize: 13,
            whiteSpace: "nowrap", overflow: "hidden", textOverflow: "ellipsis",
          }}>
            {beat.title}
          </div>
          <div style={{ color: "#888", fontSize: 11, marginTop: 2 }}>{beat.channel}</div>
        </div>
      </div>
      <div style={{ position: "relative", width: "100%", height: 220, flexShrink: 0, background: "#000" }}>
        <iframe
          key={beat.videoId}
          src={embedUrl(beat.videoId)}
          width="100%" height="220"
          style={{ display: "block", border: "none", background: "#000", position: "absolute", top: 0, left: 0 }}
          allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture"
          sandbox="allow-scripts allow-same-origin allow-presentation"
          title={beat.title}
        />
        {/* BeatFinder branded overlay — visual only, no pointer events */}
        <img
          src={BF_OVERLAY_IMG}
          alt=""
          style={{
            position: "absolute", top: 0, left: 0,
            width: "100%", height: "100%",
            objectFit: "cover",
            zIndex: 10,
            pointerEvents: "none",
            userSelect: "none",
          }}
        />
      </div>
      <div style={{ padding: "16px", borderBottom: "1px solid #1a1a1a", background: "#0a0a0a" }}>
        <div style={{ color: "white", fontWeight: 700, fontSize: 14, marginBottom: 4, lineHeight: 1.4 }}>
          {beat.title}
        </div>
        <div style={{ color: "#888", fontSize: 13 }}>{beat.channel}</div>
      </div>
      <div style={{ padding: "16px", background: "#0a0a0a", flex: 1 }}>
        {isAdmin && (
          <button onClick={removeVideo}
            style={{
              width: "100%", borderRadius: 14, padding: "14px", marginBottom: 12,
              fontWeight: 800, fontSize: 15, cursor: "pointer",
              background: "rgba(239,68,68,0.12)", border: "1.5px solid rgba(239,68,68,0.6)", color: "#F87171",
              display: "flex", alignItems: "center", justifyContent: "center", gap: 10,
            }}>
            <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="#F87171" strokeWidth="2.2" strokeLinecap="round">
              <polyline points="3 6 5 6 21 6"/><path d="M19 6l-1 14H6L5 6"/><path d="M10 11v6M14 11v6"/><path d="M9 6V4h6v2"/>
            </svg>
            Remove video from app
          </button>
        )}
        {beat.isSong ? (
          <button
            onClick={() => { try { window.dispatchEvent(new CustomEvent("bf-add-to-playlist", { detail: { video: { videoId: beat.videoId, title: beat.title, channel: beat.channel, thumbnail: beat.thumbnail } } })); } catch(_){} }}
            style={{
              width: "100%", borderRadius: 14, padding: "15px",
              fontWeight: 800, fontSize: 16, cursor: "pointer",
              background: "linear-gradient(135deg,#B026FF,#7D3CFF)",
              border: "1px solid rgba(176,38,255,0.6)", color: "#fff",
              display: "flex", alignItems: "center", justifyContent: "center", gap: 10,
              boxShadow: "0 8px 24px rgba(176,38,255,0.4), inset 0 1px 0 rgba(255,255,255,0.2)",
            }}
          >
            <AppIcon id="plus" size={18} /> Add to playlist
          </button>
        ) : (<>
        <div style={{ color: "#333", fontSize: 11, marginBottom: 12, textAlign: "center", fontStyle: "italic" }}>
          Play the beat above, then write your lyrics or save it below.
        </div>
        <button
          onClick={() => onSave(beat)}
          style={{
            marginTop: 12, width: "100%", borderRadius: 14, padding: "15px",
            fontWeight: 800, fontSize: 16, cursor: "pointer",
            background: isGuest ? "#141414" : (savedIds.has(beat.videoId) ? "rgba(192,38,211,0.15)" : "#1a1a1a"),
            border: isGuest ? "1.5px solid #2a2a2a" : (savedIds.has(beat.videoId) ? "2px solid #C026D3" : "1.5px solid #333"),
            color: isGuest ? "#555" : (savedIds.has(beat.videoId) ? "#C026D3" : "#aaa"),
            display: "flex", alignItems: "center", justifyContent: "center", gap: 10,
          }}
        >
          {isGuest && <AppIcon id="lock" size={15} />}
          {isGuest ? "Sign up to save" : (savedIds.has(beat.videoId) ? "Saved to Favourites" : "Save to Favourites")}
        </button>
        {isArtistPro ? (
          <>
            <button
              onClick={() => onOpenLyrics(beat)}
              style={{
                marginTop: 10, width: "100%", borderRadius: 14, padding: "15px",
                fontWeight: 800, fontSize: 16, cursor: "pointer",
                background: isGuest ? "#141414" : "rgba(192,38,211,0.1)",
                border: isGuest ? "1.5px solid #2a2a2a" : "1.5px solid #C026D3",
                color: isGuest ? "#555" : "#C026D3",
                display: "flex", alignItems: "center", justifyContent: "center", gap: 10,
              }}
            >
              {isGuest ? <><AppIcon id="lock" size={15} /> Sign up to write lyrics</> : <>&#9997;&#65039; Write Lyrics to This Beat</>}
            </button>
            {savedLyrics && savedLyrics.find(l => l.beatId === beat.videoId) && (
              <button
                onClick={() => { var idx = savedLyrics.findIndex(l => l.beatId === beat.videoId); onEditLyric(savedLyrics[idx], idx); }}
                style={{
                  marginTop: 10, width: "100%", borderRadius: 14, padding: "15px",
                  fontWeight: 800, fontSize: 15, cursor: "pointer",
                  background: "rgba(34,197,94,0.1)",
                  border: "1.5px solid #22C55E", color: "#22C55E",
                  display: "flex", alignItems: "center", justifyContent: "center", gap: 10,
                }}
              >
                ▤ Open Existing Lyrics - {savedLyrics.find(l => l.beatId === beat.videoId).title}
              </button>
            )}
          </>
        ) : (
          <button
            onClick={() => { onClose(); onGoMembers && onGoMembers(); }}
            style={{
              marginTop: 10, width: "100%", borderRadius: 14, padding: "15px",
              fontWeight: 800, fontSize: 15, cursor: "pointer",
              background: "#111", border: "1.5px solid #2a2a2a", color: "#444",
              display: "flex", alignItems: "center", justifyContent: "center", gap: 10,
            }}
          >
            <AppIcon id="lockkey" size={20}/> Purchase plan to write lyrics
          </button>
        )}
        </>)}

        {/* ── Contact creator button ── */}
        {beat.channel && (
          <button
            type="button"
            onClick={function() {
              openExternalLink("https://www.youtube.com/results?search_query=" + encodeURIComponent(beat.channel));
            }}
            style={{
              display: "flex", alignItems: "center", justifyContent: "center", gap: 10,
              marginTop: 12, width: "100%", borderRadius: 14, padding: "15px",
              fontWeight: 800, fontSize: 15, cursor: "pointer", textDecoration: "none",
              background: "rgba(255,0,0,0.08)",
              border: "1.5px solid rgba(255,0,0,0.35)",
              color: "#ff4444",
              boxSizing: "border-box",
            }}
          >
            <svg width="18" height="18" viewBox="0 0 24 24" fill="#ff4444" style={{flexShrink:0}}>
              <path d="M23.498 6.186a3.016 3.016 0 0 0-2.122-2.136C19.505 3.545 12 3.545 12 3.545s-7.505 0-9.377.505A3.017 3.017 0 0 0 .502 6.186C0 8.07 0 12 0 12s0 3.93.502 5.814a3.016 3.016 0 0 0 2.122 2.136c1.871.505 9.376.505 9.376.505s7.505 0 9.377-.505a3.015 3.015 0 0 0 2.122-2.136C24 15.93 24 12 24 12s0-3.93-.502-5.814zM9.545 15.568V8.432L15.818 12l-6.273 3.568z"/>
            </svg>
            Contact {beat.channel} on YouTube
          </button>
        )}

        {/* ── License notice — shown on every beat ── */}
        <div style={{
          marginTop: 12,
          padding: "12px 14px",
          borderRadius: 12,
          background: "rgba(255,255,255,0.04)",
          border: "1px solid #2a2a2a",
          color: "#666",
          fontSize: 12,
          lineHeight: 1.5,
          textAlign: "center",
        }}>
          <AppIcon id="note" size={20}/> To use this beat in Studio Mode, purchase or obtain a valid license from the creator and upload the MP3/WAV file.
        </div>

      </div>
    </div>
  );
}

// =============================================================================
// BEAT CARD
// =============================================================================
function BeatCard({ beat, savedIds, onSave, onPlay, featured, exclusive }) {
  const [imgErr, setImgErr] = useState(false);
  const [hidden, setHidden] = useState(false);
  const isAdmin = (function(){ try { return localStorage.getItem("bf_is_admin") === "1"; } catch(e) { return false; } })();
  const isSaved  = savedIds.has(beat.videoId);
  const accentClr = exclusive ? "#F59E0B" : featured ? "#C026D3" : "#7C3AED";
  const borderClr = exclusive ? "rgba(245,158,11,0.45)" : featured ? "rgba(192,38,211,0.5)" : "rgba(176,38,255,0.35)";
  const glowClr   = exclusive ? "rgba(245,158,11,0.35)" : featured ? "rgba(192,38,211,0.35)" : "rgba(176,38,255,0.2)";

  function hideVideo(e) {
    if (e) { e.stopPropagation(); }
    if (!window.confirm("Hide this video from the whole app? You can unhide it later in Settings > YouTube Cache.")) return;
    apiFetch("/api/youtube/admin/block-video", {
      method: "POST",
      body: JSON.stringify({ videoId: beat.videoId, title: beat.title || "" }),
    }).then(function(){ setHidden(true); }).catch(function(err){ alert("Couldn't hide: " + (err.message || "failed")); });
  }

  if (hidden) return null;

  return (
    <div className="bf-card bf-breathe" style={{
      background: "linear-gradient(180deg, rgba(20,15,32,0.92), rgba(10,8,20,0.95))",
      borderRadius: 20, overflow: "hidden", marginBottom: 16,
      border: "1px solid " + borderClr,
      boxShadow: "0 10px 30px rgba(0,0,0,0.55), 0 0 22px " + glowClr + ", inset 0 1px 0 rgba(255,255,255,0.05)",
      backdropFilter: "blur(6px)", WebkitBackdropFilter: "blur(6px)",
    }}>
      {/* Thumbnail */}
      <div style={{ position: "relative", height: 190, cursor: "pointer", overflow: "hidden" }}
           onClick={() => onPlay(beat)}>
        {beat.thumbnail && !imgErr ? (
          <img src={beat.thumbnail} alt={beat.title} onError={() => setImgErr(true)}
            style={{ width: "100%", height: "100%", objectFit: "cover", display: "block", transform: "scale(1.02)" }} />
        ) : (
          <div style={{ width: "100%", height: "100%", background: "linear-gradient(135deg,#1a1a2e," + accentClr + "44)", display: "flex", alignItems: "center", justifyContent: "center" }}>
            <span style={{ fontSize: 40, opacity: 0.3 }}><AppIcon id="note" size={20}/></span>
          </div>
        )}
        {/* Gradient overlay */}
        <div style={{ position: "absolute", inset: 0, background: "linear-gradient(to top,rgba(0,0,0,0.75) 0%,rgba(0,0,0,0.15) 50%,rgba(0,0,0,0.05) 100%)" }} />
        {/* Neon play button */}
        <div style={{ position:"absolute", inset:0, display:"flex", alignItems:"center", justifyContent:"center", pointerEvents:"none" }}>
          <svg width="72" height="52" viewBox="0 0 72 52" fill="none" xmlns="http://www.w3.org/2000/svg">
            <defs>
              <filter id="neon-main" x="-40%" y="-40%" width="180%" height="180%">
                <feGaussianBlur stdDeviation="3" result="blur1"/>
                <feGaussianBlur stdDeviation="8" result="blur2"/>
                <feMerge><feMergeNode in="blur2"/><feMergeNode in="blur1"/><feMergeNode in="SourceGraphic"/></feMerge>
              </filter>
              <linearGradient id="ng-main" x1="0%" y1="0%" x2="100%" y2="0%">
                <stop offset="0%" stopColor="#9333ea"/>
                <stop offset="100%" stopColor="#3b82f6"/>
              </linearGradient>
            </defs>
            <rect x="3" y="3" width="66" height="46" rx="8" stroke="url(#ng-main)" strokeWidth="1.5" fill="black" filter="url(#neon-main)" opacity="0.9"/>
            <polygon points="29,16 29,36 48,26" stroke="url(#ng-main)" strokeWidth="1.5" fill="none" strokeLinejoin="round" filter="url(#neon-main)" opacity="0.9"/>
          </svg>
        </div>
        {/* Badges */}
        {(featured || exclusive) && (
          <div style={{ position: "absolute", top: 10, left: 12 }}>
            <div style={{ background: featured ? "linear-gradient(135deg,#C026D3,#7C3AED)" : "linear-gradient(135deg,#F59E0B,#EF4444)", borderRadius: 20, padding: "4px 12px", fontSize: 10, color: "white", fontWeight: 800, letterSpacing: 0.5 }}>
              {featured ? "★ FEATURED" : "EXCLUSIVE"}
            </div>
          </div>
        )}
        {/* Save button */}
        <button className="bf-save" onClick={e => { e.stopPropagation(); onSave(beat); }}
          style={{ position: "absolute", top: 10, right: 10, width: 34, height: 34, borderRadius: "50%", border: "none", cursor: "pointer", display: "flex", alignItems: "center", justifyContent: "center", fontSize: 16,
            background: isSaved ? "rgba(192,38,211,0.9)" : "rgba(0,0,0,0.55)",
            backdropFilter: "blur(8px)",
            boxShadow: isSaved ? "0 0 12px rgba(192,38,211,0.5)" : "none",
            color: isSaved ? "white" : "rgba(255,255,255,0.7)",
          }}>

        </button>
        {/* View count at bottom of image */}
        {beat.viewsLabel && (
          <div style={{ position: "absolute", bottom: 10, left: 12 }}>
            <div style={{ background: "rgba(0,0,0,0.7)", backdropFilter: "blur(6px)", border: "1px solid rgba(192,38,211,0.4)", borderRadius: 20, padding: "3px 10px", fontSize: 11, color: "#C026D3", fontWeight: 700 }}>
              {beat.viewsLabel}
            </div>
          </div>
        )}
      </div>
      {/* Info */}
      <div style={{ padding: "14px 16px 12px" }}>
        <div onClick={() => onPlay(beat)} style={{ color: "white", fontWeight: 700, fontSize: 14, lineHeight: 1.45, marginBottom: 6, cursor: "pointer",
          overflow: "hidden", display: "-webkit-box", WebkitLineClamp: 2, WebkitBoxOrient: "vertical" }}>
          {beat.title}
        </div>
        <div style={{ display: "flex", alignItems: "center", justifyContent: "space-between" }}>
          <div style={{ color: "#666", fontSize: 12, fontWeight: 500 }}>{beat.channel}</div>
          <a href={watchUrl(beat.videoId)} target="_blank" rel="noopener noreferrer"
            onClick={e => e.stopPropagation()}
            style={{ display: "flex", alignItems: "center", gap: 4, background: "rgba(255,0,0,0.1)", border: "1px solid rgba(255,0,0,0.25)", borderRadius: 20, padding: "3px 10px", color: "#FF4444", fontSize: 10, fontWeight: 700, textDecoration: "none" }}>
            ▶ YouTube
          </a>
        </div>
      </div>
    </div>
  );
}


// =============================================================================
// PLAN PICKER — shared monthly/annual toggle used everywhere plans are shown
// =============================================================================
// PLAN_PRICES — Stripe price IDs.
// These act as a FALLBACK. On app boot the frontend fetches /api/pricing
// and mutates this object in-place with server-provided values, so we can
// change pricing in one place (backend) without a frontend code deploy.
// The values below are kept in sync as the last-known-good defaults so
// the signup flow keeps working even if /api/pricing is briefly unavailable.
var PLAN_PRICES = {
  artist:   { monthly: "price_1TQDoFFHyNSCxas89UpDKiro",  yearly: "price_1TW9CXFHyNSCxas8TM826ULo" },
  producer: { monthly: "price_1TQDpBFHyNSCxas8cktbqw1n", yearly: "price_1TW9BnFHyNSCxas8vHDrVwB6" },
};

// =============================================================================
// CURRENCY DISPLAY (subscription pricing only — Deploy 1)
// =============================================================================
// We charge in GBP everywhere via Stripe. For international users who'd
// rather see prices in their currency at-a-glance, we display an APPROXIMATE
// conversion with a "charged in GBP" disclaimer.
//
// Important: this is DISPLAY ONLY. The actual Stripe charge is GBP. The
// user's bank converts to their currency at THEIR bank's FX rate, which
// may differ slightly from what we show. The disclaimer + "≈" prefix tell
// them this.
//
// FX rates are static here — refresh manually every few months. Live FX
// APIs add a network dependency we don't need for a beta launch.
// Rates last updated: May 2026
var CURRENCIES = {
  GBP: { code: "GBP", symbol: "£", rate: 1.00, label: "GBP" },
  USD: { code: "USD", symbol: "$", rate: 1.26, label: "USD" },
  EUR: { code: "EUR", symbol: "€", rate: 1.17, label: "EUR" },
};

function bfGetCurrencyPref() {
  try {
    var saved = localStorage.getItem("bf_currency");
    if (saved && CURRENCIES[saved]) return saved;
  } catch (_) {}
  return "GBP";
}

function bfSetCurrencyPref(code) {
  try { localStorage.setItem("bf_currency", code); } catch (_) {}
}

// Convert a GBP price to display string in chosen currency.
// Examples:
//   bfFormatPrice(4.99, "GBP", "/mo")  → "£4.99/mo"
//   bfFormatPrice(4.99, "USD", "/mo")  → "≈ $6.29/mo"
//   bfFormatPrice(49.99, "EUR", "/yr") → "≈ €58.49/yr"
// Free (0) is always shown plainly without currency, no symbol — looks cleanest.
function bfFormatPrice(gbpAmount, code, suffix) {
  if (!gbpAmount || gbpAmount === 0) return "Free";
  var c = CURRENCIES[code] || CURRENCIES.GBP;
  var converted = gbpAmount * c.rate;
  var amountStr = converted.toFixed(2);
  // Strip trailing .00 for clean look (e.g. £5 not £5.00)
  if (amountStr.endsWith(".00")) amountStr = amountStr.slice(0, -3);
  var prefix = (code === "GBP") ? "" : "≈ ";
  return prefix + c.symbol + amountStr + (suffix || "");
}

// Fetched server pricing — exposed so PlanPicker can read display strings
// straight from the API response (no hardcoded amounts on the frontend).
// Populated by the boot-time fetch in BeatFinderInner.
var SERVER_PRICING = null;

// Mutate PLAN_PRICES with server values. Called once at app boot. We mutate
// in place (not reassign) so existing references in components that already
// closed over PLAN_PRICES see the new values.
function applyServerPricing(serverData) {
  try {
    if (!serverData || !serverData.subscriptions) return;
    var subs = serverData.subscriptions;
    if (subs.artist && subs.artist.monthly && subs.artist.monthly.price_id) {
      PLAN_PRICES.artist.monthly = subs.artist.monthly.price_id;
    }
    if (subs.artist && subs.artist.annual && subs.artist.annual.price_id) {
      PLAN_PRICES.artist.yearly = subs.artist.annual.price_id;
    }
    if (subs.producer && subs.producer.monthly && subs.producer.monthly.price_id) {
      PLAN_PRICES.producer.monthly = subs.producer.monthly.price_id;
    }
    if (subs.producer && subs.producer.annual && subs.producer.annual.price_id) {
      PLAN_PRICES.producer.yearly = subs.producer.annual.price_id;
    }
    SERVER_PRICING = serverData;
  } catch (_) {
    // Swallow — fallback to hardcoded values, signup still works.
  }
}

function PlanPicker({ onSelectPlan, compact, selectedPlanId }) {
  var [billing, setBilling] = React.useState("monthly"); // "monthly" | "yearly"
  // Currency preference — defaults to GBP. Stored in localStorage so it
  // sticks across sessions. Pure display: Stripe still charges in GBP.
  var [currency, setCurrency] = React.useState(function() { return bfGetCurrencyPref(); });
  function changeCurrency(code) {
    setCurrency(code);
    bfSetCurrencyPref(code);
  }

  // Compute price strings dynamically based on chosen currency. We keep
  // the GBP amounts in `monthlyPrice`/`yearlyPrice` as the source of truth
  // and convert at render time. Saving is calculated in the same currency.
  var artistMonthly  = bfFormatPrice(4.99,  currency, "/mo");
  var artistYearly   = bfFormatPrice(49.99, currency, "/yr");
  var artistSaving   = bfFormatPrice(4.99 * 12 - 49.99, currency, "");
  var producerMonthly = bfFormatPrice(8.99,  currency, "/mo");
  var producerYearly  = bfFormatPrice(89.99, currency, "/yr");
  var producerSaving  = bfFormatPrice(8.99 * 12 - 89.99, currency, "");

  var plans = [
    {
      id: "free",
      label: "Free",
      color: "#6B7280",
      monthlyPrice: 0,
      yearlyPrice:  0,
      monthlyStr: "Free",
      yearlyStr:  "Free",
      saving:     "",
      perks: ["Browse all beats", "Preview 45 seconds", "Follow producers", "Write & save lyrics"],
    },
    {
      id: "artist",
      label: "Artist Pro",
      color: "#A855F7",
      monthlyPrice: 4.99,
      yearlyPrice:  49.99,
      monthlyStr: artistMonthly,
      yearlyStr:  artistYearly,
      saving:     "Save " + artistSaving,
      perks: ["Rhyme Finder tool", "Studio recording mode", "Your own profile", "Write lyrics to beats", "Save unlimited beats", "Exclusive member beats", "Download MP3s", "Purchase leases"],
    },
    {
      id: "producer",
      label: "Producer Pro",
      color: "#C026D3",
      monthlyPrice: 8.99,
      yearlyPrice:  89.99,
      monthlyStr: producerMonthly,
      yearlyStr:  producerYearly,
      saving:     "Save " + producerSaving,
      perks: ["Everything in Artist Pro", "Upload & sell beats", "Sell MP3 leases", "Download stats", "Verified badge", "Featured in rotation"],
    },
  ];

  return (
    <div>
      {/* Billing toggle */}
      <div style={{ display: "flex", background: "#111", border: "1px solid #222", borderRadius: 24, padding: 2, marginBottom: 8, position: "relative" }}>
        {["monthly", "yearly"].map(function(b) {
          var isActive = billing === b;
          return (
            <button key={b} onClick={function() {
              setBilling(b);
              // Re-fire onSelectPlan with the new price ID for the
              // currently selected plan so the parent's selectedPriceId
              // updates to match the new billing cycle. Without this,
              // toggling Annual after picking a plan would visually
              // highlight Annual but still submit the monthly price ID.
              if (onSelectPlan && selectedPlanId && selectedPlanId !== "free") {
                var newPriceId = PLAN_PRICES[selectedPlanId]
                  ? PLAN_PRICES[selectedPlanId][b]
                  : null;
                onSelectPlan(selectedPlanId, newPriceId, b);
              }
            }}
              style={{
                flex: 1, padding: "6px 0", borderRadius: 22, border: "none", cursor: "pointer", fontWeight: 700, fontSize: 11,
                background: isActive ? (b === "yearly" ? "linear-gradient(135deg,#C026D3,#7C3AED)" : "#1a1a1a") : "transparent",
                color: isActive ? "white" : "#555",
                transition: "all 0.18s",
                position: "relative",
              }}>
              {b === "monthly" ? "Monthly" : "Annual"}
              {b === "yearly" && (
                <span style={{
                  position: "absolute", top: -7, right: 4,
                  background: "linear-gradient(135deg,#22C55E,#16A34A)",
                  color: "white", fontSize: 7, fontWeight: 800, borderRadius: 20,
                  padding: "1px 5px", letterSpacing: 0.3,
                }}>SAVE 17%</span>
              )}
            </button>
          );
        })}
      </div>

      {/* Currency picker — display only, Stripe still charges in GBP.
          Compact pill row showing GBP / USD / EUR. Active one glows. */}
      <div style={{ display: "flex", alignItems: "center", justifyContent: "space-between",
        marginBottom: 6, padding: "0 2px",
      }}>
        <span style={{ color: "#666", fontSize: 9, fontWeight: 700, letterSpacing: 0.5 }}>
          DISPLAY CURRENCY
        </span>
        <div style={{ display: "flex", gap: 4 }}>
          {Object.keys(CURRENCIES).map(function(code) {
            var active = currency === code;
            return (
              <button key={code}
                onClick={function() { changeCurrency(code); }}
                style={{
                  background: active ? "linear-gradient(135deg,rgba(192,38,211,0.18),rgba(124,58,237,0.08))" : "transparent",
                  border: "1px solid " + (active ? "rgba(192,38,211,0.45)" : "#2a2a2a"),
                  color: active ? "#E9D5FF" : "#888",
                  fontSize: 9, fontWeight: 800, letterSpacing: 0.4,
                  padding: "3px 7px", borderRadius: 6,
                  cursor: "pointer",
                }}>
                {CURRENCIES[code].symbol} {code}
              </button>
            );
          })}
        </div>
      </div>

      {/* Plan cards */}
      <div style={{ display: "flex", gap: compact ? 6 : 8 }}>
        {plans.map(function(p) {
          var isFree    = (p.id === "free");
          var priceStr  = isFree ? p.monthlyStr : (billing === "yearly" ? p.yearlyStr : p.monthlyStr);
          var priceId   = isFree ? null : (PLAN_PRICES[p.id] ? PLAN_PRICES[p.id][billing] : null);
          var isSelected = selectedPlanId === p.id;
          // LED highlight colors: each plan gets a glow tint when selected.
          // Free = grey, Artist = electric blue, Producer = magenta. The
          // selected card lifts visually and pulses with a neon glow ring.
          var glowClr  = isFree ? "#9CA3AF" : (p.id === "producer" ? "#C026D3" : "#3B82F6");
          var glowClr2 = isFree ? "#9CA3AF" : (p.id === "producer" ? "#A855F7" : "#7C3AED");
          return (
            <div key={p.id}
              onClick={function() { if (onSelectPlan) onSelectPlan(p.id, priceId, billing); }}
              style={{
                flex: 1,
                background: isSelected
                  ? "linear-gradient(160deg,rgba(124,58,237,0.08) 0%,#0d0d12 60%,#0a0a0e 100%)"
                  : "#111",
                border: "1.5px solid " + (isSelected ? glowClr : (p.color + "55")),
                borderRadius: 14,
                padding: compact ? "8px 6px" : "14px 12px",
                textAlign: "left",
                position: "relative",
                overflow: "hidden",
                cursor: "pointer",
                transform: isSelected ? "translateY(-2px)" : "none",
                transition: "all 0.22s cubic-bezier(0.22, 1, 0.36, 1)",
                boxShadow: isSelected
                  ? "0 8px 28px " + glowClr + "44, 0 0 0 1px " + glowClr + "88, 0 0 32px " + glowClr2 + "55, inset 0 1px 0 rgba(255,255,255,0.08)"
                  : "none",
              }}>
              {/* LED corner glow when selected — softens edges with neon halo */}
              {isSelected && (
                <>
                  <div style={{
                    position: "absolute", top: -20, left: -20, width: 60, height: 60,
                    background: "radial-gradient(circle," + glowClr + "66 0%,transparent 70%)",
                    pointerEvents: "none",
                  }} />
                  <div style={{
                    position: "absolute", bottom: -20, right: -20, width: 60, height: 60,
                    background: "radial-gradient(circle," + glowClr2 + "55 0%,transparent 70%)",
                    pointerEvents: "none",
                  }} />
                </>
              )}
              {!isFree && billing === "yearly" && (
                <div style={{
                  position: "absolute", top: 0, right: 0,
                  background: "linear-gradient(135deg,#22C55E,#16A34A)",
                  color: "white", fontSize: 9, fontWeight: 800,
                  padding: "3px 8px", borderBottomLeftRadius: 8,
                }}>{p.saving}</div>
              )}
              {(function() {
                // Producer Pro gets a neon purple-to-blue gradient on its label and price text.
                // Other plans use solid p.color.
                var isProducer = p.id === "producer";
                var gradientStyle = isProducer ? {
                  background: "linear-gradient(135deg,#C026D3 0%,#A855F7 50%,#3B82F6 100%)",
                  WebkitBackgroundClip: "text",
                  WebkitTextFillColor: "transparent",
                  backgroundClip: "text",
                  color: "transparent",
                  filter: "drop-shadow(0 0 6px rgba(192,38,211,0.45))",
                } : null;
                return (
                  <>
                    <div style={Object.assign({
                      fontWeight: 800, fontSize: compact ? 10 : 13, marginBottom: 1,
                      color: isProducer ? "transparent" : "white",
                    }, gradientStyle || {})}>{p.label}</div>
                    <div style={Object.assign({
                      fontWeight: 800, fontSize: compact ? 11 : 16,
                      marginBottom: (!isFree && billing === "yearly") ? 1 : 6,
                      color: isProducer ? "transparent" : p.color,
                    }, gradientStyle || {})}>{priceStr}</div>
                  </>
                );
              })()}
              {!isFree && billing === "yearly" && (
                <div style={{ color: "#555", fontSize: 9, marginBottom: 6 }}>≈ £{(p.yearlyPrice / 12).toFixed(2)}/mo</div>
              )}
              {!compact && p.perks.map(function(perk) {
                return (
                  <div key={perk} style={{ color: "#bbb", fontSize: 11, marginBottom: 6, lineHeight: 1.3, display: "flex", alignItems: "flex-start", gap: 5 }}>
                    <span style={{ color: p.color, flexShrink: 0, fontWeight: 900 }}>+</span>
                    <span>{perk}</span>
                  </div>
                );
              })}
              {onSelectPlan && (
                <button
                  onClick={function(e) { e.stopPropagation(); onSelectPlan(p.id, priceId, billing); }}
                  style={{
                    width: "100%", marginTop: compact ? 5 : 12,
                    background: isSelected
                      ? "linear-gradient(135deg," + glowClr + " 0%," + glowClr2 + " 100%)"
                      : (isFree
                        ? "transparent"
                        : (p.id === "producer"
                          ? "linear-gradient(135deg,#C026D3 0%,#A855F7 50%,#3B82F6 100%)"
                          : "linear-gradient(135deg," + p.color + ",#7C3AED)")),
                    border: isSelected
                      ? ("1.5px solid " + glowClr)
                      : (isFree ? ("1.5px solid " + p.color) : "none"),
                    borderRadius: 8,
                    color: isSelected ? "white" : (isFree ? p.color : "white"),
                    fontWeight: 900, fontSize: compact ? 10 : 13,
                    padding: compact ? "5px 3px" : "11px",
                    cursor: "pointer",
                    letterSpacing: 0.5,
                    boxShadow: isSelected
                      ? "0 0 16px " + glowClr + "88, inset 0 1px 0 rgba(255,255,255,0.25)"
                      : (p.id === "producer" ? "0 2px 12px rgba(192,38,211,0.35), inset 0 1px 0 rgba(255,255,255,0.15)" : "none"),
                    textShadow: isSelected || p.id === "producer" ? "0 1px 2px rgba(0,0,0,0.3)" : "none",
                    transition: "all 0.18s ease",
                  }}>
                  {isFree ? "Start Free" : "Subscribe"}
                </button>
              )}
            </div>
          );
        })}
      </div>

      {/* FX disclaimer — only shown when user is viewing non-GBP prices.
          Critical for trust: their bank statement will show the GBP charge
          converted at the bank's own rate, which may differ slightly from
          what we show here. */}
      {currency !== "GBP" && (
        <div style={{
          marginTop: 8, padding: "8px 10px",
          background: "rgba(124,58,237,0.06)",
          border: "1px solid rgba(124,58,237,0.18)",
          borderRadius: 8,
          color: "#A78BFA", fontSize: 10, lineHeight: 1.4,
          fontFamily: "'DM Sans',sans-serif",
        }}>
          Approximate conversion shown. <strong style={{ color: "#C4B5FD" }}>Charged in GBP</strong> at checkout — your bank converts at its own rate.
        </div>
      )}
    </div>
  );
}

// =============================================================================
// BEAT FEED
// =============================================================================
function BeatFeed({ artistName, featured, exclusive, savedIds, onSave, onPlay, showPagination, filterTitle, instrumentalOnly, max, extraQueries, blockedChannels, requireInTitle }) {
  const [beats,   setBeats]   = useState([]);
  const [loading, setLoading] = useState(true);
  const [error,   setError]   = useState(null);
  const [page,    setPage]    = useState(1);
  // True total from the backend (length of the full master list for this
  // artist), so we can show the real number of pages upfront.
  const [total,   setTotal]   = useState(0);

  useEffect(() => {
    let alive = true;
    setLoading(true);
    setError(null);
    setBeats([]);
    window.scrollTo(0, 0);

    fetchBeats(artistName, page, filterTitle, max || 20, extraQueries).then(({ beats: b, error: e, total: t }) => {
      if (!alive) return;
      const INSTRUMENTAL_KEYWORDS = ["instrumental", "riddim", "beat", "free beat", "backing track"];
      let filtered = instrumentalOnly
        ? b.filter(beat => INSTRUMENTAL_KEYWORDS.some(kw => beat.title.toLowerCase().includes(kw)))
        : b;
      if (blockedChannels && blockedChannels.length > 0) {
        const blocked = blockedChannels.map(c => c.toLowerCase());
        filtered = filtered.filter(beat => !blocked.some(c => beat.channel.toLowerCase().includes(c)));
      }
      if (requireInTitle) {
        const term = requireInTitle.toLowerCase();
        // Whole-word-ish match so e.g. "trelli" doesn't match "t-rell"/"trello".
        const re = new RegExp("(?:^|[^a-z0-9])" + term.replace(/[.*+?^${}()|[\]\\]/g, "\\$&") + "(?:[^a-z0-9]|$)", "i");
        filtered = filtered.filter(beat => re.test(beat.title || "") || re.test(beat.channel || ""));
      }
      // Never render more than one page worth, regardless of what the backend
      // returned (defends against stacked/over-long pages).
      const PAGE_SIZE = max || 10;
      if (filtered.length > PAGE_SIZE) filtered = filtered.slice(0, PAGE_SIZE);
      setBeats(filtered);
      setError(e);
      setLoading(false);
      setTotal(t || 0);
    });

    return () => { alive = false; };
  }, [artistName, page, requireInTitle]);

  // Real page count from the backend's full master-list total.
  const PER = max || 10;
  const TOTAL_PAGES = Math.max(1, Math.ceil(total / PER));

  const PageNav = () => {
    if (!showPagination) return null;
    if (TOTAL_PAGES <= 1) return null;
    return (
    <div style={{ display: "flex", alignItems: "center", justifyContent: "space-between", marginBottom: 16, background: "#111", borderRadius: 12, padding: "10px 14px", border: "1px solid #1e1e1e" }}>
      <button
        onClick={() => { if (page > 1) setPage(p => p - 1); }}
        disabled={page === 1}
        style={{
          background: page === 1 ? "transparent" : "#1a1a1a",
          border: page === 1 ? "1px solid #222" : "1px solid #444",
          borderRadius: 10, color: page === 1 ? "#333" : "white",
          fontWeight: 800, fontSize: 14, padding: "8px 16px", cursor: page === 1 ? "not-allowed" : "pointer",
          display: "flex", alignItems: "center", gap: 6,
        }}>
        Prev
      </button>
      <div style={{ color: "#888", fontSize: 13, fontWeight: 600 }}>
        Page {page} of {TOTAL_PAGES}
      </div>
      <button
        onClick={() => { if (page < TOTAL_PAGES) setPage(p => p + 1); }}
        disabled={page === TOTAL_PAGES}
        style={{
          background: page === TOTAL_PAGES ? "transparent" : "#C026D3",
          border: page === TOTAL_PAGES ? "1px solid #222" : "none",
          borderRadius: 10, color: page === TOTAL_PAGES ? "#333" : "white",
          fontWeight: 800, fontSize: 14, padding: "8px 16px", cursor: page === TOTAL_PAGES ? "not-allowed" : "pointer",
          display: "flex", alignItems: "center", gap: 6,
        }}>
        Next
      </button>
    </div>
    );
  };

  if (loading) return <BFLoader type="bars" text="LOADING BEATS...PLEASE BE PATIENT." />;

  if (error && !beats.length) return (
    <div style={{ padding: "20px 0" }}>
      <div style={{
        background: "rgba(220,38,38,0.08)", border: "1px solid rgba(220,38,38,0.25)",
        borderRadius: 14, padding: 20, textAlign: "center",
      }}>
        <div style={{ color: "#F87171", fontWeight: 700, fontSize: 15, marginBottom: 8 }}>
          Could not load beats
        </div>
        <div style={{ color: "#888", fontSize: 13, lineHeight: 1.6 }}>{error}</div>
      </div>
    </div>
  );

  return (
    <>
      <PageNav />
      {beats.map(beat => (
        <BeatCard
          key={beat.videoId}
          beat={beat}
          savedIds={savedIds}
          onSave={onSave}
          onPlay={onPlay}
          featured={featured}
          exclusive={exclusive}
        />
      ))}
      {!beats.length && (
        <div style={{ textAlign: "center", padding: "60px 0", color: "#555", fontSize: 14 }}>
          No beats found.
        </div>
      )}

      {showPagination && TOTAL_PAGES > 1 && (
        <div style={{ padding: "20px 0 40px" }}>
          <div style={{ color: "#555", fontSize: 12, textAlign: "center", marginBottom: 14 }}>
            Page {page} of {TOTAL_PAGES}
          </div>
          <div style={{ display: "flex", flexWrap: "wrap", gap: 8, justifyContent: "center" }}>
            {Array.from({ length: TOTAL_PAGES }, (_, i) => i + 1).map(p => (
              <button
                key={p}
                onClick={() => setPage(p)}
                style={{
                  width: 40, height: 40, borderRadius: "50%",
                  background: page === p ? "#C026D3" : "#1a1a1a",
                  border: page === p ? "2px solid #C026D3" : "1.5px solid #333",
                  color: page === p ? "white" : "#888",
                  fontWeight: page === p ? 800 : 600,
                  fontSize: 14, cursor: "pointer",
                }}
              >
                {p}
              </button>
            ))}
          </div>
        </div>
      )}
    </>
  );
}

// =============================================================================
// HOME SCREEN
// =============================================================================
// =============================================================================
// WORKSPACE SECTION - personalised dashboard for Artist Pro & Producer Pro
// =============================================================================
function WorkspaceSection({ user, savedLyrics, onEditLyric, onPlay, savedIds, onSave, onGenreSearch, onGoMembers }) {
  const isProducer = user?.isPro || user?.plan === "producer";

  const [beatOfDay,      setBeatOfDay]      = useState(null);
  const [botdLoading,    setBotdLoading]    = useState(true);
  const [vibeActive,     setVibeActive]     = useState(null);
  const [producerStats,  setProducerStats]  = useState(null);

  const VIBES = [
    { label: "Hype",     q: "hard trap type beat",      emoji: "flame", color: "#EF4444" },
    { label: "Chill",    q: "chill lo fi type beat",    emoji: "chill", color: "#3B82F6" },
    { label: "Dark",     q: "dark drill type beat",     emoji: "dark_face", color: "#8B5CF6" },
    { label: "Melodic",  q: "melodic type beat",        emoji: "wave", color: "#06B6D4" },
    { label: "Romantic", q: "romantic r&b type beat",   emoji: "heart", color: "#EC4899" },
  ];

  // Derive top genre from recent searches stored in localStorage
  var topGenre = null;
  try {
    var recents = JSON.parse(localStorage.getItem("bf_recents") || "[]");
    var genreKeywords = ["trap","drill","rnb","r&b","afro","melodic","dancehall","sad","lo-fi","lofi","boom bap","reggae","grime"];
    for (var ri = 0; ri < recents.length; ri++) {
      var r = recents[ri].toLowerCase();
      for (var gi = 0; gi < genreKeywords.length; gi++) {
        if (r.includes(genreKeywords[gi])) { topGenre = recents[ri]; break; }
      }
      if (topGenre) break;
    }
    if (!topGenre && recents.length > 0) topGenre = recents[0];
  } catch (e) {}

  // Last unfinished lyric
  var lastLyric = null;
  if (savedLyrics && savedLyrics.length > 0) {
    lastLyric = savedLyrics[savedLyrics.length - 1];
  }

  // Beat of the Day - fetch once and cache in sessionStorage
  useEffect(function() {
    var cacheKey = "bf_botd_" + new Date().toDateString();
    try {
      var cached = sessionStorage.getItem(cacheKey);
      if (cached) { setBeatOfDay(JSON.parse(cached)); setBotdLoading(false); return; }
    } catch(e) {}

    var queries = ["type beat free 2025", "melodic trap type beat free", "uk drill type beat free"];
    var q = queries[new Date().getDate() % queries.length];
    apiFetch("/api/youtube/search?artist=" + encodeURIComponent(q) + "&max=20&page=1&filter_title=false")
      .then(function(d) {
        var beats = d.beats || [];
        if (beats.length > 0) {
          var pick = beats[new Date().getDate() % beats.length];
          try { sessionStorage.setItem(cacheKey, JSON.stringify(pick)); } catch(e) {}
          setBeatOfDay(pick);
        }
        setBotdLoading(false);
      })
      .catch(function() { setBotdLoading(false); });
  }, []);

  // Producer stats
  useEffect(function() {
    if (!isProducer) return;
    Promise.all([
      apiFetch("/api/producer/my-beats").catch(function() { return []; }),
      apiFetch("/api/producer/my-leases").catch(function() { return []; }),
    ]).then(function(results) {
      var beats = results[0];
      var leases = results[1];
      var totalRevenue = leases.reduce(function(s, l) {
        var p = parseFloat((l.price || "0").replace(/[^0-9.]/g, ""));
        return s + (isNaN(p) ? 0 : p);
      }, 0);
      var totalDownloads = beats.reduce(function(s, b) { return s + (b.downloads || 0); }, 0);
      setProducerStats({
        totalBeats: beats.length,
        totalDownloads: totalDownloads,
        totalRevenue: totalRevenue.toFixed(2),
        recentSales: leases.length,
      });
    });
  }, [isProducer]);

  // BeatFinder Rank - based on saves count (gamified)
  var savedCount = savedIds ? savedIds.size : 0;
  var rank = savedCount >= 50 ? "Top 1%" : savedCount >= 30 ? "Top 5%" : savedCount >= 20 ? "Top 10%" : savedCount >= 10 ? "Top 25%" : savedCount >= 5 ? "Top 50%" : "Rising";
  var rankEmoji = savedCount >= 30 ? "★★★" : savedCount >= 10 ? "★★" : "★";
  var streak = Math.min(savedCount + (savedLyrics ? savedLyrics.length : 0), 99);

  var firstName = user && user.name ? user.name.split(" ")[0] : "";

  var cardStyle = {
    background: "rgba(255,255,255,0.04)",
    border: "1px solid rgba(255,255,255,0.07)",
    borderRadius: 14, padding: "14px 12px",
    cursor: "pointer", textAlign: "left",
    width: "100%", boxSizing: "border-box",
  };

  return (
    <div style={{ padding: "0 16px", marginBottom: 28 }}>
      <div style={{
        background: "linear-gradient(135deg,#080014,#10002e,#0a0020)",
        border: "1px solid rgba(192,38,211,0.25)",
        borderRadius: 20, padding: "20px 16px",
        boxShadow: "0 8px 32px rgba(0,0,0,0.5), inset 0 1px 0 rgba(255,255,255,0.05)",
      }}>
        {/* Header */}
        <div style={{ marginBottom: 18 }}>
          <div style={{ color: "#C026D3", fontSize: 10, fontWeight: 800, letterSpacing: 2.5, marginBottom: 5 }}>
            YOUR WORKSPACE
          </div>
          <div style={{ color: "white", fontWeight: 800, fontSize: 18, lineHeight: 1.2 }}>
            {"Welcome back" + (firstName ? ", " + firstName : "") + "!"}
          </div>
          {isProducer && (
            <div style={{ display:"inline-flex", alignItems:"center", gap:5, color: "#C026D3", fontSize: 12, fontWeight: 700, marginTop: 3 }}><VerifiedBadge size={14} /> Producer Pro</div>
          )}
        </div>

        {/* 1. YOUR VIBE RIGHT NOW */}
        <div style={{ marginBottom: 16 }}>
          <div style={{ color: "#888", fontSize: 10, fontWeight: 700, letterSpacing: 1.5, marginBottom: 10 }}>
            <AppIcon id="target" size={20}/> YOUR VIBE RIGHT NOW
          </div>
          <div style={{ display: "flex", gap: 7, flexWrap: "wrap" }}>
            {VIBES.map(function(v) {
              var isActive = vibeActive === v.label;
              return (
                <button
                  key={v.label}
                  onClick={function() {
                    setVibeActive(v.label);
                    onGenreSearch(v.q);
                  }}
                  style={{
                    flexShrink: 0, padding: "7px 13px", borderRadius: 20, cursor: "pointer",
                    border: "1.5px solid " + (isActive ? v.color : "rgba(255,255,255,0.1)"),
                    background: isActive ? v.color + "22" : "rgba(255,255,255,0.03)",
                    color: isActive ? v.color : "#777",
                    fontWeight: 700, fontSize: 12, display: "flex", alignItems: "center", gap: 5,
                    transition: "all 0.15s ease",
                  }}>
                  <Icon id={v.emoji} size={14} color={isActive ? v.color : "#777"} />
                  {v.label}
                </button>
              );
            })}
          </div>
        </div>

        {/* Divider */}
        <div style={{ height: 1, background: "rgba(255,255,255,0.05)", marginBottom: 16 }} />

        {/* 2. TOP GENRE */}
        <div style={{ marginBottom: 16 }}>
          <div style={{ color: "#888", fontSize: 10, fontWeight: 700, letterSpacing: 1.5, marginBottom: 10 }}>
            <AppIcon id="flame" size={20}/> YOUR TOP GENRE
          </div>
          {topGenre ? (
            <button
              onClick={function() { onGenreSearch(topGenre); }}
              style={{
                width: "100%", background: "linear-gradient(135deg,rgba(192,38,211,0.15),rgba(124,58,237,0.15))",
                border: "1.5px solid rgba(192,38,211,0.35)", borderRadius: 12,
                padding: "12px 14px", cursor: "pointer", textAlign: "left",
                display: "flex", alignItems: "center", justifyContent: "space-between",
                boxSizing: "border-box",
              }}>
              <div>
                <div style={{ color: "white", fontWeight: 700, fontSize: 14 }}>{topGenre}</div>
                <div style={{ color: "#C026D3", fontSize: 11, marginTop: 2 }}>You keep coming back to this</div>
              </div>
              <span style={{ color: "#C026D3", fontSize: 18 }}>&#9654;</span>
            </button>
          ) : (
            <div style={{ color: "#444", fontSize: 13, fontStyle: "italic", padding: "10px 0" }}>
              Search a few artists to unlock your top genre
            </div>
          )}
        </div>

        {/* Divider */}
        <div style={{ height: 1, background: "rgba(255,255,255,0.05)", marginBottom: 16 }} />

        {/* 3. CONTINUE WRITING */}
        <div style={{ marginBottom: 16 }}>
          <div style={{ color: "#888", fontSize: 10, fontWeight: 700, letterSpacing: 1.5, marginBottom: 10, display: "flex", alignItems: "center", gap: 6 }}>
            <Icon id="edit" size={12} color="#888" strokeWidth={2} /> CONTINUE WRITING
          </div>
          {lastLyric ? (
            <button
              onClick={function() { onEditLyric(lastLyric, savedLyrics.length - 1); }}
              style={{
                width: "100%", background: "rgba(192,38,211,0.08)",
                border: "1.5px solid rgba(192,38,211,0.25)", borderRadius: 12,
                padding: "12px 14px", cursor: "pointer", textAlign: "left",
                display: "flex", alignItems: "center", gap: 12,
                boxSizing: "border-box",
              }}>
              <div style={{
                width: 38, height: 38, borderRadius: 10, flexShrink: 0,
                background: "linear-gradient(135deg,#6B21A8,#C026D3)",
                display: "flex", alignItems: "center", justifyContent: "center",
              }}>
                <Icon id="edit" size={18} color="white" strokeWidth={2} />
              </div>
              <div style={{ flex: 1, overflow: "hidden" }}>
                <div style={{ color: "white", fontWeight: 700, fontSize: 14, whiteSpace: "nowrap", overflow: "hidden", textOverflow: "ellipsis" }}>
                  {lastLyric.title || "Untitled"}
                </div>
                <div style={{ color: "#C026D3", fontSize: 11, marginTop: 2 }}>
                  {lastLyric.text ? (lastLyric.text.split(" ").length + " words") : "Empty"} — tap to continue
                </div>
              </div>
              <span style={{ color: "#555", fontSize: 18 }}>&#62;</span>
            </button>
          ) : (
            <button
              onClick={function() { onGoMembers(); }}
              style={{
                width: "100%", background: "rgba(255,255,255,0.03)",
                border: "1px solid rgba(255,255,255,0.07)", borderRadius: 12,
                padding: "12px 14px", cursor: "pointer", textAlign: "center",
                color: "#555", fontSize: 13, boxSizing: "border-box",
              }}>
              No lyrics yet — play a beat and tap Write Lyrics
            </button>
          )}
        </div>

        {/* Divider */}
        <div style={{ height: 1, background: "rgba(255,255,255,0.05)", marginBottom: 16 }} />

        {/* 4. BEAT OF THE DAY */}
        <div style={{ marginBottom: isProducer ? 16 : 0 }}>
          <div style={{ color: "#888", fontSize: 10, fontWeight: 700, letterSpacing: 1.5, marginBottom: 10 }}>
            <AppIcon id="note" size={20}/> BEAT OF THE DAY
          </div>
          {botdLoading ? (
            <div style={{ color: "#444", fontSize: 13, padding: "10px 0" }}>Finding your beat...</div>
          ) : beatOfDay ? (
            <div
              onClick={function() { onPlay(beatOfDay); }}
              style={{
                display: "flex", alignItems: "center", gap: 12, cursor: "pointer",
                background: "linear-gradient(135deg,rgba(245,158,11,0.08),rgba(239,68,68,0.08))",
                border: "1.5px solid rgba(245,158,11,0.25)", borderRadius: 12, padding: "10px 12px",
              }}>
              <div style={{ position: "relative", flexShrink: 0 }}>
                <img
                  src={beatOfDay.thumbnail || "https://img.youtube.com/vi/" + beatOfDay.videoId + "/hqdefault.jpg"}
                  alt={beatOfDay.title}
                  style={{ width: 52, height: 52, borderRadius: 10, objectFit: "cover", display: "block" }}
                />
                <div style={{
                  position: "absolute", inset: 0, borderRadius: 10,
                  display: "flex", alignItems: "center", justifyContent: "center",
                  background: "rgba(0,0,0,0.4)",
                }}>
                  <svg width="16" height="16" viewBox="0 0 24 24" fill="white"><path d="M8 5v14l11-7z"/></svg>
                </div>
              </div>
              <div style={{ flex: 1, overflow: "hidden" }}>
                <div style={{ color: "white", fontWeight: 700, fontSize: 13, lineHeight: 1.3, overflow: "hidden", display: "-webkit-box", WebkitLineClamp: 2, WebkitBoxOrient: "vertical" }}>
                  {beatOfDay.title}
                </div>
                <div style={{ color: "#F59E0B", fontSize: 11, marginTop: 3, fontWeight: 600 }}>
                  Picked for you today
                </div>
              </div>
              <button
                className="bf-save"
                onClick={function(e) { e.stopPropagation(); onSave(beatOfDay); }}
                style={{
                  flexShrink: 0, width: 30, height: 30, borderRadius: "50%", border: "none",
                  cursor: "pointer", display: "flex", alignItems: "center", justifyContent: "center", fontSize: 14,
                  background: savedIds && savedIds.has(beatOfDay.videoId) ? "rgba(192,38,211,0.9)" : "rgba(255,255,255,0.08)",
                  color: savedIds && savedIds.has(beatOfDay.videoId) ? "white" : "rgba(255,255,255,0.5)",
                }}>

              </button>
            </div>
          ) : (
            <div style={{ color: "#444", fontSize: 13, fontStyle: "italic" }}>Could not load today's beat</div>
          )}
        </div>

        {/* PRODUCER ONLY SECTIONS */}
        {isProducer && (
          <div>
            {/* Divider */}
            <div style={{ height: 1, background: "rgba(255,255,255,0.05)", marginBottom: 16 }} />

            {/* 5. PRODUCER EARNINGS SNAPSHOT */}
            <div style={{ marginBottom: 16 }}>
              <div style={{ color: "#888", fontSize: 10, fontWeight: 700, letterSpacing: 1.5, marginBottom: 10 }}>
                <AppIcon id="money" size={20}/> PRODUCER EARNINGS SNAPSHOT
              </div>
              {producerStats ? (
                <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: 8 }}>
                  {[
                    { label: "Beats Uploaded",  value: producerStats.totalBeats,     color: "#C026D3", icon: "note" },
                    { label: "Total Downloads",  value: producerStats.totalDownloads, color: "#3B82F6", icon: "download" },
                    { label: "Total Revenue",    value: "£" + producerStats.totalRevenue, color: "#22C55E", icon: "moneyfly" },
                    { label: "Leases Sold",      value: producerStats.recentSales,   color: "#F59E0B", icon: "edit" },
                  ].map(function(stat) {
                    return (
                      <div key={stat.label} style={{
                        background: "rgba(255,255,255,0.03)",
                        border: "1px solid rgba(255,255,255,0.07)",
                        borderRadius: 12, padding: "12px 10px", textAlign: "center",
                      }}>
                        <div style={{ marginBottom: 4 }}><Icon id={stat.icon} size={18} color="#888" strokeWidth={1.6} /></div>
                        <div style={{ color: stat.color, fontWeight: 800, fontSize: 18, lineHeight: 1 }}>
                          {stat.value}
                        </div>
                        <div style={{ color: "#555", fontSize: 10, fontWeight: 600, marginTop: 4, letterSpacing: 0.5 }}>
                          {stat.label.toUpperCase()}
                        </div>
                      </div>
                    );
                  })}
                </div>
              ) : (
                <div style={{ color: "#444", fontSize: 13, padding: "10px 0" }}>Loading your stats...</div>
              )}
            </div>

            {/* Divider */}
            <div style={{ height: 1, background: "rgba(255,255,255,0.05)", marginBottom: 16 }} />

            {/* 6. BEATFINDER RANK */}
            <div>
              <div style={{ color: "#888", fontSize: 10, fontWeight: 700, letterSpacing: 1.5, marginBottom: 10 }}>
                <AppIcon id="trophy" size={20}/> YOUR BEATFINDER RANK
              </div>
              <div style={{
                background: "linear-gradient(135deg,rgba(192,38,211,0.12),rgba(245,158,11,0.12))",
                border: "1.5px solid rgba(192,38,211,0.2)",
                borderRadius: 14, padding: "14px 16px",
                display: "flex", alignItems: "center", gap: 14,
              }}>
                <div style={{
                  width: 48, height: 48, borderRadius: 12, flexShrink: 0,
                  background: "linear-gradient(135deg,#C026D3,#F59E0B)",
                  display: "flex", alignItems: "center", justifyContent: "center", fontSize: 22,
                }}>
                  {rankEmoji}
                </div>
                <div style={{ flex: 1 }}>
                  <div style={{ color: "white", fontWeight: 800, fontSize: 16 }}>{rank}</div>
                  <div style={{ color: "#888", fontSize: 12, marginTop: 2 }}>
                    {streak} activity points this week
                  </div>
                </div>
                <div style={{
                  background: "linear-gradient(135deg,#C026D3,#7C3AED)",
                  borderRadius: 20, padding: "5px 14px",
                  color: "white", fontWeight: 800, fontSize: 12, flexShrink: 0,
                }}>
                  PRO
                </div>
              </div>
              <div style={{ color: "#444", fontSize: 11, marginTop: 8, textAlign: "center" }}>
                Save more beats and write more lyrics to climb the ranks
              </div>
            </div>
          </div>
        )}
      </div>
    </div>
  );
}

// =============================================================================
// NOTIFICATIONS PANEL
// =============================================================================
function NotificationsPanel({ user, onClose, onJumpToFeed, onOpenPost }) {
  var [notifs,  setNotifs]  = React.useState([]);
  var [loading, setLoading] = React.useState(true);

  React.useEffect(function() {
    if (!user) { setLoading(false); return; }
    apiFetch("/api/notifications", { silent: true })
      .then(function(d) { setNotifs(d || []); setLoading(false); })
      .catch(function() { setLoading(false); });
    // Mark all read when panel opens
    apiFetch("/api/notifications/mark-read", { method: "POST" }).catch(function() {});
  }, [user]);

  function iconForType(type) {
    if (type === "like") return (
      <svg width="16" height="16" viewBox="0 0 24 24" fill="#EF4444" stroke="none"><path d="M20.84 4.61a5.5 5.5 0 0 0-7.78 0L12 5.67l-1.06-1.06a5.5 5.5 0 0 0-7.78 7.78l1.06 1.06L12 21.23l7.78-7.78 1.06-1.06a5.5 5.5 0 0 0 0-7.78z"/></svg>
    );
    if (type === "comment") return (
      <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="#C026D3" strokeWidth="2" strokeLinecap="round"><path d="M21 15a2 2 0 0 1-2 2H7l-4 4V5a2 2 0 0 1 2-2h14a2 2 0 0 1 2 2z"/></svg>
    );
    if (type === "purchase") return (
      <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="#22C55E" strokeWidth="2" strokeLinecap="round"><path d="M12 2v20M17 5H9.5a3.5 3.5 0 0 0 0 7h5a3.5 3.5 0 0 1 0 7H6"/></svg>
    );
    if (type === "message") return (
      <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="#3B82F6" strokeWidth="2" strokeLinecap="round"><path d="M21 15a2 2 0 0 1-2 2H7l-4 4V5a2 2 0 0 1 2-2h14a2 2 0 0 1 2 2z"/></svg>
    );
    if (type === "post") return (
      // Wifi/signal icon for new posts from people you follow
      <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="#A78BFA" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
        <path d="M5 12.55a11 11 0 0 1 14.08 0"/>
        <path d="M1.42 9a16 16 0 0 1 21.16 0"/>
        <path d="M8.53 16.11a6 6 0 0 1 6.95 0"/>
        <circle cx="12" cy="20" r="0.5" fill="#A78BFA"/>
      </svg>
    );
    return (
      <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="#888" strokeWidth="2" strokeLinecap="round"><circle cx="12" cy="12" r="10"/><line x1="12" y1="8" x2="12" y2="12"/><line x1="12" y1="16" x2="12.01" y2="16"/></svg>
    );
  }

  function timeAgo(iso) {
    var diff = (Date.now() - new Date(iso).getTime()) / 1000;
    if (diff < 60) return "just now";
    if (diff < 3600) return Math.floor(diff / 60) + "m ago";
    if (diff < 86400) return Math.floor(diff / 3600) + "h ago";
    return Math.floor(diff / 86400) + "d ago";
  }

  // Tap behaviour: if the notification carries a post id, jump straight
  // to that post in the Feed (the Feed will scroll-to + flash-highlight
  // it). Otherwise — older notifs that pre-date the postId field —
  // fall back to jumping to the Feed tab generically.
  function handleNotifTap(n) {
    // Backend stores it as `postId` (camelCase), but be defensive about
    // alternate spellings just in case.
    var pid = n.postId || n.post_id || n.targetId || "";
    if (pid && onOpenPost) {
      onOpenPost(pid);
      return;
    }
    // No post id — fall back. For post-type notifs at minimum we can
    // still take the user to the Feed; for like/comment without a
    // postId we sadly can't do much better.
    if (n.type === "post" && onJumpToFeed) {
      onJumpToFeed();
      onClose();
    }
  }

  return (
    <div style={{ position: "fixed", inset: 0, zIndex: 9500, background: "rgba(0,0,0,0.7)" }}
      onClick={function(e) { if (e.target === e.currentTarget) onClose(); }}>
      <div style={{
        position: "absolute", top: 0, right: 0, width: "min(360px, 100vw)",
        height: "100dvh",
        background: "linear-gradient(180deg,#0f0a1f 0%,#0a0a14 60%,#080812 100%)",
        borderLeft: "1px solid rgba(124,58,237,0.25)",
        display: "flex", flexDirection: "column",
        paddingTop: "var(--bf-safe-top, env(safe-area-inset-top))",
        boxShadow: "-12px 0 32px rgba(0,0,0,0.5)",
      }}>
        {/* LED top edge */}
        <div style={{
          position: "absolute", top: "var(--bf-safe-top, env(safe-area-inset-top))", left: 0, right: 0, height: 1,
          background: "linear-gradient(90deg,transparent,rgba(192,38,211,0.4),rgba(124,58,237,0.4),rgba(59,130,246,0.4),transparent)",
        }} />
        {/* Header */}
        <div style={{ display: "flex", alignItems: "center", justifyContent: "space-between",
          padding: "16px 16px 12px", borderBottom: "1px solid rgba(124,58,237,0.2)", flexShrink: 0 }}>
          <div style={{
            fontFamily: "'Bebas Neue',sans-serif",
            fontSize: 22, letterSpacing: 2, fontWeight: 700,
            background: "linear-gradient(90deg,#C026D3,#7C3AED,#3B82F6)",
            WebkitBackgroundClip: "text", backgroundClip: "text",
            color: "transparent",
            filter: "drop-shadow(0 0 8px rgba(124,58,237,0.3))",
          }}>NOTIFICATIONS</div>
          <button onClick={onClose}
            style={{ background: "none", border: "none", color: "#A78BFA", fontSize: 22, cursor: "pointer", padding: 4 }}>
            ✕
          </button>
        </div>

        {/* List */}
        <div style={{ flex: 1, overflowY: "auto", WebkitOverflowScrolling: "touch" }}
          onTouchMove={function(e) { e.stopPropagation(); }}>
          {loading && (
            <div style={{ textAlign: "center", padding: "60px 0", color: "#A78BFA" }}>Loading...</div>
          )}
          {!loading && notifs.length === 0 && (
            <div style={{ textAlign: "center", padding: "60px 24px" }}>
              <svg width="48" height="48" viewBox="0 0 24 24" fill="none" stroke="rgba(124,58,237,0.4)" strokeWidth="1.5" strokeLinecap="round" style={{ marginBottom: 12, filter:"drop-shadow(0 0 8px rgba(124,58,237,0.3))" }}>
                <path d="M18 8A6 6 0 0 0 6 8c0 7-3 9-3 9h18s-3-2-3-9"/><path d="M13.73 21a2 2 0 0 1-3.46 0"/>
              </svg>
              <div style={{ color: "#A78BFA", fontWeight: 700, fontSize: 15 }}>No notifications yet</div>
              <div style={{ color: "#6b6685", fontSize: 13, marginTop: 6 }}>Likes, comments, posts and purchases will show here</div>
            </div>
          )}
          {!loading && notifs.map(function(n) {
            // Clickable when we have something to navigate to: either
            // an explicit postId on the notif, or a post-type notif we
            // can take to the Feed.
            var hasPostId = !!(n.postId || n.post_id || n.targetId);
            var clickable = hasPostId || n.type === "post";
            return (
              <div key={n.id} onClick={function(){ handleNotifTap(n); }}
                style={{
                  display: "flex", alignItems: "flex-start", gap: 12,
                  padding: "14px 16px", borderBottom: "1px solid rgba(124,58,237,0.1)",
                  background: n.read ? "transparent" : "rgba(124,58,237,0.06)",
                  cursor: clickable ? "pointer" : "default",
                }}>
                <div style={{ width: 36, height: 36, borderRadius: "50%", flexShrink: 0,
                  background: "linear-gradient(135deg,rgba(124,58,237,0.15),rgba(192,38,211,0.10))",
                  border: "1px solid rgba(124,58,237,0.25)",
                  display: "flex", alignItems: "center", justifyContent: "center" }}>
                  {iconForType(n.type)}
                </div>
                <div style={{ flex: 1, minWidth: 0 }}>
                  <div style={{ color: "white", fontSize: 13, lineHeight: 1.5 }}>{n.text}</div>
                  <div style={{ color: "#6b6685", fontSize: 11, marginTop: 4 }}>{timeAgo(n.createdAt)}</div>
                </div>
                {!n.read && (
                  <div style={{ width: 8, height: 8, borderRadius: "50%", background: "#C026D3", flexShrink: 0, marginTop: 4, boxShadow:"0 0 6px rgba(192,38,211,0.6)" }} />
                )}
              </div>
            );
          })}
        </div>
      </div>
    </div>
  );
}

// ─────────────────────────────────────────────────────────────────────────
// UserSearchOverlay
// ─────────────────────────────────────────────────────────────────────────
// Quick-find users by username from the top bar. Opens as a full-screen
// modal with a sticky search input at the top + live results below. As
// the person types we debounce ~250ms then hit /api/auth/search?q=...
// which already exists and returns up to 20 matches with username, name,
// plan, bio, avatarUrl. Tapping a result fires onPickUser(username) and
// closes the overlay — the parent decides what to do (open public
// profile, typically). Designed to feel like Twitter's user-search
// dropdown: a tiny input, fast feedback, large tap targets per row.
function UserSearchOverlay({ onClose, onPickUser }) {
  const [q, setQ]               = React.useState("");
  const [results, setResults]   = React.useState([]);
  const [loading, setLoading]   = React.useState(false);
  const inputRef                = React.useRef(null);

  // Auto-focus the input as soon as the overlay opens so the user can
  // start typing without an extra tap.
  React.useEffect(function() {
    var t = setTimeout(function() {
      if (inputRef.current) inputRef.current.focus();
    }, 80);
    return function() { clearTimeout(t); };
  }, []);

  // Debounced search — fire after the user pauses typing for 250ms.
  // Backend requires q.length >= 2 (returns [] otherwise), so we don't
  // bother making the network call until we've got that.
  React.useEffect(function() {
    var query = q.trim();
    if (query.length < 2) { setResults([]); setLoading(false); return; }
    setLoading(true);
    var cancelled = false;
    var t = setTimeout(function() {
      apiFetch("/api/auth/search?q=" + encodeURIComponent(query))
        .then(function(data) {
          if (cancelled) return;
          setResults(Array.isArray(data) ? data : []);
          setLoading(false);
        })
        .catch(function() {
          if (cancelled) return;
          setResults([]); setLoading(false);
        });
    }, 250);
    return function() { cancelled = true; clearTimeout(t); };
  }, [q]);

  return (
    <div onClick={onClose} style={{
      position:"fixed", inset:0, zIndex:9500,
      background:"rgba(0,0,0,0.85)",
      backdropFilter:"blur(6px)",
      WebkitBackdropFilter:"blur(6px)",
      display:"flex", flexDirection:"column",
      paddingTop:"calc(12px + var(--bf-safe-top, env(safe-area-inset-top)))",
    }}>
      <div onClick={function(e) { e.stopPropagation(); }}
        style={{ display:"flex", flexDirection:"column", flex:1, minHeight:0 }}>
        {/* Search header */}
        <div style={{ padding:"4px 14px 10px", display:"flex", alignItems:"center", gap:8 }}>
          <div style={{
            flex:1, position:"relative",
            background:"#15131c",
            border:"1px solid #2a2336",
            borderRadius:14,
            display:"flex", alignItems:"center", padding:"0 12px",
          }}>
            <svg width="16" height="16" viewBox="0 0 24 24" fill="none"
              stroke="#777" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"
              style={{ flexShrink:0 }}>
              <circle cx="11" cy="11" r="8"/><line x1="21" y1="21" x2="16.65" y2="16.65"/>
            </svg>
            <input
              ref={inputRef}
              value={q}
              onChange={function(e) { setQ(e.target.value); }}
              placeholder="Search users by username"
              autoComplete="off"
              autoCapitalize="none"
              autoCorrect="off"
              spellCheck={false}
              style={{
                flex:1, background:"transparent", border:"none", outline:"none",
                color:"white", fontSize:15, padding:"12px 10px",
                fontFamily:"inherit",
              }}
            />
            {q && (
              <button onClick={function() { setQ(""); }}
                style={{
                  background:"#241e30", border:"none", color:"#888",
                  borderRadius:"50%", width:22, height:22, cursor:"pointer",
                  display:"flex", alignItems:"center", justifyContent:"center",
                  padding:0, fontSize:11, flexShrink:0,
                }}>✕</button>
            )}
          </div>
          <button onClick={onClose}
            style={{
              background:"transparent", border:"none", color:"#A78BFA",
              fontSize:14, fontWeight:700, cursor:"pointer", padding:"8px 4px",
            }}>Cancel</button>
        </div>

        {/* Results */}
        <div style={{ flex:1, overflowY:"auto", WebkitOverflowScrolling:"touch", padding:"4px 0 24px" }}>
          {q.trim().length < 2 ? (
            <div style={{ color:"#555", fontSize:13, textAlign:"center", padding:"60px 30px", lineHeight:1.5 }}>
              <div style={{ fontSize:30, marginBottom:10, opacity:0.5 }}>🔍</div>
              <div style={{ color:"#888", fontWeight:700, marginBottom:4 }}>Find people on BeatFinder</div>
              <div style={{ color:"#555", fontSize:12 }}>Type a username to start searching.</div>
            </div>
          ) : loading ? (
            <div style={{ color:"#555", fontSize:13, textAlign:"center", padding:"40px 0" }}>Searching...</div>
          ) : results.length === 0 ? (
            <div style={{ color:"#555", fontSize:13, textAlign:"center", padding:"40px 30px", lineHeight:1.5 }}>
              <div style={{ color:"#888", fontWeight:700, marginBottom:4 }}>No matches for "{q.trim()}"</div>
              <div style={{ color:"#444", fontSize:12 }}>Try a different spelling.</div>
            </div>
          ) : (
            results.map(function(u) {
              var initial = (u.username || u.name || "?")[0].toUpperCase();
              return (
                <button key={u.username}
                  onClick={function() { onPickUser(u.username); }}
                  style={{
                    width:"100%", display:"flex", alignItems:"center", gap:12,
                    padding:"10px 16px", background:"transparent",
                    border:"none", borderBottom:"1px solid #15131c",
                    cursor:"pointer", textAlign:"left",
                  }}>
                  {/* Avatar */}
                  <div style={{
                    width:44, height:44, borderRadius:"50%", flexShrink:0,
                    background: u.avatarUrl ? "#222" : "linear-gradient(135deg,#6B21A8,#C026D3)",
                    overflow:"hidden",
                    display:"flex", alignItems:"center", justifyContent:"center",
                    color:"white", fontWeight:800, fontSize:16,
                  }}>
                    {u.avatarUrl
                      ? <img src={u.avatarUrl} alt={u.username}
                          style={{ width:"100%", height:"100%", objectFit:"cover" }} />
                      : initial}
                  </div>
                  {/* Name + handle + plan */}
                  <div style={{ flex:1, minWidth:0 }}>
                    <div style={{ display:"flex", alignItems:"center", gap:5 }}>
                      <span style={{
                        color:"white", fontWeight:800, fontSize:14,
                        overflow:"hidden", textOverflow:"ellipsis", whiteSpace:"nowrap",
                      }}>{u.name || u.username}</span>
                      <UserBadges username={u.username} plan={u.plan} compact={true} size={11} />
                    </div>
                    <div style={{ color:"#666", fontSize:12, marginTop:1,
                      overflow:"hidden", textOverflow:"ellipsis", whiteSpace:"nowrap" }}>
                      @{u.username}
                      {u.bio && <span style={{ color:"#444" }}>{" · " + u.bio.slice(0, 60)}</span>}
                    </div>
                  </div>
                </button>
              );
            })
          )}
        </div>
      </div>
    </div>
  );
}

function HomeScreen({ savedIds, onSave, onPlay, user, onGoMembers, onGoProfile, onGenreSearch, savedLyrics, onEditLyric, onGoTrending, onGoStudio, onWriteLyrics, onGoArtists, onGoUpload, onGoSection, onShowProducerPrompt, onOpenMessages, onViewOwnProfile, onOpenPost, onOpenNotifications, onOpenUserSearch, unreadMessages, unreadNotifications }) {
  const [heroIndex, setHeroIndex] = useState(0);
  const [studioImgIdx, setStudioImgIdx] = useState(0);
  // Trending Right Now — surfaces the real trending feed on Home.
  const [homeTrending, setHomeTrending] = useState([]);
  const [homeTrendLoading, setHomeTrendLoading] = useState(true);
  useEffect(function(){
    const today = new Date();
    const daySeed = today.getUTCFullYear() + "-" + (today.getUTCMonth()+1) + "-" + today.getUTCDate();
    apiFetch("/api/youtube/trending?day=" + encodeURIComponent(daySeed))
      .then(function(d){ setHomeTrending((d && d.beats) || []); setHomeTrendLoading(false); })
      .catch(function(){ setHomeTrendLoading(false); });
  }, []);

  const HERO_SLIDES = [
    {
      title: "Find Your Sound",
      sub: "Millions of type beats - one tap away",
      emoji: "note",
      grad: "linear-gradient(135deg,#12002a 0%,#3b0070 60%,#C026D3 100%)",
      cta: "Explore Beats",
      artistsSlide: true,
      btnColor: "rgba(192,38,211,0.5)",
      btnBorder: "rgba(192,38,211,0.7)",
      imgs: ["https://i.ibb.co/xTNw33W/IMG-1073.jpg", "https://i.ibb.co/JFKq1C1t/IMG-1086.jpg"],
    },
    {
      title: "Write & Save Lyrics",
      sub: "Write lyrics whilst beats play & save to your profile",
      emoji: "note",
      grad: "linear-gradient(135deg,#001a0a 0%,#003318 40%,#065f2f 75%,#16A34A 100%)",
      cta: "Write Lyrics",
      lyricsSlide: true,
      btnColor: "rgba(139,92,246,0.45)",
      btnBorder: "rgba(176,38,255,0.7)",
      img: "https://i.ibb.co/whB3g6h7/IMG-1076.jpg",
    },
    {
      title: "Rhyme Finder",
      sub: "Find perfect rhymes & multi-syllable rhymes while you write",
      emoji: "note",
      grad: "linear-gradient(135deg,#1a0a00 0%,#3d1800 40%,#7c3500 75%,#EA580C 100%)",
      cta: "Pro Plan Required",
      proSlide: true,
      btnColor: "rgba(139,92,246,0.45)",
      btnBorder: "rgba(176,38,255,0.7)",
      img: "https://i.ibb.co/XkS3JZLG/IMG-1075.jpg",
    },
    {
      title: "Discover Rising Producers",
      sub: "Tap in with producers worldwide",
      emoji: "note",
      grad: "linear-gradient(135deg,#001230 0%,#002a70 60%,#3B82F6 100%)",
      cta: "Find Producers",
      btnColor: "rgba(59,130,246,0.5)",
      btnBorder: "rgba(59,130,246,0.7)",
      trendingSlide: true,
      img: "https://i.ibb.co/b5mW038q/IMG-1077.jpg",
    },
    {
      title: "Sell Your Beats",
      sub: "Upload, price, and earn on every lease",
      emoji: "note",
      grad: "linear-gradient(135deg,#180800 0%,#3a1500 60%,#F59E0B 100%)",
      cta: "Purchase Producer Pro",
      producerSlide: true,
      btnColor: "rgba(139,92,246,0.45)",
      btnBorder: "rgba(176,38,255,0.7)",
      img: "https://i.ibb.co/mCfh68H2/IMG-1078.jpg",
    },
    {
      title: "Record & Make Beats in Studio",
      sub: "Create projects, record vocals over any beat or produce your own beats & export your mix",
      emoji: "note",
      grad: "linear-gradient(135deg,#0a001a 0%,#1a0033 40%,#4c0080 75%,#7C3AED 100%)",
      cta: "Open Studio",
      studioSlide: true,
      btnColor: "rgba(124,58,237,0.5)",
      btnBorder: "rgba(124,58,237,0.8)",
      badge: "PRO",
      imgs: ["https://i.ibb.co/vv4B0p7m/IMG-1079.jpg", "https://i.ibb.co/v62b3q9C/IMG-1070.jpg", "https://i.ibb.co/5xtCWFc0/IMG-1068.jpg"],
    },
  ];

  const GENRES = [
    { label: "Trap",       q: "trap type beat",       color: "#C026D3", emoji: "flame" },
    { label: "UK Drill",   q: "uk drill type beat",   color: "#3B82F6", emoji: "target" },
    { label: "R&B",        q: "rnb type beat",        color: "#F59E0B", emoji: "note" },
    { label: "Afrobeat",   q: "afrobeat type beat",   color: "#22C55E", emoji: "globe" },
    { label: "Melodic",    q: "melodic type beat",    color: "#818CF8", emoji: "wave" },
    { label: "Dancehall",  q: "dancehall riddim",     color: "#EC4899", emoji: "festival" },
  ];

  const [heroPaused, setHeroPaused] = useState(false);
  const heroTouchX = useRef(null);

  useEffect(() => {
    if (heroPaused) return;
    // Multi-image slides dwell exactly long enough to show each image once
    // (count × 3500ms), so they advance without repeating the first image.
    const s = HERO_SLIDES[heroIndex];
    const dwell = (s && s.imgs) ? (s.imgs.length * 3500) : 4200;
    const t = setTimeout(() => setHeroIndex(i => (i + 1) % HERO_SLIDES.length), dwell);
    return () => clearTimeout(t);
  }, [heroPaused, heroIndex]);

  const slide = HERO_SLIDES[heroIndex];

  // Cycle the studio slide's multiple screenshots while it's on screen.
  useEffect(function(){
    setStudioImgIdx(0);
    if (!slide.imgs || slide.imgs.length < 2) return;
    const t = setInterval(function(){ setStudioImgIdx(function(i){ return (i + 1) % slide.imgs.length; }); }, 3500);
    return function(){ clearInterval(t); };
  }, [heroIndex]);
  const heroImg = slide.imgs ? slide.imgs[studioImgIdx % slide.imgs.length] : slide.img;

  const handleHeroTouchStart = (e) => {
    heroTouchX.current = e.touches[0].clientX;
    setHeroPaused(true);
  };

  const handleHeroTouchEnd = (e) => {
    if (heroTouchX.current === null) return;
    const diff = heroTouchX.current - e.changedTouches[0].clientX;
    if (Math.abs(diff) > 40) {
      if (diff > 0) {
        setHeroIndex(i => (i + 1) % HERO_SLIDES.length);
      } else {
        setHeroIndex(i => (i - 1 + HERO_SLIDES.length) % HERO_SLIDES.length);
      }
    }
    heroTouchX.current = null;
    setTimeout(() => setHeroPaused(false), 3000);
  };

  const SectionHead = ({ emoji, title, sub, color }) => {
    const c = color || "#B026FF";
    return (
      <div style={{ paddingLeft: 16, marginBottom: 14 }}>
        <div style={{ display: "flex", alignItems: "center", gap: 10, marginBottom: 3 }}>
          <span style={{
            display: "inline-flex", alignItems: "center", justifyContent: "center",
            width: 30, height: 30, borderRadius: 9, flexShrink: 0,
            background: "linear-gradient(135deg, " + c + "33, " + c + "14)",
            border: "1px solid " + c + "66", color: c,
            boxShadow: "0 0 16px " + c + "44, inset 0 1px 0 rgba(255,255,255,0.06)",
          }}><AppIcon id={emoji} size={16} /></span>
          <span style={{ color: c, fontWeight: 800, fontSize: 17,
            fontFamily: "'Bebas Neue',sans-serif", letterSpacing: 1.3,
            textShadow: "0 0 14px " + c + "55" }}>{title}</span>
        </div>
        {sub && <div style={{ color: "#7a7296", fontSize: 12, marginLeft: 40 }}>{sub}</div>}
      </div>
    );
  };

  return (
    <div className="bf-page" style={{ position: "relative", paddingBottom: 100, overflowX: "hidden", minHeight: "100%" }}>

      {/* ── Cosmic backdrop ── */}
      <div aria-hidden="true" style={{
        position: "fixed", inset: 0, zIndex: 0, pointerEvents: "none",
        background: "radial-gradient(1200px 600px at 50% -10%, rgba(176,38,255,0.16), transparent 60%), radial-gradient(900px 500px at 100% 25%, rgba(0,213,255,0.09), transparent 60%), radial-gradient(900px 600px at 0% 100%, rgba(125,60,255,0.12), transparent 60%), #050505",
      }}>
        <div style={{
          position: "absolute", inset: "-20%",
          backgroundImage: "radial-gradient(1.5px 1.5px at 40px 60px, rgba(196,181,253,0.5), transparent), radial-gradient(1px 1px at 130px 180px, rgba(0,213,255,0.32), transparent), radial-gradient(1.5px 1.5px at 260px 90px, rgba(255,255,255,0.3), transparent), radial-gradient(1px 1px at 320px 260px, rgba(176,38,255,0.4), transparent)",
          backgroundSize: "340px 340px, 200px 200px, 340px 340px, 200px 200px",
          animation: "bf-stars 75s linear infinite", opacity: 0.5,
        }} />
        <div style={{ position: "absolute", top: "-25%", left: "-12%", width: "120%", height: "70%", background: "radial-gradient(closest-side, rgba(176,38,255,0.26), transparent 70%)", filter: "blur(48px)", animation: "bf-aurora 22s ease-in-out infinite" }} />
        <div style={{ position: "absolute", bottom: "-20%", right: "-15%", width: "95%", height: "65%", background: "radial-gradient(closest-side, rgba(0,213,255,0.14), transparent 70%)", filter: "blur(56px)", animation: "bf-aurora 28s ease-in-out infinite reverse" }} />
      </div>

      <div style={{ position: "relative", zIndex: 1 }}>

      {/* Top bar — avatar + post + messages */}
      <div style={{ padding: "14px 16px 8px", display: "flex", alignItems: "center", justifyContent: "space-between" }}>
        {/* Avatar — taps to own public profile */}
        <button onClick={onViewOwnProfile} style={{ background: "none", border: "none", padding: 0, cursor: "pointer", display: "flex", alignItems: "center", gap: 10 }}>
          <div style={{ position: "relative" }}>
            <div style={{ width: 38, height: 38, borderRadius: "50%", flexShrink: 0,
              background: user ? "linear-gradient(135deg,#6B21A8,#C026D3)" : "#222",
              border: "2px solid #333", overflow: "hidden",
              display: "flex", alignItems: "center", justifyContent: "center",
              color: "white", fontWeight: 800, fontSize: 15 }}>
              {user?.avatarUrl
                ? <img src={user.avatarUrl} alt="me" style={{ width: "100%", height: "100%", objectFit: "cover" }} />
                : user ? (user.username || user.name || "?")[0].toUpperCase()
                : <AppIcon id="profile" size={18} />}
            </div>
            {user && <div style={{ position: "absolute", bottom: 1, right: 1, width: 9, height: 9, borderRadius: "50%", background: "#22C55E", border: "2px solid #0a0a0a" }} />}
          </div>
          {user && <span style={{ color: "#aaa", fontSize: 13, fontWeight: 600 }}>{user.username ? "@" + user.username : user.name}</span>}
          {!user && <span style={{ color: "#444", fontSize: 13 }}>Sign in</span>}
        </button>

        <div style={{ display: "flex", alignItems: "center", gap: 4 }}>
          {/* BETA chip — always visible in home top bar, signals to users
              that the app is in early-access state. Matches the splash
              and About-page chip visually. Tappable: opens the About
              page so curious users learn more about the project. */}
          <button
            onClick={function() {
              try { window.dispatchEvent(new CustomEvent("bf:openAbout")); } catch (_) {}
            }}
            style={{
              background: "linear-gradient(135deg,rgba(192,38,211,0.22),rgba(124,58,237,0.16))",
              border: "1px solid rgba(192,38,211,0.45)",
              color: "#E9D5FF",
              fontFamily: "'Bebas Neue',sans-serif",
              fontSize: 10,
              fontWeight: 700,
              letterSpacing: 1.6,
              padding: "3px 8px",
              borderRadius: 7,
              cursor: "pointer",
              marginRight: 4,
            }}
            aria-label="Beta — tap for info"
          >
            BETA
          </button>
          {/* Bell notifications icon */}
          {user && (
            <button onClick={onOpenNotifications} style={{ background: "none", border: "none", padding: 8, cursor: "pointer", position: "relative" }}>
              <svg width="22" height="22" viewBox="0 0 24 24" fill="none" stroke="#aaa" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                <path d="M18 8A6 6 0 0 0 6 8c0 7-3 9-3 9h18s-3-2-3-9"/><path d="M13.73 21a2 2 0 0 1-3.46 0"/>
              </svg>
              {unreadNotifications > 0 && (
                <div style={{ position: "absolute", top: 4, right: 4, width: 16, height: 16, borderRadius: "50%",
                  background: "#EF4444", display: "flex", alignItems: "center", justifyContent: "center",
                  color: "white", fontSize: 9, fontWeight: 800 }}>
                  {unreadNotifications > 9 ? "9+" : unreadNotifications}
                </div>
              )}
            </button>
          )}
          {/* User search icon — opens a quick-find overlay to look up
              other users by username. Available to signed-in users only
              (the API requires auth for /api/auth/search). */}
          {user && (
            <button onClick={onOpenUserSearch} aria-label="Search users"
              style={{ background: "none", border: "none", padding: 8, cursor: "pointer", position: "relative" }}>
              <svg width="22" height="22" viewBox="0 0 24 24" fill="none"
                stroke="#aaa" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                <circle cx="11" cy="11" r="8"/><line x1="21" y1="21" x2="16.65" y2="16.65"/>
              </svg>
            </button>
          )}
          {/* Messages icon */}
          <button onClick={onOpenMessages} style={{ background: "none", border: "none", padding: 8, cursor: "pointer", position: "relative" }}>
            <svg width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="#aaa" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
              <path d="M21 15a2 2 0 0 1-2 2H7l-4 4V5a2 2 0 0 1 2-2h14a2 2 0 0 1 2 2z"/>
            </svg>
            {unreadMessages > 0 && (
              <div style={{ position: "absolute", top: 4, right: 4, width: 16, height: 16, borderRadius: "50%",
                background: "#C026D3", display: "flex", alignItems: "center", justifyContent: "center",
                color: "white", fontSize: 9, fontWeight: 800 }}>
                {unreadMessages > 9 ? "9+" : unreadMessages}
              </div>
            )}
          </button>
          {/* Profile / settings icon — opens the Profile tab (settings, logout etc) */}
          <button onClick={onGoProfile} style={{ background: "none", border: "none", padding: 8, cursor: "pointer", position: "relative" }}>
            <svg width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="#aaa" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
              <path d="M20 21v-2a4 4 0 00-4-4H8a4 4 0 00-4 4v2"/>
              <circle cx="12" cy="7" r="4"/>
            </svg>
          </button>
        </div>
      </div>

      {/* Logo */}
      <div style={{ padding: "0 16px 0", marginBottom: -10 }}>
        <img
          src="https://i.ibb.co/vC5WLsHc/EBD28484-A830-47-EC-A53-C-574-AB47-CBB67.png"
          alt="BeatFinder"
          style={{ width: "100%", maxWidth: 440, display: "block", margin: "0 auto" }}
        />
      </div>

      {/* Hero Banner */}
      <div style={{ padding: "0 16px 0" }}>
        <div
          onTouchStart={handleHeroTouchStart}
          onTouchEnd={handleHeroTouchEnd}
          style={{
          background: "#0b0718",
          borderRadius: 22, padding: "22px 18px 18px",
          marginBottom: 20, position: "relative", overflow: "hidden",
          boxShadow: "0 18px 48px rgba(0,0,0,0.6), 0 0 30px rgba(176,38,255,0.28), inset 0 1px 0 rgba(255,255,255,0.08)",
          border: "1px solid rgba(176,38,255,0.45)",
          minHeight: 210,
          userSelect: "none",
        }}>
          {/* Layer 0 — depth gradient base */}
          <div aria-hidden="true" style={{ position: "absolute", inset: 0, zIndex: 0,
            background: "radial-gradient(120% 100% at 100% 50%, #1a0f33 0%, #0d0820 55%, #0b0718 100%)" }} />

          {/* Layer 1 — atmospheric glows behind the screenshot (purple/blue/pink) */}
          {heroImg && (
            <div aria-hidden="true" style={{ position: "absolute", inset: 0, zIndex: 1, pointerEvents: "none", overflow: "hidden" }}>
              <div style={{ position: "absolute", top: "-30%", right: "2%", width: 240, height: 240, borderRadius: "50%", background: "radial-gradient(closest-side, rgba(139,92,246,0.55), transparent 70%)", filter: "blur(90px)" }} />
              <div style={{ position: "absolute", bottom: "-35%", right: "18%", width: 220, height: 220, borderRadius: "50%", background: "radial-gradient(closest-side, rgba(59,130,246,0.4), transparent 70%)", filter: "blur(100px)" }} />
              <div style={{ position: "absolute", top: "25%", right: "-8%", width: 200, height: 200, borderRadius: "50%", background: "radial-gradient(closest-side, rgba(236,72,153,0.34), transparent 70%)", filter: "blur(95px)" }} />
            </div>
          )}

          {/* Layer 2 — screenshot, feathered on ALL edges by a single radial
              mask (renders reliably in WebKit; no fragile mask compositing).
              Scaled slightly larger than its box so edges dissolve into glow. */}
          {heroImg ? (
            <div key={"himg" + heroIndex + (slide.imgs ? studioImgIdx : "")} aria-hidden="true" style={{
              position: "absolute", top: 0, bottom: 0, right: 0, width: "62%", zIndex: 2, pointerEvents: "none",
              backgroundImage: "url(" + heroImg + ")",
              backgroundSize: "cover",
              backgroundPosition: "right top",
              backgroundRepeat: "no-repeat",
              WebkitMaskImage: "linear-gradient(90deg, transparent 0%, rgba(0,0,0,0.5) 26%, #000 55%)",
              maskImage: "linear-gradient(90deg, transparent 0%, rgba(0,0,0,0.5) 26%, #000 55%)",
              animation: "bf-fade 0.55s ease",
            }} />
          ) : (
            <div aria-hidden="true" style={{ position: "absolute", inset: 0, background: slide.grad, zIndex: 2, opacity: 0.9 }} />
          )}

          {/* Layer 3 — left scrim, confined to the text zone so it doesn't wash the image. */}
          <div aria-hidden="true" style={{ position: "absolute", inset: 0, zIndex: 3, pointerEvents: "none",
            background: "linear-gradient(90deg, rgba(11,7,24,0.92) 0%, rgba(11,7,24,0.55) 30%, rgba(11,7,24,0) 50%)" }} />
          {/* Layer 4 — light leak: a soft diagonal purple-blue sweep. */}
          <div aria-hidden="true" style={{ position: "absolute", inset: 0, zIndex: 3, pointerEvents: "none", mixBlendMode: "screen",
            background: "linear-gradient(115deg, transparent 40%, rgba(139,92,246,0.14) 58%, rgba(59,130,246,0.10) 68%, transparent 82%)" }} />
          {/* Layer 5 — faint accent tint for slide identity. */}
          <div aria-hidden="true" style={{ position: "absolute", inset: 0, zIndex: 3, pointerEvents: "none", background: slide.grad, opacity: 0.08, mixBlendMode: "screen" }} />
          {/* Layer 6 — top glass sheen (used correctly: a highlight, not a blur slab). */}
          <div aria-hidden="true" style={{ position: "absolute", top: 0, left: 0, right: 0, height: "42%", zIndex: 3, pointerEvents: "none",
            background: "linear-gradient(180deg, rgba(255,255,255,0.06), transparent)" }} />
          {/* glowing animated top edge */}
          <div aria-hidden="true" style={{ position: "absolute", top: 0, left: 0, right: 0, height: 2, zIndex: 4, background: "linear-gradient(90deg, transparent, #B026FF, #00D5FF, #7D3CFF, transparent)", animation: "bf-edge 3.2s ease-in-out infinite" }} />

          <div key={"htxt" + heroIndex} style={{ position: "relative", zIndex: 5, maxWidth: heroImg ? "56%" : "100%", animation: "bf-fade 0.55s ease" }}>
          <div style={{ color: "rgba(255,255,255,0.5)", fontSize: 10, fontWeight: 800,
            letterSpacing: 2.5, marginBottom: 8 }}>BEATFINDER</div>
          <div style={{ color: "white", fontSize: 24, fontWeight: 900,
            lineHeight: 1.1, marginBottom: 6, fontFamily: "'Bebas Neue',sans-serif",
            letterSpacing: 1 }}>{slide.title}</div>
          <div style={{ color: "rgba(255,255,255,0.6)", fontSize: 13,
            marginBottom: 18, lineHeight: 1.5 }}>{slide.sub}</div>
          <div style={{ display:"flex", alignItems:"center", gap:8, flexWrap: "wrap" }}>
            {!slide.noCta && <button onClick={
              slide.studioSlide ? onGoStudio :
              slide.trendingSlide ? onGoTrending :
              slide.artistsSlide ? onGoArtists :
              slide.lyricsSlide ? onWriteLyrics :
              slide.proSlide ? onGoMembers :
              slide.producerSlide ? onShowProducerPrompt :
              onGoProfile
            } style={{
              background: slide.btnColor || "rgba(255,255,255,0.15)",
              backdropFilter: "blur(10px)",
              border: "1px solid " + (slide.btnBorder || "rgba(255,255,255,0.25)"),
              borderRadius: 22,
              color: "white", fontWeight: 800, fontSize: 13,
              padding: "9px 20px", cursor: "pointer",
            }}>{slide.cta} →</button>}
            {slide.badge && (
              <span style={{ background:"rgba(124,58,237,0.4)", border:"1px solid rgba(124,58,237,0.7)", borderRadius:12, color:"#a78bfa", fontSize:10, fontWeight:800, padding:"4px 8px", letterSpacing:1 }}>{slide.badge}</span>
            )}
          </div>
          </div>

          {/* Dot indicators */}
          <div style={{ position: "absolute", bottom: 14, right: 16, zIndex: 5,
            display: "flex", gap: 5, alignItems: "center" }}>
            {HERO_SLIDES.map((_, i) => (
              <div key={i} onClick={() => setHeroIndex(i)} style={{
                width: i === heroIndex ? 18 : 5, height: 5, borderRadius: 3,
                background: i === heroIndex ? "rgba(255,255,255,0.9)" : "rgba(255,255,255,0.25)",
                cursor: "pointer", transition: "all 0.35s cubic-bezier(0.22,1,0.36,1)",
              }} />
            ))}
          </div>
        </div>
      </div>

      {/* ── Quick Actions command bar ── */}
      <div style={{ padding: "0 16px", marginBottom: 22 }}>
        <div style={{ display: "flex", gap: 9 }}>
          {[
            (user && user.isArtistPro && !user.isPro)
              ? { label: "Upload Track", icon: "upload", glow: "176,38,255", on: function(){ onGoSection && onGoSection("postmusic"); } }
              : { label: "Upload Beat",  icon: "upload", glow: "176,38,255", on: function(){ if (user && user.isPro) { onGoUpload && onGoUpload(); } else { onShowProducerPrompt && onShowProducerPrompt(); } } },
            { label: "Write Lyrics",     icon: "edit",    glow: "125,60,255", on: function(){ onWriteLyrics && onWriteLyrics(); } },
            { label: (user && user.isPro) ? "Record Vocals & Make Beats" : "Record Vocals", icon: "mic", glow: "0,213,255", on: function(){ onGoStudio && onGoStudio(); } },
            { label: "Browse Type Beats", icon: "search", glow: "176,38,255", on: function(){ onGoArtists && onGoArtists(); } },
          ].map(function(a){
            return (
              <button key={a.label} onClick={a.on} className="bf-press" style={{
                flex: 1, display: "flex", flexDirection: "column", alignItems: "center", justifyContent: "center", gap: 7,
                padding: "13px 4px", borderRadius: 16, cursor: "pointer",
                background: "linear-gradient(180deg, rgba(20,15,32,0.9), rgba(10,8,20,0.92))",
                border: "1px solid rgba(" + a.glow + ",0.35)",
                color: "#e9deff",
                boxShadow: "0 6px 18px rgba(0,0,0,0.5), 0 0 14px rgba(" + a.glow + ",0.14), inset 0 1px 0 rgba(255,255,255,0.05)",
                backdropFilter: "blur(6px)", WebkitBackdropFilter: "blur(6px)",
              }}>
                <span style={{
                  display: "inline-flex", alignItems: "center", justifyContent: "center",
                  width: 38, height: 38, borderRadius: 12,
                  background: "linear-gradient(135deg, rgba(" + a.glow + ",0.28), rgba(" + a.glow + ",0.1))",
                  border: "1px solid rgba(" + a.glow + ",0.5)",
                  boxShadow: "0 0 14px rgba(" + a.glow + ",0.4)",
                  color: "rgb(" + a.glow + ")",
                }}><AppIcon id={a.icon} size={18} /></span>
                <span style={{ fontSize: 10, fontWeight: 800, letterSpacing: 0.2, textAlign: "center", lineHeight: 1.15 }}>{a.label}</span>
              </button>
            );
          })}
        </div>
      </div>

      {/* Pro upsell strip for free users */}
      {(!user || (!user.isPro && !user.isArtistPro)) && (
        <div style={{ padding: "0 16px", marginBottom: 22 }}>
          <div style={{
            background: "linear-gradient(135deg,#0d0020,#1a0040)",
            border: "1px solid rgba(192,38,211,0.25)",
            borderRadius: 16, padding: "13px 16px",
            display: "flex", alignItems: "center", gap: 14,
          }}>
            <div style={{ fontSize: 28, color: "#C026D3" }}><AppIcon id="lock" size={20} color="#C026D3"/></div>
            <div style={{ flex: 1 }}>
              <div style={{ color: "white", fontWeight: 800, fontSize: 14, marginBottom: 2 }}>
                Unlock Pro
              </div>
              <div style={{ color: "#666", fontSize: 12 }}>Lyrics, MP3s, exclusive beats</div>
            </div>
            <button onClick={onGoProfile} className="bf-btn" style={{
              background: "linear-gradient(135deg,#C026D3,#7C3AED)",
              border: "none", borderRadius: 20, color: "white",
              fontWeight: 800, fontSize: 12, padding: "8px 16px",
              cursor: "pointer", flexShrink: 0,
            }}>From £4.99</button>
          </div>
        </div>
      )}

      {/* Featured Beats Carousel */}
      <div style={{ marginBottom: 28 }}>
        <SectionHead emoji="star" title="FEATURED BEATS" sub="Picked for you today" color="#B026FF" />
        <FeaturedCarousel savedIds={savedIds} onSave={onSave} onPlay={onPlay} />
      </div>

      {/* ── Trending Right Now (real /api/youtube/trending data) ── */}
      <div style={{ marginBottom: 28 }}>
        <SectionHead emoji="trending" title="TRENDING RIGHT NOW" sub="Most-viewed beats on YouTube today" color="#00D5FF" />
        {homeTrendLoading ? (
          <div style={{ paddingLeft: 16 }}><BFLoader type="bars" text="Loading trending..." /></div>
        ) : (
          <div className="bf-carousel" style={{ overflowX: "auto", scrollbarWidth: "none", WebkitOverflowScrolling: "touch", paddingLeft: 16, paddingBottom: 4 }}>
            <div style={{ display: "flex", gap: 11, paddingRight: 16 }}>
              {homeTrending.length === 0
                ? <div style={{ color: "#444", fontSize: 13, padding: "16px 0" }}>No trending beats right now.</div>
                : homeTrending.map(function(beat){
                    return (
                      <div key={beat.videoId} onClick={function(){ onPlay(beat); }} className="bf-press bf-breathe" style={{
                        flexShrink: 0, width: 134, cursor: "pointer", position: "relative",
                        background: "linear-gradient(180deg, rgba(20,15,32,0.92), rgba(10,8,20,0.95))",
                        borderRadius: 18, border: "1px solid rgba(0,213,255,0.3)", overflow: "hidden",
                        boxShadow: "0 8px 24px rgba(0,0,0,0.55), 0 0 18px rgba(0,213,255,0.14), inset 0 1px 0 rgba(255,255,255,0.05)",
                        backdropFilter: "blur(6px)", WebkitBackdropFilter: "blur(6px)",
                      }}>
                        <span aria-hidden="true" className="bf-shimmer" style={{ zIndex: 3 }} />
                        <div style={{ position: "relative", height: 88, overflow: "hidden" }}>
                          <img src={beat.thumbnail || ("https://img.youtube.com/vi/" + beat.videoId + "/hqdefault.jpg")} alt="" style={{ width: "100%", height: "100%", objectFit: "cover", display: "block" }} />
                          <div style={{ position: "absolute", inset: 0, background: "linear-gradient(to top,rgba(5,5,5,0.7),transparent 55%)" }} />
                          <div style={{ position: "absolute", inset: 0, display: "flex", alignItems: "center", justifyContent: "center", pointerEvents: "none" }}>
                            <div style={{ width: 34, height: 34, borderRadius: "50%", background: "rgba(5,5,5,0.55)", border: "1.5px solid rgba(0,213,255,0.7)", display: "flex", alignItems: "center", justifyContent: "center", color: "#00D5FF", boxShadow: "0 0 12px rgba(0,213,255,0.5)" }}>
                              <AppIcon id="play" size={15} />
                            </div>
                          </div>
                          {beat.viewsLabel && (
                            <div style={{ position: "absolute", bottom: 5, left: 6, background: "rgba(5,5,5,0.65)", color: "#cfe9ff", fontSize: 8, fontWeight: 800, padding: "2px 6px", borderRadius: 8 }}>{beat.viewsLabel}</div>
                          )}
                        </div>
                        <div style={{ padding: "8px 10px 10px" }}>
                          <div style={{ color: "#f3eeff", fontSize: 11, fontWeight: 700, lineHeight: 1.35, overflow: "hidden", display: "-webkit-box", WebkitLineClamp: 2, WebkitBoxOrient: "vertical" }}>{beat.title}</div>
                        </div>
                      </div>
                    );
                  })}
            </div>
          </div>
        )}
      </div>

      {/* Genre chips */}
      <div style={{ marginBottom: 28 }}>
        <SectionHead emoji="target" title="BROWSE BY GENRE" />
        <div className="bf-carousel" style={{ overflowX: "auto", scrollbarWidth: "none",
          WebkitOverflowScrolling: "touch", paddingLeft: 16 }}>
          <div style={{ display: "flex", gap: 10, paddingRight: 16 }}>
            {GENRES.map(g => (
              <div key={g.label} onClick={() => onGenreSearch(g.q)} style={{
                flexShrink: 0, display: "flex", alignItems: "center", gap: 7,
                background: g.color + "18",
                border: "1.5px solid " + g.color + "44",
                borderRadius: 50, padding: "9px 18px", cursor: "pointer",
              }}>
                <Icon id={g.emoji} size={15} color={g.color} />
                <span style={{ color: "white", fontWeight: 700, fontSize: 13,
                  whiteSpace: "nowrap" }}>{g.label}</span>
              </div>
            ))}
          </div>
        </div>
      </div>



      {/* Pro personalised workspace */}
      {user?.isArtistPro && (
        <WorkspaceSection
          user={user}
          savedLyrics={savedLyrics || []}
          onEditLyric={onEditLyric}
          onPlay={onPlay}
          savedIds={savedIds}
          onSave={onSave}
          onGenreSearch={onGenreSearch}
          onGoMembers={onGoMembers}
        />
      )}
      </div>
    </div>
  );
}

// =============================================================================
// FOLLOWING FEED — new uploads + posts from people the user follows
// Hits /api/auth/feed which returns a merged stream of beat uploads AND
// posts (status, music, video). Hidden when the user follows nobody.
// Carousel for beat uploads, Twitter-style cards for posts, sorted by date.
// =============================================================================
// =============================================================================
// FEED SCREEN — own tab — new uploads + posts from people the user follows
// Hits /api/auth/feed which returns a merged stream of beat uploads AND
// posts (status, music, video). Has proper loading / error / empty states
// so the user always sees feedback when the tab is opened.
// =============================================================================
function FollowingFeed({ user, onPlay, savedIds, onSave, onViewProfile, onSearchPeople, onOpenPost, refreshKey, highlightPostId, onHighlightConsumed }) {
  var [items, setItems]     = React.useState([]);
  var [loading, setLoading] = React.useState(true);
  var [error, setError]     = React.useState(null);
  var [lightbox, setLightbox] = React.useState(null); // { images, index }
  var [refreshing, setRefreshing] = React.useState(false);
  // Pull-to-refresh state. `pullDistance` is how far the user has dragged
  // down (clamped to 0..PULL_MAX). When they release past PULL_TRIGGER we
  // fire load(true). The indicator rotates proportionally to pullDistance
  // so the user sees the gesture acknowledged in realtime.
  var PULL_TRIGGER = 70;   // px before refresh fires on release
  var PULL_MAX     = 110;  // visual cap so over-pulling doesn't push everything down forever
  var [pullDistance, setPullDistance] = React.useState(0);
  var pullStartY     = React.useRef(null);
  var pullCancelled  = React.useRef(false);   // becomes true if user scrolled before pulling down
  // Mirror of pullDistance in a ref so the touchend handler can read
  // the latest value without forcing the effect to re-subscribe on every
  // touchmove (which would tear down and re-add listeners constantly).
  var pullDistanceRef = React.useRef(0);
  // Ref to the Feed's outer div. We walk UP from here at mount time to
  // find the actual scrolling ancestor (the tab container has
  // overflowY:auto + a fixed height). We attach the pull-to-refresh
  // touch listeners to THAT element, since touch events bubble up from
  // it and its scrollTop is what we need to read.
  var feedRootRef = React.useRef(null);
  var scrollParentRef = React.useRef(null);
  // Tick state — bumps every 10s to force re-render of the timeAgo labels
  // so they update live ("5s ago" → "15s ago" → "25s ago"…) without needing
  // a server fetch.
  var [, setTick] = React.useState(0);
  React.useEffect(function() {
    var id = setInterval(function() { setTick(function(n) { return n + 1; }); }, 10000);
    return function() { clearInterval(id); };
  }, []);

  // Per-post interaction state — keyed by post id
  var [postLikes,    setPostLikes]    = React.useState({});  // { id: boolean }
  var [postReposts,  setPostReposts]  = React.useState({});  // { id: boolean }
  var [postComments, setPostComments] = React.useState({});  // { id: comment[] }
  var [openCommentsFor, setOpenCommentsFor] = React.useState(null); // post.id | null
  // Which post's ••• menu is currently open. Null = none. We use the
  // feed-item id (the wrapper id for reposts) so each card menu is
  // independently togglable.
  var [openMenuFor, setOpenMenuFor] = React.useState(null);

  // Delete a post the user owns, OR remove the user's own repost. The
  // backend's DELETE /api/posts/{id} verifies the requester's username
  // matches the doc's username, so either case works through the same
  // endpoint. We pass the WRAPPER id (the item.id from the feed) so
  // reposts delete the wrapper, not the underlying original.
  function deleteOwnPost(wrapperId) {
    if (!wrapperId) return;
    if (!window.confirm("Delete this post? This cannot be undone.")) return;
    // Optimistic — remove from view immediately, restore on error
    var removed = null;
    setItems(function(prev) {
      var n = prev.filter(function(it) {
        if (it.id === wrapperId) { removed = it; return false; }
        return true;
      });
      return n;
    });
    apiFetch("/api/posts/" + wrapperId, { method: "DELETE" })
      .catch(function() {
        // Restore on failure
        if (removed) setItems(function(prev) { return [removed].concat(prev); });
        alert("Failed to delete.");
      });
  }


  // After we load the feed, hydrate liked/reposted state for the current user
  // for every post we got back. We do this in parallel so the UI hot-states
  // appear quickly. Failures are non-fatal — buttons just stay grey.
  function hydrateInteractionState(list) {
    if (!user || !list) return;
    list.forEach(function(it) {
      if (it.kind !== "post" || !it.id) return;
      apiFetch("/api/posts/" + it.id + "/liked")
        .then(function(r) { setPostLikes(function(p) { var n = Object.assign({}, p); n[it.id] = !!(r && r.liked); return n; }); })
        .catch(function() {});
      apiFetch("/api/posts/" + it.id + "/reposted")
        .then(function(r) { setPostReposts(function(p) { var n = Object.assign({}, p); n[it.id] = !!(r && r.reposted); return n; }); })
        .catch(function() {});
    });
  }

  function togglePostLike(id) {
    if (!user) return;
    var wasLiked = !!postLikes[id];
    // optimistic
    setPostLikes(function(p) { var n = Object.assign({}, p); n[id] = !wasLiked; return n; });
    setItems(function(prev) { return prev.map(function(it) {
      return it.id === id ? Object.assign({}, it, { likeCount: Math.max(0, (it.likeCount || 0) + (!wasLiked ? 1 : -1)) }) : it;
    }); });
    apiFetch("/api/posts/" + id + "/like", { method: "POST" })
      .then(function(r) { setPostLikes(function(p) { var n = Object.assign({}, p); n[id] = !!(r && r.liked); return n; }); })
      .catch(function() {
        // revert
        setPostLikes(function(p) { var n = Object.assign({}, p); n[id] = wasLiked; return n; });
        setItems(function(prev) { return prev.map(function(it) {
          return it.id === id ? Object.assign({}, it, { likeCount: Math.max(0, (it.likeCount || 0) + (wasLiked ? 1 : -1)) }) : it;
        }); });
      });
  }

  function togglePostRepost(id) {
    if (!user) return;
    var wasReposted = !!postReposts[id];
    // optimistic
    setPostReposts(function(p) { var n = Object.assign({}, p); n[id] = !wasReposted; return n; });
    setItems(function(prev) { return prev.map(function(it) {
      return it.id === id ? Object.assign({}, it, { repostCount: Math.max(0, (it.repostCount || 0) + (!wasReposted ? 1 : -1)) }) : it;
    }); });
    var method = wasReposted ? "DELETE" : "POST";
    apiFetch("/api/posts/" + id + "/repost", { method: method })
      .then(function(r) { setPostReposts(function(p) { var n = Object.assign({}, p); n[id] = !!(r && r.reposted); return n; }); })
      .catch(function() {
        setPostReposts(function(p) { var n = Object.assign({}, p); n[id] = wasReposted; return n; });
        setItems(function(prev) { return prev.map(function(it) {
          return it.id === id ? Object.assign({}, it, { repostCount: Math.max(0, (it.repostCount || 0) + (wasReposted ? 1 : -1)) }) : it;
        }); });
      });
  }

  function loadCommentsFor(id) {
    apiFetch("/api/posts/" + id + "/comments")
      .then(function(data) { setPostComments(function(p) { var n = Object.assign({}, p); n[id] = data || []; return n; }); })
      .catch(function() {});
  }

  function submitPostComment(postId, text, parentId, onSuccess, onError) {
    apiFetch("/api/posts/" + postId + "/comments", {
      method: "POST", body: JSON.stringify({ text: text, parentId: parentId || null })
    }).then(function(c) {
      setPostComments(function(p) { var n = Object.assign({}, p); n[postId] = [...(n[postId] || []), c]; return n; });
      setItems(function(prev) { return prev.map(function(it) {
        return it.id === postId ? Object.assign({}, it, { commentCount: (it.commentCount || 0) + 1 }) : it;
      }); });
      if (onSuccess) onSuccess();
    }).catch(function() { if (onError) onError(); });
  }

  function deletePostComment(postId, commentId) {
    apiFetch("/api/posts/" + postId + "/comments/" + commentId, { method: "DELETE" })
      .then(function() {
        setPostComments(function(p) { var n = Object.assign({}, p); n[postId] = (n[postId] || []).filter(function(c) { return c.id !== commentId; }); return n; });
        setItems(function(prev) { return prev.map(function(it) {
          return it.id === postId ? Object.assign({}, it, { commentCount: Math.max(0, (it.commentCount || 0) - 1) }) : it;
        }); });
      }).catch(function() {});
  }

  function load(showSpinner) {
    if (!user) { setItems([]); setLoading(false); return; }
    if (showSpinner) setRefreshing(true); else setLoading(true);
    apiFetch("/api/auth/feed?limit=30")
      .then(function(list) {
        setItems(list || []);
        setLoading(false); setRefreshing(false); setError(null);
        hydrateInteractionState(list);
      })
      .catch(function(err) {
        setError(err && err.message);
        setLoading(false); setRefreshing(false);
      });
  }

  // Quiet background refresh — same fetch, but doesn't toggle the loading
  // spinner or reset error state. Used by the 30s polling timer and the
  // window-focus re-fetch so the feed stays live without flicker.
  function quietLoad() {
    if (!user) return;
    apiFetch("/api/auth/feed?limit=30", { silent: true })
      .then(function(list) {
        if (list) { setItems(list); hydrateInteractionState(list); }
      })
      .catch(function() { /* keep old items on transient failure */ });
  }

  React.useEffect(function() { load(false); }, [user && user.id]);

  // When a notification deep-links to a post, scroll to it and clear
  // the highlight after a few seconds. We retry briefly because the
  // post may not yet be in the DOM if the feed is still loading.
  React.useEffect(function() {
    if (!highlightPostId) return;
    var tries = 0;
    function tryScroll() {
      // Match either the wrapper id (data-postid) or the underlying
      // original id (data-original-postid), since notifications carry
      // the original's id.
      var el = document.querySelector('[data-postid="' + highlightPostId + '"]')
            || document.querySelector('[data-original-postid="' + highlightPostId + '"]');
      if (el && el.scrollIntoView) {
        try { el.scrollIntoView({ behavior: "smooth", block: "center" }); }
        catch(_) { try { el.scrollIntoView(); } catch(__) {} }
        return true;
      }
      return false;
    }
    function attempt() {
      tries++;
      if (tryScroll()) return;
      if (tries < 10) setTimeout(attempt, 200);  // wait up to ~2s
    }
    attempt();
    // Clear the highlight after the user has had a moment to see it.
    var clearAt = setTimeout(function() {
      if (onHighlightConsumed) onHighlightConsumed();
    }, 2800);
    return function() { clearTimeout(clearAt); };
  }, [highlightPostId, items.length]);

  // Pull-to-refresh — find the scrolling ancestor of the Feed (the tab
  // container has overflowY:auto + height:calc(100dvh-...), so it scrolls
  // independently from window). Attach touch listeners directly to that
  // element so we read the right scrollTop and stay scoped to the Feed
  // tab. Logic:
  //   touchstart: if container scrolled to top, capture starting Y;
  //               otherwise mark the gesture cancelled
  //   touchmove:  if user dragged down past 4px, update pullDistance,
  //               and preventDefault so iOS rubber-band doesn't fight us
  //   touchend:   if pullDistance > PULL_TRIGGER, fire refresh; reset
  React.useEffect(function() {
    // Helper that updates both the state (drives the UI) and the ref
    // (so touchend can read the latest value without the effect having
    // to re-subscribe whenever pullDistance changes).
    function setPull(v) { pullDistanceRef.current = v; setPullDistance(v); }
    // Find the closest scrolling ancestor by walking up from our root.
    function findScrollParent() {
      if (scrollParentRef.current) return scrollParentRef.current;
      var el = feedRootRef.current;
      if (!el) return null;
      var node = el.parentElement;
      while (node && node !== document.body) {
        try {
          var cs = window.getComputedStyle(node);
          var oy = cs && cs.overflowY;
          // The tab container is `overflow-y: auto`; the contract
          // viewer uses `scroll`. Either counts.
          if (oy === "auto" || oy === "scroll") {
            // Don't require scrollHeight > clientHeight here — when the
            // feed is empty or short, the container can have equal
            // dimensions but we still want to attach to it so the
            // gesture works.
            scrollParentRef.current = node;
            return node;
          }
        } catch(_) {}
        node = node.parentElement;
      }
      // Fallback to window/document if we couldn't find a div
      scrollParentRef.current = window;
      return window;
    }
    var target = findScrollParent();
    if (!target) return;
    function scrollTopOf() {
      if (target === window) {
        return window.pageYOffset || document.documentElement.scrollTop || 0;
      }
      return target.scrollTop || 0;
    }
    function atTop() { return scrollTopOf() <= 0; }
    function onTouchStart(e) {
      if (refreshing || loading) { pullStartY.current = null; return; }
      if (!atTop()) { pullStartY.current = null; pullCancelled.current = true; return; }
      pullCancelled.current = false;
      pullStartY.current = (e.touches && e.touches[0]) ? e.touches[0].clientY : null;
    }
    function onTouchMove(e) {
      if (pullCancelled.current || pullStartY.current == null) return;
      // If user scrolled the page during the gesture (e.g. content was
      // taller than viewport and they swiped up first), abort.
      if (!atTop()) { pullCancelled.current = true; setPull(0); return; }
      var y  = (e.touches && e.touches[0]) ? e.touches[0].clientY : pullStartY.current;
      var dy = y - pullStartY.current;
      if (dy <= 0) { setPull(0); return; }
      // Apply a damping factor so the indicator slows down past trigger
      // — feels like iOS, not a 1:1 stretch
      var damped = dy < PULL_TRIGGER ? dy : PULL_TRIGGER + (dy - PULL_TRIGGER) * 0.35;
      var clamped = Math.min(PULL_MAX, damped);
      setPull(clamped);
      // Need to call preventDefault to suppress iOS rubber-banding. This
      // ONLY works if the listener is non-passive — see addEventListener
      // options below.
      if (dy > 6 && e.cancelable) e.preventDefault();
    }
    function onTouchEnd() {
      if (pullCancelled.current || pullStartY.current == null) {
        pullStartY.current = null;
        setPull(0);
        return;
      }
      var d = pullDistanceRef.current;
      pullStartY.current = null;
      if (d >= PULL_TRIGGER) {
        // Hold the indicator at the trigger position while the request
        // is in flight, then it disappears via the load()-driven
        // setRefreshing(false).
        setPull(PULL_TRIGGER);
        load(true);
      } else {
        setPull(0);
      }
    }
    // touchmove must be non-passive so we can preventDefault on iOS
    target.addEventListener("touchstart", onTouchStart, { passive: true });
    target.addEventListener("touchmove",  onTouchMove,  { passive: false });
    target.addEventListener("touchend",   onTouchEnd,   { passive: true });
    target.addEventListener("touchcancel", onTouchEnd,  { passive: true });
    return function() {
      target.removeEventListener("touchstart", onTouchStart);
      target.removeEventListener("touchmove",  onTouchMove);
      target.removeEventListener("touchend",   onTouchEnd);
      target.removeEventListener("touchcancel", onTouchEnd);
    };
  }, [refreshing, loading]);

  // When a refresh fires the load() callback will eventually flip
  // refreshing → false. At that point we want to retract the indicator
  // back to zero (it was being held at PULL_TRIGGER during the request).
  React.useEffect(function() {
    if (!refreshing && pullDistance > 0) {
      // small delay so the user sees the spinner finish its rotation
      var t = setTimeout(function() {
        pullDistanceRef.current = 0;
        setPullDistance(0);
      }, 200);
      return function() { clearTimeout(t); };
    }
  }, [refreshing]);

  // Live refresh on demand — when the user posts (refreshKey bumps from
  // the App level), re-fetch immediately so their own post appears at
  // the top of their feed.
  React.useEffect(function() {
    if (refreshKey > 0) quietLoad();
  }, [refreshKey]);

  // Background polling — every 30s while the feed tab is mounted AND
  // the page is visible, pull the latest posts so users see new content
  // from people they follow without manually refreshing.
  React.useEffect(function() {
    if (!user) return;
    var timer = null;
    var tick = function() {
      // Only poll when the document is visible — saves bandwidth + battery
      if (!document.hidden) quietLoad();
    };
    timer = setInterval(tick, 30000);
    // Refresh whenever the user returns to the tab (e.g. switches back
    // from another app or another tab) — gives them an instant fresh feed.
    var onVisibility = function() {
      if (!document.hidden) quietLoad();
    };
    var onFocus = function() { quietLoad(); };
    document.addEventListener("visibilitychange", onVisibility);
    window.addEventListener("focus", onFocus);
    return function() {
      if (timer) clearInterval(timer);
      document.removeEventListener("visibilitychange", onVisibility);
      window.removeEventListener("focus", onFocus);
    };
  }, [user && user.id]);

  // Logged-out state — prompt to sign in
  if (!user) {
    return (
      <div style={{ padding: "60px 24px", textAlign: "center" }}>
        <div style={{ marginBottom: 16, color: "#A78BFA" }}>
          <svg width="56" height="56" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round"
            style={{ filter: "drop-shadow(0 0 12px rgba(124,58,237,0.5))" }}>
            <path d="M5 12.55a11 11 0 0114.08 0"/>
            <path d="M1.42 9a16 16 0 0121.16 0"/>
            <path d="M8.53 16.11a6 6 0 016.95 0"/>
            <path d="M12 20h.01"/>
          </svg>
        </div>
        <div style={{ color: "white", fontSize: 18, fontWeight: 800, marginBottom: 8 }}>
          Sign in to see your feed
        </div>
        <div style={{ color: "#888", fontSize: 13, lineHeight: 1.6, maxWidth: 280, margin: "0 auto" }}>
          Follow producers and artists to see their latest beats and posts here.
        </div>
      </div>
    );
  }

  // Loading state — spinner
  if (loading) {
    return (
      <div style={{ padding: "80px 24px", textAlign: "center", color: "#555" }}>
        <BFLoader type="bars" text="Loading your feed..." />
      </div>
    );
  }

  // Error state
  if (error) {
    return (
      <div style={{ padding: "60px 24px", textAlign: "center" }}>
        <div style={{ color: "#F87171", fontSize: 14, fontWeight: 700, marginBottom: 8 }}>
          Couldn't load your feed
        </div>
        <div style={{ color: "#888", fontSize: 12, marginBottom: 18 }}>{error}</div>
        <button onClick={function() { load(false); }}
          style={{
            padding: "10px 24px", borderRadius: 20,
            background: "linear-gradient(135deg,#C026D3,#7C3AED)",
            border: "none", color: "white", fontWeight: 800, fontSize: 13,
            cursor: "pointer",
          }}>
          Try Again
        </button>
      </div>
    );
  }

  // Filter once up here so the empty-state check (which now only considers
  // posts since beats no longer render in the feed) and the render block
  // can share the same derived array.
  var posts = (items || []).filter(function(i) { return i.kind === "post"; });

  // Empty state — user follows nobody who has posted, OR follows nobody at all
  if (posts.length === 0) {
    return (
      <div style={{ padding: "60px 24px", textAlign: "center" }}>
        <div style={{ marginBottom: 16, color: "#A78BFA" }}>
          <svg width="56" height="56" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round"
            style={{ filter: "drop-shadow(0 0 12px rgba(124,58,237,0.5))" }}>
            <path d="M5 12.55a11 11 0 0114.08 0"/>
            <path d="M1.42 9a16 16 0 0121.16 0"/>
            <path d="M8.53 16.11a6 6 0 016.95 0"/>
            <path d="M12 20h.01"/>
          </svg>
        </div>
        <div style={{ color: "white", fontSize: 18, fontWeight: 800, marginBottom: 8 }}>
          Your feed is empty
        </div>
        <div style={{ color: "#888", fontSize: 13, lineHeight: 1.6, maxWidth: 280, margin: "0 auto 20px" }}>
          Follow producers and artists you love. Their posts will appear here.
        </div>
        {onSearchPeople && (
          <button onClick={onSearchPeople}
            style={{
              padding: "11px 26px", borderRadius: 20,
              background: "linear-gradient(135deg,#C026D3,#7C3AED)",
              border: "none", color: "white", fontWeight: 800, fontSize: 13,
              cursor: "pointer",
              boxShadow: "0 4px 14px rgba(124,58,237,0.4)",
            }}>
            Find People to Follow
          </button>
        )}
      </div>
    );
  }

  function timeAgo(iso) {
    if (!iso) return "";
    var diff = Date.now() - new Date(iso).getTime();
    if (diff < 0) diff = 0;
    var secs = Math.floor(diff / 1000);
    if (secs < 60) return secs + "s ago";
    var mins = Math.floor(secs / 60);
    if (mins < 60) return mins + "m ago";
    var hrs = Math.floor(mins / 60);
    if (hrs < 24) return hrs + "h ago";
    var days = Math.floor(hrs / 24);
    if (days < 7) return days + "d ago";
    var weeks = Math.floor(days / 7);
    if (weeks < 4) return weeks + "w ago";
    return Math.floor(days / 30) + "mo ago";
  }

  function Avatar({ url, username }) {
    return (
      <div style={{
        width: 34, height: 34, borderRadius: "50%", overflow: "hidden", flexShrink: 0,
        background: url ? "#000" : "linear-gradient(135deg,#6B21A8,#C026D3)",
        display: "flex", alignItems: "center", justifyContent: "center",
        color: "white", fontSize: 14, fontWeight: 900,
        border: "1px solid rgba(124,58,237,0.4)",
      }}>
        {url
          ? <img src={url} alt={username} style={{ width: "100%", height: "100%", objectFit: "cover" }} />
          : (username || "?")[0].toUpperCase()}
      </div>
    );
  }

  return (
    <div ref={feedRootRef} style={{
      paddingTop: 16, paddingBottom: 80,
      position: "relative",
      // Push the whole feed down by pullDistance so users see content
      // being "pulled". When pullDistance returns to 0 the transform
      // animates back via the transition below.
      transform: "translateY(" + pullDistance + "px)",
      transition: pullStartY.current == null ? "transform 0.25s cubic-bezier(0.22, 1, 0.36, 1)" : "none",
    }}
      onClick={function() { if (openMenuFor) setOpenMenuFor(null); }}>
      {/* Pull-to-refresh indicator — sits above the content, becomes
          visible as the user pulls down. Rotates proportionally to pull
          progress; once refreshing is true the spinner spins on its own. */}
      <div style={{
        position: "absolute", top: -50, left: 0, right: 0,
        display: "flex", justifyContent: "center",
        pointerEvents: "none",
        opacity: Math.min(1, pullDistance / 40),
      }}>
        <div style={{
          width: 36, height: 36, borderRadius: "50%",
          background: "rgba(124,58,237,0.12)",
          border: "1px solid rgba(124,58,237,0.35)",
          display: "flex", alignItems: "center", justifyContent: "center",
          color: "#A78BFA",
          boxShadow: "0 4px 14px rgba(124,58,237,0.25)",
        }}>
          <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round"
            style={{
              // While refreshing, spin continuously. Before that, rotate
              // proportionally to how far the user has pulled — gives an
              // unmistakable "we're getting ready" feel.
              animation: refreshing ? "bf-spin 0.8s linear infinite" : "none",
              transform: refreshing ? undefined : "rotate(" + (pullDistance * 4) + "deg)",
              transition: refreshing ? "none" : "transform 0.05s linear",
            }}>
            <polyline points="23 4 23 10 17 10"/>
            <polyline points="1 20 1 14 7 14"/>
            <path d="M3.51 9a9 9 0 0114.85-3.36L23 10M1 14l4.64 4.36A9 9 0 0020.49 15"/>
          </svg>
        </div>
      </div>
      {/* Big screen header (dedicated tab) */}
      <div style={{ padding: "0 16px 14px" }}>
        <div style={{
          display: "flex", alignItems: "flex-end", justifyContent: "space-between",
          gap: 12, marginBottom: 6,
        }}>
          <div style={{ display: "flex", alignItems: "center", gap: 12, flex: 1, minWidth: 0 }}>
            <div style={{
              width: 38, height: 38, borderRadius: 12,
              background: "linear-gradient(135deg,#C026D3,#7C3AED,#3B82F6)",
              boxShadow: "0 0 16px rgba(124,58,237,0.5)",
              display: "flex", alignItems: "center", justifyContent: "center",
              flexShrink: 0,
            }}>
              <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="white" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                <path d="M5 12.55a11 11 0 0114.08 0"/>
                <path d="M1.42 9a16 16 0 0121.16 0"/>
                <path d="M8.53 16.11a6 6 0 016.95 0"/>
                <path d="M12 20h.01"/>
              </svg>
            </div>
            <div style={{ minWidth: 0 }}>
              <div style={{
                color: "white", fontSize: 26, fontWeight: 800,
                fontFamily: "'Bebas Neue',sans-serif", letterSpacing: 1, lineHeight: 1,
              }}>FEED</div>
              <div style={{ color: "#888", fontSize: 12, marginTop: 4 }}>
                Latest from people you follow
              </div>
            </div>
          </div>
          {/* + Compose Post button — Pro users only. Moved here from Home header. */}
          {user && (user.isPro || user.isArtistPro) && onOpenPost && (
            <button onClick={onOpenPost}
              style={{
                width: 36, height: 36, borderRadius: "50%",
                background: "linear-gradient(135deg,#C026D3,#7C3AED)",
                border: "none", color: "white", cursor: "pointer", flexShrink: 0,
                display: "flex", alignItems: "center", justifyContent: "center",
                boxShadow: "0 4px 12px rgba(124,58,237,0.45)",
              }}
              title="Create a post">
              <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round">
                <line x1="12" y1="5" x2="12" y2="19"/><line x1="5" y1="12" x2="19" y2="12"/>
              </svg>
            </button>
          )}
        </div>
      </div>

      {/* Posts feed (Twitter-style cards stacked vertically) */}
      {posts.length > 0 && (
        <div style={{ padding: "0 16px", display: "flex", flexDirection: "column", gap: 10 }}>
          {posts.map(function(item) {
            var _src = item.original_post || item;
            if (_src && _src.type === "video" && !_src.youtubeId && !_src.videoUrl) return null;
            // Reposts: the feed item is owned by the reposter but the
            // payload it carries (text/images/etc.) is the ORIGINAL post.
            // We show "reposted by @reposter" above the original author's
            // identity. like/comment/repost actions still target the
            // ORIGINAL post id (so a repost is a re-share, not a new post
            // with its own engagement). Action ids prefer original.id then
            // fall back to repost id (legacy data).
            var isRepost = !!(item.repost_of && item.original_post);
            var post = isRepost ? Object.assign({}, item.original_post, {
              // Keep counts from the merged item if the backend already merged them
              likeCount:    item.original_post.likeCount    || 0,
              commentCount: item.original_post.commentCount || 0,
              repostCount:  item.original_post.repostCount  || 0,
            }) : item;
            var actionId  = isRepost ? (item.original_post.id || item.repost_of) : item.id;
            var liked     = !!postLikes[actionId];
            var reposted  = !!postReposts[actionId];

            // Is this card the target of a notification deep-link? We
            // match on either the wrapper id OR the underlying original
            // id (for reposts), since notifications carry the original's
            // id but the user might encounter it via someone's repost.
            var isHighlighted = highlightPostId && (
              item.id === highlightPostId ||
              (isRepost && item.repost_of === highlightPostId)
            );

            return (
              <div key={item.id}
                data-postid={item.id}
                data-original-postid={isRepost ? (item.repost_of || "") : item.id}
                style={{
                background: "linear-gradient(165deg,#0f0a1f 0%,#0a0a14 60%,#080812 100%)",
                borderRadius: 14, overflow: "hidden",
                border: isHighlighted
                  ? "2px solid rgba(192,38,211,0.85)"
                  : "1px solid rgba(124,58,237,0.18)",
                boxShadow: isHighlighted
                  ? "0 4px 24px rgba(192,38,211,0.6), 0 0 32px rgba(192,38,211,0.35)"
                  : "0 4px 14px rgba(0,0,0,0.45)",
                transition: "border-color 0.4s, box-shadow 0.4s",
              }}>
                {/* "Reposted by …" chip — only shown for reposts */}
                {isRepost && (
                  <div style={{
                    padding: "8px 14px 0", display: "flex", alignItems: "center",
                    gap: 6, color: "#A78BFA", fontSize: 11, fontWeight: 700,
                  }}>
                    <svg width="13" height="13" viewBox="0 0 24 24" fill="none"
                      stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round">
                      <polyline points="17 1 21 5 17 9"/><path d="M3 11V9a4 4 0 014-4h14"/>
                      <polyline points="7 23 3 19 7 15"/><path d="M21 13v2a4 4 0 01-4 4H3"/>
                    </svg>
                    <span
                      onClick={function(e) {
                        e.stopPropagation();
                        if (onViewProfile && item.username) onViewProfile(item.username);
                      }}
                      style={{ cursor: onViewProfile && item.username ? "pointer" : "default" }}>
                      Reposted by @{item.username}
                    </span>
                    <UserBadges username={item.username} plan={item.plan} compact={true} size={11} />
                  </div>
                )}
                {/* Header: avatar + username + time */}
                <div style={{ padding: "12px 14px 8px", display: "flex", alignItems: "center", gap: 10 }}>
                  <span
                    onClick={function(e) {
                      e.stopPropagation();
                      if (onViewProfile && post.username) onViewProfile(post.username);
                    }}
                    style={{ display: "inline-flex", cursor: onViewProfile && post.username ? "pointer" : "default" }}>
                    <Avatar url={post.user_avatar} username={post.username} />
                  </span>
                  <div style={{ flex: 1, minWidth: 0 }}>
                    <div style={{ color: "white", fontWeight: 800, fontSize: 13,
                      display: "flex", alignItems: "center", gap: 5,
                      overflow: "hidden", whiteSpace: "nowrap" }}>
                      {/* Tap @username → open that user's public profile.
                          The handler stops propagation so the click
                          doesn't bubble to the card's other handlers. */}
                      <span
                        onClick={function(e) {
                          e.stopPropagation();
                          if (onViewProfile && post.username) onViewProfile(post.username);
                        }}
                        style={{
                          overflow: "hidden", textOverflow: "ellipsis", minWidth: 0,
                          cursor: onViewProfile && post.username ? "pointer" : "default",
                        }}>@{post.username}</span>
                      <UserBadges username={post.username} plan={post.plan} compact={true} />
                    </div>
                    <div style={{ color: "#666", fontSize: 10, marginTop: 1 }}>{timeAgo(post.created_at)}</div>
                  </div>
                  {/* ••• menu — only shown if the current user owns this feed
                      item. For reposts that's the reposter (item.username);
                      for normal posts it's the author (post.username). The
                      backend DELETE endpoint enforces the same check so this
                      is just to keep the UI clean. */}
                  {user && user.username && user.username === item.username && (
                    <div style={{ position: "relative", flexShrink: 0 }}
                      onClick={function(e) { e.stopPropagation(); }}>
                      <button
                        onClick={function() {
                          setOpenMenuFor(openMenuFor === item.id ? null : item.id);
                        }}
                        style={{
                          background: "none", border: "none", color: "#555",
                          cursor: "pointer", fontSize: 18, padding: "4px 8px",
                          lineHeight: 1, borderRadius: 6,
                        }}
                        aria-label="Post options">•••</button>
                      {openMenuFor === item.id && (
                        <div style={{
                          position: "absolute", right: 0, top: "100%", zIndex: 100,
                          background: "#1a1a1a", border: "1px solid #2a2a2a",
                          borderRadius: 10, minWidth: 140,
                          boxShadow: "0 4px 20px rgba(0,0,0,0.6)", overflow: "hidden",
                          marginTop: 4,
                        }}>
                          <button
                            onClick={function() {
                              setOpenMenuFor(null);
                              deleteOwnPost(item.id);
                            }}
                            style={{
                              width: "100%", background: "none", border: "none",
                              padding: "12px 16px", color: "#F87171",
                              fontSize: 13, fontWeight: 700, cursor: "pointer",
                              textAlign: "left", display: "flex",
                              alignItems: "center", gap: 8,
                            }}>
                            <svg width="14" height="14" viewBox="0 0 24 24" fill="none"
                              stroke="#F87171" strokeWidth="2" strokeLinecap="round">
                              <polyline points="3 6 5 6 21 6"/><path d="M19 6l-1 14H6L5 6"/>
                              <path d="M10 11v6M14 11v6"/><path d="M9 6V4h6v2"/>
                            </svg>
                            {isRepost ? "Remove Repost" : "Delete Post"}
                          </button>
                        </div>
                      )}
                    </div>
                  )}
                </div>

                {/* Text — URLs autolinked + underlined */}
                {post.text && (
                  <div style={{ padding: "0 14px 10px", color: "#ddd", fontSize: 14, lineHeight: 1.55,
                    whiteSpace: "pre-wrap", wordBreak: "break-word" }}>
                    {renderTextWithLinks(post.text)}
                  </div>
                )}

                {/* Link preview card — shared component, always shows
                    a visual (real image if backend has one, else a gradient
                    placeholder seeded by the hostname). */}
                <LinkPreviewCard post={post} />

                {/* Images */}
                {post.images && post.images.length > 0 && (
                  <div style={{
                    display: "grid", gap: 2,
                    gridTemplateColumns: post.images.length === 1 ? "1fr" : post.images.length === 2 ? "1fr 1fr" : "1fr 1fr 1fr",
                  }}>
                    {post.images.slice(0, 4).map(function(img, i) {
                      var src = typeof img === "string" ? img : (img && img.url) || "";
                      return (
                        <div key={i}
                          onClick={function() { setLightbox({ images: post.images, index: i }); }}
                          style={{ cursor: "pointer", background: "#000" }}>
                          <img src={src}
                            style={{ width: "100%", aspectRatio: post.images.length === 1 ? "16/9" : "1",
                              objectFit: "cover", display: "block" }} />
                        </div>
                      );
                    })}
                  </div>
                )}

                {/* Music embed */}
                {post.type === "music" && post.embedUrl && (
                  <div style={{ padding: "0 14px 10px" }}>
                    <iframe src={post.embedUrl}
                      style={{ width: "100%", height: 152, border: "none", borderRadius: 10 }}
                      allow="autoplay; clipboard-write; encrypted-media; fullscreen; picture-in-picture" />
                  </div>
                )}

                {/* Video */}
                {post.type === "video" && post.youtubeId && (
                  <div style={{ position: "relative", width: "100%", paddingTop: "56.25%", background: "#000" }}>
                    <iframe src={"https://www.youtube.com/embed/" + post.youtubeId} title={post.caption || "video"} frameBorder="0" allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture" allowFullScreen style={{ position: "absolute", top: 0, left: 0, width: "100%", height: "100%" }} />
                  </div>
                )}
                {post.type === "video" && !post.youtubeId && post.videoUrl && (
                  <video controls playsInline preload="metadata"
                    style={{ width: "100%", maxHeight: 400, background: "#000", display: "block" }}>
                    <source src={post.videoUrl} />
                  </video>
                )}

                {/* Action bar — like / comment / repost. Always shown so the
                    user can engage even when counts are zero. Disabled if not
                    signed in. */}
                <div style={{
                  padding: "8px 6px", borderTop: "1px solid rgba(255,255,255,0.04)",
                  display: "flex", alignItems: "center",
                }}>
                  {/* Like */}
                  <button
                    onClick={function() { if (user) togglePostLike(actionId); }}
                    disabled={!user}
                    style={{
                      flex: 1, background: "none", border: "none",
                      cursor: user ? "pointer" : "not-allowed",
                      padding: "8px 6px",
                      display: "flex", alignItems: "center", justifyContent: "center", gap: 6,
                      color: liked ? "#EF4444" : "#888", fontSize: 12, fontWeight: 700,
                    }}>
                    <svg width="17" height="17" viewBox="0 0 24 24"
                      fill={liked ? "#EF4444" : "none"} stroke="currentColor"
                      strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                      <path d="M20.84 4.61a5.5 5.5 0 00-7.78 0L12 5.67l-1.06-1.06a5.5 5.5 0 00-7.78 7.78l1.06 1.06L12 21.23l7.78-7.78 1.06-1.06a5.5 5.5 0 000-7.78z"/>
                    </svg>
                    {post.likeCount > 0 && <span>{post.likeCount}</span>}
                  </button>
                  {/* Comment */}
                  <button
                    onClick={function() {
                      loadCommentsFor(actionId);
                      setOpenCommentsFor(actionId);
                    }}
                    style={{
                      flex: 1, background: "none", border: "none", cursor: "pointer",
                      padding: "8px 6px",
                      display: "flex", alignItems: "center", justifyContent: "center", gap: 6,
                      color: "#888", fontSize: 12, fontWeight: 700,
                    }}>
                    <svg width="17" height="17" viewBox="0 0 24 24" fill="none"
                      stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                      <path d="M21 11.5a8.38 8.38 0 01-.9 3.8 8.5 8.5 0 01-7.6 4.7 8.38 8.38 0 01-3.8-.9L3 21l1.9-5.7a8.38 8.38 0 01-.9-3.8 8.5 8.5 0 014.7-7.6 8.38 8.38 0 013.8-.9h.5a8.48 8.48 0 018 8v.5z"/>
                    </svg>
                    {post.commentCount > 0 && <span>{post.commentCount}</span>}
                  </button>
                  {/* Repost — only enabled for signed-in users, can't repost your own post */}
                  <button
                    onClick={function() {
                      if (!user) return;
                      if (post.username === user.username) return;
                      togglePostRepost(actionId);
                    }}
                    disabled={!user || post.username === (user && user.username)}
                    title={post.username === (user && user.username) ? "You can't repost your own post" : ""}
                    style={{
                      flex: 1, background: "none", border: "none",
                      cursor: (!user || post.username === (user && user.username)) ? "not-allowed" : "pointer",
                      padding: "8px 6px",
                      display: "flex", alignItems: "center", justifyContent: "center", gap: 6,
                      color: reposted ? "#10B981" : "#888", fontSize: 12, fontWeight: 700,
                      opacity: post.username === (user && user.username) ? 0.4 : 1,
                    }}>
                    <svg width="17" height="17" viewBox="0 0 24 24" fill="none"
                      stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                      <polyline points="17 1 21 5 17 9"/><path d="M3 11V9a4 4 0 014-4h14"/>
                      <polyline points="7 23 3 19 7 15"/><path d="M21 13v2a4 4 0 01-4 4H3"/>
                    </svg>
                    {post.repostCount > 0 && <span>{post.repostCount}</span>}
                  </button>
                </div>
              </div>
            );
          })}
        </div>
      )}

      {/* Comments bottom sheet — reused from existing component */}
      {openCommentsFor && (
        <CommentsBottomSheet
          contentId={openCommentsFor}
          itemComments={postComments[openCommentsFor] || []}
          currentUser={user}
          onSubmit={submitPostComment}
          onDelete={deletePostComment}
          onClose={function() { setOpenCommentsFor(null); }}
        />
      )}

      {/* Image lightbox */}
      {lightbox && (
        <div onClick={function() { setLightbox(null); }} style={{
          position: "fixed", inset: 0, zIndex: 99998,
          background: "rgba(0,0,0,0.94)",
          display: "flex", alignItems: "center", justifyContent: "center",
          padding: 16,
        }}>
          <img
            src={typeof lightbox.images[lightbox.index] === "string"
              ? lightbox.images[lightbox.index]
              : (lightbox.images[lightbox.index] && lightbox.images[lightbox.index].url) || ""}
            style={{ maxWidth: "100%", maxHeight: "100%", objectFit: "contain" }}
            onClick={function(e){ e.stopPropagation(); }}
          />
          <button onClick={function() { setLightbox(null); }} style={{
            position: "absolute", top: "max(16px, var(--bf-safe-top, env(safe-area-inset-top)))", right: 16,
            background: "rgba(255,255,255,0.1)", border: "1px solid #333",
            color: "white", fontSize: 22, cursor: "pointer",
            width: 40, height: 40, borderRadius: "50%",
            display: "flex", alignItems: "center", justifyContent: "center",
          }}>×</button>
        </div>
      )}
    </div>
  );
}

function FeaturedCarousel({ savedIds, onSave, onPlay }) {
  const [beats, setBeats] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    apiFetch("/api/youtube/search?artist=best+free+beats&page=1&filter_title=false&max=8")
      .then(d => { setBeats(d.beats || []); setLoading(false); })
      .catch(() => setLoading(false));
  }, []);

  if (loading) return (
    <div style={{ paddingLeft: 16 }}>
      <BFLoader type="bars" text="LOADING BEATS...PLEASE BE PATIENT." />
    </div>
  );

  return (
    <div className="bf-carousel" style={{
      overflowX: "auto", scrollbarWidth: "none",
      WebkitOverflowScrolling: "touch", paddingLeft: 16,
    }}>
      <div style={{ display: "flex", gap: 12, paddingRight: 16 }}>
        {beats.map(beat => {
          const isSaved = savedIds.has(beat.videoId);
          return (
            <div key={beat.videoId} className="bf-card" onClick={() => onPlay(beat)} style={{
              flexShrink: 0, width: 178, cursor: "pointer",
              background: "linear-gradient(180deg,#1a1a1a,#111)",
              borderRadius: 16, overflow: "hidden",
              border: "1px solid rgba(255,255,255,0.07)",
              boxShadow: "0 4px 20px rgba(0,0,0,0.5)",
            }}>
              <div style={{ position: "relative", height: 112, overflow: "hidden" }}>
                <img
                  src={beat.thumbnail || "https://img.youtube.com/vi/" + beat.videoId + "/hqdefault.jpg"}
                  alt={beat.title}
                  style={{ width: "100%", height: "100%", objectFit: "cover", display: "block" }}
                />
                <div style={{ position: "absolute", inset: 0,
                  background: "linear-gradient(to top,rgba(0,0,0,0.7),transparent 55%)" }} />
                {/* Neon play button */}
                <div style={{ position:"absolute", inset:0, display:"flex", alignItems:"center", justifyContent:"center", pointerEvents:"none" }}>
                  <svg width="54" height="38" viewBox="0 0 72 52" fill="none" xmlns="http://www.w3.org/2000/svg">
                    <defs>
                      <filter id="neon-grid" x="-40%" y="-40%" width="180%" height="180%">
                        <feGaussianBlur stdDeviation="3" result="blur1"/>
                        <feGaussianBlur stdDeviation="8" result="blur2"/>
                        <feMerge><feMergeNode in="blur2"/><feMergeNode in="blur1"/><feMergeNode in="SourceGraphic"/></feMerge>
                      </filter>
                      <linearGradient id="ng-grid" x1="0%" y1="0%" x2="100%" y2="0%">
                        <stop offset="0%" stopColor="#9333ea"/>
                        <stop offset="100%" stopColor="#3b82f6"/>
                      </linearGradient>
                    </defs>
                    <rect x="3" y="3" width="66" height="46" rx="8" stroke="url(#ng-grid)" strokeWidth="1.5" fill="black" filter="url(#neon-grid)" opacity="0.9"/>
                    <polygon points="29,16 29,36 48,26" stroke="url(#ng-grid)" strokeWidth="1.5" fill="none" strokeLinejoin="round" filter="url(#neon-grid)" opacity="0.9"/>
                  </svg>
                </div>
                <button className="bf-save" onClick={e => { e.stopPropagation(); onSave(beat); }} style={{
                  position: "absolute", top: 7, right: 7,
                  width: 30, height: 30, borderRadius: "50%", border: "none",
                  cursor: "pointer", display: "flex", alignItems: "center",
                  justifyContent: "center", fontSize: 14,
                  background: isSaved ? "rgba(192,38,211,0.9)" : "rgba(0,0,0,0.65)",
                  color: isSaved ? "white" : "rgba(255,255,255,0.6)",
                  boxShadow: isSaved ? "0 0 10px rgba(192,38,211,0.5)" : "none",
                }}><AppIcon id="bookmark" size={20}/></button>
              </div>
              <div style={{ padding: "10px 12px 12px" }}>
                <div style={{
                  color: "white", fontSize: 12, fontWeight: 700, lineHeight: 1.4,
                  overflow: "hidden", display: "-webkit-box",
                  WebkitLineClamp: 2, WebkitBoxOrient: "vertical", marginBottom: 4,
                }}>{beat.title}</div>
                <div style={{ color: "#555", fontSize: 10, fontWeight: 500 }}>{beat.channel}</div>
              </div>
            </div>
          );
        })}
      </div>
    </div>
  );
}

function TrendingStrip({ savedIds, onSave, onPlay }) {
  const [beats, setBeats] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    apiFetch("/api/youtube/trending")
      .then(d => { setBeats((d.beats || []).slice(0, 6)); setLoading(false); })
      .catch(() => setLoading(false));
  }, []);

  if (loading) return (
    <div style={{ paddingLeft: 16 }}>
      <BFLoader type="bars" text="LOADING BEATS...PLEASE BE PATIENT." />
    </div>
  );

  if (!beats.length) return null;

  return (
    <div className="bf-carousel" style={{
      overflowX: "auto", scrollbarWidth: "none",
      WebkitOverflowScrolling: "touch", paddingLeft: 16,
    }}>
      <div style={{ display: "flex", gap: 12, paddingRight: 16 }}>
        {beats.map((beat, i) => {
          const isSaved = savedIds.has(beat.videoId);
          return (
            <div key={beat.videoId} className="bf-card" onClick={() => onPlay(beat)} style={{
              flexShrink: 0, width: 152, cursor: "pointer",
              background: "linear-gradient(180deg,#161616,#0f0f0f)",
              borderRadius: 14, overflow: "hidden",
              border: "1px solid rgba(245,158,11,0.12)",
              boxShadow: "0 4px 16px rgba(0,0,0,0.5)",
            }}>
              <div style={{ position: "relative", height: 96, overflow: "hidden" }}>
                <img
                  src={beat.thumbnail || "https://img.youtube.com/vi/" + beat.videoId + "/hqdefault.jpg"}
                  alt={beat.title}
                  style={{ width: "100%", height: "100%", objectFit: "cover", display: "block" }}
                />
                <div style={{ position: "absolute", inset: 0, background: "rgba(0,0,0,0.3)" }} />
                {/* Neon play button */}
                <div style={{ position:"absolute", inset:0, display:"flex", alignItems:"center", justifyContent:"center", pointerEvents:"none" }}>
                  <svg width="48" height="34" viewBox="0 0 72 52" fill="none" xmlns="http://www.w3.org/2000/svg">
                    <defs>
                      <filter id="neon-trend" x="-40%" y="-40%" width="180%" height="180%">
                        <feGaussianBlur stdDeviation="3" result="blur1"/>
                        <feGaussianBlur stdDeviation="8" result="blur2"/>
                        <feMerge><feMergeNode in="blur2"/><feMergeNode in="blur1"/><feMergeNode in="SourceGraphic"/></feMerge>
                      </filter>
                      <linearGradient id="ng-trend" x1="0%" y1="0%" x2="100%" y2="0%">
                        <stop offset="0%" stopColor="#9333ea"/>
                        <stop offset="100%" stopColor="#3b82f6"/>
                      </linearGradient>
                    </defs>
                    <rect x="3" y="3" width="66" height="46" rx="8" stroke="url(#ng-trend)" strokeWidth="1.5" fill="black" filter="url(#neon-trend)" opacity="0.9"/>
                    <polygon points="29,16 29,36 48,26" stroke="url(#ng-trend)" strokeWidth="1.5" fill="none" strokeLinejoin="round" filter="url(#neon-trend)" opacity="0.9"/>
                  </svg>
                </div>
                {/* Rank badge */}
                <div style={{
                  position: "absolute", top: 7, left: 8,
                  background: "rgba(0,0,0,0.75)", backdropFilter: "blur(4px)",
                  border: "1px solid rgba(245,158,11,0.4)",
                  borderRadius: 20, padding: "2px 9px",
                  fontSize: 10, color: "#F59E0B", fontWeight: 900,
                }}>#{i + 1}</div>
                <button className="bf-save" onClick={e => { e.stopPropagation(); onSave(beat); }} style={{
                  position: "absolute", top: 5, right: 6,
                  width: 26, height: 26, borderRadius: "50%", border: "none",
                  cursor: "pointer", display: "flex", alignItems: "center",
                  justifyContent: "center", fontSize: 12,
                  background: isSaved ? "rgba(192,38,211,0.9)" : "rgba(0,0,0,0.65)",
                  color: isSaved ? "white" : "rgba(255,255,255,0.5)",
                }}><AppIcon id="bookmark" size={20}/></button>
              </div>
              <div style={{ padding: "8px 10px 10px" }}>
                <div style={{
                  color: "white", fontSize: 11, fontWeight: 700, lineHeight: 1.35,
                  overflow: "hidden", display: "-webkit-box",
                  WebkitLineClamp: 2, WebkitBoxOrient: "vertical", marginBottom: 3,
                }}>{beat.title}</div>
                <div style={{ color: "#555", fontSize: 10 }}>{beat.channel}</div>
              </div>
            </div>
          );
        })}
      </div>
    </div>
  );
}


// =============================================================================
// ARTISTS SCREEN
// =============================================================================
function ArtistsScreen({ onPlay, savedIds, onSave, resetKey, currentUser }) {
  const [region,       setRegion]       = useState("USA");
  const [cat,          setCat]          = useState("All");
  const [search,       setSearch]       = useState("");
  const [selectedArtist, setSelectedArtist] = useState(null);
  const [browseMode,   setBrowseMode]   = useState("beats"); // "beats" | "music"

  // When the user re-taps the Artists tab while already inside it,
  // resetKey bumps — clear any selected artist so they land back at the
  // artist-list root view.
  useEffect(function() {
    setSelectedArtist(null);
  }, [resetKey]);

  // If an artist is selected, show their beat page
  if (selectedArtist) {
    return (
      <ArtistDetailScreen
        artist={selectedArtist}
        mode={browseMode}
        currentUser={currentUser}
        onBack={() => setSelectedArtist(null)}
        onPlay={onPlay}
        savedIds={savedIds}
        onSave={onSave}
      />
    );
  }

  const REGION_MAP = {
    USA:     { artists: ARTISTS_USA,     cats: USA_CATS     },
    UK:      { artists: ARTISTS_UK,      cats: UK_CATS      },
    JAMAICA: { artists: ARTISTS_JAMAICA, cats: JAMAICA_CATS },
    AFRICA:  { artists: ARTISTS_AFRICA,  cats: AFRICA_CATS  },
  };
  const artists = REGION_MAP[region].artists;
  const cats    = REGION_MAP[region].cats;
  const list    = artists.filter(a =>
    (cat === "All" || a.cat === cat) &&
    a.name.toLowerCase().includes(search.toLowerCase())
  ).sort((a, b) => a.name.localeCompare(b.name));

  return (
    <div style={{ padding: "0 16px 100px" }}>
      <div style={{ display: "flex", alignItems: "center", justifyContent: "space-between", padding: "20px 0 14px" }}>
        <div>
          <div style={{ color: "white", fontFamily: "'Bebas Neue',sans-serif", fontSize: 32, letterSpacing: 2, display:"flex", alignItems:"center", gap:10 }}><AppIcon id="artists" size={28} />BEATFINDER</div>
          <div style={{ color: "#888", fontSize: 13 }}>{browseMode === "music" ? "Official music & videos." : "Type beats, organized."}</div>
        </div>
        <img
          src="https://i.ibb.co/v4wcZVJW/IMG-9119.jpg"
          alt="BeatFinder Logo"
          style={{
            width: 72, height: 72, borderRadius: 16,
            objectFit: "cover", flexShrink: 0,

          }}
        />
      </div>

      {/* Type Beats / Music mode toggle */}
      <div style={{ display: "flex", gap: 6, padding: 5, marginBottom: 14, borderRadius: 14, background: "rgba(20,12,42,0.5)", border: "1px solid rgba(176,38,255,0.2)" }}>
        {[{ id: "beats", label: "Type Beats", icon: "flame" }, { id: "music", label: "Music", icon: "music" }].map(function(m){
          const on = browseMode === m.id;
          return (
            <button key={m.id} onClick={function(){ setBrowseMode(m.id); }} style={{
              flex: 1, display: "flex", alignItems: "center", justifyContent: "center", gap: 7,
              padding: "11px 8px", borderRadius: 10, cursor: "pointer",
              border: on ? "1px solid rgba(176,38,255,0.7)" : "1px solid transparent",
              background: on ? "linear-gradient(180deg,rgba(176,38,255,0.22),rgba(0,213,255,0.08))" : "rgba(255,255,255,0.02)",
              color: on ? "#fff" : "#7a7296", fontWeight: 800, fontSize: 14,
              boxShadow: on ? "0 0 16px rgba(176,38,255,0.35)" : "none",
            }}><AppIcon id={m.icon} size={16} /> {m.label}</button>
          );
        })}
      </div>
      <div style={{ background: "#1a1a1a", borderRadius: 12, padding: "10px 14px", display: "flex", alignItems: "center", gap: 10, marginBottom: 12, border: "1px solid #222" }}>
        <span style={{ color: "#555" }}><AppIcon id="search" size={20}/></span>
        <input value={search} onChange={e => setSearch(e.target.value)} placeholder="Search artists"
          style={{ background: "none", border: "none", outline: "none", color: "white", fontSize: 15, flex: 1 }} />
      </div>
      <div style={{ display: "flex", gap: 8, marginBottom: 12, flexWrap: "wrap" }}>
        {[
          { id: "USA",     label: "🇺🇸 US"      },
          { id: "UK",      label: "🇬🇧 UK"      },
          { id: "JAMAICA", label: "🇯🇲 Jamaica"  },
          { id: "AFRICA",  label: "🇿🇦🇳🇬 Africa"   },
        ].map(r => (
          <button key={r.id} onClick={() => { setRegion(r.id); setCat("All"); }}
            style={{
              borderRadius: 24, padding: "8px 16px", fontWeight: 700, fontSize: 13, cursor: "pointer",
              border: region === r.id ? "2px solid #C026D3" : "1.5px solid #333",
              background: "transparent", color: region === r.id ? "white" : "#888",
            }}>
            {r.label}
          </button>
        ))}
      </div>
      <div style={{ display: "flex", gap: 8, marginBottom: 18, flexWrap: "wrap" }}>
        {cats.map(c => {
          const chipColor = c === "Melodic Trap"    ? "#38BDF8"
                          : c === "UK Melodic Trap" ? "#38BDF8"
                          : c === "Detroit"          ? "#F59E0B"
                          : c === "UK Drill"         ? "#4ADE80"
                          : c === "Rap"              ? "#EF4444"
                          : c === "UK Rap"           ? "#EF4444"
                          : c === "UK R&B"           ? "#EC4899"
                          : c === "Grime"            ? "#A78BFA"
                          : "#C026D3";
          const isActive = cat === c;
          return (
            <button key={c} onClick={() => setCat(c)}
              style={{
                borderRadius: 20, padding: "5px 13px", fontSize: 12, fontWeight: 700, cursor: "pointer",
                border: isActive ? "1.5px solid " + chipColor : "1px solid #333",
                background: isActive ? chipColor + "26" : "transparent",
                color: isActive ? chipColor : "#666",
              }}>
              {c}
            </button>
          );
        })}
      </div>
      <div style={{ color: "#555", fontSize: 12, marginBottom: 14 }}>{list.length} artists</div>
      <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr 1fr", gap: "20px 10px" }}>
        {list.map((a, i) => (
          <div key={a.id} onClick={() => setSelectedArtist(a)}
            style={{ display: "flex", flexDirection: "column", alignItems: "center", cursor: "pointer" }}>
            <Av name={a.name} size={88} idx={i} img={a.img} />
            <div style={{ color: "white", fontSize: 11, fontWeight: 600, marginTop: 8, textAlign: "center", lineHeight: 1.3 }}>
              {a.name}
            </div>
            <div style={{
              fontSize: 10, marginTop: 2,
              color: a.cat === "Detroit"                         ? "#F59E0B"
                   : a.cat === "R&B M" || a.cat === "R&B F"    ? "#EC4899"
                   : a.cat === "UK R&B"                         ? "#EC4899"
                   : a.cat === "Grime"                          ? "#A78BFA"
                   : a.cat === "Reggae"                         ? "#22C55E"
                   : a.cat === "Dancehall" || a.cat === "Bashment" ? "#F97316"
                   : a.cat === "Afrobeats"                      ? "#EAB308"
                   : a.cat === "Tribal House"                   ? "#A78BFA"
                   : a.cat === "Melodic Trap"                   ? "#38BDF8"
                   : a.cat === "UK Melodic Trap"                ? "#38BDF8"
                   : a.cat === "UK Drill"                       ? "#4ADE80"
                   : a.cat === "Rap" || a.cat === "UK Rap"      ? "#EF4444" : "#666",
            }}>
              {a.cat}
            </div>
          </div>
        ))}
      </div>
    </div>
  );
}

// =============================================================================
// ARTIST DETAIL
// =============================================================================
// Official-music feed for an artist (Music mode). Pulls only official videos/
// albums/live/freestyles via the backend's filtered endpoint — no dud links.
function ArtistMusicFeed({ artistId, artistName, aliases, channelHandle, savedIds, onSave, onPlay, mustInclude }) {
  const [songs, setSongs]   = React.useState(null);
  const [curated, setCurated] = React.useState([]);
  const [curPage, setCurPage] = React.useState(1);
  const [page, setPage]     = React.useState(1);
  const [total, setTotal]   = React.useState(0);
  const [loading, setLoading] = React.useState(true);
  const rootRef = React.useRef(null);

  function scrollFeedTop() {
    try {
      window.scrollTo(0, 0);
      if (document.scrollingElement) document.scrollingElement.scrollTop = 0;
      var n = rootRef.current ? rootRef.current.parentNode : null;
      while (n && n !== document.body) {
        if (n.scrollHeight > n.clientHeight + 4) { n.scrollTop = 0; }
        n = n.parentNode;
      }
    } catch (e) {}
  }

  // Admin-curated picks — pinned at the top, paginated 10 per page.
  React.useEffect(function(){
    if (!artistId) { setCurated([]); return; }
    apiFetch("/api/youtube/curated?artist_id=" + encodeURIComponent(artistId) + "&section=music")
      .then(function(d){ setCurated(((d && d.beats) || []).map(function(b){ return Object.assign({}, b, { isSong: true }); })); setCurPage(1); })
      .catch(function(){ setCurated([]); });
  }, [artistId]);

  React.useEffect(function(){
    setLoading(true);
    scrollFeedTop();
    var url;
    if (channelHandle) {
      url = "/api/youtube/channel-uploads?handle=" + encodeURIComponent(channelHandle) + "&kind=music&page=" + page;
    } else {
      const aliasParam = aliases ? ("&aliases=" + encodeURIComponent(aliases)) : "";
      url = "/api/youtube/artist-music?artist=" + encodeURIComponent(artistName) + aliasParam + "&page=" + page;
    }
    apiFetch(url)
      .then(function(d){
        const list = ((d && d.beats) || []).map(function(b){ return Object.assign({}, b, { isSong: true }); });
        setSongs(list); setTotal((d && d.total) || 0); setLoading(false);
      })
      .catch(function(){ setSongs([]); setLoading(false); });
  }, [artistName, aliases, channelHandle, page]);

  // Backend 'total' counts UNFILTERED results, so when mustInclude strips most
  // of them we'd otherwise show empty pages. Only offer the next page while the
  // current page still yields a reasonably full filtered set.
  var rawTotalPages = Math.max(1, Math.ceil(total / 10));
  const curatedIds = curated.map(function(c){ return c.videoId; });
  // Auto songs for this page, minus any that duplicate a curated pin.
  var autoList = (songs || []).filter(function(s){
    return curatedIds.indexOf(s.videoId) === -1;
  });
  // Require the artist's name to appear in the title or channel, on EVERY page,
  // so unrelated songs that merely surfaced in search are always dropped.
  if (mustInclude) {
    var terms = mustInclude.split("|").map(function(t){ return t.trim().toLowerCase(); }).filter(Boolean);
    if (terms.length) {
      autoList = autoList.filter(function(s){
        var hay = ((s.title || "") + " " + (s.channel || "")).toLowerCase();
        return terms.some(function(t){ return hay.indexOf(t) !== -1; });
      });
    }
  }
  var totalPages = mustInclude
    ? Math.min(rawTotalPages, autoList.length >= 8 ? page + 1 : page)
    : rawTotalPages;
  // ONE combined list under ONE pagination: curated pins lead on page 1, then
  // the auto feed. This avoids a second page-number bar appearing mid-feed.
  var displayList = (page === 1 ? curated.concat(autoList) : autoList);

  if (loading && songs === null) {
    return <div style={{ paddingTop: 10 }}><BFLoader type="bars" text="Loading official music...please be patient." /></div>;
  }
  if ((!songs || songs.length === 0) && curated.length === 0) {
    return <div style={{ color: "#7a6ea0", fontSize: 13, textAlign: "center", padding: "30px 16px", lineHeight: 1.6 }}>No official music found for this artist yet. Try the Type Beats tab, or another artist.</div>;
  }

  return (
    <div ref={rootRef}>
      {loading && <div style={{ paddingBottom: 8 }}><BFLoader type="bars" text="Loading..." /></div>}
      {/* Single pagination bar (top) */}
      {totalPages > 1 && (
        <div style={{ display: "flex", alignItems: "center", justifyContent: "space-between", marginBottom: 16, background: "#111", borderRadius: 12, padding: "10px 14px", border: "1px solid #1e1e1e" }}>
          <button onClick={function(){ if (page > 1) setPage(page - 1); }} disabled={page === 1} style={{
            background: page === 1 ? "transparent" : "#1a1a1a", border: page === 1 ? "1px solid #222" : "1px solid #444",
            borderRadius: 10, color: page === 1 ? "#333" : "white", fontWeight: 800, fontSize: 14, padding: "8px 16px",
            cursor: page === 1 ? "not-allowed" : "pointer" }}>Prev</button>
          <div style={{ color: "#888", fontSize: 13, fontWeight: 600 }}>Page {page} of {totalPages}</div>
          <button onClick={function(){ if (page < totalPages) setPage(page + 1); }} disabled={page === totalPages} style={{
            background: page === totalPages ? "transparent" : "#C026D3", border: page === totalPages ? "1px solid #222" : "none",
            borderRadius: 10, color: page === totalPages ? "#333" : "white", fontWeight: 800, fontSize: 14, padding: "8px 16px",
            cursor: page === totalPages ? "not-allowed" : "pointer" }}>Next</button>
        </div>
      )}
      {displayList.map(function(song){
        return <BeatCard key={song.videoId} beat={song} savedIds={savedIds} onSave={onSave} onPlay={onPlay} />;
      })}
      {/* Single pagination numbers (bottom only) */}
      {totalPages > 1 && (
        <div style={{ padding: "20px 0 40px" }}>
          <div style={{ color: "#555", fontSize: 12, textAlign: "center", marginBottom: 14 }}>Page {page} of {totalPages}</div>
          <div style={{ display: "flex", flexWrap: "wrap", gap: 8, justifyContent: "center" }}>
            {Array.from({ length: totalPages }, function(_, i){ return i + 1; }).map(function(p){
              return <button key={p} onClick={function(){ setPage(p); scrollFeedTop(); }} style={{
                width: 40, height: 40, borderRadius: "50%", background: page === p ? "#C026D3" : "#1a1a1a",
                border: page === p ? "2px solid #C026D3" : "1.5px solid #333", color: page === p ? "white" : "#888",
                fontWeight: page === p ? 800 : 600, fontSize: 14, cursor: "pointer" }}>{p}</button>;
            })}
          </div>
        </div>
      )}
    </div>
  );
}

// Admin-pinned videos for an artist+section, rendered above the auto feed.
// Admin overlay: pin/unpin curated videos for an artist + section.
function CuratedPinOverlay({ artist, section, onClose }) {
  const [url, setUrl] = React.useState("");
  const [busy, setBusy] = React.useState(false);
  const [msg, setMsg] = React.useState("");
  const [items, setItems] = React.useState([]);

  const reload = React.useCallback(function(){
    apiFetch("/api/youtube/admin/curated?artist_id=" + encodeURIComponent(artist.id))
      .then(function(d){ setItems(((d && d.items) || []).filter(function(it){ return it.section === section; })); })
      .catch(function(){ setItems([]); });
  }, [artist.id, section]);
  React.useEffect(function(){ reload(); }, [reload]);

  function add(){
    if (!url.trim() || busy) return;
    setBusy(true); setMsg("");
    apiFetch("/api/youtube/admin/curated", { method: "POST", body: JSON.stringify({ artist_id: artist.id, section: section, url: url.trim() }) })
      .then(function(){ setUrl(""); setMsg("Pinned!"); reload(); })
      .catch(function(e){ setMsg((e && e.message) || "Could not pin that video"); })
      .finally(function(){ setBusy(false); });
  }
  function remove(id){
    apiFetch("/api/youtube/admin/curated/" + id, { method: "DELETE" }).then(reload).catch(function(){});
  }

  return (
    <div onClick={onClose} style={{ position: "fixed", inset: 0, zIndex: 10000, background: "rgba(0,0,0,0.7)", display: "flex", alignItems: "center", justifyContent: "center", padding: "16px 12px", overflowY: "auto", WebkitOverflowScrolling: "touch" }}>
      <div onClick={function(e){ e.stopPropagation(); }} style={{
        width: "100%", maxWidth: 560, background: "#0d0820", borderRadius: 20,
        border: "1px solid rgba(176,38,255,0.4)",
        boxShadow: "0 24px 60px rgba(0,0,0,0.7), 0 0 30px rgba(176,38,255,0.25)",
        maxHeight: "calc(100dvh - 32px)", display: "flex", flexDirection: "column",
      }}>
        {/* sticky header */}
        <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", padding: "16px 16px 12px", borderBottom: "1px solid rgba(255,255,255,0.06)", flexShrink: 0 }}>
          <div style={{ color: "#fff", fontWeight: 800, fontSize: 16, paddingRight: 10 }}>Pin to {section === "music" ? "Music" : "Type Beats"} — {artist.name}</div>
          <button onClick={onClose} style={{ background: "none", border: "none", color: "#9590a8", fontSize: 22, cursor: "pointer", flexShrink: 0, lineHeight: 1 }}>&#10005;</button>
        </div>
        {/* scrollable body — generous bottom padding clears the keyboard */}
        <div style={{ overflowY: "auto", WebkitOverflowScrolling: "touch", padding: "14px 16px 120px" }}>
        <input value={url} onChange={function(e){ setUrl(e.target.value); }} placeholder="Paste YouTube link or video ID"
          style={{ width: "100%", boxSizing: "border-box", background: "rgba(20,15,32,0.9)", border: "1px solid rgba(176,38,255,0.4)", borderRadius: 12, padding: "12px 14px", color: "white", fontSize: 14, outline: "none", marginBottom: 10 }} />
        <button onClick={add} disabled={busy} style={{
          width: "100%", padding: "12px", borderRadius: 12, fontWeight: 800, fontSize: 14, cursor: busy ? "default" : "pointer",
          background: "linear-gradient(135deg,#B026FF,#7D3CFF)", border: "1px solid rgba(176,38,255,0.6)", color: "white", opacity: busy ? 0.6 : 1,
        }}>{busy ? "Pinning..." : "Pin Video"}</button>
        {msg && <div style={{ color: msg === "Pinned!" ? "#4ADE80" : "#F87171", fontSize: 13, marginTop: 8, textAlign: "center" }}>{msg}</div>}

        {items.length > 0 && (
          <div style={{ marginTop: 18 }}>
            <div style={{ color: "#9590a8", fontSize: 11, fontWeight: 800, letterSpacing: 1, marginBottom: 8 }}>PINNED ({items.length})</div>
            {items.map(function(it){
              return (
                <div key={it.id} style={{ display: "flex", alignItems: "center", gap: 10, padding: "8px 0", borderBottom: "1px solid rgba(255,255,255,0.06)" }}>
                  {it.thumbnail && <img src={it.thumbnail} alt="" style={{ width: 56, height: 34, objectFit: "cover", borderRadius: 6, flexShrink: 0 }} />}
                  <div style={{ flex: 1, color: "#ddd", fontSize: 12, lineHeight: 1.3, overflow: "hidden", textOverflow: "ellipsis" }}>{it.title}</div>
                  <button onClick={function(){ remove(it.id); }} style={{ background: "none", border: "1px solid rgba(248,113,113,0.4)", color: "#F87171", borderRadius: 8, padding: "5px 10px", fontSize: 12, fontWeight: 700, cursor: "pointer", flexShrink: 0 }}>Remove</button>
                </div>
              );
            })}
          </div>
        )}
        </div>
      </div>
    </div>
  );
}

function CuratedRow({ artistId, section, savedIds, onSave, onPlay, onCount }) {
  const [items, setItems] = React.useState([]);
  const [page, setPage]   = React.useState(1);
  React.useEffect(function(){
    if (!artistId) { setItems([]); if (onCount) onCount(0); return; }
    apiFetch("/api/youtube/curated?artist_id=" + encodeURIComponent(artistId) + "&section=" + section)
      .then(function(d){
        const isSong = section === "music";
        const list = ((d && d.beats) || []).map(function(b){ return Object.assign({}, b, isSong ? { isSong: true } : {}); });
        setItems(list); setPage(1);
        if (onCount) onCount(list.length);
      })
      .catch(function(){ setItems([]); if (onCount) onCount(0); });
  }, [artistId, section]);
  if (!items.length) return null;
  const PER = 10;
  const TOTAL_PAGES = Math.max(1, Math.ceil(items.length / PER));
  const start = (page - 1) * PER;
  const pageItems = items.slice(start, start + PER);
  return (
    <div>
      {TOTAL_PAGES > 1 && (
        <div style={{ display: "flex", alignItems: "center", justifyContent: "space-between", marginBottom: 16, background: "#111", borderRadius: 12, padding: "10px 14px", border: "1px solid #1e1e1e" }}>
          <button onClick={function(){ if (page > 1) setPage(page - 1); }} disabled={page === 1} style={{
            background: page === 1 ? "transparent" : "#1a1a1a", border: page === 1 ? "1px solid #222" : "1px solid #444",
            borderRadius: 10, color: page === 1 ? "#333" : "white", fontWeight: 800, fontSize: 14, padding: "8px 16px",
            cursor: page === 1 ? "not-allowed" : "pointer" }}>Prev</button>
          <div style={{ color: "#888", fontSize: 13, fontWeight: 600 }}>Page {page} of {TOTAL_PAGES}</div>
          <button onClick={function(){ if (page < TOTAL_PAGES) setPage(page + 1); }} disabled={page === TOTAL_PAGES} style={{
            background: page === TOTAL_PAGES ? "transparent" : "#C026D3", border: page === TOTAL_PAGES ? "1px solid #222" : "none",
            borderRadius: 10, color: page === TOTAL_PAGES ? "#333" : "white", fontWeight: 800, fontSize: 14, padding: "8px 16px",
            cursor: page === TOTAL_PAGES ? "not-allowed" : "pointer" }}>Next</button>
        </div>
      )}
      {pageItems.map(function(b){ return <BeatCard key={"cur-" + b.videoId} beat={b} savedIds={savedIds} onSave={onSave} onPlay={onPlay} />; })}
      {TOTAL_PAGES > 1 && (
        <div style={{ padding: "20px 0 40px" }}>
          <div style={{ color: "#555", fontSize: 12, textAlign: "center", marginBottom: 14 }}>Page {page} of {TOTAL_PAGES}</div>
          <div style={{ display: "flex", flexWrap: "wrap", gap: 8, justifyContent: "center" }}>
            {Array.from({ length: TOTAL_PAGES }, function(_, i){ return i + 1; }).map(function(p){
              return <button key={p} onClick={function(){ setPage(p); }} style={{
                width: 40, height: 40, borderRadius: "50%", background: page === p ? "#C026D3" : "#1a1a1a",
                border: page === p ? "2px solid #C026D3" : "1.5px solid #333", color: page === p ? "white" : "#888",
                fontWeight: page === p ? 800 : 600, fontSize: 14, cursor: "pointer" }}>{p}</button>;
            })}
          </div>
        </div>
      )}
    </div>
  );
}

function ArtistChannelBeatsFeed({ channelHandle, savedIds, onSave, onPlay, suppressEmpty }) {
  const [items, setItems]   = React.useState(null);
  const [page, setPage]     = React.useState(1);
  const [total, setTotal]   = React.useState(0);
  const [loading, setLoading] = React.useState(true);

  React.useEffect(function(){
    setLoading(true);
    apiFetch("/api/youtube/channel-uploads?handle=" + encodeURIComponent(channelHandle) + "&kind=beats&page=" + page)
      .then(function(d){ setItems((d && d.beats) || []); setTotal((d && d.total) || 0); setLoading(false); })
      .catch(function(){ setItems([]); setLoading(false); });
  }, [channelHandle, page]);

  const totalPages = Math.max(1, Math.ceil(total / 10));

  if (loading && items === null) {
    return <div style={{ paddingTop: 10 }}><BFLoader type="bars" text="Loading beats...please be patient." /></div>;
  }
  if (!items || items.length === 0) {
    if (suppressEmpty) return null;
    return <div style={{ color: "#7a6ea0", fontSize: 13, textAlign: "center", padding: "30px 16px", lineHeight: 1.6 }}>No beats found on this channel yet.</div>;
  }
  return (
    <div>
      {loading && <div style={{ paddingBottom: 8 }}><BFLoader type="bars" text="Loading..." /></div>}
      {items.map(function(b){ return <BeatCard key={b.videoId} beat={b} savedIds={savedIds} onSave={onSave} onPlay={onPlay} />; })}
      {totalPages > 1 && (
        <div style={{ display: "flex", alignItems: "center", justifyContent: "center", gap: 8, marginTop: 8, marginBottom: 20 }}>
          <button onClick={function(){ if (page > 1) setPage(page - 1); }} disabled={page <= 1} style={{
            padding: "8px 14px", borderRadius: 10, fontSize: 13, fontWeight: 800, cursor: page <= 1 ? "default" : "pointer",
            background: "rgba(255,255,255,0.04)", border: "1px solid rgba(176,38,255,0.3)", color: page <= 1 ? "#4a4366" : "#A78BFA",
          }}>Prev</button>
          <span style={{ color: "#9590a8", fontSize: 12, fontWeight: 700 }}>{page} / {totalPages}</span>
          <button onClick={function(){ if (page < totalPages) setPage(page + 1); }} disabled={page >= totalPages} style={{
            padding: "8px 14px", borderRadius: 10, fontSize: 13, fontWeight: 800, cursor: page >= totalPages ? "default" : "pointer",
            background: "rgba(255,255,255,0.04)", border: "1px solid rgba(176,38,255,0.3)", color: page >= totalPages ? "#4a4366" : "#A78BFA",
          }}>Next</button>
        </div>
      )}
    </div>
  );
}

// Unified artist feed: merges admin-pinned (curated) videos with the auto
// feed into ONE list, paginated 10 per page. Used for BOTH the Type Beats and
// Music tabs so every artist shows exactly 10 videos per page on each tab,
// whether or not videos were pinned manually. Replaces the old split of
// CuratedRow + (ArtistChannelBeatsFeed | BeatFeed) on Beats and the page-1
// concat in ArtistMusicFeed on Music (which could push a page past 10).
function ArtistFeed({ artist, section, savedIds, onSave, onPlay }) {
  const isMusic  = section === "music";
  const PER      = 10;
  const PAGE_CAP = 10; // up to 10 pages of auto videos; fewer when the search
                       // can't find enough (pages shrink to fit the results)

  const [curated, setCurated] = React.useState([]);
  const [auto, setAuto]       = React.useState(null);
  const [loading, setLoading] = React.useState(true);
  const [page, setPage]       = React.useState(1);
  const rootRef = React.useRef(null);

  const artistId      = artist.id;
  const channelHandle = artist.channelHandle || null;

  function scrollFeedTop() {
    try {
      window.scrollTo(0, 0);
      if (document.scrollingElement) document.scrollingElement.scrollTop = 0;
      var n = rootRef.current ? rootRef.current.parentNode : null;
      while (n && n !== document.body) {
        if (n.scrollHeight > n.clientHeight + 4) { n.scrollTop = 0; }
        n = n.parentNode;
      }
    } catch (e) {}
  }

  // ── Curated (admin-pinned) videos for this section ───────────────────────
  React.useEffect(function(){
    if (!artistId) { setCurated([]); return; }
    apiFetch("/api/youtube/curated?artist_id=" + encodeURIComponent(artistId) + "&section=" + section)
      .then(function(d){
        const list = ((d && d.beats) || []).map(function(b){ return Object.assign({}, b, isMusic ? { isSong: true } : {}); });
        setCurated(list);
      })
      .catch(function(){ setCurated([]); });
  }, [artistId, section]);

  // ── Auto feed — fetch ALL pages once, then merge + paginate client-side ──
  React.useEffect(function(){
    let alive = true;
    setLoading(true);
    setAuto(null);
    setPage(1);
    scrollFeedTop();

    async function fetchAutoPage(p) {
      if (isMusic) {
        let url;
        if (channelHandle) {
          url = "/api/youtube/channel-uploads?handle=" + encodeURIComponent(channelHandle) + "&kind=music&page=" + p;
        } else {
          const name = artist.musicSearch || artist.name;
          const aliasParam = artist.musicAliases ? ("&aliases=" + encodeURIComponent(artist.musicAliases)) : "";
          url = "/api/youtube/artist-music?artist=" + encodeURIComponent(name) + aliasParam + "&page=" + p;
        }
        const d = await apiFetch(url);
        const beats = ((d && d.beats) || []).map(function(b){ return Object.assign({}, b, { isSong: true }); });
        return { beats: beats, total: (d && d.total) || 0 };
      }
      // beats
      if (channelHandle) {
        const d = await apiFetch("/api/youtube/channel-uploads?handle=" + encodeURIComponent(channelHandle) + "&kind=beats&page=" + p);
        return { beats: (d && d.beats) || [], total: (d && d.total) || 0 };
      }
      const name = artist.searchOverride || artist.name;
      const res = await fetchBeats(name, p, artist.filterTitle !== false, 10, artist.extraQueries || null, 2);
      return { beats: res.beats || [], total: res.total || 0 };
    }

    (async function(){
      try {
        const first = await fetchAutoPage(1);
        let acc = first.beats || [];
        const pages = Math.min(PAGE_CAP, Math.max(1, Math.ceil((first.total || acc.length) / PER)));
        for (let p = 2; p <= pages; p++) {
          let more;
          try { more = await fetchAutoPage(p); }
          catch (e) { break; }
          if (!more || !more.beats || !more.beats.length) break;
          acc = acc.concat(more.beats);
        }
        if (!alive) return;
        setAuto(acc);
        setLoading(false);
      } catch (e) {
        if (!alive) return;
        setAuto([]);
        setLoading(false);
      }
    })();

    return function(){ alive = false; };
  }, [artistId, section, channelHandle]);

  // ── Section-specific client filters on the auto list ─────────────────────
  // (Server already returns official/instrumental sets; these mirror the
  //  extra per-artist filters the old components applied.)
  let autoList = auto || [];
  if (isMusic) {
    const mustInclude = artist.musicMustInclude || null;
    if (mustInclude) {
      const terms = mustInclude.split("|").map(function(t){ return t.trim().toLowerCase(); }).filter(Boolean);
      if (terms.length) {
        autoList = autoList.filter(function(s){
          const hay = ((s.title || "") + " " + (s.channel || "")).toLowerCase();
          return terms.some(function(t){ return hay.indexOf(t) !== -1; });
        });
      }
    }
  } else if (!channelHandle) {
    if (artist.instrumentalOnly) {
      const KW = ["instrumental", "riddim", "beat", "free beat", "backing track"];
      autoList = autoList.filter(function(b){ return KW.some(function(kw){ return (b.title || "").toLowerCase().includes(kw); }); });
    }
    if (artist.blockedChannels && artist.blockedChannels.length) {
      const blocked = artist.blockedChannels.map(function(c){ return c.toLowerCase(); });
      autoList = autoList.filter(function(b){ return !blocked.some(function(c){ return (b.channel || "").toLowerCase().includes(c); }); });
    }
    if (artist.beatTitleMustInclude) {
      const term = artist.beatTitleMustInclude.toLowerCase();
      const re = new RegExp("(?:^|[^a-z0-9])" + term.replace(/[.*+?^${}()|[\]\\]/g, "\\$&") + "(?:[^a-z0-9]|$)", "i");
      autoList = autoList.filter(function(b){ return re.test(b.title || "") || re.test(b.channel || ""); });
    }
  }

  // ── Merge: pinned videos lead, then auto (de-duped by videoId) ───────────
  const seen = {};
  const merged = [];
  curated.forEach(function(c){ if (c && c.videoId && !seen[c.videoId]) { seen[c.videoId] = true; merged.push(c); } });
  autoList.forEach(function(b){ if (b && b.videoId && !seen[b.videoId]) { seen[b.videoId] = true; merged.push(b); } });
  // Hard cap at 10 pages (PAGE_CAP * PER). Fewer pages when there aren't
  // enough videos — pages always fit the actual results, never padded.
  const combined = merged.slice(0, PAGE_CAP * PER);

  const totalPages = Math.max(1, Math.ceil(combined.length / PER));
  const safePage   = Math.min(page, totalPages);
  const start      = (safePage - 1) * PER;
  const pageItems  = combined.slice(start, start + PER);

  if (loading && auto === null) {
    return <div style={{ paddingTop: 10 }}><BFLoader type="bars" text={isMusic ? "Loading official music...please be patient." : "Loading beats...please be patient."} /></div>;
  }
  if (!combined.length) {
    return <div style={{ color: "#7a6ea0", fontSize: 13, textAlign: "center", padding: "30px 16px", lineHeight: 1.6 }}>
      {isMusic
        ? "No official music found for this artist yet. Try the Type Beats tab, or another artist."
        : "No type beats found for this artist yet. Try the Music tab, or another artist."}
    </div>;
  }

  return (
    <div ref={rootRef}>
      {loading && <div style={{ paddingBottom: 8 }}><BFLoader type="bars" text="Loading..." /></div>}
      {/* Single pagination bar (top) */}
      {totalPages > 1 && (
        <div style={{ display: "flex", alignItems: "center", justifyContent: "space-between", marginBottom: 16, background: "#111", borderRadius: 12, padding: "10px 14px", border: "1px solid #1e1e1e" }}>
          <button onClick={function(){ if (safePage > 1) { setPage(safePage - 1); scrollFeedTop(); } }} disabled={safePage === 1} style={{
            background: safePage === 1 ? "transparent" : "#1a1a1a", border: safePage === 1 ? "1px solid #222" : "1px solid #444",
            borderRadius: 10, color: safePage === 1 ? "#333" : "white", fontWeight: 800, fontSize: 14, padding: "8px 16px",
            cursor: safePage === 1 ? "not-allowed" : "pointer" }}>Prev</button>
          <div style={{ color: "#888", fontSize: 13, fontWeight: 600 }}>Page {safePage} of {totalPages}</div>
          <button onClick={function(){ if (safePage < totalPages) { setPage(safePage + 1); scrollFeedTop(); } }} disabled={safePage === totalPages} style={{
            background: safePage === totalPages ? "transparent" : "#C026D3", border: safePage === totalPages ? "1px solid #222" : "none",
            borderRadius: 10, color: safePage === totalPages ? "#333" : "white", fontWeight: 800, fontSize: 14, padding: "8px 16px",
            cursor: safePage === totalPages ? "not-allowed" : "pointer" }}>Next</button>
        </div>
      )}
      {pageItems.map(function(item){
        return <BeatCard key={item.videoId} beat={item} savedIds={savedIds} onSave={onSave} onPlay={onPlay} />;
      })}
      {/* Single pagination numbers (bottom) */}
      {totalPages > 1 && (
        <div style={{ padding: "20px 0 40px" }}>
          <div style={{ color: "#555", fontSize: 12, textAlign: "center", marginBottom: 14 }}>Page {safePage} of {totalPages}</div>
          <div style={{ display: "flex", flexWrap: "wrap", gap: 8, justifyContent: "center" }}>
            {Array.from({ length: totalPages }, function(_, i){ return i + 1; }).map(function(p){
              return <button key={p} onClick={function(){ setPage(p); scrollFeedTop(); }} style={{
                width: 40, height: 40, borderRadius: "50%", background: safePage === p ? "#C026D3" : "#1a1a1a",
                border: safePage === p ? "2px solid #C026D3" : "1.5px solid #333", color: safePage === p ? "white" : "#888",
                fontWeight: safePage === p ? 800 : 600, fontSize: 14, cursor: "pointer" }}>{p}</button>;
            })}
          </div>
        </div>
      )}
    </div>
  );
}

function ArtistDetailScreen({ artist, mode, currentUser, onBack, onPlay, savedIds, onSave }) {
  const isAdmin = currentUser && (currentUser.is_admin === true || currentUser.username === "Trelli");
  const [pinOpen, setPinOpen] = React.useState(false);
  const [curatedBeatsCount, setCuratedBeatsCount] = React.useState(0);
  const [viewMode, setViewMode] = React.useState(mode === "music" ? "music" : "beats");
  React.useEffect(function(){ setViewMode(mode === "music" ? "music" : "beats"); }, [mode, artist.id]);
  const isMusic = viewMode === "music";
  const allA = [...ARTISTS_USA, ...ARTISTS_UK, ...ARTISTS_JAMAICA, ...ARTISTS_AFRICA];
  const idx  = allA.findIndex(a => a.id === artist.id);
  const searchName = artist.searchOverride || artist.name;
  const cc = artist.cat === "Detroit"                              ? "#F59E0B"
           : artist.cat === "R&B M" || artist.cat === "R&B F"    ? "#EC4899"
           : artist.cat === "UK R&B"                              ? "#EC4899"
           : artist.cat === "Grime"                               ? "#A78BFA"
           : artist.cat === "Reggae"                              ? "#22C55E"
           : artist.cat === "Dancehall" || artist.cat === "Bashment" ? "#F97316"
           : artist.cat === "Afrobeats"                           ? "#EAB308"
           : artist.cat === "Tribal House"                        ? "#A78BFA"
           : artist.cat === "Melodic Trap"                        ? "#38BDF8"
           : artist.cat === "UK Melodic Trap"                     ? "#38BDF8"
           : artist.cat === "UK Drill"                            ? "#4ADE80"
           : artist.cat === "Rap" || artist.cat === "UK Rap"      ? "#EF4444" : "#888";

  return (
    <div style={{ padding: "0 0 100px" }}>
      <div style={{ padding: "16px 16px 0" }}>
        <button onClick={onBack} style={{ background: "none", border: "none", color: "white", fontSize: 28, cursor: "pointer" }}>
          ←
        </button>
      </div>
      <div style={{
        margin: "8px 16px 16px", background: "#111", borderRadius: 16, padding: "16px",
        display: "flex", alignItems: "center", gap: 16, border: "1px solid rgba(255,255,255,0.07)",
      }}>
        <Av name={artist.name} size={72} idx={idx >= 0 ? idx : 0} img={artist.img} />
        <div>
          <div style={{ color: "#aaa", fontSize: 12, marginBottom: 4 }}>
            {artist.flag} {artist.flag === "🇺🇸" ? "USA" : artist.flag === "🇬🇧" ? "🇬🇧" : artist.flag === "🇯🇲" ? "Jamaica" : "Africa"} • <span style={{ color: cc }}>{artist.cat}</span>
          </div>
          <div style={{ color: "white", fontSize: 21, fontWeight: 800, fontFamily: "'Bebas Neue',sans-serif", letterSpacing: 1 }}>
            {artist.name}
          </div>
          <div style={{ color: isMusic ? "#00D5FF" : "#C026D3", fontSize: 13, fontWeight: 600, marginTop: 2 }}>
            {isMusic ? "Official music & videos" : "Official type beats"}
          </div>
        </div>
      </div>

      {/* In-page Type Beats / Music toggle — switch without leaving the page */}
      <div style={{ display: "flex", gap: 6, padding: 5, margin: "0 16px 14px", borderRadius: 14, background: "rgba(20,12,42,0.5)", border: "1px solid rgba(176,38,255,0.2)" }}>
        {[{ id: "beats", label: "Type Beats", icon: "flame" }, { id: "music", label: "Music", icon: "music" }].map(function(m){
          const on = viewMode === m.id;
          return (
            <button key={m.id} onClick={function(){ setViewMode(m.id); }} style={{
              flex: 1, display: "flex", alignItems: "center", justifyContent: "center", gap: 7,
              padding: "10px 8px", borderRadius: 10, cursor: "pointer",
              border: on ? "1px solid rgba(176,38,255,0.7)" : "1px solid transparent",
              background: on ? "linear-gradient(180deg,rgba(176,38,255,0.22),rgba(0,213,255,0.08))" : "rgba(255,255,255,0.02)",
              color: on ? "#fff" : "#7a7296", fontWeight: 800, fontSize: 13.5,
              boxShadow: on ? "0 0 16px rgba(176,38,255,0.35)" : "none",
            }}><AppIcon id={m.icon} size={15} /> {m.label}</button>
          );
        })}
      </div>

      {isAdmin && (
        <div style={{ padding: "0 16px", marginBottom: 12 }}>
          <button onClick={function(){ setPinOpen(true); }} style={{
            width: "100%", display: "flex", alignItems: "center", justifyContent: "center", gap: 8,
            padding: "10px", borderRadius: 12, cursor: "pointer", fontWeight: 800, fontSize: 13,
            background: "rgba(0,213,255,0.08)", border: "1px dashed rgba(0,213,255,0.5)", color: "#7FE7FF",
          }}><AppIcon id="plus" size={15} /> Pin a video to {isMusic ? "Music" : "Type Beats"}</button>
        </div>
      )}
      {pinOpen && (
        <CuratedPinOverlay
          artist={artist}
          section={isMusic ? "music" : "beats"}
          onClose={function(){ setPinOpen(false); }}
        />
      )}

      <div style={{ padding: "0 16px" }}>
        <ArtistFeed artist={artist} section={isMusic ? "music" : "beats"} savedIds={savedIds} onSave={onSave} onPlay={onPlay} />
      </div>
    </div>
  );
}



// =============================================================================
// BEAT LEASE CARD
// =============================================================================
// ── Download helper ───────────────────────────────────────────────
// iOS Safari download behaviour:
// - window.open(_blank)  → opens new tab, shows blank/frozen page
// - blob fetch + a.click → downloads blob but opens external player page
// - window.location.href → navigates current page away (bad)
// - <a href download>    → ignored by iOS Safari (cross-origin)
//
// CORRECT approach for iOS Safari:
// Create a hidden <a> with the proxy URL and click it from a user gesture.
// Safari sees Content-Disposition: attachment and shows the native
// "Do you want to download?" popup WITHOUT opening a new page.
// The CORS headers on the backend allow this cross-origin.
// ── Lease contract button with inline viewer ──────────────────────────────────
function LeaseContractButton({ lease }) {
  var [show, setShow] = React.useState(false);
  var html = generateLeaseContractHtml(lease);
  return (
    <>
      {show && <ContractViewer html={html} filename={"BeatFinder_Lease_" + ((lease && (lease.beat_title || lease.id)) || "Contract").replace(/[^\w]/g, "_") + ".html"} onClose={function() { setShow(false); }} pdfDownloader={function() { return downloadLeaseContractPdf(lease); }} />}
      <button onClick={function() { setShow(true); }} style={{
        width: "100%", borderRadius: 12, padding: "11px", background: "transparent",
        border: "1px solid #2a2a2a", color: "#888", fontWeight: 700, fontSize: 13,
        cursor: "pointer", display:"flex", alignItems:"center", justifyContent:"center", gap:8,
      }}>
        <svg width="13" height="13" viewBox="0 0 24 24" fill="none" stroke="#888" strokeWidth="2" strokeLinecap="round"><path d="M14 2H6a2 2 0 0 0-2 2v16a2 2 0 0 0 2 2h12a2 2 0 0 0 2-2V8z"/><polyline points="14 2 14 8 20 8"/></svg>
        View Lease Contract
      </button>
    </>
  );
}

// ── In-app contract viewer — shows contract as scrollable overlay ─────────────
// `html` is the in-app preview (always HTML). `pdfDownloader`, when provided,
// is an async function that fetches a polished PDF from the backend — the
// Save button uses this in preference to saving the raw HTML. This is what
// gets iOS to show "Save to Files" directly in the share sheet.
function ContractViewer({ html, onClose, filename, pdfDownloader }) {
  var [saving, setSaving] = React.useState(false);

  async function handleSave() {
    if (pdfDownloader) {
      if (saving) return;
      setSaving(true);
      try { await pdfDownloader(); }
      catch (e) { alert((e && e.message) || "Save failed — please try again."); }
      finally { setSaving(false); }
      return;
    }

    // Legacy fallback — save HTML blob (used only if no pdfDownloader passed)
    var dlName = filename || "BeatFinder_Contract.html";
    var isIOS = /iPad|iPhone|iPod/.test(navigator.userAgent) ||
                (navigator.platform === "MacIntel" && navigator.maxTouchPoints > 1);
    try {
      var blob = new Blob([html], { type: "text/html" });
      var url  = URL.createObjectURL(blob);

      if (isIOS) {
        // Navigate current tab — back button returns to BeatFinder
        setTimeout(function() { URL.revokeObjectURL(url); }, 60000);
        window.location.href = url;
        return;
      }

      var a    = document.createElement("a");
      a.href     = url;
      a.download = dlName;
      a.style.display = "none";
      document.body.appendChild(a);
      a.click();
      setTimeout(function() { document.body.removeChild(a); URL.revokeObjectURL(url); }, 1000);
    } catch(e) {
      var blob2 = new Blob([html], { type: "text/html" });
      var url2  = URL.createObjectURL(blob2);
      window.location.href = url2;
    }
  }

  // Portal to document.body so we escape any ancestor with `transform`,
  // `filter`, or `perspective` (the Trending carousel is one such case).
  // Without this, position:fixed gets trapped inside the carousel column
  // and the contract appears clipped on both sides.
  var viewer = (
    <div style={{
      position: "fixed",
      top: 0, left: 0,
      width: "100vw", height: "100vh",
      // Use dvh on supported devices so iOS Safari's URL bar doesn't clip
      maxHeight: "100dvh",
      zIndex: 2147483647, // top of i32 — beats any modal/carousel
      background: "#fff", display: "flex", flexDirection: "column",
      overflow: "hidden",
    }}>
      {/* Header bar */}
      <div style={{
        background: "#0a0a0a",
        padding: "var(--bf-safe-top, env(safe-area-inset-top, 14px)) 16px 14px",
        paddingTop: "max(var(--bf-safe-top, env(safe-area-inset-top)), 14px)",
        display: "flex", alignItems: "center", justifyContent: "space-between",
        flexShrink: 0, gap: 12,
        borderBottom: "1px solid #1a1a1a",
      }}>
        {/* X close button */}
        <button
          onClick={onClose}
          style={{
            background: "rgba(255,255,255,0.08)", border: "1px solid #333",
            borderRadius: "50%", color: "#fff", cursor: "pointer",
            width: 36, height: 36, display: "flex", alignItems: "center",
            justifyContent: "center", flexShrink: 0, fontSize: 18, lineHeight: 1,
          }}
        >
          ✕
        </button>

        <div style={{ color: "white", fontWeight: 800, fontSize: 15, letterSpacing: 0.5, flex: 1, textAlign: "center" }}>
          Licence Contract
        </div>

        {/* Save to Device button */}
        <button
          onClick={handleSave}
          style={{
            background: "linear-gradient(135deg,#C026D3,#9333EA)",
            border: "none", borderRadius: 10, color: "white", cursor: "pointer",
            padding: "8px 14px", fontWeight: 800, fontSize: 12,
            display: "flex", alignItems: "center", gap: 6, flexShrink: 0,
            letterSpacing: 0.3,
          }}
        >
          <svg width="13" height="13" viewBox="0 0 24 24" fill="none" stroke="white" strokeWidth="2.5" strokeLinecap="round">
            <path d="M12 3v13M6 11l6 6 6-6"/><path d="M4 20h16"/>
          </svg>
          {saving ? "..." : "Save PDF"}
        </button>
      </div>

      {/* Scrollable contract content
         - iOS Safari can't reliably scroll content inside an iframe with
           touch. Workaround: let the iframe auto-resize to match its
           full content height, and have the PARENT div do the scrolling.
           This sidesteps the iframe-scroll limitation entirely while
           keeping the contract's HTML/CSS isolated. */}
      <div style={{
        flex: 1, width: "100%",
        background: "#fff",
        boxSizing: "border-box",
        overflowY: "scroll",
        WebkitOverflowScrolling: "touch",
        overscrollBehavior: "contain",
        // Explicit height so iOS Safari activates touch scrolling.
        // 100dvh minus header (~64px) and footer (~72px).
        height: "calc(100dvh - 136px)",
        minHeight: 200,
      }}>
        <iframe
          title="Licence Contract"
          srcDoc={html}
          sandbox="allow-same-origin"
          scrolling="no"
          onLoad={function(e){
            // Auto-resize iframe to content height so parent scrolls naturally.
            try {
              var ifr = e.currentTarget;
              var doc = ifr.contentDocument || ifr.contentWindow.document;
              if (doc) {
                var h = Math.max(
                  doc.body ? doc.body.scrollHeight : 0,
                  doc.documentElement ? doc.documentElement.scrollHeight : 0
                );
                if (h > 0) ifr.style.height = h + "px";
              }
            } catch(err) {}
          }}
          style={{
            width: "100%",
            border: "none", display: "block",
            background: "#fff",
            // Default height before onLoad fires — overwritten by the
            // resize logic. Generous enough to avoid initial clipping.
            height: 900,
          }}
        />
      </div>

      {/* Bottom action bar */}
      <div style={{
        background: "#0a0a0a", borderTop: "1px solid #1a1a1a",
        padding: "12px 16px",
        paddingBottom: "max(env(safe-area-inset-bottom), 16px)",
        display: "flex", gap: 10, flexShrink: 0,
      }}>
        <button
          onClick={handleSave}
          disabled={saving}
          style={{
            flex: 1, borderRadius: 14, padding: "13px",
            background: saving ? "#333" : "linear-gradient(135deg,#C026D3,#9333EA)",
            border: "none", color: "white", fontWeight: 800, fontSize: 14,
            cursor: saving ? "not-allowed" : "pointer", display: "flex", alignItems: "center",
            justifyContent: "center", gap: 8,
          }}
        >
          <svg width="15" height="15" viewBox="0 0 24 24" fill="none" stroke="white" strokeWidth="2.5" strokeLinecap="round">
            <path d="M12 3v13M6 11l6 6 6-6"/><path d="M4 20h16"/>
          </svg>
          {saving ? "Generating PDF..." : "Save PDF to Device"}
        </button>
        <button
          onClick={onClose}
          style={{
            borderRadius: 14, padding: "13px 18px",
            background: "transparent", border: "1px solid #333",
            color: "#888", fontWeight: 700, fontSize: 13, cursor: "pointer",
          }}
        >
          Close
        </button>
      </div>
    </div>
  );

  // Render via portal to document.body so the viewer is laid out against the
  // real viewport, never a transformed/clipped ancestor. Falls back to inline
  // render on the (unlikely) SSR pass where document is undefined.
  if (typeof document === "undefined" || !document.body || !ReactDOM.createPortal) {
    return viewer;
  }
  return ReactDOM.createPortal(viewer, document.body);
}

// ── Backend-generated PDF contracts ───────────────────────────────────────────
// Real application/pdf with Content-Disposition: attachment from the server —
// iOS Safari treats this as a proper PDF file in the share sheet, so the user
// sees "Save to Files" immediately without having to change Send As → PDF in
// the Options submenu. Avoids the HTML/Web Archive trap.
async function fetchContractPdf(endpoint, options) {
  options = options || {};
  var token = getToken();
  var headers = Object.assign({}, options.headers || {});
  if (token) headers.Authorization = "Bearer " + token;
  if (options.method && options.method.toUpperCase() === "POST" && !headers["Content-Type"]) {
    headers["Content-Type"] = "application/json";
  }
  var res = await fetch(API_BASE + endpoint, {
    method:  options.method || "GET",
    headers: headers,
    body:    options.body,
  });
  if (!res.ok) {
    var msg = "Contract download failed";
    try { var j = await res.json(); msg = j.detail || msg; } catch(e) {}
    throw new Error(msg);
  }
  return await res.blob();
}

async function triggerBlobDownload(blob, filename) {
  // PDF download on iOS is tricky because:
  //   - <a download> is ignored on iOS Safari (navigates the tab to the blob,
  //     stranding the user on a PDF viewer page)
  //   - window.open(blobUrl) needs a user gesture; we lost it during the
  //     async fetch
  //   - The cleanest path is the Web Share API with a File, which works even
  //     when popups are blocked because it goes through the OS share sheet
  filename = filename || "BeatFinder_Contract.pdf";
  var isIOS = /iPad|iPhone|iPod/.test(navigator.userAgent) ||
              (navigator.platform === "MacIntel" && navigator.maxTouchPoints > 1);

  // STRATEGY 1 (iOS): Web Share with a File. iOS shows the native share sheet
  // with "Save to Files" as a primary action. CRUCIALLY this does not require
  // Block Pop-ups to be disabled — share sheet is treated as a system UI,
  // not a popup. So even users with Block Pop-ups on can save the contract.
  if (isIOS && navigator.canShare && typeof File === "function") {
    try {
      var file = new File([blob], filename, { type: "application/pdf" });
      if (navigator.canShare({ files: [file] })) {
        try {
          await navigator.share({ files: [file], title: filename });
          return;
        } catch (e) {
          // AbortError = user dismissed the share sheet without picking
          // an action. Treat as cancel and stop here (do NOT fall through
          // to a path that would navigate the current tab).
          if (e && (e.name === "AbortError" ||
                    String(e.message || "").toLowerCase().indexOf("abort") !== -1 ||
                    String(e.message || "").toLowerCase().indexOf("cancel") !== -1)) {
            return;
          }
          // Real error — fall through to the final fallback below
        }
      }
    } catch (e) { /* File construction failed, fall through */ }
  }

  // STRATEGY 2 (Android / desktop / non-iOS browsers): standard <a download>
  if (!isIOS) {
    var url = URL.createObjectURL(blob);
    var a = document.createElement("a");
    a.href     = url;
    a.download = filename;
    a.style.display = "none";
    document.body.appendChild(a);
    a.click();
    setTimeout(function() {
      try { document.body.removeChild(a); } catch (e) {}
      URL.revokeObjectURL(url);
    }, 1000);
    return;
  }

  // STRATEGY 3 (iOS fallback when Web Share unavailable): navigate the
  // CURRENT tab to the blob URL. iOS will render the PDF in its native
  // viewer with built-in share/save controls. Back button returns to
  // BeatFinder. We avoid window.open entirely so the "attempting to open
  // pop-up" prompt never fires.
  var url2 = URL.createObjectURL(blob);
  setTimeout(function() { URL.revokeObjectURL(url2); }, 60000);
  window.location.href = url2;
}

async function downloadFreeContractPdf(beat, user) {
  try {
    var safeTitle = (beat.title || "Beat").replace(/[^\w]/g, "_");
    var blob = await fetchContractPdf("/api/contracts/free", {
      method: "POST",
      body:   JSON.stringify({
        beat_id:    beat.id || null,
        beat_title: beat.title || "Beat",
        producer:   beat.producer || beat.producer_username || "Producer",
      }),
    });
    await triggerBlobDownload(blob, "BeatFinder_FreeLicence_" + safeTitle + ".pdf");
  } catch (e) {
    alert(e.message || "Could not download contract — please try again.");
  }
}

async function downloadLeaseContractPdf(lease) {
  try {
    if (!lease || !lease.id) {
      alert("This lease is missing its server record — please refresh your purchases and try again.");
      return;
    }
    var safeTitle = (lease.beat_title || "Beat").replace(/[^\w]/g, "_");
    var blob = await fetchContractPdf("/api/contracts/lease/" + lease.id, { method: "GET" });
    await triggerBlobDownload(blob, "BeatFinder_Lease_" + safeTitle + ".pdf");
  } catch (e) {
    alert(e.message || "Could not download contract — please try again.");
  }
}

function generateFreeLeaseContractHtml(beat, user) {
  var date     = new Date().toLocaleDateString("en-GB", { day: "numeric", month: "long", year: "numeric" });
  var leaseId  = "BF-FREE-" + Date.now();
  var artist   = (user && (user.name || user.username)) || "Artist";
  var email    = (user && user.email) || "";
  var producer = beat.producer || beat.producer_username || "Producer";
  var beatTitle = beat.title || "Beat";
  return `<!DOCTYPE html>
<html lang="en">
<head>
<meta charset="UTF-8"/>
<title>BeatFinder Free Licence — ${beatTitle}</title>
<style>body{font-family:Georgia,serif;padding:40px;max-width:800px;margin:0 auto;font-size:14px;line-height:1.8}.header{text-align:center;border-bottom:3px solid #111;padding-bottom:20px;margin-bottom:28px}.logo{font-size:26px;font-weight:900;letter-spacing:3px}.logo span{color:#C026D3}.parties{background:#f8f8f8;border:1px solid #ddd;border-radius:8px;padding:16px 20px;margin-bottom:24px}h2{font-size:12px;font-weight:bold;letter-spacing:1px;text-transform:uppercase;margin:20px 0 8px;border-bottom:1px solid #ddd;padding-bottom:4px;color:#333}.footer{margin-top:40px;padding-top:14px;border-top:1px solid #ddd;font-size:10px;color:#999;text-align:center}</style>
</head>
<body>
<div class="header"><div class="logo">BEAT<span>FINDER</span></div><div style="font-size:11px;letter-spacing:2px;color:#555;margin-top:4px;text-transform:uppercase">beatfinder.co.uk — Free Licence</div></div>
<h1 style="text-align:center;font-size:18px">FREE NON-EXCLUSIVE BEAT LICENCE</h1>
<div style="text-align:center;font-size:11px;color:#888;margin-bottom:28px">Reference: ${leaseId} | Date: ${date}</div>
<div class="parties">
<div><strong>Beat:</strong> "${beatTitle}"</div>
<div><strong>Producer:</strong> ${producer}</div>
<div><strong>Licensee:</strong> ${artist}${email ? " (" + email + ")" : ""}</div>
<div><strong>Type:</strong> FREE Non-Exclusive | <strong>Fee:</strong> £0.00</div>
</div>
<h2>1. Grant</h2><p>Producer grants Licensee a free, non-exclusive, non-transferable licence for non-commercial use only.</p>
<h2>2. Permitted Uses</h2><p>Record one song; share on free platforms (SoundCloud, YouTube non-monetised); social media promotion; non-ticketed live performance. Must credit: <em>(Prod. by ${producer})</em></p>
<h2>3. Restrictions</h2><p>No monetisation, no selling recordings, no sub-licensing, no copyright claim on the Beat. If recording exceeds 10,000 streams or generates revenue, a paid lease must be purchased immediately.</p>
<h2>4. Ownership</h2><p>Producer retains full copyright of the Beat. Licensee owns their original vocals/lyrics only.</p>
<h2>5. Platform</h2><p>BeatFinder (beatfinder.co.uk) facilitates this licence and is not liable for disputes between parties.</p>
<h2>6. Governing Law</h2><p>England and Wales.</p>
<div style="margin-top:40px;display:flex;gap:40px"><div style="flex:1;border-top:1px solid #111;padding-top:10px"><div style="font-size:11px;color:#555;text-transform:uppercase">Producer</div><strong>${producer}</strong></div><div style="flex:1;border-top:1px solid #111;padding-top:10px"><div style="font-size:11px;color:#555;text-transform:uppercase">Licensee</div><strong>${artist}</strong></div></div>
<div class="footer">Generated by BeatFinder | ${date} | Reference: ${leaseId}<br>FREE NON-COMMERCIAL LICENCE ONLY © ${new Date().getFullYear()} BeatFinder</div>
</body></html>`;
}

function generateFreeLeaseContract(beat, user) {
  // Now delegates to the backend PDF endpoint. Returns the promise so callers
  // can await it; existing call sites that don't await are fine because the
  // function still triggers the download asynchronously.
  return downloadFreeContractPdf(beat, user);
}

function generateLeaseContractHtml(lease) {
  var date = lease.purchased_at
    ? new Date(lease.purchased_at).toLocaleDateString("en-GB", { day: "numeric", month: "long", year: "numeric" })
    : new Date().toLocaleDateString("en-GB", { day: "numeric", month: "long", year: "numeric" });
  var leaseId = lease.id || ("BF-" + Date.now());
  var artist   = lease.buyer_name || lease.buyer_username || "Artist";
  var producer = lease.producer || "Producer";
  var beatTitle = lease.beat_title || "Beat";
  var price    = lease.price || "";

  var html = `<!DOCTYPE html>
<html lang="en">
<head>
<meta charset="UTF-8"/>
<meta name="viewport" content="width=device-width, initial-scale=1.0"/>
<title>BeatFinder Lease Agreement — ${beatTitle}</title>
<style>
  * { margin: 0; padding: 0; box-sizing: border-box; }
  body { font-family: Georgia, serif; background: #fff; color: #111; padding: 60px 80px; max-width: 860px; margin: 0 auto; font-size: 14px; line-height: 1.8; }
  .header { text-align: center; border-bottom: 3px solid #111; padding-bottom: 24px; margin-bottom: 32px; }
  .logo { font-size: 28px; font-weight: 900; letter-spacing: 3px; color: #111; font-family: Arial, sans-serif; }
  .logo span { color: #C026D3; }
  .subtitle { font-size: 11px; letter-spacing: 2px; color: #555; margin-top: 4px; text-transform: uppercase; }
  h1 { font-size: 20px; margin: 24px 0 8px; text-align: center; letter-spacing: 1px; }
  .lease-id { text-align: center; font-size: 11px; color: #888; margin-bottom: 32px; letter-spacing: 1px; }
  .parties { background: #f8f8f8; border: 1px solid #ddd; border-radius: 8px; padding: 20px 24px; margin-bottom: 28px; }
  .party-row { display: flex; gap: 32px; margin-bottom: 12px; }
  .party-row:last-child { margin-bottom: 0; }
  .party-label { font-weight: bold; min-width: 120px; font-size: 12px; text-transform: uppercase; letter-spacing: 0.5px; color: #555; }
  .party-value { color: #111; }
  h2 { font-size: 13px; font-weight: bold; letter-spacing: 1px; text-transform: uppercase; margin: 24px 0 10px; border-bottom: 1px solid #ddd; padding-bottom: 6px; color: #333; }
  p { margin-bottom: 12px; }
  .clause { margin-bottom: 16px; }
  .clause-num { font-weight: bold; color: #111; }
  .signature-block { margin-top: 48px; display: flex; gap: 48px; }
  .sig-box { flex: 1; border-top: 1px solid #111; padding-top: 12px; }
  .sig-label { font-size: 11px; text-transform: uppercase; letter-spacing: 1px; color: #555; }
  .sig-name { font-size: 13px; margin-top: 4px; font-weight: bold; }
  .footer { margin-top: 48px; padding-top: 16px; border-top: 1px solid #ddd; font-size: 10px; color: #999; text-align: center; line-height: 1.6; }
  @media print { body { padding: 40px; } }
</style>
</head>
<body>
<div class="header">
  <div class="logo">BEAT<span>FINDER</span></div>
  <div class="subtitle">beatfinder.co.uk — Music Licensing Platform</div>
</div>

<h1>NON-EXCLUSIVE BEAT LEASE AGREEMENT</h1>
<div class="lease-id">Lease Reference: ${leaseId} &nbsp;|&nbsp; Date: ${date}</div>

<div class="parties">
  <div class="party-row"><span class="party-label">Beat Title</span><span class="party-value">"${beatTitle}"</span></div>
  <div class="party-row"><span class="party-label">Producer</span><span class="party-value">${producer}</span></div>
  <div class="party-row"><span class="party-label">Licensee (Artist)</span><span class="party-value">${artist}${lease.buyer_email ? " (" + lease.buyer_email + ")" : ""}</span></div>
  <div class="party-row"><span class="party-label">Licence Type</span><span class="party-value">Non-Exclusive MP3 Lease</span></div>
  <div class="party-row"><span class="party-label">Licence Fee</span><span class="party-value">${price}</span></div>
  <div class="party-row"><span class="party-label">Platform</span><span class="party-value">BeatFinder (beatfinder.co.uk)</span></div>
</div>

<h2>1. Grant of Licence</h2>
<div class="clause">
<span class="clause-num">1.1</span> The Producer hereby grants the Licensee a non-exclusive, non-transferable licence to use the above-named musical composition (the "Beat") for the purposes outlined in this Agreement, subject to the terms and conditions herein.
</div>
<div class="clause">
<span class="clause-num">1.2</span> This licence is facilitated through BeatFinder (beatfinder.co.uk), acting as an intermediary platform. BeatFinder is not a party to the creative ownership of the Beat but administers the transaction and licensing process.
</div>

<h2>2. Permitted Uses</h2>
<div class="clause">
<span class="clause-num">2.1</span> The Licensee may use the Beat for the following purposes:<br/>
&nbsp;&nbsp;&nbsp;(a) Recording a single song or project using the Beat as an instrumental;<br/>
&nbsp;&nbsp;&nbsp;(b) Distributing the finished recording on streaming platforms (Spotify, Apple Music, YouTube, etc.);<br/>
&nbsp;&nbsp;&nbsp;(c) Performing the finished recording live;<br/>
&nbsp;&nbsp;&nbsp;(d) Promoting the finished recording on social media.
</div>
<div class="clause">
<span class="clause-num">2.2</span> The Licensee must credit the Producer in all releases using the format: <em>(Prod. by ${producer})</em>
</div>

<h2>3. Restrictions</h2>
<div class="clause">
<span class="clause-num">3.1</span> This is a <strong>Non-Exclusive</strong> licence. The Producer retains the right to licence the same Beat to other artists simultaneously.
</div>
<div class="clause">
<span class="clause-num">3.2</span> The Licensee may <strong>not</strong>:<br/>
&nbsp;&nbsp;&nbsp;(a) Sell, sub-licence, transfer, or assign this licence or the Beat to any third party;<br/>
&nbsp;&nbsp;&nbsp;(b) Claim ownership or copyright of the Beat or its underlying composition;<br/>
&nbsp;&nbsp;&nbsp;(c) Register the Beat with any copyright collection society (PRS, BMI, ASCAP, etc.) as a standalone composition;<br/>
&nbsp;&nbsp;&nbsp;(d) Use the Beat in audio-visual synchronisation (TV, film, adverts) without a separate sync licence;<br/>
&nbsp;&nbsp;&nbsp;(e) Use the Beat for sampling purposes in another Beat or instrumental.
</div>
<div class="clause">
<span class="clause-num">3.3</span> Commercial use (monetised releases exceeding 100,000 streams or generating revenue exceeding £5,000) requires an upgrade to an Exclusive or Commercial licence. The Licensee agrees to contact the Producer via BeatFinder to renegotiate terms if these thresholds are reached.
</div>

<h2>4. Ownership & Copyright</h2>
<div class="clause">
<span class="clause-num">4.1</span> The Producer retains full copyright ownership of the Beat. This Agreement does not transfer any ownership rights to the Licensee.
</div>
<div class="clause">
<span class="clause-num">4.2</span> The Licensee owns the copyright in the vocals, lyrics, and any original creative contribution added to the finished recording, but not in the underlying Beat.
</div>
<div class="clause">
<span class="clause-num">4.3</span> The finished recording shall be considered a joint work for royalty purposes. The Producer is entitled to 50% of the composition royalties (writer's share) for any performance or mechanical royalties generated by the finished recording.
</div>

<h2>5. BeatFinder Platform Terms</h2>
<div class="clause">
<span class="clause-num">5.1</span> BeatFinder (beatfinder.co.uk) acts solely as a marketplace facilitating the transaction between the Producer and the Licensee. BeatFinder is not responsible for disputes between parties arising from the use of this licence.
</div>
<div class="clause">
<span class="clause-num">5.2</span> BeatFinder retains a platform fee from each transaction in accordance with its standard fee schedule. This fee is non-refundable.
</div>
<div class="clause">
<span class="clause-num">5.3</span> BeatFinder reserves the right to remove content from its platform that violates its Terms of Service. Such removal does not invalidate existing licences already granted.
</div>
<div class="clause">
<span class="clause-num">5.4</span> By completing this purchase through BeatFinder, both parties agree to BeatFinder's Terms of Service available at beatfinder.co.uk.
</div>

<h2>6. Representations & Warranties</h2>
<div class="clause">
<span class="clause-num">6.1</span> The Producer warrants that they are the sole creator and copyright owner of the Beat and have the full right to grant this licence.
</div>
<div class="clause">
<span class="clause-num">6.2</span> The Licensee warrants that they will use the Beat in compliance with all applicable laws and will not use it for any unlawful, defamatory, or harmful purpose.
</div>

<h2>7. Limitation of Liability</h2>
<div class="clause">
<span class="clause-num">7.1</span> To the fullest extent permitted by law, BeatFinder shall not be liable for any indirect, incidental, or consequential damages arising from the use of this licence or the Beat.
</div>
<div class="clause">
<span class="clause-num">7.2</span> BeatFinder's total liability in any matter related to this Agreement shall not exceed the licence fee paid.
</div>

<h2>8. Termination</h2>
<div class="clause">
<span class="clause-num">8.1</span> This licence terminates immediately and automatically if the Licensee breaches any term of this Agreement.
</div>
<div class="clause">
<span class="clause-num">8.2</span> Upon termination, the Licensee must cease all use of the Beat and remove any published recordings using the Beat.
</div>

<h2>9. Governing Law</h2>
<div class="clause">
<span class="clause-num">9.1</span> This Agreement shall be governed by and construed in accordance with the laws of England and Wales. Any disputes shall be subject to the exclusive jurisdiction of the courts of England and Wales.
</div>

<div class="signature-block">
  <div class="sig-box">
    <div class="sig-label">Producer / Rights Holder</div>
    <div class="sig-name">${producer}</div>
    <div style="margin-top:8px;font-size:11px;color:#888;">Via BeatFinder Platform</div>
  </div>
  <div class="sig-box">
    <div class="sig-label">Licensee (Artist)</div>
    <div class="sig-name">${artist}</div>
    <div style="margin-top:8px;font-size:11px;color:#888;">${lease.buyer_email || ""}</div>
  </div>
  <div class="sig-box">
    <div class="sig-label">Platform Administrator</div>
    <div class="sig-name">BeatFinder</div>
    <div style="margin-top:8px;font-size:11px;color:#888;">beatfinder.co.uk</div>
  </div>
</div>

<div class="footer">
  This licence agreement was generated automatically by BeatFinder (beatfinder.co.uk) upon successful payment.<br/>
  Lease Reference: ${leaseId} &nbsp;|&nbsp; Transaction Date: ${date}<br/>
  BeatFinder is a music licensing platform. This document constitutes a binding legal agreement between the parties named above.<br/>
  © ${new Date().getFullYear()} BeatFinder. All rights reserved.
</div>
</body>
</html>`;

  return html;
}

function generateLeaseContract(lease) {
  var html = generateLeaseContractHtml(lease);
  var beatTitle = lease.beat_title || "Beat";
  var leaseId = lease.id || ("BF-" + Date.now());
  var blob = new Blob([html], { type: "text/html" });
  var url  = URL.createObjectURL(blob);
  var a    = document.createElement("a");
  a.href     = url;
  a.download = "BeatFinder_Lease_" + beatTitle.replace(/[^\w]/g, "_") + "_" + leaseId + ".html";
  a.style.display = "none";
  document.body.appendChild(a);
  a.click();
  setTimeout(function() { document.body.removeChild(a); URL.revokeObjectURL(url); }, 1000);
}

// ── Download status toast (global) ─────────────────────────────────
// Lightweight observable that the smart download helper signals into.
// A `<DownloadToast />` mounted near the root renders the visual.
var __downloadToastListeners = [];
function __setDownloadToast(state) {
  __downloadToastListeners.forEach(function(fn) { try { fn(state); } catch (e) {} });
}
function useDownloadToast() {
  var [state, setState] = React.useState(null); // null | {status,title,progress}
  React.useEffect(function() {
    __downloadToastListeners.push(setState);
    return function() {
      __downloadToastListeners = __downloadToastListeners.filter(function(fn) { return fn !== setState; });
    };
  }, []);
  return state;
}

function DownloadToast() {
  var state = useDownloadToast();
  if (!state) return null;
  var status = state.status; // "downloading" | "success" | "error"

  // Sleek black card with neon accent based on state
  var borderColor, glowColor, icon, text;
  if (status === "downloading") {
    borderColor = "#A855F7";
    glowColor   = "rgba(168,85,247,0.55)";
    icon = (
      <div style={{
        width: 32, height: 32, borderRadius: "50%",
        background: "linear-gradient(135deg, #A855F7 0%, #6366F1 100%)",
        display: "flex", alignItems: "center", justifyContent: "center",
        boxShadow: "0 0 12px rgba(168,85,247,0.6), 0 0 6px rgba(99,102,241,0.5)",
        flexShrink: 0,
      }}>
        <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="white" strokeWidth="2.5" strokeLinecap="round"
          style={{ animation: "bf-spin 1s linear infinite" }}>
          <path d="M21 12a9 9 0 1 1-6.219-8.56"/>
        </svg>
      </div>
    );
    text = "Downloading " + (state.title || "beat");
  } else if (status === "success") {
    borderColor = "#3B82F6";
    glowColor   = "rgba(59,130,246,0.55)";
    icon = (
      <div style={{
        width: 32, height: 32, borderRadius: "50%",
        background: "linear-gradient(135deg, #6366F1 0%, #3B82F6 100%)",
        display: "flex", alignItems: "center", justifyContent: "center",
        boxShadow: "0 0 12px rgba(59,130,246,0.6), 0 0 6px rgba(99,102,241,0.5)",
        flexShrink: 0,
      }}>
        <svg width="17" height="17" viewBox="0 0 24 24" fill="none" stroke="white" strokeWidth="3" strokeLinecap="round">
          <polyline points="20 6 9 17 4 12"/>
        </svg>
      </div>
    );
    text = "Downloaded";
  } else {
    borderColor = "#EF4444";
    glowColor   = "rgba(239,68,68,0.45)";
    icon = (
      <div style={{
        width: 32, height: 32, borderRadius: "50%",
        background: "linear-gradient(135deg, #EF4444 0%, #B91C1C 100%)",
        display: "flex", alignItems: "center", justifyContent: "center",
        boxShadow: "0 0 12px rgba(239,68,68,0.5)",
        flexShrink: 0,
      }}>
        <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="white" strokeWidth="2.5" strokeLinecap="round">
          <circle cx="12" cy="12" r="10"/>
          <line x1="12" y1="8" x2="12" y2="12"/>
          <line x1="12" y1="16" x2="12.01" y2="16"/>
        </svg>
      </div>
    );
    text = state.message || "Download failed";
  }

  var toast = (
    <>
      <style>{LOADER_STYLE + "@keyframes bf-toast-in{from{transform:translate(-50%,-20px);opacity:0}to{transform:translate(-50%,0);opacity:1}}@keyframes bf-glow-pulse{0%,100%{box-shadow:0 8px 32px rgba(0,0,0,0.6),0 0 0 1px " + borderColor + "44,0 0 20px " + glowColor + "}50%{box-shadow:0 8px 32px rgba(0,0,0,0.6),0 0 0 1px " + borderColor + "66,0 0 32px " + glowColor + "}}"}</style>
      <div style={{
        position: "fixed",
        top: "max(var(--bf-safe-top, env(safe-area-inset-top)), 12px)",
        left: "50%",
        transform: "translateX(-50%)",
        zIndex: 2147483646,
        background: "linear-gradient(135deg, #0a0a0a 0%, #0f0f12 100%)",
        color: "white",
        padding: status === "downloading" ? "12px 16px 14px" : "12px 18px",
        borderRadius: 20,
        border: "1px solid " + borderColor + "55",
        boxShadow: "0 8px 32px rgba(0,0,0,0.6), 0 0 0 1px " + borderColor + "44, 0 0 20px " + glowColor,
        animation: "bf-toast-in 0.28s cubic-bezier(0.22,1,0.36,1)" + (status === "downloading" ? ", bf-glow-pulse 2s ease-in-out infinite" : ""),
        minWidth: 260, maxWidth: "calc(100vw - 32px)",
        pointerEvents: "none",
      }}>
        <div style={{ display: "flex", alignItems: "center", gap: 12 }}>
          {icon}
          <div style={{ flex: 1, minWidth: 0 }}>
            <div style={{
              fontWeight: 700, fontSize: 13, letterSpacing: 0.2,
              overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap",
            }}>
              {text}
            </div>
            {status === "downloading" && (
              <div style={{ color: "#666", fontSize: 10.5, marginTop: 2 }}>
                Please wait
              </div>
            )}
          </div>
        </div>
        {status === "downloading" && (
          <div style={{ marginTop: 10 }}>
            <div className="bf-progress-track">
              <div className="bf-progress-indet" />
            </div>
          </div>
        )}
      </div>
    </>
  );
  if (typeof document === "undefined" || !document.body || !ReactDOM.createPortal) {
    return toast;
  }
  return ReactDOM.createPortal(toast, document.body);
}

// ─────────────────────────────────────────────────────────────────────
// ONLINE PRESENCE INDICATOR
// ─────────────────────────────────────────────────────────────────────
// Tiny green pulsing dot shown next to a user's name/avatar to signal
// they were active in the last 2 minutes. The "is_online" flag comes
// from the backend, calculated from last_seen_at.
//
// This is Deploy 1 of 3 — component only, not used anywhere yet.
// Deploy 2 will add a heartbeat pinger + show the dot in the admin
// Users panel. Deploy 3 will extend to other surfaces (profile,
// search, followers/following). Each step verified before the next.
function OnlineDot({ size = 9, label = false, style = {} }) {
  var dotSize = Math.max(6, size);
  return (
    <span style={Object.assign({
      display: "inline-flex", alignItems: "center", gap: label ? 6 : 0,
      verticalAlign: "middle",
    }, style)}>
      <span style={{
        display: "inline-block",
        width: dotSize, height: dotSize, borderRadius: "50%",
        background: "#22C55E",
        boxShadow: "0 0 0 2px rgba(34,197,94,0.18), 0 0 6px rgba(34,197,94,0.6)",
        animation: "bf-pulse-dot 1.8s ease-in-out infinite",
      }} />
      {label && (
        <span style={{ color: "#22C55E", fontSize: 11, fontWeight: 700, letterSpacing: 0.3 }}>
          Online now
        </span>
      )}
    </span>
  );
}

// ─────────────────────────────────────────────────────────────────────
// SHARED LOADING UI HELPERS
// ─────────────────────────────────────────────────────────────────────
// Tiny spinner + button wrapper to standardise the loading states
// across async actions (buy, subscribe, follow, upload, etc.). Using
// these avoids "user taps button → nothing happens for 2s → user
// taps again → duplicate request → bug" patterns.
function BfSpinner({ size = 16, color = "currentColor", strokeWidth = 2 }) {
  return (
    <svg width={size} height={size} viewBox="0 0 24 24"
      style={{ animation: "bf-spin 0.7s linear infinite", display: "inline-block", verticalAlign: "middle" }}>
      <circle cx="12" cy="12" r="9"
        fill="none" stroke={color} strokeOpacity="0.25"
        strokeWidth={strokeWidth} />
      <path d="M12 3 a9 9 0 0 1 9 9"
        fill="none" stroke={color}
        strokeWidth={strokeWidth} strokeLinecap="round" />
    </svg>
  );
}

// Drop-in button that handles its own loading state for async handlers.
// Usage:
//   <BfButton onClick={async () => await something()}>Save</BfButton>
//
// While the handler is in-flight, the button is disabled, shows a
// spinner, and is visually dimmed. Double-clicks during in-flight state
// are no-ops. Falls back to a regular button if onClick is synchronous.
function BfButton({
  children,
  onClick,
  loadingText,
  disabled,
  style,
  type = "button",
  ...rest
}) {
  var [busy, setBusy] = React.useState(false);

  async function handle(e) {
    if (busy || disabled || !onClick) return;
    var result;
    try {
      result = onClick(e);
    } catch (err) {
      // Sync throw — let it bubble
      throw err;
    }
    // Detect async handlers via duck-typing on the returned promise
    if (result && typeof result.then === "function") {
      setBusy(true);
      try {
        await result;
      } catch (err) {
        // Swallow — the handler should have surfaced this itself via
        // bfToast / inline error state. Re-throwing here would crash
        // the React tree, defeating the safety net.
        try {
          if (window.bfToast && err && err.message) {
            window.bfToast.error(err.message);
          }
        } catch (_) {}
      } finally {
        setBusy(false);
      }
    }
  }

  var mergedStyle = Object.assign({
    cursor: (busy || disabled) ? "not-allowed" : "pointer",
    opacity: (busy || disabled) ? 0.6 : 1,
    transition: "opacity 0.15s ease",
    display: "inline-flex",
    alignItems: "center",
    justifyContent: "center",
    gap: 8,
  }, style || {});

  return (
    <button type={type} onClick={handle} disabled={busy || disabled} style={mergedStyle} {...rest}>
      {busy ? (
        <>
          <BfSpinner size={14} />
          <span>{loadingText || "Loading..."}</span>
        </>
      ) : children}
    </button>
  );
}

// ─────────────────────────────────────────────────────────────────────
// GLOBAL TOAST NOTIFICATION SYSTEM
// ─────────────────────────────────────────────────────────────────────
// One-shot success / error / info messages. Mount <BfToastHost /> once at
// the app root, then anywhere in the codebase call:
//
//   window.bfToast.success("Saved!");
//   window.bfToast.error("Couldn't save");
//   window.bfToast.info("Heads up — this beat has a premium licence");
//
// Toasts auto-dismiss after ~4s, tap-to-dismiss, stack top-right.
// Designed to gradually replace scattered alert()/setBioMsg patterns
// without forcing a big-bang migration — existing patterns keep working.
//
// Subscribers (BfToastHost) listen on the bf:toast custom event. The
// queue lives entirely in state; no localStorage involved.

(function installBfToast() {
  if (typeof window === "undefined" || window.bfToast) return;
  function emit(kind, message, opts) {
    try {
      var ev = new CustomEvent("bf:toast", {
        detail: {
          kind:     kind,        // "success" | "error" | "info"
          message:  String(message || ""),
          duration: (opts && opts.duration) || 4000,
          id:       Math.random().toString(36).slice(2) + Date.now(),
        }
      });
      window.dispatchEvent(ev);
    } catch (_) {}
  }
  window.bfToast = {
    success: function(msg, opts) { emit("success", msg, opts); },
    error:   function(msg, opts) { emit("error",   msg, opts); },
    info:    function(msg, opts) { emit("info",    msg, opts); },
  };
})();

function BfToastHost() {
  var [toasts, setToasts] = React.useState([]);

  React.useEffect(function() {
    function onToast(e) {
      var t = e.detail || {};
      if (!t.message) return;
      setToasts(function(curr) { return curr.concat([t]); });
      // Schedule dismissal
      setTimeout(function() {
        setToasts(function(curr) { return curr.filter(function(x) { return x.id !== t.id; }); });
      }, t.duration || 4000);
    }
    window.addEventListener("bf:toast", onToast);
    return function() { window.removeEventListener("bf:toast", onToast); };
  }, []);

  if (toasts.length === 0) return null;

  function dismiss(id) {
    setToasts(function(curr) { return curr.filter(function(x) { return x.id !== id; }); });
  }

  var host = (
    <div style={{
      position: "fixed",
      top: "calc(var(--bf-safe-top, env(safe-area-inset-top)) + 12px)",
      right: 12,
      zIndex: 99997,  // below auth-expired (99998), above app
      display: "flex", flexDirection: "column", gap: 8,
      pointerEvents: "none",  // wrapper passes through; cards re-enable
      maxWidth: "calc(100vw - 24px)",
      fontFamily: "'DM Sans',sans-serif",
    }}>
      {toasts.map(function(t) {
        var palette = (t.kind === "error") ? {
          border: "rgba(239,68,68,0.4)",
          glow:   "rgba(239,68,68,0.2)",
          accent: "#F87171",
          bg:     "linear-gradient(135deg,#1a0a14,#0d0a14)",
          icon:   "!",
        } : (t.kind === "success") ? {
          border: "rgba(34,197,94,0.4)",
          glow:   "rgba(34,197,94,0.2)",
          accent: "#22C55E",
          bg:     "linear-gradient(135deg,#0a1a12,#0a140d)",
          icon:   "✓",
        } : {
          border: "rgba(124,58,237,0.4)",
          glow:   "rgba(124,58,237,0.2)",
          accent: "#A78BFA",
          bg:     "linear-gradient(135deg,#100a1a,#0a0a14)",
          icon:   "i",
        };
        return (
          <div
            key={t.id}
            onClick={function() { dismiss(t.id); }}
            style={{
              background:    palette.bg,
              border:        "1px solid " + palette.border,
              borderRadius:  12,
              padding:       "12px 16px",
              minWidth:      240,
              maxWidth:      360,
              color:         "white",
              fontSize:      13,
              lineHeight:    1.4,
              boxShadow:     "0 8px 24px rgba(0,0,0,0.6), 0 0 16px " + palette.glow,
              display:       "flex",
              alignItems:    "center",
              gap:           10,
              cursor:        "pointer",
              pointerEvents: "auto",
              animation:     "bf-fadein-up 0.22s cubic-bezier(0.22,1,0.36,1) both",
            }}>
            <div style={{
              flex: "0 0 auto", width: 22, height: 22, borderRadius: "50%",
              background: "rgba(255,255,255,0.05)",
              border: "1px solid " + palette.border,
              display: "flex", alignItems: "center", justifyContent: "center",
              color: palette.accent, fontWeight: 900, fontSize: 12,
            }}>{palette.icon}</div>
            <div style={{ flex: 1, wordBreak: "break-word" }}>{t.message}</div>
            <div style={{ color: "#555", fontSize: 14, flex: "0 0 auto" }}>×</div>
          </div>
        );
      })}
    </div>
  );

  if (typeof document === "undefined" || !document.body) return host;
  return ReactDOM.createPortal(host, document.body);
}

// ── Popup-blocker explainer modal ──────────────────────────────────
// Some users have Safari → Settings → "Block Pop-ups" enabled, which
// silently kills the new-tab navigation we use for MP3 downloads.
// When we detect this we show a friendly modal with two paths forward:
// 1) Tap a button to retry the download in-place (within the same gesture),
//    which often works since the click handler is still active.
// 2) Step-by-step instructions for turning the setting off.
var __popupBlockedModalListeners = [];
function __setPopupBlockedModal(state) {
  __popupBlockedModalListeners.forEach(function(fn) { try { fn(state); } catch (e) {} });
}
function usePopupBlockedModal() {
  var [state, setState] = React.useState(null);
  React.useEffect(function() {
    __popupBlockedModalListeners.push(setState);
    return function() {
      __popupBlockedModalListeners = __popupBlockedModalListeners.filter(function(fn) { return fn !== setState; });
    };
  }, []);
  return [state, setState];
}

function PopupBlockedModal() {
  var [state, setState] = usePopupBlockedModal();
  if (!state) return null;

  function close() {
    setState(null);
    __setPopupBlockedModal(null);
    if (state.blobUrl) {
      try { URL.revokeObjectURL(state.blobUrl); } catch (e) {}
    }
  }

  function retryDownload() {
    // This click is a fresh user gesture, so window.open should succeed
    // even with popup blocker active (in many cases Safari permits the
    // popup when it comes from a direct tap).
    var win = window.open(state.blobUrl, "_blank");
    if (win && !win.closed) {
      close();
      __setDownloadToast({ status: "success" });
      setTimeout(function() { __setDownloadToast(null); }, 1800);
    } else {
      // Still blocked — keep the modal open. The user really does need to
      // change their settings. Animate the modal to draw their eye to it.
      var el = document.getElementById("bf-popup-blocked-modal");
      if (el) {
        el.style.animation = "none";
        // force reflow so the animation can re-trigger
        void el.offsetWidth;
        el.style.animation = "bf-shake 0.35s ease-out";
      }
    }
  }

  var modal = (
    <>
      <style>{"@keyframes bf-shake{0%,100%{transform:translateX(0)}25%{transform:translateX(-6px)}75%{transform:translateX(6px)}}"}</style>
      <div
        onClick={close}
        style={{
          position: "fixed", inset: 0, zIndex: 2147483645,
          background: "rgba(0,0,0,0.78)",
          display: "flex", alignItems: "center", justifyContent: "center",
          padding: 20,
          paddingTop: "max(var(--bf-safe-top, env(safe-area-inset-top)), 20px)",
          paddingBottom: "max(env(safe-area-inset-bottom), 20px)",
        }}
      >
        <div
          id="bf-popup-blocked-modal"
          onClick={function(e) { e.stopPropagation(); }}
          style={{
            width: "100%", maxWidth: 380,
            background: "#0f0f0f",
            border: "1px solid #2a2a2a",
            borderRadius: 20,
            padding: 22,
            color: "white",
            boxShadow: "0 20px 60px rgba(0,0,0,0.6)",
          }}
        >
          {/* Icon */}
          <div style={{
            width: 56, height: 56, borderRadius: 16,
            background: "linear-gradient(135deg,#F59E0B,#D97706)",
            display: "flex", alignItems: "center", justifyContent: "center",
            margin: "0 auto 14px",
            boxShadow: "0 8px 24px rgba(245,158,11,0.35)",
          }}>
            <svg width="28" height="28" viewBox="0 0 24 24" fill="none" stroke="white" strokeWidth="2.5" strokeLinecap="round">
              <circle cx="12" cy="12" r="10"/>
              <line x1="12" y1="8" x2="12" y2="13"/>
              <circle cx="12" cy="16.5" r="0.5" fill="white"/>
            </svg>
          </div>

          {/* Title */}
          <div style={{
            fontSize: 18, fontWeight: 800, textAlign: "center",
            marginBottom: 8, letterSpacing: 0.2,
          }}>
            Pop-ups Blocked
          </div>

          {/* Body */}
          <div style={{
            fontSize: 13, color: "#aaa", textAlign: "center",
            lineHeight: 1.55, marginBottom: 18,
          }}>
            Your browser is blocking BeatFinder from opening the download window. Tap <b style={{color:"white"}}>Try Again</b> first — if that doesn't work, you'll need to allow pop-ups for this site.
          </div>

          {/* Try again button */}
          <button
            onClick={retryDownload}
            style={{
              width: "100%", borderRadius: 14, padding: "13px",
              background: "linear-gradient(135deg,#C026D3,#9333EA)",
              border: "none", color: "white",
              fontWeight: 800, fontSize: 14,
              letterSpacing: 0.3, cursor: "pointer",
              display: "flex", alignItems: "center", justifyContent: "center", gap: 8,
              marginBottom: 12,
              boxShadow: "0 0 20px rgba(192,38,211,0.35)",
            }}
          >
            <svg width="15" height="15" viewBox="0 0 24 24" fill="none" stroke="white" strokeWidth="2.5" strokeLinecap="round">
              <polyline points="23 4 23 10 17 10"/>
              <path d="M20.49 15a9 9 0 1 1-2.12-9.36L23 10"/>
            </svg>
            Try Again
          </button>

          {/* Instructions */}
          <div style={{
            background: "#0a0a0a",
            border: "1px solid #1f1f1f",
            borderRadius: 12,
            padding: "12px 14px",
            marginBottom: 12,
          }}>
            <div style={{
              fontSize: 10, fontWeight: 800, color: "#666",
              letterSpacing: 1.2, marginBottom: 8,
            }}>
              HOW TO ALLOW POP-UPS
            </div>
            <ol style={{
              margin: 0, paddingLeft: 18,
              fontSize: 12, color: "#ccc", lineHeight: 1.7,
            }}>
              <li>Open the iPhone <b>Settings</b> app</li>
              <li>Scroll down and tap <b>Apps → Safari</b></li>
              <li>Turn <b>Block Pop-ups</b> off</li>
              <li>Come back to BeatFinder and tap download again</li>
            </ol>
          </div>

          {/* Close */}
          <button
            onClick={close}
            style={{
              width: "100%", borderRadius: 14, padding: "10px",
              background: "transparent",
              border: "1px solid #2a2a2a",
              color: "#888", fontWeight: 700, fontSize: 12,
              cursor: "pointer",
            }}
          >
            Close
          </button>
        </div>
      </div>
    </>
  );

  if (typeof document === "undefined" || !document.body || !ReactDOM.createPortal) {
    return modal;
  }
  return ReactDOM.createPortal(modal, document.body);
}

// ── Proactive pop-up block detection ──────────────────────────────
// Called from inside a tap handler (when the user opens a contract sheet),
// because iOS only permits window.open from a user gesture. Tests by opening
// about:blank in a hidden popup and immediately closing it. If the browser
// returned null / closed / undefined, pop-ups are blocked.
//
// Result is cached in sessionStorage so we don't re-test on every contract
// open — once they enable popups they're trusted for the rest of the session.
var __popupWarnListeners = [];
function __setPopupWarn(state) {
  __popupWarnListeners.forEach(function(fn) { try { fn(state); } catch (e) {} });
}
function usePopupWarn() {
  var [state, setState] = React.useState(function() {
    try { return sessionStorage.getItem("bf_popup_warn") === "1"; } catch (e) { return false; }
  });
  React.useEffect(function() {
    __popupWarnListeners.push(setState);
    return function() {
      __popupWarnListeners = __popupWarnListeners.filter(function(fn) { return fn !== setState; });
    };
  }, []);
  return [state, setState];
}

function detectPopupBlock() {
  try {
    var probe = window.open("about:blank", "_blank", "width=1,height=1,left=-9999,top=-9999");
    var blocked = !probe || probe.closed || typeof probe.closed === "undefined";
    if (probe && !probe.closed) {
      try { probe.close(); } catch (e) {}
    }
    if (blocked) {
      try { sessionStorage.setItem("bf_popup_warn", "1"); } catch (e) {}
      __setPopupWarn(true);
      return true;
    }
    // Popups are now allowed — clear the warning if it was previously shown
    try { sessionStorage.removeItem("bf_popup_warn"); } catch (e) {}
    __setPopupWarn(false);
    return false;
  } catch (e) {
    return false;
  }
}

// Mark popup state as "blocked" without doing a probe — used when the
// real download flow has already detected a blocked window.open() so we
// don't burn a second user-gesture activation on a redundant test.
function markPopupBlocked() {
  try { sessionStorage.setItem("bf_popup_warn", "1"); } catch (e) {}
  __setPopupWarn(true);
}

// Mark popups as confirmed working — called when a real window.open()
// succeeded, so the banner can disappear immediately.
function markPopupAllowed() {
  try { sessionStorage.removeItem("bf_popup_warn"); } catch (e) {}
  __setPopupWarn(false);
}

// Install a no-op probe — we no longer do any speculative window.open()
// calls. Instead, the actual download attempts (MP3, PDF) are the
// authoritative signal: when they succeed, markPopupAllowed() clears
// the banner; when they fail, markPopupBlocked() shows it.
//
// This avoids triggering iOS Safari's "This site is attempting to open
// a pop-up window" prompt, which fires on every window.open call when
// the user has chosen to confirm each popup individually.
var __popupGlobalProbeInstalled = false;
function installGlobalPopupProbe() {
  if (__popupGlobalProbeInstalled) return;
  __popupGlobalProbeInstalled = true;
  // Intentionally empty — we rely on real download flows to update state.
}

// ── Inline pop-up warning banner ──────────────────────────────────
// Renders inside the contract sheet/modal. Compact, friendly, NOT dismissible.
// Stays visible until popups are actually allowed — since the user can't
// download anything without them, hiding the warning would just confuse
// them later when downloads silently fail.
function PopupWarningBanner() {
  var [warn] = usePopupWarn();
  if (!warn) return null;

  return (
    <div
      style={{
        background: "linear-gradient(135deg, rgba(245,158,11,0.18), rgba(217,119,6,0.08))",
        border: "1px solid rgba(245,158,11,0.5)",
        borderRadius: 12,
        padding: "12px 14px",
        marginBottom: 12,
        animation: "bf-warn-in 0.4s ease-out, bf-warn-pulse 2s ease-out 0.4s 2",
        boxShadow: "0 0 0 0 rgba(245,158,11,0.5)",
      }}
    >
      <style>{"@keyframes bf-warn-in{from{opacity:0;transform:translateY(-8px)}to{opacity:1;transform:translateY(0)}}@keyframes bf-warn-pulse{0%,100%{box-shadow:0 0 0 0 rgba(245,158,11,0)}50%{box-shadow:0 0 0 8px rgba(245,158,11,0.18)}}"}</style>

      {/* Heading row */}
      <div style={{ display: "flex", alignItems: "center", gap: 10, marginBottom: 10 }}>
        <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="#F59E0B" strokeWidth="2.5" strokeLinecap="round" style={{ flexShrink: 0 }}>
          <circle cx="12" cy="12" r="10"/>
          <line x1="12" y1="8" x2="12" y2="13"/>
          <circle cx="12" cy="16.5" r="0.5" fill="#F59E0B"/>
        </svg>
        <div style={{ flex: 1, minWidth: 0 }}>
          <div style={{ color: "#F59E0B", fontWeight: 800, fontSize: 13, letterSpacing: 0.2 }}>
            Pop-ups are blocked
          </div>
          <div style={{ color: "#aaa", fontSize: 11, marginTop: 2 }}>
            You'll need to turn this off to download beats &amp; contracts
          </div>
        </div>
      </div>

      {/* Step-by-step instructions — always visible */}
      <div style={{
        padding: "10px 12px",
        background: "rgba(0,0,0,0.35)",
        borderRadius: 10,
        color: "#ccc", fontSize: 11.5, lineHeight: 1.7,
      }}>
        <div style={{ color: "#F59E0B", fontWeight: 700, fontSize: 10, letterSpacing: 1, marginBottom: 6 }}>
          HOW TO FIX (TAKES 10 SECONDS)
        </div>
        <ol style={{ margin: 0, paddingLeft: 16 }}>
          <li>Open the iPhone <b style={{ color: "white" }}>Settings</b> app</li>
          <li>Scroll down → tap <b style={{ color: "white" }}>Apps → Safari</b></li>
          <li>Turn <b style={{ color: "white" }}>Block Pop-ups</b> off</li>
          <li>Return to BeatFinder — downloads will work</li>
        </ol>
        <div style={{ marginTop: 8, color: "#666", fontSize: 10, fontStyle: "italic" }}>
          BeatFinder only uses pop-ups to open download &amp; share prompts.
        </div>
      </div>
    </div>
  );
}

// ── Smart MP3 download ────────────────────────────────────────────
// Always fetches via blob first (so we control the result, can show progress,
// and surface real errors). After download:
//   - iOS PWA: tries native Share Sheet with a File object (best UX, "Save
//     to Files" appears as a primary action)
//   - iOS Safari (browser tab): opens the blob in a NEW tab. This keeps the
//     original tab — and our React app — exactly where it was. When the
//     user taps Done they return to the same page they came from.
//   - Android/desktop: real <a download> click.
async function downloadBeatMp3(beat) {
  var beatId   = beat && (beat.id || beat.beat_id || beat._id);
  var beatUrl  = beat && (beat.url || beat.beat_url);
  var rawTitle = (beat && (beat.title || beat.beat_title)) || "beat";
  var filename = String(rawTitle).replace(/[^\w\s\-]/g, "").trim().replace(/\s+/g, "_") + ".mp3";

  var endpoint;
  if (beatId) {
    // Append the auth token as a query param so the backend can verify
    // ownership (Stripe-confirmed lease). Some browsers strip Authorization
    // headers from media downloads on iOS, so query param is the reliable path.
    var bfToken = "";
    try { bfToken = (typeof getToken === "function" && getToken()) || localStorage.getItem("bf_token") || ""; } catch (e) {}
    endpoint = API_BASE + "/api/producer/beats/" + beatId + "/file";
    if (bfToken) {
      endpoint += (endpoint.indexOf("?") >= 0 ? "&" : "?") + "token=" + encodeURIComponent(bfToken);
    }
  } else if (beatUrl) {
    endpoint = beatUrl.replace("/upload/", "/upload/fl_attachment/");
  } else {
    __setDownloadToast({ status: "error", message: "No file attached to this beat" });
    setTimeout(function() { __setDownloadToast(null); }, 2500);
    return;
  }

  var isIOS = /iPad|iPhone|iPod/.test(navigator.userAgent) ||
              (navigator.platform === "MacIntel" && navigator.maxTouchPoints > 1);
  var isStandalonePWA = window.matchMedia && window.matchMedia("(display-mode: standalone)").matches;

  // Visual feedback — appears instantly so the user knows the tap registered
  __setDownloadToast({ status: "downloading", title: rawTitle });

  try {
    var res = await fetch(endpoint, { method: "GET" });
    if (!res.ok) {
      throw new Error("Server returned " + res.status);
    }
    var blob = await res.blob();
    // Force the correct MIME on the blob even if the response was generic
    var typedBlob = blob.type === "audio/mpeg"
      ? blob
      : new Blob([blob], { type: "audio/mpeg" });
    var blobUrl = URL.createObjectURL(typedBlob);

    // STRATEGY 1 — iOS (both PWA and Safari): Web Share API with a real File.
    // iOS will show the system share sheet with "Save to Files" as a primary
    // action, plus AirDrop, Messages, Mail, etc. CRUCIALLY this does not
    // require popups to be allowed, and does not trigger iOS's "this site
    // is attempting to open a pop-up" confirmation prompt. Works in both
    // browser tab AND standalone PWA on modern iOS (14.4+).
    //
    // Once we attempt navigator.share we treat it as terminal: success,
    // cancel, or hard error — all three return without falling through
    // to other strategies that would call window.open.
    if (isIOS && navigator.canShare && typeof File === "function") {
      var file;
      try {
        file = new File([typedBlob], filename, { type: "audio/mpeg" });
      } catch (e) { file = null; }

      if (file && navigator.canShare({ files: [file] })) {
        __setDownloadToast({ status: "downloading", title: "Opening share sheet…" });
        try {
          await navigator.share({ files: [file], title: rawTitle });
          // Share completed (user picked a destination). Popups not involved
          // either way — but clear the warning since the action worked.
          markPopupAllowed();
          __setDownloadToast({ status: "success" });
          setTimeout(function() { __setDownloadToast(null); }, 1800);
          setTimeout(function() { URL.revokeObjectURL(blobUrl); }, 5000);
        } catch (e) {
          // navigator.share throws on cancel (AbortError) AND on internal
          // failures (NotAllowedError, etc). Either way, we DO NOT navigate
          // away. We just clear the toast and revoke the blob URL.
          var isCancel = e && (e.name === "AbortError" ||
                               String(e.message || "").toLowerCase().indexOf("abort") !== -1 ||
                               String(e.message || "").toLowerCase().indexOf("cancel") !== -1);
          if (isCancel) {
            __setDownloadToast({
              status: "error",
              message: "Download cancelled — tap to try again",
            });
            setTimeout(function() { __setDownloadToast(null); }, 2500);
          } else {
            __setDownloadToast({
              status: "error",
              message: "Couldn't open share sheet — please try again",
            });
            setTimeout(function() { __setDownloadToast(null); }, 3500);
          }
          setTimeout(function() { URL.revokeObjectURL(blobUrl); }, 1000);
        }
        return; // <-- terminal: do not fall through
      }
    }

    // STRATEGY 2 — iOS without Web Share support (very old iOS, ~13 and
    // below): navigate the CURRENT tab to the blob URL. iOS will render
    // the audio file in its native viewer with built-in share controls,
    // and the back button returns the user to BeatFinder. This is the
    // same trick we use for social links and avoids window.open entirely
    // (so no "attempting to open pop-up" prompt fires).
    if (isIOS) {
      markPopupAllowed();
      __setDownloadToast({ status: "success" });
      setTimeout(function() { __setDownloadToast(null); }, 1800);
      // Hold the blob URL long enough for the navigation to load it
      setTimeout(function() { URL.revokeObjectURL(blobUrl); }, 60000);
      window.location.href = blobUrl;
      return;
    }

    // STRATEGY 3 — Android / desktop: real <a download> click.
    // We DO NOT use this on iOS — Safari/WebKit ignores the `download`
    // attribute and instead navigates to the blob URL, which on the
    // standalone PWA strands the user on a file viewer page they can't
    // close back into the app.
    if (!isIOS) {
      var a = document.createElement("a");
      a.href     = blobUrl;
      a.download = filename;
      a.style.display = "none";
      document.body.appendChild(a);
      a.click();
      __setDownloadToast({ status: "success" });
      setTimeout(function() { __setDownloadToast(null); }, 1800);
      setTimeout(function() {
        document.body.removeChild(a);
        URL.revokeObjectURL(blobUrl);
      }, 1500);
      return;
    }

    // STRATEGY 4 — iOS PWA where Web Share didn't take the file.
    // Last resort: tell the user we can't auto-save and they'll need to
    // open the app in Safari for downloads. Crucially, do NOT navigate.
    __setDownloadToast({
      status: "error",
      message: "Downloads need Safari — open BeatFinder in Safari to save",
    });
    setTimeout(function() { __setDownloadToast(null); }, 4000);
    setTimeout(function() { URL.revokeObjectURL(blobUrl); }, 1000);
  } catch (e) {
    __setDownloadToast({
      status: "error",
      message: "Download failed — " + (e.message || "please try again"),
    });
    setTimeout(function() { __setDownloadToast(null); }, 3500);
  }
}

// Legacy wrapper kept for older callers that pass (url, title, beatId)
function downloadMp3(url, title, beatId) {
  return downloadBeatMp3({ url: url, title: title, id: beatId });
}

// ── Download Button ───────────────────────────────────────────────
// Renders a button (not an <a>) so we can intercept the click and run
// downloadBeatMp3 — that way we get the iOS share-sheet flow on Safari
// and a real blob download elsewhere, with proper error reporting.
function DownloadButton({ url, title, beatId, beat, user, style, children }) {
  var resolvedBeat = {
    id:    beatId || (beat && beat.id),
    url:   url    || (beat && beat.url),
    title: title  || (beat && beat.title) || "beat",
  };
  var [busy, setBusy] = React.useState(false);
  return (
    <button
      type="button"
      onClick={async function(e) {
        e.preventDefault();
        e.stopPropagation();
        if (busy) return;
        setBusy(true);
        try { await downloadBeatMp3(resolvedBeat); }
        finally { setBusy(false); }
      }}
      style={Object.assign({ textDecoration: "none", cursor: busy ? "wait" : "pointer" }, style)}
    >
      {children}
    </button>
  );
}

// ── Shared 45s PreviewBar ─────────────────────────────────────────
function PreviewBar({ previewTime, onStop }) {
  return (
    <div style={{
      background: "linear-gradient(135deg,#0d0d0d,#111)",
      border: "1px solid #1e1e1e", borderRadius: 12,
      padding: "10px 14px", display: "flex", alignItems: "center", gap: 12,
      marginBottom: 8,
    }}>
      <button onClick={onStop} style={{
        background: "rgba(99,91,255,0.15)", border: "1px solid #635BFF",
        borderRadius: 8, color: "#635BFF", cursor: "pointer",
        width: 32, height: 32, display: "flex", alignItems: "center", justifyContent: "center",
        flexShrink: 0, fontSize: 12, fontWeight: 900,
      }}>■</button>
      <div style={{ flex: 1, height: 4, background: "#222", borderRadius: 2, overflow: "hidden" }}>
        <div style={{
          height: "100%", borderRadius: 2,
          background: "linear-gradient(90deg,#635BFF,#C026D3)",
          width: (previewTime / 45 * 100) + "%",
          transition: "width 0.1s linear",
        }} />
      </div>
      <span style={{ color: "#555", fontSize: 11, fontWeight: 700, flexShrink: 0, fontVariantNumeric: "tabular-nums" }}>
        {Math.floor(previewTime)}s / 45s
      </span>
    </div>
  );
}

// ── Shared beat card shell ────────────────────────────────────────
// Renders title, genre/price badges, producer, stats row, and action area.
// Used by BeatLeaseCard, ProfileBeatCard, and the MembersBeatsTab free card.
function BeatCardShell({ beat, accentColor, children, onViewProfile, extraStats }) {
  const isFree = !beat.price || beat.price === "free" || beat.price === "£0" || beat.price === "0" || beat.price === "£0.00" || beat.price === "0.00";
  const priceColor = isFree ? "#22C55E" : (accentColor || "#C026D3");
  const borderColor = isFree ? "rgba(34,197,94,0.3)" : (accentColor ? accentColor + "55" : "rgba(192,38,211,0.3)");
  return (
    <div style={{
      background: "linear-gradient(160deg,#181818 0%,#111 60%,#0d0d0d 100%)",
      borderRadius: 18,
      border: "1px solid " + borderColor,
      marginBottom: 14,
      overflow: "hidden",
      boxShadow: "0 6px 28px rgba(0,0,0,0.55), inset 0 1px 0 rgba(255,255,255,0.04)",
    }}>
      {/* Coloured top accent bar */}
      <div style={{ height: 3, background: "linear-gradient(90deg," + priceColor + "cc, " + priceColor + "22)" }} />
      <div style={{ padding: "16px 16px 18px" }}>
        {/* Title */}
        <div style={{ color: "white", fontWeight: 800, fontSize: 16, lineHeight: 1.3, marginBottom: 10, letterSpacing: -0.2 }}>
          {beat.title}
        </div>
        {/* Badges row */}
        <div style={{ display: "flex", flexWrap: "wrap", gap: 6, marginBottom: 12, alignItems: "center" }}>
          {beat.genre && (
            <span style={{ background: "rgba(245,158,11,0.12)", border: "1px solid rgba(245,158,11,0.35)", borderRadius: 20, padding: "4px 11px", fontSize: 11, color: "#F59E0B", fontWeight: 700, letterSpacing: 0.3 }}>
              {beat.genre}
            </span>
          )}
          <span style={{ background: isFree ? "rgba(34,197,94,0.12)" : "rgba(192,38,211,0.12)", border: "1px solid " + (isFree ? "rgba(34,197,94,0.4)" : "rgba(192,38,211,0.4)"), borderRadius: 20, padding: "4px 11px", fontSize: 11, color: isFree ? "#22C55E" : "#C026D3", fontWeight: 800, letterSpacing: 0.3 }}>
            {isFree ? "FREE DOWNLOAD" : beat.price}
          </span>
          {extraStats}
        </div>
        {/* Producer + stats */}
        <div style={{ display: "flex", alignItems: "center", justifyContent: "space-between", marginBottom: 14, paddingBottom: 12, borderBottom: "1px solid rgba(255,255,255,0.05)" }}>
          <div style={{ display: "flex", alignItems: "center", gap: 7 }}>
            <div style={{ width: 26, height: 26, borderRadius: "50%", background: "linear-gradient(135deg," + (accentColor || "#C026D3") + "55,#0a0a0a)", display: "flex", alignItems: "center", justifyContent: "center", border: "1px solid " + (accentColor || "#C026D3") + "44", flexShrink: 0 }}>
              <span style={{ color: accentColor || "#C026D3", fontSize: 11, fontWeight: 800 }}>{(beat.producer || "?")[0].toUpperCase()}</span>
            </div>
            <div>
              <div style={{ color: "#888", fontSize: 10, fontWeight: 600, letterSpacing: 0.5 }}>PRODUCER</div>
              <span
                onClick={function(e) { e.stopPropagation(); if (onViewProfile && (beat.producer_username || beat.producer)) onViewProfile(beat.producer_username || beat.producer); }}
                style={{ color: accentColor || "#C026D3", fontWeight: 700, fontSize: 13, cursor: onViewProfile && (beat.producer_username || beat.producer) ? "pointer" : "default" }}>
                {beat.producer}
              </span>
            </div>
          </div>
          <div style={{ color: "#555", fontSize: 11, display: "flex", gap: 10, alignItems: "center" }}>
            {beat.downloads > 0 && (
              <span style={{ display: "flex", alignItems: "center", gap: 3 }}>
                <svg width="10" height="10" viewBox="0 0 24 24" fill="none" stroke="#555" strokeWidth="2.5" strokeLinecap="round"><path d="M12 3v13M6 11l6 6 6-6"/><path d="M4 20h16"/></svg>
                {beat.downloads}
              </span>
            )}
            {beat.playCount > 0 && (
              <span style={{ display: "flex", alignItems: "center", gap: 3 }}>
                <svg width="10" height="10" viewBox="0 0 24 24" fill="#555"><path d="M5 3.5l15 8.5-15 8.5V3.5z"/></svg>
                {fmtCount(beat.playCount)}
              </span>
            )}
          </div>
        </div>
        {/* Action area — preview button, player, download/buy */}
        {children}
      </div>
    </div>
  );
}

// =============================================================================
// NEW BEAT CARD SHELL — shared design used by all producer beat cards
// Matches the ProfileBeatCard design: logo thumbnail, waveform player,
// purple=free, gold=paid, footer info pills on free beats.
// =============================================================================
// ── Free Beat CTA — contract must be accepted before MP3 unlocks ──────────────
function FreeBeatCTA({ beat, user }) {
  // Persist "signed" state per-beat in localStorage so iOS navigating away to
  // view the downloaded HTML contract doesn't reset the UI back to "idle"
  // (which was hiding the MP3 download button after coming back).
  var signedKey = "bf_signed_free_" + (beat.id || beat.url || beat.title || "x");
  var initialStep = "idle";
  try {
    // Only honour the saved "signed" state for LOGGED-IN users — guests
    // must never see the download UI, regardless of what's in localStorage
    // (stale data left by a previous user on this device).
    if (user && typeof window !== "undefined" && window.localStorage && window.localStorage.getItem(signedKey)) {
      initialStep = "done";
    }
  } catch (e) {}

  var [step, setStep] = React.useState(initialStep);
  var [showContract, setShowContract] = React.useState(false);

  // Force back to idle whenever the user logs out (or the component mounts
  // for a guest with stale localStorage state).
  React.useEffect(function() {
    if (!user) setStep("idle");
  }, [user]);

  // Re-check localStorage on mount (covers PWA resume after iOS leaves the page)
  React.useEffect(function() {
    if (!user) return; // guests never get the "done" state
    try {
      if (window.localStorage && window.localStorage.getItem(signedKey)) {
        setStep("done");
      }
    } catch (e) {}
    // also listen for visibility change — when user returns from the
    // iOS "Open in Kodex / Done" screen, re-evaluate state
    function onVis() {
      try {
        if (document.visibilityState === "visible" && user && window.localStorage.getItem(signedKey)) {
          setStep("done");
        }
      } catch (e) {}
    }
    // Listen for server hydration completing — components mount BEFORE the
    // server-side /my-free-licences fetch resolves, so their initial read
    // of localStorage misses agreements made in a different browser context
    // (PWA vs Safari tab). When App.tsx dispatches this event after writing
    // hydrated IDs to localStorage, this re-reads and updates UI state.
    function onHydrated() {
      try {
        if (!user) { setStep("idle"); return; }
        if (window.localStorage && window.localStorage.getItem(signedKey)) {
          setStep("done");
        } else {
          // Key was removed (e.g. on logout) — reset
          setStep("idle");
        }
      } catch (e) {}
    }
    document.addEventListener("visibilitychange", onVis);
    window.addEventListener("bf-free-licences-hydrated", onHydrated);
    return function() {
      document.removeEventListener("visibilitychange", onVis);
      window.removeEventListener("bf-free-licences-hydrated", onHydrated);
    };
  }, [signedKey, user]);

  // Always use the backend proxy for downloads — it sets Content-Disposition: attachment
  // so iOS Safari shows the native download dialog instead of a blank media page.
  var downloadUrl = beat.id
    ? API_BASE + "/api/producer/beats/" + beat.id + "/file"
    : (beat.url ? beat.url.replace("/upload/", "/upload/fl_attachment/") : "#");
  var filename = (beat.title || "beat").replace(/[^\w\s\-]/g, "").trim().replace(/\s+/g, "_") + ".mp3";
  var date      = new Date().toLocaleDateString("en-GB", { day: "numeric", month: "long", year: "numeric" });
  var artist    = (user && (user.name || user.username)) || "Artist";
  var producer  = beat.producer || beat.producer_username || "Producer";

  function markSigned() {
    try { window.localStorage.setItem(signedKey, "1"); } catch (e) {}
    setStep("done");
    // Also record on the server so the agreed-licence state persists across
    // PWA, Safari, and other devices for this logged-in user.
    if (user && beat && beat.id) {
      apiFetch("/api/producer/beats/" + beat.id + "/agree-licence", {
        method: "POST",
      })
        .then(function(r) { console.log("[BF agree-licence] OK", beat.id, r); })
        .catch(function(err) { console.error("[BF agree-licence] FAILED", beat.id, err && err.message); });
    } else {
      console.warn("[BF agree-licence] skipped — no beat.id or no user", { hasUser: !!user, hasBeatId: !!(beat && beat.id) });
    }
  }

  if (step === "contract") {
    return (
      <div style={{ padding: "0 0 16px" }}>
        <PopupWarningBanner />
        {/* Contract modal inline */}
        <div style={{ background: "#0d0d0d", border: "1px solid #222", borderRadius: 14, padding: 16, marginBottom: 12, maxHeight: 320, overflowY: "auto", WebkitOverflowScrolling: "touch" }}>
          <div style={{ color: "#F59E0B", fontWeight: 800, fontSize: 12, letterSpacing: 1, marginBottom: 12, textAlign: "center" }}>FREE BEAT LICENCE AGREEMENT</div>
          <div style={{ color: "#888", fontSize: 11, lineHeight: 1.7 }}>
            <div style={{ marginBottom: 8 }}><span style={{ color: "#aaa", fontWeight: 700 }}>Beat:</span> "{beat.title}"</div>
            <div style={{ marginBottom: 8 }}><span style={{ color: "#aaa", fontWeight: 700 }}>Producer:</span> {producer}</div>
            <div style={{ marginBottom: 8 }}><span style={{ color: "#aaa", fontWeight: 700 }}>Artist:</span> {artist}</div>
            <div style={{ marginBottom: 8 }}><span style={{ color: "#aaa", fontWeight: 700 }}>Date:</span> {date}</div>
            <div style={{ height: 1, background: "#222", margin: "12px 0" }} />
            <div style={{ marginBottom: 8 }}><span style={{ color: "#C026D3", fontWeight: 700 }}>1. GRANT</span> — Producer grants Artist a free, non-exclusive licence to use the Beat for non-commercial purposes only.</div>
            <div style={{ marginBottom: 8 }}><span style={{ color: "#C026D3", fontWeight: 700 }}>2. PERMITTED</span> — Record one song, share on free platforms (SoundCloud, YouTube non-monetised), perform at non-ticketed events.</div>
            <div style={{ marginBottom: 8 }}><span style={{ color: "#C026D3", fontWeight: 700 }}>3. CREDIT</span> — Artist must credit: <em>(Prod. by {producer})</em> in all uses.</div>
            <div style={{ marginBottom: 8 }}><span style={{ color: "#C026D3", fontWeight: 700 }}>4. RESTRICTIONS</span> — No monetisation, no selling recordings, no sub-licensing, no copyright claim on the Beat. If recording exceeds 10,000 streams or generates revenue, a paid lease must be purchased.</div>
            <div style={{ marginBottom: 8 }}><span style={{ color: "#C026D3", fontWeight: 700 }}>5. OWNERSHIP</span> — Producer retains full copyright of the Beat. Artist owns their original vocals/lyrics only.</div>
            <div style={{ marginBottom: 8 }}><span style={{ color: "#C026D3", fontWeight: 700 }}>6. PLATFORM</span> — BeatFinder (beatfinder.co.uk) facilitates this licence. Not liable for disputes between parties.</div>
            <div style={{ marginBottom: 8 }}><span style={{ color: "#C026D3", fontWeight: 700 }}>7. GOVERNING LAW</span> — England and Wales.</div>
            <div style={{ height: 1, background: "#222", margin: "12px 0" }} />
            <div style={{ color: "#555", fontSize: 10 }}>By tapping "I Agree" below you accept all terms of this licence. Reference: BF-FREE-{Date.now()}</div>
          </div>
        </div>
        <button onClick={function() { markSigned(); generateFreeLeaseContract(beat, user); }} style={{
          width: "100%", borderRadius: 14, padding: "14px",
          background: "linear-gradient(135deg,#C026D3,#9333EA)", border: "none",
          color: "white", fontWeight: 800, fontSize: 14, cursor: "pointer",
          letterSpacing: 0.5, textTransform: "uppercase",
          display: "flex", alignItems: "center", justifyContent: "center", gap: 10,
          marginBottom: 8,
        }}>
          <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="white" strokeWidth="2.5" strokeLinecap="round"><polyline points="20 6 9 17 4 12"/></svg>
          I Agree — Save Contract
        </button>
        <button onClick={function() { setStep("idle"); }} style={{
          width: "100%", borderRadius: 14, padding: "10px", background: "transparent",
          border: "1px solid #222", color: "#555", fontWeight: 600, fontSize: 12, cursor: "pointer",
        }}>
          Cancel
        </button>
      </div>
    );
  }

  if (step === "done") {
    return (
      <div style={{ padding: "0 0 16px" }}>
        {showContract && (
          <ContractViewer
            html={generateFreeLeaseContractHtml(beat, user)}
            filename={"BeatFinder_FreeLicence_" + (beat.title || "Beat").replace(/[^\w]/g, "_") + ".html"}
            onClose={function() { setShowContract(false); }}
            pdfDownloader={function() { return downloadFreeContractPdf(beat, user); }}
          />
        )}
        <PopupWarningBanner />
        <div style={{ background: "rgba(34,197,94,0.08)", border: "1px solid rgba(34,197,94,0.2)", borderRadius: 14, padding: "12px 16px", marginBottom: 10, display: "flex", alignItems: "center", gap: 10 }}>
          <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="#22C55E" strokeWidth="2.5" strokeLinecap="round"><polyline points="20 6 9 17 4 12"/></svg>
          <div>
            <div style={{ color: "#22C55E", fontWeight: 800, fontSize: 12 }}>Licence Agreed ✓</div>
            <div style={{ color: "#555", fontSize: 11 }}>Download your contract & MP3 below</div>
          </div>
        </div>
        <button data-bf-download-cta="1" onClick={function() { setShowContract(true); }} style={{
          width: "100%", borderRadius: 14, padding: "12px",
          background: "transparent", border: "1px solid #333",
          color: "#888", fontWeight: 700, fontSize: 12, cursor: "pointer",
          display: "flex", alignItems: "center", justifyContent: "center", gap: 8,
          marginBottom: 8,
        }}>
          <svg width="13" height="13" viewBox="0 0 24 24" fill="none" stroke="#888" strokeWidth="2" strokeLinecap="round"><path d="M14 2H6a2 2 0 0 0-2 2v16a2 2 0 0 0 2 2h12a2 2 0 0 0 2-2V8z"/><polyline points="14 2 14 8 20 8"/></svg>
          View Licence Contract
        </button>
        <button data-bf-download-cta="1" onClick={function() { downloadBeatMp3(beat); }} style={{
          width: "100%", borderRadius: 14, padding: "15px", border: "none",
          background: "linear-gradient(135deg,#C026D3,#9333EA)",
          color: "white", fontWeight: 800, fontSize: 14,
          letterSpacing: 0.5, textTransform: "uppercase",
          display: "flex", alignItems: "center", justifyContent: "center", gap: 10,
          cursor: "pointer", boxShadow: "0 0 20px rgba(192,38,211,0.4)",
        }}>
          <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="white" strokeWidth="2.5" strokeLinecap="round"><path d="M12 3v13M6 11l6 6 6-6"/><path d="M4 20h16"/></svg>
          Save MP3 to Device
        </button>
      </div>
    );
  }

  // idle state
  return (
    <div style={{ padding: "0 0 16px" }}>
      <button onClick={function() {
        // Free MP3 downloads: any signed-in user (Free, Artist Pro,
        // Producer Pro) can grab them. Guests get a quick-signup modal
        // first so we have a real account on the licence record.
        if (!user) {
          window.dispatchEvent(new CustomEvent("bf:quickSignup", { detail: {
            intent: "download", beat: beat,
            resume: function() { setStep("contract"); },
          }}));
          return;
        }
        setStep("contract");
      }} style={{
        width: "100%", borderRadius: 14, padding: "15px",
        background: "transparent", border: "2px solid #C026D3",
        color: "#C026D3", fontWeight: 800, fontSize: 14, cursor: "pointer",
        letterSpacing: 0.5, textTransform: "uppercase",
        display: "flex", alignItems: "center", justifyContent: "center", gap: 10,
        marginBottom: 8,
      }}>
        <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="#C026D3" strokeWidth="2" strokeLinecap="round"><path d="M14 2H6a2 2 0 0 0-2 2v16a2 2 0 0 0 2 2h12a2 2 0 0 0 2-2V8z"/><polyline points="14 2 14 8 20 8"/></svg>
        Get Free Beat
      </button>
      <div style={{ display: "flex", gap: 0, borderTop: "1px solid rgba(255,255,255,0.05)", paddingTop: 12, marginTop: 4 }}>
        {[
          { icon: <svg width="13" height="13" viewBox="0 0 24 24" fill="none" stroke="#555" strokeWidth="2" strokeLinecap="round"><path d="M14 2H6a2 2 0 0 0-2 2v16a2 2 0 0 0 2 2h12a2 2 0 0 0 2-2V8z"/><polyline points="14 2 14 8 20 8"/></svg>, title: "LICENCE FIRST", sub: "Contract required." },
          { icon: <svg width="13" height="13" viewBox="0 0 24 24" fill="none" stroke="#555" strokeWidth="2" strokeLinecap="round"><rect x="3" y="11" width="18" height="11" rx="2"/><path d="M7 11V7a5 5 0 0 1 10 0v4"/></svg>, title: "100% FREE", sub: "No tags." },
          { icon: <svg width="13" height="13" viewBox="0 0 24 24" fill="none" stroke="#555" strokeWidth="2" strokeLinecap="round"><polygon points="13 2 3 14 12 14 11 22 21 10 12 10 13 2"/></svg>, title: "NON-PROFIT", sub: "Free use only." },
        ].map(function(item, i) {
          return (
            <div key={i} style={{ flex: 1, textAlign: "center", padding: "0 4px", borderRight: i < 2 ? "1px solid rgba(255,255,255,0.05)" : "none" }}>
              <div style={{ display: "flex", justifyContent: "center", marginBottom: 4 }}>{item.icon}</div>
              <div style={{ color: "#555", fontSize: 8, fontWeight: 800, letterSpacing: 0.5 }}>{item.title}</div>
              <div style={{ color: "#333", fontSize: 8, marginTop: 1 }}>{item.sub}</div>
            </div>
          );
        })}
      </div>
    </div>
  );
}

function NewBeatCardShell({ beat, previewTime, previewing, onTogglePreview, audioEl, onBuy, onDownload, buyLoading, buyErr, user, onViewProfile }) {
  var isFree    = !beat.price || beat.price === "free" || beat.price === "£0" || beat.price === "0" || beat.price === "£0.00" || beat.price === "0.00";
  // LED theme: free = magenta-violet glow, paid = electric purple-to-blue
  var accentClr = isFree ? "#C026D3" : "#7C3AED";
  var accentClr2 = isFree ? "#7C3AED" : "#3B82F6";

  // Check if the current user already owns a lease for this beat. Reads from
  // the global lease cache populated by App.tsx on login/Stripe-success, and
  // re-checks when the "bf-leases-updated" event fires (e.g. after a fresh
  // purchase, cache refresh on login, or cross-context sync). This lets the
  // card show "OWNED ✓ DOWNLOAD" instead of "BUY £50" once a lease is
  // confirmed — consistent across PWA / Safari tab / multi-device.
  var [ownedLease, setOwnedLease] = React.useState(function() {
    try {
      if (isFree || !user || !Array.isArray(window.__bf_leases__)) return null;
      var match = window.__bf_leases__.find(function(l){ return (l.beat_id === beat.id || l.id === beat.id) && !l.voided; });
      return match || null;
    } catch(e) { return null; }
  });
  React.useEffect(function() {
    if (isFree || !user) { setOwnedLease(null); return; }
    function check() {
      try {
        if (!Array.isArray(window.__bf_leases__)) return;
        var match = window.__bf_leases__.find(function(l){ return (l.beat_id === beat.id || l.id === beat.id) && !l.voided; });
        setOwnedLease(match || null);
      } catch(e) {}
    }
    check();
    window.addEventListener("bf-leases-updated", check);
    return function() { window.removeEventListener("bf-leases-updated", check); };
  }, [isFree, beat.id, user]);
  var totalSecs = Math.floor(previewTime || 0);
  var progMins  = String(Math.floor(totalSecs / 60)).padStart(2, "0");
  var progSecs  = String(totalSecs % 60).padStart(2, "0");
  // Global play count — syncs across all cards showing the same beat
  var playCount = usePlayCount(beat.id, beat.playCount);

  return (
    <div className="bf-card" style={{
      background: "linear-gradient(165deg,#0f0a1f 0%,#0a0a14 60%,#080812 100%)",
      borderRadius: 22, marginBottom: 16, overflow: "hidden",
      border: "1px solid " + accentClr + "55",
      boxShadow:
        "0 8px 40px rgba(0,0,0,0.7)," +
        "0 0 0 0.5px " + accentClr + "33," +
        "inset 0 1px 0 rgba(255,255,255,0.04)," +
        "0 0 24px " + accentClr + "1a",
      position: "relative",
    }}>
      {/* LED top edge — gradient bar with glow */}
      <div style={{
        height: 2,
        background: "linear-gradient(90deg,transparent 0%," + accentClr + " 20%," + accentClr2 + " 50%," + accentClr + " 80%,transparent 100%)",
        boxShadow: "0 0 12px " + accentClr + "cc, 0 0 24px " + accentClr2 + "66",
      }} />

      <div style={{ padding: "18px 18px 0" }}>
        {/* Header row */}
        <div style={{ display: "flex", gap: 14, marginBottom: 14 }}>
          {/* Thumbnail — BeatFinder logo */}
          <div style={{
            width: 82, height: 82, borderRadius: 14, flexShrink: 0, overflow: "hidden",
            background: "#0a0a0a",
            border: "1px solid " + accentClr + "55",
            boxShadow: "0 0 16px " + accentClr + "22, inset 0 0 0 1px rgba(255,255,255,0.03)",
            position: "relative",
          }}>
            <img src="https://i.ibb.co/v4wcZVJW/IMG-9119.jpg" alt="BeatFinder" style={{ width: "100%", height: "100%", objectFit: "cover", display: "block" }} />
            {/* Inner glow overlay */}
            <div style={{ position: "absolute", inset: 0, boxShadow: "inset 0 0 20px " + accentClr + "33", pointerEvents: "none" }} />
          </div>

          {/* Info */}
          <div style={{ flex: 1, minWidth: 0 }}>
            {beat.genre && (
              <div style={{
                color: accentClr, fontSize: 9, fontWeight: 900, letterSpacing: 2,
                marginBottom: 5, textTransform: "uppercase",
                textShadow: "0 0 8px " + accentClr + "66",
              }}>{beat.genre}</div>
            )}
            <div style={{ color: "white", fontWeight: 800, fontSize: 15, lineHeight: 1.3, marginBottom: 8, overflow: "hidden", display: "-webkit-box", WebkitLineClamp: 2, WebkitBoxOrient: "vertical" }}>{beat.title}</div>
            <div style={{ display: "flex", gap: 6, flexWrap: "wrap", alignItems: "center" }}>
              {isFree ? (
                <span style={{
                  display: "inline-flex", alignItems: "center", gap: 5,
                  background: "linear-gradient(135deg,rgba(192,38,211,0.18),rgba(124,58,237,0.18))",
                  border: "1.5px solid " + accentClr,
                  borderRadius: 20, padding: "4px 11px",
                  color: "#E9D5FF", fontSize: 10, fontWeight: 900, letterSpacing: 0.5,
                  boxShadow: "0 0 12px " + accentClr + "55, inset 0 1px 0 rgba(255,255,255,0.1)",
                }}>
                  <svg width="9" height="9" viewBox="0 0 24 24" fill="none" stroke="#E9D5FF" strokeWidth="2.5" strokeLinecap="round"><path d="M12 3v13M6 11l6 6 6-6"/></svg>
                  FREE DOWNLOAD
                </span>
              ) : (
                <span style={{
                  background: "linear-gradient(135deg,rgba(124,58,237,0.2),rgba(59,130,246,0.2))",
                  border: "1.5px solid " + accentClr,
                  borderRadius: 20, padding: "4px 11px",
                  color: "#DDD6FE", fontSize: 11, fontWeight: 900, letterSpacing: 0.5,
                  boxShadow: "0 0 14px " + accentClr + "55, inset 0 1px 0 rgba(255,255,255,0.1)",
                }}>{beat.price}</span>
              )}
              {playCount > 0 && (
                <span style={{ display: "inline-flex", alignItems: "center", gap: 4, background: "rgba(255,255,255,0.04)", border: "1px solid #2a2a2a", borderRadius: 20, padding: "3px 9px", color: "#888", fontSize: 10, fontWeight: 700 }}>
                  <svg width="9" height="9" viewBox="0 0 24 24" fill="#888"><path d="M5 3.5l15 8.5-15 8.5V3.5z"/></svg>
                  {fmtCount(playCount)}
                </span>
              )}
            </div>
          </div>
        </div>

        {/* Producer row */}
        <div style={{ display: "flex", alignItems: "center", gap: 10, padding: "12px 0", borderTop: "1px solid rgba(124,58,237,0.08)", borderBottom: "1px solid rgba(124,58,237,0.08)", marginBottom: 14 }}>
          <div style={{
            width: 36, height: 36, borderRadius: "50%", flexShrink: 0,
            background: "linear-gradient(135deg," + accentClr + "66," + accentClr2 + "33)",
            border: "1.5px solid " + accentClr + "88",
            display: "flex", alignItems: "center", justifyContent: "center", overflow: "hidden",
            boxShadow: "0 0 10px " + accentClr + "55",
          }}>
            {beat.producer_avatar
              ? <img src={beat.producer_avatar} alt={beat.producer} style={{ width: "100%", height: "100%", objectFit: "cover", display: "block" }} />
              : <span style={{ color: "white", fontWeight: 900, fontSize: 13 }}>{(beat.producer || "?")[0].toUpperCase()}</span>
            }
          </div>
          <div style={{ flex: 1, minWidth: 0 }}>
            <div style={{ color: accentClr, fontSize: 9, fontWeight: 800, letterSpacing: 1.5, textShadow: "0 0 6px " + accentClr + "55" }}>PRODUCER</div>
            <div style={{ display: "flex", alignItems: "center", gap: 5 }}>
              <span
                onClick={function(e) { e.stopPropagation(); if (onViewProfile && (beat.producer_username || beat.producer)) onViewProfile(beat.producer_username || beat.producer); }}
                style={{ color: "white", fontWeight: 700, fontSize: 13, cursor: onViewProfile && (beat.producer_username || beat.producer) ? "pointer" : "default" }}>
                {beat.producer || "Unknown"}
              </span>
              <VerifiedBadge size={13} />
            </div>
          </div>
          {beat.downloads > 0 && (
            <div style={{ display: "flex", alignItems: "center", gap: 4, color: "#555", fontSize: 11, flexShrink: 0 }}>
              <svg width="11" height="11" viewBox="0 0 24 24" fill="none" stroke="#555" strokeWidth="2.5" strokeLinecap="round"><path d="M12 3v13M6 11l6 6 6-6"/><path d="M4 20h16"/></svg>
              {beat.downloads}
            </div>
          )}
        </div>

        {/* BPM + Key + Description row */}
        {(beat.bpm > 0 || beat.key || beat.description) && (
          <div style={{ marginBottom: 12 }}>
            {(beat.bpm > 0 || beat.key) && (
              <div style={{ display: "flex", gap: 6, marginBottom: beat.description ? 8 : 0 }}>
                {beat.bpm > 0 && (
                  <span style={{ background: "rgba(6,182,212,0.1)", border: "1px solid rgba(6,182,212,0.25)", borderRadius: 20, padding: "3px 10px", fontSize: 10, color: "#06B6D4", fontWeight: 700 }}>
                    {beat.bpm} BPM
                  </span>
                )}
                {beat.key && (
                  <span style={{ background: "rgba(34,197,94,0.1)", border: "1px solid rgba(34,197,94,0.25)", borderRadius: 20, padding: "3px 10px", fontSize: 10, color: "#22C55E", fontWeight: 700 }}>
                    {beat.key}
                  </span>
                )}
              </div>
            )}
            {beat.description && (
              <div style={{ color: "#555", fontSize: 12, lineHeight: 1.5 }}>{beat.description}</div>
            )}
          </div>
        )}

        {/* Waveform player */}
        {beat.url && (
          <div style={{ marginBottom: 14 }}>
            <div style={{ display: "flex", alignItems: "center", gap: 12, background: "rgba(255,255,255,0.03)", borderRadius: 12, padding: "10px 14px", border: "1px solid #1e1e1e" }}>
              <button onClick={onTogglePreview} style={{ width: 36, height: 36, borderRadius: "50%", flexShrink: 0, background: previewing ? "rgba(99,91,255,0.2)" : "rgba(255,255,255,0.06)", border: "1.5px solid " + (previewing ? "#635BFF" : "#2a2a2a"), color: previewing ? "#635BFF" : "#aaa", display: "flex", alignItems: "center", justifyContent: "center", cursor: "pointer" }}>
                {previewing
                  ? <svg width="12" height="12" viewBox="0 0 24 24" fill="currentColor"><rect x="6" y="4" width="4" height="16"/><rect x="14" y="4" width="4" height="16"/></svg>
                  : <svg width="12" height="12" viewBox="0 0 24 24" fill="currentColor"><path d="M5 3.5l15 8.5-15 8.5V3.5z"/></svg>
                }
              </button>
              <div style={{ flex: 1, display: "flex", alignItems: "center", gap: 1.5, height: 28, overflow: "hidden" }}>
                {Array.from({ length: 40 }, function(_, i) {
                  var h = 20 + Math.sin(i * 0.8) * 12 + Math.sin(i * 1.7) * 6;
                  var filled = previewing && (i / 40) < (previewTime / 45);
                  return <div key={i} style={{ flex: 1, borderRadius: 2, height: Math.max(4, h) + "%", background: filled ? accentClr : "rgba(255,255,255,0.1)" }} />;
                })}
              </div>
              <span style={{ color: "#555", fontSize: 10, fontWeight: 700, flexShrink: 0, fontVariantNumeric: "tabular-nums" }}>
                {previewing ? progMins + ":" + progSecs + " / 0:45" : "0:45"}
              </span>
            </div>
            {audioEl}
          </div>
        )}

        {buyErr && <div style={{ color: "#F87171", fontSize: 12, marginBottom: 10 }}>{buyErr}</div>}
      </div>

      {/* CTA */}
      {isFree ? (
        <FreeBeatCTA beat={beat} user={user} />
      ) : ownedLease ? (
        /* OWNED state — user already purchased a lease for this beat.
           Replaces buy buttons across PWA / Safari / multi-device. */
        <div style={{ padding: "0 16px 16px" }}>
          <button
            onClick={function(){ onBuy && onBuy(ownedLease.tier === "premium" ? "premium" : "basic"); }}
            style={{
              width: "100%", borderRadius: 14, padding: "14px",
              background: ownedLease.tier === "premium"
                ? "linear-gradient(135deg,#C026D3 0%,#A855F7 35%,#7C3AED 70%,#3B82F6 100%)"
                : "linear-gradient(135deg,#3B82F6 0%,#6366F1 50%,#7C3AED 100%)",
              border: "1.5px solid " + (ownedLease.tier === "premium" ? "rgba(192,38,211,0.6)" : "rgba(124,58,237,0.6)"),
              color: "white", fontWeight: 900, fontSize: 14,
              cursor: "pointer", letterSpacing: 0.8,
              display: "flex", alignItems: "center", justifyContent: "center", gap: 10,
              boxShadow: ownedLease.tier === "premium"
                ? "0 4px 20px rgba(192,38,211,0.55), 0 0 36px rgba(168,85,247,0.4), inset 0 1px 0 rgba(255,255,255,0.3), inset 0 -2px 4px rgba(0,0,0,0.25)"
                : "0 4px 20px rgba(124,58,237,0.45), 0 0 32px rgba(59,130,246,0.35), inset 0 1px 0 rgba(255,255,255,0.25), inset 0 -2px 4px rgba(0,0,0,0.2)",
              textShadow: "0 1px 2px rgba(0,0,0,0.3)",
              position: "relative", overflow: "hidden",
            }}>
            <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="white" strokeWidth="3" strokeLinecap="round" style={{ filter: "drop-shadow(0 0 4px rgba(255,255,255,0.9))" }}>
              <polyline points="20 6 9 17 4 12"/>
            </svg>
            OWNED — {ownedLease.tier === "premium" ? "PREMIUM EXCLUSIVE" : "BASIC LEASE"}
          </button>
          <div style={{
            marginTop: 8, textAlign: "center",
            color: accentClr, fontSize: 10, fontWeight: 800, letterSpacing: 1,
            textShadow: "0 0 6px " + accentClr + "55",
          }}>
            TAP TO DOWNLOAD MP3
          </div>
        </div>
      ) : (
        <div style={{ padding: "0 16px 16px" }}>
          {(function() {
            var basicPrice   = Number(beat.basic_lease_price)   || 50;
            var premiumPrice = Number(beat.premium_lease_price) || 0;
            var premiumSold  = !!beat.premium_sold;
            var premiumAvailable = (premiumPrice >= 100 && premiumPrice <= 500 && !premiumSold);
            // BUSINESS RULE: once Premium is sold, the beat becomes exclusively
            // owned by the Premium buyer. Basic Lease purchases are also locked.
            // Any prior Basic leases on this beat are already voided server-side.
            var basicLocked = premiumSold;
            return (
              <>
                {/* Basic Lease — LED blue-cyan gradient, disabled once Premium has been sold */}
                <button onClick={function() { if (!basicLocked && !buyLoading) onBuy("basic"); }}
                  disabled={buyLoading || basicLocked} style={{
                  width: "100%", borderRadius: 14, padding: "14px",
                  background: (buyLoading || basicLocked)
                    ? "rgba(255,255,255,0.02)"
                    : "linear-gradient(135deg,#3B82F6 0%,#6366F1 50%,#7C3AED 100%)",
                  border: "1.5px solid " + ((buyLoading || basicLocked) ? "#222" : "rgba(124,58,237,0.6)"),
                  color: (buyLoading || basicLocked) ? "#444" : "white",
                  fontWeight: 900, fontSize: 14, cursor: (buyLoading || basicLocked) ? "not-allowed" : "pointer",
                  letterSpacing: 0.8,
                  display: "flex", alignItems: "center", justifyContent: "center", gap: 10,
                  boxShadow: (buyLoading || basicLocked)
                    ? "none"
                    : "0 4px 20px rgba(124,58,237,0.45), 0 0 32px rgba(59,130,246,0.35), inset 0 1px 0 rgba(255,255,255,0.25), inset 0 -2px 4px rgba(0,0,0,0.2)",
                  textShadow: (buyLoading || basicLocked) ? "none" : "0 1px 2px rgba(0,0,0,0.3)",
                  marginBottom: premiumPrice > 0 ? 10 : 0,
                  opacity: basicLocked ? 0.5 : 1,
                  position: "relative", overflow: "hidden",
                }}>
                  {!buyLoading && !basicLocked && (
                    <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="white" strokeWidth="2.5" strokeLinecap="round" style={{ filter: "drop-shadow(0 0 4px rgba(255,255,255,0.8))" }}>
                      <path d="M5 7a2 2 0 0 1 2-2h10a2 2 0 0 1 2 2v12l-7-3-7 3V7z"/>
                    </svg>
                  )}
                  {buyLoading ? "Loading..." : (basicLocked ? "UNAVAILABLE — Sold Exclusive" : "BASIC LEASE — £" + basicPrice)}
                </button>

                {/* Premium Lease — magenta-purple-blue LED gradient (exclusive, hottest CTA) */}
                {premiumPrice > 0 && (
                  <button
                    onClick={function() { if (premiumAvailable && !buyLoading) onBuy("premium"); }}
                    disabled={buyLoading || !premiumAvailable}
                    style={{
                      width: "100%", borderRadius: 14, padding: "14px",
                      background: (buyLoading || !premiumAvailable)
                        ? "rgba(255,255,255,0.02)"
                        : "linear-gradient(135deg,#C026D3 0%,#A855F7 35%,#7C3AED 70%,#3B82F6 100%)",
                      border: "1.5px solid " + ((buyLoading || !premiumAvailable) ? "#222" : "rgba(192,38,211,0.6)"),
                      color: (buyLoading || !premiumAvailable) ? "#444" : "white",
                      fontWeight: 900, fontSize: 14,
                      cursor: (buyLoading || !premiumAvailable) ? "not-allowed" : "pointer",
                      letterSpacing: 0.8,
                      display: "flex", alignItems: "center", justifyContent: "center", gap: 10,
                      boxShadow: (buyLoading || !premiumAvailable)
                        ? "none"
                        : "0 4px 20px rgba(192,38,211,0.55), 0 0 36px rgba(168,85,247,0.4), inset 0 1px 0 rgba(255,255,255,0.3), inset 0 -2px 4px rgba(0,0,0,0.25)",
                      textShadow: (buyLoading || !premiumAvailable) ? "none" : "0 1px 2px rgba(0,0,0,0.3)",
                      opacity: premiumSold ? 0.5 : 1,
                      position: "relative", overflow: "hidden",
                    }}>
                    {!buyLoading && premiumAvailable && (
                      <svg width="14" height="14" viewBox="0 0 24 24" fill="white" style={{ filter: "drop-shadow(0 0 4px rgba(255,255,255,0.9))" }}>
                        <path d="M12 2l2.4 6.5L21 9l-5 4.5 1.5 7L12 17l-5.5 3.5L8 13.5 3 9l6.6-0.5L12 2z"/>
                      </svg>
                    )}
                    {premiumSold ? "PREMIUM — SOLD OUT" : ("PREMIUM EXCLUSIVE — £" + premiumPrice)}
                  </button>
                )}
              </>
            );
          })()}
          <div style={{ display: "flex", marginTop: 14, borderTop: "1px solid rgba(124,58,237,0.1)", paddingTop: 12 }}>
            {[
              { icon: <svg width="13" height="13" viewBox="0 0 24 24" fill="none" stroke="#7C3AED" strokeWidth="2" strokeLinecap="round"><path d="M3 9l9-7 9 7v11a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2z"/><polyline points="9 22 9 12 15 12 15 22"/></svg>, title: "MP3 LEASE", sub: "Instant delivery." },
              { icon: <svg width="13" height="13" viewBox="0 0 24 24" fill="none" stroke="#7C3AED" strokeWidth="2" strokeLinecap="round"><circle cx="12" cy="12" r="10"/><polyline points="12 6 12 12 16 14"/></svg>, title: "UNLIMITED STREAMS", sub: "No expiry date." },
              { icon: <svg width="13" height="13" viewBox="0 0 24 24" fill="none" stroke="#7C3AED" strokeWidth="2" strokeLinecap="round"><polyline points="20 12 20 22 4 22 4 12"/><rect x="2" y="7" width="20" height="5"/><line x1="12" y1="22" x2="12" y2="7"/><path d="M12 7H7.5a2.5 2.5 0 0 1 0-5C11 2 12 7 12 7z"/><path d="M12 7h4.5a2.5 2.5 0 0 0 0-5C13 2 12 7 12 7z"/></svg>, title: "COMMERCIAL USE", sub: "Monetise your music." },
            ].map(function(item, i) {
              return (
                <div key={i} style={{ flex: 1, textAlign: "center", padding: "0 4px", borderRight: i < 2 ? "1px solid rgba(124,58,237,0.1)" : "none" }}>
                  <div style={{ display: "flex", justifyContent: "center", marginBottom: 4, filter: "drop-shadow(0 0 4px rgba(124,58,237,0.5))" }}>{item.icon}</div>
                  <div style={{ color: "#888", fontSize: 8, fontWeight: 800, letterSpacing: 0.5 }}>{item.title}</div>
                  <div style={{ color: "#444", fontSize: 8, marginTop: 1 }}>{item.sub}</div>
                </div>
              );
            })}
          </div>
        </div>
      )}
    </div>
  );
}

function BeatLeaseCard({ beat, user, onViewProfile }) {
  const [loading,     setLoading]     = useState(false);
  const [err,         setErr]         = useState("");
  const [previewing,  setPreviewing]  = useState(false);
  const [previewTime, setPreviewTime] = useState(0);
  const [sheetOpen,   setSheetOpen]   = useState(false); // opens contract preview before Stripe
  // Reopen sheet automatically if user cancelled out of Stripe checkout for
  // THIS beat. The cancel-restore effect dispatches bf-reopen-sheet with
  // detail.beat_id; we open if it matches.
  React.useEffect(function() {
    function onReopen(e) {
      if (!e || !e.detail || e.detail.beat_id !== beat.id) return;
      // De-duplicate: only the first matching listener should open its sheet.
      // Subsequent listeners for the same beat (e.g. the same beat appearing
      // on multiple screens like Trending + Profile) must NOT open too.
      if (window.__bf_reopen_consumed__ === beat.id) return;
      window.__bf_reopen_consumed__ = beat.id;
      setSheetOpen(true);
    }
    window.addEventListener("bf-reopen-sheet", onReopen);
    return function() { window.removeEventListener("bf-reopen-sheet", onReopen); };
  }, [beat.id]);
  const audioRef = React.useRef(null);
  const timerRef = React.useRef(null);
  const seekedRef = React.useRef(false);
  const isFree   = !beat.price || beat.price === "free" || beat.price === "£0" || beat.price === "0" || beat.price === "£0.00" || beat.price === "0.00";
  var previewId  = React.useRef("lease_" + beat.id);

  function startPreview() {
    if (previewing) { stopPreview(); return; }
    seekedRef.current = false;
    startGlobalPreview(previewId.current);
    setPreviewing(true); setPreviewTime(0);
    recordPlay(beat.id);
  }
  function stopPreview() {
    clearGlobalPreview(previewId.current);
    setPreviewing(false); setPreviewTime(0); clearInterval(timerRef.current);
    cancelPlayTimer(beat.id);
    if (audioRef.current) { audioRef.current.pause(); audioRef.current.currentTime = 0; }
  }
  useGlobalPreviewStop(previewId.current, React.useCallback(function() {
    setPreviewing(false); setPreviewTime(0); clearInterval(timerRef.current);
    cancelPlayTimer(beat.id);
    if (audioRef.current) { audioRef.current.pause(); audioRef.current.currentTime = 0; }
  }, [beat.id]));
  function onAudioPlay() {
    clearInterval(timerRef.current);
    var start = Date.now() - (previewTime * 1000);
    timerRef.current = setInterval(function() {
      var el = (Date.now() - start) / 1000;
      setPreviewTime(Math.min(el, 45));
      if (el >= 45) { clearInterval(timerRef.current); stopPreview(); }
    }, 100);
  }
  function onTimeUpdate(e) {
    var el = e.target, st = parseFloat(el.dataset.startTime || 0);
    if (el.currentTime >= st + 45) { el.pause(); stopPreview(); }
    if (el.currentTime < st) el.currentTime = st;
  }
  React.useEffect(function() { return function() { clearInterval(timerRef.current); }; }, []);

  // Contract-first flow: tapping Buy opens the preview sheet (CompactBeatActionSheet).
  // Stripe is only invoked when the user picks a tier inside the sheet.
  function handleBuy(tier) {
    // Premium leases require an Artist Pro or Producer Pro subscription.
    // Guests trying to buy premium also get the upgrade prompt — they
    // need to be signed in AND on a paid plan.
    if (tier === "premium") {
      if (!user || (!user.isArtistPro && !user.isPro)) {
        requireUpgrade("buy");
        return;
      }
    } else {
      // Basic tier (£50): guests get the quick-signup modal so they can
      // create an account and continue to Stripe. Free users can buy
      // basic leases directly with no Pro requirement.
      if (!user) {
        window.dispatchEvent(new CustomEvent("bf:quickSignup", { detail: {
          intent: "buyBasic", beat: beat,
          resume: function() { handleBuy(tier); },
        }}));
        return;
      }
    }
    if (beat.premium_sold) {
      setErr("This beat has been bought exclusively and is no longer available.");
      return;
    }
    setErr("");
    setSheetOpen(true);
  }

  return (
    <>
    <NewBeatCardShell
      beat={beat}
      previewing={previewing}
      previewTime={previewTime}
      onTogglePreview={startPreview}
      onDownload={function(){ downloadMp3(beat.url, beat.title, beat.id); }}
      onBuy={handleBuy}
      buyLoading={loading}
      buyErr={err}
      user={user}
      onViewProfile={onViewProfile}
      audioEl={previewing && beat.url ? (
        <audio ref={audioRef} src={beat.url} autoPlay data-start-time={String(beat.preview_start || 0)}
          onPlay={function(){ onAudioPlay(); if (audioRef.current && (beat.preview_start||0) > 0 && !seekedRef.current) { audioRef.current.currentTime = beat.preview_start; seekedRef.current = true; } }}
          onPause={function(){ clearInterval(timerRef.current); }}
          onTimeUpdate={onTimeUpdate} onEnded={stopPreview} />
      ) : null}
    />
    {sheetOpen && (
      <CompactBeatActionSheet beat={beat} user={user} compact={true} onClose={function(){ setSheetOpen(false); }} />
    )}
    </>
  );
}

// =============================================================================
function ProducerBeatsScreen({ onPlay, savedIds, onSave, user }) {
  const [beats,   setBeats]   = useState([]);
  const [leases,  setLeases]  = useState([]);
  const [loading, setLoading] = useState(true);
  const [error,   setError]   = useState(null);

  useEffect(() => {
    Promise.all([
      apiFetch("/api/producer/beats"),
      user ? apiFetch("/api/producer/my-leases").catch(() => []) : Promise.resolve([]),
    ]).then(([b, l]) => {
      setBeats(b);
      setLeases(l);
      setLoading(false);
    }).catch(e => { setError(e.message); setLoading(false); });
  }, [user]);

  // Update beat in-place when producer edits description/bpm/key/title
  useBeatUpdatedListener(React.useCallback(function(detail) {
    setBeats(function(prev) {
      return prev.map(function(b) {
        return b.id === detail.beatId ? Object.assign({}, b, detail.patch) : b;
      });
    });
  }, []));



  const header = (
    <div style={{ padding: "20px 0 16px" }}>
      <div style={{ background: "linear-gradient(135deg,#1C1917,rgba(245,158,11,0.2))", borderRadius: 16, padding: "24px 20px", marginBottom: 20, border: "1.5px solid rgba(245,158,11,0.3)" }}>
        <div style={{ color: "#F59E0B", fontSize: 13, fontWeight: 800, marginBottom: 6 }}><AppIcon id="note" size={20}/> PRODUCER BEATS</div>
        <div style={{ color: "white", fontSize: 26, fontWeight: 800, fontFamily: "'Bebas Neue',sans-serif", letterSpacing: 1 }}>
          Download MP3s
        </div>
        <div style={{ color: "#aaa", fontSize: 13, marginTop: 4 }}>Beats uploaded by verified producers</div>
      </div>
    </div>
  );

  if (loading) return (
    <div style={{ padding: "0 16px 100px" }}>
      {header}
      <BFLoader type="spinner" text="LOADING BEATS...PLEASE BE PATIENT." />
    </div>
  );

  if (error) return (
    <div style={{ padding: "0 16px 100px" }}>
      {header}
      <div style={{ background: "rgba(220,38,38,0.08)", border: "1px solid rgba(220,38,38,0.25)", borderRadius: 14, padding: 20, textAlign: "center" }}>
        <div style={{ color: "#F87171", fontWeight: 700, fontSize: 15 }}>Could not load beats</div>
        <div style={{ color: "#888", fontSize: 13, marginTop: 8 }}>{error}</div>
      </div>
    </div>
  );

  if (beats.length === 0) return (
    <div style={{ padding: "0 16px 100px" }}>
      {header}
      <div style={{ textAlign: "center", padding: "60px 0" }}>
        <div style={{ fontSize: 48, marginBottom: 16 }}><AppIcon id="knobs" size={20}/></div>
        <div style={{ color: "white", fontWeight: 800, fontSize: 17, marginBottom: 8 }}>
          No beats have been uploaded yet.
        </div>
        <div style={{ color: "#555", fontSize: 13, lineHeight: 1.7 }}>
          Producer Pro members can upload beats<br />from their Profile tab.
        </div>
      </div>
    </div>
  );

  return (
    <div style={{ padding: "0 16px 100px" }}>
      {header}

      {leases.length > 0 && (
        <div style={{ marginBottom: 20 }}>
          <div style={{ color: "white", fontWeight: 800, fontSize: 16, marginBottom: 12 }}>Your Purchased Leases</div>
          {leases.map(lease => {
            return (
            <div key={lease.id} style={{ background: "#111", borderRadius: 14, padding: 16, marginBottom: 12, border: "1px solid rgba(34,197,94,0.3)" }}>
              <div style={{ color: "white", fontWeight: 700, fontSize: 14, marginBottom: 4 }}>{lease.beat_title}</div>
              <div style={{ color: "#666", fontSize: 12, marginBottom: 12 }}>By {lease.producer} • {lease.price} • {new Date(lease.purchased_at).toLocaleDateString()}</div>
              <button onClick={function() { downloadBeatMp3({ id: lease.beat_id, url: lease.beat_url, title: lease.beat_title }); }}
                style={{ width: "100%", borderRadius: 12, padding: "13px", border: "none", background: "linear-gradient(135deg,#22C55E,#16A34A)", color: "white", fontWeight: 800, fontSize: 14, display:"flex", alignItems:"center", justifyContent:"center", gap:8, marginBottom: 8, cursor: "pointer" }}>
                <svg width="15" height="15" viewBox="0 0 24 24" fill="none" stroke="white" strokeWidth="2.5" strokeLinecap="round"><path d="M12 3v13M6 11l6 6 6-6"/><path d="M4 20h16"/></svg>
                Save Lease MP3 to Device
              </button>
              <LeaseContractButton lease={lease} />
            </div>
            );
          })}
          <div style={{ height: 1, background: "#1a1a1a", marginBottom: 20 }} />
          <div style={{ color: "#888", fontWeight: 700, fontSize: 14, marginBottom: 12 }}>All Available Beats</div>
        </div>
      )}

      {beats.map(beat => (
        <BeatLeaseCard key={beat.id} beat={beat} user={user} />
      ))}
    </div>
  );
}

// =============================================================================
// TRENDING SCREEN
// =============================================================================
function TrendingScreen({ savedIds, onSave, onPlay, onViewProfile, user }) {
  const [mode,      setMode]      = useState("producer"); // "producer" | "artist"
  const [trending,  setTrending]  = useState([]);
  const [rising,    setRising]    = useState([]);
  const [fresh,     setFresh]     = useState([]);
  const [tLoading,  setTLoading]  = useState(true);
  const [rLoading,  setRLoading]  = useState(true);
  const [fLoading,  setFLoading]  = useState(true);
  // Artist section
  const [artTrend,  setArtTrend]  = useState([]);
  const [artRising, setArtRising] = useState([]);
  const [artFresh,  setArtFresh]  = useState([]);
  const [atLoading, setAtLoading] = useState(true);
  const [arLoading, setArLoading] = useState(true);
  const [afLoading, setAfLoading] = useState(true);
  const [artistLoaded, setArtistLoaded] = useState(false);
  // Sheet hoisted to screen level — rendering it inside ProducerCard puts it
  // inside the horizontal-scroll carousel (overflow-x:auto + WebkitOverflow-
  // Scrolling:touch), which on iOS Safari traps position:fixed children so
  // they align to the carousel's scroll area instead of the viewport.
  const [sheetBeat, setSheetBeat] = useState(null);
  // Allow bf-reopen-sheet events (fired after Stripe cancel) to reopen here too.
  React.useEffect(function() {
    function onReopen(e) {
      if (!e || !e.detail || !e.detail.beat_id) return;
      // De-duplicate: only first matching listener opens sheet
      if (window.__bf_reopen_consumed__ === e.detail.beat_id) return;
      var b = (rising || []).find(function(x){ return x.id === e.detail.beat_id; })
           || (fresh   || []).find(function(x){ return x.id === e.detail.beat_id; });
      if (b) {
        window.__bf_reopen_consumed__ = e.detail.beat_id;
        setSheetBeat(b);
      }
    }
    window.addEventListener("bf-reopen-sheet", onReopen);
    return function() { window.removeEventListener("bf-reopen-sheet", onReopen); };
  }, [rising, fresh]);

  useEffect(() => {
    // Daily rotation seed: changes once per calendar day (UTC). The backend
    // can use this to vary which trending/fresh results it returns each day
    // (and to cache per-day, saving YouTube API quota). Also used as a simple
    // cache-buster so a new day pulls fresh content.
    const today = new Date();
    const daySeed = today.getUTCFullYear() + "-" + (today.getUTCMonth()+1) + "-" + today.getUTCDate();
    const dayNum = Math.floor(Date.now() / 86400000); // integer day index

    // Trending — ask for the real most-popular/most-viewed chart, rotated daily.
    apiFetch("/api/youtube/trending?day=" + encodeURIComponent(daySeed))
      .then(d => { setTrending(d.beats || []); setTLoading(false); })
      .catch(() => setTLoading(false));

    apiFetch("/api/producer/beats")
      .then(d => { setRising(d || []); setRLoading(false); })
      .catch(() => {
        apiFetch("/api/youtube/search?artist=type+beat&order=date&page=2&filter_title=false&max=10&day=" + encodeURIComponent(daySeed))
          .then(d => { setRising(d.beats || []); setRLoading(false); })
          .catch(() => setRLoading(false));
      });

    // Fresh Uploads — order by upload DATE (newest first), rotated daily.
    // No hardcoded year. The backend should apply order=date + a recent
    // published_after window so these are genuinely new uploads.
    apiFetch("/api/youtube/search?artist=type+beat&order=date&page=1&filter_title=false&max=12&day=" + encodeURIComponent(daySeed))
      .then(d => { setFresh(d.beats || []); setFLoading(false); })
      .catch(() => setFLoading(false));
  }, []);

  // Lazily load the Artist section the first time it's opened.
  React.useEffect(function(){
    if (mode !== "artist" || artistLoaded) return;
    setArtistLoaded(true);
    const today = new Date();
    const daySeed = today.getUTCFullYear() + "-" + (today.getUTCMonth()+1) + "-" + today.getUTCDate();

    apiFetch("/api/youtube/artist-trending?day=" + encodeURIComponent(daySeed))
      .then(d => { setArtTrend((d.beats || []).map(function(b){ return Object.assign({}, b, { isSong: true }); })); setAtLoading(false); })
      .catch(() => setAtLoading(false));

    apiFetch("/api/posts/rising-artists?limit=20")
      .then(d => { setArtRising((d && d.artists) || []); setArLoading(false); })
      .catch(() => setArLoading(false));

    apiFetch("/api/youtube/artist-fresh?day=" + encodeURIComponent(daySeed))
      .then(d => { setArtFresh((d.beats || []).map(function(b){ return Object.assign({}, b, { isSong: true }); })); setAfLoading(false); })
      .catch(() => setAfLoading(false));
  }, [mode, artistLoaded]);

  // Update producer beats in-place when a producer edits description/bpm/key/title
  useBeatUpdatedListener(React.useCallback(function(detail) {
    setRising(function(prev) {
      return prev.map(function(b) {
        return b.id === detail.beatId ? Object.assign({}, b, detail.patch) : b;
      });
    });
  }, []));

  // Horizontal carousel card - compact version for side-scroll
  const CarouselCard = ({ beat }) => {
    const isSaved = savedIds.has(beat.videoId);
    return (
      <div onClick={() => onPlay(beat)} className="bf-card bf-press bf-breathe" style={{
        flexShrink: 0, width: 170, cursor: "pointer", position: "relative",
        background: "linear-gradient(180deg, rgba(20,15,32,0.92), rgba(10,8,20,0.95))",
        borderRadius: 22, border: "1px solid rgba(176,38,255,0.35)",
        overflow: "hidden",
        boxShadow: "0 10px 30px rgba(0,0,0,0.55), 0 0 22px rgba(176,38,255,0.18), inset 0 1px 0 rgba(255,255,255,0.05)",
        backdropFilter: "blur(6px)", WebkitBackdropFilter: "blur(6px)",
      }}>
        <span aria-hidden="true" className="bf-shimmer" style={{ zIndex: 3 }} />
        <div style={{ position: "relative", height: 112, overflow: "hidden" }}>
          <img
            src={beat.thumbnail || ("https://img.youtube.com/vi/" + beat.videoId + "/hqdefault.jpg")}
            alt={beat.title}
            style={{ width: "100%", height: "100%", objectFit: "cover", display: "block" }}
          />
          <div style={{ position: "absolute", inset: 0, background: "linear-gradient(to top,rgba(5,5,5,0.7),transparent 55%)" }} />
          {/* holographic sheen */}
          <div aria-hidden="true" style={{ position: "absolute", inset: 0, background: "linear-gradient(120deg, transparent 30%, rgba(0,213,255,0.10) 48%, rgba(176,38,255,0.12) 55%, transparent 72%)", pointerEvents: "none" }} />
          <div style={{ position: "absolute", inset: 0, display: "flex", alignItems: "center", justifyContent: "center", pointerEvents: "none" }}>
            <svg width="54" height="38" viewBox="0 0 72 52" fill="none" xmlns="http://www.w3.org/2000/svg">
              <defs>
                <filter id="neon-carousel" x="-40%" y="-40%" width="180%" height="180%">
                  <feGaussianBlur stdDeviation="3" result="blur1"/>
                  <feGaussianBlur stdDeviation="8" result="blur2"/>
                  <feMerge><feMergeNode in="blur2"/><feMergeNode in="blur1"/><feMergeNode in="SourceGraphic"/></feMerge>
                </filter>
                <linearGradient id="ng-carousel" x1="0%" y1="0%" x2="100%" y2="0%">
                  <stop offset="0%" stopColor="#B026FF"/>
                  <stop offset="100%" stopColor="#00D5FF"/>
                </linearGradient>
              </defs>
              <rect x="3" y="3" width="66" height="46" rx="8" stroke="url(#ng-carousel)" strokeWidth="1.5" fill="black" filter="url(#neon-carousel)" opacity="0.9"/>
              <polygon points="29,16 29,36 48,26" stroke="url(#ng-carousel)" strokeWidth="1.5" fill="none" strokeLinejoin="round" filter="url(#neon-carousel)" opacity="0.9"/>
            </svg>
          </div>
          <button className="bf-save" onClick={e => { e.stopPropagation(); onSave(beat); }} style={{
            position: "absolute", top: 8, right: 8, width: 30, height: 30,
            borderRadius: "50%", border: "1px solid " + (isSaved ? "rgba(176,38,255,0.7)" : "rgba(255,255,255,0.12)"), cursor: "pointer",
            display: "flex", alignItems: "center", justifyContent: "center",
            background: isSaved ? "rgba(176,38,255,0.9)" : "rgba(5,5,5,0.55)",
            color: isSaved ? "white" : "rgba(255,255,255,0.7)",
            boxShadow: isSaved ? "0 0 12px rgba(176,38,255,0.6)" : "none",
            backdropFilter: "blur(4px)", WebkitBackdropFilter: "blur(4px)",
          }}>
            <AppIcon id={isSaved ? "star" : "plus"} size={14} />
          </button>
        </div>
        <div style={{ padding: "11px 13px 13px" }}>
          <div style={{ color: "#f3eeff", fontSize: 12, fontWeight: 700, lineHeight: 1.4, marginBottom: 5,
            overflow: "hidden", display: "-webkit-box", WebkitLineClamp: 2, WebkitBoxOrient: "vertical" }}>
            {beat.title}
          </div>
          <div style={{ color: "#7a7296", fontSize: 10, fontWeight: 600, whiteSpace: "nowrap", overflow: "hidden", textOverflow: "ellipsis" }}>
            {beat.channel}
          </div>
        </div>
      </div>
    );
  };

  // Rising producer card — full card in horizontal scroll
  const ProducerCard = ({ beat, user }) => {
    const [prev,       setPrev]       = React.useState(false);
    const [pTime,      setPTime]      = React.useState(0);
    const [buyLoading, setBuyLoading] = React.useState(false);
    const [buyErr,     setBuyErr]     = React.useState("");
    const aRef = React.useRef(null);
    const tRef = React.useRef(null);
    const seekedRef = React.useRef(false);
    const isFree = !beat.price || beat.price === "free" || beat.price === "£0" || beat.price === "0" || beat.price === "£0.00" || beat.price === "0.00";
    var prevId = React.useRef("producer_" + beat.id);

    function startPrev() {
      if (prev) { stopPrev(); return; }
      seekedRef.current = false;
      startGlobalPreview(prevId.current);
      setPrev(true); setPTime(0);
      recordPlay(beat.id);
    }
    function stopPrev() {
      clearGlobalPreview(prevId.current);
      setPrev(false); setPTime(0); clearInterval(tRef.current);
      cancelPlayTimer(beat.id);
      if (aRef.current) { aRef.current.pause(); aRef.current.currentTime = 0; }
    }
    useGlobalPreviewStop(prevId.current, React.useCallback(function() {
      setPrev(false); setPTime(0); clearInterval(tRef.current);
      cancelPlayTimer(beat.id);
      if (aRef.current) { aRef.current.pause(); aRef.current.currentTime = 0; }
    }, [beat.id]));
    function onPlay() {
      clearInterval(tRef.current);
      var start = Date.now();
      tRef.current = setInterval(function() {
        var el = (Date.now() - start) / 1000;
        setPTime(Math.min(el, 45));
        if (el >= 45) { clearInterval(tRef.current); stopPrev(); }
      }, 100);
    }
    function onTimeUpdate(e) {
      var el = e.target, st = parseFloat(el.dataset.startTime || 0);
      if (el.currentTime >= st + 45) { el.pause(); stopPrev(); }
      if (el.currentTime < st) el.currentTime = st;
    }
    // Contract-first flow: tapping Buy hoists the sheet to TrendingScreen
    // level so it renders OUTSIDE the horizontal carousel and behaves as
    // proper position:fixed (instead of being trapped inside the carousel's
    // scroll context, which iOS Safari treats as a non-viewport ancestor).
    function handleBuy(tier) {
      if (tier === "premium") {
        if (!user || (!user.isArtistPro && !user.isPro)) {
          requireUpgrade("buy");
          return;
        }
      } else {
        if (!user) {
          window.dispatchEvent(new CustomEvent("bf:quickSignup", { detail: {
            intent: "buyBasic", beat: beat,
            resume: function() { handleBuy(tier); },
          }}));
          return;
        }
      }
      if (beat.premium_sold) {
        setBuyErr("This beat has been bought exclusively and is no longer available.");
        return;
      }
      setBuyErr("");
      setSheetBeat(beat);
    }
    React.useEffect(function() { return function() { clearInterval(tRef.current); }; }, []);

    return (
      <div className="bf-press bf-breathe" style={{
        flexShrink: 0, width: 170, height: 174,
        background: "linear-gradient(165deg, rgba(20,15,32,0.92) 0%, rgba(10,8,20,0.95) 60%, rgba(8,6,18,0.97) 100%)",
        borderRadius: 22, overflow: "hidden",
        border: "1px solid rgba(176,38,255,0.38)",
        boxShadow: "0 12px 32px rgba(0,0,0,0.6), 0 0 22px rgba(176,38,255,0.2), inset 0 1px 0 rgba(255,255,255,0.05)",
        position: "relative",
        display: "flex", flexDirection: "column",
        backdropFilter: "blur(6px)", WebkitBackdropFilter: "blur(6px)",
      }}>
        <span aria-hidden="true" className="bf-shimmer" style={{ zIndex: 4 }} />
        {/* LED top edge */}
        <div style={{
          height: 2, flexShrink: 0,
          background: isFree
            ? "linear-gradient(90deg,transparent,#B026FF,#00D5FF,transparent)"
            : "linear-gradient(90deg,transparent,#7C3AED,#3B82F6,transparent)",
          boxShadow: isFree ? "0 0 8px rgba(192,38,211,0.7)" : "0 0 8px rgba(124,58,237,0.7)",
        }} />

        {/* Top half — gradient background with producer avatar fade-in,
            big play button overlay (tap = preview). FREE / £price badge
            sits top-right corner. */}
        <div
          onClick={startPrev}
          style={{
            flex: 1, position: "relative", overflow: "hidden", cursor: "pointer",
            background: isFree
              ? "linear-gradient(135deg,#1a0a2e 0%,#2a0a4a 50%,#3b0070 100%)"
              : "linear-gradient(135deg,#0f0a1f 0%,#1e1a45 50%,#1E40AF 100%)",
            display: "flex", alignItems: "center", justifyContent: "center",
          }}>
          {beat.beat_artwork && (
            <img src={beat.beat_artwork} alt=""
              style={{ width: "100%", height: "100%", objectFit: "cover", opacity: 0.65 }} />
          )}
          <div style={{ position: "absolute", inset: 0,
            background: "linear-gradient(to top,rgba(0,0,0,0.75),transparent 55%)" }} />

          {/* Play / pause button — large, centered */}
          <div style={{ position: "absolute", inset: 0,
            display: "flex", alignItems: "center", justifyContent: "center" }}>
            <div style={{
              width: 44, height: 44, borderRadius: "50%",
              background: prev
                ? "linear-gradient(135deg,#C026D3,#7C3AED)"
                : "rgba(0,0,0,0.55)",
              border: "1.5px solid " + (prev ? "rgba(192,38,211,0.8)" : "rgba(255,255,255,0.25)"),
              display: "flex", alignItems: "center", justifyContent: "center",
              boxShadow: prev
                ? "0 0 16px rgba(192,38,211,0.7)"
                : "0 4px 12px rgba(0,0,0,0.5)",
              color: "white",
            }}>
              {prev
                ? <svg width="14" height="14" viewBox="0 0 24 24" fill="currentColor"><rect x="6" y="4" width="4" height="16"/><rect x="14" y="4" width="4" height="16"/></svg>
                : <svg width="14" height="14" viewBox="0 0 24 24" fill="currentColor"><path d="M5 3.5l15 8.5-15 8.5V3.5z"/></svg>
              }
            </div>
          </div>

          {/* FREE / price badge — top-right */}
          <div style={{
            position: "absolute", top: 6, right: 6,
            background: isFree
              ? "linear-gradient(135deg,#C026D3,#7C3AED)"
              : "linear-gradient(135deg,#7C3AED,#3B82F6)",
            color: "white", fontSize: 9, fontWeight: 900,
            padding: "3px 8px", borderRadius: 10, letterSpacing: 0.5,
            boxShadow: "0 2px 6px rgba(0,0,0,0.5)",
            textShadow: "0 1px 1px rgba(0,0,0,0.3)",
          }}>
            {isFree ? "FREE" : (beat.basic_lease_price ? "£" + beat.basic_lease_price : (beat.price || "PAID"))}
          </div>

          {/* Preview progress bar — only visible while previewing */}
          {prev && (
            <div style={{
              position: "absolute", bottom: 0, left: 0, right: 0,
              height: 2, background: "rgba(255,255,255,0.1)",
            }}>
              <div style={{
                height: "100%", width: ((pTime / 45) * 100) + "%",
                background: "linear-gradient(90deg,#C026D3,#7C3AED,#3B82F6)",
                boxShadow: "0 0 6px rgba(192,38,211,0.6)",
                transition: "width 0.1s linear",
              }} />
            </div>
          )}

          {/* Title (bottom of thumbnail area) */}
          <div style={{
            position: "absolute", bottom: 0, left: 0, right: 0,
            padding: "0 8px 6px",
          }}>
            <div style={{
              color: "white", fontSize: 11, fontWeight: 800, lineHeight: 1.2,
              overflow: "hidden", display: "-webkit-box",
              WebkitLineClamp: 1, WebkitBoxOrient: "vertical",
              textShadow: "0 1px 2px rgba(0,0,0,0.7)",
            }}>{beat.title || "Untitled"}</div>
            <div
              onClick={function(e) {
                e.stopPropagation();
                const handle = beat.producer_username || beat.producer;
                if (onViewProfile && handle) onViewProfile(handle);
              }}
              style={{
                color: "#A78BFA", fontSize: 9, fontWeight: 600, marginTop: 1,
                overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap",
                textShadow: "0 1px 2px rgba(0,0,0,0.7)",
                cursor: (onViewProfile && (beat.producer_username || beat.producer)) ? "pointer" : "default",
                // Tappable bit — extend hit area slightly so a small finger
                // can reliably land on it without firing the play button.
                padding: "2px 0",
                position: "relative", zIndex: 2,
              }}>@{beat.producer_username || beat.producer || "Producer"}</div>
          </div>
        </div>

        {/* Audio element (hidden) */}
        {prev && beat.url && (
          <audio ref={aRef} src={beat.url} autoPlay data-start-time={String(beat.preview_start || 0)}
            onPlay={function(){ onPlay(); if (aRef.current && (beat.preview_start||0) > 0 && !seekedRef.current) { aRef.current.currentTime = beat.preview_start; seekedRef.current = true; } }}
            onPause={function(){ clearInterval(tRef.current); }}
            onTimeUpdate={onTimeUpdate} onEnded={stopPrev} />
        )}

        {/* Bottom CTA strip — opens the full action sheet for tier selection */}
        <button
          onClick={function() {
            if (beat.premium_sold && (!user || (user.id !== beat.premium_sold_to && user.id !== beat.producer_id))) {
              setBuyErr("Sold exclusively");
              return;
            }
            setSheetBeat(beat);
          }}
          style={{
            flexShrink: 0, width: "100%", padding: "9px",
            background: isFree
              ? "linear-gradient(135deg,#B026FF 0%,#A855F7 50%,#7D3CFF 100%)"
              : "linear-gradient(135deg,#7D3CFF 0%,#9333EA 50%,#B026FF 100%)",
            border: "none",
            color: "white", fontWeight: 900, fontSize: 10, letterSpacing: 0.8,
            cursor: "pointer",
            borderTop: "1px solid rgba(255,255,255,0.12)",
            display: "flex", alignItems: "center", justifyContent: "center", gap: 5,
            textShadow: "0 1px 2px rgba(0,0,0,0.35)",
            boxShadow: "inset 0 1px 0 rgba(255,255,255,0.22), 0 -2px 14px rgba(176,38,255,0.4)",
          }}>
          {isFree ? (
            <>
              <svg width="10" height="10" viewBox="0 0 24 24" fill="none" stroke="white" strokeWidth="2.5" strokeLinecap="round"><path d="M12 3v13M6 11l6 6 6-6"/><path d="M4 20h16"/></svg>
              GET FREE
            </>
          ) : (
            <>
              <svg width="10" height="10" viewBox="0 0 24 24" fill="none" stroke="white" strokeWidth="2.5" strokeLinecap="round"><path d="M14 2H6a2 2 0 0 0-2 2v16a2 2 0 0 0 2 2h12a2 2 0 0 0 2-2V8z"/><polyline points="14 2 14 8 20 8"/></svg>
              BUY LEASE
            </>
          )}
        </button>
      </div>
    );
  };

  // Rising Artists card — a music/video release from an Artist Pro member.
  const ArtistPostCard = ({ post, onViewProfile }) => {
    const img = post.thumbnail || "";
    const isVideo = post.type === "video";
    const handle = post.username || "";
    const verified = post.plan === "artist" || post.plan === "producer";
    const openProfile = function(e){ if (e) e.stopPropagation(); if (onViewProfile && handle) onViewProfile(handle); };
    const openRelease = function(){
      // Music post → open Spotify; video → open the video URL. Both external.
      const url = post.spotifyUrl || post.videoUrl || "";
      if (url) { try { window.open(url, "_blank"); } catch(_){} }
      else openProfile();
    };
    return (
      <div onClick={openRelease} className="bf-press bf-breathe" style={{
        flexShrink: 0, width: 170, cursor: "pointer", position: "relative",
        background: "linear-gradient(180deg, rgba(20,15,32,0.92), rgba(10,8,20,0.95))",
        borderRadius: 22, border: "1px solid rgba(0,213,255,0.3)",
        overflow: "hidden",
        boxShadow: "0 10px 30px rgba(0,0,0,0.55), 0 0 22px rgba(0,213,255,0.16), inset 0 1px 0 rgba(255,255,255,0.05)",
        backdropFilter: "blur(6px)", WebkitBackdropFilter: "blur(6px)",
      }}>
        <span aria-hidden="true" className="bf-shimmer" style={{ zIndex: 3 }} />
        <div style={{ position: "relative", height: 130, overflow: "hidden", background: "linear-gradient(135deg,#1a1530,#14102a)" }}>
          {img
            ? <img src={img} alt="" style={{ width: "100%", height: "100%", objectFit: "cover", display: "block" }} />
            : <div style={{ position: "absolute", inset: 0, display: "flex", alignItems: "center", justifyContent: "center", color: "#00D5FF" }}><AppIcon id={isVideo ? "video" : "music"} size={36} /></div>}
          <div style={{ position: "absolute", inset: 0, background: "linear-gradient(to top,rgba(5,5,5,0.7),transparent 55%)" }} />
          <div aria-hidden="true" style={{ position: "absolute", inset: 0, background: "linear-gradient(120deg, transparent 30%, rgba(0,213,255,0.10) 48%, rgba(176,38,255,0.12) 55%, transparent 72%)", pointerEvents: "none" }} />
          <div style={{ position: "absolute", top: 8, left: 8, background: "rgba(0,213,255,0.9)", color: "#06121a", fontSize: 8, fontWeight: 900, letterSpacing: 0.5, padding: "3px 8px", borderRadius: 10, boxShadow: "0 0 10px rgba(0,213,255,0.5)" }}>
            {isVideo ? "VIDEO" : "NEW RELEASE"}
          </div>
        </div>
        <div style={{ padding: "11px 13px 13px" }}>
          <div style={{ color: "#f3eeff", fontSize: 12, fontWeight: 700, lineHeight: 1.4, marginBottom: 5, overflow: "hidden", display: "-webkit-box", WebkitLineClamp: 2, WebkitBoxOrient: "vertical" }}>
            {post.title || post.caption || "Untitled"}
          </div>
          <div onClick={openProfile} style={{ display: "flex", alignItems: "center", gap: 4, color: "#00D5FF", fontSize: 10, fontWeight: 700, whiteSpace: "nowrap", overflow: "hidden", textOverflow: "ellipsis", cursor: handle ? "pointer" : "default" }}>
            @{handle || "artist"}
            {verified && <span style={{ display: "inline-flex", color: "#00D5FF" }}><AppIcon id="badge" size={12} /></span>}
          </div>
        </div>
      </div>
    );
  };

  const SectionHeader = ({ emoji, title, subtitle, color }) => {
    const c = color || "#B026FF";
    return (
      <div style={{ marginBottom: 14, paddingLeft: 16 }}>
        <div style={{ display: "flex", alignItems: "center", gap: 10, marginBottom: 3 }}>
          <span style={{
            display: "inline-flex", alignItems: "center", justifyContent: "center",
            width: 32, height: 32, borderRadius: 10, flexShrink: 0,
            background: "linear-gradient(135deg, " + c + "33, " + c + "14)",
            border: "1px solid " + c + "66", color: c,
            boxShadow: "0 0 16px " + c + "44, inset 0 1px 0 rgba(255,255,255,0.06)",
          }}><AppIcon id={emoji} size={17} /></span>
          <div style={{
            color: c, fontWeight: 800, fontSize: 18, fontFamily: "'Bebas Neue',sans-serif", letterSpacing: 1.5,
            textShadow: "0 0 14px " + c + "55",
          }}>{title}</div>
        </div>
        {subtitle && <div style={{ color: "#7a7296", fontSize: 12, marginLeft: 42 }}>{subtitle}</div>}
      </div>
    );
  };

  const LoadingRow = ({ label }) => (
    <div style={{ paddingLeft: 16, marginBottom: 32 }}>
      <BFLoader type="bars" text={(label || "Loading beats") + "...please be patient."} />
    </div>
  );

  return (
    <div style={{ position: "relative", paddingTop: 20, paddingBottom: 100, minHeight: "100%", overflow: "hidden" }}>
      {/* ── Cosmic backdrop ── */}
      <div aria-hidden="true" style={{
        position: "fixed", inset: 0, zIndex: 0, pointerEvents: "none",
        background: "radial-gradient(1200px 600px at 50% -10%, rgba(176,38,255,0.16), transparent 60%), radial-gradient(900px 500px at 100% 30%, rgba(0,213,255,0.10), transparent 60%), radial-gradient(900px 600px at 0% 100%, rgba(125,60,255,0.12), transparent 60%), #050505",
      }}>
        <div style={{
          position: "absolute", inset: "-20%",
          backgroundImage: "radial-gradient(1.5px 1.5px at 40px 60px, rgba(196,181,253,0.5), transparent), radial-gradient(1px 1px at 130px 180px, rgba(0,213,255,0.35), transparent), radial-gradient(1.5px 1.5px at 260px 90px, rgba(255,255,255,0.3), transparent), radial-gradient(1px 1px at 320px 260px, rgba(176,38,255,0.4), transparent)",
          backgroundSize: "340px 340px, 200px 200px, 340px 340px, 200px 200px",
          animation: "bf-stars 70s linear infinite", opacity: 0.55,
        }} />
        <div style={{ position: "absolute", top: "-25%", left: "-12%", width: "120%", height: "70%", background: "radial-gradient(closest-side, rgba(176,38,255,0.30), transparent 70%)", filter: "blur(46px)", animation: "bf-aurora 20s ease-in-out infinite" }} />
        <div style={{ position: "absolute", bottom: "-20%", right: "-15%", width: "95%", height: "65%", background: "radial-gradient(closest-side, rgba(0,213,255,0.16), transparent 70%)", filter: "blur(54px)", animation: "bf-aurora 26s ease-in-out infinite reverse" }} />
      </div>

      <div style={{ position: "relative", zIndex: 1 }}>

      {/* ── Header strip ── */}
      <div style={{ padding: "0 16px", marginBottom: 16 }}>
        <div style={{ display: "flex", alignItems: "center", gap: 8 }}>
          <span style={{ width: 7, height: 7, borderRadius: "50%", background: "#00D5FF", boxShadow: "0 0 8px #00D5FF", animation: "bf-pulse-dot 2s ease-in-out infinite", display: "inline-block" }} />
          <span style={{ color: "#B026FF", fontWeight: 900, fontSize: 11, letterSpacing: 3, textShadow: "0 0 12px rgba(176,38,255,0.7)" }}>TRENDING TERMINAL</span>
        </div>
      </div>

      {/* ── Hero: glassmorphism showcase ── */}
      <div style={{ padding: "0 16px", marginBottom: 28 }}>
        <div style={{
          position: "relative", overflow: "hidden", borderRadius: 24, padding: "26px 22px",
          background: "linear-gradient(135deg, rgba(20,12,42,0.72), rgba(8,6,20,0.85))",
          border: "1px solid rgba(176,38,255,0.5)",
          boxShadow: "0 18px 50px rgba(0,0,0,0.6), 0 0 40px rgba(176,38,255,0.28), inset 0 1px 0 rgba(255,255,255,0.07)",
          backdropFilter: "blur(10px)", WebkitBackdropFilter: "blur(10px)",
        }}>
          {/* glowing animated top edge */}
          <div aria-hidden="true" style={{ position: "absolute", top: 0, left: 0, right: 0, height: 2, background: "linear-gradient(90deg, transparent, #B026FF, #00D5FF, #7D3CFF, transparent)", animation: "bf-edge 3.2s ease-in-out infinite" }} />
          {/* scanline */}
          <div aria-hidden="true" style={{ position: "absolute", left: 0, right: 0, top: 0, height: "45%", background: "linear-gradient(180deg, rgba(176,38,255,0.10), transparent)", animation: "bf-scan 6s linear infinite", pointerEvents: "none" }} />
          {/* grid texture */}
          <div aria-hidden="true" style={{
            position: "absolute", inset: 0, opacity: 0.5, pointerEvents: "none",
            backgroundImage: "linear-gradient(rgba(176,38,255,0.06) 1px, transparent 1px), linear-gradient(90deg, rgba(176,38,255,0.06) 1px, transparent 1px)",
            backgroundSize: "28px 28px",
            maskImage: "radial-gradient(circle at 25% 15%, #000, transparent 78%)",
            WebkitMaskImage: "radial-gradient(circle at 25% 15%, #000, transparent 78%)",
          }} />
          {/* holographic music element — floating equalizer bars */}
          <div aria-hidden="true" style={{ position: "absolute", top: 18, right: 18, display: "flex", alignItems: "flex-end", gap: 4, height: 46, opacity: 0.9, animation: "bf-float-bob 4s ease-in-out infinite" }}>
            {[0.5, 0.85, 0.35, 1, 0.6, 0.9].map(function(h, i){
              return <span key={i} style={{
                width: 5, height: (h * 46) + "px", borderRadius: 3,
                background: "linear-gradient(180deg, #00D5FF, #B026FF)",
                boxShadow: "0 0 10px rgba(176,38,255,0.8)",
                animation: "bf-edge " + (1.4 + i * 0.25) + "s ease-in-out infinite",
              }} />;
            })}
          </div>

          <div style={{ position: "relative", maxWidth: "82%" }}>
            <div style={{ color: "#00D5FF", fontSize: 11, fontWeight: 900, marginBottom: 8, letterSpacing: 2, textShadow: "0 0 10px rgba(0,213,255,0.7)" }}>DISCOVER</div>
            <div style={{
              fontFamily: "'Bebas Neue',sans-serif", fontSize: 32, lineHeight: 0.98, letterSpacing: 1,
              background: "linear-gradient(110deg, #FFFFFF 8%, #E9D5FF 32%, #B026FF 58%, #00D5FF 82%, #FFFFFF 100%)",
              backgroundSize: "220% auto", WebkitBackgroundClip: "text", backgroundClip: "text",
              WebkitTextFillColor: "transparent", color: "transparent",
              animation: "bf-holo 6.5s linear infinite",
              filter: "drop-shadow(0 0 14px rgba(176,38,255,0.45))",
            }}>DISCOVER WHAT'S MOVING THE CULTURE</div>
            <div style={{ color: "#b9aee0", fontSize: 12.5, lineHeight: 1.55, marginTop: 12, maxWidth: 320 }}>The most played beats, fastest-rising producers and newest uploads across BeatFinder.</div>
          </div>
        </div>
      </div>

      {/* ── Producer / Artist mode toggle ── */}
      <div style={{ padding: "0 16px", marginBottom: 22 }}>
        <div style={{
          display: "flex", gap: 6, padding: 5, borderRadius: 16,
          background: "linear-gradient(180deg, rgba(20,12,42,0.6), rgba(12,8,28,0.6))",
          border: "1px solid rgba(176,38,255,0.22)",
          boxShadow: "inset 0 1px 0 rgba(255,255,255,0.04)",
          backdropFilter: "blur(8px)", WebkitBackdropFilter: "blur(8px)",
        }}>
          {[
            { id: "producer", label: "Producers", icon: "sliders", glow: "176,38,255" },
            { id: "artist",   label: "Artists",   icon: "mic",     glow: "0,213,255" },
          ].map(function(m){
            const on = mode === m.id;
            return (
              <button key={m.id} onClick={function(){ setMode(m.id); }} style={{
                flex: 1, position: "relative", overflow: "hidden",
                display: "flex", alignItems: "center", justifyContent: "center", gap: 7,
                padding: "12px 8px", borderRadius: 12, cursor: "pointer",
                border: on ? "1px solid rgba(" + m.glow + ",0.85)" : "1px solid transparent",
                background: on ? "linear-gradient(180deg, rgba(" + m.glow + ",0.22), rgba(" + m.glow + ",0.08))" : "rgba(255,255,255,0.02)",
                color: on ? "#fff" : "#7a7296",
                fontWeight: 800, fontSize: 14, letterSpacing: 0.5,
                boxShadow: on ? "0 0 18px rgba(" + m.glow + ",0.4), inset 0 1px 0 rgba(255,255,255,0.06)" : "none",
                transition: "none",
              }}>
                {on && <span aria-hidden="true" style={{ position: "absolute", top: 0, left: "30%", right: "30%", height: 2, borderRadius: 2, background: "rgb(" + m.glow + ")", boxShadow: "0 0 8px rgb(" + m.glow + ")" }} />}
                <span style={{ display: "inline-flex", filter: on ? "drop-shadow(0 0 6px rgba(" + m.glow + ",0.8))" : "none" }}><AppIcon id={m.icon} size={17} /></span>
                {m.label}
              </button>
            );
          })}
        </div>
      </div>

      {mode === "producer" && (<>
      
      <SectionHeader emoji="flame" title="TRENDING ON YOUTUBE" subtitle="Most-viewed recent uploads, refreshed daily" color="#B026FF" />
      {tLoading ? <LoadingRow /> : (
        <div className="bf-carousel" style={{ overflowX: "auto", scrollbarWidth: "none", WebkitOverflowScrolling: "touch", paddingLeft: 16, paddingBottom: 4, marginBottom: 32 }}>
          <div style={{ display: "flex", gap: 12, paddingRight: 16 }}>
            {trending.length === 0
              ? <div style={{ color: "#444", fontSize: 13, padding: "20px 0" }}>No trending beats found.</div>
              : trending.map(beat => <CarouselCard key={beat.videoId} beat={beat} />)
            }
          </div>
        </div>
      )}

      
      <SectionHeader emoji="rocket" title="RISING PRODUCERS" subtitle="New uploads from producers" color="#00D5FF" />
      {rLoading ? <LoadingRow /> : (
        <div style={{ overflowX: "auto", scrollbarWidth: "none", WebkitOverflowScrolling: "touch", paddingLeft: 16, paddingBottom: 4, marginBottom: 32 }}>
          <div style={{ display: "flex", gap: 12, paddingRight: 16 }}>
            {rising.length === 0 ? (
              <div style={{ background: "#111", borderRadius: 14, padding: "20px 24px", border: "1px solid #1e1e1e", color: "#555", fontSize: 13 }}>
                No producer uploads yet
              </div>
            ) : rising.map((beat, i) => (
              beat.videoId
                ? <CarouselCard key={beat.videoId} beat={beat} />
                : <ProducerCard key={beat.id || i} beat={beat} user={user} />
            ))}
          </div>
        </div>
      )}

      
      <SectionHeader emoji="target" title="FRESH UPLOADS" subtitle="Newest beats uploaded recently" color="#7D3CFF" />
      {fLoading ? <LoadingRow /> : (
        <div style={{ overflowX: "auto", scrollbarWidth: "none", WebkitOverflowScrolling: "touch", paddingLeft: 16, paddingBottom: 4, marginBottom: 32 }}>
          <div style={{ display: "flex", gap: 12, paddingRight: 16 }}>
            {fresh.length === 0
              ? <div style={{ color: "#444", fontSize: 13, padding: "20px 0" }}>No fresh beats found.</div>
              : fresh.map(beat => <CarouselCard key={beat.videoId} beat={beat} />)
            }
          </div>
        </div>
      )}
      </>)}

      {mode === "artist" && (<>
        <SectionHeader emoji="flame" title="TRENDING ARTISTS" subtitle="Hottest R&B / rap songs on YouTube, refreshed daily" color="#B026FF" />
        {atLoading ? <LoadingRow label="Loading songs" /> : (
          <div className="bf-carousel" style={{ overflowX: "auto", scrollbarWidth: "none", WebkitOverflowScrolling: "touch", paddingLeft: 16, paddingBottom: 4, marginBottom: 32 }}>
            <div style={{ display: "flex", gap: 12, paddingRight: 16 }}>
              {artTrend.length === 0
                ? <div style={{ color: "#444", fontSize: 13, padding: "20px 0" }}>No trending songs found.</div>
                : artTrend.map(beat => <CarouselCard key={beat.videoId} beat={beat} />)}
            </div>
          </div>
        )}

        <SectionHeader emoji="rocket" title="RISING ARTISTS" subtitle="New releases from Artist Pro members" color="#00D5FF" />
        {arLoading ? <LoadingRow label="Loading artists" /> : (
          <div style={{ overflowX: "auto", scrollbarWidth: "none", WebkitOverflowScrolling: "touch", paddingLeft: 16, paddingBottom: 4, marginBottom: 32 }}>
            <div style={{ display: "flex", gap: 12, paddingRight: 16 }}>
              {artRising.length === 0 ? (
                <div style={{ background: "rgba(20,15,32,0.9)", borderRadius: 16, padding: "20px 24px", border: "1px solid rgba(176,38,255,0.25)", color: "#7a7296", fontSize: 13 }}>
                  No artist releases yet
                </div>
              ) : artRising.map((p, i) => <ArtistPostCard key={p.id || i} post={p} onViewProfile={onViewProfile} />)}
            </div>
          </div>
        )}

        <SectionHeader emoji="target" title="FRESH ON YOUTUBE" subtitle="Newest uploads from UK & US artists" color="#7D3CFF" />
        {afLoading ? <LoadingRow label="Loading songs" /> : (
          <div className="bf-carousel" style={{ overflowX: "auto", scrollbarWidth: "none", WebkitOverflowScrolling: "touch", paddingLeft: 16, paddingBottom: 4, marginBottom: 32 }}>
            <div style={{ display: "flex", gap: 12, paddingRight: 16 }}>
              {artFresh.length === 0
                ? <div style={{ color: "#444", fontSize: 13, padding: "20px 0" }}>No fresh uploads found.</div>
                : artFresh.map(beat => <CarouselCard key={beat.videoId} beat={beat} />)}
            </div>
          </div>
        )}
      </>)}

      {/* Sheet rendered at screen-root level — escapes the carousel scroll
          context that traps position:fixed children on iOS Safari. */}
      {sheetBeat && (
        <CompactBeatActionSheet
          beat={sheetBeat}
          user={user}
          compact={true}
          onClose={function(){ setSheetBeat(null); }}
        />
      )}
      </div>
    </div>
  );
}


// =============================================================================
// SEARCH SCREEN
// =============================================================================
function SearchScreen({ savedIds, onSave, onPlay, initialQuery, onClearInitial, currentUser, onViewProfile, initialTab, onClearInitialTab }) {
  var [input, setInput] = useState("");
  var [active, setActive] = useState(null);
  // Default to "beats" but accept "people" via initialTab from parent
  // (used when the Feed empty state directs user to find people to follow).
  var [searchTab, setSearchTab] = useState(initialTab || "beats");
  var [peopleQuery, setPeopleQuery] = useState("");
  var [peopleResults, setPeopleResults] = useState([]);
  var [peopleLoading, setPeopleLoading] = useState(false);
  var [viewingProfile, setViewingProfile] = useState(null);
  // When parent passes a NEW initialTab (e.g. after the user already had this
  // screen mounted), switch to it and clear the request.
  React.useEffect(function() {
    if (initialTab) {
      setSearchTab(initialTab);
      if (onClearInitialTab) onClearInitialTab();
    }
  }, [initialTab]);
  var [recents, setRecents] = useState(function() {
    try { return JSON.parse(localStorage.getItem("bf_recents") || "[]"); } catch(e) { return []; }
  });
  // Producer-uploaded beats — fetched once, filtered client-side by query.
  // Surfaces beats from Producer Pro accounts when searching by title,
  // producer name, or genre.
  var [allProducerBeats, setAllProducerBeats] = useState([]);
  var [producerBeatsLoading, setProducerBeatsLoading] = useState(false);
  React.useEffect(function() {
    setProducerBeatsLoading(true);
    apiFetch("/api/producer/beats")
      .then(function(beats) { setAllProducerBeats(beats || []); })
      .catch(function() { setAllProducerBeats([]); })
      .finally(function() { setProducerBeatsLoading(false); });
  }, []);

  // Filter producer beats by the active query. Matches title, producer
  // username/handle, and genre (case-insensitive). Sold-exclusive beats
  // are hidden from non-owners to mirror the rest of the app.
  var producerMatches = React.useMemo(function() {
    if (!active) return [];
    var q = active.toLowerCase().trim();
    if (!q) return [];
    var uid = currentUser && currentUser.id;
    return (allProducerBeats || []).filter(function(b) {
      // Hide sold-exclusive beats from everyone except buyer + producer
      if (b.premium_sold && b.premium_sold_to !== uid && b.producer_id !== uid) return false;
      var hay = (
        (b.title || "") + " " +
        (b.producer || "") + " " +
        (b.producer_username || "") + " " +
        (b.genre || "")
      ).toLowerCase();
      return hay.indexOf(q) !== -1;
    });
  }, [active, allProducerBeats, currentUser]);

  useEffect(function() {
    if (initialQuery) {
      setInput(initialQuery);
      setActive(initialQuery);
      if (onClearInitial) onClearInitial();
    }
  }, [initialQuery]);

  function searchPeople(q) {
    if (!q.trim()) { setPeopleResults([]); return; }
    setPeopleLoading(true);
    apiFetch("/api/auth/search?q=" + encodeURIComponent(q.trim()))
      .then(function(r) { setPeopleResults(r || []); setPeopleLoading(false); })
      .catch(function() { setPeopleResults([]); setPeopleLoading(false); });
  }

  function doSearch(term) {
    var q = (term || input).trim();
    if (!q) return;
    setActive(q);
    setInput(q);
    setRecents(function(prev) {
      var next = [q].concat(prev.filter(function(r) { return r.toLowerCase() !== q.toLowerCase(); })).slice(0, 6);
      try { localStorage.setItem("bf_recents", JSON.stringify(next)); } catch(e) {}
      return next;
    });
  }

  function clearRecents() {
    setRecents([]);
    try { localStorage.removeItem("bf_recents"); } catch(e) {}
  }

  var POPULAR = ["Drake", "Travis Scott", "Lil Baby", "Central Cee", "Afrobeat", "UK Drill", "Melodic Trap", "Polo G"];
  var GENRES = [
    { label: "Rap", q: "Rap Type Beat" },
    { label: "Drill", q: "UK Drill Type Beat" },
    { label: "R&B", q: "R&B Type Beat" },
    { label: "Afrobeat", q: "Afrobeat Type Beat" },
    { label: "Melodic", q: "Melodic Type Beat" },
    { label: "Trap", q: "Trap Type Beat" },
    { label: "Dancehall", q: "Dancehall Riddim" },
    { label: "Sad", q: "Sad Type Beat" },
  ];

  function Chip({ label, onPress, color }) {
    const c = color || "#A78BFA";
    return (
      <button onClick={onPress} className="bf-press" style={{
        flexShrink: 0, padding: "8px 15px", borderRadius: 20, cursor: "pointer",
        border: "1px solid " + (color ? "rgba(176,38,255,0.55)" : "rgba(167,139,250,0.25)"),
        background: color ? "linear-gradient(135deg,rgba(176,38,255,0.2),rgba(0,213,255,0.08))" : "rgba(255,255,255,0.03)",
        color: color ? "#F3E8FF" : "#b9aee0", fontWeight: 800, fontSize: 13, whiteSpace: "nowrap",
        boxShadow: color ? "0 0 14px rgba(176,38,255,0.3)" : "none",
      }}>
        {label}
      </button>
    );
  }

  // Delegate profile viewing to parent so nav bar can be hidden
  React.useEffect(function() {
    if (viewingProfile && onViewProfile) {
      onViewProfile(viewingProfile);
      setViewingProfile(null);
    }
  }, [viewingProfile]);

  return (
    <div style={{ position: "relative", padding: "0 16px 100px", minHeight: "100%", overflow: "hidden" }}>
      {/* ── Cosmic backdrop ── */}
      <div aria-hidden="true" style={{
        position: "fixed", inset: 0, zIndex: 0, pointerEvents: "none",
        background: "radial-gradient(1200px 600px at 50% -10%, rgba(176,38,255,0.16), transparent 60%), radial-gradient(900px 500px at 100% 25%, rgba(0,213,255,0.09), transparent 60%), radial-gradient(900px 600px at 0% 100%, rgba(125,60,255,0.12), transparent 60%), #050505",
      }}>
        <div style={{ position: "absolute", inset: "-20%", backgroundImage: "radial-gradient(1.5px 1.5px at 40px 60px, rgba(196,181,253,0.5), transparent), radial-gradient(1px 1px at 130px 180px, rgba(0,213,255,0.32), transparent), radial-gradient(1.5px 1.5px at 260px 90px, rgba(255,255,255,0.3), transparent), radial-gradient(1px 1px at 320px 260px, rgba(176,38,255,0.4), transparent)", backgroundSize: "340px 340px, 200px 200px, 340px 340px, 200px 200px", animation: "bf-stars 75s linear infinite", opacity: 0.5 }} />
        <div style={{ position: "absolute", top: "-25%", left: "-12%", width: "120%", height: "70%", background: "radial-gradient(closest-side, rgba(176,38,255,0.24), transparent 70%)", filter: "blur(48px)", animation: "bf-aurora 22s ease-in-out infinite" }} />
        <div style={{ position: "absolute", bottom: "-20%", right: "-15%", width: "95%", height: "65%", background: "radial-gradient(closest-side, rgba(0,213,255,0.13), transparent 70%)", filter: "blur(56px)", animation: "bf-aurora 28s ease-in-out infinite reverse" }} />
      </div>
      <div style={{ position: "relative", zIndex: 1 }}>
      <div style={{ padding: "20px 0 10px" }}>
        <div style={{ display: "flex", alignItems: "center", gap: 10, marginBottom: 16 }}>
          <span style={{ display: "inline-flex", alignItems: "center", justifyContent: "center", width: 34, height: 34, borderRadius: 10, background: "linear-gradient(135deg, rgba(176,38,255,0.3), rgba(0,213,255,0.12))", border: "1px solid rgba(176,38,255,0.55)", color: "#C4B5FD", boxShadow: "0 0 16px rgba(176,38,255,0.45), inset 0 1px 0 rgba(255,255,255,0.08)" }}><AppIcon id="search" size={18} /></span>
          <span style={{ fontSize: 26, fontWeight: 800, fontFamily: "'Bebas Neue',sans-serif", letterSpacing: 1, background: "linear-gradient(110deg, #FFFFFF, #E9D5FF 40%, #B026FF 70%, #00D5FF)", WebkitBackgroundClip: "text", backgroundClip: "text", WebkitTextFillColor: "transparent", color: "transparent", filter: "drop-shadow(0 0 12px rgba(176,38,255,0.4))" }}>Search</span>
        </div>

        <div style={{ display: "flex", gap: 6, marginBottom: 16,
          background: "rgba(20,12,42,0.5)", border: "1px solid rgba(176,38,255,0.2)", borderRadius: 14, padding: 5 }}>
          {[{ id: "beats", label: "Beats" }, { id: "people", label: "People" }].map(function(t) {
            const on = searchTab === t.id;
            return (
              <button key={t.id} onClick={function() { setSearchTab(t.id); }}
                style={{ flex: 1, padding: "11px 0", borderRadius: 10, fontWeight: 800,
                  fontSize: 14, cursor: "pointer",
                  border: on ? "1px solid rgba(176,38,255,0.7)" : "1px solid transparent",
                  background: on ? "linear-gradient(180deg,rgba(176,38,255,0.22),rgba(176,38,255,0.08))" : "rgba(255,255,255,0.02)",
                  color: on ? "#fff" : "#7a7296",
                  boxShadow: on ? "0 0 16px rgba(176,38,255,0.35)" : "none" }}>
                {t.label}
              </button>
            );
          })}
        </div>

        {searchTab === "people" && (
          <div>
            <div style={{ background: "rgba(20,15,32,0.9)", borderRadius: 14, padding: "11px 14px",
              display: "flex", alignItems: "center", gap: 10,
              border: "1px solid rgba(176,38,255,0.4)", marginBottom: 16,
              boxShadow: "0 0 18px rgba(176,38,255,0.18), inset 0 1px 0 rgba(255,255,255,0.05)" }}>
              <span style={{ color: "#A78BFA" }}><AppIcon id="search" size={20} /></span>
              <input
                value={peopleQuery}
                onChange={function(e) { setPeopleQuery(e.target.value); searchPeople(e.target.value); }}
                placeholder="Search by username or name..."
                style={{ background: "none", border: "none", outline: "none",
                  color: "white", fontSize: 15, flex: 1 }}
              />
              {peopleQuery.length > 0 && (
                <button onClick={function() { setPeopleQuery(""); setPeopleResults([]); }}
                  style={{ background: "none", border: "none", color: "#555",
                    fontSize: 18, cursor: "pointer", padding: 0 }}>
                  &#10005;
                </button>
              )}
            </div>

            {peopleLoading && (
              <div style={{ textAlign: "center", padding: "30px 0", color: "#555", fontSize: 14 }}>
                Searching...
              </div>
            )}

            {!peopleLoading && peopleResults.length > 0 && (
              <div style={{ display: "flex", flexDirection: "column", gap: 2 }}>
                {peopleResults.map(function(u) {
                  return (
                    <button key={u.username}
                      onClick={function() { setViewingProfile(u.username); }}
                      style={{ background: "none", border: "none", borderBottom: "1px solid #111",
                        padding: "12px 0", cursor: "pointer", textAlign: "left",
                        display: "flex", alignItems: "center", gap: 14, width: "100%" }}>
                      <div style={{ width: 48, height: 48, borderRadius: "50%", flexShrink: 0,
                        background: u.avatarUrl ? "#000" : "linear-gradient(135deg,#6B21A8,#C026D3)",
                        display: "flex", alignItems: "center", justifyContent: "center",
                        color: "white", fontWeight: 800, fontSize: 18, overflow: "hidden" }}>
                        {u.avatarUrl
                          ? <img src={u.avatarUrl} alt={u.username} style={{ width: "100%", height: "100%", objectFit: "cover" }} />
                          : (u.username || u.name || "?")[0].toUpperCase()}
                      </div>
                      <div style={{ flex: 1 }}>
                        <div style={{ display: "flex", alignItems: "center", gap: 6 }}>
                          <span style={{ color: "white", fontWeight: 700, fontSize: 15 }}>{u.username || u.name}</span>
                          {(u.plan === "producer" || u.plan === "artist") && <VerifiedBadge size={14} />}
                        </div>
                        <div style={{ color: "#555", fontSize: 13, marginTop: 1 }}>{u.name}</div>
                      </div>
                      <span style={{ color: "#333", fontSize: 20 }}>&#8250;</span>
                    </button>
                  );
                })}
              </div>
            )}

            {!peopleLoading && peopleQuery && peopleResults.length === 0 && (
              <div style={{ textAlign: "center", padding: "40px 0" }}>
                <div style={{ color: "#444", fontSize: 15 }}>No users found</div>
                <div style={{ color: "#333", fontSize: 13, marginTop: 6 }}>Try a different username</div>
              </div>
            )}

            {!peopleQuery && (
              <div style={{ textAlign: "center", padding: "40px 0" }}>
                <div style={{ color: "#444", fontSize: 15 }}>Find people on BeatFinder</div>
                <div style={{ color: "#333", fontSize: 13, marginTop: 6 }}>
                  Search by username to discover artists and producers
                </div>
              </div>
            )}
          </div>
        )}

        {searchTab === "beats" && (
          <div>
            <div style={{ color: "#888", fontSize: 13, marginBottom: 14 }}>Search BeatFinder producers, beat titles, artists, genres or vibes</div>
            <div style={{ display: "flex", gap: 10, marginBottom: 16 }}>
              <div style={{ flex: 1, background: "rgba(20,15,32,0.9)", borderRadius: 14, padding: "11px 14px",
                display: "flex", alignItems: "center", gap: 10, border: "1px solid rgba(176,38,255,0.4)",
                boxShadow: "0 0 18px rgba(176,38,255,0.18), inset 0 1px 0 rgba(255,255,255,0.05)" }}>
                <span style={{ color: "#A78BFA" }}><AppIcon id="search" size={20} /></span>
                <input
                  value={input}
                  onChange={function(e) { setInput(e.target.value); }}
                  onKeyDown={function(e) { if (e.key === "Enter") doSearch(); }}
                  placeholder="e.g. Drake, Central Cee, UK drill..."
                  style={{ background: "none", border: "none", outline: "none",
                    color: "white", fontSize: 15, flex: 1 }}
                />
                {input.length > 0 && (
                  <button onClick={function() { setInput(""); setActive(null); }}
                    style={{ background: "none", border: "none", color: "#7a7296",
                      fontSize: 18, cursor: "pointer", padding: 0, lineHeight: 1 }}>
                    &#10005;
                  </button>
                )}
              </div>
              <button onClick={function() { doSearch(); }}
                style={{ background: "linear-gradient(135deg,#B026FF,#7D3CFF)", border: "1px solid rgba(176,38,255,0.6)", borderRadius: 14,
                  color: "white", fontWeight: 800, padding: "10px 18px",
                  fontSize: 14, cursor: "pointer", boxShadow: "0 6px 18px rgba(176,38,255,0.4), inset 0 1px 0 rgba(255,255,255,0.2)" }}>
                Search
              </button>
            </div>

            {!active && (
              <div>
                {recents.length > 0 && (
                  <div style={{ marginBottom: 20 }}>
                    <div style={{ display: "flex", justifyContent: "space-between",
                      alignItems: "center", marginBottom: 10 }}>
                      <div style={{ color: "#A78BFA", fontSize: 11, fontWeight: 800, letterSpacing: 1.5 }}>RECENT</div>
                      <button onClick={clearRecents}
                        style={{ background: "none", border: "none", color: "#555",
                          fontSize: 12, cursor: "pointer" }}>
                        Clear
                      </button>
                    </div>
                    <div style={{ display: "flex", gap: 8, flexWrap: "wrap" }}>
                      {recents.map(function(r) {
                        return <Chip key={r} label={r} onPress={function() { doSearch(r); }} />;
                      })}
                    </div>
                  </div>
                )}
                <div style={{ marginBottom: 20 }}>
                  <div style={{ color: "#A78BFA", fontSize: 11, fontWeight: 800, letterSpacing: 1.5, marginBottom: 10 }}>POPULAR</div>
                  <div style={{ display: "flex", gap: 8, flexWrap: "wrap" }}>
                    {POPULAR.map(function(p) {
                      return <Chip key={p} label={p} onPress={function() { doSearch(p); }} color="#C026D3" />;
                    })}
                  </div>
                </div>
                <div>
                  <div style={{ color: "#A78BFA", fontSize: 11, fontWeight: 800, letterSpacing: 1.5, marginBottom: 10 }}>GENRES</div>
                  <div style={{ display: "flex", gap: 8, flexWrap: "wrap" }}>
                    {GENRES.map(function(g) {
                      return <Chip key={g.label} label={g.label} onPress={function() { doSearch(g.q); }} />;
                    })}
                  </div>
                </div>
              </div>
            )}

            {active && (
              <div>
                <div style={{ display: "flex", alignItems: "center",
                  justifyContent: "space-between", marginBottom: 16 }}>
                  <div style={{ color: "white", fontWeight: 700, fontSize: 15 }}>
                    Results for "{active}"
                  </div>
                  <button onClick={function() { setActive(null); setInput(""); }}
                    style={{ background: "none", border: "none", color: "#C026D3",
                      fontWeight: 700, fontSize: 13, cursor: "pointer" }}>
                    Clear
                  </button>
                </div>

                {/* Producer-uploaded beat matches (from BeatFinder producers).
                    Surfaced ABOVE YouTube results because they are first-party
                    licensable content. Hidden entirely if no matches. */}
                {producerMatches.length > 0 && (
                  <div style={{ marginBottom: 24 }}>
                    <div style={{
                      color: "#A78BFA", fontSize: 11, fontWeight: 900, letterSpacing: 1.5,
                      marginBottom: 10,
                      textShadow: "0 0 6px rgba(124,58,237,0.55)",
                      display: "flex", alignItems: "center", gap: 8,
                    }}>
                      FROM BEATFINDER PRODUCERS
                      <span style={{
                        background: "linear-gradient(135deg,#C026D3,#7C3AED,#3B82F6)",
                        color: "white", fontSize: 9, fontWeight: 900,
                        padding: "2px 7px", borderRadius: 10,
                        letterSpacing: 0.5,
                        boxShadow: "0 0 8px rgba(124,58,237,0.5)",
                      }}>{producerMatches.length}</span>
                    </div>
                    {producerMatches.map(function(beat) {
                      return (
                        <BeatLeaseCard
                          key={beat.id}
                          beat={beat}
                          user={currentUser}
                          onViewProfile={function(u) { if (onViewProfile) onViewProfile(u); }}
                        />
                      );
                    })}
                  </div>
                )}

                {/* YouTube-sourced beats with the search term in their title.
                    Always shown — these are the legacy YouTube results. */}
                {producerMatches.length > 0 && (
                  <div style={{
                    color: "#666", fontSize: 11, fontWeight: 800, letterSpacing: 1.2,
                    marginBottom: 10,
                  }}>
                    FROM YOUTUBE
                  </div>
                )}
                <BeatFeed artistName={active} savedIds={savedIds} onSave={onSave} onPlay={onPlay} filterTitle={false} />
              </div>
            )}
          </div>
        )}
      </div>
      </div>
    </div>
  );
}


// =============================================================================
// SAVED SCREEN
// =============================================================================
function SavedScreen({ savedMap, savedIds, onSave, onPlay, user, onGoProfile, savedLyrics, onEditLyric, onPlayPlaylist }) {
  const list = Object.values(savedMap); // guests save locally, pro users sync to backend
  const [savedView, setSavedView] = useState("library"); // "library" | "playlists"
  const [playlistCount, setPlaylistCount] = useState(0);

  const [sort,         setSort]         = useState("recent");
  const [activeFolder, setActiveFolder] = useState("all");
  const [folders,      setFolders]      = useState(() => {
    try { return JSON.parse(localStorage.getItem("bf_folders") || "{}"); } catch { return {}; }
  });
  const [addingTo,     setAddingTo]     = useState(null); // videoId being added to folder
  const [newFolder,    setNewFolder]    = useState("");

  const PRESET_FOLDERS = ["Freestyle", "Drill", "R&B", "Favourites", "Fire"];

  const saveFolders = (next) => {
    setFolders(next);
    try { localStorage.setItem("bf_folders", JSON.stringify(next)); } catch {}
  };

  const addToFolder = (videoId, folderName) => {
    const next = { ...folders };
    if (!next[folderName]) next[folderName] = [];
    if (next[folderName].indexOf(videoId) === -1) next[folderName].push(videoId);
    saveFolders(next);
    setAddingTo(null);
  };

  const removeFromFolder = (videoId, folderName) => {
    const next = { ...folders };
    if (next[folderName]) next[folderName] = next[folderName].filter(id => id !== videoId);
    saveFolders(next);
  };

  const createAndAdd = (videoId) => {
    if (!newFolder.trim()) return;
    addToFolder(videoId, newFolder.trim());
    setNewFolder("");
  };

  const allFolderNames = Object.keys(folders).filter(f => folders[f].length > 0);

  const filteredList = activeFolder === "all"
    ? list
    : list.filter(b => folders[activeFolder] && folders[activeFolder].indexOf(b.videoId) > -1);

  const sortedList = [...filteredList].sort((a, b) => {
    if (sort === "recent") return 0; // already in insertion order
    if (sort === "alpha")  return (a.title || "").localeCompare(b.title || "");
    return 0;
  });



  return (
    <div style={{ position: "relative", padding: "0 16px 100px", minHeight: "100%", overflow: "hidden" }}>
      {/* ── Cosmic backdrop ── */}
      <div aria-hidden="true" style={{
        position: "fixed", inset: 0, zIndex: 0, pointerEvents: "none",
        background: "radial-gradient(1200px 600px at 50% -10%, rgba(176,38,255,0.16), transparent 60%), radial-gradient(900px 500px at 100% 25%, rgba(0,213,255,0.09), transparent 60%), radial-gradient(900px 600px at 0% 100%, rgba(125,60,255,0.12), transparent 60%), #050505",
      }}>
        <div style={{ position: "absolute", inset: "-20%", backgroundImage: "radial-gradient(1.5px 1.5px at 40px 60px, rgba(196,181,253,0.5), transparent), radial-gradient(1px 1px at 130px 180px, rgba(0,213,255,0.32), transparent), radial-gradient(1.5px 1.5px at 260px 90px, rgba(255,255,255,0.3), transparent), radial-gradient(1px 1px at 320px 260px, rgba(176,38,255,0.4), transparent)", backgroundSize: "340px 340px, 200px 200px, 340px 340px, 200px 200px", animation: "bf-stars 75s linear infinite", opacity: 0.5 }} />
        <div style={{ position: "absolute", top: "-25%", left: "-12%", width: "120%", height: "70%", background: "radial-gradient(closest-side, rgba(176,38,255,0.24), transparent 70%)", filter: "blur(48px)", animation: "bf-aurora 22s ease-in-out infinite" }} />
        <div style={{ position: "absolute", bottom: "-20%", right: "-15%", width: "95%", height: "65%", background: "radial-gradient(closest-side, rgba(0,213,255,0.13), transparent 70%)", filter: "blur(56px)", animation: "bf-aurora 28s ease-in-out infinite reverse" }} />
      </div>
      <div style={{ position: "relative", zIndex: 1 }}>
      <div style={{ padding: "20px 0 12px" }}>
        <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", marginBottom: 5 }}>
          <div style={{ display: "flex", alignItems: "center", gap: 10 }}>
            <span style={{ display: "inline-flex", alignItems: "center", justifyContent: "center", width: 34, height: 34, borderRadius: 10, background: "linear-gradient(135deg, rgba(176,38,255,0.3), rgba(0,213,255,0.12))", border: "1px solid rgba(176,38,255,0.55)", color: "#C4B5FD", boxShadow: "0 0 16px rgba(176,38,255,0.45), inset 0 1px 0 rgba(255,255,255,0.08)" }}><AppIcon id="saved" size={18} /></span>
            <span style={{ fontSize: 24, fontWeight: 800, fontFamily: "'Bebas Neue',sans-serif", letterSpacing: 1, background: "linear-gradient(110deg, #FFFFFF, #E9D5FF 40%, #B026FF 70%, #00D5FF)", WebkitBackgroundClip: "text", backgroundClip: "text", WebkitTextFillColor: "transparent", color: "transparent", filter: "drop-shadow(0 0 12px rgba(176,38,255,0.4))" }}>Beat Library &amp; Playlists</span>
          </div>
          <div style={{ flexShrink: 0, color: "#C4B5FD", fontSize: 11, fontWeight: 800, padding: "5px 10px", borderRadius: 20, background: "rgba(176,38,255,0.12)", border: "1px solid rgba(176,38,255,0.35)", boxShadow: "0 0 12px rgba(176,38,255,0.2)" }}>{savedView === "playlists" ? (playlistCount + " " + (playlistCount === 1 ? "playlist" : "playlists")) : (list.length + " saved")}</div>
        </div>
        <div style={{ color: "#8b82a8", fontSize: 13, marginBottom: 16 }}>Your personal beat &amp; song playlist collection</div>

        {/* Library / Playlists toggle */}
        <div style={{ display: "flex", gap: 6, padding: 4, marginBottom: 16, borderRadius: 14, background: "rgba(20,12,42,0.5)", border: "1px solid rgba(176,38,255,0.2)" }}>
          {[{ id: "library", label: "Beats", icon: "saved" }, { id: "playlists", label: "Playlists", icon: "music" }].map(function(v){
            const on = savedView === v.id;
            return (
              <button key={v.id} onClick={function(){ setSavedView(v.id); }} style={{
                flex: 1, display: "flex", alignItems: "center", justifyContent: "center", gap: 7,
                padding: "11px 8px", borderRadius: 10, cursor: "pointer",
                border: on ? "1px solid rgba(176,38,255,0.7)" : "1px solid transparent",
                background: on ? "linear-gradient(180deg,rgba(176,38,255,0.22),rgba(176,38,255,0.08))" : "rgba(255,255,255,0.02)",
                color: on ? "#fff" : "#7a7296", fontWeight: 800, fontSize: 13.5,
                boxShadow: on ? "0 0 16px rgba(176,38,255,0.35)" : "none",
              }}><AppIcon id={v.icon} size={16} /> {v.label}</button>
            );
          })}
        </div>

        {savedView === "playlists" && (
          <PlaylistsScreen onPlayPlaylist={onPlayPlaylist} onCount={setPlaylistCount} />
        )}

        
        {savedView === "library" && (<>
        <div style={{ display: "flex", gap: 8, marginBottom: 16 }}>
          {[{ id: "recent", label: "Recently Saved" }, { id: "alpha", label: "A - Z" }].map(s => (
            <button key={s.id} onClick={() => setSort(s.id)} style={{
              padding: "7px 16px", borderRadius: 20, cursor: "pointer", fontWeight: 800, fontSize: 12,
              border: sort === s.id ? "1px solid rgba(176,38,255,0.7)" : "1px solid rgba(167,139,250,0.2)",
              background: sort === s.id ? "linear-gradient(135deg,rgba(176,38,255,0.25),rgba(0,213,255,0.12))" : "rgba(255,255,255,0.03)",
              color: sort === s.id ? "#F3E8FF" : "#7a7296",
              boxShadow: sort === s.id ? "0 0 14px rgba(176,38,255,0.4)" : "none",
            }}>
              {s.label}
            </button>
          ))}
        </div>

        
        {(allFolderNames.length > 0 || list.length > 0) && (
          <div style={{ overflowX: "auto", scrollbarWidth: "none", marginBottom: 4 }}>
            <div style={{ display: "flex", gap: 8, paddingBottom: 4 }}>
              <button onClick={() => setActiveFolder("all")} style={{
                flexShrink: 0, padding: "7px 16px", borderRadius: 20, cursor: "pointer", fontWeight: 800, fontSize: 12,
                border: activeFolder === "all" ? "1px solid rgba(176,38,255,0.7)" : "1px solid rgba(167,139,250,0.2)",
                background: activeFolder === "all" ? "linear-gradient(135deg,rgba(176,38,255,0.25),rgba(0,213,255,0.12))" : "rgba(255,255,255,0.03)",
                color: activeFolder === "all" ? "#F3E8FF" : "#7a7296",
                boxShadow: activeFolder === "all" ? "0 0 14px rgba(176,38,255,0.4)" : "none",
              }}>
                All ({list.length})
              </button>
              {allFolderNames.map(f => (
                <button key={f} onClick={() => setActiveFolder(f)} style={{
                  flexShrink: 0, padding: "7px 16px", borderRadius: 20, cursor: "pointer", fontWeight: 800, fontSize: 12,
                  border: activeFolder === f ? "1px solid rgba(0,213,255,0.7)" : "1px solid rgba(167,139,250,0.2)",
                  background: activeFolder === f ? "linear-gradient(135deg,rgba(0,213,255,0.22),rgba(176,38,255,0.12))" : "rgba(255,255,255,0.03)",
                  color: activeFolder === f ? "#CFEFFF" : "#7a7296",
                  boxShadow: activeFolder === f ? "0 0 14px rgba(0,213,255,0.35)" : "none",
                }}>
                  {f} ({folders[f] ? folders[f].length : 0})
                </button>
              ))}
            </div>
          </div>
        )}
        </>)}
      </div>

      {savedView === "library" && (<>
      {list.length === 0 ? (
        <div style={{ textAlign: "center", paddingTop: 60 }}>
          <div style={{ fontSize: 56, marginBottom: 16 }}><AppIcon id="bookmark" size={20}/></div>
          <div style={{ color: "white", fontWeight: 800, fontSize: 18, marginBottom: 8 }}>No beats saved yet</div>
          <div style={{ color: "#555", fontSize: 14, lineHeight: 1.8, marginBottom: 24 }}>
            Tap the <AppIcon id="bookmark" size={20}/> bookmark icon on any beat<br />to start building your library
          </div>
          <div style={{ display: "flex", flexWrap: "wrap", gap: 8, justifyContent: "center" }}>
            {["Browse Artists", "Check Trending", "Search a Vibe"].map(t => (
              <div key={t} style={{ padding: "8px 16px", borderRadius: 20, border: "1px solid rgba(176,38,255,0.35)", background: "rgba(176,38,255,0.08)", color: "#A78BFA", fontSize: 13, fontWeight: 700 }}>{t}</div>
            ))}
          </div>
        </div>
      ) : filteredList.length === 0 ? (
        <div style={{ textAlign: "center", paddingTop: 40 }}>
          <div style={{ fontSize: 40, marginBottom: 12 }}>▤</div>
          <div style={{ color: "#555", fontSize: 14 }}>No beats in this collection yet</div>
          <div style={{ color: "#444", fontSize: 12, marginTop: 6 }}>Tap the folder icon on any beat to add it here</div>
        </div>
      ) : (
        sortedList.map(beat => (
          <div key={beat.videoId} style={{ position: "relative" }}>
            <BeatCard beat={beat} savedIds={savedIds} onSave={onSave} onPlay={onPlay} />

            
            <div style={{ marginTop: -8, marginBottom: 14, display: "flex", gap: 6, flexWrap: "wrap", paddingLeft: 4 }}>
              {addingTo === beat.videoId ? (
                <div style={{ background: "#111", border: "1px solid #2a2a2a", borderRadius: 12, padding: "10px 12px", width: "100%", boxSizing: "border-box" }}>
                  <div style={{ color: "#aaa", fontSize: 12, fontWeight: 700, marginBottom: 8 }}>ADD TO COLLECTION</div>
                  <div style={{ display: "flex", gap: 6, flexWrap: "wrap", marginBottom: 10 }}>
                    {PRESET_FOLDERS.filter(f => !folders[f] || folders[f].indexOf(beat.videoId) === -1).map(f => (
                      <button key={f} onClick={() => addToFolder(beat.videoId, f)} style={{
                        padding: "5px 12px", borderRadius: 20, cursor: "pointer", fontWeight: 600, fontSize: 12,
                        border: "1.5px solid #F59E0B", background: "rgba(245,158,11,0.1)", color: "#F59E0B",
                      }}>{f}</button>
                    ))}
                    {allFolderNames.filter(f => PRESET_FOLDERS.indexOf(f) === -1 && (!folders[f] || folders[f].indexOf(beat.videoId) === -1)).map(f => (
                      <button key={f} onClick={() => addToFolder(beat.videoId, f)} style={{
                        padding: "5px 12px", borderRadius: 20, cursor: "pointer", fontWeight: 600, fontSize: 12,
                        border: "1.5px solid #C026D3", background: "rgba(192,38,211,0.1)", color: "#C026D3",
                      }}>{f}</button>
                    ))}
                  </div>
                  <div style={{ display: "flex", gap: 8 }}>
                    <input value={newFolder} onChange={e => setNewFolder(e.target.value)}
                      placeholder="New collection name..."
                      onKeyDown={e => e.key === "Enter" && createAndAdd(beat.videoId)}
                      style={{ flex: 1, background: "#1a1a1a", border: "1px solid #333", borderRadius: 8, padding: "7px 10px", color: "white", fontSize: 13, outline: "none" }}
                    />
                    <button onClick={() => createAndAdd(beat.videoId)} style={{ background: "#C026D3", border: "none", borderRadius: 8, color: "white", fontWeight: 700, fontSize: 12, padding: "7px 12px", cursor: "pointer" }}>
                      Create
                    </button>
                    <button onClick={() => setAddingTo(null)} style={{ background: "#1a1a1a", border: "1px solid #333", borderRadius: 8, color: "#555", fontSize: 12, padding: "7px 10px", cursor: "pointer" }}>
                      Cancel
                    </button>
                  </div>
                </div>
              ) : (
                <div style={{ display: "flex", gap: 6, alignItems: "center" }}>
                  <button onClick={() => setAddingTo(beat.videoId)} style={{
                    padding: "4px 12px", borderRadius: 20, cursor: "pointer", fontWeight: 600, fontSize: 11,
                    border: "1.5px solid #2a2a2a", background: "transparent", color: "#555",
                  }}>
                    + Collection
                  </button>
                  {allFolderNames.filter(f => folders[f] && folders[f].indexOf(beat.videoId) > -1).map(f => (
                    <div key={f} style={{ display: "flex", alignItems: "center", gap: 4, padding: "4px 10px", borderRadius: 20, background: "rgba(245,158,11,0.1)", border: "1px solid rgba(245,158,11,0.3)" }}>
                      <span style={{ color: "#F59E0B", fontSize: 11, fontWeight: 700 }}>{f}</span>
                      <button onClick={() => removeFromFolder(beat.videoId, f)} style={{ background: "none", border: "none", color: "#888", fontSize: 12, cursor: "pointer", padding: 0, lineHeight: 1 }}>&#10005;</button>
                    </div>
                  ))}
                </div>
              )}
            </div>
          </div>
        ))
      )}

      {/* ── Guest Lyrics Section — only shown for non-logged-in users ── */}
      {!user && (
        <div style={{ marginTop: 32 }}>
          <div style={{ display: "flex", alignItems: "center", gap: 8, marginBottom: 4 }}>
            <span style={{ color: "#C026D3" }}><AppIcon id="writing" size={18} /></span>
            <span style={{ color: "white", fontWeight: 800, fontSize: 20, fontFamily: "'Bebas Neue',sans-serif", letterSpacing: 1 }}>
              My Lyrics
            </span>
            <span style={{ color: "#555", fontSize: 13, marginLeft: 4 }}>{(savedLyrics || []).length} saved</span>
          </div>
          <div style={{ color: "#666", fontSize: 13, marginBottom: 16 }}>Lyrics you've written — saved on this device</div>

          {(!savedLyrics || savedLyrics.length === 0) ? (
            <div style={{ textAlign: "center", padding: "32px 0" }}>
              <div style={{ color: "#333", fontSize: 13, lineHeight: 1.8 }}>
                No lyrics saved yet.<br />
                <span style={{ color: "#555" }}>Tap Write Lyrics on any beat to get started.</span>
              </div>
            </div>
          ) : (
            <div style={{ display: "flex", flexDirection: "column", gap: 10 }}>
              {(savedLyrics || []).map(function(lyric, idx) {
                return (
                  <div key={lyric.id || idx}
                    onClick={function() { onEditLyric && onEditLyric(lyric, idx); }}
                    style={{
                      background: "#111", border: "1px solid #1e1e1e",
                      borderRadius: 14, padding: "14px 16px", cursor: "pointer",
                      transition: "border-color 0.15s",
                    }}>
                    <div style={{ color: "white", fontWeight: 700, fontSize: 14, marginBottom: 4,
                      whiteSpace: "nowrap", overflow: "hidden", textOverflow: "ellipsis" }}>
                      {lyric.title || "Untitled"}
                    </div>
                    {lyric.beatTitle && (
                      <div style={{ color: "#555", fontSize: 12, marginBottom: 6,
                        whiteSpace: "nowrap", overflow: "hidden", textOverflow: "ellipsis" }}>
                        {lyric.beatTitle}
                      </div>
                    )}
                    <div style={{ color: "#444", fontSize: 12, lineHeight: 1.5,
                      overflow: "hidden", display: "-webkit-box", WebkitLineClamp: 2, WebkitBoxOrient: "vertical" }}>
                      {lyric.text}
                    </div>
                  </div>
                );
              })}
            </div>
          )}
        </div>
      )}
      </>)}
      </div>
    </div>
  );
}


// =============================================================================
// EXCLUSIVE MEMBERS SCREEN
// =============================================================================
// =============================================================================
// MEMBERS BEATS TAB
// priced=true  → Exclusive Beats (paid leases, BeatLeaseCard with preview + buy)
// priced=false → MP3 Downloads   (free beats, direct download)
// =============================================================================
// Free MP3 beat card — members area download tab with preview
function FreeMemberBeatCard({ beat, onViewProfile, user }) {
  var [previewing,  setPreviewing]  = React.useState(false);
  var [previewTime, setPreviewTime] = React.useState(0);
  var audioRef  = React.useRef(null);
  var timerRef  = React.useRef(null);
  var seekedRef = React.useRef(false);
  var previewId = React.useRef("member_" + beat.id);

  function startPreview() {
    if (previewing) { stopPreview(); return; }
    seekedRef.current = false;
    startGlobalPreview(previewId.current);
    setPreviewing(true); setPreviewTime(0);
    recordPlay(beat.id);
  }
  function stopPreview() {
    clearGlobalPreview(previewId.current);
    setPreviewing(false); setPreviewTime(0); clearInterval(timerRef.current);
    cancelPlayTimer(beat.id);
    if (audioRef.current) { audioRef.current.pause(); audioRef.current.currentTime = 0; }
  }
  useGlobalPreviewStop(previewId.current, React.useCallback(function() {
    setPreviewing(false); setPreviewTime(0); clearInterval(timerRef.current);
    cancelPlayTimer(beat.id);
    if (audioRef.current) { audioRef.current.pause(); audioRef.current.currentTime = 0; }
  }, [beat.id]));
  function onAudioPlay() {
    clearInterval(timerRef.current);
    var start = Date.now();
    timerRef.current = setInterval(function() {
      var el = (Date.now() - start) / 1000;
      setPreviewTime(Math.min(el, 45));
      if (el >= 45) { clearInterval(timerRef.current); stopPreview(); }
    }, 100);
  }
  function onTimeUpdate(e) {
    var el = e.target, st = parseFloat(el.dataset.startTime || 0);
    if (el.currentTime >= st + 45) { el.pause(); stopPreview(); }
    if (el.currentTime < st) el.currentTime = st;
  }
  React.useEffect(function() { return function() { clearInterval(timerRef.current); }; }, []);

  var pct = previewing ? (previewTime / 45) * 100 : 0;

  return (
    <NewBeatCardShell
      beat={beat}
      previewing={previewing}
      previewTime={previewTime}
      onTogglePreview={startPreview}
      onDownload={function(){ downloadMp3(beat.url, beat.title, beat.id); }}
      onViewProfile={onViewProfile}
      user={user}
      audioEl={previewing && beat.url ? (
        <audio ref={audioRef} src={beat.url} autoPlay data-start-time={String(beat.preview_start || 0)}
          onPlay={function(){ onAudioPlay(); if (audioRef.current && (beat.preview_start||0) > 0 && !seekedRef.current) { audioRef.current.currentTime = beat.preview_start; seekedRef.current = true; } }}
          onPause={function(){ clearInterval(timerRef.current); }}
          onTimeUpdate={onTimeUpdate} onEnded={stopPreview} />
      ) : null}
    />
  );
}
function MembersBeatsTab({ user, priced, onViewProfile }) {
  var [beats,   setBeats]   = React.useState([]);
  var [loading, setLoading] = React.useState(true);
  var [error,   setError]   = React.useState(null);

  function isFreePrice(p) {
    return !p || p === "free" || p === "£0" || p === "0" || p === "£0.00" || p === "0.00";
  }

  React.useEffect(function() {
    setLoading(true); setError(null);
    apiFetch("/api/producer/beats")
      .then(function(all) {
        var filtered = (all || []).filter(function(b) {
          return priced ? !isFreePrice(b.price) : isFreePrice(b.price);
        });
        setBeats(filtered);
        setLoading(false);
      })
      .catch(function(e) { setError(e.message); setLoading(false); });
  }, [priced]);

  // Remove beat instantly if deleted from profile dashboard
  useContentDeletedListener(React.useCallback(function(detail) {
    if (detail.kind === "beat") {
      setBeats(function(prev) { return prev.filter(function(b) { return b.id !== detail.id; }); });
    }
  }, []));

  // Update beat in-place when producer edits description/bpm/key/title
  useBeatUpdatedListener(React.useCallback(function(detail) {
    setBeats(function(prev) {
      return prev.map(function(b) {
        return b.id === detail.beatId ? Object.assign({}, b, detail.patch) : b;
      });
    });
  }, []));

  if (loading) return <BFLoader type="spinner" text="LOADING BEATS..." />;

  if (error) return (
    <div style={{ background: "rgba(220,38,38,0.08)", border: "1px solid rgba(220,38,38,0.25)", borderRadius: 14, padding: 20, textAlign: "center", margin: "20px 0" }}>
      <div style={{ color: "#F87171", fontWeight: 700, fontSize: 15 }}>Could not load beats</div>
      <div style={{ color: "#888", fontSize: 13, marginTop: 8 }}>{error}</div>
    </div>
  );

  if (beats.length === 0) return (
    <div style={{ textAlign: "center", padding: "60px 0" }}>
      <div style={{ fontSize: 48, marginBottom: 16 }}><AppIcon id="note" size={40} /></div>
      <div style={{ color: "white", fontWeight: 800, fontSize: 17, marginBottom: 8 }}>
        {priced ? "No beats available yet" : "No free downloads yet"}
      </div>
      <div style={{ color: "#555", fontSize: 13, lineHeight: 1.7 }}>
        {priced
          ? "Producer Pro members can upload beats from their Profile tab."
          : "Producers can set a beat price to £0 to make it free to download."}
      </div>
    </div>
  );

  if (priced) {
    // Exclusive Beats — full BeatLeaseCard with 30s preview + buy
    return (
      <div>
        <div style={{
          color: "#A78BFA", fontWeight: 900, fontSize: 12, letterSpacing: 1.5,
          marginBottom: 14,
          textShadow: "0 0 8px rgba(124,58,237,0.55)",
        }}>
          {beats.length} BEAT{beats.length !== 1 ? "S" : ""} AVAILABLE
        </div>
        {beats.map(function(beat) {
          return <BeatLeaseCard key={beat.id} beat={beat} user={user} onViewProfile={onViewProfile} />;
        })}
      </div>
    );
  }

  // MP3 Downloads — free beats with preview + save-to-device
  return (
    <div>
      <div style={{
        color: "#E9D5FF", fontWeight: 900, fontSize: 12, letterSpacing: 1.5,
        marginBottom: 14,
        textShadow: "0 0 8px rgba(192,38,211,0.55)",
      }}>
        {beats.length} FREE DOWNLOAD{beats.length !== 1 ? "S" : ""} AVAILABLE
      </div>
      {beats.map(function(beat) {
        return <FreeMemberBeatCard key={beat.id} beat={beat} onViewProfile={onViewProfile} user={user} />;
      })}
    </div>
  );
}

// =============================================================================
// MEMBERS LIBRARY / VAULT — one place for everything a member has collected:
// purchased beat leases, free MP3 downloads, saved beats, saved lyrics, and
// recorded Studio projects. Empty sections nudge the member toward the action
// that fills them.
// =============================================================================
function MembersLibraryTab({ user, savedIds, savedLyrics, onPlay, onEditLyric, onGoStudio, onGenreSearch, onViewProfile }) {
  const [leases, setLeases]       = React.useState(null);
  const [freeDls, setFreeDls]     = React.useState(null);
  const [projects, setProjects]   = React.useState(null);
  const [open, setOpen]           = React.useState({ owned: true, free: true, saved: true, lyrics: true, songs: true });

  React.useEffect(function() {
    let alive = true;
    apiFetch("/api/producer/my-leases").then(function(r) {
      if (alive) setLeases(Array.isArray(r) ? r : (r && r.leases) || []);
    }).catch(function() { if (alive) setLeases([]); });
    // Free licences come back as { beat_ids: [...] } — just IDs. Resolve them
    // against the public beats catalog so we can show titles + producers.
    Promise.all([
      apiFetch("/api/producer/my-free-licences").catch(function(){ return { beat_ids: [] }; }),
      apiFetch("/api/producer/beats").catch(function(){ return []; }),
    ]).then(function(res) {
      if (!alive) return;
      const lic = res[0] || {};
      const ids = Array.isArray(lic.beat_ids) ? lic.beat_ids : (Array.isArray(lic) ? lic : []);
      const catalog = Array.isArray(res[1]) ? res[1] : ((res[1] && res[1].beats) || []);
      const byId = {};
      catalog.forEach(function(b){ byId[String(b.id)] = b; });
      const resolved = ids.map(function(id) {
        const b = byId[String(id)];
        return b
          ? { beat_id: id, beat_title: b.title, title: b.title, producer: b.producer, url: b.url, beat_url: b.url }
          : { beat_id: id, beat_title: "Free beat", title: "Free beat", producer: "", url: "", beat_url: "" };
      });
      setFreeDls(resolved);
    });
    // Build the recorded-songs list. Local snapshots in bf_studio_projects are
    // the ones Studio's loadProject can actually restore (they hold the full
    // track/clip data), so we surface those first. Cloud-only projects are
    // tagged _kind:"cloud" so the Open handoff routes them to loadCloudProject.
    let localProjects = [];
    try { localProjects = JSON.parse(localStorage.getItem("bf_studio_projects") || "[]"); } catch (e) {}
    apiFetch("/api/studio/projects/list").then(function(r) {
      if (!alive) return;
      const cloud = Array.isArray(r) ? r : (r && r.projects) || [];
      const localNames = {};
      localProjects.forEach(function(p){ if (p && p.name) localNames[p.name] = true; });
      const cloudOnly = cloud
        .filter(function(c){ return !(c && c.name && localNames[c.name]); })
        .map(function(c){ return Object.assign({}, c, { _kind: "cloud", cloudId: c.cloudId || c._id || c.id }); });
      setProjects(localProjects.concat(cloudOnly));
    }).catch(function() {
      if (alive) setProjects(localProjects);
    });
    return function() { alive = false; };
  }, []);

  const ACCENT = "#a78bfa";
  const card = {
    background: "linear-gradient(180deg, #1a1530 0%, #14102a 100%)",
    border: "1px solid rgba(167,139,250,0.18)",
    borderRadius: 14, marginBottom: 14, overflow: "hidden",
  };
  const sectionHeader = function(key, icon, title, count) {
    return (
      <button onClick={function(){ setOpen(function(p){ return Object.assign({}, p, { [key]: !p[key] }); }); }}
        style={{
          width: "100%", display: "flex", alignItems: "center", gap: 10,
          padding: "13px 15px", background: "rgba(167,139,250,0.04)",
          border: "none", borderBottom: open[key] ? "1px solid rgba(167,139,250,0.12)" : "none",
          cursor: "pointer", textAlign: "left",
        }}>
        <span style={{ color: ACCENT, display: "inline-flex", filter: "drop-shadow(0 0 5px rgba(167,139,250,0.5))" }}>{icon}</span>
        <span style={{ color: "#e8e6f0", fontWeight: 800, fontSize: 13, letterSpacing: 0.5, flex: 1 }}>{title}</span>
        <span style={{
          fontSize: 10, fontWeight: 800, color: ACCENT,
          background: "rgba(167,139,250,0.13)", padding: "2px 9px", borderRadius: 999,
        }}>{count}</span>
        <span style={{ color: "#7a6ea0", fontSize: 12, transform: open[key] ? "rotate(90deg)" : "none", transition: "transform 0.15s" }}>›</span>
      </button>
    );
  };
  const nudge = function(text, btnLabel, onClick) {
    return (
      <div style={{ padding: "18px 16px", textAlign: "center" }}>
        <div style={{ color: "#7a6ea0", fontSize: 12, marginBottom: 12, lineHeight: 1.5 }}>{text}</div>
        <button onClick={onClick} style={{
          background: "linear-gradient(135deg, rgba(167,139,250,0.2), rgba(124,58,237,0.18))",
          border: "1px solid rgba(167,139,250,0.45)", borderRadius: 10,
          color: "#e0d4ff", fontWeight: 800, fontSize: 12, padding: "9px 18px",
          cursor: "pointer", letterSpacing: 0.3,
          boxShadow: "0 0 12px rgba(167,139,250,0.2)",
        }}>{btnLabel}</button>
      </div>
    );
  };
  const ICON = {
    owned:  (<svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><path d="M9 18V5l12-2v13"/><circle cx="6" cy="18" r="3"/><circle cx="18" cy="16" r="3"/></svg>),
    free:   (<svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><path d="M21 15v4a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2v-4"/><polyline points="7 10 12 15 17 10"/><line x1="12" y1="15" x2="12" y2="3"/></svg>),
    saved:  (<svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><path d="M19 21l-7-5-7 5V5a2 2 0 0 1 2-2h10a2 2 0 0 1 2 2z"/></svg>),
    lyrics: (<svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><path d="M14 2H6a2 2 0 0 0-2 2v16a2 2 0 0 0 2 2h12a2 2 0 0 0 2-2V8z"/><polyline points="14 2 14 8 20 8"/><line x1="16" y1="13" x2="8" y2="13"/><line x1="16" y1="17" x2="8" y2="17"/></svg>),
    songs:  (<svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><path d="M12 3v10.55A4 4 0 1 0 14 17V7h4V3z"/></svg>),
  };
  const rowStyle = {
    display: "flex", alignItems: "center", gap: 12, padding: "11px 15px",
    borderBottom: "1px solid rgba(167,139,250,0.06)", cursor: "pointer",
  };
  const thumb = function(colorA, colorB, label) {
    return (
      <div style={{
        width: 38, height: 38, borderRadius: 9, flexShrink: 0,
        background: "linear-gradient(135deg," + colorA + "," + colorB + ")",
        display: "flex", alignItems: "center", justifyContent: "center",
        color: "#fff", fontWeight: 900, fontSize: 14,
      }}>{label}</div>
    );
  };

  const ownedList  = leases || [];
  const freeList   = freeDls || [];
  const savedCount = (savedIds && (savedIds.size != null ? savedIds.size : (savedIds.length || 0))) || 0;
  const lyricList  = savedLyrics || [];
  const songList   = projects || [];
  const loading    = leases === null || freeDls === null || projects === null;

  // Inline preview — which beat row is currently playing, + a single shared
  // <audio> element. Previews the producer-uploaded URL directly; never opens
  // the YouTube player.
  const [libPlayingId, setLibPlayingId] = React.useState(null);
  const libAudioRef = React.useRef(null);
  const togglePreview = function(id, url) {
    const a = libAudioRef.current;
    if (!a || !url) return;
    if (libPlayingId === id) {
      a.pause();
      setLibPlayingId(null);
    } else {
      try { a.src = url; a.currentTime = 0; } catch(_){}
      a.play().then(function(){ setLibPlayingId(id); }).catch(function(){ setLibPlayingId(null); });
    }
  };

  // A beat row with inline play/pause preview + a download button. Used by
  // both Owned Beats and Free Downloads so users can re-grab their files
  // from the Library without leaving the page.
  const beatRow = function(l, i, accentA, accentB, subtitle) {
    const id = l.beat_id || l.id || ("row" + i);
    const url = l.beat_url || l.url || "";
    const playing = libPlayingId === id;
    return (
      <div key={id} style={Object.assign({}, rowStyle, { cursor: "default" })}>
        <div style={{
          width: 38, height: 38, borderRadius: 9, flexShrink: 0,
          background: "linear-gradient(135deg," + accentA + "," + accentB + ")",
          display: "flex", alignItems: "center", justifyContent: "center",
          color: "#fff",
        }}>
          <button onClick={function(e){ e.stopPropagation(); togglePreview(id, url); }} disabled={!url} style={{
            background: "transparent", border: "none", cursor: url ? "pointer" : "default",
            color: "#fff", display: "inline-flex", alignItems: "center", justifyContent: "center",
            padding: 0, marginLeft: playing ? 0 : 2,
          }}>
            <AppIcon id={playing ? "pause" : "play"} size={16} />
          </button>
        </div>
        <div style={{ flex: 1, minWidth: 0 }}>
          <div style={{ color: "#e8e6f0", fontWeight: 700, fontSize: 13, overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" }}>{l.beat_title || l.title || "Beat"}</div>
          <div style={{ color: "#7a6ea0", fontSize: 11, marginTop: 2 }}>{subtitle}</div>
        </div>
        {url ? (
          <button onClick={function(e){ e.stopPropagation(); downloadMp3(url, l.beat_title || l.title || "beat", id); }} style={{
            background: "rgba(167,139,250,0.1)", border: "1px solid rgba(167,139,250,0.25)",
            borderRadius: 7, color: "#c4b5fd", flexShrink: 0, cursor: "pointer",
            padding: "6px 8px", display: "inline-flex", alignItems: "center", gap: 5, fontSize: 11, fontWeight: 700,
          }}>
            <AppIcon id="download" size={12} /> Save
          </button>
        ) : null}
      </div>
    );
  };

  return (
    <div>
      {/* Shared hidden audio element for inline previews */}
      <audio ref={libAudioRef} preload="none" style={{ display: "none" }}
        onEnded={function(){ setLibPlayingId(null); }}
        onPause={function(){ /* keep state; explicit toggle handles it */ }} />

      <div style={card}>
        {sectionHeader("owned", ICON.owned, "Owned Beats", ownedList.length)}
        {open.owned && (
          ownedList.length === 0
            ? nudge("Beats you buy a lease for live here forever.", "Browse Exclusive Beats", function(){ onGenreSearch && onGenreSearch(""); })
            : ownedList.map(function(l, i) {
                const sub = (l.producer || "Producer") + (l.tier ? " · " + (l.tier === "premium" ? "Exclusive" : "Basic") + " lease" : "");
                return beatRow(l, i, "#7C3AED", "#C026D3", sub);
              })
        )}
      </div>

      <div style={card}>
        {sectionHeader("free", ICON.free, "Free Downloads", freeList.length)}
        {open.free && (
          freeList.length === 0
            ? nudge("Free MP3s you download show up here.", "Find Free Beats", function(){ onGenreSearch && onGenreSearch(""); })
            : freeList.map(function(l, i) {
                return beatRow(l, i, "#C026D3", "#7C3AED", (l.producer || "Producer") + " · Free");
              })
        )}
      </div>

      <div style={card}>
        {sectionHeader("saved", ICON.saved, "Saved Beats", savedCount)}
        {open.saved && (
          savedCount === 0
            ? nudge("Tap the bookmark on any beat to save it for later.", "Discover Beats", function(){ onGenreSearch && onGenreSearch(""); })
            : <div style={{ padding: "14px 16px", color: "#9590a8", fontSize: 12, lineHeight: 1.5 }}>
                You have {savedCount} saved beat{savedCount === 1 ? "" : "s"}. Open the <strong style={{ color: "#c4b5fd" }}>Saved</strong> tab to play or manage them.
              </div>
        )}
      </div>

      <div style={card}>
        {sectionHeader("lyrics", ICON.lyrics, "Saved Lyrics", lyricList.length)}
        {open.lyrics && (
          lyricList.length === 0
            ? nudge("Write lyrics in the notepad and they'll be saved here.", "Open Studio", function(){ onGoStudio && onGoStudio(); })
            : lyricList.slice(0, 25).map(function(ly, i) {
                return (
                  <div key={ly.id || i} style={rowStyle} onClick={function(){ onEditLyric && onEditLyric(ly, i); }}>
                    {thumb("#6B21A8", "#A78BFA", "✎")}
                    <div style={{ flex: 1, minWidth: 0 }}>
                      <div style={{ color: "#e8e6f0", fontWeight: 700, fontSize: 13, overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" }}>{ly.title || "Untitled"}</div>
                      <div style={{ color: "#7a6ea0", fontSize: 11, marginTop: 2 }}>{(ly.text || ly.content || "").slice(0, 40) || "No words yet"}</div>
                    </div>
                  </div>
                );
              })
        )}
      </div>

      <div style={card}>
        {sectionHeader("songs", ICON.songs, "Recorded Songs", songList.length)}
        {open.songs && (
          songList.length === 0
            ? nudge("Projects you create in Studio are saved here.", "Open Studio", function(){ onGoStudio && onGoStudio(); })
            : songList.slice(0, 25).map(function(pr, i) {
                const openThis = function(){
                  try {
                    const isCloud = pr._kind === "cloud" || (!pr.id && (pr.cloudId || pr._id));
                    const payload = isCloud
                      ? { cloud: true, cloudId: pr.cloudId || pr._id, name: pr.name || pr.projectName || "Project" }
                      : { id: pr.id || pr._id, name: pr.name || pr.projectName || "" };
                    localStorage.setItem("bf_open_project_id", JSON.stringify(payload));
                  } catch (e) {}
                  onGoStudio && onGoStudio();
                  // Notify an already-mounted Studio to consume the handoff.
                  try { setTimeout(function(){ window.dispatchEvent(new CustomEvent("bf:open-project")); }, 120); } catch (e) {}
                };
                return (
                  <div key={pr.id || pr._id || i} style={Object.assign({}, rowStyle, { cursor: "pointer" })} onClick={openThis}>
                    {thumb("#3B82F6", "#7C3AED", "♫")}
                    <div style={{ flex: 1, minWidth: 0 }}>
                      <div style={{ color: "#e8e6f0", fontWeight: 700, fontSize: 13, overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" }}>{pr.name || pr.projectName || "Untitled Project"}</div>
                      <div style={{ color: "#7a6ea0", fontSize: 11, marginTop: 2 }}>{pr.bpm ? pr.bpm + " BPM" : ""}{pr.trackCount ? " · " + pr.trackCount + " tracks" : ""}</div>
                    </div>
                    <button onClick={function(e){ e.stopPropagation(); openThis(); }} style={{
                      background: "rgba(167,139,250,0.1)", border: "1px solid rgba(167,139,250,0.25)",
                      borderRadius: 7, color: "#c4b5fd", fontSize: 11, fontWeight: 700, padding: "5px 10px", cursor: "pointer",
                    }}>Open</button>
                  </div>
                );
              })
        )}
      </div>

      {loading && (
        <div style={{ textAlign: "center", color: "#5a4e7a", fontSize: 11, padding: "8px 0 4px" }}>Syncing your library…</div>
      )}
    </div>
  );
}

// =============================================================================
// MEMBERS STATS DASHBOARD — light progress metrics across all-time / month /
// week windows. Read-only, calculated client-side from existing endpoints.
// =============================================================================
function MembersStatsTab({ user, savedIds, savedLyrics }) {
  const [statWindow, setStatWindow] = React.useState("all");
  const [leases, setLeases]   = React.useState(null);   // paid beats I bought
  const [freeIds, setFreeIds] = React.useState(null);   // free beats I downloaded (ids)
  const [projects, setProjects] = React.useState(null);
  const [sales, setSales]     = React.useState(null);   // accurate producer-side stats

  React.useEffect(function() {
    let alive = true;
    apiFetch("/api/producer/my-leases").then(function(r){ if (alive) setLeases(Array.isArray(r) ? r : (r && r.leases) || []); }).catch(function(){ if (alive) setLeases([]); });
    apiFetch("/api/producer/my-free-licences").then(function(r){
      const ids = (r && Array.isArray(r.beat_ids)) ? r.beat_ids : (Array.isArray(r) ? r : []);
      if (alive) setFreeIds(ids);
    }).catch(function(){ if (alive) setFreeIds([]); });
    apiFetch("/api/studio/projects/list").then(function(r){ if (alive) setProjects(Array.isArray(r) ? r : (r && r.projects) || []); }).catch(function(){
      try { if (alive) setProjects(JSON.parse(localStorage.getItem("bf_studio_projects") || "[]")); } catch(e){ if (alive) setProjects([]); }
    });
    if (user && user.isPro) {
      apiFetch("/api/producer/my-sales").then(function(r){ if (alive) setSales(r || {}); }).catch(function(){ if (alive) setSales({}); });
    } else { setSales({}); }
    return function(){ alive = false; };
  }, [user]);

  const now = Date.now();
  const cutoff = statWindow === "week" ? now - 7*24*3600*1000
               : statWindow === "month" ? now - 30*24*3600*1000
               : 0;
  const inWindow = function(item) {
    if (cutoff === 0) return true;
    const ds = item.purchased_at || item.created_at || item.uploaded_at || item.savedAt || item.date;
    if (!ds) return true;
    const t = typeof ds === "number" ? ds : Date.parse(ds);
    return isNaN(t) ? true : t >= cutoff;
  };

  // Paid beats the user bought (dated) + free beats they downloaded (undated →
  // counted only in all-time, since free_licence_agreements have no client date).
  const fLeases   = (leases || []).filter(inWindow);
  const fProjects = (projects || []).filter(inWindow);
  const freeCount = statWindow === "all" ? (freeIds || []).length : 0;

  const downloads = fLeases.length + freeCount;
  const lyricsCount = statWindow === "all" ? (savedLyrics || []).length : (savedLyrics || []).filter(inWindow).length;
  const savedCount = (savedIds && (savedIds.size != null ? savedIds.size : (savedIds.length || 0))) || 0;
  const projectsCount = fProjects.length;

  // Producer-side sales — accurate, from /my-sales (paid purchases only).
  const s = sales || {};
  const beatsSold = statWindow === "week" ? (s.beats_sold_week || 0)
                  : statWindow === "month" ? (s.beats_sold_month || 0)
                  : (s.beats_sold || 0);
  const beatsUploaded = s.beats_uploaded || 0;

  const stats = [
    { label: "Beats Downloaded", value: downloads, color: "#a78bfa", note: statWindow !== "all" ? "paid this period" : null },
    { label: "Lyrics Saved", value: lyricsCount, color: "#c026d3" },
    { label: "Saved Beats", value: savedCount, color: "#22d3ee", allTimeOnly: true },
    { label: "Projects", value: projectsCount, color: "#3b82f6" },
  ];
  if (user && user.isPro) {
    stats.push({ label: "Beats Sold", value: beatsSold, color: "#22c55e" });
    stats.push({ label: "Free DLs Given", value: (s.free_downloads_given || 0), color: "#f472b6", allTimeOnly: true });
    stats.push({ label: "Beats Uploaded", value: beatsUploaded, color: "#fbbf24", allTimeOnly: true });
  }
  if (user) {
    stats.push({ label: "Followers", value: (user.followers_count || (user.followers && user.followers.length) || 0), color: "#fb923c", allTimeOnly: true });
    stats.push({ label: "Following", value: (user.following_count || (user.following && user.following.length) || 0), color: "#60a5fa", allTimeOnly: true });
  }

  return (
    <div>
      <div style={{ display: "flex", gap: 6, marginBottom: 16, background: "rgba(20,15,40,0.5)", padding: 4, borderRadius: 10, border: "1px solid rgba(167,139,250,0.12)" }}>
        {[{ id: "all", label: "All Time" }, { id: "month", label: "This Month" }, { id: "week", label: "This Week" }].map(function(w) {
          const active = statWindow === w.id;
          return (
            <button key={w.id} onClick={function(){ setStatWindow(w.id); }} style={{
              flex: 1, padding: "8px 6px", borderRadius: 7, border: "none", cursor: "pointer",
              background: active ? "linear-gradient(135deg, rgba(167,139,250,0.25), rgba(124,58,237,0.18))" : "transparent",
              color: active ? "#e0d4ff" : "#7a6ea0", fontWeight: 800, fontSize: 11, letterSpacing: 0.3,
              boxShadow: active ? "0 0 10px rgba(167,139,250,0.2)" : "none",
            }}>{w.label}</button>
          );
        })}
      </div>

      <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: 10 }}>
        {stats.map(function(s, i) {
          const dimmed = s.allTimeOnly && statWindow !== "all";
          return (
            <div key={i} style={{
              background: "linear-gradient(180deg, #1a1530 0%, #14102a 100%)",
              border: "1px solid rgba(167,139,250,0.18)", borderRadius: 14,
              padding: "16px 14px", opacity: dimmed ? 0.5 : 1,
              boxShadow: "0 0 14px rgba(124,58,237,0.05) inset",
            }}>
              <div style={{
                fontSize: 30, fontWeight: 900, color: s.color, lineHeight: 1,
                fontFamily: "'Bebas Neue', sans-serif", letterSpacing: 1,
                textShadow: "0 0 12px " + s.color + "55",
              }}>{dimmed ? "—" : s.value}</div>
              <div style={{ fontSize: 10, color: "#9590a8", marginTop: 6, fontWeight: 700, letterSpacing: 0.5, textTransform: "uppercase" }}>{s.label}</div>
              {s.allTimeOnly && (
                <div style={{ fontSize: 8, color: "#5a4e7a", marginTop: 3 }}>all-time only</div>
              )}
              {!s.allTimeOnly && s.note && !dimmed && (
                <div style={{ fontSize: 8, color: "#5a4e7a", marginTop: 3 }}>{s.note}</div>
              )}
            </div>
          );
        })}
      </div>

      <div style={{ textAlign: "center", color: "#5a4e7a", fontSize: 11, padding: "16px 0 4px", lineHeight: 1.5 }}>
        {user && user.isPro ? "Producer Pro" : "Artist Pro"} member{user && user.created_at ? " since " + new Date(user.created_at).getFullYear() : ""}
      </div>
    </div>
  );
}

// =============================================================================
// HotBeatCard — "This Week's Hottest" hero used on both the Free MP3s tab
// (free beat) and the Exclusive tab (paid beat). Variant controls label,
// colour, and CTA. Previews inline; never opens the YouTube player.
// =============================================================================
// =============================================================================
// MEMBERS KIT STORE — browse, buy, and download drum kits. Purchased kits
// download their samples into device-local IndexedDB and become available in
// the Studio drum sequencer under "My Kits". Buying uses Stripe Connect.
// =============================================================================
// =============================================================================
// KIT UPLOAD OVERLAY — Producer Pro publishes a drum kit to the marketplace.
// =============================================================================
function KitUploadOverlay({ onClose, onPublished }) {
  const [name, setName]   = React.useState("");
  const [price, setPrice] = React.useState("0");
  const [genre, setGenre] = React.useState("");
  const [coverUrl, setCoverUrl] = React.useState("");
  const [coverUploading, setCoverUploading] = React.useState(false);
  const [cropSrc, setCropSrc] = React.useState(null);   // data URL of image awaiting crop
  const [pads, setPads]   = React.useState([
    { id: "kick",       name: "Kick",       color: "#A78BFA", url: "", uploading: false },
    { id: "snare",      name: "Snare",      color: "#A78BFA", url: "", uploading: false },
    { id: "closed-hat", name: "Closed Hat", color: "#A78BFA", url: "", uploading: false },
    { id: "open-hat",   name: "Open Hat",   color: "#A78BFA", url: "", uploading: false },
    { id: "clap",       name: "Clap",       color: "#A78BFA", url: "", uploading: false },
    { id: "808",        name: "808",        color: "#A78BFA", url: "", uploading: false },
  ]);
  const [busy, setBusy] = React.useState(false);
  const [err, setErr]   = React.useState("");

  const updatePad = function(idx, patch) {
    setPads(function(prev){ const next = prev.slice(); next[idx] = Object.assign({}, next[idx], patch); return next; });
  };
  const addPad = function() {
    setPads(function(prev){ return prev.concat([{ id: "pad" + (prev.length + 1), name: "Pad " + (prev.length + 1), color: "#A78BFA", url: "", uploading: false }]); });
  };
  const removePad = function(idx) {
    setPads(function(prev){ return prev.filter(function(_, i){ return i !== idx; }); });
  };

  const uploadSample = function(idx, file) {
    if (!file) return;
    if (file.size > 5 * 1024 * 1024) { setErr("Sample too large — max 5MB per pad"); return; }
    updatePad(idx, { uploading: true });
    setErr("");
    const fd = new FormData();
    fd.append("file", file);
    const token = getToken ? (getToken() || "") : "";
    fetch(API_BASE + "/api/kits/upload-sample", {
      method: "POST",
      headers: { Authorization: "Bearer " + token },
      body: fd,
    })
      .then(function(r){ return r.json().then(function(d){ if (!r.ok) throw new Error(d.detail || ("HTTP " + r.status)); return d; }); })
      .then(function(d){ updatePad(idx, { url: d.url, uploading: false }); })
      .catch(function(e){ setErr(e.message || "Upload failed"); updatePad(idx, { uploading: false }); });
  };

  // When the user picks an artwork file: read it, measure it. If already
  // square, upload straight away. If not, open the crop modal so they can
  // frame a square region.
  const onPickCover = function(file) {
    if (!file) return;
    if (file.size > 8 * 1024 * 1024) { setErr("Image too large — max 8MB"); return; }
    setErr("");
    const reader = new FileReader();
    reader.onload = function() {
      const dataUrl = String(reader.result || "");
      const img = new Image();
      img.onload = function() {
        if (img.width === img.height) {
          // Already square — still normalise to a clean 512px JPEG and upload.
          uploadCoverFromCrop(dataUrl, img, 0, 0, img.width);
        } else {
          setCropSrc(dataUrl);
        }
      };
      img.onerror = function(){ setErr("Could not read image"); };
      img.src = dataUrl;
    };
    reader.onerror = function(){ setErr("Could not read image"); };
    reader.readAsDataURL(file);
  };

  // Render a square crop region of the source image to a canvas, get a JPEG
  // blob, and upload it. cropX/cropY/cropSize are in source-pixel space.
  const uploadCoverFromCrop = function(dataUrl, imgEl, cropX, cropY, cropSize) {
    setCoverUploading(true);
    setCropSrc(null);
    const OUT = 512;
    const canvas = document.createElement("canvas");
    canvas.width = OUT; canvas.height = OUT;
    const ctx = canvas.getContext("2d");
    ctx.drawImage(imgEl, cropX, cropY, cropSize, cropSize, 0, 0, OUT, OUT);
    canvas.toBlob(function(blob){
      if (!blob) { setErr("Crop failed"); setCoverUploading(false); return; }
      const fd = new FormData();
      fd.append("file", blob, "cover.jpg");
      const token = getToken ? (getToken() || "") : "";
      fetch(API_BASE + "/api/kits/upload-cover", {
        method: "POST",
        headers: { Authorization: "Bearer " + token },
        body: fd,
      })
        .then(function(r){ return r.json().then(function(d){ if (!r.ok) throw new Error(d.detail || ("HTTP " + r.status)); return d; }); })
        .then(function(d){ setCoverUrl(d.url); setCoverUploading(false); })
        .catch(function(e){ setErr(e.message || "Cover upload failed"); setCoverUploading(false); });
    }, "image/jpeg", 0.9);
  };

  const publish = function() {
    setErr("");
    const priceNum = parseFloat(price) || 0;
    if (!name.trim()) { setErr("Give your kit a name"); return; }
    if (priceNum < 0 || priceNum > 29.99) { setErr("Price must be £0–£29.99"); return; }
    const filledPads = pads.filter(function(p){ return p.url; });
    if (filledPads.length === 0) { setErr("Add a sample to at least one pad"); return; }
    setBusy(true);
    apiFetch("/api/kits/create", {
      method: "POST",
      body: JSON.stringify({
        name: name.trim(),
        price: priceNum,
        genre: genre.trim(),
        cover_url: coverUrl,
        pads: filledPads.map(function(p){ return { id: p.id, name: p.name, color: p.color, url: p.url }; }),
      }),
    })
      .then(function(){ setBusy(false); onPublished && onPublished(); onClose && onClose(); })
      .catch(function(e){ setBusy(false); setErr((e && e.message) || "Publish failed"); });
  };

  const field = { width: "100%", background: "#0f0a1f", border: "1px solid rgba(167,139,250,0.25)", borderRadius: 9, color: "#e8e6f0", fontSize: 14, padding: "10px 12px", boxSizing: "border-box", fontFamily: "'DM Sans',sans-serif" };
  const labelS = { color: "#9590a8", fontSize: 11, fontWeight: 700, letterSpacing: 0.5, textTransform: "uppercase", marginBottom: 6, display: "block" };

  return (
    <div style={{
      position: "fixed", inset: 0, zIndex: 100001,
      background: "rgba(8,5,18,0.97)", backdropFilter: "blur(6px)",
      display: "flex", flexDirection: "column", fontFamily: "'DM Sans', sans-serif",
    }}>
      <div style={{ display: "flex", alignItems: "center", gap: 12, padding: "14px 16px", borderBottom: "1px solid rgba(167,139,250,0.18)", background: "linear-gradient(180deg,#14102a,#0f0a1f)", flexShrink: 0, paddingTop: "calc(14px + env(safe-area-inset-top))" }}>
        <div style={{ flex: 1 }}>
          <div style={{ color: "#e8e6f0", fontWeight: 800, fontSize: 16 }}>Upload / Sell Drum Kit</div>
        </div>
        <button onClick={onClose} style={{ width: 34, height: 34, borderRadius: "50%", background: "rgba(167,139,250,0.08)", border: "1px solid rgba(167,139,250,0.25)", color: "#c4b5fd", cursor: "pointer", display: "flex", alignItems: "center", justifyContent: "center", flexShrink: 0 }}>
          <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round"><line x1="18" y1="6" x2="6" y2="18"/><line x1="6" y1="6" x2="18" y2="18"/></svg>
        </button>
      </div>

      <div style={{ flex: 1, overflowY: "auto", WebkitOverflowScrolling: "touch", padding: "16px", paddingBottom: "calc(24px + env(safe-area-inset-bottom))" }}>
        <div style={{ display: "flex", gap: 14, marginBottom: 16, alignItems: "flex-start" }}>
          <label style={{
            width: 75, height: 75, borderRadius: 12, flexShrink: 0, cursor: "pointer",
            background: coverUrl ? "#000" : "linear-gradient(135deg,#C026D3,#7C3AED)",
            border: "1px solid rgba(167,139,250,0.3)",
            display: "flex", alignItems: "center", justifyContent: "center",
            color: "#fff", position: "relative", overflow: "hidden",
            boxShadow: coverUrl ? "0 0 12px rgba(192,38,211,0.3)" : "none",
          }}>
            {coverUrl && (
              <img src={coverUrl} alt="" style={{
                position: "absolute", top: 0, left: 0, right: 0, bottom: 0,
                width: "100%", height: "100%",
                objectFit: "cover", objectPosition: "center center", display: "block",
              }} />
            )}
            {coverUploading
              ? <span style={{ fontSize: 11, fontWeight: 700, position: "relative", zIndex: 1 }}>…</span>
              : (!coverUrl ? (
                  <span style={{ display: "flex", flexDirection: "column", alignItems: "center", gap: 3 }}>
                    <AppIcon id="upload" size={18} />
                    <span style={{ fontSize: 8, fontWeight: 700, letterSpacing: 0.5 }}>ARTWORK</span>
                  </span>
                ) : null)}
            <input type="file" accept="image/*" onChange={function(e){ onPickCover(e.target.files && e.target.files[0]); e.target.value = ""; }} style={{ display: "none" }} />
          </label>
          <div style={{ flex: 1, minWidth: 0 }}>
            <label style={labelS}>Kit name</label>
            <input value={name} onChange={function(e){ setName(e.target.value); }} placeholder="e.g. Midnight Trap Vol. 1" style={field} />
            <div style={{ color: "#5a4e7a", fontSize: 10, marginTop: 6 }}>Tap the square to add cover artwork (optional)</div>
          </div>
        </div>

        <div style={{ display: "flex", gap: 12, marginBottom: 16 }}>
          <div style={{ flex: 1 }}>
            <label style={labelS}>Price</label>
            <input value={price} onChange={function(e){ setPrice(e.target.value.replace(/[^0-9.]/g, "")); }} inputMode="decimal" placeholder="0" style={field} />
            <div style={{ color: "#5a4e7a", fontSize: 10, marginTop: 4 }}>{"£0 = free · max £29.99"}</div>
          </div>
          <div style={{ flex: 1 }}>
            <label style={labelS}>Genre</label>
            <input value={genre} onChange={function(e){ setGenre(e.target.value); }} placeholder="Trap, Drill…" style={field} />
          </div>
        </div>

        <label style={labelS}>Pads ({pads.filter(function(p){ return p.url; }).length}/{pads.length} loaded)</label>
        {pads.map(function(p, idx){
          return (
            <div key={idx} style={{ display: "flex", alignItems: "center", gap: 10, background: "#14102a", border: "1px solid rgba(167,139,250,0.15)", borderRadius: 10, padding: "10px", marginBottom: 8 }}>
              <div style={{ width: 30, height: 30, borderRadius: 7, background: "linear-gradient(135deg,#C026D3,#7C3AED)", flexShrink: 0, border: "2px solid rgba(255,255,255,0.12)", boxShadow: p.url ? "0 0 8px rgba(167,139,250,0.6)" : "none", opacity: p.url ? 1 : 0.5 }} />
              <input value={p.name} onChange={function(e){ updatePad(idx, { name: e.target.value }); }} style={{ flex: 1, minWidth: 0, background: "transparent", border: "none", color: "#e8e6f0", fontSize: 13, fontWeight: 600, fontFamily: "'DM Sans',sans-serif" }} />
              <label style={{ flexShrink: 0, background: p.url ? "rgba(34,197,94,0.15)" : "rgba(167,139,250,0.12)", border: "1px solid " + (p.url ? "rgba(34,197,94,0.4)" : "rgba(167,139,250,0.3)"), borderRadius: 7, color: p.url ? "#4ade80" : "#c4b5fd", fontSize: 11, fontWeight: 700, padding: "6px 10px", cursor: "pointer", display: "inline-flex", alignItems: "center", gap: 5 }}>
                {p.uploading ? "…" : (p.url ? <><AppIcon id="check" size={11}/> Loaded</> : <><AppIcon id="upload" size={11}/> Sample</>)}
                <input type="file" accept=".wav,.mp3,.aif,.aiff,.m4a,audio/*" onChange={function(e){ uploadSample(idx, e.target.files && e.target.files[0]); e.target.value = ""; }} style={{ display: "none" }} />
              </label>
              {pads.length > 1 && (
                <button onClick={function(){ removePad(idx); }} style={{ flexShrink: 0, background: "transparent", border: "none", color: "#7a6ea0", cursor: "pointer", fontSize: 20, lineHeight: 1, padding: "0 2px" }}>{"×"}</button>
              )}
            </div>
          );
        })}
        <button onClick={addPad} style={{ width: "100%", background: "transparent", border: "1px dashed rgba(167,139,250,0.3)", borderRadius: 9, color: "#9580c4", fontSize: 12, fontWeight: 700, padding: "10px", cursor: "pointer", marginTop: 4 }}>+ Add pad</button>

        {err && <div style={{ color: "#ff6b6b", fontSize: 12, marginTop: 12, textAlign: "center" }}>{err}</div>}

        {/* Publish button lives at the end of the scroll content so it's always
            reachable even when the on-screen keyboard or iOS chrome covers a
            fixed footer. */}
        <button onClick={publish} disabled={busy} style={{
          width: "100%", marginTop: 18,
          background: "linear-gradient(135deg,#C026D3,#7C3AED)", border: "none", borderRadius: 12,
          color: "#fff", fontWeight: 800, fontSize: 15, padding: "15px", cursor: busy ? "wait" : "pointer",
          boxShadow: "0 0 18px rgba(192,38,211,0.35)",
        }}>{busy ? "Publishing…" : "Save & Upload Kit"}</button>
      </div>

      {/* Crop modal for non-square artwork */}
      {cropSrc && (
        <KitCoverCropModal
          src={cropSrc}
          onCancel={function(){ setCropSrc(null); }}
          onConfirm={function(imgEl, x, y, size){ uploadCoverFromCrop(cropSrc, imgEl, x, y, size); }}
        />
      )}
    </div>
  );
}

// Square-crop modal: shows the image with a draggable square selection that
// the user positions over the part they want as artwork. Outputs source-pixel
// crop coordinates back to the uploader.
function KitCoverCropModal({ src, onCancel, onConfirm }) {
  const imgRef = React.useRef(null);
  const wrapRef = React.useRef(null);
  const [ready, setReady] = React.useState(false);
  const [disp, setDisp] = React.useState({ w: 0, h: 0, natW: 0, natH: 0 });
  // Selection in DISPLAY pixels: { x, y, size } relative to the displayed image.
  const [sel, setSel] = React.useState({ x: 0, y: 0, size: 0 });
  const dragRef = React.useRef(null);

  const onImgLoad = function() {
    const img = imgRef.current;
    if (!img) return;
    const natW = img.naturalWidth, natH = img.naturalHeight;
    const w = img.clientWidth, h = img.clientHeight;
    const size = Math.min(w, h);
    setDisp({ w: w, h: h, natW: natW, natH: natH });
    setSel({ x: (w - size) / 2, y: (h - size) / 2, size: size });
    setReady(true);
  };

  const clamp = function(v, min, max){ return Math.max(min, Math.min(max, v)); };

  const startDrag = function(clientX, clientY) {
    dragRef.current = { startX: clientX, startY: clientY, selX: sel.x, selY: sel.y };
  };
  const moveDrag = function(clientX, clientY) {
    if (!dragRef.current) return;
    const dx = clientX - dragRef.current.startX;
    const dy = clientY - dragRef.current.startY;
    const nx = clamp(dragRef.current.selX + dx, 0, disp.w - sel.size);
    const ny = clamp(dragRef.current.selY + dy, 0, disp.h - sel.size);
    setSel(function(s){ return { x: nx, y: ny, size: s.size }; });
  };
  const endDrag = function(){ dragRef.current = null; };

  const confirm = function() {
    const img = imgRef.current;
    if (!img || !ready) return;
    const scale = disp.natW / disp.w; // source px per display px
    const sx = Math.round(sel.x * scale);
    const sy = Math.round(sel.y * scale);
    const ssize = Math.round(sel.size * scale);
    onConfirm(img, sx, sy, ssize);
  };

  return (
    <div style={{ position: "fixed", inset: 0, zIndex: 100003, background: "rgba(5,3,12,0.97)", display: "flex", flexDirection: "column", fontFamily: "'DM Sans',sans-serif", paddingTop: "calc(14px + env(safe-area-inset-top))" }}>
      <div style={{ padding: "0 16px 12px", display: "flex", alignItems: "center", justifyContent: "space-between" }}>
        <div style={{ color: "#e8e6f0", fontWeight: 800, fontSize: 15 }}>Crop artwork</div>
        <button onClick={onCancel} style={{ background: "transparent", border: "none", color: "#9590a8", fontSize: 14, fontWeight: 700, cursor: "pointer" }}>Cancel</button>
      </div>
      <div style={{ color: "#7a6ea0", fontSize: 12, padding: "0 16px 14px", textAlign: "center" }}>Drag the square over the part you want to show</div>

      <div style={{ flex: 1, display: "flex", alignItems: "center", justifyContent: "center", padding: "0 16px", minHeight: 0 }}>
        <div ref={wrapRef} style={{ position: "relative", maxWidth: "100%", maxHeight: "100%", lineHeight: 0 }}>
          <img ref={imgRef} src={src} onLoad={onImgLoad} alt="" style={{ maxWidth: "100%", maxHeight: "60vh", display: "block", userSelect: "none", WebkitUserSelect: "none" }} draggable={false} />
          {ready && (
            <div
              onPointerDown={function(e){ e.preventDefault(); e.currentTarget.setPointerCapture(e.pointerId); startDrag(e.clientX, e.clientY); }}
              onPointerMove={function(e){ if (dragRef.current){ e.preventDefault(); moveDrag(e.clientX, e.clientY); } }}
              onPointerUp={endDrag}
              onPointerCancel={endDrag}
              style={{
                position: "absolute", left: sel.x, top: sel.y, width: sel.size, height: sel.size,
                border: "2px solid #fff", boxShadow: "0 0 0 9999px rgba(0,0,0,0.55)",
                cursor: "move", touchAction: "none", boxSizing: "border-box",
              }}
            />
          )}
        </div>
      </div>

      <div style={{ padding: "14px 16px", paddingBottom: "calc(14px + env(safe-area-inset-bottom))", flexShrink: 0 }}>
        <button onClick={confirm} style={{ width: "100%", background: "linear-gradient(135deg,#C026D3,#7C3AED)", border: "none", borderRadius: 12, color: "#fff", fontWeight: 800, fontSize: 15, padding: "14px", cursor: "pointer", boxShadow: "0 0 18px rgba(192,38,211,0.35)" }}>Use this crop</button>
      </div>
    </div>
  );
}

function MembersKitStoreTab({ user, onUpload }) {
  const [kits, setKits]   = React.useState(null);
  const [owned, setOwned] = React.useState({});
  const [busy, setBusy]   = React.useState(null);
  const [toast, setToast] = React.useState("");
  const [showUpload, setShowUpload] = React.useState(false);

  const loadAll = React.useCallback(function() {
    apiFetch("/api/kits/list").then(function(r){ setKits((r && r.kits) || []); }).catch(function(){ setKits([]); });
    apiFetch("/api/kits/my-kits").then(function(r){
      const map = {}; ((r && r.kits) || []).forEach(function(k){ map[k.kit_id] = true; });
      setOwned(map);
    }).catch(function(){ setOwned({}); });
  }, []);
  React.useEffect(function(){ loadAll(); }, [loadAll]);

  const flash = function(msg){ setToast(msg); setTimeout(function(){ setToast(""); }, 2600); };

  const installKit = async function(kitId) {
    setBusy(kitId);
    try {
      const payload = await apiFetch("/api/kits/" + kitId + "/download");
      await bfInstallMarketKit(payload);
      await bfRegisterLocalMarketKits();
      flash("Kit added to your device — find it in Studio under My Kits");
    } catch (e) {
      flash("Download failed. " + ((e && e.message) || ""));
    } finally {
      setBusy(null);
    }
  };

  const buyKit = async function(kit) {
    setBusy(kit.id);
    try {
      if (kit.price <= 0) {
        await apiFetch("/api/kits/" + kit.id + "/claim", { method: "POST", body: JSON.stringify({}) });
        setOwned(function(p){ return Object.assign({}, p, { [kit.id]: true }); });
        await installKit(kit.id);
      } else {
        const r = await apiFetch("/api/kits/" + kit.id + "/buy", { method: "POST", body: JSON.stringify({}) });
        if (r && r.checkout_url) { window.location.href = r.checkout_url; }
        else flash("Could not start checkout.");
      }
    } catch (e) {
      flash((e && e.message) || "Purchase failed.");
    } finally {
      setBusy(null);
    }
  };

  const isPro = user && user.isPro;
  const list = kits || [];

  return (
    <div>
      {isPro && (
        <button onClick={function(){ setShowUpload(true); }} style={{
          width: "100%", marginBottom: 14,
          background: "linear-gradient(135deg, rgba(192,38,211,0.2), rgba(124,58,237,0.18))",
          border: "1px solid rgba(192,38,211,0.45)", borderRadius: 12,
          color: "#F3E8FF", fontWeight: 800, fontSize: 13, padding: "12px",
          cursor: "pointer", display: "flex", alignItems: "center", justifyContent: "center", gap: 8,
          boxShadow: "0 0 14px rgba(192,38,211,0.2)",
        }}>
          <AppIcon id="upload" size={15} /> Upload a kit to sell
        </button>
      )}

      {kits === null && (
        <div style={{ textAlign: "center", color: "#5a4e7a", fontSize: 12, padding: "30px 0" }}>Loading kits…</div>
      )}
      {kits !== null && list.length === 0 && (
        <div style={{ textAlign: "center", color: "#7a6ea0", fontSize: 13, padding: "30px 16px", lineHeight: 1.6 }}>
          No kits in the marketplace yet.{isPro ? " Be the first to upload one!" : ""}
        </div>
      )}

      {list.map(function(kit){
        const isOwned = owned[kit.id] || (user && kit.producer_id === (user.id || user._id));
        const downloading = busy === kit.id;
        return (
          <div key={kit.id} style={{
            background: "linear-gradient(180deg, #1a1530 0%, #14102a 100%)",
            border: "1px solid rgba(167,139,250,0.18)", borderRadius: 14,
            padding: "14px", marginBottom: 12,
            display: "flex", alignItems: "center", gap: 14,
          }}>
            <div style={{
              width: 52, height: 52, borderRadius: 11, flexShrink: 0,
              background: kit.cover_url ? "#000" : "linear-gradient(135deg,#C026D3,#7C3AED)",
              display: "flex", alignItems: "center", justifyContent: "center",
              color: "#fff", boxShadow: "0 0 14px rgba(192,38,211,0.25)",
              position: "relative", overflow: "hidden",
            }}>
              {kit.cover_url
                ? <img src={kit.cover_url} alt="" style={{ position: "absolute", top: 0, left: 0, right: 0, bottom: 0, width: "100%", height: "100%", objectFit: "cover", objectPosition: "center center", display: "block" }} />
                : <span style={{ display: "inline-flex" }}><AppIcon id="grid" size={22} /></span>}
            </div>
            <div style={{ flex: 1, minWidth: 0 }}>
              <div style={{ color: "#e8e6f0", fontWeight: 700, fontSize: 14, overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" }}>{kit.name}</div>
              <div style={{ color: "#7a6ea0", fontSize: 11, marginTop: 2 }}>
                {kit.producer || "Producer"} · {kit.pad_count} pad{kit.pad_count === 1 ? "" : "s"}{kit.genre ? " · " + kit.genre : ""}
              </div>
              <div style={{ color: "#5a4e7a", fontSize: 10, marginTop: 3 }}>{kit.downloads} downloads</div>
            </div>
            <div style={{ flexShrink: 0 }}>
              {isOwned ? (
                <button onClick={function(){ installKit(kit.id); }} disabled={downloading} style={{
                  background: "rgba(34,197,94,0.15)", border: "1px solid rgba(34,197,94,0.4)",
                  borderRadius: 9, color: "#4ade80", fontWeight: 800, fontSize: 12,
                  padding: "9px 14px", cursor: downloading ? "wait" : "pointer", whiteSpace: "nowrap",
                  display: "flex", alignItems: "center", gap: 6,
                }}>
                  <AppIcon id="download" size={12} /> {downloading ? "Adding…" : "Download"}
                </button>
              ) : (
                <button onClick={function(){ buyKit(kit); }} disabled={downloading} style={{
                  background: "linear-gradient(135deg,#C026D3,#7C3AED)", border: "none",
                  borderRadius: 9, color: "#fff", fontWeight: 800, fontSize: 12,
                  padding: "9px 16px", cursor: downloading ? "wait" : "pointer", whiteSpace: "nowrap",
                  boxShadow: "0 0 12px rgba(192,38,211,0.35)",
                }}>
                  {downloading ? "…" : (kit.price > 0 ? "£" + kit.price.toFixed(2) : "Get Free")}
                </button>
              )}
            </div>
          </div>
        );
      })}

      {showUpload && (
        <KitUploadOverlay
          onClose={function(){ setShowUpload(false); }}
          onPublished={function(){ loadAll(); flash("Kit published to the marketplace!"); }}
        />
      )}

      {toast && (
        <div style={{
          position: "fixed", bottom: 90, left: "50%", transform: "translateX(-50%)",
          zIndex: 99999, background: "linear-gradient(135deg, rgba(167,139,250,0.97), rgba(124,58,237,0.97))",
          color: "white", fontSize: 12, fontWeight: 700, padding: "10px 18px", borderRadius: 20,
          boxShadow: "0 6px 24px rgba(0,0,0,0.5)", maxWidth: "90%", textAlign: "center",
        }}>{toast}</div>
      )}
    </div>
  );
}

function HotBeatCard({ beat, variant, onViewProfile, onOpenBeat }) {
  const [playing, setPlaying] = React.useState(false);
  const audioRef = React.useRef(null);
  if (!beat) return null;

  const isPaid = variant === "paid";
  const A1 = isPaid ? "#7C3AED" : "#C026D3";
  const A2 = isPaid ? "#3B82F6" : "#7C3AED";
  const labelColor = isPaid ? "#C4B5FD" : "#F0ABFC";
  const borderCol = isPaid ? "rgba(124,58,237,0.5)" : "rgba(192,38,211,0.5)";
  const glow = isPaid ? "rgba(124,58,237,0.2)" : "rgba(192,38,211,0.2)";
  const label = isPaid ? "This Week's Hottest Exclusive Beat" : "This Week's Hottest Free Beat";

  const toggle = function(e){
    e.stopPropagation();
    const a = audioRef.current;
    if (!a) return;
    if (playing) { a.pause(); setPlaying(false); }
    else {
      try {
        const ps = beat.preview_start || 0;
        if (ps && (a.currentTime < ps || a.currentTime > ps + 60)) a.currentTime = ps;
      } catch(_){}
      a.play().then(function(){ setPlaying(true); }).catch(function(){ setPlaying(false); });
    }
  };

  const priceLabel = isPaid
    ? (beat.basic_lease_price ? "£" + beat.basic_lease_price : (beat.price && beat.price !== "free" ? beat.price : "Buy"))
    : "Free";

  return (
    <div style={{
      position: "relative", overflow: "hidden",
      background: isPaid
        ? "linear-gradient(135deg, #1a0a3a 0%, #12092a 50%, #0a0f24 100%)"
        : "linear-gradient(135deg, #2a0a3a 0%, #1a0a2e 50%, #0f0a1f 100%)",
      border: "1.5px solid " + borderCol, borderRadius: 16,
      padding: "16px", marginBottom: 16,
      boxShadow: "0 8px 28px rgba(0,0,0,0.5), 0 0 24px " + glow,
    }}>
      <div style={{
        color: labelColor, fontWeight: 900, fontSize: 10, letterSpacing: 1.5,
        textTransform: "uppercase", marginBottom: 10,
        display: "flex", alignItems: "center", gap: 6,
        textShadow: "0 0 8px " + labelColor + "80",
      }}>
        <AppIcon id="flame" size={14} /> {label}
      </div>
      <div style={{ display: "flex", alignItems: "center", gap: 14 }}>
        <div style={{
          width: 56, height: 56, borderRadius: 12, flexShrink: 0,
          background: beat.beat_artwork ? "#000" : "linear-gradient(135deg," + A1 + "," + A2 + ")",
          backgroundImage: beat.beat_artwork ? "url(" + beat.beat_artwork + ")" : "none",
          backgroundSize: "cover", backgroundPosition: "center",
          display: "flex", alignItems: "center", justifyContent: "center",
          boxShadow: "0 0 16px " + glow,
        }}>
          <button onClick={toggle} disabled={!beat.url} style={{
            background: "transparent", border: "none", cursor: beat.url ? "pointer" : "default",
            color: "#fff", display: "inline-flex", alignItems: "center", justifyContent: "center",
            padding: 0, marginLeft: playing ? 0 : 2,
            filter: beat.beat_artwork ? "drop-shadow(0 1px 4px rgba(0,0,0,0.8))" : "none",
          }}>
            <AppIcon id={playing ? "pause" : "play"} size={22} />
          </button>
        </div>
        <div style={{ flex: 1, minWidth: 0 }}>
          <div style={{ color: "#fff", fontWeight: 800, fontSize: 16, overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" }}>{beat.title}</div>
          <div style={{ color: labelColor, fontSize: 12, marginTop: 2 }}>
            {beat.producer_username
              ? <span onClick={function(e){ e.stopPropagation(); onViewProfile && onViewProfile(beat.producer_username); }}
                  style={{ color: "#E0D4FF", fontWeight: 700, textDecoration: "underline", textDecorationColor: "rgba(224,212,255,0.4)", cursor: "pointer" }}>
                  {beat.producer || ("@" + beat.producer_username)}
                </span>
              : (beat.producer || "Producer")}
            {beat.genre ? " · " + beat.genre : ""}
          </div>
          <div style={{ color: "#7a6ea0", fontSize: 10, marginTop: 4, display: "flex", gap: 10 }}>
            <span style={{ display: "inline-flex", alignItems: "center", gap: 3 }}><AppIcon id="download" size={10} /> {beat.downloads}</span>
            <span style={{ display: "inline-flex", alignItems: "center", gap: 3 }}><AppIcon id="play" size={10} /> {beat.playCount}</span>
          </div>
        </div>
        <button onClick={function(e){ e.stopPropagation(); onOpenBeat && onOpenBeat(beat); }} style={{
          background: "linear-gradient(135deg," + A1 + "," + A2 + ")", borderRadius: 12,
          border: "none", cursor: "pointer", flexShrink: 0,
          color: "#fff", fontWeight: 800, fontSize: 12, padding: "10px 14px",
          boxShadow: "0 0 16px " + glow,
        }}>{priceLabel}</button>
      </div>
      {beat.url ? (
        <audio ref={audioRef} src={beat.url} preload="none"
          onEnded={function(){ setPlaying(false); }}
          onPause={function(){ setPlaying(false); }}
          onPlay={function(){ setPlaying(true); }}
          style={{ display: "none" }} />
      ) : null}
    </div>
  );
}

// Swipeable image carousel for news articles. Single image → just shows it.
function NewsImageCarousel({ images }) {
  const [idx, setIdx] = React.useState(0);
  const scrollRef = React.useRef(null);
  const imgs = (images || []).filter(Boolean);
  if (imgs.length === 0) return null;

  const onScroll = function(e){
    const el = e.currentTarget;
    const w = el.clientWidth || 1;
    const i = Math.round(el.scrollLeft / w);
    if (i !== idx) setIdx(Math.max(0, Math.min(imgs.length - 1, i)));
  };
  const goTo = function(i){
    const el = scrollRef.current;
    if (!el) return;
    el.scrollTo({ left: i * el.clientWidth, behavior: "smooth" });
  };

  return (
    <div style={{ marginBottom: 16 }}>
      <div style={{ position: "relative", width: "100%", borderRadius: 14, overflow: "hidden", background: "#000", border: "1px solid rgba(167,139,250,0.25)" }}>
        <div
          ref={scrollRef}
          onScroll={onScroll}
          style={{
            display: "flex", overflowX: "auto", scrollSnapType: "x mandatory",
            WebkitOverflowScrolling: "touch", scrollbarWidth: "none",
          }}
        >
          {imgs.map(function(src, i){
            return (
              <div key={i} style={{ flex: "0 0 100%", width: "100%", scrollSnapAlign: "center", display: "flex", alignItems: "center", justifyContent: "center", background: "#000" }}>
                <img src={src} alt="" style={{ width: "100%", height: "auto", display: "block", objectFit: "contain" }} />
              </div>
            );
          })}
        </div>

        {imgs.length > 1 && (
          <div style={{ position: "absolute", top: 10, right: 12, background: "rgba(0,0,0,0.6)", color: "#fff", fontSize: 11, fontWeight: 700, padding: "3px 9px", borderRadius: 12 }}>
            {idx + 1} / {imgs.length}
          </div>
        )}
      </div>

      {imgs.length > 1 && (
        <div style={{ display: "flex", justifyContent: "center", gap: 7, marginTop: 10 }}>
          {imgs.map(function(_, i){
            return (
              <button key={i} onClick={function(){ goTo(i); }} style={{
                width: i === idx ? 22 : 8, height: 8, borderRadius: 4, padding: 0, cursor: "pointer",
                border: "none", transition: "width 0.2s, background 0.2s",
                background: i === idx ? "#A78BFA" : "rgba(167,139,250,0.3)",
              }} />
            );
          })}
        </div>
      )}
    </div>
  );
}

// =============================================================================
// MEMBERS — DISCOVER / NEWS TAB (admin-editorial: artists, releases,
// tutorials, beat breakdowns, interviews, gear). Members read; admins publish.
// =============================================================================
const NEWS_CATEGORIES = [
  { id: "new_artists", label: "New Artists",     icon: "flame",    color: "#C026D3" },
  { id: "releases",    label: "Releases",        icon: "star",     color: "#A78BFA" },
  { id: "tutorials",   label: "Tutorials",       icon: "sliders",  color: "#3B82F6" },
  { id: "breakdowns",  label: "Beat Breakdowns", icon: "grid",     color: "#22D3EE" },
  { id: "interviews",  label: "Interviews",      icon: "mic",      color: "#FBBF24" },
  { id: "gear",        label: "Gear & News",     icon: "bars",     color: "#4ADE80" },
];
function newsCatMeta(id) {
  for (let i = 0; i < NEWS_CATEGORIES.length; i++) if (NEWS_CATEGORIES[i].id === id) return NEWS_CATEGORIES[i];
  return { id: id, label: id || "News", icon: "star", color: "#A78BFA" };
}
// Turn a YouTube/Vimeo URL into an embeddable iframe src. Returns "" if it
// doesn't look like a supported video link. Also supports YouTube playlists /
// albums (playlist?list=... or any URL carrying a &list= id).
function newsVideoEmbed(url) {
  if (!url) return "";
  const u = String(url).trim();
  // YouTube playlist / album — playlist?list=ID, or a watch URL with &list=ID.
  // (Album links from YouTube Music use OLAK5uy_... playlist ids.)
  let pl = u.match(/[?&]list=([A-Za-z0-9_-]+)/);
  if (pl && pl[1] && /youtu/.test(u)) {
    return "https://www.youtube.com/embed/videoseries?list=" + pl[1];
  }
  // YouTube single video — watch?v=, youtu.be/, /shorts/, /embed/
  let m = u.match(/(?:youtube\.com\/(?:watch\?(?:.*&)?v=|shorts\/|embed\/)|youtu\.be\/)([A-Za-z0-9_-]{6,})/);
  if (m && m[1]) return "https://www.youtube.com/embed/" + m[1];
  // Vimeo — vimeo.com/123456789
  m = u.match(/vimeo\.com\/(?:video\/)?(\d{6,})/);
  if (m && m[1]) return "https://player.vimeo.com/video/" + m[1];
  return "";
}
// Derive a thumbnail image URL from a video link, so an article with a video
// but no uploaded cover still has a card/hero image automatically.
function newsVideoThumb(url) {
  if (!url) return "";
  const u = String(url).trim();
  // Single video id → thumbnail. (Playlist-only links have no single id, so
  // those articles should use an uploaded cover image.)
  const m = u.match(/(?:youtube\.com\/(?:watch\?(?:.*&)?v=|shorts\/|embed\/)|youtu\.be\/)([A-Za-z0-9_-]{6,})/);
  if (m && m[1]) return "https://img.youtube.com/vi/" + m[1] + "/hqdefault.jpg";
  // Vimeo thumbnails need an API call; skip (Vimeo articles can use an upload).
  return "";
}

function MembersNewsTab({ user, isAdmin }) {
  const [articles, setArticles] = React.useState(null); // null = loading
  const [cat, setCat]           = React.useState("");    // "" = all
  const [open, setOpen]         = React.useState(null);  // full article being read
  const [editor, setEditor]     = React.useState(null);  // {mode:'new'|'edit', article?}
  const [err, setErr]           = React.useState("");
  const [page, setPage]         = React.useState(1);
  const [totalPages, setTotalPages] = React.useState(1);

  const load = React.useCallback(function(){
    setArticles(null); setErr("");
    const q = [];
    if (cat) q.push("category=" + cat);
    q.push("page=" + page);
    q.push("per_page=12");
    apiFetch("/api/news/list?" + q.join("&"))
      .then(function(r){
        setArticles((r && r.articles) || []);
        setTotalPages((r && r.total_pages) || 1);
      })
      .catch(function(){ setErr("Couldn't load news right now."); setArticles([]); setTotalPages(1); });
  }, [cat, page]);
  React.useEffect(function(){ load(); }, [load]);
  // When the category changes, jump back to page 1.
  React.useEffect(function(){ setPage(1); }, [cat]);

  const openArticle = function(a){
    // Fetch the full body (list payload omits it) + bump views.
    apiFetch("/api/news/" + a.id)
      .then(function(full){ setOpen(full || a); })
      .catch(function(){ setOpen(a); });
  };

  // Admin: pin / unpin an article into the top-3 spots.
  const togglePin = function(a, e){
    if (e) { e.stopPropagation(); }
    apiFetch("/api/news/" + a.id + "/pin", { method: "POST", body: JSON.stringify({ pinned: !a.pinned }) })
      .then(function(){ if (open && open.id === a.id) setOpen(Object.assign({}, open, { pinned: !a.pinned })); load(); })
      .catch(function(err){ alert((err && err.message) ? err.message : "Couldn't change pin (max 3 pinned)."); });
  };

  // ── Full article reader ──
  if (open) {
    const meta  = newsCatMeta(open.category);
    const embed = newsVideoEmbed(open.video_url);
    // Bottom gallery = real uploaded images, or the single cover image. We do
    // NOT fall back to the YouTube thumbnail here, because when there's a video
    // it already plays at the top — a thumbnail gallery would be redundant.
    const gallery = (Array.isArray(open.images) && open.images.length)
      ? open.images
      : (open.cover_url && !embed ? [open.cover_url] : []);
    return (
      <div>
        <button onClick={function(){ setOpen(null); }} style={{
          display: "inline-flex", alignItems: "center", gap: 6,
          background: "rgba(167,139,250,0.10)", border: "1px solid rgba(167,139,250,0.35)",
          color: "#C4B5FD", fontWeight: 700, fontSize: 13,
          cursor: "pointer", padding: "8px 14px 8px 10px", borderRadius: 22, marginBottom: 16,
          boxShadow: "0 2px 10px rgba(0,0,0,0.3)",
        }}><AppIcon id="back" size={18} /> Back</button>

        {/* Video at the TOP (if the article has one) */}
        {embed && (
          <div style={{ position: "relative", width: "100%", paddingTop: "56.25%", borderRadius: 14, overflow: "hidden", background: "#000", border: "1px solid rgba(167,139,250,0.25)", marginBottom: 16 }}>
            <iframe src={embed} title={open.title} frameBorder="0"
              allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture"
              allowFullScreen
              style={{ position: "absolute", top: 0, left: 0, width: "100%", height: "100%" }} />
          </div>
        )}
        {/* If there's NO video, show the image carousel as the hero up top */}
        {!embed && gallery.length > 0 && (
          <NewsImageCarousel images={gallery} />
        )}

        <div style={{ display: "flex", alignItems: "center", gap: 8, marginBottom: 10 }}>
          <span style={{ display: "inline-flex", alignItems: "center", gap: 5, background: meta.color + "22", border: "1px solid " + meta.color + "66", color: meta.color, borderRadius: 20, padding: "4px 10px", fontSize: 10, fontWeight: 800, letterSpacing: 0.5, textTransform: "uppercase" }}>
            <AppIcon id={meta.icon} size={12} /> {meta.label}
          </span>
          <span style={{ color: "#7a6ea0", fontSize: 11 }}>{open.views || 0} views</span>
        </div>

        <h2 style={{ color: "#fff", fontSize: 22, fontWeight: 800, lineHeight: 1.2, margin: "0 0 14px" }}>{open.title}</h2>

        {open.body ? (
          <div style={{ color: "#cbc4dd", fontSize: 15, lineHeight: 1.7, whiteSpace: "pre-wrap", marginBottom: (embed && gallery.length > 0) ? 18 : 0 }}>{open.body}</div>
        ) : (
          <div style={{ color: "#7a6ea0", fontSize: 13, marginBottom: (embed && gallery.length > 0) ? 18 : 0 }}>{open.excerpt}</div>
        )}

        {/* Image gallery at the BOTTOM (only when there's also a video up top) */}
        {embed && gallery.length > 0 && (
          <div style={{ marginTop: 4 }}>
            <div style={{ color: "#9590a8", fontSize: 11, fontWeight: 700, letterSpacing: 0.5, textTransform: "uppercase", marginBottom: 8 }}>Gallery</div>
            <NewsImageCarousel images={gallery} />
          </div>
        )}
        {/* ARTICLE_BODY_END */}

        {isAdmin && (
          <div style={{ display: "flex", gap: 8, marginTop: 20, paddingTop: 16, borderTop: "1px solid rgba(167,139,250,0.15)" }}>
            <button onClick={function(){ togglePin(open); }} style={{ flex: 1, background: open.pinned ? "rgba(251,191,36,0.16)" : "rgba(251,191,36,0.08)", border: "1px solid rgba(251,191,36,0.45)", color: "#FBBF24", borderRadius: 9, padding: "10px", fontWeight: 700, fontSize: 12, cursor: "pointer", display: "flex", alignItems: "center", justifyContent: "center", gap: 5 }}><AppIcon id="star" size={13} /> {open.pinned ? "Unpin" : "Pin to top"}</button>
            <button onClick={function(){ setEditor({ mode: "edit", article: open }); }} style={{ flex: 1, background: "rgba(167,139,250,0.12)", border: "1px solid rgba(167,139,250,0.35)", color: "#A78BFA", borderRadius: 9, padding: "10px", fontWeight: 700, fontSize: 12, cursor: "pointer" }}>Edit</button>
            <button onClick={function(){
              if (!window.confirm("Delete this article?")) return;
              apiFetch("/api/news/" + open.id, { method: "DELETE" })
                .then(function(){ setOpen(null); load(); })
                .catch(function(){ alert("Delete failed."); });
            }} style={{ flex: 1, background: "rgba(255,107,107,0.10)", border: "1px solid rgba(255,107,107,0.35)", color: "#ff6b6b", borderRadius: 9, padding: "10px", fontWeight: 700, fontSize: 12, cursor: "pointer" }}>Delete</button>
          </div>
        )}

        {editor && (
          <NewsEditorOverlay
            mode={editor.mode}
            article={editor.article}
            onClose={function(){ setEditor(null); }}
            onSaved={function(saved){ setEditor(null); setOpen(saved); load(); }}
          />
        )}
      </div>
    );
  }

  // ── Feed ──
  return (
    <div>
      {/* Category filter chips */}
      <div style={{ display: "flex", gap: 8, overflowX: "auto", WebkitOverflowScrolling: "touch", paddingBottom: 4, marginBottom: 16 }}>
        <button onClick={function(){ setCat(""); }} style={{
          flexShrink: 0, padding: "7px 14px", borderRadius: 20, fontSize: 12, fontWeight: 800, cursor: "pointer", whiteSpace: "nowrap",
          border: cat === "" ? "1.5px solid rgba(167,139,250,0.7)" : "1.5px solid #333",
          background: cat === "" ? "rgba(167,139,250,0.18)" : "transparent",
          color: cat === "" ? "#E0D4FF" : "#666",
        }}>All</button>
        {NEWS_CATEGORIES.map(function(c){
          const on = cat === c.id;
          return (
            <button key={c.id} onClick={function(){ setCat(c.id); }} style={{
              flexShrink: 0, padding: "7px 14px", borderRadius: 20, fontSize: 12, fontWeight: 800, cursor: "pointer", whiteSpace: "nowrap",
              display: "flex", alignItems: "center", gap: 5,
              border: on ? "1.5px solid " + c.color : "1.5px solid #333",
              background: on ? c.color + "22" : "transparent",
              color: on ? c.color : "#666",
            }}><AppIcon id={c.icon} size={13} /> {c.label}</button>
          );
        })}
      </div>

      {isAdmin && (
        <button onClick={function(){ setEditor({ mode: "new" }); }} style={{
          width: "100%", marginBottom: 16, padding: "12px", borderRadius: 12,
          background: "linear-gradient(135deg,rgba(192,38,211,0.18),rgba(124,58,237,0.18))",
          border: "1px solid rgba(192,38,211,0.45)", color: "#F3E8FF",
          fontWeight: 800, fontSize: 13, cursor: "pointer", display: "flex",
          alignItems: "center", justifyContent: "center", gap: 8,
        }}><AppIcon id="plus" size={16} /> Publish an article</button>
      )}

      {articles === null && (
        <div style={{ color: "#7a6ea0", fontSize: 13, textAlign: "center", padding: "40px 0" }}>Loading…</div>
      )}
      {articles !== null && articles.length === 0 && (
        <div style={{ color: "#7a6ea0", fontSize: 13, textAlign: "center", padding: "40px 20px", lineHeight: 1.6 }}>
          {err || "No articles yet. Check back soon."}
        </div>
      )}

      {articles !== null && articles.map(function(a){
        const meta  = newsCatMeta(a.category);
        const embed = newsVideoEmbed(a.video_url);
        const cardImg = a.cover_url || newsVideoThumb(a.video_url);
        return (
          <div key={a.id} onClick={function(){ openArticle(a); }} style={{
            background: "linear-gradient(180deg,#1a1530,#14102a)", borderRadius: 16,
            border: "1px solid rgba(167,139,250,0.18)", overflow: "hidden",
            marginBottom: 14, cursor: "pointer",
            boxShadow: "0 4px 18px rgba(0,0,0,0.35)",
          }}>
            {(cardImg || embed) && (
              <div style={{ position: "relative", width: "100%", paddingTop: "52%", background: "#000" }}>
                {cardImg
                  ? <img src={cardImg} alt="" style={{ position: "absolute", top: 0, left: 0, right: 0, bottom: 0, width: "100%", height: "100%", objectFit: "cover", objectPosition: "center center", display: "block" }} />
                  : <div style={{ position: "absolute", top: 0, left: 0, right: 0, bottom: 0, display: "flex", alignItems: "center", justifyContent: "center", color: meta.color }}><AppIcon id="video" size={40} /></div>}
              </div>
            )}
            <div style={{ padding: "14px 16px" }}>
              <div style={{ display: "flex", alignItems: "center", gap: 8, marginBottom: 8 }}>
                <span style={{ display: "inline-flex", alignItems: "center", gap: 5, background: meta.color + "22", border: "1px solid " + meta.color + "55", color: meta.color, borderRadius: 16, padding: "3px 9px", fontSize: 9, fontWeight: 800, letterSpacing: 0.5, textTransform: "uppercase" }}>
                  <AppIcon id={meta.icon} size={11} /> {meta.label}
                </span>
                {a.pinned && <span style={{ display: "inline-flex", alignItems: "center", gap: 4, background: "rgba(251,191,36,0.18)", border: "1px solid rgba(251,191,36,0.5)", color: "#FBBF24", borderRadius: 16, padding: "3px 8px", fontSize: 9, fontWeight: 800, letterSpacing: 0.5 }}><AppIcon id="star" size={10} /> PINNED</span>}
                {!a.published && <span style={{ color: "#FBBF24", fontSize: 9, fontWeight: 800, letterSpacing: 0.5 }}>DRAFT</span>}
                {isAdmin && (
                  <button onClick={function(e){ togglePin(a, e); }} style={{
                    marginLeft: "auto", flexShrink: 0,
                    background: a.pinned ? "rgba(251,191,36,0.18)" : "rgba(167,139,250,0.10)",
                    border: "1px solid " + (a.pinned ? "rgba(251,191,36,0.5)" : "rgba(167,139,250,0.3)"),
                    color: a.pinned ? "#FBBF24" : "#A78BFA",
                    borderRadius: 8, padding: "4px 9px", fontSize: 10, fontWeight: 800, cursor: "pointer",
                    display: "inline-flex", alignItems: "center", gap: 4,
                  }}><AppIcon id="star" size={11} /> {a.pinned ? "Unpin" : "Pin"}</button>
                )}
              </div>
              <div style={{ color: "#fff", fontWeight: 800, fontSize: 16, lineHeight: 1.25, marginBottom: 6 }}>{a.title}</div>
              {a.excerpt && <div style={{ color: "#9590a8", fontSize: 12, lineHeight: 1.5, display: "-webkit-box", WebkitLineClamp: 2, WebkitBoxOrient: "vertical", overflow: "hidden" }}>{a.excerpt}</div>}
            </div>
          </div>
        );
      })}

      {/* Page numbers */}
      {articles !== null && articles.length > 0 && totalPages > 1 && (
        <div style={{ display: "flex", alignItems: "center", justifyContent: "center", flexWrap: "wrap", gap: 6, marginTop: 18 }}>
          <button onClick={function(){ if (page > 1) setPage(page - 1); }} disabled={page <= 1} style={{
            padding: "8px 12px", borderRadius: 9, fontSize: 13, fontWeight: 800, cursor: page <= 1 ? "default" : "pointer",
            background: "rgba(255,255,255,0.04)", border: "1px solid rgba(167,139,250,0.25)",
            color: page <= 1 ? "#4a4366" : "#A78BFA",
          }}>Prev</button>
          {Array.from({ length: totalPages }).map(function(_, i){
            const p = i + 1;
            const on = p === page;
            return (
              <button key={p} onClick={function(){ setPage(p); }} style={{
                minWidth: 38, padding: "8px 0", borderRadius: 9, fontSize: 13, fontWeight: 800, cursor: "pointer",
                background: on ? "linear-gradient(135deg,rgba(192,38,211,0.25),rgba(124,58,237,0.25))" : "rgba(255,255,255,0.04)",
                border: on ? "1px solid rgba(192,38,211,0.6)" : "1px solid rgba(167,139,250,0.2)",
                color: on ? "#F3E8FF" : "#9590a8",
                boxShadow: on ? "0 0 14px rgba(192,38,211,0.4)" : "none",
              }}>{p}</button>
            );
          })}
          <button onClick={function(){ if (page < totalPages) setPage(page + 1); }} disabled={page >= totalPages} style={{
            padding: "8px 12px", borderRadius: 9, fontSize: 13, fontWeight: 800, cursor: page >= totalPages ? "default" : "pointer",
            background: "rgba(255,255,255,0.04)", border: "1px solid rgba(167,139,250,0.25)",
            color: page >= totalPages ? "#4a4366" : "#A78BFA",
          }}>Next</button>
        </div>
      )}

      {editor && (
        <NewsEditorOverlay
          mode={editor.mode}
          article={editor.article}
          onClose={function(){ setEditor(null); }}
          onSaved={function(){ setEditor(null); load(); }}
        />
      )}
    </div>
  );
}

// Admin-only publish/edit overlay for news articles.
function NewsEditorOverlay({ mode, article, onClose, onSaved }) {
  const editing = mode === "edit" && article;
  const [title, setTitle]       = React.useState(editing ? (article.title || "") : "");
  const [category, setCategory] = React.useState(editing ? (article.category || "new_artists") : "new_artists");
  const [excerpt, setExcerpt]   = React.useState(editing ? (article.excerpt || "") : "");
  const [body, setBody]         = React.useState(editing ? (article.body || "") : "");
  const [videoUrl, setVideoUrl] = React.useState(editing ? (article.video_url || "") : "");
  const [images, setImages]     = React.useState(
    editing
      ? ((Array.isArray(article.images) && article.images.length) ? article.images.slice()
         : (article.cover_url ? [article.cover_url] : []))
      : []
  );
  const [published, setPublished] = React.useState(editing ? !!article.published : true);
  const [uploadingCount, setUploadingCount] = React.useState(0);
  const [busy, setBusy] = React.useState(false);
  const [err, setErr]   = React.useState("");

  // Upload one or more picked image files; append each to the gallery.
  const onPickImages = function(fileList){
    const files = Array.prototype.slice.call(fileList || []);
    if (!files.length) return;
    setErr("");
    setUploadingCount(function(n){ return n + files.length; });
    const token = getToken();
    files.forEach(function(file){
      const fd = new FormData();
      fd.append("file", file);
      fetch(API_BASE + "/api/news/upload-cover", { method: "POST", headers: { Authorization: "Bearer " + token }, body: fd })
        .then(function(r){ return r.json().then(function(j){ if (!r.ok) throw new Error(j.detail || "Upload failed"); return j; }); })
        .then(function(j){ if (j.url) setImages(function(prev){ return prev.concat([j.url]); }); })
        .catch(function(e){ setErr(String(e.message || e)); })
        .then(function(){ setUploadingCount(function(n){ return Math.max(0, n - 1); }); });
    });
  };
  const removeImage = function(i){
    setImages(function(prev){ return prev.filter(function(_, k){ return k !== i; }); });
  };
  const makeCover = function(i){
    setImages(function(prev){
      if (i <= 0 || i >= prev.length) return prev;
      const next = prev.slice();
      const [picked] = next.splice(i, 1);
      next.unshift(picked);
      return next;
    });
  };

  const save = function(){
    if (!title.trim()) { setErr("Title is required."); return; }
    setBusy(true); setErr("");
    const payload = {
      title: title.trim(), category: category, excerpt: excerpt.trim(),
      body: body.trim(), video_url: videoUrl.trim(),
      images: images, cover_url: images[0] || "",
      published: published,
    };
    const path = editing ? ("/api/news/" + article.id + "/update") : "/api/news/create";
    apiFetch(path, { method: "POST", body: JSON.stringify(payload) })
      .then(function(saved){ onSaved && onSaved(saved); })
      .catch(function(e){ setErr("Save failed. " + (e && e.message ? e.message : "")); })
      .then(function(){ setBusy(false); });
  };

  const embed = newsVideoEmbed(videoUrl);
  const labelStyle = { color: "#9590a8", fontSize: 11, fontWeight: 700, letterSpacing: 0.5, textTransform: "uppercase", marginBottom: 6, display: "block" };
  const inputStyle = { width: "100%", boxSizing: "border-box", background: "#0e0a1c", border: "1px solid rgba(167,139,250,0.25)", borderRadius: 10, color: "#e8e6f0", fontSize: 14, padding: "11px 12px", fontFamily: "'DM Sans',sans-serif" };

  return (
    <div style={{ position: "fixed", inset: 0, zIndex: 100004, background: "#0c0818", backgroundImage: "linear-gradient(180deg,#120c24,#0c0818)", display: "flex", flexDirection: "column" }}>
      <div style={{ flexShrink: 0, display: "flex", alignItems: "center", gap: 10, padding: "calc(env(safe-area-inset-top) + 12px) 12px 12px", background: "linear-gradient(180deg,#1a1530,#14102a)", borderBottom: "1px solid rgba(167,139,250,0.2)" }}>
        <button onClick={onClose} style={{ display: "flex", alignItems: "center", gap: 4, background: "transparent", border: "none", color: "#A78BFA", fontWeight: 700, fontSize: 14, cursor: "pointer", padding: "6px 4px", flexShrink: 0 }}>
          <AppIcon id="back" size={18} /> Cancel
        </button>
        <div style={{ flex: 1, textAlign: "center" }}>
          <div style={{ color: "#e8e6f0", fontWeight: 800, fontSize: 16 }}>{editing ? "Edit Article" : "Publish Article"}</div>
        </div>
        <button onClick={onClose} aria-label="Close" style={{ background: "rgba(255,255,255,0.06)", border: "none", color: "#cbc4dd", cursor: "pointer", display: "flex", alignItems: "center", justifyContent: "center", width: 36, height: 36, borderRadius: 18, flexShrink: 0 }}><AppIcon id="x" size={20} /></button>
      </div>

      <div
        style={{
          flex: 1, minHeight: 0, overflowY: "auto",
          WebkitOverflowScrolling: "touch", overscrollBehavior: "contain",
          padding: "14px 16px calc(env(safe-area-inset-bottom) + 96px)",
        }}>
        <label style={labelStyle}>Title</label>
        <input value={title} onChange={function(e){ setTitle(e.target.value); }} placeholder="Article headline" style={{ ...inputStyle, marginBottom: 14 }} />

        <label style={labelStyle}>Category</label>
        <div style={{ display: "flex", flexWrap: "wrap", gap: 8, marginBottom: 14 }}>
          {NEWS_CATEGORIES.map(function(c){
            const on = category === c.id;
            return (
              <button key={c.id} onClick={function(){ setCategory(c.id); }} style={{
                padding: "7px 12px", borderRadius: 18, fontSize: 11, fontWeight: 800, cursor: "pointer",
                display: "flex", alignItems: "center", gap: 5,
                border: on ? "1.5px solid " + c.color : "1.5px solid #333",
                background: on ? c.color + "22" : "transparent",
                color: on ? c.color : "#888",
              }}><AppIcon id={c.icon} size={12} /> {c.label}</button>
            );
          })}
        </div>

        <label style={labelStyle}>Images {images.length > 0 ? "(" + images.length + ")" : ""}</label>
        <div style={{ display: "flex", flexWrap: "wrap", gap: 8, marginBottom: 8 }}>
          {images.map(function(src, i){
            return (
              <div key={i} style={{ position: "relative", width: 84, height: 84, borderRadius: 10, overflow: "hidden", border: i === 0 ? "2px solid #A78BFA" : "1px solid rgba(167,139,250,0.3)", background: "#000", flexShrink: 0 }}>
                <img src={src} alt="" style={{ position: "absolute", top: 0, left: 0, right: 0, bottom: 0, width: "100%", height: "100%", objectFit: "cover", objectPosition: "center center", display: "block" }} />
                {i === 0 ? (
                  <span style={{ position: "absolute", bottom: 0, left: 0, right: 0, background: "rgba(124,58,237,0.85)", color: "#fff", fontSize: 8, fontWeight: 800, textAlign: "center", padding: "2px 0", letterSpacing: 0.5 }}>COVER</span>
                ) : (
                  <button onClick={function(){ makeCover(i); }} style={{ position: "absolute", bottom: 0, left: 0, right: 0, background: "rgba(0,0,0,0.7)", color: "#A78BFA", fontSize: 8, fontWeight: 800, textAlign: "center", padding: "3px 0", letterSpacing: 0.5, border: "none", cursor: "pointer" }}>SET COVER</button>
                )}
                <button onClick={function(){ removeImage(i); }} style={{ position: "absolute", top: 3, right: 3, width: 22, height: 22, borderRadius: "50%", background: "rgba(0,0,0,0.75)", border: "none", color: "#fff", cursor: "pointer", display: "flex", alignItems: "center", justifyContent: "center", padding: 0 }}>
                  <AppIcon id="x" size={12} />
                </button>
              </div>
            );
          })}
          <label style={{ width: 84, height: 84, borderRadius: 10, flexShrink: 0, cursor: "pointer", background: "linear-gradient(135deg,#C026D3,#7C3AED)", border: "1px solid rgba(167,139,250,0.3)", display: "flex", flexDirection: "column", alignItems: "center", justifyContent: "center", color: "#fff", gap: 3 }}>
            <AppIcon id="plus" size={20} />
            <span style={{ fontSize: 8, fontWeight: 800, letterSpacing: 0.5 }}>ADD</span>
            <input type="file" accept="image/*" multiple onChange={function(e){ onPickImages(e.target.files); e.target.value = ""; }} style={{ display: "none" }} />
          </label>
        </div>
        <div style={{ color: "#7a6ea0", fontSize: 11, lineHeight: 1.5, marginBottom: 14 }}>
          {uploadingCount > 0 ? ("Uploading " + uploadingCount + " image" + (uploadingCount > 1 ? "s" : "") + "…") : "Optional. Add one or several for a swipeable gallery (the first is the cover). If you add none, the YouTube thumbnail is used as the cover automatically."}
        </div>

        <label style={labelStyle}>Video or playlist link (YouTube / Vimeo) — optional</label>
        <input value={videoUrl} onChange={function(e){ setVideoUrl(e.target.value); }} placeholder="Video, or a YouTube playlist/album link" style={{ ...inputStyle, marginBottom: videoUrl ? 8 : 14 }} />
        {videoUrl && (
          <div style={{ fontSize: 11, marginBottom: 14, color: embed ? "#4ADE80" : "#FBBF24" }}>
            {embed ? "✓ Link recognised — the video or playlist/album will embed at the top of the article." : "This doesn't look like a YouTube/Vimeo link. It won't embed."}
          </div>
        )}

        <label style={labelStyle}>Excerpt (card preview)</label>
        <textarea value={excerpt} onChange={function(e){ setExcerpt(e.target.value); }} placeholder="One or two lines shown on the feed card" rows={2} style={{ ...inputStyle, marginBottom: 14, resize: "vertical" }} />

        <label style={labelStyle}>Body</label>
        <textarea value={body} onChange={function(e){ setBody(e.target.value); }} placeholder="Write the article…" rows={10} style={{ ...inputStyle, marginBottom: 14, resize: "vertical", lineHeight: 1.6 }} />

        <button onClick={function(){ setPublished(!published); }} style={{
          display: "flex", alignItems: "center", gap: 10, width: "100%",
          background: "transparent", border: "1px solid rgba(167,139,250,0.25)",
          borderRadius: 10, padding: "11px 12px", cursor: "pointer", marginBottom: 18,
        }}>
          <span style={{ width: 42, height: 24, borderRadius: 12, background: published ? "#4ADE80" : "#333", position: "relative", flexShrink: 0, transition: "background 0.15s" }}>
            <span style={{ position: "absolute", top: 2, left: published ? 20 : 2, width: 20, height: 20, borderRadius: "50%", background: "#fff", transition: "left 0.15s" }} />
          </span>
          <span style={{ color: "#e8e6f0", fontSize: 13, fontWeight: 700 }}>{published ? "Published (visible to members)" : "Draft (hidden)"}</span>
        </button>

        {err && <div style={{ color: "#ff6b6b", fontSize: 12, marginBottom: 12 }}>{err}</div>}

        <button onClick={save} disabled={busy} style={{
          width: "100%", padding: "14px", borderRadius: 12, border: "none",
          background: busy ? "#3a3550" : "linear-gradient(135deg,#C026D3,#7C3AED)",
          color: "#fff", fontWeight: 800, fontSize: 15, cursor: busy ? "default" : "pointer",
        }}>{busy ? "Saving…" : (editing ? "Save changes" : "Publish article")}</button>
      </div>
    </div>
  );
}

function ExclusiveScreen({ user, onGoProfile, onPlay, savedIds, onSave, onSignUp, onViewProfile, savedLyrics, onEditLyric, onGoStudio, onGenreSearch }) {
  const isPro = user?.isPro || user?.isArtistPro;
  const [tab, setTab] = useState("discover");
  // Keep non-paid users out of pro-locked tabs (e.g. after expiry mid-view).
  React.useEffect(function(){
    const isPaid = user && (user.isPro || user.isArtistPro);
    if (!isPaid && (tab === "kits" || tab === "library" || tab === "stats")) {
      setTab("discover");
    }
  }, [user, tab]);
  // Wave 2 — this week's hottest free beat + featured artist of the week.
  const [featured, setFeatured] = useState(null);
  const isAdmin = user && (user.is_admin === true || user.username === "Trelli");
  useEffect(function() {
    if (!isPro) return;
    let alive = true;
    apiFetch("/api/producer/featured")
      .then(function(r){ if (alive && r) setFeatured(r); })
      .catch(function(){ /* silent — hero cards just won't render */ });
    return function(){ alive = false; };
  }, [isPro]);

  // Non-member locked screen
  if (!isPro) return (
    <div style={{ paddingBottom: 100, overflowY: "auto" }}>
      
      <div style={{ background: "linear-gradient(160deg,#1a0a2e,#10082d,#0a0a14)", padding: "32px 20px 24px", textAlign: "center", borderBottom: "1px solid rgba(124,58,237,0.25)" }}>
        <div style={{ fontSize: 44, marginBottom: 10, color: "#A78BFA", filter: "drop-shadow(0 0 12px rgba(124,58,237,0.6))" }}><AppIcon id="lock" size={36} /></div>
        <div style={{
          fontFamily: "'Bebas Neue',sans-serif", fontSize: 34, letterSpacing: 3,
          background: "linear-gradient(135deg,#C026D3 0%,#A855F7 50%,#3B82F6 100%)",
          WebkitBackgroundClip: "text", WebkitTextFillColor: "transparent",
          backgroundClip: "text", color: "transparent",
          marginBottom: 6,
          textShadow: "0 0 24px rgba(124,58,237,0.5)",
          filter: "drop-shadow(0 0 8px rgba(124,58,237,0.4))",
        }}>MEMBERS ONLY</div>
        <div style={{ color: "#aaa", fontSize: 14, lineHeight: 1.6, maxWidth: 300, margin: "0 auto" }}>
          Join the BeatFinder community and unlock the full producer ecosystem
        </div>
      </div>

      
      <div style={{ padding: "20px 20px 0" }}>
        <div style={{
          color: "#A78BFA", fontWeight: 900, fontSize: 12, letterSpacing: 1.5,
          marginBottom: 14, textAlign: "center",
          textShadow: "0 0 8px rgba(124,58,237,0.55)",
        }}>WHAT YOU UNLOCK</div>
        <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: 10, marginBottom: 20 }}>
          {[
            { icon: "note",     color: "#A78BFA", title: "Exclusive Beats", desc: "Access member-only beats unavailable anywhere else" },
            { icon: "download", color: "#A78BFA", title: "MP3 Downloads",   desc: "Download and buy leases directly from producers" },
            { icon: "edit",     color: "#A78BFA", title: "Lyrics/Studio",   desc: "Access Rhyme Finder tool with lyrics, record/mix & master ideas in BeatFinder's DAW" },
            { icon: "knobs",    color: "#A78BFA", title: "Producer Tools",  desc: "Upload beats, sell leases and get paid instantly" },
          ].map(v => (
            <div key={v.title} style={{
              background: "linear-gradient(165deg,#0f0a1f 0%,#0a0a14 60%,#080812 100%)",
              borderRadius: 14, padding: 14,
              border: "1px solid rgba(124,58,237,0.3)",
              boxShadow: "0 4px 16px rgba(0,0,0,0.4), 0 0 12px rgba(124,58,237,0.15), inset 0 1px 0 rgba(255,255,255,0.04)",
            }}>
              <div style={{ marginBottom: 10, filter: "drop-shadow(0 0 6px rgba(124,58,237,0.6))" }}><Icon id={v.icon} size={24} color={v.color} strokeWidth={1.6} /></div>
              <div style={{ color: "white", fontWeight: 800, fontSize: 13, marginBottom: 4 }}>{v.title}</div>
              <div style={{ color: "#888", fontSize: 11, lineHeight: 1.5 }}>{v.desc}</div>
            </div>
          ))}
        </div>

        
        <div style={{ marginBottom: 20 }}>
          <div style={{ color: "#888", fontWeight: 700, fontSize: 12, letterSpacing: 1, marginBottom: 12, textAlign: "center" }}>CHOOSE YOUR PLAN</div>

          {/* Guest plan card */}
          <div style={{ background: "#111", border: "1.5px solid #333", borderRadius: 14, padding: "14px 12px", marginBottom: 10 }}>
            <div style={{ color: "white", fontWeight: 800, fontSize: 13, marginBottom: 2 }}>Guest</div>
            <div style={{ color: "#555", fontWeight: 800, fontSize: 16, marginBottom: 10 }}>£0.00/mo</div>
            <div style={{ color: "#555", fontSize: 11, lineHeight: 1.6 }}>
              Search for beats, write and save lyrics to beats, discover new producers
            </div>
          </div>

          <PlanPicker onSelectPlan={function(planId, priceId, billing) {
            if (user) {
              apiFetch("/api/stripe/create-checkout", { method: "POST", body: JSON.stringify({ price_id: priceId }) })
                .then(function(r) { try { var ct = sessionStorage.getItem("bf_tab") || "exclusive"; sessionStorage.setItem("bf_return_tab", JSON.stringify({ tab: ct, path: window.location.pathname + window.location.search, ts: Date.now() })); } catch(e) {} window.location.href = r.checkout_url; })
                .catch(function(e) { alert("Error: " + e.message); });
            } else {
              onSignUp && onSignUp();
            }
          }} />
        </div>

        
        <button onClick={user ? onGoProfile : onSignUp} style={{
          width: "100%",
          background: "linear-gradient(135deg,#C026D3 0%,#A855F7 35%,#7C3AED 70%,#3B82F6 100%)",
          border: "1.5px solid rgba(192,38,211,0.6)", borderRadius: 32,
          color: "white", fontWeight: 900, padding: "16px", fontSize: 16,
          cursor: "pointer", marginBottom: 20, letterSpacing: 0.5,
          boxShadow: "0 4px 24px rgba(192,38,211,0.5), 0 0 40px rgba(168,85,247,0.35), inset 0 1px 0 rgba(255,255,255,0.25)",
          textShadow: "0 1px 2px rgba(0,0,0,0.3)",
        }}>
          {user ? "Unlock Access" : "Create Account"}
        </button>

        
        <div style={{ background: "#111", borderRadius: 14, padding: "14px 16px", border: "1px solid #1e1e1e", marginBottom: 20 }}>
          <div style={{ color: "#555", fontSize: 11, fontWeight: 700, letterSpacing: 1, marginBottom: 10 }}>WHY PRODUCERS LOVE IT</div>
          {[
            { icon: "stripe", text: "Get paid instantly via Stripe" },
            { icon: "note", text: "Your beats reach real artists daily" },
            { icon: "grid", text: "Track downloads and sales" },
          ].map(r => (
            <div key={r.text} style={{ display: "flex", alignItems: "center", gap: 10, marginBottom: 8, color: "#888", fontSize: 13 }}>
              <Icon id={r.icon} size={14} color="#888" />
              <span>{r.text}</span>
            </div>
          ))}
        </div>
      </div>
    </div>
  );

  // Logged-in pro member view
  return (
    <div style={{ position: "relative", padding: "0 16px 100px", minHeight: "100%", overflow: "hidden" }}>
      {/* ── Cosmic backdrop: starfield + drifting aurora ── */}
      <div aria-hidden="true" style={{
        position: "fixed", inset: 0, zIndex: 0, pointerEvents: "none",
        background: "radial-gradient(1200px 600px at 50% -10%, rgba(124,58,237,0.18), transparent 60%), radial-gradient(900px 500px at 100% 100%, rgba(192,38,211,0.12), transparent 60%), #07050f",
      }}>
        {/* starfield (two layered dotted gradients, slow drift) */}
        <div style={{
          position: "absolute", inset: "-20%",
          backgroundImage: "radial-gradient(1.5px 1.5px at 40px 60px, rgba(196,181,253,0.55), transparent), radial-gradient(1px 1px at 130px 180px, rgba(167,139,250,0.4), transparent), radial-gradient(1.5px 1.5px at 260px 90px, rgba(255,255,255,0.35), transparent), radial-gradient(1px 1px at 320px 260px, rgba(192,38,211,0.4), transparent)",
          backgroundSize: "340px 340px, 200px 200px, 340px 340px, 200px 200px",
          animation: "bf-stars 60s linear infinite", opacity: 0.6,
        }} />
        {/* drifting aurora blob */}
        <div style={{
          position: "absolute", top: "-30%", left: "-10%", width: "120%", height: "80%",
          background: "radial-gradient(closest-side, rgba(124,58,237,0.35), transparent 70%)",
          filter: "blur(40px)", animation: "bf-aurora 18s ease-in-out infinite",
        }} />
        <div style={{
          position: "absolute", bottom: "-20%", right: "-15%", width: "90%", height: "70%",
          background: "radial-gradient(closest-side, rgba(192,38,211,0.28), transparent 70%)",
          filter: "blur(50px)", animation: "bf-aurora 24s ease-in-out infinite reverse",
        }} />
      </div>

      {/* All content sits above the backdrop */}
      <div style={{ position: "relative", zIndex: 1 }}>
      <div style={{ padding: "20px 0 14px" }}>
        {/* ── Holographic command header ── */}
        <div style={{
          position: "relative", overflow: "hidden",
          background: "linear-gradient(135deg, rgba(20,12,42,0.85), rgba(12,8,28,0.92))",
          borderRadius: 20, padding: "20px", marginBottom: 18,
          border: "1px solid rgba(167,139,250,0.45)",
          boxShadow: "0 12px 40px rgba(0,0,0,0.6), 0 0 30px rgba(124,58,237,0.25), inset 0 1px 0 rgba(255,255,255,0.06)",
          backdropFilter: "blur(8px)", WebkitBackdropFilter: "blur(8px)",
        }}>
          {/* animated glowing top edge */}
          <div aria-hidden="true" style={{
            position: "absolute", top: 0, left: 0, right: 0, height: 2,
            background: "linear-gradient(90deg, transparent, #C026D3, #A78BFA, #3B82F6, transparent)",
            animation: "bf-edge 3.2s ease-in-out infinite",
          }} />
          {/* scanline sweep */}
          <div aria-hidden="true" style={{
            position: "absolute", left: 0, right: 0, top: 0, height: "40%",
            background: "linear-gradient(180deg, rgba(167,139,250,0.10), transparent)",
            animation: "bf-scan 5.5s linear infinite", pointerEvents: "none",
          }} />
          {/* faint grid texture */}
          <div aria-hidden="true" style={{
            position: "absolute", inset: 0, opacity: 0.5, pointerEvents: "none",
            backgroundImage: "linear-gradient(rgba(167,139,250,0.06) 1px, transparent 1px), linear-gradient(90deg, rgba(167,139,250,0.06) 1px, transparent 1px)",
            backgroundSize: "26px 26px",
            maskImage: "radial-gradient(circle at 30% 20%, #000, transparent 75%)",
            WebkitMaskImage: "radial-gradient(circle at 30% 20%, #000, transparent 75%)",
          }} />

          <div style={{ position: "relative", display: "flex", alignItems: "center", justifyContent: "space-between", gap: 12 }}>
            <div>
              <div style={{ display: "flex", alignItems: "center", gap: 7, marginBottom: 6 }}>
                <span style={{ width: 7, height: 7, borderRadius: "50%", background: "#4ADE80", boxShadow: "0 0 8px #4ADE80", animation: "bf-pulse-dot 2s ease-in-out infinite", display: "inline-block" }} />
                <span style={{ color: "#A78BFA", fontWeight: 900, fontSize: 10, letterSpacing: 2.5, textShadow: "0 0 10px rgba(124,58,237,0.7)" }}>MEMBERS TERMINAL</span>
              </div>
              <div style={{
                fontFamily: "'Bebas Neue',sans-serif", fontSize: 36, lineHeight: 0.95, letterSpacing: 1.5,
                background: "linear-gradient(110deg, #FFFFFF 10%, #DDD6FE 35%, #C026D3 60%, #A855F7 80%, #FFFFFF 100%)",
                backgroundSize: "220% auto",
                WebkitBackgroundClip: "text", backgroundClip: "text",
                WebkitTextFillColor: "transparent", color: "transparent",
                animation: "bf-holo 6s linear infinite",
                filter: "drop-shadow(0 0 14px rgba(124,58,237,0.45))",
              }}>MEMBERS AREA</div>
              <div style={{ color: "#9b90c4", fontSize: 11, marginTop: 5, letterSpacing: 0.3 }}>Your gateway to the BeatFinder ecosystem</div>
            </div>
            <div style={{
              flexShrink: 0,
              background: user?.isPro
                ? "linear-gradient(135deg,rgba(192,38,211,0.3),rgba(124,58,237,0.3))"
                : "linear-gradient(135deg,rgba(124,58,237,0.3),rgba(59,130,246,0.3))",
              border: "1px solid " + (user?.isPro ? "rgba(192,38,211,0.7)" : "rgba(124,58,237,0.7)"),
              borderRadius: 22, padding: "7px 13px",
              fontSize: 10, color: user?.isPro ? "#F3E8FF" : "#DDD6FE", fontWeight: 900, letterSpacing: 1,
              boxShadow: user?.isPro ? "0 0 16px rgba(192,38,211,0.5), inset 0 0 8px rgba(192,38,211,0.2)" : "0 0 16px rgba(124,58,237,0.5), inset 0 0 8px rgba(124,58,237,0.2)",
              display: "flex", alignItems: "center", gap: 5,
              animation: "bf-float-bob 4s ease-in-out infinite",
            }}>
              <AppIcon id="crown" size={13} /> {user?.isPro ? "PRODUCER PRO" : "ARTIST PRO"}
            </div>
          </div>
        </div>

        {/* Tab bar — a contained segmented "console" grid. All six tabs are
            always visible (3 columns × 2 rows), no horizontal scrolling. The
            grid is fixed-position so switching tabs never shifts layout (this
            is what previously caused the selected-tab glow to glitch). */}
        <div style={{
          display: "grid", gridTemplateColumns: "repeat(3, 1fr)", gap: 6,
          padding: 6, marginBottom: 18, borderRadius: 18,
          background: "linear-gradient(180deg, rgba(20,12,42,0.6), rgba(12,8,28,0.6))",
          border: "1px solid rgba(167,139,250,0.22)",
          boxShadow: "inset 0 1px 0 rgba(255,255,255,0.04), 0 8px 24px rgba(0,0,0,0.45)",
          backdropFilter: "blur(8px)", WebkitBackdropFilter: "blur(8px)",
        }}>
          {[
            { id: "discover", label: "Discover",  icon: "star",     on: "rgba(251,191,36,0.85)", glow: "251,191,36",  txt: "#FEF3C7" },
            { id: "beats",    label: "Exclusive", icon: "flame",    on: "rgba(124,58,237,0.85)", glow: "124,58,237",  txt: "#DDD6FE" },
            { id: "mp3s",     label: "Free MP3s", icon: "download", on: "rgba(192,38,211,0.85)", glow: "192,38,211",  txt: "#F3E8FF" },
            { id: "kits",     label: "Kit Store", icon: "grid",     on: "rgba(192,38,211,0.85)", glow: "192,38,211",  txt: "#F3E8FF", pro: true },
            { id: "library",  label: "Library",   icon: "grid",     on: "rgba(167,139,250,0.85)",glow: "167,139,250", txt: "#E0D4FF", pro: true },
            { id: "stats",    label: "Stats",     icon: "bars",     on: "rgba(59,130,246,0.85)", glow: "59,130,246",  txt: "#DBEAFE", pro: true },
          ].map(function(tb) {
            const active = tab === tb.id;
            const isPaid = user && (user.isPro || user.isArtistPro);
            const locked = tb.pro && !isPaid;
            return (
              <button key={tb.id} onClick={function(){
                if (locked) { if (onSignUp) onSignUp("members_pro"); else window.dispatchEvent(new CustomEvent("bf:quickSignup", { detail: { intent: "members_pro" } })); return; }
                setTab(tb.id);
              }} style={{
                position: "relative", overflow: "hidden",
                display: "flex", flexDirection: "column", alignItems: "center", justifyContent: "center", gap: 5,
                padding: "12px 4px 10px", borderRadius: 13, cursor: "pointer",
                border: active ? "1px solid " + tb.on : "1px solid transparent",
                background: active
                  ? "linear-gradient(180deg, rgba(" + tb.glow + ",0.22), rgba(" + tb.glow + ",0.08))"
                  : "rgba(255,255,255,0.02)",
                color: locked ? "#5a5470" : (active ? tb.txt : "#7a7296"),
                boxShadow: active
                  ? "0 0 18px rgba(" + tb.glow + ",0.4), inset 0 1px 0 rgba(255,255,255,0.06)"
                  : "none",
                transition: "none", opacity: locked ? 0.7 : 1,
              }}>
                {active && (
                  <span aria-hidden="true" style={{
                    position: "absolute", top: 0, left: "22%", right: "22%", height: 2, borderRadius: 2,
                    background: tb.on, boxShadow: "0 0 8px " + tb.on,
                  }} />
                )}
                {locked && (
                  <span aria-hidden="true" style={{ position: "absolute", top: 6, right: 7, color: "#8b82a8" }}><AppIcon id="lock" size={11} /></span>
                )}
                <span style={{ display: "inline-flex", filter: active ? "drop-shadow(0 0 6px rgba(" + tb.glow + ",0.8))" : "none" }}>
                  <AppIcon id={tb.icon} size={18} />
                </span>
                <span style={{
                  fontSize: 10.5, fontWeight: 800, letterSpacing: 0.3, whiteSpace: "nowrap",
                  textShadow: active ? "0 0 8px rgba(" + tb.glow + ",0.6)" : "none",
                }}>{tb.label}</span>
              </button>
            );
          })}
        </div>

        {/* ── Wave 2 hero cards (per tab) ── */}
        {/* Exclusive tab: hottest PAID beat */}
        {tab === "beats" && featured && featured.hottest_paid && (
          <HotBeatCard
            beat={featured.hottest_paid}
            variant="paid"
            onViewProfile={onViewProfile}
            onOpenBeat={function(b){ if (b && b.producer_username) onViewProfile && onViewProfile(b.producer_username); }}
          />
        )}

        {/* Free MP3s tab: hottest FREE beat */}
        {tab === "mp3s" && featured && featured.hottest_free && (
          <HotBeatCard
            beat={featured.hottest_free}
            variant="free"
            onViewProfile={onViewProfile}
            onOpenBeat={function(b){ if (b && b.url) downloadMp3(b.url, b.title || "beat", b.id); }}
          />
        )}

        {tab === "beats" && featured && featured.featured_artist && (
          <div onClick={function(){
            const a = featured.featured_artist;
            onViewProfile && onViewProfile(a.username);
          }} style={{
            position: "relative", overflow: "hidden",
            background: featured.featured_artist.header
              ? "linear-gradient(180deg, rgba(15,10,31,0.4), rgba(15,10,31,0.92)), url(" + featured.featured_artist.header + ")"
              : "linear-gradient(135deg, #0a1a3a 0%, #0f0a1f 60%, #1a0a2e 100%)",
            backgroundSize: "cover", backgroundPosition: "center",
            border: "1.5px solid rgba(59,130,246,0.5)", borderRadius: 16,
            padding: "16px", marginBottom: 18, cursor: "pointer",
            boxShadow: "0 8px 28px rgba(0,0,0,0.5), 0 0 24px rgba(59,130,246,0.15)",
          }}>
            <div style={{
              color: "#93C5FD", fontWeight: 900, fontSize: 10, letterSpacing: 1.5,
              textTransform: "uppercase", marginBottom: 10,
              display: "flex", alignItems: "center", gap: 6,
              textShadow: "0 0 8px rgba(147,197,253,0.5)",
            }}>
              <AppIcon id="crown" size={14} /> Featured Artist of the Week
            </div>
            <div style={{ display: "flex", alignItems: "center", gap: 14 }}>
              <div style={{
                width: 56, height: 56, borderRadius: "50%", flexShrink: 0,
                background: featured.featured_artist.avatar ? "#000" : "linear-gradient(135deg,#3B82F6,#7C3AED)",
                backgroundImage: featured.featured_artist.avatar ? "url(" + featured.featured_artist.avatar + ")" : "none",
                backgroundSize: "cover", backgroundPosition: "center",
                border: "2px solid rgba(147,197,253,0.6)",
                boxShadow: "0 0 16px rgba(59,130,246,0.4)",
                display: "flex", alignItems: "center", justifyContent: "center",
                color: "#fff", fontWeight: 900, fontSize: 22,
              }}>
                {!featured.featured_artist.avatar && (featured.featured_artist.name || "?").charAt(0).toUpperCase()}
              </div>
              <div style={{ flex: 1, minWidth: 0 }}>
                <div style={{ color: "#fff", fontWeight: 800, fontSize: 16, overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" }}>{featured.featured_artist.name || featured.featured_artist.username}</div>
                <div style={{ color: "#93C5FD", fontSize: 12, marginTop: 2 }}>@{featured.featured_artist.username}</div>
                <div style={{ color: "#9590a8", fontSize: 10, marginTop: 4, display: "flex", gap: 10 }}>
                  <span>{featured.featured_artist.beat_count} beats</span>
                  <span>{featured.featured_artist.follower_count} followers</span>
                </div>
              </div>
              <div style={{
                background: "rgba(59,130,246,0.18)", border: "1px solid rgba(147,197,253,0.5)",
                borderRadius: 10, padding: "8px 14px", flexShrink: 0,
                color: "#DBEAFE", fontWeight: 800, fontSize: 12,
              }}>View</div>
            </div>
            {/* Admin pick control */}
            {isAdmin && (
              <div style={{ marginTop: 12, paddingTop: 12, borderTop: "1px solid rgba(147,197,253,0.2)", display: "flex", alignItems: "center", justifyContent: "space-between", gap: 8 }}>
                <span style={{ color: "#7a6ea0", fontSize: 10 }}>
                  {featured.featured_artist.admin_pick ? "Admin-pinned" : "Auto-rotating weekly"}
                </span>
                {featured.featured_artist.admin_pick && (
                  <button onClick={function(e){
                    e.stopPropagation();
                    apiFetch("/api/producer/featured/set-artist", { method: "POST", body: JSON.stringify({ user_id: "" }) })
                      .then(function(){ return apiFetch("/api/producer/featured"); })
                      .then(function(r){ if (r) setFeatured(r); })
                      .catch(function(){});
                  }} style={{
                    background: "rgba(255,107,107,0.12)", border: "1px solid rgba(255,107,107,0.35)",
                    borderRadius: 7, color: "#ff6b6b", fontSize: 10, fontWeight: 700, padding: "5px 10px", cursor: "pointer",
                  }}>Clear pin → auto-rotate</button>
                )}
              </div>
            )}
          </div>
        )}

        
      </div>

      {tab === "beats" && (
        <MembersBeatsTab user={user} priced={true} onViewProfile={onViewProfile} />
      )}

      {tab === "mp3s" && (
        <MembersBeatsTab user={user} priced={false} onViewProfile={onViewProfile} />
      )}

      {tab === "discover" && (
        <MembersNewsTab user={user} isAdmin={isAdmin} />
      )}

      {tab === "library" && (
        <MembersLibraryTab user={user} savedIds={savedIds} savedLyrics={savedLyrics}
          onPlay={onPlay} onEditLyric={onEditLyric} onGoStudio={onGoStudio}
          onGenreSearch={onGenreSearch} onViewProfile={onViewProfile} />
      )}

      {tab === "kits" && (
        <MembersKitStoreTab user={user} onUpload={function(){ onGoStudio && onGoStudio(); }} />
      )}

      {tab === "stats" && (
        <MembersStatsTab user={user} savedIds={savedIds} savedLyrics={savedLyrics} />
      )}
      </div>
    </div>
  );
}


// =============================================================================
// LYRIC CARD - shows saved lyric with beat, opens full lyric view
// =============================================================================
function LyricCard({ lyric, lyricIndex, onDelete, onEditLyric }) {
  return (
    <div style={{ background: "#111", borderRadius: 14, marginBottom: 12, border: "1px solid #1e1e1e", overflow: "hidden" }}>
      <div
        onClick={() => onEditLyric(lyric, lyricIndex)}
        style={{ padding: "16px", cursor: "pointer", display: "flex", alignItems: "center", gap: 14 }}
      >
        <div style={{
          width: 44, height: 44, borderRadius: 12, flexShrink: 0,
          background: "linear-gradient(135deg,#6B21A8,#C026D3)",
          display: "flex", alignItems: "center", justifyContent: "center",
        }}>
          <AppIcon id="writing" size={20} style={{ color: "white" }} />
        </div>
        <div style={{ flex: 1, overflow: "hidden" }}>
          <div style={{ color: "white", fontWeight: 700, fontSize: 15, marginBottom: 3, whiteSpace: "nowrap", overflow: "hidden", textOverflow: "ellipsis" }}>
            {lyric.title}
          </div>
          {lyric.beatTitle && (
            <div style={{ color: "#C026D3", fontSize: 11, fontWeight: 600, whiteSpace: "nowrap", overflow: "hidden", textOverflow: "ellipsis" }}>
              <AppIcon id="note" size={20}/> {lyric.beatTitle}
            </div>
          )}
          <div style={{ color: "#555", fontSize: 11, marginTop: 3 }}>
            {lyric.updatedAt
              ? "Edited " + new Date(lyric.updatedAt).toLocaleDateString()
              : new Date(lyric.savedAt).toLocaleDateString()}
          </div>
        </div>
        <div style={{ display: "flex", flexDirection: "column", alignItems: "center", gap: 8, flexShrink: 0 }}>
          <span style={{ color: "#444", fontSize: 18 }}>{'>'}</span>
          <button
            onClick={e => { e.stopPropagation(); onDelete(); }}
            style={{ background: "none", border: "none", color: "#555", fontSize: 16, cursor: "pointer", padding: 4 }}>
            ⌫
          </button>
        </div>
      </div>
    </div>
  );
}


// =============================================================================
// PUBLIC PROFILE SCREEN
// =============================================================================


// =============================================================================
// COMMENTS PANEL — standalone so keyboard doesn't collapse on rerender
// =============================================================================
function CommentsBottomSheet({ contentId, itemComments, currentUser, onSubmit, onDelete, onClose }) {
  var [text, setText]       = React.useState("");
  var [replyTo, setReplyTo] = React.useState(null);
  var [submitting, setSubmitting] = React.useState(false);
  var sheetRef = React.useRef(null);
  var startY   = React.useRef(null);
  var dragY    = React.useRef(0);

  // Close on backdrop tap
  function handleBackdrop(e) {
    if (e.target === e.currentTarget) onClose();
  }

  // Drag-to-dismiss
  function onTouchStart(e) { startY.current = e.touches[0].clientY; }
  function onTouchMove(e) {
    var dy = e.touches[0].clientY - startY.current;
    if (dy > 0 && sheetRef.current) {
      dragY.current = dy;
      sheetRef.current.style.transform = "translateY(" + dy + "px)";
    }
  }
  function onTouchEnd() {
    if (dragY.current > 100) { onClose(); }
    else if (sheetRef.current) { sheetRef.current.style.transform = "translateY(0)"; }
    dragY.current = 0;
  }

  function submit() {
    if (!text.trim() || !currentUser || submitting) return;
    setSubmitting(true);
    onSubmit(contentId, text.trim(), replyTo ? replyTo.id : null, function() {
      setText(""); setReplyTo(null); setSubmitting(false);
    }, function() { setSubmitting(false); });
  }

  var allComments = itemComments || [];
  var topLevel    = allComments.filter(function(c) { return !c.parentId; });
  var getReplies  = function(id) { return allComments.filter(function(c) { return c.parentId === id; }); };

  function Avatar({ c, size }) {
    return (
      <div style={{ width: size, height: size, borderRadius: "50%", flexShrink: 0,
        background: "linear-gradient(135deg,#6B21A8,#C026D3)",
        display: "flex", alignItems: "center", justifyContent: "center",
        color: "white", fontWeight: 800, fontSize: size * 0.38, overflow: "hidden" }}>
        {c.avatarUrl
          ? <img src={c.avatarUrl} alt={c.username} style={{ width: "100%", height: "100%", objectFit: "cover" }} />
          : (c.username || "?")[0].toUpperCase()}
      </div>
    );
  }

  return (
    <div onClick={handleBackdrop}
      style={{ position: "fixed", inset: 0, zIndex: 9000, background: "rgba(0,0,0,0.6)",
        display: "flex", flexDirection: "column", justifyContent: "flex-end" }}>
      <div ref={sheetRef}
        onTouchStart={onTouchStart} onTouchMove={onTouchMove} onTouchEnd={onTouchEnd}
        style={{ background: "#111", borderRadius: "20px 20px 0 0", maxHeight: "80vh",
          display: "flex", flexDirection: "column", transition: "transform 0.1s" }}>

        {/* Handle */}
        <div style={{ display: "flex", justifyContent: "center", padding: "12px 0 4px" }}>
          <div style={{ width: 36, height: 4, borderRadius: 2, background: "#333" }} />
        </div>

        {/* Header */}
        <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center",
          padding: "4px 16px 12px", borderBottom: "1px solid #1a1a1a" }}>
          <div style={{ color: "white", fontWeight: 700, fontSize: 16 }}>
            Comments
            {allComments.length > 0 && <span style={{ color: "#555", fontWeight: 400, fontSize: 13, marginLeft: 8 }}>{allComments.length}</span>}
          </div>
          <button onClick={onClose}
            style={{ background: "none", border: "none", color: "#555", fontSize: 22, cursor: "pointer", padding: 4 }}>
            &#10005;
          </button>
        </div>

        {/* Comment list */}
        <div style={{ overflowY: "auto", flex: 1, padding: "12px 16px" }}>
          {topLevel.length === 0 && (
            <div style={{ color: "#444", fontSize: 14, textAlign: "center", padding: "30px 0" }}>
              No comments yet. Be the first!
            </div>
          )}
          {topLevel.map(function(c) {
            var replies = getReplies(c.id);
            return (
              <div key={c.id} style={{ marginBottom: 16 }}>
                <div style={{ display: "flex", gap: 10 }}>
                  <Avatar c={c} size={36} />
                  <div style={{ flex: 1 }}>
                    <div style={{ display: "flex", alignItems: "center", gap: 8, marginBottom: 3 }}>
                      <span style={{ color: "white", fontWeight: 700, fontSize: 14 }}>@{c.username}</span>
                      <span style={{ color: "#444", fontSize: 11 }}>{new Date(c.createdAt).toLocaleDateString()}</span>
                    </div>
                    <div style={{ color: "#ddd", fontSize: 14, lineHeight: 1.5 }}>{c.text}</div>
                    <div style={{ display: "flex", gap: 16, marginTop: 6 }}>
                      {currentUser && (
                        <button onClick={function() { setReplyTo({ id: c.id, username: c.username }); setText("@" + c.username + " "); }}
                          style={{ background: "none", border: "none", color: "#888", fontSize: 12, cursor: "pointer", padding: 0, fontWeight: 600 }}>
                          Reply
                        </button>
                      )}
                      {currentUser && currentUser.username === c.username && (
                        <button onClick={function() { onDelete(contentId, c.id); }}
                          style={{ background: "none", border: "none", color: "#444", fontSize: 12, cursor: "pointer", padding: 0 }}>
                          Delete
                        </button>
                      )}
                    </div>
                  </div>
                </div>
                {replies.length > 0 && (
                  <div style={{ marginLeft: 46, marginTop: 8 }}>
                    {replies.map(function(r) {
                      return (
                        <div key={r.id} style={{ display: "flex", gap: 8, marginBottom: 10 }}>
                          <Avatar c={r} size={28} />
                          <div style={{ flex: 1 }}>
                            <div style={{ display: "flex", gap: 8, alignItems: "center", marginBottom: 2 }}>
                              <span style={{ color: "white", fontWeight: 700, fontSize: 13 }}>@{r.username}</span>
                              <span style={{ color: "#444", fontSize: 11 }}>{new Date(r.createdAt).toLocaleDateString()}</span>
                            </div>
                            <div style={{ color: "#ddd", fontSize: 13, lineHeight: 1.5 }}>{r.text}</div>
                            {currentUser && currentUser.username === r.username && (
                              <button onClick={function() { onDelete(contentId, r.id); }}
                                style={{ background: "none", border: "none", color: "#444", fontSize: 11, cursor: "pointer", padding: 0, marginTop: 3 }}>
                                Delete
                              </button>
                            )}
                          </div>
                        </div>
                      );
                    })}
                  </div>
                )}
              </div>
            );
          })}
        </div>

        {/* Input */}
        <div style={{ padding: "10px 16px 24px", borderTop: "1px solid #1a1a1a" }}>
          {replyTo && (
            <div style={{ color: "#C026D3", fontSize: 12, marginBottom: 8, display: "flex", alignItems: "center", gap: 8 }}>
              Replying to @{replyTo.username}
              <button onClick={function() { setReplyTo(null); setText(""); }}
                style={{ background: "none", border: "none", color: "#444", cursor: "pointer", padding: 0, fontSize: 14 }}>
                &#10005;
              </button>
            </div>
          )}
          {currentUser ? (
            <div style={{ display: "flex", gap: 8, alignItems: "center" }}>
              <input value={text}
                onChange={function(e) { setText(e.target.value); }}
                onKeyDown={function(e) { if (e.key === "Enter" && !e.shiftKey) { e.preventDefault(); submit(); } }}
                placeholder="Leave a comment..."
                style={{ flex: 1, background: "#1a1a1a", border: "1px solid #2a2a2a", borderRadius: 24,
                  padding: "10px 16px", color: "white", fontSize: 14, outline: "none" }} />
              <button onClick={submit} disabled={submitting || !text.trim()}
                style={{ background: "#C026D3", border: "none", borderRadius: 24, color: "white",
                  fontWeight: 700, fontSize: 13, padding: "10px 18px", cursor: "pointer",
                  opacity: submitting ? 0.6 : 1, flexShrink: 0 }}>
                Post
              </button>
            </div>
          ) : (
            <div style={{ color: "#555", fontSize: 13, textAlign: "center" }}>Sign in to comment</div>
          )}
        </div>
      </div>
    </div>
  );
}



// =============================================================================
// POST SHEET — slides up from bottom, lets Pro users post track/video/status
// =============================================================================
function PostSheet({ user, onClose, onPosted }) {
  var [mode, setMode]         = React.useState(null); // null | "status" | "music" | "video"
  var [text, setText]         = React.useState("");
  var [images, setImages]     = React.useState([]);   // {url, file}
  var [spotifyUrl, setSpotifyUrl] = React.useState("");
  var [caption, setCaption]   = React.useState("");
  var [ytLink, setYtLink]     = React.useState("");
  var [loading, setLoading]   = React.useState(false);
  var [msg, setMsg]           = React.useState("");
  // Link preview state — when the user pastes a URL into the status text
  // we auto-fetch its Open Graph metadata and show a preview card. Cleared
  // if they edit the text and the URL disappears.
  var [linkPreview, setLinkPreview]   = React.useState(null);  // { url, title, description, image, siteName } | null
  var [previewLoading, setPreviewLoading] = React.useState(false);
  var lastFetchedUrlRef = React.useRef("");
  var imageFileRef             = React.useRef(null);
  var videoFileRef             = React.useRef(null);
  var sheetRef                 = React.useRef(null);
  var startY                   = React.useRef(null);

  // Match the first http(s) URL inside arbitrary text. Used both to power
  // the live preview and to send link_url to the backend so it doesn't
  // have to re-parse on its side.
  function findFirstUrl(s) {
    if (!s) return "";
    var m = s.match(/https?:\/\/[^\s<>"'`]+/i);
    return m ? m[0].replace(/[.,;!?)]+$/, "") : "";
  }

  // Debounced preview fetch — runs whenever `text` changes. We wait 600ms
  // after the user stops typing to avoid hammering the backend on every
  // keystroke. The current fetched url is tracked so we don't redundantly
  // re-fetch the same URL when the user is just editing surrounding text.
  React.useEffect(function() {
    var url = findFirstUrl(text);
    if (!url) {
      lastFetchedUrlRef.current = "";
      setLinkPreview(null);
      setPreviewLoading(false);
      return;
    }
    if (url === lastFetchedUrlRef.current) return;
    var cancelled = false;
    setPreviewLoading(true);
    var timer = setTimeout(function() {
      lastFetchedUrlRef.current = url;
      apiFetch("/api/posts/link-preview", {
        method: "POST", body: JSON.stringify({ url: url })
      }).then(function(data) {
        if (cancelled) return;
        setPreviewLoading(false);
        // If the URL changed while we were fetching, drop the result
        if (findFirstUrl(text) !== url) return;
        if (data && (data.title || data.description || data.image)) {
          setLinkPreview({
            url:         url,
            title:       data.title || "",
            description: data.description || "",
            image:       data.image || "",
            siteName:    data.siteName || data.site_name || "",
          });
        } else {
          // No useful metadata — still keep a minimal card so the user
          // sees something rather than guessing whether the URL "took".
          setLinkPreview({ url: url, title: url, description: "", image: "", siteName: "" });
        }
      }).catch(function() {
        if (cancelled) return;
        setPreviewLoading(false);
        // Fallback to bare-URL card on error
        setLinkPreview({ url: url, title: url, description: "", image: "", siteName: "" });
      });
    }, 600);
    return function() { cancelled = true; clearTimeout(timer); };
  }, [text]);

  function onTouchStart(e) { startY.current = e.touches[0].clientY; }
  function onTouchMove(e) {
    var dy = e.touches[0].clientY - startY.current;
    if (dy > 0 && sheetRef.current) sheetRef.current.style.transform = "translateY(" + dy + "px)";
  }
  function onTouchEnd(e) {
    var dy = e.touches[0].clientY - (startY.current || 0);
    if (dy > 80) onClose();
    else if (sheetRef.current) sheetRef.current.style.transform = "translateY(0)";
  }

  function addImages(files) {
    var remaining = 3 - images.length;
    var toAdd = Array.from(files).slice(0, remaining);
    toAdd.forEach(function(file) {
      var url = URL.createObjectURL(file);
      setImages(function(prev) { return [...prev, { url: url, file: file }]; });
    });
  }

  function removeImage(idx) {
    setImages(function(prev) { return prev.filter(function(_, i) { return i !== idx; }); });
  }

  async function submitStatus() {
    if (!text.trim() && images.length === 0) return;
    setLoading(true); setMsg("");
    var fd = new FormData();
    fd.append("text", text.trim());
    images.forEach(function(img) { fd.append("images", img.file); });
    // If a URL was detected in the text, send it so the backend can store
    // the parsed OG metadata on the post doc. We send the URL — not the
    // preview blob — so the server controls what gets persisted.
    var detectedUrl = findFirstUrl(text);
    if (detectedUrl) fd.append("link_url", detectedUrl);
    var attempt = 0;
    while (attempt < 2) {
      try {
        var r = await fetch(API_BASE + "/api/posts/status", {
          method: "POST",
          headers: { Authorization: "Bearer " + (getToken() || "") },
          body: fd,
        });
        var data = await r.json().catch(function() { return {}; });
        if (!r.ok) throw new Error(data.detail || ("Error " + r.status));
        if (data.detail) throw new Error(data.detail);
        setLoading(false);
        onPosted && onPosted(data);
        onClose();
        return;
      } catch(err) {
        attempt++;
        if (attempt >= 2) { setMsg(err.message || "Post failed. Please try again."); setLoading(false); return; }
        // brief pause before retry (handles cold-start / transient network blip)
        await new Promise(function(res) { setTimeout(res, 1200); });
      }
    }
  }

  function submitMusic() {
    if (!spotifyUrl.trim()) return;
    setLoading(true); setMsg("");
    apiFetch("/api/posts/music", { method: "POST", body: JSON.stringify({ spotifyUrl: spotifyUrl.trim(), caption: caption.trim() }) })
      .then(function(data) { setLoading(false); onPosted && onPosted(data); onClose(); })
      .catch(function(err) { setMsg(err.message || "Failed to post. Please try again."); setLoading(false); });
  }

  async function submitVideo(file) {
    if (!file) return;
    setLoading(true); setMsg("");
    var fd = new FormData();
    fd.append("file", file);
    fd.append("caption", caption.trim());
    var attempt = 0;
    while (attempt < 2) {
      try {
        var r = await fetch(API_BASE + "/api/posts/video", {
          method: "POST",
          headers: { Authorization: "Bearer " + (getToken() || "") },
          body: fd,
        });
        var data = await r.json().catch(function() { return {}; });
        if (!r.ok) throw new Error(data.detail || ("Error " + r.status));
        if (data.detail) throw new Error(data.detail);
        setLoading(false);
        onPosted && onPosted(data);
        onClose();
        return;
      } catch(err) {
        attempt++;
        if (attempt >= 2) { setMsg(err.message || "Upload failed. Please try again."); setLoading(false); return; }
        await new Promise(function(res) { setTimeout(res, 1200); });
      }
    }
  }

  async function submitVideoLink() {
    var url = ytLink.trim();
    if (!url || loading) return;
    setLoading(true); setMsg("");
    try {
      var data = await apiFetch("/api/posts/video-link", { method: "POST", body: JSON.stringify({ url: url, caption: caption.trim() }) });
      if (data && data.detail) throw new Error(data.detail);
      setLoading(false);
      onPosted && onPosted(data);
      onClose();
    } catch(err) {
      setMsg(err.message || "Could not post that link. Please try again.");
      setLoading(false);
    }
  }

  return (
    <div onClick={function(e) { if (e.target === e.currentTarget) onClose(); }}
      style={{ position: "fixed", inset: 0, zIndex: 10000, background: "rgba(0,0,0,0.7)",
        display: "flex", flexDirection: "column", justifyContent: "flex-end" }}>
      <div ref={sheetRef}
        style={{
          background: "linear-gradient(180deg,#0f0a1f 0%,#0a0a14 50%,#080812 100%)",
          borderRadius: "24px 24px 0 0",
          maxHeight: "85vh", minHeight: "50vh",
          display: "flex", flexDirection: "column",
          transition: "transform 0.15s",
          paddingBottom: "env(safe-area-inset-bottom)",
          boxShadow: "0 -8px 40px rgba(124,58,237,0.25), 0 -2px 16px rgba(0,0,0,0.6)",
          border: "1px solid rgba(124,58,237,0.2)",
          borderBottom: "none",
          position: "relative", overflow: "hidden",
        }}>
        {/* Subtle top accent line */}
        <div style={{
          position: "absolute", top: 0, left: 0, right: 0, height: 1,
          background: "linear-gradient(90deg,transparent,#C026D3,#7C3AED,#3B82F6,transparent)",
          opacity: 0.6,
        }} />

        {/* Handle — drag to dismiss */}
        <div onTouchStart={onTouchStart} onTouchMove={onTouchMove} onTouchEnd={onTouchEnd}
          style={{ display: "flex", justifyContent: "center", padding: "12px 0 4px", cursor: "grab" }}>
          <div style={{
            width: 40, height: 4, borderRadius: 2,
            background: "linear-gradient(90deg,#7C3AED,#C026D3)",
            opacity: 0.7,
          }} />
        </div>

        {/* Header */}
        <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center",
          padding: "4px 18px 14px", borderBottom: "1px solid rgba(124,58,237,0.15)" }}>
          {mode ? (
            <button onClick={function() { setMode(null); setMsg(""); }}
              style={{ background: "none", border: "none", color: "#A78BFA", fontSize: 14, fontWeight: 700, cursor: "pointer", padding: 0 }}>
              &#8592; Back
            </button>
          ) : (
            <div style={{
              fontWeight: 900, fontSize: 18, letterSpacing: 0.5,
              fontFamily: "'Bebas Neue',sans-serif",
              background: "linear-gradient(135deg,#C026D3,#7C3AED,#3B82F6)",
              WebkitBackgroundClip: "text", WebkitTextFillColor: "transparent",
              backgroundClip: "text",
            }}>CREATE POST</div>
          )}
          <button onClick={onClose}
            style={{
              background: "rgba(124,58,237,0.1)", border: "1px solid rgba(124,58,237,0.25)",
              borderRadius: "50%", width: 30, height: 30,
              color: "#A78BFA", fontSize: 16, cursor: "pointer", padding: 0,
              display: "flex", alignItems: "center", justifyContent: "center",
            }}>
            &#10005;
          </button>
        </div>

        <div style={{ overflowY: "auto", flex: 1, WebkitOverflowScrolling: "touch" }}
          onTouchMove={function(e) { e.stopPropagation(); }}>
          {/* Mode picker */}
          {!mode && (
            <div style={{ padding: 18, display: "flex", flexDirection: "column", gap: 12 }}>
              {[
                { id: "status", label: "Post Status", desc: "Share thoughts, news, photos or links",
                  accent: "#C026D3", accent2: "#7C3AED",
                  icon: <svg width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="white" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><path d="M21 15a2 2 0 0 1-2 2H7l-4 4V5a2 2 0 0 1 2-2h14a2 2 0 0 1 2 2z"/></svg> },
                { id: "video", label: "Post Video", desc: "Upload a video clip from your camera roll",
                  accent: "#3B82F6", accent2: "#06B6D4",
                  icon: <svg width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="white" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><rect x="2" y="2" width="20" height="20" rx="3"/><polygon points="10,8 16,12 10,16" fill="white"/></svg> },
              ].map(function(opt) {
                return (
                  <button key={opt.id} onClick={function() { setMode(opt.id); }}
                    className="bf-btn"
                    style={{
                      background: "linear-gradient(135deg,rgba(124,58,237,0.10) 0%,rgba(10,10,20,0.6) 60%,rgba(8,8,18,0.8) 100%)",
                      border: "1px solid rgba(124,58,237,0.28)",
                      borderRadius: 16,
                      padding: "16px 18px",
                      cursor: "pointer", textAlign: "left",
                      display: "flex", alignItems: "center", gap: 14,
                      boxShadow: "0 4px 14px rgba(0,0,0,0.4), 0 0 0 1px rgba(124,58,237,0.08)",
                      position: "relative", overflow: "hidden",
                    }}>
                    {/* Left accent bar */}
                    <div style={{
                      position: "absolute", left: 0, top: 0, bottom: 0, width: 3,
                      background: "linear-gradient(180deg," + opt.accent + "," + opt.accent2 + ")",
                      boxShadow: "0 0 8px " + opt.accent + "aa",
                    }} />
                    <div style={{
                      width: 48, height: 48, borderRadius: 14,
                      background: "linear-gradient(135deg," + opt.accent + "," + opt.accent2 + ")",
                      display: "flex", alignItems: "center", justifyContent: "center", flexShrink: 0,
                      boxShadow: "0 4px 14px " + opt.accent + "66",
                    }}>
                      {opt.icon}
                    </div>
                    <div style={{ flex: 1, minWidth: 0 }}>
                      <div style={{ color: "white", fontWeight: 800, fontSize: 15, marginBottom: 3, letterSpacing: 0.2 }}>{opt.label}</div>
                      <div style={{ color: "#8b8b9a", fontSize: 12, lineHeight: 1.4 }}>{opt.desc}</div>
                    </div>
                    <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="#A78BFA" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round" style={{ flexShrink: 0, opacity: 0.6 }}>
                      <polyline points="9 18 15 12 9 6"/>
                    </svg>
                  </button>
                );
              })}
            </div>
          )}

          {/* Status composer */}
          {mode === "status" && (
            <div style={{ padding: 16 }}>
              <div style={{ display: "flex", gap: 10, marginBottom: 12 }}>
                <div style={{ width: 36, height: 36, borderRadius: "50%", flexShrink: 0,
                  background: "linear-gradient(135deg,#6B21A8,#C026D3)", overflow: "hidden",
                  display: "flex", alignItems: "center", justifyContent: "center",
                  color: "white", fontWeight: 800, fontSize: 14 }}>
                  {user?.avatarUrl
                    ? <img src={user.avatarUrl} style={{ width: "100%", height: "100%", objectFit: "cover" }} />
                    : (user?.username || "?")[0].toUpperCase()}
                </div>
                <textarea value={text} onChange={function(e) { setText(e.target.value); }}
                  placeholder={"What's on your mind, " + (user?.username || "") + "?"}
                  maxLength={500}
                  style={{ flex: 1, background: "none", border: "none", outline: "none",
                    color: "white", fontSize: 15, lineHeight: 1.5, resize: "none",
                    minHeight: 80, fontFamily: "inherit" }} />
              </div>

              {/* Image previews */}
              {images.length > 0 && (
                <div style={{ display: "flex", gap: 8, marginBottom: 12, flexWrap: "wrap" }}>
                  {images.map(function(img, i) {
                    return (
                      <div key={i} style={{ position: "relative", width: 90, height: 90 }}>
                        <img src={img.url} style={{ width: 90, height: 90, borderRadius: 10, objectFit: "cover" }} />
                        <button onClick={function() { removeImage(i); }}
                          style={{ position: "absolute", top: 4, right: 4, background: "rgba(0,0,0,0.7)",
                            border: "none", borderRadius: "50%", width: 22, height: 22, color: "white",
                            cursor: "pointer", fontSize: 12, display: "flex", alignItems: "center", justifyContent: "center" }}>
                          &#10005;
                        </button>
                      </div>
                    );
                  })}
                </div>
              )}

              {/* Link preview card — appears below text when URL detected */}
              {(linkPreview || previewLoading) && (
                <div style={{
                  marginBottom: 12, borderRadius: 14, overflow: "hidden",
                  background: "linear-gradient(165deg,#0f0a1f 0%,#0a0a14 60%,#080812 100%)",
                  border: "1px solid rgba(124,58,237,0.25)",
                  boxShadow: "0 4px 14px rgba(0,0,0,0.4)",
                  position: "relative",
                }}>
                  {previewLoading && !linkPreview ? (
                    <div style={{ padding: "14px 16px", color: "#888", fontSize: 12, display: "flex", alignItems: "center", gap: 8 }}>
                      <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="#A78BFA" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" style={{ animation: "bf-spin 0.8s linear infinite" }}>
                        <path d="M21 12a9 9 0 11-6.219-8.56"/>
                      </svg>
                      Fetching link preview…
                    </div>
                  ) : (
                    <React.Fragment>
                      {linkPreview && linkPreview.image && (
                        <img src={linkPreview.image} alt="" style={{ width: "100%", maxHeight: 180, objectFit: "cover", display: "block", background: "#000" }} />
                      )}
                      <div style={{ padding: "10px 14px" }}>
                        {linkPreview && linkPreview.siteName && (
                          <div style={{ color: "#A78BFA", fontSize: 10, fontWeight: 700, letterSpacing: 0.5, textTransform: "uppercase", marginBottom: 3 }}>
                            {linkPreview.siteName}
                          </div>
                        )}
                        {linkPreview && linkPreview.title && (
                          <div style={{ color: "white", fontWeight: 800, fontSize: 13, lineHeight: 1.3, marginBottom: 3,
                            display: "-webkit-box", WebkitLineClamp: 2, WebkitBoxOrient: "vertical", overflow: "hidden" }}>
                            {linkPreview.title}
                          </div>
                        )}
                        {linkPreview && linkPreview.description && (
                          <div style={{ color: "#8b8b9a", fontSize: 12, lineHeight: 1.4,
                            display: "-webkit-box", WebkitLineClamp: 2, WebkitBoxOrient: "vertical", overflow: "hidden" }}>
                            {linkPreview.description}
                          </div>
                        )}
                      </div>
                      {/* Dismiss preview button — doesn't remove the URL from
                          text, just hides the card. The user can re-trigger
                          by editing the text. */}
                      <button onClick={function() {
                          setLinkPreview(null);
                          lastFetchedUrlRef.current = "__dismissed__";
                        }}
                        style={{ position: "absolute", top: 8, right: 8,
                          background: "rgba(0,0,0,0.65)", border: "none", borderRadius: "50%",
                          width: 22, height: 22, color: "white", cursor: "pointer", fontSize: 11,
                          display: "flex", alignItems: "center", justifyContent: "center" }}>
                        &#10005;
                      </button>
                    </React.Fragment>
                  )}
                </div>
              )}

              <input ref={imageFileRef} type="file" accept="image/*" multiple style={{ display: "none" }}
                onChange={function(e) { addImages(e.target.files); e.target.value = ""; }} />

              <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center",
                borderTop: "1px solid #1a1a1a", paddingTop: 12 }}>
                <div style={{ display: "flex", gap: 12, alignItems: "center" }}>
                  {images.length < 3 && (
                    <button onClick={function() { imageFileRef.current && imageFileRef.current.click(); }}
                      style={{ background: "none", border: "none", cursor: "pointer", padding: 4, color: "#555" }}>
                      <svg width="22" height="22" viewBox="0 0 24 24" fill="none" stroke="#C026D3" strokeWidth="2" strokeLinecap="round">
                        <rect x="3" y="3" width="18" height="18" rx="2"/><circle cx="8.5" cy="8.5" r="1.5"/>
                        <polyline points="21 15 16 10 5 21"/>
                      </svg>
                    </button>
                  )}
                  <span style={{ color: "#333", fontSize: 12 }}>{text.length}/500</span>
                </div>
                <button onClick={submitStatus} disabled={loading || (!text.trim() && images.length === 0)}
                  style={{ background: "#C026D3", border: "none", borderRadius: 20, color: "white",
                    fontWeight: 700, fontSize: 14, padding: "8px 20px", cursor: "pointer",
                    opacity: loading ? 0.6 : 1 }}>
                  {loading ? "Posting..." : "Post"}
                </button>
              </div>
              {msg && <div style={{ color: "#EF4444", fontSize: 13, marginTop: 8 }}>{msg}</div>}
            </div>
          )}

          {/* Music composer */}
          {mode === "music" && (
            <div style={{ padding: 16 }}>
              <div style={{ color: "#888", fontSize: 12, marginBottom: 8 }}>Spotify URL</div>
              <input value={spotifyUrl} onChange={function(e) { setSpotifyUrl(e.target.value); }}
                placeholder="https://open.spotify.com/track/..."
                style={{ width: "100%", background: "#1a1a1a", border: "1px solid #2a2a2a", borderRadius: 10,
                  padding: "12px 14px", color: "white", fontSize: 14, outline: "none",
                  boxSizing: "border-box", marginBottom: 12 }} />
              <div style={{ color: "#888", fontSize: 12, marginBottom: 8 }}>Caption (optional)</div>
              <textarea value={caption} onChange={function(e) { setCaption(e.target.value); }}
                placeholder="Say something about this track..." rows={3}
                style={{ width: "100%", background: "#1a1a1a", border: "1px solid #2a2a2a", borderRadius: 10,
                  padding: "12px 14px", color: "white", fontSize: 14, outline: "none",
                  resize: "none", boxSizing: "border-box", fontFamily: "inherit", marginBottom: 14 }} />
              <button onClick={submitMusic} disabled={loading || !spotifyUrl.trim()}
                style={{ width: "100%", background: "#1DB954", border: "none", borderRadius: 12,
                  color: "white", fontWeight: 800, fontSize: 15, padding: 14, cursor: "pointer",
                  opacity: loading ? 0.6 : 1 }}>
                {loading ? "Posting..." : "Post Track"}
              </button>
              {msg && <div style={{ color: "#EF4444", fontSize: 13, marginTop: 8 }}>{msg}</div>}
            </div>
          )}

          {/* Video composer */}
          {mode === "video" && (
            <div style={{ padding: 16 }}>
              <div style={{ color: "#888", fontSize: 12, marginBottom: 8 }}>Caption (optional)</div>
              <textarea value={caption} onChange={function(e) { setCaption(e.target.value); }}
                placeholder="Say something about this video..." rows={3}
                style={{ width: "100%", background: "#1a1a1a", border: "1px solid #2a2a2a", borderRadius: 10,
                  padding: "12px 14px", color: "white", fontSize: 14, outline: "none",
                  resize: "none", boxSizing: "border-box", fontFamily: "inherit", marginBottom: 14 }} />
              <input ref={videoFileRef} type="file" accept="video/*" style={{ display: "none" }}
                onChange={function(e) { submitVideo(e.target.files && e.target.files[0]); e.target.value = ""; }} />
              <button onClick={function() { videoFileRef.current && videoFileRef.current.click(); }}
                disabled={loading}
                style={{
                  width: "100%",
                  background: loading
                    ? "linear-gradient(135deg, #0a0a0a 0%, #0f0f12 100%)"
                    : "linear-gradient(135deg, #A855F7 0%, #6366F1 60%, #3B82F6 100%)",
                  border: loading ? "1px solid rgba(168,85,247,0.4)" : "none",
                  borderRadius: 12,
                  color: "white", fontWeight: 800, fontSize: 15,
                  padding: loading ? 12 : 14,
                  cursor: loading ? "not-allowed" : "pointer",
                  boxShadow: loading
                    ? "0 0 20px rgba(168,85,247,0.35)"
                    : "0 4px 18px rgba(168,85,247,0.4)",
                  display: "flex", alignItems: loading ? "stretch" : "center",
                  flexDirection: loading ? "column" : "row",
                  justifyContent: "center", gap: 10,
                  transition: "all 0.2s ease",
                }}>
                {loading ? (
                  <div style={{ width: "100%" }}>
                    <div style={{ display: "flex", alignItems: "center", justifyContent: "center", gap: 8, marginBottom: 8 }}>
                      <svg width="14" height="14" viewBox="0 0 24 24" fill="none"
                        stroke="#A855F7" strokeWidth="2.5" strokeLinecap="round"
                        style={{ animation: "bf-spin 1s linear infinite", filter: "drop-shadow(0 0 4px rgba(168,85,247,0.6))" }}>
                        <path d="M21 12a9 9 0 1 1-6.219-8.56"/>
                      </svg>
                      <span style={{ color: "#A855F7", fontSize: 12, fontWeight: 800, letterSpacing: 1 }}>UPLOADING VIDEO</span>
                    </div>
                    <BFProgressBar />
                  </div>
                ) : (
                  <>
                    <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="white" strokeWidth="2.5" strokeLinecap="round">
                      <path d="M21 15v4a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2v-4"/>
                      <polyline points="17 8 12 3 7 8"/><line x1="12" y1="3" x2="12" y2="15"/>
                    </svg>
                    Choose Video
                  </>
                )}
              </button>

              <div style={{ display: "flex", alignItems: "center", gap: 10, margin: "16px 0 12px" }}>
                <div style={{ flex: 1, height: 1, background: "rgba(255,255,255,0.08)" }} />
                <div style={{ color: "#666", fontSize: 11, fontWeight: 700, letterSpacing: 1 }}>OR PASTE A YOUTUBE LINK</div>
                <div style={{ flex: 1, height: 1, background: "rgba(255,255,255,0.08)" }} />
              </div>
              <input value={ytLink} onChange={function(e){ setYtLink(e.target.value); }}
                placeholder="https://youtube.com/watch?v=..."
                style={{ width: "100%", boxSizing: "border-box", background: "#1a1a1a", border: "1px solid #2a2a2a", borderRadius: 10, padding: "12px 14px", color: "white", outline: "none", marginBottom: 10 }} />
              <button onClick={submitVideoLink} disabled={loading || !ytLink.trim()}
                style={{ width: "100%", padding: "13px", borderRadius: 12, fontWeight: 800, fontSize: 14,
                  cursor: (loading || !ytLink.trim()) ? "not-allowed" : "pointer",
                  background: (loading || !ytLink.trim()) ? "#1a1a1a" : "rgba(192,38,211,0.15)",
                  border: "1px solid " + ((loading || !ytLink.trim()) ? "#2a2a2a" : "rgba(192,38,211,0.6)"),
                  color: (loading || !ytLink.trim()) ? "#555" : "#E9D5FF",
                  display: "flex", alignItems: "center", justifyContent: "center", gap: 8 }}>
                <AppIcon id="video" size={16} /> Post YouTube Video
              </button>
              {msg && <div style={{ color: "#EF4444", fontSize: 13, marginTop: 8 }}>{msg}</div>}
            </div>
          )}
        </div>
      </div>
    </div>
  );
}


// =============================================================================
// PROFILE BEAT CARD — preview + buy from public profiles
// Reuses the same 45-second enforced preview as BeatLeaseCard
// =============================================================================
function ProfileBeatCard({ beat, currentUser, onViewProfile }) {
  var [previewing,  setPreviewing]  = React.useState(false);
  var [previewTime, setPreviewTime] = React.useState(0);
  var [buyLoading,  setBuyLoading]  = React.useState(false);
  var [buyErr,      setBuyErr]      = React.useState("");
  var [deleted,     setDeleted]     = React.useState(false);
  var [sheetOpen,   setSheetOpen]   = React.useState(false); // opens contract preview before Stripe
  React.useEffect(function() {
    function onReopen(e) {
      if (!e || !e.detail || e.detail.beat_id !== beat.id) return;
      if (window.__bf_reopen_consumed__ === beat.id) return;
      window.__bf_reopen_consumed__ = beat.id;
      setSheetOpen(true);
    }
    window.addEventListener("bf-reopen-sheet", onReopen);
    return function() { window.removeEventListener("bf-reopen-sheet", onReopen); };
  }, [beat.id]);
  var playCount = usePlayCount(beat.id, beat.playCount);
  var audioRef  = React.useRef(null);
  var timerRef  = React.useRef(null);
  var seekedRef = React.useRef(false);  // tracks whether we've seeked to preview_start yet
  var isFree    = !beat.price || beat.price === "free" || beat.price === "£0" || beat.price === "0" || beat.price === "£0.00" || beat.price === "0.00";
  // LED theme: free = magenta-violet, paid = electric purple-to-blue
  var accentClr  = isFree ? "#C026D3" : "#7C3AED";
  var accentClr2 = isFree ? "#7C3AED" : "#3B82F6";
  // Check the global lease cache for ownership of THIS beat.
  var [ownedLease, setOwnedLease] = React.useState(function() {
    try {
      if (isFree || !currentUser || !Array.isArray(window.__bf_leases__)) return null;
      var match = window.__bf_leases__.find(function(l){ return (l.beat_id === beat.id || l.id === beat.id) && !l.voided; });
      return match || null;
    } catch(e) { return null; }
  });
  React.useEffect(function() {
    if (isFree || !currentUser) { setOwnedLease(null); return; }
    function check() {
      try {
        if (!Array.isArray(window.__bf_leases__)) return;
        var match = window.__bf_leases__.find(function(l){ return (l.beat_id === beat.id || l.id === beat.id) && !l.voided; });
        setOwnedLease(match || null);
      } catch(e) {}
    }
    check();
    window.addEventListener("bf-leases-updated", check);
    return function() { window.removeEventListener("bf-leases-updated", check); };
  }, [isFree, beat.id, currentUser]);
  var previewId = React.useRef("profile_" + beat.id);

  function startPreview() {
    if (previewing) { stopPreview(); return; }
    seekedRef.current = false;
    startGlobalPreview(previewId.current);
    setPreviewing(true); setPreviewTime(0);
    recordPlay(beat.id);
  }
  function stopPreview() {
    clearGlobalPreview(previewId.current);
    setPreviewing(false); setPreviewTime(0); clearInterval(timerRef.current);
    cancelPlayTimer(beat.id);
    if (audioRef.current) { audioRef.current.pause(); audioRef.current.currentTime = 0; }
  }
  useGlobalPreviewStop(previewId.current, React.useCallback(function() {
    setPreviewing(false); setPreviewTime(0); clearInterval(timerRef.current);
    cancelPlayTimer(beat.id);
    if (audioRef.current) { audioRef.current.pause(); audioRef.current.currentTime = 0; }
  }, [beat.id]));
  function onAudioPlay() {
    var ps = beat.preview_start || 0;
    if (ps > 0 && !seekedRef.current) {
      if (audioRef.current) { try { audioRef.current.currentTime = ps; } catch(e) {} }
      setTimeout(function() {
        if (audioRef.current && !seekedRef.current) {
          try { audioRef.current.currentTime = ps; seekedRef.current = true; } catch(e) {}
        }
      }, 0);
      setTimeout(function() {
        if (audioRef.current && !seekedRef.current) {
          try { audioRef.current.currentTime = ps; seekedRef.current = true; } catch(e) {}
        }
      }, 100);
    }
    clearInterval(timerRef.current);
    var start = Date.now() - (previewTime * 1000);
    timerRef.current = setInterval(function() {
      var elapsed = (Date.now() - start) / 1000;
      setPreviewTime(Math.min(elapsed, 45));
      if (elapsed >= 45) { clearInterval(timerRef.current); stopPreview(); }
    }, 100);
  }
  function onTimeUpdate(e) {
    var el = e.target;
    var startT = parseFloat(el.dataset.startTime || 0);
    // Hard enforce: if before start point, jump to it (handles cold-load iOS)
    if (startT > 0 && el.currentTime < startT && !seekedRef.current) {
      el.currentTime = startT;
      seekedRef.current = true;
    }
    // Hard enforce: stop at 45s from start
    if (el.currentTime >= startT + 45) { el.pause(); stopPreview(); }
  }
  // Contract-first flow: tapping Buy opens the preview sheet (CompactBeatActionSheet).
  // Stripe is only invoked when the user picks a tier inside the sheet.
  function handleBuy(tier) {
    if (tier === "premium") {
      if (!currentUser || (!currentUser.isArtistPro && !currentUser.isPro)) {
        requireUpgrade("buy");
        return;
      }
    } else {
      if (!currentUser) {
        window.dispatchEvent(new CustomEvent("bf:quickSignup", { detail: {
          intent: "buyBasic", beat: beat,
          resume: function() { handleBuy(tier); },
        }}));
        return;
      }
    }
    if (beat.premium_sold) {
      setBuyErr("This beat has been bought exclusively and is no longer available.");
      return;
    }
    setBuyErr("");
    setSheetOpen(true);
  }
  React.useEffect(function() { return function() { clearInterval(timerRef.current); }; }, []);

  useContentDeletedListener(React.useCallback(function(detail) {
    if (detail.kind === "beat" && detail.id === beat.id) setDeleted(true);
  }, [beat.id]));
  if (deleted) return null;

  // Format preview progress as mm:ss
  var totalSecs = previewing ? Math.floor(previewTime) : 0;
  var progMins = String(Math.floor(totalSecs / 60)).padStart(2, "0");
  var progSecs = String(totalSecs % 60).padStart(2, "0");

  var isProd   = beat.producer_username || beat.producer;
  var prodPlan = beat.producer_plan || null; // passed through if available

  return (
    <>
    <div className="bf-card" style={{
      background: "linear-gradient(165deg,#0f0a1f 0%,#0a0a14 60%,#080812 100%)",
      borderRadius: 22, marginBottom: 16, overflow: "hidden",
      border: "1px solid " + accentClr + "55",
      boxShadow:
        "0 8px 40px rgba(0,0,0,0.7)," +
        "0 0 0 0.5px " + accentClr + "33," +
        "inset 0 1px 0 rgba(255,255,255,0.04)," +
        "0 0 24px " + accentClr + "1a",
      position: "relative",
    }}>
      {/* ── LED top edge — glowing gradient bar ── */}
      <div style={{
        height: 2,
        background: "linear-gradient(90deg,transparent 0%," + accentClr + " 20%," + accentClr2 + " 50%," + accentClr + " 80%,transparent 100%)",
        boxShadow: "0 0 12px " + accentClr + "cc, 0 0 24px " + accentClr2 + "66",
      }} />

      <div style={{ padding: "18px 18px 0" }}>
        {/* ── Header row: thumbnail + info ── */}
        <div style={{ display: "flex", gap: 14, marginBottom: 14 }}>
          {/* Thumbnail */}
          <div style={{
            width: 82, height: 82, borderRadius: 14, flexShrink: 0, overflow: "hidden",
            background: "#0a0a0a",
            border: "1px solid " + accentClr + "55",
            boxShadow: "0 0 16px " + accentClr + "22, inset 0 0 0 1px rgba(255,255,255,0.03)",
            display: "flex", alignItems: "center", justifyContent: "center",
            position: "relative",
          }}>
            <img
              src="https://i.ibb.co/v4wcZVJW/IMG-9119.jpg"
              alt="BeatFinder"
              style={{ width: "100%", height: "100%", objectFit: "cover", display: "block" }}
            />
          </div>

          {/* Title + badges */}
          <div style={{ flex: 1, minWidth: 0 }}>
            {beat.genre && (
              <div style={{ color: accentClr, fontSize: 9, fontWeight: 800, letterSpacing: 1.5, marginBottom: 4, textTransform: "uppercase" }}>
                {beat.genre}
              </div>
            )}
            <div style={{ color: "white", fontWeight: 800, fontSize: 15, lineHeight: 1.3, marginBottom: 6,
              overflow: "hidden", display: "-webkit-box", WebkitLineClamp: 2, WebkitBoxOrient: "vertical" }}>
              {beat.title}
            </div>
            <div style={{ color: "#666", fontSize: 11, marginBottom: 8 }}>Instrumental</div>
            {/* Badges row */}
            <div style={{ display: "flex", gap: 6, flexWrap: "wrap", alignItems: "center" }}>
              {isFree ? (
                <span style={{
                  display: "inline-flex", alignItems: "center", gap: 5,
                  background: "rgba(192,38,211,0.12)", border: "1.5px solid #C026D3",
                  borderRadius: 20, padding: "3px 10px", color: "#C026D3", fontSize: 10, fontWeight: 800,
                }}>
                  <svg width="9" height="9" viewBox="0 0 24 24" fill="none" stroke="#C026D3" strokeWidth="2.5" strokeLinecap="round"><path d="M12 3v13M6 11l6 6 6-6"/></svg>
                  FREE DOWNLOAD
                </span>
              ) : (
                <span style={{
                  background: "rgba(245,158,11,0.12)", border: "1.5px solid #F59E0B",
                  borderRadius: 20, padding: "3px 10px", color: "#F59E0B", fontSize: 10, fontWeight: 800,
                }}>
                  {beat.price}
                </span>
              )}
              {playCount > 0 && (
                <span style={{
                  display: "inline-flex", alignItems: "center", gap: 4,
                  background: "rgba(255,255,255,0.05)", border: "1px solid #2a2a2a",
                  borderRadius: 20, padding: "3px 9px", color: "#666", fontSize: 10, fontWeight: 700,
                }}>
                  <svg width="9" height="9" viewBox="0 0 24 24" fill="#666"><path d="M5 3.5l15 8.5-15 8.5V3.5z"/></svg>
                  {fmtCount(playCount)}
                </span>
              )}
            </div>
          </div>
        </div>

        {/* ── Producer row ── */}
        <div style={{
          display: "flex", alignItems: "center", gap: 10,
          padding: "10px 0", borderTop: "1px solid rgba(255,255,255,0.05)", borderBottom: "1px solid rgba(255,255,255,0.05)",
          marginBottom: 14,
        }}>
          {/* Avatar */}
          <div style={{
            width: 34, height: 34, borderRadius: "50%", flexShrink: 0,
            background: "linear-gradient(135deg," + accentClr + "55,#0a0a0a)",
            border: "1.5px solid " + accentClr + "55",
            display: "flex", alignItems: "center", justifyContent: "center",
            overflow: "hidden",
          }}>
            {beat.producer_avatar
              ? <img src={beat.producer_avatar} alt={beat.producer} style={{ width: "100%", height: "100%", objectFit: "cover", display: "block" }} />
              : <span style={{ color: accentClr, fontWeight: 800, fontSize: 13 }}>{(beat.producer || "?")[0].toUpperCase()}</span>
            }
          </div>
          <div style={{ flex: 1, minWidth: 0 }}>
            <div style={{ color: "#555", fontSize: 9, fontWeight: 700, letterSpacing: 1 }}>PRODUCER</div>
            <div style={{ display: "flex", alignItems: "center", gap: 5 }}>
              <span
                onClick={function(e) { e.stopPropagation(); if (onViewProfile && isProd) onViewProfile(beat.producer_username || beat.producer); }}
                style={{ color: "white", fontWeight: 700, fontSize: 13, cursor: isProd && onViewProfile ? "pointer" : "default" }}>
                {beat.producer || "Unknown"}
              </span>
              <VerifiedBadge size={13} />
            </div>
          </div>
          {/* Download count */}
          {beat.downloads > 0 && (
            <div style={{ display: "flex", alignItems: "center", gap: 4, color: "#555", fontSize: 11, flexShrink: 0 }}>
              <svg width="11" height="11" viewBox="0 0 24 24" fill="none" stroke="#555" strokeWidth="2.5" strokeLinecap="round"><path d="M12 3v13M6 11l6 6 6-6"/><path d="M4 20h16"/></svg>
              {beat.downloads}
            </div>
          )}
        </div>

        {/* BPM + Key + Description */}
        {(beat.bpm > 0 || beat.key || beat.description) && (
          <div style={{ marginBottom: 12 }}>
            {(beat.bpm > 0 || beat.key) && (
              <div style={{ display: "flex", gap: 6, marginBottom: beat.description ? 8 : 0 }}>
                {beat.bpm > 0 && (
                  <span style={{ background: "rgba(6,182,212,0.1)", border: "1px solid rgba(6,182,212,0.25)", borderRadius: 20, padding: "3px 10px", fontSize: 10, color: "#06B6D4", fontWeight: 700 }}>
                    {beat.bpm} BPM
                  </span>
                )}
                {beat.key && (
                  <span style={{ background: "rgba(34,197,94,0.1)", border: "1px solid rgba(34,197,94,0.25)", borderRadius: 20, padding: "3px 10px", fontSize: 10, color: "#22C55E", fontWeight: 700 }}>
                    {beat.key}
                  </span>
                )}
              </div>
            )}
            {beat.description && (
              <div style={{ color: "#555", fontSize: 12, lineHeight: 1.5 }}>{beat.description}</div>
            )}
          </div>
        )}

        {/* ── Preview player ── */}
        {beat.url && (
          <div style={{ marginBottom: 14 }}>
            {/* Waveform-style player bar */}
            <div style={{
              display: "flex", alignItems: "center", gap: 12,
              background: "rgba(255,255,255,0.03)", borderRadius: 12,
              padding: "10px 14px", border: "1px solid #1e1e1e",
            }}>
              <button onClick={startPreview} style={{
                width: 36, height: 36, borderRadius: "50%", flexShrink: 0,
                background: previewing ? "rgba(99,91,255,0.2)" : "rgba(255,255,255,0.06)",
                border: "1.5px solid " + (previewing ? "#635BFF" : "#2a2a2a"),
                color: previewing ? "#635BFF" : "#aaa",
                display: "flex", alignItems: "center", justifyContent: "center",
                cursor: "pointer",
              }}>
                {previewing
                  ? <svg width="12" height="12" viewBox="0 0 24 24" fill="currentColor"><rect x="6" y="4" width="4" height="16"/><rect x="14" y="4" width="4" height="16"/></svg>
                  : <svg width="12" height="12" viewBox="0 0 24 24" fill="currentColor"><path d="M5 3.5l15 8.5-15 8.5V3.5z"/></svg>
                }
              </button>
              {/* Fake waveform bars */}
              <div style={{ flex: 1, display: "flex", alignItems: "center", gap: 1.5, height: 28, overflow: "hidden" }}>
                {Array.from({ length: 40 }, function(_, i) {
                  var h = 20 + Math.sin(i * 0.8) * 12 + Math.sin(i * 1.7) * 6;
                  var filled = previewing && (i / 40) < (previewTime / 45);
                  return (
                    <div key={i} style={{
                      flex: 1, borderRadius: 2,
                      height: Math.max(4, h) + "%",
                      background: filled ? accentClr : "rgba(255,255,255,0.1)",
                      transition: filled ? "none" : "background 0.1s",
                    }} />
                  );
                })}
              </div>
              <span style={{ color: "#555", fontSize: 10, fontWeight: 700, flexShrink: 0, fontVariantNumeric: "tabular-nums" }}>
                {previewing ? progMins + ":" + progSecs + " / 0:45" : "0:45"}
              </span>
            </div>
            {previewing && beat.url && (
              <audio ref={audioRef} src={beat.url + ((beat.preview_start||0) > 0 ? "#t=" + (beat.preview_start||0) : "")} autoPlay data-start-time={String(beat.preview_start || 0)}
                onPlay={function(){ onAudioPlay(); if (audioRef.current && (beat.preview_start||0) > 0 && !seekedRef.current) { audioRef.current.currentTime = beat.preview_start; seekedRef.current = true; } }}
                onPause={function(){ clearInterval(timerRef.current); }}
                onTimeUpdate={onTimeUpdate} onEnded={stopPreview} />
            )}
          </div>
        )}

        {buyErr && <div style={{ color: "#F87171", fontSize: 12, marginBottom: 10 }}>{buyErr}</div>}
      </div>

      {/* ── CTA button ── */}
      {isFree ? (
        <div style={{ padding: "0 16px 16px" }}>
          {currentUser
            ? <FreeBeatCTA beat={beat} user={currentUser} />
            : <button onClick={function(){
                window.dispatchEvent(new CustomEvent("bf:quickSignup", { detail: {
                  intent: "download", beat: beat,
                  // No resume — the next render with currentUser present
                  // will swap this button for the FreeBeatCTA which has
                  // its own download flow. They tap once more to start.
                }}));
              }} style={{ width: "100%", borderRadius: 14, padding: "15px", background: "transparent", border: "2px solid #C026D3", color: "#C026D3", fontWeight: 800, fontSize: 15, cursor: "pointer", letterSpacing: 0.5, textTransform: "uppercase", display: "flex", alignItems: "center", justifyContent: "center", gap: 10 }}>
                <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="#C026D3" strokeWidth="2.5" strokeLinecap="round"><path d="M14 2H6a2 2 0 0 0-2 2v16a2 2 0 0 0 2 2h12a2 2 0 0 0 2-2V8z"/><polyline points="14 2 14 8 20 8"/></svg>
                Get Free Beat
              </button>
          }
        </div>
      ) : ownedLease ? (
        /* OWNED state — user already purchased a lease for this beat */
        <div style={{ padding: "0 16px 16px" }}>
          <button
            onClick={function(){ setSheetOpen(true); }}
            style={{
              width: "100%", borderRadius: 14, padding: "14px",
              background: ownedLease.tier === "premium"
                ? "linear-gradient(135deg,#C026D3 0%,#A855F7 35%,#7C3AED 70%,#3B82F6 100%)"
                : "linear-gradient(135deg,#3B82F6 0%,#6366F1 50%,#7C3AED 100%)",
              border: "1.5px solid " + (ownedLease.tier === "premium" ? "rgba(192,38,211,0.6)" : "rgba(124,58,237,0.6)"),
              color: "white", fontWeight: 900, fontSize: 14,
              cursor: "pointer", letterSpacing: 0.8,
              display: "flex", alignItems: "center", justifyContent: "center", gap: 10,
              boxShadow: ownedLease.tier === "premium"
                ? "0 4px 20px rgba(192,38,211,0.55), 0 0 36px rgba(168,85,247,0.4), inset 0 1px 0 rgba(255,255,255,0.3), inset 0 -2px 4px rgba(0,0,0,0.25)"
                : "0 4px 20px rgba(124,58,237,0.45), 0 0 32px rgba(59,130,246,0.35), inset 0 1px 0 rgba(255,255,255,0.25), inset 0 -2px 4px rgba(0,0,0,0.2)",
              textShadow: "0 1px 2px rgba(0,0,0,0.3)",
              position: "relative", overflow: "hidden",
            }}>
            <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="white" strokeWidth="3" strokeLinecap="round" style={{ filter: "drop-shadow(0 0 4px rgba(255,255,255,0.9))" }}>
              <polyline points="20 6 9 17 4 12"/>
            </svg>
            OWNED — {ownedLease.tier === "premium" ? "PREMIUM EXCLUSIVE" : "BASIC LEASE"}
          </button>
          <div style={{
            marginTop: 8, textAlign: "center",
            color: accentClr, fontSize: 10, fontWeight: 800, letterSpacing: 1,
            textShadow: "0 0 6px " + accentClr + "55",
          }}>
            TAP TO DOWNLOAD MP3
          </div>
        </div>
      ) : (
        <div style={{ padding: "0 16px 16px" }}>
          {currentUser ? (
            (function() {
              var basicPrice   = Number(beat.basic_lease_price)   || 50;
              var premiumPrice = Number(beat.premium_lease_price) || 0;
              var premiumSold  = !!beat.premium_sold;
              var premiumAvailable = (premiumPrice >= 100 && premiumPrice <= 500 && !premiumSold);
              // BUSINESS RULE: Premium sale retires the beat from sale entirely.
              // Both Basic and Premium buttons become disabled.
              var basicLocked = premiumSold;
              return (
                <>
                  {/* Basic Lease — LED blue-cyan gradient, disabled once Premium has been sold */}
                  <button onClick={function() { if (!basicLocked && !buyLoading) handleBuy("basic"); }}
                    disabled={buyLoading || basicLocked} style={{
                    width: "100%", borderRadius: 14, padding: "14px",
                    background: (buyLoading || basicLocked)
                      ? "rgba(255,255,255,0.02)"
                      : "linear-gradient(135deg,#3B82F6 0%,#6366F1 50%,#7C3AED 100%)",
                    border: "1.5px solid " + ((buyLoading || basicLocked) ? "#222" : "rgba(124,58,237,0.6)"),
                    color: (buyLoading || basicLocked) ? "#444" : "white",
                    fontWeight: 900, fontSize: 14,
                    cursor: (buyLoading || basicLocked) ? "not-allowed" : "pointer",
                    letterSpacing: 0.8,
                    display: "flex", alignItems: "center", justifyContent: "center", gap: 10,
                    boxShadow: (buyLoading || basicLocked)
                      ? "none"
                      : "0 4px 20px rgba(124,58,237,0.45), 0 0 32px rgba(59,130,246,0.35), inset 0 1px 0 rgba(255,255,255,0.25), inset 0 -2px 4px rgba(0,0,0,0.2)",
                    textShadow: (buyLoading || basicLocked) ? "none" : "0 1px 2px rgba(0,0,0,0.3)",
                    marginBottom: premiumPrice > 0 ? 10 : 0,
                    opacity: basicLocked ? 0.5 : 1,
                    position: "relative", overflow: "hidden",
                  }}>
                    {!buyLoading && !basicLocked && (
                      <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="white" strokeWidth="2.5" strokeLinecap="round" style={{ filter: "drop-shadow(0 0 4px rgba(255,255,255,0.8))" }}>
                        <path d="M5 7a2 2 0 0 1 2-2h10a2 2 0 0 1 2 2v12l-7-3-7 3V7z"/>
                      </svg>
                    )}
                    {buyLoading ? "Loading..." : (basicLocked ? "UNAVAILABLE — Sold Exclusive" : "BASIC LEASE — £" + basicPrice)}
                  </button>
                  {/* Premium Exclusive — magenta-purple-blue LED gradient (premium CTA) */}
                  {premiumPrice > 0 && (
                    <button
                      onClick={function() { if (premiumAvailable && !buyLoading) handleBuy("premium"); }}
                      disabled={buyLoading || !premiumAvailable}
                      style={{
                        width: "100%", borderRadius: 14, padding: "14px",
                        background: (buyLoading || !premiumAvailable)
                          ? "rgba(255,255,255,0.02)"
                          : "linear-gradient(135deg,#C026D3 0%,#A855F7 35%,#7C3AED 70%,#3B82F6 100%)",
                        border: "1.5px solid " + ((buyLoading || !premiumAvailable) ? "#222" : "rgba(192,38,211,0.6)"),
                        color: (buyLoading || !premiumAvailable) ? "#444" : "white",
                        fontWeight: 900, fontSize: 14,
                        cursor: (buyLoading || !premiumAvailable) ? "not-allowed" : "pointer",
                        letterSpacing: 0.8,
                        display: "flex", alignItems: "center", justifyContent: "center", gap: 10,
                        boxShadow: (buyLoading || !premiumAvailable)
                          ? "none"
                          : "0 4px 20px rgba(192,38,211,0.55), 0 0 36px rgba(168,85,247,0.4), inset 0 1px 0 rgba(255,255,255,0.3), inset 0 -2px 4px rgba(0,0,0,0.25)",
                        textShadow: (buyLoading || !premiumAvailable) ? "none" : "0 1px 2px rgba(0,0,0,0.3)",
                        opacity: premiumSold ? 0.5 : 1,
                        position: "relative", overflow: "hidden",
                      }}>
                      {!buyLoading && premiumAvailable && (
                        <svg width="14" height="14" viewBox="0 0 24 24" fill="white" style={{ filter: "drop-shadow(0 0 4px rgba(255,255,255,0.9))" }}>
                          <path d="M12 2l2.4 6.5L21 9l-5 4.5 1.5 7L12 17l-5.5 3.5L8 13.5 3 9l6.6-0.5L12 2z"/>
                        </svg>
                      )}
                      {premiumSold ? "PREMIUM — SOLD OUT" : ("PREMIUM EXCLUSIVE — £" + premiumPrice)}
                    </button>
                  )}
                </>
              );
            })()
          ) : (
            <button onClick={function(){ alert("Sign up and purchase a plan to buy beat leases!"); }} style={{
              width: "100%", borderRadius: 14, padding: "15px",
              background: "transparent", border: "2px solid #444",
              color: "#555", fontWeight: 800, fontSize: 15, cursor: "pointer", letterSpacing: 0.5,
              display: "flex", alignItems: "center", justifyContent: "center", gap: 10,
            }}>
              <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="#555" strokeWidth="2.5" strokeLinecap="round"><rect x="3" y="11" width="18" height="11" rx="2"/><path d="M7 11V7a5 5 0 0 1 10 0v4"/></svg>
              Sign Up to Buy Lease
            </button>
          )}
          <div style={{ display: "flex", gap: 0, marginTop: 14, borderTop: "1px solid rgba(124,58,237,0.1)", paddingTop: 12 }}>
            {[
              { icon: <svg width="13" height="13" viewBox="0 0 24 24" fill="none" stroke="#7C3AED" strokeWidth="2" strokeLinecap="round"><path d="M3 9l9-7 9 7v11a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2z"/><polyline points="9 22 9 12 15 12 15 22"/></svg>, title: "MP3 LEASE", sub: "Instant delivery." },
              { icon: <svg width="13" height="13" viewBox="0 0 24 24" fill="none" stroke="#7C3AED" strokeWidth="2" strokeLinecap="round"><circle cx="12" cy="12" r="10"/><polyline points="12 6 12 12 16 14"/></svg>, title: "UNLIMITED STREAMS", sub: "No expiry date." },
              { icon: <svg width="13" height="13" viewBox="0 0 24 24" fill="none" stroke="#7C3AED" strokeWidth="2" strokeLinecap="round"><polyline points="20 12 20 22 4 22 4 12"/><rect x="2" y="7" width="20" height="5"/><line x1="12" y1="22" x2="12" y2="7"/><path d="M12 7H7.5a2.5 2.5 0 0 1 0-5C11 2 12 7 12 7z"/><path d="M12 7h4.5a2.5 2.5 0 0 0 0-5C13 2 12 7 12 7z"/></svg>, title: "COMMERCIAL USE", sub: "Monetise your music." },
            ].map(function(item, i) {
              return (
                <div key={i} style={{
                  flex: 1, textAlign: "center", padding: "0 4px",
                  borderRight: i < 2 ? "1px solid rgba(124,58,237,0.1)" : "none",
                }}>
                  <div style={{ display: "flex", justifyContent: "center", marginBottom: 4, filter: "drop-shadow(0 0 4px rgba(124,58,237,0.5))" }}>{item.icon}</div>
                  <div style={{ color: "#888", fontSize: 8, fontWeight: 800, letterSpacing: 0.5 }}>{item.title}</div>
                  <div style={{ color: "#444", fontSize: 8, marginTop: 1 }}>{item.sub}</div>
                </div>
              );
            })}
          </div>
        </div>
      )}
    </div>
    {sheetOpen && (
      <CompactBeatActionSheet beat={beat} user={currentUser} onClose={function(){ setSheetOpen(false); }} />
    )}
    </>
  );
}

// =============================================================================
// IMAGE LIGHTBOX — fullscreen image viewer with swipe to close + multi-image nav
// =============================================================================
function ImageLightbox({ images, startIndex, onClose }) {
  var [idx, setIdx] = React.useState(startIndex || 0);
  var startX = React.useRef(null);
  var startY = React.useRef(null);

  React.useEffect(function() {
    function onKey(e) {
      if (e.key === "Escape") onClose();
      if (e.key === "ArrowRight") setIdx(function(i) { return Math.min(i + 1, images.length - 1); });
      if (e.key === "ArrowLeft")  setIdx(function(i) { return Math.max(i - 1, 0); });
    }
    window.addEventListener("keydown", onKey);
    return function() { window.removeEventListener("keydown", onKey); };
  }, []);

  function onTouchStart(e) { startX.current = e.touches[0].clientX; startY.current = e.touches[0].clientY; }
  function onTouchEnd(e) {
    if (startX.current === null) return;
    var dx = e.changedTouches[0].clientX - startX.current;
    var dy = e.changedTouches[0].clientY - startY.current;
    if (Math.abs(dy) > Math.abs(dx) && dy > 60) { onClose(); return; }
    if (Math.abs(dx) > 50) {
      if (dx < 0) setIdx(function(i) { return Math.min(i + 1, images.length - 1); });
      else         setIdx(function(i) { return Math.max(i - 1, 0); });
    }
    startX.current = null; startY.current = null;
  }

  return (
    <div onTouchStart={onTouchStart} onTouchEnd={onTouchEnd}
      onClick={function(e) { if (e.target === e.currentTarget) onClose(); }}
      style={{ position: "fixed", inset: 0, zIndex: 99999, background: "rgba(0,0,0,0.96)",
        display: "flex", flexDirection: "column", alignItems: "center", justifyContent: "center" }}>
      {/* Close button */}
      <button onClick={onClose}
        style={{ position: "absolute", top: "max(var(--bf-safe-top, env(safe-area-inset-top)),16px)", right: 16,
          background: "rgba(255,255,255,0.12)", border: "none", borderRadius: "50%",
          width: 36, height: 36, color: "white", fontSize: 18, cursor: "pointer",
          display: "flex", alignItems: "center", justifyContent: "center", zIndex: 1 }}>
        ✕
      </button>

      {/* Image */}
      <img src={images[idx]} alt={"Image " + (idx + 1)}
        style={{ maxWidth: "100%", maxHeight: "90vh", objectFit: "contain", borderRadius: 4,
          userSelect: "none", WebkitUserSelect: "none", pointerEvents: "none" }} />

      {/* Prev / Next arrows (multi-image only) */}
      {images.length > 1 && (
        <>
          {idx > 0 && (
            <button onClick={function() { setIdx(function(i) { return i - 1; }); }}
              style={{ position: "absolute", left: 12, top: "50%", transform: "translateY(-50%)",
                background: "rgba(255,255,255,0.15)", border: "none", borderRadius: "50%",
                width: 40, height: 40, color: "white", fontSize: 20, cursor: "pointer",
                display: "flex", alignItems: "center", justifyContent: "center" }}>
              ‹
            </button>
          )}
          {idx < images.length - 1 && (
            <button onClick={function() { setIdx(function(i) { return i + 1; }); }}
              style={{ position: "absolute", right: 12, top: "50%", transform: "translateY(-50%)",
                background: "rgba(255,255,255,0.15)", border: "none", borderRadius: "50%",
                width: 40, height: 40, color: "white", fontSize: 20, cursor: "pointer",
                display: "flex", alignItems: "center", justifyContent: "center" }}>
              ›
            </button>
          )}
          {/* Dot indicators */}
          <div style={{ position: "absolute", bottom: "max(env(safe-area-inset-bottom),20px)",
            display: "flex", gap: 6 }}>
            {images.map(function(_, i) {
              return (
                <div key={i} onClick={function() { setIdx(i); }}
                  style={{ width: i === idx ? 18 : 6, height: 6, borderRadius: 3,
                    background: i === idx ? "white" : "rgba(255,255,255,0.3)",
                    transition: "all 0.2s", cursor: "pointer" }} />
              );
            })}
          </div>
        </>
      )}
    </div>
  );
}

// =============================================================================
// ARTIST TRACK UPLOAD MODAL
// =============================================================================
function TrackUploadModal({ currentUser, onClose, onUploaded }) {
  var [title,       setTitle]       = React.useState("");
  var [description, setDescription] = React.useState("");
  var [producerTag, setProducerTag] = React.useState("");
  var [beatTitle,   setBeatTitle]   = React.useState("");
  var [file,        setFile]        = React.useState(null);
  var [uploading,   setUploading]   = React.useState(false);
  var [error,       setError]       = React.useState("");

  async function handleUpload() {
    if (!file) { setError("Please select an audio file"); return; }
    if (!title.trim()) { setError("Please add a title"); return; }
    setUploading(true); setError("");
    try {
      var formData = new FormData();
      formData.append("file", file);
      formData.append("meta", JSON.stringify({
        title:        title.trim(),
        description:  description.trim(),
        producer_tag: producerTag.replace("@","").trim(),
        beat_title:   beatTitle.trim(),
      }));
      var token = localStorage.getItem("bf_token");
      var res = await fetch(API_BASE + "/api/auth/tracks/upload", {
        method: "POST",
        headers: token ? { "Authorization": "Bearer " + token } : {},
        body: formData,
      });
      if (!res.ok) throw new Error((await res.json()).detail || "Upload failed");
      onUploaded();
    } catch(e) {
      setError(e.message);
    } finally {
      setUploading(false);
    }
  }

  return (
    <div style={{
      position: "fixed", inset: 0, zIndex: 9999,
      background: "rgba(0,0,0,0.85)", display: "flex",
      alignItems: "flex-end", justifyContent: "center",
    }} onClick={onClose}>
      <div style={{
        background: "#111", borderRadius: "20px 20px 0 0",
        padding: 24, width: "100%", maxWidth: 430,
        border: "1px solid #222", paddingBottom: "calc(24px + env(safe-area-inset-bottom))",
      }} onClick={function(e){ e.stopPropagation(); }}>

        <div style={{ display: "flex", alignItems: "center", justifyContent: "space-between", marginBottom: 20 }}>
          <div style={{ color: "white", fontWeight: 800, fontSize: 18 }}>Upload Track</div>
          <button onClick={onClose} style={{ background: "none", border: "none", color: "#555", fontSize: 20, cursor: "pointer" }}>✕</button>
        </div>

        {error && <div style={{ color: "#F87171", fontSize: 13, marginBottom: 12, textAlign: "center" }}>{error}</div>}

        {/* File picker */}
        <div style={{ marginBottom: 12 }}>
          <label style={{
            display: "flex", alignItems: "center", justifyContent: "center", gap: 8,
            background: "rgba(245,158,11,0.1)", border: "1.5px dashed #F59E0B",
            borderRadius: 12, padding: "16px", cursor: "pointer", color: "#F59E0B", fontWeight: 700, fontSize: 14,
          }}>
            <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="#F59E0B" strokeWidth="2.5" strokeLinecap="round"><path d="M12 5v14M5 12l7-7 7 7"/></svg>
            {file ? file.name : "Choose MP3 / WAV file"}
            <input type="file" accept=".mp3,.wav,.m4a,.aac,.ogg" style={{ display: "none" }}
              onChange={function(e){ setFile(e.target.files[0]); if (!title && e.target.files[0]) setTitle(e.target.files[0].name.replace(/\.[^.]+$/,"")); }} />
          </label>
        </div>

        <input value={title} onChange={function(e){ setTitle(e.target.value); }} placeholder="Track title *"
          style={{ width: "100%", background: "#1a1a1a", border: "1px solid #333", borderRadius: 10, padding: "11px 14px", color: "white", fontSize: 14, outline: "none", boxSizing: "border-box", marginBottom: 8 }} />

        {/* Producer tag */}
        <div style={{ position: "relative", marginBottom: 8 }}>
          <span style={{ position: "absolute", left: 14, top: "50%", transform: "translateY(-50%)", color: "#F59E0B", fontWeight: 800, fontSize: 14 }}>@</span>
          <input value={producerTag} onChange={function(e){ setProducerTag(e.target.value.replace("@","")); }}
            placeholder="Tag producer username (optional)"
            style={{ width: "100%", background: "#1a1a1a", border: "1px solid #333", borderRadius: 10, padding: "11px 14px 11px 28px", color: "white", fontSize: 14, outline: "none", boxSizing: "border-box" }} />
        </div>

        <input value={beatTitle} onChange={function(e){ setBeatTitle(e.target.value); }} placeholder="Beat title used (optional)"
          style={{ width: "100%", background: "#1a1a1a", border: "1px solid #333", borderRadius: 10, padding: "11px 14px", color: "white", fontSize: 14, outline: "none", boxSizing: "border-box", marginBottom: 8 }} />

        <textarea value={description} onChange={function(e){ setDescription(e.target.value); }}
          placeholder="Description — tell people about this track..."
          maxLength={500} rows={3}
          style={{ width: "100%", background: "#1a1a1a", border: "1px solid #333", borderRadius: 10, padding: "11px 14px", color: "white", fontSize: 13, outline: "none", boxSizing: "border-box", resize: "none", fontFamily: "inherit", marginBottom: 4 }} />
        <div style={{ color: "#444", fontSize: 10, textAlign: "right", marginBottom: 14 }}>{description.length}/500</div>

        <button onClick={handleUpload} disabled={uploading || !file} style={{
          width: "100%",
          background: uploading
            ? "linear-gradient(135deg, #0a0a0a 0%, #0f0f12 100%)"
            : "linear-gradient(135deg, #A855F7 0%, #6366F1 60%, #3B82F6 100%)",
          border: uploading ? "1px solid rgba(168,85,247,0.4)" : "none",
          borderRadius: 12, color: "white", fontWeight: 800, fontSize: 16,
          padding: uploading ? "12px" : "14px",
          cursor: uploading ? "not-allowed" : "pointer",
          boxShadow: uploading
            ? "0 0 20px rgba(168,85,247,0.35), inset 0 0 0 1px rgba(168,85,247,0.15)"
            : "0 4px 18px rgba(168,85,247,0.4)",
          transition: "all 0.2s ease",
        }}>
          {uploading ? (
            <div>
              <div style={{
                display: "flex", alignItems: "center", justifyContent: "center",
                gap: 8, marginBottom: 8,
              }}>
                <svg width="14" height="14" viewBox="0 0 24 24" fill="none"
                  stroke="#A855F7" strokeWidth="2.5" strokeLinecap="round"
                  style={{ animation: "bf-spin 1s linear infinite", filter: "drop-shadow(0 0 4px rgba(168,85,247,0.6))" }}>
                  <path d="M21 12a9 9 0 1 1-6.219-8.56"/>
                </svg>
                <span style={{ color: "#A855F7", fontSize: 12, fontWeight: 800, letterSpacing: 1 }}>UPLOADING TRACK</span>
              </div>
              <BFProgressBar />
            </div>
          ) : "Upload Track"}
        </button>
      </div>
    </div>
  );
}

// =============================================================================
// ARTIST TRACK CARD — shows on artist profile Tracks tab
// =============================================================================
function ArtistTrackCard({ track, isOwner, onDeleted, onViewProfile }) {
  var [playing,    setPlaying]    = React.useState(false);
  var [currentTime,setCurrentTime]= React.useState(0);
  var [duration,   setDuration]   = React.useState(0);
  var audioRef = React.useRef(null);

  function togglePlay() {
    var a = audioRef.current;
    if (!a) return;
    if (playing) { a.pause(); setPlaying(false); }
    else { a.play(); setPlaying(true); }
  }

  async function handleDelete() {
    if (!window.confirm("Delete this track?")) return;
    try {
      await apiFetch("/api/auth/tracks/" + track.id, { method: "DELETE" });
      onDeleted();
    } catch(e) {}
  }

  var pct = duration > 0 ? (currentTime / duration) * 100 : 0;
  var fmt = function(s) { return Math.floor(s/60) + ":" + String(Math.floor(s%60)).padStart(2,"0"); };

  return (
    <div style={{ background: "#111", borderRadius: 16, border: "1px solid rgba(245,158,11,0.2)", marginBottom: 14, overflow: "hidden" }}>
      <div style={{ height: 2, background: "linear-gradient(90deg,#F59E0B,transparent)" }} />
      <div style={{ padding: "14px 16px" }}>

        {/* Header */}
        <div style={{ display: "flex", alignItems: "flex-start", gap: 12, marginBottom: 12 }}>
          {/* Play button */}
          <button onClick={togglePlay} style={{
            width: 46, height: 46, borderRadius: "50%", flexShrink: 0,
            background: playing ? "rgba(245,158,11,0.2)" : "rgba(255,255,255,0.06)",
            border: "1.5px solid " + (playing ? "#F59E0B" : "#2a2a2a"),
            color: playing ? "#F59E0B" : "#aaa",
            display: "flex", alignItems: "center", justifyContent: "center", cursor: "pointer",
          }}>
            {playing
              ? <svg width="14" height="14" viewBox="0 0 24 24" fill="currentColor"><rect x="6" y="4" width="4" height="16"/><rect x="14" y="4" width="4" height="16"/></svg>
              : <svg width="14" height="14" viewBox="0 0 24 24" fill="currentColor"><path d="M5 3.5l15 8.5-15 8.5V3.5z"/></svg>
            }
          </button>

          <div style={{ flex: 1, minWidth: 0 }}>
            <div style={{ color: "white", fontWeight: 700, fontSize: 15, marginBottom: 2 }}>{track.title}</div>
            {/* Producer tag */}
            {track.producer_tag && (
              <div style={{ display: "flex", alignItems: "center", gap: 5, marginBottom: 2 }}>
                <span style={{ color: "#F59E0B", fontSize: 12, fontWeight: 700 }}>
                  🎹 Beat by{" "}
                  <span
                    onClick={function(){ if (onViewProfile) onViewProfile(track.producer_tag); }}
                    style={{ cursor: "pointer", textDecoration: "underline", textDecorationColor: "#F59E0B44" }}
                  >
                    @{track.producer_tag}
                  </span>
                </span>
              </div>
            )}
            {track.beat_title && <div style={{ color: "#555", fontSize: 11 }}>"{track.beat_title}"</div>}
          </div>

          {isOwner && (
            <button onClick={handleDelete} style={{ background: "rgba(220,38,38,0.1)", border: "1px solid rgba(220,38,38,0.2)", borderRadius: 8, color: "#F87171", fontSize: 11, padding: "4px 10px", cursor: "pointer", fontWeight: 600, flexShrink: 0 }}>
              Delete
            </button>
          )}
        </div>

        {/* Audio player bar */}
        {track.url && (
          <div>
            <audio ref={audioRef} src={track.url}
              onTimeUpdate={function(){ setCurrentTime(audioRef.current?.currentTime||0); }}
              onLoadedMetadata={function(){ setDuration(audioRef.current?.duration||0); }}
              onEnded={function(){ setPlaying(false); setCurrentTime(0); }}
            />
            <div style={{ display: "flex", alignItems: "center", gap: 10 }}>
              <span style={{ color: "#555", fontSize: 10, fontVariantNumeric: "tabular-nums", minWidth: 32 }}>{fmt(currentTime)}</span>
              <div
                style={{ flex: 1, height: 3, background: "#1e1e1e", borderRadius: 2, cursor: "pointer", position: "relative" }}
                onClick={function(e){
                  if (!audioRef.current || !duration) return;
                  var rect = e.currentTarget.getBoundingClientRect();
                  audioRef.current.currentTime = ((e.clientX - rect.left) / rect.width) * duration;
                }}
              >
                <div style={{ width: pct + "%", height: "100%", background: "#F59E0B", borderRadius: 2, transition: "width 0.1s" }} />
              </div>
              <span style={{ color: "#555", fontSize: 10, fontVariantNumeric: "tabular-nums", minWidth: 32, textAlign: "right" }}>{fmt(duration)}</span>
            </div>
          </div>
        )}

        {/* Description */}
        {track.description && (
          <div style={{ color: "#666", fontSize: 12, marginTop: 10, lineHeight: 1.5 }}>{track.description}</div>
        )}

        {/* Plays + date */}
        <div style={{ display: "flex", gap: 12, marginTop: 10 }}>
          <span style={{ color: "#333", fontSize: 11 }}>{new Date(track.uploaded_at).toLocaleDateString()}</span>
        </div>
      </div>
    </div>
  );
}

// =============================================================================
// CONTENT TABS — shown on every public profile (Beats / Tracks / Music / Videos)
// =============================================================================

// ── Reusable bottom-sheet modal: contract gate for compact cards ──────────────
// Used by CompactBeatCard. Persists signed state in localStorage so iOS users
// returning from the file viewer (after saving the HTML contract) still see
// the MP3 download button. Mirrors FreeBeatCTA logic in a sheet UI.
//
// IMPORTANT: For LICENSED beats, "step = done" is ONLY reached after the
// server confirms the user actually owns a lease (via /api/producer/my-leases).
// The local signedKey flag is reserved for the contract-acceptance state AFTER
// the purchase is verified — it never marks a paid purchase. Cancelling the
// Stripe checkout therefore can NEVER unlock the download.
function CompactBeatActionSheet({ beat, user, onClose, compact }) {
  var isFreePrice = function(p) { return !p || p === "free" || p === "£0" || p === "0" || p === "£0.00" || p === "0.00"; };
  var isFree   = isFreePrice(beat.price);
  var accent   = isFree ? "#C026D3" : "#F59E0B";
  var signedKey = (isFree ? "bf_signed_free_" : "bf_signed_lease_") + (beat.id || beat.url || beat.title || "x");

  // For FREE beats: the signed flag is the gate, BUT only honour it for
  // logged-in users — guests must never bypass the gate even if localStorage
  // is dirty from a previous session on this device.
  // For LICENSED beats: ignore any stored flag (stale legacy) and require
  // server confirmation.
  var initialStep = "preview";
  try {
    if (isFree && user && typeof window !== "undefined" && window.localStorage && window.localStorage.getItem(signedKey)) {
      initialStep = "done";
    }
    // Clean up any old/stale licensed signedKey that might unlock the download
    // without actual server-verified payment (legacy flag from earlier flow)
    if (!isFree && typeof window !== "undefined" && window.localStorage) {
      window.localStorage.removeItem(signedKey);
    }
  } catch (e) {}

  var [step, setStep]               = React.useState(initialStep);
  var [showContract, setShowContract] = React.useState(false);
  var [buyLoading, setBuyLoading]     = React.useState(false);
  var [verifyingLease, setVerifyingLease] = React.useState(!isFree);
  var [verifiedLease,  setVerifiedLease]  = React.useState(null); // { id, ... } once confirmed
  var [contractDownloaded, setContractDownloaded] = React.useState(false);
  var sheetPanelRef = React.useRef(null);
  React.useEffect(function() {
    if (sheetPanelRef.current) {
      try { sheetPanelRef.current.scrollTop = 0; } catch(e) {}
    }
    // When server hydration of free-licence agreements completes, re-check
    // localStorage. The sheet may have been opened before the boot-time
    // fetch resolved, so the initial useState read would have missed
    // hydrated entries from a different browser context (PWA vs Safari).
    function onHydrated() {
      if (!isFree) return;
      try {
        if (window.localStorage && window.localStorage.getItem(signedKey)) {
          setStep("done");
        }
      } catch (e) {}
    }
    window.addEventListener("bf-free-licences-hydrated", onHydrated);
    return function() {
      window.removeEventListener("bf-free-licences-hydrated", onHydrated);
    };
  }, [isFree, signedKey]);

  // For LICENSED beats, check if the user owns a lease for this beat. Reads
  // from the global cache (window.__bf_leases__) first — that's populated by
  // the main App on login/Stripe-success and means we don't need a per-sheet
  // network call. Only falls back to fetching /my-leases if the cache hasn't
  // loaded yet (e.g. user opens a sheet seconds after launching the app and
  // before the boot fetch completes).
  React.useEffect(function() {
    if (isFree) return;
    if (!user) { setVerifyingLease(false); return; }
    var cancelled = false;

    function checkAgainstCache(leases) {
      var match = (leases || []).find(function(l) {
        return l.beat_id === beat.id || l.id === beat.id;
      });
      if (match) {
        setVerifiedLease(match);
        setStep("done");
      }
      setVerifyingLease(false);
    }

    // Fast path: cache already loaded — use it instantly, no network call.
    if (typeof window !== "undefined" && Array.isArray(window.__bf_leases__)) {
      setVerifyingLease(true);
      checkAgainstCache(window.__bf_leases__);
    } else {
      // Slow path: cache hasn't loaded yet, fetch ourselves.
      setVerifyingLease(true);
      apiFetch("/api/producer/my-leases").then(function(leases) {
        if (cancelled) return;
        checkAgainstCache(leases);
      }).catch(function() {
        if (!cancelled) setVerifyingLease(false);
      });
    }

    // Re-check when the global cache updates (e.g. user just bought another
    // beat in a different sheet, or returned from Stripe success).
    function onLeasesUpdated() {
      if (cancelled) return;
      if (typeof window !== "undefined" && Array.isArray(window.__bf_leases__)) {
        checkAgainstCache(window.__bf_leases__);
      }
    }
    window.addEventListener("bf-leases-updated", onLeasesUpdated);
    return function() {
      cancelled = true;
      window.removeEventListener("bf-leases-updated", onLeasesUpdated);
    };
  }, [isFree, beat.id, user]);

  // FREE-only: re-check localStorage on visibility change (iOS resume)
  React.useEffect(function() {
    if (!isFree) return;
    // Guests must never see "done" state — clear stale localStorage state
    if (!user) { setStep("idle"); return; }
    try {
      if (window.localStorage && window.localStorage.getItem(signedKey)) {
        setStep("done");
      }
    } catch (e) {}
    function onVis() {
      try {
        if (document.visibilityState === "visible" && user && window.localStorage.getItem(signedKey)) {
          setStep("done");
        }
      } catch (e) {}
    }
    function onHydrated() {
      try {
        if (!user) { setStep("idle"); return; }
        if (window.localStorage && window.localStorage.getItem(signedKey)) {
          setStep("done");
        } else {
          setStep("idle");
        }
      } catch (e) {}
    }
    document.addEventListener("visibilitychange", onVis);
    window.addEventListener("bf-free-licences-hydrated", onHydrated);
    return function() {
      document.removeEventListener("visibilitychange", onVis);
      window.removeEventListener("bf-free-licences-hydrated", onHydrated);
    };
  }, [signedKey, isFree, user]);

  function markSigned() {
    try { window.localStorage.setItem(signedKey, "1"); } catch (e) {}
    setStep("done");
    // Also record on the server so the agreed-licence state persists across
    // PWA, Safari, and other devices. Only fires for free beats; licensed
    // beats use server-confirmed lease.
    if (isFree && user && beat && beat.id) {
      apiFetch("/api/producer/beats/" + beat.id + "/agree-licence", {
        method: "POST",
      })
        .then(function(r) { console.log("[BF agree-licence/sheet] OK", beat.id, r); })
        .catch(function(err) { console.error("[BF agree-licence/sheet] FAILED", beat.id, err && err.message); });
    } else if (isFree) {
      console.warn("[BF agree-licence/sheet] skipped — no beat.id or no user", { hasUser: !!user, hasBeatId: !!(beat && beat.id) });
    }
  }

  var downloadUrl = beat.id
    ? API_BASE + "/api/producer/beats/" + beat.id + "/file"
    : (beat.url ? beat.url.replace("/upload/", "/upload/fl_attachment/") : "#");
  var producer = beat.producer || beat.producer_username || "Producer";
  var artist   = (user && (user.name || user.username)) || "Artist";

  // Stripe buy flow for LICENSED beats — tier is "basic" or "premium"
  async function handleBuy(tier) {
    var safeTier = (tier === "premium") ? "premium" : "basic";
    if (safeTier === "premium") {
      if (!user || (!user.isArtistPro && !user.isPro)) {
        requireUpgrade("buy");
        return;
      }
    } else {
      if (!user) {
        window.dispatchEvent(new CustomEvent("bf:quickSignup", { detail: {
          intent: "buyBasic", beat: beat,
          resume: function() { handleBuy(tier); },
        }}));
        return;
      }
    }
    if (beat.premium_sold) {
      alert("This beat has been bought exclusively and is no longer available.");
      return;
    }
    setBuyLoading(true);
    try {
      // Do NOT mark signed before payment — only the server-confirmed lease
      // (verified via /api/producer/my-leases after Stripe webhook fires)
      // unlocks the download. Cancelling Stripe must leave the gate locked.
      var r = await apiFetch("/api/producer/beats/" + beat.id + "/buy-lease", {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ tier: safeTier }),
      });
      try {
        var ct = sessionStorage.getItem("bf_tab") || "exclusive";
        // Save return context — used if Stripe redirects via cancel_url
        // (when user taps Stripe's "Back" button) OR if user taps the iOS
        // "Done" button (in which case the popup just closes without a
        // redirect — we detect that via the bf_payment_in_flight flag and
        // visibilitychange below).
        sessionStorage.setItem("bf_return_tab", JSON.stringify({
          tab: ct,
          path: window.location.pathname + window.location.search,
          beat_id: beat.id,
          ts: Date.now(),
        }));
        // Mark payment as in-flight so the global visibilitychange handler
        // can detect Done-button dismissal and reopen the contract sheet.
        sessionStorage.setItem("bf_payment_in_flight", beat.id);
      } catch (e) {}
      window.location.href = r.checkout_url;
    } catch (e) {
      setBuyLoading(false);
      alert(e.message || "Could not start checkout");
    }
  }

  return (
    <>
      {showContract && (
        <ContractViewer
          html={isFree
            ? generateFreeLeaseContractHtml(beat, user)
            : generateLeaseContractHtml({
                beat_title: beat.title, producer: producer,
                buyer_name: artist, price: beat.price,
                id: "BF-PREVIEW-" + Date.now(),
              })
          }
          filename={(isFree ? "BeatFinder_FreeLicence_" : "BeatFinder_Lease_") +
            (beat.title || "Beat").replace(/[^\w]/g, "_") + ".html"}
          onClose={function() { setShowContract(false); }}
          pdfDownloader={isFree
            ? function() { return downloadFreeContractPdf(beat, user); }
            : null /* lease PDF only issued after payment — preview is read-only */
          }
        />
      )}

      <div onClick={onClose} style={{
        position: "fixed",
        top: 0, left: 0, right: 0,
        bottom: compact ? "calc(72px + env(safe-area-inset-bottom))" : 0,
        zIndex: 99998,
        background: "rgba(0,0,0,0.75)",
        display: "flex", alignItems: "flex-end", justifyContent: "center",
      }}>
        <div ref={sheetPanelRef} onClick={function(e) { e.stopPropagation(); }} style={{
          width: "100%", maxWidth: 480,
          background: "#0d0d0d", borderTop: "1px solid #222",
          borderRadius: "20px 20px 0 0", padding: 18,
          paddingBottom: compact ? 24 : "max(env(safe-area-inset-bottom), 18px)",
          maxHeight: compact ? "calc(100dvh - 72px - env(safe-area-inset-bottom) - 24px)" : "85vh",
          overflowY: "auto", WebkitOverflowScrolling: "touch",
        }}>
          {/* Drag handle */}
          <div style={{ width: 40, height: 4, background: "#333", borderRadius: 2, margin: "0 auto 14px" }} />

          {/* Beat header */}
          <div style={{ display: "flex", gap: 12, alignItems: "center", marginBottom: 16 }}>
            <div style={{ width: 48, height: 48, borderRadius: 10, overflow: "hidden", flexShrink: 0, background: "#111" }}>
              <img src="https://i.ibb.co/v4wcZVJW/IMG-9119.jpg" alt="" style={{ width: "100%", height: "100%", objectFit: "cover" }} />
            </div>
            <div style={{ flex: 1, minWidth: 0 }}>
              <div style={{ color: accent, fontSize: 10, fontWeight: 800, letterSpacing: 0.5, marginBottom: 2 }}>{(beat.genre || "").toUpperCase()}</div>
              <div style={{ color: "white", fontWeight: 800, fontSize: 14, lineHeight: 1.2, overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" }}>{beat.title}</div>
              <div style={{ color: "#666", fontSize: 11, marginTop: 2 }}>by {producer}</div>
            </div>
            <button onClick={onClose} style={{
              width: 30, height: 30, borderRadius: "50%",
              background: "rgba(255,255,255,0.05)", border: "1px solid #222",
              color: "#888", fontSize: 14, cursor: "pointer", flexShrink: 0,
              display: "flex", alignItems: "center", justifyContent: "center",
            }}>✕</button>
          </div>

          {/* Pop-up block warning — only renders if blocked */}
          <PopupWarningBanner />

          {/* STEP: preview / contract terms */}
          {step === "preview" && (
            <div>
              <div style={{
                background: "#0a0a0a", border: "1px solid #1f1f1f", borderRadius: 14,
                padding: 14, marginBottom: 12,
              }}>
                <div style={{ color: accent, fontWeight: 800, fontSize: 11, letterSpacing: 1, marginBottom: 10, textAlign: "center" }}>
                  {isFree ? "FREE BEAT LICENCE AGREEMENT" : "BEAT LEASE AGREEMENT"}
                </div>
                <div style={{ color: "#888", fontSize: 11, lineHeight: 1.7 }}>
                  <div style={{ marginBottom: 6 }}><span style={{ color: "#aaa", fontWeight: 700 }}>Beat:</span> "{beat.title}"</div>
                  <div style={{ marginBottom: 6 }}><span style={{ color: "#aaa", fontWeight: 700 }}>Producer:</span> {producer}</div>
                  <div style={{ marginBottom: 6 }}><span style={{ color: "#aaa", fontWeight: 700 }}>Artist:</span> {artist}</div>
                  <div style={{ marginBottom: 6 }}><span style={{ color: "#aaa", fontWeight: 700 }}>{isFree ? "Type" : "Price"}:</span> {isFree ? "FREE Non-Commercial" : "Choose tier below"}</div>
                  <div style={{ height: 1, background: "#222", margin: "10px 0" }} />
                  {isFree ? (
                    <>
                      <div style={{ marginBottom: 6 }}><span style={{ color: accent, fontWeight: 700 }}>1. GRANT</span> — Free, non-exclusive licence for non-commercial use only.</div>
                      <div style={{ marginBottom: 6 }}><span style={{ color: accent, fontWeight: 700 }}>2. PERMITTED</span> — Record one song, share on free platforms, perform at non-ticketed events.</div>
                      <div style={{ marginBottom: 6 }}><span style={{ color: accent, fontWeight: 700 }}>3. CREDIT</span> — Must credit: <em>(Prod. by {producer})</em>.</div>
                      <div style={{ marginBottom: 6 }}><span style={{ color: accent, fontWeight: 700 }}>4. RESTRICTIONS</span> — No monetisation, no resale, no copyright claim. 10k streams cap.</div>
                      <div style={{ marginBottom: 6 }}><span style={{ color: accent, fontWeight: 700 }}>5. OWNERSHIP</span> — Producer keeps copyright. Artist owns vocals/lyrics only.</div>
                      <div style={{ marginBottom: 6 }}><span style={{ color: accent, fontWeight: 700 }}>6. LAW</span> — Governed by England & Wales.</div>
                    </>
                  ) : (function() {
                    var basicPrice    = Number(beat.basic_lease_price)   || 50;
                    var premiumPrice  = Number(beat.premium_lease_price) || 0;
                    var hasPremium    = premiumPrice > 0;
                    var premiumSold   = !!beat.premium_sold;
                    return (
                      <>
                        {/* ── BASIC LEASE TIER CARD ─────────────────────────── */}
                        <div style={{
                          background: "rgba(245,158,11,0.06)",
                          border: "1px solid rgba(245,158,11,0.30)",
                          borderRadius: 10, padding: 10, marginBottom: 10,
                          opacity: premiumSold ? 0.5 : 1,
                        }}>
                          <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", marginBottom: 6 }}>
                            <span style={{ color: "#F59E0B", fontWeight: 800, fontSize: 11, letterSpacing: 0.5 }}>
                              BASIC LEASE{premiumSold ? " — LOCKED" : ""}
                            </span>
                            <span style={{ color: "#F59E0B", fontWeight: 800, fontSize: 13 }}>£{basicPrice}</span>
                          </div>
                          <div style={{ color: "#aaa", fontSize: 10, lineHeight: 1.55 }}>
                            • Non-exclusive — beat stays available to other buyers<br/>
                            • 100,000 stream cap across all platforms<br/>
                            • Resaleable as part of your finished record<br/>
                            • <strong>75%</strong> composition royalties to producer<br/>
                            • Must credit <em>(Prod. by {producer})</em> in all releases
                          </div>
                        </div>

                        {/* ── PREMIUM LEASE TIER CARD ───────────────────────── */}
                        {hasPremium && (
                          <div style={{
                            background: "rgba(168,85,247,0.07)",
                            border: "1px solid rgba(168,85,247,0.35)",
                            borderRadius: 10, padding: 10, marginBottom: 10,
                            opacity: premiumSold ? 0.5 : 1,
                          }}>
                            <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", marginBottom: 6 }}>
                              <span style={{ color: "#A855F7", fontWeight: 800, fontSize: 11, letterSpacing: 0.5 }}>
                                PREMIUM LEASE (EXCLUSIVE){premiumSold ? " — SOLD" : ""}
                              </span>
                              <span style={{ color: "#A855F7", fontWeight: 800, fontSize: 13 }}>£{premiumPrice}</span>
                            </div>
                            <div style={{ color: "#aaa", fontSize: 10, lineHeight: 1.55 }}>
                              • <strong>EXCLUSIVE</strong> — beat retired from sale permanently<br/>
                              • Unlimited streams — no cap, no expiry<br/>
                              • All prior Basic leases on this beat are voided<br/>
                              • <strong>50%</strong> composition royalties to producer<br/>
                              • No resale or sub-licensing of the Beat itself<br/>
                              • Must credit <em>(Prod. by {producer})</em> in all releases
                            </div>
                          </div>
                        )}

                        {/* ── BASIC BUYERS RISK NOTICE (only if Premium exists) ── */}
                        {hasPremium && !premiumSold && (
                          <div style={{ background: "rgba(245,158,11,0.06)", border: "1px solid rgba(245,158,11,0.25)", borderRadius: 8, padding: 8, marginBottom: 8 }}>
                            <span style={{ color: "#F59E0B", fontWeight: 800, fontSize: 10, letterSpacing: 0.5 }}>⚠ IMPORTANT — Basic Lease buyers:</span>
                            <div style={{ color: "#aaa", fontSize: 10, marginTop: 4, lineHeight: 1.5 }}>
                              Your Basic Lease is <strong>automatically revoked</strong> if another buyer purchases the Premium (Exclusive) Lease for this beat. On revocation you must cease distribution of the New Work. <strong>No refund</strong> is issued. By buying Basic you accept this risk (Clause 6 of the Basic contract).
                            </div>
                          </div>
                        )}

                        {/* ── PREMIUM SOLD NOTICE (when applicable) ─────────── */}
                        {premiumSold && (
                          <div style={{ background: "rgba(239,68,68,0.08)", border: "1px solid rgba(239,68,68,0.30)", borderRadius: 8, padding: 8, marginBottom: 8 }}>
                            <span style={{ color: "#EF4444", fontWeight: 800, fontSize: 10, letterSpacing: 0.5 }}>BEAT NO LONGER AVAILABLE</span>
                            <div style={{ color: "#aaa", fontSize: 10, marginTop: 4, lineHeight: 1.5 }}>
                              This beat has been purchased exclusively. No further sales (Basic or Premium) can be made.
                            </div>
                          </div>
                        )}

                        <div style={{ marginBottom: 6, fontSize: 10, color: "#777" }}>Producer retains copyright on the Beat. Buyer owns vocals &amp; finished record.</div>
                        <div style={{ marginBottom: 6, fontSize: 10, color: "#777" }}>Governed by England and Wales law.</div>
                      </>
                    );
                  })()}
                </div>
              </div>

              {isFree ? (
                <>
                  <button onClick={function() {
                    if (!user) {
                      window.dispatchEvent(new CustomEvent("bf:quickSignup", { detail: {
                        intent: "download", beat: beat,
                        resume: function() {
                          markSigned();
                          generateFreeLeaseContract(beat, user);
                        },
                      }}));
                      return;
                    }
                    markSigned();
                    generateFreeLeaseContract(beat, user);
                  }} style={{
                    width: "100%", borderRadius: 14, padding: "13px",
                    background: "linear-gradient(135deg,#C026D3,#9333EA)",
                    border: "none", color: "white", fontWeight: 800, fontSize: 13,
                    letterSpacing: 0.5, textTransform: "uppercase", cursor: "pointer",
                    display: "flex", alignItems: "center", justifyContent: "center", gap: 8,
                    marginBottom: 8,
                  }}>
                    <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="white" strokeWidth="2.5" strokeLinecap="round"><polyline points="20 6 9 17 4 12"/></svg>
                    I Agree — Save Contract
                  </button>
                </>
              ) : (
                <div>
                  {(function() {
                    var basicPrice   = Number(beat.basic_lease_price)   || 50;
                    var premiumPrice = Number(beat.premium_lease_price) || 0;
                    var premiumSold  = !!beat.premium_sold;
                    var premiumAvailable = (premiumPrice >= 100 && premiumPrice <= 500 && !premiumSold);
                    var disabled = buyLoading || verifyingLease;
                    // BUSINESS RULE: Premium sale retires beat from sale. Basic
                    // also locked (matches backend enforcement in producer.py line 387).
                    var basicLocked = premiumSold;
                    var basicDisabled = disabled || basicLocked;
                    return (
                      <>
                        {/* Basic Lease card — locked once Premium has been sold */}
                        <button onClick={function() { if (!basicLocked) handleBuy("basic"); }}
                          disabled={basicDisabled} style={{
                          width: "100%", borderRadius: 12, padding: "12px 14px",
                          background: basicDisabled ? "transparent" : "rgba(245,158,11,0.08)",
                          border: "2px solid " + (basicDisabled ? "#333" : "#F59E0B"),
                          color: basicDisabled ? "#555" : "white",
                          cursor: basicDisabled ? "not-allowed" : "pointer",
                          marginBottom: 8, textAlign: "left",
                          opacity: basicLocked ? 0.55 : 1,
                        }}>
                          <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", marginBottom: 4 }}>
                            <div style={{ color: basicDisabled ? "#555" : "#F59E0B", fontWeight: 800, fontSize: 13, letterSpacing: 0.5 }}>
                              BASIC LEASE{basicLocked ? " — UNAVAILABLE" : ""}
                            </div>
                            <div style={{ color: basicDisabled ? "#555" : "#F59E0B", fontWeight: 800, fontSize: 16 }}>£{basicPrice}</div>
                          </div>
                          <div style={{ color: basicDisabled ? "#333" : "#888", fontSize: 10, lineHeight: 1.4 }}>
                            {basicLocked
                              ? "This beat has been bought exclusively and is no longer available."
                              : "Non-exclusive · 100k stream cap · Resaleable record"}
                          </div>
                        </button>

                        {/* Premium Lease card */}
                        {premiumPrice > 0 && (
                          <button
                            onClick={function() { if (premiumAvailable && !disabled) handleBuy("premium"); }}
                            disabled={disabled || !premiumAvailable}
                            style={{
                              width: "100%", borderRadius: 12, padding: "12px 14px",
                              background: (disabled || !premiumAvailable) ? "transparent" : "rgba(168,85,247,0.10)",
                              border: "2px solid " + ((disabled || !premiumAvailable) ? "#333" : "#A855F7"),
                              color: (disabled || !premiumAvailable) ? "#555" : "white",
                              cursor: (disabled || !premiumAvailable) ? "not-allowed" : "pointer",
                              marginBottom: 8, textAlign: "left",
                              opacity: (premiumSold ? 0.55 : 1),
                            }}>
                            <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", marginBottom: 4 }}>
                              <div style={{ color: (disabled || !premiumAvailable) ? "#555" : "#A855F7", fontWeight: 800, fontSize: 13, letterSpacing: 0.5 }}>
                                PREMIUM (EXCLUSIVE){premiumSold ? " — SOLD" : ""}
                              </div>
                              <div style={{ color: (disabled || !premiumAvailable) ? "#555" : "#A855F7", fontWeight: 800, fontSize: 16 }}>£{premiumPrice}</div>
                            </div>
                            <div style={{ color: (disabled || !premiumAvailable) ? "#333" : "#888", fontSize: 10, lineHeight: 1.4 }}>
                              {premiumSold
                                ? "This beat has been bought exclusively and is no longer available."
                                : "Exclusive · Unlimited streams · Beat retired · No resale"}
                            </div>
                          </button>
                        )}

                        {verifyingLease && (
                          <div style={{ color: "#555", fontSize: 11, textAlign: "center", marginTop: 6 }}>Checking purchase status…</div>
                        )}
                        {buyLoading && (
                          <div style={{ color: "#F59E0B", fontSize: 11, textAlign: "center", marginTop: 6 }}>Redirecting to Stripe…</div>
                        )}
                      </>
                    );
                  })()}
                </div>
              )}
              <button onClick={onClose} style={{
                width: "100%", borderRadius: 14, padding: "10px",
                background: "transparent", border: "1px solid #222",
                color: "#666", fontWeight: 700, fontSize: 12, cursor: "pointer",
              }}>Cancel</button>
            </div>
          )}

          {/* STEP: done — show downloads */}
          {step === "done" && isFree && (
            <div>
              <div style={{
                background: "rgba(34,197,94,0.08)", border: "1px solid rgba(34,197,94,0.2)",
                borderRadius: 14, padding: "11px 14px", marginBottom: 10,
                display: "flex", alignItems: "center", gap: 10,
              }}>
                <svg width="15" height="15" viewBox="0 0 24 24" fill="none" stroke="#22C55E" strokeWidth="2.5" strokeLinecap="round"><polyline points="20 6 9 17 4 12"/></svg>
                <div>
                  <div style={{ color: "#22C55E", fontWeight: 800, fontSize: 12 }}>Licence Agreed ✓</div>
                  <div style={{ color: "#555", fontSize: 10 }}>Download your contract & MP3 below</div>
                </div>
              </div>
              <button onClick={function() { setShowContract(true); }} style={{
                width: "100%", borderRadius: 14, padding: "11px",
                background: "transparent", border: "1px solid #333",
                color: "#888", fontWeight: 700, fontSize: 12, cursor: "pointer",
                display: "flex", alignItems: "center", justifyContent: "center", gap: 8,
                marginBottom: 8,
              }}>
                <svg width="13" height="13" viewBox="0 0 24 24" fill="none" stroke="#888" strokeWidth="2" strokeLinecap="round"><path d="M14 2H6a2 2 0 0 0-2 2v16a2 2 0 0 0 2 2h12a2 2 0 0 0 2-2V8z"/><polyline points="14 2 14 8 20 8"/></svg>
                View Licence Contract
              </button>
              <button onClick={function() { downloadBeatMp3(beat); }} style={{
                width: "100%", borderRadius: 14, padding: "14px", border: "none",
                background: "linear-gradient(135deg,#C026D3,#9333EA)",
                color: "white", fontWeight: 800, fontSize: 14,
                letterSpacing: 0.5, textTransform: "uppercase",
                display: "flex", alignItems: "center", justifyContent: "center", gap: 10,
                cursor: "pointer", boxShadow: "0 0 20px rgba(192,38,211,0.4)",
              }}>
                <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="white" strokeWidth="2.5" strokeLinecap="round"><path d="M12 3v13M6 11l6 6 6-6"/><path d="M4 20h16"/></svg>
                Save MP3 to Device
              </button>
            </div>
          )}

          {/* STEP: done for LICENSED — show two-step download:
              1) View/Download Contract  →  2) Save MP3 to Device.
              The MP3 button only unlocks once the contract has been opened.
              If the lease was VOIDED (premium sold by someone else after this
              basic purchase), show a revoked state and block downloads. */}
          {step === "done" && !isFree && (verifiedLease && verifiedLease.voided) && (
            <div>
              <div style={{
                background: "rgba(239,68,68,0.08)", border: "1px solid rgba(239,68,68,0.3)",
                borderRadius: 14, padding: "12px 14px", marginBottom: 10,
                display: "flex", alignItems: "flex-start", gap: 10,
              }}>
                <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="#EF4444" strokeWidth="2.5" strokeLinecap="round" style={{ flexShrink: 0, marginTop: 1 }}>
                  <circle cx="12" cy="12" r="10"/><line x1="15" y1="9" x2="9" y2="15"/><line x1="9" y1="9" x2="15" y2="15"/>
                </svg>
                <div>
                  <div style={{ color: "#EF4444", fontWeight: 800, fontSize: 13 }}>Basic Lease Revoked</div>
                  <div style={{ color: "#888", fontSize: 11, marginTop: 4, lineHeight: 1.5 }}>
                    Your Basic Lease for this beat was revoked because the Producer sold the Premium (Exclusive) Lease to another buyer. As agreed at purchase (Clause 6 of the Basic Lease), no refund is issued and you must cease distribution of any New Work derived from this beat.
                  </div>
                </div>
              </div>
              <button onClick={onClose} style={{
                width: "100%", borderRadius: 14, padding: "12px",
                background: "transparent", border: "1px solid #333",
                color: "#888", fontWeight: 700, fontSize: 12, cursor: "pointer",
              }}>Close</button>
            </div>
          )}

          {step === "done" && !isFree && !(verifiedLease && verifiedLease.voided) && (
            <div>
              <div style={{
                background: "rgba(34,197,94,0.08)", border: "1px solid rgba(34,197,94,0.2)",
                borderRadius: 14, padding: "11px 14px", marginBottom: 10,
                display: "flex", alignItems: "center", gap: 10,
              }}>
                <svg width="15" height="15" viewBox="0 0 24 24" fill="none" stroke="#22C55E" strokeWidth="2.5" strokeLinecap="round"><polyline points="20 6 9 17 4 12"/></svg>
                <div>
                  <div style={{ color: "#22C55E", fontWeight: 800, fontSize: 12 }}>Payment Confirmed ✓ {verifiedLease && verifiedLease.tier === "premium" ? "— Premium" : "— Basic"}</div>
                  <div style={{ color: "#555", fontSize: 10 }}>
                    {contractDownloaded
                      ? "Contract downloaded — you can now save the MP3"
                      : "Download your contract first to unlock the MP3"}
                  </div>
                </div>
              </div>
              <button onClick={function() { setShowContract(true); setContractDownloaded(true); }} style={{
                width: "100%", borderRadius: 14, padding: "11px",
                background: contractDownloaded ? "transparent" : "linear-gradient(135deg,#F59E0B,#D97706)",
                border: contractDownloaded ? "1px solid #333" : "none",
                color: contractDownloaded ? "#888" : "white",
                fontWeight: 700, fontSize: 12, cursor: "pointer",
                display: "flex", alignItems: "center", justifyContent: "center", gap: 8,
                marginBottom: 8,
              }}>
                <svg width="13" height="13" viewBox="0 0 24 24" fill="none" stroke={contractDownloaded ? "#888" : "white"} strokeWidth="2" strokeLinecap="round"><path d="M14 2H6a2 2 0 0 0-2 2v16a2 2 0 0 0 2 2h12a2 2 0 0 0 2-2V8z"/><polyline points="14 2 14 8 20 8"/></svg>
                {contractDownloaded ? "View Licence Contract Again" : "Download Licence Contract"}
              </button>
              <button
                onClick={function() {
                  if (!contractDownloaded) return;
                  downloadBeatMp3(beat);
                }}
                disabled={!contractDownloaded}
                style={{
                  width: "100%", borderRadius: 14, padding: "14px", border: "none",
                  background: contractDownloaded
                    ? "linear-gradient(135deg,#F59E0B,#D97706)"
                    : "#1a1a1a",
                  color: contractDownloaded ? "white" : "#444",
                  fontWeight: 800, fontSize: 14,
                  letterSpacing: 0.5, textTransform: "uppercase",
                  display: "flex", alignItems: "center", justifyContent: "center", gap: 10,
                  cursor: contractDownloaded ? "pointer" : "not-allowed",
                  boxShadow: contractDownloaded ? "0 0 20px rgba(245,158,11,0.4)" : "none",
                  opacity: contractDownloaded ? 1 : 0.6,
                }}>
                <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke={contractDownloaded ? "white" : "#444"} strokeWidth="2.5" strokeLinecap="round"><path d="M12 3v13M6 11l6 6 6-6"/><path d="M4 20h16"/></svg>
                {contractDownloaded ? "Save MP3 to Device" : "MP3 — Locked"}
              </button>
            </div>
          )}
        </div>
      </div>
    </>
  );
}

// ── Compact beat card for two-column profile layout ───────────────────────────
function CompactBeatCard({ beat, currentUser }) {
  var isFreePrice = function(p) { return !p || p === "free" || p === "£0" || p === "0" || p === "£0.00" || p === "0.00"; };
  var isFree  = isFreePrice(beat.price);
  // LED theme: free = magenta-violet, paid = electric purple-to-blue
  var accent  = isFree ? "#C026D3" : "#7C3AED";
  var accent2 = isFree ? "#7C3AED" : "#3B82F6";
  var [previewing, setPreviewing] = React.useState(false);
  var [previewTime, setPreviewTime] = React.useState(0);
  var [buyLoading, setBuyLoading] = React.useState(false);
  var [sheetOpen, setSheetOpen] = React.useState(false);
  // Check global lease cache for ownership of this beat
  var [ownedLease, setOwnedLease] = React.useState(function() {
    try {
      if (isFree || !currentUser || !Array.isArray(window.__bf_leases__)) return null;
      var match = window.__bf_leases__.find(function(l){ return (l.beat_id === beat.id || l.id === beat.id) && !l.voided; });
      return match || null;
    } catch(e) { return null; }
  });
  React.useEffect(function() {
    if (isFree || !currentUser) { setOwnedLease(null); return; }
    function check() {
      try {
        if (!Array.isArray(window.__bf_leases__)) return;
        var match = window.__bf_leases__.find(function(l){ return (l.beat_id === beat.id || l.id === beat.id) && !l.voided; });
        setOwnedLease(match || null);
      } catch(e) {}
    }
    check();
    window.addEventListener("bf-leases-updated", check);
    return function() { window.removeEventListener("bf-leases-updated", check); };
  }, [isFree, beat.id, currentUser]);
  React.useEffect(function() {
    function onReopen(e) {
      if (!e || !e.detail || e.detail.beat_id !== beat.id) return;
      if (window.__bf_reopen_consumed__ === beat.id) return;
      window.__bf_reopen_consumed__ = beat.id;
      setSheetOpen(true);
    }
    window.addEventListener("bf-reopen-sheet", onReopen);
    return function() { window.removeEventListener("bf-reopen-sheet", onReopen); };
  }, [beat.id]);
  var audioRef  = React.useRef(null);
  var timerRef  = React.useRef(null);
  var seekedRef = React.useRef(false);
  var previewId = React.useRef("compact_" + beat.id);

  function stopPreview() {
    clearGlobalPreview(previewId.current);
    setPreviewing(false); setPreviewTime(0); clearInterval(timerRef.current);
    cancelPlayTimer(beat.id);
    if (audioRef.current) { audioRef.current.pause(); audioRef.current.currentTime = 0; }
  }
  function startPreview() {
    if (previewing) { stopPreview(); return; }
    seekedRef.current = false;
    startGlobalPreview(previewId.current);
    setPreviewing(true); setPreviewTime(0);
    recordPlay(beat.id);
  }
  useGlobalPreviewStop(previewId.current, React.useCallback(function() {
    setPreviewing(false); setPreviewTime(0); clearInterval(timerRef.current);
    cancelPlayTimer(beat.id);
    if (audioRef.current) { audioRef.current.pause(); audioRef.current.currentTime = 0; }
  }, [beat.id]));
  function onAudioPlay() {
    var ps = beat.preview_start || 0;
    if (ps > 0 && !seekedRef.current) {
      // Try immediately
      if (audioRef.current) { try { audioRef.current.currentTime = ps; } catch(e) {} }
      // Also try after a tick for iOS
      setTimeout(function() {
        if (audioRef.current && !seekedRef.current) {
          try { audioRef.current.currentTime = ps; seekedRef.current = true; } catch(e) {}
        }
      }, 0);
      setTimeout(function() {
        if (audioRef.current && !seekedRef.current) {
          try { audioRef.current.currentTime = ps; seekedRef.current = true; } catch(e) {}
        }
      }, 100);
    }
    clearInterval(timerRef.current);
    var start = Date.now();
    timerRef.current = setInterval(function() {
      var el = (Date.now() - start) / 1000;
      setPreviewTime(Math.min(el, 45));
      if (el >= 45) { clearInterval(timerRef.current); stopPreview(); }
    }, 100);
  }
  function onTimeUpdate(e) {
    var el = e.target, st = parseFloat(el.dataset.startTime || 0);
    if (st > 0 && el.currentTime < st && !seekedRef.current) { el.currentTime = st; seekedRef.current = true; }
    if (el.currentTime >= st + 45) { el.pause(); stopPreview(); }
  }
  React.useEffect(function() { return function() { clearInterval(timerRef.current); }; }, []);

  var pct = previewing ? (previewTime / 45) * 100 : 0;

  return (
    <div style={{
      background: "linear-gradient(165deg,#0f0a1f 0%,#0a0a14 60%,#080812 100%)",
      borderRadius: 16, marginBottom: 10, overflow: "hidden",
      border: "1px solid " + accent + "55",
      boxShadow: "0 4px 20px rgba(0,0,0,0.6), 0 0 0 0.5px " + accent + "33, 0 0 16px " + accent + "1a, inset 0 1px 0 rgba(255,255,255,0.04)",
      position: "relative",
    }}>
      <div style={{
        height: 2,
        background: "linear-gradient(90deg,transparent 0%," + accent + " 20%," + accent2 + " 50%," + accent + " 80%,transparent 100%)",
        boxShadow: "0 0 8px " + accent + "cc, 0 0 16px " + accent2 + "66",
      }} />
      <div style={{ padding: "10px 10px 0" }}>
        <div style={{ display: "flex", gap: 8, marginBottom: 8, alignItems: "center" }}>
          <div style={{ width: 40, height: 40, borderRadius: 8, flexShrink: 0, overflow: "hidden", background: "#111" }}>
            <img src="https://i.ibb.co/v4wcZVJW/IMG-9119.jpg" alt="" style={{ width: "100%", height: "100%", objectFit: "cover" }} />
          </div>
          <div style={{ flex: 1, minWidth: 0 }}>
            <div style={{ color: "white", fontWeight: 700, fontSize: 11, lineHeight: 1.3,
              overflow: "hidden", textOverflow: "ellipsis", display: "-webkit-box",
              WebkitLineClamp: 2, WebkitBoxOrient: "vertical" }}>
              {beat.title}
            </div>
            <div style={{ color: "#555", fontSize: 9, marginTop: 2 }}>{beat.genre}</div>
          </div>
        </div>
        {(beat.bpm > 0 || beat.key) && (
          <div style={{ display: "flex", gap: 4, marginBottom: 8, flexWrap: "wrap" }}>
            {beat.bpm > 0 && <span style={{ background: "rgba(6,182,212,0.1)", border: "1px solid rgba(6,182,212,0.2)", borderRadius: 10, padding: "2px 6px", fontSize: 8, color: "#06B6D4", fontWeight: 700 }}>{beat.bpm} BPM</span>}
            {beat.key && <span style={{ background: "rgba(34,197,94,0.1)", border: "1px solid rgba(34,197,94,0.2)", borderRadius: 10, padding: "2px 6px", fontSize: 8, color: "#22C55E", fontWeight: 700 }}>{beat.key}</span>}
          </div>
        )}
        <div style={{ height: 3, background: "#1a1a1a", borderRadius: 2, marginBottom: 8, overflow: "hidden" }}>
          <div style={{ height: "100%", width: pct + "%", background: accent, borderRadius: 2, transition: "width 0.1s linear" }} />
        </div>
      </div>
      <div style={{ display: "flex", alignItems: "center", gap: 6, padding: "0 10px 10px" }}>
        <button onClick={startPreview} style={{
          width: 28, height: 28, borderRadius: "50%", border: "1.5px solid " + accent,
          background: previewing ? accent : "transparent",
          display: "flex", alignItems: "center", justifyContent: "center",
          cursor: "pointer", flexShrink: 0,
        }}>
          {previewing
            ? <svg width="8" height="8" viewBox="0 0 24 24" fill="white"><rect x="6" y="4" width="4" height="16"/><rect x="14" y="4" width="4" height="16"/></svg>
            : <svg width="8" height="8" viewBox="0 0 24 24" fill={accent}><polygon points="5,3 19,12 5,21"/></svg>
          }
        </button>
        <div style={{ flex: 1, minWidth: 0 }}>
          {previewing && <div style={{ color: "#555", fontSize: 8 }}>{Math.floor(previewTime)}s / 45s</div>}
        </div>
        {/* Action button — both FREE and LICENSED open the contract sheet */}
        {isFree ? (
          <button onClick={function() {
            if (!currentUser) {
              window.dispatchEvent(new CustomEvent("bf:quickSignup", { detail: {
                intent: "download", beat: beat,
                resume: function() { setSheetOpen(true); },
              }}));
              return;
            }
            setSheetOpen(true);
          }} style={{
            background: "transparent", border: "1.5px solid " + (currentUser ? "#C026D3" : "#555"),
            borderRadius: 20, padding: "5px 10px",
            color: currentUser ? "#C026D3" : "#555", fontSize: 9, fontWeight: 800,
            cursor: "pointer", flexShrink: 0, display: "flex", alignItems: "center", gap: 4,
          }}>
            <svg width="9" height="9" viewBox="0 0 24 24" fill="none" stroke={currentUser ? "#C026D3" : "#555"} strokeWidth="2.5" strokeLinecap="round">
              {currentUser
                ? <><path d="M12 3v13M6 11l6 6 6-6"/></>
                : <><rect x="3" y="11" width="18" height="11" rx="2"/><path d="M7 11V7a5 5 0 0 1 10 0v4"/></>
              }
            </svg>
            FREE
          </button>
        ) : ownedLease ? (
          /* OWNED state — small pill button showing the user owns this beat */
          <button onClick={function() { setSheetOpen(true); }} style={{
            background: ownedLease.tier === "premium"
              ? "linear-gradient(135deg,#C026D3 0%,#A855F7 35%,#7C3AED 70%,#3B82F6 100%)"
              : "linear-gradient(135deg,#3B82F6 0%,#6366F1 50%,#7C3AED 100%)",
            border: "1.5px solid " + (ownedLease.tier === "premium" ? "rgba(192,38,211,0.6)" : "rgba(124,58,237,0.6)"),
            borderRadius: 20, padding: "5px 11px",
            color: "white", fontSize: 9, fontWeight: 900,
            cursor: "pointer", flexShrink: 0,
            display: "flex", alignItems: "center", gap: 4,
            letterSpacing: 0.5,
            boxShadow: ownedLease.tier === "premium"
              ? "0 2px 10px rgba(192,38,211,0.5), 0 0 16px rgba(168,85,247,0.35), inset 0 1px 0 rgba(255,255,255,0.25)"
              : "0 2px 10px rgba(124,58,237,0.4), 0 0 14px rgba(59,130,246,0.3), inset 0 1px 0 rgba(255,255,255,0.2)",
            textShadow: "0 1px 2px rgba(0,0,0,0.3)",
          }}>
            <svg width="9" height="9" viewBox="0 0 24 24" fill="none" stroke="white" strokeWidth="3" strokeLinecap="round" style={{ filter: "drop-shadow(0 0 3px rgba(255,255,255,0.9))" }}>
              <polyline points="20 6 9 17 4 12"/>
            </svg>
            OWNED
          </button>
        ) : (function() {
          // Render tier buttons on the compact card.
          // BUSINESS RULE: once Premium is sold, beat is retired. Both buttons disabled.
          var basicP   = Number(beat.basic_lease_price)   || 0;
          var premiumP = Number(beat.premium_lease_price) || 0;
          var premiumSold = !!beat.premium_sold;
          var showPremium = premiumP > 0 && !premiumSold;
          // Fully-sold-out state: Premium has been sold → no more sales of anything
          if (premiumSold) {
            return (
              <button disabled style={{
                background: "transparent", border: "1.5px solid #555",
                borderRadius: 20, padding: "5px 10px",
                color: "#555", fontSize: 9, fontWeight: 800,
                cursor: "not-allowed", flexShrink: 0,
                display: "flex", alignItems: "center", gap: 4,
                opacity: 0.7,
              }}>
                SOLD OUT
              </button>
            );
          }
          // Fallback to legacy single price label if neither tier is set
          if (basicP === 0 && premiumP === 0) {
            return (
              <button onClick={function() {
                if (!currentUser) { alert("Sign up and purchase a plan to buy beat leases!"); return; }
                setSheetOpen(true);
              }} disabled={buyLoading} style={{
                background: currentUser ? "linear-gradient(135deg,#3B82F6 0%,#6366F1 50%,#7C3AED 100%)" : "transparent",
                border: "1.5px solid " + (currentUser ? "rgba(124,58,237,0.6)" : "#444"),
                borderRadius: 20, padding: "5px 11px",
                color: currentUser ? "white" : "#555", fontSize: 9, fontWeight: 900,
                cursor: buyLoading ? "not-allowed" : "pointer", flexShrink: 0,
                display: "flex", alignItems: "center", gap: 4,
                letterSpacing: 0.5,
                boxShadow: currentUser ? "0 2px 10px rgba(124,58,237,0.4), 0 0 14px rgba(59,130,246,0.3), inset 0 1px 0 rgba(255,255,255,0.2)" : "none",
                textShadow: currentUser ? "0 1px 2px rgba(0,0,0,0.3)" : "none",
              }}>
                {!currentUser && <svg width="9" height="9" viewBox="0 0 24 24" fill="none" stroke="#555" strokeWidth="2.5" strokeLinecap="round"><rect x="3" y="11" width="18" height="11" rx="2"/><path d="M7 11V7a5 5 0 0 1 10 0v4"/></svg>}
                {buyLoading ? "..." : "BUY " + (beat.price || "£50")}
              </button>
            );
          }
          // Two-tier (Premium still available): stacked Basic + Premium buttons
          var basicLabel = "BUY £" + (basicP || 50);
          var premiumLabel = "PREMIUM £" + premiumP;
          return (
            <div style={{ display: "flex", flexDirection: "column", gap: 5, flexShrink: 0 }}>
              <button onClick={function() {
                if (!currentUser) { alert("Sign up and purchase a plan to buy beat leases!"); return; }
                setSheetOpen(true);
              }} disabled={buyLoading} style={{
                background: currentUser ? "linear-gradient(135deg,#3B82F6 0%,#6366F1 50%,#7C3AED 100%)" : "transparent",
                border: "1.5px solid " + (currentUser ? "rgba(124,58,237,0.6)" : "#444"),
                borderRadius: 20, padding: "5px 11px",
                color: currentUser ? "white" : "#555", fontSize: 9, fontWeight: 900,
                cursor: buyLoading ? "not-allowed" : "pointer",
                display: "flex", alignItems: "center", justifyContent: "center", gap: 4,
                minWidth: 84, letterSpacing: 0.5,
                boxShadow: currentUser ? "0 2px 10px rgba(124,58,237,0.4), 0 0 14px rgba(59,130,246,0.3), inset 0 1px 0 rgba(255,255,255,0.2)" : "none",
                textShadow: currentUser ? "0 1px 2px rgba(0,0,0,0.3)" : "none",
              }}>
                {!currentUser && <svg width="9" height="9" viewBox="0 0 24 24" fill="none" stroke="#555" strokeWidth="2.5" strokeLinecap="round"><rect x="3" y="11" width="18" height="11" rx="2"/><path d="M7 11V7a5 5 0 0 1 10 0v4"/></svg>}
                {buyLoading ? "..." : basicLabel}
              </button>
              {showPremium && (
                <button onClick={function() {
                  if (!currentUser) { alert("Sign up and purchase a plan to buy beat leases!"); return; }
                  setSheetOpen(true);
                }} disabled={buyLoading} style={{
                  background: currentUser ? "linear-gradient(135deg,#C026D3 0%,#A855F7 35%,#7C3AED 70%,#3B82F6 100%)" : "transparent",
                  border: "1.5px solid " + (currentUser ? "rgba(192,38,211,0.6)" : "#444"),
                  borderRadius: 20, padding: "5px 11px",
                  color: currentUser ? "white" : "#555", fontSize: 9, fontWeight: 900,
                  cursor: buyLoading ? "not-allowed" : "pointer",
                  display: "flex", alignItems: "center", justifyContent: "center", gap: 4,
                  minWidth: 84, letterSpacing: 0.5,
                  boxShadow: currentUser ? "0 2px 10px rgba(192,38,211,0.5), 0 0 16px rgba(168,85,247,0.35), inset 0 1px 0 rgba(255,255,255,0.25)" : "none",
                  textShadow: currentUser ? "0 1px 2px rgba(0,0,0,0.3)" : "none",
                }}>
                  {buyLoading ? "..." : premiumLabel}
                </button>
              )}
            </div>
          );
        })()}
      </div>
      {sheetOpen && (
        <CompactBeatActionSheet beat={beat} user={currentUser} onClose={function() { setSheetOpen(false); }} />
      )}
      {previewing && beat.url && (
        <audio ref={audioRef} src={beat.url + ((beat.preview_start||0) > 0 ? "#t=" + (beat.preview_start||0) : "")} autoPlay data-start-time={String(beat.preview_start || 0)}
          onPlay={function(){ onAudioPlay(); if (audioRef.current && (beat.preview_start||0) > 0 && !seekedRef.current) { audioRef.current.currentTime = beat.preview_start; seekedRef.current = true; } }}
          onPause={function(){ clearInterval(timerRef.current); }}
          onTimeUpdate={onTimeUpdate} onEnded={stopPreview} />
      )}
    </div>
  );
}

class BeatsBoundary extends React.Component {
  constructor(props) { super(props); this.state = { err: null }; }
  static getDerivedStateFromError(e) { return { err: e }; }
  render() {
    if (this.state.err) {
      return React.createElement('div', { style: { padding: 20, color: '#F87171', fontSize: 13 } },
        'Error: ' + (this.state.err.message || 'Unknown error loading beats'));
    }
    return this.props.children;
  }
}

function BeatsTabContent({ profile, currentUser, onViewProfile }) {
  var beats = (profile && profile.beats) || [];
  function isFreePrice(p) { return !p || p === "free" || p === "£0" || p === "0" || p === "£0.00" || p === "0.00"; }
  var freeBeats     = beats.filter(function(b) { return isFreePrice(b.price); });
  var licensedBeats = beats.filter(function(b) { return !isFreePrice(b.price); });
  var hasBoth = freeBeats.length > 0 && licensedBeats.length > 0;

  if (beats.length === 0) return (
    <div style={{ textAlign: "center", padding: "40px 24px", color: "#555" }}>
      <AppIcon id="note" size={40} />
      <div style={{ fontSize: 15, marginTop: 12, color: "#444", fontWeight: 700 }}>No beats yet</div>
    </div>
  );

  if (!hasBoth) return (
    <div style={{ padding: "0 16px" }}>
      {freeBeats.length > 0 && (
        <div style={{ marginBottom: 24 }}>
          <div style={{ display: "flex", alignItems: "center", gap: 8, marginBottom: 12, paddingBottom: 8, borderBottom: "1px solid rgba(192,38,211,0.25)" }}>
            <div style={{
              width: 8, height: 8, borderRadius: "50%",
              background: "linear-gradient(135deg,#C026D3,#7C3AED)",
              boxShadow: "0 0 8px #C026D3cc, 0 0 14px #7C3AED88",
            }} />
            <div style={{
              color: "#E9D5FF", fontWeight: 900, fontSize: 13, letterSpacing: 1,
              textShadow: "0 0 8px rgba(192,38,211,0.55)",
            }}>FREE BEATS</div>
            <div style={{
              background: "linear-gradient(135deg,rgba(192,38,211,0.2),rgba(124,58,237,0.2))",
              border: "1px solid rgba(192,38,211,0.5)",
              borderRadius: 20, padding: "1px 8px",
              fontSize: 10, color: "#F3E8FF", fontWeight: 800,
              boxShadow: "0 0 10px rgba(192,38,211,0.35)",
            }}>{freeBeats.length}</div>
          </div>
          {freeBeats.map(function(b) { return <ProfileBeatCard key={b.id} beat={b} currentUser={currentUser} onViewProfile={onViewProfile} />; })}
        </div>
      )}
      {licensedBeats.length > 0 && (
        <div style={{ marginBottom: 24 }}>
          <div style={{ display: "flex", alignItems: "center", gap: 8, marginBottom: 12, paddingBottom: 8, borderBottom: "1px solid rgba(124,58,237,0.25)" }}>
            <div style={{
              width: 8, height: 8, borderRadius: "50%",
              background: "linear-gradient(135deg,#7C3AED,#3B82F6)",
              boxShadow: "0 0 8px #7C3AEDcc, 0 0 14px #3B82F688",
            }} />
            <div style={{
              color: "#A78BFA", fontWeight: 900, fontSize: 13, letterSpacing: 1,
              textShadow: "0 0 8px rgba(124,58,237,0.55)",
            }}>LICENSED BEATS</div>
            <div style={{
              background: "linear-gradient(135deg,rgba(124,58,237,0.2),rgba(59,130,246,0.2))",
              border: "1px solid rgba(124,58,237,0.5)",
              borderRadius: 20, padding: "1px 8px",
              fontSize: 10, color: "#DDD6FE", fontWeight: 800,
              boxShadow: "0 0 10px rgba(124,58,237,0.35)",
            }}>{licensedBeats.length}</div>
          </div>
          {licensedBeats.map(function(b) { return <ProfileBeatCard key={b.id} beat={b} currentUser={currentUser} onViewProfile={onViewProfile} />; })}
        </div>
      )}
    </div>
  );

  return (
    <div style={{ padding: "0 12px" }}>
      <div style={{ display: "flex", gap: 10 }}>
        <div style={{ flex: 1, minWidth: 0 }}>
          <div style={{ display: "flex", alignItems: "center", gap: 6, marginBottom: 10, paddingBottom: 6, borderBottom: "1px solid rgba(192,38,211,0.25)" }}>
            <div style={{
              width: 6, height: 6, borderRadius: "50%",
              background: "linear-gradient(135deg,#C026D3,#7C3AED)",
              boxShadow: "0 0 6px #C026D3cc, 0 0 10px #7C3AED88",
              flexShrink: 0,
            }} />
            <div style={{
              color: "#E9D5FF", fontWeight: 900, fontSize: 11, letterSpacing: 0.8,
              textShadow: "0 0 6px rgba(192,38,211,0.55)",
            }}>FREE</div>
            <div style={{
              background: "linear-gradient(135deg,rgba(192,38,211,0.2),rgba(124,58,237,0.2))",
              border: "1px solid rgba(192,38,211,0.5)",
              borderRadius: 20, padding: "1px 6px",
              fontSize: 9, color: "#F3E8FF", fontWeight: 800,
              boxShadow: "0 0 8px rgba(192,38,211,0.35)",
            }}>{freeBeats.length}</div>
          </div>
          {freeBeats.map(function(b) { return <CompactBeatCard key={b.id} beat={b} currentUser={currentUser} />; })}
        </div>
        <div style={{ width: 1, background: "rgba(255,255,255,0.05)", flexShrink: 0 }} />
        <div style={{ flex: 1, minWidth: 0 }}>
          <div style={{ display: "flex", alignItems: "center", gap: 6, marginBottom: 10, paddingBottom: 6, borderBottom: "1px solid rgba(124,58,237,0.25)" }}>
            <div style={{
              width: 6, height: 6, borderRadius: "50%",
              background: "linear-gradient(135deg,#7C3AED,#3B82F6)",
              boxShadow: "0 0 6px #7C3AEDcc, 0 0 10px #3B82F688",
              flexShrink: 0,
            }} />
            <div style={{
              color: "#A78BFA", fontWeight: 900, fontSize: 11, letterSpacing: 0.8,
              textShadow: "0 0 6px rgba(124,58,237,0.55)",
            }}>LICENSED</div>
            <div style={{
              background: "linear-gradient(135deg,rgba(124,58,237,0.2),rgba(59,130,246,0.2))",
              border: "1px solid rgba(124,58,237,0.5)",
              borderRadius: 20, padding: "1px 6px",
              fontSize: 9, color: "#DDD6FE", fontWeight: 800,
              boxShadow: "0 0 8px rgba(124,58,237,0.35)",
            }}>{licensedBeats.length}</div>
          </div>
          {licensedBeats.map(function(b) { return <CompactBeatCard key={b.id} beat={b} currentUser={currentUser} />; })}
        </div>
      </div>
    </div>
  );
}

function ContentTabs({ username, profile, currentUser, onPlay, savedIds, onSave, onViewProfile }) {
  var [tab, setTab]           = React.useState("posts");
  var [items, setItems]       = React.useState([]);
  var [loading, setLoading]   = React.useState(false);
  var [comments, setComments] = React.useState({});
  var [likes, setLikes]             = React.useState({});
  var [openSheet, setOpenSheet]       = React.useState(null);
  var [posts, setPosts]         = React.useState([]);
  var [openPostSheet, setOpenPostSheet] = React.useState(null);
  var [postLikes, setPostLikes] = React.useState({});
  // Per-post repost state — true when current user has reposted this
  // post. Mirrors the Feed's postReposts ref so the Repost icon
  // renders filled/coloured for the user's own reposts.
  var [postReposts, setPostReposts] = React.useState({});
  var [postComments, setPostComments] = React.useState({});
  var [lightbox, setLightbox] = React.useState(null);
  var [openMenu, setOpenMenu] = React.useState(null);

  // Artist tracks state
  var [tracks, setTracks]           = React.useState([]);
  var [tracksLoading, setTracksLoading] = React.useState(false);
  var [showTrackUpload, setShowTrackUpload] = React.useState(false);
  var isOwnerArtist = currentUser && currentUser.username === username && (currentUser.plan === "artist" || currentUser.plan === "producer");

  function loadTracks() {
    setTracksLoading(true);
    apiFetch("/api/auth/tracks/profile/" + encodeURIComponent(username))
      .then(function(d) { setTracks(d || []); setTracksLoading(false); })
      .catch(function() { setTracksLoading(false); });
  }

  React.useEffect(function() {
    if (tab === "tracks") loadTracks();
  }, [tab, username]);

  function loadPosts() {
    setLoading(true); setPosts([]);
    apiFetch("/api/posts/profile/" + encodeURIComponent(username) + "?type=all")
      .then(function(data) {
        var loaded = data || [];
        setPosts(loaded);
        setLoading(false);
        loaded.forEach(function(p) { loadPostComments(p.id); });
        if (currentUser) {
          loaded.forEach(function(p) {
            apiFetch("/api/posts/" + p.id + "/liked")
              .then(function(r) {
                setPostLikes(function(prev) { var n = Object.assign({}, prev); n[p.id] = r.liked; return n; });
              }).catch(function() {});
            // Reposted-state — endpoint mirrors /liked. Tells us if the
            // current user has already reposted this post so the icon
            // can render as active.
            apiFetch("/api/posts/" + p.id + "/reposted")
              .then(function(r) {
                setPostReposts(function(prev) { var n = Object.assign({}, prev); n[p.id] = !!(r && r.reposted); return n; });
              }).catch(function() {});
          });
        }
      }).catch(function() { setLoading(false); });
  }

  function loadPostComments(id) {
    apiFetch("/api/posts/" + id + "/comments")
      .then(function(data) {
        setPostComments(function(prev) { var n = Object.assign({}, prev); n[id] = data || []; return n; });
      }).catch(function() {});
  }

  function togglePostLike(id) {
    if (!currentUser) return;
    var wasLiked = postLikes[id] || false;
    setPostLikes(function(prev) { var n = Object.assign({}, prev); n[id] = !wasLiked; return n; });
    setPosts(function(prev) { return prev.map(function(p) {
      return p.id === id ? Object.assign({}, p, { likeCount: p.likeCount + (!wasLiked ? 1 : -1) }) : p;
    }); });
    apiFetch("/api/posts/" + id + "/like", { method: "POST" })
      .then(function(r) { setPostLikes(function(prev) { var n = Object.assign({}, prev); n[id] = r.liked; return n; }); })
      .catch(function() { setPostLikes(function(prev) { var n = Object.assign({}, prev); n[id] = wasLiked; return n; }); });
  }

  // Toggle a post repost. Same pattern as toggleLike — optimistic
  // local state flip, then POST or DELETE against the API and
  // reconcile with the server's response. Mirrors the Feed's
  // togglePostRepost so behaviour is consistent across both views.
  function togglePostRepost(id) {
    if (!currentUser) return;
    var wasReposted = !!postReposts[id];
    setPostReposts(function(prev) { var n = Object.assign({}, prev); n[id] = !wasReposted; return n; });
    setPosts(function(prev) { return prev.map(function(p) {
      return p.id === id
        ? Object.assign({}, p, { repostCount: Math.max(0, (p.repostCount || 0) + (!wasReposted ? 1 : -1)) })
        : p;
    }); });
    var method = wasReposted ? "DELETE" : "POST";
    apiFetch("/api/posts/" + id + "/repost", { method: method })
      .then(function(r) {
        setPostReposts(function(prev) { var n = Object.assign({}, prev); n[id] = !!(r && r.reposted); return n; });
      })
      .catch(function() {
        // Revert on failure
        setPostReposts(function(prev) { var n = Object.assign({}, prev); n[id] = wasReposted; return n; });
        setPosts(function(prev) { return prev.map(function(p) {
          return p.id === id
            ? Object.assign({}, p, { repostCount: Math.max(0, (p.repostCount || 0) + (wasReposted ? 1 : -1)) })
            : p;
        }); });
      });
  }

  function submitPostComment(postId, text, parentId, onSuccess, onError) {
    apiFetch("/api/posts/" + postId + "/comments", {
      method: "POST", body: JSON.stringify({ text: text, parentId: parentId || null })
    }).then(function(c) {
      setPostComments(function(prev) { var n = Object.assign({}, prev); n[postId] = [...(n[postId] || []), c]; return n; });
      setPosts(function(prev) { return prev.map(function(p) {
        return p.id === postId ? Object.assign({}, p, { commentCount: p.commentCount + 1 }) : p;
      }); });
      if (onSuccess) onSuccess();
    }).catch(function() { if (onError) onError(); });
  }

  function deletePostComment(postId, commentId) {
    apiFetch("/api/posts/" + postId + "/comments/" + commentId, { method: "DELETE" })
      .then(function() {
        setPostComments(function(prev) { var n = Object.assign({}, prev); n[postId] = (n[postId] || []).filter(function(c) { return c.id !== commentId; }); return n; });
        setPosts(function(prev) { return prev.map(function(p) {
          return p.id === postId ? Object.assign({}, p, { commentCount: Math.max(0, p.commentCount - 1) }) : p;
        }); });
      }).catch(function() {});
  }

  function loadContent(type) {
    setLoading(true); setItems([]);
    apiFetch("/api/content/profile/" + encodeURIComponent(username) + "?type=" + type)
      .then(function(data) {
        var loaded = data || [];
        setItems(loaded);
        setLoading(false);
        // Load comments for all items
        loaded.forEach(function(item) { loadComments(item.id); });
        // Check liked state for logged-in user
        if (currentUser) {
          loaded.forEach(function(item) {
            apiFetch("/api/content/" + item.id + "/liked")
              .then(function(r) {
                setLikes(function(prev) { var n = Object.assign({}, prev); n[item.id] = r.liked; return n; });
              })
              .catch(function() {});
          });
        }
      })
      .catch(function() { setLoading(false); });
  }

  React.useEffect(function() {
    if (tab === "music" || tab === "video") loadContent(tab);
    if (tab === "songs") loadContent("music");
    if (tab === "posts") loadPosts();
  }, [tab, username]);

  function toggleLike(id) {
    if (!currentUser) return;
    // Optimistic update
    var wasLiked = likes[id] || false;
    setLikes(function(prev) { var n = Object.assign({}, prev); n[id] = !wasLiked; return n; });
    setItems(function(prev) { return prev.map(function(it) {
      return it.id === id ? Object.assign({}, it, { likeCount: it.likeCount + (!wasLiked ? 1 : -1) }) : it;
    }); });
    apiFetch("/api/content/" + id + "/like", { method: "POST" })
      .then(function(r) {
        setLikes(function(prev) { var n = Object.assign({}, prev); n[id] = r.liked; return n; });
      })
      .catch(function() {
        // Revert on error
        setLikes(function(prev) { var n = Object.assign({}, prev); n[id] = wasLiked; return n; });
      });
  }

  function loadComments(id) {
    apiFetch("/api/content/" + id + "/comments")
      .then(function(data) {
        setComments(function(prev) { var n = Object.assign({}, prev); n[id] = data || []; return n; });
      })
      .catch(function() {});
  }

  function submitComment(contentId, text, parentId, onSuccess, onError) {
    apiFetch("/api/content/" + contentId + "/comments", {
      method: "POST",
      body: JSON.stringify({ text: text, parentId: parentId || null })
    }).then(function(newComment) {
      setComments(function(prev) {
        var n = Object.assign({}, prev);
        n[contentId] = [...(n[contentId] || []), newComment];
        return n;
      });
      setItems(function(prev) { return prev.map(function(it) {
        return it.id === contentId ? Object.assign({}, it, { commentCount: it.commentCount + 1 }) : it;
      }); });
      if (onSuccess) onSuccess();
    }).catch(function() { if (onError) onError(); });
  }

  function deleteComment(contentId, commentId) {
    apiFetch("/api/content/" + contentId + "/comments/" + commentId, { method: "DELETE" })
      .then(function() {
        setComments(function(prev) {
          var n = Object.assign({}, prev);
          n[contentId] = (n[contentId] || []).filter(function(c) { return c.id !== commentId; });
          return n;
        });
        setItems(function(prev) { return prev.map(function(it) {
          return it.id === contentId ? Object.assign({}, it, { commentCount: Math.max(0, it.commentCount - 1) }) : it;
        }); });
      }).catch(function() {});
  }

  var isOwner = currentUser && username &&
    currentUser.username && currentUser.username.toLowerCase() === username.toLowerCase();

  function deletePost(id) {
    if (!window.confirm("Delete this post? This cannot be undone.")) return;
    apiFetch("/api/posts/" + id, { method: "DELETE" })
      .then(function() {
        setPosts(function(prev) { return prev.filter(function(p) { return p.id !== id; }); });
        dispatchContentDeleted(id, "post");
      })
      .catch(function() { alert("Failed to delete post."); });
  }

  function deleteContent(id) {
    if (!window.confirm("Delete this? This cannot be undone.")) return;
    apiFetch("/api/content/" + id, { method: "DELETE" })
      .then(function() {
        setItems(function(prev) { return prev.filter(function(it) { return it.id !== id; }); });
        dispatchContentDeleted(id, "content");
      })
      .catch(function() { alert("Failed to delete."); });
  }

  // Listen for deletions from the profile dashboard and remove from local state
  useContentDeletedListener(React.useCallback(function(detail) {
    if (detail.kind === "music" || detail.kind === "video" || detail.kind === "content") {
      setItems(function(prev) { return prev.filter(function(it) { return it.id !== detail.id; }); });
    }
    if (detail.kind === "post") {
      setPosts(function(prev) { return prev.filter(function(p) { return p.id !== detail.id; }); });
    }
  }, []));

  // Render bottom sheet at ContentTabs level
  function SheetOverlay() {
    if (!openSheet) return null;
    var sheetItem = items.find(function(it) { return it.id === openSheet; });
    if (!sheetItem) return null;
    return (
      <CommentsBottomSheet
        contentId={openSheet}
        itemComments={comments[openSheet] || []}
        currentUser={currentUser}
        onSubmit={submitComment}
        onDelete={deleteComment}
        onClose={function() { setOpenSheet(null); }}
      />
    );
  }

  var isArtistProfile = profile && (profile.plan === "artist") && profile.plan !== "producer";
  var isProducerProfile = profile && profile.plan === "producer";

  var tabs = isArtistProfile
    ? [
        { id: "posts",  label: "Posts"  },
        { id: "songs",  label: "Tracks" },
        { id: "music",  label: "Spotify Links" },
        { id: "video",  label: "Videos" },
      ]
    : [
        { id: "posts",  label: "Posts"  },
        { id: "beats",  label: "Beats"  },
        { id: "songs",  label: "Tracks" },
        { id: "music",  label: "Spotify Links" },
        { id: "video",  label: "Videos" },
      ];

  // Render comment thread for a content item
  function ContentCard({ item }) {
    var isLiked = likes[item.id] || false;
    var itemComments = comments[item.id] || [];
    return (
      <div style={{ background: "#111", borderRadius: 16, border: "1px solid #1e1e1e", marginBottom: 16, overflow: "hidden" }}>

        {/* Uploaded song — audio player with artwork + scrub bar */}
        {item.type === "music" && item.audioUrl && (
          <SongPlayer src={item.audioUrl} title={item.title} artist={item.artist} artwork={item.songArtwork} />
        )}

        {/* Spotify embed */}
        {item.type === "music" && !item.audioUrl && item.embedUrl && (
          <SpotifyEmbed
            embedUrl={item.embedUrl}
            itemId={item.id}
            height={152}
            style={{ display: "block", borderRadius: "16px 16px 0 0" }}
          />
        )}

        {/* Video player */}
        {item.type === "video" && item.youtubeId && (
          <div style={{ position: "relative", width: "100%", paddingTop: "56.25%", background: "#000" }}>
            <iframe src={"https://www.youtube.com/embed/" + item.youtubeId} title={item.caption || "video"} frameBorder="0" allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture" allowFullScreen style={{ position: "absolute", top: 0, left: 0, width: "100%", height: "100%" }} />
          </div>
        )}
        {item.type === "video" && !item.youtubeId && item.videoUrl && (
          <video controls style={{ width: "100%", maxHeight: 400, background: "#000", display: "block" }}
            src={item.videoUrl} />
        )}

        <div style={{ padding: "12px 16px" }}>
          {item.caption && (
            <div style={{ color: "#ccc", fontSize: 13, marginBottom: 10, lineHeight: 1.5 }}>{item.caption}</div>
          )}

          {/* Like + Open in Spotify row */}
          {isOwner && (
            <div style={{ display: "flex", justifyContent: "flex-end", marginBottom: 4 }}>
              <div style={{ position: "relative" }}>
                <button onClick={function(e) { e.stopPropagation(); setOpenMenu(openMenu === item.id ? null : item.id); }}
                  style={{ background: "none", border: "none", color: "#555", cursor: "pointer",
                    fontSize: 20, padding: "2px 6px", lineHeight: 1, borderRadius: 6 }}>
                  •••
                </button>
                {openMenu === item.id && (
                  <div style={{ position: "absolute", right: 0, top: "100%", zIndex: 100,
                    background: "#1a1a1a", border: "1px solid #2a2a2a", borderRadius: 10,
                    minWidth: 120, boxShadow: "0 4px 20px rgba(0,0,0,0.6)", overflow: "hidden" }}>
                    <button onClick={function(e) { e.stopPropagation(); setOpenMenu(null); deleteContent(item.id); }}
                      style={{ width: "100%", background: "none", border: "none", padding: "12px 16px",
                        color: "#F87171", fontSize: 13, fontWeight: 700, cursor: "pointer",
                        textAlign: "left", display: "flex", alignItems: "center", gap: 8 }}>
                      <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="#F87171" strokeWidth="2" strokeLinecap="round">
                        <polyline points="3 6 5 6 21 6"/><path d="M19 6l-1 14H6L5 6"/><path d="M10 11v6M14 11v6"/><path d="M9 6V4h6v2"/>
                      </svg>
                      Delete
                    </button>
                  </div>
                )}
              </div>
            </div>
          )}
          <div style={{ display: "flex", alignItems: "center", gap: 16, marginBottom: 14 }}>
            <button onClick={function() { toggleLike(item.id); }}
              style={{ background: "none", border: "none", cursor: currentUser ? "pointer" : "default", padding: 0,
                display: "flex", alignItems: "center", gap: 6,
                color: isLiked ? "#EF4444" : "#555" }}>
              <svg width="18" height="18" viewBox="0 0 24 24" fill={isLiked ? "#EF4444" : "none"}
                stroke={isLiked ? "#EF4444" : "#555"} strokeWidth="2" strokeLinecap="round">
                <path d="M20.84 4.61a5.5 5.5 0 0 0-7.78 0L12 5.67l-1.06-1.06a5.5 5.5 0 0 0-7.78 7.78l1.06 1.06L12 21.23l7.78-7.78 1.06-1.06a5.5 5.5 0 0 0 0-7.78z"/>
              </svg>
              <span style={{ fontSize: 13, fontWeight: 600 }}>{item.likeCount || 0}</span>
            </button>

            <button onClick={function() { setOpenSheet(item.id); loadComments(item.id); }}
              style={{ background: "none", border: "none", cursor: "pointer", padding: 0,
                display: "flex", alignItems: "center", gap: 6, color: "#555" }}>
              <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="#555" strokeWidth="2" strokeLinecap="round">
                <path d="M21 15a2 2 0 0 1-2 2H7l-4 4V5a2 2 0 0 1 2-2h14a2 2 0 0 1 2 2z"/>
              </svg>
              <span style={{ fontSize: 13, fontWeight: 600, color: "#555" }}>{item.commentCount || 0}</span>
            </button>

            {item.type === "music" && item.spotifyUrl && (
              <button onClick={function() { openExternalLink(item.spotifyUrl); }}
                style={{ marginLeft: "auto", background: "#1DB954", border: "none", borderRadius: 20,
                  color: "white", fontWeight: 700, fontSize: 12, padding: "6px 14px", cursor: "pointer",
                  display: "flex", alignItems: "center", gap: 6 }}>
                <svg width="14" height="14" viewBox="0 0 24 24" fill="white">
                  <path d="M12 0C5.4 0 0 5.4 0 12s5.4 12 12 12 12-5.4 12-12S18.66 0 12 0zm5.521 17.34c-.24.359-.66.48-1.021.24-2.82-1.74-6.36-2.101-10.561-1.141-.418.122-.779-.179-.899-.539-.12-.421.18-.78.54-.9 4.56-1.021 8.52-.6 11.64 1.32.42.18.479.659.301 1.02zm1.44-3.3c-.301.42-.841.6-1.262.3-3.239-1.98-8.159-2.58-11.939-1.38-.479.12-1.02-.12-1.14-.6-.12-.48.12-1.021.6-1.141C9.6 9.9 15 10.561 18.72 12.84c.361.181.54.78.241 1.2zm.12-3.36C15.24 8.4 8.82 8.16 5.16 9.301c-.6.179-1.2-.181-1.38-.721-.18-.601.18-1.2.72-1.381 4.26-1.26 11.28-1.02 15.721 1.621.539.3.719 1.02.419 1.56-.299.421-1.02.599-1.559.3z"/>
                </svg>
                Open in Spotify
              </button>
            )}
          </div>


        </div>
      </div>
    );
  }

  return (
    <div style={{ marginTop: 8 }} onClick={function() { if (openMenu) setOpenMenu(null); }}>
      {lightbox && (
        <ImageLightbox
          images={lightbox.images}
          startIndex={lightbox.index}
          onClose={function() { setLightbox(null); }}
        />
      )}
      <SheetOverlay />
      {openPostSheet && (
        <CommentsBottomSheet
          contentId={openPostSheet}
          itemComments={postComments[openPostSheet] || []}
          currentUser={currentUser}
          onSubmit={submitPostComment}
          onDelete={deletePostComment}
          onClose={function() { setOpenPostSheet(null); }}
        />
      )}
      {/* Tab bar */}
      <div style={{ display: "flex", borderBottom: "1px solid #1a1a1a", marginBottom: 16 }}>
        {tabs.map(function(t) {
          return (
            <button key={t.id} onClick={function() { setTab(t.id); }}
              style={{ flex: 1, padding: "12px 0", background: "none", border: "none",
                borderBottom: tab === t.id ? "2px solid #C026D3" : "2px solid transparent",
                color: tab === t.id ? "white" : "#555", fontWeight: 700, fontSize: 13,
                cursor: "pointer", transition: "all 0.15s" }}>
              {t.label}
            </button>
          );
        })}
      </div>

      {/* Beats tab — producers only, split into Free and Licensed */}
      {tab === "beats" && (
        React.createElement(BeatsBoundary, null,
          React.createElement(BeatsTabContent, { profile: profile, currentUser: currentUser, onViewProfile: onViewProfile })
        )
      )}

      {/* Tracks tab — artists only */}
      {tab === "tracks" && (
        <div style={{ padding: "0 16px" }}>
          {/* Upload button — only shown to the profile owner */}
          {isOwnerArtist && (
            <button onClick={function(){ setShowTrackUpload(true); }} style={{
              width: "100%", background: "linear-gradient(135deg,#F59E0B,#D97706)",
              border: "none", borderRadius: 14, color: "white", fontWeight: 800,
              fontSize: 15, padding: "14px", cursor: "pointer", marginBottom: 16,
              display: "flex", alignItems: "center", justifyContent: "center", gap: 8,
            }}>
              <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="white" strokeWidth="2.5" strokeLinecap="round"><path d="M12 5v14M5 12l7-7 7 7"/></svg>
              Upload Track
            </button>
          )}

          {/* Track upload modal */}
          {showTrackUpload && (
            <TrackUploadModal
              currentUser={currentUser}
              onClose={function(){ setShowTrackUpload(false); }}
              onUploaded={function(){ setShowTrackUpload(false); loadTracks(); }}
            />
          )}

          {tracksLoading ? (
            <div style={{ textAlign: "center", padding: "40px 0", color: "#555" }}>Loading tracks...</div>
          ) : tracks.length === 0 ? (
            <div style={{ textAlign: "center", padding: "40px 24px", color: "#555" }}>
              <svg width="40" height="40" viewBox="0 0 24 24" fill="none" stroke="#333" strokeWidth="1.5" strokeLinecap="round"><path d="M9 18V5l12-2v13"/><circle cx="6" cy="18" r="3"/><circle cx="18" cy="16" r="3"/></svg>
              <div style={{ fontSize: 15, marginTop: 12, color: "#444", fontWeight: 700 }}>No tracks yet</div>
              {isOwnerArtist && <div style={{ color: "#444", fontSize: 13, marginTop: 6 }}>Upload your first track above</div>}
            </div>
          ) : (
            tracks.map(function(track) {
              return (
                <ArtistTrackCard
                  key={track.id}
                  track={track}
                  isOwner={isOwnerArtist}
                  onDeleted={function(){ loadTracks(); }}
                  onViewProfile={onViewProfile}
                />
              );
            })
          )}
        </div>
      )}

      {/* Music / Video tabs */}
      {tab === "posts" && (
        <div style={{ padding: "0 16px" }}>
          {loading ? (
            <div style={{ textAlign: "center", padding: "40px 0", color: "#555" }}>Loading...</div>
          ) : posts.length > 0 ? (
            posts.map(function(wrapper) {
              var _s = wrapper.original_post || wrapper;
              if (_s && _s.type === "video" && !_s.youtubeId && !_s.videoUrl) return null;
              // Reposts on profile: the doc itself is a thin wrapper, the
              // displayed content (text/images/embed/etc.) comes from the
              // original post the user reposted. The wrapper still owns
              // the timestamp ("when did you repost it") and the menu
              // actions (delete repost). The "Reposted" chip is rendered
              // above the original poster's identity. Normal posts: no
              // original_post, display === wrapper.
              var isRepost = !!(wrapper.repost_of && wrapper.original_post);
              var post = isRepost ? Object.assign({}, wrapper.original_post, {
                // Keep id as the original's id so like/comment buttons
                // target the underlying post, not the repost wrapper.
                id: wrapper.original_post.id,
              }) : wrapper;
              return (
                <div key={wrapper.id} style={{ background: "#111", borderRadius: 16, border: "1px solid #1e1e1e", marginBottom: 14, overflow: "hidden" }}>
                  {/* Reposted-by chip — only shown for reposts */}
                  {isRepost && (
                    <div style={{
                      padding: "10px 14px 0", display: "flex", alignItems: "center",
                      gap: 6, color: "#A78BFA", fontSize: 11, fontWeight: 700,
                    }}>
                      <svg width="13" height="13" viewBox="0 0 24 24" fill="none"
                        stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round">
                        <polyline points="17 1 21 5 17 9"/><path d="M3 11V9a4 4 0 014-4h14"/>
                        <polyline points="7 23 3 19 7 15"/><path d="M21 13v2a4 4 0 01-4 4H3"/>
                      </svg>
                      <span
                        onClick={function(e) {
                          e.stopPropagation();
                          if (onViewProfile && wrapper.username) onViewProfile(wrapper.username);
                        }}
                        style={{ cursor: onViewProfile && wrapper.username ? "pointer" : "default" }}>
                        Reposted by @{wrapper.username}
                      </span>
                    </div>
                  )}
                  {/* Post header */}
                  <div style={{ padding: "12px 14px 8px", display: "flex", alignItems: "center", gap: 10 }}>
                    <div
                      onClick={function(e) {
                        e.stopPropagation();
                        if (onViewProfile && post.username) onViewProfile(post.username);
                      }}
                      style={{ width: 36, height: 36, borderRadius: "50%", flexShrink: 0,
                        background: "linear-gradient(135deg,#6B21A8,#C026D3)", overflow: "hidden",
                        display: "flex", alignItems: "center", justifyContent: "center",
                        color: "white", fontWeight: 800, fontSize: 14,
                        cursor: onViewProfile && post.username ? "pointer" : "default" }}>
                      {post.avatarUrl
                        ? <img src={post.avatarUrl} alt={post.username} style={{ width: "100%", height: "100%", objectFit: "cover" }} />
                        : (post.username || "?")[0].toUpperCase()}
                    </div>
                    <div style={{ flex: 1, minWidth: 0 }}>
                      <div style={{ color: "white", fontWeight: 700, fontSize: 14,
                        display: "flex", alignItems: "center", gap: 5,
                        overflow: "hidden", whiteSpace: "nowrap" }}>
                        <span
                          onClick={function(e) {
                            e.stopPropagation();
                            if (onViewProfile && post.username) onViewProfile(post.username);
                          }}
                          style={{
                            overflow: "hidden", textOverflow: "ellipsis", minWidth: 0,
                            cursor: onViewProfile && post.username ? "pointer" : "default",
                          }}>@{post.username}</span>
                        <UserBadges username={post.username} plan={post.plan} compact={true} />
                      </div>
                      <div style={{ color: "#666", fontSize: 11, marginTop: 1 }}>{bfTimeAgo(wrapper.createdAt)}</div>
                    </div>
                    {isOwner && (
                      <div style={{ position: "relative", flexShrink: 0 }}>
                        <button onClick={function(e) { e.stopPropagation(); setOpenMenu(openMenu === wrapper.id ? null : wrapper.id); }}
                          style={{ background: "none", border: "none", color: "#555", cursor: "pointer",
                            fontSize: 20, padding: "2px 6px", lineHeight: 1, borderRadius: 6 }}>
                          •••
                        </button>
                        {openMenu === wrapper.id && (
                          <div style={{ position: "absolute", right: 0, top: "100%", zIndex: 100,
                            background: "#1a1a1a", border: "1px solid #2a2a2a", borderRadius: 10,
                            minWidth: 120, boxShadow: "0 4px 20px rgba(0,0,0,0.6)", overflow: "hidden" }}>
                            <button onClick={function(e) { e.stopPropagation(); setOpenMenu(null); deletePost(wrapper.id); }}
                              style={{ width: "100%", background: "none", border: "none", padding: "12px 16px",
                                color: "#F87171", fontSize: 13, fontWeight: 700, cursor: "pointer",
                                textAlign: "left", display: "flex", alignItems: "center", gap: 8 }}>
                              <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="#F87171" strokeWidth="2" strokeLinecap="round">
                                <polyline points="3 6 5 6 21 6"/><path d="M19 6l-1 14H6L5 6"/><path d="M10 11v6M14 11v6"/><path d="M9 6V4h6v2"/>
                              </svg>
                              {isRepost ? "Remove Repost" : "Delete Post"}
                            </button>
                          </div>
                        )}
                      </div>
                    )}
                  </div>

                  {/* Status text — URLs autolinked + underlined */}
                  {post.text && (
                    <div style={{ padding: "0 14px 10px", color: "#ddd", fontSize: 14, lineHeight: 1.6,
                      whiteSpace: "pre-wrap", wordBreak: "break-word" }}>
                      {renderTextWithLinks(post.text)}
                    </div>
                  )}

                  {/* Link preview card — same as Feed */}
                  <LinkPreviewCard post={post} />

                  {/* Images — tap to expand */}
                  {post.images && post.images.length > 0 && (
                    <div style={{ display: "grid", gap: 2,
                      gridTemplateColumns: post.images.length === 1 ? "1fr" : post.images.length === 2 ? "1fr 1fr" : "1fr 1fr 1fr" }}>
                      {post.images.map(function(img, i) {
                        return (
                          <div key={i} style={{ position: "relative", cursor: "pointer", overflow: "hidden" }}
                            onClick={function() { setLightbox({ images: post.images, index: i }); }}>
                            <img src={img} alt="post"
                              style={{ width: "100%", aspectRatio: post.images.length === 1 ? "16/9" : "1",
                                objectFit: "cover", display: "block", transition: "transform 0.15s" }} />
                            {/* Expand hint icon on single image */}
                            {post.images.length === 1 && (
                              <div style={{ position: "absolute", bottom: 8, right: 8,
                                background: "rgba(0,0,0,0.5)", borderRadius: 6, padding: "3px 6px",
                                display: "flex", alignItems: "center", gap: 4 }}>
                                <svg width="12" height="12" viewBox="0 0 24 24" fill="none" stroke="white" strokeWidth="2.5" strokeLinecap="round">
                                  <polyline points="15 3 21 3 21 9"/><polyline points="9 21 3 21 3 15"/>
                                  <line x1="21" y1="3" x2="14" y2="10"/><line x1="3" y1="21" x2="10" y2="14"/>
                                </svg>
                              </div>
                            )}
                          </div>
                        );
                      })}
                    </div>
                  )}

                  {/* Spotify embed */}
                  {post.type === "music" && post.embedUrl && (
                    <SpotifyEmbed
                      embedUrl={post.embedUrl}
                      itemId={post.id}
                      height={152}
                      style={{ display: "block" }}
                    />
                  )}

                  {/* Video */}
                  {post.type === "video" && post.youtubeId && (
                    <div style={{ position: "relative", width: "100%", paddingTop: "56.25%", background: "#000" }}>
                      <iframe src={"https://www.youtube.com/embed/" + post.youtubeId} title={post.caption || "video"} frameBorder="0" allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture" allowFullScreen style={{ position: "absolute", top: 0, left: 0, width: "100%", height: "100%" }} />
                    </div>
                  )}
                  {post.type === "video" && !post.youtubeId && post.videoUrl && (
                    <video controls src={post.videoUrl}
                      style={{ width: "100%", maxHeight: 360, background: "#000", display: "block" }} />
                  )}

                  {/* Caption */}
                  {post.caption && (
                    <div style={{ padding: "8px 14px 4px", color: "#aaa", fontSize: 13 }}>{post.caption}</div>
                  )}

                  {/* Like + comment + repost */}
                  <div style={{ padding: "8px 14px 12px", display: "flex", gap: 16, alignItems: "center" }}>
                    <button onClick={function() { togglePostLike(post.id); }}
                      style={{ background: "none", border: "none", cursor: currentUser ? "pointer" : "default",
                        padding: 0, display: "flex", alignItems: "center", gap: 5,
                        color: postLikes[post.id] ? "#EF4444" : "#555" }}>
                      <svg width="17" height="17" viewBox="0 0 24 24"
                        fill={postLikes[post.id] ? "#EF4444" : "none"}
                        stroke={postLikes[post.id] ? "#EF4444" : "#555"} strokeWidth="2" strokeLinecap="round">
                        <path d="M20.84 4.61a5.5 5.5 0 0 0-7.78 0L12 5.67l-1.06-1.06a5.5 5.5 0 0 0-7.78 7.78l1.06 1.06L12 21.23l7.78-7.78 1.06-1.06a5.5 5.5 0 0 0 0-7.78z"/>
                      </svg>
                      <span style={{ fontSize: 13 }}>{post.likeCount || 0}</span>
                    </button>
                    <button onClick={function() { setOpenPostSheet(post.id); loadPostComments(post.id); }}
                      style={{ background: "none", border: "none", cursor: "pointer", padding: 0,
                        display: "flex", alignItems: "center", gap: 5, color: "#555" }}>
                      <svg width="15" height="15" viewBox="0 0 24 24" fill="none" stroke="#555" strokeWidth="2" strokeLinecap="round">
                        <path d="M21 15a2 2 0 0 1-2 2H7l-4 4V5a2 2 0 0 1 2-2h14a2 2 0 0 1 2 2z"/>
                      </svg>
                      <span style={{ fontSize: 13 }}>{post.commentCount || 0}</span>
                    </button>
                    {/* Repost button — hidden on the user's own posts
                        (can't repost yourself). Mirrors the Feed's
                        behaviour, including the active-state green
                        colour for consistency. */}
                    {currentUser && currentUser.username !== post.username && (
                      <button onClick={function() { togglePostRepost(post.id); }}
                        style={{ background: "none", border: "none", cursor: "pointer", padding: 0,
                          display: "flex", alignItems: "center", gap: 5,
                          color: postReposts[post.id] ? "#10B981" : "#555" }}>
                        <svg width="15" height="15" viewBox="0 0 24 24" fill="none"
                          stroke={postReposts[post.id] ? "#10B981" : "#555"} strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round">
                          <polyline points="17 1 21 5 17 9"/><path d="M3 11V9a4 4 0 014-4h14"/>
                          <polyline points="7 23 3 19 7 15"/><path d="M21 13v2a4 4 0 01-4 4H3"/>
                        </svg>
                        <span style={{ fontSize: 13 }}>{post.repostCount || 0}</span>
                      </button>
                    )}
                    {post.type === "music" && post.spotifyUrl && (
                      <button onClick={function() { openExternalLink(post.spotifyUrl); }}
                        style={{ marginLeft: "auto", background: "#1DB954", border: "none", borderRadius: 20,
                          color: "white", fontWeight: 700, fontSize: 11, padding: "5px 12px", cursor: "pointer" }}>
                        Open in Spotify
                      </button>
                    )}
                  </div>


                </div>
              );
            })
          ) : (
            <div style={{ textAlign: "center", padding: "40px 24px" }}>
              <svg width="40" height="40" viewBox="0 0 24 24" fill="none" stroke="#444" strokeWidth="1.5" strokeLinecap="round" style={{ marginBottom: 12 }}>
                <path d="M21 15a2 2 0 0 1-2 2H7l-4 4V5a2 2 0 0 1 2-2h14a2 2 0 0 1 2 2z"/>
              </svg>
              <div style={{ fontSize: 15, color: "#444", fontWeight: 700 }}>No posts yet</div>
            </div>
          )}
        </div>
      )}

      {(tab === "music" || tab === "video" || tab === "songs") && (
        <div style={{ padding: "0 16px" }}>
          {(function(){
            var shown = items;
            if (tab === "music")  shown = items.filter(function(it){ return !it.audioUrl; });   // Spotify links only
            if (tab === "songs")  shown = items.filter(function(it){ return !!it.audioUrl; });   // uploaded songs only
            if (loading) return <div style={{ textAlign: "center", padding: "40px 0", color: "#555" }}>Loading...</div>;
            if (shown.length > 0) return shown.map(function(item) { return <ContentCard key={item.id} item={item} />; });
            return (
              <div style={{ textAlign: "center", padding: "40px 24px", color: "#555" }}>
                {tab === "video" ? (
                  <svg width="40" height="40" viewBox="0 0 24 24" fill="none" stroke="#444" strokeWidth="1.5" strokeLinecap="round" style={{ marginBottom: 12 }}>
                    <rect x="2" y="2" width="20" height="20" rx="3"/><polygon points="10,8 16,12 10,16"/>
                  </svg>
                ) : (
                  <svg width="40" height="40" viewBox="0 0 24 24" fill="none" stroke="#444" strokeWidth="1.5" strokeLinecap="round" style={{ marginBottom: 12 }}>
                    <path d="M9 18V5l12-2v13"/><circle cx="6" cy="18" r="3"/><circle cx="18" cy="16" r="3"/>
                  </svg>
                )}
                <div style={{ fontSize: 15, color: "#444", fontWeight: 700 }}>
                  {tab === "video" ? "No videos posted yet" : tab === "songs" ? "No tracks uploaded yet" : "No Spotify links yet"}
                </div>
              </div>
            );
          })()}
        </div>
      )}
    </div>
  );
}


// ── Format large numbers: 1200 → "1.2K", 1050000 → "1.1M" ───────
function fmtCount(n) {
  if (!n || n < 1) return "0";
  if (n >= 1000000) return (n / 1000000).toFixed(1).replace(/\.0$/, "") + "M";
  if (n >= 1000)    return (n / 1000).toFixed(1).replace(/\.0$/, "") + "K";
  return String(n);
}

// ── Followers / Following list screen ────────────────────────────
function FollowListScreen({ username, mode, onBack, onViewProfile, currentUser }) {
  // mode: "followers" | "following"
  const [users,       setUsers]       = React.useState([]);
  const [loading,     setLoading]     = React.useState(true);
  const [error,       setError]       = React.useState(null);
  const [viewingUser, setViewingUser] = React.useState(null);
  // Map of username → following bool for the logged-in user
  const [followMap,   setFollowMap]   = React.useState({});
  const [followLoading, setFollowLoading] = React.useState({});

  React.useEffect(() => {
    setLoading(true);
    apiFetch("/api/auth/" + mode + "/" + encodeURIComponent(username))
      .then(d => {
        setUsers(d || []);
        setLoading(false);
        // Pre-load follow state for each user if logged in
        if (currentUser && (d || []).length > 0) {
          apiFetch("/api/auth/following/" + encodeURIComponent(currentUser.username))
            .then(following => {
              const map = {};
              (following || []).forEach(f => { map[f.username] = true; });
              setFollowMap(map);
            })
            .catch(() => {});
        }
      })
      .catch(e => { setError(e.message); setLoading(false); });
  }, [username, mode]);

  function toggleFollow(e, targetUsername) {
    e.stopPropagation();
    if (!currentUser) return;
    const isFollowing = !!followMap[targetUsername];
    setFollowLoading(prev => ({ ...prev, [targetUsername]: true }));
    apiFetch("/api/auth/follow/" + encodeURIComponent(targetUsername), {
      method: isFollowing ? "DELETE" : "POST",
    })
      .then(() => {
        setFollowMap(prev => ({ ...prev, [targetUsername]: !isFollowing }));
        setFollowLoading(prev => ({ ...prev, [targetUsername]: false }));
      })
      .catch(() => {
        setFollowLoading(prev => ({ ...prev, [targetUsername]: false }));
      });
  }

  // If a user was tapped, show their full public profile
  if (viewingUser) {
    return (
      <div style={{ position:"fixed", inset:0, zIndex:6000, background:"#0a0a0a", overflowY:"auto", WebkitOverflowScrolling:"touch" }}
        onTouchMove={function(e){ e.stopPropagation(); }}>
        <PublicProfileScreen
          username={viewingUser}
          onBack={() => setViewingUser(null)}
          onPlay={function(){}}
          savedIds={new Set()}
          onSave={function(){}}
          currentUser={currentUser}
          hideBack={false}
        />
      </div>
    );
  }

  return (
    <div style={{ background:"#0a0a0a", minHeight:"100%", color:"white", fontFamily:"inherit" }}>
      {/* Header — padded below iPhone status bar */}
      <div style={{
        display:"flex", alignItems:"center",
        padding:"calc(var(--bf-safe-top, env(safe-area-inset-top)) + 12px) 16px 12px",
        borderBottom:"1px solid #1a1a1a", position:"sticky", top:0, background:"#0a0a0a", zIndex:10,
      }}>
        <button onClick={onBack} style={{ background:"none", border:"none", color:"white", fontSize:24, cursor:"pointer", padding:"4px 12px 4px 0", marginRight:4, lineHeight:1 }}>‹</button>
        <span style={{ fontWeight:800, fontSize:17 }}>{mode === "followers" ? "Followers" : "Following"}</span>
      </div>

      {loading && <div style={{ textAlign:"center", padding:40, color:"#555" }}>Loading...</div>}
      {error   && <div style={{ textAlign:"center", padding:40, color:"#e44" }}>{error}</div>}
      {!loading && !error && users.length === 0 && (
        <div style={{ textAlign:"center", padding:40, color:"#555" }}>
          {mode === "followers" ? "No followers yet" : "Not following anyone yet"}
        </div>
      )}

      {users.map(u => {
        const isOwnAccount = currentUser && currentUser.username === u.username;
        const isFollowing  = !!followMap[u.username];
        const isLoading    = !!followLoading[u.username];
        return (
          <div key={u.username} onClick={() => setViewingUser(u.username)}
            style={{ display:"flex", alignItems:"center", padding:"12px 16px", borderBottom:"1px solid #111", cursor:"pointer", gap:12 }}>
            {/* Avatar */}
            <div style={{ width:48, height:48, borderRadius:"50%", overflow:"hidden", flexShrink:0,
              background:"linear-gradient(135deg,#6B21A8,#C026D3)",
              display:"flex", alignItems:"center", justifyContent:"center" }}>
              {u.avatarUrl
                ? <img src={u.avatarUrl} alt="" style={{ width:"100%", height:"100%", objectFit:"cover" }} />
                : <span style={{ color:"white", fontWeight:800, fontSize:18 }}>{(u.username || "?")[0].toUpperCase()}</span>
              }
            </div>
            {/* Name + username */}
            <div style={{ flex:1, minWidth:0 }}>
              <div style={{ display:"flex", alignItems:"center", gap:6, minWidth:0 }}>
                <span style={{ fontWeight:700, fontSize:14, color:"white", whiteSpace:"nowrap", overflow:"hidden", textOverflow:"ellipsis" }}>
                  {u.name || u.username}
                </span>
                {/* Real badges — Pro tick + any lifetime seals (Trelli's
                    gold/CEO, Mikez+HMbarsdat's green/B.A). Compact mode
                    skips the textual chips and just shows the icons so
                    the row stays single-line. */}
                <UserBadges username={u.username} plan={u.plan} compact size={13} />
              </div>
              <div style={{ color:"#555", fontSize:12, marginTop:1 }}>@{u.username}</div>
            </div>
            {/* Follow / Unfollow button — hidden for own account */}
            {currentUser && !isOwnAccount && (
              <button
                onClick={(e) => toggleFollow(e, u.username)}
                disabled={isLoading}
                style={{
                  flexShrink:0, padding:"6px 14px", borderRadius:20, fontWeight:700, fontSize:12,
                  cursor: isLoading ? "not-allowed" : "pointer", border:"1.5px solid",
                  background: isFollowing ? "transparent" : "#C026D3",
                  borderColor: isFollowing ? "#444" : "#C026D3",
                  color: isFollowing ? "#777" : "white",
                  opacity: isLoading ? 0.5 : 1,
                  transition:"all 0.15s",
                }}>
                {isLoading ? "..." : isFollowing ? "Following" : "Follow"}
              </button>
            )}
            <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="#333" strokeWidth="2.5" strokeLinecap="round" style={{flexShrink:0}}><polyline points="9 18 15 12 9 6"/></svg>
          </div>
        );
      })}
    </div>
  );
}

function PublicProfileScreen({ username, onBack, onPlay, savedIds, onSave, currentUser, onMessage, hideBack }) {
  const [profile,      setProfile]      = useState(null);
  const [avatarZoom,   setAvatarZoom]   = useState(false);
  const [loading,      setLoading]      = useState(true);
  const [error,        setError]        = useState(null);
  const [following,    setFollowing]    = useState(false);
  // Does THIS profile follow the current user back? Set from the
  // profile-auth response (server adds isFollowedBy). Used together
  // with `following` to gate the Message button: DMs require BOTH
  // directions of a follow ("mutual follow only").
  const [followedBy,   setFollowedBy]   = useState(false);
  const [followLoading,setFollowLoading]= useState(false);
  const [badgePopup,   setBadgePopup]   = useState(null);
  const [followList,   setFollowList]   = useState(null);
  const [nestedProfile,setNestedProfile]= useState(null);
  // Tick every 10s so post-card timestamps update live ("5s ago" →
  // "15s ago" → "25s ago"), matching the Feed's behaviour.
  useLiveTick(10000);
  // Block-state for this profile, loaded once when profile mounts.
  // iBlockedThem  → the current user has blocked this profile
  // theyBlockedMe → this profile has blocked the current user (we hide content)
  const [iBlockedThem,  setIBlockedThem]  = useState(false);
  const [theyBlockedMe, setTheyBlockedMe] = useState(false);
  const [blockLoading,  setBlockLoading]  = useState(false);
  const [overflowOpen,  setOverflowOpen]  = useState(false);
  const [featurePinned, setFeaturePinned] = useState(false);
  const [confirmBlock,  setConfirmBlock]  = useState(false);

  // Fetch block status as soon as profile loads (only for logged-in users
  // viewing someone else's profile). Failures are silently ignored — block
  // state just defaults to false, which is the safe default.
  useEffect(() => {
    if (!currentUser || !username || currentUser.username === username) return;
    apiFetch("/api/auth/block-status/" + encodeURIComponent(username))
      .then(d => {
        setIBlockedThem(!!(d && d.i_blocked_them));
        setTheyBlockedMe(!!(d && d.they_blocked_me));
      })
      .catch(() => {});
  }, [username, currentUser && currentUser.id]);

  const toggleBlock = async () => {
    if (!currentUser) return;
    setBlockLoading(true);
    try {
      await apiFetch("/api/auth/block/" + encodeURIComponent(username), {
        method: iBlockedThem ? "DELETE" : "POST",
      });
      const newState = !iBlockedThem;
      setIBlockedThem(newState);
      // Blocking also unfollows in both directions (server-side) — keep UI in sync
      if (newState) setFollowing(false);
      setOverflowOpen(false);
      setConfirmBlock(false);
    } catch(e) {
      console.warn("[Block] failed:", e.message);
    }
    setBlockLoading(false);
  };

  useEffect(() => {
    // Always use unauthenticated route — works for everyone including guests
    // profile-auth is only used when we know user is logged in (has id)
    const endpoint = (currentUser && currentUser.id)
      ? "/api/auth/profile-auth/" + encodeURIComponent(username)
      : "/api/auth/profile/" + encodeURIComponent(username);
    apiFetch(endpoint)
      .then(async (d) => {
        // The /api/auth/profile/* endpoint returns beats but historically
        // doesn't include two-tier fields (basic_lease_price, premium_lease_price,
        // premium_sold). Without these the UI falls back to legacy single-price
        // "BUY £50" rendering. So we cross-reference the public producer beats
        // endpoint and merge tier fields by beat id.
        try {
          if (d && Array.isArray(d.beats) && d.beats.length > 0) {
            const allBeats = await apiFetch("/api/producer/beats");
            const byId = {};
            (allBeats || []).forEach(function(b) { byId[b.id] = b; });
            d.beats = d.beats.map(function(b) {
              const enriched = byId[b.id];
              if (!enriched) return b;
              return Object.assign({}, b, {
                basic_lease_price:   enriched.basic_lease_price,
                premium_lease_price: enriched.premium_lease_price,
                premium_sold:        enriched.premium_sold,
                premium_sold_to:     enriched.premium_sold_to,
              });
            });
          }
        } catch(mergeErr) {
          // Non-fatal — UI just falls back to legacy single-price for beats
          // that don't have tier data baked in already.
          console.warn("[Profile] Could not enrich beats with tier prices:", mergeErr);
        }
        setProfile(d); setFollowing(d.isFollowing || false); setFollowedBy(d.isFollowedBy || false); setLoading(false);
      })
      .catch(e => { setError(e.message); setLoading(false); });
  }, [username, currentUser?.id]);

  // Update beat in-place when producer edits — no page refresh needed
  useBeatUpdatedListener(React.useCallback(function(detail) {
    // Patch in-place immediately for zero-lag update
    setProfile(function(prev) {
      if (!prev || !prev.beats) return prev;
      return Object.assign({}, prev, {
        beats: prev.beats.map(function(b) {
          return b.id === detail.beatId ? Object.assign({}, b, detail.patch) : b;
        }),
      });
    });
  }, []));

  const toggleFollow = async () => {
    if (!currentUser) return;
    setFollowLoading(true);
    try {
      await apiFetch("/api/auth/follow/" + encodeURIComponent(username), {
        method: following ? "DELETE" : "POST"
      });
      setFollowing(!following);
      setProfile(p => ({ ...p, followerCount: (p.followerCount || 0) + (following ? -1 : 1) }));
    } catch(e) {}
    setFollowLoading(false);
  };

  // Profile sharing — opens the native iOS / Android share sheet via the
  // Web Share API when available (mobile), and falls back to clipboard
  // copy on desktop. The share URL deep-links to /profile/{username} so
  // users get straight to the profile when the link is tapped.
  const [shareCopied, setShareCopied] = useState(false);
  const shareProfile = async () => {
    if (!profile || !username) return;
    var url = "https://beatfinder.co.uk/profile/" + encodeURIComponent(username);
    var displayName = profile.name || ("@" + username);
    var shareData = {
      title:   displayName + " on BeatFinder",
      text:    "Check out " + displayName + " on BeatFinder",
      url:     url,
    };
    // Try native share first — iOS Safari, Android Chrome support this
    if (navigator.share) {
      try {
        await navigator.share(shareData);
        return;
      } catch (e) {
        // User cancelled or share unavailable in this context — fall
        // through to clipboard copy. Don't swallow real errors though;
        // AbortError is the cancellation signal we expect to ignore.
        if (e && e.name !== "AbortError") {
          // Some browsers throw for non-user-cancellation reasons; in
          // that case clipboard fallback is still better than nothing.
        } else {
          return;
        }
      }
    }
    // Clipboard fallback — Web Share API absent (desktop, older mobile)
    try {
      await navigator.clipboard.writeText(url);
      setShareCopied(true);
      setTimeout(function() { setShareCopied(false); }, 2000);
    } catch (e) {
      // Final fallback: legacy execCommand. Works in older Safari /
      // when the page isn't served over HTTPS (though ours is).
      try {
        var ta = document.createElement("textarea");
        ta.value = url;
        ta.style.position = "fixed";
        ta.style.opacity = "0";
        document.body.appendChild(ta);
        ta.select();
        document.execCommand("copy");
        ta.remove();
        setShareCopied(true);
        setTimeout(function() { setShareCopied(false); }, 2000);
      } catch (_) {}
    }
  };

  if (loading) return (
    <div style={{ textAlign: "center", padding: "80px 24px", color: "#555" }}>
      <div style={{ marginBottom: 10 }}><AppIcon id="profile" size={36} /></div>
      <div style={{ fontSize: 13 }}>Loading profile...</div>
    </div>
  );

  if (error || !profile) return (
    <div style={{ textAlign: "center", padding: "80px 24px", color: "#555" }}>
      <div style={{ marginBottom: 10 }}><AppIcon id="profile" size={36} /></div>
      <div style={{ fontSize: 15, color: "#888" }}>Profile not found</div>
      <button onClick={onBack} style={{ marginTop: 20, background: "#1a1a1a", border: "1px solid #333", borderRadius: 12, color: "white", padding: "10px 24px", cursor: "pointer" }}>Go Back</button>
    </div>
  );

  const isProd = profile.plan === "producer";
  const isArtist = profile.plan === "artist" || isProd;
  const isOwnProfile = currentUser && username && currentUser.username && 
    currentUser.username.toLowerCase() === username.toLowerCase();

  // If the profile owner has blocked the current user, hide all content
  // and show a friendly empty state. This is a defence-in-depth measure:
  // backend endpoints should also enforce this, but the frontend gate
  // prevents any cached profile data from leaking through.
  if (theyBlockedMe && !isOwnProfile) {
    return (
      <div style={{ textAlign: "center", padding: "80px 24px", color: "#555" }}>
        {!hideBack && (
          <div style={{ textAlign: "left", marginBottom: 30 }}>
            <button onClick={onBack} style={{
              background: "none", border: "none", color: "#888",
              fontSize: 15, cursor: "pointer", padding: 0,
            }}>← Back</button>
          </div>
        )}
        <div style={{ marginBottom: 14 }}>
          <svg width="48" height="48" viewBox="0 0 24 24" fill="none" stroke="#444" strokeWidth="1.8" strokeLinecap="round"><circle cx="12" cy="12" r="9"/><line x1="5.6" y1="5.6" x2="18.4" y2="18.4"/></svg>
        </div>
        <div style={{ color: "white", fontSize: 17, fontWeight: 700, marginBottom: 6 }}>
          You can't view this profile
        </div>
        <div style={{ color: "#777", fontSize: 13, lineHeight: 1.6, maxWidth: 280, margin: "0 auto" }}>
          @{username} isn't available to you right now.
        </div>
      </div>
    );
  }

  const handleDownload = async (beat) => {
    await downloadMp3(beat.url, beat.title, beat.id);
  };

  return (
    <div style={{ overflowX: "hidden" }}>

      {/* ── Avatar zoom overlay — tap profile photo to enlarge ── */}
      {avatarZoom && profile && profile.avatarUrl && (
        <div onClick={function(){ setAvatarZoom(false); }}
          style={{ position: "fixed", inset: 0, zIndex: 12000, background: "rgba(0,0,0,0.92)",
            display: "flex", alignItems: "center", justifyContent: "center", padding: 24 }}>
          <img src={profile.avatarUrl} alt={profile.username}
            style={{ maxWidth: "90vw", maxHeight: "80vh", width: "auto", height: "auto",
              borderRadius: 18, objectFit: "contain", boxShadow: "0 0 50px rgba(176,38,255,0.35)" }} />
          <button onClick={function(){ setAvatarZoom(false); }}
            style={{ position: "absolute", top: "max(20px, env(safe-area-inset-top))", right: 20,
              background: "rgba(255,255,255,0.12)", border: "none", color: "white", width: 40, height: 40,
              borderRadius: 20, fontSize: 22, cursor: "pointer", lineHeight: 1 }}>&#10005;</button>
        </div>
      )}

      {/* ── Follow list overlay ── */}
      {followList && (
        <div style={{ position:"fixed", inset:0, zIndex:5000, background:"#0a0a0a", overflowY:"auto" }}>
          <FollowListScreen
            username={username}
            mode={followList}
            currentUser={currentUser}
            onBack={() => setFollowList(null)}
            onViewProfile={(u) => { setFollowList(null); setNestedProfile(u); }}
          />
        </div>
      )}

      {/* ── Nested profile overlay (producer name tapped on beat card) ── */}
      {nestedProfile && (
        <div style={{ position:"fixed", inset:0, zIndex:6000, background:"#0a0a0a", overflowY:"auto", WebkitOverflowScrolling:"touch" }}
          onTouchMove={function(e){ e.stopPropagation(); }}>
          <PublicProfileScreen
            username={nestedProfile}
            onBack={() => setNestedProfile(null)}
            onPlay={onPlay}
            savedIds={savedIds}
            onSave={onSave}
            currentUser={currentUser}
            hideBack={false}
          />
        </div>
      )}

      {/* ── Badge info popup ── */}
      <BadgeInfoPopup message={badgePopup} onClose={() => setBadgePopup(null)} />

      {profile.headerUrl ? (
        <div>
          {/* Header image — full width, starts at very top */}
          <div style={{ position: "relative", width: "100%", height: 240, overflow: "hidden" }}>
            <img src={profile.headerUrl} alt="header"
              style={{ width: "100%", height: "100%", objectFit: "cover", objectPosition: "center", display: "block" }} />
            <div style={{ position: "absolute", inset: 0, background: "linear-gradient(to bottom, rgba(0,0,0,0.05) 0%, transparent 35%, transparent 55%, rgba(10,10,10,0.7) 80%, #0a0a0a 100%)" }} />
            {onBack && !hideBack && (
              <button onClick={onBack}
                style={{ position: "absolute", top: 54, left: 16, background: "none", border: "none",
                  color: "white", fontSize: 26, cursor: "pointer", padding: 4, lineHeight: 1,
                  textShadow: "0 1px 6px rgba(0,0,0,0.9)", zIndex: 10 }}>
                &#8592;
              </button>
            )}
          </div>
          {/* Avatar overlapping header bottom */}
          <div style={{ padding: "0 16px" }}>
            <div style={{ marginTop: -46, marginBottom: 10, position: "relative", zIndex: 2, display: "inline-block" }}>
              {profile.avatarUrl ? (
                <img src={profile.avatarUrl} alt={profile.username}
                  onClick={function(){ setAvatarZoom(true); }}
                  style={{ width: 88, height: 88, borderRadius: "50%", objectFit: "cover",
                    border: "4px solid #0a0a0a", display: "block", cursor: "pointer" }} />
              ) : (
                <div style={{ width: 88, height: 88, borderRadius: "50%",
                  background: profile.avatarColor || "linear-gradient(135deg,#6B21A8,#C026D3)",
                  display: "flex", alignItems: "center", justifyContent: "center",
                  fontSize: 34, color: "white", fontWeight: 800, border: "4px solid #0a0a0a" }}>
                  {(profile.username || profile.name || "?")[0].toUpperCase()}
                </div>
              )}
              <div style={{ position: "absolute", bottom: 8, left: 64, width: 15, height: 15,
                borderRadius: "50%", background: "#22C55E", border: "3px solid #0a0a0a" }} />
            </div>
          </div>
        </div>
      ) : (
        <div>
          {/* Black placeholder header so layout matches */}
          <div style={{ position: "relative", width: "100%", height: 140, background: "#111", overflow: "hidden" }}>
            {onBack && !hideBack && (
              <button onClick={onBack}
                style={{ position: "absolute", top: 54, left: 16, background: "none", border: "none",
                  color: "white", fontSize: 26, cursor: "pointer", padding: 4, lineHeight: 1, zIndex: 10 }}>
                &#8592;
              </button>
            )}
          </div>
          <div style={{ padding: "0 16px" }}>
            <div style={{ marginTop: -46, marginBottom: 10, position: "relative", zIndex: 2, display: "inline-block" }}>
              {profile.avatarUrl ? (
                <img src={profile.avatarUrl} alt={profile.username}
                  onClick={function(){ setAvatarZoom(true); }}
                  style={{ width: 88, height: 88, borderRadius: "50%", objectFit: "cover", border: "4px solid #0a0a0a", display: "block", cursor: "pointer" }} />
              ) : (
                <div style={{ width: 88, height: 88, borderRadius: "50%",
                  background: profile.avatarColor || "linear-gradient(135deg,#6B21A8,#C026D3)",
                  display: "flex", alignItems: "center", justifyContent: "center",
                  fontSize: 34, color: "white", fontWeight: 800, border: "4px solid #0a0a0a" }}>
                  {(profile.username || profile.name || "?")[0].toUpperCase()}
                </div>
              )}
              <div style={{ position: "absolute", bottom: 8, left: 64, width: 15, height: 15,
                borderRadius: "50%", background: "#22C55E", border: "3px solid #0a0a0a" }} />
            </div>
          </div>
        </div>
      )}

      {/* ── Profile content ── */}
      <div style={{ padding: "0 16px" }}>

        {/* Name + username */}
        <div style={{ marginBottom: 6 }}>
          <div style={{ display: "flex", alignItems: "center", gap: 8 }}>
            <div style={{ color: "white", fontSize: 26, fontWeight: 800, lineHeight: 1.1 }}>
              {profile.username || profile.name}
            </div>
            {(isProd || isArtist) && <VerifiedBadge size={20} />}
            {profile.username === "Trelli" && <GoldVerifiedBadge size={22} />}
            {(profile.username === "Mikez" || profile.username === "HMbarsdat") && <GreenVerifiedBadge size={22} />}
          </div>
          {profile.username && <div style={{ color: "#666", fontSize: 14, marginTop: 2 }}>@{profile.username}</div>}
        </div>

        {/* Plan tags */}
        {(isProd || isArtist) && (
          <div style={{ display: "flex", gap: 6, marginBottom: 10, flexWrap: "wrap" }}>
            {isProd && <span onClick={() => setBadgePopup({ icon:"producer", text:"This user is currently subscribed to Producer Pro" })} style={{ background:"rgba(192,38,211,0.15)", border:"1px solid #C026D3", borderRadius:20, padding:"2px 10px", color:"#C026D3", fontWeight:700, fontSize:11, cursor:"pointer" }}>Producer Pro</span>}
            {profile.username === "Trelli" && <CEOBadge onClick={() => setBadgePopup({ icon:"ceo", text:"Verified Chief Executive Officer of BeatFinder" })} />}
            {isArtist && !isProd && <span onClick={() => setBadgePopup({ icon:"artist", text:"This user is currently subscribed to Artist Pro" })} style={{ background:"rgba(245,158,11,0.15)", border:"1px solid #F59E0B", borderRadius:20, padding:"2px 10px", color:"#F59E0B", fontWeight:700, fontSize:11, cursor:"pointer" }}>Artist Pro</span>}
            {(profile.username === "Mikez" || profile.username === "HMbarsdat") && <BrandAmbassadorBadge onClick={() => setBadgePopup({ icon:"brand_ambassador", text:"Verified Brand Ambassador of BeatFinder" })} />}
          </div>
        )}

        {/* Bio */}
        {profile.bio && (
          <div style={{ color: "#aaa", fontSize: 13, lineHeight: 1.6, marginBottom: 10 }}>{profile.bio}</div>
        )}

        {/* Stats inline */}
        <div style={{ display: "flex", gap: 16, alignItems: "center", marginBottom: isOwnProfile ? 10 : 14, flexWrap: "wrap" }}>
          {[
            { val: profile.followerCount || 0, label: "Followers", onClick: () => setFollowList("followers") },
            { val: profile.followingCount || 0, label: "Following", onClick: () => setFollowList("following") },
            isProd
              ? { val: profile.beats?.length || 0, label: "Beats", onClick: null }
              : { val: profile.trackCount || 0,    label: "Tracks", onClick: null },
            { val: profile.playCount || 0, label: "Plays", onClick: null, icon: "play" },
          ].map((s, i) => (
            <div key={s.label}
              onClick={s.onClick || undefined}
              style={{ display: "flex", alignItems: "center", gap: 4, cursor: s.onClick ? "pointer" : "default" }}>
              {i > 0 && <span style={{ color: "#333", marginRight: 4 }}>·</span>}
              {s.icon && <svg width="11" height="11" viewBox="0 0 24 24" fill="#555" style={{flexShrink:0}}><path d="M5 3.5l15 8.5-15 8.5V3.5z"/></svg>}
              <span style={{ color: s.onClick ? "white" : "white", fontWeight: 700, fontSize: 14 }}>{fmtCount(s.val)}</span>
              <span style={{ color: s.onClick ? "#aaa" : "#555", fontSize: 13, marginLeft: 2, textDecoration: s.onClick ? "underline" : "none", textDecorationColor: "#444" }}>{s.label}</span>
            </div>
          ))}
        </div>

        {/* Action buttons — only show for other users */}
        {currentUser && !isOwnProfile && (
          <div style={{ display: "flex", gap: 8, marginBottom: 10, alignItems: "center", position: "relative" }}>
            <button onClick={toggleFollow} disabled={followLoading || iBlockedThem}
              style={{ flex: 1, padding: "10px 0", borderRadius: 20, fontWeight: 800, fontSize: 14,
                cursor: (followLoading || iBlockedThem) ? "not-allowed" : "pointer",
                opacity: iBlockedThem ? 0.4 : 1,
                background: following ? "transparent" : "#C026D3",
                border: "1.5px solid " + (following ? "#444" : "#C026D3"),
                color: following ? "#777" : "white" }}>
              {followLoading ? "..." : following ? "Following" : "Follow"}
            </button>
            {onMessage && !iBlockedThem && (
              <button onClick={() => onMessage(username)}
                style={{ padding: "10px 18px", borderRadius: 20, fontWeight: 700, fontSize: 13, cursor: "pointer",
                  background: "transparent", border: "1.5px solid #333", color: "#aaa" }}>
                Message
              </button>
            )}
            {/* Share — opens native share sheet on mobile, copies link
                on desktop. Icon-only to keep the action row compact. */}
            <button onClick={shareProfile}
              title="Share profile"
              style={{
                width: 38, height: 38, borderRadius: "50%",
                background: "transparent", border: "1.5px solid #333",
                color: "#aaa", cursor: "pointer", flexShrink: 0,
                display: "flex", alignItems: "center", justifyContent: "center",
              }}>
              <svg width="15" height="15" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                <circle cx="18" cy="5" r="3"/><circle cx="6" cy="12" r="3"/><circle cx="18" cy="19" r="3"/>
                <line x1="8.59" y1="13.51" x2="15.42" y2="17.49"/>
                <line x1="15.41" y1="6.51" x2="8.59" y2="10.49"/>
              </svg>
            </button>
            {/* Overflow kebab — block/unblock and (future) report */}
            <button onClick={() => setOverflowOpen(v => !v)}
              style={{
                width: 38, height: 38, borderRadius: "50%",
                background: "transparent", border: "1.5px solid #333",
                color: "#aaa", cursor: "pointer", flexShrink: 0,
                display: "flex", alignItems: "center", justifyContent: "center",
              }}>
              <svg width="16" height="16" viewBox="0 0 24 24" fill="currentColor"><circle cx="5" cy="12" r="2"/><circle cx="12" cy="12" r="2"/><circle cx="19" cy="12" r="2"/></svg>
            </button>
            {overflowOpen && (
              <>
                <div onClick={() => setOverflowOpen(false)}
                  style={{ position: "fixed", inset: 0, zIndex: 998, background: "transparent" }} />
                <div style={{
                  position: "absolute", top: 44, right: 0, zIndex: 999,
                  background: "#1a1a1a", border: "1px solid #2a2a2a",
                  borderRadius: 12, minWidth: 180, overflow: "hidden",
                  boxShadow: "0 8px 24px rgba(0,0,0,0.6)",
                }}>
                  <button onClick={() => {
                    if (iBlockedThem) toggleBlock();           // unblock immediately
                    else { setConfirmBlock(true); setOverflowOpen(false); }
                  }} disabled={blockLoading}
                    style={{
                      width: "100%", padding: "12px 14px", background: "transparent",
                      border: "none", color: "#F87171", fontSize: 14, fontWeight: 700,
                      textAlign: "left", cursor: "pointer",
                      display: "flex", alignItems: "center", gap: 10,
                    }}>
                    <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="#F87171" strokeWidth="2" strokeLinecap="round"><circle cx="12" cy="12" r="9"/><line x1="5.6" y1="5.6" x2="18.4" y2="18.4"/></svg>
                    {blockLoading ? "..." : iBlockedThem ? "Unblock @" + username : "Block @" + username}
                  </button>
                  {/* Admin-only: pin this producer as Featured Artist of the Week */}
                  {currentUser && (currentUser.is_admin === true || currentUser.username === "Trelli") && profile && (profile.id || profile._id) && (
                    <button onClick={() => {
                      setOverflowOpen(false);
                      const uid = profile.id || profile._id;
                      apiFetch("/api/producer/featured/set-artist", { method: "POST", body: JSON.stringify({ user_id: String(uid) }) })
                        .then(function(){ setFeaturePinned(true); setTimeout(function(){ setFeaturePinned(false); }, 2500); })
                        .catch(function(){});
                    }}
                      style={{
                        width: "100%", padding: "12px 14px", background: "transparent",
                        borderTop: "1px solid #2a2a2a", border: "none", color: "#A78BFA", fontSize: 14, fontWeight: 700,
                        textAlign: "left", cursor: "pointer",
                        display: "flex", alignItems: "center", gap: 10,
                      }}>
                      <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="#A78BFA" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><polygon points="12 2 15 9 22 9 16 14 18 21 12 17 6 21 8 14 2 9 9 9"/></svg>
                      Feature as Artist of the Week
                    </button>
                  )}
                </div>
              </>
            )}
          </div>
        )}

        {/* Share button on user's own profile — lets them share their
            own profile link to friends / external apps. Single button
            row since Follow/Message don't apply to oneself. */}
        {isOwnProfile && (
          <div style={{ display: "flex", marginBottom: 10 }}>
            <button onClick={shareProfile}
              style={{
                flex: 1, padding: "10px 16px", borderRadius: 20,
                fontWeight: 700, fontSize: 13, cursor: "pointer",
                background: "transparent",
                border: "1.5px solid #333", color: "#aaa",
                display: "flex", alignItems: "center", justifyContent: "center", gap: 8,
              }}>
              <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                <circle cx="18" cy="5" r="3"/><circle cx="6" cy="12" r="3"/><circle cx="18" cy="19" r="3"/>
                <line x1="8.59" y1="13.51" x2="15.42" y2="17.49"/>
                <line x1="15.41" y1="6.51" x2="8.59" y2="10.49"/>
              </svg>
              Share my profile
            </button>
          </div>
        )}

        {/* Copy-success toast — flashes briefly when the clipboard
            fallback fires (no native share sheet available). */}
        {shareCopied && (
          <div style={{
            position: "fixed", bottom: 90, left: "50%",
            transform: "translateX(-50%)", zIndex: 99999,
            background: "rgba(16,185,129,0.95)",
            color: "white", fontSize: 13, fontWeight: 700,
            padding: "10px 18px", borderRadius: 20,
            boxShadow: "0 6px 24px rgba(0,0,0,0.5)",
            pointerEvents: "none",
          }}>
            Link copied to clipboard
          </div>
        )}

        {featurePinned && (
          <div style={{
            position: "fixed", bottom: 90, left: "50%",
            transform: "translateX(-50%)", zIndex: 99999,
            background: "linear-gradient(135deg, rgba(167,139,250,0.97), rgba(124,58,237,0.97))",
            color: "white", fontSize: 13, fontWeight: 700,
            padding: "10px 18px", borderRadius: 20,
            boxShadow: "0 6px 24px rgba(0,0,0,0.5), 0 0 20px rgba(167,139,250,0.4)",
            pointerEvents: "none",
          }}>
            ★ @{username} is now Featured Artist of the Week
          </div>
        )}

        {/* Block confirmation modal — full-page overlay */}
        {confirmBlock && (
          <div onClick={() => setConfirmBlock(false)} style={{
            position: "fixed", inset: 0, zIndex: 99997,
            background: "rgba(0,0,0,0.85)",
            display: "flex", alignItems: "center", justifyContent: "center",
            padding: 24,
          }}>
            <div onClick={e => e.stopPropagation()} style={{
              background: "#1a1a1a", borderRadius: 16,
              border: "1px solid rgba(239,68,68,0.3)",
              maxWidth: 360, width: "100%", padding: 22,
              boxShadow: "0 16px 40px rgba(0,0,0,0.7)",
            }}>
              <div style={{ display: "flex", alignItems: "center", gap: 12, marginBottom: 14 }}>
                <div style={{
                  width: 40, height: 40, borderRadius: "50%",
                  background: "rgba(239,68,68,0.15)",
                  display: "flex", alignItems: "center", justifyContent: "center",
                  flexShrink: 0,
                }}>
                  <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="#F87171" strokeWidth="2" strokeLinecap="round"><circle cx="12" cy="12" r="9"/><line x1="5.6" y1="5.6" x2="18.4" y2="18.4"/></svg>
                </div>
                <div style={{ color: "white", fontSize: 17, fontWeight: 800 }}>Block @{username}?</div>
              </div>
              <div style={{ color: "#999", fontSize: 13, lineHeight: 1.6, marginBottom: 18 }}>
                They won't be able to see your profile, posts, beats, or message you.
                Any existing follow between you both will be removed.
                You can unblock them from Settings → Blocked Users.
              </div>
              <div style={{ display: "flex", gap: 10 }}>
                <button onClick={() => setConfirmBlock(false)}
                  style={{ flex: 1, padding: "11px", borderRadius: 12,
                    background: "transparent", border: "1.5px solid #333",
                    color: "#aaa", fontWeight: 700, fontSize: 14, cursor: "pointer" }}>
                  Cancel
                </button>
                <button onClick={toggleBlock} disabled={blockLoading}
                  style={{ flex: 1, padding: "11px", borderRadius: 12,
                    background: "linear-gradient(135deg,#DC2626,#B91C1C)", border: "none",
                    color: "white", fontWeight: 800, fontSize: 14, cursor: "pointer" }}>
                  {blockLoading ? "..." : "Block"}
                </button>
              </div>
            </div>
          </div>
        )}

        {!isOwnProfile && <div style={{ height: 1, background: "#1a1a1a", marginTop: 4, marginBottom: 8 }} />}

        {/* ── Find Me On — social links ── */}
        {(profile.instagram || profile.tiktok || profile.youtube || profile.spotify || profile.appleMusic || profile.website) && (
        <div style={{ marginTop: isOwnProfile ? 8 : 0 }}>
          <div style={{ color: "#888", fontSize: 11, fontWeight: 700, letterSpacing: 1, marginBottom: 12 }}>FIND ME ON</div>
          <div style={{ display: "flex", gap: 10, flexWrap: "wrap" }}>
            {[
              {
                key: "instagram", label: "Instagram", url: profile.instagram,
                icon: (
                  <svg width="16" height="16" viewBox="0 0 24 24" fill="none">
                    <defs><linearGradient id="ig2" x1="0%" y1="100%" x2="100%" y2="0%"><stop offset="0%" stopColor="#f09433"/><stop offset="50%" stopColor="#dc2743"/><stop offset="100%" stopColor="#bc1888"/></linearGradient></defs>
                    <rect x="2" y="2" width="20" height="20" rx="5" fill="url(#ig2)"/>
                    <circle cx="12" cy="12" r="4.5" stroke="white" strokeWidth="1.5" fill="none"/>
                    <circle cx="17.5" cy="6.5" r="1.2" fill="white"/>
                  </svg>
                ),
              },
              {
                key: "tiktok", label: "TikTok", url: profile.tiktok,
                icon: (
                  <svg width="16" height="16" viewBox="0 0 24 24" fill="white">
                    <path d="M19.59 6.69a4.83 4.83 0 0 1-3.77-4.25V2h-3.45v13.67a2.89 2.89 0 0 1-2.88 2.5 2.89 2.89 0 0 1-2.89-2.89 2.89 2.89 0 0 1 2.89-2.89c.28 0 .54.04.79.1V9.01a6.34 6.34 0 0 0-6.13 6.33 6.34 6.34 0 0 0 6.34 6.34 6.34 6.34 0 0 0 6.33-6.34V8.93a8.16 8.16 0 0 0 4.77 1.52V7.01a4.85 4.85 0 0 1-1-.32z"/>
                  </svg>
                ),
              },
              {
                key: "youtube", label: "YouTube", url: profile.youtube,
                icon: (
                  <svg width="16" height="16" viewBox="0 0 24 24" fill="none">
                    <rect x="2" y="5" width="20" height="14" rx="4" fill="#FF0000"/>
                    <polygon points="10,8.5 16,12 10,15.5" fill="white"/>
                  </svg>
                ),
              },
              {
                key: "spotify", label: "Spotify", url: profile.spotify,
                icon: (
                  <svg width="16" height="16" viewBox="0 0 24 24" fill="none">
                    <circle cx="12" cy="12" r="10" fill="#1DB954"/>
                    <path d="M8 15.5c2.5-1 5.5-1 8 0" stroke="white" strokeWidth="1.5" strokeLinecap="round" fill="none"/>
                    <path d="M7 12.5c3-1.2 7-1.2 10 0" stroke="white" strokeWidth="1.5" strokeLinecap="round" fill="none"/>
                    <path d="M6.5 9.5c3.5-1.3 8-1.3 11 0" stroke="white" strokeWidth="1.5" strokeLinecap="round" fill="none"/>
                  </svg>
                ),
              },
              {
                key: "appleMusic", label: "Apple Music", url: profile.appleMusic,
                icon: (
                  <svg width="16" height="16" viewBox="0 0 24 24" fill="none">
                    <rect x="2" y="2" width="20" height="20" rx="5" fill="linear-gradient(135deg,#fc3c44,#fc3c44)"/>
                    <rect x="2" y="2" width="20" height="20" rx="5" fill="#fc3c44"/>
                    <path d="M16 7v6.5a2.5 2.5 0 1 1-1.5-2.3V8.5L10 9.5V15a2.5 2.5 0 1 1-1.5-2.3V8l7.5-1z" fill="white"/>
                  </svg>
                ),
              },
              {
                key: "website", label: "Website", url: profile.website,
                icon: (
                  <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="white" strokeWidth="1.8" strokeLinecap="round">
                    <circle cx="12" cy="12" r="10"/>
                    <path d="M2 12h20M12 2a15.3 15.3 0 0 1 4 10 15.3 15.3 0 0 1-4 10 15.3 15.3 0 0 1-4-10 15.3 15.3 0 0 1 4-10z"/>
                  </svg>
                ),
              },
            ].filter(s => s.url).map(s => (
              <button key={s.key}
                onClick={() => { openExternalLink(s.url); }}
                style={{ display: "flex", alignItems: "center", gap: 7, background: "#111",
                  border: "1px solid #1e1e1e", borderRadius: 20, padding: "8px 14px",
                  cursor: "pointer", color: "white", fontWeight: 600, fontSize: 13 }}>
                {s.icon}
                {s.label}
              </button>
            ))}
          </div>
        </div>
        )}
      </div>

      {/* ── Content tabs: Beats / Music / Videos ── */}
      <div style={{ paddingBottom: "calc(100px + env(safe-area-inset-bottom))" }}>
        <ContentTabs username={username} profile={profile} currentUser={currentUser} onPlay={onPlay} savedIds={savedIds} onSave={onSave} onViewProfile={function(u){ setNestedProfile(u); }} />
      </div>
    </div>
  );
}


// =============================================================================
// =============================================================================
// MESSAGES SCREEN — DM system
// =============================================================================
function MessagesScreen({ user, onBack, initialThread, onViewProfile }) {
  const [conversations, setConversations] = useState([]);
  // Pending request threads (Instagram-style "Message Requests" folder).
  // Held separately from inbox so the two tabs render different lists.
  // Loaded once on mount and refreshed when user accepts/declines a
  // request or follows back the sender.
  const [requests,      setRequests]      = useState([]);
  // "inbox" or "requests" — controls which list is visible
  const [tab,           setTab]           = useState("inbox");
  const [loading,       setLoading]       = useState(true);
  const [activeThread,  setActiveThread]  = useState(initialThread || null);
  // Whether the currently-open thread is in MY inbox vs a pending
  // request. When true, we show the normal composer. When false,
  // we show an Accept/Decline banner above the composer (the user
  // shouldn't be able to reply without first accepting the request).
  const [threadInInbox, setThreadInInbox] = useState(true);
  const [threadAvatar,  setThreadAvatar]  = useState("");
  const [messages,      setMessages]      = useState([]);
  const [msgInput,      setMsgInput]      = useState("");
  const [sending,       setSending]       = useState(false);
  const [holdMenu,      setHoldMenu]      = useState(null); // message id being held
  const [confirmDelete, setConfirmDelete] = useState(null); // username to delete thread
  const msgEndRef  = React.useRef(null);
  const pollRef    = React.useRef(null);
  const latestMsgAt = React.useRef(null);

  // Load conversations + requests in parallel
  useEffect(() => {
    if (!user) { setLoading(false); return; }
    Promise.all([
      apiFetch("/api/messages/conversations").catch(function(){ return []; }),
      apiFetch("/api/messages/requests").catch(function(){ return []; }),
    ]).then(function(results) {
      setConversations(results[0] || []);
      setRequests(results[1] || []);
      setLoading(false);
    });
  }, [user]);

  // Load thread + start polling for read receipts & new messages
  useEffect(() => {
    if (!activeThread || !user) { clearInterval(pollRef.current); return; }
    setConversations(prev => prev.map(c => c.username === activeThread ? { ...c, unread: 0 } : c));
    setRequests(prev => prev.map(c => c.username === activeThread ? { ...c, unread: 0 } : c));

    // Find avatar from either list
    const conv = conversations.find(c => c.username === activeThread)
              || requests.find(c => c.username === activeThread);
    if (conv?.avatarUrl) setThreadAvatar(conv.avatarUrl);

    apiFetch("/api/messages/thread/" + encodeURIComponent(activeThread))
      .then(d => {
        // Response is now { messages, inInbox } (was a flat array).
        // Backward-compat: if d is an array, treat as old shape and
        // assume inbox=true (legacy behaviour).
        var msgs, inInbox;
        if (Array.isArray(d)) {
          msgs = d; inInbox = true;
        } else {
          msgs = (d && d.messages) || [];
          inInbox = !!(d && d.inInbox);
        }
        setMessages(msgs);
        setThreadInInbox(inInbox);
        if (msgs.length > 0) latestMsgAt.current = msgs[msgs.length - 1].createdAt;
      })
      .catch(() => {});

    // Poll every 4s for new messages and read receipt updates
    clearInterval(pollRef.current);
    pollRef.current = setInterval(() => {
      const afterParam = latestMsgAt.current
        ? "?after=" + encodeURIComponent(latestMsgAt.current) : "";
      apiFetch("/api/messages/thread/" + encodeURIComponent(activeThread) + "/updates" + afterParam, { silent: true })
        .then(data => {
          // Merge new messages
          if (data.messages && data.messages.length > 0) {
            setMessages(prev => {
              const existingIds = new Set(prev.map(m => m.id));
              const newMsgs = data.messages.filter(m => !existingIds.has(m.id));
              if (newMsgs.length === 0) return prev;
              latestMsgAt.current = data.messages[data.messages.length - 1].createdAt;
              return [...prev, ...newMsgs];
            });
          }
          // Update read status on our sent messages
          if (data.readIds && data.readIds.length > 0) {
            const readSet = new Set(data.readIds);
            setMessages(prev => prev.map(m => readSet.has(m.id) ? { ...m, read: true } : m));
          }
        })
        .catch(() => {});
    }, 4000);

    return () => clearInterval(pollRef.current);
  }, [activeThread]);

  // Scroll to bottom on new messages
  useEffect(() => {
    msgEndRef.current?.scrollIntoView({ behavior: "smooth" });
  }, [messages]);

  const sendMessage = async () => {
    if (!msgInput.trim() || !activeThread || sending) return;
    setSending(true);
    const text = msgInput.trim();
    setMsgInput("");
    const tempId = "tmp_" + Date.now();
    setMessages(prev => [...prev, { id: tempId, from: user.username, text, createdAt: new Date().toISOString(), sending: true, delivered: false, read: false }]);
    try {
      const sent = await apiFetch("/api/messages/send", { method: "POST", body: JSON.stringify({ to: activeThread, text }) });
      latestMsgAt.current = sent.createdAt;
      setMessages(prev => prev.map(m => m.id === tempId ? { ...sent, delivered: true } : m));
    } catch(e) {
      setMessages(prev => prev.filter(m => m.id !== tempId));
      setMsgInput(text);
    }
    setSending(false);
  };

  const unsendMessage = async (msgId) => {
    setHoldMenu(null);
    setMessages(prev => prev.map(m => m.id === msgId ? { ...m, unsent: true, text: "" } : m));
    apiFetch("/api/messages/" + msgId + "/unsend", { method: "DELETE" }).catch(() => {});
  };

  const deleteThread = async (username) => {
    setConfirmDelete(null);
    await apiFetch("/api/messages/thread/" + encodeURIComponent(username), { method: "DELETE" }).catch(() => {});
    setConversations(prev => prev.filter(c => c.username !== username));
    setRequests(prev => prev.filter(c => c.username !== username));
    if (activeThread === username) setActiveThread(null);
  };

  // Accept a pending message request — moves the thread from
  // Requests into Inbox. Refreshes both lists locally so the UI
  // reflects the change without a full reload.
  const acceptRequest = async (otherUsername) => {
    try {
      await apiFetch("/api/messages/requests/" + encodeURIComponent(otherUsername) + "/accept", { method: "POST" });
    } catch(e) {
      // Surface the error but don't crash — user can try again
      console.warn("[Messages] accept failed:", e.message);
      return;
    }
    // Move the conversation from requests → inbox in local state
    var moved = null;
    setRequests(function(prev) {
      var next = [];
      prev.forEach(function(c) {
        if (c.username === otherUsername) { moved = c; } else { next.push(c); }
      });
      return next;
    });
    setConversations(function(prev) {
      if (!moved) return prev;
      // De-dupe in case it was already in the list
      var filtered = prev.filter(function(c) { return c.username !== otherUsername; });
      return [moved].concat(filtered);
    });
    setThreadInInbox(true);
  };

  // Decline a pending request — deletes the thread entirely.
  // Same destructive flow as deleteThread but no confirmation dialog
  // because Instagram doesn't confirm requests-decline either.
  const declineRequest = async (otherUsername) => {
    try {
      await apiFetch("/api/messages/requests/" + encodeURIComponent(otherUsername), { method: "DELETE" });
    } catch(e) {
      console.warn("[Messages] decline failed:", e.message);
      return;
    }
    setRequests(function(prev) { return prev.filter(function(c) { return c.username !== otherUsername; }); });
    if (activeThread === otherUsername) setActiveThread(null);
  };

  function Avatar({ src, name, size }) {
    return (
      <div style={{ width: size, height: size, borderRadius: "50%", flexShrink: 0,
        background: "linear-gradient(135deg,#6B21A8,#C026D3)", overflow: "hidden",
        display: "flex", alignItems: "center", justifyContent: "center",
        color: "white", fontWeight: 800, fontSize: size * 0.38 }}>
        {src
          ? <img src={src} alt={name} style={{ width: "100%", height: "100%", objectFit: "cover" }} />
          : (name || "?")[0].toUpperCase()}
      </div>
    );
  }

  const inp = {
    background: "#111", border: "1px solid #222", borderRadius: 24,
    padding: "12px 16px", color: "white", fontSize: 14, outline: "none",
    fontFamily: "inherit",
  };

  // ── Thread view ───────────────────────────────────────────────────────────
  if (activeThread) {
    const lastMyMsg = [...messages].reverse().find(m => m.from === user?.username && !m.sending);
    return (
      <div style={{ display: "flex", flexDirection: "column", height: "100dvh" }}
        onClick={() => holdMenu && setHoldMenu(null)}>
        {/* Thread header */}
        <div style={{ display: "flex", alignItems: "center", gap: 12, padding: "16px", borderBottom: "1px solid #1a1a1a", flexShrink: 0, paddingTop: "calc(16px + var(--bf-safe-top, env(safe-area-inset-top)))", background: "#0a0a0a" }}>
          <button onClick={() => setActiveThread(null)} style={{ background: "none", border: "none", color: "white", fontSize: 22, cursor: "pointer", padding: 0, flexShrink: 0 }}>←</button>
          <button onClick={() => onViewProfile && onViewProfile(activeThread)}
            style={{ background: "none", border: "none", padding: 0, cursor: "pointer", display: "flex", alignItems: "center", gap: 10, flex: 1 }}>
            <Avatar src={threadAvatar} name={activeThread} size={36} />
            <div style={{ color: "white", fontWeight: 700, fontSize: 16 }}>@{activeThread}</div>
          </button>
          {/* Delete thread button */}
          <button onClick={() => setConfirmDelete(activeThread)}
            style={{ background: "none", border: "none", color: "#444", cursor: "pointer", padding: 4, flexShrink: 0 }}>
            <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="#555" strokeWidth="2" strokeLinecap="round">
              <polyline points="3 6 5 6 21 6"/><path d="M19 6l-1 14H6L5 6"/><path d="M10 11v6M14 11v6"/><path d="M9 6V4h6v2"/>
            </svg>
          </button>
        </div>

        {/* Confirm delete thread */}
        {confirmDelete && (
          <div style={{ position: "fixed", inset: 0, zIndex: 9999, background: "rgba(0,0,0,0.8)",
            display: "flex", alignItems: "center", justifyContent: "center", padding: 24 }}>
            <div style={{ background: "#111", borderRadius: 16, padding: 24, width: "100%", maxWidth: 320, border: "1px solid #2a2a2a" }}>
              <div style={{ color: "white", fontWeight: 800, fontSize: 16, marginBottom: 8 }}>Delete conversation?</div>
              <div style={{ color: "#888", fontSize: 13, marginBottom: 20 }}>This will permanently delete all messages in this thread for both users.</div>
              <div style={{ display: "flex", gap: 10 }}>
                <button onClick={() => setConfirmDelete(null)}
                  style={{ flex: 1, background: "#1a1a1a", border: "1px solid #333", borderRadius: 10, color: "#aaa", fontWeight: 700, padding: 12, cursor: "pointer" }}>
                  Cancel
                </button>
                <button onClick={() => deleteThread(confirmDelete)}
                  style={{ flex: 1, background: "rgba(220,38,38,0.2)", border: "1px solid rgba(220,38,38,0.4)", borderRadius: 10, color: "#F87171", fontWeight: 700, padding: 12, cursor: "pointer" }}>
                  Delete
                </button>
              </div>
            </div>
          </div>
        )}

        {/* Messages */}
        <div style={{ flex: 1, overflowY: "auto", padding: "12px 16px", display: "flex", flexDirection: "column", gap: 4 }}
          onTouchMove={e => e.stopPropagation()}>
          {messages.length === 0 && (
            <div style={{ textAlign: "center", color: "#333", fontSize: 13, paddingTop: 40 }}>
              Start a conversation with @{activeThread}
            </div>
          )}
          {messages.map((m, i) => {
            const isMe = m.from === user?.username;
            const isLast = i === messages.length - 1;
            const showAvatar = !isMe && (i === 0 || messages[i - 1].from !== m.from);
            const isLastMine = isMe && lastMyMsg?.id === m.id;
            return (
              <div key={m.id || i}>
                <div style={{ display: "flex", justifyContent: isMe ? "flex-end" : "flex-start",
                  alignItems: "flex-end", gap: 8, marginTop: showAvatar ? 10 : 2 }}>
                  {/* Other person avatar */}
                  {!isMe && (
                    <div style={{ width: 28, flexShrink: 0, marginBottom: 2 }}>
                      {showAvatar && <Avatar src={threadAvatar} name={activeThread} size={28} />}
                    </div>
                  )}
                  {/* Bubble */}
                  <div
                    onTouchStart={isMe && !m.unsent ? (function(e) {
                      var key = m.id || i;
                      var t = setTimeout(function() { setHoldMenu(key); }, 500);
                      e.currentTarget._holdTimer = t;
                    }) : undefined}
                    onTouchEnd={isMe && !m.unsent ? (function(e) {
                      clearTimeout(e.currentTarget._holdTimer);
                    }) : undefined}
                    onTouchMove={isMe && !m.unsent ? (function(e) {
                      clearTimeout(e.currentTarget._holdTimer);
                    }) : undefined}
                    onContextMenu={isMe && !m.unsent ? (function(e) { e.preventDefault(); setHoldMenu(m.id || i); }) : undefined}
                    style={{ maxWidth: "72%", padding: m.unsent ? "8px 12px" : "10px 14px",
                      borderRadius: isMe ? "18px 18px 4px 18px" : "18px 18px 18px 4px",
                      background: m.unsent ? "transparent" : isMe ? "#C026D3" : "#1a1a1a",
                      border: m.unsent ? "1px solid #2a2a2a" : "none",
                      color: m.unsent ? "#555" : "white",
                      fontSize: m.unsent ? 13 : 14, lineHeight: 1.5,
                      fontStyle: m.unsent ? "italic" : "normal",
                      opacity: m.sending ? 0.5 : 1,
                      cursor: isMe && !m.unsent ? "pointer" : "default",
                      userSelect: "none", WebkitUserSelect: "none",
                    }}>
                    {m.unsent ? "Message unsent" : m.text}
                  </div>
                </div>
                {/* Hold menu — unsend option */}
                {holdMenu === (m.id || i) && isMe && !m.unsent && (
                  <div style={{ display: "flex", justifyContent: "flex-end", marginTop: 4, marginRight: 0 }}>
                    <button onClick={() => unsendMessage(m.id)}
                      style={{ background: "#1a1a1a", border: "1px solid #2a2a2a", borderRadius: 10,
                        color: "#F87171", fontSize: 12, fontWeight: 700, padding: "6px 14px", cursor: "pointer" }}>
                      Unsend
                    </button>
                  </div>
                )}
                {/* Read / Delivered receipt — only on last sent message */}
                {isLastMine && !m.sending && (
                  <div style={{ display: "flex", justifyContent: "flex-end", marginTop: 2 }}>
                    <span style={{ color: m.read ? "#C026D3" : "#555", fontSize: 10, fontWeight: 600 }}>
                      {m.read ? "Seen" : m.delivered || !m.sending ? "Delivered" : "Sending..."}
                    </span>
                  </div>
                )}
              </div>
            );
          })}
          <div ref={msgEndRef} />
        </div>

        {/* Input — OR Accept/Decline banner if this is a pending request.
            Instagram pattern: when you open a request thread, the message
            input is locked behind an Accept gate. Tapping Accept moves
            the thread into your inbox and reveals the composer. Decline
            deletes the thread entirely. */}
        {threadInInbox ? (
          <div style={{ padding: "10px 16px", paddingBottom: "calc(10px + env(safe-area-inset-bottom))", borderTop: "1px solid #1a1a1a", display: "flex", gap: 8, flexShrink: 0, background: "#0a0a0a" }}>
            <input value={msgInput} onChange={e => setMsgInput(e.target.value)}
              onKeyDown={e => e.key === "Enter" && sendMessage()}
              placeholder="Send a message..."
              style={{ ...inp, flex: 1 }} />
            <button onClick={sendMessage} disabled={sending || !msgInput.trim()}
              style={{ background: "#C026D3", border: "none", borderRadius: 22, color: "white", fontWeight: 700, fontSize: 13, padding: "12px 18px", cursor: "pointer", opacity: !msgInput.trim() ? 0.4 : 1, flexShrink: 0 }}>
              Send
            </button>
          </div>
        ) : (
          <div style={{
            padding: "14px 16px",
            paddingBottom: "calc(14px + env(safe-area-inset-bottom))",
            borderTop: "1px solid #1a1a1a",
            background: "#0a0a0a",
            flexShrink: 0,
          }}>
            <div style={{ color: "#888", fontSize: 12, textAlign: "center", marginBottom: 10, lineHeight: 1.4 }}>
              @{activeThread} isn't in your inbox yet. Accept to reply, or decline to delete this conversation.
            </div>
            <div style={{ display: "flex", gap: 8 }}>
              <button onClick={() => declineRequest(activeThread)}
                style={{
                  flex: 1, background: "transparent",
                  border: "1.5px solid #2a2a2a", borderRadius: 22,
                  color: "#888", fontWeight: 700, fontSize: 13,
                  padding: "12px 18px", cursor: "pointer",
                }}>
                Decline
              </button>
              <button onClick={() => acceptRequest(activeThread)}
                style={{
                  flex: 1, background: "#C026D3",
                  border: "none", borderRadius: 22,
                  color: "white", fontWeight: 800, fontSize: 13,
                  padding: "12px 18px", cursor: "pointer",
                }}>
                Accept
              </button>
            </div>
          </div>
        )}
      </div>
    );
  }

  // ── Conversations list ────────────────────────────────────────────────────
  // The list view at the top of the Messages screen.
  // Two tabs: Inbox (accepted conversations) and Requests (pending DMs
  // from people you don't follow). Tab state is local to this component.
  // The Requests tab shows the pending count as a pink badge so users
  // notice new requests without needing to leave the inbox view.
  const visibleList = tab === "requests" ? requests : conversations;
  return (
    <div style={{ paddingBottom: 100 }}>
      <div style={{ display: "flex", alignItems: "center", gap: 12, padding: "16px", borderBottom: "1px solid #1a1a1a", paddingTop: "calc(16px + var(--bf-safe-top, env(safe-area-inset-top)))" }}>
        <button onClick={onBack} style={{ background: "none", border: "none", color: "white", fontSize: 22, cursor: "pointer", padding: 0 }}>←</button>
        <div style={{ color: "white", fontWeight: 800, fontSize: 18 }}>Messages</div>
      </div>

      {!user && (
        <div style={{ textAlign: "center", padding: "60px 24px", color: "#555" }}>
          <svg width="48" height="48" viewBox="0 0 24 24" fill="none" stroke="#333" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round" style={{ marginBottom: 12 }}>
            <path d="M21 15a2 2 0 0 1-2 2H7l-4 4V5a2 2 0 0 1 2-2h14a2 2 0 0 1 2 2z"/>
          </svg>
          <div style={{ fontSize: 16, fontWeight: 700, color: "#444", marginBottom: 6 }}>Sign in to message</div>
          <div style={{ fontSize: 13, color: "#333" }}>Create an account to send direct messages to other users</div>
        </div>
      )}

      {/* Tabs — only render for signed-in users with at least one
          conversation or pending request. Hidden during loading and
          for empty-state to keep the screen calm. */}
      {user && !loading && (conversations.length > 0 || requests.length > 0) && (
        <div style={{ display: "flex", borderBottom: "1px solid #1a1a1a" }}>
          <button onClick={() => setTab("inbox")}
            style={{
              flex: 1, padding: "14px 0", background: "none", border: "none",
              cursor: "pointer", position: "relative",
              color: tab === "inbox" ? "white" : "#666",
              fontWeight: tab === "inbox" ? 800 : 600, fontSize: 14,
              borderBottom: tab === "inbox" ? "2px solid #C026D3" : "2px solid transparent",
            }}>
            Inbox
          </button>
          <button onClick={() => setTab("requests")}
            style={{
              flex: 1, padding: "14px 0", background: "none", border: "none",
              cursor: "pointer", position: "relative",
              color: tab === "requests" ? "white" : "#666",
              fontWeight: tab === "requests" ? 800 : 600, fontSize: 14,
              borderBottom: tab === "requests" ? "2px solid #C026D3" : "2px solid transparent",
              display: "flex", alignItems: "center", justifyContent: "center", gap: 6,
            }}>
            Requests
            {requests.length > 0 && (
              <span style={{
                background: "#C026D3", color: "white",
                fontSize: 11, fontWeight: 800,
                padding: "1px 7px", borderRadius: 10,
                minWidth: 18, textAlign: "center",
              }}>
                {requests.length > 99 ? "99+" : requests.length}
              </span>
            )}
          </button>
        </div>
      )}

      {user && loading && <div style={{ textAlign: "center", padding: "60px 24px", color: "#555", fontSize: 14 }}>Loading...</div>}
      {user && !loading && visibleList.length === 0 && tab === "inbox" && requests.length === 0 && (
        <div style={{ textAlign: "center", padding: "60px 24px", color: "#555" }}>
          <svg width="48" height="48" viewBox="0 0 24 24" fill="none" stroke="#333" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round" style={{ marginBottom: 12 }}>
            <path d="M21 15a2 2 0 0 1-2 2H7l-4 4V5a2 2 0 0 1 2-2h14a2 2 0 0 1 2 2z"/>
          </svg>
          <div style={{ fontSize: 16, fontWeight: 700, color: "#444", marginBottom: 6 }}>No messages yet</div>
          <div style={{ fontSize: 13, color: "#333" }}>Visit a user's profile and tap Message to start a conversation</div>
        </div>
      )}
      {user && !loading && visibleList.length === 0 && tab === "inbox" && requests.length > 0 && (
        <div style={{ textAlign: "center", padding: "60px 24px", color: "#555" }}>
          <div style={{ fontSize: 15, fontWeight: 700, color: "#666", marginBottom: 6 }}>No conversations yet</div>
          <div style={{ fontSize: 13, color: "#444" }}>You have {requests.length} pending request{requests.length === 1 ? "" : "s"} — tap Requests above to view.</div>
        </div>
      )}
      {user && !loading && visibleList.length === 0 && tab === "requests" && (
        <div style={{ textAlign: "center", padding: "60px 24px", color: "#555" }}>
          <div style={{ fontSize: 15, fontWeight: 700, color: "#666", marginBottom: 6 }}>No pending requests</div>
          <div style={{ fontSize: 13, color: "#444" }}>Messages from people you don't follow appear here.</div>
        </div>
      )}
      {user && !loading && visibleList.map(c => (
        <button key={c.username} onClick={() => { setThreadAvatar(c.avatarUrl || ""); setActiveThread(c.username); }}
          style={{ width: "100%", display: "flex", alignItems: "center", gap: 14, padding: "14px 16px",
            background: "none", border: "none", borderBottom: "1px solid #111", cursor: "pointer", textAlign: "left" }}>
          {/* Profile picture */}
          <div style={{ width: 50, height: 50, borderRadius: "50%", flexShrink: 0,
            background: "linear-gradient(135deg,#6B21A8,#C026D3)", overflow: "hidden",
            display: "flex", alignItems: "center", justifyContent: "center",
            color: "white", fontWeight: 800, fontSize: 20 }}>
            {c.avatarUrl
              ? <img src={c.avatarUrl} alt={c.username} style={{ width: "100%", height: "100%", objectFit: "cover" }} />
              : c.username[0].toUpperCase()}
          </div>
          <div style={{ flex: 1, overflow: "hidden" }}>
            <div style={{ color: "white", fontWeight: 700, fontSize: 15 }}>@{c.username}</div>
            <div style={{ color: c.unread > 0 ? "#aaa" : "#555", fontSize: 13, marginTop: 2, whiteSpace: "nowrap", overflow: "hidden", textOverflow: "ellipsis", fontWeight: c.unread > 0 ? 600 : 400 }}>
              {c.lastMessage || "Start chatting"}
            </div>
          </div>
          {c.unread > 0 && (
            <div style={{ width: 20, height: 20, borderRadius: "50%", background: "#C026D3",
              display: "flex", alignItems: "center", justifyContent: "center",
              color: "white", fontSize: 11, fontWeight: 800, flexShrink: 0 }}>
              {c.unread}
            </div>
          )}
          <span style={{ color: "#333", fontSize: 16, flexShrink: 0 }}>›</span>
        </button>
      ))}
    </div>
  );
}

// =============================================================================
// STRIPE CONNECT SECTION - for Producer Pro profile
// =============================================================================
function StripeConnectSection({ user }) {
  const [status,  setStatus]  = useState(null);
  const [loading, setLoading] = useState(true);
  const [connecting, setConnecting] = useState(false);

  useEffect(() => {
    apiFetch("/api/producer/stripe-status")
      .then(d => { setStatus(d); setLoading(false); })
      .catch(() => setLoading(false));
  }, []);

  const handleConnect = async () => {
    setConnecting(true);
    try {
      const result = await apiFetch("/api/producer/connect-stripe", { method: "POST" });
      window.location.href = result.url;
    } catch (e) {
      setConnecting(false);
      setStatus({ connected: false, error: e.message });
    }
  };

  return (
    <div style={{ background: "#111", borderRadius: 14, padding: 16, border: "1px solid #222", marginBottom: 4 }}>
      <div style={{ color: "white", fontWeight: 800, fontSize: 16, marginBottom: 6 }}>▭ Stripe Payouts</div>
      <div style={{ color: "#666", fontSize: 13, marginBottom: 14, lineHeight: 1.6 }}>
        Connect your Stripe account to receive payments when artists buy your beat leases.
      </div>
      {loading ? (
        <div style={{ color: "#555", fontSize: 13 }}>Checking status...</div>
      ) : status?.connected ? (
        <div style={{ display: "flex", alignItems: "center", gap: 10 }}>
          <div style={{ background: "rgba(34,197,94,0.15)", border: "1px solid rgba(34,197,94,0.3)", borderRadius: 20, padding: "6px 14px", color: "#22C55E", fontWeight: 700, fontSize: 13 }}>
            ✓ Stripe Connected
          </div>
          <div style={{ color: "#555", fontSize: 12 }}>Payouts {status.payouts_enabled ? "enabled" : "pending"}</div>
        </div>
      ) : (
        <>
          {status?.error && (
            <div style={{ color: "#F87171", fontSize: 12, marginBottom: 10, background: "rgba(220,38,38,0.1)", borderRadius: 8, padding: "8px 12px" }}>
              {status.error}
            </div>
          )}
          <button
            onClick={handleConnect}
            disabled={connecting}
            style={{
              width: "100%", background: connecting ? "#333" : "linear-gradient(135deg,#635BFF,#8B5CF6)",
              border: "none", borderRadius: 12, color: "white",
              fontWeight: 800, fontSize: 15, padding: "13px", cursor: connecting ? "not-allowed" : "pointer",
            }}
          >
            {connecting ? "Redirecting to Stripe..." : "Connect Stripe Account"}
          </button>
        </>
      )}
    </div>
  );
}


// =============================================================================
// MY UPLOADS - Producer Pro tab to manage uploaded beats
// =============================================================================
function MyUploadsSection({ user }) {
  const [beats,    setBeats]    = useState([]);
  const [loading,  setLoading]  = useState(true);
  const [editId,    setEditId]    = useState(null);
  const [editTitle, setEditTitle] = useState("");
  const [editGenre, setEditGenre] = useState("");
  const [editPrice, setEditPrice] = useState("");
  const [editPremiumPrice, setEditPremiumPrice] = useState("");
  const [editPremiumSold,  setEditPremiumSold]  = useState(false);
  const [editDesc,  setEditDesc]  = useState("");
  const [editBpm,   setEditBpm]   = useState("");
  const [editKey,   setEditKey]   = useState("");
  const [editPreviewStart, setEditPreviewStart] = useState(0);
  const [saving,    setSaving]    = useState(false);
  const [msg,       setMsg]       = useState("");

  const load = () => {
    setLoading(true);
    apiFetch("/api/producer/my-beats")
      .then(d => { setBeats(d); setLoading(false); })
      .catch(() => setLoading(false));
  };

  useEffect(() => { load(); }, []);

  const handleEdit = (beat) => {
    setEditId(beat.id);
    setEditTitle(beat.title || "");
    setEditGenre(beat.genre || "");
    setEditPrice(beat.price || "");
    setEditPremiumPrice(beat.premium_lease_price ? String(beat.premium_lease_price) : "");
    setEditPremiumSold(!!beat.premium_sold);
    setEditDesc(beat.description || "");
    setEditBpm(beat.bpm ? String(beat.bpm) : "");
    setEditKey(beat.key || "");
    setEditPreviewStart(beat.preview_start || 0);
    setMsg("");
  };

  const handleSave = async () => {
    setSaving(true);
    setMsg("");
    try {
      // Normalise price: if user chose FREE, clear premium too
      var priceVal = (editPrice || "").trim();
      if (priceVal === "0" || priceVal === "£0" || priceVal === "") priceVal = "free";
      var isPaid = (priceVal !== "free");

      // Build the request body. We always send basic_lease_price.
      // Premium is included only when the producer set a value AND
      // it hasn't already been sold (server rejects price change if sold).
      var body = {
        title:         editTitle,
        genre:         editGenre,
        price:         priceVal,
        description:   editDesc,
        bpm:           editBpm ? parseInt(editBpm) : 0,
        key:           editKey,
        preview_start: editPreviewStart,
        basic_lease_price: isPaid ? 50 : 0,
      };

      // Only send premium price changes when allowed. If sold, the server
      // returns 409 — so we just skip the field entirely to avoid errors.
      if (isPaid && !editPremiumSold) {
        var premiumNum = parseInt(editPremiumPrice, 10);
        if (editPremiumPrice && (isNaN(premiumNum) || premiumNum < 100 || premiumNum > 500)) {
          setMsg("Error: Premium lease price must be between £100 and £500 (or leave blank for Basic only)");
          setSaving(false);
          return;
        }
        // Send 0 to clear, otherwise the chosen value
        body.premium_lease_price = editPremiumPrice ? premiumNum : 0;
      }

      await apiFetch("/api/producer/beats/" + editId + "/update", {
        method: "POST",
        body: JSON.stringify(body),
      });
      setMsg("Updated!");
      // Broadcast to all beat card lists so they update live without refresh
      dispatchBeatUpdated(editId, {
        title:               editTitle,
        genre:               editGenre,
        price:               priceVal,
        description:         editDesc,
        bpm:                 editBpm ? parseInt(editBpm) : 0,
        key:                 editKey,
        preview_start:       editPreviewStart,
        basic_lease_price:   isPaid ? 50 : 0,
        premium_lease_price: (isPaid && !editPremiumSold && editPremiumPrice) ? parseInt(editPremiumPrice, 10) : 0,
      });
      setEditId(null);
      load();
    } catch (e) {
      setMsg("Error: " + e.message);
    } finally {
      setSaving(false);
    }
  };

  const handleDelete = async (beatId, title) => {
    if (!window.confirm("Delete " + title + "? This cannot be undone.")) return;
    try {
      await apiFetch("/api/producer/beats/" + beatId, { method: "DELETE" });
      setBeats(prev => prev.filter(b => b.id !== beatId));
      dispatchContentDeleted(beatId, "beat");
    } catch (e) {
      setMsg("Error: " + e.message);
    }
  };

  if (loading) return <BFLoader type="spinner" text="Loading your beats..." />;

  return (
    <div>
      <div style={{ color: "white", fontWeight: 800, fontSize: 18, marginBottom: 6 }}><AppIcon id="knobs" size={20}/> My Uploaded Beats</div>
      <div style={{ color: "#666", fontSize: 13, marginBottom: 16 }}>Edit or delete your uploaded beats.</div>

      {msg && <div style={{ color: msg.startsWith("Error") ? "#F87171" : "#22C55E", fontSize: 13, marginBottom: 12, textAlign: "center", fontWeight: 600 }}>{msg}</div>}

      {beats.length === 0 && (
        <div style={{ background: "#111", borderRadius: 14, padding: 20, textAlign: "center", border: "1px solid #222" }}>
      <div style={{ fontSize: 36, marginBottom: 10 }}><AppIcon id="profile" size={20}/></div>
          <div style={{ color: "#555", fontSize: 14 }}>No beats uploaded yet.</div>
        </div>
      )}

      {beats.map(beat => (
        <div key={beat.id} style={{ background: "#111", borderRadius: 14, padding: 16, marginBottom: 12, border: "1px solid #1e1e1e" }}>
          {editId === beat.id ? (
            <div>
              <input
                value={editTitle}
                onChange={e => setEditTitle(e.target.value)}
                placeholder="Beat title"
                style={{ width: "100%", background: "#1a1a1a", border: "1px solid #333", borderRadius: 10, padding: "10px 14px", color: "white", fontSize: 14, outline: "none", boxSizing: "border-box", marginBottom: 8 }}
              />
              <input
                value={editGenre}
                onChange={e => setEditGenre(e.target.value)}
                placeholder="Genre"
                style={{ width: "100%", background: "#1a1a1a", border: "1px solid #333", borderRadius: 10, padding: "10px 14px", color: "white", fontSize: 14, outline: "none", boxSizing: "border-box", marginBottom: 8 }}
              />

              {/* PRICING — FREE/PAID toggle, mirrors upload form */}
              <div style={{ color: "#666", fontSize: 11, fontWeight: 700, marginBottom: 6, letterSpacing: 0.5 }}>PRICING</div>
              <div style={{ display: "flex", gap: 8, marginBottom: 10 }}>
                <button type="button"
                  disabled={editPremiumSold}
                  onClick={function() {
                    if (editPremiumSold) return;
                    setEditPrice("free");
                    setEditPremiumPrice("");
                  }}
                  style={{
                    flex: 1, padding: "10px", borderRadius: 10,
                    border: (editPrice === "free" ? "2px solid #C026D3" : "1px solid #333"),
                    background: (editPrice === "free" ? "rgba(192,38,211,0.15)" : "transparent"),
                    color: (editPrice === "free" ? "#C026D3" : "#888"),
                    fontWeight: 800, fontSize: 12,
                    cursor: editPremiumSold ? "not-allowed" : "pointer",
                    opacity: editPremiumSold ? 0.5 : 1,
                  }}>
                  FREE Beat
                </button>
                <button type="button" onClick={function() {
                  if (editPrice === "free" || !editPrice) setEditPrice("£50");
                }}
                  style={{
                    flex: 1, padding: "10px", borderRadius: 10,
                    border: (editPrice && editPrice !== "free" ? "2px solid #F59E0B" : "1px solid #333"),
                    background: (editPrice && editPrice !== "free" ? "rgba(245,158,11,0.15)" : "transparent"),
                    color: (editPrice && editPrice !== "free" ? "#F59E0B" : "#888"),
                    fontWeight: 800, fontSize: 12, cursor: "pointer",
                  }}>
                  PAID Lease
                </button>
              </div>

              {editPrice && editPrice !== "free" && (
                <div style={{ background: "#0a0a0a", border: "1px solid #1f1f1f", borderRadius: 10, padding: 12, marginBottom: 10 }}>
                  <div style={{ color: "#aaa", fontSize: 11, fontWeight: 700, marginBottom: 8, letterSpacing: 0.5 }}>
                    LEASE TIERS — buyers pick one
                  </div>
                  {/* Basic — fixed £50 */}
                  <div style={{ background: "#0d0d0d", border: "1px solid #222", borderRadius: 8, padding: 10, marginBottom: 8 }}>
                    <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center" }}>
                      <div style={{ color: "#F59E0B", fontWeight: 800, fontSize: 12 }}>Basic Lease</div>
                      <div style={{ color: "#F59E0B", fontWeight: 800, fontSize: 14 }}>£50</div>
                    </div>
                    <div style={{ color: "#666", fontSize: 10, marginTop: 4, lineHeight: 1.4 }}>
                      Non-exclusive · 75% royalties · 100k stream cap · Fixed at £50
                    </div>
                  </div>
                  {/* Premium — producer-set £100-£500 */}
                  <div style={{ background: "#0d0d0d", border: "1px solid #222", borderRadius: 8, padding: 10 }}>
                    <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", marginBottom: 6 }}>
                      <div style={{ color: "#A855F7", fontWeight: 800, fontSize: 12 }}>
                        Premium Lease (Exclusive){editPremiumSold ? " — SOLD" : ""}
                      </div>
                    </div>
                    {editPremiumSold ? (
                      <div style={{ color: "#888", fontSize: 11, lineHeight: 1.5 }}>
                        <strong style={{ color: "#A855F7" }}>£{editPremiumPrice}</strong> — Locked. The premium exclusive has already been sold; price cannot be changed.
                      </div>
                    ) : (
                      <>
                        <div style={{ display: "flex", alignItems: "center", gap: 6 }}>
                          <span style={{ color: "#A855F7", fontSize: 14, fontWeight: 800 }}>£</span>
                          <input
                            type="number"
                            min={100} max={500}
                            value={editPremiumPrice}
                            onChange={function(e) { setEditPremiumPrice(e.target.value); }}
                            placeholder="100 – 500"
                            style={{
                              flex: 1, background: "#000", border: "1px solid #333",
                              borderRadius: 6, padding: "6px 10px", color: "white",
                              fontSize: 13, outline: "none",
                            }}
                          />
                          {editPremiumPrice && (
                            <button type="button" onClick={function() { setEditPremiumPrice(""); }}
                              style={{
                                background: "transparent", border: "1px solid #333",
                                borderRadius: 6, padding: "5px 9px", color: "#888",
                                fontSize: 10, fontWeight: 700, cursor: "pointer",
                              }}>
                              Remove
                            </button>
                          )}
                        </div>
                        <div style={{ color: "#666", fontSize: 10, marginTop: 6, lineHeight: 1.4 }}>
                          EXCLUSIVE · 50% royalties · Unlimited streams · Beat retired on sale · Leave blank to offer Basic only
                        </div>
                      </>
                    )}
                  </div>
                </div>
              )}
              {/* BPM + Key row */}
              <div style={{ display: "flex", gap: 8, marginBottom: 8 }}>
                <div style={{ flex: 1 }}>
                  <div style={{ color: "#666", fontSize: 11, fontWeight: 700, marginBottom: 4, letterSpacing: 0.5 }}>BPM</div>
                  <input
                    type="number"
                    min={40} max={300}
                    value={editBpm}
                    onChange={e => setEditBpm(e.target.value)}
                    placeholder="e.g. 140"
                    style={{ width: "100%", background: "#1a1a1a", border: "1px solid #333", borderRadius: 10, padding: "10px 14px", color: "white", fontSize: 14, outline: "none", boxSizing: "border-box" }}
                  />
                </div>
                <div style={{ flex: 1 }}>
                  <div style={{ color: "#666", fontSize: 11, fontWeight: 700, marginBottom: 4, letterSpacing: 0.5 }}>KEY</div>
                  <select
                    value={editKey}
                    onChange={e => setEditKey(e.target.value)}
                    style={{ width: "100%", background: "#1a1a1a", border: "1px solid #333", borderRadius: 10, padding: "10px 14px", color: editKey ? "white" : "#555", fontSize: 14, outline: "none", boxSizing: "border-box" }}
                  >
                    <option value="">Select key</option>
                    {["C Major","C Minor","C# Major","C# Minor","D Major","D Minor","D# Major","D# Minor","E Major","E Minor","F Major","F Minor","F# Major","F# Minor","G Major","G Minor","G# Major","G# Minor","A Major","A Minor","A# Major","A# Minor","B Major","B Minor"].map(k => (
                      <option key={k} value={k}>{k}</option>
                    ))}
                  </select>
                </div>
              </div>
              {/* Description */}
              <div style={{ color: "#666", fontSize: 11, fontWeight: 700, marginBottom: 4, letterSpacing: 0.5 }}>DESCRIPTION</div>
              <textarea
                value={editDesc}
                onChange={e => setEditDesc(e.target.value)}
                placeholder="Tell listeners about this beat — mood, inspiration, usage rights..."
                maxLength={500}
                rows={3}
                style={{ width: "100%", background: "#1a1a1a", border: "1px solid #333", borderRadius: 10, padding: "10px 14px", color: "white", fontSize: 13, outline: "none", boxSizing: "border-box", resize: "none", fontFamily: "inherit", marginBottom: 4 }}
              />
              <div style={{ color: "#444", fontSize: 10, textAlign: "right", marginBottom: 12 }}>{editDesc.length}/500</div>

              {/* Preview Start */}
              <div style={{ marginBottom: 12 }}>
                <div style={{ color: "#666", fontSize: 11, fontWeight: 700, marginBottom: 4, letterSpacing: 0.5 }}>
                  PREVIEW START — <span style={{ color: "#C026D3" }}>{editPreviewStart}s</span>
                  <span style={{ color: "#444", fontWeight: 400, marginLeft: 6 }}>45s preview plays from this point</span>
                </div>
                <input
                  type="range" min={0} max={180} step={5}
                  value={editPreviewStart}
                  onChange={e => setEditPreviewStart(parseInt(e.target.value))}
                  style={{ width: "100%", accentColor: "#C026D3" }}
                />
                <div style={{ display: "flex", justifyContent: "space-between", color: "#333", fontSize: 10 }}>
                  <span>0s (start)</span><span>90s</span><span>180s</span>
                </div>
              </div>
              <div style={{ display: "flex", gap: 8 }}>
                <button onClick={handleSave} disabled={saving} style={{ flex: 1, background: "#C026D3", border: "none", borderRadius: 10, color: "white", fontWeight: 800, padding: "11px", fontSize: 14, cursor: "pointer" }}>
                  {saving ? "Saving..." : "Save Changes"}
                </button>
                <button onClick={() => setEditId(null)} style={{ flex: 1, background: "#1a1a1a", border: "1px solid #333", borderRadius: 10, color: "#aaa", fontWeight: 700, padding: "11px", fontSize: 14, cursor: "pointer" }}>
                  Cancel
                </button>
              </div>
            </div>
          ) : (
            <div>
              <div style={{ display: "flex", justifyContent: "space-between", alignItems: "flex-start", marginBottom: 6 }}>
                <div style={{ color: "white", fontWeight: 700, fontSize: 15, flex: 1 }}>{beat.title}</div>
                <div style={{ display: "flex", gap: 8, flexShrink: 0 }}>
                  <button onClick={() => handleEdit(beat)} style={{ background: "#1a1a1a", border: "1px solid #333", borderRadius: 8, color: "#aaa", fontSize: 13, padding: "5px 12px", cursor: "pointer", fontWeight: 600 }}>
                    Edit
                  </button>
                  <button onClick={() => handleDelete(beat.id, beat.title)} style={{ background: "rgba(220,38,38,0.1)", border: "1px solid rgba(220,38,38,0.3)", borderRadius: 8, color: "#F87171", fontSize: 13, padding: "5px 12px", cursor: "pointer", fontWeight: 600 }}>
                    Delete
                  </button>
                </div>
              </div>
              <div style={{ display: "flex", gap: 8, flexWrap: "wrap" }}>
                <div style={{ background: "rgba(245,158,11,0.15)", border: "1px solid rgba(245,158,11,0.3)", borderRadius: 20, padding: "2px 10px", fontSize: 11, color: "#F59E0B", fontWeight: 700 }}>{beat.genre}</div>
                <div style={{ background: "rgba(192,38,211,0.15)", border: "1px solid rgba(192,38,211,0.3)", borderRadius: 20, padding: "2px 10px", fontSize: 11, color: "#C026D3", fontWeight: 700 }}>{beat.price}</div>
                {beat.bpm > 0 && <div style={{ background: "rgba(6,182,212,0.1)", border: "1px solid rgba(6,182,212,0.3)", borderRadius: 20, padding: "2px 10px", fontSize: 11, color: "#06B6D4", fontWeight: 700 }}>{beat.bpm} BPM</div>}
                {beat.key && <div style={{ background: "rgba(34,197,94,0.1)", border: "1px solid rgba(34,197,94,0.3)", borderRadius: 20, padding: "2px 10px", fontSize: 11, color: "#22C55E", fontWeight: 700 }}>{beat.key}</div>}
                <div style={{ background: "rgba(255,255,255,0.05)", borderRadius: 20, padding: "2px 10px", fontSize: 11, color: "#555" }}>{beat.downloads} downloads</div>
              </div>
              {beat.description && <div style={{ color: "#555", fontSize: 12, marginTop: 8, lineHeight: 1.5 }}>{beat.description}</div>}
            </div>
          )}
        </div>
      ))}
    </div>
  );
}




// =============================================================================
// MY PURCHASES — list of all beats the user has purchased a lease for.
// Shows tier, voided state, and lets buyer re-download the contract & MP3.
// =============================================================================
function MyPurchasesSection({ user }) {
  const [leases,  setLeases]  = useState([]);
  const [loading, setLoading] = useState(true);
  const [err,     setErr]     = useState("");
  const [busyId,  setBusyId]  = useState(null);

  const load = function() {
    setLoading(true); setErr("");
    apiFetch("/api/producer/my-leases")
      .then(function(d) { setLeases(Array.isArray(d) ? d : []); setLoading(false); })
      .catch(function(e) { setErr(e.message || "Could not load purchases"); setLoading(false); });
  };
  React.useEffect(function() { load(); }, []);

  async function handleDownloadContract(lease) {
    setBusyId("contract_" + lease.id);
    try { await downloadLeaseContractPdf(lease); }
    catch (e) { alert(e.message || "Contract download failed"); }
    setBusyId(null);
  }

  async function handleDownloadMp3(lease) {
    setBusyId("mp3_" + lease.id);
    try {
      // Build a beat-like object for downloadBeatMp3 which expects { id, title, url }
      await downloadBeatMp3({
        id:    lease.beat_id,
        title: lease.beat_title,
        url:   lease.beat_url,
      });
    } catch (e) {
      alert(e.message || "MP3 download failed");
    }
    setBusyId(null);
  }

  function fmtDate(s) {
    if (!s) return "";
    try {
      var d = new Date(s);
      if (isNaN(d.getTime())) return s;
      return d.toLocaleDateString("en-GB", { day: "numeric", month: "short", year: "numeric" });
    } catch (e) { return s; }
  }

  return (
    <div>
      <div style={{ display: "flex", alignItems: "center", justifyContent: "space-between", marginBottom: 8 }}>
        <div style={{ color: "white", fontWeight: 800, fontSize: 18 }}>My Purchases</div>
        <button onClick={load} disabled={loading} style={{
          background: "transparent", border: "1px solid #333", borderRadius: 10,
          color: loading ? "#444" : "#888", fontSize: 11, fontWeight: 700,
          padding: "5px 12px", cursor: loading ? "default" : "pointer",
        }}>
          {loading ? "Loading…" : "Refresh"}
        </button>
      </div>
      <div style={{ color: "#666", fontSize: 12, marginBottom: 16 }}>
        Every beat lease you’ve bought. Re-download your contracts and MP3s any time.
      </div>

      {loading && (
        <div style={{ color: "#555", fontSize: 13, textAlign: "center", padding: "30px 0" }}>Loading purchases…</div>
      )}

      {!loading && err && (
        <div style={{ background: "rgba(239,68,68,0.08)", border: "1px solid rgba(239,68,68,0.3)", borderRadius: 12, padding: 12, color: "#EF4444", fontSize: 12 }}>
          {err}
        </div>
      )}

      {!loading && !err && leases.length === 0 && (
        <div style={{ background: "#0d0d0d", border: "1px solid #1a1a1a", borderRadius: 14, padding: 28, textAlign: "center" }}>
          <div style={{ fontSize: 30, marginBottom: 10 }}>🎟️</div>
          <div style={{ color: "white", fontWeight: 800, fontSize: 14, marginBottom: 4 }}>No purchases yet</div>
          <div style={{ color: "#666", fontSize: 12 }}>Browse producer profiles and tap a paid beat to buy your first lease.</div>
        </div>
      )}

      {!loading && !err && leases.length > 0 && (
        <div style={{ display: "flex", flexDirection: "column", gap: 10 }}>
          {leases.map(function(lease) {
            var isPremium = (lease.tier === "premium");
            var isVoided = !!lease.voided;
            var accent   = isVoided ? "#EF4444" : (isPremium ? "#A855F7" : "#F59E0B");
            return (
              <div key={lease.id} style={{
                background: "#0d0d0d",
                border: "1px solid " + (isVoided ? "rgba(239,68,68,0.3)" : "#1a1a1a"),
                borderRadius: 14, padding: 14,
                opacity: isVoided ? 0.85 : 1,
              }}>
                <div style={{ display: "flex", alignItems: "flex-start", justifyContent: "space-between", gap: 10, marginBottom: 8 }}>
                  <div style={{ flex: 1, minWidth: 0 }}>
                    <div style={{ color: "white", fontWeight: 800, fontSize: 14, whiteSpace: "nowrap", overflow: "hidden", textOverflow: "ellipsis" }}>
                      {lease.beat_title || "Beat"}
                    </div>
                    <div style={{ color: "#888", fontSize: 11, marginTop: 2 }}>
                      by {lease.producer || "Producer"} · {fmtDate(lease.purchased_at)}
                    </div>
                  </div>
                  <div style={{
                    background: "rgba(" + (isVoided ? "239,68,68" : (isPremium ? "168,85,247" : "245,158,11")) + ",0.15)",
                    border: "1px solid " + accent,
                    color: accent,
                    borderRadius: 20, padding: "3px 10px",
                    fontSize: 10, fontWeight: 800, letterSpacing: 0.5,
                    whiteSpace: "nowrap",
                  }}>
                    {isVoided ? "VOIDED" : (isPremium ? "PREMIUM" : "BASIC")} {lease.price ? "· " + lease.price : ""}
                  </div>
                </div>

                {isVoided ? (
                  <div style={{
                    background: "rgba(239,68,68,0.06)",
                    border: "1px solid rgba(239,68,68,0.2)",
                    borderRadius: 10, padding: 10,
                    color: "#aaa", fontSize: 11, lineHeight: 1.5,
                  }}>
                    <strong style={{ color: "#EF4444" }}>Lease revoked.</strong> The producer sold the exclusive (Premium) lease for this beat to another buyer. As accepted at purchase (Clause 6 of the Basic Lease), no refund is issued and you must cease distribution of any New Work derived from this beat.
                    {lease.voided_at && (
                      <div style={{ color: "#666", marginTop: 6, fontSize: 10 }}>
                        Voided on {fmtDate(lease.voided_at)}
                      </div>
                    )}
                  </div>
                ) : (
                  <div style={{ display: "flex", gap: 8 }}>
                    <button onClick={function() { handleDownloadContract(lease); }} disabled={busyId === ("contract_" + lease.id)} style={{
                      flex: 1,
                      background: "transparent",
                      border: "1px solid #333",
                      borderRadius: 10, padding: "9px 10px",
                      color: busyId === ("contract_" + lease.id) ? "#555" : "#aaa",
                      fontWeight: 700, fontSize: 12,
                      cursor: busyId === ("contract_" + lease.id) ? "default" : "pointer",
                      display: "flex", alignItems: "center", justifyContent: "center", gap: 6,
                    }}>
                      <svg width="12" height="12" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round"><path d="M14 2H6a2 2 0 0 0-2 2v16a2 2 0 0 0 2 2h12a2 2 0 0 0 2-2V8z"/><polyline points="14 2 14 8 20 8"/></svg>
                      {busyId === ("contract_" + lease.id) ? "..." : "Contract"}
                    </button>
                    <button onClick={function() { handleDownloadMp3(lease); }} disabled={busyId === ("mp3_" + lease.id) || !lease.beat_url} style={{
                      flex: 1,
                      background: (busyId === ("mp3_" + lease.id) || !lease.beat_url) ? "#1a1a1a" : "linear-gradient(135deg," + accent + ",#D97706)",
                      border: "none",
                      borderRadius: 10, padding: "9px 10px",
                      color: (busyId === ("mp3_" + lease.id) || !lease.beat_url) ? "#555" : "white",
                      fontWeight: 800, fontSize: 12,
                      cursor: (busyId === ("mp3_" + lease.id) || !lease.beat_url) ? "default" : "pointer",
                      display: "flex", alignItems: "center", justifyContent: "center", gap: 6,
                    }}>
                      <svg width="12" height="12" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round"><path d="M12 3v13M6 11l6 6 6-6"/><path d="M4 20h16"/></svg>
                      {busyId === ("mp3_" + lease.id) ? "..." : "MP3"}
                    </button>
                  </div>
                )}
              </div>
            );
          })}
        </div>
      )}
    </div>
  );
}


// =============================================================================
// ROOT AUTH SCREEN - shown at app root when no user logged in
// =============================================================================
function RootAuthScreen({ onLogin, startMode }) {
  const [mode,        setMode]        = useState(startMode || "landing");
  const [name,        setName]        = useState("");
  const [email,       setEmail]       = useState("");
  const [pw,          setPw]          = useState("");
  const [rememberMe,  setRememberMe]  = useState(false);
  const [authLoading, setAuthLoading] = useState(false);
  const [authErr,     setAuthErr]     = useState("");
  const [selectedPlan,    setSelectedPlan]    = useState("artist");
  const [selectedPriceId, setSelectedPriceId] = useState(PLAN_PRICES.artist.monthly);
  // Signup-only — user must tick this to agree to T&Cs. Reset whenever
  // the user toggles between login and signup so the state can't leak.
  const [acceptTerms, setAcceptTerms] = useState(false);
  // Marketing opt-in — GDPR-strict default OFF. User must explicitly tick.
  const [marketingOptIn, setMarketingOptIn] = useState(false);
  // Show the full T&Cs in a scrollable modal when the user taps the link.
  const [showTermsPreview, setShowTermsPreview] = useState(false);
  // Signup-only: chosen username + live availability check
  const [signupUsername,    setSignupUsername]    = useState("");
  const [signupUsernameStatus, setSignupUsernameStatus] = useState("idle");

  useEffect(() => {
    try {
      const saved = localStorage.getItem("bf_saved_email");
      if (saved) { setEmail(saved); setRememberMe(true); }
    } catch {}
  }, []);

  // Live username-availability check during signup. Debounced 400ms.
  useEffect(() => {
    var u = signupUsername.trim();
    if (!u) { setSignupUsernameStatus("idle"); return; }
    if (u.length < 3 || u.length > 30 || !/^[a-zA-Z0-9_.]+$/.test(u)) {
      setSignupUsernameStatus("invalid");
      return;
    }
    setSignupUsernameStatus("checking");
    var cancelled = false;
    var t = setTimeout(function() {
      apiFetch("/api/auth/search?q=" + encodeURIComponent(u))
        .then(function(matches) {
          if (cancelled) return;
          var taken = (matches || []).some(function(m) {
            return (m.username || "").toLowerCase() === u.toLowerCase();
          });
          setSignupUsernameStatus(taken ? "taken" : "available");
        })
        .catch(function() {
          if (!cancelled) setSignupUsernameStatus("idle");
        });
    }, 400);
    return function() { cancelled = true; clearTimeout(t); };
  }, [signupUsername]);

  const inp = {
    width: "100%", background: "#1a1a1a", border: "1px solid #333",
    borderRadius: 10, padding: "9px 14px", color: "white",
    fontSize: 14, outline: "none", marginBottom: 8, boxSizing: "border-box",
  };

  if (mode === "forgot") return <ForgotPasswordScreen onBack={() => setMode("login")} />;

  if (mode === "landing") return (
    <div style={{ display: "flex", flexDirection: "column", alignItems: "center", justifyContent: "center", height: "100vh", padding: 32, textAlign: "center" }}>
      <img src="https://i.ibb.co/9myqbFB7/2-BB02064-13-F6-476-C-89-FF-B1-EDDAE0-C709.png" alt="BeatFinder" style={{ width: "100%", maxWidth: 300, marginBottom: 32 }} />
      <div style={{ color: "#aaa", fontSize: 14, marginBottom: 36, lineHeight: 1.7 }}>
        Create an account to save beats,<br />or subscribe as an artist or producer.
      </div>
      <button onClick={() => setMode("signup")}
        style={{ width: "100%", background: "#C026D3", border: "none", borderRadius: 32, color: "white", fontWeight: 800, padding: 16, fontSize: 16, cursor: "pointer", marginBottom: 16 }}>
        Create Account
      </button>
      <button onClick={() => setMode("login")}
        style={{ background: "none", border: "none", color: "#06B6D4", fontWeight: 700, fontSize: 15, cursor: "pointer" }}>
        I already have an account
      </button>
    </div>
  );

  return (
    <div style={{ padding: "0 20px 20px" }}>
      <div style={{ color: "white", fontFamily: "'Bebas Neue',sans-serif", fontSize: 22, letterSpacing: 1.5, marginBottom: 12 }}>
        {mode === "signup" ? "CREATE ACCOUNT" : "WELCOME BACK"}
      </div>
      {mode === "signup" && <input value={name} onChange={e => setName(e.target.value)} placeholder="Your name" style={inp} />}
      {mode === "signup" && (
        <>
          <input
            value={signupUsername}
            onChange={e => setSignupUsername(e.target.value)}
            placeholder="Choose a username"
            autoCapitalize="none"
            autoCorrect="off"
            spellCheck={false}
            style={{ ...inp, marginBottom: 4 }}
          />
          <div style={{ minHeight: 12, marginBottom: 8, paddingLeft: 4, fontSize: 10, lineHeight: "12px" }}>
            {signupUsernameStatus === "idle" && (
              <span style={{ color: "#555" }}>3–30 characters · letters, numbers, dots, underscores</span>
            )}
            {signupUsernameStatus === "invalid" && (
              <span style={{ color: "#F87171" }}>Must be 3–30 characters · letters, numbers, dots, underscores only</span>
            )}
            {signupUsernameStatus === "checking" && (
              <span style={{ color: "#888" }}>Checking availability…</span>
            )}
            {signupUsernameStatus === "available" && (
              <span style={{ color: "#10B981", fontWeight: 700 }}>✓ @{signupUsername.trim()} is available</span>
            )}
            {signupUsernameStatus === "taken" && (
              <span style={{ color: "#F87171", fontWeight: 700 }}>✗ @{signupUsername.trim()} is already taken</span>
            )}
          </div>
        </>
      )}
      <input value={email} onChange={e => setEmail(e.target.value)} placeholder="Email address or Username" type="email" style={inp} />
      <input value={pw} onChange={e => setPw(e.target.value)} placeholder="Password" type="password" style={{ ...inp, marginBottom: 10 }} />
      {mode === "login" && (
        <div style={{ display: "flex", alignItems: "center", gap: 10, marginBottom: 12 }}>
          <input type="checkbox" checked={rememberMe} onChange={e => setRememberMe(e.target.checked)} id="rm" />
          <label htmlFor="rm" style={{ color: "#888", fontSize: 13 }}>Remember my login</label>
        </div>
      )}
      {/* Plan picker — signup only */}
      {mode === "signup" && (
        <div style={{ marginBottom: 10 }}>
          <div style={{ color: "#888", fontSize: 10, fontWeight: 700, letterSpacing: 1, marginBottom: 6 }}>CHOOSE YOUR PLAN</div>
          <PlanPicker compact selectedPlanId={selectedPlan} onSelectPlan={function(planId, priceId) {
            setSelectedPlan(planId);
            setSelectedPriceId(priceId);
          }} />
          <div style={{ color: "#444", fontSize: 9, marginTop: 4, textAlign: "center" }}>
            {selectedPlan === "free"
              ? "Upgrade any time from Profile."
              : "You'll be taken to Stripe to complete payment."}
          </div>
        </div>
      )}

      {/* T&Cs acceptance — required to sign up. This is the legal moment
          of contract formation: ticking the box + clicking Create binds
          the user to the Terms at the version-bumped current revision.
          The acceptance is recorded server-side against the new account. */}
      {mode === "signup" && (
        <div style={{
          marginBottom: 10,
          padding: "8px 10px",
          background: acceptTerms ? "rgba(124,58,237,0.08)" : "rgba(0,0,0,0.3)",
          border: "1px solid " + (acceptTerms ? "rgba(124,58,237,0.35)" : "rgba(124,58,237,0.18)"),
          borderRadius: 10,
          transition: "background 0.15s, border 0.15s",
        }}>
          <label htmlFor="bf_accept_terms"
            style={{ display: "flex", alignItems: "flex-start", gap: 8, cursor: "pointer",
              userSelect: "none", WebkitUserSelect: "none" }}>
            <input
              id="bf_accept_terms"
              type="checkbox"
              checked={acceptTerms}
              onChange={e => setAcceptTerms(e.target.checked)}
              style={{
                width: 16, height: 16, marginTop: 1, flexShrink: 0,
                accentColor: "#7C3AED", cursor: "pointer",
              }}
            />
            <span style={{ color: "#ccc", fontSize: 11, lineHeight: 1.35 }}>
              I agree to{" "}
              <span onClick={e => { e.preventDefault(); e.stopPropagation(); setShowTermsPreview(true); }}
                style={{ color: "#A78BFA", fontWeight: 700, textDecoration: "underline", cursor: "pointer" }}>
                Terms &amp; Conditions
              </span>
              . I'm at least 16.
            </span>
          </label>
          {/* Marketing opt-in — defaults OFF (GDPR). No legal implication
              of leaving it unticked; user can still receive transactional
              emails (receipts, password resets). */}
          <label htmlFor="bf_marketing_optin"
            style={{ display: "flex", alignItems: "flex-start", gap: 8, cursor: "pointer",
              userSelect: "none", WebkitUserSelect: "none", marginTop: 10 }}>
            <input
              id="bf_marketing_optin"
              type="checkbox"
              checked={marketingOptIn}
              onChange={e => setMarketingOptIn(e.target.checked)}
              style={{
                width: 16, height: 16, marginTop: 1, flexShrink: 0,
                accentColor: "#7C3AED", cursor: "pointer",
              }}
            />
            <span style={{ color: "#888", fontSize: 11, lineHeight: 1.35 }}>
              Send me occasional updates about BeatFinder (new features, beats, producer spotlights).
            </span>
          </label>
        </div>
      )}

      <button
        disabled={authLoading}
        onClick={async () => {
          setAuthErr("");
          if (mode === "signup" && !name.trim())  { setAuthErr("Please enter your name"); return; }
          if (mode === "signup" && !signupUsername.trim()) { setAuthErr("Please choose a username"); return; }
          if (mode === "signup" && signupUsernameStatus === "invalid") {
            setAuthErr("Username must be 3–30 characters, letters/numbers/dots/underscores only"); return;
          }
          if (mode === "signup" && signupUsernameStatus === "taken") {
            setAuthErr("That username is already taken"); return;
          }
          if (mode === "signup" && !email.trim()) { setAuthErr("Please enter your email"); return; }
          if (mode === "signup" && !pw.trim())    { setAuthErr("Please enter a password"); return; }
          if (mode === "signup" && !acceptTerms)  { setAuthErr("Please accept the Terms & Conditions to continue"); return; }
          if (mode === "login" && !pw.trim()) { setAuthErr("Please enter your password"); return; }
          setAuthLoading(true);
          try {
            const u = mode === "signup"
              ? await AuthAPI.register(name || email.split("@")[0], email, pw, marketingOptIn)
              : await AuthAPI.login(email, pw);
            // Persist chosen username on the new account. See companion
            // comment in ProfileScreen — without this call, new users
            // end up with no public profile / no searchable handle.
            if (mode === "signup" && u && signupUsername.trim()) {
              const desired = signupUsername.trim();
              try {
                await apiFetch("/api/auth/set-username", {
                  method: "POST",
                  body: JSON.stringify({ username: desired }),
                });
                u.username = desired;
              } catch (unameErr) {
                setAuthErr("Account created, but username '" + desired + "' couldn't be set: " + (unameErr.message || "try a different one in Settings"));
              }
            }
            // Record T&Cs acceptance immediately on signup. We do this
            // right after the register call so the server has an audit
            // trail tying the accepted version to the new account at
            // the moment of creation.
            if (mode === "signup" && u) {
              try {
                await apiFetch("/api/auth/accept-terms", {
                  method: "POST",
                  body: JSON.stringify({ version: TERMS_VERSION }),
                });
                u.terms_accepted_version = TERMS_VERSION;
              } catch (termsErr) {
                console.error("[BF terms] signup accept failed", termsErr);
                // Non-blocking — registration succeeded. The version-mismatch
                // modal will catch it on next load and re-prompt them.
              }
            }
            if (rememberMe) {
              try { localStorage.setItem("bf_saved_email", email); } catch {}
            } else {
              try { localStorage.removeItem("bf_saved_email"); } catch {}
            }
            // After signup, redirect to Stripe checkout (unless Free plan)
            if (mode === "signup") {
              onLogin(u);
              // Free plan — skip Stripe and finish immediately
              if (selectedPlan === "free" || !selectedPriceId) {
                return;
              }
              try {
                const r = await apiFetch("/api/stripe/create-checkout", {
                  method: "POST",
                  body: JSON.stringify({ price_id: selectedPriceId }),
                });
                try { var ct = sessionStorage.getItem("bf_tab") || "exclusive"; sessionStorage.setItem("bf_return_tab", JSON.stringify({ tab: ct, path: window.location.pathname + window.location.search, ts: Date.now() })); } catch(e) {} window.location.href = r.checkout_url;
              } catch(e) {
                // Checkout failed — user still has account, they can subscribe from profile
                setAuthErr("Account created! To subscribe, go to your Profile.");
              }
            } else {
              onLogin(u);
            }
          } catch (e) {
            setAuthErr(e.message);
          } finally {
            setAuthLoading(false);
          }
        }}
        style={{ width: "100%", background: authLoading ? "#555" : "#C026D3", border: "none", borderRadius: 28, color: "white", fontWeight: 800, padding: 11, fontSize: 14, cursor: "pointer", opacity: authLoading ? 0.6 : 1 }}>
        {authLoading
          ? "Please wait..."
          : mode === "signup"
            ? (selectedPlan === "free" ? "Create Free Account" : "Create Account & Subscribe")
            : "Log In"}
      </button>
      {authErr && <div style={{ color: "#F87171", fontSize: 11, textAlign: "center", marginTop: 8 }}>{authErr}</div>}
      <div style={{ textAlign: "center", marginTop: 10 }}>
        <span style={{ color: "#888", fontSize: 12 }}>{mode === "signup" ? "Already have an account? " : "No account? "}</span>
        <button onClick={() => { setAuthErr(""); setAcceptTerms(false); setMode(mode === "signup" ? "login" : "signup"); }}
          style={{ background: "none", border: "none", color: "#06B6D4", fontWeight: 700, fontSize: 12, cursor: "pointer" }}>
          {mode === "signup" ? "Log In" : "Sign Up"}
        </button>
      </div>
      {mode === "login" && (
        <div style={{ textAlign: "center", marginTop: 8 }}>
          <button onClick={e => { e.preventDefault(); e.stopPropagation(); setMode("forgot"); }}
            style={{ background: "none", border: "none", color: "#666", fontSize: 12, cursor: "pointer", textDecoration: "underline" }}>
            Forgot your password?
          </button>
        </div>
      )}

      {/* T&Cs preview modal — opens when the user taps the link
          inside the signup checkbox row. Read-only; closes on tap. */}
      {showTermsPreview && (
        <div onClick={() => setShowTermsPreview(false)} style={{
          position: "fixed", inset: 0, zIndex: 99998,
          background: "rgba(0,0,0,0.85)",
          display: "flex", alignItems: "center", justifyContent: "center",
          padding: 20,
          backdropFilter: "blur(4px)",
          fontFamily: "'DM Sans',sans-serif",
        }}>
          <div onClick={e => e.stopPropagation()} style={{
            background: "linear-gradient(165deg,#0f0a1f 0%,#0a0a14 60%,#080812 100%)",
            border: "1px solid rgba(124,58,237,0.3)",
            borderRadius: 18,
            maxWidth: 520, width: "100%", maxHeight: "85vh",
            display: "flex", flexDirection: "column",
            boxShadow: "0 16px 40px rgba(0,0,0,0.75), 0 0 24px rgba(124,58,237,0.15)",
            position: "relative", overflow: "hidden",
          }}>
            {/* LED top edge */}
            <div style={{
              position: "absolute", top: 0, left: 0, right: 0, height: 2,
              background: "linear-gradient(90deg,transparent,#C026D3,#7C3AED,#3B82F6,transparent)",
              boxShadow: "0 0 8px rgba(124,58,237,0.7)",
            }} />
            {/* Header */}
            <div style={{
              display: "flex", alignItems: "center", justifyContent: "space-between",
              padding: "16px 20px", borderBottom: "1px solid rgba(124,58,237,0.15)",
              flexShrink: 0,
            }}>
              <div>
                <div style={{
                  color: "white", fontSize: 20, fontWeight: 800,
                  fontFamily: "'Bebas Neue',sans-serif", letterSpacing: 1,
                }}>TERMS &amp; CONDITIONS</div>
                <div style={{ color: "#888", fontSize: 11, marginTop: 2 }}>
                  Version {TERMS_VERSION}
                </div>
              </div>
              <button onClick={() => setShowTermsPreview(false)} style={{
                width: 32, height: 32, borderRadius: "50%",
                background: "rgba(255,255,255,0.04)",
                border: "1px solid #2a2a2a", color: "#888",
                cursor: "pointer", flexShrink: 0,
                display: "flex", alignItems: "center", justifyContent: "center",
              }}>
                <svg width="12" height="12" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round">
                  <line x1="18" y1="6" x2="6" y2="18"/><line x1="6" y1="6" x2="18" y2="18"/>
                </svg>
              </button>
            </div>
            {/* Scrollable T&Cs body */}
            <div style={{
              flex: 1, overflowY: "auto", WebkitOverflowScrolling: "touch",
              padding: "16px 20px",
            }}>
              {TERMS_CONTENT.map(function(sec, i) {
                return (
                  <div key={i} style={{ marginBottom: 18 }}>
                    <div style={{
                      color: "#A78BFA", fontSize: 12, fontWeight: 900, letterSpacing: 1,
                      marginBottom: 6,
                    }}>{sec.title}</div>
                    <div style={{ color: "#bbb", fontSize: 12, lineHeight: 1.65 }}>
                      {sec.body}
                    </div>
                  </div>
                );
              })}
            </div>
            {/* Footer */}
            <div style={{
              padding: "12px 20px",
              borderTop: "1px solid rgba(124,58,237,0.15)",
              flexShrink: 0,
            }}>
              <button onClick={() => setShowTermsPreview(false)} style={{
                width: "100%", padding: "12px",
                background: "linear-gradient(135deg,#C026D3,#7C3AED)",
                border: "none", borderRadius: 12,
                color: "white", fontWeight: 800, fontSize: 14, letterSpacing: 0.5,
                cursor: "pointer",
                boxShadow: "0 4px 14px rgba(124,58,237,0.4)",
              }}>
                Close
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}

// =============================================================================
// RESET PASSWORD SCREEN - shown when user visits app with ?reset_token=
// =============================================================================
function ResetPasswordScreen({ token, onDone }) {
  const [newPw,    setNewPw]    = useState("");
  const [confirmPw,setConfirmPw]= useState("");
  const [loading,  setLoading]  = useState(false);
  const [msg,      setMsg]      = useState("");
  const [done,     setDone]     = useState(false);

  const inp = {
    width: "100%", background: "#1a1a1a", border: "1px solid #333",
    borderRadius: 12, padding: "14px 16px", color: "white",
    fontSize: 16, outline: "none", marginBottom: 14, boxSizing: "border-box",
  };

  const handleReset = async () => {
    if (!newPw || !confirmPw) return;
    if (newPw !== confirmPw) { setMsg("Passwords do not match"); return; }
    if (newPw.length < 6)    { setMsg("Password must be at least 6 characters"); return; }
    setLoading(true);
    setMsg("");
    try {
      await apiFetch("/api/auth/reset-password", {
        method: "POST",
        body: JSON.stringify({ token, new_password: newPw }),
      });
      setDone(true);
    } catch (e) {
      setMsg("Error: " + e.message);
    } finally {
      setLoading(false);
    }
  };

  return (
    <div style={{ padding: "60px 24px 100px", fontFamily: "'DM Sans',sans-serif" }}>
      <div style={{ color: "white", fontFamily: "'Bebas Neue',sans-serif", fontSize: 30, letterSpacing: 2, marginBottom: 8 }}>
        RESET PASSWORD
      </div>
      <div style={{ color: "#888", fontSize: 14, lineHeight: 1.7, marginBottom: 28 }}>
        Enter your new password below.
      </div>

      {!done ? (
        <>
          <input value={newPw}     onChange={e => setNewPw(e.target.value)}     type="password" placeholder="New password"     style={inp} />
          <input value={confirmPw} onChange={e => setConfirmPw(e.target.value)} type="password" placeholder="Confirm password" style={inp} />
          {msg && <div style={{ color: msg.startsWith("Error") ? "#F87171" : "#22C55E", fontSize: 13, marginBottom: 12 }}>{msg}</div>}
          <button onClick={handleReset} disabled={loading} style={{ width: "100%", background: loading ? "#333" : "#C026D3", border: "none", borderRadius: 32, color: "white", fontWeight: 800, padding: 16, fontSize: 16, cursor: loading ? "not-allowed" : "pointer" }}>
            {loading ? "Resetting..." : "Set New Password"}
          </button>
        </>
      ) : (
        <div style={{ textAlign: "center" }}>
        <div style={{ fontSize: 48, marginBottom: 16 }}><AppIcon id="knobs" size={20}/></div>
          <div style={{ color: "#22C55E", fontWeight: 800, fontSize: 18, marginBottom: 8 }}>Password Reset!</div>
          <div style={{ color: "#888", fontSize: 14, marginBottom: 24 }}>You can now log in with your new password.</div>
          <button onClick={onDone} style={{ background: "#C026D3", border: "none", borderRadius: 32, color: "white", fontWeight: 800, padding: "14px 40px", fontSize: 16, cursor: "pointer" }}>
            Go to Log In
          </button>
        </div>
      )}
    </div>
  );
}

// =============================================================================
// FORGOT PASSWORD SCREEN
// =============================================================================
function ForgotPasswordScreen({ onBack }) {
  const [email,   setEmail]   = useState("");
  const [loading, setLoading] = useState(false);
  const [msg,     setMsg]     = useState("");
  const [sent,    setSent]    = useState(false);

  const handleSubmit = async () => {
    if (!email.trim()) return;
    setLoading(true);
    setMsg("");
    try {
      await apiFetch("/api/auth/forgot-password", {
        method: "POST",
        body: JSON.stringify({ email: email.trim() }),
      });
      setSent(true);
      setMsg("Reset link sent! Check your email inbox.");
    } catch (e) {
      setMsg("Error: " + e.message);
    } finally {
      setLoading(false);
    }
  };

  const inp = {
    width: "100%", background: "#1a1a1a", border: "1px solid #333",
    borderRadius: 12, padding: "14px 16px", color: "white",
    fontSize: 16, outline: "none", marginBottom: 16, boxSizing: "border-box",
  };

  return (
    <div style={{ padding: "40px 24px 100px" }}>
      <button onClick={onBack} style={{ background: "none", border: "none", color: "white", fontSize: 28, cursor: "pointer", marginBottom: 20 }}>←</button>
      <div style={{ color: "white", fontFamily: "'Bebas Neue',sans-serif", fontSize: 30, letterSpacing: 2, marginBottom: 8 }}>
        FORGOT PASSWORD
      </div>
      <div style={{ color: "#888", fontSize: 14, lineHeight: 1.7, marginBottom: 28 }}>
        Enter the email address you used to sign up and we'll send you a link to reset your password.
      </div>

      {!sent ? (
        <>
          <input
            value={email}
            onChange={e => setEmail(e.target.value)}
            placeholder="Your email address"
            type="email"
            style={inp}
          />
          <button
            onClick={handleSubmit}
            disabled={loading}
            style={{ width: "100%", background: loading ? "#333" : "#C026D3", border: "none", borderRadius: 32, color: "white", fontWeight: 800, padding: 16, fontSize: 16, cursor: loading ? "not-allowed" : "pointer" }}
          >
            {loading ? "Sending..." : "Send Reset Link"}
          </button>
        </>
      ) : (
        <div style={{ background: "rgba(34,197,94,0.1)", border: "1px solid rgba(34,197,94,0.3)", borderRadius: 14, padding: 20, textAlign: "center" }}>
          <div style={{ fontSize: 40, marginBottom: 12 }}><AppIcon id="folder" size={20}/></div>
          <div style={{ color: "#22C55E", fontWeight: 800, fontSize: 16, marginBottom: 8 }}>Email Sent!</div>
          <div style={{ color: "#888", fontSize: 14, lineHeight: 1.7 }}>
            Check your inbox at <span style={{ color: "white", fontWeight: 600 }}>{email}</span> for a password reset link. It expires in 1 hour.
          </div>
        </div>
      )}

      {msg && !sent && (
        <div style={{ color: msg.startsWith("Error") ? "#F87171" : "#22C55E", fontSize: 13, textAlign: "center", marginTop: 12 }}>{msg}</div>
      )}

      <div style={{ textAlign: "center", marginTop: 24 }}>
        <button onClick={onBack} style={{ background: "none", border: "none", color: "#06B6D4", fontSize: 14, fontWeight: 700, cursor: "pointer" }}>
          Back to Log In
        </button>
      </div>
    </div>
  );
}

// =============================================================================
// PROFILE SCREEN
// =============================================================================
function SectionBack({ label, onBack }) {
  return (
    <button onClick={onBack}
      style={{ display: "flex", alignItems: "center", gap: 8, background: "none", border: "none", color: "#C026D3", fontWeight: 700, fontSize: 14, cursor: "pointer", padding: "0 0 16px" }}>
      &#8592; {label}
    </button>
  );
}


// =============================================================================
// POST MUSIC SECTION — paste Spotify link
// =============================================================================
function PostMusicSection({ user, onBack }) {
  var [url,       setUrl]       = React.useState("");
  var [caption,   setCaption]   = React.useState("");
  var [loading,   setLoading]   = React.useState(false);
  var [msg,       setMsg]       = React.useState("");
  var [posts,     setPosts]     = React.useState([]);
  // Song upload (R2)
  var [songTitle, setSongTitle] = React.useState("");
  var [songFile,  setSongFile]  = React.useState(null);
  var [artFile,   setArtFile]   = React.useState(null);
  var [upLoading, setUpLoading] = React.useState(false);
  var [upMsg,     setUpMsg]     = React.useState("");
  var songFileRef = React.useRef(null);
  var artFileRef  = React.useRef(null);

  React.useEffect(function() {
    if (!user) return;
    apiFetch("/api/content/profile/" + encodeURIComponent(user.username) + "?type=music")
      .then(function(data) { setPosts(data || []); })
      .catch(function() {});
  }, [user]);

  function submit() {
    if (!url.trim()) return;
    setLoading(true); setMsg("");
    apiFetch("/api/content/music", {
      method: "POST",
      body: JSON.stringify({ spotifyUrl: url.trim(), caption: caption.trim() })
    }).then(function(data) {
      setPosts(function(prev) { return [data, ...prev]; });
      setUrl(""); setCaption("");
      setMsg("Posted successfully!");
      setLoading(false);
      setTimeout(function() { setMsg(""); }, 3000);
    }).catch(function(err) {
      setMsg("Error: " + err.message);
      setLoading(false);
    });
  }

  function uploadSong() {
    if (!songFile || upLoading) return;
    var uploadedCount = posts.filter(function(p){ return p.audioUrl; }).length;
    if (uploadedCount >= 5) { setUpMsg("Upload limit reached — you can upload up to 5 songs. Delete one to add another."); return; }
    if (songFile.size > 20 * 1024 * 1024) { setUpMsg("Song too large — max 20MB (this file: " + (songFile.size/1048576).toFixed(1) + "MB)"); return; }
    if (artFile && artFile.size > 1 * 1024 * 1024) { setUpMsg("Artwork too large — please use an image under 1MB"); return; }
    setUpLoading(true); setUpMsg("");
    var fd = new FormData();
    fd.append("file", songFile);
    if (artFile) fd.append("artwork", artFile);
    var qs = "?title=" + encodeURIComponent(songTitle.trim()) + "&artist=" + encodeURIComponent(user && user.name ? user.name : (user && user.username ? user.username : ""));
    fetch(API_BASE + "/api/content/song" + qs, {
      method: "POST",
      headers: { Authorization: "Bearer " + (getToken() || "") },
      body: fd,
    })
      .then(function(r) { return r.json().then(function(d){ return { ok: r.ok, d: d }; }); })
      .then(function(res) {
        if (!res.ok) throw new Error(res.d && res.d.detail ? res.d.detail : "Upload failed");
        setPosts(function(prev) { return [res.d, ...prev]; });
        setSongFile(null); setArtFile(null); setSongTitle("");
        if (songFileRef.current) songFileRef.current.value = "";
        if (artFileRef.current) artFileRef.current.value = "";
        setUpMsg("Song uploaded!");
        setUpLoading(false);
        setTimeout(function() { setUpMsg(""); }, 3000);
      })
      .catch(function(err) { setUpMsg("Error: " + (err.message || "Upload failed")); setUpLoading(false); });
  }

  function deletePost(id) {
    if (!window.confirm("Delete this track? This cannot be undone.")) return;
    apiFetch("/api/content/" + id, { method: "DELETE" })
      .then(function() {
        setPosts(function(prev) { return prev.filter(function(p) { return p.id !== id; }); });
        dispatchContentDeleted(id, "music");
      })
      .catch(function() {});
  }

  return (
    <div>
      <SectionBack onBack={onBack} label="Back to Dashboard" />
      <div style={{ color: "white", fontWeight: 800, fontSize: 20, marginBottom: 6 }}>Post Music</div>
      <div style={{ color: "#555", fontSize: 13, marginBottom: 20 }}>Share a Spotify track on your profile</div>

      <div style={{ background: "#111", borderRadius: 14, padding: 16, border: "1px solid #1e1e1e", marginBottom: 20 }}>
        <div style={{ color: "#888", fontSize: 12, marginBottom: 8 }}>Spotify Track URL</div>
        <input value={url} onChange={function(e) { setUrl(e.target.value); }}
          placeholder="https://open.spotify.com/track/..."
          style={{ width: "100%", background: "#0a0a0a", border: "1px solid #2a2a2a", borderRadius: 10,
            padding: "12px 14px", color: "white", fontSize: 14, outline: "none", boxSizing: "border-box", marginBottom: 12 }} />

        <div style={{ color: "#888", fontSize: 12, marginBottom: 8 }}>Caption (optional)</div>
        <textarea value={caption} onChange={function(e) { setCaption(e.target.value); }}
          placeholder="Say something about this track..."
          rows={3}
          style={{ width: "100%", background: "#0a0a0a", border: "1px solid #2a2a2a", borderRadius: 10,
            padding: "12px 14px", color: "white", fontSize: 14, outline: "none",
            resize: "none", boxSizing: "border-box", fontFamily: "inherit", marginBottom: 14 }} />

        <button onClick={submit} disabled={loading || !url.trim()}
          style={{ width: "100%", background: "linear-gradient(135deg,#1DB954,#22C55E)",
            border: "none", borderRadius: 12, color: "white", fontWeight: 800,
            fontSize: 15, padding: "14px", cursor: "pointer", opacity: loading ? 0.7 : 1 }}>
          {loading ? "Posting..." : "Post to Profile"}
        </button>

        {msg && <div style={{ color: msg.startsWith("Error") ? "#EF4444" : "#22C55E", fontSize: 13, marginTop: 10, textAlign: "center" }}>{msg}</div>}
      </div>

      {/* Upload a song from device (stored on R2, free streaming) */}
      <div style={{ background: "#111", borderRadius: 14, padding: 16, border: "1px solid rgba(176,38,255,0.25)", marginBottom: 20 }}>
        <div style={{ color: "#E9D5FF", fontWeight: 800, fontSize: 15, marginBottom: 4 }}>Or upload a song</div>
        <div style={{ color: "#777", fontSize: 12, marginBottom: 14 }}>MP3, WAV or M4A · up to 20MB · artwork under 1MB · {Math.max(0, 5 - posts.filter(function(p){ return p.audioUrl; }).length)} of 5 uploads left</div>

        <input ref={songFileRef} type="file" accept="audio/*,.mp3,.wav,.m4a" style={{ display: "none" }}
          onChange={function(e){ var f = e.target.files && e.target.files[0]; setSongFile(f || null); if (f && !songTitle) setSongTitle((f.name || "").replace(/\.[^.]+$/, "")); }} />
        <input ref={artFileRef} type="file" accept="image/*" style={{ display: "none" }}
          onChange={function(e){ var f = (e.target.files && e.target.files[0]) || null; if (f && f.size > 1 * 1024 * 1024) { setUpMsg("Artwork too large — please use an image under 1MB"); setArtFile(null); e.target.value = ""; return; } setUpMsg(""); setArtFile(f); }} />

        <div style={{ color: "#888", fontSize: 12, marginBottom: 6 }}>Song title</div>
        <input value={songTitle} onChange={function(e){ setSongTitle(e.target.value); }}
          placeholder="Track title"
          style={{ width: "100%", background: "#0a0a0a", border: "1px solid #2a2a2a", borderRadius: 10, padding: "12px 14px", color: "white", outline: "none", boxSizing: "border-box", marginBottom: 12 }} />

        <div style={{ display: "flex", gap: 10, marginBottom: 12 }}>
          <button onClick={function(){ songFileRef.current && songFileRef.current.click(); }}
            style={{ flex: 1, padding: "11px", borderRadius: 10, fontWeight: 700, fontSize: 13, cursor: "pointer",
              background: songFile ? "rgba(34,197,94,0.12)" : "rgba(255,255,255,0.04)",
              border: "1px solid " + (songFile ? "rgba(34,197,94,0.5)" : "#2a2a2a"),
              color: songFile ? "#4ADE80" : "#bbb", overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" }}>
            {songFile ? ("\u2713 " + songFile.name) : "Choose audio file"}
          </button>
          <button onClick={function(){ artFileRef.current && artFileRef.current.click(); }}
            style={{ flexShrink: 0, padding: "11px 14px", borderRadius: 10, fontWeight: 700, fontSize: 13, cursor: "pointer",
              background: artFile ? "rgba(176,38,255,0.12)" : "rgba(255,255,255,0.04)",
              border: "1px solid " + (artFile ? "rgba(176,38,255,0.5)" : "#2a2a2a"),
              color: artFile ? "#E9D5FF" : "#bbb" }}>
            {artFile ? "\u2713 Art" : "+ Artwork"}
          </button>
        </div>

        <button onClick={uploadSong} disabled={upLoading || !songFile}
          style={{ width: "100%", padding: "14px", borderRadius: 12, fontWeight: 800, fontSize: 15,
            cursor: (upLoading || !songFile) ? "not-allowed" : "pointer",
            background: (upLoading || !songFile) ? "#1a1a1a" : "linear-gradient(135deg,#B026FF,#7D3CFF)",
            border: "none", color: (upLoading || !songFile) ? "#555" : "white" }}>
          {upLoading ? "Uploading..." : "Upload Song"}
        </button>
        {upMsg && <div style={{ color: upMsg.startsWith("Error") ? "#EF4444" : "#22C55E", fontSize: 13, marginTop: 10, textAlign: "center" }}>{upMsg}</div>}
      </div>

      {posts.length > 0 && (
        <div>
          <div style={{ color: "#888", fontSize: 11, fontWeight: 700, letterSpacing: 1, marginBottom: 12 }}>YOUR MUSIC POSTS</div>
          {posts.map(function(p) {
            return (
              <div key={p.id} style={{ background: "#111", borderRadius: 14, border: "1px solid #1e1e1e", marginBottom: 12, overflow: "hidden" }}>
                {p.audioUrl
                  ? <SongPlayer src={p.audioUrl} title={p.title} artist={p.artist} artwork={p.songArtwork} />
                  : <SpotifyEmbed
                      embedUrl={p.embedUrl}
                      itemId={p.id}
                      height={152}
                      style={{ display: "block" }}
                    />}
                <div style={{ padding: "10px 14px", display: "flex", justifyContent: "space-between", alignItems: "center" }}>
                  {p.caption && <div style={{ color: "#aaa", fontSize: 13, flex: 1 }}>{p.caption}</div>}
                  <button onClick={function() { deletePost(p.id); }}
                    style={{ background: "rgba(220,38,38,0.1)", border: "1px solid rgba(220,38,38,0.3)",
                      borderRadius: 8, color: "#F87171", fontSize: 12, padding: "4px 10px", cursor: "pointer", flexShrink: 0 }}>
                    Delete
                  </button>
                </div>
              </div>
            );
          })}
        </div>
      )}
    </div>
  );
}


// =============================================================================
// POST VIDEO SECTION — upload from device
// =============================================================================
function PostVideoSection({ user, onBack }) {
  var [caption,   setCaption]   = React.useState("");
  var [ytUrl,     setYtUrl]     = React.useState("");
  var [loading,   setLoading]   = React.useState(false);
  var [msg,       setMsg]       = React.useState("");
  var [posts,     setPosts]     = React.useState([]);
  var fileRef                   = React.useRef(null);

  function postYouTube() {
    var url = ytUrl.trim();
    if (!url || loading) return;
    setLoading(true); setMsg("");
    apiFetch("/api/content/video-link", { method: "POST", body: JSON.stringify({ url: url, caption: caption.trim() }) })
      .then(function(data) {
        if (data && data.detail) throw new Error(data.detail);
        setPosts(function(prev) { return [data, ...prev]; });
        setCaption(""); setYtUrl("");
        setMsg("Video posted!");
        setLoading(false);
        setTimeout(function() { setMsg(""); }, 3000);
      })
      .catch(function(err) { setMsg("Error: " + (err.message || "Could not post")); setLoading(false); });
  }

  React.useEffect(function() {
    if (!user) return;
    apiFetch("/api/content/profile/" + encodeURIComponent(user.username) + "?type=video")
      .then(function(data) { setPosts(data || []); })
      .catch(function() {});
  }, [user]);

  function upload(file) {
    if (!file) return;
    if (file.size > 100 * 1024 * 1024) { setMsg("Video too large — max 100MB"); return; }
    setLoading(true); setMsg("");
    var fd = new FormData();
    fd.append("file", file);
    fd.append("caption", caption.trim());
    var token = getToken() || "";
    fetch(API_BASE + "/api/content/video", {
      method: "POST",
      headers: { Authorization: "Bearer " + token },
      body: fd,
    })
      .then(function(r) { return r.json(); })
      .then(function(data) {
        if (data.detail) throw new Error(data.detail);
        setPosts(function(prev) { return [data, ...prev]; });
        setCaption("");
        setMsg("Video posted!");
        setLoading(false);
        setTimeout(function() { setMsg(""); }, 3000);
      })
      .catch(function(err) {
        setMsg("Error: " + err.message);
        setLoading(false);
      });
    if (fileRef.current) fileRef.current.value = "";
  }

  function deletePost(id) {
    if (!window.confirm("Delete this video? This cannot be undone.")) return;
    apiFetch("/api/content/" + id, { method: "DELETE" })
      .then(function() {
        setPosts(function(prev) { return prev.filter(function(p) { return p.id !== id; }); });
        dispatchContentDeleted(id, "video");
      })
      .catch(function() {});
  }

  return (
    <div>
      <SectionBack onBack={onBack} label="Back to Dashboard" />
      <div style={{ color: "white", fontWeight: 800, fontSize: 20, marginBottom: 6 }}>Post a Video</div>
      <div style={{ color: "#555", fontSize: 13, marginBottom: 20 }}>Share a video clip on your profile</div>

      <input ref={fileRef} type="file" accept="video/*" style={{ display: "none" }}
        onChange={function(e) { upload(e.target.files && e.target.files[0]); }} />

      <div style={{ background: "#111", borderRadius: 14, padding: 16, border: "1px solid #1e1e1e", marginBottom: 20 }}>
        <div style={{ color: "#888", fontSize: 12, marginBottom: 8 }}>Caption (optional)</div>
        <textarea value={caption} onChange={function(e) { setCaption(e.target.value); }}
          placeholder="Say something about this video..."
          rows={3}
          style={{ width: "100%", background: "#0a0a0a", border: "1px solid #2a2a2a", borderRadius: 10,
            padding: "12px 14px", color: "white", fontSize: 14, outline: "none",
            resize: "none", boxSizing: "border-box", fontFamily: "inherit", marginBottom: 14 }} />

        <button onClick={function() { if (fileRef.current) fileRef.current.click(); }}
          disabled={loading}
          style={{
            width: "100%",
            background: loading
              ? "linear-gradient(135deg, #0a0a0a 0%, #0f0f12 100%)"
              : "linear-gradient(135deg, #A855F7 0%, #6366F1 60%, #3B82F6 100%)",
            border: loading ? "1px solid rgba(168,85,247,0.4)" : "none",
            borderRadius: 12, color: "white", fontWeight: 800,
            fontSize: 15, padding: loading ? "12px" : "14px",
            cursor: loading ? "not-allowed" : "pointer",
            boxShadow: loading
              ? "0 0 20px rgba(168,85,247,0.35)"
              : "0 4px 18px rgba(168,85,247,0.4)",
            display: "flex", alignItems: "center", justifyContent: "center", gap: 10,
            flexDirection: loading ? "column" : "row",
            transition: "all 0.2s ease",
          }}>
          {loading ? (
            <div style={{ width: "100%" }}>
              <div style={{ display: "flex", alignItems: "center", justifyContent: "center", gap: 8, marginBottom: 8 }}>
                <svg width="14" height="14" viewBox="0 0 24 24" fill="none"
                  stroke="#A855F7" strokeWidth="2.5" strokeLinecap="round"
                  style={{ animation: "bf-spin 1s linear infinite", filter: "drop-shadow(0 0 4px rgba(168,85,247,0.6))" }}>
                  <path d="M21 12a9 9 0 1 1-6.219-8.56"/>
                </svg>
                <span style={{ color: "#A855F7", fontSize: 12, fontWeight: 800, letterSpacing: 1 }}>UPLOADING VIDEO</span>
              </div>
              <BFProgressBar />
            </div>
          ) : (
            <>
              <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="white" strokeWidth="2.5" strokeLinecap="round">
                <path d="M21 15v4a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2v-4"/>
                <polyline points="17 8 12 3 7 8"/>
                <line x1="12" y1="3" x2="12" y2="15"/>
              </svg>
              Choose Video from Phone
            </>
          )}
        </button>

        <div style={{ display: "flex", alignItems: "center", gap: 10, margin: "16px 0 12px" }}>
          <div style={{ flex: 1, height: 1, background: "rgba(255,255,255,0.08)" }} />
          <div style={{ color: "#666", fontSize: 11, fontWeight: 700, letterSpacing: 1 }}>OR PASTE A YOUTUBE LINK</div>
          <div style={{ flex: 1, height: 1, background: "rgba(255,255,255,0.08)" }} />
        </div>
        <input value={ytUrl} onChange={function(e){ setYtUrl(e.target.value); }}
          placeholder="https://youtube.com/watch?v=..."
          style={{ width: "100%", boxSizing: "border-box", background: "#0a0a0a", border: "1px solid #2a2a2a", borderRadius: 10, padding: "12px 14px", color: "white", fontSize: 14, outline: "none", marginBottom: 10 }} />
        <button onClick={postYouTube} disabled={loading || !ytUrl.trim()}
          style={{ width: "100%", padding: "13px", borderRadius: 12, fontWeight: 800, fontSize: 14,
            cursor: (loading || !ytUrl.trim()) ? "not-allowed" : "pointer",
            background: (loading || !ytUrl.trim()) ? "#1a1a1a" : "rgba(192,38,211,0.15)",
            border: "1px solid " + ((loading || !ytUrl.trim()) ? "#2a2a2a" : "rgba(192,38,211,0.6)"),
            color: (loading || !ytUrl.trim()) ? "#555" : "#E9D5FF",
            display: "flex", alignItems: "center", justifyContent: "center", gap: 8 }}>
          <AppIcon id="video" size={16} /> Post YouTube Video
        </button>

        {msg && <div style={{ color: msg.startsWith("Error") ? "#EF4444" : "#22C55E", fontSize: 13, marginTop: 10, textAlign: "center" }}>{msg}</div>}
      </div>

      {posts.length > 0 && (
        <div>
          <div style={{ color: "#888", fontSize: 11, fontWeight: 700, letterSpacing: 1, marginBottom: 12 }}>YOUR VIDEO POSTS</div>
          {posts.map(function(p) {
            return (
              <div key={p.id} style={{ background: "#111", borderRadius: 14, border: "1px solid #1e1e1e", marginBottom: 12, overflow: "hidden" }}>
                {p.youtubeId
                  ? <div style={{ position: "relative", width: "100%", paddingTop: "56.25%", background: "#000" }}>
                      <iframe src={"https://www.youtube.com/embed/" + p.youtubeId} title={p.caption || "video"} frameBorder="0" allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture" allowFullScreen style={{ position: "absolute", top: 0, left: 0, width: "100%", height: "100%" }} />
                    </div>
                  : <video controls style={{ width: "100%", maxHeight: 300, background: "#000", display: "block" }} src={p.videoUrl} />}
                <div style={{ padding: "10px 14px", display: "flex", justifyContent: "space-between", alignItems: "center" }}>
                  {p.caption && <div style={{ color: "#aaa", fontSize: 13, flex: 1 }}>{p.caption}</div>}
                  <button onClick={function() { deletePost(p.id); }}
                    style={{ background: "rgba(220,38,38,0.1)", border: "1px solid rgba(220,38,38,0.3)",
                      borderRadius: 8, color: "#F87171", fontSize: 12, padding: "4px 10px", cursor: "pointer", flexShrink: 0 }}>
                    Delete
                  </button>
                </div>
              </div>
            );
          })}
        </div>
      )}
    </div>
  );
}


// =============================================================================
// CONTACT SUPPORT FORM — embedded inside Settings dropdown
// Sends a message to support@beatfinder.co.uk via /api/contact
// Includes a "or email directly" mailto fallback in case the API is down
// =============================================================================
function ContactSupportForm({ currentUser }) {
  const [name,    setName]    = useState(currentUser?.name || currentUser?.username || "");
  const [email,   setEmail]   = useState(currentUser?.email || "");
  const [subject, setSubject] = useState("");
  const [message, setMessage] = useState("");
  const [sending, setSending] = useState(false);
  const [msg,     setMsg]     = useState("");
  const [sent,    setSent]    = useState(false);

  const handleSubmit = async () => {
    if (!name.trim() || !email.trim() || !subject.trim() || !message.trim()) {
      setMsg("Error: All fields are required.");
      return;
    }
    if (message.trim().length < 10) {
      setMsg("Error: Message must be at least 10 characters.");
      return;
    }
    setSending(true);
    setMsg("");
    try {
      await apiFetch("/api/contact", {
        method: "POST",
        body: JSON.stringify({
          name:    name.trim(),
          email:   email.trim(),
          subject: subject.trim(),
          message: message.trim(),
        }),
      });
      setSent(true);
      setSubject("");
      setMessage("");
      setMsg("Message sent. We'll reply by email as soon as we can.");
    } catch (e) {
      setMsg("Error: " + e.message);
    } finally {
      setSending(false);
    }
  };

  const inputStyle = {
    width: "100%", background: "#111", border: "1px solid #333", borderRadius: 10,
    padding: "10px 14px", color: "white", fontSize: 14, outline: "none",
    boxSizing: "border-box", marginBottom: 8,
  };

  if (sent) {
    return (
      <div style={{ padding: "16px 14px", background: "#1a1a1a", borderTop: "1px solid #222" }}>
        <div style={{
          background: "rgba(34,197,94,0.1)", border: "1px solid rgba(34,197,94,0.3)",
          borderRadius: 10, padding: 14, textAlign: "center",
        }}>
          <div style={{ color: "#22C55E", fontWeight: 800, fontSize: 14, marginBottom: 6 }}>Message Sent!</div>
          <div style={{ color: "#888", fontSize: 12, lineHeight: 1.6 }}>
            We'll reply to <span style={{ color: "white" }}>{email}</span> as soon as we can.
          </div>
        </div>
        <button onClick={() => { setSent(false); setMsg(""); }}
          style={{ width: "100%", background: "#1e1e1e", border: "1px solid #333", borderRadius: 10,
            color: "#aaa", fontWeight: 700, padding: 10, fontSize: 13, cursor: "pointer", marginTop: 10 }}>
          Send another message
        </button>
      </div>
    );
  }

  return (
    <div style={{ padding: "12px 14px", background: "#1a1a1a", borderTop: "1px solid #222" }}>
      <div style={{ color: "#888", fontSize: 11, lineHeight: 1.5, marginBottom: 10 }}>
        Have a question, issue, or feedback? Send us a message and we'll get back to you by email.
      </div>
      <input value={name}    onChange={e => setName(e.target.value)}    placeholder="Your name"   style={inputStyle} />
      <input value={email}   onChange={e => setEmail(e.target.value)}   placeholder="Your email"  type="email" style={inputStyle} />
      <input value={subject} onChange={e => setSubject(e.target.value)} placeholder="Subject"     style={inputStyle} />
      <textarea value={message} onChange={e => setMessage(e.target.value)} placeholder="How can we help?"
        rows={5}
        style={{ ...inputStyle, resize: "vertical", minHeight: 80, fontFamily: "inherit", marginBottom: 10 }} />
      <button onClick={handleSubmit} disabled={sending}
        style={{
          width: "100%", background: sending ? "#333" : "#C026D3", border: "none", borderRadius: 10,
          color: "white", fontWeight: 800, padding: 10, fontSize: 14,
          cursor: sending ? "not-allowed" : "pointer",
        }}>
        {sending ? "Sending..." : "Send Message"}
      </button>
      {msg && (
        <div style={{
          color: msg.startsWith("Error") ? "#F87171" : "#22C55E",
          fontSize: 12, textAlign: "center", marginTop: 8, fontWeight: 600,
        }}>{msg}</div>
      )}
      {/* Mailto fallback — always visible so users can email directly if the form fails */}
      <div style={{ marginTop: 12, paddingTop: 10, borderTop: "1px solid #2a2a2a", textAlign: "center" }}>
        <div style={{ color: "#666", fontSize: 11, marginBottom: 4 }}>Or email us directly:</div>
        <a href="mailto:support@beatfinder.co.uk"
          style={{ color: "#06B6D4", fontSize: 12, fontWeight: 700, textDecoration: "none" }}>
          support@beatfinder.co.uk
        </a>
      </div>
    </div>
  );
}


// =============================================================================
// BEAT ARTWORK UPLOADER
// Producer Pro users can set one image that displays on every beat card
// they upload. Uploading replaces it across all their existing beats.
// =============================================================================
function BeatArtworkUploader({ user, setUser }) {
  var [uploading, setUploading] = React.useState(false);
  var [msg, setMsg] = React.useState("");
  var [preview, setPreview] = React.useState(user.beatArtworkUrl || "");
  var fileInputRef = React.useRef(null);

  React.useEffect(function() { setPreview(user.beatArtworkUrl || ""); }, [user.beatArtworkUrl]);

  async function handleFile(file) {
    if (!file) return;
    if (!file.type.startsWith("image/")) {
      setMsg("Please choose an image file (JPG, PNG, etc.)");
      return;
    }
    if (file.size > 5 * 1024 * 1024) {
      setMsg("Image too large — maximum 5MB");
      return;
    }
    // Optimistic local preview
    try {
      var reader = new FileReader();
      reader.onload = function(e) { setPreview(e.target.result); };
      reader.readAsDataURL(file);
    } catch(e) {}

    setUploading(true);
    setMsg("Uploading…");
    var form = new FormData();
    form.append("file", file);
    try {
      var token = localStorage.getItem("bf_token");
      var res = await fetch(API_BASE + "/api/auth/beat-artwork", {
        method: "POST",
        headers: token ? { Authorization: "Bearer " + token } : {},
        body: form,
      });
      if (!res.ok) {
        var err = await res.json().catch(function(){ return { detail: "Upload failed" }; });
        throw new Error(err.detail || "Upload failed");
      }
      var data = await res.json();
      setPreview(data.beatArtworkUrl || "");
      // Update user in parent state so it sticks
      setUser(Object.assign({}, user, { beatArtworkUrl: data.beatArtworkUrl }));
      setMsg("✓ Artwork updated on all your beats");
      setTimeout(function() { setMsg(""); }, 3000);
    } catch(e) {
      setMsg("Error: " + (e.message || "upload failed"));
    } finally {
      setUploading(false);
    }
  }

  return (
    <div style={{ padding: "14px", background: "#1a1a1a", borderTop: "1px solid #222" }}>
      <div style={{ color: "#aaa", fontSize: 12, lineHeight: 1.5, marginBottom: 14 }}>
        Choose one image that will appear on every beat card you upload.
        Replacing it updates all your existing beats too.
      </div>

      {/* Preview */}
      <div style={{
        width: "100%", aspectRatio: "1 / 1",
        background: preview
          ? "#000"
          : "linear-gradient(135deg,#1a0a2e,#2a0a4a,#3b0070)",
        borderRadius: 12, marginBottom: 12,
        border: "1.5px solid rgba(124,58,237,0.3)",
        boxShadow: "0 0 16px rgba(124,58,237,0.15), inset 0 1px 0 rgba(255,255,255,0.05)",
        overflow: "hidden", position: "relative",
        display: "flex", alignItems: "center", justifyContent: "center",
        maxWidth: 220, marginLeft: "auto", marginRight: "auto",
      }}>
        {preview ? (
          <img src={preview} alt="Beat artwork"
            style={{ width: "100%", height: "100%", objectFit: "cover" }} />
        ) : (
          <div style={{ textAlign: "center", color: "#A78BFA", padding: 20 }}>
            <svg width="36" height="36" viewBox="0 0 24 24" fill="none" stroke="#A78BFA" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round" style={{ marginBottom: 8, filter: "drop-shadow(0 0 6px rgba(124,58,237,0.5))" }}>
              <rect x="3" y="3" width="18" height="18" rx="2"/>
              <circle cx="8.5" cy="8.5" r="1.5"/>
              <polyline points="21 15 16 10 5 21"/>
            </svg>
            <div style={{ fontSize: 11, fontWeight: 700, letterSpacing: 0.5 }}>No artwork yet</div>
          </div>
        )}
      </div>

      <input
        ref={fileInputRef}
        type="file" accept="image/*"
        style={{ display: "none" }}
        onChange={function(e) {
          if (e.target.files && e.target.files[0]) handleFile(e.target.files[0]);
          e.target.value = "";
        }}
      />
      <button
        onClick={function() { if (fileInputRef.current) fileInputRef.current.click(); }}
        disabled={uploading}
        style={{
          width: "100%",
          background: uploading
            ? "rgba(255,255,255,0.04)"
            : "linear-gradient(135deg,#C026D3 0%,#A855F7 50%,#7C3AED 100%)",
          border: "1.5px solid " + (uploading ? "#333" : "rgba(192,38,211,0.6)"),
          borderRadius: 12, padding: "12px",
          color: uploading ? "#666" : "white",
          fontWeight: 900, fontSize: 13, letterSpacing: 0.5,
          cursor: uploading ? "not-allowed" : "pointer",
          display: "flex", alignItems: "center", justifyContent: "center", gap: 8,
          boxShadow: uploading ? "none" : "0 2px 12px rgba(192,38,211,0.4), inset 0 1px 0 rgba(255,255,255,0.2)",
          textShadow: uploading ? "none" : "0 1px 2px rgba(0,0,0,0.3)",
        }}>
        <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="white" strokeWidth="2.5" strokeLinecap="round"><path d="M21 15v4a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2v-4"/><polyline points="17 8 12 3 7 8"/><line x1="12" y1="3" x2="12" y2="15"/></svg>
        {uploading ? "UPLOADING…" : (preview ? "REPLACE ARTWORK" : "UPLOAD ARTWORK")}
      </button>

      {msg && (
        <div style={{
          marginTop: 10, fontSize: 12, fontWeight: 700, textAlign: "center",
          color: msg.startsWith("Error") ? "#F87171" : (msg.startsWith("✓") ? "#22C55E" : "#A78BFA"),
        }}>{msg}</div>
      )}

      <div style={{ color: "#666", fontSize: 10, lineHeight: 1.5, marginTop: 10, textAlign: "center" }}>
        Recommended: square image, 800×800 minimum. Max 5MB.
      </div>
    </div>
  );
}

// =============================================================================
// BLOCKED USERS PANEL
// Shows everyone the current user has blocked. Unblock button opens their
// profile back up + restores follow eligibility. Empty state for fresh users.
// =============================================================================
function BlockedUsersPanel() {
  var [list, setList]       = React.useState([]);
  var [loading, setLoading] = React.useState(true);
  var [busy, setBusy]       = React.useState({}); // username → bool

  function load() {
    setLoading(true);
    apiFetch("/api/auth/blocked-users")
      .then(function(d) { setList(d || []); setLoading(false); })
      .catch(function() { setList([]); setLoading(false); });
  }

  React.useEffect(function() { load(); }, []);

  function unblock(username) {
    setBusy(function(prev) { var n = Object.assign({}, prev); n[username] = true; return n; });
    apiFetch("/api/auth/block/" + encodeURIComponent(username), { method: "DELETE" })
      .then(function() {
        setList(function(prev) { return prev.filter(function(u) { return u.username !== username; }); });
      })
      .catch(function() {})
      .finally(function() {
        setBusy(function(prev) { var n = Object.assign({}, prev); n[username] = false; return n; });
      });
  }

  return (
    <div style={{ padding: "14px", background: "#1a1a1a", borderTop: "1px solid #222" }}>
      <div style={{ color: "#aaa", fontSize: 12, lineHeight: 1.5, marginBottom: 14 }}>
        Users you've blocked can't see your profile, posts, beats, or message you.
        Unblock anytime to restore mutual visibility.
      </div>

      {loading && (
        <div style={{ color: "#555", fontSize: 13, textAlign: "center", padding: "20px 0" }}>
          Loading...
        </div>
      )}

      {!loading && list.length === 0 && (
        <div style={{ color: "#555", fontSize: 13, textAlign: "center", padding: "20px 0" }}>
          You haven't blocked anyone.
        </div>
      )}

      {!loading && list.map(function(u) {
        return (
          <div key={u.username} style={{
            display: "flex", alignItems: "center", gap: 12, padding: "10px 0",
            borderBottom: "1px solid #222",
          }}>
            <div style={{
              width: 40, height: 40, borderRadius: "50%", overflow: "hidden", flexShrink: 0,
              background: u.avatarUrl ? "#000" : "linear-gradient(135deg,#6B21A8,#C026D3)",
              display: "flex", alignItems: "center", justifyContent: "center",
              color: "white", fontSize: 16, fontWeight: 800,
            }}>
              {u.avatarUrl
                ? <img src={u.avatarUrl} alt={u.username} style={{ width: "100%", height: "100%", objectFit: "cover" }} />
                : (u.username || "?")[0].toUpperCase()}
            </div>
            <div style={{ flex: 1, minWidth: 0 }}>
              <div style={{ color: "white", fontSize: 14, fontWeight: 700,
                whiteSpace: "nowrap", overflow: "hidden", textOverflow: "ellipsis" }}>
                @{u.username}
              </div>
              {u.name && (
                <div style={{ color: "#666", fontSize: 11,
                  whiteSpace: "nowrap", overflow: "hidden", textOverflow: "ellipsis" }}>
                  {u.name}
                </div>
              )}
            </div>
            <button onClick={function() { unblock(u.username); }}
              disabled={!!busy[u.username]}
              style={{
                padding: "8px 14px", borderRadius: 16,
                background: "transparent", border: "1.5px solid #444",
                color: "#aaa", fontWeight: 700, fontSize: 12,
                cursor: busy[u.username] ? "not-allowed" : "pointer",
                flexShrink: 0,
              }}>
              {busy[u.username] ? "..." : "Unblock"}
            </button>
          </div>
        );
      })}
    </div>
  );
}

function ProfileScreen({ user, setUser, onLogout, savedLyrics, setSavedLyrics, onPlayBeat, onEditLyric, onNewLyric, onOpenMessages }) {
  const [mode,          setMode]          = useState("landing");
  const [ownBadgePopup, setOwnBadgePopup] = useState(null);
  // T&Cs acceptance — signup-only in this in-profile auth form too.
  const [acceptTerms,        setAcceptTerms]        = useState(false);
  const [showTermsPreview,   setShowTermsPreview]   = useState(false);
  const [marketingOptIn,     setMarketingOptIn]     = useState(false);
  const [email,       setEmail]       = useState(() => {
    try { return localStorage.getItem("bf_remember") === "1" ? (localStorage.getItem("bf_saved_email") || "") : ""; } catch { return ""; }
  });
  const [pw,          setPw]          = useState("");
  const [name,        setName]        = useState("");
  const [ytLink,        setYtLink]        = useState("");
  const [uploads,       setUploads]       = useState([]);
  const [plan,          setPlan]          = useState(null);
  const [usernameInput,    setUsernameInput]    = useState(user?.username || "");
  const [usernameMsg,      setUsernameMsg]      = useState("");
  const [uploadGenre,      setUploadGenre]      = useState("");
  const [uploadPrice,      setUploadPrice]      = useState("");
  // Two-tier lease pricing — only used when uploadPrice is paid (not free).
  // Basic is locked to £50 platform-wide; premium is producer-set £100-£500.
  const [uploadPremiumPrice, setUploadPremiumPrice] = useState("");
  const [uploadBpm,        setUploadBpm]        = useState("");
  const [uploadKey,        setUploadKey]        = useState("");
  const [uploadDesc,       setUploadDesc]       = useState("");
  const [uploadPreviewStart, setUploadPreviewStart] = useState(0);
  const [uploadFile,       setUploadFile]       = useState(null);
  const uploadFileRef = React.useRef(null);
  const [uploadLoading,    setUploadLoading]    = useState(false);
  const [uploadMsg,        setUploadMsg]        = useState("");
  const [settingsOpen,     setSettingsOpen]     = useState(false);
  const [openSettingsSection, setOpenSettingsSection] = useState(null); // 'username' | 'password'
  const [toolsOpen,            setToolsOpen]            = useState(false);
  const [newUsername,      setNewUsername]      = useState("");
  const [currentPw,        setCurrentPw]        = useState("");
  const [newPw,            setNewPw]            = useState("");
  const [confirmPw,        setConfirmPw]        = useState("");
  const [settingsMsg,      setSettingsMsg]      = useState("");
  const [showDeleteAccountConfirm, setShowDeleteAccountConfirm] = useState(false);
  const [deleteConfirmEmail, setDeleteConfirmEmail] = useState("");
  const [deleteReason, setDeleteReason] = useState("");
  const [deleting,         setDeleting]         = useState(false);
  const [authErr,     setAuthErr]     = useState("");
  const [authLoading, setAuthLoading] = useState(false);
  // Signup-only: username chosen as part of registration. We collect
  // this on the signup form (not the login form) so new users always
  // start with a public profile slug. Without it they end up
  // unsearchable, untaggable, and with no profile URL.
  const [signupUsername,    setSignupUsername]    = useState("");
  // "idle" | "checking" | "available" | "taken" | "invalid"
  // Drives the live availability hint shown below the username input.
  const [signupUsernameStatus, setSignupUsernameStatus] = useState("idle");
  const [rememberMe,  setRememberMe]  = useState(() => {
    try { return localStorage.getItem("bf_remember") === "1"; } catch { return false; }
  });

  // Live username-availability check during signup. Debounced 400ms
  // so we don't hammer the search endpoint on every keystroke. The
  // status drives a hint shown below the input ("available" / "taken"
  // / "invalid" etc).
  useEffect(() => {
    var u = signupUsername.trim();
    if (!u) { setSignupUsernameStatus("idle"); return; }
    // Run the same validation rules as the backend's set-username
    // endpoint so the user sees client-side feedback before any
    // network call.
    if (u.length < 3 || u.length > 30 || !/^[a-zA-Z0-9_.]+$/.test(u)) {
      setSignupUsernameStatus("invalid");
      return;
    }
    setSignupUsernameStatus("checking");
    var cancelled = false;
    var t = setTimeout(function() {
      // /api/auth/search does a substring match; we filter to exact
      // case-insensitive match so "trel" doesn't say "Trelli" is taken
      // when in fact "trel" is free.
      apiFetch("/api/auth/search?q=" + encodeURIComponent(u))
        .then(function(matches) {
          if (cancelled) return;
          var taken = (matches || []).some(function(m) {
            return (m.username || "").toLowerCase() === u.toLowerCase();
          });
          setSignupUsernameStatus(taken ? "taken" : "available");
        })
        .catch(function() {
          if (!cancelled) setSignupUsernameStatus("idle");
        });
    }, 400);
    return function() { cancelled = true; clearTimeout(t); };
  }, [signupUsername]);

  const inp = {
    width: "100%", background: "#1a1a1a", border: "1px solid #333",
    borderRadius: 12, padding: "14px 16px", color: "white", fontSize: 15,
    outline: "none", marginBottom: 12, boxSizing: "border-box",
  };

  const PLANS = [
    {
      id: "artist", label: "Artist Pro", price: "4.99",
      perks: ["Access Exclusive Members area","Bookmark unlimited beats","Artist verified badge","Personalised recommendations"],
    },
    {
      id: "producer", label: "Producer Pro", price: "8.99",
      perks: ["Everything in Artist Pro","Upload beats to Home featured","Featured in rotation","Producer verified badge","Analytics"],
    },
  ];

  // Add activeSection state for dashboard navigation
  const [activeSection, setActiveSection] = useState(null);
  // Allow other screens to open a specific profile section (e.g. Home's
  // "Upload Beat" quick action → open the upload form). Fired as a window event
  // after navigating to the profile tab.
  React.useEffect(function(){
    function onOpenSection(e){
      if (e && e.detail && e.detail.section) setActiveSection(e.detail.section);
    }
    window.addEventListener("bf-open-section", onOpenSection);
    return function(){ window.removeEventListener("bf-open-section", onOpenSection); };
  }, []);
  // Logout confirmation modal — prevents accidental sign-outs
  const [confirmLogout, setConfirmLogout] = useState(false);
  const [bio,              setBio]              = useState(user?.bio || "");
  const [editName,         setEditName]         = useState(user?.name || "");
  const [location,         setLocation]         = useState(user?.location || "");
  const [instagram,        setInstagram]        = useState(user?.instagram || "");
  const [avatarColor,      setAvatarColor]      = useState(user?.avatarColor || "linear-gradient(135deg,#6B21A8,#C026D3)");
  const [avatarUrl,        setAvatarUrl]        = useState(user?.avatarUrl || "");
  const [showAvatarPicker, setShowAvatarPicker] = useState(false);
  const [avatarUploading,  setAvatarUploading]  = useState(false);
  const [headerUrl,        setHeaderUrl]        = useState(user?.headerUrl || "");
  const [headerUploading,  setHeaderUploading]  = useState(false);
  // Header crop flow: when the user picks a file we set headerCropFile to
  // open the crop modal. The modal calls back with the cropped Blob which
  // we upload via the same /api/auth/header endpoint.
  const [headerCropFile,   setHeaderCropFile]   = useState(null);
  const [avatarCropFile,   setAvatarCropFile]   = useState(null);
  const avatarFileRef = React.useRef(null);
  const headerFileRef = React.useRef(null);
  const [tiktok,        setTiktok]        = useState(user?.tiktok || "");
  const [youtube,       setYoutube]       = useState(user?.youtube || "");
  const [spotify,       setSpotify]       = useState(user?.spotify || "");
  const [appleMusic,    setAppleMusic]    = useState(user?.appleMusic || "");
  const [website,       setWebsite]       = useState(user?.website || "");
  const [bioSaving,     setBioSaving]     = useState(false);
  const [bioMsg,        setBioMsg]        = useState("");
  const [profileSaving, setProfileSaving] = useState(false);
  const [userSearch,    setUserSearch]    = useState("");
  const [userResults,   setUserResults]   = useState([]);
  const [userSearching, setUserSearching] = useState(false);
  const [producerStats, setProducerStats] = useState(null);

  // Fetch fresh profile data on mount so form fields always show latest saved values
  useEffect(() => {
    if (!user) return;
    apiFetch("/api/auth/me")
      .then(fresh => {
        setUser(u => ({ ...u, ...fresh, isPro: fresh.subscriptionActive && fresh.plan === "producer", isArtistPro: fresh.subscriptionActive && (fresh.plan === "artist" || fresh.plan === "producer") }));
        setBio(fresh.bio || "");
        setEditName(fresh.name || "");
        setLocation(fresh.location || "");
        setInstagram(fresh.instagram || "");
        setTiktok(fresh.tiktok || "");
        setYoutube(fresh.youtube || "");
        setSpotify(fresh.spotify || "");
        setWebsite(fresh.website || "");
        setAppleMusic(fresh.appleMusic || "");
        setAvatarColor(fresh.avatarColor || "linear-gradient(135deg,#6B21A8,#C026D3)");
        setAvatarUrl(fresh.avatarUrl || "");
        setHeaderUrl(fresh.headerUrl || "");
        setNewUsername(fresh.username || "");
      })
      .catch(() => {});
  }, [user?.id]);

  useEffect(() => {
    if (user?.isPro) {
      Promise.all([
        apiFetch("/api/producer/my-beats").catch(() => []),
        apiFetch("/api/producer/my-leases").catch(() => []),
        apiFetch("/api/producer/stripe-status").catch(() => ({})),
      ]).then(([beats, leases, stripe]) => {
        setUploads(beats); // populate My Uploads count
        const totalDownloads = beats.reduce((s, b) => s + (b.downloads || 0), 0);
        const totalRevenue   = leases.reduce((s, l) => {
          const p = parseFloat((l.price || "0").replace(/[^0-9.]/g, ""));
          return s + (isNaN(p) ? 0 : p);
        }, 0);
        const topBeat = beats.reduce((best, b) => (!best || b.downloads > best.downloads) ? b : best, null);
        setProducerStats({
          totalBeats: beats.length,
          totalDownloads,
          totalRevenue: totalRevenue.toFixed(2),
          topBeat: topBeat ? topBeat.title : "No beats yet",
          stripeConnected: stripe.connected || false,
          recentSales: leases.slice(0, 5),
        });
      });
    }
  }, [user]);

  if (!user) {
    if (mode === "forgot") return <ForgotPasswordScreen onBack={() => setMode("login")} />;
    if (mode === "landing") return (
      <div style={{ display: "flex", flexDirection: "column", alignItems: "center", justifyContent: "center", height: "75vh", padding: 32, textAlign: "center" }}>
          <div style={{ fontSize: 56, marginBottom: 16 }}><AppIcon id="bookmark" size={20}/></div>
        <div style={{ color: "white", fontSize: 24, fontWeight: 800, marginBottom: 8 }}>My Profile</div>
        <div style={{ color: "#888", fontSize: 14, lineHeight: 1.7, marginBottom: 32 }}>Log in to access your saved beats, lyrics, and pro features.</div>
        <button onClick={() => setMode("signup")} style={{ width: "100%", maxWidth: 320, background: "#C026D3", border: "none", borderRadius: 32, color: "white", fontWeight: 800, padding: 16, fontSize: 16, cursor: "pointer", marginBottom: 14 }}>
          Create Account
        </button>
        <button onClick={() => setMode("login")} style={{ background: "none", border: "none", color: "#06B6D4", fontWeight: 700, fontSize: 15, cursor: "pointer" }}>
          I already have an account
        </button>
      </div>
    );
    return (
      <div style={{ padding: "40px 24px 100px" }}>
        <button onClick={() => setMode("landing")} style={{ background: "none", border: "none", color: "white", fontSize: 28, cursor: "pointer", marginBottom: 20 }}>&#8592;</button>
        <div style={{ color: "white", fontFamily: "'Bebas Neue',sans-serif", fontSize: 30, letterSpacing: 2, marginBottom: 24 }}>
          {mode === "signup" ? "CREATE ACCOUNT" : "WELCOME BACK"}
        </div>
        {mode === "signup" && <input value={name} onChange={e => setName(e.target.value)} placeholder="Your name" style={inp} />}
        {mode === "signup" && (
          <>
            <input
              value={signupUsername}
              onChange={e => setSignupUsername(e.target.value)}
              placeholder="Choose a username"
              autoCapitalize="none"
              autoCorrect="off"
              spellCheck={false}
              style={{ ...inp, marginBottom: 4 }}
            />
            {/* Live availability hint. Keeps the form compact — only
                 occupies a single line below the input. */}
            <div style={{ minHeight: 16, marginBottom: 12, paddingLeft: 4, fontSize: 11, lineHeight: "16px" }}>
              {signupUsernameStatus === "idle" && (
                <span style={{ color: "#555" }}>3–30 characters · letters, numbers, dots, underscores</span>
              )}
              {signupUsernameStatus === "invalid" && (
                <span style={{ color: "#F87171" }}>Must be 3–30 characters · letters, numbers, dots, underscores only</span>
              )}
              {signupUsernameStatus === "checking" && (
                <span style={{ color: "#888" }}>Checking availability…</span>
              )}
              {signupUsernameStatus === "available" && (
                <span style={{ color: "#10B981", fontWeight: 700 }}>✓ @{signupUsername.trim()} is available</span>
              )}
              {signupUsernameStatus === "taken" && (
                <span style={{ color: "#F87171", fontWeight: 700 }}>✗ @{signupUsername.trim()} is already taken</span>
              )}
            </div>
          </>
        )}
        <input value={email} onChange={e => setEmail(e.target.value)} placeholder="Email address" type="email" style={inp} />
        <input value={pw} onChange={e => setPw(e.target.value)} placeholder="Password" type="password" style={{ ...inp, marginBottom: 16 }} />
        {mode === "login" && (
          <div style={{ display: "flex", alignItems: "center", gap: 10, marginBottom: 16 }}>
            <input type="checkbox" checked={rememberMe} onChange={e => setRememberMe(e.target.checked)} id="rm" />
            <label htmlFor="rm" style={{ color: "#888", fontSize: 13 }}>Remember my login</label>
          </div>
        )}
        {/* T&Cs checkbox — required on signup. Records acceptance against
            the new account immediately after registration. */}
        {mode === "signup" && (
          <div style={{
            marginBottom: 16, padding: "12px 14px",
            background: acceptTerms ? "rgba(124,58,237,0.08)" : "rgba(0,0,0,0.3)",
            border: "1px solid " + (acceptTerms ? "rgba(124,58,237,0.35)" : "rgba(124,58,237,0.18)"),
            borderRadius: 12,
            transition: "background 0.15s, border 0.15s",
          }}>
            <label htmlFor="bf_accept_terms_p"
              style={{ display: "flex", alignItems: "flex-start", gap: 10, cursor: "pointer", userSelect: "none", WebkitUserSelect: "none" }}>
              <input
                id="bf_accept_terms_p"
                type="checkbox"
                checked={acceptTerms}
                onChange={e => setAcceptTerms(e.target.checked)}
                style={{ width: 18, height: 18, marginTop: 1, flexShrink: 0, accentColor: "#7C3AED", cursor: "pointer" }}
              />
              <span style={{ color: "#ccc", fontSize: 13, lineHeight: 1.5 }}>
                I agree to BeatFinder's{" "}
                <span onClick={e => { e.preventDefault(); e.stopPropagation(); setShowTermsPreview(true); }}
                  style={{ color: "#A78BFA", fontWeight: 700, textDecoration: "underline", cursor: "pointer" }}>
                  Terms &amp; Conditions
                </span>
                {" "}and Privacy Policy. I confirm I am at least 16 years old.
              </span>
            </label>
            {/* Marketing opt-in checkbox — defaults OFF */}
            <label htmlFor="bf_marketing_optin_2"
              style={{ display: "flex", alignItems: "flex-start", gap: 8, cursor: "pointer",
                userSelect: "none", WebkitUserSelect: "none", marginTop: 10 }}>
              <input
                id="bf_marketing_optin_2"
                type="checkbox"
                checked={marketingOptIn}
                onChange={e => setMarketingOptIn(e.target.checked)}
                style={{
                  width: 16, height: 16, marginTop: 1, flexShrink: 0,
                  accentColor: "#7C3AED", cursor: "pointer",
                }}
              />
              <span style={{ color: "#888", fontSize: 11, lineHeight: 1.35 }}>
                Send me occasional updates about BeatFinder.
              </span>
            </label>
          </div>
        )}
        <button disabled={authLoading} onClick={async () => {
          setAuthErr("");
          if (mode === "signup" && !name.trim())  { setAuthErr("Please enter your name"); return; }
          if (mode === "signup" && !signupUsername.trim()) { setAuthErr("Please choose a username"); return; }
          if (mode === "signup" && signupUsernameStatus === "invalid") {
            setAuthErr("Username must be 3–30 characters, letters/numbers/dots/underscores only"); return;
          }
          if (mode === "signup" && signupUsernameStatus === "taken") {
            setAuthErr("That username is already taken"); return;
          }
          if (mode === "signup" && !email.trim()) { setAuthErr("Please enter your email"); return; }
          if (mode === "signup" && !pw.trim())    { setAuthErr("Please enter a password"); return; }
          if (mode === "signup" && !acceptTerms)  { setAuthErr("Please accept the Terms & Conditions to continue"); return; }
          if (mode === "login" && !pw.trim()) { setAuthErr("Please enter your password"); return; }
          setAuthLoading(true);
          try {
            const u = mode === "signup"
              ? await AuthAPI.register(name || email.split("@")[0], email, pw, marketingOptIn)
              : await AuthAPI.login(email, pw);
            // Persist the chosen username immediately after registration.
            // The /register endpoint creates the user without a username
            // — without this call new users would end up with no public
            // profile, no searchable handle, no URL slug. We attach the
            // username to the same local user object before setUser() so
            // the UI doesn't briefly flash an empty handle.
            if (mode === "signup" && u && signupUsername.trim()) {
              const desired = signupUsername.trim();
              try {
                await apiFetch("/api/auth/set-username", {
                  method: "POST",
                  body: JSON.stringify({ username: desired }),
                });
                u.username = desired;
              } catch (unameErr) {
                // If the call fails (race condition where the name was
                // taken between availability check and submit), surface
                // a friendly error and ABORT before we proceed. The
                // account exists but has no username yet — we leave the
                // user logged in so they can retry from settings.
                setAuthErr("Account created, but username '" + desired + "' couldn't be set: " + (unameErr.message || "try a different one in Settings"));
              }
            }
            // Record T&Cs acceptance immediately on signup
            if (mode === "signup" && u) {
              try {
                await apiFetch("/api/auth/accept-terms", {
                  method: "POST",
                  body: JSON.stringify({ version: TERMS_VERSION }),
                });
                u.terms_accepted_version = TERMS_VERSION;
              } catch (termsErr) {
                console.error("[BF terms] signup accept failed", termsErr);
              }
            }
            if (rememberMe) { try { localStorage.setItem("bf_saved_email", email); localStorage.setItem("bf_remember", "1"); } catch {} }
            else { try { localStorage.removeItem("bf_saved_email"); localStorage.removeItem("bf_remember"); } catch {} }
            setUser(u);
          } catch (e) { setAuthErr(e.message); }
          finally { setAuthLoading(false); }
        }} style={{ width: "100%", background: authLoading ? "#555" : "#C026D3", border: "none", borderRadius: 32, color: "white", fontWeight: 800, padding: 16, fontSize: 16, cursor: "pointer" }}>
          {authLoading ? "Please wait..." : mode === "signup" ? "Create Account" : "Log In"}
        </button>
        {authErr && <div style={{ color: "#F87171", fontSize: 13, textAlign: "center", marginTop: 12 }}>{authErr}</div>}
        <div style={{ textAlign: "center", marginTop: 20 }}>
          <span style={{ color: "#888", fontSize: 14 }}>{mode === "signup" ? "Already have an account? " : "No account? "}</span>
          <button onClick={() => { setAuthErr(""); setAcceptTerms(false); setMode(mode === "signup" ? "login" : "signup"); }}
            style={{ background: "none", border: "none", color: "#06B6D4", fontWeight: 700, fontSize: 14, cursor: "pointer" }}>
            {mode === "signup" ? "Log In" : "Sign Up"}
          </button>
        </div>
        {mode === "login" && (
          <div style={{ textAlign: "center", marginTop: 12 }}>
            <button onClick={e => { e.preventDefault(); e.stopPropagation(); setMode("forgot"); }}
              style={{ background: "none", border: "none", color: "#666", fontSize: 13, cursor: "pointer", textDecoration: "underline" }}>
              Forgot your password?
            </button>
          </div>
        )}

        {/* T&Cs preview modal */}
        {showTermsPreview && (
          <div onClick={() => setShowTermsPreview(false)} style={{
            position: "fixed", inset: 0, zIndex: 99998,
            background: "rgba(0,0,0,0.85)",
            display: "flex", alignItems: "center", justifyContent: "center",
            padding: 20, backdropFilter: "blur(4px)", fontFamily: "'DM Sans',sans-serif",
          }}>
            <div onClick={e => e.stopPropagation()} style={{
              background: "linear-gradient(165deg,#0f0a1f 0%,#0a0a14 60%,#080812 100%)",
              border: "1px solid rgba(124,58,237,0.3)",
              borderRadius: 18, maxWidth: 520, width: "100%", maxHeight: "85vh",
              display: "flex", flexDirection: "column",
              boxShadow: "0 16px 40px rgba(0,0,0,0.75), 0 0 24px rgba(124,58,237,0.15)",
              position: "relative", overflow: "hidden",
            }}>
              <div style={{
                position: "absolute", top: 0, left: 0, right: 0, height: 2,
                background: "linear-gradient(90deg,transparent,#C026D3,#7C3AED,#3B82F6,transparent)",
                boxShadow: "0 0 8px rgba(124,58,237,0.7)",
              }} />
              <div style={{
                display: "flex", alignItems: "center", justifyContent: "space-between",
                padding: "16px 20px", borderBottom: "1px solid rgba(124,58,237,0.15)", flexShrink: 0,
              }}>
                <div>
                  <div style={{ color: "white", fontSize: 20, fontWeight: 800,
                    fontFamily: "'Bebas Neue',sans-serif", letterSpacing: 1 }}>TERMS &amp; CONDITIONS</div>
                  <div style={{ color: "#888", fontSize: 11, marginTop: 2 }}>Version {TERMS_VERSION}</div>
                </div>
                <button onClick={() => setShowTermsPreview(false)} style={{
                  width: 32, height: 32, borderRadius: "50%",
                  background: "rgba(255,255,255,0.04)", border: "1px solid #2a2a2a", color: "#888",
                  cursor: "pointer", flexShrink: 0,
                  display: "flex", alignItems: "center", justifyContent: "center",
                }}>
                  <svg width="12" height="12" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round">
                    <line x1="18" y1="6" x2="6" y2="18"/><line x1="6" y1="6" x2="18" y2="18"/>
                  </svg>
                </button>
              </div>
              <div style={{ flex: 1, overflowY: "auto", WebkitOverflowScrolling: "touch", padding: "16px 20px" }}>
                {TERMS_CONTENT.map(function(sec, i) {
                  return (
                    <div key={i} style={{ marginBottom: 18 }}>
                      <div style={{ color: "#A78BFA", fontSize: 12, fontWeight: 900, letterSpacing: 1, marginBottom: 6 }}>{sec.title}</div>
                      <div style={{ color: "#bbb", fontSize: 12, lineHeight: 1.65 }}>{sec.body}</div>
                    </div>
                  );
                })}
              </div>
              <div style={{ padding: "12px 20px", borderTop: "1px solid rgba(124,58,237,0.15)", flexShrink: 0 }}>
                <button onClick={() => setShowTermsPreview(false)} style={{
                  width: "100%", padding: "12px",
                  background: "linear-gradient(135deg,#C026D3,#7C3AED)",
                  border: "none", borderRadius: 12,
                  color: "white", fontWeight: 800, fontSize: 14, letterSpacing: 0.5,
                  cursor: "pointer", boxShadow: "0 4px 14px rgba(124,58,237,0.4)",
                }}>
                  Close
                </button>
              </div>
            </div>
          </div>
        )}
      </div>
    );
  }

  if (user) {
    const goSection = (s) => setActiveSection(activeSection === s ? null : s);

    return (
    <div style={{ padding: "0 16px 100px" }}>
      
      {/* My Profile header — redesigned with LED accent + icon buttons */}
      <div style={{ padding: "20px 0 18px", display: "flex", alignItems: "center", justifyContent: "space-between", gap: 8 }}>
        <div style={{ display: "flex", alignItems: "center", gap: 12, flex: 1, minWidth: 0 }}>
          <div style={{
            width: 36, height: 36, borderRadius: 11,
            background: "linear-gradient(135deg,#C026D3,#7C3AED,#3B82F6)",
            boxShadow: "0 0 14px rgba(124,58,237,0.5), inset 0 1px 0 rgba(255,255,255,0.2)",
            display: "flex", alignItems: "center", justifyContent: "center",
            flexShrink: 0,
          }}>
            <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="white" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
              <path d="M20 21v-2a4 4 0 00-4-4H8a4 4 0 00-4 4v2"/>
              <circle cx="12" cy="7" r="4"/>
            </svg>
          </div>
          <div style={{
            color: "white", fontSize: 26, fontWeight: 800,
            fontFamily: "'Bebas Neue',sans-serif", letterSpacing: 1.2, lineHeight: 1,
          }}>MY PROFILE</div>
        </div>
        <div style={{ display: "flex", gap: 6, alignItems: "center", position: "relative" }}>
          {/* Tools — icon button with neon ring when active */}
          <button onClick={() => { setToolsOpen(!toolsOpen); setSettingsOpen(false); }}
            title="Tools"
            style={{
              width: 38, height: 38, borderRadius: 11,
              background: toolsOpen
                ? "linear-gradient(135deg,rgba(192,38,211,0.25),rgba(124,58,237,0.18))"
                : "rgba(124,58,237,0.06)",
              border: "1px solid " + (toolsOpen ? "rgba(192,38,211,0.6)" : "rgba(124,58,237,0.2)"),
              color: toolsOpen ? "#C026D3" : "#A78BFA",
              cursor: "pointer",
              display: "flex", alignItems: "center", justifyContent: "center",
              boxShadow: toolsOpen ? "0 0 12px rgba(192,38,211,0.4)" : "none",
              transition: "all 0.15s ease",
            }}>
            <svg width="17" height="17" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
              <path d="M14.7 6.3a1 1 0 000 1.4l1.6 1.6a1 1 0 001.4 0l3.77-3.77a6 6 0 01-7.94 7.94l-6.91 6.91a2.12 2.12 0 01-3-3l6.91-6.91a6 6 0 017.94-7.94l-3.76 3.76z"/>
            </svg>
          </button>
          {/* Settings — icon button */}
          <button onClick={() => { setSettingsOpen(!settingsOpen); setToolsOpen(false); }}
            title="Settings"
            style={{
              width: 38, height: 38, borderRadius: 11,
              background: settingsOpen
                ? "linear-gradient(135deg,rgba(192,38,211,0.25),rgba(124,58,237,0.18))"
                : "rgba(124,58,237,0.06)",
              border: "1px solid " + (settingsOpen ? "rgba(192,38,211,0.6)" : "rgba(124,58,237,0.2)"),
              color: settingsOpen ? "#C026D3" : "#A78BFA",
              cursor: "pointer",
              display: "flex", alignItems: "center", justifyContent: "center",
              boxShadow: settingsOpen ? "0 0 12px rgba(192,38,211,0.4)" : "none",
              transition: "all 0.15s ease",
            }}>
            <svg width="17" height="17" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
              <circle cx="12" cy="12" r="3"/>
              <path d="M19.4 15a1.65 1.65 0 00.33 1.82l.06.06a2 2 0 11-2.83 2.83l-.06-.06a1.65 1.65 0 00-1.82-.33 1.65 1.65 0 00-1 1.51V21a2 2 0 11-4 0v-.09a1.65 1.65 0 00-1-1.51 1.65 1.65 0 00-1.82.33l-.06.06a2 2 0 11-2.83-2.83l.06-.06a1.65 1.65 0 00.33-1.82 1.65 1.65 0 00-1.51-1H3a2 2 0 110-4h.09a1.65 1.65 0 001.51-1 1.65 1.65 0 00-.33-1.82l-.06-.06a2 2 0 112.83-2.83l.06.06a1.65 1.65 0 001.82.33h0a1.65 1.65 0 001-1.51V3a2 2 0 114 0v.09a1.65 1.65 0 001 1.51h0a1.65 1.65 0 001.82-.33l.06-.06a2 2 0 112.83 2.83l-.06.06a1.65 1.65 0 00-.33 1.82v0a1.65 1.65 0 001.51 1H21a2 2 0 110 4h-.09a1.65 1.65 0 00-1.51 1z"/>
            </svg>
          </button>
          {/* Logout — icon button in red, opens confirmation modal */}
          <button onClick={() => { setConfirmLogout(true); setToolsOpen(false); setSettingsOpen(false); }}
            title="Log out"
            style={{
              width: 38, height: 38, borderRadius: 11,
              background: "rgba(239,68,68,0.06)",
              border: "1px solid rgba(239,68,68,0.25)",
              color: "#F87171",
              cursor: "pointer",
              display: "flex", alignItems: "center", justifyContent: "center",
              transition: "all 0.15s ease",
            }}>
            <svg width="17" height="17" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
              <path d="M9 21H5a2 2 0 01-2-2V5a2 2 0 012-2h4"/>
              <polyline points="16 17 21 12 16 7"/>
              <line x1="21" y1="12" x2="9" y2="12"/>
            </svg>
          </button>
          {settingsOpen && (
            <>
              {/* Click-outside backdrop */}
              <div onClick={() => setSettingsOpen(false)}
                style={{ position: "fixed", inset: 0, zIndex: 99, background: "transparent" }} />
              <div style={{
                position: "absolute", top: 46, right: 0, zIndex: 100,
                background: "linear-gradient(165deg,#0f0a1f 0%,#0a0a14 60%,#080812 100%)",
                border: "1px solid rgba(124,58,237,0.3)",
                borderRadius: 16, padding: 16, width: 300,
                boxShadow: "0 12px 40px rgba(0,0,0,0.7), 0 0 24px rgba(124,58,237,0.15)",
                maxHeight: "75vh", overflowY: "auto",
              }}>
                {/* LED top edge */}
                <div style={{
                  position: "absolute", top: 0, left: 16, right: 16, height: 2,
                  background: "linear-gradient(90deg,transparent,#C026D3,#7C3AED,#3B82F6,transparent)",
                  boxShadow: "0 0 8px rgba(124,58,237,0.7)",
                }} />
                <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", marginBottom: 14 }}>
                  <div style={{ display: "flex", alignItems: "center", gap: 8 }}>
                    <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="#A78BFA" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                      <circle cx="12" cy="12" r="3"/>
                      <path d="M19.4 15a1.65 1.65 0 00.33 1.82l.06.06a2 2 0 11-2.83 2.83l-.06-.06a1.65 1.65 0 00-1.82-.33 1.65 1.65 0 00-1 1.51V21a2 2 0 11-4 0v-.09a1.65 1.65 0 00-1-1.51 1.65 1.65 0 00-1.82.33l-.06.06a2 2 0 11-2.83-2.83l.06-.06a1.65 1.65 0 00.33-1.82 1.65 1.65 0 00-1.51-1H3a2 2 0 110-4h.09a1.65 1.65 0 001.51-1 1.65 1.65 0 00-.33-1.82l-.06-.06a2 2 0 112.83-2.83l.06.06a1.65 1.65 0 001.82.33h0a1.65 1.65 0 001-1.51V3a2 2 0 114 0v.09a1.65 1.65 0 001 1.51h0a1.65 1.65 0 001.82-.33l.06-.06a2 2 0 112.83 2.83l-.06.06a1.65 1.65 0 00-.33 1.82v0a1.65 1.65 0 001.51 1H21a2 2 0 110 4h-.09a1.65 1.65 0 00-1.51 1z"/>
                    </svg>
                    <div style={{
                      color: "#A78BFA", fontSize: 11, fontWeight: 900, letterSpacing: 1.5,
                      textShadow: "0 0 6px rgba(124,58,237,0.5)",
                    }}>SETTINGS</div>
                  </div>
                  <button onClick={() => setSettingsOpen(false)} style={{
                    background: "rgba(255,255,255,0.04)", border: "1px solid #2a2a2a",
                    borderRadius: "50%", width: 26, height: 26, color: "#888",
                    cursor: "pointer", display: "flex", alignItems: "center", justifyContent: "center",
                  }}>
                    <svg width="11" height="11" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round">
                      <line x1="18" y1="6" x2="6" y2="18"/><line x1="6" y1="6" x2="18" y2="18"/>
                    </svg>
                  </button>
                </div>
                {/* ── Username accordion ── */}
                <div style={{ borderRadius: 10, border: "1px solid rgba(124,58,237,0.15)", marginBottom: 8, overflow: "hidden", background: "rgba(0,0,0,0.3)" }}>
                <button onClick={() => setOpenSettingsSection(openSettingsSection === "username" ? null : "username")}
                  style={{ width: "100%", display: "flex", justifyContent: "space-between", alignItems: "center",
                    padding: "12px 14px", background: openSettingsSection === "username" ? "#1a1a1a" : "#141414",
                    border: "none", cursor: "pointer", color: "white", fontWeight: 700, fontSize: 14 }}>
                  <span>Username</span>
                  <span style={{ color: "#555", fontSize: 12, transition: "transform 0.2s",
                    transform: openSettingsSection === "username" ? "rotate(180deg)" : "none" }}>▼</span>
                </button>
                {openSettingsSection === "username" && (
                  <div style={{ padding: "12px 14px", background: "#1a1a1a", borderTop: "1px solid #222" }}>
                    <input value={newUsername} onChange={e => setNewUsername(e.target.value)}
                      placeholder={user.username || "Set your username"}
                      style={{ width: "100%", background: "#111", border: "1px solid #333", borderRadius: 10,
                        padding: "10px 14px", color: "white", fontSize: 14, outline: "none", boxSizing: "border-box", marginBottom: 10 }} />
                    <button onClick={async () => {
                      if (!newUsername.trim()) return;
                      try {
                        await apiFetch("/api/auth/set-username", { method: "POST", body: JSON.stringify({ username: newUsername.trim() }) });
                        setUser({ ...user, username: newUsername.trim() }); setNewUsername(""); setSettingsMsg("Username updated!"); setTimeout(() => setSettingsMsg(""), 2500);
                      } catch (e) { setSettingsMsg("Error: " + e.message); }
                    }} style={{ width: "100%", background: "#C026D3", border: "none", borderRadius: 10,
                      color: "white", fontWeight: 800, padding: "10px", fontSize: 14, cursor: "pointer" }}>
                      {user.username ? "Update Username" : "Set Username"}
                    </button>
                  </div>
                )}
              </div>

              {/* ── Manage Subscription ── only for paid plans
                  Opens Stripe's hosted Customer Portal where users can
                  cancel their sub, update card, view invoices etc.
                  No accordion needed — single tap → Stripe redirect.
                  Hidden for lifetime accounts (Trelli, Mikez, HMbarsdat)
                  since they never paid through Stripe and have no
                  subscription to manage. */}
              {(user.plan === "artist" || user.plan === "producer") && user.billingInterval !== "lifetime" && (
                <button
                  onClick={async () => {
                    setSettingsMsg("Opening subscription manager…");
                    try {
                      const r = await apiFetch("/api/stripe/customer-portal", { method: "POST" });
                      if (r && r.portal_url) {
                        window.location.href = r.portal_url;
                      } else {
                        setSettingsMsg("Couldn't open subscription manager");
                      }
                    } catch (e) {
                      setSettingsMsg("Error: " + (e.message || "couldn't open"));
                      setTimeout(() => setSettingsMsg(""), 3500);
                    }
                  }}
                  style={{
                    width: "100%", display: "flex", justifyContent: "space-between", alignItems: "center",
                    padding: "12px 14px",
                    background: "#141414",
                    borderRadius: 10,
                    border: "1px solid rgba(124,58,237,0.25)",
                    marginBottom: 8,
                    cursor: "pointer",
                    color: "white", fontWeight: 700, fontSize: 14,
                  }}>
                  <span style={{ display: "flex", alignItems: "center", gap: 10 }}>
                    <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="#A78BFA" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                      <rect x="2" y="5" width="20" height="14" rx="2"/>
                      <line x1="2" y1="10" x2="22" y2="10"/>
                    </svg>
                    Manage Subscription
                  </span>
                  <span style={{ color: "#555", fontSize: 14 }}>›</span>
                </button>
              )}

              {/* ── Change Password accordion ── */}
              <div style={{ borderRadius: 10, border: "1px solid #222", marginBottom: 8, overflow: "hidden" }}>
                <button onClick={() => setOpenSettingsSection(openSettingsSection === "password" ? null : "password")}
                  style={{ width: "100%", display: "flex", justifyContent: "space-between", alignItems: "center",
                    padding: "12px 14px", background: openSettingsSection === "password" ? "#1a1a1a" : "#141414",
                    border: "none", cursor: "pointer", color: "white", fontWeight: 700, fontSize: 14 }}>
                  <span>Change Password</span>
                  <span style={{ color: "#555", fontSize: 12, transition: "transform 0.2s",
                    transform: openSettingsSection === "password" ? "rotate(180deg)" : "none" }}>▼</span>
                </button>
                {openSettingsSection === "password" && (
                  <div style={{ padding: "12px 14px", background: "#1a1a1a", borderTop: "1px solid #222" }}>
                    <input value={currentPw} onChange={e => setCurrentPw(e.target.value)} type="password" placeholder="Current password"
                      style={{ width: "100%", background: "#111", border: "1px solid #333", borderRadius: 10,
                        padding: "10px 14px", color: "white", fontSize: 14, outline: "none", boxSizing: "border-box", marginBottom: 8 }} />
                    <input value={newPw} onChange={e => setNewPw(e.target.value)} type="password" placeholder="New password"
                      style={{ width: "100%", background: "#111", border: "1px solid #333", borderRadius: 10,
                        padding: "10px 14px", color: "white", fontSize: 14, outline: "none", boxSizing: "border-box", marginBottom: 8 }} />
                    <input value={confirmPw} onChange={e => setConfirmPw(e.target.value)} type="password" placeholder="Confirm new password"
                      style={{ width: "100%", background: "#111", border: "1px solid #333", borderRadius: 10,
                        padding: "10px 14px", color: "white", fontSize: 14, outline: "none", boxSizing: "border-box", marginBottom: 10 }} />
                    <button onClick={async () => {
                      if (!currentPw || !newPw) return;
                      if (newPw !== confirmPw) { setSettingsMsg("Passwords do not match"); return; }
                      if (newPw.length < 6) { setSettingsMsg("Password must be at least 6 characters"); return; }
                      try {
                        await apiFetch("/api/auth/change-password", { method: "POST", body: JSON.stringify({ current_password: currentPw, new_password: newPw }) });
                        setCurrentPw(""); setNewPw(""); setConfirmPw(""); setSettingsMsg("Password changed!"); setTimeout(() => setSettingsMsg(""), 2500);
                      } catch (e) { setSettingsMsg("Error: " + e.message); }
                    }} style={{ width: "100%", background: "#1e1e1e", border: "1.5px solid #333", borderRadius: 10,
                      color: "#aaa", fontWeight: 800, padding: "10px", fontSize: 14, cursor: "pointer" }}>
                      Change Password
                    </button>
                  </div>
                )}
              </div>

              {/* ── Debug Logs (admin only — Trelli) ──────────────────── */}
              {(user.is_admin === true || user.username === "Trelli") && (
                <div style={{ background: "#111", border: "1px solid #2a2a2a", borderRadius: 10, marginBottom: 8, overflow: "hidden" }}>
                  <button onClick={() => setOpenSettingsSection(openSettingsSection === "debug" ? null : "debug")}
                    style={{ width: "100%", display: "flex", justifyContent: "space-between", alignItems: "center",
                      padding: "12px 14px", background: openSettingsSection === "debug" ? "#1a1a1a" : "#141414",
                      border: "none", color: "white", fontSize: 14, fontWeight: 700, cursor: "pointer" }}>
                    <span style={{ display: "inline-flex", alignItems: "center", gap: 8 }}>
                      <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="#A78BFA" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" style={{ filter: "drop-shadow(0 0 4px rgba(124,58,237,0.6))" }}>
                        <path d="M6 18h8"/>
                        <path d="M3 22h18"/>
                        <path d="M14 22a7 7 0 1 0 0-14h-1"/>
                        <path d="M9 14h2"/>
                        <path d="M9 12a2 2 0 0 1-2-2V6h6v4a2 2 0 0 1-2 2Z"/>
                        <path d="M12 6V3a1 1 0 0 0-1-1H9a1 1 0 0 0-1 1v3"/>
                      </svg>
                      Debug Logs
                      <span style={{ color: "#A78BFA", fontSize: 9, fontWeight: 800, letterSpacing: 0.5, marginLeft: 2 }}>ADMIN</span>
                    </span>
                    <span style={{ color: "#666", fontSize: 12, transition: "transform 0.2s",
                      transform: openSettingsSection === "debug" ? "rotate(180deg)" : "none" }}>▼</span>
                  </button>
                  {openSettingsSection === "debug" && (
                    <div style={{ padding: "14px", background: "#1a1a1a", borderTop: "1px solid #222" }}>
                      <DebugLogViewer />
                    </div>
                  )}
                </div>
              )}

              {/* ── Lifetime Accounts (admin only — manage who gets lifetime) ─── */}
              {(user.is_admin === true || user.username === "Trelli") && (
                <div style={{ background: "#111", border: "1px solid #2a2a2a", borderRadius: 10, marginBottom: 8, overflow: "hidden" }}>
                  <button onClick={() => setOpenSettingsSection(openSettingsSection === "lifetime" ? null : "lifetime")}
                    style={{ width: "100%", display: "flex", justifyContent: "space-between", alignItems: "center",
                      padding: "12px 14px", background: openSettingsSection === "lifetime" ? "#1a1a1a" : "#141414",
                      border: "none", color: "white", fontSize: 14, fontWeight: 700, cursor: "pointer" }}>
                    <span style={{ display: "inline-flex", alignItems: "center", gap: 8 }}>
                      <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="#A78BFA" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" style={{ filter: "drop-shadow(0 0 4px rgba(124,58,237,0.6))" }}>
                        <path d="M12 2l3 7h7l-5.5 4.5L18 21l-6-4-6 4 1.5-7.5L2 9h7z"/>
                      </svg>
                      Lifetime Accounts
                      <span style={{ color: "#A78BFA", fontSize: 9, fontWeight: 800, letterSpacing: 0.5, marginLeft: 2 }}>ADMIN</span>
                    </span>
                    <span style={{ color: "#666", fontSize: 12, transition: "transform 0.2s",
                      transform: openSettingsSection === "lifetime" ? "rotate(180deg)" : "none" }}>▼</span>
                  </button>
                  {openSettingsSection === "lifetime" && (
                    <div style={{ padding: "14px", background: "#1a1a1a", borderTop: "1px solid #222" }}>
                      <LifetimeAccountsManager />
                    </div>
                  )}
                </div>
              )}

              {/* ── Password Reset Link Generator (admin only) ─────────────
                  Lets the admin manually generate a reset link for any
                  user when email delivery is failing (Hotmail/Outlook
                  often silently rejects transactional emails). The
                  admin types the user's email or username, gets back a
                  one-hour reset URL, and hands it to the user via DM
                  or text. Same backing token as the normal flow — the
                  only difference is the link arrives via the admin
                  instead of via email. */}
              {(user.is_admin === true || user.username === "Trelli") && (
                <div style={{ background: "#111", border: "1px solid #2a2a2a", borderRadius: 10, marginBottom: 8, overflow: "hidden" }}>
                  <button onClick={() => setOpenSettingsSection(openSettingsSection === "resetlink" ? null : "resetlink")}
                    style={{ width: "100%", display: "flex", justifyContent: "space-between", alignItems: "center",
                      padding: "12px 14px", background: openSettingsSection === "resetlink" ? "#1a1a1a" : "#141414",
                      border: "none", color: "white", fontSize: 14, fontWeight: 700, cursor: "pointer" }}>
                    <span style={{ display: "inline-flex", alignItems: "center", gap: 8 }}>
                      <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="#A78BFA" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" style={{ filter: "drop-shadow(0 0 4px rgba(124,58,237,0.6))" }}>
                        <path d="M21 2l-2 2m-7.61 7.61a5.5 5.5 0 1 1-7.778 7.778 5.5 5.5 0 0 1 7.777-7.777zm0 0L15.5 7.5m0 0l3 3L22 7l-3-3m-3.5 3.5L19 4"/>
                      </svg>
                      Password Reset Link
                      <span style={{ color: "#A78BFA", fontSize: 9, fontWeight: 800, letterSpacing: 0.5, marginLeft: 2 }}>ADMIN</span>
                    </span>
                    <span style={{ color: "#666", fontSize: 12, transition: "transform 0.2s",
                      transform: openSettingsSection === "resetlink" ? "rotate(180deg)" : "none" }}>▼</span>
                  </button>
                  {openSettingsSection === "resetlink" && (
                    <div style={{ padding: "14px", background: "#1a1a1a", borderTop: "1px solid #222" }}>
                      <AdminResetLinkGenerator />
                    </div>
                  )}
                </div>
              )}

              {/* ── Users (admin only — list of everyone who joined) ─── */}
              {(user.is_admin === true || user.username === "Trelli") && (
                <div style={{ background: "#111", border: "1px solid #2a2a2a", borderRadius: 10, marginBottom: 8, overflow: "hidden" }}>
                  <button onClick={() => setOpenSettingsSection(openSettingsSection === "users" ? null : "users")}
                    style={{ width: "100%", display: "flex", justifyContent: "space-between", alignItems: "center",
                      padding: "12px 14px", background: openSettingsSection === "users" ? "#1a1a1a" : "#141414",
                      border: "none", color: "white", fontSize: 14, fontWeight: 700, cursor: "pointer" }}>
                    <span style={{ display: "inline-flex", alignItems: "center", gap: 8 }}>
                      <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="#A78BFA" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" style={{ filter: "drop-shadow(0 0 4px rgba(124,58,237,0.6))" }}>
                        <path d="M17 21v-2a4 4 0 0 0-4-4H5a4 4 0 0 0-4 4v2"/>
                        <circle cx="9" cy="7" r="4"/>
                        <path d="M23 21v-2a4 4 0 0 0-3-3.87"/>
                        <path d="M16 3.13a4 4 0 0 1 0 7.75"/>
                      </svg>
                      Users
                      <span style={{ color: "#A78BFA", fontSize: 9, fontWeight: 800, letterSpacing: 0.5, marginLeft: 2 }}>ADMIN</span>
                    </span>
                    <span style={{ color: "#666", fontSize: 12, transition: "transform 0.2s",
                      transform: openSettingsSection === "users" ? "rotate(180deg)" : "none" }}>▼</span>
                  </button>
                  {openSettingsSection === "users" && (
                    <div style={{ padding: "14px", background: "#1a1a1a", borderTop: "1px solid #222" }}>
                      <UsersManager />
                    </div>
                  )}
                </div>
              )}

              {/* ── Email List (admin only — marketing exports) ───────── */}
              {(user.is_admin === true || user.username === "Trelli") && (
                <div style={{ background: "#111", border: "1px solid #2a2a2a", borderRadius: 10, marginBottom: 8, overflow: "hidden" }}>
                  <button onClick={() => setOpenSettingsSection(openSettingsSection === "emaillist" ? null : "emaillist")}
                    style={{ width: "100%", display: "flex", justifyContent: "space-between", alignItems: "center",
                      padding: "12px 14px", background: openSettingsSection === "emaillist" ? "#1a1a1a" : "#141414",
                      border: "none", color: "white", fontSize: 14, fontWeight: 700, cursor: "pointer" }}>
                    <span style={{ display: "inline-flex", alignItems: "center", gap: 8 }}>
                      <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="#A78BFA" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" style={{ filter: "drop-shadow(0 0 4px rgba(124,58,237,0.6))" }}>
                        <path d="M4 4h16c1.1 0 2 .9 2 2v12c0 1.1-.9 2-2 2H4c-1.1 0-2-.9-2-2V6c0-1.1.9-2 2-2z"/>
                        <polyline points="22,6 12,13 2,6"/>
                      </svg>
                      Email List
                      <span style={{ color: "#A78BFA", fontSize: 9, fontWeight: 800, letterSpacing: 0.5, marginLeft: 2 }}>ADMIN</span>
                    </span>
                    <span style={{ color: "#666", fontSize: 12, transition: "transform 0.2s",
                      transform: openSettingsSection === "emaillist" ? "rotate(180deg)" : "none" }}>▼</span>
                  </button>
                  {openSettingsSection === "emaillist" && (
                    <div style={{ padding: "14px", background: "#1a1a1a", borderTop: "1px solid #222" }}>
                      <EmailListExport />
                    </div>
                  )}
                </div>
              )}

              {/* ── YouTube Cache (admin only — clear cached results) ──── */}
              {(user.is_admin === true || user.username === "Trelli") && (
                <div style={{ background: "#111", border: "1px solid #2a2a2a", borderRadius: 10, marginBottom: 8, overflow: "hidden" }}>
                  <button onClick={() => setOpenSettingsSection(openSettingsSection === "ytcache" ? null : "ytcache")}
                    style={{ width: "100%", display: "flex", justifyContent: "space-between", alignItems: "center",
                      padding: "12px 14px", background: openSettingsSection === "ytcache" ? "#1a1a1a" : "#141414",
                      border: "none", color: "white", fontSize: 14, fontWeight: 700, cursor: "pointer" }}>
                    <span style={{ display: "inline-flex", alignItems: "center", gap: 8 }}>
                      <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="#A78BFA" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" style={{ filter: "drop-shadow(0 0 4px rgba(124,58,237,0.6))" }}>
                        <polyline points="23 4 23 10 17 10"/><path d="M20.49 15a9 9 0 1 1-2.12-9.36L23 10"/>
                      </svg>
                      YouTube Cache
                      <span style={{ color: "#A78BFA", fontSize: 9, fontWeight: 800, letterSpacing: 0.5, marginLeft: 2 }}>ADMIN</span>
                    </span>
                    <span style={{ color: "#666", fontSize: 12, transition: "transform 0.2s",
                      transform: openSettingsSection === "ytcache" ? "rotate(180deg)" : "none" }}>▼</span>
                  </button>
                  {openSettingsSection === "ytcache" && (
                    <div style={{ padding: "14px", background: "#1a1a1a", borderTop: "1px solid #222" }}>
                      <YouTubeCacheAdmin />
                    </div>
                  )}
                </div>
              )}

              {/* ── Beat Artwork (Producer Pro only) ──────────────────── */}
              {user.isPro && (
                <div style={{ background: "#111", border: "1px solid #2a2a2a", borderRadius: 10, marginBottom: 8, overflow: "hidden" }}>
                  <button onClick={() => setOpenSettingsSection(openSettingsSection === "artwork" ? null : "artwork")}
                    style={{ width: "100%", display: "flex", justifyContent: "space-between", alignItems: "center",
                      padding: "12px 14px", background: openSettingsSection === "artwork" ? "#1a1a1a" : "#141414",
                      border: "none", color: "white", fontSize: 14, fontWeight: 700, cursor: "pointer" }}>
                    <span>Beat Artwork</span>
                    <span style={{ color: "#666", fontSize: 12, transition: "transform 0.2s",
                      transform: openSettingsSection === "artwork" ? "rotate(180deg)" : "none" }}>▼</span>
                  </button>
                  {openSettingsSection === "artwork" && (
                    <BeatArtworkUploader user={user} setUser={setUser} />
                  )}
                </div>
              )}

              {/* ── What's New (changelog) — visible to all users ─────── */}
              <button
                onClick={function() {
                  try { window.dispatchEvent(new CustomEvent("bf:openChangelog")); } catch (_) {}
                  setSettingsOpen(false);
                }}
                style={{
                  width: "100%", display: "flex", justifyContent: "space-between", alignItems: "center",
                  padding: "12px 14px",
                  background: "#141414",
                  borderRadius: 10,
                  border: "1px solid #2a2a2a",
                  marginBottom: 8,
                  cursor: "pointer",
                  color: "white", fontWeight: 700, fontSize: 14,
                }}>
                <span style={{ display: "flex", alignItems: "center", gap: 10 }}>
                  <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="#A78BFA" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                    <path d="M14 2H6a2 2 0 0 0-2 2v16a2 2 0 0 0 2 2h12a2 2 0 0 0 2-2V8z"/>
                    <polyline points="14 2 14 8 20 8"/>
                    <line x1="9" y1="15" x2="15" y2="15"/>
                    <line x1="9" y1="11" x2="13" y2="11"/>
                  </svg>
                  What's New
                </span>
                <span style={{ color: "#555", fontSize: 14 }}>›</span>
              </button>

              {/* ── About BeatFinder — visible to all users ─────────── */}
              <button
                onClick={function() {
                  try { window.dispatchEvent(new CustomEvent("bf:openAbout")); } catch (_) {}
                  setSettingsOpen(false);
                }}
                style={{
                  width: "100%", display: "flex", justifyContent: "space-between", alignItems: "center",
                  padding: "12px 14px",
                  background: "#141414",
                  borderRadius: 10,
                  border: "1px solid #2a2a2a",
                  marginBottom: 8,
                  cursor: "pointer",
                  color: "white", fontWeight: 700, fontSize: 14,
                }}>
                <span style={{ display: "flex", alignItems: "center", gap: 10 }}>
                  <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="#A78BFA" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                    <circle cx="12" cy="12" r="10"/>
                    <line x1="12" y1="16" x2="12" y2="12"/>
                    <line x1="12" y1="8" x2="12.01" y2="8"/>
                  </svg>
                  About BeatFinder
                </span>
                <span style={{ color: "#555", fontSize: 14 }}>›</span>
              </button>

              {/* ── Blocked Users ──────────────────────────────────────── */}
              <div style={{ background: "#111", border: "1px solid #2a2a2a", borderRadius: 10, marginBottom: 8, overflow: "hidden" }}>
                <button onClick={() => setOpenSettingsSection(openSettingsSection === "blocked" ? null : "blocked")}
                  style={{ width: "100%", display: "flex", justifyContent: "space-between", alignItems: "center",
                    padding: "12px 14px", background: openSettingsSection === "blocked" ? "#1a1a1a" : "#141414",
                    border: "none", color: "white", fontSize: 14, fontWeight: 700, cursor: "pointer" }}>
                  <span>Blocked Users</span>
                  <span style={{ color: "#666", fontSize: 12, transition: "transform 0.2s",
                    transform: openSettingsSection === "blocked" ? "rotate(180deg)" : "none" }}>▼</span>
                </button>
                {openSettingsSection === "blocked" && (
                  <BlockedUsersPanel />
                )}
              </div>

              {/* ── Your Data (GDPR) ───────────────────────────────────── */}
              <div style={{ background: "#111", border: "1px solid #2a2a2a", borderRadius: 10, marginBottom: 8, overflow: "hidden" }}>
                <button onClick={() => setOpenSettingsSection(openSettingsSection === "data" ? null : "data")}
                  style={{ width: "100%", display: "flex", justifyContent: "space-between", alignItems: "center",
                    padding: "12px 14px", background: openSettingsSection === "data" ? "#1a1a1a" : "#141414",
                    border: "none", color: "white", fontSize: 14, fontWeight: 700, cursor: "pointer" }}>
                  <span>Your Data</span>
                  <span style={{ color: "#666", fontSize: 12, transition: "transform 0.2s",
                    transform: openSettingsSection === "data" ? "rotate(180deg)" : "none" }}>▼</span>
                </button>
                {openSettingsSection === "data" && (
                  <div style={{ padding: "14px", background: "#1a1a1a", borderTop: "1px solid #222" }}>
                    <div style={{ color: "#aaa", fontSize: 12, lineHeight: 1.5, marginBottom: 12 }}>
                      Under UK GDPR you have the right to access and erase your personal data.
                    </div>
                    <button onClick={async function() {
                      try {
                        var data = await apiFetch("/api/auth/export-my-data");
                        var blob = new Blob([JSON.stringify(data, null, 2)], { type: "application/json" });
                        var url = URL.createObjectURL(blob);
                        var a = document.createElement("a");
                        a.href = url;
                        a.download = "beatfinder_data_" + (user.username || "export") + "_" + new Date().toISOString().slice(0,10) + ".json";
                        document.body.appendChild(a); a.click();
                        setTimeout(function(){ document.body.removeChild(a); URL.revokeObjectURL(url); }, 100);
                        setSettingsMsg("Data export downloaded successfully.");
                      } catch(e) {
                        setSettingsMsg("Export failed: " + e.message);
                      }
                    }} style={{
                      width: "100%", marginBottom: 10,
                      background: "linear-gradient(135deg,#3B82F6 0%,#6366F1 50%,#7C3AED 100%)",
                      border: "1.5px solid rgba(124,58,237,0.6)",
                      borderRadius: 12, padding: "12px",
                      color: "white", fontWeight: 900, fontSize: 13,
                      letterSpacing: 0.5, cursor: "pointer",
                      display: "flex", alignItems: "center", justifyContent: "center", gap: 8,
                      boxShadow: "0 2px 12px rgba(124,58,237,0.4), inset 0 1px 0 rgba(255,255,255,0.2)",
                      textShadow: "0 1px 2px rgba(0,0,0,0.3)",
                    }}>
                      <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="white" strokeWidth="2.5" strokeLinecap="round"><path d="M12 3v13M6 11l6 6 6-6"/><path d="M4 20h16"/></svg>
                      Download My Data (JSON)
                    </button>
                    <div style={{ color: "#666", fontSize: 10, marginBottom: 14, lineHeight: 1.5 }}>
                      Downloads everything we hold on you (profile, beats, leases, lyrics, messages).
                    </div>
                    <button onClick={function() { setShowDeleteAccountConfirm(true); }} style={{
                      width: "100%",
                      background: "transparent",
                      border: "1.5px solid #DC2626", borderRadius: 12, padding: "12px",
                      color: "#F87171", fontWeight: 900, fontSize: 13,
                      letterSpacing: 0.5, cursor: "pointer",
                    }}>
                      Delete My Account
                    </button>
                    <div style={{ color: "#666", fontSize: 10, marginTop: 6, lineHeight: 1.5 }}>
                      Permanently anonymises your account. Lease contracts and tax records are retained for 6 years as required by UK law.
                    </div>
                  </div>
                )}
              </div>

              {/* ── Terms & Conditions ─────────────────────────────────── */}
              <div style={{ background: "#111", border: "1px solid #2a2a2a", borderRadius: 10, marginBottom: 8, overflow: "hidden" }}>
                <button onClick={() => setOpenSettingsSection(openSettingsSection === "terms" ? null : "terms")}
                  style={{ width: "100%", display: "flex", justifyContent: "space-between", alignItems: "center",
                    padding: "12px 14px", background: openSettingsSection === "terms" ? "#1a1a1a" : "#141414",
                    border: "none", color: "white", fontSize: 14, fontWeight: 700, cursor: "pointer" }}>
                  <span>Terms &amp; Conditions</span>
                  <span style={{ color: "#666", fontSize: 12, transition: "transform 0.2s",
                    transform: openSettingsSection === "terms" ? "rotate(180deg)" : "none" }}>▼</span>
                </button>
                {openSettingsSection === "terms" && (
                  <div style={{
                    padding: "14px 14px 16px", background: "#1a1a1a", borderTop: "1px solid #222",
                    maxHeight: 360, overflowY: "auto", WebkitOverflowScrolling: "touch",
                  }}>
                    <TermsContent compact />
                  </div>
                )}
              </div>

              {/* ── Privacy Policy ─────────────────────────────────────── */}
              <div style={{ background: "#111", border: "1px solid #2a2a2a", borderRadius: 10, marginBottom: 8, overflow: "hidden" }}>
                <button onClick={() => setOpenSettingsSection(openSettingsSection === "privacy" ? null : "privacy")}
                  style={{ width: "100%", display: "flex", justifyContent: "space-between", alignItems: "center",
                    padding: "12px 14px", background: openSettingsSection === "privacy" ? "#1a1a1a" : "#141414",
                    border: "none", color: "white", fontSize: 14, fontWeight: 700, cursor: "pointer" }}>
                  <span>Privacy Policy</span>
                  <span style={{ color: "#666", fontSize: 12, transition: "transform 0.2s",
                    transform: openSettingsSection === "privacy" ? "rotate(180deg)" : "none" }}>▼</span>
                </button>
                {openSettingsSection === "privacy" && (
                  <div style={{
                    padding: "14px 14px 16px", background: "#1a1a1a", borderTop: "1px solid #222",
                    maxHeight: 360, overflowY: "auto", WebkitOverflowScrolling: "touch",
                  }}>
                    <PrivacyContent compact />
                  </div>
                )}
              </div>

              {/* ── Contact Support ─────────────────────────────────────── */}
              <div style={{ background: "#111", border: "1px solid #2a2a2a", borderRadius: 10, marginBottom: 8, overflow: "hidden" }}>
                <button onClick={() => setOpenSettingsSection(openSettingsSection === "support" ? null : "support")}
                  style={{ width: "100%", display: "flex", justifyContent: "space-between", alignItems: "center",
                    padding: "12px 14px", background: openSettingsSection === "support" ? "#1a1a1a" : "#141414",
                    border: "none", color: "white", fontSize: 14, fontWeight: 700, cursor: "pointer" }}>
                  <span>Contact Support</span>
                  <span style={{ color: "#666", fontSize: 12, transition: "transform 0.2s",
                    transform: openSettingsSection === "support" ? "rotate(180deg)" : "none" }}>▼</span>
                </button>
                {openSettingsSection === "support" && (
                  <ContactSupportForm currentUser={user} />
                )}
              </div>

              {settingsMsg && <div style={{ color: settingsMsg.startsWith("Error") ? "#F87171" : "#22C55E", fontSize: 13, textAlign: "center", fontWeight: 600, marginTop: 8 }}>{settingsMsg}</div>}
            </div>
            </>
          )}

          {/* ── Tools panel ── */}
          {toolsOpen && (
            <>
              <div onClick={() => setToolsOpen(false)}
                style={{ position: "fixed", inset: 0, zIndex: 99, background: "transparent" }} />
            <div style={{
              position: "absolute", top: 46, right: 0, zIndex: 100,
              background: "linear-gradient(165deg,#0f0a1f 0%,#0a0a14 60%,#080812 100%)",
              border: "1px solid rgba(124,58,237,0.3)",
              borderRadius: 16, padding: 16, width: 320,
              boxShadow: "0 12px 40px rgba(0,0,0,0.7), 0 0 24px rgba(124,58,237,0.15)",
              maxHeight: "75vh", overflowY: "auto",
            }}>
              {/* LED top edge */}
              <div style={{
                position: "absolute", top: 0, left: 16, right: 16, height: 2,
                background: "linear-gradient(90deg,transparent,#C026D3,#7C3AED,#3B82F6,transparent)",
                boxShadow: "0 0 8px rgba(124,58,237,0.7)",
              }} />
              <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", marginBottom: 14 }}>
                <div style={{ display: "flex", alignItems: "center", gap: 8 }}>
                  <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="#A78BFA" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                    <path d="M14.7 6.3a1 1 0 000 1.4l1.6 1.6a1 1 0 001.4 0l3.77-3.77a6 6 0 01-7.94 7.94l-6.91 6.91a2.12 2.12 0 01-3-3l6.91-6.91a6 6 0 017.94-7.94l-3.76 3.76z"/>
                  </svg>
                  <div style={{
                    color: "#A78BFA", fontSize: 11, fontWeight: 900, letterSpacing: 1.5,
                    textShadow: "0 0 6px rgba(124,58,237,0.5)",
                  }}>TOOLS</div>
                </div>
                <button onClick={() => setToolsOpen(false)} style={{
                  background: "rgba(255,255,255,0.04)", border: "1px solid #2a2a2a",
                  borderRadius: "50%", width: 26, height: 26, color: "#888",
                  cursor: "pointer", display: "flex", alignItems: "center", justifyContent: "center",
                }}>
                  <svg width="11" height="11" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round">
                    <line x1="18" y1="6" x2="6" y2="18"/><line x1="6" y1="6" x2="18" y2="18"/>
                  </svg>
                </button>
              </div>

              {/* Artist Pro only (not Producer Pro) — artist tools */}
              {user.isArtistPro && !user.isPro && (
                <div style={{ marginBottom: 16 }}>
                  <div style={{ color: "#A78BFA", fontSize: 10, fontWeight: 900, letterSpacing: 1.5, marginBottom: 10, textShadow: "0 0 6px rgba(124,58,237,0.5)" }}>ARTIST TOOLS</div>
                  <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: 8 }}>
                    {[
                      { id: "lyrics",    icon: "edit",     label: "My Lyrics",    desc: savedLyrics.length + " saved", color: "#C026D3" },
                      { id: "purchases", icon: "stripe",   label: "My Purchases", desc: "Beats you've bought",          color: "#F59E0B" },
                      { id: "members",   icon: "note",     label: "Members Area", desc: "Exclusive beats",              color: "#F59E0B" },
                      { id: "postmusic", icon: "note",     label: "Post Music",   desc: "Spotify link or upload",            color: "#1DB954" },
                      { id: "postvideo", icon: "vocalmic", label: "Post Video",   desc: "Upload or YouTube link",       color: "#3B82F6" },
                    ].map(item => (
                      <button key={item.id} onClick={() => { setToolsOpen(false); goSection(item.id); }}
                        style={{
                          background: "linear-gradient(165deg,rgba(15,10,31,0.9) 0%,rgba(10,10,20,0.95) 100%)",
                          borderRadius: 12, padding: "12px 10px",
                          border: "1px solid rgba(124,58,237,0.18)",
                          cursor: "pointer", textAlign: "left",
                          position: "relative", overflow: "hidden",
                          boxShadow: "0 2px 8px rgba(0,0,0,0.4)",
                        }}>
                        {/* Subtle LED top edge */}
                        <div style={{
                          position: "absolute", top: 0, left: 0, right: 0, height: 1,
                          background: "linear-gradient(90deg,transparent," + item.color + "55,transparent)",
                        }} />
                        <div style={{ marginBottom: 6, color: item.color, filter: "drop-shadow(0 0 4px " + item.color + "44)" }}><AppIcon id={item.icon} size={20} /></div>
                        <div style={{ color: "white", fontWeight: 700, fontSize: 13 }}>{item.label}</div>
                        <div style={{ color: item.color, fontSize: 10, marginTop: 2, fontWeight: 600, opacity: 0.8 }}>{item.desc}</div>
                      </button>
                    ))}
                  </div>
                  {savedLyrics.length > 0 && (
                    <div style={{ marginTop: 10 }}>
                      <div style={{ color: "#666", fontSize: 9, fontWeight: 800, letterSpacing: 1.5, marginBottom: 6 }}>RECENT LYRICS</div>
                      {savedLyrics.slice(0, 2).map((lyric, i) => (
                        <div key={i} onClick={() => { setToolsOpen(false); onEditLyric(lyric, i); }}
                          style={{ background: "rgba(0,0,0,0.4)", borderRadius: 8, padding: "8px 12px", marginBottom: 4, border: "1px solid rgba(124,58,237,0.18)", cursor: "pointer", display: "flex", justifyContent: "space-between", alignItems: "center" }}>
                          <div style={{ flex: 1, overflow: "hidden" }}>
                            <div style={{ color: "white", fontWeight: 600, fontSize: 12, whiteSpace: "nowrap", overflow: "hidden", textOverflow: "ellipsis" }}>{lyric.title || "Untitled"}</div>
                            <div style={{ color: "#555", fontSize: 11, marginTop: 1, whiteSpace: "nowrap", overflow: "hidden", textOverflow: "ellipsis" }}>{lyric.beatTitle}</div>
                          </div>
                          <span style={{ color: "#C026D3", marginLeft: 8 }}><AppIcon id="writing" size={14} /></span>
                        </div>
                      ))}
                    </div>
                  )}
                </div>
              )}

              {/* Producer Pro — all tools in one section (includes artist tools) */}
              {user.isPro && (
                <div>
                  <div style={{ color: "#A78BFA", fontSize: 10, fontWeight: 900, letterSpacing: 1.5, marginBottom: 10, textShadow: "0 0 6px rgba(124,58,237,0.5)" }}>PRODUCER TOOLS</div>
                  <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: 8 }}>
                    {[
                      { id: "upload",    icon: "upload",   label: "Upload Beat",    desc: "Add new beat",                                                              color: "#C026D3" },
                      { id: "manage",    icon: "knobs",    label: "My Uploads",     desc: uploads.length + " beats",                                                  color: "#F59E0B" },
                      { id: "purchases", icon: "stripe",   label: "My Purchases",   desc: "Beats you've bought",                                                       color: "#F59E0B" },
                      { id: "postmusic", icon: "note",     label: "Post Music",     desc: "Spotify link or upload",                                                        color: "#1DB954" },
                      { id: "postvideo", icon: "vocalmic", label: "Post Video",     desc: "Upload or YouTube link",                                                   color: "#3B82F6" },
                      { id: "lyrics",    icon: "edit",     label: "My Lyrics",      desc: savedLyrics.length + " saved",                                              color: "#818CF8" },
                      { id: "members",   icon: "note",     label: "Members Area",   desc: "Exclusive beats",                                                          color: "#F59E0B" },
                      { id: "stripe",    icon: "stripe",   label: "Stripe Payouts", desc: producerStats?.stripeConnected ? "Connected" : "Not connected",              color: "#22C55E" },
                      { id: "stats",     icon: "grid",     label: "Analytics",      desc: producerStats ? producerStats.totalDownloads + " downloads" : "Loading...", color: "#818CF8" },
                    ].map(item => (
                      <button key={item.id} onClick={() => { setToolsOpen(false); goSection(item.id); }}
                        style={{
                          background: "linear-gradient(165deg,rgba(15,10,31,0.9) 0%,rgba(10,10,20,0.95) 100%)",
                          borderRadius: 12, padding: "12px 10px",
                          border: "1px solid rgba(124,58,237,0.18)",
                          cursor: "pointer", textAlign: "left",
                          position: "relative", overflow: "hidden",
                          boxShadow: "0 2px 8px rgba(0,0,0,0.4)",
                        }}>
                        {/* Subtle LED top edge */}
                        <div style={{
                          position: "absolute", top: 0, left: 0, right: 0, height: 1,
                          background: "linear-gradient(90deg,transparent," + item.color + "55,transparent)",
                        }} />
                        <div style={{ marginBottom: 6, color: item.color, filter: "drop-shadow(0 0 4px " + item.color + "44)" }}><AppIcon id={item.icon} size={20} /></div>
                        <div style={{ color: "white", fontWeight: 700, fontSize: 13 }}>{item.label}</div>
                        <div style={{ color: item.color, fontSize: 10, marginTop: 2, fontWeight: 600, opacity: 0.8 }}>{item.desc}</div>
                      </button>
                    ))}
                  </div>
                </div>
              )}

              {!user.isArtistPro && (
                <div style={{ background: "linear-gradient(135deg,#1a0a00,#1a1000)", borderRadius: 12, padding: 14, border: "1px solid rgba(245,158,11,0.2)" }}>
                  <div style={{ color: "#F59E0B", fontWeight: 800, fontSize: 13, marginBottom: 4 }}>Upgrade to Pro</div>
                  <div style={{ color: "#888", fontSize: 12, marginBottom: 12 }}>Unlock tools from £4.99/mo</div>
                  <button onClick={() => { setToolsOpen(false); goSection("upgrade"); }} style={{ background: "linear-gradient(135deg,#F59E0B,#C026D3)", border: "none", borderRadius: 20, color: "white", fontWeight: 800, fontSize: 13, padding: "8px 20px", cursor: "pointer" }}>
                    View Plans
                  </button>
                </div>
              )}
            </div>
            </>
          )}
        </div>
      </div>

      
      <div style={{ marginBottom: 20 }}>
        <div style={{ color: "white", fontWeight: 800, fontSize: 18 }}>{user.name}</div>
        <div style={{ color: "#666", fontSize: 13 }}>{user.email}</div>
        {user.username && <div style={{ color: "#555", fontSize: 12, marginTop: 2 }}>@{user.username}</div>}
        <div style={{ marginTop: 8 }}>
          {user.isPro && (
            <span onClick={() => setOwnBadgePopup({ icon:"producer", text:"This user is currently subscribed to Producer Pro" })} style={{ display:"inline-flex", alignItems:"center", gap:6, background:"rgba(192,38,211,0.15)", border:"1px solid #C026D3", borderRadius:20, padding:"4px 14px", color:"#C026D3", fontWeight:800, fontSize:12, marginRight:6, cursor:"pointer" }}>
              <VerifiedBadge size={18} /> Producer Pro
            </span>
          )}
          {user.isArtistPro && !user.isPro && <span onClick={() => setOwnBadgePopup({ icon:"artist", text:"This user is currently subscribed to Artist Pro" })} style={{ display:"inline-block", background:"rgba(245,158,11,0.2)", border:"1px solid #F59E0B", borderRadius:20, padding:"4px 14px", color:"#F59E0B", fontWeight:800, fontSize:12, cursor:"pointer" }}><AppIcon id="vocalmic" size={20}/> Artist Pro</span>}
          {user.username === "Trelli" && <CEOBadge onClick={() => setOwnBadgePopup({ icon:"ceo", text:"Verified Chief Executive Officer" })} />}
        </div>
        <BadgeInfoPopup message={ownBadgePopup} onClose={() => setOwnBadgePopup(null)} />
      </div>

      {/* ── Logout confirmation modal ── */}
      {confirmLogout && (
        <div onClick={() => setConfirmLogout(false)} style={{
          position: "fixed", inset: 0, zIndex: 99997,
          background: "rgba(0,0,0,0.85)",
          display: "flex", alignItems: "center", justifyContent: "center",
          padding: 24,
          backdropFilter: "blur(4px)",
        }}>
          <div onClick={e => e.stopPropagation()} style={{
            background: "linear-gradient(165deg,#0f0a1f 0%,#0a0a14 60%,#080812 100%)",
            border: "1px solid rgba(239,68,68,0.3)",
            borderRadius: 18, padding: 24,
            maxWidth: 360, width: "100%",
            boxShadow: "0 16px 40px rgba(0,0,0,0.75), 0 0 24px rgba(239,68,68,0.15)",
            position: "relative", overflow: "hidden",
          }}>
            {/* LED top edge — red since this is a destructive confirmation */}
            <div style={{
              position: "absolute", top: 0, left: 0, right: 0, height: 2,
              background: "linear-gradient(90deg,transparent,#DC2626,#F87171,#DC2626,transparent)",
              boxShadow: "0 0 8px rgba(239,68,68,0.7)",
            }} />

            {/* Icon + title row */}
            <div style={{ display: "flex", alignItems: "center", gap: 14, marginBottom: 16 }}>
              <div style={{
                width: 48, height: 48, borderRadius: 14, flexShrink: 0,
                background: "linear-gradient(135deg,rgba(220,38,38,0.2),rgba(220,38,38,0.08))",
                border: "1px solid rgba(239,68,68,0.4)",
                display: "flex", alignItems: "center", justifyContent: "center",
                boxShadow: "0 0 14px rgba(239,68,68,0.3)",
              }}>
                <svg width="22" height="22" viewBox="0 0 24 24" fill="none" stroke="#F87171" strokeWidth="2.2" strokeLinecap="round" strokeLinejoin="round">
                  <path d="M9 21H5a2 2 0 01-2-2V5a2 2 0 012-2h4"/>
                  <polyline points="16 17 21 12 16 7"/>
                  <line x1="21" y1="12" x2="9" y2="12"/>
                </svg>
              </div>
              <div style={{ flex: 1, minWidth: 0 }}>
                <div style={{ color: "white", fontWeight: 800, fontSize: 18, marginBottom: 2 }}>
                  Sign Out?
                </div>
                <div style={{ color: "#888", fontSize: 12 }}>
                  @{user.username || user.name}
                </div>
              </div>
            </div>

            {/* Message + data-loss warning */}
            <div style={{
              color: "#bbb", fontSize: 13, lineHeight: 1.6,
              marginBottom: 14,
              padding: "12px 14px",
              background: "rgba(0,0,0,0.3)",
              border: "1px solid rgba(124,58,237,0.15)",
              borderRadius: 10,
            }}>
              You'll need to sign back in to access your account, beats, lyrics and messages.
            </div>

            {/* Data-loss warning — amber, attention-grabbing */}
            <div style={{
              display: "flex", gap: 10, alignItems: "flex-start",
              padding: "12px 14px", marginBottom: 22,
              background: "rgba(245,158,11,0.08)",
              border: "1px solid rgba(245,158,11,0.3)",
              borderRadius: 10,
            }}>
              <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="#F59E0B" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"
                style={{ flexShrink: 0, marginTop: 1, filter: "drop-shadow(0 0 4px rgba(245,158,11,0.5))" }}>
                <path d="M10.29 3.86L1.82 18a2 2 0 001.71 3h16.94a2 2 0 001.71-3L13.71 3.86a2 2 0 00-3.42 0z"/>
                <line x1="12" y1="9" x2="12" y2="13"/>
                <line x1="12" y1="17" x2="12.01" y2="17"/>
              </svg>
              <div style={{ flex: 1, color: "#FBBF24", fontSize: 12, lineHeight: 1.55, fontWeight: 600 }}>
                Save any open lyrics and Studio projects first — unsaved work will be lost on sign out.
              </div>
            </div>

            {/* Buttons row */}
            <div style={{ display: "flex", gap: 10 }}>
              <button onClick={() => setConfirmLogout(false)}
                style={{
                  flex: 1, padding: "13px",
                  background: "rgba(255,255,255,0.04)",
                  border: "1px solid rgba(255,255,255,0.1)",
                  borderRadius: 12,
                  color: "#ccc", fontWeight: 700, fontSize: 14,
                  cursor: "pointer", letterSpacing: 0.3,
                }}>
                Cancel
              </button>
              <button onClick={() => { setConfirmLogout(false); onLogout(); }}
                style={{
                  flex: 1, padding: "13px",
                  background: "linear-gradient(135deg,#DC2626,#B91C1C)",
                  border: "none",
                  borderRadius: 12,
                  color: "white", fontWeight: 800, fontSize: 14,
                  cursor: "pointer", letterSpacing: 0.4,
                  boxShadow: "0 4px 14px rgba(220,38,38,0.4), inset 0 1px 0 rgba(255,255,255,0.15)",
                  textShadow: "0 1px 2px rgba(0,0,0,0.3)",
                  display: "flex", alignItems: "center", justifyContent: "center", gap: 7,
                }}>
                <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="white" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round">
                  <path d="M9 21H5a2 2 0 01-2-2V5a2 2 0 012-2h4"/>
                  <polyline points="16 17 21 12 16 7"/>
                  <line x1="21" y1="12" x2="9" y2="12"/>
                </svg>
                Sign Out
              </button>
            </div>
          </div>
        </div>
      )}

      
      {!activeSection && (
        <div>

          {/* ── Edit Profile (rebuilt — card-based, LED theme) ── */}

          {/* Hero section header with gradient accent bar */}
          <div style={{
            marginBottom: 22,
            padding: "0 4px",
          }}>
            <div style={{
              height: 3, width: 48, borderRadius: 2,
              background: "linear-gradient(90deg,#C026D3,#7C3AED,#3B82F6)",
              boxShadow: "0 0 12px rgba(124,58,237,0.6)",
              marginBottom: 12,
            }} />
            <div style={{
              color: "white", fontWeight: 800, fontSize: 28,
              fontFamily: "'Bebas Neue',sans-serif", letterSpacing: 1.5, lineHeight: 1,
              marginBottom: 6,
            }}>
              UPDATE PROFILE
            </div>
            <div style={{ color: "#888", fontSize: 12, lineHeight: 1.5 }}>
              Customise how others see you on BeatFinder
            </div>
          </div>

          {/* Hidden file inputs */}
          <input ref={headerFileRef} type="file" accept="image/*" style={{ display: "none" }}
            onChange={function(e) {
              const file = e.target.files && e.target.files[0];
              e.target.value = "";  // allow re-selecting same file later
              if (!file) return;
              if (file.size > 10 * 1024 * 1024) { setBioMsg("Image too large — please choose one under 10MB"); setTimeout(() => setBioMsg(""), 3500); return; }
              // Open the crop modal — actual upload happens after the user
              // confirms a crop region. See HeaderCropModal further down.
              setHeaderCropFile(file);
            }} />

          {/* Header crop modal — opens after the user picks a file. On
              confirm, we upload the cropped blob via the same endpoint as
              before. */}
          {headerCropFile && (
            <HeaderCropModal
              file={headerCropFile}
              onCancel={function() { setHeaderCropFile(null); }}
              onConfirm={async function(blob) {
                setHeaderUploading(true);
                try {
                  const fd = new FormData();
                  // Backend expects a file param — give it a sensible name
                  // and the JPEG mime our cropper outputs.
                  fd.append("file", blob, "header.jpg");
                  const token = getToken() || "";
                  const res = await fetch(API_BASE + "/api/auth/header", {
                    method: "POST",
                    headers: { Authorization: "Bearer " + token },
                    body: fd,
                  });
                  const data = await res.json();
                  if (!res.ok) throw new Error(data.detail || "Upload failed");
                  setHeaderUrl(data.headerUrl);
                  setUser(u => ({ ...u, headerUrl: data.headerUrl }));
                  setBioMsg("Header photo updated!");
                  setTimeout(function(){ setBioMsg(""); }, 2500);
                } catch(err) {
                  setBioMsg("Error: " + err.message);
                  setTimeout(function(){ setBioMsg(""); }, 3000);
                }
                setHeaderUploading(false);
                setHeaderCropFile(null);
              }}
            />
          )}
          <input ref={avatarFileRef} type="file" accept="image/*" style={{ display: "none" }}
            onChange={function(e) {
              const file = e.target.files && e.target.files[0];
              e.target.value = "";
              if (!file) return;
              if (file.size > 5 * 1024 * 1024) { setBioMsg("Image too large — please choose one under 5MB"); setTimeout(() => setBioMsg(""), 3500); return; }
              if (!/^image\//.test(file.type)) { setBioMsg("That file isn't an image"); setTimeout(() => setBioMsg(""), 3000); return; }
              setAvatarCropFile(file);
            }} />
          {avatarCropFile && (
            <HeaderCropModal
              file={avatarCropFile}
              circle
              aspect={1}
              outW={640}
              outH={640}
              title="CROP YOUR PHOTO"
              onCancel={function(){ setAvatarCropFile(null); }}
              onConfirm={async function(blob){
                setAvatarUploading(true);
                try {
                  const fd = new FormData();
                  fd.append("file", blob, "avatar.jpg");
                  const token = getToken() || "";
                  const res = await fetch(API_BASE + "/api/auth/avatar", {
                    method: "POST",
                    headers: { Authorization: "Bearer " + token },
                    body: fd,
                  });
                  if (!res.ok) { const err = await res.json().catch(() => ({})); throw new Error(err.detail || "Upload failed"); }
                  const data = await res.json();
                  setAvatarUrl(data.avatarUrl);
                  setUser(u => ({ ...u, avatarUrl: data.avatarUrl }));
                  setBioMsg("Profile photo updated!");
                  setTimeout(() => setBioMsg(""), 2500);
                } catch(err) { setBioMsg("Error: " + err.message); setTimeout(() => setBioMsg(""), 3000); }
                setAvatarUploading(false);
                setAvatarCropFile(null);
              }}
            />
          )}

          {/* ─────────────────── CARD 1 — PHOTOS ─────────────────── */}
          <div style={{
            background: "linear-gradient(165deg,#0f0a1f 0%,#0a0a14 60%,#080812 100%)",
            border: "1px solid rgba(124,58,237,0.18)",
            borderRadius: 16, overflow: "hidden",
            marginBottom: 14,
            boxShadow: "0 4px 14px rgba(0,0,0,0.45)",
            position: "relative",
          }}>
            <div style={{
              height: 2,
              background: "linear-gradient(90deg,transparent,#C026D3,#7C3AED,#3B82F6,transparent)",
              boxShadow: "0 0 8px rgba(124,58,237,0.7)",
            }} />
            <div style={{ padding: "12px 16px 10px",
              borderBottom: "1px solid rgba(255,255,255,0.04)",
              display: "flex", alignItems: "center", gap: 8 }}>
              <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="#A78BFA" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                <rect x="3" y="3" width="18" height="18" rx="2"/><circle cx="8.5" cy="8.5" r="1.5"/>
                <polyline points="21 15 16 10 5 21"/>
              </svg>
              <div style={{
                color: "#A78BFA", fontSize: 10, fontWeight: 900, letterSpacing: 1.5,
                textShadow: "0 0 6px rgba(124,58,237,0.5)",
              }}>PHOTOS</div>
            </div>
            <div style={{ position: "relative" }}>
              <div style={{ height: 130, overflow: "hidden", background: "#0a0a14", position: "relative" }}>
                {headerUrl ? (
                  <img src={headerUrl} alt="header"
                    style={{ width: "100%", height: "100%", objectFit: "cover", objectPosition: "center", display: "block" }} />
                ) : (
                  <div style={{ width: "100%", height: "100%",
                    display: "flex", flexDirection: "column", alignItems: "center", justifyContent: "center",
                    background: "linear-gradient(135deg,#1a0a2e 0%,#0f0a1f 50%,#0a0a14 100%)", gap: 6 }}>
                    <svg width="28" height="28" viewBox="0 0 24 24" fill="none" stroke="#A78BFA" strokeWidth="1.5" strokeLinecap="round"
                      style={{ filter: "drop-shadow(0 0 8px rgba(124,58,237,0.5))", opacity: 0.6 }}>
                      <rect x="3" y="3" width="18" height="18" rx="2"/><circle cx="8.5" cy="8.5" r="1.5"/>
                      <polyline points="21 15 16 10 5 21"/>
                    </svg>
                    <div style={{ color: "#666", fontSize: 11, fontWeight: 600, letterSpacing: 0.5 }}>NO HEADER PHOTO</div>
                  </div>
                )}
                <button onClick={function() { headerFileRef.current && headerFileRef.current.click(); }}
                  style={{ position: "absolute", bottom: 10, right: 10,
                    background: "rgba(124,58,237,0.85)",
                    border: "1px solid rgba(192,38,211,0.6)", borderRadius: 20, color: "white", fontSize: 11, fontWeight: 700,
                    padding: "7px 14px", cursor: "pointer", display: "flex", alignItems: "center", gap: 6,
                    backdropFilter: "blur(8px)",
                    boxShadow: "0 2px 8px rgba(0,0,0,0.4)",
                    letterSpacing: 0.3,
                  }}>
                  {headerUploading ? (
                    <div style={{
                      width: 12, height: 12,
                      border: "2px solid rgba(255,255,255,0.2)", borderTopColor: "white",
                      borderRadius: "50%", animation: "bf-spin 0.8s linear infinite",
                    }} />
                  ) : (
                    <svg width="12" height="12" viewBox="0 0 24 24" fill="none" stroke="white" strokeWidth="2.5" strokeLinecap="round">
                      <path d="M21 15v4a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2v-4"/>
                      <polyline points="17 8 12 3 7 8"/><line x1="12" y1="3" x2="12" y2="15"/>
                    </svg>
                  )}
                  {headerUploading ? "UPLOADING..." : headerUrl ? "CHANGE" : "ADD HEADER"}
                </button>
                {headerUrl && (
                  <button onClick={function() { setHeaderUrl(""); setUser(u => ({ ...u, headerUrl: "" })); }}
                    style={{ position: "absolute", top: 10, right: 10, background: "rgba(0,0,0,0.7)",
                      border: "1px solid rgba(239,68,68,0.4)", borderRadius: "50%", width: 28, height: 28, color: "#F87171",
                      cursor: "pointer", display: "flex", alignItems: "center", justifyContent: "center",
                      backdropFilter: "blur(8px)" }}>
                    <svg width="12" height="12" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round">
                      <line x1="18" y1="6" x2="6" y2="18"/><line x1="6" y1="6" x2="18" y2="18"/>
                    </svg>
                  </button>
                )}
              </div>

              <div style={{ padding: "0 16px",
                marginTop: -40, marginBottom: 16,
                display: "flex", alignItems: "flex-end", gap: 14 }}>
                <button onClick={() => avatarFileRef.current && avatarFileRef.current.click()}
                  style={{ position: "relative", background: "none", border: "none", cursor: "pointer", padding: 0 }}>
                  {avatarUrl ? (
                    <img src={avatarUrl} alt="avatar"
                      style={{ width: 86, height: 86, borderRadius: "50%", objectFit: "cover",
                        border: "3px solid #0a0a14",
                        boxShadow: "0 0 0 2px rgba(124,58,237,0.5), 0 4px 14px rgba(0,0,0,0.6)",
                        display: "block" }} />
                  ) : (
                    <div style={{ width: 86, height: 86, borderRadius: "50%",
                      background: "linear-gradient(135deg,#6B21A8,#C026D3)",
                      display: "flex", alignItems: "center", justifyContent: "center",
                      fontSize: 34, color: "white", fontWeight: 800,
                      border: "3px solid #0a0a14",
                      boxShadow: "0 0 0 2px rgba(124,58,237,0.5), 0 4px 14px rgba(0,0,0,0.6)" }}>
                      {(user.username || user.name || "?")[0].toUpperCase()}
                    </div>
                  )}
                  <div style={{ position: "absolute", bottom: 0, right: 0, width: 28, height: 28,
                    borderRadius: "50%",
                    background: "linear-gradient(135deg,#C026D3,#7C3AED,#3B82F6)",
                    border: "2px solid #0a0a14",
                    boxShadow: "0 0 10px rgba(192,38,211,0.6)",
                    display: "flex", alignItems: "center", justifyContent: "center" }}>
                    {avatarUploading
                      ? <div style={{ width: 12, height: 12,
                          border: "2px solid rgba(255,255,255,0.25)", borderTopColor: "white",
                          borderRadius: "50%", animation: "bf-spin 0.8s linear infinite" }} />
                      : <svg width="13" height="13" viewBox="0 0 24 24" fill="none" stroke="white" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round">
                          <path d="M21 15v4a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2v-4"/>
                          <polyline points="17 8 12 3 7 8"/><line x1="12" y1="3" x2="12" y2="15"/>
                        </svg>}
                  </div>
                </button>
                <div style={{ paddingBottom: 6, flex: 1, minWidth: 0 }}>
                  <div style={{
                    color: avatarUploading ? "#A78BFA" : "white",
                    fontSize: 14, fontWeight: 700, letterSpacing: 0.3,
                    overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap",
                  }}>
                    {avatarUploading ? "Uploading..." : (user.username || user.name || "Your profile")}
                  </div>
                  {(user.isPro || user.isArtistPro) && (
                    <div style={{ marginTop: 6 }}>
                      {user.isPro && (
                        <span style={{ display:"inline-flex", alignItems:"center", gap:4,
                          background:"rgba(192,38,211,0.12)", border:"1px solid rgba(192,38,211,0.4)",
                          borderRadius:20, padding:"3px 10px", color:"#C026D3", fontWeight:800, fontSize:10, letterSpacing: 0.3 }}>
                          <VerifiedBadge size={11} /> PRODUCER PRO
                        </span>
                      )}
                      {user.isArtistPro && !user.isPro && (
                        <span style={{ display:"inline-flex", alignItems:"center", gap:4,
                          background:"rgba(245,158,11,0.12)", border:"1px solid rgba(245,158,11,0.4)",
                          borderRadius:20, padding:"3px 10px", color:"#F59E0B", fontWeight:800, fontSize:10, letterSpacing: 0.3 }}>
                          <VerifiedBadge size={11}/> ARTIST PRO
                        </span>
                      )}
                    </div>
                  )}
                </div>
              </div>
            </div>
          </div>

          {/* ─────────────────── CARD 2 — IDENTITY ─────────────────── */}
          <div style={{
            background: "linear-gradient(165deg,#0f0a1f 0%,#0a0a14 60%,#080812 100%)",
            border: "1px solid rgba(124,58,237,0.18)",
            borderRadius: 16, overflow: "hidden",
            marginBottom: 14,
            boxShadow: "0 4px 14px rgba(0,0,0,0.45)",
          }}>
            <div style={{ height: 2,
              background: "linear-gradient(90deg,transparent,#C026D3,#7C3AED,#3B82F6,transparent)",
              boxShadow: "0 0 8px rgba(124,58,237,0.7)" }} />
            <div style={{ padding: "12px 16px 10px",
              borderBottom: "1px solid rgba(255,255,255,0.04)",
              display: "flex", alignItems: "center", gap: 8 }}>
              <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="#A78BFA" strokeWidth="2" strokeLinecap="round">
                <path d="M20 21v-2a4 4 0 00-4-4H8a4 4 0 00-4 4v2"/>
                <circle cx="12" cy="7" r="4"/>
              </svg>
              <div style={{
                color: "#A78BFA", fontSize: 10, fontWeight: 900, letterSpacing: 1.5,
                textShadow: "0 0 6px rgba(124,58,237,0.5)",
              }}>IDENTITY</div>
            </div>
            <div style={{ padding: "16px" }}>
              <div style={{ marginBottom: 14 }}>
                <div style={{ color: "#888", fontSize: 10, fontWeight: 700, letterSpacing: 1, marginBottom: 6 }}>NAME</div>
                <input value={editName} onChange={e => setEditName(e.target.value)}
                  placeholder="Your name"
                  style={{ width: "100%",
                    background: "rgba(0,0,0,0.4)",
                    border: "1px solid rgba(124,58,237,0.2)", borderRadius: 10,
                    padding: "12px 14px", color: "white", fontSize: 14, outline: "none",
                    boxSizing: "border-box",
                    transition: "border 0.15s, box-shadow 0.15s",
                  }}
                  onFocus={e => { e.target.style.border = "1px solid rgba(124,58,237,0.6)"; e.target.style.boxShadow = "0 0 0 3px rgba(124,58,237,0.1)"; }}
                  onBlur={e  => { e.target.style.border = "1px solid rgba(124,58,237,0.2)"; e.target.style.boxShadow = "none"; }}
                />
              </div>

              <div style={{ marginBottom: 14 }}>
                <div style={{ color: "#888", fontSize: 10, fontWeight: 700, letterSpacing: 1, marginBottom: 6 }}>USERNAME</div>
                <div style={{ position: "relative" }}>
                  <span style={{
                    position: "absolute", left: 14, top: "50%", transform: "translateY(-50%)",
                    color: "#666", fontSize: 14, fontWeight: 700, pointerEvents: "none",
                  }}>@</span>
                  <input value={newUsername} onChange={e => setNewUsername(e.target.value)}
                    placeholder={user.username || "yourhandle"}
                    style={{ width: "100%",
                      background: "rgba(0,0,0,0.4)",
                      border: "1px solid rgba(124,58,237,0.2)", borderRadius: 10,
                      padding: "12px 14px 12px 30px", color: "white", fontSize: 14, outline: "none",
                      boxSizing: "border-box",
                      transition: "border 0.15s, box-shadow 0.15s",
                    }}
                    onFocus={e => { e.target.style.border = "1px solid rgba(124,58,237,0.6)"; e.target.style.boxShadow = "0 0 0 3px rgba(124,58,237,0.1)"; }}
                    onBlur={e  => { e.target.style.border = "1px solid rgba(124,58,237,0.2)"; e.target.style.boxShadow = "none"; }}
                  />
                </div>
              </div>

              <div>
                <div style={{ color: "#888", fontSize: 10, fontWeight: 700, letterSpacing: 1, marginBottom: 6 }}>LOCATION</div>
                <div style={{ position: "relative" }}>
                  <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="#666" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"
                    style={{ position: "absolute", left: 12, top: "50%", transform: "translateY(-50%)", pointerEvents: "none" }}>
                    <path d="M21 10c0 7-9 13-9 13s-9-6-9-13a9 9 0 0118 0z"/>
                    <circle cx="12" cy="10" r="3"/>
                  </svg>
                  <input value={location} onChange={e => setLocation(e.target.value)}
                    placeholder="City, Country"
                    style={{ width: "100%",
                      background: "rgba(0,0,0,0.4)",
                      border: "1px solid rgba(124,58,237,0.2)", borderRadius: 10,
                      padding: "12px 14px 12px 34px", color: "white", fontSize: 14, outline: "none",
                      boxSizing: "border-box",
                      transition: "border 0.15s, box-shadow 0.15s",
                    }}
                    onFocus={e => { e.target.style.border = "1px solid rgba(124,58,237,0.6)"; e.target.style.boxShadow = "0 0 0 3px rgba(124,58,237,0.1)"; }}
                    onBlur={e  => { e.target.style.border = "1px solid rgba(124,58,237,0.2)"; e.target.style.boxShadow = "none"; }}
                  />
                </div>
              </div>
            </div>
          </div>

          {/* ─────────────────── CARD 3 — ABOUT ─────────────────── */}
          <div style={{
            background: "linear-gradient(165deg,#0f0a1f 0%,#0a0a14 60%,#080812 100%)",
            border: "1px solid rgba(124,58,237,0.18)",
            borderRadius: 16, overflow: "hidden",
            marginBottom: 14,
            boxShadow: "0 4px 14px rgba(0,0,0,0.45)",
          }}>
            <div style={{ height: 2,
              background: "linear-gradient(90deg,transparent,#C026D3,#7C3AED,#3B82F6,transparent)",
              boxShadow: "0 0 8px rgba(124,58,237,0.7)" }} />
            <div style={{ padding: "12px 16px 10px",
              borderBottom: "1px solid rgba(255,255,255,0.04)",
              display: "flex", alignItems: "center", justifyContent: "space-between" }}>
              <div style={{ display: "flex", alignItems: "center", gap: 8 }}>
                <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="#A78BFA" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                  <path d="M14 2H6a2 2 0 0 0-2 2v16a2 2 0 0 0 2 2h12a2 2 0 0 0 2-2V8z"/>
                  <polyline points="14 2 14 8 20 8"/><line x1="16" y1="13" x2="8" y2="13"/>
                  <line x1="16" y1="17" x2="8" y2="17"/><polyline points="10 9 9 9 8 9"/>
                </svg>
                <div style={{
                  color: "#A78BFA", fontSize: 10, fontWeight: 900, letterSpacing: 1.5,
                  textShadow: "0 0 6px rgba(124,58,237,0.5)",
                }}>ABOUT</div>
              </div>
              <div style={{
                color: bio.length > 240 ? "#F87171" : "#666",
                fontSize: 10, fontWeight: 700, fontVariantNumeric: "tabular-nums",
              }}>{bio.length}/250</div>
            </div>
            <div style={{ padding: 16 }}>
              <textarea value={bio} onChange={e => setBio(e.target.value)}
                placeholder="Tell the world about yourself..."
                rows={5}
                style={{ width: "100%",
                  background: "rgba(0,0,0,0.4)",
                  border: "1px solid rgba(124,58,237,0.2)", borderRadius: 10,
                  padding: "12px 14px", color: "white", fontSize: 14, outline: "none",
                  resize: "none", boxSizing: "border-box",
                  lineHeight: 1.6, fontFamily: "inherit",
                  transition: "border 0.15s, box-shadow 0.15s",
                }}
                onFocus={e => { e.target.style.border = "1px solid rgba(124,58,237,0.6)"; e.target.style.boxShadow = "0 0 0 3px rgba(124,58,237,0.1)"; }}
                onBlur={e  => { e.target.style.border = "1px solid rgba(124,58,237,0.2)"; e.target.style.boxShadow = "none"; }}
              />
            </div>
          </div>

          {/* ─────────────────── CARD 4 — SOCIAL LINKS ─────────────────── */}
          <div style={{
            background: "linear-gradient(165deg,#0f0a1f 0%,#0a0a14 60%,#080812 100%)",
            border: "1px solid rgba(124,58,237,0.18)",
            borderRadius: 16, overflow: "hidden",
            marginBottom: 16,
            boxShadow: "0 4px 14px rgba(0,0,0,0.45)",
          }}>
            <div style={{ height: 2,
              background: "linear-gradient(90deg,transparent,#C026D3,#7C3AED,#3B82F6,transparent)",
              boxShadow: "0 0 8px rgba(124,58,237,0.7)" }} />
            <div style={{ padding: "12px 16px 10px",
              borderBottom: "1px solid rgba(255,255,255,0.04)",
              display: "flex", alignItems: "center", gap: 8 }}>
              <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="#A78BFA" strokeWidth="2" strokeLinecap="round">
                <path d="M10 13a5 5 0 007.54.54l3-3a5 5 0 00-7.07-7.07l-1.72 1.71"/>
                <path d="M14 11a5 5 0 00-7.54-.54l-3 3a5 5 0 007.07 7.07l1.71-1.71"/>
              </svg>
              <div style={{
                color: "#A78BFA", fontSize: 10, fontWeight: 900, letterSpacing: 1.5,
                textShadow: "0 0 6px rgba(124,58,237,0.5)",
              }}>SOCIAL LINKS</div>
            </div>
            <div>
              {[
                { key: "instagram", label: "Instagram", val: instagram, set: setInstagram, placeholder: "instagram.com/yourhandle",
                  icon: (<svg width="22" height="22" viewBox="0 0 24 24" fill="none"><defs><linearGradient id="igs2" x1="0%" y1="100%" x2="100%" y2="0%"><stop offset="0%" stopColor="#f09433"/><stop offset="50%" stopColor="#dc2743"/><stop offset="100%" stopColor="#bc1888"/></linearGradient></defs><rect x="2" y="2" width="20" height="20" rx="5" fill="url(#igs2)"/><circle cx="12" cy="12" r="4.5" stroke="white" strokeWidth="1.5" fill="none"/><circle cx="17.5" cy="6.5" r="1.2" fill="white"/></svg>) },
                { key: "tiktok", label: "TikTok", val: tiktok, set: setTiktok, placeholder: "tiktok.com/@yourhandle",
                  icon: (<svg width="22" height="22" viewBox="0 0 24 24" fill="white"><path d="M19.59 6.69a4.83 4.83 0 0 1-3.77-4.25V2h-3.45v13.67a2.89 2.89 0 0 1-2.88 2.5 2.89 2.89 0 0 1-2.89-2.89 2.89 2.89 0 0 1 2.89-2.89c.28 0 .54.04.79.1V9.01a6.34 6.34 0 0 0-6.13 6.33 6.34 6.34 0 0 0 6.34 6.34 6.34 6.34 0 0 0 6.33-6.34V8.93a8.16 8.16 0 0 0 4.77 1.52V7.01a4.85 4.85 0 0 1-1-.32z"/></svg>) },
                { key: "spotify", label: "Spotify", val: spotify, set: setSpotify, placeholder: "open.spotify.com/artist/...",
                  icon: (<svg width="22" height="22" viewBox="0 0 24 24" fill="none"><circle cx="12" cy="12" r="10" fill="#1DB954"/><path d="M8 15.5c2.5-1 5.5-1 8 0" stroke="white" strokeWidth="1.5" strokeLinecap="round" fill="none"/><path d="M7 12.5c3-1.2 7-1.2 10 0" stroke="white" strokeWidth="1.5" strokeLinecap="round" fill="none"/><path d="M6.5 9.5c3.5-1.3 8-1.3 11 0" stroke="white" strokeWidth="1.5" strokeLinecap="round" fill="none"/></svg>) },
                { key: "appleMusic", label: "Apple Music", val: appleMusic, set: setAppleMusic, placeholder: "music.apple.com/artist/...",
                  icon: (<svg width="22" height="22" viewBox="0 0 24 24" fill="none"><rect x="2" y="2" width="20" height="20" rx="5" fill="#fc3c44"/><path d="M16 7v6.5a2.5 2.5 0 1 1-1.5-2.3V8.5L10 9.5V15a2.5 2.5 0 1 1-1.5-2.3V8l7.5-1z" fill="white"/></svg>) },
                { key: "youtube", label: "YouTube", val: youtube, set: setYoutube, placeholder: "youtube.com/@yourchannel",
                  icon: (<svg width="22" height="22" viewBox="0 0 24 24" fill="none"><rect x="2" y="5" width="20" height="14" rx="4" fill="#FF0000"/><polygon points="10,8.5 16,12 10,15.5" fill="white"/></svg>) },
                { key: "website", label: "Website", val: website, set: setWebsite, placeholder: "yourwebsite.com",
                  icon: (<svg width="22" height="22" viewBox="0 0 24 24" fill="none" stroke="white" strokeWidth="1.8" strokeLinecap="round"><circle cx="12" cy="12" r="10"/><path d="M2 12h20M12 2a15.3 15.3 0 0 1 4 10 15.3 15.3 0 0 1-4 10 15.3 15.3 0 0 1-4-10 15.3 15.3 0 0 1 4-10z"/></svg>) },
              ].map(function(s, i, arr) {
                const hasValue = !!s.val;
                return (
                  <div key={s.key} style={{ borderBottom: i < arr.length - 1 ? "1px solid rgba(255,255,255,0.04)" : "none" }}>
                    <div style={{ display: "flex", alignItems: "center", gap: 12, padding: "12px 16px" }}>
                      <div style={{ width: 38, height: 38, borderRadius: 10,
                        background: "rgba(0,0,0,0.4)",
                        border: "1px solid " + (hasValue ? "rgba(124,58,237,0.35)" : "rgba(124,58,237,0.12)"),
                        display: "flex", alignItems: "center", justifyContent: "center", flexShrink: 0,
                        boxShadow: hasValue ? "0 0 8px rgba(124,58,237,0.2)" : "none",
                      }}>
                        {s.icon}
                      </div>
                      <div style={{ flex: 1, minWidth: 0 }}>
                        <div style={{ color: "white", fontWeight: 700, fontSize: 13, marginBottom: 2 }}>{s.label}</div>
                        <input value={s.val} onChange={function(e) { s.set(e.target.value); }}
                          placeholder={s.placeholder}
                          style={{ background: "none", border: "none",
                            color: hasValue ? "#A78BFA" : "#555",
                            fontSize: 12, outline: "none", width: "100%", padding: 0,
                            fontWeight: hasValue ? 600 : 400,
                          }} />
                      </div>
                      {hasValue && (
                        <button onClick={function() { openExternalLink(s.val); }}
                          style={{ background: "rgba(124,58,237,0.1)",
                            border: "1px solid rgba(124,58,237,0.25)",
                            borderRadius: "50%", width: 28, height: 28,
                            cursor: "pointer", flexShrink: 0,
                            display: "flex", alignItems: "center", justifyContent: "center",
                            color: "#A78BFA",
                          }}>
                          <svg width="12" height="12" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round">
                            <line x1="7" y1="17" x2="17" y2="7"/><polyline points="7 7 17 7 17 17"/>
                          </svg>
                        </button>
                      )}
                    </div>
                  </div>
                );
              })}
            </div>
          </div>

          {/* SAVE BUTTON */}
          <button
            disabled={profileSaving}
            onClick={async () => {
              setProfileSaving(true);
              try {
                await Promise.all([
                  apiFetch("/api/auth/profile/update", { method: "POST", body: JSON.stringify({
                    name: editName.trim(), location: location.trim(),
                    instagram: instagram.trim(), tiktok: tiktok.trim(),
                    youtube: youtube.trim(), spotify: spotify.trim(), website: website.trim(),
                    bio: bio.trim(), avatarColor: avatarColor, avatarUrl: avatarUrl, appleMusic: appleMusic.trim(), headerUrl: headerUrl,
                  })}),
                  newUsername.trim() && newUsername.trim() !== user.username
                    ? apiFetch("/api/auth/set-username", { method: "POST", body: JSON.stringify({ username: newUsername.trim() }) })
                    : Promise.resolve(),
                ]);
                if (editName.trim()) setUser(u => ({ ...u, name: editName.trim() }));
                if (newUsername.trim()) setUser(u => ({ ...u, username: newUsername.trim() }));
                setUser(u => ({ ...u, bio: bio.trim(), location: location.trim(), instagram: instagram.trim(), tiktok: tiktok.trim(), youtube: youtube.trim(), spotify: spotify.trim(), website: website.trim(), appleMusic: appleMusic.trim(), avatarColor, avatarUrl, headerUrl }));
                setBioMsg("Profile saved!"); setTimeout(() => setBioMsg(""), 3000);
              } catch(e) { setBioMsg("Error: " + e.message); setTimeout(() => setBioMsg(""), 3000); }
              setProfileSaving(false);
            }}
            style={{
              width: "100%",
              background: profileSaving
                ? "rgba(255,255,255,0.05)"
                : "linear-gradient(135deg,#C026D3 0%,#A855F7 50%,#7C3AED 100%)",
              border: profileSaving ? "1px solid #333" : "none",
              borderRadius: 14, color: profileSaving ? "#666" : "white",
              fontWeight: 900, fontSize: 14, letterSpacing: 1,
              padding: "16px", cursor: profileSaving ? "not-allowed" : "pointer",
              marginBottom: 10,
              boxShadow: profileSaving ? "none" : "0 4px 18px rgba(192,38,211,0.4), inset 0 1px 0 rgba(255,255,255,0.2)",
              textShadow: profileSaving ? "none" : "0 1px 2px rgba(0,0,0,0.3)",
              display: "flex", alignItems: "center", justifyContent: "center", gap: 8,
            }}>
            {profileSaving ? (
              <>
                <div style={{ width: 14, height: 14,
                  border: "2px solid rgba(255,255,255,0.2)", borderTopColor: "#A78BFA",
                  borderRadius: "50%", animation: "bf-spin 0.8s linear infinite" }} />
                SAVING…
              </>
            ) : (
              <>
                <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="white" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round">
                  <path d="M19 21H5a2 2 0 01-2-2V5a2 2 0 012-2h11l5 5v11a2 2 0 01-2 2z"/>
                  <polyline points="17 21 17 13 7 13 7 21"/>
                  <polyline points="7 3 7 8 15 8"/>
                </svg>
                SAVE PROFILE
              </>
            )}
          </button>

          {bioMsg && (
            <div style={{
              color: bioMsg.startsWith("Error") ? "#F87171" : "#22C55E",
              fontSize: 12, fontWeight: 700, textAlign: "center",
              marginBottom: 14, padding: "8px 14px",
              background: bioMsg.startsWith("Error") ? "rgba(239,68,68,0.08)" : "rgba(34,197,94,0.08)",
              border: "1px solid " + (bioMsg.startsWith("Error") ? "rgba(239,68,68,0.25)" : "rgba(34,197,94,0.25)"),
              borderRadius: 10,
            }}>{bioMsg}</div>
          )}

          {/* SHARE LINK */}
          {user.username && (
            <div style={{
              background: "linear-gradient(165deg,#0f0a1f 0%,#0a0a14 60%,#080812 100%)",
              border: "1px solid rgba(124,58,237,0.18)",
              borderRadius: 12, padding: "12px 14px",
              display: "flex", alignItems: "center", gap: 10, marginBottom: 24,
              boxShadow: "0 4px 14px rgba(0,0,0,0.45)",
            }}>
              <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="#A78BFA" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" style={{ flexShrink: 0 }}>
                <path d="M10 13a5 5 0 007.54.54l3-3a5 5 0 00-7.07-7.07l-1.72 1.71"/>
                <path d="M14 11a5 5 0 00-7.54-.54l-3 3a5 5 0 007.07 7.07l1.71-1.71"/>
              </svg>
              <div style={{ flex: 1, color: "#888", fontSize: 12, fontFamily: "monospace",
                overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" }}>
                beatfinder.co.uk/profile/{user.username}
              </div>
              <button onClick={() => {
                navigator.clipboard?.writeText("https://beatfinder.co.uk/profile/" + user.username)
                  .then(() => { setBioMsg("Link copied!"); setTimeout(() => setBioMsg(""), 2000); });
              }} style={{
                background: "linear-gradient(135deg,#C026D3,#7C3AED)",
                border: "none", color: "white", fontSize: 11, fontWeight: 800, letterSpacing: 0.5,
                cursor: "pointer", flexShrink: 0, padding: "6px 12px",
                borderRadius: 16,
                boxShadow: "0 2px 8px rgba(192,38,211,0.35)",
              }}>
                COPY
              </button>
            </div>
          )}



        </div>
      )}

      {/* View another user's profile */}
      {activeSection && activeSection.startsWith("viewProfile:") && (
        <div>
          <SectionBack onBack={() => setActiveSection(null)} label="Back to Profile" />
          <PublicProfileScreen
            username={activeSection.replace("viewProfile:", "")}
            onBack={() => setActiveSection(null)}
            onPlay={onPlayBeat}
            savedIds={new Set()}
            onSave={() => {}}
            currentUser={user}
            onMessage={u => { setActiveSection(null); onOpenMessages && onOpenMessages(u); }}
          />
        </div>
      )}

      

      

      {activeSection === "lyrics" && (
        <div>
          <SectionBack onBack={() => setActiveSection(null)} label="Back to Dashboard" />
          <div style={{ color: "white", fontWeight: 800, fontSize: 18, marginBottom: 6 }}>My Lyrics</div>
          <div style={{ color: "#666", fontSize: 13, marginBottom: 16 }}>Tap any lyric to continue writing</div>

          {/* + New Blank Lyric — opens LyricsNotepad with no beat, for pure writing */}
          {onNewLyric && (
            <button onClick={function() { onNewLyric(); }}
              style={{
                width: "100%", marginBottom: 16,
                background: "linear-gradient(135deg,#C026D3 0%,#A855F7 50%,#7C3AED 100%)",
                border: "none", borderRadius: 12,
                color: "white", fontWeight: 900, fontSize: 13, letterSpacing: 0.6,
                padding: "13px", cursor: "pointer",
                display: "flex", alignItems: "center", justifyContent: "center", gap: 8,
                boxShadow: "0 4px 16px rgba(192,38,211,0.35), inset 0 1px 0 rgba(255,255,255,0.18)",
                textShadow: "0 1px 2px rgba(0,0,0,0.3)",
              }}>
              <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="white" strokeWidth="2.5" strokeLinecap="round">
                <line x1="12" y1="5" x2="12" y2="19"/><line x1="5" y1="12" x2="19" y2="12"/>
              </svg>
              NEW BLANK LYRIC
            </button>
          )}

          {savedLyrics.length === 0 ? (
            <div style={{ textAlign: "center", padding: "40px 0", color: "#555" }}>
            <div style={{ marginBottom: 10 }}><Icon id="edit" size={40} color="#555" strokeWidth={1.5} /></div>
              <div>No saved lyrics yet. Tap above to start writing, or open a beat and tap Write Lyrics.</div>
            </div>
          ) : savedLyrics.map(function(lyric, i) {
            return (
              <div key={i} style={{ background: "#111", borderRadius: 14, padding: 16, marginBottom: 12, border: "1px solid #1e1e1e" }}>
                <div style={{ display: "flex", justifyContent: "space-between", alignItems: "flex-start", marginBottom: 6 }}>
                  <div style={{ flex: 1 }}>
                    <div style={{ color: "white", fontWeight: 700, fontSize: 15 }}>{lyric.title || "Untitled"}</div>
                    <div style={{ color: "#555", fontSize: 12, marginTop: 2 }}>{lyric.beatTitle}</div>
                  </div>
                  <button onClick={function(){ 
                    var lyricId = lyric.id;
                    setSavedLyrics(function(prev){ 
                      var next = prev.filter(function(_, j){ return j !== i; }); 
                      try { localStorage.setItem("bf_lyrics", JSON.stringify(next)); } catch(e){} 
                      return next; 
                    });
                    if (user) {
                      apiFetch("/api/lyrics/" + lyricId, { method: "DELETE" }).catch(() => {});
                    }
                  }}
                    style={{ background: "rgba(220,38,38,0.1)", border: "1px solid rgba(220,38,38,0.3)", borderRadius: 8, color: "#F87171", fontSize: 12, padding: "4px 10px", cursor: "pointer" }}>
                    Delete
                  </button>
                </div>
                <button onClick={function(){ onEditLyric(lyric, i); }}
                  style={{ width: "100%", background: "rgba(192,38,211,0.1)", border: "1.5px solid #C026D3", borderRadius: 10, color: "#C026D3", fontWeight: 700, fontSize: 14, padding: "10px", cursor: "pointer", marginTop: 6, display: "flex", alignItems: "center", justifyContent: "center", gap: 8 }}>
                  <Icon id="edit" size={16} color="#C026D3" strokeWidth={2} /> Continue Writing
                </button>
              </div>
            );
          })}
        </div>
      )}

      {activeSection === "upload" && (
        <div>
          <SectionBack onBack={() => setActiveSection(null)} label="Back to Dashboard" />
          <div style={{ color: "white", fontWeight: 800, fontSize: 18, marginBottom: 6 }}>Upload Your Beat</div>
          <div style={{ color: "#666", fontSize: 13, marginBottom: 14 }}>Upload MP3 files to sell leases to artists.</div>
          <div style={{ background: "#111", borderRadius: 14, padding: 16, border: "1px solid #222" }}>
            <input value={ytLink} onChange={e => setYtLink(e.target.value)} placeholder="Beat title e.g. Dark Trap Beat" style={inp} />
            <input value={uploadGenre || ""} onChange={e => setUploadGenre(e.target.value)} placeholder="Genre e.g. Trap, R&B, Afrobeats" style={inp} />

            {/* PRICING — free toggle OR two-tier lease */}
            <div style={{ color: "#666", fontSize: 11, fontWeight: 700, marginBottom: 6, letterSpacing: 0.5 }}>PRICING</div>
            <div style={{ display: "flex", gap: 8, marginBottom: 10 }}>
              <button type="button" onClick={function() { setUploadPrice("free"); setUploadPremiumPrice(""); }}
                style={{
                  flex: 1, padding: "10px", borderRadius: 10,
                  border: (uploadPrice === "free" ? "2px solid #C026D3" : "1px solid #333"),
                  background: (uploadPrice === "free" ? "rgba(192,38,211,0.15)" : "transparent"),
                  color: (uploadPrice === "free" ? "#C026D3" : "#888"),
                  fontWeight: 800, fontSize: 12, cursor: "pointer",
                }}>
                FREE Beat
              </button>
              <button type="button" onClick={function() { if (uploadPrice === "free" || !uploadPrice) setUploadPrice("£50"); }}
                style={{
                  flex: 1, padding: "10px", borderRadius: 10,
                  border: (uploadPrice && uploadPrice !== "free" ? "2px solid #F59E0B" : "1px solid #333"),
                  background: (uploadPrice && uploadPrice !== "free" ? "rgba(245,158,11,0.15)" : "transparent"),
                  color: (uploadPrice && uploadPrice !== "free" ? "#F59E0B" : "#888"),
                  fontWeight: 800, fontSize: 12, cursor: "pointer",
                }}>
                PAID Lease
              </button>
            </div>

            {uploadPrice && uploadPrice !== "free" && (
              <div style={{ background: "#0a0a0a", border: "1px solid #1f1f1f", borderRadius: 10, padding: 12, marginBottom: 12 }}>
                <div style={{ color: "#aaa", fontSize: 11, fontWeight: 700, marginBottom: 8, letterSpacing: 0.5 }}>
                  TWO LEASE TIERS — buyers pick one
                </div>
                {/* Basic — fixed £50 */}
                <div style={{ background: "#0d0d0d", border: "1px solid #222", borderRadius: 8, padding: 10, marginBottom: 8 }}>
                  <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center" }}>
                    <div style={{ color: "#F59E0B", fontWeight: 800, fontSize: 12 }}>Basic Lease</div>
                    <div style={{ color: "#F59E0B", fontWeight: 800, fontSize: 14 }}>£50</div>
                  </div>
                  <div style={{ color: "#666", fontSize: 10, marginTop: 4, lineHeight: 1.4 }}>
                    Non-exclusive · 75% royalties to you · 100k stream cap · Buyer can resell finished record
                  </div>
                </div>
                {/* Premium — producer-set £100-£500 */}
                <div style={{ background: "#0d0d0d", border: "1px solid #222", borderRadius: 8, padding: 10 }}>
                  <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", marginBottom: 6 }}>
                    <div style={{ color: "#A855F7", fontWeight: 800, fontSize: 12 }}>Premium Lease (Exclusive)</div>
                  </div>
                  <div style={{ display: "flex", alignItems: "center", gap: 6 }}>
                    <span style={{ color: "#A855F7", fontSize: 14, fontWeight: 800 }}>£</span>
                    <input
                      type="number"
                      min={100} max={500}
                      value={uploadPremiumPrice}
                      onChange={e => setUploadPremiumPrice(e.target.value)}
                      placeholder="100 – 500"
                      style={{
                        flex: 1, background: "#000", border: "1px solid #333",
                        borderRadius: 6, padding: "6px 10px", color: "white",
                        fontSize: 13, outline: "none",
                      }}
                    />
                  </div>
                  <div style={{ color: "#666", fontSize: 10, marginTop: 6, lineHeight: 1.4 }}>
                    EXCLUSIVE · 50% royalties to you · Unlimited streams · Beat removed from sale once purchased · No resale by buyer
                  </div>
                </div>
              </div>
            )}

            {/* BPM + Key row */}
            <div style={{ display: "flex", gap: 8, marginBottom: 12 }}>
              <div style={{ flex: 1 }}>
                <div style={{ color: "#666", fontSize: 11, fontWeight: 700, marginBottom: 4, letterSpacing: 0.5 }}>BPM</div>
                <input
                  type="number" min={40} max={300}
                  value={uploadBpm}
                  onChange={e => setUploadBpm(e.target.value)}
                  placeholder="e.g. 140"
                  style={{ ...inp, marginBottom: 0 }}
                />
              </div>
              <div style={{ flex: 1 }}>
                <div style={{ color: "#666", fontSize: 11, fontWeight: 700, marginBottom: 4, letterSpacing: 0.5 }}>KEY</div>
                <select
                  value={uploadKey}
                  onChange={e => setUploadKey(e.target.value)}
                  style={{ ...inp, marginBottom: 0, color: uploadKey ? "white" : "#555" }}
                >
                  <option value="">Select key</option>
                  {["C Major","C Minor","C# Major","C# Minor","D Major","D Minor","D# Major","D# Minor","E Major","E Minor","F Major","F Minor","F# Major","F# Minor","G Major","G Minor","G# Major","G# Minor","A Major","A Minor","A# Major","A# Minor","B Major","B Minor"].map(k => (
                    <option key={k} value={k}>{k}</option>
                  ))}
                </select>
              </div>
            </div>

            {/* Description */}
            <div style={{ color: "#666", fontSize: 11, fontWeight: 700, marginBottom: 4, letterSpacing: 0.5 }}>DESCRIPTION</div>
            <textarea
              value={uploadDesc}
              onChange={e => setUploadDesc(e.target.value)}
              placeholder="Tell listeners about this beat — mood, inspiration, usage rights..."
              maxLength={500}
              rows={3}
              style={{ ...inp, resize: "none", fontFamily: "inherit", marginBottom: 4 }}
            />
            <div style={{ color: "#444", fontSize: 10, textAlign: "right", marginBottom: 12 }}>{uploadDesc.length}/500</div>

            {/* Preview Start */}
            <div style={{ marginBottom: 12 }}>
              <div style={{ color: "#666", fontSize: 11, fontWeight: 700, marginBottom: 4, letterSpacing: 0.5 }}>
                PREVIEW START — <span style={{ color: "#C026D3" }}>{uploadPreviewStart}s</span>
                <span style={{ color: "#444", fontWeight: 400, marginLeft: 6 }}>45s preview plays from this point</span>
              </div>
              <input
                type="range" min={0} max={180} step={5}
                value={uploadPreviewStart}
                onChange={e => setUploadPreviewStart(parseInt(e.target.value))}
                style={{ width: "100%", accentColor: "#C026D3" }}
              />
              <div style={{ display: "flex", justifyContent: "space-between", color: "#333", fontSize: 10 }}>
                <span>0s (start)</span><span>90s</span><span>180s</span>
              </div>
            </div>
            <input ref={uploadFileRef} type="file" accept=".mp3,.wav,.m4a,.aac,.ogg,.flac,.aiff,.opus" onChange={e => setUploadFile(e.target.files[0])}
              style={{ width: "100%", marginBottom: 12, color: "#aaa", fontSize: 14 }} />
            <button disabled={uploadLoading} onClick={async () => {
              if (!ytLink.trim() || !uploadFile) { setUploadMsg("Please fill all fields and select an MP3"); return; }
              setUploadLoading(true); setUploadMsg("");
              try {
                const fd = new FormData();
                var priceVal = (uploadPrice || "").trim();
                if (priceVal === "0" || priceVal === "£0" || priceVal === "0.00" || priceVal === "£0.00" || priceVal === "") priceVal = "free";
                var isPaid = (priceVal !== "free");
                // For paid beats, validate premium tier price (£100-£500)
                var premiumNum = 0;
                if (isPaid) {
                  premiumNum = parseInt(uploadPremiumPrice, 10);
                  if (!premiumNum || isNaN(premiumNum) || premiumNum < 100 || premiumNum > 500) {
                    setUploadMsg("Premium lease price must be between £100 and £500");
                    setUploadLoading(false);
                    return;
                  }
                }
                fd.append("title",         ytLink.trim());
                fd.append("genre",         uploadGenre || "");
                fd.append("price",         priceVal);
                fd.append("bpm",           uploadBpm ? String(parseInt(uploadBpm)) : "0");
                fd.append("key",           uploadKey || "");
                fd.append("description",   uploadDesc || "");
                fd.append("preview_start", String(uploadPreviewStart || 0));
                // Two-tier prices — only meaningful for paid beats
                fd.append("basic_lease_price",   "50");
                fd.append("premium_lease_price", isPaid ? String(premiumNum) : "0");
                fd.append("file",          uploadFile);
                const token = typeof getToken === "function" ? getToken() : (localStorage.getItem("bf_token") || "");
                const res = await fetch(API_BASE + "/api/producer/upload", { method: "POST", headers: { "Authorization": "Bearer " + token }, body: fd });
                const data = await res.json();
                if (!res.ok) throw new Error(data.detail || "Upload failed");
                setUploads(prev => [data.beat, ...prev]);
                setUploadMsg("Beat uploaded!");
                // Clear all fields
                setYtLink(""); setUploadGenre(""); setUploadPrice(""); setUploadPremiumPrice("");
                setUploadBpm(""); setUploadKey(""); setUploadDesc(""); setUploadPreviewStart(0);
                setUploadFile(null);
                if (uploadFileRef.current) uploadFileRef.current.value = "";
                // Clear success message after 3s, stay on form
                setTimeout(function(){ setUploadMsg(""); }, 3000);
              } catch (e) { setUploadMsg("Error: " + e.message); }
              setUploadLoading(false);
            }} disabled={uploadLoading} style={{
              width: "100%",
              background: uploadLoading
                ? "linear-gradient(135deg, #0a0a0a 0%, #0f0f12 100%)"
                : "linear-gradient(135deg, #A855F7 0%, #6366F1 60%, #3B82F6 100%)",
              border: uploadLoading ? "1px solid rgba(168,85,247,0.4)" : "none",
              borderRadius: 12,
              color: "white",
              fontWeight: 800,
              fontSize: 15,
              padding: uploadLoading ? "12px" : "14px",
              cursor: uploadLoading ? "not-allowed" : "pointer",
              boxShadow: uploadLoading
                ? "0 0 20px rgba(168,85,247,0.35), inset 0 0 0 1px rgba(168,85,247,0.15)"
                : "0 4px 18px rgba(168,85,247,0.4), 0 0 0 1px rgba(255,255,255,0.05)",
              letterSpacing: 0.5,
              transition: "all 0.2s ease",
            }}>
              {uploadLoading ? (
                <div>
                  <div style={{
                    display: "flex", alignItems: "center", justifyContent: "center",
                    gap: 8, marginBottom: 8,
                  }}>
                    <svg width="14" height="14" viewBox="0 0 24 24" fill="none"
                      stroke="#A855F7" strokeWidth="2.5" strokeLinecap="round"
                      style={{ animation: "bf-spin 1s linear infinite", filter: "drop-shadow(0 0 4px rgba(168,85,247,0.6))" }}>
                      <path d="M21 12a9 9 0 1 1-6.219-8.56"/>
                    </svg>
                    <span style={{ color: "#A855F7", fontSize: 12, fontWeight: 800, letterSpacing: 1 }}>UPLOADING BEAT</span>
                  </div>
                  <BFProgressBar />
                </div>
              ) : "Upload Beat"}
            </button>
            {uploadMsg && <div style={{ marginTop: 10, color: uploadMsg.startsWith("Error") ? "#F87171" : "#22C55E", fontSize: 13, textAlign: "center" }}>{uploadMsg}</div>}
          </div>
        </div>
      )}

      {activeSection === "manage" && (
        <div>
          <SectionBack onBack={() => setActiveSection(null)} label="Back to Dashboard" />
          <MyUploadsSection user={user} />
        </div>
      )}

      {activeSection === "purchases" && (
        <div>
          <SectionBack onBack={() => setActiveSection(null)} label="Back to Dashboard" />
          <MyPurchasesSection user={user} />
        </div>
      )}

      {activeSection === "stripe" && (
        <div>
          <SectionBack onBack={() => setActiveSection(null)} label="Back to Dashboard" />
          <div style={{ color: "white", fontWeight: 800, fontSize: 18, marginBottom: 16 }}>Stripe Payouts</div>
          <StripeConnectSection />
        </div>
      )}

      {activeSection === "stats" && (
        <div>
          <SectionBack onBack={() => setActiveSection(null)} label="Back to Dashboard" />
          <div style={{ color: "white", fontWeight: 800, fontSize: 18, marginBottom: 16 }}>Analytics</div>
          {!producerStats ? (
            <BFLoader type="spinner" text="Loading your stats..." />
          ) : (
            <div>
              <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: 10, marginBottom: 16 }}>
                {[
                  { label: "Beats Uploaded",   value: producerStats.totalBeats,     color: "#C026D3" },
                  { label: "Total Downloads",   value: producerStats.totalDownloads, color: "#F59E0B" },
                  { label: "Total Revenue",     value: "£" + producerStats.totalRevenue, color: "#22C55E" },
                  { label: "Stripe",            value: producerStats.stripeConnected ? "Connected" : "Not connected", color: "#818CF8" },
                ].map(stat => (
                  <div key={stat.label} style={{ background: "#111", borderRadius: 14, padding: 16, border: "1px solid #1e1e1e" }}>
                    <div style={{ color: stat.color, fontWeight: 800, fontSize: 20 }}>{stat.value}</div>
                    <div style={{ color: "#555", fontSize: 12, marginTop: 4 }}>{stat.label}</div>
                  </div>
                ))}
              </div>
              <div style={{ background: "#111", borderRadius: 14, padding: 16, border: "1px solid #1e1e1e", marginBottom: 16 }}>
                <div style={{ color: "#888", fontSize: 11, fontWeight: 700, letterSpacing: 1, marginBottom: 8 }}>TOP PERFORMING BEAT</div>
                <div style={{ color: "white", fontWeight: 700, fontSize: 15 }}>{producerStats.topBeat}</div>
              </div>
              {producerStats.recentSales.length > 0 && (
                <div style={{ background: "#111", borderRadius: 14, padding: 16, border: "1px solid #1e1e1e" }}>
                  <div style={{ color: "#888", fontSize: 11, fontWeight: 700, letterSpacing: 1, marginBottom: 12 }}>RECENT SALES</div>
                  {producerStats.recentSales.map((sale, i) => (
                    <div key={i} style={{ display: "flex", justifyContent: "space-between", alignItems: "center", paddingBottom: 10, marginBottom: 10, borderBottom: i < producerStats.recentSales.length - 1 ? "1px solid #1e1e1e" : "none" }}>
                      <div>
                        <div style={{ color: "white", fontSize: 13, fontWeight: 600 }}>{sale.beat_title}</div>
                        <div style={{ color: "#555", fontSize: 11 }}>{new Date(sale.purchased_at).toLocaleDateString()}</div>
                      </div>
                      <div style={{ color: "#22C55E", fontWeight: 800, fontSize: 14 }}>{sale.price}</div>
                    </div>
                  ))}
                </div>
              )}
            </div>
          )}
        </div>
      )}

      {/* ── Post Music (Spotify) ── */}
      {activeSection === "postmusic" && (
        <PostMusicSection user={user} onBack={() => setActiveSection(null)} />
      )}

      {/* ── Post Video ── */}
      {activeSection === "postvideo" && (
        <PostVideoSection user={user} onBack={() => setActiveSection(null)} />
      )}

      {activeSection === "upgrade" && (
        <div>
          <SectionBack onBack={() => setActiveSection(null)} label="Back to Dashboard" />
          <div style={{ color: "white", fontWeight: 800, fontSize: 20, marginBottom: 16 }}>Choose Your Plan</div>
          <PlanPicker onSelectPlan={async function(planId, priceId) {
            try {
              const r = await apiFetch("/api/stripe/create-checkout", { method: "POST", body: JSON.stringify({ price_id: priceId }) });
              try { var ct = sessionStorage.getItem("bf_tab") || "exclusive"; sessionStorage.setItem("bf_return_tab", JSON.stringify({ tab: ct, path: window.location.pathname + window.location.search, ts: Date.now() })); } catch(e) {} window.location.href = r.checkout_url;
            } catch (e) { alert("Error: " + e.message); }
          }} />
        </div>
      )}

      {/* ── Delete Account Confirmation Modal ── */}
      {showDeleteAccountConfirm && (
        <div style={{
          position: "fixed", inset: 0, zIndex: 99999,
          background: "rgba(0,0,0,0.9)",
          display: "flex", alignItems: "center", justifyContent: "center",
          padding: 16,
        }} onClick={function() { if (!deleting) setShowDeleteAccountConfirm(false); }}>
          <div onClick={function(e) { e.stopPropagation(); }} style={{
            width: "100%", maxWidth: 440,
            background: "linear-gradient(165deg,#1a0a0a 0%,#0a0a14 60%,#080812 100%)",
            borderRadius: 22,
            border: "1.5px solid rgba(220,38,38,0.5)",
            boxShadow: "0 16px 60px rgba(0,0,0,0.85), 0 0 40px rgba(220,38,38,0.3)",
            padding: "22px 20px max(22px, env(safe-area-inset-bottom))",
          }}>
            <div style={{ height: 2, marginLeft: -20, marginRight: -20, marginTop: -22, marginBottom: 18,
              background: "linear-gradient(90deg,transparent,#EF4444,transparent)",
              boxShadow: "0 0 12px #EF4444cc",
            }} />
            <div style={{
              color: "#F87171", fontSize: 11, fontWeight: 900, letterSpacing: 2, marginBottom: 6,
              textShadow: "0 0 6px rgba(220,38,38,0.5)",
              display: "inline-flex", alignItems: "center", gap: 6,
            }}>
              <svg width="12" height="12" viewBox="0 0 24 24" fill="none" stroke="#F87171" strokeWidth="2.4" strokeLinecap="round" strokeLinejoin="round" style={{ filter: "drop-shadow(0 0 4px rgba(220,38,38,0.6))" }}>
                <path d="M10.29 3.86 1.82 18a2 2 0 0 0 1.71 3h16.94a2 2 0 0 0 1.71-3L13.71 3.86a2 2 0 0 0-3.42 0z"/>
                <line x1="12" y1="9" x2="12" y2="13"/>
                <line x1="12" y1="17" x2="12.01" y2="17"/>
              </svg>
              PERMANENT ACTION
            </div>
            <div style={{ color: "white", fontFamily: "'Bebas Neue',sans-serif", fontSize: 24, letterSpacing: 1, marginBottom: 10 }}>DELETE YOUR ACCOUNT</div>
            <div style={{ color: "#bbb", fontSize: 13, lineHeight: 1.55, marginBottom: 16 }}>
              This will anonymise your account, remove your uploaded beats from public display, and delete your saved lyrics and free-licence records. Lease contracts (sold or purchased) and tax records are retained for 6 years as required by UK law.
            </div>
            <div style={{ color: "#888", fontSize: 11, marginBottom: 6, fontWeight: 700, letterSpacing: 0.5 }}>
              Type your email address to confirm
            </div>
            <input
              value={deleteConfirmEmail}
              onChange={function(e){ setDeleteConfirmEmail(e.target.value); }}
              placeholder={user.email}
              type="email"
              disabled={deleting}
              style={{
                width: "100%", boxSizing: "border-box",
                background: "#0a0a0a", border: "1px solid #333",
                borderRadius: 10, padding: "10px 14px",
                color: "white", fontSize: 14, outline: "none",
                marginBottom: 10,
              }}
            />
            <textarea
              value={deleteReason}
              onChange={function(e){ setDeleteReason(e.target.value); }}
              placeholder="(Optional) Tell us why you're leaving — helps us improve."
              disabled={deleting}
              rows={2}
              style={{
                width: "100%", boxSizing: "border-box",
                background: "#0a0a0a", border: "1px solid #333",
                borderRadius: 10, padding: "10px 14px",
                color: "white", fontSize: 13, outline: "none",
                marginBottom: 16, resize: "vertical", fontFamily: "inherit",
              }}
            />
            <div style={{ display: "flex", gap: 8 }}>
              <button onClick={function(){ if (!deleting) setShowDeleteAccountConfirm(false); }}
                disabled={deleting}
                style={{
                  flex: 1, background: "transparent", border: "1.5px solid #333",
                  borderRadius: 12, padding: "12px",
                  color: "#aaa", fontWeight: 800, fontSize: 13,
                  cursor: deleting ? "not-allowed" : "pointer",
                }}>
                Cancel
              </button>
              <button
                disabled={deleting || (deleteConfirmEmail.trim().toLowerCase() !== (user.email || "").toLowerCase())}
                onClick={async function() {
                  if (deleting) return;
                  if (deleteConfirmEmail.trim().toLowerCase() !== (user.email || "").toLowerCase()) return;
                  setDeleting(true);
                  try {
                    await apiFetch("/api/auth/delete-my-account", {
                      method: "POST",
                      body: JSON.stringify({ confirm_email: deleteConfirmEmail.trim(), reason: deleteReason.trim() }),
                    });
                    // Clear local state and log out
                    try { localStorage.clear(); sessionStorage.clear(); } catch(e) {}
                    alert("Your account has been deleted. You will be logged out.");
                    onLogout();
                  } catch(e) {
                    alert("Deletion failed: " + e.message);
                    setDeleting(false);
                  }
                }}
                style={{
                  flex: 1,
                  background: (deleteConfirmEmail.trim().toLowerCase() === (user.email || "").toLowerCase())
                    ? "linear-gradient(135deg,#DC2626,#991B1B)"
                    : "rgba(220,38,38,0.15)",
                  border: "1.5px solid " + ((deleteConfirmEmail.trim().toLowerCase() === (user.email || "").toLowerCase()) ? "#DC2626" : "#3a1a1a"),
                  borderRadius: 12, padding: "12px",
                  color: (deleteConfirmEmail.trim().toLowerCase() === (user.email || "").toLowerCase()) ? "white" : "#666",
                  fontWeight: 900, fontSize: 13, letterSpacing: 0.5,
                  cursor: (deleting || deleteConfirmEmail.trim().toLowerCase() !== (user.email || "").toLowerCase()) ? "not-allowed" : "pointer",
                  boxShadow: (deleteConfirmEmail.trim().toLowerCase() === (user.email || "").toLowerCase()) ? "0 0 20px rgba(220,38,38,0.5)" : "none",
                }}>
                {deleting ? "DELETING..." : "DELETE FOREVER"}
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
  }
}


// =============================================================================
// BOTTOM NAV
// =============================================================================

// =============================================================================
// INDEXEDDB AUDIO STORE
// Stores raw WAV ArrayBuffers keyed by clipId — no size limit unlike localStorage.
// DB: "BeatFinderAudio"  |  Store: "clips"  |  key: clipId (string)
// =============================================================================
const AudioDB = (function () {
  const DB_NAME    = "BeatFinderAudio";
  const STORE_NAME = "clips";
  const DB_VERSION = 1;
  let _db = null;

  function openDB() {
    if (_db) return Promise.resolve(_db);
    return new Promise(function (resolve, reject) {
      const req = indexedDB.open(DB_NAME, DB_VERSION);
      req.onupgradeneeded = function (e) {
        const db = e.target.result;
        if (!db.objectStoreNames.contains(STORE_NAME)) {
          db.createObjectStore(STORE_NAME); // key = clipId
        }
      };
      req.onsuccess  = function (e) { _db = e.target.result; resolve(_db); };
      req.onerror    = function (e) { reject(e.target.error); };
    });
  }

  // Save a WAV ArrayBuffer under a clip key.
  // Returns a Promise that resolves only after the IDB transaction has
  // FULLY committed. Rejects on quota errors / aborted transactions
  // (callers want a real failure signal so they can surface it to the
  // user rather than silently saving nothing).
  async function saveClip(clipId, wavArrayBuffer) {
    const db = await openDB();
    return new Promise(function (resolve, reject) {
      let tx;
      try {
        tx = db.transaction(STORE_NAME, "readwrite");
      } catch (e) { reject(e); return; }
      const req = tx.objectStore(STORE_NAME).put(wavArrayBuffer, clipId);
      // The request completing is necessary but not sufficient — wait
      // for tx.oncomplete to know the write is durably flushed.
      req.onerror = function (e) {
        // Surface quota / encoding errors here
        reject(e.target.error || new Error("IndexedDB put failed"));
      };
      tx.oncomplete = function () { resolve(); };
      tx.onerror    = function (e) {
        reject(e.target.error || new Error("IndexedDB transaction failed"));
      };
      tx.onabort    = function (e) {
        reject(e.target.error || new Error("IndexedDB transaction aborted (often quota)"));
      };
    });
  }

  // Retrieve a WAV ArrayBuffer by clip key (returns null if not found)
  async function getClip(clipId) {
    const db = await openDB();
    return new Promise(function (resolve) {
      const tx  = db.transaction(STORE_NAME, "readonly");
      const req = tx.objectStore(STORE_NAME).get(clipId);
      req.onsuccess = function (e) { resolve(e.target.result || null); };
      req.onerror   = function ()  { resolve(null); };
    });
  }

  // Delete one or more clip keys (pass an array of clipIds)
  async function deleteClips(clipIds) {
    if (!clipIds || clipIds.length === 0) return;
    const db = await openDB();
    return new Promise(function (resolve) {
      const tx    = db.transaction(STORE_NAME, "readwrite");
      const store = tx.objectStore(STORE_NAME);
      clipIds.forEach(function (id) { store.delete(id); });
      tx.oncomplete = resolve;
      tx.onerror    = resolve; // swallow — best effort
    });
  }

  // Delete ALL clips whose key starts with a project prefix  e.g. "proj_<id>_"
  async function deleteProjectClips(projectId) {
    const db = await openDB();
    return new Promise(function (resolve) {
      const tx    = db.transaction(STORE_NAME, "readwrite");
      const store = tx.objectStore(STORE_NAME);
      const req   = store.openCursor();
      req.onsuccess = function (e) {
        const cursor = e.target.result;
        if (!cursor) { resolve(); return; }
        if (String(cursor.key).startsWith("proj_" + projectId + "_")) {
          cursor.delete();
        }
        cursor.continue();
      };
      req.onerror = resolve;
    });
  }

  return { saveClip, getClip, deleteClips, deleteProjectClips };
})();

// =============================================================================
// MARKETPLACE KITS — device-local storage (IndexedDB) + runtime registry
// =============================================================================
// Purchased drum kits are stored on THIS device only. Each kit is saved as a
// { id, name, author, pads: [{ id, name, color, b64, chokeGroup }] } object —
// the exact shape BeatFinderKits.js expects — so once stored, registerAdminKits
// makes it work in the sequencer identically to a built-in kit. Ownership is
// tracked server-side (purchased_kits), so on a new device the user re-downloads
// the samples into this store after logging in. Refund → server drops ownership
// → kit no longer appears in My Kits → user can clear it locally.
const KitsDB = (function () {
  const DB_NAME = "BeatFinderKits";
  const STORE   = "kits";
  const DB_VERSION = 1;
  let _db = null;
  function openDB() {
    if (_db) return Promise.resolve(_db);
    return new Promise(function (resolve, reject) {
      const req = indexedDB.open(DB_NAME, DB_VERSION);
      req.onupgradeneeded = function (e) {
        const db = e.target.result;
        if (!db.objectStoreNames.contains(STORE)) db.createObjectStore(STORE); // key = kitId
      };
      req.onsuccess = function (e) { _db = e.target.result; resolve(_db); };
      req.onerror   = function (e) { reject(e.target.error); };
    });
  }
  async function saveKit(kitId, kitObj) {
    const db = await openDB();
    return new Promise(function (resolve, reject) {
      let tx;
      try { tx = db.transaction(STORE, "readwrite"); } catch (e) { reject(e); return; }
      tx.objectStore(STORE).put(kitObj, kitId);
      tx.oncomplete = function () { resolve(true); };
      tx.onerror = tx.onabort = function (e) { reject((e.target && e.target.error) || new Error("kit save failed")); };
    });
  }
  async function getKit(kitId) {
    const db = await openDB();
    return new Promise(function (resolve, reject) {
      const tx = db.transaction(STORE, "readonly");
      const req = tx.objectStore(STORE).get(kitId);
      req.onsuccess = function () { resolve(req.result || null); };
      req.onerror   = function (e) { reject(e.target.error); };
    });
  }
  async function getAllKits() {
    const db = await openDB();
    return new Promise(function (resolve, reject) {
      const tx = db.transaction(STORE, "readonly");
      const store = tx.objectStore(STORE);
      const out = [];
      const req = store.openCursor();
      req.onsuccess = function (e) {
        const cur = e.target.result;
        if (cur) { out.push(cur.value); cur.continue(); }
        else resolve(out);
      };
      req.onerror = function (e) { reject(e.target.error); };
    });
  }
  async function deleteKit(kitId) {
    const db = await openDB();
    return new Promise(function (resolve, reject) {
      const tx = db.transaction(STORE, "readwrite");
      tx.objectStore(STORE).delete(kitId);
      tx.oncomplete = function () { resolve(true); };
      tx.onerror = function (e) { reject(e.target.error); };
    });
  }
  return { saveKit, getKit, getAllKits, deleteKit };
})();

// Fetch a sample URL and return base64 (no data: prefix) — matches the b64
// field BeatFinderKits.js decodes. Used when downloading a purchased kit's
// samples into the local KitsDB.
async function bfFetchSampleB64(url) {
  const resp = await fetch(url);
  if (!resp.ok) throw new Error("sample fetch failed: " + resp.status);
  const blob = await resp.blob();
  return await new Promise(function (resolve, reject) {
    const fr = new FileReader();
    fr.onload = function () {
      const s = String(fr.result || "");
      const comma = s.indexOf(",");
      resolve(comma >= 0 ? s.slice(comma + 1) : s);
    };
    fr.onerror = function () { reject(new Error("b64 encode failed")); };
    fr.readAsDataURL(blob);
  });
}

// Download a marketplace kit's samples into device-local IndexedDB. Takes the
// ownership-gated /download payload ({ id, name, author, pads:[{id,name,color,url}] })
// and produces the BF_KITS-shaped object with b64 samples, then stores it.
// The kit id is namespaced "mkt_<id>" so it never collides with built-in or
// admin kit ids.
async function bfInstallMarketKit(downloadPayload) {
  const pads = await Promise.all((downloadPayload.pads || []).map(async function (p) {
    let b64 = "";
    try { b64 = await bfFetchSampleB64(p.url); } catch (e) { console.warn("[kit] sample dl failed", p.id, e); }
    return { id: p.id, name: p.name, color: p.color, b64: b64, chokeGroup: p.chokeGroup };
  }));
  const kitId = "mkt_" + downloadPayload.id;
  const kitObj = {
    id: kitId,
    name: downloadPayload.name || "Kit",
    author: downloadPayload.author || "",
    _market: true,
    pads: pads.filter(function (p) { return p.b64; }),
  };
  await KitsDB.saveKit(kitId, kitObj);
  return kitObj;
}

// Load every locally-stored market kit and register them into the kit system
// so the sequencer can use them. Safe to call repeatedly. registerAdminKits
// is additive in BeatFinderKits.js (merges into BF_KITS).
async function bfRegisterLocalMarketKits() {
  try {
    const kits = await KitsDB.getAllKits();
    if (kits && kits.length && typeof registerAdminKits === "function") {
      registerAdminKits(kits);
    }
    return kits || [];
  } catch (e) {
    console.warn("[kit] local market kit register failed", e);
    return [];
  }
}

// =============================================================================
// STUDIO SCREEN
// =============================================================================
// iOS-style wheel picker
function WheelPicker({ items, value, onChange, onClose, title, inline, label }) {
  var itemH = 44;
  var visCount = 5;
  var containerH = itemH * visCount;
  var scrollRef = useRef(null);
  var scrollTimeout = useRef(null);

  var idx = Math.max(0, items.indexOf(value));

  useEffect(function() {
    if (scrollRef.current) {
      scrollRef.current.scrollTop = idx * itemH;
    }
  }, []);

  var handleScroll = function(e) {
    clearTimeout(scrollTimeout.current);
    var el = e.target;
    scrollTimeout.current = setTimeout(function() {
      var nearest = Math.round(el.scrollTop / itemH);
      nearest = Math.max(0, Math.min(nearest, items.length - 1));
      // Snap scroll
      el.scrollTo({ top: nearest * itemH, behavior: "smooth" });
      onChange(items[nearest]);
    }, 80);
  };

  var wheel = (
    <div style={{ position:"relative", height:containerH, flex:1 }}>
      {label && <div style={{ position:"absolute", top:0, left:0, right:0, textAlign:"center", color:"#555", fontSize:10, paddingTop:4, zIndex:3 }}>{label}</div>}
      <div style={{ position:"absolute", top:"50%", left:4, right:4, height:itemH, transform:"translateY(-50%)", background:"rgba(192,38,211,0.10)", borderTop:"1px solid rgba(192,38,211,0.3)", borderBottom:"1px solid rgba(192,38,211,0.3)", borderRadius:8, pointerEvents:"none", zIndex:1 }} />
      <div style={{ position:"absolute", top:0, left:0, right:0, height:itemH*1.8, background:"linear-gradient(to bottom,#1c1c1c 30%,transparent)", pointerEvents:"none", zIndex:2 }} />
      <div style={{ position:"absolute", bottom:0, left:0, right:0, height:itemH*1.8, background:"linear-gradient(to top,#1c1c1c 30%,transparent)", pointerEvents:"none", zIndex:2 }} />
      <div ref={scrollRef} onScroll={handleScroll}
        style={{ height:"100%", overflowY:"auto", scrollSnapType:"y mandatory", WebkitOverflowScrolling:"touch",
          scrollbarWidth:"none", msOverflowStyle:"none" }}>
        <div style={{ height:itemH * Math.floor(visCount / 2) }} />
        {items.map(function(item) {
          var isSelected = item === value;
          return (
            <div key={item} style={{ height:itemH, display:"flex", alignItems:"center", justifyContent:"center", scrollSnapAlign:"center", cursor:"pointer" }}
              onClick={function(){ onChange(item); if(!inline) onClose(); }}>
              <span style={{ color:isSelected?"white":"#555", fontSize:isSelected?18:15, fontWeight:isSelected?700:400, transition:"all 0.1s", userSelect:"none" }}>
                {item}
              </span>
            </div>
          );
        })}
        <div style={{ height:itemH * Math.floor(visCount / 2) }} />
      </div>
    </div>
  );

  if (inline) return wheel;

  return (
    <div style={{ position:"absolute", inset:0, zIndex:9999, background:"rgba(0,0,0,0.7)", display:"flex", alignItems:"flex-end" }}
      onClick={onClose}>
      <div style={{ width:"100%", background:"#1c1c1c", borderRadius:"20px 20px 0 0", paddingBottom:"calc(20px + env(safe-area-inset-bottom))" }}
        onClick={function(e){ e.stopPropagation(); }}>
        <div style={{ display:"flex", alignItems:"center", justifyContent:"space-between", padding:"16px 20px 12px", borderBottom:"1px solid #2a2a2a" }}>
          <button onClick={onClose} style={{ background:"none", border:"none", color:"#888", fontSize:14, cursor:"pointer" }}>Cancel</button>
          <div style={{ color:"white", fontWeight:700, fontSize:15 }}>{title}</div>
          <button onClick={onClose} style={{ background:"none", border:"none", color:"#C026D3", fontSize:14, fontWeight:700, cursor:"pointer" }}>Done</button>
        </div>
        {wheel}
      </div>
    </div>
  );
}


// =============================================================================
// =============================================================================

// =============================================================================

// =============================================================================
// WAVEFORM CANVAS — real PCM rendering
// =============================================================================
function WaveformCanvas({ audioBuffer, color, width, height, playedFraction, dim, trimStart, trimEnd }) {
  const ref = useRef(null);
  // Cache the per-pixel peaks array between renders. Computing peaks
  // means iterating through every audio sample to find amplitude max
  // per pixel — for a 3-minute file that's ~8M samples. Doing that
  // 10× per second (as setCurrentTime fires during playback) was
  // pinning the main thread and breaking touch input.
  // We now recompute peaks ONLY when the audio data or visible
  // dimensions change. playedFraction updates are then just a cheap
  // re-fill of cached peak rectangles — fast enough to redraw on
  // every state tick without any perf cost.
  const peaksRef = useRef(null);
  const peaksKeyRef = useRef("");

  useEffect(function () {
    const cv = ref.current;
    if (!cv) return;
    const W = Math.max(1, Math.round(width));
    const H = Math.max(1, Math.round(height));
    cv.width = W; cv.height = H;
    const ctx = cv.getContext("2d");
    ctx.clearRect(0, 0, W, H);
    const mid = H / 2;

    if (!audioBuffer) {
      // Placeholder centre line
      ctx.strokeStyle = color + "44";
      ctx.lineWidth = 1;
      ctx.beginPath(); ctx.moveTo(0, mid); ctx.lineTo(W, mid); ctx.stroke();
      peaksRef.current = null;
      peaksKeyRef.current = "";
      return;
    }

    // Build a cache key from everything that affects the peak array.
    // playedFraction and dim are NOT in the key — they only change
    // the fill colours, not the shape of the waveform.
    const key = [
      audioBuffer.length, audioBuffer.sampleRate, W,
      trimStart || 0, trimEnd === undefined ? -1 : trimEnd,
    ].join("|");

    let peaks = peaksRef.current;
    if (!peaks || peaksKeyRef.current !== key) {
      // Downsample to at most 4096 points first — makes peak compute instant
      // regardless of file length (a 3-min file at 44.1kHz = 8M samples; capped at 4096).
      const nc  = audioBuffer.numberOfChannels;
      const totalLen = audioBuffer.length;
      const MAX_PTS = 4096;
      const step = Math.max(1, Math.floor(totalLen / MAX_PTS));
      const dsLen = Math.ceil(totalLen / step);
      const raw = new Float32Array(dsLen);
      for (let c = 0; c < nc; c++) {
        const ch = audioBuffer.getChannelData(c);
        for (let i = 0; i < dsLen; i++) {
          const idx = Math.min(i * step, ch.length - 1);
          raw[i] += Math.abs(ch[idx]) / nc;
        }
      }

      // Only draw the trimmed region of the downsampled buffer
      const dur = audioBuffer.duration;
      const tS = Math.max(0, Math.floor(((trimStart || 0) / dur) * dsLen));
      const tE = Math.min(dsLen, Math.ceil((((trimEnd !== undefined && trimEnd !== null) ? trimEnd : dur) / dur) * dsLen));
      const regionLen = Math.max(1, tE - tS);
      const spp = regionLen / W;

      // Pre-compute peaks from the downsampled region
      peaks = new Float32Array(W);
      for (let px = 0; px < W; px++) {
        const s = Math.floor(tS + px * spp);
        const e = Math.min(Math.floor(tS + (px + 1) * spp) + 1, tE);
        let pk = 0;
        for (let i = s; i < e; i++) { if (raw[i] > pk) pk = raw[i]; }
        peaks[px] = pk;
      }
      peaksRef.current = peaks;
      peaksKeyRef.current = key;
    }

    const playedPx = Math.floor(W * Math.min(1, Math.max(0, playedFraction)));

    const unplayedAlpha = dim ? "28" : "88";
    const playedAlpha   = dim ? "44" : "ee";

    // Unplayed portion
    ctx.fillStyle = color + unplayedAlpha;
    for (let px = playedPx; px < W; px++) {
      const h = Math.max(1, peaks[px] * mid * 0.92);
      ctx.fillRect(px, mid - h, 1, h * 2);
    }

    // Played portion — brighter
    if (playedPx > 0) {
      ctx.fillStyle = color + playedAlpha;
      for (let px = 0; px < playedPx; px++) {
        const h = Math.max(1, peaks[px] * mid * 0.92);
        ctx.fillRect(px, mid - h, 1, h * 2);
      }
    }
  }, [audioBuffer, width, height, color, playedFraction, dim, trimStart, trimEnd]);

  return (
    <canvas
      ref={ref}
      style={{ display:"block", width:"100%", height:"100%", imageRendering:"pixelated" }}
    />
  );
}


// =============================================================================
// useHistory — undo/redo for tracks state
// =============================================================================
// IMPORTANT: This wraps the setter to record EVERY state change into the
// past stack. The setter must support BOTH forms:
//   • setState(newValue)        — direct value
//   • setState(prev => fn(prev)) — functional updater (preferred form)
//
// Previously the functional form was broken — it pushed the function REFERENCE
// to past instead of the computed value, AND each call recorded the same
// pre-render value if multiple setState calls fired in one render cycle.
// Result: undo only worked for the 2-3 most recent direct-value calls.
//
// The fix: use setStateRaw's own functional updater to capture the truly
// current value before computing the new value, then push BOTH the prev
// and compute the new value in a single transaction.
function useHistory(initial) {
  const past    = useRef([]);
  const future  = useRef([]);
  const [state, setStateRaw] = React.useState(initial);
  const HISTORY_LIMIT = 200;  // bumped from 50 — undo should be generous

  // ── Gesture coalescing ──
  // For drag/trim/scrub interactions, every pointermove would otherwise
  // push a separate history entry. The user then has to mash undo through
  // dozens of micro-steps to revert a single gesture.
  //
  // Pattern:
  //   beginGesture()  — capture pre-gesture state; subsequent setState
  //                     calls update state but DON'T push to past
  //   setState(...)   — during gesture, mutates state silently
  //   endGesture()    — push ONE past entry containing the pre-gesture
  //                     snapshot. The whole drag becomes a single undo step.
  //
  // If beginGesture is never balanced by endGesture (e.g. browser tab
  // killed mid-drag), the next setState OUTSIDE a gesture will still work
  // — but the in-flight gesture entry is lost. Acceptable failure mode.
  const gestureSnapshotRef = useRef(null);  // pre-gesture state, or null
  const inGestureRef       = useRef(false);

  const setState = function (next) {
    // Use the functional form of setStateRaw to atomically read the truly
    // current state. This works correctly even when multiple setState calls
    // are batched together in one React render cycle.
    setStateRaw(function(prev){
      // Compute the new value (allowing both direct and functional forms)
      const newValue = (typeof next === "function") ? next(prev) : next;
      // Don't record no-op changes (e.g. same object reference back)
      if (newValue === prev) return prev;
      if (inGestureRef.current) {
        // Inside a gesture — update state but skip history push. The
        // gestureSnapshotRef already holds the pre-gesture state, which
        // will be pushed once on endGesture.
        return newValue;
      }
      past.current.push(prev);
      if (past.current.length > HISTORY_LIMIT) past.current.shift();
      future.current = [];
      return newValue;
    });
  };

  const beginGesture = function () {
    // Snapshot the CURRENT state (the one the user will undo back to).
    // We can't read `state` here because it might be stale inside an event
    // handler — use the functional setter trick to grab the truly current
    // value without changing it.
    setStateRaw(function(prev){
      gestureSnapshotRef.current = prev;
      return prev;  // no state change
    });
    inGestureRef.current = true;
  };

  const endGesture = function () {
    if (!inGestureRef.current) return;
    inGestureRef.current = false;
    // Push the pre-gesture snapshot as a single history entry.
    // Only push if the state actually changed during the gesture — comparing
    // current state to the snapshot via the functional setter again.
    setStateRaw(function(prev){
      const snap = gestureSnapshotRef.current;
      gestureSnapshotRef.current = null;
      if (snap === undefined || snap === null) return prev;
      if (snap === prev) return prev;  // gesture was a no-op
      past.current.push(snap);
      if (past.current.length > HISTORY_LIMIT) past.current.shift();
      future.current = [];
      return prev;
    });
  };

  const undo = function () {
    // If we're mid-gesture and undo fires (rare — user hits the button
    // while still dragging?), end the gesture first so the snapshot
    // gets pushed. Then undo can pop it.
    if (inGestureRef.current) endGesture();
    if (past.current.length === 0) return;
    setStateRaw(function(prev){
      const restored = past.current.pop();
      if (restored === undefined) return prev;
      future.current.push(prev);
      if (future.current.length > HISTORY_LIMIT) future.current.shift();
      return restored;
    });
  };

  const redo = function () {
    if (inGestureRef.current) endGesture();
    if (future.current.length === 0) return;
    setStateRaw(function(prev){
      const restored = future.current.pop();
      if (restored === undefined) return prev;
      past.current.push(prev);
      if (past.current.length > HISTORY_LIMIT) past.current.shift();
      return restored;
    });
  };

  const canUndo = past.current.length > 0;
  const canRedo = future.current.length > 0;

  return [state, setState, undo, redo, canUndo, canRedo, beginGesture, endGesture];
}

// =============================================================================
// STUDIO SCREEN — proper DAW physics
// =============================================================================
//
//  Global coordinate system:
//    xPosition = time * PPS
//
//  Fixed playhead at left: PLAYHEAD_X (from right-column left edge)
//    currentTime = (scrollLeft + PLAYHEAD_X) / PPS
//
//  During playback:
//    container.scrollLeft = currentTime * PPS - PLAYHEAD_X
//
//  Manual scroll:
//    currentTime = (container.scrollLeft + PLAYHEAD_X) / PPS
//
// =============================================================================

// ── Error boundary — catches any render crash inside StudioScreen ──────────
// Without this, a single bad track/clip causes a blank white screen with no
// way out except force-closing the app.
class StudioErrorBoundary extends React.Component {
  constructor(props) {
    super(props);
    this.state = { crashed: false, errMsg: "", saved: false };
  }
  static getDerivedStateFromError(err) {
    return { crashed: true, errMsg: err && err.message ? err.message : String(err) };
  }
  componentDidCatch(err, info) {
    console.error("[BeatFinder] Studio crash:", err, info);
    // Emergency save: persist whatever state we have in sessionStorage to survive the crash
    try {
      const existing = sessionStorage.getItem("bf_studio_state");
      if (existing) {
        // Mark as crash-recovered so Studio can offer to restore on next open
        const snap = JSON.parse(existing);
        snap._crashRecovered = true;
        snap._crashMsg = err && err.message ? err.message : String(err);
        snap._crashAt  = new Date().toISOString();
        sessionStorage.setItem("bf_studio_crash_recovery", JSON.stringify(snap));
      }
    } catch(e) {}
    this.setState({ saved: true });
  }
  render() {
    if (this.state.crashed) {
      return (
        <div style={{ background:"#0a0a0a", minHeight:"100dvh", display:"flex", flexDirection:"column", alignItems:"center", justifyContent:"center", padding:32, fontFamily:"'DM Sans',sans-serif", gap:16 }}>
          <svg width="52" height="52" viewBox="0 0 24 24" fill="none" stroke="#F59E0B" strokeWidth="1.5" strokeLinecap="round">
            <path d="M10.29 3.86L1.82 18a2 2 0 0 0 1.71 3h16.94a2 2 0 0 0 1.71-3L13.71 3.86a2 2 0 0 0-3.42 0z"/>
            <line x1="12" y1="9" x2="12" y2="13"/><line x1="12" y1="17" x2="12.01" y2="17"/>
          </svg>
          <div style={{ color:"white", fontWeight:800, fontSize:20, textAlign:"center" }}>Studio hit a problem</div>
          <div style={{ color:"#555", fontSize:13, textAlign:"center", lineHeight:1.7, maxWidth:280 }}>
            {this.state.errMsg || "An unexpected error occurred."}
          </div>
          {this.state.saved && (
            <div style={{ background:"rgba(34,197,94,0.1)", border:"1px solid rgba(34,197,94,0.3)", borderRadius:10, padding:"10px 18px", color:"#22C55E", fontSize:12, fontWeight:700, textAlign:"center" }}>
              Your project state has been saved — you can restore it when Studio restarts.
            </div>
          )}
          <button
            onClick={function(){
              try { localStorage.removeItem("bf_studio_active"); } catch(e){}
              window.location.reload();
            }}
            style={{ background:"#C026D3", border:"none", borderRadius:24, color:"white", fontWeight:800, fontSize:15, padding:"14px 32px", cursor:"pointer", marginTop:8 }}
          >
            Restart Studio
          </button>
          <button
            onClick={function(){
              if (window.confirm("This will delete all saved projects. Continue?")) {
                try { localStorage.removeItem("bf_studio_projects"); localStorage.removeItem("bf_studio_crash_recovery"); } catch(e){}
                window.location.reload();
              }
            }}
            style={{ background:"none", border:"none", color:"#555", fontSize:13, cursor:"pointer" }}
          >
            Clear saved projects &amp; restart
          </button>
        </div>
      );
    }
    return this.props.children;
  }
}

// ── Plugin chain panel — extracted from IIFE so useState is a valid hook call ──
function FxPanelPlugins({ fx, upd, eq5, EQGraph, CompGraph, ReverbViz, Knob, analyserNode, isPlaying, trackId }) {
  // EQPlugin / CompPlugin / ReverbPlugin / PitchPlugin are pure render helpers
  // defined here so they always have the correct fx/upd/Knob/graph refs.
  // Plugin sub-components are wrapped in React.memo at module scope,
  // so they're React component objects (not plain callable functions).
  // Render them as JSX, not by calling them directly. The aliases below
  // exist to preserve the call-site style `<NoiseRemoverPlugin .../>`
  // while still delegating to the memoized module-scope components.
  const EQPlugin           = function(p){ return <_EQPlugin {...p} />; };
  const CompPlugin         = function(p){ return <_CompPlugin {...p} />; };
  const RCompPlugin        = function(p){ return <_RCompPlugin {...p} />; };
  const ReverbPlugin       = function(p){ return <_ReverbPlugin {...p} />; };
  const OceanPlugin        = function(p){ return <_OceanPlugin {...p} />; };
  const NoiseRemoverPlugin = function(p){ return <_NoiseRemoverPlugin {...p} />; };
  const DoublerPlugin      = function(p){ return <_DoublerPlugin {...p} />; };
  const HDelayPlugin       = function(p){ return <_HDelayPlugin {...p} />; };
  const TRottenPlugin      = function(p){ return <_TRottenMasterPlugin {...p} />; };
  const BandpassPlugin     = function(p){ return <_BandpassPlugin {...p} />; };
  const PultecPlugin       = function(p){ return <_PultecPlugin {...p} />; };
  const TQPlugin           = function(p){ return <_TQPlugin {...p} />; };
  // Phosphor-style plugin icons — each tailored to its FX type
  function PhosphorPluginIcon({ id, color = "#888", size = 22 }) {
    const paths = {
      // Parametric EQ — frequency curve with nodes
      "ph-eq": "M3 18 Q6 6 9 12 Q12 18 15 8 Q18 -2 21 10",
      // Compressor — waveform being squashed
      "ph-compress": "M3 12h3 M6 12 L8 6 L10 18 L12 8 L14 16 L16 12h5 M18 7 L21 12 L18 17",
      // Reverb — expanding wave rings
      "ph-reverb": "M12 12m-2 0a2 2 0 104 0 2 2 0 10-4 0 M8.5 8.5a5 5 0 017 7 M5.5 5.5a9 9 0 0113 13 M2.5 2.5a13 13 0 0119 19",
      // Pitch — music note with arrow up
      "ph-pitch": "M9 17V6l8-2v11 M9 17a2.5 2.5 0 11-5 0 2.5 2.5 0 015 0z M17 15a2.5 2.5 0 11-5 0 2.5 2.5 0 015 0z M19 3l3 3-3 3 M22 6h-5",
      // Noise Gate — gate/threshold line with cut
      "ph-gate": "M3 12h4 M7 12 L9 6 L11 18 L13 6 L15 18 L17 12h4 M3 7h2 M3 17h2 M19 7h2 M19 17h2",
      // Doubler — two overlapping waveforms
      "ph-doubler": "M3 9 Q6 4 9 9 Q12 14 15 9 Q18 4 21 9 M3 15 Q6 10 9 15 Q12 20 15 15 Q18 10 21 15",
      // Delay — clock with echo lines
      "ph-delay": "M12 22a10 10 0 100-20 10 10 0 000 20z M12 7v5l3 2 M4 12H1 M1 9l3 3-3 3",
      // Master — crown / broadcast tower
      "ph-master": "M2 20h20 M12 4 L4 14h16z M9 14v6 M15 14v6 M12 4v10",
      // Bandpass — bandpass filter curve
      "ph-bandpass": "M2 18 Q4 18 6 14 Q8 6 12 6 Q16 6 18 14 Q20 18 22 18",
    };
    const d = paths[id];
    if (!d) return null;
    return (
      <svg width={size} height={size} viewBox="0 0 24 24" fill="none"
        stroke={color} strokeWidth={1.6} strokeLinecap="round" strokeLinejoin="round"
        style={{ display:"block" }}>
        {d.split(/\s+(?=M)/).map(function(seg, i) { return <path key={i} d={seg} />; })}
      </svg>
    );
  }

  const [showPluginPicker, setShowPluginPicker] = React.useState(false);
  // Per-plugin collapse state. Set of plugin keys (e.g. "eq", "compressor")
  // that are currently collapsed (header visible, body hidden). New
  // plugins start expanded by default — only added to the Set when the
  // user explicitly collapses them.
  const [collapsedPlugins, setCollapsedPlugins] = React.useState(function(){ return new Set(); });
  // Expose collapse-all / expand-all on window so FxPanel's header
  // buttons (defined outside this component) can trigger them. Kept in
  // sync via this effect on every render.
  React.useEffect(function(){
    if (typeof window === "undefined") return;
    window._bfCollapseAllFx = function(){
      setCollapsedPlugins(new Set(fx.pluginChain || []));
    };
    window._bfExpandAllFx = function(){
      setCollapsedPlugins(new Set());
    };
    return function(){
      try {
        if (typeof window === "undefined") return;
        delete window._bfCollapseAllFx;
        delete window._bfExpandAllFx;
      } catch(_){}
    };
  }, [fx.pluginChain]);
  const chain = fx.pluginChain || [];

  const ALL_PLUGINS = [
    { key:"eq",           label:"Pro EQ",              sub:"5-Band · Drag handles",        icon:"▦", color:"#0ea5e9" },
    { key:"compressor",   label:"Compressor",          sub:"Dynamics processor",            icon:"fader", color:"#7C3AED" },
    { key:"rcomp",        label:"R-Comp",              sub:"Renaissance · ARC auto-release", icon:"fader", color:"#06b6d4" },
    { key:"reverb",       label:"Convolution Reverb",  sub:"Room simulation",               icon:"wave", color:"#C026D3" },
    { key:"ocean",        label:"Ocean Reverb",        sub:"Deep · Lush · Atmospheric",     icon:"wave", color:"#0891b2" },
    { key:"noiseremover", label:"Noise Remover",       sub:"RNNoise · AI denoising",        icon:"mic", color:"#10B981" },
    { key:"autotune",     label:"Autotune",            sub:"T-Pain · Melodic · Key/Scale",  icon:"knobs", color:"#C026D3" },
    { key:"doubler",      label:"Vocal Doubler",        sub:"Stereo width · Haas effect",     icon:"speaker", color:"#F59E0B" },
    { key:"hdelay",       label:"H-Delay",              sub:"Tape · BPM sync · Analog",       icon:"◷", color:"#E85D04" },
    { key:"trotten",      label:"T-Rotten Master",      sub:"Mastering · Analog warmth",       icon:"knobs", color:"#C8762A" },
    { key:"bandpass",     label:"GRM Bandpass",         sub:"Dual 6th-order · Resonance",      icon:"knobs", color:"#00b4d8" },
    { key:"pultec",       label:"Pultec EQP-1A",        sub:"Passive tube · Boost/Atten trick",icon:"knobs", color:"#D4A574" },
    { key:"tq",           label:"TQ Vocal Dynamic",      sub:"7-Band · Dynamic EQ for rap",     icon:"knobs", color:"#A855F7" },
  ];

  const addPlugin = function(key) {
    if (chain.includes(key)) return;
    upd("pluginChain", null, [...chain, key]);
    setShowPluginPicker(false);
  };

  const removePlugin = function(key) {
    // Single atomic update: removes from chain + forces on:false so the
    // always-built audio node bypasses immediately (no lingering effect).
    upd("__remove", null, key);
  };

  const movePlugin = function(idx, dir) {
    const next = [...chain];
    const swap = idx + dir;
    if (swap < 0 || swap >= next.length) return;
    const tmp = next[idx]; next[idx] = next[swap]; next[swap] = tmp;
    upd("pluginChain", null, next);
  };

  // FL Studio category filter
  const FL_CATS = ["ALL", "DYNAMICS", "EQ", "REVERB", "PITCH", "UTILITY"];
  const [flCat, setFlCat] = React.useState("ALL");

  const FL_PLUGINS = [
    { key:"eq",           label:"Parametric EQ",      sub:"5-Band",          icon:"ph-eq",          color:"#00b4d8", cat:"EQ",       tag:"MIXER"  },
    { key:"compressor",   label:"Compressor",          sub:"Dynamics",        icon:"ph-compress",    color:"#7c3aed", cat:"DYNAMICS", tag:"EFFECT" },
    { key:"rcomp",        label:"R-Comp",              sub:"ARC · Warm/Electro", icon:"ph-compress", color:"#06b6d4", cat:"DYNAMICS", tag:"EFFECT" },
    { key:"reverb",       label:"Reverb",              sub:"Room sim",        icon:"ph-reverb",      color:"#0891b2", cat:"REVERB",   tag:"EFFECT" },
    { key:"ocean",        label:"Ocean Reverb",        sub:"Lush · Deep",     icon:"ph-reverb",      color:"#0369a1", cat:"REVERB",   tag:"EFFECT" },
    { key:"noiseremover", label:"Noise Gate AI",       sub:"RNNoise",         icon:"ph-gate",        color:"#059669", cat:"DYNAMICS", tag:"UTILITY" },
    { key:"autotune",     label:"Autotune",            sub:"T-Pain · Melodic",icon:"ph-master",      color:"#be185d", cat:"PITCH",    tag:"EFFECT" },
    { key:"doubler",      label:"Doubler",             sub:"Haas / Width",    icon:"ph-doubler",     color:"#d97706", cat:"UTILITY",  tag:"MIXER"  },
    { key:"hdelay",       label:"T-Delay",             sub:"Tape · BPM sync", icon:"ph-delay",       color:"#ea580c", cat:"UTILITY",  tag:"EFFECT" },
    { key:"trotten",      label:"T-Rotten Master 19",  sub:"Analog warmth",   icon:"ph-master",      color:"#854d0e", cat:"DYNAMICS", tag:"MASTER" },
    { key:"bandpass",     label:"GRM Bandpass",        sub:"Dual 6th-order",  icon:"ph-bandpass",    color:"#0e7490", cat:"EQ",       tag:"FILTER" },
    { key:"pultec",       label:"Pultec EQP-1A",       sub:"Tube · Vintage",  icon:"ph-eq",          color:"#D4A574", cat:"EQ",       tag:"EFFECT" },
    { key:"tq",           label:"TQ Vocal Dynamic",    sub:"7-Band Dyn EQ",   icon:"ph-eq",          color:"#A855F7", cat:"EQ",       tag:"EFFECT" },
  ];

  const visiblePlugins = FL_PLUGINS.filter(function(p){
    return flCat === "ALL" || p.cat === flCat;
  });

  return (
    <div style={{ padding:"14px" }}>

    {/* ── FL Studio-style Plugin Browser (full-screen overlay) ── */}
    {showPluginPicker && (
      <div style={{
        position:"fixed", inset:0, zIndex:9999,
        background:"rgba(0,0,0,0.88)",
        backdropFilter:"blur(12px)",
        display:"flex", flexDirection:"column",
        fontFamily:"'DM Sans',sans-serif",
      }}
        onClick={function(){ setShowPluginPicker(false); }}>
        <div style={{
          position:"absolute", bottom:0, left:0, right:0,
          background:"#0d0d0f",
          borderRadius:"20px 20px 0 0",
          border:"1px solid rgba(255,255,255,0.06)",
          borderBottom:"none",
          paddingBottom:"calc(16px + env(safe-area-inset-bottom))",
          height:"88vh",
          display:"flex", flexDirection:"column",
        }} onClick={function(e){ e.stopPropagation(); }}>

          {/* ── Top accent line ── */}
          <div style={{
            height:1, borderRadius:"20px 20px 0 0",
            background:"linear-gradient(90deg,transparent,rgba(168,85,247,0.6) 30%,rgba(139,92,246,0.8) 50%,rgba(168,85,247,0.6) 70%,transparent)",
          }} />

          {/* ── Title row ── */}
          <div style={{
            display:"flex", alignItems:"center", justifyContent:"space-between",
            padding:"16px 18px 10px",
            borderBottom:"1px solid rgba(255,255,255,0.04)",
          }}>
            <div>
              <div style={{ color:"#fff", fontWeight:700, fontSize:15, letterSpacing:0.2 }}>Effects</div>
              <div style={{ color:"#3a3a3a", fontSize:10, marginTop:2, letterSpacing:0.5 }}>{FL_PLUGINS.length} PLUGINS AVAILABLE</div>
            </div>
            <button onClick={function(){ setShowPluginPicker(false); }}
              style={{
                background:"rgba(255,255,255,0.04)", border:"1px solid rgba(255,255,255,0.08)",
                borderRadius:8, color:"#555", fontSize:11, fontWeight:600,
                padding:"6px 12px", cursor:"pointer", letterSpacing:0.5,
              }}>✕ Close</button>
          </div>

          {/* ── Search bar ── */}
          <div style={{ padding:"10px 16px 0" }}>
            <div style={{
              display:"flex", alignItems:"center", gap:8,
              background:"rgba(255,255,255,0.03)",
              border:"1px solid rgba(255,255,255,0.07)",
              borderRadius:10, padding:"8px 12px",
            }}>
              <span style={{ color:"#333", fontSize:11 }}>⌕</span>
              <span style={{ color:"#2a2a2a", fontSize:12, fontWeight:400, letterSpacing:0.2 }}>Search plugins…</span>
            </div>
          </div>

          {/* ── Category tabs ── */}
          <div style={{
            display:"flex", gap:4, padding:"10px 16px",
            overflowX:"auto", flexShrink:0,
          }}>
            {FL_CATS.map(function(cat){
              const active = flCat === cat;
              return (
                <button key={cat} onClick={function(){ setFlCat(cat); }}
                  style={{
                    padding:"5px 12px", borderRadius:6, flexShrink:0,
                    background: active ? "rgba(139,92,246,0.15)" : "transparent",
                    border:"1px solid " + (active ? "rgba(139,92,246,0.5)" : "rgba(255,255,255,0.06)"),
                    color: active ? "#a855f7" : "#3a3a3a",
                    fontSize:9, fontWeight:700, letterSpacing:1,
                    cursor:"pointer", transition:"all 0.15s",
                  }}>{cat}</button>
              );
            })}
          </div>

          {/* ── Plugin list ── */}
          <div
            ref={function(el){
              if (!el || el._bfPickerScrollSetup) return;
              el._bfPickerScrollSetup = true;
              // iOS-friendly JS touch scroll with momentum. preventDefault
              // on every touchmove (no threshold) so iOS never quietly
              // engages native scroll on the body or any parent during
              // the first few pixels of the gesture.
              let active = false, startY = 0, startScroll = 0;
              let lastY = 0, lastT = 0, vel = 0;
              let raf = 0;
              const cancel = function(){ if (raf) { cancelAnimationFrame(raf); raf = 0; } };
              const onStart = function(e){
                if (!e.touches || !e.touches[0]) return;
                cancel();
                active = true;
                startY      = e.touches[0].clientY;
                startScroll = el.scrollTop;
                lastY       = startY;
                lastT       = Date.now();
                vel         = 0;
              };
              const onMove = function(e){
                if (!active || !e.touches || !e.touches[0]) return;
                // ALWAYS preventDefault — guarantees iOS does not also
                // scroll the page underneath or bounce-overscroll.
                try { e.preventDefault(); } catch(_){}
                const y  = e.touches[0].clientY;
                const dy = startY - y;
                const sh = el.scrollHeight, ch = el.clientHeight;
                el.scrollTop = Math.max(0, Math.min(sh - ch, startScroll + dy));
                const now = Date.now();
                const dt  = now - lastT;
                if (dt > 0) vel = (lastY - y) / dt;
                lastY = y; lastT = now;
              };
              const onEnd = function(){
                if (!active) return;
                active = false;
                if (Math.abs(vel) > 0.05) {
                  const step = function(){
                    vel *= 0.94;
                    if (Math.abs(vel) < 0.02) { raf = 0; return; }
                    const sh = el.scrollHeight, ch = el.clientHeight;
                    el.scrollTop = Math.max(0, Math.min(sh - ch, el.scrollTop + vel * 16));
                    raf = requestAnimationFrame(step);
                  };
                  raf = requestAnimationFrame(step);
                }
              };
              el.addEventListener("touchstart",  onStart, { passive: true });
              el.addEventListener("touchmove",   onMove,  { passive: false });
              el.addEventListener("touchend",    onEnd,   { passive: true });
              el.addEventListener("touchcancel", onEnd,   { passive: true });
            }}
            style={{ overflowY:"auto", flex:1, minHeight:0, padding:"0 12px 8px", WebkitOverflowScrolling:"touch", overscrollBehavior:"contain", touchAction:"pan-y" }}>
            {visiblePlugins.map(function(p, idx){
              const already = chain.includes(p.key);
              return (
                <button key={p.key}
                  onClick={function(){ if(!already) addPlugin(p.key); }}
                  disabled={already}
                  style={{
                    display:"flex", alignItems:"center", gap:0,
                    width:"100%", marginBottom:2,
                    background: already ? "rgba(255,255,255,0.02)" : "rgba(255,255,255,0.03)",
                    border:"1px solid rgba(255,255,255,0.05)",
                    borderLeft: already ? "2px solid rgba(255,255,255,0.05)" : "2px solid rgba(139,92,246,0.35)",
                    borderRadius:8, overflow:"hidden",
                    cursor: already ? "default" : "pointer",
                    opacity: already ? 0.3 : 1,
                    textAlign:"left",
                    transition:"all 0.12s",
                  }}>

                  {/* Index */}
                  <div style={{
                    width:28, flexShrink:0,
                    display:"flex", alignItems:"center", justifyContent:"center",
                    alignSelf:"stretch",
                  }}>
                    <span style={{ color:"#2a2a2a", fontSize:9, fontWeight:700, fontFamily:"monospace" }}>
                      {String(idx+1).padStart(2,"0")}
                    </span>
                  </div>

                  {/* Icon — Phosphor-style per-plugin SVG */}
                  <div style={{
                    width:42, height:42, flexShrink:0,
                    background:"rgba(255,255,255,0.03)",
                    borderRight:"1px solid rgba(255,255,255,0.04)",
                    display:"flex", alignItems:"center", justifyContent:"center",
                  }}><PhosphorPluginIcon id={p.icon} color={already ? "#2a2a2a" : p.color} /></div>

                  {/* Name + sub */}
                  <div style={{ flex:1, padding:"10px 12px", minWidth:0 }}>
                    <div style={{
                      color: already ? "#2a2a2a" : "#d4d4d4",
                      fontWeight:600, fontSize:12, lineHeight:1,
                      overflow:"hidden", textOverflow:"ellipsis", whiteSpace:"nowrap",
                      letterSpacing:0.1,
                    }}>{p.label}</div>
                    <div style={{ color:"#2a2a2a", fontSize:10, marginTop:3, letterSpacing:0.3 }}>{p.sub}</div>
                  </div>

                  {/* Tag — subtle */}
                  <div style={{
                    padding:"2px 7px", marginRight:8, flexShrink:0,
                    background:"rgba(255,255,255,0.03)",
                    border:"1px solid rgba(255,255,255,0.06)",
                    borderRadius:4,
                    color:"#333", fontSize:8, fontWeight:700, letterSpacing:0.6,
                  }}>{p.tag}</div>

                  {/* Add button */}
                  {already ? (
                    <div style={{
                      width:34, alignSelf:"stretch", flexShrink:0,
                      display:"flex", alignItems:"center", justifyContent:"center",
                    }}>
                      <span style={{ color:"#2a2a2a", fontSize:12 }}>✓</span>
                    </div>
                  ) : (
                    <div style={{
                      width:34, alignSelf:"stretch", flexShrink:0,
                      background:"rgba(139,92,246,0.08)",
                      borderLeft:"1px solid rgba(139,92,246,0.15)",
                      display:"flex", alignItems:"center", justifyContent:"center",
                      color:"rgba(139,92,246,0.7)", fontSize:18, fontWeight:300,
                    }}>+</div>
                  )}
                </button>
              );
            })}
          </div>

          {/* ── Footer ── */}
          <div style={{
            padding:"8px 16px 0",
            borderTop:"1px solid rgba(255,255,255,0.03)",
            color:"#222", fontSize:9, fontWeight:600,
            textAlign:"center", letterSpacing:1,
          }}>TAP TO LOAD · DRAG TO REORDER IN CHAIN</div>
        </div>
      </div>
    )}

    {/* ── Empty state — FL Channel Rack empty ── */}
    {chain.length === 0 && (
      <div style={{ flex:1, display:"flex", flexDirection:"column", alignItems:"center", justifyContent:"center", padding:"40px 20px", gap:12, textAlign:"center" }}>
        <div style={{
          width:56, height:56, borderRadius:14,
          background:"#111", border:"1px solid #1e1e1e",
          display:"flex", alignItems:"center", justifyContent:"center",
          fontSize:24,
        }}><AppIcon id="knobs" size={20}/></div>
        <div style={{ color:"#333", fontWeight:800, fontSize:13, letterSpacing:1 }}>EMPTY CHAIN</div>
        <div style={{ color:"#252525", fontSize:11, lineHeight:1.6, maxWidth:220 }}>
          Open the plugin database below to load effects into this chain
        </div>
      </div>
    )}

    {/* ── Active plugin slots ── */}
    {chain.map(function(key, idx){
      const plugMeta = FL_PLUGINS.find(function(p){ return p.key === key; }) || {};
      const isCollapsed = collapsedPlugins.has(key);
      return (
        <div key={key} style={{ marginBottom:12 }}>
          {/* Slot header */}
          <div style={{ display:"flex", alignItems:"center", gap:6, marginBottom:4, paddingLeft:2 }}>
            <div style={{ display:"flex", gap:1 }}>
              <button onClick={function(){ movePlugin(idx, -1); }}
                style={{ background:"none", border:"none", color: idx===0 ? "#252525" : "#555", fontSize:13, cursor: idx===0 ? "default" : "pointer", padding:"2px 5px", lineHeight:1 }}>↑</button>
              <button onClick={function(){ movePlugin(idx, 1); }}
                style={{ background:"none", border:"none", color: idx===chain.length-1 ? "#252525" : "#555", fontSize:13, cursor: idx===chain.length-1 ? "default" : "pointer", padding:"2px 5px", lineHeight:1 }}>↓</button>
            </div>
            <span style={{ color:"#2a2a2a", fontSize:9, fontWeight:700, letterSpacing:1.5, fontFamily:"monospace" }}>SLOT {idx+1}</span>
            {/* Per-plugin VU meter — shares the track analyser since we can't tap between nodes */}
            <div style={{ flex:1, display:"flex", alignItems:"center", justifyContent:"center" }}>
              <VUMeter analyserNode={analyserNode} isActive={isPlaying} compact={true} showLabel={false} />
            </div>
            {/* Collapse / expand toggle — chevron rotates based on state */}
            <button
              aria-label={isCollapsed ? "Expand plugin" : "Collapse plugin"}
              onClick={function(){
                setCollapsedPlugins(function(prev){
                  const next = new Set(prev);
                  if (next.has(key)) next.delete(key); else next.add(key);
                  return next;
                });
              }}
              style={{
                background:"rgba(124,58,237,0.08)", border:"1px solid rgba(124,58,237,0.2)",
                borderRadius:6, color:"#A78BFA", fontSize:11, fontWeight:700,
                padding:"3px 8px", cursor:"pointer", letterSpacing:0.5,
                display:"flex", alignItems:"center", justifyContent:"center",
                minWidth: 28,
              }}>
              <span style={{
                display:"inline-block",
                transform: isCollapsed ? "rotate(-90deg)" : "rotate(0deg)",
                transition:"transform 0.18s ease",
                lineHeight:1,
              }}>▾</span>
            </button>
            <button onClick={function(){ removePlugin(key); }}
              style={{ background:"rgba(239,68,68,0.08)", border:"1px solid rgba(239,68,68,0.18)", borderRadius:6, color:"#EF4444", fontSize:9, fontWeight:800, padding:"3px 10px", cursor:"pointer", letterSpacing:0.5 }}>REMOVE</button>
          </div>

          {/* Plugin body — hidden when collapsed */}
          {!isCollapsed && (<>

          {/* EQ plugin */}
          {key === "eq" && <EQPlugin fx={fx} upd={upd} eq5={eq5} EQGraph={EQGraph} Knob={Knob} analyserNode={analyserNode} isPlaying={isPlaying} />}

          {/* Compressor plugin */}
          {key === "compressor" && <CompPlugin fx={fx} upd={upd} CompGraph={CompGraph} Knob={Knob} />}

          {/* R-Comp plugin */}
          {key === "rcomp" && <RCompPlugin fx={fx} upd={upd} Knob={Knob} />}

          {/* Reverb plugin */}
          {key === "reverb" && <ReverbPlugin fx={fx} upd={upd} ReverbViz={ReverbViz} Knob={Knob} />}

          {/* Ocean Reverb plugin */}
          {key === "ocean" && <OceanPlugin fx={fx} upd={upd} Knob={Knob} />}


          {/* Noise Remover plugin */}
          {key === "noiseremover" && <NoiseRemoverPlugin fx={fx} upd={upd} Knob={Knob} />}

          {/* Vocal Doubler plugin */}
          {key === "doubler" && <DoublerPlugin fx={fx} upd={upd} Knob={Knob} />}

          {/* H-Delay plugin */}
          {key === "hdelay" && <HDelayPlugin fx={fx} upd={upd} Knob={Knob} />}

          {/* T-Rotten Master plugin */}
          {key === "trotten" && <TRottenPlugin fx={fx} upd={upd} Knob={Knob} />}

          {/* GRM Bandpass plugin */}
          {key === "bandpass" && <BandpassPlugin fx={fx} upd={upd} Knob={Knob} />}

          {/* Pultec EQP-1A plugin */}
          {key === "pultec" && <PultecPlugin fx={fx} upd={upd} Knob={Knob} />}

          {/* TQ — Vocal Dynamic EQ plugin
              NOTE: We render `_TQPlugin` (module-scope component) directly
              instead of through the `TQPlugin` wrapper alias. The wrapper is
              a new function on every render of FxPanelPlugins, which would
              cause React to treat it as a new component type each render —
              unmounting and remounting _TQPlugin, RESETTING its useState
              (selectedBandId would bounce back to plosive after ~1s when the
              parent next re-rendered). Using the stable module-level
              reference fixes this. The other plugin aliases above could
              have the same issue but they don't rely on useState selection
              that the user cares about. */}
          {key === "tq" && <_TQPlugin fx={fx} upd={upd} Knob={Knob} trackId={trackId} />}

          {/* Autotune plugin */}
          {key === "autotune" && <AutotunePlugin fx={fx} upd={upd} Knob={Knob} />}

          </>)}
        </div>
      );
    })}

    {/* ── Add Plugin button ── */}
    {chain.length < 8 && (
      <button onClick={function(e){ e.stopPropagation(); setShowPluginPicker(true); }}
        style={{
          display:"flex", alignItems:"center", justifyContent:"center", gap:8,
          width:"100%", padding:"12px",
          background:"rgba(139,92,246,0.05)",
          border:"1px solid rgba(139,92,246,0.2)",
          borderLeft:"3px solid rgba(139,92,246,0.6)",
          borderRadius:6, cursor:"pointer",
          transition:"background 0.12s",
        }}>
        <div style={{
          width:20, height:20, borderRadius:5,
          background:"rgba(139,92,246,0.2)",
          border:"1px solid rgba(139,92,246,0.3)",
          display:"flex", alignItems:"center", justifyContent:"center",
          fontSize:14, fontWeight:300, color:"#a855f7",
          flexShrink:0,
        }}>+</div>
        <span style={{ color:"#a855f7", fontWeight:600, fontSize:12, letterSpacing:0.3 }}>Add Plugin</span>
        <span style={{ color:"#2a2a2a", fontSize:10, marginLeft:"auto" }}>Effects</span>
      </button>
    )}

    {/* spacer */}
    <div style={{ height:8 }} />

  </div>
  );
}

// =============================================================================
// ── FX plugin sub-components (pure render functions, no hooks) ──
// Defined at top-level so they are stable references; FxPanel passes them
// down via FxPanelPlugins.
// ══════════════════════════════════════════════════════════════════════════
// 5-Band Parametric EQ — shared configuration & helpers (module scope so
// both the EQ plugin UI and the inner EQGraph/applyFxLive/audio chain
// build can access them).
// Defaults match the spec: Low Shelf 80Hz, Bell 250Hz, Bell 1kHz, Bell 4kHz,
// High Shelf 10kHz. Keys retained as hpf/low/mid/high/lpf for backward
// compatibility with existing track state. Type per band is overridable
// via eq.<key>Type — when unset, defaults below apply.
const EQ_BAND_CONFIGS = [
  { key:"hpf",  bandNum:1, defaultType:"lowshelf",  defaultFreq:80,    defaultQ:0.7, color:"#FF6B6B" },
  { key:"low",  bandNum:2, defaultType:"peaking",   defaultFreq:250,   defaultQ:1.0, color:"#4FC3F7" },
  { key:"mid",  bandNum:3, defaultType:"peaking",   defaultFreq:1000,  defaultQ:1.0, color:"#69F0AE" },
  { key:"high", bandNum:4, defaultType:"peaking",   defaultFreq:4000,  defaultQ:1.0, color:"#FFD54F" },
  { key:"lpf",  bandNum:5, defaultType:"highshelf", defaultFreq:10000, defaultQ:0.7, color:"#FF80AB" },
];
const EQ_TYPE_OPTIONS = ["peaking","lowshelf","highshelf","highpass","lowpass","notch"];
const EQ_TYPE_LABEL = {
  peaking:   "BELL",
  lowshelf:  "L-SH",
  highshelf: "H-SH",
  highpass:  "H-PASS",
  lowpass:   "L-PASS",
  notch:     "NOTCH",
};
// Resolve a band's effective parameters from the eq state object.
const eqResolveBand = function(eq, idx) {
  const cfg = EQ_BAND_CONFIGS[idx];
  return {
    key: cfg.key,
    bandNum: cfg.bandNum,
    color: cfg.color,
    type: (eq && eq[cfg.key + "Type"]) || cfg.defaultType,
    freq: (eq && eq[cfg.key + "Freq"]) || cfg.defaultFreq,
    gain: (eq && eq[cfg.key]) || 0,
    q:    (eq && eq[cfg.key + "Q"])    || cfg.defaultQ,
    enabled: !(eq && eq[cfg.key + "Off"]),
  };
};
// Phase 5: A/B compare — list of EQ params that swap between slots when
// the user toggles A/B. Meta-state (on, activeSlot, altSlot itself) is
// excluded; only audio parameters swap.
const EQ_SWAPPABLE_KEYS = (function(){
  const arr = [];
  EQ_BAND_CONFIGS.forEach(function(cfg){
    arr.push(cfg.key + "Freq", cfg.key, cfg.key + "Q", cfg.key + "Type", cfg.key + "Off");
  });
  arr.push("outputGain");
  return arr;
})();

// Memoized EQ plugin. Re-renders only when fx.eq changes — not when
// other plugins' state changes, not when isPlaying changes. Knob/EQGraph
// are recreated on every parent render but are pure functions, so
// ignoring them in the comparator is safe and gives back the win.
// eq5 is derived from fx.eq via spread (new reference every parent
// render), so we comparator-check fx.eq instead.
function _EQPluginInner({ fx, upd, eq5, EQGraph, Knob, analyserNode, isPlaying }) {
  const on = !!fx.eq?.on;
  // Phase 5: A/B compare. activeSlot defaults to "A". The "altSlot"
  // sub-object stores the inactive slot's values. Tapping the inactive
  // slot button swaps top-level params with altSlot.
  const activeSlot = (eq5 && eq5.activeSlot === "B") ? "B" : "A";
  const swapSlot = function(){
    const eq = eq5 || {};
    const current = {};
    for (let i = 0; i < EQ_SWAPPABLE_KEYS.length; i++) {
      const k = EQ_SWAPPABLE_KEYS[i];
      current[k] = eq[k];
    }
    // First tap: altSlot is undefined → clone current so both slots
    // start identical. User can then tweak the new slot from there.
    const alt = eq.altSlot || current;
    const patch = {};
    for (let i = 0; i < EQ_SWAPPABLE_KEYS.length; i++) {
      const k = EQ_SWAPPABLE_KEYS[i];
      patch[k] = alt[k];
    }
    patch.altSlot   = current;
    patch.activeSlot = activeSlot === "A" ? "B" : "A";
    upd("eq", patch);
  };
  const setSlot = function(target){
    if (target === activeSlot) return;
    swapSlot();
  };
  const copyToOtherSlot = function(){
    const eq = eq5 || {};
    const current = {};
    for (let i = 0; i < EQ_SWAPPABLE_KEYS.length; i++) {
      const k = EQ_SWAPPABLE_KEYS[i];
      current[k] = eq[k];
    }
    upd("eq", { altSlot: current });
  };
  return (
    <div style={{
      background:"linear-gradient(180deg,#0f1219 0%,#090c11 100%)",
      borderRadius:16, overflow:"hidden",
      border:"2px solid " + (on ? "#38bdf8" : "#1e2535"),
      boxShadow: on ? "0 0 28px rgba(56,189,248,0.12), inset 0 1px 0 rgba(255,255,255,0.04)" : "inset 0 1px 0 rgba(255,255,255,0.02)"
    }}>
      {/* ── Header ── */}
      <div style={{
        background:"linear-gradient(180deg,#131825 0%,#0f1219 100%)",
        padding:"9px 14px", borderBottom:"1px solid #1e2535",
        display:"flex", alignItems:"center", gap:8
      }}>
        <div style={{ display:"flex", alignItems:"center", gap:7, flex:1 }}>
          {/* Band color strip */}
          <div style={{ display:"flex", gap:2, alignItems:"center" }}>
            {EQ_BAND_CONFIGS.map(function(c,i){
              return <div key={i} style={{ width:3, height:16, borderRadius:1.5, background:c.color, opacity:0.9 }}/>;
            })}
          </div>
          <div>
            <div style={{ color:"#e8f0ff", fontWeight:900, fontSize:11, letterSpacing:3, fontFamily:"monospace", lineHeight:1 }}>PRO EQ</div>
            <div style={{ color:"#2a3a5a", fontSize:7, letterSpacing:2.5, fontFamily:"monospace", marginTop:1 }}>5-BAND PARAMETRIC · ±24dB</div>
          </div>
        </div>
        <div style={{ display:"flex", alignItems:"center", gap:6 }}>
          {/* Phase 5: A/B compare pill */}
          <div style={{ display:"flex", alignItems:"center", borderRadius:5, overflow:"hidden", border:"1px solid #3b2a5a", boxShadow:"0 1px 3px rgba(0,0,0,0.5)" }}>
            <button onClick={function(){ setSlot("A"); }} style={{
              background: activeSlot === "A" ? "linear-gradient(180deg,#a855f7,#7c3aed)" : "linear-gradient(180deg,#1e1a2a,#15121e)",
              border:"none", borderRight:"1px solid #3b2a5a",
              color: activeSlot === "A" ? "#fff" : "#6b5b8a",
              fontSize:10, fontWeight:900, padding:"4px 9px", cursor:"pointer", letterSpacing:1,
              fontFamily:"monospace",
              boxShadow: activeSlot === "A" ? "0 1px 0 rgba(255,255,255,0.18) inset" : "none"
            }}>A</button>
            <button onClick={function(){ setSlot("B"); }} style={{
              background: activeSlot === "B" ? "linear-gradient(180deg,#a855f7,#7c3aed)" : "linear-gradient(180deg,#1e1a2a,#15121e)",
              border:"none",
              color: activeSlot === "B" ? "#fff" : "#6b5b8a",
              fontSize:10, fontWeight:900, padding:"4px 9px", cursor:"pointer", letterSpacing:1,
              fontFamily:"monospace",
              boxShadow: activeSlot === "B" ? "0 1px 0 rgba(255,255,255,0.18) inset" : "none"
            }}>B</button>
          </div>
          {/* COPY: clone current slot to the other slot */}
          <button onClick={copyToOtherSlot} title={"Copy " + activeSlot + " to " + (activeSlot === "A" ? "B" : "A")} style={{
            background:"linear-gradient(180deg,#1e1a2a,#15121e)",
            border:"1px solid #3b2a5a",
            borderRadius:5, color:"#a78bfa", fontSize:8, fontWeight:900,
            padding:"4px 7px", cursor:"pointer", letterSpacing:0.5, fontFamily:"monospace",
            boxShadow:"0 1px 3px rgba(0,0,0,0.5)"
          }}>{activeSlot}→{activeSlot === "A" ? "B" : "A"}</button>
          {/* Power LED */}
          <div style={{
            width:8, height:8, borderRadius:"50%",
            background: on ? "#38bdf8" : "#111827",
            boxShadow: on ? "0 0 6px #38bdf8, 0 0 14px rgba(56,189,248,0.5)" : "none",
            transition:"all 0.2s"
          }}/>
          <button onClick={function(){ upd("eq",{on:!on}); }} style={{
            background: on ? "linear-gradient(180deg,#0ea5e9,#0284c7)" : "linear-gradient(180deg,#1e2535,#151c2a)",
            border:"1px solid " + (on ? "#38bdf8" : "#2a3350"),
            borderRadius:5, color:"white", fontSize:9, fontWeight:800,
            padding:"4px 12px", cursor:"pointer", letterSpacing:1,
            boxShadow: on ? "0 1px 0 rgba(255,255,255,0.12) inset" : "0 1px 3px rgba(0,0,0,0.6)"
          }}>{on ? "BYPASS" : "ENGAGE"}</button>
        </div>
      </div>

      {/* ── Graph + knobs ── */}
      <div style={{ padding:"12px 10px 10px", opacity:on?1:0.35, transition:"opacity 0.2s" }}>
        {/* Graph */}
        <div style={{
          background:"#070a10", borderRadius:10, padding:3,
          border:"1px solid #1a2035",
          boxShadow:"inset 0 2px 8px rgba(0,0,0,0.7), 0 0 0 1px rgba(56,189,248,0.04)"
        }}>
          <EQGraph eq={eq5} onDrag={function(patch){ upd("eq", patch); }} analyserNode={analyserNode} isPlaying={isPlaying} />
        </div>

        {/* ── Band knob strips ── */}
        <div style={{ display:"flex", marginTop:10, background:"#0a0e16", borderRadius:10, border:"1px solid #1a2035", overflow:"hidden" }}>
          {EQ_BAND_CONFIGS.map(function(cfg, bi){
            const r = eqResolveBand(eq5 || {}, bi);
            const isSoloed = (eq5 && eq5.soloBand === cfg.key);
            const isBypassed = !r.enabled;
            const cycleType = function(){
              const curIdx = EQ_TYPE_OPTIONS.indexOf(r.type);
              const nextIdx = (curIdx + 1) % EQ_TYPE_OPTIONS.length;
              const patch = {}; patch[cfg.key + "Type"] = EQ_TYPE_OPTIONS[nextIdx];
              upd("eq", patch);
            };
            const toggleSolo = function(){
              upd("eq", { soloBand: isSoloed ? null : cfg.key });
            };
            const toggleBypass = function(){
              const patch = {};
              patch[cfg.key + "Off"] = !isBypassed;
              upd("eq", patch);
            };
            return (
              <div key={cfg.key} style={{
                flex:1, borderRight: "1px solid #151c2a",
                display:"flex", flexDirection:"column", alignItems:"center", padding:"6px 2px 8px",
                opacity: isBypassed ? 0.55 : 1, transition:"opacity 0.15s"
              }}>
                {/* Band TYPE selector — tap to cycle through types */}
                <button
                  onClick={cycleType}
                  style={{
                    width:"95%",
                    background:"linear-gradient(180deg," + cfg.color + "22," + cfg.color + "0a)",
                    border:"1px solid " + cfg.color + "55",
                    borderRadius:4,
                    padding:"3px 0", textAlign:"center", marginBottom:4,
                    cursor:"pointer",
                    color: cfg.color, fontSize:7, fontWeight:900, letterSpacing:0.5, fontFamily:"monospace"
                  }}>
                  B{r.bandNum} · {EQ_TYPE_LABEL[r.type]}
                </button>
                {/* Phase 6/7: SOLO + BYPASS button pair */}
                <div style={{ width:"95%", display:"flex", gap:2, marginBottom:4 }}>
                  <button
                    onClick={toggleSolo}
                    style={{
                      flex:1,
                      background: isSoloed ? "linear-gradient(180deg,#fbbf24,#d97706)" : "linear-gradient(180deg,#0f1219,#0a0d14)",
                      border:"1px solid " + (isSoloed ? "#fbbf24" : "#1e2535"),
                      borderRadius:3,
                      padding:"2px 0", textAlign:"center",
                      cursor:"pointer",
                      color: isSoloed ? "#0b0d12" : "#3a4055",
                      fontSize:6, fontWeight:900, letterSpacing:0.5, fontFamily:"monospace",
                      boxShadow: isSoloed ? "0 0 6px rgba(251,191,36,0.5)" : "none"
                    }}>
                    SOLO
                  </button>
                  <button
                    onClick={toggleBypass}
                    style={{
                      flex:1,
                      background: isBypassed ? "linear-gradient(180deg,#6b7280,#4b5563)" : "linear-gradient(180deg,#0f1219,#0a0d14)",
                      border:"1px solid " + (isBypassed ? "#9ca3af" : "#1e2535"),
                      borderRadius:3,
                      padding:"2px 0", textAlign:"center",
                      cursor:"pointer",
                      color: isBypassed ? "#fff" : "#3a4055",
                      fontSize:6, fontWeight:900, letterSpacing:0.5, fontFamily:"monospace",
                      boxShadow: isBypassed ? "0 0 6px rgba(156,163,175,0.4)" : "none"
                    }}>
                    BYP
                  </button>
                </div>
                <div style={{ display:"flex", flexDirection:"column", alignItems:"center", gap:4, overflow:"visible" }}>
                  <Knob label="FREQ" value={r.freq} min={20} max={20000} step={1} unit="Hz" color={cfg.color} scale="log"
                    onChange={function(v){ const p={}; p[cfg.key+"Freq"]=v; upd("eq", p); }}/>
                  <Knob label="GAIN" value={r.gain} min={-24} max={24} step={0.5} unit="dB" color={cfg.color}
                    onChange={function(v){ const p={}; p[cfg.key]=v; upd("eq", p); }}/>
                  <Knob label="Q" value={r.q} min={0.1} max={20} step={0.01} unit="" color={cfg.color} scale="log"
                    onChange={function(v){ const p={}; p[cfg.key+"Q"]=v; upd("eq", p); }}/>
                </div>
              </div>
            );
          })}
          {/* Phase 4: OUTPUT GAIN — post-EQ trim. Critical for level-matched
              comparison and compensating for boost/cut-induced loudness shifts. */}
          {(function(){
            const outputGain = (typeof (eq5||{}).outputGain === "number") ? eq5.outputGain : 0;
            const accent = "#e2e8f0";
            return (
              <div key="output" style={{
                flex:"0 0 56px", borderLeft:"2px solid #2a3350",
                background:"linear-gradient(180deg,#0d1320 0%,#080b14 100%)",
                display:"flex", flexDirection:"column", alignItems:"center",
                justifyContent:"flex-start", padding:"6px 2px 8px"
              }}>
                <div style={{
                  width:"95%",
                  background:"linear-gradient(180deg,#1e293b,#0f172a)",
                  border:"1px solid #334155",
                  borderRadius:4,
                  padding:"3px 0", textAlign:"center", marginBottom:4,
                  color: accent, fontSize:7, fontWeight:900, letterSpacing:0.5, fontFamily:"monospace"
                }}>
                  OUT
                </div>
                <div style={{ display:"flex", flexDirection:"column", alignItems:"center", gap:4, overflow:"visible", marginTop:"auto", marginBottom:"auto" }}>
                  <Knob label="GAIN" value={outputGain} min={-24} max={24} step={0.5} unit="dB" color={accent}
                    onChange={function(v){ upd("eq", { outputGain: v }); }}/>
                </div>
              </div>
            );
          })()}
        </div>
      </div>
    </div>
  );
}
const _EQPlugin = React.memo(_EQPluginInner, function(prev, next) {
  if (prev.upd !== next.upd) return false;
  if (prev.analyserNode !== next.analyserNode) return false;
  if (prev.isPlaying !== next.isPlaying) return false;
  const a = prev.fx ? prev.fx.eq : null;
  const b = next.fx ? next.fx.eq : null;
  if (a === b) return true;
  if (!a || !b) return false;
  // Field-level compare. The parent rebuilds the effects object on every
  // tracks-array update (e.g., audio analyser ticks), even when values
  // didn't change. Reference compare would fail spuriously and re-render
  // during drags — which blocks iOS from dispatching touch events.
  if (a.on !== b.on) return false;
  if (a.activeSlot !== b.activeSlot) return false;
  if (a.soloBand !== b.soloBand) return false;
  if (a.outputGain !== b.outputGain) return false;
  const keys = ["hpf", "low", "mid", "high", "lpf"];
  for (let i = 0; i < keys.length; i++) {
    const k = keys[i];
    if (a[k]            !== b[k])            return false;
    if (a[k + "Freq"]   !== b[k + "Freq"])   return false;
    if (a[k + "Q"]      !== b[k + "Q"])      return false;
    if (a[k + "Type"]   !== b[k + "Type"])   return false;
    if (a[k + "Off"]    !== b[k + "Off"])    return false;
  }
  return true;
});

function _CompPluginInner({ fx, upd, CompGraph, Knob }) {
  return (
    <div style={{ background:"linear-gradient(180deg,#1c1a22 0%,#130f1a 100%)", borderRadius:16, overflow:"hidden", border:"2px solid " + (fx.compressor?.on ? "#8B5CF6" : "#2a2a2a"), boxShadow: fx.compressor?.on ? "0 0 20px rgba(139,92,246,0.15), inset 0 1px 0 rgba(255,255,255,0.06)" : "inset 0 1px 0 rgba(255,255,255,0.03)" }}>
      <div style={{ background:"linear-gradient(180deg,#1e1c25 0%,#181620 100%)", padding:"8px 14px", borderBottom:"1px solid #2a2535", display:"flex", alignItems:"center", gap:8 }}>
        <div style={{ flex:1 }}>
          <div style={{ color:"#c4b5fd", fontWeight:900, fontSize:11, letterSpacing:3, fontFamily:"monospace", lineHeight:1 }}>COMPRESSOR</div>
          <div style={{ color:"#4a3f5c", fontSize:7, letterSpacing:2, fontFamily:"monospace" }}>DYNAMICS PROCESSOR</div>
        </div>
        <div style={{ display:"flex", alignItems:"center", gap:8 }}>
          <div style={{ display:"flex", gap:1.5, alignItems:"flex-end" }}>
            {[0,1,2,3,4].map(function(i){ const active = fx.compressor?.on; const colors = ["#22C55E","#22C55E","#F59E0B","#EF4444","#EF4444"]; return <div key={i} style={{ width:3, height: 6 + i * 2, borderRadius:1, background: active ? colors[i] : "#1e1e1e", boxShadow: active ? "0 0 4px " + colors[i] + "88" : "none", transition:"all 0.15s" }} />; })}
          </div>
          <div style={{ width:8, height:8, borderRadius:"50%", background: fx.compressor?.on ? "#8B5CF6" : "#1a1a1a", boxShadow: fx.compressor?.on ? "0 0 6px #8B5CF6, 0 0 12px rgba(139,92,246,0.5)" : "none", transition:"all 0.2s" }} />
          <button onClick={function(){ upd("compressor",{on:!fx.compressor?.on}); }} style={{ background: fx.compressor?.on ? "linear-gradient(180deg,#7C3AED,#6d28d9)" : "linear-gradient(180deg,#2a2a2a,#222)", border:"1px solid " + (fx.compressor?.on ? "#8B5CF6" : "#333"), borderRadius:5, color:"white", fontSize:9, fontWeight:800, padding:"4px 12px", cursor:"pointer", letterSpacing:1, boxShadow: fx.compressor?.on ? "0 1px 0 rgba(255,255,255,0.1) inset" : "0 1px 3px rgba(0,0,0,0.5)" }}>{fx.compressor?.on ? "ON" : "OFF"}</button>
        </div>
      </div>
      <div style={{ padding:"12px 14px", opacity:fx.compressor?.on?1:0.4, transition:"opacity 0.2s" }}>
        <div style={{ display:"flex", gap:10, alignItems:"flex-start" }}>
          <div style={{ background:"#050505", borderRadius:8, padding:2, border:"1px solid #1a1a1a", boxShadow:"inset 0 2px 6px rgba(0,0,0,0.8)", flexShrink:0 }}>
            <CompGraph threshold={fx.compressor?.threshold??-24} ratio={fx.compressor?.ratio??4} />
          </div>
          <div style={{ flex:1, display:"flex", flexDirection:"column", gap:8, overflow:"visible" }}>
            <div style={{ display:"flex", justifyContent:"space-around", overflow:"visible" }}>
              <Knob label="THRESH" value={fx.compressor?.threshold??-24} min={-60} max={0} step={1} unit="dB" color="#8B5CF6" onChange={function(v){ upd("compressor",{threshold:v}); }} />
              <Knob label="RATIO" value={fx.compressor?.ratio??4} min={1} max={20} step={0.5} unit=":1" color="#8B5CF6" onChange={function(v){ upd("compressor",{ratio:v}); }} />
            </div>
            <div style={{ height:1, background:"#1e1e1e", borderRadius:1 }} />
            <div style={{ display:"flex", justifyContent:"space-around", overflow:"visible" }}>
              <Knob label="ATTACK" value={Math.round((fx.compressor?.attack??0.003)*1000)} min={1} max={200} step={1} unit="ms" color="#a78bfa" onChange={function(v){ upd("compressor",{attack:v/1000}); }} />
              <Knob label="RELEASE" value={Math.round((fx.compressor?.release??0.25)*1000)} min={10} max={2000} step={10} unit="ms" color="#a78bfa" onChange={function(v){ upd("compressor",{release:v/1000}); }} />
              <Knob label="MAKEUP" value={fx.compressor?.makeupGain??0} min={0} max={24} step={0.5} unit="dB" color="#22C55E" onChange={function(v){ upd("compressor",{makeupGain:v}); }} />
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}
const _CompPlugin = React.memo(_CompPluginInner, function(prev, next) {
  return prev.fx.compressor === next.fx.compressor && prev.upd === next.upd;
});

function _ReverbPluginInner({ fx, upd, ReverbViz, Knob }) {
  return (
    <div style={{ background:"linear-gradient(180deg,#1a1220 0%,#110d19 100%)", borderRadius:16, overflow:"hidden", border:"2px solid " + (fx.reverb?.on ? "#C026D3" : "#2a2a2a"), boxShadow: fx.reverb?.on ? "0 0 20px rgba(192,38,211,0.15), inset 0 1px 0 rgba(255,255,255,0.06)" : "inset 0 1px 0 rgba(255,255,255,0.03)" }}>
      <div style={{ backgroundImage:"linear-gradient(180deg,#1e1629,#17101e)", padding:"8px 14px", borderBottom:"1px solid #2a1e35", display:"flex", alignItems:"center", gap:8 }}>
        <div style={{ flex:1 }}>
          <div style={{ color:"#e879f9", fontWeight:900, fontSize:11, letterSpacing:3, fontFamily:"monospace", lineHeight:1 }}>CONVOLUTION REVERB</div>
          <div style={{ color:"#4a2f55", fontSize:7, letterSpacing:2, fontFamily:"monospace" }}>ROOM SIMULATION</div>
        </div>
        <div style={{ display:"flex", alignItems:"center", gap:6 }}>
          <div style={{ width:8, height:8, borderRadius:"50%", background: fx.reverb?.on ? "#C026D3" : "#1a1a1a", boxShadow: fx.reverb?.on ? "0 0 6px #C026D3, 0 0 14px rgba(192,38,211,0.5)" : "none", transition:"all 0.2s" }} />
          <button onClick={function(){ upd("reverb",{on:!fx.reverb?.on}); }} style={{ background: fx.reverb?.on ? "linear-gradient(180deg,#be185d,#9d174d)" : "linear-gradient(180deg,#2a2a2a,#222)", border:"1px solid " + (fx.reverb?.on ? "#C026D3" : "#333"), borderRadius:5, color:"white", fontSize:9, fontWeight:800, padding:"4px 12px", cursor:"pointer", letterSpacing:1, boxShadow: fx.reverb?.on ? "0 1px 0 rgba(255,255,255,0.1) inset" : "0 1px 3px rgba(0,0,0,0.5)" }}>{fx.reverb?.on ? "ON" : "OFF"}</button>
        </div>
      </div>
      <div style={{ padding:"12px 14px", opacity:fx.reverb?.on?1:0.4, transition:"opacity 0.2s" }}>
        <div style={{ display:"flex", gap:10, alignItems:"center" }}>
          <div style={{ background:"#050505", borderRadius:8, padding:2, border:"1px solid #1a1a1a", boxShadow:"inset 0 2px 6px rgba(0,0,0,0.8)", flexShrink:0 }}>
            <ReverbViz wet={fx.reverb?.wet??0.25} roomSize={fx.reverb?.roomSize??0.8} />
          </div>
          <div style={{ flex:1, display:"flex", justifyContent:"space-around", overflow:"visible", padding:"4px 0" }}>
            <Knob label="WET" value={fx.reverb?.wet??0.25} min={0} max={1} step={0.01} unit="%" color="#C026D3" onChange={function(v){ upd("reverb",{wet:v}); }} />
            <Knob label="ROOM" value={fx.reverb?.roomSize??0.8} min={0.1} max={1} step={0.01} unit="%" color="#C026D3" onChange={function(v){ upd("reverb",{roomSize:v}); }} />
            <Knob label="PRE-DLY" value={fx.reverb?.preDelay??0} min={0} max={100} step={1} unit="ms" color="#8B5CF6" onChange={function(v){ upd("reverb",{preDelay:v}); }} />
          </div>
        </div>
      </div>
    </div>
  );
}
const _ReverbPlugin = React.memo(_ReverbPluginInner, function(prev, next) {
  return prev.fx.reverb === next.fx.reverb && prev.upd === next.upd;
});

function _OceanPluginInner({ fx, upd, Knob }) {
  const on = !!fx.ocean?.on;
  return (
    <div style={{ background:"linear-gradient(180deg,#0a1628 0%,#061020 100%)", borderRadius:16, overflow:"hidden", border:"2px solid " + (on ? "#0891b2" : "#2a2a2a"), boxShadow: on ? "0 0 20px rgba(8,145,178,0.2), inset 0 1px 0 rgba(255,255,255,0.06)" : "inset 0 1px 0 rgba(255,255,255,0.03)" }}>
      <div style={{ backgroundImage:"linear-gradient(180deg,#0f2035,#091828)", padding:"8px 14px", borderBottom:"1px solid #0e2a3a", display:"flex", alignItems:"center", gap:8 }}>
        <div style={{ flex:1 }}>
          <div style={{ color:"#38bdf8", fontWeight:900, fontSize:11, letterSpacing:3, fontFamily:"monospace", lineHeight:1 }}>OCEAN REVERB</div>
          <div style={{ color:"#0c3347", fontSize:7, letterSpacing:2, fontFamily:"monospace" }}>DEEP · LUSH · ATMOSPHERIC</div>
        </div>
        <div style={{ display:"flex", alignItems:"center", gap:6 }}>
          <div style={{ width:8, height:8, borderRadius:"50%", background: on ? "#0891b2" : "#1a1a1a", boxShadow: on ? "0 0 6px #0891b2, 0 0 14px rgba(8,145,178,0.5)" : "none", transition:"all 0.2s" }} />
          <button onClick={function(){ upd("ocean",{on:!on}); }} style={{ background: on ? "linear-gradient(180deg,#0369a1,#075985)" : "linear-gradient(180deg,#2a2a2a,#222)", border:"1px solid " + (on ? "#0891b2" : "#333"), borderRadius:5, color:"white", fontSize:9, fontWeight:800, padding:"4px 12px", cursor:"pointer", letterSpacing:1 }}>{on ? "ON" : "OFF"}</button>
        </div>
      </div>
      <div style={{ padding:"12px 14px", opacity:on?1:0.4, transition:"opacity 0.2s" }}>
        <div style={{ display:"flex", justifyContent:"space-around", padding:"4px 0" }}>
          <Knob label="WET"    value={fx.ocean?.wet??0.35}      min={0} max={1}   step={0.01} unit="%" color="#0891b2" onChange={function(v){ upd("ocean",{wet:v}); }} />
          <Knob label="SIZE"   value={fx.ocean?.roomSize??1.0}  min={0.5} max={2} step={0.01} unit="%" color="#0891b2" onChange={function(v){ upd("ocean",{roomSize:v}); }} />
          <Knob label="DAMP"   value={fx.ocean?.damp??0.6}      min={0} max={1}   step={0.01} unit="%" color="#38bdf8" onChange={function(v){ upd("ocean",{damp:v}); }} />
          <Knob label="PRE-DLY" value={fx.ocean?.preDelay??20}  min={0} max={150} step={1}    unit="ms" color="#38bdf8" onChange={function(v){ upd("ocean",{preDelay:v}); }} />
        </div>
      </div>
    </div>
  );
}
const _OceanPlugin = React.memo(_OceanPluginInner, function(prev, next) {
  return prev.fx.ocean === next.fx.ocean && prev.upd === next.upd;
});

var _tkc = 0; // unique counter for TKnob SVG gradient IDs — must be declared before use
function TKnob({ label, value, min, max, step, unit, onChange, size, color }) {
  const sz     = size || 44;
  const r      = sz / 2 - 4;
  const cx     = sz / 2, cy = sz / 2;
  const norm   = Math.max(0, Math.min(1, (value - min) / (max - min)));
  const START  = -135, SWEEP = 270;
  const angle  = START + norm * SWEEP;
  const rad    = function(deg){ return (deg - 90) * Math.PI / 180; };
  const pt     = function(deg, rr){ return { x: cx + rr * Math.cos(rad(deg)), y: cy + rr * Math.sin(rad(deg)) }; };
  const arcS   = pt(START, r);
  const arcE   = pt(angle, r);
  const arcFE  = pt(START + SWEEP, r);
  const swept  = angle - START;
  const large  = swept > 180 ? 1 : 0;
  const arcPath = `M ${arcS.x.toFixed(2)} ${arcS.y.toFixed(2)} A ${r} ${r} 0 ${large} 1 ${arcE.x.toFixed(2)} ${arcE.y.toFixed(2)}`;
  const ptrTip = pt(angle, r - 5);
  const ptrBase= pt(angle, 4);
  const accent = color || "#c8762a";

  // Stable unique gradient ID — assigned once at mount
  const uidRef  = React.useRef(null);
  if (!uidRef.current) { uidRef.current = "tkg" + (++_tkc); }
  const uid = uidRef.current;

  // dragRef holds { startY, startVal, pointerId } reset each pointerdown.
  // We read the LATEST value from valueRef (not the stale closure) as the baseline.
  const valueRef   = React.useRef(value);
  valueRef.current = value;
  const dragRef    = React.useRef(null);
  const svgRef     = React.useRef(null);
  const onChangeRef = React.useRef(onChange);
  onChangeRef.current = onChange;

  // Store max/min/step in refs so imperative handlers always read latest values
  const maxRef  = React.useRef(max);  maxRef.current  = max;
  const minRef  = React.useRef(min);  minRef.current  = min;
  const stepRef = React.useRef(step); stepRef.current = step;

  // Imperative pointer handlers attached with {passive:false} so preventDefault()
  // actually suppresses iOS scroll during knob drag. React synthetic events can't
  // set passive:false reliably in all iOS Safari versions.
  React.useEffect(function() {
    const el = svgRef.current;
    if (!el) return;

    function handleDown(e) {
      e.preventDefault();
      // setPointerCapture on SVG can be unreliable on iOS — capture on document instead
      dragRef.current = { startY: e.clientY, startVal: valueRef.current, pointerId: e.pointerId };
      try { el.setPointerCapture(e.pointerId); } catch(_) {}

      function handleMove(ev) {
        if (!dragRef.current) return;
        if (ev.pointerId !== undefined && ev.pointerId !== dragRef.current.pointerId) return;
        ev.preventDefault();
        const dy      = dragRef.current.startY - ev.clientY; // drag UP = positive
        const sens    = (maxRef.current - minRef.current) / 200; // 200px = full sweep
        const raw     = dragRef.current.startVal + dy * sens;
        const clamped = Math.min(maxRef.current, Math.max(minRef.current, raw));
        const snapped = stepRef.current ? Math.round(clamped / stepRef.current) * stepRef.current : clamped;
        onChangeRef.current(+snapped.toFixed(6));
      }
      function handleUp(ev) {
        if (ev.pointerId !== undefined && ev.pointerId !== dragRef.current?.pointerId) return;
        dragRef.current = null;
        document.removeEventListener("pointermove", handleMove);
        document.removeEventListener("pointerup",   handleUp);
        document.removeEventListener("pointercancel", handleUp);
      }

      // Attach move/up to document so fast drags that leave the SVG still work
      document.addEventListener("pointermove",   handleMove,  { passive: false });
      document.addEventListener("pointerup",     handleUp,    { passive: false });
      document.addEventListener("pointercancel", handleUp,    { passive: false });
    }

    el.addEventListener("pointerdown", handleDown, { passive: false });
    return function() {
      el.removeEventListener("pointerdown", handleDown);
    };
  }, []); // mount/unmount only — all live values go through refs

  const fmt = function(v) {
    if (unit === "dB" || unit === "dBFS") return (v >= 0 ? "+" : "") + v.toFixed(1);
    if (unit === "s")  return v.toFixed(2) + "s";
    if (unit === "%")  return Math.round(v) + "%";
    if (unit === ":1") return v.toFixed(1) + ":1";
    return typeof v === "number" ? (Number.isInteger(v) ? v : v.toFixed(1)) : v;
  };

  return (
    <div style={{ display:"flex", flexDirection:"column", alignItems:"center", gap:2, userSelect:"none" }}>
      <svg ref={svgRef} width={sz} height={sz}
        style={{ cursor:"ns-resize", touchAction:"none", overflow:"visible", WebkitUserSelect:"none", userSelect:"none" }}
      >
        <circle cx={cx} cy={cy} r={r+3} fill="none" stroke="#000" strokeWidth={2} opacity={0.6}/>
        {norm > 0.005 && <path d={arcPath} fill="none" stroke={accent} strokeWidth={3} strokeLinecap="round" style={{filter:`drop-shadow(0 0 3px ${accent}88)`}}/>}
        <defs>
          <radialGradient id={uid} cx="36%" cy="30%" r="70%">
            <stop offset="0%"   stopColor="#3c3228"/>
            <stop offset="40%"  stopColor="#221c12"/>
            <stop offset="100%" stopColor="#0c0900"/>
          </radialGradient>
          <radialGradient id={uid+"h"} cx="35%" cy="28%" r="55%">
            <stop offset="0%"   stopColor="#ffffff" stopOpacity={0.06}/>
            <stop offset="100%" stopColor="#ffffff" stopOpacity={0}/>
          </radialGradient>
        </defs>
        <circle cx={cx} cy={cy} r={r-1} fill={`url(#${uid})`}  stroke="#3a2a14" strokeWidth={1.2}/>
        <circle cx={cx} cy={cy} r={r-1} fill={`url(#${uid}h)`}/>
        <line x1={ptrBase.x.toFixed(2)} y1={ptrBase.y.toFixed(2)}
              x2={ptrTip.x.toFixed(2)}  y2={ptrTip.y.toFixed(2)}
              stroke="#f0d080" strokeWidth={2} strokeLinecap="round"/>
      </svg>
      <div style={{ color:"#d4a04a", fontSize:8, fontWeight:900, fontFamily:"monospace", lineHeight:1 }}>
        {fmt(value)}{unit && !["dB","dBFS","s","%",":1"].includes(unit) ? " "+unit : ""}
      </div>
      {label && (
        <div style={{ color:"#6a5030", fontSize:6.5, fontWeight:700, fontFamily:"monospace", letterSpacing:0.8, textAlign:"center", lineHeight:1.2 }}>
          {label}
        </div>
      )}
    </div>
  );
}

// ── T-Rotten Master Plugin ───────────────────────────────────────────────────
// Pixel-faithful replica of the T-Rotten Master UI:
// 4 sections (EQ / Compressor / Tape+Sat / Limiter), VU meters, analog knobs,
// mode buttons, logo skull, INPUT/OUTPUT knobs, LUFS readout, power button.
// =============================================================================
// ── GRM Bandpass Filter — emulates GRM Tools BandPass ──────────────────────
// Dual cascaded biquad bandpass (6th-order slope) with resonance, mix, and
// a real-time frequency-domain visualiser drawn on a canvas.
// Parameters:
//   center  — centre frequency  20–20000 Hz  (default 1000)
//   width   — bandwidth in octaves  0.05–6   (default 1.0)
//   res     — resonance boost dB  0–18       (default 0)
//   mix     — wet/dry  0–1                   (default 1)
//   on      — bypass toggle
// =============================================================================
function _BandpassPluginInner({ fx, upd, Knob }) {
  const bp    = fx.bandpass || {};
  const on    = !!bp.on;
  const center = bp.center ?? 1000;
  const width  = bp.width  ?? 1.0;
  const res    = bp.res    ?? 0;
  const mix    = bp.mix    ?? 1;

  // Derive lo/hi from center + width (octaves)
  const loHz = center / Math.pow(2, width / 2);
  const hiHz = center * Math.pow(2, width / 2);

  const ACCENT = "#00e5ff";
  const canvasRef = React.useRef(null);

  // Draw frequency response curve on canvas
  React.useEffect(function() {
    const canvas = canvasRef.current;
    if (!canvas) return;
    const W = canvas.width; const H = canvas.height;
    const ctx = canvas.getContext("2d");
    ctx.clearRect(0, 0, W, H);

    // Background grid
    ctx.strokeStyle = "rgba(255,255,255,0.04)";
    ctx.lineWidth = 1;
    [20,100,200,500,1000,2000,5000,10000,20000].forEach(function(f) {
      const x = Math.log10(f/20) / Math.log10(20000/20) * W;
      ctx.beginPath(); ctx.moveTo(x,0); ctx.lineTo(x,H); ctx.stroke();
    });
    [-24,-18,-12,-6,0].forEach(function(db) {
      const y = H - (db + 30) / 36 * H;
      ctx.beginPath(); ctx.moveTo(0,y); ctx.lineTo(W,y); ctx.stroke();
    });

    // Compute magnitude response of 2× cascaded bandpass biquad
    // Using bilinear transform of RLC bandpass filter
    const SR = 48000;
    function bpMag(freqHz) {
      // Q from octave width: Q = sqrt(2)^width / (2^width - 1)  (classic formula)
      const Q  = Math.sqrt(Math.pow(2, width)) / (Math.pow(2, width) - 1);
      const Qr = Q * (1 + res / 6);  // resonance boosts Q
      const w0 = 2 * Math.PI * center / SR;
      const alpha = Math.sin(w0) / (2 * Qr);
      const b0 =  alpha;
      const b1 =  0;
      const b2 = -alpha;
      const a0 =  1 + alpha;
      const a1 = -2 * Math.cos(w0);
      const a2 =  1 - alpha;
      // Eval H(e^jw) at freqHz
      const w   = 2 * Math.PI * freqHz / SR;
      const cosw = Math.cos(w); const sinw = Math.sin(w);
      const cos2w = Math.cos(2*w); const sin2w = Math.sin(2*w);
      const numRe = b0 + b1*cosw + b2*cos2w;
      const numIm =    - b1*sinw - b2*sin2w;
      const denRe = a0 + a1*cosw + a2*cos2w;
      const denIm =    - a1*sinw - a2*sin2w;
      const mag2  = (numRe*numRe + numIm*numIm) / (denRe*denRe + denIm*denIm);
      // 2× cascaded = magnitude squared
      return Math.sqrt(mag2 * mag2);
    }

    // Draw curve
    const grad = ctx.createLinearGradient(0,0,0,H);
    grad.addColorStop(0, on ? "rgba(0,229,255,0.9)" : "rgba(80,80,80,0.6)");
    grad.addColorStop(1, on ? "rgba(0,229,255,0.2)" : "rgba(40,40,40,0.2)");
    ctx.beginPath();
    for (let px = 0; px <= W; px++) {
      const f   = 20 * Math.pow(20000/20, px/W);
      const mag = bpMag(f);
      const db  = 20 * Math.log10(Math.max(1e-6, mag));
      const y   = H - (db + 30) / 36 * H;
      if (px === 0) ctx.moveTo(px, Math.min(H, Math.max(0, y)));
      else          ctx.lineTo(px, Math.min(H, Math.max(0, y)));
    }
    // Fill under curve
    const fillX = Math.log10(center/20)/Math.log10(20000/20)*W;
    ctx.lineTo(W, H); ctx.lineTo(0, H); ctx.closePath();
    ctx.fillStyle = on ? "rgba(0,229,255,0.08)" : "rgba(60,60,60,0.06)";
    ctx.fill();
    // Stroke
    ctx.beginPath();
    for (let px = 0; px <= W; px++) {
      const f   = 20 * Math.pow(20000/20, px/W);
      const mag = bpMag(f);
      const db  = 20 * Math.log10(Math.max(1e-6, mag));
      const y   = H - (db + 30) / 36 * H;
      if (px === 0) ctx.moveTo(px, Math.min(H, Math.max(0, y)));
      else          ctx.lineTo(px, Math.min(H, Math.max(0, y)));
    }
    ctx.strokeStyle = on ? ACCENT : "#444";
    ctx.lineWidth = 2;
    ctx.shadowBlur = on ? 8 : 0;
    ctx.shadowColor = ACCENT;
    ctx.stroke();
    ctx.shadowBlur = 0;

    // Center frequency marker
    const cx = Math.log10(center/20)/Math.log10(20000/20)*W;
    ctx.strokeStyle = on ? "rgba(0,229,255,0.5)" : "rgba(100,100,100,0.3)";
    ctx.lineWidth = 1;
    ctx.setLineDash([3,3]);
    ctx.beginPath(); ctx.moveTo(cx,0); ctx.lineTo(cx,H); ctx.stroke();
    ctx.setLineDash([]);

  }, [on, center, width, res, mix]);

  // Format Hz label
  // Log-scale knob for CENTER — maps 20–8000 Hz logarithmically so each
  // degree of rotation gives consistent musical resolution across the range.
  const MIN_HZ = 20, MAX_HZ = 8000;
  const logMin = Math.log10(MIN_HZ), logMax = Math.log10(MAX_HZ);

  function LogKnob({ value, color, onChange }) {
    const startRef = React.useRef(null);
    // Normalise current value in log space → 0..1
    const norm  = (Math.log10(Math.max(MIN_HZ, Math.min(MAX_HZ, value))) - logMin) / (logMax - logMin);
    const angle = -140 + norm * 280;
    const SW = 4, r = 20, PAD = SW / 2 + 2;
    const cx = r + PAD, cy = r + PAD, SIZE = (r + PAD) * 2;
    const toXY = function(deg) {
      const rad = (deg - 90) * Math.PI / 180;
      return { x: cx + r * Math.cos(rad), y: cy + r * Math.sin(rad) };
    };
    const startA = toXY(-140), endA = toXY(angle);
    const sweptDeg = angle - (-140), largeArc = sweptDeg > 180 ? 1 : 0;
    const arcD = "M " + startA.x.toFixed(2) + " " + startA.y.toFixed(2) +
                 " A " + r + " " + r + " 0 " + largeArc + " 1 " +
                 endA.x.toFixed(2) + " " + endA.y.toFixed(2);

    const onPointerDown = function(e) {
      e.preventDefault();
      const startLog = Math.log10(Math.max(MIN_HZ, Math.min(MAX_HZ, value)));
      startRef.current = { y: e.clientY, log: startLog };
      const onMove = function(me) {
        const dy     = startRef.current.y - me.clientY; // up = higher freq
        // 100px drag = full log range — same physical feel as other knobs
        const newLog = Math.max(logMin, Math.min(logMax,
          startRef.current.log + (dy / 100) * (logMax - logMin)));
        onChange(Math.max(MIN_HZ, Math.min(MAX_HZ, Math.round(Math.pow(10, newLog)))));
      };
      const onUp = function() {
        document.removeEventListener("pointermove", onMove);
        document.removeEventListener("pointerup",   onUp);
      };
      document.addEventListener("pointermove", onMove);
      document.addEventListener("pointerup",   onUp);
    };

    return (
      <div style={{ display:"flex", flexDirection:"column", alignItems:"center", gap:2, userSelect:"none" }}>
        <svg width={SIZE} height={SIZE} viewBox={"0 0 " + SIZE + " " + SIZE}
          style={{ overflow:"visible", cursor:"ns-resize", touchAction:"none" }}
          onPointerDown={onPointerDown}>
          <circle cx={cx} cy={cy} r={r} fill="none" stroke="#1e1e1e" strokeWidth={SW} strokeLinecap="round"
            strokeDasharray={(2*Math.PI*r * 280/360) + " " + (2*Math.PI*r)}
            strokeDashoffset={(2*Math.PI*r * (90+140)/360)}
            transform={"rotate(-90 " + cx + " " + cy + ")"} />
          {norm > 0 && <path d={arcD} fill="none" stroke={color} strokeWidth={SW} strokeLinecap="round" />}
          <circle cx={endA.x} cy={endA.y} r={SW * 0.9} fill={color} />
          <circle cx={cx} cy={cy} r={r * 0.42} fill="#0d0d0d" stroke="#2a2a2a" strokeWidth={1.5} />
        </svg>
        <div style={{ color:"white", fontSize:9, fontWeight:700, lineHeight:1 }}>
          {value >= 1000 ? (value/1000).toFixed(value >= 10000 ? 0 : 1)+"k" : value} Hz
        </div>
        <div style={{ color:"#444", fontSize:7, textAlign:"center", lineHeight:1.2 }}>CENTER</div>
      </div>
    );
  }


  function fmtHz(f) {
    return f >= 1000 ? (f/1000).toFixed(f>=10000?0:1)+"k" : Math.round(f)+"";
  }

  return (
    <div style={{
      background: "linear-gradient(180deg,#0a1a1e 0%,#060e11 100%)",
      borderRadius: 16, overflow: "hidden",
      border: "2px solid " + (on ? ACCENT : "#1e1e1e"),
      boxShadow: on ? "0 0 24px rgba(0,229,255,0.12), inset 0 1px 0 rgba(255,255,255,0.06)" : "inset 0 1px 0 rgba(255,255,255,0.03)",
    }}>
      {/* Header */}
      <div style={{ display:"flex", alignItems:"center", gap:8, padding:"10px 14px 8px", borderBottom:"1px solid #0d1c20" }}>
        <div style={{
          width:28, height:28, borderRadius:7, flexShrink:0,
          background: on ? "linear-gradient(135deg,#00b4cc,#006680)" : "#1a1a1a",
          display:"flex", alignItems:"center", justifyContent:"center",
          fontSize:14, boxShadow: on ? "0 0 10px rgba(0,229,255,0.4)" : "none",
          transition:"all 0.2s",
        }}><AppIcon id="knobs" size={20}/></div>
        <div style={{ flex:1, minWidth:0 }}>
          <div style={{ color:"#e0e0e0", fontWeight:800, fontSize:12, lineHeight:1 }}>GRM Bandpass</div>
          <div style={{ color:"#2a4a50", fontSize:9, marginTop:2, fontWeight:600, letterSpacing:0.5 }}>
            {fmtHz(loHz)} – {fmtHz(hiHz)} Hz · 12dB/oct × 2
          </div>
        </div>
        {/* LED cluster */}
        <div style={{ display:"flex", gap:3, alignItems:"center" }}>
          {[0,1,2,3,4].map(function(i){
            const active = on;
            const colors = ["#00e5ff","#00b4d8","#0ea5e9","#38bdf8","#7dd3fc"];
            return <div key={i} style={{
              width:3, height:6+i*2, borderRadius:1,
              background: active ? colors[i] : "#1e1e1e",
              boxShadow: active ? "0 0 4px "+colors[i]+"88" : "none",
              transition:"all 0.15s",
            }} />;
          })}
        </div>
        <div style={{ width:8, height:8, borderRadius:"50%", background: on ? ACCENT : "#1a1a1a", boxShadow: on ? "0 0 6px "+ACCENT : "none", transition:"all 0.2s" }} />
        <button onClick={function(){ upd("bandpass",{on:!on}); }} style={{
          background: on ? "linear-gradient(180deg,#007a8a,#005f6b)" : "linear-gradient(180deg,#2a2a2a,#222)",
          border:"1px solid "+(on ? ACCENT : "#333"), borderRadius:5, color:"white",
          fontSize:9, fontWeight:800, padding:"4px 12px", cursor:"pointer", letterSpacing:1,
        }}>{on ? "ON" : "OFF"}</button>
      </div>

      {/* Canvas visualiser */}
      <div style={{ padding:"10px 14px 0", opacity: on ? 1 : 0.45, transition:"opacity 0.2s" }}>
        <canvas ref={canvasRef} width={320} height={72}
          style={{ width:"100%", height:72, borderRadius:8, background:"rgba(0,10,14,0.6)", display:"block" }} />
        {/* Freq axis labels */}
        <div style={{ display:"flex", justifyContent:"space-between", padding:"2px 0 0", fontSize:8, color:"#1e3a42", fontWeight:600, letterSpacing:0.3 }}>
          {["20","100","500","1k","5k","20k"].map(function(l){ return <span key={l}>{l}</span>; })}
        </div>
      </div>

      {/* Knobs */}
      <div style={{ padding:"10px 14px 14px", opacity: on ? 1 : 0.45, transition:"opacity 0.2s" }}>
        <div style={{ display:"flex", gap:8, justifyContent:"space-between", flexWrap:"wrap" }}>
          <LogKnob value={center} color={ACCENT} onChange={function(v){ upd("bandpass",{center:v}); }} />
          <Knob label="WIDTH" value={width} min={0.05} max={6} step={0.05} unit="oct"
            color="#38bdf8" onChange={function(v){ upd("bandpass",{width:v}); }} />
          <Knob label="RESO" value={res} min={0} max={18} step={0.5} unit="dB"
            color="#7dd3fc" onChange={function(v){ upd("bandpass",{res:v}); }} />
          <Knob label="MIX" value={Math.round(mix*100)} min={0} max={100} step={1} unit="%"
            color="#0ea5e9" onChange={function(v){ upd("bandpass",{mix:v/100}); }} />
        </div>
        {/* Lo/Hi readout */}
        <div style={{ display:"flex", justifyContent:"space-between", marginTop:8, padding:"6px 10px", background:"rgba(0,229,255,0.04)", borderRadius:6, border:"1px solid rgba(0,229,255,0.08)" }}>
          <div style={{ textAlign:"center" }}>
            <div style={{ color:"#0e5a6a", fontSize:8, fontWeight:700, letterSpacing:1 }}>LO CUT</div>
            <div style={{ color: on ? "#38bdf8" : "#2a4a50", fontSize:11, fontWeight:800, marginTop:2 }}>{fmtHz(loHz)} Hz</div>
          </div>
          <div style={{ textAlign:"center" }}>
            <div style={{ color:"#0e5a6a", fontSize:8, fontWeight:700, letterSpacing:1 }}>CENTER</div>
            <div style={{ color: on ? ACCENT : "#2a4a50", fontSize:11, fontWeight:800, marginTop:2 }}>{fmtHz(center)} Hz</div>
          </div>
          <div style={{ textAlign:"center" }}>
            <div style={{ color:"#0e5a6a", fontSize:8, fontWeight:700, letterSpacing:1 }}>HI CUT</div>
            <div style={{ color: on ? "#38bdf8" : "#2a4a50", fontSize:11, fontWeight:800, marginTop:2 }}>{fmtHz(hiHz)} Hz</div>
          </div>
        </div>
      </div>
    </div>
  );
}
const _BandpassPlugin = React.memo(_BandpassPluginInner, function(prev, next) {
  return prev.fx.bandpass === next.fx.bandpass && prev.upd === next.upd;
});

// =============================================================================
// AUTOTUNE WORKLET — Proven pitch correction for iOS Safari
// Uses ring buffer + linear interpolation pitch shifting
// This is simpler than granular synthesis but actually works on iOS
// =============================================================================
const AUTOTUNE_WORKLET_CODE = `
const KEY_OFFSETS = {C:0,"C#":1,Db:1,D:2,"D#":3,Eb:3,E:4,F:5,"F#":6,Gb:6,G:7,"G#":8,Ab:8,A:9,"A#":10,Bb:10,B:11};
const SCALES = {
  major:      [0,2,4,5,7,9,11],
  minor:      [0,2,3,5,7,8,10],
  chromatic:  [0,1,2,3,4,5,6,7,8,9,10,11],
  pentatonic: [0,2,4,7,9],
  blues:      [0,3,5,6,7,10],
};

function midiToFreq(m){ return 440*Math.pow(2,(m-69)/12); }
function freqToMidi(f){ return f>0 ? 69+12*Math.log2(f/440) : -1; }

function snapMidi(midi, keyOffset, scale){
  if(midi<0) return midi;
  const rel = ((Math.round(midi)-keyOffset)%12+12)%12;
  let best=scale[0], bestD=12;
  for(let i=0;i<scale.length;i++){
    const d=Math.abs(scale[i]-rel);
    const dw=Math.min(d,12-d);
    if(dw<bestD){bestD=dw;best=scale[i];}
  }
  return keyOffset + Math.floor((midi-keyOffset)/12)*12 + best;
}

// YIN pitch detection — reliable, low CPU
function yin(buf, sr){
  const N=buf.length, half=Math.floor(N/2);
  const d=new Float32Array(half);
  d[0]=1;
  let runSum=0;
  for(let tau=1;tau<half;tau++){
    let s=0;
    for(let i=0;i<N-tau;i++){const x=buf[i]-buf[i+tau];s+=x*x;}
    d[tau]=s;
    runSum+=s;
    d[tau]=tau*d[tau]/runSum;
  }
  // Find first dip below 0.15
  for(let tau=2;tau<half-1;tau++){
    if(d[tau]<0.15 && d[tau]<d[tau-1] && d[tau]<d[tau+1]){
      // Parabolic interpolation
      const t=tau+(d[tau+1]-d[tau-1])/(2*(2*d[tau]-d[tau-1]-d[tau+1]));
      return sr/t;
    }
  }
  return -1;
}

class AutotuneProcessor extends AudioWorkletProcessor {
  static get parameterDescriptors(){
    return [
      {name:"bypass",   defaultValue:1,  minValue:0,maxValue:1,  automationRate:"k-rate"},
      {name:"key",      defaultValue:0,  minValue:0,maxValue:11, automationRate:"k-rate"},
      {name:"speed",    defaultValue:50, minValue:1,maxValue:100,automationRate:"k-rate"},
      {name:"wet",      defaultValue:1,  minValue:0,maxValue:1,  automationRate:"k-rate"},
      {name:"humanize", defaultValue:10, minValue:0,maxValue:100,automationRate:"k-rate"},
      {name:"vibrato",  defaultValue:0,  minValue:0,maxValue:1,  automationRate:"k-rate"},
    ];
  }

  constructor(){
    super();
    this._scale     = SCALES.major;
    this._bufSize   = 2048;
    this._ringBuf   = new Float32Array(this._bufSize * 4);
    this._ringWrite = 0;
    this._readPos   = 0.0;
    this._detBuf    = new Float32Array(this._bufSize);
    this._detPtr    = 0;
    this._detCount  = 0;
    this._detFreq   = -1;
    this._targetRatio = 1.0;
    this._smoothRatio = 1.0;
    this._vibPhase  = 0;
    this.port.onmessage = (e)=>{
      if(e.data.scale && SCALES[e.data.scale]) this._scale = SCALES[e.data.scale];
    };
  }

  process(inputs, outputs, params){
    const inp = inputs[0]?.[0];
    const out = outputs[0]?.[0];
    if(!inp||!out) return true;

    const bypass   = params.bypass[0] > 0.5;
    const keyOffset= Math.round(params.key[0]);
    const speed    = params.speed[0]/100;
    const wet      = params.wet[0];
    const humanize = params.humanize[0];
    const vibrato  = params.vibrato[0];

    // Write input into ring buffer
    for(let i=0;i<inp.length;i++){
      this._ringBuf[this._ringWrite % (this._bufSize*4)] = inp[i];
      this._ringWrite++;
      this._detBuf[this._detPtr % this._bufSize] = inp[i];
      this._detPtr++;
    }

    // Detect pitch every 256 samples
    this._detCount += inp.length;
    if(this._detCount >= 256){
      this._detCount = 0;
      const freq = yin(this._detBuf, sampleRate);
      if(freq > 60 && freq < 2000){
        this._detFreq = freq;
        const detMidi  = freqToMidi(freq);
        const snapMid  = snapMidi(detMidi, keyOffset, this._scale);
        const humanCents = humanize > 0 ? (Math.random()-0.5)*(humanize/100)*0.3 : 0;
        const targetMidi = snapMid + humanCents;
        this._targetRatio = midiToFreq(targetMidi) / freq;
      }
    }

    if(bypass || this._detFreq < 0){
      out.set(inp);
      return true;
    }

    // Smooth ratio — fast for hard tune, slow for melodic
    const smoothK = 1 - speed * 0.08;
    this._smoothRatio += (this._targetRatio - this._smoothRatio) * (1 - smoothK);

    let ratio = this._smoothRatio;

    // Vibrato
    if(vibrato > 0){
      this._vibPhase += (2*Math.PI*5.5)/sampleRate*inp.length;
      ratio *= 1 + vibrato*0.02*Math.sin(this._vibPhase);
    }

    // Clamp ratio to safe range (±1 octave)
    ratio = Math.max(0.5, Math.min(2.0, ratio));

    // Read from ring buffer at pitch-shifted rate using linear interpolation
    const ringSize = this._bufSize * 4;
    for(let i=0;i<out.length;i++){
      const intPos = Math.floor(this._readPos);
      const frac   = this._readPos - intPos;
      const i0 = intPos % ringSize;
      const i1 = (intPos + 1) % ringSize;
      const pitched = this._ringBuf[i0] + frac*(this._ringBuf[i1]-this._ringBuf[i0]);

      out[i] = wet*pitched + (1-wet)*inp[i];
      // Clip
      if(out[i]>1) out[i]=1;
      if(out[i]<-1) out[i]=-1;

      this._readPos += ratio;
    }

    // Keep readPos from drifting too far from writePos
    const writePos = this._ringWrite;
    const drift = writePos - this._readPos;
    if(drift > this._bufSize*2) this._readPos = writePos - this._bufSize;
    if(drift < 0) this._readPos = writePos - 128;

    return true;
  }
}
registerProcessor("autotune-processor", AutotuneProcessor);
`;

// Track registration per AudioContext — each context needs its own addModule call
const _autotuneRegistered = new WeakMap();

async function registerAutotuneWorklet(actx) {
  if (!actx || actx.state === "closed") return false;
  if (_autotuneRegistered.get(actx) === true) return true;
  if (_autotuneRegistered.get(actx) instanceof Promise) {
    return await _autotuneRegistered.get(actx);
  }
  const p = (async () => {
    try {
      const blob = new Blob([AUTOTUNE_WORKLET_CODE], { type: "application/javascript" });
      const url  = URL.createObjectURL(blob);
      await actx.audioWorklet.addModule(url);
      URL.revokeObjectURL(url);
      _autotuneRegistered.set(actx, true);
      return true;
    } catch(e) {
      console.warn("AutotuneWorklet registration failed:", e);
      _autotuneRegistered.set(actx, false);
      return false;
    }
  })();
  _autotuneRegistered.set(actx, p);
  return await p;
}

// =============================================================================
// AUTOTUNE PLUGIN UI — matches compressor/reverb style
// =============================================================================
function AutotunePlugin({ fx, upd, Knob }) {
  const at  = fx.autotune || {};
  const on  = !!at.on;
  const KEYS   = ["C","C#","D","D#","E","F","F#","G","G#","A","A#","B"];
  const SCALES = ["major","minor","chromatic","pentatonic","blues"];
  const SCALE_LABELS = { major:"Major", minor:"Minor", chromatic:"Chromatic", pentatonic:"Pentatonic", blues:"Blues" };

  return (
    <div style={{ background:"linear-gradient(180deg,#0d1117 0%,#0a0d12 100%)", borderRadius:16, overflow:"hidden", border:"2px solid " + (on ? "#C026D3" : "#2a2a2a"), boxShadow: on ? "0 0 24px rgba(192,38,211,0.2), inset 0 1px 0 rgba(255,255,255,0.06)" : "inset 0 1px 0 rgba(255,255,255,0.03)" }}>

      {/* Header */}
      <div style={{ display:"flex", alignItems:"center", gap:8, padding:"10px 14px", borderBottom:"1px solid rgba(255,255,255,0.06)", background:"rgba(192,38,211,0.04)" }}>
        <div style={{ display:"flex", gap:3 }}>
          {[0,1,2,3].map(i => <div key={i} style={{ width:3, height:4+i*3, borderRadius:1, background: on ? "#C026D3" : "#1e1e1e", boxShadow: on ? "0 0 4px #C026D388" : "none", transition:"all 0.15s" }} />)}
        </div>
        <div style={{ width:8, height:8, borderRadius:"50%", background: on ? "#C026D3" : "#1a1a1a", boxShadow: on ? "0 0 6px #C026D3, 0 0 12px rgba(192,38,211,0.5)" : "none", transition:"all 0.2s" }} />
        <button onClick={function(){ upd("autotune",{on:!on}); }} style={{ background: on ? "linear-gradient(180deg,#be185d,#9d174d)" : "linear-gradient(180deg,#2a2a2a,#222)", border:"1px solid " + (on ? "#C026D3" : "#333"), borderRadius:5, color:"white", fontSize:9, fontWeight:800, padding:"4px 12px", cursor:"pointer", letterSpacing:1 }}>{on ? "ON" : "OFF"}</button>
        <div style={{ flex:1 }} />
        <div style={{ color: on ? "#C026D3" : "#555", fontSize:10, fontWeight:800, letterSpacing:1 }}>AUTOTUNE</div>
        {/* Mode badge */}
        <div style={{ background:"rgba(192,38,211,0.15)", border:"1px solid rgba(192,38,211,0.3)", borderRadius:4, padding:"2px 7px", fontSize:8, fontWeight:800, color:"#C026D3", letterSpacing:0.5 }}>
          {(at.speed || 50) >= 80 ? "HARD" : (at.speed || 50) >= 40 ? "MELODIC" : "SMOOTH"}
        </div>
      </div>

      <div style={{ padding:"14px", opacity: on ? 1 : 0.4, transition:"opacity 0.2s" }}>

        {/* Key + Scale selectors */}
        <div style={{ display:"flex", gap:8, marginBottom:14 }}>
          <div style={{ flex:1 }}>
            <div style={{ color:"#666", fontSize:8, fontWeight:800, letterSpacing:1, marginBottom:5 }}>KEY</div>
            <div style={{ display:"flex", flexWrap:"wrap", gap:3 }}>
              {KEYS.map(function(k) {
                const active = (at.key || "C") === k;
                return (
                  <button key={k} onClick={function(){ upd("autotune",{key:k}); }} style={{
                    padding:"4px 6px", borderRadius:5, fontSize:9, fontWeight:800, cursor:"pointer",
                    background: active ? "#C026D3" : "rgba(255,255,255,0.04)",
                    border: "1px solid " + (active ? "#C026D3" : "#2a2a2a"),
                    color: active ? "white" : "#555",
                    minWidth:28, textAlign:"center",
                  }}>{k}</button>
                );
              })}
            </div>
          </div>
          <div style={{ flex:1 }}>
            <div style={{ color:"#666", fontSize:8, fontWeight:800, letterSpacing:1, marginBottom:5 }}>SCALE</div>
            <div style={{ display:"flex", flexDirection:"column", gap:3 }}>
              {SCALES.map(function(s) {
                const active = (at.scale || "major") === s;
                return (
                  <button key={s} onClick={function(){ upd("autotune",{scale:s}); }} style={{
                    padding:"4px 8px", borderRadius:5, fontSize:9, fontWeight:800, cursor:"pointer", textAlign:"left",
                    background: active ? "rgba(192,38,211,0.2)" : "rgba(255,255,255,0.03)",
                    border: "1px solid " + (active ? "#C026D3" : "#222"),
                    color: active ? "#C026D3" : "#444",
                  }}>{SCALE_LABELS[s]}</button>
                );
              })}
            </div>
          </div>
        </div>

        {/* Retune speed presets */}
        <div style={{ marginBottom:14 }}>
          <div style={{ color:"#666", fontSize:8, fontWeight:800, letterSpacing:1, marginBottom:6 }}>RETUNE SPEED</div>
          <div style={{ display:"flex", gap:5, marginBottom:8 }}>
            {[{label:"T-PAIN",speed:100},{label:"GUNNA",speed:60},{label:"DRAKE",speed:30},{label:"SMOOTH",speed:10}].map(function(p){
              const active = Math.abs((at.speed||50) - p.speed) < 10;
              return (
                <button key={p.label} onClick={function(){ upd("autotune",{speed:p.speed}); }} style={{
                  flex:1, padding:"5px 2px", borderRadius:6, fontSize:7, fontWeight:800, cursor:"pointer",
                  background: active ? "linear-gradient(135deg,#C026D3,#7C3AED)" : "rgba(255,255,255,0.04)",
                  border:"1px solid " + (active ? "#C026D3" : "#222"),
                  color: active ? "white" : "#444",
                  letterSpacing:0.3,
                }}>{p.label}</button>
              );
            })}
          </div>
        </div>

        {/* Knobs row */}
        <div style={{ display:"flex", gap:6, flexWrap:"wrap", justifyContent:"space-around" }}>
          <Knob label="SPEED"    value={at.speed??50}    min={1}   max={100} step={1}   unit="%" color="#C026D3" onChange={function(v){ upd("autotune",{speed:v}); }} />
          <Knob label="WET"      value={at.wet??1}        min={0}   max={1}   step={0.01} unit="%" color="#C026D3" onChange={function(v){ upd("autotune",{wet:v}); }} />
          <Knob label="HUMANIZE" value={at.humanize??10}  min={0}   max={100} step={1}   unit="%" color="#8B5CF6" onChange={function(v){ upd("autotune",{humanize:v}); }} />
          <Knob label="VIBRATO"  value={at.vibrato??0}    min={0}   max={1}   step={0.01} unit="%" color="#8B5CF6" onChange={function(v){ upd("autotune",{vibrato:v}); }} />
        </div>

        {/* Formant toggle */}
        <div style={{ marginTop:12, display:"flex", alignItems:"center", gap:8 }}>
          <button onClick={function(){ upd("autotune",{formant:!(at.formant??true)}); }} style={{
            padding:"5px 12px", borderRadius:6, fontSize:9, fontWeight:800, cursor:"pointer",
            background: (at.formant??true) ? "rgba(192,38,211,0.2)" : "rgba(255,255,255,0.04)",
            border:"1px solid " + ((at.formant??true) ? "#C026D3" : "#222"),
            color: (at.formant??true) ? "#C026D3" : "#444",
          }}>FORMANT PRESERVE {(at.formant??true) ? "ON" : "OFF"}</button>
          <div style={{ color:"#333", fontSize:8 }}>Preserves vocal character during pitch shift</div>
        </div>

        {/* Pitch display */}
        <div style={{ marginTop:10, background:"rgba(0,0,0,0.4)", borderRadius:8, padding:"8px 12px", border:"1px solid #111", display:"flex", alignItems:"center", gap:10 }}>
          <div style={{ width:6, height:6, borderRadius:"50%", background: on ? "#22C55E" : "#1a1a1a", boxShadow: on ? "0 0 6px #22C55E" : "none", flexShrink:0 }} />
          <div style={{ color:"#333", fontSize:9 }}>
            {on ? `Key: ${at.key||"C"} ${at.scale||"major"} · ${(at.speed||50)>=80?"Hard Tune":(at.speed||50)>=40?"Melodic":"Smooth"} mode active` : "Enable autotune to begin vocal correction"}
          </div>
        </div>
      </div>
    </div>
  );
}

// =============================================================================
// NOISE GATE WORKLET + REGISTRATION
// =============================================================================
const NOISE_GATE_WORKLET_CODE = `
class NoiseGateProcessor extends AudioWorkletProcessor {
  static get parameterDescriptors() {
    return [
      { name: "threshold",  defaultValue: -40, minValue: -80, maxValue: 0,   automationRate: "k-rate" },
      { name: "reduction",  defaultValue: -60, minValue: -80, maxValue: 0,   automationRate: "k-rate" },
      { name: "attack",     defaultValue: 0.003, minValue: 0.0001, maxValue: 0.5, automationRate: "k-rate" },
      { name: "hold",       defaultValue: 0.1,   minValue: 0,      maxValue: 1.0, automationRate: "k-rate" },
      { name: "release",    defaultValue: 0.15,  minValue: 0.001,  maxValue: 2.0, automationRate: "k-rate" },
      { name: "bypass",     defaultValue: 0,     minValue: 0,      maxValue: 1,   automationRate: "k-rate" },
    ];
  }
  constructor() {
    super();
    this._envelope    = 0;
    this._holdSamples = 0;
    this._open        = false;
    this._rmsWin   = Math.round(sampleRate * 0.01);
    this._rmsBuf   = new Float32Array(this._rmsWin);
    this._rmsIdx   = 0;
    this._rmsSumSq = 0;
  }
  process(inputs, outputs, params) {
    const inp = inputs[0]?.[0];
    const out = outputs[0]?.[0];
    if (!inp || !out) return true;
    const bypass = params.bypass[0] > 0.5;
    if (bypass) { out.set(inp); return true; }
    const threshLin  = Math.pow(10, params.threshold[0] / 20);
    const reducLin   = Math.pow(10, params.reduction[0] / 20);
    const sr         = sampleRate;
    const attackCoef  = Math.exp(-1 / (params.attack[0]  * sr));
    const releaseCoef = Math.exp(-1 / (params.release[0] * sr));
    const holdLen     = Math.round(params.hold[0] * sr);
    for (let i = 0; i < inp.length; i++) {
      const old = this._rmsBuf[this._rmsIdx];
      const cur = inp[i];
      this._rmsSumSq += cur * cur - old * old;
      this._rmsBuf[this._rmsIdx] = cur;
      this._rmsIdx = (this._rmsIdx + 1) % this._rmsWin;
      const rms = Math.sqrt(Math.max(0, this._rmsSumSq) / this._rmsWin);
      if (rms >= threshLin) {
        this._open = true; this._holdSamples = holdLen;
      } else if (this._holdSamples > 0) {
        this._holdSamples--;
      } else {
        this._open = false;
      }
      const target = this._open ? 1.0 : reducLin;
      const coef   = this._open ? attackCoef : releaseCoef;
      this._envelope = target + coef * (this._envelope - target);
      out[i] = inp[i] * this._envelope;
    }
    return true;
  }
}
registerProcessor("noise-gate-processor", NoiseGateProcessor);
`;

const _noiseGateRegistered = new WeakMap();

async function registerRNNoiseWorklet(actx) {
  if (!actx || actx.state === "closed") return false;
  if (_noiseGateRegistered.get(actx) === true) return true;
  if (_noiseGateRegistered.get(actx) instanceof Promise) {
    return await _noiseGateRegistered.get(actx);
  }
  const p = (async () => {
    try {
      const blob = new Blob([NOISE_GATE_WORKLET_CODE], { type: "application/javascript" });
      const url  = URL.createObjectURL(blob);
      await actx.audioWorklet.addModule(url);
      URL.revokeObjectURL(url);
      _noiseGateRegistered.set(actx, true);
      return true;
    } catch(e) {
      _noiseGateRegistered.set(actx, false);
      return false;
    }
  })();
  _noiseGateRegistered.set(actx, p);
  return await p;
}

// =============================================================================
// PLUGIN DELAY COMPENSATION (PDC)
//
// Measures the actual latency a track's FX chain introduces, by feeding an
// impulse into an OfflineAudioContext-replicated chain and finding when the
// audio first emerges at the output.
//
// Result is in SECONDS (not samples). Returns 0 if no FX or measurement fails.
//
// IMPORTANT: This builds a SIMPLIFIED chain that only includes the latency-
// introducing nodes. Pan, gain, mute/solo logic, and zero-latency biquads
// (EQ, Pultec) are omitted — they don't contribute to delay. We model:
//   • DynamicsCompressor — inherent ~6ms lookahead in implementation
//   • ConvolverNode      — the IR introduces some pre-delay
//   • DelayNode          — the explicit delay time
//   • WaveShaper         — zero-latency, but oversample buffers add a few samples
//   • AudioWorkletNode   — 128 samples per quantum + any internal buffering
//
// The chain inside the OfflineAudioContext is built in the SAME ORDER as the
// live chain, so cumulative buffering propagation matches reality.
// =============================================================================
async function measureChainLatency(track, sampleRate) {
  if (!track || !track.effects) return 0;
  const fx = track.effects;

  // Quick check: anything in the chain that could introduce latency?
  // If not, skip measurement entirely — biquad-only chains have zero latency.
  // NOTE: TQ has DynamicsCompressors in its sidechain, BUT they're parallel
  // to the audio path (used only for measurement, not in the audio signal).
  // The audio path is biquads only → zero direct latency. So TQ is NOT in
  // this list, even though it does compression internally.
  const hasLatencySource =
    (fx.compressor && fx.compressor.on) ||
    (fx.rcomp && fx.rcomp.on) ||
    (fx.reverb && fx.reverb.on) ||
    (fx.ocean && fx.ocean.on) ||
    (fx.doubler && fx.doubler.on) ||
    (fx.hdelay && fx.hdelay.on) ||
    (fx.noiseremover && fx.noiseremover.on) ||
    (fx.autotune && fx.autotune.on) ||
    (fx.trotten && fx.trotten.on);
  if (!hasLatencySource) return 0;

  try {
    // 2 seconds of buffer is more than enough to capture any realistic
    // FX latency (longest expected is ~150ms for autotune + reverb stacks).
    const renderSec = 2.0;
    const renderLen = Math.round(sampleRate * renderSec);
    const offline = new OfflineAudioContext(1, renderLen, sampleRate);

    // Register worklets on this offline context if any plugin needs them.
    // Each offline context is independent, so we need to do this once per
    // measurement.
    if (fx.autotune && fx.autotune.on) {
      await registerAutotuneWorklet(offline);
    }
    if (fx.noiseremover && fx.noiseremover.on) {
      await registerRNNoiseWorklet(offline);
    }

    // Build a SIMPLIFIED chain mirroring buildChain's topology, with only the
    // latency-introducing nodes. Order matches buildChain so propagation
    // delays accumulate the same way.
    let node = offline.destination;

    // R-Comp (warm/electro modes — both use DynamicsCompressor)
    if (fx.rcomp && fx.rcomp.on) {
      const rc = offline.createDynamicsCompressor();
      rc.threshold.value = fx.rcomp.threshold ?? -18;
      rc.ratio.value     = fx.rcomp.ratio ?? 3;
      rc.attack.value    = fx.rcomp.attack ?? 0.012;
      rc.release.value   = fx.rcomp.release ?? 0.18;
      rc.connect(node); node = rc;
    }

    // Compressor
    if (fx.compressor && fx.compressor.on) {
      const comp = offline.createDynamicsCompressor();
      comp.threshold.value = fx.compressor.threshold ?? -24;
      comp.ratio.value     = fx.compressor.ratio ?? 4;
      comp.attack.value    = fx.compressor.attack ?? 0.003;
      comp.release.value   = fx.compressor.release ?? 0.25;
      comp.connect(node); node = comp;
    }

    // Ocean Reverb (convolver with synthetic IR)
    if (fx.ocean && fx.ocean.on) {
      const sr = offline.sampleRate;
      const roomSize = Math.min(1.5, fx.ocean.roomSize || 1.0);
      const len = Math.round(sr * roomSize * 2.5);
      const ir = offline.createBuffer(2, len, sr);
      for (let ch=0; ch<2; ch++) {
        const d = ir.getChannelData(ch);
        for (let i=0; i<len; i++) d[i] = (Math.random()*2-1) * Math.pow(1-i/len, 1.8);
      }
      const conv = offline.createConvolver();
      conv.buffer = ir;
      const preDelaySec = (fx.ocean.preDelay || 20) / 1000;
      const preDelay = offline.createDelay(0.3);
      preDelay.delayTime.value = preDelaySec;
      preDelay.connect(conv); conv.connect(node); node = preDelay;
    }

    // Convolution Reverb (similar)
    if (fx.reverb && fx.reverb.on) {
      const sr = offline.sampleRate;
      const len = Math.round(sr * (fx.reverb.roomSize || 0.8) * 2);
      const ir = offline.createBuffer(2, len, sr);
      for (let ch=0; ch<2; ch++) {
        const d = ir.getChannelData(ch);
        for (let i=0; i<len; i++) d[i] = (Math.random()*2-1) * Math.pow(1-i/len, 1.5);
      }
      const conv = offline.createConvolver(); conv.buffer = ir;
      conv.connect(node); node = conv;
    }

    // Doubler — Haas delay is the dominant latency
    if (fx.doubler && fx.doubler.on) {
      const delayMs = fx.doubler.delay ?? 20;
      const haas = offline.createDelay(0.1);
      haas.delayTime.value = delayMs / 1000;
      haas.connect(node); node = haas;
    }

    // H-Delay — modelled as just the dry-path latency (delay node feedback
    // loop only affects wet path, dry passes through immediately)
    if (fx.hdelay && fx.hdelay.on) {
      // H-Delay's dry path is zero-latency; only the wet path is delayed.
      // The user perceives the dry signal arriving on time. No compensation.
    }

    // Noise Remover (AudioWorkletNode — 128 sample buffering)
    if (fx.noiseremover && fx.noiseremover.on) {
      try {
        const gate = new AudioWorkletNode(offline, "rnnoise-gate-processor");
        gate.connect(node); node = gate;
      } catch(e) { /* worklet not ready, skip */ }
    }

    // Autotune (AudioWorkletNode — 128 sample buffering + internal lookahead)
    if (fx.autotune && fx.autotune.on) {
      try {
        const at = new AudioWorkletNode(offline, "autotune-processor");
        at.connect(node); node = at;
      } catch(e) { /* worklet not ready, skip */ }
    }

    // T-Rotten Master — DynamicsCompressor + brickwall limiter (another DC)
    // Skip EQ sections (biquads, zero latency).
    if (fx.trotten && fx.trotten.on) {
      const comp = offline.createDynamicsCompressor();
      comp.threshold.value = fx.trotten.compThr ?? -15;
      comp.ratio.value     = 4;
      comp.attack.value    = 0.005;
      comp.release.value   = 0.1;
      const lim = offline.createDynamicsCompressor();
      lim.threshold.value = fx.trotten.limCeil ?? -0.5;
      lim.ratio.value     = 20;
      lim.attack.value    = 0.0005;
      lim.release.value   = Math.max(0.05, fx.trotten.limRel ?? 0.5);
      comp.connect(lim); lim.connect(node); node = comp;
    }

    // Feed an impulse at sample 0 of the input
    const impulse = offline.createBuffer(1, 1, sampleRate);
    impulse.getChannelData(0)[0] = 1.0;
    const src = offline.createBufferSource();
    src.buffer = impulse;
    src.connect(node);
    src.start(0);

    // Render. Returns an AudioBuffer with the result.
    const rendered = await offline.startRendering();
    const data = rendered.getChannelData(0);
    // Find the first sample with magnitude > threshold. That's our latency.
    // Threshold is set fairly low (0.005) so we catch the leading edge even
    // for chains that significantly attenuate.
    const THRESHOLD = 0.005;
    for (let i = 0; i < data.length; i++) {
      if (Math.abs(data[i]) > THRESHOLD) {
        return i / sampleRate;
      }
    }
    return 0;  // No signal made it through — assume no latency
  } catch (e) {
    console.warn("[PDC] measureChainLatency failed for track", track.id, e);
    return 0;  // Fail-soft: no compensation rather than broken playback
  }
}

// ─────────────────────────────────────────────────────────────────────────────
// TQ OFFLINE SIMULATION
// ─────────────────────────────────────────────────────────────────────────────
// Web Audio's DynamicsCompressor.reduction does NOT update meaningfully inside
// OfflineAudioContext on iOS/Safari — the spec doesn't require real-time
// metering properties to be updated during offline render. That means the
// live polling loop (which drives TQ's biquad gains from det.reduction) can't
// run in export, and CUT bands end up silent while BOOST bands stay pinned
// at their full target gain.
//
// To get an export that sounds like the live playback, we precompute the
// per-band gain envelope here in JavaScript, then schedule those values onto
// each band's biquad.gain via setValueAtTime in buildChain (when the export
// pathway calls it with opts.tqAutomation).
//
// The JS path mirrors the live path closely:
//   1. JS biquad bandpass at the band's freq+Q produces the "detector input"
//      signal at sample rate.
//   2. Envelope follower with the band's attack/release smooths the magnitude.
//   3. Reduction = max(0, envelopeDb - threshold) * (1 - 1/ratio). We use
//      ratio=20 to match the live detector configuration.
//   4. Final band gain: cut → target * min(1, r/6); boost → target * max(0, 1 - r/6).
//      Same formula as the live polling loop.
//
// We downsample the resulting envelope to one value every STEP_SAMPLES samples
// (~10ms at 48kHz) to keep the AudioParam event count manageable. AudioParam
// schedules tens of thousands of events without issue, but 480k per band ×
// 7 bands × multiple tracks would be silly. 10ms granularity is finer than
// the human ear can resolve gain changes (~20-30ms perception threshold).
async function simulateTQBandGain(sourceSamples, bandCfg, sampleRate) {
  const len = sourceSamples.length;
  const f0 = bandCfg.freq;
  const Q  = Math.max(0.6, bandCfg.q * 0.7);  // mirror live detector's bp Q

  // ── 1. JS biquad bandpass coefficients (constant 0dB peak gain form) ──
  // Standard RBJ cookbook biquad. We use direct form 1.
  const w0     = 2 * Math.PI * f0 / sampleRate;
  const cosw0  = Math.cos(w0);
  const sinw0  = Math.sin(w0);
  const alpha  = sinw0 / (2 * Q);
  const b0n =  alpha;
  const b1n =  0;
  const b2n = -alpha;
  const a0n =  1 + alpha;
  const a1n = -2 * cosw0;
  const a2n =  1 - alpha;
  // Normalize so a0 = 1
  const b0 = b0n / a0n;
  const b1 = b1n / a0n;
  const b2 = b2n / a0n;
  const a1 = a1n / a0n;
  const a2 = a2n / a0n;

  // ── 2. Run bandpass + envelope follower in one pass to avoid an extra
  //       allocation for the filtered buffer ──
  // Attack/release coefficients for single-pole IIR envelope follower.
  // exp(-1/(τ·fs)) is the standard form. When attack/release is 0, fall back
  // to ~1 sample worth of smoothing to avoid division-by-zero edge cases.
  const attackCoeff  = Math.exp(-1 / (Math.max(0.0001, bandCfg.attack)  * sampleRate));
  const releaseCoeff = Math.exp(-1 / (Math.max(0.0001, bandCfg.release) * sampleRate));
  const threshold = bandCfg.threshold;
  const ratioCoeff = 1 - 1/20;  // 20:1 detector
  const targetGain = bandCfg.gain;
  const isCut = bandCfg.type === "cut";

  // 10ms granularity for scheduled events. Round to integer sample step.
  const STEP_MS = 10;
  const STEP_SAMPLES = Math.max(1, Math.round(STEP_MS / 1000 * sampleRate));
  const stepCount = Math.ceil(len / STEP_SAMPLES);
  const times = new Float32Array(stepCount);
  const gains = new Float32Array(stepCount);

  // Biquad state (x[n-1], x[n-2], y[n-1], y[n-2])
  let x1 = 0, x2 = 0, y1 = 0, y2 = 0;
  // Envelope state
  let env = 0;
  let stepIdx = 0;
  let nextStepSample = 0;
  // Yield every ~200k samples (~4-5 seconds-worth of audio) so the export
  // spinner can repaint during this potentially-long pass. Each yield is
  // a setTimeout(0); the cost is microscopic but it lets the compositor
  // get a paint frame in.
  const YIELD_BLOCK = 200000;
  let nextYieldSample = YIELD_BLOCK;

  for (let i = 0; i < len; i++) {
    const x0 = sourceSamples[i] || 0;
    // Biquad bandpass — direct form 1
    const y0 = b0 * x0 + b1 * x1 + b2 * x2 - a1 * y1 - a2 * y2;
    x2 = x1; x1 = x0;
    y2 = y1; y1 = y0;
    // Envelope: rectify + attack/release smoothing
    const rect = Math.abs(y0);
    const coeff = rect > env ? attackCoeff : releaseCoeff;
    env = rect + coeff * (env - rect);

    if (i >= nextStepSample) {
      // Convert envelope to dB. 20log10 with a tiny floor to avoid -Infinity.
      const envDb = 20 * Math.log10(env + 1e-10);
      // ── Soft knee ──
      // DynamicsCompressor uses a 6dB soft knee by default — compression
      // starts gradually at (threshold - 3) and reaches the ratio fully at
      // (threshold + 3). A hard-knee approximation (excess = max(0, env -
      // threshold)) under-cuts in the knee region by up to ~0.7dB at the
      // threshold itself, which makes export sound less processed than
      // live (vocals "loud", low-mids "muddy"). Match the live curve:
      //
      //   envDb < T-K/2:    reduction = 0
      //   T-K/2..T+K/2:     reduction = ((envDb-(T-K/2))^2 / (2K)) * (1-1/ratio)
      //   envDb > T+K/2:    reduction = (envDb - T) * (1-1/ratio)
      const KNEE = 6; // dB (matches DynamicsCompressor default)
      const HALF_KNEE = KNEE / 2;
      let reduction;
      if (envDb < threshold - HALF_KNEE) {
        reduction = 0;
      } else if (envDb < threshold + HALF_KNEE) {
        const delta = envDb - (threshold - HALF_KNEE);
        reduction = (delta * delta / (2 * KNEE)) * ratioCoeff;
      } else {
        reduction = (envDb - threshold) * ratioCoeff;
      }
      let bandGain;
      if (isCut) {
        bandGain = targetGain * Math.min(1, reduction / 6);
      } else {
        bandGain = targetGain * Math.max(0, 1 - reduction / 6);
      }
      times[stepIdx] = i / sampleRate;
      gains[stepIdx] = bandGain;
      stepIdx++;
      nextStepSample += STEP_SAMPLES;
    }
    if (i >= nextYieldSample) {
      // Yield to event loop so spinner can paint.
      await new Promise(function(r){ setTimeout(r, 0); });
      nextYieldSample += YIELD_BLOCK;
    }
  }

  // Trim to actual filled length (the loop might not hit the very last step)
  return {
    times: times.subarray(0, stepIdx),
    gains: gains.subarray(0, stepIdx),
  };
}

// Build the per-track source signal that hits the TQ block. This is what we
// analyse to compute each band's gain envelope. For accuracy we'd want to
// model pan/makeup-gain/EQ that precede TQ, but for v1 we use the raw clip
// audio mixed at clip startTimes — those upstream blocks aren't typically
// dramatic enough to change the detector behavior noticeably.
function buildTQSourceMono(track, totalDur, sampleRate) {
  const buf = new Float32Array(Math.max(1, Math.ceil(totalDur * sampleRate)));
  if (!track.clips) return buf;
  for (const cl of track.clips) {
    if (!cl.audioBuffer || cl.active === false) continue;
    // Downmix to mono if stereo
    const ab = cl.audioBuffer;
    const ch0 = ab.getChannelData(0);
    const ch1 = ab.numberOfChannels > 1 ? ab.getChannelData(1) : null;
    const trimS = cl.trimStart || 0;
    const trimE = cl.trimEnd !== undefined ? cl.trimEnd : ab.duration;
    const dur = Math.max(0, trimE - trimS);
    if (dur <= 0) continue;
    const srcSampleOffset = Math.floor(trimS * ab.sampleRate);
    const srcLen = Math.floor(dur * ab.sampleRate);
    // If clip sample rate doesn't match, do nearest-neighbour resampling
    // (rough but adequate for envelope analysis).
    const sourceSR = ab.sampleRate;
    const dstSampleOffset = Math.floor((cl.startTime || 0) * sampleRate);
    const dstLen = Math.floor(dur * sampleRate);
    for (let i = 0; i < dstLen; i++) {
      const dstIdx = dstSampleOffset + i;
      if (dstIdx >= buf.length) break;
      // Map dst sample → source sample
      const srcIdx = srcSampleOffset + Math.floor(i * sourceSR / sampleRate);
      if (srcIdx >= ch0.length) break;
      const s0 = ch0[srcIdx] || 0;
      const s1 = ch1 ? (ch1[srcIdx] || 0) : s0;
      buf[dstIdx] += (s0 + s1) * 0.5;
    }
  }
  return buf;
}

// Apply the parametric EQ (HPF, low-shelf, mid-peak, high-shelf, LPF) to a
// mono Float32Array in JavaScript, matching what the live engine does to the
// signal before it reaches TQ. Used to pre-filter the source for offline TQ
// simulation so our detector input matches the live detector input.
//
// Uses the same biquad cookbook formulas as Web Audio's BiquadFilterNode
// (constant 0dB peak for peaking, normalized gain for shelves) in direct
// form 1. Mutates the input buffer in place and returns it.
async function applyEQPreFilter(samples, eq, sampleRate) {
  const sr = sampleRate;
  // Defaults match the live engine.
  const hpfFreq = eq.hpfFreq || 80;
  const hpfQ    = eq.hpfQ || 0.707;
  const lowFreq = eq.lowFreq || 200;
  const lowGain = eq.low || 0;
  const midFreq = eq.midFreq || 1000;
  const midGain = eq.mid || 0;
  const midQ    = eq.midQ || 1.0;
  const highFreq = eq.highFreq || 8000;
  const highGain = eq.high || 0;
  const lpfFreq = eq.lpfFreq || 18000;
  const lpfQ    = eq.lpfQ || 0.707;

  // ── Cookbook biquad coefficient helpers ──
  // Reference: RBJ Audio EQ Cookbook (Robert Bristow-Johnson).
  function highpassCoeffs(f0, Q) {
    const w0 = 2 * Math.PI * f0 / sr;
    const cw = Math.cos(w0), sw = Math.sin(w0);
    const a = sw / (2 * Q);
    const a0 = 1 + a;
    return {
      b0: ((1 + cw) / 2) / a0,
      b1: -(1 + cw) / a0,
      b2: ((1 + cw) / 2) / a0,
      a1: (-2 * cw) / a0,
      a2: (1 - a) / a0,
    };
  }
  function lowpassCoeffs(f0, Q) {
    const w0 = 2 * Math.PI * f0 / sr;
    const cw = Math.cos(w0), sw = Math.sin(w0);
    const a = sw / (2 * Q);
    const a0 = 1 + a;
    return {
      b0: ((1 - cw) / 2) / a0,
      b1: (1 - cw) / a0,
      b2: ((1 - cw) / 2) / a0,
      a1: (-2 * cw) / a0,
      a2: (1 - a) / a0,
    };
  }
  function peakingCoeffs(f0, Q, gainDb) {
    const A = Math.pow(10, gainDb / 40);
    const w0 = 2 * Math.PI * f0 / sr;
    const cw = Math.cos(w0), sw = Math.sin(w0);
    const a = sw / (2 * Q);
    const a0 = 1 + a / A;
    return {
      b0: (1 + a * A) / a0,
      b1: (-2 * cw) / a0,
      b2: (1 - a * A) / a0,
      a1: (-2 * cw) / a0,
      a2: (1 - a / A) / a0,
    };
  }
  function lowshelfCoeffs(f0, gainDb) {
    const A = Math.pow(10, gainDb / 40);
    const w0 = 2 * Math.PI * f0 / sr;
    const cw = Math.cos(w0), sw = Math.sin(w0);
    // Web Audio uses S=1 implicitly for shelves
    const a = sw / 2 * Math.sqrt((A + 1/A) * 1 + 2);
    const sqA2a = 2 * Math.sqrt(A) * a;
    const a0 = (A + 1) + (A - 1) * cw + sqA2a;
    return {
      b0: (A * ((A + 1) - (A - 1) * cw + sqA2a)) / a0,
      b1: (2 * A * ((A - 1) - (A + 1) * cw)) / a0,
      b2: (A * ((A + 1) - (A - 1) * cw - sqA2a)) / a0,
      a1: (-2 * ((A - 1) + (A + 1) * cw)) / a0,
      a2: ((A + 1) + (A - 1) * cw - sqA2a) / a0,
    };
  }
  function highshelfCoeffs(f0, gainDb) {
    const A = Math.pow(10, gainDb / 40);
    const w0 = 2 * Math.PI * f0 / sr;
    const cw = Math.cos(w0), sw = Math.sin(w0);
    const a = sw / 2 * Math.sqrt((A + 1/A) * 1 + 2);
    const sqA2a = 2 * Math.sqrt(A) * a;
    const a0 = (A + 1) - (A - 1) * cw + sqA2a;
    return {
      b0: (A * ((A + 1) + (A - 1) * cw + sqA2a)) / a0,
      b1: (-2 * A * ((A - 1) + (A + 1) * cw)) / a0,
      b2: (A * ((A + 1) + (A - 1) * cw - sqA2a)) / a0,
      a1: (2 * ((A - 1) - (A + 1) * cw)) / a0,
      a2: ((A + 1) - (A - 1) * cw - sqA2a) / a0,
    };
  }

  // Stages to apply, in the same order as the live EQ chain:
  // HPF → low-shelf → mid-peak → high-shelf → LPF.
  // Skip a stage if its gain is 0dB (shelves/peaks) or its cutoff is at
  // an extreme that makes it transparent.
  const stages = [
    hpfFreq > 20 ? highpassCoeffs(hpfFreq, hpfQ) : null,
    Math.abs(lowGain) > 0.01 ? lowshelfCoeffs(lowFreq, lowGain) : null,
    Math.abs(midGain) > 0.01 ? peakingCoeffs(midFreq, midQ, midGain) : null,
    Math.abs(highGain) > 0.01 ? highshelfCoeffs(highFreq, highGain) : null,
    lpfFreq < 22000 ? lowpassCoeffs(lpfFreq, lpfQ) : null,
  ].filter(Boolean);

  if (stages.length === 0) return samples;

  // Apply each stage in series, direct form 1, in-place.
  for (const s of stages) {
    let x1 = 0, x2 = 0, y1 = 0, y2 = 0;
    for (let i = 0; i < samples.length; i++) {
      const x0 = samples[i];
      const y0 = s.b0 * x0 + s.b1 * x1 + s.b2 * x2 - s.a1 * y1 - s.a2 * y2;
      x2 = x1; x1 = x0;
      y2 = y1; y1 = y0;
      samples[i] = y0;
    }
    // Yield between stages so the spinner can paint a frame.
    await new Promise(function(r){ setTimeout(r, 0); });
  }
  return samples;
}

// ═════════════════════════════════════════════════════════════════════════════
//  DRUM SEQUENCER — module-scope helpers
// ═════════════════════════════════════════════════════════════════════════════
//
// The drum sequencer is a new track type ("drumSequencer") that holds named
// patterns of step-grid drum hits. Pattern clips placed on the timeline
// reference a pattern by id and loop that pattern for their clip duration.
//
// Architecture overview:
//
//   Track (type:"drumSequencer")
//     ├─ kitId               — which kit's samples to use (default: "default")
//     ├─ patterns[]          — named pattern templates the user has authored
//     │    ├─ id
//     │    ├─ name           — e.g. "Verse", "Hook"
//     │    ├─ lengthSteps    — total steps in pattern (16 = 1 bar at 16ths)
//     │    ├─ stepsPerBeat   — 4 = sixteenth-note resolution (FL default)
//     │    └─ channels[]     — one per pad in the kit
//     │         ├─ padId         — kit pad reference ("kick", "snare", ...)
//     │         ├─ steps[]       — boolean grid; true = trigger
//     │         └─ volume        — per-channel gain (0..1.5)
//     └─ clips[]             — timeline placements
//          ├─ id
//          ├─ patternId      — which pattern this clip plays
//          ├─ startTime      — seconds on the project timeline
//          └─ duration       — seconds; pattern loops within this window
//
// Playback uses a Chris Wilson look-ahead scheduler approach: a setInterval
// loop schedules any drum hit that falls within the next ~150ms via
// AudioBufferSourceNode.start(exactTime). Timing is sample-accurate because
// the actual triggers ride on the AudioContext clock, not setInterval.

// FL Studio's default sequencer resolution. Each step = 1/16th note.
// stepsPerBeat=4 means: 16 steps per bar at 4/4. Patterns may override
// per-pattern but 4 is the universal default.
const DRUM_DEFAULT_STEPS_PER_BEAT = 4;

// Default new-pattern length when adding a fresh pattern clip — 16 steps
// = 1 bar at 16th resolution. Matches every step sequencer ever shipped.
const DRUM_DEFAULT_LENGTH_STEPS = 16;

// Lookahead window for the drum scheduler. Each scheduler tick schedules
// any step whose trigger time falls within this many seconds of now.
// 0.3s (300ms) safety margin. iOS Safari throttles setInterval callbacks
// under main-thread load (scroll, animation, GC), so the previous 150ms
// wasn't always enough — late notes got clamped to play immediately,
// drifting drums against MIDI tracks (which drop late notes instead).
// 300ms is well past iOS jitter and still feels responsive for live edits.
const DRUM_LOOKAHEAD_SEC = 0.3;

// How often the scheduler ticks. 25ms = 40Hz, the standard Chris Wilson
// recommendation. Combined with the 150ms lookahead, we have plenty of
// safety margin: even if a tick is delayed 100ms by main-thread work,
// there's still 50ms of pre-scheduled events to cover the gap.
const DRUM_TICK_INTERVAL_MS = 25;

// Build an empty default pattern for a freshly-created drum track. Channels
// match the kit's pads in order. All steps off; user fills in the grid.
// The optional lengthSteps arg overrides the default 16 — used when the
// user changes pattern length in the rack header.
// ── Step shape ─────────────────────────────────────────────────────────────
// A step is stored as a NUMBER in [0, 1.5]:
//   • 0       — off (no hit)
//   • >0      — on, with that value as the velocity multiplier
//                (1.0 = full volume, 0.5 = half, 1.5 = headroom for accents)
//
// This keeps backward-compatibility with the Phase 1 boolean shape:
// `!!step` evaluates correctly (false=0, true=anything-nonzero), and existing
// projects with `true`/`false` in steps[] are migrated by stepVelocity()
// below before any audio code reads them.
//
// Default tap-velocity is DRUM_DEFAULT_VELOCITY (1.0). Long-press + drag
// adjusts upward to a max of 1.5 (boosted accent) or downward toward 0
// (which functions as a fade-out before turning the step off completely).
const DRUM_DEFAULT_VELOCITY = 1.0;
const DRUM_MAX_VELOCITY     = 1.5;
const DRUM_MIN_VELOCITY     = 0.10;  // anything below this is treated as "off"

// ── Phase 2B: full per-step parameter ranges ──
// A step can be stored as:
//   • boolean (very old projects)      — true = on @ default velocity
//   • number  (Phase 2A schema)        — 0 = off, otherwise velocity
//   • object  (Phase 2B schema)        — { vel, pitch, pan, shift, repeat }
//
// stepParams() normalizes any of these to a complete params object so the
// scheduler and UI never have to branch on storage shape.
const DRUM_PITCH_MIN     = -24;   // semitones (-2 octaves)
const DRUM_PITCH_MAX     =  24;   // semitones (+2 octaves)
const DRUM_PAN_MIN       = -1;    // full left
const DRUM_PAN_MAX       =  1;    // full right
const DRUM_SHIFT_MIN     = -0.45; // fraction of a step-duration (microtiming)
const DRUM_SHIFT_MAX     =  0.45;
const DRUM_REPEAT_MIN    = 1;     // 1 = single hit (default)
const DRUM_REPEAT_MAX    = 8;     // up to 8 hits across the step (trap roll)

// Return the velocity in [0, 1.5] for a stored step value.
function stepVelocity(rawStep) {
  if (rawStep === true) return DRUM_DEFAULT_VELOCITY;
  if (rawStep === false || rawStep == null) return 0;
  if (typeof rawStep === "object") {
    return stepVelocity(rawStep.vel);
  }
  // Numeric: clamp to safe range.
  const v = +rawStep;
  if (!isFinite(v) || v <= 0) return 0;
  if (v > DRUM_MAX_VELOCITY) return DRUM_MAX_VELOCITY;
  return v;
}

// Convenience boolean for "is this step on at all".
function stepIsOn(rawStep) {
  return stepVelocity(rawStep) >= DRUM_MIN_VELOCITY;
}

// Return the full parameter set for a step, with sensible defaults for
// any value the storage shape doesn't include. Result is always:
//   { vel:0..1.5, pitch:-24..24, pan:-1..1, shift:-0.45..0.45, repeat:1..8 }
function stepParams(rawStep) {
  const vel = stepVelocity(rawStep);
  if (typeof rawStep === "object" && rawStep !== null) {
    return {
      vel: vel,
      pitch:  Math.max(DRUM_PITCH_MIN,  Math.min(DRUM_PITCH_MAX,  +rawStep.pitch  || 0)),
      pan:    Math.max(DRUM_PAN_MIN,    Math.min(DRUM_PAN_MAX,    +rawStep.pan    || 0)),
      shift:  Math.max(DRUM_SHIFT_MIN,  Math.min(DRUM_SHIFT_MAX,  +rawStep.shift  || 0)),
      repeat: Math.max(DRUM_REPEAT_MIN, Math.min(DRUM_REPEAT_MAX, Math.round(+rawStep.repeat) || 1)),
    };
  }
  return { vel: vel, pitch: 0, pan: 0, shift: 0, repeat: 1 };
}

// Update a single param on a step. If the current storage shape is a number
// (or boolean) and the new param is anything OTHER than velocity, the step
// "upgrades" to an object. This keeps simple velocity-only patterns lean
// while accommodating power-user edits without losing data.
function setStepParam(rawStep, paramName, newValue) {
  const cur = stepParams(rawStep);
  const next = { ...cur, [paramName]: newValue };
  // Re-clamp
  next.vel    = Math.max(0, Math.min(DRUM_MAX_VELOCITY, +next.vel || 0));
  next.pitch  = Math.max(DRUM_PITCH_MIN,  Math.min(DRUM_PITCH_MAX,  +next.pitch  || 0));
  next.pan    = Math.max(DRUM_PAN_MIN,    Math.min(DRUM_PAN_MAX,    +next.pan    || 0));
  next.shift  = Math.max(DRUM_SHIFT_MIN,  Math.min(DRUM_SHIFT_MAX,  +next.shift  || 0));
  next.repeat = Math.max(DRUM_REPEAT_MIN, Math.min(DRUM_REPEAT_MAX, Math.round(+next.repeat) || 1));
  // If the step is "vanilla" (all defaults except possibly velocity), collapse
  // back to the numeric shape. Saves memory and keeps round-tripped projects clean.
  if (next.pitch === 0 && next.pan === 0 && next.shift === 0 && next.repeat === 1) {
    return next.vel < DRUM_MIN_VELOCITY ? 0 : next.vel;
  }
  return next;
}

// Effective step count for a channel: per-channel length wins, otherwise
// the pattern's length is used. Channels can have shorter or longer step
// arrays than the pattern; we use whichever the channel actually owns.
function channelLengthSteps(channel, pattern) {
  if (channel && channel.lengthSteps && channel.lengthSteps > 0) return channel.lengthSteps;
  if (channel && channel.steps && channel.steps.length > 0) return channel.steps.length;
  return pattern ? pattern.lengthSteps : DRUM_DEFAULT_LENGTH_STEPS;
}

// ═══════════════════════════════════════════════════════════════════════════
//  Audio-clip pitch shifting (duration-preserving) via SoundTouch
// ═══════════════════════════════════════════════════════════════════════════
//
// Takes an input AudioBuffer + a pitch shift (in semitones, can be fractional
// for cents) and produces a NEW AudioBuffer at the new pitch with the SAME
// duration. The whole thing runs offline — we feed the source samples into
// SoundTouch one block at a time and pull processed samples out the other
// end into a fresh buffer.
//
// SoundTouch is a streaming API: you push N input samples, it produces
// approximately N output samples (at a different time scale). For pitch
// shift with no tempo change, output length matches input length within
// a few samples. We allocate the output buffer at the input's length
// and zero-pad / trim as needed.
//
// Multi-channel handling: SoundTouch processes interleaved stereo. We
// interleave on the way in, deinterleave on the way out.
//
// Returns: Promise<AudioBuffer> resolved with the pitched buffer. The
// promise model lets us yield to the event loop in chunks for very long
// clips so we don't lock the main thread on iOS Safari.
async function pitchAudioBufferOffline(audioCtx, sourceBuffer, semitones) {
  if (!sourceBuffer) return null;
  if (Math.abs(semitones) < 0.005) return sourceBuffer;  // negligible shift
  const sr = sourceBuffer.sampleRate;
  const numChannels = Math.min(2, sourceBuffer.numberOfChannels);
  const numFrames   = sourceBuffer.length;

  // SoundTouch wants interleaved Float32 samples. We allocate one input
  // buffer the size of the whole clip (acceptable up to a few minutes —
  // 60s stereo @44.1k = ~21MB which is fine on a phone).
  const interleaved = new Float32Array(numFrames * 2);
  const chL = sourceBuffer.getChannelData(0);
  const chR = numChannels > 1 ? sourceBuffer.getChannelData(1) : chL;
  for (let i = 0; i < numFrames; i++) {
    interleaved[i * 2]     = chL[i];
    interleaved[i * 2 + 1] = chR[i];
  }

  // Configure SoundTouch. We use the lower-level SoundTouch instance
  // directly (not the PitchShifter wrapper, which assumes a live audio
  // node context). SimpleFilter routes a sample source through it.
  const st = new SoundTouch();
  st.pitchSemitones = semitones;
  st.rate           = 1;   // no time-stretch
  st.tempo          = 1;   // no tempo change either — pure pitch

  // SimpleFilter wants a source object that implements:
  //   extract(target, numFrames) -> numFramesActuallyRead
  // plus position tracking. We make a minimal one wrapping our
  // interleaved input.
  let readPos = 0;
  const source = {
    extract: function(target, numFramesRequested) {
      const remaining = Math.floor((interleaved.length - readPos) / 2);
      const toRead = Math.min(remaining, numFramesRequested);
      for (let i = 0; i < toRead * 2; i++) {
        target[i] = interleaved[readPos + i];
      }
      readPos += toRead * 2;
      return toRead;
    },
  };
  const filter = new SimpleFilter(source, st);

  // Pull processed samples until exhausted. SoundTouch needs a flush at
  // the end (input fully consumed but internal buffers still have data).
  // SimpleFilter handles this if we keep extracting until 0.
  const outputChunks = [];     // arrays of interleaved Float32
  const BLOCK = 4096;
  let totalOutFrames = 0;
  // Yield to the event loop every N blocks so the UI doesn't lock up
  // on long samples — important on iOS Safari which doesn't preempt JS.
  const YIELD_EVERY = 8;
  let blockCount = 0;
  while (true) {
    const tmp = new Float32Array(BLOCK * 2);
    const got = filter.extract(tmp, BLOCK);
    if (got === 0) break;
    if (got < BLOCK) {
      outputChunks.push(tmp.subarray(0, got * 2));
    } else {
      outputChunks.push(tmp);
    }
    totalOutFrames += got;
    blockCount++;
    if (blockCount % YIELD_EVERY === 0) {
      await new Promise(function(r){ setTimeout(r, 0); });
    }
    // Safety cap: don't allow runaway output (in case of bad config).
    // 1.5× input length is generous — pitch shift shouldn't extend duration.
    if (totalOutFrames > numFrames * 1.5) break;
  }

  // Stitch chunks into one interleaved array.
  const outInter = new Float32Array(totalOutFrames * 2);
  let writeOff = 0;
  for (const chunk of outputChunks) {
    outInter.set(chunk, writeOff);
    writeOff += chunk.length;
  }

  // Build output AudioBuffer. We target the SAME frame count as the
  // input (preserving clip duration). If SoundTouch produced fewer
  // frames, pad with zeros; if more, trim.
  const out = audioCtx.createBuffer(numChannels, numFrames, sr);
  const outL = out.getChannelData(0);
  const outR = numChannels > 1 ? out.getChannelData(1) : null;
  const copyFrames = Math.min(totalOutFrames, numFrames);
  for (let i = 0; i < copyFrames; i++) {
    outL[i] = outInter[i * 2];
    if (outR) outR[i] = outInter[i * 2 + 1];
  }
  // Tail zero-pad happens automatically (createBuffer is zero-init).
  return out;
}

// ═══════════════════════════════════════════════════════════════════════════
//  Audio-clip time stretching (pitch-preserving) via SoundTouch
// ═══════════════════════════════════════════════════════════════════════════
//
// Takes an input AudioBuffer + a target duration (in seconds) and produces
// a NEW AudioBuffer at that duration with the SAME pitch as the original.
//
// SoundTouch's `tempo` parameter is the ratio (newDuration / origDuration)
// inverted — tempo=2.0 means play 2× speed (half the time), tempo=0.5 means
// half speed (twice the time). So tempo = origDuration / targetDuration.
//
// Output buffer length is rounded to whole frames at the input sample rate.
// SoundTouch streams output samples slightly differently depending on its
// internal block size, so we collect until exhaustion then trim/pad to the
// requested length.
//
// Returns: Promise<AudioBuffer> with the stretched buffer. Like pitch, runs
// async with periodic yields to keep iOS Safari's main thread responsive.
async function stretchAudioBufferOffline(audioCtx, sourceBuffer, targetDurationSec) {
  if (!sourceBuffer) return null;
  const sr = sourceBuffer.sampleRate;
  const origDur = sourceBuffer.duration;
  if (Math.abs(targetDurationSec - origDur) < 0.005) return sourceBuffer;
  if (targetDurationSec <= 0.05) return sourceBuffer; // refuse silly-short targets
  const tempo = origDur / targetDurationSec;  // >1 = faster, <1 = slower
  // SoundTouch's tempo range — extreme values produce garbage. Allow
  // 0.25× (4× slower) up to 4× (4× faster); that covers everything sensible.
  if (tempo < 0.25 || tempo > 4) return sourceBuffer;

  const numChannels = Math.min(2, sourceBuffer.numberOfChannels);
  const numFrames   = sourceBuffer.length;
  const targetFrames = Math.round(targetDurationSec * sr);

  // Interleave input.
  const interleaved = new Float32Array(numFrames * 2);
  const chL = sourceBuffer.getChannelData(0);
  const chR = numChannels > 1 ? sourceBuffer.getChannelData(1) : chL;
  for (let i = 0; i < numFrames; i++) {
    interleaved[i * 2]     = chL[i];
    interleaved[i * 2 + 1] = chR[i];
  }

  const st = new SoundTouch();
  st.tempo          = tempo;
  st.rate           = 1;
  st.pitchSemitones = 0;

  let readPos = 0;
  const source = {
    extract: function(target, numFramesRequested) {
      const remaining = Math.floor((interleaved.length - readPos) / 2);
      const toRead = Math.min(remaining, numFramesRequested);
      for (let i = 0; i < toRead * 2; i++) {
        target[i] = interleaved[readPos + i];
      }
      readPos += toRead * 2;
      return toRead;
    },
  };
  const filter = new SimpleFilter(source, st);

  const outputChunks = [];
  const BLOCK = 4096;
  let totalOutFrames = 0;
  const YIELD_EVERY = 8;
  let blockCount = 0;
  // Hard cap on output frames at 5× expected target — guards against
  // pathological cases where SoundTouch streams forever on bad params.
  const maxOutFrames = targetFrames * 5 + 8192;
  while (totalOutFrames < maxOutFrames) {
    const tmp = new Float32Array(BLOCK * 2);
    const got = filter.extract(tmp, BLOCK);
    if (got === 0) break;
    if (got < BLOCK) {
      outputChunks.push(tmp.subarray(0, got * 2));
    } else {
      outputChunks.push(tmp);
    }
    totalOutFrames += got;
    blockCount++;
    if (blockCount % YIELD_EVERY === 0) {
      await new Promise(function(r){ setTimeout(r, 0); });
    }
  }

  // Stitch + build output buffer at EXACTLY targetFrames length.
  const outInter = new Float32Array(totalOutFrames * 2);
  let writeOff = 0;
  for (const chunk of outputChunks) {
    outInter.set(chunk, writeOff);
    writeOff += chunk.length;
  }
  const out = audioCtx.createBuffer(numChannels, targetFrames, sr);
  const outL = out.getChannelData(0);
  const outR = numChannels > 1 ? out.getChannelData(1) : null;
  const copyFrames = Math.min(totalOutFrames, targetFrames);
  for (let i = 0; i < copyFrames; i++) {
    outL[i] = outInter[i * 2];
    if (outR) outR[i] = outInter[i * 2 + 1];
  }
  return out;
}

function buildEmptyDrumPattern(kitMeta, name, lengthSteps) {
  const len = lengthSteps && lengthSteps > 0 ? lengthSteps : DRUM_DEFAULT_LENGTH_STEPS;
  const channels = (kitMeta && kitMeta.pads ? kitMeta.pads : []).map(function(pad){
    return {
      padId: pad.id,
      steps: new Array(len).fill(0),  // 0 = off (was `false` pre-Phase-2A)
      volume: 1.0,
      muted: false,
      soloed: false,
      swing: 0,        // 0..0.5: how far to shift even 16ths later
      // Phase 3 additions — both safe defaults so old projects round-trip:
      chokeEnabled: false,   // FL Studio's "Cut Itself" toggle. When true AND
                             // the kit's pad belongs to a chokeGroup, firing
                             // this channel cuts any previous hit in that group.
      customName: "",        // per-channel display-name override. "" = use
                             // the kit's pad.name verbatim. User can rename
                             // a "Kick" channel to "808 Kick" without
                             // touching the underlying kit definition.
    };
  });
  return {
    id: "pat_" + Date.now() + "_" + Math.floor(Math.random()*10000),
    name: name || "Pattern 1",
    lengthSteps: len,
    stepsPerBeat: DRUM_DEFAULT_STEPS_PER_BEAT,
    channels: channels,
  };
}

// Resolve a channel's display name. Falls back gracefully through:
//   1. channel.customName  — user-renamed override
//   2. kit pad.name        — the kit's bundled name
//   3. channel.padId       — final fallback (kit metadata missing entirely)
// Used everywhere we render a pad label so renaming Just Works without
// hunting through render sites.
function drumChannelDisplayName(channel, kitPad) {
  if (!channel) return "";
  if (channel.customName && String(channel.customName).trim()) {
    return String(channel.customName).trim();
  }
  if (kitPad && kitPad.name) return kitPad.name;
  return channel.padId || "Pad";
}

// ── Per-channel mode (steps vs notes) ──────────────────────────────────────
// FL-style: a single drum pad can be "sent to piano roll" — its step pattern
// is replaced with a free MIDI note list (with pitch + duration), enabling
// rolls, flams, pitched samples that the 16-step grid can't represent.
// Default root pitch is MIDI 60 (C4) — that pitch plays the sample at its
// native rate; other pitches re-pitch via playbackRate = 2^((p-root)/12).
const DRUM_CHANNEL_ROOT_PITCH = 60;

// Resolve a channel's mode. Old channels have no `mode` field → "steps".
function drumChannelModeOf(channel) {
  if (!channel) return "steps";
  return channel.mode === "notes" ? "notes" : "steps";
}

// Convert a channel's steps[] into a notes[] array (project-local seconds).
// Each active step becomes one note at its quantized position + swing + shift,
// duration = one step. All notes land at the channel's root pitch.
// Velocity preserved per step. Used when the user picks "Send to piano roll."
function drumChannelStepsToNotes(channel, pattern, bpm) {
  const stepSec = drumStepDurationSec(bpm, pattern.stepsPerBeat || 4);
  const out = [];
  const steps = channel.steps || [];
  let nid = 0;
  for (let i = 0; i < steps.length; i++) {
    const params = stepParams(steps[i]);
    if (params.vel < DRUM_MIN_VELOCITY) continue;
    const swingOff = drumStepSwingOffset(i, channel.swing, stepSec);
    const shiftOff = (params.shift || 0) * stepSec;
    const start = i * stepSec + swingOff + shiftOff;
    out.push({
      id: "n_" + Date.now() + "_" + (nid++),
      pitch: DRUM_CHANNEL_ROOT_PITCH,
      start: Math.max(0, start),
      duration: stepSec,
      velocity: Math.max(0.05, Math.min(1.5, params.vel)),
    });
  }
  return out;
}

// Convert a channel's notes[] array back into steps[]. Each note is rounded
// to its nearest step. If multiple notes land on the same step, the loudest
// wins. Non-root pitches are kept but their pitch is lost in the conversion
// (steps don't carry pitch — they always play the sample at root rate). Used
// when the user picks "Send back to steps."
function drumChannelNotesToSteps(channel, pattern, bpm) {
  const stepSec = drumStepDurationSec(bpm, pattern.stepsPerBeat || 4);
  const len = pattern.lengthSteps || 16;
  const steps = new Array(len).fill(0);
  const velMap = {};   // step index → highest velocity seen
  const notes = channel.notes || [];
  for (let i = 0; i < notes.length; i++) {
    const n = notes[i];
    const idx = Math.max(0, Math.min(len - 1, Math.round((n.start || 0) / stepSec)));
    const v = Math.max(0.05, Math.min(1.5, n.velocity == null ? 0.85 : n.velocity));
    if (velMap[idx] == null || v > velMap[idx]) {
      velMap[idx] = v;
    }
  }
  Object.keys(velMap).forEach(function(k){
    const idx = parseInt(k, 10);
    steps[idx] = { vel: velMap[k] };
  });
  return steps;
}

// Re-shape a pattern's channels to match a NEW kit by INDEX, not by padId.
// Used when the user switches a track's kit on the fly (kit picker dropdown).
//
// The remap rule (per the user's Phase 3 decision):
//   - new channels[i] takes the OLD channel's steps/volume/mute/solo/swing
//     if a corresponding old channel exists at the same index;
//   - new channels[i].padId is set to newKitMeta.pads[i].id;
//   - any extra old channels (when new kit has fewer pads) are dropped;
//   - any new channels beyond old length get fresh defaults.
//
// Trade-off: the user knows this is "fast but pads may not align" —
// e.g. renaming Kick → Snare in the new kit's first slot will route the
// kick hits to a snare sample. They opted for this because it preserves
// the rhythm even if the timbre changes. Far better than silently dropping
// hits when padIds don't match across kits.
function remapPatternToKit(pattern, newKitMeta) {
  if (!pattern || !newKitMeta || !newKitMeta.pads) return pattern;
  const oldChannels = pattern.channels || [];
  const patLen = pattern.lengthSteps || DRUM_DEFAULT_LENGTH_STEPS;
  const newChannels = newKitMeta.pads.map(function(pad, i){
    const old = oldChannels[i];
    if (old) {
      // Preserve step grid + per-channel mix settings, retarget padId.
      return {
        padId: pad.id,
        steps: old.steps || new Array(patLen).fill(0),
        volume: old.volume == null ? 1.0 : old.volume,
        muted: !!old.muted,
        soloed: !!old.soloed,
        swing: old.swing || 0,
        chokeEnabled: !!old.chokeEnabled,
        customName: old.customName || "",   // keep user's rename across kit swap
        // Preserve per-channel length override (polyrhythm) if any
        lengthSteps: old.lengthSteps,
      };
    }
    // Fresh channel for an extra pad slot in the new kit
    return {
      padId: pad.id,
      steps: new Array(patLen).fill(0),
      volume: 1.0,
      muted: false,
      soloed: false,
      swing: 0,
      chokeEnabled: false,
      customName: "",
    };
  });
  return Object.assign({}, pattern, { channels: newChannels });
}

// ═════════════════════════════════════════════════════════════════════════════
//  ManageKitsOverlay — Phase 3 admin-only kit editor
// ═════════════════════════════════════════════════════════════════════════════
// Full-screen modal that lets admins create/edit/delete drum kits.
// Lives at module scope (not inside Studio) so it doesn't pull Studio's full
// closure into render diffs every time a kit is being edited.
//
// Props:
//   apiBase       — API_BASE constant (we don't import it directly to avoid
//                   any circular reset across hot-reload)
//   adminKits     — the current catalogue from Studio state (re-fetched after
//                   save/delete to refresh the list)
//   onReload      — function returning a promise; Studio's reloadAdminKits
//   onClose       — close the overlay
//   isAdmin       — gates everything. Should never render with false in prod,
//                   but a belt-and-braces check inside means a bad route can't
//                   silently expose the editor.
//   getToken      — auth bearer token getter (passed in to avoid scope reach)
//
// State shape:
//   { mode: "list", editing: null }                 — kit catalogue view
//   { mode: "edit", editing: { id?, name, author, pads: [...] } }  — form
function ManageKitsOverlay({ apiBase, adminKits, onReload, onClose, isAdmin, getToken }) {
  const [mode, setMode] = React.useState("list");
  const [editing, setEditing] = React.useState(null); // null = creating new
  const [busy, setBusy]       = React.useState(false);
  const [err, setErr]         = React.useState("");
  const [uploadingPad, setUploadingPad] = React.useState(null); // pad index being uploaded

  if (!isAdmin) {
    return (
      <div style={{
        position:"fixed", inset:0, zIndex:10300,
        background:"rgba(0,0,0,0.85)",
        display:"flex", alignItems:"center", justifyContent:"center",
        color:"#aaa", fontFamily:"'DM Sans',sans-serif",
      }}>
        <div>Admin only.</div>
      </div>
    );
  }

  // Start editing — either a new blank kit or an existing one cloned for edit.
  // We always work on a COPY so cancel doesn't mutate the live catalogue.
  const startNew = function() {
    setEditing({
      id: null,
      name: "New Kit",
      author: "BeatFinder",
      // Seed with 8 empty slots — typical kit shape. The chokeGroup field
      // is kept (always null) for backward compat with existing backend
      // records — self-choke doesn't use it but the API still accepts it.
      pads: [
        { id: "kick",       name: "Kick",       color: "#D97070", url: "", chokeGroup: null },
        { id: "snare",      name: "Snare",      color: "#5FA8A2", url: "", chokeGroup: null },
        { id: "closed-hat", name: "Closed Hat", color: "#7FB5AB", url: "", chokeGroup: null },
        { id: "open-hat",   name: "Open Hat",   color: "#8FB8A9", url: "", chokeGroup: null },
        { id: "clap",       name: "Clap",       color: "#C77575", url: "", chokeGroup: null },
        { id: "perc",       name: "Perc",       color: "#9C8AC4", url: "", chokeGroup: null },
        { id: "808",        name: "808",        color: "#C99AB2", url: "", chokeGroup: null },
        { id: "crash",      name: "Crash",      color: "#D4B147", url: "", chokeGroup: null },
      ],
    });
    setErr("");
    setMode("edit");
  };
  const startEdit = function(kit) {
    setEditing({
      id: kit.id,
      name: kit.name,
      author: kit.author || "BeatFinder",
      // Deep-copy pads so edits don't mutate the catalogue
      pads: (kit.pads || []).map(function(p){ return Object.assign({}, p); }),
    });
    setErr("");
    setMode("edit");
  };
  const updatePad = function(idx, patch) {
    setEditing(function(prev){
      if (!prev) return prev;
      const pads = prev.pads.slice();
      pads[idx] = Object.assign({}, pads[idx], patch);
      return Object.assign({}, prev, { pads: pads });
    });
  };
  const addPadSlot = function() {
    setEditing(function(prev){
      if (!prev) return prev;
      // Stable default id — admin should rename. Random suffix keeps them unique.
      const nextId = "pad" + (prev.pads.length + 1);
      const pads = prev.pads.concat([{
        id: nextId, name: "Pad " + (prev.pads.length + 1),
        color: "#888888", url: "", chokeGroup: null,
      }]);
      return Object.assign({}, prev, { pads: pads });
    });
  };
  const removePadSlot = function(idx) {
    setEditing(function(prev){
      if (!prev) return prev;
      const pads = prev.pads.slice();
      pads.splice(idx, 1);
      return Object.assign({}, prev, { pads: pads });
    });
  };

  // Upload a single WAV. Uses raw fetch (not apiFetch) because we send a
  // FormData body — apiFetch hard-codes Content-Type: application/json.
  const uploadSample = function(padIdx, file) {
    if (!file) return;
    if (file.size > 2 * 1024 * 1024) {
      setErr("Sample too large — max 2MB per pad");
      return;
    }
    setUploadingPad(padIdx);
    setErr("");
    const fd = new FormData();
    fd.append("file", file);
    const token = getToken ? (getToken() || "") : "";
    fetch(apiBase + "/api/admin/drum-kits/upload-sample", {
      method: "POST",
      headers: { Authorization: "Bearer " + token },
      body: fd,
    })
      .then(function(r){
        return r.json().then(function(data){
          if (!r.ok) throw new Error(data.detail || ("HTTP " + r.status));
          return data;
        });
      })
      .then(function(data){
        updatePad(padIdx, { url: data.url });
        setUploadingPad(null);
      })
      .catch(function(e){
        setErr(e.message || "Upload failed");
        setUploadingPad(null);
      });
  };

  // Save the kit (POST new, PUT existing). The backend validates and returns
  // the saved kit; we reload the catalogue so any other open Studio views
  // pick up the change.
  const saveKit = function() {
    if (!editing) return;
    setBusy(true);
    setErr("");
    const body = {
      name: editing.name,
      author: editing.author,
      pads: editing.pads.map(function(p){
        return {
          id: p.id, name: p.name, color: p.color, url: p.url,
          chokeGroup: p.chokeGroup || null,
        };
      }),
    };
    const path = editing.id
      ? "/api/admin/drum-kits/" + encodeURIComponent(editing.id)
      : "/api/admin/drum-kits";
    const method = editing.id ? "PUT" : "POST";
    const token = getToken ? (getToken() || "") : "";
    fetch(apiBase + path, {
      method: method,
      headers: {
        "Content-Type": "application/json",
        Authorization: "Bearer " + token,
      },
      body: JSON.stringify(body),
    })
      .then(function(r){
        return r.json().then(function(data){
          if (!r.ok) throw new Error(data.detail || ("HTTP " + r.status));
          return data;
        });
      })
      .then(function(){
        return onReload ? onReload() : null;
      })
      .then(function(){
        setBusy(false);
        setMode("list");
        setEditing(null);
      })
      .catch(function(e){
        setErr(e.message || "Save failed");
        setBusy(false);
      });
  };

  const deleteKit = function(kit) {
    if (!kit || !kit.id) return;
    // Built-in kits never come through this list, so a direct delete is fine.
    if (!window.confirm("Delete kit \"" + kit.name + "\"? Tracks using it will fall back to the default kit.")) return;
    setBusy(true);
    const token = getToken ? (getToken() || "") : "";
    fetch(apiBase + "/api/admin/drum-kits/" + encodeURIComponent(kit.id), {
      method: "DELETE",
      headers: { Authorization: "Bearer " + token },
    })
      .then(function(r){
        if (!r.ok) return r.json().then(function(d){ throw new Error(d.detail || ("HTTP " + r.status)); });
      })
      .then(function(){ return onReload ? onReload() : null; })
      .then(function(){ setBusy(false); })
      .catch(function(e){ setErr(e.message || "Delete failed"); setBusy(false); });
  };

  // ── Render ──
  return (
    <div style={{
      position:"fixed", inset:0, zIndex:10300,
      background:`
        radial-gradient(ellipse 80% 60% at 18% -10%, rgba(139,92,246,0.10) 0%, transparent 70%),
        radial-gradient(ellipse 70% 50% at 95% 110%, rgba(124,58,237,0.07) 0%, transparent 70%),
        linear-gradient(180deg, rgba(167,139,250,0.025) 0%, transparent 30%),
        repeating-linear-gradient(0deg, rgba(255,255,255,0.008) 0px, rgba(255,255,255,0.008) 1px, transparent 1px, transparent 3px),
        #070611
      `,
      backdropFilter:"blur(24px) saturate(140%)",
      display:"flex", flexDirection:"column",
      paddingTop:    "var(--bf-safe-top,    env(safe-area-inset-top))",
      paddingBottom: "var(--bf-safe-bottom, env(safe-area-inset-bottom))",
      paddingLeft:   "var(--bf-safe-left,   env(safe-area-inset-left))",
      paddingRight:  "var(--bf-safe-right,  env(safe-area-inset-right))",
      color:"#F8F7FA", fontFamily:"'DM Sans',sans-serif",
    }}>
      {/* Top bar */}
      <div style={{
        padding:"16px 20px",
        borderBottom:"1px solid rgba(167,139,250,0.10)",
        display:"flex", alignItems:"center", gap:14,
        background:"linear-gradient(180deg, rgba(167,139,250,0.04) 0%, transparent 100%)",
        boxShadow: "inset 0 1px 0 rgba(255,255,255,0.02), 0 1px 0 rgba(0,0,0,0.4)",
      }}>
        <div style={{
          width:32, height:32, borderRadius:9,
          background:"linear-gradient(135deg,#8B5CF6 0%,#7C3AED 100%)",
          display:"flex", alignItems:"center", justifyContent:"center",
          fontSize:15, fontWeight:900, color:"white",
          boxShadow:
            "0 0 20px rgba(139,92,246,0.35), " +
            "inset 0 1px 0 rgba(255,255,255,0.25), " +
            "inset 0 -1px 0 rgba(0,0,0,0.2)",
        }}>⚙</div>
        <div style={{ flex:1 }}>
          <div style={{ fontSize:9, fontWeight:700, color:"#7A7488", letterSpacing:1.8, textTransform:"uppercase" }}>Admin</div>
          <div style={{ fontSize:15, fontWeight:700, color:"#F8F7FA", marginTop:1, letterSpacing:0.1 }}>
            {mode === "list" ? "Drum Kits" : (editing && editing.id ? "Edit Kit" : "New Kit")}
          </div>
        </div>
        {mode === "edit" && (
          <button onClick={function(){ setMode("list"); setEditing(null); setErr(""); }}
            style={{
              background:"rgba(20,15,30,0.4)", color:"#9590A8",
              border:"1px solid rgba(167,139,250,0.10)",
              borderRadius:9, padding:"7px 14px",
              fontSize:12, fontWeight:700, cursor:"pointer",
              letterSpacing: 0.4,
            }}>← Back</button>
        )}
        <button onClick={onClose} style={{
          background:"rgba(20,15,30,0.4)", color:"#9590A8",
          border:"1px solid rgba(167,139,250,0.10)",
          borderRadius:9, padding:"7px 11px",
          fontSize:18, lineHeight:1, cursor:"pointer",
          fontWeight: 300, fontFamily:"system-ui",
        }}>×</button>
      </div>

      {err && (
        <div style={{
          padding:"12px 20px",
          background:"rgba(239,68,68,0.08)",
          borderBottom:"1px solid rgba(239,68,68,0.20)",
          color:"#FCA5A5", fontSize:12, fontWeight:600,
          letterSpacing: 0.2,
        }}>{err}</div>
      )}

      {/* Body */}
      <div
        ref={function(el){
          if (!el || el._bfRackScrollHookInstalled) return;
          el._bfRackScrollHookInstalled = true;
          el.addEventListener("touchmove", function(ev){
            ev.stopPropagation();
          }, { passive: true });
        }}
        style={{ flex:1, overflow:"auto", padding:"16px 18px" }}>
        {mode === "list" && (
          <div>
            <button onClick={startNew} disabled={busy} style={{
              background:"linear-gradient(135deg,#A78BFA 0%,#8B5CF6 50%,#7C3AED 100%)",
              color:"white", border:"none",
              borderRadius:11, padding:"11px 20px",
              fontSize:12, fontWeight:800, cursor:"pointer",
              marginBottom:20, letterSpacing:0.8,
              boxShadow:
                "0 8px 24px rgba(139,92,246,0.4), " +
                "inset 0 1px 0 rgba(255,255,255,0.25)",
              textTransform: "uppercase",
            }}>+  New Kit</button>
            {(!adminKits || adminKits.length === 0) ? (
              <div style={{ color:"#7A7488", fontSize:13, textAlign:"center", padding:"60px 12px", lineHeight: 1.6 }}>
                No custom kits yet. The built-in “Default Kit” is always available; add your own to extend the picker.
              </div>
            ) : (
              <div style={{ display:"flex", flexDirection:"column", gap:10 }}>
                {adminKits.map(function(k){
                  return (
                    <div key={k.id} style={{
                      background:"rgba(20,15,30,0.55)",
                      backdropFilter:"blur(20px) saturate(140%)",
                      WebkitBackdropFilter:"blur(20px) saturate(140%)",
                      border:"1px solid rgba(167,139,250,0.10)",
                      borderRadius:13, padding:16,
                      display:"flex", alignItems:"center", gap:12,
                      boxShadow:
                        "inset 0 1px 0 rgba(255,255,255,0.03)",
                    }}>
                      <div style={{ flex:1 }}>
                        <div style={{ fontSize:14, fontWeight:700, color: "#F8F7FA", letterSpacing: 0.1 }}>{k.name}</div>
                        <div style={{ fontSize:11, color:"#7A7488", marginTop:3, letterSpacing: 0.2 }}>
                          {(k.pads || []).length} pads · by {k.author || "BeatFinder"}
                        </div>
                      </div>
                      <button onClick={function(){ startEdit(k); }} style={{
                        background:"rgba(139,92,246,0.12)", color:"#C4B5FD",
                        border:"1px solid rgba(167,139,250,0.3)",
                        borderRadius:8, padding:"7px 14px",
                        fontSize:11, fontWeight:700, cursor:"pointer",
                        letterSpacing: 0.8,
                      }}>EDIT</button>
                      <button onClick={function(){ deleteKit(k); }} disabled={busy} style={{
                        background:"rgba(239,68,68,0.06)", color:"#F87171",
                        border:"1px solid rgba(239,68,68,0.22)",
                        borderRadius:8, padding:"7px 14px",
                        fontSize:11, fontWeight:700, cursor:"pointer",
                        letterSpacing: 0.8,
                      }}>DELETE</button>
                    </div>
                  );
                })}
              </div>
            )}
          </div>
        )}

        {mode === "edit" && editing && (
          <div>
            {/* Top form fields */}
            <div style={{ display:"flex", gap:12, flexWrap:"wrap", marginBottom:22 }}>
              <div style={{ flex:"1 1 240px", minWidth:200 }}>
                <div style={{ fontSize:9, fontWeight:700, color:"#7A7488", letterSpacing:1.8, textTransform:"uppercase", marginBottom:6 }}>Kit name</div>
                <input value={editing.name}
                  onChange={function(e){ setEditing(Object.assign({}, editing, { name: e.target.value.slice(0,80) })); }}
                  style={{
                    width:"100%", boxSizing:"border-box",
                    background:"rgba(10,8,18,0.6)",
                    border:"1px solid rgba(167,139,250,0.22)",
                    borderRadius:10, padding:"10px 12px",
                    color:"#F8F7FA", fontSize:13, fontWeight:600,
                    fontFamily:"'DM Sans',sans-serif", outline:"none",
                    letterSpacing: 0.1,
                  }}/>
              </div>
              <div style={{ flex:"1 1 200px", minWidth:180 }}>
                <div style={{ fontSize:9, fontWeight:700, color:"#7A7488", letterSpacing:1.8, textTransform:"uppercase", marginBottom:6 }}>Author</div>
                <input value={editing.author}
                  onChange={function(e){ setEditing(Object.assign({}, editing, { author: e.target.value.slice(0,80) })); }}
                  style={{
                    width:"100%", boxSizing:"border-box",
                    background:"rgba(10,8,18,0.6)",
                    border:"1px solid rgba(167,139,250,0.10)",
                    borderRadius:10, padding:"10px 12px",
                    color:"#F8F7FA", fontSize:13, fontWeight:600,
                    fontFamily:"'DM Sans',sans-serif", outline:"none",
                    letterSpacing: 0.1,
                  }}/>
              </div>
            </div>

            {/* Pads */}
            <div style={{ fontSize:10, fontWeight:700, color:"#7A7488", letterSpacing:1.8, textTransform:"uppercase", marginBottom:10 }}>
              Pads · {editing.pads.length}
            </div>
            <div style={{ display:"flex", flexDirection:"column", gap:10, marginBottom:16 }}>
              {editing.pads.map(function(p, i){
                const uploading = uploadingPad === i;
                return (
                  <div key={i} style={{
                    background:"rgba(20,15,30,0.55)",
                    backdropFilter:"blur(20px) saturate(140%)",
                    WebkitBackdropFilter:"blur(20px) saturate(140%)",
                    border:"1px solid rgba(167,139,250,0.10)",
                    borderRadius:12, padding:12,
                    display:"flex", flexDirection:"column", gap:10,
                    boxShadow: "inset 0 1px 0 rgba(255,255,255,0.03)",
                  }}>
                    <div style={{ display:"flex", alignItems:"center", gap:8, flexWrap:"wrap" }}>
                      {/* Color swatch / picker */}
                      <input type="color" value={p.color || "#888888"}
                        onChange={function(e){ updatePad(i, { color: e.target.value }); }}
                        style={{ width:34, height:34, border:"1px solid rgba(167,139,250,0.15)", padding:0, background:"transparent", cursor:"pointer", borderRadius:8 }}/>
                      {/* Pad id (stable — used by patterns) */}
                      <input value={p.id} placeholder="pad-id"
                        onChange={function(e){
                          // Keep ids URL-safe-ish — alnum + dash/underscore.
                          const v = e.target.value.toLowerCase().replace(/[^a-z0-9_-]/g,"").slice(0,40);
                          updatePad(i, { id: v });
                        }}
                        style={{
                          width:110,
                          background:"rgba(10,8,18,0.6)",
                          border:"1px solid rgba(167,139,250,0.08)",
                          borderRadius:8, padding:"7px 10px",
                          color:"#9590A8", fontSize:11, fontWeight:600,
                          fontFamily:"monospace", outline:"none",
                          letterSpacing: 0.2,
                        }}/>
                      {/* Pad name (display) */}
                      <input value={p.name} placeholder="Display name"
                        onChange={function(e){ updatePad(i, { name: e.target.value.slice(0,40) }); }}
                        style={{
                          flex:"1 1 140px", minWidth:120,
                          background:"rgba(10,8,18,0.6)",
                          border:"1px solid rgba(167,139,250,0.08)",
                          borderRadius:8, padding:"7px 10px",
                          color:"#F8F7FA", fontSize:12, fontWeight:600,
                          fontFamily:"'DM Sans',sans-serif", outline:"none",
                          letterSpacing: 0.1,
                        }}/>
                      <button onClick={function(){ removePadSlot(i); }} style={{
                        background:"rgba(239,68,68,0.06)", color:"#F87171",
                        border:"1px solid rgba(239,68,68,0.22)",
                        borderRadius:7, padding:"5px 10px",
                        fontSize:10, fontWeight:700, cursor:"pointer",
                        letterSpacing: 0.6,
                      }}>REMOVE</button>
                    </div>
                    <div style={{ display:"flex", alignItems:"center", gap:8, flexWrap:"wrap" }}>
                      <div style={{ fontSize:9, fontWeight:700, color:"#7A7488", letterSpacing:1.8, textTransform:"uppercase", minWidth:60 }}>Sample</div>
                      <label style={{
                        background: p.url ? "rgba(16,185,129,0.08)" : "rgba(139,92,246,0.10)",
                        color: p.url ? "#34D399" : "#C4B5FD",
                        border:"1px solid " + (p.url ? "rgba(16,185,129,0.28)" : "rgba(167,139,250,0.28)"),
                        borderRadius:8, padding:"7px 14px",
                        fontSize:11, fontWeight:700, cursor: uploading ? "wait" : "pointer",
                        opacity: uploading ? 0.5 : 1,
                        letterSpacing: 0.6,
                      }}>
                        {uploading ? "Uploading…" : (p.url ? "✓  Replace" : "↑  Upload WAV")}
                        <input type="file" accept="audio/wav,audio/mpeg,audio/ogg,audio/mp3"
                          disabled={uploading}
                          onChange={function(e){
                            const f = e.target.files && e.target.files[0];
                            if (f) uploadSample(i, f);
                            e.target.value = "";
                          }}
                          style={{ display:"none" }}/>
                      </label>
                      {p.url && (
                        <button onClick={function(){
                          try {
                            // Quick preview using a fresh one-shot AudioContext.
                            const Cx = window.AudioContext || window.webkitAudioContext;
                            if (!Cx) return;
                            const ctx = new Cx();
                            fetch(p.url).then(function(r){ return r.arrayBuffer(); })
                              .then(function(ab){ return ctx.decodeAudioData(ab); })
                              .then(function(buf){
                                const s = ctx.createBufferSource();
                                s.buffer = buf; s.connect(ctx.destination); s.start();
                                // Auto-close ctx after the buffer finishes so we
                                // don't leak hardware audio resources on each preview.
                                setTimeout(function(){ try { ctx.close(); } catch(_) {} },
                                  Math.ceil(buf.duration * 1000) + 200);
                              }).catch(function(e){ console.warn("preview failed", e); });
                          } catch(_) {}
                        }} style={{
                          background:"rgba(20,15,30,0.4)", color:"#9590A8",
                          border:"1px solid rgba(167,139,250,0.10)",
                          borderRadius:7, padding:"6px 11px",
                          fontSize:10, fontWeight:700, cursor:"pointer",
                          letterSpacing: 0.6,
                        }}>▶  Preview</button>
                      )}
                      {p.url && (
                        <div style={{
                          fontSize:10, color:"#5C5870",
                          overflow:"hidden", textOverflow:"ellipsis", whiteSpace:"nowrap",
                          flex:"1 1 100px", minWidth:0,
                          fontFamily:"monospace",
                          letterSpacing: 0.2,
                        }} title={p.url}>{p.url.split("/").pop()}</div>
                      )}
                    </div>
                    {/* Choke is configured PER CHANNEL in the rack (the "Cut
                        Itself" toggle in the pad-settings modal), not on the
                        kit definition itself. A pad sample doesn't decide
                        whether to choke — the user does, per pattern. So
                        there's nothing to set here at the kit level. */}
                  </div>
                );
              })}
            </div>

            <button onClick={addPadSlot} style={{
              background:"rgba(20,15,30,0.3)", color:"#9590A8",
              border:"1px dashed rgba(167,139,250,0.18)",
              borderRadius:10, padding:"10px 14px",
              fontSize:11, fontWeight:700, cursor:"pointer",
              marginBottom:22, width:"100%",
              letterSpacing: 0.6,
              textTransform: "uppercase",
            }}>+  Add Pad Slot</button>

            {/* Save / Delete bar */}
            <div style={{ display:"flex", gap:8, justifyContent:"flex-end" }}>
              {editing.id && (
                <button onClick={function(){ deleteKit({ id: editing.id, name: editing.name }); }}
                  disabled={busy} style={{
                  background:"rgba(239,68,68,0.06)", color:"#F87171",
                  border:"1px solid rgba(239,68,68,0.22)",
                  borderRadius:10, padding:"11px 18px",
                  fontSize:11, fontWeight:700, cursor:"pointer",
                  marginRight:"auto",
                  letterSpacing: 0.8,
                  textTransform: "uppercase",
                }}>Delete Kit</button>
              )}
              <button onClick={function(){ setMode("list"); setEditing(null); setErr(""); }}
                disabled={busy} style={{
                background:"rgba(20,15,30,0.4)", color:"#9590A8",
                border:"1px solid rgba(167,139,250,0.12)",
                borderRadius:10, padding:"11px 18px",
                fontSize:11, fontWeight:700, cursor:"pointer",
                letterSpacing: 0.8,
                textTransform: "uppercase",
              }}>Cancel</button>
              <button onClick={saveKit} disabled={busy} style={{
                background: busy
                  ? "rgba(60,55,75,0.4)"
                  : "linear-gradient(135deg,#A78BFA 0%,#8B5CF6 50%,#7C3AED 100%)",
                color:"white", border:"none",
                borderRadius:10, padding:"11px 22px",
                fontSize:11, fontWeight:800, cursor: busy ? "wait" : "pointer",
                boxShadow: busy
                  ? "none"
                  : "0 8px 24px rgba(139,92,246,0.4), inset 0 1px 0 rgba(255,255,255,0.25)",
                letterSpacing: 0.8,
                textTransform: "uppercase",
              }}>{busy ? "Saving…" : "Save Kit"}</button>
            </div>
          </div>
        )}
      </div>
    </div>
  );
}


// ═════════════════════════════════════════════════════════════════════════════
//  AdminSoundfontsOverlay — Phase 7 admin-only soundfont uploader
// ═════════════════════════════════════════════════════════════════════════════
// Admin-only modal for uploading SF2/SF3 soundfont files. Mirrors
// ManageKitsOverlay's shape (list + edit modes, list comes first), but
// simpler: each soundfont is one file + a small metadata block, not a
// grid of pads.
//
// Two-step upload flow:
//   1. POST /api/admin/soundfonts/upload  (file) → returns B2 URL + ids
//   2. POST /api/admin/soundfonts         (metadata + url) → saves entry
//
// The reason for two steps: the file is large (20-50 MB) and we want to
// validate metadata client-side BEFORE making the user wait through the
// upload. The flow:
//   admin taps "+ Upload" → picks file → metadata form opens with the
//   uploaded filename as default name → admin tweaks → taps Save → entry
//   is created. If admin cancels metadata, the file's already in B2 (and
//   in Mongo? no — metadata POST is what creates the entry). Orphaned B2
//   files from cancels are rare and small; we accept the tradeoff.
//
// Props:
//   apiBase                — API_BASE
//   soundfonts             — current catalogue from Studio state
//   onReload               — Studio's reloadCustomSoundfonts (Promise)
//   onClose                — close the overlay
//   isAdmin                — gate (defence in depth)
//   getToken               — bearer token getter
// Reusable Up/Down arrow button for iOS-friendly scrolling. Tapping the
// button scrolls the target container by one card-height (~70px); a
// touchstart-and-hold triggers an auto-repeat interval that fires every
// 80ms for fast multi-card navigation. Used by AdminSoundfontsOverlay
// and BankPresetSheet — anywhere native iOS touch-scroll is unreliable
// inside a nested overflow region.
function ArrowScrollButton({ direction, disabled, scrollRef, onAfter }) {
  const STEP_PX     = 72;
  const REPEAT_MS   = 80;
  const intervalRef = React.useRef(null);

  const step = function(){
    const el = scrollRef && scrollRef.current;
    if (!el) return;
    const delta = direction === "up" ? -STEP_PX : STEP_PX;
    el.scrollTop = el.scrollTop + delta;
    if (onAfter) onAfter();
  };

  const startRepeat = function(){
    if (disabled) return;
    step();
    if (intervalRef.current) clearInterval(intervalRef.current);
    intervalRef.current = setInterval(step, REPEAT_MS);
  };
  const stopRepeat = function(){
    if (intervalRef.current) {
      clearInterval(intervalRef.current);
      intervalRef.current = null;
    }
  };

  React.useEffect(function(){
    // Safety net — if component unmounts mid-repeat, clean up.
    return function(){ stopRepeat(); };
  }, []);

  return (
    <button
      onTouchStart={startRepeat}
      onTouchEnd={stopRepeat}
      onTouchCancel={stopRepeat}
      onMouseDown={startRepeat}
      onMouseUp={stopRepeat}
      onMouseLeave={stopRepeat}
      onClick={function(e){ e.preventDefault(); }}
      disabled={disabled}
      style={{
        background: disabled ? "rgba(20,15,30,0.4)" : "rgba(167,139,250,0.16)",
        color: disabled ? "#4A4458" : "#C4B5FD",
        border: "1px solid " + (disabled ? "rgba(167,139,250,0.08)" : "rgba(167,139,250,0.32)"),
        borderRadius: 7,
        padding: 0,
        width: "100%", height: 36,
        fontSize: 14, fontWeight: 700,
        cursor: disabled ? "default" : "pointer",
        touchAction: "manipulation",
        userSelect: "none", WebkitUserSelect: "none",
        display: "flex", alignItems: "center", justifyContent: "center",
      }}
    >
      {direction === "up" ? "▲" : "▼"}
    </button>
  );
}

function AdminSoundfontsOverlay({ apiBase, soundfonts, onReload, onClose, isAdmin, getToken }) {
  const [mode, setMode]       = React.useState("list");
  const [editing, setEditing] = React.useState(null);
  const [busy, setBusy]       = React.useState(false);
  const [err, setErr]         = React.useState("");
  const [uploadProgress, setUploadProgress] = React.useState(0); // 0-1
  // After file upload, hold the returned URL/ids until admin saves metadata.
  // Shape: { url, b2_file_name, b2_file_id, bytes, content_type, filename }
  const [pendingUpload, setPendingUpload] = React.useState(null);
  const fileInputRef = React.useRef(null);

  // ── List-scroll arrow controls ──
  // iOS Safari sometimes fails to recognise nested overflow-auto regions
  // as touch-scrollable, especially when they sit inside a column flex
  // parent that's already scrolling. To guarantee navigation works no
  // matter how the layout resolves on the device, we add explicit Up/
  // Down arrow buttons on the right that mutate scrollTop directly.
  // Same pattern as the BankPresetSheet's arrow column.
  const listScrollRef = React.useRef(null);
  const [arrowScrollState, setArrowScrollState] = React.useState({
    canScroll: false, atTop: true, atBottom: true,
  });
  const refreshArrowState = function(){
    const el = listScrollRef.current;
    if (!el) return;
    const max = el.scrollHeight - el.clientHeight;
    setArrowScrollState({
      canScroll: max > 4,
      atTop: el.scrollTop <= 2,
      atBottom: el.scrollTop >= max - 2,
    });
  };
  React.useEffect(function(){
    refreshArrowState();
  }, [soundfonts && soundfonts.length, mode]);

  if (!isAdmin) {
    return (
      <div style={{
        position:"fixed", inset:0, zIndex:10300,
        background:"rgba(0,0,0,0.85)",
        display:"flex", alignItems:"center", justifyContent:"center",
        color:"#aaa", fontFamily:"'DM Sans',sans-serif",
      }}>
        <div>Admin only.</div>
      </div>
    );
  }

  // Format file size for display
  const fmtBytes = function(n) {
    if (!n) return "—";
    if (n < 1024) return n + " B";
    if (n < 1024*1024) return (n/1024).toFixed(1) + " KB";
    return (n/(1024*1024)).toFixed(1) + " MB";
  };

  // Start editing an existing soundfont (metadata only — no file change)
  const startEdit = function(sf) {
    setEditing({
      id:              sf.id,
      name:            sf.name || "",
      description:     sf.description || "",
      color:           sf.color || "#F8F7FA",
      gm_program_hint: sf.gmProgramNumber || 0,
      // No file change in edit mode
      _existing: true,
    });
    setPendingUpload(null);
    setErr("");
    setMode("edit");
  };

  // Trigger the file picker. We accept anything (.sf2/.sf3 may not be
  // registered iOS file types so a restrictive `accept` could hide files).
  const triggerFilePicker = function() {
    setErr("");
    if (fileInputRef.current) {
      fileInputRef.current.value = ""; // allow re-picking same file
      fileInputRef.current.click();
    }
  };

  // File picker handler. Uploads to B2 via the backend, then opens the
  // metadata form pre-filled with the filename as a default name.
  const onFilePicked = function(ev) {
    const file = ev.target.files && ev.target.files[0];
    if (!file) return;
    const filename = file.name || "soundfont";
    const lower = filename.toLowerCase();
    if (!/\.(sf2|sf3|sfz)$/.test(lower)) {
      setErr("Please pick a .sf2, .sf3, or .sfz file");
      return;
    }
    if (file.size > 100 * 1024 * 1024) {
      setErr("File too large — max 100 MB per soundfont");
      return;
    }
    setBusy(true);
    setUploadProgress(0);
    setErr("");

    // ── Parse PHDR client-side BEFORE uploading ─────────────────────
    // We read the file as an ArrayBuffer and call bfSfParsePresetList to
    // Parse PHDR client-side BEFORE uploading. We use the result to:
    //   - Default the name (if 1 preset, use its name; else use bank stub)
    //   - Default the program number (if 1 preset, use its program)
    //   - Attach the full preset list to the entry so the piano roll can
    //     show a sub-picker for multi-preset banks
    //
    // Use FileReader rather than file.arrayBuffer() — the latter isn't
    // available on iOS Safari before 14.0 and we've seen it silently
    // fail on some versions.
    const stub = filename.replace(/\.[^.]+$/, "");
    setErr("Reading file… (" + Math.round(file.size / (1024*1024)) + " MB)");
    const reader = new FileReader();
    reader.onload = function(evt){
      const arrBuf = evt.target && evt.target.result;
      if (!arrBuf) {
        setBusy(false);
        setErr("FileReader returned empty result");
        return;
      }
      setErr("");
      let presets = [];
      try {
        presets = (typeof bfSfParsePresetList === "function")
          ? (bfSfParsePresetList(arrBuf) || []) : [];
      } catch(e){
        console.warn("[admin] phdr parse failed", e);
        presets = [];
      }
      // Always single-upload path. The preset list is attached to the
      // single entry's metadata so the piano roll can show a sub-picker.
      uploadSingleFile(file, filename, presets);
    };
    reader.onerror = function(){
      setBusy(false);
      const err = reader.error;
      setErr("FileReader error: " + (err && err.message || err));
    };
    reader.readAsArrayBuffer(file);
  };

  // Single-file upload path — used for single-preset SFs and for fall-back
  // when PHDR parsing turns up nothing useful.
  const uploadSingleFile = function(file, filename, parsedPresetsList){
    setBusy(true);
    setUploadProgress(0);
    // Use XMLHttpRequest so we can track upload progress (fetch doesn't
    // expose upload progress events on most browsers).
    const xhr = new XMLHttpRequest();
    xhr.open("POST", apiBase + "/api/admin/soundfonts/upload");
    const token = getToken && getToken();
    if (token) xhr.setRequestHeader("Authorization", "Bearer " + token);
    xhr.upload.onprogress = function(e) {
      if (e.lengthComputable) {
        setUploadProgress(e.loaded / e.total);
      }
    };
    xhr.onload = function() {
      setBusy(false);
      setUploadProgress(0);
      if (xhr.status !== 200) {
        let msg = "Upload failed (" + xhr.status + ")";
        try {
          const data = JSON.parse(xhr.responseText);
          if (data && data.detail) msg = data.detail;
        } catch(_){}
        setErr(msg);
        return;
      }
      let result;
      try { result = JSON.parse(xhr.responseText); }
      catch(_) { setErr("Server returned invalid response"); return; }
      if (result.warning) setErr(result.warning);  // non-fatal warning
      setPendingUpload(result);
      // Default name from filename (strip extension). If we parsed a
      // single preset, use ITS name instead — much more meaningful
      // ("Grand Piano" vs "GeneralUser-GS"). For multi-preset banks,
      // use the filename stub (e.g. "GeneralUser GS") — the individual
      // preset names live in `presets_data` for the piano-roll picker.
      const stub = filename.replace(/\.[^.]+$/, "");
      const isMulti = parsedPresetsList && parsedPresetsList.length > 1;
      const singlePreset = (parsedPresetsList && parsedPresetsList.length === 1) ? parsedPresetsList[0] : null;
      setEditing({
        id:              null,
        name:            singlePreset ? singlePreset.name : stub,
        description:     isMulti ? (parsedPresetsList.length + " presets in this bank") : "",
        color:           "#F8F7FA",
        gm_program_hint: singlePreset ? singlePreset.program : (isMulti && parsedPresetsList[0] ? parsedPresetsList[0].program : 0),
        gm_bank_hint:    singlePreset ? singlePreset.bank    : (isMulti && parsedPresetsList[0] ? parsedPresetsList[0].bank    : 0),
        // Stash the full parsed list onto the editing record. saveEntry
        // includes it in the POST body so the backend can store it on
        // the soundfont doc. The piano roll later reads this to know
        // whether the entry is a bank (>1 presets) and to populate its
        // sub-picker sheet.
        presets_data:    parsedPresetsList || [],
        _existing:       false,
      });
      setMode("edit");
    };
    xhr.onerror = function() {
      setBusy(false);
      setUploadProgress(0);
      setErr("Network error during upload");
    };

    const fd = new FormData();
    fd.append("file", file);
    xhr.send(fd);
  };


  // Save metadata. For new entries: POST with the pendingUpload fields.
  // For existing entries: PUT with just the editable fields.
  const saveEntry = function() {
    if (!editing) return;
    if (!editing.name || !editing.name.trim()) {
      setErr("Name is required");
      return;
    }
    setBusy(true);
    setErr("");

    const isNew = !editing._existing;
    const url = isNew
      ? "/api/admin/soundfonts"
      : "/api/admin/soundfonts/" + encodeURIComponent(editing.id);
    const method = isNew ? "POST" : "PUT";

    const body = {
      name:            editing.name.trim(),
      description:     editing.description || "",
      color:           editing.color || "#F8F7FA",
      gm_program_hint: editing.gm_program_hint || 0,
      gm_bank_hint:    editing.gm_bank_hint    || 0,
    };
    if (isNew && pendingUpload) {
      body.b2_url          = pendingUpload.url;
      body.b2_file_name    = pendingUpload.b2_file_name;
      body.b2_file_id      = pendingUpload.b2_file_id;
      body.file_size_bytes = pendingUpload.bytes;
      body.content_type    = pendingUpload.content_type;
      // Attach the parsed preset list (for multi-preset banks). The
      // backend stores it on the soundfont doc; the piano roll uses it
      // to show a sub-picker for switching between presets without
      // changing the underlying track instrument.
      if (editing.presets_data && editing.presets_data.length > 0) {
        body.presets = editing.presets_data;
      }
    }

    fetch(apiBase + url, {
      method: method,
      headers: {
        "Content-Type": "application/json",
        ...(getToken && getToken() ? { "Authorization": "Bearer " + getToken() } : {}),
      },
      body: JSON.stringify(body),
    })
      .then(function(resp){
        if (!resp.ok) {
          return resp.json().then(function(d){
            throw new Error((d && d.detail) || ("Save failed (" + resp.status + ")"));
          }, function(){
            throw new Error("Save failed (" + resp.status + ")");
          });
        }
        return resp.json();
      })
      .then(function(){
        setBusy(false);
        setPendingUpload(null);
        setEditing(null);
        setMode("list");
        return onReload && onReload();
      })
      .catch(function(e){
        setBusy(false);
        setErr(e.message || String(e));
      });
  };

  // Delete an existing soundfont — confirmed before fire
  const deleteEntry = function(sf) {
    if (!sf || !sf.id) return;
    if (!window.confirm('Delete "' + sf.name + '"? The file will be removed from B2 too.')) return;
    setBusy(true);
    setErr("");
    fetch(apiBase + "/api/admin/soundfonts/" + encodeURIComponent(sf.id), {
      method: "DELETE",
      headers: {
        ...(getToken && getToken() ? { "Authorization": "Bearer " + getToken() } : {}),
      },
    })
      .then(function(resp){
        if (!resp.ok) {
          return resp.json().then(function(d){
            throw new Error((d && d.detail) || ("Delete failed (" + resp.status + ")"));
          }, function(){
            throw new Error("Delete failed (" + resp.status + ")");
          });
        }
        return resp.json();
      })
      .then(function(result){
        setBusy(false);
        if (result && result.warning) setErr(result.warning);
        return onReload && onReload();
      })
      .catch(function(e){
        setBusy(false);
        setErr(e.message || String(e));
      });
  };

  // ── Render: full-screen overlay ──────────────────────────────────────────
  return (
    <div style={{
      // z-index 10600: must sit ABOVE the preset browser (10500), since the
      // overlay is launched FROM the preset browser. If it sat below, the
      // user would tap +SF and see "nothing happens" because the overlay
      // was rendering but hidden behind the open preset browser sheet.
      position: "fixed", inset: 0, zIndex: 10600,
      background: "rgba(7,6,17,0.96)",
      backdropFilter: "blur(20px) saturate(140%)",
      WebkitBackdropFilter: "blur(20px) saturate(140%)",
      color: "#F8F7FA", fontFamily: "'DM Sans',sans-serif",
      display: "flex", flexDirection: "column",
      overflowY: "auto", overflowX: "hidden",
      WebkitOverflowScrolling: "touch",
    }}>
      {/* Header */}
      <div style={{
        position: "sticky", top: 0, zIndex: 2,
        background: "linear-gradient(180deg, rgba(20,15,30,0.96) 0%, rgba(7,6,17,0.92) 100%)",
        borderBottom: "1px solid rgba(167,139,250,0.15)",
        // Top padding accounts for the iOS status bar / notch. Without
        // env(safe-area-inset-top), the close button sits underneath the
        // battery icon and can't be tapped.
        padding: "calc(env(safe-area-inset-top) + 16px) 18px 16px",
        display: "flex", alignItems: "center", gap: 12,
      }}>
        <div style={{ flex: 1, minWidth: 0 }}>
          <div style={{
            fontSize: 9, fontWeight: 700, letterSpacing: 1.8,
            color: "#A78BFA", textTransform: "uppercase",
          }}>Admin</div>
          <div style={{
            fontSize: 17, fontWeight: 700, color: "#F8F7FA",
            letterSpacing: 0.1, marginTop: 1,
          }}>♪ Soundfonts</div>
        </div>
        <button onClick={onClose} style={{
          background: "rgba(20,15,30,0.5)", color: "#9590A8",
          border: "1px solid rgba(167,139,250,0.15)",
          borderRadius: 10, padding: "8px 12px",
          fontSize: 20, lineHeight: 1, cursor: "pointer",
          fontWeight: 300, fontFamily: "system-ui",
        }}>×</button>
      </div>

      {/* Error / warning toast */}
      {err && (
        <div style={{
          margin: "12px 16px 0",
          background: "rgba(252,165,165,0.08)",
          border: "1px solid rgba(252,165,165,0.25)",
          borderRadius: 10, padding: "10px 14px",
          fontSize: 12, color: "#FCA5A5",
          lineHeight: 1.4,
        }}>{err}</div>
      )}

      {/* Upload progress bar */}
      {busy && uploadProgress > 0 && (
        <div style={{
          margin: "12px 16px 0",
          background: "rgba(167,139,250,0.08)",
          border: "1px solid rgba(167,139,250,0.25)",
          borderRadius: 10, padding: "10px 14px",
          fontSize: 12, color: "#C4B5FD",
        }}>
          <div style={{ marginBottom: 6 }}>
            Uploading… {Math.round(uploadProgress * 100)}%
          </div>
          <div style={{
            height: 6, borderRadius: 3,
            background: "rgba(167,139,250,0.15)",
            overflow: "hidden",
          }}>
            <div style={{
              height: "100%",
              width: (uploadProgress * 100) + "%",
              background: "linear-gradient(90deg,#A78BFA 0%,#8B5CF6 100%)",
              transition: "width 0.15s linear",
            }} />
          </div>
        </div>
      )}

      {/* List mode — catalogue + upload button */}
      {mode === "list" && (
        <div style={{ padding: "16px", flex: 1 }}>
          <button onClick={triggerFilePicker} disabled={busy} style={{
            width: "100%",
            background: busy
              ? "rgba(167,139,250,0.20)"
              : "linear-gradient(135deg,#A78BFA 0%,#8B5CF6 100%)",
            color: "white",
            border: "1px solid rgba(167,139,250,0.5)",
            borderRadius: 12, padding: "14px",
            fontSize: 13, fontWeight: 700, letterSpacing: 0.4,
            cursor: busy ? "wait" : "pointer",
            boxShadow: busy ? "none" : "0 6px 20px rgba(139,92,246,0.3), inset 0 1px 0 rgba(255,255,255,0.2)",
            marginBottom: 16,
          }}>
            {busy ? "Working…" : "+ Upload Soundfont (.sf2 / .sf3)"}
          </button>
          <input
            ref={fileInputRef}
            type="file"
            onChange={onFilePicked}
            style={{ display: "none" }}
            accept=".sf2,.sf3,.sfz,application/octet-stream"
          />

          {/* Info card — shows once when no soundfonts uploaded yet */}
          {(!soundfonts || soundfonts.length === 0) && !busy && (
            <div style={{
              background: "rgba(167,139,250,0.04)",
              border: "1px solid rgba(167,139,250,0.12)",
              borderRadius: 12, padding: "14px 16px",
              marginBottom: 16,
              fontSize: 12, color: "#9590A8", lineHeight: 1.6,
            }}>
              <div style={{
                fontSize: 9, fontWeight: 700, letterSpacing: 1.6,
                color: "#C4B5FD", textTransform: "uppercase",
                marginBottom: 6,
              }}>How it works</div>
              Soundfonts you upload here become real-instrument presets in
              the Studio's preset browser. All users see them in the
              Instruments category. Max 100 MB per file, 30 total.
              SF2 and SF3 play. SFZ uploads but doesn't play yet (FluidSynth
              limitation).
            </div>
          )}

          {/* List of existing soundfonts */}
          {soundfonts && soundfonts.length > 0 && (
            <div style={{
              position: "relative",
              // Explicit pixel height — iOS Safari handles overflow-auto
              // far more reliably with a hard-coded height than with
              // flex-grow inside an already-scrolling parent. Calculated
              // from viewport height minus rough room for the sticky
              // header (~110px), the upload button (~70px), top padding
              // (~16px) and bottom safe-area inset.
              height: `calc(${typeof window !== "undefined" ? window.innerHeight : 700}px - 250px)`,
              minHeight: 280,
              display: "flex",
              gap: 6,
            }}>
              {/* Scrollable list column */}
              <div
                ref={listScrollRef}
                onScroll={refreshArrowState}
                style={{
                  flex: 1, minWidth: 0,
                  overflowY: "auto", overflowX: "hidden",
                  WebkitOverflowScrolling: "touch",
                  display: "flex", flexDirection: "column", gap: 8,
                  paddingBottom: 12,
                }}>
              {soundfonts.map(function(sf){
                return (
                  <div key={sf.id} style={{
                    background: "rgba(20,15,30,0.55)",
                    border: "1px solid rgba(167,139,250,0.10)",
                    borderRadius: 12, padding: "12px 14px",
                    display: "flex", alignItems: "center", gap: 12,
                    flexShrink: 0,
                  }}>
                    {/* Color swatch */}
                    <div style={{
                      width: 36, height: 36, borderRadius: 9, flexShrink: 0,
                      background: "linear-gradient(135deg," + sf.color + " 0%," + sf.color + "cc 100%)",
                      boxShadow:
                        "0 0 12px " + sf.color + "55, " +
                        "inset 0 1px 0 rgba(255,255,255,0.2)",
                    }} />
                    {/* Name + meta */}
                    <div style={{ flex: 1, minWidth: 0 }}>
                      <div style={{
                        fontSize: 13, fontWeight: 700, color: "#F8F7FA",
                        overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap",
                      }}>{sf.name}</div>
                      <div style={{
                        fontSize: 10, color: "#7A7488",
                        marginTop: 2,
                        overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap",
                      }}>
                        GM #{sf.gmProgramNumber || 0} · {fmtBytes(sf.fileSize)}
                      </div>
                    </div>
                    {/* Action buttons */}
                    <button onClick={function(){ startEdit(sf); }} disabled={busy} style={{
                      background: "rgba(20,15,30,0.5)", color: "#C4B5FD",
                      border: "1px solid rgba(167,139,250,0.20)",
                      borderRadius: 8, padding: "7px 11px",
                      fontSize: 10, fontWeight: 700, letterSpacing: 0.6,
                      cursor: busy ? "wait" : "pointer", flexShrink: 0,
                    }}>EDIT</button>
                    <button onClick={function(){ deleteEntry(sf); }} disabled={busy} style={{
                      background: "rgba(239,68,68,0.08)", color: "#F87171",
                      border: "1px solid rgba(239,68,68,0.22)",
                      borderRadius: 8, padding: "7px 11px",
                      fontSize: 10, fontWeight: 700, letterSpacing: 0.6,
                      cursor: busy ? "wait" : "pointer", flexShrink: 0,
                    }}>DEL</button>
                  </div>
                );
              })}
              </div>

              {/* Arrow column — visible only when there's enough content
                  to scroll. Each arrow scrolls by ~one card height per
                  tap; press-and-hold auto-repeats every 80ms for fast
                  navigation through long lists. */}
              {arrowScrollState.canScroll && (
                <div style={{
                  width: 32, display: "flex", flexDirection: "column",
                  alignItems: "stretch", gap: 6, paddingTop: 2,
                }}>
                  <ArrowScrollButton
                    direction="up"
                    disabled={arrowScrollState.atTop}
                    scrollRef={listScrollRef}
                    onAfter={refreshArrowState}
                  />
                  {/* Thumb indicator — a tiny dot whose vertical position
                      reflects the current scroll offset. Helps the user
                      see how far through the list they've scrolled. */}
                  <div style={{
                    flex: 1, position: "relative",
                    background: "rgba(167,139,250,0.06)",
                    borderRadius: 3,
                  }}>
                    <div style={{
                      position: "absolute",
                      left: "50%", transform: "translateX(-50%)",
                      top: `${(function(){
                        const el = listScrollRef.current;
                        if (!el) return 0;
                        const max = el.scrollHeight - el.clientHeight;
                        if (max <= 0) return 0;
                        return Math.min(95, (el.scrollTop / max) * 95);
                      })()}%`,
                      width: 8, height: 16,
                      background: "rgba(196,181,253,0.55)",
                      borderRadius: 3,
                      transition: "top 0.12s ease-out",
                    }} />
                  </div>
                  <ArrowScrollButton
                    direction="down"
                    disabled={arrowScrollState.atBottom}
                    scrollRef={listScrollRef}
                    onAfter={refreshArrowState}
                  />
                </div>
              )}
            </div>
          )}
        </div>
      )}


      {/* Edit mode — metadata form */}
      {mode === "edit" && editing && (
        <div style={{ padding: "16px", flex: 1 }}>
          {!editing._existing && pendingUpload && (
            <div style={{
              background: "rgba(167,243,208,0.06)",
              border: "1px solid rgba(167,243,208,0.20)",
              borderRadius: 10, padding: "10px 14px",
              fontSize: 11, color: "#A7F3D0",
              marginBottom: 16, lineHeight: 1.5,
            }}>
              ✓ Uploaded {pendingUpload.filename} · {fmtBytes(pendingUpload.bytes)}.
              Fill in the details below and tap Save to publish.
            </div>
          )}

          {/* Name */}
          <div style={{ marginBottom: 14 }}>
            <div style={{
              fontSize: 9, fontWeight: 700, letterSpacing: 1.6,
              color: "#7A7488", textTransform: "uppercase",
              marginBottom: 6,
            }}>Name</div>
            <input type="text" value={editing.name}
              onChange={function(e){ setEditing({...editing, name: e.target.value}); }}
              maxLength={80}
              style={{
                width: "100%", boxSizing: "border-box",
                background: "rgba(20,15,30,0.55)",
                border: "1px solid rgba(167,139,250,0.20)",
                borderRadius: 9, padding: "10px 12px",
                color: "#F8F7FA", fontSize: 13, fontWeight: 600,
                fontFamily: "'DM Sans',sans-serif",
              }} />
          </div>

          {/* Description */}
          <div style={{ marginBottom: 14 }}>
            <div style={{
              fontSize: 9, fontWeight: 700, letterSpacing: 1.6,
              color: "#7A7488", textTransform: "uppercase",
              marginBottom: 6,
            }}>Description (optional)</div>
            <input type="text" value={editing.description}
              onChange={function(e){ setEditing({...editing, description: e.target.value}); }}
              maxLength={200}
              placeholder="e.g. Warm grand · my personal pack"
              style={{
                width: "100%", boxSizing: "border-box",
                background: "rgba(20,15,30,0.55)",
                border: "1px solid rgba(167,139,250,0.20)",
                borderRadius: 9, padding: "10px 12px",
                color: "#F8F7FA", fontSize: 12,
                fontFamily: "'DM Sans',sans-serif",
              }} />
          </div>

          {/* GM program hint */}
          <div style={{ marginBottom: 14 }}>
            <div style={{
              fontSize: 9, fontWeight: 700, letterSpacing: 1.6,
              color: "#7A7488", textTransform: "uppercase",
              marginBottom: 6,
            }}>GM Program (0-127)</div>
            <input type="number" value={editing.gm_program_hint}
              onChange={function(e){
                const v = parseInt(e.target.value, 10);
                setEditing({...editing, gm_program_hint: isNaN(v) ? 0 : Math.max(0, Math.min(127, v))});
              }}
              min={0} max={127}
              style={{
                width: "100%", boxSizing: "border-box",
                background: "rgba(20,15,30,0.55)",
                border: "1px solid rgba(167,139,250,0.20)",
                borderRadius: 9, padding: "10px 12px",
                color: "#F8F7FA", fontSize: 13, fontFamily: "monospace",
              }} />
            <div style={{
              fontSize: 10, color: "#7A7488",
              marginTop: 5, lineHeight: 1.4,
            }}>
              Which patch in the file to play. 0 is the first / default.
              Most single-instrument soundfonts use 0. For GM banks, use the
              standard program number (0=Piano, 24=Nylon Guitar, etc.).
            </div>
          </div>

          {/* Save + cancel buttons */}
          <div style={{ display: "flex", gap: 8, marginTop: 20 }}>
            <button onClick={function(){
              setMode("list");
              setEditing(null);
              setPendingUpload(null);
              setErr("");
            }} disabled={busy} style={{
              flex: 1,
              background: "rgba(20,15,30,0.5)", color: "#9590A8",
              border: "1px solid rgba(167,139,250,0.15)",
              borderRadius: 10, padding: "12px",
              fontSize: 12, fontWeight: 700, letterSpacing: 0.4,
              cursor: busy ? "wait" : "pointer",
            }}>Cancel</button>
            <button onClick={saveEntry} disabled={busy} style={{
              flex: 2,
              background: busy
                ? "rgba(167,139,250,0.20)"
                : "linear-gradient(135deg,#A78BFA 0%,#8B5CF6 100%)",
              color: "white",
              border: "1px solid rgba(167,139,250,0.5)",
              borderRadius: 10, padding: "12px",
              fontSize: 12, fontWeight: 700, letterSpacing: 0.4,
              cursor: busy ? "wait" : "pointer",
              boxShadow: busy ? "none" : "0 4px 14px rgba(139,92,246,0.3), inset 0 1px 0 rgba(255,255,255,0.2)",
            }}>{busy ? "Saving…" : "Save"}</button>
          </div>
        </div>
      )}
    </div>
  );
}


// ═════════════════════════════════════════════════════════════════════════════
//  SAMPLER IMPORT OVERLAY — FL Slicer-style sample upload + chop
// ═════════════════════════════════════════════════════════════════════════════
//
// Three-step flow:
//   1. Pick file (audio file from device)
//   2. Decode + auto-slice (transient detection, user can tweak sensitivity)
//   3. Confirm — uploads (with dedup), creates the sampler track
//
// The visual: glassy dark panel, neon cyan/violet accents, big waveform
// preview with slice markers. Designed to feel premium and FL-Slicer-ish
// while being completely touch-native on iPhone.
//
// Props:
//   apiBase       — backend root URL
//   getToken      — auth token getter for upload
//   onClose       — close handler
//   onCreateTrack — fn(samplerData) called when user confirms; parent
//                   creates the track and links the sampler data
// ── Sampler waveform: canvas-based renderer ─────────────────────────────────
// Receives a flat array of {pos, neg} peaks and paints them onto a canvas
// that fills its parent's width. Re-paints when peaks change OR when the
// parent resizes (ResizeObserver). Uses devicePixelRatio so the waveform
// stays crisp on retina iPhones.
//
// Why a dedicated component: the SVG version with stroke="url(#grad)" was
// rendering transparent strokes on iOS Safari at small viewport widths.
// Canvas paints reliably across every browser.
function SamplerWaveformCanvas({ peaks }) {
  const canvasRef = React.useRef(null);
  const containerRef = React.useRef(null);

  React.useEffect(function(){
    const canvas = canvasRef.current;
    const container = containerRef.current;
    if (!canvas || !container) return;

    function paint() {
      const dpr = (typeof window !== "undefined" && window.devicePixelRatio) ? window.devicePixelRatio : 1;
      const cssWidth = container.clientWidth || 320;
      const cssHeight = 80;
      // Set the canvas backing-store size for crispness, and CSS size for layout
      canvas.width = Math.max(1, Math.floor(cssWidth * dpr));
      canvas.height = Math.max(1, Math.floor(cssHeight * dpr));
      canvas.style.width  = cssWidth + "px";
      canvas.style.height = cssHeight + "px";
      const ctx = canvas.getContext("2d");
      if (!ctx) return;
      ctx.scale(dpr, dpr);
      ctx.clearRect(0, 0, cssWidth, cssHeight);
      if (!peaks || peaks.length === 0) return;

      // Vertical gradient: cyan → violet → cyan
      const grad = ctx.createLinearGradient(0, 0, 0, cssHeight);
      grad.addColorStop(0,   "rgba(34,211,238,0.9)");
      grad.addColorStop(0.5, "rgba(139,92,246,0.85)");
      grad.addColorStop(1,   "rgba(34,211,238,0.9)");
      ctx.strokeStyle = grad;
      ctx.fillStyle = grad;
      ctx.lineWidth = 1;

      const center = cssHeight / 2;
      const halfRange = cssHeight * 0.46;  // leave 4% margin top/bottom
      const colW = cssWidth / peaks.length;
      // Draw a vertical "bar" for each peak. Using fillRect with a 1px-ish
      // width per bucket renders more reliably than thousands of stroke
      // calls. For 320 buckets at ~width 360, each bar is ~1.1px wide.
      for (let i = 0; i < peaks.length; i++) {
        const p = peaks[i];
        const yTop = center - p.pos * halfRange;
        const yBot = center - p.neg * halfRange;
        const h = Math.max(1, yBot - yTop);
        ctx.fillRect(i * colW, yTop, Math.max(1, colW * 0.9), h);
      }
      // Centre line for orientation
      ctx.strokeStyle = "rgba(34,211,238,0.10)";
      ctx.lineWidth = 1;
      ctx.beginPath();
      ctx.moveTo(0, center);
      ctx.lineTo(cssWidth, center);
      ctx.stroke();
    }
    paint();

    // Repaint when the container resizes (e.g. on orientation change or
    // when the overlay first lays out).
    let ro;
    if (typeof window !== "undefined" && "ResizeObserver" in window) {
      ro = new ResizeObserver(paint);
      ro.observe(container);
    }
    return function(){ if (ro) ro.disconnect(); };
  }, [peaks]);

  return (
    <div ref={containerRef} style={{ position:"absolute", inset:0, zIndex: 1 }}>
      <canvas ref={canvasRef} style={{ display:"block", width:"100%", height:"100%" }} />
    </div>
  );
}


// ── PointerSlider ─────────────────────────────────────────────────────────
// Custom slider using POINTER EVENTS (the same pattern that fixed EQ band
// drag and drum velocity drag). Native <input type="range"> is unreliable
// for touch drag inside complex scrollable overlays on iOS — Safari's
// gesture arbitration sometimes refuses to engage the range input's
// touch handling, leaving the slider unresponsive.
//
// This component:
//  - Tracks pointermove on document so the gesture continues even if the
//    finger moves outside the slider's bounds.
//  - Listens with { passive: false } so we can preventDefault and stop
//    iOS from engaging page scroll mid-drag.
//  - touchAction:"none" on the slider tells iOS up-front not to scroll on
//    touch — so it never gets a chance to steal the gesture.
//  - Click-on-track jumps to that position (mobile-friendly: tap the value
//    you want instead of dragging).
//
// Props: min, max, step (optional, snap to grid), value, onChange, accentColor.
function PointerSlider({ min, max, step, value, onChange, accentColor }) {
  const trackRef = React.useRef(null);
  const draggingRef = React.useRef(false);
  // Compute slider position from a clientX, snapping to step.
  const computeValue = function(clientX){
    const track = trackRef.current;
    if (!track) return value;
    const rect = track.getBoundingClientRect();
    if (rect.width <= 0) return value;
    const ratio = Math.max(0, Math.min(1, (clientX - rect.left) / rect.width));
    let v = min + ratio * (max - min);
    if (step) v = Math.round(v / step) * step;
    v = Math.max(min, Math.min(max, v));
    return v;
  };
  // Document-level pointermove/up that survive component re-renders.
  // setPointerCapture failed on iOS because the slider DOM element was
  // being recreated by React after the first onChange triggered parent
  // re-renders — which released the captured pointer and killed the
  // drag. Binding on document keeps the listeners alive regardless of
  // what happens to the slider element during the gesture.
  const onPointerDown = function(e){
    try { if (typeof window !== "undefined" && window.__bfDebugLog) window.__bfDebugLog("[PS] pointerdown type=" + e.pointerType + " id=" + e.pointerId + " x=" + Math.round(e.clientX) + " cancel=" + e.cancelable); } catch(_){}
    if (e.pointerType === "mouse" && e.button !== 0) return;
    if (e.cancelable) { try { e.preventDefault(); } catch(_){} }
    try { e.stopPropagation(); } catch(_){}
    // Capture the track rect ONCE at gesture start. If the React tree
    // re-renders during drag and creates a new DOM node, the original
    // rect is still valid for the duration of this gesture.
    const track = trackRef.current;
    const rect = track ? track.getBoundingClientRect() : null;
    if (!rect || rect.width <= 0) return;
    const localCompute = function(clientX){
      const ratio = Math.max(0, Math.min(1, (clientX - rect.left) / rect.width));
      let v = min + ratio * (max - min);
      if (step) v = Math.round(v / step) * step;
      return Math.max(min, Math.min(max, v));
    };
    onChange(localCompute(e.clientX));
    const onMove = function(ev){
      try { if (typeof window !== "undefined" && window.__bfDebugLog) window.__bfDebugLog("[PS] pointermove x=" + Math.round(ev.clientX)); } catch(_){}
      if (ev.cancelable) { try { ev.preventDefault(); } catch(_){} }
      onChange(localCompute(ev.clientX));
    };
    const cleanup = function(ev){
      try { if (typeof window !== "undefined" && window.__bfDebugLog) window.__bfDebugLog("[PS] pointerup/cancel type=" + (ev && ev.type)); } catch(_){}
      document.removeEventListener("pointermove",   onMove);
      document.removeEventListener("pointerup",     cleanup);
      document.removeEventListener("pointercancel", cleanup);
    };
    document.addEventListener("pointermove",   onMove, { passive: false });
    document.addEventListener("pointerup",     cleanup);
    document.addEventListener("pointercancel", cleanup);
  };
  // Clamp value into range before computing display ratio.
  const clamped = Math.max(min, Math.min(max, value));
  const ratio   = (max - min) > 0 ? (clamped - min) / (max - min) : 0;
  return (
    <div
      ref={trackRef}
      onPointerDown={onPointerDown}
      data-pointer-slider="1"
      style={{
        width: "100%",
        minHeight: 40,
        height: 40,
        position: "relative",
        cursor: "pointer",
        touchAction: "none",
        userSelect: "none",
        WebkitUserSelect: "none",
        display: "block",
      }}>
      {/* Track */}
      <div style={{
        position: "absolute",
        top: "50%", transform: "translateY(-50%)",
        left: 0, right: 0,
        height: 5,
        background: "rgba(167,139,250,0.18)",
        borderRadius: 999,
        pointerEvents: "none",
      }} />
      {/* Fill */}
      <div style={{
        position: "absolute",
        top: "50%", transform: "translateY(-50%)",
        left: 0, width: (ratio * 100) + "%",
        height: 5,
        background: accentColor,
        borderRadius: 999,
        pointerEvents: "none",
      }} />
      {/* Thumb */}
      <div style={{
        position: "absolute",
        top: "50%",
        left: "calc(" + (ratio * 100) + "% - 10px)",
        transform: "translateY(-50%)",
        width: 20, height: 20,
        borderRadius: "50%",
        background: accentColor,
        boxShadow: "0 0 8px " + accentColor + "aa, 0 2px 4px rgba(0,0,0,0.3)",
        border: "2px solid rgba(255,255,255,0.95)",
        pointerEvents: "none",
      }} />
    </div>
  );
}

function SamplerImportOverlay({ apiBase, getToken, onClose, onCreateTrack, projectBpm, existingTrack, onUpdateTrack }) {
  // Editing-mode flag — when set, the overlay loads the existing
  // sample + slices instead of starting from the file-picker stage,
  // and the bottom CTA changes to "Save Changes" with onUpdateTrack
  // wired up instead of onCreateTrack.
  const editingExisting = !!(existingTrack && existingTrack.samplerData);
  const existing = editingExisting ? existingTrack.samplerData : null;

  const [stage, setStage] = React.useState(editingExisting ? "loading" : "pick");
  const [errorMsg, setErrorMsg] = React.useState(null);
  const [fileName, setFileName] = React.useState(existing ? (existing.sampleName || "") : "");
  const [fileSize, setFileSize] = React.useState(0);
  const [fileHash, setFileHash] = React.useState(existing ? (existing.sampleHash || "") : "");
  const [fileBytes, setFileBytes] = React.useState(null);  // ArrayBuffer for upload (only used in new-upload flow)
  const [audioBuffer, setAudioBuffer] = React.useState(null);
  const [slicePoints, setSlicePoints] = React.useState(
    existing && Array.isArray(existing.slicePoints) ? existing.slicePoints.slice() : [0]
  );
  const [sensitivity, setSensitivity] = React.useState(existing && existing.sensitivity != null ? existing.sensitivity : 0.5);
  const [sliceMode, setSliceMode] = React.useState(existing && existing.sliceMode ? existing.sliceMode : "auto");
  const [equalCount, setEqualCount] = React.useState(existing && existing.equalCount ? existing.equalCount : 16);
  const [rootPitch, setRootPitch] = React.useState(existing && existing.rootPitch != null ? existing.rootPitch : 60);
  const [playMode, setPlayMode] = React.useState(existing && existing.playMode ? existing.playMode : "to-next");
  // Pitch & tempo lock for BPM matching.
  //   pitchSemitones — user-tuned pitch offset in semitones (-24..+24).
  //                    Combines with tempoLock into a single playbackRate.
  //   sampleBpm      — what BPM the SAMPLE is at. User enters this if
  //                    tempoLock is on. Defaults to projectBpm so a "1.00x"
  //                    rate is the starting point.
  //   tempoLock      — when on, the sample auto-rate-stretches to match
  //                    projectBpm based on sampleBpm. When off, only the
  //                    pitchSemitones offset affects playbackRate.
  const [pitchSemitones, setPitchSemitones] = React.useState(existing && existing.pitchSemitones != null ? existing.pitchSemitones : 0);
  // Fine pitch offset in cents. 100 cents = 1 semitone. Range: -100..+100,
  // so a user can dial in any intermediate pitch when a semitone-only
  // step is too coarse. Combines with pitchSemitones into the playback
  // rate via the standard 2^((semis*100 + cents)/1200) formula.
  const [pitchCents, setPitchCents] = React.useState(existing && existing.pitchCents != null ? existing.pitchCents : 0);
  // Independent speed multiplier — lets the producer tweak pitch
  // (for key) without the speed dragging along, by dialing speed
  // back down. Range: 0.5x (half speed) to 2.0x (double speed).
  // The final playbackRate is pitchFactor * speedAdjust * tempoFactor.
  // True pitch-preserving stretch would need a phase vocoder worklet
  // which iOS Safari doesn't make easy; this manual decoupling is
  // the practical compromise.
  const [speedAdjust, setSpeedAdjust] = React.useState(existing && existing.speedAdjust != null ? existing.speedAdjust : 1.0);
  const [sampleBpm, setSampleBpm] = React.useState(existing && existing.sampleBpm != null ? existing.sampleBpm : 120);
  const [tempoLockEnabled, setTempoLockEnabled] = React.useState(existing && !!existing.tempoLockEnabled);
  // ── Crop region ──
  // Producers often upload a long audio file (e.g. a 4-minute loop, a
  // YouTube rip, a vocal recording) but only want to chop slices from a
  // specific section. Two handles below define the active window — all
  // slicing happens within [cropStart, cropEnd], anything outside is
  // dimmed and ignored. Stored as absolute seconds in the source buffer
  // so existing playback code (which uses slicePoints as buffer offsets)
  // requires no changes. cropEnd defaults to -1 here as a sentinel for
  // "not yet set"; resolved to audioBuffer.duration once decoded.
  const [cropStart, setCropStart] = React.useState(
    existing && existing.cropStart != null ? existing.cropStart : 0
  );
  const [cropEnd, setCropEnd] = React.useState(
    existing && existing.cropEnd != null ? existing.cropEnd : -1
  );
  const [usage, setUsage] = React.useState({ used: 0, cap: 0 });
  const fileInputRef = React.useRef(null);
  // Ref to the waveform's inner container — used by the crop handles
  // to convert touch X coordinates into seconds.
  const waveformInnerRef = React.useRef(null);
  // Which crop handle the user is currently dragging ("start", "end", or null).
  // Tracked via ref so the touchmove handler doesn't need state churn.
  const cropDragRef = React.useRef(null);

  // When editing an existing track, fetch the existing sample and decode
  // it into THIS overlay's decode context so we can show the waveform.
  // Once decoded, advance to the "edit" stage with all controls active.
  //
  // OPTIMIZATION: if the sample is already in the main playback context's
  // bfSamplerCache (which it should be, since the track was created from
  // this sample), we can REUSE that buffer for the waveform render
  // instead of fetching+decoding into a separate context. This avoids
  // creating a second AudioContext (iOS Safari has a hard limit and
  // creating a second one can suspend the first, silencing playback
  // while the editor is open). The decoded AudioBuffer is portable
  // across contexts of the same sample-rate for read-only use like
  // waveform peak extraction.
  React.useEffect(function(){
    if (!editingExisting) return;
    if (!existing.sampleUrl) {
      setErrorMsg("This sampler track is missing its source URL.");
      setStage("pick");
      return;
    }
    // Fast path: reuse the buffer the main app already has cached.
    if (existing.sampleHash && bfSamplerCache.buffers.has(existing.sampleHash)) {
      const cachedBuf = bfSamplerCache.buffers.get(existing.sampleHash);
      setAudioBuffer(cachedBuf);
      // If cropEnd is still the -1 sentinel (no saved crop on this track),
      // resolve it to the full duration. Existing saved crops are kept.
      if (cropEnd < 0) setCropEnd(cachedBuf.duration);
      setStage("edit");
      return;
    }
    // Slow path (cache miss): fetch + decode. Only happens if the user
    // opens the editor before the preload effect has populated the cache,
    // or in a brand-new browser session before any sampler note played.
    let cancelled = false;
    (async function(){
      try {
        const resp = await fetch(existing.sampleUrl);
        if (!resp.ok) throw new Error("HTTP " + resp.status);
        const arrayBuf = await resp.arrayBuffer();
        if (cancelled) return;
        setFileSize(arrayBuf.byteLength);
        const ctx = getDecodeCtx();
        const decoded = await new Promise(function(resolve, reject){
          ctx.decodeAudioData(arrayBuf.slice(0), resolve, reject);
        });
        if (cancelled) return;
        setAudioBuffer(decoded);
        if (cropEnd < 0) setCropEnd(decoded.duration);
        setStage("edit");
      } catch (err) {
        if (cancelled) return;
        console.warn("[sampler-edit] couldn't load existing sample", err);
        setErrorMsg("Couldn't load the saved sample. Network issue?");
        setStage("pick");
      }
    })();
    return function(){ cancelled = true; };
  }, [editingExisting]);

  // Pull an AudioContext for decode (separate from the main app ctx so
  // we don't disrupt live playback; the AudioBuffer is portable across
  // contexts of the same sample rate, which all browsers default to).
  const decodeCtxRef = React.useRef(null);
  function getDecodeCtx(){
    if (decodeCtxRef.current) return decodeCtxRef.current;
    decodeCtxRef.current = new (window.AudioContext || window.webkitAudioContext)();
    return decodeCtxRef.current;
  }

  // ── SHA-256 in browser via Web Crypto API ──
  async function sha256Hex(arrayBuffer) {
    const digest = await crypto.subtle.digest("SHA-256", arrayBuffer);
    return Array.from(new Uint8Array(digest))
      .map(function(b){ return b.toString(16).padStart(2, "0"); })
      .join("");
  }

  // ── File handler ──
  async function onFilePicked(e) {
    const file = e.target.files && e.target.files[0];
    if (!file) return;
    if (file.size > 20 * 1024 * 1024) {
      setErrorMsg("File too large (max 20 MB)");
      return;
    }
    setStage("loading");
    setErrorMsg(null);
    setFileName(file.name);
    setFileSize(file.size);
    try {
      const arrayBuf = await file.arrayBuffer();
      setFileBytes(arrayBuf);
      const hash = await sha256Hex(arrayBuf);
      setFileHash(hash);
      // Decode for waveform + slicing. Need a copy because decodeAudioData
      // consumes/detaches the buffer on some browsers.
      const ctx = getDecodeCtx();
      const decoded = await new Promise(function(resolve, reject){
        // .slice() returns a copy so the original is preserved for upload
        ctx.decodeAudioData(arrayBuf.slice(0), resolve, reject);
      });
      setAudioBuffer(decoded);
      // Initialise the crop window to the full sample on a fresh upload.
      // Producers crop down from here if they want only a sub-section.
      setCropStart(0);
      setCropEnd(decoded.duration);
      // Auto-slice on initial decode
      const initial = bfDetectTransients(decoded, 0.5);
      setSlicePoints(initial);
      setStage("edit");
    } catch (err) {
      console.error("[sampler-import]", err);
      setErrorMsg("Couldn't read this file. Try a different mp3 or wav.");
      setStage("pick");
    }
  }

  // ── Re-slice with current settings ──
  // Slices are constrained to the crop window [cropStart, cropEnd]:
  //   - Auto transient detection: scan the whole buffer, then keep only
  //     transients that fall within the window. Add cropStart as the
  //     first slice so we always have an anchor at the start of the
  //     active region.
  //   - Equal divisions: divide the cropped duration into N equal
  //     intervals, offset by cropStart.
  // All times are stored as absolute offsets into the source buffer so
  // the playback code (which feeds slicePoints directly to a buffer
  // source's `offset`) needs no changes.
  function reslice(modeOverride, sensOverride, countOverride) {
    if (!audioBuffer) return;
    const mode = modeOverride != null ? modeOverride : sliceMode;
    // Resolve crop window in seconds, with safety clamps.
    const lo = Math.max(0, Math.min(audioBuffer.duration, cropStart));
    const hi = Math.max(lo + 0.01, Math.min(audioBuffer.duration,
      (cropEnd < 0 ? audioBuffer.duration : cropEnd)));
    if (mode === "auto") {
      const sens = sensOverride != null ? sensOverride : sensitivity;
      const all = bfDetectTransients(audioBuffer, sens);
      // Filter transients to the window; ensure first slice = lo.
      const inRange = all.filter(function(t){ return t >= lo && t < hi; });
      const dedup = (inRange[0] !== lo) ? [lo].concat(inRange) : inRange;
      setSlicePoints(dedup.length ? dedup : [lo]);
    } else {
      const n = countOverride != null ? countOverride : equalCount;
      const span = hi - lo;
      const out = [];
      for (let i = 0; i < n; i++) out.push(lo + (i / n) * span);
      setSlicePoints(out);
    }
  }

  // Re-slice automatically whenever the crop window changes — so the user
  // sees the slices re-distribute live as they drag the crop handles.
  React.useEffect(function(){
    if (audioBuffer) reslice();
    // eslint-disable-next-line
  }, [cropStart, cropEnd]);

  // ── Upload + create track (OR update existing) ──
  async function confirmAndCreate() {
    if (!audioBuffer) return;
    // Stop any preview before we kick off the upload; a 60-second sample
    // shouldn't keep playing through the upload and into the timeline.
    stopAnyPreview();

    // EDIT MODE: the sample is already uploaded; we just need to rebuild
    // the samplerData with the user's new slice/pitch/tempo choices and
    // hand it back to the parent via onUpdateTrack. No HTTP, no dedup.
    if (editingExisting) {
      // Combined pitch in cents (semitones×100 + cents). Standard
      // equal-temperament formula: rate = 2^(totalCents/1200).
      const totalCents = pitchSemitones * 100 + pitchCents;
      const pitchFactor = Math.pow(2, totalCents / 1200);
      const safeProjBpm = (projectBpm && projectBpm > 0) ? projectBpm : 120;
      const safeSampBpm = (sampleBpm && sampleBpm > 0) ? sampleBpm : 120;
      const tempoFactor = tempoLockEnabled ? (safeProjBpm / safeSampBpm) : 1;
      // Final rate combines pitch (key), speed (user-dialed independent
      // multiplier), and tempo lock (BPM-match factor). All three are
      // multiplied; the user controls each separately.
      const playbackRate = pitchFactor * speedAdjust * tempoFactor;
      const updated = Object.assign({}, existing, {
        sliceMode:        sliceMode,
        slicePoints:      slicePoints.slice(),
        rootPitch:        rootPitch,
        playMode:         playMode,
        equalCount:       equalCount,
        sensitivity:      sensitivity,
        pitchSemitones:   pitchSemitones,
        pitchCents:       pitchCents,
        speedAdjust:      speedAdjust,
        sampleBpm:        sampleBpm,
        tempoLockEnabled: tempoLockEnabled,
        playbackRate:     playbackRate,
        cropStart:        cropStart,
        cropEnd:          cropEnd,
      });
      // ── Kick off the offline pitch render in the background ──
      // The play path automatically falls back to resampling if the
      // pitched buffer isn't ready yet, so this isn't blocking. We
      // start it here so a brief tap on Play right after saving will
      // already have the pitched buffer warm by the time the user
      // gets there.
      try {
        if (audioBuffer && existing && existing.sampleHash && totalCents !== 0) {
          const ctx = getDecodeCtx();
          if (typeof bfGetSamplerPitchedBuffer === "function") {
            bfGetSamplerPitchedBuffer(existing.sampleHash, audioBuffer, totalCents, ctx);
          }
        }
      } catch(_){}
      if (typeof onUpdateTrack === "function") onUpdateTrack(updated);
      onClose();
      return;
    }

    // CREATE MODE: standard upload flow.
    if (!fileHash || !fileBytes) return;
    setStage("uploading");
    setErrorMsg(null);
    try {
      // 1. Dedup check
      const checkResp = await fetch(apiBase + "/api/samples/check", {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
          ...(getToken && getToken() ? { Authorization: "Bearer " + getToken() } : {}),
        },
        body: JSON.stringify({ hash: fileHash, size: fileSize }),
      });
      if (!checkResp.ok) throw new Error("check failed: HTTP " + checkResp.status);
      const checkData = await checkResp.json();
      setUsage({ used: checkData.usage_bytes || 0, cap: checkData.cap_bytes || 0 });

      let sampleWire;
      if (checkData.exists) {
        sampleWire = checkData.sample;
      } else {
        // 2. Upload (auth required)
        const fd = new FormData();
        const blob = new Blob([fileBytes], { type: "audio/mpeg" });
        fd.append("file", blob, fileName);
        fd.append("hash", fileHash);
        fd.append("duration_sec", String(audioBuffer.duration));
        fd.append("sample_rate", String(audioBuffer.sampleRate));
        const upResp = await fetch(apiBase + "/api/samples/upload", {
          method: "POST",
          headers: getToken && getToken() ? { Authorization: "Bearer " + getToken() } : {},
          body: fd,
        });
        if (!upResp.ok) {
          let msg = "Upload failed (HTTP " + upResp.status + ")";
          try { const errBody = await upResp.json(); if (errBody.detail) msg = errBody.detail; } catch(_){}
          throw new Error(msg);
        }
        const upData = await upResp.json();
        sampleWire = upData.sample;
      }

      // Compute the playbackRate from pitch + (optionally) tempo lock.
      // Combined pitch in cents (semitones×100 + cents) so the user can
      // fine-tune below the semitone level. Standard equal-temperament:
      //   rate = 2^(totalCents / 1200)
      // Tempo factor = projectBpm / sampleBpm when locked, else 1.0.
      const totalCents = pitchSemitones * 100 + pitchCents;
      const pitchFactor = Math.pow(2, totalCents / 1200);
      const safeProjBpm = (projectBpm && projectBpm > 0) ? projectBpm : 120;
      const safeSampBpm = (sampleBpm && sampleBpm > 0) ? sampleBpm : 120;
      const tempoFactor = tempoLockEnabled ? (safeProjBpm / safeSampBpm) : 1;
      // Final rate combines pitch (key), speed (user-dialed independent
      // multiplier), and tempo lock (BPM-match factor). All three are
      // multiplied; the user controls each separately.
      const playbackRate = pitchFactor * speedAdjust * tempoFactor;

      // 3. Build samplerData + create track
      onCreateTrack({
        sampleHash:  sampleWire.hash,
        sampleUrl:   sampleWire.url,
        sampleName:  sampleWire.name,
        sliceMode:   sliceMode,
        slicePoints: slicePoints.slice(),
        rootPitch:   rootPitch,
        playMode:    playMode,
        gain:        1.0,
        durationSec: audioBuffer.duration,
        equalCount:  equalCount,
        sensitivity: sensitivity,
        // Tempo + pitch
        pitchSemitones:   pitchSemitones,
        pitchCents:       pitchCents,
        speedAdjust:      speedAdjust,
        sampleBpm:        sampleBpm,
        tempoLockEnabled: tempoLockEnabled,
        playbackRate:     playbackRate,
        // Crop window — slicing region defined by the user
        cropStart:        cropStart,
        cropEnd:          cropEnd,
      });
      // NOTE: We intentionally do NOT pre-cache the AudioBuffer from
      // this import overlay's decode context. iOS Safari sometimes
      // refuses to play AudioBuffers across AudioContext instances
      // (the import overlay creates its own context for decoding,
      // separate from the main app's playback context). Letting the
      // playback context fetch+decode from the B2 URL on first use
      // produces a buffer guaranteed to be context-compatible. The
      // re-decode adds ~150-400ms latency the first time the track
      // plays, then it's cached for the session. The track-load
      // effect in Studio's main render will trigger the fetch as
      // soon as the new track is added to state.
      onClose();
    } catch (err) {
      console.error("[sampler-import]", err);
      setErrorMsg(err.message || "Couldn't upload this sample. Please try again.");
      setStage("edit");
    }
  }

  // ── Waveform peaks for visualisation ──
  // Track the currently-playing preview source so we can stop it when:
  //   - the user taps another marker (only one slice plays at a time)
  //   - the user taps the same marker again (toggle stop)
  //   - the overlay closes (Cancel or Create — sample shouldn't keep ringing)
  const previewSrcRef = React.useRef(null);
  const previewIdxRef = React.useRef(null);

  function stopAnyPreview() {
    const src = previewSrcRef.current;
    if (src) {
      try { src.stop(0); } catch(_) {}
      try { src.disconnect(); } catch(_) {}
    }
    previewSrcRef.current = null;
    previewIdxRef.current = null;
  }

  // Stop preview when overlay unmounts.
  React.useEffect(function(){
    return function(){ stopAnyPreview(); };
  }, []);

  const waveformPeaks = React.useMemo(function(){
    if (!audioBuffer) return [];
    const WIDTH = 320;  // peak buckets
    const len = audioBuffer.length;
    const ch0 = audioBuffer.getChannelData(0);
    // Mix in channel 1 too if present for an honest representation of
    // the signal (stereo files can have transients on one side only).
    const ch1 = audioBuffer.numberOfChannels > 1 ? audioBuffer.getChannelData(1) : null;
    const peaks = new Array(WIDTH);
    let maxAbs = 0;
    for (let i = 0; i < WIDTH; i++) {
      const start = Math.floor((i / WIDTH) * len);
      const end   = Math.floor(((i + 1) / WIDTH) * len);
      let pos = 0, neg = 0;
      for (let j = start; j < end; j++) {
        let v = ch0[j];
        if (ch1) v = (v + ch1[j]) * 0.5;
        if (v > pos) pos = v;
        if (v < neg) neg = v;
      }
      peaks[i] = { pos: pos, neg: neg };
      if (pos > maxAbs) maxAbs = pos;
      if (-neg > maxAbs) maxAbs = -neg;
    }
    // Normalize so the loudest peak hits ~0.95 of the available height.
    // Without this, quiet samples render as a flat invisible line.
    // Guard against silent buffers (maxAbs === 0).
    const norm = maxAbs > 0.001 ? 0.95 / maxAbs : 1;
    for (let i = 0; i < WIDTH; i++) {
      peaks[i] = { pos: peaks[i].pos * norm, neg: peaks[i].neg * norm };
    }
    return peaks;
  }, [audioBuffer]);

  const slicePctList = React.useMemo(function(){
    if (!audioBuffer) return [];
    return slicePoints.map(function(t){ return (t / audioBuffer.duration) * 100; });
  }, [slicePoints, audioBuffer]);

  // ── Preview a slice on tap ──
  // Only one slice plays at a time. Tapping the same marker again stops
  // playback (toggle). Tapping a different marker stops the previous and
  // starts the new one. Required because long samples (e.g. a 60-second
  // mp3 chopped into 1 slice) would otherwise play to completion no
  // matter what the user does.
  function previewSlice(idx) {
    if (!audioBuffer || idx < 0 || idx >= slicePoints.length) return;
    // Toggle: tapping the active slice stops it.
    if (previewIdxRef.current === idx && previewSrcRef.current) {
      stopAnyPreview();
      return;
    }
    stopAnyPreview();
    const ctx = getDecodeCtx();
    if (ctx.state === "suspended") ctx.resume();
    const src = ctx.createBufferSource();
    src.buffer = audioBuffer;
    const gain = ctx.createGain();
    gain.gain.value = 0.8;
    src.connect(gain);
    gain.connect(ctx.destination);
    const start = slicePoints[idx];
    const next  = (idx + 1 < slicePoints.length) ? slicePoints[idx + 1] : audioBuffer.duration;
    const len   = next - start;
    try { src.start(0, start, len); } catch(_){}
    // Self-clear once playback ends naturally
    src.onended = function(){
      if (previewSrcRef.current === src) {
        previewSrcRef.current = null;
        previewIdxRef.current = null;
      }
    };
    previewSrcRef.current = src;
    previewIdxRef.current = idx;
  }

  return (
    <>
      <style>{
        "@keyframes samImpFadeIn { from { opacity: 0; } to { opacity: 1; } }"
        + "@keyframes samImpSlideUp { from { opacity: 0; transform: translateY(20px); } to { opacity: 1; transform: translateY(0); } }"
        + "@keyframes samImpPulse { 0%,100% { box-shadow: 0 0 16px rgba(34,211,238,0.35); } 50% { box-shadow: 0 0 28px rgba(34,211,238,0.65); } }"
        + "@keyframes samImpShimmer { 0% { background-position: -200% 0; } 100% { background-position: 200% 0; } }"
      }</style>
      <div style={{
        position: "fixed", inset: 0, zIndex: 12000,
        background: "rgba(7,6,17,0.85)",
        backdropFilter: "blur(24px) saturate(140%)",
        WebkitBackdropFilter: "blur(24px) saturate(140%)",
        display: "flex", flexDirection: "column",
        animation: "samImpFadeIn 0.2s ease",
        paddingTop: "env(safe-area-inset-top)",
        paddingBottom: "env(safe-area-inset-bottom)",
      }}>
        {/* Header */}
        <div style={{
          display: "flex", alignItems: "center",
          padding: "calc(env(safe-area-inset-top) + 8px) 16px 10px",
          borderBottom: "1px solid rgba(34,211,238,0.10)",
          background: "linear-gradient(180deg, rgba(13,10,24,0.85), rgba(13,10,24,0.4))",
        }}>
          <div style={{
            display:"flex", alignItems:"center", gap: 10, flex: 1,
          }}>
            <div style={{
              width: 36, height: 36, borderRadius: 10,
              background: "linear-gradient(135deg, rgba(34,211,238,0.3), rgba(139,92,246,0.3))",
              border: "1px solid rgba(34,211,238,0.45)",
              display:"flex", alignItems:"center", justifyContent:"center",
              fontSize: 18,
              boxShadow: "0 0 16px rgba(34,211,238,0.35)",
            }}>✂</div>
            <div>
              <div style={{
                color:"#22D3EE", fontSize:9, letterSpacing: 1.6, fontWeight:800,
                textTransform:"uppercase",
              }}>Sampler</div>
              <div style={{ color:"#F8F7FA", fontSize:17, fontWeight:800, letterSpacing:0.2 }}>
                {stage === "pick" ? "New Sampler Track" :
                 stage === "loading" ? (editingExisting ? "Loading saved sample…" : "Reading file…") :
                 stage === "uploading" ? "Saving sample…" :
                 editingExisting ? "Edit Slices" : "Chop & Slice"}
              </div>
            </div>
          </div>
          <button onClick={function(){ stopAnyPreview(); onClose(); }} style={{
            background: "rgba(255,255,255,0.05)",
            border: "1px solid rgba(255,255,255,0.10)",
            color: "#F8F7FA",
            borderRadius: 10, padding: "8px 14px",
            fontSize: 13, fontWeight: 700, cursor:"pointer",
            fontFamily:"'DM Sans',sans-serif",
          }}>Cancel</button>
        </div>

        {/* Body — minHeight:0 is REQUIRED here. Without it, in a column
            flex context, `flex: 1` won't actually shrink the body and the
            overflowY:auto becomes a no-op (content silently extends past
            the footer). On iOS, this manifests as a "stuck" overlay where
            you can see content under the CTA button but can't scroll to
            it. -webkit-overflow-scrolling:touch keeps the momentum
            scrolling feel on iOS Safari. */}
        <div
          ref={function(el){
            if (!el || el._bfSamplerScroll) return;
            el._bfSamplerScroll = true;
            let active=false,startY=0,startScroll=0,lastY=0,lastT=0,vel=0,moved=false,raf=0;
            const cancel=function(){ if(raf){cancelAnimationFrame(raf);raf=0;} };
            el.addEventListener("touchstart",function(e){
              if(!e.touches||!e.touches[0])return; cancel(); active=true; moved=false;
              startY=e.touches[0].clientY; startScroll=el.scrollTop; lastY=startY; lastT=performance.now(); vel=0;
            },{passive:false});
            el.addEventListener("touchmove",function(e){
              if(!active||!e.touches||!e.touches[0])return;
              const y=e.touches[0].clientY, dy=y-startY;
              if(!moved&&Math.abs(dy)>8)moved=true;
              el.scrollTop=Math.max(0,Math.min(el.scrollHeight-el.clientHeight,startScroll-dy));
              const now=performance.now(),dt=now-lastT; if(dt>0)vel=(lastY-y)/dt; lastY=y; lastT=now;
              if(e.cancelable){try{e.preventDefault();}catch(_){}}
            },{passive:false});
            const end=function(){
              if(!active)return; active=false;
              if(moved){ const sw=function(ev){ev.stopPropagation();ev.preventDefault();el.removeEventListener("click",sw,true);}; el.addEventListener("click",sw,true); setTimeout(function(){try{el.removeEventListener("click",sw,true);}catch(_){}},350); }
              let v=vel*16; const step=function(){ if(Math.abs(v)<0.5){raf=0;return;} el.scrollTop=Math.max(0,Math.min(el.scrollHeight-el.clientHeight,el.scrollTop+v)); v*=0.94; raf=requestAnimationFrame(step); };
              if(Math.abs(vel)>0.05)raf=requestAnimationFrame(step);
            };
            el.addEventListener("touchend",end,{passive:true});
            el.addEventListener("touchcancel",end,{passive:true});
          }}
          style={{
          flex: 1,
          minHeight: 0,
          overflowY: "scroll",
          WebkitOverflowScrolling: "touch",
          overscrollBehavior: "contain",
          touchAction: "pan-y",
          padding: "12px 14px 16px",
        }}>
          {/* PICK STAGE */}
          {stage === "pick" && (
            <div style={{
              animation: "samImpSlideUp 0.3s cubic-bezier(0.22,1,0.36,1) both",
            }}>
              <div style={{
                padding: "32px 20px",
                borderRadius: 16,
                background: "linear-gradient(135deg, rgba(34,211,238,0.06), rgba(139,92,246,0.06))",
                border: "1px dashed rgba(34,211,238,0.30)",
                textAlign: "center",
              }}>
                <div style={{
                  width: 64, height: 64, margin: "0 auto 16px", borderRadius: 18,
                  background: "linear-gradient(135deg, rgba(34,211,238,0.18), rgba(139,92,246,0.18))",
                  border: "1px solid rgba(34,211,238,0.4)",
                  display:"flex", alignItems:"center", justifyContent:"center",
                  fontSize: 28,
                  animation: "samImpPulse 2.4s ease-in-out infinite",
                }}>🎵</div>
                <div style={{ color: "#F8F7FA", fontSize:16, fontWeight:800, marginBottom:6 }}>
                  Pick an audio file
                </div>
                <div style={{ color: "#9087A8", fontSize:12, marginBottom: 18, lineHeight:1.4 }}>
                  Drag in a loop, vocal, drum break — anything.<br />
                  We'll auto-chop it into slices you can play as MIDI notes.
                </div>
                <input
                  ref={fileInputRef}
                  type="file"
                  accept=".mp3,.wav,.m4a,.aac,.ogg,.flac"
                  onChange={onFilePicked}
                  style={{ display: "none" }}
                />
                <button onClick={function(){ fileInputRef.current && fileInputRef.current.click(); }} style={{
                  background: "linear-gradient(135deg, #22D3EE 0%, #8B5CF6 100%)",
                  color: "#070611",
                  border: "none",
                  borderRadius: 12, padding: "12px 32px",
                  fontSize: 14, fontWeight: 800, cursor:"pointer",
                  letterSpacing: 0.4,
                  fontFamily:"'DM Sans',sans-serif",
                  boxShadow: "0 4px 24px rgba(34,211,238,0.4)",
                }}>Choose File</button>
                <div style={{ color:"#5F586F", fontSize:10, marginTop:14, letterSpacing:0.3 }}>
                  mp3 · wav · m4a · ogg · flac · max 20 MB
                </div>
              </div>
              {errorMsg && (
                <div style={{
                  marginTop: 14, padding: "10px 14px",
                  background: "rgba(239,68,68,0.10)",
                  border: "1px solid rgba(239,68,68,0.4)",
                  borderRadius: 10,
                  color: "#FCA5A5", fontSize: 12,
                }}>{errorMsg}</div>
              )}
            </div>
          )}

          {/* LOADING / UPLOADING STAGE */}
          {(stage === "loading" || stage === "uploading") && (
            <div style={{ padding: "40px 20px", textAlign:"center" }}>
              <div style={{
                width: 48, height: 48, margin: "0 auto 20px", borderRadius: "50%",
                border: "3px solid rgba(34,211,238,0.15)",
                borderTopColor: "#22D3EE",
                animation: "spin 0.9s linear infinite",
              }} />
              <div style={{ color:"#F8F7FA", fontSize:14, fontWeight:700 }}>
                {stage === "loading"
                  ? (editingExisting ? "Loading saved sample…" : "Decoding audio…")
                  : "Uploading to your library…"}
              </div>
              <div style={{ color:"#7A7488", fontSize:11, marginTop:6 }}>
                {stage === "loading"
                  ? (editingExisting ? "Fetching from your library" : "Analyzing transients")
                  : "This sample will be available across all your projects"}
              </div>
            </div>
          )}

          {/* EDIT STAGE — waveform + slice tools */}
          {stage === "edit" && audioBuffer && (
            <div style={{
              animation: "samImpSlideUp 0.3s cubic-bezier(0.22,1,0.36,1) both",
              display: "flex", flexDirection: "column", gap: 10,
            }}>
              {/* File info */}
              <div style={{
                padding: "7px 12px",
                background: "rgba(20,15,30,0.5)",
                border: "1px solid rgba(167,139,250,0.10)",
                borderRadius: 10,
                display: "flex", alignItems: "center", gap: 10,
              }}>
                <div style={{
                  width: 26, height: 26, borderRadius: 7,
                  background: "rgba(34,211,238,0.15)",
                  display:"flex", alignItems:"center", justifyContent:"center",
                  fontSize: 13,
                }}>🎵</div>
                <div style={{ flex:1, minWidth: 0 }}>
                  <div style={{ color:"#F8F7FA", fontSize:12, fontWeight:700,
                    whiteSpace:"nowrap", overflow:"hidden", textOverflow:"ellipsis",
                  }}>{fileName}</div>
                  <div style={{ color:"#7A7488", fontSize:10 }}>
                    {audioBuffer.duration.toFixed(2)}s · {audioBuffer.sampleRate} Hz · {(fileSize/1024).toFixed(0)} KB
                  </div>
                </div>
                <button onClick={function(){ fileInputRef.current && fileInputRef.current.click(); }} style={{
                  background: "rgba(34,211,238,0.10)",
                  border: "1px solid rgba(34,211,238,0.25)",
                  color: "#22D3EE",
                  borderRadius: 8, padding: "6px 10px",
                  fontSize: 10, fontWeight:700, cursor:"pointer", letterSpacing: 0.4,
                  fontFamily:"'DM Sans',sans-serif",
                }}>Swap</button>
              </div>

              {/* Waveform */}
              <div style={{
                position: "relative",
                background: "linear-gradient(180deg, rgba(13,10,24,0.85) 0%, rgba(7,6,17,0.95) 100%)",
                border: "1px solid rgba(34,211,238,0.15)",
                borderRadius: 12,
                padding: 10,
                boxShadow: "inset 0 0 24px rgba(34,211,238,0.04)",
              }}>
                <div
                  ref={waveformInnerRef}
                  style={{
                    position: "relative", height: 80,
                    overflow: "hidden",
                  }}>
                  {/* Waveform — drawn imperatively to a <canvas>. SVG with
                      stroke="url(#grad)" doesn't render reliably on iOS
                      Safari at small widths (returns transparent strokes
                      depending on viewport scaling). Canvas always works. */}
                  <SamplerWaveformCanvas peaks={waveformPeaks} />

                  {/* ── Crop overlay ─────────────────────────────────
                      Two black-tinted regions dim the audio OUTSIDE the
                      [cropStart, cropEnd] window. Two cyan handles let
                      the user drag the boundaries. Slices only get
                      generated inside the active window — anything dim
                      is ignored. */}
                  {(function(){
                    if (!audioBuffer) return null;
                    const dur = audioBuffer.duration;
                    const lo = Math.max(0, Math.min(dur, cropStart));
                    const hi = Math.max(lo + 0.01, Math.min(dur,
                      (cropEnd < 0 ? dur : cropEnd)));
                    const loPct = (lo / dur) * 100;
                    const hiPct = (hi / dur) * 100;
                    // Convert a clientX into seconds inside the buffer.
                    // The waveformInnerRef wraps the full visualisation
                    // and is the same width the canvas paints.
                    const xToSec = function(clientX){
                      const el = waveformInnerRef.current;
                      if (!el) return 0;
                      const rect = el.getBoundingClientRect();
                      const pct = (clientX - rect.left) / Math.max(1, rect.width);
                      return Math.max(0, Math.min(dur, pct * dur));
                    };
                    const onMove = function(e){
                      if (!cropDragRef.current) return;
                      const t = e.touches ? e.touches[0] : e;
                      if (!t) return;
                      const sec = xToSec(t.clientX);
                      if (cropDragRef.current === "start") {
                        const newLo = Math.max(0, Math.min(sec, hi - 0.05));
                        setCropStart(newLo);
                      } else if (cropDragRef.current === "end") {
                        const newHi = Math.max(lo + 0.05, Math.min(sec, dur));
                        setCropEnd(newHi);
                      }
                      if (e.preventDefault) e.preventDefault();
                    };
                    const onUp = function(){
                      cropDragRef.current = null;
                      window.removeEventListener("mousemove", onMove);
                      window.removeEventListener("mouseup", onUp);
                      window.removeEventListener("touchmove", onMove);
                      window.removeEventListener("touchend", onUp);
                      window.removeEventListener("touchcancel", onUp);
                    };
                    const startDrag = function(which){
                      return function(e){
                        cropDragRef.current = which;
                        window.addEventListener("mousemove", onMove);
                        window.addEventListener("mouseup", onUp);
                        window.addEventListener("touchmove", onMove, { passive: false });
                        window.addEventListener("touchend", onUp);
                        window.addEventListener("touchcancel", onUp);
                        if (e.stopPropagation) e.stopPropagation();
                        if (e.preventDefault)  e.preventDefault();
                      };
                    };
                    return (
                      <React.Fragment>
                        {/* Dim region LEFT of cropStart */}
                        {loPct > 0 && (
                          <div style={{
                            position: "absolute", left: 0, top: 0, bottom: 0,
                            width: loPct + "%",
                            background: "rgba(7,6,17,0.65)",
                            pointerEvents: "none",
                            zIndex: 1,
                          }} />
                        )}
                        {/* Dim region RIGHT of cropEnd */}
                        {hiPct < 100 && (
                          <div style={{
                            position: "absolute", right: 0, top: 0, bottom: 0,
                            width: (100 - hiPct) + "%",
                            background: "rgba(7,6,17,0.65)",
                            pointerEvents: "none",
                            zIndex: 1,
                          }} />
                        )}
                        {/* Subtle outline around the active region */}
                        <div style={{
                          position: "absolute",
                          left: loPct + "%",
                          width: (hiPct - loPct) + "%",
                          top: 0, bottom: 0,
                          borderLeft: "1px dashed rgba(34,211,238,0.40)",
                          borderRight: "1px dashed rgba(34,211,238,0.40)",
                          pointerEvents: "none",
                          zIndex: 1,
                        }} />
                        {/* START handle */}
                        <div
                          onMouseDown={startDrag("start")}
                          onTouchStart={startDrag("start")}
                          style={{
                            position: "absolute",
                            left: "calc(" + loPct + "% - 14px)",
                            top: 0, bottom: 0,
                            width: 28,
                            display: "flex", alignItems: "center", justifyContent: "center",
                            cursor: "ew-resize",
                            touchAction: "none",
                            zIndex: 4,
                          }}>
                          <div style={{
                            width: 4, height: "100%",
                            background: "linear-gradient(180deg,#22D3EE,#0891B2)",
                            borderRadius: 2,
                            boxShadow: "0 0 10px rgba(34,211,238,0.7)",
                          }} />
                          <div style={{
                            position: "absolute",
                            top: -6, left: "50%", transform: "translateX(-50%)",
                            width: 0, height: 0,
                            borderLeft: "5px solid transparent",
                            borderRight: "5px solid transparent",
                            borderTop: "6px solid #22D3EE",
                            filter: "drop-shadow(0 0 4px rgba(34,211,238,0.7))",
                          }} />
                        </div>
                        {/* END handle */}
                        <div
                          onMouseDown={startDrag("end")}
                          onTouchStart={startDrag("end")}
                          style={{
                            position: "absolute",
                            left: "calc(" + hiPct + "% - 14px)",
                            top: 0, bottom: 0,
                            width: 28,
                            display: "flex", alignItems: "center", justifyContent: "center",
                            cursor: "ew-resize",
                            touchAction: "none",
                            zIndex: 4,
                          }}>
                          <div style={{
                            width: 4, height: "100%",
                            background: "linear-gradient(180deg,#22D3EE,#0891B2)",
                            borderRadius: 2,
                            boxShadow: "0 0 10px rgba(34,211,238,0.7)",
                          }} />
                          <div style={{
                            position: "absolute",
                            top: -6, left: "50%", transform: "translateX(-50%)",
                            width: 0, height: 0,
                            borderLeft: "5px solid transparent",
                            borderRight: "5px solid transparent",
                            borderTop: "6px solid #22D3EE",
                            filter: "drop-shadow(0 0 4px rgba(34,211,238,0.7))",
                          }} />
                        </div>
                      </React.Fragment>
                    );
                  })()}

                  {/* Slice markers */}
                  {slicePctList.map(function(pct, i){
                    return (
                      <div key={i}
                        onClick={function(){ previewSlice(i); }}
                        style={{
                          position: "absolute",
                          left: pct + "%",
                          top: 0, bottom: 0,
                          width: 2,
                          background: "linear-gradient(180deg, #22D3EE, rgba(34,211,238,0))",
                          boxShadow: "0 0 8px rgba(34,211,238,0.7)",
                          pointerEvents: "auto",
                          cursor: "pointer",
                          touchAction: "manipulation",
                          zIndex: 2,
                        }}>
                        <div style={{
                          position: "absolute", top: -2, left: -8,
                          width: 18, height: 12, borderRadius: 4,
                          background: "#22D3EE", color: "#070611",
                          fontSize: 8, fontWeight: 800,
                          display: "flex", alignItems: "center", justifyContent: "center",
                          boxShadow: "0 0 10px rgba(34,211,238,0.6)",
                        }}>{i + 1}</div>
                      </div>
                    );
                  })}
                </div>
                <div style={{
                  marginTop: 8, display: "flex", justifyContent: "space-between",
                  color: "#5F586F", fontSize: 9, letterSpacing: 0.5,
                }}>
                  <span>0:00</span>
                  <span style={{ color: "#22D3EE" }}>{slicePoints.length} SLICES · TAP MARKERS TO PREVIEW</span>
                  <span>{audioBuffer.duration.toFixed(2)}s</span>
                </div>
                {/* Crop status + reset. Only shows when the user has
                    actually cropped (window is not the full sample). */}
                {(function(){
                  const dur = audioBuffer.duration;
                  const lo = Math.max(0, Math.min(dur, cropStart));
                  const hi = Math.max(lo + 0.01, Math.min(dur,
                    (cropEnd < 0 ? dur : cropEnd)));
                  const isCropped = (lo > 0.001) || (hi < dur - 0.001);
                  if (!isCropped) {
                    return (
                      <div style={{
                        marginTop: 6,
                        color: "#5F586F", fontSize: 9, letterSpacing: 0.5,
                        textAlign: "center",
                      }}>Drag the cyan handles to crop the slice region</div>
                    );
                  }
                  return (
                    <div style={{
                      marginTop: 6,
                      display: "flex", alignItems: "center", justifyContent: "space-between",
                      gap: 8,
                    }}>
                      <span style={{
                        color: "#22D3EE", fontSize: 10, fontWeight: 700,
                        letterSpacing: 0.5,
                      }}>
                        CROP: {lo.toFixed(2)}s → {hi.toFixed(2)}s · {(hi - lo).toFixed(2)}s
                      </span>
                      <button
                        onClick={function(){
                          setCropStart(0);
                          setCropEnd(dur);
                        }}
                        style={{
                          background: "rgba(34,211,238,0.10)",
                          border: "1px solid rgba(34,211,238,0.30)",
                          color: "#22D3EE",
                          borderRadius: 6, padding: "4px 10px",
                          fontSize: 9, fontWeight: 800, letterSpacing: 1,
                          cursor: "pointer", textTransform: "uppercase",
                          fontFamily: "'DM Sans',sans-serif",
                        }}>Reset</button>
                    </div>
                  );
                })()}
              </div>

              {/* Slice mode toggle */}
              <div>
                <div style={{ fontSize:9, color:"#7A7488", letterSpacing:1.4, textTransform:"uppercase", fontWeight:700, marginBottom:4 }}>Slice Mode</div>
                <div style={{ display:"flex", gap:6 }}>
                  {[
                    { id: "auto",  label: "Auto Transients" },
                    { id: "equal", label: "Equal Divisions" },
                  ].map(function(opt){
                    const active = sliceMode === opt.id;
                    return (
                      <button key={opt.id} onClick={function(){
                        setSliceMode(opt.id);
                        reslice(opt.id, sensitivity, equalCount);
                      }} style={{
                        flex: 1,
                        background: active ? "linear-gradient(135deg, rgba(34,211,238,0.18), rgba(139,92,246,0.18))" : "rgba(20,15,30,0.4)",
                        color: active ? "#22D3EE" : "#9590A8",
                        border: "1px solid " + (active ? "rgba(34,211,238,0.40)" : "rgba(167,139,250,0.10)"),
                        borderRadius: 8, padding:"7px 8px",
                        fontSize: 11, fontWeight: 700, cursor:"pointer",
                        fontFamily:"'DM Sans',sans-serif", letterSpacing: 0.3,
                      }}>{opt.label}</button>
                    );
                  })}
                </div>
              </div>

              {/* Auto sensitivity slider */}
              {sliceMode === "auto" && (
                <div>
                  <div style={{ display:"flex", alignItems:"center", justifyContent:"space-between", marginBottom:2 }}>
                    <div style={{ fontSize:9, color:"#7A7488", letterSpacing:1.4, textTransform:"uppercase", fontWeight:700 }}>Sensitivity</div>
                    <div style={{ fontSize:11, color:"#22D3EE", fontWeight:700, fontVariantNumeric: "tabular-nums" }}>{Math.round(sensitivity * 100)}%</div>
                  </div>
                  <PointerSlider
                    min={0} max={100} step={1}
                    value={Math.round(sensitivity * 100)}
                    onChange={function(intPct){
                      const v = intPct / 100;
                      setSensitivity(v);
                      reslice("auto", v, equalCount);
                    }}
                    accentColor="#22D3EE" />
                </div>
              )}

              {/* Equal count input */}
              {sliceMode === "equal" && (
                <div>
                  <div style={{ display:"flex", alignItems:"center", justifyContent:"space-between", marginBottom:2 }}>
                    <div style={{ fontSize:9, color:"#7A7488", letterSpacing:1.4, textTransform:"uppercase", fontWeight:700 }}>Slice Count</div>
                    <div style={{ fontSize:11, color:"#22D3EE", fontWeight:700, fontVariantNumeric: "tabular-nums" }}>{equalCount}</div>
                  </div>
                  <PointerSlider
                    min={2} max={64} step={1}
                    value={equalCount}
                    onChange={function(v){
                      setEqualCount(v);
                      reslice("equal", sensitivity, v);
                    }}
                    accentColor="#22D3EE" />
                </div>
              )}

              {/* Root pitch + play mode */}
              <div style={{ display:"flex", gap:8 }}>
                <div style={{ flex:1 }}>
                  <div style={{ fontSize:9, color:"#7A7488", letterSpacing:1.4, textTransform:"uppercase", fontWeight:700, marginBottom:4 }}>Root Note</div>
                  <div style={{ display:"flex", gap:5, alignItems:"center" }}>
                    <button onClick={function(){ setRootPitch(Math.max(24, rootPitch - 12)); }} style={{
                      width: 30, height: 30, borderRadius: 7,
                      background: "rgba(20,15,30,0.4)",
                      border: "1px solid rgba(167,139,250,0.10)",
                      color: "#9590A8", fontSize: 12, fontWeight: 800, cursor:"pointer",
                    }}>−</button>
                    <div style={{
                      flex: 1, height: 30, borderRadius: 7,
                      background: "rgba(34,211,238,0.08)",
                      border: "1px solid rgba(34,211,238,0.25)",
                      display: "flex", alignItems: "center", justifyContent: "center",
                      color: "#22D3EE", fontSize: 12, fontWeight: 800, fontVariantNumeric: "tabular-nums",
                    }}>{midiNoteName(rootPitch)}</div>
                    <button onClick={function(){ setRootPitch(Math.min(108, rootPitch + 12)); }} style={{
                      width: 30, height: 30, borderRadius: 7,
                      background: "rgba(20,15,30,0.4)",
                      border: "1px solid rgba(167,139,250,0.10)",
                      color: "#9590A8", fontSize: 12, fontWeight: 800, cursor:"pointer",
                    }}>+</button>
                  </div>
                </div>
                <div style={{ flex:1 }}>
                  <div style={{ fontSize:9, color:"#7A7488", letterSpacing:1.4, textTransform:"uppercase", fontWeight:700, marginBottom:4 }}>Play Mode</div>
                  <div style={{ display:"flex", gap:5 }}>
                    {[
                      { id: "to-next", label: "Slice" },
                      { id: "full",    label: "Full" },
                    ].map(function(opt){
                      const active = playMode === opt.id;
                      return (
                        <button key={opt.id} onClick={function(){ setPlayMode(opt.id); }} style={{
                          flex: 1, height: 30,
                          background: active ? "rgba(34,211,238,0.12)" : "rgba(20,15,30,0.4)",
                          color: active ? "#22D3EE" : "#9590A8",
                          border: "1px solid " + (active ? "rgba(34,211,238,0.35)" : "rgba(167,139,250,0.10)"),
                          borderRadius: 7,
                          fontSize: 11, fontWeight: 700, cursor:"pointer",
                          fontFamily:"'DM Sans',sans-serif",
                        }}>{opt.label}</button>
                      );
                    })}
                  </div>
                </div>
              </div>

              {/* Pitch shift — semitones + cents up/down. Affects both
                  pitch AND speed (same as FL Slicer's "Pitch" knob).
                  Two sliders: coarse (semitones, ±24) and fine (cents,
                  ±100). 100 cents = 1 semitone, so the cents slider
                  covers the full gap between semitone steps.
                  touchAction:"manipulation" on each slider tells iOS to
                  treat the slider as draggable input rather than letting
                  the gesture be captured by the overlay's scroll. */}
              <div>
                <div style={{ display:"flex", alignItems:"center", justifyContent:"space-between", marginBottom:4 }}>
                  <div style={{ fontSize:9, color:"#7A7488", letterSpacing:1.4, textTransform:"uppercase", fontWeight:700 }}>Pitch</div>
                  <div style={{ display:"flex", alignItems:"center", gap: 8 }}>
                    {/* Combined readout — tap to reset to zero */}
                    <button onClick={function(){ setPitchSemitones(0); setPitchCents(0); }}
                      style={{
                        fontSize:11, color:"#22D3EE", fontWeight:700,
                        fontVariantNumeric:"tabular-nums",
                        background:"transparent", border:"none",
                        cursor:"pointer", padding:0,
                        fontFamily:"'DM Sans',sans-serif",
                    }}>
                      {pitchSemitones >= 0 ? "+" : ""}{pitchSemitones} st
                      {pitchCents !== 0
                        ? (" " + (pitchCents > 0 ? "+" : "") + pitchCents + "¢")
                        : ""}
                    </button>
                  </div>
                </div>
                {/* Coarse: semitones */}
                <div style={{ display:"flex", gap:5, alignItems:"center", marginBottom: 6 }}>
                  <button onClick={function(){ setPitchSemitones(Math.max(-24, pitchSemitones - 12)); }} style={{
                    width: 28, height: 28, borderRadius: 7,
                    background: "rgba(20,15,30,0.4)",
                    border: "1px solid rgba(167,139,250,0.10)",
                    color: "#9590A8", fontSize: 9, fontWeight: 700, cursor:"pointer",
                  }}>−12</button>
                  <button onClick={function(){ setPitchSemitones(Math.max(-24, pitchSemitones - 1)); }} style={{
                    width: 28, height: 28, borderRadius: 7,
                    background: "rgba(20,15,30,0.4)",
                    border: "1px solid rgba(167,139,250,0.10)",
                    color: "#9590A8", fontSize: 12, fontWeight: 800, cursor:"pointer",
                  }}>−</button>
                  <PointerSlider
                    min={-24} max={24} step={1}
                    value={pitchSemitones}
                    onChange={function(v){ setPitchSemitones(v); }}
                    accentColor="#22D3EE" />
                  <button onClick={function(){ setPitchSemitones(Math.min(24, pitchSemitones + 1)); }} style={{
                    width: 28, height: 28, borderRadius: 7,
                    background: "rgba(20,15,30,0.4)",
                    border: "1px solid rgba(167,139,250,0.10)",
                    color: "#9590A8", fontSize: 12, fontWeight: 800, cursor:"pointer",
                  }}>+</button>
                  <button onClick={function(){ setPitchSemitones(Math.min(24, pitchSemitones + 12)); }} style={{
                    width: 28, height: 28, borderRadius: 7,
                    background: "rgba(20,15,30,0.4)",
                    border: "1px solid rgba(167,139,250,0.10)",
                    color: "#9590A8", fontSize: 9, fontWeight: 700, cursor:"pointer",
                  }}>+12</button>
                </div>
                {/* Fine: cents (±100). Step of 1 cent gives 200 discrete
                    positions across the slider — plenty of precision but
                    not so many that small finger movements feel jittery. */}
                <div style={{ display:"flex", gap:5, alignItems:"center" }}>
                  <div style={{
                    width: 28, fontSize: 8, color: "#7A7488",
                    textAlign: "center", fontWeight: 700, letterSpacing: 0.5,
                  }}>¢</div>
                  <button onClick={function(){ setPitchCents(Math.max(-100, pitchCents - 10)); }} style={{
                    width: 28, height: 28, borderRadius: 7,
                    background: "rgba(20,15,30,0.4)",
                    border: "1px solid rgba(167,139,250,0.10)",
                    color: "#9590A8", fontSize: 9, fontWeight: 700, cursor:"pointer",
                  }}>−10</button>
                  <PointerSlider
                    min={-100} max={100} step={1}
                    value={pitchCents}
                    onChange={function(v){ setPitchCents(v); }}
                    accentColor="#A78BFA" />
                  <button onClick={function(){ setPitchCents(Math.min(100, pitchCents + 10)); }} style={{
                    width: 28, height: 28, borderRadius: 7,
                    background: "rgba(20,15,30,0.4)",
                    border: "1px solid rgba(167,139,250,0.10)",
                    color: "#9590A8", fontSize: 9, fontWeight: 700, cursor:"pointer",
                  }}>+10</button>
                </div>
              </div>

              {/* ── SPEED slider ──
                  Independent speed multiplier. Pitch and speed are still
                  physically linked when resampling — pitching up always
                  speeds up — but exposing speed as its own control lets
                  the producer COMPENSATE: to pitch up without speeding up,
                  raise pitch and lower speed by the same amount. The
                  reverse works too: to slow down without dropping the key,
                  lower speed and raise pitch.
                  Range: 0.25× (quarter speed) to 4× (quadruple speed).
                  Coarse slider sweeps the full range; fine slider gives
                  ±0.05× precision around the current value for sub-1%
                  adjustments. Step buttons let you tap exact values. */}
              <div>
                <div style={{ display:"flex", alignItems:"baseline", justifyContent:"space-between", marginBottom:4 }}>
                  <div style={{ fontSize:9, color:"#7A7488", letterSpacing:1.4, textTransform:"uppercase", fontWeight:700 }}>Speed</div>
                  <div style={{
                    fontVariantNumeric: "tabular-nums", fontSize: 11, fontWeight: 700,
                    color: Math.abs(speedAdjust - 1) < 0.005 ? "#7A7488" : "#22D3EE",
                  }}>
                    {speedAdjust.toFixed(3)}×
                  </div>
                </div>
                {/* Coarse slider: wide range, 0.001 step for smooth sweeps */}
                <div style={{ display:"flex", gap:5, alignItems:"center" }}>
                  <button onClick={function(){
                    setSpeedAdjust(Math.max(0.25, +(speedAdjust - 0.1).toFixed(3)));
                  }} style={{
                    width: 32, height: 28, borderRadius: 7,
                    background: "rgba(20,15,30,0.4)",
                    border: "1px solid rgba(167,139,250,0.10)",
                    color: "#9590A8", fontSize: 9, fontWeight: 700, cursor:"pointer",
                  }}>−.1</button>
                  <PointerSlider
                    min={0.25} max={4} step={0.001}
                    value={speedAdjust}
                    onChange={function(v){ setSpeedAdjust(v); }}
                    accentColor="#22D3EE" />
                  <button onClick={function(){
                    setSpeedAdjust(Math.min(4, +(speedAdjust + 0.1).toFixed(3)));
                  }} style={{
                    width: 32, height: 28, borderRadius: 7,
                    background: "rgba(20,15,30,0.4)",
                    border: "1px solid rgba(167,139,250,0.10)",
                    color: "#9590A8", fontSize: 9, fontWeight: 700, cursor:"pointer",
                  }}>+.1</button>
                </div>
                {/* Fine slider: ±0.05× around the current value, 0.001 step.
                    The slider is centered on speedAdjust so dragging it
                    gives sub-percent precision. Releasing keeps the new
                    value but the fine slider stays anchored to it for
                    further fine-tuning on the next interaction. */}
                <div style={{ display:"flex", gap:5, alignItems:"center", marginTop: 6 }}>
                  <div style={{
                    width: 32, fontSize: 8, color: "#7A7488",
                    textAlign: "center", fontWeight: 700, letterSpacing: 0.3,
                  }}>FINE</div>
                  <button onClick={function(){
                    setSpeedAdjust(Math.max(0.25, +(speedAdjust - 0.01).toFixed(3)));
                  }} style={{
                    width: 32, height: 28, borderRadius: 7,
                    background: "rgba(20,15,30,0.4)",
                    border: "1px solid rgba(34,211,238,0.18)",
                    color: "#22D3EE", fontSize: 9, fontWeight: 700, cursor:"pointer",
                  }}>−.01</button>
                  <PointerSlider
                    min={parseFloat(Math.max(0.25, speedAdjust - 0.05).toFixed(3))}
                    max={parseFloat(Math.min(4, speedAdjust + 0.05).toFixed(3))}
                    step={0.001}
                    value={speedAdjust}
                    onChange={function(v){ setSpeedAdjust(v); }}
                    accentColor="#22D3EE" />
                  <button onClick={function(){
                    setSpeedAdjust(Math.min(4, +(speedAdjust + 0.01).toFixed(3)));
                  }} style={{
                    width: 32, height: 28, borderRadius: 7,
                    background: "rgba(20,15,30,0.4)",
                    border: "1px solid rgba(34,211,238,0.18)",
                    color: "#22D3EE", fontSize: 9, fontWeight: 700, cursor:"pointer",
                  }}>+.01</button>
                </div>
                {/* Reset button — quick way back to 1.0× */}
                <div style={{ display:"flex", justifyContent:"center", marginTop: 6 }}>
                  <button onClick={function(){ setSpeedAdjust(1.0); }} style={{
                    height: 24, padding: "0 14px", borderRadius: 6,
                    background: "rgba(20,15,30,0.4)",
                    border: "1px solid rgba(34,211,238,0.20)",
                    color: "#22D3EE", fontSize: 9, fontWeight: 700, cursor:"pointer",
                    letterSpacing: 0.5,
                  }} title="Reset to 1.000×">RESET TO 1×</button>
                </div>
                <div style={{ marginTop: 6, fontSize: 9, color:"#5F586F", lineHeight: 1.4 }}>
                  Pitch and speed work independently. Pitch shifts the key without affecting timing. Speed changes the timing without affecting pitch.
                </div>
              </div>

              {/* Tempo lock — auto-stretch sample to match project BPM.
                  When enabled, user enters the sample's source BPM and the
                  playback rate is calculated to fit projectBpm. Combines
                  with the pitch shift above (multiplicatively). */}
              <div>
                <div style={{ display:"flex", alignItems:"center", justifyContent:"space-between", marginBottom:4 }}>
                  <div style={{ fontSize:9, color:"#7A7488", letterSpacing:1.4, textTransform:"uppercase", fontWeight:700 }}>Tempo Lock</div>
                  <button onClick={function(){ setTempoLockEnabled(!tempoLockEnabled); }} style={{
                    background: tempoLockEnabled ? "rgba(34,211,238,0.18)" : "rgba(20,15,30,0.4)",
                    color: tempoLockEnabled ? "#22D3EE" : "#9590A8",
                    border: "1px solid " + (tempoLockEnabled ? "rgba(34,211,238,0.40)" : "rgba(167,139,250,0.10)"),
                    borderRadius: 6, padding: "4px 10px",
                    fontSize: 10, fontWeight: 700, cursor:"pointer", letterSpacing: 0.4,
                    fontFamily:"'DM Sans',sans-serif",
                  }}>{tempoLockEnabled ? "ON" : "OFF"}</button>
                </div>
                {tempoLockEnabled && (
                  <div style={{ display:"flex", gap:8, alignItems:"center" }}>
                    <div style={{ flex:1 }}>
                      <div style={{ fontSize:9, color:"#5F586F", marginBottom:4 }}>Sample BPM</div>
                      <div style={{ display:"flex", gap:4, alignItems:"center" }}>
                        <button onClick={function(){ setSampleBpm(Math.max(40, sampleBpm - 1)); }} style={{
                          width: 28, height: 28, borderRadius: 6,
                          background: "rgba(20,15,30,0.4)",
                          border: "1px solid rgba(167,139,250,0.10)",
                          color: "#9590A8", fontSize: 12, fontWeight: 800, cursor:"pointer",
                        }}>−</button>
                        <input type="number" min="40" max="300" value={sampleBpm}
                          onChange={function(e){
                            const v = parseInt(e.target.value, 10);
                            if (!isNaN(v)) setSampleBpm(Math.max(40, Math.min(300, v)));
                          }}
                          style={{
                            flex: 1, height: 28, textAlign: "center",
                            background: "rgba(34,211,238,0.08)",
                            border: "1px solid rgba(34,211,238,0.25)",
                            color: "#22D3EE", fontSize: 12, fontWeight: 800,
                            borderRadius: 6, fontVariantNumeric: "tabular-nums",
                            fontFamily:"'DM Sans',sans-serif",
                            outline: "none",
                            minWidth: 0,
                          }} />
                        <button onClick={function(){ setSampleBpm(Math.min(300, sampleBpm + 1)); }} style={{
                          width: 28, height: 28, borderRadius: 6,
                          background: "rgba(20,15,30,0.4)",
                          border: "1px solid rgba(167,139,250,0.10)",
                          color: "#9590A8", fontSize: 12, fontWeight: 800, cursor:"pointer",
                        }}>+</button>
                      </div>
                    </div>
                    <div style={{ flex:1 }}>
                      <div style={{ fontSize:9, color:"#5F586F", marginBottom:4 }}>Project BPM</div>
                      <div style={{
                        height: 28, lineHeight: "28px", textAlign:"center",
                        background: "rgba(139,92,246,0.08)",
                        border: "1px solid rgba(139,92,246,0.20)",
                        color: "#C4B5FD", fontSize: 12, fontWeight: 800,
                        borderRadius: 6, fontVariantNumeric: "tabular-nums",
                      }}>{projectBpm || 120}</div>
                    </div>
                  </div>
                )}
                {tempoLockEnabled && (
                  <div style={{
                    marginTop: 6, fontSize: 10, color:"#5F586F", letterSpacing: 0.2, textAlign: "center",
                  }}>
                    {(() => {
                      const totalCents = pitchSemitones * 100 + pitchCents;
                      const pf = Math.pow(2, totalCents / 1200);
                      const tf = sampleBpm > 0 ? ((projectBpm || 120) / sampleBpm) : 1;
                      const rate = pf * speedAdjust * tf;
                      return "Final playback rate: " + rate.toFixed(3) + "×";
                    })()}
                  </div>
                )}
              </div>

              {/* Hint */}
              <div style={{
                padding: "7px 12px",
                background: "rgba(34,211,238,0.04)",
                border: "1px solid rgba(34,211,238,0.10)",
                borderRadius: 8,
                color: "#7A7488", fontSize: 10, lineHeight: 1.35,
                letterSpacing: 0.1,
              }}>
                {slicePoints.length} slice{slicePoints.length === 1 ? "" : "s"} on keys{" "}
                <span style={{ color:"#22D3EE", fontWeight:700 }}>{midiNoteName(rootPitch)}</span> →{" "}
                <span style={{ color:"#22D3EE", fontWeight:700 }}>{midiNoteName(rootPitch + slicePoints.length - 1)}</span>
              </div>

              {errorMsg && (
                <div style={{
                  padding: "10px 14px",
                  background: "rgba(239,68,68,0.10)",
                  border: "1px solid rgba(239,68,68,0.4)",
                  borderRadius: 10,
                  color: "#FCA5A5", fontSize: 12,
                }}>{errorMsg}</div>
              )}
            </div>
          )}
        </div>

        {/* Footer — Create button */}
        {stage === "edit" && (
          <div style={{
            padding: "8px 14px calc(env(safe-area-inset-bottom) + 8px)",
            borderTop: "1px solid rgba(34,211,238,0.10)",
            background: "linear-gradient(0deg, rgba(13,10,24,0.95), rgba(13,10,24,0.6))",
          }}>
            <button onClick={confirmAndCreate} disabled={!audioBuffer || slicePoints.length === 0} style={{
              width: "100%",
              background: "linear-gradient(135deg, #22D3EE 0%, #8B5CF6 100%)",
              color: "#070611",
              border: "none",
              borderRadius: 11, padding: "11px 24px",
              fontSize: 13, fontWeight: 800, cursor:"pointer",
              letterSpacing: 0.4,
              fontFamily:"'DM Sans',sans-serif",
              boxShadow: "0 4px 24px rgba(34,211,238,0.4)",
              opacity: (!audioBuffer || slicePoints.length === 0) ? 0.4 : 1,
            }}>{editingExisting ? "Save Changes" : "Create Sampler Track →"}</button>
          </div>
        )}
      </div>
    </>
  );
}



function resizeDrumPattern(pat, newLength) {
  if (!pat || newLength === pat.lengthSteps) return pat;
  const newChannels = (pat.channels || []).map(function(ch){
    const newSteps = new Array(newLength).fill(0);
    const copyLen = Math.min(ch.steps ? ch.steps.length : 0, newLength);
    // Preserve numeric step values, and migrate any leftover booleans to
    // their numeric equivalents at the same time.
    for (let i = 0; i < copyLen; i++) {
      const raw = ch.steps[i];
      newSteps[i] = raw === true ? DRUM_DEFAULT_VELOCITY
                  : raw === false ? 0
                  : (+raw || 0);
    }
    return { ...ch, steps: newSteps };
  });
  return { ...pat, lengthSteps: newLength, channels: newChannels };
}

// Compute the swing-adjusted time offset (in seconds) for a step within
// its pattern. FL Studio's "shuffle" pushes every even-numbered 16th-note
// later by `swing × stepDuration`. swing=0 → no offset; swing=0.5 →
// triplet feel (even 16ths sit on triplet-3rds).
function drumStepSwingOffset(stepIdx, swing, stepSec) {
  if (!swing || swing <= 0) return 0;
  // Even 16ths in zero-indexed terms = odd indices (1, 3, 5, ...).
  // (Step 0 is "the 1", step 1 is the "e", etc. The "e" and "a" are
  // pushed by swing; the "1" and "+" stay put. In zero-indexed: odd.)
  if ((stepIdx & 1) === 0) return 0;
  // Clamp swing into safe range to avoid swallowing the next step.
  const sw = Math.min(0.5, Math.max(0, swing));
  return sw * stepSec;
}


// Compute the duration (in seconds) of a single step at the given BPM and
// resolution. At 120 BPM with 4 steps/beat: one beat = 0.5s, one step = 0.125s.
function drumStepDurationSec(bpm, stepsPerBeat) {
  const beatSec = 60 / Math.max(1, bpm);
  return beatSec / Math.max(1, stepsPerBeat || DRUM_DEFAULT_STEPS_PER_BEAT);
}

// Compute the natural duration (in seconds) of a pattern. The default clip
// duration when a pattern is placed on the timeline equals this — one full
// run of the pattern. Looping happens by placing multiple clips end-to-end
// (or by trimming a single clip to span longer than one cycle — the
// scheduler handles wrap-around within the clip's duration).
function drumPatternNaturalDuration(pattern, bpm) {
  if (!pattern) return 0;
  return pattern.lengthSteps * drumStepDurationSec(bpm, pattern.stepsPerBeat);
}

// Find the pattern referenced by a drum-pattern clip. Returns null if the
// pattern has been deleted (caller should treat that as a no-op clip).
function findPatternInTrack(track, patternId) {
  if (!track || !track.patterns) return null;
  for (const p of track.patterns) {
    if (p.id === patternId) return p;
  }
  return null;
}

// Schedule a single drum hit into the audio graph at a precise time. Used
// by both live playback and offline export — identical signature so the
// two render paths share code.
//
//   ctx        — AudioContext or OfflineAudioContext
//   entryNode  — the FX-chain entry node returned by buildChain
//   buffer     — the decoded AudioBuffer for the pad
//   when       — exact AudioContext time to fire
//   gain       — linear gain (0..1.5)
//   pitch      — pitch shift in semitones (-24..+24), 0 = unchanged
//   pan        — stereo pan (-1..1), 0 = center. Skipped if engine has no
//                StereoPannerNode (very old browsers; iOS Safari supports it).
//   chokeGroup — unused inside scheduleDrumHit itself — choke routing is
//                handled one layer up in scheduleStepHits (which has access
//                to the per-track chokeContext). Kept for signature stability
//                and as documentation that this hit belongs to a group.
//
// Returns: { src, gain } so callers can hold onto the gain node to ramp it
// down later (used by choke groups). Callers that don't care about choke
// just keep `src` and discard the rest.
function scheduleDrumHit(ctx, entryNode, buffer, when, gain, pitch, pan, chokeGroup, glide) {
  if (!buffer || !entryNode) return null;
  const src = ctx.createBufferSource();
  src.buffer = buffer;
  // Pitch: convert semitones to playbackRate ratio (2^(n/12)). 0 semitones
  // gives 1.0 = unchanged. Note that playbackRate also affects DURATION:
  // pitching up plays the sample faster (shorter). That's the FL Studio
  // behavior and what producers expect from a step sequencer.
  if (pitch && pitch !== 0) {
    src.playbackRate.value = Math.pow(2, pitch / 12);
  }
  // ── Portamento / slide glide ──
  // For FL-style slide notes (folded in by mergeSlideVoices), ramp the
  // source detune (cents) so the 808 glides between pitches without
  // retriggering. Each segment's `cents` is the target detune RELATIVE to
  // this hit's base pitch; `at`/`dur` are seconds relative to `when`.
  // detune combines with playbackRate, so this bends pitch on top of the
  // base rate — identical to the sampler/synth glide path.
  if (glide && glide.length && src.detune) {
    let curTarget = 0;
    try { src.detune.setValueAtTime(0, when); } catch(_){}
    for (let gi = 0; gi < glide.length; gi++) {
      const seg = glide[gi];
      const segStart = when + Math.max(0, seg.at || 0);
      const newTarget = seg.cents || 0;
      try {
        src.detune.setValueAtTime(curTarget, segStart);
        src.detune.linearRampToValueAtTime(newTarget, segStart + Math.max(0.01, seg.dur || 0.05));
      } catch(_){}
      curTarget = newTarget;
    }
  }
  // Per-hit gain. We create a tiny per-hit gain node rather than relying on
  // the buffer source's own gain (none exists — BufferSourceNode has no gain
  // property) so per-step velocity plugs in here naturally. We also need
  // this node specifically so choke can ramp it to 0 later.
  const g = ctx.createGain();
  g.gain.value = Math.max(0, Math.min(1.5, gain == null ? 1 : gain));
  src.connect(g);
  // Optional stereo pan stage. Only add the node if pan is non-zero —
  // avoids creating useless nodes for the common center case (cheaper +
  // doesn't accumulate noise across the chain).
  if (pan && pan !== 0 && typeof ctx.createStereoPanner === "function") {
    const p = ctx.createStereoPanner();
    p.pan.value = Math.max(-1, Math.min(1, pan));
    g.connect(p);
    p.connect(entryNode);
  } else {
    g.connect(entryNode);
  }
  // Late-tick handling: if the scheduled time is meaningfully in the past
  // (> 15ms), DROP the note rather than playing it at the wrong time.
  // That keeps drums and MIDI in lockstep under iOS jitter — both layers
  // skip late events, so neither drifts ahead of the other. Under 15ms,
  // imperceptible — clamp so we don't lose drums to micro-jitter.
  const lateBy = ctx.currentTime - when;
  if (lateBy > 0.015) {
    return null;  // too late — skip
  }
  const t = Math.max(when, ctx.currentTime);
  try { src.start(t); } catch(e) { /* swallow — bad time */ }
  return { src: src, gain: g };
}

// Length of the linear ramp used to choke a previous hit, in seconds. A
// hard cut (0ms) produces a click on samples with energy at their cut
// point — a few milliseconds removes the click without being audible as
// a fade-out. Matches MPC/Renaissance behaviour.
const DRUM_CHOKE_RAMP_SEC = 0.003;

// Apply a choke to whatever was previously playing in `group`, then record
// `next` as the new occupant. `chokeContext` is the per-track map shared
// across one playback session; pass `null` for tracks with choke disabled.
//
//   chokeContext — { [groupName]: { src, gain, when } }  (mutated)
//   group        — the group string, e.g. "hihat"
//   next         — { src, gain } of the new hit
//   when         — the new hit's start time in ctx time
function applyChoke(chokeContext, group, next, when) {
  if (!chokeContext || !group || !next) return;
  const prev = chokeContext[group];
  if (prev && prev.gain && prev.src) {
    try {
      // Ramp the previous hit's gain to 0 ending at `when`, then stop the
      // buffer source a hair later. setValueAtTime first locks in the
      // current ramped value, otherwise the linear ramp can start from
      // the wrong place if multiple chokes overlap.
      //
      // Hard floor: cutStart must be STRICTLY less than `when` so the
      // ramp has positive duration. At clip boundaries (note B starts
      // exactly where note A's pattern ends), prev.when can equal when
      // and the naive max() would yield cutStart === when — a zero-
      // duration ramp that Web Audio treats as a no-op, leaving the old
      // note at full volume past the cut. 23µs (1 sample at 44.1kHz) is
      // inaudible but produces a valid ramp.
      const MIN_RAMP_SEC = 1 / 44100;
      let cutStart = Math.max(when - DRUM_CHOKE_RAMP_SEC, prev.when || 0);
      if (cutStart >= when) cutStart = when - MIN_RAMP_SEC;
      // Read the gain's current value defensively. In OfflineAudioContext,
      // reading .value before render can occasionally return 0 even when
      // the intrinsic value was set non-zero — using 0 as the ramp start
      // makes the linearRampToValueAtTime a no-op (0 → 0), defeating
      // the cut. Floor at a small positive value so the ramp always
      // engages.
      let curGainVal = prev.gain.gain.value;
      if (!(curGainVal > 0)) curGainVal = 1.0;
      prev.gain.gain.cancelScheduledValues(cutStart);
      prev.gain.gain.setValueAtTime(curGainVal, cutStart);
      prev.gain.gain.linearRampToValueAtTime(0, when);
      prev.src.stop(when + 0.001);
    } catch(e) { /* swallow — node may already be stopped */ }
  }
  chokeContext[group] = { src: next.src, gain: next.gain, when: when };
}

// Schedule the hits for a single STEP — handles ratchet/repeat by firing
// N sub-hits across the step's duration. Shared by live + preview +
// offline schedulers so behavior stays identical across all three.
//
//   ctx, entryNode, buffer  — same as scheduleDrumHit
//   stepStartCtxTime        — when the step "starts" in audio-clock seconds
//   stepDurSec              — length of one step at the current BPM/resolution
//   params                  — normalized step params from stepParams()
//   channelVolume           — multiplied into each sub-hit's gain
//   collector               — optional array to push BufferSource refs into
//                              (used by live scheduler for stop-cleanup)
//   chokeContext            — per-track shared choke state (see applyChoke).
//                              Pass null to disable choking entirely.
//   chokeKey                — string key used to bucket hits in chokeContext.
//                              For FL Studio's "Cut Itself" behaviour the
//                              caller passes the channel's padId so each pad
//                              chokes ONLY its own previous hit (e.g. a new
//                              hi-hat cuts the last hi-hat; it does not cut
//                              an 808 playing nearby). Pass null on
//                              channels with chokeEnabled === false.
function scheduleStepHits(ctx, entryNode, buffer, stepStartCtxTime, stepDurSec, params, channelVolume, collector, chokeContext, chokeKey) {
  if (!params || params.vel < DRUM_MIN_VELOCITY) return;
  const baseGain = params.vel * (channelVolume == null ? 1 : channelVolume);
  const repeat = Math.max(1, Math.min(DRUM_REPEAT_MAX, Math.round(params.repeat) || 1));
  // For ratchet>1, fire N hits across the step. The first hit is at full
  // velocity; subsequent hits decay so a 4-hit roll sounds like a tail-off
  // rather than 4 equally-loud hits (which sounds robotic). Decay curve
  // matches FL Studio's defaults: ~0.7^(n-1). Each sub-hit also chokes
  // the previous hit on the SAME pad — that's how rapid hi-hat repeats
  // produce the tight machine-gun sound instead of overlapping tails.
  for (let i = 0; i < repeat; i++) {
    const subWhen = stepStartCtxTime + (i / repeat) * stepDurSec;
    const decay   = Math.pow(0.7, i);
    const hitGain = baseGain * decay;
    const hit = scheduleDrumHit(ctx, entryNode, buffer, subWhen, hitGain, params.pitch, params.pan, chokeKey);
    if (!hit) continue;
    if (chokeContext && chokeKey) {
      applyChoke(chokeContext, chokeKey, hit, subWhen);
    }
    if (collector) collector.push(hit.src);
  }
}

// ─────────────────────────────────────────────────────────────────────────────
//  Offline pattern scheduling
// ─────────────────────────────────────────────────────────────────────────────
// For offline export, walks every pattern clip on a drum track and schedules
// every hit at once into the OfflineAudioContext. No setInterval needed —
// the offline context renders the whole graph in one synchronous-ish pass.
//
//   ctx        — OfflineAudioContext
//   entryNode  — FX chain entry for the track
//   track      — the drumSequencer track
//   buffers    — { padId: AudioBuffer } from loadKitBuffers
//   bpm        — project BPM
//   timeOffset — seconds added to every scheduled time (== startupLead during
//                offline export so all events stay after the latency-comp
//                pad at the start of the buffer)
//   fromTime   — earliest project time to schedule from (export normally 0)
//   toTime     — latest project time to schedule up to (export = total duration)
//   kitMeta    — kept in the signature for backward compat (offline callers
//                still pass it) but no longer consulted: self-choke needs
//                only the channel's own padId + chokeEnabled flag.
function scheduleDrumTrackOffline(ctx, entryNode, track, buffers, bpm, timeOffset, fromTime, toTime, kitMeta) {
  if (!track || track.type !== "drumSequencer") return;
  const clips = track.clips || [];
  // Pre-compute which padIds participate in self-choke. A channel
  // participates only when chokeEnabled is true. The key in the choke
  // context is the padId itself, so an 808 channel's hits only ever cut
  // the previous 808 hit — never an unrelated pad. Matches FL Studio's
  // "Cut Itself" toggle exactly.
  const padSelfChoke = {};
  (track.patterns || []).forEach(function(pat){
    (pat.channels || []).forEach(function(ch){
      if (ch.chokeEnabled && ch.padId) {
        padSelfChoke[ch.padId] = true;
      }
    });
  });
  const chokeContext = {};
  // Sort clips by startTime so applyChoke's per-track chokeContext reflects
  // true chronological "previous note" — without this, an array stored
  // out of time order (e.g. clip B added before clip A but placed later
  // on the timeline) would process clip A AFTER clip B, breaking cross-
  // clip cuts. Live playback's tick scheduler doesn't have this problem
  // because windows are always [earliest, latest); export needs explicit
  // sort.
  const sortedClips = (clips || []).slice().sort(function(a, b){
    return (a.startTime || 0) - (b.startTime || 0);
  });
  for (const clip of sortedClips) {
    if (clip.active === false) continue;
    const pat = findPatternInTrack(track, clip.patternId);
    if (!pat) continue;
    const stepSec = drumStepDurationSec(bpm, pat.stepsPerBeat);
    const patDur  = pat.lengthSteps * stepSec;
    if (patDur <= 0) continue;
    const clipStart = clip.startTime || 0;
    const clipEnd   = clipStart + (clip.duration || patDur);
    if (clipEnd <= fromTime || clipStart >= toTime) continue;
    // Walk every step that falls within the clip's window.
    // The pattern loops within the clip's duration. For polyrhythm, each
    // channel wraps modulo ITS OWN lengthSteps (channelLengthSteps helper),
    // so a 16-step kick and 12-step hat playing together produce proper
    // polyrhythm.
    const effStart = Math.max(clipStart, fromTime);
    const effEnd   = Math.min(clipEnd, toTime);
    // Outer walk steps the pattern's master clock — what matters is that
    // we visit every step boundary for the period of interest.
    const firstStepOffset = Math.max(0, effStart - clipStart);
    let stepIdx = Math.floor(firstStepOffset / stepSec);
    let stepTimeFromClip = stepIdx * stepSec;
    // Pre-compute whether any channel is soloed — solo overrides mute.
    const anySoloed = (pat.channels || []).some(function(c){ return c.soloed; });
    while (clipStart + stepTimeFromClip < effEnd) {
      const projectTime = clipStart + stepTimeFromClip;
      if (projectTime >= effStart && projectTime < effEnd) {
        // For each channel, fire if this step is on (and channel passes
        // mute/solo gates). Wrap step index modulo each channel's own length.
        for (const ch of (pat.channels || [])) {
          if (anySoloed && !ch.soloed) continue;
          if (!anySoloed && ch.muted) continue;
          // Skip notes-mode channels — handled by the notes walk below.
          if (drumChannelModeOf(ch) === "notes") continue;
          if (!ch.steps) continue;
          const chLen = channelLengthSteps(ch, pat);
          const chStepIdx = ((stepIdx % chLen) + chLen) % chLen;
          const rawStep = ch.steps[chStepIdx];
          const params = stepParams(rawStep);
          if (params.vel < DRUM_MIN_VELOCITY) continue;
          const buf = buffers[ch.padId];
          if (!buf) continue;
          // Apply per-channel swing on even (1-indexed) 16ths +
          // per-step shift (microtiming).
          const swingOff = drumStepSwingOffset(chStepIdx, ch.swing, stepSec);
          const shiftOff = params.shift * stepSec;
          const when = projectTime + timeOffset + swingOff + shiftOff;
          // Self-choke: when this channel has chokeEnabled, hits on it
          // cut ONLY its own previous hit (key = padId). Channels without
          // chokeEnabled pass null and overlap freely with prior hits.
          const chokeKey = padSelfChoke[ch.padId] ? ch.padId : null;
          scheduleStepHits(ctx, entryNode, buf, when, stepSec, params,
            ch.volume == null ? 1 : ch.volume, null,
            chokeContext, chokeKey);
        }
      }
      stepIdx++;
      stepTimeFromClip = stepIdx * stepSec;
    }
    // ── Notes-mode channel walk ──
    // Channels in notes mode store free MIDI notes. For each loop iteration
    // of the pattern within this clip, walk every note and schedule it if
    // its absolute project time lands in [effStart, effEnd). Pitches are
    // re-pitched in semitones relative to DRUM_CHANNEL_ROOT_PITCH. Velocity
    // exactly 0 triggers a silent cut (kills any sustained previous hit on
    // the same pad without producing sound). This is what made hi-hat rolls
    // and 808 melodic notes export correctly — previously the offline path
    // only walked steps, so notes were lost (and the channel's surviving
    // legacy steps[] field made it look like the data was flattened).
    for (const ch of (pat.channels || [])) {
      if (drumChannelModeOf(ch) !== "notes") continue;
      if (anySoloed && !ch.soloed) continue;
      if (!anySoloed && ch.muted) continue;
      const buf = buffers[ch.padId];
      if (!buf) continue;
      const notes = mergeSlideVoices(ch.notes || []).slice().sort(function(a, b){
        return (a.start || 0) - (b.start || 0);
      });
      if (notes.length === 0) continue;
      const notesTempoScale = (ch.notesBpm && ch.notesBpm > 0) ? (ch.notesBpm / bpm) : 1;
      const chokeKey = padSelfChoke[ch.padId] ? ch.padId : null;
      // Walk loop iterations of the pattern that overlap the clip's window.
      const kStart = Math.floor((effStart - clipStart) / patDur);
      const kEnd   = Math.floor((effEnd   - clipStart) / patDur);
      for (let k = kStart; k <= kEnd; k++) {
        if (k < 0) continue;
        const loopBase = clipStart + k * patDur;
        if (loopBase >= clipEnd) break;
        for (let ni = 0; ni < notes.length; ni++) {
          const n = notes[ni];
          const projectTime = loopBase + (n.start || 0) * notesTempoScale;
          if (projectTime < effStart) continue;
          if (projectTime >= effEnd) continue;
          if (projectTime >= clipEnd) continue;
          const when = projectTime + timeOffset;
          const semis = (n.pitch || DRUM_CHANNEL_ROOT_PITCH) - DRUM_CHANNEL_ROOT_PITCH;
          const rawVel = n.velocity == null ? 0.85 : n.velocity;
          // FL-style silent cut: velocity exactly 0 doesn't make sound but
          // kills any still-ringing previous hit on this pad. Producers use
          // it to clip 808 tails. Same behaviour as live playback.
          if (rawVel === 0) {
            if (chokeKey && chokeContext) {
              const prev = chokeContext[chokeKey];
              if (prev && prev.gain && prev.src) {
                try {
                  const cutStart = Math.max(when - DRUM_CHOKE_RAMP_SEC, prev.when || 0);
                  prev.gain.gain.cancelScheduledValues(cutStart);
                  prev.gain.gain.setValueAtTime(prev.gain.gain.value, cutStart);
                  prev.gain.gain.linearRampToValueAtTime(0, when);
                  prev.src.stop(when + 0.001);
                } catch(_){}
                chokeContext[chokeKey] = null;
              }
            }
            continue;
          }
          const vel = Math.max(0.001, Math.min(1.5, rawVel));
          const glideCents = (n.glide && n.glide.length)
            ? n.glide.map(function(seg){ return {
                at: (seg.at || 0) * notesTempoScale,
                cents: (seg.toPitch - (n.pitch || DRUM_CHANNEL_ROOT_PITCH)) * 100,
                dur: Math.max(0.01, (seg.dur || 0.05) * notesTempoScale),
              }; })
            : null;
          const hit = scheduleDrumHit(ctx, entryNode, buf, when,
            vel * (ch.volume == null ? 1 : ch.volume),
            semis, 0, chokeKey, glideCents);
          if (hit && chokeKey) {
            applyChoke(chokeContext, chokeKey, hit, when);
          }
        }
      }
    }
  }
}

// ═════════════════════════════════════════════════════════════════════════════
//  MIDI / Instrument system (Phase 1 — runtime synthesis)
// ═════════════════════════════════════════════════════════════════════════════
//
// Built for the new MIDI Instrument track type. Phase 1 ships a small library
// of preset instruments rendered at play-time from oscillator + filter + ADSR
// (no sample baking needed yet). Phase 4 will swap zones to real recorded
// samples while keeping this scheduling API identical — meaning anything
// built on top of these functions (live scheduler, offline export, piano
// roll preview) doesn't change when the sound engine upgrades.
//
// Design rules:
//   • Each note is one self-contained voice graph. We allocate fresh nodes
//     per note and discard after release. Cheap on modern hardware up to
//     ~32 simultaneous voices; the scheduler enforces a polyphony cap.
//   • Note timing uses AudioContext absolute time. The scheduler converts
//     project time → ctx time the same way as the drum scheduler does.
//   • Looping inside a clip works the same as drum clips: pattern modulo
//     within clip duration.

// ── Constants ────────────────────────────────────────────────────────────────
const MIDI_DEFAULT_VELOCITY = 0.8;
const MIDI_MIN_VELOCITY     = 0.05;
const MIDI_POLYPHONY_CAP    = 32;      // simultaneous voices per instrument
const MIDI_LOOKAHEAD_SEC    = 0.3;     // matches drum lookahead (iOS jitter margin)
const MIDI_TICK_INTERVAL_MS = 25;
// Middle A standard reference — used to convert MIDI note → frequency.
const MIDI_A4_FREQ = 440;
const MIDI_A4_NOTE = 69;

// Convert MIDI note number → frequency Hz. Equal temperament.
function midiNoteToFreq(noteNum) {
  return MIDI_A4_FREQ * Math.pow(2, (noteNum - MIDI_A4_NOTE) / 12);
}

// MIDI note name (C4, F#5, etc) for display. Sharps only; pick a base of C-1
// per the standard convention so MIDI 60 = C4.
const _MIDI_NOTE_NAMES = ["C","C#","D","D#","E","F","F#","G","G#","A","A#","B"];
function midiNoteName(noteNum) {
  const n = noteNum | 0;
  const octave = Math.floor(n / 12) - 1;   // MIDI 0 = C-1
  const name   = _MIDI_NOTE_NAMES[((n % 12) + 12) % 12];
  return name + octave;
}

// ── Instrument library ──────────────────────────────────────────────────────
// Phase 1 presets are runtime-synthesised: each is a recipe describing how
// to build a single voice (oscillator stack + filter + ADSR + simple FX).
// The `engine` field selects the voice-builder. Future preset types (sampler,
// granular, hybrid) get their own engine name; the scheduler dispatches on it.
// ── Categories (Phase 4) ────────────────────────────────────────────────────
// The preset browser groups instruments by category. Order here is the order
// they appear in the sidebar — Keys first because that's where most producers
// start, then synth/melodic, then bass, then atmospheric, then FX.
const BF_INSTRUMENT_CATEGORIES = [
  // Real recorded instruments via soundfont engine — listed first to give
  // them prominence. The white-ish color signals "real" vs the violet-hued
  // synth categories below.
  { id: "Instruments", icon: "♪", color: "#F8F7FA" },
  { id: "Keys",    icon: "🎹", color: "#A78BFA" },
  { id: "Lead",    icon: "✦",  color: "#C4B5FD" },
  { id: "Pluck",   icon: "✧",  color: "#F0C5A9" },
  { id: "Bass",    icon: "▮",  color: "#8B5CF6" },
  { id: "Pad",     icon: "◐",  color: "#7FB5AB" },
  { id: "Bells",   icon: "◊",  color: "#D4B147" },
  { id: "Brass",   icon: "♬",  color: "#C77575" },
  { id: "FX",      icon: "✺",  color: "#9C8AC4" },
];

// Common ADSR shapes — defined once so similar-feeling presets share envelopes.
// Producers tune individual presets by tweaking from a base shape.
const _ADSR = {
  pluck:   { attack: 0.003, decay: 0.12, sustain: 0.0,  release: 0.18 },  // hits + dies
  plucky:  { attack: 0.005, decay: 0.30, sustain: 0.45, release: 0.30 },  // hits then sustains briefly
  key:     { attack: 0.005, decay: 0.35, sustain: 0.55, release: 0.45 },  // grand piano default
  bass:    { attack: 0.008, decay: 0.20, sustain: 0.70, release: 0.15 },  // tight, no boom
  lead:    { attack: 0.005, decay: 0.10, sustain: 0.85, release: 0.20 },  // sustained, articulate
  pad:     { attack: 0.45,  decay: 0.50, sustain: 0.85, release: 0.80 },  // slow swell
  swell:   { attack: 1.20,  decay: 0.40, sustain: 0.90, release: 1.50 },  // very slow
  brass:   { attack: 0.04,  decay: 0.18, sustain: 0.78, release: 0.30 },  // attack bump
  bell:    { attack: 0.002, decay: 0.65, sustain: 0.10, release: 0.90 },  // bell ring
  fx:      { attack: 0.30,  decay: 0.40, sustain: 0.70, release: 1.20 },  // FX-friendly
};

// ── Instrument library ──────────────────────────────────────────────────────
// Phase 4 presets are runtime-synthesised: each is a recipe describing how
// to build a single voice (oscillator stack + filter + ADSR + simple FX).
// The `engine` field selects the voice-builder. Future preset types (sampler,
// granular, hybrid) get their own engine name; the scheduler dispatches on it.
//
// Tuning notes:
//   • Keys/Pluck use triangle+sine stacks with filter envelopes for transient
//   • Leads/Brass use sawtooth+square with brighter cutoffs
//   • Bass presets use sine+sawtooth with low cutoffs and tight envelopes
//   • Pads use detuned saw stacks with slow attacks
//   • Bells use sine+sine-octave-up with long releases and low sustain
//   • FX use long swells / unusual osc stacks for atmosphere
// ═════════════════════════════════════════════════════════════════════════════
//  Phase 6a — Real instrument soundfont engine
// ═════════════════════════════════════════════════════════════════════════════
//
// Loads individual GM instrument SF3 files from a public CDN and plays
// them via FluidSynth (compiled to WebAssembly via js-synthesizer). This
// gives us actual recorded instrument samples instead of synthesis.
//
// Design:
//   • One module-scope singleton (lazy created on first use)
//   • Per-instrument lazy loading (each preset = one ~1-5MB SF3 file)
//   • Each preset gets its own MIDI channel + soundfont in FluidSynth
//   • Uses ScriptProcessor (non-worklet) path for iOS Safari reliability
//   • Browser HTTP cache handles return-visit instant loading
//   • Falls back silently if js-synthesizer fails to load (e.g. CDN down)
//
// The library is loaded from a CDN via <script> tag in your index.html:
//   <script src="https://cdn.jsdelivr.net/npm/js-synthesizer@1.11.0/externals/libfluidsynth-2.4.6-with-libsndfile.js"></script>
//   <script src="https://cdn.jsdelivr.net/npm/js-synthesizer@1.11.0/dist/js-synthesizer.min.js"></script>
//
// When loaded, it exposes a global `JSSynth` object. We check for its
// presence and fail soft if missing.

// The CDN base for per-instrument SF3 files. Each path looks like
// {BASE}/Fluid_R3_GM/000.sf3 for GM program 0 (Acoustic Grand Piano).
// Hosted by github.com/marmooo/free-soundfonts on Cloudflare Pages with
// CORS pre-configured. Pre-split so each file is under 10 MB.
// CDN base. Using MuseScore_General_v0.2 — the soundfont that ships with
// MuseScore, generally regarded as one of the highest-quality free GM
// banks. Sounds noticeably more polished than GeneralUser_GS for pianos,
// strings, and many orchestral patches. Same URL pattern as before
// (<base>/<NNN>.sf3 where NNN is GM program 0-127 zero-padded).
const BF_SOUNDFONT_CDN_BASE = "https://soundfonts.pages.dev/MuseScore_General_v0.2";

// Maximum number of simultaneously-loaded soundfont patches. Each patch
// occupies ~3-5MB of decoded sample memory inside FluidSynth's WASM heap.
// 15 is the max — one per MIDI channel (16 channels minus channel 9 for
// drums). Beyond this, we evict the least-recently-used patch that isn't
// pinned by an active track. If every channel is pinned (15+ active
// MIDI tracks with distinct soundfonts), new presets can't load.
const BF_SOUNDFONT_MAX_LOADED = 15;

// Channel allocator: FluidSynth supports 16 MIDI channels. We allocate
// one per loaded instrument (each track gets a unique channel). Channel
// 9 is reserved by GM for drums, so we skip it. After LRU eviction the
// channel is returned to the free pool so it can be reused by a new
// preset cleanly. Without recycling, channels wrap modulo 16 and start
// overwriting still-loaded presets' MIDI program assignments, causing
// silent or wrong-instrument playback after enough presets have been
// touched.
const _bfSfFreeChannels = [];   // available channels (pre-populated below)
const _bfSfBusyChannels = new Set();  // channels in active use
(function _initFreePool(){
  for (let i = 0; i < 16; i++) {
    if (i === 9) continue; // GM drum channel — skip
    _bfSfFreeChannels.push(i);
  }
})();
function bfSfAllocateChannel() {
  // Prefer a free channel; if pool is empty (more presets ready than
  // channels available — shouldn't happen given LRU cap = 12), evict
  // one from the busy set arbitrarily. This is a fail-safe.
  if (_bfSfFreeChannels.length > 0) {
    const ch = _bfSfFreeChannels.shift();
    _bfSfBusyChannels.add(ch);
    return ch;
  }
  // Fallback: return the lowest busy channel. Caller may overwrite an
  // existing preset's binding — accepted because we should never reach
  // here in normal operation.
  return 0;
}
function bfSfReleaseChannel(ch) {
  if (ch == null) return;
  if (!_bfSfBusyChannels.has(ch)) return;
  _bfSfBusyChannels.delete(ch);
  _bfSfFreeChannels.push(ch);
}

// The singleton engine instance. Lazy-created the first time anything
// asks for it (typically the first preset preview).
let _bfSfEngineInstance = null;

// ── SF2/SF3 PHDR parser ──────────────────────────────────────────────
// Walks the RIFF structure of a SoundFont 2/3 file and extracts the list
// of (bank, program, name) tuples from the `phdr` (preset header) chunk.
// Used to discover what patches actually exist in a soundfont so we can
// select one we KNOW is there — avoiding the silent-failure case where
// FluidSynth's midiProgramSelect "succeeds" against a non-existent
// preset by returning FLUID_FAILED (which it can't surface to JS as a
// thrown exception).
//
// Returns: array of { bank, program, name } sorted by (bank, program).
// The last entry is the "EOP" terminator — we strip it.
//
// RIFF structure (simplified):
//   RIFF <size> sfbk
//     LIST <size> INFO  (ifil, isng, INAM, ...)
//     LIST <size> sdta  (smpl)
//     LIST <size> pdta  (phdr, pbag, pmod, pgen, inst, ibag, imod, igen, shdr)
// We only care about phdr. Each phdr record is 38 bytes:
//   20 bytes name (null-padded, ASCII)
//   2 bytes wPreset (program 0-127)
//   2 bytes wBank (bank 0-128)
//   2 bytes wPresetBagNdx
//   4 bytes dwLibrary
//   4 bytes dwGenre
//   4 bytes dwMorphology
function bfSfParsePresetList(arrayBuffer) {
  try {
    const dv = new DataView(arrayBuffer);
    const td = new TextDecoder("ascii");
    if (dv.byteLength < 12) return [];
    // RIFF header
    if (td.decode(new Uint8Array(arrayBuffer, 0, 4)) !== "RIFF") return [];
    const fileFmt = td.decode(new Uint8Array(arrayBuffer, 8, 4));
    if (fileFmt !== "sfbk") return [];

    // Walk top-level chunks looking for LIST...pdta
    let pos = 12;
    let pdtaStart = -1;
    let pdtaEnd   = -1;
    while (pos + 8 <= dv.byteLength) {
      const id   = td.decode(new Uint8Array(arrayBuffer, pos, 4));
      const size = dv.getUint32(pos + 4, true);
      const dataStart = pos + 8;
      if (id === "LIST" && dataStart + 4 <= dv.byteLength) {
        const listType = td.decode(new Uint8Array(arrayBuffer, dataStart, 4));
        if (listType === "pdta") {
          pdtaStart = dataStart + 4;
          pdtaEnd   = dataStart + size;
          break;
        }
      }
      // RIFF chunks are word-aligned: skip an extra byte if size is odd
      pos = dataStart + size + (size & 1);
    }
    if (pdtaStart < 0) return [];

    // Walk pdta sub-chunks looking for phdr
    let p = pdtaStart;
    let phdrStart = -1;
    let phdrSize  = 0;
    while (p + 8 <= pdtaEnd) {
      const id   = td.decode(new Uint8Array(arrayBuffer, p, 4));
      const size = dv.getUint32(p + 4, true);
      if (id === "phdr") {
        phdrStart = p + 8;
        phdrSize  = size;
        break;
      }
      p += 8 + size + (size & 1);
    }
    if (phdrStart < 0) return [];

    // Each preset header is 38 bytes
    const RECORD_SIZE = 38;
    const records = Math.floor(phdrSize / RECORD_SIZE);
    const out = [];
    for (let i = 0; i < records; i++) {
      const off = phdrStart + i * RECORD_SIZE;
      // Name: 20 bytes null-padded
      const nameBytes = new Uint8Array(arrayBuffer, off, 20);
      let nameEnd = 20;
      for (let j = 0; j < 20; j++) {
        if (nameBytes[j] === 0) { nameEnd = j; break; }
      }
      const name = td.decode(nameBytes.subarray(0, nameEnd)).trim();
      const program = dv.getUint16(off + 20, true);
      const bank    = dv.getUint16(off + 22, true);
      // The last record is "EOP" terminator (name === "EOP"). Skip it.
      if (name === "EOP") continue;
      out.push({ bank: bank, program: program, name: name });
    }
    return out;
  } catch (e) {
    console.warn("[soundfont] phdr parse failed:", e);
    return [];
  }
}

class BFSoundfontEngine {
  constructor() {
    // ── State ──
    // "uninit" → waiting for first call
    // "loading-lib" → js-synthesizer scripts loading or initialising
    // "ready" → engine is alive, soundfonts can be loaded
    // "error" → fatal init failure, won't recover (we fall back to synth)
    this.libStatus = "uninit";
    this.libError = null;
    // The FluidSynth instance (created once libStatus is "ready")
    this.synth = null;
    // The ScriptProcessor audio node piped into the user's master gain
    this.audioNode = null;
    // The AudioContext the synth is bound to (must be stable for synth's
    // lifetime; we re-use the app's main AudioContext)
    this.audioCtx = null;
    // Per-preset soundfont state. Key = preset id (e.g. "sf-grand-piano").
    // Value = { status, sfontId, channel, programNumber, error? }
    //   status: "idle" | "loading" | "ready" | "error"
    //   sfontId: integer returned by synth.loadSFont()
    //   channel: assigned MIDI channel for this preset
    //   programNumber: GM program (0..127) — selected on the channel
    this.presets = {};
    // Cache of loaded .sf2/.sf3 files indexed by their URL. When multiple
    // preset entries reference the SAME underlying file (e.g. 128 presets
    // from a single GM bank upload), we want to:
    //   1. Fetch the file ONCE — no duplicate 30-100 MB downloads
    //   2. Call loadSFont ONCE — the synth-side parse is expensive too
    //   3. Reuse the resulting sfontId across all presets — they just
    //      need their own channel + midiProgramSelect with their own
    //      (bank, program) pair.
    // Without this cache, a 128-preset bank would re-download and
    // re-parse the file 128 times.
    // Key: URL string. Value: { sfontId, buf (the underlying buffer for
    // PHDR re-parsing), status: "loading"|"ready"|"error", inflight:
    // Promise (when status="loading") }.
    this._sfontByUrl = {};
    // Preset IDs that are "in use" by an active track on the timeline.
    // The LRU evictor skips these even if their lastUsed is ancient. The
    // app updates this set whenever the tracks list changes (a new MIDI
    // track is added, an instrument is swapped, a track is deleted).
    // Without pinning, an idle track's preset gets evicted by a new
    // load — and when the user later presses play, that track is silent
    // because its channel binding is gone.
    this._pinnedPresetIds = new Set();
    // Pending listeners — for UI components that want to know when status
    // changes (e.g. "still loading" → "ready"). Each is a callback.
    this._listeners = new Set();
    // ── Loading-state tracking for UI ──
    // Counter-based instead of relying on enumerating presets. Whenever a
    // load starts, _activeLoads is incremented and _loadingName is set to
    // the friendly name. When the load completes, _activeLoads decrements.
    // The toast stays visible as long as _activeLoads > 0 — guarantees no
    // mid-load flicker. Multiple parallel loads (e.g. base + derived) just
    // share the counter; the most recent name wins for display.
    this._activeLoads = 0;
    this._loadingName = null;
    // Most recent load failure — exposed to the UI so the user can
    // actually see WHY a load failed (CORS, 403 from B2 quota, network
    // error, etc.) instead of just a silent loading-bar loop.
    this._lastError = null;
    // ── Diagnostics ──
    // A capped ring buffer of timestamped event strings. Rendered into
    // the on-screen debug panel so we can see exactly what's happening
    // without needing a JS console (which iOS Safari makes hard to access).
    this.diag = [];
    this.diagMax = 30;
    this._log("engine constructed");
  }

  // Push a diagnostic event. Stored both in-memory (for the panel) and
  // mirrored to console.log so desktop devs can still see it.
  _log(msg) {
    const t = new Date().toISOString().slice(11, 23);  // HH:MM:SS.mmm
    this.diag.push(t + " " + msg);
    if (this.diag.length > this.diagMax) this.diag.shift();
    console.log("[soundfont]", msg);
    // NOTE: do NOT call _notify() here. _log fires on every single
    // engine activity — including every key tap (via reconnect's log
    // call), every reaffirm, every note schedule's skip path. The
    // loading toast subscriber loops through all presets on each
    // notification, and even when state is identical, React still
    // re-evaluates the toast's IIFE which re-mounts the inline <style>
    // block and re-runs the slideIn animation. The visible result is
    // a flashing toast on every key tap.
    //
    // Subscribers only care about REAL state transitions (preset
    // status changes), which have their own explicit _notify() calls
    // inside loadPreset (status: loading → ready → error). The diag
    // panel can stay slightly stale; nobody watches it live during
    // playback anyway.
  }

  // Subscribe to status changes. Returns an unsubscribe function.
  subscribe(fn) {
    this._listeners.add(fn);
    return () => { this._listeners.delete(fn); };
  }
  _notify() {
    this._listeners.forEach(fn => { try { fn(); } catch(_){} });
  }

  // Initialise the engine if not already. Idempotent — safe to call
  // multiple times. Returns a Promise that resolves when ready (or
  // rejects on fatal error).
  //
  // If called again with a DIFFERENT masterDest than we connected to
  // last time (e.g. an FX chain rebuild produced a new entry node),
  // we automatically disconnect from the old dest and reconnect to
  // the new one. This is the safety net that keeps per-track FX
  // working through chain rebuilds — without it, the engine would
  // stay connected to an orphaned old entry node and audio would go
  // silent.
  async ensureReady(ctx, masterDest) {
    if (this.libStatus === "ready") {
      // Already initialised. If the caller wants us connected to a
      // different node than we currently are, reconnect.
      if (masterDest && this._currentMasterDest !== masterDest) {
        try { this.audioNode.disconnect(); } catch(_) {}
        try {
          this.audioNode.connect(masterDest);
          this._currentMasterDest = masterDest;
          this._log("ensureReady reconnect to new masterDest");
        } catch(e) {
          this._log("ensureReady reconnect failed: " + e);
        }
      }
      return;
    }
    if (this.libStatus === "error") throw new Error(this.libError || "soundfont engine failed");
    if (this.libStatus === "loading-lib") {
      // Already initialising — wait for it
      return new Promise((resolve, reject) => {
        const unsub = this.subscribe(() => {
          if (this.libStatus === "ready") { unsub(); resolve(); }
          else if (this.libStatus === "error") { unsub(); reject(new Error(this.libError)); }
        });
      });
    }
    this.libStatus = "loading-lib";
    this._log("ensureReady() called, libStatus=loading-lib");
    this._notify();

    // Check the global script tag exposed `JSSynth`. If not, we can't init.
    // (App's index.html must include the CDN <script> tags.)
    if (typeof JSSynth === "undefined") {
      this.libStatus = "error";
      this.libError = "JSSynth global is undefined";
      this._log("ERROR: " + this.libError + " — CDN scripts didn't load");
      this._notify();
      throw new Error(this.libError);
    }
    this._log("JSSynth global is defined ✓");

    try {
      this._log("waiting for JSSynth.waitForReady()...");
      await JSSynth.waitForReady();
      this._log("JSSynth ready ✓ sampleRate=" + ctx.sampleRate);
      this.audioCtx = ctx;
      this.synth = new JSSynth.Synthesizer();
      this.synth.init(ctx.sampleRate);
      this._log("synth instance created and init'd");
      // ScriptProcessor buffer size: smaller = less latency, but higher
      // risk of dropouts when the JS thread is busy. 8192 was too much
      // (~170ms at 48 kHz) — soundfont notes lagged drums by exactly
      // that amount. 2048 brings it to ~42 ms which is below the
      // psychoacoustic threshold for most users and matches typical
      // BufferSource latency, putting drums and soundfonts in sync.
      this.audioNode = this.synth.createAudioNode(ctx, 2048);
      this._log("audio node created (ScriptProcessor, 2048 buf)");
      if (masterDest) {
        this.audioNode.connect(masterDest);
        this._currentMasterDest = masterDest;
        this._log("audioNode connected to masterDest");
      } else {
        this.audioNode.connect(ctx.destination);
        this._currentMasterDest = ctx.destination;
        this._log("audioNode connected to ctx.destination (fallback)");
      }
      this.libStatus = "ready";
      this._log("libStatus=ready ✓");
      this._notify();
    } catch(e) {
      this.libStatus = "error";
      this.libError = String(e && e.message || e);
      this._log("ERROR during init: " + this.libError);
      this._notify();
      throw e;
    }
  }

  // Re-attach the audio node to the destination. Called after the page
  // returns from background — iOS Safari sometimes severs the ScriptProcessor's
  // audio output connection when the AudioContext is suspended/interrupted,
  // causing midiNoteOn to fire but produce no audible sound. Calling
  // .connect() again restores the routing. Idempotent — connecting a node
  // that's already connected is a no-op.
  reconnect(masterDest) {
    if (this.libStatus !== "ready" || !this.audioNode) return;
    try {
      // Disconnect first to be safe (some browsers may have a stale
      // connection that's not actually routing audio). Wrapped because
      // disconnect() throws if nothing is connected.
      try { this.audioNode.disconnect(); } catch(_) {}
      const dest = masterDest || (this.audioCtx && this.audioCtx.destination);
      if (dest) {
        this.audioNode.connect(dest);
        this._currentMasterDest = dest;
        this._log("audioNode reconnected after resume");
      }
    } catch(e) {
      this._log("reconnect failed: " + (e && e.message || e));
    }
  }

  // Load a per-preset soundfont if not already loaded. Each preset
  // corresponds to one .sf3 file on the CDN. Once loaded, the preset's
  // MIDI channel is selected to the right program so future noteOn calls
  // play the right instrument.
  //
  // presetId: our internal id ("sf-grand-piano" or "sf_<timestamp>_<user>")
  // programNumber: GM program (0-127) — index of the SF3 file on the CDN
  // customUrl: optional. If provided, fetch from this URL instead of
  //            building from BF_SOUNDFONT_CDN_BASE. Used for admin-uploaded
  //            soundfonts hosted on Backblaze B2.
  // bankNumber: SoundFont bank (0-128). Critical for multi-preset banks
  //            where multiple admin entries reference the same .sf2 file
  //            but each picks out a different (bank, program). Without
  //            this, all entries would resolve to the same first preset.
  async loadPreset(presetId, programNumber, customUrl, bankNumber) {
    const existing = this.presets[presetId];
    if (existing && existing.status === "ready") return existing;
    if (existing && existing.status === "loading") {
      // Wait for in-progress load
      return new Promise((resolve, reject) => {
        const unsub = this.subscribe(() => {
          const cur = this.presets[presetId];
          if (cur && cur.status === "ready") { unsub(); resolve(cur); }
          else if (cur && cur.status === "error") { unsub(); reject(new Error(cur.error)); }
        });
      });
    }
    // If a previous attempt failed, clear the error and allow a fresh
    // retry. Many failures are transient (engine library not loaded
    // yet on the first browser open, brief network blip, etc.) and
    // permanently marking the preset as failed leaves stale FAILED
    // badges all over the sound library that never recover. The
    // timeline scheduler doesn't pound this endpoint anymore (its
    // preload guard checks for status==="ready" up-front), so the
    // retry-loop concern is moot.
    if (existing && existing.status === "error") {
      delete this.presets[presetId];
    }
    if (this.libStatus !== "ready") {
      this._log("loadPreset(" + presetId + "): engine not ready, libStatus=" + this.libStatus);
      throw new Error("engine not ready");
    }

    // ── LRU eviction: if we're at the cap, unload the oldest UNPINNED preset ──
    // Only presets NOT currently in use by an active track are candidates.
    // If every loaded preset is pinned, we still load the new one (the
    // channel allocator will fail-soft to channel 0). With N >= 15 active
    // tracks each on a distinct soundfont, the user has hit a real hardware
    // limit — we can't help that, but at least we don't silently kill an
    // in-use preset.
    const readyKeys = Object.keys(this.presets).filter((k) => this.presets[k].status === "ready");
    if (readyKeys.length >= BF_SOUNDFONT_MAX_LOADED) {
      const unpinned = readyKeys.filter((k) => !this._pinnedPresetIds.has(k));
      if (unpinned.length > 0) {
        // Sort by lastUsed ascending (oldest first); evict the very oldest UNPINNED.
        unpinned.sort((a, b) => (this.presets[a].lastUsed || 0) - (this.presets[b].lastUsed || 0));
        const victim = unpinned[0];
        const v = this.presets[victim];
        if (v && v.sfontId != null) {
          try {
            this.synth.unloadSFont(v.sfontId);
            this._log("evicted LRU preset " + victim + " (sfontId=" + v.sfontId + ")");
          } catch(e) {
            this._log("evict failed for " + victim + ": " + (e && e.message || e));
          }
          // Release the channel so it can be reassigned cleanly to a new
          // preset. Without this, the channel counter wrapped mod 16 and
          // we'd silently overwrite still-loaded presets' channel bindings,
          // causing midiNoteOn to play the wrong instrument or no sound.
          bfSfReleaseChannel(v.channel);
          delete this.presets[victim];
          this._notify();
        }
      } else {
        this._log("LRU full but all " + readyKeys.length + " presets pinned — proceeding without eviction");
      }
    }

    // URL resolution: customUrl wins (admin-uploaded B2 soundfonts), else
    // fall back to the per-program CDN pattern.
    let url;
    if (customUrl && typeof customUrl === "string" && customUrl.length > 0) {
      url = customUrl;
    } else {
      const programStr = String(programNumber).padStart(3, "0");
      url = BF_SOUNDFONT_CDN_BASE + "/" + programStr + ".sf3";
    }

    this.presets[presetId] = { status: "loading", programNumber };
    this._log("loadPreset(" + presetId + ") -> " + url);
    try { if (typeof bfDebugLogVerbose === "function") bfDebugLogVerbose("[FLUID] loadPreset START id=" + presetId + " bank=" + (bankNumber||0) + " prog=" + (programNumber||0)); } catch(_){}

    // ── Decide whether this load will actually be slow ──
    // The loading toast is for user-perceptible waits — i.e. when a
    // .sf2 file needs to be downloaded from R2/B2 and parsed by
    // FluidSynth. Switching between presets within a bank that's
    // ALREADY loaded (e.g. picking "Alto Sax" then "Tenor Sax" from
    // the same multi-preset .sf2) only triggers a midiProgramSelect
    // rebind — that's near-instant and the toast just flashes
    // unnecessarily. We detect that case here by checking whether the
    // URL's bytes are already in the engine's sfont cache.
    const urlCache = this._sfontByUrl[url];
    const willActuallyFetch = !urlCache || urlCache.status === "error";
    // An in-flight load for the same URL counts as "actually fetching"
    // because the user is still waiting — show the toast so they know
    // it's in progress.
    const willWaitForInflight = urlCache && urlCache.status === "loading";

    if (willActuallyFetch || willWaitForInflight) {
      // ── Bump active-load counter ──
      // The UI's loading toast tracks this counter. As long as it's > 0,
      // the toast stays visible — regardless of micro-flips between
      // preset statuses or any other state churn. The friendly name is
      // captured from BF_INSTRUMENTS so the toast knows what to show.
      this._activeLoads += 1;
      try {
        const meta = (typeof BF_INSTRUMENTS !== "undefined") ? BF_INSTRUMENTS[presetId] : null;
        // For derived bank-preset ids ("<baseId>::<bank>.<program>"), look
        // up the base bank's name instead so the toast shows something
        // meaningful instead of an internal id.
        let displayName = (meta && meta.name) ? meta.name : null;
        if (!displayName && presetId.indexOf("::") > 0) {
          const baseId = presetId.split("::")[0];
          const baseMeta = (typeof BF_INSTRUMENTS !== "undefined") ? BF_INSTRUMENTS[baseId] : null;
          if (baseMeta && baseMeta.name) displayName = baseMeta.name;
        }
        this._loadingName = displayName || presetId;
      } catch(_) {
        this._loadingName = presetId;
      }
      this._notify();
    }
    // Track whether THIS call contributed to the counter, so the
    // cleanup paths below only decrement when they should.
    const _bumpedCounter = willActuallyFetch || willWaitForInflight;

    try {
      // ── sfont cache lookup ─────────────────────────────────────────
      // If another preset already loaded this URL (e.g. earlier preset
      // from the same bank), reuse its sfontId + buffer. Saves the
      // download + the synth-side parse, which are the expensive bits.
      let cacheEntry = this._sfontByUrl[url];
      let sfontId;
      let buf;
      if (cacheEntry && cacheEntry.status === "ready") {
        sfontId = cacheEntry.sfontId;
        buf     = cacheEntry.buf;
        this._log("reusing cached sfont for " + url + " sfontId=" + sfontId);
      } else if (cacheEntry && cacheEntry.status === "loading") {
        // Another preset is already fetching this URL — wait for it.
        this._log("awaiting in-flight sfont load for " + url);
        const ready = await cacheEntry.inflight;
        sfontId = ready.sfontId;
        buf     = ready.buf;
      } else {
        // First time we've seen this URL — fetch + decode + load.
        const inflight = (async () => {
          this._log("fetching " + url);
          let resp;
          try {
            // Explicit CORS mode + no-store cache. Without cors mode set
            // explicitly Safari sometimes silently downgrades to no-cors
            // which makes the response opaque and arrayBuffer() empty.
            resp = await fetch(url, { mode: "cors", credentials: "omit" });
          } catch (fetchErr) {
            // Safari throws "Load failed" with no detail on CORS / DNS /
            // certificate / mixed-content failures. Re-throw with more
            // context so the user-facing error toast is actionable.
            const detail = (fetchErr && fetchErr.message) || String(fetchErr);
            // Extract the host so the user can identify which CDN/bucket failed.
            let host = "";
            try { host = new URL(url).host; } catch(_) {}
            throw new Error(
              "fetch network error" + (host ? " (" + host + ")" : "") +
              ": " + detail + " — likely CORS, DNS or HTTPS issue"
            );
          }
          if (!resp.ok) {
            // Capture status + a snippet of body for diagnostics.
            let bodyHint = "";
            try {
              const t = await resp.text();
              if (t) bodyHint = " body:" + t.slice(0, 120).replace(/\s+/g, " ");
            } catch(_) {}
            throw new Error("HTTP " + resp.status + " " + (resp.statusText || "") + bodyHint);
          }
          const bytes = await resp.arrayBuffer();
          this._log("arrayBuffer received, size=" + bytes.byteLength);
          try { if (typeof bfDebugLog === "function") bfDebugLog("[SF STAGE] arrayBuffer " + bytes.byteLength + " bytes from " + (function(){ try { return new URL(url).host; } catch(_) { return "?"; } })()); } catch(_){}
          if (!bytes || bytes.byteLength === 0) {
            throw new Error("empty response body (zero bytes received)");
          }
          try { if (typeof bfDebugLog === "function") bfDebugLog("[SF STAGE] calling loadSFont (parse + register)"); } catch(_){}
          const id = await this.synth.loadSFont(bytes);
          this._log("loadSFont ok, sfontId=" + id);
          try { if (typeof bfDebugLog === "function") bfDebugLog("[SF STAGE] loadSFont OK, sfontId=" + id); } catch(_){}
          return { sfontId: id, buf: bytes };
        })();
        cacheEntry = { status: "loading", inflight };
        this._sfontByUrl[url] = cacheEntry;
        try {
          const ready = await inflight;
          cacheEntry.status  = "ready";
          cacheEntry.sfontId = ready.sfontId;
          cacheEntry.buf     = ready.buf;
          sfontId = ready.sfontId;
          buf     = ready.buf;
        } catch (e) {
          // Mark cache entry as failed so subsequent presets surface
          // the error rather than waiting forever on a dead promise.
          this._sfontByUrl[url] = { status: "error", error: e };
          throw e;
        }
      }

      const channel = bfSfAllocateChannel();
      // ── Patch finder ─────────────────────────────────────────────
      // Single-instrument soundfont files don't always put their patch
      // at (bank 0, program 0). Many ripped-from-larger-bank SFs leave
      // it at the original GM program slot (e.g. flute at prog 73). If
      // we ask FluidSynth for (0, 0) and the file has nothing there,
      // midiNoteOn produces silence and the user thinks the preset is
      // broken.
      //
      // FluidSynth's midiProgramSelect returns FLUID_FAILED (a value, not
      // an exception) on missing presets, so try/catch can't detect it.
      // Instead we parse the SF2 file's `phdr` (preset header) chunk
      // directly to discover what (bank, program) pairs actually exist,
      // then select the first one. This works on ANY .sf2/.sf3 file
      // because the RIFF chunk structure is identical across both.
      const sfPresets = bfSfParsePresetList(buf);
      // ── Back-populate BF_INSTRUMENTS[presetId].presets ─────────────
      // For banks uploaded BEFORE the backend started storing the
      // parsed preset list on the DB record, the BF_INSTRUMENTS entry
      // arrives with no `presets` field. The engine has just parsed the
      // PHDR here, so populate it now — that's what the piano-roll
      // toolbar checks to decide whether to show the bank-preset sub-
      // picker. This also covers banks where the PHDR has more presets
      // than the upload form captured (e.g. a re-upload). We only
      // populate when the parse turns up >1 — single-preset SFs don't
      // need the picker.
      try {
        if (sfPresets && sfPresets.length > 1 && typeof BF_INSTRUMENTS === "object") {
          // Find every BF_INSTRUMENTS entry whose customUrl matches this
          // URL (multi-preset banks might be referenced under multiple
          // ids if a future feature creates derived copies).
          const allIds = Object.keys(BF_INSTRUMENTS);
          for (let i = 0; i < allIds.length; i++) {
            const m = BF_INSTRUMENTS[allIds[i]];
            if (!m || m.engine !== "soundfont-v1") continue;
            if (m.customUrl && m.customUrl === url) {
              if (!m.presets || m.presets.length <= 1) {
                m.presets = sfPresets.slice();
                this._log("back-populated BF_INSTRUMENTS[" + allIds[i] + "].presets (" + sfPresets.length + ")");
              }
            }
          }
        }
      } catch (e) {
        this._log("back-populate failed: " + (e && e.message || e));
      }
      let selectedBank = 0;
      let selectedProgram = programNumber;
      // Caller-supplied bank — defaults to 0 if not passed.
      const requestedBank = (bankNumber != null) ? bankNumber : 0;
      if (sfPresets.length > 0) {
        // Preference order:
        //   1. Exact (bank, program) match — required for multi-preset
        //      banks where many entries share the same file but each
        //      wants its OWN preset.
        //   2. (bank=0, program) match — single-instrument SFs ripped
        //      from a bank often leave their program at the original
        //      GM slot.
        //   3. First available preset — last-resort fallback.
        let chosen = sfPresets.find(function(p){
          return p.bank === requestedBank && p.program === programNumber;
        });
        if (!chosen && requestedBank !== 0) {
          chosen = sfPresets.find(function(p){
            return p.bank === 0 && p.program === programNumber;
          });
        }
        if (!chosen) chosen = sfPresets[0];
        selectedBank = chosen.bank;
        selectedProgram = chosen.program;
        this._log("parsed phdr: " + sfPresets.length + " preset(s), requested=(" +
          requestedBank + "," + programNumber + "), picked=(" +
          selectedBank + "," + selectedProgram + ") (" + (chosen.name || "?") + ")");
      } else {
        this._log("phdr parse found no presets — falling back to bank=" + requestedBank + " prog=" + programNumber);
        selectedBank = requestedBank;
      }
      try {
        this.synth.midiProgramSelect(channel, sfontId, selectedBank, selectedProgram);
      } catch(e) {
        this._log("midiProgramSelect threw: " + (e && e.message || e));
      }
      this._log("midiProgramSelect ch=" + channel + " bank=" + selectedBank + " prog=" + selectedProgram);
      this.presets[presetId] = {
        status: "ready",
        sfontId,
        channel,
        programNumber,
        selectedBank,
        selectedProgram,
        // Track the source URL too — so future eviction logic could
        // tell us when ALL presets from a given URL are gone and we
        // can release the cached buffer.
        sourceUrl: url,
        lastUsed: Date.now(),  // For LRU eviction
      };
      this._log("preset " + presetId + " READY on ch=" + channel);
      // ── Decrement active-load counter ── (only if we bumped it)
      if (_bumpedCounter) {
        this._activeLoads = Math.max(0, this._activeLoads - 1);
        if (this._activeLoads === 0) this._loadingName = null;
        this._notify();
      }
      return this.presets[presetId];
    } catch(e) {
      // Diagnostic surface to the admin Debug Log overlay. When a load
      // fails silently from the user's perspective ("toast flashes then
      // disappears"), this is the single place that captures *why*.
      // Includes the bank's display name, the host portion of the URL,
      // and the underlying error message — actionable on a phone.
      try {
        if (typeof bfDebugLog === "function") {
          const meta = (typeof BF_INSTRUMENTS !== "undefined") ? BF_INSTRUMENTS[presetId] : null;
          const friendly = (meta && meta.name) || presetId;
          let host = "";
          try { host = new URL(customUrl || "").host; } catch(_) {}
          const errMsg = String(e && e.message || e);
          bfDebugLog("[SF FAIL] " + friendly + (host ? " @ " + host : "") + " — " + errMsg);
        }
      } catch(_){}
      // Release the channel if one was already allocated for this
      // attempt — otherwise repeated retries leak channels and the
      // 16-channel FluidSynth pool fills up with phantom bindings.
      // Look for any existing.channel from a half-completed run that
      // wrote partial state before throwing.
      try {
        const partial = this.presets[presetId];
        if (partial && partial.channel != null) {
          bfSfReleaseChannel(partial.channel);
        }
      } catch(_) { /* defensive */ }
      this.presets[presetId] = {
        status: "error",
        error: String(e && e.message || e),
        programNumber,
      };
      this._log("ERROR loadPreset(" + presetId + "): " + (e && e.message || e));
      // Surface the most recent error for the UI so users / admins can
      // actually see WHY loads are failing (B2 quota, CORS, network).
      try {
        const meta = (typeof BF_INSTRUMENTS !== "undefined") ? BF_INSTRUMENTS[presetId] : null;
        const friendly = (meta && meta.name) || presetId;
        this._lastError = {
          name: friendly,
          message: String(e && e.message || e),
          time: Date.now(),
        };
      } catch(_){}
      // ── Decrement on error path too ── (only if we bumped it)
      if (_bumpedCounter) {
        this._activeLoads = Math.max(0, this._activeLoads - 1);
        if (this._activeLoads === 0) this._loadingName = null;
        this._notify();
      }
      throw e;
    }
  }

  // Schedule a note for a loaded preset. Returns a voice-handle compatible
  // with the existing scheduler interface ({ stop(when) }).
  //
  // If the preset isn't loaded yet, this returns a no-op handle so the
  // scheduler doesn't crash. Caller can pre-load via loadPreset().
  //
  // noteCutMode: optional. Controls how this note interacts with already-
  // sounding notes on the SAME preset:
  //   "off"        — default. Notes can stack indefinitely.
  //   "same-pitch" — kill any already-sounding note at this exact pitch.
  //   "mono"       — kill ALL already-sounding notes on this preset.
  // Cuts trigger a midiNoteOff on the conflicting pitches before the new
  // noteOn so FluidSynth releases the old voice cleanly (no clicks).
  scheduleNote(presetId, pitch, velocity, when, duration, noteCutMode) {
    const p = this.presets[presetId];
    if (!p || p.status !== "ready" || !this.synth) {
      // Log only occasionally to avoid spam during failed playback
      if (!this._lastSilentLog || Date.now() - this._lastSilentLog > 1000) {
        this._lastSilentLog = Date.now();
        this._log("scheduleNote(" + presetId + ") SKIPPED: preset status=" +
          (p ? p.status : "missing"));
      }
      return { stop: () => {} };
    }
    // LRU touch — fast path, no allocation
    p.lastUsed = Date.now();
    const ch = p.channel;
    const vel = Math.max(1, Math.min(127, Math.round((velocity == null ? 0.85 : velocity) * 127)));
    const pitchInt = Math.round(pitch);
    try {
      // ── Timing compensation ──────────────────────────────────────
      // The ScriptProcessor used by FluidSynth WASM has an inherent
      // buffer latency of (bufferSize / sampleRate) seconds. At 2048
      // samples / 48 kHz that's ~42 ms. midiNoteOn() queues a note
      // for the NEXT buffer fill, so the sample doesn't reach the
      // speaker until ~42 ms after the call. Drum tracks (which use
      // BufferSource.start(when) routed direct to destination) have
      // negligible buffer latency, so they hit the speaker on time.
      // We subtract the ScriptProcessor buffer latency from the delay
      // so midiNoteOn fires early enough that the resulting sample
      // reaches the speaker at the actual scheduled time. Must match
      // the buffer size passed to createAudioNode() in ensureReady().
      const SP_BUFFER_SIZE = 2048;
      const spLatencySec = SP_BUFFER_SIZE / this.audioCtx.sampleRate;
      const delay = Math.max(0, when - this.audioCtx.currentTime - spLatencySec);
      const startMs = Math.max(0, delay * 1000);
      const stopMs  = Math.max(0, (delay + Math.max(0.05, duration || 0.3)) * 1000);
      const synth = this.synth;
      // Capture preset details for per-note rebind — defends against
      // ANY case where FluidSynth's channel state drifted between when
      // this preset was loaded and when the note actually fires (e.g.
      // another preset's load reassigning state, internal reset on
      // ScriptProcessor reconnect, iOS audio session disruption). The
      // cost is one program-select op per note (~microseconds), but it
      // guarantees the note plays the right instrument. Without this,
      // the 2nd MIDI track's piano-roll preview was silent even though
      // the timeline path worked (because timeline does a bulk
      // reaffirm at doPlay start).
      const rebindSfontId = p.sfontId;
      const rebindBank    = (p.selectedBank != null) ? p.selectedBank : 0;
      const rebindProg    = (p.selectedProgram != null) ? p.selectedProgram : 0;
      // ── Note-cut handling ────────────────────────────────────────
      // If noteCutMode is set, kill any conflicting still-sounding
      // notes from this same preset BEFORE the new noteOn fires. We
      // track active voices in _activeVoices[presetId] — each entry
      // has { pitch, cutoff } where cutoff() cancels both noteOn and
      // forces a noteOff. Cuts are scheduled at the same `when` time
      // (minus a tiny lead) so the old voice releases just before the
      // new one starts — clean transition, no audible click.
      if (!this._activeVoices) this._activeVoices = {};
      if (!this._activeVoices[presetId]) this._activeVoices[presetId] = [];
      const sameKey = (noteCutMode === "same-pitch");
      const monoKey = (noteCutMode === "mono");
      if (sameKey || monoKey) {
        const voices = this._activeVoices[presetId];
        const cutDelay = Math.max(0, (when - this.audioCtx.currentTime - spLatencySec) * 1000);
        // Cancel matching voices. Walk backwards so splice doesn't
        // skip entries during iteration.
        for (let vi = voices.length - 1; vi >= 0; vi--) {
          const av = voices[vi];
          if (monoKey || (sameKey && av.pitch === pitchInt)) {
            try { av.cutoff(cutDelay); } catch(_) {}
            voices.splice(vi, 1);
          }
        }
      }
      // Log only the first few notes to avoid spamming the diag during playback
      if (!this._noteLogCount) this._noteLogCount = 0;
      if (this._noteLogCount < 3) {
        this._noteLogCount++;
        this._log("scheduleNote ch=" + ch + " pitch=" + pitchInt + " vel=" + vel + " delay=" + delay.toFixed(3) + "s");
      }
      const noteOnTimer = setTimeout(() => {
        try {
          // Force the channel to point at THIS preset's program right
          // before noteOn — idempotent if state was already correct,
          // restorative if not.
          synth.midiProgramSelect(ch, rebindSfontId, rebindBank, rebindProg);
        } catch(_) {}
        try { synth.midiNoteOn(ch, pitchInt, vel); } catch(e){ this._log("noteOn err: " + e.message); }
      }, startMs);
      const noteOffTimer = setTimeout(() => {
        try { synth.midiNoteOff(ch, pitchInt); } catch(_){}
        // Remove from active voices on natural completion
        const voicesEnd = this._activeVoices && this._activeVoices[presetId];
        if (voicesEnd) {
          const i = voicesEnd.indexOf(voiceEntry);
          if (i >= 0) voicesEnd.splice(i, 1);
        }
      }, stopMs);
      // Register this voice so future cuts on this preset can target it.
      const voiceEntry = {
        pitch: pitchInt,
        cutoff: (atMs) => {
          // Cancel the pending noteOn if it hasn't fired yet; otherwise
          // schedule an immediate-ish noteOff to release the active voice.
          clearTimeout(noteOnTimer);
          clearTimeout(noteOffTimer);
          const fireAt = Math.max(0, atMs == null ? 0 : atMs);
          setTimeout(() => {
            try { synth.midiNoteOff(ch, pitchInt); } catch(_) {}
          }, fireAt);
        },
      };
      this._activeVoices[presetId].push(voiceEntry);
      return {
        stop: (whenStop) => {
          clearTimeout(noteOnTimer);
          clearTimeout(noteOffTimer);
          try { synth.midiNoteOff(ch, pitchInt); } catch(_){}
          // Remove from active voices on manual stop
          const voicesStop = this._activeVoices && this._activeVoices[presetId];
          if (voicesStop) {
            const i = voicesStop.indexOf(voiceEntry);
            if (i >= 0) voicesStop.splice(i, 1);
          }
        },
      };
    } catch(e) {
      this._log("ERROR scheduleNote: " + e.message);
      return { stop: () => {} };
    }
  }

  // Returns the loading status of a preset for UI display.
  getPresetStatus(presetId) {
    const p = this.presets[presetId];
    if (!p) return "idle";
    return p.status;
  }

  // DEPRECATED (no longer called). FluidSynth ignores these envelope CCs
  // (#73/75/72) by design, which is why the SpessaSynth-live engine exists.
  // Edit Sound overrides are now applied per-voice via envelope GENERATORS
  // on the SpessaSynth-live engine (see _applyEnvelopeGenerators), routed
  // through scheduleMidiNote. Kept only to avoid touching a class method
  // referenced nowhere; safe to delete in a future pass.
  setChannelOverrideCCs(presetId, overrides) {
    if (!this.synth || this.libStatus !== "ready") return;
    const p = this.presets[presetId];
    if (!p || p.status !== "ready" || p.channel == null) return;
    const ch = p.channel;
    let ccs = [];
    try { ccs = (typeof bfSoundfontOverrideCCs === "function") ? bfSoundfontOverrideCCs(overrides) : []; } catch(_){ ccs = []; }
    // Always reset the relevant CCs to neutral first, so removing an
    // override returns to the native envelope (not a stale value).
    const neutral = [ {cc:73,value:64},{cc:75,value:64},{cc:72,value:64},{cc:74,value:64},{cc:71,value:64} ];
    const send = function(synth, c, v) {
      // js-synthesizer exposes CC via midiControl(channel, ctrl, value).
      // Wrapped so an unexpected binding can't break playback.
      try { if (typeof synth.midiControl === "function") { synth.midiControl(ch, c, v); return; } } catch(_){}
      try { if (typeof synth.midiCC === "function") { synth.midiCC(ch, c, v); return; } } catch(_){}
      try { if (typeof synth.midiControlChange === "function") { synth.midiControlChange(ch, c, v); return; } } catch(_){}
    };
    for (let i = 0; i < neutral.length; i++) send(this.synth, neutral[i].cc, neutral[i].value);
    for (let i = 0; i < ccs.length; i++) send(this.synth, ccs[i].cc, ccs[i].value);
    this._log("setChannelOverrideCCs " + presetId + " ch=" + ch + " applied " + ccs.length + " overrides");
  }

  // Update the set of preset IDs that are "in use" by an active track.
  // Called from the React layer whenever the tracks list changes. The
  // pinned set protects these presets from LRU eviction so adding a
  // brand-new MIDI track with a new soundfont doesn't silently destroy
  // an already-loaded one that's still attached to another track.
  setPinnedPresetIds(ids) {
    if (!ids) {
      this._pinnedPresetIds = new Set();
      return;
    }
    this._pinnedPresetIds = new Set(ids);
  }

  // Re-assert the channel→preset bindings for every loaded preset.
  // Called at the top of doPlay() to defend against any subtle case
  // where a channel's program got reassigned (e.g. by a stray MIDI
  // event, by loading a new soundfont, by FluidSynth's internal
  // reset logic). Cheap — just a midiProgramSelect per loaded
  // preset (max 12 calls per the LRU cap).
  reaffirmAllChannelBindings() {
    if (!this.synth || this.libStatus !== "ready") return;
    const ids = Object.keys(this.presets);
    for (let i = 0; i < ids.length; i++) {
      const p = this.presets[ids[i]];
      if (!p || p.status !== "ready") continue;
      if (p.sfontId == null || p.channel == null) continue;
      const bank = (p.selectedBank != null) ? p.selectedBank : 0;
      const prog = (p.selectedProgram != null) ? p.selectedProgram : 0;
      try {
        this.synth.midiProgramSelect(p.channel, p.sfontId, bank, prog);
      } catch(e) {
        this._log("reaffirm failed for " + ids[i] + ": " + (e && e.message || e));
      }
    }
  }

  // Rewire the engine's output to a new master destination. Called when
  // the host's audio routing rebuilds — most commonly when iOS swaps
  // the output device (plug/unplug headphones) and the host's master
  // gain node gets reconstructed against the new AudioContext.
  //
  // SAFETY: this method is intentionally conservative. It only attempts
  // a disconnect+reconnect of the existing audioNode against the new
  // destination. If the destination's context doesn't match the engine's
  // context (would throw in connect()), we silently no-op and return
  // false — caller can fall through to a fresh editor-mount load. We
  // do NOT null out synth/audioNode/presets here; an in-flight load
  // could be racing with us and wiping state mid-flight would corrupt it.
  rewireOutput(newMasterDest) {
    if (!this.audioNode) return false;
    if (!newMasterDest) return false;
    // Context check: connect() across different AudioContexts throws.
    // If contexts don't match, just bail — the engine's existing audio
    // graph keeps working against the old context (which iOS might
    // still consume), and a full re-init only happens if the user
    // explicitly reopens a piano roll.
    if (newMasterDest.context && this.audioCtx &&
        newMasterDest.context !== this.audioCtx) {
      this._log("rewireOutput: context mismatch, skipping reconnect");
      return false;
    }
    try {
      this.audioNode.disconnect();
    } catch(_) { /* may already be disconnected */ }
    try {
      this.audioNode.connect(newMasterDest);
      this._currentMasterDest = newMasterDest;
      this._log("rewireOutput: audioNode reconnected to new master ✓");
      return true;
    } catch(e) {
      this._log("rewireOutput: reconnect failed: " + (e && e.message || e));
      return false;
    }
  }
}

// ════════════════════════════════════════════════════════════════════════════
//  BFSpessaLiveEngine — real-time SpessaSynth soundfont engine (Stage 1)
// ════════════════════════════════════════════════════════════════════════════
//
// WHY THIS EXISTS:
// FluidSynth (js-synthesizer) ignores MIDI CC#73/72/75 (attack/decay/release)
// by default — those require custom SoundFont modulators that almost no .sf2
// defines (confirmed in FluidSynth's own docs). So per-note ADSR control is
// impossible through FluidSynth. SpessaSynth, by contrast, implements these
// as built-in "Extended" controllers and applies them per voice — which is
// exactly what the offline export already uses successfully.
//
// This engine runs SpessaSynth in REAL TIME so that soundfont tracks WITH an
// ADSR/filter override get true per-note attack live, matching the export.
// Tracks WITHOUT overrides keep using FluidSynth (unchanged). Stage 2 wires
// the routing decision; Stage 1 just defines this class in isolation.
//
// ARCHITECTURE (mirrors BFSoundfontEngine so it slots into the per-track model):
//   • ensureReady(ctx, masterDest) — dynamic-import spessasynth_core, create a
//     SpessaSynthProcessor + a ScriptProcessor node whose onaudioprocess pulls
//     audio from synth.process() in <=128-sample chunks. Connect to masterDest.
//   • loadPreset(presetId, program, url, bank) — fetch sf bytes (reuse the
//     shared cache), add the sound bank, remember the (bank,program) to assign.
//   • scheduleNote(presetId, pitch, velocity, when, duration, noteCutMode,
//     overrides) — noteOn/noteOff timed via setTimeout (same model as the
//     FluidSynth engine), applying the override CCs to the channel first so
//     attack/decay/release/filter shape THIS note.
//   • reconnect / rewireOutput / getPresetStatus — same shapes as FluidSynth.
class BFSpessaLiveEngine {
  constructor() {
    this.libStatus = "uninit";   // uninit | loading-lib | ready | error
    this.libError = null;
    this.synth = null;           // SpessaSynthProcessor instance
    this.audioNode = null;       // ScriptProcessor pumping synth.process()
    this.audioCtx = null;
    this._currentMasterDest = null;
    this._SpessaProcessor = null;
    this._SoundBankLoader = null;
    // Per-preset state. Key = preset id.
    //   { status, channel, bank, program, url, matchedPreset? }
    this.presets = {};
    // Cache of added sound banks by URL so we don't double-add.
    this._banksByUrl = {};
    // Channel allocation — one channel per distinct preset id (like the
    // FluidSynth engine). Channel 0 reserved for the first preset.
    this._nextChannel = 0;
    this._channelByPreset = {};
    // Active voices per preset for note-cut handling.
    this._activeVoices = {};
    // Debug log ring (admin diagnostics), mirrors FluidSynth engine.
    this._logs = [];
    this._readyPromise = null;
  }

  _log(msg) {
    const line = "[spessa-live] " + msg;
    this._logs.push(Date.now() + " " + line);
    if (this._logs.length > 200) this._logs.shift();
    // Also surface in the shared debug log when available.
    try { if (typeof bfDebugLog === "function") bfDebugLog(line); } catch(_){}
  }

  // Lazily import spessasynth_core, build the processor + audio node.
  // Returns a promise that resolves when libStatus === "ready".
  ensureReady(ctx, masterDest) {
    if (this.libStatus === "ready") {
      // If the master changed, rewire.
      if (masterDest && masterDest !== this._currentMasterDest) {
        try { this.rewireOutput(masterDest); } catch(_){}
      }
      return Promise.resolve();
    }
    if (this._readyPromise) return this._readyPromise;
    this.audioCtx = ctx;
    this.libStatus = "loading-lib";
    const self = this;
    this._readyPromise = (async function(){
      try {
        // IMPORTANT: literal string import for webpack (NOT a computed
        // expression — that broke the build before).
        const mod = await import("spessasynth_core");
        self._SpessaProcessor = mod.SpessaSynthProcessor;
        self._SoundBankLoader = mod.SoundBankLoader || mod.SoundFont2 || mod.loadSoundFont || null;
        const SR = ctx.sampleRate || 44100;
        // enableEffects:false — the track's own FX chain handles reverb etc.,
        // and SpessaSynth's internal effects add an attack swell we don't want.
        self.synth = new self._SpessaProcessor(SR, { enableEffects: false, enableEventSystem: false });
        if (self.synth.processorInitialized && typeof self.synth.processorInitialized.then === "function") {
          await self.synth.processorInitialized;
        }
        try { self.synth.setSystemParameter("voiceCap", 128); } catch(_){}

        // Build the ScriptProcessor that pumps synth.process() in 128-sample
        // chunks. 2048 buffer matches the FluidSynth engine's latency so the
        // two stay in sync when both are present.
        const BUF = 2048;
        const node = ctx.createScriptProcessor(BUF, 0, 2);
        const MAXQ = 128; // SpessaSynth maxBufferSize default
        node.onaudioprocess = function(e) {
          const outL = e.outputBuffer.getChannelData(0);
          const outR = e.outputBuffer.getChannelData(1);
          // Clear first (ScriptProcessor buffers can contain stale data).
          outL.fill(0); outR.fill(0);
          if (!self.synth) return;
          let filled = 0;
          try {
            while (filled < BUF) {
              const chunk = Math.min(MAXQ, BUF - filled);
              self.synth.process(outL, outR, filled, chunk);
              filled += chunk;
            }
          } catch(err) {
            // One bad quantum shouldn't kill the audio thread.
          }
        };
        self.audioNode = node;
        // Envelope gain node — ramped per note-on to produce an audible
        // attack/release. SpessaSynth's CC#73 alone didn't audibly shape
        // the envelope (confirmed via diagnostics: cc73=103 sent but no
        // perceptible change), so we apply the attack as real volume
        // automation here. Signal: ScriptProcessor → envGain → masterDest.
        const envGain = ctx.createGain();
        envGain.gain.value = 1;
        self._envGain = envGain;
        node.connect(envGain);
        if (masterDest) { envGain.connect(masterDest); self._currentMasterDest = masterDest; }
        else { envGain.connect(ctx.destination); self._currentMasterDest = ctx.destination; }
        self.libStatus = "ready";
        self._log("ready ✓ (SR=" + SR + ")");
      } catch(e) {
        self.libStatus = "error";
        self.libError = String(e && e.message || e);
        self._log("INIT FAILED: " + self.libError);
        throw e;
      }
    })();
    return this._readyPromise;
  }

  _allocChannel(presetId) {
    if (this._channelByPreset[presetId] != null) return this._channelByPreset[presetId];
    const ch = this._nextChannel++;
    this._channelByPreset[presetId] = ch;
    return ch;
  }

  // Load (or reuse) a soundfont and select its (bank, program) on a channel.
  // Mirrors BFSoundfontEngine.loadPreset's signature.
  async loadPreset(presetId, program, url, bank) {
    if (this.libStatus !== "ready") {
      try { await this.ensureReady(this.audioCtx, this._currentMasterDest); } catch(_){ return; }
    }
    if (this.presets[presetId] && this.presets[presetId].status === "ready") return;
    const ch = this._allocChannel(presetId);
    this.presets[presetId] = { status: "loading", channel: ch, bank: bank||0, program: program||0, url: url };
    try {
      // Resolve sf bytes — reuse the shared live cache when possible.
      let sfBytes = null;
      try { sfBytes = (typeof bfGetCachedSfBytes === "function") ? bfGetCachedSfBytes(url) : null; } catch(_){}
      if (!sfBytes) {
        const resp = await fetch(url, { mode: "cors", credentials: "omit" });
        if (!resp.ok) throw new Error("fetch " + resp.status);
        sfBytes = await resp.arrayBuffer();
      }
      let ab;
      if (sfBytes instanceof ArrayBuffer) ab = sfBytes.slice(0);
      else if (sfBytes && sfBytes.buffer) ab = sfBytes.buffer.slice(sfBytes.byteOffset||0, (sfBytes.byteOffset||0)+sfBytes.byteLength);
      else throw new Error("bad sf bytes");

      // Add the sound bank (reuse if same URL already added).
      let soundBank = this._banksByUrl[url];
      if (!soundBank) {
        soundBank = this._SoundBankLoader.fromArrayBuffer ? this._SoundBankLoader.fromArrayBuffer(ab) : new this._SoundBankLoader(ab);
        this._banksByUrl[url] = soundBank;
      }
      try { this.synth.soundBankManager.addSoundBank(soundBank, "main"); } catch(_){}

      // Direct preset assignment (proven approach from export) — pick the
      // exact (bank, program) and assign to the channel, handling bank 128.
      try {
        const sbPresets = (soundBank && soundBank.presets) || [];
        const wantBank = bank||0, wantProg = program||0;
        let match = sbPresets.find(function(p){
          const pb = (p.bankMSB != null) ? p.bankMSB : p.bank;
          return pb === wantBank && p.program === wantProg;
        });
        if (!match && wantBank === 128) {
          match = sbPresets.find(function(p){ return (p.isGMGSDrum || p.isDrum) && p.program === wantProg; });
        }
        if (match && this.synth.midiChannels && this.synth.midiChannels[ch]) {
          this.synth.midiChannels[ch].preset = match;
          this.presets[presetId].matchedPreset = match;
        } else {
          // Fallback: CC bank-select + program change.
          this.synth.controllerChange(ch, 0, wantBank & 0x7F);
          this.synth.controllerChange(ch, 32, 0);
          this.synth.programChange(ch, wantProg & 0x7F);
        }
        // Full volume/expression so the FX chain's volGain is the sole level.
        this.synth.controllerChange(ch, 7, 127);
        this.synth.controllerChange(ch, 11, 127);
      } catch(selErr) {
        this._log("preset select failed: " + (selErr&&selErr.message||selErr));
      }
      this.presets[presetId].status = "ready";
      this._log("loadPreset ready " + presetId + " ch=" + ch + " (" + (bank||0) + "," + (program||0) + ")");
    } catch(e) {
      this.presets[presetId].status = "error";
      this.presets[presetId].error = String(e && e.message || e);
      this._log("loadPreset FAILED " + presetId + ": " + this.presets[presetId].error);
    }
  }

  // Apply ADSR/filter override CCs to a preset's channel (reuses the shared
  // helper so live matches export).
  _applyOverrideCCs(ch, overrides) {
    let ccs = [];
    try { ccs = (typeof bfSoundfontOverrideCCs === "function") ? bfSoundfontOverrideCCs(overrides) : []; } catch(_){}
    // Reset relevant CCs to neutral first.
    const neutral = [73,75,72,74,71];
    for (let i = 0; i < neutral.length; i++) { try { this.synth.controllerChange(ch, neutral[i], 64); } catch(_){} }
    for (let i = 0; i < ccs.length; i++) { try { this.synth.controllerChange(ch, ccs[i].cc, ccs[i].value); } catch(_){} }
  }

  // Apply attack/release directly to the preset's VOLUME ENVELOPE
  // GENERATORS (SF2 gen 34 = Attack, gen 38 = Release). SpessaSynth
  // applies these PER VOICE during synthesis, so this produces a true
  // per-note attack/release with correct polyphony — unlike CC#73 (which
  // proved inaudible) or a shared output-gain ramp (which broke chords).
  //
  // Values are in timecents: tc = 1200 * log2(seconds). The volume
  // envelope generators live on the INSTRUMENT zones (the preset zones
  // point to instruments). We walk preset.zones[].instrument.zones[] and
  // set the generator on each. Re-applied whenever overrides change.
  _applyEnvelopeGenerators(presetObj, overrides) {
    if (!presetObj) return;
    const GEN_ATTACK = 34, GEN_RELEASE = 38;
    const secToTc = function(sec, lo, hi) {
      if (sec == null || sec <= 0) return null;
      let tc = Math.round(1200 * Math.log2(sec));
      if (tc < lo) tc = lo; if (tc > hi) tc = hi;
      return tc;
    };
    const amp = overrides && overrides.amp ? overrides.amp : null;
    // Attack: SF2 gen 34 range [-12000, 8000]. null => unset (default fast).
    const atkTc = amp && amp.attack != null ? secToTc(amp.attack, -12000, 8000) : null;
    // Release: SF2 gen 38 range [-7200, 8000].
    const relTc = amp && amp.release != null ? secToTc(amp.release, -7200, 8000) : null;
    // Decay: SF2 gen 36 range [-12000, 8000].
    const decTc = amp && amp.decay != null ? secToTc(amp.decay, -12000, 8000) : null;
    // Sustain: SF2 gen 37 is attenuation in CENTIBELS (0..1440), where 0 =
    // full level and higher = quieter. Map sustain 1.0 => 0 cB (full),
    // sustain 0 => ~960 cB (very quiet). This is what lets a long attack
    // ramp UP to a held level instead of being pulled down (the "goes
    // quiet" bug). When the user hasn't set sustain but HAS set an attack,
    // default sustain to full so the fade-in holds.
    let susCb = null;
    if (amp && amp.sustain != null) {
      const s = Math.max(0, Math.min(1, amp.sustain));
      // 0..1 -> 960..0 cB (full at 1.0). Linear in cB is close enough.
      susCb = Math.round((1 - s) * 960);
    } else if (atkTc != null) {
      susCb = 0; // full sustain so the attack fade-in holds
    }
    const GEN_DECAY = 36, GEN_SUSTAIN = 37;
    let zonesTouched = 0;
    const applyToZone = function(zone) {
      if (!zone || typeof zone.setGenerator !== "function") return;
      // Only touch zones that actually reference a sample — the "global"
      // zone (no sample) must not get an attack generator, and setting one
      // there can break synthesis (the silence we saw).
      if (!zone.sample) return;
      try {
        zone.setGenerator(GEN_ATTACK, atkTc, true);
        if (relTc != null) zone.setGenerator(GEN_RELEASE, relTc, true);
        if (decTc != null) zone.setGenerator(GEN_DECAY, decTc, true);
        if (susCb != null) zone.setGenerator(GEN_SUSTAIN, susCb, true);
        zonesTouched++;
      } catch(_){}
    };
    try {
      const pzones = presetObj.zones || [];
      for (let i = 0; i < pzones.length; i++) {
        const inst = pzones[i] && pzones[i].instrument;
        if (inst && inst.zones) {
          for (let j = 0; j < inst.zones.length; j++) applyToZone(inst.zones[j]);
        }
      }
      // Re-assign the preset to its channel so the binding stays valid
      // after mutating generators. (No clearCache — it was dropping voice
      // data and silencing playback.)
      try {
        if (this.synth && this.synth.midiChannels) {
          for (let ci = 0; ci < this.synth.midiChannels.length; ci++) {
            if (this.synth.midiChannels[ci] && this.synth.midiChannels[ci].preset === presetObj) {
              this.synth.midiChannels[ci].preset = presetObj;
            }
          }
        }
      } catch(_){}
    } catch(_){}
    try { if (typeof bfDebugLogVerbose === "function") bfDebugLogVerbose("[SPESSA-LIVE] envGen atkTc=" + atkTc + " relTc=" + relTc + " zones=" + zonesTouched); } catch(_){}
  }

  // Schedule a note with per-note ADSR. when is in ctx time (seconds).
  scheduleNote(presetId, pitch, velocity, when, duration, noteCutMode, overrides) {
    const p = this.presets[presetId];
    if (!p || p.status !== "ready" || !this.synth) {
      try { if (typeof bfDebugLogVerbose === "function") bfDebugLogVerbose("[SPESSA-LIVE] scheduleNote SKIP id=" + presetId + " status=" + (p?p.status:"missing")); } catch(_){}
      return { stop: function(){} };
    }
    const ch = p.channel;
    const vel = Math.max(1, Math.min(127, Math.round((velocity == null ? 0.85 : velocity) * 127)));
    const pitchInt = Math.round(pitch);
    const now = this.audioCtx ? this.audioCtx.currentTime : 0;
    const delayMs = Math.max(0, (when - now) * 1000);
    const durSec = Math.max(0.05, duration || 0.3);
    const self = this;
    // Apply volume-envelope generators (attack/release) to the matched
    // preset when they change. SpessaSynth applies them per-voice, giving
    // true per-note attack with correct polyphony. We only re-apply when
    // the attack/release values actually change (tracked via a signature),
    // since setGenerator + clearCache is heavier than a note-on.
    try {
      const amp = overrides && overrides.amp ? overrides.amp : null;
      const sig = amp ? ((amp.attack == null ? "_" : amp.attack) + "/" + (amp.release == null ? "_" : amp.release) + "/" + (amp.decay == null ? "_" : amp.decay) + "/" + (amp.sustain == null ? "_" : amp.sustain)) : "none";
      if (p.matchedPreset && p._envSig !== sig) {
        this._applyEnvelopeGenerators(p.matchedPreset, overrides);
        // Apply filter CCs (cutoff/resonance) once here too — these DO
        // work in SpessaSynth and don't need per-note reapplication.
        try {
          let ccs = (typeof bfSoundfontOverrideCCs === "function") ? bfSoundfontOverrideCCs(overrides) : [];
          for (let i = 0; i < ccs.length; i++) {
            if (ccs[i].cc === 74 || ccs[i].cc === 71) {
              try { this.synth.controllerChange(ch, ccs[i].cc, ccs[i].value); } catch(_){}
            }
          }
        } catch(_){}
        p._envSig = sig;
      }
    } catch(_){}
    let ccDesc = "";
    try { if (typeof bfDebugLogVerbose === "function") bfDebugLogVerbose("[SPESSA-LIVE] noteOn id=" + presetId + " ch=" + ch + " pitch=" + pitchInt + " (env via generators)"); } catch(_){}
    const onTimer = setTimeout(function(){
      // Envelope (attack/decay/sustain/release) is applied via per-voice
      // GENERATORS on the preset (see _applyEnvelopeGenerators), NOT via
      // CCs. We deliberately do NOT reset/apply CC#73/75/72 here — doing
      // that on every note-on disturbed the previous still-ramping voice
      // and caused alternating loud/quiet notes. Filter CCs (cutoff/res)
      // are applied once per envelope-change instead (below), not per note.
      try { self.synth.noteOn(ch, pitchInt, vel); } catch(_){}
    }, delayMs);
    const offTimer = setTimeout(function(){
      try { self.synth.noteOff(ch, pitchInt); } catch(_){}
    }, delayMs + durSec * 1000);
    return {
      stop: function(){
        clearTimeout(onTimer); clearTimeout(offTimer);
        try { self.synth.noteOff(ch, pitchInt); } catch(_){}
      },
    };
  }

  getPresetStatus(presetId) {
    const p = this.presets[presetId];
    if (!p) return "idle";
    return p.status;
  }

  reconnect(dest) {
    if (this.libStatus !== "ready") return;
    const tail = this._envGain || this.audioNode;
    if (!tail) return;
    try { tail.disconnect(); } catch(_){}
    try {
      tail.connect(dest || this._currentMasterDest || this.audioCtx.destination);
      this._currentMasterDest = dest || this._currentMasterDest;
    } catch(_){}
  }

  rewireOutput(newMaster) {
    if (this.libStatus !== "ready" || !newMaster) return false;
    const tail = this._envGain || this.audioNode;
    if (!tail) return false;
    try {
      tail.disconnect();
      tail.connect(newMaster);
      this._currentMasterDest = newMaster;
      return true;
    } catch(e) { return false; }
  }

  // Subscribe to engine state changes (preset load complete, etc.). Mirrors
  // the FluidSynth engine's shape so the shared UI/resume/loading-toast
  // subscription code reaches this engine too. Returns an unsubscribe fn.
  subscribe(fn) {
    if (typeof fn !== "function") return function(){};
    if (!this._listeners) this._listeners = [];
    this._listeners.push(fn);
    const self = this;
    return function(){
      try { self._listeners = (self._listeners || []).filter(function(x){ return x !== fn; }); } catch(_){}
    };
  }
  _notify() {
    const ls = this._listeners || [];
    for (let i = 0; i < ls.length; i++) { try { ls[i](); } catch(_){} }
  }

  // Stage 4 teardown — called when a track using this engine is deleted, so
  // we don't leak the main-thread ScriptProcessor node (which keeps the
  // audio thread busy) or the synth. Safe to call multiple times.
  dispose() {
    try { if (this.audioNode) { this.audioNode.onaudioprocess = null; this.audioNode.disconnect(); } } catch(_){}
    try { if (this._envGain) this._envGain.disconnect(); } catch(_){}
    this.audioNode = null;
    this._envGain = null;
    try { this.synth = null; } catch(_){}
    this.presets = {};
    this.libStatus = "disposed";
    this._listeners = [];
    try { if (typeof bfDebugLog === "function") bfDebugLog("[SPESSA-LIVE] disposed engine"); } catch(_){}
  }
}

// Helper to retrieve already-fetched soundfont bytes from the LIVE
// engine's cache. Used by the offline export renderer so it doesn't
// need to re-fetch the .sf2 from R2 — the bytes are already in memory
// from the live playback that just happened. Returns an ArrayBuffer
// or null if the URL hasn't been loaded yet.
function bfGetCachedSfBytes(url) {
  // Check every engine — singleton plus every per-track instance.
  // Some tracks might use a URL that only the singleton or another
  // per-track engine has cached.
  const tryEngine = function(eng) {
    if (!eng || !eng._sfontByUrl) return null;
    const entry = eng._sfontByUrl[url];
    if (entry && entry.status === "ready" && entry.buf) return entry.buf;
    return null;
  };
  let buf = tryEngine(_bfSfEngineInstance);
  if (buf) return buf;
  let found = null;
  if (typeof _bfSfEnginesByTrack !== "undefined") {
    _bfSfEnginesByTrack.forEach(function(eng){
      if (found) return;
      const b = tryEngine(eng);
      if (b) found = b;
    });
  }
  return found;
}

// Get-or-create the engine singleton. Used by code paths that don't
// have a trackId to key against (browser previews, admin tools, etc).
function getBFSoundfontEngine() {
  if (!_bfSfEngineInstance) _bfSfEngineInstance = new BFSoundfontEngine();
  return _bfSfEngineInstance;
}

// Pre-render a single MIDI soundfont track to an AudioBuffer via
// REAL-TIME playback through a fresh FluidSynth instance bound to a
// scratch AudioContext. The resulting buffer can then be played as a
// regular BufferSource inside the export's OfflineAudioContext — which
// is the ONLY reliable way to get soundfont audio into an offline
// export, because JSSynth's MIDI scheduling needs real-time event
// firing that offline contexts don't provide.
//
// Real-time means the export takes longer (per-track render time =
// track duration). But it's faithful: the audio that ends up in the
// export is identical to what plays live.
//
// Returns an AudioBuffer or null on failure.
async function preRenderSoundfontTrackToBuffer(track, fromTime, toTime, projectBpm) {
  if (!track || track.type !== "midi") return null;
  const baseInst = resolveTrackInstrumentMeta(track);
  const inst = applyInstrumentOverrides(baseInst, track.instrumentOverrides);
  if (!inst || inst.engine !== "soundfont-v1") return null;
  if (typeof JSSynth === "undefined") {
    console.warn("[export] JSSynth missing — soundfont track skipped");
    return null;
  }
  // Collect notes in [fromTime, toTime]
  const noteEvents = [];
  const clips = track.clips || [];
  for (const clip of clips) {
    if (clip.active === false) continue;
    const notes = midiClipNotesInWindow(clip, fromTime, toTime);
    for (const n of notes) {
      const t0 = n.projectTime - fromTime;
      if (t0 < 0) continue;
      noteEvents.push({ when: t0, op: "on", pitch: n.pitch, vel: n.velocity || 100 });
      const t1 = t0 + (n.duration || 0.1);
      noteEvents.push({ when: t1, op: "off", pitch: n.pitch, vel: 0 });
    }
  }
  if (noteEvents.length === 0) return null;
  noteEvents.sort(function(a, b){ return a.when - b.when; });

  // Set up a fresh real-time context just for this render. We use a
  // separate AudioContext so this rendering doesn't disturb whatever
  // the user is doing in the main app's context.
  const sampleRate = 48000;
  // Add a small tail at the end for note releases / reverb.
  const renderDur = (toTime - fromTime) + 2.0;
  const ctx = new (window.OfflineAudioContext || window.webkitOfflineAudioContext)(
    2, Math.ceil(renderDur * sampleRate), sampleRate
  );

  // Build a fresh synth bound to this offline ctx. We'll fire ALL
  // events synchronously into the synth BEFORE startRendering() so
  // FluidSynth's voice buffer is primed with the entire sequence's
  // active voices. The ScriptProcessor (or AudioWorklet) attached to
  // the offline ctx then produces audio that the offline render
  // consumes — but this only works if JSSynth's render path inside
  // its audio node respects the offline ctx's clock and renders
  // notes that are "active" right now.
  await JSSynth.waitForReady();
  const synth = new JSSynth.Synthesizer();
  synth.init(sampleRate);
  const url = inst.customUrl;
  if (!url) return null;
  let bytes = bfGetCachedSfBytes(url);
  if (!bytes) {
    try {
      const resp = await fetch(url, { mode: "cors", credentials: "omit" });
      if (!resp.ok) return null;
      bytes = await resp.arrayBuffer();
    } catch(_) { return null; }
  }
  let sfontId;
  try {
    sfontId = await synth.loadSFont(bytes);
  } catch(e) {
    console.warn("[export] loadSFont failed:", e);
    return null;
  }
  const channel = 0;
  const bank    = inst.gmBankNumber || 0;
  const program = inst.gmProgramNumber || 0;
  try {
    synth.midiProgramSelect(channel, sfontId, bank, program);
  } catch(e) {
    console.warn("[export] midiProgramSelect failed:", e);
    return null;
  }
  const audioNode = synth.createAudioNode(ctx, 2048);
  audioNode.connect(ctx.destination);

  // Schedule events. JSSynth doesn't natively support timed
  // midiNoteOn, but since OfflineAudioContext runs synchronously, we
  // can interleave event firing with manual ticks by using a wrapper
  // that calls synth.midiNoteOn/Off at the right offline-time. The
  // approach: kick off rendering in chunks, between chunks fire any
  // events whose `when` has elapsed.
  //
  // Simpler approach with the same effect: render in many small slices
  // using ScriptProcessor's natural chunking. Each createAudioNode
  // buffer is 2048 samples = ~42ms. Before each chunk the JS thread
  // gets control, so we fire pending events.
  //
  // OfflineAudioContext exposes a suspend/resume API — we use that to
  // pause rendering, fire events, then resume.
  let ev = 0;
  const tickRender = async function() {
    while (ev < noteEvents.length) {
      const e = noteEvents[ev];
      if (e.when > ctx.currentTime + 1e-6) {
        // Suspend at e.when, then process events at that exact time
        try { await ctx.suspend(e.when); } catch(_){ break; }
        // At this point currentTime == e.when. Fire all events at this time.
        while (ev < noteEvents.length && noteEvents[ev].when <= ctx.currentTime + 1e-6) {
          const cur = noteEvents[ev];
          try {
            if (cur.op === "on") synth.midiNoteOn(channel, cur.pitch, cur.vel);
            else                  synth.midiNoteOff(channel, cur.pitch);
          } catch(_){}
          ev++;
        }
        try { await ctx.resume(); } catch(_){}
      } else {
        // Event is at or before current time — fire now (immediate)
        try {
          const cur = noteEvents[ev];
          if (cur.op === "on") synth.midiNoteOn(channel, cur.pitch, cur.vel);
          else                  synth.midiNoteOff(channel, cur.pitch);
        } catch(_){}
        ev++;
      }
    }
  };
  // Fire events at t=0 immediately
  while (ev < noteEvents.length && noteEvents[ev].when <= 1e-6) {
    const cur = noteEvents[ev];
    try {
      if (cur.op === "on") synth.midiNoteOn(channel, cur.pitch, cur.vel);
      else                  synth.midiNoteOff(channel, cur.pitch);
    } catch(_){}
    ev++;
  }
  // Kick off the rendering and the event tick in parallel.
  const renderingP = ctx.startRendering();
  // tickRender uses ctx.suspend() to pause at each event time.
  await tickRender();
  let buffer;
  try {
    buffer = await renderingP;
  } catch(e) {
    console.warn("[export] offline render failed:", e);
    return null;
  }
  // Clean up the synth — it's already disconnected, but explicitly free
  try { audioNode.disconnect(); } catch(_){}
  return buffer;
}


// ── Per-track soundfont engine manager ──
// Each MIDI track gets its OWN FluidSynth instance so its audio output
// can route through that track's FX chain (Reverb, EQ, Compressor etc).
// Previously all soundfont tracks shared one FluidSynth audio node
// connected directly to master — meaning FX plugins on MIDI tracks
// were silently bypassed. With per-track instances, every track is
// independently routable.
//
// Memory trade-off: each instance loads its own .sf2 bytes (we don't
// share buffers between instances because each FluidSynth has its own
// internal sfont registry). Mitigations:
//   - Lazy init: instances created only on first scheduleNote/preview
//   - LRU eviction: when more than ENGINE_INSTANCE_CAP instances exist,
//     the least-recently-used gets torn down and rebuilt on demand
//   - The original singleton (above) is kept as a fallback for any
//     code path not yet updated to be track-aware
const _bfSfEnginesByTrack = new Map();   // trackId -> BFSoundfontEngine
const _bfSfEngineLastUsed = new Map();   // trackId -> Date.now() epoch
const ENGINE_INSTANCE_CAP = 8;

function _bfEvictOldestEngine() {
  // Find the least-recently-used trackId in the map and tear down.
  let oldestId = null;
  let oldestTs = Infinity;
  _bfSfEngineLastUsed.forEach(function(ts, tid) {
    if (ts < oldestTs) { oldestTs = ts; oldestId = tid; }
  });
  if (oldestId == null) return;
  const eng = _bfSfEnginesByTrack.get(oldestId);
  if (eng) {
    try {
      if (eng.audioNode) {
        try { eng.audioNode.disconnect(); } catch(_){}
      }
      // Don't bother explicitly destroying the synth — letting it GC is
      // fine and avoids any FluidSynth lifecycle bugs.
    } catch(_){}
  }
  _bfSfEnginesByTrack.delete(oldestId);
  _bfSfEngineLastUsed.delete(oldestId);
}

// Get-or-create a soundfont engine for a specific track. The trackId
// is used as the keying value — pass the track's id, NOT the preset id.
// One track = one engine, regardless of how many presets that track
// uses over time (the engine handles preset switching internally).
function getBFSoundfontEngineForTrack(trackId) {
  if (trackId == null) {
    // No track context — fall back to the singleton (used for browser
    // previews, admin tools, anywhere not bound to a specific track).
    return getBFSoundfontEngine();
  }
  let eng = _bfSfEnginesByTrack.get(trackId);
  if (!eng) {
    // Evict the LRU if we're at the cap. The producer would rather
    // have a brief reload on a stale track than a memory-pressure
    // tab kill from iOS.
    if (_bfSfEnginesByTrack.size >= ENGINE_INSTANCE_CAP) {
      _bfEvictOldestEngine();
    }
    eng = new BFSoundfontEngine();
    _bfSfEnginesByTrack.set(trackId, eng);
  }
  _bfSfEngineLastUsed.set(trackId, Date.now());
  return eng;
}

// Walk every active engine — used by the loading toast and headphone
// rewire logic, which need to operate on all instances at once.
function forEachBFSoundfontEngine(fn) {
  // Singleton (for non-track-keyed code paths).
  if (_bfSfEngineInstance) fn(_bfSfEngineInstance);
  _bfSfEnginesByTrack.forEach(fn);
  // SpessaSynth-live engines too (so rewire/headphone logic reaches them).
  if (typeof _bfSpessaLiveByTrack !== "undefined") _bfSpessaLiveByTrack.forEach(fn);
}

// ── SpessaSynth-live per-track engine registry ──
// Parallel to the FluidSynth registry. A soundfont track WITH sound-shaping
// overrides plays through one of these (per-note ADSR); a track without
// overrides uses FluidSynth. Both are keyed by trackId.
const _bfSpessaLiveByTrack = new Map();
const _bfSpessaLiveLastUsed = new Map();

function _bfEvictOldestSpessaLive() {
  let oldestId = null, oldestTs = Infinity;
  _bfSpessaLiveLastUsed.forEach(function(ts, tid){ if (ts < oldestTs){ oldestTs = ts; oldestId = tid; } });
  if (oldestId == null) return;
  const eng = _bfSpessaLiveByTrack.get(oldestId);
  if (eng && typeof eng.dispose === "function") { try { eng.dispose(); } catch(_){} }
  else if (eng && eng.audioNode) { try { eng.audioNode.disconnect(); } catch(_){} }
  _bfSpessaLiveByTrack.delete(oldestId);
  _bfSpessaLiveLastUsed.delete(oldestId);
}

function getBFSpessaLiveEngineForTrack(trackId) {
  if (trackId == null) return null; // SpessaSynth-live is track-scoped only
  let eng = _bfSpessaLiveByTrack.get(trackId);
  if (!eng) {
    if (_bfSpessaLiveByTrack.size >= ENGINE_INSTANCE_CAP) _bfEvictOldestSpessaLive();
    eng = new BFSpessaLiveEngine();
    _bfSpessaLiveByTrack.set(trackId, eng);
  }
  _bfSpessaLiveLastUsed.set(trackId, Date.now());
  return eng;
}

// Aggregate active-load count across ALL engines (singleton + per-track).
// Used by the loading toast subscriber so it shows whenever ANY engine
// is busy, regardless of which track triggered the load.
function bfSfTotalActiveLoads() {
  let total = 0;
  let nameSample = null;
  if (_bfSfEngineInstance && _bfSfEngineInstance._activeLoads > 0) {
    total += _bfSfEngineInstance._activeLoads;
    if (!nameSample) nameSample = _bfSfEngineInstance._loadingName;
  }
  _bfSfEnginesByTrack.forEach(function(eng){
    if (eng._activeLoads > 0) {
      total += eng._activeLoads;
      if (!nameSample) nameSample = eng._loadingName;
    }
  });
  return { count: total, name: nameSample };
}


const BF_INSTRUMENTS = {
  // ── KEYS ────────────────────────────────────────────────────────────────
  "piano-grand": {
    id: "piano-grand", name: "Grand Piano", category: "Keys",
    color: "#A78BFA",
    description: "Soft polished keys — universal starter",
    engine: "synth-v1",
    osc: [
      { type: "triangle", gain: 0.65, detune: 0 },
      { type: "sine",     gain: 0.55, detune: -7 },
      { type: "sine",     gain: 0.18, detune: 1200 },
    ],
    filter: { type: "lowpass", baseCutoff: 1800, track: 35, q: 0.6, envAmount: 1800, envAttack: 0.005, envDecay: 0.25 },
    amp: _ADSR.key,
    gain: 0.55,
  },
  "rhodes-warm": {
    id: "rhodes-warm", name: "Warm Rhodes", category: "Keys",
    color: "#C4B5FD",
    description: "Vintage e-piano · soft tine",
    engine: "synth-v1",
    osc: [
      { type: "sine",     gain: 0.80, detune: 0 },
      { type: "triangle", gain: 0.30, detune: 1200 },     // octave-up shimmer (tine)
      { type: "sine",     gain: 0.20, detune: 1907 },     // bell partial (octave + minor 7th)
    ],
    filter: { type: "lowpass", baseCutoff: 2200, track: 25, q: 0.4, envAmount: 1400, envAttack: 0.005, envDecay: 0.45 },
    amp: { attack: 0.008, decay: 0.60, sustain: 0.40, release: 0.60 },
    gain: 0.55,
  },
  "epiano-dirty": {
    id: "epiano-dirty", name: "Tape EP", category: "Keys",
    color: "#A78BFA",
    description: "Wide e-piano · cassette tone",
    engine: "synth-v1",
    osc: [
      { type: "triangle", gain: 0.65, detune: 0 },
      { type: "sawtooth", gain: 0.18, detune: 7 },        // gentle detune for tape wobble
      { type: "sine",     gain: 0.42, detune: 1200 },
    ],
    filter: { type: "lowpass", baseCutoff: 1500, track: 30, q: 0.8, envAmount: 1200, envAttack: 0.008, envDecay: 0.30 },
    amp: { attack: 0.010, decay: 0.40, sustain: 0.55, release: 0.45 },
    gain: 0.52,
  },
  // ── INSTRUMENTS (Phase 6a/6b) ────────────────────────────────────────────
  // Real recorded instruments via the soundfont engine. Each entry's
  // gmProgramNumber tells the engine which .sf3 file to fetch (0-127 maps
  // to the General MIDI patch number). All EditSoundPanel ADSR/filter
  // settings are ignored for these — the recorded sample's own envelope
  // is used. Only Volume Trim has an effect.
  //
  // Curated from the 128 GM patches: only the ones that sound genuinely
  // musical in the GeneralUser GS soundfont are included. Some GM patches
  // (e.g. orchestra hit, bird tweet, gunshot) are excluded as they're
  // gimmicks rather than musical instruments.
  "sf-grand-piano": {
    id: "sf-grand-piano", name: "Grand Piano (real)", category: "Instruments",
    color: "#F8F7FA",
    description: "Recorded acoustic grand · the universal starter",
    engine: "soundfont-v1", gmProgramNumber: 0, gain: 1.0,
  },
  "sf-bright-piano": {
    id: "sf-bright-piano", name: "Bright Piano", category: "Instruments",
    color: "#F8F7FA",
    description: "Tighter, brighter acoustic piano — pop/ballads",
    engine: "soundfont-v1", gmProgramNumber: 1, gain: 1.0,
  },
  "sf-elec-piano": {
    id: "sf-elec-piano", name: "Electric Piano", category: "Instruments",
    color: "#EDE4F7",
    description: "Classic EP · soulful, vocal-friendly",
    engine: "soundfont-v1", gmProgramNumber: 4, gain: 1.0,
  },
  "sf-honky-tonk": {
    id: "sf-honky-tonk", name: "Honky-Tonk", category: "Instruments",
    color: "#EDE4F7",
    description: "Slightly detuned upright · saloon vibes",
    engine: "soundfont-v1", gmProgramNumber: 3, gain: 1.0,
  },
  "sf-harpsichord": {
    id: "sf-harpsichord", name: "Harpsichord", category: "Instruments",
    color: "#E8DDF2",
    description: "Plucked baroque keyboard · bright + crisp",
    engine: "soundfont-v1", gmProgramNumber: 6, gain: 1.0,
  },
  "sf-music-box": {
    id: "sf-music-box", name: "Music Box", category: "Instruments",
    color: "#E2D5EF",
    description: "Tinkling lullaby · short bell-like tones",
    engine: "soundfont-v1", gmProgramNumber: 10, gain: 1.0,
  },
  "sf-vibraphone": {
    id: "sf-vibraphone", name: "Vibraphone", category: "Instruments",
    color: "#DDD0EC",
    description: "Mallet on metal · jazzy and dreamy",
    engine: "soundfont-v1", gmProgramNumber: 11, gain: 1.0,
  },
  "sf-marimba": {
    id: "sf-marimba", name: "Marimba", category: "Instruments",
    color: "#D8CBE9",
    description: "Wooden mallet · warm and woody",
    engine: "soundfont-v1", gmProgramNumber: 12, gain: 1.0,
  },
  "sf-celesta": {
    id: "sf-celesta", name: "Celesta", category: "Instruments",
    color: "#D3C6E6",
    description: "Bell-like keyboard · ethereal sparkle",
    engine: "soundfont-v1", gmProgramNumber: 8, gain: 1.0,
  },
  "sf-nylon-guitar": {
    id: "sf-nylon-guitar", name: "Nylon Guitar", category: "Instruments",
    color: "#F0E1D0",
    description: "Classical / fingerstyle guitar",
    engine: "soundfont-v1", gmProgramNumber: 24, gain: 1.0,
  },
  "sf-steel-guitar": {
    id: "sf-steel-guitar", name: "Steel Guitar", category: "Instruments",
    color: "#EAD7C0",
    description: "Acoustic steel-string · folk + indie",
    engine: "soundfont-v1", gmProgramNumber: 25, gain: 1.0,
  },
  "sf-jazz-guitar": {
    id: "sf-jazz-guitar", name: "Jazz Guitar", category: "Instruments",
    color: "#E4CDB0",
    description: "Hollow-body electric · smooth jazz tone",
    engine: "soundfont-v1", gmProgramNumber: 26, gain: 1.0,
  },
  "sf-clean-guitar": {
    id: "sf-clean-guitar", name: "Clean Electric", category: "Instruments",
    color: "#DEC3A0",
    description: "Bridge-pickup clean tone · funky chords",
    engine: "soundfont-v1", gmProgramNumber: 27, gain: 1.0,
  },
  "sf-acoustic-bass": {
    id: "sf-acoustic-bass", name: "Acoustic Bass", category: "Instruments",
    color: "#C8B8E0",
    description: "Upright double bass · jazz + folk",
    engine: "soundfont-v1", gmProgramNumber: 32, gain: 1.0,
  },
  "sf-finger-bass": {
    id: "sf-finger-bass", name: "Finger Bass", category: "Instruments",
    color: "#C2B0DC",
    description: "Electric bass · fingerstyle attack",
    engine: "soundfont-v1", gmProgramNumber: 33, gain: 1.0,
  },
  "sf-fretless-bass": {
    id: "sf-fretless-bass", name: "Fretless Bass", category: "Instruments",
    color: "#BCA8D8",
    description: "Smooth slides · Jaco-style fretless",
    engine: "soundfont-v1", gmProgramNumber: 35, gain: 1.0,
  },
  "sf-violin": {
    id: "sf-violin", name: "Violin", category: "Instruments",
    color: "#E5C3C3",
    description: "Solo bowed violin · expressive lead",
    engine: "soundfont-v1", gmProgramNumber: 40, gain: 1.0,
  },
  "sf-cello": {
    id: "sf-cello", name: "Cello", category: "Instruments",
    color: "#DCB5B5",
    description: "Solo cello · low, soulful, lyrical",
    engine: "soundfont-v1", gmProgramNumber: 42, gain: 1.0,
  },
  "sf-strings": {
    id: "sf-strings", name: "String Ensemble", category: "Instruments",
    color: "#D3A7A7",
    description: "Lush orchestral strings · cinematic bed",
    engine: "soundfont-v1", gmProgramNumber: 48, gain: 1.0,
  },
  "sf-pizzicato": {
    id: "sf-pizzicato", name: "Pizzicato Strings", category: "Instruments",
    color: "#CA9999",
    description: "Plucked strings · short, percussive",
    engine: "soundfont-v1", gmProgramNumber: 45, gain: 1.0,
  },
  "sf-harp": {
    id: "sf-harp", name: "Orchestral Harp", category: "Instruments",
    color: "#EAD0B5",
    description: "Concert harp · arpeggios + glissandi",
    engine: "soundfont-v1", gmProgramNumber: 46, gain: 1.0,
  },
  "sf-choir-aahs": {
    id: "sf-choir-aahs", name: "Choir Aahs", category: "Instruments",
    color: "#E0D0B0",
    description: "Vocal pad · ethereal layered choir",
    engine: "soundfont-v1", gmProgramNumber: 52, gain: 1.0,
  },
  "sf-trumpet": {
    id: "sf-trumpet", name: "Trumpet", category: "Instruments",
    color: "#D9A878",
    description: "Solo trumpet · bright + bold",
    engine: "soundfont-v1", gmProgramNumber: 56, gain: 1.0,
  },
  "sf-french-horn": {
    id: "sf-french-horn", name: "French Horn", category: "Instruments",
    color: "#D09870",
    description: "Mellow brass · noble + warm",
    engine: "soundfont-v1", gmProgramNumber: 60, gain: 1.0,
  },
  "sf-brass-section": {
    id: "sf-brass-section", name: "Brass Section", category: "Instruments",
    color: "#C78866",
    description: "Punchy horn stab · funk + film",
    engine: "soundfont-v1", gmProgramNumber: 61, gain: 1.0,
  },
  "sf-alto-sax": {
    id: "sf-alto-sax", name: "Alto Sax", category: "Instruments",
    color: "#C9B888",
    description: "Smooth jazz sax · expressive solo",
    engine: "soundfont-v1", gmProgramNumber: 65, gain: 1.0,
  },
  "sf-flute": {
    id: "sf-flute", name: "Flute", category: "Instruments",
    color: "#B8C8B0",
    description: "Solo concert flute · airy + light",
    engine: "soundfont-v1", gmProgramNumber: 73, gain: 1.0,
  },
  "sf-pan-flute": {
    id: "sf-pan-flute", name: "Pan Flute", category: "Instruments",
    color: "#A8C0A0",
    description: "Breathy pan pipes · folk + ambient",
    engine: "soundfont-v1", gmProgramNumber: 75, gain: 1.0,
  },
  "sf-oboe": {
    id: "sf-oboe", name: "Oboe", category: "Instruments",
    color: "#B0C0A8",
    description: "Reedy double-reed · plaintive + clear",
    engine: "soundfont-v1", gmProgramNumber: 68, gain: 1.0,
  },
  "sf-sitar": {
    id: "sf-sitar", name: "Sitar", category: "Instruments",
    color: "#D0B888",
    description: "Indian classical · drone + buzz",
    engine: "soundfont-v1", gmProgramNumber: 104, gain: 1.0,
  },
  "sf-kalimba": {
    id: "sf-kalimba", name: "Kalimba", category: "Instruments",
    color: "#C8A878",
    description: "African thumb piano · plinky + warm",
    engine: "soundfont-v1", gmProgramNumber: 108, gain: 1.0,
  },
  "sf-steel-drums": {
    id: "sf-steel-drums", name: "Steel Drums", category: "Instruments",
    color: "#B8A070",
    description: "Caribbean steel pan · bouncy + bright",
    engine: "soundfont-v1", gmProgramNumber: 114, gain: 1.0,
  },

  // ── LEAD ────────────────────────────────────────────────────────────────
  "lead-saw": {
    id: "lead-saw", name: "Saw Lead", category: "Lead",
    color: "#C4B5FD",
    description: "Bright sawtooth · cuts through",
    engine: "synth-v1",
    osc: [
      { type: "sawtooth", gain: 0.75, detune: -6 },
      { type: "sawtooth", gain: 0.75, detune: 6 },        // detuned unison
      { type: "square",   gain: 0.20, detune: 0 },
    ],
    filter: { type: "lowpass", baseCutoff: 2600, track: 45, q: 1.4, envAmount: 2400, envAttack: 0.005, envDecay: 0.18 },
    amp: _ADSR.lead,
    gain: 0.42,
  },
  "lead-trap": {
    id: "lead-trap", name: "Trap Lead", category: "Lead",
    color: "#A78BFA",
    description: "Sharp · cutting · modern",
    engine: "synth-v1",
    osc: [
      { type: "sawtooth", gain: 0.60, detune: 0 },
      { type: "square",   gain: 0.55, detune: -14 },      // slight detune for thickness
      { type: "sawtooth", gain: 0.35, detune: 14 },
    ],
    filter: { type: "lowpass", baseCutoff: 1900, track: 60, q: 2.0, envAmount: 3200, envAttack: 0.003, envDecay: 0.12 },
    amp: { attack: 0.003, decay: 0.10, sustain: 0.78, release: 0.14 },
    gain: 0.40,
  },
  "lead-dark": {
    id: "lead-dark", name: "Dark Lead", category: "Lead",
    color: "#8B5CF6",
    description: "Brooding · UK drill staple",
    engine: "synth-v1",
    osc: [
      { type: "triangle", gain: 0.55, detune: 0 },
      { type: "sawtooth", gain: 0.42, detune: -12 },      // octave-down weight
      { type: "square",   gain: 0.25, detune: -14 },
    ],
    filter: { type: "lowpass", baseCutoff: 1200, track: 35, q: 1.6, envAmount: 1800, envAttack: 0.008, envDecay: 0.20 },
    amp: { attack: 0.010, decay: 0.20, sustain: 0.70, release: 0.30 },
    gain: 0.50,
  },
  "lead-sine-fm": {
    id: "lead-sine-fm", name: "Sine Lead", category: "Lead",
    color: "#7FB5AB",
    description: "Pure tone · radio-ready",
    engine: "synth-v1",
    osc: [
      { type: "sine",     gain: 0.95, detune: 0 },
      { type: "sine",     gain: 0.25, detune: 1200 },     // octave for shimmer
    ],
    filter: { type: "lowpass", baseCutoff: 4500, track: 20, q: 0.5, envAmount: 0, envAttack: 0.005, envDecay: 0.1 },
    amp: { attack: 0.020, decay: 0.10, sustain: 0.85, release: 0.30 },
    gain: 0.62,
  },

  // ── PLUCK ───────────────────────────────────────────────────────────────
  "pluck-soft": {
    id: "pluck-soft", name: "Soft Pluck", category: "Pluck",
    color: "#F0C5A9",
    description: "Round · woody · melodic",
    engine: "synth-v1",
    osc: [
      { type: "triangle", gain: 0.85, detune: 0 },
      { type: "sine",     gain: 0.30, detune: 1200 },
    ],
    filter: { type: "lowpass", baseCutoff: 1600, track: 50, q: 1.4, envAmount: 3200, envAttack: 0.002, envDecay: 0.20 },
    amp: { attack: 0.002, decay: 0.20, sustain: 0.10, release: 0.20 },
    gain: 0.58,
  },
  "pluck-trap": {
    id: "pluck-trap", name: "Trap Pluck", category: "Pluck",
    color: "#C77575",
    description: "Sharp transient · short tail",
    engine: "synth-v1",
    osc: [
      { type: "sawtooth", gain: 0.60, detune: 0 },
      { type: "square",   gain: 0.40, detune: 0 },
    ],
    filter: { type: "lowpass", baseCutoff: 1100, track: 65, q: 2.4, envAmount: 4500, envAttack: 0.001, envDecay: 0.12 },
    amp: _ADSR.pluck,
    gain: 0.48,
  },
  "pluck-koto": {
    id: "pluck-koto", name: "Eastern Pluck", category: "Pluck",
    color: "#D4B147",
    description: "Stringed · cinematic · RnB-friendly",
    engine: "synth-v1",
    osc: [
      { type: "triangle", gain: 0.70, detune: 0 },
      { type: "triangle", gain: 0.45, detune: 700 },      // perfect 5th
      { type: "sine",     gain: 0.25, detune: 1200 },
    ],
    filter: { type: "lowpass", baseCutoff: 1800, track: 40, q: 1.0, envAmount: 2400, envAttack: 0.002, envDecay: 0.30 },
    amp: { attack: 0.002, decay: 0.45, sustain: 0.15, release: 0.40 },
    gain: 0.50,
  },

  // ── BASS ────────────────────────────────────────────────────────────────
  "bass-808": {
    id: "bass-808", name: "808 Sub", category: "Bass",
    color: "#8B5CF6",
    description: "Deep · clean · subterranean",
    engine: "synth-v1",
    osc: [
      { type: "sine",     gain: 1.00, detune: 0 },
      { type: "sine",     gain: 0.15, detune: -1200 },    // octave-down (sub of sub)
    ],
    filter: { type: "lowpass", baseCutoff: 350, track: 8, q: 0.5, envAmount: 0, envAttack: 0.005, envDecay: 0.1 },
    amp: { attack: 0.005, decay: 0.50, sustain: 0.85, release: 0.20 },
    gain: 0.75,
  },
  "bass-reese": {
    id: "bass-reese", name: "Reese Bass", category: "Bass",
    color: "#7C3AED",
    description: "Thick · detuned · driving",
    engine: "synth-v1",
    osc: [
      { type: "sawtooth", gain: 0.55, detune: -7 },
      { type: "sawtooth", gain: 0.55, detune: 7 },
      { type: "square",   gain: 0.30, detune: -1200 },    // sub octave
    ],
    filter: { type: "lowpass", baseCutoff: 800, track: 25, q: 1.6, envAmount: 1400, envAttack: 0.010, envDecay: 0.25 },
    amp: _ADSR.bass,
    gain: 0.45,
  },
  "bass-pluck": {
    id: "bass-pluck", name: "Pluck Bass", category: "Bass",
    color: "#A78BFA",
    description: "Punchy · plucked low end",
    engine: "synth-v1",
    osc: [
      { type: "triangle", gain: 0.85, detune: 0 },
      { type: "square",   gain: 0.30, detune: 0 },
    ],
    filter: { type: "lowpass", baseCutoff: 700, track: 35, q: 1.8, envAmount: 2200, envAttack: 0.002, envDecay: 0.18 },
    amp: { attack: 0.003, decay: 0.20, sustain: 0.20, release: 0.18 },
    gain: 0.55,
  },

  // ── PAD ─────────────────────────────────────────────────────────────────
  "pad-warm": {
    id: "pad-warm", name: "Warm Pad", category: "Pad",
    color: "#7FB5AB",
    description: "Wide · slow · supportive",
    engine: "synth-v1",
    osc: [
      { type: "sawtooth", gain: 0.50, detune: -10 },
      { type: "sawtooth", gain: 0.50, detune: 10 },
      { type: "triangle", gain: 0.35, detune: 1200 },
    ],
    filter: { type: "lowpass", baseCutoff: 1400, track: 25, q: 0.7, envAmount: 600, envAttack: 0.40, envDecay: 1.2 },
    amp: _ADSR.pad,
    gain: 0.38,
  },
  "pad-dark": {
    id: "pad-dark", name: "Dark Pad", category: "Pad",
    color: "#5C5870",
    description: "Cinematic · moody · drill-ready",
    engine: "synth-v1",
    osc: [
      { type: "triangle", gain: 0.55, detune: 0 },
      { type: "sawtooth", gain: 0.42, detune: -12 },      // octave down
      { type: "sine",     gain: 0.30, detune: -19 },      // octave + 5th down for darkness
    ],
    filter: { type: "lowpass", baseCutoff: 900, track: 20, q: 1.0, envAmount: 400, envAttack: 0.5, envDecay: 1.5 },
    amp: _ADSR.pad,
    gain: 0.40,
  },
  "pad-choir": {
    id: "pad-choir", name: "Choir Pad", category: "Pad",
    color: "#F0EFF5",
    description: "Voice-like · emotional · RnB",
    engine: "synth-v1",
    osc: [
      { type: "triangle", gain: 0.55, detune: -5 },
      { type: "triangle", gain: 0.55, detune: 5 },
      { type: "sawtooth", gain: 0.20, detune: 1200 },     // formant-ish brightness
    ],
    filter: { type: "lowpass", baseCutoff: 1200, track: 20, q: 0.8, envAmount: 800, envAttack: 0.30, envDecay: 0.80 },
    amp: _ADSR.swell,
    gain: 0.40,
  },
  "pad-ambient": {
    id: "pad-ambient", name: "Ambient Pad", category: "Pad",
    color: "#9C8AC4",
    description: "Lush · evolving · texture",
    engine: "synth-v1",
    osc: [
      { type: "sawtooth", gain: 0.40, detune: -14 },
      { type: "sawtooth", gain: 0.40, detune: 14 },
      { type: "sine",     gain: 0.35, detune: 1200 },
      { type: "triangle", gain: 0.25, detune: 1907 },     // shimmer
    ],
    filter: { type: "lowpass", baseCutoff: 1600, track: 18, q: 0.9, envAmount: 1200, envAttack: 0.80, envDecay: 2.0 },
    amp: _ADSR.swell,
    gain: 0.35,
  },

  // ── BELLS ───────────────────────────────────────────────────────────────
  "bell-glass": {
    id: "bell-glass", name: "Glass Bell", category: "Bells",
    color: "#D4B147",
    description: "Pure · twinkling · melodic top",
    engine: "synth-v1",
    osc: [
      { type: "sine",     gain: 0.90, detune: 0 },
      { type: "sine",     gain: 0.45, detune: 1200 },
      { type: "sine",     gain: 0.25, detune: 1907 },     // bell-like partial
    ],
    filter: { type: "lowpass", baseCutoff: 5000, track: 30, q: 0.4, envAmount: 0, envAttack: 0.005, envDecay: 0.1 },
    amp: _ADSR.bell,
    gain: 0.50,
  },
  "bell-tubular": {
    id: "bell-tubular", name: "Tubular", category: "Bells",
    color: "#C4B5FD",
    description: "Metallic · ringing · cinematic",
    engine: "synth-v1",
    osc: [
      { type: "triangle", gain: 0.65, detune: 0 },
      { type: "sine",     gain: 0.50, detune: 1200 },
      { type: "sine",     gain: 0.35, detune: 1907 },
      { type: "sine",     gain: 0.15, detune: 2786 },     // upper partial
    ],
    filter: { type: "lowpass", baseCutoff: 4000, track: 25, q: 0.6, envAmount: 800, envAttack: 0.003, envDecay: 0.50 },
    amp: { attack: 0.003, decay: 0.80, sustain: 0.15, release: 1.20 },
    gain: 0.42,
  },

  // ── BRASS ───────────────────────────────────────────────────────────────
  "brass-soft": {
    id: "brass-soft", name: "Soft Brass", category: "Brass",
    color: "#C77575",
    description: "Warm horn · cinematic",
    engine: "synth-v1",
    osc: [
      { type: "sawtooth", gain: 0.70, detune: -4 },
      { type: "sawtooth", gain: 0.70, detune: 4 },
      { type: "square",   gain: 0.25, detune: 0 },
    ],
    filter: { type: "lowpass", baseCutoff: 1400, track: 30, q: 1.2, envAmount: 2200, envAttack: 0.04, envDecay: 0.30 },
    amp: _ADSR.brass,
    gain: 0.42,
  },
  "brass-stab": {
    id: "brass-stab", name: "Brass Stab", category: "Brass",
    color: "#D97070",
    description: "Punchy · staccato · drill",
    engine: "synth-v1",
    osc: [
      { type: "sawtooth", gain: 0.75, detune: -6 },
      { type: "sawtooth", gain: 0.75, detune: 6 },
      { type: "square",   gain: 0.35, detune: 0 },
    ],
    filter: { type: "lowpass", baseCutoff: 1800, track: 40, q: 1.6, envAmount: 2600, envAttack: 0.005, envDecay: 0.15 },
    amp: { attack: 0.005, decay: 0.15, sustain: 0.0, release: 0.20 },
    gain: 0.50,
  },

  // ── FX ──────────────────────────────────────────────────────────────────
  "fx-riser": {
    id: "fx-riser", name: "Riser", category: "FX",
    color: "#9C8AC4",
    description: "Atmospheric swell · transition",
    engine: "synth-v1",
    osc: [
      { type: "sawtooth", gain: 0.45, detune: -20 },
      { type: "sawtooth", gain: 0.45, detune: 20 },
      { type: "triangle", gain: 0.30, detune: 1200 },
    ],
    filter: { type: "lowpass", baseCutoff: 2000, track: 50, q: 1.8, envAmount: 3000, envAttack: 1.20, envDecay: 2.0 },
    amp: _ADSR.fx,
    gain: 0.35,
  },
};

// Get an instrument preset by id. Falls back to the default ("piano-grand")
// if the id is unknown, so missing-preset crashes are impossible.
function getInstrumentMeta(instrumentId) {
  return BF_INSTRUMENTS[instrumentId] || BF_INSTRUMENTS["piano-grand"];
}

// Resolve the effective instrument meta for a track, applying any
// `selectedSfPreset` override the track carries. Tracks using a
// multi-preset bank can pick a specific (bank, program) from that
// bank's preset list via the piano-roll sub-picker; we propagate the
// override as a *derived* inst with its own engine-side id so each
// (sf_id, bank, program) combo lives as its own cached preset in the
// soundfont engine (avoids one track stealing another's channel
// binding when two tracks use the same bank but different presets).
//
// Returns an object shaped like BF_INSTRUMENTS entries — drop-in
// replacement for callers that previously read inst meta directly.
function resolveTrackInstrumentMeta(track) {
  if (!track || !track.instrumentId) return getInstrumentMeta("piano-grand");
  const base = getInstrumentMeta(track.instrumentId);
  if (!base) return getInstrumentMeta("piano-grand");
  // Only soundfont presets support the (bank, program) override.
  if (base.engine !== "soundfont-v1") return base;
  const sel = track.selectedSfPreset;
  if (!sel || typeof sel.bank !== "number" || typeof sel.program !== "number") {
    return base;
  }
  // Don't re-derive if the override matches the base's defaults exactly.
  const baseBank = base.gmBankNumber || 0;
  const baseProg = base.gmProgramNumber || 0;
  if (sel.bank === baseBank && sel.program === baseProg) return base;
  // Build a derived meta with a unique engine-side id. Same customUrl,
  // same color/name, different bank+program. The piano-roll display
  // name uses the selected preset's name from the bank list if we have
  // it; falls back to "<bank>:<program>".
  const presets = Array.isArray(base.presets) ? base.presets : [];
  const matched = presets.find(function(p){
    return p.bank === sel.bank && p.program === sel.program;
  });
  const derivedName = (matched && matched.name) ? matched.name : (sel.bank + ":" + sel.program);
  return Object.assign({}, base, {
    id:              base.id + "::" + sel.bank + "." + sel.program,
    name:            derivedName,
    gmBankNumber:    sel.bank,
    gmProgramNumber: sel.program,
    // Preserve the link back to the base bank so UI code can show the
    // bank name in metadata even though the inst is "derived".
    _bankBaseId:     base.id,
    _bankBaseName:   base.name,
  });
}

// List all instruments for the preset browser (Phase 4 wires this in).
function listInstruments() {
  return Object.keys(BF_INSTRUMENTS).map(function(id){
    return BF_INSTRUMENTS[id];
  });
}

// ── Voice builder: synth-v1 engine ───────────────────────────────────────────
// Builds a one-shot voice graph for a single MIDI note. Returns an object
// the scheduler can use to track + stop the voice.
//
// Why this shape:
//   • Per-note graph because polyphonic voices need independent ADSRs and
//     filter envelopes. One shared filter would crosstalk between notes.
//   • All nodes auto-disconnect after release ends — Web Audio GC handles
//     cleanup once references drop, but we explicitly stop oscillators so
//     they don't tick along inaudibly.
//
// Args:
//   ctx        — AudioContext
//   dest       — AudioNode the voice should feed into (FX chain entry,
//                or ctx.destination for raw preview)
//   inst       — instrument meta (BF_INSTRUMENTS[id])
//   pitch      — MIDI note number 0..127
//   velocity   — 0..1
//   when       — ctx time to start
//   duration   — note length in seconds (release starts at when+duration)
//
// Returns { stop(when?) } so the scheduler can cancel mid-flight.
function buildMidiVoiceSynthV1(ctx, dest, inst, pitch, velocity, when, duration, glide) {
  if (!ctx || !inst || velocity < MIDI_MIN_VELOCITY) return null;
  const baseFreq = midiNoteToFreq(pitch);

  // Per-voice gain (the ADSR envelope's output node)
  const ampGain = ctx.createGain();
  ampGain.gain.value = 0;

  // Per-voice filter
  const filterCfg = inst.filter || { type: "lowpass", baseCutoff: 8000, track: 0, q: 0.5, envAmount: 0, envAttack: 0.01, envDecay: 0.1 };
  const filter = ctx.createBiquadFilter();
  filter.type = filterCfg.type || "lowpass";
  // Keyboard-tracked cutoff: baseCutoff at C4 (MIDI 60), +track Hz per semitone above.
  const trackedCutoff = filterCfg.baseCutoff + filterCfg.track * (pitch - 60);
  filter.frequency.value = Math.max(50, trackedCutoff);
  filter.Q.value = filterCfg.q || 0.5;

  // Patch master gain (instrument-level)
  const master = ctx.createGain();
  master.gain.value = (inst.gain == null ? 0.6 : inst.gain) * velocity;

  // Oscillator stack — each layer gets its own gain so we can balance partials.
  // Detune values are in cents (Web Audio's `detune` is in cents already).
  const oscs = [];
  (inst.osc || [{ type: "triangle", gain: 0.7, detune: 0 }]).forEach(function(layer){
    const o = ctx.createOscillator();
    o.type = layer.type || "triangle";
    o.frequency.value = baseFreq;
    o.detune.value = layer.detune || 0;
    const g = ctx.createGain();
    g.gain.value = layer.gain == null ? 0.6 : layer.gain;
    o.connect(g);
    g.connect(filter);
    oscs.push(o);
  });

  // ── Portamento / slide glide ──
  // When this voice carries a glide plan (folded in from FL-style slide
  // notes), ramp each oscillator's detune from the base pitch toward each
  // slide target. detune is in cents, so (toPitch - pitch) * 100 gives the
  // semitone delta. The voice already sustains for the extended duration
  // (see mergeSlideVoices), so the glide is audible across the whole chain.
  if (glide && glide.length) {
    oscs.forEach(function(o){
      const baseDet = o.detune.value;
      let curTarget = baseDet;
      try { o.detune.setValueAtTime(curTarget, when); } catch(_){}
      for (let gi = 0; gi < glide.length; gi++) {
        const seg = glide[gi];
        const segStart = when + Math.max(0, seg.at || 0);
        const newTarget = baseDet + ((seg.toPitch - pitch) * 100);
        try {
          o.detune.setValueAtTime(curTarget, segStart);
          o.detune.linearRampToValueAtTime(newTarget, segStart + Math.max(0.01, seg.dur || 0.05));
        } catch(_){}
        curTarget = newTarget;
      }
    });
  }

  // Wire: filter -> ampGain (ADSR) -> master -> dest
  filter.connect(ampGain);
  ampGain.connect(master);
  master.connect(dest);

  // ── ADSR amp envelope ──
  // Attack + decay shape happens at `when`. Release starts at when+duration.
  // We curve through linearRampToValueAtTime; close enough to perceptually
  // smooth without the cost of computing setTargetAtTime curves.
  const amp = inst.amp || { attack: 0.005, decay: 0.2, sustain: 0.7, release: 0.3 };
  const att   = Math.max(0.001, amp.attack);
  const dec   = Math.max(0.001, amp.decay);
  const sus   = Math.max(0, Math.min(1, amp.sustain == null ? 0.7 : amp.sustain));
  const rel   = Math.max(0.005, amp.release);
  // Schedule envelope curve.
  ampGain.gain.setValueAtTime(0, when);
  ampGain.gain.linearRampToValueAtTime(1.0, when + att);
  ampGain.gain.linearRampToValueAtTime(sus, when + att + dec);
  // Release begins at note-off
  const releaseStart = when + Math.max(att + dec, duration);
  ampGain.gain.setValueAtTime(sus, releaseStart);
  ampGain.gain.linearRampToValueAtTime(0, releaseStart + rel);

  // ── Filter envelope ──
  // Opens cutoff up by `envAmount` Hz during attack, then closes down to
  // tracked cutoff. Gives the note a soft pluck/articulation.
  if (filterCfg.envAmount && filterCfg.envAmount > 0) {
    const peakCutoff = filter.frequency.value + filterCfg.envAmount;
    const envAtt = Math.max(0.001, filterCfg.envAttack || 0.005);
    const envDec = Math.max(0.001, filterCfg.envDecay || 0.2);
    filter.frequency.setValueAtTime(filter.frequency.value, when);
    filter.frequency.linearRampToValueAtTime(peakCutoff, when + envAtt);
    filter.frequency.linearRampToValueAtTime(
      Math.max(50, trackedCutoff), when + envAtt + envDec
    );
  }

  // Start + stop all oscillators. Stop is slightly after release end so we
  // don't cut tails before the ramp completes.
  const stopAt = releaseStart + rel + 0.05;
  oscs.forEach(function(o){
    o.start(when);
    try { o.stop(stopAt); } catch(_) {}
  });

  // Voice handle. `stop(t)` lets the scheduler cut the note early.
  return {
    stop: function(cutTime) {
      const t = cutTime == null ? ctx.currentTime : cutTime;
      try { ampGain.gain.cancelScheduledValues(t); } catch(_) {}
      try { ampGain.gain.setValueAtTime(ampGain.gain.value, t); } catch(_) {}
      try { ampGain.gain.linearRampToValueAtTime(0, t + 0.02); } catch(_) {}
      oscs.forEach(function(o){
        try { o.stop(t + 0.04); } catch(_) {}
      });
    },
    // For polyphony bookkeeping — when the note's audio is fully done.
    endsAt: stopAt,
  };
}

// ═════════════════════════════════════════════════════════════════════════════
// SAMPLER (FL Slicer-style) — voice engine
// ═════════════════════════════════════════════════════════════════════════════
//
// A "Sampler" track plays user-uploaded audio (mp3/wav) chopped into slices
// that map to MIDI notes. The track schema:
//   {
//     type: "midi",
//     instrumentId: "sampler:<sampleHash>",  // synthesized — not in BF_INSTRUMENTS
//     samplerData: {
//       sampleHash:  "sha256-hex",          // dedup key, fetched from B2
//       sampleUrl:   "https://...",         // resolved on project open
//       sampleName:  "drum-loop.wav",       // user-facing label
//       sliceMode:   "auto" | "equal" | "manual",
//       slicePoints: [0, 0.142, 0.301, ...], // SECONDS into the buffer
//       rootPitch:   60,                     // C5 = first slice
//       playMode:    "to-next" | "full",    // until next slice OR full slice
//       gain:        1.0,
//     },
//   }
//
// Why "midi" type? Because clips contain MIDI notes and the existing
// midi infrastructure (piano roll, scheduler, polyphony) all works
// out of the box. The engine field on the resolved inst is "sampler-v1".

const bfSamplerCache = {
  // hash → AudioBuffer (decoded). Lives in memory for the session so
  // reopening the editor / project doesn't re-fetch.
  buffers: new Map(),
  // hash → Promise (so concurrent calls coalesce into one fetch+decode)
  inflight: new Map(),
  // hash → friendly sample name (for the loading toast UI). Set when
  // ensureSamplerLoaded is called, cleared when the load completes or
  // fails. Separate from inflight so a UI subscriber can read names
  // without holding the promise.
  loadingNames: new Map(),
  // Simple pub/sub so a React component can re-render when loadingNames
  // changes. Subscribers receive no payload — they call the cache to
  // read state. Returns an unsubscribe function.
  _listeners: new Set(),
  subscribe(fn) {
    this._listeners.add(fn);
    return () => { this._listeners.delete(fn); };
  },
  _notify() {
    this._listeners.forEach(function(fn){ try { fn(); } catch(_){} });
  },
};

// ── Pitched-buffer cache for samplers ──
// Keyed by `<sampleHash>:<totalCents>` (where totalCents = semitones*100
// + cents). Stores the result of pitchAudioBufferOffline() — a true
// pitch-shifted buffer that preserves DURATION. This is what makes the
// sampler's pitch slider INDEPENDENT of speed: at playback time, the
// sampler reads from the pitched buffer at the user's chosen speed
// (playbackRate), so pitch and speed don't drag each other anymore.
//
// Empty before first use. Renders happen lazily when the editor saves
// new pitch settings, or in the background when a project is loaded.
const bfSamplerPitchedBuffers = new Map();
// Promises in flight, keyed the same way, so concurrent requests don't
// kick off duplicate renders.
const bfSamplerPitchedInflight = new Map();

// Render a pitched version of a sampler buffer using SoundTouch.
// Returns a Promise resolving to the new buffer (or the original if
// the shift is negligible). Cached for the rest of the session.
function bfGetSamplerPitchedBuffer(sampleHash, sourceBuffer, totalCents, ctx) {
  if (!sourceBuffer) return Promise.resolve(null);
  if (Math.abs(totalCents) < 1) {
    // < 1 cent shift — return the original. Doesn't bloat the cache
    // and avoids needless render work.
    return Promise.resolve(sourceBuffer);
  }
  const key = sampleHash + ":" + Math.round(totalCents);
  const cached = bfSamplerPitchedBuffers.get(key);
  if (cached) return Promise.resolve(cached);
  const inflight = bfSamplerPitchedInflight.get(key);
  if (inflight) return inflight;
  // Kick off the offline render. SoundTouch operates in semitones, so
  // convert cents back to a fractional semitones value.
  const semis = totalCents / 100;
  const p = pitchAudioBufferOffline(ctx, sourceBuffer, semis)
    .then(function(rendered){
      bfSamplerPitchedInflight.delete(key);
      if (rendered) {
        bfSamplerPitchedBuffers.set(key, rendered);
      }
      return rendered;
    })
    .catch(function(e){
      bfSamplerPitchedInflight.delete(key);
      console.warn("[sampler] pitch render failed for", key, e);
      // Fall back to original buffer on error
      return sourceBuffer;
    });
  bfSamplerPitchedInflight.set(key, p);
  return p;
}

// Fetch + decode a sample by hash, returning a Promise<AudioBuffer>.
// Caches forever (per session). Reuses the AudioContext from getActx so
// the decoded buffer is compatible with the live playback context.
function bfLoadSampleByHash(hash, url, ctx, displayName) {
  if (!hash || !ctx) return Promise.reject(new Error("missing hash or ctx"));
  if (bfSamplerCache.buffers.has(hash)) {
    return Promise.resolve(bfSamplerCache.buffers.get(hash));
  }
  if (bfSamplerCache.inflight.has(hash)) {
    return bfSamplerCache.inflight.get(hash);
  }
  // Surface to UI: mark this hash as loading with a human-readable name.
  // Cleared in BOTH the success and error tails below so the toast never
  // sticks past a completed load.
  bfSamplerCache.loadingNames.set(hash, displayName || "Sample");
  bfSamplerCache._notify();
  const p = fetch(url)
    .then(function(r){
      if (!r.ok) throw new Error("sample fetch HTTP " + r.status);
      return r.arrayBuffer();
    })
    .then(function(buf){
      return new Promise(function(resolve, reject){
        ctx.decodeAudioData(buf, function(decoded){
          bfSamplerCache.buffers.set(hash, decoded);
          bfSamplerCache.inflight.delete(hash);
          bfSamplerCache.loadingNames.delete(hash);
          bfSamplerCache._notify();
          resolve(decoded);
        }, function(err){
          bfSamplerCache.inflight.delete(hash);
          bfSamplerCache.loadingNames.delete(hash);
          bfSamplerCache._notify();
          reject(err || new Error("decode failed"));
        });
      });
    })
    .catch(function(err){
      bfSamplerCache.inflight.delete(hash);
      bfSamplerCache.loadingNames.delete(hash);
      bfSamplerCache._notify();
      throw err;
    });
  bfSamplerCache.inflight.set(hash, p);
  return p;
}

// ── Transient detection ─────────────────────────────────────────────────────
//
// Detects musical onsets in any audio buffer — drum hits, vocal phrases,
// melodic notes, sample chops. Algorithm:
//
//   1. RMS energy in 10ms windows (50% overlap → 5ms hop).
//   2. Novelty function = positive log-energy difference between adjacent
//      windows. Logarithmic so loud and quiet sections detect equally.
//   3. Adaptive threshold: rolling mean + k*stddev over a moving window.
//      Adjusts to local dynamics so a quiet verse and a loud chorus both
//      yield onsets.
//   4. Peak-pick: any window whose novelty > local threshold AND is a
//      local maximum within ±2 windows is an onset.
//   5. Min-spacing filter (configurable: 60–400ms based on sensitivity).
//   6. Snap each onset back by 5ms so the slice starts AT the attack.
//
// Sensitivity slider [0..1]:
//   0.0 → very few slices (only the most prominent transients)
//   0.5 → moderate (typical music: 10–30 slices per minute)
//   1.0 → many slices (catches every breath / vocal fricative)
//
// Returns: array of slice-start times in SECONDS, beginning with 0.
function bfDetectTransients(audioBuffer, sensitivity) {
  if (!audioBuffer) return [0];
  const sens = (sensitivity == null) ? 0.5 : Math.max(0, Math.min(1, sensitivity));
  // k = std-deviation multiplier for the adaptive threshold. Higher k =
  // fewer onsets (only big spikes). Tuned so:
  //   sens=0.0 → k≈5.0 (very few — only the most prominent transients)
  //   sens=0.5 → k≈3.0 (moderate — ~10-30 slices/min for typical music)
  //   sens=1.0 → k≈1.0 (many — catches breaths/fricatives)
  const k = 5.0 - sens * 4.0;
  // Min-spacing scales with sensitivity too. Map [0,1] → [0.60s, 0.10s].
  const minSpacingSec = 0.60 - sens * 0.50;
  const lookBackSec = 0.005;

  const sr = audioBuffer.sampleRate;
  const dur = audioBuffer.duration;
  const totalSamples = audioBuffer.length;
  const numChans = audioBuffer.numberOfChannels;
  const chans = [];
  for (let c = 0; c < numChans; c++) chans.push(audioBuffer.getChannelData(c));

  // RMS per window (10ms window, 5ms hop)
  const winSize = Math.max(128, Math.floor(sr * 0.010));
  const hopSize = Math.max(64, Math.floor(sr * 0.005));
  const rms = [];
  for (let i = 0; i + winSize <= totalSamples; i += hopSize) {
    let sum = 0;
    for (let n = 0; n < winSize; n++) {
      let s = 0;
      for (let c = 0; c < numChans; c++) s += chans[c][i + n];
      s /= numChans;
      sum += s * s;
    }
    rms.push(Math.sqrt(sum / winSize));
  }
  if (rms.length < 4) return [0];

  // Log-energy (avoid log(0) with a tiny floor)
  const EPS = 1e-6;
  const logE = new Float32Array(rms.length);
  for (let i = 0; i < rms.length; i++) logE[i] = Math.log(rms[i] + EPS);

  // Novelty = positive first difference of log-energy. Rectified so we
  // only react to energy INCREASES (onsets), not decreases (releases).
  const novelty = new Float32Array(rms.length);
  novelty[0] = 0;
  for (let i = 1; i < rms.length; i++) {
    const d = logE[i] - logE[i - 1];
    novelty[i] = d > 0 ? d : 0;
  }

  // Smooth novelty with a 3-point average to reduce noise
  const sm = new Float32Array(novelty.length);
  for (let i = 0; i < novelty.length; i++) {
    let s = 0, c = 0;
    for (let j = Math.max(0, i - 1); j <= Math.min(novelty.length - 1, i + 1); j++) {
      s += novelty[j]; c++;
    }
    sm[i] = s / c;
  }

  // Adaptive threshold: rolling mean + k*stddev over a 1-second window.
  // Window in frames: 1s / hop. Computing mean+stddev with a sliding sum
  // for speed.
  const windowFrames = Math.max(20, Math.floor(1.0 / (hopSize / sr)));
  const half = Math.floor(windowFrames / 2);

  // Pre-compute prefix sums for O(1) window mean/variance
  const ps  = new Float64Array(sm.length + 1);
  const ps2 = new Float64Array(sm.length + 1);
  for (let i = 0; i < sm.length; i++) {
    ps[i + 1]  = ps[i]  + sm[i];
    ps2[i + 1] = ps2[i] + sm[i] * sm[i];
  }

  // Find onset candidates
  const onsetFrames = [];
  for (let i = 1; i < sm.length - 1; i++) {
    // Local maximum check — within ±1 frame
    if (sm[i] <= sm[i - 1] || sm[i] < sm[i + 1]) continue;
    // Window bounds
    const lo = Math.max(0, i - half);
    const hi = Math.min(sm.length, i + half + 1);
    const n  = hi - lo;
    if (n < 4) continue;
    const mean = (ps[hi]  - ps[lo])  / n;
    const var_ = (ps2[hi] - ps2[lo]) / n - mean * mean;
    const std  = Math.sqrt(Math.max(0, var_));
    const threshold = mean + k * std;
    // Must also be above an absolute floor — prevents zero-novelty noise
    // from registering as onsets in silent sections.
    if (sm[i] < threshold) continue;
    // Absolute floor on log-novelty — prevents adaptive threshold from
    // letting tiny fluctuations through during steady-state passages.
    if (sm[i] < 0.08) continue;
    onsetFrames.push(i);
  }

  // Convert frames → seconds, enforce min spacing, snap back
  const slices = [0];
  for (let i = 0; i < onsetFrames.length; i++) {
    const t = Math.max(0, (onsetFrames[i] * hopSize) / sr - lookBackSec);
    if (t >= dur - 0.02) break;
    if (t - slices[slices.length - 1] < minSpacingSec) continue;
    slices.push(t);
  }
  return slices;
}

// Equal-division slicing — N slices of equal length. Used when the user
// picks "equal" mode in the sampler editor instead of auto.
function bfEqualSlices(audioBuffer, n) {
  if (!audioBuffer || !n || n < 1) return [0];
  const dur = audioBuffer.duration;
  const out = [];
  for (let i = 0; i < n; i++) out.push((i / n) * dur);
  return out;
}

// ── Voice builder: sampler-v1 ───────────────────────────────────────────────
//
// Plays the slice mapped to `pitch`. The sampler's `rootPitch` is the
// MIDI note assigned to slice 0; pitch = rootPitch + sliceIndex.
// (So sliceCount=16 with rootPitch=60 covers C5..D#6.)
//
// Pitches outside the slice range play nothing (silent stub) — same as
// playing a key with no sample loaded. Producers see immediately which
// keys are mapped on the piano roll's keyboard graphic.
function buildMidiVoiceSamplerV1(ctx, dest, inst, pitch, velocity, when, duration, glide) {
  if (!ctx || !inst || !inst.samplerData) return null;
  const sd = inst.samplerData;
  const buf = inst.audioBuffer;  // resolved by ensureSamplerLoaded() before scheduling
  if (!buf) return null;

  const rootPitch  = sd.rootPitch != null ? sd.rootPitch : 60;
  const sliceIdx   = Math.round(pitch) - rootPitch;
  const slicePts   = sd.slicePoints || [0];
  if (sliceIdx < 0 || sliceIdx >= slicePts.length) return null;

  const startSec   = slicePts[sliceIdx];
  // Slice duration: if playMode is "to-next", play until the next slice
  // boundary (or end of buffer). If "full", play the whole slice from
  // start to end of buffer. FL Slicer default is essentially "to-next".
  const playMode   = sd.playMode || "to-next";
  let sliceDurSec;
  if (playMode === "full") {
    sliceDurSec = buf.duration - startSec;
  } else {
    const nextStart = (sliceIdx + 1 < slicePts.length) ? slicePts[sliceIdx + 1] : buf.duration;
    sliceDurSec = nextStart - startSec;
  }
  if (sliceDurSec <= 0) return null;

  // Voice graph: BufferSource → gain → dest. The gain envelope has a
  // tiny attack (3ms) and release (8ms) to prevent clicks at slice
  // boundaries that don't fall on zero-crossings.
  const src = ctx.createBufferSource();
  src.buffer = buf;
  // Playback rate — speeds up / slows down the sample. When a
  // pitch-shifted buffer is loaded (see bfSamplerPitchedBuffers), the
  // effectivePlaybackRate excludes the pitch factor so speed and pitch
  // are independent. When no pitched buffer is available (still
  // rendering, or shift was negligible), this falls back to the
  // legacy combined playbackRate.
  const rate = (inst.effectivePlaybackRate != null && inst.effectivePlaybackRate > 0)
    ? inst.effectivePlaybackRate
    : ((sd.playbackRate != null && sd.playbackRate > 0) ? sd.playbackRate : 1.0);
  src.playbackRate.setValueAtTime(rate, when);

  // ── Portamento / slide glide ──
  // Ramp the source detune (cents) from 0 toward each slide target so an
  // 808/bass note glides between pitches without retriggering. detune
  // combines with playbackRate, so this bends pitch on top of the slice's
  // base rate. (toPitch - pitch) * 100 = the semitone delta in cents.
  if (glide && glide.length && src.detune) {
    let curTarget = 0;
    try { src.detune.setValueAtTime(0, when); } catch(_){}
    for (let gi = 0; gi < glide.length; gi++) {
      const seg = glide[gi];
      const segStart = when + Math.max(0, seg.at || 0);
      const newTarget = (seg.toPitch - pitch) * 100;
      try {
        src.detune.setValueAtTime(curTarget, segStart);
        src.detune.linearRampToValueAtTime(newTarget, segStart + Math.max(0.01, seg.dur || 0.05));
      } catch(_){}
      curTarget = newTarget;
    }
  }

  // ── Optional filter (from Edit Sound overrides) ──
  // Insert a lowpass filter only when the user set a cutoff/resonance
  // override — keeps the default path node-free and identical to before.
  const ovr = inst.instrumentOverrides || null;
  const ovrFilter = ovr && ovr.filter ? ovr.filter : null;
  let filterNode = null;
  if (ovrFilter && (ovrFilter.baseCutoff != null || ovrFilter.q != null)) {
    filterNode = ctx.createBiquadFilter();
    filterNode.type = "lowpass";
    filterNode.frequency.value = Math.max(100, Math.min(18000, ovrFilter.baseCutoff != null ? ovrFilter.baseCutoff : 18000));
    filterNode.Q.value = Math.max(0.1, Math.min(12, ovrFilter.q != null ? ovrFilter.q : 0.7));
  }

  const gain = ctx.createGain();
  const peakGain = (sd.gain != null ? sd.gain : 1.0) * Math.max(0.05, velocity == null ? 0.85 : velocity);

  // ── Amp envelope ──
  // With ADSR overrides: shape attack→decay→sustain on note-on, release
  // on note-off. Without: the original tiny 3ms attack (click guard).
  const ovrAmp = ovr && ovr.amp ? ovr.amp : null;
  const hasADSR = !!(ovrAmp && (ovrAmp.attack != null || ovrAmp.decay != null || ovrAmp.sustain != null || ovrAmp.release != null));
  const atk = hasADSR && ovrAmp.attack  != null ? Math.max(0.001, ovrAmp.attack)  : 0.003;
  const dec = hasADSR && ovrAmp.decay   != null ? Math.max(0.001, ovrAmp.decay)   : 0;
  const sus = hasADSR && ovrAmp.sustain != null ? Math.max(0,    Math.min(1, ovrAmp.sustain)) : 1;
  gain.gain.setValueAtTime(0, when);
  gain.gain.linearRampToValueAtTime(peakGain, when + atk);
  if (hasADSR && dec > 0 && sus < 1) {
    // Decay from peak down to sustain level.
    gain.gain.linearRampToValueAtTime(peakGain * sus, when + atk + dec);
  }

  // Wire: src → [filter] → gain → dest
  if (filterNode) {
    src.connect(filterNode);
    filterNode.connect(gain);
  } else {
    src.connect(gain);
  }
  gain.connect(dest);

  // Schedule start with the slice offset. The duration param to start()
  // is in BUFFER seconds (it specifies how much of the buffer to read
  // starting from startSec), not in audible seconds. So we use the raw
  // slice length here; the rate change affects the AUDIBLE duration but
  // not the read length.
  const playLen = Math.min(sliceDurSec, buf.duration - startSec - 0.001);
  try { src.start(when, startSec, playLen); } catch(_) {}

  // Audible duration of this slice at the current playback rate.
  // At rate=2x, the slice plays in half its native time.
  const audibleSliceDur = sliceDurSec / rate;

  // Schedule the release ramp + stop. Use `duration` if provided (e.g.
  // the MIDI note was short — choke playback at note-off), else play
  // the whole slice (in audible time).
  const noteOnDur = duration == null ? audibleSliceDur : Math.min(duration, audibleSliceDur);
  // Release time: from the override if set, else the original fast 15ms.
  const rel = (hasADSR && ovrAmp.release != null) ? Math.max(0.005, ovrAmp.release) : 0.015;
  // Level the envelope is at when release begins — sustain level if ADSR
  // shaped it, otherwise full peak.
  const heldLevel = (hasADSR && sus < 1) ? (peakGain * sus) : peakGain;
  const releaseStart = when + Math.max(0.01, noteOnDur - 0.008);
  const stopAt = releaseStart + rel;
  gain.gain.setValueAtTime(heldLevel, releaseStart);
  gain.gain.linearRampToValueAtTime(0, stopAt);
  try { src.stop(stopAt + 0.01); } catch(_) {}

  return {
    stop: function(cutTime){
      const t = cutTime == null ? ctx.currentTime : cutTime;
      try { gain.gain.cancelScheduledValues(t); } catch(_){}
      try { gain.gain.setValueAtTime(gain.gain.value, t); } catch(_){}
      try { gain.gain.linearRampToValueAtTime(0, t + 0.012); } catch(_){}
      try { src.stop(t + 0.02); } catch(_){}
    },
    endsAt: stopAt,
  };
}

// Helper: resolve a sampler track's "instrument" object for the scheduler.
// Different from BF_INSTRUMENTS lookup because sampler instruments are
// per-track (each track owns its own slice points and sample reference).
// Returns null if the sample isn't loaded yet (caller must skip the
// note OR await preload).
function resolveSamplerInst(track) {
  if (!track || !track.samplerData) return null;
  const sd = track.samplerData;
  const origBuf = sd.sampleHash ? bfSamplerCache.buffers.get(sd.sampleHash) : null;
  // Compute the user's chosen pitch in cents.
  const totalCents = (sd.pitchSemitones || 0) * 100 + (sd.pitchCents || 0);
  // Look for a pre-rendered pitch-shifted buffer matching that exact
  // value. If one exists, we use it AND compute a playbackRate that
  // excludes the pitch factor — so the speed slider stays independent.
  let buf = origBuf;
  let effectivePlaybackRate = sd.playbackRate || 1.0;
  if (origBuf && Math.abs(totalCents) >= 1) {
    const key = sd.sampleHash + ":" + Math.round(totalCents);
    const pitched = bfSamplerPitchedBuffers.get(key);
    if (pitched) {
      buf = pitched;
      // The pitched buffer is already at the desired pitch. The
      // playbackRate now controls ONLY speed and tempo-lock — not pitch.
      const speed = (sd.speedAdjust != null) ? sd.speedAdjust : 1.0;
      const safeProjBpm = 120; // best-effort fallback when no project bpm
      const safeSampBpm = (sd.sampleBpm && sd.sampleBpm > 0) ? sd.sampleBpm : 120;
      // We can't easily reach projectBpm from this static function; the
      // tempoFactor is already baked into sd.playbackRate at save time,
      // so derive it by dividing out the pitchFactor:
      //   savedRate = pitchFactor * speed * tempoFactor
      //   tempoFactor = savedRate / (pitchFactor * speed)
      // This keeps tempo-lock working without needing projectBpm here.
      const pitchFactor = Math.pow(2, totalCents / 1200);
      if (pitchFactor > 0 && speed > 0) {
        effectivePlaybackRate = (sd.playbackRate || (pitchFactor * speed)) / pitchFactor;
      } else {
        effectivePlaybackRate = speed;
      }
    }
  }
  return {
    id: "sampler:" + (sd.sampleHash || "unloaded"),
    name: sd.sampleName || "Sampler",
    engine: "sampler-v1",
    samplerData: sd,
    audioBuffer: buf,        // null until loaded; pitched if available
    effectivePlaybackRate: effectivePlaybackRate,  // speed-only when using pitched buf
    gain: 1.0,
    // Carry Edit Sound ADSR/filter overrides so the voice builder can
    // shape the envelope/tone. Sampler voices are built in pure JS, so
    // we apply these directly to the gain envelope + a filter node
    // (rather than via MIDI CCs like soundfonts/synths).
    instrumentOverrides: track.instrumentOverrides || null,
  };
}

// Ensure the sampler track's sample is loaded into memory. Fire-and-forget
// safe to call every scheduler tick — coalesces via bfSamplerCache.inflight.
// Also kicks off a background pitch-shift render if the sampler has a
// non-zero pitch setting, so subsequent playbacks use a true pitch-shifted
// buffer (independent of speed).
function ensureSamplerLoaded(track, ctx) {
  if (!track || !track.samplerData) return Promise.resolve(null);
  const sd = track.samplerData;
  if (!sd.sampleHash || !sd.sampleUrl) return Promise.resolve(null);
  const name = sd.sampleName || track.name || "Sample";
  return bfLoadSampleByHash(sd.sampleHash, sd.sampleUrl, ctx, name)
    .then(function(rawBuf){
      // Background pitch render — fires and resolves later. Doesn't
      // block the load promise; raw buffer is what the caller gets.
      const totalCents = (sd.pitchSemitones || 0) * 100 + (sd.pitchCents || 0);
      if (rawBuf && Math.abs(totalCents) >= 1) {
        const key = sd.sampleHash + ":" + Math.round(totalCents);
        if (!bfSamplerPitchedBuffers.has(key) && !bfSamplerPitchedInflight.has(key)) {
          bfGetSamplerPitchedBuffer(sd.sampleHash, rawBuf, totalCents, ctx);
        }
      }
      return rawBuf;
    });
}


// Top-level voice dispatcher. Picks the right engine builder based on
// inst.engine. Future engines (granular, hybrid) plug in here.
function scheduleMidiNote(ctx, dest, inst, pitch, velocity, when, duration, noteCutMode, trackId, glide) {
  if (!inst) return null;
  const engine = inst.engine || "synth-v1";
  if (engine === "synth-v1") {
    // Synth-v1 voices build their own gain envelope per note, so the
    // note-cut feature is implemented at the call site (the scheduler
    // calls voice.stop() on conflicting voices before the new one).
    return buildMidiVoiceSynthV1(ctx, dest, inst, pitch, velocity, when, duration, glide);
  }
  if (engine === "soundfont-v1") {
    // ── Routing diagnostic (throttled, verbose-gated) ──
    try {
      if (typeof bfDebugVerboseOn === "function" && bfDebugVerboseOn()) {
        if (!scheduleMidiNote._lastRouteLog || Date.now() - scheduleMidiNote._lastRouteLog > 500) {
          scheduleMidiNote._lastRouteLog = Date.now();
          const le = (trackId != null && typeof getBFSpessaLiveEngineForTrack === "function") ? getBFSpessaLiveEngineForTrack(trackId) : null;
          bfDebugLogVerbose("[ROUTE] sf id=" + inst.id + " shaped=" + (!!inst._soundShaped) +
            " trackId=" + (trackId != null) +
            " liveStatus=" + (le ? le.libStatus : "none") +
            " livePreset=" + (le ? le.getPresetStatus(inst.id) : "none"));
        }
      }
    } catch(_){}
    // ── Routing: per-note ADSR needs SpessaSynth (FluidSynth ignores the
    // attack/decay/release CCs by design). A soundfont track WITH active
    // sound-shaping overrides plays through the SpessaSynth-live engine;
    // a track WITHOUT overrides uses FluidSynth exactly as before. ──
    // CRITICAL: only route to the live engine if it's actually READY with
    // the preset loaded — otherwise fall through to FluidSynth so there's
    // always sound (avoids silent preview/notes while the live engine is
    // still warming up). FluidSynth simply won't have the per-note attack
    // until the live engine is ready, which is better than silence.
    if (trackId != null && bfInstNeedsSpessaLive(inst)) {
      const liveEng = getBFSpessaLiveEngineForTrack(trackId);
      if (liveEng && liveEng.libStatus === "ready" &&
          liveEng.getPresetStatus(inst.id) === "ready") {
        return liveEng.scheduleNote(inst.id, pitch, velocity, when, duration, noteCutMode, inst._overrides || null);
      }
      // Not ready yet — kick off the load so it's ready soon, then fall
      // through to FluidSynth for THIS note so the user hears something.
      if (liveEng && liveEng.libStatus !== "error") {
        try { liveEng.ensureReady(ctx, dest).then(function(){
          liveEng.loadPreset(inst.id, inst.gmProgramNumber || 0, inst.customUrl, inst.gmBankNumber || 0);
        }); } catch(_){}
      }
    }
    // Per-track engine routing: if trackId is provided, this track has
    // its OWN FluidSynth instance whose audioNode routes to the track's
    // FX chain entry. That's how Reverb / EQ / Compressor etc. work
    // on MIDI tracks. Falls back to the shared singleton if no trackId
    // — useful for browser previews and other non-track contexts.
    const sfEngine = (trackId != null)
      ? getBFSoundfontEngineForTrack(trackId)
      : getBFSoundfontEngine();
    return sfEngine.scheduleNote(inst.id, pitch, velocity, when, duration, noteCutMode);
  }
  if (engine === "sampler-v1") {
    // Sampler voices are per-note BufferSource graphs (one shot per
    // note). The note-cut feature is handled at the call site for
    // sampler too — same as synth-v1. inst.audioBuffer must be
    // resolved BEFORE scheduling (caller uses ensureSamplerLoaded).
    return buildMidiVoiceSamplerV1(ctx, dest, inst, pitch, velocity, when, duration, glide);
  }
  // Unknown engine — silently skip (don't break playback for one bad preset).
  console.warn("[midi] unknown instrument engine", engine);
  return null;
}

// Best-effort eager-load a soundfont preset. Called when a track gets
// a soundfont preset assigned, when the editor opens, or when local
// playback starts. Idempotent — does nothing if already loaded.
// Returns a Promise (so callers can await) but most call sites fire and
// forget — the load happens in the background while existing audio
// continues.
// trackId is optional but should be passed for MIDI tracks so the
// loaded preset binds to that track's per-track engine, with the
// engine's audioNode routed to the track's FX chain entry (masterDest).
function ensureSoundfontPresetLoaded(inst, ctx, masterDest, trackId) {
  if (!inst || inst.engine !== "soundfont-v1") return Promise.resolve();
  // If this track is sound-shaped, ALSO prime the SpessaSynth-live engine
  // (per-note ADSR). We load both engines: FluidSynth stays ready so
  // toggling overrides OFF returns to it instantly, and the live engine is
  // ready for when overrides are ON.
  const promises = [];
  if (trackId != null && bfInstNeedsSpessaLive(inst)) {
    const liveEng = getBFSpessaLiveEngineForTrack(trackId);
    if (liveEng) {
      promises.push(
        liveEng.ensureReady(ctx, masterDest)
          .then(() => liveEng.loadPreset(inst.id, inst.gmProgramNumber || 0, inst.customUrl, inst.gmBankNumber || 0))
          .catch((e) => { console.warn("[spessa-live] preload failed", inst.id, e); })
      );
    }
  }
  const sfEngine = (trackId != null)
    ? getBFSoundfontEngineForTrack(trackId)
    : getBFSoundfontEngine();
  promises.push(
    sfEngine.ensureReady(ctx, masterDest)
      .then(() => sfEngine.loadPreset(inst.id, inst.gmProgramNumber || 0, inst.customUrl, inst.gmBankNumber || 0))
      .catch((e) => { console.warn("[soundfont] preload failed", inst.id, e); })
  );
  return Promise.all(promises);
}

// ── Apply per-track sound-design overrides ─────────────────────────────────
// Producers tweak each track's ADSR + filter cutoff/resonance without
// touching the underlying preset. The track stores its override values in
// track.instrumentOverrides; the scheduler merges those onto the preset
// before building each voice.
//
// We only override the leaf fields the editor exposes — keeping the
// oscillator stack, gain, and filter envelope amount/curves locked to the
// preset's recipe. This means the user can shape the tone (brighter or
// darker filter, slower or faster envelope) without breaking the preset's
// fundamental character.
//
// Returns a new shallow-merged preset object — never mutates the input.
// Convert Edit Sound ADSR/filter overrides into MIDI CC messages that
// modify a soundfont channel. Both FluidSynth (live) and SpessaSynth
// (export) interpret these CCs the same way (0-127, 64 = neutral), so
// this single helper keeps live playback and export in sync.
//
//   CC#73 Attack Time, CC#75 Decay Time, CC#72 Release Time
//   CC#74 Brightness (filter cutoff), CC#71 Filter Resonance
//
// Time CCs are relative to the soundfont's NATIVE envelope: 64 leaves it
// untouched, each step above/below scales the time. We map the user's
// absolute seconds onto 0-127 logarithmically so the control feels even.
function bfSoundfontOverrideCCs(overrides) {
  const ccs = [];
  if (!overrides || typeof overrides !== "object") return ccs;
  const timeToCC = function(sec) {
    if (sec == null) return null;
    const s = Math.max(0.001, Math.min(8, sec));
    // 0.005s → ~0, 0.15s → ~64, 2s → ~110, 8s → 127 (log scale)
    const norm = (Math.log(s) - Math.log(0.005)) / (Math.log(8) - Math.log(0.005));
    return Math.max(0, Math.min(127, Math.round(norm * 127)));
  };
  if (overrides.amp && typeof overrides.amp === "object") {
    const a = timeToCC(overrides.amp.attack);
    const d = timeToCC(overrides.amp.decay);
    const r = timeToCC(overrides.amp.release);
    if (a != null) ccs.push({ cc: 73, value: a });  // Attack Time
    if (d != null) ccs.push({ cc: 75, value: d });  // Decay Time
    if (r != null) ccs.push({ cc: 72, value: r });  // Release Time
  }
  if (overrides.filter && typeof overrides.filter === "object") {
    if (overrides.filter.baseCutoff != null) {
      const hz = Math.max(200, Math.min(18000, overrides.filter.baseCutoff));
      const norm = (Math.log(hz) - Math.log(200)) / (Math.log(18000) - Math.log(200));
      ccs.push({ cc: 74, value: Math.max(0, Math.min(127, Math.round(norm * 127))) });  // Brightness
    }
    if (overrides.filter.q != null) {
      const q = Math.max(0.5, Math.min(12, overrides.filter.q));
      const norm = (q - 0.5) / (12 - 0.5);
      ccs.push({ cc: 71, value: Math.max(0, Math.min(127, Math.round(64 + norm * 63))) });  // Resonance
    }
  }
  return ccs;
}

function applyInstrumentOverrides(preset, overrides) {
  if (!preset) return preset;
  if (!overrides || typeof overrides !== "object") return preset;
  const out = Object.assign({}, preset);
  // ADSR overrides
  if (overrides.amp && typeof overrides.amp === "object") {
    out.amp = Object.assign({}, preset.amp || {}, overrides.amp);
  }
  // Filter overrides — only baseCutoff and q (the producer-friendly knobs).
  // We intentionally don't expose envAmount / track / envAttack / envDecay
  // since they're more about the preset's *character* than its tonality.
  if (overrides.filter && typeof overrides.filter === "object") {
    out.filter = Object.assign({}, preset.filter || {}, {
      baseCutoff: overrides.filter.baseCutoff != null ? overrides.filter.baseCutoff : (preset.filter || {}).baseCutoff,
      q: overrides.filter.q != null ? overrides.filter.q : (preset.filter || {}).q,
    });
  }
  // Per-instrument gain trim (volume) — defaults to preset's gain
  if (overrides.gain != null) {
    out.gain = overrides.gain;
  }
  // Stamp the raw overrides + a flag so downstream routing can decide
  // whether a soundfont track needs the SpessaSynth-live engine (per-note
  // ADSR) instead of FluidSynth. Only amp/filter shape the sound; a gain
  // trim alone doesn't need the heavier engine.
  out._overrides = overrides;
  out._soundShaped = !!(
    (overrides.amp && typeof overrides.amp === "object" &&
      (overrides.amp.attack != null || overrides.amp.decay != null ||
       overrides.amp.sustain != null || overrides.amp.release != null)) ||
    (overrides.filter && typeof overrides.filter === "object" &&
      (overrides.filter.baseCutoff != null || overrides.filter.q != null))
  );
  return out;
}

// True when a resolved inst carries active sound-shaping (amp/filter)
// overrides — i.e. it should play through the SpessaSynth-live engine so
// the attack/decay/release/filter are applied per note (FluidSynth can't).
function bfInstNeedsSpessaLive(inst) {
  return !!(inst && inst.engine === "soundfont-v1" && inst._soundShaped);
}

// ── Offline / live scheduling helpers ────────────────────────────────────────
// Resolve which notes from a clip fall in a given project-time window.
// ── FL-style slide-note merging (portamento) ──
// A "slide" note (n.slide === true) does NOT retrigger. Instead it glides
// the PRECEDING sounding note's pitch toward the slide note's pitch over the
// slide note's duration. This folds each base note plus its trailing chain
// of slide notes into a single voice-note carrying:
//   • extended `duration` (base start → end of the last slide in the chain)
//   • a `glide` array of { at, toPitch, dur } segments — times in seconds
//     relative to the voice start — describing each pitch ramp.
// A slide note with no voice to attach to (a gap before it) falls back to a
// normal note so it's never silent. Returns the input unchanged (zero cost)
// when there are no slide notes. Never mutates the caller's note objects.
function mergeSlideVoices(notes) {
  if (!notes || !notes.length) return notes || [];
  let hasSlide = false;
  for (let i = 0; i < notes.length; i++) {
    if (notes[i] && notes[i].slide) { hasSlide = true; break; }
  }
  if (!hasSlide) return notes;

  const sorted = notes.slice().sort(function(a, b){ return (a.start || 0) - (b.start || 0); });
  const out = [];
  let cur = null;
  for (let i = 0; i < sorted.length; i++) {
    const n = sorted[i];
    const nStart = n.start || 0;
    const nDur = n.duration || 0.25;
    const nEnd = nStart + nDur;
    if (!n.slide) {
      cur = Object.assign({}, n);
      out.push(cur);
      continue;
    }
    // Slide note: attach to the current voice if contiguous/overlapping.
    if (cur && nStart <= (cur.start + cur.duration) + 1e-3) {
      const offset = Math.max(0, nStart - cur.start);
      cur.glide = (cur.glide || []).concat([{
        at: offset,
        toPitch: n.pitch,
        dur: Math.max(0.01, nDur),
      }]);
      const newEnd = Math.max(cur.start + cur.duration, nEnd);
      cur.duration = newEnd - cur.start;
    } else {
      // Orphan slide — nothing to glide from; play it as a normal note.
      cur = Object.assign({}, n);
      delete cur.slide;
      out.push(cur);
    }
  }
  return out;
}

// Handles looping inside the clip: if loopEnabled and clip.duration is
// longer than the pattern (clip.patternDuration), the notes wrap.
//
// Returns array of { pitch, velocity, projectTime, duration } — projectTime
// is absolute (timeline time), ready to be added to timeOffset for ctx time.
function midiClipNotesInWindow(clip, windowStart, windowEnd) {
  if (!clip || !clip.notes || clip.notes.length === 0) return [];
  const clipStart = clip.startTime || 0;
  const clipDur   = clip.duration || 0;
  if (clipDur <= 0) return [];
  const clipEnd = clipStart + clipDur;
  if (clipEnd <= windowStart || clipStart >= windowEnd) return [];

  // Pattern length — defaults to full clip duration when not specified
  // (i.e. notes play once, no internal loop).
  const patDur = clip.loopEnabled && clip.patternDuration > 0
    ? clip.patternDuration
    : clipDur;

  const out = [];
  // Fold FL-style slide notes into base voices (extended duration + glide
  // plan) before projecting. No-op when the clip has no slide notes.
  const mergedNotes = mergeSlideVoices(clip.notes);
  // Walk pattern repeats whose start lands inside [windowStart, windowEnd).
  // Each repeat is anchored at clipStart + k*patDur, k = 0, 1, 2, ...
  const firstK = Math.max(0, Math.floor((windowStart - clipStart) / patDur));
  const lastK  = Math.floor((Math.min(clipEnd, windowEnd) - clipStart) / patDur);
  for (let k = firstK; k <= lastK; k++) {
    const repeatBase = clipStart + k * patDur;
    if (repeatBase >= clipEnd) break;
    for (let i = 0; i < mergedNotes.length; i++) {
      const n = mergedNotes[i];
      const projectTime = repeatBase + (n.start || 0);
      // Skip notes outside the clip's actual end (last iteration is partial).
      if (projectTime >= clipEnd) continue;
      // Skip notes outside the lookahead window.
      if (projectTime < windowStart || projectTime >= windowEnd) continue;
      out.push({
        pitch: n.pitch | 0,
        velocity: n.velocity == null ? MIDI_DEFAULT_VELOCITY : n.velocity,
        projectTime: projectTime,
        // Trim duration so the note can't bleed past the clip's end.
        duration: Math.max(0.02, Math.min(n.duration || 0.25, clipEnd - projectTime)),
        noteId: n.id,
        glide: n.glide || null,
      });
    }
  }
  return out;
}

// Offline export: walk every MIDI clip on a track and schedule all notes
// into the given OfflineAudioContext. Mirrors scheduleDrumTrackOffline.
function scheduleMidiTrackOffline(ctx, entryNode, track, bpm, timeOffset, fromTime, toTime) {
  if (!track || track.type !== "midi") return;
  // Merge per-track ADSR/filter overrides onto the preset (no-op for tracks
  // that haven't been customized — keeps default presets identical to v1).
  // resolveTrackInstrumentMeta also applies any selectedSfPreset override
  // for tracks using a multi-preset bank — so the timeline plays whatever
  // preset the piano-roll sub-picker selected, not the bank's default.
  const baseInst = resolveTrackInstrumentMeta(track);
  const inst = applyInstrumentOverrides(baseInst, track.instrumentOverrides);
  const clips = track.clips || [];
  for (const clip of clips) {
    if (clip.active === false) continue;
    const notes = midiClipNotesInWindow(clip, fromTime, toTime);
    for (let i = 0; i < notes.length; i++) {
      const n = notes[i];
      const when = n.projectTime + timeOffset;
      if (when < 0) continue;
      scheduleMidiNote(ctx, entryNode, inst, n.pitch, n.velocity, when, n.duration, track.noteCutMode, track.id, n.glide);
    }
  }
}

// ═════════════════════════════════════════════════════════════════════════════
//  MIDI Editor (Phase 2) — piano roll
// ═════════════════════════════════════════════════════════════════════════════
//
// Full-screen modal for editing notes inside a MIDI clip. Touch-first; FL
// Studio-style pinch zoom (no fixed default — user pinches X/Y to taste).
//
// Architecture:
//   • Three regions in a CSS grid: keyboard column (left, sticky), grid
//     (center, scrollable both axes), velocity lane (bottom, scrolls
//     horizontally in sync with grid).
//   • Single delegated pointer handler on the grid body. Hit-tests via the
//     pointer's logical coords (pixel → time + pitch). This avoids attaching
//     handlers to every note (which would be expensive at 200+ notes).
//   • Internal undo/redo stack scoped to this editor session. Closes wipe it.
//   • Preview audio: tapping a keyboard key or placing a note fires a one-
//     shot voice via scheduleMidiNote into a fresh GainNode → ctx.destination
//     so previews don't get routed through the FX chain (which may have
//     muting or pre-roll latency we don't want during editing).
//
// State shape stored on the clip (persists in project save):
//   clip.notes[]  = [{ id, pitch, start, duration, velocity }]
//   We never store editor UI state (zoom, scroll, selection) on the clip.

// ── Scale definitions ──────────────────────────────────────────────────────
// Interval sets relative to root (semitones). The editor highlights rows
// whose pitch class is in the set; off-scale rows render dimmer but still
// accept note placement.
const BF_SCALES = {
  "off":            { name: "No Scale",       intervals: null },
  "major":          { name: "Major",          intervals: [0,2,4,5,7,9,11] },
  "minor":          { name: "Minor",          intervals: [0,2,3,5,7,8,10] },
  "harmonic-minor": { name: "Harmonic Minor", intervals: [0,2,3,5,7,8,11] },
  "dorian":         { name: "Dorian",         intervals: [0,2,3,5,7,9,10] },
  "phrygian":       { name: "Phrygian",       intervals: [0,1,3,5,7,8,10] },
  "mixolydian":     { name: "Mixolydian",     intervals: [0,2,4,5,7,9,10] },
  "pentatonic-minor": { name: "Pent. Minor",  intervals: [0,3,5,7,10] },
  "blues":          { name: "Blues",          intervals: [0,3,5,6,7,10] },
};
const BF_SCALE_ROOTS = ["C","C#","D","D#","E","F","F#","G","G#","A","A#","B"];

// Is pitch `p` in scale (root semitone, interval set)?
function isPitchInScale(p, rootSemi, intervals) {
  if (!intervals) return true;
  const rel = ((p - rootSemi) % 12 + 12) % 12;
  for (let i = 0; i < intervals.length; i++) {
    if (intervals[i] === rel) return true;
  }
  return false;
}

// Black-key check (for keyboard rendering)
function isBlackKey(pitch) {
  const pc = ((pitch % 12) + 12) % 12;
  return pc === 1 || pc === 3 || pc === 6 || pc === 8 || pc === 10;
}

// Snap divisions — denominator of a beat. 1 = quarter, 2 = 8th, 4 = 16th,
// 8 = 32nd, 0.5 = half. "Off" means no snap.
// Trimmed from the full DAW list to the 6 that 99% of mobile productions
// need: fine fill detail (1/32), the trap/hip-hop default (1/16), the
// melodic default (1/8), beat grid (1/4), longer phrases (1/2), and Off.
// Triplets, 1/64, and "1 bar" were removed to keep the menu compact.
const BF_SNAP_OPTIONS = [
  { id: "1/32",   beatDiv: 8,    label: "1/32" },
  { id: "1/16",   beatDiv: 4,    label: "1/16" },
  { id: "1/8",    beatDiv: 2,    label: "1/8"  },
  { id: "1/4",    beatDiv: 1,    label: "1/4"  },
  { id: "1/2",    beatDiv: 0.5,  label: "1/2"  },
  { id: "off",    beatDiv: 0,    label: "Off"  },
];

// Snap a time-in-seconds to the grid given snap config + bpm.
function snapTimeToGrid(t, beatDiv, beatSec) {
  if (!beatDiv || beatDiv <= 0) return t;
  const grid = beatSec / beatDiv;   // seconds per snap step
  return Math.round(t / grid) * grid;
}

// ── Chord catalog (Phase 3) ──
// Each chord is an array of semitone intervals from a root pitch. We keep
// the list small and producer-focused — major-family, minor-family, 7ths,
// suspended, plus 5 (power chord). Inversions are not in the catalog;
// users can drag individual notes to invert after stamping.
const BF_CHORDS = [
  { id: "maj",     name: "Maj",     intervals: [0, 4, 7]       },
  { id: "min",     name: "Min",     intervals: [0, 3, 7]       },
  { id: "maj7",    name: "Maj 7",   intervals: [0, 4, 7, 11]   },
  { id: "min7",    name: "Min 7",   intervals: [0, 3, 7, 10]   },
  { id: "dom7",    name: "Dom 7",   intervals: [0, 4, 7, 10]   },
  { id: "min9",    name: "Min 9",   intervals: [0, 3, 7, 10, 14] },
  { id: "maj9",    name: "Maj 9",   intervals: [0, 4, 7, 11, 14] },
  { id: "sus2",    name: "Sus 2",   intervals: [0, 2, 7]       },
  { id: "sus4",    name: "Sus 4",   intervals: [0, 5, 7]       },
  { id: "dim",     name: "Dim",     intervals: [0, 3, 6]       },
  { id: "aug",     name: "Aug",     intervals: [0, 4, 8]       },
  { id: "5",       name: "Power",   intervals: [0, 7]          },
];

// ═════════════════════════════════════════════════════════════════════════════
//  Compose system (Phase 5) — progression / melody / counter / arp generators
// ═════════════════════════════════════════════════════════════════════════════
//
// All generators are deterministic-seedable rule-based engines (no ML).
// Output: arrays of {pitch, start, duration, velocity} payloads — caller
// adds ids + commits via the editor's commitNotes for free undo.
//
// Vocabulary used throughout:
//   • degree  — scale-degree 1..7 (I = tonic). Roman numerals map to these.
//   • root    — the project key as a 0..11 pitch class (0 = C).
//   • chord   — { degree, quality, intervals } resolved at synthesis time.
//
// Genre presets describe progressions in Roman-numeral form so the same
// progression works in any key. Each row also tags the suitable scale
// (major / minor) so we render the right chord qualities.

// ── Chord qualities derived from a scale degree ──
// In any diatonic mode, each degree has a "natural" triad quality. We map
// degree → quality lookup tables for major and minor scales. Lowercase
// roman numerals = minor; uppercase = major. ° = diminished.
const _DEGREE_TO_INTERVAL_MAJ = [0, 2, 4, 5, 7, 9, 11];      // I ii iii IV V vi vii°
const _DEGREE_TO_INTERVAL_MIN = [0, 2, 3, 5, 7, 8, 10];      // i ii° III iv v VI VII

const _QUALITIES_MAJ = ["maj", "min", "min", "maj", "maj", "min", "dim"];
const _QUALITIES_MIN = ["min", "dim", "maj", "min", "min", "maj", "maj"];

// Roman numeral → 1-indexed degree. Handles I-VII (major) and i-vii (minor).
// Modifier suffixes:
//   "7"   → add the 7th (dominant if upper, minor-7 if lower)
//   "maj7"→ force major-7 (e.g. Imaj7)
//   "°"   → diminished
//   "9"   → add 9
const _ROMAN_LOOKUP = {
  "I":1,"i":1, "II":2,"ii":2, "III":3,"iii":3, "IV":4,"iv":4,
  "V":5,"v":5, "VI":6,"vi":6, "VII":7,"vii":7,
};
function _parseRoman(token) {
  // Strip trailing modifiers and identify them
  let core = token;
  let addType = null;    // null | "7" | "maj7" | "9" | "min7" | "sus2" | "sus4"
  let forceDim = false;
  // Order matters: check longer suffixes first.
  if (core.endsWith("maj7"))   { addType = "maj7"; core = core.slice(0, -4); }
  else if (core.endsWith("9")) { addType = "9";    core = core.slice(0, -1); }
  else if (core.endsWith("7")) { addType = "7";    core = core.slice(0, -1); }
  else if (core.endsWith("sus2")) { addType = "sus2"; core = core.slice(0, -4); }
  else if (core.endsWith("sus4")) { addType = "sus4"; core = core.slice(0, -4); }
  if (core.endsWith("°")) { forceDim = true; core = core.slice(0, -1); }
  const degree = _ROMAN_LOOKUP[core];
  if (!degree) return null;
  const isUpper = core[0] === core[0].toUpperCase();
  return { degree: degree, isUpper: isUpper, addType: addType, forceDim: forceDim };
}

// Resolve a roman-numeral string to an absolute chord:
//   { rootPitchClass: 0..11, intervals: [0, ...] }
// `scaleRoot` is the project key (0..11), `mode` is "major" | "minor".
function resolveRoman(token, scaleRoot, mode) {
  const parsed = _parseRoman(token);
  if (!parsed) return null;
  const degIdx = parsed.degree - 1;
  const degInterval = (mode === "minor" ? _DEGREE_TO_INTERVAL_MIN : _DEGREE_TO_INTERVAL_MAJ)[degIdx];
  const rootPc = (scaleRoot + degInterval + 144) % 12;
  // Determine quality: explicit overrides (forceDim, sus, casing) first,
  // then default to the scale-degree's natural quality.
  let intervals;
  if (parsed.forceDim) intervals = [0, 3, 6];
  else if (parsed.addType === "sus2") intervals = [0, 2, 7];
  else if (parsed.addType === "sus4") intervals = [0, 5, 7];
  else {
    // Use casing as quality hint: upper = major triad, lower = minor.
    // For a diatonic mode the natural quality is the safe default; casing
    // only LOCKS the quality so a producer can write iii in a major key
    // and get a minor chord at the right scale degree.
    const naturalQ = (mode === "minor" ? _QUALITIES_MIN : _QUALITIES_MAJ)[degIdx];
    const q = parsed.isUpper && naturalQ !== "dim" ? "maj"
            : !parsed.isUpper ? "min"
            : naturalQ;
    if      (q === "min") intervals = [0, 3, 7];
    else if (q === "dim") intervals = [0, 3, 6];
    else                  intervals = [0, 4, 7];
  }
  // Apply 7th / 9th additions
  if (parsed.addType === "maj7") intervals = intervals.concat([11]);
  else if (parsed.addType === "7") {
    // Dominant-7 on upper, minor-7 on lower (musical default).
    intervals = intervals.concat([parsed.isUpper ? 10 : 10]);
  } else if (parsed.addType === "9") {
    intervals = intervals.concat([10, 14]);
  }
  return { rootPitchClass: rootPc, intervals: intervals };
}

// ── Progression bank ────────────────────────────────────────────────────────
// Each progression is an array of roman-numeral tokens. `mode` controls
// chord-quality interpretation. 4 chords per row = 1 chord per bar by
// default — generators stretch/compress when the user picks more or fewer
// bars.
//
// Producer-friendly tagging: each row also carries a vibe label so the
// UI can show what the progression FEELS like, not just the chords.
const BF_PROGRESSIONS = {
  trap: {
    name: "Trap",
    color: "#A78BFA",
    description: "Dark minor · driving · modern",
    mode: "minor",
    progressions: [
      { id: "trap-1", label: "i · VI · III · VII",   chords: ["i", "VI", "III", "VII"] },
      { id: "trap-2", label: "i · VII · VI · v",     chords: ["i", "VII", "VI", "v"] },
      { id: "trap-3", label: "i · iv · v · VI",      chords: ["i", "iv", "v", "VI"] },
      { id: "trap-4", label: "i · v · iv · VII",     chords: ["i", "v", "iv", "VII"] },
      { id: "trap-5", label: "i · VI · iv · VII",    chords: ["i", "VI", "iv", "VII"] },
      { id: "trap-6", label: "i · III · VII · VI",   chords: ["i", "III", "VII", "VI"] },
    ],
  },
  drill: {
    name: "Drill",
    color: "#8B5CF6",
    description: "Brooding · UK-style · half-step movement",
    mode: "minor",
    progressions: [
      { id: "drill-1", label: "i · VI · v · iv",     chords: ["i", "VI", "v", "iv"] },
      { id: "drill-2", label: "i · iv · VI · v",     chords: ["i", "iv", "VI", "v"] },
      { id: "drill-3", label: "i · iv · v · i",      chords: ["i", "iv", "v", "i"] },
      { id: "drill-4", label: "i · VII · iv · v",    chords: ["i", "VII", "iv", "v"] },
      { id: "drill-5", label: "i · ii° · VI · v",    chords: ["i", "ii°", "VI", "v"] },
    ],
  },
  rnb: {
    name: "RnB",
    color: "#C4B5FD",
    description: "Lush · 7ths · emotional",
    mode: "major",
    progressions: [
      { id: "rnb-1", label: "Imaj7 · vi7 · iimaj7 · V7", chords: ["Imaj7", "vi7", "ii7", "V7"] },
      { id: "rnb-2", label: "ii7 · V7 · Imaj7 · vi7",    chords: ["ii7", "V7", "Imaj7", "vi7"] },
      { id: "rnb-3", label: "Imaj7 · iii7 · vi7 · IV",   chords: ["Imaj7", "iii7", "vi7", "IV"] },
      { id: "rnb-4", label: "vi7 · ii7 · V7 · I",        chords: ["vi7", "ii7", "V7", "Imaj7"] },
      { id: "rnb-5", label: "I · vi · IV · V",           chords: ["I", "vi", "IV", "V"] },
      { id: "rnb-6", label: "Imaj7 · IV · iii7 · vi7",   chords: ["Imaj7", "IV", "iii7", "vi7"] },
    ],
  },
  pop: {
    name: "Pop",
    color: "#F0C5A9",
    description: "Singable · bright · radio-ready",
    mode: "major",
    progressions: [
      { id: "pop-1", label: "I · V · vi · IV",       chords: ["I", "V", "vi", "IV"] },
      { id: "pop-2", label: "vi · IV · I · V",       chords: ["vi", "IV", "I", "V"] },
      { id: "pop-3", label: "I · vi · IV · V",       chords: ["I", "vi", "IV", "V"] },
      { id: "pop-4", label: "I · IV · V · I",        chords: ["I", "IV", "V", "I"] },
      { id: "pop-5", label: "I · iii · IV · V",      chords: ["I", "iii", "IV", "V"] },
      { id: "pop-6", label: "IV · I · V · vi",       chords: ["IV", "I", "V", "vi"] },
    ],
  },
  lofi: {
    name: "Lo-fi",
    color: "#7FB5AB",
    description: "Chill · jazzy 7ths · relaxed",
    mode: "major",
    progressions: [
      { id: "lofi-1", label: "Imaj7 · vi7 · ii7 · V7",  chords: ["Imaj7", "vi7", "ii7", "V7"] },
      { id: "lofi-2", label: "iii7 · vi7 · ii7 · V7",   chords: ["iii7", "vi7", "ii7", "V7"] },
      { id: "lofi-3", label: "Imaj7 · IVmaj7 · iii7 · vi7", chords: ["Imaj7", "IVmaj7", "iii7", "vi7"] },
      { id: "lofi-4", label: "ii7 · V7 · iii7 · vi7",   chords: ["ii7", "V7", "iii7", "vi7"] },
      { id: "lofi-5", label: "Imaj7 · iii7 · IVmaj7 · iv", chords: ["Imaj7", "iii7", "IVmaj7", "iv"] },
    ],
  },
  sad: {
    name: "Sad",
    color: "#9C8AC4",
    description: "Melancholy · descending · minor key",
    mode: "minor",
    progressions: [
      { id: "sad-1", label: "i · VII · VI · v",      chords: ["i", "VII", "VI", "v"] },
      { id: "sad-2", label: "i · iv · v · i",        chords: ["i", "iv", "v", "i"] },
      { id: "sad-3", label: "i · VI · III · v",      chords: ["i", "VI", "III", "v"] },
      { id: "sad-4", label: "vi · IV · I · V",       chords: ["vi", "IV", "I", "V"], mode: "major" },
      { id: "sad-5", label: "i · iv · VII · III",    chords: ["i", "iv", "VII", "III"] },
      { id: "sad-6", label: "i · v · iv · i",        chords: ["i", "v", "iv", "i"] },
    ],
  },
  pain: {
    name: "Pain",
    color: "#B45A8D",
    description: "Cyclical · storytelling · modern trap",
    mode: "minor",
    progressions: [
      // Core pain progressions Trelli requested
      { id: "pain-1", label: "i · VI · v",            chords: ["i", "VI", "v"] },
      { id: "pain-2", label: "i · VII · VI",          chords: ["i", "VII", "VI"] },
      { id: "pain-3", label: "i · iv",                chords: ["i", "iv"] },
      // Common adjacent variants — same mood, more options
      { id: "pain-4", label: "i · VI · VII · v",      chords: ["i", "VI", "VII", "v"] },
      { id: "pain-5", label: "i · iv · VI · VII",     chords: ["i", "iv", "VI", "VII"] },
      { id: "pain-6", label: "i · v · VI · iv",       chords: ["i", "v", "VI", "iv"] },
      { id: "pain-7", label: "i · VII · v · VI",      chords: ["i", "VII", "v", "VI"] },
      { id: "pain-8", label: "i · iv · v",            chords: ["i", "iv", "v"] },
    ],
  },
  melodic: {
    name: "Melodic",
    color: "#E4A0C8",
    description: "Emotional · sung-rap · NY/UK drill melodies",
    mode: "minor",
    progressions: [
      // Tjay / JI / A Boogie pocket — descending minor with strong VII pull
      { id: "mel-1",  label: "i · VI · III · VII",    chords: ["i", "VI", "III", "VII"] },
      { id: "mel-2",  label: "i · v · VI · iv",       chords: ["i", "v", "VI", "iv"] },
      { id: "mel-3",  label: "i · VII · VI · III",    chords: ["i", "VII", "VI", "III"] },
      // Hxncho / UK-drill melodic — half-step movement, VI as anchor
      { id: "mel-4",  label: "i · VI · iv · v",       chords: ["i", "VI", "iv", "v"] },
      { id: "mel-5",  label: "VI · VII · i · v",      chords: ["VI", "VII", "i", "v"] },
      { id: "mel-6",  label: "i · iv · v · VI",       chords: ["i", "iv", "v", "VI"] },
      // Drake-style emotional R&B-rap — borrowed major chords for contrast
      { id: "mel-7",  label: "vi · IV · I · V",       chords: ["vi", "IV", "I", "V"], mode: "major" },
      { id: "mel-8",  label: "I · V · vi · iii",      chords: ["I", "V", "vi", "iii"], mode: "major" },
      { id: "mel-9",  label: "vi · I · V · IV",       chords: ["vi", "I", "V", "IV"], mode: "major" },
      // Suspended / open feel — common in A Boogie & Tjay piano leads
      { id: "mel-10", label: "i · VII · III · VI",    chords: ["i", "VII", "III", "VI"] },
      { id: "mel-11", label: "i · III · VII · iv",    chords: ["i", "III", "VII", "iv"] },
      { id: "mel-12", label: "i · v · iv · VI",       chords: ["i", "v", "iv", "VI"] },
    ],
  },
  cinematic: {
    name: "Cinematic",
    color: "#D4B147",
    description: "Epic · suspended · film score",
    mode: "minor",
    progressions: [
      { id: "cin-1", label: "i · VI · III · VII",    chords: ["i", "VI", "III", "VII"] },
      { id: "cin-2", label: "i · VII · VI · VII",    chords: ["i", "VII", "VI", "VII"] },
      { id: "cin-3", label: "i · v · VI · iv",       chords: ["i", "v", "VI", "iv"] },
      { id: "cin-4", label: "i · III · VII · v",     chords: ["i", "III", "VII", "v"] },
      { id: "cin-5", label: "VI · VII · i · v",      chords: ["VI", "VII", "i", "v"] },
    ],
  },
};
// Ordered list for the UI (object key order isn't guaranteed across engines).
const BF_PROGRESSION_GENRES = ["trap", "drill", "rnb", "pop", "lofi", "sad", "pain", "melodic", "cinematic"];

// ── Generator: chord progression ────────────────────────────────────────────
// Stamps a progression across `bars` bars of clip time. Each chord lasts
// `bars / progression.chords.length` bars (so 4 chords across 4 bars =
// 1 chord per bar; 4 chords across 8 bars = 2 bars per chord).
//
// Args:
//   prog        — { chords:[romans], mode? } row from BF_PROGRESSIONS
//   genreMode   — fallback mode ("major"|"minor") if prog doesn't specify
//   scaleRoot   — project key 0..11
//   bars        — target length in bars
//   beatSec     — seconds per beat at current bpm
//   timeSigNum  — beats per bar (typically 4)
//   octave      — base octave (the I chord's root sits in this octave)
//
// Returns: array of {pitch, start, duration, velocity}
function generateProgression(prog, genreMode, scaleRoot, bars, beatSec, timeSigNum, octave) {
  const barSec = beatSec * (timeSigNum || 4);
  const chords = prog.chords || [];
  if (chords.length === 0) return [];
  const mode = prog.mode || genreMode || "minor";
  // Quantize chord changes to bar boundaries. Pick a chord length in
  // whole bars (default 1) and repeat the progression to fill `bars`.
  // If bars < chord count, fall back to fractional-bar duration so the
  // user still sees their requested chord count play out.
  let chordBars = 1;
  if (bars >= chords.length) {
    // Use the largest whole-bar duration that fits AT LEAST one full
    // cycle of the progression. Default to 1 bar per chord (most musical
    // for trap / pain). Producers can pick bars=6 for "2 bars per chord"
    // feel naturally — see below.
    chordBars = Math.max(1, Math.floor(bars / chords.length));
  }
  const chordDur = chordBars * barSec;
  const totalChords = Math.max(1, Math.floor(bars / chordBars));
  // Base MIDI pitch for the I chord root. octave 4 (= C4=60) is the
  // mid-keyboard default — sits comfortably above bass + below leads.
  const baseRootPitch = 12 * (octave + 1);   // octave 4 → MIDI 60
  const out = [];
  for (let ci = 0; ci < totalChords; ci++) {
    const roman = chords[ci % chords.length];
    const resolved = resolveRoman(roman, scaleRoot, mode);
    if (!resolved) continue;
    // Find the absolute root pitch for this chord — same octave as the I,
    // but shifted by the degree's interval. We don't transpose down to keep
    // the chords on top of each other; voice leading is left to the user.
    const rootDelta = (resolved.rootPitchClass - (scaleRoot % 12) + 12) % 12;
    const chordRoot = baseRootPitch + rootDelta;
    // Stamp each interval as a separate note at the same start/duration
    for (let i = 0; i < resolved.intervals.length; i++) {
      const p = chordRoot + resolved.intervals[i];
      if (p < 0 || p > 127) continue;
      out.push({
        pitch: p,
        start: ci * chordDur,
        duration: Math.max(0.05, chordDur * 0.98),
        // Velocity slightly humanised — root + 5th brighter than tensions
        velocity: 0.78 + (i === 0 ? 0.10 : 0) + (Math.random() - 0.5) * 0.04,
      });
    }
  }
  return out;
}

// ── Generator: melody ───────────────────────────────────────────────────────
// Markov-ish melody generator. Constraints:
//   • Stays inside the chosen scale (root + intervals from BF_SCALES)
//   • Prefers small intervals (1-3 semitones); rarely leaps a 5th+
//   • Rhythm sampled from a small library of common 1-bar patterns
//   • "Density" controls average notes per bar
//   • "Mood" picks weight tables: e.g. Dark biases toward minor 2nds, Bright
//     biases toward major 3rds and 6ths.
//
// Returns: array of {pitch, start, duration, velocity}
const BF_MELODY_MOODS = [
  { id: "bright",     name: "Bright",     description: "Major intervals · uplifting" },
  { id: "dark",       name: "Dark",       description: "Minor 2nds · minor 3rds · brooding" },
  { id: "emotional",  name: "Emotional",  description: "Wider leaps · expressive" },
  { id: "aggressive", name: "Aggressive", description: "Faster rhythm · accented" },
];
const BF_MELODY_DENSITIES = [
  { id: "sparse", label: "Sparse", notesPerBar: 4 },
  { id: "medium", label: "Medium", notesPerBar: 7 },
  { id: "busy",   label: "Busy",   notesPerBar: 12 },
];

// Build the in-scale pitch ladder centered on a base pitch ± half-octave
// either side. We return all in-scale MIDI pitches in a useful range.
function _scalePitchesInRange(scaleRoot, intervals, lowPitch, highPitch) {
  if (!intervals) {
    // No scale → use chromatic
    const all = [];
    for (let p = lowPitch; p <= highPitch; p++) all.push(p);
    return all;
  }
  const set = new Set(intervals);
  const out = [];
  for (let p = lowPitch; p <= highPitch; p++) {
    const pc = ((p - scaleRoot) % 12 + 12) % 12;
    if (set.has(pc)) out.push(p);
  }
  return out;
}

function generateMelody(scaleRoot, scaleIntervals, mood, density, bars, beatSec, timeSigNum, octave) {
  const barSec = beatSec * (timeSigNum || 4);
  const totalBeats = bars * (timeSigNum || 4);
  // Pitch range: ±10 semitones around base octave
  const basePitch = 12 * (octave + 1) + 7;  // octave 4 + a 5th = G4 default
  const lowP  = basePitch - 7;
  const highP = basePitch + 12;
  const ladder = _scalePitchesInRange(scaleRoot, scaleIntervals, lowP, highP);
  if (ladder.length === 0) return [];
  // Find nearest-to-base index in the ladder — our starting point.
  let curIdx = 0;
  for (let i = 0; i < ladder.length; i++) {
    if (Math.abs(ladder[i] - basePitch) < Math.abs(ladder[curIdx] - basePitch)) curIdx = i;
  }

  // Mood → interval-step weights (probability of jumping +/- N ladder steps)
  // Index = absolute step count (0..7); value = relative weight.
  let stepWeights;
  if (mood === "bright")     stepWeights = [3, 8, 6, 4, 2, 2, 1, 1];   // small + thirds + sixths
  else if (mood === "dark")  stepWeights = [4, 10, 5, 2, 1, 1, 0.5, 0.5]; // very small steps
  else if (mood === "emotional") stepWeights = [2, 5, 5, 4, 4, 3, 2, 1]; // wider leaps
  else if (mood === "aggressive") stepWeights = [3, 7, 6, 5, 3, 2, 2, 1];
  else stepWeights = [3, 7, 5, 3, 2, 2, 1, 1];

  // Rhythm patterns — beats per note as fractions of a beat. Each
  // pattern tiles to fill exactly 1 bar (sums to timeSigNum beats).
  // We pick patterns based on density.
  const rhythmsSparse = [
    [1, 1, 2],          // 4 beats over 3 notes
    [2, 2],             // half notes
    [1, 1, 1, 1],       // 4 quarters
  ];
  const rhythmsMedium = [
    [0.5, 0.5, 1, 1, 1],
    [1, 0.5, 0.5, 1, 1],
    [0.5, 0.5, 0.5, 0.5, 1, 1],
    [1, 1, 0.5, 0.5, 1],
  ];
  const rhythmsBusy = [
    [0.5, 0.5, 0.5, 0.5, 0.5, 0.5, 0.5, 0.5],
    [0.25, 0.25, 0.5, 0.5, 0.5, 0.5, 0.5, 0.5, 0.5],
    [0.5, 0.25, 0.25, 0.5, 0.5, 0.5, 0.5, 0.5, 0.5],
  ];
  const rhythmPool = density === "sparse" ? rhythmsSparse
                   : density === "busy"   ? rhythmsBusy
                   : rhythmsMedium;

  const _pickWeighted = function(weights) {
    let total = 0;
    for (let i = 0; i < weights.length; i++) total += weights[i];
    let r = Math.random() * total;
    for (let i = 0; i < weights.length; i++) {
      r -= weights[i];
      if (r <= 0) return i;
    }
    return weights.length - 1;
  };

  const out = [];
  let cursorBeat = 0;
  // For each bar, sample a rhythm and emit notes
  for (let b = 0; b < bars; b++) {
    const rhythm = rhythmPool[Math.floor(Math.random() * rhythmPool.length)];
    // Tile / truncate rhythm to fit one bar (timeSigNum beats)
    let rhythmBeats = 0;
    for (let i = 0; i < rhythm.length; i++) rhythmBeats += rhythm[i];
    let scale = (timeSigNum || 4) / Math.max(0.001, rhythmBeats);
    for (let i = 0; i < rhythm.length; i++) {
      const noteBeats = rhythm[i] * scale;
      // Step on the ladder
      const stepSize = _pickWeighted(stepWeights);
      const direction = Math.random() < 0.5 ? -1 : 1;
      curIdx = Math.max(0, Math.min(ladder.length - 1, curIdx + direction * stepSize));
      // Pull toward center occasionally to prevent ladder runaway
      const center = Math.floor(ladder.length / 2);
      if (Math.random() < 0.15) {
        curIdx = curIdx > center ? curIdx - 1 : curIdx + 1;
      }
      const pitch = ladder[curIdx];
      // Velocity has a downbeat accent
      const isDownbeat = (Math.round(cursorBeat) === cursorBeat) && (Math.round(cursorBeat) % 2 === 0);
      const vel = (isDownbeat ? 0.88 : 0.74) + (Math.random() - 0.5) * 0.10;
      out.push({
        pitch: pitch,
        start: cursorBeat * beatSec,
        duration: Math.max(0.06, noteBeats * beatSec * 0.95),
        velocity: Math.max(0.3, Math.min(1, vel)),
      });
      cursorBeat += noteBeats;
    }
  }
  return out;
}

// ── Generator: counter-melody ───────────────────────────────────────────────
// Takes the existing notes as the "source" line. For each source note,
// emits a counter note at the same time (and roughly same duration) on
// a complementary pitch:
//   • Avoid unison and minor 2nds (clash)
//   • Prefer 3rds, 5ths, 6ths above (consonant)
//   • Stay in scale
//   • Occasional octave displacement for variety
function generateCounterMelody(sourceNotes, scaleRoot, scaleIntervals) {
  if (!sourceNotes || sourceNotes.length === 0) return [];
  // Sort by start so we walk left-to-right consistently
  const src = sourceNotes.slice().sort(function(a,b){ return a.start - b.start; });
  const lowP = 24, highP = 108;
  const ladder = _scalePitchesInRange(scaleRoot, scaleIntervals, lowP, highP);
  if (ladder.length === 0) return [];
  // Preferred interval offsets in semitones from the source pitch.
  // We try each in order and pick the FIRST one that lands on an in-scale
  // pitch within range and isn't a clash with the source.
  const offsets = [4, 7, 9, 12, 5, 3, -3, -5, -7, -8];  // 3rd, 5th, 6th, 8ve, 4th, m3, etc.
  const out = [];
  for (let i = 0; i < src.length; i++) {
    const s = src[i];
    let chosen = null;
    for (let k = 0; k < offsets.length; k++) {
      const cand = s.pitch + offsets[k];
      if (cand < lowP || cand > highP) continue;
      // Must be in scale
      if (scaleIntervals) {
        const pc = ((cand - scaleRoot) % 12 + 12) % 12;
        if (scaleIntervals.indexOf(pc) < 0) continue;
      }
      // Avoid minor 2nds (1 semitone) - safety check though our offsets exclude them
      if (Math.abs(cand - s.pitch) === 1) continue;
      chosen = cand;
      break;
    }
    if (chosen == null) continue;
    out.push({
      pitch: chosen,
      start: s.start,
      duration: s.duration,
      // Counter line slightly softer so the source line stays foreground
      velocity: Math.max(0.3, Math.min(1, (s.velocity || 0.8) * 0.85)),
    });
  }
  return out;
}

// ── Generator: arp from selection ──────────────────────────────────────────
// Takes a set of pitches (typically a held chord) and re-arranges them
// across time as an arpeggio. Different from Phase 3's arpeggiateSelection:
//   • Here we keep emitting until we fill `bars` bars (loop-style)
//   • Rate can be set independently of the editor's snap
//   • Patterns: up, down, updown, downup, random, trap-roll (retrigger
//     same note quickly at the start of each beat)
function generateArpFromSelection(sourceNotes, pattern, rateBeatDiv, octaveSpan, bars, beatSec, timeSigNum, opts) {
  if (!sourceNotes || sourceNotes.length === 0) return [];
  opts = opts || {};
  // Unique pitches in source
  const seen = new Set();
  const pitches = [];
  for (let i = 0; i < sourceNotes.length; i++) {
    if (!seen.has(sourceNotes[i].pitch)) {
      seen.add(sourceNotes[i].pitch);
      pitches.push(sourceNotes[i].pitch);
    }
  }
  if (pitches.length === 0) return [];
  pitches.sort(function(a,b){ return a - b; });
  // Expand octaves if asked
  let pool = pitches.slice();
  for (let o = 1; o < octaveSpan; o++) {
    for (let i = 0; i < pitches.length; i++) {
      const p = pitches[i] + 12 * o;
      if (p <= 127) pool.push(p);
    }
  }
  pool = pool.filter(function(p){ return p >= 0 && p <= 127; });
  // Build sequence based on pattern
  let sequence;
  if (pattern === "down") sequence = pool.slice().reverse();
  else if (pattern === "updown") {
    const ups = pool;
    const downs = pool.slice().reverse().slice(1, -1);   // no double-tap of endpoints
    sequence = ups.concat(downs);
  } else if (pattern === "downup") {
    const downs = pool.slice().reverse();
    const ups = pool.slice(1, -1);
    sequence = downs.concat(ups);
  } else if (pattern === "random") {
    sequence = pool.slice();
  } else if (pattern === "trap-roll") {
    // Trap roll: same lowest note retriggered repeatedly with occasional
    // jumps to other pool pitches.
    sequence = pool.slice();
  } else {
    sequence = pool.slice();  // "up" default
  }
  const stepDur = beatSec / Math.max(1, rateBeatDiv);
  const useSelRange = (opts.timeRangeDuration != null && opts.timeRangeDuration > 0);
  const rangeStart  = useSelRange ? (opts.timeRangeStart || 0) : 0;
  const totalDur    = useSelRange ? opts.timeRangeDuration : (bars * beatSec * (timeSigNum || 4));
  const cursorEnd   = rangeStart + totalDur;
  const out = [];
  let i = 0;
  let cursor = rangeStart;
  while (cursor + 0.02 < cursorEnd) {
    let pitch;
    if (pattern === "random") {
      pitch = sequence[Math.floor(Math.random() * sequence.length)];
    } else if (pattern === "trap-roll") {
      // 70% root, 30% other pool pitch
      pitch = Math.random() < 0.7 ? pool[0] : sequence[i % sequence.length];
    } else {
      pitch = sequence[i % sequence.length];
    }
    out.push({
      pitch: pitch,
      start: cursor,
      duration: Math.max(0.04, stepDur * 0.92),
      velocity: 0.78 + (Math.random() - 0.5) * 0.10,
    });
    cursor += stepDur;
    i++;
  }
  return out;
}

// Build a fresh note id. Crypto.randomUUID would be cleaner but isn't
// available in all webviews; this is good enough for in-memory uniqueness.
function newNoteId() {
  return "n_" + Date.now().toString(36) + "_" + Math.floor(Math.random()*1e6).toString(36);
}

// ── MidiEditorOverlay component ────────────────────────────────────────────
//
// Props:
//   clipId         — the MIDI clip currently being edited
//   tracks         — full tracks array (to find the clip)
//   setTracks      — setter for immutable track updates
//   onClose        — close handler (clears editor state)
//   bpm            — project BPM
//   timeSigNum     — time-signature numerator (typically 4)
//   currentTime    — master transport time (for playhead)
//   isPlaying      — whether transport is running
//   actx           — live AudioContext (for note previews)
//   masterDest     — node to route previews into (typically the master gain)
// ── Piano-roll toolbar icons (inline SVGs, no emoji dependency) ─────────────
// Single thin-stroke style across all icons — keeps the toolbar visually
// cohesive and reads as native UI rather than text glyphs. 14px viewBox
// rendered at whatever size the consumer asks for (default 14). Currents
// stroke/fill so the button color drives the icon color.
function BFIcon({ name, size }) {
  const s = size || 14;
  const common = {
    width: s, height: s,
    viewBox: "0 0 14 14",
    fill: "none",
    stroke: "currentColor",
    strokeWidth: 1.5,
    strokeLinecap: "round",
    strokeLinejoin: "round",
    style: { display: "block" },
  };
  switch (name) {
    case "pencil":
      return (
        <svg {...common}>
          <path d="M2 12 L4 10 L10.5 3.5 L11 3 L11.5 3.5 L10.5 4.5" />
          <path d="M2 12 L2 11 L3 11 L4 10" />
        </svg>
      );
    case "select":
      return (
        <svg {...common}>
          <rect x="2.5" y="3.5" width="9" height="7" rx="0.5" strokeDasharray="1.6 1.4" />
        </svg>
      );
    case "eraser":
      // Backspace-style left chevron with X inside
      return (
        <svg {...common}>
          <path d="M5 3 L2 7 L5 11 L12 11 L12 3 Z" />
          <path d="M7 5.5 L10 8.5 M10 5.5 L7 8.5" />
        </svg>
      );
    case "scissors":
      // Classic two-loop scissors
      return (
        <svg {...common}>
          <circle cx="4" cy="10.5" r="1.8" />
          <circle cx="10" cy="10.5" r="1.8" />
          <path d="M5.5 9 L12 2.5" />
          <path d="M8.5 9 L2 2.5" />
        </svg>
      );
    case "wave":
      // Waveform — for the sampler re-edit affordance
      return (
        <svg {...common}>
          <path d="M1 7 L3 7 L3 4 L3 10 L5 10 L5 2 L5 12 L7 12 L7 5 L7 9 L9 9 L9 3 L9 11 L11 11 L11 6 L11 8 L13 8" />
        </svg>
      );
    case "play":
      return (
        <svg {...common} fill="currentColor" stroke="none">
          <path d="M3.5 2.5 L11.5 7 L3.5 11.5 Z" />
        </svg>
      );
    case "stop":
      return (
        <svg {...common} fill="currentColor" stroke="none">
          <rect x="3" y="3" width="8" height="8" rx="0.7" />
        </svg>
      );
    case "rewindStart":
      // ⏮ — vertical bar + double-triangle pointing left
      return (
        <svg {...common} fill="currentColor" stroke="none">
          <rect x="2.2" y="3" width="1.4" height="8" />
          <path d="M12 3 L5.5 7 L12 11 Z" />
        </svg>
      );
    case "skipEnd":
      // ⏭ — double-triangle right + vertical bar
      return (
        <svg {...common} fill="currentColor" stroke="none">
          <path d="M2 3 L8.5 7 L2 11 Z" />
          <rect x="10.4" y="3" width="1.4" height="8" />
        </svg>
      );
    case "backBar":
      // ⏪ — double left-triangle
      return (
        <svg {...common} fill="currentColor" stroke="none">
          <path d="M7 3 L1.5 7 L7 11 Z" />
          <path d="M13 3 L7.5 7 L13 11 Z" />
        </svg>
      );
    case "fwdBar":
      // ⏩ — double right-triangle
      return (
        <svg {...common} fill="currentColor" stroke="none">
          <path d="M1 3 L6.5 7 L1 11 Z" />
          <path d="M7 3 L12.5 7 L7 11 Z" />
        </svg>
      );
    case "undo":
      // Curved arrow returning left
      return (
        <svg {...common}>
          <path d="M4 4 L1.5 6.5 L4 9" />
          <path d="M1.5 6.5 L8 6.5 A 3 3 0 0 1 11 9.5 L11 11" />
        </svg>
      );
    case "redo":
      // Mirror of undo
      return (
        <svg {...common}>
          <path d="M10 4 L12.5 6.5 L10 9" />
          <path d="M12.5 6.5 L6 6.5 A 3 3 0 0 0 3 9.5 L3 11" />
        </svg>
      );
    case "close":
      return (
        <svg {...common}>
          <path d="M3 3 L11 11 M11 3 L3 11" />
        </svg>
      );
    case "piano":
      // Mini piano keys
      return (
        <svg {...common} fill="none" stroke="currentColor" strokeWidth={1}>
          <rect x="1.5" y="3.5" width="11" height="7" rx="0.5" />
          <path d="M4.5 3.5 L4.5 7.5 M7 3.5 L7 7.5 M9.5 3.5 L9.5 7.5" />
        </svg>
      );
    case "sliders":
      // Two horizontal faders with knobs — represents sound/envelope controls
      return (
        <svg {...common}>
          <path d="M1.5 4.5 L12.5 4.5" />
          <path d="M1.5 9.5 L12.5 9.5" />
          <circle cx="5" cy="4.5" r="1.6" fill="currentColor" stroke="none" />
          <circle cx="9" cy="9.5" r="1.6" fill="currentColor" stroke="none" />
        </svg>
      );
    default:
      return null;
  }
}


function MidiEditorOverlay(props) {
  const { clipId, tracks, setTracks, onClose, bpm, timeSigNum, currentTime, isPlaying, actx, masterDest, getActx, getOrCreateMaster, onOpenPresetBrowser, onOpenEditSound, getOrBuildPreviewChain, onTransportPlay, onTransportSeek, drumChannel,
    // Optional PAT-mode delegation. Parent can provide its own pattern
    // scheduler (drum-channel mode uses the rack PAT scheduler so the
    // entire pattern plays, not just the editor's notes).
    onLocalPlay, onLocalStop, localPlayingExternal,
    // Optional preview override. When set, key-tap audition calls this
    // instead of the default soundfont/sampler preview path. Used in
    // drum-channel mode so taps play the kit pad sample at the right
    // pitch relative to root (DRUM_CHANNEL_ROOT_PITCH = C4).
    previewPitchOverride,
    // Optional PAT-playhead source. When set, the editor renders its
    // PAT playhead beam at this position (seconds within the pattern)
    // instead of its internal localPlayheadSec. Drum-channel mode feeds
    // rackPatPlayheadSec here so the playhead moves while the rack's
    // PAT scheduler loops.
    localPlayheadSecExternal,
    // Optional PAT seek hook. When the user taps the ruler, this is
    // called with the snapped pattern-local seconds so the parent can
    // reposition its scheduler. Drum-channel mode wires this to
    // rackPatSeek which re-anchors the PAT scheduler in place.
    onPatSeek,
    // Optional Cut-Itself toggle hook. Drum-channel mode wires this so
    // the toggle button in the editor toolbar flips chokeEnabled on the
    // pad's drum channel — producers can enable note-cut without
    // bouncing back to the rack's row tray.
    onToggleCutItself,
  } = props;

  // ── Drum-channel mode resolution ──
  // When the editor is opened from the drum rack (a pad "sent to piano roll"),
  // the standard track+clip lookup doesn't apply. We synthesize a track + clip
  // from the drum pattern's channel so the rest of the editor reads/writes
  // notes through its normal data path. commitNotes is overridden below to
  // write back to pattern.channels[i].notes instead of clip.notes.
  let track = null;
  let clip = null;
  let drumCtx = null;   // { drumTrack, pattern, channelIdx, channel, padInst }
  if (drumChannel) {
    const dt = tracks.find(function(t){ return t.id === drumChannel.trackId; });
    if (dt && dt.type === "drumSequencer") {
      const pat = (dt.patterns || []).find(function(p){ return p.id === drumChannel.patternId; });
      if (pat) {
        const ci = drumChannel.channelIdx;
        const ch = pat.channels && pat.channels[ci];
        if (ch) {
          const kitMeta = (typeof getKitMeta === "function") ? getKitMeta(dt.kitId || "default") : null;
          const pad = (kitMeta && kitMeta.pads) ? kitMeta.pads[ci] : null;
          const stepSec = drumStepDurationSec(bpm, pat.stepsPerBeat || 4);
          const patDur = pat.lengthSteps * stepSec;
          // Synthetic clip — pattern-local; notes live on the channel
          clip = {
            id: "__drumchan__" + dt.id + "__" + pat.id + "__" + ci,
            startTime: 0,
            duration: patDur,
            notes: ch.notes || [],
          };
          // Synthetic track — sampler-v1 inst pointing at the pad's sample.
          // The editor's audition path will play the pad sample on key tap.
          // Engine string + samplerData fields chosen to match what
          // resolveSamplerInst expects.
          track = {
            id: dt.id + "__chan_" + ci,
            type: "midi",
            name: drumChannelDisplayName(ch, pad) || "Pad",
            samplerData: pad ? {
              sampleUrl: pad.sampleUrl,
              sampleHash: pad.sampleUrl || pad.id,   // hash falls back to URL for the cache key
              sampleName: pad.name || "Pad",
              pitchSemitones: 0,
              pitchCents: 0,
              slicePoints: [0],
              playbackRate: 1,
            } : null,
            clips: [clip],
          };
          drumCtx = {
            drumTrack: dt,
            pattern: pat,
            channelIdx: ci,
            channel: ch,
            padInst: pad,
          };
        }
      }
    }
    // If drumChannel was set but resolution failed (pattern deleted?), the
    // shared "if (!clip)" effect below closes the overlay cleanly. Don't
    // call useEffect here — that'd be a conditional hook (violates RoH).
  } else {
    // ── Standard clip-from-track resolution (existing path) ──
    // Find clip + parent track. Re-resolved every render so the editor sees
    // edits we make to the track via setTracks (the editor is the source of
    // truth for notes, but other components might mutate concurrently).
    for (let ti = 0; ti < tracks.length; ti++) {
      const tr = tracks[ti];
      if (tr.type !== "midi") continue;
      for (let ci = 0; ci < (tr.clips || []).length; ci++) {
        if (tr.clips[ci].id === clipId) {
          track = tr; clip = tr.clips[ci]; break;
        }
      }
      if (clip) break;
    }
  }
  // If the clip went away (deleted under us), close cleanly on next tick.
  React.useEffect(function(){
    if (!clip) {
      const t = setTimeout(function(){ onClose && onClose(); }, 0);
      return function(){ clearTimeout(t); };
    }
  }, [!!clip]);
  if (!clip || !track) return null;

  // Resolve the preset for this track and merge per-track overrides
  // (ADSR/filter tweaks). All preview tones from this editor use the
  // merged preset, so dragging a filter cutoff slider in the Edit Sound
  // panel immediately changes what the user hears on their next tap.
  // SAMPLER tracks resolve through a different path (samplerData on the
  // track, not BF_INSTRUMENTS).
  let baseInst, inst;
  if (track.samplerData) {
    inst = resolveSamplerInst(track);
    baseInst = inst;
  } else {
    baseInst = resolveTrackInstrumentMeta(track);
    inst = applyInstrumentOverrides(baseInst, track.instrumentOverrides);
  }
  const trackColor = track.color || "#A78BFA";
  const notes = clip.notes || [];
  const clipDur = clip.duration || 4;
  const beatSec = 60 / (bpm || 120);
  const barSec  = beatSec * (timeSigNum || 4);

  // ── Editor state ──
  // Zoom: pxPerSec (X) and rowHeight (Y). FL Studio-style — pinch to zoom,
  // no fixed default. We compute reasonable starting values so the clip
  // roughly fills the screen at open.
  const initialPxPerSec = React.useMemo(function(){
    // Aim for the clip to span ~ 70vw at open. Window may not be ready
    // server-side, so guard.
    if (typeof window === "undefined") return 80;
    return Math.max(40, Math.min(400, (window.innerWidth * 0.7) / Math.max(0.5, clipDur)));
  }, [clipDur]);
  const [pxPerSec, setPxPerSec] = React.useState(initialPxPerSec);
  const [rowHeight, setRowHeight] = React.useState(18);  // px per pitch row
  const [scrollX, setScrollX] = React.useState(0);
  const [scrollY, setScrollY] = React.useState(function(){
    // Default scroll so the existing notes are centered in the viewport.
    // If the clip has notes, take the median pitch and center on it. Empty
    // clip falls back to C4 (the conventional starting point for melodic
    // composition). Pitch axis is inverted (high pitches at top), so
    // scrollY = (127 - centerPitch - rowsVisible/2) * rowHeight.
    const rowsViewport = 18;  // estimate; actual viewport height not known yet
    const initialRowH = 18;
    let centerPitch = 60;  // C4 fallback
    const existingNotes = (clip && clip.notes) || [];
    if (existingNotes.length > 0) {
      // Use the median of existing notes' pitches. Median is more robust
      // than mean to a single outlier (e.g. one note placed at C7 plus
      // ten notes around C5 → median still picks C5, not C5.5).
      const pitches = existingNotes.map(function(n){ return n.pitch; }).sort(function(a, b){ return a - b; });
      centerPitch = pitches[Math.floor(pitches.length / 2)];
    } else if (track && track.samplerData && track.samplerData.rootPitch != null) {
      // For a fresh sampler clip with no notes yet, center on the
      // sampler's root pitch so the producer sees the playable keys.
      centerPitch = track.samplerData.rootPitch;
    }
    return Math.max(0, (127 - centerPitch - rowsViewport / 2)) * initialRowH;
  });

  // ── Selection (multi) ──
  // selNoteIds is the source of truth for *all* selection (one or many).
  // We use a JS Set for cheap has() / add() / delete() during lasso loops.
  // setSelNoteIds always passes a NEW Set so React detects the change.
  const [selNoteIds, setSelNoteIds] = React.useState(function(){ return new Set(); });
  // Action-bar category menu state. The selection action bar groups
  // related actions into four category dropdowns (Pitch, Time, Vel, FX)
  // so users can find what they need by intent rather than scanning ~20
  // raw buttons. Only one menu is open at a time. The dropdown is rendered
  // via position:fixed anchored above the triggering button — the action
  // bar lives at the bottom of the editor so menus must open upward.
  const [selActionMenu, setSelActionMenu] = React.useState(null); // "pitch"|"time"|"vel"|"fx"|null
  const selActionAnchorRef = React.useRef({ left: 0, bottom: 0 });
  // Convenience: most code only cares about the first / only id. Derived
  // each render rather than maintained as separate state to avoid drift.
  const selNoteId = (function(){
    if (selNoteIds.size === 0) return null;
    if (selNoteIds.size === 1) return selNoteIds.values().next().value;
    return null;  // "no single selection"
  })();
  // Selection helpers — kept compact so the touch handlers stay readable.
  const selectOnly = function(id){
    const s = new Set(); if (id) s.add(id);
    setSelNoteIds(s);
  };
  const selectAddToggle = function(id){
    setSelNoteIds(function(prev){
      const next = new Set(prev);
      if (next.has(id)) next.delete(id); else next.add(id);
      return next;
    });
  };
  const selectClear = function(){ setSelNoteIds(new Set()); };
  const selectAll = function(){
    const s = new Set();
    for (let i = 0; i < notes.length; i++) s.add(notes[i].id);
    setSelNoteIds(s);
  };

  // ── Pattern vs Song transport coordination ──
  // When the user starts the SONG transport (main timeline), kill any
  // pattern playback that's running in this editor so they don't fight
  // for the audio output. The reverse case (song running while user
  // starts pattern) is handled inline — pressing PAT stops the song
  // explicitly via toggleLocalPlay → localPlay path doesn't touch
  // timeline state, so we add a guard here.
  const prevIsPlayingRef = React.useRef(isPlaying);
  React.useEffect(function(){
    if (isPlaying && !prevIsPlayingRef.current) {
      // Song transport just started → stop pattern playback.
      try { if (localPlaying) localStop(); } catch(_){}
    }
    prevIsPlayingRef.current = isPlaying;
  }, [isPlaying]);

  // ── Last-touched-note duration tracker ──
  // FL Studio behaviour: tapping an existing note "copies" its size, so
  // the next note placed inherits that duration. Speeds up writing
  // patterns where many notes share the same length. This ref is
  // updated wherever a note gets touched/selected/dragged, and read by
  // addNoteAt when no explicit durSec is supplied.
  const lastTouchedDurRef = React.useRef(null);

  // ── Tool mode ──
  // Pencil:  tap empty = place; tap note = select; drag note = move; drag
  //          right edge = resize; long-press = delete (legacy).
  // Select:  tap empty = clear; drag empty = lasso rectangle; tap note =
  //          toggle in selection; drag selected note = move all selected.
  // Eraser:  tap any note = delete; drag through notes also deletes.
  // Slice:   tap a note = split at that point; tapping empty does nothing.
  const [toolMode, setToolMode] = React.useState("pencil");

  // ── Clipboard for cut/copy/paste ──
  // Holds an array of normalized note payloads (no ids) plus the minimum
  // start time so paste re-anchors to current playhead position.
  // Initialized from the module-level clipboard so cross-editor pastes
  // work — copy in editor A, close, open editor B, the paste button is
  // already enabled with A's content.
  const [clipboard, setClipboard] = React.useState(function(){ return bfGetNotesClipboard(); });
  // Shape: { notes: [{pitch,start,duration,velocity}...], minStart: number }

  // ── Chord-stamp arming ──
  // When non-null, the next tap on a cell stamps this chord rooted at the
  // tapped pitch, then disarms. UI shows an "armed" badge on the chord
  // button + cell highlights as user moves finger before placing.
  const [chordArmed, setChordArmed] = React.useState(null);
  // Shape: { id: "maj7", intervals: [0,4,7,11], name: "Maj 7" }
  const [chordPickerOpen, setChordPickerOpen] = React.useState(false);
  // ── "More" panel ──
  // On phones in portrait, the toolbar can't fit Snap / Scale / Bars /
  // Loop / Select-all all at once. We keep the four tools + chord +
  // undo/redo/close visible at all times, and tuck the secondary controls
  // behind a ⋯ button that opens a dropdown panel.
  const [morePanelOpen, setMorePanelOpen] = React.useState(false);
  // ── Phase 5: Compose sheet (in-editor) ──
  // Generator panel state lives inside the editor so it can read scale,
  // root, bpm directly. When non-null/true the sheet is shown.
  const [composeOpen, setComposeOpen] = React.useState(false);

  // ── Editor-local playback (mini-transport) ──
  // The editor has its own play/pause for previewing the clip in isolation
  // without touching the master transport. When playing, we schedule the
  // clip's notes against the live AudioContext and track active voices so
  // we can stop them on pause. A local playhead advances independently
  // of the master and loops back to 0 when the clip ends (so the user can
  // listen to a loop while editing).
  const [localPlaying, setLocalPlaying] = React.useState(false);
  // When parent delegates PAT play (drum-channel mode uses the rack PAT
  // scheduler), prefer the parent's playing state for UI rendering.
  const isPatPlaying = (localPlayingExternal != null) ? !!localPlayingExternal : localPlaying;
  // Bank-preset sub-sheet — opens when the user taps the bank-preset
  // button in the toolbar. Shows the full list of (bank, program) pairs
  // from the track's multi-preset bank so they can switch sound without
  // changing the underlying track instrument. Notes stay the same.
  const [bankPresetSheetOpen, setBankPresetSheetOpen] = React.useState(false);
  // Re-render trigger when the soundfont engine populates the parsed
  // preset list for the currently-loaded bank. Without this, the
  // toolbar's bank-preset button would stay hidden for legacy banks
  // (uploaded before the backend stored a presets list) until the next
  // user action forced a re-render. The engine notifies subscribers
  // after PHDR parse completes; we bump a counter on each notify.
  const [_sfTick, _setSfTick] = React.useState(0);
  React.useEffect(function(){
    // Subscribe to every active engine — singleton plus all per-track
    // instances — so the UI re-renders when ANY engine state changes
    // (preset loaded, PHDR parse complete, etc.). The bump function is
    // idempotent so multiple-engine notify is harmless.
    const unsubs = [];
    const bump = function(){ _setSfTick(function(x){ return x + 1; }); };
    try {
      if (typeof forEachBFSoundfontEngine === "function") {
        forEachBFSoundfontEngine(function(eng){
          if (eng && typeof eng.subscribe === "function") {
            try { unsubs.push(eng.subscribe(bump)); } catch(_){}
          }
        });
      } else {
        // Fallback: subscribe only to the singleton
        const eng = (typeof getBFSoundfontEngine === "function") ? getBFSoundfontEngine() : null;
        if (eng && typeof eng.subscribe === "function") {
          try { unsubs.push(eng.subscribe(bump)); } catch(_){}
        }
      }
    } catch(_){}
    return function(){
      unsubs.forEach(function(u){ try { u && u(); } catch(_){} });
    };
    // eslint-disable-next-line
  }, []);

  // Pre-warm the soundfont for this track on mount. Triggers the engine
  // to fetch + PHDR-parse the file even before the user taps a key.
  // Two payoffs:
  //   1. First tap has zero latency (no fetch + decode delay)
  //   2. The PHDR parse populates BF_INSTRUMENTS[id].presets for legacy
  //      banks (uploaded before the backend stored presets), so the
  //      bank-preset toolbar button can render right away.
  // Fire-and-forget — failures get logged in the engine's diag panel,
  // they don't block the editor.
  React.useEffect(function(){
    if (!track || track.samplerData) return;
    const m = (typeof getInstrumentMeta === "function") ? getInstrumentMeta(track.instrumentId || "piano-grand") : null;
    if (!m || m.engine !== "soundfont-v1") return;
    try {
      let ctx = null, dest = null;
      if (typeof getOrCreateMaster === "function") {
        const mg = getOrCreateMaster();
        if (mg && mg.context) { ctx = mg.context; dest = mg; }
      }
      if (!ctx && typeof getActx === "function") {
        try { ctx = getActx(); } catch(_){}
      }
      if (ctx) {
        // Always preload the BASE inst — this triggers the .sf2 fetch
        // + PHDR parse, which back-populates BF_INSTRUMENTS[id].presets
        // for legacy banks. The result becomes visible in the toolbar.
        // Pass track.id so this uses the per-track engine (same one used
        // on project open + timeline playback) — otherwise it loads into
        // the global engine, a separate cache, causing a SECOND load.
        ensureSoundfontPresetLoaded(m, ctx, dest, track && track.id);
        // Also preload the SELECTED preset variant if one is set. This
        // is the derived inst the timeline will actually use — without
        // this preload, the timeline silently misses the first wave of
        // notes while waiting for the engine's loadPreset to resolve
        // for the unique derived id.
        if (typeof resolveTrackInstrumentMeta === "function") {
          const resolved = resolveTrackInstrumentMeta(track);
          if (resolved && resolved.engine === "soundfont-v1" && resolved.id !== m.id) {
            ensureSoundfontPresetLoaded(resolved, ctx, dest, track && track.id);
          }
        }
      }
    } catch(_) { /* non-fatal */ }
    // eslint-disable-next-line
  }, [track && track.instrumentId, track && track.selectedSfPreset && track.selectedSfPreset.bank, track && track.selectedSfPreset && track.selectedSfPreset.program]);
  // Active voices for cleanup on pause. Each voice is { stop(when) }
  // returned by scheduleMidiNote. Stored as an array, cleared on stop.
  const localVoicesRef = React.useRef([]);
  // The "transport zero" in audio-context time — when the local play started.
  // Local playhead position at time T is: ctx.currentTime - localStartCtxRef.
  const localStartCtxRef = React.useRef(0);
  // Lookahead scheduling cursor: how far into the clip we've already
  // scheduled. Advances each tick.
  const localCursorRef = React.useRef(0);
  // Tick interval handle so we can clear it on stop.
  const localTickRef = React.useRef(null);
  // Animation frame for the visible playhead beam (separate from the
  // scheduler tick because we want 60fps beam updates but only 25ms
  // scheduling resolution).
  const localBeamRafRef = React.useRef(null);
  // Local playhead position in clip-time seconds — updated each frame
  // for the visible beam. Stored as state so the playhead beam re-renders.
  const [localPlayheadSec, setLocalPlayheadSec] = React.useState(0);
  // Ref mirror of the same value, updated every RAF frame by beamFn
  // without triggering React re-renders. State stays still during
  // PAT playback (no per-frame note re-renders during scroll). Consumers
  // that need the current position (seek, pause) read from this ref.
  const localPlayheadSecRef = React.useRef(0);
  // Live ref to the current notes array. The local playback tick reads
  // through this ref so edits made while playing are reflected in the
  // *next* lookahead window. Without this, the tick captures the notes
  // array at play-time and ignores subsequent additions/edits/deletes.
  const liveNotesRef = React.useRef(notes);
  React.useEffect(function(){ liveNotesRef.current = notes; });
  // Also keep instrument resolution live (sound design slider while playing).
  const liveTrackRef = React.useRef(track);
  React.useEffect(function(){ liveTrackRef.current = track; });

  // Tool/snap config
  const [snapId, setSnapId] = React.useState("1/16");
  const snapMeta = BF_SNAP_OPTIONS.find(function(s){ return s.id === snapId; }) || BF_SNAP_OPTIONS[0];

  // Scale was previously a user-facing picker but it only highlighted rows
  // visually — no snap, no transpose, no playback effect. Removed from the
  // UI to keep the menu focused. The AI generators below still reference
  // these values; we hold them as constants (scaleId "off" defaults the
  // generator fallback path to Major, scaleRoot 0 = C). If scale-aware
  // composition is ever brought back, restore the picker + state here.
  const scaleId   = "off";
  const scaleRoot = 0;
  const scaleMeta = BF_SCALES[scaleId] || BF_SCALES["off"];

  // Undo/redo — stacks of notes arrays. Each commit snapshots `notes`.
  const [undoStack, setUndoStack] = React.useState([]);
  const [redoStack, setRedoStack] = React.useState([]);

  // Lasso state — non-null while a select-mode rectangle is being drawn.
  // Coords are in canvas space (already scroll-adjusted).
  const [lasso, setLasso] = React.useState(null);
  // Shape: { x1, y1, x2, y2 }

  // ── Layout constants ──
  const KEYBOARD_W   = 56;
  const TOOLBAR_H    = 56;
  const RULER_H      = 24;
  const VEL_LANE_H   = 84;
  const FOOTER_H     = 32;
  const PITCH_MIN    = 0;
  const PITCH_MAX    = 127;
  const TOTAL_ROWS   = PITCH_MAX - PITCH_MIN + 1;
  // Total grid pixel dimensions (the inner scrollable canvas)
  const gridW = Math.max(800, clipDur * pxPerSec + 200);  // include slack to the right
  const gridH = TOTAL_ROWS * rowHeight;

  // Refs to the scroll containers so pinch/scroll handlers can read+write.
  const gridScrollRef = React.useRef(null);
  // Refs for the custom scrollbar thumbs. We mutate their transforms
  // directly in onScroll instead of going through React state — moving
  // a 1px thumb shouldn't trigger a Studio-wide re-render every frame.
  // The keyboard column still uses scrollY state (see translateY in
  // its style) so scrollY state is kept too, but updated less often
  // via rAF batching to avoid lag during fast scrolls.
  const hThumbElRef = React.useRef(null);
  const vThumbElRef = React.useRef(null);
  const velocityScrollRef = React.useRef(null);
  const keyboardScrollRef = React.useRef(null);
  // Inner keyboard content — its translateY tracks grid scrollTop. During
  // pinch zoom we sync this directly via DOM (in the same layout effect
  // that fixes the grid's scrollTop) so React's lagging scrollY state
  // never causes the keys to render misaligned with the grid rows. The
  // result: keys stay locked to the patterns during the entire zoom
  // gesture, no shake on release.
  const keyboardInnerRef = React.useRef(null);
  // Same idea for the velocity lane row (X axis): synced with the grid's
  // scrollLeft during horizontal pinch so beat positions stay aligned.
  const velocityInnerRef = React.useRef(null);
  // Holds the latest grid touch-handler closures so the native (passive:false)
  // touch listeners we attach in the ref callback always invoke the current
  // closure. Without this, listeners freeze on stale `notes`/`snapMeta` from
  // the first render.
  const gridHandlersRef = React.useRef(null);

  // Wire scroll so vertical scroll of grid moves keyboard; horizontal scroll
  // of grid moves velocity lane. BOTH satellite columns sync via transform
  // (driven by scrollX/scrollY in render) rather than setting their own
  // scrollTop/scrollLeft, because their containers use overflow:hidden so
  // those native props no-op anyway. Transforms also avoid scroll jitter.
  // Pending vertical-scroll correction for pinch zoom. Set during the
  // pinch's rAF commit alongside setRowHeight; the layout effect below
  // applies it AFTER React's commit so the scrollTop write hits the
  // CONTAINER WITH ITS NEW HEIGHT (writing scrollTop before the
  // container grows would just get clamped to the old scrollable range,
  // which is exactly what was causing the shake during vertical pinch).
  const pendingScrollYRef = React.useRef(null);
  React.useLayoutEffect(function(){
    if (pendingScrollYRef.current == null) return;
    const target = pendingScrollYRef.current;
    pendingScrollYRef.current = null;
    if (gridScrollRef.current) {
      try { gridScrollRef.current.scrollTop = target; } catch(_){}
    }
    // Keep the keyboard column locked to the grid's new scrollTop in the
    // SAME paint frame. Without this, React's scrollY state is still
    // showing the pre-pinch value, the keyboard renders with the new
    // rowHeight but the old translateY offset, and the keys appear to
    // shake/drift relative to the note grid until a scroll event finally
    // fires and fixes the state. Direct DOM mutation skips that lag.
    if (keyboardInnerRef.current) {
      try { keyboardInnerRef.current.style.transform = "translateY(" + (-target) + "px)"; } catch(_){}
    }
  }, [rowHeight]);
  // Same idea for horizontal — keeps the X pinch path consistent.
  const pendingScrollXRef = React.useRef(null);
  React.useLayoutEffect(function(){
    if (pendingScrollXRef.current == null) return;
    const target = pendingScrollXRef.current;
    pendingScrollXRef.current = null;
    if (gridScrollRef.current) {
      try { gridScrollRef.current.scrollLeft = target; } catch(_){}
    }
    // Sync velocity lane (bottom row) with the grid's new scrollLeft so
    // the velocity bars stay aligned with their notes during X pinch.
    if (velocityInnerRef.current) {
      try { velocityInnerRef.current.style.transform = "translateX(" + (-target) + "px)"; } catch(_){}
    }
  }, [pxPerSec]);

  // Track pending rAF so we coalesce multi-fire scroll events (iOS can
  // fire onScroll up to 60-120x/second during a flick).
  const scrollRafRef = React.useRef(0);
  const handleGridScroll = function(e){
    const el = e.currentTarget;
    // Move thumbs immediately via direct DOM mutation — no React render.
    // This is what fixes the "stiff/laggy" feel: thumbs follow the
    // finger frame-perfectly instead of waiting for React to re-render.
    try {
      const vw = el.clientWidth;
      const vh = el.clientHeight;
      const sw = el.scrollWidth;
      const sh = el.scrollHeight;
      if (hThumbElRef.current && sw > vw + 1) {
        const hThumbW = Math.max(28, (vw / sw) * vw);
        const hThumbX = (el.scrollLeft / (sw - vw)) * (vw - hThumbW);
        hThumbElRef.current.style.transform = "translateX(" + hThumbX + "px)";
      }
      if (vThumbElRef.current && sh > vh + 1) {
        const vThumbH = Math.max(28, (vh / sh) * vh);
        const vThumbY = (el.scrollTop / (sh - vh)) * (vh - vThumbH);
        vThumbElRef.current.style.transform = "translateY(" + vThumbY + "px)";
      }
    } catch(_) {}
    // rAF-batch the React state update. The keyboard column's transform
    // is driven by scrollY state, so we DO need to update it — but at
    // most once per animation frame, not on every scroll event.
    if (scrollRafRef.current) return;
    scrollRafRef.current = requestAnimationFrame(function(){
      scrollRafRef.current = 0;
      const el2 = gridScrollRef.current;
      if (!el2) return;
      setScrollX(el2.scrollLeft);
      setScrollY(el2.scrollTop);
    });
  };

  // ── Initial scroll-to-notes ──
  // The grid container's actual DOM scrollTop is independent of the React
  // scrollY state. On first mount we explicitly scroll to the initial
  // scrollY (which was computed to center on existing notes) so the user
  // doesn't open the piano roll to empty space. Runs ONCE — subsequent
  // scrolls are driven by user interaction via handleGridScroll.
  const didInitialScrollRef = React.useRef(false);
  React.useEffect(function(){
    if (didInitialScrollRef.current) return;
    const el = gridScrollRef.current;
    if (!el) return;
    // Defer one frame so the grid has laid out and scrollHeight is correct
    requestAnimationFrame(function(){
      if (!gridScrollRef.current) return;
      try { gridScrollRef.current.scrollTop = scrollY; } catch(_){}
      didInitialScrollRef.current = true;
    });
  }, []);

  // ── Coord conversion helpers ──
  // pitchToY: top edge of the row containing this pitch, in canvas coords.
  // Y is inverted (pitch 127 at top → y=0; pitch 0 at bottom → y=gridH-rowHeight).
  const pitchToY = function(p) { return (PITCH_MAX - p) * rowHeight; };
  const yToPitch = function(y) {
    const row = Math.floor(y / rowHeight);
    return Math.max(PITCH_MIN, Math.min(PITCH_MAX, PITCH_MAX - row));
  };
  const timeToX = function(t) { return t * pxPerSec; };
  const xToTime = function(x) { return Math.max(0, x / pxPerSec); };

  // Writes `nextNotes` to the right place in the tracks tree. In standard
  // mode, that's the clip's notes field. In drum-channel mode, that's
  // pattern.channels[channelIdx].notes on the drum track. One helper keeps
  // commit / undo / redo in lockstep.
  const writeNotesToTracks = function(prev, nextNotes) {
    if (drumCtx) {
      return prev.map(function(tr){
        if (tr.id !== drumCtx.drumTrack.id) return tr;
        return Object.assign({}, tr, {
          patterns: (tr.patterns || []).map(function(p){
            if (p.id !== drumCtx.pattern.id) return p;
            return Object.assign({}, p, {
              channels: p.channels.map(function(ch, ci){
                if (ci !== drumCtx.channelIdx) return ch;
                return Object.assign({}, ch, { notes: nextNotes });
              }),
            });
          }),
        });
      });
    }
    return prev.map(function(tr){
      if (tr.id !== track.id) return tr;
      return Object.assign({}, tr, {
        clips: tr.clips.map(function(cl){
          if (cl.id !== clip.id) return cl;
          return Object.assign({}, cl, { notes: nextNotes });
        }),
      });
    });
  };

  // ── Note CRUD ──
  // Each mutation snapshots the previous notes to undoStack so undo works
  // for any single edit. Redo stack cleared on every fresh edit.
  const commitNotes = function(nextNotes) {
    setUndoStack(function(prev){
      // Cap history at 50 — beyond that we're paying memory for very rare
      // use. Producers undo a few steps; never 200.
      const trimmed = prev.length >= 50 ? prev.slice(prev.length - 49) : prev;
      return trimmed.concat([notes]);
    });
    setRedoStack([]);
    setTracks(function(prev){ return writeNotesToTracks(prev, nextNotes); });
  };

  const undo = function() {
    if (undoStack.length === 0) return;
    const prevNotes = undoStack[undoStack.length - 1];
    setUndoStack(function(s){ return s.slice(0, -1); });
    setRedoStack(function(s){ return s.concat([notes]); });
    setTracks(function(prev){ return writeNotesToTracks(prev, prevNotes); });
  };
  const redo = function() {
    if (redoStack.length === 0) return;
    const nextNotes = redoStack[redoStack.length - 1];
    setRedoStack(function(s){ return s.slice(0, -1); });
    setUndoStack(function(s){ return s.concat([notes]); });
    setTracks(function(prev){ return writeNotesToTracks(prev, nextNotes); });
  };

  // Preview a pitch — one-shot voice, short duration. Used for keyboard taps
  // and on-place feedback. Routes through masterDest if available (so user
  // volume + track FX apply) — falls back to ctx.destination otherwise.
  //
  // Crucially, the audio context isn't created until the user first hits
  // the master ▶ button — so if a producer opens the editor and starts
  // tapping keys immediately, the props.actx will be null. We resolve a
  // *live* context per call via getActx() which lazy-creates one. Touching
  // the keyboard or grid is itself a user gesture, satisfying iOS's
  // "must be created in a user gesture" requirement for AudioContext.
  const previewPitch = function(pitch, dur) {
    // Parent override (drum-channel mode plays the kit pad sample directly,
    // using the already-loaded drum kit buffer, with pitch-follows-key).
    if (typeof previewPitchOverride === "function") {
      try { return previewPitchOverride(pitch, dur); } catch(_) { return null; }
    }
    // Prefer the master-gain path: getOrCreateMaster() lazy-creates both
    // the AudioContext AND the master gain node, returning a destination
    // we can write into. This routes preview through the same node graph
    // as transport playback (so the master volume slider affects previews).
    //
    // Fall back to: prop snapshot → lazy getActx → bail. This three-tier
    // path means preview works regardless of whether the user has hit ▶
    // before opening the editor.
    let ctx = null;
    let dest = null;
    if (typeof getOrCreateMaster === "function") {
      try {
        const mg = getOrCreateMaster();
        if (mg && mg.context) {
          ctx = mg.context;
          dest = mg;
          // Master gain may have been ramped to 0 by stopAll() — the
          // master transport zeroes it on stop so reverb/delay tails
          // get cut. If we don't restore it here, the piano roll
          // preview routes through a silent master and produces no
          // sound. Re-arm it on any preview call.
          try {
            if (typeof mg.gain !== "undefined" && mg.gain.value === 0) {
              mg.gain.cancelScheduledValues(ctx.currentTime);
              mg.gain.setValueAtTime(1, ctx.currentTime);
            }
          } catch(_) { /* defensive */ }
        }
      } catch(_) { ctx = null; dest = null; }
    }
    if (!ctx) {
      ctx = actx;
      if (!ctx && typeof getActx === "function") {
        try { ctx = getActx(); } catch(_) { ctx = null; }
      }
      if (!ctx) return;
      dest = masterDest || ctx.destination;
    }
    // Route preview through THIS track's FX chain entry node (volume/
    // pan/mute/FX all apply) if available. Falls back to master
    // destination if the chain can't be built (e.g. no master yet).
    // Without this, the mixer volume slider has no effect on preview
    // tones — only on transport playback.
    // Route preview through THIS track's FX chain entry node. With the
    // per-track engine model, each MIDI track has its OWN FluidSynth
    // instance whose audioNode connects to the track's FX chain entry
    // — Reverb, EQ, Compressor etc. are heard on previews exactly as
    // they will sound during timeline playback. (Previously this was
    // sampler-only because soundfonts shared one audioNode across all
    // tracks; that limitation no longer applies.)
    let trackChainEntry = null;
    if (track && typeof getOrBuildPreviewChain === "function") {
      try {
        const entry = getOrBuildPreviewChain(track.id);
        if (entry) trackChainEntry = entry;
      } catch(_) { /* fall back to master dest */ }
    }
    if (trackChainEntry && track) {
      dest = trackChainEntry;
    }
    try {
      if (ctx.state === "suspended") { try { ctx.resume(); } catch(_) {} }
      // ── Soundfont preview path ──────────────────────────────────────
      // With the per-track engine model, each MIDI track has its OWN
      // FluidSynth instance. We connect that instance's audioNode to
      // THIS track's FX chain entry, so Reverb / EQ / Compressor etc.
      // are heard exactly as they will sound during timeline playback.
      // Other tracks' engines stay connected to their own chains —
      // there's no shared state to fight over anymore.
      if (inst && inst.engine === "soundfont-v1") {
        // dest is the track's chain entry (set above). Pass tr.id so
        // the per-track engine is used.
        ensureSoundfontPresetLoaded(inst, ctx, dest, track && track.id);
        try {
          const sfEng = (track && track.id != null)
            ? getBFSoundfontEngineForTrack(track.id)
            : getBFSoundfontEngine();
          if (sfEng) {
            if (typeof sfEng.reconnect === "function") sfEng.reconnect(dest);
            if (typeof sfEng.reaffirmAllChannelBindings === "function") {
              sfEng.reaffirmAllChannelBindings();
            }
          }
        } catch(_) { /* non-fatal */ }
      }
      // Sampler: ensure the audio buffer is loaded. If not yet in cache,
      // wait for the fetch+decode to complete and THEN fire the note.
      // First-tap-after-create is otherwise silent because the playback
      // context hasn't decoded the new sample yet (~200-500ms after
      // track creation). This guarantees a responsive feel.
      // For sampler tracks: ALWAYS re-resolve the inst at preview time.
      // The `inst` captured by closure at render may have audioBuffer=null
      // if the sample loaded after the editor mounted. resolveSamplerInst
      // reads the live cache so we always get the current buffer.
      if (track && track.samplerData) {
        const sd = track.samplerData;
        const cached = sd.sampleHash && bfSamplerCache.buffers.has(sd.sampleHash);
        if (!cached) {
          // Defer the note: load first, then schedule with a fresh `when`.
          // Returns a thenable handle so the caller can record the voice
          // for press-and-hold stop even when the buffer wasn't ready.
          const deferred = { voice: null, stop: function(t){ if (this.voice && this.voice.stop) this.voice.stop(t); } };
          ensureSamplerLoaded(track, ctx).then(function(){
            const liveInst = resolveSamplerInst(track);
            if (liveInst && liveInst.audioBuffer) {
              const when2 = ctx.currentTime + 0.005;
              deferred.voice = scheduleMidiNote(ctx, dest, liveInst, pitch, 0.85, when2, dur || 0.4, undefined, track && track.id);
            }
          }).catch(function(e){ console.warn("[sampler] preload failed", e); });
          return deferred;
        }
        // Cache IS populated — re-resolve to pick up the buffer and fire now
        const freshInst = resolveSamplerInst(track);
        const when = ctx.currentTime + 0.005;
        return scheduleMidiNote(ctx, dest, freshInst, pitch, 0.85, when, dur || 0.4, undefined, track && track.id);
      }
      const when = ctx.currentTime + 0.005;
      return scheduleMidiNote(ctx, dest, inst, pitch, 0.85, when, dur || 0.4, undefined, track && track.id);
    } catch(e) { console.warn("[midi-editor] preview failed", e); }
  };

  // ── Editor local playback ──
  // Plays the clip's notes in isolation without involving the master
  // transport. Loops the clip seamlessly. Pause stops all in-flight voices.
  //
  // Architecture:
  //   • setInterval at 25ms ticks schedules notes 150ms ahead of audio time
  //   • localCursorRef tracks how far we've scheduled
  //   • requestAnimationFrame loop drives the visible playhead beam
  //   • Loop wrap: when cursor exceeds clipDur, modulo back to 0
  //
  // Why two refs (cursor + start)? The audio-context time is monotonic;
  // we want the playhead to wrap, so we keep a "virtual" loop index.
  const localStop = function(){
    // Stop voices, clear timers, reset playhead to 0 so the next play
    // restarts from the beginning. (Previously this preserved position
    // for pause-resume behavior, but the user prefers restart-on-stop
    // since the editor's play is for previewing a clip — not for
    // scrubbing through long compositions.)
    const ctx = (function(){
      if (typeof getOrCreateMaster === "function") {
        try { const m = getOrCreateMaster(); if (m && m.context) return m.context; } catch(_){}
      }
      return actx || null;
    })();
    const now = ctx ? ctx.currentTime : 0;
    const voices = localVoicesRef.current || [];
    for (let i = 0; i < voices.length; i++) {
      try { if (voices[i] && voices[i].stop) voices[i].stop(now); } catch(_){}
    }
    localVoicesRef.current = [];
    if (localTickRef.current != null) {
      clearInterval(localTickRef.current);
      localTickRef.current = null;
    }
    if (localBeamRafRef.current != null) {
      cancelAnimationFrame(localBeamRafRef.current);
      localBeamRafRef.current = null;
    }
    setLocalPlaying(false);
    localPlayheadSecRef.current = 0;
    setLocalPlayheadSec(0);   // rewind to start
  };

  // Soft-stop variant that kills in-flight voices + timers WITHOUT
  // resetting the playhead. Used by seek operations so the cursor
  // stays where the user wants it. Skipping the playhead reset is the
  // key difference — localStop's reset would clobber the seek target.
  const localStopKeepPosition = function(){
    const ctx = (function(){
      if (typeof getOrCreateMaster === "function") {
        try { const m = getOrCreateMaster(); if (m && m.context) return m.context; } catch(_){}
      }
      return actx || null;
    })();
    const now = ctx ? ctx.currentTime : 0;
    const voices = localVoicesRef.current || [];
    for (let i = 0; i < voices.length; i++) {
      try { if (voices[i] && voices[i].stop) voices[i].stop(now); } catch(_){}
    }
    localVoicesRef.current = [];
    if (localTickRef.current != null) {
      clearInterval(localTickRef.current);
      localTickRef.current = null;
    }
    if (localBeamRafRef.current != null) {
      cancelAnimationFrame(localBeamRafRef.current);
      localBeamRafRef.current = null;
    }
    // Sync state from the ref so the static (non-RAF-driven) playhead
    // renders at the position where playback stopped.
    setLocalPlayheadSec(localPlayheadSecRef.current);
    setLocalPlaying(false);
  };

  const localPlay = function(startCursorOverride){
    // Resolve a context + destination, prefer the master gain node
    let ctx = null, dest = null;
    if (typeof getOrCreateMaster === "function") {
      try {
        const m = getOrCreateMaster();
        if (m && m.context) {
          ctx = m.context;
          // PAT plays through the track's FX entry node so EQ/reverb/etc
          // are heard — same path SONG uses. Without this, PAT bypassed
          // FX and sounded different from SONG playback.
          dest = m;
          try {
            const liveTrackForChain = liveTrackRef.current || track;
            const chains = (typeof window !== "undefined") ? window._bfChainsRef : null;
            if (chains && chains.current && liveTrackForChain && liveTrackForChain.id) {
              const chainEntry = chains.current[liveTrackForChain.id];
              if (chainEntry && chainEntry.entry) {
                dest = chainEntry.entry;
              }
            }
          } catch(_) { /* fall through to master */ }
          // Restore master gain if the timeline's stopAll() zeroed it.
          // Without this, the editor's local play button produces no
          // sound because master is muted.
          try {
            if (typeof m.gain !== "undefined" && m.gain.value === 0) {
              m.gain.cancelScheduledValues(ctx.currentTime);
              m.gain.setValueAtTime(1, ctx.currentTime);
            }
          } catch(_) { /* defensive */ }
        }
      } catch(_) { ctx = null; }
    }
    if (!ctx) {
      ctx = actx;
      if (!ctx && typeof getActx === "function") {
        try { ctx = getActx(); } catch(_){}
      }
      if (!ctx) return;  // nothing to play through
      dest = masterDest || ctx.destination;
    }
    if (ctx.state === "suspended") { try { ctx.resume(); } catch(_){} }

    // Pre-load the soundfont for this track if applicable. Fire-and-forget;
    // notes scheduled before the load completes will produce silence (the
    // engine's scheduleNote returns a no-op handle), and the loop will
    // start playing audibly as soon as the .sf3 fetch + decode finish.
    {
      const liveTrackForSf = liveTrackRef.current || track;
      if (liveTrackForSf.samplerData) {
        // Sampler track: preload the audio buffer. Fire-and-forget; first
        // notes may be silent if the fetch+decode is still in flight.
        ensureSamplerLoaded(liveTrackForSf, ctx);
      } else {
        const sfInstCheck = applyInstrumentOverrides(
          resolveTrackInstrumentMeta(liveTrackForSf),
          liveTrackForSf.instrumentOverrides
        );
        if (sfInstCheck && sfInstCheck.engine === "soundfont-v1") {
          ensureSoundfontPresetLoaded(sfInstCheck, ctx, dest, liveTrackForSf.id);
          // Same defenses as the previewPitch path — re-attach the engine
          // node to this master and reaffirm channel bindings so a clean
          // playback starts with the right preset on each channel.
          try {
            const sfEng = getBFSoundfontEngineForTrack(liveTrackForSf.id);
            if (sfEng) {
              if (typeof sfEng.reconnect === "function") sfEng.reconnect(dest);
              if (typeof sfEng.reaffirmAllChannelBindings === "function") {
                sfEng.reaffirmAllChannelBindings();
              }
            }
          } catch(_) { /* non-fatal */ }
        }
      }
    }

    // Initialise transport state. localStop() resets the playhead to 0
    // when stopped, so this always starts at the clip beginning.
    // If startCursorOverride was passed (e.g. from a seek), use that
    // instead of reading localPlayheadSec from state — closures captured
    // before setLocalPlayheadSec commits would otherwise see stale 0.
    const startCursor = (startCursorOverride != null) ? startCursorOverride : (localPlayheadSec || 0);
    const LOOKAHEAD = 0.15;     // schedule 150ms in advance of audio time
    const TICK_MS = 25;
    // anchor: at ctx.currentTime + 0.05, the clip is at position startCursor
    localStartCtxRef.current = ctx.currentTime + 0.05 - startCursor;
    localCursorRef.current   = startCursor;
    localVoicesRef.current   = [];

    // Use the current preset + overrides for every voice. Re-resolve
    // here so a sound-design slider drag while playing affects newly
    // scheduled notes (but won't retroactively change already-scheduled ones).
    const tickFn = function(){
      const ctxNow = ctx.currentTime;
      // How far the playhead has advanced in clip-time
      const playheadCt = ctxNow - localStartCtxRef.current;
      const scheduleUntil = playheadCt + LOOKAHEAD;
      const cursor = localCursorRef.current;
      if (scheduleUntil <= cursor) return;  // nothing new to schedule

      // We may need to schedule across the loop boundary. Walk in
      // (windowStart, windowEnd) chunks within a single loop iteration.
      let winStart = cursor;
      let winEnd   = scheduleUntil;
      // The current loop iteration's anchor (in audio-context time):
      // loop k starts at startCtx + k * clipDur. Find the iteration the
      // cursor belongs to.
      const loopIdx0 = Math.floor(cursor / clipDur);
      const loopIdx1 = Math.floor(scheduleUntil / clipDur);

      const liveTrack = liveTrackRef.current || track;
      let liveInst;
      if (liveTrack.samplerData) {
        liveInst = resolveSamplerInst(liveTrack);
      } else {
        liveInst = applyInstrumentOverrides(
          resolveTrackInstrumentMeta(liveTrack),
          liveTrack.instrumentOverrides
        );
      }
      const liveNotes = mergeSlideVoices(liveNotesRef.current || notes);

      for (let li = loopIdx0; li <= loopIdx1; li++) {
        const segStartCt = Math.max(winStart, li * clipDur);
        const segEndCt   = Math.min(winEnd,   (li + 1) * clipDur);
        if (segStartCt >= segEndCt) continue;
        const localFrom = segStartCt - li * clipDur;
        const localTo   = segEndCt   - li * clipDur;
        for (let i = 0; i < liveNotes.length; i++) {
          const n = liveNotes[i];
          if (n.start >= localTo) continue;
          if (n.start < localFrom) continue;
          const ctTime = li * clipDur + n.start + localStartCtxRef.current;
          if (ctTime < ctxNow) continue;
          const v = scheduleMidiNote(ctx, dest, liveInst, n.pitch, n.velocity == null ? 0.85 : n.velocity, ctTime, n.duration, liveTrack.noteCutMode, liveTrack.id, n.glide);
          if (v) localVoicesRef.current.push(v);
        }
      }
      localCursorRef.current = scheduleUntil;
    };

    localTickRef.current = setInterval(tickFn, TICK_MS);
    // Immediately schedule the first window so the first audible note isn't
    // delayed by a full tick.
    tickFn();

    // Beam animation — 60fps requestAnimationFrame loop. Updates the
    // visible playhead position so the producer sees scrubbing in real time.
    // Playhead beam — runs at 60fps via RAF. We avoid setLocalPlayheadSec
    // entirely here because it triggers a full piano-roll re-render —
    // which, combined with scroll re-renders, causes the notes to "shake"
    // visually as React reconciler races with scroll position updates.
    // Instead, beamFn updates:
    //   1. localPlayheadSecRef — for seek/pause consumers to read current pos
    //   2. data-bf-pat-playhead DOM elements — direct style.left mutation
    // React state is only touched on play start, pause, seek, and stop.
    //
    // CRITICAL: we cache the NodeList ONCE here. Running querySelectorAll
    // every RAF frame forces a synchronous layout calc — which during a
    // touch-drag scroll causes layout thrashing. The browser must complete
    // pending scroll-position layout work before answering the query, then
    // resume, on every frame. That thrash is what makes notes "shake"
    // during scroll while PAT plays. Caching kills the thrash entirely.
    let cachedHeads = null;
    const recache = function(){
      try { cachedHeads = document.querySelectorAll('[data-bf-pat-playhead]'); }
      catch(_) { cachedHeads = null; }
    };
    const beamFn = function(){
      const ctxNow = ctx.currentTime;
      const playheadCt = ctxNow - localStartCtxRef.current;
      const local = clipDur > 0 ? ((playheadCt % clipDur) + clipDur) % clipDur : 0;
      localPlayheadSecRef.current = local;
      if (cachedHeads === null) recache();
      if (cachedHeads && cachedHeads.length > 0) {
        for (let i = 0; i < cachedHeads.length; i++) {
          const el = cachedHeads[i];
          // Defensive: element might have been GC'd if the piano-roll
          // unmounted. Skip if no longer in DOM.
          if (!el.isConnected) { cachedHeads = null; break; }
          const ppsAttr = el.getAttribute('data-bf-pps');
          const pps = ppsAttr ? parseFloat(ppsAttr) : 0;
          const offAttr = el.getAttribute('data-bf-offset');
          const off = offAttr ? parseFloat(offAttr) : 0;
          if (pps > 0) {
            // Use transform (GPU composite, no layout) instead of `left`
            // (layout-triggering). Inside a scrolled container, mutating
            // `left` 60×/sec while the user touch-drags races with scroll
            // layout updates and causes visible shake. Transform avoids
            // that — same trick the scrollbar thumb uses.
            el.style.transform = 'translateX(' + (local * pps + off) + 'px)';
          }
        }
      }
      localBeamRafRef.current = requestAnimationFrame(beamFn);
    };
    // Defer first cache populate to next frame so the playhead elements
    // (rendered after setLocalPlaying(true) below) are mounted in the DOM.
    requestAnimationFrame(recache);
    localBeamRafRef.current = requestAnimationFrame(beamFn);
    setLocalPlaying(true);
  };

  // Toggle: tap once to play, tap again to pause. Pause keeps the
  // playhead at its last position so the next play resumes there.
  const toggleLocalPlay = function(){
    // Mutual exclusion: PAT and SONG never play together. If we're about
    // to START PAT and SONG is currently playing, stop SONG first via
    // the parent transport toggle. The reverse direction (SONG pressed
    // while PAT runs) is handled by the useEffect on `isPlaying`.
    const startingPat = !(localPlayingExternal != null ? localPlayingExternal : localPlaying);
    if (startingPat && isPlaying && typeof onTransportPlay === "function") {
      try { onTransportPlay(); } catch(_){}
    }
    // Parent delegation: drum-channel mode uses the rack PAT scheduler
    // so PAT play covers the whole pattern (not just this pad), and the
    // pad's edits land on the next loop iteration in real time.
    if (typeof onLocalPlay === "function" && typeof onLocalStop === "function") {
      if (localPlayingExternal) onLocalStop(); else onLocalPlay();
      return;
    }
    if (localPlaying) localStop(); else localPlay();
  };

  // Seek helpers — used by the transport-style buttons in the editor
  // header (rewind to start, jump back/forward one bar, skip to end).
  // When playback is running, we stop and restart at the new position
  // so the scheduler picks up the new cursor immediately. When stopped,
  // we just update the playhead state — the next play will start there.
  const seekToTime = function(targetSec){
    const clamped = Math.max(0, Math.min(targetSec, clipDur));
    const wasPlaying = localPlaying;
    if (wasPlaying) {
      // Soft-stop: kill voices/timers but DON'T zero the playhead
      // (regular localStop would, which clobbers the seek).
      localStopKeepPosition();
    }
    setLocalPlayheadSec(clamped);
    localPlayheadSecRef.current = clamped;
    if (wasPlaying) {
      // Restart with an EXPLICIT start cursor — bypasses the React
      // closure capture problem (localPlay() reads localPlayheadSec
      // from its closure, which still sees the OLD value until React
      // commits the setLocalPlayheadSec above). Passing clamped
      // directly means we don't have to wait for the state update.
      // Short timeout so localStopKeepPosition's clearInterval is
      // applied before the new tickFn schedules itself.
      setTimeout(function(){
        localPlay(clamped);
      }, 16);
    }
  };
  // ── Seek shortcuts ──
  // Behaviour: when the main timeline (SONG mode) is playing OR the
  // user has the SONG transport ready, the rewind/forward buttons
  // drive the timeline. Otherwise they drive the local pattern
  // playhead. This matches FL Studio's transport — the active mode
  // dictates what the transport controls do.
  const seekRewindToStart = function(){
    // Always reset the song timeline to 0 if the transport is wired —
    // not just when playing. Otherwise tapping ⏮ while stopped only
    // resets the local pattern playhead, and a subsequent SONG play
    // resumes from wherever the timeline was sitting (not the start),
    // which is what the user noticed: "back-to-start does nothing
    // unless SONG is already playing."
    if (onTransportSeek) onTransportSeek(0);
    seekToTime(0);
  };
  const seekBackOneBar = function(){
    if (isPlaying && onTransportSeek) {
      onTransportSeek((currentTime || 0) - barSec);
    } else {
      seekToTime((localPlayheadSec || 0) - barSec);
    }
  };
  const seekFwdOneBar  = function(){
    if (isPlaying && onTransportSeek) {
      onTransportSeek((currentTime || 0) + barSec);
    } else {
      seekToTime((localPlayheadSec || 0) + barSec);
    }
  };
  const seekToEnd       = function(){
    // "Skip to end" for SONG mode jumps to the rightmost clip edge
    // across all tracks. We don't have direct access to compute that
    // from here, so fall back to a generous bar jump if onTransportSeek
    // is wired — the user can press it multiple times to walk forward.
    if (isPlaying && onTransportSeek) {
      onTransportSeek((currentTime || 0) + barSec * 4);
    } else {
      seekToTime(clipDur - 0.05);
    }
  };

  // Cleanup on unmount: stop voices + clear timers so the audio context
  // doesn't keep firing notes for a closed editor.
  React.useEffect(function(){
    return function(){ localStop(); };
    // eslint-disable-next-line
  }, []);

  // Stop local (PAT) playback when the app goes to background. Without
  // this, the editor's localPlaying state stayed true while its scheduled
  // BufferSources were tied to a now-suspended context — on resume, the
  // editor would still show "Stop" but no audio would play, forcing the
  // user to manually stop+restart. Stopping on hide returns the editor
  // to a known-good state so a single press of Play does the right thing
  // on return.
  React.useEffect(function(){
    const onVis = function(){
      if (document.hidden) {
        // localStop is idempotent — safe to call even if nothing is
        // playing (just clears any voices/ticks/state to known-good).
        try { localStop(); } catch(_){}
      }
    };
    document.addEventListener("visibilitychange", onVis);
    return function(){ document.removeEventListener("visibilitychange", onVis); };
    // eslint-disable-next-line
  }, []);

  // Add a note at (pitch, startTime). Default first-note duration is
  // two snap-squares so a placed note spans 1 → 1.5 (or whatever the
  // current snap grid is). Visually that's the smallest size that still
  // reads as a "note" on a phone screen — single-square notes feel like
  // unintentional dots. After the first placement, size inherits from
  // the last touched note (FL-style).
  const addNoteAt = function(pitch, startTime, durSec) {
    const inheritedDur = lastTouchedDurRef.current;
    // First-tap fallback: 2 × snap step if snap is on, else half a beat.
    const snapStep = (snapMeta && snapMeta.beatDiv > 0)
      ? (beatSec / snapMeta.beatDiv)
      : (beatSec / 2);
    const defaultDur = snapStep * 2;
    const dur = durSec || inheritedDur || defaultDur;
    // Clamp into clip bounds
    const start = Math.max(0, Math.min(clipDur - 0.02, startTime));
    const duration = Math.max(0.02, Math.min(clipDur - start, dur));
    const id = newNoteId();
    const note = {
      id: id,
      pitch: pitch,
      start: start,
      duration: duration,
      velocity: 0.85,
    };
    // Slide tool → mark as a portamento note (glides the previous note's
    // pitch into this one; folded into a single voice at playback time).
    if (toolMode === "slide") note.slide = true;
    const next = notes.concat([note]);
    commitNotes(next);
    selectOnly(id);
    // Record this new note's duration as the inherit value for the
    // NEXT placement too — so if the producer places several notes
    // in a row without touching anything else, they all match.
    lastTouchedDurRef.current = duration;
    previewPitch(pitch, Math.min(0.5, duration));
    return id;
  };

  const deleteNote = function(noteId) {
    if (!noteId) return;
    const next = notes.filter(function(n){ return n.id !== noteId; });
    commitNotes(next);
    if (selNoteIds.has(noteId)) {
      setSelNoteIds(function(prev){
        const s = new Set(prev); s.delete(noteId); return s;
      });
    }
  };

  const patchNote = function(noteId, patch, opts) {
    // opts.skipUndo = true → mutate without history (used during live drag;
    // we commit a single undo entry when drag begins, not on every frame).
    const next = notes.map(function(n){
      if (n.id !== noteId) return n;
      return Object.assign({}, n, patch);
    });
    if (opts && opts.skipUndo) {
      // Direct write without history snapshot. Route through
      // writeNotesToTracks so drum-channel mode lands in the right place
      // (pattern.channels[i].notes) instead of clip.notes (which doesn't
      // exist on the synthetic track).
      setTracks(function(prev){ return writeNotesToTracks(prev, next); });
    } else {
      commitNotes(next);
    }
  };

  // ── Slice tool ──
  // Split a note at clip-local time `tapTime`. The left half keeps the
  // original id (so selection isn't lost); the right half gets a fresh id.
  // No-op if the tap point is outside the note's body or too close to the
  // edge (< 60ms — splitting at the seam isn't useful).
  const sliceNoteAt = function(noteId, tapTime) {
    const n = notes.find(function(x){ return x.id === noteId; });
    if (!n) return;
    const localT = tapTime - n.start;
    if (localT < 0.06 || localT > n.duration - 0.06) return;
    const leftDur  = localT;
    const rightDur = n.duration - localT;
    const left  = Object.assign({}, n, { duration: leftDur });
    const right = Object.assign({}, n, {
      id: newNoteId(),
      start: n.start + leftDur,
      duration: rightDur,
    });
    const next = notes.map(function(x){ return x.id === n.id ? left : x; }).concat([right]);
    commitNotes(next);
  };

  // ── Chord stamp ──
  // Add a chord rooted at `rootPitch` starting at `startTime`. Each note
  // in the chord uses the current snap step as duration (matches the
  // Pencil placement convention). All notes get a fresh id and are
  // auto-selected so the producer can immediately drag/edit the chord
  // together as a unit.
  const stampChordAt = function(chord, rootPitch, startTime) {
    if (!chord || !chord.intervals) return;
    const stepDur = snapMeta.beatDiv > 0 ? beatSec / snapMeta.beatDiv : beatSec * 0.5;
    const start = Math.max(0, Math.min(clipDur - 0.02, startTime));
    const duration = Math.max(0.02, Math.min(clipDur - start, stepDur));
    const newNotes = [];
    const newIds = new Set();
    for (let i = 0; i < chord.intervals.length; i++) {
      const p = rootPitch + chord.intervals[i];
      if (p < PITCH_MIN || p > PITCH_MAX) continue;
      const id = newNoteId();
      newIds.add(id);
      newNotes.push({
        id: id, pitch: p, start: start, duration: duration, velocity: 0.85,
      });
    }
    if (newNotes.length === 0) return;
    commitNotes(notes.concat(newNotes));
    setSelNoteIds(newIds);
    // Audible feedback — preview the chord by triggering all pitches at once
    for (let i = 0; i < newNotes.length; i++) {
      previewPitch(newNotes[i].pitch, Math.min(0.4, duration));
    }
  };

  // ── Bulk ops (all operate on selNoteIds) ──
  // Each commits ONE undo entry. They no-op gracefully when nothing is
  // selected so the action-bar buttons can always be tapped.

  const deleteSelection = function() {
    if (selNoteIds.size === 0) return;
    const next = notes.filter(function(n){ return !selNoteIds.has(n.id); });
    commitNotes(next);
    selectClear();
  };

  const duplicateSelection = function() {
    if (selNoteIds.size === 0) return;
    // Determine the offset: place the duplicates one snap step after the
    // selection's last end. If the bunch would overflow the clip, we
    // tighten the offset to "just past the original cluster" so duplicates
    // remain visible.
    const sel = notes.filter(function(n){ return selNoteIds.has(n.id); });
    let minStart = Infinity, maxEnd = -Infinity;
    for (let i = 0; i < sel.length; i++) {
      if (sel[i].start < minStart) minStart = sel[i].start;
      if (sel[i].start + sel[i].duration > maxEnd) maxEnd = sel[i].start + sel[i].duration;
    }
    const stepDur = snapMeta.beatDiv > 0 ? beatSec / snapMeta.beatDiv : beatSec * 0.5;
    let offset = (maxEnd - minStart);  // duplicate right after original
    if (offset < stepDur) offset = stepDur;
    // Clamp so the bunch fits inside the clip
    const fits = clipDur - maxEnd;
    if (offset > fits) offset = Math.max(0, fits);
    if (offset < 0.01) return;  // no room
    const newIds = new Set();
    const dupes = sel.map(function(n){
      const id = newNoteId();
      newIds.add(id);
      return Object.assign({}, n, {
        id: id,
        start: Math.min(clipDur - n.duration, n.start + offset),
      });
    });
    commitNotes(notes.concat(dupes));
    setSelNoteIds(newIds);
  };

  const copySelection = function() {
    if (selNoteIds.size === 0) return;
    const sel = notes.filter(function(n){ return selNoteIds.has(n.id); });
    let minStart = Infinity;
    for (let i = 0; i < sel.length; i++) if (sel[i].start < minStart) minStart = sel[i].start;
    const payload = {
      // Strip ids so paste creates fresh ones
      notes: sel.map(function(n){
        return {
          pitch: n.pitch,
          start: n.start - minStart,   // normalized to 0
          duration: n.duration,
          velocity: n.velocity,
        };
      }),
      minStart: minStart,
    };
    setClipboard(payload);
    // Mirror to module-level clipboard so other piano-roll editors can
    // paste this selection too (cross-editor copy/paste).
    try { bfSetNotesClipboard(payload); } catch(_){}
  };

  const cutSelection = function() {
    copySelection();
    deleteSelection();
  };

  const pasteClipboard = function() {
    // Prefer local clipboard (last copy in THIS editor instance) but fall
    // back to the module-level clipboard so producers can copy in one
    // editor and paste in another.
    let cb = clipboard;
    if (!cb || !cb.notes || cb.notes.length === 0) {
      cb = bfGetNotesClipboard();
      if (cb) setClipboard(cb);
    }
    if (!cb || !cb.notes || cb.notes.length === 0) return;
    // Paste anchored at: playhead if inside the clip, else 0.
    let anchor = 0;
    const local = currentTime - (clip.startTime || 0);
    if (local >= 0 && local <= clipDur) anchor = local;
    // Snap anchor to grid
    anchor = snapTimeToGrid(anchor, snapMeta.beatDiv, beatSec);
    // Trim notes so we don't paste past the clip end
    const pastable = cb.notes.filter(function(n){
      return anchor + n.start < clipDur;
    });
    if (pastable.length === 0) return;
    const newIds = new Set();
    const pasted = pastable.map(function(n){
      const id = newNoteId();
      newIds.add(id);
      const startT = anchor + n.start;
      const dur = Math.min(n.duration, clipDur - startT);
      return {
        id: id, pitch: n.pitch, start: startT, duration: Math.max(0.02, dur), velocity: n.velocity,
      };
    });
    commitNotes(notes.concat(pasted));
    setSelNoteIds(newIds);
  };

  // Transpose all selected notes by `semitones`. Notes pushed off the
  // 0..127 range are clamped (not dropped) so the user doesn't lose data.
  const transposeSelection = function(semitones) {
    if (selNoteIds.size === 0 || !semitones) return;
    const next = notes.map(function(n){
      if (!selNoteIds.has(n.id)) return n;
      return Object.assign({}, n, {
        pitch: Math.max(PITCH_MIN, Math.min(PITCH_MAX, n.pitch + semitones)),
      });
    });
    commitNotes(next);
  };

  // Quantize: snap each selected note's start time to the current snap grid.
  // If snap is off we default to 1/16 so the button still does something
  // useful.
  const quantizeSelection = function() {
    if (selNoteIds.size === 0) return;
    const div = snapMeta.beatDiv > 0 ? snapMeta.beatDiv : 4;  // default 1/16
    const next = notes.map(function(n){
      if (!selNoteIds.has(n.id)) return n;
      const q = snapTimeToGrid(n.start, div, beatSec);
      const clamped = Math.max(0, Math.min(clipDur - n.duration, q));
      return Object.assign({}, n, { start: clamped });
    });
    commitNotes(next);
  };

  // Humanize: now behaves like a strum + jitter pass. Notes that share a
  // start time get an ordered offset by pitch (low→high or high→low,
  // alternating per chord), so chords sound strummed instead of perfectly
  // simultaneous. Single notes get a small random time/velocity nudge so
  // melodies still feel loose. The strum step is ~14ms per voice — about
  // what a guitarist or pianist's hand does between adjacent notes in a
  // chord. Plus a small random per-voice nudge so each strum doesn't sound
  // identical.
  const humanizeSelection = function() {
    if (selNoteIds.size === 0) return;
    const sel = notes.filter(function(n){ return selNoteIds.has(n.id); });
    // Group selection by start time (with a small tolerance so notes that
    // were meant to be a chord but got slightly nudged previously still
    // group together). Tolerance: 1/64 beat — tighter than humanize jitter
    // so we don't accidentally group across actual chord boundaries.
    const tol = beatSec / 64;
    const groups = [];  // [{ keyStart: number, ids: Set<id> }]
    const sortedSel = sel.slice().sort(function(a, b){ return a.start - b.start; });
    for (const n of sortedSel) {
      let placed = false;
      for (const g of groups) {
        if (Math.abs(g.keyStart - n.start) <= tol) {
          g.ids.add(n.id);
          placed = true;
          break;
        }
      }
      if (!placed) {
        const ids = new Set();
        ids.add(n.id);
        groups.push({ keyStart: n.start, ids: ids });
      }
    }
    // Build a per-note offset map. Index by groupIdx (for strum direction
    // alternation) and per-group sorted-by-pitch position.
    const STRUM_STEP_SEC = 0.014;   // ~14ms between strummed voices
    const STRUM_RAND_SEC = 0.004;   // ±4ms random per-voice jitter
    const SINGLE_NOTE_JITTER = beatSec / 64;   // tiny time jitter for singletons
    const VEL_JITTER = 0.10;        // ±10% velocity variation across the board
    const noteOffsets = {};    // id -> { dt, dv }
    groups.forEach(function(g, gi){
      const ids = Array.from(g.ids);
      const isChord = ids.length > 1;
      if (!isChord) {
        // Singleton — small random time nudge, no strum direction
        const id = ids[0];
        noteOffsets[id] = {
          dt: (Math.random() * 2 - 1) * SINGLE_NOTE_JITTER,
          dv: (Math.random() * 2 - 1) * VEL_JITTER,
        };
        return;
      }
      // Chord — sort voices by pitch, then assign ordered strum offsets.
      // Alternate direction per chord (even = strum up low→high, odd =
      // strum down high→low) so consecutive chords don't all sound the
      // same way.
      const chordNotes = ids.map(function(id){
        return notes.find(function(nn){ return nn.id === id; });
      }).filter(Boolean).slice().sort(function(a, b){ return a.pitch - b.pitch; });
      const strumDown = (gi % 2) === 1;
      for (let vi = 0; vi < chordNotes.length; vi++) {
        const orderIdx = strumDown ? (chordNotes.length - 1 - vi) : vi;
        // Base ordered offset + small random per-voice nudge
        const baseDt = orderIdx * STRUM_STEP_SEC;
        const randDt = (Math.random() * 2 - 1) * STRUM_RAND_SEC;
        noteOffsets[chordNotes[vi].id] = {
          dt: baseDt + randDt,
          dv: (Math.random() * 2 - 1) * VEL_JITTER,
        };
      }
    });
    const next = notes.map(function(n){
      const off = noteOffsets[n.id];
      if (!off) return n;
      const start = Math.max(0, Math.min(clipDur - n.duration, n.start + off.dt));
      const velocity = Math.max(0.1, Math.min(1, (n.velocity == null ? 0.85 : n.velocity) + off.dv));
      return Object.assign({}, n, { start: start, velocity: velocity });
    });
    commitNotes(next);
  };

  // Velocity randomize: replace each velocity with a random value in
  // [0.55, 1.0]. The lower bound stops notes from dropping to a whisper.
  const velocityRandomizeSelection = function() {
    if (selNoteIds.size === 0) return;
    const next = notes.map(function(n){
      if (!selNoteIds.has(n.id)) return n;
      const v = 0.55 + Math.random() * 0.45;
      return Object.assign({}, n, { velocity: v });
    });
    commitNotes(next);
  };

  // Velocity nudge: bump every selected note's velocity by +/- delta.
  const velocityNudgeSelection = function(delta) {
    if (selNoteIds.size === 0) return;
    const next = notes.map(function(n){
      if (!selNoteIds.has(n.id)) return n;
      const cur = n.velocity == null ? 0.85 : n.velocity;
      return Object.assign({}, n, { velocity: Math.max(0.05, Math.min(1, cur + delta)) });
    });
    commitNotes(next);
  };

  // ── Arpeggiator ──
  // Turn the selection into an arpeggio: spread the selected pitches
  // sequentially across time, each at one snap step. Direction can be
  // "up", "down", or "updown" (up then back down).
  //
  // The arp anchor is the earliest start time of the selection. We
  // preserve velocities — pitch i in the arp keeps its original note's
  // velocity. If "updown", we don't double-trigger the peak.
  const arpeggiateSelection = function(direction) {
    if (selNoteIds.size === 0) return;
    const sel = notes.filter(function(n){ return selNoteIds.has(n.id); });
    if (sel.length < 2) return;  // nothing to arpeggiate
    const stepDur = snapMeta.beatDiv > 0 ? beatSec / snapMeta.beatDiv : beatSec * 0.5;
    const anchor = sel.reduce(function(a, n){ return Math.min(a, n.start); }, Infinity);
    // Sort selection by pitch
    const byPitch = sel.slice().sort(function(a,b){ return a.pitch - b.pitch; });
    let sequence;
    if (direction === "down") {
      sequence = byPitch.slice().reverse();
    } else if (direction === "updown") {
      // Up then down without re-hitting the top
      const ups = byPitch;
      const downs = byPitch.slice().reverse().slice(1);
      sequence = ups.concat(downs);
    } else {
      sequence = byPitch;  // up
    }
    const newNotes = [];
    const newIds = new Set();
    for (let i = 0; i < sequence.length; i++) {
      const src = sequence[i];
      const start = anchor + i * stepDur;
      if (start + 0.02 > clipDur) break;
      const id = newNoteId();
      newIds.add(id);
      newNotes.push({
        id: id, pitch: src.pitch, start: start,
        duration: Math.min(stepDur * 0.95, clipDur - start),
        velocity: src.velocity == null ? 0.85 : src.velocity,
      });
    }
    // Replace selection with arp
    const remaining = notes.filter(function(n){ return !selNoteIds.has(n.id); });
    commitNotes(remaining.concat(newNotes));
    setSelNoteIds(newIds);
  };

  // Roll the selected notes: replace each one with N retriggers evenly spaced
  // across its duration. Same pitch as the source; velocity decays 0.7^i so
  // the tail eases off (musical, not robotic — same curve as ratchets in the
  // drum sequencer). rateBeatDiv: 4 = 1/16, 6 = 1/16T, 8 = 1/32, 12 = 1/32T,
  // 16 = 1/64. Notes shorter than one step at the chosen rate are left as a
  // single hit (rolling something that short would be pointless).
  const rollSelection = function(rateBeatDiv) {
    if (selNoteIds.size === 0) return;
    const sel = notes.filter(function(n){ return selNoteIds.has(n.id); });
    if (sel.length === 0) return;
    const stepDur = beatSec / Math.max(1, rateBeatDiv);
    const newNotes = [];
    const newIds = new Set();
    for (let s = 0; s < sel.length; s++) {
      const src = sel[s];
      const dur = src.duration || stepDur;
      // How many retriggers fit inside this note's duration?
      const reps = Math.max(1, Math.floor(dur / stepDur));
      const baseVel = src.velocity == null ? 0.85 : src.velocity;
      for (let i = 0; i < reps; i++) {
        const start = src.start + i * stepDur;
        // 0.7^i decay — matches the ratchet decay so a 4-hit roll fades like
        // a real hi-hat tail rather than 4 equal-loudness machine-gun hits.
        const v = Math.max(0.05, Math.min(1.5, baseVel * Math.pow(0.7, i)));
        const id = newNoteId();
        newIds.add(id);
        newNotes.push({
          id: id, pitch: src.pitch, start: start,
          duration: stepDur * 0.95,
          velocity: v,
        });
      }
    }
    // Drop the originals; keep everything else; add the roll notes.
    const remaining = notes.filter(function(n){ return !selNoteIds.has(n.id); });
    commitNotes(remaining.concat(newNotes));
    setSelNoteIds(newIds);
  };


  // ── Note hit testing ──
  // Given pointer x,y in canvas coords, find which note (if any) is under
  // the pointer. Returns { note, mode: "body" | "resize" } so we can
  // distinguish a drag (whole-note move) from a resize (right edge).
  //
  // Resize hot zone is proportional to note width: never wider than 30%
  // of the note's pixel width, capped at 12px max. Without this cap, a
  // short note (like a 1/16 hi-hat) becomes mostly-resize-handle and the
  // user can't tap its body to drag it.
  const noteAt = function(canvasX, canvasY) {
    const pitchAtY = yToPitch(canvasY);
    // Iterate in reverse so the topmost-rendered note wins on overlap.
    for (let i = notes.length - 1; i >= 0; i--) {
      const n = notes[i];
      if (n.pitch !== pitchAtY) continue;
      const x1 = timeToX(n.start);
      const x2 = timeToX(n.start + n.duration);
      if (canvasX < x1 || canvasX > x2) continue;
      const noteWidthPx = x2 - x1;
      // Touch-friendly resize handle: at least 10px so it's grabbable with a
      // fingertip, up to 18px, scaling with note width (40%). A bigger edge
      // zone means an edge grab reliably starts a resize instead of being
      // misread as a timeline scroll.
      const resizeZone = Math.max(10, Math.min(18, noteWidthPx * 0.4));
      const mode = (x2 - canvasX <= resizeZone) ? "resize" : "body";
      return { note: n, mode: mode };
    }
    return null;
  };

  // ── Drag / gesture state ──
  // We use a single ref for the in-progress drag so the pointer handlers
  // don't trigger React re-renders on every move (60fps drag = 60 renders
  // per second otherwise; refs keep it cheap).
  const dragRef = React.useRef(null);
  // dragRef shape:
  //   { kind: "move"|"resize"|"create"|"pan"|"longpress",
  //     noteId, startPitch, startStart, startDuration,
  //     pointerStartX, pointerStartY,
  //     lastPreviewPitch  // so dragging across keys doesn't spam previews }

  // Pinch state — when two fingers are down. We zoom around the centroid
  // so the spot under the user's fingers stays stationary.
  const pinchRef = React.useRef(null);
  // pinchRef shape:
  //   { startDist, startPxPerSec, startRowHeight, centroidX, centroidY,
  //     startScrollX, startScrollY }

  // ── Pointer handlers on grid body ──
  // We use onTouchStart/Move/End directly (rather than onPointer*) because
  // iOS Safari's pointer event sequencing for multi-touch is unreliable —
  // touch events give us proper TouchList semantics for pinch detection.

  // Dynamic scroll lock helpers. iOS engages smooth native scroll
  // immediately on touchmove unless touchAction blocks it. Calling
  // preventDefault from a non-passive handler used to work but caused
  // iOS to refuse to scroll AT ALL (even with passive handlers it was
  // still waiting on the non-passive sibling). So instead we switch
  // touchAction live: "none" while actively dragging a note (locks
  // scroll), "pan-x pan-y" everywhere else (full native scroll).
  // Direct DOM mutation — no React re-render needed.
  const lockGridScroll = function() {
    const el = gridScrollRef.current;
    if (el) el.style.touchAction = "none";
  };
  const unlockGridScroll = function() {
    const el = gridScrollRef.current;
    if (el) el.style.touchAction = "pan-x pan-y";
  };
  // Axis-lock for native scroll. Without this, a vertical scroll attempt
  // that drifts a few degrees sideways turns into a diagonal pan, which
  // feels imprecise on a small screen. We watch the first ~6px of
  // movement and lock the touchAction to a single axis (pan-x OR pan-y)
  // for the rest of the gesture, blocking the orthogonal pan entirely.
  const scrollAxisLockRef = React.useRef(null);   // { startX, startY, axis }
  const armScrollAxisLock = function(startX, startY) {
    scrollAxisLockRef.current = { startX: startX, startY: startY, axis: null };
  };
  const maybeLockScrollAxis = function(curX, curY) {
    const a = scrollAxisLockRef.current;
    if (!a || a.axis) return;
    const adx = Math.abs(curX - a.startX);
    const ady = Math.abs(curY - a.startY);
    if (adx < 6 && ady < 6) return;  // not enough motion to commit yet
    const el = gridScrollRef.current;
    if (!el) return;
    if (adx > ady) {
      a.axis = "x";
      el.style.touchAction = "pan-x";
    } else {
      a.axis = "y";
      el.style.touchAction = "pan-y";
    }
  };
  const releaseScrollAxisLock = function() {
    scrollAxisLockRef.current = null;
    const el = gridScrollRef.current;
    // Only restore both-axis pan if we're not in a committed drag (lockGridScroll
    // sets touchAction:none and is unlocked by its own onTouchEnd path).
    if (el && el.style.touchAction !== "none") {
      el.style.touchAction = "pan-x pan-y";
    }
  };

  const onGridTouchStart = function(e) {
    if (!gridScrollRef.current) return;
    const rect = gridScrollRef.current.getBoundingClientRect();
    const sl = gridScrollRef.current.scrollLeft;
    const st = gridScrollRef.current.scrollTop;
    // Two-finger pinch: stash centroid + distance + current zoom.
    if (e.touches.length >= 2) {
      const t1 = e.touches[0], t2 = e.touches[1];
      const dx = t2.clientX - t1.clientX, dy = t2.clientY - t1.clientY;
      const dist = Math.hypot(dx, dy);
      pinchRef.current = {
        startDist: dist,
        // Cache the initial finger orientation so axis-lock has a
        // fallback if the motion-based detection times out.
        startSpanDx: dx,
        startSpanDy: dy,
        // Per-finger start positions — used by the motion-based axis
        // lock to detect whether the fingers are spreading vertically
        // or horizontally.
        startT1X: t1.clientX, startT1Y: t1.clientY,
        startT2X: t2.clientX, startT2Y: t2.clientY,
        startPxPerSec: pxPerSec,
        startRowHeight: rowHeight,
        centroidX: (t1.clientX + t2.clientX) / 2 - rect.left + sl,
        centroidY: (t1.clientY + t2.clientY) / 2 - rect.top + st,
        startScrollX: sl,
        startScrollY: st,
        // For lock-axis detection: angle of the gesture
        angle: Math.atan2(Math.abs(dy), Math.abs(dx)),
      };
      // Cancel any in-progress single-touch drag so the pinch takes over.
      dragRef.current = null;
      // Lock scroll while pinching — touchAction: none stops iOS from
      // panning while we adjust zoom level.
      lockGridScroll();
      return;
    }
    if (e.touches.length !== 1) return;
    const t = e.touches[0];
    const canvasX = t.clientX - rect.left + sl;
    const canvasY = t.clientY - rect.top + st;
    const hit = noteAt(canvasX, canvasY);
    // Arm the scroll axis-lock for this gesture. The first ~6px of motion
    // commits the lock to a single axis. Released on touchend (or earlier
    // if a note-drag commits and lockGridScroll fully disables scroll).
    armScrollAxisLock(t.clientX, t.clientY);

    // ── Chord-stamp armed: highest priority ──
    // Tap any cell (empty or note) stamps the chord rooted at the pitch
    // under the finger, then disarms. We record the tap so end can fire
    // the stamp; if the user swipes we abort like a Pencil create.
    if (chordArmed) {
      dragRef.current = {
        kind: "chord-stamp",
        pointerStartX: canvasX,
        pointerStartY: canvasY,
        moved: false,
      };
      return;
    }

    // ── Dispatch by tool mode ──
    if (toolMode === "eraser") {
      // Tap a note → delete immediately. Drag continues delete-on-touch.
      if (hit) {
        // Snapshot once for the whole drag-erase session
        const snap = notes;
        deleteNote(hit.note.id);
        dragRef.current = {
          kind: "erase",
          pointerStartX: canvasX, pointerStartY: canvasY,
          moved: false,
          erasedIds: new Set([hit.note.id]),
          undoSnapshot: snap,
        };
      } else {
        // Tap on empty space in eraser mode — set up for JS-scroll if
        // the user drags. lastClientX/Y track so touchmove can apply
        // incremental scroll deltas.
        dragRef.current = {
          kind: "pan",
          lastClientX: t.clientX,
          lastClientY: t.clientY,
        };
      }
      return;
    }

    if (toolMode === "slice") {
      if (hit) {
        // Split the note at the tapped X. We commit immediately so the
        // user can see both halves; no drag needed.
        const tap = xToTime(canvasX);
        sliceNoteAt(hit.note.id, tap);
        dragRef.current = { kind: "slice-fired" };
      } else {
        // Empty space in slice mode — set up for JS-scroll.
        dragRef.current = {
          kind: "pan",
          lastClientX: t.clientX,
          lastClientY: t.clientY,
        };
      }
      return;
    }

    if (toolMode === "select") {
      if (hit) {
        // Tap a note in Select mode → add/toggle into selection. If the
        // note is already selected, drag moves the entire selection
        // together. Long-press: no delete in this mode (Eraser handles it).
        const alreadySelected = selNoteIds.has(hit.note.id);
        if (!alreadySelected) {
          // Switch selection to just this note (consistent with Notes app
          // / Photos app behavior — tap an item that wasn't selected to
          // start a fresh selection on it). If user wants to ADD, they
          // can long-press; but for Phase 3 we keep it simple — tap+drag
          // moves THIS note plus anything previously selected only if it
          // was already in the set.
          selectOnly(hit.note.id);
        }
        // Capture the touched note's duration so the NEXT note placed
        // inherits the same size — FL Studio's "copy size on touch"
        // behaviour. Reset every time a note is tapped so repeated
        // edits naturally pick up the latest size the producer wants.
        lastTouchedDurRef.current = hit.note.duration;
        // Build the move snapshot — capture the original positions of
        // every selected note so a drag delta can be applied to all.
        const movingIds = alreadySelected ? selNoteIds : new Set([hit.note.id]);
        const startSnapshot = [];
        for (let i = 0; i < notes.length; i++) {
          if (movingIds.has(notes[i].id)) {
            startSnapshot.push({
              id: notes[i].id,
              pitch: notes[i].pitch,
              start: notes[i].start,
              duration: notes[i].duration,
            });
          }
        }
        dragRef.current = {
          kind: hit.mode === "resize"
            ? (movingIds.size > 1 ? "resize-multi" : "resize")
            : "move-multi",
          anchorId: hit.note.id,
          movingIds: movingIds,
          startSnapshot: startSnapshot,
          pointerStartX: canvasX, pointerStartY: canvasY,
          lastPreviewPitch: hit.note.pitch,
          moved: false,
          undoSnapshot: notes,
        };
        // For single-note resize the legacy handler expects these props.
        if (hit.mode === "resize" && movingIds.size <= 1) {
          dragRef.current.noteId       = hit.note.id;
          dragRef.current.startStart   = hit.note.start;
          dragRef.current.startDuration= hit.note.duration;
        }
        // Grabbing an edge = resize intent: lock scroll right away so the
        // gesture can't be stolen by iOS native pan before the 8px commit.
        if (hit.mode === "resize") lockGridScroll();
      } else {
        // Empty → start lasso
        dragRef.current = {
          kind: "lasso",
          pointerStartX: canvasX, pointerStartY: canvasY,
          moved: false,
        };
      }
      return;
    }

    // ── Pencil (default) ──
    if (hit) {
      // Touched an existing note — could be select, move, or resize.
      // (Long-press-to-delete removed: the toolbar's delete button +
      // eraser tool cover this now, and the long-press timer made
      // touch scrolling feel jumpy because a brief pause over a note
      // during a scroll could trigger an unintended delete.)
      // Multi-selection preservation: if the tapped note is part of an
      // existing lasso/Select-All multi-selection, treat this drag as a
      // group move/resize — every selected note travels together. Only
      // when tapping a note that's NOT in the current selection do we
      // collapse to a single-note edit (preserving the old behavior so
      // tap-on-stray-note still feels like "selecting that note").
      const isInMultiSel = selNoteIds.size > 1 && selNoteIds.has(hit.note.id);
      if (isInMultiSel) {
        // Build the multi-snapshot so the move-multi delta math has each
        // selected note's starting pitch/start/duration to apply against.
        const startSnapshot = [];
        for (let i = 0; i < notes.length; i++) {
          if (selNoteIds.has(notes[i].id)) {
            startSnapshot.push({
              id: notes[i].id,
              pitch: notes[i].pitch,
              start: notes[i].start,
              duration: notes[i].duration,
            });
          }
        }
        dragRef.current = {
          kind: hit.mode === "resize" ? "resize-multi" : "move-multi",
          anchorId: hit.note.id,
          movingIds: selNoteIds,
          startSnapshot: startSnapshot,
          pointerStartX: canvasX, pointerStartY: canvasY,
          lastPreviewPitch: hit.note.pitch,
          moved: false,
          undoSnapshot: notes,
        };
        lastTouchedDurRef.current = hit.note.duration;
      } else {
        dragRef.current = {
          kind: hit.mode === "resize" ? "resize" : "move",
          noteId: hit.note.id,
          startPitch: hit.note.pitch,
          startStart: hit.note.start,
          startDuration: hit.note.duration,
          pointerStartX: canvasX,
          pointerStartY: canvasY,
          lastPreviewPitch: hit.note.pitch,
          moved: false,
          undoSnapshot: notes,
        };
        selectOnly(hit.note.id);
        // Capture for size inheritance on next placement.
        lastTouchedDurRef.current = hit.note.duration;
        // Edge grab = resize: lock scroll immediately (see multi-sel branch).
        if (hit.mode === "resize") lockGridScroll();
      }
    } else {
      // Empty space — tap to place a note. We don't fire the placement
      // until touch end so a swipe (scroll) doesn't accidentally place a
      // note. Stash pointer for movement detection.
      dragRef.current = {
        kind: "create",
        pointerStartX: canvasX,
        pointerStartY: canvasY,
        moved: false,
      };
    }
  };

  const onGridTouchMove = function(e) {
    if (!gridScrollRef.current) return;
    // Single-finger scroll: commit axis lock as soon as the gesture
    // exceeds the threshold. iOS reads touchAction during pan deciding
    // so mutating it here while passive prevents diagonal scroll.
    if (e.touches.length === 1 && scrollAxisLockRef.current) {
      const t0 = e.touches[0];
      maybeLockScrollAxis(t0.clientX, t0.clientY);
    }
    // Pinch zoom path
    if (e.touches.length >= 2 && pinchRef.current) {
      const t1 = e.touches[0], t2 = e.touches[1];
      const dx = t2.clientX - t1.clientX, dy = t2.clientY - t1.clientY;
      const dist = Math.hypot(dx, dy);
      const pinch = pinchRef.current;

      // ── Axis lock (motion-based) ──
      // The user wants ONE axis at a time. We pick based on how the
      // fingers are MOVING apart — not where they started. Two fingers
      // placed side-by-side and pulled apart vertically is a Y zoom;
      // the same fingers pulled apart horizontally is an X zoom.
      // Using the start orientation was wrong: putting fingers
      // side-by-side and pulling them apart vertically would still be
      // detected as "horizontal fingers" → X zoom, which is the bug.
      //
      // We track each finger's displacement from its START position.
      // The axis with the larger combined movement wins. We wait until
      // the combined movement is decisive (>= 16px) before locking, so
      // tiny first-frame jitter doesn't trip the detector.
      if (!pinch.axis) {
        // Compute each finger's per-axis movement from its starting
        // position. Sum the absolute moves on each axis — this is
        // robust to either finger being still while the other moves.
        const finger1Dx = Math.abs(t1.clientX - pinch.startT1X);
        const finger1Dy = Math.abs(t1.clientY - pinch.startT1Y);
        const finger2Dx = Math.abs(t2.clientX - pinch.startT2X);
        const finger2Dy = Math.abs(t2.clientY - pinch.startT2Y);
        const totalMovX = finger1Dx + finger2Dx;
        const totalMovY = finger1Dy + finger2Dy;
        const totalMov  = totalMovX + totalMovY;
        if (totalMov < 16) {
          // Not yet decisive — wait. Don't apply any zoom this frame.
          return;
        }
        // Lock to the axis with the dominant motion. Require a clear
        // 1.4× margin over the other axis — without the margin, a
        // diagonal pinch of (12px X, 11px Y) would flip-flop between
        // ticks. The margin enforces a real winner.
        if (totalMovX >= totalMovY * 1.4) {
          pinch.axis = "x";
        } else if (totalMovY >= totalMovX * 1.4) {
          pinch.axis = "y";
        } else {
          // Still ambiguous — keep waiting for the user to commit.
          return;
        }
      }
      // Compute the zoom ratio using the AXIS-SPECIFIC distance so
      // vertical pinches feel as responsive as horizontal ones.
      // Using the diagonal hypot for vertical pinches meant the user
      // had to move further to get the same zoom effect because Y
      // movement was being diluted by any small X drift.
      let ratio;
      if (pinch.axis === "x") {
        const currentSpanX = Math.abs(t2.clientX - t1.clientX);
        const startSpanX   = Math.abs(pinch.startSpanDx);
        ratio = currentSpanX / Math.max(20, startSpanX);
      } else {
        const currentSpanY = Math.abs(t2.clientY - t1.clientY);
        const startSpanY   = Math.abs(pinch.startSpanDy);
        ratio = currentSpanY / Math.max(20, startSpanY);
      }
      let nextPxPerSec  = pinch.startPxPerSec;
      let nextRowHeight = pinch.startRowHeight;
      if (pinch.axis === "x") {
        nextPxPerSec  = Math.max(20, Math.min(800, pinch.startPxPerSec * ratio));
      } else {
        nextRowHeight = Math.max(8, Math.min(48, pinch.startRowHeight * ratio));
      }
      const sxRatio = nextPxPerSec / pinch.startPxPerSec;
      const syRatio = nextRowHeight / pinch.startRowHeight;
      const nextScrollX = Math.max(0, (pinch.centroidX * sxRatio) - (pinch.centroidX - pinch.startScrollX));
      const nextScrollY = Math.max(0, (pinch.centroidY * syRatio) - (pinch.centroidY - pinch.startScrollY));
      // rAF-coalesced commit. Stash the latest pending values on the
      // pinchRef so out-of-order touchmove fires can't clobber each
      // other; the rAF callback uses whatever values were last written.
      pinch.pendingPxPerSec  = nextPxPerSec;
      pinch.pendingRowHeight = nextRowHeight;
      pinch.pendingScrollX   = nextScrollX;
      pinch.pendingScrollY   = nextScrollY;
      if (!pinch.rafScheduled) {
        pinch.rafScheduled = true;
        requestAnimationFrame(function(){
          // Re-read in case a later move overwrote the values
          const p = pinchRef.current;
          if (!p) return;
          p.rafScheduled = false;
          const sx = p.pendingScrollX, sy = p.pendingScrollY;
          const npx = p.pendingPxPerSec, nrh = p.pendingRowHeight;
          // Stash the target scroll position on the pendingScroll ref so
          // the useLayoutEffect can apply it AFTER React commits the new
          // zoom and the browser has laid out the container at its new
          // size. Writing scrollTop here (before layout) would clamp to
          // the OLD scrollable range — that's what caused the shake.
          if (p.axis === "x") {
            pendingScrollXRef.current = sx;
            setPxPerSec(npx);
          } else {
            pendingScrollYRef.current = sy;
            setRowHeight(nrh);
          }
        });
      }
      return;
    }
    const dr = dragRef.current;
    if (!dr) return;
    if (e.touches.length !== 1) return;
    const rect = gridScrollRef.current.getBoundingClientRect();
    const sl = gridScrollRef.current.scrollLeft;
    const st = gridScrollRef.current.scrollTop;
    const t = e.touches[0];
    const canvasX = t.clientX - rect.left + sl;
    const canvasY = t.clientY - rect.top + st;
    const dx = canvasX - dr.pointerStartX;
    const dy = canvasY - dr.pointerStartY;
    // Movement threshold for "this is a drag, not a tap". Bumped from
    // 4px → 8px so Safari pinch-zoomed pages don't trip on finger jitter
    // during what the user intends as a stationary tap.
    if (!dr.moved && (Math.abs(dx) > 8 || Math.abs(dy) > 8)) {
      dr.moved = true;
      // Commit: lock scroll on the grid so the drag isn't fighting iOS
      // native pan. Unlocked on touchend.
      if (dr.kind === "move" || dr.kind === "move-multi" || dr.kind === "resize" || dr.kind === "resize-multi"
          || dr.kind === "lasso" || dr.kind === "erase") {
        lockGridScroll();
      }
    }

    if (dr.kind === "move" && dr.moved) {
      const newPitch = yToPitch(canvasY);
      const rawTime = dr.startStart + (dx / pxPerSec);
      const snappedTime = snapTimeToGrid(rawTime, snapMeta.beatDiv, beatSec);
      const newStart = Math.max(0, Math.min(clipDur - dr.startDuration, snappedTime));
      patchNote(dr.noteId, { pitch: newPitch, start: newStart }, { skipUndo: true });
      if (newPitch !== dr.lastPreviewPitch) {
        previewPitch(newPitch, 0.18);
        dr.lastPreviewPitch = newPitch;
      }
    } else if (dr.kind === "move-multi" && dr.moved) {
      // Drag the entire selection. Compute the delta from the anchor's
      // start snapshot to the snapped tap-time; apply to all selected
      // notes uniformly. Pitch delta is in semitones (rows).
      const anchor = dr.startSnapshot.find(function(s){ return s.id === dr.anchorId; });
      if (!anchor) return;
      const newPitch = yToPitch(canvasY);
      const pitchDelta = newPitch - anchor.pitch;
      const rawTime = anchor.start + (dx / pxPerSec);
      const snappedAnchorStart = snapTimeToGrid(rawTime, snapMeta.beatDiv, beatSec);
      const timeDelta = snappedAnchorStart - anchor.start;
      // Clamp delta so no note goes out of bounds.
      // Compute the max allowed shifts so the most-constrained note stays in.
      let minStart = Infinity, maxEnd = -Infinity, minPitch = 127, maxPitch = 0;
      for (let i = 0; i < dr.startSnapshot.length; i++) {
        const s = dr.startSnapshot[i];
        if (s.start < minStart) minStart = s.start;
        if (s.start + s.duration > maxEnd) maxEnd = s.start + s.duration;
        if (s.pitch < minPitch) minPitch = s.pitch;
        if (s.pitch > maxPitch) maxPitch = s.pitch;
      }
      const tMin = -minStart;
      const tMax = clipDur - maxEnd;
      const pMin = PITCH_MIN - minPitch;
      const pMax = PITCH_MAX - maxPitch;
      const clampedTimeDelta = Math.max(tMin, Math.min(tMax, timeDelta));
      const clampedPitchDelta = Math.max(pMin, Math.min(pMax, pitchDelta));
      // Patch all moving notes in a single setTracks
      const movingIds = dr.movingIds;
      const snapshotById = {};
      for (let i = 0; i < dr.startSnapshot.length; i++) snapshotById[dr.startSnapshot[i].id] = dr.startSnapshot[i];
      const next = notes.map(function(n){
        if (!movingIds.has(n.id)) return n;
        const s = snapshotById[n.id];
        if (!s) return n;
        return Object.assign({}, n, {
          pitch: s.pitch + clampedPitchDelta,
          start: s.start + clampedTimeDelta,
        });
      });
      setTracks(function(prev){ return writeNotesToTracks(prev, next); });
      // Audible feedback only when the anchor's pitch crosses a row
      const anchorNewPitch = anchor.pitch + clampedPitchDelta;
      if (anchorNewPitch !== dr.lastPreviewPitch) {
        previewPitch(anchorNewPitch, 0.18);
        dr.lastPreviewPitch = anchorNewPitch;
      }
    } else if (dr.kind === "resize-multi" && dr.moved) {
      // Group resize: every selected note grows/shrinks by the same time
      // delta the anchor is being dragged by. Preserves the rhythmic
      // shape of the selection — e.g. drag any one note's right edge and
      // all selected notes lengthen by that amount together.
      const anchor = dr.startSnapshot.find(function(s){ return s.id === dr.anchorId; });
      if (!anchor) return;
      const rawEnd = anchor.start + anchor.duration + (dx / pxPerSec);
      const snappedEnd = snapTimeToGrid(rawEnd, snapMeta.beatDiv, beatSec);
      const newAnchorDur = Math.max(0.02, snappedEnd - anchor.start);
      const durDelta = newAnchorDur - anchor.duration;
      // Compute the maximum allowed delta so no note in the group goes
      // negative or past the clip end.
      let minDur = Infinity, maxEnd = -Infinity;
      for (let i = 0; i < dr.startSnapshot.length; i++) {
        const s = dr.startSnapshot[i];
        if (s.duration < minDur) minDur = s.duration;
        if (s.start + s.duration > maxEnd) maxEnd = s.start + s.duration;
      }
      const minAllowedDelta = 0.02 - minDur;     // shrink no further than 20ms min
      const maxAllowedDelta = clipDur - maxEnd;  // grow no further than clip end
      const clampedDelta = Math.max(minAllowedDelta, Math.min(maxAllowedDelta, durDelta));
      const movingIds = dr.movingIds;
      const snapshotById = {};
      for (let i = 0; i < dr.startSnapshot.length; i++) snapshotById[dr.startSnapshot[i].id] = dr.startSnapshot[i];
      const next = notes.map(function(n){
        if (!movingIds.has(n.id)) return n;
        const s = snapshotById[n.id];
        if (!s) return n;
        return Object.assign({}, n, { duration: Math.max(0.02, s.duration + clampedDelta) });
      });
      setTracks(function(prev){ return writeNotesToTracks(prev, next); });
      // Track size for inheritance: the anchor's new length is the
      // strongest signal of intent.
      lastTouchedDurRef.current = Math.max(0.02, anchor.duration + clampedDelta);
    } else if (dr.kind === "resize" && dr.moved) {
      const rawEnd = dr.startStart + dr.startDuration + (dx / pxPerSec);
      const snappedEnd = snapTimeToGrid(rawEnd, snapMeta.beatDiv, beatSec);
      const newDur = Math.max(0.02, Math.min(clipDur - dr.startStart, snappedEnd - dr.startStart));
      patchNote(dr.noteId, { duration: newDur }, { skipUndo: true });
      // Inherit-on-next-placement: a deliberate resize is the strongest
      // signal of the producer's intended note length, so update the
      // tracker live during the drag.
      lastTouchedDurRef.current = newDur;
    } else if (dr.kind === "lasso" && dr.moved) {
      // Update lasso rectangle. We store live coords; intersection happens
      // on touchend so the user can refine without per-frame churn.
      setLasso({
        x1: dr.pointerStartX, y1: dr.pointerStartY,
        x2: canvasX, y2: canvasY,
      });
    } else if (dr.kind === "erase" && dr.moved) {
      // Swipe-erase: delete any new notes the finger passes over.
      const passHit = noteAt(canvasX, canvasY);
      if (passHit && !dr.erasedIds.has(passHit.note.id)) {
        dr.erasedIds.add(passHit.note.id);
        // Remove this one — patch notes directly without a separate undo
        // entry. We'll commit one final undo at touchEnd for the whole
        // erase session.
        const next = notes.filter(function(n){ return n.id !== passHit.note.id; });
        setTracks(function(prev){ return writeNotesToTracks(prev, next); });
      }
    } else if (dr.kind === "create" && (Math.abs(dx) > 12 || Math.abs(dy) > 12)) {
      // User committed to a swipe → switch to JS-driven scroll. iOS
      // native scroll has been unreliable in this overlay (sometimes
      // refuses to engage even with the right CSS), so we manually
      // update scrollLeft/scrollTop here. Track lastClientX/Y so we
      // apply incremental finger-delta scrolling per move.
      dragRef.current = {
        kind: "pan",
        lastClientX: t.clientX,
        lastClientY: t.clientY,
      };
    } else if (dr.kind === "pan") {
      // Manual scroll: subtract finger delta from current scroll. Finger
      // moving right = content moves right under finger = scrollLeft
      // decreases. Same for vertical.
      const moveDx = t.clientX - dr.lastClientX;
      const moveDy = t.clientY - dr.lastClientY;
      const sc = gridScrollRef.current;
      if (sc) {
        sc.scrollLeft = Math.max(0, Math.min(sc.scrollWidth - sc.clientWidth, sc.scrollLeft - moveDx));
        sc.scrollTop  = Math.max(0, Math.min(sc.scrollHeight - sc.clientHeight, sc.scrollTop  - moveDy));
      }
      dr.lastClientX = t.clientX;
      dr.lastClientY = t.clientY;
    } else if (dr.kind === "chord-stamp" && (Math.abs(dx) > 12 || Math.abs(dy) > 12)) {
      // Swipe in chord-armed mode → abort stamp (let user pan)
      dragRef.current = null;
    }
  };

  const onGridTouchEnd = function(e) {
    if (e.touches.length >= 1) {
      pinchRef.current = null;
      return;
    }
    pinchRef.current = null;
    // Always unlock scroll on touch end so the container is fully
    // scrollable again for the next gesture.
    unlockGridScroll();
    // Release the single-axis scroll lock so the NEXT gesture starts
    // fresh — its own first 6px of motion will commit its own axis.
    releaseScrollAxisLock();
    const dr = dragRef.current;
    if (!dr) {
      // Touch-end with no drag context might be a select-mode tap on
      // empty after lasso was already committed. Nothing to do.
      return;
    }

    if (dr.kind === "chord-stamp" && !dr.moved) {
      // Stamp chord rooted at the tapped pitch
      const pitch = yToPitch(dr.pointerStartY);
      const rawTime = xToTime(dr.pointerStartX);
      const snapped = snapTimeToGrid(rawTime, snapMeta.beatDiv, beatSec);
      stampChordAt(chordArmed, pitch, snapped);
      setChordArmed(null);  // disarm after one stamp
    } else if (dr.kind === "create" && !dr.moved) {
      // Pencil tap on empty → place a note
      const pitch = yToPitch(dr.pointerStartY);
      const rawTime = xToTime(dr.pointerStartX);
      const snapped = snapTimeToGrid(rawTime, snapMeta.beatDiv, beatSec);
      addNoteAt(pitch, snapped);
    } else if (dr.kind === "lasso") {
      if (dr.moved) {
        // Compute selection from the current lasso rectangle
        const x1 = Math.min(dr.pointerStartX, lasso ? lasso.x2 : dr.pointerStartX);
        const x2 = Math.max(dr.pointerStartX, lasso ? lasso.x2 : dr.pointerStartX);
        const y1 = Math.min(dr.pointerStartY, lasso ? lasso.y2 : dr.pointerStartY);
        const y2 = Math.max(dr.pointerStartY, lasso ? lasso.y2 : dr.pointerStartY);
        const t1 = xToTime(x1), t2 = xToTime(x2);
        const pTop = yToPitch(y1), pBottom = yToPitch(y2);
        const pHi = Math.max(pTop, pBottom);
        const pLo = Math.min(pTop, pBottom);
        const next = new Set();
        for (let i = 0; i < notes.length; i++) {
          const n = notes[i];
          if (n.pitch < pLo || n.pitch > pHi) continue;
          // Intersect note time-range with lasso time-range
          const nStart = n.start, nEnd = n.start + n.duration;
          if (nEnd < t1 || nStart > t2) continue;
          next.add(n.id);
        }
        setSelNoteIds(next);
      } else {
        // Empty tap in select mode → clear selection
        selectClear();
      }
      setLasso(null);
    } else if ((dr.kind === "move" || dr.kind === "move-multi" || dr.kind === "resize" || dr.kind === "resize-multi") && dr.moved && dr.undoSnapshot) {
      // Commit a single batched undo entry for the whole drag
      setUndoStack(function(prev){
        const trimmed = prev.length >= 50 ? prev.slice(prev.length - 49) : prev;
        return trimmed.concat([dr.undoSnapshot]);
      });
      setRedoStack([]);
    } else if (dr.kind === "erase" && dr.undoSnapshot) {
      // Commit a single undo entry for the entire erase session
      setUndoStack(function(prev){
        const trimmed = prev.length >= 50 ? prev.slice(prev.length - 49) : prev;
        return trimmed.concat([dr.undoSnapshot]);
      });
      setRedoStack([]);
    }
    dragRef.current = null;
  };

  // ── Pressed-key visual feedback ──
  // Tracks which piano-roll keyboard keys are currently "lit" so the user
  // sees which note they tapped. Stored as a Set in state (Set so adds
  // and deletes are O(1) and the render reads cleanly). Each tap adds
  // the pitch to the set and schedules a delayed remove. If the same
  // pitch is tapped again during the timeout, we reset the timer so the
  // glow stays continuous rather than flickering off-then-on.
  const [pressedKeys, setPressedKeys] = React.useState(function(){ return new Set(); });
  const pressedTimersRef = React.useRef({});  // pitch -> timeout id
  const flashKey = React.useCallback(function(pitch, holdMs){
    const hold = holdMs == null ? 220 : holdMs;
    // Replace the Set so React picks up the change (mutating in place
    // wouldn't trigger a re-render).
    setPressedKeys(function(prev){
      const next = new Set(prev);
      next.add(pitch);
      return next;
    });
    // Reset existing timer if any
    if (pressedTimersRef.current[pitch]) {
      clearTimeout(pressedTimersRef.current[pitch]);
    }
    pressedTimersRef.current[pitch] = setTimeout(function(){
      setPressedKeys(function(prev){
        const next = new Set(prev);
        next.delete(pitch);
        return next;
      });
      delete pressedTimersRef.current[pitch];
    }, hold);
  }, []);
  // Clear all pending timers on unmount so we don't setState on a dead component
  React.useEffect(function(){
    return function(){
      const timers = pressedTimersRef.current;
      Object.keys(timers).forEach(function(k){
        try { clearTimeout(timers[k]); } catch(_){}
      });
      pressedTimersRef.current = {};
    };
  }, []);

  // ── Keyboard taps for preview ──
  // Press-and-hold: while a finger is down on a key, the voice plays;
  // when the finger lifts, the voice stops. Per-pitch ref map so
  // multi-touch (chord taps) works.
  //
  // Critical correctness invariant: every voice we start MUST be
  // recorded so we can stop it on release OR on editor unmount.
  // Otherwise a sample slice keeps playing after the user leaves the
  // piano roll. The unmount cleanup at the bottom of this hook flushes
  // any voices still alive when the editor closes.
  const activePreviewVoicesRef = React.useRef(new Map());  // pitch → voice

  const stopPreviewVoice = function(pitch){
    const m = activePreviewVoicesRef.current;
    const v = m.get(pitch);
    if (v) {
      try {
        // Voice handles expose stop(ctxTime). Without an arg the voice
        // builder uses ctx.currentTime — the natural "now" stop.
        if (typeof v.stop === "function") v.stop();
      } catch(_) {}
      m.delete(pitch);
    }
  };
  const stopAllPreviewVoices = function(){
    const m = activePreviewVoicesRef.current;
    m.forEach(function(v){
      try { if (v && typeof v.stop === "function") v.stop(); } catch(_) {}
    });
    m.clear();
  };

  const onKeyTap = function(pitch) {
    flashKey(pitch, 260);
    // Stop any prior voice for this same pitch (rapid re-taps shouldn't
    // double-fire — chops the old voice first).
    stopPreviewVoice(pitch);
    // 8s cap is a safety so a held sample doesn't run forever. The
    // explicit stop on touchend cuts it earlier in normal use.
    const voice = previewPitch(pitch, 8.0);
    if (voice) activePreviewVoicesRef.current.set(pitch, voice);
  };
  const onKeyRelease = function(pitch) {
    stopPreviewVoice(pitch);
    setPressedKeys(function(prev){
      if (!prev.has(pitch)) return prev;
      const next = new Set(prev);
      next.delete(pitch);
      return next;
    });
  };
  // Clean up any voices still ringing when the editor unmounts so the
  // sample doesn't keep playing after the user exits the piano roll.
  React.useEffect(function(){
    return function(){ stopAllPreviewVoices(); };
  }, []);

  // ── Velocity lane drag ──
  // Vertical drag on a note's velocity bar updates that note's velocity.
  // No undo per frame; commit one entry per drag.
  const velDragRef = React.useRef(null);
  const onVelTouchStart = function(e, noteId) {
    if (e.touches.length !== 1) return;
    e.stopPropagation();
    const lane = velocityScrollRef.current;
    if (!lane) return;
    const rect = lane.getBoundingClientRect();
    velDragRef.current = {
      noteId: noteId,
      laneTop: rect.top,
      laneH: rect.height,
      undoSnapshot: notes,
      moved: false,
    };
    selectOnly(noteId);
  };
  const onVelTouchMove = function(e) {
    const dr = velDragRef.current;
    if (!dr) return;
    if (e.touches.length !== 1) return;
    const t = e.touches[0];
    const y = t.clientY - dr.laneTop;
    const vel = Math.max(0, Math.min(1, 1 - (y / dr.laneH)));
    dr.moved = true;
    patchNote(dr.noteId, { velocity: vel }, { skipUndo: true });
    // No preventDefault — the lane's touchAction:"pan-x" already blocks
    // vertical page scroll. Calling preventDefault here would only matter
    // if React's listener weren't passive, which it is by default in
    // current browsers; so the call is a no-op anyway.
  };
  const onVelTouchEnd = function() {
    const dr = velDragRef.current;
    if (dr && dr.moved && dr.undoSnapshot) {
      setUndoStack(function(prev){
        const trimmed = prev.length >= 50 ? prev.slice(prev.length - 49) : prev;
        return trimmed.concat([dr.undoSnapshot]);
      });
      setRedoStack([]);
    }
    velDragRef.current = null;
  };

  // ── Auto-scroll during playback ──
  // When the master transport is playing AND the playhead is inside the
  // clip's time range, gently scroll the grid so the playhead stays in
  // view. Only scroll if the playhead is near an edge — don't constantly
  // re-center (jittery).
  React.useEffect(function(){
    if (!isPlaying || !gridScrollRef.current) return;
    const clipStart = clip.startTime || 0;
    const local = currentTime - clipStart;
    if (local < 0 || local > clipDur) return;
    const phX = timeToX(local);
    const sl = gridScrollRef.current.scrollLeft;
    const vw = gridScrollRef.current.clientWidth;
    const margin = vw * 0.25;
    if (phX < sl + margin) {
      gridScrollRef.current.scrollLeft = Math.max(0, phX - margin);
    } else if (phX > sl + vw - margin) {
      gridScrollRef.current.scrollLeft = phX - vw + margin;
    }
  }, [currentTime, isPlaying]);

  // ── Length / loop changes ──
  // Update clip.duration via setTracks. Doesn't go through commitNotes
  // because it's not a note edit (still has its own undo path? no — for
  // Phase 2 we let length changes be non-undoable, like in most DAWs).
  const setClipDuration = function(nextDur) {
    if (drumCtx) {
      // Drum-channel mode: length lives on the pattern. Convert seconds to
      // steps using the pattern's current stepsPerBeat and bpm; round to
      // a whole bar so the grid stays clean. The rack's step grid and the
      // pad-scope editor both read pattern.lengthSteps so they re-render
      // together. lengthSteps is clamped to a minimum of one bar.
      const pat = drumCtx.pattern;
      const stepSec = drumStepDurationSec(bpm, pat.stepsPerBeat || 4);
      const stepsPerBar = (pat.stepsPerBeat || 4) * (timeSigNum || 4);
      const requestedSteps = Math.round(nextDur / stepSec);
      const bars = Math.max(1, Math.round(requestedSteps / stepsPerBar));
      const nextLen = bars * stepsPerBar;
      setTracks(function(prev){
        return prev.map(function(tr){
          if (tr.id !== drumCtx.drumTrack.id) return tr;
          return Object.assign({}, tr, {
            patterns: (tr.patterns || []).map(function(p){
              if (p.id !== drumCtx.pattern.id) return p;
              // Resize every channel's steps array to match the new length.
              // Pad with zeros on grow, slice on shrink. Notes-mode channels
              // keep their notes (filtered to the new length).
              const patDurNew = nextLen * stepSec;
              const newChannels = (p.channels || []).map(function(ch){
                if (drumChannelModeOf(ch) === "notes") {
                  return Object.assign({}, ch, {
                    notes: (ch.notes || []).filter(function(n){
                      return (n.start || 0) < patDurNew;
                    }),
                  });
                }
                const oldSteps = ch.steps || [];
                const newSteps = new Array(nextLen).fill(0);
                for (let i = 0; i < Math.min(oldSteps.length, nextLen); i++) {
                  newSteps[i] = oldSteps[i];
                }
                return Object.assign({}, ch, { steps: newSteps });
              });
              return Object.assign({}, p, { lengthSteps: nextLen, channels: newChannels });
            }),
          });
        });
      });
      return;
    }
    setTracks(function(prev){
      return prev.map(function(tr){
        if (tr.id !== track.id) return tr;
        return Object.assign({}, tr, {
          clips: tr.clips.map(function(cl){
            if (cl.id !== clip.id) return cl;
            return Object.assign({}, cl, {
              duration: nextDur,
              // Keep patternDuration in sync if loop is off (so when the user
              // later enables loop, it loops at the full clip length, not a
              // stale shorter pattern from before).
              patternDuration: cl.loopEnabled ? cl.patternDuration : nextDur,
            });
          }),
        });
      });
    });
  };
  const toggleLoop = function() {
    if (drumCtx) return;  // drum-channel mode: pattern loop is controlled at the rack level
    setTracks(function(prev){
      return prev.map(function(tr){
        if (tr.id !== track.id) return tr;
        return Object.assign({}, tr, {
          clips: tr.clips.map(function(cl){
            if (cl.id !== clip.id) return cl;
            return Object.assign({}, cl, { loopEnabled: !cl.loopEnabled });
          }),
        });
      });
    });
  };

  // ── Render ──
  // Compute beat/bar gridline positions once per render. Bar lines are
  // brightest, beat lines mid, subdivisions faint.
  const beatLines = [];
  const beatsTotal = Math.ceil(clipDur / beatSec) + 1;
  for (let b = 0; b <= beatsTotal; b++) {
    const t = b * beatSec;
    if (t > clipDur + 0.001) break;
    const isBar = (b % (timeSigNum || 4)) === 0;
    beatLines.push({ t: t, x: timeToX(t), isBar: isBar, idx: b });
  }
  // Subdivision lines for current snap
  const subLines = [];
  if (snapMeta.beatDiv > 0 && snapMeta.beatDiv >= 1) {
    const step = beatSec / snapMeta.beatDiv;
    for (let s = 0; s * step <= clipDur; s++) {
      const t = s * step;
      // Skip times that coincide with a beat line
      if (Math.abs(t / beatSec - Math.round(t / beatSec)) < 0.001) continue;
      subLines.push({ t: t, x: timeToX(t) });
    }
  }

  // Visible rows for keyboard rendering — only render rows that are within
  // (or near) the scroll viewport. At rowHeight=18, 127 rows = 2286px; even
  // off-screen rows render OK, but virtualization keeps it tight on weak
  // devices. We render all for now; revisit if perf complaints come in.

  // Playhead: clip-local position. Two cases:
  //   1. Standard MIDI clip: local = currentTime - clip.startTime.
  //   2. Drum-channel mode: the synthetic clip has startTime=0 / duration=patDur,
  //      so we need to find which timeline clip on the drum track is
  //      currently being played and compute the pattern-local time inside
  //      it (patterns loop within a clip so we modulo by patDur). If no
  //      clip on the timeline contains currentTime, the playhead is hidden.
  let playheadLocal = currentTime - (clip.startTime || 0);
  let playheadVisible = isPlaying && playheadLocal >= 0 && playheadLocal <= clipDur;
  if (drumCtx && isPlaying) {
    const dt = drumCtx.drumTrack;
    const pat = drumCtx.pattern;
    const stepSec = drumStepDurationSec(bpm, pat.stepsPerBeat || 4);
    const patDur = pat.lengthSteps * stepSec;
    let foundLocal = -1;
    for (const tcl of (dt.clips || [])) {
      if (tcl.active === false) continue;
      if (tcl.patternId !== pat.id) continue;
      const cs = tcl.startTime || 0;
      const ce = cs + (tcl.duration || patDur);
      if (currentTime >= cs && currentTime < ce) {
        const elapsed = currentTime - cs;
        foundLocal = patDur > 0 ? (elapsed % patDur) : elapsed;
        break;
      }
    }
    if (foundLocal >= 0) {
      playheadLocal = foundLocal;
      playheadVisible = true;
    } else {
      playheadVisible = false;
    }
  }
  const playheadX = timeToX(playheadLocal);

  // Snap step px width (for the empty-area highlight when zoomed in)
  const snapStepPx = snapMeta.beatDiv > 0 ? timeToX(beatSec / snapMeta.beatDiv) : 0;

  // Update handler ref each render so the native listeners use fresh closures.
  // (See gridHandlersRef declaration for full rationale.)
  gridHandlersRef.current = {
    start: onGridTouchStart,
    move:  onGridTouchMove,
    end:   onGridTouchEnd,
  };

  // ── Force re-render on grid mount / resize / zoom ──
  // Scrollbar thumb sizes depend on gridScrollRef.current.clientWidth
  // (the viewport) which is only available after mount. We bump a tick
  // counter on mount + on every resize so the scrollbars recompute and
  // appear/disappear correctly. We also tick on pinch zoom indirectly
  // (scroll fires → setScrollX → re-render).
  const [resizeTick, setResizeTick] = React.useState(0);
  React.useEffect(function(){
    const el = gridScrollRef.current;
    if (!el) return;
    // Initial tick to redraw scrollbars now that ref is populated.
    setResizeTick(function(n){ return n + 1; });
    // ResizeObserver isn't available in older webviews; fall back to
    // window resize listener which is good enough for orientation flips.
    let ro = null;
    if (typeof ResizeObserver !== "undefined") {
      ro = new ResizeObserver(function(){
        setResizeTick(function(n){ return n + 1; });
      });
      ro.observe(el);
    }
    const onWinResize = function(){ setResizeTick(function(n){ return n + 1; }); };
    window.addEventListener("resize", onWinResize);
    window.addEventListener("orientationchange", onWinResize);
    return function(){
      if (ro) try { ro.disconnect(); } catch(_) {}
      window.removeEventListener("resize", onWinResize);
      window.removeEventListener("orientationchange", onWinResize);
    };
  }, []);
  // resizeTick used in the scrollbar IIFE — accessing it here just to make
  // sure the IIFE re-runs when it changes (the IIFE reads .current refs,
  // which don't trigger renders by themselves).
  void resizeTick;

  return (
    <div style={{
      position:"fixed", inset: 0, zIndex: 10400,
      // Same monochrome violet luxury background as the drum rack
      background: `
        radial-gradient(ellipse 80% 60% at 18% -10%, rgba(139,92,246,0.10) 0%, transparent 70%),
        radial-gradient(ellipse 70% 50% at 95% 110%, rgba(124,58,237,0.07) 0%, transparent 70%),
        linear-gradient(180deg, rgba(167,139,250,0.025) 0%, transparent 30%),
        repeating-linear-gradient(0deg, rgba(255,255,255,0.008) 0px, rgba(255,255,255,0.008) 1px, transparent 1px, transparent 3px),
        #070611
      `,
      backdropFilter: "blur(24px) saturate(140%)",
      display:"flex", flexDirection:"column",
      paddingTop:    "var(--bf-safe-top,    env(safe-area-inset-top))",
      paddingBottom: "var(--bf-safe-bottom, env(safe-area-inset-bottom))",
      paddingLeft:   "var(--bf-safe-left,   env(safe-area-inset-left))",
      paddingRight:  "var(--bf-safe-right,  env(safe-area-inset-right))",
      color:"#F8F7FA", fontFamily:"'DM Sans',sans-serif",
      overflow: "hidden",
    }}>

      {/* ═══ TOP TOOLBAR — compact, always fits portrait ═══
          What stays always visible:
            • Brand icon + track name
            • 4 tool buttons (Pencil / Select / Eraser / Slice)
            • Chord button
            • ⋯ More (snap / scale / bars / loop / select-all live inside)
            • Undo / Redo / Paste (when clipboard has data)
            • Close ×
          Anything that doesn't fit on a phone in portrait moves to
          the More dropdown. Tablet/desktop users see everything inline. */}
      <div style={{
        flexShrink: 0,
        // Flex-wrap layout: all controls fit on one row when there's space
        // (landscape, tablet), and wrap to two rows when there isn't
        // (portrait phones). No horizontal scrolling — everything always
        // visible without discovery.
        minHeight: TOOLBAR_H,
        padding: "6px 12px",
        borderBottom: "1px solid rgba(167,139,250,0.10)",
        background: "linear-gradient(180deg, rgba(167,139,250,0.04) 0%, transparent 100%)",
        boxShadow: "inset 0 1px 0 rgba(255,255,255,0.02), 0 1px 0 rgba(0,0,0,0.4)",
        display:"flex", alignItems:"center", gap: 6,
        flexWrap: "wrap",
        rowGap: 6,
        position: "relative",
      }}>
        {/* Brand mark — also serves as the "change instrument" entry point.
            Tapping it opens the preset browser scoped to this track. The
            track color tint hints at the current preset's color. */}
        <button
          onClick={function(){
            if (onOpenPresetBrowser) onOpenPresetBrowser(track.id);
          }}
          title={"Instrument: " + (inst.name || "Grand Piano")}
          style={{
            width: 30, height: 30, borderRadius: 8,
            background: "linear-gradient(135deg," + trackColor + " 0%," + trackColor + "cc 100%)",
            display:"flex", alignItems:"center", justifyContent:"center",
            color: "#070611",
            fontSize: 14,
            border: "none",
            cursor: "pointer",
            padding: 0,
            boxShadow:
              "0 0 18px " + trackColor + "55, " +
              "inset 0 1px 0 rgba(255,255,255,0.25), " +
              "inset 0 -1px 0 rgba(0,0,0,0.2)",
            flexShrink: 0,
            fontFamily: "system-ui",
          }}><BFIcon name="piano" size={16} /></button>

        {/* Bank-preset sub-picker — only visible when the track's
            instrument is a multi-preset bank. Tapping opens a modal
            sheet showing every preset in the bank; selecting one
            updates the track's selectedSfPreset and switches the sound
            without changing the underlying track instrument. */}
        {baseInst && baseInst.presets && baseInst.presets.length > 1 && (
          <button
            onClick={function(){ setBankPresetSheetOpen(true); }}
            title={"Pick preset · " + baseInst.presets.length + " in this bank"}
            style={{
              height: 30, borderRadius: 8,
              padding: "0 10px",
              background: "rgba(34,211,238,0.10)",
              color: "#22D3EE",
              border: "1px solid rgba(34,211,238,0.30)",
              fontSize: 10, fontWeight: 800,
              cursor: "pointer",
              fontFamily: "system-ui",
              letterSpacing: 1.2, textTransform: "uppercase",
              display: "inline-flex", alignItems: "center", gap: 6,
              flexShrink: 0,
              maxWidth: 160,
              overflow: "hidden",
              whiteSpace: "nowrap",
            }}>
            <span style={{ opacity: 0.6, fontSize: 9 }}>Preset</span>
            <span style={{
              overflow: "hidden", textOverflow: "ellipsis",
              fontSize: 11,
              color: "#F8F7FA",
              fontWeight: 700, textTransform: "none", letterSpacing: 0,
              maxWidth: 100,
            }}>
              {(function(){
                // Resolve display name from the track's selectedSfPreset
                // (or first preset if no selection yet).
                const sel = track.selectedSfPreset;
                const list = baseInst.presets;
                let match = null;
                if (sel) {
                  match = list.find(function(p){
                    return p.bank === sel.bank && p.program === sel.program;
                  });
                }
                if (!match && list.length) match = list[0];
                return (match && match.name) || "—";
              })()}
            </span>
            <span style={{ opacity: 0.7, fontSize: 8 }}>▼</span>
          </button>
        )}

        {/* Tool buttons — flex-shrink: 0 so they're never squeezed off */}
        <div style={{ display:"flex", alignItems:"center", gap: 2, flexShrink: 0, marginLeft: 4 }}>
          {[
            { id: "pencil", icon: "pencil",  title: "Pencil (tap to place)" },
            { id: "select", icon: "select",  title: "Select (lasso, bulk move)" },
            { id: "eraser", icon: "eraser",  title: "Eraser (tap to delete)" },
            { id: "slice",  icon: "scissors",  title: "Slice (split note at tap)" },
            { id: "slide",  glyph: "\u2197",  title: "Slide (gliding 808 / portamento — glides the previous note's pitch into this one)" },
          ].map(function(tool){
            const active = toolMode === tool.id;
            return (
              <button key={tool.id}
                onClick={function(){
                  // Toggle: tapping the currently-active tool returns to
                  // pencil. Otherwise tools like slice/eraser are sticky
                  // and the user can't easily get back to placing notes.
                  // Pencil is the default mode so tapping it is a no-op.
                  if (toolMode === tool.id && tool.id !== "pencil") {
                    setToolMode("pencil");
                  } else {
                    setToolMode(tool.id);
                  }
                  if (chordArmed) setChordArmed(null);
                }}
                title={tool.title}
                style={{
                  background: active
                    ? "linear-gradient(135deg,#A78BFA 0%,#8B5CF6 100%)"
                    : "rgba(20,15,30,0.5)",
                  color: active ? "white" : "#9590A8",
                  border: "1px solid " + (active ? "rgba(167,139,250,0.5)" : "rgba(167,139,250,0.10)"),
                  borderRadius: 7,
                  padding: "6px 9px",
                  fontSize: 13, fontWeight: 700,
                  cursor: "pointer",
                  fontFamily: "system-ui",
                  boxShadow: active
                    ? "0 4px 14px rgba(139,92,246,0.35), inset 0 1px 0 rgba(255,255,255,0.2)"
                    : "none",
                  lineHeight: 1,
                  minWidth: 30,
                  flexShrink: 0,
                  display: "inline-flex", alignItems: "center", justifyContent: "center",
                }}>{tool.glyph
                  ? <span style={{ fontSize: 16, fontWeight: 800, lineHeight: 1 }}>{tool.glyph}</span>
                  : <BFIcon name={tool.icon} size={14} />}</button>
            );
          })}
        </div>

        {/* Re-open Sampler editor — only shows for sampler tracks. Lets
            the producer tweak slice points, pitch, tempo lock after the
            track was originally created. Re-uses the same import overlay
            in editing mode (no re-upload — the sample lives in B2). */}
        {track.samplerData && (
          <button
            onClick={function(){
              if (props.onOpenSampler) props.onOpenSampler(track.id);
            }}
            title="Re-slice / change pitch / tempo"
            style={{
              background: "rgba(34,211,238,0.10)",
              color: "#22D3EE",
              border: "1px solid rgba(34,211,238,0.30)",
              borderRadius: 7, padding: "6px 9px",
              fontSize: 13, fontWeight: 700, cursor:"pointer",
              fontFamily: "system-ui",
              lineHeight: 1,
              flexShrink: 0,
              boxShadow: "0 0 12px rgba(34,211,238,0.15)",
              display: "inline-flex", alignItems: "center", justifyContent: "center",
            }}><BFIcon name="wave" size={14} /></button>
        )}
        {/* Edit Sound (ADSR/filter) for sampler tracks — shapes the
            sample's envelope and tone, applied live and on export. */}
        {track.samplerData && (
          <button
            onClick={function(){
              if (props.onOpenEditSound) props.onOpenEditSound(track.id);
            }}
            title="Edit sound — attack, decay, filter"
            style={{
              background: track.instrumentOverrides
                ? "rgba(139,92,246,0.18)"
                : "rgba(34,211,238,0.10)",
              color: track.instrumentOverrides ? "#C4B5FD" : "#22D3EE",
              border: "1px solid " + (track.instrumentOverrides ? "rgba(167,139,250,0.4)" : "rgba(34,211,238,0.30)"),
              borderRadius: 7, padding: "6px 9px",
              fontSize: 13, fontWeight: 700, cursor:"pointer",
              fontFamily: "system-ui",
              lineHeight: 1,
              flexShrink: 0,
              display: "inline-flex", alignItems: "center", justifyContent: "center",
            }}><BFIcon name="sliders" size={14} /></button>
        )}

        {/* Chord stamp button */}
        <div style={{ position: "relative", flexShrink: 0 }}>
          <button onClick={function(){ setChordPickerOpen(function(v){ return !v; }); setMorePanelOpen(false); }}
            title="Chord stamp"
            style={{
              background: chordArmed
                ? "linear-gradient(135deg,#A78BFA 0%,#8B5CF6 100%)"
                : "rgba(20,15,30,0.5)",
              color: chordArmed ? "white" : "#C4B5FD",
              border: "1px solid " + (chordArmed ? "rgba(167,139,250,0.5)" : "rgba(167,139,250,0.20)"),
              borderRadius: 7, padding: "6px 9px",
              fontSize: 11, fontWeight: 700, cursor:"pointer",
              fontFamily: "'DM Sans',sans-serif",
              letterSpacing: 0.4,
              boxShadow: chordArmed
                ? "0 4px 14px rgba(139,92,246,0.35), inset 0 1px 0 rgba(255,255,255,0.2)"
                : "none",
              flexShrink: 0,
              whiteSpace: "nowrap",
            }}>{chordArmed ? "♫ " + chordArmed.name : "♫"}</button>
          {chordPickerOpen && (
            <div style={{
              position:"absolute", top: "calc(100% + 6px)", left: 0,
              background:"linear-gradient(165deg,rgba(28,20,42,0.95) 0%,rgba(18,14,28,0.95) 100%)",
              backdropFilter:"blur(20px) saturate(140%)",
              WebkitBackdropFilter:"blur(20px) saturate(140%)",
              border:"1px solid rgba(167,139,250,0.22)",
              borderRadius: 12, padding: 10,
              minWidth: 220,
              boxShadow:"0 16px 48px rgba(0,0,0,0.6), 0 0 0 1px rgba(167,139,250,0.08)",
              zIndex: 10500,
              display:"grid", gridTemplateColumns:"1fr 1fr 1fr", gap: 6,
            }}>
              {BF_CHORDS.map(function(c){
                const isCur = chordArmed && chordArmed.id === c.id;
                return (
                  <button key={c.id}
                    onClick={function(){
                      setChordArmed(c);
                      setChordPickerOpen(false);
                    }}
                    style={{
                      background: isCur ? "rgba(139,92,246,0.18)" : "rgba(20,15,30,0.4)",
                      color: isCur ? "#C4B5FD" : "#F0EFF5",
                      border: "1px solid " + (isCur ? "rgba(167,139,250,0.4)" : "rgba(167,139,250,0.10)"),
                      borderRadius: 8, padding:"8px 6px",
                      fontSize: 11, fontWeight: 700, cursor:"pointer",
                      fontFamily:"'DM Sans',sans-serif",
                      letterSpacing: 0.3,
                    }}>{c.name}</button>
                );
              })}
              {chordArmed && (
                <button onClick={function(){
                  setChordArmed(null);
                  setChordPickerOpen(false);
                }} style={{
                  gridColumn: "1 / -1",
                  background: "rgba(239,68,68,0.06)", color: "#F87171",
                  border: "1px solid rgba(239,68,68,0.22)",
                  borderRadius: 8, padding: "8px 12px",
                  fontSize: 11, fontWeight: 700, cursor:"pointer",
                  letterSpacing: 0.6, marginTop: 2,
                }}>Disarm</button>
              )}
            </div>
          )}
        </div>

        {/* ✦ Compose — opens the AI-style generator panel (Phase 5).
            Sits next to the chord stamp button since both are "create new
            notes" actions. Same active-state treatment as the other
            toolbar buttons. */}
        <button onClick={function(){
          setComposeOpen(function(v){ return !v; });
          setMorePanelOpen(false);
          setChordPickerOpen(false);
        }} title="Compose · generate chords / melody / arp" style={{
          background: composeOpen
            ? "linear-gradient(135deg,#A78BFA 0%,#8B5CF6 100%)"
            : "rgba(20,15,30,0.5)",
          color: composeOpen ? "white" : "#C4B5FD",
          border: "1px solid " + (composeOpen ? "rgba(167,139,250,0.5)" : "rgba(167,139,250,0.20)"),
          borderRadius: 7, padding: "6px 9px",
          fontSize: 11, fontWeight: 700, cursor:"pointer",
          fontFamily: "'DM Sans',sans-serif",
          letterSpacing: 0.4,
          boxShadow: composeOpen
            ? "0 4px 14px rgba(139,92,246,0.35), inset 0 1px 0 rgba(255,255,255,0.2)"
            : "none",
          flexShrink: 0,
          whiteSpace: "nowrap",
        }}>✦</button>

        {/* ⋯ More button — hosts snap / scale / bars / loop / select-all */}
        <button onClick={function(){
          setMorePanelOpen(function(v){ return !v; });
          setChordPickerOpen(false);
        }} title="More options" style={{
          background: morePanelOpen
            ? "rgba(139,92,246,0.18)"
            : "rgba(20,15,30,0.5)",
          color: morePanelOpen ? "#C4B5FD" : "#9590A8",
          border: "1px solid " + (morePanelOpen ? "rgba(167,139,250,0.4)" : "rgba(167,139,250,0.10)"),
          borderRadius: 7, padding: "6px 10px",
          fontSize: 14, lineHeight: 1, fontWeight: 700, cursor:"pointer",
          fontFamily: "system-ui",
          flexShrink: 0,
        }}>⋯</button>

        {/* Flexible spacer with the track name. Now that the toolbar
            horizontally scrolls, the name can take whatever space it
            wants and just gets the natural left-to-right position.
            We give it a sensible max so it doesn't push everything else
            offscreen on long instrument names. */}
        <div style={{
          flex: "0 0 auto",
          maxWidth: 140,
          padding: "0 8px",
          overflow: "hidden",
          fontSize: 12, fontWeight: 700, color: "#F8F7FA",
          whiteSpace: "nowrap", textOverflow: "ellipsis",
          letterSpacing: 0.1,
        }}>{track.name}</div>

        {/* PASTE (only when clipboard has something) */}
        {clipboard && (
          <button onClick={pasteClipboard} style={{
            background: "rgba(20,15,30,0.5)", color: "#C4B5FD",
            border: "1px solid rgba(167,139,250,0.25)",
            borderRadius: 7, padding: "6px 9px",
            fontSize: 10, fontWeight: 700, letterSpacing: 0.6,
            cursor: "pointer",
            fontFamily:"'DM Sans',sans-serif", flexShrink: 0,
          }} title="Paste at playhead">PASTE</button>
        )}
        {/* Rewind to start */}
        <button onClick={seekRewindToStart} title="Rewind to start" style={{
          background: "rgba(20,15,30,0.5)",
          color: "#C4B5FD",
          border: "1px solid rgba(167,139,250,0.20)",
          borderRadius: 7, padding: "6px 9px",
          fontSize: 12, lineHeight: 1, fontWeight: 700, cursor:"pointer",
          fontFamily: "system-ui", flexShrink: 0, minWidth: 30,
          display:"inline-flex", alignItems:"center", justifyContent:"center",
        }}><BFIcon name="rewindStart" size={13} /></button>
        {/* Back one bar */}
        <button onClick={seekBackOneBar} title="Back one bar" style={{
          background: "rgba(20,15,30,0.5)",
          color: "#9590A8",
          border: "1px solid rgba(167,139,250,0.15)",
          borderRadius: 7, padding: "6px 9px",
          fontSize: 11, lineHeight: 1, fontWeight: 700, cursor:"pointer",
          fontFamily: "system-ui", flexShrink: 0, minWidth: 30,
          display:"inline-flex", alignItems:"center", justifyContent:"center",
        }}><BFIcon name="backBar" size={13} /></button>
        {/* Play / Pause — editor-local mini transport (PATTERN mode).
            Loops just this clip. The cyan SONG button next to it plays
            the full timeline arrangement instead (FL Studio-style). */}
        <button onClick={toggleLocalPlay} title={isPatPlaying ? "Stop pattern" : "Play pattern (loop this clip)"} style={{
          background: isPatPlaying
            ? "linear-gradient(135deg,#A78BFA 0%,#8B5CF6 100%)"
            : "rgba(20,15,30,0.5)",
          color: isPatPlaying ? "white" : "#C4B5FD",
          border: "1px solid " + (isPatPlaying ? "rgba(167,139,250,0.5)" : "rgba(167,139,250,0.25)"),
          borderRadius: 7, padding: "6px 11px",
          fontSize: 12, lineHeight: 1, fontWeight: 700, cursor:"pointer",
          fontFamily: "system-ui",
          flexShrink: 0,
          boxShadow: isPatPlaying
            ? "0 4px 14px rgba(139,92,246,0.45), inset 0 1px 0 rgba(255,255,255,0.2)"
            : "none",
          minWidth: 34,
          display:"inline-flex", alignItems:"center", justifyContent:"center",
          position: "relative",
        }}>
          <BFIcon name={isPatPlaying ? "stop" : "play"} size={13} />
          {/* Mode label tucked below the icon — tiny so it doesn't bulk
              up the toolbar. Cyan stripe identifies SONG button. */}
          <span style={{
            position: "absolute",
            bottom: -1, left: 0, right: 0,
            fontSize: 6, fontWeight: 800, letterSpacing: 0.6,
            color: isPatPlaying ? "rgba(255,255,255,0.85)" : "#9590A8",
            textAlign: "center",
            pointerEvents: "none",
            lineHeight: 1,
          }}>PAT</span>
        </button>
        {/* SONG play — triggers the main timeline transport so the producer
            can write a melody over their full arrangement. Cyan accent
            distinguishes it from the purple PATTERN button. When the
            timeline is playing this lights up; the editor's own play
            button (PAT) becomes secondary. */}
        {onTransportPlay && (
          <button onClick={onTransportPlay} title={isPlaying ? "Stop song" : "Play full song"} style={{
            background: isPlaying
              ? "linear-gradient(135deg,#22D3EE 0%,#06B6D4 100%)"
              : "rgba(20,15,30,0.5)",
            color: isPlaying ? "white" : "#A5F3FC",
            border: "1px solid " + (isPlaying ? "rgba(34,211,238,0.5)" : "rgba(34,211,238,0.25)"),
            borderRadius: 7, padding: "6px 11px",
            fontSize: 12, lineHeight: 1, fontWeight: 700, cursor:"pointer",
            fontFamily: "system-ui",
            flexShrink: 0,
            boxShadow: isPlaying
              ? "0 4px 14px rgba(6,182,212,0.45), inset 0 1px 0 rgba(255,255,255,0.2)"
              : "none",
            minWidth: 34,
            display:"inline-flex", alignItems:"center", justifyContent:"center",
            position: "relative",
          }}>
            <BFIcon name={isPlaying ? "stop" : "play"} size={13} />
            <span style={{
              position: "absolute",
              bottom: -1, left: 0, right: 0,
              fontSize: 6, fontWeight: 800, letterSpacing: 0.6,
              color: isPlaying ? "rgba(255,255,255,0.85)" : "#7DD3DD",
              textAlign: "center",
              pointerEvents: "none",
              lineHeight: 1,
            }}>SONG</span>
          </button>
        )}
        {/* Cut Itself toggle — only in drum-channel mode. Producers expect
            note-cut behaviour to be configurable from the piano roll itself
            (FL Studio's Channel Rack puts it on each row but BeatFinder's
            pad-scope editor IS the row for one pad, so the toggle lives
            here). Tap to flip. ON = each new hit cuts any still-ringing
            previous hit on this pad. */}
        {drumCtx && (function(){
          const cutOn = !!drumCtx.channel.chokeEnabled;
          return (
            <button onClick={function(){ if (typeof onToggleCutItself === "function") onToggleCutItself(); }}
              title={cutOn ? "Cut Itself: ON — new hits cut previous on this pad" : "Cut Itself: OFF — hits overlap freely"}
              style={{
                background: cutOn
                  ? "linear-gradient(135deg,#A78BFA 0%,#7C3AED 100%)"
                  : "rgba(20,15,30,0.5)",
                color: cutOn ? "white" : "#9590A8",
                border: "1px solid " + (cutOn ? "rgba(167,139,250,0.5)" : "rgba(167,139,250,0.15)"),
                borderRadius: 7, padding: "6px 9px",
                fontSize: 9, lineHeight: 1, fontWeight: 800, cursor:"pointer",
                fontFamily: "system-ui", flexShrink: 0,
                letterSpacing: 0.6, textTransform: "uppercase",
                boxShadow: cutOn
                  ? "0 4px 12px rgba(139,92,246,0.4), inset 0 1px 0 rgba(255,255,255,0.18)"
                  : "none",
              }}>CUT</button>
          );
        })()}
        {/* Forward one bar */}
        <button onClick={seekFwdOneBar} title="Forward one bar" style={{
          background: "rgba(20,15,30,0.5)",
          color: "#9590A8",
          border: "1px solid rgba(167,139,250,0.15)",
          borderRadius: 7, padding: "6px 9px",
          fontSize: 11, lineHeight: 1, fontWeight: 700, cursor:"pointer",
          fontFamily: "system-ui", flexShrink: 0, minWidth: 30,
          display:"inline-flex", alignItems:"center", justifyContent:"center",
        }}><BFIcon name="fwdBar" size={13} /></button>
        {/* Skip to end */}
        <button onClick={seekToEnd} title="Skip to end" style={{
          background: "rgba(20,15,30,0.5)",
          color: "#C4B5FD",
          border: "1px solid rgba(167,139,250,0.20)",
          borderRadius: 7, padding: "6px 9px",
          fontSize: 12, lineHeight: 1, fontWeight: 700, cursor:"pointer",
          fontFamily: "system-ui", flexShrink: 0, minWidth: 30,
          display:"inline-flex", alignItems:"center", justifyContent:"center",
        }}><BFIcon name="skipEnd" size={13} /></button>
        {/* Undo / Redo */}
        <button onClick={undo} disabled={undoStack.length === 0} style={{
          background: undoStack.length === 0 ? "rgba(20,15,30,0.2)" : "rgba(20,15,30,0.5)",
          color: undoStack.length === 0 ? "#3A3548" : "#9590A8",
          border: "1px solid rgba(167,139,250,0.10)",
          borderRadius: 7, padding: "6px 10px",
          fontSize: 14, fontWeight: 600, cursor: undoStack.length === 0 ? "not-allowed" : "pointer",
          fontFamily:"'DM Sans',sans-serif", flexShrink: 0, lineHeight: 1,
          display:"inline-flex", alignItems:"center", justifyContent:"center",
        }} title="Undo"><BFIcon name="undo" size={13} /></button>
        <button onClick={redo} disabled={redoStack.length === 0} style={{
          background: redoStack.length === 0 ? "rgba(20,15,30,0.2)" : "rgba(20,15,30,0.5)",
          color: redoStack.length === 0 ? "#3A3548" : "#9590A8",
          border: "1px solid rgba(167,139,250,0.10)",
          borderRadius: 7, padding: "6px 10px",
          fontSize: 14, fontWeight: 600, cursor: redoStack.length === 0 ? "not-allowed" : "pointer",
          fontFamily:"'DM Sans',sans-serif", flexShrink: 0, lineHeight: 1,
          display:"inline-flex", alignItems:"center", justifyContent:"center",
        }} title="Redo"><BFIcon name="redo" size={13} /></button>
        {/* Close */}
        <button onClick={onClose} style={{
          background: "rgba(20,15,30,0.4)", color:"#9590A8",
          border: "1px solid rgba(167,139,250,0.10)",
          borderRadius: 8, padding: "6px 9px",
          fontSize: 16, lineHeight: 1, cursor:"pointer",
          fontWeight: 300, fontFamily: "system-ui",
          flexShrink: 0,
          display:"inline-flex", alignItems:"center", justifyContent:"center",
        }}><BFIcon name="close" size={13} /></button>

        {/* ─── More dropdown panel ───
            Slides down from the ⋯ button. Holds Snap / Scale / Bars /
            Loop / Select-All. Absolute-positioned so it doesn't push
            anything around when it opens. Tapping outside closes via
            the overlay div underneath. */}
        {morePanelOpen && (
          <>
            {/* Tap-out scrim — closes the panel when user taps anywhere
                in the editor outside the panel itself. Sits below the
                panel's z-index so the panel still receives input. */}
            <div onClick={function(){ setMorePanelOpen(false); }}
              style={{
                position: "fixed", inset: 0,
                zIndex: 10499,
                background: "transparent",
              }} />
            <div style={{
              position: "absolute",
              top: "calc(100% + 6px)",
              right: 12,
              minWidth: 260, maxWidth: "calc(100vw - 24px)",
              background:"linear-gradient(165deg,rgba(28,20,42,0.95) 0%,rgba(18,14,28,0.95) 100%)",
              backdropFilter:"blur(24px) saturate(140%)",
              WebkitBackdropFilter:"blur(24px) saturate(140%)",
              border:"1px solid rgba(167,139,250,0.22)",
              borderRadius: 14, padding: 14,
              boxShadow:
                "0 24px 64px rgba(0,0,0,0.7), " +
                "0 0 0 1px rgba(167,139,250,0.08), " +
                "inset 0 1px 0 rgba(255,255,255,0.04)",
              zIndex: 10500,
              display:"flex", flexDirection:"column", gap: 12,
            }}>
              {/* Snap */}
              <div>
                <div style={{ fontSize: 9, color:"#7A7488", letterSpacing: 1.8, textTransform:"uppercase", fontWeight: 700, marginBottom: 6 }}>Snap</div>
                <div style={{ display:"flex", gap: 4, flexWrap:"wrap" }}>
                  {BF_SNAP_OPTIONS.map(function(s){
                    const sel = snapId === s.id;
                    return (
                      <button key={s.id} onClick={function(){ setSnapId(s.id); }}
                        style={{
                          background: sel ? "rgba(139,92,246,0.18)" : "rgba(20,15,30,0.4)",
                          color: sel ? "#C4B5FD" : "#9590A8",
                          border: "1px solid " + (sel ? "rgba(167,139,250,0.4)" : "rgba(167,139,250,0.08)"),
                          borderRadius: 7, padding:"6px 10px",
                          fontSize: 11, fontWeight: 700, cursor:"pointer",
                          fontFamily:"'DM Sans',sans-serif",
                          letterSpacing: 0.3,
                          flex: "1 1 auto", minWidth: 50,
                        }}>{s.label}</button>
                    );
                  })}
                </div>
              </div>
              {/* Bars */}
              <div>
                <div style={{ fontSize: 9, color:"#7A7488", letterSpacing: 1.8, textTransform:"uppercase", fontWeight: 700, marginBottom: 6 }}>Length</div>
                <div style={{ display:"flex", gap: 4 }}>
                  {[1, 2, 4, 8, 16].map(function(b){
                    const dur = b * barSec;
                    const sel = Math.abs(clipDur - dur) < 0.05;
                    return (
                      <button key={b} onClick={function(){ setClipDuration(dur); }} style={{
                        background: sel
                          ? "linear-gradient(135deg,#8B5CF6 0%,#7C3AED 100%)"
                          : "rgba(20,15,30,0.5)",
                        color: sel ? "white" : "#9590A8",
                        border: sel ? "1px solid rgba(167,139,250,0.4)" : "1px solid rgba(167,139,250,0.08)",
                        borderRadius: 7, padding: "6px 10px",
                        fontSize: 11, fontWeight: 700, cursor:"pointer",
                        fontFamily:"'DM Sans',sans-serif",
                        letterSpacing: 0.3,
                        flex: "1 1 0",
                      }}>{b} bar{b > 1 ? "s" : ""}</button>
                    );
                  })}
                </div>
              </div>
              {/* Sound section — instrument selector + edit sound */}
              <div>
                <div style={{ fontSize: 9, color:"#7A7488", letterSpacing: 1.8, textTransform:"uppercase", fontWeight: 700, marginBottom: 6 }}>Sound</div>
                <div style={{ display:"flex", gap: 8 }}>
                  <button onClick={function(){
                    if (onOpenPresetBrowser) onOpenPresetBrowser(track.id);
                    setMorePanelOpen(false);
                  }} style={{
                    flex: 1,
                    background: "linear-gradient(165deg, rgba(167,139,250,0.10) 0%, rgba(20,15,30,0.6) 100%)",
                    color: "#F8F7FA",
                    border: "1px solid rgba(167,139,250,0.20)",
                    borderRadius: 8, padding: "10px 12px",
                    fontSize: 11, fontWeight: 700, cursor:"pointer",
                    fontFamily:"'DM Sans',sans-serif",
                    letterSpacing: 0.3,
                    textAlign: "left",
                    overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap",
                  }} title="Change instrument">
                    <span style={{ fontSize: 9, color: "#7A7488", letterSpacing: 1.4, textTransform: "uppercase", fontWeight: 700, display: "block" }}>Instrument</span>
                    <span style={{ fontSize: 12 }}>{inst.name}</span>
                  </button>
                  <button onClick={function(){
                    if (onOpenEditSound) onOpenEditSound(track.id);
                    setMorePanelOpen(false);
                  }} style={{
                    flex: "0 0 auto",
                    background: track.instrumentOverrides
                      ? "rgba(139,92,246,0.18)"
                      : "rgba(20,15,30,0.4)",
                    color: track.instrumentOverrides ? "#C4B5FD" : "#9590A8",
                    border: "1px solid " + (track.instrumentOverrides ? "rgba(167,139,250,0.4)" : "rgba(167,139,250,0.10)"),
                    borderRadius: 8, padding: "10px 14px",
                    fontSize: 11, fontWeight: 700, cursor:"pointer",
                    fontFamily:"'DM Sans',sans-serif",
                    letterSpacing: 0.6,
                  }} title="Edit ADSR + filter">Edit</button>
                </div>
              </div>
              {/* Loop + Select All */}
              <div style={{ display:"flex", gap: 8 }}>
                <button onClick={toggleLoop} style={{
                  flex: 1,
                  background: clip.loopEnabled ? "rgba(139,92,246,0.18)" : "rgba(20,15,30,0.4)",
                  color: clip.loopEnabled ? "#C4B5FD" : "#9590A8",
                  border: "1px solid " + (clip.loopEnabled ? "rgba(167,139,250,0.4)" : "rgba(167,139,250,0.10)"),
                  borderRadius: 8, padding: "8px 12px",
                  fontSize: 11, fontWeight: 700, cursor:"pointer",
                  fontFamily:"'DM Sans',sans-serif",
                  letterSpacing: 0.4,
                }}>{clip.loopEnabled ? "↻ Loop On" : "↻ Loop Off"}</button>
                <button onClick={function(){ selectAll(); setMorePanelOpen(false); }}
                  disabled={notes.length === 0}
                  style={{
                    flex: 1,
                    background: notes.length === 0 ? "rgba(20,15,30,0.2)" : "rgba(20,15,30,0.4)",
                    color: notes.length === 0 ? "#3A3548" : "#C4B5FD",
                    border: "1px solid rgba(167,139,250,0.10)",
                    borderRadius: 8, padding: "8px 12px",
                    fontSize: 11, fontWeight: 700, letterSpacing: 0.6,
                    cursor: notes.length === 0 ? "not-allowed" : "pointer",
                    fontFamily:"'DM Sans',sans-serif",
                  }}>Select All</button>
              </div>
              {/* Note Cut — toggle. Off: notes overlap freely (chords,
                  pads). On: any new note cuts the previous one regardless
                  of pitch (leads, basses, monophonic instruments). */}
              <div>
                <div style={{
                  fontSize: 9, color: "#7A7488", letterSpacing: 1.4,
                  textTransform: "uppercase", fontWeight: 700,
                  marginBottom: 6,
                }}>Note Cut</div>
                <div style={{ display:"flex", gap: 6 }}>
                  {[
                    { id: "off",  label: "Off" },
                    { id: "mono", label: "On"  },
                  ].map(function(opt){
                    // Note-cut state differs by context: MIDI tracks use
                    // track.noteCutMode; drum-pad channels use the channel's
                    // chokeEnabled ("Cut Itself"). The drum scheduler reads
                    // chokeEnabled, NOT noteCutMode — so on a pad we must
                    // read/write that instead (the synthetic editor track
                    // isn't in the real tracks array, which is why writing
                    // noteCutMode there silently did nothing).
                    var cur = drumCtx
                      ? ((drumCtx.channel && drumCtx.channel.chokeEnabled) ? "mono" : "off")
                      : ((track.noteCutMode === "mono") ? "mono" : "off");
                    var active = cur === opt.id;
                    return (
                      <button key={opt.id} onClick={function(){
                        if (drumCtx) {
                          // Set chokeEnabled to the exact desired state on the
                          // real drum channel (idempotent — tapping the active
                          // option is a no-op).
                          var wantOn = (opt.id === "mono");
                          setTracks(function(prev){
                            return prev.map(function(t){
                              if (t.id !== drumCtx.drumTrack.id) return t;
                              return Object.assign({}, t, {
                                patterns: (t.patterns || []).map(function(p){
                                  if (p.id !== drumCtx.pattern.id) return p;
                                  return Object.assign({}, p, {
                                    channels: p.channels.map(function(c, ci){
                                      if (ci !== drumCtx.channelIdx) return c;
                                      return Object.assign({}, c, { chokeEnabled: wantOn });
                                    }),
                                  });
                                }),
                              });
                            });
                          });
                        } else {
                          setTracks(function(prev){
                            return prev.map(function(t){
                              if (t.id !== track.id) return t;
                              return Object.assign({}, t, { noteCutMode: opt.id });
                            });
                          });
                        }
                      }} style={{
                        flex: 1,
                        background: active ? "rgba(139,92,246,0.18)" : "rgba(20,15,30,0.4)",
                        color: active ? "#C4B5FD" : "#9590A8",
                        border: "1px solid " + (active ? "rgba(167,139,250,0.4)" : "rgba(167,139,250,0.10)"),
                        borderRadius: 8, padding: "8px 6px",
                        fontSize: 11, fontWeight: 700, cursor:"pointer",
                        fontFamily:"'DM Sans',sans-serif",
                        letterSpacing: 0.4,
                      }}>{opt.label}</button>
                    );
                  })}
                </div>
                <div style={{
                  fontSize: 10, color: "#6A6478", marginTop: 6,
                  letterSpacing: 0.1, lineHeight: 1.3,
                }}>{
                  (drumCtx
                    ? (drumCtx.channel && drumCtx.channel.chokeEnabled)
                    : (track.noteCutMode === "mono"))
                    ? "Each new note cuts the previous one. Best for 808 rolls, leads and basses."
                    : "Notes overlap freely. Best for chords and pads."
                }</div>
              </div>
            </div>
          </>
        )}
      </div>

      {/* ═══ MAIN BODY — keyboard + grid + velocity lane ═══ */}
      <div style={{
        flex: 1,
        minHeight: 0,                      // critical for nested flex scrolling
        display: "grid",
        gridTemplateColumns: KEYBOARD_W + "px 1fr",
        gridTemplateRows: RULER_H + "px 1fr " + VEL_LANE_H + "px",
        gridTemplateAreas: "'kb-tl ruler' 'kb grid' 'kb-sp vel'",
      }}>
        {/* ─ Ruler corner (top-left, sits above the keyboard column) ─ */}
        <div style={{
          gridArea: "kb-tl",
          background: "rgba(13,10,20,0.7)",
          borderRight: "1px solid rgba(167,139,250,0.10)",
          borderBottom: "1px solid rgba(167,139,250,0.15)",
        }} />
        {/* ─ Ruler (beats + bars across the top, scrolls with grid) ─
            Shows BARS and BEATS aligned with the timeline arrangement so
            producers know exactly which bar a note will fire on. Uses
            project bpm + timeSig to compute spacing — same math as the
            main timeline ruler. Tapping the ruler seeks both the editor's
            local playhead AND the master transport — matches the timeline
            arrangement's tap-to-seek behaviour so a producer can preview
            a melody from any bar/beat. */}
        <div
          onMouseDown={function(e){
            const target = e.currentTarget;
            const rect = target.getBoundingClientRect();
            const localT = Math.max(0, ((e.clientX - rect.left) + scrollX) / pxPerSec);
            const snapped = snapTimeToGrid(localT, snapMeta.beatDiv, beatSec);
            // Editor's local playhead (PAT preview, non-delegated case)
            seekToTime(snapped);
            // Master timeline — clip-relative localT + clip's start time
            if (onTransportSeek) onTransportSeek((clip.startTime || 0) + snapped);
            // PAT re-anchor for delegated schedulers (drum-channel mode)
            if (typeof onPatSeek === "function") {
              try { onPatSeek(snapped); } catch(_){}
            }
          }}
          onTouchStart={function(e){
            if (!e.touches || e.touches.length === 0) return;
            const target = e.currentTarget;
            const rect = target.getBoundingClientRect();
            const localT = Math.max(0, ((e.touches[0].clientX - rect.left) + scrollX) / pxPerSec);
            const snapped = snapTimeToGrid(localT, snapMeta.beatDiv, beatSec);
            seekToTime(snapped);
            if (onTransportSeek) onTransportSeek((clip.startTime || 0) + snapped);
            if (typeof onPatSeek === "function") {
              try { onPatSeek(snapped); } catch(_){}
            }
          }}
          style={{
          gridArea: "ruler",
          position: "relative",
          overflow: "hidden",   // scrolls via transform to stay in sync with grid
          background: "linear-gradient(180deg, rgba(20,15,30,0.75), rgba(13,10,20,0.7))",
          borderBottom: "1px solid rgba(167,139,250,0.15)",
          cursor: "pointer",
          touchAction: "manipulation",   // disable iOS double-tap zoom on the ruler
        }}>
          <div style={{
            position: "absolute", top: 0, left: 0,
            width: Math.max(clipDur * pxPerSec, 1),
            height: RULER_H,
            transform: "translateX(" + (-scrollX) + "px)",
            willChange: "transform",
            pointerEvents: "none",
          }}>
            {(function(){
              // Bar/beat ticks. Use the same beatSec as the editor so the
              // ticks line up with the grid's beat lines below.
              const barCount = Math.max(1, Math.ceil(clipDur / barSec) + 1);
              const ticks = [];
              for (let b = 0; b < barCount; b++) {
                const barT = b * barSec;
                if (barT > clipDur + 0.001) break;
                const x = barT * pxPerSec;
                ticks.push(
                  <React.Fragment key={"bar-" + b}>
                    <div style={{
                      position: "absolute", left: x, top: 0, bottom: 0,
                      width: 1, background: "rgba(167,139,250,0.45)",
                    }} />
                    <div style={{
                      position: "absolute", left: x + 4, top: 3,
                      fontSize: 10, fontWeight: 700, color: "#C4B5FD",
                      letterSpacing: 0.4, fontFamily: "'DM Sans',sans-serif",
                      fontVariantNumeric: "tabular-nums",
                    }}>{b + 1}</div>
                  </React.Fragment>
                );
                // Sub-beat ticks (lighter)
                for (let bb = 1; bb < (timeSigNum || 4); bb++) {
                  const subT = barT + bb * beatSec;
                  if (subT > clipDur + 0.001) break;
                  const sx = subT * pxPerSec;
                  ticks.push(
                    <div key={"beat-" + b + "-" + bb} style={{
                      position: "absolute", left: sx, top: RULER_H * 0.5, bottom: 0,
                      width: 1, background: "rgba(167,139,250,0.18)",
                    }} />
                  );
                }
              }
              return ticks;
            })()}
            {/* Playhead indicator on the ruler (matches grid playhead).
                Visible only when the editor is locally playing or when
                the master transport is playing AND this clip is in
                scope. */}
            {(isPatPlaying || playheadVisible) && (function(){
              // PAT: read from REF (fresh, updated every RAF frame by beamFn).
              // Reading from state would be stale (state never updates during
              // PAT playback) — and scroll-triggered re-renders would snap
              // the playhead back to the stale value, causing visible shake.
              const patT = (localPlayheadSecExternal != null)
                ? localPlayheadSecExternal
                : (isPatPlaying ? localPlayheadSecRef.current : localPlayheadSec);
              const x = isPatPlaying ? patT * pxPerSec : playheadX;
              return (
                <div
                  data-bf-pat-playhead={isPatPlaying ? "1" : undefined}
                  data-bf-pps={isPatPlaying ? pxPerSec : undefined}
                  style={{
                    position: "absolute",
                    left: 0,
                    transform: "translateX(" + x + "px)",
                    top: 0, bottom: 0, width: 2,
                    background: "linear-gradient(180deg, #22D3EE, #8B5CF6)",
                    boxShadow: "0 0 8px rgba(34,211,238,0.6)",
                    willChange: isPatPlaying ? "transform" : undefined,
                  }} />
              );
            })()}
          </div>
        </div>
        {/* ─ Keyboard column (sticky left) ─ */}
        <div
          ref={function(el){ keyboardScrollRef.current = el; }}
          style={{
            gridArea: "kb",
            background: "rgba(13,10,20,0.5)",
            borderRight: "1px solid rgba(167,139,250,0.10)",
            overflow: "hidden",   // scroll-synced to grid; no native scrollbar
            position: "relative",
          }}>
          <div
            ref={keyboardInnerRef}
            style={{
            height: gridH, position: "relative",
            // Sync vertical position with the grid via transform.
            // (Using scrollTop on overflow:hidden doesn't work; transforms do
            // and are GPU-accelerated so they don't add scroll-lag.)
            transform: "translateY(" + (-scrollY) + "px)",
            willChange: "transform",
          }}>
            {Array.from({length: TOTAL_ROWS}, function(_, ri){
              const p = PITCH_MAX - ri;
              const top = ri * rowHeight;
              const black = isBlackKey(p);
              const isC = (((p % 12) + 12) % 12) === 0;
              const isPressed = pressedKeys.has(p);
              // Show pitch label every C (e.g. C4) for orientation.
              // Pressed keys override background + add a neon-violet glow
              // so the producer can see at a glance which key they hit.
              return (
                <div key={p}
                  onTouchStart={function(e){
                    e.preventDefault();
                    onKeyTap(p);
                  }}
                  onTouchEnd={function(e){
                    e.preventDefault();
                    onKeyRelease(p);
                  }}
                  onTouchCancel={function(e){
                    e.preventDefault();
                    onKeyRelease(p);
                  }}
                  onMouseDown={function(){ onKeyTap(p); }}
                  onMouseUp={function(){ onKeyRelease(p); }}
                  onMouseLeave={function(){ onKeyRelease(p); }}
                  style={{
                    position: "absolute",
                    top: top, left: 0, right: 0, height: rowHeight,
                    background: isPressed
                      ? "linear-gradient(90deg, #A78BFA 0%, #8B5CF6 60%, #7C3AED 100%)"
                      : (black
                          ? "linear-gradient(90deg, #0a0712 0%, #110b1c 100%)"
                          : "linear-gradient(90deg, #1a1426 0%, #221a32 100%)"),
                    borderBottom: "1px solid " + (isC ? "rgba(167,139,250,0.18)" : "rgba(0,0,0,0.4)"),
                    color: isPressed ? "#070611" : (black ? "#9590A8" : "#F0EFF5"),
                    display:"flex", alignItems:"center", justifyContent:"flex-end",
                    paddingRight: 6,
                    fontSize: 8, fontWeight: 700, letterSpacing: 0.4,
                    textTransform: "uppercase",
                    cursor: "pointer",
                    userSelect: "none",
                    touchAction: "manipulation",
                    boxShadow: isPressed
                      ? "0 0 14px rgba(167,139,250,0.85), 0 0 28px rgba(139,92,246,0.45), inset 0 0 8px rgba(255,255,255,0.18)"
                      : "none",
                    transition: isPressed
                      ? "background 40ms ease-out, box-shadow 40ms ease-out"
                      : "background 180ms ease-out, box-shadow 220ms ease-out",
                  }}>
                  {isC ? midiNoteName(p) : ""}
                </div>
              );
            })}
          </div>
        </div>
        {/* ─ Grid (scrollable both axes) ─ */}
        <div
          ref={function(el){
            gridScrollRef.current = el;
            // Attach native touch listeners. Passive: true so iOS engages
            // smooth native scrolling immediately on touchmove without
            // waiting for a preventDefault decision. Scroll blocking
            // during active note drags is handled by dynamic
            // touchAction (set to "none" when dragRef.current is set
            // to a committed drag kind, "pan-x pan-y" otherwise).
            //
            // We dispatch through *refs* that we update every render so the
            // native listener always invokes the current closure (i.e. it
            // sees the latest `notes`, `snapMeta`, etc). Attaching once and
            // calling onGridTouchStart directly would freeze on stale state.
            if (el && !el._bfPianoListenersInstalled) {
              el._bfPianoListenersInstalled = true;
              el.addEventListener("touchstart", function(ev){
                const fn = gridHandlersRef.current && gridHandlersRef.current.start;
                if (fn) fn(ev);
              }, { passive: true });
              el.addEventListener("touchmove",  function(ev){
                const fn = gridHandlersRef.current && gridHandlersRef.current.move;
                if (fn) fn(ev);
              }, { passive: true });
              el.addEventListener("touchend",   function(ev){
                const fn = gridHandlersRef.current && gridHandlersRef.current.end;
                if (fn) fn(ev);
              }, { passive: true  });
              el.addEventListener("touchcancel",function(ev){
                const fn = gridHandlersRef.current && gridHandlersRef.current.end;
                if (fn) fn(ev);
              }, { passive: true  });
            }
          }}
          onScroll={handleGridScroll}
          style={{
            gridArea: "grid",
            overflow: "auto",
            WebkitOverflowScrolling: "touch",
            position: "relative",
            background: "#0a0814",
            touchAction: "pan-x pan-y",   // allow scroll, intercept pinch via touches.length>=2
          }}>
          <div style={{
            width: gridW, height: gridH,
            position: "relative",
          }}>
            {/* ─ Row backgrounds (alternating + scale highlight) ─ */}
            {Array.from({length: TOTAL_ROWS}, function(_, ri){
              const p = PITCH_MAX - ri;
              const black = isBlackKey(p);
              const inScale = isPitchInScale(p, scaleRoot, scaleMeta.intervals);
              // Off-scale rows render dimmer; in-scale rows brighter; C rows
              // get a faint accent line to help orient.
              const isC = (((p % 12) + 12) % 12) === 0;
              let bg = black ? "rgba(8,6,16,0.85)" : "rgba(18,14,28,0.55)";
              if (scaleMeta.intervals && !inScale) {
                // Dim off-scale: blend with deeper black
                bg = black ? "rgba(6,4,12,0.92)" : "rgba(14,11,22,0.75)";
              } else if (scaleMeta.intervals && inScale) {
                // Highlight in-scale: subtle violet tint
                bg = black ? "rgba(18,12,30,0.85)" : "rgba(28,20,46,0.6)";
              }
              return (
                <div key={p} style={{
                  position:"absolute", left: 0, right: 0,
                  top: ri * rowHeight, height: rowHeight,
                  background: bg,
                  borderBottom: isC
                    ? "1px solid rgba(167,139,250,0.18)"
                    : "1px solid rgba(255,255,255,0.025)",
                  pointerEvents: "none",
                }} />
              );
            })}
            {/* ─ Subdivision vertical lines (faint) ─ */}
            {subLines.map(function(s, i){
              return (
                <div key={"sub"+i} style={{
                  position:"absolute", top: 0, bottom: 0,
                  left: s.x, width: 1,
                  background: "rgba(167,139,250,0.04)",
                  pointerEvents: "none",
                }} />
              );
            })}
            {/* ─ Beat / bar vertical lines ─ */}
            {beatLines.map(function(b, i){
              return (
                <div key={"beat"+i} style={{
                  position:"absolute", top: 0, bottom: 0,
                  left: b.x, width: b.isBar ? 1.5 : 1,
                  background: b.isBar
                    ? "rgba(167,139,250,0.20)"
                    : "rgba(167,139,250,0.09)",
                  pointerEvents: "none",
                }} />
              );
            })}
            {/* ─ Clip-end boundary (visual cap) ─ */}
            <div style={{
              position:"absolute", top: 0, bottom: 0,
              left: timeToX(clipDur), width: 2,
              background: "linear-gradient(180deg, rgba(167,139,250,0.45), rgba(139,92,246,0.25))",
              pointerEvents: "none",
              boxShadow: "0 0 8px rgba(139,92,246,0.5)",
            }} />
            <div style={{
              position:"absolute", top: 0, bottom: 0,
              left: timeToX(clipDur), right: 0,
              background: "rgba(0,0,0,0.4)",
              pointerEvents: "none",
            }} />
            {/* ─ Notes ─ */}
            {notes.map(function(n){
              const isSel = selNoteIds.has(n.id);
              const isSlide = !!n.slide;
              const x = timeToX(n.start);
              const w = Math.max(4, timeToX(n.duration));
              const y = pitchToY(n.pitch);
              const h = Math.max(6, rowHeight - 2);
              const vel = n.velocity == null ? 0.85 : n.velocity;
              return (
                <div key={n.id} style={{
                  position:"absolute",
                  left: x, top: y + 1, width: w, height: h,
                  borderRadius: Math.min(4, h / 2),
                  background: isSlide
                    ? "repeating-linear-gradient(115deg, " + trackColor + " 0px, " + trackColor + " 5px, " + trackColor + "77 5px, " + trackColor + "77 10px)"
                    : "linear-gradient(180deg, " + trackColor + " 0%, " + trackColor + "cc 100%)",
                  border: isSel
                    ? "1.5px solid #F8F7FA"
                    : (isSlide ? "1px dashed #FBBF24" : "1px solid " + trackColor),
                  boxShadow: isSel
                    ? "0 0 12px " + trackColor + "cc, inset 0 1px 0 rgba(255,255,255,0.3)"
                    : (isSlide
                        ? "0 0 8px rgba(251,191,36,0.45), inset 0 1px 0 rgba(255,255,255,0.18)"
                        : "0 0 6px " + trackColor + "66, inset 0 1px 0 rgba(255,255,255,0.18), inset 0 -1px 0 rgba(0,0,0,0.18)"),
                  opacity: 0.55 + 0.45 * vel,
                  pointerEvents: "none",     // hit testing is canvas-level
                  zIndex: isSel ? 3 : 2,
                }} />
              );
            })}
            {/* ─ Playhead beam (master transport) ─ */}
            {playheadVisible && (
              <div style={{
                position:"absolute", top: 0, bottom: 0,
                left: playheadX - 1, width: 2,
                background: "linear-gradient(180deg, rgba(196,181,253,0.9), rgba(139,92,246,0.6))",
                boxShadow: "0 0 16px rgba(139,92,246,0.6)",
                pointerEvents: "none",
                zIndex: 5,
              }} />
            )}
            {/* ─ Local playhead beam (editor mini-transport) ─
                Brighter / wider than the master beam so the user can tell
                them apart when both are visible (e.g. master playing in
                the background while the editor scrubs locally). When the
                parent delegates PAT (drum-channel mode), the playhead
                position comes from the external source. */}
            {isPatPlaying && (function(){
              // Read from ref — see comment on the first playhead. State is
              // stale during PAT; ref is fresh.
              const playheadT = (localPlayheadSecExternal != null)
                ? localPlayheadSecExternal
                : localPlayheadSecRef.current;
              const x = timeToX(playheadT);
              return (
                <div
                  data-bf-pat-playhead="1"
                  data-bf-pps={pxPerSec}
                  data-bf-offset="-1.5"
                  style={{
                    position:"absolute", top: 0, bottom: 0,
                    left: 0,
                    transform: "translateX(" + (x - 1.5) + "px)",
                    width: 3,
                    background: "linear-gradient(180deg, #F8F7FA, #C4B5FD)",
                    boxShadow:
                      "0 0 22px rgba(196,181,253,0.85), " +
                      "0 0 8px rgba(255,255,255,0.4)",
                    pointerEvents: "none",
                    zIndex: 6,
                    borderRadius: 1.5,
                    willChange: "transform",
                  }} />
              );
            })()}
            {/* ─ Lasso selection rectangle (Select tool only) ─ */}
            {lasso && (function(){
              const x = Math.min(lasso.x1, lasso.x2);
              const y = Math.min(lasso.y1, lasso.y2);
              const w = Math.abs(lasso.x2 - lasso.x1);
              const h = Math.abs(lasso.y2 - lasso.y1);
              return (
                <div style={{
                  position:"absolute", left: x, top: y, width: w, height: h,
                  background: "rgba(167,139,250,0.10)",
                  border: "1.5px dashed rgba(196,181,253,0.7)",
                  borderRadius: 4,
                  pointerEvents: "none",
                  zIndex: 4,
                  boxShadow: "0 0 12px rgba(139,92,246,0.25)",
                }} />
              );
            })()}
          </div>
        </div>

        {/* ─ Scrollbar overlays ─
            Both live inside grid-area:"grid" so they stack on top of the
            scroll container without affecting layout. Thumb size mirrors
            viewport/total ratio; position mirrors scrollX/Y. Both are
            tappable+draggable to navigate quickly through long clips. */}
        {(function(){
          // Horizontal scrollbar — bottom of grid area, full width minus
          // the future vertical bar so they don't overlap at the corner.
          const HSB_H = 14;        // visual height — wider for easier touch
          const VSB_W = 14;        // visual width  — wider for easier touch
          const SCROLLBAR_MARGIN = 4;
          // Compute thumb dimensions/positions. Bail if the content fits
          // in the viewport (no scrolling needed → don't show the bar).
          const el = gridScrollRef.current;
          const vw = el ? el.clientWidth  : 0;
          const vh = el ? el.clientHeight : 0;
          const sw = el ? el.scrollWidth  : gridW;
          const sh = el ? el.scrollHeight : gridH;
          // Only show bars when there's actually overflow AND the scroll
          // container has been mounted (without it we can't measure
          // viewport size; first render flicker is fine).
          const showH = !!el && sw > vw + 1;
          const showV = !!el && sh > vh + 1;
          // Thumb sizing: at least 28px so it's always tappable.
          const hThumbW = showH ? Math.max(28, (vw / sw) * vw) : 0;
          const vThumbH = showV ? Math.max(28, (vh / sh) * vh) : 0;
          // Thumb position scales with scroll. Scrollable distance is
          // (sw - vw); thumb travel range is (vw - hThumbW). Linear map.
          const hThumbX = showH && (sw - vw) > 0
            ? (scrollX / (sw - vw)) * (vw - hThumbW)
            : 0;
          const vThumbY = showV && (sh - vh) > 0
            ? (scrollY / (sh - vh)) * (vh - vThumbH)
            : 0;
          // Drag handlers — captured per scrollbar. We use pointerdown +
          // global pointermove so the user can drag outside the bar
          // without losing the gesture.
          const startHDrag = function(e){
            if (!gridScrollRef.current) return;
            e.preventDefault(); e.stopPropagation();
            const startClientX = (e.touches ? e.touches[0].clientX : e.clientX);
            const startScrollLeft = gridScrollRef.current.scrollLeft;
            const sx = sw - vw;
            const tx = vw - hThumbW;
            const onMove = function(ev){
              const cx = (ev.touches ? ev.touches[0].clientX : ev.clientX);
              const dx = cx - startClientX;
              if (tx <= 0) return;
              const newScroll = startScrollLeft + (dx / tx) * sx;
              if (gridScrollRef.current) {
                gridScrollRef.current.scrollLeft = Math.max(0, Math.min(sx, newScroll));
              }
            };
            const onEnd = function(){
              window.removeEventListener("touchmove", onMove);
              window.removeEventListener("touchend", onEnd);
              window.removeEventListener("mousemove", onMove);
              window.removeEventListener("mouseup", onEnd);
            };
            window.addEventListener("touchmove", onMove, { passive: false });
            window.addEventListener("touchend", onEnd);
            window.addEventListener("mousemove", onMove);
            window.addEventListener("mouseup", onEnd);
          };
          const startVDrag = function(e){
            if (!gridScrollRef.current) return;
            e.preventDefault(); e.stopPropagation();
            const startClientY = (e.touches ? e.touches[0].clientY : e.clientY);
            const startScrollTop = gridScrollRef.current.scrollTop;
            const sy = sh - vh;
            const ty = vh - vThumbH;
            const onMove = function(ev){
              const cy = (ev.touches ? ev.touches[0].clientY : ev.clientY);
              const dy = cy - startClientY;
              if (ty <= 0) return;
              const newScroll = startScrollTop + (dy / ty) * sy;
              if (gridScrollRef.current) {
                gridScrollRef.current.scrollTop = Math.max(0, Math.min(sy, newScroll));
              }
            };
            const onEnd = function(){
              window.removeEventListener("touchmove", onMove);
              window.removeEventListener("touchend", onEnd);
              window.removeEventListener("mousemove", onMove);
              window.removeEventListener("mouseup", onEnd);
            };
            window.addEventListener("touchmove", onMove, { passive: false });
            window.addEventListener("touchend", onEnd);
            window.addEventListener("mousemove", onMove);
            window.addEventListener("mouseup", onEnd);
          };
          // Tap on track (not thumb) → page-scroll toward tap
          const tapTrackH = function(e){
            if (!gridScrollRef.current) return;
            const rect = e.currentTarget.getBoundingClientRect();
            const cx = (e.touches ? e.touches[0].clientX : e.clientX);
            const tapX = cx - rect.left;
            const targetThumbX = Math.max(0, Math.min(vw - hThumbW, tapX - hThumbW / 2));
            const targetScroll = (targetThumbX / Math.max(1, vw - hThumbW)) * (sw - vw);
            gridScrollRef.current.scrollLeft = Math.max(0, Math.min(sw - vw, targetScroll));
          };
          const tapTrackV = function(e){
            if (!gridScrollRef.current) return;
            const rect = e.currentTarget.getBoundingClientRect();
            const cy = (e.touches ? e.touches[0].clientY : e.clientY);
            const tapY = cy - rect.top;
            const targetThumbY = Math.max(0, Math.min(vh - vThumbH, tapY - vThumbH / 2));
            const targetScroll = (targetThumbY / Math.max(1, vh - vThumbH)) * (sh - vh);
            gridScrollRef.current.scrollTop = Math.max(0, Math.min(sh - vh, targetScroll));
          };
          return (
            <React.Fragment>
              {showH && (
                <div
                  onTouchStart={tapTrackH}
                  onMouseDown={tapTrackH}
                  style={{
                    gridArea: "grid",
                    alignSelf: "end",
                    justifySelf: "stretch",
                    height: HSB_H,
                    marginBottom: SCROLLBAR_MARGIN,
                    marginRight: showV ? (VSB_W + SCROLLBAR_MARGIN * 2) : SCROLLBAR_MARGIN,
                    marginLeft: SCROLLBAR_MARGIN,
                    background: "transparent",
                    borderRadius: 5,
                    position: "relative",
                    cursor: "pointer",
                    touchAction: "none",
                    zIndex: 8,
                    pointerEvents: "auto",
                  }}>
                  <div
                    ref={hThumbElRef}
                    onTouchStart={startHDrag}
                    onMouseDown={startHDrag}
                    style={{
                      position: "absolute",
                      top: 1, bottom: 1,
                      left: 0, width: hThumbW,
                      // Move via transform so onScroll can mutate this
                      // single property at 60+fps without re-render.
                      transform: "translateX(" + hThumbX + "px)",
                      background: "transparent",
                      borderRadius: 5,
                      boxShadow: "none",
                      cursor: "grab",
                      touchAction: "none",
                      willChange: "transform",
                    }} />
                </div>
              )}
              {showV && (
                <div
                  onTouchStart={tapTrackV}
                  onMouseDown={tapTrackV}
                  style={{
                    gridArea: "grid",
                    justifySelf: "end",
                    alignSelf: "stretch",
                    width: VSB_W,
                    marginRight: SCROLLBAR_MARGIN,
                    marginTop: SCROLLBAR_MARGIN,
                    marginBottom: showH ? (HSB_H + SCROLLBAR_MARGIN * 2) : SCROLLBAR_MARGIN,
                    background: "transparent",
                    borderRadius: 5,
                    position: "relative",
                    cursor: "pointer",
                    touchAction: "none",
                    zIndex: 8,
                    pointerEvents: "auto",
                  }}>
                  <div
                    ref={vThumbElRef}
                    onTouchStart={startVDrag}
                    onMouseDown={startVDrag}
                    style={{
                      position: "absolute",
                      left: 1, right: 1,
                      top: 0, height: vThumbH,
                      transform: "translateY(" + vThumbY + "px)",
                      background: "transparent",
                      borderRadius: 5,
                      boxShadow: "none",
                      cursor: "grab",
                      touchAction: "none",
                      willChange: "transform",
                    }} />
                </div>
              )}
            </React.Fragment>
          );
        })()}

        {/* ─ Velocity-lane spacer (under keyboard) ─ */}
        <div style={{
          gridArea: "kb-sp",
          background: "rgba(10,8,18,0.6)",
          borderRight: "1px solid rgba(167,139,250,0.10)",
          borderTop: "1px solid rgba(167,139,250,0.08)",
          display:"flex", alignItems:"center", justifyContent:"center",
          fontSize: 8, fontWeight: 700, letterSpacing: 1.8,
          color: "#5C5870", textTransform: "uppercase",
        }}>Vel</div>
        {/* ─ Velocity lane (scrolls horizontally with grid) ─ */}
        <div
          ref={function(el){ velocityScrollRef.current = el; }}
          onTouchMove={onVelTouchMove}
          onTouchEnd={onVelTouchEnd}
          onTouchCancel={onVelTouchEnd}
          style={{
            gridArea: "vel",
            background: "rgba(13,10,20,0.7)",
            borderTop: "1px solid rgba(167,139,250,0.08)",
            overflow: "hidden",
            position: "relative",
            touchAction: "pan-x",
          }}>
          <div
            ref={velocityInnerRef}
            style={{
            position:"relative", width: gridW, height: VEL_LANE_H - 1,
            // Mirror the grid's horizontal scroll position.
            transform: "translateX(" + (-scrollX) + "px)",
            willChange: "transform",
          }}>
            {/* Subtle horizontal reference lines (25/50/75%) */}
            {[0.25, 0.5, 0.75].map(function(f){
              return (
                <div key={f} style={{
                  position:"absolute", left: 0, right: 0,
                  top: (1 - f) * (VEL_LANE_H - 1), height: 1,
                  background: "rgba(167,139,250,0.06)",
                  pointerEvents: "none",
                }} />
              );
            })}
            {/* Beat lines for context */}
            {beatLines.map(function(b, i){
              return (
                <div key={"vbeat"+i} style={{
                  position:"absolute", top: 0, bottom: 0,
                  left: b.x, width: b.isBar ? 1.5 : 1,
                  background: b.isBar
                    ? "rgba(167,139,250,0.18)"
                    : "rgba(167,139,250,0.07)",
                  pointerEvents: "none",
                }} />
              );
            })}
            {/* Velocity bars — one per note. Drag vertically to set vel. */}
            {notes.map(function(n){
              const vel = n.velocity == null ? 0.85 : n.velocity;
              const x = timeToX(n.start);
              const h = vel * (VEL_LANE_H - 8);
              const isSel = selNoteIds.has(n.id);
              return (
                <div key={n.id}
                  onTouchStart={function(e){ onVelTouchStart(e, n.id); }}
                  style={{
                    position:"absolute",
                    left: x - 1, bottom: 1,
                    width: 6, height: h,
                    background: "linear-gradient(180deg, " + trackColor + " 0%, " + trackColor + "aa 100%)",
                    border: "1px solid " + (isSel ? "#F8F7FA" : trackColor),
                    borderRadius: 2,
                    boxShadow: isSel
                      ? "0 0 8px " + trackColor + "cc"
                      : "0 0 4px " + trackColor + "66",
                    cursor: "ns-resize",
                    touchAction: "none",
                  }} />
              );
            })}
            {/* Clip-end shading */}
            <div style={{
              position:"absolute", top: 0, bottom: 0,
              left: timeToX(clipDur), right: 0,
              background: "rgba(0,0,0,0.4)",
              pointerEvents: "none",
            }} />
          </div>
        </div>
      </div>
      {/* ═══ SELECTION ACTION BAR (Phase 3) ═══════════════════════════════
          Slides up when ≥1 note is selected. Hosts every bulk operation:
          delete, duplicate, copy, paste, transpose ±1/±12, quantize,
          humanize, velocity randomize, velocity ±10%, arp up/down/updown.
          Horizontally scrollable so all controls fit on phone widths. */}
      {selNoteIds.size > 0 && (
        <div style={{
          flexShrink: 0,
          padding: "10px 14px",
          borderTop: "1px solid rgba(167,139,250,0.15)",
          background: "linear-gradient(180deg, rgba(28,20,42,0.85) 0%, rgba(18,14,28,0.85) 100%)",
          backdropFilter: "blur(20px) saturate(140%)",
          WebkitBackdropFilter: "blur(20px) saturate(140%)",
          boxShadow:
            "0 -8px 32px rgba(0,0,0,0.4), " +
            "inset 0 1px 0 rgba(255,255,255,0.05)",
          display: "flex", alignItems: "center", gap: 8,
          flexWrap: "wrap",
          rowGap: 8,
        }}>
          {/* Selection badge */}
          <div style={{
            background: "linear-gradient(135deg," + trackColor + " 0%," + trackColor + "cc 100%)",
            color: "white",
            borderRadius: 8, padding: "6px 10px",
            fontSize: 10, fontWeight: 800, letterSpacing: 0.8,
            textTransform: "uppercase",
            boxShadow:
              "0 4px 12px " + trackColor + "55, " +
              "inset 0 1px 0 rgba(255,255,255,0.25)",
            flexShrink: 0, whiteSpace: "nowrap",
          }}>{selNoteIds.size} sel.</div>
          {/* Helper: a styled action button */}
          {(function(){
            const btn = function(label, onClick, opts) {
              opts = opts || {};
              return (
                <button key={label} onClick={onClick} style={{
                  background: opts.danger
                    ? "rgba(239,68,68,0.06)"
                    : opts.primary
                      ? "linear-gradient(135deg,#A78BFA 0%,#8B5CF6 100%)"
                      : "rgba(20,15,30,0.5)",
                  color: opts.danger ? "#F87171" : opts.primary ? "white" : "#F0EFF5",
                  border: "1px solid " + (
                    opts.danger ? "rgba(239,68,68,0.22)"
                      : opts.primary ? "rgba(167,139,250,0.5)"
                      : "rgba(167,139,250,0.12)"
                  ),
                  borderRadius: 8, padding: "7px 11px",
                  fontSize: 11, fontWeight: 700, cursor: "pointer",
                  fontFamily: "'DM Sans',sans-serif",
                  letterSpacing: 0.4, flexShrink: 0, whiteSpace: "nowrap",
                  boxShadow: opts.primary
                    ? "0 4px 12px rgba(139,92,246,0.3), inset 0 1px 0 rgba(255,255,255,0.2)"
                    : "none",
                }}>{label}</button>
              );
            };
            // Category dropdown trigger button. Captures its rect on open
            // so the dropdown can be positioned via position:fixed above it.
            const menuBtn = function(key, label){
              const isOpen = selActionMenu === key;
              return (
                <button key={key} onClick={function(e){
                  e.stopPropagation();
                  if (!isOpen) {
                    const rect = e.currentTarget.getBoundingClientRect();
                    selActionAnchorRef.current = {
                      left: rect.left,
                      bottom: window.innerHeight - rect.top + 6,
                    };
                  }
                  setSelActionMenu(isOpen ? null : key);
                }} style={{
                  background: isOpen
                    ? "linear-gradient(135deg,rgba(167,139,250,0.25),rgba(139,92,246,0.18))"
                    : "rgba(20,15,30,0.5)",
                  color: "#F0EFF5",
                  border: "1px solid " + (isOpen ? "rgba(167,139,250,0.45)" : "rgba(167,139,250,0.12)"),
                  borderRadius: 8, padding: "7px 11px",
                  fontSize: 11, fontWeight: 700, cursor: "pointer",
                  fontFamily: "'DM Sans',sans-serif",
                  letterSpacing: 0.4, flexShrink: 0, whiteSpace: "nowrap",
                  display: "inline-flex", alignItems: "center", gap: 4,
                }}>
                  {label}
                  <span style={{ fontSize: 8, opacity: 0.7 }}>▾</span>
                </button>
              );
            };
            return [
              btn("Cut", cutSelection),
              btn("Copy", copySelection),
              clipboard ? btn("Paste", pasteClipboard) : null,
              btn("Dup", duplicateSelection),
              <div key="div1" style={{ width: 1, height: 18, background: "rgba(167,139,250,0.12)", flexShrink: 0 }} />,
              menuBtn("pitch", "Pitch"),
              menuBtn("time",  "Time"),
              menuBtn("vel",   "Vel"),
              menuBtn("fx",    "FX"),
              <div key="div5" style={{ flex: 1, minWidth: 8 }} />,
              btn("Delete", deleteSelection, { danger: true }),
              btn("Done", selectClear, { primary: true }),
            ].filter(Boolean);
          })()}
          {/* ── Category dropdowns ── one popup at a time. Each item
              shows the action plus a short hint so the user knows what
              it does without trial-and-error. */}
          {selActionMenu && (function(){
            const CATEGORIES = {
              pitch: {
                title: "PITCH",
                items: [
                  { label: "+1 semitone",  hint: "Shift up by one note",        action: function(){ transposeSelection(1); } },
                  { label: "−1 semitone",  hint: "Shift down by one note",      action: function(){ transposeSelection(-1); } },
                  { label: "+1 octave",    hint: "Shift up 12 semitones",       action: function(){ transposeSelection(12); } },
                  { label: "−1 octave",    hint: "Shift down 12 semitones",     action: function(){ transposeSelection(-12); } },
                ],
              },
              time: {
                title: "TIMING",
                items: [
                  { label: "Quantize",     hint: "Snap notes to the grid",                   action: quantizeSelection },
                  { label: "Humanize",     hint: "Add natural timing variation",             action: humanizeSelection },
                ],
              },
              vel: {
                title: "VELOCITY",
                items: [
                  { label: "Louder",       hint: "+10% velocity (volume)",                   action: function(){ velocityNudgeSelection(0.1); } },
                  { label: "Quieter",      hint: "−10% velocity (volume)",                   action: function(){ velocityNudgeSelection(-0.1); } },
                  { label: "Randomize",    hint: "Vary velocities for natural feel",         action: velocityRandomizeSelection },
                ],
              },
              fx: {
                title: "EFFECTS",
                items: [
                  { label: "Arp ↑ Up",     hint: "Spread selected notes ascending",          action: function(){ arpeggiateSelection("up"); } },
                  { label: "Arp ↓ Down",   hint: "Spread selected notes descending",         action: function(){ arpeggiateSelection("down"); } },
                  { label: "Arp ⇅ Up/Down",hint: "Bounce up then down",                      action: function(){ arpeggiateSelection("updown"); } },
                  { label: "Roll 1/16",    hint: "Repeat each note at sixteenth notes",      action: function(){ rollSelection(4); } },
                  { label: "Roll 1/32",    hint: "Repeat each note at thirty-second notes",  action: function(){ rollSelection(8); } },
                  { label: "Roll 1/64",    hint: "Repeat each note at sixty-fourth notes",   action: function(){ rollSelection(16); } },
                ],
              },
            };
            const cat = CATEGORIES[selActionMenu];
            if (!cat) return null;
            const anchor = selActionAnchorRef.current;
            const MENU_W = 240;
            const vw = (typeof window !== "undefined") ? window.innerWidth : 390;
            const leftClamped = Math.min(Math.max(8, anchor.left), vw - MENU_W - 8);
            return (
              <>
                {/* Backdrop: tap outside to close */}
                <div onClick={function(){ setSelActionMenu(null); }}
                  style={{ position:"fixed", inset:0, zIndex:200 }} />
                <div style={{
                  position: "fixed",
                  left: leftClamped,
                  bottom: anchor.bottom,
                  width: MENU_W,
                  zIndex: 201,
                  background: "linear-gradient(180deg,#15101e,#0c0916)",
                  border: "1px solid rgba(167,139,250,0.30)",
                  borderRadius: 12,
                  boxShadow: "0 -8px 32px rgba(0,0,0,0.7), 0 0 0 1px rgba(255,255,255,0.03)",
                  overflow: "hidden",
                  fontFamily: "'DM Sans',sans-serif",
                }}>
                  <div style={{
                    padding: "10px 14px",
                    background: "linear-gradient(180deg,rgba(167,139,250,0.12),rgba(139,92,246,0.06))",
                    borderBottom: "1px solid rgba(167,139,250,0.15)",
                    color: "#C4B5FD",
                    fontSize: 10, fontWeight: 800, letterSpacing: 1.5,
                  }}>{cat.title}</div>
                  {cat.items.map(function(item){
                    return (
                      <button key={item.label} onClick={function(){
                        try { item.action(); } catch(_){}
                        setSelActionMenu(null);
                      }}
                        style={{
                          width: "100%",
                          background: "transparent", border: "none",
                          borderBottom: "1px solid rgba(167,139,250,0.06)",
                          color: "#E5E2EF", padding: "12px 14px",
                          textAlign: "left", cursor: "pointer",
                          display: "block",
                          fontFamily: "'DM Sans',sans-serif",
                        }}
                        onPointerEnter={function(e){ e.currentTarget.style.background = "rgba(167,139,250,0.07)"; }}
                        onPointerLeave={function(e){ e.currentTarget.style.background = "transparent"; }}>
                        <div style={{ color: "white", fontSize: 12, fontWeight: 700, marginBottom: 2 }}>{item.label}</div>
                        <div style={{ color: "#7A7488", fontSize: 10, lineHeight: 1.3 }}>{item.hint}</div>
                      </button>
                    );
                  })}
                </div>
              </>
            );
          })()}
        </div>
      )}
      {/* ═══ FOOTER ═══ */}
      <div style={{
        flexShrink: 0, height: FOOTER_H,
        padding: "0 16px",
        borderTop: "1px solid rgba(167,139,250,0.08)",
        background: "rgba(13,10,20,0.5)",
        display:"flex", alignItems:"center", gap: 14,
        fontSize: 10, color: "#7A7488", letterSpacing: 0.3,
        overflowX: "auto",
      }}>
        <span><span style={{ color:"#C4B5FD", fontWeight: 700 }}>{notes.length}</span> notes</span>
        <span style={{ color: "#2A2535" }}>·</span>
        <span><span style={{ color:"#C4B5FD", fontWeight: 700 }}>{bpm}</span> BPM</span>
        <span style={{ color: "#2A2535" }}>·</span>
        <span><span style={{ color:"#C4B5FD", fontWeight: 700 }}>{clipDur.toFixed(2)}</span> s</span>
        <span style={{ color: "#2A2535" }}>·</span>
        {/* Hint text adapts to the current tool so the producer always knows
            what their next tap will do. */}
        <span style={{ color: "#5C5870", whiteSpace:"nowrap" }}>{
          chordArmed
            ? "Tap a cell to stamp " + chordArmed.name + " · pinch to zoom"
            : toolMode === "select"
              ? "Drag empty to lasso · tap note to select · drag selection to move"
              : toolMode === "eraser"
                ? "Tap or swipe notes to delete · pinch to zoom"
                : toolMode === "slice"
                  ? "Tap a note to split it at that point"
                  : "Tap empty to place · drag to move · pinch to zoom"
        }</span>
      </div>

      {/* ═══ COMPOSE SHEET (Phase 5) ═══════════════════════════════════════
          Slide-up generator panel. We pass the editor's live scale, root,
          and BPM so the generators are key-aware. New notes commit via
          commitNotes (same path as manual edits → undo just works).
          Auto-selects the result so the producer can immediately tweak. */}
      {composeOpen && (
        <ComposeSheet
          scaleRoot={scaleRoot}
          scaleId={scaleId}
          bpm={bpm}
          timeSigNum={timeSigNum}
          clipDur={clipDur}
          selectedNotes={notes.filter(function(n){ return selNoteIds.has(n.id); })}
          allNotes={notes}
          onApplyNotes={function(payloads, opts){
            opts = opts || {};
            // Add ids to each payload + commit. Modes:
            //  • default (replace true)   → wipe everything, write only newOnes
            //  • opts.replace === false   → keep all, append newOnes (counter-melody)
            //  • opts.replaceSelected     → drop only the currently-selected
            //    notes (selNoteIds), keep the rest, append newOnes. This is
            //    what lets an ARP target just a highlighted portion of a
            //    melody (e.g. turn 4 selected notes into a hi-hat roll
            //    without losing the rest of the part).
            const newOnes = payloads.map(function(p){
              return Object.assign({ id: newNoteId() }, p);
            });
            let next;
            if (opts.replaceSelected) {
              const selSet = selNoteIds || new Set();
              const kept = notes.filter(function(n){ return !selSet.has(n.id); });
              next = kept.concat(newOnes);
            } else if (opts.replace === false) {
              next = notes.concat(newOnes);
            } else {
              next = newOnes;
            }
            commitNotes(next);
            // Auto-select the new notes so the producer can immediately
            // act on them via the existing action bar (transpose, humanize,
            // delete, etc.).
            const newIds = new Set();
            for (let i = 0; i < newOnes.length; i++) newIds.add(newOnes[i].id);
            setSelNoteIds(newIds);
            setComposeOpen(false);
          }}
          onClose={function(){ setComposeOpen(false); }}
        />
      )}

      {/* Bank-preset sub-picker sheet — opens when the user taps the
          bank-preset button in the toolbar. Shows the full list of
          presets in the track's multi-preset bank as a scrollable
          grid. Tapping a preset writes track.selectedSfPreset and
          closes the sheet. Notes stay as-is; the next note played
          uses the new preset's sound. */}
      {bankPresetSheetOpen && baseInst && baseInst.presets && baseInst.presets.length > 0 && (
        <BankPresetSheet
          bankInstName={baseInst._bankBaseName || baseInst.name}
          presets={baseInst.presets}
          currentSelection={track.selectedSfPreset || {
            bank:    baseInst.gmBankNumber    || 0,
            program: baseInst.gmProgramNumber || 0,
          }}
          onSelect={function(sel){
            // Apply the override via setTracks. resolveTrackInstrumentMeta
            // picks this up at preview/playback time and derives a per-
            // selection inst id so each (sf, bank, program) caches as its
            // own engine entry — no channel-stealing across selections.
            setTracks(function(prev){
              return prev.map(function(tr){
                if (tr.id !== track.id) return tr;
                return Object.assign({}, tr, {
                  selectedSfPreset: { bank: sel.bank, program: sel.program },
                });
              });
            });
            // Pre-warm the engine for the derived inst id so the
            // timeline doesn't silently miss the first wave of notes
            // when the user hits Play. Without this preload, the user
            // picks a preset, hits Play, and gets ~200ms of silence
            // (or the wrong preset) while the engine spins up the
            // channel binding for the new (bank, program) pair.
            try {
              const tempTr = Object.assign({}, track, {
                selectedSfPreset: { bank: sel.bank, program: sel.program },
              });
              const resolved = (typeof resolveTrackInstrumentMeta === "function")
                ? resolveTrackInstrumentMeta(tempTr) : null;
              if (resolved && resolved.engine === "soundfont-v1") {
                let ctx = null, dest = null;
                if (typeof getOrCreateMaster === "function") {
                  const mg = getOrCreateMaster();
                  if (mg && mg.context) { ctx = mg.context; dest = mg; }
                }
                if (!ctx && typeof getActx === "function") {
                  try { ctx = getActx(); } catch(_){}
                }
                if (ctx) ensureSoundfontPresetLoaded(resolved, ctx, dest);
              }
            } catch(_) { /* non-fatal */ }
            setBankPresetSheetOpen(false);
          }}
          onClose={function(){ setBankPresetSheetOpen(false); }}
        />
      )}
    </div>
  );
}

// ═════════════════════════════════════════════════════════════════════════════
//  BankPresetSheet — sub-picker for multi-preset soundfont banks
// ═════════════════════════════════════════════════════════════════════════════
//
// Slide-up modal that lists every preset inside a multi-preset bank. The
// user taps a preset and the track switches to that sound (without
// changing its instrument). Search filter at the top for big banks.
//
// Props:
//   bankInstName       — name of the parent bank ("GeneralUser GS")
//   presets            — [{ bank, program, name }, ...]
//   currentSelection   — { bank, program } currently active
//   onSelect({bank,program}) — caller writes track.selectedSfPreset
//   onClose            — close handler
function BankPresetSheet({ bankInstName, presets, currentSelection, onSelect, onClose }) {
  const [search, setSearch] = React.useState("");
  // Ref to the scroll container so the up/down buttons can directly
  // manipulate scrollTop. This bypasses iOS Safari's touch-scroll
  // behavior entirely — the buttons just write a number to scrollTop
  // and the DOM scrolls. No reliance on touch gestures, flex layout,
  // or any iOS-specific overflow quirks.
  const scrollRef = React.useRef(null);
  // Tick used to force a re-render after we scroll so the position
  // indicator updates. scrollTop changes don't trigger React renders
  // on their own.
  const [scrollTick, setScrollTick] = React.useState(0);
  // How far each tap of the arrow scrolls — roughly one card row.
  const STEP_PX = 60;
  // Auto-repeat: holding the button continues to scroll. Stored on
  // a ref so we can clear it on touchend / mouseup.
  const repeatTimerRef = React.useRef(null);
  const stopRepeat = function(){
    if (repeatTimerRef.current) {
      clearInterval(repeatTimerRef.current);
      repeatTimerRef.current = null;
    }
  };
  const scrollBy = function(delta){
    const el = scrollRef.current;
    if (!el) return;
    const next = Math.max(0, Math.min(el.scrollHeight - el.clientHeight, el.scrollTop + delta));
    el.scrollTop = next;
    setScrollTick(function(x){ return x + 1; });
  };
  const startRepeat = function(delta){
    stopRepeat();
    scrollBy(delta);
    repeatTimerRef.current = setInterval(function(){ scrollBy(delta); }, 80);
  };
  React.useEffect(function(){ return stopRepeat; }, []);
  // Snapshot the window height at mount. iOS Safari changes window.innerHeight
  // as the address bar shows/hides, but we don't care — we just need a stable
  // pixel value to anchor the scroll container's height. Using the value at
  // mount avoids any flex/svh/vh ambiguity that has been failing on iOS.
  const [winH, setWinH] = React.useState(function(){
    if (typeof window === "undefined") return 800;
    return window.innerHeight || 800;
  });
  React.useEffect(function(){
    const onResize = function(){
      if (typeof window !== "undefined") setWinH(window.innerHeight || 800);
    };
    window.addEventListener("resize", onResize);
    window.addEventListener("orientationchange", onResize);
    return function(){
      window.removeEventListener("resize", onResize);
      window.removeEventListener("orientationchange", onResize);
    };
  }, []);

  const filtered = React.useMemo(function(){
    const q = search.trim().toLowerCase();
    if (!q) return presets;
    return presets.filter(function(p){
      return (p.name || "").toLowerCase().indexOf(q) >= 0
          || String(p.program) === q
          || (p.bank + ":" + p.program).indexOf(q) >= 0;
    });
  }, [presets, search]);

  // Layout: header + search are fixed-height at the top. The scroll
  // container fills the remaining space with an EXPLICIT pixel height
  // computed from winH. No flex sizing, no svh — just numbers.
  const HEADER_H = 70;   // header
  const SEARCH_H = 60;   // search row
  const SAFE_BOT = 24;   // estimate of bottom safe-area
  const sheetH = Math.round(winH * 0.85);
  const scrollH = Math.max(200, sheetH - HEADER_H - SEARCH_H - SAFE_BOT);

  return (
    <div style={{ position: "fixed", inset: 0, zIndex: 10800 }}>
      {/* Backdrop layer — captures close-on-tap. */}
      <div
        onClick={onClose}
        style={{
          position: "absolute", inset: 0,
          background: "rgba(7,6,17,0.78)",
        }}
      />
      {/* Sheet — absolute positioning, explicit pixel height. */}
      <div style={{
        position: "absolute",
        left: 0, right: 0, bottom: 0,
        margin: "0 auto",
        maxWidth: 720,
        height: sheetH,
        background: "linear-gradient(180deg,#0d0a1a 0%,#070611 100%)",
        borderRadius: "16px 16px 0 0",
        borderTop: "1px solid rgba(167,139,250,0.20)",
        fontFamily: "'DM Sans',sans-serif",
        overflow: "hidden",   // ← clip; the inner scroll container does the real work
      }}>
        {/* Header */}
        <div style={{
          height: HEADER_H, boxSizing: "border-box",
          padding: "14px 16px 10px",
          display: "flex", alignItems: "center", gap: 10,
          borderBottom: "1px solid rgba(167,139,250,0.10)",
        }}>
          <div style={{ flex: 1, minWidth: 0 }}>
            <div style={{
              fontSize: 9, color: "#7A7488", letterSpacing: 1.6,
              textTransform: "uppercase", fontWeight: 700, marginBottom: 2,
            }}>Pick Preset</div>
            <div style={{
              fontSize: 14, color: "#F8F7FA", fontWeight: 700,
              whiteSpace: "nowrap", overflow: "hidden", textOverflow: "ellipsis",
            }}>{bankInstName || "Bank"}</div>
          </div>
          <button onClick={onClose} style={{
            width: 32, height: 32, borderRadius: 8,
            background: "rgba(20,15,30,0.5)",
            color: "#9590A8",
            border: "1px solid rgba(167,139,250,0.10)",
            cursor: "pointer", fontFamily: "system-ui",
            display: "inline-flex", alignItems: "center", justifyContent: "center",
            flexShrink: 0,
          }}><BFIcon name="close" size={13} /></button>
        </div>

        {/* Search */}
        <div style={{ height: SEARCH_H, boxSizing: "border-box", padding: "10px 16px 6px" }}>
          <input
            type="text"
            value={search}
            onChange={function(e){ setSearch(e.target.value); }}
            placeholder={"Search " + presets.length + " presets…"}
            style={{
              width: "100%", padding: "10px 14px",
              background: "rgba(20,15,30,0.5)",
              border: "1px solid rgba(167,139,250,0.15)",
              borderRadius: 10, color: "#F8F7FA",
              fontSize: 13, outline: "none", boxSizing: "border-box",
              fontFamily: "'DM Sans',sans-serif",
            }}
          />
        </div>

        {/* Visible scrollbar styling */}
        <style>{
          ".bf-bank-scroll::-webkit-scrollbar { width: 10px; -webkit-appearance: none; }" +
          ".bf-bank-scroll::-webkit-scrollbar-track { background: rgba(20,15,30,0.4); border-radius: 4px; }" +
          ".bf-bank-scroll::-webkit-scrollbar-thumb { background: rgba(167,139,250,0.55); border-radius: 4px; border: 2px solid transparent; background-clip: padding-box; }" +
          ".bf-bank-scroll { scrollbar-width: thin; scrollbar-color: rgba(167,139,250,0.55) rgba(20,15,30,0.4); }"
        }</style>

        {/* Scroll container + arrow column. Arrows directly mutate
            scrollTop via the ref, bypassing touch-scroll entirely so
            users have a guaranteed way to navigate the list. */}
        <div style={{
          display: "flex",
          height: scrollH,
        }}>
          <div
            ref={function(el){
              scrollRef.current = el;
              if (!el || el._bfBankSheetSetup) return;
              el._bfBankSheetSetup = true;
              // JS-driven touch scroll with momentum. Bypasses iOS Safari's
              // native overflow scrolling, which can be intercepted by the
              // sheet's parent backdrop/container handlers. Same pattern
              // used by the FX preset list — confirmed reliable inside
              // modal sheets.
              //
              // Crucially: scroll engages on touches ANYWHERE in the list,
              // including on preset buttons. We track whether the gesture
              // moved beyond a small threshold; if so, the touchend
              // installs a one-shot capture-phase click suppressor so the
              // button's onClick (which would fire AFTER touchend) is
              // swallowed. That means users can freely drag any preset
              // tile to scroll, and a still tap still selects normally.
              let active = false, startY = 0, startScroll = 0;
              let lastY = 0, lastT = 0, vel = 0;
              let moved = false;
              const MOVE_THRESHOLD = 8; // px before treating as scroll
              let raf = 0;
              const cancelMomentum = function(){
                if (raf) { cancelAnimationFrame(raf); raf = 0; }
              };
              const onTouchStart = function(e){
                if (!e.touches || !e.touches[0]) return;
                cancelMomentum();
                active = true;
                moved = false;
                startY = e.touches[0].clientY;
                startScroll = el.scrollTop;
                lastY = startY;
                lastT = performance.now();
                vel = 0;
              };
              const onTouchMove = function(e){
                if (!active || !e.touches || !e.touches[0]) return;
                const y = e.touches[0].clientY;
                const dy = y - startY;
                if (!moved && Math.abs(dy) > MOVE_THRESHOLD) moved = true;
                el.scrollTop = Math.max(0, Math.min(el.scrollHeight - el.clientHeight, startScroll - dy));
                const now = performance.now();
                const dt = now - lastT;
                if (dt > 0) vel = (lastY - y) / dt;  // px/ms
                lastY = y; lastT = now;
                if (e.cancelable) { try { e.preventDefault(); } catch(_){} }
              };
              const onTouchEnd = function(){
                if (!active) return;
                active = false;
                // If the gesture became a drag, swallow the upcoming click
                // event that iOS will fire on the button beneath the
                // touchend. Capture-phase + onceflag = perfect one-shot.
                if (moved) {
                  const swallow = function(ev){
                    ev.stopPropagation();
                    ev.preventDefault();
                    el.removeEventListener("click", swallow, true);
                  };
                  el.addEventListener("click", swallow, true);
                  // Belt-and-braces: clear the handler after 350ms in case
                  // iOS doesn't fire the synthetic click (e.g. fast flick).
                  setTimeout(function(){
                    try { el.removeEventListener("click", swallow, true); } catch(_){}
                  }, 350);
                }
                // Momentum decay — same curve as the FX preset list.
                let v = vel * 16;  // approx px/frame at 60Hz
                const step = function(){
                  if (Math.abs(v) < 0.5) { raf = 0; return; }
                  el.scrollTop = Math.max(0, Math.min(el.scrollHeight - el.clientHeight, el.scrollTop + v));
                  v *= 0.94;
                  raf = requestAnimationFrame(step);
                };
                if (Math.abs(vel) > 0.05) raf = requestAnimationFrame(step);
                setScrollTick(function(x){ return x + 1; });
              };
              el.addEventListener("touchstart",  onTouchStart, { passive: false });
              el.addEventListener("touchmove",   onTouchMove,  { passive: false });
              el.addEventListener("touchend",    onTouchEnd,   { passive: true });
              el.addEventListener("touchcancel", onTouchEnd,   { passive: true });
            }}
            className="bf-bank-scroll"
            style={{
              flex: 1,
              height: "100%",
              overflowY: "scroll",
              WebkitOverflowScrolling: "touch",
              touchAction: "pan-y",
              overscrollBehavior: "contain",
              padding: "8px 12px 16px",
              boxSizing: "border-box",
            }}>
            <div style={{
              display: "grid",
              gridTemplateColumns: "repeat(auto-fill, minmax(140px, 1fr))",
              gap: 8,
            }}>
              {filtered.length === 0 && (
                <div style={{
                  gridColumn: "1 / -1",
                  padding: "30px 16px",
                  textAlign: "center",
                  color: "#5C5870", fontSize: 12,
                }}>No presets match "{search}"</div>
              )}
              {filtered.map(function(p, idx){
                const isCurrent = currentSelection
                  && currentSelection.bank === p.bank
                  && currentSelection.program === p.program;
                return (
                  <button key={p.bank + ":" + p.program + ":" + idx}
                    onClick={function(){ onSelect({ bank: p.bank, program: p.program }); }}
                    style={{
                      textAlign: "left",
                      padding: "10px 12px",
                      background: isCurrent
                        ? "linear-gradient(135deg, rgba(167,139,250,0.20) 0%, rgba(139,92,246,0.10) 100%)"
                        : "rgba(20,15,30,0.5)",
                      border: "1px solid " + (isCurrent ? "rgba(167,139,250,0.5)" : "rgba(167,139,250,0.08)"),
                      borderRadius: 10, cursor: "pointer",
                      color: isCurrent ? "#F8F7FA" : "#C4B5FD",
                      display: "flex", flexDirection: "column", gap: 4,
                      boxShadow: isCurrent ? "0 0 14px rgba(139,92,246,0.20)" : "none",
                      minHeight: 50,
                    }}>
                    <div style={{
                      fontSize: 12, fontWeight: 700,
                      whiteSpace: "nowrap", overflow: "hidden", textOverflow: "ellipsis",
                      width: "100%",
                    }}>{p.name || "(unnamed)"}</div>
                    <div style={{
                      fontSize: 9, color: isCurrent ? "#A78BFA" : "#5C5870",
                      letterSpacing: 0.8, fontVariantNumeric: "tabular-nums",
                    }}>{p.bank}:{p.program}{isCurrent ? "  ●  ACTIVE" : ""}</div>
                  </button>
                );
              })}
            </div>
          </div>

          {/* Up/Down arrow column — direct scrollTop manipulation. */}
          {(function(){
            const el = scrollRef.current;
            const maxScroll = el ? Math.max(0, el.scrollHeight - el.clientHeight) : 1;
            const curScroll = el ? el.scrollTop : 0;
            const pct = maxScroll > 0 ? (curScroll / maxScroll) : 0;
            const trackH = scrollH - 110;   // leave room for the two buttons + padding
            const thumbH = Math.max(30, trackH * (el ? Math.min(1, el.clientHeight / el.scrollHeight) : 0.2));
            const thumbTop = (trackH - thumbH) * pct;
            return (
              <div style={{
                width: 48,
                display: "flex", flexDirection: "column", alignItems: "center",
                padding: "8px 0",
                borderLeft: "1px solid rgba(167,139,250,0.10)",
                background: "rgba(7,6,17,0.4)",
              }}>
                {/* Up arrow */}
                <button
                  onMouseDown={function(){ startRepeat(-STEP_PX); }}
                  onMouseUp={stopRepeat}
                  onMouseLeave={stopRepeat}
                  onTouchStart={function(e){ e.preventDefault(); startRepeat(-STEP_PX); }}
                  onTouchEnd={function(e){ e.preventDefault(); stopRepeat(); }}
                  onTouchCancel={function(e){ e.preventDefault(); stopRepeat(); }}
                  aria-label="Scroll up"
                  style={{
                    width: 36, height: 44, borderRadius: 8,
                    background: "rgba(167,139,250,0.10)",
                    color: "#C4B5FD",
                    border: "1px solid rgba(167,139,250,0.30)",
                    fontSize: 18, fontWeight: 700, cursor: "pointer",
                    fontFamily: "system-ui",
                    display: "inline-flex", alignItems: "center", justifyContent: "center",
                    flexShrink: 0,
                    boxShadow: "0 4px 12px rgba(139,92,246,0.25), inset 0 1px 0 rgba(255,255,255,0.10)",
                  }}>▲</button>

                {/* Position track + thumb (visual indicator of position) */}
                <div style={{
                  flex: 1,
                  width: 6,
                  margin: "10px 0",
                  background: "rgba(20,15,30,0.7)",
                  borderRadius: 3,
                  position: "relative",
                  overflow: "hidden",
                }}>
                  <div style={{
                    position: "absolute",
                    left: 0, right: 0,
                    top: thumbTop,
                    height: thumbH,
                    background: "linear-gradient(180deg,#A78BFA,#8B5CF6)",
                    borderRadius: 3,
                    boxShadow: "0 0 8px rgba(139,92,246,0.5)",
                  }} />
                </div>

                {/* Down arrow */}
                <button
                  onMouseDown={function(){ startRepeat(STEP_PX); }}
                  onMouseUp={stopRepeat}
                  onMouseLeave={stopRepeat}
                  onTouchStart={function(e){ e.preventDefault(); startRepeat(STEP_PX); }}
                  onTouchEnd={function(e){ e.preventDefault(); stopRepeat(); }}
                  onTouchCancel={function(e){ e.preventDefault(); stopRepeat(); }}
                  aria-label="Scroll down"
                  style={{
                    width: 36, height: 44, borderRadius: 8,
                    background: "rgba(167,139,250,0.10)",
                    color: "#C4B5FD",
                    border: "1px solid rgba(167,139,250,0.30)",
                    fontSize: 18, fontWeight: 700, cursor: "pointer",
                    fontFamily: "system-ui",
                    display: "inline-flex", alignItems: "center", justifyContent: "center",
                    flexShrink: 0,
                    boxShadow: "0 4px 12px rgba(139,92,246,0.25), inset 0 1px 0 rgba(255,255,255,0.10)",
                  }}>▼</button>
              </div>
            );
          })()}
        </div>
      </div>
    </div>
  );
}


// ═════════════════════════════════════════════════════════════════════════════
//  PresetBrowserSheet (Phase 4) — bottom-sheet preset browser
// ═════════════════════════════════════════════════════════════════════════════
//
// Slide-up bottom sheet with category sidebar + preset grid. Like Apple
// Music but for instruments. Tap a card to assign + close; tap-and-hold
// (200ms) to audition with a C-major arpeggio.
//
// Props:
//   trackId          — the MIDI track receiving the new instrument
//   currentInstId    — currently assigned (for highlighting)
//   onSelect(id)     — caller assigns the new id via setTracks
//   onClose          — close handler
//   getOrCreateMaster — same as editor: lazy-create audio context for preview
//   bpm              — used for the audition arp tempo
function PresetBrowserSheet(props) {
  const { currentInstId, onSelect, onClose, getOrCreateMaster, bpm } = props;
  // Active category — defaults to the category of the current preset so
  // the user lands where they were last looking.
  const initialCategory = React.useMemo(function(){
    const curMeta = currentInstId && BF_INSTRUMENTS[currentInstId];
    return (curMeta && curMeta.category) || "Keys";
  }, [currentInstId]);
  const [activeCategory, setActiveCategory] = React.useState(initialCategory);

  // Force-rerender hook for soundfont engine state changes. When a
  // preset's load status flips from "loading" → "ready", the cards need
  // to re-render to show the new badge. We bump a counter on every event.
  const [_sfTick, _setSfTick] = React.useState(0);
  React.useEffect(function(){
    const eng = _bfSfEngineInstance;
    if (!eng) {
      // Engine doesn't exist yet — poll briefly to catch its creation,
      // since opening the browser may trigger preview which creates it.
      const t = setInterval(function(){
        if (_bfSfEngineInstance) { _setSfTick(function(x){ return x + 1; }); clearInterval(t); }
      }, 300);
      return function(){ clearInterval(t); };
    }
    const unsub = eng.subscribe(function(){ _setSfTick(function(x){ return x + 1; }); });
    return unsub;
  }, []);

  // Search query — filters across ALL categories when non-empty (overrides
  // the active-category filter).
  const [search, setSearch] = React.useState("");

  // Tap-and-hold preview management. Holding a card for >= 200ms triggers
  // an audition arp; releasing or moving outside cancels.
  // We track which card is currently being held + a timer handle.
  const previewRef = React.useRef({ holdTimer: null, currentVoices: [], cardId: null });

  // Stop any in-flight preview voices (called on hold-end, card-switch, close).
  const stopPreview = function(){
    const ref = previewRef.current;
    if (ref.holdTimer) { clearTimeout(ref.holdTimer); ref.holdTimer = null; }
    if (ref.currentVoices && ref.currentVoices.length) {
      const now = (function(){
        try {
          const m = getOrCreateMaster && getOrCreateMaster();
          return (m && m.context && m.context.currentTime) || 0;
        } catch(_) { return 0; }
      })();
      ref.currentVoices.forEach(function(v){
        if (v && v.stop) { try { v.stop(now); } catch(_) {} }
      });
      ref.currentVoices = [];
    }
    ref.cardId = null;
  };

  // Fire an audition: C-major arpeggio (C-E-G-C-G-E-C) at 1/8 notes.
  // Cheap to build, distinctive across presets, doesn't require key context.
  const startPreview = function(presetId){
    stopPreview();
    if (!getOrCreateMaster) return;
    let dest = null;
    try { dest = getOrCreateMaster(); } catch(_) { dest = null; }
    if (!dest || !dest.context) return;
    const ctx = dest.context;
    if (ctx.state === "suspended") { try { ctx.resume(); } catch(_) {} }
    const inst = BF_INSTRUMENTS[presetId];
    if (!inst) return;
    // If it's a soundfont preset, kick off the load. The audition tones
    // will be silent until the sf3 file finishes downloading.
    if (inst.engine === "soundfont-v1") {
      ensureSoundfontPresetLoaded(inst, ctx, dest);
    }
    const beat = 60 / (bpm || 120);
    const step = beat / 2;                // 8th notes
    const pattern = [60, 64, 67, 72, 67, 64, 60];
    const startAt = ctx.currentTime + 0.02;
    const voices = [];
    for (let i = 0; i < pattern.length; i++) {
      const v = scheduleMidiNote(ctx, dest, inst, pattern[i], 0.85, startAt + i * step, step * 0.9);
      if (v) voices.push(v);
    }
    previewRef.current.currentVoices = voices;
    previewRef.current.cardId = presetId;
  };

  // Cleanup on unmount — release any held voices
  React.useEffect(function(){
    return function(){ stopPreview(); };
    // eslint-disable-next-line
  }, []);

  // ── Resolve which presets to show ──
  // If search is non-empty, list everything that matches across categories.
  // Otherwise list only the active category. Either way we preserve the
  // declaration order in BF_INSTRUMENTS (which puts Keys → Lead → Pluck →
  // Bass → Pad → Bells → Brass → FX).
  const filtered = (function(){
    const q = search.trim().toLowerCase();
    const all = Object.keys(BF_INSTRUMENTS).map(function(id){ return BF_INSTRUMENTS[id]; });
    if (q) {
      return all.filter(function(p){
        return p.name.toLowerCase().indexOf(q) >= 0
          || (p.description || "").toLowerCase().indexOf(q) >= 0
          || (p.category || "").toLowerCase().indexOf(q) >= 0;
      });
    }
    return all.filter(function(p){ return p.category === activeCategory; });
  })();

  return (
    <div
      onClick={function(){ stopPreview(); onClose && onClose(); }}
      style={{
        position:"fixed", inset: 0, zIndex: 10500,
        background:"rgba(7,6,17,0.55)",
        backdropFilter:"blur(14px) saturate(140%)",
        WebkitBackdropFilter:"blur(14px) saturate(140%)",
        display:"flex", alignItems:"flex-end", justifyContent:"center",
      }}>
      <div
        onClick={function(e){ e.stopPropagation(); }}
        style={{
          // height (not maxHeight) gives the flex column a definite size
          // — without this, the grid child's overflowY:auto can't trigger
          // because the parent has no upper bound to overflow against.
          width: "100%", maxWidth: 720, height: "85vh",
          // Slide-up sheet. Rounded only on top corners.
          background:"linear-gradient(180deg,rgba(28,20,42,0.97) 0%,rgba(18,14,28,0.97) 100%)",
          backdropFilter:"blur(28px) saturate(140%)",
          WebkitBackdropFilter:"blur(28px) saturate(140%)",
          borderTop:"1px solid rgba(167,139,250,0.22)",
          borderLeft:"1px solid rgba(167,139,250,0.10)",
          borderRight:"1px solid rgba(167,139,250,0.10)",
          borderRadius: "20px 20px 0 0",
          boxShadow:
            "0 -16px 64px rgba(0,0,0,0.7), " +
            "0 0 0 1px rgba(167,139,250,0.08), " +
            "inset 0 1px 0 rgba(255,255,255,0.05)",
          display:"flex", flexDirection:"column",
          paddingBottom: "var(--bf-safe-bottom, env(safe-area-inset-bottom))",
          color:"#F8F7FA", fontFamily:"'DM Sans',sans-serif",
          overflow: "hidden",
        }}>
        {/* Drag-handle bar — visual affordance only, not draggable in
            Phase 4. Matches the iOS sheet-presentation convention. */}
        <div style={{
          width: 36, height: 4, borderRadius: 2,
          background: "rgba(167,139,250,0.25)",
          margin: "10px auto 8px",
          flexShrink: 0,
        }} />
        {/* Header */}
        <div style={{
          padding: "8px 18px 14px",
          display:"flex", alignItems:"center", gap: 12,
          borderBottom: "1px solid rgba(167,139,250,0.08)",
          flexShrink: 0,
        }}>
          <div style={{
            width: 32, height: 32, borderRadius: 9,
            background: "linear-gradient(135deg,#8B5CF6 0%,#7C3AED 100%)",
            display:"flex", alignItems:"center", justifyContent:"center",
            fontSize: 15,
            boxShadow:
              "0 0 18px rgba(139,92,246,0.4), " +
              "inset 0 1px 0 rgba(255,255,255,0.25), " +
              "inset 0 -1px 0 rgba(0,0,0,0.2)",
            flexShrink: 0,
          }}>♫</div>
          <div style={{ flex: 1, minWidth: 0 }}>
            <div style={{ fontSize: 9, fontWeight: 700, color: "#7A7488", letterSpacing: 1.8, textTransform: "uppercase" }}>Instruments</div>
            <div style={{ fontSize: 15, fontWeight: 700, color: "#F8F7FA", marginTop: 1, letterSpacing: 0.1 }}>Sound Library</div>
          </div>
          {/* Admin-only: open the soundfont upload overlay. Only visible
              for admins. Tap closes the preset browser and opens the
              admin overlay. */}
          {props.isAdmin && (
            <button onClick={function(){
              stopPreview();
              if (props.onOpenAdminSoundfonts) props.onOpenAdminSoundfonts();
            }} title="Manage soundfonts (admin)" style={{
              background: "rgba(167,139,250,0.06)",
              color: "#C4B5FD",
              border: "1px solid rgba(167,139,250,0.25)",
              borderRadius: 9, padding: "7px 10px",
              fontSize: 10, fontWeight: 800, letterSpacing: 0.8,
              cursor: "pointer",
              fontFamily: "'DM Sans',sans-serif",
              flexShrink: 0,
            }}>+ SF</button>
          )}
          <button onClick={function(){ stopPreview(); onClose && onClose(); }}
            style={{
              background:"rgba(20,15,30,0.4)", color:"#9590A8",
              border:"1px solid rgba(167,139,250,0.10)",
              borderRadius: 9, padding:"7px 11px",
              fontSize: 18, lineHeight: 1, cursor:"pointer",
              fontWeight: 300, fontFamily:"system-ui",
              flexShrink: 0,
            }}>×</button>
        </div>
        {/* Search bar */}
        <div style={{
          padding: "10px 16px",
          borderBottom: "1px solid rgba(167,139,250,0.06)",
          flexShrink: 0,
        }}>
          <input
            value={search}
            onChange={function(e){ setSearch(e.target.value); stopPreview(); }}
            placeholder="Search instruments…"
            style={{
              width: "100%", boxSizing: "border-box",
              background: "rgba(10,8,18,0.6)",
              border: "1px solid rgba(167,139,250,0.15)",
              borderRadius: 10, padding: "10px 14px",
              color: "#F8F7FA", fontSize: 13, fontWeight: 600,
              fontFamily:"'DM Sans',sans-serif",
              outline: "none",
              letterSpacing: 0.1,
            }}
          />
        </div>
        {/* Body: category sidebar + grid */}
        <div style={{
          flex: 1, minHeight: 0,
          display: "flex",
        }}>
          {/* Category sidebar — vertical list on the left.
              Hidden when searching since the search spans all categories. */}
          {!search && (
            <div style={{
              width: 100, flexShrink: 0,
              borderRight: "1px solid rgba(167,139,250,0.08)",
              background: "rgba(13,10,20,0.4)",
              overflowY: "auto",
              WebkitOverflowScrolling: "touch",
              padding: "8px 6px",
            }}>
              {BF_INSTRUMENT_CATEGORIES.map(function(cat){
                const active = activeCategory === cat.id;
                return (
                  <button key={cat.id} onClick={function(){
                    setActiveCategory(cat.id);
                    stopPreview();
                  }} style={{
                    display:"block", width:"100%",
                    background: active
                      ? "rgba(139,92,246,0.18)"
                      : "transparent",
                    color: active ? "#F8F7FA" : "#9590A8",
                    border: "1px solid " + (active ? "rgba(167,139,250,0.3)" : "transparent"),
                    borderRadius: 8,
                    padding: "10px 8px",
                    fontSize: 11, fontWeight: 700,
                    cursor: "pointer",
                    fontFamily:"'DM Sans',sans-serif",
                    letterSpacing: 0.4,
                    marginBottom: 4,
                    textAlign: "left",
                    // Prevent long labels (like "Instruments") from overflowing
                    // the narrow sidebar — clamp to one line with ellipsis.
                    whiteSpace: "nowrap",
                    overflow: "hidden",
                    textOverflow: "ellipsis",
                  }}>
                    <span style={{
                      fontSize: 13, marginRight: 6,
                      color: active ? cat.color : "#7A7488",
                    }}>{cat.icon}</span>
                    {cat.id}
                  </button>
                );
              })}
            </div>
          )}
          {/* Preset grid */}
          <div className="bf-show-scroll"
            onTouchMove={function(e){ e.stopPropagation(); }}
            style={{
              flex: 1, minWidth: 0,
              overflowY: "scroll",
              WebkitOverflowScrolling: "touch",
              overscrollBehavior: "contain",
              padding: "12px 14px",
              // Force GPU layer — sometimes fixes iOS scroll latch
              transform: "translateZ(0)",
            }}>
            {filtered.length === 0 ? (
              <div style={{
                color: "#5C5870", fontSize: 13, textAlign: "center",
                padding: "40px 12px", lineHeight: 1.5,
              }}>No instruments match "{search}"</div>
            ) : (
              <div style={{
                display: "grid",
                gridTemplateColumns: "repeat(auto-fill, minmax(140px, 1fr))",
                gap: 10,
              }}>
                {filtered.map(function(p){
                  const isCurrent = p.id === currentInstId;
                  // Tap-and-hold handlers. We use a 200ms hold threshold —
                  // long enough that a tap doesn't accidentally preview,
                  // short enough that the producer doesn't feel stuck.
                  //
                  // CRITICAL: touch-action: pan-y on the card style tells
                  // iOS Safari "let vertical scrolling through this element
                  // pass to the parent." Without it, React's onTouchStart
                  // handler blocks the parent's native scroll even though
                  // we don't stopPropagation. Mouse-only platforms ignore
                  // this property — no harm to desktop behavior.
                  const handleCardDown = function(e){
                    const id = p.id;
                    const t = (e.touches && e.touches[0]) || e;
                    previewRef.current.startX = t.clientX;
                    previewRef.current.startY = t.clientY;
                    previewRef.current.scrolled = false;
                    previewRef.current.holdTimer = setTimeout(function(){
                      previewRef.current.holdTimer = null;
                      startPreview(id);
                    }, 200);
                  };
                  const handleCardMove = function(e){
                    // Cancel the pending preview if user is scrolling
                    const ref = previewRef.current;
                    const t = (e.touches && e.touches[0]) || e;
                    const dx = t.clientX - (ref.startX || 0);
                    const dy = t.clientY - (ref.startY || 0);
                    if (Math.abs(dx) > 10 || Math.abs(dy) > 10) {
                      ref.scrolled = true;
                      if (ref.holdTimer) {
                        clearTimeout(ref.holdTimer);
                        ref.holdTimer = null;
                      }
                    }
                  };
                  const handleCardUp = function(e){
                    // Three cases:
                    //   1. Preview was running (long-press fired) → stop it
                    //   2. Scrolled too much → do nothing (was a scroll gesture)
                    //   3. Quick tap with timer still pending → assign preset
                    const ref = previewRef.current;
                    const wasPreviewing = !!ref.cardId && ref.cardId === p.id;
                    const wasScroll     = !!ref.scrolled;
                    const hadTimer      = !!ref.holdTimer;
                    if (ref.holdTimer) {
                      clearTimeout(ref.holdTimer); ref.holdTimer = null;
                    }
                    if (wasPreviewing) {
                      stopPreview();
                    } else if (!wasScroll && hadTimer) {
                      // Quick tap → assign preset
                      onSelect && onSelect(p.id);
                    }
                    // Reset scrolled flag for next gesture
                    ref.scrolled = false;
                  };
                  const handleCardCancel = function(){
                    const ref = previewRef.current;
                    if (ref.holdTimer) {
                      clearTimeout(ref.holdTimer);
                      ref.holdTimer = null;
                    }
                    ref.scrolled = false;
                    stopPreview();
                  };
                  return (
                    <div key={p.id}
                      onTouchStart={handleCardDown}
                      onTouchMove={handleCardMove}
                      onTouchEnd={handleCardUp}
                      onTouchCancel={handleCardCancel}
                      onMouseDown={handleCardDown}
                      onMouseMove={handleCardMove}
                      onMouseUp={handleCardUp}
                      onMouseLeave={handleCardCancel}
                      style={{
                        background: isCurrent
                          ? "linear-gradient(165deg, rgba(167,139,250,0.18) 0%, rgba(20,15,30,0.6) 100%)"
                          : "linear-gradient(165deg, rgba(20,15,30,0.6) 0%, rgba(13,10,20,0.6) 100%)",
                        border: "1px solid " + (isCurrent ? "rgba(167,139,250,0.45)" : "rgba(167,139,250,0.10)"),
                        borderRadius: 12,
                        padding: 12,
                        cursor: "pointer",
                        boxShadow: isCurrent
                          ? "0 8px 24px rgba(139,92,246,0.20), inset 0 1px 0 rgba(255,255,255,0.06)"
                          : "inset 0 1px 0 rgba(255,255,255,0.02)",
                        transition: "border 0.12s, box-shadow 0.12s",
                        userSelect: "none",
                        WebkitUserSelect: "none",
                        WebkitTouchCallout: "none",
                        // CRITICAL: lets iOS Safari pass vertical scrolling
                        // through to the parent scroll container even though
                        // this element has touchstart handlers. Without this,
                        // every touch is captured by the card and the grid
                        // can never scroll.
                        touchAction: "pan-y",
                        position: "relative",
                      }}>
                      {/* Color swatch */}
                      <div style={{
                        width: 36, height: 36, borderRadius: 9,
                        background: "linear-gradient(135deg," + p.color + " 0%," + p.color + "cc 100%)",
                        boxShadow:
                          "0 0 16px " + p.color + "55, " +
                          "inset 0 1px 0 rgba(255,255,255,0.25), " +
                          "inset 0 -1px 0 rgba(0,0,0,0.2)",
                        marginBottom: 10,
                      }} />
                      <div style={{
                        fontSize: 12, fontWeight: 700, color: "#F8F7FA",
                        letterSpacing: 0.1, marginBottom: 3,
                        overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap",
                      }}>{p.name}</div>
                      <div style={{
                        fontSize: 10, color: "#7A7488",
                        letterSpacing: 0.2, lineHeight: 1.4,
                        // 2-line clamp
                        display: "-webkit-box", WebkitLineClamp: 2,
                        WebkitBoxOrient: "vertical",
                        overflow: "hidden",
                        minHeight: 28,
                      }}>{p.description || p.category}</div>
                      {/* Badges row — multi-preset bank badge + Custom
                          badge sit side-by-side in the same row so they
                          never overlap. */}
                      {((p.presets && p.presets.length > 1) || p.isCustom) && (
                        <div style={{
                          marginTop: 6,
                          display: "flex", flexWrap: "wrap", gap: 4,
                          alignItems: "center",
                        }}>
                          {p.presets && p.presets.length > 1 && (
                            <div style={{
                              fontSize: 8, fontWeight: 800, letterSpacing: 1,
                              background: "rgba(34,211,238,0.10)",
                              color: "#22D3EE",
                              padding: "2px 6px", borderRadius: 4,
                              textTransform: "uppercase",
                              border: "1px solid rgba(34,211,238,0.25)",
                              whiteSpace: "nowrap",
                            }}>BANK · {p.presets.length}</div>
                          )}
                          {p.isCustom && previewRef.current.cardId !== p.id && (
                            <div style={{
                              fontSize: 8, fontWeight: 800, letterSpacing: 1,
                              background: "rgba(167,139,250,0.18)",
                              color: "#C4B5FD",
                              padding: "2px 6px", borderRadius: 4,
                              textTransform: "uppercase",
                              border: "1px solid rgba(167,139,250,0.25)",
                              whiteSpace: "nowrap",
                            }}>Custom</div>
                          )}
                        </div>
                      )}
                      {isCurrent && (
                        <div style={{
                          position: "absolute", top: 8, right: 8,
                          fontSize: 8, fontWeight: 800, letterSpacing: 1,
                          background: "rgba(167,139,250,0.25)",
                          color: "#C4B5FD",
                          padding: "2px 6px", borderRadius: 4,
                          textTransform: "uppercase",
                        }}>Active</div>
                      )}
                      {/* Soundfont loading indicator. The engine instance is
                          a module-scope singleton; we check its per-preset
                          status. Only renders for soundfont presets (synth
                          presets are always "ready"). */}
                      {p.engine === "soundfont-v1" && (function(){
                        const eng = _bfSfEngineInstance;
                        const st = eng ? eng.getPresetStatus(p.id) : "idle";
                        if (st === "loading") {
                          return (
                            <div style={{
                              position: "absolute", top: 8, right: isCurrent ? 56 : 8,
                              fontSize: 8, fontWeight: 800, letterSpacing: 1,
                              background: "rgba(167,139,250,0.18)",
                              color: "#C4B5FD",
                              padding: "2px 6px", borderRadius: 4,
                              textTransform: "uppercase",
                              display: "flex", alignItems: "center", gap: 4,
                            }}>
                              <span style={{
                                display: "inline-block",
                                width: 6, height: 6, borderRadius: "50%",
                                background: "#C4B5FD",
                                animation: "bf-sf-pulse 0.9s ease-in-out infinite",
                              }} />
                              Loading
                            </div>
                          );
                        }
                        if (st === "ready") {
                          return (
                            <div style={{
                              position: "absolute", top: 8, right: isCurrent ? 56 : 8,
                              fontSize: 8, fontWeight: 800, letterSpacing: 1,
                              background: "rgba(167,243,208,0.18)",
                              color: "#A7F3D0",
                              padding: "2px 6px", borderRadius: 4,
                              textTransform: "uppercase",
                            }}>✓ Loaded</div>
                          );
                        }
                        // Error states are NOT surfaced as a badge here.
                        // Most failures are transient (the audio context
                        // wasn't ready yet, brief network blip on B2,
                        // engine library still initialising) and a
                        // FAILED badge gave the false impression that
                        // the preset was permanently broken. Tapping
                        // the card retries the load via loadPreset's
                        // delete-on-error path; the user sees the
                        // Loading indicator again and (if the retry
                        // succeeds) the ✓ Loaded badge.
                        return null;
                      })()}
                      {previewRef.current.cardId === p.id && (
                        <div style={{
                          position: "absolute", bottom: 8, right: 8,
                          fontSize: 8, fontWeight: 800, letterSpacing: 1,
                          color: p.color,
                          textTransform: "uppercase",
                        }}>♪ Preview</div>
                      )}
                    </div>
                  );
                })}
              </div>
            )}
          </div>
        </div>
        {/* Footer hint */}
        <div style={{
          padding: "10px 16px",
          borderTop: "1px solid rgba(167,139,250,0.06)",
          fontSize: 10, color: "#5C5870", letterSpacing: 0.3,
          flexShrink: 0,
          textAlign: "center",
        }}>Tap to assign · hold to preview</div>
      </div>
    </div>
  );
}

// ═════════════════════════════════════════════════════════════════════════════
//  EditSoundPanel (Phase 4) — per-track ADSR + filter overrides
// ═════════════════════════════════════════════════════════════════════════════
//
// Sliders adjust track.instrumentOverrides.* — merged onto the preset at
// schedule time. Resetting clears the overrides so the track returns to
// the preset's defaults.
//
// Props:
//   track            — the MIDI track (we read .instrumentId + .instrumentOverrides)
//   onChange(patch)  — caller applies the patch (merged into instrumentOverrides) via setTracks
//   onReset          — clears all overrides
//   onClose          — close handler
function EditSoundPanel(props) {
  const { track, onChange, onReset, onClose } = props;
  // Sampler tracks have samplerData and a synthetic instrumentId that
  // isn't in BF_INSTRUMENTS — synthesize an inst-like meta so the panel
  // shows ADSR/filter controls (the sampler voice builder applies them).
  const isSampler = !!(track && track.samplerData);
  const inst = isSampler
    ? {
        id: "sampler:" + (track.samplerData.sampleHash || "?"),
        name: track.samplerData.sampleName || track.name || "Sampler",
        engine: "sampler-v1",
        amp: { attack: 0.003, decay: 0.0, sustain: 1.0, release: 0.015 },
        filter: { baseCutoff: 18000, q: 0.7 },
        gain: track.samplerData.gain != null ? track.samplerData.gain : 1.0,
      }
    : getInstrumentMeta(track.instrumentId || "piano-grand");
  const trackColor = track.color || "#A78BFA";
  const overrides = track.instrumentOverrides || {};
  // Effective ADSR/filter values — overrides if set, else preset defaults.
  // We display the effective values so sliders always show the current
  // sound, not a confusing "untouched" zero.
  const effAttack  = overrides.amp && overrides.amp.attack  != null ? overrides.amp.attack  : (inst.amp || {}).attack  || 0.005;
  const effDecay   = overrides.amp && overrides.amp.decay   != null ? overrides.amp.decay   : (inst.amp || {}).decay   || 0.30;
  const effSustain = overrides.amp && overrides.amp.sustain != null ? overrides.amp.sustain : (inst.amp || {}).sustain || 0.50;
  const effRelease = overrides.amp && overrides.amp.release != null ? overrides.amp.release : (inst.amp || {}).release || 0.30;
  const effCutoff  = overrides.filter && overrides.filter.baseCutoff != null ? overrides.filter.baseCutoff : (inst.filter || {}).baseCutoff || 2000;
  const effQ       = overrides.filter && overrides.filter.q          != null ? overrides.filter.q          : (inst.filter || {}).q          || 0.8;
  const effGain    = overrides.gain != null ? overrides.gain : (inst.gain != null ? inst.gain : 0.55);

  const setAmp = function(field, value){
    const nextAmp = Object.assign({}, overrides.amp || {});
    nextAmp[field] = value;
    onChange && onChange({ amp: nextAmp });
  };
  const setFilter = function(field, value){
    const nextFilter = Object.assign({}, overrides.filter || {});
    nextFilter[field] = value;
    onChange && onChange({ filter: nextFilter });
  };
  const setGain = function(value){
    onChange && onChange({ gain: value });
  };

  // Slider helper — a labeled vertical-prose row with the current value
  // shown right-aligned so the producer can see absolute numbers.
  const slider = function(opts){
    return (
      <div style={{ marginBottom: 18 }} key={opts.label}>
        <div style={{
          display:"flex", justifyContent:"space-between", alignItems:"center",
          marginBottom: 6,
        }}>
          <div style={{ color:"#7A7488", fontSize: 10, fontWeight: 700, letterSpacing: 1.6, textTransform:"uppercase" }}>{opts.label}</div>
          <div style={{ color: "#F8F7FA", fontSize: 11, fontWeight: 700, letterSpacing: 0.3, fontFamily: "monospace" }}>
            {opts.format(opts.value)}
          </div>
        </div>
        <input type="range"
          min={opts.min} max={opts.max} step={opts.step}
          value={opts.value}
          onChange={function(e){ opts.onChange(+e.target.value); }}
          style={{ width:"100%", accentColor: "#A78BFA" }}
        />
      </div>
    );
  };

  return (
    <div
      onClick={function(){ onClose && onClose(); }}
      style={{
        position:"fixed", inset: 0, zIndex: 10500,
        background:"rgba(7,6,17,0.55)",
        backdropFilter:"blur(14px) saturate(140%)",
        WebkitBackdropFilter:"blur(14px) saturate(140%)",
        display:"flex", alignItems:"center", justifyContent:"center",
        padding: 16,
      }}>
      <div
        onClick={function(e){ e.stopPropagation(); }}
        style={{
          background:"linear-gradient(165deg,rgba(28,20,42,0.92) 0%,rgba(18,14,28,0.92) 100%)",
          backdropFilter:"blur(24px) saturate(140%)",
          WebkitBackdropFilter:"blur(24px) saturate(140%)",
          border:"1px solid rgba(167,139,250,0.2)",
          borderRadius: 18, padding: 22,
          width:"100%", maxWidth: 420,
          maxHeight:"85vh", overflowY:"auto",
          boxShadow:
            "0 32px 80px rgba(0,0,0,0.7), " +
            "0 0 0 1px rgba(167,139,250,0.08), " +
            "0 0 60px " + trackColor + "22, " +
            "inset 0 1px 0 rgba(255,255,255,0.05)",
          color:"#F8F7FA", fontFamily:"'DM Sans',sans-serif",
        }}>
        {/* Header */}
        <div style={{ display:"flex", alignItems:"center", gap: 12, marginBottom: 18 }}>
          <div style={{
            width: 36, height: 36, borderRadius: 10,
            background: "linear-gradient(135deg," + trackColor + " 0%," + trackColor + "cc 100%)",
            boxShadow:
              "0 0 16px " + trackColor + "66, " +
              "inset 0 1px 0 rgba(255,255,255,0.25), " +
              "inset 0 -1px 0 rgba(0,0,0,0.2)",
            flexShrink: 0,
          }} />
          <div style={{ flex: 1, minWidth: 0 }}>
            <div style={{ fontSize: 9, fontWeight: 700, color: "#7A7488", letterSpacing: 1.8, textTransform: "uppercase" }}>Edit Sound</div>
            <div style={{ fontSize: 15, fontWeight: 700, color: "#F8F7FA", marginTop: 1, letterSpacing: 0.1,
              overflow:"hidden", textOverflow:"ellipsis", whiteSpace:"nowrap" }}>{inst.name}</div>
          </div>
          <button onClick={function(){ onClose && onClose(); }}
            style={{
              background:"rgba(20,15,30,0.4)", color:"#9590A8",
              border:"1px solid rgba(167,139,250,0.10)",
              borderRadius: 9, padding:"7px 11px",
              fontSize: 18, lineHeight: 1, cursor:"pointer",
              fontWeight: 300, fontFamily:"system-ui",
            }}>×</button>
        </div>

        {/* Soundfonts get the full ADSR/filter controls too — they map to
            MIDI CC modifiers (attack/decay/release/cutoff/resonance) that
            shape the sample's native envelope, applied both live and on
            export. A short hint clarifies these are relative to the
            recorded sound. */}
        {(inst.engine === "soundfont-v1" || inst.engine === "sampler-v1") && (
          <div style={{
            background: "rgba(167,139,250,0.06)",
            border: "1px solid rgba(167,139,250,0.16)",
            borderRadius: 10,
            padding: "10px 14px",
            marginBottom: 16,
          }}>
            <div style={{
              fontSize: 9, fontWeight: 700, letterSpacing: 1.8,
              color: "#C4B5FD", textTransform: "uppercase",
              marginBottom: 5,
            }}>♪ Real Instrument</div>
            <div style={{ fontSize: 11, color: "#9590A8", lineHeight: 1.5 }}>
              These shape the sample's natural envelope and tone — set them to
              taste. They apply both live and on export.
            </div>
          </div>
        )}
        <React.Fragment>
            {/* Section: Envelope */}
            <div style={{
              fontSize: 9, fontWeight: 700, letterSpacing: 1.8,
              color: "#5C5870", textTransform: "uppercase",
              marginBottom: 10,
            }}>Envelope</div>
            {(function(){
              // DAW-style exponential time mapping. A linear seconds slider
              // crams all the musical detail (1-300ms) into the bottom sliver
              // of travel, causing the "huge jumps" — so we run the slider as
              // 0..1000 and map exponentially to seconds. Most of the travel
              // lives where it matters (short times), and the top reaches 2s.
              const ATK_MIN = 0.001, ATK_MAX = 2.0;
              const secToPos = function(sec){
                const s = Math.max(ATK_MIN, Math.min(ATK_MAX, sec));
                return Math.round(1000 * (Math.log(s) - Math.log(ATK_MIN)) / (Math.log(ATK_MAX) - Math.log(ATK_MIN)));
              };
              const posToSec = function(pos){
                const f = Math.max(0, Math.min(1000, pos)) / 1000;
                return ATK_MIN * Math.exp(f * (Math.log(ATK_MAX) - Math.log(ATK_MIN)));
              };
              return slider({
                label: "Attack",
                value: secToPos(effAttack), min: 0, max: 1000, step: 1,
                format: function(){ return effAttack < 0.01 ? (effAttack*1000).toFixed(0) + " ms" : effAttack.toFixed(2) + " s"; },
                onChange: function(pos){ setAmp("attack", posToSec(pos)); },
              });
            })()}
            {(function(){
              const D_MIN = 0.01, D_MAX = 3.0;
              const secToPos = function(sec){ const s=Math.max(D_MIN,Math.min(D_MAX,sec)); return Math.round(1000*(Math.log(s)-Math.log(D_MIN))/(Math.log(D_MAX)-Math.log(D_MIN))); };
              const posToSec = function(pos){ const f=Math.max(0,Math.min(1000,pos))/1000; return D_MIN*Math.exp(f*(Math.log(D_MAX)-Math.log(D_MIN))); };
              return slider({
                label: "Decay",
                value: secToPos(effDecay), min: 0, max: 1000, step: 1,
                format: function(){ return effDecay < 0.01 ? (effDecay*1000).toFixed(0) + " ms" : effDecay.toFixed(2) + " s"; },
                onChange: function(pos){ setAmp("decay", posToSec(pos)); },
              });
            })()}
            {slider({
              label: "Sustain",
              value: effSustain, min: 0, max: 1, step: 0.01,
              format: function(v){ return Math.round(v * 100) + "%"; },
              onChange: function(v){ setAmp("sustain", v); },
            })}
            {(function(){
              const R_MIN = 0.01, R_MAX = 4.0;
              const secToPos = function(sec){ const s=Math.max(R_MIN,Math.min(R_MAX,sec)); return Math.round(1000*(Math.log(s)-Math.log(R_MIN))/(Math.log(R_MAX)-Math.log(R_MIN))); };
              const posToSec = function(pos){ const f=Math.max(0,Math.min(1000,pos))/1000; return R_MIN*Math.exp(f*(Math.log(R_MAX)-Math.log(R_MIN))); };
              return slider({
                label: "Release",
                value: secToPos(effRelease), min: 0, max: 1000, step: 1,
                format: function(){ return effRelease < 0.01 ? (effRelease*1000).toFixed(0) + " ms" : effRelease.toFixed(2) + " s"; },
                onChange: function(pos){ setAmp("release", posToSec(pos)); },
              });
            })()}
            {/* Section: Filter */}
            <div style={{
              fontSize: 9, fontWeight: 700, letterSpacing: 1.8,
              color: "#5C5870", textTransform: "uppercase",
              marginTop: 18, marginBottom: 10,
            }}>Filter</div>
            {slider({
              label: "Cutoff",
              value: effCutoff, min: 100, max: 8000, step: 10,
              format: function(v){ return Math.round(v) + " Hz"; },
              onChange: function(v){ setFilter("baseCutoff", v); },
            })}
            {slider({
              label: "Resonance",
              value: effQ, min: 0.1, max: 8, step: 0.1,
              format: function(v){ return v.toFixed(1); },
              onChange: function(v){ setFilter("q", v); },
            })}
          </React.Fragment>
        {/* Section: Gain trim — applies to both engines */}
        <div style={{
          fontSize: 9, fontWeight: 700, letterSpacing: 1.8,
          color: "#5C5870", textTransform: "uppercase",
          marginTop: 18, marginBottom: 10,
        }}>Volume</div>
        {slider({
          label: "Trim",
          value: effGain, min: 0, max: 1.2, step: 0.01,
          format: function(v){ return Math.round(v * 100) + "%"; },
          onChange: function(v){ setGain(v); },
        })}

        {/* Reset */}
        <div style={{ display:"flex", justifyContent:"flex-end", gap: 8, marginTop: 18 }}>
          <button onClick={function(){ onReset && onReset(); }} style={{
            background:"rgba(20,15,30,0.4)", color:"#9590A8",
            border:"1px solid rgba(167,139,250,0.12)",
            borderRadius: 10, padding:"10px 16px",
            fontSize: 11, fontWeight: 700, cursor:"pointer",
            fontFamily:"'DM Sans',sans-serif",
            letterSpacing: 0.6, textTransform: "uppercase",
          }}>Reset to Preset</button>
          <button onClick={function(){ onClose && onClose(); }} style={{
            background:"linear-gradient(135deg,#A78BFA 0%,#8B5CF6 50%,#7C3AED 100%)",
            color:"white", border:"none",
            borderRadius: 10, padding:"10px 20px",
            fontSize: 11, fontWeight: 800, cursor:"pointer",
            boxShadow:
              "0 6px 18px rgba(139,92,246,0.4), " +
              "inset 0 1px 0 rgba(255,255,255,0.25)",
            letterSpacing: 0.6, textTransform: "uppercase",
          }}>Done</button>
        </div>
      </div>
    </div>
  );
}

// ═════════════════════════════════════════════════════════════════════════════
//  ComposeSheet (Phase 5) — generator panel (chords / melody / counter / arp)
// ═════════════════════════════════════════════════════════════════════════════
//
// Slide-up bottom sheet, four tabs. Each tab has its own controls and a
// big "Generate" CTA at the bottom. Generation REPLACES the clip's notes
// (undo restores). New notes are auto-selected so the user can immediately
// transpose / move / humanize via the existing action bar.
//
// Props:
//   open                — when true, sheet is visible
//   scaleRoot           — current key (0..11)
//   scaleId             — current scale id ("off"|"major"|"minor"|...)
//   bpm, timeSigNum     — for rhythm timing
//   clipDur             — current clip duration in seconds
//   selectedNotes       — array of currently-selected note payloads (for arp + counter source)
//   allNotes            — array of all notes in the clip (used as fallback source)
//   onApplyNotes(notes) — caller commits notes via the editor's commitNotes + selects them
//   onClose             — close handler
function ComposeSheet(props) {
  const { scaleRoot, scaleId, bpm, timeSigNum, clipDur, selectedNotes, allNotes, onApplyNotes, onClose } = props;
  const beatSec = 60 / (bpm || 120);
  const barSec  = beatSec * (timeSigNum || 4);
  const maxBars = Math.max(1, Math.floor(clipDur / barSec));
  const scaleMeta = BF_SCALES[scaleId] || BF_SCALES["off"];

  // ── Tab state ──
  const [tab, setTab] = React.useState("chords");

  // ── Chord-progression tab state ──
  const [genreId, setGenreId] = React.useState("trap");
  const [progBars, setProgBars] = React.useState(Math.min(4, maxBars));
  const [progOctave, setProgOctave] = React.useState(3);  // C3 root
  const genre = BF_PROGRESSIONS[genreId];

  // ── Melody tab state ──
  const [moodId, setMoodId] = React.useState("emotional");
  const [densityId, setDensityId] = React.useState("medium");
  const [melodyBars, setMelodyBars] = React.useState(Math.min(2, maxBars));
  const [melodyOctave, setMelodyOctave] = React.useState(4);

  // ── Counter-melody tab state ──
  // Nothing to configure — operates on existing notes. Show feedback if
  // no source notes exist.

  // ── Arp tab state ──
  const [arpPattern, setArpPattern] = React.useState("up");
  const [arpRate, setArpRate] = React.useState(4);     // 1/16 default (beat / 4)
  const [arpOctaveSpan, setArpOctaveSpan] = React.useState(1);
  const [arpBars, setArpBars] = React.useState(Math.min(2, maxBars));

  // Resolve which source notes the arp/counter use.
  // Selection wins; if no selection, fall back to all notes in the clip.
  const arpSource = (selectedNotes && selectedNotes.length > 0) ? selectedNotes : allNotes;

  // ── Generate handlers ──
  const generateProgressionAction = function(){
    if (!genre) return;
    // Pick a random progression from the genre's bank
    const progs = genre.progressions || [];
    if (progs.length === 0) return;
    const prog = progs[Math.floor(Math.random() * progs.length)];
    const notes = generateProgression(prog, genre.mode, scaleRoot, progBars, beatSec, timeSigNum || 4, progOctave);
    if (onApplyNotes) onApplyNotes(notes);
  };
  const generateMelodyAction = function(){
    // Use the editor's scale if set, else default to major (so melody
    // generation always has SOMETHING to work with).
    const intervals = scaleMeta.intervals || BF_SCALES["major"].intervals;
    const notes = generateMelody(scaleRoot, intervals, moodId, densityId, melodyBars, beatSec, timeSigNum || 4, melodyOctave);
    if (onApplyNotes) onApplyNotes(notes);
  };
  const generateCounterAction = function(){
    if (!arpSource || arpSource.length === 0) return;
    const intervals = scaleMeta.intervals || BF_SCALES["major"].intervals;
    const notes = generateCounterMelody(arpSource, scaleRoot, intervals);
    // Counter merges with the source — we ADD the counter line. This is
    // the one exception to "replace": a counter-melody alone isn't useful
    // without the source. We mark it with replace:false.
    if (onApplyNotes) onApplyNotes(notes, { replace: false });
  };
  const generateArpAction = function(){
    if (!arpSource || arpSource.length === 0) return;
    // If the user has notes selected, scope the ARP to the selection's
    // time range and replace ONLY those notes — so a producer can grab a
    // few notes of their melody and turn them into a hi-hat roll or fill
    // without losing the rest of the part. If no selection, generate over
    // the full bar count as before.
    const useSel = selectedNotes && selectedNotes.length > 0;
    let opts = null;
    if (useSel) {
      let selStart = Infinity, selEnd = -Infinity;
      for (let i = 0; i < selectedNotes.length; i++) {
        const n = selectedNotes[i];
        const s = n.start || 0;
        const e = s + (n.duration || 0.1);
        if (s < selStart) selStart = s;
        if (e > selEnd)   selEnd   = e;
      }
      if (selEnd > selStart) {
        opts = { timeRangeStart: selStart, timeRangeDuration: selEnd - selStart };
      }
    }
    const notes = generateArpFromSelection(arpSource, arpPattern, arpRate, arpOctaveSpan, arpBars, beatSec, timeSigNum || 4, opts);
    if (onApplyNotes) onApplyNotes(notes, useSel ? { replaceSelected: true } : undefined);
  };

  // ── UI helpers ──
  const tabBtn = function(id, label){
    const active = tab === id;
    return (
      <button key={id} onClick={function(){ setTab(id); }}
        style={{
          flex: 1,
          background: active ? "rgba(139,92,246,0.18)" : "transparent",
          color: active ? "#F8F7FA" : "#7A7488",
          border: "none",
          borderBottom: "2px solid " + (active ? "#A78BFA" : "transparent"),
          padding: "12px 8px",
          fontSize: 11, fontWeight: 700, cursor: "pointer",
          fontFamily: "'DM Sans',sans-serif",
          letterSpacing: 0.6, textTransform: "uppercase",
          transition: "color 0.12s, background 0.12s",
        }}>{label}</button>
    );
  };

  const sectionLabel = function(text){
    return (
      <div style={{
        fontSize: 9, color:"#7A7488", letterSpacing: 1.8,
        textTransform:"uppercase", fontWeight: 700, marginBottom: 8,
      }}>{text}</div>
    );
  };

  const chip = function(opts){
    return (
      <button key={opts.key || opts.label} onClick={opts.onClick} style={{
        background: opts.active
          ? "linear-gradient(135deg,#8B5CF6 0%,#7C3AED 100%)"
          : "rgba(20,15,30,0.5)",
        color: opts.active ? "white" : "#9590A8",
        border: "1px solid " + (opts.active ? "rgba(167,139,250,0.4)" : "rgba(167,139,250,0.10)"),
        borderRadius: 7, padding: "6px 11px",
        fontSize: 11, fontWeight: 700, cursor:"pointer",
        fontFamily:"'DM Sans',sans-serif",
        letterSpacing: 0.3, whiteSpace: "nowrap",
        boxShadow: opts.active
          ? "0 4px 12px rgba(139,92,246,0.3), inset 0 1px 0 rgba(255,255,255,0.15)"
          : "none",
      }}>{opts.label}</button>
    );
  };

  // The main CTA at the bottom of each tab. Always violet gradient.
  const cta = function(label, onClick, disabled){
    return (
      <button onClick={onClick} disabled={disabled} style={{
        width: "100%",
        background: disabled
          ? "rgba(60,55,75,0.4)"
          : "linear-gradient(135deg,#A78BFA 0%,#8B5CF6 50%,#7C3AED 100%)",
        color: "white", border: "none",
        borderRadius: 12, padding: "14px 20px",
        fontSize: 13, fontWeight: 800, cursor: disabled ? "not-allowed" : "pointer",
        boxShadow: disabled
          ? "none"
          : "0 8px 24px rgba(139,92,246,0.4), inset 0 1px 0 rgba(255,255,255,0.25)",
        letterSpacing: 1.4, textTransform: "uppercase",
        fontFamily:"'DM Sans',sans-serif",
        marginTop: 10,
      }}>{label}</button>
    );
  };

  return (
    <div
      onClick={onClose}
      style={{
        position:"fixed", inset: 0, zIndex: 10500,
        background:"rgba(7,6,17,0.55)",
        backdropFilter:"blur(14px) saturate(140%)",
        WebkitBackdropFilter:"blur(14px) saturate(140%)",
        display:"flex", alignItems:"flex-end", justifyContent:"center",
      }}>
      <div
        onClick={function(e){ e.stopPropagation(); }}
        style={{
          width: "100%", maxWidth: 720, maxHeight: "88vh",
          background:"linear-gradient(180deg,rgba(28,20,42,0.97) 0%,rgba(18,14,28,0.97) 100%)",
          backdropFilter:"blur(28px) saturate(140%)",
          WebkitBackdropFilter:"blur(28px) saturate(140%)",
          borderTop:"1px solid rgba(167,139,250,0.22)",
          borderLeft:"1px solid rgba(167,139,250,0.10)",
          borderRight:"1px solid rgba(167,139,250,0.10)",
          borderRadius: "20px 20px 0 0",
          boxShadow:
            "0 -16px 64px rgba(0,0,0,0.7), " +
            "0 0 0 1px rgba(167,139,250,0.08), " +
            "inset 0 1px 0 rgba(255,255,255,0.05)",
          display:"flex", flexDirection:"column",
          paddingBottom: "var(--bf-safe-bottom, env(safe-area-inset-bottom))",
          color:"#F8F7FA", fontFamily:"'DM Sans',sans-serif",
          overflow: "hidden",
        }}>
        {/* Drag handle */}
        <div style={{
          width: 36, height: 4, borderRadius: 2,
          background: "rgba(167,139,250,0.25)",
          margin: "10px auto 8px",
          flexShrink: 0,
        }} />
        {/* Header */}
        <div style={{
          padding: "8px 18px 14px",
          display:"flex", alignItems:"center", gap: 12,
          borderBottom: "1px solid rgba(167,139,250,0.08)",
          flexShrink: 0,
        }}>
          <div style={{
            width: 32, height: 32, borderRadius: 9,
            background: "linear-gradient(135deg,#8B5CF6 0%,#7C3AED 100%)",
            display:"flex", alignItems:"center", justifyContent:"center",
            fontSize: 15,
            boxShadow:
              "0 0 18px rgba(139,92,246,0.4), " +
              "inset 0 1px 0 rgba(255,255,255,0.25), " +
              "inset 0 -1px 0 rgba(0,0,0,0.2)",
            flexShrink: 0,
          }}>✦</div>
          <div style={{ flex: 1, minWidth: 0 }}>
            <div style={{ fontSize: 9, fontWeight: 700, color: "#7A7488", letterSpacing: 1.8, textTransform: "uppercase" }}>Compose</div>
            <div style={{ fontSize: 15, fontWeight: 700, color: "#F8F7FA", marginTop: 1, letterSpacing: 0.1 }}>Generate Notes</div>
          </div>
          <button onClick={onClose}
            style={{
              background:"rgba(20,15,30,0.4)", color:"#9590A8",
              border:"1px solid rgba(167,139,250,0.10)",
              borderRadius: 9, padding:"7px 11px",
              fontSize: 18, lineHeight: 1, cursor:"pointer",
              fontWeight: 300, fontFamily:"system-ui",
              flexShrink: 0,
            }}>×</button>
        </div>

        {/* Tab bar */}
        <div style={{
          display: "flex",
          borderBottom: "1px solid rgba(167,139,250,0.08)",
          flexShrink: 0,
        }}>
          {tabBtn("chords", "Chords")}
          {tabBtn("melody", "Melody")}
          {tabBtn("counter", "Counter")}
          {tabBtn("arp", "Arp")}
        </div>

        {/* Body — scrollable, holds the active tab's controls */}
        <div style={{
          flex: 1, minHeight: 0,
          overflowY: "auto",
          WebkitOverflowScrolling: "touch",
          padding: "16px",
        }}>
          {/* ── CHORDS TAB ── */}
          {tab === "chords" && (
            <div>
              {sectionLabel("Genre")}
              <div style={{ display:"flex", gap: 6, flexWrap:"wrap", marginBottom: 18 }}>
                {BF_PROGRESSION_GENRES.map(function(gid){
                  const g = BF_PROGRESSIONS[gid];
                  return chip({
                    key: gid,
                    label: g.name,
                    active: genreId === gid,
                    onClick: function(){ setGenreId(gid); },
                  });
                })}
              </div>
              {genre && (
                <div style={{
                  marginBottom: 18,
                  padding: "10px 12px",
                  background: "rgba(13,10,20,0.5)",
                  border: "1px solid rgba(167,139,250,0.08)",
                  borderRadius: 10,
                  fontSize: 11, color: "#9590A8",
                  letterSpacing: 0.2, lineHeight: 1.5,
                }}>
                  <div style={{ color: "#F8F7FA", fontWeight: 700, marginBottom: 3 }}>{genre.description}</div>
                  <div style={{ fontSize: 10, color: "#5C5870" }}>
                    {genre.progressions.length} progressions · {genre.mode} mode · random pick each tap
                  </div>
                </div>
              )}
              {sectionLabel("Bars")}
              <div style={{ display:"flex", gap: 6, marginBottom: 18 }}>
                {[1, 2, 4, 8, 16].filter(function(b){ return b <= maxBars; }).map(function(b){
                  return chip({
                    key: b, label: b + (b === 1 ? " bar" : " bars"),
                    active: progBars === b,
                    onClick: function(){ setProgBars(b); },
                  });
                })}
              </div>
              {sectionLabel("Octave")}
              <div style={{ display:"flex", gap: 6, marginBottom: 18 }}>
                {[2, 3, 4, 5].map(function(o){
                  return chip({
                    key: o, label: "C" + o,
                    active: progOctave === o,
                    onClick: function(){ setProgOctave(o); },
                  });
                })}
              </div>
              {cta("Generate Chords", generateProgressionAction)}
              <div style={{ fontSize: 10, color: "#5C5870", textAlign: "center", marginTop: 10, lineHeight: 1.5 }}>
                Replaces all notes in this clip · undo to restore
              </div>
            </div>
          )}

          {/* ── MELODY TAB ── */}
          {tab === "melody" && (
            <div>
              {sectionLabel("Mood")}
              <div style={{ display:"flex", gap: 6, flexWrap:"wrap", marginBottom: 18 }}>
                {BF_MELODY_MOODS.map(function(m){
                  return chip({
                    key: m.id, label: m.name,
                    active: moodId === m.id,
                    onClick: function(){ setMoodId(m.id); },
                  });
                })}
              </div>
              {sectionLabel("Density")}
              <div style={{ display:"flex", gap: 6, marginBottom: 18 }}>
                {BF_MELODY_DENSITIES.map(function(d){
                  return chip({
                    key: d.id, label: d.label,
                    active: densityId === d.id,
                    onClick: function(){ setDensityId(d.id); },
                  });
                })}
              </div>
              {sectionLabel("Bars")}
              <div style={{ display:"flex", gap: 6, marginBottom: 18 }}>
                {[1, 2, 4, 8].filter(function(b){ return b <= maxBars; }).map(function(b){
                  return chip({
                    key: b, label: b + (b === 1 ? " bar" : " bars"),
                    active: melodyBars === b,
                    onClick: function(){ setMelodyBars(b); },
                  });
                })}
              </div>
              {sectionLabel("Octave")}
              <div style={{ display:"flex", gap: 6, marginBottom: 18 }}>
                {[3, 4, 5, 6].map(function(o){
                  return chip({
                    key: o, label: "C" + o,
                    active: melodyOctave === o,
                    onClick: function(){ setMelodyOctave(o); },
                  });
                })}
              </div>
              {cta("Generate Melody", generateMelodyAction)}
              <div style={{ fontSize: 10, color: "#5C5870", textAlign: "center", marginTop: 10, lineHeight: 1.5 }}>
                Replaces all notes in this clip · undo to restore
              </div>
            </div>
          )}

          {/* ── COUNTER TAB ── */}
          {tab === "counter" && (
            <div>
              <div style={{
                background: "rgba(13,10,20,0.5)",
                border: "1px solid rgba(167,139,250,0.08)",
                borderRadius: 10, padding: 14,
                marginBottom: 18,
                fontSize: 12, color: "#9590A8",
                lineHeight: 1.6, letterSpacing: 0.1,
              }}>
                <div style={{ color: "#F8F7FA", fontWeight: 700, marginBottom: 6 }}>How it works</div>
                Generates a complementary harmony line on top of your current notes. Each source note gets a partner pitch — usually a third, fifth, or sixth above — that stays in scale and avoids clashes.
              </div>
              {sectionLabel("Source")}
              <div style={{
                padding: "10px 12px",
                background: "rgba(20,15,30,0.4)",
                border: "1px solid rgba(167,139,250,0.10)",
                borderRadius: 10,
                fontSize: 11, color: "#9590A8",
                letterSpacing: 0.2, marginBottom: 18,
              }}>
                {arpSource && arpSource.length > 0
                  ? <span><span style={{ color: "#C4B5FD", fontWeight: 700 }}>{arpSource.length}</span> note{arpSource.length === 1 ? "" : "s"} {selectedNotes && selectedNotes.length > 0 ? "selected" : "in clip"}</span>
                  : <span style={{ color: "#5C5870" }}>No notes — place some first or pick a clip with content</span>
                }
              </div>
              {cta("Generate Counter-Melody", generateCounterAction, !arpSource || arpSource.length === 0)}
              <div style={{ fontSize: 10, color: "#5C5870", textAlign: "center", marginTop: 10, lineHeight: 1.5 }}>
                Adds to existing notes · doesn't replace
              </div>
            </div>
          )}

          {/* ── ARP TAB ── */}
          {tab === "arp" && (
            <div>
              {sectionLabel("Source")}
              <div style={{
                padding: "10px 12px",
                background: "rgba(20,15,30,0.4)",
                border: "1px solid rgba(167,139,250,0.10)",
                borderRadius: 10,
                fontSize: 11, color: "#9590A8",
                letterSpacing: 0.2, marginBottom: 18,
              }}>
                {arpSource && arpSource.length > 0
                  ? <span><span style={{ color: "#C4B5FD", fontWeight: 700 }}>{arpSource.length}</span> note{arpSource.length === 1 ? "" : "s"} {selectedNotes && selectedNotes.length > 0 ? "selected" : "in clip"} — arp uses these pitches</span>
                  : <span style={{ color: "#5C5870" }}>No notes — place a chord first</span>
                }
              </div>
              {sectionLabel("Pattern")}
              <div style={{ display:"flex", gap: 6, flexWrap:"wrap", marginBottom: 18 }}>
                {[
                  { id: "up", label: "↑ Up" },
                  { id: "down", label: "↓ Down" },
                  { id: "updown", label: "⇅ Up-Down" },
                  { id: "downup", label: "⇵ Down-Up" },
                  { id: "random", label: "✦ Random" },
                  { id: "trap-roll", label: "● Trap Roll" },
                ].map(function(p){
                  return chip({
                    key: p.id, label: p.label,
                    active: arpPattern === p.id,
                    onClick: function(){ setArpPattern(p.id); },
                  });
                })}
              </div>
              {sectionLabel("Rate")}
              <div style={{ display:"flex", gap: 6, marginBottom: 18 }}>
                {[
                  { id: 2, label: "1/8" },
                  { id: 4, label: "1/16" },
                  { id: 8, label: "1/32" },
                  { id: 16, label: "1/64" },
                ].map(function(r){
                  return chip({
                    key: r.id, label: r.label,
                    active: arpRate === r.id,
                    onClick: function(){ setArpRate(r.id); },
                  });
                })}
              </div>
              {sectionLabel("Octave Span")}
              <div style={{ display:"flex", gap: 6, marginBottom: 18 }}>
                {[1, 2, 3].map(function(o){
                  return chip({
                    key: o, label: o + " oct",
                    active: arpOctaveSpan === o,
                    onClick: function(){ setArpOctaveSpan(o); },
                  });
                })}
              </div>
              {sectionLabel("Length")}
              <div style={{ display:"flex", gap: 6, marginBottom: 18 }}>
                {[1, 2, 4, 8].filter(function(b){ return b <= maxBars; }).map(function(b){
                  return chip({
                    key: b, label: b + (b === 1 ? " bar" : " bars"),
                    active: arpBars === b,
                    onClick: function(){ setArpBars(b); },
                  });
                })}
              </div>
              {cta("Generate Arp", generateArpAction, !arpSource || arpSource.length === 0)}
              <div style={{ fontSize: 10, color: "#5C5870", textAlign: "center", marginTop: 10, lineHeight: 1.5 }}>
                Replaces all notes in this clip · undo to restore
              </div>
            </div>
          )}
        </div>
      </div>
    </div>
  );
}

function _TRottenMasterPluginInner({ fx, upd }) {
  const m  = fx.trotten || {};
  const on = !!m.on;

  // ── Mode button ─────────────────────────────────────────────────────────────
  const ModeBtn = function({ label, active, onClick, accent }) {
    const ac = accent || "#c8762a";
    return (
      <button onClick={onClick} style={{
        padding:"3px 7px", fontSize:7, fontWeight:900,
        fontFamily:"monospace", letterSpacing:0.8,
        background: active ? ac + "22" : "#120e06",
        border:"1px solid " + (active ? ac : "#2a1c08"),
        borderRadius:3, color: active ? ac : "#3a2510",
        cursor:"pointer", transition:"all 0.12s",
        boxShadow: active ? "0 0 6px " + ac + "55, inset 0 1px 0 " + ac + "22" : "none",
        textShadow: active ? "0 0 8px " + ac : "none",
      }}>{label}</button>
    );
  };

  // ── LED indicator ────────────────────────────────────────────────────────────
  const LED = function({ color, glow }) {
    return (
      <div style={{
        width:6, height:6, borderRadius:"50%",
        background: glow ? color : "#1a1208",
        boxShadow: glow ? `0 0 6px ${color}, 0 0 12px ${color}66` : "inset 0 1px 2px #00000088",
        border:"1px solid " + (glow ? color : "#0a0800"),
        transition:"all 0.3s",
      }}/>
    );
  };

  // ── VU meter segment row ─────────────────────────────────────────────────────
  const HVU = function({ level, label }) {
    const segs   = 30;
    const filled = Math.round(Math.min(1, Math.max(0, level)) * segs);
    return (
      <div style={{ display:"flex", alignItems:"center", gap:3 }}>
        <div style={{ color:"#4a3020", fontSize:6.5, fontFamily:"monospace", width:7, textAlign:"right", flexShrink:0 }}>{label}</div>
        <div style={{ display:"flex", gap:1, alignItems:"center" }}>
          {Array.from({length: segs}, function(_, i) {
            const lit     = i < filled;
            const isRed   = i >= segs - 3;
            const isAmber = i >= segs - 8 && i < segs - 3;
            const col     = !lit ? "#160f04" : isRed ? "#ef4444" : isAmber ? "#f59e0b" : "#c8762a";
            return <div key={i} style={{
              width: isRed ? 3.5 : isAmber ? 4 : 4.5, height:7, borderRadius:1,
              background: col,
              boxShadow: lit ? `0 0 4px ${col}bb` : "none",
            }}/>;
          })}
        </div>
      </div>
    );
  };

  // ── Section label ─────────────────────────────────────────────────────────
  const SecLabel = function({ children }) {
    return (
      <div style={{ color:"#6a5535", fontSize:7.5, fontWeight:900, letterSpacing:2.5,
        marginBottom:8, textAlign:"center", fontFamily:"monospace",
        textShadow:"0 1px 3px #000" }}>
        {children}
      </div>
    );
  };

  const Divider = function() {
    return <div style={{ width:1, alignSelf:"stretch", background:"linear-gradient(180deg,transparent,#2a1c08 20%,#2a1c08 80%,transparent)", margin:"0 2px" }}/>;
  };

  // State
  const eqLow    = m.eqLow    ?? 0;
  const eqMid    = m.eqMid    ?? 0;
  const eqHigh   = m.eqHigh   ?? 0;
  const eqLowT   = m.eqLowT   || "shelf";
  const eqMidT   = m.eqMidT   || "bell";
  const eqHighT  = m.eqHighT  || "shelf";
  const compThr  = m.compThr  ?? -15;
  const compAmt  = m.compAmt  ?? 50;
  const compMode = m.compMode || "auto";
  const tapeDrv  = m.tapeDrv  ?? 50;
  const tapeSat  = m.tapeSat  ?? 5;
  const tapeMode = m.tapeMode || "modern";
  const limCeil  = m.limCeil  ?? -0.5;
  const limRel   = m.limRel   ?? 0.5;
  const limMode  = m.limMode  || "truepeak";
  const inGain   = m.inputGain  ?? 0;
  const outGain  = m.outputGain ?? 0;

  const vuLvl = Math.max(0.12, Math.min(0.97, 0.62 + outGain / 40 + (eqLow + eqHigh) / 60));

  return (
    <div style={{
      borderRadius:12, overflow:"hidden", fontFamily:"monospace",
      border:"2px solid " + (on ? "#3a2810" : "#1a1006"),
      boxShadow: on ? "0 4px 40px rgba(160,90,10,0.18), inset 0 1px 0 rgba(255,200,100,0.04)" : "0 2px 12px rgba(0,0,0,0.6)",
      background:"#0c0900",
    }}>

      {/* ══ TOP BAR ═════════════════════════════════════════════════════════ */}
      <div style={{
        background:"linear-gradient(180deg,#141008 0%,#0c0900 100%)",
        borderBottom:"1px solid #201508",
        padding:"6px 12px",
        display:"flex", alignItems:"center", justifyContent:"space-between",
      }}>
        <div style={{ display:"flex", alignItems:"center", gap:8 }}>
          <span style={{ color:"#3a2510", fontSize:10, lineHeight:1 }}>≡</span>
          <span style={{ color:"#3a2510", fontSize:10 }}>‹</span>
          <span style={{ color:"#c8762a", fontSize:9, fontWeight:900, letterSpacing:1.5,
            textShadow:"0 0 10px rgba(200,118,42,0.6)" }}>Rotten But Loud</span>
          <span style={{ color:"#3a2510", fontSize:10 }}>›</span>

        </div>
        <div style={{ display:"flex", alignItems:"center", gap:10 }}>
          <div style={{ display:"flex", gap:5, alignItems:"center" }}>
            <span style={{ color:"#c8762a", fontSize:8, fontWeight:900, letterSpacing:1 }}>A</span>
            <span style={{ color:"#2a1a06", fontSize:8 }}>|</span>
            <span style={{ color:"#3a2510", fontSize:8 }}>B</span>
            <span style={{ color:"#3a2510", fontSize:8, marginLeft:4, letterSpacing:1 }}>COPY</span>
          </div>
          <div style={{ display:"flex", alignItems:"center", gap:4, borderLeft:"1px solid #2a1a08", paddingLeft:10 }}>
            <div style={{ display:"flex", flexDirection:"column", alignItems:"flex-end" }}>
              <span style={{ color:"#3a2510", fontSize:6, letterSpacing:1 }}>OVERSAMPLING</span>
              <span style={{ color:"#6a5030", fontSize:7, fontWeight:900 }}>4x ▾</span>
            </div>
            <span style={{ color:"#3a2510", fontSize:11 }}>⚙</span>
            {/* Power */}
            <div onClick={function(){ upd("trotten",{on:!on}); }} style={{
              width:22, height:22, borderRadius:"50%", cursor:"pointer",
              border:"1.5px solid " + (on ? "#c8762a" : "#2a1a08"),
              display:"flex", alignItems:"center", justifyContent:"center",
              background: on ? "#1c1006" : "#0c0900",
              boxShadow: on ? "0 0 10px #c8762a66, inset 0 0 6px #c8762a22" : "none",
              transition:"all 0.2s",
            }}>
              <span style={{ fontSize:10, color: on ? "#c8762a" : "#2a1a08", lineHeight:1 }}>⏻</span>
            </div>
          </div>
        </div>
      </div>

      {/* ══ MAIN FACEPLATE ══════════════════════════════════════════════════ */}
      <div style={{
        background:"linear-gradient(160deg,#1c1509 0%,#141006 40%,#0e0c05 100%)",
        padding:"10px 8px 10px",
        opacity: on ? 1 : 0.35, transition:"opacity 0.25s",
      }}>

        {/* ── ROW 1: Logo + EQ + Compressor ─────────────────────────────── */}
        <div style={{ display:"flex", gap:0 }}>

          {/* LOGO */}
          <div style={{ width:58, flexShrink:0, display:"flex", flexDirection:"column",
            justifyContent:"flex-start", paddingRight:7, paddingTop:2 }}>
            <div style={{ color:"#c8762a", fontSize:13, fontWeight:900, letterSpacing:0.5, lineHeight:1.1,
              textShadow:"0 0 12px rgba(200,118,42,0.55)" }}>T-<br/>ROTTEN</div>
            <div style={{ color:"#8c1e10", fontSize:8.5, fontWeight:900, letterSpacing:2, lineHeight:1, marginTop:2 }}>MASTER</div>
            <div style={{ color:"#3a2510", fontSize:5, letterSpacing:1, marginTop:3, lineHeight:1.5 }}>
              MASTERING<br/>PROCESSOR
            </div>
            <div style={{ marginTop:5, opacity:0.25 }}>
              <div style={{ fontSize:22, filter:"sepia(100%) saturate(40%) brightness(55%)" }}><AppIcon id="skull" size={20}/></div>
            </div>
          </div>

          <Divider/>

          {/* ── EQUALIZER ── */}
          <div style={{ flex:1.3, display:"flex", flexDirection:"column", alignItems:"center", padding:"0 5px" }}>
            <SecLabel>EQUALIZER</SecLabel>
            <div style={{ display:"flex", gap:6, justifyContent:"center" }}>
              <div style={{ display:"flex", flexDirection:"column", alignItems:"center", gap:3 }}>
                <LED color="#ef4444" glow={eqLow !== 0}/>
                <TKnob label="LOW" value={eqLow} min={-12} max={12} step={0.5} unit="dB" size={42}
                  onChange={function(v){ upd("trotten",{eqLow:v}); }}/>
                <ModeBtn label={eqLowT.toUpperCase()} active={true}
                  onClick={function(){ upd("trotten",{eqLowT: eqLowT==="shelf"?"bell":"shelf"}); }}/>
              </div>
              <div style={{ display:"flex", flexDirection:"column", alignItems:"center", gap:3 }}>
                <LED color="#888" glow={false}/>
                <TKnob label="MID" value={eqMid} min={-12} max={12} step={0.5} unit="dB" size={42}
                  onChange={function(v){ upd("trotten",{eqMid:v}); }}/>
                <ModeBtn label={eqMidT.toUpperCase()} active={true}
                  onClick={function(){ upd("trotten",{eqMidT: eqMidT==="bell"?"shelf":"bell"}); }}/>
              </div>
              <div style={{ display:"flex", flexDirection:"column", alignItems:"center", gap:3 }}>
                <LED color="#ef4444" glow={eqHigh !== 0}/>
                <TKnob label="HIGH" value={eqHigh} min={-12} max={12} step={0.5} unit="dB" size={42}
                  onChange={function(v){ upd("trotten",{eqHigh:v}); }}/>
                <ModeBtn label={eqHighT.toUpperCase()} active={true}
                  onClick={function(){ upd("trotten",{eqHighT: eqHighT==="shelf"?"bell":"shelf"}); }}/>
              </div>
            </div>
          </div>

          <Divider/>

          {/* ── COMPRESSOR ── */}
          <div style={{ flex:1, display:"flex", flexDirection:"column", alignItems:"center", padding:"0 5px" }}>
            <SecLabel>COMPRESSOR</SecLabel>
            <div style={{ display:"flex", gap:6, justifyContent:"center" }}>
              <div style={{ display:"flex", flexDirection:"column", alignItems:"center", gap:3 }}>
                <LED color="#f59e0b" glow={compMode !== "off"}/>
                <TKnob label="THRESHOLD" value={compThr} min={-30} max={0} step={1} unit="dB" size={42}
                  onChange={function(v){ upd("trotten",{compThr:v}); }}/>
              </div>
              <div style={{ display:"flex", flexDirection:"column", alignItems:"center", gap:3 }}>
                <LED color="#888" glow={false}/>
                <TKnob label="AMOUNT" value={compAmt} min={0} max={100} step={1} unit="%" size={42}
                  onChange={function(v){ upd("trotten",{compAmt:v}); }}/>
              </div>
            </div>
            <div style={{ display:"flex", gap:3, marginTop:4 }}>
              {["auto","relax","punch"].map(function(m2){
                return <ModeBtn key={m2} label={m2.toUpperCase()} active={compMode===m2}
                  accent="#c8762a" onClick={function(){ upd("trotten",{compMode:m2}); }}/>;
              })}
            </div>
          </div>

        </div>

        {/* ── Horizontal rule between rows ─────────────────────────────── */}
        <div style={{ height:1, background:"linear-gradient(90deg,transparent,#2a1c08 15%,#2a1c08 85%,transparent)", margin:"10px 0 8px" }}/>

        {/* ── ROW 2: Tape/Saturation + Limiter ─────────────────────────── */}
        <div style={{ display:"flex", gap:0 }}>

          {/* ── TAPE / SATURATION ── */}
          <div style={{ flex:1, display:"flex", flexDirection:"column", alignItems:"center", padding:"0 5px" }}>
            <SecLabel>TAPE / SATURATION</SecLabel>
            <div style={{ display:"flex", gap:6, justifyContent:"center" }}>
              <div style={{ display:"flex", flexDirection:"column", alignItems:"center", gap:3 }}>
                <LED color="#f59e0b" glow={tapeMode !== "off"}/>
                <TKnob label="DRIVE" value={tapeDrv} min={0} max={10} step={0.1} size={42}
                  onChange={function(v){ upd("trotten",{tapeDrv:v}); }}/>
              </div>
              <div style={{ display:"flex", flexDirection:"column", alignItems:"center", gap:3 }}>
                <LED color="#888" glow={false}/>
                <TKnob label="TAPE" value={tapeSat} min={0} max={10} step={0.1} size={42}
                  onChange={function(v){ upd("trotten",{tapeSat:v}); }}/>
              </div>
            </div>
            <div style={{ display:"flex", gap:3, marginTop:4 }}>
              {["classic","modern","dirty"].map(function(m2){
                return <ModeBtn key={m2} label={m2.toUpperCase()} active={tapeMode===m2}
                  accent="#c8762a" onClick={function(){ upd("trotten",{tapeMode:m2}); }}/>;
              })}
            </div>
          </div>

          <Divider/>

          {/* ── LIMITER ── */}
          <div style={{ flex:1, display:"flex", flexDirection:"column", alignItems:"center", padding:"0 5px" }}>
            <SecLabel>LIMITER</SecLabel>
            <div style={{ display:"flex", gap:6, justifyContent:"center" }}>
              <div style={{ display:"flex", flexDirection:"column", alignItems:"center", gap:3 }}>
                <LED color="#ef4444" glow={true}/>
                <TKnob label="CEILING" value={limCeil} min={-1} max={0} step={0.1} unit="dBFS" size={42}
                  onChange={function(v){ upd("trotten",{limCeil:v}); }}/>
              </div>
              <div style={{ display:"flex", flexDirection:"column", alignItems:"center", gap:3 }}>
                <LED color="#888" glow={false}/>
                <TKnob label="RELEASE" value={limRel} min={0.1} max={1} step={0.01} unit="s" size={42}
                  onChange={function(v){ upd("trotten",{limRel:v}); }}/>
              </div>
            </div>
            <div style={{ marginTop:4 }}>
              <ModeBtn label="TRUE PEAK" active={limMode==="truepeak"} accent="#c8762a"
                onClick={function(){ upd("trotten",{limMode: limMode==="truepeak"?"peak":"truepeak"}); }}/>
            </div>
          </div>

        </div>

      </div>

      {/* ══ METERS STRIP ════════════════════════════════════════════════════ */}
      <div style={{
        background:"linear-gradient(180deg,#0e0b05 0%,#080600 100%)",
        borderTop:"1px solid #201508",
        padding:"8px 10px 7px",
        display:"flex", gap:8, alignItems:"center",
        opacity: on ? 1 : 0.28, transition:"opacity 0.25s",
      }}>
        {/* Input knob */}
        <div style={{ display:"flex", flexDirection:"column", alignItems:"center", gap:2, flexShrink:0 }}>
          <div style={{ color:"#6a5030", fontSize:7, fontWeight:900, letterSpacing:1 }}>INPUT</div>
          <TKnob label="" value={inGain} min={-24} max={24} step={0.5} unit="dB" size={40}
            onChange={function(v){ upd("trotten",{inputGain:v}); }}/>
          <div style={{ color:"#c8762a", fontSize:8, fontFamily:"monospace", fontWeight:700 }}>
            {(inGain >= 0 ? "+" : "") + inGain.toFixed(1)}
          </div>
        </div>

        {/* VU meters — input + output stacked */}
        <div style={{ flex:1, display:"flex", flexDirection:"column", gap:4 }}>
          <HVU level={vuLvl * 0.88} label="L"/>
          <HVU level={vuLvl * 0.83} label="R"/>
          <div style={{ display:"flex", justifyContent:"space-between", paddingLeft:11 }}>
            {["-40","-18","-9","-3","0"].map(function(l){
              return <div key={l} style={{ color:"#3a2510", fontSize:5, fontFamily:"monospace" }}>{l}</div>;
            })}
          </div>
        </div>

        {/* Output knob */}
        <div style={{ display:"flex", flexDirection:"column", alignItems:"center", gap:2, flexShrink:0 }}>
          <div style={{ color:"#6a5030", fontSize:7, fontWeight:900, letterSpacing:1 }}>OUTPUT</div>
          <TKnob label="" value={outGain} min={-24} max={24} step={0.5} unit="dB" size={40}
            onChange={function(v){ upd("trotten",{outputGain:v}); }}/>
          <div style={{ color:"#c8762a", fontSize:8, fontFamily:"monospace", fontWeight:700 }}>
            {(outGain >= 0 ? "+" : "") + outGain.toFixed(1)}
          </div>
        </div>
      </div>



    </div>
  );
}
const _TRottenMasterPlugin = React.memo(_TRottenMasterPluginInner, function(prev, next) {
  return prev.fx.trotten === next.fx.trotten && prev.upd === next.upd;
});

// ── Noise Remover Plugin UI ──────────────────────────────────────────────────
function _NoiseRemoverPluginInner({ fx, upd, Knob }) {
  const nr        = fx.noiseremover || {};
  const on        = !!nr.on;
  const threshold = nr.threshold ?? -40;   // dB: -80 to 0
  const release   = nr.release   ?? 0.15;  // seconds: 0.01–2.0
  const hold      = nr.hold      ?? 0.08;  // seconds: 0–0.5
  const hiss      = nr.hiss      ?? 0;     // 0–1 → hiss cut amount
  const lookahead = !!(nr.lookahead ?? true);

  // ── Real-time gate-open indicator via AnalyserNode polling ──
  const [gateOpen, setGateOpen] = React.useState(false);
  const [inputDb,  setInputDb]  = React.useState(-80);
  const rafRef = React.useRef(null);

  React.useEffect(function() {
    if (!on) { setGateOpen(false); setInputDb(-80); return; }
    let alive = true;
    const poll = function() {
      if (!alive) return;
      // Read from the track's analyser if available via window.__bfTrackAnalyser
      // The gate meter is a visual approximation: input dB vs threshold
      const analyser = window.__bfActiveAnalyser;
      if (analyser) {
        const buf = new Float32Array(analyser.frequencyBinCount);
        analyser.getFloatTimeDomainData(buf);
        let rms = 0;
        for (let i = 0; i < buf.length; i++) rms += buf[i] * buf[i];
        rms = Math.sqrt(rms / buf.length);
        const db = rms > 0 ? 20 * Math.log10(rms) : -80;
        setInputDb(db);
        // Hysteresis: open if 2dB above threshold, close if 3dB below
        setGateOpen(function(prev) {
          return prev ? db > (threshold - 3) : db > (threshold + 2);
        });
      }
      rafRef.current = requestAnimationFrame(poll);
    };
    rafRef.current = requestAnimationFrame(poll);
    return function() { alive = false; cancelAnimationFrame(rafRef.current); };
  }, [on, threshold]);

  // Threshold fill % for visual bar (map -80..0 dB → 0..100%)
  const threshPct = Math.round(((threshold + 80) / 80) * 100);
  const inputPct  = Math.round(Math.max(0, Math.min(100, ((inputDb + 80) / 80) * 100)));

  // Release display
  const relMs = Math.round(release * 1000);

  // Hiss cut display
  const hissCutDb = Math.round(hiss * 18);

  return (
    <div style={{
      background:"linear-gradient(160deg,#020f0a 0%,#071410 60%,#050d0a 100%)",
      borderRadius:16, overflow:"hidden",
      border:"2px solid " + (on ? "#10B981" : "#1a2a22"),
      boxShadow: on ? "0 0 24px rgba(16,185,129,0.18), inset 0 1px 0 rgba(16,185,129,0.08)" : "inset 0 1px 0 rgba(255,255,255,0.02)",
      transition:"border-color 0.25s, box-shadow 0.25s",
    }}>

      {/* ── Header ── */}
      <div style={{ background:"linear-gradient(180deg,#0d1f18 0%,#091710 100%)", padding:"9px 14px", borderBottom:"1px solid #0d2018", display:"flex", alignItems:"center", gap:8 }}>
        <div style={{ flex:1 }}>
          <div style={{ color:"#10B981", fontWeight:900, fontSize:11, letterSpacing:3, fontFamily:"monospace", lineHeight:1 }}>NOISE GATE</div>
          <div style={{ color:"#0d3d28", fontSize:7, letterSpacing:2, fontFamily:"monospace", marginTop:2 }}>THRESHOLD · ATTACK · HOLD · RELEASE · HISS CUT</div>
        </div>
        <div style={{ display:"flex", alignItems:"center", gap:6 }}>
          {/* Gate state LED */}
          <div style={{
            width:10, height:10, borderRadius:"50%",
            background: !on ? "#0d2018" : gateOpen ? "#10B981" : "#EF4444",
            boxShadow: !on ? "none" : gateOpen
              ? "0 0 8px #10B981, 0 0 16px rgba(16,185,129,0.6)"
              : "0 0 8px #EF4444, 0 0 16px rgba(239,68,68,0.5)",
            transition:"background 0.05s, box-shadow 0.05s",
          }} />
          <button onClick={function(){ upd("noiseremover",{on:!on}); }}
            style={{ background: on ? "linear-gradient(180deg,#059669,#047857)" : "linear-gradient(180deg,#0d2018,#091510)", border:"1px solid " + (on ? "#10B981" : "#0d2a1c"), borderRadius:5, color: on ? "white" : "#1a4a30", fontSize:9, fontWeight:800, padding:"4px 12px", cursor:"pointer", letterSpacing:1, transition:"all 0.2s" }}>
            {on ? "ON" : "OFF"}
          </button>
        </div>
      </div>

      <div style={{ padding:"12px 14px", opacity:on?1:0.35, transition:"opacity 0.25s", pointerEvents:on?"auto":"none" }}>

        {/* ── Gate meter + status ── */}
        <div style={{ display:"flex", gap:10, alignItems:"stretch", marginBottom:14 }}>

          {/* Level meter column */}
          <div style={{ display:"flex", flexDirection:"column", gap:4, alignItems:"center", background:"#050e0a", borderRadius:8, padding:"8px 10px", border:"1px solid #0d1f15", flexShrink:0, minWidth:52 }}>
            {/* Input level bar */}
            <div style={{ width:20, height:70, background:"#0a0a0a", borderRadius:3, border:"1px solid #111", position:"relative", overflow:"hidden" }}>
              {/* Threshold line */}
              <div style={{ position:"absolute", left:0, right:0, bottom: threshPct + "%", height:1, background:"#F59E0B", zIndex:2, opacity:0.8 }} />
              {/* Level fill */}
              <div style={{
                position:"absolute", bottom:0, left:0, right:0,
                height: on ? inputPct + "%" : "0%",
                background: gateOpen
                  ? "linear-gradient(0deg,#10B981cc,#34D39988)"
                  : "linear-gradient(0deg,#EF4444cc,#F8717188)",
                transition:"height 0.04s linear, background 0.08s",
                borderRadius:2,
              }} />
            </div>
            <div style={{ color: gateOpen && on ? "#10B981" : "#EF4444", fontSize:7, fontWeight:800, letterSpacing:0.5, fontFamily:"monospace", transition:"color 0.08s" }}>
              {on ? (gateOpen ? "OPEN" : "GATE") : "OFF"}
            </div>
          </div>

          {/* Gate state description */}
          <div style={{ flex:1, display:"flex", flexDirection:"column", justifyContent:"center", gap:6 }}>
            <div style={{ background:"#060e0a", borderRadius:6, padding:"6px 10px", border:"1px solid #0d1f15" }}>
              <div style={{ display:"flex", justifyContent:"space-between", alignItems:"center" }}>
                <span style={{ color:"#1a4a30", fontSize:8, fontFamily:"monospace", letterSpacing:1 }}>INPUT</span>
                <span style={{ color: gateOpen && on ? "#10B981" : "#4a5068", fontSize:9, fontWeight:800, fontFamily:"monospace" }}>{on ? inputDb.toFixed(1) : "--"} dB</span>
              </div>
            </div>
            <div style={{ background:"#060e0a", borderRadius:6, padding:"6px 10px", border:"1px solid #0d1f15" }}>
              <div style={{ display:"flex", justifyContent:"space-between", alignItems:"center" }}>
                <span style={{ color:"#1a4a30", fontSize:8, fontFamily:"monospace", letterSpacing:1 }}>THRESH</span>
                <span style={{ color:"#F59E0B", fontSize:9, fontWeight:800, fontFamily:"monospace" }}>{threshold.toFixed(0)} dB</span>
              </div>
            </div>
            <div style={{ background:"#060e0a", borderRadius:6, padding:"6px 10px", border:"1px solid #0d1f15" }}>
              <div style={{ display:"flex", justifyContent:"space-between", alignItems:"center" }}>
                <span style={{ color:"#1a4a30", fontSize:8, fontFamily:"monospace", letterSpacing:1 }}>STATUS</span>
                <span style={{ color: gateOpen && on ? "#10B981" : "#EF4444", fontSize:9, fontWeight:800, fontFamily:"monospace", letterSpacing:1 }}>
                  {!on ? "BYPASS" : gateOpen ? "● PASS" : "● CUT"}
                </span>
              </div>
            </div>
          </div>
        </div>

        {/* ── Threshold slider ── */}
        <div style={{ marginBottom:12 }}>
          <div style={{ display:"flex", justifyContent:"space-between", marginBottom:5 }}>
            <span style={{ color:"#0d3d28", fontSize:8, fontFamily:"monospace", letterSpacing:2, fontWeight:700 }}>THRESHOLD</span>
            <span style={{ color:"#F59E0B", fontSize:9, fontFamily:"monospace", fontWeight:800 }}>{threshold.toFixed(0)} dB</span>
          </div>
          <PointerSlider min={-80} max={0} step={1} value={threshold}
            onChange={function(v){ upd("noiseremover",{threshold:v}); }}
            accentColor="#10B981" />
          <div style={{ display:"flex", justifyContent:"space-between", marginTop:2 }}>
            <span style={{ color:"#0d3d28", fontSize:7, fontFamily:"monospace" }}>-80dB (loud)</span>
            <span style={{ color:"#0d3d28", fontSize:7, fontFamily:"monospace" }}>0dB (quiet)</span>
          </div>
        </div>

        {/* ── Release slider ── */}
        <div style={{ marginBottom:12 }}>
          <div style={{ display:"flex", justifyContent:"space-between", marginBottom:5 }}>
            <span style={{ color:"#0d3d28", fontSize:8, fontFamily:"monospace", letterSpacing:2, fontWeight:700 }}>RELEASE</span>
            <span style={{ color:"#34D399", fontSize:9, fontFamily:"monospace", fontWeight:800 }}>{relMs}ms</span>
          </div>
          <PointerSlider min={0.01} max={2.0} step={0.01} value={release}
            onChange={function(v){ upd("noiseremover",{release:v}); }}
            accentColor="#34D399" />
          <div style={{ display:"flex", justifyContent:"space-between", marginTop:2 }}>
            <span style={{ color:"#0d3d28", fontSize:7, fontFamily:"monospace" }}>10ms (tight)</span>
            <span style={{ color:"#0d3d28", fontSize:7, fontFamily:"monospace" }}>2s (smooth)</span>
          </div>
        </div>

        {/* ── Hiss / High-shelf cut slider ── */}
        <div style={{ marginBottom:12 }}>
          <div style={{ display:"flex", justifyContent:"space-between", marginBottom:5 }}>
            <span style={{ color:"#0d3d28", fontSize:8, fontFamily:"monospace", letterSpacing:2, fontWeight:700 }}>HISS CUT (8kHz)</span>
            <span style={{ color:"#6EE7B7", fontSize:9, fontFamily:"monospace", fontWeight:800 }}>{hissCutDb > 0 ? "-"+hissCutDb+"dB" : "OFF"}</span>
          </div>
          <PointerSlider min={0} max={1} step={0.01} value={hiss}
            onChange={function(v){ upd("noiseremover",{hiss:v}); }}
            accentColor="#6EE7B7" />
          <div style={{ display:"flex", justifyContent:"space-between", marginTop:2 }}>
            <span style={{ color:"#0d3d28", fontSize:7, fontFamily:"monospace" }}>Flat</span>
            <span style={{ color:"#0d3d28", fontSize:7, fontFamily:"monospace" }}>-18dB (heavy cut)</span>
          </div>
        </div>

        {/* ── Lookahead toggle ── */}
        <div style={{ display:"flex", alignItems:"center", justifyContent:"space-between", background:"#060e0a", borderRadius:8, padding:"8px 12px", border:"1px solid #0d1f15" }}>
          <div>
            <div style={{ color:"#9febe8", fontSize:11, fontWeight:700 }}>Lookahead Attack</div>
            <div style={{ color:"#1a3d2a", fontSize:9, marginTop:1 }}>1ms attack for fast transients (vs 3ms)</div>
          </div>
          <button onClick={function(){ upd("noiseremover",{lookahead:!lookahead}); }}
            style={{ background: lookahead ? "rgba(16,185,129,0.15)" : "#0a1a10", border:"1px solid " + (lookahead ? "#10B981" : "#1a2a1f"), borderRadius:6, color: lookahead ? "#10B981" : "#1a3d2a", fontSize:9, fontWeight:800, padding:"5px 12px", cursor:"pointer", letterSpacing:1, minWidth:44, transition:"all 0.2s" }}>
            {lookahead ? "ON" : "OFF"}
          </button>
        </div>

      </div>
    </div>
  );
}
const _NoiseRemoverPlugin = React.memo(_NoiseRemoverPluginInner, function(prev, next) {
  return prev.fx.noiseremover === next.fx.noiseremover && prev.upd === next.upd;
});
// =============================================================================
// ── VOCAL DOUBLER PLUGIN ──────────────────────────────────────────────────
// Professional Haas-effect stereo doubler with detuned copies, width control
// and a stereo correlation meter (L/R phase visualiser).
// =============================================================================
function _DoublerPluginInner({ fx, upd, Knob }) {
  const d     = fx.doubler || {};
  const on    = !!d.on;
  const delay = d.delay    ?? 20;      // ms  – Haas time offset (L copy)
  const detune= d.detune   ?? 8;       // cents – subtle pitch detuning for the doubled copy
  const width = d.width    ?? 0.7;     // 0–1 stereo spread
  const mix   = d.mix      ?? 0.5;     // 0–1 wet/dry

  // Live meter: simple animated L/R bars to convey stereo width
  const [meterL, setMeterL] = React.useState(0.6);
  const [meterR, setMeterR] = React.useState(0.6);
  const rafRef = React.useRef(null);
  React.useEffect(function () {
    if (!on) { setMeterL(0); setMeterR(0); return; }
    let t = 0;
    const tick = function () {
      t += 0.04;
      setMeterL(0.35 + Math.abs(Math.sin(t * 1.3 + 0.5)) * 0.55 * width);
      setMeterR(0.35 + Math.abs(Math.sin(t * 1.1))        * 0.55 * width);
      rafRef.current = requestAnimationFrame(tick);
    };
    rafRef.current = requestAnimationFrame(tick);
    return function () { cancelAnimationFrame(rafRef.current); };
  }, [on, width]);

  const accentColor = "#F59E0B";

  return (
    <div style={{ background:"linear-gradient(180deg,#1c1710 0%,#130f08 100%)", borderRadius:16, overflow:"hidden",
      border:"2px solid " + (on ? accentColor : "#2a2a2a"),
      boxShadow: on ? "0 0 20px rgba(245,158,11,0.15), inset 0 1px 0 rgba(255,255,255,0.06)" : "inset 0 1px 0 rgba(255,255,255,0.03)" }}>

      {/* ── Header ── */}
      <div style={{ display:"flex", alignItems:"center", gap:8, padding:"10px 14px 8px", borderBottom:"1px solid #1e1e1e" }}>
        {/* Stereo correlation meter */}
        <div style={{ display:"flex", flexDirection:"column", gap:2, flexShrink:0 }}>
          {["L","R"].map(function(ch, i){
            const val = i === 0 ? meterL : meterR;
            return (
              <div key={ch} style={{ display:"flex", alignItems:"center", gap:3 }}>
                <span style={{ color:"#555", fontSize:7, fontWeight:800, fontFamily:"monospace", width:6 }}>{ch}</span>
                <div style={{ width:32, height:4, background:"#111", borderRadius:2, overflow:"hidden" }}>
                  <div style={{ width: Math.round(val * 100) + "%", height:"100%",
                    background: on
                      ? "linear-gradient(90deg,#F59E0B," + (val > 0.7 ? "#EF4444" : "#FBBF24") + ")"
                      : "#1e1e1e",
                    transition:"width 0.05s linear", borderRadius:2 }} />
                </div>
              </div>
            );
          })}
        </div>

        <div style={{ flex:1 }}>
          <div style={{ color:"white", fontWeight:800, fontSize:13, lineHeight:1 }}>Vocal Doubler</div>
          <div style={{ color:"#555", fontSize:9, marginTop:2, letterSpacing:0.5 }}>
            Haas · Detune · Stereo Width
          </div>
        </div>

        {/* LED + ON/OFF */}
        <div style={{ width:8, height:8, borderRadius:"50%",
          background: on ? accentColor : "#1a1a1a",
          boxShadow: on ? "0 0 6px " + accentColor + ", 0 0 12px rgba(245,158,11,0.5)" : "none",
          transition:"all 0.2s" }} />
        <button onClick={function(){ upd("doubler", {on: !on}); }}
          style={{ background: on
            ? "linear-gradient(180deg,#d97706,#b45309)"
            : "linear-gradient(180deg,#2a2a2a,#222)",
            border:"1px solid " + (on ? accentColor : "#333"),
            borderRadius:5, color:"white", fontSize:9, fontWeight:800,
            padding:"4px 12px", cursor:"pointer", letterSpacing:1,
            boxShadow: on ? "0 1px 0 rgba(255,255,255,0.1) inset" : "0 1px 3px rgba(0,0,0,0.5)" }}>
          {on ? "ON" : "OFF"}
        </button>
      </div>

      {/* ── Controls ── */}
      <div style={{ padding:"14px 14px 10px", opacity: on ? 1 : 0.38, transition:"opacity 0.2s" }}>
        <div style={{ display:"flex", justifyContent:"space-around", flexWrap:"wrap", gap:8 }}>
          <Knob label="DELAY"  value={delay}  min={5}   max={50}  step={1}    unit="ms"   color={accentColor} onChange={function(v){ upd("doubler",{delay:v}); }} />
          <Knob label="DETUNE" value={detune} min={0}   max={50}  step={0.5}  unit="¢"    color="#FBBF24"    onChange={function(v){ upd("doubler",{detune:v}); }} />
          <Knob label="WIDTH"  value={width}  min={0}   max={1}   step={0.01} unit="%"    color="#F97316"    onChange={function(v){ upd("doubler",{width:v}); }} />
          <Knob label="MIX"    value={mix}    min={0}   max={1}   step={0.01} unit="%"    color="#FB923C"    onChange={function(v){ upd("doubler",{mix:v}); }} />
        </div>

        {/* Info strip */}
        <div style={{ marginTop:12, display:"flex", flexDirection:"column", gap:6 }}>
          <div style={{ display:"flex", alignItems:"center", justifyContent:"space-between",
            background:"#0d0b06", borderRadius:8, padding:"8px 12px", border:"1px solid #1c1608" }}>
            <div>
              <div style={{ color:"#fcd34d", fontSize:11, fontWeight:700 }}>Haas Offset</div>
              <div style={{ color:"#2a200a", fontSize:9, marginTop:1 }}>
                Sub-35ms delays = wide stereo, no flange
              </div>
            </div>
            <div style={{ color:accentColor, fontSize:11, fontWeight:800, fontFamily:"monospace" }}>
              {delay}ms
            </div>
          </div>
          <div style={{ display:"flex", alignItems:"center", justifyContent:"space-between",
            background:"#0d0b06", borderRadius:8, padding:"8px 12px", border:"1px solid #1c1608" }}>
            <div>
              <div style={{ color:"#fcd34d", fontSize:11, fontWeight:700 }}>Pitch Detune</div>
              <div style={{ color:"#2a200a", fontSize:9, marginTop:1 }}>
                Chorusing via subtle detuned copy
              </div>
            </div>
            <div style={{ color:accentColor, fontSize:11, fontWeight:800, fontFamily:"monospace" }}>
              ±{detune}¢
            </div>
          </div>
        </div>

        {/* Footer badge */}
        <div style={{ marginTop:10, display:"flex", alignItems:"center", gap:6 }}>
          <div style={{ flex:1, height:1, background:"#1c1608" }} />
          <div style={{ color:"#2a1800", fontSize:8, fontFamily:"monospace", letterSpacing:2, fontWeight:700 }}>
            HAAS · DETUNE · M/S WIDTH
          </div>
          <div style={{ flex:1, height:1, background:"#1c1608" }} />
        </div>
      </div>
    </div>
  );
}
const _DoublerPlugin = React.memo(_DoublerPluginInner, function(prev, next) {
  return prev.fx.doubler === next.fx.doubler && prev.upd === next.upd;
});

// =============================================================================
// ── H-DELAY PLUGIN UI ────────────────────────────────────────────────────────
// Emulates Waves H-Delay: tape/digital modes, BPM sync, ping-pong, hi/lo cut,
// analog-style saturation, modulation, and a live tap-tempo delay display.
// =============================================================================
function _HDelayPluginInner({ fx, upd, Knob }) {
  const hd = fx.hdelay || {};
  const on         = !!hd.on;
  const mode       = hd.mode       ?? "digital";   // "digital" | "tape" | "ping"
  const sync       = hd.sync       ?? false;        // BPM sync on/off
  const bpm        = hd.bpm        ?? 120;
  const subdivision= hd.subdivision?? "1/4";        // note value for BPM sync
  const delayMs    = hd.delayMs    ?? 375;          // manual delay in ms (up to 2000ms)
  const feedback   = hd.feedback   ?? 0.35;         // 0–0.95
  const wet        = hd.wet        ?? 0.40;         // mix
  const hiCut      = hd.hiCut      ?? 8000;         // Hz
  const loCut      = hd.loCut      ?? 80;           // Hz
  const modDepth   = hd.modDepth   ?? 0.15;         // 0–1 (chorus-style mod on tape mode)
  const modRate    = hd.modRate    ?? 0.5;           // Hz
  const drive      = hd.drive      ?? 0.0;          // 0–1 analog saturation
  const stereoOf   = hd.stereoOf   ?? 0.0;          // ping-pong offset 0–1

  const ACCENT = "#E85D04";
  const ACCENT2 = "#FB923C";

  // ── BPM sync helper ──
  const SUBDIVISIONS = ["1/1","1/2","1/2T","1/4","1/4T","1/8","1/8T","1/16","1/16T","1/32"];
  function subdivMs(sub, bpmVal) {
    const beat = 60000 / bpmVal;
    const map = {
      "1/1": beat*4, "1/2": beat*2, "1/2T": beat*(4/3),
      "1/4": beat,   "1/4T": beat*(2/3),
      "1/8": beat/2, "1/8T": beat/3,
      "1/16":beat/4, "1/16T":beat/6, "1/32":beat/8,
    };
    return Math.min(2000, Math.round(map[sub] || beat));
  }
  const effectiveMs = sync ? subdivMs(subdivision, bpm) : delayMs;

  // ── Animated echo trail visualiser ──
  const canvasRef = React.useRef(null);
  const vizRaf    = React.useRef(null);
  const vizPhase  = React.useRef(0);
  React.useEffect(function() {
    const cvs = canvasRef.current; if (!cvs) return;
    const ctx = cvs.getContext("2d");
    const W = cvs.width, H = cvs.height;
    function draw() {
      vizPhase.current += 0.018;
      const phase = vizPhase.current;
      ctx.clearRect(0,0,W,H);
      if (!on) { vizRaf.current = requestAnimationFrame(draw); return; }
      // Background grid
      ctx.strokeStyle = "#1a1008"; ctx.lineWidth = 1;
      for(let x=0;x<W;x+=24){ ctx.beginPath();ctx.moveTo(x,0);ctx.lineTo(x,H);ctx.stroke(); }
      for(let y=0;y<H;y+=12){ ctx.beginPath();ctx.moveTo(0,y);ctx.lineTo(W,y);ctx.stroke(); }
      // Echo pulses — draw decaying echoes at delay intervals
      const maxEchoes = 7;
      for(let e=0;e<maxEchoes;e++){
        const decay   = Math.pow(feedback, e);
        if (decay < 0.03) break;
        // X position: map echo number to horizontal position (delayed in time)
        const pxPerMs = W / 600;
        const tOff    = (effectiveMs * e * pxPerMs) % W;
        // Animated shimmer using phase
        const shimmer = mode === "tape" ? Math.sin(phase * modRate * 6.28 + e) * modDepth * 6 : 0;
        const cx = (tOff + shimmer + (phase * 40)) % W;
        const pulseH = H * 0.72 * decay;
        const alpha  = decay * (0.85 - e * 0.08);
        // Ping-pong: alternate left/right
        const cy = mode === "ping"
          ? (e % 2 === 0 ? H * 0.3 : H * 0.7)
          : H / 2;
        // Glow
        const grad = ctx.createRadialGradient(cx, cy, 0, cx, cy, pulseH * 0.6);
        grad.addColorStop(0, `rgba(232,93,4,${alpha})`);
        grad.addColorStop(0.5,`rgba(251,146,60,${alpha * 0.5})`);
        grad.addColorStop(1,  "rgba(232,93,4,0)");
        ctx.fillStyle = grad;
        ctx.beginPath();
        ctx.ellipse(cx, cy, pulseH * 0.35, pulseH * 0.45, 0, 0, Math.PI*2);
        ctx.fill();
        // Vertical bar
        ctx.fillStyle = `rgba(232,93,4,${alpha * 0.6})`;
        ctx.fillRect(cx-1.5, cy - pulseH/2, 3, pulseH);
      }
      // Center line
      ctx.strokeStyle = "rgba(232,93,4,0.12)"; ctx.lineWidth = 1;
      ctx.beginPath(); ctx.moveTo(0,H/2); ctx.lineTo(W,H/2); ctx.stroke();
      vizRaf.current = requestAnimationFrame(draw);
    }
    vizRaf.current = requestAnimationFrame(draw);
    return function(){ cancelAnimationFrame(vizRaf.current); };
  }, [on, effectiveMs, feedback, mode, modDepth, modRate]);

  // ── Tap tempo ──
  const tapTimes = React.useRef([]);
  function tapTempo() {
    const now = Date.now();
    tapTimes.current = tapTimes.current.filter(function(t){ return now - t < 3000; });
    tapTimes.current.push(now);
    if (tapTimes.current.length >= 2) {
      const diffs = tapTimes.current.slice(1).map(function(t,i){ return t - tapTimes.current[i]; });
      const avg = diffs.reduce(function(a,b){ return a+b; }, 0) / diffs.length;
      const tBpm = Math.round(60000 / avg);
      upd("hdelay", { bpm: Math.max(40, Math.min(250, tBpm)), sync: true });
    }
  }

  const modeBtnStyle = function(m) { return ({
    flex:1, padding:"7px 4px", border:"1px solid " + (mode===m ? ACCENT : "#2a2a2a"),
    background: mode===m ? "linear-gradient(180deg,#7c2d0a,#5a1f06)" : "rgba(255,255,255,0.03)",
    borderRadius:7, color: mode===m ? "#FB923C" : "#444",
    fontSize:10, fontWeight:800, cursor:"pointer", letterSpacing:0.5,
    transition:"all 0.15s",
  }); };

  return (
    <div style={{ background:"linear-gradient(180deg,#1a0e06 0%,#100800 100%)", borderRadius:16,
      overflow:"hidden", border:"2px solid " + (on ? ACCENT : "#2a2a2a"),
      boxShadow: on ? "0 0 24px rgba(232,93,4,0.18), inset 0 1px 0 rgba(255,255,255,0.06)" : "inset 0 1px 0 rgba(255,255,255,0.03)" }}>

      {/* ── Header ── */}
      <div style={{ display:"flex", alignItems:"center", gap:8, padding:"10px 14px 8px", borderBottom:"1px solid #1e1000" }}>
        <div style={{ flex:1 }}>
          <div style={{ color:"white", fontWeight:800, fontSize:14, lineHeight:1, letterSpacing:0.3 }}>H-Delay</div>
          <div style={{ color:"#4a2800", fontSize:9, marginTop:2, letterSpacing:1 }}>TAPE · DIGITAL · PING-PONG</div>
        </div>
        {/* Delay time readout */}
        <div style={{ textAlign:"right" }}>
          <div style={{ color: ACCENT, fontFamily:"monospace", fontWeight:800, fontSize:16, lineHeight:1 }}>
            {effectiveMs}
          </div>
          <div style={{ color:"#4a2800", fontSize:8, fontWeight:700, letterSpacing:1 }}>MS</div>
        </div>
        {/* LED + toggle */}
        <div style={{ width:8, height:8, borderRadius:"50%",
          background: on ? ACCENT : "#1a1a1a",
          boxShadow: on ? "0 0 6px "+ACCENT+",0 0 14px rgba(232,93,4,0.5)" : "none", transition:"all 0.2s" }} />
        <button onClick={function(){ upd("hdelay",{on:!on}); }}
          style={{ background: on ? "linear-gradient(180deg,#c2410c,#9a3412)" : "linear-gradient(180deg,#2a2a2a,#222)",
            border:"1px solid " + (on ? ACCENT : "#333"), borderRadius:5, color:"white",
            fontSize:9, fontWeight:800, padding:"4px 12px", cursor:"pointer", letterSpacing:1,
            boxShadow: on ? "0 1px 0 rgba(255,255,255,0.1) inset" : "0 1px 3px rgba(0,0,0,0.5)" }}>
          {on ? "ON" : "OFF"}
        </button>
      </div>

      <div style={{ padding:"12px 14px 10px", opacity: on ? 1 : 0.38, transition:"opacity 0.2s" }}>

        {/* ── Mode selector ── */}
        <div style={{ display:"flex", gap:5, marginBottom:12 }}>
          {[["digital","Digital"],["tape","Tape"],["ping","Ping-Pong"]].map(function([m,l]){
            return <button key={m} onClick={function(){ upd("hdelay",{mode:m}); }} style={modeBtnStyle(m)}>{l}</button>;
          })}
        </div>

        {/* ── Echo visualiser ── */}
        <canvas ref={canvasRef} width={288} height={56}
          style={{ display:"block", width:"100%", height:56, borderRadius:8,
            border:"1px solid #1e1000", marginBottom:12, background:"#0a0600" }} />

        {/* ── BPM sync row ── */}
        <div style={{ display:"flex", alignItems:"center", gap:8, marginBottom:12,
          background:"#0d0800", borderRadius:10, padding:"8px 10px", border:"1px solid #1e1000" }}>
          <button onClick={function(){ upd("hdelay",{sync:!sync}); }}
            style={{ background: sync ? "linear-gradient(180deg,#c2410c,#9a3412)" : "rgba(255,255,255,0.04)",
              border:"1px solid " + (sync ? ACCENT : "#2a2a2a"), borderRadius:6,
              color: sync ? "#FB923C" : "#444", fontSize:9, fontWeight:800,
              padding:"5px 10px", cursor:"pointer", letterSpacing:0.5, flexShrink:0 }}>
            SYNC
          </button>
          {sync ? (
            <div style={{ flex:1, display:"flex", gap:4, overflowX:"auto" }}>
              {SUBDIVISIONS.map(function(s){
                const active = subdivision === s;
                return (
                  <button key={s} onClick={function(){ upd("hdelay",{subdivision:s}); }}
                    style={{ flexShrink:0, padding:"4px 7px", border:"1px solid " + (active ? ACCENT : "#222"),
                      background: active ? ACCENT+"22" : "transparent",
                      borderRadius:5, color: active ? ACCENT2 : "#444",
                      fontSize:9, fontWeight:800, cursor:"pointer", whiteSpace:"nowrap" }}>
                    {s}
                  </button>
                );
              })}
            </div>
          ) : (
            <div style={{ flex:1 }}>
              <PointerSlider min={1} max={2000} step={1} value={delayMs}
                onChange={function(v){ upd("hdelay",{delayMs:v}); }}
                accentColor={ACCENT} />
              <div style={{ display:"flex", justifyContent:"space-between", marginTop:2 }}>
                <span style={{ color:"#2a1800", fontSize:8 }}>1ms</span>
                <span style={{ color:ACCENT, fontSize:9, fontWeight:800, fontFamily:"monospace" }}>{delayMs}ms</span>
                <span style={{ color:"#2a1800", fontSize:8 }}>2000ms</span>
              </div>
            </div>
          )}
          {/* Tap tempo */}
          <button onClick={tapTempo}
            style={{ flexShrink:0, background:"rgba(232,93,4,0.08)",
              border:"1px solid rgba(232,93,4,0.25)", borderRadius:7,
              color:ACCENT2, fontSize:9, fontWeight:800, padding:"6px 10px",
              cursor:"pointer", letterSpacing:0.3, lineHeight:1.2, textAlign:"center" }}>
            TAP<br />
            <span style={{ fontSize:8, color:"#4a2800" }}>{bpm}BPM</span>
          </button>
        </div>

        {/* ── BPM field (sync mode) ── */}
        {sync && (
          <div style={{ display:"flex", alignItems:"center", gap:8, marginBottom:12,
            background:"#0d0800", borderRadius:10, padding:"7px 10px", border:"1px solid #1e1000" }}>
            <span style={{ color:"#4a2800", fontSize:10, fontWeight:700 }}>BPM</span>
            <input type="number" min={40} max={250} step={1} value={bpm}
              onChange={function(e){ upd("hdelay",{bpm:Math.max(40,Math.min(250,+e.target.value))}); }}
              style={{ flex:1, background:"#050300", border:"1px solid #1e1000", borderRadius:6,
                color:ACCENT2, fontSize:13, fontWeight:800, fontFamily:"monospace",
                padding:"4px 8px", textAlign:"center", outline:"none" }} />
            <span style={{ color:"#4a2800", fontSize:9 }}>{effectiveMs}ms / beat div</span>
          </div>
        )}

        {/* ── Knob row 1: main ── */}
        <div style={{ display:"flex", justifyContent:"space-around", flexWrap:"wrap", gap:8, marginBottom:4 }}>
          <Knob label="FEEDBACK" value={Math.round(feedback*100)} min={0}  max={95} step={1}   unit="%" color={ACCENT}  onChange={function(v){ upd("hdelay",{feedback:v/100}); }} />
          <Knob label="MIX"      value={Math.round(wet*100)}      min={0}  max={100} step={1}  unit="%" color={ACCENT2} onChange={function(v){ upd("hdelay",{wet:v/100}); }} />
          <Knob label="HI CUT"   value={hiCut}  min={500}  max={20000} step={100} unit="Hz" color="#FB923C" onChange={function(v){ upd("hdelay",{hiCut:v}); }} />
          <Knob label="LO CUT"   value={loCut}  min={20}   max={500}   step={5}   unit="Hz" color="#FDBA74" onChange={function(v){ upd("hdelay",{loCut:v}); }} />
        </div>

        {/* ── Knob row 2: character ── */}
        <div style={{ display:"flex", justifyContent:"space-around", flexWrap:"wrap", gap:8 }}>
          <Knob label="DRIVE"    value={Math.round(drive*100)}    min={0}  max={100} step={1} unit="%"  color="#EF4444" onChange={function(v){ upd("hdelay",{drive:v/100}); }} />
          <Knob label="MOD RATE" value={modRate}  min={0.05} max={5}  step={0.05} unit="Hz" color="#F97316" onChange={function(v){ upd("hdelay",{modRate:v}); }} />
          <Knob label="MOD DEPT" value={Math.round(modDepth*100)} min={0} max={100} step={1} unit="%" color="#FB923C" onChange={function(v){ upd("hdelay",{modDepth:v/100}); }} />
          {mode === "ping" && <Knob label="PAN OFF" value={Math.round(stereoOf*100)} min={0} max={100} step={1} unit="%" color="#FBBF24" onChange={function(v){ upd("hdelay",{stereoOf:v/100}); }} />}
        </div>

        {/* ── Info strip ── */}
        <div style={{ marginTop:12, display:"flex", flexDirection:"column", gap:5 }}>
          <div style={{ display:"flex", alignItems:"center", justifyContent:"space-between",
            background:"#0a0600", borderRadius:8, padding:"7px 10px", border:"1px solid #150c00" }}>
            <div>
              <div style={{ color:"#FB923C", fontSize:11, fontWeight:700 }}>
                {mode==="tape" ? "Tape Mode" : mode==="ping" ? "Ping-Pong" : "Digital Mode"}
              </div>
              <div style={{ color:"#2a1800", fontSize:9, marginTop:1 }}>
                {mode==="tape" ? "Wow/flutter + saturation + modulation" :
                 mode==="ping" ? "L↔R alternate bouncing echoes" :
                 "Clean infinite-precision repeats"}
              </div>
            </div>
            <div style={{ color:ACCENT, fontSize:11, fontWeight:800, fontFamily:"monospace" }}>
              {Math.round(feedback*100)}%
            </div>
          </div>
          {drive > 0.02 && (
            <div style={{ display:"flex", alignItems:"center", justifyContent:"space-between",
              background:"#0a0600", borderRadius:8, padding:"7px 10px", border:"1px solid #150c00" }}>
              <div style={{ color:"#FB923C", fontSize:11, fontWeight:700 }}>Analog Drive</div>
              <div style={{ color:ACCENT, fontSize:11, fontWeight:800, fontFamily:"monospace" }}>
                {Math.round(drive*100)}%
              </div>
            </div>
          )}
        </div>

        {/* Footer badge */}
        <div style={{ marginTop:10, display:"flex", alignItems:"center", gap:6 }}>
          <div style={{ flex:1, height:1, background:"#1e1000" }} />
          <div style={{ color:"#2a1800", fontSize:8, fontFamily:"monospace", letterSpacing:2, fontWeight:700 }}>
            WAVES H-DELAY EMULATION · BPM SYNC · TAP TEMPO
          </div>
          <div style={{ flex:1, height:1, background:"#1e1000" }} />
        </div>

      </div>
    </div>
  );
}
const _HDelayPlugin = React.memo(_HDelayPluginInner, function(prev, next) {
  return prev.fx.hdelay === next.fx.hdelay && prev.upd === next.upd;
});

// =============================================================================
// R-COMP — Waves Renaissance Compressor style. Single-band compressor with:
//   • ARC (Auto-Release Control): release time follows envelope dynamically
//   • Warm mode: soft asymmetric tube-style saturation + 200Hz low-shelf body
//   • Electro mode: harder symmetric clip + 80Hz HPF pre-emphasis
// Designed for vocals — the warm mode is the classic Waves R-Comp tone.
// =============================================================================
function _RCompPluginInner({ fx, upd, Knob }) {
  const rc      = fx.rcomp || {};
  const on      = !!rc.on;
  const mode    = rc.mode || "warm";
  const arcOn   = rc.arc !== false; // default ON

  return (
    <div style={{ background:"linear-gradient(180deg,#0a1820 0%,#06121a 100%)", borderRadius:16, overflow:"hidden", border:"2px solid " + (on ? "#06b6d4" : "#2a2a2a"), boxShadow: on ? "0 0 20px rgba(6,182,212,0.18), inset 0 1px 0 rgba(255,255,255,0.06)" : "inset 0 1px 0 rgba(255,255,255,0.03)" }}>
      {/* Header */}
      <div style={{ background:"linear-gradient(180deg,#0c1c26 0%,#08131c 100%)", padding:"8px 14px", borderBottom:"1px solid #163245", display:"flex", alignItems:"center", gap:8 }}>
        <div style={{ flex:1 }}>
          <div style={{ color:"#67e8f9", fontWeight:900, fontSize:11, letterSpacing:3, fontFamily:"monospace", lineHeight:1 }}>R-COMP</div>
          <div style={{ color:"#1e4d5c", fontSize:7, letterSpacing:2, fontFamily:"monospace" }}>RENAISSANCE COMPRESSOR · ARC</div>
        </div>
        <div style={{ display:"flex", alignItems:"center", gap:8 }}>
          {/* Indicator LEDs */}
          <div style={{ display:"flex", gap:1.5, alignItems:"flex-end" }}>
            {[0,1,2,3,4].map(function(i){
              const colors = ["#22C55E","#22C55E","#F59E0B","#EF4444","#EF4444"];
              return <div key={i} style={{ width:3, height: 6 + i * 2, borderRadius:1, background: on ? colors[i] : "#1a2a32", boxShadow: on ? "0 0 4px " + colors[i] + "88" : "none", transition:"all 0.15s" }} />;
            })}
          </div>
          <div style={{ width:8, height:8, borderRadius:"50%", background: on ? "#06b6d4" : "#1a1a1a", boxShadow: on ? "0 0 6px #06b6d4, 0 0 12px rgba(6,182,212,0.5)" : "none", transition:"all 0.2s" }} />
          <button onClick={function(){ upd("rcomp",{on:!on}); }}
            style={{ background: on ? "linear-gradient(180deg,#0891b2,#0e7490)" : "linear-gradient(180deg,#2a2a2a,#222)", border:"1px solid " + (on ? "#06b6d4" : "#333"), borderRadius:5, color:"white", fontSize:9, fontWeight:800, padding:"4px 12px", cursor:"pointer", letterSpacing:1, boxShadow: on ? "0 1px 0 rgba(255,255,255,0.1) inset" : "0 1px 3px rgba(0,0,0,0.5)" }}>
            {on ? "ON" : "OFF"}
          </button>
        </div>
      </div>

      <div style={{ padding:"12px 14px", opacity: on ? 1 : 0.4, transition:"opacity 0.2s" }}>
        {/* Mode + ARC controls row */}
        <div style={{ display:"flex", alignItems:"center", justifyContent:"space-between", marginBottom:10, gap:8 }}>
          {/* Mode toggle: warm / electro */}
          <div style={{ display:"flex", gap:0, background:"#020608", border:"1px solid #163245", borderRadius:6, padding:2 }}>
            <button onClick={function(){ upd("rcomp",{mode:"warm"}); }}
              style={{ background: mode === "warm" ? "linear-gradient(180deg,#0891b2,#0e7490)" : "transparent", border:"none", borderRadius:4, color: mode === "warm" ? "white" : "#456", fontSize:9, fontWeight:800, padding:"3px 10px", cursor:"pointer", letterSpacing:1 }}>WARM</button>
            <button onClick={function(){ upd("rcomp",{mode:"electro"}); }}
              style={{ background: mode === "electro" ? "linear-gradient(180deg,#0891b2,#0e7490)" : "transparent", border:"none", borderRadius:4, color: mode === "electro" ? "white" : "#456", fontSize:9, fontWeight:800, padding:"3px 10px", cursor:"pointer", letterSpacing:1 }}>ELECTRO</button>
          </div>
          {/* ARC toggle */}
          <div style={{ display:"flex", alignItems:"center", gap:6 }}>
            <span style={{ color: arcOn ? "#67e8f9" : "#456", fontSize:9, fontWeight:800, letterSpacing:1, fontFamily:"monospace" }}>ARC</span>
            <button onClick={function(){ upd("rcomp",{arc:!arcOn}); }}
              style={{ width:36, height:18, borderRadius:9, background: arcOn ? "linear-gradient(90deg,#0891b2,#06b6d4)" : "#1a2a32", border:"1px solid " + (arcOn ? "#06b6d4" : "#2a3a42"), cursor:"pointer", position:"relative", padding:0 }}>
              <div style={{ position:"absolute", top:1, left: arcOn ? 19 : 1, width:14, height:14, borderRadius:"50%", background: arcOn ? "white" : "#456", transition:"left 0.15s", boxShadow:"0 1px 3px rgba(0,0,0,0.4)" }} />
            </button>
          </div>
        </div>

        {/* Knobs row 1: THRESH, RATIO */}
        <div style={{ display:"flex", justifyContent:"space-around", overflow:"visible", marginBottom:8 }}>
          <Knob label="THRESH"  value={rc.threshold ?? -18} min={-60} max={0}  step={1}    unit="dB" color="#06b6d4" onChange={function(v){ upd("rcomp",{threshold:v}); }} />
          <Knob label="RATIO"   value={rc.ratio ?? 3}        min={1}   max={20} step={0.5}  unit=":1" color="#06b6d4" onChange={function(v){ upd("rcomp",{ratio:v}); }} />
        </div>
        <div style={{ height:1, background:"#163245", borderRadius:1 }} />
        {/* Knobs row 2: ATTACK, RELEASE, GAIN */}
        <div style={{ display:"flex", justifyContent:"space-around", overflow:"visible", marginTop:8 }}>
          <Knob label="ATTACK"  value={Math.round((rc.attack ?? 0.012) * 1000)} min={1}  max={200}  step={1}   unit="ms" color="#67e8f9" onChange={function(v){ upd("rcomp",{attack:v/1000}); }} />
          <Knob label={arcOn ? "REL · ARC" : "RELEASE"} value={Math.round((rc.release ?? 0.18) * 1000)} min={10} max={2000} step={10}  unit="ms" color={arcOn ? "#1e4d5c" : "#67e8f9"} onChange={function(v){ if (!arcOn) upd("rcomp",{release:v/1000}); }} />
          <Knob label="GAIN"    value={rc.makeupGain ?? 0}    min={0}   max={24}  step={0.5} unit="dB" color="#22C55E" onChange={function(v){ upd("rcomp",{makeupGain:v}); }} />
        </div>
      </div>
    </div>
  );
}
const _RCompPlugin = React.memo(_RCompPluginInner, function(prev, next) {
  return prev.fx.rcomp === next.fx.rcomp && prev.upd === next.upd;
});

// =============================================================================
// PULTEC EQP-1A — Passive Program Equalizer (1950s tube unit)
// The legendary "boost and cut the same frequency" passive EQ. Famous for
// its musical curves and the iconic "Pultec trick": simultaneously boosting
// AND attenuating the same low frequency produces a uniquely scooped curve
// that adds deep weight while taking out the muddy 200-400Hz region.
//
// Topology (matches the original signal flow):
//   in → [Low Boost lowshelf] → [Low Atten peaking with negative gain]
//      → [High Boost peaking with bandwidth Q] → [High Atten highshelf] → out
//
// Frequency selects are discrete (authentic), bandwidth (SHARP) maps to Q
// (higher SHARP = wider bell, lower SHARP = narrower). Boosts always positive,
// attens always positive in UI but inverted as negative gain in the filters.
// =============================================================================
function _PultecPluginInner({ fx, upd, Knob }) {
  const pt        = fx.pultec || {};
  const on        = !!pt.on;
  const lowFreq   = pt.lowFreq   ?? 60;        // 20 | 30 | 60 | 100
  const lowBoost  = pt.lowBoost  ?? 0;         // 0..10 dB
  const lowAtten  = pt.lowAtten  ?? 0;         // 0..10 dB (applied as negative)
  const hiFreq    = pt.hiFreq    ?? 5000;      // 3000|4000|5000|8000|10000|12000|16000
  const hiBoost   = pt.hiBoost   ?? 0;         // 0..10 dB
  const sharp     = pt.sharp     ?? 5;         // 1..10 (higher = wider bell)
  const attenFreq = pt.attenFreq ?? 10000;     // 5000 | 10000 | 20000
  const hiAtten   = pt.hiAtten   ?? 0;         // 0..10 dB (applied as negative)

  const LOW_FREQS  = [20, 30, 60, 100];
  const HIGH_FREQS = [3000, 4000, 5000, 8000, 10000, 12000, 16000];
  const ATTEN_FREQS = [5000, 10000, 20000];
  const fmtHz = function(hz){ return hz >= 1000 ? (hz/1000).toFixed(hz % 1000 === 0 ? 0 : 1) + "K" : String(hz); };

  // Section button style — the iconic Pultec frequency-select look
  const SelectBtn = function({ value, current, onClick, children }){
    const sel = value === current;
    return (
      <button onClick={function(){ onClick(value); }}
        style={{
          background: sel
            ? "linear-gradient(180deg,#F5E4C3 0%,#D4A574 60%,#A07843 100%)"
            : "linear-gradient(180deg,#3a2e1f 0%,#2a2015 100%)",
          border: "1px solid " + (sel ? "#8B5E2E" : "#1a1208"),
          borderRadius: 3,
          color: sel ? "#3d1f00" : "#7a6045",
          fontSize: 9,
          fontWeight: 800,
          fontFamily: "monospace",
          padding: "5px 7px",
          cursor: "pointer",
          minWidth: 28,
          textShadow: sel ? "0 1px 0 rgba(255,255,255,0.3)" : "none",
          boxShadow: sel
            ? "inset 0 1px 0 rgba(255,255,255,0.4), 0 2px 4px rgba(0,0,0,0.4)"
            : "inset 0 1px 0 rgba(0,0,0,0.3)",
          transition: "all 0.1s",
        }}>{children}</button>
    );
  };

  return (
    <div style={{
      background: "linear-gradient(180deg,#3a2918 0%,#2a1f13 100%)",
      borderRadius: 12,
      overflow: "hidden",
      border: "2px solid " + (on ? "#8B5E2E" : "#2a2018"),
      boxShadow: on
        ? "0 0 24px rgba(212,165,116,0.18), inset 0 1px 0 rgba(245,228,195,0.1), 0 4px 12px rgba(0,0,0,0.5)"
        : "inset 0 1px 0 rgba(245,228,195,0.04), 0 2px 6px rgba(0,0,0,0.4)",
      position: "relative",
    }}>
      {/* Wooden side panels for hardware look */}
      <div style={{ position:"absolute", top:0, bottom:0, left:0, width:8,
        background:"linear-gradient(90deg,#4a2810 0%,#6b3a15 50%,#3a1f0a 100%)",
        borderRight:"1px solid #1a0e05", zIndex:1 }} />
      <div style={{ position:"absolute", top:0, bottom:0, right:0, width:8,
        background:"linear-gradient(90deg,#3a1f0a 0%,#6b3a15 50%,#4a2810 100%)",
        borderLeft:"1px solid #1a0e05", zIndex:1 }} />

      {/* Header — brushed metal faceplate */}
      <div style={{
        background: "linear-gradient(180deg,#5c4a35 0%,#4a3a28 50%,#3d2f1f 100%)",
        padding: "8px 16px",
        margin: "0 8px",
        borderBottom: "1px solid #2a2015",
        display: "flex",
        alignItems: "center",
        gap: 12,
        boxShadow: "inset 0 1px 0 rgba(245,228,195,0.2)",
      }}>
        <div style={{ flex:1 }}>
          <div style={{
            color: "#F5E4C3",
            fontWeight: 900,
            fontSize: 12,
            letterSpacing: 3,
            fontFamily: "'Georgia', serif",
            lineHeight: 1,
            textShadow: "0 1px 0 rgba(0,0,0,0.4)",
          }}>PULTEC EQP-1A</div>
          <div style={{
            color: "#a08e6a",
            fontSize: 7,
            letterSpacing: 2,
            fontFamily: "monospace",
            marginTop: 2,
          }}>PROGRAM EQUALIZER · PASSIVE</div>
        </div>
        <div style={{ display:"flex", alignItems:"center", gap:8 }}>
          {/* Tube-glow indicator */}
          <div style={{
            width: 10, height: 10, borderRadius: "50%",
            background: on
              ? "radial-gradient(circle at 35% 35%,#FFE6B0 0%,#FCA453 40%,#7C3A0A 100%)"
              : "radial-gradient(circle,#1a1208 0%,#0a0604 100%)",
            boxShadow: on
              ? "0 0 8px #FCA453, 0 0 16px rgba(252,164,83,0.5), inset 0 1px 1px rgba(255,230,176,0.6)"
              : "inset 0 1px 1px rgba(0,0,0,0.6)",
            transition: "all 0.3s",
          }} />
          <button onClick={function(){ upd("pultec",{on:!on}); }}
            style={{
              background: on
                ? "linear-gradient(180deg,#A07843 0%,#7C5832 100%)"
                : "linear-gradient(180deg,#2a2015 0%,#1a1208 100%)",
              border: "1px solid " + (on ? "#D4A574" : "#3a2e1f"),
              borderRadius: 4,
              color: on ? "#F5E4C3" : "#5a4838",
              fontSize: 9,
              fontWeight: 800,
              padding: "5px 14px",
              cursor: "pointer",
              letterSpacing: 1,
              boxShadow: on
                ? "inset 0 1px 0 rgba(245,228,195,0.3), 0 1px 3px rgba(0,0,0,0.4)"
                : "inset 0 1px 0 rgba(0,0,0,0.4)",
            }}>{on ? "IN" : "OUT"}</button>
        </div>
      </div>

      <div style={{
        padding: "14px 18px",
        opacity: on ? 1 : 0.5,
        transition: "opacity 0.2s",
        // Faceplate texture
        background: "radial-gradient(ellipse at center,rgba(245,228,195,0.04) 0%,transparent 70%)",
      }}>
        {/* ── LOW FREQUENCY SECTION ── */}
        <div style={{ marginBottom: 14 }}>
          <div style={{
            color: "#D4A574",
            fontSize: 8,
            letterSpacing: 2,
            fontWeight: 800,
            fontFamily: "'Georgia', serif",
            marginBottom: 6,
            textShadow: "0 1px 0 rgba(0,0,0,0.4)",
          }}>LOW FREQUENCY</div>

          {/* Low freq select buttons */}
          <div style={{
            display: "flex",
            gap: 4,
            background: "linear-gradient(180deg,#1a1208 0%,#0a0604 100%)",
            padding: 6,
            borderRadius: 5,
            border: "1px solid #2a2015",
            boxShadow: "inset 0 2px 4px rgba(0,0,0,0.6)",
            marginBottom: 8,
          }}>
            <span style={{ color:"#7a6045", fontSize:8, fontWeight:700, letterSpacing:1, alignSelf:"center", marginRight:4 }}>CPS</span>
            {LOW_FREQS.map(function(f){
              return <SelectBtn key={f} value={f} current={lowFreq} onClick={function(v){ upd("pultec",{lowFreq:v}); }}>{f}</SelectBtn>;
            })}
          </div>

          {/* Low boost + atten knobs */}
          <div style={{ display:"flex", justifyContent:"space-around", gap:8 }}>
            <Knob label="LOW BOOST" value={lowBoost} min={0} max={10} step={0.1} unit="dB" color="#D4A574" onChange={function(v){ upd("pultec",{lowBoost:v}); }} />
            <Knob label="LOW ATTEN" value={lowAtten} min={0} max={10} step={0.1} unit="dB" color="#A07843" onChange={function(v){ upd("pultec",{lowAtten:v}); }} />
          </div>
        </div>

        <div style={{ height:1, background:"linear-gradient(90deg,transparent,#5c4a35,transparent)", margin:"10px 0" }} />

        {/* ── HIGH FREQUENCY BOOST SECTION ── */}
        <div style={{ marginBottom: 14 }}>
          <div style={{
            color: "#D4A574",
            fontSize: 8,
            letterSpacing: 2,
            fontWeight: 800,
            fontFamily: "'Georgia', serif",
            marginBottom: 6,
            textShadow: "0 1px 0 rgba(0,0,0,0.4)",
          }}>HIGH FREQUENCY BOOST</div>

          <div style={{
            display: "flex",
            gap: 3,
            background: "linear-gradient(180deg,#1a1208 0%,#0a0604 100%)",
            padding: 6,
            borderRadius: 5,
            border: "1px solid #2a2015",
            boxShadow: "inset 0 2px 4px rgba(0,0,0,0.6)",
            marginBottom: 8,
            flexWrap: "wrap",
          }}>
            <span style={{ color:"#7a6045", fontSize:8, fontWeight:700, letterSpacing:1, alignSelf:"center", marginRight:2 }}>KCS</span>
            {HIGH_FREQS.map(function(f){
              return <SelectBtn key={f} value={f} current={hiFreq} onClick={function(v){ upd("pultec",{hiFreq:v}); }}>{fmtHz(f)}</SelectBtn>;
            })}
          </div>

          <div style={{ display:"flex", justifyContent:"space-around", gap:8 }}>
            <Knob label="HI BOOST" value={hiBoost} min={0} max={10} step={0.1} unit="dB" color="#D4A574" onChange={function(v){ upd("pultec",{hiBoost:v}); }} />
            <Knob label="SHARP" value={sharp} min={1} max={10} step={0.1} unit="" color="#a08e6a" onChange={function(v){ upd("pultec",{sharp:v}); }} />
          </div>
          <div style={{ textAlign:"center", color:"#7a6045", fontSize:8, marginTop:2, fontFamily:"monospace", letterSpacing:1 }}>
            ← BROAD · SHARP → NARROW
          </div>
        </div>

        <div style={{ height:1, background:"linear-gradient(90deg,transparent,#5c4a35,transparent)", margin:"10px 0" }} />

        {/* ── ATTEN SELECT + HIGH ATTEN ── */}
        <div>
          <div style={{
            color: "#D4A574",
            fontSize: 8,
            letterSpacing: 2,
            fontWeight: 800,
            fontFamily: "'Georgia', serif",
            marginBottom: 6,
            textShadow: "0 1px 0 rgba(0,0,0,0.4)",
          }}>ATTEN SEL</div>

          <div style={{
            display: "flex",
            gap: 4,
            background: "linear-gradient(180deg,#1a1208 0%,#0a0604 100%)",
            padding: 6,
            borderRadius: 5,
            border: "1px solid #2a2015",
            boxShadow: "inset 0 2px 4px rgba(0,0,0,0.6)",
            marginBottom: 8,
          }}>
            <span style={{ color:"#7a6045", fontSize:8, fontWeight:700, letterSpacing:1, alignSelf:"center", marginRight:4 }}>KCS</span>
            {ATTEN_FREQS.map(function(f){
              return <SelectBtn key={f} value={f} current={attenFreq} onClick={function(v){ upd("pultec",{attenFreq:v}); }}>{fmtHz(f)}</SelectBtn>;
            })}
          </div>

          <div style={{ display:"flex", justifyContent:"center" }}>
            <Knob label="ATTEN" value={hiAtten} min={0} max={10} step={0.1} unit="dB" color="#A07843" onChange={function(v){ upd("pultec",{hiAtten:v}); }} />
          </div>
        </div>

        {/* Pultec trick hint when both low boost and atten are engaged */}
        {on && lowBoost > 0.5 && lowAtten > 0.5 && (
          <div style={{
            marginTop: 12,
            padding: "6px 10px",
            background: "linear-gradient(180deg,rgba(245,228,195,0.08),rgba(212,165,116,0.04))",
            borderRadius: 4,
            border: "1px solid rgba(212,165,116,0.3)",
            color: "#F5E4C3",
            fontSize: 9,
            fontStyle: "italic",
            textAlign: "center",
            fontFamily: "'Georgia',serif",
            letterSpacing: 0.5,
          }}>✦ Pultec trick engaged — deep lows + carved mids</div>
        )}
      </div>
    </div>
  );
}
const _PultecPlugin = React.memo(_PultecPluginInner, function(prev, next) {
  return prev.fx.pultec === next.fx.pultec && prev.upd === next.upd;
});

// =============================================================================
// TQ — VOCAL DYNAMIC EQ — 7-band sidechain-driven dynamic processor
// UI panel design (Session 2):
//   • Header: brand, ON/OFF, master mode (dyneq only for now — mb is Session 3)
//   • Band tiles row: 7 mini cards, one per band, with live GR meter and on/off LED
//   • Selected band detail: knobs for freq, Q, threshold, gain, attack, release
//     + band type toggle (cut/boost) + per-band BYP and SOLO buttons
//   • Each band has its own accent color so the UI tells you what you're working on
//
// Memoization: re-renders only when fx.tq, upd, or trackId change.
// =============================================================================

// Band metadata — colors keyed to what the band does. Each band has a 2-letter
// short code that fits in mini tile + a descriptive subtitle for the detail header.
// ── TQ Frequency Response Visualizer ───────────────────────────────────────
// Canvas-based EQ curve showing each band's static peak/shelf shape PLUS
// live gain adjustment from the sidechain detectors. Updates at ~20Hz so
// users can SEE the dynamic EQ working in real time.
//
// Math (approximation — close enough for visualization, not DSP-accurate):
//   Peaking band:  response(f) = g_eff / (1 + (q * (f/f0 - f0/f))^2)
//   High shelf:    response(f) = g_eff / (1 + (f0/f)^4)
//
// `g_eff` is the EFFECTIVE current gain — for cut bands, this is
//   targetGain * min(1, reduction/6); for boost bands, it's
//   targetGain * max(0, 1 - reduction/6). Mirrors the polling-loop formula.
//
// When the track isn't playing or there's no live det, g_eff = 0 (no movement).
// When playing, bars at each band's frequency show how much that band is
// reducing/boosting right now — overlaid on the curve so you see both the
// static intent and the live response.
function TQFreqCurve({ trackId, bands, listenBandId, selectedBandId, width, height }) {
  const canvasRef = React.useRef(null);
  // Live reductions per band, polled at ~20Hz from the live detectors.
  // Keep as a ref so we don't trigger re-renders on every poll tick —
  // the draw loop reads the ref directly each frame.
  const liveReductionsRef = React.useRef({});

  React.useEffect(function(){
    let alive = true;
    const poll = function(){
      try {
        const liveNodes = _getLiveFxNodes(trackId);
        if (liveNodes && liveNodes.tq && liveNodes.tq.bandNodes) {
          const next = {};
          for (const bn of liveNodes.tq.bandNodes) {
            next[bn.id] = Math.abs(bn.det.reduction || 0);
          }
          liveReductionsRef.current = next;
        } else {
          liveReductionsRef.current = {};
        }
      } catch(e) { liveReductionsRef.current = {}; }
    };
    const intervalId = setInterval(poll, 50);
    poll();
    return function(){ alive = false; clearInterval(intervalId); };
  }, [trackId]);

  // Draw loop — uses requestAnimationFrame for smooth curve animation.
  // Reads bands prop (static config) + liveReductionsRef.current (live state).
  React.useEffect(function(){
    let rafId = null;
    const draw = function(){
      const canvas = canvasRef.current;
      if (!canvas) { rafId = requestAnimationFrame(draw); return; }
      // Performance Mode short-circuit — skip drawing entirely but keep the
      // rAF scheduled so we resume cleanly when user toggles back. Same
      // pattern as useAnalyser.
      if (_performanceMode) {
        rafId = requestAnimationFrame(draw);
        return;
      }
      const ctx = canvas.getContext("2d");
      const w = canvas.width;
      const h = canvas.height;
      ctx.clearRect(0, 0, w, h);

      // ── Background grid ──
      ctx.strokeStyle = "rgba(168,85,247,0.06)";
      ctx.lineWidth = 1;
      // Horizontal: 0dB center, ±6dB and ±12dB lines
      const midY = h / 2;
      const dbToY = function(db){ return midY - (db / 12) * (h * 0.45); };
      for (const db of [-12, -6, 0, 6, 12]) {
        const y = dbToY(db);
        ctx.beginPath();
        ctx.moveTo(0, y);
        ctx.lineTo(w, y);
        ctx.stroke();
      }
      // Vertical: log-spaced frequency markers (100Hz, 1k, 10k)
      const fMin = 20, fMax = 20000;
      const fToX = function(f){ return (Math.log10(f) - Math.log10(fMin)) / (Math.log10(fMax) - Math.log10(fMin)) * w; };
      for (const f of [100, 1000, 10000]) {
        const x = fToX(f);
        ctx.beginPath();
        ctx.moveTo(x, 0);
        ctx.lineTo(x, h);
        ctx.stroke();
      }
      // 0dB centerline (slightly brighter)
      ctx.strokeStyle = "rgba(168,85,247,0.15)";
      ctx.beginPath();
      ctx.moveTo(0, midY);
      ctx.lineTo(w, midY);
      ctx.stroke();

      // ── Compute effective gain per band (static * live reduction modulation) ──
      const reductions = liveReductionsRef.current;
      const effectiveBands = bands.map(function(b){
        if (!b.on) return null;
        // LISTEN mode: only the listened band's static EQ curve is meaningful
        // (because the audio is band-passed to that range — other bands'
        // effects are inaudible). Show only the listened band's curve.
        if (listenBandId && b.id !== listenBandId) return null;
        const r = reductions[b.id] || 0;
        let gEff;
        if (b.type === "cut") {
          gEff = b.gain * Math.min(1, r / 6);
        } else {
          gEff = b.gain * Math.max(0, 1 - r / 6);
        }
        return { id:b.id, freq:b.freq, q:b.q, gain:gEff, type:b.type, isAir:b.id === "air" };
      }).filter(Boolean);

      // ── Per-band curve calc ──
      const respAt = function(f){
        let total = 0;
        for (const b of effectiveBands) {
          if (b.isAir) {
            // High-shelf approximation
            total += b.gain / (1 + Math.pow(b.freq / f, 4));
          } else {
            // Peaking bell
            const ratio = f / b.freq - b.freq / f;
            total += b.gain / (1 + Math.pow(b.q * ratio, 2));
          }
        }
        return total;
      };

      // ── Filled fill area under the curve ──
      const samplesAcross = Math.max(80, Math.floor(w / 2));
      ctx.beginPath();
      ctx.moveTo(0, midY);
      for (let i = 0; i <= samplesAcross; i++) {
        const x = (i / samplesAcross) * w;
        const f = Math.pow(10, Math.log10(fMin) + (i / samplesAcross) * (Math.log10(fMax) - Math.log10(fMin)));
        const db = respAt(f);
        const y = dbToY(db);
        ctx.lineTo(x, y);
      }
      ctx.lineTo(w, midY);
      ctx.closePath();
      const fill = ctx.createLinearGradient(0, 0, 0, h);
      fill.addColorStop(0,   "rgba(168,85,247,0.22)");
      fill.addColorStop(0.5, "rgba(168,85,247,0.08)");
      fill.addColorStop(1,   "rgba(168,85,247,0.22)");
      ctx.fillStyle = fill;
      ctx.fill();

      // ── Curve stroke on top ──
      ctx.beginPath();
      let started = false;
      for (let i = 0; i <= samplesAcross; i++) {
        const x = (i / samplesAcross) * w;
        const f = Math.pow(10, Math.log10(fMin) + (i / samplesAcross) * (Math.log10(fMax) - Math.log10(fMin)));
        const db = respAt(f);
        const y = dbToY(db);
        if (!started) { ctx.moveTo(x, y); started = true; } else { ctx.lineTo(x, y); }
      }
      ctx.strokeStyle = "rgba(196,181,253,0.9)";
      ctx.lineWidth = 1.5;
      ctx.stroke();

      // ── Per-band markers (dots at each band's freq, colored by band) ──
      for (const b of bands) {
        if (!b.on) continue;
        const meta = TQ_BAND_META[b.id];
        if (!meta) continue;
        const x = fToX(b.freq);
        const r = reductions[b.id] || 0;
        let gEff;
        if (b.type === "cut") {
          gEff = b.gain * Math.min(1, r / 6);
        } else {
          gEff = b.gain * Math.max(0, 1 - r / 6);
        }
        const y = dbToY(gEff);
        const isSel = b.id === selectedBandId;
        const isListening = listenBandId === b.id;
        const dim = (listenBandId && !isListening) ? 0.25 : 1.0;
        // Halo for selected band
        if (isSel) {
          ctx.beginPath();
          ctx.arc(x, y, 7, 0, Math.PI*2);
          ctx.fillStyle = meta.color + "33";
          ctx.fill();
        }
        // Marker dot
        ctx.beginPath();
        ctx.arc(x, y, isSel ? 4 : 3, 0, Math.PI*2);
        ctx.fillStyle = meta.color + (dim < 1 ? "55" : "ff");
        ctx.fill();
        if (isSel) {
          ctx.strokeStyle = "white";
          ctx.lineWidth = 1.2;
          ctx.stroke();
        }
      }
      rafId = requestAnimationFrame(draw);
    };
    rafId = requestAnimationFrame(draw);
    return function(){ if (rafId) cancelAnimationFrame(rafId); };
  }, [bands, listenBandId, selectedBandId]);

  // Width/height respect the panel — match canvas backing to displayed size
  // for crisp rendering on Retina/HiDPI displays.
  const dpr = (typeof window !== "undefined") ? (window.devicePixelRatio || 1) : 1;
  return (
    <canvas
      ref={canvasRef}
      width={width * dpr}
      height={height * dpr}
      style={{ width: width + "px", height: height + "px", display:"block", borderRadius:8, background:"rgba(0,0,0,0.3)", pointerEvents:"none" }}
    />
  );
}

// ── TQ Presets ─────────────────────────────────────────────────────────────
// Each preset is a fresh `bands` array tuned for a specific vocal style.
// Applying a preset replaces the bands entirely (clean apply, not merge) so
// any user-tweaked values get reset to the preset's intent.
//
// Tuning philosophy:
//   VOCAL — balanced default; matches the on-load state. Conservative.
//   RAP   — aggressive de-essing, strong harsh tamer, presence ON to lift
//           quiet ad-libs, air OFF (rap prefers darker tonality).
//   POP   — gentler everywhere, air ON for polish, presence ON for forward
//           vocal, softer de-ess.
//   LOOSE — high thresholds, catches only the worst peaks. Preserves character.
//   TIGHT — low thresholds, larger gain targets, tames problem material.
const TQ_PRESETS = {
  vocal: {
    label: "VOCAL", desc: "Balanced default",
    bands: [
      { id:"plosive",  type:"cut",   on:true,  freq:80,    q:1.4, gain:-6,  threshold:-12, attack:0.002, release:0.080, name:"PLOSIVE" },
      { id:"mud",      type:"cut",   on:true,  freq:250,   q:1.0, gain:-4,  threshold:-18, attack:0.008, release:0.120, name:"MUD" },
      { id:"box",      type:"cut",   on:true,  freq:600,   q:1.2, gain:-3,  threshold:-15, attack:0.005, release:0.100, name:"BOX" },
      { id:"presence", type:"boost", on:false, freq:3000,  q:1.0, gain:+3,  threshold:-30, attack:0.020, release:0.200, name:"PRESENCE" },
      { id:"harsh",    type:"cut",   on:true,  freq:3200,  q:1.8, gain:-3,  threshold:-12, attack:0.003, release:0.080, name:"HARSH" },
      { id:"deess",    type:"cut",   on:true,  freq:7000,  q:2.5, gain:-6,  threshold:-14, attack:0.001, release:0.060, name:"DE-ESS" },
      { id:"air",      type:"boost", on:false, freq:12000, q:0.7, gain:+3,  threshold:-32, attack:0.030, release:0.250, name:"AIR" },
    ]
  },
  rap: {
    label: "RAP", desc: "Aggressive · Hot delivery",
    bands: [
      { id:"plosive",  type:"cut",   on:true,  freq:90,    q:1.6, gain:-8,  threshold:-10, attack:0.001, release:0.060, name:"PLOSIVE" },
      { id:"mud",      type:"cut",   on:true,  freq:300,   q:1.0, gain:-3,  threshold:-18, attack:0.008, release:0.120, name:"MUD" },
      { id:"box",      type:"cut",   on:true,  freq:700,   q:1.4, gain:-4,  threshold:-14, attack:0.004, release:0.090, name:"BOX" },
      { id:"presence", type:"boost", on:true,  freq:3500,  q:1.0, gain:+4,  threshold:-26, attack:0.015, release:0.180, name:"PRESENCE" },
      { id:"harsh",    type:"cut",   on:true,  freq:3500,  q:2.0, gain:-5,  threshold:-10, attack:0.002, release:0.070, name:"HARSH" },
      { id:"deess",    type:"cut",   on:true,  freq:7500,  q:2.8, gain:-8,  threshold:-12, attack:0.0008,release:0.050, name:"DE-ESS" },
      { id:"air",      type:"boost", on:false, freq:12000, q:0.7, gain:+2,  threshold:-32, attack:0.030, release:0.250, name:"AIR" },
    ]
  },
  pop: {
    label: "POP", desc: "Polished · Bright",
    bands: [
      { id:"plosive",  type:"cut",   on:true,  freq:80,    q:1.4, gain:-5,  threshold:-14, attack:0.002, release:0.080, name:"PLOSIVE" },
      { id:"mud",      type:"cut",   on:true,  freq:220,   q:0.9, gain:-3,  threshold:-20, attack:0.010, release:0.150, name:"MUD" },
      { id:"box",      type:"cut",   on:true,  freq:550,   q:1.0, gain:-2,  threshold:-18, attack:0.006, release:0.110, name:"BOX" },
      { id:"presence", type:"boost", on:true,  freq:2800,  q:0.9, gain:+4,  threshold:-32, attack:0.020, release:0.200, name:"PRESENCE" },
      { id:"harsh",    type:"cut",   on:true,  freq:3000,  q:1.5, gain:-2,  threshold:-14, attack:0.004, release:0.090, name:"HARSH" },
      { id:"deess",    type:"cut",   on:true,  freq:6800,  q:2.2, gain:-5,  threshold:-16, attack:0.0015,release:0.070, name:"DE-ESS" },
      { id:"air",      type:"boost", on:true,  freq:12000, q:0.7, gain:+4,  threshold:-30, attack:0.025, release:0.220, name:"AIR" },
    ]
  },
  loose: {
    label: "LOOSE", desc: "Only the worst peaks",
    bands: [
      { id:"plosive",  type:"cut",   on:true,  freq:80,    q:1.4, gain:-4,  threshold:-6,  attack:0.002, release:0.080, name:"PLOSIVE" },
      { id:"mud",      type:"cut",   on:true,  freq:250,   q:1.0, gain:-2,  threshold:-12, attack:0.010, release:0.150, name:"MUD" },
      { id:"box",      type:"cut",   on:false, freq:600,   q:1.2, gain:-2,  threshold:-10, attack:0.006, release:0.110, name:"BOX" },
      { id:"presence", type:"boost", on:false, freq:3000,  q:1.0, gain:+2,  threshold:-26, attack:0.020, release:0.200, name:"PRESENCE" },
      { id:"harsh",    type:"cut",   on:true,  freq:3200,  q:1.6, gain:-2,  threshold:-8,  attack:0.004, release:0.090, name:"HARSH" },
      { id:"deess",    type:"cut",   on:true,  freq:7000,  q:2.2, gain:-4,  threshold:-8,  attack:0.0015,release:0.070, name:"DE-ESS" },
      { id:"air",      type:"boost", on:false, freq:12000, q:0.7, gain:+2,  threshold:-30, attack:0.030, release:0.250, name:"AIR" },
    ]
  },
  tight: {
    label: "TIGHT", desc: "Surgical · Problem source",
    bands: [
      { id:"plosive",  type:"cut",   on:true,  freq:80,    q:1.8, gain:-10, threshold:-18, attack:0.001, release:0.060, name:"PLOSIVE" },
      { id:"mud",      type:"cut",   on:true,  freq:250,   q:1.2, gain:-6,  threshold:-24, attack:0.006, release:0.100, name:"MUD" },
      { id:"box",      type:"cut",   on:true,  freq:600,   q:1.4, gain:-5,  threshold:-20, attack:0.004, release:0.090, name:"BOX" },
      { id:"presence", type:"boost", on:false, freq:3000,  q:1.0, gain:+3,  threshold:-30, attack:0.020, release:0.200, name:"PRESENCE" },
      { id:"harsh",    type:"cut",   on:true,  freq:3200,  q:2.2, gain:-6,  threshold:-18, attack:0.002, release:0.070, name:"HARSH" },
      { id:"deess",    type:"cut",   on:true,  freq:7000,  q:3.0, gain:-10, threshold:-20, attack:0.0008,release:0.050, name:"DE-ESS" },
      { id:"air",      type:"boost", on:false, freq:12000, q:0.7, gain:+2,  threshold:-32, attack:0.030, release:0.250, name:"AIR" },
    ]
  },
};

const TQ_BAND_META = {
  plosive:  { code:"PL", color:"#EF4444", desc:"Plosive Tamer · low-freq P/B hits" },
  mud:      { code:"MU", color:"#92400E", desc:"Mud Control · low-mid muddiness" },
  box:      { code:"BX", color:"#A16207", desc:"Box · midrange boxy honk" },
  presence: { code:"PR", color:"#22D3EE", desc:"Presence · upward lift in quiet passages" },
  harsh:    { code:"HA", color:"#F97316", desc:"Harshness · upper-mid bite" },
  deess:    { code:"DE", color:"#FACC15", desc:"De-Esser · sibilance" },
  air:      { code:"AR", color:"#7DD3FC", desc:"Air · breath & sheen (upward)" },
};

// Mini gain-reduction meter for a band tile. Polls the live detector's
// reduction property at ~20Hz and renders a vertical bar (top-down: more
// reduction = taller red bar). When no live node available (track not playing),
// shows a flat dark bar.
function TQBandGRMeter({ trackId, bandId, color, height }) {
  const [gr, setGr] = React.useState(0);
  React.useEffect(function(){
    let alive = true;
    let intervalId = null;
    const poll = function(){
      try {
        const liveNodes = _getLiveFxNodes(trackId);
        const bandNode = liveNodes && liveNodes.tq && liveNodes.tq.bandNodes
          ? liveNodes.tq.bandNodes.find(function(b){ return b.id === bandId; })
          : null;
        if (bandNode && bandNode.det) {
          const r = Math.abs(bandNode.det.reduction || 0);
          if (alive) setGr(r);
        } else {
          if (alive) setGr(0);
        }
      } catch(e) {}
    };
    intervalId = setInterval(poll, 50);  // 20Hz
    poll();
    return function(){ alive = false; if (intervalId) clearInterval(intervalId); };
  }, [trackId, bandId]);
  // Scale: 0dB = empty, 12dB = full. Most bands target -3 to -6dB so 12dB is generous headroom.
  const pct = Math.min(1, gr / 12);
  const h = height || 28;
  return (
    <div style={{ width:6, height:h, background:"rgba(0,0,0,0.4)", borderRadius:2, position:"relative", overflow:"hidden" }}>
      <div style={{
        position:"absolute", left:0, right:0, top:0,
        height: (pct * 100) + "%",
        background:"linear-gradient(180deg," + color + "ff," + color + "88)",
        boxShadow: pct > 0.05 ? "0 0 4px " + color + "aa" : "none",
        transition:"height 0.08s ease-out",
      }} />
    </div>
  );
}

function _TQPluginInner({ fx, upd, Knob, trackId }) {
  const tq = fx.tq || {};
  const on = !!tq.on;
  const bands = tq.bands || [];

  // ── Legacy-track seed ──
  // If this track was created before TQ existed in defaultEffects, fx.tq
  // may be {} or have no bands array. Seed sensible defaults the first
  // time this panel renders so the user has something to work with.
  // Uses default vocal-tuned values matching defaultEffects(). Runs once.
  React.useEffect(function(){
    if (!tq.bands || tq.bands.length !== 7) {
      // Seed in shape that matches defaultEffects so audio engine + UI agree.
      const seedBands = [
        { id:"plosive",  type:"cut",   on:true, freq:80,   q:1.4, gain:-6,  threshold:-12, attack:0.002, release:0.080, name:"PLOSIVE" },
        { id:"mud",      type:"cut",   on:true, freq:250,  q:1.0, gain:-4,  threshold:-18, attack:0.008, release:0.120, name:"MUD" },
        { id:"box",      type:"cut",   on:true, freq:600,  q:1.2, gain:-3,  threshold:-15, attack:0.005, release:0.100, name:"BOX" },
        { id:"presence", type:"boost", on:false,freq:3000, q:1.0, gain:+3,  threshold:-30, attack:0.020, release:0.200, name:"PRESENCE" },
        { id:"harsh",    type:"cut",   on:true, freq:3200, q:1.8, gain:-3,  threshold:-12, attack:0.003, release:0.080, name:"HARSH" },
        { id:"deess",    type:"cut",   on:true, freq:7000, q:2.5, gain:-6,  threshold:-14, attack:0.001, release:0.060, name:"DE-ESS" },
        { id:"air",      type:"boost", on:false,freq:12000,q:0.7, gain:+3,  threshold:-32, attack:0.030, release:0.250, name:"AIR" },
      ];
      upd("tq", { mode: tq.mode || "dyneq", bands: seedBands });
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // ── Local panel state ──
  // Selected band — which one's detail is shown. Initialized lazily from the
  // bands array on first render. Subsequent changes to bands DON'T fight the
  // user's selection (e.g. applying a preset keeps the current selection).
  // If selectedBandId ever points to a non-existent band, the detail render
  // falls back to bands[0] (no state mutation needed).
  const [selectedBandId, setSelectedBandId] = React.useState(function(){
    const firstOn = (tq.bands || []).find(function(b){ return b.on; });
    return firstOn ? firstOn.id : ((tq.bands && tq.bands[0]) ? tq.bands[0].id : "plosive");
  });

  // ── LISTEN state ──
  // When set, the TQ chain inserts a bandpass filter at the chosen band's
  // frequency, so the user hears ONLY that band's slice of audio (with its
  // dynamic processing applied). All other frequencies are silenced.
  // Stored in fx.tq.listenBandId so applyFxLive can configure the live
  // listen filter without a chain rebuild (listen filter is always built,
  // just configured transparent when listenBandId is null).
  const listenBandId = tq.listenBandId || null;
  const setListenBandId = function(bandId) {
    upd("tq", { listenBandId: bandId });
  };

  // Clear LISTEN when the user closes/disables the plugin. Listen filter is
  // ephemeral — saving a project with listen still active would be confusing
  // ("why is my vocal band-passed?"). Better to always start fresh.
  React.useEffect(function(){
    if (!on && listenBandId) setListenBandId(null);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [on]);

  // Helper: update a single band's properties without disturbing others.
  // Most band-knob changes flow through here.
  const updBand = function(bandId, patch) {
    const newBands = bands.map(function(b){
      if (b.id !== bandId) return b;
      return Object.assign({}, b, patch);
    });
    upd("tq", { bands: newBands });
  };

  // Selected band data
  const selBand = bands.find(function(b){ return b.id === selectedBandId; }) || bands[0];
  const selMeta = selBand ? TQ_BAND_META[selBand.id] : null;

  return (
    <div style={{
      background:"linear-gradient(180deg,#1a0f2e 0%,#0e0820 100%)",
      borderRadius:16, overflow:"hidden",
      border:"2px solid " + (on ? "#A855F7" : "#2a2a2a"),
      boxShadow: on ? "0 0 24px rgba(168,85,247,0.18), inset 0 1px 0 rgba(255,255,255,0.06)" : "inset 0 1px 0 rgba(255,255,255,0.03)",
    }}>
      {/* ── Header ── */}
      <div style={{
        background:"linear-gradient(180deg,#1f1340 0%,#150a30 100%)",
        padding:"8px 14px", borderBottom:"1px solid #2a1a4a",
        display:"flex", alignItems:"center", gap:8,
      }}>
        <div style={{ flex:1 }}>
          <div style={{ color:"#C4B5FD", fontWeight:900, fontSize:12, letterSpacing:3, fontFamily:"monospace", lineHeight:1 }}>TQ</div>
          <div style={{ color:"#4c2d80", fontSize:7, letterSpacing:2, fontFamily:"monospace", marginTop:2 }}>VOCAL DYNAMIC · 7-BAND</div>
        </div>
        <div style={{ display:"flex", alignItems:"center", gap:8 }}>
          <div style={{ width:8, height:8, borderRadius:"50%", background: on ? "#A855F7" : "#1a1a1a", boxShadow: on ? "0 0 6px #A855F7, 0 0 12px rgba(168,85,247,0.5)" : "none", transition:"all 0.2s" }} />
          <button onClick={function(){ upd("tq",{on:!on}); }}
            style={{ background: on ? "linear-gradient(180deg,#9333EA,#7E22CE)" : "linear-gradient(180deg,#2a2a2a,#222)", border:"1px solid " + (on ? "#A855F7" : "#333"), borderRadius:5, color:"white", fontSize:9, fontWeight:800, padding:"4px 12px", cursor:"pointer", letterSpacing:1, boxShadow: on ? "0 1px 0 rgba(255,255,255,0.1) inset" : "0 1px 3px rgba(0,0,0,0.5)" }}>
            {on ? "ON" : "OFF"}
          </button>
        </div>
      </div>

      <div style={{ padding:"12px 12px 14px", opacity: on ? 1 : 0.4, transition:"opacity 0.2s" }}>
        {/* ── Frequency response visualizer ──
            Canvas showing the cumulative EQ curve PLUS live dot markers at
            each band's frequency. Dots move vertically as detectors trigger,
            showing the live dynamic behavior. The whole curve fills/un-fills
            in real time. ~80px tall — enough to read on a phone. */}
        <div style={{ marginBottom:10 }}>
          <TQFreqCurve
            trackId={trackId}
            bands={bands}
            listenBandId={listenBandId}
            selectedBandId={selectedBandId}
            width={340}
            height={88} />
        </div>
        {/* ── Preset chips row ──
            Five quick-tune chips that replace the bands array entirely with
            curated values for that vocal style. Applying a preset wipes any
            user adjustments — there's no merge. The "PRESET" label on the
            left makes the row scannable; chips themselves are compact. */}
        <div style={{ display:"flex", alignItems:"center", gap:6, marginBottom:10, overflowX:"auto", paddingBottom:2 }}>
          <div style={{ color:"#4c2d80", fontSize:8, fontWeight:800, letterSpacing:1.5, fontFamily:"monospace", flexShrink:0 }}>PRESET</div>
          {Object.keys(TQ_PRESETS).map(function(key){
            const p = TQ_PRESETS[key];
            return (
              <button key={key}
                onClick={function(){
                  // Clear LISTEN first — applying a preset on top of LISTEN
                  // would be confusing because the listen filter would keep
                  // band-passing audio with the new preset's values.
                  if (listenBandId) setListenBandId(null);
                  // Deep-clone the preset bands so subsequent edits don't
                  // mutate TQ_PRESETS' shared state.
                  const cloned = p.bands.map(function(b){ return Object.assign({}, b); });
                  upd("tq", { bands: cloned });
                }}
                title={p.desc}
                style={{
                  flexShrink:0,
                  background:"rgba(168,85,247,0.08)",
                  border:"1px solid rgba(168,85,247,0.3)",
                  borderRadius:6,
                  color:"#C4B5FD",
                  fontSize:9, fontWeight:800, letterSpacing:0.8,
                  padding:"5px 10px",
                  cursor:"pointer",
                  fontFamily:"monospace",
                  whiteSpace:"nowrap",
                }}>
                {p.label}
              </button>
            );
          })}
        </div>
        {/* ── Band tiles row — 7 mini cards ── */}
        <div style={{ display:"flex", gap:6, marginBottom:14 }}>
          {bands.map(function(b){
            const meta = TQ_BAND_META[b.id] || { code:"?", color:"#888" };
            const isSelected = b.id === selectedBandId;
            const isListening = b.id === listenBandId;
            // Active = band is on AND not muted by LISTEN. When LISTEN is
            // engaged on band X, all OTHER bands' audio is band-passed away
            // by the listen filter, so they're effectively inaudible —
            // visually dim them to communicate this.
            const isActive   = b.on && (!listenBandId || isListening);
            return (
              <button key={b.id}
                onClick={function(){
                  setSelectedBandId(b.id);
                }}
                style={{
                  flex:1, minWidth:0,
                  background: isSelected
                    ? "linear-gradient(180deg," + meta.color + "33," + meta.color + "11)"
                    : "rgba(0,0,0,0.3)",
                  border:"1px solid " + (isSelected ? meta.color : (b.on ? meta.color + "44" : "#1f1230")),
                  borderRadius:7,
                  padding:"6px 4px 5px",
                  cursor: "pointer",
                  // Dim non-listened bands during LISTEN mode (they're inaudible)
                  opacity: (listenBandId && !isListening) ? 0.4 : 1,
                  display:"flex", flexDirection:"column", alignItems:"center", gap:4,
                  position:"relative",
                  transition:"all 0.15s",
                  boxShadow: isSelected ? "0 0 12px " + meta.color + "33" : "none",
                }}>
                {/* On/off LED top-right corner */}
                <div style={{ position:"absolute", top:3, right:3, width:4, height:4, borderRadius:"50%",
                  background: isActive ? meta.color : "#2a1a4a",
                  boxShadow: isActive ? "0 0 4px " + meta.color : "none",
                }} />
                {/* Listen indicator top-left — cyan "L" when listening to this band */}
                {isListening && (
                  <div style={{ position:"absolute", top:2, left:3, fontSize:7, fontWeight:900, color:"#22D3EE", letterSpacing:0.5 }}>L</div>
                )}
                {/* Band 2-letter code */}
                <div style={{
                  color: isActive ? meta.color : "#3a2a55",
                  fontSize:10, fontWeight:900, fontFamily:"monospace", letterSpacing:0.5, lineHeight:1,
                }}>{meta.code}</div>
                {/* Mini GR meter */}
                <TQBandGRMeter trackId={trackId} bandId={b.id} color={meta.color} height={20} />
              </button>
            );
          })}
        </div>

        {/* ── Selected band detail ── */}
        {selBand && selMeta && (
          <div style={{
            background:"rgba(0,0,0,0.25)",
            border:"1px solid " + selMeta.color + "33",
            borderRadius:10, padding:"10px 10px 12px",
          }}>
            {/* Detail header: band name + type toggle + bypass + solo */}
            <div style={{ display:"flex", alignItems:"center", justifyContent:"space-between", marginBottom:8, gap:8 }}>
              <div style={{ flex:1, minWidth:0 }}>
                <div style={{ color:selMeta.color, fontSize:11, fontWeight:900, letterSpacing:2, fontFamily:"monospace", lineHeight:1 }}>{selBand.name}</div>
                <div style={{ color:"#4a3568", fontSize:8, marginTop:2, letterSpacing:0.3, lineHeight:1.3 }}>{selMeta.desc}</div>
              </div>
              <div style={{ display:"flex", gap:4, flexShrink:0 }}>
                {/* Cut/Boost type toggle */}
                <div style={{ display:"flex", gap:0, background:"#0a0418", border:"1px solid " + selMeta.color + "33", borderRadius:5, padding:1 }}>
                  <button onClick={function(){ updBand(selBand.id, {type:"cut", gain: selBand.gain > 0 ? -3 : selBand.gain }); }}
                    style={{ background: selBand.type === "cut" ? selMeta.color : "transparent", border:"none", borderRadius:3, color: selBand.type === "cut" ? "white" : "#4a3568", fontSize:8, fontWeight:800, padding:"3px 7px", cursor:"pointer", letterSpacing:0.5 }}>CUT</button>
                  <button onClick={function(){ updBand(selBand.id, {type:"boost", gain: selBand.gain < 0 ? +3 : selBand.gain }); }}
                    style={{ background: selBand.type === "boost" ? selMeta.color : "transparent", border:"none", borderRadius:3, color: selBand.type === "boost" ? "white" : "#4a3568", fontSize:8, fontWeight:800, padding:"3px 7px", cursor:"pointer", letterSpacing:0.5 }}>BOOST</button>
                </div>
                {/* Per-band on/off (BYP = bypassed, dim) */}
                <button onClick={function(){ updBand(selBand.id, {on: !selBand.on}); }}
                  style={{
                    background: selBand.on ? "rgba(255,255,255,0.04)" : "rgba(0,0,0,0.4)",
                    border:"1px solid " + (selBand.on ? selMeta.color + "55" : "#2a1a4a"),
                    borderRadius:5, color: selBand.on ? selMeta.color : "#4a3568",
                    fontSize:8, fontWeight:800, padding:"3px 7px", cursor:"pointer", letterSpacing:0.5,
                  }}>{selBand.on ? "ACTIVE" : "BYP"}</button>
                {/* Listen — band-passes the audio to ONLY this band's
                    frequency range so the user can hear what this band is
                    catching. Cyan because it's an analytical/scope tool, not
                    a mute toggle. Toggles off when tapped again. */}
                <button onClick={function(){
                    setListenBandId(listenBandId === selBand.id ? null : selBand.id);
                  }}
                  style={{
                    background: listenBandId === selBand.id ? "linear-gradient(180deg,#22D3EE,#0891B2)" : "rgba(0,0,0,0.4)",
                    border:"1px solid " + (listenBandId === selBand.id ? "#22D3EE" : "#2a1a4a"),
                    borderRadius:5, color: listenBandId === selBand.id ? "#0a0418" : "#4a3568",
                    fontSize:8, fontWeight:800, padding:"3px 7px", cursor:"pointer", letterSpacing:0.5,
                  }}>LISTEN</button>
              </div>
            </div>

            {/* Knobs row 1: FREQ, Q, THRESH, GAIN */}
            <div style={{ display:"flex", justifyContent:"space-around", overflow:"visible", marginBottom:4 }}>
              <Knob label="FREQ"
                value={Math.round(selBand.freq)}
                min={selBand.id === "plosive" ? 40 : (selBand.id === "air" ? 8000 : 100)}
                max={selBand.id === "plosive" ? 200 : (selBand.id === "air" ? 18000 : (selBand.id === "deess" ? 12000 : 8000))}
                step={selBand.freq < 1000 ? 5 : 50}
                unit="Hz" color={selMeta.color}
                onChange={function(v){ updBand(selBand.id, {freq:v}); }} />
              <Knob label="Q"
                value={selBand.q}
                min={0.4} max={6.0} step={0.1}
                unit="" color={selMeta.color}
                onChange={function(v){ updBand(selBand.id, {q:v}); }} />
              <Knob label="THRESH"
                value={selBand.threshold}
                min={-60} max={0} step={1}
                unit="dB" color={selMeta.color}
                onChange={function(v){ updBand(selBand.id, {threshold:v}); }} />
              <Knob label={selBand.type === "boost" ? "BOOST" : "CUT"}
                value={selBand.gain}
                min={selBand.type === "boost" ? 0 : -12}
                max={selBand.type === "boost" ? 12 : 0}
                step={0.5}
                unit="dB" color={selMeta.color}
                onChange={function(v){ updBand(selBand.id, {gain:v}); }} />
            </div>
            <div style={{ height:1, background:selMeta.color + "22", borderRadius:1, marginTop:6, marginBottom:6 }} />
            {/* Knobs row 2: ATTACK, RELEASE */}
            <div style={{ display:"flex", justifyContent:"space-around", overflow:"visible" }}>
              <Knob label="ATTACK"
                value={Math.round(selBand.attack * 1000)}
                min={0.5} max={50} step={0.5}
                unit="ms" color={selMeta.color}
                onChange={function(v){ updBand(selBand.id, {attack:v/1000}); }} />
              <Knob label="RELEASE"
                value={Math.round(selBand.release * 1000)}
                min={20} max={500} step={5}
                unit="ms" color={selMeta.color}
                onChange={function(v){ updBand(selBand.id, {release:v/1000}); }} />
            </div>
          </div>
        )}
      </div>
    </div>
  );
}
const _TQPlugin = React.memo(_TQPluginInner, function(prev, next) {
  return prev.fx.tq === next.fx.tq && prev.upd === next.upd && prev.trackId === next.trackId;
});

// This is the correct way to prevent re-renders from the 30fps currentTime
// here because this is a real component, not an IIFE or callback.
// Re-renders ONLY when fx data, fxTrackId, trackName, or trackColor change.
// =============================================================================
const FxPanel = React.memo(function FxPanel({ fx, fxTrackId, trackName, trackColor, onClose, onUpd, onApplyPreset, analyserNode, isPlaying, isRecording, onTogglePlay, onRecord, onSeekBack, onSeekForward, presets, isAdmin, onSavePreset, onDeletePreset }) {
  try { if (typeof window !== "undefined" && window.__bfDebugLog && fx && fx.eq) window.__bfDebugLog("[FX-RENDER] eq.midFreq=" + fx.eq.midFreq + " eq.mid=" + fx.eq.mid); } catch(_){}
  // ── Undo/Redo history ───────────────────────────────────────────────
  // Stacks of fx snapshots. Wrap onUpd to push the CURRENT fx onto the
  // undo stack before applying the change. A 300ms cooldown prevents
  // continuous knob/slider drags from flooding the stack — only one
  // snapshot per gesture. Stack capped at 50.
  const historyRef = React.useRef({ undo: [], redo: [] });
  const lastSnapTsRef = React.useRef(0);
  const fxRef = React.useRef(fx);
  React.useEffect(function(){ fxRef.current = fx; }, [fx]);
  // Force re-render whenever stack lengths change (so button enabled/
  // disabled states reflect reality).
  const [historyTick, setHistoryTick] = React.useState(0);
  const bumpHistory = function(){ setHistoryTick(function(n){ return n + 1; }); };
  const upd = React.useCallback(function(section, patch, rawValue){
    // Don't snapshot when we're applying an undo/redo restore.
    if (section !== "__restore") {
      const now = Date.now();
      if (now - lastSnapTsRef.current > 300) {
        try {
          historyRef.current.undo.push(JSON.parse(JSON.stringify(fxRef.current || {})));
          if (historyRef.current.undo.length > 50) historyRef.current.undo.shift();
          // Any new user action wipes the redo stack.
          if (historyRef.current.redo.length > 0) historyRef.current.redo = [];
          bumpHistory();
        } catch(_){}
      }
      lastSnapTsRef.current = now;
    }
    onUpd(section, patch, rawValue);
  }, [onUpd]);
  const doUndo = function(){
    if (historyRef.current.undo.length === 0) return;
    const prev = historyRef.current.undo.pop();
    try {
      historyRef.current.redo.push(JSON.parse(JSON.stringify(fxRef.current || {})));
      if (historyRef.current.redo.length > 50) historyRef.current.redo.shift();
    } catch(_){}
    // Bypass cooldown so the restore doesn't push itself to the stack.
    lastSnapTsRef.current = Date.now();
    onUpd("__restore", null, prev);
    bumpHistory();
  };
  const doRedo = function(){
    if (historyRef.current.redo.length === 0) return;
    const next = historyRef.current.redo.pop();
    try {
      historyRef.current.undo.push(JSON.parse(JSON.stringify(fxRef.current || {})));
      if (historyRef.current.undo.length > 50) historyRef.current.undo.shift();
    } catch(_){}
    lastSnapTsRef.current = Date.now();
    onUpd("__restore", null, next);
    bumpHistory();
  };
  const canUndo = historyRef.current.undo.length > 0;
  const canRedo = historyRef.current.redo.length > 0;
  const [presetMenuOpen, setPresetMenuOpen] = React.useState(false);
  const [fxAllCollapsed, setFxAllCollapsed] = React.useState(false);
  const presetButtonRef = React.useRef(null);
  const [presetMenuTop, setPresetMenuTop] = React.useState(60);

  // ── Rotary Knob ──
  const Knob = function({ label, value, min, max, step, unit, onChange, color, scale }) {
    const startRef = useRef(null);
    color = color || "#8B5CF6";
    const isLog = scale === "log" && min > 0 && max > min;
    // Normalize value <-> [0,1] using log or linear mapping.
    const valToNorm = function(v){
      if (isLog) return Math.log(Math.max(min, Math.min(max, v)) / min) / Math.log(max / min);
      return (Math.max(min, Math.min(max, v)) - min) / (max - min);
    };
    const normToVal = function(n){
      const c = Math.max(0, Math.min(1, n));
      if (isLog) return min * Math.pow(max / min, c);
      return min + c * (max - min);
    };
    const norm  = valToNorm(value);
    const angle = -140 + norm * 280;
    const SW = 4, r = 20, PAD = SW / 2 + 2;
    const cx = r + PAD, cy = r + PAD, SIZE = (r + PAD) * 2;
    const toXY = function(deg) { const rad = (deg - 90) * Math.PI / 180; return { x: cx + r * Math.cos(rad), y: cy + r * Math.sin(rad) }; };
    const startA = toXY(-140), endA = toXY(angle);
    const sweptDeg = angle - (-140), largeArc = sweptDeg > 180 ? 1 : 0;
    const arcD = `M ${startA.x.toFixed(2)} ${startA.y.toFixed(2)} A ${r} ${r} 0 ${largeArc} 1 ${endA.x.toFixed(2)} ${endA.y.toFixed(2)}`;
    const onPointerDown = function(e) {
      e.preventDefault();
      startRef.current = { y: e.clientY, norm: valToNorm(value) };
      // Log knobs: full range over 200px of drag (less sensitive, since
      // 200 pixels covers 3+ decades of frequency or 2+ decades of Q).
      // Linear knobs: full range over 100px of drag (existing behaviour).
      const pixelsPerFullRange = isLog ? 200 : 100;
      const onMove = function(me) {
        const dy = startRef.current.y - me.clientY;
        const newNorm = startRef.current.norm + dy / pixelsPerFullRange;
        const raw = normToVal(newNorm);
        const clamped = Math.min(max, Math.max(min, raw));
        const snapped = step ? Math.round(clamped / step) * step : clamped;
        onChange(+snapped.toFixed(3));
      };
      const onUp = function() { document.removeEventListener("pointermove", onMove); document.removeEventListener("pointerup", onUp); };
      document.addEventListener("pointermove", onMove);
      document.addEventListener("pointerup", onUp);
    };
    const display = unit === "dB" ? (value >= 0 ? "+" : "") + value + "dB"
                  : unit === "%" ? Math.round(value * 100) + "%"
                  : unit === "ms" ? value + "ms"
                  : unit === ":1" ? value + ":1"
                  : unit === "Q"  ? value
                  : unit === " st" ? (value >= 0 ? "+" : "") + value + " st"
                  : unit === "Hz" ? (value >= 1000 ? (value/1000).toFixed(value >= 10000 ? 1 : 2) + "k" : Math.round(value)) + (value >= 1000 ? "" : "Hz")
                  : value + (unit || "");
    return (
      <div style={{ display:"flex", flexDirection:"column", alignItems:"center", gap:2, userSelect:"none" }}>
        <svg width={SIZE} height={SIZE} viewBox={`0 0 ${SIZE} ${SIZE}`} data-knob="1" style={{ overflow:"visible", cursor:"ns-resize", touchAction:"none" }} onPointerDown={onPointerDown}>
          <circle cx={cx} cy={cy} r={r} fill="none" stroke="#1e1e1e" strokeWidth={SW} strokeLinecap="round"
            strokeDasharray={`${2*Math.PI*r * 280/360} ${2*Math.PI*r}`}
            strokeDashoffset={`${2*Math.PI*r * (90+140)/360}`}
            transform={`rotate(-90 ${cx} ${cy})`} />
          {norm > 0 && <path d={arcD} fill="none" stroke={color} strokeWidth={SW} strokeLinecap="round" />}
          <circle cx={endA.x} cy={endA.y} r={SW * 0.9} fill={color} />
          <circle cx={cx} cy={cy} r={r * 0.42} fill="#0d0d0d" stroke="#2a2a2a" strokeWidth={1.5} />
        </svg>
        <div style={{ color:"white", fontSize:9, fontWeight:700, lineHeight:1 }}>{display}</div>
        <div style={{ color:"#444", fontSize:7, textAlign:"center", lineHeight:1.2 }}>{label}</div>
      </div>
    );
  };

  // ── EQ Graph ──
  const EQ_SR = 44100;
  const eqCalcCoeffs = function(type, freq, gainDB, Q) {
    const f=Math.max(10,Math.min(freq,EQ_SR*0.499)), w0=2*Math.PI*f/EQ_SR, cw=Math.cos(w0), sw=Math.sin(w0);
    const A=Math.pow(10,gainDB/40), aq=sw/(2*Math.max(0.001,Q));
    let b0,b1,b2,a0,a1,a2;
    if(type==="peaking"){b0=1+aq*A;b1=-2*cw;b2=1-aq*A;a0=1+aq/A;a1=-2*cw;a2=1-aq/A;}
    else if(type==="lowshelf"){const sa=2*Math.sqrt(A)*aq;b0=A*((A+1)-(A-1)*cw+sa);b1=2*A*((A-1)-(A+1)*cw);b2=A*((A+1)-(A-1)*cw-sa);a0=(A+1)+(A-1)*cw+sa;a1=-2*((A-1)+(A+1)*cw);a2=(A+1)+(A-1)*cw-sa;}
    else if(type==="highshelf"){const sa=2*Math.sqrt(A)*aq;b0=A*((A+1)+(A-1)*cw+sa);b1=-2*A*((A-1)+(A+1)*cw);b2=A*((A+1)+(A-1)*cw-sa);a0=(A+1)-(A-1)*cw+sa;a1=2*((A-1)-(A+1)*cw);a2=(A+1)+(A-1)*cw-sa;}
    else if(type==="highpass"){b0=(1+cw)/2;b1=-(1+cw);b2=(1+cw)/2;a0=1+aq;a1=-2*cw;a2=1-aq;}
    else if(type==="notch"){b0=1;b1=-2*cw;b2=1;a0=1+aq;a1=-2*cw;a2=1-aq;}
    else{b0=(1-cw)/2;b1=1-cw;b2=(1-cw)/2;a0=1+aq;a1=-2*cw;a2=1-aq;}
    if(Math.abs(a0)<1e-30) return {b0:1,b1:0,b2:0,a1:0,a2:0};
    return {b0:b0/a0,b1:b1/a0,b2:b2/a0,a1:a1/a0,a2:a2/a0};
  };
  const eqEvalMag = function(c,f){
    const w=2*Math.PI*Math.max(1,f)/EQ_SR, cw=Math.cos(w), sw=Math.sin(w), cw2=Math.cos(2*w), sw2=Math.sin(2*w);
    const bRe=c.b0+c.b1*cw+c.b2*cw2, bIm=c.b1*sw+c.b2*sw2, aRe=1+c.a1*cw+c.a2*cw2, aIm=c.a1*sw+c.a2*sw2;
    const den=aRe*aRe+aIm*aIm; if(den<1e-30) return 0;
    return 20*Math.log10(Math.sqrt((bRe*bRe+bIm*bIm)/den));
  };
  const EQGraph = function EQGraph({ eq, onDrag, analyserNode, isPlaying }) {
    try { if (typeof window !== "undefined" && window.__bfDebugLog && eq) window.__bfDebugLog("[EQ-RENDER] midFreq=" + eq.midFreq + " mid=" + eq.mid); } catch(_){}
    // ── Pro Q3-style EQ display ──────────────────────────────────────────────
    // Full-width canvas, 20Hz–20kHz log scale, ±24dB range, vivid curve fill,
    // per-band Q-width rings on handles, live frequency/gain readout tooltip.
    const W = 320, H = 160;
    const DB_MAX = 24;   // ±24 dB range per spec (matches FabFilter Pro-Q default view)
    const PAD_L = 28;    // left margin for dB labels
    const PAD_B = 16;    // bottom margin for freq labels
    const PW = W - PAD_L, PH = H - PAD_B;
    // Build the 5-band array from the shared config + state. Every band is
    // type-changeable now; all bands drag in XY.
    const bands = EQ_BAND_CONFIGS.map(function(_cfg, idx){
      const r = eqResolveBand(eq || {}, idx);
      return {
        key:     r.key,
        bandNum: r.bandNum,
        type:    r.type,
        freq:    r.freq,
        gain:    r.gain,
        q:       r.q,
        enabled: r.enabled,
        color:   r.color,
        drag:    "xy",
        label:   EQ_TYPE_LABEL[r.type] || r.type.toUpperCase(),
      };
    });
    const LOG_RANGE = Math.log10(20000/20);
    // Coordinate helpers — all values live in the padded plot area
    const freqToX = function(f){ return PAD_L + PW*Math.log10(Math.max(20,f)/20)/LOG_RANGE; };
    const xToFreq = function(px){ return Math.round(20*Math.pow(20000/20,Math.max(0,Math.min(1,(px-PAD_L)/PW)))); };
    const gainToY = function(g){ return (PH/2)-(Math.max(-DB_MAX,Math.min(DB_MAX,g))/DB_MAX)*(PH/2-6); };
    const yToGain = function(py){ return +((PH/2-py)/(PH/2-6)*DB_MAX).toFixed(2); };
    // Build the combined frequency response curve. Resolution at 40
    // points across 320px = 8px/point — Bezier smoothing makes it
    // visually identical to higher-resolution paths. Memoized on band
    // values so audio analyser ticks (which re-render this component
    // every ~1-2s) don't trigger the biquad math when nothing has
    // changed. This is critical for keeping iOS responsive on touch
    // events that arrive immediately after a release.
    const nPts = 100;
    // Solo is an AUDIO-only feature — the curve shows the full EQ shape
    // regardless of solo state so the user keeps their visual reference.
    // The audio chain (applyFxLive) still swaps non-soloed bands to allpass.
    // soloKey is retained ONLY to render a pulsing ring on the soloed
    // band marker — no other visual changes (markers stay at full opacity).
    const soloKey = (eq && eq.soloBand) || null;
    const curveKey = bands.map(function(b){
      return b.type + ":" + b.freq + ":" + b.gain + ":" + b.q + ":" + (b.enabled ? "1" : "0");
    }).join("|");
    const { path, fill } = React.useMemo(function(){
      const pts = [];
      for (let i = 0; i <= nPts; i++) {
        const f = 20 * Math.pow(20000/20, i/nPts);
        let db = 0;
        // Full EQ shape — sum of all enabled bands. Identical whether or
        // not solo is active.
        for (let bi = 0; bi < bands.length; bi++) {
          const b = bands[bi];
          if (!b.enabled) continue;
          db += eqEvalMag(eqCalcCoeffs(b.type, b.freq, b.gain, b.q), f);
        }
        pts.push([freqToX(f), gainToY(Math.max(-DB_MAX-2, Math.min(DB_MAX+2, db)))]);
      }
      // Straight line segments at high resolution — pixel-smooth visual,
      // no Bezier overshoot at high-Q peaks. This is how pro EQs (FabFilter,
      // iZotope, etc.) render — high density + lines = perfectly accurate.
      let path = "M " + pts[0][0].toFixed(1) + " " + pts[0][1].toFixed(1);
      for (let i = 1; i < pts.length; i++) {
        path += " L " + pts[i][0].toFixed(1) + " " + pts[i][1].toFixed(1);
      }
      const zeroY = gainToY(0);
      const fill = path + " L " + W + " " + zeroY + " L " + PAD_L + " " + zeroY + " Z";
      return { path, fill };
    // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [curveKey]);
    const zeroY = gainToY(0);
    const dragging=useRef(null), svgRef=useRef(null), [hovered,setHovered]=useState(null);
    // Tooltip state: {x,y,freq,gain}
    const [tip,setTip]=useState(null);
    // Q ring radius: maps Q (0.1–10) to a visual bandwidth arc in pixels
    const qToRingR = function(q,freq){ const bwHz=(freq/Math.max(0.01,q)); return Math.max(10,Math.min(40,PW*(Math.log10((freq+bwHz/2)/Math.max(20,freq-bwHz/2))/LOG_RANGE)*0.6)); };
    const FREQ_MARKS = [20,50,100,200,500,1000,2000,5000,10000,20000];
    const DB_MARKS   = [-18,-12,-6,0,6,12,18];
    // ──────────────────────────────────────────────────────────────────
    // Real-time FFT Spectrum Analyzer (Phase 2)
    // ──────────────────────────────────────────────────────────────────
    // Reads frequency data from the track's AnalyserNode and renders a
    // log-frequency spectrum behind the EQ curve. Direct DOM via
    // setAttribute on a single <path> element — no React re-renders,
    // no virtual DOM diffing. The spectrum follows the same x-axis
    // mapping as the EQ (20Hz to 20kHz log) and is amplitude-mapped
    // into the graph's y-space.
    //
    // Performance:
    //  - getByteFrequencyData: ~50μs (just memcpy)
    //  - 60-bucket log-aggregation: ~200μs
    //  - Path string + 1 setAttribute: ~100μs
    //  Total ~0.4ms per RAF = ~24ms/sec of CPU at 60Hz. Negligible.
    //
    // Pauses when isPlaying is false or analyserNode is null. iOS touch
    // events stay responsive because this loop never touches React
    // state, never invalidates memos, never triggers re-renders.
    React.useEffect(function(){
      if (!analyserNode || !isPlaying) return;
      // Configure the analyser. Smoothing helps the visual look natural.
      try {
        analyserNode.fftSize = 2048;
        analyserNode.smoothingTimeConstant = 0.75;
        analyserNode.minDecibels = -90;
        analyserNode.maxDecibels = -10;
      } catch(_){}
      const binCount = analyserNode.frequencyBinCount; // 1024 with fftSize=2048
      const freqData = new Uint8Array(binCount);
      const sampleRate = (analyserNode.context && analyserNode.context.sampleRate) || 44100;
      const nyquist = sampleRate / 2;
      // Pre-compute the bin index range for each output bucket so we
      // don't recompute these every frame.
      const N_BUCKETS = 60;
      const bucketBinStart = new Int32Array(N_BUCKETS + 1);
      const bucketBinEnd   = new Int32Array(N_BUCKETS + 1);
      // Pre-compute per-bucket x coordinate too (no Math.pow / freqToX per frame).
      const bucketX = new Float32Array(N_BUCKETS + 1);
      for (let i = 0; i <= N_BUCKETS; i++) {
        const f1 = 20 * Math.pow(20000/20, i/N_BUCKETS);
        const f2 = 20 * Math.pow(20000/20, (i+1)/N_BUCKETS);
        bucketBinStart[i] = Math.max(0, Math.min(binCount - 1, Math.floor(f1 / nyquist * binCount)));
        bucketBinEnd[i]   = Math.max(bucketBinStart[i] + 1, Math.min(binCount, Math.ceil(f2 / nyquist * binCount)));
        bucketX[i] = freqToX(f1);
      }
      // Visual smoothing buffer (one-pole low-pass) to reduce jitter.
      const smoothed = new Float32Array(N_BUCKETS + 1);
      // Peak hold state — tracks max value per bucket with hold + decay.
      const peakValues     = new Float32Array(N_BUCKETS + 1);
      const peakHoldFrames = new Int32Array(N_BUCKETS + 1);
      const HOLD_FRAMES      = 30;    // ~500ms at 60fps before decay begins
      const DECAY_PER_FRAME  = 0.008; // ~1.6s for a peak to fall from 1.0 to 0
      let raf = 0;
      const PLOT_BOTTOM = H - PAD_B;
      const PLOT_TOP = 0;
      const PLOT_RANGE = PLOT_BOTTOM - PLOT_TOP;
      const tick = function(){
        const svgEl = svgRef.current;
        if (!svgEl) { raf = requestAnimationFrame(tick); return; }
        analyserNode.getByteFrequencyData(freqData);
        // Build BOTH the spectrum fill path AND the peak hold line path
        // in one pass. Two single setAttribute calls at the end keeps
        // DOM mutation count minimal.
        let pathStr = "";
        let peakStr = "";
        for (let i = 0; i <= N_BUCKETS; i++) {
          const start = bucketBinStart[i];
          const end = bucketBinEnd[i];
          let maxVal = 0;
          for (let bi = start; bi < end; bi++) {
            const v = freqData[bi];
            if (v > maxVal) maxVal = v;
          }
          // Normalize 0..255 to 0..1, apply visual smoothing.
          const norm = maxVal / 255;
          smoothed[i] = smoothed[i] * 0.6 + norm * 0.4;
          // Peak hold logic — if smoothed value rises above the held peak,
          // snap up & reset hold counter. Otherwise count down hold; once
          // expired, decay slowly.
          if (smoothed[i] > peakValues[i]) {
            peakValues[i] = smoothed[i];
            peakHoldFrames[i] = HOLD_FRAMES;
          } else if (peakHoldFrames[i] > 0) {
            peakHoldFrames[i]--;
          } else {
            peakValues[i] -= DECAY_PER_FRAME;
            if (peakValues[i] < 0) peakValues[i] = 0;
          }
          const x = bucketX[i];
          const y     = PLOT_BOTTOM - smoothed[i]     * PLOT_RANGE;
          const peakY = PLOT_BOTTOM - peakValues[i]   * PLOT_RANGE;
          if (i === 0) {
            pathStr = "M " + x.toFixed(1) + " " + PLOT_BOTTOM
              + " L " + x.toFixed(1) + " " + y.toFixed(1);
            peakStr = "M " + x.toFixed(1) + " " + peakY.toFixed(1);
          } else {
            pathStr += " L " + x.toFixed(1) + " " + y.toFixed(1);
            peakStr += " L " + x.toFixed(1) + " " + peakY.toFixed(1);
          }
        }
        // Close spectrum fill path at the bottom.
        const lastX = bucketX[N_BUCKETS];
        pathStr += " L " + lastX.toFixed(1) + " " + PLOT_BOTTOM + " Z";
        const spectrumEl = svgEl.querySelector('[data-eq-spectrum="1"]');
        if (spectrumEl) spectrumEl.setAttribute("d", pathStr);
        const peakEl = svgEl.querySelector('[data-eq-peakhold="1"]');
        if (peakEl) peakEl.setAttribute("d", peakStr);
        raf = requestAnimationFrame(tick);
      };
      raf = requestAnimationFrame(tick);
      return function(){
        if (raf) cancelAnimationFrame(raf);
        // Clear both paths so they don't display stale data on next mount.
        try {
          const svgEl = svgRef.current;
          if (svgEl) {
            const spectrumEl = svgEl.querySelector('[data-eq-spectrum="1"]');
            if (spectrumEl) spectrumEl.setAttribute("d", "");
            const peakEl = svgEl.querySelector('[data-eq-peakhold="1"]');
            if (peakEl) peakEl.setAttribute("d", "");
          }
        } catch(_){}
      };
    }, [analyserNode, isPlaying]);
    // Simple pointer-event band drag. Same pattern as the rotary knobs
    // (which work perfectly): pointerdown attaches pointermove/pointerup to
    // document, each move computes new freq/gain from pointer position and
    // commits state directly. React handles re-renders and curve recompute.
    // No direct DOM mutation, no RAF batching, no live-value tracking — just
    // state updates per move.
    const startBandDrag = function(b, e) {
      try { e.preventDefault(); } catch(_){}
      try { e.stopPropagation(); } catch(_){}
      const svgEl = svgRef.current;
      if (!svgEl) return;
      dragging.current = b;
      const rect = svgEl.getBoundingClientRect();
      // SVG is now stretched to fill the container width via viewBox +
      // preserveAspectRatio="none". Convert rendered px → viewBox px so
      // band drag math (which uses viewBox W=320, H=160) stays correct.
      const scaleX = rect.width > 0 ? (W / rect.width) : 1;
      const computeAndApply = function(clientX, clientY) {
        const px = (clientX - rect.left) * scaleX;
        const py = clientY - rect.top;
        const newFreq = Math.max(20, Math.min(20000, Math.round(xToFreq(px))));
        const newGain = Math.max(-DB_MAX, Math.min(DB_MAX, Math.round(yToGain(py) * 2) / 2));
        const patch = {};
        patch[b.key + "Freq"] = newFreq;
        patch[b.key] = newGain;
        onDrag(patch);
      };
      const onMove = function(me) { computeAndApply(me.clientX, me.clientY); };
      const onUp = function() {
        dragging.current = null;
        document.removeEventListener("pointermove",   onMove);
        document.removeEventListener("pointerup",     onUp);
        document.removeEventListener("pointercancel", onUp);
      };
      document.addEventListener("pointermove",   onMove);
      document.addEventListener("pointerup",     onUp);
      document.addEventListener("pointercancel", onUp);
    };
    return (
      <svg ref={svgRef} width="100%" height={H} viewBox={"0 0 " + W + " " + H} preserveAspectRatio="none"
        style={{display:"block",background:"#0b0d12",borderRadius:10,touchAction:"none",cursor:"crosshair",userSelect:"none"}}
        onTouchStart={function(e){
          try { if (typeof window !== "undefined" && window.__bfDebugLog) window.__bfDebugLog("[EQ_SVG] svg touchstart target=" + (e.target && e.target.tagName) + " touches=" + (e.touches ? e.touches.length : 0)); } catch(_){}
        }}>

        {/* ── Background gradient (Pro Q3 dark navy) ── */}
        <defs>
          <linearGradient id="eqBg" x1="0" y1="0" x2="0" y2="1">
            <stop offset="0%" stopColor="#0f1219"/>
            <stop offset="100%" stopColor="#080b0f"/>
          </linearGradient>
          <linearGradient id="curveFill" x1="0" y1="0" x2="0" y2="1">
            <stop offset="0%" stopColor="#38bdf8" stopOpacity="0.22"/>
            <stop offset="55%" stopColor="#818cf8" stopOpacity="0.10"/>
            <stop offset="100%" stopColor="#38bdf8" stopOpacity="0.02"/>
          </linearGradient>
          <filter id="eqGlow">
            <feGaussianBlur stdDeviation="1.5" result="blur"/>
            <feMerge><feMergeNode in="blur"/><feMergeNode in="SourceGraphic"/></feMerge>
          </filter>
        </defs>
        <rect x={0} y={0} width={W} height={H} fill="url(#eqBg)" rx={10}/>

        {/* ── Frequency grid lines ── */}
        {FREQ_MARKS.map(function(f){
          const x=freqToX(f);
          const major=[100,1000,10000].includes(f);
          return <line key={f} x1={x} y1={0} x2={x} y2={PH} stroke={major?"#1f2433":"#131720"} strokeWidth={major?1:0.75}/>;
        })}

        {/* ── dB grid lines ── */}
        {DB_MARKS.map(function(g){
          const y=gainToY(g);
          return (
            <g key={g}>
              <line x1={PAD_L} y1={y} x2={W} y2={y}
                stroke={g===0?"#2a3045":"#161b28"}
                strokeWidth={g===0?1.2:0.75}
                strokeDasharray={g===0?"none":"3 4"}/>
              <text x={PAD_L-3} y={y+3.5} fill={g===0?"#3d4a6a":"#2a3350"}
                fontSize={7} textAnchor="end" fontFamily="monospace">
                {g>0?"+":""}{g}
              </text>
            </g>
          );
        })}

        {/* ── Frequency labels ── */}
        {FREQ_MARKS.map(function(f){
          const x=freqToX(f);
          const label=f>=1000?(f/1000)+"k":f;
          return <text key={f} x={x} y={H-3} fill="#2a3350" fontSize={7} textAnchor="middle" fontFamily="monospace">{label}</text>;
        })}

        {/* ── FFT spectrum analyzer (Phase 2): renders BEHIND the curve.
            Updated via direct DOM setAttribute in a RAF loop inside
            useEffect — no React state, no re-renders. */}
        <path data-eq-spectrum="1" d="" fill="rgba(56,189,248,0.18)" stroke="rgba(56,189,248,0.35)" strokeWidth={1} pointerEvents="none"/>
        {/* ── Peak hold line (Phase 3): floats above the live spectrum,
            decays slowly so transient peaks remain visible. Updated by
            the same RAF tick — single setAttribute, no React. */}
        <path data-eq-peakhold="1" d="" fill="none" stroke="rgba(125,211,252,0.85)" strokeWidth={1} strokeLinejoin="round" strokeLinecap="round" pointerEvents="none"/>

        {/* ── Curve fill & stroke ── */}
        <path data-eq-fill="1" d={fill} fill="url(#curveFill)"/>
        <path data-eq-curve="1" d={path} fill="none" stroke="#38bdf8" strokeWidth={2} strokeLinecap="round" strokeLinejoin="round" filter="url(#eqGlow)"/>

        {/* ── Zero line ── */}
        <line x1={PAD_L} y1={zeroY} x2={W} y2={zeroY} stroke="#2a3045" strokeWidth={1}/>

        {/* ── Band handles: Q ring + center dot ── */}
        {bands.map(function(b){
          const hx=freqToX(b.freq), hy=gainToY(b.gain);
          if (b.key === "mid") {
            try { if (typeof window !== "undefined" && window.__bfDebugLog) window.__bfDebugLog("[EQ-MAP-MID] b.freq=" + b.freq + " hx=" + hx.toFixed(2) + " b.gain=" + b.gain + " hy=" + hy.toFixed(2)); } catch(_){}
          }
          const cur=b.drag==="x"?"ew-resize":b.drag==="y"?"ns-resize":"move";
          const rRing=qToRingR(b.q,b.freq);
          const isActive=hovered===b.key||dragging.current?.key===b.key;
          // Solo is purely audio — markers stay fully visible regardless
          // of solo state. The soloed band gets a pulsing amber ring to
          // indicate which one is being heard; no dimming on others.
          const isSoloed = soloKey === b.key;
          return (
            <g key={b.key} data-band-key={b.key} opacity={1}>
              {/* Vertical frequency guide */}
              <line x1={hx} y1={0} x2={hx} y2={PH}
                stroke={b.color+"30"} strokeWidth={isActive?1.5:1} strokeDasharray="3 4"/>
              {/* Q-width bandwidth ring — mimics Pro Q3's bell width arc */}
              <ellipse cx={hx} cy={hy} rx={rRing} ry={Math.min(rRing*0.55,20)}
                fill="none" stroke={b.color+"35"} strokeWidth={isActive?1.5:1}/>
              {/* Outer glow disc — also acts as a secondary touch target */}
              <circle cx={hx} cy={hy} r={isActive?17:13}
                fill={b.color+"18"} stroke={b.color+"22"} strokeWidth={1}
                style={{cursor:cur}}
                onPointerDown={function(e){ startBandDrag(b, e); }}/>
              {/* Solo ring — pulses around the soloed band */}
              {isSoloed && (
                <circle cx={hx} cy={hy} r={20} fill="none"
                  stroke="#fbbf24" strokeWidth={2} strokeDasharray="3 3"
                  pointerEvents="none">
                  <animate attributeName="r" values="18;24;18" dur="1.4s" repeatCount="indefinite"/>
                  <animate attributeName="opacity" values="0.9;0.4;0.9" dur="1.4s" repeatCount="indefinite"/>
                </circle>
              )}
              {/* Main handle dot */}
              <circle cx={hx} cy={hy} r={isActive?9:7}
                fill={b.color} fillOpacity={isActive?1:0.85}
                stroke="#0b0d12" strokeWidth={2}
                style={{cursor:cur,pointerEvents:"none"}}/>
              {/* Invisible large hit target — 24px radius (~48px diameter)
                  meets Apple HIG 44pt touch minimum. The visible handle
                  stays small for the pro-DAW look; this catches the touch
                  even if the finger isn't perfectly centred on the dot. */}
              <circle cx={hx} cy={hy} r={24}
                fill="transparent" stroke="none"
                style={{cursor:cur}}
                onPointerDown={function(e){ startBandDrag(b, e); }}
                onMouseEnter={function(){ if (!dragging.current) setHovered(b.key); }}
                onMouseLeave={function(){ if (!dragging.current) setHovered(null); }}/>
              {/* Band label inside handle */}
              <text x={hx} y={hy+3} fill="white" fontSize={isActive?7:6}
                textAnchor="middle" fontWeight="900" pointerEvents="none"
                fontFamily="monospace">{b.label}</text>
              {/* Live readout badge when active */}
              {isActive && (
                <g>
                  <rect x={hx-22} y={hy-28} width={44} height={14} rx={4}
                    fill="#0b0d12" stroke={b.color+"55"} strokeWidth={1}/>
                  <text x={hx} y={hy-18} fill={b.color} fontSize={7}
                    textAnchor="middle" fontFamily="monospace" fontWeight="700">
                    {b.freq>=1000?(b.freq/1000).toFixed(1)+"k":b.freq+"Hz"}
                    {b.gain!==0?" "+(b.gain>0?"+":"")+b.gain.toFixed(1)+"dB":""}
                  </text>
                </g>
              )}
            </g>
          );
        })}
        {/* Phase 8: Headline live readout — top-left of plot. React-rendered
            from hover/state; overridden by direct-DOM textContent updates
            during band drag for instant feedback without re-renders. */}
        <text data-eq-readout="1"
          x={PAD_L + 4} y={12}
          fill="#cbd5e1" fontSize={9.5} fontFamily="monospace" fontWeight="800"
          letterSpacing={0.8} pointerEvents="none">
          {(function(){
            const focusKey = (dragging.current && dragging.current.key) || hovered;
            if (focusKey) {
              const fb = bands.find(function(bb){ return bb.key === focusKey; });
              if (fb) {
                const freqStr = fb.freq >= 1000 ? (fb.freq/1000).toFixed(2) + "kHz" : Math.round(fb.freq) + "Hz";
                const gainStr = (fb.gain >= 0 ? "+" : "") + fb.gain.toFixed(1) + "dB";
                return "B" + fb.bandNum + " · " + freqStr + " · " + gainStr + " · Q" + fb.q.toFixed(1);
              }
            }
            return soloKey ? ("SOLO B" + (bands.find(function(bb){return bb.key===soloKey;})||{}).bandNum) : "DRAG BANDS TO EDIT";
          })()}
        </text>
      </svg>
    );
  };

  // ── Compressor graph ──
  const CompGraph = function({ threshold, ratio }) {
    const W=150, H=110, dBtoP=function(db){return(db+60)/60;}, pts=[];
    for(let i=0;i<=60;i++){const inDb=-60+i;const outDb=inDb<threshold?inDb:threshold+(inDb-threshold)/ratio;pts.push(`${W*dBtoP(inDb)},${H*(1-dBtoP(outDb))}`);}
    const thX=W*dBtoP(threshold);
    return (<svg width={W} height={H} style={{display:"block",background:"#080808",borderRadius:8,flexShrink:0}}><line x1={0} y1={H} x2={W} y2={0} stroke="#1e1e1e" strokeWidth={1} strokeDasharray="4 3"/><path d={"M "+pts.join(" L ")} fill="none" stroke="#8B5CF6" strokeWidth={2} strokeLinecap="round"/><path d={"M "+pts.join(" L ")+` L ${W} ${H} L 0 ${H} Z`} fill="rgba(139,92,246,0.1)"/><line x1={thX} y1={0} x2={thX} y2={H} stroke="#EF4444" strokeWidth={1} strokeDasharray="3 2"/><text x={thX+3} y={11} fill="#EF4444" fontSize={8}>{threshold}dB</text><text x={3} y={10} fill="#444" fontSize={7}>OUT</text><text x={W-22} y={H-3} fill="#444" fontSize={7}>IN</text></svg>);
  };

  // ── Reverb visualiser ──
  const ReverbViz = function({ wet, roomSize }) {
    const W=140, H=80, decay=roomSize*3;
    return (<svg width={W} height={H} style={{display:"block",background:"#080808",borderRadius:8,flexShrink:0}}><line x1={0} y1={H/2} x2={W} y2={H/2} stroke="#1a1a1a" strokeWidth={1}/>{Array.from({length:28},function(_,i){const x=(i/27)*W;const amp=Math.exp(-i/(decay*4))*(H/2-5)*wet;const j=(Math.sin(i*7.3)*0.4+0.6)*amp;return <line key={i} x1={x} y1={H/2-j} x2={x} y2={H/2+j} stroke={`rgba(192,38,211,${0.25+0.75*Math.exp(-i/5)})`} strokeWidth={2}/>;})}</svg>);
  };

  // ── EQ defaults — Pro Q3 style: tighter midQ, ±18 dB range ──
  const eq5 = { hpfFreq:80,hpfQ:0.707,lowFreq:200,low:0,lowQ:0.707,midFreq:1000,mid:0,midQ:1.41,highFreq:8000,high:0,highQ:0.707,lpfFreq:18000,lpfQ:0.707,...fx.eq };

  return (
    <div style={{
      position:"absolute", inset:0, zIndex:800, background:"rgba(0,0,0,0.97)",
      display:"flex", flexDirection:"column", overflow:"hidden",
      paddingTop:"var(--bf-safe-top, env(safe-area-inset-top))",
    }} onClick={function(e){ e.stopPropagation(); }}>
      {/* Sticky top header — track name, presets, done */}
      <div style={{ display:"flex", alignItems:"center", padding:"12px 16px", borderBottom:"1px solid #1e1e1e", background:"#0a0a0a", flexShrink:0, zIndex:10 }}>
        <div style={{ width:8, height:8, borderRadius:"50%", background:trackColor, marginRight:8 }} />
        <span style={{ color:"white", fontWeight:800, fontSize:14, flex:1, overflow:"hidden", textOverflow:"ellipsis", whiteSpace:"nowrap" }}>{trackName} — Effects</span>
        {/* Presets dropdown */}
        {onApplyPreset && (
          <div style={{ position:"relative", marginRight:8 }}>
            <button ref={presetButtonRef} onClick={function(e){
              e.stopPropagation();
              if (!presetMenuOpen && presetButtonRef.current) {
                const rect = presetButtonRef.current.getBoundingClientRect();
                setPresetMenuTop(rect.bottom + 4);
              }
              setPresetMenuOpen(function(v){return !v;});
            }}
              style={{
                background:"linear-gradient(180deg,#1e1b3a,#15132d)",
                border:"1px solid #4c3a7a", borderRadius:8,
                color:"#c4b5fd", fontSize:11, fontWeight:800, letterSpacing:0.5,
                padding:"5px 10px", cursor:"pointer",
                display:"flex", alignItems:"center", gap:5,
              }}>
              <svg width="11" height="11" viewBox="0 0 24 24" fill="none" stroke="#c4b5fd" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round">
                <polygon points="12 2 15.09 8.26 22 9.27 17 14.14 18.18 21.02 12 17.77 5.82 21.02 7 14.14 2 9.27 8.91 8.26 12 2"/>
              </svg>
              PRESETS
            </button>
            {presetMenuOpen && (
              <>
                <div onClick={function(){ setPresetMenuOpen(false); }}
                  style={{ position:"fixed", inset:0, zIndex:200 }} />
                <div style={{
                  position:"fixed", top:presetMenuTop, right:8, zIndex:201,
                  background:"#0f0f15", border:"1px solid #2a2a40",
                  borderRadius:10, boxShadow:"0 8px 24px rgba(0,0,0,0.7)",
                  minWidth:260, maxWidth:"calc(100vw - 16px)", overflow:"hidden",
                }}>
                  <div style={{ padding:"10px 14px", borderBottom:"1px solid #1f1f30", background:"#15132d" }}>
                    <div style={{ color:"#c4b5fd", fontSize:11, fontWeight:800, letterSpacing:1 }}>FX PRESETS</div>
                    <div style={{ color:"#6b6486", fontSize:10, marginTop:2 }}>Replaces all current FX</div>
                  </div>
                  {/* Admin: save current FX state as a new preset. */}
                  {isAdmin && onSavePreset && (
                    <button onClick={function(e){
                      e.stopPropagation();
                      setPresetMenuOpen(false);
                      onSavePreset();
                    }}
                      style={{
                        width:"100%",
                        background:"linear-gradient(180deg,#1b2e1b,#142214)",
                        border:"none", borderBottom:"1px solid #15152a",
                        color:"#86efac", padding:"10px 14px", textAlign:"left",
                        cursor:"pointer", display:"flex", alignItems:"center", gap:8,
                        fontSize:11, fontWeight:800, letterSpacing:0.5,
                      }}>
                      <span style={{ fontSize:14 }}>＋</span>
                      <span>Save current as preset</span>
                      <span style={{ marginLeft:"auto", color:"#3f6f3f", fontSize:9, fontWeight:700 }}>ADMIN</span>
                    </button>
                  )}
                  <div style={{ position:"relative" }}>
                  <div
                    ref={function(el){
                      if (!el || el._bfPresetListSetup) return;
                      el._bfPresetListSetup = true;
                      let active = false, startY = 0, startScroll = 0;
                      let lastY = 0, lastT = 0, vel = 0;
                      let raf = 0;
                      const cancel = function(){ if (raf) { cancelAnimationFrame(raf); raf = 0; } };
                      // "Scroll for more" hint visibility — set true when
                      // the list is scrollable and NOT at the bottom. The
                      // hint element is queried by data-attribute below.
                      const updateHint = function(){
                        try {
                          const root = el.parentElement;
                          if (!root) return;
                          const hint = root.querySelector('[data-bf-preset-hint]');
                          if (!hint) return;
                          const sh = el.scrollHeight, ch = el.clientHeight;
                          const sb = el.scrollTop;
                          const hasMore = sh > ch + 4 && (sh - sb - ch) > 8;
                          hint.style.opacity = hasMore ? "1" : "0";
                          hint.style.pointerEvents = hasMore ? "auto" : "none";
                        } catch(_){}
                      };
                      el.addEventListener("scroll", updateHint, { passive: true });
                      setTimeout(updateHint, 30);
                      setTimeout(updateHint, 200);
                      const onStart = function(e){
                        if (!e.touches || !e.touches[0]) return;
                        cancel();
                        active = true;
                        startY      = e.touches[0].clientY;
                        startScroll = el.scrollTop;
                        lastY       = startY;
                        lastT       = Date.now();
                        vel         = 0;
                      };
                      const onMove = function(e){
                        if (!active || !e.touches || !e.touches[0]) return;
                        try { e.preventDefault(); } catch(_){}
                        const y  = e.touches[0].clientY;
                        const dy = startY - y;
                        const sh = el.scrollHeight, ch = el.clientHeight;
                        el.scrollTop = Math.max(0, Math.min(sh - ch, startScroll + dy));
                        const now = Date.now();
                        const dt  = now - lastT;
                        if (dt > 0) vel = (lastY - y) / dt;
                        lastY = y; lastT = now;
                      };
                      const onEnd = function(){
                        if (!active) return;
                        active = false;
                        if (Math.abs(vel) > 0.05) {
                          const step = function(){
                            vel *= 0.94;
                            if (Math.abs(vel) < 0.02) { raf = 0; return; }
                            const sh = el.scrollHeight, ch = el.clientHeight;
                            el.scrollTop = Math.max(0, Math.min(sh - ch, el.scrollTop + vel * 16));
                            raf = requestAnimationFrame(step);
                          };
                          raf = requestAnimationFrame(step);
                        }
                      };
                      el.addEventListener("touchstart",  onStart, { passive: true });
                      el.addEventListener("touchmove",   onMove,  { passive: false });
                      el.addEventListener("touchend",    onEnd,   { passive: true });
                      el.addEventListener("touchcancel", onEnd,   { passive: true });
                    }}
                    style={{ maxHeight:360, overflowY:"auto", overscrollBehavior:"contain", touchAction:"pan-y", WebkitOverflowScrolling:"touch" }}>
                    {(presets && presets.length ? presets : FX_PRESETS).map(function(p){
                      const isUserPreset = p.kind === "user";
                      return (
                        <div key={p.id} style={{
                          display:"flex", alignItems:"stretch",
                          borderBottom:"1px solid #15152a",
                        }}>
                          <button onClick={function(e){
                            e.stopPropagation();
                            setPresetMenuOpen(false);
                            onApplyPreset(p);
                          }}
                            style={{
                              flex:1, background:"none", border:"none",
                              color:"#d0d0d0", padding:"10px 14px", textAlign:"left",
                              cursor:"pointer", display:"block", minWidth:0,
                            }}
                            onPointerEnter={function(e){ e.currentTarget.style.background="#181830"; }}
                            onPointerLeave={function(e){ e.currentTarget.style.background="transparent"; }}
                          >
                            <div style={{ color:"white", fontSize:12, fontWeight:800, marginBottom:2, display:"flex", alignItems:"center", gap:6 }}>
                              <span style={{ overflow:"hidden", textOverflow:"ellipsis", whiteSpace:"nowrap" }}>{p.name}</span>
                              {isUserPreset && (
                                <span style={{ background:"rgba(134,239,172,0.15)", color:"#86efac", fontSize:8, padding:"1px 5px", borderRadius:3, fontWeight:700, letterSpacing:0.5, flexShrink:0 }}>USER</span>
                              )}
                            </div>
                            <div style={{ color:"#6b6486", fontSize:10, lineHeight:1.4 }}>{p.desc}</div>
                          </button>
                          {isAdmin && onDeletePreset && (
                            <button onClick={function(e){
                              e.stopPropagation();
                              onDeletePreset(p);
                            }}
                              title={isUserPreset ? "Delete this preset" : "Hide this built-in preset for all users"}
                              style={{
                                background:"none", border:"none",
                                borderLeft:"1px solid #15152a",
                                color:"#666", fontSize:14, cursor:"pointer",
                                padding:"0 12px", flexShrink:0,
                              }}
                              onPointerEnter={function(e){ e.currentTarget.style.color="#EF4444"; }}
                              onPointerLeave={function(e){ e.currentTarget.style.color="#666"; }}
                            >🗑</button>
                          )}
                        </div>
                      );
                    })}
                  </div>
                  {/* "Scroll for more" hint — sits at the bottom of the
                      list, with a fade gradient above it so it looks
                      attached to the content. The script above toggles
                      visibility based on scroll position. */}
                  <div data-bf-preset-hint style={{
                    position:"absolute",
                    bottom:0, left:0, right:0,
                    height:30,
                    pointerEvents:"none",
                    background:"linear-gradient(180deg, rgba(15,15,21,0) 0%, rgba(15,15,21,0.85) 60%, rgba(15,15,21,0.95) 100%)",
                    display:"flex", alignItems:"flex-end", justifyContent:"center",
                    paddingBottom:6,
                    opacity:0,
                    transition:"opacity 0.18s",
                    color:"#a78bfa", fontSize:9, fontWeight:800, letterSpacing:1,
                    textTransform:"uppercase",
                  }}>↓ Scroll for more</div>
                  </div>
                </div>
              </>
            )}
          </div>
        )}
        {/* FX panel master VU meter */}
        <div style={{ marginRight:10 }}>
          <VUMeter analyserNode={analyserNode} isActive={isPlaying} compact={true} showLabel={false} />
        </div>
        {/* Undo / Redo */}
        <button
          aria-label="Undo"
          onClick={doUndo}
          disabled={!canUndo}
          style={{
            background: canUndo ? "#15101e" : "#0d0a14",
            border: "1px solid " + (canUndo ? "#3a2a52" : "#1f1a30"),
            borderRadius:8,
            color: canUndo ? "#9580c4" : "#3a2f4a",
            fontSize:14, fontWeight:700,
            padding:"3px 9px",
            cursor: canUndo ? "pointer" : "default",
            marginRight:4,
            opacity: canUndo ? 1 : 0.5,
          }}>↶</button>
        <button
          aria-label="Redo"
          onClick={doRedo}
          disabled={!canRedo}
          style={{
            background: canRedo ? "#15101e" : "#0d0a14",
            border: "1px solid " + (canRedo ? "#3a2a52" : "#1f1a30"),
            borderRadius:8,
            color: canRedo ? "#9580c4" : "#3a2f4a",
            fontSize:14, fontWeight:700,
            padding:"3px 9px",
            cursor: canRedo ? "pointer" : "default",
            marginRight:8,
            opacity: canRedo ? 1 : 0.5,
          }}>↷</button>
        {/* Collapse / Expand all FX — single toggle button */}
        <button
          aria-label={fxAllCollapsed ? "Expand all FX" : "Collapse all FX"}
          onClick={function(){
            try {
              if (fxAllCollapsed) {
                if (window._bfExpandAllFx) window._bfExpandAllFx();
                setFxAllCollapsed(false);
              } else {
                if (window._bfCollapseAllFx) window._bfCollapseAllFx();
                setFxAllCollapsed(true);
              }
            } catch(_){}
          }}
          style={{ background:"#15101e", border:"1px solid #3a2a52", borderRadius:8, color:"#9580c4", fontSize:11, fontWeight:700, padding:"5px 9px", cursor:"pointer", marginRight:8, letterSpacing:0.3 }}>
          {fxAllCollapsed ? "▾▾" : "▸▸"}
        </button>
        <button onClick={onClose} style={{ background:"#1a1a1a", border:"1px solid #2a2a2a", borderRadius:8, color:"#888", fontSize:13, padding:"5px 14px", cursor:"pointer" }}>Done</button>
      </div>
      {/* Scrollable middle area — the FX content. Wrapped in a
          position:relative container so we can overlay a custom
          scrollbar on the right edge. iOS Safari refuses to engage
          native touch scrolling on this container for reasons we
          cannot determine (the container IS mechanically scrollable
          — el.scrollTop changes work). So we provide a custom
          touch-draggable scrollbar instead. Touch events on the
          scrollbar are on its OWN element — completely separate
          from the FX content area — so they cannot interfere with
          the EQ band drag's touch handling. */}
      <div style={{ flex:1, minHeight:0, position:"relative" }}>
        <div
          ref={function(el){
            if (typeof window !== "undefined") window._bfFxScrollEl = el;
            if (!el || el._bfFxScrollSetup) return;
            el._bfFxScrollSetup = true;
            // Updates the thumb's height + position based on current
            // scrollHeight / clientHeight / scrollTop.
            const updateThumb = function(){
              const thumb = el._bfFxThumb;
              const track = el._bfFxTrack;
              if (!thumb || !track) return;
              const ch = el.clientHeight;
              const sh = el.scrollHeight;
              const trackH = track.clientHeight;
              if (sh <= ch || trackH === 0) {
                // No overflow — hide scrollbar
                track.style.opacity = "0";
                track.style.pointerEvents = "none";
                return;
              }
              track.style.opacity = "1";
              track.style.pointerEvents = "auto";
              const thumbH = Math.max(24, (ch / sh) * trackH);
              const thumbTop = (el.scrollTop / (sh - ch)) * (trackH - thumbH);
              thumb.style.height = thumbH + "px";
              thumb.style.transform = "translateY(" + thumbTop + "px)";
            };
            el._bfFxUpdateThumb = updateThumb;
            // Recompute when scroll position changes (e.g., set
            // programmatically by something).
            el.addEventListener("scroll", updateThumb, { passive: true });
            // Recompute when content size changes (plugin added/
            // removed/collapsed).
            try {
              const ro = new ResizeObserver(updateThumb);
              ro.observe(el);
              if (el.children[0]) ro.observe(el.children[0]);
            } catch(_){}
            // Initial recalc after layout settles.
            setTimeout(updateThumb, 50);
            setTimeout(updateThumb, 300);
            // ──────────────────────────────────────────────────────────
            // JS-driven touch scroll. iOS Safari refuses native overflow-
            // touch on this container, so we drive scrollTop ourselves.
            // CRUCIAL: skip engaging if the touch started on an interactive
            // element (band marker, knob, button) — otherwise scroll would
            // fight with the band drag and knob drag. The .closest() check
            // is the only reliable way: iOS dispatches touchstart BEFORE
            // pointerdown, so we can't read a "drag-in-progress" flag —
            // the target check happens at the right moment.
            // ──────────────────────────────────────────────────────────
            let touchActive       = false;
            let touchStartY       = 0;
            let touchStartScroll  = 0;
            let lastTouchY        = 0;
            let lastTouchTime     = 0;
            let touchVelocity     = 0;
            let momentumRaf       = 0;
            let touchStartedAt    = 0;
            const cancelMomentum  = function(){
              if (momentumRaf) { cancelAnimationFrame(momentumRaf); momentumRaf = 0; }
            };
            const isInteractive = function(target){
              if (!target || !target.closest) return false;
              return !!target.closest("[data-band-key], [data-knob], [data-pointer-slider], button, input, [data-eq-readout]");
            };
            const onScrollTouchStart = function(e){
              if (!e.touches || !e.touches[0]) return;
              // Skip if touching an interactive element — band drag,
              // knob, or button gets the touch.
              if (isInteractive(e.target)) return;
              cancelMomentum();
              touchActive      = true;
              touchStartY      = e.touches[0].clientY;
              touchStartScroll = el.scrollTop;
              lastTouchY       = touchStartY;
              lastTouchTime    = Date.now();
              touchStartedAt   = lastTouchTime;
              touchVelocity    = 0;
            };
            const onScrollTouchMove = function(e){
              if (!touchActive || !e.touches || !e.touches[0]) return;
              try { e.preventDefault(); } catch(_){}
              const y  = e.touches[0].clientY;
              const dy = touchStartY - y;
              const sh = el.scrollHeight, ch = el.clientHeight;
              el.scrollTop = Math.max(0, Math.min(sh - ch, touchStartScroll + dy));
              const now = Date.now();
              const dt  = now - lastTouchTime;
              if (dt > 0) touchVelocity = (lastTouchY - y) / dt;
              lastTouchY    = y;
              lastTouchTime = now;
            };
            const onScrollTouchEnd = function(){
              if (!touchActive) return;
              touchActive = false;
              if (Math.abs(touchVelocity) > 0.05) {
                const step = function(){
                  touchVelocity *= 0.94;
                  if (Math.abs(touchVelocity) < 0.02) { momentumRaf = 0; return; }
                  const sh = el.scrollHeight, ch = el.clientHeight;
                  el.scrollTop = Math.max(0, Math.min(sh - ch, el.scrollTop + touchVelocity * 16));
                  momentumRaf = requestAnimationFrame(step);
                };
                momentumRaf = requestAnimationFrame(step);
              }
            };
            el.addEventListener("touchstart",  onScrollTouchStart, { passive: true });
            el.addEventListener("touchmove",   onScrollTouchMove,  { passive: false });
            el.addEventListener("touchend",    onScrollTouchEnd,   { passive: true });
            el.addEventListener("touchcancel", onScrollTouchEnd,   { passive: true });
          }}
          style={{ width:"100%", height:"100%", overflowY:"auto", WebkitOverflowScrolling:"touch" }}>
          <FxPanelPlugins fx={fx} upd={upd} eq5={eq5} EQGraph={EQGraph} CompGraph={CompGraph} ReverbViz={ReverbViz} Knob={Knob} analyserNode={analyserNode} isPlaying={isPlaying} trackId={fxTrackId} />
        </div>
        {/* Custom scrollbar track + thumb. Sits on the right edge,
            inside the panel. The thumb is the only interactive
            element — touches anywhere else in the FX content don't
            reach this code. */}
        <div
          ref={function(track){
            const scrollEl = (typeof window !== "undefined") ? window._bfFxScrollEl : null;
            if (!track || !scrollEl) return;
            scrollEl._bfFxTrack = track;
            if (track._bfFxTrackSetup) return;
            track._bfFxTrackSetup = true;
            // Refresh thumb dims now that the track is mounted.
            try { if (scrollEl._bfFxUpdateThumb) scrollEl._bfFxUpdateThumb(); } catch(_){}
          }}
          style={{
            position:"absolute", top:6, bottom:6, right:3, width:14,
            background:"transparent",
            borderRadius:4,
            transition:"opacity 0.2s",
            zIndex:50,
          }}>
          {/* Thumb */}
          <div
            ref={function(thumb){
              const scrollEl = (typeof window !== "undefined") ? window._bfFxScrollEl : null;
              if (!thumb || !scrollEl) return;
              scrollEl._bfFxThumb = thumb;
              if (thumb._bfFxThumbSetup) return;
              thumb._bfFxThumbSetup = true;
              // Touch drag on the thumb sets el.scrollTop directly.
              // Window-level touchmove + touchend so we keep dragging
              // even if the finger leaves the thumb area.
              let startY = 0;
              let startScrollTop = 0;
              let active = false;
              const onMove = function(e){
                if (!active || !e.touches || !e.touches[0]) return;
                const track = scrollEl._bfFxTrack;
                if (!track) return;
                const trackH = track.clientHeight;
                const ch = scrollEl.clientHeight;
                const sh = scrollEl.scrollHeight;
                if (sh <= ch) return;
                const thumbH = Math.max(24, (ch / sh) * trackH);
                const dy = e.touches[0].clientY - startY;
                // dy in track space → scrollTop in content space.
                const scrollRange = sh - ch;
                const trackRange = trackH - thumbH;
                if (trackRange <= 0) return;
                const newScrollTop = startScrollTop + dy * (scrollRange / trackRange);
                scrollEl.scrollTop = Math.max(0, Math.min(scrollRange, newScrollTop));
                // (scroll event fires → updateThumb fires)
              };
              const onUp = function(){
                active = false;
                window.removeEventListener("touchmove",   onMove);
                window.removeEventListener("touchend",    onUp);
                window.removeEventListener("touchcancel", onUp);
                window.removeEventListener("mousemove",   onMouseMove);
                window.removeEventListener("mouseup",     onUp);
              };
              const onMouseMove = function(e){
                if (!active) return;
                const track = scrollEl._bfFxTrack;
                if (!track) return;
                const trackH = track.clientHeight;
                const ch = scrollEl.clientHeight;
                const sh = scrollEl.scrollHeight;
                if (sh <= ch) return;
                const thumbH = Math.max(24, (ch / sh) * trackH);
                const dy = e.clientY - startY;
                const scrollRange = sh - ch;
                const trackRange = trackH - thumbH;
                if (trackRange <= 0) return;
                const newScrollTop = startScrollTop + dy * (scrollRange / trackRange);
                scrollEl.scrollTop = Math.max(0, Math.min(scrollRange, newScrollTop));
              };
              thumb.addEventListener("touchstart", function(e){
                if (!e.touches || !e.touches[0]) return;
                e.preventDefault();
                e.stopPropagation();
                active = true;
                startY = e.touches[0].clientY;
                startScrollTop = scrollEl.scrollTop;
                window.addEventListener("touchmove",   onMove, { passive: false });
                window.addEventListener("touchend",    onUp);
                window.addEventListener("touchcancel", onUp);
              }, { passive: false });
              thumb.addEventListener("mousedown", function(e){
                e.preventDefault();
                e.stopPropagation();
                active = true;
                startY = e.clientY;
                startScrollTop = scrollEl.scrollTop;
                window.addEventListener("mousemove", onMouseMove);
                window.addEventListener("mouseup",   onUp);
              });
            }}
            style={{
              position:"absolute", top:0, left:0, right:0,
              background:"transparent",
              borderRadius:4,
              boxShadow:"none",
              minHeight:24,
              touchAction:"none",
              cursor:"grab",
            }}/>
        </div>
      </div>
      {/* ── FX-internal transport bar ─────────────────────────────────────
          Lives INSIDE the FX panel so iOS sees a single context: panel
          content + transport row owned by the same React subtree. No
          shared buttons with the main timeline transport (which is
          hidden via display:none in StudioScreen while fxTrackId is set).

          Five buttons: Back, Rewind, Record, Play/Pause, Fast Forward.
          Calls the same underlying functions as the main transport. */}
      <div style={{
        flexShrink:0,
        background:"linear-gradient(180deg,rgba(15,10,31,0.96) 0%,rgba(10,10,20,0.98) 100%)",
        borderTop:"1px solid rgba(124,58,237,0.25)",
        padding:"10px 12px",
        paddingBottom:"calc(10px + env(safe-area-inset-bottom))",
        display:"flex", alignItems:"center", justifyContent:"center", gap:14,
      }}>
        {/* Back — same action as Done in header, but accessible from bottom */}
        <button onClick={onClose}
          aria-label="Back to timeline"
          style={{
            width:44, height:44, borderRadius:12,
            background:"#141414", border:"1px solid #2a2a2a",
            cursor:"pointer", display:"flex", alignItems:"center", justifyContent:"center",
            flexShrink:0, color:"#aaa", fontSize:13, fontWeight:700,
          }}>
          <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.2" strokeLinecap="round" strokeLinejoin="round">
            <polyline points="15 18 9 12 15 6"/>
          </svg>
        </button>
        {/* Rewind 5s — mirrors main transport seekBack */}
        {onSeekBack && (
          <button onClick={onSeekBack}
            aria-label="Rewind 5 seconds"
            style={{
              width:36, height:36, borderRadius:8,
              background:"#141414", border:"1px solid #222",
              cursor:"pointer", display:"flex", alignItems:"center", justifyContent:"center",
              flexShrink:0,
            }}>
            <svg width="16" height="16" viewBox="0 0 24 24" fill="white">
              <path d="M12.5 8.5 7 12l5.5 3.5V8.5z"/>
              <path d="M19.5 8.5 14 12l5.5 3.5V8.5z"/>
            </svg>
          </button>
        )}
        {/* Record */}
        {onRecord && (
          <button onClick={onRecord}
            aria-label={isRecording ? "Stop recording" : "Record"}
            style={{
              width:52, height:52, borderRadius:"50%",
              background:isRecording?"#EF4444":"linear-gradient(135deg,#EF4444,#DC2626)",
              border:isRecording?"3px solid rgba(239,68,68,0.5)":"3px solid rgba(239,68,68,0.2)",
              cursor:"pointer", display:"flex", alignItems:"center", justifyContent:"center",
              boxShadow:isRecording?"0 0 20px rgba(239,68,68,0.5)":"none",
              flexShrink:0,
            }}>
            {isRecording
              ? <div style={{ width:16, height:16, background:"white", borderRadius:3 }} />
              : <div style={{ width:20, height:20, background:"white", borderRadius:"50%" }} />}
          </button>
        )}
        {/* Play / Pause */}
        {onTogglePlay && (
          <button onClick={onTogglePlay}
            aria-label={isPlaying ? "Pause" : "Play"}
            style={{
              width:44, height:44, borderRadius:"50%",
              background:"linear-gradient(135deg,#C026D3,#7C3AED)",
              border:"none", cursor:"pointer",
              display:"flex", alignItems:"center", justifyContent:"center",
              flexShrink:0,
            }}>
            {isPlaying
              ? <svg width="18" height="18" viewBox="0 0 24 24" fill="white"><path d="M6 19h4V5H6v14zm8-14v14h4V5h-4z"/></svg>
              : <svg width="18" height="18" viewBox="0 0 24 24" fill="white"><path d="M8 5v14l11-7z"/></svg>}
          </button>
        )}
        {/* Fast Forward 5s — mirrors main transport seekForward */}
        {onSeekForward && (
          <button onClick={onSeekForward}
            aria-label="Fast forward 5 seconds"
            style={{
              width:36, height:36, borderRadius:8,
              background:"#141414", border:"1px solid #222",
              cursor:"pointer", display:"flex", alignItems:"center", justifyContent:"center",
              flexShrink:0,
            }}>
            <svg width="16" height="16" viewBox="0 0 24 24" fill="white">
              <path d="M11.5 8.5 17 12l-5.5 3.5V8.5z"/>
              <path d="M4.5 8.5 10 12 4.5 15.5V8.5z"/>
            </svg>
          </button>
        )}
      </div>
    </div>
  );
}, function areEqual(prev, next) {
  // Skip re-render if only currentTime/scroll changed — only re-render for real fx data changes
  return prev.fxTrackId === next.fxTrackId &&
         prev.fx === next.fx &&
         prev.trackName === next.trackName &&
         prev.trackColor === next.trackColor &&
         prev.isPlaying === next.isPlaying &&
         prev.isRecording === next.isRecording &&
         prev.onApplyPreset === next.onApplyPreset &&
         prev.presets === next.presets &&
         prev.isAdmin === next.isAdmin;
});

// =============================================================================
// BEAT GRID OVERLAY COMPONENT
// Renders detected beat marker lines over the DAW timeline.
// Uses absolute positioning to match the scroll container's coordinate space.
// Designed to be a zero-overhead read-only visual layer — no audio access.
// =============================================================================
function BeatGridOverlay({ beats, effectivePPS, sidebarW, rulerH, scrollRef, isPlaying, currentTime, bpm }) {
  const canvasRef = useRef(null);

  // Draw beat grid on a canvas that floats over the timeline lanes
  useEffect(function () {
    const canvas = canvasRef.current;
    if (!canvas || !beats || beats.length === 0) return;

    const el = scrollRef.current;
    if (!el) return;

    const ctx = canvas.getContext("2d");
    const W = canvas.width;
    const H = canvas.height;
    ctx.clearRect(0, 0, W, H);

    const scrollLeft = el.scrollLeft;

    // Visible time range (add 2s buffer on each side)
    const visStart = Math.max(0, scrollLeft / effectivePPS - 2);
    const visEnd   = (scrollLeft + W) / effectivePPS + 2;

    // Draw each beat line
    beats.forEach(function (t, i) {
      if (t < visStart || t > visEnd) return;
      const x = Math.round(t * effectivePPS - scrollLeft);
      if (x < 0 || x > W) return;

      // Downbeat (every 4th beat) gets a brighter, taller line
      const isDownbeat = (i % 4 === 0);

      ctx.beginPath();
      ctx.moveTo(x, rulerH);
      ctx.lineTo(x, H);
      ctx.strokeStyle = isDownbeat
        ? "rgba(192,38,211,0.35)"
        : "rgba(192,38,211,0.13)";
      ctx.lineWidth = isDownbeat ? 1.5 : 0.75;
      ctx.stroke();

      // Tiny triangle at the ruler bottom for downbeats
      if (isDownbeat) {
        ctx.beginPath();
        ctx.moveTo(x - 4, rulerH);
        ctx.lineTo(x + 4, rulerH);
        ctx.lineTo(x, rulerH + 6);
        ctx.closePath();
        ctx.fillStyle = "rgba(192,38,211,0.6)";
        ctx.fill();
      }
    });

  // Redraw when scroll or beat data changes
  // eslint-disable-next-line
  }, [beats, effectivePPS, currentTime, bpm]);

  // Keep canvas sized to container
  useEffect(function () {
    const el = scrollRef.current;
    if (!el) return;
    const canvas = canvasRef.current;
    if (!canvas) return;
    const onScroll = function () {
      // Force re-paint on scroll
      canvas.dispatchEvent(new Event("bfredraw"));
    };
    el.addEventListener("scroll", onScroll, { passive:true });
    return function () { el.removeEventListener("scroll", onScroll); };
  }, [scrollRef]);

  return (
    <canvas
      ref={canvasRef}
      width={window.innerWidth}
      height={window.innerHeight}
      style={{
        position:"absolute",
        top:0, left:sidebarW,
        pointerEvents:"none",
        zIndex:15,
        opacity:1,
      }}
    />
  );
}

// ── BeatFinder Mixer — neon-purple LED theme to match FX panel ──
const GB_BG       = "#0f0a1e";  // Deep purple-black panel base
let gbFaderDragging = false; // true while any fader is being dragged — blocks mixer scroll
const GB_STRIP    = "#15102a"; // Channel strip body
const GB_BORDER   = "#2a2050"; // Subtle purple border
const GB_LABEL    = "#7a6ea0"; // Muted purple label text
const GB_ACCENT   = "#a78bfa"; // Neon purple primary
const GB_FADER_H  = 140; // px tall fader travel area
const STRIP_W     = 58;

function GBPanKnob({ pan, color, onChange }) {
  const dragRef = React.useRef(null);
  // pan −1..1 → norm 0..1
  const norm  = (pan + 1) / 2;
  const angle = -140 + norm * 280;
  const r = 14, SW = 3, PAD = SW / 2 + 2;
  const cx = r + PAD, cy = r + PAD, SIZE = (r + PAD) * 2;
  const toXY = function(deg){ const rad=(deg-90)*Math.PI/180; return {x:cx+r*Math.cos(rad),y:cy+r*Math.sin(rad)}; };
  const startA = toXY(-140), endA = toXY(angle);
  const swept  = angle-(-140), large = swept>180?1:0;
  const arcD   = "M "+startA.x.toFixed(2)+" "+startA.y.toFixed(2)+" A "+r+" "+r+" 0 "+large+" 1 "+endA.x.toFixed(2)+" "+endA.y.toFixed(2);
  function onPD(e){
    e.preventDefault();
    dragRef.current = { y:e.clientY, val:pan };
    function onM(me){
      const dy = dragRef.current.y - me.clientY;
      const nv = Math.max(-1, Math.min(1, dragRef.current.val + dy/60));
      onChange(+nv.toFixed(2));
    }
    function onU(){ document.removeEventListener("pointermove",onM); document.removeEventListener("pointerup",onU); }
    document.addEventListener("pointermove",onM);
    document.addEventListener("pointerup",onU);
  }
  return (
    <svg width={SIZE} height={SIZE} viewBox={"0 0 "+SIZE+" "+SIZE}
      style={{cursor:"ns-resize",touchAction:"none",overflow:"visible",display:"block"}}
      onPointerDown={onPD}
      onDoubleClick={function(){ onChange(0); }}>
      <defs>
        <filter id="bfPanGlow" x="-50%" y="-50%" width="200%" height="200%">
          <feGaussianBlur stdDeviation="1.2" result="b"/>
          <feMerge><feMergeNode in="b"/><feMergeNode in="SourceGraphic"/></feMerge>
        </filter>
      </defs>
      {/* Track arc — deep purple */}
      <circle cx={cx} cy={cy} r={r} fill="none" stroke="#2a2050" strokeWidth={SW} strokeLinecap="round"
        strokeDasharray={(2*Math.PI*r*280/360)+" "+(2*Math.PI*r)}
        strokeDashoffset={(2*Math.PI*r*(90+140)/360)}
        transform={"rotate(-90 "+cx+" "+cy+")"} />
      {/* Centre tick */}
      <line x1={cx} y1={PAD} x2={cx} y2={PAD+4} stroke="#7a6ea0" strokeWidth={1} />
      {/* Filled arc — neon purple with glow when off-center */}
      {norm>0.01 && norm<0.99 && <path d={arcD} fill="none" stroke="#a78bfa" strokeWidth={SW} strokeLinecap="round" filter="url(#bfPanGlow)"/>}
      {/* Centered dot lights up purple */}
      {Math.abs(pan)<0.02 && <circle cx={cx} cy={PAD+1} r={2} fill="#a78bfa" filter="url(#bfPanGlow)"/>}
      {/* Thumb — neon purple glow */}
      <circle cx={endA.x} cy={endA.y} r={SW*0.85} fill="#c4b5fd" filter="url(#bfPanGlow)"/>
      {/* Inner cap — deep purple */}
      <circle cx={cx} cy={cy} r={r*0.38} fill="#0f0a1e" stroke="#2a2050" strokeWidth={1.5}/>
    </svg>
  );
}

function GBFader({ vol, color, muted, onChange, onBeginGesture, onEndGesture }) {
  const trackH = GB_FADER_H;
  const thumbY = trackH - Math.max(0, Math.min(1, vol / 1.5)) * trackH;
  const unityY = trackH - (1/1.5)*trackH;

  function onPD(e){
    e.preventDefault();
    e.stopPropagation();
    // Capture pointer to THIS element so moves are delivered here only —
    // never leaking to adjacent faders even if finger drifts sideways.
    const el = e.currentTarget;
    el.setPointerCapture(e.pointerId);
    const rect = el.getBoundingClientRect();
    const pid  = e.pointerId;
    gbFaderDragging = true;
    if (onBeginGesture) try { onBeginGesture(); } catch(_) {}
    function onM(me){
      if (me.pointerId !== pid) return;
      const dy = me.clientY - rect.top;
      const n  = Math.max(0, Math.min(1, dy / trackH));
      onChange(+((1-n)*1.5).toFixed(3));
    }
    function onU(ue){
      if (ue.pointerId !== pid) return;
      gbFaderDragging = false;
      el.releasePointerCapture(pid);
      el.removeEventListener("pointermove", onM);
      el.removeEventListener("pointerup",   onU);
      if (onEndGesture) try { onEndGesture(); } catch(_) {}
    }
    el.addEventListener("pointermove", onM);
    el.addEventListener("pointerup",   onU);
  }

  const thumbColor = muted ? "#636366" : "#e5e5ea";
  const fillH = Math.max(0, Math.min(1, vol/1.5)) * trackH;

  return (
    <div style={{ position:"relative", width:28, height:trackH, flexShrink:0, cursor:"pointer" }}
      onPointerDown={onPD}
      onDoubleClick={function(){ onChange(1); }}>
      {/* Rail background — deep purple shadow track */}
      <div style={{
        position:"absolute", left:"50%", transform:"translateX(-50%)",
        top:0, bottom:0, width:4,
        background:"linear-gradient(180deg,#1a1230,#0d0820)",
        borderRadius:2,
        boxShadow:"inset 0 0 4px rgba(0,0,0,0.7)",
      }} />
      {/* Fill — neon purple LED gradient with glow */}
      <div style={{
        position:"absolute", left:"50%", transform:"translateX(-50%)",
        bottom:0, width:4,
        height: fillH,
        background: muted
          ? "linear-gradient(0deg,#3a2a52,#251a3a)"
          : "linear-gradient(0deg,#c4b5fd,#a78bfa 50%,#8b5cf6cc 80%,transparent)",
        borderRadius:2,
        boxShadow: muted ? "none" : "0 0 8px rgba(167,139,250,0.6), 0 0 14px rgba(139,92,246,0.3)",
      }} />
      {/* Unity tick — subtle purple marker */}
      <div style={{ position:"absolute", left:"calc(50% + 4px)", top: unityY-0.5, width:6, height:1, background:"#7a6ea0" }} />
      {/* Fader thumb — light pill with neon top edge */}
      <div style={{
        position:"absolute", left:"50%", top: thumbY - 14,
        transform:"translateX(-50%)",
        width:24, height:28,
        background: muted
          ? "linear-gradient(180deg,#3a2a52,#251a3a)"
          : "linear-gradient(180deg,#f0e7ff,#c4b5fd 60%,#9580c4)",
        borderRadius:5,
        boxShadow: muted
          ? "0 1px 3px rgba(0,0,0,0.8)"
          : "0 2px 6px rgba(0,0,0,0.7), 0 1px 0 rgba(255,255,255,0.25) inset, 0 0 10px rgba(167,139,250,0.45)",
        border: muted ? "1px solid #2a2050" : "1px solid rgba(167,139,250,0.5)",
        display:"flex", alignItems:"center", justifyContent:"center",
      }}>
        {/* Grip lines */}
        {[0,1,2].map(function(i){
          return <div key={i} style={{ position:"absolute", left:5, right:5, top:9+i*3, height:1, background: muted?"rgba(100,90,140,0.5)":"rgba(60,40,100,0.35)", borderRadius:0.5 }} />;
        })}
      </div>
    </div>
  );
}

function GBVUBars({ analyserNode, active }) {
  const { level, peak, clipping } = useAnalyser(analyserNode, active);
  const rOff   = React.useRef(0.88 + Math.random()*0.12);
  const rLevel = Math.min(1, level * rOff.current);
  const rPeak  = Math.min(1, peak  * rOff.current);
  const BAR_H  = GB_FADER_H;
  const SEGS   = 24;

  function renderCol(lv, pk) {
    return (
      <div style={{
        display:"flex", flexDirection:"column-reverse", gap:1, height:BAR_H, width:5,
        background:"rgba(0,0,0,0.5)", borderRadius:1, padding:"1px 0",
      }}>
        {Array.from({length:SEGS}, function(_,i){
          const frac    = i / SEGS;
          const lit     = lv >= frac;
          const isPeak  = pk>0 && Math.abs(pk-frac)<(1.5/SEGS);
          // Color zones: cyan/violet for safe, magenta/pink for hot, red for clip.
          const col     = frac>=0.875 ? "#ff3b6b"
                        : frac>=0.688 ? "#f472b6"
                        : frac>=0.5   ? "#a78bfa"
                        : "#67e8f9";
          // Dim color stays in the matching hue, very dark.
          const dim     = frac>=0.875 ? "#2a0a18"
                        : frac>=0.688 ? "#2a1530"
                        : frac>=0.5   ? "#1a1235"
                        : "#0a1f2a";
          // Glow color matches lit color but softer.
          const glow    = frac>=0.875 ? "rgba(255,59,107,0.5)"
                        : frac>=0.688 ? "rgba(244,114,182,0.5)"
                        : frac>=0.5   ? "rgba(167,139,250,0.45)"
                        : "rgba(103,232,249,0.4)";
          return <div key={i} style={{
            flex:1, borderRadius:1,
            background: isPeak ? "#fff" : (lit ? col : dim),
            boxShadow: (lit || isPeak) ? ("0 0 4px " + glow) : "none",
          }}/>;
        })}
      </div>
    );
  }

  return (
    <div style={{ display:"flex", gap:2, alignItems:"flex-end" }}>
      {renderCol(level, peak)}
      {renderCol(rLevel, rPeak)}
    </div>
  );
}

// =============================================================================
// VERIFIED BADGE — blue-to-purple gradient seal with white checkmark
// Matches the Instagram/X-style verification badge design
// =============================================================================

// Gold verification badge — Trelli only
// Green verified badge — Brand Ambassadors
function GreenVerifiedBadge({ size = 20 }) {
  return (
    <svg width={size} height={size} viewBox="0 0 100 100" fill="none" xmlns="http://www.w3.org/2000/svg">
      <defs>
        <linearGradient id="greenGrad" x1="0%" y1="0%" x2="100%" y2="100%">
          <stop offset="0%" stopColor="#22C55E"/>
          <stop offset="50%" stopColor="#16A34A"/>
          <stop offset="100%" stopColor="#14532D"/>
        </linearGradient>
        <linearGradient id="greenShine" x1="0%" y1="0%" x2="100%" y2="0%">
          <stop offset="0%" stopColor="#4ADE80" stopOpacity="0.9"/>
          <stop offset="50%" stopColor="#86EFAC"/>
          <stop offset="100%" stopColor="#22C55E" stopOpacity="0.9"/>
        </linearGradient>
      </defs>
      <path d="M50 4 L61 20 L79 14 L79 33 L96 38 L88 55 L96 72 L79 77 L79 96 L61 90 L50 106 L39 90 L21 96 L21 77 L4 72 L12 55 L4 38 L21 33 L21 14 L39 20 Z"
        fill="url(#greenGrad)" stroke="#22C55E" strokeWidth="1.5"/>
      <path d="M50 10 L59 24 L75 19 L75 35 L90 40 L83 55 L90 70 L75 75 L75 91 L59 86 L50 100 L41 86 L25 91 L25 75 L10 70 L17 55 L10 40 L25 35 L25 19 L41 24 Z"
        fill="url(#greenShine)" opacity="0.3"/>
      <polyline points="34,52 45,63 67,40" stroke="#000" strokeWidth="9" strokeLinecap="round" strokeLinejoin="round" fill="none"/>
      <polyline points="34,52 45,63 67,40" stroke="#4ADE80" strokeWidth="6" strokeLinecap="round" strokeLinejoin="round" fill="none"/>
    </svg>
  );
}

// Brand Ambassador badge
function BrandAmbassadorBadge({ onClick }) {
  return (
    <span onClick={onClick} style={{
      display: "inline-flex", alignItems: "center", gap: 4,
      background: "linear-gradient(135deg,#052e16 0%,#14532D 40%,#15803D 100%)",
      border: "1.5px solid #22C55E", borderRadius: 20, padding: "3px 10px",
      color: "#4ADE80", fontWeight: 900, fontSize: 10, letterSpacing: 1.5,
      textTransform: "uppercase", boxShadow: "0 0 8px rgba(34,197,94,0.4)",
      cursor: onClick ? "pointer" : "default",
    }}>
      <svg width="10" height="10" viewBox="0 0 24 24" fill="#4ADE80">
        <path d="M12 2C6.48 2 2 6.48 2 12s4.48 10 10 10 10-4.48 10-10S17.52 2 12 2zm-1 14H9V8h2v8zm4 0h-2V8h2v8z"/>
      </svg>
      B.A
    </span>
  );
}

function GoldVerifiedBadge({ size = 20 }) {
  return (
    <svg width={size} height={size} viewBox="0 0 100 100" fill="none" xmlns="http://www.w3.org/2000/svg">
      <defs>
        <linearGradient id="goldGrad" x1="0%" y1="0%" x2="100%" y2="100%">
          <stop offset="0%" stopColor="#FFD700"/>
          <stop offset="40%" stopColor="#FFA500"/>
          <stop offset="70%" stopColor="#B8860B"/>
          <stop offset="100%" stopColor="#000000"/>
        </linearGradient>
        <linearGradient id="goldShine" x1="0%" y1="0%" x2="100%" y2="0%">
          <stop offset="0%" stopColor="#FFD700" stopOpacity="0.9"/>
          <stop offset="50%" stopColor="#FFFACD"/>
          <stop offset="100%" stopColor="#B8860B" stopOpacity="0.9"/>
        </linearGradient>
      </defs>
      <path d="M50 4 L61 20 L79 14 L79 33 L96 38 L88 55 L96 72 L79 77 L79 96 L61 90 L50 106 L39 90 L21 96 L21 77 L4 72 L12 55 L4 38 L21 33 L21 14 L39 20 Z"
        fill="url(#goldGrad)" stroke="#FFD700" strokeWidth="1.5"/>
      <path d="M50 10 L59 24 L75 19 L75 35 L90 40 L83 55 L90 70 L75 75 L75 91 L59 86 L50 100 L41 86 L25 91 L25 75 L10 70 L17 55 L10 40 L25 35 L25 19 L41 24 Z"
        fill="url(#goldShine)" opacity="0.3"/>
      <polyline points="34,52 45,63 67,40" stroke="#000" strokeWidth="9" strokeLinecap="round" strokeLinejoin="round" fill="none"/>
      <polyline points="34,52 45,63 67,40" stroke="#FFD700" strokeWidth="6" strokeLinecap="round" strokeLinejoin="round" fill="none"/>
    </svg>
  );
}

// CEO badge — Trelli only
function CEOBadge({ onClick }) {
  return (
    <span onClick={onClick} style={{
      display: "inline-flex", alignItems: "center", gap: 4,
      background: "linear-gradient(135deg, #000 0%, #1a1a1a 40%, #B8860B 100%)",
      border: "1.5px solid #FFD700", borderRadius: 20, padding: "3px 10px",
      color: "#FFD700", fontWeight: 900, fontSize: 10, letterSpacing: 1.5,
      textTransform: "uppercase", boxShadow: "0 0 8px rgba(255,215,0,0.4)",
      cursor: onClick ? "pointer" : "default",
    }}>
      <svg width="10" height="10" viewBox="0 0 24 24" fill="#FFD700">
        <path d="M2 20h20v2H2zM4 17l4-8 4 4 4-8 4 8H4z"/>
      </svg>
      CEO
    </span>
  );
}

function BadgeInfoPopup({ message, onClose }) {
  if (!message) return null;
  function BadgeIcon({ type }) {
    if (type === "ceo") return <GoldVerifiedBadge size={44} />;
    if (type === "producer") return <VerifiedBadge size={44} />;
    if (type === "brand_ambassador") return <GreenVerifiedBadge size={44} />;
    if (type === "artist") return (
      <svg width="44" height="44" viewBox="0 0 24 24" fill="none" stroke="#F59E0B" strokeWidth="1.5" strokeLinecap="round">
        <path d="M12 2a3 3 0 0 0-3 3v7a3 3 0 0 0 6 0V5a3 3 0 0 0-3-3z"/><path d="M19 10v2a7 7 0 0 1-14 0v-2"/><line x1="12" y1="19" x2="12" y2="23"/><line x1="8" y1="23" x2="16" y2="23"/>
      </svg>
    );
    return null;
  }
  return (
    <>
      <div onClick={onClose} style={{ position:"fixed", inset:0, zIndex:99998, background:"rgba(0,0,0,0.6)" }}/>
      <div style={{
        position:"fixed", top:"50%", left:"50%", transform:"translate(-50%,-50%)",
        zIndex:99999, background:"#1a1a1a", border:"1px solid rgba(255,255,255,0.12)",
        borderRadius:20, padding:"28px 28px 22px", minWidth:260, maxWidth:320,
        boxShadow:"0 16px 60px rgba(0,0,0,0.85)",
        display:"flex", flexDirection:"column", alignItems:"center", gap:14, textAlign:"center",
      }}>
        <div style={{ display:"flex", alignItems:"center", justifyContent:"center" }}><BadgeIcon type={message.icon} /></div>
        <div style={{ color:"white", fontSize:15, fontWeight:600, lineHeight:1.5 }}>{message.text}</div>
        <button onClick={onClose} style={{
          marginTop:4, background:"rgba(255,255,255,0.08)", border:"1px solid rgba(255,255,255,0.15)",
          borderRadius:50, color:"white", fontWeight:700, fontSize:13, padding:"9px 30px", cursor:"pointer",
        }}>Got it</button>
      </div>
    </>
  );
}

function VerifiedBadge({ size = 20 }) {
  const id = "vbg";
  return (
    <svg width={size} height={size} viewBox="0 0 100 100" fill="none"
      style={{ display:"inline-block", verticalAlign:"middle", flexShrink:0 }}>
      <defs>
        <linearGradient id={id} x1="0%" y1="0%" x2="100%" y2="100%">
          <stop offset="0%"   stopColor="#4F8EF7"/>
          <stop offset="100%" stopColor="#C026D3"/>
        </linearGradient>
      </defs>
      {/* 8-pointed rounded seal shape */}
      <path fill={"url(#"+id+")"}
        d="M50 4
           C53 4 58 10 62 10
           C66 10 72 6 75 8
           C78 10 77 17 80 20
           C83 23 90 24 91 28
           C92 32 87 37 87 41
           C87 45 92 50 91 54
           C90 58 83 59 80 62
           C77 65 79 72 76 75
           C73 78 66 76 62 78
           C58 80 54 86 50 86
           C46 86 42 80 38 78
           C34 76 27 78 24 75
           C21 72 23 65 20 62
           C17 59 10 58 9 54
           C8 50 13 45 13 41
           C13 37 8 32 9 28
           C10 24 17 23 20 20
           C23 17 22 10 25 8
           C28 6 34 10 38 10
           C42 10 47 4 50 4Z"/>
      {/* White checkmark */}
      <path d="M31 50 L43 63 L69 37"
        stroke="white" strokeWidth="9" strokeLinecap="round" strokeLinejoin="round" fill="none"/>
    </svg>
  );
}


// =============================================================================
// USER BADGES — small, composed component that renders the right set of
// badges next to a username depending on who it is and what plan they're on.
// Mirrors the logic used on the public profile header (PublicProfileScreen
// around line 12697) so post bylines stay visually consistent with profiles.
//
// Rules (kept in one place so they only need updating once):
//   • Trelli (CEO) → gold seal + CEO chip
//   • Mikez (Brand Ambassador) → green seal + B.A chip
//   • Any user with plan "producer" or "artist" → blue/purple verified tick
//
// Pass `compact` for inline use in post bylines (smaller sizes, hides
// the heavy chip components and just shows the seal icon).
// =============================================================================
function UserBadges({ username, plan, size, compact }) {
  if (!username) return null;
  var sz = size || (compact ? 13 : 18);
  var isProd   = plan === "producer";
  var isArtist = plan === "artist" || isProd;
  var isPro    = isProd || isArtist;
  // Brand Ambassadors get the green seal + (when not compact) the B.A
  // chip. Adding a new BA = add their username here.
  var isBrandAmbassador = username === "Mikez" || username === "HMbarsdat";
  return (
    <React.Fragment>
      {/* Pro plan → blue/purple verified tick. Always shown for any
          Pro user, including the special-badge holders (Trelli, Mikez,
          HMbarsdat) since they're also Pro plan members. Their special
          seal sits alongside the tick rather than replacing it. */}
      {isPro && <VerifiedBadge size={sz} />}
      {/* Trelli: gold seal + (if not compact) the CEO chip */}
      {username === "Trelli" && <GoldVerifiedBadge size={sz + 2} />}
      {username === "Trelli" && !compact && <CEOBadge />}
      {/* Brand Ambassadors: green seal + (if not compact) the B.A chip */}
      {isBrandAmbassador && <GreenVerifiedBadge size={sz + 2} />}
      {isBrandAmbassador && !compact && <BrandAmbassadorBadge />}
    </React.Fragment>
  );
}



// =============================================================================
// FX PRESETS — one-tap chains for common production scenarios.
// Each preset describes a target effects state. Applying replaces the track's
// effects object entirely (clean apply, not additive) and the FX chain rebuilds
// on next Play press. Cost is the same as turning the same plugins on manually.
// =============================================================================
const FX_PRESETS = [
  {
    id: "clean",
    name: "Vocal — Clean",
    desc: "Light compression + air. Good for natural vocals.",
    fx: {
      compressor:   { on:true, threshold:-22, ratio:3, attack:0.005, release:0.18, makeupGain:2 },
      eq:           { on:true, low:0, mid:0, high:2.5, hpfFreq:90 },
      noiseremover: { on:true, threshold:-45, release:0.18, hold:0.08, hiss:0.1, lookahead:true },
    },
  },
  {
    id: "polished",
    name: "Vocal — Polished",
    desc: "Compressor + EQ + plate reverb + subtle doubler.",
    fx: {
      compressor:   { on:true, threshold:-20, ratio:4, attack:0.003, release:0.2, makeupGain:3 },
      eq:           { on:true, low:-1, mid:0.5, high:3, hpfFreq:100 },
      reverb:       { on:true, wet:0.18, roomSize:0.7, preDelay:25 },
      doubler:      { on:true, wet:0.25, width:0.6, detune:8 },
      noiseremover: { on:true, threshold:-45, release:0.15, hold:0.08, hiss:0.15, lookahead:true },
    },
  },
  {
    id: "hard_tune",
    name: "Hard Autotune",
    desc: "T-Pain style hard pitch correction + tight compression + slap delay.",
    fx: {
      autotune:     { on:true, key:"C", scale:"chromatic", speed:100, humanize:0, wet:1, vibrato:0 },
      compressor:   { on:true, threshold:-18, ratio:6, attack:0.001, release:0.12, makeupGain:3 },
      eq:           { on:true, low:0, mid:1, high:4, hpfFreq:120 },
      hdelay:       { on:true, mode:"digital", subdivision:"1/16", sync:true, feedback:0.2, wet:0.18, hiCut:8000, loCut:200 },
      noiseremover: { on:true, threshold:-42, release:0.15, hold:0.05, hiss:0.1, lookahead:true },
    },
  },
  {
    id: "melodic_tune",
    name: "Melodic Tune",
    desc: "Smooth pitch correction + warm plate + chorus doubler.",
    fx: {
      autotune:     { on:true, key:"C", scale:"major", speed:30, humanize:20, wet:0.9, vibrato:0.1 },
      compressor:   { on:true, threshold:-20, ratio:3, attack:0.005, release:0.2, makeupGain:2 },
      eq:           { on:true, low:0, mid:0, high:2, hpfFreq:100 },
      reverb:       { on:true, wet:0.25, roomSize:0.85, preDelay:30 },
      doubler:      { on:true, wet:0.3, width:0.7, detune:12 },
      noiseremover: { on:true, threshold:-45, release:0.18, hold:0.08, hiss:0.15, lookahead:true },
    },
  },
  {
    id: "lofi",
    name: "Lo-Fi",
    desc: "Bandpass filter + tape saturation. Vintage radio character.",
    fx: {
      bandpass:     { on:true, center:1400, width:2.5, res:1, mix:0.85 },
      trotten:      { on:true, eqLow:-2, eqMid:1, eqHigh:-3, compThr:-18, compAmt:60, compMode:"auto", tapeDrv:30, tapeSat:25, tapeMode:"vintage", limCeil:-1, limRel:0.3, inputGain:0, outputGain:0 },
      noiseremover: { on:false },
    },
  },
  {
    id: "stadium",
    name: "Stadium",
    desc: "Long ocean reverb + tape glue. Huge, distant vocal.",
    fx: {
      compressor:   { on:true, threshold:-22, ratio:3, attack:0.005, release:0.25, makeupGain:2 },
      eq:           { on:true, low:0, mid:0, high:1.5, hpfFreq:110 },
      ocean:        { on:true, wet:0.5, roomSize:1.3, damp:0.5, preDelay:40 },
      hdelay:       { on:true, mode:"tape", subdivision:"1/4", sync:true, feedback:0.3, wet:0.18, hiCut:6500, loCut:180, modDepth:0.2, modRate:0.5 },
      noiseremover: { on:true, threshold:-45, release:0.2, hold:0.1, hiss:0.1, lookahead:true },
    },
  },
  {
    id: "master",
    name: "Master / Beat Bus",
    desc: "T-Rotten master chain for beats. Use on instrumental track.",
    fx: {
      trotten:      { on:true, eqLow:1, eqMid:0, eqHigh:1.5, compThr:-12, compAmt:40, compMode:"auto", tapeDrv:8, tapeSat:10, tapeMode:"modern", limCeil:-0.5, limRel:0.4, limMode:"truepeak", inputGain:0, outputGain:0 },
      eq:           { on:true, low:1, mid:0, high:1, hpfFreq:30 },
    },
  },
];


// =============================================================================
// STUDIO DEBUG OVERLAY (admin-only)
// =============================================================================
// Performance diagnostics overlay. Shown when admin taps the 🐛 button in
// the Studio toolbar. Refreshes every 500ms while visible.
//
// What we can reliably measure:
//   • AudioContext state, sample rate, latency, currentTime
//   • Node counts (we keep manual tallies; Web Audio doesn't expose this)
//   • Per-track FX chain state from the existing refs
//   • requestAnimationFrame jitter (proxy for buffer-underrun risk)
//
// What we infer / approximate (and label as such):
//   • "Buffer health" — derived from frame jitter; if frames consistently
//     exceed 33ms while audio is playing, audio is likely stuttering
//   • Memory — `performance.memory` only works on Chromium; Safari shows
//     "Unavailable"
//
// Intentionally NOT measured: per-worklet CPU (would need worklet
// instrumentation), buffer underrun count (not exposed by Web Audio).
function StudioDebugOverlay({
  actxRef, gainNodesRef, fxNodesRef, chainsRef, trackAnalysersRef,
  tracks, isPlaying, isRecording, onClose,
}) {
  var [tick, setTick] = React.useState(0);
  var frameTimesRef   = React.useRef([]);
  var rafRef          = React.useRef(0);

  // Tick every 500ms while overlay is open
  React.useEffect(function() {
    var id = setInterval(function() { setTick(function(t) { return t + 1; }); }, 500);
    return function() { clearInterval(id); };
  }, []);

  // Track requestAnimationFrame jitter — captures the last 60 frames so we
  // can compute avg/max/p95. If frame time spikes past 33ms (the 30fps
  // boundary) consistently while audio is playing, that's a strong signal
  // the audio thread is also stuttering because the main thread is hogging
  // CPU.
  React.useEffect(function() {
    var last = performance.now();
    var alive = true;
    function loop(now) {
      if (!alive) return;
      var dt = now - last;
      last = now;
      var arr = frameTimesRef.current;
      arr.push(dt);
      if (arr.length > 60) arr.shift();
      rafRef.current = requestAnimationFrame(loop);
    }
    rafRef.current = requestAnimationFrame(loop);
    return function() { alive = false; cancelAnimationFrame(rafRef.current); };
  }, []);

  // ── Gather metrics for current render ─────────────────────────
  var actx = actxRef && actxRef.current;
  var ctxState     = actx ? actx.state : "no context";
  var sampleRate   = actx ? actx.sampleRate : 0;
  var baseLatency  = (actx && typeof actx.baseLatency   === "number") ? actx.baseLatency   : null;
  var outLatency   = (actx && typeof actx.outputLatency === "number") ? actx.outputLatency : null;
  var ctxTime      = actx ? actx.currentTime : 0;

  // Node counts — derived from refs since Web Audio doesn't expose this
  var gainCount       = Object.keys(gainNodesRef.current || {}).length;
  var analyserCount   = Object.keys(trackAnalysersRef.current || {}).length;
  var chainCount      = Object.keys(chainsRef.current || {}).length;
  var fxBlockCount    = Object.keys(fxNodesRef.current || {}).length;

  // FX node breakdown — sum across all tracks
  var fxBreakdown = { eq:0, comp:0, reverb:0, ocean:0, hdelay:0, doubler:0, nrm:0, trotten:0, bandpass:0, autotune:0, makeupGain:0, panner:0 };
  Object.keys(fxNodesRef.current || {}).forEach(function(tid) {
    var live = fxNodesRef.current[tid] || {};
    Object.keys(fxBreakdown).forEach(function(k) {
      if (live[k]) fxBreakdown[k] += 1;
    });
  });
  var totalFxNodes = Object.keys(fxBreakdown).reduce(function(sum, k) { return sum + fxBreakdown[k]; }, 0);

  // Frame time stats
  var frames = frameTimesRef.current.slice();
  frames.sort(function(a,b){ return a-b; });
  var fAvg = frames.length ? (frames.reduce(function(s,v){return s+v;}, 0) / frames.length) : 0;
  var fMax = frames.length ? frames[frames.length - 1] : 0;
  var fP95 = frames.length ? frames[Math.floor(frames.length * 0.95)] : 0;

  // Buffer-health heuristic. While playing, if p95 frame time > 33ms or
  // max > 50ms, we're likely seeing audio stutter. Color the indicator.
  var health = "good";
  var healthReason = "Frame times stable";
  if (isPlaying) {
    if (fMax > 80 || fP95 > 50) { health = "bad"; healthReason = "Frame stalls detected — audio likely stuttering"; }
    else if (fMax > 50 || fP95 > 33) { health = "warn"; healthReason = "Frame jitter — audio may glitch"; }
  } else {
    healthReason = "Not playing — start playback to measure";
  }
  var healthColor = health === "good" ? "#22C55E" : (health === "warn" ? "#F59E0B" : "#EF4444");

  // Memory (Chrome only; Safari returns nothing)
  var mem = (typeof performance !== "undefined" && performance.memory) ? performance.memory : null;
  var memUsedMB = mem ? (mem.usedJSHeapSize / 1024 / 1024).toFixed(1) : null;
  var memLimitMB = mem ? (mem.jsHeapSizeLimit / 1024 / 1024).toFixed(0) : null;

  // Track FX summary — count plugins ON per track
  var trackSummary = (tracks || []).map(function(t) {
    var fx = t.effects || {};
    var onPlugins = [];
    ["eq","compressor","reverb","ocean","hdelay","doubler","noiseremover","trotten","bandpass","autotune"].forEach(function(k){
      if (fx[k] && fx[k].on) onPlugins.push(k);
    });
    var chainLen = Array.isArray(fx.pluginChain) ? fx.pluginChain.length : 0;
    return { id: t.id, name: t.name || ("Track " + t.id), onPlugins: onPlugins, chainLen: chainLen };
  });

  function Section(props) {
    return (
      <div style={{ marginBottom: 10 }}>
        <div style={{ color: "#A78BFA", fontSize: 9, fontWeight: 800, letterSpacing: 1.2, marginBottom: 4 }}>
          {props.label}
        </div>
        <div style={{ background: "#0d0d0d", border: "1px solid #1f1f1f", borderRadius: 6, padding: "8px 10px", fontFamily: "monospace", fontSize: 11, color: "#ccc", lineHeight: 1.55 }}>
          {props.children}
        </div>
      </div>
    );
  }
  function Row(props) {
    return (
      <div style={{ display: "flex", justifyContent: "space-between", gap: 8 }}>
        <span style={{ color: "#888" }}>{props.k}</span>
        <span style={{ color: props.color || "#ddd", textAlign: "right" }}>{props.v}</span>
      </div>
    );
  }

  return (
    <div style={{
      position: "absolute",
      top: "calc(env(safe-area-inset-top) + 56px)",
      right: 10, left: 10,
      maxWidth: 360, marginLeft: "auto",
      zIndex: 6000,
      background: "rgba(10,10,10,0.97)",
      border: "1px solid #2a2a2a",
      borderRadius: 10,
      padding: "10px 12px 12px",
      fontFamily: "'DM Sans',sans-serif",
      boxShadow: "0 12px 32px rgba(0,0,0,0.7), 0 0 24px rgba(34,197,94,0.08)",
      maxHeight: "calc(100vh - 120px)",
      overflowY: "auto",
      WebkitOverflowScrolling: "touch",
    }}>
      {/* Header */}
      <div style={{ display: "flex", alignItems: "center", justifyContent: "space-between", marginBottom: 8 }}>
        <div style={{ display: "flex", alignItems: "center", gap: 6 }}>
          <span style={{ fontSize: 14 }}>🐛</span>
          <span style={{ color: "white", fontWeight: 800, fontSize: 12, letterSpacing: 0.5 }}>STUDIO DEBUG</span>
          <span style={{
            display: "inline-block", width: 8, height: 8, borderRadius: "50%",
            background: healthColor, boxShadow: "0 0 6px " + healthColor,
            marginLeft: 4,
          }} />
        </div>
        <button onClick={onClose} style={{
          background: "transparent", border: "none", color: "#888",
          fontSize: 16, cursor: "pointer", padding: "2px 6px",
        }}>×</button>
      </div>

      <Section label="HEALTH">
        <Row k="Status" v={health.toUpperCase()} color={healthColor} />
        <Row k="Reason" v={healthReason} />
      </Section>

      <Section label="AUDIO CONTEXT">
        <Row k="State"        v={ctxState} color={ctxState === "running" ? "#22C55E" : (ctxState === "suspended" ? "#F59E0B" : "#888")} />
        <Row k="Sample rate"  v={sampleRate ? (sampleRate + " Hz") : "—"} />
        <Row k="Base latency" v={baseLatency != null ? ((baseLatency * 1000).toFixed(2) + " ms") : "n/a"} />
        <Row k="Output latency" v={outLatency != null ? ((outLatency * 1000).toFixed(2) + " ms") : "n/a"} />
        <Row k="Uptime" v={ctxTime.toFixed(1) + "s"} />
      </Section>

      <Section label="AUDIO GRAPH">
        <Row k="Active chains"   v={chainCount} />
        <Row k="Track FX blocks" v={fxBlockCount} />
        <Row k="Volume gains"    v={gainCount} />
        <Row k="VU analysers"    v={analyserCount} />
        <Row k="Total FX nodes"  v={totalFxNodes} color={totalFxNodes > 30 ? "#F59E0B" : "#ddd"} />
        {totalFxNodes > 0 && (
          <div style={{ marginTop: 4, paddingTop: 4, borderTop: "1px solid #1f1f1f", color: "#666", fontSize: 10 }}>
            {Object.keys(fxBreakdown).filter(function(k){ return fxBreakdown[k] > 0; }).map(function(k) {
              return k + ":" + fxBreakdown[k];
            }).join("  ")}
          </div>
        )}
      </Section>

      <Section label="PLAYBACK">
        <Row k="Playing"    v={isPlaying ? "yes" : "no"}    color={isPlaying ? "#22C55E" : "#888"} />
        <Row k="Recording"  v={isRecording ? "yes" : "no"}  color={isRecording ? "#EF4444" : "#888"} />
        <Row k="Track count" v={(tracks || []).length} />
      </Section>

      <Section label="FRAME TIME (last ~1s)">
        <Row k="Avg" v={fAvg.toFixed(1) + " ms"} color={fAvg > 20 ? "#F59E0B" : "#22C55E"} />
        <Row k="p95" v={fP95.toFixed(1) + " ms"} color={fP95 > 33 ? "#F59E0B" : "#22C55E"} />
        <Row k="Max" v={fMax.toFixed(1) + " ms"} color={fMax > 50 ? "#EF4444" : (fMax > 33 ? "#F59E0B" : "#22C55E")} />
        <div style={{ marginTop: 4, paddingTop: 4, borderTop: "1px solid #1f1f1f", color: "#666", fontSize: 10, lineHeight: 1.4 }}>
          16.7ms = 60fps · 33ms = 30fps · 50ms+ = noticeable jank
        </div>
      </Section>

      <Section label="MEMORY">
        {mem ? (
          <Row k="JS heap" v={memUsedMB + " / " + memLimitMB + " MB"} />
        ) : (
          <div style={{ color: "#666", fontStyle: "italic" }}>Not exposed by this browser (Safari)</div>
        )}
      </Section>

      {trackSummary.length > 0 && (
        <Section label={"TRACKS (" + trackSummary.length + ")"}>
          {trackSummary.map(function(t, i) {
            return (
              <div key={t.id || i} style={{ marginBottom: i === trackSummary.length - 1 ? 0 : 4, paddingBottom: 4, borderBottom: i === trackSummary.length - 1 ? "none" : "1px solid #1a1a1a" }}>
                <div style={{ color: "#ddd", fontSize: 10 }}>
                  {t.name} <span style={{ color: "#666" }}>· {t.onPlugins.length} fx ON</span>
                </div>
                {t.onPlugins.length > 0 && (
                  <div style={{ color: "#888", fontSize: 9, marginTop: 1 }}>
                    {t.onPlugins.join(", ")}
                  </div>
                )}
              </div>
            );
          })}
        </Section>
      )}

      <div style={{ marginTop: 6, color: "#555", fontSize: 9, fontFamily: "'DM Sans',sans-serif", lineHeight: 1.4 }}>
        Refreshes every 500ms. Health is inferred from frame jitter — Web Audio doesn't expose buffer underruns directly.
      </div>
    </div>
  );
}


// =============================================================================
// STUDIO GUIDES — in-app help, opened from the HELP button in the top bar.
// Two tracks: Artist Pro (record/mix/master/export) and Producer Pro
// (compose/FX/drums/piano roll/tools). Content is structured as blocks so it
// renders consistently and image slots can be filled with screenshot URLs.
//
// To add a screenshot/GIF to any step: set its `img` to a hosted URL.
// Leave `img` as "" for text-only.
// =============================================================================
const BF_GUIDE_DATA = {
  artist: {
    title: "Artist Pro Guide",
    subtitle: "Record, mix, master & export your song",
    accent: "#3b82f6",
    sections: [
      {
        h: "1 · Add a beat to your project",
        body: "Every song starts with a beat. Open the + ADD TRACK button at the bottom-left of the timeline.",
        steps: [
          "Tap + ADD TRACK, then Upload Audio to bring in a beat you own (MP3/WAV).",
          "Or tap Sampler to chop a sample into playable slices.",
          "The beat lands as an audio clip on its own track — drag it left/right to position it.",
        ],
        img: "",
      },
      {
        h: "2 · Record your vocals",
        body: "Add a vocal track and arm it. Monitoring uses your phone mic with low latency.",
        steps: [
          "Tap + ADD TRACK → Record Vocals. A red vocal track appears.",
          "Put on wired headphones to avoid the beat bleeding into your mic.",
          "Press the round Record button in the transport bar. There's a count-in, then it captures your take.",
          "Press Stop when done. Your take appears as a clip. Record again on the same track to stack takes.",
        ],
        img: "",
      },
      {
        h: "3 · Comp & arrange takes",
        body: "Keep the best parts of each take.",
        steps: [
          "Tap a vocal clip to select it. Drag its edges to trim the start/end.",
          "Use the clip menu (long-press) for Fade In / Fade Out to smooth entrances and exits.",
          "Drag clips to line them up with the beat. Turn on Snap (⋯ menu) so they lock to the grid.",
        ],
        img: "",
      },
      {
        h: "4 · Mix",
        body: "Balance levels and add effects per track from the mixer.",
        steps: [
          "Open the Mixer. Each track has a fader, pan knob, and M/S (mute/solo) buttons.",
          "Set rough levels first: beat slightly lower, lead vocal on top, ad-libs underneath.",
          "Tap a track's FX slot to open the FX panel — EQ, compression, and reverb with visual graphs.",
          "On vocals: cut lows with the EQ high-pass, tame peaks with the compressor, add a touch of reverb for space.",
        ],
        img: "",
      },
      {
        h: "5 · Master",
        body: "Glue the full mix together so it's loud and balanced.",
        steps: [
          "Select the Master strip in the mixer.",
          "Add gentle compression on the master to control dynamics.",
          "Use the master EQ for broad tone shaping — a small high-shelf lift adds air.",
          "Aim for a full sound without clipping — watch the master meter stays out of the red.",
        ],
        img: "",
      },
      {
        h: "6 · Export your song",
        body: "Bounce everything down to a single audio file.",
        steps: [
          "Open the ··· project menu → Export.",
          "Choose your format. The app renders the full arrangement with all FX and levels baked in.",
          "Save or share the exported file. That's your finished song.",
        ],
        img: "",
      },
    ],
  },
  producer: {
    title: "Producer Pro Guide",
    subtitle: "Compose & produce beats in Studio",
    accent: "#c026d3",
    sections: [
      {
        h: "1 · Start with a tempo & key",
        body: "Set the foundation before you build.",
        steps: [
          "Open Settings (gear icon) to set your BPM and project key.",
          "Turn on the Metronome and Beat Grid so everything stays in time.",
          "Snap keeps notes and clips locked to the grid — leave it on while building.",
        ],
        img: "",
      },
      {
        h: "2 · Build a drum pattern",
        body: "The drum sequencer is a step grid — tap cells to place hits.",
        steps: [
          "Tap + ADD TRACK → Drum Sequencer. The channel rack opens.",
          "Tap cells across the row to place kicks, snares, and hats on the beat.",
          "Per-step controls: pitch, pan, nudge (shift), and ratchet (repeat) for rolls.",
          "Set a different loop length per channel for polyrhythms. Long-press a row label for the full graph editor.",
        ],
        img: "",
      },
      {
        h: "3 · Play melodies with the piano roll",
        body: "Add a MIDI instrument and draw notes.",
        steps: [
          "Tap + ADD TRACK → MIDI Instrument. Pick a sound from the instrument library (incl. your custom soundfonts).",
          "Tap the clip to open the piano roll. Tap the grid to place notes — the first note is two squares wide.",
          "Drag a note's edge to resize, drag its body to move. Lasso-select multiple notes to move them together.",
          "Use the ⋯ menu for snap value and note-cut (mono) mode.",
        ],
        img: "",
      },
      {
        h: "4 · Chop samples with the Sampler",
        body: "Turn any audio into a playable instrument.",
        steps: [
          "Tap + ADD TRACK → Sampler, then import an audio file.",
          "The sampler auto-slices the sample. Each slice maps to a piano-roll note.",
          "Play the slices melodically in the piano roll, or sequence them in the drum grid.",
        ],
        img: "",
      },
      {
        h: "5 · Shape your sound with FX",
        body: "Every track has a professional FX chain.",
        steps: [
          "Tap a track's FX slot to open the panel: parametric EQ, compressor, and reverb.",
          "The EQ graph is draggable — pull bands up/down and sweep frequency. It uses correct DSP math.",
          "Use the compressor to control dynamics; watch the curve graph respond.",
          "Add reverb for depth. Save your favourite chains as presets (admin) for reuse.",
        ],
        img: "",
      },
      {
        h: "6 · Arrange the full beat",
        body: "Lay out sections across the timeline.",
        steps: [
          "Drum patterns and MIDI clips drop onto the timeline as blocks — duplicate and arrange them into intro/verse/chorus.",
          "Copy/paste preserves source channels. Use the project ··· menu for copy/paste and duplicate.",
          "Use loop markers to focus on a section while you refine it.",
          "Undo/redo captures every timeline edit, so experiment freely.",
        ],
        img: "",
      },
      {
        h: "7 · Tools & extras",
        body: "Handy things that speed up your workflow.",
        steps: [
          "Pitch-shift and time-stretch audio clips from the clip menu (long-press a clip).",
          "Per-step ratchet + pitch in the drum sequencer = fast hi-hat rolls and 808 glides.",
          "No autosave by design — save deliberately from the ··· menu (playback stops, then it saves).",
          "Custom drum kits you buy in the marketplace appear under My Kits in the drum sequencer.",
        ],
        img: "",
      },
    ],
  },
};

function StudioGuides({ onClose, defaultTrack }) {
  const [track, setTrack] = React.useState(defaultTrack || "artist");
  const g = BF_GUIDE_DATA[track];
  const A = g.accent;

  return (
    <div style={{
      position: "fixed", inset: 0, zIndex: 100000,
      background: "rgba(8,5,18,0.92)", backdropFilter: "blur(6px)",
      display: "flex", flexDirection: "column",
      fontFamily: "'DM Sans', sans-serif",
      paddingTop: "var(--bf-safe-top, env(safe-area-inset-top))",
    }}>
      <div style={{
        display: "flex", alignItems: "center", gap: 12,
        padding: "14px 16px", borderBottom: "1px solid rgba(167,139,250,0.18)",
        background: "linear-gradient(180deg, #14102a, #0f0a1f)", flexShrink: 0,
      }}>
        <div style={{
          width: 32, height: 32, borderRadius: 9,
          background: "linear-gradient(135deg, rgba(167,139,250,0.25), rgba(124,58,237,0.18))",
          border: "1px solid rgba(167,139,250,0.4)",
          display: "flex", alignItems: "center", justifyContent: "center",
          color: "#a78bfa", flexShrink: 0,
        }}>
          <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.2" strokeLinecap="round" strokeLinejoin="round"><circle cx="12" cy="12" r="10"/><path d="M9.09 9a3 3 0 0 1 5.83 1c0 2-3 3-3 3"/><line x1="12" y1="17" x2="12.01" y2="17"/></svg>
        </div>
        <div style={{ flex: 1, minWidth: 0 }}>
          <div style={{ color: "#e8e6f0", fontWeight: 800, fontSize: 15, letterSpacing: 0.3 }}>Studio Help</div>
          <div style={{ color: "#7a6ea0", fontSize: 11 }}>Guides & walkthroughs</div>
        </div>
        <button onClick={onClose} style={{
          width: 34, height: 34, borderRadius: "50%",
          background: "rgba(167,139,250,0.08)", border: "1px solid rgba(167,139,250,0.25)",
          color: "#c4b5fd", cursor: "pointer", flexShrink: 0,
          display: "flex", alignItems: "center", justifyContent: "center",
        }}>
          <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round"><line x1="18" y1="6" x2="6" y2="18"/><line x1="6" y1="6" x2="18" y2="18"/></svg>
        </button>
      </div>

      <div style={{ display: "flex", gap: 8, padding: "12px 16px", flexShrink: 0 }}>
        {[
          { id: "artist",   label: "Artist Pro",   c: "#3b82f6" },
          { id: "producer", label: "Producer Pro", c: "#c026d3" },
        ].map(function(t) {
          const active = track === t.id;
          return (
            <button key={t.id} onClick={function(){ setTrack(t.id); }} style={{
              flex: 1, padding: "10px", borderRadius: 10, cursor: "pointer",
              border: active ? "1.5px solid " + t.c : "1.5px solid #2a2050",
              background: active ? t.c + "22" : "transparent",
              color: active ? "#fff" : "#7a6ea0", fontWeight: 800, fontSize: 13,
              boxShadow: active ? "0 0 14px " + t.c + "40" : "none",
              transition: "none",
            }}>{t.label}</button>
          );
        })}
      </div>

      <div
        ref={function(el){
          if (!el || el._bfGuideScrollSetup) return;
          el._bfGuideScrollSetup = true;
          // JS-driven touch scroll with momentum — native overflow scrolling
          // is unreliable inside fixed-position overlays on iOS Safari. Same
          // pattern used by the bank preset sheet. Scroll engages anywhere,
          // including on buttons/links; a >8px drag is treated as a scroll and
          // its trailing click is swallowed so taps still select.
          let active = false, startY = 0, startScroll = 0;
          let lastY = 0, lastT = 0, vel = 0, moved = false;
          const MOVE_THRESHOLD = 8;
          let raf = 0;
          const cancelMomentum = function(){ if (raf) { cancelAnimationFrame(raf); raf = 0; } };
          const onTouchStart = function(e){
            if (!e.touches || !e.touches[0]) return;
            cancelMomentum();
            active = true; moved = false;
            startY = e.touches[0].clientY;
            startScroll = el.scrollTop;
            lastY = startY; lastT = performance.now(); vel = 0;
          };
          const onTouchMove = function(e){
            if (!active || !e.touches || !e.touches[0]) return;
            const y = e.touches[0].clientY;
            const dy = y - startY;
            if (!moved && Math.abs(dy) > MOVE_THRESHOLD) moved = true;
            el.scrollTop = Math.max(0, Math.min(el.scrollHeight - el.clientHeight, startScroll - dy));
            const now = performance.now();
            const dt = now - lastT;
            if (dt > 0) vel = (lastY - y) / dt;
            lastY = y; lastT = now;
            if (e.cancelable) { try { e.preventDefault(); } catch(_){} }
          };
          const onTouchEnd = function(){
            if (!active) return;
            active = false;
            if (moved) {
              const swallow = function(ev){ ev.stopPropagation(); ev.preventDefault(); el.removeEventListener("click", swallow, true); };
              el.addEventListener("click", swallow, true);
              setTimeout(function(){ try { el.removeEventListener("click", swallow, true); } catch(_){} }, 350);
            }
            let v = vel * 16;
            const step = function(){
              if (Math.abs(v) < 0.5) { raf = 0; return; }
              el.scrollTop = Math.max(0, Math.min(el.scrollHeight - el.clientHeight, el.scrollTop + v));
              v *= 0.94;
              raf = requestAnimationFrame(step);
            };
            if (Math.abs(vel) > 0.05) raf = requestAnimationFrame(step);
          };
          el.addEventListener("touchstart",  onTouchStart, { passive: false });
          el.addEventListener("touchmove",   onTouchMove,  { passive: false });
          el.addEventListener("touchend",    onTouchEnd,   { passive: true });
          el.addEventListener("touchcancel", onTouchEnd,   { passive: true });
        }}
        style={{ flex: 1, overflowY: "scroll", WebkitOverflowScrolling: "touch", touchAction: "pan-y", overscrollBehavior: "contain", padding: "4px 16px 40px" }}>
        <div style={{ color: A, fontWeight: 900, fontSize: 20, fontFamily: "'Bebas Neue', sans-serif", letterSpacing: 1, marginBottom: 2 }}>{g.title}</div>
        <div style={{ color: "#9590a8", fontSize: 12, marginBottom: 18 }}>{g.subtitle}</div>

        {g.sections.map(function(sec, i) {
          return (
            <div key={i} style={{
              background: "linear-gradient(180deg, #1a1530 0%, #14102a 100%)",
              border: "1px solid rgba(167,139,250,0.18)", borderRadius: 14,
              padding: "16px", marginBottom: 12,
            }}>
              <div style={{ color: "#e8e6f0", fontWeight: 800, fontSize: 15, marginBottom: 6, display: "flex", alignItems: "center", gap: 8 }}>
                <span style={{ width: 4, height: 16, borderRadius: 2, background: A, boxShadow: "0 0 8px " + A, flexShrink: 0 }} />
                {sec.h}
              </div>
              {sec.body ? <div style={{ color: "#b8b0cc", fontSize: 13, lineHeight: 1.55, marginBottom: sec.steps ? 10 : 0 }}>{sec.body}</div> : null}
              {sec.img ? (
                <img src={sec.img} alt="" style={{ width: "100%", borderRadius: 10, margin: "8px 0", border: "1px solid rgba(167,139,250,0.2)" }} />
              ) : null}
              {sec.steps ? (
                <div style={{ display: "flex", flexDirection: "column", gap: 8, marginTop: 4 }}>
                  {sec.steps.map(function(st, j) {
                    return (
                      <div key={j} style={{ display: "flex", gap: 10, alignItems: "flex-start" }}>
                        <div style={{
                          width: 20, height: 20, borderRadius: "50%", flexShrink: 0,
                          background: A + "22", border: "1px solid " + A + "66",
                          color: A, fontSize: 11, fontWeight: 800,
                          display: "flex", alignItems: "center", justifyContent: "center",
                          marginTop: 1,
                        }}>{j + 1}</div>
                        <div style={{ color: "#c8c2d8", fontSize: 13, lineHeight: 1.5, flex: 1 }}>{st}</div>
                      </div>
                    );
                  })}
                </div>
              ) : null}
            </div>
          );
        })}

        <div style={{ textAlign: "center", color: "#5a4e7a", fontSize: 11, padding: "10px 0", lineHeight: 1.5 }}>
          More guides coming soon. Tap HELP anytime to reopen this.
        </div>
      </div>
    </div>
  );
}

function StudioScreen({ user, onExit, savedLyrics, onEditLyric, onNewLyric, onRegisterTransport }) {

  // ── Constants ─────────────────────────────────────────────────
  // PIXELS_PER_SECOND. Lowered from 100 → 85 to zoom the timeline out a
  // bit more — at the same H% setting, the user now sees more of their
  // arrangement on screen. The user-displayed H% number is unchanged
  // (it shows the raw zoom multiplier).
  const PPS         = 85;
  const SIDEBAR_W   = 158;       // left column: Logic Pro-style track headers
  const RULER_H     = 32;        // timeline ruler height
  const PLAYHEAD_X  = 0;         // playhead flush with sidebar edge — no gap

  // ── Track array: [{id, name, type, audioBuffer, url, isMuted, isSoloed, startTime, duration}]
  const [tracks, setTracks, undoTracks, redoTracks, canUndo, canRedo, beginTracksGesture, endTracksGesture] = useHistory([]);
  const [bpm,          setBpm]          = useState(120);
  // Global tempo change for a BAR-ANCHORED timeline. Clip data is stored in
  // seconds; on a tempo change we rescale those seconds by oldBpm/newBpm so
  // every clip keeps its musical position (same bar) and length (same number
  // of bars). Because the pixel layout scales with bpm (effectivePPS ∝ bpm),
  // these rescaled seconds map to the SAME pixels, so nothing moves or resizes
  // on screen — but a 3-bar drum clip stays exactly 3 bars and plays in time.
  //
  // Per type:
  //  • drum / midi clips: scale startTime + duration (+ patternDuration, notes,
  //    and notes-mode channel note offsets) — all fully tempo-relative.
  //  • audio (beat/vocal) clips: scale startTime only; keep the true audio
  //    duration (audio is never time-stretched), so it stays anchored to its
  //    bar without the clip box lying about the sample length.
  const setBpmWithRescale = React.useCallback(function(updater) {
    setBpm(function(prevBpm){
      const nextBpm = typeof updater === "function" ? updater(prevBpm) : updater;
      const safeNext = Math.max(40, Math.min(220, Number(nextBpm) || prevBpm));
      if (safeNext === prevBpm) return prevBpm;
      const ratio = prevBpm / safeNext;  // seconds scale by old/new tempo
      const scaleNotes = function(notes){
        if (!Array.isArray(notes)) return notes;
        return notes.map(function(n){
          return Object.assign({}, n, {
            start:    (typeof n.start    === "number") ? n.start    * ratio : n.start,
            duration: (typeof n.duration === "number") ? n.duration * ratio : n.duration,
          });
        });
      };
      setTracks(function(prevTracks){
        return (prevTracks || []).map(function(track){
          if (!track) return track;
          const next = Object.assign({}, track);
          const isAudioTrack = (track.type === "beat" || track.type === "vocal");

          // Clips
          if (Array.isArray(track.clips)) {
            next.clips = track.clips.map(function(clip){
              if (!clip) return clip;
              const c = Object.assign({}, clip);
              if (typeof c.startTime === "number") c.startTime = c.startTime * ratio;
              const isAudioClip = isAudioTrack
                || !!(c.audioBuffer || c.audioB64 || c.url || c.idbKey);
              if (isAudioClip && !Array.isArray(c.notes)) {
                // keep real audio duration; start already scaled
                return c;
              }
              if (typeof c.duration === "number")        c.duration        = c.duration * ratio;
              if (typeof c.patternDuration === "number") c.patternDuration = c.patternDuration * ratio;
              if (typeof c.trimStart === "number")       c.trimStart       = c.trimStart * ratio;
              if (typeof c.trimEnd === "number")         c.trimEnd         = c.trimEnd * ratio;
              if (Array.isArray(c.notes)) c.notes = scaleNotes(c.notes);
              return c;
            });
          }

          // Drum-sequencer notes-mode channels store note seconds on the
          // channel; scale them + tag with the new bpm.
          if (track.type === "drumSequencer" && Array.isArray(track.patterns)) {
            next.patterns = track.patterns.map(function(pat){
              if (!pat || !Array.isArray(pat.channels)) return pat;
              const channels = pat.channels.map(function(ch){
                if (!ch || ch.mode !== "notes" || !Array.isArray(ch.notes) || ch.notes.length === 0) return ch;
                return Object.assign({}, ch, { notesBpm: safeNext, notes: scaleNotes(ch.notes) });
              });
              return Object.assign({}, pat, { channels: channels });
            });
          }

          // Legacy flat audio buffer directly on a beat track — start only.
          if (next.audioBuffer && typeof next.startTime === "number") {
            next.startTime = next.startTime * ratio;
          }
          return next;
        });
      });
      return safeNext;
    });
  }, [setTracks]);
  const [timeSigNum,   setTimeSigNum]   = useState(4);
  const [zoom,         setZoom]         = useState(0.1); // start at 10% H-zoom
  const [vZoom,        setVZoom]        = useState(0.99); // vertical track-height zoom — start at 100%

  // Base row height. Bumped from 92 → 110 to give the pan knob (last row in
  // the track header) full vertical space. The user-displayed V% reading is
  // unchanged — only the underlying pixel multiplier is larger so each row
  // gets a bit more headroom at every zoom level.
  const TRACK_H     = Math.round(110 * vZoom);
  const [snapToGrid,   setSnapToGrid]   = useState(true);
  const [projectKey,   setProjectKey]   = useState("C major");
  const [projectName,  setProjectName]  = useState("New Project");

  // ── Lyrics workspace overlay ─────────────────────────────────
  // BandLab-style: a side panel that slides in from the right so the
  // user can read or write lyrics while recording. Reads `savedLyrics`
  // from the App; tapping a saved lyric opens the full editor.
  const [lyricsPanelOpen, setLyricsPanelOpen] = useState(false);

  // ── Studio welcome modal — first open per session ─────────────
  // Shows once when Studio opens to set expectations about beta
  // status + iOS Web Audio limits. Dismissed for the session via
  // a window-level flag (not localStorage — we want users to see
  // it each new session as a reminder).
  const [showStudioWelcome, setShowStudioWelcome] = useState(function() {
    try {
      if (typeof window === "undefined") return false;
      return !window.__bfStudioWelcomeShown;
    } catch (_) { return false; }
  });
  useEffect(function() {
    if (showStudioWelcome) {
      try { window.__bfStudioWelcomeShown = true; } catch (_) {}
    }
  }, [showStudioWelcome]);

  // ── Playback ──────────────────────────────────────────────────
  const [isPlaying,    setIsPlaying]    = useState(false);
  const [isRecording,  setIsRecording]  = useState(false);

  // FX-on-record warning modal. Shown when the user tries to record onto
  // a track that already has FX enabled — chained effects add audible
  // latency through the mic monitor loop. State holds the pending track
  // id so the user can confirm + continue, dismiss permanently, or cancel.
  // fxRecordWarn state removed — PDC + drift compensation now handle
  // FX-on-channel recording automatically. No warning needed.

  // Returns true if the given track has any audio FX enabled — used to
  // warn the user before recording into an effected chain.
  function trackHasFx(track) {
    if (!track || !track.effects) return false;
    var fx = track.effects;
    // Plugin chain
    if (Array.isArray(fx.pluginChain) && fx.pluginChain.length > 0) return true;
    // Individual effect flags (in case pluginChain isn't the source of truth)
    var keys = ["reverb","delay","eq","compressor","autotune","saturation","chorus","flanger","phaser","distortion","filter","limiter"];
    for (var i = 0; i < keys.length; i++) {
      var f = fx[keys[i]];
      if (f && f.on === true) return true;
    }
    return false;
  }
  const [currentTime,  setCurrentTime]  = useState(0);
  // Version counter bumped by seek handlers to force the playhead RAF
  // useEffect to re-run. Without this, a seek-while-playing toggle
  // (setIsPlaying false→true) batches into no-op for isPlaying, the
  // RAF dep list sees no change, and the RAF stays cancelled — visible
  // as a frozen playhead after jump-to-start during SONG.
  const [seekVersion, setSeekVersion] = useState(0);
  const [loopEnabled,  setLoopEnabled]  = useState(false);
  const [loopIn,       setLoopIn]       = useState(0);
  const [loopOut,      setLoopOut]      = useState(0);

  // ── UI ────────────────────────────────────────────────────────
  const [contextMenu,  setContextMenu]  = useState(null); // {region, x, y}
  // ── Multi-select lasso ───────────────────────────────────────
  const [selBox,       setSelBox]       = useState(null);  // {x,y,w,h} in px relative to scroll container
  const [selClipIds,   setSelClipIds]   = useState(new Set()); // selected clip ids
  const selBoxRef      = useRef(null);   // live drag state {startX,startY,scrollX}

  // ── Clip clipboard ──
  // Holds a snapshot of a clip (or array of clips) the user has Copied.
  // Survives across rack open/close, project edits, etc. Cleared only on
  // project switch. Paste places at currentTime on the SAME track that
  // the clip came from. We deliberately store the original track id so
  // the paste target is unambiguous even if the user has tapped a
  // different track since copying.
  //   shape: { trackId, clips: [<clip data>] } | null
  const [clipboardClips, setClipboardClips] = useState(null);
  const trimActiveRef   = useRef(false);  // true while a trim drag is in progress
  const longPressRef   = useRef(null);   // setTimeout handle for long-press detection
  const [showSettings, setShowSettings] = useState(false);
  const [showProjects, setShowProjects] = useState(false);
  const [showProjMenu, setShowProjMenu] = useState(false);
  const [showGuides, setShowGuides] = useState(false);
  const [showAddMenu,  setShowAddMenu]  = useState(false);
  // Sampler import overlay — open when user taps "Sampler" in + menu.
  // Stores either `true` (new sampler) or a track id (edit existing).
  const [samplerImportOpen, setSamplerImportOpen] = useState(false);
  // Sampler editor overlay — open when user double-taps a sampler track's
  // clip (or hits the sampler track's instrument label). Lets them
  // re-slice, adjust slice points, change root pitch, swap sample.
  const [samplerEditorTrackId, setSamplerEditorTrackId] = useState(null);
  const [renamingProj, setRenamingProj] = useState(false);
  // Admin debug overlay — toggled by the 🐛 button in the toolbar
  // (only visible to admins). Shows AudioContext/node/FX/frame metrics.
  const [showDebug,    setShowDebug]    = useState(false);
  const [unsavedAlert, setUnsavedAlert] = useState(false);
  const [showNamePrompt, setShowNamePrompt] = useState(false); // name-before-save modal
  const [pendingNameAction, setPendingNameAction] = useState(null); // "exit" | "new"
  const [pendingName, setPendingName] = useState("");
  const [isSaved,      setIsSaved]      = useState(false);
  const [savedProjects,setSavedProjects]= useState([]);
  // Phase 3: cloud-only projects (in MongoDB but not local). Fetched
  // when the picker opens. Merged with savedProjects for display.
  // Each entry has { cloudId, name, bpm, key, updated_at, size_bytes }.
  const [cloudProjects, setCloudProjects] = useState([]);
  const [cloudFetchState, setCloudFetchState] = useState("idle"); // "idle"|"loading"|"error"|"done"
  // Admin-managed FX presets fetched from backend on Studio mount.
  // Merged with hardcoded FX_PRESETS for the preset picker (with hidden
  // built-ins filtered out by admins via hide-builtin endpoint).
  const [userPresets, setUserPresets] = React.useState([]);
  const [hiddenBuiltins, setHiddenBuiltins] = React.useState([]);
  // Merged preset list — built-ins (minus hidden) followed by admin-saved.
  // useMemo so the reference stays stable while FxPanel is open and lets
  // the memo comparator skip re-renders.
  const mergedPresets = React.useMemo(function(){
    var hiddenSet = new Set(hiddenBuiltins);
    var builtins = (typeof FX_PRESETS !== "undefined" ? FX_PRESETS : [])
      .filter(function(p){ return !hiddenSet.has(p.id); })
      .map(function(p){ return Object.assign({}, p, { kind: "builtin" }); });
    return builtins.concat(userPresets || []);
  }, [userPresets, hiddenBuiltins]);
  // Fetch admin-managed presets once on Studio mount. Public endpoint,
  // no auth required. Soft-fails (sticks with built-ins) if backend is
  // unreachable — the picker still works.
  React.useEffect(function(){
    var alive = true;
    apiFetch("/api/fx-presets").then(function(res){
      if (!alive || !res) return;
      if (Array.isArray(res.presets)) setUserPresets(res.presets);
      if (Array.isArray(res.hidden_builtins)) setHiddenBuiltins(res.hidden_builtins);
    }).catch(function(e){
      console.warn("[Studio] Could not load FX presets:", e);
    });
    return function(){ alive = false; };
  }, []);
  const [saveStatus,   setSaveStatus]   = useState("");
  const [error,        setError]        = useState("");
  const [isDecodingFile, setIsDecodingFile] = useState(false);
  const [countIn,      setCountIn]      = useState(0);
  const [exporting,    setExporting]    = useState(false);
  const [exportMsg,    setExportMsg]    = useState("");
  // Pre-export warning screen state. Set true when the user picks
  // "Export Mix" from the project menu; the modal explains the rules
  // (keep screen on, don't switch apps, don't accept calls) and only
  // when the user taps "Start Export" does the actual export run.
  const [showExportWarning, setShowExportWarning] = useState(false);
  // Persistent debug log — stored in sessionStorage so it survives the
  // post-export Studio unmount/remount (a useRef is wiped on remount).
  // Admin-only debugging aid for the soundfont export work.
  const DEBUG_LOG_KEY = "bf_debug_log";
  const [debugLogVersion, setDebugLogVersion] = useState(0);
  const [showDebugPanel, setShowDebugPanel] = useState(false);
  const bfReadDebugLog = function() {
    try {
      const raw = sessionStorage.getItem(DEBUG_LOG_KEY);
      return raw ? JSON.parse(raw) : [];
    } catch(_) { return []; }
  };
  const bfDebugLog = function(line) {
    // Delegate to the module-level writer (single shared sessionStorage
    // store, also reachable by top-level engine functions), then bump the
    // version so the open panel re-renders.
    try { if (typeof window !== "undefined" && typeof window.__bfDebugLog === "function") window.__bfDebugLog(line); } catch(_){}
    try { setDebugLogVersion(function(v){ return v + 1; }); } catch(_){}
  };
  // Export progress 0..1. Updated at known phase boundaries (preparing,
  // measuring, mixing each track, TQ analysis, rendering, encoding). Used
  // by the export overlay's progress bar — a CSS spinner alone freezes on
  // iOS when the main thread is locked by heavy DSP work, so we drive a
  // visible progress bar from explicit state writes instead.
  const [exportProgress, setExportProgress] = useState(0);
  const [bpmDetecting, setBpmDetecting] = useState(false);
  const [detectedBpm,  setDetectedBpm]  = useState(null);
  // ── Advanced BPM engine state ─────────────────────────────────
  const [bpmConfidence,  setBpmConfidence]  = useState(null);   // 0–1
  const [bpmBandConf,    setBpmBandConf]    = useState(null);   // {low,mid,high} 0–1 each
  const [beatPositions,  setBeatPositions]  = useState([]);     // beat timestamps in seconds
  const [onsetTimestamps,setOnsetTimestamps]= useState([]);     // onset timestamps in seconds
  const [bpmProgress,    setBpmProgress]    = useState(0);      // 0–100 analysis progress
  const [bpmDetectMsg,   setBpmDetectMsg]   = useState("");
  const [showBeatGrid,   setShowBeatGrid]   = useState(true);   // overlay toggle
  const [tapTimes,       setTapTimes]       = useState([]);     // tap-tempo timestamps
  const [bpmSource,      setBpmSource]      = useState(null);   // "auto"|"tap"|"manual"
  const [swingAmount,    setSwingAmount]    = useState(0);      // 0–1 estimated swing
  const [bpmCandidates,  setBpmCandidates]  = useState([]);     // secondary BPM candidates
  const [bpmTemporal,    setBpmTemporal]    = useState(null);   // temporal consistency 0–1
  const [bpmWorkerRef]                      = useState(() => ({ current: null }));

  const [metronomeOn,  setMetronomeOn]  = useState(false);
  const [inputDevice,  setInputDevice]  = useState("default");
  const [recTrail,     setRecTrail]     = useState([]);
  const [recTrackId,   setRecTrackId]   = useState(null);
  const [showTSPicker, setShowTSPicker] = useState(false);
  const [showKeyPick,  setShowKeyPick]  = useState(false);
  const [draggingReg,  setDraggingReg]  = useState(null);
  const [showMixer,    setShowMixer]     = useState(false);
  const [fxTrackId,    setFxTrackId]     = useState(null);
  // Mirror fxTrackId into a ref so the rAF playback loop (a useEffect
  // that captures values at setup) can read the latest value without
  // restarting. Used to skip the 100ms setCurrentTime tick when FX
  // panel is open — the time display in the toolbar isn't visible
  // during FX mode anyway, and skipping it avoids re-rendering the
  // entire FX tree 10x/sec during playback (which was choking the
  // main thread and causing iOS to drop taps on transport buttons).
  const fxTrackIdRef = useRef(null);
  React.useEffect(function(){ fxTrackIdRef.current = fxTrackId; }, [fxTrackId]);
  const [masterVolume, setMasterVolume]  = useState(1); // independent master fader 0..1.5
  // Manual recording offset in milliseconds. Default 80 (the previous
  // hardcoded Safari latency compensation). Adjustable -200..+200ms in
  // 10ms steps. Persisted to localStorage so it survives reloads.
  //   POSITIVE value = subtracts from clip start time = clip moves EARLIER
  //   NEGATIVE value = adds to clip start time = clip moves LATER
  // If your recordings come in early, REDUCE this value.
  // If your recordings come in late, INCREASE this value.
  const [recOffsetMs, setRecOffsetMs] = useState(function(){
    try {
      var raw = localStorage.getItem("bf_rec_offset_ms");
      if (raw === null) return 80;
      var n = parseInt(raw, 10);
      if (isNaN(n)) return 80;
      return Math.max(-200, Math.min(200, n));
    } catch(e) { return 80; }
  });
  // Mirror to a ref so the recording flow (closure captured deeper) reads
  // the live value without us having to wire it through every callback.
  const recOffsetMsRef = useRef(80);
  React.useEffect(function(){
    recOffsetMsRef.current = recOffsetMs;
    try { localStorage.setItem("bf_rec_offset_ms", String(recOffsetMs)); } catch(e) {}
  }, [recOffsetMs]);
  // Additional FX-induced recording offset. Applied on top of recOffsetMs
  // when ANY track has FX enabled at recording start. FX adds audio-graph
  // latency that the base offset can't account for, and it varies per
  // device. Range -300..+300ms. Default 0 — user calibrates via the
  // Calibrate button or slider in Studio Settings.
  const [fxRecOffsetMs, setFxRecOffsetMs] = useState(function(){
    try {
      var raw = localStorage.getItem("bf_fx_rec_offset_ms");
      if (raw === null) return 0;
      var n = parseInt(raw, 10);
      if (isNaN(n)) return 0;
      return Math.max(-300, Math.min(300, n));
    } catch(e) { return 0; }
  });
  const fxRecOffsetMsRef = useRef(0);
  React.useEffect(function(){
    fxRecOffsetMsRef.current = fxRecOffsetMs;
    try { localStorage.setItem("bf_fx_rec_offset_ms", String(fxRecOffsetMs)); } catch(e) {}
  }, [fxRecOffsetMs]);
  // Calibration modal visibility. Opened from the "Calibrate" button next
  // to the FX offset slider in Studio Settings.
  const [calibrateOpen, setCalibrateOpen] = useState(false);
  // Performance Mode — when enabled, all visualisations short-circuit:
  //   • VU meters freeze (no FFT reads, no React state updates)
  //   • Toolbar time display freezes (no 10Hz setCurrentTime calls)
  //   • Playhead still moves smoothly via DOM ref (uses refs not state)
  //   • Audio engine untouched
  // Intended for screen recording, slow devices, or low-power mode.
  // Persisted to localStorage as bf_perf_mode.
  const [performanceMode, setPerformanceMode_state] = useState(function(){
    try { return localStorage.getItem("bf_perf_mode") === "1"; } catch(e) { return false; }
  });
  // React setter wraps state setter — module-scope flag is synced via the
  // effect below so all paths stay in agreement.
  const setPerformanceMode = function(v){ setPerformanceMode_state(!!v); };
  // Sync module-scope flag whenever React state changes. The module-scope
  // _setPerformanceMode is what useAnalyser reads at module scope — without
  // this sync, the toggle wouldn't affect VU meters.
  React.useEffect(function(){
    _setPerformanceMode(performanceMode);
    try { localStorage.setItem("bf_perf_mode", performanceMode ? "1" : "0"); } catch(e) {}
  }, [performanceMode]);
  // On initial mount, also sync the module flag (handles localStorage restore
  // before the effect fires). The effect above runs after first render but
  // useAnalyser hooks in child components mount earlier in the tree.
  React.useEffect(function(){ _setPerformanceMode(performanceMode); }, []);
  const fxUpdRef = useRef(null); // stable ref so memoized FX panel always calls latest upd
  const applyPresetRef = useRef(null); // stable ref so memoized FX panel always calls latest preset applier
  // FX-internal transport callbacks. The challenge: FxPanel is memo'd,
  // so if we pass togglePlay directly it caches the FIRST render's
  // closure forever and reads stale isPlaying. If we add togglePlay to
  // the memo comparator, FxPanel re-renders every parent render (since
  // togglePlay is a new reference each time), defeating the memo.
  //
  // Solution: stable PROXY functions defined once via useMemo. They
  // read fxTogglePlayRef.current and fxRecordRef.current AT CALL TIME,
  // not at render time. The refs are updated each parent render with
  // fresh closures, so the proxies always invoke the latest function
  // with the latest state. FxPanel sees the same proxy reference
  // forever and memo stays effective.
  const fxTogglePlayRef = useRef(null);
  const fxRecordRef     = useRef(null);
  const fxTogglePlayProxy = React.useMemo(function(){
    return function(){ if (fxTogglePlayRef.current) fxTogglePlayRef.current(); };
  }, []);
  const fxRecordProxy = React.useMemo(function(){
    return function(){ if (fxRecordRef.current) fxRecordRef.current(); };
  }, []);
  // Same stable-proxy pattern for admin preset save/delete. Refs are
  // assigned fresh each render so the proxies always invoke the
  // latest closure with current state (fx, fxTrackId, setUserPresets).
  const fxSavePresetRef   = useRef(null);
  const fxDeletePresetRef = useRef(null);
  const fxSavePresetProxy = React.useMemo(function(){
    return function(){ if (fxSavePresetRef.current) fxSavePresetRef.current(); };
  }, []);
  const fxDeletePresetProxy = React.useMemo(function(){
    return function(p){ if (fxDeletePresetRef.current) fxDeletePresetRef.current(p); };
  }, []);
  const [showTakes,    setShowTakes]     = useState(null);
  const [trimmingClip, setTrimmingClip]  = useState(null);
  const [monitoringOn, setMonitoringOn] = useState(false);
  const [monitorVol,   setMonitorVol]   = useState(0.8);
  const micInputGainRef = useRef(1.0); // default 0dB (unity gain)
  const [micInputGain, setMicInputGainState] = useState(1.0); // 0.25–4.0 range (-12..+12dB)
  const [headphonesIn, setHeadphonesIn] = useState(false);
  // Mirror headphonesIn into a ref so plain functions (restoreIOSSpeaker, doPlay)
  // can read the live value without going through closures.
  const headphonesInRef = useRef(false);
  React.useEffect(function() { headphonesInRef.current = headphonesIn; }, [headphonesIn]);
  // Live ref for Bluetooth-specific detection. Set by checkHeadphones() when
  // an output or input device label matches BT keywords. Used to warn about
  // monitoring latency and to bias buffer sizes for stability over latency.
  const bluetoothInRef = useRef(false);
  const [monitorWarn,  setMonitorWarn]  = useState("");
  const [showAutoPitch,setShowAutoPitch] = useState(false);
  const [autoPitch,    setAutoPitch]     = useState({ on:false, key:"C", scale:"major", speed:50, humanize:10, wet:1, vibrato:0, formant:true });
  const [lowLatency,   setLowLatency]    = useState(true);

  // Live-sync autoPitch params to running AutotuneWorklet node without restarting monitoring
  React.useEffect(function() {
    const node = window.__bfAutotuneNode;
    if (!node) return;
    const KEY_OFFSETS = {C:0,"C#":1,Db:1,D:2,"D#":3,Eb:3,E:4,F:5,"F#":6,Gb:6,G:7,"G#":8,Ab:8,A:9,"A#":10,Bb:10,B:11};
    try {
      node.parameters.get("bypass").value   = autoPitch.on ? 0 : 1;
      node.parameters.get("key").value      = KEY_OFFSETS[autoPitch.key || "C"] || 0;
      node.parameters.get("speed").value    = autoPitch.speed ?? 50;
      node.parameters.get("humanize").value = autoPitch.humanize ?? 10;
      node.parameters.get("wet").value      = autoPitch.wet ?? 1;
      node.parameters.get("vibrato").value  = autoPitch.vibrato ?? 0;
      node.port.postMessage({ scale: autoPitch.scale || "major" });
    } catch(e) {}
  }, [autoPitch]);
  // "builtin" = iPhone built-in mic, "headset" = wired headset mic
  // Always start on iPhone mic regardless of what's connected at load time
  const [micSource,    setMicSource]     = useState("builtin");
  // Tracks whether the user has explicitly chosen a mic — prevents auto-switch overriding their pick
  const userPickedMicRef = useRef(false);
  const [availableMics, setAvailableMics] = useState([]);

  // ── Refs ──────────────────────────────────────────────────────
  const actxRef         = useRef(null);
  const masterStartRef  = useRef(0);
  const loopRestartingRef = useRef(false); // prevents re-entrant loop restarts
  const playheadAtRef   = useRef(0);
  const animRef         = useRef(null);
  const scrollRef       = useRef(null);
  const playheadRef     = useRef(null); // direct DOM ref — updated without React re-render
  const trackContainerRef = useRef(null);
  const sidebarRef      = useRef(null);  // left column (locked horizontal)
  const sidebarRowsRef  = useRef(null);  // inner rows div — driven by translateY for vertical sync
  const mediaRecRef     = useRef(null);
  const chunksRef       = useRef([]);
  const recIntRef       = useRef(null);
  const recDurRef       = useRef(0);
  const recStartTimeRef = useRef(0);  // AudioContext-clock-derived timeline position at rec start
  const recStopActxTimeRef = useRef(null); // actx.currentTime snapshot at the moment stop is pressed
  // Raw actx.currentTime when mr.start() fired. Together with recStopActxTimeRef
  // this gives us the playback-clock duration of the recording — which we
  // compare to buf.duration (mic clock) to detect and compensate for clock drift.
  const recStartActxTimeRef = useRef(null);
  const clipIdRef = useRef(null);
  const countTimerRef   = useRef(null);
  const metroRef        = useRef(null);
  const pinchRef        = useRef(null);
  const zoomRef         = useRef(zoom);
  const scheduledRef    = useRef([]);
  const isPlayingRef    = useRef(false);
  const tracksRef       = useRef([]);  // always mirrors tracks — gives doPlay a fresh snapshot
  const saveStateRef    = useRef(null); // updated each render so lifecycle effect always saves fresh state
  // ── Auto-scroll control ───────────────────────────────────────────────────
  const userScrolledAt  = useRef(null);  // timestamp of last manual scroll — null = auto-scroll active
  const autoScrollRaf   = useRef(null);  // rAF handle for the auto-scroll module
  const liveTimeRef     = useRef(0);     // exact playhead time updated every RAF tick — used by stop
  const scrollSeekTimer = useRef(null);  // timer to resume playback after scroll-while-playing

  // ── Input monitoring refs ──────────────────────────────────────
  const monitorStreamRef    = useRef(null);
  const monitorSrcRef       = useRef(null);
  const monitorGainRef      = useRef(null);
  const monitorMicBoostRef  = useRef(null); // pre-gain mic input boost node
  const monitorAnalyserRef  = useRef(null);
  const monitorSplitterRef  = useRef(null); // ChannelSplitter for mono-centre
  const monitorMergerRef    = useRef(null); // ChannelMerger  for mono-centre
  const monitorCtxRef       = useRef(null); // persistent AudioContext — never closed between sessions
  // Persistent mic stream — requested once on mount, reused for both monitoring and recording
  const micStreamRef        = useRef(null);

  // Tracks whether the NoiseGateProcessor AudioWorklet has been registered on
  // the current playback AudioContext. Set true by doPlay() after the worklet
  // registers successfully. buildChain reads this to decide whether to use the
  // worklet path (proper RMS-based noise gate) or fall back to a DynamicsCompressor.
  const noiseGateWorkletReady = useRef(false);

  // Re-route iOS audio output after mic use.
  // iOS switches to earpiece when getUserMedia is called; this forces the
  // route to be re-evaluated. With NO headset connected, iOS picks the
  // loudspeaker. With a wired/Bluetooth headset connected, iOS keeps it
  // on the headset because that's its natural priority.
  //
  // CRITICAL: we must NOT pause/replay the silent audio element when a BT
  // headset is in use — doing so causes iOS to drop the BT route and pick
  // the phone speaker, which is exactly the wrong behaviour. We only
  // bounce the silent element when there's no headset detected.
  const restoreIOSSpeaker = async function() {
    const hasHeadphones = !!headphonesInRef.current;
    try {
      const old = actxRef.current;
      if (old && old.state !== "closed") {
        try { await old.close(); } catch(e) {}
      }
      actxRef.current = null;
      gainNodesRef.current = {};
      masterGainRef.current = null;
      fxNodesRef.current = {};
      chainsRef.current = {};
      scheduledRef.current = [];
      noiseGateWorkletReady.current = false;

      // Only bounce the silent audio element when there's no headset. With
      // a headset (wired or BT) connected, this would fight iOS's natural
      // routing and force audio back through the phone speaker.
      if (!hasHeadphones) {
        try {
          const sa = silentAudioRef.current;
          if (sa) {
            sa.pause();
            await new Promise(function(res){ setTimeout(res, 30); });
            try { sa.currentTime = 0; } catch(e) {}
            await sa.play().catch(function(){});
          }
        } catch(e) {}
        // Slightly longer pause so iOS finishes the session re-evaluation
        await new Promise(function(res) { setTimeout(res, 120); });
      } else {
        // With a headset connected, just give iOS a brief moment to settle
        // the audio session before we build the new context. No element bounce.
        await new Promise(function(res) { setTimeout(res, 80); });
      }

      // Create fresh AudioContext.
      // - Without a headset: iOS picks the loudspeaker.
      // - With BT/wired headset: iOS routes to the headset.
      // latencyHint "playback" gives Bluetooth devices a larger buffer
      // window, which reduces dropouts caused by BT's lossy transport.
      // We use "interactive" only when no headset is present (lower latency
      // matters more for tight playback on phone speaker than for BT).
      actxRef.current = new (window.AudioContext || window.webkitAudioContext)({
        latencyHint: hasHeadphones ? "playback" : "interactive",
      });
      if (actxRef.current.state === "suspended") {
        try { await actxRef.current.resume(); } catch(e) {}
      }
    } catch(e) { console.warn("[audio route restore]", e); }
  };
  // micReady: true if we've confirmed mic permission this session.
  // We check the Permissions API first — if the browser shows "granted", skip the prompt.
  // If it shows "prompt" or "denied", clear our cached flag and ask again.
  const [micReady,      setMicReady]      = useState(false);
  const [micDenied,     setMicDenied]     = useState(false);
  // effectivePPS changes with zoom — keep a ref so lasso onMove closure can read it
  // MUST be declared here (before line 4743 uses it) to avoid "uninitialized variable" crash
  const effectivePPSRef = useRef(100);
  const lassoContainerRef = useRef(null); // ref to the DAW wrapper — lasso overlay is positioned inside this

  // ── Audio engine gain/FX refs (declared here to follow Rules of Hooks) ──
  const gainNodesRef       = useRef({});   // trackId → GainNode
  const trackAnalysersRef  = useRef({});   // trackId → AnalyserNode (for VU meters)
  const masterAnalyserRef  = useRef(null); // master output analyser
  const masterGainRef      = useRef(null); // single master output
  // Speaker mute node — sits between master and acx.destination. We use
  // this to silence the speakers during real-time export without
  // disturbing the recording tap (which is upstream of this node).
  // gain = 1 normally, gain = 0 during export. NOT a replacement for
  // mute/solo logic — purely a routing trick to detach the speakers
  // from playback temporarily.
  const speakerMuteRef     = useRef(null);
  const fxNodesRef    = useRef({});   // trackId → live audio node references

  // Publish the live-fx-nodes lookup to module scope so plugin UIs at module
  // scope (e.g. _TQPluginInner's gain reduction meters) can read the live
  // detector values. The lookup function reads fxNodesRef on call, so it
  // always returns the current snapshot — no stale closures.
  React.useEffect(function(){
    _registerLiveFxNodesLookup(function(trackId){
      return fxNodesRef.current ? fxNodesRef.current[trackId] : null;
    });
    return function(){ _registerLiveFxNodesLookup(null); };
  }, []);

  // ── Persistent FX chain cache ────────────────────────────────────────────
  // Each entry: { entry: AudioNode, fxSig: string }
  // entry = the upstream gain node that clips connect into for this track
  // fxSig = a fingerprint of which plugins are ON; when it changes, we rebuild
  //         that track's chain (turning a plugin on/off requires re-graph; just
  //         tweaking a knob does NOT — that's handled live by applyFxLive)
  // Invalidated when: AudioContext is recreated, track is deleted, plugin
  //                   on/off state changes for that track.
  const chainsRef     = useRef({});   // trackId → { entry, fxSig }
  // Expose chainsRef via window so child components (piano-roll editor)
  // running their own PAT scheduler can route notes through the same FX
  // chain SONG uses. Without this, PAT was sending notes direct to
  // master, bypassing EQ/reverb/etc. and sounding different from SONG.
  if (typeof window !== "undefined") window._bfChainsRef = chainsRef;
  // PDC latency cache. Keyed by trackId, value is { fxSig, latencySec }.
  // Re-measured automatically when fxSig changes (chain rebuild).
  const fxLatencyRef  = useRef({});

  // ── Drum sequencer state ────────────────────────────────────────────────
  // Decoded kit buffers, keyed by kitId. Populated on demand the first time
  // any drum track references a kit. The actual decoding is delegated to
  // loadKitBuffers() in BeatFinderKits.js, which caches per (AudioContext, kitId).
  // We mirror that cache here in a ref so React renders don't trigger reloads.
  const drumKitBuffersRef = useRef({}); // kitId -> { padId: AudioBuffer }

  // ── Admin drum kits (Phase 3) ──
  // The frontend fetches admin-uploaded kits from /api/drum-kits on Studio
  // mount and feeds them into BeatFinderKits.js via registerAdminKits().
  // We also keep a React state copy so the kit picker dropdown re-renders
  // when the catalogue changes (e.g. after an admin creates a new kit).
  // Built-in "default" kit is NOT in this state — it lives hardcoded in
  // BeatFinderKits.js and is always present.
  const [adminKits, setAdminKits] = useState([]);
  // Manage Kits overlay (admin-only). When non-null, full-screen modal opens
  // with the kit editor. State shape: { mode: "list" | "edit", kitId?: string }
  const [manageKitsOverlay, setManageKitsOverlay] = useState(null);

  // ── Custom soundfonts (Phase 7) ──
  // Admin-uploaded soundfonts from /api/soundfonts. These get merged into
  // BF_INSTRUMENTS at runtime so they appear in the preset browser's
  // Instruments category alongside the built-in CDN-hosted ones. Each
  // entry has a `customUrl` field that the soundfont engine fetches
  // directly (instead of building from BF_SOUNDFONT_CDN_BASE).
  //
  // Built-in soundfont presets (sf-grand-piano, etc.) are NOT in this
  // state — they live in BF_INSTRUMENTS as static entries.
  const [customSoundfonts, setCustomSoundfonts] = useState([]);
  // Manage Soundfonts overlay (admin-only). Same pattern as Manage Kits.
  const [manageSoundfontsOverlay, setManageSoundfontsOverlay] = useState(null);

  // Fetch + register custom soundfonts. Re-callable so the admin overlay
  // can refresh after upload/edit/delete without remounting Studio.
  const reloadCustomSoundfonts = useCallback(function() {
    return apiFetch("/api/soundfonts")
      .then(function(data){
        const items = (data && data.soundfonts) || [];
        // Mutate BF_INSTRUMENTS in place — adding new keys is safe because
        // every code path reads it via getInstrumentMeta() which checks at
        // call time. Removing entries (after a delete) just deletes the key.
        // First clear any previously-registered custom entries so a delete
        // doesn't leave stale entries in the registry.
        Object.keys(BF_INSTRUMENTS).forEach(function(k){
          if (BF_INSTRUMENTS[k] && BF_INSTRUMENTS[k].isCustom) {
            delete BF_INSTRUMENTS[k];
          }
        });
        items.forEach(function(item){
          BF_INSTRUMENTS[item.id] = item;  // wire format already matches preset shape
        });
        setCustomSoundfonts(items);
        return items;
      })
      .catch(function(e){
        console.warn("[soundfonts] fetch failed", e);
        return [];
      });
  }, []);
  useEffect(function(){
    reloadCustomSoundfonts();
  }, [reloadCustomSoundfonts]);

  // ── Soundfont loading indicator ──────────────────────────────────
  // Subscribe to the soundfont engine and surface a global "loading…"
  // toast whenever ANY preset is in the "loading" state. Without this,
  // tapping a soundfont preset feels broken — the file is downloading
  // (can take 1-5s on slow networks) and the user thinks nothing's
  // happening. The toast shows the friendly name of the preset that's
  // loading.
  const [loadingPresetName, setLoadingPresetName] = useState(null);
  useEffect(function(){
    // Per-track engines mean loads can happen on ANY engine — we must
    // aggregate across all of them. Otherwise the toast would only fire
    // for the singleton (used by browser previews), missing the more
    // common case of a track-bound load triggered by playback.
    const MIN_VISIBLE_MS = 3000;
    let shownAtMs = 0;
    let checkTimer = null;
    const stopCheck = function(){
      if (checkTimer) { clearTimeout(checkTimer); checkTimer = null; }
    };
    const tick = function(){
      const totals = (typeof bfSfTotalActiveLoads === "function")
        ? bfSfTotalActiveLoads()
        : { count: 0, name: null };
      const active = totals.count > 0;
      if (active) {
        if (shownAtMs === 0) shownAtMs = Date.now();
        if (totals.name) setLoadingPresetName(totals.name);
        stopCheck();
        checkTimer = setTimeout(tick, 200);
        return;
      }
      // counter is 0
      if (shownAtMs === 0) {
        stopCheck();
        return;
      }
      const elapsed = Date.now() - shownAtMs;
      if (elapsed < MIN_VISIBLE_MS) {
        stopCheck();
        checkTimer = setTimeout(tick, MIN_VISIBLE_MS - elapsed);
        return;
      }
      shownAtMs = 0;
      setLoadingPresetName(null);
      stopCheck();
    };
    // Subscribe to every active engine so any engine's notify triggers
    // a re-check. New engines created later via getBFSoundfontEngineForTrack
    // also notify the toast subscriber via the next-tick poll (because
    // checkTimer keeps the tick running while any load is active).
    const unsubs = [];
    if (typeof forEachBFSoundfontEngine === "function") {
      forEachBFSoundfontEngine(function(eng){
        if (eng && typeof eng.subscribe === "function") {
          try { unsubs.push(eng.subscribe(tick)); } catch(_){}
        }
      });
    } else {
      const engine = getBFSoundfontEngine();
      if (engine && typeof engine.subscribe === "function") {
        try { unsubs.push(engine.subscribe(tick)); } catch(_){}
      }
    }
    tick();
    // Steady heartbeat — checks active loads on ALL engines every 250ms,
    // independent of engine subscriptions. Without this, a load on a
    // per-track engine created after mount could start without the
    // subscription firing tick, so the bar never appeared (the bug where
    // single-preset soundfonts loaded silently with no progress bar).
    const heartbeat = setInterval(tick, 250);
    return function(){
      stopCheck();
      clearInterval(heartbeat);
      unsubs.forEach(function(u){ try { u && u(); } catch(_){} });
    };
  }, []);

  // ── Soundfont ERROR toast ──────────────────────────────────────────
  // When a soundfont load fails (R2 outage, CORS block, network
  // failure, etc.), surface the error to the user instead of silently
  // looping the loading bar. Auto-dismisses after 12 seconds.
  const [sfErrorToast, setSfErrorToast] = useState(null);
  useEffect(function(){
    let lastSeenTime = 0;
    let dismissTimer = null;
    const tick = function(){
      // Walk every engine for the most recent error. We track the
      // most-recent-error time across all engines so a brand new error
      // on a per-track engine still triggers the toast.
      let latest = null;
      const consider = function(eng){
        const err = eng && eng._lastError;
        if (err && err.time && err.time > lastSeenTime) {
          if (!latest || err.time > latest.time) latest = err;
        }
      };
      if (typeof forEachBFSoundfontEngine === "function") {
        forEachBFSoundfontEngine(consider);
      } else {
        consider(getBFSoundfontEngine());
      }
      if (latest) {
        lastSeenTime = latest.time;
        setSfErrorToast({ name: latest.name, message: latest.message });
        if (dismissTimer) clearTimeout(dismissTimer);
        dismissTimer = setTimeout(function(){
          setSfErrorToast(null);
        }, 12000);
      }
    };
    const unsubs = [];
    if (typeof forEachBFSoundfontEngine === "function") {
      forEachBFSoundfontEngine(function(eng){
        if (eng && typeof eng.subscribe === "function") {
          try { unsubs.push(eng.subscribe(tick)); } catch(_){}
        }
      });
    } else {
      const engine = getBFSoundfontEngine();
      if (engine && typeof engine.subscribe === "function") {
        try { unsubs.push(engine.subscribe(tick)); } catch(_){}
      }
    }
    tick();
    return function(){
      if (dismissTimer) clearTimeout(dismissTimer);
      unsubs.forEach(function(u){ try { u && u(); } catch(_){} });
    };
  }, []);

  // ── Sample-loading toast state ──
  // Same pattern as loadingPresetName above, but for sampler buffers
  // (Backblaze fetch + Web Audio decode). Subscribes to bfSamplerCache
  // and shows the friendly name of whichever sample is currently
  // inflight. Multiple samples loading concurrently? Just show the first
  // one — single-pill UX is cleaner than a stacked-list approach, and
  // the toast naturally cycles to the next as each completes.
  const [loadingSampleName, setLoadingSampleName] = useState(null);
  useEffect(function(){
    const refresh = function(){
      if (bfSamplerCache.loadingNames.size === 0) {
        setLoadingSampleName(null);
        return;
      }
      // Take the first name in iteration order (insertion order in JS Maps).
      const first = bfSamplerCache.loadingNames.values().next().value;
      setLoadingSampleName(first || "Sample");
    };
    const unsub = bfSamplerCache.subscribe(refresh);
    refresh();
    return unsub;
  }, []);

  // ── Pin every track's soundfont preset against LRU eviction ──
  // Whenever the track list changes (track added/removed/instrument
  // swapped), compute the set of preset IDs currently in use by ANY
  // MIDI track using a soundfont engine, and tell the soundfont engine
  // to never evict those. Without this, adding a 13th, 14th, Nth MIDI
  // track with a new soundfont silently evicts an older still-active
  // preset, breaking that track. With pinning, the cap acts only on
  // truly unused presets (e.g. ones the user auditioned in the browser
  // but didn't assign to a track).
  useEffect(function(){
    const engine = getBFSoundfontEngine();
    if (!engine || typeof engine.setPinnedPresetIds !== "function") return;
    const pinned = new Set();
    for (const tr of tracks) {
      if (!tr || tr.type !== "midi") continue;
      if (!tr.instrumentId) continue;
      const meta = BF_INSTRUMENTS[tr.instrumentId];
      if (meta && meta.engine === "soundfont-v1") {
        pinned.add(tr.instrumentId);
      }
    }
    engine.setPinnedPresetIds(pinned);
  }, [tracks]);

  // ── Preload sampler buffers as tracks change ──
  // When the user opens a saved project, every sampler track's
  // samplerData has a sampleHash + sampleUrl but the audio buffer
  // isn't in memory yet. Walk the tracks list and trigger loads for
  // any sampler track whose hash isn't cached. Fire-and-forget — the
  // cache coalesces concurrent loads of the same hash so this is safe
  // to call repeatedly.
  //
  // IMPORTANT: do NOT call getOrCreateMaster() here. That creates the
  // AudioContext on Studio mount BEFORE any user gesture, which on iOS
  // Safari leaves the context in a suspended state that's hard to
  // recover from. Only use getActx() — it returns the existing context
  // if one was already created (by a user tap on transport play), or
  // null otherwise. When null we just skip the preload sweep; the
  // first user gesture will create the context and the next track
  // change (or scheduler tick) will pick it up.
  React.useEffect(function(){
    if (typeof window === "undefined") return;
    if (!tracks || tracks.length === 0) return;
    let ctx = null;
    if (typeof getActx === "function") {
      try { ctx = getActx(); } catch(_){}
    }
    if (!ctx) return;  // context not yet created — skip until it is
    for (const tr of tracks) {
      if (tr && tr.samplerData && tr.samplerData.sampleHash && tr.samplerData.sampleUrl) {
        if (!bfSamplerCache.buffers.has(tr.samplerData.sampleHash)) {
          ensureSamplerLoaded(tr, ctx).catch(function(e){
            console.warn("[sampler] preload failed for", tr.id, e);
          });
        }
      }
    }
  }, [tracks]);

  // ── Fetch admin kits on Studio mount ──
  // Pulls the catalogue from /api/drum-kits and feeds it to BeatFinderKits.js
  // so getKitMeta/loadKitBuffers/listKits all see admin kits alongside the
  // built-in "default". Public endpoint — works for guests too, so the kit
  // picker is populated whether or not the user is signed in.
  // Re-callable: `reloadAdminKits` is exposed for the Manage Kits overlay so
  // a save/delete refreshes the catalogue without remounting Studio.
  const reloadAdminKits = useCallback(function() {
    return apiFetch("/api/drum-kits")
      .then(function(data){
        const kits = (data && data.kits) || [];
        registerAdminKits(kits);
        // Always re-register device-local marketplace kits right after the
        // network kits. registerAdminKits may replace (not merge) the
        // in-memory registry, so without this any admin reload would wipe the
        // user's downloaded kits until they re-downloaded. Re-running it here
        // makes downloaded kits durable across every refresh path.
        return bfRegisterLocalMarketKits().then(function(localKits){
          const ids = {}; kits.forEach(function(k){ ids[k.id] = true; });
          const merged = kits.slice();
          (localKits || []).forEach(function(k){ if (!ids[k.id]) merged.push(k); });
          setAdminKits(merged);
          return merged;
        });
      })
      .catch(function(e){
        // Non-fatal — built-in kit still works without the network. Still try
        // to surface any locally-downloaded kits so they don't disappear when
        // offline.
        console.warn("[drum-kits] fetch failed", e);
        return bfRegisterLocalMarketKits().then(function(localKits){
          if (localKits && localKits.length) setAdminKits(localKits);
          return localKits || [];
        }).catch(function(){ return []; });
      });
  }, []);
  useEffect(function(){
    // reloadAdminKits now handles local-kit registration internally, so a
    // single call covers both admin (network) and device-local market kits.
    reloadAdminKits();
  }, [reloadAdminKits]);

  // ── Clip pitch shift (preserves duration) ──
  // Cache of pre-rendered pitched buffers keyed by (clip.id + ":" + semitones).
  // SoundTouch processing is offline + async so we cache aggressively — re-
  // playing the same clip+pitch combo is instant after the first render.
  // Cleared when the clip's underlying audioBuffer changes (rare — projects
  // generally don't mutate buffers after load).
  const clipPitchedBuffersRef = useRef({}); // "<clipId>:<semitonesx100>" -> AudioBuffer
  // When the user opens the pitch modal from the clip context menu, this
  // state holds { trackId, clipId, semis, cents, processing }. Closes when
  // set to null. Slider release triggers the offline re-pitch.
  const [pitchModal, setPitchModal] = useState(null);

  // ── Clip time stretch (preserves pitch) ──
  // Cache of pre-rendered stretched buffers keyed by (clip.id + ":" + targetMs).
  // Like pitched buffers, SoundTouch runs offline and the result is cached.
  // Stretching alongside pitch is supported via two separate cache lookups —
  // we don't pre-combine them into a single buffer (would double cache space).
  // When both are set, the scheduler picks one as the primary playback buffer
  // and the other is best-effort. In practice users rarely combine both.
  const clipStretchedBuffersRef = useRef({}); // "<clipId>:<targetMs>" -> AudioBuffer

  // ── Background time-stretch render (reliability backstop) ──
  // Mirrors the background PITCH render in loadProject. The stretch handle's
  // release handler also kicks off a render, but that depends on touchend
  // firing cleanly — on iOS a re-render mid-drag can swallow the touchend
  // (fires touchcancel instead), so the clip resizes visually but never
  // renders, and plays at its raw length. This effect renders the stretched
  // buffer from COMMITTED clip state regardless of how stretchedDurationSec
  // changed (handle drag, or restored on project load where the in-memory
  // cache is gone). Debounced so a drag (which updates state every frame)
  // only renders once it settles, not once per touch-move.
  useEffect(function () {
    const ctx = actxRef.current;
    if (!ctx) return;
    const timer = setTimeout(function () {
      (tracksRef.current || []).forEach(function (tr) {
        if (!tr || !tr.clips) return;
        tr.clips.forEach(function (cl) {
          if (!cl || !cl.stretchMode || !cl.audioBuffer) return;
          const target = +(cl.stretchedDurationSec || 0);
          if (!(target > 0)) return;
          // Nothing to render if the target matches the raw trim length.
          const rawLen = (cl.trimEnd != null ? cl.trimEnd : cl.audioBuffer.duration) - (cl.trimStart || 0);
          if (Math.abs(target - rawLen) < 0.01) return;
          const skey = cl.id + ":" + Math.round(target * 1000);
          if (clipStretchedBuffersRef.current[skey]) return; // already cached
          stretchAudioBufferOffline(ctx, cl.audioBuffer, target).then(function (rendered) {
            if (rendered) clipStretchedBuffersRef.current[skey] = rendered;
          }).catch(function (e) { console.warn("[stretch] background render failed for", cl.id, e); });
        });
      });
    }, 300);
    return function () { clearTimeout(timer); };
  }, [tracks]);

  // ── Clip fade in/out modal ──
  // When non-null, shows the fade modal: { trackId, clipId, kind: "in"|"out" }.
  // Fade durations live on the clip object itself (fadeInSec, fadeOutSec).
  // We don't need to pre-render anything for fades — they're applied at
  // playback time via GainNode ramps.
  const [fadeModal, setFadeModal] = useState(null);

  // ── Drum rack scroll bar refs ──
  // The rack pattern body is wider than the viewport for long patterns or
  // narrow phones. iOS hides native scrollbars, so we render a custom
  // scrollbar at the bottom of the rack. These refs let scroll events
  // drive the thumb position imperatively (no re-renders per scroll frame).
  const drumRackScrollEl = useRef(null);
  const drumRackScrollTrackEl = useRef(null);
  const drumRackScrollThumbEl = useRef(null);

  // Drum scheduler interval timer. One global interval drives all drum tracks
  // — the per-track scheduling logic is fast enough that 40Hz with a 150ms
  // lookahead handles dozens of tracks comfortably.
  const drumSchedulerRef = useRef(null);
  // Per-drum-track scheduler cursor: keyed by trackId, value is the next
  // project time (in seconds) that hasn't yet been scheduled. The scheduler
  // tick advances each cursor forward by DRUM_LOOKAHEAD_SEC each pass.
  const drumCursorsRef = useRef({}); // trackId -> nextProjectTimeToSchedule
  // Per-(trackId, clipId) "next step index to schedule" map. Eliminates
  // double-firing at tick boundaries: schedulers use this as the source of
  // truth instead of recomputing stepIdx from a fuzzy projectTime each tick.
  // Format: { "trackId:clipId": nextStepIdx }. Wiped on every doPlay.
  const drumClipStepsRef = useRef({});
  // When BPM changes mid-playback, the drum scheduler's per-clip step cursors
  // (a step INDEX) and per-track project-time cursors were computed against the
  // OLD step duration. Walking forward from them after the tempo change lands
  // hits at the wrong project times, and because each track recovers on its own
  // schedule they drift apart (drums vs 808 vs sampler). Clearing all cursors
  // together forces every track to re-seed from the current playhead against the
  // new stepSec on the next tick, so they stay locked to each other and the grid.
  useEffect(function(){
    drumCursorsRef.current = {};
    drumClipStepsRef.current = {};
  }, [bpm]);
  // Per-track choke contexts for the live scheduler. Each entry is the
  // map of chokeGroup -> { src, gain, when } that lets the scheduler ramp
  // down the previous hit before firing a new one in the same group.
  // Lives across scheduler ticks so a closed-hat scheduled in tick N can
  // be choked by an open-hat scheduled in tick N+1 (otherwise the choke
  // effect would reset every 25ms, defeating the whole point).
  // Reset to {} when the master scheduler restarts so a stale source from
  // the previous playback session doesn't get ramped (and likely fail).
  const drumChokeContextsRef = useRef({}); // trackId -> { [group]: {src,gain,when} }

  // ── MIDI scheduler refs ──
  // Mirror of the drum scheduler infrastructure for MIDI Instrument tracks.
  // We use a separate interval timer (rather than merging with the drum
  // scheduler) so MIDI playback can be added/removed without risking drum
  // timing regressions. Both schedulers read the same `tracksRef` so they
  // stay frame-accurate together.
  const midiSchedulerRef = useRef(null);
  // trackId -> next project time to schedule from
  const midiCursorsRef = useRef({});
  // trackId -> array of active voice handles (for cleanup on stop / mute /
  // track delete). Each voice has a `.stop(when)` method so we can release
  // notes mid-flight without clicks.
  const midiVoicesRef = useRef({});
  // Piano-roll editor — when set, the placeholder modal is open for this clip.
  // Phase 1 ships a placeholder explaining Phase 2 will add the editor; the
  // state shape is set up now so Phase 2 just wires the editor component in.
  const [midiEditorClipId, setMidiEditorClipId] = useState(null);
  // ── Phase 4: preset browser + edit-sound state ──
  // When non-null, the bottom-sheet preset browser is open for this track.
  // (Used by the piano-roll toolbar "Sound" button and the track-rack
  // instrument chip — both set this to the track id.)
  const [presetBrowserForTrackId, setPresetBrowserForTrackId] = useState(null);
  // When non-null, the Edit Sound panel is open for this track.
  const [editSoundForTrackId, setEditSoundForTrackId] = useState(null);
  // ID for the channel rack overlay — open when set to a track id, closed when null.
  const [drumRackTrackId, setDrumRackTrackId] = useState(null);
  // Editing target inside the rack — which pattern is currently displayed
  // for editing. Keyed by trackId so each track remembers its own active pattern.
  const [drumActivePatternByTrack, setDrumActivePatternByTrack] = useState({});
  // Ref mirrors so the master scheduler closure (running in setInterval)
  // always reads the CURRENT rack state — without these, the scheduler would
  // capture a stale value when it was created and ignore later edits.
  //
  // The rack-overlay scheduling feature (FL Studio-style live mixing of the
  // editing pattern over the timeline) needs both: which track is being
  // edited AND which pattern within that track. Both can change while
  // playback is running.
  const drumRackTrackIdRef = useRef(null);
  const drumActivePatternByTrackRef = useRef({});
  useEffect(function(){ drumRackTrackIdRef.current = drumRackTrackId; }, [drumRackTrackId]);
  useEffect(function(){ drumActivePatternByTrackRef.current = drumActivePatternByTrack; }, [drumActivePatternByTrack]);
  // In-rack pattern preview state. When set to a pattern id, the preview
  // scheduler is running — looping that pattern at project BPM directly to
  // ctx.destination (bypasses track FX so the user hears the raw pattern).
  // Cleared on stop, on rack close, on main transport play.
  const [drumPreviewPatternId, setDrumPreviewPatternId] = useState(null);
  const drumPreviewSchedulerRef = useRef(null);  // setInterval handle
  const drumPreviewStateRef = useRef({           // shared state with scheduler
    startCtxTime: 0,                              // ctx time we started preview
    cursorPatTime: 0,                             // next pattern-time to schedule
    activeSources: [],                            // for stop cleanup
    chokeContext: {},                             // group -> {src, gain, when} for preview choking
  });
  // Currently animating step index for the active pattern preview. Used
  // to visually pulse the step column that's currently sounding.
  const [drumPreviewPlayheadStep, setDrumPreviewPlayheadStep] = useState(-1);
  // Place-confirmation toast: when set, shows "Placed at MM:SS ✓" briefly.
  const [drumPlaceToast, setDrumPlaceToast] = useState(null);  // { time, t0 }
  // Snap mode for "Place on Timeline" — 1 = bar, 2 = half-bar, 4 = beat,
  // 0 = no snap. FL Studio defaults to bar-snap; we default to beat-snap
  // since dropping a drum pattern at a beat boundary is the most common
  // and forgiving choice on a touch device.
  const [drumSnapDiv, setDrumSnapDiv] = useState(4);
  // How many pattern loops to place inside a single clip. 1 = clip plays
  // the pattern once and stops; 2 = pattern plays twice back-to-back; etc.
  // This is the simplest way to make placed clips "loop smoothly" for the
  // duration the user wants without having to drag the clip after placing.
  const [drumPlaceLoopCount, setDrumPlaceLoopCount] = useState(1);

  // ── PAT play state ──
  // The rack's pattern-loop transport. When true, a setInterval tick is
  // scheduling drum hits into the live AudioContext on a loop. Independent
  // of the master transport (isPlaying): the rack can PAT-loop without the
  // song playing, and vice versa.
  const [rackPatPlaying, setRackPatPlaying] = useState(false);
  const rackPatPlayingRef = useRef(false);
  useEffect(function(){ rackPatPlayingRef.current = rackPatPlaying; }, [rackPatPlaying]);
  // Scheduler refs — kept out of React state to avoid per-tick re-renders.
  const rackPatTickRef     = useRef(null);   // setInterval handle
  const rackPatAnchorRef   = useRef(0);      // ctx.currentTime at which pattern position = 0
  const rackPatCursorRef   = useRef(0);      // next pattern-time to schedule (seconds)
  const rackPatVoicesRef   = useRef([]);     // active BufferSource handles (for stop)
  const rackPatChokeRef    = useRef({});     // padId -> { src, gain, when } for self-choke
  const rackPatBeamRafRef  = useRef(null);   // beam animation rAF
  // Visible playhead in PAT mode (seconds within one pattern loop). Drives
  // the LED chase on the cell grid when rackPatPlaying is true; the cell
  // render computes the step from this just like it computes from the
  // master-transport time when SONG is playing.
  const [rackPatPlayheadSec, setRackPatPlayheadSec] = useState(0);

  // PAT-mode scheduler: loops the active pattern of the currently-edited
  // rack track, independently of the song transport. Mirrors the piano
  // roll's localPlay architecture (25ms tick, 150ms lookahead) but uses
  // the drum-specific primitives (scheduleStepHits + per-channel choke).
  const rackPatStart = function() {
    // Mutual exclusion with SONG transport. FL-style: PAT mode = song
    // paused, only the pattern loops. If master is playing, stop it
    // before starting PAT, otherwise both schedulers fire from the same
    // pattern → kick + 808 doubled, loud spikes.
    if (isPlayingRef.current) {
      try { stopAll(); } catch(_){}
      setIsPlaying(false);
    }
    // Resolve a track + pattern (closure-locks them at the start of play).
    const tid = drumRackTrackId;
    if (tid == null) return;
    const tr = tracksRef.current.find(function(x){ return x.id === tid; });
    if (!tr || tr.type !== "drumSequencer") return;
    const activeId = drumActivePatternByTrack[tid] || ((tr.patterns||[])[0] && (tr.patterns||[])[0].id);
    const pat = (tr.patterns || []).find(function(p){ return p.id === activeId; });
    if (!pat) return;
    // Resolve an AudioContext + destination. Prefer the master so PAT
    // playback flows through the track's FX chain just like SONG does.
    let ctx = null, dest = null;
    if (typeof getOrCreateMaster === "function") {
      try {
        const m = getOrCreateMaster();
        if (m && m.context) {
          ctx = m.context;
          // PAT plays through the track's FX entry node so reverbs etc
          // are heard. chainsRef holds those per-track entry nodes.
          const chain = chainsRef.current[tid];
          dest = (chain && chain.entry) ? chain.entry : m;
          // Defensive: restore master gain in case stopAll zeroed it.
          try {
            if (typeof m.gain !== "undefined" && m.gain.value === 0) {
              m.gain.cancelScheduledValues(ctx.currentTime);
              m.gain.setValueAtTime(1, ctx.currentTime);
            }
          } catch(_){}
        }
      } catch(_) { ctx = null; }
    }
    if (!ctx) return;
    if (ctx.state === "suspended") { try { ctx.resume(); } catch(_){} }
    // Resolve drum buffers for this track's kit.
    const kitId = tr.kitId || "default";
    let buffers = drumKitBuffersRef.current[kitId];
    if (!buffers) {
      // Lazy-load and retry — fire-and-forget so first notes may be silent
      // for a beat while the kit loads, then become audible.
      loadKitBuffers(ctx, kitId).then(function(buf){
        drumKitBuffersRef.current[kitId] = buf;
      }).catch(function(){});
      buffers = {};
    }
    const stepSec = drumStepDurationSec(bpm, pat.stepsPerBeat);
    const loopDur = pat.lengthSteps * stepSec;
    if (loopDur <= 0) return;
    // Pre-compute which pads self-choke.
    const padSelfChoke = {};
    (pat.channels || []).forEach(function(ch){
      if (ch.chokeEnabled && ch.padId) padSelfChoke[ch.padId] = true;
    });
    // Anchor: at ctx.currentTime + 0.05, pattern position = 0.
    rackPatAnchorRef.current = ctx.currentTime + 0.05;
    rackPatCursorRef.current = 0;
    rackPatVoicesRef.current = [];
    rackPatChokeRef.current  = {};
    const LOOKAHEAD = 0.15;
    const TICK_MS   = 25;
    const tickFn = function(){
      // Re-read the track + active pattern every tick so live edits
      // (toggling steps, changing velocity) are reflected immediately.
      const trNow = tracksRef.current.find(function(x){ return x.id === tid; });
      if (!trNow) return;
      const liveActive = drumActivePatternByTrack[tid] || ((trNow.patterns||[])[0] && (trNow.patterns||[])[0].id);
      const patNow = (trNow.patterns || []).find(function(p){ return p.id === liveActive; });
      if (!patNow) return;
      const stepSecNow = drumStepDurationSec(bpm, patNow.stepsPerBeat);
      const loopDurNow = patNow.lengthSteps * stepSecNow;
      if (loopDurNow <= 0) return;
      const ctxNow = ctx.currentTime;
      // Pattern position at ctxNow:
      const playheadPt = ctxNow - rackPatAnchorRef.current;
      const scheduleUntil = playheadPt + LOOKAHEAD;
      const cursor = rackPatCursorRef.current;
      if (scheduleUntil <= cursor) return;
      // Walk in loop chunks: schedule from cursor to scheduleUntil,
      // mod-wrapping within loopDurNow.
      let from = cursor;
      const to = scheduleUntil;
      while (from < to) {
        const loopIdx     = Math.floor(from / loopDurNow);
        const loopStart   = loopIdx * loopDurNow;
        const segStartPt  = from;
        const segEndPt    = Math.min(to, loopStart + loopDurNow);
        const fromLocal   = segStartPt - loopStart;
        const toLocal     = segEndPt   - loopStart;
        const fromStepIdx = Math.floor(fromLocal / stepSecNow);
        const toStepIdx   = Math.ceil(toLocal   / stepSecNow);
        const anySoloed   = (patNow.channels || []).some(function(c){ return c.soloed; });
        for (let stepIdx = fromStepIdx; stepIdx < toStepIdx; stepIdx++) {
          const stepLocalTime = stepIdx * stepSecNow;
          if (stepLocalTime < fromLocal || stepLocalTime >= toLocal) continue;
          for (const ch of (patNow.channels || [])) {
            if (anySoloed && !ch.soloed) continue;
            if (!anySoloed && ch.muted) continue;
            // Skip notes-mode channels here — they're handled below.
            if (drumChannelModeOf(ch) === "notes") continue;
            if (!ch.steps) continue;
            const chLen = channelLengthSteps(ch, patNow);
            const chStep = ((stepIdx % chLen) + chLen) % chLen;
            const raw = ch.steps[chStep];
            const params = stepParams(raw);
            if (params.vel < DRUM_MIN_VELOCITY) continue;
            const buf = buffers[ch.padId];
            if (!buf) continue;
            const swingOff = drumStepSwingOffset(chStep, ch.swing, stepSecNow);
            const shiftOff = params.shift * stepSecNow;
            const ctTime = rackPatAnchorRef.current + loopStart + stepLocalTime + swingOff + shiftOff;
            if (ctTime < ctxNow) continue;
            const chokeKey = padSelfChoke[ch.padId] ? ch.padId : null;
            scheduleStepHits(ctx, dest, buf, ctTime, stepSecNow, params,
              ch.volume == null ? 1 : ch.volume, rackPatVoicesRef.current,
              rackPatChokeRef.current, chokeKey);
          }
        }
        // ── Notes-mode channel walk (FL-style "send to piano roll") ──
        // Channels in notes mode store free MIDI notes. Schedule each
        // note whose pattern-local start falls inside [fromLocal, toLocal).
        // Same per-tick window we already computed for the step walk.
        for (const ch of (patNow.channels || [])) {
          if (drumChannelModeOf(ch) !== "notes") continue;
          if (anySoloed && !ch.soloed) continue;
          if (!anySoloed && ch.muted) continue;
          const buf = buffers[ch.padId];
          if (!buf) continue;
          const notes = mergeSlideVoices(ch.notes || []).slice().sort(function(a, b){
            return (a.start || 0) - (b.start || 0);
          });
          if (notes.length === 0) continue;
          const notesTempoScale = (ch.notesBpm && ch.notesBpm > 0) ? (ch.notesBpm / bpm) : 1;
          const chokeKey = padSelfChoke[ch.padId] ? ch.padId : null;
          for (let ni = 0; ni < notes.length; ni++) {
            const n = notes[ni];
            const nStart = (n.start || 0) * notesTempoScale;
            if (nStart < fromLocal || nStart >= toLocal) continue;
            const ctTime = rackPatAnchorRef.current + loopStart + nStart;
            if (ctTime < ctxNow) continue;
            const semis = (n.pitch || DRUM_CHANNEL_ROOT_PITCH) - DRUM_CHANNEL_ROOT_PITCH;
            const rawVel = n.velocity == null ? 0.85 : n.velocity;
            // FL-style silent cut: velocity exactly 0 kills any
            // still-ringing previous hit on this pad without spawning
            // a new audible source. See master scheduler for full reasoning.
            if (rawVel === 0) {
              if (chokeKey && rackPatChokeRef.current) {
                const prev = rackPatChokeRef.current[chokeKey];
                if (prev && prev.gain && prev.src) {
                  try {
                    const cutStart = Math.max(ctTime - DRUM_CHOKE_RAMP_SEC, prev.when || 0);
                    prev.gain.gain.cancelScheduledValues(cutStart);
                    prev.gain.gain.setValueAtTime(prev.gain.gain.value, cutStart);
                    prev.gain.gain.linearRampToValueAtTime(0, ctTime);
                    prev.src.stop(ctTime + 0.001);
                  } catch(_){ /* node may already be stopped */ }
                  rackPatChokeRef.current[chokeKey] = null;
                }
              }
              continue;
            }
            const vel = Math.max(0.001, Math.min(1.5, rawVel));
            const glideCents = (n.glide && n.glide.length)
              ? n.glide.map(function(seg){ return {
                  at: (seg.at || 0) * notesTempoScale,
                  cents: (seg.toPitch - (n.pitch || DRUM_CHANNEL_ROOT_PITCH)) * 100,
                  dur: Math.max(0.01, (seg.dur || 0.05) * notesTempoScale),
                }; })
              : null;
            const hit = scheduleDrumHit(ctx, dest, buf, ctTime,
              vel * (ch.volume == null ? 1 : ch.volume),
              semis, 0, chokeKey, glideCents);
            if (hit && chokeKey && rackPatChokeRef.current) {
              applyChoke(rackPatChokeRef.current, chokeKey, hit, ctTime);
            }
            if (hit && hit.src) rackPatVoicesRef.current.push(hit.src);
          }
        }
        from = segEndPt;
      }
      rackPatCursorRef.current = scheduleUntil;
    };
    rackPatTickRef.current = setInterval(tickFn, TICK_MS);
    tickFn();
    // Beam animation — drives the LED chase.
    const beamFn = function(){
      const ctxNow = ctx.currentTime;
      const playheadPt = ctxNow - rackPatAnchorRef.current;
      const loc = loopDur > 0 ? ((playheadPt % loopDur) + loopDur) % loopDur : 0;
      setRackPatPlayheadSec(loc);
      rackPatBeamRafRef.current = requestAnimationFrame(beamFn);
    };
    rackPatBeamRafRef.current = requestAnimationFrame(beamFn);
    setRackPatPlaying(true);
    // Sync ref immediately so mutual-exclusion guards in doPlay can see
    // PAT is on without waiting a render for the useEffect to run.
    rackPatPlayingRef.current = true;
  };
  const rackPatStop = function() {
    if (rackPatTickRef.current != null) {
      clearInterval(rackPatTickRef.current);
      rackPatTickRef.current = null;
    }
    if (rackPatBeamRafRef.current != null) {
      cancelAnimationFrame(rackPatBeamRafRef.current);
      rackPatBeamRafRef.current = null;
    }
    // Stop any in-flight voices so a long-tail sample doesn't keep ringing.
    let now = 0;
    try {
      const m = (typeof getOrCreateMaster === "function") ? getOrCreateMaster() : null;
      if (m && m.context) now = m.context.currentTime;
    } catch(_){}
    const voices = rackPatVoicesRef.current || [];
    for (let i = 0; i < voices.length; i++) {
      try { if (voices[i] && typeof voices[i].stop === "function") voices[i].stop(now); } catch(_){}
    }
    rackPatVoicesRef.current = [];
    rackPatChokeRef.current  = {};
    setRackPatPlaying(false);
    setRackPatPlayheadSec(0);
    // Sync ref synchronously so subsequent guards see PAT-off immediately.
    rackPatPlayingRef.current = false;
  };

  // Reposition the PAT-mode playhead to `targetTime` (seconds within the
  // pattern). When PAT is running, kills in-flight voices, re-anchors so
  // (ctxNow - newAnchor) = targetTime, resets the cursor, and updates the
  // visible beam. No-op when PAT isn't running — the caller's normal
  // editor-local seek path handles the stopped case.
  const rackPatSeek = function(targetTime){
    if (!rackPatPlayingRef.current) return;
    const t = Math.max(0, targetTime || 0);
    let ctxNow = 0;
    try {
      const m = (typeof getOrCreateMaster === "function") ? getOrCreateMaster() : null;
      if (m && m.context) ctxNow = m.context.currentTime;
    } catch(_){ return; }
    // Stop in-flight voices so we don't leave a tail playing at the old
    // position.
    const voices = rackPatVoicesRef.current || [];
    for (let i = 0; i < voices.length; i++) {
      try { if (voices[i] && typeof voices[i].stop === "function") voices[i].stop(ctxNow); } catch(_){}
    }
    rackPatVoicesRef.current = [];
    rackPatChokeRef.current  = {};
    // Re-anchor so playheadPt = ctxNow - anchor = t.
    rackPatAnchorRef.current = ctxNow - t;
    rackPatCursorRef.current = t;
    setRackPatPlayheadSec(t);
  };

  // ── Phase 2A rack state ──
  // While the user is long-pressing a step cell + dragging vertically to
  // adjust velocity, this state holds the active drag info. When non-null,
  // the rack renders a floating velocity tooltip and disables the normal
  // tap-toggle on release. Cleared when the user lifts their finger.
  const [drumVelocityDrag, setDrumVelocityDrag] = useState(null);
  // ^ shape when active: { channelIdx, stepIdx, startY, startVel, currentVel }
  // Set to true by the long-press handler when a velocity drag completes,
  // so the click event that iOS fires after touchend gets suppressed
  // (otherwise the cell toggles OFF, removing the note the user just
  // adjusted). A ref instead of a DOM property because React may replace
  // the cell DOM element between long-press and click as state updates.
  const suppressNextStepClickRef = useRef(false);
  // Whether the pattern-management menu (rename / duplicate / delete) is open.
  const [drumPatternMenuOpen, setDrumPatternMenuOpen] = useState(false);
  // Rename dialog state — when non-null, shows an inline rename input.
  const [drumRenameDraft, setDrumRenameDraft] = useState(null);
  // Per-row expander — when set to a channelIdx, that row's swing+volume
  // controls expand below the step grid for that row. Tap the row's stripe
  // dot to expand; tap again to collapse. Only one row expanded at a time.
  const [drumExpandedRow, setDrumExpandedRow] = useState(null);
  // Pad-scoped piano roll: { trackId, patternId, channelIdx } | null
  // When set, the piano roll overlay opens in "drum channel" mode, editing
  // ONLY this pad's notes (read from pattern.channels[channelIdx].notes).
  // Set when the user taps a notes-mode preview strip; cleared on close.
  const [drumPadEditor, setDrumPadEditor] = useState(null);
  // Per-channel rename state — when non-null, shape:
  //   { channelIdx, draft }
  // Renders an inline input next to the row's expanded tray. The user types,
  // hits Enter (or taps Save) to commit; Esc / tap Cancel to abort.
  // Lives in the rack — not used outside the channel rack overlay.
  const [drumChannelRename, setDrumChannelRename] = useState(null);
  // Kit-switch confirmation. When the user picks a new kit from the picker
  // AND the active pattern already has hits, we show a confirm dialog so
  // they're not surprised when their kick suddenly sounds like a snare.
  // Shape: { newKitId, oldKitId } — null when no confirm pending.
  const [drumKitSwitchConfirm, setDrumKitSwitchConfirm] = useState(null);

  // ── Phase 2B: Graph editor state ──
  // When non-null, the full-screen graph editor overlay is open. Shape:
  //   { channelIdx, param }   param ∈ "vel"|"pitch"|"pan"|"shift"|"repeat"
  // Opened by long-pressing a row label in the channel rack. The user
  // can switch parameters via tabs at the top of the overlay without
  // re-opening — the channelIdx persists until the overlay is closed.
  const [drumGraphEditorState, setDrumGraphEditorState] = useState(null);
  // Live drag state for the graph editor — when the user drags a step
  // bar, this holds { stepIdx, startY, startVal, currentVal } so the
  // global mousemove/touchmove handler can resolve the drag delta and
  // call setStepParam. Cleared on release.
  const [drumGraphDrag, setDrumGraphDrag] = useState(null);

  // Defensive cleanup: whenever the rack closes (drumRackTrackId -> null),
  // stop any in-rack preview that's still running. The Close button does
  // this directly too, but this handles the case where the rack is closed
  // via some other path (auto-close after Place, project switch, etc).
  // Also closes the graph editor if it's open.
  useEffect(function(){
    if (drumRackTrackId === null) {
      // Stop PAT-loop scheduler if running.
      try { rackPatStop(); } catch(_){}
      if (drumPreviewSchedulerRef.current) {
        clearInterval(drumPreviewSchedulerRef.current);
        drumPreviewSchedulerRef.current = null;
        drumPreviewStateRef.current.activeSources.forEach(function(s){
          try { s.stop(0); } catch(e) {}
          try { s.disconnect(); } catch(e) {}
        });
        drumPreviewStateRef.current.activeSources = [];
        setDrumPreviewPatternId(null);
        setDrumPreviewPlayheadStep(-1);
      }
      // Close graph editor if it was open
      if (drumGraphEditorState) {
        setDrumGraphEditorState(null);
        setDrumGraphDrag(null);
      }
    }
  }, [drumRackTrackId]);

  // ── Rack-overlay rescheduling (REMOVED) ──
  // Previously this effect called doPlay(t) when the rack opened/closed
  // or the active pattern changed mid-playback, on the theory that
  // scheduled BufferSources for the rack's track needed to be flushed.
  // But the scheduler reads `tracks` from refs every tick, so opening
  // the rack doesn't actually change scheduling output — there's nothing
  // to flush. The reschedule was a vestige of an overlay-mode feature
  // that no longer exists, and it caused playhead freezes because doPlay's
  // internal stopAll() flips isPlaying false. Removed entirely. Master
  // transport now runs smoothly across rack open/close.

  // ── iOS Safari backgrounding / suspend handler ──
  //
  // PROBLEM: when the user switches apps, locks the phone, or pulls down
  // Control Center, iOS Safari pauses execution and suspends the
  // AudioContext. Our schedulers were ticking and pushing events into the
  // audio clock; when the page comes back the events fire all at once
  // (clicks, glitches, no sound at all in worst case).
  //
  // SOLUTION: hook visibilitychange + pagehide.
  //   • On HIDE: if playback or in-rack preview is active, stop everything
  //     cleanly so no scheduled events linger. We also pause the AudioContext
  //     to be polite (iOS does this anyway, but explicit suspend protects
  //     against subtle bugs).
  //   • On SHOW: if the AudioContext is suspended, ask it to resume so the
  //     next user-gesture play has a working context. iOS only honors
  //     ctx.resume() inside or right after a user gesture, but calling it
  //     here can't hurt — it'll either succeed or queue.
  //
  // We don't try to auto-resume playback. iOS will sometimes block it
  // silently, and what users actually want is to hit play themselves
  // and pick up where they left off — which currentTime already preserves.
  useEffect(function(){
    const onVisibilityChange = function() {
      if (document.hidden) {
        // PAGE HIDDEN — stop all audio activity cleanly.
        // Main transport
        if (isPlayingRef.current) {
          try { stopAll(); } catch(e) {}
          setIsPlaying(false);
        }
        // Stop the rack PAT-loop scheduler too — same reasoning as the
        // editor's local play. PAT runs its own setInterval scheduling
        // drum hits; after suspend, those scheduled hits would either
        // be lost or fire in a clump on resume. Cleanly stopping it
        // returns the rack to a known-stopped state.
        try {
          if (typeof rackPatStop === "function") rackPatStop();
        } catch(_){}
        // In-rack preview (independent scheduler)
        if (drumPreviewSchedulerRef.current) {
          clearInterval(drumPreviewSchedulerRef.current);
          drumPreviewSchedulerRef.current = null;
          drumPreviewStateRef.current.activeSources.forEach(function(s){
            try { s.stop(0); } catch(e) {}
            try { s.disconnect(); } catch(e) {}
          });
          drumPreviewStateRef.current.activeSources = [];
          setDrumPreviewPatternId(null);
          setDrumPreviewPlayheadStep(-1);
        }
        // Suspend the AudioContext explicitly to prevent any leftover
        // scheduled nodes (in case stopAll missed any) from firing on
        // return. iOS auto-suspends on background but being explicit
        // makes recovery cleaner.
        try {
          if (actxRef.current && actxRef.current.state === "running") {
            actxRef.current.suspend();
          }
        } catch(e) {}
      } else {
        // PAGE VISIBLE AGAIN — wake the AudioContext if it suspended,
        // restore master gain (stopAll on hide ramped it to 0), and
        // reconnect EVERY soundfont engine's audio node so the next
        // note has a path to the speakers.
        try {
          // iOS sometimes returns the ctx as "interrupted" instead of
          // "suspended" — both states need resume(). state === "running"
          // means nothing to do.
          if (actxRef.current && actxRef.current.state !== "running") {
            actxRef.current.resume().catch(function(){});
          }
        } catch(e) {}
        // Get a fresh master gain node — getOrCreateMaster reuses the
        // existing one when the AudioContext is still alive, and
        // rebuilds against a fresh actx if iOS recreated the context.
        let freshMaster = null;
        try { freshMaster = getOrCreateMaster(); } catch(_) {}
        // Restore master gain. stopAll(on hide) ramps it to 0 to kill
        // any tails; without this restore, audio stays silent until
        // the user presses play (which calls doPlay → restores gain).
        try {
          if (freshMaster && freshMaster.gain) {
            freshMaster.gain.cancelScheduledValues(0);
            freshMaster.gain.value = masterVolume;
          }
        } catch(_){}
        // Reconnect EVERY soundfont engine. The original code only
        // touched the singleton FluidSynth engine via getBFSoundfontEngine;
        // per-track FluidSynth instances and per-track SpessaSynth-live
        // engines (used by sound-shaped soundfont tracks) also need
        // their audioNode reconnected to the fresh master. iOS severs
        // the ScriptProcessor output connection during suspend, so any
        // engine that wasn't reconnected stays silent on noteOn. This
        // is the root cause of "soundfonts stop playing after backgrounding".
        try {
          if (typeof forEachBFSoundfontEngine === "function" && freshMaster) {
            forEachBFSoundfontEngine(function(eng){
              if (eng && typeof eng.reconnect === "function") {
                try { eng.reconnect(freshMaster); } catch(_){}
              }
            });
          } else if (freshMaster) {
            // Defensive fallback for any environment where the
            // forEach helper hasn't loaded yet — old behaviour.
            const sfEngine = (typeof getBFSoundfontEngine === "function")
              ? getBFSoundfontEngine() : null;
            if (sfEngine && typeof sfEngine.reconnect === "function") {
              sfEngine.reconnect(freshMaster);
            }
          }
        } catch(e) {}
      }
    };
    // pagehide fires on iOS in cases visibilitychange doesn't (e.g. some
    // back-navigations). Treat it the same as a hide event for safety.
    const onPageHide = function() {
      if (isPlayingRef.current) {
        try { stopAll(); } catch(e) {}
        setIsPlaying(false);
      }
      if (drumPreviewSchedulerRef.current) {
        clearInterval(drumPreviewSchedulerRef.current);
        drumPreviewSchedulerRef.current = null;
        drumPreviewStateRef.current.activeSources.forEach(function(s){
          try { s.stop(0); } catch(e) {}
          try { s.disconnect(); } catch(e) {}
        });
        drumPreviewStateRef.current.activeSources = [];
        setDrumPreviewPatternId(null);
        setDrumPreviewPlayheadStep(-1);
      }
    };
    document.addEventListener("visibilitychange", onVisibilityChange);
    window.addEventListener("pagehide", onPageHide);
    return function() {
      document.removeEventListener("visibilitychange", onVisibilityChange);
      window.removeEventListener("pagehide", onPageHide);
    };
  }, []);

  // ── Velocity-drag tracker ──
  // When the user long-presses a step cell, drumVelocityDrag is set with
  // { channelIdx, stepIdx, startY, startVel, currentVel }. We listen to
  // global mousemove/touchmove events and update the velocity based on
  // vertical drag distance. ~150px of vertical travel = full velocity range.
  // On release, we commit and clear state.
  // Velocity-drag end safety net — clears stuck drag state if pointer-up
  // somehow doesn't fire (e.g. pointer cancelled by OS-level gesture).
  // The actual velocity update during drag is handled by the cell's
  // pointer-event handlers below.
  useEffect(function(){
    if (!drumVelocityDrag) return;
    const onEnd = function() { setDrumVelocityDrag(null); };
    window.addEventListener("blur", onEnd);
    return function(){
      window.removeEventListener("blur", onEnd);
    };
  }, [drumVelocityDrag]);

  // ── Graph editor bar-drag tracker ──
  // When the user grabs a bar in the full-screen graph editor, drumGraphDrag
  // is set with { stepIdx, startY, startVal, currentVal }. Vertical drag
  // adjusts the parameter value; ~150px of travel covers the full param range.
  // Released by mouseup/touchend.
  useEffect(function(){
    if (!drumGraphDrag || !drumGraphEditorState) return;
    const PX_PER_FULL_RANGE = 150;
    const ge = drumGraphEditorState;
    // Param range for the active tab
    const ranges = {
      vel:    { min: 0,                max: DRUM_MAX_VELOCITY, integer: false },
      pitch:  { min: DRUM_PITCH_MIN,   max: DRUM_PITCH_MAX,   integer: true  },
      pan:    { min: DRUM_PAN_MIN,     max: DRUM_PAN_MAX,     integer: false },
      shift:  { min: DRUM_SHIFT_MIN,   max: DRUM_SHIFT_MAX,   integer: false },
      repeat: { min: DRUM_REPEAT_MIN,  max: DRUM_REPEAT_MAX,  integer: true  },
    };
    const r = ranges[ge.param] || ranges.vel;
    const onMove = function(e) {
      const clientY = e.touches && e.touches[0] ? e.touches[0].clientY : e.clientY;
      if (clientY == null) return;
      const dy = drumGraphDrag.startY - clientY;  // up = positive
      const range = r.max - r.min;
      const delta = (dy / PX_PER_FULL_RANGE) * range;
      let newVal = Math.max(r.min, Math.min(r.max, drumGraphDrag.startVal + delta));
      if (r.integer) newVal = Math.round(newVal);
      setDrumGraphDrag(function(prev){
        if (!prev) return prev;
        return { ...prev, currentVal: newVal };
      });
      // Commit through the rack-mounted helper. Same pattern as velocity-drag:
      // we replicate the mutation here so it works even if the rack overlay
      // re-renders between drag events.
      setTracks(function(prev){
        return prev.map(function(tr){
          if (tr.id !== drumRackTrackId) return tr;
          const activeId = drumActivePatternByTrack[drumRackTrackId]
            || ((tr.patterns||[])[0] && (tr.patterns||[])[0].id);
          return { ...tr, patterns: (tr.patterns || []).map(function(p){
            if (p.id !== activeId) return p;
            const newChannels = p.channels.map(function(ch, ci){
              if (ci !== ge.channelIdx) return ch;
              const newSteps = ch.steps.slice();
              newSteps[drumGraphDrag.stepIdx] = setStepParam(
                newSteps[drumGraphDrag.stepIdx], ge.param, newVal);
              return { ...ch, steps: newSteps };
            });
            return { ...p, channels: newChannels };
          })};
        });
      });
      e.preventDefault();
    };
    const onEnd = function() { setDrumGraphDrag(null); };
    window.addEventListener("mousemove", onMove);
    window.addEventListener("mouseup",   onEnd);
    window.addEventListener("touchmove", onMove, { passive: false });
    window.addEventListener("touchend",  onEnd);
    window.addEventListener("touchcancel", onEnd);
    return function(){
      window.removeEventListener("mousemove", onMove);
      window.removeEventListener("mouseup",   onEnd);
      window.removeEventListener("touchmove", onMove);
      window.removeEventListener("touchend",  onEnd);
      window.removeEventListener("touchcancel", onEnd);
    };
  }, [drumGraphDrag ? drumGraphDrag.stepIdx : null,
      drumGraphDrag ? drumGraphDrag.startY : null,
      drumGraphDrag ? drumGraphDrag.startVal : null,
      drumGraphEditorState ? drumGraphEditorState.param : null,
      drumGraphEditorState ? drumGraphEditorState.channelIdx : null,
      drumRackTrackId, drumActivePatternByTrack]);

  // ── Ruler drag ref ───────────────────────────────────────────────────────
  const rulerDragRef = useRef(null); // { mode: "in"|"out"|"new", startX, startT }
  // Direct ref to the ruler tap DOM element. We attach touch listeners
  // imperatively in a useEffect below rather than via React's onTouchStart
  // props because React's synthetic touch listeners are PASSIVE — meaning
  // e.preventDefault() inside the handler is silently ignored. iOS Safari
  // then sees the touch as ambiguous, decides it might become a scroll
  // gesture, and cancels the touchend's tap-to-seek logic. This was the
  // root cause of "tap-to-seek doesn't work once audio is loaded" — with
  // a clip on the timeline, the content gets long enough that iOS's
  // scroll-vs-tap heuristic flips the wrong way. Native non-passive
  // listeners give preventDefault its teeth back.
  const rulerElRef = useRef(null);

  // ── Clip/track selection state ───────────────────────────────────────────
  const [selectedTrackId, setSelectedTrackId] = useState(null); // which vocal track records into
  const [selectedClipId, setSelectedClipId] = useState(null);
  // Ticks up by 1 every time the user nudges the selected clip. Used purely
  // as a re-render trigger for the readout-pulse animation in the Nudge bar
  // so the user gets visible confirmation that the tap registered.
  const [nudgePulseTick, setNudgePulseTick] = useState(0);
  const dragRef = useRef(null);

  // ── Track reorder via long-press drag ────────────────────────────────────
  const [reorderDragId,   setReorderDragId]   = useState(null);  // id of track being dragged
  const [reorderDropIdx,  setReorderDropIdx]  = useState(null);  // index where it will drop
  const reorderLongPressRef = useRef(null);   // setTimeout handle
  const reorderDragRef      = useRef(null);   // { trackId, startY, currentIdx }

  // Reorder helpers
  const reorderMoveTrack = useCallback(function(fromIdx, toIdx) {
    if (fromIdx === toIdx) return;
    setTracks(function(prev) {
      const next = [...prev];
      const [moved] = next.splice(fromIdx, 1);
      next.splice(toIdx, 0, moved);
      return next;
    });
  }, [setTracks]);

  const handleHeaderLongPressStart = useCallback(function(e, track) {
    const touch = e.touches ? e.touches[0] : e;
    const startY = touch.clientY;
    const currentIdx = tracks.findIndex(function(t){ return t.id === track.id; });
    reorderLongPressRef.current = setTimeout(function() {
      // Vibrate to confirm hold (if supported)
      if (navigator.vibrate) navigator.vibrate(40);
      setReorderDragId(track.id);
      setReorderDropIdx(currentIdx);
      reorderDragRef.current = { trackId: track.id, startY, currentIdx };
    }, 500);
  }, [tracks]);

  const handleHeaderLongPressCancel = useCallback(function() {
    clearTimeout(reorderLongPressRef.current);
  }, []);

  const handleHeaderDragMove = useCallback(function(e) {
    if (!reorderDragRef.current) return;
    e.preventDefault();
    const touch = e.touches ? e.touches[0] : e;
    const dy = touch.clientY - reorderDragRef.current.startY;
    const movedRows = Math.round(dy / TRACK_H);
    const fromIdx = reorderDragRef.current.currentIdx;
    const toIdx = Math.max(0, Math.min(tracks.length - 1, fromIdx + movedRows));
    setReorderDropIdx(toIdx);
  }, [tracks.length, TRACK_H]);

  const handleHeaderDragEnd = useCallback(function() {
    if (!reorderDragRef.current) return;
    const fromIdx = reorderDragRef.current.currentIdx;
    if (reorderDropIdx !== null && reorderDropIdx !== fromIdx) {
      reorderMoveTrack(fromIdx, reorderDropIdx);
    }
    setReorderDragId(null);
    setReorderDropIdx(null);
    reorderDragRef.current = null;
    clearTimeout(reorderLongPressRef.current);
  }, [reorderDropIdx, reorderMoveTrack]);

  useEffect(function () { zoomRef.current = zoom; }, [zoom]);
  useEffect(function () { isPlayingRef.current = isPlaying; }, [isPlaying]);
  useEffect(function () { tracksRef.current = tracks; }, [tracks]);

  // Set initial position — Bar1 under centred playhead at t=0
  useEffect(function () {
    if (scrollRef.current && trackContainerRef.current) {
      if(scrollRef.current) scrollRef.current.scrollLeft = 0;
    }
  }, []);

  // ── Studio mount: request mic permission only after user navigates to Studio ──
  // A 800ms delay ensures the tab transition completes before the iOS dialog appears.
  // Uses the Permissions API to check if mic is genuinely already granted,
  // so we only skip the prompt when the browser has confirmed it.
  useEffect(function () {
    var cancelled = false;
    var timer = setTimeout(function () {
      if (cancelled) return;
      async function init() {
        // 1. Check if mic permission is already granted via Permissions API
        var alreadyGranted = false;
        try {
          if (navigator.permissions) {
            var result = await navigator.permissions.query({ name: "microphone" });
            alreadyGranted = result.state === "granted";
          }
        } catch(e) {
          // Permissions API not available (some iOS versions) — fall back to localStorage
          try { alreadyGranted = localStorage.getItem("bf_mic_granted") === "1"; } catch(e2) {}
        }

        if (alreadyGranted) {
          if (!cancelled) { setMicReady(true); }
        } else {
          // Ask for permission
          try {
            var stream = await navigator.mediaDevices.getUserMedia({ audio: true, video: false });
            stream.getTracks().forEach(function(t) { t.stop(); });
            // Restore speaker route immediately after mic permission check closes the stream
            await restoreIOSSpeaker();
            if (!cancelled) {
              setMicReady(true);
              try { localStorage.setItem("bf_mic_granted", "1"); } catch(e) {}
            }
          } catch(e) {
            if (!cancelled) {
              setMicDenied(true);
              try { localStorage.removeItem("bf_mic_granted"); } catch(e2) {}
              if (e.name === "NotAllowedError") {
                setError("Mic access denied. Go to Settings → Safari → Microphone → Allow.");
              }
            }
          }
        }

        // 2. Always run headphone detection
        if (!cancelled) {
          setTimeout(checkHeadphones, 300);
          setTimeout(checkHeadphones, 1200);
        }
        // 3. Pre-warm playback AudioContext
        if (!cancelled && (!actxRef.current || actxRef.current.state === "closed")) {
          try {
            actxRef.current = new (window.AudioContext || window.webkitAudioContext)({
              latencyHint: "interactive",
            });
          } catch(e) {}
        }
      }
      init();
    }, 800);
    return function() { cancelled = true; clearTimeout(timer); };
  }, []);

  // ── Lazy mic permission — kept for backward compat, now rarely needed ──
  // requestMicPermissionOnce is a no-op if micReady is already true from mount init
  const requestMicPermissionOnce = async function () {
    if (micReady) return true;
    try {
      var stream = await navigator.mediaDevices.getUserMedia({ audio: true });
      // Immediately stop — we just needed the permission grant
      stream.getTracks().forEach(function (t) { t.stop(); });
      // Restore speaker route after mic closes
      await restoreIOSSpeaker();
      setMicReady(true);
      try { localStorage.setItem("bf_mic_granted", "1"); } catch(e) {}
      setTimeout(checkHeadphones, 300);
      return true;
    } catch (e) {
      setMicDenied(true);
      if (e.name === "NotAllowedError") {
        setError("Mic access denied. Go to Settings → Safari → Microphone → Allow.");
      }
      return false;
    }
  };

  // ── Computed ──────────────────────────────────────────────────
  // BAR-ANCHORED LAYOUT: pixels-per-second scales with BPM so that one BAR is
  // always the same pixel width no matter the tempo. Clip data is still stored
  // in seconds, but on a BPM change we also rescale those seconds (see
  // setBpmWithRescale) by oldBpm/newBpm. The two effects cancel in pixel space,
  // so clips and the bar grid both stay visually fixed when tempo changes —
  // while a clip remains a whole number of bars, so it always plays in time.
  // 120 is the reference tempo (layout identical to the old seconds-based one
  // at 120 BPM, preserving the existing zoom feel).
  const BPM_REF       = 120;
  const effectivePPS = PPS * zoom * (bpm / BPM_REF);
  effectivePPSRef.current = effectivePPS; // keep ref in sync for lasso closure
  const spb          = 60 / bpm;
  const spBar        = spb * timeSigNum;

  // ── Dynamic timeline width ────────────────────────────────────────────────
  // 1. Find the furthest endpoint across all clips (startTime + trimmed duration)
  const clipsFurthestEnd = tracks.reduce(function(acc, t) {
    const clips = t.clips || (t.audioBuffer ? [{startTime:t.startTime||0, duration:t.audioBuffer.duration, trimEnd:t.audioBuffer.duration}] : []);
    const trackEnd = clips.reduce(function(tAcc, cl) {
      const end = (cl.startTime||0) + ((cl.trimEnd||cl.duration||0) - (cl.trimStart||0));
      return end > tAcc ? end : tAcc;
    }, 0);
    return trackEnd > acc ? trackEnd : acc;
  }, 0);
  // 2. During recording, extend by live elapsed recording time so the scrollable
  //    area expands in real-time as the take grows (recDurRef updates every RAF tick)
  const recExtension = isRecording ? recDurRef.current : 0;
  // 3. Combine: max of clip end, playhead position (so timeline always shows where we are),
  //    and live recording extension — then add 10s buffer so playhead never hits the wall
  const BUFFER_SECS = 10;
  const totalDur = Math.max(
    60,                                       // minimum 60s always visible
    clipsFurthestEnd + BUFFER_SECS,           // clips + 10s breathing room
    currentTime + BUFFER_SECS,               // playhead always has 10s ahead
    (recExtension > 0 ? currentTime + recExtension + BUFFER_SECS : 0) // live recording
  );
  // 4. Convert to pixels — zoom-aware so width stays correct at any zoom level
  const totalW   = totalDur * effectivePPS;
  const numBars  = Math.ceil(totalDur / spBar) + 2;
  // Beat tracks always get purple/magenta. Vocal tracks cycle through bright high-contrast colours.
  const COLORS       = ["#3B82F6","#22C55E","#F59E0B","#EC4899","#8B5CF6","#06B6D4","#EF4444","#F97316"];
  const VOCAL_COLORS = ["#38BDF8","#34D399","#FB923C","#F472B6","#A78BFA","#22D3EE","#F87171","#FBBF24"];
  const hasContent   = tracks.length > 0;

  // ── Load projects ─────────────────────────────────────────────
  useEffect(function () {
    try { setSavedProjects(JSON.parse(localStorage.getItem("bf_studio_projects") || "[]")); } catch (e) {}
  }, []);

  // ── Library "Open" handoff ────────────────────────────────────
  // When the Members → Library "Open" button is tapped, it stashes the
  // project id (and cloud flag) in localStorage and navigates here. This
  // must work in two cases: (a) Studio is freshly mounting, and (b) Studio
  // is ALREADY mounted from an earlier visit this session (studioVisited
  // stays true), in which case a mount-only effect would never fire. So we
  // both check once on mount AND listen for a custom event the Library
  // dispatches every time its Open button is pressed.
  useEffect(function () {
    let cancelled = false;

    const consumeHandoff = function () {
      let raw = null;
      try { raw = localStorage.getItem("bf_open_project_id"); } catch (e) {}
      if (!raw) return;
      try { localStorage.removeItem("bf_open_project_id"); } catch (e) {}
      let handoff;
      try { handoff = JSON.parse(raw); } catch (e) { handoff = { id: raw }; }
      if (!handoff) return;
      // Small delay so the audio context + refs are ready on a fresh mount.
      setTimeout(function () {
        if (cancelled) return;
        try {
          if (handoff.cloud && handoff.cloudId) {
            loadCloudProject(handoff.cloudId, handoff.name || "Project");
          } else {
            let list = [];
            try { list = JSON.parse(localStorage.getItem("bf_studio_projects") || "[]"); } catch (e) {}
            let match = list.find(function (p) { return String(p.id) === String(handoff.id); });
            if (!match && handoff.name) {
              match = list.find(function (p) { return p.name === handoff.name; });
            }
            if (match) loadProject(match);
            else if (handoff.cloudId) loadCloudProject(handoff.cloudId, handoff.name || "Project");
          }
        } catch (e) {
          console.warn("[BeatFinder] open-project handoff failed:", e);
        }
      }, 350);
    };

    // Case (a): freshly mounting — consume any pending handoff now.
    consumeHandoff();

    // Case (b): already mounted — the Library fires this event on Open.
    const onEvt = function () { consumeHandoff(); };
    window.addEventListener("bf:open-project", onEvt);
    return function () { cancelled = true; window.removeEventListener("bf:open-project", onEvt); };
  }, []);

  // ── Cloud project list (Phase 3) ──────────────────────────────
  // Fetch the user's cloud projects whenever the project picker opens.
  // Best-effort: silent on auth/permission errors (free tier 403, logged
  // out 401 are normal). Failures don't block the picker — local-only
  // projects still display from savedProjects.
  useEffect(function() {
    if (!showProjects) return;
    setCloudFetchState("loading");
    apiFetch("/api/studio/projects/list")
      .then(function(resp) {
        const list = (resp && resp.projects) || [];
        setCloudProjects(list);
        setCloudFetchState("done");
      })
      .catch(function(err) {
        var msg = (err && err.message) || "";
        // 403 = free tier, 401 = not logged in. Both expected, silent.
        if (msg && !msg.includes("403") && !msg.includes("401")) {
          console.warn("[BeatFinder] Cloud projects fetch failed:", err);
        }
        setCloudProjects([]);
        setCloudFetchState("error");
      });
  }, [showProjects]);

  // ── iOS Background Persistence ────────────────────────────────
  // iOS Safari can fully reload the page when the app is backgrounded.
  // We save lightweight studio state (no AudioBuffers — they can't be serialized)
  // to sessionStorage so the project name, BPM, key and settings survive a reload.
  // Audio clips themselves cannot be restored (they live in memory), but the project
  // identity and settings are recovered so the session feels uninterrupted.
  useEffect(function () {
    try {
      const snap = {
        projectName, bpm, projectKey, timeSigNum, zoom, vZoom,
        snapToGrid, loopEnabled, loopIn, loopOut,
        metronomeOn, showBeatGrid, micInputGain,
        // Save full track metadata including volume and pan
        tracksMeta: tracks.map(function(t) {
          return { id: t.id, name: t.name, type: t.type, color: t.color,
                   isMuted: t.isMuted, isSoloed: t.isSoloed,
                   volume: t.volume, pan: t.pan };
        }),
      };
      sessionStorage.setItem("bf_studio_state", JSON.stringify(snap));
    } catch(e) {}
  }, [projectName, bpm, projectKey, timeSigNum, zoom, vZoom, snapToGrid, loopEnabled, loopIn, loopOut, metronomeOn, showBeatGrid, micInputGain, tracks]);

  // Restore studio state on mount (handles iOS background reload)
  useEffect(function () {
    try {
      const raw = sessionStorage.getItem("bf_studio_state");
      if (!raw) return;
      const snap = JSON.parse(raw);
      if (snap.projectName) setProjectName(snap.projectName);
      if (snap.bpm)          setBpm(snap.bpm);
      if (snap.projectKey)   setProjectKey(snap.projectKey);
      if (snap.timeSigNum)   setTimeSigNum(snap.timeSigNum);
      if (snap.zoom)         setZoom(snap.zoom);
      if (snap.vZoom)        setVZoom(snap.vZoom);
      if (snap.snapToGrid !== undefined)  setSnapToGrid(snap.snapToGrid);
      if (snap.loopEnabled !== undefined) setLoopEnabled(snap.loopEnabled);
      if (snap.loopIn  !== undefined)     setLoopIn(snap.loopIn);
      if (snap.loopOut !== undefined && snap.loopOut > 0) setLoopOut(snap.loopOut);
      if (snap.metronomeOn !== undefined) setMetronomeOn(snap.metronomeOn);
      if (snap.showBeatGrid !== undefined) setShowBeatGrid(snap.showBeatGrid);
      if (snap.micInputGain !== undefined) setMicInputGainState(snap.micInputGain);
      // Restore track metadata (volume, pan, mute, solo) — clips can't be serialised
      if (Array.isArray(snap.tracksMeta) && snap.tracksMeta.length > 0) {
        setTracks(function(prev) {
          // If tracks already exist (e.g. loaded project), merge; otherwise seed shells
          if (prev.length > 0) {
            return prev.map(function(t) {
              const meta = snap.tracksMeta.find(function(m) { return m.id === t.id; });
              if (!meta) return t;
              return Object.assign({}, t, {
                name: meta.name ?? t.name,
                color: meta.color ?? t.color,
                isMuted: meta.isMuted ?? t.isMuted,
                isSoloed: meta.isSoloed ?? t.isSoloed,
                volume: meta.volume ?? t.volume,
                pan: meta.pan ?? t.pan,
              });
            });
          }
          // No tracks yet — restore shells so the track list isn't empty
          return snap.tracksMeta.map(function(m) {
            return {
              id: m.id, name: m.name, type: m.type || "vocal",
              color: m.color || "#30D158",
              isMuted: m.isMuted || false, isSoloed: m.isSoloed || false,
              volume: m.volume ?? 1, pan: m.pan ?? 0,
              clips: [], // audio clips can't be serialised — user sees empty lanes
            };
          });
        });
      }
    } catch(e) {}
  }, []); // run once on mount only

  // ── AudioContext ──────────────────────────────────────────────
  const getActx = function () {
    if (!actxRef.current || actxRef.current.state === "closed") {
      // Bluetooth devices need a bigger buffer window than the phone speaker
      // to avoid dropouts and glitching on BT's lossy transport. We use
      // "playback" latency hint whenever a headset is connected (wired or BT)
      // and the tighter "interactive" hint only on the phone speaker.
      const hint = headphonesInRef.current ? "playback" : "interactive";
      actxRef.current = new (window.AudioContext || window.webkitAudioContext)({
        latencyHint: hint,
      });
      chainsRef.current = {};
      fxNodesRef.current = {};
      masterGainRef.current = null;
      noiseGateWorkletReady.current = false;
      silentSrcNodeRef.current = null;
      ensureSilentAudio();
    }
    if (actxRef.current.state === "suspended") {
      actxRef.current.resume().catch(function(){});
    }
    return actxRef.current;
  };

  // ── Headphone detection + mic enumeration ────────────────────
  // Returns { headphonesConnected, mics: [{deviceId, label, isBuiltIn}] }
  // iOS Safari only reveals real device labels after mic permission is granted.
  // We identify built-in vs headset by label keywords; fallback to input count.
  const checkHeadphones = async function () {
    try {
      const devices = await navigator.mediaDevices.enumerateDevices();
      const outputs = devices.filter(function(d){ return d.kind === "audiooutput"; });
      const inputs  = devices.filter(function(d){ return d.kind === "audioinput"; });

      // 1st check: named audio output (works on desktop, Chrome Android)
      const hpByOutput = outputs.some(function(d){
        const l = (d.label||"").toLowerCase();
        return l.includes("headphone")||l.includes("earphone")||l.includes("airpods")||
               l.includes("bluetooth")||l.includes("headset")||l.includes("wired");
      });

      // 2nd check (iOS): wired headset exposes a labelled "Headset Microphone" audioinput
      // after mic permission is granted. This is the most reliable iOS signal.
      const hpByInputLabel = !hpByOutput && inputs.some(function(d){
        const l = (d.label||"").toLowerCase();
        return l.includes("headset")||l.includes("wired")||l.includes("external")||
               l.includes("earphone")||l.includes("airpod")||l.includes("earbud")||
               l.includes("bluetooth")||l.includes("headphone");
      });

      // 3rd check: labels visible + more than one input = headset mic present
      // Only applies when labels ARE populated (after mic permission granted)
      const anyLabel  = inputs.some(function(d){ return !!(d.label); });
      const hpByCount = !hpByOutput && !hpByInputLabel && anyLabel && inputs.length > 1;

      // NOTE: Removed hpByLatency (iPhone reports low latency regardless of headset state)
      // and hpByExtraInput (false positive after getUserMedia temporarily shows 2 inputs)

      // Bluetooth-specific detection. Used to warn about input-monitoring
      // latency (BT has ~100-200ms RTT that no software can fix) and to
      // pick a more forgiving audio-context buffer.
      const isBT = [...outputs, ...inputs].some(function(d) {
        const l = (d.label || "").toLowerCase();
        return l.includes("bluetooth") || l.includes("airpod") ||
               l.includes("airpods") || l.includes("beats");
      });
      bluetoothInRef.current = isBT;

      const hp = hpByOutput || hpByInputLabel || hpByCount;
      setHeadphonesIn(hp);

      // Classify mic inputs
      // iOS exposes labels like "iPhone Microphone" and "Headset Microphone" after permission.
      // Before permission all labels are blank — we fall back to index (index 0 = built-in,
      // index 1+ = external/headset on iOS when a headset is present).
      let builtInId   = null;
      let headsetId   = null;
      let builtInLabel = "📱 iPhone Mic";
      let headsetLabel = "🎧 Headset Mic";

      inputs.forEach(function(d, idx) {
        if (!d.deviceId) return;
        const l = (d.label||"").toLowerCase();
        const isHeadset = l.includes("headset")||l.includes("wired")||l.includes("external")||
                          l.includes("airpod")||l.includes("earphone")||l.includes("earbud")||
                          l.includes("bluetooth");
        const isBuiltIn = l.includes("iphone")||l.includes("built-in")||l.includes("internal")||
                          l.includes("front")||l.includes("default");

        if (l) {
          // Labels available — use keyword classification
          if (isHeadset && !headsetId) {
            headsetId   = d.deviceId;
            headsetLabel = "🎧 " + (d.label.length < 30 ? d.label : "Headset Mic");
          } else if ((isBuiltIn || (!isHeadset && !builtInId)) && !builtInId) {
            builtInId   = d.deviceId;
            builtInLabel = "📱 " + (d.label.includes("iPhone") ? "iPhone Mic" : (d.label.length < 30 ? d.label : "iPhone Mic"));
          }
        } else {
          // No labels yet (pre-permission) — assume index 0 = built-in, index 1 = headset
          if (idx === 0 && !builtInId) builtInId = d.deviceId;
          if (idx === 1 && !headsetId) headsetId = d.deviceId;
        }
      });

      // Build the two-option list
      const mics = [
        { deviceId: builtInId || "builtin", label: builtInLabel, isBuiltIn: true },
        { deviceId: headsetId || "headset", label: headsetLabel, isBuiltIn: false,
          detected: hp,
        },
      ];
      setAvailableMics(mics);
      return { headphonesConnected: hp, mics };
    } catch(e){ return { headphonesConnected: false, mics: [] }; }
  };

  useEffect(function () {
    checkHeadphones();

    const onDeviceChange = function() {
      // Wrap the whole handler — iOS audio events can throw obscure
      // native errors at any boundary (createGain on a half-closed
      // context, connect across detached contexts, etc.). A crash here
      // would unmount the entire Studio, so we contain it.
      try {
        // iOS audio-route switching (plug/unplug headphones, AirPods
        // connect/disconnect, Bluetooth speakers etc.) leaves multiple
        // pieces of state stale, but ONLY if the audio graph was already
        // up. If no AudioContext exists yet (user hasn't tapped anything
        // to trigger lazy-init), we must NOT force-create one here —
        // contexts created outside a user gesture stay suspended and
        // can't be resumed without one, leaving the app silent until a
        // page reload.
        const ctx = actxRef.current;
        if (!ctx) {
          // Nothing to fix — lazy-init will set everything up correctly
          // when the user next interacts. Just refresh the headphones label.
          setTimeout(checkHeadphones, 500);
          setTimeout(checkHeadphones, 1500);
          return;
        }
        // Resume if suspended (devicechange often suspends).
        if (ctx.state === "suspended") {
          ctx.resume().catch(function(){});
        }
        // Rebuild master gain if context-mismatched (happens when the
        // context got closed and recreated). getOrCreateMaster handles
        // the comparison internally; if nothing changed, no work happens.
        let freshMaster = null;
        try { freshMaster = getOrCreateMaster(); } catch(_) {}
        // Rewire the soundfont engine output(s) so FluidSynth samples
        // reach the new master. With per-track engines, each track has
        // its own engine whose audioNode connects to its FX chain entry
        // — those don't change on device switch (the chains stay live).
        // But the singleton (used for browser previews etc) IS connected
        // directly to master and needs the rewire. rewireOutput is
        // conservative: it silently bails if there's a context mismatch.
        try {
          if (typeof forEachBFSoundfontEngine === "function" && freshMaster) {
            forEachBFSoundfontEngine(function(eng){
              if (eng && typeof eng.rewireOutput === "function") {
                try { eng.rewireOutput(freshMaster); } catch(_){}
              }
            });
          } else {
            const sfEng = (typeof getBFSoundfontEngine === "function")
              ? getBFSoundfontEngine() : null;
            if (sfEng && freshMaster) {
              sfEng.rewireOutput(freshMaster);
            }
          }
        } catch(_){}
        // Defer the label enumeration — iOS needs time to settle after route change
        setTimeout(checkHeadphones, 500);
        setTimeout(checkHeadphones, 1500);
      } catch(e) {
        // Last-resort: log and continue. iOS will retry the device
        // change on the next event, and the polling fallback at the
        // end of this useEffect will catch anything we missed.
        try { console.warn("[devicechange] handler error", e && e.message); } catch(_){}
      }
    };
    navigator.mediaDevices.addEventListener("devicechange", onDeviceChange);

    // Poll every 2 seconds as fallback for devices that don't fire devicechange reliably
    const pollInterval = setInterval(checkHeadphones, 2000);

    return function(){
      navigator.mediaDevices.removeEventListener("devicechange", onDeviceChange);
      clearInterval(pollInterval);
    };
  }, []);

  // Re-scan as soon as mic permission is granted — iOS only reveals device labels
  // (e.g. "iPhone Microphone", "Headset Microphone") after the first getUserMedia call.
  // Without this, headphonesIn stays false even when a headset is already plugged in.
  useEffect(function () {
    if (micReady) {
      setTimeout(checkHeadphones, 400);
      setTimeout(checkHeadphones, 1500); // second pass after iOS settles labels
    }
  }, [micReady]);

  // Headphone connect/disconnect — only controls monitoring AVAILABILITY.
  // Never auto-starts monitoring. User must toggle it manually.
  const prevHeadphonesRef = useRef(null);
  useEffect(function () {
    if (prevHeadphonesRef.current === null) {
      prevHeadphonesRef.current = headphonesIn;
      return;
    }
    const wasHeadphones = prevHeadphonesRef.current;
    const isHeadphones  = headphonesIn;

    if (!isHeadphones && wasHeadphones) {
      // Headphones disconnected — defer all cleanup off the main thread.
      // iOS is mid-route-change here; doing work synchronously causes the freeze.
      setTimeout(function () {
        stopMonitoring();
        userPickedMicRef.current = false;
        setMicSource("builtin");
        setMonitorWarn("");
        // Resume AudioContext if iOS suspended it during the route change
        if (actxRef.current && actxRef.current.state === "suspended") {
          actxRef.current.resume().catch(function(){});
        }
      }, 200);
    }

    // On EITHER direction of change, if we're not playing, close the current
    // AudioContext so the next playback rebuilds it with the correct latency
    // hint ("playback" for headset/BT, "interactive" for speaker). This is
    // what prevents Bluetooth playback dropouts after a mid-session BT pair.
    if (wasHeadphones !== isHeadphones && !isPlayingRef.current) {
      setTimeout(function() {
        try {
          if (actxRef.current && actxRef.current.state !== "closed") {
            actxRef.current.close().catch(function(){});
          }
          actxRef.current = null;
          chainsRef.current = {};
          fxNodesRef.current = {};
          masterGainRef.current = null;
          noiseGateWorkletReady.current = false;
          silentSrcNodeRef.current = null;
        } catch(e) {}
      }, 300);
    }

    prevHeadphonesRef.current = headphonesIn;
  }, [headphonesIn]);

  // ── Shared mic constraint builder ────────────────────────────
  // Resolves the correct deviceId for "builtin" or "headset" on iOS and returns
  // a getUserMedia audio constraints object. Used by both monitoring and recording
  // so they always use the same mic source the user has selected.
  //
  // CLEAN SIGNAL DESIGN:
  //   • echoCancellation: false  — browser AEC destroys vocal tone; use post-processing instead
  //   • noiseSuppression: false  — browser NS adds artifacts/pumping; Noise Remover FX handles this
  //   • autoGainControl:  false  — AGC causes level jumps mid-take; set gain manually via mixer
  //   • sampleRate ideal 48000   — RNNoise native rate; falls back gracefully if unsupported
  //   • channelCount ideal 1     — mono mic; "ideal" won't fail on stereo-only devices
  //   • All device constraints use "ideal" not "exact" to prevent OverconstrainedError on iOS
  const buildMicConstraints = async function (wantSource, extraConstraints) {
    const base = Object.assign({
      echoCancellation: { exact: false },   // Never allow AEC — it destroys vocal tone
      noiseSuppression: { exact: false },   // Never allow browser NS — use Noise Remover FX
      autoGainControl:  { exact: false },   // Never allow AGC — causes level jumps mid-take
      channelCount:     { ideal: 1 },       // Request mono from hardware (ideal = won't fail on stereo-only devices)
      sampleRate:       { ideal: 48000, min: 44100 }, // 48kHz for best quality; 44100 fallback
      sampleSize:       { ideal: 16 },      // 16-bit samples — standard high-quality PCM depth
      // Note: NO `latency` constraint. iOS interprets `latency: 0` as "use the
      // low-power audio path" which routes through a quality-reduced codec.
      // Omitting it lets iOS pick the best-quality path for the requested sampleRate.
    }, extraConstraints || {});

    const wantBuiltIn = wantSource === "builtin" || wantSource === "default";

    if (wantBuiltIn) {
      // Try to get the real hardware deviceId for the built-in iPhone mic
      let builtInDeviceId = null;
      const builtInEntry = availableMics.find(function(m){ return m.isBuiltIn; });
      if (builtInEntry && builtInEntry.deviceId && builtInEntry.deviceId !== "builtin") {
        builtInDeviceId = builtInEntry.deviceId;
      }
      if (!builtInDeviceId) {
        // Labels hidden — request permission then re-enumerate to get real IDs
        try {
          const tmp = await navigator.mediaDevices.getUserMedia({ audio: true });
          tmp.getTracks().forEach(function(t){ t.stop(); });
          const freshDevices = await navigator.mediaDevices.enumerateDevices();
          freshDevices.filter(function(d){ return d.kind === "audioinput"; }).forEach(function(d){
            const l = (d.label||"").toLowerCase();
            if (!builtInDeviceId && (l.includes("iphone")||l.includes("built-in")||l.includes("internal"))) {
              builtInDeviceId = d.deviceId;
            }
          });
        } catch(e) {}
      }
      // Use "ideal" so iOS doesn't throw OverconstrainedError if deviceId becomes stale
      base.deviceId = builtInDeviceId ? { ideal: builtInDeviceId } : undefined;
    } else {
      // Headset mic — use known deviceId or let iOS auto-route (no constraint = headset when plugged in)
      const headsetEntry = availableMics.find(function(m){ return !m.isBuiltIn; });
      const headsetId = headsetEntry && headsetEntry.deviceId !== "headset" ? headsetEntry.deviceId : null;
      if (headsetId) base.deviceId = { ideal: headsetId };
      // No deviceId → iOS routes to headset automatically when plugged in
    }
    return base;
  };

  // ── Input Monitoring ──────────────────────────────────────────
  // DESIGN: Monitoring is a completely separate pipeline from recording.
  //   • Own mic stream (never shared with recording micStreamRef)
  //   • Own AudioContext (monitorCtxRef) — never touches actxRef (playback)
  //   • Mic stream is stopped fully on stopMonitoring so iOS releases the input session
  //   • This prevents monitoring from forcing iOS into "recording session" behaviour
  //     which was causing the large output buffer / background playback failures
  const monitorMicStreamRef = useRef(null); // monitoring-only mic stream, separate from recording

  const startMonitoring = async function (forceLowLatency, forceMicSource) {
    if (monitorSrcRef.current) return; // already running
    setMonitorWarn("");

    const useMicSource = forceMicSource !== undefined ? forceMicSource : micSource;
    const audioConstraints = await buildMicConstraints(useMicSource);

    try {
      // Always open a fresh dedicated stream for monitoring — never reuse recording stream
      const stream = await navigator.mediaDevices.getUserMedia({
        audio: { ...audioConstraints, echoCancellation: false, noiseSuppression: false, autoGainControl: false },
        video: false,
      });
      monitorMicStreamRef.current = stream;
      monitorStreamRef.current    = stream;

      // Reuse or create monitoring AudioContext.
      // For Bluetooth headsets we use "playback" hint — BT can't deliver low
      // latency monitoring anyway (~100-200ms RTT), so a larger buffer at
      // least keeps audio glitch-free. Wired headsets use "interactive".
      if (!monitorCtxRef.current || monitorCtxRef.current.state === "closed") {
        monitorCtxRef.current = new (window.AudioContext || window.webkitAudioContext)({
          latencyHint: bluetoothInRef.current ? "playback" : "interactive",
        });
      }
      const mCtx = monitorCtxRef.current;
      if (mCtx.state === "suspended") await mCtx.resume();

      // Surface a one-time warning about BT monitoring latency — user can
      // still proceed, but at least they know why their vocal sounds delayed.
      if (bluetoothInRef.current) {
        setMonitorWarn("Bluetooth audio has noticeable latency — use wired headphones for low-latency monitoring");
      }

      const src      = mCtx.createMediaStreamSource(stream);
      const micBoost = mCtx.createGain();
      micBoost.gain.value = micInputGainRef.current;
      const gain = mCtx.createGain();
      gain.gain.value = monitorVol;

      const analyser = mCtx.createAnalyser();
      analyser.fftSize = 256;

      // Mono → stereo centre: prevents iOS routing mono only to left channel
      const splitter = mCtx.createChannelSplitter(1);
      const merger   = mCtx.createChannelMerger(2);

      // ── Autotune insert ───────────────────────────────────────────
      // Register worklet and insert AutotuneProcessor between micBoost and splitter.
      // If registration fails (older iOS), falls back to direct connection.
      let autotuneNode = null;
      const at = autoPitch || {};
      try {
        const atReady = await registerAutotuneWorklet(mCtx);
        if (atReady) {
          autotuneNode = new AudioWorkletNode(mCtx, "autotune-processor", {
            numberOfInputs:  1,
            numberOfOutputs: 1,
            outputChannelCount: [1],
          });
          const KEY_OFFSETS = {C:0,"C#":1,Db:1,D:2,"D#":3,Eb:3,E:4,F:5,"F#":6,Gb:6,G:7,"G#":8,Ab:8,A:9,"A#":10,Bb:10,B:11};
          const keyOffset = KEY_OFFSETS[at.key || "C"] || 0;
          // bypass=0 means ACTIVE, bypass=1 means bypassed
          autotuneNode.parameters.get("bypass").value    = at.on ? 0 : 1;
          autotuneNode.parameters.get("key").value       = keyOffset;
          autotuneNode.parameters.get("speed").value     = at.speed ?? 50;
          autotuneNode.parameters.get("humanize").value  = at.humanize ?? 10;
          autotuneNode.parameters.get("wet").value       = at.wet ?? 1;
          autotuneNode.parameters.get("vibrato").value   = at.vibrato ?? 0;
          autotuneNode.port.postMessage({ scale: at.scale || "major" });
          window.__bfAutotuneNode = autotuneNode;
        }
      } catch(e) {
        console.warn("Autotune worklet failed:", e);
        autotuneNode = null;
      }

      // Build chain: src → micBoost → [autotune?] → splitter → merger → gain → destination
      src.connect(micBoost);
      if (autotuneNode) {
        micBoost.connect(autotuneNode);
        autotuneNode.connect(splitter);
      } else {
        micBoost.connect(splitter);
      }
      splitter.connect(merger, 0, 0);
      splitter.connect(merger, 0, 1);
      merger.connect(gain);
      gain.connect(mCtx.destination);
      gain.connect(analyser);

      monitorSrcRef.current      = src;
      monitorGainRef.current     = gain;
      monitorMicBoostRef.current = micBoost;
      monitorAnalyserRef.current = analyser;
      monitorSplitterRef.current = splitter;
      monitorMergerRef.current   = merger;
      setMonitoringOn(true);
    } catch (e) {
      monitorMicStreamRef.current = null;
      monitorStreamRef.current    = null;
      setMonitorWarn("Could not start monitoring: " + (e.message || e.name));
    }
  };

  const stopMonitoring = function () {
    // Immediately update UI state — don't wait for cleanup
    setMonitoringOn(false);

    // Capture refs now before they're cleared
    const gain      = monitorGainRef.current;
    const src       = monitorSrcRef.current;
    const boost     = monitorMicBoostRef.current;
    const analyser  = monitorAnalyserRef.current;
    const splitter  = monitorSplitterRef.current;
    const merger    = monitorMergerRef.current;
    const mCtx      = monitorCtxRef.current;
    const micStream = monitorMicStreamRef.current;

    // Clear refs immediately so nothing tries to reuse them
    monitorSrcRef.current      = null;
    monitorGainRef.current     = null;
    monitorMicBoostRef.current = null;
    monitorAnalyserRef.current = null;
    monitorSplitterRef.current = null;
    monitorMergerRef.current   = null;
    monitorStreamRef.current   = null;
    monitorMicStreamRef.current = null;

    // Defer all actual audio cleanup off the main thread entirely.
    // track.stop() on a disconnected hardware device can block on iOS —
    // running it in a timeout prevents the UI freeze on headphone disconnect.
    setTimeout(function () {
      // Ramp gain to zero first to avoid pop
      if (gain && mCtx && mCtx.state !== "closed") {
        try { gain.gain.setTargetAtTime(0, mCtx.currentTime, 0.02); } catch(e) {}
      }
      setTimeout(function () {
        try { src     && src.disconnect(); }     catch(e) {}
        try { gain    && gain.disconnect(); }    catch(e) {}
        try { boost   && boost.disconnect(); }   catch(e) {}
        try { analyser && analyser.disconnect(); } catch(e) {}
        try { splitter && splitter.disconnect(); } catch(e) {}
        try { merger  && merger.disconnect(); }  catch(e) {}

        // Stop mic tracks — deferred so iOS doesn't block main thread
        if (micStream) {
          micStream.getTracks().forEach(function(t) {
            try { t.stop(); } catch(e) {}
          });
        }

        // Close monitor context entirely during recording so iOS fully releases
        // the audio session — suspend() still holds the session and causes mic ducking.
        if (mCtx && mCtx.state !== "closed") {
          mCtx.close().catch(function(){});
          // Clear the ref so startMonitoring creates a fresh context next time
          monitorCtxRef.current = null;
        }

        // After the monitor context is fully gone, kick the main playback
        // AudioContext through a suspend → resume cycle. iOS's audio session
        // category gets upgraded to "playAndRecord" the moment any context
        // opens a mic stream — which downgrades output quality (sample rate
        // handling, mono routing, lower volume). Closing the monitor context
        // alone doesn't revert this; the main context has to renegotiate.
        //
        // Only do this if NO audio is currently playing — suspending mid-
        // playback would cause an audible glitch. For mid-playback toggles
        // the session gets refreshed on the next play cycle anyway.
        // 100ms delay gives iOS time to process the monitor close above.
        setTimeout(function(){
          const main = actxRef.current;
          if (!main || main.state === "closed") return;
          if (isPlayingRef.current) return;  // don't disturb active playback
          if (main.state === "running") {
            main.suspend().then(function(){
              try { main.resume(); } catch(_){}
            }).catch(function(){
              try { main.resume(); } catch(_){}
            });
          }
        }, 100);
      }, 80);
    }, 0);
  };

  useEffect(function(){
    if (monitorGainRef.current && monitorCtxRef.current) {
      monitorGainRef.current.gain.setTargetAtTime(monitorVol, monitorCtxRef.current.currentTime, 0.01);
    }
  }, [monitorVol]);

  useEffect(function(){
    micInputGainRef.current = micInputGain;
    if (monitorMicBoostRef.current && monitorCtxRef.current) {
      monitorMicBoostRef.current.gain.setTargetAtTime(micInputGain, monitorCtxRef.current.currentTime, 0.01);
    }
  }, [micInputGain]);

  // ── Scroll container helpers ──────────────────────────────────
  // With paddingLeft=PLAYHEAD_X on inner div, Bar1 (x=0) sits exactly under the playhead.
  // scrollLeft=0 → t=0 (Bar1 under playhead). scrollLeft=t*effectivePPS → t plays through.
  const getTimeFromScroll = function () {
    const el = scrollRef.current;
    if (!el) return 0;
    return Math.max(0, el.scrollLeft / effectivePPS);
  };

  // Set the waveform container's translateX so Bar1 is under the centred playhead
  // Formula: offsetX = colWidth/2 - t * PPS
  // At t=0: offsetX = colWidth/2  → Bar1 is at the centre
  // At t=5s: offsetX = colWidth/2 - 500  → content scrolled 500px left
  const setOffsetForTime = function (t) {
    // Now uses scrollLeft — translateX is gone
    const el = scrollRef.current;
    if (el) el.scrollLeft = Math.max(0, t * effectivePPS);
  };

  // Move playhead div directly — no React re-render, no jitter
  // Always uses effectivePPSRef.current so it's accurate even in stale closures.
  const updatePlayheadDOM = function (t, scrollLeft) {
    const ph = playheadRef.current;
    if (!ph) return;
    const pps = effectivePPSRef.current;
    const sl = scrollLeft !== undefined ? scrollLeft : (scrollRef.current ? scrollRef.current.scrollLeft : 0);
    const px = SIDEBAR_W + t * pps - sl;
    ph.style.left = px + "px";
  };

  // ── syncUItoTime ──────────────────────────────────────────────
  // Single source of truth for updating playhead + scroll when NOT in the RAF loop.
  // Call this at the end of any seek, rewind, skip, or pause operation.
  // Mirrors the 75%-pin logic from the RAF loop so paused seeks feel identical.
  const syncUItoTime = function (t) {
    const pps = effectivePPSRef.current;
    // Write all position refs
    playheadAtRef.current = t;
    liveTimeRef.current   = t;
    setCurrentTime(t);
    const el = scrollRef.current;
    if (!el) return;
    const viewW      = el.clientWidth;
    const playheadPx = t * pps;                    // content-space position
    const pinAt      = viewW * 0.75;               // same threshold as RAF loop
    const targetSL   = playheadPx - pinAt;
    // Scroll forward to keep playhead at 75% pin once it crosses that mark
    if (targetSL > el.scrollLeft) {
      el.scrollLeft = Math.max(0, targetSL);
    }
    // If playhead is offscreen to the left, snap it back into view
    const playheadViewX = playheadPx - el.scrollLeft;
    if (playheadViewX < 0) {
      el.scrollLeft = Math.max(0, playheadPx - pinAt);
    }
    // Finally draw the playhead at its exact pixel position
    updatePlayheadDOM(t, el.scrollLeft);
  };

  // ── RAF playback loop ─────────────────────────────────────────
  // Mutates DOM transform directly — no React re-render per frame = smooth 60fps
  useEffect(function () {
    if (!isPlaying) { cancelAnimationFrame(animRef.current); loopRestartingRef.current = false; return; }
    var lastUIUpdate = 0;

    const tick = function (ts) {
      const actx = actxRef.current;
      if (!actx) {
        // Don't kill the chain — schedule next frame and try again.
        // Transient nullness (rack open/close transitions) used to kill
        // the playhead RAF permanently. Now self-heals.
        animRef.current = requestAnimationFrame(tick);
        return;
      }
      const elapsed = actx.currentTime - masterStartRef.current;
      let t;
      // During loop-restart wait: masterStartRef has been pushed into the
      // future by LOOP_RESTART_LEAD so elapsed is negative. The naive
      // formula (playheadAtRef + elapsed = loopIn - 0.2) would yank the
      // playhead backward — that's the "jump" the user sees. Override so
      // the playhead instead approaches loopOut smoothly over the wait
      // window, then snaps cleanly to loopIn at the wrap point.
      if (loopRestartingRef.current && elapsed < 0) {
        t = loopOut + elapsed;          // loopOut - lead → loopOut
      } else {
        t = playheadAtRef.current + elapsed;
      }
      liveTimeRef.current = t;

      const el = scrollRef.current;
      if (el) {
        const viewW          = el.clientWidth;
        const playheadPx     = t * effectivePPS;         // playhead in content space
        const playheadViewX  = playheadPx - el.scrollLeft; // playhead within viewport

        // Only auto-scroll if the user isn't manually scrolling
        if (userScrolledAt.current === null) {
          // Scroll forward to keep playhead at 75% of viewport once it hits that mark
          const pinAt    = viewW * 0.75;
          const targetSL = playheadPx - pinAt;
          if (targetSL > el.scrollLeft) {
            el.scrollLeft = targetSL;
          }
          // If playhead goes out of view entirely (e.g. after rewind), snap it back immediately
          if (playheadViewX < 0 || playheadViewX > viewW) {
            el.scrollLeft = Math.max(0, playheadPx - pinAt);
          }
        }

        // Always draw playhead at its true audio position regardless of scroll
        updatePlayheadDOM(t, el.scrollLeft);
      }

      if (!lastUIUpdate || ts - lastUIUpdate > 100) {
        // Skip setCurrentTime while FX panel is open OR Performance Mode is on.
        // The toolbar time display isn't visible in FX mode anyway, and in
        // Performance Mode we deliberately freeze it to free CPU for the
        // audio thread (used during screen recording or on slower devices).
        // The audio engine itself uses refs (playheadAtRef, liveTimeRef)
        // and is unaffected by skipping this React state update.
        if (!fxTrackIdRef.current && !_performanceMode) {
          setCurrentTime(t);
        }
        lastUIUpdate = ts;
      }

      // Trigger loop restart EARLY so doPlay's startupLead (150-300ms)
      // completes by the time we actually hit loopOut. Without this lead
      // time, the old audio ends at loopOut and new audio doesn't start
      // until loopOut+150ms, creating an audible gap on every loop.
      // The 0.2s lead-time matches doPlay's typical setup time on iOS.
      const LOOP_RESTART_LEAD = 0.2;
      if (loopEnabled && loopOut > loopIn && t >= loopOut - LOOP_RESTART_LEAD) {
        // Prevent re-entrant loop restarts: if already looping back, skip
        if (!loopRestartingRef.current) {
          loopRestartingRef.current = true;
          // Exact audio-clock time of the musical seam (project loopOut).
          // Audio advances 1:1 with project time, so loopOut is (loopOut - t)
          // seconds away right now. We anchor the next iteration to THIS time
          // and let the current loop's audio ring right up to it, so the loop
          // is gapless AND drops on the beat.
          const seamAudio = actx.currentTime + Math.max(0, loopOut - t);
          // Reset time refs immediately so the next tick doesn't overshoot.
          playheadAtRef.current = loopIn;
          liveTimeRef.current   = loopIn;
          masterStartRef.current = seamAudio;
          // Don't update the visible currentTime/playhead yet — let the user
          // see the old loop finish on screen even though we're scheduling
          // the new one underneath.
          // NOTE: we deliberately do NOT stopAll() here. doPlay(isLoopRestart)
          // stops the outgoing sources exactly at the seam (via stopAt) so
          // their tails ring into the downbeat — no silent gap, no early cut.
          doPlay(loopIn, { isLoopRestart: true, anchorAudioTime: seamAudio }).then(function() {
            loopRestartingRef.current = false;
            setIsPlaying(true);
          });
        }
        animRef.current = requestAnimationFrame(tick);
        return;
      }
      animRef.current = requestAnimationFrame(tick);
    };
    animRef.current = requestAnimationFrame(tick);
    return function () { cancelAnimationFrame(animRef.current); };
  }, [isPlaying, effectivePPS, loopEnabled, loopIn, loopOut, seekVersion]);

  // ── Keep the playhead anchored to its true audio time across zoom changes ──
  // Without this, zooming while STOPPED leaves the playhead at its last
  // pixel position (computed for the OLD effectivePPS) — visually it
  // appears to shift to a different bar/beat until the user seeks. We
  // reposition on a rAF so the new layout has committed (scroll widths,
  // content reflow) before we measure scrollLeft and write the new left.
  React.useEffect(function(){
    const id = requestAnimationFrame(function(){
      const el = scrollRef.current;
      const sl = el ? el.scrollLeft : 0;
      // playheadAtRef holds the audio-true time; updatePlayheadDOM reads
      // effectivePPSRef which we already updated to the new zoom value.
      updatePlayheadDOM(playheadAtRef.current || 0, sl);
    });
    return function(){ cancelAnimationFrame(id); };
  }, [effectivePPS]);

  // ── Audio engine — gain nodes persist so mute/solo works in real-time ──
  // gainNodesRef, masterGainRef, fxNodesRef declared at top of component (Rules of Hooks)

  // ── Phase-vocoder pitch-shift worklet ────────────────────────────────────
  // Registered once per AudioContext via a Blob URL. Implements OLA (overlap-add)
  // pitch shifting that preserves tempo — unlike playbackRate which stretches time.
  // The worklet exposes a single AudioParam: "pitch" (ratio, 1.0 = no shift).
  // To shift by N semitones: ratio = 2^(N/12).
  // =============================================================================

  const getOrCreateMaster = function () {
    const actx = getActx();
    if (!masterGainRef.current || masterGainRef.current.context !== actx) {
      masterGainRef.current = actx.createGain();
      masterGainRef.current.gain.value = masterVolume;
      // ── Speaker mute node ──
      // Inserted between master and destination so we can silence the
      // speakers during real-time export without disconnecting any
      // graph edges (disconnect+reconnect was fragile and sometimes
      // left audio routed nowhere). Defaults to gain = 1 (passthrough).
      // The recording tap (added later in exportMix) attaches to
      // `master` directly — upstream of this node — so muting speakers
      // does NOT affect the recording level.
      const speakerMute = actx.createGain();
      speakerMute.gain.value = 1;
      masterGainRef.current.connect(speakerMute);
      speakerMute.connect(actx.destination);
      speakerMuteRef.current = speakerMute;
      // Master output VU analyser (side-branch from master, not from
      // speakerMute, so VU keeps working when speakers are silenced).
      const ma = actx.createAnalyser();
      ma.fftSize = 256;
      ma.smoothingTimeConstant = 0.5;
      masterGainRef.current.connect(ma);
      masterAnalyserRef.current = ma;
    }
    return masterGainRef.current;
  };

  // Keep masterGainRef in sync with masterVolume state at all times
  React.useEffect(function() {
    if (masterGainRef.current) {
      masterGainRef.current.gain.value = masterVolume;
    }
  }, [masterVolume]);

  // Returns the entry node of a track's FX chain — the same node the
  // timeline scheduler routes notes to. Lets the piano-roll preview
  // (single key taps + on-place feedback) flow through the track's
  // volume/pan/mute/FX so dragging the mixer volume slider actually
  // affects what the user hears when tapping keys.
  //
  // Builds the chain on-demand if it doesn't exist yet (e.g. the user
  // hasn't pressed play yet, so no chain has been allocated). Reuses
  // the existing chain if it does — cheap.
  //
  // Returns null if the master node isn't ready yet (very early lifecycle).
  // Tear down a track's FX chain completely. Hoisted to Studio scope
  // so it can be reused by getOrBuildPreviewChain (which needs to
  // dispose stale chains when FX topology changes). Was originally
  // local to doPlay.
  const tearDownChain = function(trackId) {
    // Disconnect the volume gain (terminal, connects to master) and entry
    // (upstream, where clips feed in) so the old chain is fully detached.
    const vg = gainNodesRef.current[trackId];
    if (vg) { try { vg.disconnect(); } catch(e) {} }
    const cached = chainsRef.current[trackId];
    if (cached && cached.entry) {
      try { cached.entry.disconnect(); } catch(e) {}
    }
    // Clean up R-Comp ARC interval timer if it was running. Without this,
    // each chain rebuild leaks a 30Hz polling interval.
    const fxNodes = fxNodesRef.current[trackId];
    if (fxNodes && fxNodes.rcomp && fxNodes.rcomp.arcTimer) {
      try { clearInterval(fxNodes.rcomp.arcTimer); } catch(e) {}
      fxNodes.rcomp.arcTimer = null;
    }
    // Clean up TQ per-band polling intervals (one per active band).
    if (fxNodes && fxNodes.tq && fxNodes.tq.bandNodes) {
      for (const bn of fxNodes.tq.bandNodes) {
        if (bn.intervalId) {
          try { clearInterval(bn.intervalId); } catch(e) {}
          bn.intervalId = null;
        }
      }
    }
    delete chainsRef.current[trackId];
    delete fxNodesRef.current[trackId];
    delete gainNodesRef.current[trackId];
    delete trackAnalysersRef.current[trackId];
  };

  const getOrBuildPreviewChain = function(trackId) {
    const master = masterGainRef.current;
    const actx = getActx();
    if (!master || !actx) return null;
    const cached = chainsRef.current[trackId];
    // Compute the live fxSig from current track data — if the user has
    // toggled a plugin on/off since this chain was built, the cached
    // entry is wired with the OLD FX topology and we must rebuild.
    const tList = tracksRef.current || tracks;
    const tr = tList.find(function(t){ return t.id === trackId; });
    if (!tr) return null;
    const currentSig = fxSignature(tr.effects);
    if (cached && cached.entry && cached.entry.context === actx && cached.fxSig === currentSig) {
      return cached.entry;
    }
    // Tear down stale chain if topology changed
    if (cached) {
      try { tearDownChain(trackId); } catch(_){}
    }
    const hasSolo = tList.some(function(t){ return t.isSoloed; });
    try {
      const entry = buildChain(actx, master, tr, hasSolo, { registerLive: true });
      chainsRef.current[trackId] = { entry: entry, fxSig: currentSig };
      // Rewire the per-track soundfont engine to the new entry. Same
      // reason as in doPlay's getOrBuildChain — a fresh entry node
      // means the engine's audioNode would otherwise point at the
      // orphaned old entry and audio would go silent.
      if (tr.type === "midi") {
        try {
          const eng = _bfSfEnginesByTrack.get(trackId);
          if (eng && typeof eng.reconnect === "function") {
            eng.reconnect(entry);
          }
        } catch(_){}
      }
      return entry;
    } catch (e) {
      console.warn("[preview] buildChain failed for track", trackId, e);
      return null;
    }
  };

  // Apply mute/solo/volume/pan to all live gain nodes without restarting playback
  const applyGains = function (updatedTracks) {
    const tList = updatedTracks || tracks;
    const hasSolo = tList.some(function(t){ return t.isSoloed; });
    const actx = getActx();
    tList.forEach(function(t){
      const g = gainNodesRef.current[t.id];
      if (!g) return;
      const shouldPlay = !t.isMuted && (!hasSolo || t.isSoloed);
      const vol = shouldPlay ? (t.volume ?? 1) : 0;
      g.gain.setTargetAtTime(vol, actx.currentTime, 0.01);
      // Also update pan live if a panner node exists for this track
      const live = fxNodesRef.current[t.id];
      if (live && live.panner) {
        live.panner.pan.setTargetAtTime(t.pan || 0, actx.currentTime, 0.01);
      }
    });
  };

  // ── Real-time FX update — called by upd() whenever FX params change while playing ──
  // Directly mutates live Web Audio nodes so changes are heard immediately with no restart.
  // Note: ONLY parameter changes go through here. Toggling a plugin ON/OFF requires
  //       a chain rebuild (see fxSignature + chainsRef in doPlay).
  const fxSignature = function(effects) {
    // Compact fingerprint of which plugins are ON. Used to detect when the chain
    // topology needs rebuilding vs when we can just tweak params live.
    const e = effects || {};
    return [
      (e.reverb && e.reverb.on)        ? "r"  : "-",
      (e.ocean && e.ocean.on)          ? "o"  : "-",
      (e.eq && e.eq.on)                ? "eq" : "--",
      (e.compressor && e.compressor.on)? "c"  : "-",
      (e.rcomp && e.rcomp.on)          ? "rc" : "--",
      (e.doubler && e.doubler.on)      ? "d"  : "-",
      (e.hdelay && e.hdelay.on)        ? "h"  : "-",
      (e.noiseremover && e.noiseremover.on) ? "n" : "-",
      (e.trotten && e.trotten.on)      ? "t"  : "-",
      (e.bandpass && e.bandpass.on)    ? "b"  : "-",
      (e.autotune && e.autotune.on)    ? "a"  : "-",
      (e.rcomp && e.rcomp.mode)        || "-",  // r-comp warm/electro affects pre-stage
      (e.pultec && e.pultec.on)        ? "pt" : "--",
      // TQ — signature includes plugin on/off PLUS each band's on flag PLUS
      // its mode (cut/boost). Frequency/Q/threshold/attack/release all
      // morph live without rebuild — only topology bits live here.
      (e.tq && e.tq.on) ?
        ("tq:" + (e.tq.mode || "dyneq") + ":" +
         (e.tq.bands || []).map(function(b){
           return (b.on ? "1" : "0") + b.type[0];
         }).join("")) : "----",
    ].join("|");
  };

  const applyFxLive = function (trackId, effects) {
    const actx = actxRef.current;
    if (!actx || actx.state === "closed") return;
    if (!isPlayingRef.current) return;
    const live = fxNodesRef.current[trackId];
    if (!live) return;
    const fx = effects || {};
    const T = 0.008; // 8ms ramp — smooth, inaudible transition
    const now = actx.currentTime;

    // ── EQ ──
    if (live.eq) {
      const eq = fx.eq || {};
      const on = !!(fx.eq && fx.eq.on);
      // Phase 6: Band Solo. If eq.soloBand is set, override each band's
      // type/freq/Q so only the soloed band's frequency range passes.
      const soloKey = eq.soloBand;
      const soloIdx = soloKey ? EQ_BAND_CONFIGS.findIndex(function(c){ return c.key === soloKey; }) : -1;
      const soloBand = soloIdx >= 0 ? eqResolveBand(eq, soloIdx) : null;
      for (let i = 0; i < EQ_BAND_CONFIGS.length; i++) {
        const cfg = EQ_BAND_CONFIGS[i];
        const bq  = live.eq[cfg.key];
        if (!bq) continue;
        if (on && soloBand) {
          if (cfg.key === soloKey) {
            // Soloed band → bandpass at the solo band's freq/Q
            try { if (bq.type !== "bandpass") bq.type = "bandpass"; } catch(_){}
            bq.frequency.setTargetAtTime(soloBand.freq, now, T);
            bq.Q.setTargetAtTime(soloBand.q, now, T);
            try { bq.gain.setTargetAtTime(0, now, T); } catch(_){}
          } else {
            // Other bands → allpass (transparent, just phase shift)
            try { if (bq.type !== "allpass") bq.type = "allpass"; } catch(_){}
            bq.frequency.setTargetAtTime(1000, now, T);
            bq.Q.setTargetAtTime(0.7, now, T);
            try { bq.gain.setTargetAtTime(0, now, T); } catch(_){}
          }
          continue;
        }
        // Normal mode — each band as configured.
        const r = eqResolveBand(eq, i);
        try {
          if (bq.type !== r.type) bq.type = r.type;
        } catch(_){}
        bq.frequency.setTargetAtTime(r.freq, now, T);
        bq.Q.setTargetAtTime(on && r.enabled ? r.q : 0.001, now, T);
        try { bq.gain.setTargetAtTime(on && r.enabled ? r.gain : 0, now, T); } catch(_){}
      }
      // Output gain (post-EQ trim, Phase 4). Smoothly ramped to avoid
      // zipper noise when the user drags the knob. When EQ is off,
      // forced to unity so the trim doesn't affect bypassed audio.
      if (live.eqOutputGain) {
        const outputGainDb = (typeof eq.outputGain === "number") ? eq.outputGain : 0;
        const linearGain = on ? Math.pow(10, outputGainDb / 20) : 1;
        live.eqOutputGain.gain.setTargetAtTime(linearGain, now, T);
      }
    }

    // ── Compressor ──
    if (live.comp) {
      const compOn = !!(fx.compressor && fx.compressor.on);
      live.comp.threshold.setTargetAtTime(compOn ? (fx.compressor.threshold ?? -24) : 0,     now, T);
      live.comp.ratio.setTargetAtTime(    compOn ? (fx.compressor.ratio ?? 4)       : 1,     now, T);
      live.comp.attack.setTargetAtTime(   compOn ? (fx.compressor.attack ?? 0.003)  : 0.003, now, T);
      live.comp.release.setTargetAtTime(  compOn ? (fx.compressor.release ?? 0.25)  : 0.25,  now, T);
    }

    // ── Makeup gain ──
    if (live.makeupGain) {
      const mgOn = !!(fx.compressor && fx.compressor.on && fx.compressor.makeupGain);
      const mgVal = mgOn ? Math.pow(10, (fx.compressor.makeupGain || 0) / 20) : 1;
      live.makeupGain.gain.setTargetAtTime(mgVal, now, T);
    }

    // ── Reverb wet/dry ──
    if (live.reverb) {
      const revOn = !!(fx.reverb && fx.reverb.on);
      const wet = revOn ? (fx.reverb.wet || 0.25) : 0;
      live.reverb.wetG.gain.setTargetAtTime(wet,     now, T);
      live.reverb.dryG.gain.setTargetAtTime(1 - wet, now, T);
      if (revOn && live.reverb.preDelay && fx.reverb.preDelay !== undefined) {
        live.reverb.preDelay.delayTime.setTargetAtTime(fx.reverb.preDelay / 1000, now, T);
      }
    }

    // ── Ocean Reverb — live wet/dry and damping ──
    if (live.ocean) {
      const oceanOn = !!(fx.ocean && fx.ocean.on);
      const wet = oceanOn ? (fx.ocean.wet || 0.35) : 0;
      live.ocean.wetG.gain.setTargetAtTime(wet,     now, T);
      live.ocean.dryG.gain.setTargetAtTime(1 - wet, now, T);
      if (oceanOn && live.ocean.preDelay && fx.ocean.preDelay !== undefined) {
        live.ocean.preDelay.delayTime.setTargetAtTime(fx.ocean.preDelay / 1000, now, T);
      }
      if (oceanOn && live.ocean.lpf && fx.ocean.damp !== undefined) {
        const freq = 800 + (1 - fx.ocean.damp) * 7200;
        live.ocean.lpf.frequency.setTargetAtTime(freq, now, T);
      }
    }
    // ── H-Delay — live param morphing ──
    if (live.hdelay && fx.hdelay !== undefined) {
      const hd      = fx.hdelay || {};
      const hdOn    = !!(fx.hdelay && fx.hdelay.on);
      const mode    = hd.mode ?? "digital";

      function hdSubdivMsLive(sub, bpmVal) {
        const beat = 60000 / (bpmVal || 120);
        const map = {
          "1/1":beat*4,"1/2":beat*2,"1/2T":beat*(4/3),
          "1/4":beat,"1/4T":beat*(2/3),
          "1/8":beat/2,"1/8T":beat/3,
          "1/16":beat/4,"1/16T":beat/6,"1/32":beat/8,
        };
        return Math.min(2000, (map[sub] || beat)) / 1000;
      }
      const delayTimeSec = hdOn
        ? (hd.sync ? hdSubdivMsLive(hd.subdivision ?? "1/4", hd.bpm ?? 120) : (hd.delayMs ?? 375) / 1000)
        : 0.375;

      const feedback = hdOn ? Math.min(0.95, hd.feedback ?? 0.35) : 0;
      const wetMixV  = hdOn ? (hd.wet ?? 0.40) : 0;
      const hiCut    = hd.hiCut ?? 8000;
      const loCut    = hd.loCut ?? 80;
      const modDepth = hdOn ? (hd.modDepth ?? 0.15) : 0;
      const modRate  = hd.modRate ?? 0.5;

      live.hdelay.hdDelay.delayTime.setTargetAtTime(delayTimeSec, now, T);
      if (live.hdelay.hdDelayFlip) {
        live.hdelay.hdDelayFlip.delayTime.setTargetAtTime(delayTimeSec, now, T);
      }
      live.hdelay.hdFb.gain.setTargetAtTime(feedback, now, T);
      if (live.hdelay.hdFbFlip) {
        live.hdelay.hdFbFlip.gain.setTargetAtTime(mode === "ping" ? feedback : 0, now, T);
      }
      live.hdelay.hdDry.gain.setTargetAtTime(1 - wetMixV, now, T);
      live.hdelay.hdWet.gain.setTargetAtTime(wetMixV,     now, T);
      live.hdelay.hdHiCut.frequency.setTargetAtTime(hiCut, now, T);
      live.hdelay.hdLoCut.frequency.setTargetAtTime(loCut, now, T);
      live.hdelay.hdPan.pan.setTargetAtTime(mode === "ping" ? -1 : 0, now, T);
      if (live.hdelay.hdPanFlip) {
        live.hdelay.hdPanFlip.pan.setTargetAtTime(mode === "ping" ? 1 : 0, now, T);
      }
      // LFO for tape mode
      live.hdelay.hdLfo.frequency.setTargetAtTime(mode === "tape" ? modRate : 0.001, now, T);
      live.hdelay.hdLfoGain.gain.setTargetAtTime(mode === "tape" ? modDepth * 0.015 : 0, now, T);
    }

    // ── Vocal Doubler — live param morphing ──
    if (live.doubler && fx.doubler !== undefined) {
      const db      = fx.doubler || {};
      const doubOn  = !!(fx.doubler && fx.doubler.on);
      const delayMs = doubOn ? (db.delay  ?? 20)  : 20;
      const detune  = doubOn ? (db.detune ?? 8)   : 8;
      const width   = doubOn ? (db.width  ?? 0.7) : 0;
      const mixWet  = doubOn ? (db.mix    ?? 0.5) : 0;

      live.doubler.haasDelay.delayTime.setTargetAtTime(delayMs / 1000, now, T);
      live.doubler.chorusDelay.delayTime.setTargetAtTime((detune / 1000) * 0.012 + 0.001, now, T);
      live.doubler.dryGain.gain.setTargetAtTime(1 - mixWet, now, T);
      live.doubler.wetGain.gain.setTargetAtTime(mixWet,     now, T);
      live.doubler.lWidthGain.gain.setTargetAtTime(0.5 + width * 0.5, now, T);
      live.doubler.rWidthGain.gain.setTargetAtTime(0.5 + width * 0.5, now, T);
    }

    // ── Noise Gate — live parameter updates ──────────────────────────────────
    if (live.rnnoiseNode || live.rnnoiseGate) {
      const nr       = fx.noiseremover || {};
      const nrOn     = !!(fx.noiseremover && fx.noiseremover.on);
      const threshDb = nrOn ? (nr.threshold ?? -40) : -200;
      const relSec   = nrOn ? (nr.release   ?? 0.15) : 0.15;
      const holdSec  = nrOn ? (nr.hold      ?? 0.08) : 0.08;
      const lookahead = !!(nr.lookahead ?? true);
      const attackSec = lookahead ? 0.001 : 0.003;

      if (live.rnnoiseNode) {
        try {
          live.rnnoiseNode.parameters.get("bypass").setTargetAtTime(nrOn ? 0 : 1, now, T);
          live.rnnoiseNode.parameters.get("threshold").setTargetAtTime(threshDb, now, T);
          live.rnnoiseNode.parameters.get("attack").setTargetAtTime(attackSec, now, T);
          live.rnnoiseNode.parameters.get("hold").setTargetAtTime(Math.min(1, holdSec), now, T);
          live.rnnoiseNode.parameters.get("release").setTargetAtTime(relSec, now, T);
        } catch(e) {}
      }
      if (live.rnnoiseGate) {
        live.rnnoiseGate.threshold.setTargetAtTime(nrOn ? threshDb : 0, now, T);
        live.rnnoiseGate.ratio.setTargetAtTime(nrOn ? 20 : 1, now, T);
        live.rnnoiseGate.attack.setTargetAtTime(attackSec, now, T);
        live.rnnoiseGate.release.setTargetAtTime(relSec, now, T);
      }
      // Hiss filter live update
      if (live.noiseremover && live.noiseremover.hissFilter) {
        const hissAmt = nrOn ? (nr.hiss ?? 0) : 0;
        live.noiseremover.hissFilter.gain.setTargetAtTime(hissAmt * -18, now, T);
      }
    }

    // ── T-Rotten Master — live param morphing ──────────────────────────────
    if (live.trotten && fx.trotten !== undefined) {
      const tr   = fx.trotten || {};
      const trOn = !!(fx.trotten && fx.trotten.on);

      // Input / output gain
      live.trotten.trInGain.gain.setTargetAtTime(
        trOn ? Math.pow(10, (tr.inputGain ?? 0) / 20) : 1, now, T
      );
      live.trotten.trOutGain.gain.setTargetAtTime(
        trOn ? Math.pow(10, (tr.outputGain ?? 0) / 20) : 1, now, T
      );

      // EQ bands
      live.trotten.trEqLow.type       = trOn && tr.eqLowT  === "bell"  ? "peaking"  : "lowshelf";
      live.trotten.trEqMid.type       = trOn && tr.eqMidT  === "shelf" ? "lowshelf" : "peaking";
      live.trotten.trEqHigh.type      = trOn && tr.eqHighT === "bell"  ? "peaking"  : "highshelf";
      live.trotten.trEqLow.gain.setTargetAtTime(  trOn ? (tr.eqLow  ?? 0) : 0, now, T);
      live.trotten.trEqMid.gain.setTargetAtTime(  trOn ? (tr.eqMid  ?? 0) : 0, now, T);
      live.trotten.trEqHigh.gain.setTargetAtTime( trOn ? (tr.eqHigh ?? 0) : 0, now, T);

      // Compressor
      const compRatio = trOn ? Math.max(1, 1 + (tr.compAmt ?? 50) / 100 * 19) : 1;
      live.trotten.trComp.threshold.setTargetAtTime(trOn ? (tr.compThr ?? -15) : 0, now, T);
      live.trotten.trComp.ratio.setTargetAtTime(compRatio, now, T);
      live.trotten.trComp.attack.setTargetAtTime(
        trOn && tr.compMode === "fast" ? 0.001 : 0.005, now, T
      );
      live.trotten.trComp.release.setTargetAtTime(
        trOn && tr.compMode === "slow" ? 0.5 : 0.1, now, T
      );

      // Tape saturation — rebuild curve when drive/sat/mode changes
      live.trotten.trTape.curve = live.trotten.buildTapeCurve(
        trOn ? (tr.tapeDrv ?? 5) : 0,
        trOn ? (tr.tapeSat ?? 5) : 0,
        trOn ? (tr.tapeMode ?? "modern") : "modern"
      );
      live.trotten.trTapeShelf.gain.setTargetAtTime(
        trOn && tr.tapeMode === "vintage" ? 1.5 : 0, now, T
      );

      // Limiter
      live.trotten.trLim.threshold.setTargetAtTime(trOn ? (tr.limCeil ?? -0.5) : 0, now, T);
      live.trotten.trLim.release.setTargetAtTime(
        trOn ? Math.max(0.05, tr.limRel ?? 0.5) : 0.25, now, T
      );
    }

    // ── GRM Bandpass — live param morphing ────────────────────────────────
    if (live.bandpass && fx.bandpass !== undefined) {
      const bpFx  = fx.bandpass || {};
      const bpOn  = !!(fx.bandpass && fx.bandpass.on);
      const center = bpOn ? Math.min(8000, bpFx.center ?? 1000) : 1000;
      const octW   = bpOn ? (bpFx.width  ?? 1.0)  : 1.0;
      const resDb  = bpOn ? (bpFx.res    ?? 0)    : 0;
      const mixWet = bpOn ? (bpFx.mix    ?? 1)    : 0;
      const Q  = Math.sqrt(Math.pow(2, octW)) / (Math.pow(2, octW) - 1);
      const Qr = Math.max(0.1, Q * (1 + resDb / 6));
      live.bandpass.bp1.frequency.setTargetAtTime(center, now, T);
      live.bandpass.bp1.Q.setTargetAtTime(Qr, now, T);
      live.bandpass.bp2.frequency.setTargetAtTime(center, now, T);
      live.bandpass.bp2.Q.setTargetAtTime(Qr, now, T);
      live.bandpass.bpDry.gain.setTargetAtTime(1 - mixWet, now, T);
      live.bandpass.bpWet.gain.setTargetAtTime(mixWet,     now, T);
    }

    // ── Autotune — live param updates during playback ──
    if (live.autotune) {
      const at   = fx.autotune || {};
      const atOn = !!(at.on);
      const KEY_OFFSETS = {C:0,"C#":1,Db:1,D:2,"D#":3,Eb:3,E:4,F:5,"F#":6,Gb:6,G:7,"G#":8,Ab:8,A:9,"A#":10,Bb:10,B:11};
      try {
        live.autotune.parameters.get("bypass").value   = atOn ? 0 : 1;
        live.autotune.parameters.get("key").value      = KEY_OFFSETS[at.key || "C"] || 0;
        live.autotune.parameters.get("speed").value    = at.speed ?? 50;
        live.autotune.parameters.get("humanize").value = at.humanize ?? 10;
        live.autotune.parameters.get("wet").value      = at.wet ?? 1;
        live.autotune.parameters.get("vibrato").value  = at.vibrato ?? 0;
        live.autotune.port.postMessage({ scale: at.scale || "major" });
      } catch(e) {}
    }

    // ── R-Comp live param updates ──
    // Mode (warm/electro) and on/off toggles require chain rebuild (handled
    // upstream via fxSignature). Knob tweaks are live here.
    if (live.rcomp && fx.rcomp !== undefined) {
      const rc    = fx.rcomp || {};
      const rcOn  = !!rc.on;
      try {
        live.rcomp.rcComp.threshold.setTargetAtTime(rcOn ? (rc.threshold ?? -18) : 0,    now, T);
        live.rcomp.rcComp.ratio.setTargetAtTime(    rcOn ? (rc.ratio ?? 3)        : 1,    now, T);
        live.rcomp.rcComp.attack.setTargetAtTime(   rcOn ? (rc.attack ?? 0.012)   : 0.003,now, T);
        // Release: only set directly when ARC is OFF. When ARC is ON, the
        // arcTimer is in charge of release modulation.
        if (rc.arc === false) {
          live.rcomp.rcComp.release.setTargetAtTime(rcOn ? (rc.release ?? 0.18)   : 0.25, now, T);
        }
        // Makeup gain
        const mgVal = rcOn ? Math.pow(10, ((rc.makeupGain ?? 0)) / 20) : 1;
        live.rcomp.rcMakeup.gain.setTargetAtTime(mgVal, now, T);
        // Pre-stage shelf gain (mode-dependent)
        if (live.rcomp.mode === "warm") {
          live.rcomp.rcShelf.gain.setTargetAtTime(rcOn ? 1.2 : 0, now, T);
        }
      } catch(e) {}
      // ARC enable/disable at runtime: if ARC was OFF and is now ON, start
      // the interval. If it was ON and is now OFF, kill it.
      const arcShouldRun = rcOn && (rc.arc !== false);
      const arcRunning   = !!live.rcomp.arcTimer;
      if (arcShouldRun && !arcRunning) {
        const rcComp = live.rcomp.rcComp;
        const MIN_REL = 0.05, MAX_REL = 1.5;
        let smoothed = rcComp.release.value;
        live.rcomp.arcTimer = setInterval(function(){
          try {
            const red = Math.abs(rcComp.reduction || 0);
            const target = MIN_REL + (Math.min(8, red) / 8) * (MAX_REL - MIN_REL);
            smoothed = smoothed * 0.85 + target * 0.15;
            rcComp.release.setTargetAtTime(smoothed, actx.currentTime, 0.05);
          } catch(e) {}
        }, 33);
      } else if (!arcShouldRun && arcRunning) {
        try { clearInterval(live.rcomp.arcTimer); } catch(e) {}
        live.rcomp.arcTimer = null;
      }
    }

    // ── Pultec EQP-1A live param updates ──
    // Frequency selects (lowFreq, hiFreq, attenFreq) are topology-stable —
    // the same biquad nodes just retune. So all knob and freq-select changes
    // can morph live without a chain rebuild. Only on/off toggles trigger
    // a rebuild via fxSignature.
    if (live.pultec && fx.pultec !== undefined) {
      const pt   = fx.pultec || {};
      const ptOn = !!pt.on;
      try {
        // Stage 1: Low Boost
        live.pultec.ptLowBoost.frequency.setTargetAtTime(pt.lowFreq ?? 60,                  now, T);
        live.pultec.ptLowBoost.gain.setTargetAtTime(     ptOn ? (pt.lowBoost ?? 0) : 0,     now, T);
        // Stage 2: Low Atten (negative gain on a peaking filter at same freq)
        live.pultec.ptLowAtten.frequency.setTargetAtTime(pt.lowFreq ?? 60,                  now, T);
        live.pultec.ptLowAtten.gain.setTargetAtTime(     ptOn ? -(pt.lowAtten ?? 0) : 0,    now, T);
        // Stage 3: High Boost (with SHARP-controlled Q)
        const sharpVal = pt.sharp ?? 5;
        const hiBoostQ = 2.0 - ((sharpVal - 1) / 9) * 1.7;
        live.pultec.ptHiBoost.frequency.setTargetAtTime( pt.hiFreq ?? 5000,                 now, T);
        live.pultec.ptHiBoost.Q.setTargetAtTime(         hiBoostQ,                          now, T);
        live.pultec.ptHiBoost.gain.setTargetAtTime(      ptOn ? (pt.hiBoost ?? 0) : 0,      now, T);
        // Stage 4: High Atten
        live.pultec.ptHiAtten.frequency.setTargetAtTime( pt.attenFreq ?? 10000,             now, T);
        live.pultec.ptHiAtten.gain.setTargetAtTime(      ptOn ? -(pt.hiAtten ?? 0) : 0,     now, T);
      } catch(e) {}
    }

    // ── TQ — Vocal Dynamic EQ live updates ──
    // Frequency, Q, threshold, attack, release morph live via AudioParam.
    // Target gain and band type morph live via mutating bn.bandRef so the
    // polling loop picks up new values on its next tick.
    // Plugin on/off and per-band on/off live in fxSignature and trigger a
    // chain rebuild (correct — we want to fully tear down the sidechain
    // detector for off bands to save CPU).
    if (live.tq && live.tq.bandNodes && fx.tq) {
      const bands = fx.tq.bands || [];
      for (const bn of live.tq.bandNodes) {
        const cfg = bands.find(function(b){ return b.id === bn.id; });
        if (!cfg) continue;
        try {
          // Audio path: peaking/shelf biquad
          bn.biquad.frequency.setTargetAtTime(cfg.freq, now, T);
          bn.biquad.Q.setTargetAtTime(cfg.q, now, T);
          // (biquad.gain is driven by the polling loop — don't touch directly)
          // Sidechain detector path
          bn.bp.frequency.setTargetAtTime(cfg.freq, now, T);
          bn.bp.Q.setTargetAtTime(Math.max(0.6, cfg.q * 0.7), now, T);
          bn.det.threshold.setTargetAtTime(cfg.threshold, now, T);
          bn.det.attack.setTargetAtTime(cfg.attack, now, T);
          bn.det.release.setTargetAtTime(cfg.release, now, T);
          // Live target-gain + band-type for the polling loop. Mutating
          // bandRef here means the very next 33ms poll tick picks up the
          // new values without any chain rebuild.
          bn.bandRef.gain = cfg.gain;
          bn.bandRef.type = cfg.type;
          bn.cfg = cfg;
        } catch(e) {}
      }
      // ── Listen filter live update ──
      // When listenBandId is set, configure as a bandpass at that band's freq
      // (so user hears only that frequency range). When null, configure as a
      // very-wide lowpass at 22kHz = transparent. Filter TYPE switches are
      // free in Web Audio (no rebuild), just set the .type property.
      if (live.tq.listenFilter) {
        try {
          const lid = fx.tq.listenBandId || null;
          if (lid) {
            const lb = bands.find(function(b){ return b.id === lid; });
            if (lb) {
              live.tq.listenFilter.type = "bandpass";
              live.tq.listenFilter.frequency.setTargetAtTime(lb.freq, now, T);
              live.tq.listenFilter.Q.setTargetAtTime(2.0, now, T);
            } else {
              // Listening to a band that doesn't exist — fall back to transparent
              live.tq.listenFilter.type = "lowpass";
              live.tq.listenFilter.frequency.setTargetAtTime(22000, now, T);
              live.tq.listenFilter.Q.setTargetAtTime(0.7, now, T);
            }
          } else {
            // No listen — transparent
            live.tq.listenFilter.type = "lowpass";
            live.tq.listenFilter.frequency.setTargetAtTime(22000, now, T);
            live.tq.listenFilter.Q.setTargetAtTime(0.7, now, T);
          }
        } catch(e) {}
      }
    }
  };
  const toggleMute = function (id) {
    setTracks(function(prev){
      const next = prev.map(function(t){ return t.id===id?{...t,isMuted:!t.isMuted}:t; });
      applyGains(next);
      return next;
    });
    setIsSaved(false);
  };

  const toggleSolo = function (id) {
    setTracks(function(prev){
      const next = prev.map(function(t){ return t.id===id?{...t,isSoloed:!t.isSoloed}:t; });
      applyGains(next);
      return next;
    });
    setIsSaved(false);
  };

  // ── Stop all audio nodes — hard stop, no glitch ──────────────
  const stopAll = function (opts) {
    opts = opts || {};
    const keepGain = !!opts.keepGain;
    // stopAt: optional AudioContext time at which outgoing sources/voices
    // should stop. Used by the loop restart so the previous iteration's
    // audio rings right up to the seam (gapless) instead of being cut the
    // instant we begin scheduling the next iteration. When null, everything
    // stops immediately (the normal transport-stop behavior).
    const stopAt = (opts.stopAt != null && opts.stopAt > 0) ? opts.stopAt : null;
    cancelAnimationFrame(animRef.current);
    if (scrollSeekTimer.current) { clearTimeout(scrollSeekTimer.current); scrollSeekTimer.current = null; }
    userScrolledAt.current = null;
    // Stop the drum sequencer's look-ahead scheduler. Any already-scheduled
    // drum hits live in scheduledRef and get stopped in the loop below.
    if (drumSchedulerRef.current) {
      clearInterval(drumSchedulerRef.current);
      drumSchedulerRef.current = null;
    }
    drumCursorsRef.current = {};
    drumClipStepsRef.current = {};
    // Drop choke state too — its source/gain references point to now-stopped
    // BufferSourceNodes from this playback session.
    drumChokeContextsRef.current = {};
    // ── MIDI cleanup ──
    // Stop the MIDI lookahead scheduler and release every active voice.
    // Each voice has its own ADSR + osc graph that auto-stops at endsAt;
    // calling .stop(0) here cuts any voice that's still mid-release so we
    // get instant silence on transport stop (no ghost tails).
    if (midiSchedulerRef.current) {
      clearInterval(midiSchedulerRef.current);
      midiSchedulerRef.current = null;
    }
    Object.keys(midiVoicesRef.current).forEach(function(tid){
      const voices = midiVoicesRef.current[tid] || [];
      voices.forEach(function(v){
        if (v && v.stop) { try { v.stop(stopAt != null ? stopAt : 0); } catch(_) {} }
      });
    });
    midiVoicesRef.current = {};
    midiCursorsRef.current = {};
    // Stop and disconnect every scheduled source node immediately.
    // BufferSources are single-use — they're created fresh on each doPlay.
    scheduledRef.current.forEach(function (s) {
      if (stopAt != null) {
        // Ring out to the seam, then disconnect once the node ends. Sources
        // scheduled to START after the seam get stop(seamTime) <= their start,
        // so Web Audio simply never plays them (no wrong content past the loop
        // point). Sources mid-flight are cut cleanly at the seam.
        try { s.stop(stopAt); } catch (e) { try { s.stop(0); } catch(_) {} }
        try { s.onended = function(){ try { s.disconnect(); } catch(_) {} }; } catch(_) {}
      } else {
        try { s.stop(0); } catch (e) {} // stop(0) = immediate
        try { s.disconnect(); } catch (e) {}
      }
    });
    scheduledRef.current = [];
    // PERF: do NOT tear down FX chains, gain nodes, or the master gain.
    // Rebuilding them every Play press costs 50-200ms per track and is the
    // root cause of the "press play, audio starts a second later" lag with
    // multiple FX loaded. The cached chains in chainsRef survive across
    // play/stop cycles; only BufferSources are disposable.
    // Ramp master gain to 0 momentarily so any reverb/delay tails are cut.
    // SKIP this on loop restart so we don't hear a click/gap as gain rams
    // to 0 and back up — the new clips will overwrite the old audio
    // seamlessly once doPlay schedules them.
    if (!keepGain && masterGainRef.current) {
      try {
        masterGainRef.current.gain.cancelScheduledValues(0);
        masterGainRef.current.gain.value = 0;
      } catch (e) {}
    }
  };

  // ── Play from a given time — all tracks share master destination ──
  // Build Web Audio effects chain for a track, return the gain node for mute/solo control
  // Accepts ctx as parameter so live (actx) and offline (OfflineAudioContext)
  // can both call into the same DSP code. opts.registerLive=true causes
  // React-ref side effects to fire (live mute/solo control, VU analysers,
  // FX node tracking for live param updates); offline export passes false.
  const buildChain = function (ctx, master, track, hasSolo, opts) {
    opts = opts || { registerLive: true };
    const registerLive = opts.registerLive !== false;
    let node = master;
    const fx = track.effects || {};
    const liveNodes = {}; // collect refs for real-time updates

    // Pan
    if (ctx.createStereoPanner) {
      const panner = ctx.createStereoPanner();
      panner.pan.value = track.pan || 0;
      panner.connect(node); node = panner;
      liveNodes.panner = panner;
    }

    // Makeup gain (post-compressor) — always create so we can update it live
    const mg = ctx.createGain();
    mg.gain.value = (fx.compressor && fx.compressor.on && fx.compressor.makeupGain)
      ? Math.pow(10, (fx.compressor.makeupGain || 0) / 20) : 1;
    mg.connect(node); node = mg;
    liveNodes.makeupGain = mg;

    // Track volume
    const volGain = ctx.createGain();
    const shouldPlay = !track.isMuted && (!hasSolo || track.isSoloed);
    volGain.gain.value = (track.volume ?? 1) * (shouldPlay ? 1 : 0);
    volGain.connect(node); node = volGain;
    if (registerLive) gainNodesRef.current[track.id] = volGain;

    // ── Per-track VU analyser (side-branch, read-only, doesn't affect sound) ──
    // Offline export skips this — VU analyser does no useful work without
    // a UI to read it, and saves a node per track.
    if (registerLive) {
      const trackAnalyser = ctx.createAnalyser();
      trackAnalyser.fftSize = 256;
      trackAnalyser.smoothingTimeConstant = 0.5;
      volGain.connect(trackAnalyser); // taps off AFTER volume/mute
      trackAnalysersRef.current[track.id] = trackAnalyser;
    }

    // ── Reverb — only allocate ConvolverNode when plugin is ON ──
    // CRASH FIX: allocating IR buffers for every track even when off was
    // the main cause of iOS memory crashes with 4+ tracks.
    {
      const reverbOn = !!(fx.reverb && fx.reverb.on);
      const wetVal   = reverbOn ? (fx.reverb.wet || 0.25) : 0;
      const dryG = ctx.createGain(); dryG.gain.value = 1 - wetVal;
      const wetG = ctx.createGain(); wetG.gain.value = wetVal;
      const mix  = ctx.createGain();
      dryG.connect(mix); wetG.connect(mix); mix.connect(node);

      if (reverbOn) {
        const sr = ctx.sampleRate;
        const preDelaySec = (fx.reverb.preDelay || 0) / 1000;
        const preDelay = ctx.createDelay(0.2);
        preDelay.delayTime.value = preDelaySec;
        const roomSize = fx.reverb.roomSize || 0.8;
        // Shared IR buffer per roomSize to avoid re-allocating per track
        const len = Math.round(sr * Math.min(roomSize, 1.5) * 2); // capped at 2s IR
        const ir = ctx.createBuffer(2, len, sr);
        for (let ch=0;ch<2;ch++){const d=ir.getChannelData(ch);for(let i=0;i<len;i++)d[i]=(Math.random()*2-1)*Math.pow(1-i/len,2.5);}
        const conv = ctx.createConvolver(); conv.buffer = ir;
        const split = ctx.createGain(); split.gain.value = 1;
        split.connect(dryG); split.connect(preDelay);
        preDelay.connect(conv); conv.connect(wetG);
        node = split;
        liveNodes.reverb = { dryG, wetG, preDelay, conv };
      } else {
        // Bypass: dry path only, no convolver allocated
        const split = ctx.createGain(); split.gain.value = 1;
        split.connect(dryG);
        node = split;
        liveNodes.reverb = { dryG, wetG };
      }
    }

    // ── Ocean Reverb — only allocate when ON ──
    {
      const oceanOn = !!(fx.ocean && fx.ocean.on);
      const wetVal  = oceanOn ? (fx.ocean.wet || 0.35) : 0;
      const dryG = ctx.createGain(); dryG.gain.value = 1 - wetVal;
      const wetG = ctx.createGain(); wetG.gain.value = wetVal;
      const mix  = ctx.createGain();
      dryG.connect(mix); wetG.connect(mix); mix.connect(node);

      if (oceanOn) {
        const sr = ctx.sampleRate;
        const preDelaySec = (fx.ocean.preDelay || 20) / 1000;
        const roomSize = Math.min(1.5, fx.ocean.roomSize || 1.0); // hard cap at 1.5s IR
        const len = Math.round(sr * roomSize * 2.5);
        const ir = ctx.createBuffer(2, len, sr);
        for (let ch=0;ch<2;ch++){
          const d = ir.getChannelData(ch);
          for (let i=0;i<len;i++) d[i] = (Math.random()*2-1)*Math.pow(1-i/len, 1.8);
        }
        const conv = ctx.createConvolver(); conv.buffer = ir;
        const damp = fx.ocean.damp || 0.6;
        const lpf = ctx.createBiquadFilter();
        lpf.type = "lowpass";
        lpf.frequency.value = 800 + (1 - damp) * 7200;
        lpf.Q.value = 0.5;
        const preDelay = ctx.createDelay(0.3); preDelay.delayTime.value = preDelaySec;
        const split = ctx.createGain(); split.gain.value = 1;
        split.connect(dryG); split.connect(preDelay);
        preDelay.connect(conv); conv.connect(lpf); lpf.connect(wetG);
        node = split;
        liveNodes.ocean = { dryG, wetG, preDelay, lpf, conv };
      } else {
        // Bypass: dry path only, no convolver/lpf allocated
        const split = ctx.createGain(); split.gain.value = 1;
        split.connect(dryG);
        node = split;
        liveNodes.ocean = { dryG, wetG };
      }
    }

    // ── 5-band parametric EQ — only built when on ──
    // PERF: 5 biquad filters in series consume real audio-thread CPU
    // per track. When EQ is OFF we skip them entirely; turning EQ on
    // bumps fxSignature so the chain rebuilds with the filters
    // inserted. Audible result is identical (the previous "bypass"
    // mode set frequencies/gains to neutral but the biquads still
    // processed audio).
    {
      const eq = fx.eq || {};
      const T  = 0.005;
      const eqOn = !!(fx.eq && fx.eq.on);

      if (eqOn) {
        // Build the 5-band chain. Each band's type/freq/gain/Q is
        // resolved from eq state via the shared eqResolveBand helper.
        // Phase 6: If solo is active, configure bands accordingly.
        const soloKey = eq.soloBand;
        const soloIdx = soloKey ? EQ_BAND_CONFIGS.findIndex(function(c){ return c.key === soloKey; }) : -1;
        const soloBand = soloIdx >= 0 ? eqResolveBand(eq, soloIdx) : null;
        const eqNodes = {};
        const eqOrdered = [];
        for (let i = 0; i < EQ_BAND_CONFIGS.length; i++) {
          const cfg = EQ_BAND_CONFIGS[i];
          const bq = ctx.createBiquadFilter();
          if (soloBand) {
            if (cfg.key === soloKey) {
              bq.type = "bandpass";
              bq.frequency.setTargetAtTime(soloBand.freq, ctx.currentTime, T);
              bq.Q.setTargetAtTime(soloBand.q, ctx.currentTime, T);
            } else {
              bq.type = "allpass";
              bq.frequency.setTargetAtTime(1000, ctx.currentTime, T);
              bq.Q.setTargetAtTime(0.7, ctx.currentTime, T);
            }
          } else {
            const r = eqResolveBand(eq, i);
            bq.type = r.type;
            bq.frequency.setTargetAtTime(r.freq, ctx.currentTime, T);
            bq.Q.setTargetAtTime(r.q, ctx.currentTime, T);
            try { bq.gain.setTargetAtTime(r.gain, ctx.currentTime, T); } catch(_){}
          }
          eqNodes[cfg.key] = bq;
          eqOrdered.push(bq);
        }
        // Phase 4: Output gain — post-EQ trim. Lets the user compensate
        // for overall loudness shifts from boost/cut combinations.
        // Essential for level-matched A/B comparison. dB → linear via
        // standard 10^(dB/20) conversion. Default 0dB (unity).
        const outputGainDb = (typeof eq.outputGain === "number") ? eq.outputGain : 0;
        const outputGainNode = ctx.createGain();
        outputGainNode.gain.setTargetAtTime(Math.pow(10, outputGainDb / 20), ctx.currentTime, T);
        // Chain serially: hpf → low → mid → high → lpf → outputGain → [rest]
        for (let i = eqOrdered.length - 1; i > 0; i--) {
          eqOrdered[i-1].connect(eqOrdered[i]);
        }
        eqOrdered[eqOrdered.length - 1].connect(outputGainNode);
        outputGainNode.connect(node);
        node = eqOrdered[0];
        liveNodes.eq = eqNodes;
        liveNodes.eqOutputGain = outputGainNode;
      }
      // else: EQ off — node passes through unchanged. liveNodes.eq
      // is omitted; live-update code paths must null-check it.
    }

    // ── TQ — Vocal Dynamic EQ — 7-band sidechain-driven dynamic processor ──
    // Each band lives as a peaking/shelf biquad IN SERIES on the audio path.
    // A parallel SIDECHAIN path measures level at the band's frequency
    // via bandpass + DynamicsCompressor; the compressor's `reduction`
    // property is polled at ~30Hz and used to modulate the audio band's
    // gain toward the user's target.
    //
    // Cut bands: when sidechain detects loud signal at the band's freq,
    //            band gain dips toward `gain` (which is negative).
    // Boost bands: "upward expansion" — when sidechain is QUIET, band gain
    //              rises toward `gain` (which is positive). Implemented by
    //              inverting the reduction-to-gain mapping.
    //
    // The polling interval is the same pattern used for R-Comp's ARC.
    // Each band's polling is independent so bands respond at their own
    // attack/release without cross-talk.
    //
    // Build cost: ~3 nodes per active band + 1 interval. For all 7 bands
    // active that's ~21 nodes per track. Inactive bands skip allocation
    // entirely — when TQ is OFF, no nodes are built and the audio
    // path passes through unchanged.
    if (fx.tq && fx.tq.on) {
      const tq = fx.tq;
      const bands = tq.bands || [];
      const tqBandNodes = [];  // per-band {biquad, bp, det, intervalId, cfg}

      for (let i = 0; i < bands.length; i++) {
        const b = bands[i];
        if (!b || !b.on) continue;

        // ── Audio path: the actual EQ biquad ──
        // Peaking filter at the band's frequency. Initial gain is 0 (transparent);
        // the polling loop modulates it toward the target when sidechain triggers.
        const biquad = ctx.createBiquadFilter();
        // Air band uses high-shelf so it lifts everything above 12kHz, not just a bell
        if (b.id === "air") {
          biquad.type = "highshelf";
        } else {
          biquad.type = "peaking";
        }
        biquad.frequency.value = b.freq;
        biquad.Q.value = b.q;
        biquad.gain.value = 0;  // start transparent; sidechain drives it

        // ── Sidechain detector path ──
        // Tap the signal BEFORE the biquad processes it, bandpass it at the
        // band's center frequency to measure ONLY that range's level, then
        // feed into a DynamicsCompressor whose `reduction` we'll poll.
        const bp = ctx.createBiquadFilter();
        bp.type = "bandpass";
        bp.frequency.value = b.freq;
        bp.Q.value = Math.max(0.6, b.q * 0.7);  // detector bandwidth slightly wider than EQ Q

        const det = ctx.createDynamicsCompressor();
        det.threshold.value = b.threshold;
        det.ratio.value     = 20;  // detector ratio is intentionally hot — we
                                   // want a clear gain-reduction signal even
                                   // for small over-threshold excursions
        det.attack.value    = b.attack;
        det.release.value   = b.release;
        det.knee.value      = 6;

        // CRITICAL: the detector's output must have a path to the
        // AudioContext destination, or Web Audio won't actually run the
        // compressor. The spec only "actively processes" nodes whose
        // output reaches the destination. A dead-end connection causes
        // `det.reduction` to stay at 0 forever — making cut bands appear
        // to do nothing.
        //
        // We route through a silent gain (gain=0) to the destination so
        // the compressor runs but contributes no audio to the mix.
        const detSink = ctx.createGain();
        detSink.gain.value = 0;
        det.connect(detSink);
        detSink.connect(ctx.destination);

        bp.connect(det);

        // ── Topology wiring ──
        // Upstream signal goes into a fanout gain. The fanout splits to:
        //   (a) the audio biquad (in series, continues down the chain)
        //   (b) the detector path (sidechain, measures level)
        // The biquad then connects to `node` (whatever the previous band's
        // fanout or downstream block is). This makes the next band's
        // upstream the fanout of THIS band.
        const fanout = ctx.createGain();
        fanout.gain.value = 1;
        fanout.connect(biquad);
        fanout.connect(bp);
        biquad.connect(node);
        node = fanout;  // upstream connects here, fanning out

        // ── Band config holder ──
        // The polling loop reads target gain + band type from `bandRef`
        // (mutable). applyFxLive mutates this object so live param tweaks
        // (changing gain target or type) flow through to the next poll tick
        // without rebuilding the chain.
        const bandRef = { gain: b.gain, type: b.type };

        // ── Polling loop (LIVE) vs Offline Automation (EXPORT) ──
        // Live: poll det.reduction at 33Hz and set biquad.gain.
        // Offline: DynamicsCompressor.reduction doesn't update meaningfully
        // in OfflineAudioContext (iOS/Safari spec quirk). Instead we
        // pre-compute a gain envelope in JS and schedule it onto biquad.gain
        // via setValueAtTime. The simulation runs BEFORE this buildChain
        // call (see simulateTQBandGain) and the result lands in
        // opts.tqAutomation, keyed by band id.
        let intervalId = null;
        if (registerLive) {
          intervalId = setInterval(function(){
            try {
              const r = Math.abs(det.reduction || 0);  // dB of detector reduction
              const targetGain = bandRef.gain;
              const bandType   = bandRef.type;
              let bandGain;
              if (bandType === "cut") {
                // Cut: gain dips from 0 toward target (negative) as r grows.
                // Scale linearly: 0dB → 0%; 6dB → 100% of target cut.
                bandGain = targetGain * Math.min(1, r / 6);
              } else {
                // Boost (upward expansion): full target when quiet (r~0),
                // backs off to 0 as detector compresses (loud passages).
                bandGain = targetGain * Math.max(0, 1 - r / 6);
              }
              biquad.gain.setTargetAtTime(bandGain, ctx.currentTime, 0.030);
            } catch(e) {}
          }, 33);
        } else if (opts && opts.tqAutomation && opts.tqAutomation[b.id]) {
          // Offline render: schedule the pre-computed gain envelope.
          // The automation arrives as {times: Float32Array, gains: Float32Array}.
          // Times are in seconds RELATIVE to the source audio's t=0. The
          // offline context starts everything later by `startupLead` (PDC
          // headroom), and clips are scheduled at startupLead + clipStartTime
          // - trackLatency. To keep the gain curve aligned with the audio
          // hitting the biquad, we offset every event by opts.tqAutomationOffset
          // (= startupLead, which approximates the average alignment well
          // enough for typical chains; minor sub-frame error from downstream
          // FX latency is inaudible).
          const auto = opts.tqAutomation[b.id];
          const tOff = opts.tqAutomationOffset || 0;
          try {
            // Anchor the start so the curve begins at the correct value.
            biquad.gain.setValueAtTime(auto.gains[0] || 0, Math.max(0, tOff));
            for (let i = 0; i < auto.times.length; i++) {
              biquad.gain.setValueAtTime(auto.gains[i], auto.times[i] + tOff);
            }
          } catch(e) {}
        }

        tqBandNodes.push({ id:b.id, biquad, bp, det, detSink, fanout, intervalId, bandRef, cfg:b });
      }

      // ── LISTEN filter ──
      // Sits UPSTREAM of all bands (signal hits it before entering the
      // band chain). When listenBandId is null, configured as a wide
      // lowpass at 22kHz = effectively transparent. When listenBandId is
      // set, configured as a bandpass at that band's frequency with Q=2
      // so the user hears ONLY that frequency range through the band's
      // dynamic processing.
      //
      // Always built so live updates (applyFxLive) can configure it
      // without a chain rebuild — toggling LISTEN is just an AudioParam
      // ramp, no node creation.
      //
      // EXPORT BEHAVIOR: LISTEN is a panel-level audition aid, not a
      // mix decision. Force it OFF in offline render so the exported WAV
      // is the full mix even if the user left LISTEN engaged.
      const listenFilter = ctx.createBiquadFilter();
      const initialListenId = registerLive ? (tq.listenBandId || null) : null;
      if (initialListenId) {
        const lb = bands.find(function(b){ return b.id === initialListenId; });
        if (lb) {
          listenFilter.type = "bandpass";
          listenFilter.frequency.value = lb.freq;
          listenFilter.Q.value = 2.0;
        } else {
          listenFilter.type = "lowpass";
          listenFilter.frequency.value = 22000;
          listenFilter.Q.value = 0.7;
        }
      } else {
        // Transparent: very wide lowpass at the Nyquist edge passes everything.
        listenFilter.type = "lowpass";
        listenFilter.frequency.value = 22000;
        listenFilter.Q.value = 0.7;
      }
      listenFilter.connect(node);  // listenFilter feeds INTO the upstream-most band fanout
      node = listenFilter;
      liveNodes.tq = { bandNodes: tqBandNodes, listenFilter: listenFilter };
    }

    // ── Compressor — always built; bypass when off via extreme settings ──
    {
      const comp = ctx.createDynamicsCompressor();
      const compOn = !!(fx.compressor && fx.compressor.on);
      comp.threshold.value = compOn ? (fx.compressor.threshold ?? -24) : 0;
      comp.ratio.value     = compOn ? (fx.compressor.ratio ?? 4)       : 1;
      comp.attack.value    = compOn ? (fx.compressor.attack ?? 0.003)  : 0.003;
      comp.release.value   = compOn ? (fx.compressor.release ?? 0.25)  : 0.25;
      comp.connect(node); node = comp;
      liveNodes.comp = comp;
    }

    // ── R-Comp (Waves Renaissance Compressor style) ────────────────────────
    // ARC = Auto-Release Control: when enabled, release time follows the
    // input envelope (longer for sustained signals, shorter for transients).
    // Web Audio's DynamicsCompressor has fixed release, so we simulate ARC
    // by setting a base release time and reacting to compressor.reduction
    // via a setInterval that nudges release between MIN (0.05s) and MAX (1.5s).
    //
    // Warm mode: subtle pre-stage tube saturation (light WaveShaper curve)
    //            + low-shelf boost @ 200Hz for body. Vocal-friendly.
    // Electro:   harder knee, faster attack, slight pre-emphasis HPF @ 80Hz.
    //            Tighter, punchier — good for drums/bass/synths.
    //
    // Always built so warm/electro can switch and ARC can toggle without
    // tearing down the chain. When off, threshold=0 + ratio=1 makes it
    // transparent (no gain reduction).
    {
      const rc       = fx.rcomp || {};
      const rcOn     = !!rc.on;
      const rcMode   = rc.mode || "warm";  // "warm" | "electro"

      // ── Pre-stage: mode-specific tone-shaper ──
      const rcInGain = ctx.createGain(); rcInGain.gain.value = 1;
      const rcShelf  = ctx.createBiquadFilter();
      const rcSat    = ctx.createWaveShaper();
      rcSat.oversample = "2x";
      // Build saturation curve per mode. Warm = soft tube-style asymmetric
      // clip; Electro = symmetric harder clip. Curve length 1024 is fine
      // for vocal frequencies (Nyquist aliasing minimal at oversample=2x).
      const makeRCompCurve = function(mode, amount) {
        const n = 1024;
        const c = new Float32Array(n);
        const drive = Math.max(0.01, amount);
        for (let i=0; i<n; i++) {
          const x = (i * 2 / n - 1);
          if (mode === "warm") {
            // Asymmetric soft clip — gentler on negative half
            const sign = x >= 0 ? 1 : 0.82;
            c[i] = Math.tanh(x * (1 + drive * 0.6) * sign) * 0.94;
          } else {
            // Electro: harder symmetric clip with brighter edge
            c[i] = Math.tanh(x * (1 + drive * 1.4)) * 0.98;
          }
        }
        return c;
      };
      // Warm mode adds low-shelf body; electro adds high-pass pre-emphasis
      if (rcMode === "warm") {
        rcShelf.type = "lowshelf";
        rcShelf.frequency.value = 200;
        rcShelf.gain.value = rcOn ? 1.2 : 0;
      } else {
        rcShelf.type = "highpass";
        rcShelf.frequency.value = 80;
        rcShelf.Q.value = 0.5;
      }
      rcSat.curve = makeRCompCurve(rcMode, rcOn ? 0.18 : 0);

      // ── Core compressor (DynamicsCompressor) ──
      const rcComp = ctx.createDynamicsCompressor();
      rcComp.threshold.value = rcOn ? (rc.threshold ?? -18) : 0;
      rcComp.ratio.value     = rcOn ? (rc.ratio ?? 3)        : 1;
      rcComp.attack.value    = rcOn ? (rc.attack ?? 0.012)   : 0.003;
      // Initial release; ARC will modulate this if enabled.
      rcComp.release.value   = rcOn ? (rc.release ?? 0.18)   : 0.25;
      // Knee: warm = soft (6dB), electro = harder (2dB)
      rcComp.knee.value      = rcOn ? (rcMode === "warm" ? 6 : 2) : 30;

      // ── Makeup gain ──
      const rcMakeup = ctx.createGain();
      rcMakeup.gain.value = rcOn ? Math.pow(10, ((rc.makeupGain ?? 0)) / 20) : 1;

      // Connect: rcInGain → rcShelf → rcSat → rcComp → rcMakeup → [rest]
      rcInGain.connect(rcShelf);
      rcShelf.connect(rcSat);
      rcSat.connect(rcComp);
      rcComp.connect(rcMakeup);
      rcMakeup.connect(node);
      node = rcInGain;

      // ── ARC: auto-release modulation ──
      // Poll reduction value at 30Hz. If reduction stays heavy, gradually
      // extend release toward MAX. If reduction is intermittent, pull back
      // toward MIN. This approximates Waves' ARC heuristic without
      // requiring a full envelope follower implementation.
      let arcTimer = null;
      // ARC interval only meaningful in live context — offline render is
      // instantaneous, the timer would never fire during render anyway.
      if (registerLive && rcOn && (rc.arc !== false)) {  // ARC defaults to ON
        const MIN_REL = 0.05, MAX_REL = 1.5;
        let smoothed = rcComp.release.value;
        arcTimer = setInterval(function(){
          try {
            // reduction is negative (in dB). More negative = more compression.
            const red = Math.abs(rcComp.reduction || 0);
            // Map: low reduction (< 2dB) → short release; high (> 8dB) → long
            const target = MIN_REL + (Math.min(8, red) / 8) * (MAX_REL - MIN_REL);
            // EMA smoothing so release doesn't jitter on every poll
            smoothed = smoothed * 0.85 + target * 0.15;
            rcComp.release.setTargetAtTime(smoothed, ctx.currentTime, 0.05);
          } catch(e) {}
        }, 33);
      }

      liveNodes.rcomp = {
        rcInGain, rcShelf, rcSat, rcComp, rcMakeup,
        mode: rcMode,
        arcTimer,
        makeRCompCurve,
      };
    }

    // ── Pultec EQP-1A — Passive Program Equalizer ──────────────────────────
    // Four-stage biquad chain emulating the classic Pultec topology:
    //   Stage 1: Low Boost  — lowshelf at lowFreq, +0..10 dB
    //   Stage 2: Low Atten  — peaking at lowFreq, -0..10 dB (creates scoop above)
    //   Stage 3: High Boost — peaking at hiFreq, +0..10 dB, Q from SHARP knob
    //   Stage 4: High Atten — highshelf at attenFreq, -0..10 dB
    //
    // The "Pultec trick": engaging Low Boost AND Low Atten simultaneously at
    // the same frequency creates a uniquely musical curve — deep weight at
    // the chosen frequency with a scoop just above it (carves out mud).
    // The trick works here because the lowshelf and peaking filter have
    // different curve shapes; their sum is not flat even though both target
    // the same Hz.
    //
    // SHARP knob (1..10) maps to Q: SHARP=1 → Q=2 (narrow), SHARP=10 → Q=0.3
    // (wide). This matches the Pultec hardware bandwidth behavior (counter-
    // intuitive: higher SHARP = WIDER bell, due to historical labeling).
    //
    // Always built so all knobs can morph live without a chain rebuild.
    // When the plugin is OFF, all gains go to 0 (transparent pass-through).
    {
      const pt = fx.pultec || {};
      const ptOn = !!pt.on;
      const T  = 0.005;

      // ── Stage 1: Low Boost (lowshelf) ──
      const ptLowBoost = ctx.createBiquadFilter();
      ptLowBoost.type = "lowshelf";
      ptLowBoost.frequency.value = pt.lowFreq ?? 60;
      ptLowBoost.gain.value = ptOn ? (pt.lowBoost ?? 0) : 0;

      // ── Stage 2: Low Atten (peaking, negative gain) ──
      // Peaking filter at the same freq with NEGATIVE gain creates the
      // characteristic Pultec mid-scoop above the bass region.
      const ptLowAtten = ctx.createBiquadFilter();
      ptLowAtten.type = "peaking";
      ptLowAtten.frequency.value = pt.lowFreq ?? 60;
      ptLowAtten.Q.value = 0.6;  // Pultec's low atten has a wider bell
      ptLowAtten.gain.value = ptOn ? -(pt.lowAtten ?? 0) : 0;

      // ── Stage 3: High Boost (peaking with bandwidth/Q) ──
      // SHARP knob (1..10) inverted to Q: high SHARP = wide bell (low Q),
      // low SHARP = narrow bell (high Q). Map linearly from Q=2 to Q=0.3.
      const sharpVal = pt.sharp ?? 5;
      const hiBoostQ = 2.0 - ((sharpVal - 1) / 9) * 1.7;  // 1→2.0, 10→0.3
      const ptHiBoost = ctx.createBiquadFilter();
      ptHiBoost.type = "peaking";
      ptHiBoost.frequency.value = pt.hiFreq ?? 5000;
      ptHiBoost.Q.value = hiBoostQ;
      ptHiBoost.gain.value = ptOn ? (pt.hiBoost ?? 0) : 0;

      // ── Stage 4: High Atten (highshelf, negative gain) ──
      const ptHiAtten = ctx.createBiquadFilter();
      ptHiAtten.type = "highshelf";
      ptHiAtten.frequency.value = pt.attenFreq ?? 10000;
      ptHiAtten.gain.value = ptOn ? -(pt.hiAtten ?? 0) : 0;

      // Chain: ptLowBoost → ptLowAtten → ptHiBoost → ptHiAtten → [rest]
      ptHiAtten.connect(node);
      ptHiBoost.connect(ptHiAtten);
      ptLowAtten.connect(ptHiBoost);
      ptLowBoost.connect(ptLowAtten);
      node = ptLowBoost;

      liveNodes.pultec = { ptLowBoost, ptLowAtten, ptHiBoost, ptHiAtten };
    }

    // ── Vocal Doubler — Haas delay + gentle detune chorus + M/S width ────────
    // Always built so wet/dry and width can morph live without a rebuild.
    {
      const db       = fx.doubler || {};
      const doubOn   = !!(fx.doubler && fx.doubler.on);
      const delayMs  = doubOn ? (db.delay  ?? 20)  : 20;
      const detune   = doubOn ? (db.detune ?? 8)   : 8;   // cents
      const width    = doubOn ? (db.width  ?? 0.7) : 0;
      const mixWet   = doubOn ? (db.mix    ?? 0.5) : 0;

      // ── Dry path ──
      const dryGain  = ctx.createGain();
      dryGain.gain.value = 1 - mixWet;

      // ── Wet path: Haas delay (L) + detuned copy (R) ──
      const wetGain  = ctx.createGain();
      wetGain.gain.value = mixWet;

      // Splitter/merger for M/S width
      const splitter = ctx.createChannelSplitter(2);
      const merger   = ctx.createChannelMerger(2);

      // Left copy: Haas delay
      const haasDelay = ctx.createDelay(0.1);
      haasDelay.delayTime.value = delayMs / 1000;

      // Right copy: detune via oscillator-as-LFO? No — use a BiquadFilter allpass
      // for subtle chorus via comb phasing (detune knob drives a slight pitchbend
      // via a short feedback delay acting as a chorus line).
      const chorusDelay = ctx.createDelay(0.05);
      chorusDelay.delayTime.value = (detune / 1000) * 0.012 + 0.001; // 1–13ms chorus

      const chorusFeedback = ctx.createGain();
      chorusFeedback.gain.value = 0.18;

      // Width: blend L vs R amount into merger
      const lWidthGain = ctx.createGain(); lWidthGain.gain.value = 0.5 + width * 0.5;
      const rWidthGain = ctx.createGain(); rWidthGain.gain.value = 0.5 + width * 0.5;

      // Wet mix bus
      const wetMix = ctx.createGain(); wetMix.gain.value = 1;
      const dryMix = ctx.createGain(); dryMix.gain.value = 1;
      const outMix = ctx.createGain(); outMix.gain.value = 1;

      // Entry gain (splits the signal)
      const doubEntry = ctx.createGain(); doubEntry.gain.value = 1;

      // Routing: entry → dryGain → outMix (always)
      // When doubler is OFF, skip the wet path entirely. The two delay
      // lines (plus chorus feedback loop) would otherwise process the
      // signal even when the wet gain is 0, costing audio-thread CPU.
      // Audible result is identical (wet would have been silent anyway).
      doubEntry.connect(dryGain);
      dryGain.connect(outMix);

      if (doubOn) {
        // Chorus feedback loop — only wired when needed
        chorusDelay.connect(chorusFeedback);
        chorusFeedback.connect(chorusDelay);

        doubEntry.connect(haasDelay);
        doubEntry.connect(chorusDelay);

        haasDelay.connect(lWidthGain);
        chorusDelay.connect(rWidthGain);

        lWidthGain.connect(wetMix);
        rWidthGain.connect(wetMix);

        wetMix.connect(wetGain);
        wetGain.connect(outMix);
      }

      outMix.connect(node);
      node = doubEntry;

      liveNodes.doubler = {
        haasDelay, chorusDelay, chorusFeedback,
        dryGain, wetGain, wetMix,
        lWidthGain, rWidthGain,
      };
    }

    // ── H-Delay — Waves H-Delay emulation ────────────────────────────────────
    // Always built so wet/dry and all params can morph live.
    // Architecture: input → [dry path] + [delay loop with hi/lo cut + drive]
    // Tape mode: adds a slow LFO modulating the delay time (wow/flutter)
    // Ping-pong: alternates wet signal L vs R each repeat via a StereoPanner
    {
      const hd       = fx.hdelay || {};
      const hdOn     = !!(fx.hdelay && fx.hdelay.on);
      const mode     = hd.mode        ?? "digital";
      const feedback = hdOn ? Math.min(0.95, hd.feedback ?? 0.35) : 0;
      const wetMixV  = hdOn ? (hd.wet ?? 0.40) : 0;
      const hiCut    = hd.hiCut   ?? 8000;
      const loCut    = hd.loCut   ?? 80;
      const drive    = hdOn ? (hd.drive ?? 0) : 0;

      // BPM sync helper (mirrors UI)
      function hdSubdivMs(sub, bpmVal) {
        const beat = 60000 / (bpmVal || 120);
        const map = {
          "1/1":beat*4,"1/2":beat*2,"1/2T":beat*(4/3),
          "1/4":beat,"1/4T":beat*(2/3),
          "1/8":beat/2,"1/8T":beat/3,
          "1/16":beat/4,"1/16T":beat/6,"1/32":beat/8,
        };
        return Math.min(2000, (map[sub] || beat)) / 1000;
      }
      const delayTimeSec = hdOn
        ? (hd.sync ? hdSubdivMs(hd.subdivision ?? "1/4", hd.bpm ?? 120) : (hd.delayMs ?? 375) / 1000)
        : 0.375;

      // Entry split
      const hdEntry  = ctx.createGain(); hdEntry.gain.value = 1;

      // Dry path
      const hdDry    = ctx.createGain(); hdDry.gain.value = 1 - wetMixV;

      // Wet output gain
      const hdWet    = ctx.createGain(); hdWet.gain.value = wetMixV;

      // Output sum
      const hdOut    = ctx.createGain(); hdOut.gain.value = 1;

      // Main delay (up to 2.1s)
      const hdDelay  = ctx.createDelay(2.1);
      hdDelay.delayTime.value = delayTimeSec;

      // Feedback path: delay → hiCut filter → loCut filter → (drive) → feedback gain → delay
      const hdHiCut  = ctx.createBiquadFilter();
      hdHiCut.type = "lowpass";
      hdHiCut.frequency.value = hiCut;
      hdHiCut.Q.value = 0.5;

      const hdLoCut  = ctx.createBiquadFilter();
      hdLoCut.type = "highpass";
      hdLoCut.frequency.value = loCut;
      hdLoCut.Q.value = 0.5;

      // Analog saturation waveshaper (soft clip — bypassed when drive=0)
      // PERF: oversample:"2x" doubles CPU on this stage (processes at
      // 2× sample rate). Only enable when drive > 0 AND h-delay is on,
      // so disabled tracks don't pay the cost.
      const hdSat = ctx.createWaveShaper();
      const satCurve = (function() {
        const n = 256, c = new Float32Array(n);
        for(let i=0;i<n;i++){
          const x = (i * 2 / n) - 1;
          c[i] = x * (1 + drive * 4) / (1 + Math.abs(x) * drive * 4);
        }
        return c;
      })();
      hdSat.curve = satCurve;
      hdSat.oversample = (hdOn && drive > 0) ? "2x" : "none";

      // Tape wow/flutter: LFO → delay modulation (only in tape mode).
      // PERF: an OscillatorNode runs at audio rate even at 0.001 Hz —
      // each running LFO is a small but real CPU cost on the audio
      // thread, multiplied across every track. Only START the LFO
      // when it's actually needed (h-delay on AND tape mode). It can
      // be lazily started later via the live-update path if mode
      // changes to "tape" mid-session.
      const hdLfo   = ctx.createOscillator();
      const hdLfoGain = ctx.createGain();
      const modDepth = hdOn ? (hd.modDepth ?? 0.15) : 0;
      const modRate  = hd.modRate ?? 0.5;
      hdLfo.type = "sine";
      hdLfo.frequency.value = mode === "tape" ? modRate : 0.001;
      hdLfoGain.gain.value = mode === "tape" ? modDepth * 0.015 : 0;
      hdLfo.connect(hdLfoGain);
      hdLfoGain.connect(hdDelay.delayTime);
      if (hdOn && mode === "tape") {
        try { hdLfo.start(); } catch (e) {}
      }

      // Feedback gain
      const hdFb    = ctx.createGain(); hdFb.gain.value = feedback;

      // Ping-pong panner: alternates L/R on each repeat via a secondary delay copy
      // We implement ping-pong with a StereoPanner that is flipped by feedback count.
      // Simplification: in ping-pong we duplicate the wet signal with a half-offset copy.
      const hdPan   = ctx.createStereoPanner();
      hdPan.pan.value = mode === "ping" ? -1 : 0; // first echo hard-L, feedback flips

      const hdPanFlip = ctx.createStereoPanner();
      hdPanFlip.pan.value = mode === "ping" ? 1 : 0; // second echo hard-R

      const hdFbFlip = ctx.createGain(); hdFbFlip.gain.value = mode === "ping" ? feedback : 0;
      const hdDelayFlip = ctx.createDelay(2.1);
      hdDelayFlip.delayTime.value = delayTimeSec; // half-offset for flip copy

      // ── Routing ──
      // Main chain: hdEntry → hdDelay → hdHiCut → hdLoCut → hdSat → hdPan → hdWet → hdOut
      //             └─ feedback: hdSat → hdFb → hdDelay (loop)
      // PERF: when h-delay is OFF, route through the dry path ONLY.
      // The wet path's 2.1s delay buffer + biquads + waveshaper + LFO
      // all consume audio-thread CPU even when their output gain is 0.
      // Skipping the connection entirely saves all of that, with zero
      // audible difference (wet output would be silent anyway).
      hdEntry.connect(hdDry);
      hdDry.connect(hdOut);

      if (hdOn) {
        hdEntry.connect(hdDelay);
        hdDelay.connect(hdHiCut);
        hdHiCut.connect(hdLoCut);
        hdLoCut.connect(hdSat);
        hdSat.connect(hdPan);
        hdPan.connect(hdWet);
        // Feedback loop
        hdSat.connect(hdFb);
        hdFb.connect(hdDelay);

        // Ping-pong second copy (offset by half a delay period)
        if (mode === "ping") {
          hdSat.connect(hdFbFlip);
          hdFbFlip.connect(hdDelayFlip);
          hdDelayFlip.connect(hdPanFlip);
          hdPanFlip.connect(hdWet);
        }

        hdWet.connect(hdOut);
      }
      hdOut.connect(node);
      node = hdEntry;

      liveNodes.hdelay = {
        hdDelay, hdDelayFlip, hdHiCut, hdLoCut, hdSat,
        hdDry, hdWet, hdFb, hdFbFlip,
        hdPan, hdPanFlip, hdLfo, hdLfoGain, hdOut,
      };
    }
    // ── Noise Remover — always built so on/off toggle works live ────────────
    // When off: bypass=1 (worklet path) or threshold=0/ratio=1 (fallback).
    // voice param drives a post-gate presence shelf (+0..+8dB at 2.5kHz).
    // ── Noise Gate + Hiss Reduction chain ──────────────────────────────────
    {
      const nr         = fx.noiseremover || {};
      const nrOn       = !!(fx.noiseremover && fx.noiseremover.on);
      const threshDb   = nrOn ? (nr.threshold ?? -40) : -200; // direct dB
      const releaseSec = nrOn ? (nr.release   ?? 0.15) : 0.15;
      const holdSec    = nrOn ? (nr.hold      ?? 0.08) : 0.08;
      const hissAmt    = nrOn ? (nr.hiss      ?? 0)    : 0;   // 0–1 → 0..-18dB @ 8kHz
      const lookahead  = !!(nr.lookahead ?? true);
      const attackSec  = lookahead ? 0.001 : 0.003;
      const reductDb   = -80; // gate floor: near-silence when closed

      // ── High-shelf hiss filter (always in chain for live updates) ─────────
      // Cuts 8kHz+ by 0..–18dB. Reduces white noise/hiss that gets through
      // when the gate is open (e.g. room hiss under a loud vocal).
      const hissFilter = ctx.createBiquadFilter();
      hissFilter.type = "highshelf";
      hissFilter.frequency.value = 8000;
      hissFilter.gain.value = hissAmt * -18; // 0 = flat, 1 = -18dB cut
      hissFilter.connect(node);
      node = hissFilter;

      // ── Noise Gate AudioWorklet (primary path) ────────────────────────────
      // noiseGateWorkletReady is an optional ref — if it's not declared in this
      // build, skip the worklet path entirely and use the DynamicsCompressor fallback.
      // (A missing ref previously threw a ReferenceError here that killed buildChain
      //  for every track, resulting in total Studio silence.)
      //
      // PERF: only allocate the worklet when the user actually has noise
      // removal ON. The processor runs JS per-sample even with bypass=1, so
      // a dormant worklet still burns audio-thread CPU. We rely on the
      // auto-stop-on-FX-toggle behaviour to safely rebuild the chain when
      // user enables it during playback.
      const gateReady = (typeof noiseGateWorkletReady !== "undefined")
        && noiseGateWorkletReady
        && noiseGateWorkletReady.current;
      if (gateReady && nrOn) {
        try {
          const gateNode = new AudioWorkletNode(ctx, "noise-gate-processor", {
            numberOfInputs: 1, numberOfOutputs: 1, outputChannelCount: [1],
          });
          gateNode.parameters.get("threshold").value = threshDb;
          gateNode.parameters.get("reduction").value = reductDb;
          gateNode.parameters.get("attack").value    = attackSec;
          gateNode.parameters.get("hold").value      = Math.min(1.0, holdSec);
          gateNode.parameters.get("release").value   = releaseSec;
          gateNode.parameters.get("bypass").value    = nrOn ? 0 : 1;
          // Patch a message port so the UI can read gate open/closed state
          gateNode.port.onmessage = function(e) {};
          liveNodes.rnnoiseNode = gateNode;
          gateNode.connect(node);
          node = gateNode;
        } catch(e) { /* fall through */ }
      }

      // ── Fallback: DynamicsCompressor as gate ──────────────────────────────
      // Only allocate when noise removal is ON. A passthrough compressor
      // (threshold=0, ratio=1) is mathematically a no-op but still has
      // tiny per-sample cost across 4-6 tracks.
      if (!liveNodes.rnnoiseNode && nrOn) {
        const gate = ctx.createDynamicsCompressor();
        gate.threshold.value = threshDb;
        gate.ratio.value     = 20;
        gate.attack.value    = attackSec;
        gate.release.value   = releaseSec;
        gate.knee.value      = 0;
        gate.connect(node); node = gate;
        liveNodes.rnnoiseGate = gate;
      }

      liveNodes.noiseremover = { on: nrOn, hissFilter };
    }

    // ── T-Rotten Master — IK Multimedia T-RackS One emulation ──────────────
    // Signal chain: inputGain → 3-band EQ → compressor → tape saturator → limiter → outputGain
    // Always built so all knobs can morph live without a restart.
    {
      const tr   = fx.trotten || {};
      const trOn = !!(fx.trotten && fx.trotten.on);

      // Input gain
      const trInGain = ctx.createGain();
      trInGain.gain.value = trOn ? Math.pow(10, (tr.inputGain ?? 0) / 20) : 1;

      // Low-shelf EQ
      const trEqLow = ctx.createBiquadFilter();
      trEqLow.type = trOn && tr.eqLowT === "bell" ? "peaking" : "lowshelf";
      trEqLow.frequency.value = 200;
      trEqLow.gain.value      = trOn ? (tr.eqLow ?? 0) : 0;
      trEqLow.Q.value         = 0.707;

      // Mid peaking/bell EQ
      const trEqMid = ctx.createBiquadFilter();
      trEqMid.type = trOn && tr.eqMidT === "shelf" ? "lowshelf" : "peaking";
      trEqMid.frequency.value = 1000;
      trEqMid.gain.value      = trOn ? (tr.eqMid ?? 0) : 0;
      trEqMid.Q.value         = 1.0;

      // High-shelf EQ
      const trEqHigh = ctx.createBiquadFilter();
      trEqHigh.type = trOn && tr.eqHighT === "bell" ? "peaking" : "highshelf";
      trEqHigh.frequency.value = 8000;
      trEqHigh.gain.value      = trOn ? (tr.eqHigh ?? 0) : 0;
      trEqHigh.Q.value         = 0.707;

      // Compressor — compAmt 0..100 maps to ratio 1..20, threshold from compThr
      const trComp = ctx.createDynamicsCompressor();
      const compRatio = trOn ? Math.max(1, 1 + (tr.compAmt ?? 50) / 100 * 19) : 1;
      trComp.threshold.value = trOn ? (tr.compThr ?? -15) : 0;
      trComp.ratio.value     = compRatio;
      trComp.attack.value    = trOn && tr.compMode === "fast" ? 0.001 : 0.005;
      trComp.release.value   = trOn && tr.compMode === "slow" ? 0.5   : 0.1;
      trComp.knee.value      = 6;

      // Tape saturation waveshaper — tapeDrv 0..10, tapeSat 0..10
      const trTape = ctx.createWaveShaper();
      const buildTapeCurve = function(drv, sat, mode) {
        const n = 512; const c = new Float32Array(n);
        const k = (drv / 10) * 4 + (sat / 10) * 2; // combined drive+sat
        const warmBoost = mode === "vintage" ? 1.3 : 1.0;
        for (let i = 0; i < n; i++) {
          const x = (i * 2 / n - 1) * warmBoost;
          if (k < 0.01) { c[i] = x; }
          else { c[i] = (Math.PI + k) * x / (Math.PI + k * Math.abs(x)); }
        }
        return c;
      };
      trTape.curve      = buildTapeCurve(
        trOn ? (tr.tapeDrv ?? 5) : 0,
        trOn ? (tr.tapeSat ?? 5) : 0,
        trOn ? (tr.tapeMode ?? "modern") : "modern"
      );
      trTape.oversample = "4x";

      // Tape tone coloring: slight low-end warmth boost in vintage mode
      const trTapeShelf = ctx.createBiquadFilter();
      trTapeShelf.type          = "lowshelf";
      trTapeShelf.frequency.value = 300;
      trTapeShelf.gain.value    = trOn && tr.tapeMode === "vintage" ? 1.5 : 0;

      // Brickwall limiter (emulate true-peak limiter via hard DynamicsCompressor)
      const trLim = ctx.createDynamicsCompressor();
      const limCeil = trOn ? (tr.limCeil ?? -0.5) : 0;
      trLim.threshold.value = trOn ? limCeil : 0;
      trLim.ratio.value     = 20; // near-infinite ratio = brickwall
      trLim.attack.value    = 0.0005; // 0.5ms lookahead
      trLim.release.value   = trOn ? Math.max(0.05, tr.limRel ?? 0.5) : 0.25;
      trLim.knee.value      = 0;

      // Output gain
      const trOutGain = ctx.createGain();
      trOutGain.gain.value = trOn ? Math.pow(10, (tr.outputGain ?? 0) / 20) : 1;

      // Chain: trInGain → trEqLow → trEqMid → trEqHigh → trComp → trTape → trTapeShelf → trLim → trOutGain → [rest]
      trOutGain.connect(node);
      trLim.connect(trOutGain);
      trTapeShelf.connect(trLim);
      trTape.connect(trTapeShelf);
      trComp.connect(trTape);
      trEqHigh.connect(trComp);
      trEqMid.connect(trEqHigh);
      trEqLow.connect(trEqMid);
      trInGain.connect(trEqLow);
      node = trInGain;

      liveNodes.trotten = {
        trInGain, trEqLow, trEqMid, trEqHigh,
        trComp, trTape, trTapeShelf, trLim, trOutGain,
        buildTapeCurve,
      };
    }

    // ── GRM Bandpass — dual cascaded biquad bandpass (≈12 dB/oct × 2 = 6th order) ──
    // Always built wet/dry so mix morphs live. When off, dryG=1 / wetG=0.
    {
      const bpFx   = fx.bandpass || {};
      const bpOn   = !!(fx.bandpass && fx.bandpass.on);
      const center = bpOn ? Math.min(8000, bpFx.center ?? 1000) : 1000;
      const octW   = bpOn ? (bpFx.width  ?? 1.0)  : 1.0;
      const resDb  = bpOn ? (bpFx.res    ?? 0)    : 0;
      const mixWet = bpOn ? (bpFx.mix    ?? 1)    : 0;

      // Q from octave bandwidth: Q = √2^w / (2^w − 1)
      const Q  = Math.sqrt(Math.pow(2, octW)) / (Math.pow(2, octW) - 1);
      const Qr = Math.max(0.1, Q * (1 + resDb / 6));

      // First cascaded biquad bandpass
      const bp1 = ctx.createBiquadFilter();
      bp1.type = "bandpass";
      bp1.frequency.value = center;
      bp1.Q.value = Qr;

      // Second cascaded biquad bandpass (same params → steeper skirts)
      const bp2 = ctx.createBiquadFilter();
      bp2.type = "bandpass";
      bp2.frequency.value = center;
      bp2.Q.value = Qr;

      // Wet/dry mix
      const bpDry = ctx.createGain(); bpDry.gain.value = 1 - mixWet;
      const bpWet = ctx.createGain(); bpWet.gain.value = mixWet;
      const bpOut = ctx.createGain(); bpOut.gain.value = 1;
      const bpEntry = ctx.createGain(); bpEntry.gain.value = 1;

      // Routing: entry → dry → out ; entry → bp1 → bp2 → wet → out → next
      bpEntry.connect(bpDry);
      bpDry.connect(bpOut);
      bpEntry.connect(bp1);
      bp1.connect(bp2);
      bp2.connect(bpWet);
      bpWet.connect(bpOut);
      bpOut.connect(node);
      node = bpEntry;

      liveNodes.bandpass = { bp1, bp2, bpDry, bpWet, bpOut };
    }

    // ── Autotune — pitch correction on playback ───────────────────────────
    // Only allocated when ON. AudioWorklet processors run JS on every audio
    // frame even with bypass=1, which on iOS Safari can cause crackles when
    // multiple tracks each have a dormant worklet sitting in the chain.
    //
    // The auto-stop-on-FX-toggle behaviour (in fxUpdRef.current) ensures
    // that toggling autotune ON during playback stops playback so the chain
    // rebuilds with the worklet inserted on the next Play press. No UX
    // surprise — the toast tells the user "Press Play to apply".
    {
      const at   = fx.autotune || {};
      const atOn = !!(at.on);
      if (atOn) {
      try {
        const KEY_OFFSETS = {C:0,"C#":1,Db:1,D:2,"D#":3,Eb:3,E:4,F:5,"F#":6,Gb:6,G:7,"G#":8,Ab:8,A:9,"A#":10,Bb:10,B:11};
        const atNode = new AudioWorkletNode(ctx, "autotune-processor", {
          numberOfInputs: 1, numberOfOutputs: 1, outputChannelCount: [1],
        });
        atNode.parameters.get("bypass").value   = atOn ? 0 : 1;
        atNode.parameters.get("key").value      = KEY_OFFSETS[at.key || "C"] || 0;
        atNode.parameters.get("speed").value    = at.speed ?? 50;
        atNode.parameters.get("humanize").value = at.humanize ?? 10;
        atNode.parameters.get("wet").value      = at.wet ?? 1;
        atNode.parameters.get("vibrato").value  = at.vibrato ?? 0;
        atNode.port.postMessage({ scale: at.scale || "major" });
        atNode.connect(node);
        node = atNode;
        liveNodes.autotune = atNode;
      } catch(e) {
        // Worklet not ready yet — autotune simply unavailable for this play session
        console.warn("[Studio] Autotune node creation failed:", e);
      }
      } // end if(atOn) — skip worklet allocation entirely when off
    }

    // Store live nodes for this track
    if (registerLive) fxNodesRef.current[track.id] = liveNodes;

    return node; // clips connect here
  };

  const doPlay = async function (fromTime, opts) {
    opts = opts || {};
    const isLoopRestart = !!opts.isLoopRestart;
    // Mutual exclusion with PAT transport. FL-style: SONG mode = song
    // plays from the timeline; the rack PAT loop must NOT also be firing
    // the same pattern, or hits double. Skip on loop-restart since that's
    // an internal master transition, not a fresh SONG start.
    if (!isLoopRestart && rackPatPlayingRef.current) {
      try { rackPatStop(); } catch(_){}
    }
    // On a loop restart we don't tear down master gain — that would
    // create an audible gap on every loop. The previous loop's clips
    // are about to end naturally; the new clips will start exactly when
    // they do so the audio is continuous. We also defer the outgoing
    // sources' stop to the seam (anchorAudioTime) so their tails ring
    // right into the downbeat instead of leaving a silent gap.
    stopAll({
      keepGain: isLoopRestart,
      stopAt: (isLoopRestart && opts.anchorAudioTime) ? opts.anchorAudioTime : null,
    });
    // ── Re-assert every loaded soundfont preset's channel binding ──
    // Defends against the case where a preset's channel state was
    // clobbered between plays (e.g. iOS suspend, second preset load
    // racing). Cheap, idempotent. With per-track engines, we walk
    // EVERY engine so all tracks start playing cleanly.
    try {
      if (typeof forEachBFSoundfontEngine === "function") {
        forEachBFSoundfontEngine(function(eng){
          if (eng && typeof eng.reaffirmAllChannelBindings === "function") {
            try { eng.reaffirmAllChannelBindings(); } catch(_){}
          }
        });
      } else {
        const sfEng = (typeof getBFSoundfontEngine === "function")
          ? getBFSoundfontEngine() : null;
        if (sfEng && typeof sfEng.reaffirmAllChannelBindings === "function") {
          sfEng.reaffirmAllChannelBindings();
        }
      }
    } catch (_) { /* non-fatal */ }
    userScrolledAt.current = null; // reset manual override — auto-scroll active from start
    liveTimeRef.current = fromTime;
    const actx   = getActx();
    const master = getOrCreateMaster();

    // ── iOS audio pipeline pre-warm ─────────────────────────────
    // iOS Safari adds ~200-400ms of latency from actx.resume() to the
    // first audible sample. Before scheduling notes, we play a silent
    // buffer directly into actx.destination to force the audio output
    // pipeline to spin up. By the time we measure FX latency and start
    // scheduling notes (~50-100ms later), the speaker is already
    // streaming, so first-note timing matches the playhead display.
    // SKIP on loop restart — the pipeline is already streaming, and
    // an extra primer buffer would be wasted work that delays the
    // doPlay function returning (= delays the loop seamlessness).
    if (!isLoopRestart) {
      try {
        if (actx.state === "suspended") {
          await actx.resume();
        }
        const primeBuf = actx.createBuffer(1, Math.max(1, Math.floor(actx.sampleRate * 0.02)), actx.sampleRate);
        // Buffer is naturally zero-filled — outputting silence is fine for warmup
        const primeSrc = actx.createBufferSource();
        primeSrc.buffer = primeBuf;
        primeSrc.connect(actx.destination);
        primeSrc.start(0);
      } catch (e) {
        // Non-fatal — playback will still work, just with cold-start latency
      }
    }

    // Restore master gain — stopAll ramps it to 0 to cut tails.
    // On loop restart we already passed keepGain:true so the gain is
    // still at masterVolume — but re-asserting it here is harmless.
    try {
      master.gain.cancelScheduledValues(0);
      master.gain.value = masterVolume;
    } catch (e) {}
    // Register pitch worklet if not done yet (async, required before AudioWorkletNode)
    // Register RNNoise worklet if not done yet
    const gateOk = await registerRNNoiseWorklet(actx);
    noiseGateWorkletReady.current = !!gateOk;
    // Register autotune worklet on the playback AudioContext so it's ready for buildChain
    await registerAutotuneWorklet(actx);

    // ── PDC: Measure FX chain latencies in parallel ──
    // For each track with FX, measure its actual end-to-end latency via
    // OfflineAudioContext. Result cached per (trackId, fxSig) so we don't
    // re-measure on every play. The MAX latency across all tracks determines
    // how far ahead we need to schedule audio so FX-laden tracks finish their
    // processing at the same wall-clock moment as dry tracks.
    let maxFxLatency = 0;
    try {
      const liveTracks = tracksRef.current || [];
      const measureTasks = liveTracks.map(async function(track) {
        const sig = fxSignature(track.effects);
        const cached = fxLatencyRef.current[track.id];
        // Cache hit — skip measurement
        if (cached && cached.fxSig === sig) return cached.latencySec;
        // Measure for real
        const lat = await measureChainLatency(track, actx.sampleRate);
        fxLatencyRef.current[track.id] = { fxSig: sig, latencySec: lat };
        return lat;
      });
      const latencies = await Promise.all(measureTasks);
      for (let i = 0; i < latencies.length; i++) {
        if (latencies[i] > maxFxLatency) maxFxLatency = latencies[i];
      }
      // Log so we can see what was measured during development
      try {
        if (maxFxLatency > 0) {
          console.log("[PDC] Measured chain latencies:",
            liveTracks.map(function(t, i){
              return t.name + "=" + (latencies[i]*1000).toFixed(1) + "ms";
            }).join(", "),
            "→ max=" + (maxFxLatency * 1000).toFixed(1) + "ms");
        }
      } catch(e) {}
    } catch(e) {
      console.warn("[PDC] Measurement pass failed, no compensation applied:", e);
      maxFxLatency = 0;
    }

    // Schedule audio (50ms + maxFxLatency) ahead so all tracks start at
    // exactly the same moment. masterStartRef MUST equal `now` (the
    // scheduled start), NOT actx.currentTime.
    // Recording timing: playheadAtRef + (startActxTime - masterStartRef).
    // If masterStartRef is 50ms behind `now`, every clip is placed 50ms too early
    // on the timeline, causing it to play back late (behind the beat) on playback.
    // BUMPING NOW by maxFxLatency gives FX-laden tracks enough headroom to be
    // scheduled at (now - trackLatency) without going below actx.currentTime.
    // Capped at 250ms total so worst-case startup is bearable; latencies above
    // that almost certainly indicate a measurement glitch.
    // iOS Safari has a ~200-400ms cold-start delay between actx.resume()
    // and the first audible sample reaching the speaker, even though
    // actx.currentTime ticks forward immediately. We absorb most of this
    // by playing a silent prewarm buffer at the top of doPlay() (above),
    // which spins up the output pipeline before we schedule notes. 150ms
    // of lead-in after prewarm gives the pipeline time to settle while
    // keeping the tap-to-play feel responsive. Maximum stays at 300ms
    // for tracks with heavy FX chains that need more headroom.
    const startupLead = Math.min(0.3, Math.max(0.15, maxFxLatency + 0.1));
    // On a loop restart, anchor the new iteration so project-time `fromTime`
    // (loopIn) lands EXACTLY on the musical seam (the old loopOut) that the
    // rAF loop computed. This is what makes the loop "drop on beat": without
    // it, `now` was taken AFTER the variable setup awaits, so the downbeat
    // landed at a drifting, late time. Guard against the awaits overrunning
    // the restart lead so we never anchor in the past (Web Audio would drop
    // those events) — degrade to a hair after `now` instead.
    const now = (isLoopRestart && opts.anchorAudioTime)
      ? Math.max(opts.anchorAudioTime, actx.currentTime + 0.03)
      : (actx.currentTime + startupLead);
    masterStartRef.current = now;
    playheadAtRef.current  = fromTime;

    const hasSolo = tracksRef.current.some(function(t){ return t.isSoloed; });


    const scheduleClip = function (track, clip, entryNode) {
      if (clip.active === false || !clip.audioBuffer) return;
      try {
        // ── Buffer resolution: stretched, then pitched, then raw ──
        // Order matters: if the clip is BOTH stretched and pitched, we
        // prefer the stretched buffer as the playback source (more common
        // case is one or the other). Pitch will still be applied if there's
        // a pitched buffer of the stretched result cached, but that's a
        // Phase 4 chain; for now the priority is: stretched > pitched > raw.
        let buf = clip.audioBuffer;
        const pitch = clip.pitchSemitones || 0;
        // Stretched playback REQUIRES both:
        //   • clip.stretchMode is ON (user explicitly enabled it)
        //   • we have a cached stretched buffer for the current stretched duration
        // If stretch is ON but the cache miss (render hasn't finished yet),
        // we fall back to playing the RAW buffer at ITS OWN duration — NOT
        // the stretched duration. Playing the raw buffer for the stretched
        // duration causes audio to either run out early (when stretching
        // longer) or cut off (when compressing). Using raw timing keeps
        // playback intact until the stretched buffer arrives.
        const stretchActive = !!clip.stretchMode && clip.stretchedDurationSec && clip.stretchedDurationSec > 0;
        let usingStretchedBuffer = false;
        if (stretchActive) {
          const skey = clip.id + ":" + Math.round(clip.stretchedDurationSec * 1000);
          const sc = clipStretchedBuffersRef.current[skey];
          if (sc) {
            buf = sc;
            usingStretchedBuffer = true;
          }
          // If sc is missing, leave buf as the raw buffer AND set the
          // duration calc below to use the raw length — see trimE logic.
        } else if (Math.abs(pitch) > 0.005) {
          const key = clip.id + ":" + Math.round(pitch * 100);
          const cached = clipPitchedBuffersRef.current[key];
          if (cached) buf = cached;
        }
        // Effective trim end:
        //   • If we're actually playing a stretched buffer → use stretched length
        //   • Otherwise (no stretch, OR stretch missing from cache) → use raw trimEnd
        // This keeps playback aligned with whatever buffer we actually have.
        const trimS    = clip.trimStart || 0;
        const trimE    = usingStretchedBuffer
          ? (trimS + clip.stretchedDurationSec)
          : (clip.trimEnd || buf.duration);
        const trimDur  = Math.max(0, trimE - trimS);
        if (trimDur <= 0) return;
        const clipStart = clip.startTime || 0;
        const clipEnd   = clipStart + trimDur;
        if (clipEnd <= fromTime) return;

        const src = actx.createBufferSource();
        src.buffer = buf;

        // ── Per-clip gain node for fade ramps ──
        // Fades are applied via a GainNode with exponential ramps. We
        // create one whenever the clip has fadeIn or fadeOut set; otherwise
        // we connect the source directly to entryNode (saves a node).
        const fadeIn  = Math.max(0, +clip.fadeInSec  || 0);
        const fadeOut = Math.max(0, +clip.fadeOutSec || 0);
        const hasFade = fadeIn > 0 || fadeOut > 0;
        let downstreamNode;
        let fadeGain = null;
        if (hasFade) {
          fadeGain = actx.createGain();
          src.connect(fadeGain);
          fadeGain.connect(entryNode);
          downstreamNode = fadeGain;
        } else {
          src.connect(entryNode);
          downstreamNode = entryNode;
        }

        // ── PDC: pull this track's clips back in time by its FX latency ──
        // So when the audio reaches the master destination after passing
        // through the chain, it arrives at the same wall-clock moment as
        // dry tracks. `now` has already been bumped by maxFxLatency in
        // doPlay, so every track has enough headroom to subtract its own
        // latency without going below actx.currentTime.
        const cached = fxLatencyRef.current[track.id];
        const trackLatency = (cached && cached.latencySec) || 0;

        let when, bufOff;
        if (fromTime <= clipStart) { when=now+(clipStart-fromTime); bufOff=trimS; }
        else                       { when=now; bufOff=trimS+(fromTime-clipStart); }
        // Compensate for THIS track's FX chain latency
        when = when - trackLatency;
        // Safety clamp: never schedule before actx.currentTime (Web Audio errors)
        if (when < actx.currentTime) when = actx.currentTime;
        const remain = (trimS + trimDur) - bufOff;
        if (remain > 0) {
          // ── Schedule fade ramps on the gain node ──
          // Equal-power curve (sin²) sounds smoother than linear and
          // doesn't have the silence-on-min problem of exponentialRampToValueAtTime
          // (which can't ramp to/from 0). We pre-build the curve as a
          // Float32Array and use setValueCurveAtTime.
          if (fadeGain) {
            const RAMP_RES = 64; // 64-sample curve is plenty smooth, cheap to compute
            // Default to full gain throughout the clip. Then carve fade-in
            // at the head and fade-out at the tail.
            fadeGain.gain.setValueAtTime(1.0, when);
            if (fadeIn > 0) {
              // Clamp fade-in to clip duration so we never overshoot.
              const fdur = Math.min(fadeIn, remain);
              const curve = new Float32Array(RAMP_RES);
              for (let i = 0; i < RAMP_RES; i++) {
                // sin²: smooth equal-power curve from 0 to 1
                const t = i / (RAMP_RES - 1);
                const v = Math.sin((t * Math.PI) / 2);
                curve[i] = v * v;
              }
              try { fadeGain.gain.setValueCurveAtTime(curve, when, fdur); } catch(e){}
            }
            if (fadeOut > 0) {
              const fdur = Math.min(fadeOut, remain);
              const fadeStart = when + remain - fdur;
              const curve = new Float32Array(RAMP_RES);
              for (let i = 0; i < RAMP_RES; i++) {
                const t = i / (RAMP_RES - 1);
                const v = Math.cos((t * Math.PI) / 2);  // 1 → 0, equal-power
                curve[i] = v * v;
              }
              try { fadeGain.gain.setValueCurveAtTime(curve, fadeStart, fdur); } catch(e){}
            }
          }
          src.start(when, bufOff, remain);
          scheduledRef.current.push(src);
          if (fadeGain) scheduledRef.current.push(fadeGain);
        }
      } catch(e) {}
    };

    // ── Cached-chain helper ────────────────────────────────────────────────
    // Returns the cached entry node for this track if the FX signature still
    // matches; otherwise rebuilds the chain. Rebuilds also fire when the
    // AudioContext has been recreated (cache invalidated externally).
    const getOrBuildChain = function(track) {
      const cached = chainsRef.current[track.id];
      const currentSig = fxSignature(track.effects);
      // Reuse cached chain if it exists, points to the same AudioContext, and
      // the plugin on/off topology hasn't changed since it was built.
      if (cached && cached.entry && cached.entry.context === actx && cached.fxSig === currentSig) {
        // Live param state (volume/pan/mute/solo/FX knobs) gets applied separately
        // via applyFxLive / applyGains — no rebuild needed.
        return cached.entry;
      }
      // Tear down old chain fully before building a new one
      tearDownChain(track.id);
      const entry = buildChain(actx, master, track, hasSolo, { registerLive: true });
      chainsRef.current[track.id] = { entry: entry, fxSig: currentSig };
      // ── Per-track soundfont engine: rewire to the NEW entry node ──
      // When the chain topology changes (FX added/removed/toggled), the
      // entry node is a brand new AudioNode. The per-track FluidSynth
      // engine is still connected to the OLD entry (now disconnected
      // from master), so audio would go nowhere. Tell the engine to
      // reconnect to the new entry. Safe for non-MIDI tracks — they
      // don't have a per-track engine and the lookup just returns
      // undefined, no-op.
      if (track.type === "midi") {
        try {
          const eng = _bfSfEnginesByTrack.get(track.id);
          if (eng && typeof eng.reconnect === "function") {
            eng.reconnect(entry);
          }
        } catch(_){}
      }
      return entry;
    };

    tracksRef.current.forEach(function(track) {
      try {
        // Drum sequencer tracks have their own scheduler that fires below
        // this loop. We still need to build their FX chain here so the
        // entry node exists when the scheduler ticks.
        if (track.type === "drumSequencer") {
          if (!track.isMuted && (!hasSolo || track.isSoloed)) {
            try { getOrBuildChain(track); } catch(e) { console.warn("[Studio] drum chain build failed", track.name, e); }
          }
          return;  // skip the audio-clip scheduler path
        }
        // MIDI Instrument tracks: same treatment. Notes are scheduled via
        // the midiSchedulerRef tick which reads chainsRef[trackId].entry
        // for routing. Without a built chain, the MIDI scheduler skips
        // the track silently (per the !chain check in midiTick).
        if (track.type === "midi") {
          if (!track.isMuted && (!hasSolo || track.isSoloed)) {
            try { getOrBuildChain(track); } catch(e) { console.warn("[Studio] midi chain build failed", track.name, e); }
          }
          return;
        }
        const clips = track.clips && track.clips.length > 0
          ? track.clips
          : track.audioBuffer // legacy flat model
            ? [{ id:"lg", audioBuffer:track.audioBuffer, url:track.url, startTime:track.startTime||0, duration:track.audioBuffer.duration, trimStart:0, trimEnd:track.audioBuffer.duration, active:true }]
            : [];
        if (clips.length === 0) return;
        const entryNode = getOrBuildChain(track);
        clips.forEach(function(clip){
          try { scheduleClip(track, clip, entryNode); } catch(clipErr) {
            console.warn("[Studio] clip schedule error on track", track.name, clipErr);
          }
        });
      } catch(trackErr) {
        // One bad track should never stop others from playing
        console.warn("[Studio] track chain error on track", track.name, trackErr);
      }
    });

    // ── DRUM SEQUENCER live scheduler ───────────────────────────────────
    // After all audio-clip tracks are scheduled, set up a Chris Wilson
    // look-ahead scheduler for any drumSequencer tracks. The scheduler
    // ticks every DRUM_TICK_INTERVAL_MS (~25ms) and schedules any drum
    // hit whose firing time falls within the next DRUM_LOOKAHEAD_SEC
    // (~150ms). Triggers are sample-accurate because they ride on the
    // AudioContext clock, not setInterval.
    //
    // `masterStartRef.current` is the AudioContext time corresponding to
    // project time = fromTime. We use this to convert project time
    // (which is what patterns are stored in) to AudioContext time
    // (which is what BufferSourceNode.start() takes).
    //
    // Each drum track has its own cursor in drumCursorsRef. The cursor
    // is the next project time we haven't yet scheduled for that track.
    // Each tick advances the cursor by however far ahead we scheduled.
    const drumTracks = tracksRef.current.filter(function(tr){ return tr.type === "drumSequencer"; });
    if (drumTracks.length > 0) {
      // Initialize cursors at fromTime so playback starts at the right place.
      drumCursorsRef.current = {};
      drumTracks.forEach(function(tr){ drumCursorsRef.current[tr.id] = fromTime; });
      // Wipe per-clip step-index tracker — each play session starts fresh.
      drumClipStepsRef.current = {};
      // Wipe choke contexts — any source from a previous playback session is
      // either already stopped (the AudioContext was torn down) or stale, so
      // we start with a fresh map per track. The scheduler tick will
      // populate as choke-enabled channels fire.
      drumChokeContextsRef.current = {};
      drumTracks.forEach(function(tr){ drumChokeContextsRef.current[tr.id] = {}; });

      // Make sure kit buffers are loaded for every drum track before any
      // scheduling fires. Kick off loads in parallel and stash on the ref.
      // The scheduler tick will simply skip any track whose buffers
      // haven't arrived yet; once they land, scheduling proceeds normally.
      const usedKitIds = new Set();
      drumTracks.forEach(function(tr){ usedKitIds.add(tr.kitId || "default"); });
      usedKitIds.forEach(function(kid){
        if (!drumKitBuffersRef.current[kid]) {
          loadKitBuffers(actx, kid).then(function(bufs){
            drumKitBuffersRef.current[kid] = bufs;
          }).catch(function(e){ console.warn("[drum] kit load failed for", kid, e); });
        }
      });

      // Clear any leftover scheduler from a previous play session.
      if (drumSchedulerRef.current) {
        clearInterval(drumSchedulerRef.current);
        drumSchedulerRef.current = null;
      }

      // The tick function. Reads current AudioContext time, converts to
      // project time, and walks each drum track's clips to schedule any
      // hits in the lookahead window.
      const tick = function() {
        if (!isPlayingRef.current) {
          // Defensive: if we're no longer playing for any reason, stop ticking.
          if (drumSchedulerRef.current) {
            clearInterval(drumSchedulerRef.current);
            drumSchedulerRef.current = null;
          }
          return;
        }
        const acx = actxRef.current;
        if (!acx) return;
        // Project time = how far into the project we are right now in live time.
        const projectNow = fromTime + (acx.currentTime - masterStartRef.current);
        const scheduleUntilProjectTime = projectNow + DRUM_LOOKAHEAD_SEC;

        // For each drum track that's playable, schedule its hits.
        const liveTracks = tracksRef.current;
        const anySolo = liveTracks.some(function(tr){ return tr.isSoloed; });
        for (const tr of liveTracks) {
          if (tr.type !== "drumSequencer") continue;
          if (tr.isMuted) continue;
          if (anySolo && !tr.isSoloed) continue;
          const buffers = drumKitBuffersRef.current[tr.kitId || "default"];
          if (!buffers) continue;  // kit not loaded yet — skip silently
          // Get this track's FX-chain entry node from the cached chains.
          const chain = chainsRef.current[tr.id];
          if (!chain || !chain.entry) continue;
          const entryNode = chain.entry;
          // PDC: shift drum hits earlier by the track's FX-chain latency
          // so they arrive at the master at the right wall-clock moment.
          const cached = fxLatencyRef.current[tr.id];
          const trackLatency = (cached && cached.latencySec) || 0;
          // Start scheduling from where this track's cursor left off.
          // Where we last left off scheduling. If main thread stalled,
          // projectNow may be far past the cursor — we must NOT just resume
          // from the stale cursor (we'd schedule events in the past, which
          // Web Audio drops). Clamp cursor up to projectNow if it fell behind.
          let cursorProject = drumCursorsRef.current[tr.id];
          let cursorWasClamped = false;
          if (cursorProject == null || cursorProject < projectNow) {
            cursorProject = projectNow;
            cursorWasClamped = true;
          }
          // If we just clamped the cursor forward (main thread stalled),
          // invalidate per-clip step trackers for this track so the walker
          // re-seeds from the new windowStart. Otherwise we'd iterate from
          // a stale stepIdx through every past step (each gets filtered by
          // the `when < currentTime` check, but the iteration burns CPU).
          if (cursorWasClamped) {
            const stepMap = drumClipStepsRef.current;
            const prefix = tr.id + ":";
            const keys = Object.keys(stepMap);
            for (let ki = 0; ki < keys.length; ki++) {
              if (keys[ki].indexOf(prefix) === 0) delete stepMap[keys[ki]];
            }
          }
          // The lookahead window in project time we need to cover this tick.
          const windowStart = cursorProject;
          const windowEnd   = scheduleUntilProjectTime;
          if (windowStart >= windowEnd) continue;

          // Build padId -> self-choke flag for THIS track. Re-computed each
          // tick because the channel-level chokeEnabled flag can change at
          // any time. The work is tiny (~8-32 pads) so per-tick rebuild
          // is fine. Self-choke means an 808 cuts the previous 808, a
          // hi-hat cuts the previous hi-hat, etc. — matches FL Studio's
          // "Cut Itself" toggle. We use the padId itself as the choke key.
          const padSelfChokeLive = {};
          (tr.patterns || []).forEach(function(_pat){
            (_pat.channels || []).forEach(function(_ch){
              if (_ch.chokeEnabled && _ch.padId) {
                padSelfChokeLive[_ch.padId] = true;
              }
            });
          });
          // Lazily allocate this track's choke context if not yet present.
          // Lives across ticks so a hit scheduled now can be choked by a
          // hit scheduled next tick.
          if (!drumChokeContextsRef.current[tr.id]) {
            drumChokeContextsRef.current[tr.id] = {};
          }
          const chokeContextLive = drumChokeContextsRef.current[tr.id];

          // Walk every pattern clip and schedule hits that fall in the window.
          // Rack-overlay virtual clip removed: opening the rack no longer
          // changes scheduling. Real clips on the timeline always play.
          // This eliminates the double-fire seam at rack open/close (where
          // already-scheduled real-clip hits would overlap with new
          // virtual-overlay hits, producing random kick distortion).
          // Producers who want to hear an unplaced pattern can use PAT to
          // loop it; SONG plays whatever's on the timeline.
          const clips = tr.clips || [];
          for (const clip of clips) {
            if (clip.active === false) continue;
            const pat = findPatternInTrack(tr, clip.patternId);
            if (!pat) continue;
            const stepSec = drumStepDurationSec(bpm, pat.stepsPerBeat);
            if (stepSec <= 0) continue;
            const clipStart = clip.startTime || 0;
            const clipDur   = clip.duration || (pat.lengthSteps * stepSec);
            const clipEnd   = clipStart + clipDur;
            // Quick reject: clip is entirely before or after our window.
            if (clipEnd <= windowStart) continue;
            if (clipStart >= windowEnd) continue;
            // Walk steps that land inside the window AND inside this clip.
            const eStart = Math.max(clipStart, windowStart);
            const eEnd   = Math.min(clipEnd, windowEnd);
            if (eStart >= eEnd) continue;
            // Per-clip "next step to schedule" state. The previous version
            // recomputed stepIdx from a fuzzy projectTime each tick, which
            // caused boundary double-fires when the same canonical step
            // could be both < windowEnd in tick A AND ≥ windowStart in
            // tick B due to floating-point fuzz. The state-based approach
            // is bulletproof: we save the next step we expect to schedule
            // and walk forward from there, no boundary math.
            const clipKey = tr.id + ":" + clip.id;
            let stepIdx;
            if (drumClipStepsRef.current[clipKey] != null) {
              // Resume from where the previous tick left off.
              stepIdx = drumClipStepsRef.current[clipKey];
            } else {
              // First encounter with this clip — seed from windowStart.
              const offsetIntoClip = eStart - clipStart;
              stepIdx = Math.floor(offsetIntoClip / stepSec);
              if (clipStart + stepIdx * stepSec < eStart - 1e-9) stepIdx++;
            }
            // Walk forward until we exceed the clip's window or pattern wraps end.
            // Compute pattern-wide mute/solo gates once per clip (cheap; saves
            // repeating the lookup per step).
            const anySoloed = (pat.channels || []).some(function(c){ return c.soloed; });
            while (true) {
              const projectTime = clipStart + stepIdx * stepSec;
              if (projectTime >= eEnd) break;
              for (const ch of (pat.channels || [])) {
                if (anySoloed && !ch.soloed) continue;
                if (!anySoloed && ch.muted) continue;
                // Skip notes-mode channels in the step walk — they're
                // scheduled separately in the notes walk below.
                if (drumChannelModeOf(ch) === "notes") continue;
                if (!ch.steps) continue;
                // Per-channel length: each channel wraps modulo ITS OWN
                // step count for true polyrhythm. Falls back to pattern
                // length when the channel doesn't define one.
                const chLen = channelLengthSteps(ch, pat);
                const chStepIdx = ((stepIdx % chLen) + chLen) % chLen;
                const rawStep = ch.steps[chStepIdx];
                const params = stepParams(rawStep);
                if (params.vel < DRUM_MIN_VELOCITY) continue;
                const buf = buffers[ch.padId];
                if (!buf) continue;
                // Per-channel swing offsets even (1-indexed) 16ths later +
                // per-step shift adjusts microtiming within the step.
                const swingOff = drumStepSwingOffset(chStepIdx, ch.swing, stepSec);
                const shiftOff = params.shift * stepSec;
                // Convert project time to AudioContext time, apply PDC + swing + shift.
                const when = masterStartRef.current + (projectTime - fromTime) - trackLatency + swingOff + shiftOff;
                // Safety: never schedule before currentTime (Web Audio throws).
                if (when < acx.currentTime) continue;
                // scheduleStepHits handles ratchet (repeat>1) by firing N
                // sub-hits across the step duration with decaying velocity.
                // Self-choke: key = padId when this channel has chokeEnabled,
                // so a new hit cuts only the PREVIOUS hit on the same pad.
                const chokeKeyLive = padSelfChokeLive[ch.padId] ? ch.padId : null;
                scheduleStepHits(acx, entryNode, buf, when, stepSec, params,
                  ch.volume == null ? 1 : ch.volume, scheduledRef.current,
                  chokeContextLive, chokeKeyLive);
              }
              stepIdx++;
            }
            // Save the next step index for the next tick. stepIdx is now
            // pointing at the step we would have scheduled IF the window
            // were larger — so it's the right starting point next time.
            drumClipStepsRef.current[clipKey] = stepIdx;
            // ── Notes-mode channel walk ──
            // After the step-grid walk, schedule every note in any channel
            // that's been "sent to piano roll" (mode = "notes"). Same window
            // [eStart, eEnd) in project time. Each note carries pitch +
            // duration + velocity; pitch != root pitch re-pitches the sample
            // via playbackRate so producers can write pitched-sample melodies
            // from a single pad.
            const patDur = pat.lengthSteps * stepSec;
            for (const ch of (pat.channels || [])) {
              if (drumChannelModeOf(ch) !== "notes") continue;
              if (anySoloed && !ch.soloed) continue;
              if (!anySoloed && ch.muted) continue;
              const buf = buffers[ch.padId];
              if (!buf) continue;
              const notes = mergeSlideVoices(ch.notes || []).slice().sort(function(a, b){
                return (a.start || 0) - (b.start || 0);
              });
              if (notes.length === 0) continue;
              // Tempo scale: note start/duration were authored in seconds at
              // ch.notesBpm. The step grid scales with the live bpm via stepSec,
              // so the melody must scale by the same factor to stay locked.
              // factor = authoredBpm / liveBpm  (matches stepSec's 1/bpm scaling).
              const notesTempoScale = (ch.notesBpm && ch.notesBpm > 0) ? (ch.notesBpm / bpm) : 1;
              const chokeKeyLive = padSelfChokeLive[ch.padId] ? ch.padId : null;
              // Walk pattern loop iterations that overlap the clip window.
              // For each iteration k, the pattern's local time 0 corresponds
              // to project time (clipStart + k * patDur).
              const kStart = Math.floor((eStart - clipStart) / patDur);
              const kEnd   = Math.floor((eEnd   - clipStart) / patDur);
              for (let k = kStart; k <= kEnd; k++) {
                if (k < 0) continue;
                const loopBase = clipStart + k * patDur;
                if (loopBase >= clipEnd) break;
                for (let ni = 0; ni < notes.length; ni++) {
                  const n = notes[ni];
                  const projectTime = loopBase + (n.start || 0) * notesTempoScale;
                  if (projectTime < eStart) continue;
                  if (projectTime >= eEnd) continue;
                  if (projectTime >= clipEnd) continue;
                  const when = masterStartRef.current + (projectTime - fromTime) - trackLatency;
                  if (when < acx.currentTime) continue;
                  // Re-pitch relative to root. scheduleDrumHit takes pitch
                  // in SEMITONES (it does the 2^(s/12) conversion itself),
                  // so we pass the delta directly. At root pitch, semis=0
                  // → playbackRate stays at 1.
                  const semis = (n.pitch || DRUM_CHANNEL_ROOT_PITCH) - DRUM_CHANNEL_ROOT_PITCH;
                  const rawVel = n.velocity == null ? 0.85 : n.velocity;
                  // FL-style silent cut: a note with velocity EXACTLY 0
                  // doesn't make sound but DOES kill any still-ringing
                  // previous hit on the same pad (when choke is enabled).
                  // Producers drag velocity all the way down where they
                  // want an 808 tail to die. Low-but-non-zero velocities
                  // still play as quiet hits — they don't trigger the cut.
                  if (rawVel === 0) {
                    if (chokeKeyLive && chokeContextLive) {
                      const prev = chokeContextLive[chokeKeyLive];
                      if (prev && prev.gain && prev.src) {
                        try {
                          const cutStart = Math.max(when - DRUM_CHOKE_RAMP_SEC, prev.when || 0);
                          prev.gain.gain.cancelScheduledValues(cutStart);
                          prev.gain.gain.setValueAtTime(prev.gain.gain.value, cutStart);
                          prev.gain.gain.linearRampToValueAtTime(0, when);
                          prev.src.stop(when + 0.001);
                        } catch(_){ /* node may already be stopped */ }
                        // Clear the slot so the next real note doesn't
                        // try to cut a node that's already been stopped.
                        chokeContextLive[chokeKeyLive] = null;
                      }
                    }
                    continue;
                  }
                  const vel = Math.max(0.001, Math.min(1.5, rawVel));
                  // scheduleDrumHit fires a single sample-accurate BufferSource.
                  // Returns {src, gain} so applyChoke can ramp the previous
                  // hit on this pad to 0 (matching FL's "Cut Itself" toggle).
                  const glideCents = (n.glide && n.glide.length)
                    ? n.glide.map(function(seg){ return {
                        at: (seg.at || 0) * notesTempoScale,
                        cents: (seg.toPitch - (n.pitch || DRUM_CHANNEL_ROOT_PITCH)) * 100,
                        dur: Math.max(0.01, (seg.dur || 0.05) * notesTempoScale),
                      }; })
                    : null;
                  const hit = scheduleDrumHit(acx, entryNode, buf, when,
                    vel * (ch.volume == null ? 1 : ch.volume),
                    semis, 0, chokeKeyLive, glideCents);
                  if (hit && chokeKeyLive && chokeContextLive) {
                    applyChoke(chokeContextLive, chokeKeyLive, hit, when);
                  }
                  if (hit && hit.src) scheduledRef.current.push(hit.src);
                }
              }
            }
          }
          // Advance cursor to the end of the window we just scheduled.
          drumCursorsRef.current[tr.id] = windowEnd;
        }
      };

      // Fire one immediate tick so initial hits are scheduled before the
      // first interval fires (otherwise we'd wait 25ms which can be audible).
      tick();
      drumSchedulerRef.current = setInterval(tick, DRUM_TICK_INTERVAL_MS);
    }

    // ── MIDI live scheduler ──
    // Same architecture as the drum scheduler: walk every MIDI Instrument
    // track each tick and schedule any notes whose project times fall in
    // the lookahead window into the audio graph. Each scheduled note
    // becomes a self-contained voice (osc + filter + ADSR + master gain)
    // that auto-releases at note-off.
    const midiTracks = tracksRef.current.filter(function(tr){ return tr.type === "midi"; });
    if (midiTracks.length > 0) {
      midiCursorsRef.current = {};
      midiTracks.forEach(function(tr){ midiCursorsRef.current[tr.id] = fromTime; });
      // Wipe any leftover voices from the previous session — they were
      // attached to the OLD AudioContext, which is gone.
      midiVoicesRef.current = {};
      midiTracks.forEach(function(tr){ midiVoicesRef.current[tr.id] = []; });

      if (midiSchedulerRef.current) {
        clearInterval(midiSchedulerRef.current);
        midiSchedulerRef.current = null;
      }

      const midiTick = function() {
        if (!isPlayingRef.current) {
          if (midiSchedulerRef.current) {
            clearInterval(midiSchedulerRef.current);
            midiSchedulerRef.current = null;
          }
          return;
        }
        // Wrap the entire tick body — a throw anywhere inside (null
        // property access on a malformed track, a synth method error,
        // an unexpected state shape) would crash up into setInterval,
        // browser logs it, and on iOS sometimes manifests as the
        // Studio's error boundary catching a stale state and showing
        // the crash screen. Defensive try/catch keeps the scheduler
        // alive and lets the next tick attempt to recover.
        try {
          const acx = actxRef.current;
          if (!acx) return;
          const projectNow = fromTime + (acx.currentTime - masterStartRef.current);
          const scheduleUntilProjectTime = projectNow + MIDI_LOOKAHEAD_SEC;

          const liveTracks = tracksRef.current;
          const anySolo = liveTracks.some(function(tr){ return tr.isSoloed; });
          for (const tr of liveTracks) {
            // Per-track try/catch — one malformed track must never
            // prevent others from scheduling. The most common cause is
            // a track added mid-playback whose chain hasn't been
            // built yet — we silently skip it this tick.
            try {
              if (tr.type !== "midi") continue;
              if (tr.isMuted) continue;
              if (anySolo && !tr.isSoloed) continue;
              const chain = chainsRef.current[tr.id];
              if (!chain || !chain.entry) continue;
              const entryNode = chain.entry;
              // PDC: shift notes earlier by the track's FX-chain latency.
              const cached = fxLatencyRef.current[tr.id];
              const trackLatency = (cached && cached.latencySec) || 0;

              let cursorProject = midiCursorsRef.current[tr.id];
              if (cursorProject == null || cursorProject < projectNow) {
                cursorProject = projectNow;
              }
              const windowStart = cursorProject;
              const windowEnd   = scheduleUntilProjectTime;
              if (windowStart >= windowEnd) continue;

              // Resolve the per-voice preset: base preset + any track-level
              // ADSR/filter overrides the producer has dialed in. Re-computed
              // each tick because overrides can change live (slider drag).
              // SAMPLER tracks are special: their "preset" data lives on the
              // track itself (samplerData), not in BF_INSTRUMENTS. We build
              // a synthetic inst object that the dispatcher can route.
              //
              // resolveTrackInstrumentMeta applies the track's
              // selectedSfPreset override for multi-preset banks — so the
              // timeline plays whatever preset the producer picked in the
              // piano-roll sub-picker, not the bank's default sound.
              let inst;
              if (tr.samplerData) {
                inst = resolveSamplerInst(tr);
                // Preload the sample if not in cache yet. First few transport
                // ticks may silently miss notes while the fetch+decode runs.
                ensureSamplerLoaded(tr, acx);
              } else {
                const baseInst = resolveTrackInstrumentMeta(tr);
                inst = applyInstrumentOverrides(baseInst, tr.instrumentOverrides);
              }

              // If this track uses a soundfont preset, fire-and-forget a
              // preload. The first iteration of the transport when a new
              // soundfont preset is selected will silently miss notes while
              // the .sf3 fetch is in flight; subsequent iterations play.
              // Calling this every tick is cheap because loadPreset is
              // idempotent and bails immediately if already loaded.
              if (inst && inst.engine === "soundfont-v1") {
                ensureSoundfontPresetLoaded(inst, acx, entryNode, tr.id);
              }

              // Walk each clip on this track. midiClipNotesInWindow handles
              // looping internally so we just collect what fires in the window.
              const clips = tr.clips || [];
              const voices = midiVoicesRef.current[tr.id] || (midiVoicesRef.current[tr.id] = []);
              for (const clip of clips) {
                if (clip.active === false) continue;
                const notes = midiClipNotesInWindow(clip, windowStart, windowEnd);
                for (let i = 0; i < notes.length; i++) {
                  // Polyphony cap — if a track is already at the cap, sacrifice
                  // the oldest still-active voice. Producer would rather
                  // hear the new note than a stuck old one.
                  while (voices.length >= MIDI_POLYPHONY_CAP) {
                    const oldest = voices.shift();
                    if (oldest && oldest.stop) {
                      try { oldest.stop(acx.currentTime); } catch(_) {}
                    }
                  }
                  const n = notes[i];
                  const when = masterStartRef.current + (n.projectTime - fromTime) - trackLatency;
                  if (when < acx.currentTime) continue;
                  let voice = null;
                  try {
                    voice = scheduleMidiNote(acx, entryNode, inst, n.pitch, n.velocity, when, n.duration, tr.noteCutMode, tr.id, n.glide);
                  } catch(noteErr) {
                    // Single-note scheduling failure must never kill the
                    // tick. Most common cause: synth method throws on a
                    // half-loaded preset. Skip and continue.
                  }
                  if (voice) voices.push(voice);
                }
              }
              midiCursorsRef.current[tr.id] = windowEnd;

              // Garbage-collect expired voices so the array doesn't grow without
              // bound. A voice is expired once its endsAt is before now.
              const nowCt = acx.currentTime;
              const alive = [];
              for (let v = 0; v < voices.length; v++) {
                if (voices[v].endsAt > nowCt) alive.push(voices[v]);
              }
              midiVoicesRef.current[tr.id] = alive;
            } catch(trackErr) {
              try { console.warn("[midiTick] track error on", tr && tr.name, trackErr && trackErr.message); } catch(_){}
            }
          }
        } catch(tickErr) {
          try { console.warn("[midiTick] tick body error", tickErr && tickErr.message); } catch(_){}
        }
      };

      midiTick();
      midiSchedulerRef.current = setInterval(midiTick, MIDI_TICK_INTERVAL_MS);
    }
  };

  const togglePlay = async function () {
    if (isPlaying) {
      // If actively recording, pause should ALSO stop the recording.
      // Previously: pause stopped only the beat playback and left the
      // recorder running, silently capturing nothing useful.
      if (isRecording) {
        stopRecording();
        return;  // stopRecording handles its own UI state + stops playback
      }
      // Capture exact live position before tearing down audio graph
      const exactT = liveTimeRef.current > 0 ? liveTimeRef.current : currentTime;
      stopAll();
      setIsPlaying(false);
      // Freeze everything — playhead stays exactly where audio stopped
      syncUItoTime(exactT);
    } else {
      // CRITICAL: force AudioContext resume INSIDE the user-gesture click handler.
      // Browsers (esp. iOS Safari, recent Chrome) require this on every play attempt
      // and the silent `.catch()` inside getActx() was hiding failures, leading to a
      // playing UI with no audio. Surface failures so they're visible in DevTools.
      try {
        const ctx = getActx();
        if (ctx && ctx.state === "suspended") {
          await ctx.resume();
        }
        // After resume, if it's STILL suspended, that's an autoplay-policy failure.
        if (ctx && ctx.state !== "running") {
          console.warn("[Studio] AudioContext failed to resume — state:", ctx.state);
        }
      } catch (resumeErr) {
        console.error("[Studio] AudioContext.resume() threw:", resumeErr);
      }
      // Resume from the synced position — playheadAtRef was written by syncUItoTime.
      // BUT: if loop is enabled and the playhead isn't already inside the loop
      // region, snap to loopIn so playback starts at the loop's beginning.
      // Users expect "loop + play" to begin at the loop start, not from
      // wherever the playhead happened to be.
      let startT = playheadAtRef.current;
      if (loopEnabled && loopOut > loopIn) {
        if (startT < loopIn || startT >= loopOut) {
          startT = loopIn;
          syncUItoTime(startT);  // pre-position the playhead UI too
        }
      }
      doPlay(startT);
      setIsPlaying(true);
    }
  };

  const rewind = function () {
    stopAll();
    setIsPlaying(false);
    masterStartRef.current = 0;
    const t = loopEnabled ? loopIn : 0;
    // Always scroll back to start so the ruler shows bar 1
    if (scrollRef.current) scrollRef.current.scrollLeft = 0;
    syncUItoTime(t);
  };

  const skipToEnd = function () {
    const furthest = tracks.reduce(function(acc, t) {
      const clips = t.clips || (t.audioBuffer ? [{startTime:t.startTime||0, duration:t.audioBuffer.duration, trimEnd:t.audioBuffer.duration}] : []);
      const trackEnd = clips.reduce(function(tAcc, cl) {
        const end = (cl.startTime||0) + ((cl.trimEnd||cl.duration||0) - (cl.trimStart||0));
        return end > tAcc ? end : tAcc;
      }, 0);
      return trackEnd > acc ? trackEnd : acc;
    }, 0);
    const endT = loopEnabled && loopOut > loopIn ? loopOut : furthest || 0;
    syncUItoTime(endT);
  };

  const SEEK_STEP = 5; // seconds per tap

  const seekBack = function () {
    const wasPlaying = isPlayingRef.current;
    const curT = liveTimeRef.current > 0 ? liveTimeRef.current : playheadAtRef.current || 0;
    const newT = Math.max(0, curT - SEEK_STEP);
    syncUItoTime(newT);
    if (wasPlaying) {
      setIsPlaying(false);
      doPlay(newT).then(function() { setIsPlaying(true); });
    }
  };

  const seekForward = function () {
    const wasPlaying = isPlayingRef.current;
    const curT = liveTimeRef.current > 0 ? liveTimeRef.current : playheadAtRef.current || 0;
    const newT = curT + SEEK_STEP;
    syncUItoTime(newT);
    if (wasPlaying) {
      setIsPlaying(false);
      doPlay(newT).then(function() { setIsPlaying(true); });
    }
  };

  const stopRecording = function () {
    clearInterval(recIntRef.current);
    clipIdRef.current = null;
    if (mediaRecRef.current && mediaRecRef.current.state !== "inactive") {
      // Stamp the actx clock NOW — onstop uses it for clip timing.
      // Do NOT call stopAll() here: onstop runs async and needs actx + masterStartRef
      // intact to decode audio and compute clipStartTime correctly.
      // stopAll + setIsPlaying(false) are called at the END of the onstop handler instead.
      const actx = actxRef.current;
      recStopActxTimeRef.current = actx ? actx.currentTime : null;
      mediaRecRef.current.stop();
      mediaRecRef.current = null;
    } else {
      mediaRecRef.current = null;
      stopAll();
      setIsPlaying(false);
    }
  };

  // ── Ruler tap/drag → set loop in/out points ONLY ─────────────
  // Tapping the ruler only creates/adjusts the loop region.
  // It never seeks the playhead or interrupts playback.
  // First tap on empty area: sets loop-in, second tap to the right: sets loop-out.
  // Dragging: if tap lands within 0.3s of loopIn → drag loopIn,
  //           if tap lands within 0.3s of loopOut → drag loopOut,
  //           otherwise start a new loop region from that point.
  // rulerDragRef declared at top of component (Rules of Hooks)
  const rulerTimeFromClientX = function (clientX) {
    const el = scrollRef.current;
    if (!el) return 0;
    const rect  = el.getBoundingClientRect();
    // scrollRef is now the right-side lanes only (no sidebar) so no SIDEBAR_W offset needed
    const laneX = clientX - rect.left + el.scrollLeft;
    return Math.max(0, laneX / effectivePPS);
  };

  // Ruler snaps to the nearest BEAT boundary (not just bars).
  // This allows loop regions to start/end on any individual beat,
  // making 1-bar, 2-bar, half-bar loops all equally easy to set.
  const snapToBar = function (t) {
    if (spb <= 0) return Math.max(0, t);
    // Snap to nearest beat
    const snappedBeat = Math.max(0, Math.round(t / spb) * spb);
    return snappedBeat;
  };

  const handleRulerMouseDown = function (e) {
    e.preventDefault();
    // Pause RAF auto-scroll for the duration of the click — same reason
    // as the touch handler. While playing, the RAF loop's per-frame
    // scrollLeft writes can disrupt click handling. Cleared on mouseup.
    userScrolledAt.current = performance.now();
    const raw = rulerTimeFromClientX(e.clientX);
    const t   = snapToBar(raw);
    // Grab threshold: half a beat (so handles feel magnetic near beat boundaries)
    const grabThresh = Math.max(0.15, spb * 0.5);
    let mode = "new";
    if (Math.abs(raw - loopIn)  < grabThresh) mode = "in";
    else if (Math.abs(raw - loopOut) < grabThresh) mode = "out";
    // Move-region: pressed inside the loop bar (not on its edges) → drag whole region
    else if (loopOut > loopIn && raw > loopIn + grabThresh && raw < loopOut - grabThresh) {
      mode = "moveLoop";
    }
    rulerDragRef.current = {
      mode,
      startX: e.clientX,
      startT: t,
      rawT: raw,  // unsnapped — used by tap-to-seek for precise click positioning
      startIn: loopIn,
      duration: loopOut - loopIn,
      committed: mode !== "new",
    };

    // Do NOT auto-create loop region on mousedown for empty areas — wait for drag
    const onMove = function (me) {
      const nt = snapToBar(rulerTimeFromClientX(me.clientX));
      if (!rulerDragRef.current) return;
      const dx = Math.abs(me.clientX - rulerDragRef.current.startX);
      // Commit new loop only once user has dragged at least 4px
      if (!rulerDragRef.current.committed && dx > 4) {
        rulerDragRef.current.committed = true;
        if (rulerDragRef.current.mode === "new") { setLoopIn(rulerDragRef.current.startT); setLoopOut(snapToBar(rulerDragRef.current.startT + spb)); }
      }
      if (!rulerDragRef.current.committed) return;
      if (rulerDragRef.current.mode === "moveLoop") {
        // Shift the entire region left/right by the drag delta, snapped to bars.
        const dtMove = (me.clientX - rulerDragRef.current.startX) / effectivePPS;
        const newIn  = Math.max(0, snapToBar(rulerDragRef.current.startIn + dtMove));
        setLoopIn(newIn);
        setLoopOut(newIn + rulerDragRef.current.duration);
      } else if (rulerDragRef.current.mode === "in")  { setLoopIn(Math.min(nt, loopOut - spb)); }
      else if (rulerDragRef.current.mode === "out") { setLoopOut(Math.max(nt, loopIn + spb)); }
      else {
        if (nt > rulerDragRef.current.startT) setLoopOut(Math.max(nt, rulerDragRef.current.startT + spb));
        else { setLoopIn(Math.min(nt, rulerDragRef.current.startT)); setLoopOut(rulerDragRef.current.startT); }
      }
    };
    const onUp = function (ue) {
      // Tap-to-seek: fires when the press never committed to a drag AND
      // it landed in "new" mode (not on a handle, not inside the loop
      // region). Works regardless of whether LOOP is enabled — only the
      // mode of the original press matters.
      const drag = rulerDragRef.current;
      const movedFar = drag && Math.abs((ue && ue.clientX) - drag.startX) > 4;
      const wasTap = drag && !drag.committed && !movedFar && drag.mode === "new";
      if (wasTap) {
        const wasPlaying = isPlayingRef.current;
        // Snap to the nearest beat line so taps land cleanly on the
        // grid for precision cutting. drag.startT is already snapped
        // (snapToBar of the raw tap position). Fallback to rawT only
        // if startT wasn't set for some reason.
        const seekT = (typeof drag.startT === "number") ? drag.startT : drag.rawT;
        syncUItoTime(seekT);
        if (wasPlaying) {
          setIsPlaying(false);
          doPlay(seekT).then(function() { setIsPlaying(true); });
        }
      }
      rulerDragRef.current = null;
      // Resume RAF auto-scroll — see handleRulerMouseDown for context.
      userScrolledAt.current = null;
      document.removeEventListener("mousemove", onMove);
      document.removeEventListener("mouseup",   onUp);
    };
    document.addEventListener("mousemove", onMove);
    document.addEventListener("mouseup",   onUp);
  };

  const handleRulerTouchStart = function (e) {
    e.preventDefault();
    e.stopPropagation();
    // PAUSE AUTO-SCROLL DURING THE GESTURE
    // The RAF playback loop writes `scrollRef.current.scrollLeft = ...`
    // on every frame to keep the playhead pinned at 75% of the viewport.
    // iOS Safari treats those programmatic scroll mutations as scroll
    // activity and cancels any touch sequence in progress, which means
    // tap-to-seek silently fails while audio is playing. Setting
    // userScrolledAt to "now" reuses the existing manual-scroll-pause
    // mechanism: the RAF loop checks this ref every frame and skips
    // auto-scroll while it's non-null. We clear it in touchend so
    // auto-scroll resumes after the gesture completes.
    userScrolledAt.current = performance.now();
    const raw = rulerTimeFromClientX(e.touches[0].clientX);
    const t   = snapToBar(raw);
    const grabThresh = Math.max(0.15, spb * 0.5);
    let mode = "new";
    // Only classify the tap as a loop-region interaction if the loop
    // is actually enabled. When loop is off, loopIn/loopOut may still
    // hold leftover values (e.g. 0 and a large number from a previous
    // session) — without this guard, ANY tap was being classified as
    // "moveLoop" because it fell inside the invisible region, which
    // meant tap-to-seek silently never fired.
    if (loopEnabled) {
      if (Math.abs(raw - loopIn)  < grabThresh) mode = "in";
      else if (Math.abs(raw - loopOut) < grabThresh) mode = "out";
      // ── Move-region: touch landed strictly between the handles ──
      // (and there IS a region to move, i.e. loopOut > loopIn).
      // Drags the entire loop bar left/right, preserving its length.
      else if (loopOut > loopIn && raw > loopIn + grabThresh && raw < loopOut - grabThresh) {
        mode = "moveLoop";
      }
    }
    rulerDragRef.current = {
      mode,
      startT: t,
      // rawT is the UNSNAPPED position — used by tap-to-seek so the
      // playhead lands exactly where you tapped, not at the nearest
      // beat. Loop-region operations still use the snapped startT
      // because beat-aligned loops are the right default for those.
      rawT: raw,
      startX: e.touches[0].clientX,
      startY: e.touches[0].clientY,
      // For moveLoop we need to remember the original region so we can
      // shift it by exactly the drag delta.
      startIn:   loopIn,
      duration:  loopOut - loopIn,
      // Edge grabs + moveLoop both commit immediately — only "new" waits
      // for ≥4px movement.
      committed: mode !== "new",
    };
  };

  const handleRulerTouchMove = function (e) {
    e.preventDefault();
    e.stopPropagation();
    if (!rulerDragRef.current) return;
    const nt = snapToBar(rulerTimeFromClientX(e.touches[0].clientX));
    const dx = Math.abs(e.touches[0].clientX - (rulerDragRef.current.startX || 0));
    if (!rulerDragRef.current.committed && dx > 4) {
      rulerDragRef.current.committed = true;
      if (rulerDragRef.current.mode === "new") { setLoopIn(rulerDragRef.current.startT); setLoopOut(snapToBar(rulerDragRef.current.startT + spb)); }
    }
    if (!rulerDragRef.current.committed) return;
    if (rulerDragRef.current.mode === "moveLoop") {
      const dtMove = (e.touches[0].clientX - rulerDragRef.current.startX) / effectivePPS;
      const newIn = Math.max(0, snapToBar(rulerDragRef.current.startIn + dtMove));
      setLoopIn(newIn);
      setLoopOut(newIn + rulerDragRef.current.duration);
    } else if (rulerDragRef.current.mode === "in")  { setLoopIn(Math.min(nt, loopOut - spb)); }
    else if (rulerDragRef.current.mode === "out") { setLoopOut(Math.max(nt, loopIn + spb)); }
    else {
      if (nt > rulerDragRef.current.startT) setLoopOut(Math.max(nt, rulerDragRef.current.startT + spb));
      else { setLoopIn(Math.min(nt, rulerDragRef.current.startT)); setLoopOut(rulerDragRef.current.startT); }
    }
  };

  // Touch end on the ruler — tap-to-seek for non-loop mode. When the user
  // releases without having dragged (no commit, minimal movement), and the
  // loop is off, jump the playhead to where they tapped. Continues playback
  // from the new position if a track was playing.
  //
  // Also defensively checks if the released touch position is far from the
  // initial press — touch sequences on iOS sometimes don't trigger
  // touchmove for small vertical drift but still indicate a scroll intent,
  // so we treat any release more than 10px away in either axis as a drag,
  // not a tap.
  const handleRulerTouchEnd = function (e) {
    const drag = rulerDragRef.current;
    // Tap-to-seek fires when:
    //   - The press never committed to a drag (no movement), AND
    //   - The press landed in "new" mode (i.e. NOT on a loop handle and
    //     NOT inside the loop region — those modes have their own
    //     behaviours: resize / move).
    if (drag && !drag.committed && drag.mode === "new") {
      // Widened deadband — finger jitter on iOS regularly registers
      // 5–9px of drift even on a clean tap, which was killing taps.
      // 10px in both axes is a much more forgiving threshold and
      // matches what UIKit uses for cancelsTouchesInView.
      let movedFar = false;
      try {
        const tch = (e.changedTouches && e.changedTouches[0]) || null;
        if (tch && drag.startX !== undefined && drag.startY !== undefined) {
          const dx = Math.abs(tch.clientX - drag.startX);
          const dy = Math.abs(tch.clientY - drag.startY);
          movedFar = dx > 10 || dy > 10;
        }
      } catch (err) { /* fall through with movedFar=false */ }

      if (!movedFar) {
        const wasPlaying = isPlayingRef.current;
        // Snap the seek to the nearest beat line (matches the ruler's
        // own snap) so the playhead lands exactly on a grid boundary
        // for precision cutting — never in dead space between lines.
        // drag.startT is the beat-snapped tap position.
        const seekT = (typeof drag.startT === "number") ? drag.startT : drag.rawT;
        syncUItoTime(seekT);
        if (wasPlaying) {
          setIsPlaying(false);
          doPlay(seekT).then(function() { setIsPlaying(true); });
        }
      }
    }
    rulerDragRef.current = null;
    // RESUME AUTO-SCROLL
    // touchstart paused it by setting userScrolledAt. After tap-to-seek
    // we want the playhead-follow behaviour to resume immediately so the
    // newly-seeked position pins correctly. Clearing the ref tells the
    // RAF loop's auto-scroll branch (line ~21796) it can run again.
    userScrolledAt.current = null;
  };

  // Attach the ruler's touch handlers IMPERATIVELY with passive:false.
  // React's onTouchStart/Move/End props are passive by default since
  // React 17, which silently disables preventDefault() inside the
  // handler. With audio loaded, the timeline content gets wide enough
  // that iOS treats the touch as a possible scroll gesture and aborts
  // the touchend's tap-to-seek path. preventDefault() on touchstart
  // resolves the ambiguity in our favour — but only works with a
  // non-passive listener. Setting touchAction:"none" via CSS is not
  // sufficient on iOS Safari in PWA mode.
  //
  // We use a "latest-handler" ref pattern so the native listeners
  // always call the current render's handler closure (which has fresh
  // state for loopIn/loopOut/effectivePPS/spb/etc.) rather than a stale
  // closure captured at mount time.
  const rulerHandlersRef = useRef({});
  rulerHandlersRef.current = {
    start:  handleRulerTouchStart,
    move:   handleRulerTouchMove,
    end:    handleRulerTouchEnd,
  };
  useEffect(function () {
    const el = rulerElRef.current;
    if (!el) return;
    const ts = function (e) { rulerHandlersRef.current.start(e); };
    const tm = function (e) { rulerHandlersRef.current.move(e); };
    const te = function (e) { rulerHandlersRef.current.end(e); };
    el.addEventListener("touchstart",  ts, { passive: false });
    el.addEventListener("touchmove",   tm, { passive: false });
    el.addEventListener("touchend",    te, { passive: false });
    el.addEventListener("touchcancel", te, { passive: false });
    return function () {
      el.removeEventListener("touchstart",  ts);
      el.removeEventListener("touchmove",   tm);
      el.removeEventListener("touchend",    te);
      el.removeEventListener("touchcancel", te);
    };
    // eslint-disable-next-line
  }, []);

  const handleScroll = function (e) {
    // Vertical scroll syncs sidebar only
    if (sidebarRef.current) sidebarRef.current.scrollTop = e.target.scrollTop;
  };

  // ── Snap ──────────────────────────────────────────────────────
  const snapSecs = function (t) {
    if (!snapToGrid) return Math.max(0, t);
    return Math.max(0, Math.round(t / spb) * spb);
  };

  // ── Track/clip helpers (clips[] model) ───────────────────────
  const defaultEffects = function () {
    return {
      reverb:     { on:false, wet:0.25, roomSize:0.8 },
      ocean:      { on:false, wet:0.35, roomSize:1.0, damp:0.6, preDelay:20 },
      eq:         { on:false, low:0, mid:0, high:0 },
      compressor: { on:false, threshold:-24, ratio:4, attack:0.003, release:0.25 },
      rcomp:      { on:false, threshold:-18, ratio:3, attack:0.012, release:0.18, makeupGain:0, mode:"warm", arc:true },
      pultec:     { on:false, lowFreq:60, lowBoost:0, lowAtten:0, hiFreq:5000, hiBoost:0, sharp:5, attenFreq:10000, hiAtten:0 },
      trotten:    { on:false, eqLow:0, eqMid:0, eqHigh:0, eqLowT:"shelf", eqMidT:"bell", eqHighT:"shelf", compThr:-15, compAmt:50, compMode:"auto", tapeDrv:5, tapeSat:5, tapeMode:"modern", limCeil:-0.5, limRel:0.5, limMode:"truepeak", inputGain:0, outputGain:0 },
      noiseremover: { on:false, threshold:-40, release:0.15, hold:0.08, hiss:0, lookahead:true },
      // ── TQ — Vocal Dynamic EQ ──────────────────────────────────────
      // 7-band dynamic EQ tuned for rap vocals. Each band has a sidechain
      // detector (bandpass + compressor) measuring the level at the band's
      // frequency. When the detector exceeds threshold, the band's gain
      // moves toward `gain` (dB). Cut bands have negative target gain;
      // boost bands have positive.
      //
      // Default state: TQ off, all bands ON internally (but plugin bypass
      // skips the entire processing block until user enables it). Frequencies
      // and Qs are tuned to where rap vocals actually have problems.
      tq: { on:false, mode:"dyneq",
        // mode "dyneq" = each band is a sidechain-driven dynamic biquad
        // mode "mb" (future) = parallel multiband compressor topology
        bands: [
          // type "cut" = reduction is negative; "boost" = positive
          // attack/release in seconds; threshold in dB
          { id:"plosive",  type:"cut",   on:true, freq:80,   q:1.4, gain:-6,  threshold:-12, attack:0.002, release:0.080, name:"PLOSIVE" },
          { id:"mud",      type:"cut",   on:true, freq:250,  q:1.0, gain:-4,  threshold:-18, attack:0.008, release:0.120, name:"MUD" },
          { id:"box",      type:"cut",   on:true, freq:600,  q:1.2, gain:-3,  threshold:-15, attack:0.005, release:0.100, name:"BOX" },
          { id:"presence", type:"boost", on:false,freq:3000, q:1.0, gain:+3,  threshold:-30, attack:0.020, release:0.200, name:"PRESENCE" },
          { id:"harsh",    type:"cut",   on:true, freq:3200, q:1.8, gain:-3,  threshold:-12, attack:0.003, release:0.080, name:"HARSH" },
          { id:"deess",    type:"cut",   on:true, freq:7000, q:2.5, gain:-6,  threshold:-14, attack:0.001, release:0.060, name:"DE-ESS" },
          { id:"air",      type:"boost", on:false,freq:12000,q:0.7, gain:+3,  threshold:-32, attack:0.030, release:0.250, name:"AIR" },
        ]
      },
    };
  };

  // Apply an FX preset to a track. Clean apply — starts from defaultEffects so
  // any plugin not mentioned in the preset is turned OFF. Triggers the FX chain
  // cache to rebuild on next Play (handled automatically by fxSignature change).
  const applyPreset = function (trackId, preset) {
    if (!preset || !preset.fx) return;
    // Build the chain ordering from the preset's plugin list. Preserve the
    // order plugins are defined in FX_PRESETS (insertion order in the fx
    // object) — this is the producer-curated processing order.
    // Only include plugins that are actually turned ON in the preset.
    const presetChain = Object.keys(preset.fx).filter(function(k) {
      return preset.fx[k] && preset.fx[k].on === true;
    });
    setTracks(function (prev) {
      return prev.map(function(t) {
        if (t.id !== trackId) return t;
        // Start from defaults so unmentioned plugins go OFF (clean apply, not additive)
        const base = defaultEffects();
        // Deep-merge preset on top
        const merged = { ...base };
        Object.keys(preset.fx).forEach(function(key) {
          merged[key] = { ...(base[key] || {}), ...preset.fx[key] };
        });
        // Set the visible plugin chain so the FX panel actually renders
        // these plugins (without this they'd be ON in audio terms but
        // invisible in the UI because the panel reads pluginChain).
        merged.pluginChain = presetChain;
        return { ...t, effects: merged };
      });
    });
    setIsSaved(false);
    // If playback is active, sync the new effects state into the live nodes
    // immediately. (Chain rebuild for newly-ON plugins happens on next Play.)
    const newEffectsForLive = (function() {
      const base = defaultEffects();
      const merged = { ...base };
      Object.keys(preset.fx).forEach(function(key) {
        merged[key] = { ...(base[key] || {}), ...preset.fx[key] };
      });
      merged.pluginChain = presetChain;
      return merged;
    })();
    applyFxLive(trackId, newEffectsForLive);
    // Sync autotune to monitoring state for live tune during recording
    if (newEffectsForLive.autotune) {
      setAutoPitch(function(prev) { return Object.assign({}, prev, newEffectsForLive.autotune); });
    }
    // Presets always change chain topology (different plugins ON/OFF
    // than before). If playback is active, stop so it can rebuild
    // with the new chain on the next Play press.
    if (isPlayingRef.current) {
      try { stopAll(); } catch (_) {}
      setIsPlaying(false);
      try {
        if (typeof window !== "undefined" && window.bfToast && window.bfToast.info) {
          window.bfToast.info("Press Play to apply preset");
        }
      } catch (_) {}
    }
  };

  const addTrackObj = function (obj) {
    // Ensure every track has a color — vocals get VOCAL_COLORS, others get COLORS
    const fallbackColor = (obj.type === "vocal") ? VOCAL_COLORS[0] : COLORS[0];
    const full = { volume:1, pan:0, effects:defaultEffects(), clips:[], color:fallbackColor, ...obj };
    // If old flat model passed in, wrap audioBuffer as a clip
    if (full.audioBuffer && full.clips.length === 0) {
      const buf = full.audioBuffer;
      const st = full.startTime || 0;
      full.clips = [{ id:full.id+"_c0", audioBuffer:buf, url:full.url, blob:full.blob,
        startTime:st, nudgeOriginStart:st, duration:buf.duration, trimStart:0, trimEnd:buf.duration,
        label:"Main", active:true }];
      delete full.audioBuffer; delete full.url; delete full.blob; delete full.startTime; delete full.duration;
    }
    setTracks(function (prev) { return [...prev, full]; });
    setIsSaved(false);
  };

  // Duplicate a track: clones the FX chain only (no clips, no recordings).
  // Inserts the new track directly after the source track.
  // Use case: build an FX chain on one track, then duplicate to record dry
  // and re-process through the same chain later.
  const duplicateTrack = function (srcId) {
    setTracks(function(prev) {
      const idx = prev.findIndex(function(t){ return t.id === srcId; });
      if (idx === -1) return prev;
      const src = prev[idx];
      // Pick the next vocal number based on existing vocal tracks
      const vocalCount = prev.filter(function(t){ return t.type === "vocal"; }).length;
      const newId = Date.now() + Math.random();
      // Deep-clone effects so toggles on the new track don't mutate the source
      const clonedEffects = src.effects ? JSON.parse(JSON.stringify(src.effects)) : defaultEffects();
      const isVocal = src.type === "vocal";
      const baseName = isVocal ? ("Vocal " + (vocalCount + 1)) : (src.name + " Copy");
      const clone = {
        id: newId,
        name: baseName,
        type: src.type,
        color: isVocal ? VOCAL_COLORS[(vocalCount) % VOCAL_COLORS.length] : src.color,
        volume: src.volume ?? 1,
        pan: src.pan ?? 0,
        isMuted: false,
        isSoloed: false,
        effects: clonedEffects,
        clips: [], // FX only — no recordings copied
      };
      const next = [...prev];
      next.splice(idx + 1, 0, clone);
      return next;
    });
    setIsSaved(false);
  };

  const updateTrack = function (id, patch) {
    setTracks(function (prev) {
      const next = prev.map(function(t){ return t.id===id?{...t,...patch}:t; });
      // Apply volume/pan/mute/solo to live audio nodes immediately.
      // Run this regardless of isPlaying so a producer dragging the
      // volume slider on a stopped project still hears the change on
      // the next tap or playback start (otherwise the gain node sits
      // at its stale value until the chain rebuilds). Cheap — just a
      // setTargetAtTime per affected node.
      if ("volume" in patch || "pan" in patch || "isMuted" in patch || "isSoloed" in patch) {
        // Ensure the affected track has a chain built so its volume
        // node actually exists. Without this, dragging the fader on
        // a MIDI track that has never played did nothing — applyGains
        // would skip it because gainNodesRef.current[id] was undefined.
        // This also covers freshly-added tracks that the user wants
        // to mix before pressing play.
        try {
          const targetTrack = next.find(function(t){ return t.id === id; });
          if (targetTrack && !gainNodesRef.current[id]) {
            getOrBuildPreviewChain(id);
          }
        } catch(_) {}
        applyGains(next);
      }
      return next;
    });
    setIsSaved(false);
  };

  const updateClip = function (trackId, clipId, patch) {
    setTracks(function (prev) {
      return prev.map(function(t) {
        if (t.id !== trackId) return t;
        return { ...t, clips: t.clips.map(function(cl){ return cl.id===clipId?{...cl,...patch}:cl; }) };
      });
    });
    setIsSaved(false);
  };

  const addClipToTrack = function (trackId, clip) {
    setTracks(function (prev) {
      return prev.map(function(t) {
        if (t.id !== trackId) return t;
        // All clips remain active — users can record multiple takes on the same track
        // and they all play back together. Use the track's Takes panel to manage them.
        return { ...t, clips:[...t.clips, {...clip, active:true}] };
      });
    });
    setIsSaved(false);
  };

  // ── Slice a clip into two at a given timeline time ────────────────
  // Non-destructive: both halves share the same audioBuffer, only
  // trimStart/trimEnd boundaries differ. Snaps to grid if snap is on.
  // Returns {ok, reason} — ok=false if the slice point isn't inside
  // the clip's audible range.
  //
  // Math:
  //   bufferSlicePoint = trimStart + (timelineTime - startTime)
  //   Left:  { trimStart: same,         trimEnd: bufferSlicePoint }
  //   Right: { startTime: timelineTime, trimStart: bufferSlicePoint, trimEnd: same }
  //
  // The right clip gets a new id (clipId + "_r") so React keys stay unique.
  // The left clip keeps the original id so anything referencing it
  // (selectedClipId, nudgeOriginStart's owner) stays valid.
  const sliceClipAtTime = function (trackId, clipId, timelineTime) {
    let outcome = { ok: false, reason: "not-found" };
    setTracks(function (prev) {
      return prev.map(function(t) {
        if (t.id !== trackId) return t;
        const idx = t.clips.findIndex(function(cl){ return cl.id === clipId; });
        if (idx < 0) return t;
        const cl = t.clips[idx];

        // Snap the slice point to grid (if enabled)
        const snappedT = snapSecs(timelineTime);
        const MARGIN = 0.05;  // 50ms minimum on each side

        // ── MIDI/SAMPLER clip path ──
        // Clips without an audioBuffer (e.g. MIDI tracks, sampler tracks
        // that drive their playback through samplerData rather than a
        // baked buffer) get sliced via the notes array. Audio clips
        // continue to use the trimStart/trimEnd path below.
        if (!cl.audioBuffer) {
          const clipStart = cl.startTime || 0;
          const clipDur   = cl.duration || 0;
          const clipEnd   = clipStart + clipDur;
          if (snappedT <= clipStart + MARGIN || snappedT >= clipEnd - MARGIN) {
            outcome = { ok:false, reason:"out-of-range" };
            return t;
          }
          // Position within the clip where the cut happens (clip-relative)
          const sliceOffset = snappedT - clipStart;
          const notes = (cl.notes || []);
          const leftNotes  = [];
          const rightNotes = [];
          for (let i = 0; i < notes.length; i++) {
            const n = notes[i];
            const nStart = n.start || 0;
            const nEnd   = nStart + (n.duration || 0);
            if (nEnd <= sliceOffset + 1e-6) {
              // Entirely before the cut — stays in left as-is
              leftNotes.push(n);
            } else if (nStart >= sliceOffset - 1e-6) {
              // Entirely after the cut — moves to right, start re-baselined
              rightNotes.push(Object.assign({}, n, {
                start: nStart - sliceOffset,
              }));
            } else {
              // Straddles the cut — left half trimmed to end at the cut,
              // right half starts at 0 with the remaining duration.
              const leftDur  = sliceOffset - nStart;
              const rightDur = nEnd - sliceOffset;
              if (leftDur > 0.005) {
                leftNotes.push(Object.assign({}, n, { duration: leftDur }));
              }
              if (rightDur > 0.005) {
                rightNotes.push(Object.assign({}, n, {
                  // Reuse the same id with a marker? No — keep simple, just
                  // give it a new id so React keys stay unique.
                  id: (n.id || "n") + "_r" + Math.random().toString(36).slice(2, 7),
                  start: 0,
                  duration: rightDur,
                }));
              }
            }
          }
          const leftClip = Object.assign({}, cl, {
            duration: sliceOffset,
            notes: leftNotes,
          });
          const rightClip = Object.assign({}, cl, {
            id: cl.id + "_r" + Date.now().toString(36),
            startTime: snappedT,
            duration: clipDur - sliceOffset,
            notes: rightNotes,
            // Reset nudge origin for the new right half
            nudgeOriginStart: snappedT,
            label: (cl.label || "Clip") + " (B)",
          });
          if (!/(A|B)\)$/.test(leftClip.label || "")) {
            leftClip.label = (cl.label || "Clip") + " (A)";
          }
          const newClips = t.clips.slice();
          newClips.splice(idx, 1, leftClip, rightClip);
          outcome = { ok:true };
          return Object.assign({}, t, { clips: newClips });
        }

        // ── AUDIO clip path (original logic) ──
        const clipStart = cl.startTime || 0;
        const trimS     = cl.trimStart || 0;
        const trimE     = cl.trimEnd !== undefined ? cl.trimEnd : cl.audioBuffer.duration;
        const clipEnd   = clipStart + (trimE - trimS);

        if (snappedT <= clipStart + MARGIN || snappedT >= clipEnd - MARGIN) {
          outcome = { ok:false, reason:"out-of-range" };
          return t;
        }
        const bufferSlicePoint = trimS + (snappedT - clipStart);
        const leftClip = Object.assign({}, cl, {
          trimEnd: bufferSlicePoint,
        });
        const rightClip = Object.assign({}, cl, {
          id: cl.id + "_r" + Date.now().toString(36),
          startTime: snappedT,
          trimStart: bufferSlicePoint,
          nudgeOriginStart: snappedT,
          label: (cl.label || "Clip") + " (B)",
        });
        if (!/(A|B)\)$/.test(leftClip.label || "")) {
          leftClip.label = (cl.label || "Clip") + " (A)";
        }
        const newClips = t.clips.slice();
        newClips.splice(idx, 1, leftClip, rightClip);
        outcome = { ok:true };
        return Object.assign({}, t, { clips: newClips });
      });
    });
    setIsSaved(false);
    return outcome;
  };

  const removeClip = function (trackId, clipId) {
    setTracks(function (prev) {
      return prev.map(function(t) {
        if (t.id !== trackId) return t;
        const rem = t.clips.filter(function(cl){ return cl.id!==clipId; });
        // Auto-activate last clip if none active
        if (rem.length > 0 && !rem.some(function(cl){ return cl.active; })) {
          rem[rem.length-1] = {...rem[rem.length-1], active:true};
        }
        return { ...t, clips:rem };
      });
    });
    setContextMenu(null); setIsSaved(false);
  };

  // Move a clip between tracks atomically (one setTracks call so it's a single
  // re-render and undo-history entry). Optionally update the clip's startTime
  // at the same time (when the user dragged it horizontally too).
  const moveClipToTrack = function (fromTrackId, clipId, toTrackId, patch) {
    if (fromTrackId === toTrackId) return;
    setTracks(function(prev) {
      let moved = null;
      // First pass: find and detach the clip from its source track
      const detached = prev.map(function(t) {
        if (t.id !== fromTrackId) return t;
        const clip = (t.clips || []).find(function(cl){ return cl.id === clipId; });
        if (!clip) return t;
        moved = patch ? { ...clip, ...patch } : clip;
        return { ...t, clips: t.clips.filter(function(cl){ return cl.id !== clipId; }) };
      });
      if (!moved) return prev;
      // Second pass: attach to destination track
      return detached.map(function(t) {
        if (t.id !== toTrackId) return t;
        return { ...t, clips: [...(t.clips || []), { ...moved, active: true }] };
      });
    });
    setIsSaved(false);
  };

  const setActiveClip = function (trackId, clipId) {
    setTracks(function (prev) {
      return prev.map(function(t) {
        if (t.id !== trackId) return t;
        return { ...t, clips: t.clips.map(function(cl){ return {...cl, active:cl.id===clipId}; }) };
      });
    });
  };

  const [confirmDeleteTrackId, setConfirmDeleteTrackId] = React.useState(null);

  const removeTrack = function (id) {
    setTracks(function (prev) { return prev.filter(function(t){ return t.id!==id; }); });
    // Tear down the cached FX chain for this track so it doesn't leak audio nodes
    const cached = chainsRef.current[id];
    if (cached && cached.entry) {
      try { cached.entry.disconnect(); } catch(e) {}
    }
    // Clean R-Comp ARC interval if present (matches teardownChain logic)
    const fxNodes = fxNodesRef.current[id];
    if (fxNodes && fxNodes.rcomp && fxNodes.rcomp.arcTimer) {
      try { clearInterval(fxNodes.rcomp.arcTimer); } catch(e) {}
    }
    // Clean TQ band polling intervals (matches teardownChain logic)
    if (fxNodes && fxNodes.tq && fxNodes.tq.bandNodes) {
      for (const bn of fxNodes.tq.bandNodes) {
        if (bn.intervalId) { try { clearInterval(bn.intervalId); } catch(e) {} }
      }
    }
    delete chainsRef.current[id];
    delete fxNodesRef.current[id];
    delete gainNodesRef.current[id];
    delete trackAnalysersRef.current[id];
    delete fxLatencyRef.current[id];  // PDC cache
    // Stage 4: dispose this track's SpessaSynth-live engine if it has one,
    // so its main-thread ScriptProcessor node doesn't leak (it keeps the
    // audio thread busy otherwise). Also drop it from the registry.
    try {
      if (typeof _bfSpessaLiveByTrack !== "undefined" && _bfSpessaLiveByTrack.has(id)) {
        const liveEng = _bfSpessaLiveByTrack.get(id);
        if (liveEng && typeof liveEng.dispose === "function") liveEng.dispose();
        _bfSpessaLiveByTrack.delete(id);
      }
    } catch(_){}
    setContextMenu(null); setIsSaved(false);
  };

  const cloneTrack = function (track) {
    addTrackObj({
      ...track,
      id: Date.now() + Math.random(),
      name: track.name + " (copy)",
      clips: track.clips ? track.clips.map(function(cl){
        // Unique per-clip suffix so cloning a track multiple times
        // doesn't collide on shared "_cp" ids.
        const sfx = "_cp" + Date.now().toString(36) + Math.random().toString(36).slice(2, 6);
        return { ...cl, id: cl.id + sfx, startTime:(cl.startTime||0)+(cl.duration||2)+0.5 };
      }) : [],
    });
    setContextMenu(null);
  };

  const toggleMuteUnused = null; const toggleSoloUnused = null; // replaced above

  // ── Upload beat / audio ───────────────────────────────────────
  const handleFileUpload = async function (e, type) {
    const files = Array.from(e.target.files);
    if (!files.length) return;
    setError("");
    setShowAddMenu(false);
    // Grab AudioContext synchronously inside the user-gesture
    const actx = getActx();
    // Ensure it's running — await the resume so it's truly ready before decode
    try { await actx.resume(); } catch(err) {}
    for (const file of files) {
      const url  = URL.createObjectURL(file);
      const name = file.name.replace(/\.[^.]+$/, "");
      try {
        const ab  = await file.arrayBuffer();
        const buf = await actx.decodeAudioData(ab);
        const tId = Date.now() + Math.random();
        const newTrack = {
          id: tId, name, type: type || "beat",
          isMuted: false, isSoloed: false,
          volume: 1, pan: 0,
          effects: defaultEffects(),
          color: type === "beat" ? "#C026D3" : VOCAL_COLORS[tracksRef.current.filter(function(t){return t.type==="vocal";}).length % VOCAL_COLORS.length],
          clips: [{ id:tId+"_c0", audioBuffer:buf, url, startTime:0, nudgeOriginStart:0, duration:buf.duration,
            trimStart:0, trimEnd:buf.duration, label:"Main", active:true }],
        };
        setTracks(function(prev){ return [...prev, newTrack]; });
        setIsSaved(false);
        if (type === "beat") {
          // Set loop bounds to match the first uploaded beat so the user
          // gets sensible default loop punch-in/out. Do NOT rename the
          // project — the producer named it (or will name it on save).
          setLoopOut(buf.duration);
        }
        if (isPlayingRef.current) {
          setTimeout(function() {
            applyGains(tracksRef.current);
            if (masterGainRef.current) masterGainRef.current.gain.value = masterVolume;
          }, 0);
        }
        // After React paints the new track row, nudge the playhead DOM into place
        // so the clip is visible without needing to press play first
        requestAnimationFrame(function() {
          requestAnimationFrame(function() {
            syncUItoTime(playheadAtRef.current);
          });
        });
      } catch (e2) {
        console.error("[Upload error]", e2);
        setError("Could not load: " + file.name + " — " + (e2 && e2.message ? e2.message : String(e2)));
      }
    }
    e.target.value = "";
  };

  // ── Recording ─────────────────────────────────────────────────

  // ── Background audio for iOS home screen web app ─────────────
  // iOS suspends Web Audio when a home screen web app goes to background,
  // UNLESS an <audio> element is actively playing audible content.
  // The element must be: in the DOM, loop=true, volume above ~0.01, actually playing.
  // We keep it completely separate from the Web Audio graph (no createMediaElementSource)
  // so it doesn't interfere with playback volume or routing.
  const silentAudioRef = useRef(null);
  const silentSrcNodeRef = useRef(null); // kept for API compat, unused

  const ensureSilentAudio = function () {
    try {
      if (!silentAudioRef.current) {
        // 1-second single-tone WAV at very low amplitude (audible to iOS, inaudible to users)
        const sr = 44100, nSamples = sr;
        const ab = new ArrayBuffer(44 + nSamples * 2);
        const dv = new DataView(ab);
        const ws = function(o, s) { for (var i=0;i<s.length;i++) dv.setUint8(o+i,s.charCodeAt(i)); };
        ws(0,"RIFF"); dv.setUint32(4,36+nSamples*2,true); ws(8,"WAVE");
        ws(12,"fmt "); dv.setUint32(16,16,true); dv.setUint16(20,1,true); dv.setUint16(22,1,true);
        dv.setUint32(24,sr,true); dv.setUint32(28,sr*2,true); dv.setUint16(32,2,true); dv.setUint16(34,16,true);
        ws(36,"data"); dv.setUint32(40,nSamples*2,true);
        // Write a 20Hz sine wave at amplitude 50 (out of 32767) — inaudible, but non-zero
        var arr = new Int16Array(ab, 44);
        for (var i = 0; i < nSamples; i++) {
          arr[i] = Math.round(50 * Math.sin(2 * Math.PI * 20 * i / sr));
        }
        const blob = new Blob([ab], {type:"audio/wav"});
        const a = new Audio();
        a.src = URL.createObjectURL(blob);
        a.loop = true;
        a.volume = 0.02; // low but above iOS background audio threshold
        a.setAttribute("playsinline", "");
        // Must be in the DOM for iOS home screen web app background audio
        a.style.cssText = "position:fixed;width:0;height:0;left:-9999px;opacity:0;";
        document.body.appendChild(a);
        silentAudioRef.current = a;
      }
      if (silentAudioRef.current.paused) {
        silentAudioRef.current.play().catch(function(){});
      }
    } catch(e) {}
  };

  // ── MediaSession: lock screen controls + background playback permission ──
  useEffect(function () {
    if (!("mediaSession" in navigator)) return;
    try {
      navigator.mediaSession.metadata = new MediaMetadata({
        title: projectName || "BeatFinder Studio",
        artist: "BeatFinder",
        album: "Studio Mode",
      });
      navigator.mediaSession.setActionHandler("play", function () {
        ensureSilentAudio();
        if (actxRef.current && actxRef.current.state === "suspended") {
          actxRef.current.resume().catch(function(){});
        }
        setIsPlaying(true);
      });
      navigator.mediaSession.setActionHandler("pause", function () {
        setIsPlaying(false);
      });
      navigator.mediaSession.setActionHandler("stop", function () {
        setIsPlaying(false);
        setCurrentTime(0);
        playheadAtRef.current = 0;
      });
      navigator.mediaSession.playbackState = isPlaying ? "playing" : "paused";
    } catch(e) {}
  }, [isPlaying, projectName]);

  // ── App lifecycle: background / foreground handling ───────────
  // Uses refs so this effect never needs to re-register — stable listener.
  const monitoringOnRef = useRef(monitoringOn);
  useEffect(function () { monitoringOnRef.current = monitoringOn; }, [monitoringOn]);

  // Keep saveStateRef always pointing at a closure with latest state
  saveStateRef.current = function () {
    try {
      sessionStorage.setItem("bf_studio_state", JSON.stringify({
        projectName, bpm, projectKey, timeSigNum, zoom, vZoom,
        snapToGrid, loopEnabled, loopIn, loopOut,
        metronomeOn, showBeatGrid, micInputGain,
        tracksMeta: tracksRef.current.map(function(t) {
          return { id:t.id, name:t.name, type:t.type, color:t.color,
                   isMuted:t.isMuted, isSoloed:t.isSoloed, volume:t.volume, pan:t.pan };
        }),
      }));
      sessionStorage.setItem("bf_session_started", "1");
    } catch(e) {}
  };

  useEffect(function () {
    const onBackground = function () {
      // Stop mic — do NOT touch AudioContext or playback
      if (monitoringOnRef.current) { stopMonitoring(); }
      if (saveStateRef.current) saveStateRef.current();
      // Ensure silent audio is playing so iOS keeps the audio session alive
      ensureSilentAudio();
    };

    const onForeground = function () {
      // Use requestAnimationFrame so we wait for the screen to be fully active
      // before touching the AudioContext — prevents the freeze
      requestAnimationFrame(function () {
        requestAnimationFrame(function () {
          try {
            if (actxRef.current) {
              if (actxRef.current.state === "suspended") {
                actxRef.current.resume().catch(function(){});
              }
            }
            // Reconnect silent audio through AudioContext if needed
            ensureSilentAudio();
          } catch(e) {}
          // Defer non-critical work further
          setTimeout(function () {
            try {
              if (monitorCtxRef.current && monitorCtxRef.current.state === "suspended") {
                monitorCtxRef.current.resume().catch(function(){});
              }
              if ("mediaSession" in navigator) {
                navigator.mediaSession.playbackState = isPlayingRef.current ? "playing" : "paused";
              }
              checkHeadphones();
            } catch(e) {}
          }, 300);
        });
      });
    };

    const onVisibility = function () {
      if (document.hidden) { onBackground(); } else { onForeground(); }
    };

    document.addEventListener("visibilitychange", onVisibility);
    window.addEventListener("pagehide", function () {
      if (saveStateRef.current) saveStateRef.current();
    });
    return function () {
      document.removeEventListener("visibilitychange", onVisibility);
    };
  }, []); // stable — reads live values via refs


  // selectedTrackId declared at top of component (Rules of Hooks)

  const startCountIn = async function (trackId) {
    // Request mic permission here (lazy) — only fires on first use, browser caches grant
    const granted = await requestMicPermissionOnce();
    if (!granted) return;

    const vocalTracks = tracks.filter(function(t){ return t.type === "vocal"; });
    let targetId = trackId || selectedTrackId;
    if (!targetId) {
      if (vocalTracks.length > 0) {
        targetId = vocalTracks[vocalTracks.length - 1].id;
      } else {
        // Auto-create vocal track so record works immediately
        const newId = Date.now() + Math.random();
        addTrackObj({ id:newId, name:"Vocal 1", type:"vocal", isMuted:false, isSoloed:false, clips:[], color:VOCAL_COLORS[0] });
        setSelectedTrackId(newId);
        targetId = newId;
      }
    }
    setError(""); setCountIn(3); let n = 3;
    countTimerRef.current = setInterval(function () {
      n--; setCountIn(n);
      if (n <= 0) { clearInterval(countTimerRef.current); setCountIn(0); doRecord(targetId); }
    }, 800);
  };

  const doRecord = async function (targetTrackId) {
    const newClipId = Date.now();
    clipIdRef.current = newClipId;

    try {
      // Build mic constraints using the same helper as monitoring,
      // so the selected mic source (iPhone vs headset) is respected during recording.
      // All DSP (echo cancellation, noise suppression, AGC) stays OFF — raw clean signal.
      // Post-processing is handled by the Noise Remover FX plugin instead.
      const recConstraints = await buildMicConstraints(micSource);
      // Always open a fresh stream for recording — never reuse the monitoring stream.
      const stream = await navigator.mediaDevices.getUserMedia({ audio: recConstraints });

      const actx    = getActx();
      const srcNode = actx.createMediaStreamSource(stream);

      // ── Mic input gain boost ──────────────────────────────────────────────
      const micInputBoost = actx.createGain();
      micInputBoost.gain.value = micInputGainRef.current;
      srcNode.connect(micInputBoost);

      // ── Mono merge + analyser ─────────────────────────────────────────────
      const analyser = actx.createAnalyser(); analyser.fftSize = 256;
      const recSplitter = actx.createChannelSplitter(1);
      const recMerger   = actx.createChannelMerger(2);
      micInputBoost.connect(recSplitter);
      recSplitter.connect(recMerger, 0, 0);
      recSplitter.connect(recMerger, 0, 1);
      recMerger.connect(analyser);

      // ── Route mic through Web Audio graph → MediaStreamDestination ───────
      // KEY FIX FOR MIC DUCKING: MediaRecorder must capture from a stream that
      // belongs to the SAME AudioContext as playback. When MediaRecorder captures
      // the raw getUserMedia stream directly, iOS treats it as a competing audio
      // session and activates its hardware ducker on the mic input, reducing
      // recording level whenever other audio is playing.
      // By routing mic → Web Audio → createMediaStreamDestination, everything
      // shares one AudioContext = one iOS audio session = no ducking.
      let recStream = stream; // fallback if MediaStreamDestination unavailable
      let recDest = null;
      try {
        recDest = actx.createMediaStreamDestination();
        recMerger.connect(recDest);
        recStream = recDest.stream;
      } catch(destErr) {
        // createMediaStreamDestination not supported — record raw stream (iOS < 14.5)
        console.warn("[BeatFinder] MediaStreamDestination unavailable, using raw stream");
      }

      setRecTrail([]);
      chunksRef.current = [];

      // Pick highest-quality codec available:
      // opus in webm (best compression + quality), then webm fallback, then mp4 for iOS Safari
      const CODEC_PREFS = [
        "audio/webm;codecs=opus",
        "audio/webm",
        "audio/ogg;codecs=opus",
        "audio/mp4",
      ];
      const mime = CODEC_PREFS.find(function(m){ return MediaRecorder.isTypeSupported(m); }) || "";

      // 320kbps Opus — top-tier quality for vocals. Opus stops gaining audible quality
      // beyond 256kbps for mono, but 320kbps gives extra headroom for any transcoding
      // the user does later (mix, master, export to lossy formats).
      const mr = new MediaRecorder(recStream, {
        mimeType:          mime || undefined,
        audioBitsPerSecond: 320000,
      });

      mr.ondataavailable = function (ev) {
        if (ev.data && ev.data.size > 0) chunksRef.current.push(ev.data);
      };

      mr.onstop = async function () {
        stream.getTracks().forEach(function (t) { t.stop(); });
        try { analyser.disconnect(); } catch(e) {}
        try { if (recDest) recDest.disconnect(); } catch(e) {}
        try { srcNode.disconnect(); } catch(e) {}
        try { micInputBoost.disconnect(); } catch(e) {}

        // ── iOS speaker re-routing fix ────────────────────────────────────────
        // Close and recreate AudioContext so iOS re-routes to speaker (not earpiece).
        // Must happen before decodeAudioData so the new context is used for playback.
        await restoreIOSSpeaker();
        
        const blob = new Blob(chunksRef.current, { type: mime });
        const url  = URL.createObjectURL(blob);

        let buf = null;
        try {
          const ab = await blob.arrayBuffer();
          buf = await getActx().decodeAudioData(ab.slice(0));
        } catch (decErr) {
          setError("Could not decode recording. Try again.");
          stopAll(); setIsPlaying(false);
          setIsRecording(false); setRecTrackId(null); setRecTrail([]);
          return;
        }

        // ── Downmix to true mono (synchronous JS — no OfflineAudioContext to avoid
        //    async interference with live AudioContext on iOS) ────────────────────
        // iOS MediaRecorder sometimes produces a 2-channel (stereo) buffer even
        // when channelCount:1 was requested — the right channel is often silent
        // or carries a phase-shifted copy, causing audio to pan hard left on playback.
        if (buf && buf.numberOfChannels > 1) {
          try {
            const monoData = new Float32Array(buf.length);
            const nc = buf.numberOfChannels;
            for (let c = 0; c < nc; c++) {
              const ch = buf.getChannelData(c);
              for (let i = 0; i < buf.length; i++) monoData[i] += ch[i];
            }
            const inv = 1 / nc;
            for (let i = 0; i < monoData.length; i++) monoData[i] *= inv;
            const monoBuf = getActx().createBuffer(1, buf.length, buf.sampleRate);
            monoBuf.copyToChannel(monoData, 0);
            buf = monoBuf;
          } catch (monoErr) { /* keep stereo if downmix fails */ }
        }

        if (!buf || buf.duration < 0.05) {
          stopAll(); setIsPlaying(false);
          setIsRecording(false); setRecTrackId(null); setRecTrail([]);
          return;
        }

        // ── Clock-drift compensation ───────────────────────────────────────
        // PROBLEM: The mic's recording clock and the AudioContext's playback
        // clock are two separate hardware oscillators on iOS. They tick at
        // slightly different rates (typically a few parts per million off).
        // Over a short take this is inaudible, but over a 1-2 minute recording
        // the drift accumulates to 5-20ms — enough to make the vocal slide
        // behind (or ahead of) the beat by the end.
        //
        // FIX: Compare the recording duration as measured by:
        //   • mic clock    = buf.duration (samples / sampleRate, from MediaRecorder)
        //   • actx clock   = stopActxTime - startActxTime (what playback engine sees)
        // If they differ, resample buf to fit the actx duration exactly. Then
        // every sample in the recording plays back at the rate the playback
        // engine expects, so timing stays locked from start to end.
        //
        // We use simple linear interpolation. At ppm-level drift the resample
        // ratio is essentially 1.0 (e.g. 0.99995..1.00005) and the audible
        // difference between linear and cubic is undetectable. The pitch
        // shift introduced is also imperceptible (50 ppm = 0.08 cents).
        //
        // SAFETY: Only compensate if drift is small (< 5%). Larger differences
        // mean something else went wrong (codec hiccup, clock reset) and the
        // safe thing is to keep the buffer untouched.
        try {
          const startActx = recStartActxTimeRef.current;
          const stopActx  = recStopActxTimeRef.current;
          if (startActx !== null && stopActx !== null && stopActx > startActx) {
            const actxDuration = stopActx - startActx;
            const micDuration  = buf.duration;
            const driftRatio   = actxDuration / micDuration;
            const driftAbsMs   = Math.abs(actxDuration - micDuration) * 1000;
            // Compensate when:
            //   • Drift is more than 5ms (below this, audibly nothing to fix)
            //   • Drift is less than 5% (above this, something pathological)
            //   • Recording is at least 5 seconds (short clips don't need it)
            if (driftAbsMs > 5 && Math.abs(driftRatio - 1) < 0.05 && micDuration >= 5) {
              const sr        = buf.sampleRate;
              const channels  = buf.numberOfChannels;
              const targetLen = Math.round(actxDuration * sr);
              const srcLen    = buf.length;
              // Build a new buffer at the same sample rate but with sample count
              // that matches the actx-clock duration. The audio is "stretched" or
              // "compressed" by the drift ratio via linear interpolation.
              const stretched = getActx().createBuffer(channels, targetLen, sr);
              for (let ch = 0; ch < channels; ch++) {
                const srcData = buf.getChannelData(ch);
                const dstData = stretched.getChannelData(ch);
                // For each output sample, find its position in the source and
                // linearly interpolate between the two nearest source samples.
                // step = how far through the source we advance per output sample
                const step = (srcLen - 1) / (targetLen - 1);
                for (let i = 0; i < targetLen; i++) {
                  const srcPos = i * step;
                  const i0     = Math.floor(srcPos);
                  const i1     = Math.min(srcLen - 1, i0 + 1);
                  const frac   = srcPos - i0;
                  dstData[i]   = srcData[i0] * (1 - frac) + srcData[i1] * frac;
                }
              }
              try {
                console.log("[BF] Clock drift compensated:",
                  "mic=" + micDuration.toFixed(3) + "s",
                  "actx=" + actxDuration.toFixed(3) + "s",
                  "drift=" + (driftAbsMs).toFixed(1) + "ms",
                  "ratio=" + driftRatio.toFixed(6));
              } catch(e) {}
              buf = stretched;
            }
          }
        } catch(driftErr) {
          // Compensation failed — keep the original buffer.
          // Better to ship a slightly-drifted recording than to lose the take.
          console.warn("[BF] Clock drift compensation failed:", driftErr);
        }

        // ── Ground-truth timing ───────────────────────────────────────────
        // buf.duration = the ONLY reliable measure of how long was captured (always use this).
        // Wall-clock (recDurRef) drifts due to async gaps and codec buffering — never use it.
        //
        // PRIMARY strategy: start-anchor.
        // recStartTimeRef was stamped the instant mr.start() fired, with inputLatency subtracted.
        // clipStartTime = recStartTimeRef (already accounting for mic hardware delay).
        //
        // SECONDARY (sanity check): stop-anchor.
        // stopActxTime was captured when stop() was pressed. If stopAll() hasn't run yet,
        // we can derive stopTimelinePos. If the two methods agree within 50ms we use start-anchor.
        // If they disagree badly it means stopAll() had already reset masterStartRef — start-anchor wins.
        let clipStartTime = recStartTimeRef.current;

        const stopActxTime = recStopActxTimeRef.current;
        if (stopActxTime !== null && masterStartRef.current > 0) {
          const stopTimelinePos  = playheadAtRef.current + (stopActxTime - masterStartRef.current);
          const stopAnchorStart  = Math.max(0, stopTimelinePos - buf.duration);
          const diff = Math.abs(stopAnchorStart - clipStartTime);
          // Only trust stop-anchor if it agrees closely — large diff means masterStart was reset
          if (diff < 0.05) clipStartTime = stopAnchorStart;
          // Otherwise keep start-anchor (recStartTimeRef) — it was stamped before any resets
        }

        // ── Strip leading silence from the decoded buffer ──────────────────
        // On iOS, getUserMedia + MediaRecorder always produce a silent warmup
        // period at the start of the buffer (~50–250ms).
        //
        // trimStart = how far into the buffer audio content begins (skips silent warmup on playback).
        // startTime = clipStartTime — the wall-clock moment mr.start() fired. This is already
        // the correct timeline position. We do NOT shift it forward by leadingSilenceSec because
        // clipStartTime was stamped at mr.start(), not at the first sample — so the silence was
        // captured starting at that exact moment. Shifting startTime forward would place the vocal
        // later than where it was actually performed.
        const SILENCE_THRESHOLD = 0.001; // -60 dBFS
        const MAX_SILENCE_SCAN  = 0.5;   // never strip more than 500ms
        let leadingSilenceSec   = 0;
        try {
          const ch  = buf.getChannelData(0);
          const sr  = buf.sampleRate;
          const max = Math.min(ch.length, Math.floor(sr * MAX_SILENCE_SCAN));
          for (let i = 0; i < max; i++) {
            if (Math.abs(ch[i]) > SILENCE_THRESHOLD) {
              leadingSilenceSec = i / sr;
              break;
            }
          }
        } catch(e) { leadingSilenceSec = 0; }

        const newClip = {
          id: String(Date.now()) + "_rec",
          audioBuffer: buf, url, blob,
          startTime: clipStartTime,       // wall-clock anchor — do NOT adjust for silence
          nudgeOriginStart: clipStartTime, // baseline for the per-clip nudge readout
          duration:  buf.duration,
          trimStart: leadingSilenceSec,   // skip silent warmup during playback only
          trimEnd:   buf.duration,
          label: "Take " + new Date().toLocaleTimeString(),
          active: true,
        };

        if (targetTrackId) {
          addClipToTrack(targetTrackId, newClip);
        } else {
          addTrackObj({
            id: Date.now() + Math.random(),
            name: "Vocal " + (tracks.filter(function (t) { return t.type === "vocal"; }).length + 1),
            type: "vocal",
            isMuted: false, isSoloed: false,
            color: VOCAL_COLORS[tracks.filter(function(t){return t.type==="vocal";}).length % VOCAL_COLORS.length],
            clips: [newClip],
          });
        }
        // Restore all track gain states — decodeAudioData and getUserMedia can cause
        // iOS to duck/suspend the AudioContext, discarding scheduled gain values.
        // Re-apply before stopAll so the next doPlay starts with correct volumes.
        const liveActx = actxRef.current;
        if (liveActx && liveActx.state === "suspended") {
          try { await liveActx.resume(); } catch(e) {}
        }
        if (isPlayingRef.current) {
          applyGains(tracksRef.current);
          if (masterGainRef.current) masterGainRef.current.gain.value = masterVolume;
        }
        // Stop playback now that decoding is done — safe to tear down audio graph here
        stopAll();
        setIsPlaying(false);
        setRecTrail([]); setIsRecording(false); setRecTrackId(null);
      };

      recDurRef.current = 0;

      // ── MUST await doPlay() before starting the recorder ──────────────────
      // doPlay() is async — it calls await registerPitchWorklet() which can take
      // 50–200ms on first use. If we don't await it, masterStartRef.current is
      // still 0 when mr.start() fires, making trueStartTime wildly wrong and
      // causing the clip to jump to the wrong position on the timeline.
      if (!isPlaying) {
        await doPlay(playheadAtRef.current);
        setIsPlaying(true);
      }

      // Snapshot both refs immediately after doPlay() resolves —
      // these are the ground-truth values we use for clip placement.
      const masterStart   = masterStartRef.current;   // actx time when playback started
      const headPos       = playheadAtRef.current;    // timeline position at that moment

      // ── Start the recorder and stamp the clock as tightly as possible ──────
      mr.start(100); // 100ms chunks — reliable across iOS Safari + Chrome; 50ms causes gaps
      const startActxTime = actx.currentTime;
      recStartActxTimeRef.current = startActxTime; // for clock-drift compensation
      // Chrome exposes actx.inputLatency; Safari does not (it's undefined).
      // On Safari, we apply a user-configurable recording offset (default 80ms)
      // via the Studio settings slider. The offset compensates for mic + monitor
      // hardware delay PLUS any audio-graph latency added by FX plugins on
      // other tracks. If recordings drift, the user can adjust the slider.
      // Chrome devices use the platform-reported inputLatency (typically more
      // accurate) and ignore the manual offset.
      const inputLatency      = actx.inputLatency || 0;
      const manualOffsetSec   = (typeof actx.inputLatency === "undefined")
                                  ? ((recOffsetMsRef.current || 0) / 1000)
                                  : 0;
      // ── FX-induced latency compensation ──
      // When ANY track has FX enabled, the audio graph adds extra latency
      // that the base offset doesn't account for. The user calibrates a
      // SEPARATE FX offset value (Studio Settings → "FX Recording Offset"
      // slider + Calibrate button). It auto-applies on top of the base
      // offset only when FX is detected in any track at recording start.
      // Safari-only — Chrome's reported inputLatency already handles this.
      let fxOffsetSec = 0;
      if (typeof actx.inputLatency === "undefined") {
        try {
          const anyTrackHasFx = (tracksRef.current || []).some(function(tr){
            return trackHasFx(tr);
          });
          if (anyTrackHasFx) {
            fxOffsetSec = (fxRecOffsetMsRef.current || 0) / 1000;
          }
        } catch(e) { fxOffsetSec = 0; }
      }
      const totalLatency      = inputLatency + manualOffsetSec + fxOffsetSec;

      // Timeline position = where the playhead was + how far actx has advanced since masterStart
      const trueStartTime = Math.max(0,
        headPos + (startActxTime - masterStart) - totalLatency
      );
      recStartTimeRef.current = trueStartTime;

      const recStart = Date.now();
      recIntRef.current = setInterval(function () {
        recDurRef.current = (Date.now() - recStart) / 1000;
        const data = new Uint8Array(analyser.frequencyBinCount);
        analyser.getByteTimeDomainData(data);
        let peak = 0;
        for (var i = 0; i < data.length; i++) {
          var v = Math.abs(data[i] - 128) / 128;
          if (v > peak) peak = v;
        }
        setRecTrail(function (prev) { return [...prev.slice(-200), peak]; });
      }, 50);

      mediaRecRef.current = mr;
      setIsRecording(true);
      setRecTrackId(targetTrackId);

    } catch (e) {
      if (e.name === "NotAllowedError") {
        setError("Mic access denied. Go to Settings → Safari → Microphone → Allow.");
      } else {
        setError("Could not start recording: " + e.message);
      }
      stopAll(); setIsPlaying(false);
      setIsRecording(false); setRecTrackId(null);
      setCountIn(0);
    }
  };


  // ── Region gestures ───────────────────────────────────────────
  // ── Clip selection & drag state ───────────────────────────────
  // selectedClipId and dragRef declared at top of component (Rules of Hooks)

  // ── Per-clip nudge model ──────────────────────────────────────────
  // Each clip stores a `nudgeOriginStart` property — the startTime to
  // treat as "0 ms" for the Nudge bar's readout. The readout shows the
  // clip's CURRENT displacement from this origin, persists across
  // selections, and survives save/load.
  //
  // Lifecycle of nudgeOriginStart:
  //   • SET on clip creation (recording stop, file import, duplicate, etc.)
  //     to whatever startTime the clip is placed at
  //   • RE-BASELINED when the user horizontally drags the clip (intentional
  //     reposition) — the new drop position becomes the new origin
  //   • NOT touched by nudge buttons (±5, ±50, Reset) — the readout reflects
  //     the offset they introduce
  //
  // Legacy clips without nudgeOriginStart fall back to using the clip's
  // current startTime so the readout shows 0 ms instead of garbage.

  const selectClip = function (clipId) {
    setSelectedClipId(clipId);
  };

  const deselectClip = function () {
    setSelectedClipId(null);
  };

  // Update a specific clip's nudgeOriginStart to a new baseline (typically
  // the position it was just dropped at after a horizontal drag). After
  // this, the Nudge bar's readout reads 0 ms for the clip — reflecting
  // that it sits exactly at its new "origin."
  const rebaseClipNudgeOrigin = function (clipId, newOrigin) {
    if (!clipId) return;
    setTracks(function(prev){
      return prev.map(function(tr){
        if (!tr.clips || tr.clips.length === 0) return tr;
        let touched = false;
        const newClips = tr.clips.map(function(cl){
          if (cl.id !== clipId) return cl;
          touched = true;
          return Object.assign({}, cl, { nudgeOriginStart: newOrigin });
        });
        if (!touched) return tr;
        return Object.assign({}, tr, { clips: newClips });
      });
    });
  };

  // Apply a nudge delta in MILLISECONDS to the selected clip's startTime.
  // The nudge origin is unchanged so the readout reflects the new offset.
  // Clamps startTime to [0, +∞).
  const nudgeSelectedClip = function (deltaMs) {
    const id = selectedClipId;
    if (!id) return;
    const deltaSec = deltaMs / 1000;
    setTracks(function(prev){
      return prev.map(function(tr){
        if (!tr.clips || tr.clips.length === 0) return tr;
        let touched = false;
        const newClips = tr.clips.map(function(cl){
          if (cl.id !== id) return cl;
          touched = true;
          const ns = Math.max(0, (cl.startTime || 0) + deltaSec);
          return Object.assign({}, cl, { startTime: ns });
        });
        if (!touched) return tr;
        return Object.assign({}, tr, { clips: newClips });
      });
    });
    setIsSaved(false);
  };

  // Set the selected clip's startTime to its nudgeOriginStart + offsetMs.
  // Used by Reset (offsetMs=0). Reads nudgeOriginStart from the clip
  // itself rather than a closure variable, so the baseline persists
  // across selections.
  const setSelectedClipNudge = function (offsetMs) {
    const id = selectedClipId;
    if (!id) return;
    setTracks(function(prev){
      return prev.map(function(tr){
        if (!tr.clips || tr.clips.length === 0) return tr;
        let touched = false;
        const newClips = tr.clips.map(function(cl){
          if (cl.id !== id) return cl;
          touched = true;
          const origin = (cl.nudgeOriginStart !== undefined) ? cl.nudgeOriginStart : (cl.startTime || 0);
          const target = Math.max(0, origin + offsetMs / 1000);
          return Object.assign({}, cl, { startTime: target });
        });
        if (!touched) return tr;
        return Object.assign({}, tr, { clips: newClips });
      });
    });
    setIsSaved(false);
  };

  const showClipMenu = function (track, clip, x, y) {
    if (navigator.vibrate) navigator.vibrate(30);
    setContextMenu({ track, clip, x, y });
  };

  // ── Unified clip gesture handlers ─────────────────────────────
  // All handlers receive {track, clip} directly — no more proxy objects

  const handleRegionMouseDown = function (e, track, clip) {
    if (e.button === 2) return;
    e.stopPropagation();
    selectClip(clip.id);
    const startX = e.clientX;
    const startY = e.clientY;
    const startT = clip.startTime || 0;
    const startTrackIdx = tracksRef.current.findIndex(function(t){ return t.id === track.id; });
    // Multi-drag: if this clip is part of a multi-selection, drag the whole
    // group together (same delta) and snapshot every selected clip's start
    // time so the math works from each clip's original position.
    const isMultiDrag = selClipIds.size > 1 && selClipIds.has(clip.id);
    const multiStartTimes = {};
    if (isMultiDrag) {
      (tracksRef.current || []).forEach(function(t){
        (t.clips || []).forEach(function(cl){
          if (selClipIds.has(cl.id)) multiStartTimes[cl.id] = cl.startTime || 0;
        });
      });
    }
    let moved = false;
    let lastTargetIdx = startTrackIdx;
    let pendingStartT = startT;
    const onMove = function (me) {
      const dx = me.clientX - startX;
      const dy = me.clientY - startY;
      if (!moved && Math.abs(dx) < 5 && Math.abs(dy) < 5) return;
      if (!moved) {
        // First confirmed movement — open a history gesture so all
        // pointermove updates coalesce into a single undo step.
        beginTracksGesture();
      }
      moved = true;
      if (isMultiDrag) {
        // Shift every selected clip by the same delta — keeps the
        // arrangement's internal spacing intact (e.g. sliding everything
        // right to make room for an intro).
        const deltaSec = dx / effectivePPS;
        setTracks(function(prev){
          return prev.map(function(t){
            return Object.assign({}, t, {
              clips: (t.clips || []).map(function(cl){
                if (!selClipIds.has(cl.id)) return cl;
                const newT = snapSecs(Math.max(0, multiStartTimes[cl.id] + deltaSec));
                return Object.assign({}, cl, { startTime: newT });
              }),
            });
          });
        });
      } else {
        // Horizontal: live-update startTime within current track
        pendingStartT = snapSecs(startT + dx / effectivePPS);
        updateClip(track.id, clip.id, { startTime: pendingStartT });
        // Vertical: compute target track index but defer the move until drop
        const rowDelta = Math.round(dy / TRACK_H);
        const newTargetIdx = Math.max(0, Math.min(tracksRef.current.length - 1, startTrackIdx + rowDelta));
        lastTargetIdx = newTargetIdx;
      }
    };
    const onUp = function () {
      document.removeEventListener("mousemove", onMove);
      document.removeEventListener("mouseup", onUp);
      // If dropped on a different track row, transfer the clip — single-drag only.
      if (!isMultiDrag && moved && lastTargetIdx !== startTrackIdx) {
        const targetTrack = tracksRef.current[lastTargetIdx];
        if (targetTrack && targetTrack.id !== track.id) {
          moveClipToTrack(track.id, clip.id, targetTrack.id, { startTime: pendingStartT });
        }
      }
      // Re-baseline the clip's nudge origin to its new drop position so
      // Reset snaps back here, not to where it was first recorded. Done
      // ONLY when the clip actually moved (a fail-to-move tap shouldn't
      // wipe out the existing baseline).
      if (moved) {
        try { rebaseClipNudgeOrigin(clip.id, pendingStartT); } catch(e) {}
        // Close the history gesture — single undo step covers the drag.
        endTracksGesture();
      }
    };
    document.addEventListener("mousemove", onMove);
    document.addEventListener("mouseup", onUp);
  };

  // ── Multi-select lasso handlers ─────────────────────────────────────────
  // Long-press (1.5s) on blank lane space activates a drag-to-select box.
  // Any clip whose rendered rect overlaps the box gets added to selClipIds.
  // An action bar then appears with Delete and Duplicate for all selected clips.

  const laneScrollRef = scrollRef; // reuse existing scroll container ref

  const getLaneScrollX = function() {
    return laneScrollRef.current ? laneScrollRef.current.scrollLeft : 0;
  };

  const startLassoFromTouch = function(clientX, clientY, laneTop) {
    // Use the DAW wrapper (lassoContainerRef) as the coordinate space —
    // the lasso overlay div is position:absolute inside it, so x/y must be
    // relative to that element, not the scroll container.
    const scrollX = getLaneScrollX();
    const wrapRect = lassoContainerRef.current
      ? lassoContainerRef.current.getBoundingClientRect()
      : { left: 0, top: 0 };
    const containerRect = laneScrollRef.current
      ? laneScrollRef.current.getBoundingClientRect()
      : { left: 0, top: 0 };

    // X is in scroll-content space (accounts for horizontal scroll)
    const absX = clientX - containerRect.left + scrollX;
    // Y is relative to the DAW wrapper (where position:absolute is anchored)
    const absY = clientY - wrapRect.top;

    selBoxRef.current = { startX: absX, startY: absY, scrollX };
    setSelBox({ x: absX, y: absY, w: 0, h: 0 });
    setSelClipIds(new Set());

    const scrollEl = laneScrollRef.current;
    if (!scrollEl) return;

    // ── Lock the scroll container while the lasso is active ──
    // Without this, the underlying scroll listener still receives the
    // active touch and scrolls the timeline horizontally while the
    // user is trying to drag a selection box. iOS treats the lasso
    // gesture and the native pan as two competing consumers of the
    // same finger. We disable native scrolling for the duration of
    // the lasso and restore it on touchend/cancel.
    const prevTouchAction = scrollEl.style.touchAction;
    const prevOverflow    = scrollEl.style.overflow;
    scrollEl.style.touchAction = "none";
    scrollEl.style.overflow    = "hidden";

    const onMove = function(te) {
      if (!selBoxRef.current) return;
      // If a second finger lands while the lasso is active, the user is
      // pinch-zooming. Abort the lasso so the native pinch can take over.
      // We DON'T preventDefault here — that's the whole point: let the
      // browser handle the gesture as a zoom.
      if (te.touches && te.touches.length > 1) {
        selBoxRef.current = null;
        setSelBox(null);
        setSelClipIds(new Set());
        // Restore scroll behaviour so the pinch can take over cleanly.
        scrollEl.style.touchAction = prevTouchAction;
        scrollEl.style.overflow    = prevOverflow;
        scrollEl.removeEventListener("touchmove", onMove);
        scrollEl.removeEventListener("touchend", onEnd);
        return;
      }
      te.preventDefault();
      te.stopPropagation();
      const tx = te.touches[0].clientX;
      const ty = te.touches[0].clientY;
      const sx = getLaneScrollX();
      const ax = tx - containerRect.left + sx;
      const ay = ty - wrapRect.top;
      const x = Math.min(ax, selBoxRef.current.startX);
      const y = Math.min(ay, selBoxRef.current.startY);
      const w = Math.abs(ax - selBoxRef.current.startX);
      const h = Math.abs(ay - selBoxRef.current.startY);
      setSelBox({ x, y, w, h });

      // Hit-test all clips against the lasso box — use DOM rects directly
      // so we don't have to keep tracking ruler / scroll / safe-area offsets
      // in sync with the layout. The lasso box in screen-coords is
      // (clientX, clientY) at the user's finger; the box's "anchor" was set
      // at the initial touch. We recompute screen-coords of the lasso box
      // on every move and compare against each clip element's
      // getBoundingClientRect.
      const wrapForHitTest = lassoContainerRef.current
        ? lassoContainerRef.current.getBoundingClientRect()
        : { top: 0, left: 0 };
      // Convert the lasso (wrapper-coords) back to viewport-coords for
      // rect comparison.
      const lassoLeftVP   = wrapForHitTest.left + x - (laneScrollRef.current ? laneScrollRef.current.scrollLeft : 0) + (SIDEBAR_W || 0);
      const lassoTopVP    = wrapForHitTest.top  + y;
      const lassoRightVP  = lassoLeftVP + w;
      const lassoBottomVP = lassoTopVP  + h;
      const hit = new Set();
      // Query every clip in the DAW wrapper and check rect overlap.
      const root = lassoContainerRef.current;
      if (root) {
        const clipEls = root.querySelectorAll("[data-clipid]");
        clipEls.forEach(function(el) {
          const r = el.getBoundingClientRect();
          // No overlap if entirely above/below/left/right of lasso.
          if (r.right < lassoLeftVP || r.left > lassoRightVP) return;
          if (r.bottom < lassoTopVP || r.top  > lassoBottomVP) return;
          const cid = el.getAttribute("data-clipid");
          if (cid) hit.add(cid);
        });
      }
      setSelClipIds(hit);
    };

    const onEnd = function() {
      // Drop the visual lasso rectangle once the user lifts. The selection
      // ITSELF (selClipIds) survives — it was populated on every touchmove
      // tick via the hit-test above, so the highlighted clips stay
      // highlighted via their own border/glow styling. Without this, the
      // dashed rectangle hangs around after the gesture, making the
      // interface look stuck.
      selBoxRef.current = null;
      setSelBox(null);
      // Restore the scroll container's native gesture handling.
      scrollEl.style.touchAction = prevTouchAction;
      scrollEl.style.overflow    = prevOverflow;
      scrollEl.removeEventListener("touchmove", onMove);
      scrollEl.removeEventListener("touchend", onEnd);
    };

    scrollEl.addEventListener("touchmove", onMove, { passive: false });
    scrollEl.addEventListener("touchend", onEnd, { passive: true });
  };

  const handleLaneLongPress = function(e, track) {
    // Cancel if touch landed on a clip or a trim handle
    if (e.target && e.target.closest && (
      e.target.closest("[data-clipid]") ||
      e.target.closest("[data-trimhandle]")
    )) return;
    // Bail entirely on multi-touch — the user is pinching to zoom, not
    // attempting to lasso. Without this guard, the long-press timer would
    // still fire 0.6s after the first finger landed, hijacking the pinch
    // gesture and starting an unwanted selection box.
    if (e.touches && e.touches.length > 1) return;
    // Called from onTouchStart on blank lane space.
    // We start a 0.6s timer; if the finger doesn't move much, activate lasso.
    const touch = e.touches[0];
    const startX = touch.clientX;
    const startY = touch.clientY;

    // Cancel the lasso if the scroll container scrolls — that's a definite
    // "user is scrolling, not lassoing" signal. iOS sometimes commits a
    // scroll before the touch reports cross the 14px threshold (e.g. fast
    // flicks), and the finger-movement check below would miss it.
    const scrollEl = laneScrollRef.current;
    const onScrollCancel = function() {
      if (longPressRef.current) {
        clearTimeout(longPressRef.current);
        longPressRef.current = null;
      }
      if (scrollEl) scrollEl.removeEventListener("scroll", onScrollCancel);
    };
    if (scrollEl) scrollEl.addEventListener("scroll", onScrollCancel, { passive: true });

    longPressRef.current = setTimeout(function() {
      longPressRef.current = null;
      if (scrollEl) scrollEl.removeEventListener("scroll", onScrollCancel);
      // Haptic-style visual pulse then start lasso
      startLassoFromTouch(startX, startY);
    }, 600); // 0.6s — clear intent, doesn't fight with scroll

    const cancelLP = function(te) {
      if (!longPressRef.current) return;
      // Cancel immediately if a second finger lands during the long-press
      // window — that's a pinch gesture, not a lasso intent.
      if (te.touches && te.touches.length > 1) {
        clearTimeout(longPressRef.current);
        longPressRef.current = null;
        return;
      }
      // Cancel if finger moved more than 14px — gives the user normal
      // scroll headroom on iOS (10-12px is typical finger drift before
      // a scroll gesture commits). Previously 8px was too tight.
      const dx = te.touches[0].clientX - startX;
      const dy = te.touches[0].clientY - startY;
      if (Math.abs(dx) > 14 || Math.abs(dy) > 14) {
        clearTimeout(longPressRef.current);
        longPressRef.current = null;
      }
    };
    // Also watch touchstart globally so a SECOND finger landing during the
    // long-press window cancels the lasso. touchmove alone misses the case
    // where the user puts down a second finger but doesn't immediately move.
    const cancelOnSecondTouch = function(te) {
      if (longPressRef.current && te.touches && te.touches.length > 1) {
        clearTimeout(longPressRef.current);
        longPressRef.current = null;
      }
    };
    const cancelOnEnd = function() {
      if (longPressRef.current) { clearTimeout(longPressRef.current); longPressRef.current = null; }
      if (scrollEl) scrollEl.removeEventListener("scroll", onScrollCancel);
      document.removeEventListener("touchmove",  cancelLP);
      document.removeEventListener("touchend",   cancelOnEnd);
      document.removeEventListener("touchstart", cancelOnSecondTouch);
    };
    document.addEventListener("touchmove",  cancelLP,            { passive: true });
    document.addEventListener("touchstart", cancelOnSecondTouch, { passive: true });
    document.addEventListener("touchend",   cancelOnEnd,         { passive: true });
  };

  const deleteSelectedClips = function() {
    setTracks(function(prev) {
      return prev.map(function(t) {
        return { ...t, clips: (t.clips||[]).filter(function(cl){ return !selClipIds.has(cl.id); }) };
      });
    });
    setSelClipIds(new Set());
    setSelBox(null);
    setIsSaved(false);
  };

  // ── Clip duration helper (works for ALL clip types) ─────────────────────
  // Audio clips use trimmed/stretched length; drum + MIDI clips use .duration.
  const getClipDur = function(cl) {
    if (cl.audioBuffer) {
      const trimS = cl.trimStart || 0;
      const trimE = cl.trimEnd !== undefined ? cl.trimEnd : cl.audioBuffer.duration;
      return (cl.stretchMode && cl.stretchedDurationSec > 0)
        ? cl.stretchedDurationSec
        : Math.max(0.01, trimE - trimS);
    }
    return Math.max(0.01, cl.duration || 2);
  };

  // ── Find next empty slot across ALL tracks ────────────────────────────────
  // Returns the earliest start time >= `searchFrom` (snapped to grid) where
  // a block of `groupDuration` seconds fits on every involved track without
  // overlapping any existing clip. "Involved tracks" is the set of track IDs
  // in `trackIds` — for a paste group that spans three tracks, all three must
  // have a clear gap at the same time before we commit.
  //
  // Algorithm: scan existing clips on involved tracks, collect every occupied
  // interval [start, end], then walk forward from `searchFrom` until we find
  // a gap wide enough for `groupDuration`. Each candidate is snapped to the
  // grid so clips always land on beat.
  const findNextEmptySlot = function(trackIds, groupDuration, searchFrom) {
    const beatSec  = 60 / Math.max(1, bpm);
    const meta     = BF_SNAP_OPTIONS.find(function(s){ return s.id === snapId; }) || BF_SNAP_OPTIONS[0];
    const gridStep = (meta && meta.beatDiv > 0) ? (beatSec / meta.beatDiv) : (beatSec / 4);

    // Collect occupied intervals from all involved tracks (and all tracks if
    // trackIds is null, which means "check everything").
    const intervals = [];
    (tracksRef.current || []).forEach(function(t) {
      if (trackIds && !trackIds.has(t.id)) return;
      (t.clips || []).forEach(function(cl) {
        const s = cl.startTime || 0;
        const e = s + getClipDur(cl);
        intervals.push([s, e]);
      });
    });

    // Sort intervals by start time.
    intervals.sort(function(a, b){ return a[0] - b[0]; });

    // Snap the initial candidate to the grid.
    let candidate = Math.round(Math.max(0, searchFrom) / gridStep) * gridStep;

    // Walk forward until we find a gap. Cap at 10,000 iterations to prevent
    // infinite loops on pathological arrangements.
    for (let i = 0; i < 10000; i++) {
      const candidateEnd = candidate + groupDuration;
      let blocked = false;
      let jumpTo  = candidateEnd;
      for (let j = 0; j < intervals.length; j++) {
        const [is, ie] = intervals[j];
        // Does this interval overlap [candidate, candidateEnd)?
        // Two ranges overlap when start < other.end AND end > other.start.
        if (candidate < ie - 1e-4 && candidateEnd > is + 1e-4) {
          blocked = true;
          // Jump candidate to the end of this blocking interval (snapped up).
          if (ie > jumpTo) jumpTo = ie;
        }
      }
      if (!blocked) return candidate;
      // Snap the jump point to the next grid boundary.
      candidate = Math.ceil(jumpTo / gridStep) * gridStep;
    }
    return candidate; // fallback — should never reach here
  };

  // Shared paste-from-clipboard helper. Used by:
  //   • Paste button in the per-clip long-press menu
  //   • Paste button in the long-press-on-empty-space menu
  //
  // Pastes the current clipboard contents at the supplied playhead time.
  // Each clip lands on its OWN source track (the one it was copied from).
  // The EARLIEST clip in the clipboard is anchored at the playhead; the
  // rest are shifted by the same offset so the relative timing of the
  // group is preserved. If a clip's source track has been deleted since
  // copy, that clip is silently skipped (we don't surprise the user with
  // a paste landing on the wrong track).
  const pasteClipsAtPlayhead = function(playheadT) {
    if (!clipboardClips || !clipboardClips.clips || clipboardClips.clips.length === 0) return;

    const anchor = clipboardClips.anchorStart || 0;

    // Measure the clipboard group span so we know how wide a gap we need.
    let groupEarliestStart = Infinity;
    let groupLatestEnd     = -Infinity;
    clipboardClips.clips.forEach(function(src){
      const startT = src.startTime || 0;
      const endT   = startT + getClipDur(src);
      if (startT < groupEarliestStart) groupEarliestStart = startT;
      if (endT   > groupLatestEnd)     groupLatestEnd     = endT;
    });
    if (groupEarliestStart === Infinity) return;
    const groupSpan = groupLatestEnd - groupEarliestStart;

    // Search from the playhead. findNextEmptySlot will snap to the grid
    // and skip forward past any existing clips on the destination tracks,
    // so if the playhead is sitting on top of a clip the paste lands at
    // the next free snap position right after it — exactly what the user
    // expects. No viewport or source-end offset: paste goes where the
    // playhead is, full stop.
    const searchFrom = Math.max(0, playheadT);

    // Collect the set of destination track IDs so collision detection only
    // checks tracks that will actually receive pasted clips.
    const destTrackIds = new Set();
    clipboardClips.clips.forEach(function(src){
      if (src.__sourceTrackId != null) destTrackIds.add(src.__sourceTrackId);
    });

    // Find the first grid-snapped time where the full group fits without
    // overlapping anything on any of the destination tracks.
    const pasteStart = findNextEmptySlot(destTrackIds, groupSpan, searchFrom);

    const ts = Date.now();
    const newByTrack = {};
    const newIds = new Set();
    clipboardClips.clips.forEach(function(src, i){
      const destTrackId = src.__sourceTrackId;
      if (destTrackId == null) return;
      // Preserve the relative offset of each clip within the group.
      const relativeOffset = (src.startTime || 0) - groupEarliestStart;
      const newId = (src.id || "clip") + "_paste" + ts + "_" + i;
      const persisted = { ...src };
      delete persisted.__sourceTrackId;
      const newClip = {
        ...persisted,
        id: newId,
        startTime: +(pasteStart + relativeOffset).toFixed(4),
        label: (src.label || "Take") + " (paste)",
        active: true,
      };
      if (!newByTrack[destTrackId]) newByTrack[destTrackId] = [];
      newByTrack[destTrackId].push(newClip);
      newIds.add(newId);
    });

    setTracks(function(prev){
      return prev.map(function(tt){
        const adds = newByTrack[tt.id];
        if (!adds || adds.length === 0) return tt;
        return { ...tt, clips: [...(tt.clips || []), ...adds] };
      });
    });

    setTimeout(function(){ setSelClipIds(newIds); }, 0);
    setIsSaved(false);
    try { if (navigator.vibrate) navigator.vibrate(15); } catch(_){}
  };

  const duplicateSelectedClips = function() {
    // Measure the selected group so we know the group span + earliest start.
    let earliestStart = Infinity;
    let latestEnd     = -Infinity;
    const involvedTrackIds = new Set();

    (tracksRef.current || []).forEach(function(t){
      (t.clips || []).forEach(function(cl){
        if (!selClipIds.has(cl.id)) return;
        const startT = cl.startTime || 0;
        const endT   = startT + getClipDur(cl);
        if (startT < earliestStart) earliestStart = startT;
        if (endT   > latestEnd)     latestEnd     = endT;
        involvedTrackIds.add(t.id);
      });
    });
    if (earliestStart === Infinity) return; // nothing selected

    const groupSpan = latestEnd - earliestStart;

    // Search from the end of the source group. findNextEmptySlot will
    // snap to the grid and skip over any occupied space, so the duplicate
    // always lands flush after the source (or the next free beat if
    // something is already there). Viewport is irrelevant here — the
    // duplicate goes next to what was selected, not next to what's visible.
    const searchFrom = latestEnd;

    const dupStart = findNextEmptySlot(involvedTrackIds, groupSpan, searchFrom);
    const shift = dupStart - earliestStart;

    const newIds = new Set();
    const ts = Date.now();
    setTracks(function(prev){
      return prev.map(function(t){
        if (!involvedTrackIds.has(t.id)) return t;
        const newClips = [];
        (t.clips || []).forEach(function(cl){
          newClips.push(cl);
          if (selClipIds.has(cl.id)) {
            const dupId = cl.id + "_dup" + ts;
            newIds.add(dupId);
            newClips.push({
              ...cl,
              id: dupId,
              startTime: +((cl.startTime || 0) + shift).toFixed(4),
              label: (cl.label || "Take") + " (copy)",
            });
          }
        });
        return { ...t, clips: newClips };
      });
    });

    setTimeout(function(){ setSelClipIds(newIds); }, 0);
    setSelBox(null);
    setIsSaved(false);
  };

  const handleRegionRightClick = function (e, track, clip) {
    e.preventDefault();
    e.stopPropagation();
    selectClip(clip.id);
    showClipMenu(track, clip, e.clientX, e.clientY);
  };

  const handleRegionTouchStart = function (e, track, clip) {
    if (e.touches.length === 2) return;
    e.stopPropagation();
    selectClip(clip.id);
    const startX = e.touches[0].clientX;
    const startY = e.touches[0].clientY;
    const startT = clip.startTime || 0;
    const menuX  = e.touches[0].clientX;
    const menuY  = e.touches[0].clientY - 60;
    const startTrackIdx = tracksRef.current.findIndex(function(t){ return t.id === track.id; });

    // If this clip is part of a multi-selection, snapshot all selected clips' start times
    // so we can shift them all by the same delta during drag.
    const isMultiDrag = selClipIds.size > 1 && selClipIds.has(clip.id);
    const multiStartTimes = {}; // { clipId: startTime }
    if (isMultiDrag) {
      tracksRef.current.forEach(function(t) {
        (t.clips || []).forEach(function(cl) {
          if (selClipIds.has(cl.id)) {
            multiStartTimes[cl.id] = cl.startTime || 0;
          }
        });
      });
    }

    // Long-press timer — fires if no drag occurs within 500ms
    const lp = setTimeout(function () {
      if (dragRef.current && !dragRef.current.moved) {
        showClipMenu(track, clip, menuX, menuY);
      }
    }, 500);

    dragRef.current = { trackId: track.id, clipId: clip.id, startX, startT, moved: false, lp };
    // Track which row the finger is currently over so we can move the clip on release
    let lastTargetIdx = startTrackIdx;
    let pendingStartT = startT;

    // Add non-passive touchmove directly on the element so we can preventDefault
    // This stops the scroll container from stealing the gesture during clip drag
    const elem = e.currentTarget;
    const onMove = function (me) {
      if (!dragRef.current) return;
      // If a second finger lands mid-drag, the user is starting a pinch-zoom
      // gesture. Abort the clip drag immediately so the pinch handler on the
      // scroll container can take over. Without this, the drag onMove keeps
      // reading touches[0] while the pinch handler reads both fingers, and
      // the clip jumps around as the user's fingers move during the zoom.
      if (me.touches && me.touches.length >= 2) {
        clearTimeout(dragRef.current.lp);
        // If the drag had already started moving the clip, end the history
        // gesture so the partial drag becomes its own undo entry (rather
        // than mysteriously coalescing with later edits).
        if (dragRef.current.moved) {
          try { endTracksGesture(); } catch(_) {}
        }
        dragRef.current = null;
        elem.removeEventListener("touchmove", onMove);
        elem.removeEventListener("touchend",  onEnd);
        return;
      }
      me.preventDefault(); // must be non-passive to work
      const dx = me.touches[0].clientX - dragRef.current.startX;
      const dy = me.touches[0].clientY - startY;
      // Treat movement in either axis as a drag (cancels the long-press menu)
      if (Math.abs(dx) > 6 || Math.abs(dy) > 6) {
        clearTimeout(dragRef.current.lp);
        if (!dragRef.current.moved) {
          // First confirmed movement of this drag — open a history gesture
          // so all subsequent updateClip calls coalesce into ONE undo step.
          beginTracksGesture();
        }
        dragRef.current.moved = true;
        if (isMultiDrag) {
          // Shift every selected clip by the same delta, keeping their relative positions
          const deltaSecs = dx / effectivePPSRef.current;
          setTracks(function(prev) {
            return prev.map(function(t) {
              return {
                ...t,
                clips: (t.clips || []).map(function(cl) {
                  if (!selClipIds.has(cl.id)) return cl;
                  const newT = snapSecs(Math.max(0, multiStartTimes[cl.id] + deltaSecs));
                  return { ...cl, startTime: newT };
                }),
              };
            });
          });
        } else {
          pendingStartT = snapSecs(dragRef.current.startT + dx / effectivePPS);
          updateClip(dragRef.current.trackId, dragRef.current.clipId, { startTime: pendingStartT });
        }
        // Track target row for vertical drag — only applies to single-clip drag.
        // Multi-select drag stays in original rows.
        if (!isMultiDrag) {
          const rowDelta = Math.round(dy / TRACK_H);
          lastTargetIdx = Math.max(0, Math.min(tracksRef.current.length - 1, startTrackIdx + rowDelta));
        }
      }
    };
    const onEnd = function () {
      clearTimeout(dragRef.current && dragRef.current.lp);
      // If user dropped on a different track row, transfer the clip now
      if (!isMultiDrag && dragRef.current && dragRef.current.moved && lastTargetIdx !== startTrackIdx) {
        const targetTrack = tracksRef.current[lastTargetIdx];
        if (targetTrack && targetTrack.id !== track.id) {
          moveClipToTrack(track.id, clip.id, targetTrack.id, { startTime: pendingStartT });
        }
      }
      // Re-baseline the clip's nudge origin to its new drop position so
      // Reset snaps back here. Only when the clip actually moved (a tap
      // without movement shouldn't reset the baseline).
      if (dragRef.current && dragRef.current.moved) {
        try { rebaseClipNudgeOrigin(clip.id, pendingStartT); } catch(e) {}
        // Close the history gesture — one undo entry now represents the
        // entire drag (including row-change + nudge rebase).
        endTracksGesture();
      }
      dragRef.current = null;
      elem.removeEventListener("touchmove", onMove);
      elem.removeEventListener("touchend",  onEnd);
    };
    elem.addEventListener("touchmove", onMove, { passive: false });
    elem.addEventListener("touchend",  onEnd,  { passive: true  });
  };

  // handleRegionTouchMove and handleRegionTouchEnd are now handled by imperative listeners above
  const handleRegionTouchMove = function () {}; // kept for any legacy references
  const handleRegionTouchEnd  = function () {};

  // ── Pinch zoom ────────────────────────────────────────────────
  // Logic Pro / BandLab behaviour — but strictly axis-locked:
  //   • Horizontal pinch (fingers moving apart/together left-right) → H-zoom only
  //   • Vertical pinch   (fingers moving apart/together up-down)    → V-zoom only
  //   • Diagonal pinches: lock to whichever axis is moving MORE during the
  //     gesture (not which axis is wider at start). Once an axis is locked
  //     it never switches. This prevents the timeline jittering both
  //     horizontally and vertically at the same time.
  useEffect(function () {
    const el = scrollRef.current;
    if (!el) return;
    const onStart = function (e) {
      if (e.touches.length !== 2) return;
      const dx = e.touches[0].clientX - e.touches[1].clientX;
      const dy = e.touches[0].clientY - e.touches[1].clientY;
      const midX = (e.touches[0].clientX + e.touches[1].clientX) / 2;
      pinchRef.current = {
        startDx: dx,
        startDy: dy,
        distX: Math.abs(dx),
        distY: Math.abs(dy),
        // axis stays null until the user's pinch *movement* clearly favours
        // one direction (see onMove). This avoids mislocking on diagonal
        // gestures.
        axis: null,
        zoom:  zoomRef.current,
        vZoom: vZoom,
        midX,
        scrollLeft: el.scrollLeft,
      };
    };
    const onMove = function (e) {
      if (e.touches.length !== 2 || !pinchRef.current) return;
      e.preventDefault();
      const dx = e.touches[0].clientX - e.touches[1].clientX;
      const dy = e.touches[0].clientY - e.touches[1].clientY;
      const absDx = Math.abs(dx);
      const absDy = Math.abs(dy);

      // ── Determine axis from how much the pinch has CHANGED in each axis ──
      // (not from initial span — that biases against perfectly horizontal/
      // vertical finger placements).
      if (pinchRef.current.axis === null) {
        const movedX = Math.abs(absDx - pinchRef.current.distX);
        const movedY = Math.abs(absDy - pinchRef.current.distY);
        // Require at least 8px of movement before committing AND a clear
        // 1.5× dominance to avoid wobbly axis switches on diagonal pinches.
        if (movedX < 8 && movedY < 8) return;
        if (movedX >= movedY * 1.5) {
          pinchRef.current.axis = "H";
        } else if (movedY >= movedX * 1.5) {
          pinchRef.current.axis = "V";
        } else {
          // Still ambiguous — wait for more decisive movement.
          return;
        }
      }

      // ── Apply zoom on the LOCKED axis only ──
      if (pinchRef.current.axis === "H" && pinchRef.current.distX > 4) {
        const newZoom = Math.min(1, Math.max(0.1,
          +(pinchRef.current.zoom * absDx / pinchRef.current.distX).toFixed(3)));
        const ratio  = newZoom / pinchRef.current.zoom;
        const pivotX = pinchRef.current.midX - el.getBoundingClientRect().left + pinchRef.current.scrollLeft;
        el.scrollLeft = Math.max(0, pivotX * ratio - (pinchRef.current.midX - el.getBoundingClientRect().left));
        setZoom(newZoom);
      } else if (pinchRef.current.axis === "V" && pinchRef.current.distY > 4) {
        const newVZoom = Math.min(0.99, Math.max(0.46,
          +(pinchRef.current.vZoom * absDy / pinchRef.current.distY).toFixed(3)));
        setVZoom(newVZoom);
      }
    };
    const onEnd = function () { pinchRef.current = null; };
    el.addEventListener("touchstart", onStart, { passive: true });
    el.addEventListener("touchmove",  onMove,  { passive: false });
    el.addEventListener("touchend",   onEnd,   { passive: true });
    return function () {
      el.removeEventListener("touchstart", onStart);
      el.removeEventListener("touchmove",  onMove);
      el.removeEventListener("touchend",   onEnd);
    };
  }, [vZoom]);  // re-bind when vZoom changes so snapshot stays fresh

  // ── Metronome ─────────────────────────────────────────────────
  // Ticks ONLY when transport is playing. Scheduled in audio time
  // against the main AudioContext (same clock that drives drums &
  // MIDI) so it stays locked to the beat. We compute each beat's
  // audio time from masterStartRef + (beatPlayheadPos - playheadAtRef),
  // which lets us survive seeks and loop restarts — when those refs
  // change, the next pump naturally aligns to the new playhead.
  useEffect(function () {
    if (!metronomeOn || !isPlaying) {
      clearInterval(metroRef.current);
      metroRef.current = null;
      return;
    }
    const actx = actxRef.current;
    if (!actx) return;
    const dest = masterGainRef.current || actx.destination;

    const beatSec  = 60 / Math.max(1, bpm);
    // Output-latency compensation: drums and MIDI travel through the
    // full FX chain (pan, gain, dynamics, etc) which adds ~20-40ms of
    // processing latency that the metronome's direct path doesn't have.
    // Without this, the metronome ticks audibly EARLIER than drum hits
    // on the same beat. We delay metronome scheduling by a small fixed
    // offset so it lines up with what the user hears from drums.
    const METRO_OUTPUT_OFFSET = 0.025;  // 25ms

    // Track the last masterStartRef + playheadAtRef seen so we can
    // detect seeks/loop restarts and reset our beat counter.
    let lastMasterStart = NaN;
    let lastPlayheadAt  = NaN;
    let nextBeatIndex   = 0;

    const scheduleOneTick = function(when, beatIdx) {
      const osc = actx.createOscillator();
      const g   = actx.createGain();
      osc.connect(g); g.connect(dest);
      osc.frequency.value = (beatIdx % Math.max(1, timeSigNum) === 0) ? 1000 : 700;
      const t = when + METRO_OUTPUT_OFFSET;
      g.gain.setValueAtTime(0.3, t);
      g.gain.exponentialRampToValueAtTime(0.001, t + 0.05);
      osc.start(t);
      osc.stop(t + 0.1);
    };

    const pump = function() {
      const ms = masterStartRef.current;
      const ph = playheadAtRef.current;
      if (!Number.isFinite(ms)) return;
      // Detect a transport reset (seek, loop restart, play). When
      // either ref changes meaningfully, reset our beat counter so
      // we start from the current playhead position.
      if (Math.abs(ms - lastMasterStart) > 0.01 || Math.abs(ph - lastPlayheadAt) > 0.01) {
        lastMasterStart = ms;
        lastPlayheadAt  = ph;
        // Round up to the next whole beat at or after the current
        // playhead so we don't miss the downbeat after a restart.
        nextBeatIndex = Math.max(0, Math.ceil(ph / beatSec - 1e-6));
      }
      const now = actx.currentTime;
      const horizon = now + 0.25;
      let safety = 0;
      while (safety++ < 1000) {
        const targetPlayhead = nextBeatIndex * beatSec;
        const idealAudioTime = ms + (targetPlayhead - ph);
        if (idealAudioTime > horizon) break;
        // If the ideal time is already in the past, schedule the
        // tick for the next safe moment (a few ms ahead). Better
        // a slightly-late first tick than a dropped one — the
        // following beats will be on time because they reference
        // idealAudioTime not the late-scheduled time.
        const scheduledAt = Math.max(idealAudioTime, now + 0.005);
        scheduleOneTick(scheduledAt, nextBeatIndex);
        nextBeatIndex++;
      }
    };
    pump();
    metroRef.current = setInterval(pump, 50);
    return function () {
      clearInterval(metroRef.current);
      metroRef.current = null;
    };
  }, [metronomeOn, isPlaying, bpm, timeSigNum]);

  // ── Save/Load ─────────────────────────────────────────────────
  const audioBufferToBase64 = function (buf) {
    try {
      const nc=buf.numberOfChannels, sr=buf.sampleRate, len=buf.length, bl=len*nc*2;
      const ab=new ArrayBuffer(44+bl), dv=new DataView(ab);
      const ws=function(o,s){for(let i=0;i<s.length;i++)dv.setUint8(o+i,s.charCodeAt(i));};
      ws(0,"RIFF");dv.setUint32(4,36+bl,true);ws(8,"WAVE");ws(12,"fmt ");
      dv.setUint32(16,16,true);dv.setUint16(20,1,true);dv.setUint16(22,nc,true);
      dv.setUint32(24,sr,true);dv.setUint32(28,sr*nc*2,true);dv.setUint16(32,nc*2,true);
      dv.setUint16(34,16,true);ws(36,"data");dv.setUint32(40,bl,true);
      let off=44;
      for(let i=0;i<len;i++)for(let c=0;c<nc;c++){
        const s=Math.max(-1,Math.min(1,buf.getChannelData(c)[i]));
        dv.setInt16(off,s<0?s*0x8000:s*0x7FFF,true);off+=2;
      }
      const bytes=new Uint8Array(ab);
      let b64=""; for(let i=0;i<bytes.length;i++)b64+=String.fromCharCode(bytes[i]);
      return btoa(b64);
    } catch(e){ return null; }
  };

  const base64ToAudioBuffer = async function (b64) {
    try {
      const bin=atob(b64);
      const ab=new ArrayBuffer(bin.length);
      const ua=new Uint8Array(ab);
      for(let i=0;i<bin.length;i++)ua[i]=bin.charCodeAt(i);
      const actx=getActx();
      return await actx.decodeAudioData(ab);
    } catch(e){ return null; }
  };

  const saveProject = async function () {
    try {
      // Stop any active playback / recording BEFORE snapshotting tracks.
      // Saving while audio is being scheduled causes glitches and, because the
      // WAV encoding below blocks the main thread, the transport can appear
      // frozen ("couldn't stop the song"). We stop synchronously, flip the UI
      // state, show the Saving banner, then YIELD one frame so React actually
      // paints the stopped transport + status before the heavy encode begins.
      try {
        if (isPlayingRef.current || isRecording) {
          stopAll();
          setIsPlaying(false);
          setIsRecording(false);
        }
        try { if (typeof rackPatStop === "function") rackPatStop(); } catch(_){}
      } catch(_){}

      setSaveStatus("Saving...");
      setIsSaved(false);

      // Yield to the event loop twice so the stopped transport + "Saving..."
      // banner render before the synchronous audioBufferToWav calls (which can
      // block for a second on large sessions) lock up the main thread.
      await new Promise(function(r){ requestAnimationFrame(function(){ requestAnimationFrame(r); }); });

      // Generate a stable project id — reuse existing one if this project was already saved
      let list = JSON.parse(localStorage.getItem("bf_studio_projects")||"[]");
      const existingIdx = list.findIndex(function(p){ return p.name===projectName; });
      const projId = (existingIdx >= 0 && list[existingIdx].id) ? list[existingIdx].id : Date.now();

      // Track per-clip save outcomes so we can surface real failures to
      // the user (instead of saying "Saved! ✓" when half the audio was
      // silently dropped on quota / IDB errors).
      let clipsFailed = 0;
      let clipsAttempted = 0;

      // ── 1. Save each clip's audio into IndexedDB (no size limit) ──────────
      const savedTracks = await Promise.all(tracks.map(async function(t){
        const savedClips = await Promise.all((t.clips||[]).map(async function(cl){
          // Key: "proj_<projId>_<clipId>"
          const idbKey = "proj_" + projId + "_" + cl.id;
          if (cl.audioBuffer) {
            // Convert AudioBuffer → WAV ArrayBuffer and store in IndexedDB
            const wavBuf = audioBufferToWav(cl.audioBuffer);
            clipsAttempted++;
            try {
              await AudioDB.saveClip(idbKey, wavBuf);
            } catch(e) {
              clipsFailed++;
              console.warn("IDB save clip failed", idbKey, e);
            }
          }
          // Return clip metadata only — no audio blobs in localStorage
          return { ...cl, audioBuffer:null, url:null, blob:null, audioB64:null, idbKey };
        }));

        // Legacy flat audioBuffer (beat tracks)
        const trackIdbKey = "proj_" + projId + "_track_" + t.id;
        if (t.audioBuffer) {
          const wavBuf = audioBufferToWav(t.audioBuffer);
          clipsAttempted++;
          try {
            await AudioDB.saveClip(trackIdbKey, wavBuf);
          } catch(e) {
            clipsFailed++;
            console.warn("IDB save track failed", trackIdbKey, e);
          }
        }

        return { ...t, audioBuffer:null, url:null, blob:null, audioB64:null, trackIdbKey, clips:savedClips };
      }));

      // ── 2. Save metadata to localStorage (tiny — just names/timing/FX) ───
      // CRITICAL: Preserve cloudId from the existing entry. The cloud sync
      // stamps cloudId back onto the local doc after first save so the
      // second save can UPDATE the same backend row. Without this preserve,
      // every save creates a duplicate cloud project.
      const existingCloudId    = (existingIdx >= 0 && list[existingIdx].cloudId)    || null;
      const existingCloudSavedAt = (existingIdx >= 0 && list[existingIdx].cloudSavedAt) || null;
      const proj = {
        id:projId, name:projectName, bpm, timeSigNum, projectKey,
        savedAt:new Date().toISOString(), tracks:savedTracks,
        cloudId: existingCloudId,
        cloudSavedAt: existingCloudSavedAt,
      };
      if (existingIdx >= 0) list[existingIdx] = proj; else list.unshift(proj);
      list = list.slice(0, 10);
      try {
        localStorage.setItem("bf_studio_projects", JSON.stringify(list));
      } catch(quotaErr) {
        list = list.slice(0, 3);
        localStorage.setItem("bf_studio_projects", JSON.stringify(list));
      }

      setSavedProjects(list);

      if (clipsFailed > 0 && clipsFailed === clipsAttempted) {
        // EVERY clip failed — almost certainly quota / IDB unavailable.
        // Don't mark the project saved because reloading it would
        // restore an empty session.
        setIsSaved(false);
        setSaveStatus("");
        setError("Couldn't save audio — your browser's storage may be full. Project metadata saved but clips will be lost. Try removing other saved projects.");
        setTimeout(function(){ setError(""); }, 8000);
      } else if (clipsFailed > 0) {
        setIsSaved(true);
        setSaveStatus("");
        setError("Saved with " + clipsFailed + " of " + clipsAttempted + " clips missing — storage may be near full.");
        setTimeout(function(){ setError(""); }, 6000);
      } else {
        setIsSaved(true);
        setSaveStatus("Saved! ✓"); setTimeout(function(){ setSaveStatus(""); }, 2500);
      }

      // ── Cloud sync (Pro feature) ─────────────────────────────────────────
      // Phase 1: project METADATA (track config, FX state, BPM, clip
      //          positions/durations) pushed to backend MongoDB.
      // Phase 2 (THIS): vocal recordings uploaded to Cloudinary so audio
      //          survives PWA reinstall. Beat clips remain local-only
      //          for now (they're already uploaded files the user has).
      //
      // Sequence:
      //   1. Save project metadata first (gets us a cloud ID we need
      //      to namespace the Cloudinary uploads).
      //   2. For each VOCAL clip without an existing vocalUrl, upload
      //      its audio buffer to Cloudinary and capture the secure_url.
      //   3. Re-save the project metadata with the new vocalUrls baked
      //      into the clips array.
      //
      // All of this runs in the background AFTER the local save has
      // succeeded. User sees "Saved ✓" instantly; the cloud bits don't
      // block UI. Failures are logged but never shown unless unexpected.
      (async function cloudSync() {
        try {
          // Build the cloud-shaped track list. PRESERVE existing vocalUrl
          // from prior saves so we don't re-upload audio that's already
          // in Cloudinary. The original `tracks` state still has the
          // audio buffers we'll need for fresh uploads — savedTracks (the
          // post-IDB-save copy) has them nulled out.
          // Build the cloud-shaped track list. We serialize the COMPLETE
          // track + clip objects, stripping ONLY the runtime-only fields
          // that either can't be JSON-serialized (AudioBuffer) or are
          // re-derived on load (url/blob). Everything else — notes,
          // patterns, samplerData, instrumentId, instrumentOverrides,
          // selectedSfPreset, kitId, noteCutMode, loop settings, and any
          // future field — is preserved verbatim.
          //
          // This is a DENYLIST (strip these few) rather than an ALLOWLIST
          // (keep these few). The previous allowlist silently dropped
          // notes and patterns, so projects reconstructed from the cloud
          // were hollow skeletons. A denylist preserves everything by
          // default, which is the only safe posture for user data.
          const RUNTIME_ONLY_CLIP_FIELDS = ["audioBuffer", "url", "blob", "audioB64"];
          const RUNTIME_ONLY_TRACK_FIELDS = ["audioBuffer", "url", "blob", "audioB64"];
          const stripRuntime = function(obj, fields) {
            const out = {};
            for (const k in obj) {
              if (!Object.prototype.hasOwnProperty.call(obj, k)) continue;
              if (fields.indexOf(k) !== -1) continue;
              out[k] = obj[k];
            }
            return out;
          };
          const cloudTracks = savedTracks.map(function(savedT, ti) {
            const liveT = tracks[ti] || savedT;
            const cleanClips = (savedT.clips || []).map(function(cl, ci) {
              const liveClip = (liveT.clips || [])[ci] || cl;
              // Start from the FULL saved clip (audio already nulled by the
              // IDB-save step) and strip any remaining runtime fields.
              const full = stripRuntime(cl, RUNTIME_ONLY_CLIP_FIELDS);
              // Preserve an existing cloud vocal URL if present so we don't
              // re-upload unchanged audio.
              full.vocalUrl = liveClip.vocalUrl || cl.vocalUrl || null;
              full.idbKey   = cl.idbKey || null;
              return full;
            });
            const fullTrack = stripRuntime(savedT, RUNTIME_ONLY_TRACK_FIELDS);
            fullTrack.clips = cleanClips;
            fullTrack.trackIdbKey = savedT.trackIdbKey || null;
            return fullTrack;
          });

          // For the cloud, project ID is a Mongo ObjectId string — the
          // backend assigns it on first save. We persist the cloud ID
          // back on the local project doc so subsequent saves update
          // (not duplicate).
          const body = {
            id:            proj.cloudId || null,
            name:          proj.name,
            bpm:           proj.bpm,
            key:           proj.projectKey,
            time_sig_num:  proj.timeSigNum,
            tracks:        cloudTracks,
          };
          const resp = await apiFetch("/api/studio/projects/save", {
            method: "POST",
            body:   JSON.stringify(body),
          });

          // Stamp the returned cloud ID back onto the local project doc
          const cloudProjectId = resp && resp.id;
          if (cloudProjectId) {
            const cur = JSON.parse(localStorage.getItem("bf_studio_projects") || "[]");
            const idx = cur.findIndex(function(p) { return p.id === proj.id; });
            if (idx >= 0) {
              cur[idx].cloudId    = cloudProjectId;
              cur[idx].cloudSavedAt = resp.saved_at;
              localStorage.setItem("bf_studio_projects", JSON.stringify(cur));
              setSavedProjects(cur);
            }
          }

          // ── Phase 2: Upload vocal recordings to Cloudinary ──────────────
          // Now that we have a cloud project ID, upload any vocal clips
          // that haven't been uploaded yet. The `tracks` variable here
          // still has the LIVE AudioBuffers (state hasn't changed during
          // the save flow). audioBufferToWav is already used by the IDB
          // save above so we know it works.
          if (!cloudProjectId) return; // first save failed, nothing to upload to

          const uploadsNeeded = [];
          tracks.forEach(function(t, ti) {
            if (t.type !== "vocal") return; // beats not uploaded in Phase 2
            (t.clips || []).forEach(function(cl, ci) {
              // Skip if already uploaded (vocalUrl set from prior save)
              const cloudClip = cloudTracks[ti] && cloudTracks[ti].clips[ci];
              if (cloudClip && cloudClip.vocalUrl) return;
              // Need a live audio buffer to upload from
              if (!cl.audioBuffer) return;
              uploadsNeeded.push({ trackIdx: ti, clipIdx: ci, clip: cl });
            });
          });

          if (uploadsNeeded.length === 0) return;

          // Upload each vocal clip one at a time (sequential — avoid
          // saturating mobile uplink with parallel requests).
          let uploadsCompleted = 0;
          for (const job of uploadsNeeded) {
            try {
              // 1. Get a signed upload signature from our backend
              const sig = await apiFetch("/api/studio/projects/sign-vocal-upload", {
                method: "POST",
                body:   JSON.stringify({
                  project_id: cloudProjectId,
                  clip_id:    String(job.clip.id).replace(/[^A-Za-z0-9._-]/g, "_"),
                }),
              });
              if (!sig || !sig.upload_url) throw new Error("No signature returned");

              // 2. Convert audio buffer → WAV blob
              const wavBuf  = audioBufferToWav(job.clip.audioBuffer);
              const wavBlob = new Blob([wavBuf], { type: "audio/wav" });

              // 3. POST directly to Cloudinary with the signed params
              const fd = new FormData();
              fd.append("api_key",   sig.api_key);
              fd.append("timestamp", String(sig.timestamp));
              fd.append("folder",    sig.folder);
              fd.append("public_id", sig.public_id);
              fd.append("signature", sig.signature);
              fd.append("file",      wavBlob, "vocal.wav");

              const cloudResp = await fetch(sig.upload_url, {
                method: "POST",
                body:   fd,
              });
              if (!cloudResp.ok) {
                throw new Error("Cloudinary upload returned " + cloudResp.status);
              }
              const cloudData = await cloudResp.json();
              const vocalUrl  = cloudData && cloudData.secure_url;
              if (!vocalUrl) throw new Error("Cloudinary did not return secure_url");

              // 4. Stamp the URL into our cloudTracks for re-save
              cloudTracks[job.trackIdx].clips[job.clipIdx].vocalUrl = vocalUrl;
              uploadsCompleted++;
            } catch (uploadErr) {
              console.warn("[BeatFinder] Vocal upload failed (clip ", job.clip.id, "):", uploadErr);
              // Continue with next clip — partial upload is better than none
            }
          }

          // If at least one upload succeeded, re-save the project so the
          // cloud doc reflects the new URLs. The metadata save endpoint
          // performs upsert by `id`, so this is an in-place update.
          if (uploadsCompleted > 0) {
            try {
              await apiFetch("/api/studio/projects/save", {
                method: "POST",
                body:   JSON.stringify({
                  id:            cloudProjectId,
                  name:          proj.name,
                  bpm:           proj.bpm,
                  key:           proj.projectKey,
                  time_sig_num:  proj.timeSigNum,
                  tracks:        cloudTracks,
                }),
              });
              // Also mirror vocalUrl onto local state so future saves
              // skip re-upload. Update tracks state in-place.
              setTracks(function(prev) {
                return prev.map(function(t, ti) {
                  if (t.type !== "vocal") return t;
                  return Object.assign({}, t, {
                    clips: (t.clips || []).map(function(cl, ci) {
                      const cu = cloudTracks[ti] && cloudTracks[ti].clips[ci];
                      if (cu && cu.vocalUrl && !cl.vocalUrl) {
                        return Object.assign({}, cl, { vocalUrl: cu.vocalUrl });
                      }
                      return cl;
                    }),
                  });
                });
              });
            } catch (resaveErr) {
              console.warn("[BeatFinder] Re-save after vocal upload failed:", resaveErr);
            }
          }
        } catch (cloudErr) {
          // 403 (free tier) and 401 (logged out) are expected — silent.
          // Everything else is surfaced so the user knows their cloud
          // backup didn't complete (critical: a silent cloud failure is
          // how projects get lost on the next PWA reinstall).
          var msg = (cloudErr && cloudErr.message) || "";
          if (msg.includes("403") || msg.includes("401")) {
            // expected, silent
          } else if (msg.includes("413")) {
            setError("This project is too large to back up to the cloud. It's saved on this device, but won't survive a reinstall. Try splitting it into smaller projects.");
            setTimeout(function(){ setError(""); }, 9000);
          } else {
            console.warn("[BeatFinder] Cloud sync failed (local save OK):", cloudErr);
            setError("Cloud backup failed — your project is saved on this device only. Check your connection and save again.");
            setTimeout(function(){ setError(""); }, 7000);
          }
        }
      })();
    } catch(e){
      console.error("[BeatFinder] saveProject error:", e);
      setSaveStatus("Save failed — " + (e.message || "unknown error"));
      setTimeout(function(){ setSaveStatus(""); }, 4000);
    }
  };

  // ── Cloud project restore (Phase 3 Part B) ───────────────────────
  // When user taps a cloud-only project in the picker, we need to:
  //   1. Fetch the full project doc from /api/studio/projects/get/{id}
  //   2. Download each vocal clip's audio from Cloudinary
  //   3. Decode each audio file into an AudioBuffer
  //   4. Save the audio back to local IndexedDB so future loads are fast
  //   5. Hand off to loadProject as if it were a local project
  //
  // SAFETY:
  //   • loadProject itself is UNTOUCHED — we just reuse it as the final step
  //   • Beat clips have no cloud URL yet (Phase 2 only uploads vocals).
  //     They restore with structure but no audio — user gets a warning.
  //   • Individual clip failures don't kill the whole restore.
  const loadCloudProject = async function (cloudId, displayName) {
    setShowProjects(false);
    setSaveStatus("Restoring from cloud…");
    setError("");

    try {
      // 1. Fetch the full cloud doc
      const cloudDoc = await apiFetch("/api/studio/projects/get/" + encodeURIComponent(cloudId));
      if (!cloudDoc || !cloudDoc.tracks) {
        throw new Error("Project data not found in cloud");
      }

      const actx = getActx();
      if (actx.state === "suspended") {
        try { await actx.resume(); } catch(_) {}
      }

      // 2-3. For each clip with a vocalUrl, fetch & decode in parallel.
      // Use a local project id (numeric Date.now) for IndexedDB keys,
      // matching the format used by saveProject (proj_<id>_<clipId>).
      const localProjId = Date.now();
      let totalVocals = 0;
      let restoredVocals = 0;
      let failedVocals  = 0;

      // First pass: count vocals so we can show progress
      cloudDoc.tracks.forEach(function(t) {
        (t.clips || []).forEach(function(cl) {
          if (cl.vocalUrl) totalVocals++;
        });
      });

      const restoredTracks = await Promise.all(cloudDoc.tracks.map(async function(t, ti) {
        const restoredClips = await Promise.all((t.clips || []).map(async function(cl, ci) {
          // No vocal URL → no audio to restore (beat clips, or vocals
          // saved before Phase 2 upload landed). Restore structure only.
          if (!cl.vocalUrl) {
            return Object.assign({}, cl, {
              audioBuffer: null,
              url:         null,
              blob:        null,
            });
          }
          try {
            // Fetch the audio file from Cloudinary
            const resp = await fetch(cl.vocalUrl);
            if (!resp.ok) throw new Error("HTTP " + resp.status);
            const ab = await resp.arrayBuffer();
            // Decode — slice so the buffer isn't detached for later IDB save
            const buf = await actx.decodeAudioData(ab.slice(0));

            // Cache to IndexedDB so the next open is instant (no cloud fetch)
            const idbKey = "proj_" + localProjId + "_" + cl.id;
            try {
              const wavBuf = audioBufferToWav(buf);
              await AudioDB.saveClip(idbKey, wavBuf);
            } catch(idbErr) {
              console.warn("[BeatFinder] Cloud restore: IDB cache failed for", cl.id, idbErr);
              // Continue anyway — buf is in memory, user can use this session
            }

            restoredVocals++;
            setSaveStatus("Restoring from cloud… " + restoredVocals + "/" + totalVocals);

            return Object.assign({}, cl, {
              audioBuffer: buf,
              url:         null,
              blob:        null,
              idbKey:      idbKey,
            });
          } catch (e) {
            failedVocals++;
            console.warn("[BeatFinder] Cloud restore: clip", cl.id, "failed:", e);
            return Object.assign({}, cl, {
              audioBuffer: null,
              url:         null,
              blob:        null,
            });
          }
        }));
        return Object.assign({}, t, { clips: restoredClips });
      }));

      // 4. Build a local-shape project object. Reuse fields from cloud doc.
      const localProj = {
        id:           localProjId,
        cloudId:      cloudDoc.id,
        cloudSavedAt: cloudDoc.updated_at,
        name:         cloudDoc.name || displayName || "Untitled",
        bpm:          cloudDoc.bpm || 120,
        timeSigNum:   cloudDoc.time_sig_num || 4,
        projectKey:   cloudDoc.key || "C major",
        savedAt:      new Date().toISOString(),
        tracks:       restoredTracks,
      };

      // 5. Save metadata to localStorage so it appears in future picker
      //    loads without needing a cloud fetch.
      try {
        let list = JSON.parse(localStorage.getItem("bf_studio_projects") || "[]");
        // Strip live AudioBuffers — same shape the regular save uses
        const cleanTracks = restoredTracks.map(function(t) {
          return Object.assign({}, t, {
            audioBuffer: null,
            url:         null,
            blob:        null,
            clips: (t.clips || []).map(function(cl) {
              return Object.assign({}, cl, {
                audioBuffer: null,
                url:         null,
                blob:        null,
              });
            }),
          });
        });
        const persisted = Object.assign({}, localProj, { tracks: cleanTracks });
        list.unshift(persisted);
        list = list.slice(0, 10);
        localStorage.setItem("bf_studio_projects", JSON.stringify(list));
        setSavedProjects(list);
      } catch(persistErr) {
        console.warn("[BeatFinder] Could not persist restored project to localStorage:", persistErr);
      }

      // 6. Hand off to existing loadProject — this is the SAFE part because
      // we're using the EXACT same code path as a regular local project load.
      // loadProject reads audioBuffer/idbKey from each clip, sets up the
      // audio engine, populates UI state.
      await loadProject(localProj);

      // 7. Show summary if any vocals failed
      if (failedVocals > 0) {
        setError("Restored project — but " + failedVocals + " of " + totalVocals + " vocal recordings couldn't be downloaded. They may still be in the cloud; try again later.");
        setTimeout(function(){ setError(""); }, 8000);
      }
    } catch (e) {
      console.error("[BeatFinder] Cloud restore failed:", e);
      setSaveStatus("");
      setError("Could not restore from cloud — " + ((e && e.message) || "unknown error"));
      setTimeout(function(){ setError(""); }, 6000);
    }
  };

  const loadProject = async function (p) {
    setShowProjects(false);
    setSaveStatus("Loading…");
    setError("");
    try {
      stopAll(); setIsPlaying(false);
      setCurrentTime(0); setSelectedClipId(null); setSelectedTrackId(null);
      if (scrollRef.current) scrollRef.current.scrollLeft = 0;

      setProjectName(p.name||"Untitled");
      setBpm(p.bpm||120);
      setTimeSigNum(p.timeSigNum||4);
      setProjectKey(p.projectKey||"C major");

      const actx = getActx();
      if (actx.state === "suspended") {
        try { await actx.resume(); } catch(e) {}
      }

      let missingAudio = false;

      // Restore tracks — pull audio from IndexedDB using the idbKey saved per clip
      const restored = await Promise.all((p.tracks||[]).map(async function(t){

        // Drum-sequencer tracks never have per-clip audio buffers — their
        // clips are pattern references (patternId), not waveforms. Skip the
        // missingAudio flag for the whole track so loading a project with
        // drum clips doesn't trigger the false-positive "some audio clips
        // couldn't be restored" warning. Same logic applies to MIDI tracks —
        // their clips never had audio data to begin with.
        const isDrumTrack = t.type === "drumSequencer";
        const isMidiTrack = t.type === "midi";

        const restoredClips = await Promise.all((t.clips||[]).map(async function(cl){
          let buf = null;

          // ── New path: audio stored in IndexedDB ──────────────────────────
          if (cl.idbKey) {
            try {
              const wavBuf = await AudioDB.getClip(cl.idbKey);
              if (wavBuf) {
                buf = await actx.decodeAudioData(wavBuf.slice(0)); // slice so buffer isn't detached
              }
            } catch(e) { console.warn("IDB load clip failed", cl.idbKey, e); }
          }

          // ── Legacy path: audio was base64 in the save object ─────────────
          if (!buf && cl.audioB64) {
            buf = await base64ToAudioBuffer(cl.audioB64);
          }

          // Only flag a clip as "missing audio" if it actually referenced
          // some audio source (idbKey or audioB64) AND that source failed
          // to load. Clips that never had audio in the first place — drum
          // sequencer pattern clips, MIDI clips, or any clip identified
          // only by patternId — should NOT count as failures.
          //
          // Without this guard, every project containing a drum-sequencer
          // clip pops the "Some audio clips couldn't be restored" toast,
          // even though every clip plays perfectly.
          const expectedAudio = !!(cl.idbKey || cl.audioB64);
          if (!buf && expectedAudio && !isDrumTrack && !isMidiTrack && !cl.patternId && cl.type !== "midi") {
            missingAudio = true;
          }
          // Migrate legacy drum clips with no stored duration → stamp a fixed
          // duration at the project's saved bpm so the clip holds its size on
          // any later tempo change (render + scheduler both rely on it).
          let migrated = { ...cl, audioBuffer: buf, url: null, blob: null };
          if (isDrumTrack && cl.patternId && !(typeof cl.duration === "number" && cl.duration > 0)) {
            const pat = (t.patterns || []).find(function(pp){ return pp && pp.id === cl.patternId; });
            if (pat) {
              migrated.duration = pat.lengthSteps * drumStepDurationSec(p.bpm || 120, pat.stepsPerBeat || 4);
            }
          }
          return migrated;
        }));

        // Legacy flat audioBuffer (beat tracks)
        let trackBuf = null;
        if (t.trackIdbKey) {
          try {
            const wavBuf = await AudioDB.getClip(t.trackIdbKey);
            if (wavBuf) trackBuf = await actx.decodeAudioData(wavBuf.slice(0));
          } catch(e) {}
        }
        if (!trackBuf && t.audioB64) {
          trackBuf = await base64ToAudioBuffer(t.audioB64);
        }

        return { ...t, audioBuffer: trackBuf, url: null, blob: null, clips: restoredClips };
      }));

      setTracks(restored);
      setIsSaved(true);

      // Background pitch render — any clip in the loaded project that has a
      // non-zero pitchSemitones needs its pitched buffer re-rendered (the
      // cache is in-memory only and doesn't survive a page reload). We do
      // this fire-and-forget so loadProject returns immediately and the UI
      // is responsive. The scheduler falls back to the raw buffer until
      // each pitched buffer is ready, so the worst-case is that the first
      // playback of a pitched clip plays unpitched, then subsequent plays
      // are correct. Pretty unobtrusive.
      (function(){
        const ctx = actxRef.current;
        if (!ctx) return;
        restored.forEach(function(tr){
          (tr.clips || []).forEach(function(cl){
            const pitch = cl.pitchSemitones || 0;
            if (Math.abs(pitch) < 0.005 || !cl.audioBuffer) return;
            const key = cl.id + ":" + Math.round(pitch * 100);
            if (clipPitchedBuffersRef.current[key]) return; // already cached
            pitchAudioBufferOffline(ctx, cl.audioBuffer, pitch).then(function(rendered){
              if (rendered) clipPitchedBuffersRef.current[key] = rendered;
            }).catch(function(e){ console.warn("[pitch] background render failed for", cl.id, e); });
          });
        });
      })();

      // Eagerly load every soundfont track's preset NOW, on project open —
      // the same full load the first note tap would trigger (note the
      // trackId, so it binds to the per-track engine exactly like
      // playback). Without this, opening a project only did a partial
      // load; the soundfont wasn't actually playable until the user's
      // first tap kicked off the real preset bind, which wasted that tap
      // and made the loading bar look like it had lied (bar gone, but
      // first tap still silent). Doing the complete load here means the
      // loading bar covers the whole process and the first tap plays.
      (function(){
        const ctx = actxRef.current;
        if (!ctx) return;
        let masterDest = null;
        try { masterDest = getOrCreateMaster(); } catch(_){}
        restored.forEach(function(tr){
          if (tr.type !== "midi" || tr.samplerData) return;
          let inst;
          try {
            const baseInst = resolveTrackInstrumentMeta(tr);
            inst = applyInstrumentOverrides(baseInst, tr.instrumentOverrides);
          } catch(_) { return; }
          if (inst && inst.engine === "soundfont-v1") {
            try {
              const baseInst = resolveTrackInstrumentMeta(tr);
              const ovr = tr.instrumentOverrides;
              const trackId = tr.id;
              const pr = ensureSoundfontPresetLoaded(inst, ctx, masterDest, trackId);
              // ADSR/filter overrides are applied by the SpessaSynth-live
              // engine (per-voice envelope generators) via scheduleMidiNote
              // routing — not via FluidSynth CCs (FluidSynth ignores them).
              // Nothing to do here on preset-ready beyond the load itself.
              void ovr; void pr;
            } catch(_){}
          }
        });
      })();

      if (missingAudio) {
        setSaveStatus("");
        setError("⚠️ Some audio clips couldn't be restored — they may have been recorded in a different browser/app session.");
        setTimeout(function(){ setError(""); }, 8000);
      } else {
        setSaveStatus("Loaded! ✓");
        setTimeout(function(){ setSaveStatus(""); }, 1500);
      }
    } catch(e) {
      console.error("[BeatFinder] loadProject error:", e);
      setError("Could not load project — " + (e.message||"unknown error"));
      setSaveStatus("");
    }
  };

  const deleteProject = function (id) {
    // Remove audio clips from IndexedDB (best-effort, async — no need to await)
    AudioDB.deleteProjectClips(id).catch(function(){});
    // Find the project being deleted so we can mirror the deletion to the
    // cloud (if it had been synced). Done before we remove from local list.
    const toDelete = savedProjects.find(function(p){ return p.id===id; });
    const u = savedProjects.filter(function(p){ return p.id!==id; });
    setSavedProjects(u);
    localStorage.setItem("bf_studio_projects", JSON.stringify(u));

    // Cloud delete — best-effort, silent on failure. The local delete is
    // already committed by this point; if cloud fails the worst case is
    // an orphaned cloud doc that next save will sync over anyway.
    if (toDelete && toDelete.cloudId) {
      apiFetch("/api/studio/projects/delete/" + encodeURIComponent(toDelete.cloudId), {
        method: "DELETE",
      }).catch(function(err) {
        var msg = (err && err.message) || "";
        if (msg && !msg.includes("403") && !msg.includes("401") && !msg.includes("404")) {
          console.warn("[BeatFinder] Cloud delete failed:", err);
        }
      });
    }
  };

  // ══════════════════════════════════════════════════════════════
  // BPM DETECTION ENGINE — multi-band, Worker-based, DAW-grade
  // ══════════════════════════════════════════════════════════════

  // ── encodeWAV: serialise an AudioBuffer to a 16-bit PCM WAV blob ─────────
  // Used to upload the in-memory audio to the backend BPM endpoint. WAV is
  // chosen because every browser can encode it natively (no MP3 encoder
  // needed) and the backend's librosa+soundfile reads WAV without ffmpeg.
  // The mono downmix + 22050 Hz target sample rate also keep the upload
  // small (~2.6 MB per minute of audio) and match what the backend will
  // resample to anyway, so we avoid sending pointless precision.
  const encodeWAV = useCallback(function (audioBuffer) {
    const SR_OUT = 22050;
    const nc = audioBuffer.numberOfChannels;
    const lenIn = audioBuffer.length;
    const srIn  = audioBuffer.sampleRate;

    // Step 1: downmix to mono
    const mono = new Float32Array(lenIn);
    for (let c = 0; c < nc; c++) {
      const ch = audioBuffer.getChannelData(c);
      for (let i = 0; i < lenIn; i++) mono[i] += ch[i];
    }
    const gain = 1 / nc;
    for (let i = 0; i < lenIn; i++) mono[i] *= gain;

    // Step 2: simple linear-interp resample to SR_OUT
    const lenOut = Math.floor(lenIn * SR_OUT / srIn);
    const out = new Float32Array(lenOut);
    const ratio = srIn / SR_OUT;
    for (let i = 0; i < lenOut; i++) {
      const srcPos = i * ratio;
      const i0 = Math.floor(srcPos);
      const i1 = Math.min(i0 + 1, lenIn - 1);
      const frac = srcPos - i0;
      out[i] = mono[i0] * (1 - frac) + mono[i1] * frac;
    }

    // Step 3: write 16-bit PCM WAV
    const dataSize = lenOut * 2;
    const buf = new ArrayBuffer(44 + dataSize);
    const view = new DataView(buf);
    let p = 0;
    function w32(v) { view.setUint32(p, v, true); p += 4; }
    function w16(v) { view.setUint16(p, v, true); p += 2; }
    function wStr(s) { for (let i = 0; i < s.length; i++) view.setUint8(p++, s.charCodeAt(i)); }
    wStr("RIFF"); w32(36 + dataSize); wStr("WAVE");
    wStr("fmt "); w32(16); w16(1); w16(1); w32(SR_OUT); w32(SR_OUT * 2); w16(2); w16(16);
    wStr("data"); w32(dataSize);
    for (let i = 0; i < lenOut; i++) {
      const sample = Math.max(-1, Math.min(1, out[i]));
      view.setInt16(p, sample < 0 ? sample * 0x8000 : sample * 0x7FFF, true);
      p += 2;
    }
    return new Blob([buf], { type: "audio/wav" });
  }, []);

  // ── Cleanup poll timer on unmount ───────────────────────────────────────
  useEffect(function () {
    return function () {
      if (bpmWorkerRef.current && bpmWorkerRef.current.pollTimer) {
        clearTimeout(bpmWorkerRef.current.pollTimer);
        bpmWorkerRef.current.aborted = true;
      }
    };
  }, []);

  // ── Main detectBpm: uploads audio to backend, polls for result ─────────
  // The heavy analysis (librosa onset envelope, dynamic-programming beat
  // tracker, tempogram autocorrelation, half/double-time correction,
  // confidence scoring) now happens server-side. This keeps the mobile
  // bundle lightweight and gives us DAW-grade accuracy from the Python
  // ecosystem. All existing state setters are still called with the same
  // shape so every BPM display + beat marker continues to work unchanged.
  const detectBpm = useCallback(async function (sourceBuffer) {
    // If no buffer passed, find best candidate track (unchanged logic)
    const buf = sourceBuffer || (function () {
      const beatTrack = tracks.find(function(t){
        return t.type === "beat" && t.clips && t.clips.some(function(c){ return c.audioBuffer; });
      });
      if (beatTrack) {
        const cl = beatTrack.clips.find(function(c){ return c.audioBuffer; });
        return cl ? cl.audioBuffer : null;
      }
      for (const t of tracks) {
        const cl = t.clips && t.clips.find(function(c){ return c.audioBuffer; });
        if (cl) return cl.audioBuffer;
      }
      return null;
    })();

    if (!buf) { setBpmDetectMsg("No audio loaded."); return; }

    // Cancel any in-flight poll
    if (bpmWorkerRef.current && bpmWorkerRef.current.pollTimer) {
      clearTimeout(bpmWorkerRef.current.pollTimer);
      bpmWorkerRef.current.aborted = true;
    }
    // Reuse the existing ref slot to track this run's state. Naming kept
    // for compatibility but it no longer points at a Web Worker.
    bpmWorkerRef.current = { aborted: false, pollTimer: null };
    const myRun = bpmWorkerRef.current;

    setBpmDetecting(true);
    setBpmProgress(0);
    setBpmDetectMsg("Reading track audio…");
    setDetectedBpm(null);
    setBpmConfidence(null);
    setBpmBandConf(null);
    setBeatPositions([]);
    setOnsetTimestamps([]);
    setBpmCandidates([]);
    setBpmTemporal(null);

    try {
      // 1. Encode AudioBuffer → WAV blob
      const wavBlob = encodeWAV(buf);
      if (myRun.aborted) return;

      // Guardrail: client-side size cap that matches backend (15 MB).
      if (wavBlob.size > 15 * 1024 * 1024) {
        setBpmDetectMsg("Audio too long for analysis (max ~6 min).");
        setBpmDetecting(false);
        setTimeout(function(){ setBpmDetectMsg(""); }, 3000);
        return;
      }

      // 2. Upload to /api/bpm/analyze
      setBpmProgress(15);
      setBpmDetectMsg("Sending to analyser…");
      const fd = new FormData();
      fd.append("file", wavBlob, "studio_track.wav");

      const token = getToken() || "";
      let res;
      try {
        res = await fetch(API_BASE + "/api/bpm/analyze", {
          method:  "POST",
          headers: { Authorization: "Bearer " + token },
          body:    fd,
        });
      } catch (netErr) {
        throw new Error("Network error: " + netErr.message);
      }
      if (myRun.aborted) return;

      if (!res.ok) {
        let detail = "HTTP " + res.status;
        try { const j = await res.json(); if (j.detail) detail = j.detail; } catch (e) {}
        throw new Error(detail);
      }
      const startResp = await res.json();

      // 3a. Cache hit — backend returned the result inline
      if (startResp.status === "done" && startResp.result) {
        applyBpmResult(startResp.result);
        return;
      }

      // 3b. Async job — poll status until done or error
      const jobId = startResp.job_id;
      if (!jobId) throw new Error("Analysis did not return a job id");

      setBpmProgress(35);
      setBpmDetectMsg("Analysing…");

      // Poll loop. Backend caps analysis at 60s server-side so we cap polling
      // at ~90s wall-clock here as a defence in depth.
      const MAX_POLL_MS = 90000;
      const POLL_INTERVAL = 1500;
      const t0 = Date.now();
      let lastProgress = 35;

      while (true) {
        if (myRun.aborted) return;
        if (Date.now() - t0 > MAX_POLL_MS) {
          throw new Error("Analysis is taking too long. Please try a shorter clip.");
        }
        await new Promise(function(r){ myRun.pollTimer = setTimeout(r, POLL_INTERVAL); });
        if (myRun.aborted) return;

        let statusJson;
        try {
          const sres = await fetch(API_BASE + "/api/bpm/status/" + jobId, {
            headers: { Authorization: "Bearer " + token },
          });
          if (!sres.ok) {
            if (sres.status === 404) throw new Error("Analysis job expired. Please try again.");
            throw new Error("Status check failed (HTTP " + sres.status + ")");
          }
          statusJson = await sres.json();
        } catch (e) {
          throw e;
        }

        // Soft progress ramp — backend reports 10/100 during processing.
        // We interpolate so the user sees movement even on a slow Render dyno.
        lastProgress = Math.min(90, Math.max(lastProgress + 4, statusJson.progress || 0));
        setBpmProgress(lastProgress);
        if (statusJson.message) setBpmDetectMsg(statusJson.message);

        if (statusJson.status === "done") break;
        if (statusJson.status === "error") {
          throw new Error(statusJson.error || "Analysis failed");
        }
      }

      // 4. Fetch result
      const rres = await fetch(API_BASE + "/api/bpm/result/" + jobId, {
        headers: { Authorization: "Bearer " + token },
      });
      if (myRun.aborted) return;
      if (!rres.ok) throw new Error("Could not retrieve result");
      const result = await rres.json();
      applyBpmResult(result);

    } catch(err) {
      if (myRun.aborted) return; // user navigated away — silent
      console.error('[BeatFinder] BPM detection error:', err);
      setDetectedBpm(-1);
      setBpmDetectMsg(err.message || "Detection failed");
      setTimeout(function(){ setBpmDetectMsg(""); }, 4000);
      setBpmDetecting(false);
    }

    // Helper — applies backend result to all existing state setters so
    // the rest of the UI (beat markers, confidence pill, candidates list,
    // etc.) doesn't need to change. Shape is mapped from backend BpmResult.
    function applyBpmResult(result) {
      const finalBpm = Math.round(result.bpm_rounded || result.bpm || 0);
      setDetectedBpm(finalBpm);
      setBpm(finalBpm);
      setBpmConfidence(result.confidence ?? 0);
      // bands not detected on the backend — clear so the UI hides band pills
      setBpmBandConf(null);
      // Beat times — backend gives them in seconds at the file's playback rate
      const beats = (result.beat_grid && result.beat_grid.times) || [];
      setBeatPositions(beats);
      setOnsetTimestamps(beats);
      // Backend doesn't compute swing right now (heavy + low-value). Leave 0.
      setSwingAmount(0);
      setBpmSource("auto");
      // Map alternative_bpms → existing candidates state shape
      setBpmCandidates((result.alternative_bpms || []).map(function(c){
        return { bpm: c.bpm, score: c.score };
      }));
      setBpmTemporal(result.confidence ?? null);
      setBpmProgress(100);
      setBpmDetectMsg(result.cached ? "Done ✓ (cached)" : "Done ✓");
      setTimeout(function(){ setBpmDetectMsg(""); setBpmProgress(0); }, 2000);
      setBpmDetecting(false);
    }
  }, [tracks, encodeWAV]);


  // ── Tap-tempo logic ──────────────────────────────────────────────────────
  const handleTapTempo = useCallback(function () {
    const now = Date.now();
    setTapTimes(function (prev) {
      const fresh = prev.filter(function(t){ return now - t < 3000; });
      const next = [...fresh, now];
      if (next.length >= 2) {
        const intervals = [];
        for (let i = 1; i < next.length; i++) intervals.push(next[i] - next[i-1]);
        const avg = intervals.reduce(function(a,b){ return a+b; }, 0) / intervals.length;
        const tapped = Math.round(60000 / avg);
        if (tapped >= 40 && tapped <= 220) {
          setBpmWithRescale(tapped);
          setDetectedBpm(tapped);
          setBpmSource("tap");
          setBpmConfidence(null); // tap has no confidence score
        }
      }
      return next;
    });
  }, [setBpmWithRescale]);

  // Expose transport controls to the parent App so the LyricsNotepad can
  // surface them in its own header when launched from Studio (the main
  // fixed transport bar is hidden behind the fullscreen lyrics overlay).
  // Re-register whenever any of the relevant state changes so the buttons
  // always have fresh handlers + live state (avoids stale React closures).
  React.useEffect(function() {
    if (!onRegisterTransport) return;
    onRegisterTransport({
      isPlaying:   isPlaying,
      isRecording: isRecording,
      countIn:     countIn,
      togglePlay:  togglePlay,
      rewind:      rewind,
      skipToEnd:   skipToEnd,
      seekBack:    seekBack,
      seekForward: seekForward,
      onRecord:    function() {
        if (isRecording) { stopRecording(); return; }
        // PDC + drift compensation now handle FX-on-channel timing
        // automatically, so no warning is needed.
        startCountIn(selectedTrackId);
      },
    });
    return function() {
      // Clear the registration on unmount so the App doesn't hold a
      // stale reference to a destroyed Studio instance.
      if (onRegisterTransport) onRegisterTransport(null);
    };
  }, [isPlaying, isRecording, countIn, selectedTrackId, tracks]);


  // Yieldable variant of audioBufferToWav. The original was a single sync
  // loop that, for a multi-minute project, could block the main thread for
  // 200-500ms — long enough to freeze the export spinner's CSS animation.
  // This variant yields every N samples so the compositor can paint frames.
  // Caller MUST await this. Optional onProgress(fraction) callback fires
  // periodically (0..1) so a UI bar can advance.
  const audioBufferToWavAsync = async function (buf, onProgress) {
    const nc=buf.numberOfChannels,sr=buf.sampleRate,len=buf.length,bl=len*nc*2;
    const ab=new ArrayBuffer(44+bl),dv=new DataView(ab);
    const ws=function(o,s){for(let i=0;i<s.length;i++)dv.setUint8(o+i,s.charCodeAt(i));};
    ws(0,"RIFF");dv.setUint32(4,36+bl,true);ws(8,"WAVE");ws(12,"fmt ");
    dv.setUint32(16,16,true);dv.setUint16(20,1,true);dv.setUint16(22,nc,true);
    dv.setUint32(24,sr,true);dv.setUint32(28,sr*nc*2,true);dv.setUint16(32,nc*2,true);
    dv.setUint16(34,16,true);ws(36,"data");dv.setUint32(40,bl,true);
    // Pre-grab channel data refs ONCE — getChannelData() inside the inner
    // loop was a hot-path overhead that scaled with sample count.
    const chans = [];
    for (let c=0;c<nc;c++) chans.push(buf.getChannelData(c));
    let off=44;
    // Yield every ~50ms-worth of samples. 44100 * 0.05 = 2205 samples per
    // chunk, but we use 4096 for clean power-of-two iteration cost.
    const YIELD_EVERY = 4096;
    for(let i=0;i<len;i++){
      for(let c=0;c<nc;c++){
        const s=Math.max(-1,Math.min(1,chans[c][i]));
        dv.setInt16(off,s<0?s*0x8000:s*0x7FFF,true);off+=2;
      }
      if ((i & (YIELD_EVERY - 1)) === 0 && i > 0) {
        // setTimeout(0) yields to the event loop; the spinner repaints
        // before we resume.
        await new Promise(function(r){ setTimeout(r, 0); });
        if (onProgress) {
          try { onProgress(i / len); } catch(e) {}
        }
      }
    }
    return ab;
  };

  // ── Export ────────────────────────────────────────────────────
  // Render an AudioBuffer to a WAV-format ArrayBuffer.
  // RETURNS AN ArrayBuffer (NOT a Blob) so it can be stored directly in
  // IndexedDB. iOS Safari has historically been broken at persisting
  // Blob values across IndexedDB transactions — Blobs would either
  // silently fail to persist OR persist but come back empty on read.
  // ArrayBuffers are persisted reliably. Callers that need a Blob
  // (e.g. exportMix for the download href) wrap with `new Blob([wav])`
  // themselves at the point of use.
  const audioBufferToWav = function (buf) {
    const nc=buf.numberOfChannels,sr=buf.sampleRate,len=buf.length,bl=len*nc*2;
    const ab=new ArrayBuffer(44+bl),dv=new DataView(ab);
    const ws=function(o,s){for(let i=0;i<s.length;i++)dv.setUint8(o+i,s.charCodeAt(i));};
    ws(0,"RIFF");dv.setUint32(4,36+bl,true);ws(8,"WAVE");ws(12,"fmt ");
    dv.setUint32(16,16,true);dv.setUint16(20,1,true);dv.setUint16(22,nc,true);
    dv.setUint32(24,sr,true);dv.setUint32(28,sr*nc*2,true);dv.setUint16(32,nc*2,true);
    dv.setUint16(34,16,true);ws(36,"data");dv.setUint32(40,bl,true);
    let off=44;
    for(let i=0;i<len;i++)for(let c=0;c<nc;c++){
      const s=Math.max(-1,Math.min(1,buf.getChannelData(c)[i]));
      dv.setInt16(off,s<0?s*0x8000:s*0x7FFF,true);off+=2;
    }
    return ab;
  };


  // ═════════════════════════════════════════════════════════════════════
  // Real-time export
  // ═════════════════════════════════════════════════════════════════════
  // Records the LIVE playback through the master output to a WAV file.
  // Replaces offline export because soundfont tracks (FluidSynth-based)
  // only render audio in the live AudioContext. The trade-off: export
  // takes as long as the song duration (real time playback). Everything
  // else is faithful — exactly what the user hears through the speakers
  // is what gets recorded.
  //
  // Flow:
  //   1. Compute total project duration from clip endpoints
  //   2. Create a MediaStreamDestinationNode tapping the master gain
  //   3. Start a MediaRecorder on that stream
  //   4. Call doPlay(0) — same path as live playback
  //   5. Wait totalDur seconds, updating progress every 100ms
  //   6. Stop transport + recorder
  //   7. Decode the recorded blob (MP4/AAC on iOS) → AudioBuffer → WAV
  //   8. Trigger download via <a> tag
  //
  // iOS caveat: the screen must stay ON during export. Locking the
  // screen or backgrounding the app suspends the AudioContext and the
  // recording will have a gap or be cut short. Wake Lock acquired
  // when available; user warning shown regardless.
  const exportMix = async function () {
    setExporting(true); setExportMsg("Preparing..."); setExportProgress(0.02);

    // Track these refs so the catch / cleanup can release them.
    let recorder = null;
    let streamDest = null;
    let progressTimer = null;
    let wakeLock = null;
    // Hoisted to function scope so the catch block can remove the
    // listener (the try-block declarations would be out of reach).
    let onStateChange = null;
    // Remember the user's performance-mode setting so we can restore
    // it after the export. We force performance mode ON during the
    // recording phase so VU meters, scrolling waveforms, and other
    // rAF-driven visuals stop consuming CPU. Each track's VU meter
    // alone is cheap, but at 10+ tracks they aggregate into multi-ms
    // per frame which causes audio dropouts in the recording.
    const _perfModeBackup = isPerformanceMode();
    _setPerformanceMode(true);

    try {
      const liveTracks = tracksRef.current || tracks || [];

      // ── Compute total project duration from clip endpoints ──
      let totalDur = 0;
      for (const tr of liveTracks) {
        const clips = tr.clips || [];
        for (const cl of clips) {
          if (cl.active === false) continue;
          let clipEnd = 0;
          if (tr.type === "drumSequencer" || tr.type === "midi") {
            clipEnd = (cl.startTime || 0) + (cl.duration || 0);
          } else if (cl.audioBuffer) {
            const trimS = cl.trimStart || 0;
            const trimE = cl.trimEnd !== undefined ? cl.trimEnd : cl.audioBuffer.duration;
            const trimDur = Math.max(0, trimE - trimS);
            clipEnd = (cl.startTime || 0) + trimDur;
          }
          if (clipEnd > totalDur) totalDur = clipEnd;
        }
      }
      // Add a 2-second tail to capture reverb decays, delay echoes etc.
      totalDur += 2.0;

      if (totalDur < 0.5) {
        setExportMsg("Nothing to export");
        setTimeout(function(){ setExporting(false); setExportMsg(""); }, 2000);
        return;
      }

      // Refuse to start if already playing — would be a mess.
      if (isPlayingRef.current) {
        setExportMsg("Stop playback first");
        setTimeout(function(){ setExporting(false); setExportMsg(""); }, 2500);
        return;
      }

      // ── Request screen wake lock so iOS doesn't lock during export ──
      // Wake Lock API is supported in Safari 16.4+. Falls back silently
      // on older browsers — the user warning below covers that case.
      if (navigator.wakeLock && navigator.wakeLock.request) {
        try {
          wakeLock = await navigator.wakeLock.request("screen");
        } catch(_) { /* permission denied or feature absent — keep going */ }
      }

      setExportMsg("Setting up...");
      setExportProgress(0.05);

      // ── Ensure live audio is ready ──
      const acx = getActx();
      const master = getOrCreateMaster();
      if (!acx || !master) {
        throw new Error("audio context unavailable");
      }
      if (acx.state === "suspended") {
        try { await acx.resume(); } catch(_) {}
      }

      // ── AudioContext interrupt monitor ──
      // If the live audio context suspends mid-export (phone call, screen
      // lock, app backgrounded, notification with audio ducking that
      // doesn't auto-recover), we abort. We deliberately do NOT try to
      // resume — resuming would splice audio from the interruption point
      // onward into the recording, producing a broken take.
      let interrupted = false;
      let interruptReason = "";
      onStateChange = function(){
        if (acx.state === "suspended" || acx.state === "interrupted" || acx.state === "closed") {
          interrupted = true;
          interruptReason = acx.state;
        }
      };
      try { acx.addEventListener("statechange", onStateChange); } catch(_) {}

      // ── Tap the master output with a MediaStreamDestination ──
      // The master gain stays connected to acx.destination as before
      // (so the user hears the song through the speakers). We ADD a
      // parallel connection to a MediaStreamDestinationNode whose
      // .stream we feed to MediaRecorder. The two connections are
      // independent — speaker volume isn't affected.
      streamDest = acx.createMediaStreamDestination();
      master.connect(streamDest);

      // ── Pick a supported MediaRecorder MIME type ──
      const candidates = [
        "audio/mp4",
        "audio/webm;codecs=opus",
        "audio/webm",
        "audio/ogg;codecs=opus",
      ];
      let mimeType = "";
      if (window.MediaRecorder && MediaRecorder.isTypeSupported) {
        for (const c of candidates) {
          if (MediaRecorder.isTypeSupported(c)) { mimeType = c; break; }
        }
      }
      if (!window.MediaRecorder) {
        throw new Error("MediaRecorder not supported in this browser");
      }

      // ── Silence the speakers (real-time monitoring off) ──
      // Setting speakerMute.gain to 0 makes acx.destination silent.
      // The MediaStreamDestination tap added below attaches to master,
      // which is UPSTREAM of speakerMute, so the recording stream
      // continues to receive full-level audio. We restore gain to 1
      // in both the success path and the catch — even if the export
      // fails mid-flight the user shouldn't be left with dead speakers.
      try {
        if (speakerMuteRef.current) speakerMuteRef.current.gain.value = 0;
      } catch(_) {}

      // ── Pre-warm phase ──
      // The first 1-2 seconds of playback are when the audio engine is
      // most fragile: FX chains haven't been built yet, soundfont
      // voices aren't allocated, sampler buffers aren't warm. Glitches
      // in those first seconds get baked into the export. To avoid
      // this we kick off a brief silent play+stop to force everything
      // to instantiate, then wait ~400ms for the audio graph to settle
      // before the real recording starts. Pre-warm is silent because
      // speakerMute is already at 0.
      setExportMsg("Warming up engine...");
      setExportProgress(0.06);
      try {
        await doPlay(0);
        // Immediately stop — we don't want to actually record this pass.
        // doPlay's promise resolves after its initial setup so by now
        // chains exist, soundfonts are mid-load, etc.
        await new Promise(function(r){ setTimeout(r, 100); });
        try { stopAll(); } catch(_) {}
        setIsPlaying(false);
        // Settle window: long enough for soundfont voice pool
        // allocations to complete on iOS Safari (typically 200-300ms)
        // but short enough not to feel like a stall.
        await new Promise(function(r){ setTimeout(r, 400); });
      } catch(prewarmErr) {
        // Pre-warm failures are non-fatal — we'll just have the old
        // glitchy first few seconds. Log and continue.
        console.warn("[export] pre-warm failed:", prewarmErr);
      }

      const recorderOpts = mimeType ? { mimeType: mimeType } : {};
      recorder = new MediaRecorder(streamDest.stream, recorderOpts);
      const chunks = [];
      recorder.ondataavailable = function(e){
        if (e.data && e.data.size > 0) chunks.push(e.data);
      };
      recorder.start();

      // ── Start the live transport ──
      setExportMsg("Recording...");
      setExportProgress(0.10);
      const startEpoch = performance.now();
      try {
        await doPlay(0);
        setIsPlaying(true);
      } catch(playErr) {
        throw new Error("playback failed: " + (playErr && playErr.message || playErr));
      }

      // ── Wait totalDur seconds (real time), updating progress ──
      // Aborts early if the AudioContext suspends (call, lock, etc).
      const exportPromise = new Promise(function(resolve, reject){
        progressTimer = setInterval(function(){
          if (interrupted) {
            clearInterval(progressTimer);
            progressTimer = null;
            reject(new Error("Export interrupted (audio " + (interruptReason || "suspended") + "). Please try again."));
            return;
          }
          const elapsed = (performance.now() - startEpoch) / 1000;
          const frac = Math.min(1, elapsed / totalDur);
          setExportProgress(0.10 + 0.75 * frac);
          setExportMsg("Recording... " + Math.floor(elapsed) + " / " + Math.ceil(totalDur) + "s");
          if (elapsed >= totalDur) {
            clearInterval(progressTimer);
            progressTimer = null;
            resolve();
          }
        }, 100);
      });
      await exportPromise;

      // ── Stop transport + recorder ──
      try { stopAll(); } catch(_) {}
      setIsPlaying(false);

      setExportMsg("Finalizing recording...");
      setExportProgress(0.86);

      // Recorder.stop is async — wait for the final dataavailable.
      const stopP = new Promise(function(resolve){
        recorder.onstop = function(){ resolve(); };
        try { recorder.stop(); } catch(_) { resolve(); }
      });
      await stopP;

      // ── Disconnect the stream tap ──
      try { master.disconnect(streamDest); } catch(_) {}
      try {
        const ts = streamDest.stream.getTracks();
        for (const t of ts) t.stop();
      } catch(_) {}
      streamDest = null;

      if (chunks.length === 0) {
        throw new Error("recording produced no audio data");
      }

      // ── Decode the recorded blob to an AudioBuffer ──
      setExportMsg("Decoding...");
      setExportProgress(0.88);
      const recordedBlob = new Blob(chunks, { type: recorder.mimeType || mimeType || "audio/mp4" });
      const arrBuf = await recordedBlob.arrayBuffer();
      const decodeCtx = new (window.OfflineAudioContext || window.webkitOfflineAudioContext)(
        2, 1, acx.sampleRate
      );
      let finalBuffer;
      try {
        finalBuffer = await decodeCtx.decodeAudioData(arrBuf.slice(0));
      } catch(decodeErr) {
        throw new Error("decode failed: " + (decodeErr && decodeErr.message || decodeErr));
      }

      // ── Encode as WAV and download ──
      setExportMsg("Encoding...");
      setExportProgress(0.92);
      const wavBuf = await audioBufferToWavAsync(finalBuffer, function(frac){
        setExportProgress(0.92 + 0.07 * frac);
      });
      const wavBlob = new Blob([wavBuf], { type: "audio/wav" });
      const a = document.createElement("a");
      a.href = URL.createObjectURL(wavBlob);
      a.download = (projectName || "project") + " - mix.wav";

      try {
        sessionStorage.setItem("bf_share_sheet_active", String(Date.now()));
      } catch(e) {}

      a.click();
      setTimeout(function(){ try { URL.revokeObjectURL(a.href); } catch(e) {} }, 1000);

      setExportMsg("Done!"); setExportProgress(1);
      setTimeout(function(){ setExporting(false); setExportMsg(""); setExportProgress(0); }, 2500);
      setTimeout(function(){
        try { sessionStorage.removeItem("bf_share_sheet_active"); } catch(e) {}
      }, 60000);

      try { if (wakeLock && wakeLock.release) await wakeLock.release(); } catch(_) {}
      try { acx.removeEventListener("statechange", onStateChange); } catch(_) {}
      // Restore performance mode (VU meters, animations, etc.)
      try { _setPerformanceMode(_perfModeBackup); } catch(_) {}
      // Restore speaker mute → passthrough so the user hears playback
      // normally again.
      try {
        if (speakerMuteRef.current) speakerMuteRef.current.gain.value = 1;
      } catch(_) {}
    } catch (e) {
      console.error("[export] Failed:", e);
      setExportMsg("Export failed: " + (e.message || "unknown error"));
      setTimeout(function(){ setExporting(false); setExportMsg(""); setExportProgress(0); }, 3500);

      try { if (progressTimer) clearInterval(progressTimer); } catch(_) {}
      try { stopAll(); } catch(_) {}
      try { setIsPlaying(false); } catch(_) {}
      try { if (recorder && recorder.state !== "inactive") recorder.stop(); } catch(_) {}
      try {
        if (streamDest) {
          const m2 = masterGainRef.current;
          if (m2) m2.disconnect(streamDest);
          const ts = streamDest.stream.getTracks();
          for (const t of ts) t.stop();
        }
      } catch(_) {}
      try { if (wakeLock && wakeLock.release) await wakeLock.release(); } catch(_) {}
      // Remove the statechange listener — `acx` may not be in scope if
      // we failed before it was assigned, so guard the call.
      try {
        const liveCtx = (typeof actxRef !== "undefined") && actxRef && actxRef.current;
        if (liveCtx && onStateChange) {
          liveCtx.removeEventListener("statechange", onStateChange);
        }
      } catch(_) {}
      // Restore performance mode even on failure.
      try { _setPerformanceMode(_perfModeBackup); } catch(_) {}
      // Restore speaker mute on failure — CRITICAL so the user isn't
      // left with dead speakers after a failed export.
      try {
        if (speakerMuteRef.current) speakerMuteRef.current.gain.value = 1;
      } catch(_) {}
    }
  };

  // Render one soundfont MIDI track to an AudioBuffer using SpessaSynth.
  // This is the offline-export soundfont path. SpessaSynth renders in
  // pure JS (no AudioWorklet), so it works where FluidSynth can't.
  //
  // Multi-bank correctness: we parse the .sf2's real preset list with the
  // existing bfSfParsePresetList() and run the SAME selection algorithm
  // the live engine uses in loadPreset (exact bank+program → bank0+program
  // → first preset). Then we tell SpessaSynth to select that bank+program
  // via CC0 (bank MSB) + program change before rendering notes. This makes
  // multi-bank soundfonts pick the same preset they play live.
  //
  // Returns an AudioBuffer (2ch, totalDur, SR) bound to ocForBuffer, or
  // null if there are no notes / the soundfont can't be loaded.
  const renderSoundfontTrackWithSpessa = async function(
    track, totalDur, SR, ocForBuffer, SpessaSynthProcessor, SoundBankLoader, diag
  ) {
    if (!track || track.type !== "midi") return null;
    const baseInst = resolveTrackInstrumentMeta(track);
    const inst = applyInstrumentOverrides(baseInst, track.instrumentOverrides);
    if (!inst || inst.engine !== "soundfont-v1") return null;

    // Gather note on/off events across the whole export window.
    // IMPORTANT: SpessaSynth (like all MIDI) expects velocity as an
    // INTEGER 1-127. This app stores velocity as a 0-1 float (e.g. 0.85).
    // Passing 0.85 to noteOn truncates to 0, which MIDI treats as
    // note-OFF — so no voice allocates and the render is silent. We
    // convert here: 0-1 floats scale to 1-127; values already >1 are
    // assumed to be 0-127 and just clamped.
    const toMidiVel = function(v) {
      if (v == null) return 100;
      let out;
      if (v <= 1) out = Math.round(v * 127);   // 0-1 float → 0-127
      else out = Math.round(v);                  // already 0-127
      if (out < 1) out = 1;                       // never 0 (= note-off)
      if (out > 127) out = 127;
      return out;
    };
    const events = [];
    const clips = track.clips || [];
    for (const clip of clips) {
      if (clip.active === false) continue;
      const notes = midiClipNotesInWindow(clip, 0, totalDur);
      for (const n of notes) {
        const tOn = n.projectTime;
        if (tOn < 0 || tOn >= totalDur) continue;
        events.push({ at: tOn, on: true, pitch: n.pitch, vel: toMidiVel(n.velocity) });
        const tOff = Math.min(tOn + (n.duration || 0.1), totalDur);
        events.push({ at: tOff, on: false, pitch: n.pitch, vel: 0 });
      }
    }
    if (events.length === 0) { if (diag) diag.push((track.name||"?") + ": no notes in window"); return null; }
    events.sort(function(a, b){ return a.at - b.at; });
    if (diag) diag.push((track.name||"?") + ": " + (events.length/2) + " notes collected");

    // Get the .sf2/.sf3 bytes. URL resolution mirrors the live engine:
    // customUrl wins (admin/uploaded soundfonts); otherwise build the CDN
    // URL from the program number (built-in MuseScore soundfonts). Without
    // the CDN fallback, built-in soundfonts had no customUrl and exported
    // silent.
    let url = inst.customUrl;
    if (!url) {
      const prog = inst.gmProgramNumber || 0;
      const programStr = String(prog).padStart(3, "0");
      url = BF_SOUNDFONT_CDN_BASE + "/" + programStr + ".sf3";
      if (diag) diag.push((track.name||"?") + ": no customUrl, using CDN " + programStr + ".sf3");
    }
    if (!url) { if (diag) diag.push((track.name||"?") + ": NO SOUNDFONT URL on inst"); console.warn("[export-sf] no soundfont URL:", track.name); return null; }
    let sfBytes = bfGetCachedSfBytes(url);
    if (sfBytes) {
      if (diag) diag.push((track.name||"?") + ": sf bytes from cache");
    } else {
      if (diag) diag.push((track.name||"?") + ": fetching sf from " + url.slice(0, 48) + "...");
      try {
        const resp = await fetch(url, { mode: "cors", credentials: "omit" });
        if (!resp.ok) { if (diag) diag.push((track.name||"?") + ": FETCH FAILED " + resp.status); console.warn("[export-sf] fetch failed:", resp.status, url); return null; }
        sfBytes = await resp.arrayBuffer();
      } catch(e) { if (diag) diag.push((track.name||"?") + ": FETCH ERROR " + (e&&e.message||e)); console.warn("[export-sf] fetch error:", e); return null; }
    }
    // Normalize to a fresh ArrayBuffer for SpessaSynth.
    let sfArrayBuffer;
    if (sfBytes instanceof ArrayBuffer) {
      sfArrayBuffer = sfBytes.slice(0);
    } else if (sfBytes && sfBytes.buffer) {
      sfArrayBuffer = sfBytes.buffer.slice(sfBytes.byteOffset || 0, (sfBytes.byteOffset || 0) + sfBytes.byteLength);
    } else {
      if (diag) diag.push((track.name||"?") + ": UNEXPECTED sf bytes type");
      console.warn("[export-sf] unexpected sf bytes type:", track.name);
      return null;
    }
    if (diag) diag.push((track.name||"?") + ": sf size " + Math.round(sfArrayBuffer.byteLength/1024) + "KB");

    // ── Determine which preset to select (multi-bank correctness) ──
    // The track's resolved inst already carries the user's chosen
    // bank+program (resolveTrackInstrumentMeta applies selectedSfPreset).
    const requestedBank    = inst.gmBankNumber    || 0;
    const requestedProgram = inst.gmProgramNumber || 0;
    // Parse the real preset list and run the same matching the live
    // engine uses, so we pick exactly what FluidSynth would.
    let selBank = requestedBank;
    let selProgram = requestedProgram;
    try {
      const presetList = bfSfParsePresetList(sfArrayBuffer.slice(0));
      if (presetList && presetList.length > 0) {
        let chosen = presetList.find(function(p){
          return p.bank === requestedBank && p.program === requestedProgram;
        });
        if (!chosen && requestedBank !== 0) {
          chosen = presetList.find(function(p){
            return p.bank === 0 && p.program === requestedProgram;
          });
        }
        if (!chosen) chosen = presetList[0];
        selBank = chosen.bank;
        selProgram = chosen.program;
        if (diag) diag.push((track.name||"?") + ": phdr " + presetList.length + " presets, req(" + requestedBank + "," + requestedProgram + ") picked(" + selBank + "," + selProgram + ") " + (chosen.name||"").slice(0,16));
      } else {
        if (diag) diag.push((track.name||"?") + ": phdr parsed 0 presets, using req(" + requestedBank + "," + requestedProgram + ")");
      }
    } catch(parseErr) {
      if (diag) diag.push((track.name||"?") + ": phdr parse threw — " + (parseErr&&parseErr.message||parseErr));
    }

    // ── Create the synth and load the bank ──
    let synth, soundBank;
    try {
      soundBank = SoundBankLoader.fromArrayBuffer(sfArrayBuffer);
      // maxBufferSize must be >= our render chunk (128). Set it explicitly
      // so process() never throws "sampleCount exceeds maxBufferSize".
      // enableEffects:false — SpessaSynth's internal reverb/chorus/delay
      // add a wet swell to the first note's attack (the export-only
      // fade-in bug). The track's own FX chain handles reverb etc. in the
      // offline graph, so the synth should render a DRY signal with a
      // clean immediate attack, matching live FluidSynth.
      synth = new SpessaSynthProcessor(SR, { enableEffects: false, enableEventSystem: false });
      synth.soundBankManager.addSoundBank(soundBank, "main");
      // processorInitialized MUST be awaited (required for SF3/compressed,
      // harmless otherwise).
      if (synth.processorInitialized && typeof synth.processorInitialized.then === "function") {
        await synth.processorInitialized;
      }
      // Correct method per official 4.3.x docs is setSystemParameter,
      // NOT setMasterParameter (which doesn't exist — my earlier code
      // called the wrong name and it silently threw). Raise the voice
      // cap so dense parts don't get starved.
      try { synth.setSystemParameter("voiceCap", 256); } catch(e){ if(diag) diag.push("voiceCap set failed: " + (e&&e.message||e)); }
      if (diag) diag.push((track.name||"?") + ": synth created, bank added");
      // Read what SpessaSynth itself parsed — authoritative preset list.
      try {
        const sbPresets = soundBank && soundBank.presets;
        if (sbPresets && sbPresets.length > 0) {
          if (diag) diag.push((track.name||"?") + ": SpessaSynth sees " + sbPresets.length + " presets; first=(" + (sbPresets[0].bankMSB != null ? sbPresets[0].bankMSB : sbPresets[0].bank) + "," + (sbPresets[0].program) + ") " + ((sbPresets[0].name||sbPresets[0].presetName||"")+"").slice(0,16));
        } else {
          if (diag) diag.push((track.name||"?") + ": SpessaSynth parsed NO presets from bank!");
        }
      } catch(pe){ if (diag) diag.push((track.name||"?") + ": preset introspect threw — " + (pe&&pe.message||pe)); }
    } catch(initErr) {
      if (diag) diag.push((track.name||"?") + ": SYNTH INIT FAILED — " + (initErr&&initErr.message||initErr));
      console.warn("[export-sf] synth init failed:", track.name, initErr);
      return null;
    }

    // ── Select the preset on channel 0 via DIRECT preset assignment ──
    // SpessaSynth's MIDIChannel.preset can be set directly to a parsed
    // preset object — the authoritative way to pick a preset regardless
    // of bank number or drum status. This avoids CC0 bank-select, which
    // is 7-bit (so bank 128 can't be expressed) and avoids wrongly
    // assuming bank 128 means drums (some multi-banks put MELODIC presets
    // there, e.g. Thumpiano in Pure Phlames). We match SpessaSynth's own
    // parsed preset list on (bank, program) and assign it.
    const channel = 0;
    let directAssigned = false;
    try {
      const sbPresets = (soundBank && soundBank.presets) || [];
      // Match on bank (bankMSB in 4.x, or legacy .bank) + program.
      const wantBank = selBank, wantProg = selProgram;
      let match = sbPresets.find(function(p){
        const pb = (p.bankMSB != null) ? p.bankMSB : p.bank;
        return pb === wantBank && p.program === wantProg;
      });
      // Some drum presets report bank via isGMGSDrum rather than 128.
      if (!match && wantBank === 128) {
        match = sbPresets.find(function(p){
          return (p.isGMGSDrum || p.isDrum) && p.program === wantProg;
        });
      }
      if (match && synth.midiChannels && synth.midiChannels[channel]) {
        synth.midiChannels[channel].preset = match;
        directAssigned = true;
        if (diag) diag.push((track.name||"?") + ": DIRECT preset assign (" + wantBank + "," + wantProg + ") " + ((match.name||match.presetName||"")+"").slice(0,16));
      }
    } catch(assignErr) {
      if (diag) diag.push((track.name||"?") + ": direct assign failed — " + (assignErr&&assignErr.message||assignErr));
    }
    try {
      if (!directAssigned) {
        // Fallback: CC bank-select + program change. Won't handle bank 128
        // but covers normal banks if direct assignment wasn't available.
        synth.controllerChange(channel, 0, selBank & 0x7F);
        synth.controllerChange(channel, 32, 0);
        synth.programChange(channel, selProgram & 0x7F);
        if (diag) diag.push((track.name||"?") + ": CC select fallback bank=" + (selBank & 0x7F) + " prog=" + selProgram);
      }
      // Main Volume / Expression to full so the mixer's volGain node is the
      // sole level control (SpessaSynth's CC#7 default is 100 ≈ 79%).
      synth.controllerChange(channel, 7, 127);   // Main Volume full
      synth.controllerChange(channel, 11, 127);  // Expression full
      // Apply the user's Edit Sound ADSR/filter overrides as CCs so the
      // export matches what they hear live. No overrides → native envelope.
      var _ovCCs = bfSoundfontOverrideCCs(track.instrumentOverrides);
      for (var _ci = 0; _ci < _ovCCs.length; _ci++) {
        try { synth.controllerChange(channel, _ovCCs[_ci].cc, _ovCCs[_ci].value); } catch(_){}
      }
      if (diag) diag.push((track.name||"?") + ": vol/expr=127 overrideCCs=" + _ovCCs.length);
    } catch(selErr) {
      if (diag) diag.push((track.name||"?") + ": preset select failed — " + (selErr&&selErr.message||selErr));
      console.warn("[export-sf] preset select failed (continuing with default):", selErr);
    }

    // ── Render loop: fire events in chunks, pull audio out ──
    const BUF = 128;
    const totalSamples = Math.ceil(totalDur * SR);
    const outL = new Float32Array(totalSamples);
    const outR = new Float32Array(totalSamples);
    let filled = 0;
    let ei = 0;
    let notesFired = 0;
    let processErrored = false;
    let loggedVoiceCount = false;
    // Log the first note's details so we can check pitch/velocity sanity.
    if (diag && events.length > 0) {
      const firstOn = events.find(function(e){ return e.on; });
      if (firstOn) diag.push((track.name||"?") + ": first note pitch=" + firstOn.pitch + " vel=" + firstOn.vel + " at=" + firstOn.at.toFixed(2) + "s");
    }
    while (filled < totalSamples) {
      const chunk = Math.min(BUF, totalSamples - filled);
      const chunkEnd = (filled + chunk) / SR;
      while (ei < events.length && events[ei].at < chunkEnd) {
        const e = events[ei];
        try {
          if (e.on) { synth.noteOn(channel, Math.round(e.pitch), e.vel); notesFired++; }
          else      synth.noteOff(channel, Math.round(e.pitch));
        } catch(noteErr){
          if (diag && !loggedVoiceCount) diag.push((track.name||"?") + ": noteOn THREW — " + (noteErr&&noteErr.message||noteErr));
        }
        ei++;
      }
      // After the first note fires, capture voiceCount — the decisive
      // measurement. 0 = no voice allocated (preset has no matching zone
      // for this pitch, or preset is empty). >0 = voice exists but renders
      // silent (envelope/sample-decode/SF3 issue).
      if (!loggedVoiceCount && notesFired > 0 && diag) {
        var vc = "n/a";
        try { vc = String(synth.voiceCount); } catch(_){}
        diag.push((track.name||"?") + ": voiceCount after noteOn = " + vc);
        loggedVoiceCount = true;
      }
      try {
        synth.process(outL, outR, filled, chunk);
      } catch(procErr) {
        if (!processErrored && diag) diag.push((track.name||"?") + ": PROCESS ERROR — " + (procErr&&procErr.message||procErr));
        processErrored = true;
        console.warn("[export-sf] process error:", track.name, procErr);
        break;
      }
      filled += chunk;
    }

    // Measure peak so we can tell silence from real audio.
    let peak = 0;
    for (let i = 0; i < totalSamples; i += 97) {  // sparse scan, fast
      const a = Math.abs(outL[i]);
      if (a > peak) peak = a;
    }
    if (diag) diag.push((track.name||"?") + ": fired " + notesFired + " notes, peak=" + peak.toFixed(4) + (peak < 0.0001 ? " (SILENT!)" : " (has audio)"));

    // ── Pack into an AudioBuffer bound to the export's offline ctx ──
    let buf;
    try {
      buf = ocForBuffer.createBuffer(2, totalSamples, SR);
      buf.copyToChannel(outL, 0);
      buf.copyToChannel(outR, 1);
    } catch(bufErr) {
      if (diag) diag.push((track.name||"?") + ": buffer pack failed — " + (bufErr&&bufErr.message||bufErr));
      console.warn("[export-sf] buffer pack failed:", track.name, bufErr);
      return null;
    }
    return buf;
  };

  // ═════════════════════════════════════════════════════════════════════
  // Offline export — PHASE 1 (everything except soundfonts)
  // ═════════════════════════════════════════════════════════════════════
  //
  // Renders drums, audio clips, samplers, and synth-v1 MIDI into a single
  // OfflineAudioContext with full FX chains. Soundfont tracks are SKIPPED
  // in this phase (they'll be added in phase 2 via SpessaSynth) — but we
  // warn the user if the project contains soundfont tracks so the export
  // isn't silently incomplete.
  //
  // Why offline: deterministic, faster-than-real-time, glitch-impossible.
  // The real-time export remains available as a separate menu item.
  //
  // Critical detail learned from the failed first attempt: sampler tracks
  // CANNOT be scheduled via scheduleMidiTrackOffline because that helper
  // resolves instruments via resolveTrackInstrumentMeta, which returns
  // piano metadata for sampler tracks (no audioBuffer → silent). Instead
  // we mirror the LIVE scheduling path: if tr.samplerData, use
  // resolveSamplerInst(tr) to get the synthetic inst carrying the
  // decoded buffer.
  const exportMixOfflineBeta = async function () {
    setExporting(true);
    setExportMsg("Preparing offline render...");
    setExportProgress(0.02);
    // Yield so the export overlay/progress bar actually paints before the
    // heavy synchronous render work starts. Without this, the blocking
    // SpessaSynth render begins before React paints and the load bar
    // never appears — most noticeable on a quick single-soundfont export.
    await new Promise(function(r){ setTimeout(r, 60); });

    try {
      const liveTracks = tracksRef.current || tracks || [];

      // ── Duration ──
      let totalDur = 0;
      for (const tr of liveTracks) {
        const clips = tr.clips || [];
        for (const cl of clips) {
          if (cl.active === false) continue;
          let clipEnd = 0;
          if (tr.type === "drumSequencer" || tr.type === "midi") {
            clipEnd = (cl.startTime || 0) + (cl.duration || 0);
          } else if (cl.audioBuffer) {
            const trimS = cl.trimStart || 0;
            const trimE = cl.trimEnd !== undefined ? cl.trimEnd : cl.audioBuffer.duration;
            clipEnd = (cl.startTime || 0) + Math.max(0, trimE - trimS);
          }
          if (clipEnd > totalDur) totalDur = clipEnd;
        }
      }
      totalDur += 2.0;
      if (totalDur < 0.5) {
        setExportMsg("Nothing to export");
        setTimeout(function(){ setExporting(false); setExportMsg(""); }, 2000);
        return;
      }

      // ── Count soundfont tracks so we can warn about phase-1 omission ──
      let sfCount = 0;
      for (const tr of liveTracks) {
        if (tr.type === "midi" && !tr.samplerData && !tr.isMuted) {
          const meta = resolveTrackInstrumentMeta(tr);
          if (meta && meta.engine === "soundfont-v1") {
            const hasNotes = (tr.clips || []).some(function(c){
              return c.active !== false && c.notes && c.notes.length > 0;
            });
            if (hasNotes) sfCount++;
          }
        }
      }

      // ── Sample rate ──
      const liveActx = (typeof actxRef !== "undefined") && actxRef && actxRef.current;
      const liveSR = liveActx && liveActx.sampleRate ? liveActx.sampleRate : 48000;
      const SR = (liveSR >= 22050 && liveSR <= 96000) ? liveSR : 48000;

      // ── Make sure all sampler samples are decoded & cached ──
      // resolveSamplerInst reads bfSamplerCache.buffers by hash. If the
      // user just loaded the project and never hit play, the cache may be
      // cold. We pre-load every sampler track's sample (and pitch buffer)
      // before rendering. We decode into the LIVE context — AudioBuffers
      // are shareable across contexts per spec, and this keeps the cache
      // consistent for live playback afterward.
      setExportMsg("Loading samples...");
      setExportProgress(0.06);
      const samplerTracks = liveTracks.filter(function(tr){
        return tr.type === "midi" && tr.samplerData && tr.samplerData.sampleHash && !tr.isMuted;
      });
      for (const tr of samplerTracks) {
        try {
          await ensureSamplerLoaded(tr, liveActx || (typeof getActx === "function" ? getActx() : null));
        } catch(e) { console.warn("[export-beta] sampler preload failed:", tr.name, e); }
      }
      // Give any kicked-off pitch renders a moment to land in the cache.
      await new Promise(function(r){ setTimeout(r, 250); });

      // ── Build offline context ──
      setExportMsg("Building mix...");
      setExportProgress(0.12);
      // 0.5s tail headroom (+ START_LEAD pushes everything 0.2s right, so we
      // need that much extra room at the end before the render context cuts).
      const ocLen = Math.ceil((totalDur + 0.5) * SR);
      const oc = new OfflineAudioContext(2, ocLen, SR);

      // Worklets some FX need.
      const needsAutotune  = liveTracks.some(function(t){ return t.effects && t.effects.autotune && t.effects.autotune.on; });
      const needsNoiseGate = liveTracks.some(function(t){ return t.effects && t.effects.noiseremover && t.effects.noiseremover.on; });
      if (needsAutotune)  { try { await registerAutotuneWorklet(oc); } catch(e){ console.warn("[export-beta] autotune worklet:", e); } }
      if (needsNoiseGate) { try { await registerRNNoiseWorklet(oc); } catch(e){ console.warn("[export-beta] noisegate worklet:", e); } }

      const exportMaster = oc.createGain();
      exportMaster.gain.value = masterVolume;
      exportMaster.connect(oc.destination);

      // Global startup lead: shift EVERY scheduled source forward by this
      // amount so nothing begins at exactly sample 0. OfflineAudioContext
      // 200ms global lead. 50ms wasn't enough on iOS Safari — the first kick's
      // transient ("punch") was being clipped by the OfflineAudioContext's
      // startup behaviour (the first few sample blocks can have anomalies on
      // iOS regardless of when sources are scheduled). 200ms is far past any
      // startup artifact, and a post-render trim removes the silent intro so
      // the exported file doesn't start with a long pause.
      const START_LEAD = 0.2;

      const hasSolo = liveTracks.some(function(t){ return t.isSoloed; });

      // ── Drum kit buffers in the offline context ──
      const drumKitsForExport = {};
      const drumKitIdsUsed = new Set();
      for (const tr of liveTracks) {
        if (tr.type === "drumSequencer" && (tr.clips || []).length > 0 && !tr.isMuted) {
          drumKitIdsUsed.add(tr.kitId || "default");
        }
      }
      if (drumKitIdsUsed.size > 0) {
        setExportMsg("Loading drum kits...");
        for (const kid of drumKitIdsUsed) {
          try { drumKitsForExport[kid] = await loadKitBuffers(oc, kid); }
          catch(e){ console.warn("[export-beta] kit load:", kid, e); drumKitsForExport[kid] = {}; }
        }
      }

      // ── PHASE 2: pre-render soundfont tracks via SpessaSynth ──
      // Each soundfont MIDI track is rendered to an AudioBuffer (bound to
      // the offline ctx) which we later feed through that track's FX
      // chain as a BufferSource. We load SpessaSynth lazily so the
      // library is only fetched when an export actually needs it.
      const preRenderedSf = {};  // trackId -> AudioBuffer
      const sfDiag = [];  // on-screen diagnostics (no iPhone console access)
      if (sfCount > 0) {
        let SpessaSynthProcessor = null, SoundBankLoader = null;
        try {
          // Static literal specifier — webpack needs this to build the
          // lazy chunk. (An earlier attempt used a computed string to
          // dodge Vite's build-time resolution, but this project uses
          // webpack/CRA, where a non-literal import() expression triggers
          // "Critical dependency: the request of a dependency is an
          // expression" and fails the CI build.)
          const mod = await import("spessasynth_core");
          SpessaSynthProcessor = mod.SpessaSynthProcessor;
          SoundBankLoader = mod.SoundBankLoader;
          sfDiag.push("SpessaSynth imported OK");
        } catch(impErr) {
          sfDiag.push("IMPORT FAILED: " + (impErr && impErr.message || impErr));
          console.warn("[export-beta] SpessaSynth import failed:", impErr);
        }
        if (SpessaSynthProcessor && SoundBankLoader) {
          const sfTracksToRender = liveTracks.filter(function(tr){
            if (tr.isMuted) return false;
            if (hasSolo && !tr.isSoloed) return false;
            if (tr.type !== "midi" || tr.samplerData) return false;
            const meta = resolveTrackInstrumentMeta(tr);
            return meta && meta.engine === "soundfont-v1";
          });
          sfDiag.push(sfTracksToRender.length + " soundfont track(s) to render");
          for (let i = 0; i < sfTracksToRender.length; i++) {
            const sfTr = sfTracksToRender[i];
            setExportMsg("Rendering " + (sfTr.name || "instrument") + " (" + (i+1) + "/" + sfTracksToRender.length + ")...");
            setExportProgress(0.15 + 0.12 * (i / Math.max(1, sfTracksToRender.length)));
            // Yield so the progress UI paints this track's name before the
            // blocking synchronous SpessaSynth render starts.
            await new Promise(function(r){ setTimeout(r, 30); });
            try {
              const buf = await renderSoundfontTrackWithSpessa(
                sfTr, totalDur, SR, oc, SpessaSynthProcessor, SoundBankLoader, sfDiag
              );
              if (buf) preRenderedSf[sfTr.id] = buf;
              else { sfDiag.push((sfTr.name||"?") + ": returned NO buffer"); console.warn("[export-beta] no buffer rendered for:", sfTr.name); }
            } catch(sfErr) {
              sfDiag.push((sfTr.name||"?") + ": THREW — " + (sfErr&&sfErr.message||sfErr));
              console.warn("[export-beta] soundfont render failed:", sfTr.name, sfErr);
            }
          }
        }
        // Write diagnostics to the persistent debug log (survives the
        // post-export refresh — viewable via the debug panel any time).
        if (sfDiag.length > 0) {
          bfDebugLog("──── SF EXPORT (" + new Date().toLocaleTimeString() + ") ────");
          for (const line of sfDiag) bfDebugLog(line);
        }
      }

      // ── Schedule every track ──
      setExportMsg("Scheduling...");
      setExportProgress(0.30);
      for (const tr of liveTracks) {
        if (tr.isMuted) continue;
        if (hasSolo && !tr.isSoloed) continue;
        const clips = tr.clips || [];
        if (clips.length === 0) continue;

        const entry = buildChain(oc, exportMaster, tr, hasSolo, { registerLive: false });

        // Drums
        if (tr.type === "drumSequencer") {
          const buffers = drumKitsForExport[tr.kitId || "default"] || {};
          const kitMetaForExport = getKitMeta(tr.kitId || "default");
          try { scheduleDrumTrackOffline(oc, entry, tr, buffers, bpm, START_LEAD, 0, totalDur, kitMetaForExport); }
          catch(e){ console.warn("[export-beta] drum schedule:", e); }
          continue;
        }

        // MIDI (sampler OR synth-v1 OR soundfont)
        if (tr.type === "midi") {
          // Resolve the instrument the SAME way live playback does.
          let inst;
          if (tr.samplerData) {
            inst = resolveSamplerInst(tr);
          } else {
            const baseInst = resolveTrackInstrumentMeta(tr);
            inst = applyInstrumentOverrides(baseInst, tr.instrumentOverrides);
          }
          // PHASE 2: soundfont tracks use a pre-rendered SpessaSynth
          // buffer fed through the FX chain. Timing is already baked into
          // the buffer (notes were placed at projectTime during render),
          // so the source starts at t=0.
          if (inst && inst.engine === "soundfont-v1") {
            const preBuf = preRenderedSf[tr.id];
            bfDebugLog("SF SCHEDULE " + (tr.name||"?") + ": vol=" + (tr.volume ?? "undef") + " pan=" + (tr.pan ?? "undef") + " muted=" + !!tr.isMuted + " buf=" + (preBuf ? "yes" : "NO"));
            if (preBuf) {
              try {
                const src = oc.createBufferSource();
                src.buffer = preBuf;
                src.connect(entry);
                src.start(START_LEAD);
              } catch(e){ console.warn("[export-beta] sf source failed:", tr.name, e); }
            }
            continue;
          }
          if (!inst) continue;

          // Schedule each note through scheduleMidiNote with the resolved
          // inst. This covers sampler-v1 and synth-v1.
          for (const clip of clips) {
            if (clip.active === false) continue;
            const notes = midiClipNotesInWindow(clip, 0, totalDur);
            for (let i = 0; i < notes.length; i++) {
              const n = notes[i];
              const when = n.projectTime + START_LEAD;
              if (when < 0) continue;
              try {
                scheduleMidiNote(oc, entry, inst, n.pitch, n.velocity, when, n.duration, tr.noteCutMode);
              } catch(e){ /* one bad note shouldn't kill the render */ }
            }
          }
          continue;
        }

        // Audio clips
        for (const cl of clips) {
          if (cl.active === false || !cl.audioBuffer) continue;
          try {
            const src = oc.createBufferSource();
            src.buffer = cl.audioBuffer;
            const trimS = cl.trimStart || 0;
            const trimE = cl.trimEnd !== undefined ? cl.trimEnd : cl.audioBuffer.duration;
            const trimDur = Math.max(0, trimE - trimS);
            if (trimDur <= 0) continue;
            const fadeIn  = Math.max(0, cl.fadeIn  || 0);
            const fadeOut = Math.max(0, cl.fadeOut || 0);
            const safeStart = (cl.startTime || 0) + START_LEAD;
            if (fadeIn > 0 || fadeOut > 0) {
              const g = oc.createGain();
              src.connect(g); g.connect(entry);
              g.gain.setValueAtTime(fadeIn > 0 ? 0.0001 : 1.0, safeStart);
              if (fadeIn > 0) g.gain.linearRampToValueAtTime(1.0, safeStart + Math.min(fadeIn, trimDur));
              if (fadeOut > 0) {
                const fs = safeStart + trimDur - fadeOut;
                g.gain.setValueAtTime(1.0, fs);
                g.gain.linearRampToValueAtTime(0.0001, safeStart + trimDur);
              }
            } else {
              src.connect(entry);
            }
            src.start(safeStart, trimS, trimDur);
          } catch(e){ console.warn("[export-beta] audio clip:", e); }
        }
      }

      // ── Render ──
      const sfRendered = Object.keys(preRenderedSf).length;
      const sfMissing = Math.max(0, sfCount - sfRendered);
      setExportMsg(sfMissing > 0
        ? ("Rendering (" + sfMissing + " soundfont track" + (sfMissing===1?"":"s") + " failed to load)...")
        : "Rendering mix...");
      setExportProgress(0.55);
      const tick = setInterval(function(){
        setExportProgress(function(p){ return p >= 0.88 ? p : p + 0.004; });
      }, 200);
      let rendered;
      try { rendered = await oc.startRendering(); }
      finally { clearInterval(tick); }

      // ── Trim leading silence ──
      // The 200ms START_LEAD guarantees the first transient is fully
      // captured (well past the OAC's startup artifact that was eating the
      // kick's punch), but we don't want users to hear 200ms of silence at
      // the start of their export. Find the first sample with energy above
      // -46dB, back off 5ms so we never clip into the transient's rising
      // edge, and emit a buffer starting from there.
      (function trimLeadingSilence(){
        try {
          const nc = rendered.numberOfChannels;
          const sr = rendered.sampleRate;
          const len = rendered.length;
          const threshold = 0.005;          // -46dB
          const guard = Math.round(sr * 0.005); // 5ms safety margin
          const chans = [];
          for (let c = 0; c < nc; c++) chans.push(rendered.getChannelData(c));
          let firstNonSilent = -1;
          for (let i = 0; i < len; i++) {
            for (let c = 0; c < nc; c++) {
              if (Math.abs(chans[c][i]) > threshold) { firstNonSilent = i; break; }
            }
            if (firstNonSilent >= 0) break;
          }
          if (firstNonSilent < 0) return;          // entirely silent — leave as-is
          const trimStart = Math.max(0, firstNonSilent - guard);
          if (trimStart < sr * 0.01) return;       // < 10ms to trim — not worth it
          const newLen = len - trimStart;
          const trimmedCtx = new (window.OfflineAudioContext || window.webkitOfflineAudioContext)(nc, newLen, sr);
          const trimmed = trimmedCtx.createBuffer(nc, newLen, sr);
          for (let c = 0; c < nc; c++) {
            const src = chans[c];
            const dst = trimmed.getChannelData(c);
            for (let i = 0; i < newLen; i++) dst[i] = src[i + trimStart];
          }
          rendered = trimmed;
        } catch(_) { /* on any failure, fall through and ship the untrimmed buffer */ }
      })();

      // ── Encode + download ──
      setExportMsg("Encoding...");
      setExportProgress(0.92);
      const wavBuf = await audioBufferToWavAsync(rendered, function(frac){
        setExportProgress(0.92 + 0.07 * frac);
      });
      const wavBlob = new Blob([wavBuf], { type: "audio/wav" });
      const a = document.createElement("a");
      a.href = URL.createObjectURL(wavBlob);
      a.download = (projectName || "project") + " - mix.wav";
      try { sessionStorage.setItem("bf_share_sheet_active", String(Date.now())); } catch(_){}
      a.click();
      setTimeout(function(){ try { URL.revokeObjectURL(a.href); } catch(_){} }, 1000);

      setExportMsg(sfMissing > 0 ? ("Done (" + sfMissing + " soundfont" + (sfMissing===1?"":"s") + " missing)") : "Done!");
      setExportProgress(1);
      setTimeout(function(){ setExporting(false); setExportMsg(""); setExportProgress(0); }, 3000);
      setTimeout(function(){ try { sessionStorage.removeItem("bf_share_sheet_active"); } catch(_){} }, 60000);

    } catch (e) {
      console.error("[export-beta] Failed:", e);
      setExportMsg("Export failed: " + (e.message || "unknown error"));
      setTimeout(function(){ setExporting(false); setExportMsg(""); setExportProgress(0); }, 4000);
    }
  };



  const fmt = function (s) {
    s=Math.max(0,s||0);
    const m=Math.floor(s/60),sec=Math.floor(s%60),ms=Math.floor((s%1)*10);
    return (m<10?"0":"")+m+":"+(sec<10?"0":"")+sec+"."+ms;
  };

  // ── RENDER ────────────────────────────────────────────────────
  return (
    <div
      style={{ background:"#000", height:"calc(100% - 72px - env(safe-area-inset-bottom))", display:"flex", flexDirection:"column", fontFamily:"'DM Sans',sans-serif", overflow:"hidden", overscrollBehavior:"none", WebkitUserSelect:"none", userSelect:"none", WebkitTouchCallout:"none" }}
      onClick={function(){ setContextMenu(null); setShowProjMenu(false); setShowSettings(false); setShowAddMenu(false); setShowProjects(false); setSelectedClipId(null); setSelBox(null); setSelClipIds(new Set()); }}
    >
      {/* ── Overlays ── */}
      {/* Studio welcome modal — shows once per session on first open.
          Sets expectations about beta status + iOS Web Audio limits
          before the user dives in and potentially loses work. */}
      {showStudioWelcome && (
        <div
          onClick={function() { setShowStudioWelcome(false); }}
          style={{
            position: "fixed", inset: 0, zIndex: 9500,
            background: "rgba(0,0,0,0.82)", backdropFilter: "blur(6px)",
            display: "flex", alignItems: "center", justifyContent: "center",
            padding: 20, fontFamily: "'DM Sans',sans-serif",
          }}>
          <div
            onClick={function(e) { e.stopPropagation(); }}
            style={{
              background: "linear-gradient(165deg,#1a0f1f 0%,#0f0a1f 50%,#0a0a14 100%)",
              border: "1px solid rgba(192,38,211,0.35)",
              borderRadius: 16,
              padding: "24px 22px 20px",
              maxWidth: 380, width: "100%",
              boxShadow: "0 24px 48px rgba(0,0,0,0.7), 0 0 32px rgba(192,38,211,0.18)",
              position: "relative",
            }}>
            {/* LED top edge */}
            <div style={{
              position: "absolute", top: 0, left: 16, right: 16, height: 2,
              background: "linear-gradient(90deg,transparent,#C026D3,#7C3AED,#3B82F6,transparent)",
              boxShadow: "0 0 8px rgba(124,58,237,0.5)",
            }} />

            {/* BETA chip */}
            <div style={{ display: "flex", justifyContent: "center", marginBottom: 12 }}>
              <span style={{
                display: "inline-block",
                background: "linear-gradient(135deg,rgba(192,38,211,0.22),rgba(124,58,237,0.16))",
                border: "1px solid rgba(192,38,211,0.45)",
                color: "#E9D5FF",
                fontFamily: "'Bebas Neue',sans-serif",
                fontSize: 11, fontWeight: 700, letterSpacing: 2,
                padding: "3px 10px", borderRadius: 8,
              }}>
                BETA · WEB APP
              </span>
            </div>

            <div style={{
              fontFamily: "'Bebas Neue',sans-serif",
              fontSize: 22, letterSpacing: 1.5, color: "white",
              textAlign: "center", marginBottom: 8,
            }}>
              WELCOME TO STUDIO
            </div>

            <div style={{ color: "#ccc", fontSize: 13, lineHeight: 1.55, marginBottom: 14 }}>
              BeatFinder Studio is a full multi-track DAW running entirely in your
              browser. Powerful — but mobile browsers have limits.
            </div>

            <div style={{
              background: "rgba(245,158,11,0.08)",
              border: "1px solid rgba(245,158,11,0.3)",
              borderRadius: 10, padding: "12px 14px", marginBottom: 14,
            }}>
              <div style={{ color: "#FCD34D", fontSize: 11, fontWeight: 800, letterSpacing: 0.5, marginBottom: 6 }}>
                ⚠ THINGS TO KNOW
              </div>
              <ul style={{ margin: 0, padding: "0 0 0 16px", color: "#FDE68A", fontSize: 12, lineHeight: 1.6 }}>
                <li>Using FX on 4+ tracks may glitch or crash on iPhone</li>
                <li><strong>Save your work often</strong> — Web Audio is fragile on mobile</li>
                <li>Disable FX on tracks you aren't actively monitoring</li>
                <li>For best results: wired headphones, not Bluetooth</li>
              </ul>
            </div>

            <div style={{ color: "#A78BFA", fontSize: 12, lineHeight: 1.5, marginBottom: 18, textAlign: "center", fontStyle: "italic" }}>
              A native iOS app with better Studio performance is on the way.
            </div>

            <button
              onClick={function() { setShowStudioWelcome(false); }}
              style={{
                width: "100%",
                background: "linear-gradient(135deg,#C026D3,#7C3AED)",
                color: "white", border: "none",
                borderRadius: 12, padding: "13px 18px",
                fontSize: 14, fontWeight: 800, letterSpacing: 0.5,
                cursor: "pointer",
                boxShadow: "0 4px 16px rgba(192,38,211,0.4)",
              }}>
              Continue to Studio
            </button>
          </div>
        </div>
      )}

      {/* CPU-load warning — Web Audio on iOS Safari struggles past ~4 channels
          with active FX. Warning is informational; doesn't block anything.
          Shows once per session (auto-dismissed by tap or X). */}
      {(function() {
        if (typeof window === "undefined") return null;
        if (window.__bfStudioFxWarnDismissed) return null;
        var loadedFxTracks = (tracks || []).filter(function(t) {
          var fx = t && t.effects;
          if (!fx) return false;
          return !!(
            (fx.autotune  && fx.autotune.on)  ||
            (fx.reverb    && fx.reverb.on)    ||
            (fx.delay     && fx.delay.on)     ||
            (fx.eq        && fx.eq.on)        ||
            (fx.compressor && fx.compressor.on) ||
            (fx.noiseGate && fx.noiseGate.on)
          );
        }).length;
        if (loadedFxTracks < 4) return null;
        return (
          <div style={{
            position: "absolute",
            top: "calc(env(safe-area-inset-top) + 56px)",
            left: 12, right: 12,
            zIndex: 5000,
            background: "linear-gradient(135deg,#2a1a08 0%,#1a1208 100%)",
            border: "1px solid rgba(245,158,11,0.45)",
            borderRadius: 12,
            padding: "10px 14px",
            color: "#FBBF24",
            fontSize: 12, lineHeight: 1.45, fontFamily: "'DM Sans',sans-serif",
            boxShadow: "0 4px 16px rgba(0,0,0,0.5), 0 0 12px rgba(245,158,11,0.15)",
            display: "flex", gap: 10, alignItems: "flex-start",
          }}>
            <div style={{ flex: "0 0 auto", fontSize: 16, lineHeight: 1 }}>⚠️</div>
            <div style={{ flex: 1, color: "#FCD34D" }}>
              <strong style={{ color: "#FDE68A" }}>Beta — heads up:</strong> BeatFinder is currently a beta web app.
              Using FX on {loadedFxTracks} tracks may glitch or crash on iPhone — Web Audio
              has tight CPU limits on mobile browsers. Mute or disable FX on tracks you
              aren't actively using, and <strong style={{ color: "#FDE68A" }}>save your work often</strong>.
              <em style={{ color: "#A78BFA", display: "block", marginTop: 4 }}>
                A native iOS app with better Studio performance is on the way.
              </em>
            </div>
            <div
              onClick={function(e) {
                e.stopPropagation();
                try { window.__bfStudioFxWarnDismissed = true; } catch (_) {}
                // Force re-render so the warning disappears
                try {
                  var ev = new CustomEvent("bf:studio-warn-dismissed");
                  window.dispatchEvent(ev);
                } catch (_) {}
              }}
              style={{ flex: "0 0 auto", color: "#FBBF24", fontSize: 16, cursor: "pointer", padding: 2 }}>
              ×
            </div>
          </div>
        );
      })()}

      {/* Studio guides — opened via HELP button in the top bar */}
      {showGuides && (
        <StudioGuides
          onClose={function(){ setShowGuides(false); }}
          defaultTrack={user && user.isPro ? "producer" : "artist"}
        />
      )}

      {/* Admin debug overlay — toggle via 🐛 button in toolbar */}
      {showDebug && (
        <StudioDebugOverlay
          actxRef={actxRef}
          gainNodesRef={gainNodesRef}
          fxNodesRef={fxNodesRef}
          chainsRef={chainsRef}
          trackAnalysersRef={trackAnalysersRef}
          tracks={tracks}
          isPlaying={isPlaying}
          isRecording={isRecording}
          onClose={function(){ setShowDebug(false); }}
        />
      )}

      {showDebugPanel && user && (user.is_admin === true || user.username === "Trelli") && (function(){
        const _log = bfReadDebugLog();
        return (
        <div style={{ position:"absolute",inset:0,zIndex:9300,background:"rgba(0,0,0,0.97)",display:"flex",flexDirection:"column",paddingLeft:14,paddingRight:14,paddingTop:"calc(env(safe-area-inset-top, 0px) + 16px)",paddingBottom:"calc(env(safe-area-inset-bottom, 0px) + 16px)" }}>
          <div style={{ display:"flex",alignItems:"center",justifyContent:"space-between",marginBottom:10 }}>
            <div style={{ color:"#22D3EE",fontWeight:800,fontSize:16 }}>🐞 Debug Log</div>
            <button onClick={function(){ setShowDebugPanel(false); }} style={{ background:"rgba(40,40,46,0.9)",border:"1px solid rgba(255,255,255,0.1)",color:"#D4D4D8",borderRadius:9,padding:"8px 14px",fontSize:14,fontWeight:700,cursor:"pointer" }}>Close</button>
          </div>
          <div style={{ display:"flex",gap:8,marginBottom:10 }}>
            <button onClick={function(){
              try {
                const txt = bfReadDebugLog().join("\n");
                if (navigator.clipboard && navigator.clipboard.writeText) {
                  navigator.clipboard.writeText(txt);
                }
              } catch(_){}
            }} style={{ flex:1,background:"rgba(34,211,238,0.12)",border:"1px solid rgba(34,211,238,0.3)",color:"#22D3EE",borderRadius:9,padding:"9px",fontSize:13,fontWeight:700,cursor:"pointer" }}>Copy All</button>
            <button onClick={function(){
              try { sessionStorage.removeItem(DEBUG_LOG_KEY); } catch(_){}
              setDebugLogVersion(function(v){ return v + 1; });
            }} style={{ flex:1,background:"rgba(239,68,68,0.1)",border:"1px solid rgba(239,68,68,0.3)",color:"#F87171",borderRadius:9,padding:"9px",fontSize:13,fontWeight:700,cursor:"pointer" }}>Clear</button>
          </div>
          {(function(){
            let von = false;
            try { von = (typeof window !== "undefined" && typeof window.__bfDebugVerboseGet === "function") ? window.__bfDebugVerboseGet() : false; } catch(_){}
            let ton = false;
            try { ton = (typeof window !== "undefined" && typeof window.__bfTouchTraceGet === "function") ? window.__bfTouchTraceGet() : false; } catch(_){}
            return (
              <div style={{ display:"flex",gap:8,marginBottom:10,alignItems:"center",flexWrap:"wrap" }}>
                <button onClick={function(){
                  try {
                    const next = !((typeof window.__bfDebugVerboseGet === "function") && window.__bfDebugVerboseGet());
                    if (typeof window.__bfDebugVerboseSet === "function") window.__bfDebugVerboseSet(next);
                  } catch(_){}
                  setDebugLogVersion(function(v){ return v + 1; });
                }} style={{ flex:"1 1 140px",background: von ? "rgba(34,197,94,0.15)" : "rgba(80,80,90,0.25)",border:"1px solid " + (von ? "rgba(34,197,94,0.4)" : "rgba(255,255,255,0.12)"),color: von ? "#4ADE80" : "#9CA3AF",borderRadius:9,padding:"9px",fontSize:12,fontWeight:700,cursor:"pointer" }}>
                  Verbose engine logs: {von ? "ON" : "OFF"}
                </button>
                <button onClick={function(){
                  try {
                    const next = !((typeof window.__bfTouchTraceGet === "function") && window.__bfTouchTraceGet());
                    if (typeof window.__bfTouchTraceSet === "function") window.__bfTouchTraceSet(next);
                  } catch(_){}
                  setDebugLogVersion(function(v){ return v + 1; });
                }} style={{ flex:"1 1 140px",background: ton ? "rgba(244,114,182,0.15)" : "rgba(80,80,90,0.25)",border:"1px solid " + (ton ? "rgba(244,114,182,0.4)" : "rgba(255,255,255,0.12)"),color: ton ? "#F472B6" : "#9CA3AF",borderRadius:9,padding:"9px",fontSize:12,fontWeight:700,cursor:"pointer" }}>
                  Touch trace: {ton ? "ON" : "OFF"}
                </button>
              </div>
            );
          })()}
          <div style={{ flex:1,overflowY:"auto",WebkitOverflowScrolling:"touch",background:"#08080c",border:"1px solid #1d1d28",borderRadius:10,padding:"10px 12px" }}>
            {_log.length === 0 ? (
              <div style={{ color:"#6B7280",fontSize:13,fontStyle:"italic" }}>
                No log entries yet. Run an Offline Beta export, then reopen this panel — the diagnostics persist across the post-export refresh.
              </div>
            ) : (
              <div style={{ color:"#D4D4D8",fontSize:11,fontFamily:"monospace",lineHeight:1.5,whiteSpace:"pre-wrap",wordBreak:"break-word" }}>
                {_log.join("\n")}
              </div>
            )}
          </div>
          <div style={{ color:"#6B7280",fontSize:10,marginTop:8,textAlign:"center" }}>
            {_log.length} entries · admin only · v{debugLogVersion}
          </div>
        </div>
        );
      })()}

      {showExportWarning && (
        <div style={{ position:"absolute",inset:0,zIndex:9100,background:"rgba(0,0,0,0.96)",display:"flex",alignItems:"center",justifyContent:"center",flexDirection:"column",padding:"32px 20px" }}>
          {/* Title */}
          <div style={{ color:"white",fontWeight:800,fontSize:20,letterSpacing:0.2,marginBottom:16,textAlign:"center" }}>
            Before You Export
          </div>

          {/* Scrollable explanation area — modal content can exceed
              viewport on small phones once the tip section is added. */}
          <div style={{
            maxWidth:340,
            width:"100%",
            maxHeight:"calc(100vh - 240px)",
            overflowY:"auto",
            WebkitOverflowScrolling:"touch",
            marginBottom:16,
          }}>
            {/* Explanation card */}
            <div style={{
              background:"rgba(167,139,250,0.06)",
              border:"1px solid rgba(167,139,250,0.20)",
              borderRadius:14,
              padding:"18px 18px 14px",
              color:"#D4D4D8",
              fontSize:13,
              lineHeight:1.55,
              marginBottom:12,
            }}>
              <div style={{ color:"#A78BFA",fontWeight:700,fontSize:11,letterSpacing:1.2,textTransform:"uppercase",marginBottom:10 }}>
                Offline render
              </div>
              <div style={{ marginBottom:12 }}>
                Your song is rendered to a file silently and usually faster than real time. You don&apos;t need to listen through it — just keep the app open while it finishes.
              </div>
              <div style={{ color:"#A78BFA",fontWeight:700,fontSize:11,letterSpacing:1.2,textTransform:"uppercase",marginBottom:10,marginTop:14 }}>
                One thing to avoid
              </div>
              <div style={{ paddingLeft:4 }}>
                • Switching to another app or locking the screen mid-render<br/>
                &nbsp;&nbsp;&nbsp;(iOS can pause the render in the background)
              </div>
              <div style={{ color:"#9CA3AF",fontSize:12,marginTop:14,paddingTop:12,borderTop:"1px solid rgba(167,139,250,0.12)" }}>
                If the render is interrupted, just start the export again.
              </div>
            </div>

            {/* Tip card — cyan to differentiate from the warning card.
                Concrete lag-reduction steps the user can take before
                starting. Order is by impact: closing apps and DnD have
                the biggest effect; the rest are smaller improvements. */}
            <div style={{
              background:"rgba(34,211,238,0.05)",
              border:"1px solid rgba(34,211,238,0.18)",
              borderRadius:14,
              padding:"18px 18px 16px",
              color:"#D4D4D8",
              fontSize:13,
              lineHeight:1.55,
            }}>
              <div style={{ color:"#22D3EE",fontWeight:700,fontSize:11,letterSpacing:1.2,textTransform:"uppercase",marginBottom:10 }}>
                For best quality
              </div>
              <div style={{ paddingLeft:4 }}>
                • Close other apps in the app switcher<br/>
                • Turn on <span style={{ color:"#22D3EE",fontWeight:600 }}>Do Not Disturb</span><br/>
                • Plug into power (CPU throttles on low battery)<br/>
                • Don&apos;t scroll or tap during export<br/>
                • Keep the BeatFinder tab in the foreground
              </div>
              <div style={{ color:"#9CA3AF",fontSize:12,marginTop:14,paddingTop:12,borderTop:"1px solid rgba(34,211,238,0.10)" }}>
                These reduce CPU competition and prevent audio glitches in the recording.
              </div>
            </div>
          </div>

          {/* Buttons */}
          <div style={{ display:"flex",gap:10,width:"100%",maxWidth:340 }}>
            <button onClick={function(){ setShowExportWarning(false); }} style={{
              flex:1,
              height:48,
              borderRadius:11,
              background:"rgba(40,40,46,0.8)",
              border:"1px solid rgba(255,255,255,0.08)",
              color:"#D4D4D8",
              fontSize:14,
              fontWeight:700,
              cursor:"pointer",
            }}>
              Cancel
            </button>
            <button onClick={function(){
              setShowExportWarning(false);
              setTimeout(function(){ exportMixOfflineBeta(); }, 80);
            }} style={{
              flex:1.4,
              height:48,
              borderRadius:11,
              background: "linear-gradient(90deg, #7C3AED, #C026D3)",
              border:"none",
              color:"white",
              fontSize:14,
              fontWeight:800,
              letterSpacing:0.3,
              cursor:"pointer",
              boxShadow:"0 0 16px rgba(124,58,237,0.4)",
            }}>
              Start Export
            </button>
          </div>
        </div>
      )}

      {exporting && (
        <div style={{ position:"absolute",inset:0,zIndex:9000,background:"rgba(0,0,0,0.94)",display:"flex",alignItems:"center",justifyContent:"center",flexDirection:"column",gap:18,padding:"32px" }}>
          {/* Title */}
          <div style={{ color:"white",fontWeight:800,fontSize:18,letterSpacing:0.2 }}>Exporting Mix</div>

          {/* Progress bar — driven by setExportProgress(...) at known phase
              boundaries, plus per-band bumps and a creeping interval during
              the offline render. Unlike a CSS-animated spinner, this can
              never freeze: every visible state change is an explicit React
              render triggered by a useState write. iOS Safari will always
              repaint between awaits, so the user sees forward motion. */}
          <div style={{ width:"100%", maxWidth:280, height:8, background:"#1f1f23", borderRadius:6, overflow:"hidden", border:"1px solid #2a2a2e" }}>
            <div style={{
              height:"100%",
              width: Math.round(Math.max(0.02, Math.min(1, exportProgress)) * 100) + "%",
              background: "linear-gradient(90deg, #7C3AED, #C026D3)",
              borderRadius:6,
              transition: "width 0.25s cubic-bezier(0.4, 0, 0.2, 1)",
              boxShadow: "0 0 8px rgba(192,38,211,0.5)",
            }} />
          </div>

          {/* Percent + current step. Min-height keeps the line stable so
              the bar doesn't jump as messages of varying length swap in. */}
          <div style={{ color:"#bbb",fontSize:12,minHeight:18,textAlign:"center",fontFamily:"ui-monospace, SFMono-Regular, monospace" }}>
            {Math.round(Math.max(0, Math.min(1, exportProgress)) * 100)}% — {exportMsg}
          </div>

          {/* Time-estimate hint */}
          {exportMsg !== "Done!" && !exportMsg.startsWith("Export failed") && (
            <div style={{ color:"#666",fontSize:11,textAlign:"center",lineHeight:1.5,maxWidth:280,marginTop:4 }}>
              This can take 2–3 minutes depending on track length and how many FX you have on. Keep the app open — leaving may interrupt the export.
            </div>
          )}
        </div>
      )}

      {countIn > 0 && (
        <div style={{ position:"absolute",inset:0,zIndex:1000,background:"rgba(0,0,0,0.92)",display:"flex",alignItems:"center",justifyContent:"center",flexDirection:"column" }}>
          <div style={{ color:"#EF4444",fontSize:100,fontWeight:900,lineHeight:1 }}>{countIn}</div>
          <div style={{ color:"#555",fontSize:14,marginTop:8 }}>Allow mic when prompted</div>
        </div>
      )}

      {contextMenu && (
        <div style={{ position:"fixed", inset:0, zIndex:9000 }} onClick={function(){ setContextMenu(null); }}>
          {(function(){
            // ── Smart positioning ──
            // The menu has ~6-9 items depending on clip type; each row is
            // ~44px, plus a header. We measure availability dynamically
            // rather than guessing a fixed height. When the menu can't
            // fit below the touch point, we flip it above. When it
            // can't fit either way, we cap maxHeight and let the inner
            // scroll handle the overflow so no actions get hidden.
            const winH = window.innerHeight;
            const safeBottom = 100; // leave room for bottom transport bar
            const safeTop    = 60;  // status bar / safe area
            const tapY       = contextMenu.y;
            // How much room below the tap point?
            const roomBelow  = winH - safeBottom - (tapY + 8);
            // How much room above?
            const roomAbove  = (tapY - 8) - safeTop;
            // Prefer below if there's enough; otherwise flip above.
            const placeBelow = roomBelow >= 220 || roomBelow >= roomAbove;
            const maxH       = Math.max(180, placeBelow ? roomBelow : roomAbove);
            const topPx      = placeBelow
              ? Math.max(safeTop, tapY + 8)
              : Math.max(safeTop, tapY - maxH - 8);
            const leftPx     = Math.max(12, Math.min(contextMenu.x - 100, window.innerWidth - 220));
            return (
          <div onClick={function(e){ e.stopPropagation(); }} style={{
            position:"absolute",
            top: topPx,
            left: leftPx,
            background:"#1a1a1a",
            border:"1px solid #2a2a2a",
            borderRadius:16,
            overflowY: "auto",
            overflowX: "hidden",
            minWidth:204,
            maxWidth: 320,
            maxHeight: maxH,
            WebkitOverflowScrolling: "touch",
            boxShadow:"0 16px 48px rgba(0,0,0,0.95)",
          }}>
            <div style={{ padding:"10px 16px 8px", borderBottom:"1px solid #222", display:"flex", alignItems:"center", gap:8 }}>
              <div style={{ width:8,height:8,borderRadius:"50%",background:contextMenu.track.color,flexShrink:0 }} />
              <span style={{ color:"#666",fontSize:11,fontWeight:600 }}>{contextMenu.clip ? contextMenu.clip.label : contextMenu.track.name}</span>
            </div>
            {contextMenu.clip && (<>
              <button onClick={function(){
                const c = contextMenu.clip; const t = contextMenu.track;
                // ── Bar-aligned duplicate ──
                // Producers expect the copy to land cleanly on the next
                // bar so the music stays in time. Old code used
                // startTime + duration + 0.25s which was nearly always
                // off-grid. New logic:
                //   1. Compute ideal end: startTime + duration
                //   2. Snap UP to the next bar boundary if not already on one
                //   3. Use that as the duplicate's startTime
                const beatSecLocal = 60 / (bpm || 120);
                const barSecLocal  = beatSecLocal * (timeSigNum || 4);
                const origStart = c.startTime || 0;
                const origDur   = c.duration || 2;
                const idealEnd  = origStart + origDur;
                // Snap UP — Math.ceil with a small epsilon so a clip
                // ending exactly on a bar boundary doesn't get bumped
                // forward by one extra bar.
                const EPS = 0.005;
                const barsBeforeEnd = Math.ceil(idealEnd / barSecLocal - EPS);
                const snappedStart  = barsBeforeEnd * barSecLocal;
                // Unique id — uses Date.now + random suffix so duplicating
                // the same source clip multiple times doesn't produce id
                // collisions (which would cause updateClip to mutate
                // multiple clips at once, making earlier duplicates
                // appear to "disappear" when one of them is dragged).
                const uniqueId = c.id + "_dup" + Date.now().toString(36) + Math.random().toString(36).slice(2, 6);
                addClipToTrack(t.id, {
                  ...c,
                  id: uniqueId,
                  startTime: snappedStart,
                  label: c.label + " (copy)",
                  active: true,
                });
                setContextMenu(null);
              }} style={{ display:"flex",alignItems:"center",gap:12,width:"100%",padding:"13px 16px",background:"none",border:"none",borderBottom:"1px solid #111",color:"white",fontSize:14,cursor:"pointer" }}>
                <span style={{ width:20,textAlign:"center" }}>⧉</span> Duplicate
              </button>
              {/* Copy — stash a snapshot of this clip in the in-app clipboard.
                  Paste below pulls it back at the current playhead. The clipboard
                  is in-memory only (not the OS clipboard) so it's persistent
                  across the project session but not across reloads. */}
              <button onClick={function(){
                const c = contextMenu.clip; const t = contextMenu.track;
                // Snapshot — shallow copy preserves non-serializable refs
                // like audioBuffer (an AudioBuffer object can't survive
                // JSON.stringify; would become {} and the paste produced
                // an "empty shell" clip). Sharing an audioBuffer reference
                // across multiple clips is safe in Web Audio.
                const snap = { ...c, __sourceTrackId: t.id };
                setClipboardClips({
                  anchorStart: c.startTime || 0,
                  clips: [snap],
                });
                setContextMenu(null);
                try { if (navigator.vibrate) navigator.vibrate(10); } catch(_){}
              }} style={{ display:"flex",alignItems:"center",gap:12,width:"100%",padding:"13px 16px",background:"none",border:"none",borderBottom:"1px solid #111",color:"white",fontSize:14,cursor:"pointer" }}>
                <span style={{ width:20,textAlign:"center" }}>⎘</span> Copy
              </button>
              {/* Paste — only enabled when clipboard has something. Pastes to the
                  current playhead on the SAME track that the clip was copied from
                  (or on this track if the user picked from the menu without copying
                  first — we treat the contextMenu track as the implicit target).
                  New clip gets a fresh id, label suffix " (paste)", and is added
                  to the existing clips array. */}
              {clipboardClips && clipboardClips.clips && clipboardClips.clips.length > 0 && (
                <button onClick={function(){
                  // Paste at the playhead, on the source track from copy.
                  const targetTrackId = clipboardClips.trackId;
                  const placeAt = currentTime || 0;
                  const ts = Date.now();
                  const newClips = clipboardClips.clips.map(function(src, i){
                    return {
                      ...src,
                      id: (src.id || "clip") + "_paste" + ts + "_" + i,
                      startTime: placeAt,
                      label: (src.label || "Take") + " (paste)",
                      active: true,
                    };
                  });
                  setTracks(function(prev){
                    return prev.map(function(tt){
                      if (tt.id !== targetTrackId) return tt;
                      return { ...tt, clips: [...(tt.clips || []), ...newClips] };
                    });
                  });
                  // Select the freshly-pasted clip(s).
                  setSelClipIds(new Set(newClips.map(function(c){ return c.id; })));
                  setContextMenu(null);
                  setIsSaved(false);
                  try { if (navigator.vibrate) navigator.vibrate(15); } catch(_){}
                }} style={{ display:"flex",alignItems:"center",gap:12,width:"100%",padding:"13px 16px",background:"none",border:"none",borderBottom:"1px solid #111",color:"white",fontSize:14,cursor:"pointer" }}>
                  <span style={{ width:20,textAlign:"center" }}>📋</span>
                  <span style={{ flex:1, textAlign:"left" }}>Paste at playhead</span>
                </button>
              )}
              {/* Slice at playhead — splits the clip into two at the
                  current play position. Snaps to grid if snap is enabled.
                  Only shown when the playhead is inside the clip's audible
                  range (with a 50ms margin from each edge to avoid creating
                  invisibly-thin sub-clips). Works for audio AND MIDI/sampler
                  clips — MIDI clips split via the notes array, audio clips
                  via trimStart/trimEnd. */}
              {(function(){
                const c = contextMenu.clip;
                if (!c) return null;
                const t = currentTime;
                const startT = c.startTime || 0;
                // Determine the clip's audible end based on its type.
                let endT;
                if (c.audioBuffer) {
                  const trimS = c.trimStart || 0;
                  const trimE = c.trimEnd !== undefined ? c.trimEnd : c.audioBuffer.duration;
                  endT = startT + (trimE - trimS);
                } else {
                  // MIDI / sampler clip — uses duration directly
                  endT = startT + (c.duration || 0);
                }
                if (t <= startT + 0.05 || t >= endT - 0.05) return null;  // out of range
                const snappedT = snapSecs(t);
                const sliceT = (snappedT > startT + 0.05 && snappedT < endT - 0.05) ? snappedT : t;
                return (
                  <button onClick={function(){
                    sliceClipAtTime(contextMenu.track.id, contextMenu.clip.id, sliceT);
                    setContextMenu(null);
                    try { if (navigator.vibrate) navigator.vibrate(20); } catch(_){}
                  }} style={{ display:"flex",alignItems:"center",gap:12,width:"100%",padding:"13px 16px",background:"none",border:"none",borderBottom:"1px solid #111",color:"white",fontSize:14,cursor:"pointer" }}>
                    <span style={{ width:20,textAlign:"center" }}>✂</span>
                    <span style={{ flex:1, textAlign:"left" }}>Slice at playhead{snapToGrid ? " (snap)" : ""}</span>
                  </button>
                );
              })()}
              {/* Move to track above — preserves startTime so it lands in the same
                  timeline position, just on a different track. Useful when the
                  manual drag is fiddly (e.g. drifting horizontally when the user
                  intended a vertical-only move to a channel with FX applied). */}
              {(function(){
                const idx = tracks.findIndex(function(tr){ return tr.id === contextMenu.track.id; });
                if (idx <= 0) return null;
                const above = tracks[idx - 1];
                return (
                  <button onClick={function(){
                    moveClipToTrack(contextMenu.track.id, contextMenu.clip.id, above.id);
                    setContextMenu(null);
                  }} style={{ display:"flex",alignItems:"center",gap:12,width:"100%",padding:"13px 16px",background:"none",border:"none",borderBottom:"1px solid #111",color:"white",fontSize:14,cursor:"pointer" }}>
                    <span style={{ width:20,textAlign:"center" }}>↑</span>
                    <span style={{ flex:1, textAlign:"left" }}>Move to {above.name}</span>
                  </button>
                );
              })()}
              {(function(){
                const idx = tracks.findIndex(function(tr){ return tr.id === contextMenu.track.id; });
                if (idx < 0 || idx >= tracks.length - 1) return null;
                const below = tracks[idx + 1];
                return (
                  <button onClick={function(){
                    moveClipToTrack(contextMenu.track.id, contextMenu.clip.id, below.id);
                    setContextMenu(null);
                  }} style={{ display:"flex",alignItems:"center",gap:12,width:"100%",padding:"13px 16px",background:"none",border:"none",borderBottom:"1px solid #111",color:"white",fontSize:14,cursor:"pointer" }}>
                    <span style={{ width:20,textAlign:"center" }}>↓</span>
                    <span style={{ flex:1, textAlign:"left" }}>Move to {below.name}</span>
                  </button>
                );
              })()}
              {/* Pitch — opens the modal with semitones + cents sliders.
                  Audio clips only; drum-pattern clips use per-step pitch
                  inside the graph editor. The current pitchSemitones (if
                  set) shows in the label so the user knows the current value. */}
              {contextMenu.clip && contextMenu.clip.audioBuffer && (function(){
                const cur = contextMenu.clip.pitchSemitones || 0;
                const semis = Math.trunc(cur);
                const cents = Math.round((cur - semis) * 100);
                const label = cur === 0
                  ? "Pitch shift…"
                  : "Pitch: " + (cur > 0 ? "+" : "") + semis + " st" +
                    (cents !== 0 ? " " + (cents > 0 ? "+" : "") + cents + "¢" : "");
                return (
                  <button onClick={function(){
                    setPitchModal({
                      trackId: contextMenu.track.id,
                      clipId: contextMenu.clip.id,
                      semis: semis,
                      cents: cents,
                      processing: false,
                      // Snapshot the original pitch so X/backdrop can revert.
                      origPitch: cur,
                    });
                    setContextMenu(null);
                  }} style={{ display:"flex",alignItems:"center",gap:12,width:"100%",padding:"13px 16px",background: cur !== 0 ? "rgba(96,165,250,0.06)" : "none",border:"none",borderBottom:"1px solid #111",color: cur !== 0 ? "#60A5FA" : "white",fontSize:14,cursor:"pointer" }}>
                    <span style={{ width:20,textAlign:"center" }}>🎵</span>
                    <span style={{ flex:1, textAlign:"left" }}>{label}</span>
                  </button>
                );
              })()}
              {/* Fade in — opens the fade modal for the head of the clip.
                  Label shows the current fadeInSec (if any) so users know
                  at a glance whether a fade is set. */}
              {contextMenu.clip && contextMenu.clip.audioBuffer && (function(){
                const fIn = +contextMenu.clip.fadeInSec || 0;
                const label = fIn > 0
                  ? "Fade in: " + fIn.toFixed(2) + "s"
                  : "Fade in…";
                return (
                  <button onClick={function(){
                    setFadeModal({
                      trackId: contextMenu.track.id,
                      clipId: contextMenu.clip.id,
                      kind: "in",
                      // Snapshot the original value so X/backdrop can revert.
                      // draft is the live slider value; only committed on Apply.
                      origValue: fIn,
                      draft: fIn,
                    });
                    setContextMenu(null);
                  }} style={{ display:"flex",alignItems:"center",gap:12,width:"100%",padding:"13px 16px",background: fIn > 0 ? "rgba(251,191,36,0.06)" : "none",border:"none",borderBottom:"1px solid #111",color: fIn > 0 ? "#FBBF24" : "white",fontSize:14,cursor:"pointer" }}>
                    <span style={{ width:20,textAlign:"center" }}>◢</span>
                    <span style={{ flex:1, textAlign:"left" }}>{label}</span>
                  </button>
                );
              })()}
              {/* Fade out — same as above but for the tail of the clip. */}
              {contextMenu.clip && contextMenu.clip.audioBuffer && (function(){
                const fOut = +contextMenu.clip.fadeOutSec || 0;
                const label = fOut > 0
                  ? "Fade out: " + fOut.toFixed(2) + "s"
                  : "Fade out…";
                return (
                  <button onClick={function(){
                    setFadeModal({
                      trackId: contextMenu.track.id,
                      clipId: contextMenu.clip.id,
                      kind: "out",
                      origValue: fOut,
                      draft: fOut,
                    });
                    setContextMenu(null);
                  }} style={{ display:"flex",alignItems:"center",gap:12,width:"100%",padding:"13px 16px",background: fOut > 0 ? "rgba(251,191,36,0.06)" : "none",border:"none",borderBottom:"1px solid #111",color: fOut > 0 ? "#FBBF24" : "white",fontSize:14,cursor:"pointer" }}>
                    <span style={{ width:20,textAlign:"center" }}>◣</span>
                    <span style={{ flex:1, textAlign:"left" }}>{label}</span>
                  </button>
                );
              })()}
              {/* Time stretch toggle — turns the trim handles into stretch
                  handles. When ON, dragging the right edge changes the
                  clip's stretchedDurationSec (SoundTouch renders the
                  stretched buffer on release). When OFF, handles go back
                  to normal trim behavior. Pink highlight when active. */}
              {contextMenu.clip && contextMenu.clip.audioBuffer && (function(){
                const on = !!contextMenu.clip.stretchMode;
                return (
                  <button onClick={function(){
                    setTracks(function(prev){
                      return prev.map(function(tr){
                        if (tr.id !== contextMenu.track.id) return tr;
                        return { ...tr, clips: (tr.clips || []).map(function(c){
                          if (c.id !== contextMenu.clip.id) return c;
                          if (on) {
                            // Turning OFF: keep stretchedDurationSec stored
                            // so toggling back ON returns to where they were,
                            // but clear stretchMode so handles trim again.
                            return { ...c, stretchMode: false };
                          } else {
                            // Turning ON: if there's no stretchedDurationSec
                            // yet, seed it with the clip's current trim length
                            // so the right handle starts at the visual edge.
                            const trimS = c.trimStart || 0;
                            const trimE = c.trimEnd !== undefined ? c.trimEnd : (c.audioBuffer ? c.audioBuffer.duration : c.duration || 2);
                            const initLen = c.stretchedDurationSec || (trimE - trimS);
                            return { ...c, stretchMode: true, stretchedDurationSec: +initLen.toFixed(4) };
                          }
                        })};
                      });
                    });
                    setContextMenu(null);
                    setIsSaved(false);
                  }} style={{ display:"flex",alignItems:"center",gap:12,width:"100%",padding:"13px 16px",background: on ? "rgba(244,114,182,0.1)" : "none",border:"none",borderBottom:"1px solid #111",color: on ? "#F472B6" : "white",fontSize:14,cursor:"pointer" }}>
                    <span style={{ width:20,textAlign:"center" }}>⇄</span>
                    <span style={{ flex:1, textAlign:"left" }}>Time stretch{on ? ": ON ✓" : ""}</span>
                  </button>
                );
              })()}
              <button onClick={function(){ removeClip(contextMenu.track.id, contextMenu.clip.id); }} style={{ display:"flex",alignItems:"center",gap:12,width:"100%",padding:"13px 16px",background:"none",border:"none",borderBottom:"1px solid #111",color:"#EF4444",fontSize:14,cursor:"pointer" }}>
                <span style={{ width:20,textAlign:"center" }}>⌫</span> Delete clip
              </button>
            </>)}
            <button onClick={function(){
              const n=window.prompt("Rename track:", contextMenu.track.name);
              if(n&&n.trim()) updateTrack(contextMenu.track.id,{name:n.trim()});
              setContextMenu(null);
            }} style={{ display:"flex",alignItems:"center",gap:12,width:"100%",padding:"13px 16px",background:"none",border:"none",borderBottom:"1px solid #111",color:"white",fontSize:14,cursor:"pointer" }}>
              <span style={{ width:20,textAlign:"center" }}>✏️</span> Rename track
            </button>
            {!contextMenu.clip && (
              <button onClick={function(){ setConfirmDeleteTrackId(contextMenu.track.id); setContextMenu(null); }} style={{ display:"flex",alignItems:"center",gap:12,width:"100%",padding:"13px 16px",background:"none",border:"none",color:"#EF4444",fontSize:14,cursor:"pointer" }}>
                <span style={{ width:20,textAlign:"center" }}>⌫</span> Delete track
              </button>
            )}
          </div>
            );
          })()}
        </div>
      )}

      {/* ══ AUDIO CLIP PITCH MODAL ═══════════════════════════════════════════
          Opens from the clip long-press context menu when the user taps the
          🎵 Pitch item. Provides two sliders:
            • Semitones (coarse, -24..+24, integer)
            • Cents     (fine,   -100..+100, integer)
          Total pitch shift = semitones + cents/100. On release of either
          slider we kick off an OFFLINE pitch render via SoundTouch — the
          modal shows a "Processing…" spinner while it runs, then unblocks.
          The pitched buffer is cached on clipPitchedBuffersRef so subsequent
          playbacks are instant.
      */}
      {pitchModal && (function(){
        const tt = (tracksRef.current || tracks).find(function(x){ return x.id === pitchModal.trackId; });
        const cl = tt && (tt.clips || []).find(function(c){ return c.id === pitchModal.clipId; });
        if (!cl || !cl.audioBuffer) return null;
        const totalPitch = pitchModal.semis + pitchModal.cents / 100;
        // Commit on Done only: writes pitch to the clip state and triggers
        // the offline SoundTouch render. Until Done is tapped, the modal
        // is a pure preview/staging area — slider changes only mutate
        // pitchModal's own local state.
        const commitAndClose = async function() {
          // Special case: pitch == orig → nothing to do, just close.
          if (Math.abs(totalPitch - (pitchModal.origPitch || 0)) < 0.0005) {
            setPitchModal(null);
            return;
          }
          // Show spinner; commit to clip; render offline; close.
          setPitchModal(function(prev){ return prev ? { ...prev, processing: true } : prev; });
          setTracks(function(prev){
            return prev.map(function(track){
              if (track.id !== pitchModal.trackId) return track;
              return { ...track, clips: (track.clips || []).map(function(c){
                if (c.id !== pitchModal.clipId) return c;
                return { ...c, pitchSemitones: totalPitch };
              })};
            });
          });
          if (Math.abs(totalPitch) < 0.005) {
            // Zero pitch — no render needed.
            setIsSaved(false);
            setPitchModal(null);
            return;
          }
          try {
            const ctx = actxRef.current;
            if (!ctx) { setPitchModal(null); return; }
            const rendered = await pitchAudioBufferOffline(ctx, cl.audioBuffer, totalPitch);
            const key = cl.id + ":" + Math.round(totalPitch * 100);
            if (rendered) clipPitchedBuffersRef.current[key] = rendered;
          } catch(e) {
            console.warn("[pitch] offline render failed:", e);
          } finally {
            setIsSaved(false);
            setPitchModal(null);
          }
        };
        // Cancel: discard modal state without touching the clip. The clip
        // retains whatever pitchSemitones it had before the modal opened.
        const cancelAndClose = function() {
          if (pitchModal.processing) return;
          setPitchModal(null);
        };
        return (
          <div
            onClick={function(){ cancelAndClose(); }}
            style={{
              position: "fixed", inset: 0,
              background: "rgba(0,0,0,0.7)", backdropFilter: "blur(6px)",
              display: "flex", alignItems: "center", justifyContent: "center",
              zIndex: 9500, padding: 24,
            }}>
            <div
              onClick={function(e){ e.stopPropagation(); }}
              style={{
                background: "linear-gradient(165deg,#0a1220 0%,#0a0a18 50%,#06060a 100%)",
                border: "1px solid rgba(96,165,250,0.4)",
                borderRadius: 18,
                padding: 22,
                width: "100%", maxWidth: 380,
                boxShadow: "0 24px 64px rgba(0,0,0,0.7), 0 0 0 1px rgba(96,165,250,0.1)",
                position: "relative",
              }}>
              <div style={{ display:"flex", alignItems:"center", gap:10, marginBottom: 16 }}>
                <div style={{
                  width: 30, height: 30, borderRadius: 8,
                  background: "linear-gradient(135deg,#3B82F6,#60A5FA)",
                  display:"flex", alignItems:"center", justifyContent:"center",
                  boxShadow: "0 0 16px rgba(96,165,250,0.4)",
                  fontSize: 16,
                }}>🎵</div>
                <div style={{ flex: 1 }}>
                  <div style={{ fontSize: 9, fontWeight: 800, letterSpacing: 1.5, color: "#666", textTransform: "uppercase" }}>Pitch Shift</div>
                  <div style={{ fontSize: 14, fontWeight: 800, color: "white", overflow:"hidden", textOverflow:"ellipsis", whiteSpace:"nowrap" }}>
                    {cl.label || "Clip"}
                  </div>
                </div>
                <button
                  onClick={function(){ cancelAndClose(); }}
                  disabled={pitchModal.processing}
                  style={{
                    background: "rgba(255,255,255,0.04)",
                    color: pitchModal.processing ? "#444" : "#aaa",
                    border: "1px solid rgba(255,255,255,0.08)",
                    borderRadius: 8, padding: "6px 10px",
                    fontSize: 18, lineHeight: 1,
                    cursor: pitchModal.processing ? "not-allowed" : "pointer",
                  }}>×</button>
              </div>

              {/* Total pitch display */}
              <div style={{
                background: "rgba(96,165,250,0.08)",
                border: "1px solid rgba(96,165,250,0.2)",
                borderRadius: 12,
                padding: "14px 16px",
                marginBottom: 18,
                textAlign: "center",
              }}>
                <div style={{ fontSize: 9, fontWeight: 800, letterSpacing: 1.5, color: "#60A5FA", textTransform: "uppercase", marginBottom: 4 }}>Total</div>
                <div style={{ fontSize: 28, fontWeight: 900, color: "white", letterSpacing: 0.5 }}>
                  {totalPitch === 0 ? "0" : (totalPitch > 0 ? "+" : "") + totalPitch.toFixed(2)}
                  <span style={{ fontSize: 14, color: "#888", marginLeft: 6, fontWeight: 600 }}>st</span>
                </div>
              </div>

              {/* Semitones slider */}
              <div style={{ marginBottom: 14 }}>
                <div style={{ display:"flex", justifyContent:"space-between", alignItems:"center", marginBottom: 6 }}>
                  <div style={{ fontSize: 10, fontWeight: 800, color: "#888", letterSpacing: 1, textTransform:"uppercase" }}>Semitones (coarse)</div>
                  <div style={{ fontSize: 13, color: "#60A5FA", fontWeight: 800, minWidth: 36, textAlign:"right" }}>
                    {pitchModal.semis > 0 ? "+" : ""}{pitchModal.semis}
                  </div>
                </div>
                <PointerSlider min={-24} max={24} step={1}
                  value={pitchModal.semis}
                  onChange={function(v){
                    if (pitchModal.processing) return;
                    setPitchModal(function(prev){ return prev ? { ...prev, semis: v } : prev; });
                  }}
                  accentColor="#60A5FA"
                />
                <div style={{ display:"flex", justifyContent:"space-between", marginTop: 2 }}>
                  <span style={{ fontSize: 9, color:"#444" }}>-24</span>
                  <span style={{ fontSize: 9, color:"#444" }}>0</span>
                  <span style={{ fontSize: 9, color:"#444" }}>+24</span>
                </div>
              </div>

              {/* Cents slider */}
              <div style={{ marginBottom: 18 }}>
                <div style={{ display:"flex", justifyContent:"space-between", alignItems:"center", marginBottom: 6 }}>
                  <div style={{ fontSize: 10, fontWeight: 800, color: "#888", letterSpacing: 1, textTransform:"uppercase" }}>Cents (fine)</div>
                  <div style={{ fontSize: 13, color: "#60A5FA", fontWeight: 800, minWidth: 36, textAlign:"right" }}>
                    {pitchModal.cents > 0 ? "+" : ""}{pitchModal.cents}¢
                  </div>
                </div>
                <PointerSlider min={-100} max={100} step={1}
                  value={pitchModal.cents}
                  onChange={function(v){
                    if (pitchModal.processing) return;
                    setPitchModal(function(prev){ return prev ? { ...prev, cents: v } : prev; });
                  }}
                  accentColor="#60A5FA"
                />
                <div style={{ display:"flex", justifyContent:"space-between", marginTop: 2 }}>
                  <span style={{ fontSize: 9, color:"#444" }}>-100¢</span>
                  <span style={{ fontSize: 9, color:"#444" }}>0</span>
                  <span style={{ fontSize: 9, color:"#444" }}>+100¢</span>
                </div>
              </div>

              {/* Actions */}
              <div style={{ display:"flex", gap: 10 }}>
                <button
                  onClick={function(){
                    // Reset = zero out the modal's local state. No commit yet
                    // — user still has to press Done. This means Reset+X is
                    // a true cancel that doesn't touch the clip.
                    setPitchModal(function(prev){ return prev ? { ...prev, semis: 0, cents: 0 } : prev; });
                  }}
                  disabled={pitchModal.processing || (pitchModal.semis === 0 && pitchModal.cents === 0)}
                  style={{
                    flex: 1,
                    background: "transparent",
                    color: (pitchModal.semis === 0 && pitchModal.cents === 0) ? "#444" : "#aaa",
                    border: "1px solid rgba(255,255,255,0.08)",
                    borderRadius: 10, padding: "10px 14px",
                    fontSize: 12, fontWeight: 800,
                    cursor: (pitchModal.semis === 0 && pitchModal.cents === 0) ? "not-allowed" : "pointer",
                    letterSpacing: 0.4,
                  }}>Reset</button>
                <button
                  onClick={function(){ if (!pitchModal.processing) commitAndClose(); }}
                  disabled={pitchModal.processing}
                  style={{
                    flex: 2,
                    background: "linear-gradient(135deg,#3B82F6,#60A5FA)",
                    color: "white",
                    border: "none",
                    borderRadius: 10, padding: "10px 14px",
                    fontSize: 13, fontWeight: 800,
                    cursor: pitchModal.processing ? "wait" : "pointer",
                    letterSpacing: 0.4,
                    boxShadow: "0 4px 14px rgba(96,165,250,0.4)",
                    opacity: pitchModal.processing ? 0.7 : 1,
                  }}>Apply</button>
              </div>

              {/* Processing spinner overlay — shown while SoundTouch is
                  rendering. Floats above the modal body so the user can't
                  drag sliders during render. */}
              {pitchModal.processing && (
                <div style={{
                  position: "absolute", inset: 0,
                  background: "rgba(6,6,12,0.85)",
                  backdropFilter: "blur(4px)",
                  borderRadius: 18,
                  display: "flex", flexDirection: "column",
                  alignItems: "center", justifyContent: "center",
                  zIndex: 10,
                }}>
                  <div style={{
                    width: 36, height: 36,
                    border: "3px solid rgba(96,165,250,0.2)",
                    borderTopColor: "#60A5FA",
                    borderRadius: "50%",
                    animation: "spin 0.8s linear infinite",
                  }} />
                  <div style={{ marginTop: 14, color: "#60A5FA", fontSize: 12, fontWeight: 800, letterSpacing: 1.4, textTransform: "uppercase" }}>
                    Processing pitch…
                  </div>
                  <div style={{ marginTop: 4, color: "#666", fontSize: 10 }}>
                    SoundTouch · preserves duration
                  </div>
                </div>
              )}
            </div>
          </div>
        );
      })()}

      {/* ══ AUDIO CLIP FADE MODAL ════════════════════════════════════════════
          Opens from the long-press menu when the user taps Fade in… or
          Fade out… Single slider 0..5s. Live update — no async render
          needed since fades are applied at playback time via GainNode
          ramps (no buffer processing).
      */}
      {fadeModal && (function(){
        const tt = (tracksRef.current || tracks).find(function(x){ return x.id === fadeModal.trackId; });
        const cl = tt && (tt.clips || []).find(function(c){ return c.id === fadeModal.clipId; });
        if (!cl || !cl.audioBuffer) return null;
        const fieldName = fadeModal.kind === "in" ? "fadeInSec" : "fadeOutSec";
        // Local draft value — only commits on Apply.
        const draft = fadeModal.draft == null ? (+cl[fieldName] || 0) : fadeModal.draft;
        const origValue = fadeModal.origValue == null ? (+cl[fieldName] || 0) : fadeModal.origValue;
        const trimS = cl.trimStart || 0;
        const trimE = cl.trimEnd !== undefined ? cl.trimEnd : cl.audioBuffer.duration;
        const visualLen = (cl.stretchMode && cl.stretchedDurationSec > 0)
          ? cl.stretchedDurationSec
          : (trimE - trimS);
        const maxFade = Math.min(5, Math.max(0.1, visualLen));
        const applyAndClose = function() {
          setTracks(function(prev){
            return prev.map(function(track){
              if (track.id !== fadeModal.trackId) return track;
              return { ...track, clips: (track.clips || []).map(function(c){
                if (c.id !== fadeModal.clipId) return c;
                return { ...c, [fieldName]: +draft.toFixed(3) };
              })};
            });
          });
          setIsSaved(false);
          setFadeModal(null);
        };
        // Cancel: don't touch the clip. fadeInSec/fadeOutSec stays at
        // whatever the clip had before the modal opened.
        const cancelAndClose = function() { setFadeModal(null); };
        const titleColor = "#FBBF24";
        return (
          <div
            onClick={function(){ cancelAndClose(); }}
            style={{
              position: "fixed", inset: 0,
              background: "rgba(0,0,0,0.7)", backdropFilter: "blur(6px)",
              display: "flex", alignItems: "center", justifyContent: "center",
              zIndex: 9500, padding: 24,
            }}>
            <div
              onClick={function(e){ e.stopPropagation(); }}
              style={{
                background: "linear-gradient(165deg,#1f1500 0%,#180f00 50%,#0a0700 100%)",
                border: "1px solid rgba(251,191,36,0.4)",
                borderRadius: 18,
                padding: 22,
                width: "100%", maxWidth: 380,
                boxShadow: "0 24px 64px rgba(0,0,0,0.7), 0 0 0 1px rgba(251,191,36,0.1)",
              }}>
              <div style={{ display:"flex", alignItems:"center", gap:10, marginBottom: 16 }}>
                <div style={{
                  width: 30, height: 30, borderRadius: 8,
                  background: "linear-gradient(135deg,#F59E0B,#FBBF24)",
                  display:"flex", alignItems:"center", justifyContent:"center",
                  boxShadow: "0 0 16px rgba(251,191,36,0.4)",
                  fontSize: 14, color: "white", fontWeight: 900,
                }}>{fadeModal.kind === "in" ? "◢" : "◣"}</div>
                <div style={{ flex: 1 }}>
                  <div style={{ fontSize: 9, fontWeight: 800, letterSpacing: 1.5, color: "#666", textTransform: "uppercase" }}>Fade {fadeModal.kind === "in" ? "in" : "out"}</div>
                  <div style={{ fontSize: 14, fontWeight: 800, color: "white", overflow:"hidden", textOverflow:"ellipsis", whiteSpace:"nowrap" }}>
                    {cl.label || "Clip"}
                  </div>
                </div>
                <button
                  onClick={function(){ cancelAndClose(); }}
                  style={{
                    background: "rgba(255,255,255,0.04)",
                    color: "#aaa",
                    border: "1px solid rgba(255,255,255,0.08)",
                    borderRadius: 8, padding: "6px 10px",
                    fontSize: 18, lineHeight: 1, cursor: "pointer",
                  }}>×</button>
              </div>

              {/* Big duration display */}
              <div style={{
                background: "rgba(251,191,36,0.08)",
                border: "1px solid rgba(251,191,36,0.2)",
                borderRadius: 12,
                padding: "14px 16px",
                marginBottom: 18,
                textAlign: "center",
              }}>
                <div style={{ fontSize: 9, fontWeight: 800, letterSpacing: 1.5, color: titleColor, textTransform: "uppercase", marginBottom: 4 }}>Duration</div>
                <div style={{ fontSize: 28, fontWeight: 900, color: "white", letterSpacing: 0.5 }}>
                  {draft.toFixed(2)}
                  <span style={{ fontSize: 14, color: "#888", marginLeft: 6, fontWeight: 600 }}>sec</span>
                </div>
              </div>

              {/* Slider */}
              <div style={{ marginBottom: 18 }}>
                <div style={{ display:"flex", justifyContent:"space-between", alignItems:"center", marginBottom: 6 }}>
                  <div style={{ fontSize: 10, fontWeight: 800, color: "#888", letterSpacing: 1, textTransform:"uppercase" }}>Slide to set</div>
                  <div style={{ fontSize: 10, color: "#555" }}>max {maxFade.toFixed(1)}s</div>
                </div>
                <PointerSlider min={0} max={maxFade} step={0.05}
                  value={Math.min(draft, maxFade)}
                  onChange={function(v){
                    setFadeModal(function(prev){ return prev ? { ...prev, draft: v } : prev; });
                  }}
                  accentColor={titleColor}
                />
                <div style={{ display:"flex", justifyContent:"space-between", marginTop: 2 }}>
                  <span style={{ fontSize: 9, color:"#444" }}>0</span>
                  <span style={{ fontSize: 9, color:"#444" }}>{(maxFade/2).toFixed(1)}s</span>
                  <span style={{ fontSize: 9, color:"#444" }}>{maxFade.toFixed(1)}s</span>
                </div>
              </div>

              {/* Hint text */}
              <div style={{ fontSize: 10, color: "#666", marginBottom: 14, lineHeight: 1.5, textAlign: "center" }}>
                Equal-power curve · changes apply when you tap Apply
              </div>

              {/* Actions */}
              <div style={{ display:"flex", gap: 10 }}>
                <button
                  onClick={function(){
                    // Reset = zero the modal draft. Clip not touched until Apply.
                    setFadeModal(function(prev){ return prev ? { ...prev, draft: 0 } : prev; });
                  }}
                  disabled={draft === 0}
                  style={{
                    flex: 1,
                    background: "transparent",
                    color: draft === 0 ? "#444" : "#aaa",
                    border: "1px solid rgba(255,255,255,0.08)",
                    borderRadius: 10, padding: "10px 14px",
                    fontSize: 12, fontWeight: 800,
                    cursor: draft === 0 ? "not-allowed" : "pointer",
                    letterSpacing: 0.4,
                  }}>Reset</button>
                <button
                  onClick={function(){ applyAndClose(); }}
                  style={{
                    flex: 2,
                    background: "linear-gradient(135deg,#F59E0B,#FBBF24)",
                    color: "#1a0e00",
                    border: "none",
                    borderRadius: 10, padding: "10px 14px",
                    fontSize: 13, fontWeight: 900,
                    cursor: "pointer",
                    letterSpacing: 0.4,
                    boxShadow: "0 4px 14px rgba(251,191,36,0.4)",
                  }}>Apply</button>
              </div>
            </div>
          </div>
        );
      })()}

      {/* ── Confirm delete track modal ─────────────────────────── */}
      {confirmDeleteTrackId && (
        <div style={{ position:"fixed",inset:0,zIndex:9500,background:"rgba(0,0,0,0.85)",display:"flex",alignItems:"center",justifyContent:"center",padding:"32px" }}>
          <div style={{ background:"#1a1a1a",border:"1px solid #2a2a2a",borderRadius:20,padding:"28px 24px",width:"100%",maxWidth:320,textAlign:"center" }}>
            <div style={{ width:48,height:48,borderRadius:"50%",background:"rgba(239,68,68,0.15)",border:"1px solid rgba(239,68,68,0.3)",display:"flex",alignItems:"center",justifyContent:"center",margin:"0 auto 16px" }}>
              <svg width="22" height="22" viewBox="0 0 24 24" fill="none" stroke="#EF4444" strokeWidth="2" strokeLinecap="round">
                <polyline points="3 6 5 6 21 6"/><path d="M19 6l-1 14H6L5 6"/><path d="M10 11v6M14 11v6"/><path d="M9 6V4h6v2"/>
              </svg>
            </div>
            <div style={{ color:"white",fontWeight:800,fontSize:17,marginBottom:10 }}>Delete Track?</div>
            <div style={{ color:"#888",fontSize:13,marginBottom:24,lineHeight:1.7 }}>
              Are you sure you want to delete this track and all of its contents? This cannot be undone.
            </div>
            <div style={{ display:"flex",gap:10 }}>
              <button onClick={function(){ setConfirmDeleteTrackId(null); }}
                style={{ flex:1,background:"none",border:"1px solid #2a2a2a",borderRadius:12,color:"#888",fontWeight:700,fontSize:14,padding:"13px",cursor:"pointer" }}>
                Cancel
              </button>
              <button onClick={function(){
                removeTrack(confirmDeleteTrackId);
                setConfirmDeleteTrackId(null);
              }}
                style={{ flex:1,background:"rgba(239,68,68,0.2)",border:"1px solid rgba(239,68,68,0.4)",borderRadius:12,color:"#EF4444",fontWeight:800,fontSize:14,padding:"13px",cursor:"pointer" }}>
                Delete
              </button>
            </div>
          </div>
        </div>
      )}

      {unsavedAlert && (
        <div style={{ position:"absolute",inset:0,zIndex:8000,background:"rgba(0,0,0,0.85)",display:"flex",alignItems:"center",justifyContent:"center",padding:"32px" }}>
          <div style={{ background:"#1a1a1a",border:"1px solid #2a2a2a",borderRadius:20,padding:"28px 24px",width:"100%",maxWidth:320,textAlign:"center" }}>
            <div style={{ fontSize:36,marginBottom:12 }}><AppIcon id="save" size={20}/></div>
            <div style={{ color:"white",fontWeight:800,fontSize:18,marginBottom:10 }}>Save before leaving?</div>
            <div style={{ color:"#888",fontSize:14,marginBottom:28,lineHeight:1.6 }}>Your project has unsaved changes.</div>
            <div style={{ display:"flex",flexDirection:"column",gap:10 }}>
              <button onClick={function(){
                // Open the name prompt — capture which action triggered this
                setUnsavedAlert(false);
                setPendingName(projectName === "New Project" ? "" : projectName);
                setPendingNameAction(unsavedAlert); // "exit" or "new"
                setShowNamePrompt(true);
              }} style={{ background:"linear-gradient(135deg,#C026D3,#7C3AED)",border:"none",borderRadius:12,color:"white",fontWeight:800,fontSize:15,padding:"14px",cursor:"pointer" }}>
                Save {unsavedAlert==="new"?"& New":"& Exit"}
              </button>
              <button onClick={function(){
                setUnsavedAlert(false);
                // Full reset so old project doesn't ghost back
                stopMonitoring();
                stopAll(); setIsPlaying(false);
                setTracks([]); setProjectName("New Project");
                setBpm(120); setProjectKey("C major"); setTimeSigNum(4);
                setCurrentTime(0); setIsSaved(true); setSelectedTrackId(null);
                if(scrollRef.current) scrollRef.current.scrollLeft = 0;
                if(unsavedAlert !== "new") onExit();
              }} style={{ background:"rgba(239,68,68,0.15)",border:"1px solid rgba(239,68,68,0.3)",borderRadius:12,color:"#EF4444",fontWeight:700,fontSize:15,padding:"14px",cursor:"pointer" }}>Don't Save</button>
              <button onClick={function(){ setUnsavedAlert(false); }} style={{ background:"none",border:"1px solid #2a2a2a",borderRadius:12,color:"#666",fontSize:14,padding:"12px",cursor:"pointer" }}>Cancel</button>
            </div>
          </div>
        </div>
      )}

      {/* ── Name your project modal ───────────────────────────────── */}
      {showNamePrompt && (
        <div style={{ position:"absolute",inset:0,zIndex:8100,background:"rgba(0,0,0,0.9)",display:"flex",alignItems:"center",justifyContent:"center",padding:"32px" }}>
          <div style={{ background:"#1a1a1a",border:"1px solid #333",borderRadius:20,padding:"28px 24px",width:"100%",maxWidth:320 }}>
            <div style={{ fontSize:36,marginBottom:12,textAlign:"center" }}>✏️</div>
            <div style={{ color:"white",fontWeight:800,fontSize:18,marginBottom:6,textAlign:"center" }}>Name your project</div>
            <div style={{ color:"#666",fontSize:13,marginBottom:20,textAlign:"center",lineHeight:1.5 }}>Give this project a name so you can find it later.</div>
            <input
              autoFocus
              value={pendingName}
              onChange={function(e){ setPendingName(e.target.value); }}
              onKeyDown={function(e){
                if(e.key==="Enter" && pendingName.trim()){
                  const name = pendingName.trim();
                  setProjectName(name);
                  setShowNamePrompt(false);
                  setTimeout(async function(){
                    await saveProject();
                    // "save" action: just save and stay. Other actions
                    // (new / exit) tear down the project state.
                    if (pendingNameAction === "save") return;
                    stopMonitoring();
                    stopAll(); setIsPlaying(false);
                    setTracks([]); setProjectName("New Project");
                    setBpm(120); setProjectKey("C major"); setTimeSigNum(4);
                    setCurrentTime(0); setIsSaved(true); setSelectedTrackId(null);
                    if(scrollRef.current) scrollRef.current.scrollLeft = 0;
                    if(pendingNameAction !== "new") onExit();
                  }, 30);
                }
                if(e.key==="Escape"){ setShowNamePrompt(false); }
              }}
              placeholder="e.g. Summer Freestyle, Hook Idea..."
              style={{ width:"100%",boxSizing:"border-box",background:"#111",border:"1.5px solid #C026D3",borderRadius:12,padding:"13px 16px",color:"white",fontSize:15,outline:"none",marginBottom:16,fontFamily:"'DM Sans',sans-serif" }}
            />
            <div style={{ display:"flex",flexDirection:"column",gap:10 }}>
              <button
                onClick={async function(){
                  const name = (pendingName.trim()) || projectName;
                  setProjectName(name);
                  setShowNamePrompt(false);
                  await saveProject();
                  // "save" action: just save and stay.
                  if (pendingNameAction === "save") return;
                  stopMonitoring();
                  // Full reset — close the project before going home
                  stopAll(); setIsPlaying(false);
                  setTracks([]); setProjectName("New Project");
                  setBpm(120); setProjectKey("C major"); setTimeSigNum(4);
                  setCurrentTime(0); setIsSaved(true); setSelectedTrackId(null);
                  if(scrollRef.current) scrollRef.current.scrollLeft = 0;
                  if(pendingNameAction !== "new") onExit();
                }}
                disabled={!pendingName.trim()}
                style={{ background: pendingName.trim() ? "linear-gradient(135deg,#C026D3,#7C3AED)" : "#2a2a2a",border:"none",borderRadius:12,color: pendingName.trim() ? "white" : "#555",fontWeight:800,fontSize:15,padding:"14px",cursor: pendingName.trim() ? "pointer" : "default",transition:"all 0.15s" }}>
                {pendingNameAction === "save" ? "Save" : ("Save & " + (pendingNameAction==="new" ? "New Project" : "Go Home"))}
              </button>
              <button onClick={function(){ setShowNamePrompt(false); }} style={{ background:"none",border:"1px solid #2a2a2a",borderRadius:12,color:"#666",fontSize:14,padding:"12px",cursor:"pointer" }}>Cancel</button>
            </div>
          </div>
        </div>
      )}

      {showTSPicker && (
        <div style={{ position:"absolute",inset:0,zIndex:9999,background:"rgba(0,0,0,0.7)",display:"flex",alignItems:"flex-end" }} onClick={function(){ setShowTSPicker(false); }}>
          <div style={{ width:"100%",background:"#1c1c1c",borderRadius:"20px 20px 0 0",paddingBottom:"calc(20px + env(safe-area-inset-bottom))" }} onClick={function(e){ e.stopPropagation(); }}>
            <div style={{ display:"flex",alignItems:"center",justifyContent:"space-between",padding:"16px 20px 12px",borderBottom:"1px solid #2a2a2a" }}>
              <button onClick={function(){ setShowTSPicker(false); }} style={{ background:"none",border:"none",color:"#888",fontSize:14,cursor:"pointer" }}>Cancel</button>
              <span style={{ color:"white",fontWeight:700,fontSize:15 }}>Time Signature</span>
              <button onClick={function(){ setShowTSPicker(false); }} style={{ background:"none",border:"none",color:"#C026D3",fontSize:14,fontWeight:700,cursor:"pointer" }}>Done</button>
            </div>
            <div style={{ display:"flex" }}>
              <WheelPicker items={["2","3","4","5","6","7"]} value={String(timeSigNum)} onChange={function(v){ setTimeSigNum(parseInt(v)); }} onClose={function(){}} inline={true} label="Beats" />
              <div style={{ display:"flex",alignItems:"center",color:"white",fontSize:28,fontWeight:700,padding:"0 8px" }}>/</div>
              <WheelPicker items={["4"]} value="4" onChange={function(){}} onClose={function(){}} inline={true} label="Note" />
            </div>
          </div>
        </div>
      )}

      {showKeyPick && (
        <div style={{ position:"absolute",inset:0,zIndex:9999,background:"rgba(0,0,0,0.7)",display:"flex",alignItems:"flex-end" }} onClick={function(){ setShowKeyPick(false); }}>
          <div style={{ width:"100%",background:"#1c1c1c",borderRadius:"20px 20px 0 0",paddingBottom:"calc(20px + env(safe-area-inset-bottom))" }} onClick={function(e){ e.stopPropagation(); }}>
            <div style={{ display:"flex",alignItems:"center",justifyContent:"space-between",padding:"16px 20px 12px",borderBottom:"1px solid #2a2a2a" }}>
              <button onClick={function(){ setShowKeyPick(false); }} style={{ background:"none",border:"none",color:"#888",fontSize:14,cursor:"pointer" }}>Cancel</button>
              <span style={{ color:"white",fontWeight:700,fontSize:15 }}>Project Key</span>
              <button onClick={function(){ setShowKeyPick(false); }} style={{ background:"none",border:"none",color:"#C026D3",fontSize:14,fontWeight:700,cursor:"pointer" }}>Done</button>
            </div>
            <WheelPicker items={["C major","C# major","D major","D# major","E major","F major","F# major","G major","G# major","A major","A# major","B major","C minor","C# minor","D minor","D# minor","E minor","F minor","F# minor","G minor","G# minor","A minor","A# minor","B minor"]} value={projectKey} onChange={function(v){ setProjectKey(v); }} onClose={function(){ setShowKeyPick(false); }} inline={true} />
          </div>
        </div>
      )}

      {showSettings && (
        <div style={{ position:"absolute",inset:0,zIndex:700,background:"rgba(0,0,0,0.85)" }} onClick={function(){ setShowSettings(false); }}>
          <div style={{
            position:"absolute",bottom:0,left:0,right:0,
            background:"linear-gradient(180deg, #110b22 0%, #0a0716 60%, #08050f 100%)",
            borderRadius:"22px 22px 0 0",
            border:"1px solid rgba(167,139,250,0.15)",
            borderBottom:"none",
            maxHeight:"88vh",overflowY:"auto",WebkitOverflowScrolling:"touch",
            boxShadow:"0 -12px 40px rgba(0,0,0,0.7), 0 -1px 0 rgba(167,139,250,0.18) inset",
          }}
            onClick={function(e){ e.stopPropagation(); }}
            onTouchMove={function(e){ e.stopPropagation(); }}>
            {/* Drag-affordance pill */}
            <div style={{
              width:36, height:4, borderRadius:2,
              background:"rgba(167,139,250,0.3)",
              margin:"10px auto 4px",
            }}/>
            {/* Header */}
            <div style={{ display:"flex",alignItems:"center",justifyContent:"space-between",padding:"10px 20px 14px" }}>
              <div style={{ display:"flex", alignItems:"center", gap:10 }}>
                <div style={{
                  width:8, height:8, borderRadius:"50%",
                  background:"#a78bfa",
                  boxShadow:"0 0 8px rgba(167,139,250,0.8), 0 0 14px rgba(139,92,246,0.4)",
                }}/>
                <span style={{ color:"#e8e6f0",fontWeight:800,fontSize:13,letterSpacing:2,textTransform:"uppercase" }}>Project Settings</span>
              </div>
              <button onClick={function(){ setShowSettings(false); }} style={{
                background:"rgba(167,139,250,0.08)",
                border:"1px solid #2a2050",
                borderRadius:6,
                color:"#a78bfa",fontSize:16,fontWeight:700,
                cursor:"pointer",lineHeight:1,padding:"3px 9px",
              }}>×</button>
            </div>
            <div style={{
              margin:"0 16px 16px",
              background:"linear-gradient(165deg,#0f0a1f 0%,#0a0a14 60%,#080812 100%)",
              border:"1px solid rgba(124,58,237,0.25)",
              borderRadius:14,overflow:"hidden",
              boxShadow:"0 0 24px rgba(124,58,237,0.08), inset 0 0 0 1px rgba(192,38,211,0.05)",
              position:"relative",
            }}>
              {/* LED top edge */}
              <div style={{
                position:"absolute", top:0, left:0, right:0, height:1,
                background:"linear-gradient(90deg,transparent,rgba(192,38,211,0.5),rgba(124,58,237,0.5),rgba(59,130,246,0.5),transparent)",
              }} />
              <div style={{
                padding:"14px 16px 8px",
                fontFamily:"'Bebas Neue',sans-serif",
                fontSize:16, letterSpacing:2,
                background:"linear-gradient(90deg,#C026D3,#7C3AED,#3B82F6)",
                WebkitBackgroundClip:"text", backgroundClip:"text",
                color:"transparent",
                fontWeight:700,
              }}>TEMPO</div>

              {/* ── BPM progress bar (shows during analysis) ── */}
              {bpmDetecting && (
                <div style={{ padding:"0 16px 10px" }}>
                  <div style={{ background:"rgba(124,58,237,0.08)",borderRadius:6,overflow:"hidden",height:4,marginBottom:6,border:"1px solid rgba(124,58,237,0.15)" }}>
                    <div style={{ height:"100%",background:"linear-gradient(90deg,#C026D3,#7C3AED,#3B82F6)",width:bpmProgress+"%",transition:"width 0.3s ease",borderRadius:6,boxShadow:"0 0 8px rgba(192,38,211,0.5)" }} />
                  </div>
                  <div style={{ color:"#A78BFA",fontSize:10,letterSpacing:1.5,fontWeight:600 }}>{bpmDetectMsg || "Analyzing…"}</div>
                </div>
              )}

              {/* ── BPM display row ── */}
              <div style={{ display:"flex",alignItems:"stretch",borderTop:"1px solid rgba(124,58,237,0.18)" }}>
                <button onClick={function(){ setBpmWithRescale(function(b){ return Math.max(40,b-1); }); setBpmSource("manual"); }} style={{ flex:1,background:"none",border:"none",borderRight:"1px solid rgba(124,58,237,0.18)",color:"#A78BFA",fontSize:28,cursor:"pointer",padding:"14px 0",fontWeight:300 }}>−</button>

                {/* Tap tempo centre */}
                <div onClick={handleTapTempo} style={{ flex:2,display:"flex",flexDirection:"column",alignItems:"center",justifyContent:"center",padding:"12px 0",cursor:"pointer",position:"relative" }}>
                  {/* Animated beat pulse ring */}
                  {isPlaying && (
                    <div style={{
                      position:"absolute", width:72, height:72, borderRadius:"50%",
                      border:"2px solid rgba(192,38,211,0.5)",
                      animation:"bf-play-pulse 1s ease infinite",
                      animationDuration: (60/bpm)+"s",
                      pointerEvents:"none",
                    }} />
                  )}
                  <div style={{
                    fontWeight:900,fontSize:40,lineHeight:1,fontFamily:"monospace",letterSpacing:-1,
                    background:"linear-gradient(135deg,#C026D3 0%,#7C3AED 50%,#3B82F6 100%)",
                    WebkitBackgroundClip:"text", backgroundClip:"text",
                    color:"transparent",
                    filter:"drop-shadow(0 0 12px rgba(124,58,237,0.4))",
                  }}>
                    {bpm}<span style={{ fontSize:14,color:"#6b6685",fontWeight:600,letterSpacing:0,background:"none",WebkitTextFillColor:"#6b6685" }}> BPM</span>
                  </div>
                  <div style={{ color:"#6b6685",fontSize:10,marginTop:4,letterSpacing:1.5,fontWeight:600 }}>TAP TEMPO</div>
                  {bpmSource && (
                    <div style={{
                      fontSize:9,fontWeight:800,letterSpacing:1.5,marginTop:3,
                      padding:"2px 8px", borderRadius:8,
                      background: bpmSource==="auto" ? "rgba(34,197,94,0.12)" : bpmSource==="tap" ? "rgba(192,38,211,0.15)" : "rgba(124,58,237,0.12)",
                      border: "1px solid "+(bpmSource==="auto" ? "rgba(34,197,94,0.35)" : bpmSource==="tap" ? "rgba(192,38,211,0.4)" : "rgba(124,58,237,0.3)"),
                      color: bpmSource==="auto"?"#4ADE80":bpmSource==="tap"?"#C026D3":"#A78BFA",
                    }}>
                      {bpmSource==="auto"?"AUTO-DETECTED":bpmSource==="tap"?"TAP":"MANUAL"}
                    </div>
                  )}
                </div>

                <button onClick={function(){ setBpmWithRescale(function(b){ return Math.min(220,b+1); }); setBpmSource("manual"); }} style={{ flex:1,background:"none",border:"none",borderLeft:"1px solid rgba(124,58,237,0.18)",color:"#A78BFA",fontSize:28,cursor:"pointer",padding:"14px 0",fontWeight:300 }}>+</button>
              </div>

              {/* ── BPM range slider ── */}
              <div style={{ padding:"6px 16px 10px" }}>
                <input type="range" min={40} max={220} step={1} value={bpm}
                  onChange={function(e){ setBpmWithRescale(parseInt(e.target.value)); setBpmSource("manual"); }}
                  style={{ width:"100%",accentColor:"#C026D3",touchAction:"none" }} />
              </div>

              {/* ── Confidence meter + band display ── */}
              {bpmConfidence !== null && (
                <div style={{ padding:"0 16px 14px" }}>
                  <div style={{ display:"flex",alignItems:"center",gap:8,marginBottom:8 }}>
                    <div style={{ color:"#A78BFA",fontSize:10,fontWeight:800,letterSpacing:1.5,width:78 }}>CONFIDENCE</div>
                    <div style={{ flex:1,background:"rgba(124,58,237,0.08)",border:"1px solid rgba(124,58,237,0.2)",borderRadius:4,overflow:"hidden",height:6 }}>
                      <div style={{ height:"100%",borderRadius:4,transition:"width 0.5s ease",
                        background: bpmConfidence > 0.7
                          ? "linear-gradient(90deg,#C026D3,#7C3AED,#3B82F6)"
                          : bpmConfidence > 0.4
                          ? "linear-gradient(90deg,#7C3AED,#A78BFA)"
                          : "linear-gradient(90deg,#4c1d95,#7C3AED)",
                        boxShadow: bpmConfidence > 0.7 ? "0 0 8px rgba(124,58,237,0.5)" : "none",
                        width: Math.round(bpmConfidence*100)+"%" }} />
                    </div>
                    <div style={{
                      color: bpmConfidence > 0.7 ? "#C026D3" : bpmConfidence > 0.4 ? "#A78BFA" : "#7C3AED",
                      fontSize:11,fontWeight:800,width:36,textAlign:"right",
                      textShadow: bpmConfidence > 0.7 ? "0 0 8px rgba(192,38,211,0.4)" : "none",
                    }}>
                      {Math.round(bpmConfidence*100)}%
                    </div>
                  </div>

                  {/* Per-band confidence bars */}
                  {bpmBandConf && (
                    <div style={{ display:"flex",gap:6 }}>
                      {[
                        { label:"LOW",  val:bpmBandConf.low,  color:"#C026D3" },
                        { label:"MID",  val:bpmBandConf.mid,  color:"#7C3AED" },
                        { label:"HIGH", val:bpmBandConf.high, color:"#3B82F6" },
                      ].map(function(band){
                        return (
                          <div key={band.label} style={{ flex:1,textAlign:"center" }}>
                            <div style={{ background:"#111",borderRadius:3,overflow:"hidden",height:28,position:"relative",marginBottom:3 }}>
                              <div style={{ position:"absolute",bottom:0,left:0,right:0,borderRadius:3,transition:"height 0.4s ease",
                                background:band.color+"44", height: Math.round((band.val||0)*100)+"%" }} />
                              <div style={{ position:"absolute",bottom:0,left:0,right:0,borderRadius:3,
                                background:band.color, height:2 }} />
                            </div>
                            <div style={{ color:"#555",fontSize:8,fontWeight:800,letterSpacing:1 }}>{band.label}</div>
                          </div>
                        );
                      })}
                    </div>
                  )}

                  {/* Swing indicator */}
                  {swingAmount > 0.05 && (
                    <div style={{ marginTop:8,display:"flex",alignItems:"center",gap:8 }}>
                      <div style={{ color:"#555",fontSize:10,fontWeight:700,letterSpacing:1,width:48 }}>SWING</div>
                      <div style={{ flex:1,background:"#111",borderRadius:4,overflow:"hidden",height:4 }}>
                        <div style={{ height:"100%",borderRadius:4,background:"#F59E0B",width:Math.round(swingAmount*100)+"%" }} />
                      </div>
                      <div style={{ color:"#F59E0B",fontSize:10,fontWeight:700,width:30,textAlign:"right" }}>
                        {Math.round(swingAmount*100)}%
                      </div>
                    </div>
                  )}

                  {/* ── Temporal consistency meter ── */}
                  {bpmTemporal !== null && (
                    <div style={{ marginTop:8,display:"flex",alignItems:"center",gap:8 }}>
                      <div style={{ color:"#555",fontSize:10,fontWeight:700,letterSpacing:1,width:68 }}>STABILITY</div>
                      <div style={{ flex:1,background:"#111",borderRadius:4,overflow:"hidden",height:4 }}>
                        <div style={{ height:"100%",borderRadius:4,
                          background: bpmTemporal > 0.7 ? "#22C55E" : bpmTemporal > 0.4 ? "#F59E0B" : "#EF4444",
                          width:Math.round(bpmTemporal*100)+"%", transition:"width 0.5s ease" }} />
                      </div>
                      <div style={{ color: bpmTemporal > 0.7 ? "#22C55E" : bpmTemporal > 0.4 ? "#F59E0B" : "#EF4444",
                        fontSize:10,fontWeight:700,width:30,textAlign:"right" }}>
                        {Math.round(bpmTemporal*100)}%
                      </div>
                    </div>
                  )}

                  {/* ── Secondary BPM candidates ── */}
                  {bpmCandidates && bpmCandidates.length > 0 && (
                    <div style={{ marginTop:10 }}>
                      <div style={{ color:"#A78BFA",fontSize:9,fontWeight:800,letterSpacing:1.5,marginBottom:6 }}>ALT CANDIDATES</div>
                      <div style={{ display:"flex",gap:6,flexWrap:"wrap" }}>
                        {bpmCandidates.map(function(b, i) {
                          // Handle both shapes: number or {bpm, score}
                          var bpmVal = (b && typeof b === "object") ? b.bpm : b;
                          return (
                            <button key={i} onClick={function(){ setBpm(Math.round(bpmVal)); setDetectedBpm(Math.round(bpmVal)); setBpmSource("auto"); }}
                              style={{
                                background:"linear-gradient(135deg,rgba(192,38,211,0.12),rgba(124,58,237,0.12),rgba(59,130,246,0.12))",
                                border:"1px solid rgba(124,58,237,0.35)",
                                borderRadius:8,color:"#A78BFA",fontSize:11,fontWeight:800,padding:"6px 11px",cursor:"pointer",
                                boxShadow:"0 0 8px rgba(124,58,237,0.1)",
                              }}>
                              {Math.round(bpmVal)} BPM
                            </button>
                          );
                        })}
                      </div>
                    </div>
                  )}
                </div>
              )}

              {/* ── Action row: Auto-detect + Beat Grid toggle ── */}
              <div style={{ padding:"0 16px 14px",display:"flex",alignItems:"center",gap:8,flexWrap:"wrap" }}>
                {/* Auto-detect from loaded audio */}
                {tracks.some(function(t){ return t.clips && t.clips.some(function(c){ return c.audioBuffer; }); }) && (
                  <button onClick={function(){ detectBpm(); }}
                    disabled={bpmDetecting}
                    style={{
                      background: bpmDetecting
                        ? "rgba(124,58,237,0.08)"
                        : "linear-gradient(135deg,rgba(192,38,211,0.18),rgba(124,58,237,0.18),rgba(59,130,246,0.18))",
                      border:"1px solid "+(bpmDetecting ? "rgba(124,58,237,0.2)" : "rgba(192,38,211,0.45)"),
                      borderRadius:8,
                      color:bpmDetecting?"#6b6685":"#E9D5FF",
                      fontSize:11,fontWeight:800,letterSpacing:0.5,
                      padding:"7px 14px",cursor:bpmDetecting?"not-allowed":"pointer",
                      opacity:bpmDetecting?0.7:1,display:"flex",alignItems:"center",gap:6,
                      boxShadow: bpmDetecting ? "none" : "0 0 12px rgba(192,38,211,0.15)",
                    }}>
                    {bpmDetecting
                      ? <span style={{ width:10,height:10,borderRadius:"50%",border:"2px solid rgba(192,38,211,0.25)",borderTop:"2px solid #C026D3",animation:"bf-spin 0.7s linear infinite",display:"inline-block" }} />
                      : <span style={{ filter:"drop-shadow(0 0 4px rgba(192,38,211,0.6))" }}>⚡</span>}
                    {bpmDetecting ? "Analyzing…" : "Auto-Detect"}
                  </button>
                )}

                {/* Beat grid overlay toggle */}
                <button onClick={function(){ setShowBeatGrid(function(v){ return !v; }); }}
                  style={{
                    background: showBeatGrid
                      ? "linear-gradient(135deg,rgba(59,130,246,0.18),rgba(124,58,237,0.18))"
                      : "rgba(124,58,237,0.05)",
                    border:"1px solid "+(showBeatGrid?"rgba(59,130,246,0.5)":"rgba(124,58,237,0.18)"),
                    borderRadius:8,
                    color: showBeatGrid?"#93C5FD":"#6b6685",
                    fontSize:11,fontWeight:800,letterSpacing:0.5,
                    padding:"7px 12px",cursor:"pointer",
                    boxShadow: showBeatGrid ? "0 0 12px rgba(59,130,246,0.15)" : "none",
                  }}>
                  {showBeatGrid ? "▦ Grid ON" : "▦ Grid OFF"}
                </button>

                {/* Result display */}
                {detectedBpm > 0 && !bpmDetecting && (
                  <span style={{
                    fontSize:12,fontWeight:800,marginLeft:"auto",letterSpacing:0.5,
                    background:"linear-gradient(135deg,#C026D3,#7C3AED,#3B82F6)",
                    WebkitBackgroundClip:"text", backgroundClip:"text", color:"transparent",
                    filter:"drop-shadow(0 0 6px rgba(124,58,237,0.4))",
                  }}>✓ {detectedBpm} BPM</span>
                )}
                {detectedBpm === -1 && !bpmDetecting && (
                  <span style={{ color:"#A78BFA",fontSize:11,fontWeight:600,marginLeft:"auto",opacity:0.7 }}>Could not detect</span>
                )}
              </div>
            </div>
            {/* ── PROJECT KEY (rare-change, but useful for scale-aware AI tools) ── */}
            <div style={{
              margin:"0 16px 12px",
              background:"linear-gradient(180deg, rgba(167,139,250,0.06), rgba(167,139,250,0.02))",
              border:"1px solid rgba(167,139,250,0.18)",
              borderRadius:14,
              boxShadow:"0 0 14px rgba(124,58,237,0.05) inset",
            }}>
              <button onClick={function(){ setShowKeyPick(true); }} style={{ width:"100%",display:"flex",alignItems:"center",justifyContent:"space-between",padding:"14px 16px",background:"none",border:"none",cursor:"pointer" }}>
                <div style={{ display:"flex", alignItems:"center", gap:10 }}>
                  <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="#a78bfa" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><path d="M9 18V5l12-2v13"/><circle cx="6" cy="18" r="3"/><circle cx="18" cy="16" r="3"/></svg>
                  <span style={{ color:"#e8e6f0",fontWeight:700,fontSize:13,letterSpacing:0.3 }}>Project Key</span>
                </div>
                <div style={{ display:"flex", alignItems:"center", gap:6 }}>
                  <span style={{
                    color:"#c4b5fd",fontSize:13,fontWeight:800,letterSpacing:0.5,
                    textShadow:"0 0 6px rgba(167,139,250,0.35)",
                  }}>{projectKey}</span>
                  <span style={{ color:"#7a6ea0", fontSize:14 }}>›</span>
                </div>
              </button>
            </div>

            {/* ── Quick toggle grid: Snap · Metronome · Beat Grid · Loop ── */}
            <div style={{ margin:"0 16px 12px", display:"grid", gridTemplateColumns:"1fr 1fr", gap:8 }}>
              {[
                { label:"Snap to Grid",  desc:"Snap regions to beat",  value: snapToGrid,    onToggle: function(){ setSnapToGrid(function(v){ return !v; }); },
                  icon: <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><rect x="3" y="3" width="18" height="18" rx="2"/><line x1="9" y1="3" x2="9" y2="21"/><line x1="15" y1="3" x2="15" y2="21"/><line x1="3" y1="9" x2="21" y2="9"/><line x1="3" y1="15" x2="21" y2="15"/></svg> },
                { label:"Metronome",     desc:"Click while recording", value: metronomeOn,   onToggle: function(){ setMetronomeOn(function(v){ return !v; }); },
                  icon: <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><path d="M6 22h12L16 4H8L6 22z"/><line x1="12" y1="10" x2="12" y2="16"/></svg> },
                { label:"Beat Grid",     desc:"Show bars on timeline", value: showBeatGrid,  onToggle: function(){ setShowBeatGrid(function(v){ return !v; }); },
                  icon: <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><line x1="4" y1="6" x2="4" y2="18"/><line x1="10" y1="6" x2="10" y2="18"/><line x1="16" y1="6" x2="16" y2="18"/><line x1="20" y1="6" x2="20" y2="18"/></svg> },
                { label:"Loop Region",   desc:"Repeat selected range", value: loopEnabled,   onToggle: function(){ setLoopEnabled(function(v){ return !v; }); },
                  icon: <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><polyline points="17 1 21 5 17 9"/><path d="M3 11V9a4 4 0 0 1 4-4h14"/><polyline points="7 23 3 19 7 15"/><path d="M21 13v2a4 4 0 0 1-4 4H3"/></svg> },
              ].map(function(item, i){
                return (
                  <button key={i} onClick={item.onToggle} style={{
                    background: item.value
                      ? "linear-gradient(180deg, rgba(167,139,250,0.15), rgba(124,58,237,0.10))"
                      : "linear-gradient(180deg, rgba(20,15,40,0.7), rgba(15,10,30,0.7))",
                    border: "1px solid " + (item.value ? "rgba(167,139,250,0.5)" : "rgba(167,139,250,0.12)"),
                    borderRadius: 10,
                    padding: "10px 12px",
                    cursor: "pointer",
                    textAlign: "left",
                    color: item.value ? "#e0d4ff" : "#7a6ea0",
                    boxShadow: item.value ? "0 0 12px rgba(167,139,250,0.18)" : "none",
                    transition: "all 0.15s",
                  }}>
                    <div style={{ display:"flex", alignItems:"center", gap:6, marginBottom:3 }}>
                      <span style={{
                        color: item.value ? "#a78bfa" : "#5a4e7a",
                        filter: item.value ? "drop-shadow(0 0 4px rgba(167,139,250,0.7))" : "none",
                        display: "inline-flex",
                      }}>{item.icon}</span>
                      <span style={{ fontWeight:800, fontSize:11, letterSpacing:0.5 }}>{item.label}</span>
                    </div>
                    <div style={{ fontSize:9, color: item.value ? "#9580c4" : "#4a3f6a", lineHeight:1.2 }}>{item.desc}</div>
                  </button>
                );
              })}
            </div>

            {/* ── Master Volume (replaces buried master fader access for non-mixer users) ── */}
            <div style={{
              margin:"0 16px 12px",
              background:"linear-gradient(180deg, rgba(167,139,250,0.06), rgba(167,139,250,0.02))",
              border:"1px solid rgba(167,139,250,0.18)",
              borderRadius:14,
            }}>
              <div style={{ padding:"12px 16px" }}>
                <div style={{ display:"flex", alignItems:"center", justifyContent:"space-between", marginBottom:8 }}>
                  <div style={{ display:"flex", alignItems:"center", gap:10 }}>
                    <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="#a78bfa" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><polygon points="11 5 6 9 2 9 2 15 6 15 11 19 11 5"/><path d="M19.07 4.93a10 10 0 0 1 0 14.14M15.54 8.46a5 5 0 0 1 0 7.07"/></svg>
                    <span style={{ color:"#e8e6f0",fontWeight:700,fontSize:13 }}>Master Volume</span>
                  </div>
                  <span style={{
                    fontSize:12,fontWeight:800,fontFamily:"monospace",
                    color: masterVolume === 0 ? "#ff6b6b" : masterVolume > 1 ? "#f472b6" : "#a78bfa",
                    minWidth:50,textAlign:"right",
                    textShadow: "0 0 6px rgba(167,139,250,0.35)",
                  }}>
                    {masterVolume <= 0 ? "−∞" : (20*Math.log10(masterVolume)).toFixed(1)} dB
                  </span>
                </div>
                <input type="range" min={0} max={1.5} step={0.01} value={masterVolume}
                  onChange={function(e){ setMasterVolume(parseFloat(e.target.value)); }}
                  style={{ width:"100%",accentColor:"#a78bfa",touchAction:"none" }} />
              </div>
            </div>
            <div style={{
              margin:"0 16px 12px",
              background:"linear-gradient(180deg, rgba(167,139,250,0.06), rgba(167,139,250,0.02))",
              border:"1px solid rgba(167,139,250,0.18)",
              borderRadius:14,
            }}>
              <div style={{ padding:"12px 16px 10px" }}>
                <div style={{ display:"flex",alignItems:"center",justifyContent:"space-between",marginBottom:8 }}>
                  <div style={{ flex:1, minWidth:0 }}>
                    <div style={{ display:"flex", alignItems:"center", gap:10 }}>
                      <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="#a78bfa" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><path d="M12 1a3 3 0 0 0-3 3v8a3 3 0 0 0 6 0V4a3 3 0 0 0-3-3z"/><path d="M19 10v2a7 7 0 0 1-14 0v-2M12 19v4M8 23h8"/></svg>
                      <div style={{ color:"#e8e6f0",fontWeight:700,fontSize:13 }}>Microphone Gain</div>
                    </div>
                    <div style={{ color:"#7a6ea0",fontSize:10,marginTop:3,marginLeft:26 }}>Boost or cut the mic input level</div>
                  </div>
                  <span style={{
                    fontSize:12,fontWeight:800,fontFamily:"monospace",
                    color:micInputGain > 3 ? "#f472b6" : "#a78bfa",
                    minWidth:48,textAlign:"right",
                    textShadow:"0 0 6px rgba(167,139,250,0.35)",
                  }}>
                    {micInputGain >= 1 ? "+" : ""}{(20 * Math.log10(micInputGain)).toFixed(1)} dB
                  </span>
                </div>
                <input type="range" min={0.25} max={4} step={0.05} value={micInputGain}
                  title={"Mic Gain: " + (20 * Math.log10(micInputGain)).toFixed(1) + " dB  (default = 0.0 dB)"}
                  onChange={function(e){ setMicInputGainState(parseFloat(e.target.value)); }}
                  style={{ width:"100%",accentColor:"#a78bfa" }} />
                <div style={{ display:"flex",justifyContent:"space-between",marginTop:4 }}>
                  <span style={{ color:"#5a4e7a",fontSize:9,fontFamily:"monospace" }}>−12 dB</span>
                  <span style={{ color:"#5a4e7a",fontSize:9,fontFamily:"monospace" }}>0 dB</span>
                  <span style={{ color:"#5a4e7a",fontSize:9,fontFamily:"monospace" }}>+12 dB</span>
                </div>
              </div>
            </div>
            {/* ── Recording Offset slider ──
                Compensates for the delay between when audio reaches your ears
                (after the audio graph processes it) and when the mic captures
                your voice. If recordings come in EARLY, REDUCE this value.
                If recordings come in LATE, INCREASE it. Default 80ms is a
                middle-of-the-road starting point. */}
            <div style={{
              margin:"0 16px 12px",
              background:"linear-gradient(180deg, rgba(167,139,250,0.06), rgba(167,139,250,0.02))",
              border:"1px solid rgba(167,139,250,0.18)",
              borderRadius:14,
            }}>
              <div style={{ padding:"12px 16px 10px" }}>
                <div style={{ display:"flex",alignItems:"center",justifyContent:"space-between",marginBottom:8 }}>
                  <div style={{ flex:1, minWidth:0 }}>
                    <div style={{ display:"flex", alignItems:"center", gap:10 }}>
                      <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="#a78bfa" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><circle cx="12" cy="12" r="10"/><polyline points="12 6 12 12 16 14"/></svg>
                      <div style={{ color:"#e8e6f0",fontWeight:700,fontSize:13 }}>Recording Offset</div>
                    </div>
                    <div style={{ color:"#7a6ea0",fontSize:10,marginTop:3,marginLeft:26,lineHeight:1.3 }}>If vocals come in early, reduce. If late, increase.</div>
                  </div>
                  <span style={{
                    fontSize:12,fontWeight:800,fontFamily:"monospace",color:"#a78bfa",
                    minWidth:52,textAlign:"right",
                    textShadow:"0 0 6px rgba(167,139,250,0.35)",
                  }}>
                    {recOffsetMs > 0 ? "+" : ""}{recOffsetMs} ms
                  </span>
                </div>
                <input type="range" min={-200} max={200} step={5} value={recOffsetMs}
                  title={"Recording offset: " + recOffsetMs + " ms (default = 80 ms)"}
                  onChange={function(e){ setRecOffsetMs(parseInt(e.target.value, 10)); }}
                  style={{ width:"100%",accentColor:"#a78bfa" }} />
                <div style={{ display:"flex",justifyContent:"space-between",marginTop:4 }}>
                  <span style={{ color:"#5a4e7a",fontSize:9,fontFamily:"monospace" }}>−200</span>
                  <span style={{ color:"#5a4e7a",fontSize:9,fontFamily:"monospace" }}>0</span>
                  <span style={{ color:"#5a4e7a",fontSize:9,fontFamily:"monospace" }}>+200</span>
                </div>
                <button onClick={function(){ setRecOffsetMs(80); }}
                  style={{ marginTop:10,background:"rgba(167,139,250,0.08)",border:"1px solid rgba(167,139,250,0.2)",borderRadius:6,color:"#9580c4",fontSize:10,fontWeight:700,padding:"5px 10px",cursor:"pointer",letterSpacing:0.3 }}>
                  Reset to default (80 ms)
                </button>
              </div>
            </div>
            {/* ── FX Recording Offset slider ──
                Auto-applies on top of the base offset when ANY track has
                FX enabled at recording start. Different devices need
                different values (depends on headphones, FX chain length,
                worklet latency). Use the Calibrate button for a guided
                first-time setup. */}
            <div style={{
              margin:"0 16px 12px",
              background:"linear-gradient(180deg, rgba(167,139,250,0.06), rgba(167,139,250,0.02))",
              border:"1px solid rgba(167,139,250,0.18)",
              borderRadius:14,
            }}>
              <div style={{ padding:"12px 16px 10px" }}>
                <div style={{ display:"flex",alignItems:"center",justifyContent:"space-between",marginBottom:8 }}>
                  <div style={{ flex:1, minWidth:0 }}>
                    <div style={{ display:"flex", alignItems:"center", gap:10 }}>
                      <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="#a78bfa" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><path d="M3 12h4l3-9 4 18 3-9h4"/></svg>
                      <div style={{ color:"#e8e6f0",fontWeight:700,fontSize:13 }}>FX Recording Offset</div>
                    </div>
                    <div style={{ color:"#7a6ea0",fontSize:10,marginTop:3,marginLeft:26,lineHeight:1.3 }}>Extra offset when any track has FX enabled.</div>
                  </div>
                  <span style={{
                    fontSize:12,fontWeight:800,fontFamily:"monospace",color:"#a78bfa",
                    minWidth:52,textAlign:"right",
                    textShadow:"0 0 6px rgba(167,139,250,0.35)",
                  }}>
                    {fxRecOffsetMs > 0 ? "+" : ""}{fxRecOffsetMs} ms
                  </span>
                </div>
                <input type="range" min={-300} max={300} step={5} value={fxRecOffsetMs}
                  title={"FX Recording Offset: " + fxRecOffsetMs + " ms (default = 0 ms)"}
                  onChange={function(e){ setFxRecOffsetMs(parseInt(e.target.value, 10)); }}
                  style={{ width:"100%",accentColor:"#a78bfa" }} />
                <div style={{ display:"flex",justifyContent:"space-between",marginTop:4 }}>
                  <span style={{ color:"#5a4e7a",fontSize:9,fontFamily:"monospace" }}>−300</span>
                  <span style={{ color:"#5a4e7a",fontSize:9,fontFamily:"monospace" }}>0</span>
                  <span style={{ color:"#5a4e7a",fontSize:9,fontFamily:"monospace" }}>+300</span>
                </div>
                <div style={{ display:"flex",gap:8,marginTop:10 }}>
                  <button onClick={function(){ setCalibrateOpen(true); }}
                    style={{
                      background:"linear-gradient(180deg, rgba(167,139,250,0.18), rgba(124,58,237,0.18))",
                      border:"1px solid rgba(167,139,250,0.45)",
                      borderRadius:6,color:"#e0d4ff",fontSize:11,fontWeight:800,padding:"6px 14px",cursor:"pointer",letterSpacing:0.5,
                      boxShadow:"0 0 8px rgba(167,139,250,0.25)",
                    }}>
                    Calibrate
                  </button>
                  <button onClick={function(){ setFxRecOffsetMs(0); }}
                    style={{ background:"rgba(167,139,250,0.08)",border:"1px solid rgba(167,139,250,0.2)",borderRadius:6,color:"#9580c4",fontSize:10,fontWeight:700,padding:"5px 10px",cursor:"pointer",letterSpacing:0.3 }}>
                    Reset to 0 ms
                  </button>
                </div>
              </div>
            </div>
            {/* ── Performance Mode toggle ──
                When ON, freezes VU meters and the toolbar time display.
                The playhead still moves smoothly (DOM-ref based, not React
                state). Audio engine is untouched. Use when screen recording
                or on slower devices. */}
            <div style={{
              margin:"0 16px 80px",
              background:"linear-gradient(180deg, rgba(167,139,250,0.06), rgba(167,139,250,0.02))",
              border:"1px solid rgba(167,139,250,0.18)",
              borderRadius:14,
            }}>
              <div style={{ padding:"12px 16px" }}>
                <div style={{ display:"flex",alignItems:"center",justifyContent:"space-between",gap:12 }}>
                  <div style={{ flex:1, minWidth:0 }}>
                    <div style={{ display:"flex",alignItems:"center",gap:10 }}>
                      <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke={performanceMode?"#fbbf24":"#a78bfa"} strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><polygon points="13 2 3 14 12 14 11 22 21 10 12 10 13 2"/></svg>
                      <div style={{ color:"#e8e6f0",fontWeight:700,fontSize:13,display:"flex",alignItems:"center",gap:8 }}>
                        Performance Mode
                        {performanceMode && (
                          <span style={{ background:"rgba(251,191,36,0.15)", color:"#fbbf24", fontSize:9, padding:"2px 6px", borderRadius:4, fontWeight:800, letterSpacing:0.5 }}>ON</span>
                        )}
                      </div>
                    </div>
                    <div style={{ color:"#7a6ea0",fontSize:10,marginTop:3,marginLeft:26,lineHeight:1.4 }}>
                      Freezes VU meters and time display to free up CPU. Audio is unaffected.
                    </div>
                  </div>
                  <button onClick={function(){ setPerformanceMode(!performanceMode); }}
                    style={{
                      width:48, height:28, borderRadius:14,
                      background: performanceMode ? "linear-gradient(90deg,#f59e0b,#fbbf24)" : "rgba(20,15,40,0.7)",
                      border: "1px solid " + (performanceMode ? "#fcd34d" : "rgba(167,139,250,0.2)"),
                      cursor:"pointer", position:"relative", padding:0, flexShrink:0,
                      boxShadow: performanceMode ? "0 0 10px rgba(251,191,36,0.4)" : "none",
                    }}>
                    <div style={{
                      position:"absolute", top:2, left: performanceMode ? 22 : 2,
                      width:22, height:22, borderRadius:"50%",
                      background: performanceMode ? "white" : "#5a4e7a",
                      transition:"left 0.15s",
                      boxShadow:"0 1px 4px rgba(0,0,0,0.5)",
                    }} />
                  </button>
                </div>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* ── Calibration modal ──
          Guided 5-option prompt for tuning the FX recording offset.
          Each option maps to a starting value. User can refine via the
          slider afterwards. */}
      {calibrateOpen && (
        <div style={{ position:"absolute", inset:0, zIndex:750, background:"rgba(0,0,0,0.85)", display:"flex", alignItems:"center", justifyContent:"center", padding:20 }}
          onClick={function(){ setCalibrateOpen(false); }}>
          <div onClick={function(e){ e.stopPropagation(); }}
            style={{ background:"#111", borderRadius:16, maxWidth:380, width:"100%", overflow:"hidden", border:"1px solid #1f1f1f", boxShadow:"0 12px 40px rgba(0,0,0,0.7)" }}>
            <div style={{ padding:"18px 20px 12px", borderBottom:"1px solid #1a1a1a" }}>
              <div style={{ color:"white", fontWeight:800, fontSize:16 }}>Calibrate FX Recording</div>
              <div style={{ color:"#666", fontSize:12, marginTop:4, lineHeight:1.4 }}>
                Record a short take with FX on a track, then tell me how it sounded. I'll set a starting value you can refine on the slider.
              </div>
            </div>
            <div style={{ padding:"8px 0" }}>
              {[
                { label: "🎯 On time",         desc: "Stays the same — no FX offset needed", val: 0 },
                { label: "⏪ A bit early",      desc: "Vocal slightly ahead of the beat",     val: 30 },
                { label: "⏩ A bit late",       desc: "Vocal slightly behind the beat",       val: -30 },
                { label: "🔥 Way too early",   desc: "Vocal clearly ahead of the beat",      val: 120 },
                { label: "🐢 Way too late",    desc: "Vocal clearly behind the beat",        val: -120 },
              ].map(function(opt, idx){
                return (
                  <button key={opt.val} onClick={function(){
                    setFxRecOffsetMs(opt.val);
                    setCalibrateOpen(false);
                    try {
                      if (typeof window !== "undefined" && window.bfToast && window.bfToast.info) {
                        window.bfToast.info("FX offset set to " + (opt.val > 0 ? "+" : "") + opt.val + "ms. Fine-tune with the slider if needed.");
                      }
                    } catch(e) {}
                  }}
                    style={{
                      display:"flex", alignItems:"center", gap:10,
                      width:"100%", padding:"14px 20px",
                      background:"none", border:"none",
                      borderBottom: idx < 4 ? "1px solid #161616" : "none",
                      color:"white", cursor:"pointer", textAlign:"left",
                    }}
                    onPointerEnter={function(e){ e.currentTarget.style.background="#181818"; }}
                    onPointerLeave={function(e){ e.currentTarget.style.background="transparent"; }}>
                    <div style={{ flex:1 }}>
                      <div style={{ fontWeight:700, fontSize:13 }}>{opt.label}</div>
                      <div style={{ color:"#666", fontSize:11, marginTop:2 }}>{opt.desc}</div>
                    </div>
                    <span style={{ color:"#10B981", fontSize:11, fontWeight:800, fontFamily:"monospace", minWidth:48, textAlign:"right" }}>
                      {opt.val > 0 ? "+" : ""}{opt.val} ms
                    </span>
                  </button>
                );
              })}
            </div>
            <div style={{ padding:"12px 20px", borderTop:"1px solid #1a1a1a", display:"flex", justifyContent:"flex-end" }}>
              <button onClick={function(){ setCalibrateOpen(false); }}
                style={{ background:"none", border:"1px solid #2a2a2a", borderRadius:6, color:"#888", fontSize:12, padding:"6px 14px", cursor:"pointer" }}>
                Cancel
              </button>
            </div>
          </div>
        </div>
      )}

      {showProjects && (
        <div
          onClick={function(){ setShowProjects(false); }}
          style={{
            position:"fixed", inset:0, zIndex:9000,
            background:"rgba(0,0,0,0.78)",
            backdropFilter:"blur(4px)",
            WebkitBackdropFilter:"blur(4px)",
            display:"flex", alignItems:"center", justifyContent:"center",
            padding:"16px",
          }}>
          <div
            onClick={function(e){ e.stopPropagation(); }}
            style={{
              width:"100%", maxWidth:520,
              background:"linear-gradient(180deg,#141118 0%,#0d0c12 100%)",
              borderRadius:24,
              border:"1px solid rgba(124,58,237,0.18)",
              boxShadow:"0 20px 60px rgba(0,0,0,0.7)",
              maxHeight:"82vh",
              display:"flex", flexDirection:"column",
              overflow:"hidden",
            }}>
            {/* Sheet header */}
            <div style={{ padding:"20px 20px 14px", display:"flex", alignItems:"center", justifyContent:"space-between", flexShrink:0 }}>
              <div>
                <div style={{ color:"white", fontWeight:800, fontSize:18, letterSpacing:0.2 }}>Saved Projects</div>
                <div style={{ color:"#666", fontSize:11, marginTop:2 }}>
                  {(function(){
                    // Count = local + cloud-only (cloud projects without a local mirror)
                    var localCloudIds = new Set(
                      (savedProjects || [])
                        .map(function(p){ return p.cloudId; })
                        .filter(Boolean)
                    );
                    var cloudOnly = (cloudProjects || []).filter(function(cp){
                      return !localCloudIds.has(cp.id);
                    });
                    var total = (savedProjects || []).length + cloudOnly.length;
                    return total + " " + (total === 1 ? "project" : "projects");
                  })()}
                  {cloudFetchState === "loading" && (
                    <span style={{ color:"#A78BFA", marginLeft:8, opacity:0.7 }}>· syncing…</span>
                  )}
                </div>
              </div>
              <button
                onClick={function(){ setShowProjects(false); }}
                style={{
                  background:"#1a1a1a", border:"1px solid #2a2a2a",
                  borderRadius:"50%", width:32, height:32, color:"#888",
                  fontSize:16, cursor:"pointer", display:"flex",
                  alignItems:"center", justifyContent:"center", padding:0,
                }}>✕</button>
            </div>
            {/* Scrollable list */}
            <div
              onTouchMove={function(e){ e.stopPropagation(); }}
              style={{ flex:1, minHeight:0, overflowY:"auto", padding:"0 16px 20px", WebkitOverflowScrolling:"touch", touchAction:"pan-y", overscrollBehavior:"contain" }}>
              {(function(){
                // ── Merge local + cloud project lists for display ───────
                // Pattern:
                //   localProj with cloudId matching a cloudProj.id → SYNCED (show local)
                //   localProj with no cloudId / no match           → LOCAL_ONLY
                //   cloudProj with no local mirror                  → CLOUD_ONLY (restore-able)
                //
                // We render all of them in one merged list, sorted by most
                // recent save first. Visual badges indicate the storage state.
                var localList = (savedProjects || []).map(function(p) {
                  var matchedCloud = null;
                  if (p.cloudId) {
                    matchedCloud = (cloudProjects || []).find(function(c) {
                      return c.id === p.cloudId;
                    });
                  }
                  return Object.assign({}, p, {
                    _kind: matchedCloud ? "synced" : "local",
                    _sortDate: p.savedAt || (matchedCloud && matchedCloud.updated_at) || null,
                  });
                });
                var localCloudIds = new Set(
                  localList.map(function(p){ return p.cloudId; }).filter(Boolean)
                );
                var cloudOnlyList = (cloudProjects || [])
                  .filter(function(cp){ return !localCloudIds.has(cp.id); })
                  .map(function(cp){
                    return {
                      id:        cp.id,    // we use cloud id as the React key
                      cloudId:   cp.id,
                      name:      cp.name,
                      bpm:       cp.bpm,
                      projectKey:cp.key,
                      tracks:    [],
                      savedAt:   cp.updated_at,
                      _kind:     "cloud",
                      _sortDate: cp.updated_at,
                    };
                  });
                var merged = localList.concat(cloudOnlyList);
                // Sort newest first by _sortDate (ISO strings compare lexically just fine)
                merged.sort(function(a, b){
                  var aD = a._sortDate || "";
                  var bD = b._sortDate || "";
                  return aD < bD ? 1 : aD > bD ? -1 : 0;
                });
                if (merged.length === 0) {
                  return (
                    <div style={{ color:"#555", fontSize:14, textAlign:"center", padding:"48px 20px" }}>
                      <div style={{ fontSize:32, marginBottom:10, opacity:0.4 }}>📂</div>
                      <div style={{ fontWeight:700, color:"#888", marginBottom:4 }}>No saved projects</div>
                      <div style={{ fontSize:12, color:"#444" }}>Save a project from the ••• menu to see it here.</div>
                    </div>
                  );
                }
                return merged.map(function(p) {
                  const savedAt = p.savedAt ? new Date(p.savedAt) : null;
                  const dateStr = savedAt
                    ? (savedAt.toLocaleDateString(undefined, { day:"numeric", month:"short" }) +
                       " · " + savedAt.toLocaleTimeString(undefined, { hour:"2-digit", minute:"2-digit" }))
                    : "";
                  return (
                    <div key={p._kind === "cloud" ? "cloud_" + p.id : p.id} style={{
                      background:"#15131c",
                      borderRadius:14,
                      padding:"14px 14px",
                      marginBottom:10,
                      display:"flex", alignItems:"center", gap:10,
                      border:"1px solid " + (p._kind === "cloud" ? "rgba(59,130,246,0.3)" : "#1f1c28"),
                    }}>
                      <div
                        onClick={function(){
                          if (p._kind === "cloud") {
                            // Phase 3 Part B: actually restore from cloud
                            loadCloudProject(p.cloudId, p.name);
                          } else {
                            loadProject(p);
                          }
                        }}
                        style={{ flex:1, minWidth:0, cursor:"pointer" }}>
                        <div style={{
                          color:"white", fontWeight:700, fontSize:14, marginBottom:4,
                          overflow:"hidden", textOverflow:"ellipsis", whiteSpace:"nowrap",
                          display:"flex", alignItems:"center", gap:6,
                        }}>
                          {p.name}
                          {p._kind === "cloud" && (
                            <span style={{
                              fontSize:9, fontWeight:800, letterSpacing:0.5,
                              color:"#60A5FA", background:"rgba(59,130,246,0.12)",
                              padding:"2px 6px", borderRadius:4,
                              border:"1px solid rgba(59,130,246,0.3)",
                              flexShrink:0,
                            }}>☁ CLOUD</span>
                          )}
                          {p._kind === "synced" && (
                            <span style={{
                              fontSize:9, fontWeight:800, letterSpacing:0.5,
                              color:"#34D399", background:"rgba(52,211,153,0.10)",
                              padding:"2px 6px", borderRadius:4,
                              border:"1px solid rgba(52,211,153,0.25)",
                              flexShrink:0,
                            }}>✓ SYNCED</span>
                          )}
                        </div>
                        <div style={{ color:"#777", fontSize:11, display:"flex", flexWrap:"wrap", gap:"4px 8px" }}>
                          <span>{(p.tracks||[]).length} {((p.tracks||[]).length === 1) ? "track" : "tracks"}</span>
                          <span style={{ color:"#333" }}>·</span>
                          <span>{p.bpm||120} BPM</span>
                          {dateStr && <span style={{ color:"#333" }}>·</span>}
                          {dateStr && <span>{dateStr}</span>}
                        </div>
                      </div>
                      <button
                        onClick={function(e){
                          e.stopPropagation();
                          if (p._kind === "cloud") {
                            // Cloud-only delete — fires backend DELETE directly
                            if (!window.confirm("Delete '" + p.name + "' from cloud? This cannot be undone.")) return;
                            apiFetch("/api/studio/projects/delete/" + encodeURIComponent(p.cloudId), {
                              method: "DELETE",
                            }).then(function(){
                              setCloudProjects(function(prev){
                                return (prev || []).filter(function(c){ return c.id !== p.cloudId; });
                              });
                            }).catch(function(err){
                              console.warn("[BeatFinder] Cloud delete failed:", err);
                              setError("Could not delete from cloud — " + ((err && err.message) || "unknown error"));
                              setTimeout(function(){ setError(""); }, 4000);
                            });
                          } else {
                            deleteProject(p.id);
                          }
                        }}
                        style={{
                          background:"#1f1a26", border:"1px solid #2a2336",
                          borderRadius:8, width:32, height:32, color:"#777",
                          fontSize:13, cursor:"pointer", flexShrink:0,
                          display:"flex", alignItems:"center", justifyContent:"center", padding:0,
                        }}>✕</button>
                    </div>
                  );
                });
              })()}
            </div>
          </div>
        </div>
      )}

      {/* ══ TOP BAR ══ Neon-purple theme to match the rest of the studio.
          Sized to fit narrow phones (iPhone SE, iPhone 13 mini at 375px).
          Button sizes and gaps are deliberately tight; the project name
          ellipses to absorb any remaining width. */}
      <div style={{
        display:"flex", alignItems:"center",
        padding:"8px 8px",
        paddingTop:"calc(8px + var(--bf-safe-top, env(safe-area-inset-top)))",
        borderBottom:"1px solid rgba(167,139,250,0.15)",
        background:"linear-gradient(180deg, #110b22 0%, #0a0716 100%)",
        boxShadow:"0 1px 0 rgba(167,139,250,0.05) inset, 0 4px 14px rgba(0,0,0,0.5)",
        flexShrink:0, gap:4, zIndex:50,
        boxSizing:"border-box",
        width:"100%",
        overflow:"hidden",
      }}>
        {/* Home / Exit */}
        <button onClick={function(){
          if(!isSaved&&hasContent){
            setUnsavedAlert("exit");
          } else {
            stopMonitoring();
            stopAll(); setIsPlaying(false);
            setTracks([]); setProjectName("New Project");
            setBpm(120); setProjectKey("C major"); setTimeSigNum(4);
            setCurrentTime(0); setIsSaved(true); setSelectedTrackId(null);
            if(scrollRef.current) scrollRef.current.scrollLeft = 0;
            onExit();
          }
        }} style={{
          background:"linear-gradient(180deg, rgba(20,15,40,0.9), rgba(15,10,30,0.9))",
          border:"1px solid rgba(167,139,250,0.18)",
          borderRadius:8, color:"#9580c4",
          width:28, height:28,
          display:"flex", alignItems:"center", justifyContent:"center",
          cursor:"pointer", flexShrink:0,
        }}>
          <svg width="13" height="13" viewBox="0 0 24 24" fill="currentColor"><path d="M10 20v-6h4v6h5v-8h3L12 3 2 12h3v8z"/></svg>
        </button>

        {/* Undo / Redo */}
        <button onClick={undoTracks} disabled={!canUndo} title="Undo" style={{
          background: canUndo ? "rgba(20,15,40,0.7)" : "rgba(10,7,20,0.5)",
          border: "1px solid " + (canUndo ? "rgba(167,139,250,0.18)" : "rgba(167,139,250,0.06)"),
          borderRadius:6,
          color: canUndo ? "#a78bfa" : "#3a2f4a",
          width:24, height:24,
          display:"flex", alignItems:"center", justifyContent:"center",
          cursor: canUndo ? "pointer" : "not-allowed", flexShrink:0,
        }}>
          <svg width="11" height="11" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.2" strokeLinecap="round" strokeLinejoin="round"><path d="M3 7h11a5 5 0 0 1 0 10H7"/><polyline points="7 3 3 7 7 11"/></svg>
        </button>
        <button onClick={redoTracks} disabled={!canRedo} title="Redo" style={{
          background: canRedo ? "rgba(20,15,40,0.7)" : "rgba(10,7,20,0.5)",
          border: "1px solid " + (canRedo ? "rgba(167,139,250,0.18)" : "rgba(167,139,250,0.06)"),
          borderRadius:6,
          color: canRedo ? "#a78bfa" : "#3a2f4a",
          width:24, height:24,
          display:"flex", alignItems:"center", justifyContent:"center",
          cursor: canRedo ? "pointer" : "not-allowed", flexShrink:0,
        }}>
          <svg width="11" height="11" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.2" strokeLinecap="round" strokeLinejoin="round"><path d="M21 7H10a5 5 0 0 0 0 10h7"/><polyline points="17 3 21 7 17 11"/></svg>
        </button>

        {/* Project name */}
        {renamingProj
          ? <input autoFocus defaultValue={projectName}
              onBlur={function(e){ setProjectName(e.target.value||projectName);setRenamingProj(false);setIsSaved(false); }}
              onKeyDown={function(e){ if(e.key==="Enter"){setProjectName(e.target.value||projectName);setRenamingProj(false);setIsSaved(false);} }}
              style={{
                background:"none", border:"none",
                borderBottom:"1px solid #a78bfa",
                color:"#e0d4ff", fontSize:12, fontWeight:700,
                outline:"none", flex:1, minWidth:0, padding:"0 0 2px",
              }} />
          : <span style={{
              color:"#e0d4ff", fontWeight:700, fontSize:11, flex:1, minWidth:0,
              overflow:"hidden", textOverflow:"ellipsis", whiteSpace:"nowrap",
              letterSpacing:0.3,
              textShadow:"0 0 8px rgba(167,139,250,0.25)",
              padding:"0 2px",
            }}>{projectName}</span>
        }

        {/* HELP — opens the Studio guides */}
        <button onClick={function(e){ e.stopPropagation(); setShowGuides(true); setShowProjMenu(false); setShowSettings(false); }}
          style={{
            background: "rgba(167,139,250,0.10)",
            border: "1px solid rgba(167,139,250,0.3)",
            borderRadius:6,
            color: "#a78bfa",
            fontSize:11, fontWeight:800,
            padding:"4px 9px", cursor:"pointer", flexShrink:0, letterSpacing:1,
            display:"flex", alignItems:"center", gap:4,
            transition:"all 0.12s",
          }}>
          <svg width="12" height="12" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.2" strokeLinecap="round" strokeLinejoin="round"><circle cx="12" cy="12" r="10"/><path d="M9.09 9a3 3 0 0 1 5.83 1c0 2-3 3-3 3"/><line x1="12" y1="17" x2="12.01" y2="17"/></svg>
          HELP
        </button>

        {/* ··· menu */}
        <button onClick={function(e){ e.stopPropagation();setShowProjMenu(function(v){return !v;});setShowSettings(false); }}
          style={{
            background: showProjMenu ? "rgba(167,139,250,0.18)" : "rgba(20,15,40,0.7)",
            border: "1px solid " + (showProjMenu ? "rgba(167,139,250,0.5)" : "rgba(167,139,250,0.18)"),
            borderRadius:6,
            color: showProjMenu ? "#c4b5fd" : "#9580c4",
            fontSize:12, fontWeight:700,
            padding:"4px 8px", cursor:"pointer", flexShrink:0, letterSpacing:1.5,
            boxShadow: showProjMenu ? "0 0 10px rgba(167,139,250,0.25)" : "none",
            transition:"all 0.12s",
          }}>···</button>

        {/* Settings gear */}
        <button onClick={function(e){ e.stopPropagation();setShowProjMenu(false);setShowSettings(function(v){return !v;}); }}
          style={{
            background: showSettings ? "rgba(167,139,250,0.18)" : "rgba(20,15,40,0.7)",
            border: "1px solid " + (showSettings ? "rgba(167,139,250,0.5)" : "rgba(167,139,250,0.18)"),
            borderRadius:6,
            color: showSettings ? "#c4b5fd" : "#9580c4",
            width:26, height:26,
            display:"flex", alignItems:"center", justifyContent:"center",
            cursor:"pointer", flexShrink:0,
            boxShadow: showSettings ? "0 0 10px rgba(167,139,250,0.25)" : "none",
            transition:"all 0.12s",
          }}>
          <svg width="12" height="12" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><circle cx="12" cy="12" r="3"/><path d="M19.4 15a1.65 1.65 0 0 0 .33 1.82l.06.06a2 2 0 0 1 0 2.83 2 2 0 0 1-2.83 0l-.06-.06a1.65 1.65 0 0 0-1.82-.33 1.65 1.65 0 0 0-1 1.51V21a2 2 0 0 1-2 2 2 2 0 0 1-2-2v-.09A1.65 1.65 0 0 0 9 19.4a1.65 1.65 0 0 0-1.82.33l-.06.06a2 2 0 0 1-2.83 0 2 2 0 0 1 0-2.83l.06-.06a1.65 1.65 0 0 0 .33-1.82 1.65 1.65 0 0 0-1.51-1H3a2 2 0 0 1-2-2 2 2 0 0 1 2-2h.09A1.65 1.65 0 0 0 4.6 9a1.65 1.65 0 0 0-.33-1.82l-.06-.06a2 2 0 0 1 0-2.83 2 2 0 0 1 2.83 0l.06.06a1.65 1.65 0 0 0 1.82.33H9a1.65 1.65 0 0 0 1-1.51V3a2 2 0 0 1 2-2 2 2 0 0 1 2 2v.09a1.65 1.65 0 0 0 1 1.51 1.65 1.65 0 0 0 1.82-.33l.06-.06a2 2 0 0 1 2.83 0 2 2 0 0 1 0 2.83l-.06.06a1.65 1.65 0 0 0-.33 1.82V9a1.65 1.65 0 0 0 1.51 1H21a2 2 0 0 1 2 2 2 2 0 0 1-2 2h-.09a1.65 1.65 0 0 0-1.51 1z"/></svg>
        </button>

        {/* Debug overlay toggle — admin only */}
        {(user && (user.is_admin === true || user.username === "Trelli")) && (
          <button onClick={function(e){ e.stopPropagation(); setShowDebug(function(v){ return !v; }); }}
            title="Debug (admin)"
            style={{
              background: showDebug ? "rgba(34,197,94,0.18)" : "rgba(20,15,40,0.7)",
              border: "1px solid " + (showDebug ? "rgba(34,197,94,0.5)" : "rgba(167,139,250,0.18)"),
              borderRadius: 6, color: showDebug ? "#22C55E" : "#9580c4",
              width: 26, height: 26,
              display: "flex", alignItems: "center", justifyContent: "center",
              cursor: "pointer", flexShrink: 0,
              boxShadow: showDebug ? "0 0 8px rgba(34,197,94,0.25)" : "none",
            }}>
            <svg width="12" height="12" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><rect x="8" y="6" width="8" height="14" rx="4"/><path d="M19 7l-3 2M5 7l3 2M19 17l-3-2M5 17l3-2M12 6V3M9 4l3 2 3-2"/></svg>
          </button>
        )}

        {/* Lyrics toggle */}
        <button onClick={function(e){ e.stopPropagation(); setLyricsPanelOpen(function(v){ return !v; }); }}
          title="Lyrics"
          style={{
            background: lyricsPanelOpen ? "rgba(167,139,250,0.18)" : "rgba(20,15,40,0.7)",
            border: "1px solid " + (lyricsPanelOpen ? "rgba(167,139,250,0.5)" : "rgba(167,139,250,0.18)"),
            borderRadius: 6, color: lyricsPanelOpen ? "#c4b5fd" : "#9580c4",
            width: 26, height: 26,
            display: "flex", alignItems: "center", justifyContent: "center",
            cursor: "pointer", flexShrink: 0,
            boxShadow: lyricsPanelOpen ? "0 0 10px rgba(167,139,250,0.3)" : "none",
            transition: "all 0.15s ease",
          }}>
          <svg width="12" height="12" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
            <path d="M14 2H6a2 2 0 0 0-2 2v16a2 2 0 0 0 2 2h12a2 2 0 0 0 2-2V8z"/>
            <polyline points="14 2 14 8 20 8"/>
            <line x1="16" y1="13" x2="8" y2="13"/>
            <line x1="16" y1="17" x2="8" y2="17"/>
            <polyline points="10 9 9 9 8 9"/>
          </svg>
        </button>

        {/* Time display — neon LED readout */}
        <div style={{
          color: isRecording ? "#ff6b6b" : "#c4b5fd",
          fontSize:10, fontFamily:"monospace", fontWeight:800,
          letterSpacing:0.5,
          flexShrink:0,
          background: isRecording
            ? "linear-gradient(180deg, rgba(60,15,15,0.9), rgba(40,10,10,0.9))"
            : "linear-gradient(180deg, rgba(20,15,40,0.9), rgba(15,10,30,0.9))",
          border: "1px solid " + (isRecording ? "rgba(255,107,107,0.4)" : "rgba(167,139,250,0.25)"),
          borderRadius:5, padding:"3px 6px",
          boxShadow: isRecording
            ? "0 0 8px rgba(255,107,107,0.4), 0 0 0 1px rgba(0,0,0,0.3) inset"
            : "0 0 8px rgba(167,139,250,0.18), 0 0 0 1px rgba(0,0,0,0.3) inset",
          textShadow: isRecording
            ? "0 0 6px rgba(255,107,107,0.6)"
            : "0 0 6px rgba(167,139,250,0.5)",
        }}>{fmt(currentTime)}</div>
      </div>

      {showProjMenu&&(
        <div style={{ position:"absolute",top:58,right:52,zIndex:6000 }} onClick={function(e){ e.stopPropagation(); }}>
          <div style={{
            background:"linear-gradient(180deg, #1a1530 0%, #14102a 100%)",
            border:"1px solid #2a2050",
            borderRadius:14,
            overflow:"hidden",
            minWidth:240,
            maxWidth:280,
            boxShadow:"0 12px 36px rgba(0,0,0,0.85), 0 0 0 1px rgba(167,139,250,0.06)",
            maxHeight: "calc(100vh - 120px)",
            overflowY: "auto",
          }}>
          {(function(){
            // Reusable item style + components for consistency.
            const SectionHeader = function(props){
              return (
                <div style={{
                  padding:"10px 14px 6px",
                  background:"rgba(167,139,250,0.04)",
                  fontSize:9, fontWeight:800,
                  color:"#7a6ea0",
                  letterSpacing:1.8,
                  textTransform:"uppercase",
                  borderTop: props.first ? "none" : "1px solid #221a40",
                }}>{props.title}</div>
              );
            };
            const ItemRow = function(p){
              const accent = p.accent || "#a78bfa";
              const labelColor = p.disabled ? "#3a2f4a" : (p.danger ? "#ff6b6b" : "#e8e6f0");
              const hintColor  = p.disabled ? "#2a223e" : (p.danger ? "#7a3a3a" : "#7a6ea0");
              const hoverBg    = p.disabled ? "transparent"
                : (p.danger ? "rgba(255,59,48,0.10)" : "rgba(167,139,250,0.08)");
              return (
                <button
                  onClick={p.disabled ? undefined : p.onClick}
                  disabled={!!p.disabled}
                  style={{
                    width:"100%",
                    background:"none", border:"none",
                    padding:"11px 14px",
                    textAlign:"left",
                    cursor: p.disabled ? "default" : "pointer",
                    display:"flex", alignItems:"center", gap:12,
                  }}
                  onPointerEnter={function(e){ if (!p.disabled) e.currentTarget.style.background = hoverBg; }}
                  onPointerLeave={function(e){ e.currentTarget.style.background = "transparent"; }}>
                  <div style={{
                    flexShrink:0, width:18, height:18,
                    display:"flex", alignItems:"center", justifyContent:"center",
                    color: p.disabled ? "#3a2f4a" : accent,
                  }}>{p.icon}</div>
                  <div style={{flex:1, minWidth:0}}>
                    <div style={{ fontSize:13, fontWeight:600, color:labelColor, lineHeight:1.2,
                                  whiteSpace:"nowrap", overflow:"hidden", textOverflow:"ellipsis" }}>{p.label}</div>
                    {p.hint && (
                      <div style={{ fontSize:10, color:hintColor, marginTop:2, lineHeight:1.2,
                                    whiteSpace:"nowrap", overflow:"hidden", textOverflow:"ellipsis" }}>{p.hint}</div>
                    )}
                  </div>
                  {p.badge != null && (
                    <span style={{
                      flexShrink:0,
                      fontSize:10, fontWeight:800,
                      color: accent,
                      letterSpacing:0.4,
                      background: "rgba(167,139,250,0.13)",
                      padding:"2px 8px", borderRadius:999,
                      lineHeight:1.2,
                    }}>{p.badge}</span>
                  )}
                </button>
              );
            };
            // Clip selection state for Copy/Paste rows
            const hasMulti  = selClipIds && selClipIds.size > 0;
            const hasSingle = !hasMulti && selectedClipId != null;
            const copyEnabled  = hasMulti || hasSingle;
            const copyCount    = hasMulti ? selClipIds.size : (hasSingle ? 1 : 0);
            const pasteEnabled = clipboardClips && clipboardClips.clips && clipboardClips.clips.length > 0;
            const pasteCount   = pasteEnabled ? clipboardClips.clips.length : 0;
            const isAdminUser  = user && (user.is_admin === true || user.username === "Trelli");
            const ICON = {
              save: (<svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><path d="M19 21H5a2 2 0 0 1-2-2V5a2 2 0 0 1 2-2h11l5 5v11a2 2 0 0 1-2 2z"/><polyline points="17 21 17 13 7 13 7 21"/><polyline points="7 3 7 8 15 8"/></svg>),
              saveAs: (<svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><path d="M14 2H6a2 2 0 0 0-2 2v16a2 2 0 0 0 2 2h12a2 2 0 0 0 2-2V8z"/><polyline points="14 2 14 8 20 8"/><line x1="12" y1="18" x2="12" y2="12"/><line x1="9" y1="15" x2="15" y2="15"/></svg>),
              rename: (<svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><path d="M12 20h9"/><path d="M16.5 3.5a2.121 2.121 0 0 1 3 3L7 19l-4 1 1-4L16.5 3.5z"/></svg>),
              folder: (<svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><path d="M22 19a2 2 0 0 1-2 2H4a2 2 0 0 1-2-2V5a2 2 0 0 1 2-2h5l2 3h9a2 2 0 0 1 2 2z"/></svg>),
              plus: (<svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><line x1="12" y1="5" x2="12" y2="19"/><line x1="5" y1="12" x2="19" y2="12"/></svg>),
              selectAll: (<svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><path d="M9 11l3 3L22 4"/><path d="M21 12v7a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2V5a2 2 0 0 1 2-2h11"/></svg>),
              copy: (<svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><rect x="9" y="9" width="13" height="13" rx="2"/><path d="M5 15H4a2 2 0 0 1-2-2V4a2 2 0 0 1 2-2h9a2 2 0 0 1 2 2v1"/></svg>),
              paste: (<svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><path d="M16 4h2a2 2 0 0 1 2 2v14a2 2 0 0 1-2 2H6a2 2 0 0 1-2-2V6a2 2 0 0 1 2-2h2"/><rect x="8" y="2" width="8" height="4" rx="1" ry="1"/></svg>),
              export: (<svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><path d="M21 15v4a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2v-4"/><polyline points="7 10 12 15 17 10"/><line x1="12" y1="15" x2="12" y2="3"/></svg>),
              bug: (<svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><rect x="8" y="6" width="8" height="14" rx="4"/><path d="M19 7l-3 2M5 7l3 2M19 17l-3-2M5 17l3-2M12 6V3M9 4l3 2 3-2"/></svg>),
            };
            return (
              <>
                {/* ── PROJECT ── */}
                <SectionHeader title="Project" first/>
                <ItemRow
                  icon={ICON.save} label="Save" hint="Save changes to this project"
                  onClick={function(){
                    setShowProjMenu(false);
                    if (!projectName || projectName === "New Project") {
                      setPendingName(""); setPendingNameAction("save"); setShowNamePrompt(true);
                    } else { saveProject(); }
                  }}/>
                <ItemRow
                  icon={ICON.saveAs} label="Save As…" hint="Save a copy with a new name"
                  onClick={function(){
                    setShowProjMenu(false);
                    setTimeout(function(){
                      const n = window.prompt("Save as:", projectName + " (copy)");
                      if (n) { setProjectName(n); setIsSaved(false); setTimeout(saveProject, 50); }
                    }, 50);
                  }}/>
                <ItemRow
                  icon={ICON.rename} label="Rename" hint="Change the project name"
                  onClick={function(){ setShowProjMenu(false); setRenamingProj(true); }}/>
                <ItemRow
                  icon={ICON.folder} label="All Projects" hint="Open the project browser"
                  onClick={function(){ setShowProjMenu(false); setTimeout(function(){ setShowProjects(true); }, 50); }}/>
                <ItemRow
                  icon={ICON.plus} label="New Project" hint="Start with an empty timeline"
                  onClick={function(){
                    setShowProjMenu(false);
                    setTimeout(function(){
                      if (!isSaved && hasContent) setUnsavedAlert("new");
                      else {
                        stopAll(); setIsPlaying(false);
                        setTracks([]); setProjectName("New Project");
                        setBpm(120); setProjectKey("C major"); setTimeSigNum(4);
                        setCurrentTime(0); setIsSaved(true); setSelectedTrackId(null);
                        if (scrollRef.current) scrollRef.current.scrollLeft = 0;
                      }
                    }, 50);
                  }}/>

                {/* ── EDIT ── */}
                <SectionHeader title="Edit"/>
                <ItemRow
                  icon={ICON.selectAll} label="Select All Clips" hint="Select every clip across all tracks"
                  onClick={function(){
                    setShowProjMenu(false);
                    const ids = new Set();
                    (tracksRef.current || []).forEach(function(t){
                      (t.clips || []).forEach(function(cl){ if (cl && cl.id != null) ids.add(cl.id); });
                    });
                    setSelectedClipId(null); setSelClipIds(ids);
                  }}/>
                <ItemRow
                  icon={ICON.copy}
                  label="Copy"
                  hint={copyEnabled ? "Copy selected clips" : "Select clips first"}
                  accent="#3B82F6"
                  badge={copyEnabled ? copyCount : null}
                  disabled={!copyEnabled}
                  onClick={function(){
                    setShowProjMenu(false);
                    const snapshots = [];
                    let earliestStart = Infinity;
                    const targetIds = hasMulti ? selClipIds : new Set([selectedClipId]);
                    tracksRef.current.forEach(function(t){
                      (t.clips || []).forEach(function(cl){
                        if (!targetIds.has(cl.id)) return;
                        const startT = cl.startTime || 0;
                        if (startT < earliestStart) earliestStart = startT;
                        const snap = { ...cl, __sourceTrackId: t.id };
                        snapshots.push(snap);
                      });
                    });
                    if (snapshots.length === 0) return;
                    if (earliestStart === Infinity) earliestStart = 0;
                    setClipboardClips({ anchorStart: earliestStart, clips: snapshots });
                    try { if (navigator.vibrate) navigator.vibrate(10); } catch(_){}
                  }}/>
                <ItemRow
                  icon={ICON.paste}
                  label="Paste at Playhead"
                  hint={pasteEnabled ? "Paste " + pasteCount + " clip" + (pasteCount === 1 ? "" : "s") : "Clipboard is empty"}
                  accent="#22C55E"
                  badge={pasteEnabled ? pasteCount : null}
                  disabled={!pasteEnabled}
                  onClick={function(){ setShowProjMenu(false); pasteClipsAtPlayhead(currentTime || 0); }}/>

                {/* ── EXPORT ── */}
                <SectionHeader title="Export"/>
                <ItemRow
                  icon={ICON.export} label="Export Mix" hint="Bounce to WAV / MP3 for download"
                  accent="#C026D3"
                  onClick={function(){ setShowProjMenu(false); setTimeout(function(){ setShowExportWarning(true); }, 50); }}/>

                {/* ── ADMIN (only when applicable) ── */}
                {isAdminUser && (<>
                  <SectionHeader title="Admin"/>
                  <ItemRow
                    icon={ICON.bug} label="Debug Log" hint="Diagnostic events and performance"
                    accent="#22C55E"
                    onClick={function(){ setShowProjMenu(false); setTimeout(function(){ setShowDebugPanel(true); }, 50); }}/>
                </>)}
              </>
            );
          })()}
          </div>
        </div>
      )}

      {saveStatus&&<div style={{ background:"rgba(34,197,94,0.1)",borderBottom:"1px solid rgba(34,197,94,0.2)",color:"#22C55E",fontSize:12,padding:"5px 16px",textAlign:"center",flexShrink:0 }}>{saveStatus}</div>}
      {error&&<div style={{ background:"rgba(239,68,68,0.1)",borderBottom:"1px solid rgba(239,68,68,0.2)",color:"#F87171",fontSize:12,padding:"5px 16px",textAlign:"center",flexShrink:0 }}>{error}</div>}

      {/* Zoom / Loop bar — monitoring toggle lives here too */}
      <div style={{ display:"flex",alignItems:"center",gap:6,padding:"4px 12px",background:"#090909",borderBottom:"1px solid #0f0f0f",flexShrink:0 }}>
        {/* H-zoom label */}
        <span style={{ color:"#333",fontSize:7,fontWeight:800,letterSpacing:0.5,flexShrink:0 }}>H</span>
        <button onClick={function(){
          setZoom(function(z){
            // Exponential zoom-out: each tap multiplies by ~0.7 so steps feel even at all levels
            const next = Math.max(0.1, parseFloat((z * 0.7).toFixed(3)));
            // Anchor scroll so playhead stays in view after zoom
            requestAnimationFrame(function(){
              const el = scrollRef.current;
              if (!el) return;
              const newPPS = PPS * next;
              const oldPPS = PPS * z;
              const sl = el.scrollLeft;
              el.scrollLeft = Math.max(0, sl * (newPPS / oldPPS));
            });
            return next;
          });
        }} style={{ background:"#141414",border:"1px solid #222",borderRadius:5,color:"#888",fontSize:16,width:24,height:24,cursor:"pointer",display:"flex",alignItems:"center",justifyContent:"center" }}>−</button>
        <span style={{ color:"#555",fontSize:10,fontFamily:"monospace",width:38,textAlign:"center" }}>
          {Math.round((zoom - 0.1) / (1 - 0.1) * 100) + "%"}
        </span>
        <button onClick={function(){
          setZoom(function(z){
            // Exponential zoom-in: each tap multiplies by ~1.4
            const next = Math.min(1, parseFloat((z * 1.4).toFixed(3)));
            requestAnimationFrame(function(){
              const el = scrollRef.current;
              if (!el) return;
              const newPPS = PPS * next;
              const oldPPS = PPS * z;
              const sl = el.scrollLeft;
              el.scrollLeft = Math.max(0, sl * (newPPS / oldPPS));
            });
            return next;
          });
        }} style={{ background:"#141414",border:"1px solid #222",borderRadius:5,color:"#888",fontSize:16,width:24,height:24,cursor:"pointer",display:"flex",alignItems:"center",justifyContent:"center" }}>+</button>
        <div style={{ width:1,background:"#1a1a1a",height:14,margin:"0 4px" }} />
        {/* Vertical zoom controls — identical behaviour to horizontal, scales track row heights */}
        <span style={{ color:"#333",fontSize:7,fontWeight:800,letterSpacing:0.5,flexShrink:0 }}>V</span>
        <button onClick={function(){
          setVZoom(function(v){ return Math.max(0.46, parseFloat((v * 0.7).toFixed(3))); });
        }} style={{ background:"#141414",border:"1px solid #222",borderRadius:5,color:"#888",fontSize:12,width:24,height:24,cursor:"pointer",display:"flex",alignItems:"center",justifyContent:"center",lineHeight:1 }} title="Vertical zoom out">
          <svg width="13" height="13" viewBox="0 0 13 13" fill="none"><path d="M6.5 2v9M2 6.5h9" stroke="#888" strokeWidth="1.6" strokeLinecap="round"/><path d="M3.5 9.5 9.5 3.5" stroke="transparent" strokeWidth="0"/><path d="M4 9h5" stroke="#888" strokeWidth="1.6" strokeLinecap="round"/></svg>
        </button>
        <span style={{ color:"#555",fontSize:9,fontFamily:"monospace",width:32,textAlign:"center" }}>
          {Math.round((vZoom - 0.46) / (0.99 - 0.46) * 100) + "%"}
        </span>
        <button onClick={function(){
          setVZoom(function(v){ return Math.min(0.99, parseFloat((v * 1.4).toFixed(3))); });
        }} style={{ background:"#141414",border:"1px solid #222",borderRadius:5,color:"#888",fontSize:12,width:24,height:24,cursor:"pointer",display:"flex",alignItems:"center",justifyContent:"center",lineHeight:1 }} title="Vertical zoom in">
          <svg width="13" height="13" viewBox="0 0 13 13" fill="none"><path d="M6.5 2v9M2 6.5h9" stroke="#888" strokeWidth="1.6" strokeLinecap="round"/></svg>
        </button>
        <div style={{ width:1,background:"#1a1a1a",height:14,margin:"0 4px" }} />
        <button onClick={function(){
          setLoopEnabled(function(v){
            const enabling = !v;
            // On enable: always reset to a 2-bar region spanning bar 1 to bar 3.
            // We do NOT preserve any saved/previous region — long-audio sessions
            // would otherwise restore a giant loopOut from earlier work and the
            // loop bar would span the whole track. The user can always drag the
            // handles or middle of the region to resize/move it afterwards.
            if (enabling) {
              setLoopIn(0);
              setLoopOut(spBar * 2);
            }
            return enabling;
          });
        }} style={{ background:loopEnabled?"rgba(59,130,246,0.2)":"#141414",border:"1px solid "+(loopEnabled?"#3B82F6":"#222"),borderRadius:6,color:loopEnabled?"#3B82F6":"#555",fontSize:10,fontWeight:700,padding:"3px 8px",cursor:"pointer" }}>LOOP</button>
        <div style={{ flex:1 }} />
        <div style={{ width:1, background:"#1a1a1a", height:14, margin:"0 2px" }} />
        {/* Input monitoring toggle — only available when headphones/headset connected */}
        <button
          onClick={function(){ if (!headphonesIn) return; monitoringOn ? stopMonitoring() : startMonitoring(); }}
          disabled={!headphonesIn}
          title={headphonesIn ? (monitoringOn ? "Turn off monitoring" : "Turn on monitoring") : "Connect headphones or a headset to enable monitoring"}
          style={{
            display:"flex", alignItems:"center", gap:5,
            background: monitoringOn ? "rgba(34,197,94,0.15)" : "#141414",
            border: "1px solid " + (monitoringOn ? "#22C55E" : headphonesIn ? "#2a2a2a" : "#1a1a1a"),
            borderRadius:8, padding:"4px 8px",
            cursor: headphonesIn ? "pointer" : "not-allowed",
            opacity: headphonesIn ? 1 : 0.35,
          }}
        >
          <span style={{ fontSize:13 }}><Icon id="headphones" size={16} color={monitoringOn ? "#22C55E" : headphonesIn ? "#aaa" : "#444"} strokeWidth={1.8}/></span>
          <div style={{ width:22,height:12,borderRadius:6,background:monitoringOn?"#22C55E":"#333",position:"relative",transition:"background 0.15s" }}>
            <div style={{ position:"absolute",top:2,left:monitoringOn?10:2,width:8,height:8,borderRadius:"50%",background:"white",transition:"left 0.15s" }} />
          </div>
        </button>
        {monitoringOn && (
          <input type="range" min={0} max={1} step={0.05} value={monitorVol}
            onChange={function(e){ setMonitorVol(parseFloat(e.target.value)); }}
            style={{ width:50,accentColor:"#22C55E",height:2 }} />
        )}
        {/* Mic source picker — dropdown. Headset option is never disabled;
            status is shown in the label itself so users always know what's connected. */}
        <select
          value={micSource}
          onChange={function(e){
            const next = e.target.value;
userPickedMicRef.current = true;
            setMicSource(next);
            if (monitoringOn) { stopMonitoring(); setTimeout(function(){ startMonitoring(undefined, next); }, 150); }
          }}
          title="Choose microphone source"
          style={{ background:"#141414", border:"1px solid #2a2a2a", borderRadius:6, color:"#aaa", fontSize:9, fontWeight:700, padding:"3px 5px", cursor:"pointer", outline:"none", maxWidth:130 }}
        >
          <option value="builtin">📱 iPhone Mic</option>
          <option value="headset">🎧 Headset Mic</option>
        </select>
      </div>
      {monitorWarn && <div style={{ background:"rgba(245,158,11,0.1)",borderBottom:"1px solid rgba(245,158,11,0.2)",color:"#F59E0B",fontSize:11,padding:"4px 16px",textAlign:"center",flexShrink:0 }}>{monitorWarn}</div>}

      {/* ══ DAW LAYOUT — sticky track headers + shared horizontal scroll ════
          Single scrollable div holds ruler + all track rows.
          Track name column uses position:sticky,left:0 — never scrolls away.
          Ruler uses position:sticky,top:0 — always visible.
          One scrollLeft drives everything — zero sync bugs.
      ══════════════════════════════════════════════════════════════════════ */}
      <div ref={lassoContainerRef} style={{ flex:1, minHeight:0, overflow:"clip", position:"relative" }}>

        {/* Playhead — position updated directly via DOM ref, no React re-render = smooth */}
        <div
          ref={playheadRef}
          style={{
            position:"absolute",
            top:0, bottom:0,
            left: SIDEBAR_W, // t=0 = bar 1; lassoContainerRef spans both sidebar+lanes so offset by sidebar width
            width:1, zIndex:40, pointerEvents:"none",
            willChange:"left",
          }}
        >
          <div style={{ position:"absolute", top:RULER_H-8, left:-5, width:0, height:0, borderLeft:"5px solid transparent", borderRight:"5px solid transparent", borderTop:"8px solid white" }} />
          <div style={{ position:"absolute", top:RULER_H, bottom:0, width:1, background:"rgba(255,255,255,0.88)" }} />
          <div style={{ position:"absolute", top:0, height:RULER_H, width:1, background:"rgba(255,255,255,0.22)" }} />
        </div>

        {/* ── Beat Grid Overlay — renders beat marker lines across all tracks ── */}
        {showBeatGrid && beatPositions.length > 0 && (
          <BeatGridOverlay
            beats={beatPositions}
            effectivePPS={effectivePPS}
            sidebarW={SIDEBAR_W}
            rulerH={RULER_H}
            scrollRef={scrollRef}
            isPlaying={isPlaying}
            currentTime={currentTime}
            bpm={bpm}
          />
        )}

        {/* ── TWO-COLUMN DAW LAYOUT ─────────────────────────────────────────────
            LEFT:  sidebar — track headers only, scrolls vertically in sync, never horizontally
            RIGHT: scroll area — ruler + lanes, scrolls both axes
        ──────────────────────────────────────────────────────────────────────── */}
        <div style={{ display:"flex", width:"100%", height:"100%", overflow:"hidden" }}>

          {/* ── LEFT SIDEBAR — track headers, locked horizontally ── */}
          <div
            ref={sidebarRef}
            style={{
              width: SIDEBAR_W, flexShrink:0,
              overflowY:"hidden", overflowX:"hidden",
              background:"#0a0a0a",
              borderRight:"1px solid #141414",
              display:"flex", flexDirection:"column",
              zIndex:50,
            }}
          >
            {/* Corner — TRACKS label, aligns with ruler height */}
            <div style={{
              height:RULER_H, flexShrink:0,
              borderBottom:"1px solid #1a1a1a",
              display:"flex", alignItems:"center", paddingLeft:10,
            }}>
              <span style={{ color:"#333", fontSize:9, fontWeight:700 }}>TRACKS</span>
            </div>

            {/* Track header rows — scroll vertically in sync with main area */}
            <div style={{ flex:1, overflow:"hidden", position:"relative" }}>
              <div ref={sidebarRowsRef} style={{ position:"absolute", top:0, left:0, right:0 }}>
                {tracks.map(function(track, trackIdx){
                const isRec   = isRecording && recTrackId === track.id;
                const isBeingDragged = reorderDragId === track.id;
                const isDropTgt = reorderDropIdx === trackIdx && reorderDragId !== null && reorderDragId !== track.id;
                return (
                  <div key={track.id} style={{ height:TRACK_H, borderBottom: isDropTgt ? "2px solid #30D158" : "1px solid #0f0f0f", flexShrink:0 }}>
                    <LogicTrackHeader
                      track={track}
                      isRec={isRec}
                      isSelected={selectedTrackId===track.id}
                      fxOpen={fxTrackId===track.id}
                      showTakes={showTakes===track.id}
                      onSelect={function(){ setSelectedTrackId(track.id); }}
                      onMute={function(){ toggleMute(track.id); }}
                      onSolo={function(){ toggleSolo(track.id); }}
                      onFx={function(){ setFxTrackId(function(v){ return v===track.id?null:track.id; }); }}
                      onDrumRack={track.type === "drumSequencer" ? function(){
                        // Toggle the channel rack overlay for this track.
                        setDrumRackTrackId(function(v){ return v === track.id ? null : track.id; });
                      } : undefined}
                      onOpenSound={track.type === "midi" ? function(){
                        // Open the preset browser scoped to this track.
                        setPresetBrowserForTrackId(track.id);
                      } : undefined}
                      onTakes={function(){ setShowTakes(function(v){ return v===track.id?null:track.id; }); }}
                      onRemove={function(){ setConfirmDeleteTrackId(track.id); }}
                      onDuplicate={function(){ duplicateTrack(track.id); }}
                      updateTrack={updateTrack}
                      analyserNode={trackAnalysersRef.current[track.id] || null}
                      isPlaying={isPlaying}
                      isDragging={isBeingDragged}
                      isDropTarget={isDropTgt}
                      onLongPressStart={function(e){ handleHeaderLongPressStart(e, track); }}
                      onLongPressCancel={handleHeaderLongPressCancel}
                      onDragMove={handleHeaderDragMove}
                      onDragEnd={handleHeaderDragEnd}
                      beginGesture={beginTracksGesture}
                      endGesture={endTracksGesture}
                    />
                  </div>
                );
              })}
              {/* Add track button — neon-purple themed */}
              <div style={{ height:48, borderBottom:"1px solid rgba(167,139,250,0.10)", display:"flex", alignItems:"center", justifyContent:"center" }}>
                <button onClick={function(e){ e.stopPropagation(); setShowAddMenu(function(v){return !v;}); }} style={{
                  width:"100%", height:"100%",
                  background: showAddMenu ? "rgba(167,139,250,0.10)" : "transparent",
                  border:"none", cursor:"pointer",
                  display:"flex", alignItems:"center", justifyContent:"center",
                  gap:6,
                  transition:"background 0.15s",
                }}>
                  <span style={{
                    width:22, height:22, borderRadius:"50%",
                    background: showAddMenu
                      ? "linear-gradient(180deg, #a78bfa, #7c3aed)"
                      : "linear-gradient(180deg, rgba(30,20,50,0.7), rgba(20,15,40,0.7))",
                    border: "1px solid " + (showAddMenu ? "#c4b5fd" : "rgba(167,139,250,0.25)"),
                    color: showAddMenu ? "#fff" : "#a78bfa",
                    fontSize:16, fontWeight:300, lineHeight:1,
                    display:"flex", alignItems:"center", justifyContent:"center",
                    boxShadow: showAddMenu
                      ? "0 0 12px rgba(167,139,250,0.5)"
                      : "0 0 6px rgba(167,139,250,0.1)",
                    transition:"all 0.15s",
                  }}>+</span>
                  <span style={{
                    color: showAddMenu ? "#c4b5fd" : "#7a6ea0",
                    fontSize:10, fontWeight:800, letterSpacing:1.5,
                    textTransform:"uppercase",
                  }}>Add Track</span>
                </button>
              </div>
              </div>
            </div>
          </div>

          {/* ── RIGHT: scroll area — ruler + lanes only, no headers ── */}
          <div
            ref={scrollRef}
            style={{
              flex:1, height:"100%",
              overflowX:"scroll", overflowY:"auto",
              WebkitOverflowScrolling:"touch",
              overscrollBehavior:"none",
              scrollbarWidth:"thin", scrollbarColor:"#2a2a2a #0a0a0a",
            }}
            onTouchMove={function(e){ if (!selBoxRef.current && !trimActiveRef.current) e.stopPropagation(); }}
            onScroll={function(e){
              // Sync sidebar vertical scroll
              if (sidebarRowsRef.current) {
                sidebarRowsRef.current.style.transform = "translateY(-" + e.target.scrollTop + "px)";
              }
              const sl = e.target.scrollLeft;

              if (!isPlayingRef.current) {
                // Stopped — playhead stays at its stored position, timeline scrolls under it
                updatePlayheadDOM(playheadAtRef.current, sl);
              } else {
                // Playing — mark that user is scrolling so auto-scroll pauses
                // Playhead stays at true audio position, RAF tick redraws it each frame
                userScrolledAt.current = performance.now();
                // Clear the flag after 1.5s of no scroll so auto-follow resumes
                if (scrollSeekTimer.current) clearTimeout(scrollSeekTimer.current);
                scrollSeekTimer.current = setTimeout(function() {
                  userScrolledAt.current  = null;
                  scrollSeekTimer.current = null;
                }, 1500);
                // Keep drawing playhead at true audio position while user scrolls
                const actx = actxRef.current;
                const t = actx ? playheadAtRef.current + (actx.currentTime - masterStartRef.current) : playheadAtRef.current;
                updatePlayheadDOM(t, sl);
              }
            }}
          >
          {/* Inner content — wide enough for whole project, no sidebar width needed */}
          <div style={{ minWidth: totalW, position:"relative" }}>

            {/* ── RULER ROW — sticky top ── */}
            <div style={{ position:"sticky", top:0, zIndex:25, height:RULER_H, background:"#0c0c0c", borderBottom:"1px solid #1a1a1a" }}>
              {/* Ruler tick area — tap to seek.
                  Touch handlers are attached imperatively via useEffect
                  (with passive:false) instead of via React's onTouch*
                  props, which would be passive and silently break
                  e.preventDefault() on iOS. */}
              <div
                ref={rulerElRef}
                style={{ position:"relative", height:"100%", cursor:"col-resize", touchAction:"none" }}
                onMouseDown={handleRulerMouseDown}
              >
                {Array.from({length:numBars}, function(_,bi){
                  const bx = bi * spBar * effectivePPS;
                  return (
                    <div key={bi} style={{ position:"absolute", left:bx, top:0, bottom:0 }}>
                      <div style={{ position:"absolute", left:0, top:0, bottom:0, width:1, background:bi===0?"#555":"#1e1e1e" }} />
                      <span style={{ position:"absolute", top:5, left:4, color:"#555", fontSize:9, fontFamily:"monospace", fontWeight:700, userSelect:"none" }}>{bi+1}</span>
                      {Array.from({length:timeSigNum}, function(_,di){
                        if (di===0) return null;
                        return <div key={di} style={{ position:"absolute", left:di*spb*effectivePPS, top:Math.round(RULER_H*0.55), bottom:0, width:1, background:"#181818" }} />;
                      })}
                    </div>
                  );
                })}
                {loopEnabled&&loopOut>loopIn&&(
                  <div
                    onMouseDown={function(e){
                      e.stopPropagation();
                      const startX = e.clientX;
                      const startIn = loopIn;
                      const startOut = loopOut;
                      const duration = startOut - startIn;
                      const onMove = function(me){
                        const dx = me.clientX - startX;
                        const dt = dx / effectivePPS;
                        const newIn = Math.max(0, snapToBar(startIn + dt));
                        setLoopIn(newIn);
                        setLoopOut(newIn + duration);
                      };
                      const onUp = function(){ document.removeEventListener("mousemove",onMove); document.removeEventListener("mouseup",onUp); };
                      document.addEventListener("mousemove",onMove);
                      document.addEventListener("mouseup",onUp);
                    }}
                    onTouchStart={function(e){
                      e.stopPropagation();
                      const startX = e.touches[0].clientX;
                      const startIn = loopIn;
                      const duration = loopOut - startIn;
                      rulerDragRef.current = { mode:"moveLoop", startX, startIn, duration, committed:false };
                    }}
                    style={{ position:"absolute", left:loopIn*effectivePPS, width:(loopOut-loopIn)*effectivePPS, top:0, bottom:0, background:"rgba(59,130,246,0.15)", borderLeft:"2px solid #3B82F6", borderRight:"2px solid #3B82F6", cursor:"grab", touchAction:"none" }}>
                    <span style={{ position:"absolute", top:4, left:4, color:"#3B82F6", fontSize:8, fontWeight:700, pointerEvents:"none", userSelect:"none" }}>LOOP</span>
                  </div>
                )}
              </div>
            </div>

            {/* ── LANE ROWS ── lanes only, no headers ── */}
            {tracks.map(function(track, trackIdx){
              const isRec   = isRecording && recTrackId === track.id;
              const hasSolo = tracks.some(function(t){return t.isSoloed;});
              const dimmed  = hasSolo && !track.isSoloed;
              const clips   = track.clips || [];
              const isBeingDragged = reorderDragId === track.id;
              const isDropTgt = reorderDropIdx === trackIdx && reorderDragId !== null && reorderDragId !== track.id;
              return (
                <div key={track.id} data-lane-trackid={track.id} style={{ display:"flex", height:TRACK_H, borderBottom: isDropTgt ? "2px solid #30D158" : "1px solid #0f0f0f", opacity:dimmed?0.4:1, position:"relative" }}>


                  {/* Lane outer — overflow:visible for trim handles only */}
                  <div style={{
                    position:"relative", flex:1,
                    overflow:"visible",
                    isolation:"isolate",
                  }}
                    onClick={function(e){
                      deselectClip(e);
                      // Clear multi-selection when clicking empty space —
                      // check both the outer lane and the inner mask (which covers the full area)
                      const t = e.target;
                      const isClip = t && t.closest && t.closest("[data-clipid]");
                      if (!isClip) { setSelBox(null); setSelClipIds(new Set()); }
                    }}
                    onTouchStart={function(e){ e.stopPropagation(); handleLaneLongPress(e, track); }}
                  >

                    {/* ── INNER MASK — hard overflow:hidden, everything inside is clipped ── */}
                    <div style={{ position:"absolute", inset:0, overflow:"hidden", background:"#0a0a0a" }}>
                      {/* Beat grid */}
                      {Array.from({length:Math.ceil(totalDur/spb)+1}, function(_,i){
                        return <div key={i} style={{ position:"absolute", left:i*spb*effectivePPS, top:0, bottom:0, width:1, background:i%timeSigNum===0?"#191919":"#131313", pointerEvents:"none" }} />;
                      })}
                      {/* Recording trail — inside mask, can never bleed left */}
                      {isRec&&recTrail.length>0&&(
                        <div style={{ position:"absolute", left:Math.max(0,currentTime*effectivePPS-recTrail.length*2), top:4, height:TRACK_H-8, display:"flex", alignItems:"center", pointerEvents:"none" }}>
                          {recTrail.map(function(v,i){ return <div key={i} style={{ width:2, background:track.color||"#EF4444", borderRadius:1, height:Math.max(2,v*(TRACK_H-12)), opacity:0.4+0.6*(i/recTrail.length) }} />; })}
                        </div>
                      )}
                      {/* Clip bodies — inside mask so waveforms never bleed outside lane */}
                      {clips.map(function(clip){
                        // ── Drum-pattern clip branch ──
                        // Drum tracks' clips reference a pattern by id rather than holding
                        // an AudioBuffer. They render as colored rectangles with a tiny
                        // step-grid preview (dots = active steps per pad), and behave
                        // like audio clips for drag/select/right-click/delete.
                        if (track.type === "drumSequencer" && clip.patternId) {
                          const pat = findPatternInTrack(track, clip.patternId);
                          const stepSec = drumStepDurationSec(bpm, pat ? pat.stepsPerBeat : DRUM_DEFAULT_STEPS_PER_BEAT);
                          // Clip width must NOT change when the project tempo
                          // changes — clips hold their size and position. Use
                          // the stored duration; only if it's missing (legacy
                          // clips) fall back to a FIXED-reference length so the
                          // box never resizes on a bpm change.
                          const fallbackDur = pat
                            ? pat.lengthSteps * drumStepDurationSec(120, pat.stepsPerBeat)
                            : 2;
                          const dur = (typeof clip.duration === "number" && clip.duration > 0)
                            ? clip.duration
                            : fallbackDur;
                          const clipL = Math.max(0, (clip.startTime || 0) * effectivePPS);
                          // No min-width clamp: at low zoom the visual width must
                          // match the actual duration, otherwise clips look like
                          // they overlap when they don't. (Previously was
                          // Math.max(40, ...) which fattened short clips and made
                          // them visually collide with their neighbors.)
                          const clipW = dur * effectivePPS;
                          const isSel = selectedClipId === clip.id;
                          const isMultiSel = selClipIds.has(clip.id);
                          const isActv = clip.active !== false;
                          // Build a tiny preview: one row per channel that has hits,
                          // each row shows colored dots at active step positions.
                          const previewRows = (pat && pat.channels) ? pat.channels.filter(function(ch){
                            return ch.steps && ch.steps.some(function(s){ return stepIsOn(s); });
                          }).slice(0, 4) : [];  // cap rows so very full patterns still fit
                          const previewRowH = Math.max(3, Math.floor((TRACK_H - 14) / Math.max(1, previewRows.length)));
                          return (
                            <div key={clip.id + "_body"}
                              data-clipid={clip.id}
                              onClick={function(e){ e.stopPropagation(); selectClip(clip.id); }}
                              onMouseDown={function(e){ handleRegionMouseDown(e, track, clip); }}
                              onTouchStart={function(e){ handleRegionTouchStart(e, track, clip); }}
                              onContextMenu={function(e){ handleRegionRightClick(e, track, clip); }}
                              style={{
                                position:"absolute", left:clipL, top:3, width:clipW, height:TRACK_H-6,
                                borderRadius:6, overflow:"hidden", touchAction:"none",
                                background: isActv
                                  ? "linear-gradient(180deg, #1a0f23 0%, #14091c 100%)"
                                  : "#0a0a0a",
                                border: isMultiSel ? "2px solid #FACC15"
                                  : isSel ? "2px solid " + track.color
                                  : "1.5px solid " + (isActv ? track.color + "AA" : track.color + "33"),
                                boxShadow: isMultiSel ? "0 0 10px rgba(250,204,21,0.4)"
                                  : isSel ? "inset 0 0 0 1px " + track.color + "33"
                                  : "none",
                                opacity: isActv ? 1 : 0.3,
                                cursor:"grab",
                                zIndex: isMultiSel ? 3 : isActv ? 2 : 1,
                                transition:"border 0.12s, box-shadow 0.12s",
                              }}>
                              {/* Pattern preview — one row per channel that has any hits */}
                              <div style={{ position:"absolute", top:3, left:4, right:4, bottom:12, display:"flex", flexDirection:"column", gap:1, pointerEvents:"none" }}>
                                {previewRows.map(function(ch, ri){
                                  const padMeta = (getKitMeta(track.kitId||"default")||{pads:[]}).pads.find(function(p){return p.id===ch.padId;});
                                  const padColor = padMeta ? padMeta.color : track.color;
                                  return (
                                    <div key={ri} style={{ height: previewRowH, display:"flex", alignItems:"center", gap:1 }}>
                                      {ch.steps.map(function(on, si){
                                        const v = stepVelocity(on);
                                        if (v < DRUM_MIN_VELOCITY) return null;
                                        // Position dot proportionally — each step occupies
                                        // (1 / lengthSteps) of the row's width.
                                        const xPct = (si / Math.max(1, pat.lengthSteps)) * 100;
                                        const wPct = (1 / Math.max(1, pat.lengthSteps)) * 100;
                                        return (
                                          <div key={si} style={{
                                            position:"absolute",
                                            left: xPct + "%",
                                            width: wPct + "%",
                                            top: 3 + ri * (previewRowH + 1),
                                            height: previewRowH,
                                            background: padColor,
                                            borderRadius: 1,
                                            opacity: Math.min(1, 0.45 + 0.4 * v),  // velocity drives visibility
                                          }} />
                                        );
                                      })}
                                    </div>
                                  );
                                })}
                              </div>
                              <div style={{ position:"absolute", bottom:2, left:6, right: isSel ? 14 : 6, color:"rgba(255,255,255,0.85)", fontSize:7, fontWeight:700, pointerEvents:"none", textShadow:"0 1px 3px rgba(0,0,0,0.95)", overflow:"hidden", textOverflow:"ellipsis", whiteSpace:"nowrap" }}>
                                {clip.label || (pat ? pat.name : "Pattern")}{!isActv ? " (inactive)" : ""}
                              </div>
                              {isSel && <div style={{ position:"absolute", top:3, right:3, width:5, height:5, borderRadius:"50%", background:track.color, pointerEvents:"none" }} />}
                            </div>
                          );
                        }
                        // ── MIDI Instrument clip branch ──
                        // MIDI clips live on tracks of type "midi". They carry a
                        // notes[] array (pitch / start / duration / velocity).
                        // Visual:
                        //   • Rounded rect with the track color
                        //   • Mini piano-roll preview: each note is a horizontal
                        //     bar; Y is pitch (normalized to the clip's pitch
                        //     range so the preview fills the track height); X is
                        //     time within the clip.
                        //   • Empty clips show a faint "double-tap to edit" hint
                        //     so producers know to dive in.
                        //   • Selection, drag, resize, duplicate all reuse the
                        //     existing handlers via data-clipid.
                        //   • Tap (no-drag) opens the piano-roll editor — Phase 1
                        //     ships a placeholder modal; Phase 2 builds it out.
                        if (track.type === "midi") {
                          const dur   = clip.duration || 4;
                          const clipL = Math.max(0, (clip.startTime || 0) * effectivePPS);
                          // No min-width clamp — actual duration drives width at
                          // all zooms so clips don't visually overlap their neighbors.
                          const clipW = dur * effectivePPS;
                          const isSel      = selectedClipId === clip.id;
                          const isMultiSel = selClipIds.has(clip.id);
                          const isActv     = clip.active !== false;
                          const notes = clip.notes || [];
                          const trackColor = track.color || "#A78BFA";
                          // Compute pitch range for the preview. We use the clip's
                          // actual range (with a small pad) so even a one-octave
                          // melody fills the preview vertically. Empty clips fall
                          // back to a default range.
                          let minPitch = 60, maxPitch = 72;
                          if (notes.length > 0) {
                            minPitch =  127;
                            maxPitch = 0;
                            for (let i = 0; i < notes.length; i++) {
                              if (notes[i].pitch < minPitch) minPitch = notes[i].pitch;
                              if (notes[i].pitch > maxPitch) maxPitch = notes[i].pitch;
                            }
                            // Pad by a couple semitones so top/bottom notes don't
                            // touch the clip edges (looks cramped).
                            minPitch = Math.max(0,   minPitch - 1);
                            maxPitch = Math.min(127, maxPitch + 1);
                            if (maxPitch - minPitch < 4) {
                              // Single-pitch or near-unison preview — give it a
                              // sensible visual range so the bar isn't full-height.
                              const center = Math.floor((minPitch + maxPitch) / 2);
                              minPitch = Math.max(0,   center - 3);
                              maxPitch = Math.min(127, center + 3);
                            }
                          }
                          const pitchSpan = Math.max(1, maxPitch - minPitch);
                          const previewH  = TRACK_H - 14;  // leave room for label
                          return (
                            <div key={clip.id + "_body"}
                              data-clipid={clip.id}
                              onClick={function(e){ e.stopPropagation(); selectClip(clip.id); }}
                              onMouseDown={function(e){ handleRegionMouseDown(e, track, clip); }}
                              onTouchStart={function(e){ handleRegionTouchStart(e, track, clip); }}
                              onContextMenu={function(e){ handleRegionRightClick(e, track, clip); }}
                              onDoubleClick={function(e){
                                e.stopPropagation();
                                setMidiEditorClipId(clip.id);
                              }}
                              style={{
                                position:"absolute", left:clipL, top:3, width:clipW, height:TRACK_H-6,
                                borderRadius:6, overflow:"hidden", touchAction:"none",
                                // Match the drum-clip aesthetic: deep violet-tinted
                                // gradient base + violet border. Inner glow when
                                // selected ties to the rack redesign.
                                background: isActv
                                  ? "linear-gradient(180deg, #1a1228 0%, #120a1f 100%)"
                                  : "#0a0a0a",
                                border: isMultiSel ? "2px solid #FACC15"
                                  : isSel ? "2px solid " + trackColor
                                  : "1.5px solid " + (isActv ? trackColor + "AA" : trackColor + "33"),
                                boxShadow: isMultiSel ? "0 0 10px rgba(250,204,21,0.4)"
                                  : isSel
                                    ? "inset 0 0 0 1px " + trackColor + "33, 0 0 12px " + trackColor + "44"
                                    : "0 0 8px " + trackColor + "22",
                                opacity: isActv ? 1 : 0.3,
                                cursor:"grab",
                                zIndex: isMultiSel ? 3 : isActv ? 2 : 1,
                                transition:"border 0.12s, box-shadow 0.12s",
                              }}>
                              {/* Note preview — each note rendered as a tiny bar.
                                  Position: x by note.start, width by note.duration,
                                  y by pitch (inverted because higher pitch = top).
                                  Color: track color tinted by velocity. */}
                              <div style={{
                                position:"absolute", top:3, left:4, right:4, height: previewH,
                                pointerEvents:"none",
                              }}>
                                {notes.map(function(n){
                                  const startFrac = Math.max(0, Math.min(1, (n.start || 0) / Math.max(0.001, dur)));
                                  const widthFrac = Math.max(0.005, Math.min(1 - startFrac, (n.duration || 0.1) / Math.max(0.001, dur)));
                                  const pitchFrac = (n.pitch - minPitch) / pitchSpan;
                                  // Invert Y so high pitches sit at the top.
                                  const yFrac = 1 - pitchFrac;
                                  const vel = n.velocity == null ? 0.8 : n.velocity;
                                  return (
                                    <div key={n.id || (n.pitch + "_" + n.start)}
                                      style={{
                                        position:"absolute",
                                        left: (startFrac * 100) + "%",
                                        width: (widthFrac * 100) + "%",
                                        top: Math.round(yFrac * (previewH - 2)),
                                        height: 2,
                                        background: trackColor,
                                        borderRadius: 1,
                                        opacity: Math.min(1, 0.5 + 0.4 * vel),
                                        boxShadow: "0 0 3px " + trackColor + "88",
                                      }}/>
                                  );
                                })}
                                {/* Empty-clip hint */}
                                {notes.length === 0 && (
                                  <div style={{
                                    position:"absolute", inset: 0,
                                    display:"flex", alignItems:"center", justifyContent:"center",
                                    color: trackColor + "88",
                                    fontSize: 8, fontWeight: 700,
                                    letterSpacing: 1.2, textTransform: "uppercase",
                                    pointerEvents:"none",
                                  }}>Double-tap to edit</div>
                                )}
                              </div>
                              <div style={{ position:"absolute", bottom:2, left:6, right: isSel ? 14 : 6, color:"rgba(255,255,255,0.85)", fontSize:7, fontWeight:700, pointerEvents:"none", textShadow:"0 1px 3px rgba(0,0,0,0.95)", overflow:"hidden", textOverflow:"ellipsis", whiteSpace:"nowrap" }}>
                                {clip.label || "MIDI"}{!isActv ? " (inactive)" : ""}
                              </div>
                              {isSel && <div style={{ position:"absolute", top:3, right:3, width:5, height:5, borderRadius:"50%", background:trackColor, pointerEvents:"none" }} />}
                            </div>
                          );
                        }
                        if (!clip.audioBuffer) return null;
                        const trimS   = clip.trimStart || 0;
                        const trimE   = clip.trimEnd !== undefined ? clip.trimEnd : (clip.audioBuffer ? clip.audioBuffer.duration : clip.duration || 2);
                        // Visual length: stretched mode uses stretchedDurationSec
                        // for the on-screen width (clip looks longer/shorter than
                        // its source buffer). Normal mode uses trim length.
                        const visualLen = (clip.stretchMode && clip.stretchedDurationSec > 0)
                          ? clip.stretchedDurationSec
                          : (trimE - trimS);
                        // No min-width clamp — actual duration drives width at
                        // all zooms so clips don't visually overlap their neighbors.
                        const clipW   = visualLen * effectivePPS;
                        const clipL   = Math.max(0, (clip.startTime || 0) * effectivePPS);
                        const playedF = Math.max(0, Math.min(1, (currentTime - (clip.startTime||0)) / (visualLen || 1)));
                        const isSel      = selectedClipId === clip.id;
                        const isActv     = clip.active !== false;
                        const isMultiSel = selClipIds.has(clip.id);
                        return (
                          <div
                            key={clip.id + "_body"}
                            data-clipid={clip.id}
                            onClick={function(e){ e.stopPropagation(); selectClip(clip.id); }}
                            onMouseDown={function(e){ handleRegionMouseDown(e, track, clip); }}
                            onTouchStart={function(e){ handleRegionTouchStart(e, track, clip); }}
                            onContextMenu={function(e){ handleRegionRightClick(e, track, clip); }}
                            style={{
                              position:"absolute", left:clipL, top:3, width:clipW, height:TRACK_H-6,
                              borderRadius:6, overflow:"hidden", touchAction:"none",
                              background: isActv ? "#111" : "#0a0a0a",
                              border: isMultiSel ? "2px solid #FACC15" : isSel ? "2px solid "+track.color : "1.5px solid "+(isActv?track.color+"66":track.color+"22"),
                              boxShadow: isMultiSel ? "0 0 10px rgba(250,204,21,0.4)" : isSel ? "inset 0 0 0 1px "+track.color+"33" : "none",
                              opacity: isActv ? 1 : 0.3,
                              cursor:"grab", zIndex: isMultiSel ? 3 : isActv ? 2 : 1,
                              transition:"border 0.12s, box-shadow 0.12s",
                            }}
                          >
                            <WaveformCanvas audioBuffer={clip.audioBuffer} color={track.color}
                              width={Math.max(1,Math.round(clipW))} height={TRACK_H-6}
                              playedFraction={playedF} dim={!isActv}
                              trimStart={trimS} trimEnd={trimE} />
                            {/* Fade-in overlay — semi-transparent triangle in
                                the top-left corner of the clip body, narrow
                                end at clip start. Shows the user how much
                                fade is applied without obstructing the waveform. */}
                            {(clip.fadeInSec > 0) && (function(){
                              const fW = Math.min(clipW, (clip.fadeInSec || 0) * effectivePPS);
                              if (fW < 2) return null;
                              return (
                                <svg style={{ position:"absolute", top:0, left:0, width:fW, height:TRACK_H-6, pointerEvents:"none", zIndex:3 }}
                                     viewBox={"0 0 " + fW + " " + (TRACK_H-6)} preserveAspectRatio="none">
                                  <polygon points={"0," + (TRACK_H-6) + " " + fW + ",0 0,0"}
                                    fill="rgba(0,0,0,0.5)" stroke="rgba(255,255,255,0.55)" strokeWidth="1" />
                                </svg>
                              );
                            })()}
                            {/* Fade-out overlay — same idea, mirror'd to top-right corner */}
                            {(clip.fadeOutSec > 0) && (function(){
                              const fW = Math.min(clipW, (clip.fadeOutSec || 0) * effectivePPS);
                              if (fW < 2) return null;
                              return (
                                <svg style={{ position:"absolute", top:0, right:0, width:fW, height:TRACK_H-6, pointerEvents:"none", zIndex:3 }}
                                     viewBox={"0 0 " + fW + " " + (TRACK_H-6)} preserveAspectRatio="none">
                                  <polygon points={"0,0 " + fW + ",0 " + fW + "," + (TRACK_H-6)}
                                    fill="rgba(0,0,0,0.5)" stroke="rgba(255,255,255,0.55)" strokeWidth="1" />
                                </svg>
                              );
                            })()}
                            {/* Stretch mode indicator — small pink badge in
                                top-left when stretchMode is on, so users know
                                the trim handles are stretch handles. */}
                            {clip.stretchMode && (
                              <div style={{
                                position: "absolute", top: 3, left: 3,
                                background: "rgba(244,114,182,0.85)",
                                color: "white",
                                fontSize: 7, fontWeight: 900,
                                letterSpacing: 0.6,
                                padding: "2px 5px",
                                borderRadius: 3,
                                pointerEvents: "none",
                                zIndex: 4,
                                boxShadow: "0 1px 3px rgba(0,0,0,0.5)",
                              }}>STRETCH</div>
                            )}
                            <div style={{ position:"absolute", bottom:2, left:6, right:isSel?14:6, color:"rgba(255,255,255,0.85)", fontSize:7, fontWeight:700, pointerEvents:"none", textShadow:"0 1px 3px rgba(0,0,0,0.95)", overflow:"hidden", textOverflow:"ellipsis", whiteSpace:"nowrap" }}>
                              {clip.label}{!isActv?" (inactive)":""}
                            </div>
                            {isSel&&<div style={{ position:"absolute", top:3, right:3, width:5, height:5, borderRadius:"50%", background:track.color, pointerEvents:"none" }} />}
                          </div>
                        );
                      })}
                    </div>{/* end inner mask */}

                    {/* ── TRIM HANDLES ── */}
                    {clips.map(function(clip){
                      if (!clip.audioBuffer) return null;
                      const trimS  = clip.trimStart || 0;
                      const trimE  = clip.trimEnd !== undefined ? clip.trimEnd : (clip.audioBuffer ? clip.audioBuffer.duration : clip.duration || 2);
                      // Visual width matches the body's: stretched mode uses
                      // stretchedDurationSec so handles sit at the visual edges.
                      const visualLen = (clip.stretchMode && clip.stretchedDurationSec > 0)
                        ? clip.stretchedDurationSec
                        : (trimE - trimS);
                      // No min-width clamp — actual duration drives width.
                      const clipW  = visualLen * effectivePPS;
                      const clipL  = Math.max(0, (clip.startTime || 0) * effectivePPS);
                      const bufDur = clip.audioBuffer ? clip.audioBuffer.duration : (clip.duration || 2);
                      const pps    = effectivePPS;

                      // Shared trim start: set ref so scroll container lets touchmove through
                      const startTrim = function() { trimActiveRef.current = true; };
                      const endTrim   = function() { trimActiveRef.current = false; };

                      return (
                        <div key={clip.id+"_handles"} style={{ position:"absolute", left:clipL, top:0, width:clipW, height:TRACK_H, pointerEvents:"none", zIndex:20 }}>

                          {/* LEFT handle */}
                          <div data-trimhandle="left"
                            style={{ position:"absolute", left:-22, top:0, bottom:0, width:26,
                              cursor:"col-resize", pointerEvents:"all", touchAction:"none",
                              display:"flex", alignItems:"center", justifyContent:"flex-end" }}
                            onMouseDown={function(e){
                              e.stopPropagation(); e.preventDefault();
                              startTrim();
                              beginTracksGesture();
                              const x0=e.clientX, ts0=trimS, cs0=clip.startTime||0;
                              const mv=function(me){
                                const d=(me.clientX-x0)/pps;
                                const ts=Math.max(0,Math.min(ts0+d,trimE-0.05));
                                updateClip(track.id,clip.id,{trimStart:+ts.toFixed(4),startTime:Math.max(0,+(cs0+(ts-ts0)).toFixed(4))});
                              };
                              const up=function(){ endTrim(); endTracksGesture(); document.removeEventListener("mousemove",mv); document.removeEventListener("mouseup",up); };
                              document.addEventListener("mousemove",mv); document.addEventListener("mouseup",up);
                            }}
                            onTouchStart={function(e){
                              e.stopPropagation(); e.preventDefault();
                              startTrim();
                              beginTracksGesture();
                              const x0=e.touches[0].clientX, ts0=trimS, cs0=clip.startTime||0;
                              const mv=function(ev){
                                ev.preventDefault();
                                const d=(ev.touches[0].clientX-x0)/pps;
                                const ts=Math.max(0,Math.min(ts0+d,trimE-0.05));
                                updateClip(track.id,clip.id,{trimStart:+ts.toFixed(4),startTime:Math.max(0,+(cs0+(ts-ts0)).toFixed(4))});
                              };
                              const up=function(){ endTrim(); endTracksGesture(); document.removeEventListener("touchmove",mv); document.removeEventListener("touchend",up); };
                              document.addEventListener("touchmove",mv,{passive:false});
                              document.addEventListener("touchend",up,{passive:true});
                            }}
                          >
                            <div style={{ width:6, height:TRACK_H*0.75, borderRadius:"3px 0 0 3px",
                              background: clip.stretchMode ? "#F472B644" : track.color,
                              boxShadow: clip.stretchMode ? "0 0 6px rgba(244,114,182,0.3)" : "0 0 10px "+track.color }} />
                          </div>

                          {/* RIGHT handle. In stretch mode, dragging adjusts
                              clip.stretchedDurationSec — SoundTouch then
                              renders a stretched buffer on release. In
                              normal mode, dragging adjusts trimEnd as
                              before. The handle's visual changes color
                              when stretch mode is on so the user can see
                              which mode they're in. */}
                          <div data-trimhandle="right"
                            style={{ position:"absolute", right:-22, top:0, bottom:0, width:26,
                              cursor:"col-resize", pointerEvents:"all", touchAction:"none",
                              display:"flex", alignItems:"center", justifyContent:"flex-start" }}
                            onMouseDown={function(e){
                              e.stopPropagation(); e.preventDefault();
                              startTrim();
                              beginTracksGesture();
                              const stretch = !!clip.stretchMode;
                              const x0=e.clientX;
                              const te0 = trimE;
                              const sd0 = clip.stretchedDurationSec || (trimE - trimS);
                              // Track the latest stretch target locally so the
                              // up handler doesn't depend on tracksRef having
                              // committed in time. Last value mv sets is the
                              // final value to render.
                              let latestStretched = sd0;
                              const mv=function(me){
                                if (stretch) {
                                  const origDur = clip.audioBuffer ? (trimE - trimS) : sd0;
                                  const newSd = Math.max(0.1, Math.min(origDur * 4, sd0 + (me.clientX-x0)/pps));
                                  latestStretched = newSd;
                                  updateClip(track.id, clip.id, { stretchedDurationSec: +newSd.toFixed(4) });
                                } else {
                                  const te=Math.max(trimS+0.05,Math.min(te0+(me.clientX-x0)/pps,bufDur));
                                  updateClip(track.id,clip.id,{trimEnd:+te.toFixed(4)});
                                }
                              };
                              const up=function(){
                                endTrim(); endTracksGesture();
                                document.removeEventListener("mousemove",mv); document.removeEventListener("mouseup",up);
                                if (stretch && clip.audioBuffer) {
                                  const ctx = actxRef.current;
                                  const targetDur = +latestStretched.toFixed(4);
                                  if (ctx && targetDur > 0) {
                                    const skey = clip.id + ":" + Math.round(targetDur * 1000);
                                    if (!clipStretchedBuffersRef.current[skey]) {
                                      stretchAudioBufferOffline(ctx, clip.audioBuffer, targetDur).then(function(rendered){
                                        if (rendered) clipStretchedBuffersRef.current[skey] = rendered;
                                      }).catch(function(e){ console.warn("[stretch] render failed", e); });
                                    }
                                  }
                                }
                              };
                              document.addEventListener("mousemove",mv); document.addEventListener("mouseup",up);
                            }}
                            onTouchStart={function(e){
                              e.stopPropagation(); e.preventDefault();
                              startTrim();
                              beginTracksGesture();
                              const stretch = !!clip.stretchMode;
                              const x0=e.touches[0].clientX;
                              const te0 = trimE;
                              const sd0 = clip.stretchedDurationSec || (trimE - trimS);
                              let latestStretched = sd0;
                              const mv=function(ev){
                                ev.preventDefault();
                                if (stretch) {
                                  const origDur = clip.audioBuffer ? (trimE - trimS) : sd0;
                                  const newSd = Math.max(0.1, Math.min(origDur * 4, sd0 + (ev.touches[0].clientX-x0)/pps));
                                  latestStretched = newSd;
                                  updateClip(track.id, clip.id, { stretchedDurationSec: +newSd.toFixed(4) });
                                } else {
                                  const newTE=Math.max(trimS+0.05,Math.min(te0+(ev.touches[0].clientX-x0)/pps,bufDur));
                                  updateClip(track.id,clip.id,{trimEnd:+newTE.toFixed(4)});
                                }
                              };
                              const up=function(){
                                endTrim(); endTracksGesture();
                                document.removeEventListener("touchmove",mv); document.removeEventListener("touchend",up);
                                if (stretch && clip.audioBuffer) {
                                  const ctx = actxRef.current;
                                  const targetDur = +latestStretched.toFixed(4);
                                  if (ctx && targetDur > 0) {
                                    const skey = clip.id + ":" + Math.round(targetDur * 1000);
                                    if (!clipStretchedBuffersRef.current[skey]) {
                                      stretchAudioBufferOffline(ctx, clip.audioBuffer, targetDur).then(function(rendered){
                                        if (rendered) clipStretchedBuffersRef.current[skey] = rendered;
                                      }).catch(function(e){ console.warn("[stretch] render failed", e); });
                                    }
                                  }
                                }
                              };
                              document.addEventListener("touchmove",mv,{passive:false});
                              document.addEventListener("touchend",up,{passive:true});
                            }}
                          >
                            <div style={{ width:6, height:TRACK_H*0.75, borderRadius:"0 3px 3px 0",
                              background: clip.stretchMode ? "#F472B6" : track.color,
                              boxShadow: clip.stretchMode ? "0 0 12px #F472B6" : "0 0 10px "+track.color }} />
                          </div>

                        </div>
                      );
                    })}

                  </div>{/* end lane outer */}
                </div>
              );
            })}

            {/* Add track lane filler row — button is in the sidebar */}
            <div style={{ height:48, borderBottom:"1px solid #111", background:"#090909" }} />

          </div>{/* end inner */}
          </div>{/* end right scroll area */}
        </div>{/* end two-column flex */}

      {/* ── Lasso selection box overlay — inside DAW wrapper so coords match ── */}
      {selBox && selBox.w > 4 && selBox.h > 4 && (
        <div style={{
          position:"absolute",
          left: SIDEBAR_W + selBox.x - (scrollRef.current ? scrollRef.current.scrollLeft : 0),
          top:  selBox.y,
          width: selBox.w,
          height: selBox.h,
          border: "2px solid #FACC15",
          background: "rgba(250,204,21,0.08)",
          borderRadius: 4,
          pointerEvents: "none",
          zIndex: 500,
        }} />
      )}
      </div>{/* end DAW layout */}

      {/* ── Multi-select action bar ──────────────────────────────────────────── */}
      {selClipIds.size > 0 && (
        <div style={{
          position:"absolute", bottom: 90, left:"50%", transform:"translateX(-50%)",
          zIndex: 600,
          background:"#1c1c1c", border:"1px solid #333",
          borderRadius:20, padding:"10px 18px",
          display:"flex", alignItems:"center", gap:12,
          boxShadow:"0 8px 32px rgba(0,0,0,0.9)",
        }}>
          <span style={{ color:"#FACC15", fontSize:12, fontWeight:700 }}>
            {selClipIds.size} clip{selClipIds.size > 1 ? "s" : ""} selected
          </span>
          <div style={{ width:1, height:20, background:"#333" }} />
          <button
            onClick={duplicateSelectedClips}
            style={{ background:"rgba(250,204,21,0.1)", border:"1px solid rgba(250,204,21,0.3)", borderRadius:10, color:"#FACC15", fontSize:13, fontWeight:700, padding:"8px 14px", cursor:"pointer" }}
          >⧉ Duplicate</button>
          <button
            onClick={function(){
              // Copy all selected clips to the in-app clipboard. Each clip
              // remembers ITS OWN source track so Paste can land each one
              // back on the right lane (preserving the user's multi-track
              // groove). We also remember the earliest startTime — Paste
              // anchors that earliest clip at the current playhead and
              // shifts the others by the same offset, preserving the
              // RELATIVE timing of the group.
              const snapshots = [];
              let earliestStart = Infinity;
              tracksRef.current.forEach(function(t){
                (t.clips || []).forEach(function(cl){
                  if (!selClipIds.has(cl.id)) return;
                  const startT = cl.startTime || 0;
                  if (startT < earliestStart) earliestStart = startT;
                  // Shallow copy preserves audioBuffer ref (non-serializable
                  // — JSON-stringify would drop it and produce empty shells).
                  const snap = { ...cl, __sourceTrackId: t.id };
                  snapshots.push(snap);
                });
              });
              if (snapshots.length > 0) {
                if (earliestStart === Infinity) earliestStart = 0;
                setClipboardClips({
                  // Anchor: the timestamp from which relative offsets are
                  // measured. On paste we set the FIRST clip's startTime
                  // to (playhead + clip.startTime - anchorStart).
                  anchorStart: earliestStart,
                  clips: snapshots,
                });
                try { if (navigator.vibrate) navigator.vibrate(10); } catch(_){}
              }
            }}
            style={{ background:"rgba(59,130,246,0.1)", border:"1px solid rgba(59,130,246,0.3)", borderRadius:10, color:"#3B82F6", fontSize:13, fontWeight:700, padding:"8px 14px", cursor:"pointer" }}
          >⎘ Copy</button>
          <button
            onClick={deleteSelectedClips}
            style={{ background:"rgba(239,68,68,0.1)", border:"1px solid rgba(239,68,68,0.3)", borderRadius:10, color:"#EF4444", fontSize:13, fontWeight:700, padding:"8px 14px", cursor:"pointer" }}
          >⌫ Delete</button>
          <button
            onClick={function(){ setSelClipIds(new Set()); setSelBox(null); }}
            style={{ background:"none", border:"none", color:"#555", fontSize:18, cursor:"pointer", padding:"0 4px", lineHeight:1 }}
          >✕</button>
        </div>
      )}

      {/* Add menu popup */}
      {showAddMenu&&(
        <div style={{
          position:"absolute",bottom:90,left:12,zIndex:400,
          background:"linear-gradient(180deg, #1a1530 0%, #14102a 100%)",
          border:"1px solid #2a2050",
          borderRadius:14,overflow:"hidden",
          minWidth:260, maxWidth:300,
          boxShadow:"0 12px 36px rgba(0,0,0,0.85), 0 0 0 1px rgba(167,139,250,0.06)",
        }} onClick={function(e){ e.stopPropagation(); }}>
          {/* Section header */}
          <div style={{
            padding:"10px 14px 8px",
            background:"rgba(167,139,250,0.04)",
            borderBottom:"1px solid #221a40",
            display:"flex", alignItems:"center", gap:8,
          }}>
            <div style={{
              width:6, height:6, borderRadius:"50%",
              background:"#a78bfa",
              boxShadow:"0 0 6px rgba(167,139,250,0.8)",
            }}/>
            <span style={{
              fontSize:9, fontWeight:800, color:"#a78bfa",
              letterSpacing:1.8, textTransform:"uppercase",
            }}>Add Track</span>
          </div>

          {(function(){
            // Shared row component to keep every item visually consistent.
            const Row = function(p){
              const accent = p.accent || "#a78bfa";
              return (
                <button onClick={p.onClick} style={{
                  width:"100%",
                  background:"none", border:"none",
                  padding:"11px 14px",
                  textAlign:"left",
                  cursor:"pointer",
                  display:"flex", alignItems:"center", gap:12,
                  borderTop: p.first ? "none" : "1px solid rgba(167,139,250,0.06)",
                }}
                  onPointerEnter={function(e){ e.currentTarget.style.background = "rgba(167,139,250,0.08)"; }}
                  onPointerLeave={function(e){ e.currentTarget.style.background = "transparent"; }}>
                  <div style={{
                    width:34, height:34, borderRadius:9,
                    background: "linear-gradient(180deg, " + accent + "20, " + accent + "10)",
                    border: "1px solid " + accent + "55",
                    display:"flex", alignItems:"center", justifyContent:"center",
                    color: accent,
                    flexShrink:0,
                    boxShadow: "0 0 10px " + accent + "25, 0 0 0 1px rgba(0,0,0,0.15) inset",
                  }}>{p.icon}</div>
                  <div style={{ flex:1, minWidth:0 }}>
                    <div style={{ fontSize:13, fontWeight:700, color:"#e8e6f0", lineHeight:1.2, display:"flex", alignItems:"center", gap:6 }}>
                      {p.label}
                      {p.badge && (
                        <span style={{
                          fontSize:8, fontWeight:800, color: accent,
                          background: accent + "20",
                          border: "1px solid " + accent + "55",
                          padding:"1px 5px", borderRadius:4,
                          letterSpacing:0.8,
                        }}>{p.badge}</span>
                      )}
                    </div>
                    <div style={{ fontSize:10, color:"#7a6ea0", marginTop:2, lineHeight:1.3 }}>{p.hint}</div>
                  </div>
                </button>
              );
            };

            const ICON = {
              upload: (<svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><path d="M21 15v4a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2v-4"/><polyline points="17 8 12 3 7 8"/><line x1="12" y1="3" x2="12" y2="15"/></svg>),
              mic:    (<svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><path d="M12 1a3 3 0 0 0-3 3v8a3 3 0 0 0 6 0V4a3 3 0 0 0-3-3z"/><path d="M19 10v2a7 7 0 0 1-14 0v-2"/><line x1="12" y1="19" x2="12" y2="23"/><line x1="8" y1="23" x2="16" y2="23"/></svg>),
              drums:  (<svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><rect x="3" y="3" width="7" height="7" rx="1"/><rect x="14" y="3" width="7" height="7" rx="1"/><rect x="3" y="14" width="7" height="7" rx="1"/><rect x="14" y="14" width="7" height="7" rx="1"/></svg>),
              piano:  (<svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><rect x="3" y="5" width="18" height="14" rx="1"/><line x1="9" y1="5" x2="9" y2="14"/><line x1="15" y1="5" x2="15" y2="14"/></svg>),
              slice:  (<svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><circle cx="6" cy="6" r="3"/><circle cx="6" cy="18" r="3"/><line x1="20" y1="4" x2="8.12" y2="15.88"/><line x1="14.47" y1="14.48" x2="20" y2="20"/><line x1="8.12" y1="8.12" x2="12" y2="12"/></svg>),
            };

            // Upload Audio uses a <label> for the hidden file input;
            // emulate the Row shape inline so the file picker triggers.
            return (
              <>
                <label style={{ display:"block" }}>
                  <div onClick={function(){}} // hover handled by inner styles below
                    style={{
                      padding:"11px 14px",
                      cursor:"pointer",
                      display:"flex", alignItems:"center", gap:12,
                    }}
                    onPointerEnter={function(e){ e.currentTarget.style.background = "rgba(167,139,250,0.08)"; }}
                    onPointerLeave={function(e){ e.currentTarget.style.background = "transparent"; }}>
                    <div style={{
                      width:34, height:34, borderRadius:9,
                      background:"linear-gradient(180deg, #3b82f620, #3b82f610)",
                      border:"1px solid #3b82f655",
                      display:"flex", alignItems:"center", justifyContent:"center",
                      color:"#3b82f6", flexShrink:0,
                      boxShadow:"0 0 10px #3b82f625, 0 0 0 1px rgba(0,0,0,0.15) inset",
                    }}>{ICON.upload}</div>
                    <div style={{ flex:1, minWidth:0 }}>
                      <div style={{ fontSize:13, fontWeight:700, color:"#e8e6f0", lineHeight:1.2 }}>Upload Audio</div>
                      <div style={{ fontSize:10, color:"#7a6ea0", marginTop:2, lineHeight:1.3 }}>Beat, vocal, any audio file</div>
                    </div>
                  </div>
                  <input type="file" accept=".mp3,.wav,.m4a,.aac,.ogg,.flac,.aiff,.opus" multiple onChange={function(e){handleFileUpload(e,"beat");}} style={{ display:"none" }} />
                </label>

                <Row
                  icon={ICON.mic} label="Record Vocals" hint="Record a new take" accent="#ef4444"
                  onClick={function(){
                    const newId = Date.now() + Math.random();
                    const vocalN = tracks.filter(function(t){return t.type==="vocal";}).length + 1;
                    addTrackObj({ id:newId, name:"Vocal "+vocalN, type:"vocal", isMuted:false, isSoloed:false, clips:[], color:VOCAL_COLORS[vocalN % VOCAL_COLORS.length] });
                    setSelectedTrackId(newId);
                    setShowAddMenu(false);
                    if (isPlayingRef.current) {
                      setTimeout(function() {
                        applyGains(tracksRef.current);
                        if (masterGainRef.current) masterGainRef.current.gain.value = masterVolume;
                      }, 0);
                    }
                  }}/>

                <Row
                  icon={ICON.drums} label="Drum Sequencer" hint="Step grid · build beats in studio" accent="#c026d3"
                  onClick={function(){
                    const newId = Date.now() + Math.random();
                    const drumN = tracks.filter(function(t){return t.type==="drumSequencer";}).length + 1;
                    const kitMeta = getKitMeta("default");
                    const initialPattern = buildEmptyDrumPattern(kitMeta, "Pattern 1");
                    addTrackObj({
                      id: newId, name: "Drums " + drumN, type: "drumSequencer",
                      kitId: "default", patterns: [initialPattern], clips: [],
                      isMuted: false, isSoloed: false, color: "#C026D3",
                    });
                    setSelectedTrackId(newId);
                    setDrumActivePatternByTrack(function(prev){ return { ...prev, [newId]: initialPattern.id }; });
                    setDrumRackTrackId(newId);
                    setShowAddMenu(false);
                    try {
                      const ctx = actxRef.current;
                      if (ctx) {
                        loadKitBuffers(ctx, "default").then(function(bufs){
                          drumKitBuffersRef.current["default"] = bufs;
                        }).catch(function(e){ console.warn("[drum] kit preload failed:", e); });
                      }
                    } catch(e) {}
                  }}/>

                <Row
                  icon={ICON.piano} label="MIDI Instrument" hint="Compose melodies, chords & instruments" accent="#a78bfa"
                  onClick={function(){
                    const newId = Date.now() + Math.random();
                    const midiN = tracks.filter(function(t){return t.type==="midi";}).length + 1;
                    const beatSec = 60 / (bpm || 120);
                    const barSec = beatSec * (timeSigNum || 4);
                    const clipDur = barSec * 4;
                    const initialClip = {
                      id: "mc_" + Date.now() + "_" + Math.floor(Math.random()*10000),
                      type: "midi", startTime: 0, duration: clipDur, notes: [],
                      loopEnabled: false, patternDuration: clipDur, active: true,
                      label: "MIDI " + midiN,
                    };
                    addTrackObj({
                      id: newId, name: "Instrument " + midiN, type: "midi",
                      instrumentId: "piano-grand", clips: [initialClip],
                      isMuted: false, isSoloed: false, color: "#A78BFA",
                    });
                    setSelectedTrackId(newId);
                    setShowAddMenu(false);
                    if (isPlayingRef.current) {
                      setTimeout(function() {
                        applyGains(tracksRef.current);
                        if (masterGainRef.current) masterGainRef.current.gain.value = masterVolume;
                      }, 0);
                    }
                  }}/>

                <Row
                  icon={ICON.slice} label="Sampler" hint="Chop a sample · play slices as MIDI notes" accent="#22d3ee" badge="NEW"
                  onClick={function(){
                    setSamplerImportOpen(true);
                    setShowAddMenu(false);
                  }}/>
              </>
            );
          })()}
        </div>
      )}

      {/* ══ MANAGE KITS OVERLAY (admin) ═══════════════════════════════════════
          Full-screen modal for creating/editing/deleting drum kits.
          Visible only when user is admin AND has opened it via the Manage
          Kits button in the rack header. After save, the catalogue is
          refetched so the kit picker reflects the new state immediately. */}
      {manageKitsOverlay && (
        <ManageKitsOverlay
          apiBase={API_BASE}
          adminKits={adminKits}
          onReload={reloadAdminKits}
          onClose={function(){ setManageKitsOverlay(null); }}
          isAdmin={!!(user && (user.is_admin === true || user.username === "Trelli"))}
          getToken={getToken}
        />
      )}

      {/* ══ MANAGE SOUNDFONTS OVERLAY (admin, Phase 7) ════════════════════════
          Full-screen modal for uploading/editing/deleting custom soundfonts.
          Each upload goes to Backblaze B2; metadata lives in MongoDB. After
          save/delete, the soundfont catalogue is refetched so the preset
          browser's Instruments category reflects the change immediately. */}
      {manageSoundfontsOverlay && (
        <AdminSoundfontsOverlay
          apiBase={API_BASE}
          soundfonts={customSoundfonts}
          onReload={reloadCustomSoundfonts}
          onClose={function(){ setManageSoundfontsOverlay(null); }}
          isAdmin={!!(user && (user.is_admin === true || user.username === "Trelli"))}
          getToken={getToken}
        />
      )}

      {/* ══ SAMPLER IMPORT OVERLAY ════════════════════════════════════════════
          Triggered from the + menu's Sampler tile. Picks a file, decodes
          it, auto-slices via transient detection, lets the user tweak
          settings, then uploads (with content-hash dedup so the same
          file across users only takes ONE storage slot) and creates a
          new sampler track wired to that sample. */}
      {samplerImportOpen && (
        <SamplerImportOverlay
          apiBase={API_BASE}
          getToken={getToken}
          projectBpm={bpm || 120}
          onClose={function(){ setSamplerImportOpen(false); }}
          onCreateTrack={function(samplerData){
            const newId = Date.now() + Math.random();
            const samplerN = tracks.filter(function(t){
              return t.type === "midi" && t.samplerData;
            }).length + 1;
            // Initial empty MIDI clip so the user immediately has something
            // to double-tap and edit. Length matches the sample duration
            // rounded up to the next beat.
            const beatSec = 60 / (bpm || 120);
            const clipBeats = Math.max(1, Math.ceil(samplerData.durationSec / beatSec));
            const clipDur = clipBeats * beatSec;
            const initialClip = {
              id: "clip-" + Date.now(),
              startTime: 0,
              duration: clipDur,
              notes: [],
              loopEnabled: false,
              patternDuration: clipDur,
              active: true,
              label: "Sampler " + samplerN,
            };
            addTrackObj({
              id: newId,
              name: "Sampler " + samplerN,
              type: "midi",
              instrumentId: "sampler:" + samplerData.sampleHash,
              samplerData: samplerData,
              clips: [initialClip],
              isMuted: false,
              isSoloed: false,
              color: "#22D3EE",  // signature cyan for sampler tracks
            });
            setSelectedTrackId(newId);
          }}
        />
      )}

      {/* ══ SAMPLER RE-EDIT MODE ═════════════════════════════════════════════
          Same overlay component, but in "edit existing" mode. Loads the
          track's existing sample from B2 and pre-populates all controls
          (slices, root pitch, play mode, pitch shift, tempo lock). Saving
          merges the new samplerData back into the track without
          re-uploading. Opened from the ✂ button in the piano-roll
          toolbar of a sampler track. */}
      {samplerEditorTrackId !== null && (function(){
        const editTrack = tracks.find(function(t){ return t.id === samplerEditorTrackId; });
        if (!editTrack || !editTrack.samplerData) {
          // The track went away (deleted while editor was open). Clean up.
          setTimeout(function(){ setSamplerEditorTrackId(null); }, 0);
          return null;
        }
        return (
          <SamplerImportOverlay
            apiBase={API_BASE}
            getToken={getToken}
            projectBpm={bpm || 120}
            existingTrack={editTrack}
            onClose={function(){ setSamplerEditorTrackId(null); }}
            onUpdateTrack={function(newSamplerData){
              // Merge updated samplerData into the track. The instrumentId
              // stays the same (sample is the same), only slice/pitch/tempo
              // change. Clear any cached buffer for the OLD playbackRate?
              // No need — playbackRate is consulted at voice-build time,
              // so the next note picks up the new rate automatically.
              setTracks(function(prev){
                return prev.map(function(t){
                  if (t.id !== samplerEditorTrackId) return t;
                  return Object.assign({}, t, { samplerData: newSamplerData });
                });
              });
            }}
          />
        );
      })()}

      {/* ══ SOUNDFONT LOADING TOAST ═══════════════════════════════════════════
          Sleek top-of-screen pill that surfaces immediately when a soundfont
          preset begins downloading. Custom dual-stripe animated loader:
          two violet light beams slide left→right at different speeds with
          a phase offset, creating a continuous flowing-energy effect. The
          keyframes are injected inline RIGHT NEXT to the component (not
          relying on any global CSS) and use unique class names (sfTL-*)
          to guarantee they cannot be killed by other styles. The toast
          disappears the moment the preset is ready. */}
      {(loadingPresetName || loadingSampleName) && (function(){
        // Decide what to show: prefer the more-recently-started load.
        // (We display whichever is non-null; if both are loading, show
        // the preset since that's typically the bigger / slower one.)
        const showingSample = !loadingPresetName && loadingSampleName;
        const label = showingSample ? "Sample" : "Loading";
        const name  = loadingPresetName || loadingSampleName;
        // Cyan accent for samples (matches the sampler's signature
        // color), violet for presets. Stripe colors switch with the
        // accent so the toast feels native to whichever is loading.
        const accent     = showingSample ? "#22D3EE" : "#A78BFA";
        const accentDim  = showingSample ? "rgba(34,211,238,0.30)" : "rgba(167,139,250,0.30)";
        const accentSoft = showingSample ? "rgba(34,211,238,0.25)" : "rgba(167,139,250,0.25)";
        const stripeA    = showingSample
          ? "linear-gradient(90deg, rgba(34,211,238,0) 0%, rgba(34,211,238,0.65) 35%, rgba(165,243,252,1) 50%, rgba(34,211,238,0.65) 65%, rgba(34,211,238,0) 100%)"
          : "linear-gradient(90deg, rgba(167,139,250,0) 0%, rgba(167,139,250,0.65) 35%, rgba(196,181,253,1) 50%, rgba(167,139,250,0.65) 65%, rgba(167,139,250,0) 100%)";
        const stripeAShadow = showingSample ? "0 0 14px rgba(34,211,238,0.7)" : "0 0 14px rgba(167,139,250,0.7)";
        const stripeB    = showingSample
          ? "linear-gradient(90deg, rgba(165,243,252,0) 0%, rgba(165,243,252,0.5) 50%, rgba(165,243,252,0) 100%)"
          : "linear-gradient(90deg, rgba(196,181,253,0) 0%, rgba(196,181,253,0.5) 50%, rgba(196,181,253,0) 100%)";
        const stripeBShadow = showingSample ? "0 0 10px rgba(165,243,252,0.6)" : "0 0 10px rgba(196,181,253,0.6)";
        return (
        <>
          <style>{
            "@keyframes sfTLflowA {"
            + "0% { transform: translateX(-100%); }"
            + "100% { transform: translateX(380%); }"
            + "}"
            + "@keyframes sfTLflowB {"
            + "0% { transform: translateX(-160%); }"
            + "100% { transform: translateX(340%); }"
            + "}"
            + "@keyframes sfTLpulse {"
            + "0%,100% { opacity: 0.55; }"
            + "50% { opacity: 1; }"
            + "}"
            + "@keyframes sfTLslideIn {"
            + "from { opacity: 0; transform: translate(-50%, -10px); }"
            + "to { opacity: 1; transform: translate(-50%, 0); }"
            + "}"
            + ".sfTL-track {"
            + "position: relative;"
            + "height: 6px;"
            + "width: 100%;"
            + "background: rgba(167,139,250,0.08);"
            + "border-radius: 6px;"
            + "overflow: hidden;"
            + "box-shadow: inset 0 1px 2px rgba(0,0,0,0.5);"
            + "}"
            + ".sfTL-stripeA {"
            + "position: absolute;"
            + "top: 0; bottom: 0;"
            + "left: 0;"
            + "width: 30%;"
            + "border-radius: 6px;"
            + "background: linear-gradient(90deg, rgba(167,139,250,0) 0%, rgba(167,139,250,0.65) 35%, rgba(196,181,253,1) 50%, rgba(167,139,250,0.65) 65%, rgba(167,139,250,0) 100%);"
            + "box-shadow: 0 0 14px rgba(167,139,250,0.7);"
            + "animation: sfTLflowA 1.6s linear infinite, sfTLpulse 1.6s ease-in-out infinite;"
            + "will-change: transform;"
            + "}"
            + ".sfTL-stripeB {"
            + "position: absolute;"
            + "top: 0; bottom: 0;"
            + "left: 0;"
            + "width: 22%;"
            + "border-radius: 6px;"
            + "background: linear-gradient(90deg, rgba(196,181,253,0) 0%, rgba(196,181,253,0.5) 50%, rgba(196,181,253,0) 100%);"
            + "box-shadow: 0 0 10px rgba(196,181,253,0.6);"
            + "animation: sfTLflowB 2.2s linear infinite;"
            + "animation-delay: 0.4s;"
            + "will-change: transform;"
            + "}"
            // Cyan variants for sampler loads — same shape, sampler-signature
            // color. Applied by swapping the class name based on which kind
            // of load is in progress.
            + ".sfTL-stripeA-sample {"
            + "position: absolute;"
            + "top: 0; bottom: 0;"
            + "left: 0;"
            + "width: 30%;"
            + "border-radius: 6px;"
            + "background: linear-gradient(90deg, rgba(34,211,238,0) 0%, rgba(34,211,238,0.65) 35%, rgba(165,243,252,1) 50%, rgba(34,211,238,0.65) 65%, rgba(34,211,238,0) 100%);"
            + "box-shadow: 0 0 14px rgba(34,211,238,0.7);"
            + "animation: sfTLflowA 1.6s linear infinite, sfTLpulse 1.6s ease-in-out infinite;"
            + "will-change: transform;"
            + "}"
            + ".sfTL-stripeB-sample {"
            + "position: absolute;"
            + "top: 0; bottom: 0;"
            + "left: 0;"
            + "width: 22%;"
            + "border-radius: 6px;"
            + "background: linear-gradient(90deg, rgba(165,243,252,0) 0%, rgba(165,243,252,0.5) 50%, rgba(165,243,252,0) 100%);"
            + "box-shadow: 0 0 10px rgba(165,243,252,0.6);"
            + "animation: sfTLflowB 2.2s linear infinite;"
            + "animation-delay: 0.4s;"
            + "will-change: transform;"
            + "}"
          }</style>
          <div style={{
            position: "fixed",
            top: "calc(env(safe-area-inset-top) + 14px)",
            left: "50%",
            transform: "translateX(-50%)",
            zIndex: 11000,
            minWidth: 240, maxWidth: "82vw",
            background: "linear-gradient(180deg, rgba(28,20,42,0.96) 0%, rgba(18,14,28,0.96) 100%)",
            backdropFilter: "blur(20px) saturate(140%)",
            WebkitBackdropFilter: "blur(20px) saturate(140%)",
            color: "#F8F7FA",
            fontFamily: "'DM Sans',sans-serif",
            padding: "12px 18px 12px",
            borderRadius: 14,
            border: "1px solid " + accentDim,
            boxShadow:
              "0 12px 32px rgba(7,6,17,0.5), " +
              "0 0 0 1px rgba(167,139,250,0.08), " +
              "inset 0 1px 0 rgba(255,255,255,0.06)",
            pointerEvents: "none",
            animation: "sfTLslideIn 0.22s cubic-bezier(0.22,1,0.36,1) both",
          }}>
            {/* Title row */}
            <div style={{
              display: "flex", alignItems: "center", gap: 8,
              marginBottom: 8,
            }}>
              {/* Label badge */}
              <div style={{
                fontSize: 8, fontWeight: 800, letterSpacing: 1.6,
                color: accent,
                textTransform: "uppercase",
                opacity: 0.85,
              }}>{label}</div>
              <div style={{
                flex: 1, height: 1,
                background: "linear-gradient(90deg, " + accentSoft + ", transparent)",
              }} />
            </div>
            {/* Preset / sample name */}
            <div style={{
              fontSize: 14, fontWeight: 700, color: "#F8F7FA",
              letterSpacing: 0.1, lineHeight: 1.2,
              marginBottom: 4,
              whiteSpace: "nowrap", overflow: "hidden", textOverflow: "ellipsis",
            }}>{name}</div>
            {/* Patience hint */}
            <div style={{
              fontSize: 10.5, color: "#9087A8",
              marginBottom: 10,
              letterSpacing: 0.1,
            }}>Please be patient…</div>
            {/* Dual-stripe animated loader */}
            <div className="sfTL-track">
              <div className={showingSample ? "sfTL-stripeA-sample" : "sfTL-stripeA"} />
              <div className={showingSample ? "sfTL-stripeB-sample" : "sfTL-stripeB"} />
            </div>
          </div>
        </>
        );
      })()}

      {/* Soundfont ERROR toast — surfaces _lastError from the engine
          so users can see WHY a load failed (B2 quota hit, CORS, etc.).
          Auto-dismisses after 8 seconds, or on tap. Sits below the
          status bar in the same lane as the loading toast. */}
      {sfErrorToast && (
        <div
          onClick={function(){ setSfErrorToast(null); }}
          style={{
            position: "fixed",
            top: "calc(env(safe-area-inset-top) + 14px)",
            left: "50%",
            transform: "translateX(-50%)",
            zIndex: 11001,
            minWidth: 280, maxWidth: "88vw",
            background: "linear-gradient(180deg, rgba(48,18,18,0.97) 0%, rgba(28,10,10,0.97) 100%)",
            backdropFilter: "blur(20px) saturate(140%)",
            WebkitBackdropFilter: "blur(20px) saturate(140%)",
            color: "#F8F7FA",
            fontFamily: "'DM Sans',sans-serif",
            padding: "12px 18px",
            borderRadius: 14,
            border: "1px solid rgba(239,68,68,0.40)",
            boxShadow: "0 12px 32px rgba(7,6,17,0.6), 0 0 0 1px rgba(239,68,68,0.10)",
            cursor: "pointer",
          }}>
          <div style={{
            fontSize: 8, fontWeight: 800, letterSpacing: 1.6,
            color: "#FCA5A5", textTransform: "uppercase", marginBottom: 6,
          }}>Soundfont Load Failed</div>
          <div style={{
            fontSize: 13, fontWeight: 700, color: "#FFFFFF", marginBottom: 4,
            whiteSpace: "nowrap", overflow: "hidden", textOverflow: "ellipsis",
          }}>{sfErrorToast.name}</div>
          <div style={{
            fontSize: 10, color: "#FCA5A5", lineHeight: 1.4,
            wordBreak: "break-word",
          }}>{sfErrorToast.message}</div>
          <div style={{
            marginTop: 6, fontSize: 9, color: "#9590A8", letterSpacing: 0.4,
          }}>tap to dismiss</div>
        </div>
      )}

      {/* ══ MIDI PIANO ROLL EDITOR (Phase 2) ══════════════════════════════════
          Full-screen editor opened by double-tapping a MIDI clip in the
          timeline. Receives the live audio context + master destination so
          tapping a keyboard key or placing a note plays an immediate preview
          tone routed through the same master gain as transport playback.
          Closing the editor restores the timeline; no playback state is
          touched (the master transport keeps running if it was running). */}
      {midiEditorClipId !== null && (
        <MidiEditorOverlay
          clipId={midiEditorClipId}
          tracks={tracks}
          setTracks={setTracks}
          onClose={function(){ setMidiEditorClipId(null); }}
          bpm={bpm}
          timeSigNum={timeSigNum}
          currentTime={currentTime}
          isPlaying={isPlaying}
          actx={actxRef.current}
          masterDest={masterGainRef.current}
          getActx={getActx}
          getOrCreateMaster={getOrCreateMaster}
          onOpenPresetBrowser={function(trackId){ setPresetBrowserForTrackId(trackId); }}
          onOpenEditSound={function(trackId){ setEditSoundForTrackId(trackId); }}
          onOpenSampler={function(trackId){ setSamplerEditorTrackId(trackId); }}
          getOrBuildPreviewChain={getOrBuildPreviewChain}
          /* FL Studio-style PATTERN vs SONG transport.
             PATTERN button (already in editor) loops just this clip.
             SONG button below uses the main timeline transport.
             When SONG is active, the rewind/forward seek buttons
             ALSO drive the timeline via onTransportSeek instead of
             the editor's local playhead. */
          onTransportPlay={function(){
            // Mutual exclusion: starting SONG stops PAT first.
            try { if (rackPatPlaying) rackPatStop(); } catch(_){}
            if (isPlaying) {
              setIsPlaying(false);
              try { stopAll(); } catch(_) {}
            } else {
              // FL-style "play in context": SONG jumps the timeline
              // playhead to the clip the user is currently editing, so
              // they hear their work where it lives in the arrangement.
              // Falls back to the current playhead position if the clip
              // can't be resolved (e.g. just deleted).
              let startT = currentTime || 0;
              try {
                outer: for (let ti = 0; ti < tracks.length; ti++) {
                  const tr = tracks[ti];
                  if (!tr.clips) continue;
                  for (let ci = 0; ci < tr.clips.length; ci++) {
                    if (tr.clips[ci].id === midiEditorClipId) {
                      startT = tr.clips[ci].startTime || 0;
                      if (typeof syncUItoTime === "function") {
                        syncUItoTime(startT);
                      } else {
                        setCurrentTime(startT);
                      }
                      break outer;
                    }
                  }
                }
              } catch(_){}
              doPlay(startT);
              setIsPlaying(true);
            }
          }}
          onTransportSeek={function(targetSec){
            // Clamp at >= 0; no upper bound (timeline ruler scales).
            const clamped = Math.max(0, targetSec);
            if (isPlaying) {
              // Mid-playback seek — stop, reposition, restart from new time.
              try { stopAll(); } catch(_){}
              setIsPlaying(false);
              setCurrentTime(clamped);
              doPlay(clamped);
              setIsPlaying(true);
              // Force the playhead RAF to restart. React batches the
              // setIsPlaying false → true into no-op for the useEffect
              // dep, but stopAll cancelled the RAF — without this bump
              // the playhead would freeze.
              setSeekVersion(function(v){ return v + 1; });
            } else {
              // Stopped — just move the playhead.
              setCurrentTime(clamped);
            }
          }}
        />
      )}

      {/* ══ DRUM-CHANNEL PIANO ROLL (FL-style "Send to Piano Roll") ═════════
          Opens when the producer taps a notes-mode preview strip in the
          drum rack. The overlay's drumChannel prop tells it to synthesize
          its track + clip from pattern.channels[channelIdx] and write
          edits back to the same place. All other wiring (preview audio,
          transport, etc.) is the same as the regular piano roll. */}
      {drumPadEditor !== null && (
        <MidiEditorOverlay
          clipId={"__drum__"}
          drumChannel={drumPadEditor}
          tracks={tracks}
          setTracks={setTracks}
          onClose={function(){ setDrumPadEditor(null); }}
          bpm={bpm}
          timeSigNum={timeSigNum}
          currentTime={currentTime}
          isPlaying={isPlaying}
          actx={actxRef.current}
          masterDest={masterGainRef.current}
          getActx={getActx}
          getOrCreateMaster={getOrCreateMaster}
          onOpenPresetBrowser={function(){ /* preset picker doesn't apply to pad-scope */ }}
          onOpenEditSound={function(){ /* edit-sound doesn't apply to pad-scope */ }}
          onOpenSampler={function(){ /* sampler editor doesn't apply to pad-scope */ }}
          getOrBuildPreviewChain={getOrBuildPreviewChain}
          /* Cut Itself toggle hook. Producers expect to enable note-cut
             from inside the piano roll, not from the rack tray. This
             flips chokeEnabled on the pad's channel in the active drum
             pattern. The editor reads the current value via the drumChannel
             prop (the channel object is accessible through drumCtx). */
          onToggleCutItself={function(){
            setTracks(function(prev){
              return prev.map(function(tr){
                if (tr.id !== drumPadEditor.trackId) return tr;
                return Object.assign({}, tr, {
                  patterns: (tr.patterns || []).map(function(p){
                    if (p.id !== drumPadEditor.patternId) return p;
                    return Object.assign({}, p, {
                      channels: p.channels.map(function(ch, ci){
                        if (ci !== drumPadEditor.channelIdx) return ch;
                        return Object.assign({}, ch, { chokeEnabled: !ch.chokeEnabled });
                      }),
                    });
                  }),
                });
              });
            });
          }}
          /* PAT-button delegation: route the editor's PATTERN play through
             the rack's pattern scheduler so the entire drum pattern loops
             (not just this pad's notes), and the pad's edits are heard
             live on the next loop iteration. SONG (onTransportPlay below)
             uses the master transport which already plays drum tracks
             correctly through Stage-1 routing. */
          onLocalPlay={function(){
            // Mutual exclusion: starting PAT stops SONG first.
            if (isPlaying) {
              try { stopAll(); } catch(_){}
              setIsPlaying(false);
            }
            try { rackPatStart(); } catch(_){}
          }}
          onLocalStop={rackPatStop}
          localPlayingExternal={rackPatPlaying}
          localPlayheadSecExternal={rackPatPlayheadSec}
          onPatSeek={rackPatSeek}
          onTransportPlay={function(){
            // Mutual exclusion: starting SONG stops PAT first.
            try { if (rackPatPlaying) rackPatStop(); } catch(_){}
            if (isPlaying) {
              setIsPlaying(false);
              try { stopAll(); } catch(_) {}
            } else {
              // FL-style "play pattern in context": if the active
              // pattern is placed on the timeline as a clip, jump the
              // playhead to that clip's start time. Otherwise play from
              // wherever the playhead is now (same as the main timeline's
              // play button).
              let startT = currentTime || 0;
              try {
                const dt = tracks.find(function(x){ return x.id === drumPadEditor.trackId; });
                if (dt) {
                  const clips = (dt.clips || []).filter(function(c){
                    return c.patternId === drumPadEditor.patternId && c.active !== false;
                  });
                  if (clips.length > 0) {
                    const earliest = clips.reduce(function(acc, c){
                      return (c.startTime || 0) < (acc.startTime || 0) ? c : acc;
                    }, clips[0]);
                    startT = earliest.startTime || 0;
                    if (typeof syncUItoTime === "function") {
                      syncUItoTime(startT);
                    } else {
                      setCurrentTime(startT);
                    }
                  }
                }
              } catch(_){}
              // Match togglePlay's pattern: fire setIsPlaying(true) in
              // the same synchronous tick as the user's tap, NOT in a
              // .then() callback. Otherwise the state update can land
              // after the editor closes, the RAF effect misses the
              // transition, and the playhead freezes on the timeline.
              doPlay(startT);
              setIsPlaying(true);
            }
          }}
          /* ── Preview audio for keyboard taps ──
             The drum pad's sample is already loaded in drumKitBuffersRef
             (the rack scheduler put it there on track open). We play it
             directly via scheduleDrumHit with pitch = (tappedPitch - root)
             so the rate follows the key. Routes through the track's FX
             chain entry so volume/pan/FX apply just like during playback. */
          previewPitchOverride={function(pitch, dur){
            try {
              const dt = tracks.find(function(t){ return t.id === drumPadEditor.trackId; });
              if (!dt) return null;
              const buffers = drumKitBuffersRef.current[dt.kitId || "default"];
              if (!buffers) return null;
              const pat = (dt.patterns || []).find(function(p){ return p.id === drumPadEditor.patternId; });
              if (!pat) return null;
              const ch = pat.channels && pat.channels[drumPadEditor.channelIdx];
              if (!ch) return null;
              const buf = buffers[ch.padId];
              if (!buf) return null;
              const ctx = actxRef.current || (typeof getActx === "function" ? getActx() : null);
              if (!ctx) return null;
              // Make sure master gain is audible (transport stop may have
              // ramped it to 0).
              try {
                const mg = getOrCreateMaster();
                if (mg && mg.gain && mg.gain.value === 0) {
                  mg.gain.cancelScheduledValues(ctx.currentTime);
                  mg.gain.setValueAtTime(masterVolume, ctx.currentTime);
                }
              } catch(_){}
              if (ctx.state === "suspended") { try { ctx.resume(); } catch(_){} }
              // Route through the drum track's FX chain entry. Falls back
              // to master if the chain isn't built yet (rack not opened).
              let entryNode = null;
              try {
                if (chainsRef.current[dt.id] && chainsRef.current[dt.id].entry) {
                  entryNode = chainsRef.current[dt.id].entry;
                }
              } catch(_){}
              if (!entryNode) {
                entryNode = masterGainRef.current || ctx.destination;
              }
              // Pitch in semitones relative to root. scheduleDrumHit does
              // the 2^(s/12) ratio conversion internally.
              const semis = (pitch || DRUM_CHANNEL_ROOT_PITCH) - DRUM_CHANNEL_ROOT_PITCH;
              const when = ctx.currentTime + 0.005;
              const hit = scheduleDrumHit(ctx, entryNode, buf, when,
                (ch.volume == null ? 1 : ch.volume),
                semis, 0, null);
              // Return a voice handle compatible with the editor's preview
              // tracker — { src, stop(t) }. The editor calls .stop on key
              // release for press-and-hold behaviour.
              if (hit && hit.src) {
                return {
                  src: hit.src,
                  stop: function(t){
                    try { hit.src.stop(t || 0); } catch(_){}
                  },
                };
              }
              return null;
            } catch(e) { return null; }
          }}
          onTransportSeek={function(targetSec){
            const clamped = Math.max(0, targetSec);
            if (isPlaying) {
              try { stopAll(); } catch(_){}
              setIsPlaying(false);
              setCurrentTime(clamped);
              doPlay(clamped);
              setIsPlaying(true);
              // Force the playhead RAF to restart — see MIDI editor handler.
              setSeekVersion(function(v){ return v + 1; });
            } else {
              setCurrentTime(clamped);
            }
          }}
        />
      )}

      {/* ══ INSTRUMENT PRESET BROWSER (Phase 4) ════════════════════════════
          Bottom-sheet modal that assigns a new instrumentId to the target
          track. Tap a card to assign + close; tap-and-hold to audition. */}
      {presetBrowserForTrackId !== null && (function(){
        const tr = tracks.find(function(t){ return t.id === presetBrowserForTrackId; });
        if (!tr) {
          setTimeout(function(){ setPresetBrowserForTrackId(null); }, 0);
          return null;
        }
        return (
          <PresetBrowserSheet
            trackId={tr.id}
            currentInstId={tr.instrumentId}
            bpm={bpm}
            getOrCreateMaster={getOrCreateMaster}
            isAdmin={!!(user && (user.is_admin === true || user.username === "Trelli"))}
            onOpenAdminSoundfonts={function(){
              setPresetBrowserForTrackId(null);
              setManageSoundfontsOverlay({ mode: "list" });
            }}
            onSelect={function(newInstId){
              // Apply the new preset. We also CLEAR any prior overrides
              // because they were tuned to the old preset's defaults —
              // carrying them over usually results in something weird
              // (e.g. an attack tuned for a bell preset on a bass).
              //
              // For multi-preset banks, default selectedSfPreset to the
              // bank's first preset so the track has a valid sound
              // immediately. For single-preset SFs, clear selectedSfPreset
              // so resolveTrackInstrumentMeta uses the inst defaults.
              const meta = BF_INSTRUMENTS[newInstId];
              let defaultSelectedSfPreset = null;
              if (meta && meta.presets && meta.presets.length > 0) {
                const first = meta.presets[0];
                defaultSelectedSfPreset = { bank: first.bank, program: first.program };
              }
              setTracks(function(prev){
                return prev.map(function(x){
                  if (x.id !== tr.id) return x;
                  return Object.assign({}, x, {
                    instrumentId: newInstId,
                    instrumentOverrides: null,
                    selectedSfPreset: defaultSelectedSfPreset,
                  });
                });
              });
              setPresetBrowserForTrackId(null);
              // Kick off the soundfont download IMMEDIATELY rather than
              // waiting until Play or the piano-roll editor opens. The
              // loading toast subscribes to the engine's preset map and
              // surfaces a sleek progress bar while the .sf3 downloads —
              // so the user sees feedback as soon as they pick the
              // preset, not after they later try to play.
              try {
                const inst = BF_INSTRUMENTS[newInstId];
                if (inst && inst.engine === "soundfont-v1") {
                  const acx = getActx();
                  const masterDest = getOrCreateMaster();
                  ensureSoundfontPresetLoaded(inst, acx, masterDest);
                }
              } catch(_) {}
            }}
            onClose={function(){ setPresetBrowserForTrackId(null); }}
          />
        );
      })()}

      {/* ══ EDIT SOUND PANEL (Phase 4) ═════════════════════════════════════
          Per-track ADSR + filter overrides on top of the preset. */}
      {editSoundForTrackId !== null && (function(){
        const tr = tracks.find(function(t){ return t.id === editSoundForTrackId; });
        if (!tr) {
          setTimeout(function(){ setEditSoundForTrackId(null); }, 0);
          return null;
        }
        return (
          <EditSoundPanel
            track={tr}
            onChange={function(patch){
              // Patch is a partial { amp?, filter?, gain? }. We deep-merge
              // it into the existing instrumentOverrides so multiple
              // slider edits compose (drag attack, then drag cutoff,
              // both values persist).
              let mergedOverrides = null;
              setTracks(function(prev){
                return prev.map(function(x){
                  if (x.id !== tr.id) return x;
                  const cur = x.instrumentOverrides || {};
                  const merged = Object.assign({}, cur);
                  if (patch.amp)    merged.amp    = Object.assign({}, cur.amp    || {}, patch.amp);
                  if (patch.filter) merged.filter = Object.assign({}, cur.filter || {}, patch.filter);
                  if (patch.gain != null) merged.gain = patch.gain;
                  mergedOverrides = merged;
                  return Object.assign({}, x, { instrumentOverrides: merged });
                });
              });
              try {
                if (typeof bfDebugVerboseOn === "function" && bfDebugVerboseOn()) {
                  const a = mergedOverrides && mergedOverrides.amp ? mergedOverrides.amp.attack : undefined;
                  bfDebugLogVerbose("[EDITSOUND] onChange track=" + tr.id + " attack=" + a + " hasAmp=" + !!(mergedOverrides&&mergedOverrides.amp) + " hasFilter=" + !!(mergedOverrides&&mergedOverrides.filter));
                }
              } catch(_){}
              // The override is saved to track state above. The SpessaSynth-
              // live engine applies it per-voice (envelope generators) via
              // scheduleMidiNote routing on the next note — no FluidSynth CC
              // call needed (FluidSynth ignores these envelope/filter CCs).
              //
              // Warm-up: if this override makes the track sound-shaped, start
              // loading the SpessaSynth-live engine NOW (in the background) so
              // it's ready before the user plays — otherwise the first notes
              // briefly fall back to FluidSynth (no attack) while it loads.
              try {
                const baseInst = resolveTrackInstrumentMeta(tr);
                const shapedInst = applyInstrumentOverrides(baseInst, mergedOverrides);
                if (shapedInst && typeof bfInstNeedsSpessaLive === "function" && bfInstNeedsSpessaLive(shapedInst)) {
                  const ctx = getActx();
                  const liveEng = (typeof getBFSpessaLiveEngineForTrack === "function") ? getBFSpessaLiveEngineForTrack(tr.id) : null;
                  if (ctx && liveEng && liveEng.libStatus !== "ready" && liveEng.libStatus !== "loading-lib") {
                    const dest = (typeof getOrBuildPreviewChain === "function") ? getOrBuildPreviewChain(tr.id) : masterGainRef.current;
                    liveEng.ensureReady(ctx, dest || masterGainRef.current).then(function(){
                      try { liveEng.loadPreset(shapedInst.id, shapedInst.gmProgramNumber || 0, shapedInst.customUrl, shapedInst.gmBankNumber || 0); } catch(_){}
                    }).catch(function(){});
                  }
                }
              } catch(_){}
            }}
            onReset={function(){
              setTracks(function(prev){
                return prev.map(function(x){
                  if (x.id !== tr.id) return x;
                  return Object.assign({}, x, { instrumentOverrides: null });
                });
              });
              // Stage 4: the track now has no overrides, so routing returns
              // to FluidSynth. Restore the soundfont's native envelope on the
              // live engine's matched preset (the bank object is shared, so
              // stale generators would otherwise persist), then dispose the
              // now-unused SpessaSynth-live engine to stop its ScriptProcessor.
              try {
                if (typeof _bfSpessaLiveByTrack !== "undefined" && _bfSpessaLiveByTrack.has(tr.id)) {
                  const liveEng = _bfSpessaLiveByTrack.get(tr.id);
                  try {
                    // Clear envelope generators back to soundfont defaults.
                    if (liveEng && typeof liveEng._applyEnvelopeGenerators === "function") {
                      const presets = liveEng.presets || {};
                      Object.keys(presets).forEach(function(pid){
                        const mp = presets[pid] && presets[pid].matchedPreset;
                        if (mp) liveEng._applyEnvelopeGenerators(mp, null);
                      });
                    }
                  } catch(_){}
                  if (liveEng && typeof liveEng.dispose === "function") liveEng.dispose();
                  _bfSpessaLiveByTrack.delete(tr.id);
                }
              } catch(_){}
            }}
            onClose={function(){ setEditSoundForTrackId(null); }}
          />
        );
      })()}


      {/* ══ CHANNEL RACK OVERLAY ═══════════════════════════════════════════
          FL Studio-style step sequencer for a drum track.

          Layout:
            • Top bar: track name, pattern selector, + Pattern, length picker, ×
            • Body: scrollable step grid with sticky pad column on the left.
                    Tap cells to toggle. Visual playhead column animates
                    during in-rack preview playback.
            • Bottom: in-rack preview Play/Stop, snap-to-grid selector,
                       PLACE button. After Place, a confirmation toast
                       shows for ~1s and the rack auto-closes so the user
                       sees the clip on the timeline.
      */}
      {drumRackTrackId !== null && (function(){
        const t = tracks.find(function(tr){ return tr.id === drumRackTrackId; });
        if (!t || t.type !== "drumSequencer") return null;
        const kitMeta = getKitMeta(t.kitId || "default") || { pads: [] };
        const patterns = t.patterns || [];
        const activePatId = drumActivePatternByTrack[drumRackTrackId] || (patterns[0] && patterns[0].id);
        const activePat = patterns.find(function(p){ return p.id === activePatId; }) || patterns[0];
        if (!activePat) return null;

        // ── Pattern mutation helpers ──
        const updateActivePattern = function(patchFn) {
          setTracks(function(prev){
            return prev.map(function(tr){
              if (tr.id !== drumRackTrackId) return tr;
              return { ...tr, patterns: (tr.patterns || []).map(function(p){
                if (p.id !== activePatId) return p;
                return patchFn(p);
              })};
            });
          });
        };
        const toggleStep = function(channelIdx, stepIdx) {
          updateActivePattern(function(p){
            const newChannels = p.channels.map(function(ch, ci){
              if (ci !== channelIdx) return ch;
              const newSteps = ch.steps.slice();
              // Numeric step shape: 0 = off, >=DRUM_MIN_VELOCITY = on.
              // Toggle: if currently on, turn off; if currently off, turn on
              // at default velocity (1.0). Velocity-drag is handled separately
              // by the long-press handler below.
              const cur = stepVelocity(newSteps[stepIdx]);
              newSteps[stepIdx] = cur >= DRUM_MIN_VELOCITY ? 0 : DRUM_DEFAULT_VELOCITY;
              return { ...ch, steps: newSteps };
            });
            return { ...p, channels: newChannels };
          });
        };
        // Long-press + drag adjusts the velocity of a single step. If the
        // step is currently off, we first turn it on at default velocity,
        // then start tracking drag delta. Drag DOWN reduces velocity (with
        // a floor of DRUM_MIN_VELOCITY); drag UP increases (capped at MAX).
        // Dragging well below MIN turns the step off.
        const setStepVelocity = function(channelIdx, stepIdx, newVel) {
          updateActivePattern(function(p){
            const newChannels = p.channels.map(function(ch, ci){
              if (ci !== channelIdx) return ch;
              const newSteps = ch.steps.slice();
              if (newVel < DRUM_MIN_VELOCITY) {
                newSteps[stepIdx] = 0;
              } else {
                newSteps[stepIdx] = Math.min(DRUM_MAX_VELOCITY, newVel);
              }
              return { ...ch, steps: newSteps };
            });
            return { ...p, channels: newChannels };
          });
        };
        const toggleChannelMute = function(channelIdx) {
          updateActivePattern(function(p){
            const newChannels = p.channels.map(function(ch, ci){
              if (ci !== channelIdx) return ch;
              return { ...ch, muted: !ch.muted };
            });
            return { ...p, channels: newChannels };
          });
        };
        const toggleChannelSolo = function(channelIdx) {
          updateActivePattern(function(p){
            const newChannels = p.channels.map(function(ch, ci){
              if (ci !== channelIdx) return ch;
              return { ...ch, soloed: !ch.soloed };
            });
            return { ...p, channels: newChannels };
          });
        };
        const setChannelSwing = function(channelIdx, newSwing) {
          updateActivePattern(function(p){
            const newChannels = p.channels.map(function(ch, ci){
              if (ci !== channelIdx) return ch;
              return { ...ch, swing: Math.max(0, Math.min(0.5, newSwing)) };
            });
            return { ...p, channels: newChannels };
          });
        };
        const setChannelVolume = function(channelIdx, newVol) {
          updateActivePattern(function(p){
            const newChannels = p.channels.map(function(ch, ci){
              if (ci !== channelIdx) return ch;
              return { ...ch, volume: Math.max(0, Math.min(1.5, newVol)) };
            });
            return { ...p, channels: newChannels };
          });
        };
        // Set per-channel length (polyrhythm). When newLen is null, the
        // channel falls back to the pattern's length (the "Match" option).
        // When newLen differs from the current channel length, we resize
        // the channel's steps array: shorter loses tail steps, longer
        // fills with zeros. Existing values up to min(old,new) are kept.
        const setChannelLength = function(channelIdx, newLen) {
          updateActivePattern(function(p){
            const newChannels = p.channels.map(function(ch, ci){
              if (ci !== channelIdx) return ch;
              if (newLen == null) {
                // Match pattern length: clear the channel override AND
                // resize steps[] to match pattern.lengthSteps.
                const target = p.lengthSteps;
                const cur = (ch.steps || []).slice(0, target);
                while (cur.length < target) cur.push(0);
                const next = { ...ch, steps: cur };
                delete next.lengthSteps;
                return next;
              }
              const target = Math.max(1, Math.min(64, newLen));
              const cur = (ch.steps || []).slice(0, target);
              while (cur.length < target) cur.push(0);
              return { ...ch, steps: cur, lengthSteps: target };
            });
            return { ...p, channels: newChannels };
          });
        };
        // Set the channel's display-name override. Empty string clears it
        // (falls back to the kit pad's default name in drumChannelDisplayName).
        const setChannelCustomName = function(channelIdx, newName) {
          updateActivePattern(function(p){
            const newChannels = p.channels.map(function(ch, ci){
              if (ci !== channelIdx) return ch;
              return { ...ch, customName: (newName || "").slice(0, 40) };
            });
            return { ...p, channels: newChannels };
          });
        };
        // Toggle the channel's "Cut Itself" / choke participation. When ON
        // AND the kit's pad has a chokeGroup set, this channel's hits cut
        // any previous hit in that group (and vice versa). Default OFF so
        // existing patterns behave identically until the user opts in.
        const toggleChannelChoke = function(channelIdx) {
          updateActivePattern(function(p){
            const newChannels = p.channels.map(function(ch, ci){
              if (ci !== channelIdx) return ch;
              return { ...ch, chokeEnabled: !ch.chokeEnabled };
            });
            return { ...p, channels: newChannels };
          });
        };
        // Convert a channel's steps → free-form notes ("send to piano roll").
        // The producer can then write rolls, flams, pitched samples, etc. that
        // the step grid can't represent. Steps are kept as a backup field so
        // we can flip back losslessly when the producer is happy.
        const convertChannelToNotes = function(channelIdx) {
          updateActivePattern(function(p){
            const newChannels = p.channels.map(function(ch, ci){
              if (ci !== channelIdx) return ch;
              if (drumChannelModeOf(ch) === "notes") return ch;  // already in notes mode
              const notes = drumChannelStepsToNotes(ch, p, bpm);
              return {
                ...ch,
                mode: "notes",
                notes: notes,
                notesBpm: bpm,            // tempo the note seconds were authored at
                _stepsBackup: ch.steps,   // round-trip insurance
                steps: ch.steps,          // keep so any legacy code that reads steps doesn't crash
              };
            });
            return { ...p, channels: newChannels };
          });
        };
        // Convert a channel's notes → steps. Restores from _stepsBackup when
        // possible (the original step pattern survives), otherwise quantizes
        // the notes back to the grid (lossy — non-root pitches lose pitch,
        // multiple-notes-per-step collapse to the loudest).
        const convertChannelToSteps = function(channelIdx) {
          updateActivePattern(function(p){
            const newChannels = p.channels.map(function(ch, ci){
              if (ci !== channelIdx) return ch;
              if (drumChannelModeOf(ch) === "steps") return ch;
              const steps = ch._stepsBackup || drumChannelNotesToSteps(ch, p, bpm);
              const next = { ...ch, mode: "steps", steps: steps };
              delete next._stepsBackup;
              delete next.notes;
              return next;
            });
            return { ...p, channels: newChannels };
          });
        };
        // Apply a new kit to THE WHOLE TRACK (every pattern). Hits are remapped
        // by index (kick → first pad of new kit, etc.) — the user's documented
        // choice; preserves rhythm at the cost of timbre alignment when pad
        // orders differ. Also kicks off a kit-buffer load for the new kit so
        // playback works immediately after the switch.
        const applyKitToTrack = function(newKitId) {
          const newMeta = getKitMeta(newKitId);
          if (!newMeta) return;
          setTracks(function(prev){
            return prev.map(function(tr){
              if (tr.id !== drumRackTrackId) return tr;
              const remapped = (tr.patterns || []).map(function(p){ return remapPatternToKit(p, newMeta); });
              return { ...tr, kitId: newKitId, patterns: remapped };
            });
          });
          // Background load — scheduler reads drumKitBuffersRef so we mirror
          // the cache there as well, mirroring how the rest of the code paths
          // populate it.
          try {
            const ctx = actxRef.current;
            if (ctx) {
              loadKitBuffers(ctx, newKitId).then(function(bufs){
                drumKitBuffersRef.current[newKitId] = bufs;
              }).catch(function(e){ console.warn("[drum] new kit load failed", e); });
            }
          } catch(_) {}
        };
        // Pattern management — rename / duplicate / delete the active pattern.
        const renameActivePattern = function(newName) {
          updateActivePattern(function(p){ return { ...p, name: newName || p.name }; });
        };
        const duplicateActivePattern = function() {
          // Deep copy by JSON round-trip — patterns are plain data, safe.
          const copy = JSON.parse(JSON.stringify(activePat));
          copy.id = "pat_" + Date.now() + "_" + Math.floor(Math.random()*10000);
          copy.name = activePat.name + " copy";
          setTracks(function(prev){
            return prev.map(function(tr){
              if (tr.id !== drumRackTrackId) return tr;
              return { ...tr, patterns: [...(tr.patterns || []), copy] };
            });
          });
          setDrumActivePatternByTrack(function(prev){
            return { ...prev, [drumRackTrackId]: copy.id };
          });
        };
        // Cross-rack pattern copy/paste via the module-level clipboard.
        // Copy snapshots the active pattern's channel data (steps, notes,
        // mode, choke flag, swing, volume, length) plus the pattern's
        // grid metadata (lengthSteps, stepsPerBeat).
        // Paste maps channels into the destination's active pattern BY
        // INDEX so position N in source overrides position N in destination.
        // PadId references are intentionally kept — they're kit-pad
        // indices, so the destination rack's kit determines the sound at
        // each index (same model duplicateActivePattern uses).
        const copyActivePattern = function() {
          try {
            const snap = JSON.parse(JSON.stringify({
              channels: activePat.channels || [],
              lengthSteps: activePat.lengthSteps,
              stepsPerBeat: activePat.stepsPerBeat,
            }));
            bfSetDrumPatternClipboard(snap);
          } catch(_){}
        };
        const pasteIntoActivePattern = function() {
          const cb = bfGetDrumPatternClipboard();
          if (!cb || !cb.channels) return;
          // Deep copy the clipboard so the destination pattern owns its
          // own data (no shared references across patterns).
          const newChannels = JSON.parse(JSON.stringify(cb.channels));
          updateActivePattern(function(p){
            // Merge by index: source channels override destination
            // channels at the same index. Destination's extra channels
            // beyond the source's length are preserved.
            const existing = p.channels || [];
            const merged = [];
            const len = Math.max(existing.length, newChannels.length);
            for (let i = 0; i < len; i++) {
              if (i < newChannels.length) {
                // Keep destination's padId if it has one — kit pads may
                // differ between racks. The user's intent is "paste the
                // RHYTHM into this rack's pads."
                const srcCh = newChannels[i];
                const dstCh = existing[i] || {};
                merged.push(Object.assign({}, srcCh, {
                  padId: dstCh.padId != null ? dstCh.padId : srcCh.padId,
                }));
              } else {
                merged.push(existing[i]);
              }
            }
            return Object.assign({}, p, {
              channels: merged,
              // Match source's grid so the paste rhythm reads correctly.
              lengthSteps: cb.lengthSteps != null ? cb.lengthSteps : p.lengthSteps,
              stepsPerBeat: cb.stepsPerBeat != null ? cb.stepsPerBeat : p.stepsPerBeat,
            });
          });
        };
        const deleteActivePattern = function() {
          if (patterns.length <= 1) return;  // never delete the last pattern
          // Drop the pattern AND any timeline clips referencing it.
          const idx = patterns.findIndex(function(p){ return p.id === activePatId; });
          const newActive = patterns[idx === 0 ? 1 : idx - 1];
          setTracks(function(prev){
            return prev.map(function(tr){
              if (tr.id !== drumRackTrackId) return tr;
              return {
                ...tr,
                patterns: (tr.patterns || []).filter(function(p){ return p.id !== activePatId; }),
                clips: (tr.clips || []).filter(function(c){ return c.patternId !== activePatId; }),
              };
            });
          });
          setDrumActivePatternByTrack(function(prev){
            return { ...prev, [drumRackTrackId]: newActive.id };
          });
        };
        const addPattern = function() {
          // Match the current pattern's length so new patterns are consistent.
          const newPat = buildEmptyDrumPattern(kitMeta, "Pattern " + (patterns.length + 1), activePat.lengthSteps);
          setTracks(function(prev){
            return prev.map(function(tr){
              if (tr.id !== drumRackTrackId) return tr;
              return { ...tr, patterns: [...(tr.patterns || []), newPat] };
            });
          });
          setDrumActivePatternByTrack(function(prev){
            return { ...prev, [drumRackTrackId]: newPat.id };
          });
        };
        const setActivePat = function(patId) {
          setDrumActivePatternByTrack(function(prev){
            return { ...prev, [drumRackTrackId]: patId };
          });
        };
        // Resize the active pattern to a new step count.
        const setPatternLength = function(newLen) {
          if (newLen === activePat.lengthSteps) return;
          updateActivePattern(function(p){ return resizeDrumPattern(p, newLen); });
        };

        // ── Place on timeline with snap-to-grid + confirmation toast ──
        // Snap target derived from the snapMode state below — picks the
        // nearest beat / half-bar / bar from the current playhead position.
        const placeOnTimeline = function(snapDivisions) {
          const stepSec = drumStepDurationSec(bpm, activePat.stepsPerBeat);
          const patDur  = activePat.lengthSteps * stepSec;
          // Clip duration = pattern duration × loop-count. Pattern loops
          // smoothly INSIDE the clip via the scheduler's wrap-modulo math.
          // Default is 1 loop (single pattern) but most beat producers want
          // 2 or 4 bars of continuous drums under their vocals.
          const loops = Math.max(1, drumPlaceLoopCount || 1);
          const clipDur = patDur * loops;
          let placeAt = currentTime || 0;
          // Snap to the nearest grid increment. 1 = bar (4 beats), 2 = half bar,
          // 4 = beat. Default is 4 (beat) since that's the most musical for
          // dropping a drum pattern.
          if (snapDivisions && snapDivisions > 0) {
            const beatSec = 60 / Math.max(1, bpm);
            const barSec  = beatSec * (timeSigNum || 4);
            const snapUnit = barSec / snapDivisions;
            placeAt = Math.round(placeAt / snapUnit) * snapUnit;
          }
          const newClip = {
            id: "dc_" + Date.now() + "_" + Math.floor(Math.random()*10000),
            patternId: activePat.id,
            startTime: placeAt,
            duration: clipDur,
            active: true,
            label: activePat.name + (loops > 1 ? " ×" + loops : ""),
          };
          setTracks(function(prev){
            return prev.map(function(tr){
              if (tr.id !== drumRackTrackId) return tr;
              return { ...tr, clips: [...(tr.clips || []), newClip] };
            });
          });
          // Visible confirmation: toast for ~1.4s, then auto-close the rack.
          setDrumPlaceToast({ time: placeAt, t0: Date.now() });
          setTimeout(function(){ setDrumPlaceToast(null); }, 1400);
          // Auto-close after a brief pause so the user actually sees the toast.
          // We just clear drumRackTrackId — closing the rack flips the master
          // scheduler out of overlay mode automatically (the rack-overlay
          // reschedule effect handles transport continuity if playback is on).
          setTimeout(function(){
            setDrumRackTrackId(null);
          }, 950);
        };

        // ── Pad audition (single one-shot) ──
        const auditionPad = async function(padId) {
          const ctx = actxRef.current;
          if (!ctx) return;
          try {
            if (ctx.state === "suspended") await ctx.resume();
            let bufs = drumKitBuffersRef.current[t.kitId || "default"];
            if (!bufs) {
              bufs = await loadKitBuffers(ctx, t.kitId || "default");
              drumKitBuffersRef.current[t.kitId || "default"] = bufs;
            }
            const buf = bufs[padId];
            if (!buf) return;
            const src = ctx.createBufferSource();
            src.buffer = buf;
            const g = ctx.createGain();
            g.gain.value = 1.0;
            src.connect(g);
            g.connect(ctx.destination);
            src.start();
          } catch(e) { console.warn("[drum] audition failed", e); }
        };

        // ── In-rack pattern preview ──
        // Rewired (Phase 4 — FL Studio behaviour): the rack ▶ button now
        // controls the MASTER transport, not a private preview scheduler.
        // The master scheduler's rack-overlay path schedules the active
        // editing pattern as a virtual continuous clip on this track,
        // alongside any audio clips already on the timeline. Result: the
        // user hears the pattern they're editing on top of the rest of
        // the song, in sync, just like FL Studio.
        //
        // isPreviewing is true whenever the master transport is playing
        // AND the rack is open — that's the visual cue we render against.
        const isPreviewing = isPlaying;
        const startPreview = function() {
          // Delegate to the master transport. We don't need to load kit
          // buffers here — doPlay() / the master scheduler tick handles it.
          if (isPlaying) return;
          togglePlay();
        };
        const stopPreview = function() {
          if (!isPlaying) return;
          togglePlay();
        };
        const togglePreview = function() {
          if (isPlaying) stopPreview();
          else startPreview();
        };

        // ── Layout constants ──
        const CELL_SIZE = 34;
        // ── Monochrome glass palette (Stage redesign) ──
        // One purple lineage across the whole rack so the UI reads as a
        // single instrument rather than a multi-colour grid. Brightness
        // (not hue) signals state: dim = off, lit = on, halo = playing.
        // Tweaking these constants restyles the entire rack consistently.
        const RACK_ACCENT       = "#A78BFA";   // primary violet
        const RACK_ACCENT_DEEP  = "#7C3AED";   // deeper end of the gradient
        const RACK_ACCENT_BRT   = "#C4B5FD";   // brighter accent (for the LED chase head)
        const RACK_GLASS_BG     = "rgba(20,15,30,0.5)";
        const RACK_HAIRLINE     = "rgba(167,139,250,0.10)";
        const RACK_HAIRLINE_BR  = "rgba(167,139,250,0.18)";   // bar-start hairline
        // Bar-start columns brightest, beat-start columns nearly the same
        // brightness (FL Studio channel rack style — every beat is clearly
        // visible as a lane), non-beat subdivisions stay quiet so the
        // 4-beat grid pops. The user looks for where kick/snare go and
        // their eye lands on the bright columns first.
        const RACK_TINT_BAR     = "rgba(255,255,255,0.10)";   // bar starts — brightest
        const RACK_TINT_BEAT    = "rgba(255,255,255,0.07)";   // beat starts — close to bar
        const RACK_TINT_REST    = "rgba(255,255,255,0.035)";  // subdivisions — visible but dimmer

        // ── Type scale ──
        // Three sizes only (10/12/14) with two weights (600/800). The
        // small-uppercase-tracked label is the rack's section header style;
        // body is for interactive controls; heading for the toolbar mark.
        const RACK_FONT = "'DM Sans',sans-serif";
        const RACK_LABEL_STYLE = {
          color: "#6B6580",
          textTransform: "uppercase",
          fontSize: 10,
          fontWeight: 700,
          letterSpacing: 1.6,
          fontFamily: RACK_FONT,
        };
        const RACK_DIVIDER = (
          <div style={{
            width: 1, height: 18,
            background: "rgba(167,139,250,0.10)",
            flexShrink: 0,
          }} />
        );

        // ── Shared button styles ──
        // Spread one of these into a button's style prop so every control
        // in the rack agrees on padding, type, and corner radius. Variants:
        //   default  — dark glass, low-emphasis (unselected tabs, options)
        //   accent   — violet gradient, primary action / selected state
        //   icon     — square dark-glass tile for icon-only controls (×, ⏮)
        //   danger   — red-tinted destructive action
        // Heights are explicit so vertical alignment doesn't drift across rows.
        const rackBtnBase = {
          height: 34,
          padding: "0 12px",
          borderRadius: 9,
          fontSize: 12,
          fontWeight: 700,
          letterSpacing: 0.3,
          fontFamily: RACK_FONT,
          cursor: "pointer",
          display: "inline-flex",
          alignItems: "center",
          justifyContent: "center",
          whiteSpace: "nowrap",
          flexShrink: 0,
          transition: "background 0.12s, box-shadow 0.12s, transform 0.06s",
          lineHeight: 1,
        };
        const rackBtnDefault = Object.assign({}, rackBtnBase, {
          background: RACK_GLASS_BG,
          color: "#9B95B0",
          border: "1px solid " + RACK_HAIRLINE,
          boxShadow: "inset 0 1px 0 rgba(255,255,255,0.03)",
        });
        const rackBtnAccent = Object.assign({}, rackBtnBase, {
          background: "linear-gradient(135deg," + RACK_ACCENT + " 0%," + RACK_ACCENT_DEEP + " 100%)",
          color: "white",
          border: "1px solid rgba(167,139,250,0.45)",
          boxShadow: "0 4px 14px rgba(139,92,246,0.32), inset 0 1px 0 rgba(255,255,255,0.18)",
          fontWeight: 800,
        });
        const rackBtnIcon = Object.assign({}, rackBtnBase, {
          width: 34, padding: 0,
          background: RACK_GLASS_BG,
          color: "#A5A0BB",
          border: "1px solid " + RACK_HAIRLINE,
          boxShadow: "inset 0 1px 0 rgba(255,255,255,0.03)",
          fontSize: 16,
          fontWeight: 400,
        });
        const rackBtnDanger = Object.assign({}, rackBtnBase, {
          background: "rgba(239,68,68,0.08)",
          color: "#F87171",
          border: "1px solid rgba(239,68,68,0.25)",
          boxShadow: "inset 0 1px 0 rgba(255,255,255,0.02)",
        });

        // ── Crisp glyph icons ──
        // Inline SVG icons used inside rack buttons. Returns a <svg> element
        // sized to `size` (px) and using currentColor so it inherits the
        // button's text color. SVG dodges the iOS emoji-rendering roulette
        // (where ⏮ ✓ ✕ etc. silently become coloured cartoon glyphs in
        // Safari) and gives a single coherent icon system across the rack.
        const RackIcon = function(name, size) {
          const s = size || 12;
          const props = { width: s, height: s, viewBox: "0 0 24 24", fill: "currentColor", "aria-hidden": "true", style: { flexShrink: 0 } };
          switch (name) {
            case "pencil":  return (
              <svg {...props}><path d="M14.06 4.94l5 5L7.92 21.08H3v-4.92L14.06 4.94zm1.42-1.42a2 2 0 0 1 2.83 0l2.17 2.17a2 2 0 0 1 0 2.83l-1.8 1.8-5-5 1.8-1.8z" /></svg>
            );
            case "copy":    return (
              <svg {...props}><path d="M8 2h11a2 2 0 0 1 2 2v11h-2V4H8V2zM5 6h11a2 2 0 0 1 2 2v12a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2V8a2 2 0 0 1 2-2zm0 2v12h11V8H5z" /></svg>
            );
            case "x":       return (
              <svg {...props}><path d="M6.4 5l1.4-1.4L12 7.8l4.2-4.2L17.6 5 13.4 9.2l4.2 4.2-1.4 1.4L12 10.6 7.8 14.8 6.4 13.4l4.2-4.2L6.4 5z" transform="translate(0 2.8)" /></svg>
            );
            case "gear":    return (
              <svg {...props}><path d="M12 8a4 4 0 1 0 0 8 4 4 0 0 0 0-8zm9.4 5.6l1.9 1.4-2 3.5-2.2-.8a8 8 0 0 1-2.4 1.4l-.4 2.3h-4l-.4-2.3a8 8 0 0 1-2.4-1.4l-2.2.8-2-3.5 1.9-1.4a8 8 0 0 1 0-2.8L1.3 9.5l2-3.5 2.2.8A8 8 0 0 1 8 5.4L8.4 3h4l.4 2.3A8 8 0 0 1 15.2 6.8l2.2-.8 2 3.5-1.9 1.4a8 8 0 0 1 0 2.8z" /></svg>
            );
            case "check":   return (
              <svg {...props}><path d="M9.3 17.6l-5.2-5.2 1.4-1.4 3.8 3.8 9.2-9.2 1.4 1.4L9.3 17.6z" /></svg>
            );
            default: return null;
          }
        };
        const CELL_GAP  = 4;
        const PAD_COL_W = 124;
        const totalGridW = activePat.lengthSteps * (CELL_SIZE + CELL_GAP);

        // ── Render ──
        // ── Visual playhead inside the rack ──
        // Reads from the MASTER playback clock so the rack's step indicator
        // animates in sync with the timeline. The editing pattern is anchored
        // at project time 0 (see the rack-overlay path in the master
        // scheduler), so pattern-local time = projectTime mod patDur, and
        // the step index = (pattern-local time) / stepSec.
        //
        // We read `currentTime` (state) rather than liveTimeRef.current (ref)
        // because state triggers re-renders — without that, the rack's
        // playhead wouldn't move visually. currentTime is updated at ~10Hz
        // by the main UI tick loop, which gives a step indicator that
        // visibly walks through the pattern.
        //
        // When playback is stopped, playheadStep is -1 so the indicator
        // disappears.
        const playheadStep = (function(){
          const stepSec = drumStepDurationSec(bpm, activePat.stepsPerBeat);
          if (stepSec <= 0) return -1;
          const patDur = activePat.lengthSteps * stepSec;
          if (patDur <= 0) return -1;
          // PAT mode wins when active — its playhead is the loop-local time
          // the scheduler maintains. Otherwise fall back to the song clock
          // (used for the rack-overlay scheduling and the legacy preview path).
          if (rackPatPlaying) {
            const t = rackPatPlayheadSec || 0;
            return Math.floor(((t % patDur) + patDur) % patDur / stepSec);
          }
          if (!isPreviewing) return -1;
          const t = currentTime || 0;
          const phase = t - Math.floor(t / patDur) * patDur;
          return Math.floor(phase / stepSec);
        })();

        return (
          <div style={{
            position: "fixed", inset: 0, zIndex: 10050,
            // Monochrome violet luxury background. Layered:
            //  • Deep near-black base with a touch of violet
            //  • Top-down soft violet wash that fades at 40%
            //  • Off-center radial bloom for depth
            //  • Fine scanline texture (very low contrast) for tactile feel
            // No more competing blue/magenta — just violet wash on near-black.
            background: `
              radial-gradient(ellipse 80% 60% at 18% -10%, rgba(139,92,246,0.10) 0%, transparent 70%),
              radial-gradient(ellipse 70% 50% at 95% 110%, rgba(124,58,237,0.07) 0%, transparent 70%),
              linear-gradient(180deg, rgba(167,139,250,0.025) 0%, transparent 30%),
              repeating-linear-gradient(0deg, rgba(255,255,255,0.008) 0px, rgba(255,255,255,0.008) 1px, transparent 1px, transparent 3px),
              #070611
            `,
            backdropFilter: "blur(24px) saturate(140%)",
            display: "flex", flexDirection: "column",
            // Respect safe-area on ALL four sides. In landscape, iPhones with
            // notches expose insets on left/right too — without these the top
            // bar and bottom bar slide under the notch and buttons become
            // un-tappable. The CSS vars (--bf-safe-*) fall back to env() if
            // not set.
            paddingTop:    "var(--bf-safe-top,    env(safe-area-inset-top))",
            paddingBottom: "var(--bf-safe-bottom, env(safe-area-inset-bottom))",
            paddingLeft:   "var(--bf-safe-left,   env(safe-area-inset-left))",
            paddingRight:  "var(--bf-safe-right,  env(safe-area-inset-right))",
            color: "white", fontFamily: "'DM Sans',sans-serif",
          }}>
            {/* ═══ TOP BAR ═══ */}
            <div style={{
              flexShrink: 0,
              padding: "16px 20px",
              borderBottom: "1px solid rgba(167,139,250,0.10)",
              display: "flex", alignItems: "center", gap: 14,
              background: "linear-gradient(180deg, rgba(167,139,250,0.04) 0%, transparent 100%)",
              boxShadow: "inset 0 1px 0 rgba(255,255,255,0.02), 0 1px 0 rgba(0,0,0,0.4)",
              // In landscape on phones, the safe-area inset eats horizontal
              // space and the pattern selector + buttons can spill off. Allow
              // wrapping so the brand mark drops to its own row when needed.
              flexWrap: "wrap",
              rowGap: 8,
            }}>
              {/* Brand mark + track name */}
              <div style={{
                display:"flex", alignItems:"center", gap: 12, flexShrink: 0,
              }}>
                <div style={{
                  width: 32, height: 32, borderRadius: 9,
                  background: "linear-gradient(135deg,#8B5CF6 0%,#7C3AED 100%)",
                  display:"flex", alignItems:"center", justifyContent:"center",
                  fontSize: 15, fontWeight: 900, color: "white",
                  boxShadow:
                    "0 0 20px rgba(139,92,246,0.35), " +
                    "inset 0 1px 0 rgba(255,255,255,0.25), " +
                    "inset 0 -1px 0 rgba(0,0,0,0.2)",
                }}>▦</div>
                <div>
                  <div style={{
                    fontSize: 9, fontWeight: 700, color: "#7A7488", letterSpacing: 1.8,
                    textTransform: "uppercase",
                  }}>Channel Rack</div>
                  <div style={{
                    fontSize: 15, fontWeight: 700, color: "#F8F7FA", marginTop: 1,
                    letterSpacing: 0.1,
                  }}>{t.name}</div>
                </div>
              </div>
              <div style={{ flex: 1 }} />
              {/* Pattern selector */}
              <div style={{ display:"flex", alignItems:"center", gap: 8, flexShrink: 0 }}>
                <div style={{ fontSize: 9, color: "#5C5870", letterSpacing: 1.8, textTransform:"uppercase", fontWeight: 700 }}>Pattern</div>
                <select value={activePat.id}
                  onChange={function(e){ setActivePat(e.target.value); }}
                  style={{
                    background:"rgba(20,15,30,0.6)",
                    backdropFilter:"blur(12px)",
                    WebkitBackdropFilter:"blur(12px)",
                    color:"#F8F7FA",
                    border:"1px solid rgba(167,139,250,0.18)",
                    borderRadius: 9, padding:"7px 30px 7px 12px",
                    fontSize: 12, fontWeight: 700,
                    fontFamily:"'DM Sans',sans-serif",
                    cursor:"pointer",
                    appearance: "none",
                    WebkitAppearance: "none",
                    backgroundImage: "url(\"data:image/svg+xml;utf8,<svg xmlns='http://www.w3.org/2000/svg' viewBox='0 0 12 12'><path fill='%23A78BFA' d='M2 4l4 4 4-4z'/></svg>\")",
                    backgroundRepeat: "no-repeat",
                    backgroundPosition: "right 8px center",
                    backgroundSize: "10px",
                  }}>
                  {patterns.map(function(p){
                    return <option key={p.id} value={p.id}>{p.name}</option>;
                  })}
                </select>
                <button onClick={addPattern} style={{
                  background:"rgba(167,139,250,0.06)",
                  color:"#C4B5FD",
                  border:"1px solid rgba(167,139,250,0.25)",
                  borderRadius: 9, padding:"7px 12px",
                  fontSize: 11, fontWeight: 800, cursor:"pointer",
                  letterSpacing: 0.6,
                }}>+ NEW</button>
                {/* Pattern management menu — rename / duplicate / delete */}
                <div style={{ position:"relative", flexShrink: 0 }}>
                  <button onClick={function(){ setDrumPatternMenuOpen(function(v){ return !v; }); }} style={{
                    background: drumPatternMenuOpen ? "rgba(167,139,250,0.15)" : "rgba(20,15,30,0.4)",
                    color: drumPatternMenuOpen ? "#C4B5FD" : "#7A7488",
                    border: "1px solid " + (drumPatternMenuOpen ? "rgba(167,139,250,0.35)" : "rgba(167,139,250,0.08)"),
                    borderRadius: 9, padding:"7px 11px",
                    fontSize: 16, fontWeight: 900, lineHeight: 1, cursor:"pointer",
                  }}>⋮</button>
                  {drumPatternMenuOpen && (
                    <div style={{
                      position:"absolute", top:"calc(100% + 6px)", right: 0,
                      background:"linear-gradient(165deg,rgba(28,20,42,0.92) 0%,rgba(18,14,28,0.92) 100%)",
                      backdropFilter:"blur(20px) saturate(140%)",
                      WebkitBackdropFilter:"blur(20px) saturate(140%)",
                      border:"1px solid rgba(167,139,250,0.18)",
                      borderRadius: 12, minWidth: 210,
                      boxShadow:"0 16px 48px rgba(0,0,0,0.6), 0 0 0 1px rgba(167,139,250,0.08), inset 0 1px 0 rgba(255,255,255,0.04)",
                      zIndex: 10200,
                      overflow:"hidden",
                    }}>
                      <button onClick={function(){
                        setDrumRenameDraft(activePat.name);
                        setDrumPatternMenuOpen(false);
                      }} style={{
                        display:"block", width:"100%", textAlign:"left",
                        padding:"11px 14px", background:"transparent", color:"#F8F7FA",
                        border:"none", borderBottom: "1px solid rgba(167,139,250,0.06)",
                        fontSize: 12, fontWeight: 600, cursor:"pointer",
                        fontFamily:"'DM Sans',sans-serif",
                        letterSpacing: 0.2,
                      }}>
                        <span style={{ display: "inline-flex", alignItems: "center", gap: 10 }}>
                          {RackIcon("pencil", 13)}<span>Rename</span>
                        </span>
                      </button>
                      <button onClick={function(){
                        duplicateActivePattern();
                        setDrumPatternMenuOpen(false);
                      }} style={{
                        display:"block", width:"100%", textAlign:"left",
                        padding:"11px 14px", background:"transparent", color:"#F8F7FA",
                        border:"none", borderBottom: "1px solid rgba(167,139,250,0.06)",
                        fontSize: 12, fontWeight: 600, cursor:"pointer",
                        fontFamily:"'DM Sans',sans-serif",
                        letterSpacing: 0.2,
                      }}>
                        <span style={{ display: "inline-flex", alignItems: "center", gap: 10 }}>
                          {RackIcon("copy", 13)}<span>Duplicate</span>
                        </span>
                      </button>
                      <button onClick={function(){
                        copyActivePattern();
                        setDrumPatternMenuOpen(false);
                      }} style={{
                        display:"block", width:"100%", textAlign:"left",
                        padding:"11px 14px", background:"transparent", color:"#F8F7FA",
                        border:"none", borderBottom: "1px solid rgba(167,139,250,0.06)",
                        fontSize: 12, fontWeight: 600, cursor:"pointer",
                        fontFamily:"'DM Sans',sans-serif",
                        letterSpacing: 0.2,
                      }}>
                        <span style={{ display: "inline-flex", alignItems: "center", gap: 10 }}>
                          {RackIcon("copy", 13)}<span>Copy pattern</span>
                        </span>
                      </button>
                      {(function(){
                        const hasClip = !!bfGetDrumPatternClipboard();
                        return (
                          <button
                            onClick={function(){
                              if (!hasClip) return;
                              pasteIntoActivePattern();
                              setDrumPatternMenuOpen(false);
                            }}
                            disabled={!hasClip}
                            style={{
                              display:"block", width:"100%", textAlign:"left",
                              padding:"11px 14px", background:"transparent",
                              color: hasClip ? "#F8F7FA" : "#3A3548",
                              border:"none", borderBottom: "1px solid rgba(167,139,250,0.06)",
                              fontSize: 12, fontWeight: 600,
                              cursor: hasClip ? "pointer" : "not-allowed",
                              fontFamily:"'DM Sans',sans-serif",
                              letterSpacing: 0.2,
                            }}>
                            <span style={{ display: "inline-flex", alignItems: "center", gap: 10 }}>
                              {RackIcon("copy", 13)}<span>Paste pattern{hasClip ? "" : " (nothing copied)"}</span>
                            </span>
                          </button>
                        );
                      })()}
                      <button
                        onClick={function(){
                          if (patterns.length <= 1) return;
                          deleteActivePattern();
                          setDrumPatternMenuOpen(false);
                        }}
                        disabled={patterns.length <= 1}
                        style={{
                          display:"block", width:"100%", textAlign:"left",
                          padding:"11px 14px",
                          background:"transparent",
                          color: patterns.length <= 1 ? "#3A3548" : "#F87171",
                          border:"none",
                          fontSize: 12, fontWeight: 600,
                          cursor: patterns.length <= 1 ? "not-allowed" : "pointer",
                          fontFamily:"'DM Sans',sans-serif",
                          letterSpacing: 0.2,
                      }}>
                        <span style={{ display: "inline-flex", alignItems: "center", gap: 10 }}>
                          {RackIcon("x", 13)}<span>Delete{patterns.length <= 1 ? " (last pattern)" : ""}</span>
                        </span>
                      </button>
                    </div>
                  )}
                </div>
              </div>
              {/* Close */}
              {/* Closes the rack only. Master transport (timeline playback)
                  is NOT stopped — the rack-overlay effect handles the
                  transition cleanly. If the user wants to stop playback,
                  they hit the master ▶/⏸ button. */}
              <button onClick={function(){ setDrumRackTrackId(null); }} style={rackBtnIcon}>×</button>
            </div>

            {/* ═══ SECONDARY BAR — length, kit, BPM info ═══ */}
            <div style={{
              flexShrink: 0,
              padding: "12px 20px",
              borderBottom: "1px solid rgba(167,139,250,0.06)",
              background: "rgba(13,10,20,0.4)",
              display: "flex", alignItems:"center", gap: 16, flexWrap:"wrap",
              fontSize: 10, letterSpacing: 0.6,
            }}>
              {/* Pattern length picker */}
              <div style={{ display:"flex", alignItems:"center", gap: 8 }}>
                <div style={RACK_LABEL_STYLE}>Length</div>
                {[16, 32, 48, 64].map(function(opt){
                  const sel = activePat.lengthSteps === opt;
                  // Show length as bars (matches the timeline ruler).
                  // stepsPerBar = stepsPerBeat × time-signature numerator.
                  // At 4 steps/beat × 4/4 = 16 steps/bar, so 16/32/48/64
                  // = 1/2/3/4 bars.
                  const stepsPerBar = (activePat.stepsPerBeat || 4) * (timeSigNum || 4);
                  const barCount = Math.max(1, Math.round(opt / stepsPerBar));
                  const label = barCount + (barCount === 1 ? " bar" : " bars");
                  return (
                    <button key={opt}
                      onClick={function(){ if (isPreviewing) stopPreview(); setPatternLength(opt); }}
                      style={sel ? rackBtnAccent : rackBtnDefault}
                    >{label}</button>
                  );
                })}
              </div>
              <div style={{ width: 1, height: 16, background: "rgba(167,139,250,0.08)" }} />
              {/* ── Kit picker ── */}
              {/* Switches the track's kit. Hits are remapped by index so the
                  rhythm survives the swap, but timbres may shift. When the
                  pattern already has hits, we ask for confirmation first. */}
              <div style={{ display:"flex", alignItems:"center", gap: 8, flexShrink: 0 }}>
                <div style={RACK_LABEL_STYLE}>Kit</div>
                <select
                  value={t.kitId || "default"}
                  onChange={function(e){
                    const newKitId = e.target.value;
                    if (newKitId === (t.kitId || "default")) return;
                    if (isPreviewing) stopPreview();
                    // If the pattern has any hits, confirm before changing.
                    const hasHits = (t.patterns || []).some(function(p){
                      return (p.channels || []).some(function(ch){
                        return (ch.steps || []).some(function(s){ return stepIsOn(s); });
                      });
                    });
                    if (hasHits) {
                      setDrumKitSwitchConfirm({ newKitId: newKitId, oldKitId: t.kitId || "default" });
                    } else {
                      applyKitToTrack(newKitId);
                    }
                  }}
                  style={{
                    background:"rgba(20,15,30,0.6)",
                    backdropFilter:"blur(12px)",
                    WebkitBackdropFilter:"blur(12px)",
                    color:"#F8F7FA",
                    border:"1px solid rgba(167,139,250,0.18)",
                    borderRadius: 7, padding:"5px 24px 5px 10px",
                    fontSize: 11, fontWeight: 700, letterSpacing: 0.3,
                    fontFamily:"'DM Sans',sans-serif",
                    cursor:"pointer",
                    appearance: "none",
                    WebkitAppearance: "none",
                    backgroundImage: "url(\"data:image/svg+xml;utf8,<svg xmlns='http://www.w3.org/2000/svg' viewBox='0 0 12 12'><path fill='%23A78BFA' d='M2 4l4 4 4-4z'/></svg>\")",
                    backgroundRepeat: "no-repeat",
                    backgroundPosition: "right 8px center",
                    backgroundSize: "8px",
                  }}>
                  {(function(){
                    const all = listKits();
                    const mine = all.filter(function(k){ return String(k.id).indexOf("mkt_") === 0; });
                    const builtin = all.filter(function(k){ return String(k.id).indexOf("mkt_") !== 0; });
                    return (
                      <>
                        {builtin.map(function(k){ return <option key={k.id} value={k.id}>{k.name}</option>; })}
                        {mine.length > 0 && (
                          <optgroup label="My Kits">
                            {mine.map(function(k){ return <option key={k.id} value={k.id}>{k.name}</option>; })}
                          </optgroup>
                        )}
                      </>
                    );
                  })()}
                </select>
                {/* Admin-only: Manage Kits button. Opens the kit editor overlay. */}
                {(user && (user.is_admin === true || user.username === "Trelli")) && (
                  <button onClick={function(){ setManageKitsOverlay({ mode: "list" }); }}
                    title="Manage Kits (admin)"
                    style={{
                      background:"rgba(167,139,250,0.06)",
                      color:"#C4B5FD",
                      border:"1px solid rgba(167,139,250,0.25)",
                      borderRadius: 7, padding:"5px 10px",
                      fontSize: 10, fontWeight: 800, letterSpacing: 0.8,
                      cursor:"pointer",
                      fontFamily:"'DM Sans',sans-serif",
                    }}>
                    <span style={{ display: "inline-flex", alignItems: "center", gap: 6 }}>
                      {RackIcon("gear", 11)}<span>KITS</span>
                    </span>
                  </button>
                )}
              </div>
              <div style={{ width: 1, height: 16, background: "rgba(167,139,250,0.08)" }} />
              {/* Stats */}
              <div style={{ color: "#5C5870", fontWeight: 600, fontSize: 10, letterSpacing: 0.5 }}>
                <span style={{ color: "#C4B5FD", fontWeight: 700 }}>{bpm}</span> BPM
                <span style={{ margin: "0 10px", color: "#2A2535" }}>·</span>
                <span style={{ color: "#C4B5FD", fontWeight: 700 }}>{drumPatternNaturalDuration(activePat, bpm).toFixed(2)}</span> s
                <span style={{ margin: "0 10px", color: "#2A2535" }}>·</span>
                <span style={{ color: "#9590A8" }}>{(activePat.lengthSteps / activePat.stepsPerBeat / (timeSigNum || 4)).toFixed(1)} bars</span>
              </div>
            </div>

            {/* ═══ GRID BODY — scrollable horizontally for long patterns ═══ */}
            <div
              ref={function(el){
                if (!el) return;
                drumRackScrollEl.current = el;
                // CRITICAL for iOS landscape: index.html installs a
                // document.body `touchmove → preventDefault` (passive:false)
                // to stop the page rubber-band. That blanket block also
                // kills native scrolling inside `overflow:auto` regions
                // — so in landscape the grid body (8 pads tall) can't be
                // scrolled to reveal Clap/Perc/808/Crash.
                //
                // The fix: install our OWN non-passive touchmove on this
                // element that stops the body listener from running for
                // touches inside the rack. We don't call preventDefault
                // ourselves — we WANT the browser to scroll this region
                // natively. We only need to stop the body's blanket
                // preventDefault from blocking that.
                //
                // Idempotent: we only attach once per element (the ref
                // callback runs again on every render).
                if (!el._bfRackScrollHookInstalled) {
                  el._bfRackScrollHookInstalled = true;
                  el.addEventListener("touchmove", function(ev){
                    ev.stopPropagation();
                  }, { passive: true });
                }
              }}
              onScroll={function(e){
                // Drive the custom scrollbar's thumb position from the scroll
                // event. We bypass React state by mutating the ref's transform
                // directly so we don't trigger a re-render on every scroll
                // frame — much smoother on iOS.
                const el = e.currentTarget;
                if (drumRackScrollThumbEl.current && el.scrollWidth > el.clientWidth) {
                  const frac = el.scrollLeft / (el.scrollWidth - el.clientWidth);
                  const trackW = drumRackScrollTrackEl.current
                    ? drumRackScrollTrackEl.current.clientWidth
                    : 0;
                  const thumbW = drumRackScrollThumbEl.current.clientWidth;
                  const maxLeft = Math.max(0, trackW - thumbW);
                  drumRackScrollThumbEl.current.style.transform = "translateX(" + (frac * maxLeft) + "px)";
                }
              }}
              style={{
                flex: 1,
                overflow: drumVelocityDrag ? "hidden" : "auto",
                position: "relative",
                padding: "10px 0 14px 0",
                WebkitOverflowScrolling: "touch",
                // While adjusting a step's velocity (long-press + vertical
                // drag), lock scrolling so the grid container can't steal
                // the gesture — otherwise the vertical drag just scrolls the
                // grid and the velocity never changes.
                touchAction: drumVelocityDrag ? "none" : "auto",
                overscrollBehavior: drumVelocityDrag ? "none" : "auto",
              }}>
              {/* Inner container has fixed width = PAD_COL + grid so the
                  pad column scrolls horizontally with the grid (sticky on
                  the left). Without this the grid would scroll and leave
                  the pad column behind. */}
              <div style={{ minWidth: PAD_COL_W + totalGridW + 20, position: "relative" }}>

                {/* Step ruler */}
                <div style={{
                  display: "flex", alignItems: "center",
                  position: "sticky", top: 0, zIndex: 5,
                  background: "rgba(7,6,17,0.88)",
                  backdropFilter: "blur(14px) saturate(140%)",
                  WebkitBackdropFilter: "blur(14px) saturate(140%)",
                  paddingBottom: 8, paddingTop: 4,
                  borderBottom: "1px solid rgba(167,139,250,0.08)",
                }}>
                  {/* Sticky pad-column header */}
                  <div style={{
                    width: PAD_COL_W, flexShrink: 0,
                    position: "sticky", left: 0, zIndex: 6,
                    background: "rgba(7,6,17,0.95)",
                    paddingLeft: 20, paddingTop: 4, paddingBottom: 4,
                    fontSize: 9, fontWeight: 700,
                    color: "#5C5870", letterSpacing: 1.8, textTransform:"uppercase",
                  }}>Pads</div>
                  {Array.from({length: activePat.lengthSteps}, function(_, i){
                    const isBeatStart = i % activePat.stepsPerBeat === 0;
                    const stepsPerBar = activePat.stepsPerBeat * (timeSigNum || 4);
                    const isBarStart  = i % stepsPerBar === 0;
                    const isPlayhead  = i === playheadStep;
                    // Bar-based label that matches the timeline ruler:
                    //   • Bar-start cells (every 16 steps at 4/4 + 4 spb)
                    //     show the bar number: 1, 2, 3, …
                    //   • Beat-start cells inside a bar show bar.beat:
                    //     1.2, 1.3, 1.4
                    //   • Non-beat cells stay blank — keeps the ruler quiet
                    // Net result: kick on bar 1, snare on bar 1.3 = same
                    // position as the timeline shows it.
                    let label = "";
                    if (isBarStart) {
                      label = String(Math.floor(i / stepsPerBar) + 1);
                    } else if (isBeatStart) {
                      const barNum  = Math.floor(i / stepsPerBar) + 1;
                      const beatNum = Math.floor((i % stepsPerBar) / activePat.stepsPerBeat) + 1;
                      label = barNum + "." + beatNum;
                    }
                    return (
                      <div key={"hdr"+i} style={{
                        width: CELL_SIZE, marginRight: CELL_GAP,
                        textAlign:"center",
                        color: isPlayhead ? "#C4B5FD"
                              : isBarStart ? "#9590A8"
                              : isBeatStart ? "#5C5870"
                              : "#2A2535",
                        fontSize: isBarStart ? 11 : 8,
                        fontWeight: isBarStart ? 800 : 600,
                        letterSpacing: 0.4,
                        textShadow: isPlayhead ? "0 0 10px rgba(196,181,253,0.6)" : "none",
                        transition: "color 0.1s",
                      }}>{label}</div>
                    );
                  })}
                </div>

                {/* Pad rows */}
                <div style={{ paddingTop: 8 }}>
                  {kitMeta.pads.map(function(pad, channelIdx){
                    const channel = activePat.channels[channelIdx];
                    if (!channel) return null;
                    const isMuted  = !!channel.muted;
                    const isSoloed = !!channel.soloed;
                    const anySoloed = activePat.channels.some(function(c){ return c.soloed; });
                    // Effective audibility: muted explicit dim; if some channel
                    // is soloed and THIS one isn't, also dim (it won't sound).
                    const effDim = isMuted || (anySoloed && !isSoloed);
                    const isExpanded = drumExpandedRow === channelIdx;
                    return (
                      <div key={pad.id}>
                        <div style={{
                          display: "flex", alignItems: "center",
                          marginBottom: 4,
                          position: "relative",
                          opacity: effDim ? 0.32 : 1,
                          transition: "opacity 0.15s",
                        }}>
                          {/* Sticky pad label column. The colored stripe is
                              the tap-to-mute / long-press-to-solo control.
                              The pad name is the audition button. The
                              chevron at the right expands a per-row settings
                              tray (swing + volume). */}
                          <div style={{
                            width: PAD_COL_W, flexShrink: 0,
                            position: "sticky", left: 0, zIndex: 4,
                            background: "linear-gradient(90deg, rgba(7,6,17,0.96) 60%, rgba(7,6,17,0.45) 100%)",
                            display: "flex", alignItems: "center", gap: 10,
                            paddingLeft: 20, paddingRight: 8,
                            height: CELL_SIZE,
                          }}>
                            {/* Color stripe — tap mute, long-press solo */}
                            <button
                              onClick={function(e){
                                e.stopPropagation();
                                // Skip mute toggle if this was the end of a long-press.
                                if (e.currentTarget._bfHadLongPress) {
                                  e.currentTarget._bfHadLongPress = false;
                                  return;
                                }
                                toggleChannelMute(channelIdx);
                              }}
                              onTouchStart={function(e){
                                const tgt = e.currentTarget;
                                tgt._bfHadLongPress = false;
                                tgt._bfLongPressTimer = setTimeout(function(){
                                  tgt._bfHadLongPress = true;
                                  toggleChannelSolo(channelIdx);
                                }, 450);
                              }}
                              onTouchEnd={function(e){
                                if (e.currentTarget._bfLongPressTimer) {
                                  clearTimeout(e.currentTarget._bfLongPressTimer);
                                  e.currentTarget._bfLongPressTimer = null;
                                }
                              }}
                              onTouchMove={function(e){
                                if (e.currentTarget._bfLongPressTimer) {
                                  clearTimeout(e.currentTarget._bfLongPressTimer);
                                  e.currentTarget._bfLongPressTimer = null;
                                }
                              }}
                              onMouseDown={function(e){
                                const tgt = e.currentTarget;
                                tgt._bfHadLongPress = false;
                                tgt._bfLongPressTimer = setTimeout(function(){
                                  tgt._bfHadLongPress = true;
                                  toggleChannelSolo(channelIdx);
                                }, 450);
                              }}
                              onMouseUp={function(e){
                                if (e.currentTarget._bfLongPressTimer) {
                                  clearTimeout(e.currentTarget._bfLongPressTimer);
                                  e.currentTarget._bfLongPressTimer = null;
                                }
                              }}
                              style={{
                                width: 6, height: CELL_SIZE - 4,
                                borderRadius: 3,
                                background: isMuted
                                  ? "rgba(58,53,72,0.6)"
                                  : "linear-gradient(180deg, " + RACK_ACCENT + " 0%, " + RACK_ACCENT_DEEP + " 100%)",
                                border: isSoloed ? "1px solid #D4B147" : "none",
                                boxShadow: isMuted
                                  ? "none"
                                  : isSoloed
                                    ? "0 0 12px rgba(212,177,71,0.55)"
                                    : "0 0 10px " + RACK_ACCENT + "55",
                                flexShrink: 0,
                                cursor: "pointer",
                                padding: 0,
                                position: "relative",
                                outline: "none",
                                // Wider hit area by negative margin trick — the
                                // visible stripe is 6px but the hit target is
                                // padded outward via the surrounding flex gap.
                              }}
                              title={isSoloed ? "Soloed (long-press to unsolo)" : isMuted ? "Muted (tap to unmute)" : "Tap = Mute · Hold = Solo"}
                            >
                              {/* Tiny dot indicator inside the stripe */}
                              {isMuted && (
                                <div style={{
                                  position:"absolute", inset: 0,
                                  display:"flex", alignItems:"center", justifyContent:"center",
                                  color:"#5C5870", fontSize: 8, fontWeight: 900,
                                }}>M</div>
                              )}
                              {isSoloed && !isMuted && (
                                <div style={{
                                  position:"absolute", inset: 0,
                                  display:"flex", alignItems:"center", justifyContent:"center",
                                  color:"#D4B147", fontSize: 8, fontWeight: 900,
                                  textShadow:"0 0 6px rgba(0,0,0,0.8)",
                                }}>S</div>
                              )}
                            </button>
                            {/* Audition button — tap to audition, long-press
                                (450ms) to open the full-screen graph editor
                                for this channel. Uses direct DOM property
                                tracking (same pattern as the velocity-drag
                                long-press on cells) for tap-vs-press disambiguation. */}
                            <button
                              onClick={function(e){
                                if (e.currentTarget._bfLongPressFired) {
                                  e.currentTarget._bfLongPressFired = false;
                                  return;
                                }
                                auditionPad(pad.id);
                              }}
                              onMouseDown={function(e){
                                const tgt = e.currentTarget;
                                tgt._bfLongPressFired = false;
                                tgt._bfLongPressTimer = setTimeout(function(){
                                  tgt._bfLongPressFired = true;
                                  // Open graph editor on velocity by default —
                                  // the user can switch parameter via the tabs
                                  // inside the overlay.
                                  setDrumGraphEditorState({ channelIdx: channelIdx, param: "vel" });
                                  try { if (navigator.vibrate) navigator.vibrate(20); } catch(_){}
                                }, 450);
                              }}
                              onMouseUp={function(e){
                                if (e.currentTarget._bfLongPressTimer) {
                                  clearTimeout(e.currentTarget._bfLongPressTimer);
                                  e.currentTarget._bfLongPressTimer = null;
                                }
                              }}
                              onMouseLeave={function(e){
                                if (e.currentTarget._bfLongPressTimer) {
                                  clearTimeout(e.currentTarget._bfLongPressTimer);
                                  e.currentTarget._bfLongPressTimer = null;
                                }
                              }}
                              onTouchStart={function(e){
                                const tgt = e.currentTarget;
                                tgt._bfLongPressFired = false;
                                tgt._bfLongPressTimer = setTimeout(function(){
                                  tgt._bfLongPressFired = true;
                                  setDrumGraphEditorState({ channelIdx: channelIdx, param: "vel" });
                                  try { if (navigator.vibrate) navigator.vibrate(20); } catch(_){}
                                }, 450);
                              }}
                              onTouchEnd={function(e){
                                if (e.currentTarget._bfLongPressTimer) {
                                  clearTimeout(e.currentTarget._bfLongPressTimer);
                                  e.currentTarget._bfLongPressTimer = null;
                                }
                              }}
                              onTouchMove={function(e){
                                // Cancel long-press on finger movement so user
                                // can still scroll the rack vertically.
                                if (e.currentTarget._bfLongPressTimer) {
                                  clearTimeout(e.currentTarget._bfLongPressTimer);
                                  e.currentTarget._bfLongPressTimer = null;
                                }
                              }}
                              style={{
                              background:"transparent",
                              color: "#F0EFF5",
                              border:"none", padding: 0,
                              fontSize: 13, fontWeight: 600,
                              cursor:"pointer", textAlign:"left",
                              flex: 1, overflow:"hidden",
                              textOverflow:"ellipsis", whiteSpace:"nowrap",
                              letterSpacing: 0.1,
                            }}>{drumChannelDisplayName(channel, pad)}</button>
                            {/* Chevron — expand/collapse the row's settings tray */}
                            <button onClick={function(e){
                              e.stopPropagation();
                              setDrumExpandedRow(isExpanded ? null : channelIdx);
                            }} style={{
                              background: isExpanded ? "rgba(139,92,246,0.18)" : "rgba(20,15,30,0.5)",
                              border:"1px solid " + (isExpanded ? "rgba(167,139,250,0.35)" : "rgba(167,139,250,0.08)"),
                              color: isExpanded ? "#C4B5FD" : "#5C5870",
                              fontSize: 11, fontWeight: 700,
                              padding: "3px 8px",
                              borderRadius: 6,
                              cursor:"pointer",
                              flexShrink: 0,
                            }}>{isExpanded ? "▾" : "▸"}</button>
                          </div>
                          {/* Step cells (or notes preview when in notes mode) */}
                          {drumChannelModeOf(channel) === "notes" ? (
                            // ── Notes-mode preview strip ──
                            // Drawn at the same width as the step grid so the
                            // pattern length is visible. Each note is a small
                            // bar at start..start+duration. Tapping opens the
                            // pad-scoped piano roll (wired in Stage 3).
                            <div
                              onClick={function(e){
                                e.stopPropagation();
                                // Stage 3 hook — opens the piano roll for this
                                // channel. drumPadEditor state is wired below.
                                setDrumPadEditor({
                                  trackId: drumRackTrackId,
                                  patternId: activePat.id,
                                  channelIdx: channelIdx,
                                });
                              }}
                              style={{
                                position: "relative",
                                width: totalGridW,
                                height: CELL_SIZE,
                                background: "linear-gradient(180deg,rgba(20,15,30,0.45) 0%,rgba(13,10,20,0.45) 100%)",
                                border: "1px solid rgba(167,139,250,0.10)",
                                borderRadius: 6,
                                cursor: "pointer",
                                overflow: "hidden",
                                flexShrink: 0,
                              }}
                              title="Tap to edit in piano roll"
                            >
                              {/* Bar/beat gridlines mirroring the step grid */}
                              {(function(){
                                const stepsPerBar = activePat.stepsPerBeat * (timeSigNum || 4);
                                const stepWidth = totalGridW / activePat.lengthSteps;
                                const lines = [];
                                for (let i = 1; i < activePat.lengthSteps; i++) {
                                  const isBar = i % stepsPerBar === 0;
                                  const isBeat = i % activePat.stepsPerBeat === 0;
                                  if (!isBeat) continue;
                                  lines.push(
                                    <div key={"l"+i} style={{
                                      position: "absolute",
                                      left: i * stepWidth,
                                      top: 0, bottom: 0,
                                      width: 1,
                                      background: isBar ? "rgba(255,255,255,0.10)" : "rgba(255,255,255,0.04)",
                                    }} />
                                  );
                                }
                                return lines;
                              })()}
                              {/* Note bars */}
                              {(function(){
                                const notes = channel.notes || [];
                                const stepSec = drumStepDurationSec(bpm, activePat.stepsPerBeat || 4);
                                const patDur = activePat.lengthSteps * stepSec;
                                if (patDur <= 0) return null;
                                return notes.map(function(n, ni){
                                  const x = ((n.start || 0) / patDur) * totalGridW;
                                  const w = Math.max(2, ((n.duration || stepSec) / patDur) * totalGridW);
                                  const vel = Math.max(0.1, Math.min(1.5, n.velocity == null ? 0.85 : n.velocity));
                                  return (
                                    <div key={n.id || ni} style={{
                                      position: "absolute",
                                      left: x,
                                      top: 4, bottom: 4,
                                      width: w - 1,
                                      background: "linear-gradient(180deg,#C4B5FD," + RACK_ACCENT_DEEP + ")",
                                      borderRadius: 3,
                                      boxShadow: "0 0 6px rgba(196,181,253," + (0.3 * vel).toFixed(2) + ")",
                                      opacity: Math.min(1, 0.6 + 0.4 * vel),
                                    }} />
                                  );
                                });
                              })()}
                              {/* Empty hint */}
                              {(channel.notes || []).length === 0 ? (
                                <div style={{
                                  position: "absolute",
                                  inset: 0,
                                  display: "flex",
                                  alignItems: "center",
                                  justifyContent: "center",
                                  color: "#5C5870",
                                  fontSize: 10,
                                  fontWeight: 700,
                                  letterSpacing: 1,
                                  textTransform: "uppercase",
                                  pointerEvents: "none",
                                }}>Tap to edit notes</div>
                              ) : null}
                            </div>
                          ) : channel.steps.map(function(stepVal, stepIdx){
                            const params = stepParams(stepVal);
                            const vel = params.vel;
                            const on  = vel >= DRUM_MIN_VELOCITY;
                            // Step has "advanced" data when any non-velocity
                            // param differs from default. Shown as a small
                            // corner dot on the cell so the user can see at
                            // a glance which cells have graph-editor edits.
                            const hasAdvanced = on && (
                              params.pitch !== 0 ||
                              params.pan !== 0 ||
                              params.shift !== 0 ||
                              params.repeat > 1
                            );
                            const isBeatStart = stepIdx % activePat.stepsPerBeat === 0;
                            const isBarStart  = stepIdx % (activePat.stepsPerBeat * (timeSigNum || 4)) === 0;
                            const isPlayhead  = stepIdx === playheadStep;
                            const isVelDragTarget = drumVelocityDrag
                              && drumVelocityDrag.channelIdx === channelIdx
                              && drumVelocityDrag.stepIdx === stepIdx;
                            // Visual intensity scales with velocity so accents
                            // really stand out and quiet ghost notes look quiet.
                            const intensity = on ? (0.45 + 0.55 * (vel / DRUM_MAX_VELOCITY)) : 0;
                            // Long-press → velocity-drag, implemented with
                            // POINTER EVENTS (the EQ band drag pattern). On
                            // pointerdown we start a 350ms timer; if it fires
                            // before the user moves >8px or lifts off, we
                            // enter velocity-drag mode and listen for
                            // pointermove on document. Pointer events bypass
                            // iOS's touch/scroll arbitration entirely — so
                            // the drag actually receives moves regardless of
                            // what iOS would have done with touch events.
                            const PX_PER_FULL = 150;
                            const onCellPointerDown = function(e) {
                              if (e.pointerType === "mouse" && e.button !== 0) return;
                              const startY = e.clientY;
                              const startX = e.clientX;
                              const startVel = on ? vel : DRUM_DEFAULT_VELOCITY;
                              let longPressFired = false;
                              let timer = setTimeout(function(){
                                longPressFired = true;
                                if (!on) setStepVelocity(channelIdx, stepIdx, DRUM_DEFAULT_VELOCITY);
                                setDrumVelocityDrag({
                                  channelIdx: channelIdx,
                                  stepIdx:    stepIdx,
                                  startY:     startY,
                                  startVel:   startVel,
                                  currentVel: startVel,
                                });
                                try { auditionPad(pad.id); } catch(_){}
                              }, 350);
                              const cleanup = function(){
                                if (timer) { clearTimeout(timer); timer = null; }
                                document.removeEventListener("pointermove", onMove);
                                document.removeEventListener("pointerup",   onUp);
                                document.removeEventListener("pointercancel", onUp);
                              };
                              const onMove = function(ev){
                                if (!longPressFired) {
                                  // Pre-long-press: cancel timer if user moved
                                  // too far (it's a scroll/drag, not a hold).
                                  const dx = Math.abs(ev.clientX - startX);
                                  const dy = Math.abs(ev.clientY - startY);
                                  if (dx > 8 || dy > 8) {
                                    cleanup();
                                  }
                                  return;
                                }
                                // Velocity-drag mode: live update.
                                if (ev.cancelable) { try { ev.preventDefault(); } catch(_){} }
                                const dyMove = startY - ev.clientY; // up = +
                                const deltaVel = (dyMove / PX_PER_FULL) * DRUM_MAX_VELOCITY;
                                let newVel = startVel + deltaVel;
                                newVel = Math.max(0, Math.min(DRUM_MAX_VELOCITY, newVel));
                                setStepVelocity(channelIdx, stepIdx, newVel);
                                setDrumVelocityDrag(function(prev){
                                  if (!prev) return prev;
                                  if (prev.currentVel === newVel) return prev;
                                  return { ...prev, currentVel: newVel };
                                });
                              };
                              const onUp = function(){
                                if (longPressFired) {
                                  // Velocity drag completed. Suppress the
                                  // click event iOS will fire after touchend
                                  // (otherwise the cell toggles OFF, removing
                                  // the note the user just adjusted).
                                  suppressNextStepClickRef.current = true;
                                  setDrumVelocityDrag(null);
                                }
                                cleanup();
                              };
                              document.addEventListener("pointermove",  onMove, { passive: false });
                              document.addEventListener("pointerup",    onUp);
                              document.addEventListener("pointercancel", onUp);
                            };
                            return (
                              <div key={"c"+stepIdx}
                                onClick={function(e){
                                  // Skip the tap-toggle if a long-press just
                                  // fired — otherwise we'd remove the cell
                                  // the user just velocity-adjusted.
                                  if (suppressNextStepClickRef.current) {
                                    suppressNextStepClickRef.current = false;
                                    return;
                                  }
                                  toggleStep(channelIdx, stepIdx);
                                }}
                                onPointerDown={onCellPointerDown}
                                style={{
                                  width: CELL_SIZE, height: CELL_SIZE,
                                  marginRight: CELL_GAP,
                                  borderRadius: 6,
                                  // ── Monochrome glass cell ──
                                  // ON cells: violet glass gradient with a
                                  // brighter top edge (glass gloss). OFF cells:
                                  // bar-start brightest, beat-start a touch
                                  // brighter, rest faint — same dim-violet
                                  // throughout, no per-pad colour.
                                  background: on
                                    ? "linear-gradient(155deg, " +
                                        RACK_ACCENT      + "ff 0%, " +
                                        RACK_ACCENT_DEEP + "dd 55%, " +
                                        RACK_ACCENT_DEEP + "bb 100%)"
                                    : isBarStart ? RACK_TINT_BAR
                                      : isBeatStart ? RACK_TINT_BEAT
                                        : RACK_TINT_REST,
                                  // Brightness rides velocity so accents read
                                  // brighter than ghost hits (still monochrome).
                                  opacity: on ? Math.min(1, intensity + 0.2) : 1,
                                  border: on
                                    ? "1px solid " + RACK_ACCENT + "cc"
                                    : isBarStart ? "1px solid " + RACK_HAIRLINE_BR
                                      : isBeatStart ? "1px solid rgba(167,139,250,0.14)"
                                        : "1px solid rgba(167,139,250,0.10)",
                                  // ON cells: violet halo + top inset highlight
                                  // (glass gloss) + bottom inset shadow (recess).
                                  // Playhead: brighter rim + larger halo — the
                                  // LED chase head. Off-cells off the playhead:
                                  // none, but their shadow transition (below)
                                  // lets the halo decay smoothly when the
                                  // playhead leaves, producing the chase tail.
                                  boxShadow: on
                                    ? (
                                        "0 0 " + (6 + 12 * intensity) + "px " + RACK_ACCENT + "88, " +
                                        "inset 0 1.5px 0 rgba(255,255,255,0.32), " +
                                        "inset 0 -1px 0 rgba(0,0,0,0.28), " +
                                        "inset 0 0 6px " + RACK_ACCENT_DEEP + "55"
                                      )
                                    : isPlayhead
                                      ? "inset 0 0 0 1px " + RACK_ACCENT_BRT + ", 0 0 14px rgba(167,139,250,0.45)"
                                      : "none",
                                  cursor:"pointer",
                                  // 140ms ease-out on box-shadow gives the
                                  // playhead halo a graceful decay tail — when
                                  // the chase moves on, the previous cells fade
                                  // their glow instead of cutting hard. That's
                                  // what reads as an animated LED chase.
                                  transition: isVelDragTarget ? "none" : "background 0.08s, box-shadow 0.14s ease-out, transform 0.06s, opacity 0.08s",
                                  flexShrink: 0,
                                  position: "relative",
                                  transform: on ? "scale(1.0)" : "scale(0.97)",
                                  outline: isVelDragTarget ? "2px solid " + RACK_ACCENT_BRT : "none",
                                  outlineOffset: isVelDragTarget ? "1px" : "0",
                                  touchAction: isVelDragTarget ? "none" : "manipulation",
                                }}
                              >
                                {/* Velocity badge — only shown when this is the
                                    cell currently being velocity-dragged. */}
                                {isVelDragTarget && (
                                  <div style={(function(){
                                    // For the top row, the badge above the cell is
                                    // clipped by the sticky ruler — flip it below.
                                    const below = channelIdx === 0;
                                    const base = {
                                      position:"absolute",
                                      left: "50%",
                                      transform: "translateX(-50%)",
                                      background: "rgba(0,0,0,0.92)",
                                      color: "white",
                                      fontSize: 10, fontWeight: 900,
                                      padding: "4px 8px",
                                      borderRadius: 5,
                                      border: "1px solid " + RACK_ACCENT,
                                      whiteSpace: "nowrap",
                                      zIndex: 60,
                                      pointerEvents:"none",
                                      boxShadow: "0 4px 12px rgba(0,0,0,0.5), 0 0 12px " + RACK_ACCENT + "66",
                                    };
                                    if (below) { base.top = "100%"; base.marginTop = 6; }
                                    else { base.bottom = "100%"; base.marginBottom = 6; }
                                    return base;
                                  })()}>
                                    {Math.round(drumVelocityDrag.currentVel * 100)}%
                                  </div>
                                )}
                                {/* Advanced-params indicator — small white
                                    dot in the top-right corner of the cell
                                    when any non-velocity param (pitch / pan /
                                    shift / ratchet) is non-default. Lets the
                                    user spot graph-editor edits at a glance. */}
                                {hasAdvanced && (
                                  <div style={{
                                    position:"absolute",
                                    top: 2, right: 2,
                                    width: 4, height: 4,
                                    borderRadius: "50%",
                                    background: "white",
                                    boxShadow: "0 0 4px rgba(255,255,255,0.9)",
                                    pointerEvents:"none",
                                  }} />
                                )}
                                {/* Ratchet stripes — when repeat>1, show that
                                    many vertical bars on the cell so the user
                                    can see at a glance "this is a roll". */}
                                {on && params.repeat > 1 && (
                                  <div style={{
                                    position:"absolute",
                                    inset: 3,
                                    display: "flex",
                                    alignItems: "center",
                                    justifyContent: "space-around",
                                    pointerEvents: "none",
                                  }}>
                                    {Array.from({length: params.repeat}, function(_, i){
                                      return <div key={i} style={{
                                        width: 1, height: "65%",
                                        background: "rgba(0,0,0,0.5)",
                                      }} />;
                                    })}
                                  </div>
                                )}
                              </div>
                            );
                          })}
                        </div>
                        {/* The per-row settings tray (Swing / Vol / Loop /
                            Choke / Rename) used to render inline here. It
                            now opens as a centered full-screen modal — see
                            the drumExpandedRow modal rendered below the
                            grid. Keeping it as a modal means every control
                            is visible at once on a phone screen without
                            sideways scrolling. */}
                      </div>
                    );
                  })}
                </div>

                {/* Playhead vertical column — soft violet glow that
                    walks the grid in sync with the master transport. */}
                {playheadStep >= 0 && (
                  <div style={{
                    position: "absolute",
                    left: PAD_COL_W + playheadStep * (CELL_SIZE + CELL_GAP) - 1,
                    top: 0, bottom: 0,
                    width: CELL_SIZE + 2,
                    background: "linear-gradient(180deg, rgba(139,92,246,0.18) 0%, rgba(139,92,246,0.08) 100%)",
                    border: "1px solid rgba(167,139,250,0.3)",
                    borderRadius: 6,
                    pointerEvents: "none",
                    zIndex: 2,
                    boxShadow:
                      "0 0 24px rgba(139,92,246,0.35), " +
                      "inset 0 0 12px rgba(139,92,246,0.15)",
                    transition: "left 0.05s linear",
                  }} />
                )}
              </div>
            </div>

            {/* ═══ CUSTOM HORIZONTAL SCROLLBAR ═══
                A visible scrollbar at the bottom of the rack body for long
                patterns. iOS hides native scrollbars so users have no idea
                the grid extends beyond the viewport. Thumb width reflects
                what fraction of the pattern is visible; position reflects
                the current scrollLeft. Drag to scroll, tap to jump. */}
            <div style={{
              flexShrink: 0,
              padding: "0 12px 4px",
            }}>
              <div
                ref={function(el){ if (el) drumRackScrollTrackEl.current = el; }}
                onMouseDown={function(e){
                  const track = e.currentTarget;
                  const scrollEl = drumRackScrollEl.current;
                  if (!scrollEl) return;
                  const trackRect = track.getBoundingClientRect();
                  const seekToClientX = function(cx) {
                    const trackW = trackRect.width;
                    const thumbEl = drumRackScrollThumbEl.current;
                    const thumbW = thumbEl ? thumbEl.clientWidth : 32;
                    const localX = cx - trackRect.left - thumbW / 2;
                    const maxLeft = Math.max(0, trackW - thumbW);
                    const frac = Math.max(0, Math.min(1, localX / maxLeft));
                    scrollEl.scrollLeft = frac * (scrollEl.scrollWidth - scrollEl.clientWidth);
                  };
                  seekToClientX(e.clientX);
                  const mv = function(me){ seekToClientX(me.clientX); };
                  const up = function(){
                    document.removeEventListener("mousemove", mv);
                    document.removeEventListener("mouseup", up);
                  };
                  document.addEventListener("mousemove", mv);
                  document.addEventListener("mouseup", up);
                }}
                onTouchStart={function(e){
                  const track = e.currentTarget;
                  const scrollEl = drumRackScrollEl.current;
                  if (!scrollEl) return;
                  const trackRect = track.getBoundingClientRect();
                  const seekToClientX = function(cx) {
                    const trackW = trackRect.width;
                    const thumbEl = drumRackScrollThumbEl.current;
                    const thumbW = thumbEl ? thumbEl.clientWidth : 32;
                    const localX = cx - trackRect.left - thumbW / 2;
                    const maxLeft = Math.max(0, trackW - thumbW);
                    const frac = Math.max(0, Math.min(1, localX / maxLeft));
                    scrollEl.scrollLeft = frac * (scrollEl.scrollWidth - scrollEl.clientWidth);
                  };
                  seekToClientX(e.touches[0].clientX);
                  const mv = function(te){
                    te.preventDefault();
                    seekToClientX(te.touches[0].clientX);
                  };
                  const up = function(){
                    document.removeEventListener("touchmove", mv);
                    document.removeEventListener("touchend", up);
                  };
                  document.addEventListener("touchmove", mv, { passive: false });
                  document.addEventListener("touchend", up, { passive: true });
                }}
                style={{
                  position: "relative",
                  height: 10,
                  background: "rgba(167,139,250,0.05)",
                  border: "1px solid rgba(167,139,250,0.06)",
                  borderRadius: 5,
                  cursor: "pointer",
                  touchAction: "none",
                  overflow: "hidden",
                }}>
                {(function(){
                  const el = drumRackScrollEl.current;
                  let thumbWPct = 30;
                  if (el && el.scrollWidth > 0) {
                    const frac = el.clientWidth / el.scrollWidth;
                    thumbWPct = Math.max(15, Math.min(98, frac * 100));
                  }
                  return (
                    <div
                      ref={function(el){ if (el) drumRackScrollThumbEl.current = el; }}
                      style={{
                        position: "absolute",
                        top: 1, bottom: 1, left: 1,
                        width: thumbWPct + "%",
                        background: "linear-gradient(180deg, rgba(167,139,250,0.55), rgba(139,92,246,0.35))",
                        borderRadius: 4,
                        boxShadow:
                          "0 0 10px rgba(139,92,246,0.4), " +
                          "inset 0 1px 0 rgba(255,255,255,0.18)",
                        pointerEvents: "none",
                      }} />
                  );
                })()}
              </div>
            </div>

            {/* ═══ BOTTOM BAR — transport + PLACE CTA ═══ */}
            <div style={{
              flexShrink: 0,
              padding: "14px 20px",
              borderTop: "1px solid rgba(167,139,250,0.10)",
              background: "linear-gradient(0deg, rgba(20,15,30,0.5) 0%, transparent 100%)",
              display: "flex", alignItems: "center", gap: 12,
              // Wrap on narrow viewports (landscape phone, especially after
              // safe-area-inset eats horizontal space). Without flexWrap the
              // PLACE button and Loop selector fall off the right edge
              // and become un-tappable.
              flexWrap: "wrap",
              rowGap: 8,
            }}>
              {/* ── Transport row — mirrors piano roll's PAT/SONG layout ──
                  ⏮ rewind-to-start  ⏪ back-bar  PAT▶  SONG▶  ⏩ fwd-bar  ⏭ end
                  PAT loops just the active pattern via rackPatStart/Stop.
                  SONG plays the full timeline (togglePlay). Starting one
                  stops the other so they never play together. */}
              {(function(){
                const barSec = (60 / (bpm || 120)) * (timeSigNum || 4);
                const isSongPlaying = isPlaying;
                const isPatPlaying  = rackPatPlaying;
                // Shared icon-button style for the four navigation buttons.
                const navBtn = Object.assign({}, rackBtnIcon, { width: 36 });
                return (
                  <>
                    {/* ⏮ rewind to start — resets both transports + PAT cursor.
                        Does NOT auto-play; user taps PAT or SONG after. */}
                    <button onClick={function(){
                      try { if (isPatPlaying) rackPatStop(); } catch(_){}
                      try { if (isSongPlaying) stopAll(); } catch(_){}
                      setIsPlaying(false);
                      setCurrentTime(0);
                    }} style={Object.assign({}, navBtn, { color: "#C4B5FD" })} title="Back to start">
                      <BFIcon name="rewindStart" size={13} />
                    </button>
                    {/* ⏪ back one bar — works whether SONG or PAT is playing
                        OR stopped. When SONG plays we stop+reseek+restart; the
                        master scheduler reads from its own anchor so a bare
                        setCurrentTime doesn't actually re-aim it. When PAT
                        plays we just re-anchor the PAT scheduler. */}
                    <button onClick={function(){
                      const target = Math.max(0, (currentTime || 0) - barSec);
                      if (isSongPlaying) {
                        try { stopAll(); } catch(_){}
                        setIsPlaying(false);
                        setCurrentTime(target);
                        doPlay(target).then(function(){ setIsPlaying(true); }).catch(function(){});
                      } else if (isPatPlaying) {
                        // Re-anchor PAT: shift the anchor so the new pattern
                        // position is target (mod loop). The scheduler reads
                        // ctxTime - anchorRef to derive pattern time.
                        try {
                          const m = (typeof getOrCreateMaster === "function") ? getOrCreateMaster() : null;
                          const ctx = m && m.context;
                          if (ctx) {
                            rackPatAnchorRef.current = ctx.currentTime - target;
                            rackPatCursorRef.current = target;
                          }
                        } catch(_){}
                        setCurrentTime(target);
                      } else {
                        setCurrentTime(target);
                      }
                    }} style={navBtn} title="Back one bar">
                      <BFIcon name="backBar" size={13} />
                    </button>
                    {/* PAT ▶ — loops just the active pattern. Purple accent
                        to match the piano roll's PAT button. */}
                    <button onClick={function(){
                      if (isPatPlaying) {
                        rackPatStop();
                      } else {
                        // Stop SONG if it's running so they don't double up.
                        if (isSongPlaying) {
                          try { stopAll(); } catch(_){}
                          setIsPlaying(false);
                        }
                        rackPatStart();
                      }
                    }} style={Object.assign({}, rackBtnBase, {
                      width: 42,
                      padding: 0,
                      position: "relative",
                      background: isPatPlaying
                        ? "linear-gradient(135deg,#A78BFA 0%,#7C3AED 100%)"
                        : RACK_GLASS_BG,
                      color: isPatPlaying ? "white" : "#C4B5FD",
                      border: "1px solid " + (isPatPlaying ? "rgba(167,139,250,0.5)" : "rgba(167,139,250,0.25)"),
                      boxShadow: isPatPlaying
                        ? "0 4px 14px rgba(139,92,246,0.45), inset 0 1px 0 rgba(255,255,255,0.18)"
                        : "inset 0 1px 0 rgba(255,255,255,0.03)",
                    })} title={isPatPlaying ? "Stop pattern" : "Play pattern (loop)"}>
                      <BFIcon name={isPatPlaying ? "stop" : "play"} size={13} />
                      <span style={{
                        position: "absolute",
                        bottom: 2, left: 0, right: 0,
                        fontSize: 6, fontWeight: 800, letterSpacing: 0.6,
                        color: isPatPlaying ? "rgba(255,255,255,0.85)" : "#9590A8",
                        textAlign: "center",
                        pointerEvents: "none",
                        lineHeight: 1,
                      }}>PAT</span>
                    </button>
                    {/* SONG ▶ — plays the full timeline. Cyan to match the
                        piano roll's SONG button and signal "the whole arrangement". */}
                    <button onClick={function(){
                      // Stop PAT if it's running so they don't double up.
                      if (isPatPlaying) {
                        try { rackPatStop(); } catch(_){}
                      }
                      // FL-style: if the active pattern is placed on the
                      // timeline as a clip, hitting SONG jumps the
                      // playhead to that clip's start time so the producer
                      // hears their pattern "in context." If not placed,
                      // play from the current playhead position (same as
                      // the main timeline's play button).
                      if (!isSongPlaying) {
                        try {
                          const clips = (t.clips || []).filter(function(c){
                            return c.patternId === activePat.id && c.active !== false;
                          });
                          if (clips.length > 0) {
                            // Earliest placement on the timeline.
                            const earliest = clips.reduce(function(acc, c){
                              return (c.startTime || 0) < (acc.startTime || 0) ? c : acc;
                            }, clips[0]);
                            const targetT = earliest.startTime || 0;
                            // Re-anchor the playhead then play from there.
                            // syncUItoTime updates currentTime + playheadAtRef so
                            // togglePlay's startT picks it up correctly.
                            if (typeof syncUItoTime === "function") {
                              syncUItoTime(targetT);
                            } else {
                              setCurrentTime(targetT);
                            }
                          }
                        } catch(_){}
                      }
                      togglePlay();
                    }} style={Object.assign({}, rackBtnBase, {
                      width: 42,
                      padding: 0,
                      position: "relative",
                      background: isSongPlaying
                        ? "linear-gradient(135deg,#22D3EE 0%,#06B6D4 100%)"
                        : RACK_GLASS_BG,
                      color: isSongPlaying ? "white" : "#A5F3FC",
                      border: "1px solid " + (isSongPlaying ? "rgba(34,211,238,0.5)" : "rgba(34,211,238,0.25)"),
                      boxShadow: isSongPlaying
                        ? "0 4px 14px rgba(6,182,212,0.45), inset 0 1px 0 rgba(255,255,255,0.2)"
                        : "inset 0 1px 0 rgba(255,255,255,0.03)",
                    })} title={isSongPlaying ? "Stop song" : "Play full song"}>
                      <BFIcon name={isSongPlaying ? "stop" : "play"} size={13} />
                      <span style={{
                        position: "absolute",
                        bottom: 2, left: 0, right: 0,
                        fontSize: 6, fontWeight: 800, letterSpacing: 0.6,
                        color: isSongPlaying ? "rgba(255,255,255,0.85)" : "#7DD3DD",
                        textAlign: "center",
                        pointerEvents: "none",
                        lineHeight: 1,
                      }}>SONG</span>
                    </button>
                    {/* ⏩ forward one bar — same logic as back-bar in reverse */}
                    <button onClick={function(){
                      const target = (currentTime || 0) + barSec;
                      if (isSongPlaying) {
                        try { stopAll(); } catch(_){}
                        setIsPlaying(false);
                        setCurrentTime(target);
                        doPlay(target).then(function(){ setIsPlaying(true); }).catch(function(){});
                      } else if (isPatPlaying) {
                        try {
                          const m = (typeof getOrCreateMaster === "function") ? getOrCreateMaster() : null;
                          const ctx = m && m.context;
                          if (ctx) {
                            rackPatAnchorRef.current = ctx.currentTime - target;
                            rackPatCursorRef.current = target;
                          }
                        } catch(_){}
                        setCurrentTime(target);
                      } else {
                        setCurrentTime(target);
                      }
                    }} style={navBtn} title="Forward one bar">
                      <BFIcon name="fwdBar" size={13} />
                    </button>
                    {/* ⏭ skip to end — jumps to the rightmost clip across all
                        tracks. Compute on the fly so it stays current. */}
                    <button onClick={function(){
                      let latest = 0;
                      (tracksRef.current || []).forEach(function(tr){
                        (tr.clips || []).forEach(function(cl){
                          const s = (cl.startTime || 0);
                          const d = (cl.duration  || 0);
                          if (s + d > latest) latest = s + d;
                        });
                      });
                      if (isSongPlaying) {
                        try { stopAll(); } catch(_){}
                        setIsPlaying(false);
                        setCurrentTime(latest);
                        doPlay(latest).then(function(){ setIsPlaying(true); }).catch(function(){});
                      } else {
                        setCurrentTime(latest);
                      }
                    }} style={Object.assign({}, navBtn, { color: "#C4B5FD" })} title="Skip to end">
                      <BFIcon name="skipEnd" size={13} />
                    </button>
                  </>
                );
              })()}
              <div style={{ flex: 1 }} />
              {/* Loop count — how many times the pattern repeats inside the placed clip */}
              <div style={{ display: "flex", alignItems: "center", gap: 6 }}>
                <div style={{ color: "#5C5870", fontSize: 9, letterSpacing: 1.8, textTransform: "uppercase", fontWeight: 700 }}>Loop</div>
                {[1, 2, 4, 8].map(function(n){
                  const sel = drumPlaceLoopCount === n;
                  return (
                    <button key={n}
                      onClick={function(){ setDrumPlaceLoopCount(n); }}
                      style={{
                        background: sel ? "rgba(139,92,246,0.18)" : "rgba(20,15,30,0.4)",
                        color: sel ? "#C4B5FD" : "#7A7488",
                        border: "1px solid " + (sel ? "rgba(167,139,250,0.4)" : "rgba(167,139,250,0.08)"),
                        borderRadius: 7, padding: "5px 10px",
                        fontSize: 11, fontWeight: 700, cursor: "pointer",
                        fontFamily:"'DM Sans',sans-serif",
                        letterSpacing: 0.3,
                        boxShadow: sel ? "inset 0 1px 0 rgba(255,255,255,0.06)" : "none",
                      }}>×{n}</button>
                  );
                })}
              </div>
              <div style={{ width: 1, height: 22, background: "rgba(167,139,250,0.10)" }} />
              {/* PLACE — main CTA. Refined violet gradient with proper
                  inner highlights — drops the magenta-pink third stop
                  in favor of a cleaner violet-to-deep-violet gradient. */}
              <button onClick={function(){ placeOnTimeline(4); }} style={{
                background: "linear-gradient(135deg,#A78BFA 0%,#8B5CF6 50%,#7C3AED 100%)",
                color:"white", border:"none", borderRadius: 11,
                padding: "11px 24px",
                fontSize: 12, fontWeight: 800,
                cursor:"pointer",
                letterSpacing: 1.6, textTransform:"uppercase",
                boxShadow:
                  "0 8px 24px rgba(139,92,246,0.45), " +
                  "inset 0 1px 0 rgba(255,255,255,0.3), " +
                  "inset 0 -1px 0 rgba(0,0,0,0.18)",
              }}>↓  Place</button>
            </div>

            {/* Place-confirmation toast: appears on Place, fades out, then
                the rack auto-closes so the user sees the new clip on the
                timeline. */}
            {drumPlaceToast && (
              <div style={{
                position: "absolute",
                top: "50%", left: "50%",
                transform: "translate(-50%, -50%)",
                background: "linear-gradient(135deg, rgba(16,185,129,0.95), rgba(5,150,105,0.95))",
                color: "white", padding: "16px 28px",
                borderRadius: 14,
                fontSize: 13, fontWeight: 800,
                letterSpacing: 0.8,
                boxShadow:
                  "0 16px 48px rgba(16,185,129,0.4), " +
                  "inset 0 1px 0 rgba(255,255,255,0.2)",
                pointerEvents: "none",
                zIndex: 10100,
                display: "flex", alignItems: "center", gap: 10,
                animation: "bf-pop 0.3s cubic-bezier(0.34, 1.56, 0.64, 1)",
              }}>
                <div style={{
                  width: 22, height: 22, borderRadius: 11,
                  background: "rgba(255,255,255,0.25)",
                  display:"flex", alignItems:"center", justifyContent:"center",
                }}>{RackIcon("check", 13)}</div>
                <div>
                  Placed at {(function(){
                    const tt = drumPlaceToast.time;
                    const m = Math.floor(tt / 60);
                    const s = Math.floor(tt - m * 60);
                    return m + ":" + (s < 10 ? "0" + s : s);
                  })()}
                </div>
              </div>
            )}

            {/* ══ GRAPH EDITOR OVERLAY ═══════════════════════════════════════════
                Opens when long-pressing a row label in the rack. Provides a
                dedicated full-screen view for editing per-step parameters
                (velocity / pitch / pan / shift / ratchet) on a single channel.
                Layout:
                  • Top bar: pad icon + name + parameter tabs + close
                  • Body: vertical bars per step, draggable to set value
                  • Bottom: Clear / Reset / Preview channel
            ──────────────────────────────────────────────────────────────────── */}
            {drumGraphEditorState && (function(){
              const ge = drumGraphEditorState;
              const gePad = (kitMeta.pads || [])[ge.channelIdx];
              const geChannel = activePat.channels[ge.channelIdx];
              if (!gePad || !geChannel) return null;
              // The channel's effective length — per-channel override OR pattern length.
              const geLen = channelLengthSteps(geChannel, activePat);
              // Resolve parameter metadata: min/max, centre-zero, format, color.
              const PARAM_META = {
                vel:    { label: "Velocity", min: 0, max: DRUM_MAX_VELOCITY, centre: false, fmt: function(v){ return Math.round(v*100)+"%"; },   color: gePad.color },
                pitch:  { label: "Pitch",    min: DRUM_PITCH_MIN, max: DRUM_PITCH_MAX, centre: true,  fmt: function(v){ return (v>0?"+":"")+Math.round(v)+" st"; }, color: "#60A5FA" },
                pan:    { label: "Pan",      min: DRUM_PAN_MIN,   max: DRUM_PAN_MAX,   centre: true,  fmt: function(v){ const p = Math.round(v*100); return p===0?"C":p>0?"R"+p:"L"+(-p); }, color: "#F472B6" },
                shift:  { label: "Shift",    min: DRUM_SHIFT_MIN, max: DRUM_SHIFT_MAX, centre: true,  fmt: function(v){ const p = Math.round(v*100); return (p>0?"+":"")+p+"%"; }, color: "#A78BFA" },
                repeat: { label: "Ratchet",  min: DRUM_REPEAT_MIN,max: DRUM_REPEAT_MAX,centre: false, fmt: function(v){ return "×"+Math.round(v); }, integer: true, color: "#FB923C" },
              };
              const meta = PARAM_META[ge.param] || PARAM_META.vel;
              // Pull current value for a step (in the active parameter).
              const getStepParamValue = function(stepIdx) {
                const p = stepParams(geChannel.steps[stepIdx]);
                return p[ge.param];
              };
              // Mutate a single step's parameter through the rack-scope helper.
              const setStepParamLive = function(stepIdx, newValue) {
                updateActivePattern(function(p){
                  const newChannels = p.channels.map(function(ch, ci){
                    if (ci !== ge.channelIdx) return ch;
                    const newSteps = ch.steps.slice();
                    newSteps[stepIdx] = setStepParam(newSteps[stepIdx], ge.param, newValue);
                    return { ...ch, steps: newSteps };
                  });
                  return { ...p, channels: newChannels };
                });
              };
              // Reset all steps to the parameter's default. "Default" for velocity
              // means leave it alone (we don't want to clear the pattern itself);
              // for everything else it's 0 (or 1 for ratchet).
              const clearAllSteps = function() {
                const defaultVal = ge.param === "vel" ? DRUM_DEFAULT_VELOCITY
                                 : ge.param === "repeat" ? 1
                                 : 0;
                updateActivePattern(function(p){
                  const newChannels = p.channels.map(function(ch, ci){
                    if (ci !== ge.channelIdx) return ch;
                    const newSteps = ch.steps.map(function(s){
                      // Only touch ON steps — don't accidentally turn off OFF steps.
                      if (!stepIsOn(s) && ge.param !== "vel") return s;
                      return setStepParam(s, ge.param, defaultVal);
                    });
                    return { ...ch, steps: newSteps };
                  });
                  return { ...p, channels: newChannels };
                });
              };
              // Layout — graph spans full width below header. Each step is a
              // bar; height encodes value. For centre-zero params the bar grows
              // up OR down from the middle. For 0-min params it grows up from bottom.
              const GRAPH_H = 220;  // px of vertical drawing space
              const STEP_GAP = 2;
              // Bar width derived to fit ~all steps on screen. For long patterns
              // we let it scroll horizontally.
              const BAR_W = Math.max(18, Math.min(40, Math.floor((window.innerWidth - 40) / Math.max(1, geLen)) - STEP_GAP));
              // Convert value -> pixel height + top offset within the graph area.
              const valueToRect = function(value, isOn) {
                if (meta.centre) {
                  const halfH = GRAPH_H / 2;
                  const ratio = (value || 0) / (meta.max);  // -1..1
                  const barH = Math.abs(ratio) * (halfH - 4);
                  const top  = ratio < 0 ? halfH : halfH - barH;
                  return { top: top, height: Math.max(1, barH) };
                } else {
                  // 0-min: zero at bottom, max at top
                  const range = meta.max - meta.min;
                  const norm = ((value || 0) - meta.min) / range;
                  const barH = Math.max(2, norm * (GRAPH_H - 8));
                  return { top: GRAPH_H - barH - 4, height: barH };
                }
              };
              // The graph drag handler — converts vertical drag distance to
              // a parameter value, then commits per move event.
              const startBarDrag = function(stepIdx, startY) {
                const startVal = getStepParamValue(stepIdx);
                setDrumGraphDrag({
                  stepIdx: stepIdx,
                  startY: startY,
                  startVal: startVal,
                  currentVal: startVal,
                });
              };
              return (
                <div style={{
                  position: "fixed", inset: 0, zIndex: 10080,
                  background: `
                    linear-gradient(180deg, ` + meta.color + `15 0%, rgba(0,0,0,0) 30%),
                    radial-gradient(ellipse at 50% 100%, ` + meta.color + `12 0%, rgba(0,0,0,0) 60%),
                    repeating-linear-gradient(0deg, rgba(255,255,255,0.012) 0px, rgba(255,255,255,0.012) 1px, transparent 1px, transparent 3px),
                    #06060a
                  `,
                  backdropFilter: "blur(20px)",
                  display: "flex", flexDirection: "column",
                  paddingTop:    "var(--bf-safe-top,    env(safe-area-inset-top))",
                  paddingBottom: "var(--bf-safe-bottom, env(safe-area-inset-bottom))",
                  paddingLeft:   "var(--bf-safe-left,   env(safe-area-inset-left))",
                  paddingRight:  "var(--bf-safe-right,  env(safe-area-inset-right))",
                  color: "white", fontFamily: "'DM Sans',sans-serif",
                }}>
                  {/* Top bar */}
                  <div style={{
                    flexShrink: 0,
                    padding: "12px 16px",
                    borderBottom: "1px solid " + meta.color + "30",
                    display: "flex", alignItems: "center", gap: 10, flexWrap: "wrap", rowGap: 8,
                    background: "linear-gradient(180deg, " + meta.color + "10 0%, transparent 100%)",
                  }}>
                    <div style={{
                      width: 26, height: 26, borderRadius: 6,
                      background: gePad.color,
                      boxShadow: "0 0 10px " + gePad.color + "aa",
                      flexShrink: 0,
                    }} />
                    <div style={{ flexShrink: 0 }}>
                      <div style={{ fontSize: 9, fontWeight: 800, letterSpacing: 1.4, color: "#888", textTransform: "uppercase" }}>Graph Editor</div>
                      <div style={{ fontSize: 13, fontWeight: 800, color: "white" }}>{drumChannelDisplayName(geChannel, gePad)}</div>
                    </div>
                    <div style={{ flex: 1, minWidth: 12 }} />
                    {/* Parameter tabs */}
                    {["vel", "pitch", "pan", "shift", "repeat"].map(function(p){
                      const sel = ge.param === p;
                      const pmeta = PARAM_META[p];
                      return (
                        <button key={p}
                          onClick={function(){ setDrumGraphEditorState({ channelIdx: ge.channelIdx, param: p }); }}
                          style={{
                            background: sel ? pmeta.color + "30" : "transparent",
                            color: sel ? pmeta.color : "#666",
                            border: "1px solid " + (sel ? pmeta.color : "rgba(255,255,255,0.08)"),
                            borderRadius: 7, padding: "5px 9px",
                            fontSize: 11, fontWeight: 800, cursor: "pointer",
                            fontFamily: "'DM Sans',sans-serif",
                            letterSpacing: 0.3,
                          }}>{pmeta.label}</button>
                      );
                    })}
                    <button onClick={function(){ setDrumGraphEditorState(null); }} style={{
                      background: "rgba(255,255,255,0.04)",
                      color:"#aaa",
                      border: "1px solid rgba(255,255,255,0.08)",
                      borderRadius: 8, padding: "5px 10px",
                      fontSize: 18, lineHeight: 1, cursor:"pointer",
                      fontWeight: 400, fontFamily: "system-ui",
                      flexShrink: 0,
                    }}>×</button>
                  </div>

                  {/* Graph body — scrolls horizontally if pattern is long */}
                  <div style={{
                    flex: 1,
                    overflowX: "auto",
                    overflowY: "hidden",
                    WebkitOverflowScrolling: "touch",
                    display: "flex",
                    alignItems: "center",
                    padding: "20px 16px",
                  }}>
                    <div style={{ position: "relative", height: GRAPH_H, display: "flex", alignItems: "stretch", gap: STEP_GAP }}>
                      {/* Centre-zero reference line for pan/shift/pitch */}
                      {meta.centre && (
                        <div style={{
                          position: "absolute", left: 0, right: 0, top: GRAPH_H/2,
                          height: 1,
                          background: "rgba(255,255,255,0.12)",
                          pointerEvents: "none",
                        }} />
                      )}
                      {/* Per-step bars */}
                      {Array.from({length: geLen}, function(_, stepIdx){
                        const stepIsActive = stepIsOn(geChannel.steps[stepIdx]);
                        const v = getStepParamValue(stepIdx);
                        const rect = valueToRect(v, stepIsActive);
                        const isDragging = drumGraphDrag && drumGraphDrag.stepIdx === stepIdx;
                        const isBeatStart = stepIdx % activePat.stepsPerBeat === 0;
                        const isBarStart  = stepIdx % (activePat.stepsPerBeat * (timeSigNum || 4)) === 0;
                        return (
                          <div key={"gb"+stepIdx}
                            onMouseDown={function(e){ startBarDrag(stepIdx, e.clientY); e.preventDefault(); }}
                            onTouchStart={function(e){
                              if (e.touches && e.touches[0]) startBarDrag(stepIdx, e.touches[0].clientY);
                            }}
                            style={{
                              position: "relative",
                              width: BAR_W,
                              height: GRAPH_H,
                              flexShrink: 0,
                              background: isBarStart ? "rgba(192,38,211,0.05)"
                                : isBeatStart ? "rgba(255,255,255,0.025)"
                                : "rgba(255,255,255,0.01)",
                              borderRadius: 4,
                              cursor: "ns-resize",
                              touchAction: "none",
                              border: isDragging ? "1px solid white" : "1px solid rgba(255,255,255,0.04)",
                              transition: isDragging ? "none" : "border 0.08s",
                            }}>
                            {/* The actual value bar */}
                            <div style={{
                              position: "absolute",
                              left: 2, right: 2,
                              top: rect.top,
                              height: rect.height,
                              background: stepIsActive
                                ? "linear-gradient(180deg, " + meta.color + "ff 0%, " + meta.color + "aa 100%)"
                                : meta.color + "55",
                              borderRadius: 2,
                              boxShadow: stepIsActive ? "0 0 8px " + meta.color + "aa" : "none",
                              opacity: stepIsActive ? 1 : 0.5,
                              pointerEvents: "none",
                              transition: isDragging ? "none" : "top 0.08s, height 0.08s",
                            }} />
                            {/* Value badge while dragging this bar */}
                            {isDragging && (
                              <div style={{
                                position: "absolute",
                                top: -28, left: "50%",
                                transform: "translateX(-50%)",
                                background: "rgba(0,0,0,0.92)",
                                color: "white",
                                fontSize: 10, fontWeight: 900,
                                padding: "3px 8px",
                                borderRadius: 5,
                                border: "1px solid " + meta.color,
                                whiteSpace: "nowrap",
                                pointerEvents: "none",
                                boxShadow: "0 4px 12px rgba(0,0,0,0.5)",
                              }}>{meta.fmt(drumGraphDrag.currentVal)}</div>
                            )}
                            {/* Step number footer */}
                            <div style={{
                              position: "absolute", bottom: -16, left: 0, right: 0,
                              textAlign: "center",
                              color: isBarStart ? "#888" : isBeatStart ? "#555" : "#333",
                              fontSize: isBarStart ? 10 : 9,
                              fontWeight: 700,
                              pointerEvents: "none",
                            }}>{stepIdx + 1}</div>
                          </div>
                        );
                      })}
                    </div>
                  </div>

                  {/* Bottom bar — actions */}
                  <div style={{
                    flexShrink: 0,
                    padding: "12px 16px",
                    borderTop: "1px solid " + meta.color + "30",
                    background: "linear-gradient(0deg, " + meta.color + "10 0%, transparent 100%)",
                    display: "flex", alignItems: "center", gap: 10, flexWrap: "wrap", rowGap: 8,
                  }}>
                    <div style={{ color:"#666", fontSize: 10, fontWeight: 700, letterSpacing: 0.5 }}>
                      {meta.label} · {geLen} steps · drag bars to edit
                    </div>
                    <div style={{ flex: 1 }} />
                    <button onClick={clearAllSteps} style={{
                      background:"rgba(239,68,68,0.1)", color:"#EF4444",
                      border:"1px solid rgba(239,68,68,0.3)",
                      borderRadius: 8, padding:"7px 12px",
                      fontSize: 11, fontWeight: 800, cursor:"pointer",
                      letterSpacing: 0.4,
                    }}>Reset {meta.label}</button>
                  </div>
                </div>
              );
            })()}

            {/* ══ PER-PAD SETTINGS MODAL ═════════════════════════════════════
                Centered overlay showing every per-channel control at once:
                Swing, Volume, Loop length (polyrhythm), Choke toggle, Rename.
                Opens when the user taps the ▾ chevron on a pad row. Replaces
                the old inline tray that overflowed the side of the rack on
                phones. ────────────────────────────────────────────────────── */}
            {drumExpandedRow !== null && (function(){
              const expChannelIdx = drumExpandedRow;
              const expPad = (kitMeta && kitMeta.pads) ? kitMeta.pads[expChannelIdx] : null;
              const expChannel = activePat && activePat.channels ? activePat.channels[expChannelIdx] : null;
              if (!expPad || !expChannel) return null;
              const renaming = drumChannelRename && drumChannelRename.channelIdx === expChannelIdx;
              return (
                <div
                  onClick={function(){
                    // Backdrop click closes — also commits any in-flight rename
                    // so the user doesn't lose what they typed when they tap away.
                    if (renaming) {
                      setChannelCustomName(expChannelIdx, drumChannelRename.draft);
                      setDrumChannelRename(null);
                    }
                    setDrumExpandedRow(null);
                  }}
                  style={{
                    position:"absolute", inset: 0,
                    background:"rgba(7,6,17,0.55)",
                    backdropFilter:"blur(14px) saturate(140%)",
                    WebkitBackdropFilter:"blur(14px) saturate(140%)",
                    display:"flex", alignItems:"center", justifyContent:"center",
                    zIndex: 10160,
                    padding: 16,
                  }}>
                  <div
                    onClick={function(e){ e.stopPropagation(); }}
                    ref={function(el){
                      // Same iOS landscape scroll fix as the grid body —
                      // see the comment on the rack scroll element.
                      if (!el || el._bfRackScrollHookInstalled) return;
                      el._bfRackScrollHookInstalled = true;
                      el.addEventListener("touchmove", function(ev){
                        ev.stopPropagation();
                      }, { passive: true });
                    }}
                    style={{
                      background:"linear-gradient(165deg,rgba(28,20,42,0.85) 0%,rgba(18,14,28,0.85) 100%)",
                      backdropFilter:"blur(24px) saturate(140%)",
                      WebkitBackdropFilter:"blur(24px) saturate(140%)",
                      border:"1px solid rgba(167,139,250,0.2)",
                      borderRadius: 18, padding: 22,
                      width:"100%", maxWidth: 400,
                      maxHeight:"82vh", overflowY:"auto",
                      boxShadow:
                        "0 32px 80px rgba(0,0,0,0.7), " +
                        "0 0 0 1px rgba(167,139,250,0.08), " +
                        "0 0 60px " + expPad.color + "12, " +
                        "inset 0 1px 0 rgba(255,255,255,0.05)",
                    }}>
                    {/* Header */}
                    <div style={{ display:"flex", alignItems:"center", gap: 12, marginBottom: 22 }}>
                      <div style={{
                        width: 32, height: 32, borderRadius: 9,
                        background: "linear-gradient(135deg," + expPad.color + " 0%," + expPad.color + "cc 100%)",
                        boxShadow:
                          "0 0 16px " + expPad.color + "88, " +
                          "inset 0 1px 0 rgba(255,255,255,0.25), " +
                          "inset 0 -1px 0 rgba(0,0,0,0.2)",
                        flexShrink: 0,
                      }} />
                      <div style={{ flex: 1, minWidth: 0 }}>
                        <div style={{
                          fontSize: 9, fontWeight: 700, letterSpacing: 1.8,
                          color: "#7A7488", textTransform: "uppercase",
                        }}>Pad Settings</div>
                        <div style={{
                          fontSize: 16, fontWeight: 700, color: "#F8F7FA",
                          overflow:"hidden", textOverflow:"ellipsis", whiteSpace:"nowrap",
                          letterSpacing: 0.1, marginTop: 1,
                        }}>{drumChannelDisplayName(expChannel, expPad)}</div>
                      </div>
                      <button onClick={function(){
                        if (renaming) {
                          setChannelCustomName(expChannelIdx, drumChannelRename.draft);
                          setDrumChannelRename(null);
                        }
                        setDrumExpandedRow(null);
                      }} style={{
                        background:"rgba(20,15,30,0.4)", color:"#9590A8",
                        border:"1px solid rgba(167,139,250,0.10)",
                        borderRadius: 9, padding:"7px 11px",
                        fontSize: 18, lineHeight: 1, cursor:"pointer",
                        fontWeight: 300, fontFamily:"system-ui",
                      }}>×</button>
                    </div>

                    {/* ── Swing ── */}
                    <div style={{ marginBottom: 18 }}>
                      <div style={{
                        display:"flex", justifyContent:"space-between", alignItems:"center",
                        marginBottom: 8,
                      }}>
                        <div style={{ color:"#7A7488", fontSize: 10, fontWeight: 700, letterSpacing: 1.8, textTransform:"uppercase" }}>Swing</div>
                        <div style={{ color: "#F8F7FA", fontSize: 12, fontWeight: 700, letterSpacing: 0.3 }}>
                          {Math.round((expChannel.swing || 0) * 200)}%
                        </div>
                      </div>
                      <input type="range" min={0} max={0.5} step={0.01}
                        value={expChannel.swing || 0}
                        onChange={function(e){ setChannelSwing(expChannelIdx, +e.target.value); }}
                        style={{ width:"100%", accentColor: "#A78BFA" }}
                      />
                    </div>

                    {/* ── Volume ── */}
                    <div style={{ marginBottom: 18 }}>
                      <div style={{
                        display:"flex", justifyContent:"space-between", alignItems:"center",
                        marginBottom: 8,
                      }}>
                        <div style={{ color:"#7A7488", fontSize: 10, fontWeight: 700, letterSpacing: 1.8, textTransform:"uppercase" }}>Volume</div>
                        <div style={{ color: "#F8F7FA", fontSize: 12, fontWeight: 700, letterSpacing: 0.3 }}>
                          {Math.round((expChannel.volume == null ? 1 : expChannel.volume) * 100)}%
                        </div>
                      </div>
                      <input type="range" min={0} max={1.5} step={0.01}
                        value={expChannel.volume == null ? 1 : expChannel.volume}
                        onChange={function(e){ setChannelVolume(expChannelIdx, +e.target.value); }}
                        style={{ width:"100%", accentColor: "#A78BFA" }}
                      />
                    </div>

                    {/* ── Loop length (polyrhythm) ── */}
                    <div style={{ marginBottom: 18 }}>
                      <div style={{ color:"#7A7488", fontSize: 10, fontWeight: 700, letterSpacing: 1.8, textTransform:"uppercase", marginBottom: 8 }}>Loop Length</div>
                      <div style={{ display:"flex", gap: 6, flexWrap:"wrap" }}>
                        {[
                          { label: "Match", val: null },
                          { label: "8",  val: 8  },
                          { label: "12", val: 12 },
                          { label: "16", val: 16 },
                          { label: "24", val: 24 },
                          { label: "32", val: 32 },
                        ].map(function(opt){
                          const cur = expChannel.lengthSteps || null;
                          const sel = cur === opt.val;
                          return (
                            <button key={opt.label}
                              onClick={function(){ setChannelLength(expChannelIdx, opt.val); }}
                              style={{
                                background: sel ? "rgba(139,92,246,0.18)" : "rgba(20,15,30,0.4)",
                                color: sel ? "#C4B5FD" : "#7A7488",
                                border: "1px solid " + (sel ? "rgba(167,139,250,0.4)" : "rgba(167,139,250,0.08)"),
                                borderRadius: 8, padding: "7px 12px",
                                fontSize: 11, fontWeight: 700, cursor:"pointer",
                                fontFamily:"'DM Sans',sans-serif",
                                flex: opt.val === null ? "0 0 auto" : "1 1 0",
                                minWidth: 52,
                                letterSpacing: 0.3,
                                boxShadow: sel ? "inset 0 1px 0 rgba(255,255,255,0.06)" : "none",
                              }}>{opt.label}</button>
                          );
                        })}
                      </div>
                    </div>

                    {/* ── Choke (Cut Itself) ──
                        Self-only: when ON, every new hit on this pad cuts
                        the previous hit on the SAME pad. Works on any
                        sound — no group setup needed. Off by default so
                        existing patterns sound identical until toggled. */}
                    <div style={{ marginBottom: 18 }}>
                      <div style={{
                        display:"flex", justifyContent:"space-between", alignItems:"center",
                        marginBottom: 8,
                      }}>
                        <div style={{ color:"#7A7488", fontSize: 10, fontWeight: 700, letterSpacing: 1.8, textTransform:"uppercase" }}>Choke · Cut Itself</div>
                      </div>
                      <button onClick={function(){ toggleChannelChoke(expChannelIdx); }}
                        title="Each new hit cuts the previous hit on this pad"
                        style={{
                          width:"100%",
                          background: expChannel.chokeEnabled
                            ? "linear-gradient(135deg,#A78BFA 0%,#8B5CF6 50%,#7C3AED 100%)"
                            : "rgba(20,15,30,0.5)",
                          color: expChannel.chokeEnabled ? "white" : "#9590A8",
                          border: expChannel.chokeEnabled
                            ? "1px solid rgba(167,139,250,0.4)"
                            : "1px solid rgba(167,139,250,0.10)",
                          borderRadius: 10, padding: "12px 14px",
                          fontSize: 12, fontWeight: 800,
                          cursor: "pointer",
                          fontFamily:"'DM Sans',sans-serif",
                          boxShadow: expChannel.chokeEnabled
                            ? "0 6px 18px rgba(139,92,246,0.35), inset 0 1px 0 rgba(255,255,255,0.22)"
                            : "inset 0 1px 0 rgba(255,255,255,0.02)",
                          letterSpacing: 1,
                          textTransform: "uppercase",
                        }}>{expChannel.chokeEnabled ? "Choke On" : "Choke Off"}</button>
                      <div style={{ color:"#5C5870", fontSize: 10, marginTop: 8, lineHeight: 1.5, letterSpacing: 0.2 }}>
                        Stops a new hit from overlapping the previous one on this pad. Useful for 808s and long tails.
                      </div>
                    </div>

                    {/* ── Piano Roll mode (FL-style "send to piano roll") ──
                        Flips a single pad's pattern between the step grid and
                        a free MIDI note list. Notes mode lets the producer
                        write rolls, flams, and pitched samples that the
                        16-step grid can't express. The conversion preserves
                        the step pattern as a backup so flipping back is
                        lossless when the producer hasn't edited the notes.
                        Each channel toggles independently — kick can be
                        on piano roll while snare stays on the step grid. */}
                    <div style={{ marginBottom: 18 }}>
                      <div style={{
                        display:"flex", justifyContent:"space-between", alignItems:"center",
                        marginBottom: 8,
                      }}>
                        <div style={{ color:"#7A7488", fontSize: 10, fontWeight: 700, letterSpacing: 1.8, textTransform:"uppercase" }}>Piano Roll</div>
                      </div>
                      <button onClick={function(){
                        if (drumChannelModeOf(expChannel) === "notes") {
                          convertChannelToSteps(expChannelIdx);
                        } else {
                          convertChannelToNotes(expChannelIdx);
                        }
                      }}
                        title={drumChannelModeOf(expChannel) === "notes"
                          ? "Convert this pad's notes back to a step grid"
                          : "Send this pad's pattern to the piano roll for note-level editing"}
                        style={{
                          width:"100%",
                          background: drumChannelModeOf(expChannel) === "notes"
                            ? "linear-gradient(135deg,#A78BFA 0%,#8B5CF6 50%,#7C3AED 100%)"
                            : "rgba(20,15,30,0.5)",
                          color: drumChannelModeOf(expChannel) === "notes" ? "white" : "#9590A8",
                          border: drumChannelModeOf(expChannel) === "notes"
                            ? "1px solid rgba(167,139,250,0.4)"
                            : "1px solid rgba(167,139,250,0.10)",
                          borderRadius: 10, padding: "12px 14px",
                          fontSize: 12, fontWeight: 800,
                          cursor: "pointer",
                          fontFamily:"'DM Sans',sans-serif",
                          boxShadow: drumChannelModeOf(expChannel) === "notes"
                            ? "0 4px 14px rgba(139,92,246,0.32), inset 0 1px 0 rgba(255,255,255,0.18)"
                            : "inset 0 1px 0 rgba(255,255,255,0.02)",
                          letterSpacing: 1,
                          textTransform: "uppercase",
                        }}>
                        {drumChannelModeOf(expChannel) === "notes"
                          ? "Send Back to Steps"
                          : "Send to Piano Roll"}
                      </button>
                      <div style={{ color:"#5C5870", fontSize: 10, marginTop: 8, lineHeight: 1.5, letterSpacing: 0.2 }}>
                        {drumChannelModeOf(expChannel) === "notes"
                          ? "Currently editing as MIDI notes. Tap to convert back — your original step pattern is preserved."
                          : "Edit this pad as MIDI notes for rolls, flams, and pitched samples."}
                      </div>
                    </div>

                    {/* ── Rename ── */}
                    <div style={{ marginBottom: 6 }}>
                      <div style={{
                        display:"flex", justifyContent:"space-between", alignItems:"center",
                        marginBottom: 8,
                      }}>
                        <div style={{ color:"#7A7488", fontSize: 10, fontWeight: 700, letterSpacing: 1.8, textTransform:"uppercase" }}>Display Name</div>
                        {expChannel.customName ? (
                          <button onClick={function(){
                            setChannelCustomName(expChannelIdx, "");
                            setDrumChannelRename(null);
                          }} style={{
                            background:"rgba(20,15,30,0.4)", color:"#9590A8",
                            border:"1px solid rgba(167,139,250,0.10)",
                            borderRadius: 6, padding:"4px 10px",
                            fontSize: 10, fontWeight: 700, cursor:"pointer",
                            letterSpacing: 0.4,
                          }}>Reset to default</button>
                        ) : null}
                      </div>
                      <input
                        value={renaming ? drumChannelRename.draft : (expChannel.customName || "")}
                        onFocus={function(){
                          if (!renaming) {
                            setDrumChannelRename({
                              channelIdx: expChannelIdx,
                              draft: expChannel.customName || "",
                            });
                          }
                        }}
                        onChange={function(e){
                          setDrumChannelRename({
                            channelIdx: expChannelIdx,
                            draft: e.target.value.slice(0, 40),
                          });
                        }}
                        onBlur={function(){
                          if (renaming) {
                            setChannelCustomName(expChannelIdx, drumChannelRename.draft);
                            setDrumChannelRename(null);
                          }
                        }}
                        onKeyDown={function(e){
                          if (e.key === "Enter") {
                            if (renaming) {
                              setChannelCustomName(expChannelIdx, drumChannelRename.draft);
                              setDrumChannelRename(null);
                            }
                            e.currentTarget.blur();
                          } else if (e.key === "Escape") {
                            setDrumChannelRename(null);
                            e.currentTarget.blur();
                          }
                        }}
                        placeholder={expPad.name || "Pad name"}
                        style={{
                          width:"100%", boxSizing:"border-box",
                          background:"rgba(10,8,18,0.6)", color:"#F8F7FA",
                          border:"1px solid rgba(167,139,250,0.18)",
                          borderRadius: 10, padding:"12px 14px",
                          fontSize: 13, fontWeight: 600,
                          outline:"none",
                          fontFamily:"'DM Sans',sans-serif",
                          letterSpacing: 0.1,
                        }}/>
                      <div style={{ color:"#5C5870", fontSize: 10, marginTop: 6, letterSpacing: 0.2 }}>
                        Leave blank to use the kit's default name ({expPad.name || "—"}).
                      </div>
                    </div>
                  </div>
                </div>
              );
            })()}

            {/* Kit-switch confirmation. When the user picks a new kit and the
                pattern already has hits, this modal asks them to confirm —
                because hits are remapped by INDEX (kick → first pad of new
                kit) and the new pads' timbres may not match. */}
            {drumKitSwitchConfirm !== null && (
              <div
                onClick={function(){ setDrumKitSwitchConfirm(null); }}
                style={{
                  position:"absolute", inset: 0,
                  background:"rgba(7,6,17,0.55)",
                  backdropFilter:"blur(14px) saturate(140%)",
                  WebkitBackdropFilter:"blur(14px) saturate(140%)",
                  display:"flex", alignItems:"center", justifyContent:"center",
                  zIndex: 10150,
                  padding: 20,
                }}>
                <div
                  onClick={function(e){ e.stopPropagation(); }}
                  style={{
                    background:"linear-gradient(165deg,rgba(28,20,42,0.85) 0%,rgba(18,14,28,0.85) 100%)",
                    backdropFilter:"blur(24px) saturate(140%)",
                    WebkitBackdropFilter:"blur(24px) saturate(140%)",
                    border:"1px solid rgba(167,139,250,0.2)",
                    borderRadius: 18, padding: 26,
                    minWidth: 280, maxWidth: 460,
                    boxShadow:
                      "0 32px 80px rgba(0,0,0,0.7), " +
                      "0 0 0 1px rgba(167,139,250,0.08), " +
                      "inset 0 1px 0 rgba(255,255,255,0.05)",
                  }}>
                  <div style={{
                    fontSize: 10, fontWeight: 700, letterSpacing: 1.8,
                    color:"#9590A8", textTransform:"uppercase",
                    marginBottom: 12,
                  }}>Switch Kit?</div>
                  <div style={{ color:"#D8D4E0", fontSize: 13, lineHeight: 1.6, marginBottom: 20 }}>
                    Your hits will be remapped to the new kit by pad position.
                    The rhythm stays the same, but pads may sound different.
                    Continue?
                  </div>
                  <div style={{ display:"flex", gap: 8, justifyContent:"flex-end" }}>
                    <button onClick={function(){ setDrumKitSwitchConfirm(null); }} style={{
                      background:"rgba(20,15,30,0.4)", color:"#9590A8",
                      border:"1px solid rgba(167,139,250,0.12)",
                      borderRadius: 10, padding:"10px 18px",
                      fontSize: 12, fontWeight: 700, cursor:"pointer",
                      fontFamily:"'DM Sans',sans-serif",
                      letterSpacing: 0.4,
                    }}>Cancel</button>
                    <button onClick={function(){
                      const nk = drumKitSwitchConfirm.newKitId;
                      setDrumKitSwitchConfirm(null);
                      applyKitToTrack(nk);
                    }} style={{
                      background:"linear-gradient(135deg,#A78BFA 0%,#8B5CF6 50%,#7C3AED 100%)",
                      color:"white", border:"none",
                      borderRadius: 10, padding:"10px 20px",
                      fontSize: 12, fontWeight: 800, cursor:"pointer",
                      boxShadow:
                        "0 6px 18px rgba(139,92,246,0.4), " +
                        "inset 0 1px 0 rgba(255,255,255,0.25)",
                      letterSpacing: 0.6,
                    }}>Switch Kit</button>
                  </div>
                </div>
              </div>
            )}

            {/* Rename pattern modal. When drumRenameDraft is non-null we show
                a centered input dialog. Submit on Enter or button click;
                cancel on Esc or backdrop click. */}
            {drumRenameDraft !== null && (
              <div
                onClick={function(){ setDrumRenameDraft(null); }}
                style={{
                  position:"absolute", inset: 0,
                  background:"rgba(7,6,17,0.55)",
                  backdropFilter:"blur(14px) saturate(140%)",
                  WebkitBackdropFilter:"blur(14px) saturate(140%)",
                  display:"flex", alignItems:"center", justifyContent:"center",
                  zIndex: 10150,
                  padding: 20,
                }}>
                <div
                  onClick={function(e){ e.stopPropagation(); }}
                  style={{
                    background:"linear-gradient(165deg,rgba(28,20,42,0.85) 0%,rgba(18,14,28,0.85) 100%)",
                    backdropFilter:"blur(24px) saturate(140%)",
                    WebkitBackdropFilter:"blur(24px) saturate(140%)",
                    border:"1px solid rgba(167,139,250,0.2)",
                    borderRadius: 18, padding: 26,
                    minWidth: 280, maxWidth: 420, width:"100%",
                    boxShadow:
                      "0 32px 80px rgba(0,0,0,0.7), " +
                      "0 0 0 1px rgba(167,139,250,0.08), " +
                      "inset 0 1px 0 rgba(255,255,255,0.05)",
                  }}>
                  <div style={{
                    fontSize: 10, fontWeight: 700, letterSpacing: 1.8,
                    color:"#9590A8", textTransform:"uppercase",
                    marginBottom: 14,
                  }}>Rename Pattern</div>
                  <input
                    type="text"
                    autoFocus
                    value={drumRenameDraft}
                    onChange={function(e){ setDrumRenameDraft(e.target.value); }}
                    onKeyDown={function(e){
                      if (e.key === "Enter") {
                        renameActivePattern(drumRenameDraft);
                        setDrumRenameDraft(null);
                      } else if (e.key === "Escape") {
                        setDrumRenameDraft(null);
                      }
                    }}
                    style={{
                      width:"100%", boxSizing:"border-box",
                      background:"rgba(10,8,18,0.6)",
                      border:"1px solid rgba(167,139,250,0.2)",
                      borderRadius: 10, padding:"12px 14px",
                      color:"#F8F7FA", fontSize: 14, fontWeight: 600,
                      fontFamily:"'DM Sans',sans-serif",
                      outline:"none",
                      letterSpacing: 0.1,
                    }}
                  />
                  <div style={{ display:"flex", justifyContent:"flex-end", gap: 8, marginTop: 16 }}>
                    <button onClick={function(){ setDrumRenameDraft(null); }} style={{
                      background:"rgba(20,15,30,0.4)", color:"#9590A8",
                      border:"1px solid rgba(167,139,250,0.12)",
                      borderRadius: 10, padding:"10px 16px",
                      fontSize: 12, fontWeight: 700, cursor:"pointer",
                      fontFamily:"'DM Sans',sans-serif",
                      letterSpacing: 0.4,
                    }}>Cancel</button>
                    <button onClick={function(){
                      renameActivePattern(drumRenameDraft);
                      setDrumRenameDraft(null);
                    }} style={{
                      background:"linear-gradient(135deg,#A78BFA 0%,#8B5CF6 50%,#7C3AED 100%)",
                      color:"white", border:"none",
                      borderRadius: 10, padding:"10px 20px",
                      fontSize: 12, fontWeight: 800, cursor:"pointer",
                      boxShadow:
                        "0 6px 18px rgba(139,92,246,0.4), " +
                        "inset 0 1px 0 rgba(255,255,255,0.25)",
                      letterSpacing: 0.6,
                    }}>Save</button>
                  </div>
                </div>
              </div>
            )}
          </div>
        );
      })()}

      {/* ══ FX PANEL ═══════════════════════════════════════════ */}
      {fxTrackId && (function(){
        const t = tracks.find(function(tr){ return tr.id===fxTrackId; });
        if (!t) return null;
        const fx = t.effects || {};
        // Keep ref current every render so FxPanel's stable onUpd always has latest data
        fxUpdRef.current = function(section, patch, rawValue){
          let newEffects;
          // Track whether this update changes the chain TOPOLOGY (which
          // plugins are present + their ON state). Topology changes
          // require rebuilding the Web Audio graph — applyFxLive can
          // only tweak parameters of nodes that already exist, not
          // add/remove nodes mid-playback.
          let changesTopology = false;

          if (section === "pluginChain") {
            newEffects = { ...t.effects, pluginChain: rawValue };
            // Add / move / reorder always counts as topology change.
            changesTopology = true;
          } else if (section === "__restore") {
            // Undo/redo path: rawValue is a full effects snapshot.
            // Always treat as topology change since the restored state
            // may have different plugins active vs current.
            newEffects = rawValue;
            changesTopology = true;
          } else if (section === "__remove") {
            // Atomic: remove from chain AND force plugin section off so audio bypasses
            const pluginKey = rawValue;
            const newChain  = (t.effects.pluginChain || []).filter(function(k){ return k !== pluginKey; });
            newEffects = {
              ...t.effects,
              pluginChain: newChain,
              [pluginKey]: { ...(t.effects[pluginKey] || {}), on: false },
            };
            changesTopology = true;
          } else {
            newEffects = { ...t.effects, [section]:{ ...(t.effects[section]||{}), ...patch } };
            // Per-effect ON/OFF toggle = topology change (the node may
            // not exist in the current chain — buildChain conditionally
            // allocates based on `on`). Other parameter tweaks are fine
            // to apply live.
            const oldOn = !!(t.effects[section] && t.effects[section].on);
            const newOn = !!(newEffects[section] && newEffects[section].on);
            if (oldOn !== newOn) changesTopology = true;
            // R-Comp mode (warm/electro) changes pre-stage WaveShaper curve
            // & HPF cutoff — requires a chain rebuild.
            if (section === "rcomp" && patch && patch.mode !== undefined &&
                t.effects.rcomp && t.effects.rcomp.mode !== patch.mode) {
              changesTopology = true;
            }
            // TQ rescue: tracks loaded from projects created BEFORE TQ
            // existed don't have `tq` in their effects. When the user
            // adds TQ and toggles master ON, the spread above creates
            // {tq: {on:true}} with NO bands — buildChain then sees an
            // empty bands array and silently does nothing. Detect this
            // case and inject the default bands so the plugin actually
            // processes audio. Same for missing/empty bands.
            if (section === "tq") {
              const cur = newEffects.tq;
              if (cur && (!Array.isArray(cur.bands) || cur.bands.length === 0)) {
                newEffects = Object.assign({}, newEffects, {
                  tq: Object.assign({ mode: "dyneq" }, cur, { bands: [
                    { id:"plosive",  type:"cut",   on:true, freq:80,   q:1.4, gain:-6, threshold:-12, attack:0.002, release:0.080, name:"PLOSIVE" },
                    { id:"mud",      type:"cut",   on:true, freq:250,  q:1.0, gain:-4, threshold:-18, attack:0.008, release:0.120, name:"MUD" },
                    { id:"box",      type:"cut",   on:true, freq:600,  q:1.2, gain:-3, threshold:-15, attack:0.005, release:0.100, name:"BOX" },
                    { id:"presence", type:"boost", on:false,freq:3000, q:1.0, gain:+3, threshold:-30, attack:0.020, release:0.200, name:"PRESENCE" },
                    { id:"harsh",    type:"cut",   on:true, freq:3200, q:1.8, gain:-3, threshold:-12, attack:0.003, release:0.080, name:"HARSH" },
                    { id:"deess",    type:"cut",   on:true, freq:7000, q:2.5, gain:-6, threshold:-14, attack:0.001, release:0.060, name:"DE-ESS" },
                    { id:"air",      type:"boost", on:false,freq:12000,q:0.7, gain:+3, threshold:-32, attack:0.030, release:0.250, name:"AIR" },
                  ]}),
                });
                // Bands appearing is a topology change (n bands == n nodes to build)
                changesTopology = true;
              }
            }
          }

          updateTrack(t.id, { effects: newEffects });
          applyFxLive(t.id, newEffects);

          // If this changed chain topology AND playback is active, stop
          // playback. The chain will rebuild on the next Play press with
          // the new topology — that's the architecture, and rebuilding
          // mid-playback would risk audio dropouts or clicks. Tell the
          // user via a brief toast so they know why.
          if (changesTopology && isPlayingRef.current) {
            try { stopAll(); } catch (_) {}
            setIsPlaying(false);
            try {
              if (typeof window !== "undefined" && window.bfToast && window.bfToast.info) {
                window.bfToast.info("Press Play to apply FX changes");
              }
            } catch (_) {}
          }

          // Sync autotune fx changes to autoPitch monitoring state so the
          // live worklet node parameters update without restarting monitoring
          if (section === "autotune" && newEffects.autotune) {
            setAutoPitch(function(prev) { return Object.assign({}, prev, newEffects.autotune); });
          }
        };
        // Stable preset applier — same pattern as fxUpdRef so FxPanel's memo
        // comparator sees the same function reference across re-renders
        applyPresetRef.current = function(preset){ applyPreset(t.id, preset); };
        // Stable transport callbacks — set fresh each render so closure
        // captures CURRENT isPlaying/isRecording/tracks state, but ref
        // identity stays the same so memoized FxPanel sees same props.
        fxTogglePlayRef.current = togglePlay;
        fxRecordRef.current = function(){
          // Mirror the main transport record-button logic so behavior
          // is identical from inside FX panel. Count-in + FX-warn-modal
          // logic must be preserved.
          if (isRecording) {
            stopRecording();
            return;
          }
          // PDC + drift compensation now handle FX-on-channel timing
          // automatically, so no warning is needed.
          startCountIn(fxTrackId);
        };
        // Admin preset save: snapshot the current FX state and POST to the
        // backend. On success append to userPresets (no full re-fetch). Only
        // wired up if the user is an admin — the proxy itself is stable.
        fxSavePresetRef.current = function(){
          try {
            // Confirm there's actually something to save
            var hasAnyOn = false;
            if (fx && typeof fx === "object") {
              Object.keys(fx).forEach(function(k){
                var v = fx[k]; if (v && v.on) hasAnyOn = true;
              });
            }
            if (!hasAnyOn) {
              alert("Turn on at least one plugin before saving a preset.");
              return;
            }
            var name = window.prompt("Preset name:", "");
            if (!name || !name.trim()) return;
            var desc = window.prompt("Short description (optional):", "") || "";
            // Strip pluginChain from the fx snapshot — the backend derives it
            // from which plugins have on:true. Keeps the saved payload tidy.
            var fxSnap = {};
            Object.keys(fx).forEach(function(k){
              if (k === "pluginChain") return;
              fxSnap[k] = fx[k];
            });
            apiFetch("/api/admin/fx-presets", {
              method: "POST",
              headers: { "Content-Type": "application/json" },
              body: JSON.stringify({ name: name.trim(), desc: desc.trim(), fx: fxSnap }),
            }).then(function(saved){
              if (saved && saved.id) {
                setUserPresets(function(prev){ return prev.concat([saved]); });
              }
            }).catch(function(e){
              alert("Could not save preset: " + ((e && e.message) || "unknown error"));
            });
          } catch(e) {
            console.warn("[Studio] Save preset failed:", e);
          }
        };
        // Admin preset delete: for user-presets, calls DELETE; for built-ins,
        // calls hide-builtin (which keeps the hardcoded preset alive but
        // marks it hidden for ALL users). Both update local state on success.
        fxDeletePresetRef.current = function(p){
          if (!p || !p.id) return;
          var isUserPreset = p.kind === "user";
          var confirmMsg = isUserPreset
            ? ("Delete preset \"" + p.name + "\"? This affects all users.")
            : ("Hide built-in preset \"" + p.name + "\"? It will be hidden from all users until restored.");
          if (!window.confirm(confirmMsg)) return;
          if (isUserPreset) {
            apiFetch("/api/admin/fx-presets/" + encodeURIComponent(p.id), {
              method: "DELETE",
            }).then(function(){
              setUserPresets(function(prev){ return prev.filter(function(x){ return x.id !== p.id; }); });
            }).catch(function(e){
              alert("Could not delete preset: " + ((e && e.message) || "unknown error"));
            });
          } else {
            apiFetch("/api/admin/fx-presets/hide-builtin", {
              method: "POST",
              headers: { "Content-Type": "application/json" },
              body: JSON.stringify({ id: p.id }),
            }).then(function(){
              setHiddenBuiltins(function(prev){ return prev.indexOf(p.id) < 0 ? prev.concat([p.id]) : prev; });
            }).catch(function(e){
              alert("Could not hide preset: " + ((e && e.message) || "unknown error"));
            });
          }
        };
        return (
          <FxPanel
            key={fxTrackId}
            fx={fx}
            fxTrackId={fxTrackId}
            trackName={t.name}
            trackColor={t.color}
            onClose={function(){
              // Sync the time display to the live audio position when
              // closing FX, since we've been skipping setCurrentTime
              // updates during FX mode to keep the main thread free.
              try {
                if (typeof liveTimeRef !== "undefined" && liveTimeRef.current >= 0) {
                  setCurrentTime(liveTimeRef.current);
                }
              } catch(e) {}
              setFxTrackId(null);
            }}
            onUpd={fxUpdRef.current}
            onApplyPreset={applyPresetRef.current}
            analyserNode={trackAnalysersRef.current[fxTrackId] || null}
            isPlaying={isPlaying}
            isRecording={isRecording}
            onTogglePlay={fxTogglePlayProxy}
            onRecord={fxRecordProxy}
            onSeekBack={seekBack}
            onSeekForward={seekForward}
            presets={mergedPresets}
            isAdmin={!!(user && user.is_admin)}
            onSavePreset={fxSavePresetProxy}
            onDeletePreset={fxDeletePresetProxy}
          />
        );
      })()}

      {/* ══ TAKES / COMPING PANEL ══════════════════════════════ */}
      {showTakes && (function(){
        const t = tracks.find(function(tr){ return tr.id===showTakes; });
        if (!t || !t.clips || t.clips.length <= 1) return null;
        return (
          <div style={{ background:"#0f0f0f",borderTop:"1px solid #1e1e1e",padding:"10px 14px",flexShrink:0,maxHeight:140,overflowY:"auto" }} onClick={function(e){ e.stopPropagation(); }}>
            <div style={{ display:"flex",alignItems:"center",justifyContent:"space-between",marginBottom:8 }}>
              <span style={{ color:"#3B82F6",fontWeight:700,fontSize:12 }}>Takes — {t.name}</span>
              <button onClick={function(){ setShowTakes(null); }} style={{ background:"none",border:"none",color:"#555",fontSize:16,cursor:"pointer" }}>✕</button>
            </div>
            {t.clips.map(function(cl,idx){
              return (
                <div key={cl.id} style={{ display:"flex",alignItems:"center",gap:8,marginBottom:6,padding:"6px 8px",background:cl.active?"rgba(59,130,246,0.1)":"#111",borderRadius:8,border:"1px solid "+(cl.active?"#3B82F6":"#1e1e1e") }}>
                  <div style={{ width:5,height:5,borderRadius:"50%",background:cl.active?"#3B82F6":"#333" }} />
                  <span style={{ color:cl.active?"white":"#555",fontSize:11,flex:1 }}>{cl.label || ("Take "+(idx+1))}</span>
                  <span style={{ color:"#333",fontSize:9 }}>{cl.duration?cl.duration.toFixed(1)+"s":""}</span>
                  <button onClick={function(){ setActiveClip(t.id, cl.id); }} style={{ background:cl.active?"#3B82F6":"#1e1e1e",border:"none",borderRadius:5,color:"white",fontSize:9,padding:"3px 8px",cursor:"pointer",fontWeight:700 }}>{cl.active?"Active":"Use"}</button>
                  <button onClick={function(){ removeClip(t.id, cl.id); }} style={{ background:"none",border:"none",color:"#444",fontSize:12,cursor:"pointer" }}>⌫</button>
                </div>
              );
            })}
          </div>
        );
      })()}

      {/* ══ GARAGEBAND-STYLE MIXER ══════════════════════════════ */}
      {showMixer && (function(){
        // GB mixer: dark #1c1c1e panel, per-channel strips with:
        //   knob pan, tall vertical fader with GB-style thumb, dual VU bars, M/S buttons, colour tab at bottom

        return (
          <div style={{
            background: "linear-gradient(180deg, #0f0a1e 0%, #0a0716 100%)",
            borderTop: "1px solid " + GB_BORDER,
            boxShadow: "0 -8px 24px rgba(0,0,0,0.5), inset 0 1px 0 rgba(167,139,250,0.08)",
            flexShrink: 0,
            maxHeight: "58vh",
            display: "flex",
            flexDirection: "column",
          }} onClick={function(e){ e.stopPropagation(); }} onTouchMove={function(e){ e.stopPropagation(); }}>

            {/* ── Mixer header bar — neon-purple branded ── */}
            <div style={{
              display:"flex", alignItems:"center", justifyContent:"space-between",
              padding:"10px 16px 8px",
              borderBottom:"1px solid " + GB_BORDER,
              background: "linear-gradient(180deg, rgba(167,139,250,0.06), transparent)",
              flexShrink:0,
            }}>
              <div style={{ display:"flex", alignItems:"center", gap:8 }}>
                {/* Tiny neon LED indicator */}
                <div style={{
                  width:8, height:8, borderRadius:"50%",
                  background: "#a78bfa",
                  boxShadow: "0 0 8px rgba(167,139,250,0.8), 0 0 14px rgba(139,92,246,0.4)",
                }}/>
                <span style={{
                  color:"#e8e6f0", fontSize:12, fontWeight:800, letterSpacing:2,
                  textTransform:"uppercase",
                }}>Mixer</span>
              </div>
              <button onClick={function(){ setShowMixer(false); }} style={{
                background:"rgba(167,139,250,0.08)",
                border:"1px solid #2a2050",
                borderRadius:6,
                color:GB_ACCENT, fontSize:16, fontWeight:700,
                cursor:"pointer", lineHeight:1, padding:"3px 9px",
              }}>×</button>
            </div>

            {/* ── Channel strip scroll area ── */}
            <div
              style={{ overflowX:"auto", overflowY:"hidden", WebkitOverflowScrolling:"touch", overscrollBehavior:"none", flex:1, minHeight:0 }}
              onTouchMove={function(e){
                e.stopPropagation();
                // Block horizontal scroll entirely while a fader is being dragged
                if (gbFaderDragging) { e.preventDefault(); return; }
                const el = e.currentTarget;
                const atLeft  = el.scrollLeft <= 0;
                const atRight = el.scrollLeft >= el.scrollWidth - el.clientWidth - 1;
                const dx = e.touches[0].clientX - (e.currentTarget._startX || e.touches[0].clientX);
                if ((atLeft && dx > 0) || (atRight && dx < 0)) e.preventDefault();
              }}
              onTouchStart={function(e){
                e.currentTarget._startX = e.touches[0].clientX;
              }}
            >
              <div style={{ display:"flex", gap:0, minWidth:"max-content", padding:"10px 8px 0" }}>

                {tracks.map(function(t, ti){
                  const vol     = t.volume ?? 1;
                  const pan     = t.pan    || 0;
                  const muted   = !!t.isMuted;
                  const soloed  = !!t.isSoloed;
                  const hasSolo = tracks.some(function(x){ return x.isSoloed; });
                  const dimmed  = hasSolo && !soloed && !muted;
                  const active  = isPlaying && !muted && (!hasSolo || soloed);

                  // dB readout: 0→−∞, 1→0dB, 1.5→+3.5dB
                  const volDb   = vol <= 0 ? "−∞" : (20*Math.log10(vol)).toFixed(1);

                  return (
                    <div key={t.id} style={{
                      width: STRIP_W,
                      display:"flex", flexDirection:"column", alignItems:"center",
                      borderRight:"1px solid " + GB_BORDER,
                      paddingBottom:0,
                      opacity: dimmed ? 0.38 : 1,
                      transition:"opacity 0.15s",
                      background: soloed ? "linear-gradient(180deg, rgba(253,224,71,0.06), transparent)" : "transparent",
                    }}>

                      {/* ── Pan knob ── */}
                      <div style={{ display:"flex",flexDirection:"column",alignItems:"center",gap:1,paddingBottom:6,paddingTop:2 }}>
                        <GBPanKnob pan={pan} color="#8e8e93"
                          onChange={function(v){ updateTrack(t.id,{pan:v}); }} />
                        <span style={{
                          fontSize:7, fontWeight:600, color: GB_LABEL,
                          letterSpacing:0.2,
                        }}>
                          {pan===0?"C":pan>0?"R "+Math.round(pan*100):"L "+Math.round(Math.abs(pan)*100)}
                        </span>
                      </div>

                      {/* ── Fader + VU side by side ── */}
                      <div style={{ display:"flex", alignItems:"flex-start", gap:3, paddingBottom:6 }}>
                        <GBFader vol={vol} color="#636366" muted={muted}
                          onChange={function(v){ updateTrack(t.id,{volume:v}); }}
                          onBeginGesture={beginTracksGesture}
                          onEndGesture={endTracksGesture} />
                        <GBVUBars analyserNode={trackAnalysersRef.current[t.id]||null} active={active} />
                      </div>

                      {/* ── dB readout ── */}
                      <div style={{
                        fontSize:8, fontWeight:700,
                        color: muted ? "#5a4870"
                             : vol === 0 ? "#ff3b6b"
                             : vol > 1 ? "#f472b6"
                             : "#a78bfa",
                        marginBottom:4, letterSpacing:0.5,
                        textShadow: (muted || vol === 0) ? "none" : "0 0 4px rgba(167,139,250,0.35)",
                      }}>
                        {muted ? "mute" : volDb+" dB"}
                      </div>

                      {/* ── M / S buttons — neon LED style ── */}
                      <div style={{ display:"flex", gap:3, width:"100%", padding:"0 5px", marginBottom:6 }}>
                        <button onClick={function(e){ e.stopPropagation(); toggleMute(t.id); }} style={{
                          flex:1, padding:"4px 0",
                          background: muted
                            ? "linear-gradient(180deg, #fb923c, #ea580c)"
                            : "linear-gradient(180deg, #1f1735, #15102a)",
                          border:"1px solid " + (muted ? "#fdba74" : "#2a2050"),
                          borderRadius:4,
                          color: muted ? "#1a0a00" : GB_LABEL,
                          fontSize:10, fontWeight:800, cursor:"pointer",
                          boxShadow: muted
                            ? "0 0 10px rgba(251,146,60,0.7), 0 1px 0 rgba(255,255,255,0.25) inset"
                            : "0 1px 0 rgba(167,139,250,0.05) inset",
                          transition:"all 0.12s",
                        }}>M</button>
                        <button onClick={function(e){ e.stopPropagation(); toggleSolo(t.id); }} style={{
                          flex:1, padding:"4px 0",
                          background: soloed
                            ? "linear-gradient(180deg, #fde047, #facc15)"
                            : "linear-gradient(180deg, #1f1735, #15102a)",
                          border:"1px solid " + (soloed ? "#fef08a" : "#2a2050"),
                          borderRadius:4,
                          color: soloed ? "#1a1500" : GB_LABEL,
                          fontSize:10, fontWeight:800, cursor:"pointer",
                          boxShadow: soloed
                            ? "0 0 12px rgba(253,224,71,0.7), 0 1px 0 rgba(255,255,255,0.25) inset"
                            : "0 1px 0 rgba(167,139,250,0.05) inset",
                          transition:"all 0.12s",
                        }}>S</button>
                      </div>

                      {/* ── Track name tab — track color LED bar on top ── */}
                      <div style={{
                        width:"100%", height:22,
                        background:"linear-gradient(180deg, #1f1735, #15102a)",
                        borderTop:"2px solid " + t.color,
                        boxShadow: "0 -2px 6px " + t.color + "55",
                        display:"flex", alignItems:"center", justifyContent:"center",
                        overflow:"hidden",
                      }}>
                        <span style={{
                          color:"#c4b5fd", fontSize:7, fontWeight:800,
                          letterSpacing:0.5,
                          overflow:"hidden", textOverflow:"ellipsis", whiteSpace:"nowrap",
                          padding:"0 4px", maxWidth:"100%",
                        }}>{t.name}</span>
                      </div>

                    </div>
                  );
                })}

                {/* ── Master strip — neon-purple branded ── */}
                {(function(){
                  const masterDb = masterVolume <= 0 ? "−∞" : (20*Math.log10(masterVolume)).toFixed(1);
                  return (
                    <div style={{
                      width: STRIP_W+4,
                      display:"flex", flexDirection:"column", alignItems:"center",
                      background: "linear-gradient(180deg, #1a1230 0%, #15102a 100%)",
                      borderLeft:"2px solid " + GB_ACCENT,
                      boxShadow: "inset 2px 0 8px rgba(167,139,250,0.15)",
                    }}>
                      {/* MST label */}
                      <div style={{ height:38, display:"flex", alignItems:"center", justifyContent:"center" }}>
                        <span style={{
                          color: GB_ACCENT, fontSize:8, fontWeight:800, letterSpacing:1.5,
                          textShadow: "0 0 6px rgba(167,139,250,0.5)",
                        }}>MST</span>
                      </div>
                      {/* Master fader — only changes masterVolume, never touches tracks */}
                      <div style={{ display:"flex", alignItems:"flex-start", gap:3, paddingBottom:6 }}>
                        <GBFader vol={masterVolume} color="#c4b5fd" muted={false}
                          onChange={function(v){ setMasterVolume(v); }} />
                        <GBVUBars analyserNode={masterAnalyserRef.current} active={isPlaying} />
                      </div>
                      {/* dB */}
                      <div style={{ fontSize:8,fontWeight:700,color:"#c4b5fd",marginBottom:4,letterSpacing:0.5 }}>
                        {masterDb} dB
                      </div>
                      {/* Spacer where M/S would be */}
                      <div style={{ height:26 }} />
                      {/* Master label tab — glowing neon */}
                      <div style={{
                        width:"100%", height:22,
                        background:"linear-gradient(180deg, #8b5cf6, #6d28d9)",
                        boxShadow:"0 -2px 12px rgba(167,139,250,0.5), inset 0 1px 0 rgba(255,255,255,0.2)",
                        display:"flex",alignItems:"center",justifyContent:"center",
                      }}>
                        <span style={{
                          color:"#fff",fontSize:8,fontWeight:800,letterSpacing:1.5,
                          textShadow:"0 0 4px rgba(255,255,255,0.6)",
                        }}>MASTER</span>
                      </div>
                    </div>
                  );
                })()}

              </div>
            </div>
          </div>
        );
      })()}

      {/* ══ CLIP NUDGE BAR ══════════════════════════════════════════
          Floating bar that appears when a clip is selected. Lets the user
          shift the clip's startTime in fine increments (±5ms) without
          having to drag horizontally on the timeline — useful for aligning
          vocals to the beat after recording.

          The slider is RELATIVE — its center (0) represents the clip's
          startTime at the moment of selection. Moving the slider right
          pushes the clip later in time; left pulls it earlier. Tap "Reset"
          to snap back to the selection-time position.

          Hidden when no clip is selected. Sits ABOVE the transport so it
          doesn't block playback controls. Also hidden when FX panel is open
          (the clip selection logic in that view uses different patterns). */}
      {(function(){
        if (!selectedClipId) return null;
        if (fxTrackId) return null;  // hide while FX panel is open
        if (contextMenu) return null;  // hide while clip context menu is open
                                       // (Duplicate / Move / Delete menu sits
                                       // above the timeline near where the
                                       // nudge bar would render — overlap
                                       // makes the menu unreadable)
        // Find the selected clip and compute its current displacement from
        // its persistent nudgeOriginStart. Fallback to current startTime
        // for legacy clips so the readout shows 0 instead of garbage.
        let currentOffsetMs = 0;
        const tracksNow = tracksRef.current || [];
        for (const tr of tracksNow) {
          const cl = (tr.clips || []).find(function(c){ return c.id === selectedClipId; });
          if (cl) {
            const orig = (cl.nudgeOriginStart !== undefined) ? cl.nudgeOriginStart : (cl.startTime || 0);
            currentOffsetMs = Math.round(((cl.startTime || 0) - orig) * 1000);
            break;
          }
        }
        return (
          <div
            onClick={function(e){ e.stopPropagation(); }}
            onMouseDown={function(e){ e.stopPropagation(); }}
            onTouchStart={function(e){ e.stopPropagation(); }}
            onPointerDown={function(e){ e.stopPropagation(); }}
            style={{
              position:"fixed", left:0, right:0,
              // Sit above the transport: transport is ~80px tall (varies with safe area).
              bottom: "calc(72px + max(env(safe-area-inset-bottom), 12px))",
              background:"linear-gradient(180deg,rgba(20,15,35,0.95) 0%,rgba(15,10,25,0.97) 100%)",
              borderTop:"1px solid rgba(124,58,237,0.2)",
              borderBottom:"1px solid rgba(124,58,237,0.1)",
              padding:"12px 14px",
              zIndex:9050,
              backdropFilter:"blur(8px)",
              display:"flex", alignItems:"center", gap:12,
              fontFamily:"'DM Sans',sans-serif",
              boxShadow:"0 -4px 12px rgba(0,0,0,0.4)",
            }}>
            {/* Label + readout. Readout PULSES (scale + glow) every time the
                value changes so the user gets clear visual confirmation. */}
            <div style={{ minWidth:90, flexShrink:0 }}>
              <div style={{ color:"#aaa", fontSize:9, fontWeight:700, letterSpacing:1.2, textTransform:"uppercase" }}>
                Nudge Clip
              </div>
              <div
                key={"nudge-readout-" + nudgePulseTick}
                style={{
                  color:currentOffsetMs===0?"#666":"#A78BFA",
                  fontSize:16, fontWeight:900, fontFamily:"monospace",
                  marginTop:2,
                  textShadow: currentOffsetMs===0 ? "none" : "0 0 10px rgba(167,139,250,0.5)",
                  animation: "bfNudgePulse 0.35s ease-out",
                }}>
                {currentOffsetMs > 0 ? "+" : ""}{currentOffsetMs} ms
              </div>
            </div>
            {/* Spacer — pushes buttons to the right */}
            <div style={{ flex:1 }} />
            {/* Nudge buttons — ±5 ms and ±50 ms increments, plus Reset.
                Slider was removed — fiddly on mobile and the buttons give
                clearer feedback via pulse + haptic. */}
            <div style={{ display:"flex", gap:5, flexShrink:0, flexWrap:"nowrap" }}>
              <button onClick={function(e){
                  e.stopPropagation();
                  nudgeSelectedClip(-50);
                  setNudgePulseTick(function(n){ return n + 1; });
                  try { if (navigator.vibrate) navigator.vibrate(14); } catch(_){}
                }}
                style={{
                  background:"linear-gradient(180deg,#2a2050,#1a1535)",
                  border:"1px solid #3a3060", borderRadius:7,
                  color:"#A78BFA", fontSize:11, fontWeight:800,
                  padding:"8px 9px", cursor:"pointer", fontFamily:"monospace",
                  minWidth:42, boxShadow:"inset 0 1px 0 rgba(167,139,250,0.15)",
                }}>
                −50
              </button>
              <button onClick={function(e){
                  e.stopPropagation();
                  nudgeSelectedClip(-5);
                  setNudgePulseTick(function(n){ return n + 1; });
                  try { if (navigator.vibrate) navigator.vibrate(12); } catch(_){}
                }}
                style={{
                  background:"linear-gradient(180deg,#2a2050,#1a1535)",
                  border:"1px solid #3a3060", borderRadius:7,
                  color:"#A78BFA", fontSize:12, fontWeight:800,
                  padding:"8px 11px", cursor:"pointer", fontFamily:"monospace",
                  minWidth:40, boxShadow:"inset 0 1px 0 rgba(167,139,250,0.15)",
                }}>
                −5
              </button>
              <button onClick={function(e){
                  e.stopPropagation();
                  nudgeSelectedClip(5);
                  setNudgePulseTick(function(n){ return n + 1; });
                  try { if (navigator.vibrate) navigator.vibrate(12); } catch(_){}
                }}
                style={{
                  background:"linear-gradient(180deg,#2a2050,#1a1535)",
                  border:"1px solid #3a3060", borderRadius:7,
                  color:"#A78BFA", fontSize:12, fontWeight:800,
                  padding:"8px 11px", cursor:"pointer", fontFamily:"monospace",
                  minWidth:40, boxShadow:"inset 0 1px 0 rgba(167,139,250,0.15)",
                }}>
                +5
              </button>
              <button onClick={function(e){
                  e.stopPropagation();
                  nudgeSelectedClip(50);
                  setNudgePulseTick(function(n){ return n + 1; });
                  try { if (navigator.vibrate) navigator.vibrate(14); } catch(_){}
                }}
                style={{
                  background:"linear-gradient(180deg,#2a2050,#1a1535)",
                  border:"1px solid #3a3060", borderRadius:7,
                  color:"#A78BFA", fontSize:11, fontWeight:800,
                  padding:"8px 9px", cursor:"pointer", fontFamily:"monospace",
                  minWidth:42, boxShadow:"inset 0 1px 0 rgba(167,139,250,0.15)",
                }}>
                +50
              </button>
              <button onClick={function(e){
                  e.stopPropagation();
                  setSelectedClipNudge(0);
                  setNudgePulseTick(function(n){ return n + 1; });
                  try { if (navigator.vibrate) navigator.vibrate(6); } catch(_){}
                }}
                style={{
                  background:"#0a0814", border:"1px solid #2a2545", borderRadius:7,
                  color:"#888", fontSize:11, fontWeight:700,
                  padding:"8px 12px", cursor:"pointer",
                }}>
                Reset
              </button>
            </div>
          </div>
        );
      })()}

      {/* ══ TRANSPORT ════════════════════════════════════════════
          Fixed to the bottom of the viewport so the user can always
          reach Record/Play/seek/etc. — even when the FX, Mixer, Takes,
          or Lyrics panel is open. z-index sits above all overlays
          (FX panel = 800, Lyrics panel = 9001, so 9100 wins). */}
      <div style={{
        position:"fixed", left:0, right:0, bottom:0,
        background:"linear-gradient(180deg,rgba(15,10,31,0.96) 0%,rgba(10,10,20,0.98) 100%)",
        borderTop:"1px solid rgba(124,58,237,0.25)",
        padding:"8px 12px",
        paddingBottom:"max(env(safe-area-inset-bottom), 12px)",
        flexShrink:0,
        zIndex:9100,
        backdropFilter:"blur(8px)",
        boxShadow:"0 -8px 24px rgba(0,0,0,0.5), 0 -1px 0 rgba(124,58,237,0.15)",
        // When FX panel is open, hide the main transport entirely. The FX
        // screen has its OWN transport bar rendered inside its own subtree,
        // so there's no shared-button-touch-clash with the timeline.
        // display:"none" fully removes from layout AND hit-testing.
        display: fxTrackId ? "none" : undefined,
      }}>
        {/* LED top edge — subtle accent */}
        <div style={{
          position:"absolute", top:0, left:0, right:0, height:1,
          background:"linear-gradient(90deg,transparent,rgba(192,38,211,0.4),rgba(124,58,237,0.4),transparent)",
        }} />
        <div style={{ display:"flex",alignItems:"center",justifyContent:"space-between",gap:6 }}>
          {/* Mixer toggle */}
          <div style={{ width:36,height:36,borderRadius:10,background:showMixer?"rgba(139,92,246,0.2)":"#141414",border:"1px solid "+(showMixer?"#8B5CF6":"#222"),display:"flex",alignItems:"center",justifyContent:"center",cursor:"pointer",flexShrink:0 }} onClick={function(){ setShowMixer(function(v){return !v;}); }}>
            <Icon id="fader" size={18} color={showMixer ? "#8B5CF6" : "#aaa"} strokeWidth={1.8}/>
          </div>
          {/* Skip to Start */}
          <button onClick={rewind} title="Skip to start" style={{ width:32,height:32,borderRadius:8,background:"#141414",border:"1px solid #222",cursor:"pointer",display:"flex",alignItems:"center",justifyContent:"center",flexShrink:0 }}>
            <svg width="15" height="15" viewBox="0 0 24 24" fill="white"><path d="M6 6h2v12H6zm3.5 6 8.5 6V6z"/></svg>
          </button>
          {/* Rewind –5s */}
          <button onClick={seekBack} title="Rewind 5s" style={{ width:32,height:32,borderRadius:8,background:"#141414",border:"1px solid #222",cursor:"pointer",display:"flex",alignItems:"center",justifyContent:"center",flexShrink:0 }}>
            <svg width="15" height="15" viewBox="0 0 24 24" fill="white"><path d="M12.5 8.5 7 12l5.5 3.5V8.5z"/><path d="M19.5 8.5 14 12l5.5 3.5V8.5z"/></svg>
          </button>
          {/* Record */}
          <button onClick={function(){
            if(isRecording){ stopRecording(); return; }
            // PDC + drift compensation now handle FX-on-channel timing
            // automatically, so no warning is needed.
            startCountIn(selectedTrackId);
          }} disabled={countIn>0} style={{ width:48,height:48,borderRadius:"50%",background:isRecording?"#EF4444":"linear-gradient(135deg,#EF4444,#DC2626)",border:isRecording?"3px solid rgba(239,68,68,0.5)":"3px solid rgba(239,68,68,0.2)",cursor:countIn>0?"not-allowed":"pointer",opacity:countIn>0?0.4:1,display:"flex",alignItems:"center",justifyContent:"center",boxShadow:isRecording?"0 0 20px rgba(239,68,68,0.5)":"none",flexShrink:0 }}>
            {isRecording?<div style={{ width:14,height:14,background:"white",borderRadius:3 }} />:<div style={{ width:18,height:18,background:"white",borderRadius:"50%" }} />}
          </button>
          {/* Play / Pause */}
          <button onClick={togglePlay} style={{ width:36,height:36,borderRadius:"50%",background:"linear-gradient(135deg,#C026D3,#7C3AED)",border:"none",cursor:"pointer",display:"flex",alignItems:"center",justifyContent:"center",flexShrink:0 }}>
            {isPlaying?<svg width="15" height="15" viewBox="0 0 24 24" fill="white"><path d="M6 19h4V5H6v14zm8-14v14h4V5h-4z"/></svg>:<svg width="15" height="15" viewBox="0 0 24 24" fill="white"><path d="M8 5v14l11-7z"/></svg>}
          </button>
          {/* Fast Forward +5s */}
          <button onClick={seekForward} title="Fast forward 5s" style={{ width:32,height:32,borderRadius:8,background:"#141414",border:"1px solid #222",cursor:"pointer",display:"flex",alignItems:"center",justifyContent:"center",flexShrink:0 }}>
            <svg width="15" height="15" viewBox="0 0 24 24" fill="white"><path d="M11.5 8.5 17 12l-5.5 3.5V8.5z"/><path d="M4.5 8.5 10 12 4.5 15.5V8.5z"/></svg>
          </button>
          {/* Skip to End */}
          <button onClick={skipToEnd} title="Skip to end" style={{ width:32,height:32,borderRadius:8,background:"#141414",border:"1px solid #222",cursor:"pointer",display:"flex",alignItems:"center",justifyContent:"center",flexShrink:0 }}>
            <svg width="15" height="15" viewBox="0 0 24 24" fill="white"><path d="M16 6h2v12h-2zm-2.5 6L5 6v12z"/></svg>
          </button>
        </div>
      </div>

      {/* ── Lyrics workspace panel (BandLab-style side overlay) ──
          Lets the user read or write lyrics while recording. Slides in
          from the right, sits above the timeline but doesn't block it. */}
      {lyricsPanelOpen && (
        <>
          {/* Tap-outside backdrop */}
          <div onClick={function(){ setLyricsPanelOpen(false); }}
            style={{ position: "fixed", inset: 0, zIndex: 9000, background: "rgba(0,0,0,0.4)", backdropFilter: "blur(2px)" }} />
          {/* The panel */}
          <div onClick={function(e){ e.stopPropagation(); }}
            style={{
              position: "fixed", top: 0, right: 0,
              bottom: "calc(80px + env(safe-area-inset-bottom))",
              width: "min(380px, 88vw)", zIndex: 9001,
              background: "linear-gradient(165deg,#0f0a1f 0%,#0a0a14 60%,#080812 100%)",
              borderLeft: "1px solid rgba(124,58,237,0.3)",
              boxShadow: "-12px 0 40px rgba(0,0,0,0.7), 0 0 24px rgba(124,58,237,0.15)",
              display: "flex", flexDirection: "column",
              paddingTop: "var(--bf-safe-top, env(safe-area-inset-top))",
              fontFamily: "'DM Sans',sans-serif",
            }}>
            {/* LED top edge */}
            <div style={{
              height: 2,
              background: "linear-gradient(90deg,transparent,#C026D3,#7C3AED,#3B82F6,transparent)",
              boxShadow: "0 0 8px rgba(124,58,237,0.7)",
            }} />
            {/* Header */}
            <div style={{
              display: "flex", alignItems: "center", justifyContent: "space-between",
              padding: "14px 16px", borderBottom: "1px solid rgba(124,58,237,0.15)",
              flexShrink: 0,
            }}>
              <div style={{ display: "flex", alignItems: "center", gap: 10, flex: 1, minWidth: 0 }}>
                <div style={{
                  width: 32, height: 32, borderRadius: 9,
                  background: "linear-gradient(135deg,#C026D3,#7C3AED,#3B82F6)",
                  boxShadow: "0 0 12px rgba(124,58,237,0.5)",
                  display: "flex", alignItems: "center", justifyContent: "center",
                  flexShrink: 0,
                }}>
                  <svg width="15" height="15" viewBox="0 0 24 24" fill="none" stroke="white" strokeWidth="2.2" strokeLinecap="round" strokeLinejoin="round">
                    <path d="M14 2H6a2 2 0 0 0-2 2v16a2 2 0 0 0 2 2h12a2 2 0 0 0 2-2V8z"/>
                    <polyline points="14 2 14 8 20 8"/>
                    <line x1="16" y1="13" x2="8" y2="13"/>
                    <line x1="16" y1="17" x2="8" y2="17"/>
                  </svg>
                </div>
                <div style={{ minWidth: 0 }}>
                  <div style={{
                    color: "white", fontSize: 18, fontWeight: 800,
                    fontFamily: "'Bebas Neue',sans-serif", letterSpacing: 1, lineHeight: 1,
                  }}>LYRICS</div>
                  <div style={{ color: "#888", fontSize: 11, marginTop: 2 }}>
                    Write while you record
                  </div>
                </div>
              </div>
              <button onClick={function(){ setLyricsPanelOpen(false); }}
                style={{
                  width: 32, height: 32, borderRadius: "50%",
                  background: "rgba(255,255,255,0.04)",
                  border: "1px solid #2a2a2a", color: "#888",
                  cursor: "pointer", flexShrink: 0,
                  display: "flex", alignItems: "center", justifyContent: "center",
                }}>
                <svg width="12" height="12" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round">
                  <line x1="18" y1="6" x2="6" y2="18"/><line x1="6" y1="6" x2="18" y2="18"/>
                </svg>
              </button>
            </div>

            {/* New Blank Lyric button */}
            {onNewLyric && (
              <div style={{ padding: "14px 16px 8px", flexShrink: 0 }}>
                <button onClick={function(){ setLyricsPanelOpen(false); onNewLyric(); }}
                  style={{
                    width: "100%",
                    background: "linear-gradient(135deg,#C026D3 0%,#A855F7 50%,#7C3AED 100%)",
                    border: "none", borderRadius: 12,
                    color: "white", fontWeight: 900, fontSize: 13, letterSpacing: 0.6,
                    padding: "12px", cursor: "pointer",
                    display: "flex", alignItems: "center", justifyContent: "center", gap: 8,
                    boxShadow: "0 4px 16px rgba(192,38,211,0.35), inset 0 1px 0 rgba(255,255,255,0.18)",
                    textShadow: "0 1px 2px rgba(0,0,0,0.3)",
                  }}>
                  <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="white" strokeWidth="2.5" strokeLinecap="round">
                    <line x1="12" y1="5" x2="12" y2="19"/><line x1="5" y1="12" x2="19" y2="12"/>
                  </svg>
                  NEW BLANK LYRIC
                </button>
              </div>
            )}

            {/* Section label */}
            <div style={{
              padding: "10px 16px 8px", flexShrink: 0,
              display: "flex", alignItems: "center", justifyContent: "space-between",
            }}>
              <div style={{
                color: "#A78BFA", fontSize: 10, fontWeight: 900, letterSpacing: 1.5,
                textShadow: "0 0 6px rgba(124,58,237,0.5)",
              }}>SAVED LYRICS</div>
              {savedLyrics && savedLyrics.length > 0 && (
                <div style={{
                  background: "rgba(124,58,237,0.15)",
                  border: "1px solid rgba(124,58,237,0.3)",
                  color: "#A78BFA", fontSize: 9, fontWeight: 900, letterSpacing: 0.5,
                  padding: "2px 8px", borderRadius: 12,
                }}>{savedLyrics.length}</div>
              )}
            </div>

            {/* Saved lyrics list — scrollable */}
            <div style={{
              flex: 1, overflowY: "auto", WebkitOverflowScrolling: "touch",
              padding: "0 16px 16px",
            }}>
              {(!savedLyrics || savedLyrics.length === 0) ? (
                <div style={{
                  textAlign: "center", padding: "40px 16px",
                  background: "rgba(0,0,0,0.3)",
                  border: "1px solid rgba(124,58,237,0.12)",
                  borderRadius: 12,
                }}>
                  <div style={{ marginBottom: 10, color: "#A78BFA", opacity: 0.6 }}>
                    <svg width="42" height="42" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round"
                      style={{ filter: "drop-shadow(0 0 8px rgba(124,58,237,0.4))" }}>
                      <path d="M14 2H6a2 2 0 0 0-2 2v16a2 2 0 0 0 2 2h12a2 2 0 0 0 2-2V8z"/>
                      <polyline points="14 2 14 8 20 8"/>
                      <line x1="16" y1="13" x2="8" y2="13"/>
                      <line x1="16" y1="17" x2="8" y2="17"/>
                    </svg>
                  </div>
                  <div style={{ color: "white", fontSize: 13, fontWeight: 700, marginBottom: 4 }}>
                    No saved lyrics yet
                  </div>
                  <div style={{ color: "#888", fontSize: 11, lineHeight: 1.5 }}>
                    Tap NEW BLANK LYRIC above to start writing.
                  </div>
                </div>
              ) : (
                savedLyrics.map(function(lyric, i) {
                  return (
                    <button key={i}
                      onClick={function(){ setLyricsPanelOpen(false); onEditLyric && onEditLyric(lyric, i); }}
                      style={{
                        display: "block", width: "100%", textAlign: "left",
                        background: "linear-gradient(165deg,rgba(15,10,31,0.7) 0%,rgba(10,10,20,0.85) 100%)",
                        border: "1px solid rgba(124,58,237,0.18)",
                        borderRadius: 12, padding: "12px 14px", marginBottom: 8,
                        cursor: "pointer",
                        position: "relative", overflow: "hidden",
                      }}>
                      {/* tiny LED edge */}
                      <div style={{
                        position: "absolute", top: 0, left: 0, right: 0, height: 1,
                        background: "linear-gradient(90deg,transparent,rgba(192,38,211,0.5),transparent)",
                      }} />
                      <div style={{ color: "white", fontWeight: 700, fontSize: 13, marginBottom: 3,
                        overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" }}>
                        {lyric.title || "Untitled"}
                      </div>
                      {lyric.beatTitle && (
                        <div style={{ color: "#888", fontSize: 11, marginBottom: 6,
                          overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" }}>
                          🎵 {lyric.beatTitle}
                        </div>
                      )}
                      {lyric.text && (
                        <div style={{
                          color: "#777", fontSize: 11, lineHeight: 1.5,
                          display: "-webkit-box", WebkitLineClamp: 2, WebkitBoxOrient: "vertical",
                          overflow: "hidden",
                        }}>
                          {lyric.text}
                        </div>
                      )}
                      <div style={{ display: "flex", alignItems: "center", gap: 6, marginTop: 8 }}>
                        <svg width="11" height="11" viewBox="0 0 24 24" fill="none" stroke="#A78BFA" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                          <path d="M12 20h9"/><path d="M16.5 3.5a2.121 2.121 0 013 3L7 19l-4 1 1-4L16.5 3.5z"/>
                        </svg>
                        <span style={{ color: "#A78BFA", fontSize: 10, fontWeight: 700, letterSpacing: 0.5 }}>
                          TAP TO EDIT
                        </span>
                      </div>
                    </button>
                  );
                })
              )}
            </div>
          </div>
        </>
      )}

    </div>
  );
}


const NAV = [
  { id: "home",      label: "Home",    icon: "home" },
  { id: "artists",   label: "Artists", icon: "artists" },
  { id: "feed",      label: "Feed",    icon: "feed" },
  { id: "trending",  label: "Trending",icon: "trending" },
  { id: "search",    label: "Search",  icon: "search" },
  { id: "saved",     label: "Saved",   icon: "saved" },
  { id: "exclusive", label: "Members", icon: "members" },
  { id: "studio",    label: "Studio",  icon: "studio" },
];

// =============================================================================
// ICON SYSTEM — Lucide icons unified across nav + app
// Usage: <Icon id="music" size={20} color="#fff" strokeWidth={1.5} />
// =============================================================================
const LucideIcons = {
  // Nav
  home:       "M3 9l9-7 9 7v11a2 2 0 01-2 2H5a2 2 0 01-2-2z M9 22V12h6v10",
  artists:    "M3 3h7v7H3z M14 3h7v7h-7z M14 14h7v7h-7z M3 14h7v7H3z",
  feed:       "M5 12.55a11 11 0 0114.08 0 M1.42 9a16 16 0 0121.16 0 M8.53 16.11a6 6 0 016.95 0 M12 20h.01",
  trending:   "M13 2c0 0-7 6-7 12a7 7 0 0014 0c0-3.5-2-7-3-9 0 2.5-1.5 4-2.5 5.5C14.5 8.5 13 5 13 2z",
  search:     "M21 21l-4.35-4.35M17 11A6 6 0 115 11a6 6 0 0112 0z",
  saved:      "M19 21l-7-5-7 5V5a2 2 0 012-2h10a2 2 0 012 2z",
  members:    "M18 11V8a6 6 0 00-12 0v3 M5 11h14a2 2 0 012 2v7a2 2 0 01-2 2H5a2 2 0 01-2-2v-7a2 2 0 012-2z M12 15v3",
  profile:    "M20 21v-2a4 4 0 00-4-4H8a4 4 0 00-4 4v2 M12 11a4 4 0 100-8 4 4 0 000 8z",
  studio:     "M12 1a3 3 0 00-3 3v8a3 3 0 006 0V4a3 3 0 00-3-3z M19 10v2a7 7 0 01-14 0v-2 M12 19v4 M8 23h8",
  // App icons
  note:       "M9 18V5l12-2v13 M9 18a3 3 0 11-6 0 3 3 0 016 0z M21 16a3 3 0 11-6 0 3 3 0 016 0z",
  knobs:      "M12 3v1m0 16v1M3 12h1m16 0h1m-2.636-6.364l-.707.707M6.343 17.657l-.707.707m0-12.728l.707.707M17.657 17.657l.707.707 M12 8a4 4 0 100 8 4 4 0 000-8z",
  bookmark:   "M19 21l-7-5-7 5V5a2 2 0 012-2h10a2 2 0 012 2z",
  lock:       "M18 11V8a6 6 0 00-12 0v3 M5 11h14a2 2 0 012 2v7a2 2 0 01-2 2H5a2 2 0 01-2-2v-7a2 2 0 012-2z M12 15v3",
  lockkey:    "M21 2l-2 2m-7.61 7.61a5.5 5.5 0 11-7.778 7.778 5.5 5.5 0 017.777-7.777zm0 0L15.5 7.5m0 0l3 3L22 7l-3-3m-3.5 3.5L19 4",
  mic:        "M12 1a3 3 0 00-3 3v8a3 3 0 006 0V4a3 3 0 00-3-3z M19 10v2a7 7 0 01-14 0v-2 M12 19v4 M8 23h8",
  vocalmic:   "M12 1a3 3 0 00-3 3v8a3 3 0 006 0V4a3 3 0 00-3-3z M19 10v2a7 7 0 01-14 0v-2 M12 19v4 M8 23h8",
  headphones: "M3 18v-6a9 9 0 0118 0v6 M21 19a2 2 0 01-2 2h-1a2 2 0 01-2-2v-3a2 2 0 012-2h3z M3 19a2 2 0 002 2h1a2 2 0 002-2v-3a2 2 0 00-2-2H3z",
  piano:      "M9 18H5a2 2 0 01-2-2V6a2 2 0 012-2h14a2 2 0 012 2v10a2 2 0 01-2 2h-4 M9 18v4 M15 18v4 M9 6v6 M15 6v6 M12 6v3",
  fader:      "M4 21V14 M4 10V3 M12 21v-7 M12 10V3 M20 21v-4 M20 13V3 M1 14h6 M9 10h6 M17 13h6",
  flame:      "M8.5 14.5A2.5 2.5 0 0011 12c0-1.38-.5-2-1-3-1.072-2.143-.224-4.054 2-6 .5 2.5 2 4.9 4 6.5 2 1.6 3 3.5 3 5.5a7 7 0 11-14 0c0-1.153.433-2.294 1-3a2.5 2.5 0 002.5 2.5z",
  target:     "M12 22a10 10 0 100-20 10 10 0 000 20z M12 18a6 6 0 100-12 6 6 0 000 12z M12 14a2 2 0 100-4 2 2 0 000 4z",
  wave:       "M2 12 C5 8 7 16 10 12 C13 8 15 16 18 12 C21 8 22 12 22 12",
  globe:      "M12 2a10 10 0 100 20A10 10 0 0012 2z M2 12h20 M12 2a15.3 15.3 0 010 20 M12 2a15.3 15.3 0 000 20",
  star:       "M12 2l3.09 6.26L22 9.27l-5 4.87 1.18 6.88L12 17.77l-6.18 3.25L7 14.14 2 9.27l6.91-1.01L12 2z",
  trophy:     "M8 21h8 M12 17v4 M7 4H4a1 1 0 000 2c0 5.5 3.5 10.7 8 11 4.5-.3 8-5.5 8-11a1 1 0 000-2h-3 M7 4h10v7a5 5 0 01-10 0V4z",
  save:       "M19 21H5a2 2 0 01-2-2V5a2 2 0 012-2h11l5 5v11a2 2 0 01-2 2z M17 21v-8H7v8 M7 3v5h8",
  folder:     "M22 19a2 2 0 01-2 2H4a2 2 0 01-2-2V5a2 2 0 012-2h5l2 3h9a2 2 0 012 2z",
  edit:       "M12 20h9 M16.5 3.5a2.121 2.121 0 013 3L7 19l-4 1 1-4L16.5 3.5z",
  tape:       "M21 16a2 2 0 01-2 2H5a2 2 0 01-2-2V8a2 2 0 012-2h14a2 2 0 012 2z M9 12a3 3 0 100-6 3 3 0 000 6z M15 12a3 3 0 100-6 3 3 0 000 6z",
  shuffle:    "M16 3h5v5 M4 20L21 3 M21 16v5h-5 M15 15l6 6 M4 4l5 5",
  speaker:    "M11 5L6 9H2v6h4l5 4V5z M19.07 4.93a10 10 0 010 14.14 M15.54 8.46a5 5 0 010 7.07",
  mute:       "M11 5L6 9H2v6h4l5 4V5z M23 9l-6 6 M17 9l6 6",
  clock:      "M12 22a10 10 0 100-20 10 10 0 000 20z M12 6v6l4 2",
  skull:      "M12 2a6 6 0 016 6 6 6 0 01-3.2 5.3V16H9.2v-2.7A6 6 0 016 8a6 6 0 016-6z M9 21h6 M10 17v4 M14 17v4",
  rocket:     "M4.5 16.5c-1.5 1.26-2 5-2 5s3.74-.5 5-2c.71-.84.7-2.13-.09-2.91a2.18 2.18 0 00-2.91-.09z M12 15l-3-3a22 22 0 012-3.95A12.88 12.88 0 0122 2c0 2.72-.78 7.5-6 11a22.35 22.35 0 01-4 2z M9 12H4s.55-3.03 2-4c1.62-1.08 5 0 5 0 M12 15v5s3.03-.55 4-2c1.08-1.62 0-5 0-5",
  swirl:      "M12 22a10 10 0 100-20 10 10 0 000 20z M12 18a6 6 0 100-12 6 6 0 000 12z M12 14a2 2 0 100-4 2 2 0 000 4z",
  chill:      "M12 22a10 10 0 100-20 10 10 0 000 20z M8 14s1.5 2 4 2 4-2 4-2 M9 9h.01 M15 9h.01",
  dark_face:  "M12 22a10 10 0 100-20 10 10 0 000 20z M8 15s1.5-2 4-2 4 2 4 2 M7.5 8.5l2 1.5 M16.5 8.5l-2 1.5",
  heart:      "M20.84 4.61a5.5 5.5 0 00-7.78 0L12 5.67l-1.06-1.06a5.5 5.5 0 00-7.78 7.78l1.06 1.06L12 21.23l7.78-7.78 1.06-1.06a5.5 5.5 0 000-7.78z",
  crown:      "M12 2L9 9H3l5 4-2 7 6-4 6 4-2-7 5-4h-6L12 2z",
  wave_hand:  "M18 11V6a2 2 0 00-2-2v0a2 2 0 00-2 2v0 M14 10V4a2 2 0 00-2-2v0a2 2 0 00-2 2v2 M10 10.5V6a2 2 0 00-2-2v0a2 2 0 00-2 2v8 M6 14v0a6 6 0 006 6h2a8 8 0 008-8v-2a2 2 0 00-2-2v0a2 2 0 00-2 2v0",
  money:      "M12 1v22 M17 5H9.5a3.5 3.5 0 000 7h5a3.5 3.5 0 010 7H6",
  moneyfly:   "M12 1v22 M17 5H9.5a3.5 3.5 0 000 7h5a3.5 3.5 0 010 7H6",
  writing:    "M12 20h9 M16.5 3.5a2.121 2.121 0 013 3L7 19l-4 1 1-4L16.5 3.5z",
  lightning:  "M13 2L3 14h9l-1 8 10-12h-9l1-8z",
  warning:    "M10.29 3.86L1.82 18a2 2 0 001.71 3h16.94a2 2 0 001.71-3L13.71 3.86a2 2 0 00-3.42 0z M12 9v4 M12 17h.01",
  festival:   "M9 19c-5 1.5-5-2.5-7-3m14 6v-3.87a3.37 3.37 0 00-.94-2.61c3.14-.35 6.44-1.54 6.44-7A5.44 5.44 0 0020 4.77 5.07 5.07 0 0019.91 1S18.73.65 16 2.48a13.38 13.38 0 00-7 0C6.27.65 5.09 1 5.09 1A5.07 5.07 0 005 4.77a5.44 5.44 0 00-1.5 3.78c0 5.42 3.3 6.61 6.44 7A3.37 3.37 0 009 18.13V22",
  check:      "M20 6L9 17l-5-5",
  download:   "M21 15v4a2 2 0 01-2 2H5a2 2 0 01-2-2v-4 M7 10l5 5 5-5 M12 15V3",
  upload:     "M21 15v4a2 2 0 01-2 2H5a2 2 0 01-2-2v-4 M17 8l-5-5-5 5 M12 3v12",
  grid:       "M3 3h7v7H3z M14 3h7v7h-7z M14 14h7v7h-7z M3 14h7v7H3z",
  bars:       "M18 20V10 M12 20V4 M6 20v-6",
  stripe:     "M21 4H3a2 2 0 00-2 2v12a2 2 0 002 2h18a2 2 0 002-2V6a2 2 0 00-2-2z M1 10h22",
  eq:         "M4 21V14 M4 10V3 M12 21v-7 M12 10V3 M20 21v-4 M20 13V3 M1 14h6 M9 10h6 M17 13h6",
  play:       "M6 4l14 8-14 8z",
  pause:      "M7 4v16 M17 4v16",
};

function Icon({ id, size = 20, color = "currentColor", strokeWidth = 1.8, style: extra = {}, className = "" }) {
  const def = LucideIcons[id];
  if (!def) return null;
  // Split compound path strings into individual paths at each top-level M command
  const segments = def.trim().split(/\s+(?=M)/);
  return (
    <svg
      width={size} height={size} viewBox="0 0 24 24"
      fill="none" stroke={color} strokeWidth={strokeWidth}
      strokeLinecap="round" strokeLinejoin="round"
      style={{ display: "inline-block", verticalAlign: "middle", flexShrink: 0, ...extra }}
      className={className}
    >
      {segments.map(function(d, i) { return <path key={i} d={d} />; })}
    </svg>
  );
}

// Aliases for backwards compat
function AppIcon({ id, size = 20, style: extraStyle = {} }) {
  return <Icon id={id} size={size} color="currentColor" style={extraStyle} />;
}

function NavIcon({ id, size = 22, active, activeColor }) {
  const iconId = id === "exclusive" ? "members" : id;
  const color = active ? activeColor : "#666";
  const filter = active ? `drop-shadow(0 0 5px ${activeColor})` : "none";
  return <Icon id={iconId} size={size} color={color} strokeWidth={active ? 2.2 : 1.6} style={{ filter, transition: "filter 0.2s ease" }} />;
}

// =============================================================================
// =============================================================================
// WELCOME SCREEN — shown after splash, before app, allows guest access
// =============================================================================
function WelcomeScreen({ onSignIn, onGuest }) {
  return (
    <div style={{
      position: "fixed", inset: 0, zIndex: 99998,
      background: "#0a0a0a",
      display: "flex", flexDirection: "column",
      alignItems: "center", justifyContent: "center",
      padding: "40px 28px",
      fontFamily: "'DM Sans',sans-serif",
      paddingTop: "calc(40px + var(--bf-safe-top, env(safe-area-inset-top)))",
      paddingBottom: "calc(40px + env(safe-area-inset-bottom))",
    }}>

      {/* Logo */}
      <img
        src="https://i.ibb.co/9myqbFB7/2-BB02064-13-F6-476-C-89-FF-B1-EDDAE0-C709.png"
        alt="BeatFinder"
        style={{ width: "100%", maxWidth: 260, marginBottom: 12, objectFit: "contain" }}
      />

      {/* Tagline */}
      <div style={{ color: "#555", fontSize: 14, marginBottom: 56, textAlign: "center", lineHeight: 1.6 }}>
        Find beats. Write lyrics. Record music.
      </div>

      {/* Sign In button */}
      <button
        onClick={onSignIn}
        style={{
          width: "100%", maxWidth: 340,
          background: "linear-gradient(135deg,#C026D3,#7C3AED)",
          border: "none", borderRadius: 32,
          color: "white", fontWeight: 800, fontSize: 17,
          padding: "17px 0", cursor: "pointer",
          marginBottom: 14,
          boxShadow: "0 4px 24px rgba(192,38,211,0.35)",
          letterSpacing: 0.3,
        }}
      >
        Sign In / Create Account
      </button>

      {/* Continue as Guest */}
      <button
        onClick={onGuest}
        style={{
          width: "100%", maxWidth: 340,
          background: "transparent",
          border: "1.5px solid #222",
          borderRadius: 32,
          color: "#888", fontWeight: 700, fontSize: 15,
          padding: "15px 0", cursor: "pointer",
          marginBottom: 32,
          letterSpacing: 0.2,
        }}
      >
        Continue as Guest
      </button>

      {/* Fine print */}
      <div style={{ color: "#333", fontSize: 11, textAlign: "center", lineHeight: 1.7, maxWidth: 300 }}>
        Guests can browse and play beats.{" "}
        <span style={{ color: "#555" }}>Sign in to save beats, write lyrics, and access the studio.</span>
      </div>

    </div>
  );
}

// =============================================================================
// SPLASH SCREEN
// =============================================================================
function SplashScreen({ onDone }) {
  const [fading, setFading] = useState(false);

  useEffect(function() {
    var fadeTimer = setTimeout(function() { setFading(true); }, 1200);
    var doneTimer = setTimeout(function() { onDone(); }, 1600);
    return function() { clearTimeout(fadeTimer); clearTimeout(doneTimer); };
  }, []);

  return (
    <div style={{
      position: "fixed", inset: 0, zIndex: 99999,
      background: "#0a0a0a",
      display: "flex", alignItems: "center", justifyContent: "center",
      transition: "opacity 0.4s ease",
      opacity: fading ? 0 : 1,
      pointerEvents: fading ? "none" : "all",
    }}>
      <img
        src="/splash.png"
        alt="BeatFinder"
        style={{
          width: "100%", height: "100%",
          objectFit: "cover", objectPosition: "center",
          display: "block",
        }}
      />
      {/* BETA tag — small chip in the top-right corner. Sets the
          expectation that this is an early version of the product,
          so users are forgiving of rough edges + occasional issues. */}
      <div style={{
        position: "absolute",
        top: "calc(env(safe-area-inset-top) + 16px)",
        right: 16,
        background: "linear-gradient(135deg,#C026D3 0%,#7C3AED 100%)",
        color: "white",
        fontFamily: "'Bebas Neue',sans-serif",
        fontSize: 13,
        fontWeight: 700,
        letterSpacing: 2,
        padding: "4px 10px",
        borderRadius: 10,
        boxShadow: "0 4px 12px rgba(192,38,211,0.4), 0 0 16px rgba(124,58,237,0.3)",
        border: "1px solid rgba(255,255,255,0.15)",
      }}>
        BETA
      </div>
    </div>
  );
}

// =============================================================================
// INSTALL PROMPT — "Add to Home Screen" banner
// Platform-aware:
//   • Android Chrome/Edge/Samsung: native install prompt via beforeinstallprompt
//   • iOS Safari: manual instructions (Apple doesn't allow programmatic install)
//   • Already-installed PWA: hides itself entirely
//   • Dismissed: snoozed for 7 days via localStorage flag
// =============================================================================
// =============================================================================
// TERMS & CONDITIONS — modal (acceptance) and reusable content viewer
// =============================================================================
// Generic legal-document renderer used by both T&C and Privacy Policy pages.
// Provides consistent neon styling, header, and footer across legal pages.
function LegalContent({ title, label, version, effective, sections, compact, footerText }) {
  return (
    <div>
      <div style={{ marginBottom: 16, paddingBottom: 12, borderBottom: "1px solid rgba(124,58,237,0.2)" }}>
        <div style={{
          color: "#A78BFA", fontSize: 10, fontWeight: 900, letterSpacing: 1.5,
          textShadow: "0 0 6px rgba(124,58,237,0.55)", marginBottom: 4,
        }}>{label}</div>
        <div style={{
          color: "white", fontSize: compact ? 16 : 20, fontWeight: 900,
          fontFamily: "'Bebas Neue', sans-serif", letterSpacing: 1, marginBottom: 4,
        }}>{title}</div>
        <div style={{ color: "#888", fontSize: 11 }}>
          Version {version} · Effective {effective}
        </div>
      </div>
      {sections.map(function(section, i) {
        return (
          <div key={i} style={{ marginBottom: 16 }}>
            <div style={{
              color: "#DDD6FE", fontWeight: 800, fontSize: compact ? 13 : 14,
              marginBottom: 6, letterSpacing: 0.3,
            }}>{section.heading}</div>
            <div style={{
              color: "#bbb", fontSize: compact ? 12 : 13, lineHeight: 1.65,
            }}>{section.body}</div>
          </div>
        );
      })}
      <div style={{
        marginTop: 20, padding: 12,
        background: "linear-gradient(135deg,rgba(124,58,237,0.08),rgba(59,130,246,0.08))",
        border: "1px solid rgba(124,58,237,0.3)",
        borderRadius: 10,
        color: "#A78BFA", fontSize: 11, fontWeight: 700, textAlign: "center",
        letterSpacing: 0.3,
      }}>
        {footerText || "Questions? Contact support@beatfinder.co.uk"}
      </div>
    </div>
  );
}

function TermsContent({ compact }) {
  return (
    <LegalContent
      label="BEATFINDER · LEGAL"
      title="TERMS & CONDITIONS"
      version={TERMS_VERSION}
      effective={TERMS_EFFECTIVE_DATE}
      sections={TERMS_CONTENT}
      compact={compact}
    />
  );
}

function PrivacyContent({ compact }) {
  return (
    <LegalContent
      label="BEATFINDER · PRIVACY"
      title="PRIVACY POLICY"
      version={PRIVACY_VERSION}
      effective={PRIVACY_EFFECTIVE_DATE}
      sections={PRIVACY_CONTENT}
      compact={compact}
      footerText="Privacy questions? Contact support@beatfinder.co.uk · ICO: ico.org.uk"
    />
  );
}

// ─────────────────────────────────────────────────────────────────────────
// UsernameSetupModal
// ─────────────────────────────────────────────────────────────────────────
// Modal that pops up for logged-in users who don't yet have a username.
// This catches two cases:
//   1. Users who signed up before the signup form collected usernames
//      (historic accounts with username === "")
//   2. Edge cases where the signup form succeeded at registration but
//      the /set-username call failed mid-flow
//
// Required modal — no dismiss button, no backdrop dismiss. Users must
// set a username to continue, since without one they can't be searched
// for, can't have a public profile, can't be tagged, etc.
// ─────────────────────────────────────────────────────────────────────────
// HeaderCropModal
// ─────────────────────────────────────────────────────────────────────────
// Lets the user pan + pinch-zoom an image inside a fixed 16:9 crop frame
// before uploading it as their profile header. On confirm, the visible
// region inside the frame is drawn to an offscreen canvas and emitted as
// a JPEG Blob via onConfirm.
//
// Implementation notes:
//   - Position is held in refs (offsetX/Y, scale) and React only re-renders
//     on actual transform updates via a tick counter. Touch handlers mutate
//     the refs directly without re-render so panning is buttery-smooth.
//   - The crop frame is 16:9 to match the profile's 240px-tall display.
//   - Output resolution is 1280×720 — sharp on retina but small enough
//     to upload quickly and keep Cloudinary storage cheap.
//   - Pinch-zoom uses two-finger distance ratio relative to the gesture
//     start, matching Instagram-style image crops.
// ─────────────────────────────────────────────────────────────────────────
// QuickSignupModal
// ─────────────────────────────────────────────────────────────────────────
// One-screen account creation for guests who try to download a free beat
// or purchase a basic lease. Collects the minimum needed fields (name,
// username, email, password) and creates the account, sets the username,
// and accepts T&Cs in one go. On success, calls onAccountCreated(newUser)
// so the caller can resume the original action (download or checkout).
//
// Reused for two intents:
//   intent="download"   → "Sign up to download" framing
//   intent="buyBasic"   → "Sign up to checkout" framing
function QuickSignupModal({ intent, beat, onClose, onAccountCreated }) {
  var [name, setName]                     = React.useState("");
  var [username, setUsername]             = React.useState("");
  var [usernameStatus, setUsernameStatus] = React.useState("idle");
  var [email, setEmail]                   = React.useState("");
  var [pw, setPw]                         = React.useState("");
  var [acceptTerms, setAcceptTerms]       = React.useState(false);
  var [marketingOptIn, setMarketingOptIn] = React.useState(false);
  var [busy, setBusy]                     = React.useState(false);
  var [err, setErr]                       = React.useState("");

  // Live availability check (debounced 400ms) — same pattern as the
  // main signup form's username field
  React.useEffect(function() {
    var u = username.trim();
    if (!u) { setUsernameStatus("idle"); return; }
    if (u.length < 3 || u.length > 30 || !/^[a-zA-Z0-9_.]+$/.test(u)) {
      setUsernameStatus("invalid"); return;
    }
    setUsernameStatus("checking");
    var cancelled = false;
    var t = setTimeout(function() {
      apiFetch("/api/auth/search?q=" + encodeURIComponent(u))
        .then(function(matches) {
          if (cancelled) return;
          var taken = (matches || []).some(function(m) {
            return (m.username || "").toLowerCase() === u.toLowerCase();
          });
          setUsernameStatus(taken ? "taken" : "available");
        })
        .catch(function() { if (!cancelled) setUsernameStatus("idle"); });
    }, 400);
    return function() { cancelled = true; clearTimeout(t); };
  }, [username]);

  async function submit() {
    setErr("");
    if (!name.trim())     { setErr("Please enter your name"); return; }
    if (!username.trim()) { setErr("Please choose a username"); return; }
    if (usernameStatus === "invalid") { setErr("Must be 3–30 chars · letters/numbers/dots/underscores"); return; }
    if (usernameStatus === "taken")   { setErr("That username is taken — pick another"); return; }
    if (!email.trim())    { setErr("Please enter your email"); return; }
    if (!pw.trim() || pw.length < 6) { setErr("Password must be at least 6 characters"); return; }
    if (!acceptTerms)     { setErr("Please accept the Terms & Conditions"); return; }

    setBusy(true);
    try {
      var u = await AuthAPI.register(name.trim(), email.trim(), pw, marketingOptIn);
      try {
        await apiFetch("/api/auth/set-username", {
          method: "POST",
          body: JSON.stringify({ username: username.trim() }),
        });
        u.username = username.trim();
      } catch (e) {
        // Username race condition — abort so they retry with a different one
        setBusy(false);
        setErr("Username '" + username.trim() + "' was just taken — pick another");
        return;
      }
      try {
        await apiFetch("/api/auth/accept-terms", {
          method: "POST",
          body: JSON.stringify({ version: TERMS_VERSION }),
        });
        u.terms_accepted_version = TERMS_VERSION;
      } catch (_) { /* non-fatal */ }
      // Caller resumes whichever action triggered the signup
      onAccountCreated(u);
    } catch (e) {
      setBusy(false);
      setErr(e.message || "Could not create account");
    }
  }

  var beatTitle = (beat && beat.title) || "this beat";
  var heading, sub, cta;
  if (intent === "buyBasic") {
    heading = "CREATE ACCOUNT TO BUY";
    sub     = "Quick signup so we can deliver your MP3 + contract for " +
              beatTitle + " and let you re-download anytime.";
    cta     = "Continue to Checkout →";
  } else {
    heading = "CREATE ACCOUNT TO DOWNLOAD";
    sub     = "Quick signup so you can grab " + beatTitle + " and any" +
              " other free beats you find on BeatFinder.";
    cta     = "Continue to Download →";
  }

  return (
    <div onClick={onClose}
      style={{
        position: "fixed", inset: 0, zIndex: 99999,
        background: "rgba(0,0,0,0.92)",
        backdropFilter: "blur(6px)",
        WebkitBackdropFilter: "blur(6px)",
        display: "flex", alignItems: "center", justifyContent: "center",
        padding: 16, fontFamily: "'DM Sans',sans-serif",
        overflowY: "auto",
      }}>
      <div onClick={function(e){ e.stopPropagation(); }}
        style={{
          background: "linear-gradient(165deg,#0f0a1f 0%,#0a0a14 60%,#080812 100%)",
          border: "1px solid rgba(124,58,237,0.3)",
          borderRadius: 18, maxWidth: 420, width: "100%",
          padding: 24,
          boxShadow: "0 16px 40px rgba(0,0,0,0.75), 0 0 24px rgba(124,58,237,0.15)",
          maxHeight: "92vh", overflowY: "auto",
        }}>
        <div style={{
          color: "#A78BFA", fontSize: 10, fontWeight: 900, letterSpacing: 1.5,
          marginBottom: 6,
        }}>
          QUICK SIGNUP
        </div>
        <div style={{
          color: "white", fontFamily: "'Bebas Neue',sans-serif",
          fontSize: 24, letterSpacing: 1.2, lineHeight: 1.1, marginBottom: 6,
        }}>
          {heading}
        </div>
        <div style={{ color: "#888", fontSize: 12, lineHeight: 1.5, marginBottom: 16 }}>
          {sub}
        </div>

        <input value={name} onChange={function(e){ setName(e.target.value); }}
          placeholder="Your name"
          style={qsInputStyle}
        />
        <input value={username} onChange={function(e){ setUsername(e.target.value); }}
          placeholder="Username" autoCapitalize="none" autoCorrect="off" spellCheck={false}
          style={Object.assign({}, qsInputStyle, { marginBottom: 4 })}
        />
        <div style={{ minHeight: 14, marginBottom: 10, paddingLeft: 4, fontSize: 10.5, lineHeight: "14px" }}>
          {usernameStatus === "idle"     && <span style={{ color: "#555" }}>3–30 characters · letters/numbers/dots/underscores</span>}
          {usernameStatus === "invalid"  && <span style={{ color: "#F87171" }}>Must be 3–30 chars · letters/numbers/dots/underscores</span>}
          {usernameStatus === "checking" && <span style={{ color: "#888" }}>Checking availability…</span>}
          {usernameStatus === "available"&& <span style={{ color: "#10B981", fontWeight: 700 }}>✓ @{username.trim()} is available</span>}
          {usernameStatus === "taken"    && <span style={{ color: "#F87171", fontWeight: 700 }}>✗ @{username.trim()} is taken</span>}
        </div>

        <input value={email} onChange={function(e){ setEmail(e.target.value); }}
          placeholder="Email address" type="email" autoCapitalize="none" autoCorrect="off" spellCheck={false}
          style={qsInputStyle}
        />
        <input value={pw} onChange={function(e){ setPw(e.target.value); }}
          placeholder="Password (6+ characters)" type="password"
          style={Object.assign({}, qsInputStyle, { marginBottom: 14 })}
        />

        <label style={{ display: "flex", alignItems: "flex-start", gap: 8, marginBottom: 14, cursor: "pointer" }}>
          <input type="checkbox" checked={acceptTerms}
            onChange={function(e){ setAcceptTerms(e.target.checked); }}
            style={{ marginTop: 3, accentColor: "#C026D3" }}
          />
          <span style={{ color: "#aaa", fontSize: 11, lineHeight: 1.5 }}>
            I agree to the BeatFinder Terms & Conditions and confirm I'm at least 13 years old.
          </span>
        </label>

        {/* Marketing opt-in — defaults OFF, GDPR-safe */}
        <label style={{ display: "flex", alignItems: "flex-start", gap: 8, marginBottom: 14, cursor: "pointer" }}>
          <input type="checkbox" checked={marketingOptIn}
            onChange={function(e){ setMarketingOptIn(e.target.checked); }}
            style={{ marginTop: 3, accentColor: "#C026D3" }}
          />
          <span style={{ color: "#888", fontSize: 11, lineHeight: 1.5 }}>
            Send me occasional updates about BeatFinder.
          </span>
        </label>

        <button onClick={submit} disabled={busy}
          style={{
            width: "100%",
            background: busy ? "#555" : "linear-gradient(135deg,#C026D3,#7C3AED)",
            border: "none", borderRadius: 32,
            color: "white", fontWeight: 800, padding: 13, fontSize: 14,
            cursor: busy ? "not-allowed" : "pointer",
            opacity: busy ? 0.5 : 1,
            marginBottom: 10,
          }}>
          {busy ? "Creating account…" : cta}
        </button>
        <button onClick={onClose} disabled={busy}
          style={{
            width: "100%", background: "none", border: "none",
            color: "#666", fontSize: 12, cursor: "pointer", padding: 6,
          }}>
          Cancel
        </button>

        {err && (
          <div style={{ color: "#F87171", fontSize: 12, textAlign: "center", marginTop: 10 }}>
            {err}
          </div>
        )}
      </div>
    </div>
  );
}

var qsInputStyle = {
  width: "100%", background: "#1a1a1a", border: "1px solid #333",
  borderRadius: 10, padding: "11px 14px", color: "white", fontSize: 14,
  outline: "none", marginBottom: 10, boxSizing: "border-box",
};

function HeaderCropModal({ file, onConfirm, onCancel, aspect, circle, outW, outH, title }) {
  var ASPECT = aspect || (16 / 9);
  var OUT_W  = outW || 1280;
  var OUT_H  = outH || Math.round(OUT_W / ASPECT);
  var TITLE  = title || "CROP YOUR HEADER";
  var [imgUrl, setImgUrl]   = React.useState("");
  var [imgDims, setImgDims] = React.useState(null); // {w, h}
  var [busy, setBusy]       = React.useState(false);
  var [err, setErr]         = React.useState("");
  // Re-render trigger — handlers mutate the refs below and bump this to
  // pull the latest values into the transform style.
  var [tick, setTick] = React.useState(0);

  // Crop frame size (constrained to viewport). We compute this once
  // images are loaded.
  var frameRef = React.useRef(null);
  // Image transform state
  var offset = React.useRef({ x: 0, y: 0 });
  var scale  = React.useRef(1);
  // Gesture state
  var gesture = React.useRef(null);

  // Load the file into a data URL we can render in an <img>
  React.useEffect(function() {
    if (!file) return;
    var reader = new FileReader();
    reader.onload = function() { setImgUrl(reader.result); };
    reader.onerror = function() { setErr("Couldn't read image file"); };
    reader.readAsDataURL(file);
  }, [file]);

  // When image data URL is ready, measure natural dimensions
  React.useEffect(function() {
    if (!imgUrl) return;
    var probe = new Image();
    probe.onload = function() {
      setImgDims({ w: probe.naturalWidth, h: probe.naturalHeight });
    };
    probe.onerror = function() { setErr("Couldn't load image"); };
    probe.src = imgUrl;
  }, [imgUrl]);

  // Compute frame size — fit a 16:9 box inside ~92vw, no taller than 56vh
  // so the buttons remain visible even on small phones in landscape.
  function getFrameSize() {
    var vw = window.innerWidth;
    var vh = window.innerHeight;
    var maxW = Math.min(vw * 0.92, 460);
    var w = maxW;
    var h = w / ASPECT;
    var maxH = vh * 0.56;
    if (h > maxH) { h = maxH; w = h * ASPECT; }
    return { w: Math.round(w), h: Math.round(h) };
  }
  var frameSize = getFrameSize();

  // Compute the BASE scale: the smallest scale that still covers the
  // frame. The image transform is then base * scaleMultiplier.
  function getBaseScale() {
    if (!imgDims) return 1;
    var sx = frameSize.w / imgDims.w;
    var sy = frameSize.h / imgDims.h;
    return Math.max(sx, sy); // cover, not contain
  }

  function clampOffset(x, y, currentScale) {
    if (!imgDims) return { x: x, y: y };
    var dispW = imgDims.w * currentScale;
    var dispH = imgDims.h * currentScale;
    // Limit pan so image edges can't move inside the frame
    var maxX = Math.max(0, (dispW - frameSize.w) / 2);
    var maxY = Math.max(0, (dispH - frameSize.h) / 2);
    return {
      x: Math.max(-maxX, Math.min(maxX, x)),
      y: Math.max(-maxY, Math.min(maxY, y)),
    };
  }

  function onTouchStart(e) {
    e.preventDefault();
    if (e.touches.length === 1) {
      gesture.current = {
        kind: "pan",
        startX: e.touches[0].clientX,
        startY: e.touches[0].clientY,
        baseOffset: { x: offset.current.x, y: offset.current.y },
      };
    } else if (e.touches.length === 2) {
      var dx = e.touches[0].clientX - e.touches[1].clientX;
      var dy = e.touches[0].clientY - e.touches[1].clientY;
      gesture.current = {
        kind: "pinch",
        startDist: Math.sqrt(dx*dx + dy*dy),
        baseScale: scale.current,
      };
    }
  }
  function onTouchMove(e) {
    e.preventDefault();
    if (!gesture.current) return;
    if (gesture.current.kind === "pan" && e.touches.length === 1) {
      var nx = gesture.current.baseOffset.x + (e.touches[0].clientX - gesture.current.startX);
      var ny = gesture.current.baseOffset.y + (e.touches[0].clientY - gesture.current.startY);
      var c = clampOffset(nx, ny, getBaseScale() * scale.current);
      offset.current = c;
      setTick(function(t){ return t + 1; });
    } else if (gesture.current.kind === "pinch" && e.touches.length === 2) {
      var dx2 = e.touches[0].clientX - e.touches[1].clientX;
      var dy2 = e.touches[0].clientY - e.touches[1].clientY;
      var dist = Math.sqrt(dx2*dx2 + dy2*dy2);
      var ratio = dist / gesture.current.startDist;
      var nextScale = Math.max(1, Math.min(4, gesture.current.baseScale * ratio));
      scale.current = nextScale;
      // Re-clamp offset after scale change so edges still cover
      var c2 = clampOffset(offset.current.x, offset.current.y, getBaseScale() * nextScale);
      offset.current = c2;
      setTick(function(t){ return t + 1; });
    }
  }
  function onTouchEnd(e) {
    e.preventDefault();
    if (e.touches.length === 0) gesture.current = null;
  }

  // Mouse fallback (desktop testing / drag)
  function onMouseDown(e) {
    e.preventDefault();
    gesture.current = {
      kind: "pan",
      startX: e.clientX,
      startY: e.clientY,
      baseOffset: { x: offset.current.x, y: offset.current.y },
    };
    function move(ev) {
      if (!gesture.current) return;
      var nx = gesture.current.baseOffset.x + (ev.clientX - gesture.current.startX);
      var ny = gesture.current.baseOffset.y + (ev.clientY - gesture.current.startY);
      var c = clampOffset(nx, ny, getBaseScale() * scale.current);
      offset.current = c;
      setTick(function(t){ return t + 1; });
    }
    function up() {
      gesture.current = null;
      window.removeEventListener("mousemove", move);
      window.removeEventListener("mouseup", up);
    }
    window.addEventListener("mousemove", move);
    window.addEventListener("mouseup", up);
  }

  function onWheel(e) {
    if (!imgDims) return;
    e.preventDefault();
    var delta = -e.deltaY * 0.002;
    var nextScale = Math.max(1, Math.min(4, scale.current * (1 + delta)));
    scale.current = nextScale;
    var c = clampOffset(offset.current.x, offset.current.y, getBaseScale() * nextScale);
    offset.current = c;
    setTick(function(t){ return t + 1; });
  }

  function confirmCrop() {
    if (!imgDims || busy) return;
    setBusy(true);
    setErr("");
    try {
      // Map the on-screen crop frame back to source-image coordinates.
      // The transform we use is: image is drawn at natural size, then
      // scaled by (base * scaleMultiplier), then offset by (offset.x, y).
      // The visible region of the image inside the frame is therefore:
      var displayScale = getBaseScale() * scale.current;
      // Source-pixel coordinates of the frame's top-left corner
      var srcCenterX = imgDims.w / 2 - offset.current.x / displayScale;
      var srcCenterY = imgDims.h / 2 - offset.current.y / displayScale;
      var srcW = frameSize.w / displayScale;
      var srcH = frameSize.h / displayScale;
      var srcX = srcCenterX - srcW / 2;
      var srcY = srcCenterY - srcH / 2;

      var canvas = document.createElement("canvas");
      canvas.width = OUT_W;
      canvas.height = OUT_H;
      var ctx = canvas.getContext("2d");
      var img = new Image();
      img.onload = function() {
        ctx.drawImage(img, srcX, srcY, srcW, srcH, 0, 0, OUT_W, OUT_H);
        canvas.toBlob(function(blob) {
          if (!blob) {
            setBusy(false);
            setErr("Couldn't process image");
            return;
          }
          onConfirm(blob);
        }, "image/jpeg", 0.9);
      };
      img.onerror = function() {
        setBusy(false);
        setErr("Couldn't process image");
      };
      img.src = imgUrl;
    } catch (e) {
      setBusy(false);
      setErr(e.message || "Couldn't process image");
    }
  }

  // Don't render the frame until we have image dimensions
  var baseScale = getBaseScale();
  var totalScale = baseScale * scale.current;

  return (
    <div style={{
      position: "fixed", inset: 0, zIndex: 99999,
      background: "rgba(0,0,0,0.96)",
      display: "flex", flexDirection: "column",
      alignItems: "center", justifyContent: "center",
      padding: 16, fontFamily: "'DM Sans',sans-serif",
    }}>
      <div style={{
        color: "#A78BFA", fontSize: 11, fontWeight: 900, letterSpacing: 1.5,
        marginBottom: 8,
      }}>
        {TITLE}
      </div>
      <div style={{ color: "#888", fontSize: 12, marginBottom: 16, textAlign: "center" }}>
        Drag to reposition · pinch to zoom
      </div>

      <div ref={frameRef}
        onTouchStart={onTouchStart}
        onTouchMove={onTouchMove}
        onTouchEnd={onTouchEnd}
        onTouchCancel={onTouchEnd}
        onMouseDown={onMouseDown}
        onWheel={onWheel}
        style={{
          position: "relative",
          width: frameSize.w, height: frameSize.h,
          overflow: "hidden",
          borderRadius: circle ? "50%" : 12,
          border: "2px solid rgba(192,38,211,0.5)",
          background: "#0a0a0a",
          touchAction: "none",
          WebkitUserSelect: "none", userSelect: "none",
          boxShadow: "0 0 24px rgba(124,58,237,0.25)",
        }}>
        {imgUrl && imgDims && (
          <img
            src={imgUrl}
            alt=""
            draggable={false}
            style={{
              position: "absolute",
              left: "50%", top: "50%",
              width: imgDims.w, height: imgDims.h,
              // Transform order matters in CSS — rightmost applies first.
              // We want:
              //   1. translate image so its CENTER is at the frame center
              //      (translate(-50%,-50%) does this in image-local pixels)
              //   2. scale around that centered point
              //   3. translate by the user's pan in SCREEN pixels (must be
              //      OUTSIDE the scale so it isn't divided by it)
              transform:
                "translate(" + offset.current.x + "px," + offset.current.y + "px) " +
                "scale(" + totalScale + ") " +
                "translate(-50%,-50%)",
              transformOrigin: "0 0",
              pointerEvents: "none",
            }}
          />
        )}
        {!imgUrl && (
          <div style={{ color: "#666", fontSize: 13,
            display: "flex", alignItems: "center", justifyContent: "center",
            width: "100%", height: "100%" }}>
            Loading…
          </div>
        )}
      </div>

      {err && (
        <div style={{ color: "#F87171", fontSize: 12, marginTop: 12, textAlign: "center" }}>
          {err}
        </div>
      )}

      <div style={{ display: "flex", gap: 10, marginTop: 18, width: "100%", maxWidth: frameSize.w }}>
        <button onClick={onCancel} disabled={busy}
          style={{
            flex: 1, padding: "14px",
            background: "rgba(255,255,255,0.04)",
            border: "1px solid rgba(255,255,255,0.1)",
            borderRadius: 12,
            color: "#ccc", fontWeight: 700, fontSize: 14,
            cursor: "pointer", letterSpacing: 0.3,
            opacity: busy ? 0.5 : 1,
          }}>
          Cancel
        </button>
        <button onClick={confirmCrop} disabled={busy || !imgDims}
          style={{
            flex: 1, padding: "14px",
            background: (busy || !imgDims) ? "#555" : "#C026D3",
            border: "none", borderRadius: 12,
            color: "white", fontWeight: 800, fontSize: 14,
            cursor: "pointer", letterSpacing: 0.3,
            opacity: (busy || !imgDims) ? 0.5 : 1,
          }}>
          {busy ? "Uploading…" : "Use this crop"}
        </button>
      </div>
    </div>
  );
}

function UsernameSetupModal({ user, onSet }) {
  var [input, setInput]   = React.useState("");
  var [status, setStatus] = React.useState("idle");
  var [error, setError]   = React.useState("");
  var [saving, setSaving] = React.useState(false);

  // Same live availability check as the signup form
  React.useEffect(function() {
    var u = input.trim();
    if (!u) { setStatus("idle"); return; }
    if (u.length < 3 || u.length > 30 || !/^[a-zA-Z0-9_.]+$/.test(u)) {
      setStatus("invalid"); return;
    }
    setStatus("checking");
    var cancelled = false;
    var t = setTimeout(function() {
      apiFetch("/api/auth/search?q=" + encodeURIComponent(u))
        .then(function(matches) {
          if (cancelled) return;
          var taken = (matches || []).some(function(m) {
            return (m.username || "").toLowerCase() === u.toLowerCase();
          });
          setStatus(taken ? "taken" : "available");
        })
        .catch(function() {
          if (!cancelled) setStatus("idle");
        });
    }, 400);
    return function() { cancelled = true; clearTimeout(t); };
  }, [input]);

  // Don't render until we know the user lacks a username
  if (!user || user.username) return null;

  function submit() {
    var desired = input.trim();
    if (!desired) { setError("Please choose a username"); return; }
    if (status === "invalid") { setError("Must be 3–30 characters, letters/numbers/dots/underscores only"); return; }
    if (status === "taken") { setError("That username is taken — pick another"); return; }
    setSaving(true);
    setError("");
    apiFetch("/api/auth/set-username", {
      method: "POST",
      body: JSON.stringify({ username: desired }),
    })
      .then(function() {
        setSaving(false);
        onSet(desired);
      })
      .catch(function(e) {
        setSaving(false);
        setError(e.message || "Couldn't set username, try another");
      });
  }

  return (
    <div style={{
      position: "fixed", inset: 0, zIndex: 99997,
      background: "rgba(0,0,0,0.92)",
      backdropFilter: "blur(6px)",
      WebkitBackdropFilter: "blur(6px)",
      display: "flex", alignItems: "center", justifyContent: "center",
      padding: 20, fontFamily: "'DM Sans',sans-serif",
    }}>
      <div style={{
        background: "linear-gradient(165deg,#0f0a1f 0%,#0a0a14 60%,#080812 100%)",
        border: "1px solid rgba(124,58,237,0.3)",
        borderRadius: 18, maxWidth: 420, width: "100%",
        padding: 28,
        boxShadow: "0 16px 40px rgba(0,0,0,0.75), 0 0 24px rgba(124,58,237,0.15)",
      }}>
        <div style={{
          color: "#A78BFA", fontSize: 11, fontWeight: 900, letterSpacing: 1.5,
          marginBottom: 8,
        }}>
          ONE MORE THING
        </div>
        <div style={{
          color: "white", fontFamily: "'Bebas Neue',sans-serif",
          fontSize: 28, letterSpacing: 1.5, lineHeight: 1.1, marginBottom: 12,
        }}>
          CHOOSE YOUR USERNAME
        </div>
        <div style={{ color: "#888", fontSize: 13, lineHeight: 1.5, marginBottom: 18 }}>
          This is how other producers and artists will find you on BeatFinder.
          You can change it later from Settings.
        </div>
        <input
          value={input}
          onChange={function(e){ setInput(e.target.value); setError(""); }}
          placeholder="username"
          autoCapitalize="none"
          autoCorrect="off"
          spellCheck={false}
          autoFocus
          style={{
            width: "100%", background: "#1a1a1a", border: "1px solid #333",
            borderRadius: 12, padding: "14px 16px", color: "white", fontSize: 15,
            outline: "none", marginBottom: 6, boxSizing: "border-box",
          }}
        />
        <div style={{ minHeight: 16, marginBottom: 14, paddingLeft: 4, fontSize: 11, lineHeight: "16px" }}>
          {status === "idle" && (
            <span style={{ color: "#555" }}>3–30 characters · letters, numbers, dots, underscores</span>
          )}
          {status === "invalid" && (
            <span style={{ color: "#F87171" }}>Must be 3–30 characters · letters, numbers, dots, underscores only</span>
          )}
          {status === "checking" && (
            <span style={{ color: "#888" }}>Checking availability…</span>
          )}
          {status === "available" && (
            <span style={{ color: "#10B981", fontWeight: 700 }}>✓ @{input.trim()} is available</span>
          )}
          {status === "taken" && (
            <span style={{ color: "#F87171", fontWeight: 700 }}>✗ @{input.trim()} is already taken</span>
          )}
        </div>
        <button
          onClick={submit}
          disabled={saving || status === "checking" || status === "invalid" || status === "taken" || !input.trim()}
          style={{
            width: "100%",
            background: (saving || status !== "available") ? "#555" : "#C026D3",
            border: "none", borderRadius: 32,
            color: "white", fontWeight: 800, padding: 14, fontSize: 15, cursor: "pointer",
            opacity: (saving || status === "checking" || status === "invalid" || status === "taken" || !input.trim()) ? 0.5 : 1,
          }}>
          {saving ? "Setting username…" : "Set Username"}
        </button>
        {error && (
          <div style={{ color: "#F87171", fontSize: 12, textAlign: "center", marginTop: 12 }}>
            {error}
          </div>
        )}
      </div>
    </div>
  );
}

function TermsModal({ user, onAccepted }) {
  // Three-state logic to prevent the modal flashing for logged-in users
  // while their session is being restored:
  //   null  = still deciding (don't render anything)
  //   true  = show the modal
  //   false = hide the modal (already accepted, or guest with consent)
  var [open, setOpen] = React.useState(null);
  var [scrolledToEnd, setScrolledToEnd] = React.useState(false);
  var [accepting, setAccepting] = React.useState(false);
  var contentRef = React.useRef(null);

  React.useEffect(function() {
    function check() {
      // If a JWT exists but user hasn't loaded yet, hold off the decision.
      // Otherwise we'd flash the modal (because user is null) before /me
      // returns and reveals the accepted_version.
      var hasToken = false;
      try { hasToken = !!localStorage.getItem("bf_token"); } catch(e) {}
      if (hasToken && !user) {
        console.log("[BF terms] waiting for /me to load");
        setOpen(null);
        return;
      }
      if (user) {
        // Logged-in user — show modal only if their accepted version is stale.
        // Acceptance is recorded server-side against their account (proper audit trail).
        var accepted = user.terms_accepted_version;
        console.log("[BF terms] check — accepted:", accepted, "vs TERMS_VERSION:", TERMS_VERSION, "match:", accepted === TERMS_VERSION);
        setOpen(accepted !== TERMS_VERSION);
      } else {
        // Guest — DON'T show the modal. Guests cannot legally accept T&Cs
        // for an account they don't yet have. Acceptance happens at signup
        // via the required checkbox on the registration form, which ties
        // who-and-when to the new account record at creation time.
        setOpen(false);
      }
    }
    check();
  }, [user]);

  function onScroll(e) {
    var el = e.target;
    if (el.scrollHeight - el.scrollTop - el.clientHeight < 40) {
      setScrolledToEnd(true);
    }
  }

  function handleAccept() {
    if (!scrolledToEnd || accepting) return;
    if (!user) {
      // Should not happen — modal only renders for logged-in users now.
      setOpen(false);
      return;
    }
    setAccepting(true);
    console.log("[BF terms] POSTing accept-terms version:", TERMS_VERSION);
    apiFetch("/api/auth/accept-terms", {
      method: "POST",
      body: JSON.stringify({ version: TERMS_VERSION }),
    }).then(function(r) {
      console.log("[BF terms] accept-terms OK", r);
      setOpen(false);
      if (onAccepted) onAccepted(TERMS_VERSION);
    }).catch(function(err) {
      console.error("[BF terms] accept-terms FAILED", err && err.message);
      // Don't silently mark as accepted — keep the modal open so the
      // user is prompted again. Better to be annoying than to record
      // a false acceptance.
    }).finally(function() { setAccepting(false); });
  }

  if (!open) return null;

  return (
    <div style={{
      position: "fixed", inset: 0, zIndex: 99999,
      background: "rgba(0,0,0,0.92)",
      display: "flex", alignItems: "center", justifyContent: "center",
      padding: 16,
    }}>
      <div style={{
        width: "100%", maxWidth: 520,
        background: "linear-gradient(165deg,#0f0a1f 0%,#0a0a14 60%,#080812 100%)",
        borderRadius: 22,
        border: "1px solid rgba(124,58,237,0.55)",
        boxShadow:
          "0 16px 60px rgba(0,0,0,0.85)," +
          "0 0 0 1px rgba(124,58,237,0.3)," +
          "0 0 40px rgba(124,58,237,0.3)," +
          "inset 0 1px 0 rgba(255,255,255,0.05)",
        display: "flex", flexDirection: "column",
        maxHeight: "92vh", overflow: "hidden",
      }}>
        {/* LED top edge */}
        <div style={{
          height: 2,
          background: "linear-gradient(90deg,transparent 0%,#7C3AED 20%,#3B82F6 50%,#7C3AED 80%,transparent 100%)",
          boxShadow: "0 0 12px #7C3AEDcc, 0 0 24px #3B82F666",
        }} />

        {/* Header */}
        <div style={{ padding: "18px 20px 14px", textAlign: "center" }}>
          <div style={{
            color: "#A78BFA", fontSize: 10, fontWeight: 900, letterSpacing: 2,
            marginBottom: 6, textShadow: "0 0 8px rgba(124,58,237,0.55)",
          }}>BEFORE YOU CONTINUE</div>
          <div style={{
            fontFamily: "'Bebas Neue',sans-serif", fontSize: 26, letterSpacing: 2,
            background: "linear-gradient(135deg,#C026D3 0%,#A855F7 50%,#3B82F6 100%)",
            WebkitBackgroundClip: "text", WebkitTextFillColor: "transparent",
            color: "transparent",
          }}>TERMS &amp; CONDITIONS</div>
          <div style={{ color: "#888", fontSize: 11, marginTop: 4 }}>
            Please read and scroll to the bottom to continue
          </div>
        </div>

        {/* Scrollable content */}
        <div
          ref={contentRef}
          onScroll={onScroll}
          onTouchMove={function(e){ e.stopPropagation(); }}
          style={{
            overflowY: "scroll",
            WebkitOverflowScrolling: "touch",
            overscrollBehavior: "contain",
            padding: "0 20px 16px",
            borderTop: "1px solid rgba(124,58,237,0.15)",
            borderBottom: "1px solid rgba(124,58,237,0.15)",
            // Explicit height instead of flex:1 — iOS Safari needs a
            // concrete computed height on the scrolling child for
            // -webkit-overflow-scrolling:touch to kick in. Calculated
            // as the modal max-height minus the header (~110px) and
            // footer (~110px) chrome.
            maxHeight: "calc(92vh - 220px)",
            minHeight: 200,
          }}>
          <div style={{ paddingTop: 16 }}>
            <TermsContent compact />
          </div>
        </div>

        {/* Action footer */}
        <div style={{
          padding: "14px 20px max(14px, env(safe-area-inset-bottom))",
          background: "linear-gradient(180deg,rgba(0,0,0,0) 0%,rgba(0,0,0,0.4) 100%)",
        }}>
          {!scrolledToEnd && (
            <div style={{
              color: "#888", fontSize: 11, textAlign: "center", marginBottom: 10,
            }}>↓ Scroll to the bottom to enable acceptance</div>
          )}
          <button onClick={handleAccept} disabled={!scrolledToEnd || accepting}
            style={{
              width: "100%", borderRadius: 14, padding: "14px",
              background: (!scrolledToEnd || accepting)
                ? "rgba(255,255,255,0.04)"
                : "linear-gradient(135deg,#C026D3 0%,#A855F7 35%,#7C3AED 70%,#3B82F6 100%)",
              border: "1.5px solid " + ((!scrolledToEnd || accepting) ? "#222" : "rgba(192,38,211,0.6)"),
              color: (!scrolledToEnd || accepting) ? "#444" : "white",
              fontWeight: 900, fontSize: 14, letterSpacing: 0.8,
              cursor: (!scrolledToEnd || accepting) ? "not-allowed" : "pointer",
              display: "flex", alignItems: "center", justifyContent: "center", gap: 10,
              boxShadow: (!scrolledToEnd || accepting) ? "none"
                : "0 4px 20px rgba(192,38,211,0.55), 0 0 36px rgba(168,85,247,0.4), inset 0 1px 0 rgba(255,255,255,0.3)",
              textShadow: (!scrolledToEnd || accepting) ? "none" : "0 1px 2px rgba(0,0,0,0.3)",
            }}>
            {accepting ? "SAVING..." : (scrolledToEnd ? "✓ I AGREE TO THE TERMS" : "PLEASE SCROLL TO CONTINUE")}
          </button>
        </div>
      </div>
    </div>
  );
}

function DebugLogViewer() {
  var [, force] = React.useReducer(function(x){ return x + 1; }, 0);
  React.useEffect(function() {
    var unsub = subscribeBfDebugLogs(function(){ force(); });
    return unsub;
  }, []);
  var logs = getBfDebugLogs();
  var [filter, setFilter] = React.useState("");
  var filtered = filter ? logs.filter(function(l){ return l.msg.toLowerCase().indexOf(filter.toLowerCase()) !== -1; }) : logs;
  function copyAll() {
    var text = logs.map(function(l) {
      var t = new Date(l.ts).toISOString().slice(11, 23);
      return "[" + t + "] [" + l.level.toUpperCase() + "] " + l.msg;
    }).join("\n");
    try {
      if (navigator.clipboard && navigator.clipboard.writeText) {
        navigator.clipboard.writeText(text).then(function(){ alert("Copied " + logs.length + " log entries to clipboard"); });
      } else {
        // Fallback for older iOS
        var ta = document.createElement("textarea");
        ta.value = text; document.body.appendChild(ta);
        ta.select(); document.execCommand("copy"); document.body.removeChild(ta);
        alert("Copied " + logs.length + " log entries");
      }
    } catch(e) {
      alert("Copy failed — long-press the log area to select manually.");
    }
  }
  return (
    <div>
      <div style={{ color: "#888", fontSize: 11, marginBottom: 10, lineHeight: 1.5 }}>
        Captures all [BF *] console messages. Used to diagnose sync issues. {logs.length} of 200 entries captured.
      </div>
      <input
        value={filter} onChange={function(e){ setFilter(e.target.value); }}
        placeholder="Filter (e.g. agree-licence)"
        style={{
          width: "100%", boxSizing: "border-box",
          background: "#0a0a0a", border: "1px solid #333",
          borderRadius: 8, padding: "8px 12px",
          color: "white", fontSize: 12, outline: "none", marginBottom: 8,
        }}
      />
      <div style={{ display: "flex", gap: 6, marginBottom: 10 }}>
        <button onClick={copyAll} style={{
          flex: 1, background: "linear-gradient(135deg,#3B82F6,#7C3AED)",
          border: "1.5px solid rgba(124,58,237,0.6)", borderRadius: 8,
          padding: "8px", color: "white", fontWeight: 900, fontSize: 11, cursor: "pointer",
          letterSpacing: 0.3, boxShadow: "0 2px 8px rgba(124,58,237,0.4)",
          display: "flex", alignItems: "center", justifyContent: "center", gap: 6,
        }}>
          <svg width="12" height="12" viewBox="0 0 24 24" fill="none" stroke="white" strokeWidth="2.2" strokeLinecap="round" strokeLinejoin="round">
            <rect x="9" y="9" width="13" height="13" rx="2" ry="2"/>
            <path d="M5 15H4a2 2 0 0 1-2-2V4a2 2 0 0 1 2-2h9a2 2 0 0 1 2 2v1"/>
          </svg>
          Copy All
        </button>
        <button onClick={clearBfDebugLogs} style={{
          flex: 1, background: "transparent",
          border: "1.5px solid #444", borderRadius: 8,
          padding: "8px", color: "#aaa", fontWeight: 800, fontSize: 11, cursor: "pointer",
        }}>Clear</button>
      </div>

      {/* ── Reset client-side prompt state ── */}
      <div style={{
        marginBottom: 10, padding: 10,
        background: "rgba(192,38,211,0.06)",
        border: "1px solid rgba(192,38,211,0.2)",
        borderRadius: 8,
      }}>
        <div style={{ color: "#A78BFA", fontSize: 10, fontWeight: 800, letterSpacing: 0.5, marginBottom: 6 }}>
          RESET CLIENT PROMPTS (FOR TESTING)
        </div>
        <div style={{ display: "flex", gap: 6, flexWrap: "wrap" }}>
          <button onClick={function() {
            try { localStorage.removeItem("bf_cookie_choice_v1"); localStorage.removeItem("bf_cookie_choice_at"); } catch(e) {}
            alert("Cookie banner reset. Reload to see it again.");
          }} style={{
            flex: "1 1 0", minWidth: 110,
            background: "transparent", border: "1px solid #444", borderRadius: 8,
            padding: "7px 6px", color: "#bbb", fontSize: 10, fontWeight: 700, cursor: "pointer",
            display: "flex", alignItems: "center", justifyContent: "center", gap: 5,
          }}>
            <svg width="11" height="11" viewBox="0 0 24 24" fill="none" stroke="#bbb" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
              <path d="M12 2a10 10 0 1 0 10 10 4 4 0 0 1-5-5 4 4 0 0 1-5-5"/>
              <path d="M8.5 8.5v.01"/>
              <path d="M16 15.5v.01"/>
              <path d="M12 12v.01"/>
              <path d="M11 17v.01"/>
              <path d="M7 14v.01"/>
            </svg>
            Cookie Banner
          </button>
          <button onClick={function() {
            try { localStorage.removeItem("bf_install_dismissed"); } catch(e) {}
            alert("Install prompt reset. Reload to see it again (iOS Safari / Android Chrome only).");
          }} style={{
            flex: "1 1 0", minWidth: 110,
            background: "transparent", border: "1px solid #444", borderRadius: 8,
            padding: "7px 6px", color: "#bbb", fontSize: 10, fontWeight: 700, cursor: "pointer",
            display: "flex", alignItems: "center", justifyContent: "center", gap: 5,
          }}>
            <svg width="11" height="11" viewBox="0 0 24 24" fill="none" stroke="#bbb" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
              <rect x="6" y="2" width="12" height="20" rx="2" ry="2"/>
              <line x1="12" y1="18" x2="12" y2="18"/>
            </svg>
            Install Prompt
          </button>
          <button onClick={function() {
            try { sessionStorage.removeItem("bf_terms_accepted_v" + TERMS_VERSION); } catch(e) {}
            alert("Guest T&C acceptance reset. Reload to re-test (logged-out users only).");
          }} style={{
            flex: "1 1 0", minWidth: 110,
            background: "transparent", border: "1px solid #444", borderRadius: 8,
            padding: "7px 6px", color: "#bbb", fontSize: 10, fontWeight: 700, cursor: "pointer",
            display: "flex", alignItems: "center", justifyContent: "center", gap: 5,
          }}>
            <svg width="11" height="11" viewBox="0 0 24 24" fill="none" stroke="#bbb" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
              <path d="M14 2H6a2 2 0 0 0-2 2v16a2 2 0 0 0 2 2h12a2 2 0 0 0 2-2V8z"/>
              <polyline points="14 2 14 8 20 8"/>
              <line x1="9" y1="13" x2="15" y2="13"/>
              <line x1="9" y1="17" x2="15" y2="17"/>
            </svg>
            Guest T&amp;C
          </button>
          <button onClick={function() {
            if (!confirm("Reset ALL prompts (cookie banner, install prompt, guest T&C)? Reload page to see them again.")) return;
            try {
              localStorage.removeItem("bf_cookie_choice_v1");
              localStorage.removeItem("bf_cookie_choice_at");
              localStorage.removeItem("bf_install_dismissed");
              sessionStorage.removeItem("bf_terms_accepted_v" + TERMS_VERSION);
            } catch(e) {}
            alert("All prompts reset. Reload now.");
          }} style={{
            flex: "1 1 100%", marginTop: 4,
            background: "linear-gradient(135deg,#C026D3 0%,#A855F7 50%,#3B82F6 100%)",
            border: "1.5px solid rgba(192,38,211,0.6)", borderRadius: 8,
            padding: "8px", color: "white", fontWeight: 900, fontSize: 11, cursor: "pointer",
            boxShadow: "0 2px 10px rgba(192,38,211,0.4)",
            textShadow: "0 1px 2px rgba(0,0,0,0.3)", letterSpacing: 0.5,
            display: "flex", alignItems: "center", justifyContent: "center", gap: 6,
          }}>
            <svg width="13" height="13" viewBox="0 0 24 24" fill="none" stroke="white" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round">
              <polyline points="23 4 23 10 17 10"/>
              <polyline points="1 20 1 14 7 14"/>
              <path d="M3.51 9a9 9 0 0 1 14.85-3.36L23 10M1 14l4.64 4.36A9 9 0 0 0 20.49 15"/>
            </svg>
            Reset All Prompts
          </button>
        </div>
      </div>
      <div style={{
        background: "#000", border: "1px solid #2a2a2a", borderRadius: 8,
        padding: 10, fontFamily: "ui-monospace, monospace", fontSize: 10,
        maxHeight: 280, overflowY: "auto", WebkitOverflowScrolling: "touch",
        userSelect: "text", WebkitUserSelect: "text",
      }}>
        {filtered.length === 0
          ? <div style={{ color: "#555", textAlign: "center", padding: 20 }}>No logs yet — tap "I Agree" on a free beat to capture activity.</div>
          : filtered.map(function(l, i) {
              var color = l.level === "error" ? "#F87171" : l.level === "warn" ? "#FBBF24" : "#A78BFA";
              var time = new Date(l.ts).toLocaleTimeString();
              return (
                <div key={i} style={{ marginBottom: 6, color: color, wordBreak: "break-all", whiteSpace: "pre-wrap" }}>
                  <span style={{ color: "#444" }}>{time}</span>{" "}{l.msg}
                </div>
              );
            })}
      </div>
    </div>
  );
}

// =============================================================================
// EMAIL LIST EXPORT (admin only)
// =============================================================================
// Shows opt-in counts and offers a CSV export of opted-in users for
// importing into a marketing email tool (Mailchimp, Resend, ConvertKit etc.).
//
// Two consent categories in the export:
//   • "explicit" — user ticked the marketing-opt-in box at signup
//   • "soft"     — existing pre-checkbox user (PECR soft opt-in basis)
//
// Use the consent_type column in your mailing tool to send safer-first to
// the EU users via "explicit" only if you want to be extra cautious.
function YouTubeCacheAdmin() {
  var [busy, setBusy] = React.useState("");
  var [msg, setMsg]   = React.useState("");
  var [blocked, setBlocked] = React.useState(null);

  function loadBlocked() {
    apiFetch("/api/youtube/admin/blocked-videos")
      .then(function(d){ setBlocked(d || []); })
      .catch(function(){ setBlocked([]); });
  }
  React.useEffect(function(){ loadBlocked(); }, []);

  function unblock(vid) {
    apiFetch("/api/youtube/admin/block-video/" + encodeURIComponent(vid), { method: "DELETE" })
      .then(function(){ setBlocked(function(prev){ return (prev || []).filter(function(b){ return b.videoId !== vid; }); }); })
      .catch(function(e){ setMsg("Error: " + (e.message || "failed")); });
  }

  function clear(scope) {
    if (busy) return;
    setBusy(scope); setMsg("");
    var qs = scope === "all" ? "?all=true" : (scope === "trending" ? "" : "?prefix=" + encodeURIComponent(scope));
    apiFetch("/api/youtube/admin/clear-cache" + qs, { method: "POST" })
      .then(function(r) {
        setMsg("Cleared " + ((r && r.cleared) || 0) + " cached entr" + (((r && r.cleared) === 1) ? "y" : "ies") + " (" + ((r && r.scope) || scope) + "). New videos load on next view.");
        setBusy("");
      })
      .catch(function(e) { setMsg("Error: " + (e.message || "failed")); setBusy(""); });
  }

  var btn = function(label, scope, primary) {
    return {
      width: "100%", padding: "12px", borderRadius: 10, fontWeight: 800, fontSize: 14,
      cursor: busy ? "default" : "pointer", marginBottom: 8,
      background: primary ? "linear-gradient(135deg,#B026FF,#7D3CFF)" : "rgba(255,255,255,0.04)",
      border: primary ? "none" : "1px solid #2a2a2a",
      color: primary ? "white" : "#bbb", opacity: busy ? 0.6 : 1,
    };
  };

  return (
    <div>
      <div style={{ color: "#888", fontSize: 12, marginBottom: 12, lineHeight: 1.5 }}>
        Clears cached YouTube results so they re-fetch fresh (e.g. after a filter change). Trending costs ~1 fetch; "all" re-fetches each section as it's viewed.
      </div>
      <button onClick={function(){ clear("trending"); }} disabled={!!busy} style={btn("", "trending", true)}>
        {busy === "trending" ? "Clearing..." : "Clear Trending Cache"}
      </button>
      <button onClick={function(){ clear("artist_trending"); }} disabled={!!busy} style={btn("", "artist_trending", false)}>
        {busy === "artist_trending" ? "Clearing..." : "Clear Artist Trending"}
      </button>
      <button onClick={function(){ clear("artist_fresh"); }} disabled={!!busy} style={btn("", "artist_fresh", false)}>
        {busy === "artist_fresh" ? "Clearing..." : "Clear Artist Fresh"}
      </button>
      <button onClick={function(){ clear("all"); }} disabled={!!busy} style={btn("", "all", false)}>
        {busy === "all" ? "Clearing..." : "Clear ALL Cache"}
      </button>
      {msg && <div style={{ color: msg.startsWith("Error") ? "#F87171" : "#4ADE80", fontSize: 13, marginTop: 6, lineHeight: 1.5 }}>{msg}</div>}

      {/* Hidden videos — unhide controls */}
      <div style={{ borderTop: "1px solid #2a2a2a", marginTop: 16, paddingTop: 14 }}>
        <div style={{ color: "#E9D5FF", fontWeight: 800, fontSize: 14, marginBottom: 4 }}>Hidden Videos</div>
        <div style={{ color: "#888", fontSize: 12, marginBottom: 12, lineHeight: 1.5 }}>
          Videos you've hidden from the app (via the Hide button on a video). Tap Unhide to bring one back.
        </div>
        {blocked === null ? (
          <div style={{ color: "#666", fontSize: 13 }}>Loading...</div>
        ) : blocked.length === 0 ? (
          <div style={{ color: "#666", fontSize: 13 }}>No hidden videos.</div>
        ) : (
          blocked.map(function(b){
            return (
              <div key={b.videoId} style={{ display: "flex", alignItems: "center", gap: 10, padding: "8px 0", borderBottom: "1px solid #1e1e1e" }}>
                <img src={"https://img.youtube.com/vi/" + b.videoId + "/default.jpg"} alt=""
                  style={{ width: 56, height: 42, borderRadius: 6, objectFit: "cover", flexShrink: 0, background: "#000" }} />
                <div style={{ flex: 1, minWidth: 0 }}>
                  <div style={{ color: "#ddd", fontSize: 12.5, fontWeight: 600, overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" }}>{b.title || b.videoId}</div>
                  <div style={{ color: "#666", fontSize: 10.5, fontFamily: "monospace" }}>{b.videoId}</div>
                </div>
                <button onClick={function(){ unblock(b.videoId); }}
                  style={{ flexShrink: 0, background: "rgba(124,58,237,0.15)", border: "1px solid rgba(124,58,237,0.5)",
                    borderRadius: 8, color: "#A78BFA", fontSize: 12, fontWeight: 700, padding: "7px 12px", cursor: "pointer" }}>
                  Unhide
                </button>
              </div>
            );
          })
        )}
      </div>
    </div>
  );
}

function EmailListExport() {
  var [stats, setStats] = React.useState(null);
  var [loading, setLoading] = React.useState(true);
  var [downloading, setDownloading] = React.useState(false);
  var [err, setErr] = React.useState("");

  function loadStats() {
    setLoading(true);
    setErr("");
    apiFetch("/api/auth/admin/email-stats")
      .then(function(r) { setStats(r); })
      .catch(function(e) { setErr("Couldn't load stats: " + (e.message || "")); })
      .finally(function() { setLoading(false); });
  }

  React.useEffect(function() { loadStats(); }, []);

  function download() {
    // Use fetch directly so we can grab the blob and trigger a download.
    setDownloading(true);
    var token;
    try { token = localStorage.getItem("bf_token"); } catch (_) {}
    fetch(API_BASE + "/api/auth/admin/export-emails", {
      headers: { "Authorization": "Bearer " + (token || "") },
    })
      .then(function(r) {
        if (!r.ok) throw new Error("Export failed (HTTP " + r.status + ")");
        return r.blob();
      })
      .then(function(blob) {
        var url = URL.createObjectURL(blob);
        var a = document.createElement("a");
        var stamp = new Date().toISOString().slice(0, 19).replace(/[T:]/g, "-");
        a.href = url;
        a.download = "beatfinder-emails-" + stamp + ".csv";
        document.body.appendChild(a);
        a.click();
        document.body.removeChild(a);
        URL.revokeObjectURL(url);
      })
      .catch(function(e) { setErr("Download failed: " + (e.message || "")); })
      .finally(function() { setDownloading(false); });
  }

  if (loading) {
    return <div style={{ color: "#888", fontSize: 12 }}>Loading…</div>;
  }

  return (
    <div>
      {err && (
        <div style={{ color: "#F87171", fontSize: 12, marginBottom: 10 }}>{err}</div>
      )}

      <div style={{ color: "#888", fontSize: 11, lineHeight: 1.5, marginBottom: 14 }}>
        Export a CSV of users who can receive marketing emails. The export
        includes anyone who explicitly opted in at signup OR existed before
        the opt-in checkbox shipped (under PECR soft opt-in). Anyone who
        actively opted out is excluded.
      </div>

      {stats && (
        <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: 8, marginBottom: 14 }}>
          {[
            { label: "EXPLICIT OPT-IN", value: stats.explicit_opt_in,  color: "#22C55E" },
            { label: "SOFT OPT-IN",      value: stats.soft_opt_in,      color: "#A78BFA" },
            { label: "OPTED OUT",        value: stats.explicit_opt_out, color: "#888" },
            { label: "EXPORTABLE TOTAL", value: stats.exportable_total, color: "#FBBF24" },
          ].map(function(stat, i) {
            return (
              <div key={i} style={{
                background: "#0d0d0d", border: "1px solid #222",
                borderRadius: 8, padding: "10px 12px",
              }}>
                <div style={{ color: stat.color, fontSize: 9, fontWeight: 800, letterSpacing: 0.5, marginBottom: 4 }}>
                  {stat.label}
                </div>
                <div style={{ color: "white", fontSize: 20, fontWeight: 800 }}>
                  {stat.value.toLocaleString()}
                </div>
              </div>
            );
          })}
        </div>
      )}

      <button
        onClick={download}
        disabled={downloading || !stats || stats.exportable_total === 0}
        style={{
          width: "100%",
          background: (downloading || !stats || stats.exportable_total === 0)
            ? "#222"
            : "linear-gradient(135deg,#C026D3,#7C3AED)",
          color: "white", border: "none",
          borderRadius: 10, padding: "12px 16px",
          fontSize: 13, fontWeight: 800, letterSpacing: 0.5,
          cursor: (downloading || !stats || stats.exportable_total === 0) ? "not-allowed" : "pointer",
          marginBottom: 10,
        }}>
        {downloading ? "Downloading…" : "Download CSV (" + ((stats && stats.exportable_total) || 0) + " users)"}
      </button>

      <div style={{
        background: "rgba(245,158,11,0.06)",
        border: "1px solid rgba(245,158,11,0.2)",
        borderRadius: 8, padding: "10px 12px",
        color: "#FCD34D", fontSize: 10, lineHeight: 1.5,
      }}>
        <strong style={{ color: "#FDE68A" }}>Compliance reminder:</strong> every marketing
        email you send MUST include an unsubscribe link. When users unsubscribe,
        update their <code style={{ color: "#FBBF24" }}>marketing_opt_in</code> to false
        so they're suppressed from future exports.
      </div>
    </div>
  );
}

// =============================================================================
// LIFETIME ACCOUNTS MANAGER (admin only)
// =============================================================================
// Lets the admin grant or revoke a lifetime subscription to any username.
// Backed by /api/auth/admin/lifetime/{grant,revoke,list}.
// Hardcoded permanent lifetimes (Trelli/Mikez/HMbarsdat) are shown but
// cannot be revoked through this UI — those are managed in source code.
function LifetimeAccountsManager() {
  var [granted, setGranted]       = React.useState([]);
  var [permanent, setPermanent]   = React.useState([]);
  var [loading, setLoading]       = React.useState(true);
  var [error, setError]           = React.useState("");
  var [grantUsername, setGrantUsername] = React.useState("");
  var [grantPlan, setGrantPlan]   = React.useState("artist");
  var [grantBusy, setGrantBusy]   = React.useState(false);
  var [actionMsg, setActionMsg]   = React.useState("");

  function load() {
    setLoading(true);
    setError("");
    apiFetch("/api/auth/admin/lifetime/list")
      .then(function(r) {
        setGranted((r && r.granted) || []);
        setPermanent((r && r.permanent) || []);
      })
      .catch(function(e) {
        setError("Couldn't load list: " + (e.message || ""));
      })
      .finally(function() { setLoading(false); });
  }

  React.useEffect(load, []);

  function grant() {
    var u = (grantUsername || "").trim();
    if (!u) { setActionMsg("Enter a username first"); return; }
    setGrantBusy(true);
    setActionMsg("");
    apiFetch("/api/auth/admin/lifetime/grant", {
      method: "POST",
      body: JSON.stringify({ username: u, plan: grantPlan }),
    })
      .then(function(r) {
        if (r && r.ok) {
          setActionMsg(
            "✓ Granted " + grantPlan + " lifetime to @" + r.username +
            (r.user_exists ? "" : " (note: user hasn't signed up yet)")
          );
          setGrantUsername("");
          load();
        } else {
          setActionMsg("Couldn't grant — unknown response");
        }
      })
      .catch(function(e) { setActionMsg("Error: " + (e.message || "couldn't grant")); })
      .finally(function() { setGrantBusy(false); setTimeout(function(){ setActionMsg(""); }, 4000); });
  }

  function revoke(username) {
    if (!window.confirm("Revoke lifetime access for @" + username + "?")) return;
    apiFetch("/api/auth/admin/lifetime/revoke", {
      method: "POST",
      body: JSON.stringify({ username: username }),
    })
      .then(function(r) {
        if (r && r.ok) {
          setActionMsg("✓ Revoked @" + username);
          load();
        }
      })
      .catch(function(e) { setActionMsg("Error: " + (e.message || "couldn't revoke")); })
      .finally(function() { setTimeout(function(){ setActionMsg(""); }, 4000); });
  }

  return (
    <div style={{ color: "white", fontSize: 13 }}>
      <div style={{ color: "#aaa", fontSize: 11, lineHeight: 1.5, marginBottom: 12 }}>
        Grant lifetime Artist Pro or Producer Pro access to any username.
        Users get the plan permanently with no Stripe subscription.
      </div>

      {/* Grant form */}
      <div style={{ background: "#0d0d0d", border: "1px solid #2a2a2a", borderRadius: 10, padding: 12, marginBottom: 14 }}>
        <div style={{ color: "#A78BFA", fontSize: 10, fontWeight: 800, letterSpacing: 1, marginBottom: 8 }}>
          GRANT LIFETIME
        </div>
        <input value={grantUsername} onChange={function(e){ setGrantUsername(e.target.value); }}
          placeholder="username (case-sensitive)"
          autoCapitalize="none" autoCorrect="off" spellCheck={false}
          style={{ width: "100%", background: "#111", border: "1px solid #333", borderRadius: 8,
            padding: "10px 12px", color: "white", fontSize: 13, outline: "none",
            boxSizing: "border-box", marginBottom: 8 }} />
        <div style={{ display: "flex", gap: 8, marginBottom: 8 }}>
          {["artist", "producer"].map(function(p) {
            var active = grantPlan === p;
            return (
              <button key={p} onClick={function(){ setGrantPlan(p); }}
                style={{
                  flex: 1, padding: "8px 0", border: "none", borderRadius: 8,
                  background: active
                    ? (p === "producer" ? "linear-gradient(135deg,#C026D3,#7C3AED)" : "#3B82F6")
                    : "#1a1a1a",
                  color: active ? "white" : "#888", fontWeight: 700, fontSize: 12,
                  cursor: "pointer", textTransform: "uppercase", letterSpacing: 0.5,
                }}>
                {p === "artist" ? "Artist Pro" : "Producer Pro"}
              </button>
            );
          })}
        </div>
        <button onClick={grant} disabled={grantBusy}
          style={{ width: "100%", background: grantBusy ? "#555" : "#C026D3",
            border: "none", borderRadius: 8, color: "white",
            fontWeight: 800, fontSize: 13, padding: "10px",
            cursor: grantBusy ? "wait" : "pointer", opacity: grantBusy ? 0.6 : 1 }}>
          {grantBusy ? "Granting..." : "Grant Lifetime"}
        </button>
        {actionMsg && (
          <div style={{
            color: actionMsg.indexOf("Error") === 0 ? "#F87171" : "#10B981",
            fontSize: 11, marginTop: 8, textAlign: "center", lineHeight: 1.4,
          }}>{actionMsg}</div>
        )}
      </div>

      {/* Granted lifetimes (revocable) */}
      <div style={{ color: "#A78BFA", fontSize: 10, fontWeight: 800, letterSpacing: 1, marginBottom: 8 }}>
        ADMIN-GRANTED ({granted.length})
      </div>
      {loading && <div style={{ color: "#666", fontSize: 12, padding: "8px 0" }}>Loading…</div>}
      {error && <div style={{ color: "#F87171", fontSize: 12, padding: "8px 0" }}>{error}</div>}
      {!loading && !error && granted.length === 0 && (
        <div style={{ color: "#555", fontSize: 12, padding: "8px 0", fontStyle: "italic" }}>
          No admin-granted lifetimes yet.
        </div>
      )}
      {granted.map(function(g) {
        return (
          <div key={g.username} style={{
            display: "flex", justifyContent: "space-between", alignItems: "center",
            background: "#0d0d0d", border: "1px solid #2a2a2a", borderRadius: 8,
            padding: "10px 12px", marginBottom: 6,
          }}>
            <div style={{ flex: 1, minWidth: 0 }}>
              <div style={{ color: "white", fontWeight: 700, fontSize: 13, overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" }}>
                @{g.username}
              </div>
              <div style={{ color: "#888", fontSize: 10, marginTop: 2 }}>
                {g.plan === "producer" ? "Producer Pro" : "Artist Pro"}
                {g.granted_by ? " · by @" + g.granted_by : ""}
              </div>
            </div>
            <button onClick={function(){ revoke(g.username); }}
              style={{
                background: "rgba(239,68,68,0.1)", border: "1px solid rgba(239,68,68,0.3)",
                color: "#F87171", fontSize: 11, fontWeight: 700,
                padding: "6px 10px", borderRadius: 6, cursor: "pointer",
              }}>
              Revoke
            </button>
          </div>
        );
      })}

      {/* Permanent lifetimes (read-only) */}
      <div style={{ color: "#A78BFA", fontSize: 10, fontWeight: 800, letterSpacing: 1, marginTop: 14, marginBottom: 8 }}>
        PERMANENT (CODE-MANAGED)
      </div>
      <div style={{ color: "#666", fontSize: 10, marginBottom: 8, fontStyle: "italic" }}>
        These can't be revoked through the UI — edit the source code to change them.
      </div>
      {permanent.map(function(p) {
        return (
          <div key={p.username} style={{
            display: "flex", justifyContent: "space-between", alignItems: "center",
            background: "#0a0a0a", border: "1px solid #1a1a1a", borderRadius: 8,
            padding: "10px 12px", marginBottom: 6, opacity: 0.7,
          }}>
            <div style={{ flex: 1, minWidth: 0 }}>
              <div style={{ color: "#ccc", fontWeight: 700, fontSize: 13 }}>
                @{p.username}
                {p.is_admin && (
                  <span style={{ color: "#A78BFA", fontSize: 9, fontWeight: 800, marginLeft: 6, letterSpacing: 0.5 }}>
                    ADMIN
                  </span>
                )}
              </div>
              <div style={{ color: "#666", fontSize: 10, marginTop: 2 }}>
                {p.plan === "producer" ? "Producer Pro" : "Artist Pro"} · permanent
              </div>
            </div>
            <div style={{ color: "#444", fontSize: 14 }}>🔒</div>
          </div>
        );
      })}
    </div>
  );
}

// =============================================================================
// ADMIN RESET LINK GENERATOR (admin only)
// =============================================================================
// When a user can't receive password-reset emails (Hotmail/Outlook
// silently rejecting transactional email is the most common cause),
// the admin types the user's email or username here and gets back a
// one-hour reset URL. The admin sends that URL to the user via DM /
// text / whatever channel works. Same token mechanism as the normal
// /forgot-password flow — the only difference is the delivery path.
// Backed by POST /api/auth/admin/generate-reset-link.
function AdminResetLinkGenerator() {
  var [input,     setInput]     = React.useState("");
  var [busy,      setBusy]      = React.useState(false);
  var [resultUrl, setResultUrl] = React.useState("");
  var [resultUser, setResultUser] = React.useState(null); // {username, email}
  var [error,     setError]     = React.useState("");
  var [copied,    setCopied]    = React.useState(false);

  function generate() {
    var s = (input || "").trim();
    if (!s) { setError("Enter an email or username"); return; }
    setBusy(true);
    setError("");
    setResultUrl("");
    setResultUser(null);
    setCopied(false);
    // Heuristic: if it contains an @, treat as email; otherwise username.
    var body = s.indexOf("@") >= 0
      ? { email: s }
      : { username: s };
    apiFetch("/api/auth/admin/generate-reset-link", {
      method: "POST",
      body: JSON.stringify(body),
    })
      .then(function(r) {
        if (r && r.success && r.reset_url) {
          setResultUrl(r.reset_url);
          setResultUser({ username: r.username, email: r.email });
        } else {
          setError("Couldn't generate link — unknown response");
        }
      })
      .catch(function(e) {
        // The backend returns 404 when the user doesn't exist — surface that
        // as a friendly message instead of the raw error.
        var msg = (e && e.message) || "Couldn't generate link";
        if (msg.indexOf("404") >= 0 || /no user/i.test(msg)) {
          setError("No user found with that email or username");
        } else {
          setError(msg);
        }
      })
      .finally(function() { setBusy(false); });
  }

  function copyLink() {
    if (!resultUrl) return;
    // navigator.clipboard works on iOS Safari 13.4+ over HTTPS.
    try {
      if (navigator.clipboard && navigator.clipboard.writeText) {
        navigator.clipboard.writeText(resultUrl).then(function() {
          setCopied(true);
          setTimeout(function(){ setCopied(false); }, 2000);
        }).catch(function() {
          fallbackCopy(resultUrl);
        });
      } else {
        fallbackCopy(resultUrl);
      }
    } catch(_) {
      fallbackCopy(resultUrl);
    }
  }
  function fallbackCopy(text) {
    // Legacy: synthesize a textarea, select, execCommand('copy'). Works
    // on iOS for older Safari versions without navigator.clipboard.
    try {
      var ta = document.createElement("textarea");
      ta.value = text;
      ta.style.position = "fixed";
      ta.style.left = "-9999px";
      document.body.appendChild(ta);
      ta.select();
      ta.setSelectionRange(0, text.length);
      document.execCommand("copy");
      document.body.removeChild(ta);
      setCopied(true);
      setTimeout(function(){ setCopied(false); }, 2000);
    } catch(_) {}
  }

  return (
    <div style={{ display: "flex", flexDirection: "column", gap: 10 }}>
      <div style={{ color: "#aaa", fontSize: 12, lineHeight: 1.5 }}>
        Use this when a user reports they aren't getting their reset email.
        Hotmail and Outlook addresses sometimes silently block our emails.
        Enter the user's email <em>or</em> username, copy the link, and send it to them.
      </div>

      <input
        type="text"
        value={input}
        onChange={function(e){ setInput(e.target.value); }}
        placeholder="user@example.com or @username"
        autoCapitalize="none"
        autoCorrect="off"
        spellCheck={false}
        style={{
          width: "100%", boxSizing: "border-box",
          padding: "10px 12px",
          background: "#0a0a0a",
          border: "1px solid #2a2a2a",
          borderRadius: 8,
          color: "white",
          fontSize: 14,
          outline: "none",
          fontFamily: "'DM Sans',sans-serif",
        }}
      />

      <button
        onClick={generate}
        disabled={busy || !input.trim()}
        style={{
          width: "100%",
          padding: "12px 16px",
          background: (busy || !input.trim())
            ? "#2a2a2a"
            : "linear-gradient(135deg,#C026D3,#7C3AED)",
          color: "white",
          border: "none",
          borderRadius: 8,
          fontSize: 14,
          fontWeight: 800,
          letterSpacing: 0.3,
          cursor: (busy || !input.trim()) ? "default" : "pointer",
          opacity: (busy || !input.trim()) ? 0.6 : 1,
        }}>
        {busy ? "GENERATING..." : "GENERATE RESET LINK"}
      </button>

      {error && (
        <div style={{
          padding: "10px 12px",
          background: "rgba(239,68,68,0.10)",
          border: "1px solid rgba(239,68,68,0.30)",
          borderRadius: 8,
          color: "#fca5a5",
          fontSize: 12,
        }}>{error}</div>
      )}

      {resultUrl && (
        <div style={{
          padding: "12px",
          background: "rgba(167,139,250,0.08)",
          border: "1px solid rgba(167,139,250,0.30)",
          borderRadius: 8,
          display: "flex", flexDirection: "column", gap: 8,
        }}>
          <div style={{ color: "#A78BFA", fontSize: 10, fontWeight: 800, letterSpacing: 1.2, textTransform: "uppercase" }}>
            Link generated · expires in 1 hour
          </div>
          {resultUser && (
            <div style={{ color: "#ccc", fontSize: 12 }}>
              For <strong style={{ color: "white" }}>@{resultUser.username}</strong>
              {resultUser.email ? <span style={{ color: "#888" }}> · {resultUser.email}</span> : null}
            </div>
          )}
          <div style={{
            padding: "8px 10px",
            background: "#0a0a0a",
            border: "1px solid #2a2a2a",
            borderRadius: 6,
            color: "#ccc",
            fontSize: 11,
            fontFamily: "ui-monospace, monospace",
            wordBreak: "break-all",
            userSelect: "all", WebkitUserSelect: "all",
          }}>{resultUrl}</div>
          <button
            onClick={copyLink}
            style={{
              width: "100%",
              padding: "10px 14px",
              background: copied ? "#10b981" : "#1f1f1f",
              color: "white",
              border: "1px solid " + (copied ? "#10b981" : "#2a2a2a"),
              borderRadius: 8,
              fontSize: 13,
              fontWeight: 700,
              cursor: "pointer",
            }}>{copied ? "✓ COPIED" : "COPY LINK"}</button>
        </div>
      )}
    </div>
  );
}


// =============================================================================
// USERS MANAGER (admin only) — list of all users who've joined
// =============================================================================
// Paginated table of every user in the database. Admin can search by
// name / username / email and see plan, signup date, payment status, etc.
// Backed by GET /api/auth/admin/users.
function UsersManager() {
  var [page, setPage]     = React.useState(0);
  var [users, setUsers]   = React.useState([]);
  var [total, setTotal]   = React.useState(0);
  var [loading, setLoading] = React.useState(true);
  var [error, setError]   = React.useState("");
  var [query, setQuery]   = React.useState("");
  var [debounced, setDebounced] = React.useState("");
  var [filterBy, setFilterBy] = React.useState("");          // "", "active", "lifetime", "free", "admin", "payment_failing", "deleted"
  var [showDeleted, setShowDeleted] = React.useState(false);
  var [selectedUser, setSelectedUser] = React.useState(null); // user row clicked → shows action sheet
  var [confirmDelete, setConfirmDelete] = React.useState(null); // user pending delete confirm
  var [deleting, setDeleting] = React.useState(false);
  var PAGE_SIZE = 20;

  // Debounce the search input so we don't fire a query on every keystroke
  React.useEffect(function() {
    var t = setTimeout(function() { setDebounced(query.trim()); setPage(0); }, 300);
    return function() { clearTimeout(t); };
  }, [query]);

  // Reset to page 0 whenever the filters change
  React.useEffect(function() { setPage(0); }, [filterBy, showDeleted]);

  function load() {
    setLoading(true);
    setError("");
    var qs = "?skip=" + (page * PAGE_SIZE) + "&limit=" + PAGE_SIZE +
             (debounced ? "&q=" + encodeURIComponent(debounced) : "") +
             (filterBy ? "&filter_by=" + encodeURIComponent(filterBy) : "") +
             ((showDeleted || filterBy === "deleted") ? "&include_deleted=true" : "");
    apiFetch("/api/auth/admin/users" + qs)
      .then(function(r) {
        setUsers((r && r.users) || []);
        setTotal((r && r.total) || 0);
      })
      .catch(function(e) { setError(e.message || "Couldn't load users"); })
      .finally(function() { setLoading(false); });
  }

  React.useEffect(load, [page, debounced, filterBy, showDeleted]);

  function doDelete(u) {
    setDeleting(true);
    apiFetch("/api/auth/admin/users/" + encodeURIComponent(u.id) + "/delete", {
      method: "POST",
      body:   JSON.stringify({}),
    })
      .then(function() {
        try { window.bfToast && window.bfToast.success("Account removed"); } catch (_) {}
        setConfirmDelete(null);
        setSelectedUser(null);
        load();
      })
      .catch(function(e) {
        try { window.bfToast && window.bfToast.error(e.message || "Couldn't remove account"); } catch (_) {}
      })
      .finally(function() { setDeleting(false); });
  }

  var totalPages = Math.max(1, Math.ceil(total / PAGE_SIZE));

  function planChip(u) {
    var bg, border, color, label;
    if (u.deleted) {
      bg = "#1a1a1a"; border = "#333"; color = "#666"; label = "DELETED";
    } else if (u.is_lifetime) {
      bg = "linear-gradient(135deg,rgba(245,158,11,0.18),rgba(245,158,11,0.05))";
      border = "rgba(245,158,11,0.5)";
      color = "#F59E0B";
      label = (u.plan === "producer" ? "PROD" : "ARTIST") + " · LIFETIME";
    } else if (u.plan === "producer") {
      bg = "linear-gradient(135deg,rgba(192,38,211,0.18),rgba(124,58,237,0.05))";
      border = "rgba(192,38,211,0.5)";
      color = "#C026D3";
      label = "PRODUCER PRO";
    } else if (u.plan === "artist") {
      bg = "rgba(59,130,246,0.12)";
      border = "rgba(59,130,246,0.5)";
      color = "#3B82F6";
      label = "ARTIST PRO";
    } else {
      bg = "#1a1a1a";
      border = "#333";
      color = "#888";
      label = "FREE";
    }
    return (
      <span style={{
        display: "inline-block", padding: "2px 7px", borderRadius: 6,
        background: bg, border: "1px solid " + border, color: color,
        fontSize: 9, fontWeight: 800, letterSpacing: 0.4, whiteSpace: "nowrap",
      }}>{label}</span>
    );
  }

  function formatDate(iso) {
    if (!iso) return "—";
    try {
      var d = new Date(iso);
      return d.toLocaleDateString("en-GB", { day: "numeric", month: "short", year: "numeric" });
    } catch (_) { return "—"; }
  }

  return (
    <div style={{ color: "white", fontSize: 13 }}>
      <div style={{ color: "#aaa", fontSize: 11, lineHeight: 1.5, marginBottom: 12 }}>
        Every account that's joined BeatFinder. Search by name, username, or email.
      </div>

      {/* Search */}
      <input
        value={query}
        onChange={function(e) { setQuery(e.target.value); }}
        placeholder="Search by name, @username, or email..."
        autoCapitalize="none"
        autoCorrect="off"
        spellCheck={false}
        style={{
          width: "100%", background: "#0d0d0d", border: "1px solid #2a2a2a",
          borderRadius: 10, padding: "10px 14px", color: "white", fontSize: 13,
          outline: "none", boxSizing: "border-box", marginBottom: 10,
        }} />

      {/* Filter chips — horizontal scroll on small screens */}
      <div style={{
        display: "flex", gap: 6, marginBottom: 10,
        overflowX: "auto", WebkitOverflowScrolling: "touch",
        paddingBottom: 2, // space for scrollbar shadow
      }}>
        {[
          { id: "",                 label: "All" },
          { id: "active",           label: "Active" },
          { id: "lifetime",         label: "Lifetime" },
          { id: "free",             label: "Free" },
          { id: "admin",            label: "Admin" },
          { id: "payment_failing",  label: "⚠ Pay Fail" },
          { id: "deleted",          label: "Deleted" },
        ].map(function(c) {
          var active = filterBy === c.id;
          return (
            <button
              key={c.id || "all"}
              onClick={function() { setFilterBy(c.id); }}
              style={{
                flex: "0 0 auto",
                background: active ? "linear-gradient(135deg,#C026D3,#7C3AED)" : "#1a1a1a",
                border: "1px solid " + (active ? "transparent" : "#2a2a2a"),
                color: active ? "white" : "#aaa",
                borderRadius: 16, padding: "5px 12px",
                fontSize: 11, fontWeight: 700, letterSpacing: 0.3,
                cursor: "pointer", whiteSpace: "nowrap",
                boxShadow: active ? "0 0 12px rgba(192,38,211,0.4)" : "none",
              }}>
              {c.label}
            </button>
          );
        })}
      </div>

      {/* Show deleted toggle — secondary, smaller */}
      <label style={{
        display: "flex", alignItems: "center", gap: 6, color: "#888", fontSize: 11,
        marginBottom: 10, cursor: "pointer", userSelect: "none",
      }}>
        <input
          type="checkbox"
          checked={showDeleted}
          onChange={function(e) { setShowDeleted(e.target.checked); }}
          style={{ cursor: "pointer" }} />
        Include deleted accounts in results
      </label>

      {/* Total + refresh + page indicator */}
      <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", marginBottom: 10, gap: 6 }}>
        <div style={{ color: "#888", fontSize: 11, flex: 1, minWidth: 0 }}>
          {loading ? "Loading…" : (
            total === 0
              ? "No users found"
              : (debounced
                ? total + " match" + (total === 1 ? "" : "es")
                : total + " total user" + (total === 1 ? "" : "s"))
          )}
        </div>
        <button
          onClick={load}
          disabled={loading}
          title="Refresh"
          style={{
            background: loading ? "#1a1a1a" : "#2a1a3a",
            border: "1px solid " + (loading ? "#222" : "rgba(124,58,237,0.4)"),
            color: loading ? "#444" : "#A78BFA",
            borderRadius: 6, padding: "4px 8px", fontSize: 11, fontWeight: 700,
            cursor: loading ? "not-allowed" : "pointer",
            display: "inline-flex", alignItems: "center", gap: 4,
          }}>
          <svg width="11" height="11" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round"
            style={loading ? { animation: "bf-spin 0.7s linear infinite" } : null}>
            <polyline points="23 4 23 10 17 10"/>
            <polyline points="1 20 1 14 7 14"/>
            <path d="M3.51 9a9 9 0 0 1 14.85-3.36L23 10M1 14l4.64 4.36A9 9 0 0 0 20.49 15"/>
          </svg>
          <span>Refresh</span>
        </button>
        {totalPages > 1 && (
          <div style={{ display: "flex", alignItems: "center", gap: 6 }}>
            <button
              onClick={function() { if (page > 0) setPage(page - 1); }}
              disabled={page === 0}
              style={{
                background: page === 0 ? "#1a1a1a" : "#2a1a3a",
                border: "1px solid " + (page === 0 ? "#222" : "rgba(124,58,237,0.4)"),
                color: page === 0 ? "#444" : "#A78BFA",
                borderRadius: 6, padding: "4px 8px", fontSize: 11, fontWeight: 700,
                cursor: page === 0 ? "not-allowed" : "pointer",
              }}>‹</button>
            <span style={{ color: "#888", fontSize: 11, fontWeight: 700, minWidth: 40, textAlign: "center" }}>
              {page + 1} / {totalPages}
            </span>
            <button
              onClick={function() { if (page < totalPages - 1) setPage(page + 1); }}
              disabled={page >= totalPages - 1}
              style={{
                background: page >= totalPages - 1 ? "#1a1a1a" : "#2a1a3a",
                border: "1px solid " + (page >= totalPages - 1 ? "#222" : "rgba(124,58,237,0.4)"),
                color: page >= totalPages - 1 ? "#444" : "#A78BFA",
                borderRadius: 6, padding: "4px 8px", fontSize: 11, fontWeight: 700,
                cursor: page >= totalPages - 1 ? "not-allowed" : "pointer",
              }}>›</button>
          </div>
        )}
      </div>

      {error && <div style={{ color: "#F87171", fontSize: 12, padding: "8px 0" }}>{error}</div>}

      {/* User rows */}
      {!loading && users.map(function(u) {
        return (
          <div
            key={u.id}
            onClick={function() { setSelectedUser(u); }}
            style={{
              background: u.deleted ? "#0a0a0a" : "#0d0d0d",
              border: "1px solid " + (u.deleted ? "#1a1a1a" : "#2a2a2a"),
              borderRadius: 10,
              padding: "10px 12px", marginBottom: 6,
              display: "flex", gap: 10, alignItems: "flex-start",
              cursor: "pointer",
              opacity: u.deleted ? 0.55 : 1,
            }}>
            {/* Avatar */}
            <div style={{
              flex: "0 0 auto", width: 36, height: 36, borderRadius: "50%",
              background: u.avatarUrl ? ("#1a1a1a url(" + u.avatarUrl + ") center/cover") : "linear-gradient(135deg,#C026D3,#7C3AED)",
              display: "flex", alignItems: "center", justifyContent: "center",
              color: "white", fontWeight: 900, fontSize: 14,
              filter: u.deleted ? "grayscale(1)" : "none",
            }}>
              {!u.avatarUrl && ((u.name || u.username || "?").charAt(0).toUpperCase())}
            </div>
            {/* Info */}
            <div style={{ flex: 1, minWidth: 0 }}>
              <div style={{ display: "flex", alignItems: "center", gap: 6, marginBottom: 2 }}>
                <div style={{ color: "white", fontWeight: 700, fontSize: 13, overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" }}>
                  {u.name || u.username || "(no name)"}
                </div>
                {u.is_admin && (
                  <span style={{ color: "#A78BFA", fontSize: 8, fontWeight: 900, letterSpacing: 0.5 }}>ADMIN</span>
                )}
                {u.payment_failing && (
                  <span style={{ color: "#F87171", fontSize: 8, fontWeight: 900, letterSpacing: 0.5 }}>⚠ PAY FAIL</span>
                )}
              </div>
              {u.username && (
                <div style={{ color: "#888", fontSize: 11, marginBottom: 4 }}>@{u.username}</div>
              )}
              <div style={{ color: "#666", fontSize: 10, marginBottom: 6, overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" }}>
                {u.email || "(no email)"}
              </div>
              <div style={{ display: "flex", alignItems: "center", gap: 6, flexWrap: "wrap" }}>
                {planChip(u)}
                <span style={{ color: "#555", fontSize: 10 }}>
                  joined {formatDate(u.created_at)}
                </span>
              </div>
            </div>
            <div style={{ flex: "0 0 auto", color: "#444", fontSize: 16, alignSelf: "center" }}>›</div>
          </div>
        );
      })}

      {!loading && users.length === 0 && !error && (
        <div style={{ color: "#555", fontSize: 12, padding: "20px 0", textAlign: "center", fontStyle: "italic" }}>
          {debounced ? "No users match \"" + debounced + "\"" : "No users yet"}
        </div>
      )}

      {/* ── Action sheet for a selected user ───────────────────────── */}
      {/* Rendered via createPortal so it escapes iOS Safari's bug where
          position:fixed gets re-anchored to any ancestor with a transform,
          filter, or backdrop-filter (which the Settings overlay has). */}
      {selectedUser && typeof document !== "undefined" && ReactDOM.createPortal((
        <div
          onClick={function() { setSelectedUser(null); }}
          style={{
            position: "fixed", inset: 0, zIndex: 99990,
            background: "rgba(0,0,0,0.7)", backdropFilter: "blur(4px)",
            display: "flex", alignItems: "flex-end", justifyContent: "center",
          }}>
          <div
            onClick={function(e) { e.stopPropagation(); }}
            style={{
              width: "100%", maxWidth: 440,
              background: "#0d0d0d",
              borderTopLeftRadius: 18, borderTopRightRadius: 18,
              padding: "20px 18px calc(env(safe-area-inset-bottom) + 24px)",
              border: "1px solid #2a2a2a",
              borderBottom: "none",
              animation: "bf-fadein-up 0.22s cubic-bezier(0.22,1,0.36,1) both",
            }}>
            {/* Header */}
            <div style={{ display: "flex", gap: 12, alignItems: "center", marginBottom: 18 }}>
              <div style={{
                flex: "0 0 auto", width: 48, height: 48, borderRadius: "50%",
                background: selectedUser.avatarUrl ? ("#1a1a1a url(" + selectedUser.avatarUrl + ") center/cover") : "linear-gradient(135deg,#C026D3,#7C3AED)",
                display: "flex", alignItems: "center", justifyContent: "center",
                color: "white", fontWeight: 900, fontSize: 18,
                filter: selectedUser.deleted ? "grayscale(1)" : "none",
              }}>
                {!selectedUser.avatarUrl && ((selectedUser.name || selectedUser.username || "?").charAt(0).toUpperCase())}
              </div>
              <div style={{ flex: 1, minWidth: 0 }}>
                <div style={{ color: "white", fontWeight: 800, fontSize: 16, marginBottom: 2 }}>
                  {selectedUser.name || selectedUser.username || "(no name)"}
                </div>
                {selectedUser.username && (
                  <div style={{ color: "#888", fontSize: 12 }}>@{selectedUser.username}</div>
                )}
                <div style={{ color: "#666", fontSize: 11, marginTop: 2, overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" }}>
                  {selectedUser.email || "(no email)"}
                </div>
              </div>
            </div>

            {/* Quick info chips */}
            <div style={{ display: "flex", flexWrap: "wrap", gap: 6, marginBottom: 18 }}>
              {planChip(selectedUser)}
              {selectedUser.is_admin && (
                <span style={{
                  background: "rgba(124,58,237,0.15)", border: "1px solid rgba(124,58,237,0.4)",
                  color: "#A78BFA", borderRadius: 6, padding: "2px 7px",
                  fontSize: 9, fontWeight: 800, letterSpacing: 0.4,
                }}>ADMIN</span>
              )}
              {selectedUser.payment_failing && (
                <span style={{
                  background: "rgba(239,68,68,0.15)", border: "1px solid rgba(239,68,68,0.4)",
                  color: "#F87171", borderRadius: 6, padding: "2px 7px",
                  fontSize: 9, fontWeight: 800, letterSpacing: 0.4,
                }}>⚠ PAY FAIL</span>
              )}
              {selectedUser.subscription_active && !selectedUser.is_lifetime && (
                <span style={{
                  background: "rgba(34,197,94,0.15)", border: "1px solid rgba(34,197,94,0.4)",
                  color: "#22C55E", borderRadius: 6, padding: "2px 7px",
                  fontSize: 9, fontWeight: 800, letterSpacing: 0.4,
                }}>SUBSCRIBED</span>
              )}
            </div>

            {/* Metadata */}
            <div style={{ background: "#0a0a0a", border: "1px solid #1a1a1a", borderRadius: 10, padding: "10px 12px", marginBottom: 16, fontSize: 11, lineHeight: 1.6 }}>
              <div style={{ color: "#888", marginBottom: 2 }}>Joined: <span style={{ color: "#ccc" }}>{formatDate(selectedUser.created_at)}</span></div>
              {selectedUser.billing_interval && (
                <div style={{ color: "#888" }}>Billing: <span style={{ color: "#ccc" }}>{selectedUser.billing_interval}</span></div>
              )}
            </div>

            {/* Actions */}
            {selectedUser.deleted ? (
              <div style={{
                background: "#1a1a1a", border: "1px solid #2a2a2a", borderRadius: 10,
                padding: "12px 14px", color: "#888", fontSize: 12, textAlign: "center",
              }}>
                This account has already been removed.
              </div>
            ) : (
              <button
                onClick={function() { setConfirmDelete(selectedUser); }}
                style={{
                  width: "100%", background: "rgba(239,68,68,0.12)",
                  border: "1px solid rgba(239,68,68,0.4)",
                  color: "#F87171", borderRadius: 12, padding: "12px 14px",
                  fontSize: 13, fontWeight: 800, letterSpacing: 0.3,
                  cursor: "pointer",
                  display: "flex", alignItems: "center", justifyContent: "center", gap: 8,
                }}>
                <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                  <polyline points="3 6 5 6 21 6"/>
                  <path d="M19 6l-2 14a2 2 0 0 1-2 2H9a2 2 0 0 1-2-2L5 6"/>
                  <path d="M10 11v6M14 11v6"/>
                  <path d="M9 6V4a2 2 0 0 1 2-2h2a2 2 0 0 1 2 2v2"/>
                </svg>
                Remove account
              </button>
            )}

            <button
              onClick={function() { setSelectedUser(null); }}
              style={{
                width: "100%", marginTop: 8,
                background: "transparent", border: "1px solid #2a2a2a",
                color: "#888", borderRadius: 12, padding: "10px 14px",
                fontSize: 12, fontWeight: 700, cursor: "pointer",
              }}>
              Close
            </button>
          </div>
        </div>
      ), document.body)}

      {/* ── Confirm delete modal ───────────────────────────────────── */}
      {/* Same portal trick as the action sheet above. */}
      {confirmDelete && typeof document !== "undefined" && ReactDOM.createPortal((
        <div
          onClick={function() { if (!deleting) setConfirmDelete(null); }}
          style={{
            position: "fixed", inset: 0, zIndex: 99991,
            background: "rgba(0,0,0,0.8)", backdropFilter: "blur(6px)",
            display: "flex", alignItems: "center", justifyContent: "center",
            padding: "20px",
          }}>
          <div
            onClick={function(e) { e.stopPropagation(); }}
            style={{
              background: "linear-gradient(165deg,#1a0a14 0%,#0d0a14 60%,#0a0a0a 100%)",
              border: "1px solid rgba(239,68,68,0.3)",
              borderRadius: 16, padding: "22px 20px",
              maxWidth: 360, width: "100%",
              boxShadow: "0 24px 48px rgba(0,0,0,0.6), 0 0 32px rgba(239,68,68,0.2)",
            }}>
            <div style={{
              width: 44, height: 44, borderRadius: "50%",
              background: "rgba(239,68,68,0.15)", border: "1px solid rgba(239,68,68,0.4)",
              display: "flex", alignItems: "center", justifyContent: "center",
              margin: "0 auto 14px",
            }}>
              <svg width="22" height="22" viewBox="0 0 24 24" fill="none" stroke="#F87171" strokeWidth="2.2" strokeLinecap="round" strokeLinejoin="round">
                <circle cx="12" cy="12" r="10"/>
                <line x1="12" y1="8" x2="12" y2="12"/>
                <line x1="12" y1="16" x2="12.01" y2="16"/>
              </svg>
            </div>
            <div style={{ color: "white", fontWeight: 800, fontSize: 17, textAlign: "center", marginBottom: 8 }}>
              Remove this account?
            </div>
            <div style={{ color: "#aaa", fontSize: 13, lineHeight: 1.5, textAlign: "center", marginBottom: 4 }}>
              You're about to remove
            </div>
            <div style={{ color: "white", fontWeight: 800, fontSize: 14, textAlign: "center", marginBottom: 14 }}>
              {confirmDelete.name || confirmDelete.username || "(no name)"}
              {confirmDelete.username && <span style={{ color: "#888", fontWeight: 600 }}> · @{confirmDelete.username}</span>}
            </div>
            <div style={{ color: "#888", fontSize: 11, lineHeight: 1.5, textAlign: "center", marginBottom: 18 }}>
              The account will be anonymised, their beats hidden, and any lifetime grant revoked.
              Lease records are kept for legal compliance.
            </div>
            <div style={{ display: "flex", gap: 8 }}>
              <button
                onClick={function() { if (!deleting) setConfirmDelete(null); }}
                disabled={deleting}
                style={{
                  flex: 1, background: "transparent", border: "1px solid #333",
                  color: "#aaa", borderRadius: 10, padding: "11px 14px",
                  fontSize: 13, fontWeight: 700, cursor: deleting ? "not-allowed" : "pointer",
                }}>Cancel</button>
              <button
                onClick={function() { doDelete(confirmDelete); }}
                disabled={deleting}
                style={{
                  flex: 1,
                  background: deleting ? "#3a1a1a" : "linear-gradient(135deg,#EF4444,#DC2626)",
                  border: "1px solid rgba(239,68,68,0.5)",
                  color: "white", borderRadius: 10, padding: "11px 14px",
                  fontSize: 13, fontWeight: 800, cursor: deleting ? "not-allowed" : "pointer",
                  boxShadow: deleting ? "none" : "0 4px 16px rgba(239,68,68,0.3)",
                }}>{deleting ? "Removing…" : "Remove"}</button>
            </div>
          </div>
        </div>
      ), document.body)}
    </div>
  );
}

// =============================================================================
// CHANGELOG — public release notes
// =============================================================================
// Add new entries at the TOP of the array. Each entry is a small object;
// the page renders them as a timeline so producers/artists can see the app
// is being actively maintained. Builds trust + signals quality.
//
// Tags: "new", "improved", "fixed", "security". Use sparingly — fewer,
// higher-impact bullets read better than long laundry lists.
var CHANGELOG_ENTRIES = [
  {
    version: "1.1",
    date:    "May 2026",
    title:   "Post-launch polish",
    items: [
      { tag: "new",       text: "Manage Subscription button — cancel, update card, download invoices via Stripe's hosted portal." },
      { tag: "new",       text: "Receipt emails after every beat lease purchase with licence terms attached." },
      { tag: "new",       text: "Automated emails when card payments fail, with instructions to update payment method." },
      { tag: "improved",  text: "Friendlier error messages across the app — no more 'Server returned 401'." },
      { tag: "improved",  text: "Auto sign-out banner when your session expires, so you know what happened." },
      { tag: "security",  text: "Error monitoring (Sentry) added — we now see crashes in real time and ship fixes faster." },
    ],
  },
  {
    version: "1.0",
    date:    "May 2026",
    title:   "BeatFinder is live",
    items: [
      { tag: "new", text: "Public launch — full beat marketplace with Basic + Premium exclusive licences." },
      { tag: "new", text: "Producer profiles with Stripe Connect — keep up to 99% of every sale." },
      { tag: "new", text: "Artist Pro + Producer Pro subscriptions, monthly or annual (save 17%)." },
      { tag: "new", text: "Studio with multi-track recording, BPM-locked beat grid, and FX." },
      { tag: "new", text: "Lyric notepad linked to beats — write straight from the player." },
      { tag: "new", text: "Followers, posts, direct messages, share to socials." },
    ],
  },
];

function ChangelogScreen({ onBack }) {
  function tagStyle(tag) {
    var palette = {
      "new":      { bg: "rgba(34,197,94,0.12)",  border: "rgba(34,197,94,0.4)",  color: "#22C55E", label: "NEW" },
      "improved": { bg: "rgba(124,58,237,0.12)", border: "rgba(124,58,237,0.4)", color: "#A78BFA", label: "IMPROVED" },
      "fixed":    { bg: "rgba(59,130,246,0.12)", border: "rgba(59,130,246,0.4)", color: "#60A5FA", label: "FIXED" },
      "security": { bg: "rgba(245,158,11,0.12)", border: "rgba(245,158,11,0.4)", color: "#F59E0B", label: "SECURITY" },
    }[tag] || { bg: "#1a1a1a", border: "#333", color: "#888", label: tag.toUpperCase() };
    return {
      display: "inline-block", padding: "2px 8px", borderRadius: 8,
      background: palette.bg, border: "1px solid " + palette.border, color: palette.color,
      fontSize: 9, fontWeight: 800, letterSpacing: 0.5, marginRight: 8,
      verticalAlign: "middle", flexShrink: 0,
    };
  }

  return (
    <div style={{
      // The page itself is the scroll container — height:100vh constrains
      // it to viewport, and overflowY:auto on THIS element scrolls its
      // contents. Avoids nested-scroll issues we hit when relying on a
      // parent overlay wrapper to scroll on iOS Safari.
      height: "100vh",
      overflowY: "auto",
      WebkitOverflowScrolling: "touch",
      overscrollBehavior: "contain",
      background: "#0a0a0a", color: "white",
      fontFamily: "'DM Sans',sans-serif",
      paddingTop: "var(--bf-safe-top, env(safe-area-inset-top))",
      paddingBottom: "calc(env(safe-area-inset-bottom) + 40px)",
    }}>
      <div style={{
        display: "flex", alignItems: "center", padding: "12px 16px",
        borderBottom: "1px solid #1a1a1a",
      }}>
        {onBack && (
          <button onClick={onBack} style={{
            background: "none", border: "none", color: "white",
            fontSize: 22, padding: "8px 12px 8px 0", cursor: "pointer",
          }}>←</button>
        )}
        <div style={{ fontFamily: "'Bebas Neue',sans-serif", fontSize: 22, letterSpacing: 2 }}>
          CHANGELOG
        </div>
      </div>

      <div style={{ padding: "10px 14px", maxWidth: 640, margin: "0 auto" }}>
        <div style={{ color: "#aaa", fontSize: 11, marginBottom: 10, lineHeight: 1.4 }}>
          What's new in BeatFinder. We ship updates regularly.
        </div>

        {CHANGELOG_ENTRIES.map(function(entry, idx) {
          return (
            <div key={entry.version} style={{
              background: "linear-gradient(165deg,#0f0a1f 0%,#0a0a14 60%,#080812 100%)",
              border: "1px solid rgba(124,58,237,0.18)",
              borderRadius: 12, padding: "10px 12px",
              marginBottom: 8,
              position: "relative", overflow: "hidden",
            }}>
              {/* LED top edge */}
              <div style={{
                position: "absolute", top: 0, left: 12, right: 12, height: 2,
                background: idx === 0
                  ? "linear-gradient(90deg,transparent,#C026D3,#7C3AED,#3B82F6,transparent)"
                  : "linear-gradient(90deg,transparent,#444,#666,#444,transparent)",
                boxShadow: idx === 0 ? "0 0 8px rgba(124,58,237,0.5)" : "none",
              }} />

              <div style={{ display: "flex", alignItems: "baseline", gap: 8, marginBottom: 2 }}>
                <div style={{
                  fontFamily: "'Bebas Neue',sans-serif", fontSize: 16, letterSpacing: 1.2,
                  color: idx === 0 ? "#C026D3" : "#888",
                }}>v{entry.version}</div>
                <div style={{ color: "#555", fontSize: 9, fontWeight: 700, letterSpacing: 0.5 }}>
                  {entry.date}
                </div>
              </div>
              <div style={{ color: "white", fontWeight: 700, fontSize: 12, marginBottom: 6 }}>
                {entry.title}
              </div>

              {entry.items.map(function(item, i) {
                return (
                  <div key={i} style={{
                    display: "flex", alignItems: "flex-start", gap: 6,
                    color: "#ccc", fontSize: 11, lineHeight: 1.35,
                    marginBottom: 4,
                  }}>
                    <span style={tagStyle(item.tag)}>{({
                      "new":"NEW","improved":"IMPR","fixed":"FIX","security":"SEC"
                    }[item.tag]) || item.tag.toUpperCase()}</span>
                    <span style={{ flex: 1 }}>{item.text}</span>
                  </div>
                );
              })}
            </div>
          );
        })}

        <div style={{ color: "#444", fontSize: 9, textAlign: "center", marginTop: 8, lineHeight: 1.4 }}>
          Feedback? <span style={{ color: "#A78BFA" }}>support@beatfinder.co.uk</span>
        </div>
      </div>
    </div>
  );
}

// =============================================================================
// ABOUT SCREEN — public "who's behind BeatFinder" page
// =============================================================================
// Trust-building page that shows the founder, location, contact info,
// and the platform's mission. Linked from Settings → "About BeatFinder".
function AboutScreen({ onBack }) {
  function openExternal(url) {
    try {
      var a = document.createElement("a");
      a.href = url;
      a.target = bfIsPWAiOS() ? "_self" : "_blank";
      a.rel = "noopener noreferrer";
      document.body.appendChild(a);
      a.click();
      document.body.removeChild(a);
    } catch (_) {
      try { window.location.href = url; } catch (_) {}
    }
  }

  function Row({ icon, label, value, href }) {
    return (
      <div
        onClick={href ? function() { openExternal(href); } : undefined}
        style={{
          display: "flex", alignItems: "center", gap: 10,
          padding: "8px 12px",
          background: "#0d0d0d",
          border: "1px solid #2a2a2a",
          borderRadius: 10,
          marginBottom: 6,
          cursor: href ? "pointer" : "default",
        }}>
        <div style={{
          flex: "0 0 auto", width: 26, height: 26, borderRadius: 6,
          background: "rgba(124,58,237,0.12)",
          border: "1px solid rgba(124,58,237,0.3)",
          display: "flex", alignItems: "center", justifyContent: "center",
          color: "#A78BFA",
        }}>
          {icon}
        </div>
        <div style={{ flex: 1, minWidth: 0, display: "flex", alignItems: "center", gap: 8 }}>
          <span style={{ color: "#888", fontSize: 9, fontWeight: 700, letterSpacing: 0.8, flex: "0 0 auto" }}>
            {label}
          </span>
          <span style={{ color: "white", fontSize: 12, fontWeight: 600, overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" }}>
            {value}
          </span>
        </div>
        {href && <div style={{ color: "#555", fontSize: 13, flex: "0 0 auto" }}>↗</div>}
      </div>
    );
  }

  return (
    <div style={{
      // The page itself is the scroll container — height:100vh constrains
      // it to viewport, and overflowY:auto on THIS element scrolls its
      // contents. Avoids nested-scroll issues we hit when relying on a
      // parent overlay wrapper to scroll on iOS Safari.
      height: "100vh",
      overflowY: "auto",
      WebkitOverflowScrolling: "touch",
      overscrollBehavior: "contain",
      background: "#0a0a0a", color: "white",
      fontFamily: "'DM Sans',sans-serif",
      paddingTop: "var(--bf-safe-top, env(safe-area-inset-top))",
      paddingBottom: "calc(env(safe-area-inset-bottom) + 40px)",
    }}>
      <div style={{
        display: "flex", alignItems: "center", padding: "12px 16px",
        borderBottom: "1px solid #1a1a1a",
      }}>
        {onBack && (
          <button onClick={onBack} style={{
            background: "none", border: "none", color: "white",
            fontSize: 22, padding: "8px 12px 8px 0", cursor: "pointer",
          }}>←</button>
        )}
        <div style={{ fontFamily: "'Bebas Neue',sans-serif", fontSize: 22, letterSpacing: 2 }}>
          ABOUT
        </div>
      </div>

      <div style={{ padding: "12px 14px", maxWidth: 540, margin: "0 auto" }}>
        <div style={{ textAlign: "center", marginBottom: 12, padding: "8px 0" }}>
          <div style={{
            fontFamily: "'Bebas Neue',sans-serif", fontSize: 28, letterSpacing: 4,
            background: "linear-gradient(135deg,#C026D3 0%,#A855F7 50%,#3B82F6 100%)",
            WebkitBackgroundClip: "text", WebkitTextFillColor: "transparent",
            backgroundClip: "text",
            filter: "drop-shadow(0 0 10px rgba(192,38,211,0.4))",
            marginBottom: 2,
          }}>
            BEATFINDER
          </div>
          <div style={{ color: "#666", fontSize: 10, fontWeight: 700, letterSpacing: 1.5, marginBottom: 6 }}>
            EST. MAY 2026 · LONDON, UK
          </div>
          <div style={{
            display: "inline-block",
            background: "linear-gradient(135deg,rgba(192,38,211,0.18),rgba(124,58,237,0.12))",
            border: "1px solid rgba(192,38,211,0.4)",
            color: "#E9D5FF",
            fontFamily: "'Bebas Neue',sans-serif",
            fontSize: 9,
            fontWeight: 700,
            letterSpacing: 1.5,
            padding: "2px 8px",
            borderRadius: 6,
          }}>
            BETA · WEB APP
          </div>
        </div>

        <div style={{
          background: "linear-gradient(165deg,#0f0a1f 0%,#0a0a14 60%,#080812 100%)",
          border: "1px solid rgba(124,58,237,0.18)",
          borderRadius: 12, padding: "12px 14px",
          marginBottom: 12,
          position: "relative", overflow: "hidden",
        }}>
          <div style={{
            position: "absolute", top: 0, left: 14, right: 14, height: 2,
            background: "linear-gradient(90deg,transparent,#C026D3,#7C3AED,#3B82F6,transparent)",
            boxShadow: "0 0 8px rgba(124,58,237,0.5)",
          }} />
          <div style={{ color: "#A78BFA", fontSize: 9, fontWeight: 800, letterSpacing: 1.5, marginBottom: 6 }}>
            MISSION
          </div>
          <div style={{ color: "#ddd", fontSize: 12, lineHeight: 1.5 }}>
            A producer-led marketplace built so artists and producers keep more of what they earn.
            No 50/50 splits, no middleman holding the money. Producers connect Stripe and get paid direct
            — we take a 1% platform fee, full stop.
          </div>
        </div>

        <div style={{ color: "#A78BFA", fontSize: 10, fontWeight: 800, letterSpacing: 1.5, marginBottom: 6, paddingLeft: 4 }}>
          GET IN TOUCH
        </div>

        <Row
          icon={<svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><path d="M4 4h16c1.1 0 2 .9 2 2v12c0 1.1-.9 2-2 2H4c-1.1 0-2-.9-2-2V6c0-1.1.9-2 2-2z"/><polyline points="22,6 12,13 2,6"/></svg>}
          label="EMAIL"
          value="support@beatfinder.co.uk"
          href="mailto:support@beatfinder.co.uk"
        />

        <div style={{ marginTop: 12, marginBottom: 12 }}>
          <div style={{ color: "#A78BFA", fontSize: 10, fontWeight: 800, letterSpacing: 1.5, marginBottom: 6, paddingLeft: 4 }}>
            HOW IT WORKS
          </div>
          <div style={{
            background: "#0d0d0d", border: "1px solid #2a2a2a", borderRadius: 12,
            padding: "10px 12px",
          }}>
            <div style={{ color: "#ddd", fontSize: 11, lineHeight: 1.5 }}>
              <div style={{ marginBottom: 4 }}>
                <span style={{ color: "#22C55E", fontWeight: 900, marginRight: 6 }}>•</span>
                Producers keep <strong style={{ color: "white" }}>up to 99%</strong> (1% platform fee).
              </div>
              <div style={{ marginBottom: 4 }}>
                <span style={{ color: "#3B82F6", fontWeight: 900, marginRight: 6 }}>•</span>
                Payouts direct to Stripe — never held by us.
              </div>
              <div style={{ marginBottom: 4 }}>
                <span style={{ color: "#A855F7", fontWeight: 900, marginRight: 6 }}>•</span>
                <strong style={{ color: "white" }}>Basic £50</strong> (non-exclusive) or <strong style={{ color: "white" }}>Premium £100–£500</strong> (exclusive).
              </div>
              <div>
                <span style={{ color: "#C026D3", fontWeight: 900, marginRight: 6 }}>•</span>
                Every purchase ships a written licence — no handshakes.
              </div>
            </div>
          </div>
        </div>

        <div style={{ color: "#444", fontSize: 10, textAlign: "center", marginTop: 8, lineHeight: 1.5 }}>
          © 2026 BeatFinder · London, UK
        </div>
      </div>
    </div>
  );
}

function CookieBanner() {
  // PECR (UK cookie law) requires opt-in for non-essential cookies.
  // We show this banner once; choice is persisted in localStorage.
  // "accept" = consent to all (analytics + functional);
  // "essential-only" = strictly necessary only (still allowed without consent).
  var [open, setOpen] = React.useState(function() {
    try {
      var existing = localStorage.getItem("bf_cookie_choice_v1");
      console.log("[BF cookie] mount check — existing choice:", existing, "→ open:", !existing);
      return !existing;
    } catch (e) { return true; }
  });
  if (!open) return null;
  function setChoice(choice) {
    try {
      localStorage.setItem("bf_cookie_choice_v1", choice);
      localStorage.setItem("bf_cookie_choice_at", String(Date.now()));
    } catch (e) {}
    try { window.dispatchEvent(new CustomEvent("bf-cookie-choice", { detail: { choice: choice } })); } catch(e) {}
    setOpen(false);
  }
  return (
    <div style={{
      position: "fixed", bottom: 0, left: 0, right: 0,
      zIndex: 99997,
      padding: "12px 14px max(12px, env(safe-area-inset-bottom))",
      background: "linear-gradient(180deg,rgba(15,10,31,0.85) 0%,rgba(10,10,20,0.97) 100%)",
      backdropFilter: "blur(16px)", WebkitBackdropFilter: "blur(16px)",
      borderTop: "1px solid rgba(124,58,237,0.45)",
      boxShadow: "0 -8px 32px rgba(0,0,0,0.6), 0 -1px 0 0 rgba(124,58,237,0.35)",
    }}>
      <div style={{ maxWidth: 520, margin: "0 auto" }}>
        <div style={{
          color: "#A78BFA", fontSize: 10, fontWeight: 900, letterSpacing: 1.5,
          marginBottom: 6, textShadow: "0 0 6px rgba(124,58,237,0.55)",
          display: "flex", alignItems: "center", gap: 6,
        }}>
          <svg width="11" height="11" viewBox="0 0 24 24" fill="none" stroke="#A78BFA" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" style={{ filter: "drop-shadow(0 0 4px rgba(124,58,237,0.5))" }}>
            <path d="M12 2a10 10 0 1 0 10 10 4 4 0 0 1-5-5 4 4 0 0 1-5-5"/>
            <path d="M8.5 8.5v.01"/>
            <path d="M16 15.5v.01"/>
            <path d="M12 12v.01"/>
            <path d="M11 17v.01"/>
            <path d="M7 14v.01"/>
          </svg>
          COOKIE NOTICE
        </div>
        <div style={{ color: "#ddd", fontSize: 12, lineHeight: 1.5, marginBottom: 10 }}>
          We use strictly-necessary cookies to keep you logged in and process payments. We'd also like to use functional cookies (saved preferences) and aggregate analytics to improve BeatFinder. You can change this later in Settings → Privacy Policy.
        </div>
        <div style={{ display: "flex", gap: 8 }}>
          <button onClick={function(){ setChoice("essential-only"); }} style={{
            flex: 1, background: "transparent", border: "1.5px solid #333",
            borderRadius: 10, padding: "10px",
            color: "#aaa", fontWeight: 800, fontSize: 12, letterSpacing: 0.3, cursor: "pointer",
          }}>
            Essential Only
          </button>
          <button onClick={function(){ setChoice("accept"); }} style={{
            flex: 1.4,
            background: "linear-gradient(135deg,#C026D3 0%,#A855F7 35%,#7C3AED 70%,#3B82F6 100%)",
            border: "1.5px solid rgba(192,38,211,0.6)",
            borderRadius: 10, padding: "10px",
            color: "white", fontWeight: 900, fontSize: 12, letterSpacing: 0.5, cursor: "pointer",
            boxShadow: "0 2px 14px rgba(192,38,211,0.5), inset 0 1px 0 rgba(255,255,255,0.25)",
            textShadow: "0 1px 2px rgba(0,0,0,0.3)",
          }}>
            Accept All
          </button>
        </div>
      </div>
    </div>
  );
}

function InstallPrompt() {
  const [show, setShow]               = React.useState(false);
  const [platform, setPlatform]       = React.useState(null); // "android" | "ios" | null
  const [deferredEvt, setDeferredEvt] = React.useState(null); // Android beforeinstallprompt event

  React.useEffect(function() {
    // Bail out if already installed (running in standalone mode)
    try {
      var isStandalone =
        (window.matchMedia && window.matchMedia("(display-mode: standalone)").matches) ||
        window.navigator.standalone === true;
      if (isStandalone) {
        console.log("[BF install] skipped — running in standalone (PWA) mode");
        return;
      }
    } catch(e) {}

    // Bail out if user dismissed recently (within 7 days)
    try {
      var dismissedAt = parseInt(localStorage.getItem("bf_install_dismissed") || "0", 10);
      if (dismissedAt && (Date.now() - dismissedAt) < 7 * 24 * 60 * 60 * 1000) {
        var daysLeft = Math.ceil((7 * 24 * 60 * 60 * 1000 - (Date.now() - dismissedAt)) / (24*60*60*1000));
        console.log("[BF install] skipped — dismissed", daysLeft, "days ago (7-day cooldown)");
        return;
      }
    } catch(e) {}

    // Wait until the cookie banner has been resolved — they should never
    // both be on screen at once. Cookie banner sets bf_cookie_choice_v1
    // on either button tap.
    function cookiesResolved() {
      try { return !!localStorage.getItem("bf_cookie_choice_v1"); } catch(e) { return true; }
    }
    if (!cookiesResolved()) {
      console.log("[BF install] waiting for cookie banner choice");
      function onCookieChoice() {
        window.removeEventListener("bf-cookie-choice", onCookieChoice);
        console.log("[BF install] cookie choice made, starting detection");
        setTimeout(function() { startDetection(); }, 400);
      }
      window.addEventListener("bf-cookie-choice", onCookieChoice);
      return function() { window.removeEventListener("bf-cookie-choice", onCookieChoice); };
    }
    var androidHandler = null;
    function startDetection() {
      var ua = (navigator.userAgent || "").toLowerCase();
      var isIOS = /iphone|ipad|ipod/.test(ua);
      var isMacTouchpad = navigator.maxTouchPoints > 1 && /macintosh/.test(ua);
      var isAndroid = /android/.test(ua);
      var isSafari = /safari/.test(ua) && !/crios|fxios|chrome|edg/.test(ua);
      console.log("[BF install] detection — iOS:", isIOS, "Safari:", isSafari, "Android:", isAndroid);
      if ((isIOS || isMacTouchpad) && isSafari) {
        console.log("[BF install] showing iOS Safari install instructions");
        setPlatform("ios");
        setShow(true);
      } else if (isAndroid) {
        console.log("[BF install] waiting for Android beforeinstallprompt event");
        androidHandler = function(e) {
          e.preventDefault();
          setDeferredEvt(e);
          setPlatform("android");
          setShow(true);
        };
        window.addEventListener("beforeinstallprompt", androidHandler);
      } else {
        console.log("[BF install] no eligible platform detected — no banner");
      }
    }
    startDetection();
    return function() {
      if (androidHandler) window.removeEventListener("beforeinstallprompt", androidHandler);
    };
  }, []);

  function dismiss() {
    try { localStorage.setItem("bf_install_dismissed", String(Date.now())); } catch(e) {}
    setShow(false);
  }

  async function handleInstall() {
    if (!deferredEvt) return;
    try {
      deferredEvt.prompt();
      var choice = await deferredEvt.userChoice;
      // Whether they accept or decline, hide the banner. If declined, snooze 7 days.
      if (choice && choice.outcome !== "accepted") {
        try { localStorage.setItem("bf_install_dismissed", String(Date.now())); } catch(e) {}
      }
      setShow(false);
      setDeferredEvt(null);
    } catch(e) {
      console.warn("[Install] Prompt failed:", e);
      setShow(false);
    }
  }

  if (!show || !platform) return null;

  return (
    <div style={{
      position: "fixed",
      left: 12, right: 12,
      bottom: "calc(env(safe-area-inset-bottom, 0px) + 12px)",
      zIndex: 99998,
      background: "linear-gradient(135deg,#16101f 0%,#0d0d12 100%)",
      border: "1.5px solid rgba(192,38,211,0.4)",
      borderRadius: 18,
      padding: 16,
      boxShadow: "0 12px 36px rgba(0,0,0,0.7), 0 0 30px rgba(192,38,211,0.15)",
      fontFamily: "'DM Sans',sans-serif",
      pointerEvents: "auto",
    }}>
      <button onClick={dismiss} aria-label="Dismiss"
        style={{
          position: "absolute", top: 8, right: 8,
          background: "transparent", border: "none",
          color: "#666", fontSize: 18, cursor: "pointer",
          padding: 6, lineHeight: 1,
        }}>×</button>

      <div style={{ display: "flex", gap: 12, alignItems: "flex-start", marginBottom: 12 }}>
        {/* BeatFinder logo bubble */}
        <div style={{
          width: 44, height: 44, borderRadius: 11, flexShrink: 0,
          background: "linear-gradient(135deg,#C026D3,#7C3AED)",
          display: "flex", alignItems: "center", justifyContent: "center",
          boxShadow: "0 0 14px rgba(192,38,211,0.5)",
        }}>
          <svg width="22" height="22" viewBox="0 0 24 24" fill="none" stroke="white" strokeWidth="2.2" strokeLinecap="round" strokeLinejoin="round">
            <path d="M9 18V5l12-2v13"/>
            <circle cx="6" cy="18" r="3"/>
            <circle cx="18" cy="16" r="3"/>
          </svg>
        </div>
        <div style={{ flex: 1, minWidth: 0, paddingRight: 20 }}>
          <div style={{ color: "white", fontWeight: 800, fontSize: 15, marginBottom: 2 }}>
            Install BeatFinder
          </div>
          <div style={{ color: "#999", fontSize: 12, lineHeight: 1.4 }}>
            {platform === "ios"
              ? "Get the full app experience on your home screen — faster loading, fullscreen, no browser bar."
              : "Add to your home screen for one-tap access and a native app feel."}
          </div>
        </div>
      </div>

      {platform === "android" ? (
        <button onClick={handleInstall} style={{
          width: "100%", border: "none", borderRadius: 12,
          background: "linear-gradient(135deg,#C026D3,#7C3AED)",
          color: "white", fontWeight: 800, fontSize: 14,
          padding: "12px", cursor: "pointer", letterSpacing: 0.3,
          boxShadow: "0 4px 14px rgba(192,38,211,0.4)",
        }}>
          Install App
        </button>
      ) : (
        <div style={{
          background: "rgba(255,255,255,0.04)",
          border: "1px solid rgba(255,255,255,0.08)",
          borderRadius: 12, padding: "10px 12px",
          color: "#bbb", fontSize: 12, lineHeight: 1.6,
        }}>
          <div style={{ display: "flex", alignItems: "center", gap: 6, marginBottom: 4 }}>
            <span style={{ color: "#999", fontWeight: 700 }}>1.</span>
            <span>Tap the</span>
            <svg width="14" height="18" viewBox="0 0 14 18" fill="none" stroke="#3B82F6" strokeWidth="1.6" strokeLinecap="round" strokeLinejoin="round" style={{ flexShrink: 0 }}>
              <path d="M7 11V1M3 5l4-4 4 4"/>
              <path d="M1 9v6a2 2 0 0 0 2 2h8a2 2 0 0 0 2-2V9"/>
            </svg>
            <span>Share button at the bottom of Safari</span>
          </div>
          <div style={{ display: "flex", alignItems: "center", gap: 6 }}>
            <span style={{ color: "#999", fontWeight: 700 }}>2.</span>
            <span>Scroll down and tap <strong style={{ color: "white" }}>"Add to Home Screen"</strong></span>
          </div>
        </div>
      )}
    </div>
  );
}

// =============================================================================
// PLAYLISTS — add-to-playlist picker, playlists list/manager, auto-advance player
// =============================================================================

// Bottom sheet: pick which playlist to add a video to (or create a new one).
function AddToPlaylistSheet({ video, onClose }) {
  const [lists, setLists]   = React.useState(null);
  const [creating, setCreating] = React.useState(false);
  const [newName, setNewName]   = React.useState("");
  const [busy, setBusy]     = React.useState(false);
  const [done, setDone]     = React.useState("");

  React.useEffect(function(){
    apiFetch("/api/playlists/list")
      .then(function(r){ setLists((r && r.playlists) || []); })
      .catch(function(){ setLists([]); });
  }, []);

  const addTo = function(pl){
    setBusy(true);
    apiFetch("/api/playlists/" + pl.id + "/add", { method: "POST", body: JSON.stringify({
      videoId: video.videoId, title: video.title || "", channel: video.channel || "",
      thumbnail: video.thumbnail || ("https://img.youtube.com/vi/" + video.videoId + "/hqdefault.jpg"),
    })})
      .then(function(){ setDone("Added to " + pl.name); setTimeout(onClose, 700); })
      .catch(function(){ setBusy(false); alert("Couldn't add to playlist."); });
  };
  const createAndAdd = function(){
    if (!newName.trim()) return;
    setBusy(true);
    apiFetch("/api/playlists/create", { method: "POST", body: JSON.stringify({
      name: newName.trim(),
      item: { videoId: video.videoId, title: video.title || "", channel: video.channel || "",
        thumbnail: video.thumbnail || ("https://img.youtube.com/vi/" + video.videoId + "/hqdefault.jpg") },
    })})
      .then(function(){ setDone("Created " + newName.trim()); setTimeout(onClose, 700); })
      .catch(function(){ setBusy(false); alert("Couldn't create playlist."); });
  };

  return (
    <div onClick={onClose} style={{ position: "fixed", inset: 0, zIndex: 100050, background: "rgba(0,0,0,0.6)", display: "flex", alignItems: "flex-end" }}>
      <div onClick={function(e){ e.stopPropagation(); }} style={{
        width: "100%", maxWidth: 520, margin: "0 auto",
        background: "linear-gradient(180deg,#140c24,#0a0716)",
        borderTopLeftRadius: 22, borderTopRightRadius: 22,
        border: "1px solid rgba(176,38,255,0.35)", borderBottom: "none",
        boxShadow: "0 -12px 40px rgba(0,0,0,0.6), 0 0 30px rgba(176,38,255,0.2)",
        padding: "16px 16px calc(env(safe-area-inset-bottom) + 18px)",
        maxHeight: "75vh", overflowY: "auto", WebkitOverflowScrolling: "touch",
      }}>
        <div style={{ width: 40, height: 4, borderRadius: 2, background: "rgba(255,255,255,0.2)", margin: "0 auto 14px" }} />
        <div style={{ color: "#fff", fontWeight: 800, fontSize: 17, marginBottom: 4 }}>Add to playlist</div>
        <div style={{ color: "#9590a8", fontSize: 12, marginBottom: 16, overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" }}>{video.title}</div>

        {done ? (
          <div style={{ color: "#4ADE80", fontSize: 14, fontWeight: 700, textAlign: "center", padding: "20px 0" }}>{done}</div>
        ) : (<>
          {!creating ? (
            <button onClick={function(){ setCreating(true); }} style={{
              width: "100%", display: "flex", alignItems: "center", gap: 10, marginBottom: 12,
              background: "linear-gradient(135deg,rgba(176,38,255,0.2),rgba(125,60,255,0.2))",
              border: "1px solid rgba(176,38,255,0.5)", color: "#E9D5FF",
              borderRadius: 12, padding: "13px 14px", fontWeight: 800, fontSize: 14, cursor: "pointer",
            }}><AppIcon id="plus" size={18} /> New playlist</button>
          ) : (
            <div style={{ display: "flex", gap: 8, marginBottom: 12 }}>
              <input autoFocus value={newName} onChange={function(e){ setNewName(e.target.value); }} placeholder="Playlist name (e.g. My Songs)" style={{
                flex: 1, background: "#0e0a1c", border: "1px solid rgba(176,38,255,0.35)", borderRadius: 12,
                color: "#e8e6f0", fontSize: 14, padding: "12px", boxSizing: "border-box",
              }} />
              <button onClick={createAndAdd} disabled={busy} style={{
                background: "linear-gradient(135deg,#B026FF,#7D3CFF)", border: "none", color: "#fff",
                borderRadius: 12, padding: "0 16px", fontWeight: 800, fontSize: 14, cursor: "pointer",
              }}>Create</button>
            </div>
          )}

          {lists === null ? (
            <div style={{ color: "#7a6ea0", fontSize: 13, textAlign: "center", padding: "20px 0" }}>Loading…</div>
          ) : lists.length === 0 && !creating ? (
            <div style={{ color: "#7a6ea0", fontSize: 13, textAlign: "center", padding: "16px 0" }}>No playlists yet — create your first one above.</div>
          ) : (
            lists.map(function(pl){
              return (
                <button key={pl.id} onClick={function(){ if (!busy) addTo(pl); }} style={{
                  width: "100%", display: "flex", alignItems: "center", gap: 12, marginBottom: 8,
                  background: "rgba(255,255,255,0.03)", border: "1px solid rgba(167,139,250,0.2)",
                  borderRadius: 12, padding: "10px 12px", cursor: "pointer", textAlign: "left",
                }}>
                  <div style={{ width: 44, height: 44, borderRadius: 9, flexShrink: 0, overflow: "hidden", background: "#1a1530", display: "flex", alignItems: "center", justifyContent: "center", color: "#A78BFA" }}>
                    {pl.cover ? <img src={pl.cover} alt="" style={{ width: "100%", height: "100%", objectFit: "cover" }} /> : <AppIcon id="music" size={18} />}
                  </div>
                  <div style={{ flex: 1, minWidth: 0 }}>
                    <div style={{ color: "#fff", fontWeight: 700, fontSize: 14, whiteSpace: "nowrap", overflow: "hidden", textOverflow: "ellipsis" }}>{pl.name}</div>
                    <div style={{ color: "#7a7296", fontSize: 11 }}>{pl.count} {pl.count === 1 ? "track" : "tracks"}</div>
                  </div>
                  <AppIcon id="plus" size={18} />
                </button>
              );
            })
          )}
        </>)}
      </div>
    </div>
  );
}

// Full-screen playlist player — uses the YouTube IFrame API to auto-advance.
function PlaylistPlayer({ playlist, startIndex, onClose }) {
  const items = (playlist && playlist.items) || [];
  const [idx, setIdx] = React.useState(startIndex || 0);
  const playerRef = React.useRef(null);
  const containerRef = React.useRef(null);
  const idxRef = React.useRef(idx);
  idxRef.current = idx;

  React.useEffect(function(){
    let cancelled = false;
    function makePlayer(){
      if (cancelled || !containerRef.current || !window.YT || !window.YT.Player) return;
      playerRef.current = new window.YT.Player(containerRef.current, {
        videoId: items[idxRef.current] ? items[idxRef.current].videoId : undefined,
        playerVars: { autoplay: 1, playsinline: 1, rel: 0, modestbranding: 1, controls: 1 },
        events: {
          onStateChange: function(e){
            if (e.data === 0) { // ENDED → next track
              const next = idxRef.current + 1;
              if (next < items.length) setIdx(next);
            }
          },
        },
      });
    }
    if (window.YT && window.YT.Player) {
      makePlayer();
    } else {
      const existing = window.onYouTubeIframeAPIReady;
      window.onYouTubeIframeAPIReady = function(){ if (existing) try { existing(); } catch(_){} makePlayer(); };
      if (!document.getElementById("bf-yt-iframe-api")) {
        const tag = document.createElement("script");
        tag.id = "bf-yt-iframe-api";
        tag.src = "https://www.youtube.com/iframe_api";
        document.body.appendChild(tag);
      }
    }
    return function(){ cancelled = true; try { if (playerRef.current && playerRef.current.destroy) playerRef.current.destroy(); } catch(_){} };
  }, []);

  React.useEffect(function(){
    const p = playerRef.current;
    if (p && p.loadVideoById && items[idx]) {
      try { p.loadVideoById(items[idx].videoId); } catch(_){}
    }
  }, [idx]);

  const cur = items[idx];
  return (
    <div style={{ position: "fixed", inset: 0, zIndex: 100060, background: "#050505", display: "flex", flexDirection: "column" }}>
      <div style={{ display: "flex", alignItems: "center", gap: 12, padding: "calc(env(safe-area-inset-top) + 12px) 14px 12px", borderBottom: "1px solid rgba(176,38,255,0.25)", background: "linear-gradient(180deg,#140c24,#0a0716)" }}>
        <button onClick={onClose} style={{ display: "inline-flex", alignItems: "center", gap: 6, background: "rgba(176,38,255,0.12)", border: "1px solid rgba(176,38,255,0.4)", color: "#E9D5FF", padding: "8px 14px 8px 10px", borderRadius: 22, fontWeight: 700, fontSize: 13, cursor: "pointer", flexShrink: 0 }}><AppIcon id="back" size={18} /> Back</button>
        <div style={{ flex: 1, minWidth: 0 }}>
          <div style={{ color: "#fff", fontWeight: 800, fontSize: 15, whiteSpace: "nowrap", overflow: "hidden", textOverflow: "ellipsis" }}>{playlist.name}</div>
          <div style={{ color: "#9590a8", fontSize: 11 }}>{idx + 1} / {items.length}</div>
        </div>
      </div>

      <div style={{ position: "relative", width: "100%", paddingTop: "56.25%", background: "#000" }}>
        <div ref={containerRef} style={{ position: "absolute", top: 0, left: 0, width: "100%", height: "100%" }} />
      </div>

      {cur && (
        <div style={{ padding: "14px 16px", borderBottom: "1px solid rgba(176,38,255,0.15)" }}>
          <div style={{ color: "#fff", fontWeight: 700, fontSize: 14 }}>{cur.title}</div>
          <div style={{ color: "#9590a8", fontSize: 12, marginTop: 2 }}>{cur.channel}</div>
        </div>
      )}

      <div style={{ flex: 1, overflowY: "auto", WebkitOverflowScrolling: "touch", padding: "10px 12px calc(env(safe-area-inset-bottom) + 20px)" }}>
        {items.map(function(it, i){
          const on = i === idx;
          return (
            <button key={it.videoId + "_" + i} onClick={function(){ setIdx(i); }} style={{
              width: "100%", display: "flex", alignItems: "center", gap: 11, marginBottom: 6,
              background: on ? "linear-gradient(135deg,rgba(176,38,255,0.18),rgba(0,213,255,0.1))" : "transparent",
              border: on ? "1px solid rgba(176,38,255,0.5)" : "1px solid transparent",
              borderRadius: 12, padding: "8px 10px", cursor: "pointer", textAlign: "left",
            }}>
              <div style={{ width: 46, height: 46, borderRadius: 8, overflow: "hidden", flexShrink: 0, background: "#1a1530" }}>
                <img src={it.thumbnail || ("https://img.youtube.com/vi/" + it.videoId + "/hqdefault.jpg")} alt="" style={{ width: "100%", height: "100%", objectFit: "cover" }} />
              </div>
              <div style={{ flex: 1, minWidth: 0 }}>
                <div style={{ color: on ? "#fff" : "#cbc4dd", fontWeight: on ? 800 : 600, fontSize: 13, lineHeight: 1.3, overflow: "hidden", display: "-webkit-box", WebkitLineClamp: 2, WebkitBoxOrient: "vertical" }}>{it.title}</div>
                <div style={{ color: "#7a7296", fontSize: 10, marginTop: 2, whiteSpace: "nowrap", overflow: "hidden", textOverflow: "ellipsis" }}>{it.channel}</div>
              </div>
              {on && <span style={{ color: "#00D5FF", display: "inline-flex" }}><AppIcon id="play" size={16} /></span>}
            </button>
          );
        })}
      </div>
    </div>
  );
}

// Playlists list/manager screen — opens from Saved tab.
function PlaylistsScreen({ onPlayPlaylist, onCount }) {
  const [lists, setLists] = React.useState(null);
  const load = React.useCallback(function(){
    apiFetch("/api/playlists/list").then(function(r){ var ls = (r && r.playlists) || []; setLists(ls); if (onCount) onCount(ls.length); }).catch(function(){ setLists([]); if (onCount) onCount(0); });
  }, [onCount]);
  React.useEffect(function(){ load(); }, [load]);

  const openAndPlay = function(pl){
    apiFetch("/api/playlists/" + pl.id)
      .then(function(full){ if (full && full.items && full.items.length) onPlayPlaylist(full, 0); else alert("This playlist is empty."); })
      .catch(function(){ alert("Couldn't open playlist."); });
  };
  const del = function(pl, e){
    e.stopPropagation();
    if (!window.confirm("Delete playlist \"" + pl.name + "\"?")) return;
    apiFetch("/api/playlists/" + pl.id, { method: "DELETE" }).then(load).catch(function(){ alert("Delete failed."); });
  };

  return (
    <div>
      <div style={{ color: "#fff", fontWeight: 800, fontSize: 18, fontFamily: "'Bebas Neue',sans-serif", letterSpacing: 1, marginBottom: 14, display: "flex", alignItems: "center", gap: 8 }}>
        <AppIcon id="music" size={20} /> MY PLAYLISTS
      </div>
      {lists === null ? (
        <div style={{ color: "#7a6ea0", fontSize: 13, padding: "20px 0" }}>Loading…</div>
      ) : lists.length === 0 ? (
        <div style={{ color: "#7a6ea0", fontSize: 13, padding: "20px 0", lineHeight: 1.6 }}>No playlists yet. Tap the playlist icon on any song or beat to start one.</div>
      ) : (
        lists.map(function(pl){
          return (
            <div key={pl.id} onClick={function(){ openAndPlay(pl); }} className="bf-press" style={{
              display: "flex", alignItems: "center", gap: 12, marginBottom: 10, cursor: "pointer",
              background: "linear-gradient(180deg, rgba(20,15,32,0.9), rgba(10,8,20,0.92))",
              border: "1px solid rgba(176,38,255,0.3)", borderRadius: 16, padding: "12px",
              boxShadow: "0 6px 18px rgba(0,0,0,0.5), 0 0 14px rgba(176,38,255,0.12)",
            }}>
              <div style={{ width: 56, height: 56, borderRadius: 11, overflow: "hidden", flexShrink: 0, background: "#1a1530", display: "flex", alignItems: "center", justifyContent: "center", color: "#A78BFA" }}>
                {pl.cover ? <img src={pl.cover} alt="" style={{ width: "100%", height: "100%", objectFit: "cover" }} /> : <AppIcon id="music" size={22} />}
              </div>
              <div style={{ flex: 1, minWidth: 0 }}>
                <div style={{ color: "#fff", fontWeight: 800, fontSize: 15, whiteSpace: "nowrap", overflow: "hidden", textOverflow: "ellipsis" }}>{pl.name}</div>
                <div style={{ color: "#7a7296", fontSize: 12, marginTop: 2 }}>{pl.count} {pl.count === 1 ? "track" : "tracks"}</div>
              </div>
              <button onClick={function(e){ del(pl, e); }} style={{ background: "transparent", border: "none", color: "#6b6385", cursor: "pointer", padding: 6 }}><AppIcon id="delete" size={17} /></button>
              <span style={{ color: "#00D5FF", display: "inline-flex" }}><AppIcon id="play" size={20} /></span>
            </div>
          );
        })
      )}
    </div>
  );
}

function BeatFinderInner() {
  // ── Install the global pop-up block detector once ────────────────────────
  // Runs on the very first user tap anywhere on the page (after this effect
  // mounts). This guarantees the warning banner appears for users who
  // resume into a "Licence Agreed" state from localStorage and would
  // otherwise never trigger the per-button detection.
  React.useEffect(function() {
    installGlobalPopupProbe();
  }, []);

  // ── Boot-time pricing sync ────────────────────────────────────────
  // Fetch /api/pricing once on app load and mutate PLAN_PRICES with
  // the server's authoritative values. Hardcoded fallbacks remain in
  // place so signup still works if the API hiccups during boot.
  // No auth required — pricing is public.
  React.useEffect(function() {
    fetch(API_BASE + "/api/stripe/pricing")
      .then(function(r) { return r.ok ? r.json() : null; })
      .then(function(data) {
        if (data) applyServerPricing(data);
      })
      .catch(function() {
        // Swallow — fallback values in PLAN_PRICES keep the signup flow
        // working even if the pricing endpoint is unreachable.
      });
  }, []);

  // ── Safe-area-inset stabilizer (minimal version) ─────────────────────
  // FIXES: page header overlapping the iOS status bar after closing an
  // in-app browser sheet (SFSafariViewController). Diagnostic data
  // showed that after the sheet dismisses, iOS reports
  // env(safe-area-inset-top) as 0 for ~100ms — layouts pinned to env()
  // get stuck at the wrong position.
  //
  // FIX: pin all layouts to a CSS variable --bf-safe-top that we set
  // ONCE from JS on first load. The variable wins over env(), so iOS
  // can't poison the layout during the bad-values window.
  //
  // Why this version is minimal — earlier attempts polled on visibility
  // change and re-measured on every event, which created a feedback
  // loop: the probe insertion itself fired resize events, which
  // re-triggered the stabilizer. Now we just measure once at startup
  // and never touch it again. Trade-off: phone rotation won't update
  // the value, but the resize-loop bug is much worse.
  React.useEffect(function() {
    function measureSafeTop() {
      try {
        var probe = document.createElement("div");
        probe.style.cssText = "position:fixed;top:env(safe-area-inset-top);left:0;width:1px;height:1px;pointer-events:none;opacity:0;";
        document.body.appendChild(probe);
        var top = probe.getBoundingClientRect().top;
        probe.remove();
        return Math.round(top);
      } catch(_) { return 0; }
    }

    function setSafeTopVar(px) {
      try {
        document.documentElement.style.setProperty("--bf-safe-top", px + "px");
      } catch(_) {}
    }

    // Measure the canonical safe-top once at startup and pin the
    // variable to it. We do NOT update this on visibility/focus/resize
    // because that creates a feedback loop on iOS — the probe insertion
    // itself fires resize events, which re-trigger the stabilizer, which
    // re-inserts the probe... ad infinitum. Setting it once on a stable
    // post-load tick avoids all that.
    //
    // The trade-off: rotation won't change the variable, so a phone
    // rotated mid-session will have slightly stale safe-top. We accept
    // that — the alternative (the resize loop) is much worse.
    setTimeout(function() {
      var v = measureSafeTop();
      if (v > 0) setSafeTopVar(v);
    }, 200);

    // No event listeners. No polling. No teardown needed.
  }, []);

  // ── External browser dismissal recovery ──────────────────────────────────
  // When BeatFinder navigates the user away to a social media / external URL
  // (YouTube, Instagram, TikTok, Spotify, Apple Music etc.), iOS Safari may
  // restore BeatFinder as a blank white page when the user comes back via
  // the back button. The bfcache restoration isn't reliable for SPAs.
  //
  // Additionally, tapping a "suggested video" inside the YouTube iframe can
  // cause iOS to navigate the parent tab (not just the iframe) and the back
  // gesture leaves BeatFinder in a broken state.
  //
  // We use multiple signals to detect and recover from blank state:
  //   1. pageshow with e.persisted = true (definitive bfcache restore)
  //   2. visibilitychange becoming visible — if app root is missing or
  //      body background is white, the React tree died and we reload
  //   3. pagehide with persisted=true means the page WAS bfcached, so the
  //      next pageshow needs special handling
  //   4. focus event — iOS sometimes fires focus before visibilitychange
  React.useEffect(function() {
    var wasBFCached = false;
    var reloadInProgress = false;
    var lastRafTime = Date.now();
    var rafHandle = null;
    var pollHandle = null;

    function safeReload() {
      if (reloadInProgress) return;
      reloadInProgress = true;
      try {
        // Use replace so the broken state doesn't sit in history
        window.location.replace(window.location.pathname + window.location.search);
      } catch(e) {
        window.location.reload();
      }
    }

    function isAppBlank() {
      try {
        var appEl = document.querySelector("[data-bf-app]");
        if (!appEl) return true;
        var root = document.getElementById("root") || appEl;
        if (root && root.children.length === 0) return true;
        // Check if the app element has zero rendered area
        var rect = appEl.getBoundingClientRect();
        if (rect.width === 0 || rect.height === 0) return true;
        return false;
      } catch(e) {
        return true;
      }
    }

    function checkAndRecover() {
      // Skip recovery while iOS share sheet is up (Save to Files, etc.).
      // The share sheet backgrounds the app for several seconds while the
      // user picks a location and taps Save. When it dismisses, RAF
      // resumes after a >800ms gap and the recovery would otherwise
      // false-positive: app rect is briefly 0 during iOS's repaint, so
      // safeReload() fires and bounces the user out of studio.
      try {
        const flag = sessionStorage.getItem("bf_share_sheet_active");
        if (flag) {
          // Older than 60s? expired — clear and proceed normally.
          if (Date.now() - parseInt(flag, 10) < 60000) return;
          sessionStorage.removeItem("bf_share_sheet_active");
        }
      } catch(e) {}
      if (isAppBlank()) safeReload();
    }

    // RAF-gap detection: when iOS opens an in-app web viewer / system browser
    // (Safari View Controller, the YouTube share-sheet preview, etc.) over the
    // top of BeatFinder, requestAnimationFrame pauses. As soon as the user
    // dismisses the overlay ("Done" button) and returns, RAF resumes — so a
    // big gap in RAF timestamps is the most reliable signal of a return.
    // visibilitychange and focus aren't always fired by iOS's in-app viewer.
    function tickRaf() {
      var now = Date.now();
      var gap = now - lastRafTime;
      if (gap > 800) {
        // We were paused (likely backgrounded or covered by in-app viewer).
        // Check several times in case the DOM hasn't re-rendered yet.
        // The safe-area stabilizer effect handles the layout reflow on
        // its own once iOS sends the correct insets — this just handles
        // the blank-app case.
        setTimeout(checkAndRecover, 50);
        setTimeout(checkAndRecover, 250);
        setTimeout(checkAndRecover, 700);
        setTimeout(checkAndRecover, 1500);
      }
      lastRafTime = now;
      rafHandle = window.requestAnimationFrame(tickRaf);
    }
    rafHandle = window.requestAnimationFrame(tickRaf);

    // Backup: poll every 2s to catch any case where RAF detection misses
    pollHandle = window.setInterval(function() {
      if (document.visibilityState === "visible") checkAndRecover();
    }, 2000);

    function onPageShow(e) {
      // e.persisted = true means page was restored from back-forward cache.
      // On iOS this often means the React tree is alive but the DOM may
      // be visually blank. A reload guarantees a clean state.
      if (e.persisted || wasBFCached) {
        wasBFCached = false;
        safeReload();
        return;
      }
      // Even if not bfcached, check immediately if the app root is missing
      checkAndRecover();
    }

    function onPageHide(e) {
      // If the page is being put into bfcache, remember so we can recover
      // even if pageshow.persisted reports false on the return trip
      if (e.persisted) {
        wasBFCached = true;
      }
    }

    function onVisibilityChange() {
      if (document.visibilityState === "visible") {
        // Multiple checks at different delays to catch slow restorations.
        // The safe-area stabilizer effect (separate useEffect) handles
        // forcing a layout reflow once iOS sends correct inset values.
        setTimeout(checkAndRecover, 50);
        setTimeout(checkAndRecover, 200);
        setTimeout(checkAndRecover, 500);
        setTimeout(checkAndRecover, 1000);
        setTimeout(checkAndRecover, 1500);
      } else {
        // Page is being hidden — mark RAF time so we can detect a long gap on return
        lastRafTime = Date.now();
      }
    }

    function onFocus() {
      // iOS sometimes fires focus before visibilitychange when returning
      // from an external link / YouTube suggested video tap
      setTimeout(checkAndRecover, 100);
      setTimeout(checkAndRecover, 400);
    }

    window.addEventListener("pageshow",      onPageShow);
    window.addEventListener("pagehide",      onPageHide);
    window.addEventListener("focus",         onFocus);
    document.addEventListener("visibilitychange", onVisibilityChange);
    return function() {
      window.removeEventListener("pageshow",      onPageShow);
      window.removeEventListener("pagehide",      onPageHide);
      window.removeEventListener("focus",         onFocus);
      document.removeEventListener("visibilitychange", onVisibilityChange);
      if (rafHandle) window.cancelAnimationFrame(rafHandle);
      if (pollHandle) window.clearInterval(pollHandle);
    };
  }, []);

  const [splashDone, setSplashDone] = useState(false);
  // welcomeDone: true once user has passed the welcome gate this session
  // Uses sessionStorage so it resets every time the tab is opened
  const [welcomeDone, setWelcomeDone] = useState(function() {
    // If user is already logged in, skip the welcome gate
    try { if (localStorage.getItem("bf_token")) return true; } catch(e) {}
    // If the page was opened on a public profile URL, skip the welcome gate
    // so the visitor sees the profile straight away (and the profile is
    // rendered inside the main app tree so scrolling works in Safari).
    try {
      if (/^\/(?:u|profile)\/[^/]+/.test(window.location.pathname)) return true;
    } catch(e) {}
    return false;
  });
  // showAuthWall: true when user tapped "Sign In" — shows auth form full-screen, no app
  const [showAuthWall,   setShowAuthWall]   = useState(false);
  const [authStartMode,  setAuthStartMode]  = useState("login");

  const doWelcome = function() {
    setWelcomeDone(true);
  };
  const doWelcomeAsGuest = function() {
    setShowAuthWall(false);
    doWelcome();
  };
  const [tab, setTab] = useState(function() {
    // Restore the active tab after an iOS background reload.
    // Never open with studio on fresh load — user must navigate there
    // intentionally — UNLESS we know we were mid-export when the reload
    // happened (Save-to-Files share sheet can cause iOS to reload Safari
    // tabs in low-memory situations). In that case, restore the user to
    // the studio so they don't lose their place after exporting.
    try {
      const saved = sessionStorage.getItem("bf_tab");
      const shareSheetActive = sessionStorage.getItem("bf_share_sheet_active");
      if (saved === "studio" && shareSheetActive) {
        // Came back from a share sheet — restore studio.
        return "studio";
      }
      return (saved && saved !== "studio") ? saved : "home";
    } catch(e) { return "home"; }
  });
  // studioVisited: true once the user has navigated to Studio at least once this session.
  // StudioScreen only mounts after first visit — prevents mic permission firing on page load.
  // If we're restoring to studio (returning from share sheet), pre-flip this so
  // StudioScreen actually mounts on first render — otherwise the user lands on
  // a blank studio tab.
  const [studioVisited, setStudioVisited] = useState(function() {
    try {
      const saved = sessionStorage.getItem("bf_tab");
      const shareSheetActive = sessionStorage.getItem("bf_share_sheet_active");
      return saved === "studio" && !!shareSheetActive;
    } catch(e) { return false; }
  });
  // Bumped whenever the user re-taps the Artists tab while already on it.
  // ArtistsScreen watches this and clears its internal selectedArtist state,
  // returning the user to the artist-list root.
  const [artistsResetKey, setArtistsResetKey] = useState(0);
  const [searchQuery, setSearchQuery] = useState("");
  // When something routes the user into Search and wants to land them on a
  // specific tab (e.g. Feed empty-state → People tab), pass that here.
  const [searchInitialTab, setSearchInitialTab] = useState(null);
  const [artist,  setArtist]  = useState(null); // kept for compatibility
  const [user,    setUser]    = useState(null);
  // Brief banner shown when the user's token expires mid-session. Triggered
  // by apiFetch dispatching a bf:auth-expired event after a 401. We clear
  // the user state and show this so they know what happened — much nicer
  // than features silently failing.
  const [authExpiredBanner, setAuthExpiredBanner] = useState(false);
  // Public overlay screens triggered by global events (e.g. from Settings).
  // Using events rather than prop threading because the trigger is deep
  // inside ProfileScreen and the overlay is at app-root level.
  const [showChangelog, setShowChangelog] = useState(false);
  const [showAbout,     setShowAbout]     = useState(false);
  React.useEffect(function() {
    function onChangelog() { setShowChangelog(true); }
    function onAbout()     { setShowAbout(true); }
    window.addEventListener("bf:openChangelog", onChangelog);
    window.addEventListener("bf:openAbout",     onAbout);
    return function() {
      window.removeEventListener("bf:openChangelog", onChangelog);
      window.removeEventListener("bf:openAbout",     onAbout);
    };
  }, []);
  React.useEffect(function() {
    function onAuthExpired() {
      setUser(null);
      setAuthExpiredBanner(true);
      // Auto-dismiss after 6s in case they ignore it
      setTimeout(function() { setAuthExpiredBanner(false); }, 6000);
    }
    window.addEventListener("bf:auth-expired", onAuthExpired);
    return function() { window.removeEventListener("bf:auth-expired", onAuthExpired); };
  }, []);
  // Global lease cache — fetched once at login and refreshed on returning
  // from Stripe success. Every CompactBeatActionSheet reads from this instead
  // of issuing its own /my-leases request, which (a) makes sheet opens
  // instant for already-purchased beats, (b) cuts redundant network calls,
  // and (c) keeps state consistent across PWA / Safari / multiple devices
  // since the server is the single source of truth.
  const [userLeases, setUserLeases] = useState(null); // null = not yet loaded, [] = loaded but empty
  // Refresh function — called on login, app boot, and after Stripe success
  const refreshUserLeases = React.useCallback(function() {
    return apiFetch("/api/producer/my-leases")
      .then(function(leases) {
        setUserLeases(Array.isArray(leases) ? leases : []);
        return leases;
      })
      .catch(function() {
        setUserLeases([]);
        return [];
      });
  }, []);
  // Expose to all CompactBeatActionSheet instances via a window-level event
  // — they listen for "bf-leases-updated" and re-check their own beat against
  // the new list. This avoids prop-drilling through 4 different card types.
  React.useEffect(function() {
    if (userLeases !== null) {
      try {
        window.__bf_leases__ = userLeases;
        window.dispatchEvent(new CustomEvent("bf-leases-updated"));
      } catch(e) {}
    }
  }, [userLeases]);
  const [playing, setPlaying] = useState(function() {
    // Restore the beat being viewed if the page was reloaded mid-view
    // (e.g. after iOS bfcache recovery from a YouTube suggested-video tap)
    try {
      const raw = sessionStorage.getItem("bf_playing");
      if (raw) return JSON.parse(raw);
    } catch(e) {}
    return null;
  });
  // Playlist system: the video the user is adding to a playlist (opens picker),
  // and the playlist currently playing through the playlist player.
  const [addToPlaylistVideo, setAddToPlaylistVideo] = useState(null);
  const [playingPlaylist, setPlayingPlaylist] = useState(null); // { playlist, index }
  React.useEffect(function(){
    function onAdd(e){
      if (e && e.detail && e.detail.video && e.detail.video.videoId) {
        if (!user) { try { window.dispatchEvent(new CustomEvent("bf:quickSignup", { detail: { intent: "playlist" } })); } catch(_){} return; }
        setAddToPlaylistVideo(e.detail.video);
      }
    }
    window.addEventListener("bf-add-to-playlist", onAdd);
    return function(){ window.removeEventListener("bf-add-to-playlist", onAdd); };
  }, [user]);

  // Whenever the currently-viewed beat changes, persist it so we can restore
  // after iOS reloads the page from bfcache or external link return
  React.useEffect(function() {
    try {
      if (playing) {
        sessionStorage.setItem("bf_playing", JSON.stringify(playing));
      } else {
        sessionStorage.removeItem("bf_playing");
      }
    } catch(e) {}
  }, [playing]);

  // savedMap: { [videoId]: beat } - localStorage for guests, backend for logged-in users
  const [savedMap, setSavedMap] = useState(loadSaved);
  const savedIds = new Set(Object.keys(savedMap));

  // Handle URL routing — reset_token and /u/:username public profiles
  const resetToken = new URLSearchParams(window.location.search).get("reset_token");
  const leaseSuccess   = new URLSearchParams(window.location.search).get("lease") === "success";
  const leaseCancelled = new URLSearchParams(window.location.search).get("lease") === "cancelled";
  const leaseBeatId  = new URLSearchParams(window.location.search).get("beat_id");
  const [leaseModal, setLeaseModal] = React.useState(leaseSuccess);
  const [leaseBeat,  setLeaseBeat]  = React.useState(null);
  const [showLeaseContract, setShowLeaseContract] = React.useState(false);

  // When returning from Stripe AFTER CANCEL (user tapped Stripe's "Back" link).
  // Per UX decision: don't try to restore the exact page/sheet — just clean up
  // and let them land on home. They'll re-discover the beat from there if they
  // still want it. Avoids confusion if they backed out because they clicked
  // the wrong tier by mistake.
  React.useEffect(function() {
    if (!leaseCancelled) return;
    try { sessionStorage.removeItem("bf_payment_in_flight"); } catch(e) {}
    try { sessionStorage.removeItem("bf_return_tab"); } catch(e) {}
    // Strip the ?lease=cancelled params from the URL
    try { window.history.replaceState({}, "", "/"); } catch(e) {}
    setTab("home");
  }, []);

  // ── Kit purchase return from Stripe ───────────────────────────
  // The webhook records ownership; on return we trigger the device-local
  // sample download so the kit is immediately usable in Studio. Retries
  // briefly in case the webhook hasn't landed the ownership record yet.
  React.useEffect(function() {
    var kqs = new URLSearchParams(window.location.search);
    if (kqs.get("kit") !== "success") return;
    var kitId = kqs.get("kit_id");
    try { window.history.replaceState({}, "", "/"); } catch(e) {}
    setTab("exclusive");
    if (!kitId) return;
    var attempts = 0;
    var tryDownload = function() {
      attempts++;
      apiFetch("/api/kits/" + kitId + "/download")
        .then(function(payload){
          return bfInstallMarketKit(payload).then(function(){ return bfRegisterLocalMarketKits(); });
        })
        .then(function(){ /* installed — appears under My Kits in Studio */ })
        .catch(function(){
          if (attempts < 4) setTimeout(tryDownload, 1500 * attempts);
        });
    };
    setTimeout(tryDownload, 1200);
  }, []);

  // Detect iOS PWA "Done" dismissal of Stripe checkout. In PWA mode, tapping
  // Done collapses the whole webview and the app reloads fresh on next launch.
  // We persist a payment-in-flight flag in sessionStorage right before
  // redirecting to Stripe; on app mount, if that flag is still set and we're
  // NOT returning via Stripe's success/cancel redirect, the user dismissed
  // Stripe via Done. Per UX decision: just clean up flags and land on home.
  // No path restore, no sheet reopen — avoids reopening unwanted sheets if
  // the user backed out because they tapped the wrong tier by mistake.
  React.useEffect(function() {
    var beatId;
    try { beatId = sessionStorage.getItem("bf_payment_in_flight"); } catch(e) {}
    if (!beatId) return;
    var qs = new URLSearchParams(window.location.search);
    if (qs.get("lease") === "success" || qs.get("lease") === "cancelled") {
      // Returning via Stripe redirect — let those dedicated handlers run.
      return;
    }
    // Done dismissal. Clear all flags. Land on home — splash will play first.
    try { sessionStorage.removeItem("bf_payment_in_flight"); } catch(e) {}
    try { sessionStorage.removeItem("bf_return_tab"); } catch(e) {}
    try { window.__bf_pending_reopen__ = null; } catch(e) {}
    try { window.__bf_reopen_consumed__ = null; } catch(e) {}
    setTab("home");
  }, []);

  // When returning from Stripe, fetch the purchased beat details and restore page
  React.useEffect(function() {
    if (!leaseSuccess || !leaseBeatId || !user) return;
    // Successful payment — clear the Done-detection flag.
    try { sessionStorage.removeItem("bf_payment_in_flight"); } catch(e) {}
    // Clear the URL params without reload
    window.history.replaceState({}, "", "/");
    // Restore the tab — default to members/exclusive area where beats are purchased
    try {
      var saved = JSON.parse(sessionStorage.getItem("bf_return_tab") || "{}");
      sessionStorage.removeItem("bf_return_tab");
      setTab(saved.tab || "exclusive");
    } catch(e) {
      setTab("exclusive");
    }
    // Refresh the global lease cache so every card now sees the new lease.
    // Then locate the just-purchased beat to display the success modal.
    refreshUserLeases().then(function(leases) {
      var match = (leases || []).find(function(l) { return l.beat_id === leaseBeatId || l.id === leaseBeatId; });
      if (match) setLeaseBeat(match);
    });
  }, [user]);
  // /u/username or /profile/username — open a public profile directly from a shared link
  const urlUsername = (function() {
    const m = window.location.pathname.match(/^\/(?:u|profile)\/([^/]+)/);
    return m ? decodeURIComponent(m[1]) : null;
  })();

  // When user logs OUT (user goes from truthy to null), clear the savedMap
  // so we don't show their private beats to the next guest session.
  // We use a ref to detect the transition rather than running on every guest render.
  const prevUserRef = React.useRef(user);
  useEffect(() => {
    if (prevUserRef.current && !user) {
      // Logged out — restore from localStorage so guest keeps their data
      setSavedMap(loadSaved());
      try {
        const localLyrics = JSON.parse(localStorage.getItem("bf_lyrics") || "[]");
        setSavedLyrics(localLyrics);
      } catch(e) {}
    }
    prevUserRef.current = user;
  }, [user]);

  // Mark session as started on mount — used to skip splash on iOS background reload
  useEffect(() => {
    try { sessionStorage.setItem("bf_session_started", "1"); } catch(e) {}
  }, []);

  // Restore session from stored JWT on app load
  useEffect(() => {
    const token = getToken();
    if (!token) return;
    AuthAPI.me()
      .then(u => {
        const active = u.subscriptionActive || false;
        try { var _adm = (u.is_admin === true || u.username === "Trelli"); window.__bfIsAdmin = _adm; localStorage.setItem("bf_is_admin", _adm ? "1" : "0"); } catch(e) {}
        setUser({
          ...u,
          isPro:       active && u.plan === "producer",
          isArtistPro: active && (u.plan === "artist" || u.plan === "producer"),
        });
        // Auto-skip welcome — user already has a valid session
        try { localStorage.setItem("bf_welcomed", "1"); } catch(e) {}
        setWelcomeDone(true);
        // BACKFILL: any free-licence agreements that exist in localStorage
        // but have never been sent to the server. This covers users who
        // agreed BEFORE the /agree-licence endpoint shipped (their
        // agreements only live in this context's localStorage). After the
        // backfill completes, the GET below will see them and they'll
        // sync to all other contexts.
        try {
          var backfillKeys = [];
          for (var i = 0; i < localStorage.length; i++) {
            var k = localStorage.key(i);
            if (k && k.indexOf("bf_signed_free_") === 0) {
              var beatId = k.slice("bf_signed_free_".length);
              // Skip non-Mongo-style ids (YouTube urls/titles can't be saved server-side)
              if (beatId && /^[a-f0-9]{24}$/.test(beatId)) {
                // Skip ones we've already backfilled this device (avoid hammering on every boot)
                if (localStorage.getItem("bf_backfilled_" + beatId) !== "1") {
                  backfillKeys.push(beatId);
                }
              }
            }
          }
          if (backfillKeys.length > 0) {
            console.log("[BF backfill] sending", backfillKeys.length, "local-only agreements to server");
            Promise.all(backfillKeys.map(function(id) {
              return apiFetch("/api/producer/beats/" + id + "/agree-licence", { method: "POST" })
                .then(function() { try { localStorage.setItem("bf_backfilled_" + id, "1"); } catch(e) {} return true; })
                .catch(function(err) {
                  // 404 means beat was deleted — mark as backfilled so we don't retry
                  if (err && /404|not found/i.test(err.message || "")) {
                    try { localStorage.setItem("bf_backfilled_" + id, "1"); } catch(e) {}
                  }
                  return false;
                });
            })).then(function() {
              console.log("[BF backfill] complete — re-fetching server list");
              // Re-fetch and re-hydrate now that the server has the full picture
              apiFetch("/api/producer/my-free-licences")
                .then(function(res) {
                  var ids = (res && res.beat_ids) || [];
                  ids.forEach(function(id) {
                    try { localStorage.setItem("bf_signed_free_" + id, "1"); } catch(e) {}
                  });
                  try { window.dispatchEvent(new CustomEvent("bf-free-licences-hydrated")); } catch(e) {}
                  console.log("[BF backfill] post-fetch ids:", ids.length);
                }).catch(function(){});
            });
          }
        } catch(e) { console.warn("[BF backfill] error", e && e.message); }

        // Hydrate free-licence agreements from server into localStorage.
        // The agreed-licence state was previously localStorage-only, which is
        // sandboxed per-browser-context on iOS (Safari tab vs home-screen PWA
        // have separate localStorages). Pulling the server list and writing
        // each agreed beat back into localStorage means the "Licence Agreed
        // ✓" state appears consistently no matter which context the user
        // opens beatfinder.co.uk from.
        apiFetch("/api/producer/my-free-licences")
          .then(function(res) {
            var ids = (res && res.beat_ids) || [];
            console.log("[BF hydrate/boot] /my-free-licences returned", ids.length, "ids:", ids);
            ids.forEach(function(id) {
              try { localStorage.setItem("bf_signed_free_" + id, "1"); } catch(e) {}
            });
            // Tell already-mounted FreeBeatCTA components to re-read
            // localStorage. They mount BEFORE this hydration completes,
            // so their initial useState read missed these IDs — without
            // this event, the UI would stay in the "unsigned" state until
            // the user refreshes the page.
            try { window.dispatchEvent(new CustomEvent("bf-free-licences-hydrated")); } catch(e) {}
          })
          .catch(function(err) { console.error("[BF hydrate/boot] /my-free-licences FAILED", err && err.message); });
        // TEMPORARY: also call the debug endpoint to see raw DB state
        apiFetch("/api/producer/debug-free-licences")
          .then(function(d) { console.log("[BF DEBUG free-licences]", d); })
          .catch(function(err) { console.error("[BF DEBUG free-licences] FAILED", err && err.message); });
        // Also hydrate the paid-lease cache so beat sheets that need to check
        // ownership can do so instantly without their own API call.
        refreshUserLeases();
      })
      .catch(() => clearToken());
  }, []);

  // Re-hydrate cross-context state when the user returns to this tab/window.
  // Free-licence agreements and paid leases may have been added in another
  // browser context (PWA vs Safari, or another device), so we re-pull from
  // server whenever the document becomes visible again.
  useEffect(() => {
    if (!user) return;
    function rehydrate() {
      if (document.visibilityState !== "visible") return;
      // Re-pull free licences and tell mounted components to re-check
      apiFetch("/api/producer/my-free-licences")
        .then(function(res) {
          var ids = (res && res.beat_ids) || [];
          ids.forEach(function(id) {
            try { localStorage.setItem("bf_signed_free_" + id, "1"); } catch(e) {}
          });
          try { window.dispatchEvent(new CustomEvent("bf-free-licences-hydrated")); } catch(e) {}
        })
        .catch(function() { /* non-fatal */ });
      // Re-pull paid leases too (window.__bf_leases__ + bf-leases-updated event)
      refreshUserLeases();
    }
    document.addEventListener("visibilitychange", rehydrate);
    return function() { document.removeEventListener("visibilitychange", rehydrate); };
  }, [user]);

  // Poll unread counts every 15s when logged in, plus an immediate
  // refresh whenever the tab becomes visible again. This keeps the bell
  // badge live without hammering the server (15s × hidden = paused).
  useEffect(() => {
    if (!user) { setUnreadMessages(0); setUnreadNotifications(0); return; }
    function fetchUnread() {
      // Skip polling when the tab is hidden — saves Render request quota
      if (document.hidden) return;
      // Two parallel calls:
      //   • notifications/unread-count for the notifications bell
      //   • messages/summary for INBOX-only message count
      // The top-bar message badge counts only accepted-inbox messages,
      // NOT pending requests. Requests get their own indicator inside
      // the Messages screen itself (Instagram-style behaviour). The
      // older notifications endpoint also returns a `messages` field
      // but it counts ALL unread including pending requests — by using
      // messages/summary instead we get the cleaner number.
      Promise.all([
        apiFetch("/api/notifications/unread-count").catch(function(){ return null; }),
        apiFetch("/api/messages/summary").catch(function(){ return null; }),
      ]).then(function(results) {
        var notifData = results[0];
        var msgData   = results[1];
        if (msgData && typeof msgData.inboxUnread === "number") {
          setUnreadMessages(msgData.inboxUnread);
        } else if (notifData) {
          // Fallback if messages/summary not deployed yet — use the
          // legacy total. Slightly inflated but won't break the UI.
          setUnreadMessages(notifData.messages || 0);
        }
        if (notifData) {
          setUnreadNotifications(notifData.notifications || 0);
        }
      });
    }
    fetchUnread();
    var interval = setInterval(fetchUnread, 15000);
    // Immediate refresh when user returns to the tab
    var onVis = function() { if (!document.hidden) fetchUnread(); };
    var onFocus = function() { fetchUnread(); };
    document.addEventListener("visibilitychange", onVis);
    window.addEventListener("focus", onFocus);
    return function() {
      clearInterval(interval);
      document.removeEventListener("visibilitychange", onVis);
      window.removeEventListener("focus", onFocus);
    };
  }, [user]);

  // When user opens messages, clear message badge
  function openMessages(thread) {
    setMessageThread(thread || null);
    setShowMessages(true);
    setUnreadMessages(0);
  }

  // When notifications panel closes, clear badge
  function openNotifications() {
    setShowNotifications(true);
    setUnreadNotifications(0);
  }

  // When user logs in, merge local guest beats with their backend library
  useEffect(() => {
    if (!user) return;
    const guestBeats = loadSaved(); // beats saved while guest
    BeatsAPI.list()
      .then(list => {
        const map = {};
        list.forEach(b => { map[b.video_id] = { videoId: b.video_id, title: b.title, channel: b.channel, thumbnail: b.thumbnail }; });
        // Merge guest beats into backend (upload any that aren't already there)
        Object.values(guestBeats).forEach(beat => {
          if (!map[beat.videoId]) {
            map[beat.videoId] = beat;
            BeatsAPI.save(beat).catch(() => {}); // sync guest beat to account
          }
        });
        setSavedMap(map);
        persistSaved(map);
      })
      .catch(() => {
        // Backend unavailable — keep guest beats in memory
        setSavedMap(guestBeats);
      });
  }, [user]);

  const [showAuthPrompt, setShowAuthPrompt] = useState(false);
  const [showGuestWarning, setShowGuestWarning] = useState(false);
  const [promptReason,   setPromptReason]   = useState("studio"); // "studio" | "profile" | "save" | "buy" | "download" | "rhymefinder" | "producer" | "expired"

  // Quick-signup state for guests who try to download a free beat or buy
  // a basic lease. Holds { intent, beat, resumeAction } — resumeAction
  // is the function to call after the account is created so the user
  // doesn't have to tap again.
  //   intent="download"  → guest tapped download on a free beat
  //   intent="buyBasic"  → guest tapped Buy on a basic-lease beat
  const [quickSignup, setQuickSignup] = useState(null);

  // Listen for global upgrade-required events from anywhere in the tree
  React.useEffect(function() {
    function handler(e) {
      var r = (e.detail && e.detail.reason) || "save";
      setPromptReason(r);
      setShowAuthPrompt(true);
    }
    window.addEventListener("bf:requireUpgrade", handler);
    return function() { window.removeEventListener("bf:requireUpgrade", handler); };
  }, []);

  // Listen for guest-quick-signup events from beat cards.
  //   detail = { intent: "download"|"buyBasic", beat, resume: fn(user) }
  // resume is invoked AFTER the new user is set, so the originating card
  // can finish the action without the user having to tap again.
  React.useEffect(function() {
    function handler(e) {
      var d = e.detail || {};
      if (!d.intent || !d.beat) return;
      setQuickSignup({ intent: d.intent, beat: d.beat, resume: d.resume || null });
    }
    window.addEventListener("bf:quickSignup", handler);
    return function() { window.removeEventListener("bf:quickSignup", handler); };
  }, []);

  const toggleSave = useCallback(beat => {
    const isPaid = user && (user.isArtistPro || user.isPro);
    const already = !!savedMap[beat.videoId];
    // GATE: guests cannot save anything — must create a free account.
    if (!user) {
      window.dispatchEvent(new CustomEvent("bf:quickSignup", { detail: { intent: "save" } }));
      return;
    }
    // Free (signed-in, unpaid) users can save up to a cap; then prompt upgrade.
    const FREE_SAVE_CAP = 10;
    if (!isPaid && !already && Object.keys(savedMap).length >= FREE_SAVE_CAP) {
      setPromptReason("save");
      setShowAuthPrompt(true);
      return;
    }
    setSavedMap(prev => {
      const next = { ...prev };
      if (next[beat.videoId]) {
        delete next[beat.videoId];
        BeatsAPI.remove(beat.videoId).catch(console.warn);
      } else {
        next[beat.videoId] = beat;
        BeatsAPI.save(beat).catch(console.warn);
      }
      persistSaved(next);
      return next;
    });
  }, [user, savedMap]);


  const handleSaveLyric = useCallback((lyric, editIndex) => {
    const isEditing = editIndex !== null && editIndex !== undefined;
    // GATE: guests cannot save lyrics — must create a free account.
    if (!user) {
      window.dispatchEvent(new CustomEvent("bf:quickSignup", { detail: { intent: "lyrics" } }));
      return;
    }
    const isPaid = user.isArtistPro || user.isPro;
    const FREE_LYRIC_CAP = 5;
    setSavedLyrics(prev => {
      // Free users can save up to a cap of new lyrics; editing existing is fine.
      if (!isPaid && !isEditing && (prev || []).length >= FREE_LYRIC_CAP) {
        setPromptReason("lyrics");
        setShowAuthPrompt(true);
        return prev;
      }
      let next;
      if (isEditing) {
        next = prev.map((l, i) => i === editIndex ? lyric : l);
      } else {
        next = [lyric, ...prev];
      }
      try { localStorage.setItem("bf_lyrics", JSON.stringify(next)); } catch(e) {}
      apiFetch("/api/lyrics", { method: "POST", body: JSON.stringify(lyric) }).catch(() => {});
      return next;
    });
  }, [user]);

  const handleOpenLyrics = useCallback(beat => {
    if (!user) { window.dispatchEvent(new CustomEvent("bf:quickSignup", { detail: { intent: "lyrics" } })); return; }
    setPlaying(null);
    setLyricsBeat(beat);
    setEditingLyric(null);
    setEditingIndex(null);
    setLyricsFromStudio(false);
    setLyricsOpen(true);
  }, [user]);

  // Open the lyrics notepad without any beat — for standalone writing
  // (used by Profile → My Lyrics → New Blank Lyric, and the Studio
  // workspace lyrics overlay). LyricsNotepad already hides the YouTube
  // iframe when beat is null.
  const handleNewLyric = useCallback(() => {
    setPlaying(null);
    setLyricsBeat(null);
    setEditingLyric(null);
    setEditingIndex(null);
    setLyricsFromStudio(false);
    setLyricsOpen(true);
  }, []);

  // Studio-specific variants — set lyricsFromStudio=true so the notepad
  // shows the in-header transport controls. Same payload, different flag.
  const handleNewLyricFromStudio = useCallback(() => {
    setPlaying(null);
    setLyricsBeat(null);
    setEditingLyric(null);
    setEditingIndex(null);
    setLyricsFromStudio(true);
    setLyricsOpen(true);
  }, []);

  const handleEditLyric = useCallback((lyric, index) => {
    // Reconstruct beat object from stored fields if full object not available
    var beatObj = lyric.beat || null;
    if (!beatObj && lyric.beatId) {
      beatObj = {
        videoId:      lyric.beatId,
        title:        lyric.beatTitle || "Beat",
        channel:      lyric.beatChannel || "",
        channelTitle: lyric.beatChannel || "",
      };
    }
    setLyricsBeat(beatObj);
    setEditingLyric(lyric);
    setEditingIndex(index);
    setLyricsFromStudio(false);
    setLyricsOpen(true);
  }, []);

  // Same as handleEditLyric but force-hides the YouTube beat embed.
  // Used when opening saved lyrics from Profile → My Lyrics or from the
  // Studio side panel — these contexts should not surface the YouTube
  // player even if the lyric was originally written against a YT beat.
  // The original beat reference is preserved on the lyric record itself
  // (beatId / beatTitle / beatChannel) so future opens from the main
  // player can still reattach it.
  const handleEditLyricNoBeat = useCallback((lyric, index) => {
    setLyricsBeat(null);
    setEditingLyric(lyric);
    setEditingIndex(index);
    setLyricsFromStudio(false);
    setLyricsOpen(true);
  }, []);

  // Studio-specific variant — same as handleEditLyricNoBeat plus the flag.
  const handleEditLyricFromStudio = useCallback((lyric, index) => {
    setLyricsBeat(null);
    setEditingLyric(lyric);
    setEditingIndex(index);
    setLyricsFromStudio(true);
    setLyricsOpen(true);
  }, []);

  const handlePlay = useCallback(beat => setPlaying(beat), []);
  // Lightweight in-app navigation history — tracks the user's tab path so
  // both browser back gestures AND in-screen Back buttons can pop the stack.
  // We push every tab change EXCEPT re-taps of the same tab.
  const navHistoryRef = useRef([]);

  const goTab = id => {
    // Guests cannot access Studio or Profile — prompt sign-in
    if ((id === "studio" || id === "profile") && !user) {
      setPromptReason(id);
      setShowAuthPrompt(true);
      return;
    }
    // Studio is paid-only — free signed-in users get the "Pro Plan Required" prompt.
    // Expired paid users get a different "Subscription Expired" prompt.
    if (user && id === "studio" && !user.isArtistPro && !user.isPro) {
      // Distinguish "never paid" (plan === "free") from "expired paid"
      setPromptReason(user.plan === "free" ? "studio" : "expired");
      setShowAuthPrompt(true);
      return;
    }
    // Exclusive (Members Area) requires an active paid subscription
    if (user && id === "exclusive" && !user.subscriptionActive) {
      setPromptReason(user.plan === "free" ? "studio" : "expired");
      setShowAuthPrompt(true);
      return;
    }
    setPlaying(null);

    // Re-tap behaviour: if the user taps the tab they're already on, signal
    // the tab's component to reset to its root view (only Artists uses this
    // right now — clears selectedArtist back to the artist list).
    setTab(prev => {
      if (prev === id) {
        if (id === "artists") setArtistsResetKey(k => k + 1);
        return prev;
      }
      // Push the previous tab onto the back stack
      navHistoryRef.current.push(prev);
      // Cap history length to prevent memory growth
      if (navHistoryRef.current.length > 50) {
        navHistoryRef.current.shift();
      }
      // Also push to browser history so the native back gesture works
      try {
        window.history.pushState({ bfTab: id }, "", "");
      } catch (e) {}
      return id;
    });

    if (id === "studio") setStudioVisited(true);
    try { sessionStorage.setItem("bf_tab", id); } catch(e) {}
  };

  // Pop the back stack — used by browser back gesture / popstate.
  // Returns true if it popped, false if nothing to pop (caller can decide
  // whether to ignore or exit the app).
  const goBack = useCallback(() => {
    const hist = navHistoryRef.current;
    if (hist.length === 0) return false;
    const prevTab = hist.pop();
    setPlaying(null);
    setTab(prevTab);
    try { sessionStorage.setItem("bf_tab", prevTab); } catch(e) {}
    return true;
  }, []);

  // Listen for browser back gesture (popstate fires when iOS swipe-back
  // is used or when the user taps a hardware back button on Android).
  useEffect(function() {
    const onPopState = function(e) {
      // If our in-app history has entries, pop one; otherwise let the
      // browser do its default thing (which usually means closing the PWA).
      if (navHistoryRef.current.length > 0) {
        const popped = navHistoryRef.current.pop();
        setPlaying(null);
        setTab(popped);
        try { sessionStorage.setItem("bf_tab", popped); } catch(err) {}
      }
    };
    window.addEventListener("popstate", onPopState);
    return function() { window.removeEventListener("popstate", onPopState); };
  }, []);

  // Lyrics state — backed by MongoDB for logged-in users, localStorage for guests
  const [lyricsOpen,    setLyricsOpen]    = useState(false);
  const [lyricsBeat,    setLyricsBeat]    = useState(null);
  const [editingLyric,  setEditingLyric]  = useState(null);
  const [editingIndex,  setEditingIndex]  = useState(null);
  // Track whether the lyrics editor was launched from inside Studio. When
  // true, LyricsNotepad shows transport controls (record/play/seek) in its
  // header so the user can keep recording while writing lyrics — the main
  // fixed transport bar is hidden behind the fullscreen lyrics overlay.
  const [lyricsFromStudio, setLyricsFromStudio] = useState(false);
  // Ref holding the live Studio transport API. The Studio populates this
  // on mount and clears on unmount; LyricsNotepad reads it through the
  // studioControls prop and the snapshot is refreshed via a tiny tick state.
  const studioTransportRef = useRef(null);
  const [studioTransportTick, setStudioTransportTick] = useState(0);
  // If the page was opened via /u/:username or /profile/:username, boot
  // straight into that public profile rendered inside the main app tree
  // (same code path as in-app navigation). This avoids the broken early-
  // return that didn't allow scrolling in Safari (non-PWA).
  const [publicProfile, setPublicProfile] = useState(function() {
    return urlUsername || null;
  });
  const [searchProfile, setSearchProfile] = useState(null);
  const [showMessages,       setShowMessages]       = useState(false);
  const [showPost,           setShowPost]           = useState(false);
  // Bumped each time the user successfully posts, so the FollowingFeed
  // re-fetches and shows their own post immediately at the top.
  const [feedRefreshKey,     setFeedRefreshKey]     = useState(0);
  // When a user taps a notification, we stash the post id here, jump to
  // the Feed, and the Feed component scrolls to + highlights that post.
  // Cleared back to null once the highlight animation finishes.
  const [highlightPostId,    setHighlightPostId]    = useState(null);
  const [messageThread,      setMessageThread]      = useState(null);
  const [showNotifications,  setShowNotifications]  = useState(false);
  // Global "find a user" overlay — opened from the magnifying-glass
  // icon in the top bar. Lets users search by username from anywhere
  // on the Home screen and jump straight to that user's public
  // profile.
  const [showUserSearch,     setShowUserSearch]     = useState(false);
  const [unreadMessages,     setUnreadMessages]     = useState(0);
  const [unreadNotifications,setUnreadNotifications]= useState(0);
  const [savedLyrics,   setSavedLyrics]   = useState(() => {
    try { return JSON.parse(localStorage.getItem("bf_lyrics") || "[]"); } catch { return []; }
  });

  // When user logs in: fetch their lyrics from backend, migrate any local ones
  useEffect(() => {
    if (!user) return;
    apiFetch("/api/lyrics")
      .then(cloudLyrics => {
        var localLyrics = [];
        try { localLyrics = JSON.parse(localStorage.getItem("bf_lyrics") || "[]"); } catch(e) {}
        const cloudIds = new Set(cloudLyrics.map(l => l.id));
        const toMigrate = localLyrics.filter(l => l.id && !cloudIds.has(l.id));
        if (toMigrate.length > 0) {
          // Upload local-only lyrics to backend
          apiFetch("/api/lyrics/bulk-import", {
            method: "POST",
            body: JSON.stringify({ lyrics: toMigrate }),
          }).then(() => {
            const merged = [...toMigrate, ...cloudLyrics];
            setSavedLyrics(merged);
            try { localStorage.setItem("bf_lyrics", JSON.stringify(merged)); } catch(e) {}
          }).catch(() => {
            // bulk-import failed — still show merged locally
            const merged = [...toMigrate, ...cloudLyrics];
            setSavedLyrics(merged);
            try { localStorage.setItem("bf_lyrics", JSON.stringify(merged)); } catch(e) {}
          });
        } else {
          // No local-only lyrics — use cloud as source of truth
          setSavedLyrics(cloudLyrics);
          try { localStorage.setItem("bf_lyrics", JSON.stringify(cloudLyrics)); } catch(e) {}
        }
      })
      .catch(() => {
        // Backend unavailable — keep localStorage version
      });
  }, [user?.id]);

  const isArtistPro = !!(user?.subscriptionActive && (user?.isPro || user?.isArtistPro || user?.plan === "artist" || user?.plan === "producer"));


  // Public profile URL — handled by setting publicProfile state from urlUsername
  // (see useState initializer above). We render the profile INSIDE the main app
  // tree which provides the proper Safari scroll context. We also clean the URL
  // so a refresh boots directly to the public profile via state, not URL routing.
  React.useEffect(function() {
    if (urlUsername) {
      try { window.history.replaceState({}, "", "/"); } catch(e) {}
    }
  }, []);

  if (resetToken) {
    return (
      <div data-bf-app="1" style={{ maxWidth: 430, margin: "0 auto", minHeight: "100vh", background: "#0a0a0a", fontFamily: "'DM Sans',sans-serif", paddingTop: "var(--bf-safe-top, env(safe-area-inset-top))" }}>
        <DownloadToast />
        <PopupBlockedModal />
        <ResetPasswordScreen token={resetToken} onDone={() => { window.history.replaceState({}, "", "/"); window.location.reload(); }} />
      </div>
    );
  }

  return (
    <div key="app-root" data-bf-app="1" id="bf-portrait-lock" style={{ maxWidth: 430, margin: "0 auto", minHeight: "100vh", background: "#0a0a0a", fontFamily: "'DM Sans',sans-serif", paddingTop: "var(--bf-safe-top, env(safe-area-inset-top))" }}>
      <link href="https://fonts.googleapis.com/css2?family=Bebas+Neue&family=DM+Sans:wght@400;600;700;800&display=swap" rel="stylesheet" />
      <DownloadToast />
      <BfToastHost />
      <PopupBlockedModal />
      <GlobalMiniPlayer />

      {/* Changelog overlay — opened via window event "bf:openChangelog"
          Rendered via portal to document.body so it escapes the
          maxWidth:430 phone-frame ancestor. Otherwise position:fixed
          can get trapped on iOS Safari when an ancestor has constraints
          like maxWidth/margin auto, leaving the overlay with the wrong
          scroll viewport. */}
      {showChangelog && typeof document !== "undefined" && document.body && ReactDOM.createPortal(
        (
          <div style={{
            position: "fixed", inset: 0, zIndex: 99996,
            background: "#0a0a0a",
          }}>
            <ChangelogScreen onBack={function() { setShowChangelog(false); }} />
          </div>
        ),
        document.body
      )}

      {/* About overlay — same portal treatment as Changelog above. */}
      {showAbout && typeof document !== "undefined" && document.body && ReactDOM.createPortal(
        (
          <div style={{
            position: "fixed", inset: 0, zIndex: 99996,
            background: "#0a0a0a",
          }}>
            <AboutScreen onBack={function() { setShowAbout(false); }} />
          </div>
        ),
        document.body
      )}

      {/* Auth-expired toast — shown briefly when apiFetch detects a 401
          after a previously-valid token. Tells the user they've been
          signed out and how to fix it without leaving them confused
          about why features stopped working. */}
      {authExpiredBanner && (
        <div onClick={function() { setAuthExpiredBanner(false); }} style={{
          position: "fixed",
          top: "calc(var(--bf-safe-top, env(safe-area-inset-top)) + 12px)",
          left: "50%",
          transform: "translateX(-50%)",
          zIndex: 99998,
          background: "linear-gradient(135deg,#1a0a14,#0d0a14)",
          border: "1px solid rgba(239,68,68,0.4)",
          borderRadius: 12,
          padding: "12px 16px",
          maxWidth: 360, width: "calc(100vw - 32px)",
          color: "white",
          fontSize: 13,
          fontFamily: "'DM Sans',sans-serif",
          boxShadow: "0 8px 24px rgba(0,0,0,0.6), 0 0 16px rgba(239,68,68,0.2)",
          display: "flex", alignItems: "center", gap: 10,
          cursor: "pointer",
        }}>
          <div style={{ flex: "0 0 auto", width: 24, height: 24, borderRadius: "50%",
            background: "rgba(239,68,68,0.15)", border: "1px solid rgba(239,68,68,0.4)",
            display: "flex", alignItems: "center", justifyContent: "center",
            color: "#F87171", fontWeight: 900, fontSize: 14 }}>!</div>
          <div style={{ flex: 1, lineHeight: 1.4 }}>
            <div style={{ fontWeight: 700, marginBottom: 2 }}>You've been signed out</div>
            <div style={{ color: "#aaa", fontSize: 11 }}>Sign back in to continue.</div>
          </div>
          <div style={{ color: "#555", fontSize: 16 }}>×</div>
        </div>
      )}

      {!splashDone && <SplashScreen onDone={() => setSplashDone(true)} />}

      {/* Welcome screen — shown once per session after splash */}
      {splashDone && !welcomeDone && !showAuthWall && (
        <WelcomeScreen
          onSignIn={function() { setAuthStartMode("login"); setShowAuthWall(true); }}
          onGuest={doWelcomeAsGuest}
        />
      )}

      {/* Auth wall — full-screen login/signup, no app behind it.
          Use dvh (dynamic viewport height) instead of inset:0 because
          iOS Safari's bottom toolbar doesn't reduce the viewport for
          fixed-positioned elements; dvh accounts for the toolbar. */}
      {splashDone && showAuthWall && !welcomeDone && (
        <div style={{
          position: "fixed",
          top: 0, left: 0, right: 0,
          height: "100dvh",
          zIndex: 99999,
          background: "#0a0a0a",
          overflowY: "auto",
          WebkitOverflowScrolling: "touch",
          overscrollBehavior: "contain",
          fontFamily: "'DM Sans',sans-serif",
          paddingTop: "var(--bf-safe-top, env(safe-area-inset-top))",
        }}>
          <button
            onClick={function() { setShowAuthWall(false); }}
            style={{ background: "none", border: "none", color: "white", fontSize: 22,
              cursor: "pointer", padding: "10px 16px", display: "block" }}>
            ←
          </button>
          <RootAuthScreen startMode={authStartMode} onLogin={function(u) {
            const active = !!u.subscriptionActive;
            try { var _adm = (u.is_admin === true || u.username === "Trelli"); window.__bfIsAdmin = _adm; localStorage.setItem("bf_is_admin", _adm ? "1" : "0"); } catch(e) {}
            setUser({
              ...u,
              isPro:       active && u.plan === "producer",
              isArtistPro: active && (u.plan === "artist" || u.plan === "producer"),
            });
            // Hydrate free-licence agreements from server into localStorage
            // so this newly-logged-in session sees the same "Licence Agreed ✓"
            // state across PWA/Safari/devices.
            apiFetch("/api/producer/my-free-licences")
              .then(function(res) {
                var ids = (res && res.beat_ids) || [];
                ids.forEach(function(id) {
                  try { localStorage.setItem("bf_signed_free_" + id, "1"); } catch(e) {}
                });
                try { window.dispatchEvent(new CustomEvent("bf-free-licences-hydrated")); } catch(e) {}
              })
              .catch(function() { /* non-fatal */ });
            // Also hydrate the paid-lease cache so every sheet renders the
            // correct "Save MP3 to Device" state instantly for already-owned
            // beats — no per-sheet network round-trip required.
            refreshUserLeases();
            doWelcome();
          }} />
        </div>
      )}

      {showGuestWarning && (
        <div style={{
          position: "fixed", inset: 0, zIndex: 20001,
          background: "rgba(0,0,0,0.85)", display: "flex",
          alignItems: "center", justifyContent: "center",
          padding: 24, fontFamily: "'DM Sans',sans-serif",
        }} onClick={() => setShowGuestWarning(false)}>
          <div style={{
            background: "#111", border: "1.5px solid #F59E0B",
            borderRadius: 20, padding: 28, width: "100%", maxWidth: 340,
            textAlign: "center",
          }} onClick={e => e.stopPropagation()}>
            <div style={{ marginBottom: 12 }}>
              <svg width="40" height="40" viewBox="0 0 24 24" fill="none" stroke="#F59E0B" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                <path d="M10.29 3.86L1.82 18a2 2 0 0 0 1.71 3h16.94a2 2 0 0 0 1.71-3L13.71 3.86a2 2 0 0 0-3.42 0z"/>
                <line x1="12" y1="9" x2="12" y2="13"/>
                <line x1="12" y1="17" x2="12.01" y2="17"/>
              </svg>
            </div>
            <div style={{ color: "white", fontWeight: 800, fontSize: 18, marginBottom: 8 }}>
              Saves Stored on This Device
            </div>
            <div style={{ color: "#888", fontSize: 14, lineHeight: 1.7, marginBottom: 24 }}>
              You're using BeatFinder as a guest. Your saves are kept on this device only and can be lost if you clear your browser data or switch devices. Create a free account to access them anywhere.
            </div>
            <button onClick={() => {
              try { localStorage.setItem("bf_guest_warning_shown", "1"); } catch(e) {}
              setShowGuestWarning(false);
              setShowAuthWall(true);
              setWelcomeDone(false);
            }}
              style={{ width: "100%", background: "linear-gradient(135deg,#F59E0B,#D97706)", border: "none", borderRadius: 32, color: "white", fontWeight: 800, fontSize: 16, padding: "14px", cursor: "pointer", marginBottom: 12 }}>
              Create Free Account
            </button>
            <button onClick={() => {
              try { localStorage.setItem("bf_guest_warning_shown", "1"); } catch(e) {}
              setShowGuestWarning(false);
            }}
              style={{ background: "none", border: "none", color: "#555", fontSize: 14, cursor: "pointer" }}>
              Continue as Guest
            </button>
          </div>
        </div>
      )}

      {/* PWA install prompt — shows on first visit for Android Chrome (native)
          and iOS Safari (manual instructions). Hidden if already installed
          or dismissed within the last 7 days. */}
      <InstallPrompt />

      {/* Cookie consent banner (PECR/UK GDPR). Shows once; choice persists. */}
      <CookieBanner />

      {/* Terms & Conditions acceptance modal.
          - Logged-in users: appears once per TERMS_VERSION; acceptance is
            persisted server-side via /api/auth/accept-terms.
          - Guests: appears on every fresh session (sessionStorage only).
          - Blocks the UI until the user scrolls to the bottom and taps
            "I AGREE". The version bump rule means we can re-prompt all
            users globally simply by bumping TERMS_VERSION above. */}
      <TermsModal user={user} onAccepted={function(version) {
        // Mirror server response onto the in-memory user object so the
        // modal doesn't re-open on subsequent re-renders this session.
        if (user) setUser(Object.assign({}, user, { terms_accepted_version: version }));
      }} />

      {/* Username setup — required for any logged-in user without a
          username. Catches historic accounts (pre-username-at-signup)
          and edge cases where signup completed but set-username failed.
          Modal handles its own visibility via user.username check. */}
      <UsernameSetupModal user={user} onSet={function(username) {
        if (user) setUser(Object.assign({}, user, { username: username }));
      }} />

      {/* Quick signup for guests — fires when a guest tries to download
          a free beat or purchase a basic lease. On success the new user
          is set as the logged-in user, and the original action is
          resumed (if a resume fn was provided). */}
      {quickSignup && (
        <QuickSignupModal
          intent={quickSignup.intent}
          beat={quickSignup.beat}
          onClose={function() { setQuickSignup(null); }}
          onAccountCreated={function(newUser) {
            // Set the user with computed Pro flags so any gates that
            // re-fire during the resume see the up-to-date account.
            // (New free accounts have isPro/isArtistPro=false, but
            // the resume actions here don't gate on Pro for basic-lease
            // or free download.)
            var fullUser = Object.assign({}, newUser, {
              isPro:       newUser.plan === "producer",
              isArtistPro: newUser.plan === "artist" || newUser.plan === "producer",
            });
            setUser(fullUser);
            var resumeFn = quickSignup.resume;
            setQuickSignup(null);
            // Defer the resume so React commits the user state first.
            // Otherwise the resume closure still sees the stale user=null
            // and gates re-fire incorrectly.
            if (resumeFn) {
              setTimeout(function() { try { resumeFn(fullUser); } catch (_) {} }, 80);
            }
          }}
        />
      )}

      {showAuthPrompt && (
        <div style={{
          position: "fixed", inset: 0, zIndex: 20000,
          background: "rgba(0,0,0,0.85)", display: "flex",
          alignItems: "center", justifyContent: "center",
          padding: 24, fontFamily: "'DM Sans',sans-serif",
        }} onClick={() => setShowAuthPrompt(false)}>
          <div style={{
            background: "#111", border: "1.5px solid " + (promptReason === "expired" ? "#F59E0B" : "#C026D3"),
            borderRadius: 20, padding: 28, width: "100%", maxWidth: 340,
            textAlign: "center",
          }} onClick={e => e.stopPropagation()}>
            <div style={{ fontSize: 40, marginBottom: 12, color: promptReason === "expired" ? "#F59E0B" : "#C026D3" }}>
              {promptReason === "expired"
                ? <svg width="36" height="36" viewBox="0 0 24 24" fill="none" stroke="#F59E0B" strokeWidth="2" strokeLinecap="round"><circle cx="12" cy="12" r="10"/><polyline points="12 6 12 12 16 14"/></svg>
                : <AppIcon id={promptReason === "studio" ? "studio" : promptReason === "producer" ? "money" : promptReason === "save" ? "heart" : promptReason === "buy" ? "stripe" : promptReason === "download" ? "upload" : promptReason === "rhymefinder" ? "edit" : "profile"} size={36} />}
            </div>
            <div style={{ color: "white", fontWeight: 800, fontSize: 18, marginBottom: 8 }}>
              {promptReason === "expired" ? "Subscription Expired"
               : promptReason === "studio" || promptReason === "producer" ? "Pro Plan Required"
               : promptReason === "save" ? "Upgrade to Save Beats"
               : promptReason === "buy" ? "Upgrade to Buy Leases"
               : promptReason === "download" ? "Upgrade to Download"
               : promptReason === "rhymefinder" ? "Upgrade for Rhyme Finder"
               : "Sign In Required"}
            </div>
            <div style={{ color: "#888", fontSize: 14, lineHeight: 1.7, marginBottom: 24 }}>
              {promptReason === "expired"
                ? "Your subscription has ended. Renew your plan to regain access to Studio, exclusive beats and all Pro features."
                : promptReason === "studio"
                ? "Purchase a Pro plan to unlock Studio mode and start recording."
                : promptReason === "producer"
                ? "Purchase Producer Pro to upload beats, sell leases and get paid instantly."
                : promptReason === "save"
                ? "Saving beats requires Artist Pro or Producer Pro. Upgrade to build your library and access them anywhere."
                : promptReason === "buy"
                ? "Buying beat leases requires Artist Pro or Producer Pro. Upgrade to license tracks and get the MP3 + contract instantly."
                : promptReason === "download"
                ? "Downloading MP3s requires Artist Pro or Producer Pro. Free accounts can preview only."
                : promptReason === "rhymefinder"
                ? "Rhyme Finder is part of Artist Pro and Producer Pro. Upgrade to unlock the lyric-writing assistant."
                : "Sign in to access your profile, save beats and more."}
            </div>
            <button onClick={() => { setShowAuthPrompt(false); if (user) { goTab("exclusive"); } else if (promptReason === "expired" || promptReason === "studio" || promptReason === "producer") { goTab("exclusive"); } else { setShowAuthWall(true); setWelcomeDone(false); } }}
              style={{ width: "100%", background: promptReason === "expired" ? "linear-gradient(135deg,#F59E0B,#EF4444)" : "linear-gradient(135deg,#C026D3,#7C3AED)", border: "none", borderRadius: 32, color: "white", fontWeight: 800, fontSize: 16, padding: "14px", cursor: "pointer", marginBottom: 12 }}>
              {promptReason === "expired" ? "Renew Subscription"
               : (user ? "View Pro Plans" : "Sign In / Create Account")}
            </button>
            <button onClick={() => setShowAuthPrompt(false)}
              style={{ background: "none", border: "none", color: "#555", fontSize: 14, cursor: "pointer" }}>
              Maybe later
            </button>
          </div>
        </div>
      )}

      {publicProfile && (
        <div style={{ position: "fixed", inset: 0, zIndex: 9998, background: "#0a0a0a", overflowY: "auto", overscrollBehavior: "none", WebkitOverflowScrolling: "touch" }} onTouchMove={function(e){ e.stopPropagation(); }}>
          <PublicProfileScreen username={publicProfile} onBack={() => setPublicProfile(null)} onPlay={handlePlay} savedIds={savedIds} onSave={toggleSave} currentUser={user} onMessage={u => { setPublicProfile(null); setMessageThread(u); setShowMessages(true); }} />
        </div>
      )}

      {showMessages && (
        <div style={{ position: "fixed", inset: 0, zIndex: 9999, background: "#0a0a0a", overflowY: "auto", overscrollBehavior: "none", paddingTop: "var(--bf-safe-top, env(safe-area-inset-top))", fontFamily: "'DM Sans',sans-serif" }} onTouchMove={function(e){ e.stopPropagation(); }}>
          <MessagesScreen user={user} onBack={() => { setShowMessages(false); setMessageThread(null); }} initialThread={messageThread} onViewProfile={u => { setShowMessages(false); setPublicProfile(u); }} />
        </div>
      )}

      <div style={{ position: "relative" }}>
        {["home","artists","feed","trending","search","saved","studio","exclusive"].map(t => (
          <div key={t} style={{
            display: tab === t ? "block" : "none",
            ...(t === "studio" ? {
              position: "fixed",
              top: 0, left: 0, right: 0, bottom: 0,
              zIndex: 10,
              overflow: "hidden",
            } : {
              overflowY: "auto",
              height: "calc(100dvh - calc(72px + env(safe-area-inset-bottom)))",
              WebkitOverflowScrolling: "touch",
              overscrollBehavior: "none",
            }),
          }}
          onTouchMove={t !== "studio" ? function(e){ e.stopPropagation(); } : undefined}
          >
            {t === "home"      && <HomeScreen savedIds={savedIds} onSave={toggleSave} onPlay={handlePlay} user={user} onGoMembers={() => goTab("exclusive")} onGoProfile={() => goTab("profile")} onGenreSearch={q => { setSearchQuery(q); goTab("search"); }} savedLyrics={savedLyrics} onEditLyric={handleEditLyric} onGoTrending={() => goTab("trending")} onGoStudio={() => goTab("studio")} onWriteLyrics={handleNewLyric} onGoArtists={() => goTab("artists")} onGoUpload={() => { goTab("profile"); setTimeout(function(){ window.dispatchEvent(new CustomEvent("bf-open-section", { detail: { section: "upload" } })); }, 120); }} onGoSection={(section) => { goTab("profile"); setTimeout(function(){ window.dispatchEvent(new CustomEvent("bf-open-section", { detail: { section: section } })); }, 120); }} onShowProducerPrompt={() => { setPromptReason("producer"); setShowAuthPrompt(true); }} onOpenMessages={() => openMessages(null)} onViewOwnProfile={() => user ? setPublicProfile(user.username) : goTab("profile")} onOpenPost={() => setShowPost(true)} onOpenNotifications={openNotifications} onOpenUserSearch={() => setShowUserSearch(true)} unreadMessages={unreadMessages} unreadNotifications={unreadNotifications} />}
            {t === "artists"   && <ArtistsScreen onPlay={handlePlay} savedIds={savedIds} onSave={toggleSave} resetKey={artistsResetKey} currentUser={user} />}
            {t === "feed"      && <FollowingFeed user={user} onPlay={handlePlay} savedIds={savedIds} onSave={toggleSave} onViewProfile={function(u) { setPublicProfile(u); }} onSearchPeople={function() { setSearchInitialTab("people"); goTab("search"); }} onOpenPost={() => setShowPost(true)} refreshKey={feedRefreshKey} highlightPostId={highlightPostId} onHighlightConsumed={() => setHighlightPostId(null)} />}
            {t === "trending"  && <TrendingScreen savedIds={savedIds} onSave={toggleSave} onPlay={handlePlay} onViewProfile={function(u) { setPublicProfile(u); }} user={user} />}
            {t === "search"    && <SearchScreen savedIds={savedIds} onSave={toggleSave} onPlay={handlePlay} initialQuery={searchQuery} onClearInitial={() => setSearchQuery("")} initialTab={searchInitialTab} onClearInitialTab={() => setSearchInitialTab(null)} currentUser={user} onViewProfile={function(u) { setSearchProfile(u); }} />}
            {t === "saved"     && <SavedScreen savedMap={savedMap} savedIds={savedIds} onSave={toggleSave} user={user} onGoProfile={() => goTab("profile")} onPlay={handlePlay} savedLyrics={savedLyrics} onEditLyric={handleEditLyric} onPlayPlaylist={function(full, i){ setPlayingPlaylist({ playlist: full, index: i || 0 }); }} />}
            {t === "studio"    && studioVisited && <StudioErrorBoundary><StudioScreen user={user} onExit={() => goTab("home")} savedLyrics={savedLyrics} onEditLyric={handleEditLyricFromStudio} onNewLyric={handleNewLyricFromStudio} onRegisterTransport={api => { studioTransportRef.current = api; setStudioTransportTick(t => t + 1); }} /></StudioErrorBoundary>}
            {t === "exclusive" && <ExclusiveScreen user={user} onGoProfile={() => goTab("profile")} onPlay={handlePlay} savedIds={savedIds} onSave={toggleSave} onSignUp={() => { setAuthStartMode("signup"); setShowAuthWall(true); setWelcomeDone(false); }} onViewProfile={function(u) { setPublicProfile(u); }} savedLyrics={savedLyrics} onEditLyric={handleEditLyric} onGoStudio={function(){ goTab("studio"); }} onGenreSearch={q => { setSearchQuery(q); goTab("search"); }} />}
          </div>
        ))}
        {tab === "profile" && (
          <div style={{ overflowY: "auto", height: "calc(100dvh - calc(72px + env(safe-area-inset-bottom)))", WebkitOverflowScrolling: "touch", overscrollBehavior: "none" }} onTouchMove={function(e){ e.stopPropagation(); }}>
            <ProfileScreen key={user ? user.id : "guest"} user={user} setUser={setUser} onLogout={() => {
              AuthAPI.logout();
              setUser(null);
              // Clear per-user licence cache from localStorage so the next
              // session (or a guest sharing this device) doesn't inherit
              // the previous user's "Licence Agreed ✓" state.
              try {
                var rm = [];
                for (var i = 0; i < localStorage.length; i++) {
                  var k = localStorage.key(i);
                  if (k && (
                    k.indexOf("bf_signed_free_") === 0 ||
                    k.indexOf("bf_signed_lease_") === 0 ||
                    k.indexOf("bf_backfilled_") === 0
                  )) rm.push(k);
                }
                rm.forEach(function(k) { try { localStorage.removeItem(k); } catch(e) {} });
              } catch(e) {}
              // Also clear cached paid-lease list
              try { window.__bf_leases__ = []; } catch(e) {}
              try { window.dispatchEvent(new CustomEvent("bf-leases-updated")); } catch(e) {}
              // Notify FreeBeatCTA components to re-check state (they'll see no agreement and reset)
              try { window.dispatchEvent(new CustomEvent("bf-free-licences-hydrated")); } catch(e) {}
              // Reset welcome gate so the welcome screen shows again
              try { localStorage.removeItem("bf_welcomed"); } catch(e) {}
              setWelcomeDone(false);
              setShowAuthWall(false);
              setAuthStartMode("login");
              goTab("home");
            }} savedLyrics={savedLyrics} setSavedLyrics={setSavedLyrics} onPlayBeat={handlePlay} onEditLyric={handleEditLyricNoBeat} onNewLyric={handleNewLyric} onOpenMessages={u => { openMessages(u); }} />
          </div>
        )}
      </div>

      {showPost && (
        <PostSheet user={user} onClose={() => setShowPost(false)} onPosted={() => {
          setShowPost(false);
          // Bump the feed refresh key so FollowingFeed re-fetches and the
          // user's own post appears at the top of the live feed immediately.
          setFeedRefreshKey(k => k + 1);
        }} />
      )}

      {showNotifications && (
        <NotificationsPanel
          user={user}
          onClose={() => setShowNotifications(false)}
          onJumpToFeed={() => goTab("feed")}
          onOpenPost={function(postId) {
            // Stash the post id, switch to Feed; the FollowingFeed will
            // pick it up via its highlightPostId prop, scroll to it,
            // briefly flash a border, then call onHighlightConsumed
            // which clears the state above.
            setHighlightPostId(postId);
            goTab("feed");
            setShowNotifications(false);
          }} />
      )}

      {showUserSearch && (
        <UserSearchOverlay
          onClose={() => setShowUserSearch(false)}
          onPickUser={function(username) {
            // Close the overlay and jump straight to the user's public
            // profile. Using setPublicProfile (rather than setSearchProfile)
            // gives them the same back-stack as following an avatar from
            // the Feed.
            setShowUserSearch(false);
            setPublicProfile(username);
          }} />
      )}

      {searchProfile && (
        <div style={{ position: "fixed", inset: 0, zIndex: 5000, background: "#0a0a0a", overflowY: "auto", WebkitOverflowScrolling: "touch", overscrollBehavior: "none" }}
          onTouchMove={function(e) { e.stopPropagation(); }}>
          <PublicProfileScreen
            username={searchProfile}
            onBack={function() { setSearchProfile(null); }}
            onPlay={handlePlay} savedIds={savedIds} onSave={toggleSave} currentUser={user}
            hideBack={false}
            onMessage={function(u) { setSearchProfile(null); setMessageThread(u); setShowMessages(true); }} />
        </div>
      )}

      {lyricsOpen && <LyricsNotepad
        beat={lyricsBeat}
        onClose={() => { setLyricsOpen(false); setEditingLyric(null); setEditingIndex(null); setLyricsFromStudio(false); }}
        onSaveLyric={handleSaveLyric}
        initialLyric={editingLyric}
        lyricIndex={editingIndex}
        user={user}
        studioControls={(lyricsFromStudio && studioTransportRef.current) ? studioTransportRef.current : null}
      />}

      {/* ── Lease purchase success modal ── */}
      {leaseModal && (
        <div style={{ position: "fixed", inset: 0, zIndex: 99999, background: "rgba(0,0,0,0.85)", display: "flex", alignItems: "center", justifyContent: "center", padding: 24 }}>
          <div style={{ background: "#111", borderRadius: 20, padding: 28, width: "100%", maxWidth: 380, border: "1.5px solid rgba(245,158,11,0.4)" }}>
            <div style={{ textAlign: "center", marginBottom: 20 }}>
              <div style={{ fontSize: 48, marginBottom: 8 }}>🎉</div>
              <div style={{ color: "#F59E0B", fontWeight: 800, fontSize: 20, marginBottom: 6 }}>Purchase Complete!</div>
              <div style={{ color: "#aaa", fontSize: 13 }}>
                {leaseBeat ? "\"" + leaseBeat.beat_title + "\" lease confirmed." : "Your lease is ready."}
              </div>
            </div>

            {leaseBeat && (function() {
              var contractHtml = generateLeaseContractHtml(leaseBeat);
              var dlUrl = leaseBeat.beat_id
                ? API_BASE + "/api/producer/beats/" + leaseBeat.beat_id + "/file"
                : (leaseBeat.beat_url ? leaseBeat.beat_url.replace("/upload/", "/upload/fl_attachment/") : "#");
              return (
                <>
                  {showLeaseContract && (
                    <ContractViewer html={contractHtml} filename={"BeatFinder_Lease_" + (leaseBeat.beat_title || "Beat").replace(/[^\w]/g, "_") + "_" + (leaseBeat.id || Date.now()) + ".html"} onClose={function() { setShowLeaseContract(false); }} pdfDownloader={function() { return downloadLeaseContractPdf(leaseBeat); }} />
                  )}
                  {/* Contract summary */}
                  <div style={{ background: "#0d0d0d", border: "1px solid #222", borderRadius: 12, padding: 14, marginBottom: 12, maxHeight: 200, overflowY: "auto", WebkitOverflowScrolling: "touch", fontSize: 11, color: "#888", lineHeight: 1.6 }}>
                    <div style={{ color: "#F59E0B", fontWeight: 800, fontSize: 11, marginBottom: 8, textAlign: "center", letterSpacing: 1 }}>BEAT LEASE AGREEMENT</div>
                    <div style={{ marginBottom: 6 }}><span style={{ color: "#aaa", fontWeight: 700 }}>Beat:</span> "{leaseBeat.beat_title}"</div>
                    <div style={{ marginBottom: 6 }}><span style={{ color: "#aaa", fontWeight: 700 }}>Producer:</span> {leaseBeat.producer}</div>
                    <div style={{ marginBottom: 6 }}><span style={{ color: "#aaa", fontWeight: 700 }}>Artist:</span> {leaseBeat.buyer_name || leaseBeat.buyer_username || user?.name || user?.username}</div>
                    <div style={{ marginBottom: 6 }}><span style={{ color: "#aaa", fontWeight: 700 }}>Price:</span> {leaseBeat.price}</div>
                    <div style={{ height: 1, background: "#222", margin: "8px 0" }} />
                    <div style={{ marginBottom: 4 }}><span style={{ color: "#F59E0B", fontWeight: 700 }}>Non-Exclusive MP3 Lease</span> — stream, perform, distribute commercially up to 100k streams.</div>
                    <div style={{ marginBottom: 4 }}>Must credit: <em>(Prod. by {leaseBeat.producer})</em> in all releases.</div>
                    <div style={{ marginBottom: 4 }}>Producer retains copyright. 50% composition royalties to producer.</div>
                    <div style={{ marginBottom: 4 }}>No resale, no sub-licensing, no copyright claim on the Beat.</div>
                    <div style={{ marginBottom: 4 }}>Governed by England and Wales law.</div>
                    <div style={{ color: "#555", fontSize: 10, marginTop: 8 }}>By tapping "I Agree" you accept all lease terms. Ref: {leaseBeat.id}</div>
                  </div>

                  {/* I Agree — opens full contract viewer */}
                  <button onClick={function() { setShowLeaseContract(true); }} style={{
                    width: "100%", borderRadius: 14, padding: "13px",
                    background: "linear-gradient(135deg,#F59E0B,#D97706)",
                    border: "none", color: "white", fontWeight: 800, fontSize: 14,
                    letterSpacing: 0.5, textTransform: "uppercase",
                    display: "flex", alignItems: "center", justifyContent: "center", gap: 8,
                    cursor: "pointer", marginBottom: 8,
                  }}>
                    <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="white" strokeWidth="2.5" strokeLinecap="round"><polyline points="20 6 9 17 4 12"/></svg>
                    I Agree — View Full Contract
                  </button>

                  {/* Download MP3 */}
                  {leaseBeat.beat_url && (
                    <button onClick={function() { downloadBeatMp3({ id: leaseBeat.beat_id, url: leaseBeat.beat_url, title: leaseBeat.beat_title }); }} style={{
                      width: "100%", borderRadius: 14, padding: "13px",
                      background: "transparent", border: "2px solid #F59E0B",
                      color: "#F59E0B", fontWeight: 800, fontSize: 14,
                      letterSpacing: 0.5, textTransform: "uppercase",
                      display: "flex", alignItems: "center", justifyContent: "center", gap: 8,
                      cursor: "pointer", marginBottom: 8,
                    }}>
                      <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="#F59E0B" strokeWidth="2.5" strokeLinecap="round"><path d="M12 3v13M6 11l6 6 6-6"/><path d="M4 20h16"/></svg>
                      Download MP3
                    </button>
                  )}
                </>
              );
            })()}

            <button onClick={function() { setLeaseModal(false); goTab("profile"); }} style={{
              width: "100%", background: "transparent", border: "1px solid #222",
              borderRadius: 14, color: "#555", fontWeight: 600, fontSize: 13,
              padding: "11px", cursor: "pointer",
            }}>
              View My Purchases
            </button>
          </div>
        </div>
      )}
      {playing && (
        <Player
          beat={playing}
          onClose={() => setPlaying(null)}
          savedIds={savedIds}
          onSave={toggleSave}
          isGuest={!user}
          isArtistPro={!user || user?.isPro || user?.isArtistPro}
          onGoMembers={() => goTab("exclusive")}
          onOpenLyrics={handleOpenLyrics}
          savedLyrics={savedLyrics}
          onEditLyric={handleEditLyric}
        />
      )}
      {addToPlaylistVideo && (
        <AddToPlaylistSheet
          video={addToPlaylistVideo}
          onClose={function(){ setAddToPlaylistVideo(null); }}
        />
      )}
      {playingPlaylist && (
        <PlaylistPlayer
          playlist={playingPlaylist.playlist}
          startIndex={playingPlaylist.index || 0}
          onClose={function(){ setPlayingPlaylist(null); }}
        />
      )}
      <div style={{
          position: "fixed", bottom: 0, left: "50%", transform: "translateX(-50%)",
          width: "100%", maxWidth: 430,
          background: "linear-gradient(180deg, rgba(15,10,28,0.82) 0%, rgba(8,6,16,0.95) 100%)",
          backdropFilter: "blur(18px)", WebkitBackdropFilter: "blur(18px)",
          borderTop: "1px solid rgba(176,38,255,0.4)",
          display: tab === "studio" ? "none" : "flex",
          paddingTop: 8,
          paddingBottom: "max(env(safe-area-inset-bottom), 16px)",
          boxShadow: "0 -10px 34px rgba(0,0,0,0.85), 0 -1px 0 0 rgba(176,38,255,0.3)",
          zIndex: 1000,
        }}>
        {NAV.filter(function(n){ return !(n.id === 'profile' && !user); }).map(n => {
          const isPro    = user?.isPro || user?.isArtistPro;
          const locked   = (n.id === "exclusive" && (!user || !isPro)) || (n.id === "studio" && (!user || (user && !user.subscriptionActive && user.username !== "Trelli")));
          const isActive = tab === n.id;
          const activeColor = n.id === "exclusive" ? "#A78BFA" : n.id === "studio" ? "#22C55E" : "#C026D3";
          const isStudio = n.id === "studio";
          return (
            <React.Fragment key={n.id}>
              {/* Divider before Studio */}
              {isStudio && (
                <div style={{
                  width: 1, background: "rgba(255,255,255,0.08)",
                  margin: "10px 0", flexShrink: 0,
                }} />
              )}
              <button onClick={() => goTab(n.id)} className="bf-nav-btn"
                style={{
                  flex: 1, background: isStudio && isActive ? "rgba(34,197,94,0.08)" : "none",
                  border: "none", cursor: "pointer",
                  display: "flex", flexDirection: "column", alignItems: "center", justifyContent: "center", gap: 4,
                  color: isActive ? activeColor : locked ? "#A78BFA44" : "#444",
                  position: "relative", paddingTop: 6, paddingBottom: 6,
                  transition: "color 0.2s ease",
                  borderRadius: isStudio ? 0 : 0,
                }}>
                {/* Active indicator dot */}
                {isActive && (
                  <div style={{
                    position: "absolute", top: 0, left: "50%", transform: "translateX(-50%)",
                    width: 20, height: 2, borderRadius: 1,
                    background: activeColor,
                    boxShadow: "0 0 8px " + activeColor,
                  }} />
                )}
                {/* Active glow bg */}
                {isActive && (
                  <div style={{
                    position: "absolute", inset: 0,
                    background: "radial-gradient(ellipse at 50% 80%, " + activeColor + "18 0%, transparent 70%)",
                    borderRadius: 12,
                    pointerEvents: "none",
                  }} />
                )}
                <NavIcon id={n.id} size={22} active={isActive} activeColor={activeColor} />
                <span style={{
                  fontSize: 9, fontWeight: isActive ? 800 : 600,
                  letterSpacing: isActive ? 0.3 : 0,
                  transition: "all 0.2s ease",
                }}>{n.label}</span>
                {locked && <div style={{
                  position: "absolute", top: 6, right: "calc(50% - 14px)",
                  background: "#A78BFA", borderRadius: "50%",
                  width: 7, height: 7,
                  boxShadow: "0 0 6px rgba(124,58,237,0.6)",
                }} />}
                {n.id === "saved" && savedIds.size > 0 && (
                  <div style={{
                    position: "absolute", top: 5, right: "calc(50% - 16px)",
                    background: "#C026D3", borderRadius: "50%",
                    fontSize: 8, fontWeight: 800, color: "white",
                    padding: "1px 4px", minWidth: 14, textAlign: "center",
                    lineHeight: "14px", height: 14,
                    boxShadow: "0 0 6px rgba(192,38,211,0.6)",
                  }}>
                    {savedIds.size}
                  </div>
                )}
              </button>
            </React.Fragment>
          );
        })}
      </div>
    </div>
  );
}

// ─────────────────────────────────────────────────────────────────────────
// Sentry ErrorBoundary wrapper for the root export.
// If anything in the React tree throws during render, Sentry catches the
// error with full component stack + we show a graceful fallback UI instead
// of a white screen.
// ─────────────────────────────────────────────────────────────────────────
function BeatFinderErrorFallback({ error, resetError }) {
  return (
    <div style={{
      minHeight: "100vh", background: "#0a0a0a", color: "white",
      fontFamily: "'DM Sans',sans-serif",
      display: "flex", flexDirection: "column",
      alignItems: "center", justifyContent: "center",
      padding: 24, textAlign: "center",
    }}>
      <div style={{
        fontFamily: "'Bebas Neue',sans-serif", fontSize: 32, letterSpacing: 2,
        marginBottom: 12, color: "#C026D3",
      }}>
        SOMETHING BROKE
      </div>
      <div style={{ color: "#aaa", fontSize: 14, lineHeight: 1.6, maxWidth: 360, marginBottom: 24 }}>
        We've been notified and are looking into it. Try reloading the page —
        if it keeps happening, drop us a line at support@beatfinder.co.uk.
      </div>
      <button onClick={function() {
        try { resetError && resetError(); } catch (_) {}
        try { window.location.reload(); } catch (_) {}
      }} style={{
        background: "#C026D3", border: "none", borderRadius: 28,
        color: "white", fontWeight: 800, fontSize: 14,
        padding: "12px 28px", cursor: "pointer",
      }}>
        Reload App
      </button>
    </div>
  );
}

// ═════════════════════════════════════════════════════════════════════════════
//  BFSoundfontDebugPanel — on-screen diagnostic log for the soundfont engine
// ═════════════════════════════════════════════════════════════════════════════
//
// Visible only after the engine has ever been initialised (i.e. the user
// has touched a soundfont preset). Shows the last 30 events from the
// engine: load steps, fetch progress, scheduleNote calls, errors.
//
// This exists because iOS Safari makes the JS console nearly unreachable
// on a phone. The panel surfaces what's happening in plain English so
// we can debug "no sound" without remote dev tools.
//
// Dismissable via the × button. Re-appears after a page reload.
function BFSoundfontDebugPanel() {
  const [tick, setTick] = React.useState(0);
  const [dismissed, setDismissed] = React.useState(false);
  const [collapsed, setCollapsed] = React.useState(false);

  React.useEffect(() => {
    // Poll the singleton's diag buffer at 250ms. We don't subscribe
    // directly because the engine may not exist yet at mount time —
    // polling sidesteps that ordering.
    const id = setInterval(() => setTick(t => t + 1), 250);
    return () => clearInterval(id);
  }, []);

  // Avoid creating the engine just to look at it; if it doesn't exist
  // yet, render nothing. _bfSfEngineInstance is the module-scope handle.
  const eng = _bfSfEngineInstance;
  if (!eng || dismissed) return null;
  if (!eng.diag || eng.diag.length === 0) return null;

  const bg = "rgba(10,5,20,0.94)";
  const border = "1px solid rgba(167,139,250,0.35)";
  const lastErr = eng.diag.slice().reverse().find(l => l.includes("ERROR"));

  return (
    <div style={{
      position: "fixed",
      top: "calc(var(--bf-safe-top, env(safe-area-inset-top)) + 6px)",
      left: 6, right: 6,
      zIndex: 99997,
      background: bg,
      border: border,
      borderRadius: 10,
      padding: collapsed ? "6px 10px" : "8px 10px 10px",
      color: "#E8E4F0",
      fontSize: 10,
      fontFamily: "ui-monospace, SFMono-Regular, Menlo, monospace",
      boxShadow: "0 8px 24px rgba(0,0,0,0.6)",
      lineHeight: 1.35,
      maxHeight: collapsed ? 28 : "40vh",
      overflow: "hidden",
    }}>
      <div style={{
        display: "flex", justifyContent: "space-between", alignItems: "center",
        gap: 6,
        marginBottom: collapsed ? 0 : 4,
      }}>
        <div style={{
          fontSize: 9, letterSpacing: 1.5, fontWeight: 700,
          color: lastErr ? "#FCA5A5" : "#C4B5FD",
        }}>
          SOUNDFONT · {eng.libStatus.toUpperCase()}
          {lastErr ? " · ERROR" : ""}
        </div>
        <div style={{ display: "flex", gap: 4 }}>
          <button onClick={() => setCollapsed(c => !c)} style={{
            background: "rgba(167,139,250,0.18)", border: "none",
            color: "#E8E4F0", fontSize: 10, padding: "2px 8px",
            borderRadius: 6, fontFamily: "inherit",
          }}>{collapsed ? "expand" : "min"}</button>
          <button onClick={() => setDismissed(true)} style={{
            background: "rgba(252,165,165,0.18)", border: "none",
            color: "#FCA5A5", fontSize: 11, padding: "2px 8px",
            borderRadius: 6, fontFamily: "inherit",
          }}>×</button>
        </div>
      </div>
      {!collapsed && (
        <div style={{
          maxHeight: "32vh",
          overflowY: "auto",
          fontSize: 9.5,
          whiteSpace: "pre-wrap",
          wordBreak: "break-word",
        }}>
          {eng.diag.slice().reverse().map((line, i) => (
            <div key={i} style={{
              color: line.includes("ERROR") ? "#FCA5A5"
                   : line.includes("✓") ? "#A7F3D0"
                   : "#C4B5FD",
              opacity: i < 8 ? 1 : 0.55,
              marginBottom: 1.5,
            }}>{line}</div>
          ))}
        </div>
      )}
    </div>
  );
}

export default function BeatFinder(props) {
  return (
    <Sentry.ErrorBoundary fallback={BeatFinderErrorFallback}>
      <BeatFinderInner {...props} />
    </Sentry.ErrorBoundary>
  );
}
